
// ===================== glue/assets.jsx =====================
// glue/assets.jsx is FIRST in the concat. It creates the window.OO namespace skeleton and the normalised
// boot config, and provides OO.asset(path), the port of the warehouse window.sipoAsset.
//
// OO.asset('online-order/fixtures/img/fat-boy.jpg') resolves a public-root-relative path against the
// deploy root (__SIPO_BOOT.assetRoot), so it survives a sub-directory mount such as /dev_server/public.
// The host is stripped to keep same-origin, path-only URLs. With no assetRoot it falls back to a plain
// root-absolute path. window.sipoAsset is kept as an alias for parity with the other SPAs.
//
// Namespace rule: every source file self-wraps in an IIFE and exports ONLY through window.OO.*
// (OO.router, OO.pages.Menu, OO.ui.Button, OO.lib.cart, …). esbuild puts the whole concat in one IIFE
// scope, so two files with the same top-level name would collide.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};
  OO.data = OO.data || {};
  OO.ui = OO.ui || {};
  OO.pages = OO.pages || {};

  var BOOT = window.__SIPO_BOOT || {};
  OO.boot = BOOT;
  // 'online' (online.sipocloudpos.com; also the WL-3 landing mount) | 'app' (app.sipocloudpos.com) | 'qr'
  // (qr.sipocloudpos.com, qr.sipo.nz; P4-14)
  OO.mode = BOOT.mode === 'app' || BOOT.mode === 'qr' || BOOT.mode === 'kiosk' ? BOOT.mode : 'online';
  // WL-3: boot mode 'landing' (the /endpoint/landing mount) is mode online with the chain's landing page at /{hotel}
  // (glue/router.jsx "Mode landing"); the ordering pages read OO.mode 'online' and behave as on the online host.
  OO.landingMode = BOOT.mode === 'landing';

  OO.asset = function asset(path) {
    var b = window.__SIPO_BOOT || {};
    var root = String(b.assetRoot || '').replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, ''); // path-only, no trailing slash
    return root + '/' + String(path == null ? '' : path).replace(/^\/+/, '');
  };
  window.sipoAsset = OO.asset;
})();


// ===================== glue/api-client.jsx =====================
// OO.http (v2) is the JSON transport for the Laravel API (routes/api.php). The real adapters (src/data/api-real*.jsx)
// call it; the fixture adapter (src/data/store-api.jsx) never does. No JSX: tests/http.test.mjs runs it raw in node:vm.
//
// Requests are same-origin: `${apiBase}/${path}?${query}`. apiBase is __SIPO_BOOT.apiBase (default '/api'), path-only
// ('/dev_server/public/api' on a sub-directory mount), trailing slashes trimmed. Every request sends
// credentials: 'same-origin' and Accept: application/json. A body goes as JSON (a string as is) with
// Content-Type: application/json, never on GET / HEAD. No bearer token by default (the customer endpoints are public);
// a caller passes one in opts.headers: B16 POST customer/profile sends the verify-otp custom token (P3-09).
// X-App-Version and X-Current-Time are NEVER sent, not even when a caller passes them (R-API-1/2, D-15: the backend
// turns X-App-Version into a checkout kill-switch). There is no other default header.
//
//   OO.http.req(method, path, opts) → Promise. v1-compatible: resolves the parsed body of a 2xx (null if empty, the
//       raw text if not JSON); rejects with an Error (below) on any other status or a network failure.
//   OO.http.get(path, opts) · OO.http.post(path, body, opts)
//   opts = {
//     query       object → query string, in key order; null / undefined values dropped.
//     headers     extra headers.
//     signal      the caller's AbortSignal, combined with the read deadline. A CALLER abort rejects with the native
//                 AbortError (name 'AbortError'), never converted, even when soft. A read with a signal is never
//                 deduped (it neither joins nor is joined), so one caller's abort can't cancel another's request.
//     withStatus  true → resolves { status, data } for ANY HTTP status; rejects only on a network failure or the
//                 deadline (kind 'network'). For checkout (E12) and payment_update (E13).
//     read        true | false overrides the read / write class. Default: GET / HEAD = read; POST = read iff its
//                 path (leading '/' and any '?…' stripped) is in READ_POSTS; anything else = write.
//     cache       reads only: true (= TTL_MS) | a TTL in ms = the OLDEST entry this read accepts (an entry is stamped
//                 when it is stored). Stores the parsed body of a 2xx only when isSuccess(body): never an
//                 unparseable body, never a status-zoo failure such as getRestaurant's 200 {status:'False'} (that
//                 still resolves to the caller). Key: METHOD + ' ' + path + '?' + sorted query + ' ' + JSON body.
//                 A hit resolves a fresh copy (structuredClone, else a JSON round-trip), so no caller can mutate
//                 the cache.
//     fresh       true → skips the cache lookup AND joining an in-flight read. It still stores its result (with
//                 cache), and later identical reads join it.
//     soft        true → a failure resolves null instead of rejecting: a network failure, the deadline, a non-2xx
//                 (unless withStatus) or an unparseable body. It logs ONE console.warn('[OO.http] soft-fail <path>
//                 <status|kind>'), path only (queries and bodies carry customer ids). For wallet, loyalty,
//                 transactions, pricetags and suggestions (A F12 "soft failure").
//     deadlineMs  reads only: overrides READ_DEADLINE_MS; 0 = no deadline. Writes NEVER time out.
//   }
//   Errors: an Error with { status, data, kind, message }, plus timeout: true when the read deadline fired.
//     status   the HTTP status; 0 for a network failure or the deadline.
//     kind     kindOf(status): 'network' (status 0: fetch rejected, offline, the deadline) · 'unavailable' (408, 425,
//              429, ≥ 500) · 'notFound' (404, by STATUS ALONE: prod 404 bodies are nginx HTML, never rely on them) ·
//              'client' (any other 4xx).
//     data     the parsed error body (JSON), else its text; null if empty.
//     message  data.message, then data.error (non-empty strings), else 'HTTP <status>'. A network failure: 'Network
//              error. Check your connection and try again.' The deadline: 'The server took too long to respond. Try
//              again.'
//   Read deadline: READ_DEADLINE_MS per read, via AbortController + setTimeout (both resolved at call time; without
//     AbortController there is no deadline). It rejects at once, then aborts the request; settling clears the timer.
//   Dedupe: identical in-flight reads (same cache key + withStatus flag) share ONE request; every joiner resolves its
//     own copy. The entry is dropped when the request settles. Writes are never deduped.
//   Write flush (R-API-3): a write that settles with a 2xx (withStatus too) clears the WHOLE read cache and the dedupe
//     map, so a read started after it never joins a pre-write read; a read in flight across it stores nothing.
//   The cache is in memory only (never localStorage), at most 300 entries; the oldest is evicted.
//   OO.http.flush()          clears the read cache and the dedupe map.
//   OO.http.isSuccess(body)  the status-zoo test (R-API-5): false iff body is an object whose status, Status or
//                            success is false, 0, '0', 'false', 'error', 'failed' or 'failure' (strings trimmed, any
//                            case). No flag, a null / empty body (REF reads it as {}) or a non-object = success.
//   OO.http.kindOf(status)   'network' | 'unavailable' | 'notFound' | 'client' | null (2xx / 3xx).
//   OO.http.READ_POSTS       the frozen list of POST paths that are reads (A F12 "Read vs write" + BATCH2-PLAN §5).
//   OO.http.TTL_MS = 900000 (15 min) · OO.http.READ_DEADLINE_MS = 30000 (30 s).
//   Test hooks: __setFetch(fn | null) replaces fetch (default window.fetch, resolved at call time);
//               __setClock(fn | null) replaces the ms clock of the cache (default Date.now).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});

  var TTL_MS = 900000;
  var READ_DEADLINE_MS = 30000;
  var MAX_TIMER_MS = 2147483647;            // setTimeout fires at once beyond this
  var CACHE_MAX = 300;
  var READ_POSTS = Object.freeze([
    'getRestaurant', 'getRestaurantBulk', 'get_menu', 'get_redemption_catalogue', 'coupon_get_v4', 'validate_coupon_v4',
    'get_table_booking_config', 'check_table_booking_slots', 'table_booking_status', 'online_order_status',
    'get_customer_orders', 'get_customer_wallet_points', 'get_user_loyalty_products_v4',
    'get_customer_table_bookings', 'customer/store-rating-bulk',
  ]);
  var NEVER_SENT = /^x-(app-version|current-time)$/i;
  var FAIL_FLAGS = ['false', '0', 'error', 'failed', 'failure'];
  var NETWORK_MESSAGE = 'Network error. Check your connection and try again.';
  var DEADLINE_MESSAGE = 'The server took too long to respond. Try again.';
  var GLOBAL = typeof globalThis !== 'undefined' ? globalThis : window;

  var fetchHook = null;
  var clockHook = null;
  var cache = Object.create(null);          // key → { status, data, at, seq }, oldest first
  var cacheSize = 0;
  var inflight = Object.create(null);       // dedupe key → { promise, cache, gen, seq }
  var gen = 0;                              // flush generation: a read stores only when no flush ran meanwhile
  var seq = 0;                              // read counter: an older read never overwrites a newer cached body

  // ---- web APIs, resolved at CALL time (tests and polyfills may swap them after load) -------------------------
  // window first, then the global scope (node:vm tests put stubs there). They are always called with window as
  // `this`: a browser throws "Illegal invocation" for any other receiver.
  function webApi(name) {
    var f = typeof window[name] === 'function' ? window[name] : GLOBAL[name];
    return typeof f === 'function' ? f : null;
  }
  function now() { return clockHook ? Number(clockHook()) : Date.now(); }
  function copy(v) {
    if (v === null || typeof v !== 'object') return v;
    var clone = webApi('structuredClone');
    if (clone) {
      try { return clone.call(window, v); } catch (e) { /* the JSON round-trip below */ }
    }
    return JSON.parse(JSON.stringify(v));
  }

  // ---- request building -----------------------------------------------------------------------------------
  function apiBase() {
    var b = window.__SIPO_BOOT || {};
    return String(b.apiBase || '/api').replace(/\/+$/, '');
  }

  // 'k=v&…' in key order (sorted for the cache key); null / undefined values dropped.
  function queryString(query, sorted) {
    var keys = Object.keys(query || {});
    if (sorted) keys.sort();
    var parts = [];
    keys.forEach(function (key) {
      var v = query[key];
      if (v === undefined || v === null) return;
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(v)));
    });
    return parts.join('&');
  }

  function isRead(method, path, opts) {
    if (typeof opts.read === 'boolean') return opts.read;
    if (method === 'GET' || method === 'HEAD') return true;
    return method === 'POST' && READ_POSTS.indexOf(path.split('?')[0]) >= 0;
  }

  function ttlOf(cacheOpt) {
    if (cacheOpt === true) return TTL_MS;
    return typeof cacheOpt === 'number' && cacheOpt > 0 ? cacheOpt : 0;
  }

  function deadlineOf(opts) {
    if (opts.deadlineMs === undefined || opts.deadlineMs === null) return READ_DEADLINE_MS;
    var ms = Number(opts.deadlineMs);
    return ms > 0 && ms <= MAX_TIMER_MS ? ms : 0;
  }

  // ---- errors and the status zoo --------------------------------------------------------------------------
  function kindOf(status) {
    var s = Number(status);
    if (!(s > 0)) return 'network';
    if (s === 404) return 'notFound';
    if (s === 408 || s === 425 || s === 429 || s >= 500) return 'unavailable';
    return s >= 400 ? 'client' : null;
  }

  function messageOf(data, status) {
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message) return data.message;
      if (typeof data.error === 'string' && data.error) return data.error;
    }
    return 'HTTP ' + status;
  }

  function httpError(status, message, data) {
    var err = new Error(message);
    err.status = status;
    err.data = data;
    err.kind = kindOf(status);
    return err;
  }

  function networkError(timeout) {
    var err = httpError(0, timeout ? DEADLINE_MESSAGE : NETWORK_MESSAGE, null);
    if (timeout) err.timeout = true;
    return err;
  }

  function failFlag(v) {
    if (v === false || v === 0) return true;
    return typeof v === 'string' && FAIL_FLAGS.indexOf(v.trim().toLowerCase()) >= 0;
  }

  function isSuccess(body) {
    if (!body || typeof body !== 'object') return true;
    return !(failFlag(body.status) || failFlag(body.Status) || failFlag(body.success));
  }

  // ---- one HTTP exchange ----------------------------------------------------------------------------------
  // → { status, ok, data, parseFailed }. Rejects with a network Error (also for the deadline) or with the caller's
  // native AbortError. A read deadline rejects at once and then aborts the request, so it also covers a stalled
  // body or a fetch that ignores its signal.
  function exchange(url, init, deadline, signal) {
    return new Promise(function (resolve, reject) {
      var doFetch = fetchHook || webApi('fetch');
      var Ctrl = deadline ? webApi('AbortController') : null;
      var setTimer = Ctrl ? webApi('setTimeout') : null;
      var timer = null;
      var onAbort = null;
      var settled = false;
      function settle(fn, value) {
        if (settled) return;
        settled = true;
        var clearTimer = timer !== null ? webApi('clearTimeout') : null;
        if (clearTimer) clearTimer.call(window, timer);
        if (onAbort) signal.removeEventListener('abort', onAbort);
        fn(value);
      }
      if (setTimer) {
        var ctrl = new Ctrl();
        if (signal && signal.aborted) ctrl.abort();
        else if (signal) {
          onAbort = function () { ctrl.abort(); };
          signal.addEventListener('abort', onAbort);
        }
        init.signal = ctrl.signal;
        timer = setTimer.call(window, function () {
          timer = null;
          settle(reject, networkError(true));
          ctrl.abort();
        }, deadline);
      } else if (signal) {
        init.signal = signal;
      }
      if (!doFetch) { settle(reject, networkError()); return; }
      var pending;
      try { pending = doFetch.call(window, url, init); } catch (e) { pending = Promise.reject(e); }
      Promise.resolve(pending).then(function (res) {
        return Promise.resolve(res.text()).then(function (text) {
          var out = { status: res.status, ok: !!res.ok, data: null, parseFailed: false };
          if (text) {
            try { out.data = JSON.parse(text); } catch (e) { out.data = text; out.parseFailed = true; }
          }
          return out;
        });
      }).then(function (out) { settle(resolve, out); }, function (e) {
        settle(reject, e && e.name === 'AbortError' && signal && signal.aborted ? e : networkError());
      });
    });
  }

  // ---- read cache + dedupe --------------------------------------------------------------------------------
  function outcomeCopy(out) {
    return { status: out.status, ok: out.ok, data: copy(out.data), parseFailed: out.parseFailed };
  }

  function cacheGet(key, ttl) {
    var entry = cache[key];
    var age = entry ? now() - entry.at : -1;
    if (!(age >= 0 && age < ttl)) return null;
    return { status: entry.status, ok: true, data: copy(entry.data), parseFailed: false };
  }

  function cachePut(key, out, readSeq) {
    var old = cache[key];
    if (old && old.seq > readSeq) return;   // a newer read already stored this key
    if (old) delete cache[key]; else cacheSize += 1;
    cache[key] = { status: out.status, data: copy(out.data), at: now(), seq: readSeq };
    if (cacheSize > CACHE_MAX) {
      for (var oldest in cache) { delete cache[oldest]; cacheSize -= 1; break; }
    }
  }

  function flush() {
    cache = Object.create(null);
    cacheSize = 0;
    inflight = Object.create(null);
    gen += 1;
  }

  // A read that missed the cache: joins an identical in-flight read (a copy of its outcome) or starts one.
  function sharedRead(key, ttl, url, init, deadline, opts) {
    var dkey = opts.signal ? null : (opts.withStatus ? 'S ' : 'B ') + key;
    var rec = dkey && !opts.fresh ? inflight[dkey] : null;
    if (rec) {
      if (ttl) rec.cache = true;
      return rec.promise.then(outcomeCopy);
    }
    rec = { cache: ttl > 0, gen: gen, seq: (seq += 1) };
    function release() { if (dkey && inflight[dkey] === rec) delete inflight[dkey]; }
    rec.promise = exchange(url, init, deadline, opts.signal).then(function (out) {
      release();
      // Stored: a 2xx parsed body that passes isSuccess, from a read no flush overtook. A status-zoo failure (a
      // getRestaurant 200 {status:'False'}) still resolves to its callers but is never cached.
      if (rec.cache && out.ok && !out.parseFailed && isSuccess(out.data) && rec.gen === gen) {
        cachePut(key, out, rec.seq);
      }
      return out;
    }, function (e) {
      release();
      throw e;
    });
    if (dkey) inflight[dkey] = rec;
    return rec.promise;
  }

  // ---- per caller: shape the outcome (v1: a 2xx resolves the body, anything else rejects) --------------------
  function softFail(path, why) {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn('[OO.http] soft-fail ' + path + ' ' + why);
    }
    return null;
  }

  function deliver(out, call) {
    if (call.soft && (out.parseFailed || (!out.ok && !call.withStatus))) {
      return softFail(call.path, out.status || 'network');
    }
    if (call.withStatus) return { status: out.status, data: out.data };
    if (out.ok) return out.data;
    throw httpError(out.status, messageOf(out.data, out.status), out.data);
  }

  function failed(e, call) {
    if (call.soft && !(e && e.name === 'AbortError')) return softFail(call.path, (e && e.kind) || 'network');
    throw e;
  }

  function req(method, path, opts) {
    opts = opts || {};
    method = String(method || 'GET').toUpperCase();
    path = String(path || '').replace(/^\/+/, '');
    var headers = { Accept: 'application/json' };
    Object.keys(opts.headers || {}).forEach(function (name) {
      if (!NEVER_SENT.test(name)) headers[name] = opts.headers[name];
    });
    var init = { method: method, headers: headers, credentials: 'same-origin' };
    if (opts.body !== undefined && method !== 'GET' && method !== 'HEAD') {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
    }
    var qs = queryString(opts.query);
    var url = apiBase() + '/' + path + (qs ? (path.indexOf('?') < 0 ? '?' : '&') + qs : '');
    var call = { path: path.split('?')[0], withStatus: !!opts.withStatus, soft: !!opts.soft };
    var outcome;
    if (isRead(method, path, opts)) {
      var ttl = ttlOf(opts.cache);
      var key = method + ' ' + path + '?' + queryString(opts.query, true) + ' '
        + (init.body === undefined ? '' : init.body);
      var hit = ttl && !opts.fresh ? cacheGet(key, ttl) : null;
      outcome = hit ? Promise.resolve(hit) : sharedRead(key, ttl, url, init, deadlineOf(opts), opts);
    } else {
      outcome = exchange(url, init, 0, opts.signal).then(function (out) {
        if (out.ok) flush();                 // R-API-3: a successful write invalidates every read
        return out;
      });
    }
    return outcome.then(function (out) { return deliver(out, call); }, function (e) { return failed(e, call); });
  }

  OO.http = {
    req: req,
    get: function (path, opts) { return req('GET', path, opts); },
    post: function (path, body, opts) { return req('POST', path, Object.assign({}, opts, { body: body })); },
    flush: flush,
    isSuccess: isSuccess,
    kindOf: kindOf,
    READ_POSTS: READ_POSTS,
    TTL_MS: TTL_MS,
    READ_DEADLINE_MS: READ_DEADLINE_MS,
    __setFetch: function (fn) { fetchHook = typeof fn === 'function' ? fn : null; },
    __setClock: function (fn) { clockHook = typeof fn === 'function' ? fn : null; },
  };
})();


// ===================== glue/router.jsx =====================
// OO.router — the PURE path router of the Online Order SPA (one bundle, three modes). NO JSX in this
// file: tests/router.test.mjs runs it raw in node:vm. React is only touched lazily inside useRoute().
//
// Paths are relative to routeBase, which is '' on the bare hosts (online./app./qr.sipocloudpos.com, qr.sipo.nz). On
// other hosts it is the mount, e.g. '/endpoint/online-order', '/endpoint/qr-order' or
// '/dev_server/public/endpoint/customer-app' (the blade injects __SIPO_BOOT.routeBase and __SIPO_BOOT.mode).
// Modes (normMode; any other value is 'online'): 'online' (online ordering), 'app' (the customer app), 'qr' (ordering
// at a table, P4-14: its own route table, "Mode qr" below).
//
// The first segment is the store: {hotel} = users.online_key. It is free text and may contain spaces,
// '&' or "'", so it arrives URL-encoded. parse() decodes it BEFORE matching it (P5-01: '/Benny%27s/menu' is the store
// "Benny's", '/%61pi/x' is the reserved '/api/x', as the backend reads it) and href() encodes it. In mode app it names
// the CHAIN and the branch rides in ?store= ("Mode app" below). Sub-path names (menu, table, product, …) match as
// they come.
//
// Route table (relative to routeBase; modes online and app — mode qr below). parse() returns
// { hotel, page, params, query [, redirectTo] } (mode app, under a store: + chain, via):
//   /                                   find-store        (hotel null)
//   /payment-callback                   payment-callback  (hotel null; a page forwards to the store's)
//   /{hotel}                            menu (mode online) | home (mode app)
//                                       ?book=1 (REF's legacy booking link) → /{hotel}/booking, the query kept minus book
//   /{hotel}/menu                       menu
//   /{hotel}/product/{name}-{id}        menu              P5-01, REF's SEO item paths (D-19): params { item: 'product' |
//   /{hotel}/combo/{name}-{id}                            'combo', itemId (the trailing digits of the segment: after a
//                                                         '-', or the whole segment), itemSlug (the segment, decoded) };
//                                                         the menu opens that item. KEPT as it is (the SEO canonical;
//                                                         the menu page leaves it when the sheet closes). A deep-link
//                                                         query on it is dropped (the path wins); a segment with no id
//                                                         → the plain menu (/{hotel} online, /{hotel}/menu app)
//   /{hotel}/table/{tableId}            → the store's menu (/{hotel} online, /{hotel}/menu app): tables are qr-only
//                                       (P4-14; the online and app hosts send table URLs to the qr host, P4-13)
//   /{hotel}/cart | /checkout           cart | checkout
//   /{hotel}/orderStatus                order-status
//   /{hotel}/profile/orders/{id}        order-status      params.orderId (href's form: order emails link here)
//   /{hotel}/orders/{id}                order-status      params.orderId (REF's order detail path; ?print=1 kept)
//   /{hotel}/orders | /profile/orders   orders
//   /{hotel}/orders/{id}/review         review            params.orderId
//   /{hotel}/track                      track             the order lookup (?mode=booking: booking-track, below)
//   /{hotel}/track/{id}                 track             params.orderId: the public track
//   /{hotel}/wallet | /about | /coupons | /deals
//                                       wallet | about | coupons | deals
//   /{hotel}/account | /rewards | /booking
//                                       account | rewards | booking (both modes since P3-01: D-18)
//   /{hotel}/bookings                   bookings          the signed-in booking history
//   /{hotel}/bookings/{id}              bookings          params.reservationId
//   /{hotel}/booking/track              booking-track     the booking lookup; also /{hotel}/track?mode=booking
//   /{hotel}/booking/track/{id}         booking-track     params.reservationId
//   /{hotel}/profile                    profile
//   /{hotel}/edit-profile               profile           params.edit = true
//   /{hotel}/payment-callback           payment-callback
//   /{hotel}/terms-and-conditions | /privacy-policy | /allergy-information
//                                       legal             params.kind = terms | privacy | allergy
//   App mode only: /{hotel}/home | /stores | /notifications
//   /{hotel}/__ui                       ui-gallery        dev UI kit gallery (src/pages/ui-gallery.jsx), every mode
//   /{hotel}/manifest.webmanifest | /icon | /opengraph-image | /twitter-image
//                                       not-found         (hotel null) machine routes the backend serves (B21, P5-03…05);
//                                                         one that reaches the SPA is no page, in every mode
// Ids in a path ({id}, {tableId}, an item's {name}-{id}) are decoded; href() encodes them.
// The landing keys ?q= (a menu search to pre-fill) and ?orderType=pickup|delivery are the pages' (the menu, app Home:
// they apply and drop them); mode app's /{hotel} (Home) sends a ?q= to the menu URL (below).
//
// Mode qr (P4-14; BATCH2-PLAN §3 P4 "Routing in qr mode"; qr.sipocloudpos.com, qr.sipo.nz, the /endpoint/qr-order
// mount). A table is the context: printed codes encode /{hotel}/table/{id}, and that URL stays the table menu's
// canonical URL (D-29: never break that shape). The table keys a URL may carry are t_id › table_id › tableId (REF's
// order; a trimmed, non-empty value).
//   /                                   scan              (hotel null) the generic "Scan the QR code on your table"
//   /{hotel}/table/{tableId}            menu              params.tableId: the entry (the table page checks the table,
//                                                         P4-01). Table keys in its query are dropped (the path wins)
//   /{hotel} | /{hotel}/menu            scan              no table in the URL: the store's scan landing, unless a table
//                                                         context applies (qrGate, below: then the table menu)
//   any of the above with a table key   → /{hotel}/table/{id} (the query kept minus the table keys)
//   /{hotel}/cart | /checkout | /about | the legal slugs
//                                       cart | checkout | about | legal   need a table context (qrGate); a table key in
//                                                         the query → /{hotel}/table/{id}
//   /payment-callback, /{hotel}/payment-callback, /{hotel}/orderStatus, /{hotel}/profile/orders/{id},
//   /{hotel}/orders/{id}, REF's /order-confirmed and /success (→ /orderStatus as above)
//                                       always allowed, exactly as they come (a gateway return may arrive in a fresh tab,
//                                       and P4-08 reads their t_id): never canonicalised, never gated
//   /{hotel}/__ui                       ui-gallery        as in the other modes
//   everything else (account, rewards, booking, bookings, booking/track, wallet, coupons, deals, track, review, the
//   order history, profile, edit-profile, home, stores, notifications, unknown sub-paths, path deep links, P5-01's
//   /{hotel}?book=1 (book dropped))
//                                       → /{hotel} (the scan landing; a table key → /{hotel}/table/{id}), query kept:
//                                       off at a table (D-20), qrGate takes /{hotel} on to the table menu
//   /{hotel}/product|combo/{name}-{id}  the same, with the item as a deep-link query (?type=product|combo&id=…): the table
//                                       menu opens it (P5-01; mode qr is noindex, the table URL stays its canonical)
// href() in mode qr: 'menu' with a tableId → /{hotel}/table/{id}, without one → /{hotel} (qrGate takes it to the table
// menu); 'scan' → /{hotel} (or '/'); a page off at a table → /{hotel}; the rest as in mode online.
//
// Mode app (P4-11; D-03, D-30 (a); BATCH2-PLAN §3 P4 "Per-chain app"): app.sipocloudpos.com/{masterKey} is one app per
// chain (chainedshops: a master store and its branches). The path key is the CHAIN, the branch rides in the query:
// /{masterKey}/{page}?store={branchKey} (the route table above, plus `store`). A route under a store carries
//   route.chain   the path key, decoded (a child key redirects to its master, below)
//   route.hotel   the active BRANCH (what the pages load: OO.useHotel(route.hotel)), or null (none: OO.App shows
//                 Select store first)
//   route.via     how route.hotel was found: 'store' (the URL's ?store=), 'saved' (the branch this device last used in the
//                 chain: branchOf), 'only' (the chain's only listed store), 'path' (the path key itself: the chain isn't
//                 known, or no store has the key), null (none)
// The chains come from B31 (OO.api.getChain → OO.model.normalizeChain): OO.App reads the route's chain and hands it to
// setChain, which remembers it in localStorage, so a later visit resolves at once. "Listed" = in B31's `stores`, the
// stores a customer can order from (a store with online ordering switched off still counts: ChainStoreDirectory); store
// keys compare in any case, and the URL's spelling is kept (OO.store keys the carts by the route's spelling).
//   Chain known       ?store= naming a listed store → it ('store'); else the saved branch, when listed ('saved'); else
//                     the chain's only listed store ('only'; the path's spelling when it is the path key); else none.
//                     A child key in the path → /{masterKey}/{same page}?store={childKey} (?store= instead when it names
//                     a listed store; a child the chain doesn't list resolves as a store-less URL), in the same redirect
//                     as any of the route table's; a ?store= the chain doesn't list, or an empty one, → the same page
//                     without it (and with the branch that then resolves).
//   Not known yet     (never read, a read in flight, a failed read) Batch 1's behaviour, never blocked: ?store= as given
//                     ('store'), else the saved branch ('saved'), else the path key ('path'). OO.App reads the chain and
//                     the route re-resolves when it lands (useRoute follows setChain).
//   No such store     (B31 404) or not live here: the path key ('path'); ?store= and the saved branch are ignored, so the
//                     pages' store-not-found and OO.App's "not available here yet" card apply.
//   The payment pages (payment-callback, order-status: KEEP_QUERY) never wait, validate or redirect for the chain:
//                     route.hotel = ?store= as given, else the path key, and their query keeps `store` as it came. Every
//                     other route's query is without `store` (the router writes it).
// href() in mode app: the path names the chain (opts.chain; else the current chain when the hotel is the current branch
// or in the current chain; else the known chain's master; else the hotel itself) and store={hotel} goes FIRST in the
// query, on every page under the store (D-30's /{masterKey}/{page}?store=…: deep links, order links, Home, Select
// store…), unless it adds nothing: the hotel is the path's own key and the current route found it by 'path' / 'only',
// or its known chain lists it alone, or its chain isn't known (the path names it, as in Batch 1). No hotel (none, or
// opts.hotel null) → the current chain without `store`. A `store` in opts.query is always replaced.
// Root /payment-callback in mode app: OO.App forwards it (paymentReturn) to /{masterKey}/payment-callback?store={branch}
// followed by the query as it came.
//
// Mode landing (WL-3; docs/whitelabel/WHITELABEL-PLAN.md §7.3; the /endpoint/landing mount, boot mode 'landing'): mode
// online's route table and behaviour, except that /{hotel} is the chain's landing page (page 'landing', src/pages/
// landing.jsx) and the menu is /{hotel}/menu (href('menu') writes it). A deep-link query or a ?q= on /{hotel} goes to the
// menu URL (as mode app's Home); an unknown sub-path or an app-only page goes to /{hotel}. OO.mode stays 'online' there
// (glue/assets.jsx: OO.landingMode), so every ordering page behaves exactly as on the online host.
// WL-4 (custom domains, routeBase ''): '/' is the landing page of the domain's chain master (the boot's whitelabel.key,
// which the server takes from the host: domainKey()), and href('landing') for that key writes '/'. The server only
// serves the domain's own chain under /{key}/…; with the kill switch off it answers '/' with a redirect to the menu.
// WL-VEL (the website's pages and legacy redirects; mode landing only — the online, app, qr and kiosk modes are unchanged):
//   /{hotel}/page/{slug}               landing           params.slug: a site page (landing.pages) on the landing mount; the
//                                                         page checks the slug and replaces an unknown one with /{hotel}
//   custom domain /{slug}              landing           params.slug, when the boot's site (whitelabel.site.document) has
//                                                         that page; RESERVED first, then pages, redirects, store keys
//   custom domain /{from…}, mount /{hotel}{from…} (an otherwise unknown sub-path; VEL-FIX: in any letter case)
//                                      landing           params.redirect = the `from` of a landing.redirects entry of the
//                                                         boot's site: the page resolves its `to` and replaces the URL
//                                                         (the query kept without the PII keys: withoutPii, exported)
//   href('landing', { slug }) → /{hotel}/page/{slug} on the mount, /{slug} on the domain's own custom domain.
// Redirects are returned as data (route.redirectTo); OO.App performs them once with go(…, {replace}). A redirecting
// route describes its target: parse(redirectTo) gives the same page, params and query.
//   * Deep links from the current app (onlineorderv3 app/[hotel]/[...deeplink]/page.tsx):
//     '/{hotel}/product&id=123' → menu '?type=product&id=123' (type: product|combo|category|cart).
//     The sub-path is decoded and split on its FIRST '&'. A leading 'type=' is stripped, then the type
//     is lower-cased. Only `id` is read from the rest. Other query params are kept.
//   * Shorthand query deep link (Next DeepLinkHandler): '?category&id=5' → '?type=category&id=5', i.e.
//     with no `type`, the first query KEY naming a type is the type. Applies to menu URLs. In app mode
//     the store landing ('/{hotel}') is Home, so any deep-link query there also goes to the menu URL, and so does a ?q=
//     (P5-01: Home has no menu search).
//   * P5-01 (BATCH2-PLAN §3 P5 "Route compatibility", D-19): REF's '/{hotel}?book=1' → '/{hotel}/booking' (mode qr: off
//     at a table, as /booking); an SEO item path whose segment has no id → the plain menu; a deep-link query on an
//     item path is dropped (the path names the item). An item path itself never redirects in modes online and app.
//     How an open item sheet is named: today's canonical, href('menu', {}, { query: { type, id } })
//     ('/{hotel}?type=product&id=N' online, '/{hotel}/menu?…' app, '/{hotel}/table/{t}?…' qr); href() writes an item
//     path only back from a parsed one's params (round trips, mode app's redirects), never from an id.
//   * REF's confirmation pages, '/{hotel}/order-confirmed' (gateway orders) and '/{hotel}/success' (counter orders),
//     keyed by ?order_id= with display params → '/{hotel}/orderStatus', query kept (the order status page reads it).
//     Without a payment_method the redirect adds REF's default for the path (P3-02: REF's pages default it by path, and
//     the page can't tell the paths apart): 'online' for order-confirmed, 'cash' for success.
//   * Any other unknown sub-path under a store, and app-only pages in online mode, redirect to the
//     store landing '/{hotel}' (query kept). The current app does the same.
//   * Tables are qr-only (P4-14): in modes online and app a table URL that still arrives (the hosts redirect them to
//     the qr host, P4-13) is tolerated: /{hotel}/table/{id} goes to the store's menu, and the table keys (t_id,
//     table_id, tableId) are dropped from every URL but the payment pages' (payment-callback and order-status keep
//     their query as it came), in the same redirect.
// not-found is only for URLs that name no store: a malformed %-escape in the store segment, a path
// outside routeBase, or a RESERVED first segment (decoded), and for the machine routes under a store (MACHINE).
//
// Privacy (Phase 3; B §9 Q13): no URL this router writes carries a contact field. href() drops the PII query keys
// (email, phone, mobile, customer_name, customer_email, customer_phone, address; any case) from opts.query, so link()
// and every redirectTo are free of them too. An incoming URL keeps them in route.query for its page to read (REF's
// booking links carry ?email=, its confirmation pages ?customer_name=); a page that reads one replaces its URL with
// href(), which leaves it out.
//
// Tables (D-20: the extras are off at a table; mode qr only since P4-14). `table` below is a table context
// { id, hotel, … } | null (P4-01's, else OO.store's); it applies to a route when its hotel is route.hotel (any case:
// store keys are case-insensitive) and it has an id.
//   offAtTable(page) → true for the pages a table never gets: account, rewards, booking, bookings, booking-track,
//       wallet, coupons, deals, track, review (REF's table side routes them away), and since P4-14 the order history
//       (orders), profile and the app-only home, stores and notifications.
//   tableGate(route, table, { routeBase, mode }) → in mode qr, where a page off at a table goes: the table menu
//       ('/{hotel}/table/{id}', the query kept minus the table keys) when a table applies (the URL's table key, else
//       `table`), else the store's scan landing ('/{hotel}', the query kept minus the table keys); null when the page
//       stays, for a store-less route, and in modes online and app. Pages ask it whether they are at a table
//       (about, order status, orders).
//   qrGate(route, table, { routeBase, mode }) → where OO.App sends a route in mode qr, or null when it stays: the scan
//       landing with a table that applies → the table menu (query kept); cart, checkout, about and the legal pages
//       without one → '/{hotel}' (the scan landing, query kept); a page off at a table → tableGate's target. The entry
//       (/{hotel}/table/{id}), the payment pages, the dev gallery and every store-less route stay. null in modes online
//       and app and for a route that redirects anyway (route.redirectTo).
//   tableIdOf(query) → the table id a query names (t_id › table_id › tableId: the first trimmed, non-empty value), or
//       null. The payment pages keep their table keys (above); P4-08 restores the table from them.
//
// Chains (mode app; "Mode app" above). The registry: memory, plus localStorage 'oo-chain-v1' { chains: [{ master, keys,
// aliases, at }], branch: { [chain]: { key, at } } } (at most 20 chains and 20 saved branches, the most recent kept;
// storage that is missing or throws means memory only). Every change re-resolves the current route (useRoute).
//   chainOf(key) → null (never read) | { state: 'known', master (key | null), keys (the listed stores, B31's order), at,
//       fresh (read this page load; false: remembered from an earlier one) } | { state: 'unknown' (no store has the key)
//       | 'notLive' (not served here) | 'failed' (to retry), at }   the chain of a store key: its master's, a listed
//       store's, or the key a read was made for (a store the chain doesn't list).
//   setChain(key, chain, now?) → chainOf(key)   the chain read for `key`: OO.model.normalizeChain's Chain (key, master
//       { key }, stores [{ key }]), or { key?, master: key, stores: [key] }.
//   chainFailed(key, state, now?)   the read for `key` failed: 'unknown' (B31 404: forgets what was known for the key),
//       'notLive', else 'failed'. Memory only.
//   branchOf(chain) → key | null   the branch saved for the chain whose path key is `chain`.
//   setBranch(chain, key, now?) → bool   saves the branch (null clears it); true when it changed.
//   paymentReturn(branch, search, { routeBase, fallback }) → URL | null   the root /payment-callback's forward in mode app:
//       '/{masterKey}/payment-callback?store={branch}' + '&' + search (the gateway's query, verbatim) when the registry
//       knows the branch's chain (its master; no master: the branch's own key); else null, or with fallback the
//       branch's own path.
//
// NGINX: `try_files $uri $uri/ /index.php?$query_string` serves existing public/ files and dirs FIRST.
// A client route must therefore NEVER start with a public/ directory: css, fonts, images, sipo, stores,
// uploads, wc, e-suite, endpoint-admin, licensing, warehouse, online-order, barcodes (nor robots.txt, favicon.*,
// 100, …). Such a segment can't be a store either: '/css/missing.css' reaches Laravel, but redirecting
// it to '/css' would hit nginx's directory (403). parse() treats them, plus api/endpoint/payment-callback and (P5-01)
// the root machine routes and REF's reserved root names (robots.txt, sitemap.xml, manifest.webmanifest, favicon.ico,
// icon, opengraph-image, twitter-image, _next) and (P5-06) __tags, the tag frame's document, as RESERVED → not-found.
// Keep app-level routes (stores, …) BELOW /{hotel}, never at the root. OnlineOrderSpaController::RESERVED_SEGMENTS
// mirrors RESERVED.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});

  var EVENT = 'oo:route';
  var DEEP_LINK_TYPES = ['product', 'combo', 'category', 'cart'];
  var NAMED = {
    'menu': 'menu',
    'cart': 'cart',
    'checkout': 'checkout',
    'orderStatus': 'order-status',
    'orders': 'orders',
    'profile': 'profile',
    'profile/orders': 'orders',
    'payment-callback': 'payment-callback',
    'account': 'account',
    'rewards': 'rewards',
    'booking': 'booking',
    'wallet': 'wallet',
    'track': 'track',
    'about': 'about',
    'coupons': 'coupons',
    'deals': 'deals',
    'bookings': 'bookings',
    'booking/track': 'booking-track',
    '__ui': 'ui-gallery',
  };
  // The routes with an id in the path: their segments, ':name' where the (decoded) id goes as params.name.
  var WITH_ID = [
    { path: ['table', ':tableId'], page: 'menu' },
    { path: ['profile', 'orders', ':orderId'], page: 'order-status' },
    { path: ['orders', ':orderId'], page: 'order-status' },
    { path: ['orders', ':orderId', 'review'], page: 'review' },
    { path: ['track', ':orderId'], page: 'track' },
    { path: ['bookings', ':reservationId'], page: 'bookings' },
    { path: ['booking', 'track', ':reservationId'], page: 'booking-track' },
  ];
  var APP_ONLY = { home: 1, stores: 1, notifications: 1 };
  // REF's order confirmation pages → order-status ('/{hotel}/orderStatus'), each with REF's default payment_method.
  var TO_STATUS = { 'order-confirmed': 'online', 'success': 'cash' };
  // D-20: the pages a table customer never gets (see "Tables" above; P4-14 adds the order history, profile and the
  // app-only pages: mode qr's "everything else").
  var TABLE_OFF = {
    account: 1, rewards: 1, booking: 1, bookings: 1, 'booking-track': 1, wallet: 1, coupons: 1, deals: 1, track: 1, review: 1,
    orders: 1, profile: 1, home: 1, stores: 1, notifications: 1,
  };
  // The table keys a URL may carry, in REF's order (useTableSideHref: t_id › table_id › tableId).
  var TABLE_KEYS = ['t_id', 'table_id', 'tableId'];
  // The payment pages: their query reaches the page exactly as it came, in every mode (a gateway return may arrive in a
  // fresh tab; P4-08 reads its t_id). Mode qr never gates them either.
  var KEEP_QUERY = { 'payment-callback': 1, 'order-status': 1 };
  // Mode qr: the pages that need a table context (qrGate; else the scan landing).
  var NEEDS_TABLE = { cart: 1, checkout: 1, about: 1, legal: 1 };
  // The contact fields href() never writes into a URL (see "Privacy" above). Compared lower-cased.
  var PII = { email: 1, phone: 1, mobile: 1, customer_name: 1, customer_email: 1, customer_phone: 1, address: 1 };
  var LEGAL_KIND = { 'terms-and-conditions': 'terms', 'privacy-policy': 'privacy', 'allergy-information': 'allergy' };
  var LEGAL_SLUG = { terms: 'terms-and-conditions', privacy: 'privacy-policy', allergy: 'allergy-information' };
  var RESERVED = {
    'api': 1, 'endpoint': 1, 'payment-callback': 1, 'css': 1, 'fonts': 1, 'images': 1, 'sipo': 1,
    'stores': 1, 'uploads': 1, 'wc': 1, 'e-suite': 1, 'endpoint-admin': 1, 'licensing': 1,
    'warehouse': 1, 'online-order': 1,
    'barcodes': 1, // public/barcodes: created at runtime on Live, gitignored (P0-AUDIT §5)
    // P5-01: the machine routes at the root (B21: /sitemap.xml, /robots.txt) and REF's other reserved root names (its
    // RESERVED_PATHS, src/shared/lib/seo.ts: no store could have one of them on REF either).
    'robots.txt': 1, 'sitemap.xml': 1, 'manifest.webmanifest': 1, 'favicon.ico': 1, 'icon': 1, 'opengraph-image': 1,
    'twitter-image': 1, '_next': 1,
    // P5-06: the sandboxed tag frame's document, which the backend serves at {routeBase}/__tags (src/lib/analytics.jsx).
    '__tags': 1,
    // WL-1: the white-label studio mount (/endpoint/whitelabel; whitelabel.sipocloudpos.com is its own host).
    'whitelabel': 1,
    // WL-3: the white-label landing mount (/endpoint/landing).
    'landing': 1,
  };
  // P5-01: the machine routes under a store (B21 / P5-03…05: /{hotel}/manifest.webmanifest, /icon, /opengraph-image,
  // /twitter-image). The backend serves them; one that reaches the SPA names no page (not-found, store-less).
  var MACHINE = { 'manifest.webmanifest': 1, 'icon': 1, 'opengraph-image': 1, 'twitter-image': 1 };
  // P5-01: REF's SEO item paths (D-19): /{hotel}/product/{name}-{id}, /{hotel}/combo/{name}-{id}. The id is the trailing
  // digits of the (decoded) last segment after a '-', or the whole segment (REF parseMenuItemRouteSegment: 'item-123',
  // '123'); the name part is free.
  var ITEM_KINDS = { product: 1, combo: 1 };
  var ITEM_ID = /(?:^|-)(\d+)$/;

  // Own-property lookup: a store named 'constructor' or 'toString' must not hit Object.prototype.
  function has(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }
  function boot() { return window.__SIPO_BOOT || {}; }
  // WL-4: a custom domain (OnlineOrderSpaController@domain: boot mode 'landing' at routeBase '', which the mount never
  // has) → the domain's chain master from the boot's whitelabel.key (the server takes it from the HOST); null anywhere
  // else, and with the kill switch off (the boot is then exactly { enabled: false } and the server answers '/' itself).
  function domainKey(base, mode) {
    if (mode !== 'landing' || base) return null;
    var w = boot().whitelabel;
    return w && typeof w === 'object' && w.enabled === true && typeof w.key === 'string' && w.key !== '' ? w.key : null;
  }
  // WL-VEL: the boot's published site for `key` (mode landing): its landing document's pages and redirects, or null.
  var PAGE_SLUG = /^[a-z][a-z0-9-]{1,31}$/;
  function bootLanding(key) {
    var w = boot().whitelabel;
    if (!w || typeof w !== 'object' || w.enabled !== true || typeof w.key !== 'string' || !same(w.key, key)) return null;
    var d = w.site && typeof w.site === 'object' ? w.site.document : null;
    return d && typeof d === 'object' && d.landing && typeof d.landing === 'object' ? d.landing : null;
  }
  function sitePageIn(key, slug) {
    var l = bootLanding(key);
    return !!l && PAGE_SLUG.test(slug) && slug !== 'page' && Array.isArray(l.pages) && l.pages.some(function (p) { return p && p.slug === slug; });
  }
  function redirectIn(key, path) {
    var l = bootLanding(key);
    var p = String(path).toLowerCase(); // VEL-FIX: any letter case (`from` is lower case), as the server matches it
    var hit = l && Array.isArray(l.redirects) ? l.redirects.filter(function (r) { return r && typeof r.from === 'string' && r.from !== '/' && r.from === p; })[0] : null;
    return hit ? hit.from : null;
  }
  // WL-3: 'landing' is mode online's route table with /{hotel} as the chain's landing page ("Mode landing" above).
  function normMode(mode) { return mode === 'app' || mode === 'qr' || mode === 'landing' || mode === 'kiosk' ? mode : 'online'; }
  // '' or '/a/b': path-only (scheme + host stripped), leading slash, no trailing slash.
  function normBase(base) {
    var b = String(base == null ? '' : base).replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '').replace(/\/+$/, '');
    if (b && b.charAt(0) !== '/') b = '/' + b;
    return b;
  }
  function safeDecode(s) { try { return decodeURIComponent(s); } catch (e) { return null; } }
  function decodeOr(s) { var d = safeDecode(s); return d === null ? s : d; }
  function enc(s) { return encodeURIComponent(String(s)); }
  function given(v) { return v !== undefined && v !== null && v !== ''; }
  function isDeepLinkType(t) { return DEEP_LINK_TYPES.indexOf(String(t).toLowerCase().trim()) >= 0; }
  // A copy of query without the keys keep() refuses.
  function filterKeys(query, keep) {
    var out = {};
    Object.keys(query).forEach(function (key) { if (keep(key)) out[key] = query[key]; });
    return out;
  }
  // REF's legacy booking link (?book=1 on the store landing).
  function bookIn(query) { return has(query, 'book') && String(query.book).trim() === '1'; }
  // A menu search to pre-fill (?q=: not blank).
  function searchIn(query) { return has(query, 'q') && query.q !== null && query.q !== undefined && String(query.q).trim() !== ''; }
  // A copy of query without the PII keys (null / undefined pass through).
  function withoutPii(query) {
    if (!query) return query;
    var out = {};
    Object.keys(query).forEach(function (key) {
      if (!has(PII, key.toLowerCase())) out[key] = query[key];
    });
    return out;
  }
  // The table id a query names (the first table key with a trimmed, non-empty value), or null.
  function tableIdIn(query) {
    if (!query) return null;
    for (var i = 0; i < TABLE_KEYS.length; i++) {
      if (!has(query, TABLE_KEYS[i])) continue;
      var v = query[TABLE_KEYS[i]] == null ? '' : String(query[TABLE_KEYS[i]]).trim();
      if (v) return v;
    }
    return null;
  }
  // query without the table keys: the SAME object when it has none (callers compare), else a copy.
  function withoutTable(query) {
    if (!query || !TABLE_KEYS.some(function (k) { return has(query, k); })) return query;
    var out = {};
    Object.keys(query).forEach(function (key) { if (TABLE_KEYS.indexOf(key) < 0) out[key] = query[key]; });
    return out;
  }
  // The table context when it applies to this store (see "Tables" above), else null.
  function tableFor(hotel, table) {
    if (!table || typeof table !== 'object' || !given(hotel) || !given(table.hotel)) return null;
    if (table.id === undefined || table.id === null || String(table.id).trim() === '') return null;
    return String(table.hotel).toLowerCase() === String(hotel).toLowerCase() ? table : null;
  }

  // ---- mode app: the branch in the query (see "Mode app" above) ----------------------------------------------------
  // Store keys compare in any case.
  function same(a, b) { return given(a) && given(b) && String(a).toLowerCase() === String(b).toLowerCase(); }
  // The branch a query names: its `store`, trimmed; null when absent or empty.
  function storeIn(query) {
    if (!query || !has(query, 'store') || query.store === null || query.store === undefined) return null;
    var v = String(query.store).trim();
    return v || null;
  }
  // query without `store`: the SAME object when it has none (callers compare), else a copy.
  function withoutStore(query) {
    if (!query || !has(query, 'store')) return query;
    var out = {};
    Object.keys(query).forEach(function (key) { if (key !== 'store') out[key] = query[key]; });
    return out;
  }
  // query with store = branch FIRST (D-30's /{masterKey}/{page}?store=…), its own `store` dropped; no branch: without it.
  function withStore(branch, query) {
    var rest = withoutStore(query);
    if (!given(branch)) return rest;
    var out = { store: String(branch) };
    if (rest) Object.keys(rest).forEach(function (key) { out[key] = rest[key]; });
    return out;
  }

  // The WITH_ID route the segments after the store match → { page, params }, or null.
  function withId(rest) {
    for (var i = 0; i < WITH_ID.length; i++) {
      var path = WITH_ID[i].path;
      if (path.length !== rest.length) continue;
      var params = {};
      for (var j = 0; j < path.length && params; j++) {
        if (path[j].charAt(0) === ':') params[path[j].slice(1)] = decodeOr(rest[j]);
        else if (path[j] !== rest[j]) params = null;
      }
      if (params) return { page: WITH_ID[i].page, params: params };
    }
    return null;
  }

  // ---- query strings ----------------------------------------------------------------------------
  // Form semantics ('+' is a space). A bare key ('?category') maps to ''. The last duplicate wins.
  // Malformed escapes are kept raw.
  function parseQuery(search) {
    var out = {};
    var s = String(search == null ? '' : search);
    if (s.charAt(0) === '?') s = s.slice(1);
    var hashAt = s.indexOf('#');
    if (hashAt >= 0) s = s.slice(0, hashAt);
    if (!s) return out;
    var parts = s.split('&');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (!part) continue;
      var eq = part.indexOf('=');
      var key = decodeOr((eq >= 0 ? part.slice(0, eq) : part).replace(/\+/g, ' '));
      if (!key || key === '__proto__') continue;
      out[key] = eq >= 0 ? decodeOr(part.slice(eq + 1).replace(/\+/g, ' ')) : '';
    }
    return out;
  }

  // Inverse of parseQuery. null/undefined values are dropped and '' becomes a bare key. Every other
  // value is String()-ed, so { directLogin: true } → 'directLogin=true'.
  function buildQuery(query) {
    if (!query) return '';
    var parts = [];
    Object.keys(query).forEach(function (key) {
      var v = query[key];
      if (v === undefined || v === null) return;
      parts.push(v === '' ? enc(key) : enc(key) + '=' + enc(v));
    });
    return parts.length ? '?' + parts.join('&') : '';
  }

  // ---- deep links ---------------------------------------------------------------------------------
  // Canonical deep-link query: type, then id, then every other param in its original order. Keys that
  // themselves name a type (shorthand leftovers) are dropped.
  function deepLinkQuery(type, id, query) {
    var q = { type: type };
    if (id !== undefined && id !== null && id !== '') q.id = id;
    Object.keys(query).forEach(function (key) {
      if (key === 'type' || key === 'id' || isDeepLinkType(key)) return;
      q[key] = query[key];
    });
    return q;
  }

  // '/{hotel}/product&id=123' (sub = 'product&id=123') → { type: 'product', id: '123' }, or null.
  function pathDeepLink(sub) {
    var raw = decodeOr(sub);
    var amp = raw.indexOf('&');
    var type = (amp >= 0 ? raw.slice(0, amp) : raw).replace(/^type=/i, '').toLowerCase().trim();
    if (DEEP_LINK_TYPES.indexOf(type) < 0) return null;
    return { type: type, id: amp >= 0 ? parseQuery(raw.slice(amp + 1)).id : undefined };
  }

  // A deep link carried in the query: '?type=product&id=1' (canonical) or the shorthand
  // '?category&id=5'. Returns { query: canonicalQuery, shorthand } or null.
  function queryDeepLink(query) {
    var keys = Object.keys(query);
    if (has(query, 'type') && String(query.type).trim() !== '') {
      return isDeepLinkType(query.type) ? { query: query, shorthand: false } : null;
    }
    for (var i = 0; i < keys.length; i++) {
      if (isDeepLinkType(keys[i])) {
        return { query: deepLinkQuery(keys[i].toLowerCase().trim(), query.id, query), shorthand: true };
      }
    }
    return null;
  }
  // A menu-like URL's query with a shorthand deep link canonicalised (else as it is).
  function menuQuery(query) {
    var qd = queryDeepLink(query);
    return qd && qd.shorthand ? qd.query : query;
  }
  // query without its deep link (type, id and the keys naming a type), when it carries one; else the SAME object.
  function withoutDeepLink(query) {
    if (!queryDeepLink(query)) return query;
    return filterKeys(query, function (key) { return key !== 'type' && key !== 'id' && !isDeepLinkType(key); });
  }

  // ---- item paths (P5-01; see ITEM_KINDS and the route table) ---------------------------------------------------------
  // The segments after the store, '/product|combo/{seg}' → { item, itemId, itemSlug } (itemSlug = the segment decoded,
  // itemId = its trailing digits); null for such a path whose segment has no id; undefined for any other path.
  function itemPath(rest) {
    if (rest.length !== 2 || !has(ITEM_KINDS, rest[0])) return undefined;
    var slug = decodeOr(rest[1]);
    var m = ITEM_ID.exec(slug);
    return m ? { item: rest[0], itemId: m[1], itemSlug: slug } : null;
  }
  // Whether params name an item path href() can write back (its kind, and a slug with an id: as itemPath() gives them).
  function itemOf(params) {
    return has(ITEM_KINDS, params.item) && typeof params.itemSlug === 'string' && ITEM_ID.test(params.itemSlug);
  }

  // ---- chains (mode app; see "Mode app" and "Chains" above) ----------------------------------------------------------
  var CHAIN_KEY = 'oo-chain-v1';
  var MAX_CHAINS = 20;   // remembered chains, and saved branches: the most recent kept
  var MAX_STORES = 500;  // listed stores kept per chain
  var MAX_ALIASES = 20;  // keys read for a chain that it doesn't list (a hidden child, another spelling)
  var KEY_MAX = 255;     // users.online_key
  var version = 0;       // bumped by every registry change: useRoute's snapshot, so the route re-resolves
  // reg = { chains: [Entry], index: { [lower-cased key]: Entry }, branch: { [lower-cased chain]: { key, at } },
  //   miss: { [lower-cased key]: { state, at } } } (maps without a prototype: a store may be named 'constructor').
  // Entry = { id, master (key | null), keys (listed), aliases, at, fresh }. Read from storage on first use.
  var reg = null;

  function lower(s) { return String(s).toLowerCase(); }
  function dict() { return Object.create(null); }
  function stamp(now) { return typeof now === 'number' && isFinite(now) ? now : Date.now(); }
  function cleanKey(v) {
    if (typeof v !== 'string' && typeof v !== 'number') return null;
    var s = String(v).trim();
    return s && s.length <= KEY_MAX ? s : null;
  }
  // A key, or the `key` of a { key } (a ChainStore, a Chain's master).
  function keyOf(v) { return v && typeof v === 'object' ? cleanKey(v.key) : cleanKey(v); }
  // The distinct keys of a list (any case: the first spelling wins), at most `max`.
  function distinctKeys(list, max) {
    var out = [], seen = dict();
    (Array.isArray(list) ? list : []).forEach(function (v) {
      var k = keyOf(v);
      if (!k || seen[lower(k)] || out.length >= max) return;
      seen[lower(k)] = true;
      out.push(k);
    });
    return out;
  }
  function entry(master, keys, aliases, at, fresh) {
    var id = master || keys[0] || aliases[0];
    return id ? { id: lower(id), master: master, keys: keys, aliases: aliases, at: at, fresh: fresh } : null;
  }
  function storage() { try { return window.localStorage || null; } catch (e) { return null; } }

  // Every key → its chain; the newest chain wins a key two of them name.
  function reindex() {
    var idx = dict();
    reg.chains.slice().sort(function (a, b) { return a.at - b.at; }).forEach(function (e) {
      [e.master].concat(e.aliases, e.keys).forEach(function (k) { if (k) idx[lower(k)] = e; });
    });
    reg.index = idx;
  }
  function trim() {
    if (reg.chains.length > MAX_CHAINS) reg.chains = reg.chains.slice().sort(function (a, b) { return b.at - a.at; }).slice(0, MAX_CHAINS);
    var ks = Object.keys(reg.branch);
    if (ks.length > MAX_CHAINS) {
      ks.sort(function (a, b) { return reg.branch[b].at - reg.branch[a].at; }).slice(MAX_CHAINS).forEach(function (k) { delete reg.branch[k]; });
    }
  }
  function registry() {
    if (reg) return reg;
    reg = { chains: [], index: dict(), branch: dict(), miss: dict() };
    var s = storage(), data = null;
    try { data = s ? JSON.parse(s.getItem(CHAIN_KEY) || 'null') : null; } catch (e) { data = null; }
    if (data && typeof data === 'object') {
      (Array.isArray(data.chains) ? data.chains : []).forEach(function (c) {
        if (!c || typeof c !== 'object') return;
        var at = typeof c.at === 'number' && isFinite(c.at) ? c.at : 0;
        var e = entry(cleanKey(c.master), distinctKeys(c.keys, MAX_STORES), distinctKeys(c.aliases, MAX_ALIASES), at, false);
        if (e) reg.chains.push(e);
      });
      var b = data.branch && typeof data.branch === 'object' && !Array.isArray(data.branch) ? data.branch : {};
      Object.keys(b).forEach(function (k) {
        var v = b[k], chain = cleanKey(k), key = v && typeof v === 'object' ? cleanKey(v.key) : null;
        if (chain && key) reg.branch[lower(chain)] = { key: key, at: typeof v.at === 'number' && isFinite(v.at) ? v.at : 0 };
      });
      trim();
    }
    reindex();
    return reg;
  }
  function save() {
    var s = storage();
    if (!s) return;
    var branch = {};
    Object.keys(reg.branch).forEach(function (k) { branch[k] = reg.branch[k]; });
    var chains = reg.chains.map(function (e) { return { master: e.master, keys: e.keys, aliases: e.aliases, at: e.at }; });
    try { s.setItem(CHAIN_KEY, JSON.stringify({ chains: chains, branch: branch })); } catch (e) { /* full or blocked: memory only */ }
  }
  function changed() { version += 1; emit(); }

  // The chain the registry knows for a key, or null.
  function lookup(key) {
    var k = cleanKey(key);
    return k ? registry().index[lower(k)] || null : null;
  }
  // 'known' | 'unknown' | 'notLive' | 'failed' | null: a key no store has, or one not served here, outranks what is
  // remembered for it.
  function stateOf(key) {
    var k = cleanKey(key);
    if (!k) return null;
    var m = registry().miss[lower(k)];
    if (m && m.state !== 'failed') return m.state;
    return lookup(k) ? 'known' : m ? 'failed' : null;
  }
  // The listed store of chain e that `key` names (any case), or null.
  function listed(e, key) {
    for (var i = 0; i < e.keys.length; i++) if (same(e.keys[i], key)) return e.keys[i];
    return null;
  }
  function savedBranch(chain) {
    var k = cleanKey(chain);
    var b = k ? registry().branch[lower(k)] : null;
    return b ? b.key : null;
  }

  function chainOf(key) {
    var st = stateOf(key);
    if (!st) return null;
    if (st === 'known') {
      var e = lookup(key);
      return { state: 'known', master: e.master, keys: e.keys.slice(), at: e.at, fresh: e.fresh };
    }
    return { state: st, at: registry().miss[lower(cleanKey(key))].at };
  }
  function setChain(key, chain, now) {
    var k = cleanKey(key);
    if (!k || !chain || typeof chain !== 'object') return null;
    var R = registry();
    var master = keyOf(chain.master);
    var keys = distinctKeys(chain.stores, MAX_STORES);
    var e = entry(master, keys, [], stamp(now), true);
    if (!e) e = entry(null, [], [k], stamp(now), true);
    var prev = null;
    R.chains = R.chains.filter(function (x) { if (x.id === e.id) { prev = x; return false; } return true; });
    // The keys read for it that it doesn't list (the requested key, as asked and as the server spells it) stay found.
    e.aliases = distinctKeys((prev ? prev.aliases : []).concat(e.aliases, [k, keyOf(chain.key)]), MAX_ALIASES + 2).filter(function (a) {
      return !same(a, master) && !listed(e, a);
    }).slice(0, MAX_ALIASES);
    R.chains.push(e);
    [k].concat(keys, e.aliases, master ? [master] : []).forEach(function (x) { delete R.miss[lower(x)]; });
    trim();
    reindex();
    save();
    changed();
    return chainOf(k);
  }
  function chainFailed(key, state, now) {
    var k = cleanKey(key);
    if (!k) return;
    var R = registry();
    var st = state === 'unknown' || state === 'notLive' ? state : 'failed';
    var e = st === 'unknown' ? lookup(k) : null;
    if (e) { // no store has the key (any more): forget it
      if (same(e.master, k)) R.chains = R.chains.filter(function (x) { return x !== e; });
      else {
        e.keys = e.keys.filter(function (x) { return !same(x, k); });
        e.aliases = e.aliases.filter(function (x) { return !same(x, k); });
      }
      reindex();
      save();
    }
    R.miss[lower(k)] = { state: st, at: stamp(now) };
    changed();
  }
  function branchOf(chain) { return savedBranch(chain); }
  function setBranch(chain, key, now) {
    var c = cleanKey(chain);
    if (!c) return false;
    var R = registry();
    var k = key === null || key === undefined ? null : cleanKey(key);
    var cur = R.branch[lower(c)];
    if ((cur ? cur.key : null) === k) return false;
    if (k) R.branch[lower(c)] = { key: k, at: stamp(now) };
    else delete R.branch[lower(c)];
    trim();
    save();
    changed();
    return true;
  }

  // Mode app: the chain and branch of a store route whose path key is `key` (see "Mode app" above) → { chain, hotel,
  // via, redirect } (redirect: the URL must change: a child key, an unlisted or empty ?store=).
  function appResolve(key, query) {
    var store = storeIn(query);
    var blank = !!query && has(query, 'store') && !store;
    var st = stateOf(key);
    if (st === 'unknown' || st === 'notLive') return { chain: key, hotel: key, via: 'path', redirect: blank };
    var e = st === 'known' ? lookup(key) : null;
    if (!e) { // not known yet: Batch 1's guess
      if (store) return { chain: key, hotel: store, via: 'store', redirect: false };
      var guess = savedBranch(key);
      return guess ? { chain: key, hotel: guess, via: 'saved', redirect: blank } : { chain: key, hotel: key, via: 'path', redirect: blank };
    }
    var chain = e.master && !same(e.master, key) ? e.master : key;
    var moved = chain !== key;
    if (store && listed(e, store)) return { chain: chain, hotel: store, via: 'store', redirect: moved };
    if (moved && listed(e, key)) return { chain: chain, hotel: key, via: 'store', redirect: true };
    var off = moved || !!store || blank;
    var saved = savedBranch(chain);
    if (saved && listed(e, saved)) return { chain: chain, hotel: saved, via: 'saved', redirect: off };
    if (e.keys.length === 1) return { chain: chain, hotel: same(e.keys[0], chain) ? chain : e.keys[0], via: 'only', redirect: off };
    return { chain: chain, hotel: null, via: null, redirect: off };
  }
  // Whether a URL for branch `r.hotel` under chain path `r.chain` must carry store= (see href() in "Mode app").
  function writesStore(r) {
    return given(r.hotel) && (r.via === 'store' || r.via === 'saved' || !same(r.hotel, r.chain));
  }

  // ---- parse ----------------------------------------------------------------------------------------
  function result(hotel, page, params, query, redirectTo) {
    var r = { hotel: hotel, page: page, params: params || {}, query: query };
    if (redirectTo) r.redirectTo = redirectTo;
    return r;
  }
  // Mode app: a store route with its chain (A = appResolve's). A redirecting route describes its target, as parse() reads
  // that URL: store= written → via 'store'; a payment page's query keeps it, and without it its branch is the path key.
  function appResult(A, page, params, query, redirectTo) {
    var w = !!redirectTo && writesStore(A);
    var r;
    if (redirectTo && has(KEEP_QUERY, page)) {
      r = { hotel: w ? A.hotel : A.chain, page: page, params: params || {}, query: w ? withStore(A.hotel, query) : query, chain: A.chain, via: w ? 'store' : 'path' };
    } else {
      r = { hotel: A.hotel, page: page, params: params || {}, query: query, chain: A.chain, via: w ? 'store' : A.via };
    }
    if (redirectTo) r.redirectTo = redirectTo;
    return r;
  }
  // Mode app, a payment page: never validated or redirected for the chain (see "Mode app" above); its query as it came.
  function payResult(key, page, params, query) {
    var store = storeIn(query);
    return { hotel: store || key, page: page, params: params || {}, query: query, chain: key, via: store ? 'store' : 'path' };
  }

  // Zeta's online presentation has a home, store directory and hosted pages. This only extends its URLs;
  // OO.mode stays online so pricing, coupons and payment payloads keep their web rules.
  function onlineZeta(hotel, mode) {
    var W = OO.whitelabel;
    return mode === 'online' && W && W.surface() === 'online' && W.skin(hotel, 'menu') === 'zeta';
  }

  function parse(pathname, search, routeBase, mode) {
    mode = normMode(mode);
    var base = normBase(routeBase);
    var path = String(pathname == null || pathname === '' ? '/' : pathname);
    var qAt = path.indexOf('?');
    if (qAt >= 0) { // tolerate a full href passed as pathname
      if (search == null || search === '') search = path.slice(qAt);
      path = path.slice(0, qAt);
    }
    var query = parseQuery(search);

    if (base) {
      if (path === base || path.indexOf(base + '/') === 0) path = path.slice(base.length);
      else return result(null, 'not-found', {}, query); // outside the mount
    }
    var segs = path.split('/').filter(Boolean); // tolerates trailing and doubled slashes
    var qr = mode === 'qr';
    if (!segs.length) {
      var dk = domainKey(base, mode); // WL-4: a custom domain's root is its chain master's landing page
      if (dk) return result(dk, 'landing', {}, query);
      return result(null, qr ? 'scan' : 'find-store', {}, query);
    }
    // The store key is decoded BEFORE it is matched (P5-01, gap row 80; REF A-Δ1: '/Benny%27s/menu' names "Benny's",
    // '/%61pi' is '/api'), as the backend reads it (tableRedirect).
    var hotel = safeDecode(segs[0]);
    if (hotel === null) return result(null, 'not-found', {}, query); // malformed %-escape
    if (segs.length === 1 && hotel === 'payment-callback') return result(null, 'payment-callback', {}, query);
    // A brand's store directory is a website page. Only a resolved custom domain may use this reserved root path.
    var storesMaster = domainKey(base, mode);
    if (storesMaster && segs.length === 1 && hotel === 'stores') return result(storesMaster, 'landing', { stores: true }, query);
    if (has(RESERVED, hotel)) return result(null, 'not-found', {}, query);
    // WL-VEL: a custom domain's own site page (/{slug}) or legacy redirect, before the first segment names a store.
    var dk2 = mode === 'landing' ? domainKey(base, mode) : null;
    if (dk2) {
      if (segs.length === 1 && sitePageIn(dk2, hotel)) return result(dk2, 'landing', { slug: hotel }, query);
      var dr = redirectIn(dk2, '/' + segs.join('/'));
      if (dr) return result(dk2, 'landing', { redirect: dr }, query);
    }
    var rest = segs.slice(1);
    var sub = rest.join('/');
    var appPages = mode === 'app' || onlineZeta(hotel, mode);
    if (rest.length === 1 && has(MACHINE, rest[0])) return result(null, 'not-found', {}, query); // a machine route (MACHINE)

    // Mode app (P4-11): the path key is the chain, the branch comes from ?store= ("Mode app" above). The payment pages
    // take the query as it came (raw); every other route reads it without `store`, which the router writes.
    var raw = query;
    var A = mode === 'app' ? appResolve(hotel, query) : null;
    if (A) query = withoutStore(query);

    var landing = appPages ? 'home' : qr ? 'scan' : mode === 'landing' ? 'landing' : 'menu';
    var to = function (page, params, q) {
      if (A) return build(page, params, { segment: A.chain, branch: A.hotel, store: writesStore(A), base: base, mode: mode, query: q });
      return href(page, params, { hotel: hotel, routeBase: base, mode: mode, query: q });
    };
    // A redirect: the target route (its query as href() writes it: without the PII keys) and its href.
    var redirect = function (page, params, q) {
      if (A) return appResult(A, page, params, withoutPii(q), to(page, params, q));
      return result(hotel, page, params, withoutPii(q), to(page, params, q));
    };
    // To the store for a URL that names no page here (an unknown sub-path, an app-only page off the app host, a path
    // deep link): modes online / app → `page` without the table keys (tables are qr-only); mode qr → the table menu
    // when the query names a table, else the scan landing.
    var land = function (page, q) {
      if (!qr) return redirect(page, {}, withoutTable(q));
      var tid = tableIdIn(q);
      var mq = menuQuery(withoutTable(q));
      return tid !== null ? redirect('menu', { tableId: tid }, mq) : redirect('scan', {}, mq);
    };
    var idRoute = withId(rest);
    var item = itemPath(rest);
    var page, params = {};

    if (sub === '') {
      // REF's legacy booking link (?book=1 on the store landing: page.tsx) → the booking page, the query kept without
      // `book`; mode qr: booking is off at a table (D-20), so the table entry or the scan landing, as /booking goes.
      if (bookIn(query)) return land('booking', filterKeys(query, function (key) { return key !== 'book'; }));
      page = landing;
    }
    else if (mode === 'landing' && sub === 'stores') { page = 'landing'; params = { stores: true }; }
    else if ((mode === 'landing' || appPages) && rest.length === 2 && rest[0] === 'page' && PAGE_SLUG.test(rest[1]) && rest[1] !== 'page') { page = appPages ? 'app-content' : 'landing'; params = { slug: rest[1] }; }
    else if (appPages && sub === 'settings') page = 'settings';
    else if (appPages && sub === 'addresses') page = 'zeta-addresses';
    else if (appPages && sub === 'favourites') { page = 'menu'; query.saved = 'true'; }
    else if (appPages && sub === 'offers') page = 'deals';
    else if (appPages && sub === 'orders/track') page = 'track';
    else if (appPages && rest.length === 1 && ['catering','franchise','help','contact','allergen'].indexOf(sub)>=0) { page = 'app-content'; params = {slug:sub}; }
    else if (has(NAMED, sub)) page = qr && NAMED[sub] === 'menu' ? 'scan' : NAMED[sub];
    else if (sub === 'edit-profile') { page = 'profile'; params = { edit: true }; }
    else if (has(LEGAL_KIND, sub)) { page = 'legal'; params = { kind: LEGAL_KIND[sub] }; }
    else if (idRoute) { page = idRoute.page; params = idRoute.params; }
    else if (item !== undefined) {
      // An SEO item path (D-19): the menu with that item (params { item, itemId, itemSlug }), kept as it is in modes
      // online and app. No id → the plain menu. Mode qr → the table entry or the scan landing with the item as a
      // deep-link query (the table URL is the canonical there), as a path deep link goes.
      if (!item || qr) return land('menu', item ? deepLinkQuery(item.item, item.itemId, query) : query);
      page = 'menu';
      params = item;
    }
    else if (rest.length === 1 && has(TO_STATUS, sub)) {
      return redirect('order-status', {}, given(query.payment_method) ? query : Object.assign({}, query, { payment_method: TO_STATUS[sub] }));
    }
    else if (rest.length === 1 && has(APP_ONLY, sub)) {
      var ar = mode === 'landing' ? redirectIn(hotel, '/' + sub) : null; // WL-VEL
      if (ar) return result(hotel, 'landing', { redirect: ar }, query);
      if (!appPages) return land(landing, query);
      page = sub;
    } else {
      var mr = mode === 'landing' ? redirectIn(hotel, '/' + rest.join('/')) : null; // WL-VEL
      if (mr) return result(hotel, 'landing', { redirect: mr }, query);
      var dl = pathDeepLink(sub);
      if (dl) return land('menu', deepLinkQuery(dl.type, dl.id, query));
      return land(landing, query);
    }

    // REF's unified lookup in booking mode ('/{hotel}/track?mode=booking') is the booking lookup (query kept).
    if (page === 'track' && !given(params.orderId) && String(query.mode || '').toLowerCase().trim() === 'booking') {
      page = 'booking-track';
    }

    // The payment pages take the query as it came, in every mode (KEEP_QUERY); so does the dev gallery in mode qr.
    if (A && has(KEEP_QUERY, page)) return payResult(hotel, page, params, raw);
    if (has(KEEP_QUERY, page) || (qr && page === 'ui-gallery')) return result(hotel, page, params, query);

    if (qr) { // see "Mode qr" above
      var tq = menuQuery(withoutTable(query)); // the table keys dropped, a shorthand deep link canonicalised
      if (page === 'menu') return tq === query ? result(hotel, page, params, query) : redirect('menu', params, tq); // the entry
      var tid = tableIdIn(query);
      if (tid !== null) return redirect('menu', { tableId: tid }, tq);
      if (page === 'scan') return tq === query ? result(hotel, page, params, query) : redirect('scan', {}, tq);
      if (!has(NEEDS_TABLE, page)) return redirect('scan', {}, tq); // off at a table (D-20); qrGate goes on to the table
      var nq = withoutTable(query);
      return nq === query ? result(hotel, page, params, query) : redirect(page, params, nq);
    }

    // Modes online and app: tables are qr-only (P4-14). A table URL goes to the store's menu, and the table keys are
    // dropped, in the same redirect as the deep-link canonicalisation below.
    var tabled = page === 'menu' && given(params.tableId);
    if (tabled) params = {};
    var q = withoutTable(query);
    // An item path names its item: a deep-link query on it is dropped (the path wins), in the same redirect.
    if (item) q = withoutDeepLink(q);
    // Deep-link query on a menu URL (or on the app-mode landing, which is Home): canonicalise it. Home has no menu
    // search either, so a ?q= there goes to the menu URL too (P5-01). Mode app: a child key, an unlisted or empty ?store=
    // (A.redirect) change the URL too, in the same redirect; one that lands on Home with a deep link or a ?q= goes on to
    // the menu (the landing's rule), in one hop.
    var home = (page === 'home' && (sub === '' || !!(A && A.redirect))) || (page === 'landing' && sub === '');
    if (page === 'menu' || home) {
      var qd = queryDeepLink(q);
      if (qd && (qd.shorthand || home)) return redirect('menu', params, qd.query);
      if (home && searchIn(q)) return redirect('menu', params, q);
    }
    if (tabled || q !== query || (A && A.redirect)) return redirect(page, params, q);
    return A ? appResult(A, page, params, query) : result(hotel, page, params, query);
  }

  // ---- href (inverse of parse) ------------------------------------------------------------------
  // href(page, params, { hotel, query, hash, routeBase, mode, replace, chain }). hotel, routeBase and mode
  // default to the current location and __SIPO_BOOT. A page that needs a store but has none resolves to
  // the root. In online mode the app-only pages (home, stores, notifications) resolve to the store landing; in mode qr
  // every page off at a table does (offAtTable; qrGate goes on to the table menu), and 'scan' is the store landing.
  // Only a page's own params reach the path (tableId — mode qr only —, orderId, reservationId, kind, edit, and P5-01's
  // item / itemSlug — modes online and app, a slug with an id only —); the query goes out without the PII keys (see
  // "Privacy" above). review without an orderId resolves to the order history.
  // Mode app: hotel is the BRANCH; the path names its chain (opts.chain, else as "Mode app" above says) and store= goes
  // first in the query.
  function relPath(page, params, hotel, mode) {
    var appPages = mode === 'app' || onlineZeta(hotel, mode);
    var H = hotel === null || hotel === undefined || hotel === '' ? null : '/' + enc(hotel);
    if (page === 'find-store') return '/';
    if (page === 'payment-callback') return H ? H + '/payment-callback' : '/payment-callback';
    if (!H) return '/';
    if (mode === 'qr' && has(TABLE_OFF, page)) return H;
    switch (page) {
      case 'scan':
        return H;
      case 'menu':
        if (mode === 'qr') return given(params.tableId) ? H + '/table/' + enc(params.tableId) : H;
        if (itemOf(params)) return H + '/' + params.item + '/' + enc(params.itemSlug); // a parsed item path, as it came (D-19)
        return appPages || mode === 'landing' ? H + '/menu' : H; // tables are qr-only (P4-14): a tableId is ignored here
      case 'landing':
        if (mode === 'landing' && given(params.slug)) return H + '/page/' + enc(params.slug); // WL-VEL: a site page
        return H; // WL-3: mode landing's /{hotel}
      case 'app-content':
        return appPages && PAGE_SLUG.test(params.slug || '') ? H + '/page/' + enc(params.slug) : H;
      case 'zeta-addresses': return appPages ? H + '/addresses' : H;
      case 'settings':
        return appPages ? H + '/settings' : H;
      case 'home':
        return H;
      case 'cart': return H + '/cart';
      case 'checkout': return H + '/checkout';
      case 'orders': return H + '/orders';
      case 'order-status':
        if (given(params.orderId)) return H + '/profile/orders/' + enc(params.orderId);
        return H + '/orderStatus';
      case 'review':
        return given(params.orderId) ? H + '/orders/' + enc(params.orderId) + '/review' : H + '/orders';
      case 'track':
        return H + '/track' + (given(params.orderId) ? '/' + enc(params.orderId) : '');
      case 'bookings':
        return H + '/bookings' + (given(params.reservationId) ? '/' + enc(params.reservationId) : '');
      case 'booking-track':
        return H + '/booking/track' + (given(params.reservationId) ? '/' + enc(params.reservationId) : '');
      case 'profile': return params.edit ? H + '/edit-profile' : H + '/profile';
      case 'legal': return H + '/' + (has(LEGAL_SLUG, params.kind) ? LEGAL_SLUG[params.kind] : LEGAL_SLUG.terms);
      case 'account': case 'rewards': case 'booking': case 'wallet': case 'about': case 'coupons': case 'deals':
        return H + '/' + page;
      case 'stores':
        return mode === 'landing' ? H + '/stores/' : appPages ? H + '/stores' : H;
      case 'notifications':
        return appPages ? H + '/' + page : H;
      case 'ui-gallery': return H + '/__ui';
      default:
        return H; // not-found / unknown page → the store landing
    }
  }

  // The URL of `page` under o.base: o.segment is the path's store key (mode app: the chain), o.query goes out without the
  // PII keys; mode app writes store = o.branch first when o.store (and drops any other `store`). Never reads the
  // location: parse() builds its redirects with it.
  function build(page, params, o) {
    var rel = relPath(page, params || {}, o.segment, o.mode);
    // WL-4: on a custom domain the chain master's landing page is the root.
    if (page === 'landing' && given(o.segment) && same(o.segment, domainKey(o.base, o.mode))) rel = params && given(params.slug) ? '/' + enc(params.slug) : '/'; // WL-VEL: + a site page
    if (o.mode === 'landing' && given(o.segment) && ((page === 'landing' && params && params.stores) || page === 'stores')) {
      rel = same(o.segment, domainKey(o.base, o.mode)) ? '/stores/' : '/' + enc(o.segment) + '/stores/';
    }
    var url = rel === '/' && o.base ? o.base : o.base + rel;
    var query = withoutPii(o.query);
    if (o.mode === 'app') query = withStore(o.store && given(o.segment) && page !== 'find-store' ? o.branch : null, query);
    var hash = o.hash ? '#' + String(o.hash).replace(/^#/, '') : '';
    return url + buildQuery(query) + hash;
  }

  function href(page, params, opts) {
    params = params || {};
    opts = opts || {};
    var b = boot();
    var base = normBase(opts.routeBase !== undefined ? opts.routeBase : b.routeBase);
    var mode = normMode(opts.mode !== undefined ? opts.mode : b.mode);
    if (mode === 'app') return appHref(page, params, opts, base);
    var hotel = opts.hotel !== undefined ? opts.hotel : currentHotel();
    var query = OO.orderingQr && mode === 'online' ? OO.orderingQr.query(hotel, opts.query) : opts.query;
    return build(page, params, { segment: hotel, base: base, mode: mode, query: query, hash: opts.hash });
  }

  // Mode app: the chain path for branch `hotel` (see href() in "Mode app" above). cur = the current route or null.
  function chainFor(hotel, cur) {
    var here = cur && given(cur.chain) ? cur.chain : null;
    if (!given(hotel)) return here;
    if (here && same(hotel, cur.hotel)) return here;
    var e = lookup(hotel);
    if (!e) return String(hotel);
    if (here && lookup(here) === e) return here;
    return e.master || String(hotel);
  }
  // Mode app: whether the URL for branch `hotel` under chain path `chain` carries store= (see href() in "Mode app").
  function storeFor(hotel, chain, cur) {
    if (!given(hotel) || !given(chain)) return false;
    if (cur && cur.via !== undefined && same(hotel, cur.hotel) && same(chain, cur.chain)) return writesStore(cur);
    if (!same(hotel, chain)) return true;
    var e = lookup(hotel);
    return !!e && !(e.keys.length === 1 && same(e.keys[0], hotel));
  }
  function appHref(page, params, opts, base) {
    var cur = currentRoute();
    var hotel = opts.hotel !== undefined ? opts.hotel : cur ? cur.hotel : null;
    hotel = given(hotel) ? String(hotel) : null;
    var chain = given(opts.chain) ? String(opts.chain) : chainFor(hotel, cur);
    return build(page, params, { segment: chain, branch: hotel, store: storeFor(hotel, chain, cur), base: base, mode: 'app', query: opts.query, hash: opts.hash });
  }

  // paymentReturn(branch, search, { routeBase, fallback }) → the root /payment-callback's forward in mode app ("Chains"
  // above), or null.
  function paymentReturn(branch, search, opts) {
    opts = opts || {};
    var b = cleanKey(branch);
    if (!b) return null;
    var e = lookup(b);
    var chain = e ? e.master || b : opts.fallback ? b : null;
    if (!chain) return null;
    var base = normBase(opts.routeBase !== undefined ? opts.routeBase : boot().routeBase);
    var s = String(search == null ? '' : search).replace(/^\?/, '');
    return base + '/' + enc(chain) + '/payment-callback?store=' + enc(b) + (s ? '&' + s : '');
  }

  // ---- tables (D-20; mode qr; see "Tables" above) -------------------------------------------------
  function offAtTable(page) { return has(TABLE_OFF, page); }
  function modeOf(opts) { return normMode(opts && opts.mode !== undefined ? opts.mode : boot().mode); }

  // tableGate(route, table, { routeBase, mode }) → in mode qr, the table menu's href, else the scan landing's, for a
  // page off at a table; null otherwise.
  function tableGate(route, table, opts) {
    opts = opts || {};
    if (modeOf(opts) !== 'qr' || !route || !given(route.hotel) || !offAtTable(route.page)) return null;
    var q = route.query || {};
    var id = tableIdIn(q);
    var t = tableFor(route.hotel, table);
    if (id === null && t) id = String(t.id);
    var o = { hotel: route.hotel, routeBase: opts.routeBase, mode: 'qr', query: withoutTable(q) };
    return id !== null ? href('menu', { tableId: id }, o) : href('scan', {}, o);
  }

  // qrGate(route, table, { routeBase, mode }) → where OO.App sends a route in mode qr, or null when it stays.
  function qrGate(route, table, opts) {
    opts = opts || {};
    if (modeOf(opts) !== 'qr' || !route || !given(route.hotel) || route.redirectTo) return null;
    if (offAtTable(route.page)) return tableGate(route, table, opts);
    var t = tableFor(route.hotel, table);
    var o = { hotel: route.hotel, routeBase: opts.routeBase, mode: 'qr', query: route.query };
    if (route.page === 'scan') return t ? href('menu', { tableId: String(t.id) }, o) : null;
    if (has(NEEDS_TABLE, route.page)) return t ? null : href('scan', {}, o);
    return null; // the entry, the payment pages, the dev gallery
  }

  // ---- browser side (guarded: the vm test has no location/history unless it fakes them) ----------
  function current() {
    var l = window.location || { pathname: '/', search: '' };
    var b = boot();
    return parse(l.pathname, l.search, b.routeBase, b.mode);
  }
  function currentHotel() {
    try { return window.location ? current().hotel : null; } catch (e) { return null; }
  }
  // Mode app's href() reads the current route for every link: parsed once per location and registry version (read-only).
  var curCache = { key: null, route: null };
  function currentRoute() {
    var l = window.location;
    if (!l) return null;
    var b = boot();
    var key = [l.pathname, l.search, version, b.routeBase, b.mode, OO.whitelabel && OO.whitelabel.revision()].join('\n');
    if (curCache.key !== key) {
      try { curCache.route = parse(l.pathname, l.search, b.routeBase, b.mode); } catch (e) { curCache.route = null; }
      curCache.key = key;
    }
    return curCache.route;
  }
  function stateIdx() {
    var s = window.history && window.history.state;
    return s && typeof s.ooIdx === 'number' ? s.ooIdx : 0;
  }
  function emit() {
    try { window.dispatchEvent(new CustomEvent(EVENT)); } catch (e) { /* no CustomEvent: nothing listens */ }
  }

  // Navigate in-app. history.state.ooIdx counts in-app depth so back() knows whether a previous in-app
  // entry exists. Prefer go(…, { replace: true }) over raw history.replaceState, which would reset it.
  function go(to, opts) {
    opts = opts || {};
    var h = window.history, l = window.location;
    if (!h || !l) return;
    if (OO.orderingQr && OO.orderingQr.scopeChanged(to) && l.assign) { l.assign(to); return; }
    var idx = stateIdx();
    if (opts.replace || to === l.pathname + l.search + l.hash) h.replaceState({ ooIdx: idx }, '', to);
    else h.pushState({ ooIdx: idx + 1 }, '', to);
    emit();
  }

  // Back within the app, or (deep-linked entry, no in-app history) replace with the fallback, which
  // defaults to the store landing.
  function back(fallbackHref) {
    if (stateIdx() > 0 && window.history) { window.history.back(); return; }
    go(fallbackHref || href(normMode(boot().mode) === 'app' ? 'home' : 'menu'), { replace: true });
  }

  // <a {...OO.router.link('cart')}>: a real href (middle-click, new tab and copy link work) plus an
  // onClick that navigates in-app on a plain primary click.
  function link(page, params, opts) {
    var to = href(page, params, opts);
    var replace = !!(opts && opts.replace);
    return {
      href: to,
      onClick: function (e) {
        if (!e || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (e.preventDefault) e.preventDefault();
        go(to, { replace: replace });
      },
    };
  }

  function subscribe(cb) {
    window.addEventListener('popstate', cb);
    window.addEventListener(EVENT, cb);
    var offTheme = OO.whitelabel && OO.whitelabel.subscribe(cb);
    return function () {
      window.removeEventListener('popstate', cb);
      window.removeEventListener(EVENT, cb);
      if (offTheme) offTheme();
    };
  }
  // The URL, plus the chain registry's version (mode app: a chain or a saved branch that lands re-resolves the route).
  function locationKey() { var l = window.location; return (l ? l.pathname + l.search : '') + '\n' + version + '\n' + (OO.whitelabel ? OO.whitelabel.revision() : 0); }

  // React hook. useSyncExternalStore re-reads the URL after subscribing, so a navigation that fires
  // before the subscription exists (e.g. OO.App's first-mount redirect) is never missed.
  function useRoute() {
    var React = window.React;
    var key = React.useSyncExternalStore(subscribe, locationKey);
    return React.useMemo(function () { return current(); }, [key]);
  }

  // Stamp the landing entry once so back() can tell an in-app history apart from a deep-linked entry.
  try {
    var hs = window.history;
    if (hs && hs.replaceState && !(hs.state && typeof hs.state.ooIdx === 'number')) {
      var prev = hs.state && typeof hs.state === 'object' ? hs.state : {};
      hs.replaceState(Object.assign({}, prev, { ooIdx: 0 }), '');
    }
  } catch (e) { /* history unavailable */ }

  OO.router = {
    EVENT: EVENT,
    parse: parse,
    href: href,
    go: go,
    back: back,
    link: link,
    current: current,
    useRoute: useRoute,
    parseQuery: parseQuery,
    buildQuery: buildQuery,
    offAtTable: offAtTable,
    tableGate: tableGate,
    qrGate: qrGate,
    tableIdOf: tableIdIn,
    chainOf: chainOf,
    setChain: setChain,
    chainFailed: chainFailed,
    branchOf: branchOf,
    setBranch: setBranch,
    paymentReturn: paymentReturn,
    withoutPii: withoutPii, // WL-VEL (VEL-FIX): the website's redirects carry the query on without the PII keys
  };
})();


// ===================== src/lib/format.jsx =====================
// src/lib/format.jsx: OO.format (alias OO.lib.format). Pure formatting, clock and image-URL helpers. No JSX,
// no DOM, so node:vm tests can run it. Every other data-layer file builds on it.
//
// Money (design rules from docs/online-order/design/ordering-flow.dc.txt):
//   money(n, cur='$')       → '$12.90', '-$1.50'. Always 2 dp. null/NaN → '$0.00'.
//   plusMoney(n, cur='$')   → '' for 0/null, '+$5' for whole dollars, '+$1.50' otherwise (option prices).
//   minusMoney(n, cur='$')  → '-$0.00', '-$4.70' (the Discount row).
//   round2(n)               → n rounded half-up to cents, float-safe (1.005 → 1.01, 27×10/110 → 2.45).
//   currencySymbol(code)    → '$' for NZD/AUD/USD/… (default), '£', '€', '¥', '₹', 'NT$'.
// Text:
//   pluralize(n, one, many?) → '1 item', '3 items'.     initials(name) → 'ST'.
//   pointsDelta(n)           → '+27', '−150' (U+2212 minus, as in the design's Activity list).
//   stripHtml(html)          → plain text (tags dropped, basic entities decoded, whitespace collapsed).
// Time. "minutes" means minutes after local midnight. "hm" is 'HH:mm'. "ymd" is 'YYYY-MM-DD':
//   toMinutes('18:30' | '18:30:00' | 1110) → 1110.    hm(1110) → '18:30' (wraps past 24h).
//   time12(x)     → '6:30pm', '12:00pm'.               hourLabel(x) → '10pm', '11:30am' ("Open until 10pm").
//   rangeMins(a, b) → '15–20 mins'.
//   zoned(date, tz?) → Clock = { y, mo, d, h, mi, s, dow, ymd, hm, minutes, tz }. The wall clock of instant `date`
//                     in IANA zone `tz`. A missing or invalid tz uses the browser zone.
//   parseWallClock('2026-09-30 18:05:00') → Clock. No tz conversion; this is getRestaurant's current_time format.
//   clock(nowLike, tz?) → Clock from a Date, epoch ms, wall-clock string or Clock (store-local "now").
//   tzOffsetMinutes(date, tz) → +600 (AEST), +780 (NZDT).
//   instantOf(ymd, hmOrMinutes, tz) → epoch ms of that wall-clock time in tz (DST-safe).
//   addDays(ymd, n) → ymd.  weekdayOf(ymd) → 0 (Sun)..6.  dayDiff(a, b) → whole days from a to b.
//   weekdayShort(dow) → 'Wed'.  shortDate(ymd) → '12 Sep'.
//   relativeDay(ymd, todayYmd) → 'Today' | 'Yesterday' | 'Fri' (2–7 days ago) | '12 Sep'.
//   relativeDayLong(ymd, todayYmd) → 'Today' | 'Yesterday' | 'Last Friday' (2–7 days ago) | '12 Sep'.
// Images:
//   imageUrl(v) → src | null. 'https://…', '//…', 'data:…' and 'blob:…' pass through. Raw base64 (logos)
//                 becomes 'data:image/png;base64,…'. A relative path resolves via OO.asset (same origin).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  var MINUS = '−';
  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var SYMBOLS = { GBP: '£', EUR: '€', JPY: '¥', CNY: '¥', INR: '₹', TWD: 'NT$', KRW: '₩', PHP: '₱', THB: '฿' };

  function num(n) {
    var v = typeof n === 'string' ? parseFloat(n) : Number(n);
    return isFinite(v) ? v : 0;
  }

  // Half-up to cents via exponent shifting: avoids 1.005 → 1.00 and 2.4545… drift.
  function round2(n) {
    var v = num(n);
    var sign = v < 0 ? -1 : 1;
    var r = Number(Math.round(Number(Math.abs(v) + 'e2')) + 'e-2');
    return sign * r || 0;
  }

  function money(n, cur) {
    var v = round2(n);
    return (v < 0 ? '-' : '') + (cur == null ? '$' : cur) + Math.abs(v).toFixed(2);
  }

  function plusMoney(n, cur) {
    var v = round2(n);
    if (!v) return '';
    var abs = Math.abs(v);
    var body = abs % 1 === 0 ? String(abs) : abs.toFixed(2);
    return (v < 0 ? '-' : '+') + (cur == null ? '$' : cur) + body;
  }

  function minusMoney(n, cur) {
    return '-' + (cur == null ? '$' : cur) + Math.abs(round2(n)).toFixed(2);
  }

  function currencySymbol(code) {
    var c = String(code || '').toUpperCase().trim();
    return Object.prototype.hasOwnProperty.call(SYMBOLS, c) ? SYMBOLS[c] : '$';
  }

  function pluralize(n, one, many) {
    var count = num(n);
    return count + ' ' + (count === 1 ? one : (many || one + 's'));
  }

  function initials(name) {
    var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    var first = parts[0].charAt(0);
    var last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  function pointsDelta(n) {
    var v = Math.round(num(n));
    return v < 0 ? MINUS + Math.abs(v) : '+' + v;
  }

  var ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };
  function stripHtml(html) {
    if (html == null) return '';
    return String(html)
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h\d)>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&(#?\w+);/g, function (m, e) { return Object.prototype.hasOwnProperty.call(ENTITIES, e) ? ENTITIES[e] : m; })
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ---- time ----------------------------------------------------------------------------------------
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function toMinutes(x) {
    if (typeof x === 'number') return x;
    var m = /^(\d{1,2}):(\d{2})/.exec(String(x || ''));
    return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
  }

  function hm(minutes) {
    var m = ((Math.round(toMinutes(minutes)) % 1440) + 1440) % 1440;
    return pad2(Math.floor(m / 60)) + ':' + pad2(m % 60);
  }

  function parts12(x) {
    var m = ((Math.round(toMinutes(x)) % 1440) + 1440) % 1440;
    var h = Math.floor(m / 60), mi = m % 60;
    return { h: h % 12 === 0 ? 12 : h % 12, mi: mi, ap: h < 12 ? 'am' : 'pm' };
  }
  function time12(x) { var p = parts12(x); return p.h + ':' + pad2(p.mi) + p.ap; }
  function hourLabel(x) { var p = parts12(x); return p.h + (p.mi ? ':' + pad2(p.mi) : '') + p.ap; }
  function rangeMins(a, b) { return num(a) + '–' + num(b) + ' mins'; }

  function ymdOf(y, mo, d) { return y + '-' + pad2(mo) + '-' + pad2(d); }
  function splitYmd(ymd) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
    return m ? { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) } : null;
  }
  function addDays(ymd, n) {
    var p = splitYmd(ymd);
    if (!p) return ymd;
    var t = new Date(Date.UTC(p.y, p.mo - 1, p.d + num(n)));
    return ymdOf(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
  }
  function weekdayOf(ymd) {
    var p = splitYmd(ymd);
    return p ? new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay() : NaN;
  }
  function dayDiff(a, b) {
    var pa = splitYmd(a), pb = splitYmd(b);
    if (!pa || !pb) return NaN;
    return Math.round((Date.UTC(pb.y, pb.mo - 1, pb.d) - Date.UTC(pa.y, pa.mo - 1, pa.d)) / 86400000);
  }
  function weekdayShort(dow) { return DOW[((num(dow) % 7) + 7) % 7]; }
  function shortDate(ymd) {
    var p = splitYmd(ymd);
    return p ? p.d + ' ' + MON[p.mo - 1] : '';
  }
  function relativeDay(ymd, todayYmd) {
    var diff = dayDiff(ymd, todayYmd); // days ago
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff >= 2 && diff <= 7) return weekdayShort(weekdayOf(ymd));
    return shortDate(ymd);
  }
  var DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  function relativeDayLong(ymd, todayYmd) {
    var diff = dayDiff(ymd, todayYmd);
    if (diff >= 2 && diff <= 7) return 'Last ' + DOW_LONG[weekdayOf(ymd)];
    return relativeDay(ymd, todayYmd);
  }

  function makeClock(y, mo, d, h, mi, s, tz) {
    var ymd = ymdOf(y, mo, d);
    return { y: y, mo: mo, d: d, h: h, mi: mi, s: s, dow: weekdayOf(ymd), ymd: ymd, hm: pad2(h) + ':' + pad2(mi), minutes: h * 60 + mi, tz: tz || null };
  }

  var formatters = {};
  function formatterFor(tz) {
    if (Object.prototype.hasOwnProperty.call(formatters, tz)) return formatters[tz];
    var f = null;
    try {
      f = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch (e) { f = null; } // RangeError: not an IANA zone (e.g. 'New Zealand Standard Time')
    formatters[tz] = f;
    return f;
  }

  function zoned(date, tz) {
    var dt = date instanceof Date ? date : new Date(typeof date === 'number' ? date : Date.parse(date));
    if (isNaN(dt.getTime())) dt = new Date(0);
    var f = tz ? formatterFor(tz) : null;
    if (!f) {
      return makeClock(dt.getFullYear(), dt.getMonth() + 1, dt.getDate(), dt.getHours(), dt.getMinutes(), dt.getSeconds(), null);
    }
    var p = {};
    f.formatToParts(dt).forEach(function (x) { p[x.type] = x.value; });
    return makeClock(Number(p.year), Number(p.month), Number(p.day), Number(p.hour) % 24, Number(p.minute), Number(p.second), tz);
  }

  function parseWallClock(str) {
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(String(str || ''));
    if (!m) return null;
    return makeClock(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0), null);
  }

  function isClock(x) { return !!x && typeof x === 'object' && typeof x.ymd === 'string' && typeof x.minutes === 'number'; }

  // nowLike: Date | epoch ms | 'Y-m-d H:i:s' wall clock (no conversion) | ISO-with-zone string | Clock.
  function clock(nowLike, tz) {
    if (isClock(nowLike)) return nowLike;
    if (typeof nowLike === 'string' && /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}(:\d{2})?)?$/.test(nowLike.trim())) {
      return parseWallClock(nowLike.trim());
    }
    if (nowLike == null) return zoned(new Date(), tz);
    return zoned(nowLike, tz);
  }

  function tzOffsetMinutes(date, tz) {
    var dt = date instanceof Date ? date : new Date(date);
    var c = zoned(dt, tz);
    var asUtc = Date.UTC(c.y, c.mo - 1, c.d, c.h, c.mi, c.s);
    return Math.round((asUtc - (dt.getTime() - dt.getMilliseconds())) / 60000);
  }

  function instantOf(ymd, hmOrMinutes, tz) {
    var p = splitYmd(ymd);
    if (!p) return NaN;
    var mins = toMinutes(hmOrMinutes == null ? 0 : hmOrMinutes);
    var wall = Date.UTC(p.y, p.mo - 1, p.d, 0, mins);
    if (!tz || !formatterFor(tz)) {
      return new Date(p.y, p.mo - 1, p.d, Math.floor(mins / 60), mins % 60).getTime();
    }
    // Two passes settle DST edges: the offset is taken at the guess, then at the corrected instant.
    var guess = wall - tzOffsetMinutes(new Date(wall), tz) * 60000;
    return wall - tzOffsetMinutes(new Date(guess), tz) * 60000;
  }

  // ---- images ----------------------------------------------------------------------------------------
  function imageUrl(v) {
    if (v == null) return null;
    var s = String(v).trim();
    if (!s || s === 'null' || s === 'undefined') return null;
    if (/^(https?:)?\/\//i.test(s) || /^(data|blob):/i.test(s)) return s;
    if (s.length > 64 && /^[A-Za-z0-9+/=\r\n]+$/.test(s)) return 'data:image/png;base64,' + s.replace(/\s+/g, '');
    return typeof OO.asset === 'function' ? OO.asset(s) : '/' + s.replace(/^\/+/, '');
  }

  OO.format = OO.lib.format = {
    num: num,
    round2: round2,
    money: money,
    plusMoney: plusMoney,
    minusMoney: minusMoney,
    currencySymbol: currencySymbol,
    pluralize: pluralize,
    initials: initials,
    pointsDelta: pointsDelta,
    stripHtml: stripHtml,
    pad2: pad2,
    toMinutes: toMinutes,
    hm: hm,
    time12: time12,
    hourLabel: hourLabel,
    rangeMins: rangeMins,
    zoned: zoned,
    parseWallClock: parseWallClock,
    clock: clock,
    tzOffsetMinutes: tzOffsetMinutes,
    instantOf: instantOf,
    addDays: addDays,
    weekdayOf: weekdayOf,
    dayDiff: dayDiff,
    weekdayShort: weekdayShort,
    shortDate: shortDate,
    relativeDay: relativeDay,
    relativeDayLong: relativeDayLong,
    imageUrl: imageUrl,
  };
})();


// ===================== src/lib/theme.jsx =====================
// src/lib/theme.jsx: OO.theme (alias OO.lib.theme). Venue theming: back-office theme → normalised Theme →
// --oo-* CSS custom properties on :root (styles/tokens.css), plus Google Fonts loading. No JSX.
//
// Theme (normalised; a null field means "keep the styles/tokens.css default", which is the design's look):
//   { primary, onPrimary, header, onHeader, canvas, onCanvas, ink, accent, onAccent, accentBar, heroBg, heroInk,
//     font, fontB, headFont, headWeight, buttonRadius, cardRadius, logo, backgroundImage, isConfigured
//     [, key, label, brand] }
//   header is never null from fromRaw (REF's fallback chain ends at #E8553E), nor is canvas (default #F5F0EB).
//   isConfigured = the merchant set a theme of their own (see isConfigured below); toCssVars reads it.
//
// API:
//   normalizeColor(c)       → '#RRGGBB' | null. Strips a doubled '##' (§6), expands '#RGB', accepts 'RRGGBB' and the
//                             CSS named colours ('red' → '#FF0000'; 'transparent' → null), and passes rgb()/hsl()
//                             through.
//   contrast(color)         → '#000000' | '#FFFFFF'. Next's YIQ rule: (r*299+g*587+b*114)/1000 > 128 → black.
//   contrastRatio(a, b)     → the WCAG 2 contrast ratio (1–21) of text colour a on surface b, in any normalizeColor
//                             form (a translucent rgba()/hsla() text is composited over the surface first); NaN when
//                             either colour can't be read.
//   readable(text, surface, min = 4.5) → text itself when it already reaches `min` on `surface`; else text stepped in
//       1 % HSL-lightness steps toward whichever of black / white reads better on the surface (REF develop
//       themeContrast.ts, A-Δ2), as '#RRGGBB'. Unreadable inputs come back unchanged.
//   normalizeFont(f)        → 'Albert Sans' | null. First face of a comma list, unquoted, CamelCase split (§6). A name
//                             outside ^[A-Za-z0-9 _-]{1,80}$ is rejected (null), so it is safe in CSS and in a Google
//                             Fonts URL (R-THEME-1).
//   dataUri(v)              → a raw base64 image (no data: prefix; production sends theme images that way) as
//                             'data:image/<png|jpeg|gif|webp>;base64,…', typed by its magic bytes (iVBOR png, /9j/
//                             jpeg, R0lGOD gif, UklGR webp; other long base64 → png). null when v is not one.
//   imageSrc(v)             → src | null. http(s), //, data: and blob: pass through; a raw base64 blob → dataUri(v);
//                             anything else → OO.format.imageUrl (a relative path, same origin). Never a bare base64.
//   isConfigured(onlineTheme) → bool. REF's rule (storeTheme.ts, A F10): the merchant set any of the background,
//       header, button, card-background or card-border colour, or a font (font_family / font_family_b), in
//       shopThemes.online_theme. Invalid colours and fonts don't count.
//   fromRaw({ onlineTheme, structured, proposed, onlineThemeColor, image }) → Theme. Used by OO.model.normalizeStore:
//       onlineTheme = data[0].shopThemes.online_theme (object or JSON string): button.button_color → primary,
//                     header colour → header, background_color → canvas (default #F5F0EB), font_family(_b) →
//                     font(B), header_logo → logo, background_image.
//                     header = online_theme.header.background_color › header_color › header.backgroundcolor ›
//                     onlineThemeColor (data[0].online_theme_color = user_metadatas.online_theme_color) › #E8553E.
//       structured  = data[0].theme. online_order.accent → accent, ONLY when online_order.enabled === true.
//       proposed    = data[0].proposed.theme (Batch-2 gap; see BATCH2-GAPS.md): heading_font, heading_weight,
//                     ink, accent_bar, on_accent, hero_bg, hero_ink.
//       image       = the image resolver for header_logo / background_image (normalizeStore passes its store-media
//                     rules); default imageSrc.
//       The *_font_color values from the backend are ignored. Contrast is always derived (§6).
//   toCssVars(theme, mode)  → { '--oo-primary': '#1E5B3A', … }. It emits only the variables the theme defines.
//       Mode 'online' (the default) applies the merchant theme ONLY when theme.isConfigured !== false; otherwise it
//       returns {} and the SIPO default (styles/tokens.css) shows (R-THEME-1). Modes 'app' and 'qr' (and 'kiosk')
//       always apply it. Mode 'app' paints the top bar with primary/onPrimary (the design's themed Home); the
//       others use the header colour. A missing accent falls back to primary, so a single-colour store stays
//       coherent. Then the contrast pass (below) runs, in every mode.
//   apply(vars, el?)        → vars. Sets them on document.documentElement and removes vars set by the previous
//                             apply() that are not in the new map, so switching store resets cleanly.
//   loadGoogleFont(family, weights?) → true if a css2 <link> was injected. Loads each family once. Skips system
//       fonts and families the page already links (the blade preloads Poppins 400–700). Unknown families load
//       without a weight axis, because Google 400s on a weight a family lacks. WL-SUPER: for a family the page
//       already links, explicit `weights` the existing links don't cover (their wght@ lists; a link without a
//       weight axis is 400) get ONE extra link with just those, limited to the weights the family serves
//       (WL_FONT_WEIGHTS, else KNOWN_WEIGHTS; neither: none); nothing when all are covered, and a repeat call
//       finds its own link (so the blade's Poppins 400–700 + a Super / bold body's [800, 900] → Poppins:wght@800;900).
//   fontsOf(theme)          → the web fonts to load: [font, headFont, scriptFont] without nulls or duplicates.
//   fontWeights(theme, family) → the weights to load for one of fontsOf(theme): null for every theme but a white-label
//       one (theme.whitelabel; WL-2), so loadGoogleFont keeps its own rule; for a white-label theme, the family's
//       WL_FONT_WEIGHTS ∩ [400, 500, 600, 700, headWeight] (+ theme.bodyWeights for the body family, theme.font),
//       null for a family off the allowlist.
//   WL_FONTS                → the white-label font allowlist (docs/whitelabel/WHITELABEL-PLAN.md §5 Font), in order.
//   wlFont(f)               → the allowlisted family f names exactly (case-insensitive), else null. Nothing else ever
//                             reaches a white-label theme's font fields, so nothing else reaches the Google Fonts URL.
//   RADII, CARD_SHADOWS     → the white-label shape scales (below).
//   PRESETS                 → { bennys, nonnas, harbour }: the design's white-label app themes (customer-app THEMES).
//   resolve(theme, key)     → the store theme with PRESETS[key] merged over it (the ?theme= preview; it counts as
//                             configured); else theme.
//   PAIRS, DEFAULTS, REFS   → the contrast pass's (text, surface) token pairs; the styles/tokens.css values it assumes
//                             for tokens a theme doesn't emit; and the tokens.css tokens that are var() references to
//                             another token, which it follows instead ({ '--oo-card-dark': '--oo-ink', '--oo-page-ink':
//                             '--oo-ink', '--oo-page-muted': '--oo-muted' }). tests/theme.test.mjs checks them against
//                             tokens.css. A token's value under a vars map: vars[k], else REFS[k]'s, else DEFAULTS[k].
//
// White-label fields (WL-2; src/lib/whitelabel.jsx sets them from a theme document, nothing else does). Each is
// optional; a theme without them gives exactly the variables it gave before.
//   surface      → --oo-card (the card / sheet surface; the contrast pass checks ink and muted on it).
//   muted        → --oo-muted.
//   headerVariant 'solid' (header / onHeader) | 'brand' (primary / onPrimary) | 'transparent' (the canvas and ink: the
//                bar blends into the page) decides the top bar in every mode; without one, the mode rule below.
//   radius       'none' | 'sm' | 'md' | 'lg' | 'xl' | 'pill' → the --oo-r-* scale (RADII; 'md' is styles/tokens.css,
//                so it emits nothing).
//   card         'flat' | 'outline' | 'shadow' → --oo-shadow-card / --oo-shadow-hover (CARD_SHADOWS).
//   whitelabel   true: fontWeights() loads the allowlisted weights.
//   scriptFont   WL-SUPER: the Super design's script accent family (typography.script) → --oo-script-font (the family,
//                then var(--oo-head-font)); loaded like the others, at the weights it has (400). Absent: nothing emitted.
//   bodyWeights  WL-CHK: extra weights fontWeights() asks for the body family only ([800, 900]: the bold landing design's
//                labels, mode landing only); still capped by the family's allowlist (Fredoka stays at most 700). Absent
//                on every other theme, which therefore requests exactly the weights it did before.
//   headWeights  WL-VEL: the same for the display family only ([800]: the boutique ordering skin's section heads).
//
// Contrast pass (REF develop A-Δ2: every text token ≥ 4.5:1 on its own surface, 1 % steps). A pair is checked when
// the theme emits its text or its surface token (for a REFS text token, also the token it follows); the other side
// is the emitted value or the tokens.css default. A failing text token is stepped and emitted; surfaces are never
// changed (they are the merchant's colours).
//   Text on one surface — stepped toward black or white, whichever reads better (readable()):
//     --oo-on-primary on --oo-primary          (buttons, chips, active tabs, avatars; the inverted cart pill)
//     --oo-on-accent on --oo-accent            (promo CTAs)
//     --oo-hero-ink on --oo-hero-bg            (app hero / loyalty card)
//     --oo-header-ink on --oo-header-bg        (top bars; the inverted cart pill)
//     --oo-header-sub on --oo-header-bg        (bar sub-text and links: the default #C8D0D0, or rgba(0,0,0,.6) on a
//                                               light bar, composited before it is measured; a stepped one is a hex)
//     --oo-header-ink on --oo-header-field     (text typed in the desktop header search field)
//     --oo-header-field-ink on --oo-header-field (its placeholder)
//       The field (tokens.css #1C1C1C: the black bar lifted 11 %) is the one derived surface: when the header-ink
//       can't be read on it (a light or mid-tone bar), the theme emits the bar moved 11 % in lightness away from the
//       ink (a lighter field under dark ink); a bar too near white for that moves up to 11 % toward the ink instead,
//       as far as the ink still reads at 4.5:1 (the bar itself at worst).
//   Card text — stepped DARKER only, because tokens.css builds the dark loyalty card (--oo-card-dark) from --oo-ink
//   with white text, so ink must stay a dark colour:
//     --oo-ink on --oo-card (#FFFFFF, not themed); and --oo-on-card-dark / --oo-on-card-dark-sub on --oo-card-dark
//       (= --oo-ink)
//     --oo-muted on --oo-card
//     While some dark colour also reaches 4.5:1 on the canvas, ink and muted are stepped to pass there too, so the page
//     text of a light canvas (which follows them, below) keeps the theme's ink. A canvas no dark text reads on (e.g.
//     prod Charlie's #000000) keeps only the card side: its page text gets its own colour.
//   Page text — the text drawn straight on the canvas (styles/tokens.css: --oo-page-ink / --oo-page-muted, = ink /
//   muted by default: page and section titles, sheet bodies, page links and icons, canvas-coloured chips). After the
//   card pass, stepped toward whichever of black / white reads better on the canvas (readable()):
//     --oo-page-ink on --oo-canvas
//     --oo-page-muted on --oo-canvas
//     A page ink under 3:1 starts from that end itself (REF: below 3:1 the readable fallback is used, then the 4.5:1
//     step), so a black canvas gets white page text, not the first grey that passes; page muted steps from muted. A
//     canvas ink and muted already pass on (every light one) emits neither, so its page text stays ink / muted.
//   Not covered: --oo-faint (the design's placeholder / disabled / struck-price tone; it is under 4.5:1 on the SIPO
//   default too), and --oo-primary / --oo-accent where the CSS uses them as text (tab bar, home actions, promo tag):
//   they are the merchant's surface colours, and stepping them would recolour every button. Nor --oo-on-primary where
//   .oo-btn--success reuses it on --oo-success (#1F7A55): a mid-tone primary needs dark text and the green needs light,
//   so one token can't pass on both (that button needs its own --oo-on-success; see the P1-07 report). Nor the fixed
//   status colours (--oo-success / --oo-warn / --oo-danger, tokens.css only): they sit on their own tints or on white,
//   except the app store finder's location error (warn on the canvas: 3.1:1 on a black one).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function fmt() { return OO.format; }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }

  function parseObj(v) {
    if (v && typeof v === 'object') return v;
    if (typeof v === 'string' && v.trim().charAt(0) === '{') {
      try { var o = JSON.parse(v); return o && typeof o === 'object' ? o : null; } catch (e) { return null; }
    }
    return null;
  }

  // ---- colours -------------------------------------------------------------------------------------------
  // The CSS named colours (CSS Color 4), 'name:RRGGBB' pairs, parsed once on first use.
  var NAMED_SRC = 'aliceblue:F0F8FF,antiquewhite:FAEBD7,aqua:00FFFF,aquamarine:7FFFD4,azure:F0FFFF,beige:F5F5DC,' +
    'bisque:FFE4C4,black:000000,blanchedalmond:FFEBCD,blue:0000FF,blueviolet:8A2BE2,brown:A52A2A,burlywood:DEB887,' +
    'cadetblue:5F9EA0,chartreuse:7FFF00,chocolate:D2691E,coral:FF7F50,cornflowerblue:6495ED,cornsilk:FFF8DC,' +
    'crimson:DC143C,cyan:00FFFF,darkblue:00008B,darkcyan:008B8B,darkgoldenrod:B8860B,darkgray:A9A9A9,' +
    'darkgreen:006400,darkgrey:A9A9A9,darkkhaki:BDB76B,darkmagenta:8B008B,darkolivegreen:556B2F,darkorange:FF8C00,' +
    'darkorchid:9932CC,darkred:8B0000,darksalmon:E9967A,darkseagreen:8FBC8F,darkslateblue:483D8B,' +
    'darkslategray:2F4F4F,darkslategrey:2F4F4F,darkturquoise:00CED1,darkviolet:9400D3,deeppink:FF1493,' +
    'deepskyblue:00BFFF,dimgray:696969,dimgrey:696969,dodgerblue:1E90FF,firebrick:B22222,floralwhite:FFFAF0,' +
    'forestgreen:228B22,fuchsia:FF00FF,gainsboro:DCDCDC,ghostwhite:F8F8FF,gold:FFD700,goldenrod:DAA520,gray:808080,' +
    'green:008000,greenyellow:ADFF2F,grey:808080,honeydew:F0FFF0,hotpink:FF69B4,indianred:CD5C5C,indigo:4B0082,' +
    'ivory:FFFFF0,khaki:F0E68C,lavender:E6E6FA,lavenderblush:FFF0F5,lawngreen:7CFC00,lemonchiffon:FFFACD,' +
    'lightblue:ADD8E6,lightcoral:F08080,lightcyan:E0FFFF,lightgoldenrodyellow:FAFAD2,lightgray:D3D3D3,' +
    'lightgreen:90EE90,lightgrey:D3D3D3,lightpink:FFB6C1,lightsalmon:FFA07A,lightseagreen:20B2AA,' +
    'lightskyblue:87CEFA,lightslategray:778899,lightslategrey:778899,lightsteelblue:B0C4DE,lightyellow:FFFFE0,' +
    'lime:00FF00,limegreen:32CD32,linen:FAF0E6,magenta:FF00FF,maroon:800000,mediumaquamarine:66CDAA,' +
    'mediumblue:0000CD,mediumorchid:BA55D3,mediumpurple:9370DB,mediumseagreen:3CB371,mediumslateblue:7B68EE,' +
    'mediumspringgreen:00FA9A,mediumturquoise:48D1CC,mediumvioletred:C71585,midnightblue:191970,mintcream:F5FFFA,' +
    'mistyrose:FFE4E1,moccasin:FFE4B5,navajowhite:FFDEAD,navy:000080,oldlace:FDF5E6,olive:808000,olivedrab:6B8E23,' +
    'orange:FFA500,orangered:FF4500,orchid:DA70D6,palegoldenrod:EEE8AA,palegreen:98FB98,paleturquoise:AFEEEE,' +
    'palevioletred:DB7093,papayawhip:FFEFD5,peachpuff:FFDAB9,peru:CD853F,pink:FFC0CB,plum:DDA0DD,' +
    'powderblue:B0E0E6,purple:800080,rebeccapurple:663399,red:FF0000,rosybrown:BC8F8F,royalblue:4169E1,' +
    'saddlebrown:8B4513,salmon:FA8072,sandybrown:F4A460,seagreen:2E8B57,seashell:FFF5EE,sienna:A0522D,' +
    'silver:C0C0C0,skyblue:87CEEB,slateblue:6A5ACD,slategray:708090,slategrey:708090,snow:FFFAFA,' +
    'springgreen:00FF7F,steelblue:4682B4,tan:D2B48C,teal:008080,thistle:D8BFD8,tomato:FF6347,turquoise:40E0D0,' +
    'violet:EE82EE,wheat:F5DEB3,white:FFFFFF,whitesmoke:F5F5F5,yellow:FFFF00,yellowgreen:9ACD32';
  var named = null;
  function namedColor(s) {
    if (!named) {
      named = {};
      NAMED_SRC.split(',').forEach(function (p) { var kv = p.split(':'); named[kv[0]] = '#' + kv[1]; });
    }
    var k = s.toLowerCase();
    return own(named, k) ? named[k] : null;
  }

  function normalizeColor(c) {
    if (c == null) return null;
    var s = String(c).trim();
    if (!s) return null;
    if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return /^(rgba?|hsla?)\([0-9a-z.,%\s\/+-]*\)$/i.test(s) ? s : null;
    if (/^[a-z]+$/i.test(s) && !/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s)) return namedColor(s);
    s = s.replace(/^#+/, '');
    if (/^[0-9a-f]{3}$/i.test(s)) s = s.charAt(0) + s.charAt(0) + s.charAt(1) + s.charAt(1) + s.charAt(2) + s.charAt(2);
    if (/^[0-9a-f]{8}$/i.test(s)) s = s.slice(0, 6); // drop alpha
    return /^[0-9a-f]{6}$/i.test(s) ? '#' + s.toUpperCase() : null;
  }

  function contrast(color) {
    var hex = normalizeColor(color);
    if (!hex || hex.charAt(0) !== '#') return '#FFFFFF';
    var r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#000000' : '#FFFFFF';
  }

  // ---- WCAG maths (rgba objects: r, g, b in 0–255, a in 0–1) -----------------------------------------------
  var BLACK = { r: 0, g: 0, b: 0, a: 1 }, WHITE = { r: 255, g: 255, b: 255, a: 1 };
  function clamp(x, lo, hi) { return x < lo ? lo : (x > hi ? hi : x); }

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360 / 360;
    if (!s) return { r: Math.round(l * 255), g: Math.round(l * 255), b: Math.round(l * 255), a: 1 };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    function hue(t) {
      t = t < 0 ? t + 1 : (t > 1 ? t - 1 : t);
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    return { r: Math.round(hue(h + 1 / 3) * 255), g: Math.round(hue(h) * 255), b: Math.round(hue(h - 1 / 3) * 255), a: 1 };
  }
  function rgbToHsl(c) {
    var r = c.r / 255, g = c.g / 255, b = c.b / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    var l = (max + min) / 2, h = 0, s = 0;
    if (d) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? (g - b) / d + (g < b ? 6 : 0) : (max === g ? (b - r) / d + 2 : (r - g) / d + 4);
      h *= 60;
    }
    return { h: h, s: s, l: l };
  }

  // Any normalizeColor form → { r, g, b, a } | null (rgb()/rgba()/hsl()/hsla(), comma or space syntax, % values).
  function rgbaOf(c) {
    var s = normalizeColor(c);
    if (!s) return null;
    if (s.charAt(0) === '#') {
      var n = parseInt(s.slice(1), 16);
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
    }
    var m = /^(rgba?|hsla?)\(([^)]*)\)$/i.exec(s);
    if (!m) return null;
    var p = m[2].split(/[\s,\/]+/).filter(Boolean);
    if (p.length < 3) return null;
    // A number, or a percentage of `full` ('50%' of 255 → 127.5).
    function val(x, full) { var v = parseFloat(x); return /%$/.test(x) ? v / 100 * full : v; }
    var a = p.length > 3 ? val(p[3], 1) : 1;
    var out;
    if (m[1].toLowerCase().charAt(0) === 'r') {
      out = { r: val(p[0], 255), g: val(p[1], 255), b: val(p[2], 255), a: a };
    } else { // hsl(h, s%, l%): s and l are percentages with or without the '%'
      out = hslToRgb(parseFloat(p[0]), val(p[1], 100) / 100, val(p[2], 100) / 100);
      out.a = a;
    }
    if (![out.r, out.g, out.b, out.a].every(isFinite)) return null;
    return { r: clamp(out.r, 0, 255), g: clamp(out.g, 0, 255), b: clamp(out.b, 0, 255), a: clamp(out.a, 0, 1) };
  }
  function over(top, bottom) {
    if (top.a >= 1) return top;
    return { r: top.r * top.a + bottom.r * (1 - top.a), g: top.g * top.a + bottom.g * (1 - top.a), b: top.b * top.a + bottom.b * (1 - top.a), a: 1 };
  }
  function lin(v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
  function luminance(c) { return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b); }
  function ratioOf(x, y) {
    var a = luminance(x), b = luminance(y);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }
  function hexOf(c) {
    return '#' + [c.r, c.g, c.b].map(function (v) { var h = Math.round(clamp(v, 0, 255)).toString(16); return h.length < 2 ? '0' + h : h; }).join('').toUpperCase();
  }
  function opaque(c) { var o = rgbaOf(c); return o ? over(o, WHITE) : null; } // a translucent surface sits on white

  function contrastRatio(text, surface) {
    var bg = opaque(surface), fg = rgbaOf(text);
    return bg && fg ? ratioOf(over(fg, bg), bg) : NaN;
  }

  // The first colour 1 %, 2 %, … away from c in HSL lightness (dir -1 darker, +1 lighter) for which ok(colour)
  // holds; null when even black / white doesn't.
  function stepLightness(c, dir, ok) {
    var hsl = rgbToHsl(c);
    for (var k = 1; k <= 100; k++) {
      var l = clamp(hsl.l + dir * k / 100, 0, 1);
      var cand = hslToRgb(hsl.h, hsl.s, l);
      if (ok(cand)) return cand;
      if (l === 0 || l === 1) break;
    }
    return null;
  }

  // Black reads at least as well as white on the (opaque) surface bg.
  function blackReads(bg) { return ratioOf(BLACK, bg) >= ratioOf(WHITE, bg); }
  // The end that reads better on a surface: '#000000' | '#FFFFFF'; null when the surface can't be read.
  function readableEnd(surface) {
    var bg = opaque(surface);
    return bg ? (blackReads(bg) ? '#000000' : '#FFFFFF') : null;
  }

  function readable(text, surface, min) {
    min = min || 4.5;
    var bg = opaque(surface), fg0 = rgbaOf(text);
    if (!bg || !fg0) return text;
    var fg = over(fg0, bg);
    if (ratioOf(fg, bg) >= min) return text;
    var dir = blackReads(bg) ? -1 : 1;
    var hit = stepLightness(fg, dir, function (c) { return ratioOf(c, bg) >= min; });
    return hexOf(hit || (dir < 0 ? BLACK : WHITE));
  }

  // Card text (ink, muted): stepped darker only, until it reaches `min` on every surface. null when it can't (a
  // surface too dark for dark text).
  function darkened(text, surfaces, min) {
    var fg0 = rgbaOf(text), bgs = surfaces.map(opaque).filter(Boolean);
    if (!fg0 || !bgs.length) return text;
    var fg = over(fg0, bgs[0]);
    function ok(c) { return bgs.every(function (bg) { return ratioOf(c, bg) >= min; }); }
    if (ok(fg)) return text;
    var hit = stepLightness(fg, -1, ok);
    return hit ? hexOf(hit) : null;
  }

  // ---- fonts / images ---------------------------------------------------------------------------------
  var FONT_NAME = /^[A-Za-z0-9 _-]{1,80}$/;
  function normalizeFont(f) {
    if (f == null) return null;
    var first = String(f).split(',')[0].trim().replace(/^['"]+|['"]+$/g, '').trim();
    if (!first || !FONT_NAME.test(first)) return null;
    // 'AlbertSans' → 'Albert Sans'; 'DMSerifDisplay' → 'DM Serif Display'.
    var out = first.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    return FONT_NAME.test(out) ? out : null;
  }

  var MAGIC = [['iVBOR', 'png'], ['/9j/', 'jpeg'], ['R0lGOD', 'gif'], ['UklGR', 'webp']];
  function dataUri(v) {
    var s = String(v == null ? '' : v).replace(/\s+/g, '');
    if (s.length < 16 || !/^[A-Za-z0-9+\/]+={0,2}$/.test(s)) return null;
    for (var i = 0; i < MAGIC.length; i++) {
      if (s.indexOf(MAGIC[i][0]) === 0) return 'data:image/' + MAGIC[i][1] + ';base64,' + s;
    }
    return s.length > 64 ? 'data:image/png;base64,' + s : null; // OO.format.imageUrl's rule for unknown base64
  }
  function imageSrc(v) {
    if (v == null) return null;
    var s = String(v).trim();
    if (!s || s === 'null' || s === 'undefined') return null;
    if (/^(https?:)?\/\//i.test(s) || /^(data|blob):/i.test(s)) return s;
    var d = dataUri(s);
    if (d) return d;
    var f = fmt();
    return f ? f.imageUrl(s) : null;
  }

  function numOrNull(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }

  // ---- white-label fonts and shape scales (WL-2) --------------------------------------------------------------
  // The §5 Font allowlist and the weights Google Fonts serves for each (css2 400s on a weight a family lacks).
  var WL_FONT_WEIGHTS = {
    'Inter': [400, 500, 600, 700, 800, 900], 'Roboto': [400, 500, 700, 900], 'Open Sans': [400, 500, 600, 700, 800],
    'Lato': [400, 700, 900], 'Montserrat': [400, 500, 600, 700, 800, 900], 'Poppins': [400, 500, 600, 700, 800, 900],
    'Nunito': [400, 500, 600, 700, 800, 900], 'Fredoka': [400, 500, 600, 700], 'Raleway': [400, 500, 600, 700, 800, 900],
    'Oswald': [400, 500, 600, 700], 'Bebas Neue': [400], 'Playfair Display': [400, 500, 600, 700, 800, 900],
    'DM Sans': [400, 500, 600, 700, 800, 900], 'Work Sans': [400, 500, 600, 700, 800, 900],
    'Rubik': [400, 500, 600, 700, 800, 900], 'Manrope': [400, 500, 600, 700, 800], 'Outfit': [400, 500, 600, 700, 800, 900],
    'Quicksand': [400, 500, 600, 700], 'Source Sans 3': [400, 500, 600, 700, 800, 900], 'Baloo 2': [400, 500, 600, 700, 800], 'Archivo': [400, 500, 600, 700, 800, 900],
    // WL-SUPER: script and poster faces, each served at 400 only (css2 rejects the whole request for a missing weight).
    'Pacifico': [400], 'Yellowtail': [400], 'Anton': [400], 'Archivo Black': [400],
    'Mulish': [400, 500, 600, 700, 800], // WL-VEL
    'Cormorant Garamond': [400, 500, 600, 700], // K Kitchen's bundled serif face.
  };
  // WL-VEL: the families Google Fonts serves with italics (css2 `ital` axis; checked 2026-10-12). A theme with headItalic
  // (the boutique landing) loads its display family's italics too, when it is one of these. Archivo keeps its own axis.
  var WL_ITALIC = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Nunito', 'Raleway', 'Playfair Display', 'DM Sans', 'Work Sans', 'Rubik', 'Source Sans 3', 'Mulish'];
  var WL_FONTS = Object.keys(WL_FONT_WEIGHTS);
  function wlFont(f) {
    if (typeof f !== 'string') return null;
    var k = f.trim().toLowerCase();
    for (var i = 0; i < WL_FONTS.length; i++) if (WL_FONTS[i].toLowerCase() === k) return WL_FONTS[i];
    return null;
  }
  // --oo-r-* per radius ('md' = styles/tokens.css: nothing emitted). 'pill' rounds every control fully.
  var R_KEYS = ['--oo-r-xs', '--oo-r-sm', '--oo-r-md', '--oo-r-lg', '--oo-r-xl', '--oo-r-2xl', '--oo-r-3xl', '--oo-r-icon', '--oo-r-pill'];
  var RADII = {
    none: [0, 0, 0, 0, 0, 0, 0, 0, 0],
    sm: [3, 6, 8, 9, 10, 12, 14, 5, 999],
    md: null,
    lg: [6, 12, 16, 18, 20, 22, 24, 10, 999],
    xl: [8, 14, 20, 22, 24, 26, 28, 12, 999],
    pill: [999, 999, 24, 26, 28, 30, 32, 999, 999],
  };
  var CARD_SHADOWS = {
    flat: { '--oo-shadow-card': 'none', '--oo-shadow-hover': 'none' },
    outline: { '--oo-shadow-card': '0 0 0 1px rgba(0, 0, 0, .12)', '--oo-shadow-hover': '0 0 0 1px rgba(0, 0, 0, .24)' },
    shadow: { '--oo-shadow-card': '0 4px 14px rgba(0, 0, 0, .08)', '--oo-shadow-hover': '0 8px 22px rgba(0, 0, 0, .12)' },
  };

  // ---- back-office theme → Theme ------------------------------------------------------------------------
  var HEADER_DEFAULT = '#E8553E';
  var BACKGROUND_DEFAULT = '#F5F0EB';

  function isConfigured(onlineTheme) {
    var t = parseObj(onlineTheme);
    if (!t || Array.isArray(t)) return false;
    var button = parseObj(t.button) || {}, card = parseObj(t.card) || {}, head = parseObj(t.header) || {};
    var colours = [t.background_color, head.background_color, t.header_color, head.backgroundcolor, button.button_color,
      t.button_color, card.card_background_color, card.card_border_color];
    return colours.some(function (c) { return !!normalizeColor(c); }) || !!normalizeFont(t.font_family) || !!normalizeFont(t.font_family_b);
  }

  function fromRaw(input) {
    input = input || {};
    var t = parseObj(input.onlineTheme) || {};
    var button = parseObj(t.button) || {};
    var card = parseObj(t.card) || {};
    var head = parseObj(t.header) || {}; // [] on some production stores
    var structured = parseObj(input.structured) || {};
    var oo = parseObj(structured.online_order) || {};
    var p = parseObj(input.proposed) || {};
    var image = typeof input.image === 'function' ? input.image : imageSrc;

    var primary = normalizeColor(button.button_color) || normalizeColor(t.button_color);
    var header = normalizeColor(head.background_color) || normalizeColor(t.header_color) || normalizeColor(head.backgroundcolor) ||
      normalizeColor(input.onlineThemeColor) || HEADER_DEFAULT;
    var canvas = normalizeColor(t.background_color) || BACKGROUND_DEFAULT;
    var accent = oo.enabled === true ? normalizeColor(oo.accent) : null;
    var heroBg = normalizeColor(p.hero_bg);
    return {
      primary: primary,
      onPrimary: primary ? contrast(primary) : null,
      header: header,
      onHeader: contrast(header),
      canvas: canvas,
      onCanvas: contrast(canvas),
      ink: normalizeColor(p.ink),
      accent: accent,
      onAccent: accent ? (normalizeColor(p.on_accent) || contrast(accent)) : null,
      accentBar: normalizeColor(p.accent_bar) || accent || null,
      heroBg: heroBg,
      heroInk: normalizeColor(p.hero_ink) || (heroBg ? contrast(heroBg) : null),
      font: normalizeFont(t.font_family),
      fontB: normalizeFont(t.font_family_b),
      headFont: normalizeFont(p.heading_font),
      headWeight: numOrNull(p.heading_weight),
      buttonRadius: numOrNull(button.border_radius),
      cardRadius: numOrNull(card.card_border_radius),
      logo: image(t.header_logo) || null,
      backgroundImage: image(t.background_image) || null,
      isConfigured: isConfigured(t),
    };
  }

  // ---- Theme → CSS variables --------------------------------------------------------------------------
  var BASE_STACK = "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";
  function quoteFamily(f) { return "'" + String(f).replace(/'/g, '') + "'"; }
  function isSerif(f) { return /serif/i.test(f) && !/sans/i.test(f); }
  function fontStack(f) { return quoteFamily(f) + (String(f).toLowerCase() === 'poppins' ? '' : ", 'Poppins'") + ', ' + BASE_STACK; }
  function headStack(f) { return quoteFamily(f) + (isSerif(f) ? ', Georgia, serif' : ', var(--oo-font)'); }
  // WL-SUPER: the script accent family, falling back to the display family.
  function scriptStack(f) { return quoteFamily(f) + ', var(--oo-head-font)'; }

  // styles/tokens.css values of the tokens the contrast pass reads, and the tokens.css tokens that are var() references
  // to another token (the loyalty card is built from ink; page text is ink / muted unless a theme steps it).
  var DEFAULTS = {
    '--oo-primary': '#000000', '--oo-on-primary': '#FFFFFF', '--oo-accent': '#000000', '--oo-on-accent': '#FFFFFF',
    '--oo-ink': '#000000', '--oo-canvas': '#EFF4F4', '--oo-hero-bg': '#000000', '--oo-hero-ink': '#FFFFFF',
    '--oo-card': '#FFFFFF', '--oo-header-bg': '#000000', '--oo-header-ink': '#FFFFFF', '--oo-header-sub': '#C8D0D0',
    '--oo-header-field': '#1C1C1C', '--oo-header-field-ink': '#A9B3B3', '--oo-muted': '#4A5656',
    '--oo-on-card-dark': '#FFFFFF', '--oo-on-card-dark-sub': '#C8D0D0',
  };
  var REFS = { '--oo-card-dark': '--oo-ink', '--oo-page-ink': '--oo-ink', '--oo-page-muted': '--oo-muted' };
  var PAIRS = [
    ['--oo-on-primary', '--oo-primary'],
    ['--oo-on-accent', '--oo-accent'],
    ['--oo-hero-ink', '--oo-hero-bg'],
    ['--oo-header-ink', '--oo-header-bg'],
    ['--oo-header-sub', '--oo-header-bg'],
    ['--oo-header-ink', '--oo-header-field'],
    ['--oo-header-field-ink', '--oo-header-field'],
    ['--oo-ink', '--oo-card'],
    ['--oo-on-card-dark', '--oo-card-dark'],
    ['--oo-on-card-dark-sub', '--oo-card-dark'],
    ['--oo-muted', '--oo-card'],
    ['--oo-page-ink', '--oo-canvas'],
    ['--oo-page-muted', '--oo-canvas'],
  ];
  var FIELD_LIFT = 0.11; // tokens.css: #1C1C1C is the #000000 bar 11 % lighter

  // The header search field for a bar whose ink can't be read on the default field: the bar moved 11 % in lightness
  // away from the ink (a lighter field under dark ink), or, when the bar is too near white / black for that, up to
  // 11 % toward the ink while the ink still reads at 4.5:1; the bar itself at worst.
  function fieldFor(bar, ink) {
    var b = opaque(bar), i = opaque(ink);
    if (!b || !i) return null;
    var toward = luminance(i) < luminance(b) ? -1 : 1, hsl = rgbToHsl(b);
    var lAway = clamp(hsl.l - toward * FIELD_LIFT, 0, 1);
    if (Math.abs(lAway - hsl.l) >= FIELD_LIFT / 2) {
      var away = hslToRgb(hsl.h, hsl.s, lAway);
      if (ratioOf(i, away) >= 4.5) return hexOf(away);
    }
    for (var k = Math.round(FIELD_LIFT * 100); k > 0; k--) {
      var c = hslToRgb(hsl.h, hsl.s, clamp(hsl.l + toward * k / 100, 0, 1));
      if (ratioOf(i, c) >= 4.5) return hexOf(c);
    }
    return hexOf(b);
  }

  function contrastPass(vars) {
    function eff(k) { return own(vars, k) ? vars[k] : (own(REFS, k) ? eff(REFS[k]) : DEFAULTS[k]); }
    function touched(a, b) { return own(vars, a) || own(vars, b) || (own(REFS, a) && own(vars, REFS[a])); }
    function set(k, v) { if (v && v !== eff(k)) vars[k] = v; }
    function single(text, surface) { if (touched(text, surface)) set(text, readable(eff(text), eff(surface))); }

    single('--oo-on-primary', '--oo-primary');
    single('--oo-on-accent', '--oo-accent');
    single('--oo-hero-ink', '--oo-hero-bg');
    single('--oo-header-ink', '--oo-header-bg');
    single('--oo-header-sub', '--oo-header-bg');
    if (touched('--oo-header-ink', '--oo-header-bg') && !(contrastRatio(eff('--oo-header-ink'), eff('--oo-header-field')) >= 4.5)) {
      set('--oo-header-field', fieldFor(eff('--oo-header-bg'), eff('--oo-header-ink')));
    }
    single('--oo-header-field-ink', '--oo-header-field');

    // A configured card can use light ink. Keep it when it already reads on that card, and adjust the loyalty
    // card's own labels instead of darkening every product heading to preserve the default white loyalty labels.
    var lightCardInk = own(vars, '--oo-card')
      && contrastRatio(eff('--oo-ink'), '#000000') > contrastRatio(eff('--oo-ink'), '#FFFFFF')
      && contrastRatio(eff('--oo-ink'), eff('--oo-card')) >= 4.5;
    if (lightCardInk) {
      single('--oo-muted', '--oo-card');
      ['--oo-on-card-dark', '--oo-on-card-dark-sub'].forEach(function (k) {
        set(k, readable(eff(k), eff('--oo-card-dark')));
      });
    } else {
      // Standard cards keep the existing darker-only pass, including the loyalty and canvas checks.
      [['--oo-ink', ['--oo-card', '--oo-on-card-dark', '--oo-on-card-dark-sub']], ['--oo-muted', ['--oo-card']]].forEach(function (x) {
        var k = x[0];
        if (!touched(k, '--oo-canvas') && !own(vars, '--oo-card')) return;
        var others = x[1].map(eff);
        var v = darkened(eff(k), [eff('--oo-canvas')].concat(others), 4.5);
        if (v === null) v = darkened(eff(k), others, 4.5);
        set(k, v);
      });
    }

    // Page text (ink / muted unless stepped here): toward whichever end reads better on the canvas. A page ink under
    // 3:1 starts from that end (REF's readable fallback), so a black canvas gets white titles rather than a dim grey.
    ['--oo-page-ink', '--oo-page-muted'].forEach(function (k) {
      if (!touched(k, '--oo-canvas')) return;
      var text = eff(k), canvas = eff('--oo-canvas');
      if (k === '--oo-page-ink' && !(contrastRatio(text, canvas) >= 3)) text = readableEnd(canvas) || text;
      set(k, readable(text, canvas));
    });
    return vars;
  }

  function onlineMode(mode) { return mode !== 'app' && mode !== 'qr'; }

  function toCssVars(theme, mode) {
    var t = theme || {};
    var vars = {};
    if (onlineMode(mode) && t.isConfigured === false) return vars; // the SIPO default (styles/tokens.css)
    function put(name, value) { if (value !== null && value !== undefined && value !== '') vars[name] = String(value); }

    var primary = t.primary || null;
    var onPrimary = t.onPrimary || (primary ? contrast(primary) : null);
    put('--oo-primary', primary);
    put('--oo-on-primary', onPrimary);

    // The app's themed Home paints its top bar with primary/onPrimary. Online ordering keeps the header colour. A
    // white-label header variant decides in every mode.
    var headerBg, headerInk, hv = t.headerVariant;
    if (hv === 'brand' && primary) {
      headerBg = primary; headerInk = onPrimary;
    } else if (hv === 'transparent' && t.canvas) {
      headerBg = t.canvas; headerInk = t.ink || contrast(t.canvas);
    } else if ((hv === 'solid' || hv === 'brand' || hv === 'transparent') && t.header) {
      headerBg = t.header; headerInk = t.onHeader || contrast(t.header);
    } else {
      headerBg = mode === 'app' ? (primary || t.header) : (t.header || null);
      headerInk = mode === 'app' && primary ? onPrimary : (t.onHeader || (headerBg ? contrast(headerBg) : null));
    }
    put('--oo-header-bg', headerBg);
    put('--oo-header-ink', headerInk);
    put('--oo-logo-bg', t.logoBackground === 'transparent' ? 'transparent' : normalizeColor(t.logoBackground));
    // The token default (#C8D0D0) suits any dark bar. A light bar needs a dark sub-text.
    if (headerBg && headerInk === '#000000') put('--oo-header-sub', 'rgba(0, 0, 0, .6)');

    var background = mode === 'app' ? appBackground(t.appBackground, t.canvas) : null;
    put('--oo-canvas', background ? background.color : t.canvas);
    if (background) put('--oo-app-background-image', background.image);
    put('--oo-ink', t.ink);
    put('--oo-card', t.surface);
    put('--oo-muted', t.muted);

    var accent = t.accent || primary;
    put('--oo-accent', accent);
    put('--oo-on-accent', t.accent ? (t.onAccent || contrast(t.accent)) : (accent ? onPrimary : null));
    put('--oo-accent-bar', t.accentBar || accent);

    put('--oo-hero-bg', t.heroBg);
    put('--oo-hero-ink', t.heroInk || (t.heroBg ? contrast(t.heroBg) : null));

    if (t.font) put('--oo-font', fontStack(t.font));
    if (t.headFont) put('--oo-head-font', headStack(t.headFont));
    if (t.headWeight !== null && t.headWeight !== undefined) put('--oo-head-weight', t.headWeight);
    if (t.scriptFont) put('--oo-script-font', scriptStack(t.scriptFont)); // WL-SUPER (set only for the Super design)
    // WL-VEL: the extra palette slots, only when the document sets them.
    put('--oo-surface-alt', normalizeColor(t.surfaceAlt));
    put('--oo-border', normalizeColor(t.border));
    put('--oo-field', normalizeColor(t.field));
    var radii = t.radius && own(RADII, t.radius) ? RADII[t.radius] : null;
    if (radii) R_KEYS.forEach(function (k, i) { put(k, radii[i] + 'px'); });
    var shadows = t.card && own(CARD_SHADOWS, t.card) ? CARD_SHADOWS[t.card] : null;
    if (shadows) Object.keys(shadows).forEach(function (k) { put(k, shadows[k]); });
    return contrastPass(vars);
  }

  // Only structured, bounded values can become a CSS background. Older themes retain their flat canvas.
  function appBackground(value, fallback) {
    if (!value || ['color', 'gradient', 'image'].indexOf(value.type) < 0) return null;
    function hex(v) { return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v.toUpperCase() : null; }
    var color = hex(value.color) || normalizeColor(fallback) || '#FFFFFF';
    var image = 'none';
    if (value.type === 'gradient') {
      var end = hex(value.endColor) || color;
      var angle = Number.isInteger(value.angle) && value.angle >= 0 && value.angle <= 360 ? value.angle : 135;
      image = 'linear-gradient(' + angle + 'deg, ' + color + ', ' + end + ')';
    } else if (value.type === 'image' && typeof value.image === 'string' && value.image.length <= 500 &&
      /^https:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?(?:[/?#]|$)/i.test(value.image) && !/[\s"'<>\\`\u0000-\u001F\u007F]/.test(value.image)) {
      var tint = Number.isInteger(value.tint) && value.tint >= 0 && value.tint <= 100 ? value.tint : 70;
      var wash = color + Math.round(tint * 2.55).toString(16).padStart(2, '0');
      image = 'linear-gradient(' + wash + ', ' + wash + '), url("' + value.image + '")';
    }
    return { color: color, image: image };
  }

  var applied = [];
  function apply(vars, el) {
    var target = el || (typeof document !== 'undefined' ? document.documentElement : null);
    vars = vars || {};
    if (!target || !target.style) return vars;
    applied.forEach(function (k) { if (!Object.prototype.hasOwnProperty.call(vars, k)) target.style.removeProperty(k); });
    Object.keys(vars).forEach(function (k) { target.style.setProperty(k, vars[k]); });
    applied = Object.keys(vars);
    return vars;
  }

  var SYSTEM_FONTS = [
    'system-ui', '-apple-system', 'blinkmacsystemfont', 'segoe ui', 'arial', 'helvetica', 'helvetica neue',
    'georgia', 'times', 'times new roman', 'courier', 'courier new', 'verdana', 'tahoma', 'trebuchet ms',
    'sans-serif', 'serif', 'monospace', 'cursive', 'fantasy', 'inherit', 'initial',
  ];
  var KNOWN_WEIGHTS = {
    'poppins': [400, 500, 600, 700], 'inter': [400, 500, 600, 700], 'space grotesk': [400, 500, 600, 700],
    'dm sans': [400, 500, 600, 700], 'albert sans': [400, 500, 600, 700], 'montserrat': [400, 500, 600, 700],
    'roboto': [400, 500, 700], 'open sans': [400, 600, 700], 'lato': [400, 700], 'nunito': [400, 600, 700],
    'playfair display': [400, 700], 'dm serif display': [400], 'archivo black': [400], 'bebas neue': [400],
  };
  var loadedFonts = {};

  function familyParam(family) { return encodeURIComponent(family).replace(/%20/g, '+'); }

  // The hrefs (lower case) of the page's Google Fonts links that load `family`.
  function linksFor(family) {
    var out = [];
    if (typeof document === 'undefined' || !document.querySelectorAll) return out;
    var needle = 'family=' + familyParam(family).toLowerCase();
    var links = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
    for (var i = 0; i < links.length; i++) {
      var href = String(links[i].href || (links[i].getAttribute && links[i].getAttribute('href')) || '').toLowerCase();
      if (href.indexOf(needle + ':') >= 0 || href.indexOf(needle + '&') >= 0 || href.slice(-needle.length) === needle) out.push(href);
    }
    return out;
  }
  function alreadyLinked(family) { return linksFor(family).length > 0; }

  // WL-SUPER: does one of those hrefs load `family` at weight w? css2 "family=X:wght@400;700" (a value may be a range
  // "100..900"; with more axes, "ital,wght@0,400;1,700" reads the wght column), css v1 "family=X:400,700italic", and
  // a family with no axis (400 only).
  function linkCovers(hrefs, family, w) {
    var needle = 'family=' + familyParam(family).toLowerCase();
    return hrefs.some(function (href) {
      var at = href.indexOf(needle);
      while (at >= 0) {
        var rest = href.slice(at + needle.length), next = rest.charAt(0);
        if (next === '' || next === '&' || next === '|') { if (w === 400) return true; }
        else if (next === ':') {
          var spec = rest.slice(1).split(/[&|]/)[0];
          try { spec = decodeURIComponent(spec); } catch (e) { /* keep it encoded */ }
          var parts = spec.split('@');
          if (parts.length === 1) { if ((spec.match(/\d{3}/g) || []).map(Number).indexOf(w) >= 0) return true; }
          else {
            var col = parts[0].split(',').indexOf('wght');
            if (col >= 0 && parts[1].split(';').some(function (tuple) {
              var v = tuple.split(',')[col] || '';
              var range = v.split('..').map(Number);
              return range.length === 2 ? w >= range[0] && w <= range[1] : range[0] === w;
            })) return true;
          }
        }
        at = href.indexOf(needle, at + needle.length);
      }
      return false;
    });
  }

  // WL-VEL: does one of those hrefs load `family`'s italic at weight w? Only css2 "ital,wght@…;1,w" tuples (a range too)
  // count; css v1 "family=X:400italic" too.
  function linkCoversItalic(hrefs, family, w) {
    var needle = 'family=' + familyParam(family).toLowerCase();
    return hrefs.some(function (href) {
      var at = href.indexOf(needle);
      while (at >= 0) {
        var rest = href.slice(at + needle.length);
        if (rest.charAt(0) === ':') {
          var spec = rest.slice(1).split(/[&|]/)[0];
          try { spec = decodeURIComponent(spec); } catch (e) { /* keep it encoded */ }
          var parts = spec.split('@');
          if (parts.length === 1) { if (spec.split(',').indexOf(w + 'italic') >= 0 || spec.split(',').indexOf(w + 'i') >= 0) return true; }
          else {
            var axes = parts[0].split(','), ci = axes.indexOf('ital'), cw = axes.indexOf('wght');
            if (ci >= 0 && parts[1].split(';').some(function (tuple) {
              var v = tuple.split(',');
              if (v[ci] !== '1') return false;
              if (cw < 0) return w === 400;
              var range = (v[cw] || '').split('..').map(Number);
              return range.length === 2 ? w >= range[0] && w <= range[1] : range[0] === w;
            })) return true;
          }
        }
        at = href.indexOf(needle, at + needle.length);
      }
      return false;
    });
  }

  // WL-SUPER: the extra link for a family the page links, with the requested weights it serves and no link covers.
  // WL-VEL: with italic (only ever true for an italic-capable family, below), the italics too: the upright weights no
  // link covers and the italic ones no link's `1,w` tuple covers, in one css2 `ital,wght` link (sorted tuples). Without
  // italic: exactly WL-SUPER's link.
  function addMissingWeights(f, key, weights, italic) {
    var served = WL_FONT_WEIGHTS[wlFont(f)] || KNOWN_WEIGHTS[key];
    if (!served || !weights || !weights.length) return false;
    var hrefs = linksFor(f);
    var missing = served.filter(function (w) { return weights.indexOf(w) >= 0 && !linkCovers(hrefs, f, w); });
    var slanted = italic === true && key !== 'archivo' && WL_ITALIC.indexOf(f) >= 0
      ? served.filter(function (w) { return weights.indexOf(w) >= 0 && !linkCoversItalic(hrefs, f, w); }) : [];
    if (!missing.length && !slanted.length) return false;
    var axis = ':wght@' + missing.join(';');
    if (slanted.length) axis = ':ital,wght@' + missing.map(function (w) { return '0,' + w; }).concat(slanted.map(function (w) { return '1,' + w; })).join(';');
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' + familyParam(f) + axis + '&display=swap';
    link.setAttribute('data-oo-font', f);
    document.head.appendChild(link);
    return true;
  }

  // A pure URL builder also used by the published website's initial HTML.
  function googleFontHref(family, weights, italic) {
    var f = normalizeFont(family);
    if (!f || SYSTEM_FONTS.indexOf(f.toLowerCase()) >= 0 || ['k kitchen body', 'cormorant garamond'].indexOf(f.toLowerCase()) >= 0) return null;
    var key = f.toLowerCase();
    var w = weights && weights.length ? weights : KNOWN_WEIGHTS[key];
    var axis = w && w.length ? ':wght@' + w.slice().sort(function (a, b) { return a - b; }).join(';') : '';
    // Archivo's width axis supplies the condensed market headings without distorting the logo or body text.
    if (key === 'archivo') axis = ':wdth,wght@75..100,400..900';
    // WL-VEL: upright and italic tuples, sorted (ital then wght), for a family in WL_ITALIC.
    if (italic === true && w && w.length && key !== 'archivo' && WL_ITALIC.indexOf(f) >= 0) {
      var ws = w.slice().sort(function (a, b) { return a - b; });
      axis = ':ital,wght@' + ws.map(function (x) { return '0,' + x; }).concat(ws.map(function (x) { return '1,' + x; })).join(';');
    }
    return 'https://fonts.googleapis.com/css2?family=' + familyParam(f) + axis + '&display=swap';
  }

  function loadGoogleFont(family, weights, italic) {
    var f = normalizeFont(family);
    if (!f) return false;
    var key = f.toLowerCase();
    if (SYSTEM_FONTS.indexOf(key) >= 0 || ['k kitchen body', 'cormorant garamond'].indexOf(key) >= 0 || loadedFonts[key] === true) return false;
    if (typeof document === 'undefined' || !document.createElement || !document.head) return false;
    // WL-SUPER: a family the page links (not one this loader linked: that stays once) may still lack requested weights.
    if (loadedFonts[key] === 'page' || alreadyLinked(f)) { loadedFonts[key] = 'page'; return addMissingWeights(f, key, weights, italic); } // WL-VEL: + italic
    loadedFonts[key] = true;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = googleFontHref(f, weights, italic);
    link.setAttribute('data-oo-font', f);
    document.head.appendChild(link);
    return true;
  }

  function fontsOf(theme) {
    var out = [];
    [theme && theme.font, theme && theme.headFont, theme && theme.scriptFont].forEach(function (f) { // WL-SUPER: + scriptFont
      var n = normalizeFont(f);
      if (n && out.indexOf(n) < 0) out.push(n);
    });
    return out;
  }

  function fontWeights(theme, family) {
    if (!theme || theme.whitelabel !== true) return null;
    var f = wlFont(family);
    if (!f) return null;
    var want = [400, 500, 600, 700];
    var hw = Number(theme.headWeight);
    if (isFinite(hw) && want.indexOf(hw) < 0) want.push(hw);
    // WL-CHK: the body family's extra weights (bodyWeights), never another family's.
    if (Array.isArray(theme.bodyWeights) && wlFont(theme.font) === f) theme.bodyWeights.forEach(function (w) { if (want.indexOf(w) < 0) want.push(w); });
    // WL-VEL: the display family's extra weights (headWeights), never another family's.
    if (Array.isArray(theme.headWeights) && wlFont(theme.headFont) === f) theme.headWeights.forEach(function (w) { if (want.indexOf(w) < 0) want.push(w); });
    return WL_FONT_WEIGHTS[f].filter(function (w) { return want.indexOf(w) >= 0; });
  }
  // WL-VEL: whether `family` is loaded with its italics too (the theme's display family under headItalic).
  function fontItalic(theme, family) {
    if (!theme || theme.whitelabel !== true || theme.headItalic !== true) return false;
    var f = wlFont(family);
    return !!f && f === wlFont(theme.headFont) && WL_ITALIC.indexOf(f) >= 0;
  }

  // The design's three white-label app themes (docs/online-order/design/customer-app.dc.txt THEMES). Their
  // onAccent values are the design's own picks, not YIQ.
  var PRESETS = {
    bennys: {
      key: 'bennys', label: 'Theme A · Bennys Burger', brand: 'Bennys Burger',
      primary: '#C8102E', onPrimary: '#FFFFFF', header: '#C8102E', onHeader: '#FFFFFF',
      accent: '#FFC72C', onAccent: '#1A1A1A', accentBar: '#C8102E',
      canvas: '#FFF6E9', onCanvas: '#000000', ink: '#1A1A1A', heroBg: '#1A1A1A', heroInk: '#FFFFFF',
      font: null, headFont: 'Archivo Black', headWeight: 400,
    },
    nonnas: {
      key: 'nonnas', label: "Theme B · Nonna's Pizzeria", brand: "Nonna's",
      primary: '#1E5B3A', onPrimary: '#FFFFFF', header: '#1E5B3A', onHeader: '#FFFFFF',
      accent: '#E4572E', onAccent: '#FFFFFF', accentBar: '#E4572E',
      canvas: '#F7F1E6', onCanvas: '#000000', ink: '#1F2A22', heroBg: '#FFFFFF', heroInk: '#1F2A22',
      font: null, headFont: 'DM Serif Display', headWeight: 400,
    },
    harbour: {
      key: 'harbour', label: 'Theme C · Harbour Coffee', brand: 'HARBOUR',
      primary: '#14213D', onPrimary: '#FFFFFF', header: '#14213D', onHeader: '#FFFFFF',
      accent: '#F2A541', onAccent: '#14213D', accentBar: '#F2A541',
      canvas: '#EEF1F6', onCanvas: '#000000', ink: '#14213D', heroBg: '#F2A541', heroInk: '#14213D',
      font: null, headFont: 'Space Grotesk', headWeight: 700,
    },
  };

  function resolve(theme, key) {
    var k = String(key || '').toLowerCase().trim();
    if (!k || !Object.prototype.hasOwnProperty.call(PRESETS, k)) return theme || {};
    var merged = Object.assign({}, theme || {});
    var preset = PRESETS[k];
    Object.keys(preset).forEach(function (f) { if (preset[f] !== null && preset[f] !== undefined) merged[f] = preset[f]; });
    merged.isConfigured = true; // a chosen preset is a theme of its own, on every channel
    return merged;
  }

  OO.theme = OO.lib.theme = {
    normalizeColor: normalizeColor,
    contrast: contrast,
    contrastRatio: contrastRatio,
    readable: readable,
    normalizeFont: normalizeFont,
    dataUri: dataUri,
    imageSrc: imageSrc,
    appBackground: appBackground,
    isConfigured: isConfigured,
    fromRaw: fromRaw,
    toCssVars: toCssVars,
    apply: apply,
    loadGoogleFont: loadGoogleFont,
    googleFontHref: googleFontHref,
    fontsOf: fontsOf,
    fontWeights: fontWeights,
    fontItalic: fontItalic, // WL-VEL
    wlFont: wlFont,
    WL_FONTS: WL_FONTS,
    RADII: RADII,
    CARD_SHADOWS: CARD_SHADOWS,
    PRESETS: PRESETS,
    resolve: resolve,
    PAIRS: PAIRS,
    DEFAULTS: DEFAULTS,
    REFS: REFS,
  };
})();


// ===================== src/lib/model.jsx =====================
// src/lib/model.jsx: OO.model core; model-*.jsx add the normalisers from RAW API responses (API-CONTRACT.md
// shapes, quirks included) to the view-models the pages render. No JSX, no fetching, no DOM. Each normaliser
// takes an injectable `now`, so availability and opening hours are testable.
//
// `now` (in every opts) is a Date | epoch ms | 'Y-m-d H:i:s' store wall clock | Clock (OO.format.clock). Without one,
// the normalisers use the DEVICE clock (new Date()). getRestaurant's current_time is never used as "now": the backend
// formats it in the REQUEST's timeZone (metadataController@getRestaurant; Next sends 'UTC'), not the store's, so read
// as a store wall clock it is off by the offset. Store.serverTime keeps it as sent, for display / debugging only.
// `domain` is the availability domain (§3.2; see domain() below): 'ONLINE_PICKUP' (default) | 'ONLINE_DELIVERY' |
// 'ONLINE_APP_PICKUP' | 'ONLINE_APP_DELIVERY' | 'TABLE_SIDE' | 'KIOSK'. OO.data.loadHotel passes ONLINE_APP_PICKUP in
// app mode.
//
// Availability rules (§3.2 AvailabilityRule; REF shared/menu/lib/availabilityService.ts, inventory B §1.1). The engine
// reads a store Clock (storeNow / OO.format.clock: ymd, dow, minutes in the STORE timezone, as the BE does; REF uses the
// device's) and never the device clock.
//   Parsing: a JSON string (double-encoded ok) or an array of rule objects; anything else = no rules. Rule aliases:
//     DM|dm|domains, D|d|days (numbers or day names → '0'–'6', Sunday = 0), H|h|timings (windows with
//     S|start|start_time|startTime|from and E|end|end_time|endTime|to: 'H:mm', 'HH:mm[:ss]', an optional am/pm, or
//     minutes; '24:00' allowed), SD|sd|from and ED|ed|to (dates). type 'unavailable' makes a BLACKOUT rule (a product
//     pause is one: RID 'pz_…', SD = ED, an H window). Junk never throws: unreadable rules, list entries, windows and
//     dates are dropped, and a list left empty is no limit.
//   One rule matches at a minute of the clock's day when: its DM (if any) names the domain; today ≥ SD and today ≤ ED
//     (either alone is fine; both inclusive); its D (if any) holds today's dow; some H window (if any) holds the
//     minute. A window with E < S wraps past midnight (cur ≥ S || cur ≤ E), judged on TODAY's dow: a Friday
//     22:00–02:00 rule covers Friday 01:00, not Saturday 01:00 (REF). '24:00' = to the end of the day.
//   The entity: no rules → available; any matching blackout → unavailable (it vetoes every positive rule; bug 2); no
//     positive rules → available; positive rules carry DM but none names the domain → unavailable (REF and our Next
//     port; the BE would still pass a rule without DM); else available = some positive rule matches.
//   Domains: both sides are normalised (normalizeDomain). On the plain web (ONLINE_PICKUP / ONLINE_DELIVERY) a rule's
//     generic WEB or ONLINE also matches; never for the app, table or kiosk domains.
//
// API:
//   isAvailable(rules, clock, domain) → bool
//   availabilityInfo(rules, clock, domain) → { available, today, restricted, blackout, reason }
//       today = available at some minute of the clock's day (now included).
//       restricted = day/date-limited: every positive rule for the domain has SD, ED or a D list short of 7 days
//       (false when the DM gate shuts the domain out).
//       blackout = a blackout rule vetoes it now. reason = why it is unavailable now, first that applies:
//       'blackout' › 'domain' (DM gate) › 'dateRange' (every positive rule for the domain is outside SD/ED) › 'time'
//       (day or hour); null when available.
//   parseRules(v) → AvailabilityRule[] = { blackout, RID, DM:[normalised domain], D:['0'–'6'], H:[{ S, E }] (minutes),
//       SD, ED: 'YYYY-MM-DD' | null }. An empty list = no limit. Idempotent (its output parses to itself).
//   normalizeDomain(s) → trim, [\s-]+ → '_', upper-case: 'online pickup' → 'ONLINE_PICKUP'.
//   storeNow(store, now) → Clock in the store's timezone.
//   domain(orderType, mode = OO.mode, opts) → the availability / coupon domain, first that applies: 'KIOSK' (mode
//       'kiosk') › 'TABLE_SIDE' (mode 'qr' or opts.table) › 'ONLINE_APP_DELIVERY' | 'ONLINE_APP_PICKUP' (mode 'app')
//       › 'ONLINE_DELIVERY' | 'ONLINE_PICKUP'. Delivery = an orderType of 'DELIVERY' or '…_DELIVERY' (any case).
//   resolveLink(link) → { kind:'url', href } | { kind:'route', page, params, query } | null
//   resolveImage(path) → src | null                 (= OO.format.imageUrl)
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});

  function F() { return OO.format; }

  // ---- small coercions ---------------------------------------------------------------------------
  function num(v) { var n = typeof v === 'string' ? parseFloat(v) : Number(v); return isFinite(n) ? n : 0; }
  function numOrNull(v) { if (v === null || v === undefined || v === '') return null; var n = Number(v); return isFinite(n) ? n : null; }
  function str(v) { return v === null || v === undefined || v === 'null' ? '' : String(v); }
  function flag(v) { return v === true || v === 1 || v === '1' || v === 'true'; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function parseJson(v, fallback) {
    if (typeof v !== 'string') return v === undefined ? fallback : v;
    var s = v.trim();
    if (!s || s === 'null') return fallback;
    try { return JSON.parse(s); } catch (e) { return fallback; }
  }
  // discount_meta may be double-encoded: '"{\"price_tag\":3}"'.
  function parseDeep(v) {
    var out = v;
    for (var i = 0; i < 3 && typeof out === 'string'; i++) out = parseJson(out, null);
    return out;
  }
  function ribbon(v) { var s = str(v).trim(); return s && s.toLowerCase() !== 'null' ? s : null; }
  // A store-provided URL that pages may use as an href: http(s) only (no javascript:, data:, …), else null.
  function httpUrl(v) { var s = str(v).trim(); return /^https?:\/\/[^\s]+$/i.test(s) ? s : null; }
  function slug(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }

  // Instant (ms) from an epoch (s or ms), an ISO string (any fraction length) or a UTC 'Y-m-d H:i:s'.
  function parseInstant(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return v < 1e11 ? v * 1000 : v;
    var s = String(v).trim();
    if (/^\d+$/.test(s)) return parseInstant(Number(s));
    s = s.replace(/(\.\d{3})\d+/, '$1');
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s)) s = s.replace(' ', 'T') + 'Z';
    var t = Date.parse(s);
    return isNaN(t) ? null : t;
  }

  // ---- now / clock -------------------------------------------------------------------------------
  // → { clock, instant, offset }. offset = minutes to add to UTC to get the store wall clock.
  function resolveNow(now, tz) {
    var f = F();
    if (now && typeof now === 'object' && !(now instanceof Date) && typeof now.ymd === 'string') {
      var inst = f.instantOf(now.ymd, now.minutes, tz) + (now.s || 0) * 1000;
      return { clock: now, instant: inst, offset: f.tzOffsetMinutes(new Date(inst), tz) };
    }
    if (typeof now === 'string' && /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}(:\d{2})?)?$/.test(now.trim())) {
      var c = f.parseWallClock(now.trim());
      var i2 = f.instantOf(c.ymd, c.minutes, tz) + c.s * 1000;
      return { clock: c, instant: i2, offset: f.tzOffsetMinutes(new Date(i2), tz) };
    }
    var d = now == null ? new Date() : (now instanceof Date ? now : new Date(typeof now === 'number' ? now : Date.parse(now)));
    return { clock: f.zoned(d, tz), instant: d.getTime(), offset: f.tzOffsetMinutes(d, tz) };
  }
  function storeNow(store, now) { return resolveNow(now, store && store.timezone).clock; }

  // ---- availability (§3.2 AvailabilityRule; REF availabilityService.ts; the rules are in the header) --------------
  var WEB_DOMAINS = ['ONLINE_PICKUP', 'ONLINE_DELIVERY']; // the plain web: generic WEB / ONLINE rules match too
  var DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  function normalizeDomain(s) { return str(s).trim().replace(/[\s-]+/g, '_').toUpperCase(); }
  // The DM entries a requested domain answers to: itself, plus WEB and ONLINE on the plain web.
  function domainSet(dom) {
    var d = normalizeDomain(dom) || 'ONLINE_PICKUP';
    return WEB_DOMAINS.indexOf(d) >= 0 ? [d, 'WEB', 'ONLINE'] : [d];
  }

  // A rule list or list field: an array, or a JSON array string (double-encoded ok). Anything else is no list.
  function listOf(v) { var x = parseDeep(v); return Array.isArray(x) ? x : []; }
  // The value of the first alias present (null, '' and 'null' count as absent).
  function pick(o, keys) {
    for (var i = 0; i < keys.length; i++) {
      var v = o[keys[i]];
      if (v !== null && v !== undefined && v !== '' && v !== 'null') return v;
    }
    return null;
  }
  function uniq(list) { return list.filter(function (x, i) { return x !== null && x !== '' && list.indexOf(x) === i; }); }
  // 0 | '6' | 'Sun' | 'wednesday' | 'Thurs' → '0'–'6' (Sunday = 0); else null.
  function dayOf(v) {
    var s = str(v).trim().toLowerCase().replace(/\.$/, '');
    if (/^\d+$/.test(s)) return Number(s) <= 6 ? String(Number(s)) : null;
    for (var i = 0; s.length >= 3 && i < 7; i++) if (DAY_NAMES[i].indexOf(s) === 0) return String(i);
    return null;
  }
  // '9:00' | '09:00' | '17:30:00' | '5:30 pm' | minutes → 0..1440 ('24:00' = 1440); else NaN.
  function minuteOf(v) {
    if (typeof v === 'number') return isFinite(v) && v >= 0 && v <= 1440 ? Math.floor(v) : NaN;
    var m = /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([ap])\.?m\.?)?$/i.exec(str(v).trim());
    if (!m) return NaN;
    var h = Number(m[1]), mi = Number(m[2]);
    if (m[3]) {
      if (h < 1 || h > 12) return NaN;
      h = (h % 12) + (m[3].toLowerCase() === 'p' ? 12 : 0);
    }
    return mi <= 59 && (h < 24 || (h === 24 && mi === 0)) ? h * 60 + mi : NaN;
  }
  // '2026-10-02' | '2026-10-2 00:00:00' | '2026-10-02T…' → '2026-10-02' (the date as written); else null.
  function dateOf(v) {
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str(v).trim());
    return m ? m[1] + '-' + ('0' + m[2]).slice(-2) + '-' + ('0' + m[3]).slice(-2) : null;
  }
  function normalizeRule(r) {
    var hours = [];
    listOf(pick(r, ['H', 'h', 'timings'])).forEach(function (w) {
      if (!w || typeof w !== 'object') return;
      var s = minuteOf(pick(w, ['S', 'start', 'start_time', 'startTime', 'from']));
      var e = minuteOf(pick(w, ['E', 'end', 'end_time', 'endTime', 'to']));
      if (!isNaN(s) && !isNaN(e)) hours.push({ S: s, E: e });
    });
    return {
      blackout: r.blackout === true || str(r.type).trim().toLowerCase() === 'unavailable',
      RID: str(r.RID) || null,
      DM: uniq(listOf(pick(r, ['DM', 'dm', 'domains'])).map(function (x) { return typeof x === 'string' ? normalizeDomain(x) : ''; })),
      D: uniq(listOf(pick(r, ['D', 'd', 'days'])).map(dayOf)),
      H: hours,
      SD: dateOf(pick(r, ['SD', 'sd', 'from'])),
      ED: dateOf(pick(r, ['ED', 'ed', 'to'])),
    };
  }
  function parseRules(v) {
    return listOf(v).filter(function (x) { return x && typeof x === 'object' && !Array.isArray(x); }).map(normalizeRule);
  }

  function domainOk(r, doms) { return !r.DM.length || r.DM.some(function (d) { return doms.indexOf(d) >= 0; }); }
  function datesOk(r, ymd) { return (!r.SD || ymd >= r.SD) && (!r.ED || ymd <= r.ED); }
  function inWindow(w, cur) { return w.E < w.S ? (cur >= w.S || cur <= w.E) : (cur >= w.S && cur <= w.E); }
  // One rule at minute `cur` of the clock's day (the clock gives the date and the dow).
  function ruleMatches(r, clock, cur, doms) {
    if (!domainOk(r, doms) || !datesOk(r, clock.ymd)) return false;
    if (r.D.length && r.D.indexOf(String(clock.dow)) < 0) return false;
    return !r.H.length || r.H.some(function (w) { return inWindow(w, cur); });
  }
  // The entity at minute `cur` of the clock's day → { available, reason } (the order is the header's).
  function decide(list, clock, cur, doms) {
    var positive = [];
    for (var i = 0; i < list.length; i++) {
      if (!list[i].blackout) positive.push(list[i]);
      else if (ruleMatches(list[i], clock, cur, doms)) return { available: false, reason: 'blackout' };
    }
    if (!positive.length) return { available: true, reason: null };
    var withDm = positive.filter(function (r) { return r.DM.length; });
    if (withDm.length && !withDm.some(function (r) { return domainOk(r, doms); })) return { available: false, reason: 'domain' };
    if (positive.some(function (r) { return ruleMatches(r, clock, cur, doms); })) return { available: true, reason: null };
    var eligible = positive.filter(function (r) { return domainOk(r, doms); }); // never empty past the DM gate
    return { available: false, reason: eligible.every(function (r) { return !datesOk(r, clock.ymd); }) ? 'dateRange' : 'time' };
  }
  function dayLimited(r) { return !!(r.SD || r.ED) || (r.D.length > 0 && r.D.length < 7); }

  function isAvailable(rules, clock, domain) {
    var list = parseRules(rules);
    return !list.length || decide(list, clock, clock.minutes, domainSet(domain)).available;
  }
  function availabilityInfo(rules, clock, domain) {
    var list = parseRules(rules);
    if (!list.length) return { available: true, today: true, restricted: false, blackout: false, reason: null };
    var doms = domainSet(domain);
    var now = decide(list, clock, clock.minutes, doms);
    // Within a day the answer only changes at a window's start or just after its end, so testing those minutes (and
    // midnight) tells whether ANY minute of today is available. The caller's clock is never modified.
    var today = now.available;
    var marks = [0];
    list.forEach(function (r) { r.H.forEach(function (w) { marks.push(w.S, w.E + 1); }); });
    for (var i = 0; !today && i < marks.length; i++) today = marks[i] < 1440 && decide(list, clock, marks[i], doms).available;
    var positive = list.filter(function (r) { return !r.blackout; });
    var eligible = positive.filter(function (r) { return domainOk(r, doms); });
    var gated = positive.some(function (r) { return r.DM.length; }) && !eligible.some(function (r) { return r.DM.length; });
    return {
      available: now.available,
      today: today,
      restricted: !gated && eligible.length > 0 && eligible.every(dayLimited),
      blackout: now.reason === 'blackout',
      reason: now.reason,
    };
  }

  function domain(orderType, mode, opts) {
    var m = mode === undefined ? OO.mode : mode;
    if (m === 'kiosk') return 'KIOSK';
    if (m === 'qr' || (opts && opts.table)) return 'TABLE_SIDE';
    var delivery = /(^|_)DELIVERY$/.test(normalizeDomain(orderType));
    return (m === 'app' ? 'ONLINE_APP_' : 'ONLINE_') + (delivery ? 'DELIVERY' : 'PICKUP');
  }

  // ---- misc ----------------------------------------------------------------------------------------
  var APP_PAGES = ['menu', 'cart', 'checkout', 'orders', 'profile', 'home', 'stores', 'rewards', 'booking', 'notifications', 'account',
    'wallet', 'track', 'bookings', 'about', 'coupons', 'deals']; // P3-01: the Phase 3 pages links can name
  // Banner/popup/notification links: 'https://…' (external), a deep link ('?type=combo&id=9', 'type=…',
  // 'combo&id=9') or an in-app page ('/rewards', 'rewards').
  function resolveLink(link) {
    var s = str(link).trim();
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) return { kind: 'url', href: s };
    var q = s.replace(/^\/+/, '');
    var page = q.split(/[?&#]/)[0].toLowerCase();
    if (APP_PAGES.indexOf(page) >= 0 && !/^(product|combo|category|cart)&/i.test(q)) {
      var qs = q.indexOf('?') >= 0 ? OO.router && OO.router.parseQuery ? OO.router.parseQuery(q.slice(q.indexOf('?'))) : {} : {};
      return { kind: 'route', page: page, params: {}, query: qs };
    }
    var dl = q.replace(/^\?/, '').replace(/^type=/i, '');
    var m = /^(product|combo|category|cart)(?:&(.*))?$/i.exec(dl);
    if (m) {
      var rest = OO.router && OO.router.parseQuery ? OO.router.parseQuery(m[2] || '') : {};
      var query = { type: m[1].toLowerCase() };
      if (rest.id) query.id = rest.id;
      return { kind: 'route', page: 'menu', params: {}, query: query };
    }
    return null;
  }

  OO.model = {
    isAvailable: isAvailable,
    availabilityInfo: availabilityInfo,
    parseRules: parseRules,
    normalizeDomain: normalizeDomain,
    storeNow: storeNow,
    resolveLink: resolveLink,
    resolveImage: function (p) { return F().imageUrl(p); },
    parseInstant: parseInstant,
    domain: domain,
    // internal: shared by the model-*.jsx files only; pages never read it
    _: {
      F: F, num: num, numOrNull: numOrNull, str: str, flag: flag, arr: arr, parseJson: parseJson, parseDeep: parseDeep,
      ribbon: ribbon, httpUrl: httpUrl, slug: slug, parseInstant: parseInstant, resolveNow: resolveNow,
      isAvailable: isAvailable, availabilityInfo: availabilityInfo, resolveLink: resolveLink, normalizeDomain: normalizeDomain,
    },
  };
})();


// ===================== src/lib/model-store.jsx =====================
// src/lib/model-store.jsx: OO.model's store normalisers, on the model.jsx core (its `now` / domain notes apply). API:
//   normalizeStore(rawRestaurant, {now}) → Store            (POST getRestaurant, §3.1; also each getRestaurantBulk entry)
//   openStatus / pickupOptions / slotInstant are thin wrappers over the scheduling engine OO.schedule
//   (src/lib/schedule.jsx, Batch 2 P2-22), which holds their logic; this file parses the raw fields they read.
//   openStatus(store, now, type='OPENING') → { isOpen, label, closesAt, opensAt, reason } (+ opensOn: the ymd of the
//       next opening, when closed). label: 'Open until 10pm' · 'Opens 12:30pm' (later today) · 'Opens tomorrow
//       12:30pm' · 'Opens Sun 12:30pm' (2–7 days ahead) · 'Temporarily closed' (a closure) · 'Not taking online
//       orders' · 'Closed'.
//   pickupOptions(store, now, {orderType}) → { days:[{key, date, label, slots}], slots, asapLabel, cookingTimeMins }
//       (no ASAP slot when a closure covers now + the buffer)
//   slotInstant(store, slot, now) → ms: ASAP → now; a scheduled slot → its store-local deliveryTime
//   storeImage(v, ownerId) → src | null: the store image rules below (every Store image goes through them)
//
// normalizeStore reads the store from data[0] (or data.restaurant). Any other body (a 404 page, {status:false}, null)
// gives a Store of defaults and never throws. The rules (REF develop: A §4 R-STORE-1, R-THEME-1; C §3.4; B §3.4, §4.0):
//   online.active  hotelStatus[0].active › online_order_active › true. hotelStatus is the raw DB switch. During an
//                  active scheduled closure the BE zeroes online_order_active (and pickup / delivery) in the payload
//                  only; `closures` then blocks the closure's own time (openStatus: "Temporarily closed" + reason).
//   orderTypes     PICKUP / DELIVERY stay offered while an active scheduled closure with a reason disables them (the BE
//                  then sends pickup|delivery 0 plus pickup|delivery_inactive_reason), so the UI can say "closed until …".
//   closures       upcomming_scheduled_disable_data (sic) › upcoming_scheduled_disable_data › scheduled_disable_data ›
//                  scheduled_closures (the first non-empty one). Epochs in seconds or ms. domain: OO.model.normalizeDomain
//                  (trim, [\s-]+ → _, upper-case), then ONLINE[_APP]_PICKUP → PICKUP and ONLINE[_APP]_DELIVERY → DELIVERY.
//   defaults       couponsEnabled true (coupon_enabled › coupon_enable), tableOrdering true (enable_table_side_ordering),
//                  currencyCode 'NZD', timezone 'Pacific/Auckland' (also for a zone Intl can't read).
//   terms          terms_condition › terms_of_use; the string "null" is empty. getRestaurant has no privacy policy, so
//                  terms.privacy is null.
//   banners/popups data[0].banner / .popup (StorePromoSettings): kept when enabled and is_active (absent = on; REF
//                  isSipoEnabled, so 0 / '0' are off; the server computes is_active in the store tz when it answers).
//                  rules = their availability (OO.model.parseRules; [] = always), which the menu overlays re-check at
//                  the menu's moment (P2-29, REF useAvailableRestaurantBanners). linkRaw = the link as sent (a
//                  popup's button_link), link = OO.model.resolveLink of it.
//   memberLoginPrompt member_login_prompt from data[0], else the body's root, else an object `data` (REF
//                  firstPresent); false when absent.
//   storeLogoUrl   shop_logo › logo › hotel_logo › store_logo (store profile, independent of the old theme builder).
//   logoUrl        storeLogoUrl › the theme's header_logo (legacy app / QR compatibility).
//   images         storeImage: http(s), //, data: and blob: pass through (every captured store image is absolute). A raw
//                  base64 blob gets the data: prefix its magic bytes name (OO.theme.dataUri). A store-media path
//                  ('user_{id}_image/{file}', or a bare '{file}.png|jpg|…' of this store) → the Linode objects URL
//                  https://sipomedia.ap-south-1.linodeobjects.com/user_{id}_image/{file} (REF restaurant-media.ts). Any
//                  other relative path → OO.format.imageUrl (the API host, which is this origin; the fixtures' rule).
//                  No capture holds a relative store image: those two rules have no real-data coverage yet.
//
// Store = { id, onlineKey, name, address:{street, city, postcode, country, full, short}, phone, website (http(s) |
//   null), description, mapLink (http(s) | null), lat, lng (numbers | null; the Distance Matrix origin), timezone,
//   currencyCode, currencySymbol (currency_symbol as sent; default ''), currency (the UI symbol: currencySymbol, else
//   the code's: '$' for NZD), logoUrl, bannerUrl, taxRate (default 15), taxInclusive, hideTaxLabel,
//   surcharge:{enabled, amount, percent} (as sent), surcharges:[{type:'fixed'|'percentage', value, rules}] (the ENABLED
//   configs: a raw `surcharges` array [{enabled, type, value, availability}] when the API sends one; else
//   surcharge_amount → fixed and surcharge_percent → percentage while surcharge_enable, each only if > 0, rules []),
//   couponsEnabled, loyaltyProductLevel (P2-06: the store redeems wallet points on product rewards only, wallet type 2;
//   REF isProductLevelLoyaltyEnabled: the first flag of loyalty_enable_product_level, loyalty_product_level_enabled,
//   loyaltyEnableProductLevel, loyality_enable_product_level (sic, getRestaurant's); default false; OO.wallet reads it),
//   applyDiscountToModifiers (B13 apply_discount_to_modifiers: the store's GlobalPreference the checkout
//   validator prices with, a line's discount % netting its modifiers too; OO.pricing mirrors it; default false),
//   minimumAge (the age gate's minimum, REF resolveMinimumAge: the first of minimum_age, min_age, age_limit, av_age
//   that is a number in (0, 130), else null; the item sheet then uses 18; P2-21),
//   orderTypes:['PICKUP'|'DELIVERY'|'DINEIN'], modesLabel ('Pickup · Dine-in'), booking
//   (table_reservation), tableOrdering, cookingTimeMins (pickup_buffer_time, default 15), deliveryTimeMins,
//   online:{active, reason}, open:{isOpen, label, closesAt, opensAt, reason}, hours:{OPENING|PICKUP|DELIVERY: {dow:
//   [{start, end}]}} (local minutes at the load-time UTC offset; end > 1440 = past midnight), hoursUtc (the same
//   shifts in UTC minutes, as sent: OO.schedule converts them per day, at that day's offset), holidays:[ymd],
//   holidayLabels:{ymd: label} (a shop_holidays row's label | name | reason | title, when it has one),
//   closures:[{start, end (epoch ms), domain, reason}], scheduling:{preorder (is_preorder), preorderDays (pre_min_day,
//   a whole number ≥ 0), orderLater (order_later › order_for_later: the first one sent, REF IDX:2273), orderLaterDays
//   (order_later_days, whole ≥ 0)}: OO.schedule.window's inputs (settings.orderLater / orderLaterDays keep the raw
//   order_later / order_later_days),
//   paymentMethods:[{key:'ONLINE_PAYMENT'|'PAY_AT_COUNTER'|'CASH', label, gateway, gatewayId, typeId, orderTypes,
//     position, isDefault [, gateways | types]}]: the checkout's methods, one per key, each with every option of its
//     key (ONLINE_PAYMENT's gateways, the counters' types: paymentMethods below; P2-27),
//   paymentOptions:[{id, kind:'counter'|'gateway', name, label, gateway, gatewayId, position, isDefault,
//     channels:{web, pickup, delivery, tableSide, kiosk}, orderTypes}]: the unified payment_methods list, data only
//     (paymentOptions below; the checkout's choices come from paymentMethods),
//   deliveryZones:[{id, name, minimumOrder, fee, radiusKm (zone_area, km), type ('circle'), orderOutside, lat, lng}]
//     (deleted zones dropped; sorted by radiusKm, ties in API order, as REF's zone selection sorts them),
//   theme (OO.theme Theme), banners:[{id, image, title, tag, subtitle, cta, link, linkRaw, rules}], popups:[{id, image,
//   text, buttonName, link, linkRaw, rules}] (P2-29: banners and popups below), alert:{enabled, text, image},
//   checkoutAlert:{PICKUP, DELIVERY} (P2-24, REF's checkout alert: checkout_alert_pickup | _delivery as text while
//   is_checkout_alert_pickup | _delivery is on, else null; the checkout confirms it before Pay),
//   terms:{terms, use (terms_of_use as sent, '' for none: P3-25's "Terms of use"), privacy (null), allergy} (HTML or a URL),
//   settings:{showSizePrice, categoryBar, poweredBy, forceLoginForOrdering, forceLoginForCoupon, memberLoginPrompt,
//   orderLater, orderLaterDays, itemsSorting, onlinePayment (online_payment_enable), tableUserForm (is_user_form_table_side:
//   at a table, REF's guest-count form and mandatory contact)}, relatedBranches:[{id, name, onlineKey,
//   addressFull}], serverTime (getRestaurant current_time as sent: request-timezone, display / debug only),
//   reviewSettings:{storeRating, productRating} (P3-07: review_settings, OO.model.restaurantReviewSettings: whether the
//   store's and the products' average ratings show; absent = both on, REF),
//   loyalty:{programName, pointsWord},
//   menuLayout 'categories'|'sections' (MENU-LAYOUT, the owner's decision of 2026-10-06: the menu page's layout, which
//   the route's ?layout= overrides, OO.model.menuLayout): data[0].proposed.menu_layout when it is exactly 'categories'
//   or 'sections' (PROPOSED); else the admin's Online-settings "Auto Menu Popup" toggle, data[0].menu_popup_auto: on
//   (1, '1', true, 'true') → 'categories' (the category tiles); else 'sections' (the full menu: the category tab bar
//   and every category's items in sections): off, null or absent (no online_order_settings row: the BE sends 0),
//   PROPOSED (data[0].proposed; see BATCH2-GAPS.md): categoryStyle 'image'|'text', homeModules
//   [{key:'slider'|'loyalty'|'actions'|'reorder'|'popular', enabled}], checkout:{priceSummary 'inline'|'collapsed',
//   loginStyle 'card'|'solid'}, pickup (pickupOptions at load time) }
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var _ = OO.model._, F = _.F, num = _.num, numOrNull = _.numOrNull, str = _.str, flag = _.flag, arr = _.arr, parseJson = _.parseJson, httpUrl = _.httpUrl, resolveNow = _.resolveNow, resolveLink = _.resolveLink, parseInstant = _.parseInstant;

  // ---- hours (parsed here; evaluated by OO.schedule) ---------------------------------------------
  // workingInfo running_hours are UTC 'HH:mm:ss'; each endpoint is converted on its own (an Auckland 7am–4pm
  // day is 18:00 → 03:00 UTC), and end <= start means the shift runs past local midnight.
  function hoursFrom(workingInfo, offset) {
    var out = { OPENING: {}, PICKUP: {}, DELIVERY: {} };
    var f = F();
    arr(workingInfo).forEach(function (day) {
      if (!day || flag(day.is_closed)) return;
      var dow = num(day.day);
      arr(day.hotelDayInfo).forEach(function (info) {
        var type = String(info && info.type || '').toUpperCase();
        if (!type) return;
        if (!out[type]) out[type] = {};
        var list = out[type][dow] || (out[type][dow] = []);
        arr(info.running_hours).slice().sort(function (a, b) { return num(a.order) - num(b.order); }).forEach(function (rh) {
          var s0 = f.toMinutes(rh.start), e0 = f.toMinutes(rh.stop);
          if (isNaN(s0) || isNaN(e0)) return;
          var s = (((s0 + offset) % 1440) + 1440) % 1440;
          var e = (((e0 + offset) % 1440) + 1440) % 1440;
          if (e <= s) e += 1440;
          list.push({ start: s, end: e });
        });
        list.sort(function (a, b) { return a.start - b.start; });
      });
    });
    return out;
  }

  // ---- store -----------------------------------------------------------------------------------
  var MODULE_KEYS = ['slider', 'loyalty', 'actions', 'reorder', 'popular'];
  function homeModules(v) {
    var list = arr(parseJson(v, null));
    if (!list.length) return MODULE_KEYS.map(function (k) { return { key: k, enabled: true }; });
    var seen = {};
    var out = [];
    list.forEach(function (m) {
      var key = typeof m === 'string' ? m : m && m.key;
      if (MODULE_KEYS.indexOf(key) < 0 || seen[key]) return;
      seen[key] = true;
      out.push({ key: key, enabled: typeof m === 'string' ? true : m.enabled !== false && m.enabled !== 0 });
    });
    MODULE_KEYS.forEach(function (k) { if (!seen[k]) out.push({ key: k, enabled: false }); });
    return out;
  }

  // Store.menuLayout, the menu page's layout (MENU-LAYOUT: the owner's decision of 2026-10-06), in this precedence:
  //   1. proposed.menu_layout when it is exactly 'categories' or 'sections' (PROPOSED: no endpoint sends it yet);
  //   2. else the admin's Online-settings "Auto Menu Popup" toggle, getRestaurant data[0].menu_popup_auto: on (1, '1',
  //      true, 'true') → 'categories' (the category tiles);
  //   3. else 'sections' (the full menu: the category tab bar and every category's items in sections): 0, null, absent
  //      (the BE sends 0 for a store without an online_order_settings row) or anything else.
  // The route's ?layout= overrides the result on the page (OO.model.menuLayout, model-menu.jsx).
  function menuLayoutOf(p, d) {
    if (p.menu_layout === 'categories' || p.menu_layout === 'sections') return p.menu_layout;
    return flag(d.menu_popup_auto) ? 'categories' : 'sections';
  }

  // ---- getRestaurant quirks --------------------------------------------------------------------------
  function has(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  // Text that may arrive as the strings "null" / "undefined": '' then. Trimmed.
  function text(v) { var s = str(v).trim(); return s === 'null' || s === 'undefined' ? '' : s; }
  // A 0/1 / boolean / 'true' flag that may be absent: true | false | null (absent, null, '' or not a flag).
  function tri(v) {
    if (v === true || v === false) return v;
    var s = text(v).toLowerCase();
    if (!s) return null;
    if (s === 'true' || s === 'false') return s === 'true';
    var n = Number(s);
    return isFinite(n) ? n !== 0 : null;
  }
  // The first value that is a flag (tri), else `dflt`.
  function firstFlag(values, dflt) {
    for (var i = 0; i < values.length; i++) { var t = tri(values[i]); if (t !== null) return t; }
    return dflt;
  }
  function firstList(o, keys) {
    for (var i = 0; i < keys.length; i++) { if (arr(o[keys[i]]).length) return o[keys[i]]; }
    return [];
  }
  function firstOf(o, keys) {
    for (var i = 0; i < keys.length; i++) { var v = o[keys[i]]; if (v !== undefined && v !== null && v !== '') return v; }
    return null;
  }
  // An IANA zone Intl can read, else null.
  function validZone(tz) {
    var s = text(tz);
    if (!s || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return s || null;
    try { new Intl.DateTimeFormat('en-US', { timeZone: s }); return s; } catch (e) { return null; }
  }
  // Stable sort on `position`: numbers ascending, null last, ties in the given order.
  function byPosition(list) {
    return list.map(function (x, i) { return { x: x, i: i }; }).sort(function (a, b) {
      var pa = a.x.position, pb = b.x.position;
      if (pa === null || pb === null) return pa === pb ? a.i - b.i : (pa === null ? 1 : -1);
      return pa - pb || a.i - b.i;
    }).map(function (y) { return y.x; });
  }

  // ---- images ----------------------------------------------------------------------------------------
  var STORE_MEDIA = 'https://sipomedia.ap-south-1.linodeobjects.com/';
  function storeImage(v, ownerId) {
    var s = text(v);
    if (!s) return null;
    if (!/^(https?:)?\/\//i.test(s) && !/^(data|blob):/i.test(s) && !OO.theme.dataUri(s)) {
      var path = s.replace(/^\/+/, '');
      var m = /^user_(\d+)_image\/([^?#]+)$/.exec(path);
      if (m) return STORE_MEDIA + 'user_' + m[1] + '_image/' + m[2];
      var owner = num(ownerId);
      if (owner > 0 && /^[^\/?#]+\.(png|jpe?g|gif|webp|avif|svg)$/i.test(path)) return STORE_MEDIA + 'user_' + owner + '_image/' + path;
    }
    return OO.theme.imageSrc(s);
  }

  // ---- payment ---------------------------------------------------------------------------------------
  // A unified payment_methods row that is an online gateway (REF paymentMethodsContract.ts), not a payment type.
  function isGatewayRow(u) { return u.method === 'online' || (!!text(u.gateway) && num(u.gateway_id) > 0); }
  // P2-27: a unified gateway row of the config (name, id): the same gateway_id AND, when the row names one, the same
  // gateway name. Production keeps WINDCAVE configs under STRIPE's id 4 (P0-AUDIT §3), so an id alone can rank or
  // dedupe the wrong gateway.
  function sameGateway(u, name, id) {
    if (!isGatewayRow(u) || id === null || id === undefined || String(u.gateway_id) !== String(id)) return false;
    var n = text(u.gateway).toUpperCase();
    return !n || n === String(name).toUpperCase();
  }
  // The order types a unified row is enabled for (REF's web pickup / web delivery / table-side channels).
  function rowOrderTypes(u) {
    var out = [];
    if (flag(u.payment_pickup)) out.push('PICKUP');
    if (flag(u.payment_delivery)) out.push('DELIVERY');
    if (flag(u.payment_table_side)) out.push('DINEIN');
    return out;
  }
  // The first unified row `match` accepts: { index (-1: none), position, isDefault, orderTypes (null: none) }.
  function rankIn(unified, match) {
    for (var i = 0; i < unified.length; i++) {
      var u = unified[i];
      if (u && typeof u === 'object' && match(u)) {
        return { index: i, position: numOrNull(u.position), isDefault: tri(u.is_default) === true, orderTypes: rowOrderTypes(u) };
      }
    }
    return { index: -1, position: null, isDefault: false, orderTypes: null };
  }
  // The method's own option among its gateways / types: the store's default (is_default), else the first.
  function pickDefault(list) { return list.filter(function (x) { return x.isDefault; })[0] || list[0] || null; }

  // Store.paymentMethods: the checkout's methods. cart, checkout, pricing, the payload and payment-callback branch on
  // these three keys, so there is at most one entry per key. An entry's own fields name the option the payload sends
  // when the checkout picks none of its others (P2-27: the checkout lists every option,
  // OO.pages.CheckoutSections.payment):
  //   ONLINE_PAYMENT  while online_payment_enable and payment_gateway lists a gateway: the default gateway (is_default ›
  //                   lowest position › first). gatewayId as sent (a number on the real API: the
  //                   OnlinePaymentGatewayConfigs.gateway FK). gateways = every gateway, one per gateway name + id
  //                   pair, [{gateway, gatewayId, label, position, isDefault, orderTypes, order}] in position order;
  //                   a gateway's orderTypes are its unified row's channels (payment_pickup → PICKUP,
  //                   payment_delivery → DELIVERY, payment_table_side → DINEIN), else all three. The entry's
  //                   orderTypes: PICKUP, DELIVERY, DINEIN.
  //   PAY_AT_COUNTER  the default (is_default) payment_pickup type, else the first by position (else the same of
  //                   payment_table_side): typeId, label; orderTypes PICKUP and / or DINEIN. types = every pickup and
  //                   table-side type, one per id, [{typeId, label, position, isDefault, orderTypes (PICKUP and / or
  //                   DINEIN: the lists it is in), order}].
  //   CASH            the default payment_delivery type, else the first by position; orderTypes DELIVERY; types = every
  //                   delivery type (orderTypes ['DELIVERY']).
  // Which methods exist comes from those legacy lists (the fixtures' payment_methods leaves their cash type out). The
  // unified payment_methods row with the same id (payment type) or the same gateway name + gateway_id (gateway) ranks
  // them: position (null last, then API order), is_default and, for a gateway, its channels. The entries are in
  // position order too. `order` is that row's index in payment_methods (a legacy option without a row: after every
  // ranked one, types before gateways, in the legacy list's order), so the checkout lists its choices in the server's
  // order. The unified rows' `active` is never read: for a payment type it is the generic is_online flag, not the
  // channel's.
  var UNRANKED_TYPE = 100000, UNRANKED_GATEWAY = 200000;
  function paymentMethods(d, kiosk) {
    if (kiosk) {
      // The dedicated lists are authoritative; never fall back to a web gateway
      // or a pickup/cash method when this kiosk has none configured.
      d = Object.assign({}, d, {
        online_payment_enable: 1, payment_gateway: d.payment_gateway_kiosk || [],
        payment_pickup: d.payment_kiosk || [], payment_table_side: [], payment_delivery: [],
        payment_methods: arr(d.payment_methods).map(function (m) {
          return Object.assign({}, m, { payment_pickup: m.payment_kiosk, payment_delivery: false, payment_table_side: false });
        }),
      });
    }
    var unified = arr(d.payment_methods);
    var seenGateway = {};
    var gateways = byPosition(arr(d.payment_gateway).filter(function (g) { return g && text(g.gateway); }).map(function (g, i) {
      var id = g.gateway_id === undefined || g.gateway_id === '' ? null : g.gateway_id;
      var name = text(g.gateway);
      var rank = rankIn(unified, function (u) { return sameGateway(u, name, id); });
      return {
        gateway: name, gatewayId: id, label: text(g.label) || 'Pay Online', position: rank.position, isDefault: rank.isDefault,
        orderTypes: rank.orderTypes || ['PICKUP', 'DELIVERY', 'DINEIN'], order: rank.index >= 0 ? rank.index : UNRANKED_GATEWAY + i,
      };
    }).filter(function (g) {
      var k = g.gateway.toUpperCase() + '|' + String(g.gatewayId);
      if (seenGateway[k]) return false;
      seenGateway[k] = true;
      return true;
    }));
    function counters(list, orderType) {
      return byPosition(arr(list).filter(function (c) { return c && flag(c.active); }).map(function (c, i) {
        var rank = rankIn(unified, function (u) { return !isGatewayRow(u) && num(u.id) > 0 && num(u.id) === num(c.id); });
        return {
          typeId: numOrNull(c.id), label: text(c.type) || 'Pay at Counter', position: rank.position, isDefault: rank.isDefault,
          orderTypes: [orderType], order: rank.index >= 0 ? rank.index : UNRANKED_TYPE + i,
        };
      }));
    }
    // One type per id, its order types merged (a type in both payment_pickup and payment_table_side), by position.
    function mergeTypes(lists) {
      var byId = {}, merged = [];
      lists.forEach(function (list) {
        list.forEach(function (t) {
          var k = String(t.typeId);
          if (byId[k]) {
            if (byId[k].orderTypes.indexOf(t.orderTypes[0]) < 0) byId[k].orderTypes.push(t.orderTypes[0]);
            return;
          }
          byId[k] = Object.assign({}, t, { orderTypes: t.orderTypes.slice() });
          merged.push(byId[k]);
        });
      });
      return byPosition(merged);
    }
    var out = [];
    function add(key, m, extra) {
      out.push(Object.assign({ key: key, label: m.label, gateway: null, gatewayId: null, typeId: null, orderTypes: [], position: m.position, isDefault: m.isDefault }, extra));
    }
    if (flag(d.online_payment_enable) && gateways.length) {
      var def = pickDefault(gateways);
      add('ONLINE_PAYMENT', def, { gateway: def.gateway, gatewayId: def.gatewayId, orderTypes: ['PICKUP', 'DELIVERY', 'DINEIN'], gateways: gateways });
    }
    var pickup = counters(d.payment_pickup, 'PICKUP'), table = counters(d.payment_table_side, 'DINEIN');
    var delivery = counters(d.payment_delivery, 'DELIVERY');
    var counter = pickDefault(pickup) || pickDefault(table);
    if (counter) {
      add('PAY_AT_COUNTER', counter, {
        typeId: counter.typeId, orderTypes: (pickup.length ? ['PICKUP'] : []).concat(table.length ? ['DINEIN'] : []),
        types: mergeTypes([pickup, table]),
      });
    }
    var cash = pickDefault(delivery);
    if (cash) add('CASH', cash, { typeId: cash.typeId, orderTypes: ['DELIVERY'], types: mergeTypes([delivery]) });
    return byPosition(out);
  }

  // Store.paymentOptions: the unified payment_methods list (metadataController::buildMergedPaymentMethods), data only.
  // Counters are payment-type rows with an id > 0; gateways are isGatewayRow rows with a gateway_id, one per gateway
  // name + gateway_id pair (P2-27: REF dedupes by the id alone, which hides one of two configs sharing an id, e.g. a
  // WINDCAVE config stored under STRIPE's id 4). A row enabled on no channel is left out. In position order (null last,
  // then API order). channels: web = payment, pickup = payment_pickup, delivery = payment_delivery, tableSide =
  // payment_table_side, kiosk = payment_kiosk; orderTypes PICKUP / DELIVERY / DINEIN follow pickup / delivery /
  // tableSide. gatewayId is a number when numeric, else as sent; null for counters. Gateways still need
  // settings.onlinePayment (online_payment_enable). [] when the API sends no list.
  function paymentOptions(d) {
    var seen = {};
    return byPosition(arr(d.payment_methods).map(function (u) {
      if (!u || typeof u !== 'object') return null;
      var gateway = isGatewayRow(u);
      var channels = { web: flag(u.payment), pickup: flag(u.payment_pickup), delivery: flag(u.payment_delivery), tableSide: flag(u.payment_table_side), kiosk: flag(u.payment_kiosk) };
      if (!Object.keys(channels).some(function (k) { return channels[k]; })) return null;
      var gid = null;
      if (gateway) {
        gid = numOrNull(u.gateway_id) !== null ? numOrNull(u.gateway_id) : (text(u.gateway_id) || null);
        var pair = 'g' + text(u.gateway).toUpperCase() + '|' + gid;
        if (gid === null || seen[pair]) return null;
        seen[pair] = true;
      } else if (!(num(u.id) > 0)) {
        return null;
      }
      var orderTypes = rowOrderTypes(u);
      return {
        id: numOrNull(u.id), kind: gateway ? 'gateway' : 'counter', name: text(u.name) || text(u.type),
        label: text(u.label) || text(u.name) || text(u.type), gateway: gateway ? text(u.gateway) || null : null, gatewayId: gid,
        position: numOrNull(u.position), isDefault: tri(u.is_default) === true, channels: channels, orderTypes: orderTypes,
      };
    }).filter(Boolean));
  }

  // ---- surcharges, zones, closures ---------------------------------------------------------------------
  var SURCHARGE_TYPE = { fixed: 'fixed', flat: 'fixed', amount: 'fixed', percentage: 'percentage', percent: 'percentage' };
  function surchargesOf(d) {
    if (Array.isArray(d.surcharges)) {
      return d.surcharges.map(function (c) {
        if (!c || typeof c !== 'object' || tri(c.enabled) !== true) return null;
        var type = SURCHARGE_TYPE[text(c.type).toLowerCase()], value = num(c.value);
        return type && value > 0 ? { type: type, value: value, ...(text(c.label) ? { label: text(c.label) } : {}), rules: OO.model.parseRules(c.availability) } : null;
      }).filter(Boolean);
    }
    var out = [];
    if (flag(d.surcharge_enable)) {
      if (num(d.surcharge_amount) > 0) out.push({ type: 'fixed', value: num(d.surcharge_amount), ...(text(d.surcharge_label) ? { label: text(d.surcharge_label) } : {}), rules: [] });
      if (num(d.surcharge_percent) > 0) out.push({ type: 'percentage', value: num(d.surcharge_percent), ...(text(d.surcharge_label) ? { label: text(d.surcharge_label) } : {}), rules: [] });
    }
    return out;
  }

  function deliveryZones(d) {
    var zones = arr(d.deliveryzones).filter(function (z) { return z && typeof z === 'object' && !flag(z.is_delete); }).map(function (z, i) {
      return {
        z: {
          id: num(z.id), name: str(z.name), minimumOrder: num(z.minimum_amount), fee: num(z.delivery_fee), radiusKm: num(z.zone_area),
          type: text(z.type).toLowerCase() || 'circle', orderOutside: flag(z.order_outside), lat: numOrNull(z.latitude), lng: numOrNull(z.longitude),
        },
        i: i,
      };
    });
    return zones.sort(function (a, b) { return a.z.radiusKm - b.z.radiusKm || a.i - b.i; }).map(function (x) { return x.z; });
  }

  var CLOSURE_LISTS = ['upcomming_scheduled_disable_data', 'upcoming_scheduled_disable_data', 'scheduled_disable_data', 'scheduled_closures'];
  var CLOSURE_DOMAIN = { ONLINE_PICKUP: 'PICKUP', ONLINE_APP_PICKUP: 'PICKUP', ONLINE_DELIVERY: 'DELIVERY', ONLINE_APP_DELIVERY: 'DELIVERY' };
  function closureDomain(v) {
    var s = text(v);
    var n = str(typeof OO.model.normalizeDomain === 'function' ? OO.model.normalizeDomain(s) : s.replace(/[\s-]+/g, '_').toUpperCase());
    return has(CLOSURE_DOMAIN, n) ? CLOSURE_DOMAIN[n] : n;
  }
  function closuresOf(d) {
    return arr(firstList(d, CLOSURE_LISTS)).map(function (x) {
      if (!x || typeof x !== 'object') return null;
      var start = parseInstant(firstOf(x, ['schedule_start_time', 'start_time', 'start']));
      var end = parseInstant(firstOf(x, ['schedule_end_time', 'end_time', 'end']));
      if (start === null || end === null || !(end > start)) return null;
      return { start: start, end: end, domain: closureDomain(firstOf(x, ['domain_type', 'domain'])), reason: text(firstOf(x, ['close_reason', 'reason'])) || null };
    }).filter(Boolean);
  }

  // shop_holidays labels by date (REF normalizeShopHolidays IDX:1354: label › name › reason › title).
  function holidayLabelsOf(d) {
    var out = {};
    arr(firstList(d, ['shop_holidays', 'shop_hoilidays'])).forEach(function (h) {
      if (!h || typeof h !== 'object') return;
      var ymd = str(h.date).slice(0, 10), label = text(firstOf(h, ['label', 'name', 'reason', 'title']));
      if (/^\d{4}-\d{2}-\d{2}$/.test(ymd) && label && !has(out, ymd)) out[ymd] = label;
    });
    return out;
  }

  // The order-later window's inputs (OO.schedule.window). REF IDX:2174-2178, 2273-2278: order_later falls back to
  // order_for_later only when order_later isn't sent (no online_order_settings row); day counts are whole numbers ≥ 0.
  function schedulingOf(d) {
    function sent(v) { return v !== undefined && v !== null && text(v) !== ''; }
    function days(v) { var n = num(v); return n > 0 ? Math.floor(n) : 0; }
    var later = [d.order_later, d.order_for_later].filter(sent)[0];
    return {
      preorder: tri(d.is_preorder) === true, preorderDays: days(firstOf(d, ['pre_min_day', 'preMinDay'])),
      orderLater: tri(later) === true, orderLaterDays: days(d.order_later_days),
    };
  }

  function addressOf(d) {
    var a = arr(d.address)[0] || {};
    var street = str(a.streetName).trim(), city = str(a.city).trim(), postcode = str(a.postalCode).trim(), country = str(a.country).trim();
    // Only use the Store Settings address after the store opts in: legacy records may contain a
    // different address in their structured fields. Keep those fields intact for other consumers.
    var preferred = tri(d.online_use_full_address) === true && typeof d.address_full === 'string' ? text(d.address_full) : '';
    if (/^(null|undefined|false)$/i.test(preferred)) preferred = '';
    var full = preferred || [street, city, country, postcode].filter(Boolean).join(', ');
    return { street: street, city: city, postcode: postcode, country: country, full: full, short: preferred || street || full };
  }

  // A banner / popup row that is on: enabled and is_active, each absent = on (REF normalizeRestaurantBanners).
  function promoOn(b) { return !!b && typeof b === 'object' && firstFlag([b.enabled], true) && firstFlag([b.is_active], true); }
  function promoSlides(rows, image) {
    return arr(rows).filter(promoOn).map(function (b) {
      var bp = b.proposed && typeof b.proposed === 'object' ? b.proposed : {};
      return {
        id: num(b.id), image: image(b.image), title: str(b.text), tag: str(bp.tag) || null,
        subtitle: str(bp.subtitle) || null, cta: str(bp.cta) || null, linkRaw: str(b.link) || null, link: resolveLink(b.link),
        rules: OO.model.parseRules(b.availability),
      };
    });
  }

  // The store row: data[0], or data.restaurant; {} for any other body.
  function storeRow(raw) {
    var data = raw && typeof raw === 'object' ? raw.data : null;
    var d = arr(data)[0] || (data && typeof data === 'object' && !Array.isArray(data) ? data.restaurant : null);
    return d && typeof d === 'object' && !Array.isArray(d) ? d : {};
  }

  function normalizeStore(raw, opts) {
    opts = opts || {};
    var f = F();
    var d = storeRow(raw);
    var p = d.proposed && typeof d.proposed === 'object' ? d.proposed : {};
    var id = num(d.id || d.hotelId);
    var tz = validZone(d.timezone) || 'Pacific/Auckland';
    function image(v) { return storeImage(v, id); }
    var themes = d.shopThemes && typeof d.shopThemes === 'object' ? d.shopThemes : null;
    var onlineTheme = themes ? (themes.online_theme != null ? themes.online_theme : themes) : (raw && raw.shopThemes && raw.shopThemes.online_theme);
    var theme = OO.theme.fromRaw({ onlineTheme: onlineTheme, structured: d.theme, proposed: p.theme, onlineThemeColor: d.online_theme_color, image: image });
    var storeLogoUrl = image(d.shop_logo) || image(d.logo) || image(d.hotel_logo) || image(d.store_logo) || null;
    var r = resolveNow(opts.now != null ? opts.now : null, tz); // not current_time (see the header)
    var status = arr(d.hotelStatus)[0];
    var active = firstFlag([status && status.active, d.online_order_active], true);
    var orderTypes = [];
    if (flag(d.pickup) || text(d.pickup_inactive_reason)) orderTypes.push('PICKUP');
    if (flag(d.delivery) || text(d.delivery_inactive_reason)) orderTypes.push('DELIVERY');
    if (flag(d.dine_in)) orderTypes.push('DINEIN');
    var MODE_LABEL = { PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
    var loyaltyP = p.loyalty && typeof p.loyalty === 'object' ? p.loyalty : {};
    var checkoutP = p.checkout && typeof p.checkout === 'object' ? p.checkout : {};
    var taxRaw = numOrNull(d.tax);
    var code = text(d.currency).toUpperCase();
    var currencyCode = /^[A-Z]{3}$/.test(code) ? code : 'NZD';
    var currencySymbol = text(d.currency_symbol);

    var store = {
      id: id,
      onlineKey: str(d.online_key),
      name: str(d.hotelName),
      address: addressOf(d),
      phone: str(d.mobile),
      website: httpUrl(d.website) || (/^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#][^\s]*)?$/i.test(str(d.website).trim()) ? 'https://' + str(d.website).trim() : null),
      description: str(d.description),
      mapLink: httpUrl(d.map_link),
      lat: numOrNull(d.latitude),
      lng: numOrNull(d.longitude),
      timezone: tz,
      currencyCode: currencyCode,
      currencySymbol: currencySymbol,
      currency: currencySymbol || f.currencySymbol(currencyCode),
      storeLogoUrl: storeLogoUrl,
      logoUrl: storeLogoUrl || theme.logo || null,
      bannerUrl: image(d.shop_image),
      taxRate: taxRaw === null || taxRaw <= 0 ? 15 : taxRaw,
      taxInclusive: num(d.tax_exclusive) !== 1,
      hideTaxLabel: flag(d.hide_tax_label),
      surcharge: { ...(text(d.surcharge_label) ? { label: text(d.surcharge_label) } : {}), enabled: flag(d.surcharge_enable), amount: num(d.surcharge_amount), percent: num(d.surcharge_percent) },
      surcharges: surchargesOf(d),
      orderFees: d.order_fees || null,
      couponsEnabled: firstFlag([d.coupon_enabled, d.coupon_enable], true),
      loyaltyProductLevel: firstFlag([d.loyalty_enable_product_level, d.loyalty_product_level_enabled, d.loyaltyEnableProductLevel, d.loyality_enable_product_level], false),
      applyDiscountToModifiers: flag(d.apply_discount_to_modifiers),
      minimumAge: ['minimum_age', 'min_age', 'age_limit', 'av_age'].map(function (k) { return numOrNull(d[k]); })
        .filter(function (n) { return n !== null && n > 0 && n < 130; })[0] || null,
      orderTypes: orderTypes,
      modesLabel: orderTypes.map(function (t) { return MODE_LABEL[t]; }).join(' · '),
      booking: flag(d.table_reservation),
      tableOrdering: firstFlag([d.enable_table_side_ordering], true),
      cookingTimeMins: num(d.pickup_buffer_time) || 15,
      deliveryTimeMins: num(d.delivery_buffer_time) || 30,
      online: { active: active, reason: text(d.online_inactive_reason) || (status && text(status.active_reason)) || null },
      hours: hoursFrom(d.workingInfo, r.offset),
      hoursUtc: hoursFrom(d.workingInfo, 0),
      holidays: arr(firstList(d, ['shop_holidays', 'shop_hoilidays'])).map(function (h) {
        return str(h && typeof h === 'object' ? h.date : h).slice(0, 10);
      }).filter(function (ymd) { return /^\d{4}-\d{2}-\d{2}$/.test(ymd); }),
      holidayLabels: holidayLabelsOf(d),
      closures: closuresOf(d),
      scheduling: schedulingOf(d),
      paymentMethods: paymentMethods(d, OO.mode === 'kiosk'),
      paymentOptions: paymentOptions(d),
      deliveryZones: deliveryZones(d),
      theme: theme,
      banners: promoSlides(d.banner, image),
      // null/absent means the store still uses its legacy banners; [] means intentionally empty.
      appSlides: Array.isArray(d.app_slider) ? promoSlides(d.app_slider, image) : null,
      popups: arr(d.popup).filter(promoOn).map(function (b) {
        return {
          id: num(b.id), image: image(b.image), text: str(b.text), buttonName: str(b.button_name) || null,
          linkRaw: str(b.button_link) || null, link: resolveLink(b.button_link), rules: OO.model.parseRules(b.availability),
        };
      }),
      alert: { enabled: flag(d.is_alert), text: text(d.alert_text) || null, image: image(d.alert_image) },
      checkoutAlert: {
        PICKUP: flag(d.is_checkout_alert_pickup) ? text(d.checkout_alert_pickup) || null : null,
        DELIVERY: flag(d.is_checkout_alert_delivery) ? text(d.checkout_alert_delivery) || null : null,
      },
      terms: { terms: text(d.terms_condition) || text(d.terms_of_use), use: text(d.terms_of_use), privacy: null, allergy: text(d.terms_of_allergy) },
      settings: {
        showSizePrice: flag(d.show_size_price),
        hideOutsideAvailability: flag(d.hide_outside_availability),
        tippingEnabled: flag(d.tipping_enabled),
        timeSlotDisplay: d.time_slot_display === 'slots' ? 'slots' : 'dropdown',
        categoryBar: flag(d.isCategoryBar),
        poweredBy: d.powered_by_logo_enable === undefined ? true : flag(d.powered_by_logo_enable),
        forceLoginForOrdering: flag(d.force_login_for_ordering),
        forceLoginForCoupon: flag(d.force_login_for_coupon),
        tableUserForm: flag(d.is_user_form_table_side), // P4-05 / P4-03: the table's guest form + mandatory contact (REF)
        orderPrompt: flag(d.order_prompt_dialog),
        memberLoginPrompt: firstFlag([d.member_login_prompt, raw && raw.member_login_prompt,
          raw && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data.member_login_prompt : null], false),
        orderLater: flag(d.order_later),
        orderLaterDays: num(d.order_later_days),
        itemsSorting: numOrNull(d.items_sorting),
        onlinePayment: flag(d.online_payment_enable),
      },
      relatedBranches: arr(raw && raw.related_branches).filter(function (b) { return b && typeof b === 'object'; }).map(function (b) {
        return { id: num(b.id), name: str(b.name), onlineKey: str(b.online_key), addressFull: str(b.address_full) };
      }),
      serverTime: str(raw && raw.current_time) || null,
      reviewSettings: OO.model.restaurantReviewSettings(raw),
      loyalty: {
        programName: str(loyaltyP.program_name) || (str(d.hotelName).split(' ').slice(0, 2).join(' ') + ' Rewards').trim(),
        pointsWord: str(loyaltyP.points_word) || 'points',
      },
      // MENU-LAYOUT: proposed.menu_layout ('categories' | 'sections' exactly), else the Auto Menu Popup toggle
      // (menu_popup_auto on → 'categories', the tiles; off / absent → 'sections', the full menu). See menuLayoutOf.
      menuLayout: menuLayoutOf(p, d),
      // PROPOSED settings (data[0].proposed). See BATCH2-GAPS.md.
      categoryStyle: p.category_style === 'text' ? 'text' : 'image',
      homeModules: homeModules(p.home_modules),
      checkout: {
        priceSummary: checkoutP.price_summary === 'collapsed' ? 'collapsed' : 'inline',
        loginStyle: checkoutP.login_style === 'solid' ? 'solid' : 'card',
      },
    };
    if (OO.mode === 'kiosk') {
      var alerts = d.kiosk_checkout_alert || {};
      store.kiosk = {
        active: firstFlag([d.kiosk_status], true), reason: text(d.kiosk_message) || null,
        alerts: { DINEIN: flag(alerts.dine_in) ? text(alerts.dine_in_alert) : null,
          TAKEAWAY: flag(alerts.takeaway) ? text(alerts.takeaway_alert) : null },
        slides: arr(d.kiosk_slider).map(function (slide) {
          var meta = parseJson(slide.metadata, {}) || {};
          return { image: image(slide.path), title: text(meta.title), rules: OO.model.parseRules(slide.availability) };
        }).filter(function (slide) { return !!slide.image && OO.model.isAvailable(slide.rules, r.clock, 'KIOSK'); }),
      };
      store.orderTypes = ['PICKUP']; // shared immediate, non-delivery draft; wire type comes from the kiosk choice
      store.modesLabel = 'Dine-in · Takeaway';
      store.online = { active: store.kiosk.active, reason: store.kiosk.reason };
      store.cookingTimeMins = Math.max(0, num(d.kiosk_buffer_time));
      store.settings.forceLoginForOrdering = false;
      store.settings.orderPrompt = false;
    }
    if (OO.orderingQr && d.ordering_qr) store = OO.orderingQr.decorateStore(store, d.ordering_qr);
    store.open = OO.mode === 'kiosk' ? { isOpen: store.kiosk.active, label: store.kiosk.active ? 'Order here' : 'Ordering is paused', reason: store.kiosk.reason }
      : OO.schedule.openStatus(store, r.instant);
    store.pickup = OO.schedule.pickupOptions(store, r.instant);
    return store;
  }

  var M = OO.model;
  M.normalizeStore = normalizeStore;
  // Thin wrappers over the scheduling engine (src/lib/schedule.jsx, loaded later: resolved at call time).
  M.openStatus = function (store, now, type) { return OO.schedule.openStatus(store, now, type); };
  M.pickupOptions = function (store, now, opts) { return OO.schedule.pickupOptions(store, now, opts); };
  M.slotInstant = function (store, slot, now) { return OO.schedule.slotInstant(store, slot, now); };
  M.storeImage = storeImage;
})();


// ===================== src/lib/model-menu.jsx =====================
// src/lib/model-menu.jsx: OO.model's menu normalisers, on the model.jsx core (its `now` / domain notes apply). API:
//   normalizeMenu(rawMenu, {now, timeZone, domain}) → Menu  (POST get_menu, §3.2 + combos §7)
//   availabilityAt(menu, target, {now, timeZone, orderType, mode, table, domain, menuId}) → Availability | null
//       (the inheritance chain at any time; see "availabilityAt" below)
//   displayState(menu, target, {…availabilityAt's, today}) → Display | null (the D-07 grey / hide policy; P2-16)
//   nextAvailableLabel(next, todayYmd) → 'Available Tomorrow At 7:00 AM' (REF's next-available text)
//   namedMenus(menu, {…displayState's, selected}) → { menus, activeId, showSelector } (the active named menu)
//   menuDisplay(menu, {…namedMenus'}) → MenuDisplay (every product, combo and category under the policy at once)
//       (the four: see "displayState" below)
//   requiredChoiceLabel(groupName, required, available) → REF's "Drink Requires 1 Choice, But No Valid Options Are
//       Available." / "Pizza Requires 2 Choices, But Only 1 Is Available." (the combo `min` rule's label)
//   popularProducts(rawPopular, menu) → Product[]   popularCombos(rawPopular, menu) → Combo[]
//   searchMenu(menu, query) → productIds
//   menuLayout(store, routeQuery) → 'categories' | 'sections' (?layout= overrides store.menuLayout: MENU-LAYOUT,
//       proposed.menu_layout, else the admin's "Auto Menu Popup" toggle: on → 'categories', off → 'sections')
//
// Menu = { version, type, categories: Category[], products: {[id]: Product}, combos: {[id]: Combo},
//   menus: NamedMenu[], timeZone, domain } (timeZone / domain: what it was normalised for; availabilityAt's defaults)
//   Category = { id (number | 'combo-<id>'), key, kind:'combo'|'menu', name, description, image, position,
//     available, inactiveReason, unavailableReason, showOnly, ageRestricted, chain, subcategories:[{id, key, name,
//     position, productIds}], productIds, comboIds, itemCount, meta ('4 categories' | '6 items') }. Combo categories come
//     first, as in Next's MenuDisplay. A category the BE sends without `item` stays, empty (the menu page skips empty
//     ones). showOnly = the raw show_only flag, data only (see "Availability"). ageRestricted = its `av` flag (REF
//     is_age_restricted: every item in it is age-restricted; combo categories: false).
//   Product = { id, kind:'product', name, description, price, image, sizes:[Size], groups:[Group], available,
//     soldOut, inactiveReason, unavailableReason, chain, categoryId, subcategoryId, subcategoryName, ribbon, wasPrice,
//     tags:[string], hideNotes, alert, isLoyalty, weighted, ageRestricted }. ageRestricted = its own `av` or its
//     category's (REF isAgeRestrictedItem; the item sheet's age gate, P2-21).
//   Size = { id, name, price (REPLACES the base price: size_amount, else price), position, excludeOptionIds,
//     preselect (the row's preselect; none is implied), available (its own rules) }
//   Group = { id, name, min, max, required, allowSame, unlimited, position, available, sizeLike, hint, options,
//     minRaw, freeCount }
//     Option = { id, name, price, image, preselect, available, position, alert } (alert = { text } when is_alert
//     carries an alert_text, else null: REF toasts it when the option is picked; the item sheet shows it, P2-20)
//   Combo = { id, kind:'combo', name, description, descriptionHtml, price, image, ribbon, available,
//     unavailableReason, inactiveReason, availableToday, todayOnly, categoryId, comboCategoryId (combo_cat_id |
//     null), categoryName, startDate, endDate ('0000-00-00 …' = null), groups:[ComboGroup], chain,
//     unavailableGroup (the first required group with too few available options, e.g. a sold-out 'Chips'; it makes
//     `available` false) | null }
//   ComboGroup = { id, name, noun, min, max, allowSame, position, options:[ComboOption] }
//   ComboOption = { mappingId, productId, name, description, image, extraPrice, price, isDefault, available,
//     soldOut, chain, sizeId, sizeName, sizeMode, sizes, groups }. Description and image are enriched from the menu
//     product, because raw cat_mappings products carry no description.
//   NamedMenu = { id, name, description, position, enabled, available, availableToday, chain, categoryIds, comboIds,
//     comboCategoryIds } (raw `menus`; namedMenus picks the active one). available = enabled, it lists something and
//     its rules pass now (REF MenuSelectionContext); the refs keep their position order. categoryIds match a menu
//     Category.id, comboCategoryIds are combo_cat_ids (Category.id 'combo-<id>'). Sorted by position.
//   chain = the entity's OWN availability layers (REF order, empty sources left out), each { source, rules:
//     AvailabilityRule[] } (model.jsx parseRules output) or { source, off: true, label } (a switch that is off).
//     availabilityAt's input; pages never read it. Its ancestors' layers live on the ancestors.
//
// Groups (D-25 (a), checked on the captured get_menu bodies). The three raw arrays are additive routes to a
// product's modifiers: groupstoproduct = single modifiers mapped to the product (the BE already filters its addOns to
// modifiermaps), group = whole groups via the product's category, groups = whole groups on the product. They are
// UNION-merged by groupId, with no modifiermaps filter: options union (the first copy of an addOnId wins), required
// and allowSame OR'd, the larger min and max (unbounded wins), freeCount = the first non-zero, availability = the
// first copy with rules, name and position from the first copy. In the captures the copies differ only in options.
//   required = is_mandatory | required | is_required (REF). min = required ? max(min_modifier, 1) (≤ max) : 0, so
//     `min` remains the unconditional minimum. minRaw keeps the configured minimum: an optional group can be
//     skipped, but once selected OO.modifiers and the BE enforce minRaw. Legacy optional min 1 stays optional.
//   max_modifier > 0 bounds the group. 0 or null is UNBOUNDED (kept from (b): the BE's rule, "legacy null/zero values
//     are unbounded", and REF's "0 means 0" would freeze staging's 'Spice Level' groups): unlimited = true and
//     max = allowSame ? Number.MAX_SAFE_INTEGER : the option count (≥ 1).
//   freeCount = max_no_price_count, a whole number ≥ 0: that many units of the group are free per unit of the line
//     (OO.modifiers, P1-11).
//   Option price = price (0 kept), else extra_price, else sale_price. Image = mod_image | imageUrl | image.
//   'size'-labelled groups sort first, then by position; options by position (a missing one = 0, which the BE sends
//   first), the merged sent order breaking ties.
//
// Availability: the inheritance chain (the model.jsx engine; REF useMenuCategoriesWithAvailability + filterAvailable,
// B §1.1; P2-15). named menu → category → product (combo → its required groups' options). Every rule layer is judged by
// the engine, so a matching "Unavailable during" blackout (type 'unavailable') vetoes at whatever level it sits. The
// first failing source wins:
//   Named menu (availabilityAt only, and only with opts.menuId: the ACTIVE menu of the P2 selector; REF scopes only
//     when 2+ menus are available, so normalizeMenu and the cart / checkout re-checks never apply it): the target must
//     be one it lists (a product through its category, a combo by combo_id or combo_cat_id), else 'menu' ("Not On
//     This Menu"); then the menu itself: enabled → lists something → its rules.
//   Category: inactive | is_inactive → 'inactive' (its inactive_reason, else "Not Available") → its availability
//     rules (a { availability: [...] } wrapper is unwrapped) → catAvailability. catAvailability is read as REF does:
//     only rule-shaped entries count (REF looksLikeRule: a DM / D / H / SD / ED alias key, or type 'unavailable').
//     get_menu sends the legacy category_availabilities rows there ({ day, start, end, is_active, … }: pre-2024-04
//     data the admin can no longer show, which REF, the POS and the BE all ignore), so they never restrict anything.
//     show_only is carried as Category.showOnly and has no effect: REF parses it and never acts on it, and no client
//     or backend path reads it.
//   Product: soldOut = the hard flags (is_inactive, is_available false), kept apart from `available`.
//     available = its category's state → prod_cat_availability → category_active / category_availability →
//     sub_cat_availability (else subCatAvailability, else its sub-category row's availability / rule-shaped
//     catAvailability, as REF) → its own availability (get_menu rows carry only the last; suggestion and combo rows
//     carry more).
//   Combo: is_inactive / is_available false (REF; get_menu sends neither) → category_active → category_availability
//     (the combo category's rules) → its own availability; then the `min` rule (REF hasEnoughChoices): a required group
//     (min ≥ 1) needs `min` available options, or one when allow_same (a pick may repeat), else 'group'.
//     ComboOption: the product chain on its own row (it carries prod_cat_availability etc., never its menu category's
//     inactive flag, as REF), and soldOut.
//   Modifier groups, options and sizes keep their own rules (OO.modifiers drops an unavailable group whole).
//   unavailableReason = that source's code: 'inactive' | 'blackout' | 'domain' | 'dateRange' | 'time' | 'group'
//     (combos); null when available. inactiveReason = the label: sold out → its raw inactive_reason (null without
//     one); 'inactive' → the category's inactive_reason or "Not Available"; 'blackout' → "Temporarily Unavailable";
//     'domain' → "Not Available For Delivery" | "… For Pickup" | "… For Dine-In" | "Not Available At This Kiosk" (by
//     the requested domain); 'dateRange' → "Outside Available Date Range"; 'time' → "Not Available At The Selected
//     Time"; 'group' → requiredChoiceLabel; null when available.
//
// availabilityAt(menu, target, opts) → Availability | null (P2-16/17 build labels and grey / hide on it):
//   target: a Product or Combo of `menu` (or { kind:'product'|'combo', id }), a Category ({ kind:'category', id }, or
//   the Category itself) or { kind:'namedMenu', id }; null when `menu` has no such entity.
//   opts: now (as normalizeMenu; default the device clock), timeZone (default menu.timeZone), domain (else
//   domain(orderType, mode, { table }) when orderType is given, else menu.domain), menuId (see "Named menu").
//   Availability = { available, reason, label, source, group, next, nextAt }:
//     available: orderable at that minute in that domain. Unlike Product.available, a sold-out item is NOT available
//       (reason 'soldOut', label its inactive_reason or "Sold Out"); otherwise `available` / reason / label equal the
//       normalised flags at the same time and domain.
//     reason / label: the first failing source (the codes and labels above, plus 'soldOut' and 'menu'); null when
//       available. source: where it failed: 'menu' | 'category' | 'product' | 'combo' | 'group'. group: the group
//       name for 'group'.
//     next: the first minute it is available, within today and the next 7 days (REF getNextAvailableLabel), as the
//       store wall clock { ymd, dow, minutes, hm }; nextAt: that minute's epoch ms (in opts.timeZone). Both null when
//       it is available now, and when it never is in the horizon (sold out, an off switch, out of the named menu, a
//       domain gate, a date range past or > 7 days out, a group that can't be met). The search re-judges the WHOLE
//       chain (a combo's required options included) at every minute availability can change on: midnight, each
//       window's start S and the minute after its end E.
//   A combo category is the union of its combos: available when one is; else the first combo's reason and the
//   earliest next. Pure: nothing reads the device clock unless `now` is missing.
//
// displayState(menu, target, opts) → Display | null: the D-07 policy (owner decision 2026-10-04): what can be ordered
// later is GREYED with an "available from" label, and only what can never be ordered here is HIDDEN (REF develop
// d298a7e hides every unavailable item). Online and QR table ordering pass hideUnavailable (owner 2026-11-04):
// every unavailable product, combo and category is hidden, including sold-out and temporarily unavailable entries.
// Without that presentation option the grey/hide policy below remains available to the other surfaces.
//   opts: availabilityAt's, where `now` is the moment the order is for (the draft's chosen slot, else now), plus today:
//   the app clock the label's day words count from (default now). REF counts them from the judged slot, so a slot
//   tomorrow would read "Today"; judged at now the two agree.
//   Display = { show: 'normal' | 'grey' | 'hide', label (null unless grey), reason, source, group, next, nextAt } (the
//   last five as availabilityAt). By availabilityAt's first failure:
//     none → normal.
//     'domain' (the order type's / channel's domain gate) or 'menu' (not on the ACTIVE named menu) → hide.
//     a next minute ('time', 'blackout', 'dateRange', 'group', a named menu's own window) → grey, label =
//       nextAvailableLabel(next, today).
//     no next minute:
//       'soldOut', 'inactive' (a category / combo switched off) or 'group' (its options short): states staff can
//         restore. The chain is judged again with them lifted (the sold-out check, every `off` switch, sold-out and
//         switched-off combo options): orderable now or within the horizon → grey, label = the item's inactive_reason
//         or "Sold out" | the switch's inactive_reason or "Not available" | "Not available"; else hide. So a sold-out
//         item in a kiosk-only or expired category hides (the sold-out check comes first in the chain, so the reason
//         alone would grey it, but it can never be ordered here), and a group impossible through a domain or date
//         gate hides.
//       'time', 'blackout', 'dateRange' → hide (nothing within the horizon).
//   A combo category is the union of its combos: normal when one is; else the grey one with the soonest next (else
//   the first grey one); else hidden.
//   Labels, all built here: nextAvailableLabel's REF wording in REF's title case (availabilityService.ts
//   formatNextAvailable: 'Available Today From 5:00 PM', 'Available Tomorrow At 7:00 AM', 'Available Monday At 7:00 AM';
//   a next at a day's first minute: 'Available Tomorrow' / 'Available Monday'; the time is built by hand on a 12-hour
//   clock, so no ICU U+202F) and the D-07 brief's "Sold out" / "Not available". next.minutes 0 reads as untimed even
//   when a window starts at 00:00 (REF: "… At 12:00 AM").
// Named menus: namedMenus(menu, opts) → { menus: NamedMenu[], activeId, showSelector } (REF MenuSelectionContext). A
//   named menu can be picked when it is enabled, its own rules pass at opts' moment and domain (availabilityAt) and it
//   lists something displayState shows (judged with no named-menu scope); sorted by position, then id (REF).
//   showSelector = two or more. activeId = null with fewer than two (REF then scopes nothing), else opts.selected when
//   it is one of them (the customer's pick; REF keeps it in memory only), else the first. Pass it as opts.menuId.
// menuDisplay(menu, opts) → { menuId, menus, showSelector, products: {[id]: Display}, combos: {[id]: Display},
//   categories: {[id]: Display + { productIds, comboIds, subcategories: [{ id, productIds }] }} }: namedMenus picks
//   menuId (opts.menuId is ignored) and every product and combo is judged with it. A category keeps its shown (not
//   hidden) products and combos in menu order, and only the sub-categories with one; with none, or hidden itself,
//   it is 'hide' and its lists are empty. A menu category's own Display is its own switch / window (a category greyed
//   as a whole); a combo category's is the union of its shown combos.
//
// Combo sizes: sizeMode = size_mode as 'none' | 'fixed' | 'choice' ('choice' with no size rows = 'none'); without one
// it is inferred (size_id → 'fixed', size rows → 'choice', else 'none'). A fixed option's sizeId falls back to
// configured_size.id; its price is that size's (else configured_size.size_amount, else sale_price).
//
// Popular (GET popular-dishes/{key}: { status:'success', data:{ products:[{id,…}], combos:[{id,…}] }, meta }; the
// fixture's { status, data:[{itemId, rank}] | [id] } is accepted too): the ids in server order, present in the menu,
// at most 5 products and 5 combos. A 404 body (nginx HTML) gives [].
//
// Images go through OO.format.imageUrl: absolute URLs pass, raw base64 becomes a data URI, '' / null / 'null' →
// null, and a relative path resolves via OO.asset, which on our same-origin deploy is REF's API-host rule. Every
// image in the captured get_menu bodies is absolute (4 hosts) or missing, so REF's store-media rule never applies.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var _ = OO.model._, F = _.F, num = _.num, str = _.str, flag = _.flag, arr = _.arr, parseJson = _.parseJson, parseDeep = _.parseDeep, ribbon = _.ribbon, slug = _.slug, resolveNow = _.resolveNow, isAvailable = _.isAvailable, availabilityInfo = _.availabilityInfo, normalizeDomain = _.normalizeDomain;
  var parseRules = OO.model.parseRules;
  function hideOutsideAvailability(store, mode) {
    var actualMode = mode === undefined ? OO.mode : mode;
    return actualMode === 'online' && !!(store && store.settings && store.settings.hideOutsideAvailability) &&
      !(OO.orderingQr && OO.orderingQr.active());
  }

  // ---- availability: the chain's layers, reasons and labels (see "Availability" in the header) ----------------------
  var REASON_LABEL = { blackout: 'Temporarily Unavailable', dateRange: 'Outside Available Date Range', time: 'Not Available At The Selected Time' };
  var DOMAIN_LABEL = {
    ONLINE_DELIVERY: 'Not Available For Delivery', ONLINE_APP_DELIVERY: 'Not Available For Delivery',
    ONLINE_PICKUP: 'Not Available For Pickup', ONLINE_APP_PICKUP: 'Not Available For Pickup',
    TABLE_SIDE: 'Not Available For Dine-In', KIOSK: 'Not Available At This Kiosk',
  };
  // REF looksLikeRule (availabilityService.ts): an entry is a rule when it carries one of these keys or type 'unavailable'.
  var RULE_KEYS = ['DM', 'dm', 'domains', 'D', 'd', 'days', 'H', 'h', 'timings', 'SD', 'sd', 'ED', 'ed', 'from', 'to'];

  // A rule list, unwrapping the { availability: [...] } object some categories carry (a JSON string of one too).
  function rulesOf(v) {
    var x = typeof v === 'string' ? parseJson(v, null) : v;
    return x && typeof x === 'object' && !Array.isArray(x) && x.availability !== undefined ? x.availability : v;
  }
  // A missing active flag counts as active; otherwise it must be a true flag (0 / '0' / false switch the entity off).
  function activeFlag(v) { return absent(v) || flag(v); }
  function failure(code, ctx, label) {
    return { code: code, label: label || (code === 'domain' ? DOMAIN_LABEL[normalizeDomain(ctx.domain)] : REASON_LABEL[code]) || 'Not Available' };
  }
  function hardOff(item) { return flag(item.is_inactive) || item.is_available === false || item.is_available === 0 || item.is_available === '0'; }

  function ruleShaped(r) {
    if (!r || typeof r !== 'object' || Array.isArray(r)) return false;
    return str(r.type).trim().toLowerCase() === 'unavailable' || RULE_KEYS.some(function (k) { return Object.prototype.hasOwnProperty.call(r, k); });
  }
  // One rule source → { source, rules } (model.jsx parseRules output), or null when it holds no rule. shapedOnly keeps
  // the rule-shaped entries only (catAvailability: its legacy category_availabilities rows are not rules).
  function rulesLayer(source, v, shapedOnly) {
    var list = rulesOf(v);
    if (shapedOnly) {
      var x = typeof list === 'string' ? parseDeep(list) : list;
      list = Array.isArray(x) ? x.filter(ruleShaped) : [];
    }
    var rules = parseRules(list);
    return rules.length ? { source: source, rules: rules } : null;
  }
  // A switch that is off: it fails at every minute ('inactive', with its label).
  function offLayer(source, label) { return { source: source, off: true, label: label || null }; }
  function layers(list) { return list.filter(Boolean); }

  // Each entity's own layers, in REF order (the source is the raw field that holds them).
  function categoryChain(rc) {
    return layers([
      flag(rc.inactive) || flag(rc.is_inactive) ? offLayer('inactive', ribbon(rc.inactive_reason)) : null,
      rulesLayer('availability', rc.availability),
      rulesLayer('catAvailability', rc.catAvailability, true),
    ]);
  }
  // A product row after its category (menu items, combo options). subRow = its sub-category row: its rules stand in
  // when the row itself carries none (REF).
  function itemChain(item, subRow) {
    var sub = rulesLayer('sub_cat_availability', item.sub_cat_availability) || rulesLayer('sub_cat_availability', item.subCatAvailability);
    if (!sub && subRow) {
      var own = rulesLayer('sub_cat_availability', subRow.availability), legacy = rulesLayer('sub_cat_availability', subRow.catAvailability, true);
      var rules = (own ? own.rules : []).concat(legacy ? legacy.rules : []);
      if (rules.length) sub = { source: 'sub_cat_availability', rules: rules };
    }
    return layers([
      rulesLayer('prod_cat_availability', item.prod_cat_availability),
      activeFlag(item.category_active) ? null : offLayer('category_active', null),
      rulesLayer('category_availability', item.category_availability),
      sub,
      rulesLayer('availability', item.availability),
    ]);
  }
  function comboChain(rc) {
    return layers([
      hardOff(rc) ? offLayer('is_inactive', ribbon(rc.inactive_reason)) : null,
      activeFlag(rc.category_active) ? null : offLayer('category_active', null),
      rulesLayer('category_availability', rc.category_availability),
      rulesLayer('availability', rc.availability),
    ]);
  }
  function namedMenuChain(m, listsSome) {
    return layers([
      flag(m.enabled) ? null : offLayer('enabled', null),
      listsSome ? null : offLayer('refs', null),
      rulesLayer('availability', m.availability),
    ]);
  }

  // The first failing layer of a chain at ctx.clock / ctx.domain → { code, label } | null.
  function chainFailure(chain, ctx) {
    for (var i = 0; i < chain.length; i++) {
      var l = chain[i];
      if (l.off) return failure('inactive', ctx, l.label);
      var info = availabilityInfo(l.rules, ctx.clock, ctx.domain);
      if (!info.available) return failure(info.reason || 'time', ctx);
    }
    return null;
  }
  // The same as a yes / no (isAvailable: no reason, no day scan), for the next-available search.
  function chainOk(chain, clock, domain) {
    for (var i = 0; i < chain.length; i++) if (chain[i].off || !isAvailable(chain[i].rules, clock, domain)) return false;
    return true;
  }

  // ---- the combo `min` rule (REF filterAvailable hasEnoughChoices) -------------------------------------------------
  // How many available options a group needs: none when optional (min 0); one when allow_same lets a pick repeat;
  // else min.
  function needOf(g) { return g.min >= 1 ? (g.allowSame ? 1 : g.min) : 0; }
  // The first group with fewer available options (ok(option)) than it needs → { name, label, count } | null.
  function shortGroup(groups, ok) {
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i], need = needOf(g);
      if (!need) continue;
      var n = arr(g.options).filter(ok).length;
      if (n < need) return { name: g.name, label: requiredChoiceLabel(g.name, g.min, n), count: n };
    }
    return null;
  }
  // REF getRequiredChoiceUnavailableReason (its count sentence; the option's own reason is not appended).
  function requiredChoiceLabel(name, required, available) {
    var g = str(name).trim() || 'This Required Group';
    var n = num(required), k = num(available);
    if (k <= 0) return g + ' Requires ' + n + ' ' + (n === 1 ? 'Choice' : 'Choices') + ', But No Valid Options Are Available.';
    return g + ' Requires ' + n + ' Choices, But Only ' + k + ' ' + (k === 1 ? 'Is' : 'Are') + ' Available.';
  }

  // ---- menu ------------------------------------------------------------------------------------
  var PLURAL_ONLY = ['chips', 'fries', 'nachos', 'wings', 'greens', 'noodles', 'drinks?'];
  function nounOf(name) {
    var s = String(name || '').trim().replace(/^(choose|pick|select|add|choice\s+of)\s+((an?|your|one|any|the)\s+)?/i, '').toLowerCase();
    if (!s) return 'item';
    return s.replace(/([a-z]+)$/, function (w) {
      if (PLURAL_ONLY.indexOf(w) >= 0 || /(ss|us|is)$/.test(w)) return w;
      if (/ies$/.test(w) && w.length > 4) return w.slice(0, -3) + 'y';
      if (/(ches|shes|xes|sses)$/.test(w)) return w.slice(0, -2);
      return /s$/.test(w) ? w.slice(0, -1) : w;
    });
  }

  function absent(v) { return v === null || v === undefined || v === '' || v === 'null'; }
  function firstOf(o, keys) {
    for (var i = 0; i < keys.length; i++) if (!absent(o[keys[i]])) return o[keys[i]];
    return null;
  }
  // Stable sort by `position` (the sent order breaks ties).
  function byPosition(list) {
    return list.map(function (x, i) { return { x: x, i: i }; }).sort(function (a, b) { return a.x.position - b.x.position || a.i - b.i; })
      .map(function (w) { return w.x; });
  }

  function normalizeSizes(list, ctx) {
    return byPosition(arr(parseJson(list, [])).filter(function (s) { return s && typeof s === 'object'; }).map(function (s, i) {
      return {
        id: num(s.id), name: str(s.size_name), price: num(absent(s.size_amount) ? s.price : s.size_amount),
        position: s.position == null ? i : num(s.position),
        excludeOptionIds: arr(parseJson(s.modifiesxids, [])).map(num),
        preselect: flag(s.preselect), available: isAvailable(rulesOf(s.availability), ctx.clock, ctx.domain),
      };
    }));
  }

  function groupHint(g) {
    if (!g.required && g.minRaw > 1) {
      if (g.unlimited) return 'Optional · choose at least ' + g.minRaw + ' if selected';
      return 'Optional · choose ' + g.minRaw + (g.minRaw === g.max ? '' : '–' + g.max) + ' if selected';
    }
    if (g.max === 1) return g.required ? 'Choose 1' : 'Choose up to 1';
    if (g.min > 0 && g.min === g.max) return 'Choose ' + g.min;
    // Design: Fat Boy add-ons (4 options, max 9) read "Choose any"; poutine add-ons (3 options, max 3) "Choose up to 3".
    if (g.unlimited || (g.max > g.options.length && !g.allowSame)) return g.min > 0 ? 'Choose at least ' + g.min : 'Choose any';
    return g.min > 0 ? 'Choose ' + g.min + '–' + g.max : 'Choose up to ' + g.max;
  }

  // max_no_price_count → a whole number ≥ 0 (junk = 0).
  function freeCountOf(v) { var n = Math.floor(num(v)); return n > 0 ? n : 0; }
  function optionPrice(a) { return num(firstOf(a, ['price', 'extra_price', 'sale_price'])); }

  // A missing position ('' / null, as the BE sends many) counts as 0: the BE's ORDER BY sends those first.
  function normalizeOption(a, ctx) {
    return {
      id: num(a.addOnId), name: str(a.addOnName), price: optionPrice(a), image: F().imageUrl(firstOf(a, ['mod_image', 'imageUrl', 'image'])),
      preselect: flag(a.preselect), available: isAvailable(rulesOf(a.availability), ctx.clock, ctx.domain),
      position: num(a.position), alert: flag(a.is_alert) && ribbon(a.alert_text) ? { text: ribbon(a.alert_text) } : null,
    };
  }

  // One merged group (see "Groups" in the header) → Group.
  function finishGroup(m, ctx) {
    var options = byPosition(m.options.map(function (a) { return normalizeOption(a, ctx); }));
    var unlimited = m.unbounded;
    var max = unlimited ? (m.allowSame ? Number.MAX_SAFE_INTEGER : Math.max(options.length, 1)) : m.max;
    var g = {
      id: m.id, name: str(m.first.label), min: m.required ? Math.min(Math.max(m.minRaw, 1), max) : 0, max: max,
      required: m.required, allowSame: m.allowSame, unlimited: unlimited, position: num(m.first.position),
      available: isAvailable(m.rules, ctx.clock, ctx.domain), sizeLike: /size/i.test(str(m.first.label)), options: options,
      minRaw: m.minRaw, freeCount: m.freeCount,
    };
    g.hint = groupHint(g);
    return g;
  }

  function mergeGroups(item, ctx) {
    var byId = {};
    var order = [];
    ['groupstoproduct', 'group', 'groups'].forEach(function (key) {
      arr(parseJson(item[key], [])).forEach(function (raw) {
        if (!raw || typeof raw !== 'object' || raw.groupId == null) return;
        var id = num(raw.groupId);
        var m = byId[id];
        if (!m) {
          m = byId[id] = { id: id, first: raw, required: false, allowSame: false, minRaw: 0, max: 0, unbounded: false, freeCount: 0, rules: [], options: [], seen: {} };
          order.push(m);
        }
        m.required = m.required || flag(raw.is_mandatory) || flag(raw.required) || flag(raw.is_required);
        m.allowSame = m.allowSame || flag(raw.allow_same);
        m.minRaw = Math.max(m.minRaw, Math.max(0, Math.floor(num(raw.min_modifier))));
        var max = Math.floor(num(raw.max_modifier));
        if (max > 0) m.max = Math.max(m.max, max); else m.unbounded = true;
        if (!m.freeCount) m.freeCount = freeCountOf(raw.max_no_price_count);
        if (!m.rules.length) m.rules = parseRules(rulesOf(raw.availability));
        arr(raw.addOns).forEach(function (a) {
          if (!a || typeof a !== 'object' || a.addOnId == null || m.seen[num(a.addOnId)]) return;
          m.seen[num(a.addOnId)] = true;
          m.options.push(a);
        });
      });
    });
    return order.map(function (m, i) { return { g: finishGroup(m, ctx), i: i }; }).sort(function (a, b) {
      return (b.g.sizeLike - a.g.sizeLike) || (a.g.position - b.g.position) || (a.i - b.i);
    }).map(function (x) { return x.g; });
  }

  function tagsOf(v) {
    return arr(parseJson(v, [])).map(function (t) { return typeof t === 'string' ? t : str(t && (t.tag || t.name)); }).filter(Boolean);
  }

  // cat = { id, fail (its chain's failure now), subRows ({ [sub_id]: raw sub-category row }), av (its age flag) } | null
  function normalizeProduct(item, cat, ctx) {
    var f = F();
    var price = num(item.sale_price);
    var was = num(item.banner_price);
    var soldOut = hardOff(item);
    var chain = itemChain(item, cat && item.sub_cat_id != null ? cat.subRows[num(item.sub_cat_id)] : null);
    var fail = (cat && cat.fail) || chainFailure(chain, ctx);
    return {
      id: num(item.itemId),
      kind: 'product',
      name: str(item.label),
      description: f.stripHtml(item.description),
      price: price,
      image: f.imageUrl(item.imageUrl),
      sizes: normalizeSizes(item.sizes, ctx),
      groups: mergeGroups(item, ctx),
      available: !fail,
      soldOut: soldOut,
      inactiveReason: soldOut ? ribbon(item.inactive_reason) : (fail ? fail.label : null),
      unavailableReason: fail ? fail.code : null,
      chain: chain,
      categoryId: cat ? cat.id : null,
      subcategoryId: item.sub_cat_id != null ? num(item.sub_cat_id) : null,
      subcategoryName: str(item.sub_cat_name) || null,
      ribbon: ribbon(item.banner),
      wasPrice: was > 0 && was !== price ? was : null,
      tags: tagsOf(item.tags),
      hideNotes: flag(item.special_instruction_hide),
      alert: flag(item.is_alert) && str(item.alert_text) ? { text: str(item.alert_text) } : null,
      isLoyalty: flag(item.is_loyalty_product),
      weighted: flag(item.weight),
      ageRestricted: flag(item.av) || !!(cat && cat.av),
    };
  }

  function comboCategoryKey(rc) { return rc.combo_cat_id != null ? 'combo-' + rc.combo_cat_id : 'combo-' + slug(rc.category_name || 'deals'); }
  // The BE's zero date ('0000-00-00 00:00:00') and blanks are no date.
  function dateOrNull(v) { var s = str(v).trim(); return s && !/^0000-00-00/.test(s) ? s : null; }

  function sizeModeOf(p, sizeId, sizes) {
    var m = str(p.size_mode).trim().toLowerCase();
    if (m === 'none' || m === 'fixed') return m;
    if (m === 'choice') return sizes.length ? 'choice' : 'none';
    return sizeId != null ? 'fixed' : (sizes.length ? 'choice' : 'none');
  }

  function normalizeComboOption(p, ctx, products) {
    var pid = num(p.product_id != null ? p.product_id : p.itemId);
    var menuProduct = products[pid] || null;
    var sizes = normalizeSizes(p.sizes, ctx);
    var configured = p.configured_size && typeof p.configured_size === 'object' ? p.configured_size : null;
    var sizeId = !absent(p.size_id) ? num(p.size_id) : null;
    var sizeMode = sizeModeOf(p, sizeId, sizes);
    if (sizeId == null && sizeMode === 'fixed' && configured && !absent(configured.id)) sizeId = num(configured.id);
    var size = sizeId != null ? sizes.filter(function (s) { return s.id === sizeId; })[0] : null;
    var soldOut = hardOff(p);
    var chain = itemChain(p, null);
    return {
      mappingId: num(p.mapping_id),
      productId: pid,
      name: str(p.label) || (menuProduct ? menuProduct.name : ''),
      description: menuProduct ? menuProduct.description : '',
      image: F().imageUrl(p.imageUrl) || (menuProduct ? menuProduct.image : null),
      extraPrice: num(p.extra_price),
      price: size ? size.price : (configured && configured.size_amount != null ? num(configured.size_amount) : num(p.sale_price)),
      isDefault: flag(p.is_default),
      available: !soldOut && !chainFailure(chain, ctx),
      soldOut: soldOut,
      chain: chain,
      sizeId: sizeId,
      sizeName: size ? size.name : (configured ? str(configured.size_name) || null : null),
      sizeMode: sizeMode,
      sizes: sizes,
      groups: mergeGroups(p, ctx),
    };
  }

  function normalizeCombo(rc, ctx, products) {
    var f = F();
    var info = availabilityInfo(rulesOf(rc.availability), ctx.clock, ctx.domain);
    var catInfo = availabilityInfo(rulesOf(rc.category_availability), ctx.clock, ctx.domain);
    var on = !hardOff(rc) && activeFlag(rc.category_active); // the combo's switches (availableToday / todayOnly)
    var chain = comboChain(rc);
    var fail = chainFailure(chain, ctx);
    var groups = byPosition(arr(rc.cat_mappings).map(function (m, gi) {
      var name = str(m.group_name) || 'Item';
      var min = Math.max(0, num(m.min));
      return {
        id: num(m.id != null ? m.id : m.categoryId),
        name: name,
        noun: nounOf(name),
        min: min,
        max: Math.max(min, num(m.max) || min || 1),
        allowSame: flag(m.allow_same),
        position: m.position == null ? gi : num(m.position),
        options: arr(m.products).map(function (p) { return normalizeComboOption(p, ctx, products); }),
      };
    }));
    // The `min` rule: a required group with fewer available options than it needs (all sold out / outside their
    // window, or fewer than min distinct picks without allow_same) makes the whole deal unorderable: it could never be
    // completed (a single-option group would otherwise be "added automatically").
    var lacking = shortGroup(groups, function (o) { return o.available !== false; });
    return {
      id: num(rc.combo_id),
      kind: 'combo',
      name: str(rc.combo_name),
      description: f.stripHtml(rc.description),
      descriptionHtml: str(rc.description),
      price: num(rc.combo_price),
      image: f.imageUrl(rc.image),
      ribbon: ribbon(rc.banner),
      available: !fail && !lacking,
      unavailableReason: fail ? fail.code : (lacking ? 'group' : null),
      inactiveReason: fail ? fail.label : (lacking ? lacking.label : null),
      unavailableGroup: lacking ? lacking.name : null,
      availableToday: on && info.today && catInfo.today,
      todayOnly: on && (info.restricted || catInfo.restricted) && info.today && catInfo.today,
      categoryId: comboCategoryKey(rc),
      comboCategoryId: absent(rc.combo_cat_id) ? null : num(rc.combo_cat_id),
      categoryName: str(rc.category_name),
      startDate: dateOrNull(rc.startdate),
      endDate: dateOrNull(rc.enddate),
      groups: groups,
      chain: chain,
    };
  }

  function categoryMeta(c) {
    var f = F();
    return c.subcategories.length > 1 ? c.subcategories.length + ' categories' : f.pluralize(c.itemCount, 'item');
  }

  // Named menus (raw `menus`) → NamedMenu[] (see the header).
  function refIds(list, key) {
    return byPosition(arr(list).map(function (r, i) {
      var o = r && typeof r === 'object';
      return { id: num(o ? r[key] : r), position: o && !absent(r.position) ? num(r.position) : i };
    })).map(function (r) { return r.id; });
  }
  function normalizeNamedMenus(list, ctx) {
    return byPosition(arr(list).filter(function (m) { return m && typeof m === 'object'; }).map(function (m, i) {
      var info = availabilityInfo(rulesOf(m.availability), ctx.clock, ctx.domain);
      var enabled = flag(m.enabled);
      var out = {
        id: num(m.id), name: str(m.name), description: str(m.description), position: absent(m.position) ? i : num(m.position),
        enabled: enabled, available: false, availableToday: false, chain: [],
        categoryIds: refIds(m.categories, 'category_id'), comboIds: refIds(m.combos, 'combo_id'),
        comboCategoryIds: refIds(m.combo_categories, 'combo_category_id'),
      };
      var listsSome = out.categoryIds.length + out.comboIds.length + out.comboCategoryIds.length > 0;
      out.chain = namedMenuChain(m, listsSome);
      out.available = !chainFailure(out.chain, ctx);
      out.availableToday = enabled && listsSome && info.today;
      return out;
    }));
  }

  // A category's raw sub-category rows by sub_id (hidden ones included: their rules still reach their products).
  function subRowsOf(rc) {
    var out = {};
    arr(rc.sub_categories).forEach(function (s) {
      var id = s && typeof s === 'object' ? firstOf(s, ['sub_id', 'sub_cat_id']) : null;
      if (id != null && !out[num(id)]) out[num(id)] = s;
    });
    return out;
  }

  function normalizeMenu(raw, opts) {
    opts = opts || {};
    var clock = resolveNow(opts.now, opts.timeZone || null).clock;
    var ctx = { clock: clock, domain: raw && raw.ordering_qr ? 'TABLE_SIDE' : opts.domain || 'ONLINE_PICKUP' };
    var products = {};
    // Reward-only products never enter normal categories, search, popular items or menu cards.
    var redemptions = null;
    if (raw && Array.isArray(raw.redemption_products)) {
      redemptions = {};
      raw.redemption_products.forEach(function (row) {
        if (!row || flag(row.is_delete)) return;
        var cat = { id: row.category_id == null ? null : num(row.category_id), fail: null, subRows: {}, av: flag(row.category_av) };
        var p = normalizeProduct(row, cat, ctx);
        if (p.id > 0) redemptions[p.id] = p;
      });
    }
    var menuCats = arr(raw && raw.data).map(function (rc, idx) {
      var id = num(rc.categoryId);
      var chain = categoryChain(rc);
      var fail = chainFailure(chain, ctx);
      var cat = { id: id, fail: fail, subRows: subRowsOf(rc), av: flag(rc.av) };
      var items = arr(rc.item);
      var productIds = [];
      items.forEach(function (it) {
        var p = normalizeProduct(it, cat, ctx);
        if (!products[p.id]) products[p.id] = p;
        if (productIds.indexOf(p.id) < 0) productIds.push(p.id);
      });
      var subs = arr(rc.sub_categories).filter(function (s) { return s && !flag(s.is_hidden); }).map(function (s, i) {
        var sid = num(s.sub_id);
        return {
          id: sid, key: slug(s.sub_name), name: str(s.sub_name), description: str(s.sub_description),
          position: s.position == null ? i : num(s.position),
          productIds: items.filter(function (it) { return it.sub_cat_id != null && num(it.sub_cat_id) === sid; }).map(function (it) { return num(it.itemId); }),
        };
      }).filter(function (s) { return s.productIds.length > 0; }).sort(function (a, b) { return a.position - b.position; });
      var c = {
        id: id, key: slug(rc.label), kind: 'menu', name: str(rc.label), description: F().stripHtml(rc.description),
        image: F().imageUrl(rc.imageURL), position: rc.position == null ? idx : num(rc.position),
        available: !fail, inactiveReason: fail ? fail.label : null, unavailableReason: fail ? fail.code : null,
        showOnly: flag(rc.show_only), ageRestricted: cat.av, chain: chain,
        subcategories: subs, productIds: productIds, comboIds: [], itemCount: productIds.length,
      };
      c.meta = categoryMeta(c);
      return c;
    });

    var combos = {};
    var comboCats = [];
    var byKey = {};
    var rawCombos = raw && raw.combos;
    var comboList = Array.isArray(rawCombos) ? rawCombos : (rawCombos && typeof rawCombos === 'object'
      ? Object.keys(rawCombos).reduce(function (all, k) { return all.concat(arr(rawCombos[k])); }, []) : []);
    comboList.forEach(function (rc) {
      var combo = normalizeCombo(rc, ctx, products);
      combos[combo.id] = combo;
      var key = combo.categoryId;
      if (!byKey[key]) {
        byKey[key] = {
          id: key, key: slug(rc.category_name || 'deals'), kind: 'combo', name: str(rc.category_name) || 'Deals', description: '',
          image: null, position: num(rc.category_position), available: false, inactiveReason: null, unavailableReason: null,
          showOnly: false, ageRestricted: false, chain: [], subcategories: [], productIds: [], comboIds: [], itemCount: 0,
        };
        comboCats.push(byKey[key]);
      }
      byKey[key].comboIds.push(combo.id);
      byKey[key].itemCount += 1;
      if (combo.available) byKey[key].available = true;
    });
    comboCats.forEach(function (c) { c.meta = categoryMeta(c); });
    comboCats.sort(function (a, b) { return a.position - b.position; });

    return {
      version: str(raw && raw.menu_version) || null,
      type: str(raw && raw.menu_type) || null,
      categories: comboCats.concat(menuCats),
      products: products,
      combos: combos,
      menus: normalizeNamedMenus(raw && raw.menus, ctx),
      timeZone: opts.timeZone || null,
      domain: ctx.domain,
      ...(redemptions !== null ? { redemptionProducts: redemptions } : {}),
      ...(raw && Array.isArray(raw.redemption_coupon_ids) ? { redemptionCouponIds: raw.redemption_coupon_ids.map(num) } : {}),
      ...(raw && raw.ordering_qr ? { orderingQr: raw.ordering_qr } : {}),
    };
  }

  // A view used only by redemption pickers and reward lines. An empty new catalogue is authoritative; never
  // resurrect a deleted reward from the older normal-menu cache. Old API responses retain their existing behaviour.
  var redemptionMenus = new WeakMap();
  function redemptionMenu(menu) {
    if (!menu || !Object.prototype.hasOwnProperty.call(menu, 'redemptionProducts')) return menu;
    var view = redemptionMenus.get(menu);
    if (!view) {
      view = Object.assign({}, menu, { products: menu.redemptionProducts, categories: [] });
      redemptionMenus.set(menu, view);
      redemptionMenus.set(view, view);
    }
    return view;
  }

  // ---- availabilityAt: the chain at any minute, in any domain (see the header) ------------------------------------
  var NEXT_DAYS = 7; // today and the next 7 days (REF getNextAvailableLabel's offsets 0..7)

  function sameId(a, b) { return a != null && b != null && String(a) === String(b); }
  function categoryOf(menu, id) { return arr(menu.categories).filter(function (c) { return sameId(c.id, id); })[0] || null; }
  function namedMenuOf(menu, id) { return id == null ? null : arr(menu.menus).filter(function (m) { return sameId(m.id, id); })[0] || null; }
  // An entity's layers; a hand-built object without them falls back to its static flag.
  function chainOf(e) {
    if (Array.isArray(e.chain)) return e.chain;
    return e.available === false ? [offLayer('available', e.inactiveReason)] : [];
  }
  function optionOk(o, clock, domain) {
    return !o.soldOut && (Array.isArray(o.chain) ? chainOk(o.chain, clock, domain) : o.available !== false);
  }

  // Whether named menu nm lists the target: a product through a category holding it, a combo by combo_id or
  // combo_cat_id, a category by id (a combo category through its combos).
  function listedIn(nm, menu, kind, e) {
    if (kind === 'product') {
      return arr(menu.categories).some(function (c) { return c.kind === 'menu' && nm.categoryIds.indexOf(c.id) >= 0 && arr(c.productIds).indexOf(e.id) >= 0; });
    }
    if (kind === 'combo') return nm.comboIds.indexOf(e.id) >= 0 || (e.comboCategoryId != null && nm.comboCategoryIds.indexOf(e.comboCategoryId) >= 0);
    if (e.kind !== 'combo') return nm.categoryIds.indexOf(e.id) >= 0;
    return arr(e.comboIds).some(function (id) { return !!menu.combos[id] && listedIn(nm, menu, 'combo', menu.combos[id]); });
  }

  // target → { kind: 'product' | 'combo' | 'category' | 'namedMenu', entity } | null (an entity of `menu`).
  function targetOf(menu, target) {
    var kind = target.kind, id = target.id, e = null;
    if (kind === 'product') e = menu.products ? menu.products[id] : null;
    else if (kind === 'combo' && !/^combo-/.test(String(id))) e = menu.combos ? menu.combos[id] : null;
    else if (kind === 'category' || kind === 'menu' || kind === 'combo') { kind = 'category'; e = categoryOf(menu, id); }
    else if (kind === 'namedMenu') e = namedMenuOf(menu, id);
    return e ? { kind: kind, entity: e } : null;
  }

  // The checks, in chain order; the first that fails wins. { source, fixed } always fails (sold out, not on the
  // named menu); { source, chain } fails at its first failing layer; { source:'group', groups } is the `min` rule.
  function checksOf(menu, t, menuId) {
    var checks = [];
    var e = t.entity;
    if (t.kind === 'namedMenu') return [{ source: 'menu', chain: chainOf(e) }];
    var nm = namedMenuOf(menu, menuId);
    if (nm) {
      if (!listedIn(nm, menu, t.kind, e)) checks.push({ source: 'menu', fixed: { code: 'menu', label: 'Not On This Menu' } });
      checks.push({ source: 'menu', chain: chainOf(nm) });
    }
    if (t.kind === 'category') return checks.concat([{ source: 'category', chain: chainOf(e) }]);
    if (t.kind === 'combo') return checks.concat([{ source: 'combo', chain: chainOf(e) }, { source: 'group', groups: arr(e.groups) }]);
    if (e.soldOut) checks.push({ source: 'product', fixed: { code: 'soldOut', label: e.inactiveReason || 'Sold Out' } });
    var cat = e.categoryId != null ? categoryOf(menu, e.categoryId) : null;
    if (cat) checks.push({ source: 'category', chain: chainOf(cat) });
    checks.push({ source: 'product', chain: chainOf(e) });
    return checks;
  }

  function failAt(checks, ctx) {
    for (var i = 0; i < checks.length; i++) {
      var c = checks[i], f = null;
      if (c.fixed) f = c.fixed;
      else if (c.chain) f = chainFailure(c.chain, ctx);
      else {
        var s = shortGroup(c.groups, function (o) { return optionOk(o, ctx.clock, ctx.domain); });
        if (s) f = { code: 'group', label: s.label, group: s.name };
      }
      if (f) return { code: f.code, label: f.label, source: c.source, group: f.group || null };
    }
    return null;
  }
  function okAt(checks, clock, domain) {
    for (var i = 0; i < checks.length; i++) {
      var c = checks[i];
      if (c.fixed) return false;
      if (c.chain ? !chainOk(c.chain, clock, domain) : !!shortGroup(c.groups, function (o) { return optionOk(o, clock, domain); })) return false;
    }
    return true;
  }

  // Within one day the chain only changes at midnight, at a window's start S and at the minute after its end E, so
  // those minutes of every layer (a combo's options included) are the only candidates.
  function marksOf(checks) {
    var marks = [0];
    function add(chain) {
      arr(chain).forEach(function (l) {
        arr(l.rules).forEach(function (r) { arr(r.H).forEach(function (w) { marks.push(w.S, w.E + 1); }); });
      });
    }
    checks.forEach(function (c) {
      add(c.chain);
      arr(c.groups).forEach(function (g) { arr(g.options).forEach(function (o) { add(o.chain); }); });
    });
    return marks.filter(function (m, i) { return m >= 0 && m < 1440 && marks.indexOf(m) === i; }).sort(function (a, b) { return a - b; });
  }
  // The first minute after `clock` (today + NEXT_DAYS) at which every check passes → { ymd, dow, minutes, hm } | null.
  // A fixed failure or a switch that is off never passes: no search.
  function nextOf(checks, clock, domain) {
    var never = checks.some(function (c) { return !!c.fixed || arr(c.chain).some(function (l) { return l.off; }); });
    if (never) return null;
    var marks = marksOf(checks);
    for (var k = 0; k <= NEXT_DAYS; k++) {
      var ymd = k ? F().addDays(clock.ymd, k) : clock.ymd;
      var dow = (num(clock.dow) + k) % 7;
      for (var i = 0; i < marks.length; i++) {
        if (k === 0 && marks[i] <= clock.minutes) continue;
        var at = { ymd: ymd, dow: dow, minutes: marks[i], hm: F().hm(marks[i]) };
        if (okAt(checks, at, domain)) return at;
      }
    }
    return null;
  }
  function earlier(a, b) { return a.ymd < b.ymd || (a.ymd === b.ymd && a.minutes < b.minutes); }

  function availabilityAt(menu, target, opts) {
    opts = opts || {};
    if (!menu || !target || typeof target !== 'object') return null;
    var t = targetOf(menu, target);
    if (!t) return null;
    var tz = opts.timeZone !== undefined ? opts.timeZone : (menu.timeZone || null);
    if (t.kind === 'category' && t.entity.kind === 'combo') {
      // A combo category: the union of its combos.
      var all = arr(t.entity.comboIds).map(function (id) { return availabilityAt(menu, { kind: 'combo', id: id }, opts); }).filter(Boolean);
      var open = all.filter(function (r) { return r.available; })[0];
      if (open || !all.length) return open || null;
      var soon = all.filter(function (r) { return r.next; }).reduce(function (best, r) { return !best || earlier(r.next, best.next) ? r : best; }, null);
      return {
        available: false, reason: all[0].reason, label: all[0].label, source: all[0].source, group: all[0].group,
        next: soon ? soon.next : null, nextAt: soon ? soon.nextAt : null,
      };
    }
    var clock = resolveNow(opts.now, tz).clock;
    var dom = domainFor(menu, opts);
    var checks = checksOf(menu, t, opts.menuId);
    var fail = failAt(checks, { clock: clock, domain: dom });
    var next = fail ? nextOf(checks, clock, dom) : null;
    return {
      available: !fail,
      reason: fail ? fail.code : null,
      label: fail ? fail.label : null,
      source: fail ? fail.source : null,
      group: fail ? fail.group : null,
      next: next,
      nextAt: next ? F().instantOf(next.ymd, next.minutes, tz) : null,
    };
  }

  // ---- displayState: the D-07 display policy (see "displayState" in the header) -----------------------------------
  var SOLD_OUT = 'Sold out', NOT_AVAILABLE = 'Not available';
  var WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var RESTORABLE = ['soldOut', 'inactive', 'group']; // the switches staff can turn back on (and a group short of them)

  // 'h:mm AM': minutes after midnight on a 12-hour clock with the period upper-cased (REF formats with Intl en-NZ and
  // upper-cases am / pm; built by hand here, so no ICU puts a U+202F before the period).
  function clock12(minutes) {
    var m = ((Math.floor(num(minutes)) % 1440) + 1440) % 1440, h = Math.floor(m / 60), mi = m % 60;
    return ((h % 12) || 12) + ':' + (mi < 10 ? '0' : '') + mi + ' ' + (h < 12 ? 'AM' : 'PM');
  }
  // REF formatNextAvailable's text for a next minute (availabilityAt's { ymd, minutes }), its day worded from todayYmd.
  function nextAvailableLabel(next, todayYmd) {
    if (!next || !next.ymd) return null;
    var day = next.ymd === todayYmd ? 'Today' : next.ymd === F().addDays(todayYmd, 1) ? 'Tomorrow' : WEEKDAY[F().weekdayOf(next.ymd)];
    if (!num(next.minutes)) return day === 'Today' ? 'Available Now' : 'Available ' + day;
    return day === 'Today' ? 'Available Today From ' + clock12(next.minutes) : 'Available ' + day + ' At ' + clock12(next.minutes);
  }

  // availabilityAt's domain for opts.
  function domainFor(menu, opts) {
    if (menu.orderingQr) return 'TABLE_SIDE';
    return opts.domain || (opts.orderType != null ? M.domain(opts.orderType, opts.mode, opts) : (menu.domain || 'ONLINE_PICKUP'));
  }
  // The checks with every state staff can restore lifted: the sold-out check, each `off` switch, and sold-out or
  // switched-off combo options. A fixed 'menu' check (not on the named menu) stays: no switch restores it.
  function liftOption(o) {
    return Object.assign({}, o, { soldOut: false, available: true, chain: arr(o.chain).filter(function (l) { return !l.off; }) });
  }
  function liftedChecks(checks) {
    return checks.filter(function (c) { return !(c.fixed && c.fixed.code === 'soldOut'); }).map(function (c) {
      if (c.chain) return { source: c.source, chain: c.chain.filter(function (l) { return !l.off; }) };
      if (c.groups) return { source: c.source, groups: c.groups.map(function (g) { return Object.assign({}, g, { options: arr(g.options).map(liftOption) }); }) };
      return c;
    });
  }
  // The label of the switch that failed first ('inactive'): its own inactive_reason, else "Not available". The first
  // failing check fails at its first `off` layer (every earlier layer passed, and an `off` layer always fails).
  function offLabel(checks, ctx) {
    for (var i = 0; i < checks.length; i++) {
      if (!failAt([checks[i]], ctx)) continue;
      var off = arr(checks[i].chain).filter(function (l) { return l.off; })[0];
      return (off && off.label) || NOT_AVAILABLE;
    }
    return NOT_AVAILABLE;
  }
  // A combo category: the union of its combos' Displays (normal when one is; else the soonest grey one, else the first
  // grey one; else hidden). null without one.
  function unionDisplay(list) {
    if (!list.length) return null;
    var normal = list.filter(function (d) { return d.show === 'normal'; })[0];
    if (normal) return normal;
    var grey = list.filter(function (d) { return d.show === 'grey'; });
    var soon = grey.filter(function (d) { return d.next; }).reduce(function (best, d) { return !best || earlier(d.next, best.next) ? d : best; }, null);
    return soon || grey[0] || list[0];
  }

  // With fewer than two pickable named menus the legacy picker applies no menu scope. For an
  // opted-in store, content listed only on timed-closed named menus must still stay hidden; content
  // shared with an open menu or not assigned to a named menu keeps its usual behaviour.
  function closedNamedMenu(menu, target, ctx) {
    var matches = arr(menu.menus).filter(function (nm) { return nm.enabled && listedIn(nm, menu, target.kind, target.entity); });
    if (!matches.length) return null;
    var failures = matches.map(function (nm) { return failAt([{ source: 'menu', chain: chainOf(nm) }], ctx); });
    return failures.every(function (f) { return f && ['time', 'blackout', 'dateRange'].indexOf(f.code) >= 0; }) ? failures[0] : null;
  }

  function displayState(menu, target, opts) {
    opts = opts || {};
    if (!menu || !target || typeof target !== 'object') return null;
    var t = targetOf(menu, target);
    if (!t) return null;
    if (t.kind === 'category' && t.entity.kind === 'combo') {
      return unionDisplay(arr(t.entity.comboIds).map(function (id) { return displayState(menu, { kind: 'combo', id: id }, opts); }).filter(Boolean));
    }
    var tz = opts.timeZone !== undefined ? opts.timeZone : (menu.timeZone || null);
    var clock = resolveNow(opts.now, tz).clock;
    var dom = domainFor(menu, opts);
    var ctx = { clock: clock, domain: dom };
    var hideTimed = opts.hideOutsideAvailability && !menu.orderingQr && (dom === 'ONLINE_PICKUP' || dom === 'ONLINE_DELIVERY');
    var closedMenu = (hideTimed || opts.hideUnavailable) && opts.menuId == null && t.kind !== 'namedMenu' ? closedNamedMenu(menu, t, ctx) : null;
    if (closedMenu) return { show: 'hide', label: null, reason: closedMenu.code, source: 'menu', group: null, next: null, nextAt: null };
    var checks = checksOf(menu, t, opts.menuId);
    var fail = failAt(checks, ctx);
    var out = { show: 'normal', label: null, reason: null, source: null, group: null, next: null, nextAt: null };
    if (!fail) return out;
    if (opts.hideUnavailable) return { show: 'hide', label: null, reason: fail.code, source: fail.source, group: fail.group, next: null, nextAt: null };
    var next = nextOf(checks, clock, dom);
    out = { show: 'hide', label: null, reason: fail.code, source: fail.source, group: fail.group, next: next, nextAt: next ? F().instantOf(next.ymd, next.minutes, tz) : null };
    if (fail.code === 'domain' || fail.code === 'menu') return out;
    if (hideTimed) {
      // Ignore staff-controlled sold-out/off switches while checking the schedule: an item which is
      // both sold out and outside its hours is still outside its hours. A combo can also be timed
      // through the options needed to satisfy a required group.
      var timingChecks = liftedChecks(checks);
      var timingFailure = failAt(timingChecks, ctx);
      if (timingFailure && (['time', 'blackout', 'dateRange'].indexOf(timingFailure.code) >= 0 ||
          (timingFailure.code === 'group' && nextOf(timingChecks, clock, dom)))) return out;
    }
    if (next) {
      out.show = 'grey';
      out.label = nextAvailableLabel(next, opts.today != null ? resolveNow(opts.today, tz).clock.ymd : clock.ymd);
      return out;
    }
    if (RESTORABLE.indexOf(fail.code) < 0) return out; // 'time' / 'blackout' / 'dateRange': nothing within the horizon
    var lifted = liftedChecks(checks);
    if (!okAt(lifted, clock, dom) && !nextOf(lifted, clock, dom)) return out; // never, even with every switch back on
    out.show = 'grey';
    out.label = fail.code === 'soldOut' ? t.entity.inactiveReason || SOLD_OUT : fail.code === 'inactive' ? offLabel(checks, ctx) : NOT_AVAILABLE;
    return out;
  }

  // ---- named menus: which one is active (REF MenuSelectionContext; see "Named menus" in the header) ----------------
  // Whether named menu nm lists something visible(kind, id) passes: a product of a menu category it lists, or a combo
  // it lists (by combo_id or combo_cat_id).
  function listsVisible(menu, nm, visible) {
    return arr(menu.categories).some(function (c) {
      return c.kind === 'menu' && nm.categoryIds.indexOf(c.id) >= 0 && arr(c.productIds).some(function (id) { return visible('product', id); });
    }) || Object.keys(menu.combos || {}).some(function (id) { return listedIn(nm, menu, 'combo', menu.combos[id]) && visible('combo', id); });
  }
  function namedMenusWith(menu, opts, visible) {
    var list = arr(menu.menus).filter(function (nm) {
      var r = availabilityAt(menu, { kind: 'namedMenu', id: nm.id }, opts);
      return !!r && r.available && listsVisible(menu, nm, visible);
    }).map(function (nm, i) { return { nm: nm, i: i }; }).sort(function (a, b) {
      return num(a.nm.position) - num(b.nm.position) || num(a.nm.id) - num(b.nm.id) || a.i - b.i;
    }).map(function (w) { return w.nm; });
    var picked = opts.selected == null ? null : list.filter(function (nm) { return sameId(nm.id, opts.selected); })[0] || null;
    var active = list.length < 2 ? null : picked || list[0];
    return { menus: list, activeId: active ? active.id : null, showSelector: list.length >= 2 };
  }
  function namedMenus(menu, opts) {
    if (!menu) return { menus: [], activeId: null, showSelector: false };
    var base = Object.assign({}, opts || {}, { menuId: null });
    var seen = {};
    return namedMenusWith(menu, base, function (kind, id) {
      var k = kind + ':' + id;
      if (!Object.prototype.hasOwnProperty.call(seen, k)) {
        var d = displayState(menu, { kind: kind, id: id }, base);
        seen[k] = !!d && d.show !== 'hide';
      }
      return seen[k];
    });
  }

  // ---- menuDisplay: the whole menu at once (see "menuDisplay" in the header) -----------------------------------------
  function menuDisplay(menu, opts) {
    var out = { menuId: null, menus: [], showSelector: false, products: {}, combos: {}, categories: {} };
    if (!menu) return out;
    var base = Object.assign({}, opts || {}, { menuId: null });
    function judgeAll(o) {
      var d = { products: {}, combos: {} };
      Object.keys(menu.products || {}).forEach(function (id) { d.products[id] = displayState(menu, { kind: 'product', id: id }, o); });
      Object.keys(menu.combos || {}).forEach(function (id) { d.combos[id] = displayState(menu, { kind: 'combo', id: id }, o); });
      return d;
    }
    function shownIn(map) { return function (id) { return !!map[id] && map[id].show !== 'hide'; }; }
    var d = judgeAll(base);
    var nm = namedMenusWith(menu, base, function (kind, id) { return shownIn(kind === 'product' ? d.products : d.combos)(id); });
    var o = base;
    if (nm.activeId != null) {
      o = Object.assign({}, base, { menuId: nm.activeId });
      d = judgeAll(o);
    }
    var product = shownIn(d.products), combo = shownIn(d.combos);
    out.menuId = nm.activeId;
    out.menus = nm.menus;
    out.showSelector = nm.showSelector;
    out.products = d.products;
    out.combos = d.combos;
    arr(menu.categories).forEach(function (c) {
      var pids = arr(c.productIds).filter(product), cids = arr(c.comboIds).filter(combo);
      var own = c.kind === 'combo' ? unionDisplay(cids.map(function (id) { return d.combos[id]; })) : displayState(menu, { kind: 'category', id: c.id }, o);
      var entry = Object.assign({ show: 'hide', label: null, reason: null, source: null, group: null, next: null, nextAt: null }, own || {});
      var hide = !own || own.show === 'hide' || (!pids.length && !cids.length);
      if (hide) entry.show = 'hide';
      entry.productIds = hide ? [] : pids;
      entry.comboIds = hide ? [] : cids;
      entry.subcategories = hide ? [] : arr(c.subcategories).map(function (s) {
        return { id: s.id, productIds: arr(s.productIds).filter(product) };
      }).filter(function (s) { return s.productIds.length > 0; });
      out.categories[c.id] = entry;
    });
    return out;
  }

  function searchMenu(menu, query) {
    var q = String(query || '').trim().toLowerCase();
    if (!q || !menu) return [];
    return Object.keys(menu.products).map(function (k) { return menu.products[k]; }).filter(function (p) {
      return p.name.toLowerCase().indexOf(q) >= 0 || p.description.toLowerCase().indexOf(q) >= 0 || p.tags.some(function (t) { return t.toLowerCase().indexOf(q) >= 0; });
    }).map(function (p) { return p.id; });
  }

  // The menu page's layout: ?layout=categories|sections (route query; case and spaces ignored) overrides
  // store.menuLayout, which normalizeStore derives (model-store.jsx, MENU-LAYOUT): proposed.menu_layout when it is
  // exactly 'categories' | 'sections', else the admin's Online-settings "Auto Menu Popup" toggle (menu_popup_auto):
  // on → 'categories' (the category tiles), off / absent → 'sections' (the full menu). No store yet → 'categories'.
  function menuLayout(store, query) {
    var q = query && typeof query.layout === 'string' ? query.layout.toLowerCase().trim() : '';
    if (q === 'categories' || q === 'sections') return q;
    return store && store.menuLayout === 'sections' ? 'sections' : 'categories';
  }

  // popular-dishes → the menu's entities for one id list, in server order, deduped, at most 5 (see the header).
  var POPULAR_MAX = 5;
  function popularPick(list, pool) {
    var out = [];
    arr(list).forEach(function (x) {
      var id = num(x && typeof x === 'object' ? (x.id != null ? x.id : x.itemId) : x);
      var hit = pool && pool[id];
      if (hit && out.indexOf(hit) < 0 && out.length < POPULAR_MAX) out.push(hit);
    });
    return out;
  }
  function popularData(raw) { return raw && typeof raw === 'object' ? raw.data : null; }
  function popularProducts(raw, menu) {
    var d = popularData(raw);
    return menu ? popularPick(Array.isArray(d) ? d : d && d.products, menu.products) : [];
  }
  function popularCombos(raw, menu) {
    var d = popularData(raw);
    return menu && d && !Array.isArray(d) ? popularPick(d.combos, menu.combos) : [];
  }

  var M = OO.model;
  M.normalizeMenu = normalizeMenu;
  M.availabilityAt = availabilityAt;
  M.hideOutsideAvailability = hideOutsideAvailability;
  M.displayState = displayState;
  M.nextAvailableLabel = nextAvailableLabel;
  M.namedMenus = namedMenus;
  M.menuDisplay = menuDisplay;
  M.redemptionMenu = redemptionMenu;
  M.requiredChoiceLabel = requiredChoiceLabel;
  M.popularProducts = popularProducts;
  M.popularCombos = popularCombos;
  M.searchMenu = searchMenu;
  M.menuLayout = menuLayout;
  M.nounOf = nounOf;
})();


// ===================== src/lib/model-commerce.jsx =====================
// src/lib/model-commerce.jsx: OO.model's coupons and pricetags, on the model.jsx core (P2: suggestions, membership). API:
//   normalizeCoupons(rawCouponGetV4, {now, timeZone, domain}) → Coupon[]   (coupon_get_v4, §3.6, E09; expired dropped)
//   normalizePricetags(rawPricetagOrder, rawPricetagPrices) → Pricetags   (E05 + E06, B §3.5; OO.pricetags selects)
//   normalizeCoupon(row, {now, timeZone, domain}) → Coupon | null   (ONE row: coupon_get_v4's, or validate_coupon_v4's
//       data; never dropped as expired; null for a non-object)
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var _ = OO.model._, num = _.num, str = _.str, arr = _.arr, parseJson = _.parseJson, resolveNow = _.resolveNow, isAvailable = _.isAvailable;
  var parseRules = OO.model.parseRules;

  // ---- pricetags (B §3.5; get_pricetag_order E05 + get_pricetag_prices E06; P2-01) -------------------------------
  // normalizePricetags(rawOrder, rawPrices) → Pricetags = { rows: [PricetagRow], prices: { [tagId]: TagPrices } | null }
  // The two reads are soft (a failure is null): no rows, or prices null, and no tag applies (OO.pricetags.select).
  //   rawOrder: get_pricetag_order's { status: true, data: [row] } (a bare array too). Any other status → no rows.
  //   PricetagRow = { tagId, name, domain, sortOrder, active, override, checkMembership, rules, membership: [{ id, type }] }
  //     tagId    pricetags_id | pricetag_id | price_tag_id | tag_id (REF's aliases); a row without one (> 0) is dropped.
  //     domain   domain_name in the SPACE form REF compares: quotes dropped, [_-]+ → ' ', spaces collapsed, upper-case
  //              ('online_app pickup' → 'ONLINE APP PICKUP'). The chain's rows for every domain (POS, KIOSK, …) stay.
  //     sortOrder, active (is_active), override, name (tag_name), rules = the tag's own availability as sent (a JSON
  //              string or a list; '[]' and null are no limit).
  //     membership = the server's membership types whose discount_meta.price_tag is this tag (CustomerMembershipType
  //              rows) as { id, type: membership_type | null }; checkMembership = check_membership, or a non-empty
  //              membership list (REF).
  //   rawPrices: get_pricetag_prices' { status: 'success', data: { productpricetags, sizepricetags, modifierpricetags,
  //     combopricetags } } (the data object alone too). null, a failed status or no data object → prices null.
  //   TagPrices = { product: { [productId]: price }, size: { 'productId:sizeId': price }, modifier: { [modifierId]:
  //     price }, combo: { [comboId]: price } } per tag id, from rows { product_id | size_id | modifier_id | combo_id,
  //     tag_id, price }. The FIRST row of a key wins (REF's find; the chain can hold one per store). A price that isn't
  //     a number > 0 is kept as 0 = "no override": the base price stands (REF; the validator reads 0 as falsy too, and
  //     a negative tag price is never applied).
  function pricetagDomain(v) {
    return str(v).replace(/["']/g, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase();
  }
  function successBody(body) {
    var s = body && body.status;
    return s === true || s === 1 || s === '1' || (typeof s === 'string' && /^(true|success)$/i.test(s.trim()));
  }
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  // A whole number > 0 (a number or a digit string), else null.
  function posInt(v) {
    var n = v === null || v === undefined || v === '' || typeof v === 'boolean' ? NaN : Number(v);
    return isFinite(n) && n > 0 && Math.floor(n) === n ? n : null;
  }
  function tagIdOf(r) {
    var raw = [r.pricetags_id, r.pricetag_id, r.price_tag_id, r.tag_id].filter(function (v) { return v !== null && v !== undefined && v !== ''; })[0];
    return posInt(raw);
  }
  function pricetagRow(r) {
    var id = isRecord(r) ? tagIdOf(r) : null;
    if (id === null) return null;
    var membership = arr(r.membership).filter(function (m) { return m && typeof m === 'object'; }).map(function (m) {
      return { id: _.numOrNull(m.id), type: _.numOrNull(m.membership_type) };
    });
    return {
      tagId: id, name: str(r.tag_name), domain: pricetagDomain(r.domain_name), sortOrder: num(r.sort_order),
      active: _.flag(r.is_active), override: _.flag(r.override),
      checkMembership: _.flag(r.check_membership) || membership.length > 0,
      rules: r.availability === undefined ? null : r.availability, membership: membership,
    };
  }
  // [TagPrices table, raw list, the row's key (null: an id that isn't a whole number > 0, the row is skipped)]
  function idKey() {
    var ids = Array.prototype.slice.call(arguments).map(posInt);
    return ids.every(function (n) { return n !== null; }) ? ids.join(':') : null;
  }
  var PRICE_TABLES = [
    ['product', 'productpricetags', function (r) { return idKey(r.product_id); }],
    ['size', 'sizepricetags', function (r) { return idKey(r.product_id, r.size_id); }],
    ['modifier', 'modifierpricetags', function (r) { return idKey(r.modifier_id); }],
    ['combo', 'combopricetags', function (r) { return idKey(r.combo_id); }],
  ];
  function tagPriceOf(v) { var n = v === null || v === '' || typeof v === 'boolean' ? NaN : Number(v); return isFinite(n) && n > 0 ? n : 0; }
  function pricetagPrices(raw) {
    if (!isRecord(raw)) return null;
    var data = raw;
    if (raw.status !== undefined || raw.data !== undefined) { // the envelope: its data, only on a success status
      if (raw.status !== undefined && !successBody(raw)) return null;
      data = raw.data;
    }
    if (!isRecord(data)) return null;
    var out = {};
    PRICE_TABLES.forEach(function (t) {
      arr(data[t[1]]).forEach(function (r) {
        var tag = isRecord(r) ? posInt(r.tag_id) : null;
        if (tag === null) return;
        var key = t[2](r);
        if (key === null) return;
        var into = out[tag] || (out[tag] = { product: {}, size: {}, modifier: {}, combo: {} });
        if (!Object.prototype.hasOwnProperty.call(into[t[0]], key)) into[t[0]][key] = tagPriceOf(r.price);
      });
    });
    return out;
  }
  function normalizePricetags(rawOrder, rawPrices) {
    var rows = Array.isArray(rawOrder) ? rawOrder : (successBody(rawOrder) ? arr(rawOrder.data) : []);
    return { rows: rows.map(pricetagRow).filter(Boolean), prices: pricetagPrices(rawPrices) };
  }
  OO.model.normalizePricetags = normalizePricetags;

  // ---- coupons (§3.6; coupon_get_v4, E09) -----------------------------------------------------------------
  // raw: the coupon_get_v4 body { status, data:[row], user_id, user_name } or a bare array of rows (both are served).
  // Coupon = { id, name, description, code, type, resultType, appliesTo 'ALL'|'PICKUP'|'DELIVERY', minOrder, maxOrder,
  //            percent, maxDiscount, amount, maxFreeCount, minItemCount, itemIds, scope, resultScope, available, raw }
  //   Rule keys (P2-09, `result ?? rules`): each key is read as the checkout guard reads it
  //   (OnlineOrderValidationController::guardCouponRules): rules[key] ?? result[key], only a missing or null key
  //   falling through to the other block. The admin (endpoint-admin glue/adapters/coupons.jsx) and the offer coupons
  //   (OfferController) write DISCOUNT / MAX_DISCOUNT_VALUE / max_free_count / MAX_FREE_COUNT / RESULTS_ITEMS in result
  //   and the rest in rules, so on their rows this is REF's `result ?? rules` for those keys and `rules ?? result` for
  //   the others; a row carrying one key in BOTH blocks (no capture has one) takes the rules value, the one the guard
  //   enforces at checkout (REF would take result's), so the SPA never applies more than the guard allows.
  //   type = COUPON_TYPE ?? type (older rows carry COUPON_TYPE; the guard reads it first), normalised as the admin
  //   reads it (toProto; G1 fix D): trim, [\s-]+ → '_', upper-case, so the legacy 'CART DISCOUNT' / 'FREE DELIVERY'
  //   rows are CART_DISCOUNT / FREE_DELIVERY ('BUY ONE GET ONE FREE' → BUY_ONE_GET_ONE_FREE, which OO.coupons still
  //   refuses). resultType = result.type, else rules.type, normalised the same way. OO.coupons keys off `type`.
  //   percent = DISCOUNT, maxDiscount = MAX_DISCOUNT_VALUE, minOrder / maxOrder = MIN_ / MAX_ORDER_VALUE (0 = none).
  //   amount = a FIXED / FIXED_AMOUNT / AMOUNT DISCOUNT_TYPE (upper-cased, exactly as the guard compares it): a CART
  //   coupon of DISCOUNT dollars (OO.coupons.isAmount, as is a CART DISCOUNT over 100 %). No admin writes it.
  //   maxFreeCount = max_free_count ?? MAX_FREE_COUNT ?? GET_QUANTITY (the guard's max_free; REF reads the first two
  //   for BOGO). minItemCount = MIN_ITEM_COUNT ?? BUY_QUANTITY ?? BUY_COUNT (BUY_N; the guard's min_count).
  //   itemIds = parseditems, else the JSON string `items`.
  //   scope = the item scope the validator matches lines against (guardCouponEntries; the server's
  //   CouponItemMatcher::couponEntries): [{ id, sizes: [sizeId…] }] from APPLICABLE_ITEMS when it has entries, else
  //   itemIds with sizes []. sizes [] = any size; an entry may be a bare id; a size or id that isn't a whole number
  //   > 0 is dropped (a sizes list left empty means any size, as the guard reads it). resultScope = RESULTS_ITEMS the
  //   same way (BUY_N's rewards). The enriched applicable_products / result_products (CouponSizeEnricher's size rows,
  //   minus deleted sizes) stay on raw for display and the pickers; matching never reads them (all of an entry's sizes
  //   deleted would read there as "any size", while the guard still restricts the line to them).
  //   raw is the untouched row: validate_coupon, OO.coupons and the checkout payload read it, and so will P2's pickers
  //   (its applicable_products / result_products are menu-shaped rows whose modifier groups keep max_no_price_count).
  //   appliesTo = APPLICABLE_FOR normalised (trim, [\s-]+ → '_', upper): live rows hold ALL / PICKUP / DELIVERY; the
  //   REF forms ONLINE_X, ONLINE_APP_X and APP_X become X; empty is 'ALL'; anything else is kept (and refused).
  //   available = the availability rules match `now` (store clock) in `domain`, and the store's today is not before
  //   the start_date's day.
  // Dates (the owner, 2026-10-02; the server takes the same rule, CouponValidationService::isInStoreDateWindow):
  //   start_date / end_date (get_v4 serves the timestamp columns as 'YYYY-MM-DD HH:MM:SS'; the admin saves 00:00:00)
  //   cover the store's WHOLE LOCAL DAY: the date part is read against the store's today (`now` in opts.timeZone). A
  //   missing date is no limit on that side. The coupon end-time follow-up: an end_date with a time of day other than
  //   00:00:00 ALSO runs until that stored instant read as UTC (the server's rule before G1 fix G), so it ends at the
  //   later of the two (a time after 24 h − the store's UTC offset, 11:00 UTC in NZDT, is on the next local day). A
  //   start_date's time part is still ignored.
  // Expired rows are DROPPED: coupon_get_v4 also serves expired coupons that are still exposed (P0-AUDIT §4). A row is
  //   expired when the store's today is after its end_date's day (and `now` is after a timed end_date's instant), or
  //   after the ED of every non-blackout availability rule (no rule can match again, in any domain). Before its
  //   start_date's day a row is listed, not available.
  function ymdOf(v) {
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str(v).trim());
    return m ? m[1] + '-' + ('0' + m[2]).slice(-2) + '-' + ('0' + m[3]).slice(-2) : null;
  }
  // A TIMED end_date's stored instant (its wall clock read as UTC) in ms; null for a midnight or missing one.
  function timedEndMs(v) {
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(str(v).trim());
    if (!m || !(+m[4] || +m[5] || +m[6])) return null;
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  }
  // trim, [\s-]+ → '_', upper-case: the admin's reading of a coupon type, and of APPLICABLE_FOR.
  function token(v) { return str(v).trim().replace(/[\s-]+/g, '_').toUpperCase(); }
  function appliesToOf(v) {
    var s = token(v);
    var m = /^(?:ONLINE_APP_|ONLINE_|APP_)?(PICKUP|DELIVERY)$/.exec(s);
    return !s ? 'ALL' : (m ? m[1] : s);
  }
  function expired(c, today, instant) {
    var end = ymdOf(c.end_date);
    if (end && today > end) {
      var until = timedEndMs(c.end_date);
      if (until === null || !(instant <= until)) return true;
    }
    var positive = parseRules(c.availability).filter(function (r) { return !r.blackout; });
    return positive.length > 0 && positive.every(function (r) { return !!r.ED && today > r.ED; });
  }

  // A rule key as the guard reads it: rules[key] ?? result[key] (null / undefined fall through).
  function pick(rules, result, key) { return rules[key] !== undefined && rules[key] !== null ? rules[key] : result[key]; }
  function firstSet(list) {
    for (var i = 0; i < list.length; i++) if (list[i] !== undefined && list[i] !== null) return list[i];
    return undefined;
  }
  // The guard's guardId: a whole number > 0 (a number or a numeric string, truncated), else null.
  function idOf(v) {
    if (v === null || v === undefined || typeof v === 'object' || typeof v === 'boolean' || (typeof v === 'string' && !v.trim())) return null;
    var n = Math.floor(Number(v));
    return isFinite(n) && n > 0 ? n : null;
  }
  // [{ id, sizes }] from [{ id, sizes: [sizeId…] }] rows or bare ids (guardCouponEntries).
  function entriesOf(list) {
    var out = [];
    arr(list).forEach(function (e) {
      var id = idOf(e && typeof e === 'object' ? e.id : e);
      if (id === null) return;
      var sizes = e && typeof e === 'object' ? arr(e.sizes).map(idOf).filter(function (s) { return s !== null; }) : [];
      out.push({ id: id, sizes: sizes });
    });
    return out;
  }
  function objOf(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; }
  var AMOUNT_TYPES = ['FIXED', 'FIXED_AMOUNT', 'AMOUNT'];

  function couponOf(c, clock, domain) {
    var rules = objOf(parseJson(c.rules, {}));
    var result = objOf(parseJson(c.result, {}));
    var get = function (key) { return pick(rules, result, key); };
    var parsed = Array.isArray(c.parseditems) ? c.parseditems : arr(parseJson(c.items, []));
    var itemIds = parsed.map(num);
    var applicable = entriesOf(get('APPLICABLE_ITEMS'));
    var typeRaw = firstSet([get('COUPON_TYPE'), get('type')]);
    var start = ymdOf(c.start_date);
    return {
      id: num(c.id), name: str(c.name), description: str(c.description), code: str(c.code),
      type: token(typeRaw), resultType: token(result.type) || token(rules.type),
      appliesTo: appliesToOf(get('APPLICABLE_FOR')),
      minOrder: num(get('MIN_ORDER_VALUE')), maxOrder: num(get('MAX_ORDER_VALUE')),
      percent: num(get('DISCOUNT')), maxDiscount: num(get('MAX_DISCOUNT_VALUE')),
      amount: AMOUNT_TYPES.indexOf(str(get('DISCOUNT_TYPE')).toUpperCase()) >= 0,
      maxFreeCount: num(firstSet([get('max_free_count'), get('MAX_FREE_COUNT'), get('GET_QUANTITY')])),
      minItemCount: num(firstSet([get('MIN_ITEM_COUNT'), get('BUY_QUANTITY'), get('BUY_COUNT')])),
      itemIds: itemIds,
      scope: applicable.length ? applicable : entriesOf(itemIds),
      resultScope: entriesOf(get('RESULTS_ITEMS')),
      available: isAvailable(c.availability, clock, domain || 'ONLINE_PICKUP') && !(start && clock.ymd < start),
      raw: c,
    };
  }

  function normalizeCoupon(row, opts) {
    opts = opts || {};
    if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
    return couponOf(row, resolveNow(opts.now, opts.timeZone || null).clock, opts.domain);
  }

  function normalizeCoupons(raw, opts) {
    opts = opts || {};
    var at = resolveNow(opts.now, opts.timeZone || null);
    var clock = at.clock;
    var rows = Array.isArray(raw) ? raw : arr(raw && raw.data);
    return rows.filter(function (c) { return c && typeof c === 'object' && !expired(c, clock.ymd, at.instant); }).map(function (c) {
      return couponOf(c, clock, opts.domain);
    });
  }

  var M = OO.model;
  M.normalizeCoupons = normalizeCoupons;
  M.normalizeCoupon = normalizeCoupon;
})();


// ===================== src/lib/model-account.jsx =====================
// src/lib/model-account.jsx: OO.model's loyalty, activity, orders, notifications, customer; on the model.jsx core. API:
//   normalizeLoyalty({wallet, products, catalogue}, {store, orderType, mode, isMobileApp, table, kiosk, chain, master})
//       → Loyalty                                                                          (§3.12; P3-13, P4-16 below)
//   chainName(chain, master) → the name of an app chain: its master's (P4-16, the loyalty scope below)
//   normalizeActivity(rawWalletTx, {now, timeZone, pointsPerDollar}) → Activity[]         (§3.12; P3-13 below)
//   activityStats(activity, {now, timeZone}) → { earned, redeemed, expired, thisMonth }   (P3-14's wallet page)
//   normalizeOrders(rawPaginator, {now, timeZone}) → Order[]   normalizeOrder(rawOrderData, opts) → Order | null  (§3.11)
//       normalizeOrders takes get_customer_orders' BARE paginator (E16); rows without a ticket_id are dropped, so
//       customer/getOrders' { status, data: paginator } of online_orders rows (E17: normalizeOnlineOrders) gives [].
//       normalizeOrder takes get_order_data's { status: true, data: Ticket } (or a bare Ticket); null when the body
//       holds no ticket (its 400 / 404 error bodies, an empty body).
//   normalizeOnlineOrders(rawPage, {now, timeZone, menu}) → OnlineOrder[]   (E17 customer/getOrders: the orders page's
//       All tab; Batch 2 P3-04, moved here from pages/orders.jsx by P3-FIX #22; the "All orders" section below)
//   lastCompletedOrder(orders) → Order | null       (Home "Order again")
//   orderProgress(rawOnlineOrderStatus, {orderType}) → Progress                           (§3.10, E14)
//   orderSnapshot(lastOrder, {orderId, hotel, now, timeZone}) → Snapshot | null           (OO.store lastOrder.snapshot)
//   orderLinkSnapshot(query, {orderId, now, timeZone}) → Link | null      (REF's /order-confirmed, /success display params)
//   orderAccess({orderId, device, account, signedIn, list}) → Access     (who may see the detail: B §9 Q13, owner only)
//   orderStatusView({order, orderState, progress, statusState, snapshot}) → View          (the order-status page)
//   normalizeNotifications(raw, {now, timeZone, read}) → { items, unread }                (proposed endpoint)
//   normalizeCustomer(rawCustomerOrGetdata) → Customer | null                             (§3.7)
// Ratings (Batch 2 P3-06 / P3-07: customer/order-rating, rate-order, product-rating, store-rating; the "ratings"
// section below, which registers its own OO.model members):
//   RATING_LABELS ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent']   ratingLabel(n) → RATING_LABELS[n] | ''
//   normalizeOrderRating(raw) → OrderRating | null        (E30 body, a get_customer_orders row or get_order_data body)
//   orderRatingRows(rating, order) → [ReviewRow]          ratingItems(order) → [{ productId, name, qty }]
//   canRateOrder(order) → bool                            (the order's status: preparing, ready or completed)
//   normalizeReviewSettings(value) → { storeRating, productRating }   (getRestaurant review_settings; REF default on)
//   restaurantReviewSettings(rawRestaurant) → { storeRating, productRating }   (the same, found in a getRestaurant body)
//   normalizeRatingSummary(raw) → { average, total } | null   (E28 store-rating / E29 product-rating; null = no reviews)
//   ratingScore(average) → '4.6'                          ratingCount(total) → '212' | '1.2k' | '12k'
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var _ = OO.model._, F = _.F, num = _.num, numOrNull = _.numOrNull, str = _.str, flag = _.flag, arr = _.arr, parseJson = _.parseJson, parseDeep = _.parseDeep, parseInstant = _.parseInstant, resolveNow = _.resolveNow, resolveLink = _.resolveLink;

  // ---- loyalty / wallet (§3.12) --------------------------------------------------------------------
  // raw = { wallet: get_customer_wallet_points, products: get_user_loyalty_products_v4,
  //         catalogue: PROPOSED loyalty catalogue (every reward incl. locked ones), optional }. A failed read is a null
  //         part (the real adapter's reads are soft: a 403 "module off", a 400 and a network failure all read null).
  // opts = { store, orderType ('PICKUP' default | 'DELIVERY'), mode (default OO.mode), isMobileApp, table,
  //   kiosk }: the store and the CHANNEL the customer orders on, OO.wallet.channel's ctx (P3-13): the redemption row and
  //   the credit row are that channel's (REF selectWalletRedemptionRule), never the first one sent. The rows agree across
  //   channels at most stores, so a caller without a channel (the default: PICKUP in OO.mode) sees what it saw before.
  // Loyalty = { enabled, programName, pointsWord, tier, memberName, memberCode, customerId, points, walletPoints,
  //   pointsPerDollar, earnPerDollar, walletValue, priceTagId, rewards:[{id, name, cost, image, price, state:
  //   'redeem'|'locked', canRedeem}], goal:{id, name, cost, toGo, pct} | null, + P3-13: channel, rule,
  //   ratioRows, earnRule, usageLimitOver, usedLimitCount, usageText, productLevel, redeemOff, redeemText, rewardsOff,
  //   rewardsText, rewardsRead, + P4-16 (with opts.chain only): scope }
  //   enabled          the wallet read is usable (status true). False is "loyalty off here" as much as a failed read:
  //                    the soft reads can't tell a 403 MODULE_SUBSCRIPTION_DISABLED from a network failure. A
  //                    cart-level store keeps enabled (it has points; only its rewards are off: productLevel below).
  //   channel          OO.wallet.channel(ctx): 'ONLINE_PICKUP' | 'ONLINE_DELIVERY' | 'ONLINE_APP_…' | 'dining' | 'KIOSK'.
  //   rule             OO.wallet.rule(wallet, ctx): { orderType, ratio, min, max } | null, the channel's redemption row
  //                    with ratio_points > 0 (else an ALL row). pointsPerDollar = rule.ratio | null (points per $1 of
  //                    redemption) and walletValue = round2(walletPoints / pointsPerDollar) | 0 follow it.
  //   earnRule         the channel's credit_ratio row (the same match: order type trimmed and upper-cased, else ALL) with
  //                    ratio_points > 0: { orderType, ratio } | null. earnPerDollar = earnRule.ratio | null.
  //   ratioRows        every redemption row as sent, in order, rows without an order type left out (the wallet page's
  //                    "how points convert" table; REF lists them all): [{ orderType (as sent), label ('Online pickup',
  //                    'App delivery', 'Table orders', 'Kiosk', 'All orders', else the type in sentence case), ratio,
  //                    min, max, usable, current }]. usable = ratio > 0 and a maximum of 1 point or more: the server
  //                    refuses points above the maximum whatever it is, so a maximum ≤ 0 means NO points (OO.wallet's
  //                    NO_MAX), never REF's "No cap". current = the row `rule` is (the channel's).
  //   usageLimitOver, usedLimitCount, usageText   OO.wallet.usage(wallet): the daily redeem cap (usage_limit_over,
  //                    used_limit_count) and its customer copy (null unless over).
  //   productLevel     OO.wallet.productLevel(store): the store redeems points on product rewards only (wallet type 2);
  //                    else it takes them off the order at checkout (type 1, the cart level).
  //   redeemOff        why this channel takes no points at all right now, whatever the cart: null | the engine's code,
  //                    the first that applies of 'NO_WALLET' (no usable wallet read) · 'LIMIT' · 'NO_RULE' · 'NO_MAX'.
  //                    redeemText: its copy (usageText; OO.wallet.TEXT's for NO_RULE / NO_MAX; null for NO_WALLET).
  //   rewardsOff       OO.wallet.rewardBudget([], ctx)'s reason, why no reward can be redeemed here (lines only move the
  //                    budget's figures, never its reason): null | 'NO_WALLET' | 'LIMIT' | 'CART_LEVEL' | 'KIOSK' |
  //                    'NO_RULE' | 'NO_MAX'. rewardsText: its text.
  //   rewardsRead      the get_user_loyalty_products_v4 part: 'ok' (a body OO.wallet.rewardRows reads) · 'off' (no body
  //                    while the wallet holds no usable row for v4's own channel, ONLINE_DELIVERY, ONLINE_APP_DELIVERY in
  //                    app mode (it picks by User-Agent, whatever the order type): the server's "Redemption ratio not
  //                    set" / "Invalid points ratio", loyalty off for rewards, not an error) · 'failed' (no body
  //                    otherwise: try again) · 'none' (raw has no `products` part: not asked).
  // OO.wallet (src/lib/wallet.jsx, after this file in the concat) is read at call time; without it no row applies.
  // points = walletPoints, the server's balance (P3-15: the Batch 1 local "Redeem" taps, opts.redeemed, are gone: a
  // reward is redeemed as a cart line, so nothing comes off here). rewards / goal judge each reward against it: 'redeem'
  // (canRedeem) when the balance covers its cost, else 'locked'; the goal is the cheapest locked one, else the dearest.
  // The loyalty scope (Batch 2 P4-16; mode app, D-03; BATCH2-PLAN §3 P4 "Loyalty scope"). The app is one per chain and
  // every wallet read goes with the BRANCH's key: get_customer_wallet_points keeps the balance on the master's customer
  // row for a branch hyperlinked to the master (getHyperlinkedMaster) and on the store's own otherwise; the ratios and
  // the daily cap are always the branch's. B31 says which (shared_loyalty). With opts.chain (OO.model.normalizeChain's
  // Chain: OO.data.loyaltyScope) and opts.store the branch:
  //   scope        (only with opts.chain) null unless the chain lists this store (store.onlineKey, any case) and at least
  //                one other; else { shared, name, text }: shared = the store's shared_loyalty (a child linked to the
  //                master: its wallet is the master's; the master: some child is linked); name = the chain's name
  //                (chainName: the master's) when shared, else the store's own; text '{Word} shared across the {name}
  //                group' | '{Word} for {name} only' ({Word}: the points word, capitalised).
  //   programName  shared, with opts.master (the master's normalised Store): the master's programme, the one the
  //                balance belongs to; otherwise the store's, as before.
  // Without opts.chain, Loyalty carries no scope key and nothing else changes.
  var RATIO_LABELS = {
    ONLINE_PICKUP: 'Online pickup', ONLINE_DELIVERY: 'Online delivery', ONLINE_APP_PICKUP: 'App pickup',
    ONLINE_APP_DELIVERY: 'App delivery', DINING: 'Table orders', KIOSK: 'Kiosk', ALL: 'All orders',
  };
  function ratioLabel(type) {
    var key = type.toUpperCase();
    if (RATIO_LABELS[key]) return RATIO_LABELS[key];
    var s = type.replace(/^ONLINE_/i, '').replace(/_+/g, ' ').trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  function engine() { return OO.wallet && typeof OO.wallet.rule === 'function' ? OO.wallet : null; }
  // The redemption list as sent (OO.wallet.rows' keys: the first non-empty of redemption_ratio, redemptionRatios,
  // redumption_ratio), rows with ratio ≤ 0 included: the display table.
  function ratioRowsOf(w, rule) {
    var lists = [w.redemption_ratio, w.redemptionRatios, w.redumption_ratio];
    var list = [];
    for (var i = 0; i < lists.length && !list.length; i++) list = arr(lists[i]);
    var cur = rule ? str(rule.orderType).toUpperCase() : '';
    var out = [];
    list.forEach(function (r) {
      if (!r || typeof r !== 'object') return;
      var type = str(r.order_type != null ? r.order_type : r.orderType).trim();
      if (!type) return;
      var ratio = num(r.ratio_points != null ? r.ratio_points : r.ratioPoints);
      var min = num(r.min_consumable_points != null ? r.min_consumable_points : (r.minimum_consumable_points != null ? r.minimum_consumable_points : r.minConsumablePoints));
      var max = num(r.maximum_consumable_points != null ? r.maximum_consumable_points : r.maximumConsumablePoints);
      out.push({
        orderType: type, label: ratioLabel(type), ratio: ratio, min: min, max: max,
        usable: ratio > 0 && Math.floor(max + 1e-9) >= 1, current: !!cur && type.toUpperCase() === cur,
      });
    });
    return out;
  }
  // The channel's credit row (OO.wallet.rule's match over credit_ratio), ratio_points > 0: { orderType, ratio } | null.
  function earnRuleOf(w, channel) {
    var rows = arr(w.credit_ratio != null ? w.credit_ratio : w.creditRatio).map(function (r) {
      return r && typeof r === 'object' ? { orderType: str(r.order_type != null ? r.order_type : r.orderType).trim(), ratio: num(r.ratio_points != null ? r.ratio_points : r.ratioPoints) } : null;
    }).filter(function (r) { return r && r.orderType && r.ratio > 0; });
    var want = str(channel).toUpperCase();
    return rows.filter(function (r) { return r.orderType.toUpperCase() === want; })[0]
      || rows.filter(function (r) { return r.orderType.toUpperCase() === 'ALL'; })[0] || null;
  }
  // ---- the loyalty scope (P4-16; see "The loyalty scope" above) ----
  // The chain's name: its master's (B31: the master is "the chain's master (its theme and name)"), from the master's
  // Store when it is read, else the chain's master row; '' when neither names it.
  function chainName(chain, master) {
    var m = master && typeof master === 'object' ? str(master.name).trim() : '';
    if (m) return m;
    return chain && typeof chain === 'object' && chain.master ? str(chain.master.name).trim() : '';
  }
  function capital(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  // Loyalty.scope for opts (opts.chain given), or null.
  function scopeOf(opts, word) {
    var chain = opts.chain, store = opts.store;
    var stores = chain && typeof chain === 'object' ? arr(chain.stores) : [];
    var key = store ? str(store.onlineKey).trim().toLowerCase() : '';
    var mine = key ? stores.filter(function (s) { return !!s && str(s.key).trim().toLowerCase() === key; })[0] : null;
    if (!mine || stores.length < 2) return null;
    var shared = mine.sharedLoyalty === true;
    var own = str(store.name).trim() || str(mine.name).trim();
    var name = shared ? chainName(chain, opts.master) || own : own;
    var w = capital(str(word) || 'points');
    return { shared: shared, name: name, text: shared ? w + ' shared across the ' + name + ' group' : w + ' for ' + name + ' only' };
  }

  // v4's own row (see rewardsRead): ONLINE_DELIVERY, ONLINE_APP_DELIVERY in app mode, with ratio_points > 0.
  function v4RowUsable(rows, app) {
    var want = app ? 'ONLINE_APP_DELIVERY' : 'ONLINE_DELIVERY';
    return rows.some(function (r) { return r.orderType.toUpperCase() === want && r.ratio > 0; });
  }
  function normalizeLoyalty(raw, opts) {
    opts = opts || {};
    raw = raw || {};
    var w = raw.wallet || {};
    var lp = raw.products && raw.products.data || {};
    var walletPoints = num(w.wallet_points != null ? w.wallet_points : lp.current_points);
    var W = engine();
    var usableWallet = !!(raw.wallet && (raw.wallet.status === true || raw.wallet.status === 'true'));
    var ctx = {
      wallet: usableWallet ? raw.wallet : null, store: opts.store || null, orderType: opts.orderType || 'PICKUP',
      mode: opts.mode, isMobileApp: opts.isMobileApp, table: opts.table || null, kiosk: !!opts.kiosk, signedIn: true,
    };
    var channel = W ? W.channel(ctx) : null;
    var rule = W && usableWallet ? W.rule(raw.wallet, ctx) : null;
    var earnRule = usableWallet ? earnRuleOf(w, channel) : null;
    var usage = W ? W.usage(raw.wallet) : { over: false, used: 0, text: null };
    var ratioRows = usableWallet ? ratioRowsOf(w, rule) : [];
    var redeemOff = !usableWallet ? 'NO_WALLET' : usage.over ? 'LIMIT' : !rule ? 'NO_RULE' : Math.floor(rule.max + 1e-9) < 1 ? 'NO_MAX' : null;
    var budget = W ? W.rewardBudget([], ctx) : { reason: 'NO_WALLET', text: null };
    var rewardsRead = !Object.prototype.hasOwnProperty.call(raw, 'products') ? 'none'
      : W && W.rewardRows(raw.products) ? 'ok'
        : usableWallet && !v4RowUsable(ratioRows, ctx.isMobileApp !== undefined && ctx.isMobileApp !== null ? !!ctx.isMobileApp : (opts.mode || OO.mode) === 'app') ? 'off' : 'failed';
    var pointsPerDollar = rule ? rule.ratio : null;
    var source = raw.catalogue && arr(raw.catalogue.data).length ? arr(raw.catalogue.data) : arr(lp.eligible_products);
    var all = source.map(function (p) {
      var price = num(p.sale_price);
      return {
        id: String(p.itemId != null ? p.itemId : p.id), name: str(p.label || p.name), image: F().imageUrl(p.imageUrl),
        price: price, cost: num(p.points_required) || (pointsPerDollar ? Math.ceil(price * pointsPerDollar) : 0),
      };
    }).sort(function (a, b) { return a.cost - b.cost; });
    var points = Math.max(0, walletPoints);
    var rewards = all.map(function (r) {
      var can = points >= r.cost;
      return Object.assign({}, r, { state: can ? 'redeem' : 'locked', canRedeem: can });
    });
    var locked = rewards.filter(function (r) { return r.state === 'locked'; });
    var target = locked[0] || rewards.slice(-1)[0] || null;
    var store = opts.store || {};
    var meta = parseDeep(w.membership_type_obj && w.membership_type_obj.discount_meta);
    var noRow = W && W.TEXT ? W.TEXT[redeemOff] || null : null;
    var pointsWord = store.loyalty ? store.loyalty.pointsWord : 'points';
    var scope = opts.chain ? scopeOf(opts, pointsWord) : undefined; // P4-16: the loyalty scope (above)
    var programme = scope && scope.shared && opts.master && opts.master.loyalty ? opts.master.loyalty.programName : null;
    var out = {
      enabled: usableWallet,
      programName: programme || (store.loyalty ? store.loyalty.programName : 'Rewards'),
      pointsWord: pointsWord,
      tier: w.membership_type_obj ? str(w.membership_type_obj.name) || null : null,
      memberName: str(w.name),
      memberCode: str(w.customer_code) || null,
      customerId: str(w.customer_id) || null,
      points: points,
      walletPoints: walletPoints,
      channel: channel,
      rule: rule,
      pointsPerDollar: pointsPerDollar,
      earnRule: earnRule,
      earnPerDollar: earnRule ? earnRule.ratio : null,
      walletValue: pointsPerDollar ? F().round2(walletPoints / pointsPerDollar) : 0,
      ratioRows: ratioRows,
      usageLimitOver: usage.over,
      usedLimitCount: usage.used,
      usageText: usage.text,
      productLevel: !!(W && W.productLevel(opts.store || null)),
      redeemOff: redeemOff,
      redeemText: redeemOff === 'LIMIT' ? usage.text : redeemOff === 'NO_RULE' || redeemOff === 'NO_MAX' ? noRow : null,
      rewardsOff: budget.reason || null,
      rewardsText: budget.reason ? budget.text || null : null,
      rewardsRead: rewardsRead,
      priceTagId: meta && typeof meta === 'object' && meta.price_tag != null ? num(meta.price_tag) : null,
      rewards: rewards,
      goal: target ? {
        id: target.id, name: target.name, cost: target.cost, toGo: Math.max(0, target.cost - points),
        pct: target.cost ? Math.min(100, Math.round(points / target.cost * 100)) : 100,
      } : null,
    };
    if (opts.chain) out.scope = scope;
    return out;
  }

  // Activity = { id, title, when ('Today' | 'Fri' | '12 Sep'), points (signed), pointsLabel ('+27' | '−150'),
  //              kind 'credit'|'debit', ticketId, + P3-13: source, whenLong, orderValue, value }. A PROPOSED
  //              row.proposed.description wins over the derived title.
  //   transaction_type (any case): 'debit' (a redemption, 'Points redeemed') and 'expiry' (wallet:expire, 'Points
  //   expired'; G1 fix D) are deductions, kind 'debit' with negative points, as the server's balance counts them
  //   (CustomerWalletPointsTransactionController); anything else is a credit.
  //   source      'expiry' (an expiry row) · 'giveaway' (created_type giveaway: a bonus) · 'order' (created_type ticket /
  //               order, or a ticket_id) · 'other' (REF's "Points credited / debited").
  //   whenLong    the day and time in the store's zone: 'Today, 2:32pm' · 'Fri, 9:00am' · '12 Sep, 12:40pm', with the year
  //               when it isn't this year's ('23 Aug 2024, 3:13pm'); '' without a created_at.
  //   orderValue  ticket_value (the order the points came with) when > 0, else null.
  //   value       the points' worth in dollars at opts.pointsPerDollar (the channel's redemption ratio, P3-13): round2(
  //               |points| / pointsPerDollar), null without a ratio (REF divides by 1 then: a wrong figure).
  var ORDER_SOURCES = { ticket: true, order: true };
  function normalizeActivity(raw, opts) {
    opts = opts || {};
    var f = F();
    var tz = opts.timeZone || null;
    var nowClock = resolveNow(opts.now, tz).clock;
    var today = nowClock.ymd;
    var ratio = num(opts.pointsPerDollar);
    return arr(raw && raw.data).map(function (t) {
      var type = str(t.transaction_type).toLowerCase();
      var expiry = type === 'expiry'; // wallet:expire's rows: a deduction, as the server's balance counts them
      var debit = type === 'debit' || expiry;
      var pts = Math.abs(num(t.wallet_points)) * (debit ? -1 : 1);
      var at = parseInstant(t.created_at);
      var c = at == null ? null : f.zoned(new Date(at), tz);
      var ymd = c ? c.ymd : today;
      var created = str(t.created_type).toLowerCase();
      var proposed = t.proposed && typeof t.proposed === 'object' ? t.proposed : {};
      var title = str(proposed.description) || (expiry ? 'Points expired' : (debit ? 'Points redeemed' : (created === 'giveaway' ? 'Bonus points' : (t.ticket_id ? 'Order points' : 'Points earned'))));
      var orderValue = num(t.ticket_value);
      var day = f.relativeDay(ymd, today);
      // '12 Sep' (a date beyond the last week) carries the year when it isn't this year's.
      var dated = c && c.y !== nowClock.y && /^\d/.test(day) ? day + ' ' + c.y : day;
      return {
        id: String(t.id), title: title, when: day, at: at, points: pts, pointsLabel: f.pointsDelta(pts),
        kind: debit ? 'debit' : 'credit', ticketId: str(t.ticket_id) || null,
        source: expiry ? 'expiry' : created === 'giveaway' ? 'giveaway' : (ORDER_SOURCES[created] || t.ticket_id) ? 'order' : 'other',
        whenLong: c ? dated + ', ' + f.time12(c.minutes) : '',
        orderValue: orderValue > 0 ? f.round2(orderValue) : null,
        value: ratio > 0 ? f.round2(Math.abs(pts) / ratio) : null,
      };
    }).sort(function (a, b) { return (b.at || 0) - (a.at || 0); });
  }

  // activityStats(activity, { now, timeZone }) → { earned, redeemed, expired, thisMonth } (P3-14; REF's wallet stats,
  // over every row given): earned = Σ credit points, redeemed = Σ debit points of redemptions, expired = Σ expiry points
  // (REF counts expiries as redeemed), thisMonth = Σ credit points created in the current calendar month of the store's
  // zone. Whole sums of |points| (≥ 0); rows without a time count everywhere but thisMonth.
  function activityStats(activity, opts) {
    opts = opts || {};
    var f = F();
    var tz = opts.timeZone || null;
    var now = resolveNow(opts.now, tz).clock;
    var out = { earned: 0, redeemed: 0, expired: 0, thisMonth: 0 };
    arr(activity).forEach(function (a) {
      if (!a || typeof a !== 'object') return;
      var pts = Math.abs(num(a.points));
      if (a.kind !== 'debit') {
        out.earned += pts;
        if (a.at != null) {
          var c = f.zoned(new Date(a.at), tz);
          if (c.y === now.y && c.mo === now.mo) out.thisMonth += pts;
        }
      } else if (a.source === 'expiry') out.expired += pts;
      else out.redeemed += pts;
    });
    Object.keys(out).forEach(function (k) { out[k] = f.round2(out[k]); });
    return out;
  }

  // ---- orders (§3.11) --------------------------------------------------------------------------------
  // Order = { id (ticket_id), shortId '#1042', number '1042', orderType 'PICKUP'|'DELIVERY'|…, typeLabel,
  //   status:{key 'received'|'preparing'|'ready'|'completed'|'rejected'|'cancelled', label, tone
  //   'neutral'|'warn'|'success'|'danger'} (cancelled: ticket_status VOID / CANCELLED, orderStatus CANCELLED / FAILED,
  //   or the POS's order_state 'cancelled'), paymentStatus, placedAt (ms), placedLabel ('Today'|'Fri'|'12 Sep'),
  //   placedLabelLong ('Today'|'Last Friday'|'12 Sep'), placedTime ('6:02pm'), pickupAt (ms) | null, pickupTime
  //   ('6:17pm') | null, pickupLabel ('Today, 6:17pm' | 'Tomorrow, 12:15pm' | 'Fri 2 Oct, …' → see below) | null
  //   (the pickup / delivery time: the ticket's delivery_time epoch, else get_order_data's scheduled_time; 0 / empty
  //   = none), subtotal (the ticket's), tax, discount (total − billTotal: coupon/wallet), surcharge (the AMOUNT: the
  //   ticket keeps a % of its subtotal, P3-05), total (amount paid), itemCount, summary, notes, storeId (the ticket's
  //   store: user_id, a whole number > 0 | null), rating (P3-07: the ticket's own rating, OO.model.normalizeOrderRating
  //   of its rating / feedback / item_ratings; null = not rated),
  //   lines:[ {kind:'item', productId, name, qty, unitPrice, total (GROSS), sizeName, modifiers:[{id, name, count
  //   (per unit), price}], notes, discountPercent, reward, rewardPoints} | {kind:'combo', comboId, name,
  //   description, qty, unitPrice, total, items:[{productId, name,
  //   qty, sizeName, extraPrice, modifiers}]} ], couponName, address, totals, payment }
  //   reward (W3-fix #18; REF reorder.ts skips only these) = a loyalty reward line the points paid for: isWalletRedeemed
  //   with walletType 2. A walletType 1 line (the order's points spread over its lines) or one without a walletType is
  //   a plain purchase: no "Reward" badge, and "Order again" rebuilds it. rewardPoints = walletUsedForProduct (the
  //   points the line used, either type) | null.
  //   The delivery fee is not a line (P3-05): the server adds the zone's fee to the order as a product ('Delivery Fee'
  //   + the zone's name, e.g. 'Delivery FeeZone2'), which the POS imports as a line at the full fee. A DELIVERY order's
  //   item lines named like that (REF isDeliveryFeeLine: /\bdelivery\s*fee/i) become totals.delivery, so they are no
  //   item, no item count and nothing "Order again" tries to add.
  //   totals (the detail and receipt rows, REF's set; Batch 2 P3-05) = { subtotal, delivery, surcharge,
  //   surchargePercent, tip, tipPercent, tax, coupon: { code, name, type, amount, free } | null, points: { amount,
  //   points } | null, discount, discountLabel, total, paid, remaining, settled, refunded }, built so the rows add up:
  //   subtotal + delivery + surcharge + tip − coupon − points − discount = total.
  //     The ticket's subtotal is the POS's: its lines net of line discounts, the delivery line included; surcharge and
  //     gratuity are % of it (the backend's invoice reads them so): surcharge = subtotal × % / 100, tip likewise.
  //     subtotal = get_order_data's total (the checkout's subtotal + delivery + surcharge) − delivery − surcharge: the
  //     items as charged, before any saving (without that total: the lines' sum). total = billTotal (payable_amount:
  //     what checkout charged) + tip (a POS tip; without a billTotal: the ticket's paid, else the subtotal).
  //     Everything checkout took off (total − billTotal) splits into coupon (the coupon applied: couponDetail's code and
  //     name, else discount_reason; amount coupon_discount_amount, else the rest after the points; free for BOGO /
  //     BUY_N without an amount), points (Σ walletAmount and Σ walletUsedForProduct of the isWalletRedeemed lines,
  //     combo children included; wallet_points_consumed is a flag, never points) and discount (the rest: membership and
  //     other savings; labelled discount_reason when no coupon is applied, else 'Discount').
  //     paid = the ticket's paid; settled = the payment went through (online_orders PAID, the ticket PAID, or nothing
  //     to pay); remaining = total − paid unless settled or refunded (REF shows Paid / Remaining for a part payment
  //     only: an older POS can book a few cents under an online payment).
  //   payment = { method 'online' | 'counter' | 'delivery' | 'points' | null (payment_type), status 'paid' | 'unpaid' |
  //     'refunded', label ('Paid online' | 'Not paid yet' | 'Paid at the counter' | 'Pay at the counter' | 'Paid on
  //     delivery' | 'Pay on delivery' | 'Paid with points' | 'Paid' | 'Nothing to pay' | 'Refunded') }: the ticket's
  //     status counts too (a counter order paid in store keeps online_orders UNPAID).
  function orderTypeOf(t) {
    var tt = str(t.ticket_type || t.order_type).toUpperCase();
    if (tt.indexOf('DELIVERY') >= 0) return 'DELIVERY';
    if (tt.indexOf('PICKUP') >= 0) return 'PICKUP';
    if (tt.indexOf('DIN') >= 0 || tt.indexOf('TABLE') >= 0) return 'DINEIN';
    return tt || 'PICKUP';
  }
  var TYPE_LABEL = { PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
  // online_orders.order_status values that end an order without it being made: the cancellation services write
  // CANCELLED; FAILED is read by the backend's coupon release (never written today; REF maps it to cancelled).
  var CANCELLED_STATUS = { CANCELLED: true, CANCELED: true, FAILED: true };
  function orderStatusOf(t, type) {
    var os = str(t.orderStatus).toUpperCase(), state = str(t.order_state).toLowerCase(), ts = str(t.ticket_status).toUpperCase();
    var pickup = type !== 'DELIVERY';
    if (os === 'REJECTED') return { key: 'rejected', label: 'Rejected', tone: 'danger' };
    if (ts === 'VOID' || ts === 'CANCELLED' || ts === 'CANCELED' || CANCELLED_STATUS[os] || state === 'cancelled' || state === 'canceled') {
      return { key: 'cancelled', label: 'Cancelled', tone: 'danger' };
    }
    if (state === 'completed') return { key: 'completed', label: pickup ? 'Picked up' : 'Delivered', tone: 'success' };
    if (state === 'ready') return { key: 'ready', label: pickup ? 'Ready for pickup' : 'Out for delivery', tone: 'success' };
    if (os === 'ACCEPTED') return { key: 'preparing', label: 'Preparing', tone: 'warn' };
    return { key: 'received', label: 'Order received', tone: 'neutral' };
  }
  function orderModifiers(line) {
    return arr(line.modifiersDetail).map(function (m) {
      return { id: num(m.modifier_id), name: str(m.modifier), count: num(m.modifier_count) || 1, price: num(m.price) };
    });
  }
  function feeMetadata(line) {
    var meta = line && line.order_meta;
    if (typeof meta === 'string') { try { meta = JSON.parse(meta); } catch (_) { return null; } }
    var fee = meta && meta.order_fee;
    return fee && typeof fee === 'object' && !Array.isArray(fee) ? fee : null;
  }
  function itemLineOf(l) {
    var fee = feeMetadata(l);
    var qty = num(l.product_count) || 1;
    var mods = orderModifiers(l);
    var total = num(l.temp_rate) * qty + num(l.extra_price) + mods.reduce(function (s, m) { return s + m.price * m.count; }, 0);
    // Totals stay GROSS: the ticket's subtotal includes redeemed/discounted lines and billTotal carries the
    // deduction, so lines + subtotal − discount = total.
    return {
      kind: 'item', productId: num(l.product_id), name: str(l.productName), qty: qty, sizeName: str(l.size) || null,
      modifiers: mods.map(function (m) { return Object.assign({}, m, { count: Math.max(1, Math.round(m.count / qty)) }); }),
      notes: str(l.kitchen_note) || null, unitPrice: F().round2(total / qty), total: F().round2(total),
      ...(fee ? { automaticFee: true, feeDescription: str(fee.description).trim() } : {}),
      discountPercent: num(l.discount || l.rate_of_discount), reward: flag(l.isWalletRedeemed) && num(l.walletType) === 2,
      rewardPoints: l.walletUsedForProduct != null ? num(l.walletUsedForProduct) : null,
    };
  }
  function comboLineOf(group) {
    var det = group.comboDetails || {};
    var items = arr(group.orderItems).map(function (l) {
      var qty = num(l.product_count) || 1;
      return { productId: num(l.product_id), name: str(l.productName), qty: qty, sizeName: str(l.size) || null, extraPrice: num(l.extra_price), modifiers: orderModifiers(l) };
    });
    var total = num(det.comboPrice) + items.reduce(function (s, it) {
      return s + it.extraPrice * it.qty + it.modifiers.reduce(function (m, x) { return m + x.price * x.count; }, 0);
    }, 0);
    var first = arr(group.orderItems)[0] || {};
    return {
      kind: 'combo', comboId: first.combo_id != null ? num(first.combo_id) : null, name: str(det.combo_name),
      description: F().stripHtml(det.description), qty: 1, unitPrice: F().round2(total), total: F().round2(total), items: items,
    };
  }
  // ---- the detail totals and the payment (Batch 2 P3-05; Order.totals / Order.payment above) ----------------------
  // The server's delivery fee product on a DELIVERY order (REF isDeliveryFeeLine).
  function isDeliveryFee(l, type) { return type === 'DELIVERY' && l.kind === 'item' && /\bdelivery\s*fee/i.test(l.name); }
  // Every raw order row: the item rows and the combo children (their wallet fields included).
  function rawRows(t) {
    var d = t.ordersDetail || {};
    return arr(d.nonComboOrders).concat([].concat.apply([], arr(d.comboOrders).map(function (g) { return arr(g && g.orderItems); })))
      .filter(function (l) { return !!l && typeof l === 'object'; });
  }
  var FREE_ITEM_COUPON = { BUY_ONE_GET_ONE_FREE: true, BUY_N_GET_M_FREE: true };
  function ticketTotals(t, lines, fees) {
    var r2 = F().round2;
    var base = Math.max(0, num(t.subtotal));
    var surchargePercent = Math.max(0, num(t.surcharge));
    var tipPercent = Math.max(0, num(t.gratuity));
    var surcharge = r2(base * surchargePercent / 100);
    var tip = t.tip_amount != null ? Math.max(0, r2(num(t.tip_amount))) : r2(base * tipPercent / 100);
    var delivery = r2(fees.reduce(function (s, l) { return s + l.total; }, 0));
    var grand = numOrNull(t.total);
    var payable = numOrNull(t.billTotal);
    var subtotal = grand != null ? Math.max(0, r2(grand - delivery - surcharge)) : r2(lines.reduce(function (s, l) { return s + l.total; }, 0));
    // Everything checkout took off (null when the order has no checkout figures to compare).
    var off = grand != null && payable != null ? Math.max(0, r2(grand - payable)) : null;
    var used = rawRows(t).filter(function (l) { return flag(l.isWalletRedeemed); });
    var walletAmount = r2(used.reduce(function (s, l) { return s + Math.max(0, num(l.walletAmount)); }, 0));
    var walletPoints = r2(used.reduce(function (s, l) { return s + Math.max(0, num(l.walletUsedForProduct)); }, 0));
    var pointsAmount = off != null ? Math.min(walletAmount, off) : walletAmount;
    var detail = t.couponDetail && typeof t.couponDetail === 'object' ? t.couponDetail : null;
    var coupon = null;
    if (flag(t.isCouponApplied) || detail) {
      var type = str(t.coupon_type).trim().toUpperCase().replace(/[\s-]+/g, '_') || null;
      var saved = numOrNull(t.coupon_discount_amount);
      var amount = saved != null && saved > 0 ? saved : (off != null ? off - pointsAmount : 0);
      if (off != null) amount = Math.min(amount, off - pointsAmount);
      amount = Math.max(0, r2(amount));
      coupon = {
        code: str(detail && detail.code).trim() || null, name: str(detail && detail.name).trim() || str(t.discount_reason).trim() || null,
        type: type, amount: amount, free: amount === 0 && !!FREE_ITEM_COUPON[type],
      };
    }
    var discount = off != null ? Math.max(0, r2(off - (coupon ? coupon.amount : 0) - pointsAmount)) : 0;
    var total = r2((payable != null ? payable : (num(t.paid) || subtotal)) + tip);
    var online = str(t.onlinePaymentStatus).trim().toUpperCase();
    var state = str(t.ticket_status).trim().toUpperCase();
    var refunded = /^REFUND/.test(state) || /^REFUND/.test(online);
    var settled = !refunded && (online === 'PAID' || state === 'PAID' || total <= 0);
    var paid = Math.max(0, r2(num(t.paid)));
    return {
      subtotal: subtotal, delivery: delivery, surcharge: surcharge, surchargePercent: surchargePercent, ...(t.surcharge_label ? { surchargeLabel: t.surcharge_label } : {}), tip: tip, tipPercent: tipPercent,
      tax: num(t.tax), coupon: coupon, points: walletAmount > 0 || walletPoints > 0 ? { amount: pointsAmount, points: walletPoints } : null,
      discount: discount, discountLabel: !coupon && str(t.discount_reason).trim() ? str(t.discount_reason).trim() : 'Discount',
      total: total, paid: paid, remaining: settled || refunded ? 0 : Math.max(0, r2(total - paid)), settled: settled, refunded: refunded,
    };
  }
  var PAY_TEXT = {
    online: ['Paid online', 'Not paid yet'], counter: ['Paid at the counter', 'Pay at the counter'],
    delivery: ['Paid on delivery', 'Pay on delivery'], none: ['Paid', 'Not paid yet'],
  };
  function ticketPayment(t, totals) {
    var pt = str(t.payment_type).trim().toLowerCase();
    var method = !pt ? null : (pt === 'wallet' ? 'points' : (/counter|store/.test(pt) ? 'counter' : (/cash|deliver/.test(pt) ? 'delivery' : 'online')));
    var status = totals.refunded ? 'refunded' : (totals.settled ? 'paid' : 'unpaid');
    var label = status === 'refunded' ? 'Refunded'
      : method === 'points' ? 'Paid with points'
        : status === 'paid' && totals.total <= 0 && !totals.points ? 'Nothing to pay'
          : PAY_TEXT[method || 'none'][status === 'paid' ? 0 : 1];
    return { method: method, status: status, label: label };
  }

  // An instant that is really set: epoch 0 / '' / null (delivery_time 0, agreed_delivery_time 0) mean "none".
  function setInstant(v) { var t = parseInstant(v); return t != null && t > 0 ? t : null; }
  // 'Today, 6:17pm' · 'Tomorrow, 12:15pm' · 'Yesterday, …' · 'Fri, …' (2–7 days ago) · '2 Oct, 6:30pm'.
  function dayTimeLabel(clock, today) {
    var f = F();
    var day = f.dayDiff(clock.ymd, today) === -1 ? 'Tomorrow' : f.relativeDay(clock.ymd, today);
    return day + ', ' + f.time12(clock.minutes);
  }
  function normalizeTicket(t, opts) {
    var f = F();
    var tz = opts.timeZone || null;
    var today = resolveNow(opts.now, tz).clock.ymd;
    var type = orderTypeOf(t);
    var detail = t.ordersDetail || {};
    var all = arr(detail.nonComboOrders).map(itemLineOf).concat(arr(detail.comboOrders).map(comboLineOf));
    var fees = all.filter(function (l) { return isDeliveryFee(l, type); });
    var lines = fees.length ? all.filter(function (l) { return fees.indexOf(l) < 0; }) : all;
    var merchandiseLines = lines.filter(function (l) { return !l.automaticFee; });
    var totals = ticketTotals(t, lines, fees);
    var at = t.create_at ? parseInstant(num(t.create_at)) : parseInstant(t.created_at);
    var placed = at == null ? null : f.zoned(new Date(at), tz);
    var pickupAt = setInstant(t.delivery_time) || setInstant(t.scheduled_time);
    var pickup = pickupAt == null ? null : f.zoned(new Date(pickupAt), tz);
    var paid = t.billTotal != null && t.billTotal !== '' ? num(t.billTotal) : num(t.paid);
    var grand = t.total != null && t.total !== '' ? num(t.total) : null;
    var subtotal = num(t.subtotal);
    var shortId = str(t.short_id) || null;
    var itemCount = merchandiseLines.reduce(function (s, l) { return s + l.qty; }, 0);
    var first = lines[0];
    return {
      id: str(t.ticket_id), shortId: shortId, number: shortId ? shortId.replace(/^#/, '') : null,
      orderType: type, typeLabel: TYPE_LABEL[type] || type, deliveryType: str(t.deliver_type || t.delivery_type) || null,
      status: orderStatusOf(t, type), paymentStatus: str(t.onlinePaymentStatus || t.paymentStatus || t.ticket_status) || null,
      placedAt: at, placedLabel: placed ? f.relativeDay(placed.ymd, today) : null,
      placedLabelLong: placed ? f.relativeDayLong(placed.ymd, today) : null, placedTime: placed ? f.time12(placed.minutes) : null,
      pickupAt: pickupAt, pickupTime: pickup ? f.time12(pickup.minutes) : null, pickupLabel: pickup ? dayTimeLabel(pickup, today) : null,
      subtotal: subtotal, tax: num(t.tax), surcharge: totals.surcharge,
      discount: grand != null ? Math.max(0, f.round2(grand - paid)) : 0,
      total: paid || subtotal,
      itemCount: itemCount,
      summary: first ? first.name + (lines.length > 1 ? ' + ' + (lines.length - 1) + ' more' : '') : '',
      notes: str(t.note) || null, couponName: t.isCouponApplied ? str(t.discount_reason) || null : null,
      address: str(t.customer_address) || null, storeAddress: str(t.hotel_address) || null,
      storeId: wholeId(t.user_id), rating: normalizeOrderRating(t),
      lines: lines, totals: totals, payment: ticketPayment(t, totals),
    };
  }
  function isTicket(t) { return !!t && typeof t === 'object' && !!str(t.ticket_id).trim(); }
  function normalizeOrders(raw, opts) {
    return arr(raw && raw.data).filter(isTicket).map(function (t) { return normalizeTicket(t, opts || {}); })
      .sort(function (a, b) { return (b.placedAt || 0) - (a.placedAt || 0); });
  }
  function normalizeOrder(raw, opts) {
    var t = raw && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
    return isTicket(t) ? normalizeTicket(t, opts || {}) : null;
  }
  function lastCompletedOrder(orders) {
    return arr(orders).filter(function (o) { return o.status.key === 'completed'; })[0] || null;
  }

  // ---- "All orders" (E17 customer/getOrders: online_orders rows; Batch 2 P3-04, moved from pages/orders.jsx: P3-FIX #22)
  // normalizeOnlineOrders(raw, { now, timeZone, menu? }) → OnlineOrder[], newest first. raw: customer/getOrders'
  //   { status, data: paginator } (or the bare paginator) of the chain's online_orders rows, unpaid and pending
  //   included. Rows without an order_id are dropped (a body without a page, e.g. its 400 error body, gives []).
  //   OnlineOrder = { id (order_id), shortId '#1042' (the ticket's, once it exists) | null, number, orderType 'PICKUP' |
  //   'DELIVERY' | 'DINEIN' (order_type: APP_* and 'dining' included), typeLabel, deliveryType, status { key, label, tone }
  //   (as a ticket's, orderStatusOf; plus 'payment': PROCESSING = unpaid, 'Awaiting payment' within ONLINE_LIVE_MS (24 h)
  //   of placing, else 'Not paid'), paymentStatus, placedAt (ms; the server's UTC created_at) | null, placedLabel,
  //   placedLabelLong, placedTime, pickupAt (deliver_time ms) | null, total (billTotal = the payable, else total), items
  //   [{ kind 'item' | 'combo', name, qty }] (orderData: an array, a JSON string of one, or a payload with `data`; combo
  //   rows grouped by comboBatch into one unit, named from the row, else menu.combos[id], else 'Combo'), itemCount,
  //   summary ('Burger + 2 more'), rating (OrderRating: OO.model.normalizeOrderRating of the row, read when called) |
  //   null, online: true, branch (the row's store name) | null, source 'online' }. No `lines`: these rows never reorder. Never reads the row's contact
  //   fields. OO.pages.Orders.logic.normalizeOnlineOrders (pages/orders.jsx) forwards here.
  var ONLINE_LIVE_MS = 24 * 3600000; // as pages/orders.jsx's LIVE_MS: an unpaid order still "Awaiting payment" within it
  // A row's flag: 'true' in any case counts too (the online_orders rows as sent; model.jsx's flag takes 'true' only).
  function onlineFlag(v) { return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true'; }
  // A row's rating (OrderRating | null) through OO.model.normalizeOrderRating as it is when called, as the orders page
  // read it before the move (a normaliser swapped on OO.model applies here too).
  function onlineRatingOf(r) {
    var rate = OO.model.normalizeOrderRating;
    return (typeof rate === 'function' ? rate(r) : normalizeOrderRating(r)) || null;
  }
  function onlineTypeOf(v) {
    var t = text(v).toUpperCase();
    if (t.indexOf('DELIVERY') >= 0) return 'DELIVERY';
    if (t.indexOf('PICKUP') >= 0) return 'PICKUP';
    if (t.indexOf('DIN') >= 0 || t.indexOf('TABLE') >= 0) return 'DINEIN';
    return t || 'PICKUP';
  }
  // An online_orders row's status, worded as a ticket's (orderStatusOf above), plus PROCESSING (unpaid).
  function onlineStatusOf(r, type, placedAt, nowMs) {
    var os = text(r.order_status).toUpperCase(), state = text(r.order_state).toLowerCase(), ts = text(r.ticket_status).toUpperCase();
    var pickup = type !== 'DELIVERY';
    if (os === 'REJECTED') return { key: 'rejected', label: 'Rejected', tone: 'danger' };
    if (ts === 'VOID' || ts === 'CANCELLED' || ts === 'CANCELED' || CANCELLED_STATUS[os] || state === 'cancelled' || state === 'canceled') {
      return { key: 'cancelled', label: 'Cancelled', tone: 'danger' };
    }
    if (state === 'completed' || os === 'COMPLETED' || os === 'COLLECTED') return { key: 'completed', label: pickup ? 'Picked up' : 'Delivered', tone: 'success' };
    if (state === 'ready' || os === 'READY') return { key: 'ready', label: pickup ? 'Ready for pickup' : 'Out for delivery', tone: 'success' };
    if (os === 'ACCEPTED') return { key: 'preparing', label: 'Preparing', tone: 'warn' };
    if (os === 'PROCESSING') {
      var recent = placedAt !== null && isFinite(nowMs) && Math.abs(nowMs - placedAt) < ONLINE_LIVE_MS;
      return recent ? { key: 'payment', label: 'Awaiting payment', tone: 'warn' } : { key: 'payment', label: 'Not paid', tone: 'neutral' };
    }
    return { key: 'received', label: 'Order received', tone: 'neutral' };
  }
  // orderData: the order's V4 rows (an array, a JSON string of one, or a whole payload with `data`). Combo children
  // share a comboBatch per combo unit (payload.jsx), so a batch is one combo.
  function onlineItemsOf(data, menu) {
    var rows = data;
    if (typeof rows === 'string') { try { rows = JSON.parse(rows); } catch (e) { rows = []; } }
    if (rows && !Array.isArray(rows) && typeof rows === 'object' && Array.isArray(rows.data)) rows = rows.data;
    var out = [];
    var combos = Object.create(null);
    arr(rows).forEach(function (l) {
      if (!l || typeof l !== 'object') return;
      var comboId = l.combo_id === null || l.combo_id === undefined || l.combo_id === '' ? null : l.combo_id;
      if (!onlineFlag(l.is_combo) && !(comboId !== null && num(comboId) > 0)) {
        var qty = Math.max(1, Math.round(num(l.count != null ? l.count : l.product_count)) || 1);
        out.push({ kind: 'item', name: text(l.productname) || text(l.productName) || text(l.name) || 'Item', qty: qty });
        return;
      }
      var batch = text(l.comboBatch) || 'combo-' + text(comboId);
      if (combos[batch]) return;
      var c = menu && menu.combos && comboId !== null ? menu.combos[comboId] : null;
      combos[batch] = { kind: 'combo', name: text(l.combo_name) || text(l.comboName) || (c && text(c.name)) || 'Combo', qty: 1 };
      out.push(combos[batch]);
    });
    return out;
  }
  function normalizeOnlineOrders(raw, opts) {
    var o = opts || {};
    var f = F();
    var tz = o.timeZone || null;
    var nowMs = resolveNow(o.now, tz).instant;
    var today = resolveNow(o.now, tz).clock.ymd;
    var body = raw && typeof raw === 'object' ? raw : {};
    var page = body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : body;
    return arr(page.data).filter(function (r) { return !!r && typeof r === 'object' && text(r.order_id) !== ''; }).map(function (r) {
      var type = onlineTypeOf(r.order_type);
      var at = parseInstant(r.created_at);
      var placed = at === null ? null : f.zoned(new Date(at), tz);
      var due = parseInstant(text(r.deliver_time));
      var items = onlineItemsOf(r.orderData, o.menu || null);
      var shortId = text(r.short_id) || null;
      var paid = r.billTotal !== null && r.billTotal !== undefined && r.billTotal !== '' ? num(r.billTotal) : num(r.total);
      return {
        id: text(r.order_id), shortId: shortId, number: shortId ? shortId.replace(/^#/, '') : null,
        orderType: type, typeLabel: TYPE_LABEL[type] || type, deliveryType: text(r.delivery_type) || null,
        status: onlineStatusOf(r, type, at, nowMs), paymentStatus: text(r.payment_status) || null,
        placedAt: at, placedLabel: placed ? f.relativeDay(placed.ymd, today) : null,
        placedLabelLong: placed ? f.relativeDayLong(placed.ymd, today) : null, placedTime: placed ? f.time12(placed.minutes) : null,
        pickupAt: due !== null && due > 0 ? due : null,
        total: paid, items: items, itemCount: items.reduce(function (s, it) { return s + it.qty; }, 0),
        summary: items.length ? items[0].name + (items.length > 1 ? ' + ' + (items.length - 1) + ' more' : '') : '',
        rating: onlineRatingOf(r), online: true, branch: text(r.name) || null, source: 'online',
      };
    }).sort(function (a, b) { return (b.placedAt || 0) - (a.placedAt || 0); });
  }

  // ---- order progress (§3.10, E14: online_order_status) ----------------------------------------------
  // The body: { orderid, orderstatus (the online_orders row: PROCESSING = an unpaid online order, PENDING = paid or
  // pay-at-counter and waiting for the store, ACCEPTED, REJECTED, CANCELLED), response (the store's text, e.g. 'Your
  // order #1043 will be ready in 15 minutes', or null), deliver_time, delivery_type, updated_time, ticket_created,
  // ticket_state (the POS ticket's order_state: null → 'ready' → 'completed', or 'cancelled'), fulfillment_type,
  // status: true }. The status key is also read as orderStatus / order_status, the state as order_state.
  // Progress = { step, steps:[{key, label, state 'done'|'current'|'todo'}] (received → preparing → ready: all todo
  //   before 'received', all done once completed), message (`response`) | null, orderNumber = pickupNumber ('1043':
  //   pickup_number, else /#(\d+)/ in `response`) | null, deliverTime, awaitingPayment (PROCESSING), done }
  //   step: 'payment' (PROCESSING: awaiting payment, not a failure) · 'received' (PENDING) · 'preparing' (ACCEPTED) ·
  //   'ready' (ticket_state 'ready') · 'completed' (ticket_state 'completed') · 'rejected' · 'cancelled' (CANCELLED,
  //   FAILED, ticket_state 'cancelled') · 'unknown' (anything else, e.g. the 400 { orderstatus: 'NO_DATA' }).
  //   done = a FINAL step (completed, rejected, cancelled): poll until then. READY is not final (collected follows).
  var FINAL_STEP = { completed: true, rejected: true, cancelled: true };
  function firstText(o, keys) {
    for (var i = 0; i < keys.length; i++) { var s = str(o[keys[i]]).trim(); if (s) return s; }
    return '';
  }
  function orderProgress(raw, opts) {
    opts = opts || {};
    raw = raw && typeof raw === 'object' ? raw : {};
    var os = firstText(raw, ['orderstatus', 'orderStatus', 'order_status']).toUpperCase();
    var state = firstText(raw, ['ticket_state', 'order_state']).toLowerCase();
    var pickup = opts.orderType !== 'DELIVERY';
    var step = os === 'REJECTED' ? 'rejected'
      : (CANCELLED_STATUS[os] || state === 'cancelled' || state === 'canceled') ? 'cancelled'
        : (state === 'completed' || os === 'COMPLETED' || os === 'COLLECTED') ? 'completed'
          : (state === 'ready' || os === 'READY') ? 'ready'
            : os === 'ACCEPTED' ? 'preparing'
              : os === 'PENDING' ? 'received'
                : os === 'PROCESSING' ? 'payment' : 'unknown';
    var keys = ['received', 'preparing', 'ready'];
    var labels = { received: 'Order received', preparing: 'Preparing', ready: pickup ? 'Ready for pickup' : 'On its way' };
    var at = keys.indexOf(step);
    var m = /#(\d+)/.exec(str(raw.response));
    var number = firstText(raw, ['pickup_number']).replace(/^#/, '') || (m ? m[1] : '');
    return {
      step: step,
      steps: keys.map(function (k, i) {
        return { key: k, label: labels[k], state: step === 'completed' || (at >= 0 && i < at) ? 'done' : (i === at ? 'current' : 'todo') };
      }),
      message: str(raw.response).trim() || null,
      orderNumber: number || null,
      pickupNumber: number || null,
      deliverTime: str(raw.deliver_time) || null,
      awaitingPayment: step === 'payment',
      done: !!FINAL_STEP[step],
    };
  }

  // ---- the lastOrder snapshot (contract: Batch 2 P1 "snapshot", written by checkout, kept by OO.store) ------------
  // lastOrder = { orderId, hotel, paymentMethod, placedAt (ms), total, …, snapshot: { v: 1, storeName, orderType
  // 'PICKUP'|'DELIVERY'|'DINEIN', when: { asap, label, at (ISO) | null }, lines: [{ name, qty, mods, total (money
  // text) }], rows: [{ key, label, text }], total (money text), address, table, paymentLabel } }. OO.store sanitises it
  // on save and load: a wrong-typed field is ABSENT, and a lastOrder without a valid snapshot has no `snapshot` key.
  // So every field falls back here.
  // orderSnapshot → null unless lastOrder is THIS order (orderId) of THIS store (hotel) and its snapshot has v 1 and at
  // least one line with a name (without lines the page shows the status-only card). Else Snapshot = { storeName,
  // orderType (default 'PICKUP'), typeLabel, asap, whenLabel ('ASAP · about 15 min' as saved; a scheduled `at` is
  // re-labelled for `now`: 'Today, 6:30pm' | 'Tomorrow, …') | null, lines:[{ name, qty (≥ 1), mods, total }], itemCount,
  // rows:[{ key, label, value, description? }] (PriceRows rows), total | null, address | null, table | null, paymentLabel | null,
  // placedAt (ms) | null, placedLabel ('Today, 6:02pm' | 'Last Friday, 7:15pm') | null }. Strings are trimmed.
  function text(v) { return typeof v === 'string' ? v.trim() : (typeof v === 'number' && isFinite(v) ? String(v) : ''); }
  function orderSnapshot(lastOrder, opts) {
    opts = opts || {};
    var lo = lastOrder && typeof lastOrder === 'object' ? lastOrder : null;
    var s = lo && lo.snapshot && typeof lo.snapshot === 'object' ? lo.snapshot : null;
    if (!s || s.v !== 1 || !text(opts.orderId) || !text(opts.hotel)) return null;
    if (text(lo.orderId) !== text(opts.orderId) || text(lo.hotel) !== text(opts.hotel)) return null;
    var lines = arr(s.lines).filter(function (l) { return l && typeof l === 'object' && text(l.name); }).map(function (l) {
      var qty = num(l.qty);
      return { name: text(l.name), qty: qty >= 1 ? Math.round(qty) : 1, mods: text(l.mods), total: text(l.total) };
    });
    if (!lines.length) return null;
    var f = F();
    var tz = opts.timeZone || null;
    var today = resolveNow(opts.now, tz).clock.ymd;
    var type = text(s.orderType).toUpperCase();
    if (!TYPE_LABEL[type]) type = 'PICKUP';
    var when = s.when && typeof s.when === 'object' ? s.when : {};
    var asap = when.asap === true;
    var at = asap ? null : setInstant(text(when.at));
    var placedAt = typeof lo.placedAt === 'number' ? setInstant(lo.placedAt) : null;
    var placed = placedAt == null ? null : f.zoned(new Date(placedAt), tz);
    return {
      storeName: text(s.storeName) || null, orderType: type, typeLabel: TYPE_LABEL[type], asap: asap,
      whenLabel: at != null ? dayTimeLabel(f.zoned(new Date(at), tz), today) : (text(when.label) || null),
      lines: lines, itemCount: lines.filter(function (l) { return !l.automaticFee; }).reduce(function (n, l) { return n + l.qty; }, 0),
      rows: arr(s.rows).filter(function (r) { return r && typeof r === 'object' && text(r.label); }).map(function (r, i) {
        return { key: text(r.key) || 'row' + i, label: text(r.label), value: text(r.text), ...(text(r.description) ? {description: text(r.description)} : {}) };
      }),
      total: text(s.total) || null, address: text(s.address) || null, table: text(s.table) || null,
      paymentLabel: text(s.paymentLabel) || null,
      placedAt: placedAt, placedLabel: placed ? f.relativeDayLong(placed.ymd, today) + ', ' + f.time12(placed.minutes) : null,
    };
  }

  // ---- REF's confirmation links (Batch 2 P3-02) ---------------------------------------------------------------------
  // REF's /{slug}/order-confirmed (gateway orders) and /{slug}/success (counter orders) render the order from their URL:
  // order_id plus display params (REF PaymentStatusPage / the checkout pages write them; the router sends both paths to
  // /orderStatus with the query kept minus the contact fields). The plan reads them "only as a snapshot fallback" (B §5):
  // the ticket and this device's snapshot win. They are display-only and anyone can edit them, so each is checked, cut,
  // and left out when wrong; REF's defaults ('20-30 min', 'pickup', '0.00', now) are never invented.
  // orderLinkSnapshot(query, { orderId, now, timeZone }) → null unless query.order_id is this order (orderId) and at least
  // one param is usable. Else Link = { orderType 'PICKUP'|'DELIVERY'|'DINEIN' | null (order_type pickup | delivery |
  // dining / dinein / table), typeLabel | null, paymentMethod 'online' | 'cash' | null (payment_method; REF's two values),
  // paymentLabel ('Online payment' | 'Pay on delivery' (cash, delivery) | 'Pay at the counter' (cash)) | null, total
  // (number ≥ 0, total) | null, itemCount (whole > 0, items_count) | null, placedAt (ms, placed_at) | null, placedLabel
  // ('Today, 6:02pm') | null, eta (estimated_time as written, ≤ 24 chars: '20-30 min') | null, pickupNumber ('1043':
  // order_number, else pickup_number, else kitchen_order_number; digits, an optional '#') | null, table (table_name,
  // ≤ 40 chars) | null }. 'online' says how it was to be paid, never that it was: the live status decides that.
  var LINK_TYPE = { pickup: 'PICKUP', delivery: 'DELIVERY', dining: 'DINEIN', dinein: 'DINEIN', 'dine-in': 'DINEIN', table: 'DINEIN' };
  function linkText(v, max) {
    var s = typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '';
    return s && s.length <= max ? s : '';
  }
  function orderLinkSnapshot(query, opts) {
    opts = opts || {};
    var q = query && typeof query === 'object' ? query : {};
    var id = text(opts.orderId);
    if (!id || linkText(q.order_id, 64) !== id) return null;
    var type = LINK_TYPE[linkText(q.order_type, 16).toLowerCase()] || null;
    var pm = linkText(q.payment_method, 16).toLowerCase();
    var method = pm === 'online' || pm === 'cash' ? pm : null;
    var totalText = linkText(q.total, 12);
    var total = /^\d{1,7}(\.\d{1,2})?$/.test(totalText) ? Number(totalText) : null;
    var countText = linkText(q.items_count, 4);
    var count = /^\d{1,4}$/.test(countText) && Number(countText) > 0 ? Number(countText) : null;
    var placedAt = setInstant(linkText(q.placed_at, 40) || null);
    var f = F();
    var tz = opts.timeZone || null;
    var placed = placedAt == null ? null : f.zoned(new Date(placedAt), tz);
    var today = resolveNow(opts.now, tz).clock.ymd;
    var numberText = linkText(q.order_number, 16) || linkText(q.pickup_number, 16) || linkText(q.kitchen_order_number, 16);
    var number = /^#?\s*(\d{1,10})$/.exec(numberText);
    var out = {
      orderType: type, typeLabel: type ? TYPE_LABEL[type] : null, paymentMethod: method,
      paymentLabel: method === 'online' ? 'Online payment' : (method === 'cash' ? (type === 'DELIVERY' ? 'Pay on delivery' : 'Pay at the counter') : null),
      total: total, itemCount: count, placedAt: placedAt,
      placedLabel: placed ? f.relativeDayLong(placed.ymd, today) + ', ' + f.time12(placed.minutes) : null,
      eta: linkText(q.estimated_time, 24) || null, pickupNumber: number ? number[1] : null, table: linkText(q.table_name, 40) || null,
    };
    var any = Object.keys(out).some(function (k) { return out[k] !== null; });
    return any ? out : null;
  }

  // ---- who may see the order's detail (Batch 2 P3-02; B §9 Q13) ------------------------------------------------------
  // get_order_data returns the customer's name, phone and address to anyone holding an order id, so the detail, its
  // receipt and "Order again" are for the order's owner only: this device placed it (OO.api.isOwnOrder: OO.store's
  // lastOrder is this order of this store), or it is on the signed-in customer's own order list (get_customer_orders:
  // the POS tickets, chain-wide). Anyone else sees the public status (online_order_status: no contact data).
  // orderAccess({ orderId, device, account, signedIn, list }) → Access. Pure.
  //   device    true when this device placed it      account   true when the id came from the signed-in customer's own
  //   list (the /orderStatus fallback)               signedIn  a customer is signed in
  //   list      that customer's orders as read: 'loading' (or undefined / null: not read yet) | 'error' | [Order | id]
  // Access = { state, owner, via }: state 'owner' (via 'device' | 'account') · 'checking' (signed in, the list isn't read
  // yet) · 'error' (the list couldn't be read: not judged, offer Try again) · 'guest' (signed out, no id, or the list
  // doesn't hold it). owner = state 'owner'; via null unless the owner.
  function orderAccess(input) {
    var i = input || {};
    var id = text(i.orderId);
    var guest = { state: 'guest', owner: false, via: null };
    if (!id) return guest;
    if (i.device === true) return { state: 'owner', owner: true, via: 'device' };
    if (i.account === true) return { state: 'owner', owner: true, via: 'account' };
    if (i.signedIn !== true) return guest;
    if (i.list === 'error') return { state: 'error', owner: false, via: null };
    if (!Array.isArray(i.list)) return { state: 'checking', owner: false, via: null };
    var has = i.list.some(function (o) { return text(o && typeof o === 'object' ? o.id : o) === id; });
    return has ? { state: 'owner', owner: true, via: 'account' } : guest;
  }

  // ---- the order-status page's decision (pages/order-status.jsx renders it) ----------------------------------------
  // orderStatusView({ order, orderState, progress, statusState, snapshot }) → View. Pure.
  //   order        the ticket (normalizeOrder) | null
  //   orderState   get_order_data: 'loading' (no answer yet) | 'ok' | 'missing' (404 / 400 / no ticket in the body:
  //                the POS hasn't created the ticket yet) | 'error' (any other failure) | 'private' (never asked: the
  //                viewer isn't the order's owner, orderAccess; P3-02). 'private' reads as 'missing' but is never retried.
  //   progress     orderProgress of the latest online_order_status answer | null (none yet)
  //   statusState  the status poll: 'loading' (no answer yet) | 'ok' | 'retrying' (a network / 5xx failure, still
  //                polling) | 'gone' (400 / 404 / 422: stopped) | 'off' (stopped after repeated failures) | 'stale'
  //                (stopped at the 30-minute cap)
  //   snapshot     orderSnapshot(…) | null
  // View = { show, key, final, live, refresh, retryOrder, pickupNumber, awaitingPayment }
  //   show: 'ticket' (the order from get_order_data) › 'snapshot' (this device's order as checkout showed it) ›
  //     'status' (the live status card alone: no lines) › 'loading' (an answer is pending) › 'missing' (no ticket and
  //     the status is gone: "We couldn't find this order") › 'error'. A ticket 404 is never an error by itself.
  //   key: with a ticket, its status moved FORWARD by the live progress (received → preparing → ready → completed; a
  //     live 'payment' only outranks 'received'); a live rejected / cancelled wins; the ticket's own final state wins
  //     over everything. Without one: the progress step ('unknown' → 'received'), else 'checking' while the first
  //     answer is pending, else 'unknown' (no live status to show).
  //   final: key is completed, rejected or cancelled. live: the poll runs ("Updates automatically") and key isn't
  //   final. refresh: the poll stopped (stale / off) before a final state: offer Refresh. retryOrder: no ticket yet
  //   (missing / error) while the poll runs: ask get_order_data again in 30 s. pickupNumber: the live one, else the
  //   ticket's number. awaitingPayment: key 'payment'.
  var RANK = { payment: -1, received: 0, preparing: 1, ready: 2, completed: 3 };
  function mergeKey(k, p) {
    if (FINAL_STEP[k]) return k;
    var base = RANK[k] == null ? 'received' : k;
    if (!p) return base;
    if (p.step === 'rejected' || p.step === 'cancelled') return p.step;
    if (p.step === 'payment') return base === 'received' ? 'payment' : base;
    return RANK[p.step] != null && RANK[p.step] > RANK[base] ? p.step : base;
  }
  function orderStatusView(input) {
    var i = input || {};
    var order = i.order || null;
    var p = i.progress || null;
    var os = i.orderState || 'loading';
    var ss = i.statusState || 'loading';
    var running = ss === 'loading' || ss === 'ok' || ss === 'retrying';
    var key = order ? mergeKey(order.status && order.status.key, p)
      : p ? (p.step === 'unknown' ? 'received' : p.step)
        : (ss === 'loading' || ss === 'retrying') ? 'checking' : 'unknown';
    var final = !!FINAL_STEP[key];
    var show = order ? 'ticket'
      : i.snapshot ? 'snapshot'
        : p ? 'status'
          : (os === 'loading' || ss === 'loading') ? 'loading'
            : ((os === 'missing' || os === 'private') && ss === 'gone') ? 'missing' : 'error';
    var live = !final && running;
    return {
      show: show, key: key, final: final, live: live,
      refresh: !final && (ss === 'stale' || ss === 'off'),
      retryOrder: !order && (os === 'missing' || os === 'error') && live,
      pickupNumber: (p && p.pickupNumber) || (order && order.number) || null,
      awaitingPayment: key === 'payment',
    };
  }

  // ---- notifications (PROPOSED endpoint) ------------------------------------------------------------
  // → { items:[{ id, type 'order'|'points'|'offer'|'booking', title, body, group 'Today'|'Earlier', header (the
  //     group name on the first item of each group, else ''), time ('6:12pm' today, else 'Wed' / '12 Sep'),
  //     unread, link }], unread }. `read` is a list of locally read ids. Server read_at also counts.
  function normalizeNotifications(raw, opts) {
    opts = opts || {};
    var f = F();
    var tz = opts.timeZone || null;
    var today = resolveNow(opts.now, tz).clock.ymd;
    var read = arr(opts.read).map(String);
    var items = arr(raw && raw.data).map(function (n) {
      var at = parseInstant(n.created_at);
      var c = at == null ? null : f.zoned(new Date(at), tz);
      var isToday = !!c && c.ymd === today;
      var id = String(n.id);
      return {
        id: id, type: str(n.type) || 'info', title: str(n.title), body: str(n.body), at: at,
        group: isToday ? 'Today' : 'Earlier', time: c ? (isToday ? f.time12(c.minutes) : f.relativeDay(c.ymd, today)) : '',
        unread: !n.read_at && read.indexOf(id) < 0, link: resolveLink(n.link),
      };
    }).sort(function (a, b) { return (b.at || 0) - (a.at || 0); });
    items.forEach(function (n, i) { n.header = i === 0 || items[i - 1].group !== n.group ? n.group : ''; });
    return { items: items, unread: items.filter(function (n) { return n.unread; }).length };
  }

  // ---- ratings (Batch 2 P3-06 / P3-07) ------------------------------------------------------------------------------
  // The backend (CustomerOnlineOrderController, OrderItemRatingService): customer_reviews.order_id is the ticket id and
  // user_id the TICKET's store; one customer_review_items row per product of the order (online_orders.orderData.data[]
  // product_id, combo children included), each with its own rating or, when the customer gave none, the order's.
  // REF: shared/orders/orderReview.ts, orderReviewPresentation.ts, lib/api/direct.ts fetchOrderRating.
  //   OrderRating = { rating (a whole 1–5), label ('Great'), feedback (trimmed) | null, items: [{ productId, name |
  //     null, rating (1–5) | null, feedback | null }] }. normalizeOrderRating reads the E30 body ({ status, data:
  //     { rating, feedback, items } }; data: null = not rated), a ticket (a get_customer_orders row or get_order_data's
  //     { status, data: Ticket }: rating, feedback, item_ratings) or a bare { rating, … }. REF's spellings are read too:
  //     order_rating / order_feedback; items › item_ratings › customer_review_items › review_items; an item's id
  //     product_id › productId › productItemId › item_id › itemId › id, its name product_name › productName ›
  //     productname › name, its rating rating › item_rating › review_rating, its feedback feedback › item_feedback ›
  //     review_feedback. null when there is no whole 1–5 rating (REF hasSubmittedReview): the order isn't rated.
  //   orderRatingRows(rating, order) → [{ key, productId, name, qty | null, rating | null, feedback | null }]: the items
  //     the review holds that carry a rating or a feedback (REF buildReviewRows), named by the review, else by the
  //     order's line for that product (normalizeOrder, combo children included), else 'Item'; qty = that line's.
  //   ratingItems(order) → [{ productId, name, qty }]: what the rating form lists, one row per product of the order
  //     (normalizeOrder's lines and every combo's items; the backend keys item ratings by product_id, so a product on
  //     two lines is one row with the quantities added), in order. A line without a whole product id > 0 is left out,
  //     and so is a "Delivery fee" line (REF reorder.ts skips those too: older tickets carry one).
  //   canRateOrder(order) → true when the order's status is preparing, ready or completed (REF's ACCEPTED / READY /
  //     COMPLETED, OnlineOrderDetailsPage canReview); a received, rejected or cancelled order can't be rated.
  var RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];
  var RATABLE = { preparing: true, ready: true, completed: true };
  function stars(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return n % 1 === 0 && n >= 1 && n <= 5 ? n : null;
  }
  function ratingLabel(n) { var s = stars(n); return s ? RATING_LABELS[s] : ''; }
  function firstOf(o, keys) {
    for (var i = 0; i < keys.length; i++) { if (o[keys[i]] !== undefined && o[keys[i]] !== null) return o[keys[i]]; }
    return null;
  }
  function trimmed(v) { var s = typeof v === 'string' || typeof v === 'number' ? String(v).trim() : ''; return s || null; }
  function wholeId(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return n % 1 === 0 && n > 0 ? n : null;
  }
  function normalizeOrderRating(raw) {
    var o = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
    var d = o && o.data && typeof o.data === 'object' && !Array.isArray(o.data) ? o.data : o;
    var rating = d ? stars(firstOf(d, ['rating', 'order_rating'])) : null;
    if (!rating) return null;
    var items = arr(firstOf(d, ['items', 'item_ratings', 'customer_review_items', 'review_items'])).map(function (it) {
      if (!it || typeof it !== 'object') return null;
      var id = wholeId(firstOf(it, ['product_id', 'productId', 'productItemId', 'item_id', 'itemId', 'id']));
      if (!id) return null;
      return {
        productId: id, name: trimmed(firstOf(it, ['product_name', 'productName', 'productname', 'name'])),
        rating: stars(firstOf(it, ['rating', 'item_rating', 'review_rating'])),
        feedback: trimmed(firstOf(it, ['feedback', 'item_feedback', 'review_feedback'])),
      };
    }).filter(Boolean);
    return { rating: rating, label: RATING_LABELS[rating], feedback: trimmed(firstOf(d, ['feedback', 'order_feedback'])), items: items };
  }
  // The order's products in line order: item lines, then each combo's items (normalizeOrder's line shapes).
  function orderProducts(order) {
    var out = [];
    arr(order && order.lines).forEach(function (l) {
      if (!l || typeof l !== 'object') return;
      if (l.kind === 'combo') {
        var times = num(l.qty) || 1;
        arr(l.items).forEach(function (it) { if (it) out.push({ productId: it.productId, name: it.name, qty: (num(it.qty) || 1) * times }); });
      } else {
        out.push({ productId: l.productId, name: l.name, qty: num(l.qty) || 1, ...(l.automaticFee ? { automaticFee: true } : {}) });
      }
    });
    return out;
  }
  var DELIVERY_FEE = /\bdelivery\s*fee\b/i;
  function ratingItems(order) {
    var rows = [], at = {};
    orderProducts(order).forEach(function (p) {
      var id = wholeId(p.productId);
      var name = trimmed(p.name);
      if (!id || p.automaticFee || (name && DELIVERY_FEE.test(name))) return;
      if (at[id] !== undefined) { rows[at[id]].qty += p.qty; return; }
      at[id] = rows.length;
      rows.push({ productId: id, name: name || 'Item', qty: p.qty });
    });
    return rows;
  }
  function orderRatingRows(rating, order) {
    var lines = {};
    orderProducts(order).forEach(function (p) {
      var id = wholeId(p.productId);
      if (id && !lines[id]) lines[id] = p;
    });
    return arr(rating && rating.items).map(function (it, i) {
      if (!it || (it.rating == null && !it.feedback)) return null;
      var line = lines[it.productId] || null;
      return {
        key: it.productId + '-' + i, productId: it.productId, name: it.name || trimmed(line && line.name) || 'Item',
        qty: line && num(line.qty) > 0 ? num(line.qty) : null, rating: it.rating == null ? null : it.rating, feedback: it.feedback || null,
      };
    }).filter(Boolean);
  }
  function canRateOrder(order) { return !!(order && order.status && RATABLE[order.status.key]); }

  // P3-07, the average ratings (E28 / E29) and whether a store shows them (getRestaurant review_settings; the admin's
  // "Display ratings": ReviewsSettingController, no row = both on).
  //   normalizeReviewSettings(value) → { storeRating, productRating } (REF displaySettings.ts normalizeReviewSettings):
  //     value = review_settings (an array: its first entry); display_store_rating › displayStoreRating and
  //     display_product_rating › displayProductRating. A flag that is absent / null is ON; true, 1, '1', 'true',
  //     'yes' (trimmed, any case) are on; anything else is off.
  //   restaurantReviewSettings(raw) → the same for a getRestaurant body: review_settings from data[0] › data.restaurant ›
  //     an object data › the root (the first present; A §4: REF reads the flag "at root or under data"); none = both on.
  //   normalizeRatingSummary(raw) → { average (rounded to 0.1), total (whole) } | null. raw = the E28 / E29 body
  //     ({ status: true, data: { average_rating, total_reviews } }) or its data; REF's spellings averageRating and
  //     totalRatings › reviewsCount › count too. null (show nothing, REF RatingBadge / ProductRatingPill) when the body
  //     is a failure (status false, 'error' …), the total isn't above 0 or the average isn't above 0.
  //   ratingScore(average) → one decimal ('4.6', '5.0'); '' for a non-number. ratingCount(total) → '212', and from 1000
  //     REF's compact form: '1.2k' (one decimal below 10 000, '.0' dropped: '1k'), '12k'.
  function onFlag(v) {
    if (v === undefined || v === null) return true;
    if (v === true || v === 1) return true;
    return typeof v === 'string' && ['1', 'true', 'yes'].indexOf(v.trim().toLowerCase()) >= 0;
  }
  function normalizeReviewSettings(value) {
    var c = Array.isArray(value) ? value[0] : value;
    var s = c && typeof c === 'object' ? c : {};
    return {
      storeRating: onFlag(firstOf(s, ['display_store_rating', 'displayStoreRating'])),
      productRating: onFlag(firstOf(s, ['display_product_rating', 'displayProductRating'])),
    };
  }
  function restaurantReviewSettings(raw) {
    var o = raw && typeof raw === 'object' ? raw : {};
    var d = o.data;
    var candidates = [
      Array.isArray(d) && d[0] && typeof d[0] === 'object' ? d[0] : null,
      d && typeof d === 'object' && !Array.isArray(d) && d.restaurant && typeof d.restaurant === 'object' ? d.restaurant : null,
      d && typeof d === 'object' && !Array.isArray(d) ? d : null,
      o,
    ];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (c && c.review_settings !== undefined && c.review_settings !== null) return normalizeReviewSettings(c.review_settings);
    }
    return normalizeReviewSettings(null);
  }
  // The status zoo (OO.http.isSuccess, kept pure here): a status / Status / success of false, 0, '0', 'false', 'error',
  // 'failed' or 'failure' is a failure body.
  function failed(o) {
    return ['status', 'Status', 'success'].some(function (k) {
      var v = o[k];
      return v === false || v === 0 || (typeof v === 'string' && ['0', 'false', 'error', 'failed', 'failure'].indexOf(v.trim().toLowerCase()) >= 0);
    });
  }
  function normalizeRatingSummary(raw) {
    var o = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
    if (!o || failed(o)) return null;
    var d = o.data && typeof o.data === 'object' && !Array.isArray(o.data) ? o.data : o;
    var avg = numOrNull(firstOf(d, ['average_rating', 'averageRating']));
    var total = numOrNull(firstOf(d, ['total_reviews', 'totalRatings', 'reviewsCount', 'count']));
    if (!(avg > 0) || !(total > 0)) return null;
    return { average: Math.round(avg * 10) / 10, total: Math.floor(total) };
  }
  function ratingScore(avg) {
    var n = numOrNull(avg);
    return n === null ? '' : (Math.round(n * 10) / 10).toFixed(1);
  }
  function ratingCount(total) {
    var n = Math.max(0, Math.floor(num(total)));
    if (n < 1000) return String(n);
    return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'k';
  }

  OO.model.RATING_LABELS = RATING_LABELS;
  OO.model.ratingLabel = ratingLabel;
  OO.model.normalizeOrderRating = normalizeOrderRating;
  OO.model.orderRatingRows = orderRatingRows;
  OO.model.ratingItems = ratingItems;
  OO.model.canRateOrder = canRateOrder;
  OO.model.normalizeReviewSettings = normalizeReviewSettings;
  OO.model.restaurantReviewSettings = restaurantReviewSettings;
  OO.model.normalizeRatingSummary = normalizeRatingSummary;
  OO.model.ratingScore = ratingScore;
  OO.model.ratingCount = ratingCount;

  // ---- customer (§3.7) --------------------------------------------------------------------------------
  // A stored address value → its entries (REF unpackAddressSource): a list, a JSON string, one object, or the plain
  // string customer/register stores (one entry, the default). Entries without a line are dropped.
  function addressRows(v) {
    var x = v;
    if (typeof x === 'string') {
      var s = x.trim();
      if (!s || s === 'null') return [];
      try { x = JSON.parse(s); } catch (e) { return [{ address: s, selected: true }]; }
      if (typeof x === 'string') return x.trim() ? [{ address: x.trim(), selected: true }] : [];
    }
    if (x && typeof x === 'object' && !Array.isArray(x)) x = [x];
    return arr(x).filter(function (a) { return a && typeof a === 'object' && str(a.address).trim(); });
  }
  // Customer = { uid, id, name, firstName, email, phone, birthday, addresses, customerCode, tier, points } (no customerId:
  // checkout takes the wallet's, see below). Each address is { address, city, zipcode, type, lat, lng, uid, selected }:
  // uid as stored (null without one), exactly one selected (the first marked, else the first; REF), so the checkout
  // can preselect the default (P3-11; OO.pages.Addresses.logic reads the same shape).
  // Accepts a CustomerData row, a verify-otp body ({customer}), or a customer/getdata body ({data, customerId, …}).
  // A verify-otp 201 ({customer: null, customToken} plus the adapter's root uid: a NEW email, not yet registered) is
  // null, never a customer holding only a uid.
  function normalizeCustomer(raw) {
    if (!raw) return null;
    if (Object.prototype.hasOwnProperty.call(raw, 'customer') && !raw.customer) return null;
    var c = raw.customer && typeof raw.customer === 'object' ? raw.customer : (raw.data && !Array.isArray(raw.data) ? raw.data : raw);
    if (!c || typeof c !== 'object' || (!c.uid && !c.email)) return null;
    var name = str(c.name).trim();
    var rows = addressRows(c.address);
    var picked = -1;
    rows.forEach(function (a, i) { if (picked < 0 && flag(a.selected)) picked = i; });
    return {
      uid: str(c.uid) || null, id: numOrNull(c.id), name: name, firstName: name.split(/\s+/)[0] || '',
      email: str(c.email), phone: str(c.phone_no), birthday: str(c.birthday) || null,
      addresses: rows.map(function (a, i) {
        return {
          address: str(a.address).trim(), city: str(a.city), zipcode: str(a.zipcode), type: str(a.type) || null,
          ...(a.label ? {label:str(a.label).trim().slice(0,80)} : {}),
          lat: a.geoLoc ? numOrNull(a.geoLoc.latitude) : null, lng: a.geoLoc ? numOrNull(a.geoLoc.longitude) : null,
          uid: a.uid === undefined || a.uid === null || a.uid === '' ? null : a.uid, selected: i === (picked < 0 ? 0 : picked),
        };
      }),
      // No customerId: customer/getdata's `customerId` comes from a lookup by EMAIL across stores (not store-scoped);
      // the store's cust_… id is the wallet's (get_customer_wallet_points → normalizeLoyalty().customerId, §3.12).
      customerCode: str(raw.customerCode || c.customer_code) || null,
      tier: str(raw.membershipTypeName) || null,
      points: raw.customerWalletPoints != null ? num(raw.customerWalletPoints) : null,
    };
  }

  var M = OO.model;
  M.normalizeLoyalty = normalizeLoyalty;
  M.chainName = chainName;
  M.normalizeActivity = normalizeActivity;
  M.activityStats = activityStats;
  M.normalizeOrders = normalizeOrders;
  M.normalizeOrder = normalizeOrder;
  M.normalizeOnlineOrders = normalizeOnlineOrders;
  M.lastCompletedOrder = lastCompletedOrder;
  M.orderProgress = orderProgress;
  M.orderSnapshot = orderSnapshot;
  M.orderLinkSnapshot = orderLinkSnapshot;
  M.orderAccess = orderAccess;
  M.orderStatusView = orderStatusView;
  M.normalizeNotifications = normalizeNotifications;
  M.normalizeCustomer = normalizeCustomer;
})();


// ===================== src/lib/model-booking.jsx =====================
// src/lib/model-booking.jsx: OO.model's branches, discovery, table booking and table, on the model.jsx core. API:
//   normalizeBranches(rawBulk, {now, origin, current}) → Branch[]                         (getRestaurantBulk)
//   normalizeDiscovery(rawBody, {origin}) → Discovered[] · discoveryDistanceLabel(km) → '400 m' | '3.2 km' | null
//       · discoveryQuery(raw) → { q, length, searchable } · validCoords(lat, lng) → bool · haversineKm(a, b) → km
//       (the store finder, E33 / E34; P3-23, below)
//   normalizeChain(rawChain, {now, origin, byDistance, current}) → Chain   (B31 or its interim: OO.api.getChain; P3-24)
//   normalizeBooking(rawConfig, {now, timeZone}) → Booking                                 (table booking, below)
//   normalizeSlots(rawSlots, {guests, now, date, timeZone}) → Slot[]
//   validBookingPhone(phone, timeZone) · validBookingEmail(email) → bool  (the booking's contact rules, REF)
//   normalizeReservation(rawRowOrBody, {timeZone}) → Reservation · reservationStatus(status, subStatus) → {status,
//       label, text, tone, terminal}                                                        (E39 / E40, P3-20)
//   bookingHistory(rawBody, {now, timeZone}) → { all, upcoming, next }                      (E40, P3-21)
//   normalizeTable(rawGettableDetails) → { ok, id, name, … } | { ok:false, error }        (§3.13)
//   judgeTable(rawGettableDetails, store, tableId) → { status, reason, id, name, seats, excludedCategoryIds }
//       may this table take orders for this store (mode qr's table check, P4-01; below)
//   tableSettings(rawGetRestaurant) → { enabled, guestForm }: the store's table-side switches (P4-03; below)
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var _ = OO.model._, F = _.F, num = _.num, numOrNull = _.numOrNull, str = _.str, flag = _.flag, arr = _.arr, parseJson = _.parseJson, resolveNow = _.resolveNow;
  function normalizeStore(raw, opts) { return OO.model.normalizeStore(raw, opts); }

  // ---- branches (getRestaurant.related_branches + getRestaurantBulk) ----------------------------------
  // Branch = { key, id, name, shortName, address, status:{isOpen, label}, open (bool), distanceKm, distanceLabel,
  //            modes:['Pickup','Dine-in'], modesLabel, current }
  function haversineKm(a, b) {
    var R = 6371, rad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  function distanceLabel(km) {
    if (km == null) return null;
    return km < 1 ? Math.round(km * 1000 / 10) * 10 + ' m' : (Math.round(km * 10) / 10).toFixed(1) + ' km';
  }
  function commonWordPrefix(names) {
    if (names.length < 2) return 0;
    var split = names.map(function (n) { return n.split(/\s+/); });
    var n = 0;
    while (split.every(function (w) { return w.length > n + 1 && w[n] === split[0][n]; })) n += 1;
    return n;
  }
  function normalizeBranches(rawBulk, opts) {
    opts = opts || {};
    var keys = rawBulk && typeof rawBulk === 'object' ? Object.keys(rawBulk) : [];
    var list = keys.map(function (k) { return { key: k, raw: rawBulk[k] }; })
      .filter(function (x) { return x.raw && (x.raw.status === true || x.raw.status === 'true') && arr(x.raw.data).length; })
      .map(function (x) { return { key: x.key, store: normalizeStore(x.raw, { now: opts.now }), raw: x.raw }; });
    var prefix = commonWordPrefix(list.map(function (x) { return x.store.name; }));
    var origin = opts.origin && isFinite(opts.origin.lat) && isFinite(opts.origin.lng) ? opts.origin : null;
    var out = list.map(function (x) {
      var s = x.store;
      var related = arr(x.raw.related_branches).filter(function (b) { return str(b.online_key) === x.key; })[0];
      var km = origin && s.lat != null && s.lng != null ? haversineKm(origin, { lat: s.lat, lng: s.lng }) : null;
      var MODE = { PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
      return {
        key: x.key, id: s.id, name: s.name,
        shortName: prefix ? s.name.split(/\s+/).slice(prefix).join(' ') : s.name,
        address: related && str(related.address_full) || s.address.full,
        status: { isOpen: s.open.isOpen, label: s.open.label }, open: s.open.isOpen,
        distanceKm: km == null ? null : Math.round(km * 100) / 100, distanceLabel: distanceLabel(km),
        modes: s.orderTypes.map(function (t) { return MODE[t]; }), modesLabel: s.modesLabel,
        current: !!opts.current && x.key === opts.current,
      };
    });
    if (origin) out.sort(function (a, b) { return (a.distanceKm == null) - (b.distanceKm == null) || a.distanceKm - b.distanceKm; });
    return out;
  }

  // ---- discovery: the store finder (get_online_store_list_using_name_search E34 / get_online_store_listing E33) ----
  // P3-23; REF develop src/shared/lib/onlineStoreDiscovery.ts + useRestaurantDiscovery.ts (attachDistances,
  // formatDiscoveryDistance). No map (D-27), but REF's row rules are kept.
  // Discovered = { key (online_key), name, address ('' when none), lat, lng (numbers), logo (src | null), distanceKm
  //   (km in 0.1 steps | null), distanceLabel (discoveryDistanceLabel | null) }
  // normalizeDiscovery(rawBody, {origin}) → Discovered[]: a body whose status / success is false, or without a data
  //   array, is []. A row needs a name, an online_key (online_key › onlineKey; a number is read as its digits) and valid
  //   coordinates (validCoords); the strings 'null' / 'undefined' count as nothing. Rows that fail, and a key seen
  //   before, are dropped; at most DISCOVERY_MAX (30) rows, in API order. logo: shop_logo › shop_image › logo, the first
  //   that is an absolute https URL (REF); a value without a scheme goes through OO.model.storeImage (a store-media path
  //   → the Linode https URL, the fixtures' relative paths → this origin); any other scheme (http:, data:, //host) is
  //   no logo. distanceKm: the row's `distance` (km, E33; a number ≥ 0), else the haversine distance from opts.origin
  //   ({lat, lng}) when it is valid, rounded to 0.1 km; else null. Then nearest first, rows without a distance last,
  //   ties in API order (REF).
  // discoveryDistanceLabel(km) (REF formatDiscoveryDistance): under 1 km whole metres, at least '100 m'; under 10 km
  //   '3.2 km'; from 10 km '12 km'; null without a distance.
  // discoveryQuery(raw) → { q (trimmed), length (code points, the LIKE wildcards % and _ not counted: B11 and
  //   OO.api.searchStores count the same way), searchable (length ≥ DISCOVERY_SEARCH_MIN, 2) }.
  // validCoords(lat, lng): both finite numbers, latitude within ±85, longitude within ±180 (REF isValidCoordinate).
  // haversineKm(a, b): the great-circle distance in km between two {lat, lng} points (R = 6371 km).
  var DISCOVERY_MAX = 30;
  var DISCOVERY_SEARCH_MIN = 2;
  function coordOf(v) {
    if (typeof v === 'number') return v;
    return typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  }
  function validCoords(lat, lng) {
    return typeof lat === 'number' && typeof lng === 'number' && isFinite(lat) && isFinite(lng)
      && lat >= -85 && lat <= 85 && lng >= -180 && lng <= 180;
  }
  function cleanText(v) {
    var s = typeof v === 'string' ? v.trim() : (typeof v === 'number' && isFinite(v) ? String(v) : '');
    var low = s.toLowerCase();
    return low === 'null' || low === 'undefined' ? '' : s;
  }
  function discoveryLogo(v) {
    var s = typeof v === 'string' ? cleanText(v) : '';
    if (!s) return null;
    if (/^https:\/\/[^\s]+$/i.test(s)) return s;
    if (/^[a-z][a-z0-9+.-]*:/i.test(s) || s.indexOf('//') === 0) return null;
    return OO.model.storeImage(s) || null;
  }
  function round1(km) { return Math.round(km * 10) / 10; }
  function discoveryDistanceLabel(km) {
    if (typeof km !== 'number' || !isFinite(km) || km < 0) return null;
    if (km < 1) return Math.max(100, Math.round(km * 1000)) + ' m';
    return km.toFixed(km < 10 ? 1 : 0) + ' km';
  }
  function discoveryQuery(raw) {
    var q = str(raw).trim();
    var length = q.replace(/[%_]/g, '').replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '.').length;
    return { q: q, length: length, searchable: length >= DISCOVERY_SEARCH_MIN };
  }
  function normalizeDiscovery(raw, opts) {
    opts = opts || {};
    if (!raw || typeof raw !== 'object' || raw.status === false || raw.success === false || !Array.isArray(raw.data)) return [];
    var o = opts.origin;
    var origin = o && validCoords(coordOf(o.lat), coordOf(o.lng)) ? { lat: coordOf(o.lat), lng: coordOf(o.lng) } : null;
    var seen = {};
    var rows = [];
    for (var i = 0; i < raw.data.length && rows.length < DISCOVERY_MAX; i++) {
      var r = raw.data[i];
      if (!r || typeof r !== 'object') continue;
      var name = cleanText(r.name);
      var key = cleanText(r.online_key) || cleanText(r.onlineKey);
      var lat = coordOf(r.latitude), lng = coordOf(r.longitude);
      if (!name || !key || !validCoords(lat, lng) || Object.prototype.hasOwnProperty.call(seen, key)) continue;
      seen[key] = true;
      var sent = coordOf(r.distance);
      var km = isFinite(sent) && sent >= 0 ? round1(sent) : (origin ? round1(haversineKm(origin, { lat: lat, lng: lng })) : null);
      rows.push({
        key: key, name: name, address: cleanText(r.address), lat: lat, lng: lng,
        logo: discoveryLogo(r.shop_logo) || discoveryLogo(r.shop_image) || discoveryLogo(r.logo),
        distanceKm: km, distanceLabel: discoveryDistanceLabel(km), i: rows.length,
      });
    }
    rows.sort(function (a, b) {
      var da = a.distanceKm === null ? Infinity : a.distanceKm, db = b.distanceKm === null ? Infinity : b.distanceKm;
      return da !== db ? da - db : a.i - b.i;
    });
    return rows.map(function (x) { delete x.i; return x; });
  }

  // ---- the chain (B31 online_order/chain/{key}, E47; the interim E45 + E35; P3-24, D-03) -----------------------------
  // Chain = { key (the requested store's key as the server spells it: B31's data.online_key, the interim store's own;
  //   opts.current when the body names none), found (that key is among `stores`; false: the store can't be ordered from
  //   here, being deleted, inactive, hidden from listings or without a POS: "unavailable"), current (that store | null),
  //   master: { id, key, name, image, logo (srcs | null), orderable (listed in stores), sharedLoyalty }, stores:
  //   ChainStore[], sharedLoyalty (the master's), source: 'chain' (B31) | 'interim' | null (an unreadable body) }
  // ChainStore = a Branch (normalizeBranches: key, id, name, shortName, address, status {isOpen, label}, open, distanceKm,
  //   distanceLabel, modes, modesLabel, current) plus { image, logo (srcs | null), lat, lng (valid coordinates | null),
  //   isMaster, onlineActive, inactiveReason, orderTypes: ['PICKUP' | 'DELIVERY' | 'DINEIN'], timezone, sharedLoyalty (its
  //   wallet is the master's), closesAt, opensAt ('HH:mm' | null, store-local), opensOn ('YYYY-MM-DD' | null),
  //   closedReason }
  // normalizeChain(raw, {now, origin, byDistance, current}) reads both of OO.api.getChain's bodies:
  //   B31 {status:true, data:{online_key, master, stores}}: the stores as sent and in the server's order (the master,
  //     then list_position, name, id); open_now / open_label / closes_at / opens_at / opens_on / closed_reason as sent
  //     (judged in each store's zone; a missing label reads 'Open' / 'Closed'); image / logo through
  //     OO.model.storeImage(v, id). A row without an online_key is dropped.
  //   The interim {interim:true, online_key, restaurant, list, bulk} (get_chained_shop_list + getRestaurantBulk, master
  //     keys only): the requested store is the master; the members are the list rows' online keys, the master first,
  //     then the list's order; a member is listed when its bulk entry is a usable getRestaurant body (status true and a
  //     data[0]: the legacy list is looser than B31, so deleted, suspended and POS-less stores drop out here),
  //     normalised by normalizeStore at opts.now (the open status from its hours, as the store pages show it).
  //     sharedLoyalty: a child row's chainedshops.linking; the master's, any child linked.
  //   Both: shortName as normalizeBranches; with opts.origin ({lat, lng}) a store with coordinates gets its distance
  //   (km to 0.01, '450 m' | '1.2 km'); opts.byDistance (with an origin) sorts nearest first (stores without one last,
  //   ties in chain order), else the chain's order stays. Anything else (null, an error body) is an empty chain.
  var MODE = { PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
  function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function ymdOrNull(v) { var s = str(v).trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null; }
  function textOrNull(v) { return cleanText(v) || null; }
  function emptyMaster() { return { id: null, key: null, name: null, image: null, logo: null, orderable: false, sharedLoyalty: false }; }
  // A B31 store row → the ChainStore fields (before shortName, distance and current).
  function b31Store(r) {
    var key = cleanText(r.online_key);
    if (!key) return null;
    var id = numOrNull(r.id);
    var lat = numOrNull(r.latitude), lng = numOrNull(r.longitude);
    var types = [];
    if (flag(r.pickup)) types.push('PICKUP');
    if (flag(r.delivery)) types.push('DELIVERY');
    if (flag(r.dine_in)) types.push('DINEIN');
    var open = flag(r.open_now);
    return {
      key: key, id: id, name: cleanText(r.name) || key, address: cleanText(r.address),
      image: OO.model.storeImage(r.image, id), logo: OO.model.storeImage(r.logo, id),
      lat: validCoords(lat, lng) ? lat : null, lng: validCoords(lat, lng) ? lng : null,
      isMaster: flag(r.is_master), onlineActive: flag(r.online_order_active), inactiveReason: textOrNull(r.online_inactive_reason),
      orderTypes: types, timezone: textOrNull(r.timezone), sharedLoyalty: flag(r.shared_loyalty),
      isOpen: open, label: cleanText(r.open_label) || (open ? 'Open' : 'Closed'),
      closesAt: clockOf(r.closes_at), opensAt: clockOf(r.opens_at), opensOn: ymdOrNull(r.opens_on), closedReason: textOrNull(r.closed_reason),
    };
  }
  // A usable getRestaurantBulk entry → the ChainStore fields.
  function bulkStore(key, body, now, masterKey, shared) {
    if (!isObj(body) || !(body.status === true || body.status === 'true') || !isObj(arr(body.data)[0])) return null;
    var s = normalizeStore(body, { now: now });
    var o = s.open || {};
    var ok = validCoords(s.lat, s.lng);
    return {
      key: key, id: s.id || null, name: s.name || key, address: s.address.full,
      image: s.bannerUrl || null, logo: s.logoUrl || null, lat: ok ? s.lat : null, lng: ok ? s.lng : null,
      isMaster: key === masterKey, onlineActive: !!s.online.active, inactiveReason: s.online.reason || null,
      orderTypes: s.orderTypes.slice(), timezone: s.timezone || null, sharedLoyalty: shared,
      isOpen: !!o.isOpen, label: o.label || (o.isOpen ? 'Open' : 'Closed'),
      closesAt: o.closesAt || null, opensAt: o.opensAt || null, opensOn: o.opensOn || null, closedReason: o.reason || null,
    };
  }
  function interimChain(raw, opts) {
    var d = arr(raw.restaurant && raw.restaurant.data)[0];
    d = isObj(d) ? d : {};
    var masterKey = cleanText(d.online_key) || cleanText(raw.online_key);
    var keys = [], linking = {}, anyLinked = false;
    arr(raw.list && raw.list.data).forEach(function (r) {
      var k = isObj(r) ? cleanText(r.online_key) : '';
      if (!k || keys.indexOf(k) >= 0) return;
      keys.push(k);
      if (r.childshop != null && k !== masterKey) { linking[k] = flag(r.linking); anyLinked = anyLinked || linking[k]; }
    });
    var at = keys.indexOf(masterKey);
    if (at > 0) keys.unshift(keys.splice(at, 1)[0]);
    var bulk = isObj(raw.bulk) ? raw.bulk : {};
    var stores = keys.map(function (k) {
      return bulkStore(k, Object.prototype.hasOwnProperty.call(bulk, k) ? bulk[k] : null, opts.now, masterKey, k === masterKey ? anyLinked : !!linking[k]);
    }).filter(Boolean);
    var top = normalizeStore(raw.restaurant, { now: opts.now });
    var id = numOrNull(d.id || d.hotelId);
    return {
      key: masterKey || null, source: 'interim', stores: stores,
      master: {
        id: id, key: masterKey || null, name: cleanText(d.hotelName) || null, image: top.bannerUrl || null, logo: top.logoUrl || null,
        orderable: stores.some(function (s) { return s.isMaster; }), sharedLoyalty: anyLinked,
      },
    };
  }
  function b31Chain(data) {
    var m = isObj(data.master) ? data.master : {};
    var id = numOrNull(m.id);
    var seen = {};
    var stores = arr(data.stores).map(function (r) { return isObj(r) ? b31Store(r) : null; }).filter(function (s) {
      if (!s || seen[s.key]) return false;
      seen[s.key] = true;
      return true;
    });
    return {
      key: cleanText(data.online_key) || null, source: 'chain', stores: stores,
      master: {
        id: id, key: textOrNull(m.online_key), name: textOrNull(m.name), image: OO.model.storeImage(m.image, id),
        logo: OO.model.storeImage(m.logo, id), orderable: flag(m.orderable), sharedLoyalty: flag(m.shared_loyalty),
      },
    };
  }
  function normalizeChain(raw, opts) {
    opts = opts || {};
    var c = null;
    if (isObj(raw) && raw.interim === true) c = interimChain(raw, opts);
    else if (isObj(raw) && raw.status !== false && isObj(raw.data) && Array.isArray(raw.data.stores)) c = b31Chain(raw.data);
    if (!c) c = { key: null, source: null, stores: [], master: emptyMaster() };
    var key = c.key || cleanText(opts.current) || null;
    var o = opts.origin;
    var origin = o && validCoords(coordOf(o.lat), coordOf(o.lng)) ? { lat: coordOf(o.lat), lng: coordOf(o.lng) } : null;
    var prefix = commonWordPrefix(c.stores.map(function (s) { return s.name; }));
    var stores = c.stores.map(function (s, i) {
      var km = origin && s.lat !== null && s.lng !== null ? haversineKm(origin, { lat: s.lat, lng: s.lng }) : null;
      return {
        key: s.key, id: s.id, name: s.name, shortName: prefix ? s.name.split(/\s+/).slice(prefix).join(' ') : s.name,
        address: s.address, image: s.image, logo: s.logo, lat: s.lat, lng: s.lng,
        status: { isOpen: s.isOpen, label: s.label }, open: s.isOpen,
        distanceKm: km === null ? null : Math.round(km * 100) / 100, distanceLabel: distanceLabel(km),
        modes: s.orderTypes.map(function (t) { return MODE[t]; }), modesLabel: s.orderTypes.map(function (t) { return MODE[t]; }).join(' · '),
        orderTypes: s.orderTypes, isMaster: s.isMaster, onlineActive: s.onlineActive, inactiveReason: s.inactiveReason,
        timezone: s.timezone, sharedLoyalty: s.sharedLoyalty, closesAt: s.closesAt, opensAt: s.opensAt, opensOn: s.opensOn,
        closedReason: s.closedReason, current: !!key && s.key === key, i: i,
      };
    });
    if (opts.byDistance && origin) {
      stores.sort(function (a, b) {
        if (a.distanceKm === null || b.distanceKm === null) return (a.distanceKm === null) - (b.distanceKm === null) || a.i - b.i;
        return a.distanceKm - b.distanceKm || a.i - b.i;
      });
    }
    stores.forEach(function (s) { delete s.i; });
    var current = stores.filter(function (s) { return s.current; })[0] || null;
    return { key: key, found: !!current, current: current, master: c.master, stores: stores, sharedLoyalty: c.master.sharedLoyalty, source: c.source };
  }

  // ---- table booking (get_table_booking_config / check_table_booking_slots) ---------------------------
  // Booking = { enabled, reason, slotMinutes, minGuests, maxGuests, perBookingCap, guestOptions:[{value, label,
  //   more}], dates:[{ymd, dow ('Today'|'Thu'), day (number), label}], today (ymd), timeZone, shopName,
  //   rating:{average, total}, contact:{ phone, email, address, mapLink (http(s) | null) } (the config's shop: the
  //   guest-limit dialog's "contact the restaurant", P3-19; '' when not sent) }
  //   E36: the payload sits at the root or under `data`; is_active null (or absent) means active.
  //   Party size (P3-18; REF useTableBookingFlow): minGuests = minimum_seat (a whole number ≥ 1, else 1); maxGuests =
  //   maximum_people_per_booking when it is > 0 and ≥ the minimum (perBookingCap: true; REF hasPerBookingGuestLimit),
  //   else maximum_seat when it is ≥ the minimum, else max(minimum, 10). The server checks both again on place.
  //   guestOptions: the design's tiles. Up to 8 sizes, a tile each (min … max); more than that, min … min + 6 and one
  //   last tile { value: min + 7, label: '8+' (min + 7, then '+'), more: true }, under which the page offers a stepper
  //   from min + 7 to maxGuests.
  //   dates: available_dates (they skip the store's holidays and closed days) as 'YYYY-MM-DD', de-duplicated, sorted,
  //   from `today` on: the store's day at opts.now (a Date | ms | Clock; default the device clock) in opts.timeZone
  //   (the loaded store's), else shop.timezone. REF filters them by the device's day.
  // Slot = { time 'HH:mm', label '5:00pm', from (stimeEpoch, ms) | null, upto (etimeEpoch, ms) | null, available, full,
  //   remaining (remaining_guests) | null, past }
  //   available (REF allowBooking): allow_booking, AND remaining_guests null or ≥ opts.guests (when given); full =
  //   !available. past: the slot starts before opts.now (its stimeEpoch, else opts.date + time in opts.timeZone):
  //   the server lists past slots too; false without opts.now.
  // An IANA zone Intl can read, else null.
  function bookingZone(tz) {
    var s = str(tz).trim();
    if (!s || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return s || null;
    try { new Intl.DateTimeFormat('en-US', { timeZone: s }); return s; } catch (e) { return null; }
  }
  // 'YYYY-MM-DD' (or longer, e.g. an ISO date-time) | 'DD-MM-YYYY' | 'DD/MM/YYYY' → 'YYYY-MM-DD', else null.
  function bookingYmd(v) {
    var s = str(v).trim();
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return m[1] + '-' + m[2] + '-' + m[3];
    m = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(s);
    return m ? m[3] + '-' + m[2] + '-' + m[1] : null;
  }
  // A whole number from the config (a numeric string too), else NaN.
  function wholeOf(v) {
    var n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (typeof v === 'number' ? v : NaN);
    return isFinite(n) ? Math.floor(n) : NaN;
  }
  // An epoch in ms (a slot's stimeEpoch / etimeEpoch), else null.
  function epochMs(v) {
    var n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (typeof v === 'number' ? v : NaN);
    return isFinite(n) && n >= 1e12 ? Math.round(n) : null;
  }
  var GUEST_TILES = 8;
  function guestBounds(cfg) {
    var minRaw = wholeOf(cfg.minimum_seat);
    var minG = minRaw >= 1 ? minRaw : 1;
    var cap = wholeOf(cfg.maximum_people_per_booking);
    var seats = wholeOf(cfg.maximum_seat);
    var perBookingCap = cap > 0 && cap >= minG;
    var maxG = perBookingCap ? cap : (seats >= minG ? seats : Math.max(minG, 10));
    var options = [];
    var tiles = maxG - minG + 1 <= GUEST_TILES ? maxG - minG + 1 : GUEST_TILES - 1;
    for (var g = minG; g < minG + tiles; g++) options.push({ value: g, label: String(g), more: false });
    if (tiles < maxG - minG + 1) options.push({ value: minG + tiles, label: (minG + tiles) + '+', more: true });
    return { minGuests: minG, maxGuests: maxG, perBookingCap: perBookingCap, guestOptions: options };
  }
  function normalizeBooking(raw, opts) {
    opts = opts || {};
    var f = F();
    raw = raw || {};
    var wrapped = !raw.config && raw.data && typeof raw.data === 'object' && raw.data.config;
    var body = wrapped ? raw.data : raw;
    var cfg = body.config || {};
    var shop = body.shop || {};
    var tz = bookingZone(opts.timeZone) || bookingZone(shop.timezone);
    var today = resolveNow(opts.now, tz).clock.ymd;
    var guests = guestBounds(cfg);
    var review = body.review_summary || {};
    var seen = {};
    var dates = arr(body.available_dates).map(bookingYmd).filter(function (ymd) {
      if (!ymd || ymd < today || seen[ymd]) return false;
      seen[ymd] = true;
      return true;
    }).sort();
    return {
      enabled: (raw.status === true || raw.status === 'true') && (cfg.is_active == null || flag(cfg.is_active)),
      reason: str(cfg.inactive_reason) || str(raw.error) || str(body.error) || null,
      slotMinutes: num(cfg.slot_duration) || 15,
      minGuests: guests.minGuests,
      maxGuests: guests.maxGuests,
      perBookingCap: guests.perBookingCap,
      guestOptions: guests.guestOptions,
      dates: dates.map(function (p) {
        return { ymd: p, dow: p === today ? 'Today' : f.weekdayShort(f.weekdayOf(p)), day: Number(p.slice(8, 10)), label: p === today ? 'Today' : f.weekdayShort(f.weekdayOf(p)) + ' ' + f.shortDate(p) };
      }),
      today: today,
      timeZone: tz,
      shopName: str(shop.shop_name),
      rating: { average: numOrNull(review.average_rating), total: num(review.total_reviews) },
      contact: { phone: str(shop.mobile).trim(), email: str(shop.email).trim(), address: str(shop.address).trim(), mapLink: _.httpUrl(shop.map_link) },
    };
  }
  function normalizeSlots(raw, opts) {
    opts = opts || {};
    var f = F();
    var tz = bookingZone(opts.timeZone);
    var nowMs = opts.now == null ? null : resolveNow(opts.now, tz).instant;
    var party = Number(opts.guests) > 0 ? Number(opts.guests) : null;
    var date = bookingYmd(opts.date);
    return arr(raw && raw.slots).map(function (s) {
      var remaining = numOrNull(s.remaining_guests);
      var available = flag(s.allow_booking) && (remaining === null || party === null || remaining >= party);
      var from = epochMs(s.stimeEpoch);
      var start = from !== null ? from : (date && str(s.stime) ? f.instantOf(date, str(s.stime), tz) : null);
      return {
        time: str(s.stime), label: f.time12(s.stime), from: from, upto: epochMs(s.etimeEpoch),
        available: available, full: !available, remaining: remaining,
        past: nowMs !== null && start !== null && isFinite(start) && start < nowMs,
      };
    });
  }

  // The booking's contact rules (REF isValidBookingMobileNumber / isValidBookingEmail). The mobile number: digits,
  // spaces and ( ) . - with at most one leading '+', 8–15 digits. One starting '+' or '00' passes; otherwise, by the
  // STORE's time zone, Pacific/Auckland wants an NZ mobile (02 + 7–9 digits, or 642 + 7–9) and Australia/* an AU one
  // (04 + 8 digits, or 614 + 8); any other zone takes any 8–15 digits.
  function validBookingPhone(value, timeZone) {
    var raw = str(value).trim();
    if (!raw || !/^\+?[0-9\s().-]+$/.test(raw)) return false;
    var compact = raw.replace(/[\s().-]/g, '');
    var digits = compact.replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 15) return false;
    if (compact.charAt(0) === '+' || digits.slice(0, 2) === '00') return true;
    var tz = str(timeZone);
    if (tz === 'Pacific/Auckland') return /^(02\d{7,9}|642\d{7,9})$/.test(digits);
    if (tz.indexOf('Australia/') === 0) return /^(04\d{8}|614\d{8})$/.test(digits);
    return true;
  }
  function validBookingEmail(value) {
    var raw = str(value).trim();
    return !!raw && raw.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
  }

  // ---- a reservation (table_booking_status's row, E39; a get_customer_table_bookings row, E40) -------------------
  // Reservation = { id, status, label, text, tone, terminal (see reservationStatus), raw (reservation_status as sent),
  //   subStatus (sub_status upper-cased | null: only E39 sends it, and E40 since B09), date ('YYYY-MM-DD' | null:
  //   reservation_date, a 'Y-m-d' string or an ISO date-time, first 10 characters), time ('HH:mm' | null), at (the start
  //   in ms: reservation_from, else date + time in opts.timeZone, the store's; null), upto (reservation_upto ms |
  //   null), dayLabel ('Sat 3 Oct' | ''), timeLabel ('7:30pm' | '7:30pm – 8:00pm' with the window | ''), guests (a whole
  //   number ≥ 0), name, notes, tableId, storeId (user_id), storeName / storePhone / storeAddress (E40's store_* | ''),
  //   createdAt (ms | null) }. The row's customer_email / customer_phone are never copied (pages must not show them).
  //   rawRowOrBody: the row, or { status, data: row }.
  // reservationStatus(reservation_status, sub_status) → { status, label, text, tone, terminal }, the first that applies
  //   (tokens trimmed, upper-cased, spaces / dashes → '_'; REF reservationStatus.ts, plus sub_status, which REF never reads):
  //     REJECT(ED) DECLINE(D) DENY / DENIED → 'declined' · CANCEL(L)ED → 'cancelled' (both terminal; they win over sub_status)
  //     sub_status SEATED → 'seated' (still live) · COMPLETE(D) → 'completed' · NOSHOW / NO_SHOW → 'noshow' (terminal)
  //     COMPLETE(D) DONE SEATED ARRIVED VISITED → 'completed' (terminal) · ACCEPT(ED) CONFIRM(ED) APPROVE(D) BOOKED →
  //     'confirmed' · PENDING REQUEST(ED) PLACED NEW → 'pending' · anything else, or nothing → 'checking'.
  //   tone: 'warn' (pending) | 'done' (confirmed, seated) | 'neutral' (completed, noshow, checking) | 'danger' (declined,
  //   cancelled); text: the one-line explanation the pages show.
  var STATUS = {
    pending: { label: 'Pending', text: 'The restaurant is reviewing your request. We’ll email you when it’s confirmed.', tone: 'warn', terminal: false },
    confirmed: { label: 'Confirmed', text: 'Your table is booked. See you soon!', tone: 'done', terminal: false },
    seated: { label: 'Seated', text: 'You’re at your table. Enjoy!', tone: 'done', terminal: false },
    completed: { label: 'Completed', text: 'This booking is complete. Thanks for visiting!', tone: 'neutral', terminal: true },
    noshow: { label: 'No-show', text: 'This booking was marked as a no-show.', tone: 'neutral', terminal: true },
    declined: { label: 'Declined', text: 'The restaurant couldn’t accept this request.', tone: 'danger', terminal: true },
    cancelled: { label: 'Cancelled', text: 'This booking was cancelled.', tone: 'danger', terminal: true },
    checking: { label: 'Checking', text: 'We’re checking this booking’s status.', tone: 'neutral', terminal: false },
  };
  function statusToken(v) { return str(v).trim().toUpperCase().replace(/[\s-]+/g, '_'); }
  function statusKey(status, subStatus) {
    var s = statusToken(status), sub = statusToken(subStatus);
    if (/^(REJECT|REJECTED|DECLINE|DECLINED|DENY|DENIED)$/.test(s)) return 'declined';
    if (/^(CANCEL|CANCELLED|CANCELED)$/.test(s)) return 'cancelled';
    if (sub === 'SEATED') return 'seated';
    if (sub === 'COMPLETE' || sub === 'COMPLETED') return 'completed';
    if (sub === 'NOSHOW' || sub === 'NO_SHOW') return 'noshow';
    if (/^(COMPLETE|COMPLETED|DONE|SEATED|ARRIVED|VISITED)$/.test(s)) return 'completed';
    if (/^(ACCEPT|ACCEPTED|CONFIRM|CONFIRMED|APPROVE|APPROVED|BOOKED)$/.test(s)) return 'confirmed';
    if (/^(PENDING|REQUEST|REQUESTED|PLACED|NEW)$/.test(s)) return 'pending';
    return 'checking';
  }
  function reservationStatus(status, subStatus) {
    var k = statusKey(status, subStatus), s = STATUS[k];
    return { status: k, label: s.label, text: s.text, tone: s.tone, terminal: s.terminal };
  }
  // 'H:mm' | 'HH:mm' | 'HH:mm:ss' → 'HH:mm', else null.
  function clockOf(v) {
    var m = /^(\d{1,2}):(\d{2})(?::\d{2})?/.exec(str(v).trim());
    return m && +m[1] <= 23 && +m[2] <= 59 ? (m[1].length < 2 ? '0' : '') + m[1] + ':' + m[2] : null;
  }
  function normalizeReservation(raw, opts) {
    opts = opts || {};
    var f = F();
    var row = raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : (raw || {});
    var tz = bookingZone(opts.timeZone);
    var st = reservationStatus(row.reservation_status, row.sub_status);
    var date = bookingYmd(row.reservation_date);
    var time = clockOf(row.reservation_time);
    var from = epochMs(row.reservation_from);
    var upto = epochMs(row.reservation_upto);
    var at = from !== null ? from : (date && time ? f.instantOf(date, time, tz) : null);
    if (at !== null && !isFinite(at)) at = null;
    if (!date && at !== null) date = f.zoned(new Date(at), tz).ymd;
    if (!time && at !== null) time = f.zoned(new Date(at), tz).hm;
    var end = upto !== null && at !== null && upto > at ? f.zoned(new Date(upto), tz).hm : null;
    var guests = wholeOf(row.person_count);
    var sub = statusToken(row.sub_status);
    return {
      id: str(row.reservation_id).trim(), status: st.status, label: st.label, text: st.text, tone: st.tone, terminal: st.terminal,
      raw: str(row.reservation_status), subStatus: sub || null, date: date, time: time, at: at, upto: upto,
      dayLabel: date ? f.weekdayShort(f.weekdayOf(date)) + ' ' + f.shortDate(date) : '',
      timeLabel: time ? f.time12(time) + (end && end !== time ? ' – ' + f.time12(end) : '') : '',
      guests: guests >= 0 ? guests : 0, name: str(row.customer_name).trim(), notes: str(row.notes).trim(),
      tableId: numOrNull(row.table_id), storeId: numOrNull(row.user_id),
      storeName: str(row.store_name).trim(), storePhone: str(row.store_phone).trim(), storeAddress: str(row.store_address).trim(),
      createdAt: _.parseInstant(row.created_at),
    };
  }

  // ---- the signed-in history (get_customer_table_bookings, E40; P3-21; REF OnlineOrderBookingHistory) ------------
  // bookingHistory(rawBody, {now, timeZone}) → { all, upcoming, next }: body.data's rows (the whole chain's, matched by
  //   the customer's email) as Reservations (rows without a reservation_id dropped), each with two more keys:
  //   upcoming (REF isUpcoming: not final, and the END of its booking day, in the store's zone, is still ahead of
  //   opts.now; a row with no day at all counts) and relative ('Today' | 'Tomorrow' | 'In N days' for 2–7 days ahead
  //   in the store's zone | ''). all: newest first; upcoming: the upcoming ones, soonest first; next: the first
  //   upcoming one's id (REF's "Next up") | null. Sorted by the start (reservation_from, else day + time), else
  //   created_at; rows with neither keep the API's order at the end.
  function relativeBookingDay(ymd, today) {
    var d = F().dayDiff(today, ymd);
    if (d === 0) return 'Today';
    if (d === 1) return 'Tomorrow';
    return d >= 2 && d <= 7 ? 'In ' + d + ' days' : '';
  }
  function bookingHistory(raw, opts) {
    opts = opts || {};
    var f = F();
    var tz = bookingZone(opts.timeZone);
    var now = resolveNow(opts.now, tz);
    var list = arr(raw && raw.data).filter(function (r) { return r && typeof r === 'object'; }).map(function (r, i) {
      var x = normalizeReservation(r, { timeZone: tz });
      var dayEnd = x.date ? f.instantOf(f.addDays(x.date, 1), 0, tz) - 1 : null;
      x.upcoming = !x.terminal && (dayEnd === null || !isFinite(dayEnd) || dayEnd >= now.instant);
      x.relative = x.date ? relativeBookingDay(x.date, now.clock.ymd) : '';
      return { r: x, i: i, key: x.at !== null ? x.at : x.createdAt };
    }).filter(function (e) { return !!e.r.id; });
    function order(dir) {
      return function (a, b) {
        if (a.key === null || b.key === null) return (a.key === null) - (b.key === null) || a.i - b.i;
        return (a.key - b.key) * dir || a.i - b.i;
      };
    }
    var all = list.slice().sort(order(-1)).map(function (e) { return e.r; });
    var upcoming = list.filter(function (e) { return e.r.upcoming; }).sort(order(1)).map(function (e) { return e.r; });
    return { all: all, upcoming: upcoming, next: upcoming.length ? upcoming[0].id : null };
  }

  // ---- table (§3.13) ----------------------------------------------------------------------------------
  function normalizeTable(raw) {
    if (!raw || raw.Status !== true) return { ok: false, error: str(raw && (raw.error || raw.message)) || 'Table not found' };
    var t = arr(raw.data)[0] || {};
    return {
      ok: true, id: num(t.id), name: str(t.name), seats: num(t.total_seat_count), groupId: numOrNull(t.tablesgroup_id),
      active: str(t.status || 'active') === 'active', excludedCategoryIds: arr(parseJson(t.excluded_category_ids, [])).map(num),
    };
  }

  // ---- the table check (mode qr, Batch 2 P4-01; REF TableSideContext: tableBelongsToRestaurant, the status machine) --
  // judgeTable(raw, store, tableId): raw = the E19 body (GET tables/gettableDetails?tabId=, not store-scoped), store = the
  // OO.model Store the table is for, tableId = the id asked for. → { status, reason, id, name, seats, excludedCategoryIds }:
  //   'disabled' / 'disabled'   the store takes no table orders (Store.tableOrdering false): wins over everything (REF)
  //   'closed'   / 'locked'     E19's 200 { Status:false, error:'Table is Locked!' } (tables.status inactive). REF shows
  //                             its "couldn't verify" copy for it; we say this table isn't taking orders (a deliberate
  //                             deviation: the answer is exact)
  //   'closed'   / 'inactive'   a row whose status isn't 'active'
  //   'error'    / 'unknown'    no such table ('Table Not Locked!': none or deleted), an unusable body, or no store
  //   'error'    / 'foreign'    the row belongs to another store: its user_id isn't Store.id (both must be known: a
  //                             table id from the URL alone is never trusted; REF lets a missing one through)
  //   'error'    / 'mismatch'   the row is another table than the one asked for
  //   'open'     / null         orderable
  // id (the row's, else the asked id as a number), name ('' when none), seats (total_seat_count, 0 = unknown) and
  // excludedCategoryIds (the table group's, whole numbers > 0) come from the row when there is one.
  function judgeTable(raw, store, tableId) {
    var asked = numOrNull(tableId);
    var out = { status: 'error', reason: 'unknown', id: asked, name: '', seats: 0, excludedCategoryIds: [] };
    function as(status, reason) { out.status = status; out.reason = reason; return out; }
    if (!store) return out;
    if (store.tableOrdering === false) return as('disabled', 'disabled');
    var t = normalizeTable(raw);
    if (!t.ok) return /\bis\s+locked\b/i.test(str(raw && raw.error)) ? as('closed', 'locked') : out;
    var row = arr(raw.data)[0];
    if (!row || typeof row !== 'object') return out;
    out.id = t.id;
    out.name = t.name;
    out.seats = t.seats > 0 ? Math.floor(t.seats) : 0;
    out.excludedCategoryIds = t.excludedCategoryIds.filter(function (n, i, all) { return n > 0 && Math.floor(n) === n && all.indexOf(n) === i; });
    var owner = numOrNull(row.user_id), storeId = numOrNull(store.id);
    if (!(owner > 0) || !(storeId > 0) || owner !== storeId) return as('error', 'foreign');
    if (asked === null || t.id !== asked) return as('error', 'mismatch');
    if (!t.active) return as('closed', 'inactive');
    return as('open', null);
  }

  // ---- the store's table-side switches (getRestaurant; P4-03) -------------------------------------------------------
  // tableSettings(raw) → { enabled, guestForm }, from the store row of a getRestaurant body (data[0], else data.restaurant,
  // as normalizeStore reads it): enabled = enable_table_side_ordering (default true: Store.tableOrdering reads the same
  // flag); guestForm = is_user_form_table_side (table_side_orders.is_user_form; default false): REF asks a table for its
  // party size (TableWelcome) and makes contact details mandatory at its checkout when it is on. A flag is true / 1 /
  // '1' / 'true' (false / 0 / '0' / 'false' off; anything else the default). The normalised Store carries guestForm as
  // settings.tableUserForm (src/lib/model-store.jsx); this reader of the raw body serves a Store without that key.
  function flagOr(v, dflt) {
    if (v === true || v === false) return v;
    var s = str(v).trim().toLowerCase();
    if (s === '1' || s === 'true') return true;
    if (s === '0' || s === 'false') return false;
    return dflt;
  }
  function tableSettings(raw) {
    var data = raw && typeof raw === 'object' ? raw.data : null;
    var d = arr(data)[0] || (data && typeof data === 'object' && !Array.isArray(data) ? data.restaurant : null);
    d = d && typeof d === 'object' && !Array.isArray(d) ? d : {};
    return { enabled: flagOr(d.enable_table_side_ordering, true), guestForm: flagOr(d.is_user_form_table_side, false) };
  }

  var M = OO.model;
  M.normalizeBranches = normalizeBranches;
  M.normalizeDiscovery = normalizeDiscovery;
  M.discoveryDistanceLabel = discoveryDistanceLabel;
  M.discoveryQuery = discoveryQuery;
  M.validCoords = validCoords;
  M.haversineKm = haversineKm;
  M.normalizeChain = normalizeChain;
  M.normalizeBooking = normalizeBooking;
  M.normalizeSlots = normalizeSlots;
  M.validBookingPhone = validBookingPhone;
  M.validBookingEmail = validBookingEmail;
  M.normalizeReservation = normalizeReservation;
  M.reservationStatus = reservationStatus;
  M.bookingHistory = bookingHistory;
  M.normalizeTable = normalizeTable;
  M.judgeTable = judgeTable;
  M.tableSettings = tableSettings;
})();


// ===================== src/lib/cart.jsx =====================
// src/lib/cart.jsx: OO.cart (alias OO.lib.cart). Pure cart-line maths; OO.pricing is pricing.jsx + payload.jsx.
// The live, persisted cart is OO.store (src/lib/store.jsx), which calls these. No JSX, no storage.
//
// Lines are built to convert in Batch 2 to the checkout payload (§3.8, here: pricing.checkoutPayload) or to
// online_order/cart/sync (§3.14):
//   item  = { key, kind:'item', itemId, name, sizeId, sizeName, basePrice (size price || sale_price: the
//             payload `price`), modifiers:[{ addOnId, groupId, name, price, count, free }], qty, notes,
//             unitPrice, image, categoryId, pricetagId:null[, reward] }
//   A REWARD line (P2-06: wallet type 2, a loyalty product the customer's points pay for; REF isLoyaltyAddedProduct) is
//   an item line with reward = { points } (the points it cost when it was added: OO.wallet.addReward), qty 1 and a
//   unique key 'reward-{item key}-{uid}'. It is priced like any item line (its unitPrice and lineTotal count in the
//   subtotal, its modifiers are charged); the wallet engine (OO.wallet, wallet.jsx) takes its product price off and
//   sends it as a reward row. It never merges with another line, and its qty never goes above 1 (REF locks it).
//   combo = { key, kind:'combo', comboId, name, comboPrice, picks:[{ groupId, mappingId, productId, name, sizeId,
//             sizeName, price (product price, payload only, never charged), extraPrice, count, modifiers:[…] }],
//             qty, notes, unitPrice, image }
//   Modifier rows (an item's, a combo pick's) are OO.modifiers.selectedOptions rows: count and free are PER UNIT (of
//   the item, of the pick), in pick order (group order, then first-pick order within a group).
//
// Free modifier allowances (B §3.1; the server's ModifierPricingService::allocate). `free` = the row's free units from
// OO.modifiers.allocateFree: Group.freeCount units of a group for ONE unit, handed out in row order; zero-priced units
// use them up too. A row charges price × (count − free):
//   item  unitPrice = (size || base price) + Σ price × (count − free)
//   combo unitPrice = combo price + Σ picks: count × (extraPrice + Σ price × (count − free)), each pick allocated on
//                     its own product's groups (the product price is never charged)
// lineTotal = unitPrice × qty is the server's figure: per payload row it allocates freeCount × N over count × N units
// in the row order, which is the line's (N = the item qty, or a combo pick's count in each combo unit's batch; see
// OO.modifiers' header for why that is N × ours). The payload sends every selected unit in `count` and no free
// field; `free` is never read back from storage (sanitize).
//
// OO.cart:
//   itemLine(product, { sizeId, sel, qty, notes }) → item line (OO.modifiers rules: hidden options dropped, rows with
//       their free units, the unit price above: OO.modifiers.unitPrice).
//   comboLine(combo, picks:[{ groupId, mappingId, option, mods }], { qty, notes, key }) → combo line. Identical
//       picks merge into count: the same group, mapping, modifier ids and free units per option. Picks of the same
//       options picked in another order, whose allowance lands on other options, stay apart: a merge never moves the
//       price (each pick keeps its own allocation, as OO.combo prices it). OO.combo.line() calls this. A pick of a
//       'choice' option comes AT its chosen size (sizeId, sizeName, price = the size's), and picks merge only at the
//       same size. Stored 'choice' picks are read back at their own sizeId everywhere below (sanitize, fromOrder,
//       lineIssue, reprice): that size's price and groups; a size the menu no longer has makes the line 'changed'.
//   lineKey(line) → the item key per §8: '{itemId}-{sizeId|no-size}-{sorted modifier ids|no-mods}', plus
//       '-note:{notes}' when there are notes, so kitchen notes never merge away. A combo keeps its unique key
//       (combos never merge), and so does a reward line ('reward-…'; a new one gets 'reward-{item key}-{uid}'). The ids
//       are sorted: two item lines that differ only in pick order share a key and merge (add, replace, sanitize), and
//       the merged line keeps the FIRST line's rows, their order and its unit price. That is what the server charges
//       for it: it allocates in the payload's row order, the kept line's.
//   add(lines, line) → lines'. An item with an equal key merges qty. A combo or a reward line is appended (its key is
//       made unique; a reward line at qty 1).
//   setQty(lines, key, qty) → lines' (qty ≤ 0 removes; a reward line stays at 1).   inc(lines, key) / dec(lines, key)
//       (dec at 1 removes).
//   remove(lines, key) → lines'.   replace(lines, key, line) → lines' (edit; merges into an equal line; a reward line
//       keeps its key and qty 1 and never merges).
//   rewardLine(product, { sizeId, sel, notes }, { points }) → a reward line (P2-06): OO.cart.itemLine at qty 1 with
//       reward = { points } (≥ 0) and a reward key. OO.wallet.addReward builds it once its gates pass.
//   count(lines) → Σ qty.   subtotal(lines) → Σ unitPrice × qty.   lineTotal(line) → unitPrice × qty.
//   displayMods(line) → 'Make it a Meal · 1 × Onion Rings, 1 × Mountain Dew' (item: size · count × option, …),
//                       'Margherita (Classic · Extra cheese) · 2 × Garlic Bread · Coca-Cola (Can 375ml)' (combo).
//       P2-33 (REF develop's "(Free)" rows, B-Δ1): a PRICED row whose units the allowance covers says so: '1 × Bacon
//       (Free)' (every unit free), '2 × Bacon (1 free)' (some); a row priced 0 never does, even when its units used up
//       the allowance (REF marks only rows with a price: nothing was saved on it). REF prints each row's charged amount
//       instead; these lines show no per-option prices, so only the free units are marked. Combo picks' rows follow
//       the same rule (REF marks no combo child).
//   sanitize(raw, menu?) → the valid lines of an untrusted array (storage); bad lines are dropped and totals
//       recomputed. A stored `free` is never trusted: every line's rows are re-allocated, in their stored order, with
//       the allowances known now: `menu`'s Group.freeCount (the groups of the line's product / of each pick's option).
//       Without a menu (OO.store's load before the hotel's menu is in) none is known, so free is 0 until OO.store
//       sanitises the lines again with the menu at the order's moment (atMoment's; W5-FIX #1) or reprice re-derives
//       it. The stored unitPrice is kept either way (reprice re-derives prices). A reward line keeps its
//       reward key and comes back at qty 1 with reward = { points }; one whose `reward` isn't an object with points
//       > 0 is DROPPED (never turned into a line the customer pays for in full).
//   fromOrder(order, menu, { now }) → { lines, skipped:[{ name, reason }] }. "Reorder" / "Order again": an OO.model
//       Order rebuilt as cart lines on the CURRENT menu (today's prices; OO.cart.itemLine / comboLine), the ONE
//       implementation every page uses. Order lines carry sizeName (not sizeId) and modifier ids without their group,
//       so both are looked up in the menu product:
//         item  → the product by id, its size by name (no size on the order → the first size), each modifier by option
//                 id within the visible groups; the selection must still fit every group (min / max).
//         combo → the combo by id; each item (× its qty) onto a group option of the same product (and size name),
//                 within the group's capacity under OO.combo's step rules; every group's required picks must be met.
//       Loyalty rewards (line.reward) are left out entirely: they aren't purchasable. Skipped reasons:
//         'missing'      no longer on the menu (the product or the combo)
//         'soldOut'      sold out (the product, or the only combo option for an item)
//         'unavailable'  outside its availability window or its category's (combos: the deal's days / hours)
//         'changed'      the size, options or deal contents can't be rebuilt (removed, or a new required choice)
//       Availability is read from the menu as it was normalised (OO.data.loadHotel normalises at load time; the
//       products keep no raw rules to re-check). `now` is accepted for symmetry with the normalisers and reserved.
//   lineIssue(line, menu, { at }) → null | { code, text }: why a cart line can't be ordered from `menu` — a menu
//       normalised for the time and order-type domain to check (OO.data.menuAt for the selected pickup slot; null menu
//       → null). Codes as fromOrder's: 'missing' ('No longer available': the product / combo is gone; 'No longer
//       available as a reward': a reward line's product is no longer a loyalty product, Product.isLoyalty), 'soldOut'
//       ('Sold out' | 'Crispy Chips is sold out' for a combo pick), 'unavailable' ('Not available right now', or
//       'Not available at 6:30pm' with opts.at = '6:30pm'; the product, its category, the combo or a pick),
//       'changed' ('Options have changed': a size, option or combo pick is gone, or a required choice is new; the
//       line can be edited).
//   issues(lines, menu, opts) → { [lineKey]: { code, text } } for the lines that have one.
//   reprice(lines, menu) → { lines, changed:[keys] }: every unit price re-derived from the current menu (size or base
//       price, option prices; combo price + pick extras + pick option prices; names refreshed; each row's `free`
//       re-allocated from today's Group.freeCount in the line's row order) without touching keys, quantities or
//       notes. `changed` lists the lines whose unit price moved, by an allowance change too; a line whose only change
//       is `free` (on a zero-priced unit, or the 0 sanitize left) is refreshed but not listed. A line the menu can't
//       rebuild keeps its price (lineIssue flags it). The SAME array comes back when nothing changed.
//   atMoment(menu, at, lines) → menu' (W5-FIX #1): `menu` with the products and combos `lines` name judged at another
//       moment. `at` is the same menu normalised then (OO.data.menuAt: the order's slot and domain); each such product,
//       and each option of such a combo, takes its sizes', groups' and options' `available` from `at`
//       (OO.modifiers.withAvailability), while prices, names and everything else stay `menu`'s (a tag's prices
//       included). `menu` itself when `at` is null or `menu`, or nothing moves. sanitize / reprice over it read a line's
//       groups, and so its allowances, as they are at that moment: a group offered only at the chosen slot keeps its
//       free units (OO.store re-derives a reloaded cart's there).
//   __setUid(fn) → injects the unique-suffix generator (tests).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function F() { return OO.format; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function r2(n) { return F().round2(n); }

  var seq = 0;
  var uidGen = function () { seq += 1; return Date.now().toString(36) + seq.toString(36); };

  // ---- lines ---------------------------------------------------------------------------------------
  function modsKey(mods) {
    var ids = [];
    arr(mods).forEach(function (m) { for (var i = 0; i < Math.max(1, num(m.count)); i++) ids.push(num(m.addOnId)); });
    ids.sort(function (a, b) { return a - b; });
    return ids.length ? ids.join(',') : 'no-mods';
  }

  // A modifier row's charged units (count − free) and Σ price × charged units of a row list: the allowance-aware
  // modifier total of one unit (of an item, of a combo pick). `free` is OO.modifiers.allocateFree's.
  function charged(m) { return Math.max(0, num(m.count) - num(m.free)); }
  function modsTotal(mods) { return arr(mods).reduce(function (t, m) { return t + num(m.price) * charged(m); }, 0); }

  // A reward line (P2-06; see the header): an item line carrying `reward`.
  function isReward(line) { return !!line && line.kind === 'item' && !!line.reward; }
  var REWARD_PREFIX = 'reward-';
  function itemKey(line) {
    var notes = String(line.notes || '').trim();
    return line.itemId + '-' + (line.sizeId != null ? line.sizeId : 'no-size') + '-' + modsKey(line.modifiers) + (notes ? '-note:' + notes : '');
  }
  function lineKey(line) {
    if (!line) return '';
    if (line.kind === 'combo') return line.key || 'combo-' + line.comboId + '-' + uidGen();
    if (isReward(line)) return typeof line.key === 'string' && line.key.indexOf(REWARD_PREFIX) === 0 ? line.key : REWARD_PREFIX + itemKey(line) + '-' + uidGen();
    return itemKey(line);
  }

  function itemLine(product, opts) {
    opts = opts || {};
    var M = OO.modifiers;
    var sizes = arr(product.sizes);
    var sizeId = opts.sizeId !== undefined ? opts.sizeId : (sizes.length ? sizes[0].id : null);
    var size = sizes.filter(function (s) { return s.id === sizeId; })[0] || null;
    if (!size) sizeId = null;
    var groups = M.visibleGroups(product, sizeId);
    var mods = M.selectedOptions(groups, opts.sel || {});
    var line = {
      key: '', kind: 'item', itemId: product.id, name: product.name,
      sizeId: sizeId, sizeName: size ? size.name : null,
      basePrice: size ? size.price : num(product.price),
      modifiers: mods,
      qty: Math.max(1, Math.floor(num(opts.qty)) || 1),
      notes: String(opts.notes || '').trim(),
      unitPrice: M.unitPrice(product, sizeId, opts.sel || {}),
      image: product.image || null,
      categoryId: product.categoryId == null ? null : product.categoryId,
      pricetagId: null,
    };
    line.key = lineKey(line);
    return line;
  }

  function rewardLine(product, opts, reward) {
    opts = opts || {};
    var line = itemLine(product, { sizeId: opts.sizeId, sel: opts.sel, notes: opts.notes, qty: 1 });
    line.reward = { points: Math.max(0, num(reward && reward.points)) };
    line.key = lineKey(Object.assign({}, line, { key: '' }));
    return line;
  }

  // Two picks merge only when they charge the same: the same modifier ids (× count) AND the same free units per
  // option. The same options picked in another order can hold the allowance on other options (allocateFree runs in
  // row order); merged, the second pick would be priced with the first one's allocation.
  function freeKey(mods) { return arr(mods).map(function (m) { return num(m.addOnId) + ':' + num(m.free); }).sort().join(','); }
  function samePickMods(a, b) { return modsKey(a) === modsKey(b) && freeKey(a) === freeKey(b); }

  function comboLine(combo, picks, opts) {
    opts = opts || {};
    var M = OO.modifiers;
    var out = [];
    arr(picks).forEach(function (p) {
      var o = p.option || {};
      var groups = M.visibleGroups(o, o.sizeId);
      var mods = M.selectedOptions(groups, p.mods || {});
      var sizeId = o.sizeId == null ? null : o.sizeId;
      var existing = out.filter(function (x) { return x.groupId === p.groupId && x.mappingId === p.mappingId && x.sizeId === sizeId && samePickMods(x.modifiers, mods); })[0];
      if (existing) { existing.count += 1; return; }
      out.push({
        groupId: p.groupId, mappingId: p.mappingId, productId: o.productId, name: o.name,
        sizeId: sizeId, sizeName: o.sizeName || null, price: num(o.price),
        extraPrice: num(o.extraPrice), count: 1, modifiers: mods,
      });
    });
    var unit = num(combo.price) + out.reduce(function (t, x) { return t + x.count * (x.extraPrice + modsTotal(x.modifiers)); }, 0);
    return {
      key: 'combo-' + combo.id + '-' + (opts.key || uidGen()),
      kind: 'combo', comboId: combo.id, name: combo.name, comboPrice: num(combo.price), picks: out,
      qty: Math.max(1, Math.floor(num(opts.qty)) || 1), notes: String(opts.notes || '').trim(),
      unitPrice: r2(unit), image: combo.image || null,
    };
  }

  function lineTotal(line) { return r2(num(line && line.unitPrice) * num(line && line.qty)); }
  function count(lines) { return arr(lines).reduce(function (t, l) { return t + num(l.qty); }, 0); }
  function subtotal(lines) { return r2(arr(lines).reduce(function (t, l) { return t + num(l.unitPrice) * num(l.qty); }, 0)); }

  function uniqueKey(lines, key) {
    var k = key, n = 2;
    while (arr(lines).some(function (l) { return l.key === k; })) { k = key + '~' + n; n += 1; }
    return k;
  }

  function add(lines, line) {
    var list = arr(lines);
    if (!line || num(line.qty) <= 0) return list;
    if (line.kind === 'combo') {
      return list.concat([Object.assign({}, line, { key: uniqueKey(list, line.key || lineKey(line)) })]);
    }
    if (isReward(line)) return list.concat([Object.assign({}, line, { qty: 1, key: uniqueKey(list, lineKey(line)) })]);
    var key = lineKey(line);
    var hit = list.filter(function (l) { return l.key === key; })[0];
    if (!hit) return list.concat([Object.assign({}, line, { key: key })]);
    return list.map(function (l) { return l.key === key ? Object.assign({}, l, { qty: num(l.qty) + num(line.qty) }) : l; });
  }

  function setQty(lines, key, qty) {
    var q = Math.floor(num(qty));
    if (q <= 0) return remove(lines, key);
    return arr(lines).map(function (l) { return l.key === key ? Object.assign({}, l, { qty: isReward(l) ? 1 : q }) : l; });
  }
  function inc(lines, key) {
    var l = arr(lines).filter(function (x) { return x.key === key; })[0];
    return l ? setQty(lines, key, num(l.qty) + 1) : arr(lines);
  }
  function dec(lines, key) {
    var l = arr(lines).filter(function (x) { return x.key === key; })[0];
    return l ? setQty(lines, key, num(l.qty) - 1) : arr(lines);
  }
  function remove(lines, key) { return arr(lines).filter(function (l) { return l.key !== key; }); }

  function replace(lines, key, line) {
    var list = arr(lines);
    var at = -1;
    list.forEach(function (l, i) { if (l.key === key) at = i; });
    if (at < 0) return add(list, line);
    var rest = list.slice(0, at).concat(list.slice(at + 1));
    if (isReward(line)) { // an edited reward line keeps its place, its key (a reward one) and qty 1
      var rk = isReward(list[at]) ? key : uniqueKey(rest, lineKey(line));
      var out = rest.slice();
      out.splice(at, 0, Object.assign({}, line, { key: rk, qty: 1 }));
      return out;
    }
    var nextKey = line.kind === 'combo' ? key : lineKey(line);
    var merge = line.kind !== 'combo' && rest.filter(function (l) { return l.key === nextKey; })[0];
    if (merge) return rest.map(function (l) { return l.key === nextKey ? Object.assign({}, l, { qty: num(l.qty) + num(line.qty) }) : l; });
    var copy = rest.slice();
    copy.splice(at, 0, Object.assign({}, line, { key: nextKey }));
    return copy;
  }

  // ' (Free)' when every unit of a priced row is free, ' (1 free)' when only some are, '' when none (or no `free`) and
  // for a row priced 0 (P2-33, REF: an allowance unit spent on a free option saves nothing, so it isn't marked).
  function freeTag(m) {
    var free = num(m.free);
    if (free <= 0 || !(num(m.price) > 0)) return '';
    return free >= num(m.count) ? ' (Free)' : ' (' + free + ' free)';
  }

  function modsText(mods, joiner) {
    return arr(mods).map(function (m) { return m.count + ' × ' + m.name + freeTag(m); }).join(joiner);
  }

  function displayMods(line) {
    if (!line) return '';
    if (line.kind === 'combo') {
      return arr(line.picks).map(function (p) {
        var mt = arr(p.modifiers).map(function (m) { return (m.count > 1 ? m.count + ' × ' : '') + m.name + freeTag(m); }).join(' · ');
        return (p.count > 1 ? p.count + ' × ' : '') + p.name + (mt ? ' (' + mt + ')' : '');
      }).join(' · ');
    }
    var parts = [];
    if (line.sizeName) parts.push(line.sizeName);
    var mt2 = modsText(line.modifiers, ', ');
    if (mt2) parts.push(mt2);
    return parts.join(' · ');
  }

  // A stored line's rows, in their stored order, with `free` re-allocated over `groups` (the stored value is never
  // trusted; no groups = no allowance known = free 0).
  function cleanMods(list, groups) {
    var M = OO.modifiers;
    var rows = arr(list).filter(function (m) { return m && isFinite(Number(m.addOnId)); }).map(function (m) {
      return { addOnId: num(m.addOnId), groupId: num(m.groupId), name: String(m.name || ''), price: num(m.price), count: Math.max(1, Math.floor(num(m.count)) || 1) };
    });
    return M.allocateFree(rows, M.freeByGroupOf(groups));
  }
  // The groups whose Group.freeCount sanitize allocates with: the line's product (at its size) or the pick's option on
  // `menu`; [] without a menu or when it no longer has them (reprice / lineIssue deal with those lines).
  function storedItemGroups(menu, itemId, sizeId, reward) {
    if (reward) menu = OO.model.redemptionMenu(menu);
    var p = menu && menu.products ? menu.products[itemId] : null;
    return p && sizeOf(p, sizeId).ok ? OO.modifiers.visibleGroups(p, sizeId) : [];
  }
  function storedPickGroups(menu, comboId, pick) {
    var c = menu && menu.combos ? menu.combos[comboId] : null;
    var o = c ? pickAt(c, pick) : null;
    return o ? OO.modifiers.visibleGroups(o, o.sizeId) : [];
  }

  function sanitize(raw, menu) {
    var out = [];
    arr(raw).forEach(function (l) {
      if (!l || typeof l !== 'object' || !(num(l.qty) >= 1) || !isFinite(Number(l.unitPrice))) return;
      var qty = Math.floor(num(l.qty));
      if (l.kind === 'item' && isFinite(Number(l.itemId))) {
        // A reward line (P2-06) needs its reward with points > 0, or it goes: never a reward priced in full.
        var reward = l.reward === undefined || l.reward === null || l.reward === false ? null : l.reward;
        if (reward !== null && !(reward && typeof reward === 'object' && num(reward.points) > 0)) return;
        var sizeId = l.sizeId == null ? null : num(l.sizeId);
        var item = {
          key: '', kind: 'item', itemId: num(l.itemId), name: String(l.name || ''),
          sizeId: sizeId, sizeName: l.sizeName ? String(l.sizeName) : null,
          basePrice: num(l.basePrice != null ? l.basePrice : l.unitPrice), modifiers: cleanMods(l.modifiers, storedItemGroups(menu, num(l.itemId), sizeId, reward)), qty: qty,
          notes: String(l.notes || ''), unitPrice: r2(l.unitPrice), image: l.image || null,
          categoryId: l.categoryId == null ? null : l.categoryId, pricetagId: l.pricetagId == null ? null : l.pricetagId,
        };
        if (reward) {
          item.reward = { points: num(reward.points) };
          item.qty = 1;
          item.key = typeof l.key === 'string' && l.key.indexOf(REWARD_PREFIX) === 0 ? l.key : '';
        }
        item.key = lineKey(item);
        out = add(out, item);
      } else if (l.kind === 'combo' && isFinite(Number(l.comboId)) && Array.isArray(l.picks)) {
        out = add(out, {
          key: String(l.key || 'combo-' + l.comboId + '-' + uidGen()), kind: 'combo', comboId: num(l.comboId), name: String(l.name || ''),
          comboPrice: num(l.comboPrice), qty: qty, notes: String(l.notes || ''), unitPrice: r2(l.unitPrice), image: l.image || null,
          picks: l.picks.filter(function (p) { return p && isFinite(Number(p.mappingId)); }).map(function (p) {
            var pick = {
              groupId: num(p.groupId), mappingId: num(p.mappingId), productId: num(p.productId), name: String(p.name || ''),
              sizeId: p.sizeId == null ? null : num(p.sizeId), sizeName: p.sizeName ? String(p.sizeName) : null,
              price: num(p.price), extraPrice: num(p.extraPrice), count: Math.max(1, Math.floor(num(p.count)) || 1), modifiers: [],
            };
            pick.modifiers = cleanMods(p.modifiers, storedPickGroups(menu, num(l.comboId), pick));
            return pick;
          }),
        });
      }
    });
    return out;
  }

  // ---- reorder: fromOrder (see the header) ----------------------------------------------------------------
  function norm(s) { return String(s == null ? '' : s).trim().toLowerCase(); }

  // The selection ({ groupId: [optionId…] }) that reproduces an order line's modifiers (option id + PER-UNIT count)
  // within `groups`, or null when an option is gone or the selection no longer fits a group (min / max).
  function selFromOrder(groups, mods) {
    var M = OO.modifiers;
    var sel = {};
    var list = arr(mods);
    for (var i = 0; i < list.length; i++) {
      var id = num(list[i].id);
      var g = null;
      for (var gi = 0; gi < groups.length && !g; gi++) {
        if (arr(groups[gi].options).some(function (o) { return o.id === id; })) g = groups[gi];
      }
      if (!g) return null;
      var n = Math.max(1, Math.round(num(list[i].count)) || 1);
      for (var k = 0; k < n; k++) sel[g.id] = (sel[g.id] || []).concat([id]);
    }
    if (groups.some(function (x) { return M.count(sel, x.id) > x.max; })) return null;
    return M.validate(groups, sel).ok ? sel : null;
  }

  function reorderItem(l, menu, cats) {
    var p = menu.products ? menu.products[l.productId] : null;
    if (!p) return { reason: 'missing' };
    if (p.soldOut) return { reason: 'soldOut' };
    var cat = p.categoryId != null ? cats[String(p.categoryId)] : null;
    if (p.available === false || (cat && cat.available === false)) return { reason: 'unavailable' };
    var sizes = arr(p.sizes);
    var sizeId = null;
    if (sizes.length) {
      var size = l.sizeName ? sizes.filter(function (s) { return norm(s.name) === norm(l.sizeName); })[0] : sizes[0];
      if (!size) return { reason: 'changed' };
      sizeId = size.id;
    } else if (l.sizeName) {
      return { reason: 'changed' };
    }
    var sel = selFromOrder(OO.modifiers.visibleGroups(p, sizeId), l.modifiers);
    if (!sel) return { reason: 'changed' };
    return { line: itemLine(p, { sizeId: sizeId, sel: sel, qty: l.qty, notes: l.notes || '' }) };
  }

  // A combo group holds up to max picks and needs min (OO.combo's steps: max(1, min) single picks, or one multi step
  // of min..max). The first group with room that offers the product (same size name) takes the item; a sold-out /
  // unavailable option is only the reason when no usable one is left.
  function reorderCombo(l, menu) {
    var c = l.comboId != null && menu.combos ? menu.combos[l.comboId] : null;
    if (!c) return { reason: 'missing' };
    if (c.available === false) return { reason: 'unavailable' };
    var groups = arr(c.groups);
    var used = {};
    var picks = [];
    var items = arr(l.items);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var qty = Math.max(1, Math.round(num(it.qty)) || 1);
      // The order's modifier counts cover the item's whole qty: back to per unit.
      var mods = arr(it.modifiers).map(function (m) { return Object.assign({}, m, { count: Math.max(1, Math.round(num(m.count) / qty) || 1) }); });
      for (var k = 0; k < qty; k++) {
        var hit = null, why = null;
        for (var gi = 0; gi < groups.length && !hit; gi++) {
          var g = groups[gi];
          if ((used[g.id] || 0) >= Math.max(1, num(g.max) || 1)) continue;
          var opts = arr(g.options);
          for (var oi = 0; oi < opts.length && !hit; oi++) {
            var o = opts[oi];
            if (o.productId !== it.productId) continue;
            if (it.sizeName && o.sizeName && norm(o.sizeName) !== norm(it.sizeName)) continue;
            // A 'choice' option is re-picked at the order's size (by name), or not at all.
            var at = isChoice(o) ? atSize(o, arr(o.sizes).filter(function (z) { return it.sizeName && norm(z.name) === norm(it.sizeName); })[0]) : o;
            if (!at) continue;
            if (o.soldOut) why = why || 'soldOut';
            else if (o.available === false || (at !== o && sizeOf(o, at.sizeId).size.available === false)) why = why || 'unavailable';
            else hit = { g: g, o: at };
          }
        }
        if (!hit) return { reason: why || 'changed' };
        var sel = selFromOrder(OO.modifiers.visibleGroups(hit.o, hit.o.sizeId), mods);
        if (!sel) return { reason: 'changed' };
        used[hit.g.id] = (used[hit.g.id] || 0) + 1;
        picks.push({ groupId: hit.g.id, mappingId: hit.o.mappingId, option: hit.o, mods: sel });
      }
    }
    var short = groups.some(function (g) { return arr(g.options).length && (used[g.id] || 0) < num(g.min); });
    if (!picks.length || short) return { reason: 'changed' };
    return { line: comboLine(c, picks, { qty: l.qty }) };
  }

  function fromOrder(order, menu, opts) { // opts.now: reserved (see the header)
    var out = { lines: [], skipped: [] };
    if (!order || !menu) return out;
    var cats = {};
    arr(menu.categories).forEach(function (c) { cats[String(c.id)] = c; });
    arr(order.lines).forEach(function (l) {
      if (!l || l.automaticFee || (l.kind !== 'combo' && l.reward)) return; // a redeemed reward isn't a purchasable item
      var r;
      try { r = l.kind === 'combo' ? reorderCombo(l, menu) : reorderItem(l, menu, cats); } catch (e) { r = { reason: 'changed' }; }
      if (r.line) out.lines.push(r.line);
      else out.skipped.push({ name: String(l.name || ''), reason: r.reason });
    });
    return out;
  }

  // ---- line issues + reprice (see the header) ------------------------------------------------------------
  function issue(code, text) { return { code: code, text: text }; }
  function offText(opts, name) {
    var when = opts && opts.at ? ' at ' + opts.at : ' right now';
    return name ? name + ' isn’t available' + when : 'Not available' + when;
  }
  // The option rows behind a line's modifiers within `groups` (visible groups), or null when one is gone.
  function modOptions(groups, mods) {
    var out = [];
    var list = arr(mods);
    for (var i = 0; i < list.length; i++) {
      var id = num(list[i].addOnId);
      var hit = null;
      for (var gi = 0; gi < groups.length && !hit; gi++) {
        var opts = arr(groups[gi].options);
        for (var oi = 0; oi < opts.length; oi++) if (opts[oi].id === id) { hit = { group: groups[gi], option: opts[oi] }; break; }
      }
      if (!hit) return null;
      out.push(hit);
    }
    return out;
  }
  function selOfLineMods(mods) {
    var sel = {};
    arr(mods).forEach(function (m) {
      for (var k = 0; k < Math.max(1, num(m.count) || 1); k++) sel[m.groupId] = (sel[m.groupId] || []).concat([num(m.addOnId)]);
    });
    return sel;
  }
  // The line's size on the product: a line without a size is still fine (the server then checks the base price).
  function sizeOf(product, sizeId) {
    if (sizeId == null) return { ok: true, size: null };
    var size = arr(product.sizes).filter(function (z) { return z.id === sizeId; })[0] || null;
    return { ok: !!size, size: size };
  }
  function itemProblem(line, menu, cats, opts) {
    var M = OO.modifiers;
    if (line.reward || line.free) { menu = OO.model.redemptionMenu(menu); cats = categoriesOf(menu); }
    var p = menu.products ? menu.products[line.itemId] : null;
    if (!p) return issue('missing', 'No longer available');
    if (p.soldOut) return issue('soldOut', 'Sold out');
    var cat = p.categoryId != null ? cats[String(p.categoryId)] : null;
    if (p.available === false || (cat && cat.available === false)) return issue('unavailable', offText(opts));
    var sz = sizeOf(p, line.sizeId);
    if (!sz.ok) return issue('changed', 'Options have changed');
    var groups = M.visibleGroups(p, line.sizeId == null ? null : line.sizeId);
    if (!modOptions(groups, line.modifiers) || !M.validate(groups, selOfLineMods(line.modifiers)).ok) return issue('changed', 'Options have changed');
    // A reward line's product must still be a loyalty product: the validator refuses another (ecode 701).
    if (isReward(line) && p.isLoyalty !== true) return issue('missing', 'No longer available as a reward');
    return null;
  }
  function pickOption(combo, pick) {
    var g = arr(combo.groups).filter(function (x) { return x.id === num(pick.groupId); })[0];
    return g ? arr(g.options).filter(function (o) { return o.mappingId === num(pick.mappingId); })[0] || null : null;
  }
  // 'choice' sizes (see the header): the option a stored pick was ordered as.
  function isChoice(o) { return !!o && o.sizeMode === 'choice' && arr(o.sizes).length > 0; }
  function atSize(o, z) { return z ? Object.assign({}, o, { sizeId: z.id, sizeName: z.name, price: num(z.price) }) : null; }
  // The pick's option on `combo` at the pick's size: a 'choice' option at the pick's sizeId (null when that size is
  // gone, or the pick has none); any other option as it is (its own fixed size).
  function pickAt(combo, pick) {
    var o = pickOption(combo, pick);
    return isChoice(o) ? atSize(o, sizeOf(o, pick.sizeId == null ? null : num(pick.sizeId)).size) : o;
  }
  function comboProblem(line, menu, opts) {
    var M = OO.modifiers;
    var c = menu.combos ? menu.combos[line.comboId] : null;
    if (!c) return issue('missing', 'No longer available');
    var picks = arr(line.picks);
    for (var i = 0; i < picks.length; i++) {
      var o = pickOption(c, picks[i]);
      if (!o) return issue('changed', 'Options have changed');
      if (o.soldOut) return issue('soldOut', (picks[i].name || o.name) + ' is sold out');
      if (o.available === false) return issue('unavailable', offText(opts, picks[i].name || o.name));
      var at = pickAt(c, picks[i]);
      if (!at) return issue('changed', 'Options have changed'); // its chosen size is gone
      if (at !== o && sizeOf(o, at.sizeId).size.available === false) return issue('unavailable', offText(opts, picks[i].name || o.name));
      if (!modOptions(M.visibleGroups(at, at.sizeId), picks[i].modifiers)) return issue('changed', 'Options have changed');
    }
    if (c.available === false) return issue('unavailable', offText(opts));
    return null;
  }
  function categoriesOf(menu) {
    var cats = {};
    arr(menu && menu.categories).forEach(function (c) { cats[String(c.id)] = c; });
    return cats;
  }
  function lineIssue(line, menu, opts) {
    if (!line || !menu) return null;
    try {
      return line.kind === 'combo' ? comboProblem(line, menu, opts) : itemProblem(line, menu, categoriesOf(menu), opts);
    } catch (e) { return issue('changed', 'Options have changed'); }
  }
  function issues(lines, menu, opts) {
    var out = {};
    if (!menu) return out;
    var cats = categoriesOf(menu);
    arr(lines).forEach(function (l) {
      var why;
      try { why = l.kind === 'combo' ? comboProblem(l, menu, opts) : itemProblem(l, menu, cats, opts); } catch (e) { why = issue('changed', 'Options have changed'); }
      if (why) out[l.key] = why;
    });
    return out;
  }

  function sameMods(a, b) {
    return a.length === b.length && a.every(function (m, i) { return m.price === b[i].price && m.name === b[i].name && num(m.free) === num(b[i].free); });
  }
  // The line's rows with today's names and prices and `free` re-allocated from today's Group.freeCount, in the line's
  // row order (never re-sorted: that order is the payload's, which the server allocates in); null when one is gone.
  function repriceMods(groups, mods) {
    var found = modOptions(groups, mods);
    if (!found) return null;
    var rows = arr(mods).map(function (m, i) { return Object.assign({}, m, { name: found[i].option.name, price: num(found[i].option.price) }); });
    return OO.modifiers.allocateFree(rows, OO.modifiers.freeByGroupOf(groups));
  }
  function repriceItem(l, menu) {
    if (l.reward || l.free) menu = OO.model.redemptionMenu(menu);
    var p = menu.products ? menu.products[l.itemId] : null;
    if (!p) return l;
    var sz = sizeOf(p, l.sizeId);
    if (!sz.ok) return l;
    var mods = repriceMods(OO.modifiers.visibleGroups(p, l.sizeId == null ? null : l.sizeId), l.modifiers);
    if (!mods) return l;
    var base = sz.size ? num(sz.size.price) : num(p.price);
    var unit = r2(base + modsTotal(mods));
    if (unit === l.unitPrice && base === l.basePrice && sameMods(mods, arr(l.modifiers)) && p.name === l.name) return l;
    return Object.assign({}, l, { name: p.name, basePrice: base, modifiers: mods, unitPrice: unit, sizeName: sz.size ? sz.size.name : l.sizeName });
  }
  function repriceCombo(l, menu) {
    var c = menu.combos ? menu.combos[l.comboId] : null;
    if (!c) return l;
    var picks = [];
    var src = arr(l.picks);
    for (var i = 0; i < src.length; i++) {
      var o = pickAt(c, src[i]); // a 'choice' pick: its size's price (the payload's child price) and groups
      if (!o) return l;
      var mods = repriceMods(OO.modifiers.visibleGroups(o, o.sizeId), src[i].modifiers);
      if (!mods) return l;
      picks.push(Object.assign({}, src[i], { name: o.name, price: num(o.price), extraPrice: num(o.extraPrice), modifiers: mods }));
    }
    var unit = r2(num(c.price) + picks.reduce(function (t, x) { return t + num(x.count) * (x.extraPrice + modsTotal(x.modifiers)); }, 0));
    var same = unit === l.unitPrice && num(c.price) === l.comboPrice && c.name === l.name && picks.every(function (x, k) {
      return x.price === src[k].price && x.extraPrice === src[k].extraPrice && x.name === src[k].name && sameMods(x.modifiers, arr(src[k].modifiers));
    });
    return same ? l : Object.assign({}, l, { name: c.name, comboPrice: num(c.price), picks: picks, unitPrice: unit });
  }
  function reprice(lines, menu) {
    var list = arr(lines);
    if (!menu) return { lines: list, changed: [] };
    var changed = [];
    var next = list.map(function (l) {
      var n;
      try { n = l.kind === 'combo' ? repriceCombo(l, menu) : repriceItem(l, menu); } catch (e) { n = l; }
      if (n !== l && n.unitPrice !== l.unitPrice) changed.push(l.key);
      return n;
    });
    var same = next.every(function (l, i) { return l === list[i]; });
    return { lines: same ? list : next, changed: changed };
  }

  // ---- atMoment (W5-FIX #1; see the header) --------------------------------------------------------------------------
  // A combo's options with `at`'s flags: each option (by group id and mapping id) through OO.modifiers.withAvailability.
  function comboAt(c, at) {
    var moved = false;
    var groups = arr(c.groups).map(function (g) {
      var ga = arr(at.groups).filter(function (x) { return x && x.id === g.id; })[0];
      if (!ga) return g;
      var changed = false;
      var options = arr(g.options).map(function (o) {
        var oa = arr(ga.options).filter(function (x) { return x && x.mappingId === o.mappingId; })[0];
        var next = oa ? OO.modifiers.withAvailability(o, oa) : o;
        if (next !== o) changed = true;
        return next;
      });
      if (!changed) return g;
      moved = true;
      return Object.assign({}, g, { options: options });
    });
    return moved ? Object.assign({}, c, { groups: groups }) : c;
  }
  function atMoment(menu, at, lines) {
    if (!menu || !at || at === menu) return menu;
    var products = null, combos = null, redemptions = null;
    arr(lines).forEach(function (l) {
      if (!l || typeof l !== 'object') return;
      if (l.kind === 'combo') {
        var c = menu.combos ? menu.combos[l.comboId] : null;
        var ca = at.combos ? at.combos[l.comboId] : null;
        if (!c || !ca || (combos && combos[l.comboId] !== c)) return; // gone, or already judged
        var nc = comboAt(c, ca);
        if (nc !== c) { combos = combos || Object.assign({}, menu.combos); combos[l.comboId] = nc; }
        return;
      }
      var reward = (l.reward || l.free) && menu.redemptionProducts && at.redemptionProducts;
      var source = reward ? menu.redemptionProducts : menu.products;
      var target = reward ? at.redemptionProducts : at.products;
      var moved = reward ? redemptions : products;
      var p = source ? source[l.itemId] : null;
      var pa = target ? target[l.itemId] : null;
      if (!p || !pa || (moved && moved[l.itemId] !== p)) return;
      var np = OO.modifiers.withAvailability(p, pa);
      if (np !== p) {
        if (reward) { redemptions = redemptions || Object.assign({}, source); redemptions[l.itemId] = np; }
        else { products = products || Object.assign({}, source); products[l.itemId] = np; }
      }
    });
    if (!products && !combos && !redemptions) return menu;
    return Object.assign({}, menu, { products: products || menu.products, combos: combos || menu.combos },
      redemptions ? { redemptionProducts: redemptions } : {});
  }

  OO.cart = OO.lib.cart = {
    itemLine: itemLine,
    comboLine: comboLine,
    rewardLine: rewardLine,
    lineKey: lineKey,
    add: add,
    setQty: setQty,
    inc: inc,
    dec: dec,
    remove: remove,
    replace: replace,
    count: count,
    subtotal: subtotal,
    lineTotal: lineTotal,
    displayMods: displayMods,
    sanitize: sanitize,
    fromOrder: fromOrder,
    lineIssue: lineIssue,
    issues: issues,
    reprice: reprice,
    atMoment: atMoment, // W5-FIX #1
    __setUid: function (fn) { uidGen = typeof fn === 'function' ? fn : uidGen; },
  };
})();


// ===================== src/lib/pricing.jsx =====================
// src/lib/pricing.jsx: OO.pricing (alias OO.lib.pricing): coupons, the summary, the line-total and contributor hooks;
// payload.jsx adds checkoutPayload.
//
// OO.pricing:
//   couponEligibility(coupon, { subtotal, orderType, lines, currency, paymentMethod, store }) → { ok, reason }: the
//       coupon engine's OO.coupons.eligibility (coupons.jsx, P2-09; its header lists every gate and reason) less its
//       `code`. In short: Next's gates (§4.3); the types the payload can't carry (unknown types, a FIXED ITEM_DISCOUNT)
//       are { ok: false, reason: 'Not available online yet' }, so an applied coupon the payload can't carry never looks
//       applied; an ITEM_DISCOUNT matches only the item lines its rules.APPLICABLE_ITEMS scope names, sizes included
//       (P2-09); a free-item flow (BUY_ONE_GET_ONE_FREE, BUY_N_GET_M_FREE, ITEM_DISCOUNT at 100%; online since P2-11)
//       is judged by its builder too (OO.coupons.freeLines: no eligible line, too few qualifying units, a choice still
//       to make: 'Not available online yet' while its picker is offline); FREE_DELIVERY is online-only (the owner,
//       2026-10-02: 'Free delivery applies when you pay online.' unless paymentMethod === 'ONLINE_PAYMENT'; a missing
//       method counts as not online, as for the surcharge and in the payload); with `store`, a store whose coupons are
//       switched off (store.couponsEnabled false) refuses every coupon.
//   couponDiscount(lines, coupon, { orderType, deliveryFee, currency, paymentMethod, store }) →
//       { amount, deliveryDiscount, ok, reason, pending }: OO.coupons.discount less its `code`, over the subtotal
//       Σ lineTotal(line, store) (below; the cart's own lines: free lines never count towards a minimum).
//       CART_DISCOUNT: subtotal × DISCOUNT%, or DISCOUNT dollars when it is over 100 (REF reads it as an AMOUNT;
//       P2-09); capped at MAX_DISCOUNT_VALUE when > 0, never above the subtotal.
//       ITEM_DISCOUNT (<100%): Σ eligible lines' basePrice × qty × DISCOUNT%, capped: the product part only, exactly
//       the validator's add-back (item_wise_discount_by_coupon). Modifiers are excluded, as in Next; at a store with
//       applyDiscountToModifiers the summary nets them (below), the amount stays the product part.
//       Free-item flows (P2-10): the free units' product part (BOGO / BUY_N clones and rewards at 100 %, the ITEM
//       100 % unit at its capped %), the validator's add-back too; their modifiers stay payable (REF) except where the
//       summary nets them (applyDiscountToModifiers, below).
//       FREE_DELIVERY (DELIVERY + ONLINE_PAYMENT only; B §3.8, D-28 a): deliveryDiscount = cap > 0 ? min(fee, cap) :
//       fee, cap = MAX_DISCOUNT_VALUE. DISCOUNT is ignored (it was read as an amount: the "$100 sentinel", bug 6);
//       amount stays 0.
//       pending: a free-item coupon waiting for the customer's choice (code PICK; ok false, nothing applied).
//   summary(lines, store, { paymentMethod, coupon, orderType, deliveryFee, walletAmount, table, now }) →
//       { subtotal, displaySubtotal, delivery, deliveryDiscount, discount, membershipItem, membershipCart, lineSaving,
//         wallet, surcharge, surcharges:[{ type, value, amount }], tax, taxInclusive, taxRate, taxLabel, total,
//         coupon:{ ok, reason, pending }, rows:[{ key, label, value, text }] }                 (B §3.2; every step round2)
//       subtotal = Σ lineTotal(line, store), gross (prices include tax), over the lines as charged when a coupon
//       applies (couponWire, below): an applied free-item coupon's free lines (BOGO, BUY_N, a giveaway) count at full
//       price, as on the wire and in REF, and its amount takes their product part off. At a store with
//       applyDiscountToModifiers, less each discounted line's charged modifiers × its coupon % (couponWire's percents:
//       an ITEM_DISCOUNT's eligible lines, a free line, the ITEM 100 % unit). delivery = deliveryFee for DELIVERY
//       without a table, else 0. The coupon is checked against the cart's own subtotal before all that, with
//       opts.paymentMethod (FREE_DELIVERY). discount = the coupon amount; deliveryDiscount = the FREE_DELIVERY waiver.
//       membershipItem,
//       membershipCart, lineSaving, wallet = Σ over the contributors (below); wallet also adds opts.walletAmount
//       (compatibility). itemOff = membershipItem + lineSaving: the line-level savings the server nets from the rows.
//       Tax (bug 1, B §3.3): the INCLUDED tax x·r/(100+r), x = max(0, subtotal − itemOff), r = store.taxRate
//            (default 15; AU 10% → ×10/110; NZ → 3/23). NEVER added to the total, inclusive or exclusive: a
//            tax-exclusive store (taxInclusive false) only changes the label ('Tax (GST Exclusive)', REF's wording)
//            and shows the Sub-total net, displaySubtotal = subtotal − includedTax(subtotal) (= subtotal when inclusive).
//       Surcharge (B §3.4): only for paymentMethod 'ONLINE_PAYMENT', and not when the wallet pays it all ("full wallet":
//            wallet > 0 and wallet ≥ the base before the wallet). Configs: store.surcharges [{ type 'fixed'|'percentage',
//            value, rules }] (the enabled ones; an empty list means none), else derived from store.surcharge
//            { enabled, amount, percent }: none unless enabled, then fixed if amount > 0 and percentage if percent > 0,
//            rules []. A config applies when OO.model.isAvailable(rules, OO.model.storeNow(store, opts.now), domain) passes
//            (domain = OO.data.domain(orderType), else OO.model.domain(orderType)). The type matches in any case;
//            other types, values ≤ 0 and enabled false|0 are skipped. Each is rounded on its own: percentage →
//            round2(base × value/100), fixed → round2(value); surcharge = Σ; surcharges = the applied ones.
//            base = max(0, subtotal + delivery − discount − deliveryDiscount − itemOff − wallet).
//       total (= payable) = max(0, subtotal + delivery + surcharge − (discount + deliveryDiscount + itemOff +
//            membershipCart + wallet)).
//       rows: Sub-total (displaySubtotal), Delivery (DELIVERY only: the fee after the waiver), Surcharge, Tax (unless
//            store.hideTaxLabel), Discount (coupon + wallet), Total; then the contributed rows are placed (below).
//   lineTotal(line, store) → the line's total for the subtotal: the function set by setLineTotal (called with
//       (line, store)), else the default: OO.cart.lineTotal(line) (looked up on every call, so cart.jsx changes reach
//       it), and at a store with applyDiscountToModifiers a combo line nets its children's modifiers (below):
//       round2(OO.cart.lineTotal(line) − Σ units × child modifiers × the child's spread %). The 1-argument call
//       lineTotal(line) (no store) is the plain OO.cart.lineTotal(line).
//   setLineTotal(fn | null) → swaps the ONE line-total function (fn(line, store) → number; e.g. a server quote, D-08
//       prep; a 1-argument fn still works); null (or any non-function) restores the default.
//   contribute(key, fn | null) → registers a summary contributor under key (an existing key is replaced in place) or,
//       with null, removes it, so the P2 engines (membership, wallet, …) never edit this file. The contract:
//       - summary calls each fn({ lines, store, opts, subtotal, orderType }) in registration order (opts as given to
//         summary; subtotal = the summary's subtotal, above). It returns null or
//         { membershipItem?, membershipCart?, lineSaving?, wallet?, rows?: [{ key, label, value, text, after? }] }.
//       - The amounts are savings: a negative or non-numeric one counts as 0. Each is summed across contributors,
//         rounded and used above: membershipItem and lineSaving lower the tax base, the surcharge base and the total;
//         membershipCart lowers the total; wallet lowers the surcharge base and the total.
//       - lineSaving (W5-FIX #5) is the neutral field for a line-level saving that is no membership saving: one the
//         server takes off the payload's rows themselves, so it also comes off the subtotal sent (payload.jsx), e.g.
//         the wallet's, whose row % nets the line's charged modifiers at an applyDiscountToModifiers store.
//       - Only the wallet is in a built-in row (Discount). A contributor that returns a membership amount or a
//         lineSaving must add its own row, or the visible rows won't add up to the total.
//       - A row goes right after the row whose key equals `after` (after the rows already placed there); without
//         `after`, or when no such row is present (e.g. 'delivery' on a pickup order, 'tax' while hidden), just before
//         'total'. The output row is a copy without `after`; text defaults to money(value); a row without a string
//         key is ignored.
//       - A contributor that throws is skipped (its amounts and rows alike) with one console.warn per registration;
//         it never breaks the summary.
//       - At a store with applyDiscountToModifiers, a line discount % the payload sends (e.g. a membership item
//         saving) also nets that line's charged modifiers on the server: a contributor's saving must include them.
//
// applyDiscountToModifiers (store.applyDiscountToModifiers, getRestaurant's B13 apply_discount_to_modifiers: the same
// GlobalPreference the validator reads). With it on, OnlineOrderValidationController prices every payload row's
// modifiers with ModifierPricingService::allocate at the row's `discount` %, clamped to 0–100: each row's CHARGED units
// (count − free, free allocated as the cart does) × price × (1 − rate / 100). It adds back only the PRODUCT part of an
// item coupon, so the modifier part comes off its computed total S (ecode 300 if our `total` doesn't follow). Mirrored:
//   combo line      each child's modifiers × the child's spread % (comboSpread: the % the payload sends), in the
//                   default lineTotal(line, store);
//   item coupons    each discounted line's modifiers × its coupon % (couponWire's percents: the payload's per-line %,
//                   so a capped coupon's scaled %; a BOGO clone or a BUY_N reward at 100 %, so its modifiers become
//                   free there), in the summary's subtotal; the coupon amount stays the product part.
// Off (every Batch-1 fixture store), nothing here changes a figure.
//
// OO.pricing._ (internal: shared with payload.jsx only; pages never read it), so the payload sends exactly the figures
// priced here and there is ONE copy of each:
//   eligibleLines(lines, coupon) → the ITEM_DISCOUNT coupon's item lines: OO.coupons.eligibleLines (kind 'item', the
//       itemId in the coupon's scope and, when that scope entry lists sizes, the line's sizeId among them).
//   itemDiscountPercents(lines, coupon) → { [line.key]: % } for an ITEM_DISCOUNT (<100%: the couponEligibility rule)
//       coupon's eligible lines: OO.coupons.itemPercents, the coupon %, scaled down by the cap's share when it applies
//       (Next calculateItemDiscount); {} otherwise.
//   couponWire(lines, coupon, store) → { lines, percents: { [line.key]: % } }: OO.coupons.wire (P2-10), the lines as
//       charged: for an applied free-item coupon the cart's lines with the ITEM 100 % unit split off its line, then
//       the free lines; else the cart's lines with itemDiscountPercents. The summary prices them; the payload sends
//       them.
// The coupon functions above look OO.coupons up at call time: coupons.jsx loads after this file.
//   comboSpread(line) → { actual, actualExtra, totalDisc, picks:[{ factor, factorValue, pct }] } (picks in line.picks
//       order): actual = Σ pick price × count, actualExtra = Σ extraPrice × count, totalDisc = actual − (comboPrice +
//       actualExtra) (the combo saving, V4); each pick's price share factor (%), its share of the saving and the
//       discount % it carries (0 for a pick priced 0), as the payload sends them (dicountFactor, dicountFactorValue,
//       discount).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function F() { return OO.format; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function r2(n) { return F().round2(n); }
  function saving(v) { return Math.max(0, num(v)); }

  // ---- line discounts the payload sends (shared with payload.jsx: OO.pricing._) ---------------------
  // The coupon's lines and per-line % are the coupon engine's (OO.coupons, coupons.jsx: size scopes, P2-09), looked up
  // at call time: coupons.jsx loads after this file. None for a coupon the payload can't honour (notOnlineYet), so no
  // row ever carries its %.
  function C() { return OO.coupons; }
  function eligibleLines(lines, coupon) { return C().eligibleLines(lines, coupon); }
  function itemDiscountPercents(lines, coupon) { return C().itemPercents(lines, coupon); }
  function couponWire(lines, coupon, store) { return C().wire(lines, coupon, { store: store }); }

  // A combo line's children carry their product prices (V4), so the combo saving is spread over them by price share:
  // the validator's nets then add up to combo price + extras. The figures every combo unit's rows carry.
  function comboSpread(line) {
    var picks = arr(line && line.picks);
    var actual = picks.reduce(function (t, p) { return t + num(p.price) * num(p.count); }, 0);
    var actualExtra = picks.reduce(function (t, p) { return t + num(p.extraPrice) * num(p.count); }, 0);
    var totalDisc = actual - (num(line && line.comboPrice) + actualExtra);
    return {
      actual: actual, actualExtra: actualExtra, totalDisc: totalDisc,
      picks: picks.map(function (p) {
        var factor = actual > 0 ? num(p.price) * num(p.count) * 100 / actual : 0;
        var factorValue = totalDisc * factor / 100;
        var pct = num(p.price) > 0 && num(p.count) > 0 ? factorValue / num(p.price) / num(p.count) * 100 : 0;
        return { factor: factor, factorValue: factorValue, pct: pct };
      }),
    };
  }

  // ---- applyDiscountToModifiers (see the header) --------------------------------------------------
  function discountsModifiers(store) { return !!store && store.applyDiscountToModifiers === true; }

  // What the validator takes off a row list's modifiers at `pct` (ModifierPricingService::allocate, rate clamped to
  // 0–100): Σ price × charged units × rate / 100, charged = (count − free) per unit (cart.jsx's units) × `units` (the
  // item qty; a combo child's count per combo unit).
  function modsDiscount(mods, units, pct) {
    var rate = Math.min(100, Math.max(0, num(pct)));
    if (!rate) return 0;
    return arr(mods).reduce(function (t, m) { return t + num(m.price) * Math.max(0, num(m.count) - num(m.free)) * units * rate / 100; }, 0);
  }

  // A combo line's modifier discount: every unit (one comboBatch each) nets each child's modifiers by its spread %.
  function comboModsDiscount(line) {
    var spread = comboSpread(line);
    var unit = arr(line.picks).reduce(function (t, p, i) { return t + modsDiscount(p.modifiers, num(p.count), spread.picks[i].pct); }, 0);
    return unit * num(line.qty);
  }

  // An applied coupon's modifier discount over the lines as charged (couponWire): each discounted line's modifiers ×
  // its coupon % (an ITEM_DISCOUNT's capped %, a free line's 100 %, the ITEM 100 % unit's).
  function couponModsDiscount(charged) {
    return charged.lines.reduce(function (t, l) { return t + modsDiscount(l.modifiers, num(l.qty), charged.percents[l.key] || 0); }, 0);
  }

  // ---- line total (D-08 prep) ----------------------------------------------------------------------
  var customLineTotal = null;
  function defaultLineTotal(line, store) {
    var gross = OO.cart.lineTotal(line);
    if (!discountsModifiers(store) || !line || line.kind !== 'combo') return gross;
    return r2(num(gross) - comboModsDiscount(line));
  }
  function lineTotal(line, store) { return customLineTotal ? customLineTotal(line, store) : defaultLineTotal(line, store); }
  function setLineTotal(fn) { customLineTotal = typeof fn === 'function' ? fn : null; }
  function subtotal(lines, store) { return r2(arr(lines).reduce(function (t, l) { return t + num(lineTotal(l, store)); }, 0)); }

  // ---- coupons (OO.coupons, coupons.jsx: the ONE copy; see the header) ------------------------------------
  // The engine's results less their `code` (the shapes callers and tests pin).
  function couponEligibility(coupon, ctx) {
    var e = C().eligibility(coupon, ctx);
    return { ok: e.ok, reason: e.reason };
  }

  function couponDiscount(lines, coupon, ctx) {
    ctx = ctx || {};
    if (!coupon) return { amount: 0, deliveryDiscount: 0, ok: false, reason: null, pending: false };
    var d = C().discount(lines, coupon, Object.assign({}, ctx, { subtotal: subtotal(lines, ctx.store) }));
    return { amount: d.amount, deliveryDiscount: d.deliveryDiscount, ok: d.ok, reason: d.reason, pending: d.pending };
  }

  // ---- surcharge (B §3.4) --------------------------------------------------------------------------
  // store.surcharges (the enabled configs), else REF's fallbacks from the legacy surcharge_* fields.
  function surchargeConfigs(store) {
    if (Array.isArray(store.surcharges)) return store.surcharges;
    var sc = store.surcharge || {};
    var out = [];
    if (!sc.enabled) return out;
    if (num(sc.amount) > 0) out.push({ type: 'fixed', value: num(sc.amount), ...(sc.label ? { label: sc.label } : {}), rules: [] });
    if (num(sc.percent) > 0) out.push({ type: 'percentage', value: num(sc.percent), ...(sc.label ? { label: sc.label } : {}), rules: [] });
    return out;
  }

  function surchargeDomain(orderType) {
    return OO.data && typeof OO.data.domain === 'function' ? OO.data.domain(orderType) : OO.model.domain(orderType);
  }

  // The applied configs → [{ type, value, amount }], each amount rounded on its own. The store clock is only read
  // when a config has rules.
  function appliedSurcharges(store, base, orderType, now) {
    var clock = null;
    var out = [];
    arr(surchargeConfigs(store)).forEach(function (c) {
      if (!c || typeof c !== 'object' || c.enabled === false || c.enabled === 0 || c.enabled === '0') return;
      var type = String(c.type || '').toLowerCase();
      var value = num(c.value);
      if ((type !== 'fixed' && type !== 'percentage') || value <= 0) return;
      if (OO.model.parseRules(c.rules).length) {
        clock = clock || OO.model.storeNow(store, now);
        if (!OO.model.isAvailable(c.rules, clock, surchargeDomain(orderType))) return;
      }
      out.push({ type: type, value: value, ...(c.label ? { label: String(c.label).trim() } : {}), amount: type === 'percentage' ? r2(base * value / 100) : r2(value) });
    });
    return out;
  }

  // Additional Charges use the exact same pure rules as Laravel and offline POS.
  function feeOrderType(type, table, store) {
    if (table) return 'TABLE_SIDE';
    if (OO.mode === 'kiosk') {
      var selected = OO.kiosk && OO.kiosk.orderType(store.onlineKey);
      return (selected || type) === 'DINEIN' ? 'kiosk_dine_in' : 'kiosk_takeout';
    }
    return (OO.mode === 'app' ? 'ONLINE_APP_' : 'ONLINE_') + (type === 'DELIVERY' ? 'DELIVERY' : 'PICKUP');
  }
  function gatewayFeeKey(name, id) { return 'gateway:' + String(name || '').trim().replace(/[\s-]+/g, '_').toUpperCase() + ':' + id; }
  function feePaymentKey(store, opts) {
    if (opts.feePaymentKey !== undefined) return opts.feePaymentKey;
    if (store.orderFeePaymentKey !== undefined) return store.orderFeePaymentKey;
    var pm = arr(store.paymentMethods).filter(function (m) { return m && m.key === opts.paymentMethod; })[0] || {};
    return opts.paymentMethod === 'ONLINE_PAYMENT' ? (pm.gatewayId == null ? '' : gatewayFeeKey(pm.gateway, pm.gatewayId))
      : (pm.typeId == null ? '' : 'type:' + pm.typeId);
  }
  function orderFees(config, orderType, paymentKey, base) {
    base = r2(Math.max(0, num(base)));
    var lines = [];
    arr(config && config.fees).forEach(function (fee) {
      if (!fee || !fee.enabled || fee.enabled === '0') return;
      var rule = arr(fee.rules).filter(function (r) {
        if (r.order_type !== orderType || r.payment_key !== paymentKey) return false;
        var condition = r.condition || 'always';
        if (condition === 'always') return true;
        if (r.threshold == null || !Number.isFinite(Number(r.threshold)) || Number(r.threshold) < 0) return false;
        var threshold = r2(Number(r.threshold));
        return condition === 'below' ? base < threshold : condition === 'above' && base > threshold;
      })[0];
      if (!rule || num(rule.value) <= 0) return;
      var amount = r2(rule.type === 'percentage' ? base * num(rule.value) / 100 : num(rule.value));
      if (amount > 0) lines.push({ fee_id: fee.id, product_id: num(fee.product_id), name: fee.name, amount: amount,
        description: String(fee.description || '').trim(),
        base: base, type: rule.type, value: num(rule.value), order_type: orderType, payment_key: paymentKey, revision: config.revision || 0,
        ...(rule.condition && rule.condition !== 'always' ? {condition: rule.condition, threshold: rule.threshold} : {}) });
    });
    return { lines: lines, total: r2(lines.reduce(function (t, f) { return t + f.amount; }, 0)) };
  }

  // ---- contributors (the P2 engines' hook) ---------------------------------------------------------
  var contributors = []; // [{ key, fn, warned }], in registration order

  function contribute(key, fn) {
    var k = String(key);
    var at = -1;
    contributors.forEach(function (c, i) { if (c.key === k) at = i; });
    if (typeof fn !== 'function') {
      if (at >= 0) contributors.splice(at, 1);
      return;
    }
    var entry = { key: k, fn: fn, warned: false };
    if (at >= 0) contributors[at] = entry; else contributors.push(entry);
  }

  function warnOnce(c, err) {
    if (c.warned) return;
    c.warned = true;
    if (typeof console !== 'undefined' && console && typeof console.warn === 'function') {
      console.warn('OO.pricing: summary contributor "' + c.key + '" threw and was skipped', err);
    }
  }

  // Every contributor's amounts (summed, not yet rounded) and rows; a throwing one adds nothing.
  function contributions(ctx) {
    var out = { membershipItem: 0, membershipCart: 0, lineSaving: 0, wallet: 0, rows: [] };
    contributors.slice().forEach(function (c) {
      var res;
      try { res = c.fn(ctx); } catch (err) { warnOnce(c, err); return; }
      if (!res || typeof res !== 'object') return;
      out.membershipItem += saving(res.membershipItem);
      out.membershipCart += saving(res.membershipCart);
      out.lineSaving += saving(res.lineSaving); // W5-FIX #5: the neutral line-level saving
      out.wallet += saving(res.wallet);
      arr(res.rows).forEach(function (row) {
        if (row && typeof row === 'object' && typeof row.key === 'string' && row.key) out.rows.push(row);
      });
    });
    return out;
  }

  function indexOfKey(rows, key) {
    for (var i = 0; i < rows.length; i++) if (rows[i].key === key) return i;
    return -1;
  }

  // Contributed rows go right after the row keyed `after` (after the rows already placed there), else before 'total'.
  function placeRows(rows, extra, cur) {
    var out = rows.slice();
    var lastAt = {}; // anchor key → the row placed there last
    extra.forEach(function (x) {
      var row = {};
      Object.keys(x).forEach(function (k) { if (k !== 'after') row[k] = x[k]; });
      if (row.text == null) row.text = F().money(row.value, cur);
      var anchor = typeof x.after === 'string' ? x.after : null;
      var at = anchor === null ? -1 : (lastAt[anchor] ? out.indexOf(lastAt[anchor]) : indexOfKey(out, anchor));
      if (at >= 0) {
        out.splice(at + 1, 0, row);
        lastAt[anchor] = row;
      } else {
        var t = indexOfKey(out, 'total');
        out.splice(t >= 0 ? t : out.length, 0, row);
      }
    });
    return out;
  }

  // ---- summary (B §3.2) ----------------------------------------------------------------------------
  function tipsEnabled(store, opts) {
    return !!(store && store.settings && store.settings.tippingEnabled && !(opts && opts.table)
      && OO.mode !== 'kiosk' && OO.mode !== 'qr');
  }
  function validTip(value) {
    return /^\d{1,6}(\.\d{1,2})?$/.test(String(value)) && Number(value) <= 999999.99;
  }
  function tipAmount(store, value, opts) {
    return tipsEnabled(store, opts) && validTip(value) ? r2(value) : 0;
  }
  function summary(lines, store, opts) {
    opts = opts || {};
    store = store || {};
    var f = F();
    var cur = store.currency || '$';
    var orderType = String(opts.orderType || 'PICKUP').toUpperCase();
    var sub = subtotal(lines, store);
    var qrDiscount = OO.orderingQr ? OO.orderingQr.saving(lines, store, sub) : 0;
    var delivery = orderType === 'DELIVERY' && !opts.table ? r2(opts.deliveryFee) : 0;
    var c = couponDiscount(lines, opts.coupon, { orderType: orderType, deliveryFee: delivery, currency: cur, paymentMethod: opts.paymentMethod, store: store });
    // The lines as charged (couponWire, P2-10): an applied free-item coupon's free lines count at full price, as on the
    // wire; its amount takes their product part off.
    var charged = c.ok ? couponWire(lines, opts.coupon, store) : null;
    if (charged && charged.lines !== arr(lines)) sub = subtotal(charged.lines, store);
    // applyDiscountToModifiers: an item coupon's % nets its lines' modifiers too, with no add-back for them, so that
    // part comes off the subtotal (the validator's S); the coupon amount stays the product part.
    if (charged && discountsModifiers(store)) sub = r2(sub - couponModsDiscount(charged));
    var k = contributions({ lines: arr(lines), store: store, opts: opts, subtotal: sub, orderType: orderType });
    var membershipItem = r2(k.membershipItem);
    var membershipCart = r2(k.membershipCart);
    var lineSaving = r2(k.lineSaving);
    // The line-level savings the server nets from the rows (membershipItem + lineSaving; see the header), summed before
    // rounding: every figure below is the one the single membershipItem field gave before W5-FIX #5.
    var itemOff = r2(k.membershipItem + k.lineSaving);
    var wallet = r2(saving(opts.walletAmount) + k.wallet);

    // Surcharge: on the base after every saving but the cart-level membership one; none when the wallet pays it all.
    var beforeWallet = r2(Math.max(0, sub + delivery - c.amount - c.deliveryDiscount - itemOff - qrDiscount));
    var base = r2(Math.max(0, beforeWallet - wallet));
    var fullWallet = wallet > 0 && wallet >= beforeWallet;
    var surcharges = opts.paymentMethod === 'ONLINE_PAYMENT' && !fullWallet ? appliedSurcharges(store, base, orderType, opts.now) : [];
    var surcharge = r2(surcharges.reduce(function (t, s) { return t + s.amount; }, 0));

    var feeKey = feePaymentKey(store, opts);
    var feeType = feeOrderType(orderType, opts.table, store);
    var feeBase = Math.max(0, sub - itemOff - c.amount - membershipCart - qrDiscount);
    var fees = orderFees(store.orderFees, feeType, feeKey, feeBase);
    // A wallet-only order uses its own matrix cell. A partial wallet retains the selected method's fees.
    var walletFees = orderFees(store.orderFees, feeType, 'pos:wallet', feeBase);
    if (wallet > 0 && wallet >= beforeWallet - membershipCart + walletFees.total) {
      feeKey = 'pos:wallet'; fees = walletFees;
    }

    // Tax: always the tax included in the gross prices; never added (bug 1).
    var rate = num(store.taxRate) > 0 ? num(store.taxRate) : 15;
    var inclusive = store.taxInclusive !== false;
    function includedTax(x) { return r2(x * rate / (100 + rate)); }
    var tax = includedTax(r2(Math.max(0, sub - itemOff) + fees.total));
    var displaySubtotal = inclusive ? sub : r2(sub - includedTax(sub));
    var taxLabel = inclusive ? 'Tax (GST inclusive)' : 'Tax (GST Exclusive)';

    var tip = tipAmount(store, opts.tipAmount, opts);
    var total = r2(Math.max(0, sub + delivery + surcharge + fees.total - (c.amount + c.deliveryDiscount + itemOff + membershipCart + wallet + qrDiscount)) + tip);
    var rows = [{ key: 'subtotal', label: 'Sub-total', value: displaySubtotal, text: f.money(displaySubtotal, cur) }];
    if (orderType === 'DELIVERY') rows.push({ key: 'delivery', label: 'Delivery', value: r2(delivery - c.deliveryDiscount), text: f.money(delivery - c.deliveryDiscount, cur) });
    fees.lines.forEach(function (fee) { rows.push({ key: 'fee:' + fee.fee_id, label: fee.name, description: fee.description, value: fee.amount, text: f.money(fee.amount, cur) }); });
    rows.push({ key: 'surcharge', label: Array.from(new Set(surcharges.map(function (s) { return s.label || "Surcharge"; }))).join(' + ') || 'Surcharge', value: surcharge, text: f.money(surcharge, cur) });
    if (!store.hideTaxLabel) rows.push({ key: 'tax', label: taxLabel, value: tax, text: f.money(tax, cur) });
    rows.push({ key: 'discount', label: 'Discount', value: r2(c.amount + wallet), text: f.minusMoney(c.amount + wallet, cur) });
    if (qrDiscount > 0) {
      var qrTier = OO.orderingQr.tier(store.orderingQr.discount, sub);
      rows.push({ key: 'orderingQrDiscount', label: store.orderingQr.name + ' discount' + (qrTier ? ' (' + qrTier.percent + '%)' : ''), value: qrDiscount, text: f.minusMoney(qrDiscount, cur) });
    }
    if (tip > 0) rows.push({ key: 'tip', label: 'Tip', value: tip, text: f.money(tip, cur) });
    rows.push({ key: 'total', label: 'Total', value: total, text: f.money(total, cur) });
    return {
      subtotal: sub, displaySubtotal: displaySubtotal, delivery: delivery, deliveryDiscount: c.deliveryDiscount, discount: c.amount,
      membershipItem: membershipItem, membershipCart: membershipCart, lineSaving: lineSaving, wallet: wallet, surcharge: surcharge,
      surcharges: surcharges,
      ...(tip > 0 ? { tip: tip } : {}),
      ...(store.orderingQr ? { orderingQrDiscount: qrDiscount } : {}),
      ...(store.orderFees ? { orderFees: fees, feePaymentKey: feeKey } : {}),
      tax: tax, taxInclusive: inclusive, taxRate: rate, taxLabel: taxLabel, total: total,
      coupon: { ok: c.ok, reason: c.reason, pending: c.pending }, rows: placeRows(rows, k.rows, cur),
    };
  }

  OO.pricing = OO.lib.pricing = {
    tipsEnabled: tipsEnabled,
    validTip: validTip,
    tipAmount: tipAmount,
    orderFees: orderFees,
    feePaymentKey: feePaymentKey,
    gatewayFeeKey: gatewayFeeKey,
    couponEligibility: couponEligibility,
    couponDiscount: couponDiscount,
    summary: summary,
    lineTotal: lineTotal,
    setLineTotal: setLineTotal,
    contribute: contribute,
    // internal: shared with payload.jsx only; pages never read it (see the header)
    _: { eligibleLines: eligibleLines, itemDiscountPercents: itemDiscountPercents, couponWire: couponWire, comboSpread: comboSpread },
  };
})();


// ===================== src/lib/payload.jsx =====================
// src/lib/payload.jsx: OO.payload (alias OO.lib.payload): the V4 checkout body (POST checkout, B §4.2) and its contributor
// hooks. OO.pricing (made by pricing.jsx) gets checkoutPayload (the same function) and SERVER_SECRET.
//
// OO.payload:
//   checkoutPayload(lines, store, ctx) → the POST checkout body. Money fields are toFixed(2) strings ("str2").
//       It THROWS rather than return a half-built order: Error { kind: 'payload', code } with code
//         PREVIEW (checked first: a white-label studio preview, OO.whitelabel.isPreview(); WL-2) · NO_STORE (no restaurant_key / res_id) · EMPTY_CART · NO_TABLE (P4-07: a table order whose table id isn't a
//         whole number > 0, a digit string too: every POS build decodes table_id as an int, so a decimal fails the
//         order's import) · TABLE_UNSUPPORTED (a table order on a LIVE store, OO.api.isLiveFor, whose lines were not
//         priced from the TABLE menu: ctx.menuType isn't 'Table Menu'. Without the facade every non-fixture store
//         counts as live. The validator prices a table row at table_price (ecode 100 on an online price) and guard
//         Rule L floors a table combo at its table price, so online prices fail or undercharge; get_menu answers a
//         table request with the ONLINE menu and no menu_type while its menu cache is cold. Fixture stores keep table
//         orders) · PRICETAG_DOMAIN (P4-07: an active ctx.pricetag of the wrong channel: a table order's must be a
//         'TABLE SIDE' tag and no other order's is one; the validator re-prices a row from whatever tag it claims, so
//         another channel's tag prices would go through unnoticed) · NO_ZONE (a DELIVERY order without a zone whose
//         id > 0: the validator answers ecode 201) · COMBO_UNPRICEABLE (a combo whose picks are all priced 0: no price
//         share can carry combo_price, a certain ecode 300 plus an error mail) · NO_TIME (a SCHEDULED order without a
//         usable time) · NO_GATEWAY (online payment without a gateway name and a numeric id > 0) · CONTRIBUTOR (a hook
//         threw or returned a non-object) · WALLET (a wallet saving or payment_type 'wallet' without wallet_point_used) ·
//         TOTALS (payable_amount ≠ summary.total, or total ≠ subtotal + deliverycharge + surcharge) · D08 (cart_id or an
//         authoritative-combo key on the wire: D-08 keeps the server off its cart-pricing path) · STALE_TOTAL (checked
//         last: ctx.expectedPayable is given and |round2(it) − payable| > 0.004, or it isn't a number; the error
//         carries `payable`, the amount the order would charge now: a surcharge rule or a price can change between
//         render and Pay, and an order is never sent at a total the customer didn't see).
//       ctx = { orderType 'PICKUP'|'DELIVERY', slot (an OO.model.pickupOptions slot) | deliveryType 'ASAP'|'SCHEDULED'
//         + deliverTime (ISO | Date | ms), paymentMethod 'ONLINE_PAYMENT'|'PAY_AT_COUNTER'|'CASH', contact:{ name,
//         phone, email }, customer (OO.model Customer), customerId (the store-scoped cust_… from the wallet call,
//         get_customer_wallet_points — never customer/getdata's), dob (the VERIFIED date of birth 'YYYY-MM-DD'; the age
//         gate passes it only once verified), notes, coupon, marketing (the customer's opt-in), table ({ id, name }:
//         a table order), guestCount and menuType (P4-07: "Table orders" below), isMobileApp, deliveryFee,
//         deliveryZone (a store zone { id, name, fee, minimumOrder, radiusKm } or a raw deliveryzones row),
//         address:{ line, zipcode, lat, lng }, drivingTime (minutes or text), expectedPayable (number: the total the
//         Pay button showed; null / undefined skips the check), walletAmount, browserId, now (Date | ms | ISO; default
//         OO.data.now(), the app clock), uuid() }
//       Every amount comes from OO.pricing.summary(lines, store, { paymentMethod, coupon, orderType, deliveryFee, table })
//       (+ walletAmount / now when ctx has them): the call the cart and checkout pages make, so the charged amount is
//       the displayed total. The summary is allowance-aware through the lines (OO.pricing.lineTotal(line, store)) and
//       follows the store's applyDiscountToModifiers.
//   contribute(key, spec | null) → registers payload hooks under key (an existing key is replaced in place), so the P2
//       engines (pricetags, membership, wallet, …) never edit this file; null, or a spec with neither hook, removes it.
//       spec = { line?: fn(row, line, ctx) → fields merged into that data[] row,
//                root?: fn(body, ctx) → fields merged into the body }
//       - They run after the core fields: first every line hook (contributors in registration order, each over the
//         rows in order; `line` is the row's cart line, the combo line for each of its child rows), then every root
//         hook in registration order, so a root hook sees the final rows. Hooks may also edit row / body in place.
//       - ctx = the checkoutPayload ctx plus { store, lines, summary (the summary above), orderType, now (a Date) }.
//       - null / undefined / false = nothing to merge. A hook that throws, or returns anything else that isn't a
//         plain object, aborts the payload (Error code CONTRIBUTOR, the cause attached).
//       - The guards above run on the final body: a contributor may set fields (e.g. wallet_point_used,
//         payment_type 'wallet', membership_name, a line's discount), but payable_amount must stay summary.total.
//   e164(phone, store) → the phone in E.164, never with the country code twice (G1 fix D): a '(0)' trunk marker, then
//       spaces, '-', '(', ')' and '.' are dropped; a leading '00' becomes '+'; a '+' number is kept, less a trunk 0
//       right after +64 / +61 / +886 ('+64 021…' → '+6421…'); a number that already starts with its country code but
//       no '+' (642 + 7–9 digits, 614 + 8, 8869 + 8: NZ / AU / TW mobiles, whatever the store's country) gets the '+';
//       otherwise a leading 0 becomes the dial code and anything else gets the dial code prepended; '' stays ''. Dial
//       code (B §4.2): store.address.country, else store.currencyCode, else OO.boot.region: NZ +64, AU +61, TW +886
//       (default +64).
// OO.pricing.checkoutPayload = OO.payload.checkoutPayload. OO.pricing.SERVER_SECRET = '' (below).
//
// The body (B §4.2; REF develop's wire unless a BATCH2-PLAN §9 decision says otherwise):
//   version 'V4' (D-26: the validator prices combo children at sale_price) · res_id store.id (number) · restaurant_key ·
//   currency (lower case) · server_secret '' (always sent, see SERVER_SECRET) · isMobileApp (ctx.isMobileApp; true in the
//   app shell) · browserId · fcmToken '' (the shell's token: OO.platform's contributor, P4-09) · kiosk_id null · notes
//   order_type      a table order 'dining'; app mode 'APP_PICKUP' | 'APP_DELIVERY'; else 'PICKUP' | 'DELIVERY'
//   delivery_type   'ASAP' | 'SCHEDULED' (a table order is always ASAP)
//   deliver_time    ASAP: now + max(buffer, 2) min as ISO, buffer = store.deliveryTimeMins for a DELIVERY order, else
//                   store.cookingTimeMins (the validator rejects a present ASAP time that isn't in the future: ecode
//                   103). SCHEDULED: ctx.deliverTime, else the slot's store-tz deliveryTime, as ISO (UTC).
//   customer_name (trimmed; a table order without one: the table name, else 'Guest') · customer_email (no spaces) ·
//   customer_phone (e164) · customer_dob (ONLY with ctx.dob) · customer_id · fuid = userUniqueId (customer.uid) ·
//   customer_address / customer_zipcode (ctx.address; '' at a table)
//   marketing_consent  true ONLY when ctx.marketing === true; otherwise the key is OMITTED, not a withdrawal. The checked
//                   state may come from a preselected default or a customer action; the wire value does not distinguish
//                   them. The server records consent whenever the key is set (isset(false) is true). The POS order
//                   model has no marketing field.
//   table_id (a whole number; null without a table) · table_name (the trimmed name, else null) · is_tableside ·
//   guest null · guest_count (ctx.guestCount's whole part, 1 to GUESTS_MAX, else 1; "Table orders" below)
//   payment_type    'wallet' (wallet used, nothing left to pay) | 'ONLINE_ORDER_CARD' | 'Pay at Counter'. A $0
//                   ONLINE_PAYMENT order (payable_amount '0.00', not wallet-paid) is sent as a COUNTER order: a $0
//                   gateway session fails after the server has already claimed the coupon and created the order, and
//                   the POS auto-pays a counter order whose payable is 0. The caller reads body.payment_type and takes
//                   the counter path (no payment_url, nothing to confirm).
//   payment_gateway  online: the method's gateway name; wallet: 'wallet'; counter: the method's gateway, else 'Pay at
//                   Counter'; a $0 online order: 'Pay at Counter'
//   payment_gateway_id = terminal_id  online: the NUMERIC gateway id (> 0); wallet / counter: null (the fixture
//                   stores carry numeric ids too since P1-17).
//   payment_status 'UNPAID' · delivery_by 'Inhouse' · uber_order false · tip fixed money (null when none)
//   delivery_driving_time  ALWAYS a string (the POS model is String?; a number breaks its decode of the whole order):
//                   ctx.drivingTime as text (12 → '12'; '12 mins' as is), else '0' (REF); '0' at a table
//   deliverycharge  str2: the FULL fee (a FREE_DELIVERY waiver lowers payable_amount only)
//   deliveryzone    { id, name, delivery_fee, minimum_amount, zone_area } on a delivery order (required: NO_ZONE),
//                   else null
//   delivery_lat / delivery_lng  ctx.address.lat / lng on a delivery order (B10 re-checks them), else null
//   subtotal        str2: summary.subtotal − (summary.membershipItem + summary.lineSaving) (allowance-aware, net of the
//                   membership item saving, B §3.9, and of the other line-level savings: lineSaving, W5-FIX #5, the
//                   wallet's modifier part; at a store with applyDiscountToModifiers also net of the modifier discount
//                   the validator takes: combo children × their spread %, an item coupon's discounted rows × their %,
//                   OO.pricing's header; free-item lines at full price, P2-11). Never re-derived from the rows: their
//                   modifier counts include the free units.
//   total           str2: subtotal + delivery + surcharge. No tax, no discounts: the validator's figure (ecode 300).
//   payable_amount  str2: max(0, total − coupon − free-delivery waiver − membershipCart − wallet).
//                   payable_amount + tip = summary.total, including when points cover all items.
//   tax             str2 (B §3.3 payload rule): taxable = max(0, subtotal − the coupon when it is item-level:
//                   ITEM_DISCOUNT, BUY_ONE_GET_ONE_FREE, BUY_N_GET_M_FREE, REF isItemLevelCoupon); tax-inclusive ×
//                   r/(100 + r), exclusive × r/100 (r = summary.taxRate). The server never adds it.
//   surcharge       str2: summary.surcharge (ONLINE_PAYMENT only, none when the wallet pays it all)
//   Coupons (D-06, D-28): a ROOT claim → is_coupon_applied true, coupon_type, coupon_id, discount_reason (the name),
//                   coupon_discount_amount = Number(saved.toFixed(2)): FREE_DELIVERY saves the WAIVED min(fee, cap);
//                   CART_DISCOUNT and the item-level coupons save summary.discount (an item coupon's product part, the
//                   free units' too: the validator's item_wise_discount_by_coupon, which the server reads
//                   coupon_discount_amount as). Every applied coupon claims: CART_DISCOUNT, FREE_DELIVERY (applied
//                   only with ONLINE_PAYMENT, see OO.pricing) and the item-level ones (ITEM_DISCOUNT, its 100 % unit
//                   and giveaway, BUY_ONE_GET_ONE_FREE, BUY_N_GET_M_FREE) while ITEM_COUPON_ROOT_CLAIM (true: D-06 a).
//                   No root claim → is_coupon_applied false and the four others null.
//                   Root `discount` % = coupon × 100 / total for CART_DISCOUNT only (the server takes total × % off as
//                   the cart discount), else 0. A coupon that covers the whole total (the same cents) is exactly 100:
//                   x × 100 / x is not 100 in floating point for some totals (2.99 → 99.99999999999999), and POS builds
//                   from 1000 auto-pay a $0 ticket only when the root % is ≥ 100 (compat, L4 audit C6).
//   wallet_point_used false · wallet_order_type ('dining' for a table, else ONLINE_[APP_]PICKUP|DELIVERY) ·
//   wallet_applied_type 0 · wallet_amount 0 · wallet_points 0 · membership_name / membership_id null ·
//   isVoucherApplied false · voucherData null (the P2 contributors fill what they use)
//   data[] item row: { is_combo:false, combo_id:null, product_id, count (qty), price (basePrice), kitchen_note,
//                   modifiers, sizeenabled, size[], discount (the line's coupon %: OO.pricing._.couponWire's percents),
//                   isWalletRedeemed / walletDiscount / walletAmount / walletType / walletUsedForProduct (off),
//                   is_membership_discount_applied false, discount_reason (the coupon's name on a discounted row),
//                   coupon_id, is_coupon_applied (any applied coupon; the validator adds a line's coupon discount back
//                   only when this is true), comboBatch null, extra_price 0, price_tag_id null, is_price_tag_applied
//                   false }
//   The rows are those of OO.pricing._.couponWire's lines (P2-11; OO.coupons.wire): the cart's lines, and for an
//   applied free-item coupon (OO.coupons.freeLines) REF's free rows (sipoCheckoutWire buildProductData /
//   calculateItemDiscount), each an item row at its menu price (the validator checks it) whose `discount` frees it:
//     BOGO            a clone of a paid unit (its size, modifiers, notes) per free unit: count 1, discount 100.
//     BUY_N           each picked reward: count 1, discount 100.
//     ITEM 100 %      the free unit split off its line (REF): [count 1, discount 100 or the capped %, modifiers × 1],
//                     then [count − 1, discount 0, modifiers × (count − 1)] when any is left; a picked giveaway is its
//                     own row (count 1, its %). The capped % splits too (REF sends a capped line whole: qty × the cap
//                     on the row, which guard Rule L flags).
//   The validator nets a free row to 0 and adds its product part back (is_coupon_applied), so `total` counts it at full
//   price and payable_amount takes summary.discount off; its modifiers are charged unless the store applies discounts
//   to modifiers (then the row's % nets them). The line hooks see a free row with its free line (line.free).
//   data[] combo child: one comboBatch (UUID) per combo UNIT (the server prices a batch as ONE combo), per pick:
//                   { is_combo:true, combo_id, combo_price, extraComments, product_id, count (per unit), price (the
//                   pick's sale_price or size price: V4), dicountFactor / dicountFactorValue / totalDicount (sic) and
//                   discount % (the combo saving spread by price share, OO.pricing._.comboSpread, so the validator's
//                   nets add up to combo_price + extras; at a store with applyDiscountToModifiers it nets the child's
//                   modifiers too), discount_reason (CART_DISCOUNT name, else the combo name), kitchen_note,
//                   modifiers, sizeenabled, size[], size_id, size_name, coupon_id, is_coupon_applied, the wallet
//                   fields (off), comboBatch, extra_price, price_tag_id null, is_price_tag_applied false }
//   size[]          [{ id, size_name, size_amount, user_id, position, modifiesxnames, modifiesxids }] (get_menu's row
//                   shape; the validator reads size[0].id) or [] without a size.
//   modifiers[]     { addOnId, addOnName, price, preselect, is_alert, alert_text, position, availability, groupId,
//                   count } in the line's row order (the server allocates free units in this order). count = EVERY
//                   selected unit: per-unit count × qty (a combo child: × its count per unit). `free` is never sent;
//                   the server derives it from modifierscategories.max_no_price_count.
//   Table orders (P4-07; REF sipoCheckoutWire's table-side wire, inventory D §2.1 Q7). ctx.table makes one; the shell
//   (checkout, P4-05/06) passes:
//     table       { id, name }: OO.store's table context for this store (P4-01 validated it). table_id = id as a
//                 number (NO_TABLE unless a whole number > 0), table_name = the trimmed name, else null (REF),
//                 is_tableside true.
//     guestCount  the party size (P4-03): guest_count = its whole part when ≥ 1 (at most GUESTS_MAX), else 1. REF
//                 sends 0 when its guest form is off; the POS decodes an int guest count.
//     menuType    the `type` of the menu the lines were priced from (OO.model Menu.type = get_menu's menu_type; the
//                 page's menu, which OO.store.cartReprice reprices the cart from): 'Table Menu' (products at
//                 COALESCE(table_price, sale_price), combos at COALESCE(table_price, combo_price)). A live store's
//                 table order without it is TABLE_UNSUPPORTED.
//     pricetag    (P2-01) the priced menu's record: a TABLE SIDE tag at a table (PRICETAG_DOMAIN otherwise).
//     paymentMethod  PAY_AT_COUNTER (one of the store's payment_table_side types: the web's counter wire, 'Pay at
//                 Counter' and no ids, P2-27; REF puts the type's name in payment_gateway, which neither the POS nor
//                 the server reads) or ONLINE_PAYMENT (the table's gateway: the shell lists
//                 CS.payment.choices(methods, 'DINEIN') and builds the payload on CS.payment.payStore(store, choice)).
//     orderType   'PICKUP' or 'DINEIN', either: a table order is order_type / wallet_order_type 'dining', delivery_type
//                 'ASAP' at now + store.cookingTimeMins (pickup_buffer_time, REF), and never has a delivery, whatever
//                 the ctx carries: deliverycharge '0.00' (OO.pricing charges no fee with opts.table), deliveryzone /
//                 delivery_lat / delivery_lng null, customer_address / customer_zipcode '', delivery_driving_time '0'.
//   Contact is optional at a table (REF; the shell asks for it when is_user_form_table_side or a gateway needs it):
//   customer_name falls back to the table name, else 'Guest'. Left to the shell and the router: D-20 (no coupon,
//   points or rewards at a table; the wire still carries them, parity wallet-points-dining) and the table's validity.
//   Never sent: X-App-Version-style data, cart_id, combo_mapping_id / combo_group_id / combo_size_contract_version /
//   authoritative_combo_pricing (D-08).
//   Not ported yet (P2, through the hooks): wallet redemption, membership, pricetags.
//   applyDiscountToModifiers (B13, P1-22): modelled in OO.pricing, so subtotal / total / payable follow the validator's
//   S at those stores too; the rows themselves are unchanged (the server derives the modifier discount from `discount`).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function F() { return OO.format; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function r2(n) { return F().round2(n); }
  function str2(n) { return r2(n).toFixed(2); }
  function hasOwn(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  // The root CART_DISCOUNT % of total (> 0). Exactly 100 when the coupon covers the whole total, to the cent: the
  // division alone gives 99.99999999999999 for some totals, and the POS then leaves the $0 ticket unpaid (header).
  function rootPercent(discount, total) {
    return Math.round(discount * 100) === Math.round(total * 100) ? 100 : discount * 100 / total;
  }
  function summary(lines, store, opts) { return OO.pricing.summary(lines, store, opts); }
  // The lines as charged with their coupon %, and the combo spread: pricing.jsx's ONE copy, so the rows carry exactly
  // what was priced.
  function couponWire(lines, coupon, store) { return OO.pricing._.couponWire(lines, coupon, store); }
  function comboSpread(line) { return OO.pricing._.comboSpread(line); }

  // ---- constants -------------------------------------------------------------------------------------
  // The real checkout `server_secret` (§0) is deliberately NOT in this public bundle, and must never be injected into
  // it at runtime either (a boot value is just as public). The field is always sent, as an empty string, and stays:
  // the POS order model requires a non-null String serverSecret (sipo-application-v2
  // lib/models/onlineOrderResponseRoot.dart:141/189), the B01 SPA-origin path ignores it, and the fixture checkout
  // compares it ('' with '').
  var SERVER_SECRET = '';

  // D-06: should an item-level coupon (ITEM_DISCOUNT; BOGO / BUY_N since P2-11) be claimed at the ROOT? (a) yes: the
  // root coupon_id creates a CouponTransaction (OnlinePaymentController: is_coupon_applied + coupon_id set) and runs
  // CouponController::checkCouponValidity, the usage caps included (ecode 800). (b) REF: no root claim
  // (is_coupon_applied false, coupon_id null, no coupon_type / discount_reason / coupon_discount_amount), so no claim
  // and no cap. DECIDED (the owner, 2026-10-02): (a), record item-coupon use; the POS's own claim is idempotent per
  // (coupon, ticket). Either way the LINES carry coupon_id and is_coupon_applied: the validator adds a line's coupon
  // discount back only when the line's flag is true (else ecode 300).
  var ITEM_COUPON_ROOT_CLAIM = true;
  // The item-level coupon types (REF isItemLevelCoupon): their saving sits on the rows (the validator's add-back), so the
  // tax base drops it, and they claim at the root only while ITEM_COUPON_ROOT_CLAIM.
  var ITEM_LEVEL = ['ITEM_DISCOUNT', 'BUY_ONE_GET_ONE_FREE', 'BUY_N_GET_M_FREE'];

  // ASAP deliver_time is at least this far ahead: the validator rejects a present ASAP time ≤ its own clock (ecode
  // 103), and a store buffer of 0 would send "now" (B §7 A3).
  var ASAP_MIN_MINUTES = 2;

  // P4-07 (the header's "Table orders"): a live store's table order is priced from the table menu (get_menu's
  // menu_type, compared trimmed and lower-cased) and claims only a TABLE SIDE pricetag (OO.pricetags.domainOf at a
  // table). guest_count stays a plain int for the POS: no table seats more.
  var TABLE_MENU = 'table menu';
  var TABLE_TAG_DOMAIN = 'TABLE SIDE';
  var GUESTS_MAX = 999;

  // D-08: keys that switch the server to its cart-sync / authoritative-combo pricing path (CartItemNormalizer
  // usesAuthoritativeComboContract tests the KEYS, so even a null value counts).
  var D08_ROOT_KEYS = ['cart_id'];
  var D08_LINE_KEYS = ['combo_mapping_id', 'combo_group_id', 'combo_size_contract_version', 'authoritative_combo_pricing'];

  var DIAL = { NZ: '+64', AU: '+61', TW: '+886' };
  var COUNTRY = { nz: 'NZ', nzl: 'NZ', newzealand: 'NZ', au: 'AU', aus: 'AU', australia: 'AU', tw: 'TW', twn: 'TW', taiwan: 'TW' };
  var CURRENCY = { nzd: 'NZ', aud: 'AU', twd: 'TW' };

  // ---- errors ----------------------------------------------------------------------------------------
  function payloadError(code, message, cause) {
    var e = new Error('Checkout payload: ' + message);
    e.kind = 'payload';
    e.code = code;
    if (cause !== undefined) e.cause = cause;
    return e;
  }

  // ---- E.164 -------------------------------------------------------------------------------------------
  function key(v) { return String(v == null ? '' : v).toLowerCase().replace(/[^a-z]/g, ''); }
  function dialCode(store) {
    store = store || {};
    var region = COUNTRY[key(store.address && store.address.country)] || CURRENCY[key(store.currencyCode)]
      || COUNTRY[key(OO.boot && OO.boot.region)];
    return DIAL[region] || DIAL.NZ;
  }
  // A number that already starts with its country code but has no '+' (REF's 642… / 614… form): NZ, AU and TW mobiles,
  // after the separators are gone. Read the same whatever the store's country.
  var BARE_INTERNATIONAL = /^(?:642\d{7,9}|614\d{8}|8869\d{8})$/;
  // A trunk 0 kept after one of our country codes: '+64 021…', '+61 (0)4…' once the brackets are gone.
  var TRUNK_AFTER_CC = /^\+(64|61|886)0/;
  function e164(phone, store) {
    // The '(0)' trunk marker goes first: dropping only its brackets would leave the 0 in the number.
    var s = String(phone == null ? '' : phone).replace(/\(0\)/g, '').replace(/[\s\-().]/g, '');
    if (!s) return s;
    if (s.slice(0, 2) === '00') s = '+' + s.slice(2); // the international prefix
    if (s.charAt(0) === '+') return s.replace(TRUNK_AFTER_CC, '+$1');
    if (BARE_INTERNATIONAL.test(s)) return '+' + s;
    return dialCode(store) + (s.charAt(0) === '0' ? s.slice(1) : s);
  }

  // ---- helpers -----------------------------------------------------------------------------------------
  function uuid4() {
    var c = typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto : null;
    var b = [];
    for (var i = 0; i < 16; i++) b.push(c ? c.getRandomValues(new Uint8Array(1))[0] : Math.floor(Math.random() * 256));
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    var h = b.map(function (x) { return (x < 16 ? '0' : '') + x.toString(16); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  // ctx.now, else the app clock (OO.data.now: the fixture clock on a fixture store, else the device clock); the device
  // clock directly only when store.jsx isn't loaded.
  function nowOf(ctx) {
    var v = ctx.now != null ? ctx.now : (OO.data && typeof OO.data.now === 'function' ? OO.data.now() : null);
    var t = v instanceof Date ? v.getTime() : (v == null ? NaN : new Date(v).getTime());
    return new Date(isFinite(t) ? t : Date.now());
  }

  // A gateway id as the server's FK: a whole number > 0 (a number or a digit string), else null.
  function gatewayIdOf(v) {
    var s = String(v == null ? '' : v).trim();
    return /^\d+$/.test(s) && Number(s) > 0 ? Number(s) : null;
  }
  function isFixtureStore(store) {
    var fx = OO.data && OO.data.fixtures;
    return !!(fx && typeof fx.has === 'function' && store && store.onlineKey && fx.has(store.onlineKey));
  }
  function validGatewayId(v) {
    return typeof v === 'number' && isFinite(v) && v > 0 && Math.floor(v) === v;
  }

  // A store the real adapter serves (P1-02's facade, OO.api.isLiveFor). Without the facade (or if it throws) every
  // store but a fixture one counts as live: a table order without the table menu fails closed (TABLE_UNSUPPORTED).
  function isLiveStore(store) {
    var api = OO.api;
    if (api && typeof api.isLiveFor === 'function') {
      try { return !!api.isLiveFor(store.onlineKey); } catch (e) { /* fall through */ }
    }
    return !isFixtureStore(store);
  }

  // ---- table orders (P4-07) ------------------------------------------------------------------------------
  // table_id as every POS build decodes it (an int): a whole number > 0, from a number or a digit string; else null.
  // At most 15 digits, so the JSON number is never one a 64-bit int can't hold.
  function tableIdOf(table) {
    var v = table && typeof table === 'object' ? table.id : null;
    var s = typeof v === 'number' || typeof v === 'string' ? String(v).trim() : '';
    var n = /^\d{1,15}$/.test(s) ? Number(s) : 0;
    return n > 0 ? n : null;
  }
  // guest_count: the whole part of ctx.guestCount when it is at least 1 (at most GUESTS_MAX), else 1.
  function guestCountOf(v) {
    var n = Math.floor(num(v));
    return n >= 1 ? Math.min(n, GUESTS_MAX) : 1;
  }
  // The channel of the tag the rows claim: only an active ctx.pricetag with a tag id is claimed (the pricetags
  // contributor's own gate), and its domain must be TABLE SIDE exactly on a table order.
  function checkTagDomain(pricing, table) {
    if (!pricing || typeof pricing !== 'object' || pricing.active !== true || pricing.tagId == null) return;
    var domain = String(pricing.domain || '').trim().replace(/[\s_-]+/g, ' ').toUpperCase();
    if (OO.mode === 'kiosk') {
      if (domain !== 'KIOSK') throw payloadError('PRICETAG_DOMAIN', 'A kiosk order must use kiosk prices.');
      return;
    }
    if ((domain === TABLE_TAG_DOMAIN) === table) return;
    throw payloadError('PRICETAG_DOMAIN', table
      ? 'a table order is priced with a ' + (domain || 'domain-less') + ' pricetag, not a ' + TABLE_TAG_DOMAIN + ' one'
      : 'a ' + TABLE_TAG_DOMAIN + ' pricetag can only price a table order');
  }

  // delivery_driving_time: always a string (the POS decodes a String?). Minutes → '12'; text as given; nothing → '0'.
  function drivingTimeOf(v) {
    if (typeof v === 'number') return isFinite(v) ? String(v) : '0';
    var s = typeof v === 'string' ? v.trim() : '';
    return s || '0';
  }

  // ctx.expectedPayable (the total the Pay button showed) against the payable about to be sent: STALE_TOTAL, carrying
  // the new payable, when they differ by more than half a cent or the expectation isn't a number.
  function checkExpected(expected, payable) {
    if (expected === undefined || expected === null) return;
    var n = typeof expected === 'number' ? expected : (typeof expected === 'string' && expected.trim() ? Number(expected) : NaN);
    if (isFinite(n) && Math.abs(r2(n) - payable) <= 0.004) return;
    var e = payloadError('STALE_TOTAL', 'the total is now ' + str2(payable) + ', not the ' + (isFinite(n) ? str2(n) : JSON.stringify(expected)) + ' shown');
    e.payable = payable;
    throw e;
  }

  function payloadMods(mods, factor) {
    return arr(mods).map(function (m) {
      return {
        addOnId: m.addOnId, addOnName: m.name, price: m.price, preselect: 0, is_alert: 0, alert_text: null,
        position: null, availability: null, groupId: m.groupId, count: num(m.count) * factor,
      };
    });
  }

  function sizeRow(store, id, name, amount) {
    return id == null ? [] : [{ id: id, size_name: name || '', size_amount: amount, user_id: store.id, position: null, modifiesxnames: [], modifiesxids: [] }];
  }

  function zoneRow(z) {
    if (!z || typeof z !== 'object') return null;
    function pick(raw, normalised) { return num(z[raw] != null ? z[raw] : z[normalised]); }
    return { id: num(z.id), name: String(z.name || ''), delivery_fee: pick('delivery_fee', 'fee'), minimum_amount: pick('minimum_amount', 'minimumOrder'), zone_area: pick('zone_area', 'radiusKm') };
  }

  function coordinate(v, limit) {
    var n = v === null || v === '' || typeof v === 'boolean' ? NaN : Number(v);
    return isFinite(n) && Math.abs(n) <= limit ? n : null;
  }

  // ---- contributors (the P2 engines' hook) -------------------------------------------------------------
  var contributors = []; // [{ key, line, root }], in registration order

  function contribute(k, spec) {
    var id = String(k);
    var at = -1;
    contributors.forEach(function (c, i) { if (c.key === id) at = i; });
    var line = spec && typeof spec.line === 'function' ? spec.line : null;
    var root = spec && typeof spec.root === 'function' ? spec.root : null;
    if (!line && !root) {
      if (at >= 0) contributors.splice(at, 1);
      return;
    }
    var entry = { key: id, line: line, root: root };
    if (at >= 0) contributors[at] = entry; else contributors.push(entry);
  }

  function mergeInto(target, res, who) {
    if (res === null || res === undefined || res === false) return;
    if (typeof res !== 'object' || Array.isArray(res)) {
      throw payloadError('CONTRIBUTOR', who + ' returned ' + (Array.isArray(res) ? 'an array' : typeof res) + ', not an object of fields');
    }
    Object.keys(res).forEach(function (k) { target[k] = res[k]; });
  }

  function call(fn, args, who) {
    try {
      return fn.apply(null, args);
    } catch (err) {
      throw payloadError('CONTRIBUTOR', who + ' failed: ' + (err && err.message ? err.message : String(err)), err);
    }
  }

  function applyContributors(body, sources, hookCtx) {
    var list = contributors.slice();
    list.forEach(function (c) {
      if (!c.line) return;
      body.data.forEach(function (row, i) {
        var who = 'contributor "' + c.key + '" (line hook, row ' + i + ')';
        mergeInto(row, call(c.line, [row, sources[i], hookCtx], who), who);
      });
    });
    list.forEach(function (c) {
      if (!c.root) return;
      var who = 'contributor "' + c.key + '" (root hook)';
      mergeInto(body, call(c.root, [body, hookCtx], who), who);
    });
  }

  // ---- the final body's guards (after the contributors) -------------------------------------------------
  function checkBody(body, s, store) {
    D08_ROOT_KEYS.forEach(function (k) {
      if (hasOwn(body, k)) throw payloadError('D08', '`' + k + '` must not be sent (D-08: no cart sync)');
    });
    arr(body.data).forEach(function (row, i) {
      D08_LINE_KEYS.forEach(function (k) {
        if (row && typeof row === 'object' && hasOwn(row, k)) throw payloadError('D08', 'data[' + i + '].' + k + ' must not be sent (D-08: no authoritative combo pricing)');
      });
    });
    if (body.payment_type === 'ONLINE_ORDER_CARD') {
      if (typeof body.payment_gateway !== 'string' || !body.payment_gateway.trim()) {
        throw payloadError('NO_GATEWAY', 'online payment needs a payment gateway; this store has none set up for it');
      }
      if (!validGatewayId(body.payment_gateway_id) || !validGatewayId(body.terminal_id)) {
        throw payloadError('NO_GATEWAY', 'online payment needs a numeric gateway id > 0 (got ' + JSON.stringify(body.payment_gateway_id) + ')');
      }
    }
    if ((body.payment_type === 'wallet' || s.wallet > 0) && !body.wallet_point_used) {
      throw payloadError('WALLET', 'a wallet saving or payment_type "wallet" needs wallet_point_used (the wallet contributor sets it)');
    }
    if (body.payable_amount !== str2(s.total - num(s.tip)) || num(body.tip) !== num(s.tip)) {
      throw payloadError('TOTALS', 'payable_amount plus tip must equal the summary total ' + str2(s.total));
    }
    var parts = str2(num(body.subtotal) + num(body.deliverycharge) + num(body.surcharge));
    if (body.total !== parts) throw payloadError('TOTALS', 'total ' + body.total + ' is not subtotal + deliverycharge + surcharge = ' + parts);
  }

  // ---- checkout payload (B §4.2) ------------------------------------------------------------------------
  function checkoutPayload(lines, store, ctx) {
    ctx = ctx || {};
    store = store || {};
    var f = F();
    lines = arr(lines);
    var orderType = String(ctx.orderType || 'PICKUP').toUpperCase();
    var delivery = orderType === 'DELIVERY';
    var table = ctx.table || null;
    var app = !!ctx.isMobileApp;
    var kiosk = OO.mode === 'kiosk';
    var kioskId = kiosk && OO.kiosk ? OO.kiosk.terminal(store.onlineKey) : null;
    var kioskType = kiosk && OO.kiosk ? OO.kiosk.orderType(store.onlineKey) : null;
    var now = nowOf(ctx);
    // WL-2: a white-label studio preview (?wlPreview=) never builds an order.
    if ((OO.orderingQr && OO.orderingQr.preview()) || (OO.whitelabel && typeof OO.whitelabel.isPreview === 'function' && OO.whitelabel.isPreview())) {
      throw payloadError('PREVIEW', 'orders are disabled in a white-label preview');
    }
    if (!store.onlineKey || !(num(store.id) > 0)) throw payloadError('NO_STORE', 'the store has no online key or id');
    if (!lines.length) throw payloadError('EMPTY_CART', 'the cart is empty');
    if (kiosk && (!kioskId || !kioskType || table || delivery)) {
      throw payloadError('KIOSK_SETUP', 'Choose dine-in or takeaway on this kiosk before ordering.');
    }
    // P4-07: a table order needs its table (a whole-number id) and lines priced for the table: on a live store, from
    // the TABLE menu (an online menu's prices fail the validator or undercharge a table combo; a cold menu cache
    // answers a table request with the online menu), and a pricetag only from the TABLE SIDE domain.
    var tableId = table ? tableIdOf(table) : null;
    if (table && tableId === null) {
      throw payloadError('NO_TABLE', 'a table order needs its table: a whole-number id > 0, not ' + JSON.stringify(typeof table === 'object' ? table.id : table));
    }
    if (table && isLiveStore(store) && String(ctx.menuType || '').trim().toLowerCase() !== TABLE_MENU) {
      var menuNamed = ctx.menuType ? JSON.stringify(ctx.menuType) : 'unnamed';
      throw payloadError('TABLE_UNSUPPORTED', 'a table order needs lines priced from the table menu, not the ' + menuNamed + ' one');
    }
    checkTagDomain(ctx.pricetag, !!table);
    var zone = delivery && !table ? zoneRow(ctx.deliveryZone) : null;
    if (delivery && !table && !(zone && zone.id > 0)) throw payloadError('NO_ZONE', 'a delivery order needs its delivery zone');

    // The summary exactly as the cart / checkout pages ask for it (pages/cart.jsx summaryOf), so payable = the total
    // they show. walletAmount and now only when the caller passed them.
    var opts = { paymentMethod: ctx.paymentMethod, coupon: ctx.coupon, orderType: orderType, deliveryFee: ctx.deliveryFee, table: table || undefined };
    opts.tipAmount = ctx.tipAmount;
    if (ctx.tipAmount != null && OO.pricing.tipsEnabled(store, opts) && !OO.pricing.validTip(ctx.tipAmount)) {
      throw payloadError('TIP', 'Enter a valid tip with at most two decimal places.');
    }
    if (ctx.walletAmount != null) opts.walletAmount = ctx.walletAmount;
    if (ctx.now != null) opts.now = ctx.now;
    var s = summary(lines, store, opts);

    var coupon = ctx.coupon || null;
    var applied = !!(coupon && s.coupon.ok && !s.coupon.pending);
    var itemLevel = applied && ITEM_LEVEL.indexOf(coupon.type) >= 0;
    var rootClaim = applied && (!itemLevel || ITEM_COUPON_ROOT_CLAIM);
    var gen = typeof ctx.uuid === 'function' ? ctx.uuid : uuid4;
    // The lines as the summary priced them (OO.pricing._.couponWire): an applied free-item coupon's free unit split
    // off its line and its free lines added (P2-11), each with its coupon %.
    var charged = applied ? couponWire(lines, coupon, store) : { lines: lines, percents: {} };
    var pcts = charged.percents;
    var walletOff = { isWalletRedeemed: false, walletDiscount: 0, walletAmount: 0, walletType: 0, walletUsedForProduct: 0 };

    // ---- lines ----
    var data = [];
    var sources = []; // the cart line behind each row, for the line hooks
    charged.lines.forEach(function (l) { // the cart's lines, with an applied coupon's free rows (couponWire)
      if (l.kind === 'combo') {
        // The server prices each comboBatch as ONE combo (combo_price once + extras × count + modifiers;
        // OnlinePaymentController), so a combo line with qty N becomes N batches. The spread (OO.pricing._.comboSpread)
        // is the one the summary priced the children's modifiers with (applyDiscountToModifiers).
        var spread = comboSpread(l);
        // The children carry combo_price + extras only through their price shares: with every pick priced 0 there is
        // no share to carry them (a certain ecode 300, and an error mail per attempt).
        if (!(spread.actual > 0) && r2(num(l.comboPrice) + spread.actualExtra) > 0) {
          throw payloadError('COMBO_UNPRICEABLE', 'combo "' + l.name + '" can not be priced: every pick is priced 0');
        }
        for (var q = 0; q < Math.max(1, num(l.qty)); q++) {
          var batch = gen();
          arr(l.picks).forEach(function (p, i) {
            var share = spread.picks[i];
            data.push(Object.assign({
              is_combo: true, combo_id: l.comboId, combo_price: l.comboPrice, extraComments: '', product_id: p.productId,
              count: p.count, price: p.price, dicountFactor: share.factor, dicountFactorValue: share.factorValue, totalDicount: spread.totalDisc, discount: share.pct,
              discount_reason: applied && coupon.type === 'CART_DISCOUNT' ? coupon.name : l.name, kitchen_note: l.notes || '',
              modifiers: payloadMods(p.modifiers, num(p.count)), sizeenabled: p.sizeId != null,
              size: sizeRow(store, p.sizeId, p.sizeName, p.price), size_id: p.sizeId != null ? p.sizeId : null,
              size_name: p.sizeId != null ? p.sizeName || '' : null, coupon_id: applied ? coupon.id : null, is_coupon_applied: applied,
            }, walletOff, { comboBatch: batch, extra_price: p.extraPrice, price_tag_id: null, is_price_tag_applied: false }));
            sources.push(l);
          });
        }
        return;
      }
      var pct = pcts[l.key] || 0;
      data.push(Object.assign({
        is_combo: false, combo_id: null, product_id: l.itemId, count: l.qty, price: l.basePrice, kitchen_note: l.notes || '',
        modifiers: payloadMods(l.modifiers, num(l.qty)), sizeenabled: l.sizeId != null, size: sizeRow(store, l.sizeId, l.sizeName, l.basePrice),
        discount: pct,
      }, walletOff, {
        is_membership_discount_applied: false, discount_reason: pct > 0 ? coupon.name : null, coupon_id: applied ? coupon.id : null,
        is_coupon_applied: applied, comboBatch: null, extra_price: 0, price_tag_id: null, is_price_tag_applied: false,
      }));
      sources.push(l);
    });

    // ---- money (B §3.2–3.4, §3.8, §3.9) ----
    // s.discount is the coupon amount (CART_DISCOUNT / ITEM_DISCOUNT; 0 for FREE_DELIVERY or no coupon) and
    // s.deliveryDiscount the FREE_DELIVERY waiver: payable subtracts exactly what summary.total subtracts.
    // W5-FIX #5: the line-level savings are the membership's item saving and the neutral lineSaving (the wallet's).
    var subtotal = r2(Math.max(0, s.subtotal - r2(s.membershipItem + num(s.lineSaving))) + (s.orderFees ? s.orderFees.total : 0));
    var total = r2(subtotal + s.delivery + s.surcharge);
    var payable = r2(Math.max(0, total - s.discount - s.deliveryDiscount - s.membershipCart - s.wallet - num(s.orderingQrDiscount)));
    var rate = s.taxRate;
    var taxable = Math.max(0, subtotal - (itemLevel ? s.discount : 0));
    var tax = r2(s.taxInclusive ? taxable * rate / (100 + rate) : taxable * rate / 100);
    var saved = !rootClaim ? null : (coupon.type === 'FREE_DELIVERY' ? s.deliveryDiscount : s.discount);

    // ---- payment (an online order's gateway is checked on the final body: a contributor may supply it) ----
    var method = ctx.paymentMethod || 'PAY_AT_COUNTER';
    var pm = arr(store.paymentMethods).filter(function (m) { return m && m.key === method; })[0] || {};
    var walletPaid = s.wallet > 0 && payable + num(s.tip) <= 0;
    var freeOnline = method === 'ONLINE_PAYMENT' && !walletPaid && payable + num(s.tip) <= 0; // sent as a counter order (header)
    var online = method === 'ONLINE_PAYMENT' && !walletPaid && !freeOnline;
    var gatewayId = online ? gatewayIdOf(pm.gatewayId) : null;

    // ---- time ----
    var deliveryType = table || kiosk ? 'ASAP' : String(ctx.deliveryType || (ctx.slot ? (ctx.slot.asap || ctx.slot.value === 'ASAP' ? 'ASAP' : 'SCHEDULED') : (ctx.deliverTime ? 'SCHEDULED' : 'ASAP'))).toUpperCase();
    var deliverTime;
    if (deliveryType === 'ASAP') {
      var buffer = delivery && !table ? num(store.deliveryTimeMins) : num(store.cookingTimeMins);
      deliverTime = new Date(now.getTime() + Math.max(buffer, ASAP_MIN_MINUTES) * 60000).toISOString();
    } else {
      var at = NaN;
      if (ctx.deliverTime != null && ctx.deliverTime !== '') {
        at = ctx.deliverTime instanceof Date ? ctx.deliverTime.getTime() : new Date(ctx.deliverTime).getTime();
      } else if (ctx.slot && ctx.slot.deliveryTime) {
        var wc = f.parseWallClock(ctx.slot.deliveryTime);
        if (wc) at = f.instantOf(wc.ymd, wc.minutes, store.timezone);
      }
      if (!isFinite(at)) throw payloadError('NO_TIME', 'a scheduled order needs its time');
      deliverTime = new Date(at).toISOString();
    }

    // ---- customer ----
    var contact = ctx.contact || {};
    var customer = ctx.customer || {};
    var email = String(contact.email || customer.email || '').replace(/\s+/g, '');
    var name = String(contact.name || customer.name || '').trim();
    var tableName = table ? String(table.name == null ? '' : table.name).trim() : '';
    if (!name && table) name = tableName || 'Guest';
    if (!name && kiosk) name = 'Guest';
    // A table order has no delivery address (P4-07): none reaches the POS customer it creates.
    var address = table ? null : ctx.address || null;
    var lat = delivery && !table && address ? coordinate(address.lat, 90) : null;
    var lng = delivery && !table && address ? coordinate(address.lng, 180) : null;
    if (lat === null || lng === null || (lat === 0 && lng === 0)) { lat = null; lng = null; }

    var body = {
      version: 'V4',
      res_id: num(store.id),
      restaurant_key: store.onlineKey,
      server_secret: SERVER_SECRET,
      currency: String(store.currencyCode || 'nzd').toLowerCase(),
      isMobileApp: app,
      browserId: ctx.browserId || (email ? email + '-web' : 'web'),
      fcmToken: '',
      kiosk_id: kioskId,
      userUniqueId: customer.uid || null,
      fuid: customer.uid || null,
      customer_id: ctx.customerId || null,
      notes: ctx.notes || '',
      order_type: kiosk ? (kioskType === 'DINEIN' ? 'kiosk_dine_in' : 'kiosk_takeout') : table ? 'dining' : (app ? 'APP_' : '') + (delivery ? 'DELIVERY' : 'PICKUP'),
      delivery_type: deliveryType,
      deliver_time: deliverTime,
      customer_name: name,
      customer_email: email,
      customer_phone: e164(contact.phone || customer.phone || '', store),
      customer_address: address ? address.line || '' : '',
      customer_zipcode: address ? address.zipcode || '' : '',
      table_id: tableId,
      table_name: table ? tableName || null : null,
      is_tableside: !!table,
      guest: null,
      guest_count: guestCountOf(ctx.guestCount),
      payment_type: walletPaid ? 'wallet' : (online ? 'ONLINE_ORDER_CARD' : 'Pay at Counter'),
      payment_gateway: walletPaid ? 'wallet' : (online ? pm.gateway || null : (freeOnline ? 'Pay at Counter' : pm.gateway || 'Pay at Counter')),
      payment_gateway_id: gatewayId,
      terminal_id: gatewayId,
      payment_status: 'UNPAID',
      delivery_by: 'Inhouse',
      uber_order: false,
      tip: s.tip > 0 ? s.tip : null,
      deliverycharge: str2(s.delivery),
      deliveryzone: zone,
      delivery_driving_time: table ? '0' : drivingTimeOf(ctx.drivingTime),
      delivery_lat: lat,
      delivery_lng: lng,
      subtotal: subtotal.toFixed(2),
      total: total.toFixed(2),
      payable_amount: payable.toFixed(2),
      tax: tax.toFixed(2),
      surcharge: str2(s.surcharge),
      ...(s.surcharges.some(function (c) { return c.label; }) ? { surcharge_label: s.surcharges.map(function (c) { return c.label || "Surcharge"; }).filter(function (v, i, a) { return a.indexOf(v) === i; }).join(" + ") } : {}),
      discount: applied && coupon.type === 'CART_DISCOUNT' && total > 0 ? rootPercent(s.discount, total) : 0,
      is_coupon_applied: rootClaim,
      coupon_type: rootClaim ? coupon.type : null,
      coupon_id: rootClaim ? coupon.id : null,
      discount_reason: rootClaim ? coupon.name : null,
      coupon_discount_amount: saved === null ? null : Number(num(saved).toFixed(2)),
      wallet_point_used: false,
      wallet_order_type: table ? 'dining' : 'ONLINE_' + (app ? 'APP_' : '') + (delivery ? 'DELIVERY' : 'PICKUP'),
      wallet_applied_type: 0,
      wallet_amount: 0,
      wallet_points: 0,
      membership_name: null,
      membership_id: null,
      isVoucherApplied: false,
      voucherData: null,
      data: data,
    };
    if (store.orderFees) {
      body.order_fee_version = 1;
      body.order_fee_payment_key = s.feePaymentKey;
      body.order_fee_total = str2(s.orderFees.total);
      body.payment_type_id = pm.typeId == null ? null : pm.typeId;
    }

    if (ctx.dob && /^\d{4}-\d{2}-\d{2}$/.test(String(ctx.dob))) body.customer_dob = String(ctx.dob);
    if (ctx.marketing === true) body.marketing_consent = true; // else omitted: never a withdrawal (D-13, header)

    applyContributors(body, sources, Object.assign({}, ctx, { store: store, lines: lines, summary: s, orderType: orderType, now: now }));
    if (store.orderingQr) {
      body.ordering_qr = store.orderingQr.token;
      body.ordering_qr_revision = store.orderingQr.revision;
      body.ordering_qr_discount = num(s.orderingQrDiscount);
      body.ordering_qr_return_path = OO.boot && /\/endpoint\/online-order$/.test(OO.boot.routeBase || '') ? OO.boot.routeBase : '';
      body.discount = total > 0 ? num(s.orderingQrDiscount) * 100 / total : 0;
      body.discount_reason = store.orderingQr.name;
      var instructions = delivery && store.orderingQr.delivery_address && store.orderingQr.delivery_address.instructions;
      if (instructions) body.notes = [body.notes, instructions].filter(Boolean).join('\n');
    }
    checkBody(body, s, store);
    checkExpected(ctx.expectedPayable, r2(num(body.payable_amount) + num(body.tip)));
    return body;
  }

  OO.payload = OO.lib.payload = {
    checkoutPayload: checkoutPayload,
    contribute: contribute,
    e164: e164,
  };
  OO.pricing.checkoutPayload = checkoutPayload;
  OO.pricing.SERVER_SECRET = SERVER_SECRET;
})();


// ===================== src/lib/modifiers.jsx =====================
// src/lib/modifiers.jsx: OO.modifiers (alias OO.lib.modifiers). The pure option-group rules of the item sheet
// (design 02 Brisket Poutine / D02 Fat Boy). It works on the normalised Product/Group of OO.model. No JSX.
//
// Selection model: sel = { [groupId]: [optionId, …] }, in PICK order (toggle / increment append). A repeated id
// is a count, for allow_same counter groups. Sizes are separate (sizeId). A size REPLACES the base price, and its
// excludeOptionIds (modifiesxids) hide options.
//
// Hidden vs blocked (B §7 A9, B L1699 h). visibleGroups applies the size first, then availability:
//   - the size: a group whose EVERY option the size excludes is hidden by that size and DROPPED, required or not
//     (Spicy Beef "Just the Burger" drops the meal side and drink; REF sizeModifierExclusions);
//   - availability: a group the size keeps options of, but none of them is available (sold out, outside its window),
//     is DROPPED when optional and BLOCKED when required (min ≥ 1): it stays, with options [], blocked: true and
//     reason = hint = '<Group> is unavailable right now', so validate() fails and view() reads "Unavailable". One
//     unavailable option plus the size excluding the rest is therefore blocked (the size didn't hide the group).
//   A group unavailable as a whole (Group.available false) or with no options at all is dropped, as before.
//
// Free modifier allowances (B §3.1, B-Δ1; the server's ModifierPricingService::allocate). Group.freeCount (the
// group's max_no_price_count; missing = 0) units of a group are free for ONE unit of the line. allocateFree walks the
// rows in order (group order, then first-pick order within a group) and each row takes min(row count, what is left
// of its group's allowance); zero-priced rows use it up too. Per unit × qty equals the server, which allocates
// allowance × productCount over count × qty units in the same row order: by induction over the rows, the server's
// remaining allowance is always qty × ours, so each row gets exactly qty × our free units and the line total is
// qty × unitPrice.
//
// Sizes. Start with the first available size marked `preselect`, otherwise the first available size in menu order.
// Before a size is chosen every group shows (nothing is excluded yet) and the base price is the product's. Picking another
// size RESETS the selection to that size's defaults (defaults()), as REF does. A size that is unavailable is listed
// but can't be picked; with none available the item is blocked.
//
// Moments (P2-20, D-07). Availability is normalised into the menu at one time and domain. The sheet judges its sizes,
// groups and options at the moment the order is for with withAvailability(product, the same product from
// OO.data.menuAt at that moment); every rule here then reads `available` as before.
//
// API:
//   initial(product) → { sizeId, sel }. sizeId = the first AVAILABLE size whose row has `preselect`, else the first
//       available size, or null when none is available; sel = defaults(product, sizeId).
//   defaults(product, sizeId) → sel: the preselect options of the groups visible at that size (a max-1 group takes its
//       first, a multi group up to its max). Repeatable required groups repeat their preselects up to the minimum,
//       within the maximum. The selection after a size change.
//   withAvailability(product, at) → product with each size's, group's and option's `available` taken from `at` (the
//       same product normalised at another moment and domain); prices, names and everything else stay `product`'s,
//       and an id `at` lacks keeps its own flag. The SAME object comes back when no flag differs, or for a null `at`.
//   visibleGroups(product, sizeId) → Group[]. Available groups with the available options the size does not exclude,
//       plus the blocked required groups ({ …Group, options: [], blocked: true, reason, hint: reason }); other emptied
//       groups are DROPPED (see "Hidden vs blocked").
//   sizeGroup(product, sizeId) → null | { id:'size', name:'Sizes', hint:'Choose 1', badge:'Done', required:true,
//       options:[{ id, name, price, delta, priceLabel ('+$6' vs base), absLabel ('$31.90'), on }] } (every size; the
//       sheet renders view().sizes, which adds availability)
//   toggle(group, sel, optionId) → sel'. When max===1 it acts as a radio: a required radio can't be deselected,
//       an optional radio deselects. A multi-choice group is capped at max (a full group ignores new picks).
//   increment(group, sel, optionId) / decrement(group, sel, optionId) → sel'. allow_same counters; the total
//       is capped at max. decrement removes the LAST pick of the option.
//   count(sel, groupId[, optionId]) → number.  isOn(sel, groupId, optionId) → bool.
//   validate(groups, sel) → { ok, missing:[groupId], blocked?:[groupId] }. A blocked group is always missing, and
//       `blocked` (present only when non-empty) lists those. Other groups with no options are skipped.
//       Optional groups may be empty; a nonempty selection must meet their configured minRaw.
//   groupBadge(group, sel) → 'Required' | 'Done' | 'Optional'.
//   groupSummary(group, sel) → 'Brioche Bun', '2 × Extra shot, Vanilla' or the hint ('Choose up to 1').
//   prune(product, sizeId, sel) → sel without options the size hides (call after a size change).
//   selectedOptions(groups, sel) → [{ addOnId, groupId, name, price, count, free }]. count and free are PER UNIT (§8),
//       in group order and then FIRST-PICK order (the order of sel[groupId]), so the server allocates the same units.
//       These are the cart line's modifiers, and the payload sends them in this order.
//   allocateFree(rows, freeByGroup) → the rows as new objects, each with free = min(count, what is left of its group's
//       allowance), walked in row order; zero-priced rows consume too. freeByGroup = { [groupId]: freeCount } (a group
//       not in it has no allowance).
//   freeByGroupOf(groups) → { [groupId]: freeCount } (Group.freeCount; missing / negative / junk → 0).
//   unitPrice(product, sizeId, sel) → (size price || base price) + Σ option price × (count − free).
//   view(product, { sizeId, sel, qty }) → everything the item sheet renders (sizeId undefined = initial()'s):
//       { sizes: null | { …sizeGroup, badge ('Required' until a size is chosen, then 'Done'), chosen (bool), blocked
//           (no size is available), reason / hint ('No size is available right now' when blocked), options: [{ …,
//           on (the chosen size, while available), disabled (unavailable), reason ('Unavailable' | null) }] },
//         sizeId (the chosen size while it is available, else null: the size the groups and prices use), groups:[{ id,
//         name, hint ('Choose up to 3 · First 1 free' with an allowance), badge, summary, required, radio, multi,
//         counter, full, freeCount, blocked, reason (null unless blocked), alerts ([{ id, name, text }]: the picked
//         options' alerts), options:[{ id, name, price, priceLabel, image, on, count, free (its free units), disabled,
//         alert (Option.alert's text | null) }] }], unitPrice, total, totalLabel (allowance-aware: what OO.cart.itemLine
//         charges, so what the payload sends), qty, ok, missing ('size' first while no size is chosen, then group ids),
//         blocked ('size' when no size is available, then group ids), ctaEnabled, ctaLabel ('Add to cart' | 'Choose a
//         size' | 'Choose a base' | 'Unavailable' when something is blocked) }
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function F() { return OO.format; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function list(sel, groupId) { return arr(sel && sel[groupId]); }

  function sizeOf(product, sizeId) {
    return arr(product && product.sizes).filter(function (s) { return s.id === sizeId; })[0] || null;
  }

  // Size exclusion first, then availability (see the header): a group the size empties is dropped; a required group
  // that availability empties is kept, blocked.
  function visibleGroups(product, sizeId) {
    var size = sizeOf(product, sizeId);
    var hidden = size ? arr(size.excludeOptionIds) : [];
    var out = [];
    arr(product && product.groups).forEach(function (g) {
      if (g.available === false) return;
      var all = arr(g.options);
      var kept = all.filter(function (o) { return hidden.indexOf(o.id) < 0; });
      var options = kept.filter(function (o) { return o.available !== false; });
      if (options.length) out.push(options.length === all.length ? g : Object.assign({}, g, { options: options }));
      else if (kept.length && g.required) {
        var reason = (g.name || 'This choice') + ' is unavailable right now';
        out.push(Object.assign({}, g, { options: [], blocked: true, reason: reason, hint: reason }));
      }
    });
    return out;
  }

  function count(sel, groupId, optionId) {
    var l = list(sel, groupId);
    if (optionId === undefined) return l.length;
    return l.filter(function (x) { return x === optionId; }).length;
  }
  function isOn(sel, groupId, optionId) { return count(sel, groupId, optionId) > 0; }
  function distinct(l) { return l.filter(function (x, i) { return l.indexOf(x) === i; }); }

  function withGroup(sel, groupId, next) {
    var out = Object.assign({}, sel || {});
    if (next.length) out[groupId] = next; else delete out[groupId];
    return out;
  }

  function toggle(group, sel, optionId) {
    var cur = list(sel, group.id);
    var on = cur.indexOf(optionId) >= 0;
    var next;
    if (group.max === 1) next = on ? (group.required ? cur : []) : [optionId];
    else if (on) next = cur.filter(function (x) { return x !== optionId; });
    else next = (group.allowSame ? cur.length : distinct(cur).length) < group.max ? cur.concat([optionId]) : cur;
    return next === cur ? sel : withGroup(sel, group.id, next);
  }

  function increment(group, sel, optionId) {
    var cur = list(sel, group.id);
    if (!group.allowSame) return isOn(sel, group.id, optionId) ? sel : toggle(group, sel, optionId);
    if (group.max === 1) return toggle(group, sel, optionId);
    return cur.length < group.max ? withGroup(sel, group.id, cur.concat([optionId])) : sel;
  }

  function decrement(group, sel, optionId) {
    var cur = list(sel, group.id);
    var at = cur.lastIndexOf(optionId);
    if (at < 0) return sel;
    if (group.max === 1 && group.required && cur.length === 1) return sel;
    return withGroup(sel, group.id, cur.slice(0, at).concat(cur.slice(at + 1)));
  }

  function validate(groups, sel) {
    var missing = [], blocked = [];
    arr(groups).forEach(function (g) {
      if (g.blocked) { missing.push(g.id); blocked.push(g.id); }
      else if (arr(g.options).length > 0) {
        var selected = count(sel, g.id);
        var minimum = g.required ? Math.max(1, g.min) : Math.max(0, g.minRaw == null ? g.min : g.minRaw);
        if ((g.required || selected > 0) && selected < minimum) missing.push(g.id);
      }
    });
    var out = { ok: missing.length === 0, missing: missing };
    if (blocked.length) out.blocked = blocked;
    return out;
  }

  function groupBadge(group, sel) {
    if (!group.required) return 'Optional';
    return count(sel, group.id) >= Math.max(1, group.min) ? 'Done' : 'Required';
  }

  function groupSummary(group, sel) {
    var l = list(sel, group.id);
    if (!l.length) return group.hint || '';
    return distinct(l).map(function (id) {
      var o = arr(group.options).filter(function (x) { return x.id === id; })[0];
      var n = count(sel, group.id, id);
      return o ? (n > 1 ? n + ' × ' : '') + o.name : '';
    }).filter(Boolean).join(', ');
  }

  function prune(product, sizeId, sel) {
    var groups = visibleGroups(product, sizeId);
    var out = {};
    groups.forEach(function (g) {
      var ids = g.options.map(function (o) { return o.id; });
      var kept = list(sel, g.id).filter(function (id) { return ids.indexOf(id) >= 0; });
      if (kept.length) out[g.id] = kept;
    });
    return out;
  }

  // ---- free modifier allowances (see the header) ------------------------------------------------------
  // Group.freeCount as a whole number of units ≥ 0; missing, negative or junk = 0 (no allowance).
  function freeCountOf(group) {
    var n = Math.floor(Number(group && group.freeCount));
    return isFinite(n) && n > 0 ? n : 0;
  }

  function freeByGroupOf(groups) {
    var out = {};
    arr(groups).forEach(function (g) { if (g && g.id != null && !out[g.id]) out[g.id] = freeCountOf(g); });
    return out;
  }

  // ModifierPricingService::allocate on one unit of a line: rows in order, each takes min(count, what is left of its
  // group's allowance). Zero-priced rows consume it too. New row objects; the inputs are left alone.
  function allocateFree(rows, freeByGroup) {
    var left = {};
    Object.keys(freeByGroup || {}).forEach(function (k) { left[k] = freeCountOf({ freeCount: freeByGroup[k] }); });
    return arr(rows).map(function (r) {
      var k = String(r.groupId);
      var has = Object.prototype.hasOwnProperty.call(left, k);
      var free = Math.min(Math.max(0, Number(r.count) || 0), has ? left[k] : 0);
      if (has) left[k] -= free;
      return Object.assign({}, r, { free: free });
    });
  }

  // Rows in group order and, within a group, in FIRST-PICK order (sel order), so the cart line, its payload rows and
  // the server's allocation agree on which units are free. A repeated id merges into count.
  function selectedOptions(groups, sel) {
    var out = [];
    arr(groups).forEach(function (g) {
      distinct(list(sel, g.id)).forEach(function (id) {
        var o = arr(g.options).filter(function (x) { return x.id === id; })[0];
        if (o) out.push({ addOnId: o.id, groupId: g.id, name: o.name, price: o.price, count: count(sel, g.id, id) });
      });
    });
    return allocateFree(out, freeByGroupOf(groups));
  }

  function basePrice(product, sizeId) {
    var size = sizeOf(product, sizeId);
    return size ? size.price : Number(product && product.price) || 0;
  }

  // Σ price × charged units (count − free) of selectedOptions rows.
  function modsTotal(rows) {
    return arr(rows).reduce(function (s, m) { return s + m.price * (m.count - (m.free || 0)); }, 0);
  }

  function unitPrice(product, sizeId, sel) {
    return F().round2(basePrice(product, sizeId) + modsTotal(selectedOptions(visibleGroups(product, sizeId), sel)));
  }

  function defaults(product, sizeId) {
    var sel = {};
    visibleGroups(product, sizeId).forEach(function (g) {
      var pre = g.options.filter(function (o) { return o.preselect; }).map(function (o) { return o.id; });
      if (!pre.length) return;
      var picked = g.max === 1 ? [pre[0]] : pre.slice(0, g.max);
      // The minimum counts units when repeats are allowed: one configured default can supply two required units.
      if (g.allowSame) {
        for (var i = 0; picked.length < g.min && picked.length < g.max; i++) picked.push(pre[i % pre.length]);
      }
      sel[g.id] = picked;
    });
    return sel;
  }

  // Preserve an available configured default, otherwise start at the first available size in displayed order.
  function initial(product) {
    var available = arr(product && product.sizes).filter(function (s) { return s.available !== false; });
    var pre = available.filter(function (s) { return s.preselect; })[0] || available[0];
    var sizeId = pre ? pre.id : null;
    return { sizeId: sizeId, sel: defaults(product, sizeId) };
  }

  // ---- availability at another moment (see "Moments" in the header) -------------------------------------
  function byId(list) {
    var out = {};
    arr(list).forEach(function (x) { if (x && x.id != null && !out[x.id]) out[x.id] = x; });
    return out;
  }
  function sameFlag(a, b) { return (a.available !== false) === (b.available !== false); }
  function withFlag(x, from) { return Object.assign({}, x, { available: from.available !== false }); }

  function withAvailability(product, at) {
    if (!product || !at || at === product) return product;
    var changed = false;
    var sizesAt = byId(at.sizes), groupsAt = byId(at.groups);
    var sizes = arr(product.sizes).map(function (s) {
      var a = sizesAt[s.id];
      if (!a || sameFlag(a, s)) return s;
      changed = true;
      return withFlag(s, a);
    });
    var groups = arr(product.groups).map(function (g) {
      var a = groupsAt[g.id];
      if (!a) return g;
      var optionsAt = byId(a.options), moved = !sameFlag(a, g);
      var options = arr(g.options).map(function (o) {
        var ao = optionsAt[o.id];
        if (!ao || sameFlag(ao, o)) return o;
        moved = true;
        return withFlag(o, ao);
      });
      if (!moved) return g;
      changed = true;
      return Object.assign(withFlag(g, a), { options: options });
    });
    return changed ? Object.assign({}, product, { sizes: sizes, groups: groups }) : product;
  }

  function sizeGroup(product, sizeId) {
    var sizes = arr(product && product.sizes);
    if (!sizes.length) return null;
    var f = F();
    var base = Number(product.price) || 0;
    return {
      id: 'size', name: 'Sizes', hint: 'Choose 1', badge: 'Done', required: true,
      options: sizes.map(function (s) {
        var delta = f.round2(s.price - base);
        return { id: s.id, name: s.name, price: s.price, delta: delta, priceLabel: f.plusMoney(delta), absLabel: f.money(s.price), on: s.id === sizeId };
      }),
    };
  }

  function article(noun) { return /^[aeiou]/i.test(noun) ? 'an ' : 'a '; }

  // view().sizes (see the API): sizeGroup with each size's availability. An unavailable size stays listed, disabled
  // (as the combo builder's size step), and is never the chosen one.
  var NO_SIZE = 'No size is available right now';
  function sizesView(product, sizeId) {
    var g = sizeGroup(product, sizeId);
    if (!g) return null;
    var sizes = byId(product.sizes);
    var options = g.options.map(function (o) {
      var off = !!sizes[o.id] && sizes[o.id].available === false;
      return Object.assign({}, o, { on: o.on && !off, disabled: off, reason: off ? 'Unavailable' : null });
    });
    var chosen = options.some(function (o) { return o.on; });
    var blocked = !options.some(function (o) { return !o.disabled; });
    return Object.assign({}, g, {
      hint: blocked ? NO_SIZE : g.hint, badge: chosen ? 'Done' : 'Required', options: options,
      chosen: chosen, blocked: blocked, reason: blocked ? NO_SIZE : null,
    });
  }

  // An option's alert text (Option.alert = { text }: REF is_alert + alert_text), or null.
  function alertOf(o) {
    var t = o && o.alert && typeof o.alert === 'object' ? String(o.alert.text == null ? '' : o.alert.text).trim() : '';
    return t || null;
  }

  function view(product, state) {
    state = state || {};
    var f = F();
    var wanted = state.sizeId === undefined ? initial(product).sizeId : state.sizeId;
    var sizes = sizesView(product, wanted);
    // The size the groups and prices use: the chosen one while it is available; none before a size is chosen.
    var sizeId = sizes && !sizes.chosen ? null : wanted;
    var sel = state.sel || {};
    var qty = Math.max(1, Number(state.qty) || 1);
    var groups = visibleGroups(product, sizeId);
    var v = validate(groups, sel);
    var missing = (sizes && !sizes.chosen ? ['size'] : []).concat(v.missing);
    var blocked = (sizes && sizes.blocked ? ['size'] : []).concat(v.blocked || []);
    var ok = missing.length === 0;
    var rows = selectedOptions(groups, sel);
    var unit = f.round2(basePrice(product, sizeId) + modsTotal(rows));
    var firstMissing = groups.filter(function (g) { return g.id === missing[0]; })[0];
    var noun = missing[0] === 'size' ? 'size'
      : firstMissing ? (OO.model && OO.model.nounOf ? OO.model.nounOf(firstMissing.name) : firstMissing.name.toLowerCase()) : '';
    return {
      sizes: sizes,
      sizeId: sizeId,
      groups: groups.map(function (g) {
        var taken = g.allowSame ? count(sel, g.id) : distinct(list(sel, g.id)).length;
        var full = g.max > 1 && taken >= g.max;
        var freeCount = freeCountOf(g);
        // "Choose up to 3 · First 1 free" (REF "· First N Free"); a blocked group's hint is its reason.
        var hint = freeCount > 0 && !g.blocked ? (g.hint ? g.hint + ' · ' : '') + 'First ' + freeCount + ' free' : g.hint;
        var options = g.options.map(function (o) {
          var n = count(sel, g.id, o.id);
          var row = rows.filter(function (r) { return r.groupId === g.id && r.addOnId === o.id; })[0];
          return {
            id: o.id, name: o.name, price: o.price, priceLabel: f.plusMoney(o.price), image: o.image, on: n > 0, count: n,
            free: row ? row.free : 0, disabled: full && n === 0, alert: alertOf(o),
          };
        });
        return {
          id: g.id, name: g.name, hint: hint, badge: groupBadge(g, sel), summary: list(sel, g.id).length ? groupSummary(g, sel) : hint || '',
          required: g.required, radio: g.max === 1, multi: g.max > 1 && !g.allowSame, counter: g.allowSame && g.max > 1, full: full,
          freeCount: freeCount, blocked: !!g.blocked, reason: g.blocked ? g.reason : null,
          alerts: options.filter(function (o) { return o.on && o.alert; }).map(function (o) { return { id: o.id, name: o.name, text: o.alert }; }),
          options: options,
        };
      }),
      unitPrice: unit,
      qty: qty,
      total: f.round2(unit * qty),
      totalLabel: f.money(unit * qty),
      ok: ok,
      missing: missing,
      blocked: blocked,
      ctaEnabled: ok,
      ctaLabel: ok ? 'Add to cart' : blocked.length ? 'Unavailable' : 'Choose ' + article(noun) + noun,
    };
  }

  OO.modifiers = OO.lib.modifiers = {
    initial: initial,
    defaults: defaults,
    withAvailability: withAvailability,
    visibleGroups: visibleGroups,
    sizeGroup: sizeGroup,
    toggle: toggle,
    increment: increment,
    decrement: decrement,
    count: count,
    isOn: isOn,
    validate: validate,
    groupBadge: groupBadge,
    groupSummary: groupSummary,
    prune: prune,
    selectedOptions: selectedOptions,
    allocateFree: allocateFree,
    freeByGroupOf: freeByGroupOf,
    basePrice: basePrice,
    unitPrice: unitPrice,
    view: view,
  };
})();


// ===================== src/lib/combo.jsx =====================
// src/lib/combo.jsx: OO.combo (alias OO.lib.combo). A pure port of the design's comboVals state machine
// (docs/online-order/design/ordering-flow.dc.txt ~L1401–1457, K01/K02/K03) over the normalised Combo of
// OO.model. Reducer-style: every action takes (state, combo, …) and returns a NEW state. view() derives
// everything the page renders, so the combo page is pure rendering. No JSX.
//
// Steps: one step per combo group, as in the POS.
//   * max > 1                        → ONE multi-pick step (choose min..max, including an exact quantity).
//   * otherwise                       → one single-pick step. With min 0 it is optional and "Skip" continues.
//   A single step whose group has exactly one option is `fixed`. A fixed step with nothing required to choose (no
//   size choice, no required modifiers) is `auto`: it shows as "added automatically" and the flow skips it, but
//   Review can Customise it.
// Phases: intro (g = -1) → for each non-auto step: item pick → options (only if the pick has something to choose: a
//   size choice or option groups) → … → review (g = L) → add to cart. A Change/Customise from Review sets `ret`, so
//   the step goes straight back to Review. An overview gear opens its group with ret='intro' and returns to the overview.
// Sizes (ComboOption.sizeMode; B §1.2 "Combo builder"; bug 3):
//   'choice'  the pick asks for a size, first in its options phase: required, NOTHING preselected (REF forces a choice,
//             even with one size; Size.preselect is not read here). Until a size is chosen the phase shows no option
//             groups (the size decides which are visible) and the CTA reads "Choose a size". The first size brings the
//             preselect options of THAT size; a later change prunes what the new size hides (the item sheet's rule).
//             The chosen size's excludeOptionIds prune the groups (OO.modifiers.visibleGroups).
//   'fixed'   the combo sets the size (ComboOption.sizeId): auto-selected, shown read-only ("Size · Set By Combo").
//   'none'    no size step.
//   The size price is NEVER charged (the combo price stands), but the pick carries the size: entries() / line() hand
//   OO.cart.comboLine the option AT the chosen size (sizeId, sizeName, price = the size's), so the cart pick has the
//   real size row and the child sale_price the V4 validator checks (OO.payload sends price and size[] from the pick).
// Orderable (P1-09b blocked groups): an option is orderable when it is available AND can be completed. A 'choice'
//   option needs one usable size (available, no BLOCKED required group under it); any other option no blocked group
//   at its own size. An unorderable option is disabled in the picker with its reason ('Sauce is unavailable right
//   now') and never opens an impossible options phase.
// The `min` rule (P2-15; REF filterAvailable hasEnoughChoices, as OO.model): a required group (min ≥ 1) needs `min`
//   orderable options, or one when allow_same (a pick may repeat; without it the sibling steps, or a multi step, need
//   distinct options). A group short of that makes the deal unavailable: view().unavailableGroup (OO.model's own,
//   else that group's name), view().unavailableReason ('Pizza Requires 2 Choices, But Only 1 Is Available.':
//   OO.model.requiredChoiceLabel), view().available false. A combo is never added until the deal is available and
//   every step, so every group, holds its minimum: Review's ctaEnabled is false otherwise and cta() there is a no-op.
//   (The checkout validator checks no group minimum on the path the SPA uses, so this is the only gate.)
// Price: combo.price + Σ extraPrice + Σ modifier option prices × charged units (count − free: each pick's
//   OO.modifiers allowances, on its own product's groups at its size). The product (and size) price is NEVER added
//   (API-CONTRACT §7). Free units read "Free" on the option rows and "(Free)" / "(1 free)" in the review text
//   (OO.cart.displayMods' tags); a group with an allowance hints "· First N free" (as OO.modifiers.view).
// Next parity: options with is_default (and orderable) are preselected, with their preselect modifiers.
//   allow_same=0 disables an option already picked in a sibling step of the same group.
//
// API:
//   withAvailability(combo, at) → combo with availability from another moment; existing prices are retained.
//   init(combo) → state.
//       state = { g, ph:'item'|'mods', mi, picks:{[stepIndex]: [{mappingId, mods, sizeId?}]}, skipped:{[stepIndex]:true},
//                 ret:false|true|'intro', qty, notes, added, addedLine }      (sizeId: a 'choice' pick's size, once chosen)
//   fromLine(combo, line) → state for editing a cart line (OO.cart.comboLine's shape): merged quantities expand within
//       their groups, auto-added picks (a Customise) are kept, empty optional steps are skipped, options are
//       pruned at each pick's size; a 'choice' pick gets its size back while the option still offers it (else it
//       asks again). It opens at the first incomplete step, else at Review.
//   steps(combo) → Step[]: { index, groupIndex, groupId, name, full, noun, copy, copies, multi, optional,
//       fixed, auto, min, max }
//   Actions (all return a new state):
//     start(s, c)                       intro CTA ("Start") when required choices remain
//     pickItem(s, c, mappingId)         single step: pick (nothing to choose → auto-advance; a size or options → the
//                                       options phase). multi step: toggle (allowSame: add one more). Unorderable → no-op.
//     removeItem(s, c, mappingId)       multi + allowSame: remove one
//     pickSize(s, c, sizeId)            the current pick's size ('choice' picks, options phase). An unusable size, or the
//                                       one already chosen, is a no-op.
//     toggleMod(s, c, groupId, optionId)  / incMod / decMod     on the current pick (OO.modifiers rules)
//     cta(s, c)                         the bottom CTA. At review, or an intro with valid preselected picks, it resets to intro with added:true and
//                                       addedLine = the cart line (OO.cart.comboLine) for the page to add; a no-op
//                                       while the deal is unavailable or a step lacks its minimum (the `min` rule).
//     back(s, c)   goStep(s, c, i) (step bar; reachable only)   change(s, c, i) (review Change/Customise)
//     customiseGroup(s, c, i)           overview gear: open any group, return to the overview when done
//     changeItem(s, c) (mods phase "Change")   dismissAdded(s)
//     setQty(s, n) | setQty(s, c, n)    setNotes(s, text) | setNotes(s, c, text)   (both call shapes; the combo is
//                                       not needed, the 3-argument form matches the other actions)
//   view(s, c, { currency = '$' }) → { isIntro, notIntro, isReview, notReview, isItem, isMods, added, stepLabel,
//       ('2 items · $12.90' | 'Step 2 of 3 · Sides' | 'Customise · Chips' for an auto-added step opened from Review
//       | 'Review your combo'); every money label uses `currency` (the store's, e.g. OO.model Store.currency),
//       available (false: the deal can't be ordered now), unavailableGroup (null | the group that blocks it),
//       unavailableReason (null | why it can't: OO.model's Combo.inactiveReason, else the `min` rule's label),
//       steps:[{index, num ('1'|'✓'), name, label, summary, state 'done'|'current'|'todo', reachable}],
//       addFromIntro (all required choices met, selected optional choices valid),
//       includes:[{num, index (step index or null), label, sub (selected choices or 'None selected'), done}], groupTitle, groupName, groupHint, multi,
//       items:[{key, mappingId, img, name, desc, price ('+$2.00'|''), extraPrice, included, hasMods (a size or
//               options to choose next), on, count, disabled, soldOut, reason (null | why an available option can't
//               be ordered: 'Sauce is unavailable right now')}],
//       curItem, curKey, curImg, curFixed, canChangeItem,
//       curSize: null | { mode 'fixed', id, name, label 'Size · Set By Combo', locked true }
//                     | { mode 'choice', id, name (null until chosen), label 'Size', locked false },
//       sizes: null | the current 'choice' pick's size group, in the mods shape below: { id 'size', name 'Size', hint
//              'Choose 1', badge 'Required'|'Done', required true, radio true, opts:[{ id (Size.id), label, price
//              ('' — or 'Unavailable' for a disabled size), on, count, disabled, reason }] },
//       mods:[{id, name, hint ('Choose up to 3 · First 1 free' with an allowance), badge, required, radio, check,
//              counter, full, blocked, opts:[{id, label, price ('+$2.00' | 'Free' once every picked unit is free |
//              '+$2.00 · 1 free'), on, count, free, radio, check, disabled}]}]   ([] while a 'choice' pick has no size),
//       review:[{index, key, img, canChange, changeLabel ('Change'|'Customise'), group, item, mods ('Large · Classic ·
//                Extra cheese (Free)': a chosen size first, free tags), hasMods, price, extraPrice}],
//       unitPrice, total ('$29.90'), ctaLabel, ctaEnabled, ctaPrice ('$59.80' at review), qty, decEnabled, notes,
//       missing: ['items' | 'size' | modifierGroupId] (required choices in the current phase) }
//   CTA labels: 'Start', 'Choose a {noun}', 'Continue', 'Skip', 'Choose a size', 'Make required
//       choices', 'Unavailable' (a blocked group), 'Continue to {next step}', 'Review combo', 'Add to cart'.
//   entries(s, c) → [{ step, groupId, mappingId, option, mods, sizeId, sizeName, rows }]: the picks in step order (auto
//       steps included). option = the ComboOption, or for a 'choice' pick with its size a copy AT that size (sizeId,
//       sizeName, price = the size's: what OO.cart.comboLine reads); sizeId / sizeName = the pick's size (fixed or
//       chosen; null for none or not chosen yet); rows = OO.modifiers.selectedOptions at that size (with `free`).
//   unitPrice(s, c) → number (allowance-aware).   line(s, c) → the cart line (OO.cart.comboLine of entries()).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function F() { return OO.format; }
  function M() { return OO.modifiers; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function slug(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }

  // Keep the priced combo, but judge its choices, sizes and modifiers at the order's selected time/channel.
  // Group IDs and mapping IDs distinguish repeated products and choices with the same display name.
  function withAvailability(combo, at) {
    if (!combo || !at || at === combo) return combo;
    var changed = false;
    var groups = arr(combo.groups).map(function (g) {
      var ga = arr(at.groups).filter(function (x) { return x.id === g.id; })[0];
      if (!ga) return g;
      var moved = false;
      var options = arr(g.options).map(function (o) {
        var oa = arr(ga.options).filter(function (x) { return x.mappingId === o.mappingId; })[0];
        if (!oa) return o;
        var next = M().withAvailability(o, oa);
        if ((o.available !== false) !== (oa.available !== false)) {
          next = Object.assign({}, next, { available: oa.available !== false });
        }
        if (next !== o) moved = true;
        return next;
      });
      if (!moved) return g;
      changed = true;
      return Object.assign({}, g, { options: options });
    });
    var flags = {};
    ['available', 'unavailableReason', 'inactiveReason', 'unavailableGroup'].forEach(function (key) {
      if (combo[key] !== at[key]) { changed = true; flags[key] = at[key]; }
    });
    return changed ? Object.assign({}, combo, flags, { groups: groups }) : combo;
  }

  // ---- sizes and orderability (see the header) -----------------------------------------------------------
  function isChoice(option) { return !!option && option.sizeMode === 'choice' && arr(option.sizes).length > 0; }
  function sizeById(option, id) {
    if (id == null || id === '') return null;
    var n = Number(id);
    return arr(option && option.sizes).filter(function (z) { return z.id === n; })[0] || null;
  }
  function blockedGroup(option, sizeId) {
    return M().visibleGroups(option, sizeId).filter(function (g) { return g.blocked; })[0] || null;
  }
  // Why a size can't be picked, or null: its own availability, else a blocked required group under it.
  function sizeProblem(option, z) {
    if (z.available === false) return 'Not available right now';
    var b = blockedGroup(option, z.id);
    return b ? b.reason : null;
  }
  function usableSize(option, id) {
    var z = sizeById(option, id);
    return z && !sizeProblem(option, z) ? z : null;
  }
  // Why an AVAILABLE option can't be completed, or null (an unavailable one keeps its own flags: soldOut, available).
  function optionProblem(option) {
    if (!option || option.available === false) return null;
    if (isChoice(option)) {
      var sizes = arr(option.sizes), why = null;
      for (var i = 0; i < sizes.length; i++) {
        var p = sizeProblem(option, sizes[i]);
        if (!p) return null;
        if (!why && sizes[i].available !== false) why = p;
      }
      return why || 'No size is available right now';
    }
    var b = blockedGroup(option, option.sizeId);
    return b ? b.reason : null;
  }
  function orderable(option) { return !!option && option.available !== false && !optionProblem(option); }

  // The size a pick is pruned and validated at: a 'choice' pick's chosen (usable) size, else the option's own.
  function sizeIdOf(option, entry) {
    if (isChoice(option)) { var z = usableSize(option, entry && entry.sizeId); return z ? z.id : null; }
    return option && option.sizeId != null ? option.sizeId : null;
  }
  // The option groups of a pick at its size; none for a 'choice' pick until its size is chosen.
  function groupsOf(option, entry) {
    if (!option) return [];
    if (isChoice(option)) { var id = sizeIdOf(option, entry); return id == null ? [] : M().visibleGroups(option, id); }
    return M().visibleGroups(option, option.sizeId);
  }
  // Something to choose after the pick (REF comboChildNeedsCustomization): a size choice or any option group.
  function hasChoices(option) { return isChoice(option) || groupsOf(option, null).length > 0; }
  function hasRequired(option) { return isChoice(option) || groupsOf(option, null).some(function (g) { return g.min > 0; }); }

  // How a pick is ordered: a 'choice' pick → a copy of the option at its chosen size (price = the size's); any other
  // option as it is. sizeId / sizeName are the pick's size (null for none, or a 'choice' size not chosen yet).
  function orderedAs(option, entry) {
    if (!isChoice(option)) return { option: option, sizeId: option.sizeId == null ? null : option.sizeId, sizeName: option.sizeName || null };
    var z = usableSize(option, entry && entry.sizeId);
    if (!z) return { option: option, sizeId: null, sizeName: null };
    return { option: Object.assign({}, option, { sizeId: z.id, sizeName: z.name, price: z.price }), sizeId: z.id, sizeName: z.name };
  }

  // ---- steps -------------------------------------------------------------------------------------
  var stepCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  function steps(combo) {
    if (stepCache && combo && stepCache.has(combo)) return stepCache.get(combo);
    var out = [];
    arr(combo && combo.groups).forEach(function (gr, gi) {
      var options = arr(gr.options);
      if (!options.length) return;
      var multi = gr.max > 1;
      // Implicit fixed inclusions keep their configured quantity. An explicit
      // default is one unit, including when it is the group's only option.
      var fixed = gr.min >= 1 && gr.min === gr.max && options.length === 1
        && (gr.max === 1 || !options[0].isDefault);
      out.push({
        index: out.length,
        groupIndex: gi,
        groupId: gr.id,
        group: gr,
        copy: 0,
        copies: 1,
        name: gr.name,
        full: quantityLabel(gr.min, gr.max) + ' × ' + gr.name,
        noun: gr.noun,
        multi: multi,
        optional: !multi && gr.min === 0,
        min: multi ? gr.min : (gr.min === 0 ? 0 : 1),
        max: multi ? gr.max : 1,
        fixed: fixed,
        auto: fixed && orderable(options[0]) && !hasRequired(options[0]),
      });
    });
    if (stepCache && combo) stepCache.set(combo, out);
    return out;
  }

  function quantityLabel(min, max) {
    return min === max ? String(max) : (min > 0 ? min + '–' : 'Up to ') + max;
  }

  // A fixed step whose only option can actually be ordered: it opens straight on that option's choices.
  function heldFixed(st) { return !!st && st.fixed && orderable(st.group.options[0]); }

  function optionOf(step, mappingId) {
    return arr(step && step.group.options).filter(function (o) { return o.mappingId === mappingId; })[0] || null;
  }

  // The picks of step i. A fixed step implicitly holds its only option — unless that option can't be ordered (sold
  // out, unavailable, a blocked group): then the step is simply unfilled (and the combo can't be completed).
  function picksOf(s, combo, i) {
    var st = steps(combo)[i];
    var p = s.picks && s.picks[i];
    if (p && p.length) return p;
    if (heldFixed(st)) return Array.from({ length: st.min }, function () { return freshEntry(st.group.options[0]); });
    return [];
  }

  function initialMods(option, sizeId) {
    return M().defaults(option, sizeId);
  }
  // A new pick: the preselect options at the option's size. A 'choice' pick starts empty: its preselects come with
  // the size (pickSize).
  function freshEntry(option) {
    return { mappingId: option.mappingId, mods: isChoice(option) ? {} : initialMods(option, option.sizeId) };
  }

  function entryMet(step, entry) {
    var o = optionOf(step, entry.mappingId);
    if (!o || (isChoice(o) && sizeIdOf(o, entry) == null)) return false;
    return M().validate(groupsOf(o, entry), entry.mods).ok;
  }

  // Requirement met: picks ≥ min, every pick's size (a 'choice') and required modifiers set. An optional step counts
  // once picked or skipped.
  function stepSatisfied(s, combo, i) {
    var st = steps(combo)[i];
    var p = picksOf(s, combo, i);
    if (p.length < st.min || p.length > st.max) return false;
    if (!p.length) return !!(s.skipped && s.skipped[i]) || st.multi;
    return p.every(function (e) { return entryMet(st, e); });
  }

  function hasPick(s, combo, i) { return picksOf(s, combo, i).length > 0; }
  function stepHasMods(s, combo, i) {
    var st = steps(combo)[i];
    return picksOf(s, combo, i).some(function (e) { return hasChoices(optionOf(st, e.mappingId)); });
  }
  function modEntryIndexes(s, combo, i) {
    var st = steps(combo)[i];
    return picksOf(s, combo, i).map(function (e, k) { return hasChoices(optionOf(st, e.mappingId)) ? k : -1; }).filter(function (k) { return k >= 0; });
  }

  function stepIdx(combo) { return steps(combo).filter(function (st) { return !st.auto; }).map(function (st) { return st.index; }); }
  function nextStep(combo, gi) { var j = stepIdx(combo).filter(function (x) { return x > gi; })[0]; return j == null ? steps(combo).length : j; }
  function prevStep(combo, gi) { var p = stepIdx(combo).filter(function (x) { return x < gi; }); return p.length ? p[p.length - 1] : -1; }

  // ---- state ---------------------------------------------------------------------------------------
  function init(combo) {
    var picks = {};
    steps(combo).forEach(function (st) {
      if (st.fixed) return;
      var seen = new Set();
      var defaults = st.group.options.filter(function (o) {
        if (!o.isDefault || !orderable(o) || seen.has(o.mappingId)) return false;
        seen.add(o.mappingId);
        return true;
      });
      if (defaults.length) picks[st.index] = defaults.map(freshEntry);
    });
    return { g: -1, ph: 'item', mi: 0, picks: picks, skipped: {}, ret: false, qty: 1, notes: '', added: false, addedLine: null };
  }

  function set(s, o) { return Object.assign({}, s, { added: false, addedLine: null }, o); }

  function enter(s, combo, j, o) {
    var L = steps(combo).length;
    var st = steps(combo)[j];
    var base = { g: j, ph: j >= 0 && j < L && heldFixed(st) ? 'mods' : 'item', mi: 0 };
    if (base.ph === 'mods') base.mi = modEntryIndexes(s, combo, j)[0] || 0;
    return set(s, Object.assign(base, o || {}));
  }

  function next(s, combo, gi) {
    return enter(s, combo, s.ret === 'intro' ? -1 : s.ret ? steps(combo).length : nextStep(combo, gi), { ret: false });
  }

  function start(s, combo) { return s.g < 0 ? enter(s, combo, nextStep(combo, -1)) : s; }

  function withPicks(s, i, list) {
    var picks = Object.assign({}, s.picks);
    picks[i] = list;
    var skipped = Object.assign({}, s.skipped);
    delete skipped[i];
    return { picks: picks, skipped: skipped };
  }

  function siblingTaken(s, combo, st, mappingId) {
    if (st.group.allowSame || st.multi) return false;
    return steps(combo).some(function (o) {
      return o.index !== st.index && o.groupIndex === st.groupIndex && picksOf(s, combo, o.index).some(function (e) { return e.mappingId === mappingId; });
    });
  }

  function pickItem(s, combo, mappingId) {
    var L = steps(combo).length;
    var gi = s.g;
    var st = steps(combo)[gi];
    if (!st || s.ph !== 'item') return s;
    var option = optionOf(st, mappingId);
    if (!orderable(option) || siblingTaken(s, combo, st, mappingId)) return s;
    var cur = picksOf(s, combo, gi);
    if (st.multi) {
      var on = cur.some(function (e) { return e.mappingId === mappingId; });
      var list;
      if (on && !st.group.allowSame) list = cur.filter(function (e) { return e.mappingId !== mappingId; });
      else if (cur.length < st.max) list = cur.concat([freshEntry(option)]);
      else return s;
      return set(s, withPicks(s, gi, list));
    }
    // Re-picking the current item keeps its size and options (Back / Change from Review).
    var keep = cur.length === 1 && cur[0].mappingId === mappingId ? cur : [freshEntry(option)];
    var patch = withPicks(s, gi, keep);
    if (hasChoices(option)) return set(s, Object.assign(patch, { ph: 'mods', mi: 0 }));
    var j = s.ret === 'intro' ? -1 : s.ret ? L : nextStep(combo, gi);
    var nextFixed = j < L && heldFixed(steps(combo)[j]);
    var tmp = set(s, patch);
    return set(tmp, { g: j, ph: nextFixed ? 'mods' : 'item', mi: nextFixed ? (modEntryIndexes(tmp, combo, j)[0] || 0) : 0, ret: false });
  }

  function removeItem(s, combo, mappingId) {
    var st = steps(combo)[s.g];
    if (!st || !st.multi) return s;
    var cur = picksOf(s, combo, s.g);
    var at = -1;
    cur.forEach(function (e, k) { if (e.mappingId === mappingId) at = k; });
    if (at < 0) return s;
    return set(s, withPicks(s, s.g, cur.slice(0, at).concat(cur.slice(at + 1))));
  }

  // The current pick of the options phase: { st, list (the step's picks), entry, option } or null.
  function current(s, combo) {
    var st = steps(combo)[s.g];
    if (!st || s.ph !== 'mods') return null;
    var list = picksOf(s, combo, s.g);
    var entry = list[s.mi];
    return entry ? { st: st, list: list, entry: entry, option: optionOf(st, entry.mappingId) } : null;
  }
  function replaceEntry(s, cur, entry) {
    var list = cur.list.slice();
    list[s.mi] = entry;
    return set(s, withPicks(s, s.g, list));
  }

  // First size: the preselects of that size (unless the pick already holds options, e.g. a cart line whose size is
  // gone); a change: prune what the new size hides.
  function pickSize(s, combo, sizeId) {
    var cur = current(s, combo);
    var z = cur && isChoice(cur.option) ? usableSize(cur.option, sizeId) : null;
    if (!z || cur.entry.sizeId === z.id) return s;
    var mods = cur.entry.mods || {};
    var had = !!usableSize(cur.option, cur.entry.sizeId);
    var next = had || Object.keys(mods).length ? M().prune(cur.option, z.id, mods) : initialMods(cur.option, z.id);
    return replaceEntry(s, cur, Object.assign({}, cur.entry, { sizeId: z.id, mods: next }));
  }

  function updateMods(s, combo, fn) {
    var cur = current(s, combo);
    if (!cur) return s;
    var mods = fn(groupsOf(cur.option, cur.entry), cur.entry.mods);
    if (mods === cur.entry.mods) return s;
    return replaceEntry(s, cur, Object.assign({}, cur.entry, { mods: mods }));
  }
  function groupById(groups, id) { return groups.filter(function (g) { return g.id === id; })[0]; }
  function toggleMod(s, combo, groupId, optionId) {
    return updateMods(s, combo, function (groups, sel) { var g = groupById(groups, groupId); return g ? M().toggle(g, sel, optionId) : sel; });
  }
  function incMod(s, combo, groupId, optionId) {
    return updateMods(s, combo, function (groups, sel) { var g = groupById(groups, groupId); return g ? M().increment(g, sel, optionId) : sel; });
  }
  function decMod(s, combo, groupId, optionId) {
    return updateMods(s, combo, function (groups, sel) { var g = groupById(groups, groupId); return g ? M().decrement(g, sel, optionId) : sel; });
  }

  function back(s, combo) {
    var L = steps(combo).length;
    if (s.g < 0) return s;
    var isReview = s.g >= L;
    var from = isReview ? L : s.g;
    if (!isReview && s.ph === 'mods') {
      var idx = modEntryIndexes(s, combo, s.g);
      var pos = idx.indexOf(s.mi);
      if (pos > 0) return set(s, { mi: idx[pos - 1] });
      if (!steps(combo)[s.g].fixed) return set(s, { ph: 'item', mi: 0 });
    }
    var p = s.ret === 'intro' ? -1 : prevStep(combo, from);
    if (p < 0) return set(s, { g: -1, ph: 'item', mi: 0, ret: false });
    var hasMods = stepHasMods(s, combo, p);
    var idxs = modEntryIndexes(s, combo, p);
    return set(s, { g: p, ph: hasMods ? 'mods' : 'item', mi: hasMods ? idxs[idxs.length - 1] : 0, ret: false });
  }

  function firstOpen(s, combo) {
    var all = steps(combo);
    for (var i = 0; i < all.length; i++) if (!stepSatisfied(s, combo, i)) return i;
    return all.length;
  }

  function goStep(s, combo, i) {
    if (i < 0 || i >= steps(combo).length || i > firstOpen(s, combo)) return s;
    return enter(s, combo, i);
  }
  function change(s, combo, i) {
    if (i < 0 || i >= steps(combo).length) return s;
    return enter(s, combo, i, { ret: true });
  }
  function customiseGroup(s, combo, i) {
    if (i == null || i < 0 || i >= steps(combo).length) return s;
    return enter(s, combo, i, { ret: 'intro' });
  }
  function changeItem(s, combo) {
    var st = steps(combo)[s.g];
    return st && !st.fixed && s.ph === 'mods' ? set(s, { ph: 'item', mi: 0 }) : s;
  }
  // (s, value) or (s, combo, value): the value is the last argument of the two shapes.
  function setQty(s) {
    var n = arguments.length >= 3 ? arguments[2] : arguments[1];
    return set(s, { qty: Math.max(1, Math.floor(Number(n)) || 1) });
  }
  function setNotes(s) {
    var text = arguments.length >= 3 ? arguments[2] : arguments[1];
    return set(s, { notes: String(text == null ? '' : text) });
  }
  function dismissAdded(s) { return Object.assign({}, s, { added: false, addedLine: null }); }

  // ---- edit mode: a cart line → builder state (see fromLine in the header) ----------------------------
  function selFromRows(rows) {
    var sel = {};
    arr(rows).forEach(function (m) {
      var n = Math.max(1, Math.floor(Number(m.count)) || 1);
      for (var i = 0; i < n; i++) (sel[m.groupId] = sel[m.groupId] || []).push(Number(m.addOnId));
    });
    return sel;
  }
  function restoredEntry(option, pick) {
    var sel = selFromRows(pick.modifiers);
    if (!isChoice(option)) return { mappingId: option.mappingId, mods: M().prune(option, option.sizeId, sel) };
    var z = usableSize(option, pick.sizeId);
    if (!z) return { mappingId: option.mappingId, mods: M().prune(option, null, sel) }; // asked again; options kept
    return { mappingId: option.mappingId, mods: M().prune(option, z.id, sel), sizeId: z.id };
  }

  function fromLine(combo, line) {
    var all = steps(combo);
    var queue = {};
    arr(line && line.picks).forEach(function (p) {
      var n = Math.max(1, Math.floor(Number(p.count)) || 1);
      for (var i = 0; i < n; i++) (queue[p.groupId] = queue[p.groupId] || []).push(p);
    });
    var picks = {}, skipped = {};
    all.forEach(function (st) {
      var q = queue[st.groupId] || [];
      var take = q.splice(0, st.multi ? st.max : 1).map(function (p) {
        var option = optionOf(st, Number(p.mappingId));
        return option ? restoredEntry(option, p) : null;
      }).filter(Boolean);
      if (take.length) picks[st.index] = take;
      else if (st.optional || st.multi) skipped[st.index] = true;
    });
    var s = Object.assign(init(combo), {
      g: all.length, ph: 'item', mi: 0, picks: picks, skipped: skipped,
      qty: Math.max(1, Math.floor(Number(line && line.qty)) || 1), notes: (line && line.notes) || '',
    });
    var open = view(s, combo).steps.filter(function (x) { return x.state !== 'done'; })[0];
    return open ? goStep(s, combo, open.index) : s;
  }

  // ---- derived values ---------------------------------------------------------------------------
  function entries(s, combo) {
    var out = [];
    steps(combo).forEach(function (st) {
      picksOf(s, combo, st.index).forEach(function (e) {
        var o = optionOf(st, e.mappingId);
        if (!o) return;
        var at = orderedAs(o, e);
        var mods = e.mods || {};
        out.push({
          step: st.index, groupId: st.groupId, mappingId: e.mappingId, option: at.option, mods: mods,
          sizeId: at.sizeId, sizeName: at.sizeName, rows: M().selectedOptions(groupsOf(o, e), mods),
        });
      });
    });
    return out;
  }

  function entryExtra(option, entry) {
    var sel = M().selectedOptions(groupsOf(option, entry), entry.mods || {});
    return (Number(option.extraPrice) || 0) + sel.reduce(function (t, m) { return t + m.price * (m.count - m.free); }, 0);
  }
  function stepExtra(s, combo, i) {
    var st = steps(combo)[i];
    return picksOf(s, combo, i).reduce(function (t, e) { var o = optionOf(st, e.mappingId); return t + (o ? entryExtra(o, e) : 0); }, 0);
  }
  function unitPrice(s, combo) {
    var total = Number(combo.price) || 0;
    steps(combo).forEach(function (st) { total += stepExtra(s, combo, st.index); });
    return F().round2(total);
  }
  // ' (Free)' when every unit of a priced row is free, ' (1 free)' when only some are (OO.cart.displayMods' tags: a row
  // priced 0 is never marked, P2-33).
  function freeTag(m) {
    var free = Number(m.free) || 0;
    if (free <= 0 || !((Number(m.price) || 0) > 0)) return '';
    return free >= m.count ? ' (Free)' : ' (' + free + ' free)';
  }
  // A pick's text: its chosen size ('choice' only: a fixed size is the deal's), then its options with free tags.
  function modsText(option, entry) {
    var z = isChoice(option) ? usableSize(option, entry.sizeId) : null;
    var rows = M().selectedOptions(groupsOf(option, entry), entry.mods || {});
    return (z ? [z.name] : []).concat(rows.map(function (m) { return (m.count > 1 ? m.count + ' × ' : '') + m.name + freeTag(m); })).join(' · ');
  }
  function stepNames(s, combo, i) {
    var st = steps(combo)[i];
    return picksOf(s, combo, i).map(function (e) { var o = optionOf(st, e.mappingId); return o ? o.name : ''; }).filter(Boolean).join(', ');
  }
  function stepModsText(s, combo, i) {
    var st = steps(combo)[i];
    return picksOf(s, combo, i).map(function (e) { var o = optionOf(st, e.mappingId); return o ? modsText(o, e) : ''; }).filter(Boolean).join(' · ');
  }

  function selectedGroupText(s, combo, groupSteps) {
    var selected = [];
    groupSteps.forEach(function (st) {
      picksOf(s, combo, st.index).forEach(function (e) {
        var option = optionOf(st, e.mappingId);
        if (!option) return;
        var mods = modsText(option, e);
        var text = option.name + (mods ? ' (' + mods + ')' : '');
        var row = selected.filter(function (x) { return x.text === text; })[0];
        if (row) row.count += 1;
        else selected.push({ text: text, count: 1 });
      });
    });
    return selected.length ? selected.map(function (x) { return (x.count > 1 ? x.count + ' × ' : '') + x.text; }).join(', ') : 'None selected';
  }

  function line(s, combo) {
    var picks = entries(s, combo).map(function (e) {
      return { groupId: e.groupId, mappingId: e.mappingId, option: e.option, mods: e.mods, sizeId: e.sizeId, sizeName: e.sizeName };
    });
    return OO.cart.comboLine(combo, picks, { qty: s.qty, notes: s.notes });
  }

  // The `min` rule over ORDERABLE options (see the header): the first required group with fewer than it needs →
  // { name, min, count } | null.
  function shortGroup(combo) {
    var gs = arr(combo && combo.groups);
    for (var i = 0; i < gs.length; i++) {
      var gr = gs[i], need = gr.min >= 1 ? (gr.allowSame ? 1 : gr.min) : 0;
      if (!need) continue;
      var n = arr(gr.options).filter(orderable).length;
      if (n < need) return { name: gr.name, min: gr.min, count: n };
    }
    return null;
  }
  // OO.model's unavailableGroup, else the group the `min` rule fails on.
  function unavailableGroupOf(combo) {
    if (combo && combo.unavailableGroup) return combo.unavailableGroup;
    var g = shortGroup(combo);
    return g ? g.name : null;
  }
  // Why the deal can't be ordered: OO.model's label when it says so, else the `min` rule's; null when it can.
  function unavailableReasonOf(combo) {
    if (combo && combo.available === false) return combo.inactiveReason || null;
    var g = shortGroup(combo);
    return g ? OO.model.requiredChoiceLabel(g.name, g.min, g.count) : null;
  }
  // Review may add the combo: the deal is available and every step (so every group) holds its minimum.
  function reviewOk(s, combo) {
    return combo.available !== false && !unavailableGroupOf(combo) && firstOpen(s, combo) >= steps(combo).length;
  }

  // Preselected combos can be added from the intro. Empty optional groups need no visit, while every selected
  // item must still meet its quantity, availability, size and modifier requirements.
  function introReady(s, combo) {
    var all = steps(combo);
    return combo.available !== false && !unavailableGroupOf(combo)
      && all.some(function (st) { return hasPick(s, combo, st.index); })
      && all.every(function (st) {
        var p = picksOf(s, combo, st.index);
        return p.length >= st.min && p.length <= st.max
          && p.every(function (e) { return orderable(optionOf(st, e.mappingId)) && entryMet(st, e); });
      });
  }

  function cta(s, combo) {
    var L = steps(combo).length;
    if (s.g < 0 && !introReady(s, combo)) return start(s, combo);
    if (s.g < 0 || s.g >= L) {
      if (s.g >= L && !reviewOk(s, combo)) return s;
      var added = line(s, combo);
      var fresh = init(combo);
      return Object.assign(fresh, { added: true, addedLine: added });
    }
    var st = steps(combo)[s.g];
    if (s.ph === 'item') {
      if (!itemOk(s, combo)) return s;
      if (!hasPick(s, combo, s.g)) {
        var skipped = Object.assign({}, s.skipped);
        skipped[s.g] = true;
        return next(set(s, { skipped: skipped }), combo, s.g);
      }
      if (stepHasMods(s, combo, s.g)) return set(s, { ph: 'mods', mi: modEntryIndexes(s, combo, s.g)[0] || 0 });
      return next(s, combo, s.g);
    }
    var cur = picksOf(s, combo, s.g)[s.mi];
    if (!cur || !entryMet(st, cur)) return s;
    var idx = modEntryIndexes(s, combo, s.g);
    var pos = idx.indexOf(s.mi);
    if (pos >= 0 && pos < idx.length - 1) return set(s, { mi: idx[pos + 1] });
    return next(s, combo, s.g);
  }

  function itemOk(s, combo) {
    var st = steps(combo)[s.g];
    var n = picksOf(s, combo, s.g).length;
    return n <= st.max && (st.multi ? n >= st.min : (st.optional || n > 0));
  }

  // The current pick's size line (view().curSize).
  function sizeLine(option, entry) {
    if (isChoice(option)) {
      var z = usableSize(option, entry && entry.sizeId);
      return { mode: 'choice', id: z ? z.id : null, name: z ? z.name : null, label: 'Size', locked: false };
    }
    if (option && option.sizeMode === 'fixed' && option.sizeId != null && option.sizeName) {
      return { mode: 'fixed', id: option.sizeId, name: option.sizeName, label: 'Size · Set By Combo', locked: true };
    }
    return null;
  }

  // The 'choice' size group of a pick (view().sizes): required, nothing preselected, no prices (the combo price stands).
  function sizeGroupView(option, entry) {
    var chosen = usableSize(option, entry.sizeId);
    return {
      id: 'size', name: 'Size', hint: 'Choose 1', badge: chosen ? 'Done' : 'Required', required: true,
      radio: true, check: false, counter: false, full: false, blocked: false,
      opts: arr(option.sizes).map(function (z) {
        var why = sizeProblem(option, z);
        var on = !!chosen && chosen.id === z.id;
        return { id: z.id, label: z.name, price: why ? 'Unavailable' : '', on: on, count: on ? 1 : 0, radio: true, check: false, disabled: !!why, reason: why };
      }),
    };
  }

  function view(s, combo, opts) {
    var f = F();
    var cur = (opts && opts.currency) || '$';
    function money(n) { return f.money(n, cur); }
    // '+$2.00'; 'Free' once every picked unit is free; '+$2.00 · 1 free' when some are; '' for a $0 option.
    function optPrice(price, count, free) {
      if (!price) return '';
      if (count > 0 && free >= count) return 'Free';
      return '+' + money(price) + (free > 0 ? ' · ' + free + ' free' : '');
    }
    var all = steps(combo);
    var L = all.length;
    var g = s.g, ph = s.ph;
    var isIntro = g < 0, isReview = g >= L;
    var st = !isIntro && !isReview ? all[g] : null;
    var idx = stepIdx(combo);
    var open = firstOpen(s, combo);
    var unit = unitPrice(s, combo);
    var blockedBy = unavailableGroupOf(combo);
    // An auto-added step (Customise from Review) isn't one of the numbered steps: never "Step 0 of N".
    var stepNo = st ? idx.indexOf(g) : -1;
    var itemCount = all.reduce(function (n, x) { return n + Math.max(1, x.min); }, 0);
    var stepLabel = isIntro ? f.pluralize(itemCount, 'item') + ' · ' + money(combo.price)
      : isReview ? 'Review your combo'
        : stepNo < 0 ? 'Customise · ' + st.name
          : 'Step ' + (stepNo + 1) + ' of ' + idx.length + ' · ' + st.name;
    var curEntry = st && ph === 'mods' ? picksOf(s, combo, g)[s.mi] : null;
    var curPick = curEntry || (st && hasPick(s, combo, g) && !st.multi ? picksOf(s, combo, g)[0] : null);
    var curOption = curPick ? optionOf(st, curPick.mappingId) : null;
    var curMods = curOption && curEntry ? groupsOf(curOption, curEntry) : [];
    var curRows = curEntry ? M().selectedOptions(curMods, curEntry.mods || {}) : [];

    var addFromIntro = isIntro && introReady(s, combo);
    var ctaLabel, ok = true;
    if (isIntro) ctaLabel = addFromIntro ? 'Add to cart' : 'Start';
    else if (isReview) { ctaLabel = 'Add to cart'; ok = reviewOk(s, combo); }
    else if (ph === 'item') {
      ok = itemOk(s, combo);
      var n = picksOf(s, combo, g).length;
      if (!ok) ctaLabel = st.multi && st.min > 1 ? 'Choose ' + (st.min - n) + ' more' : 'Choose ' + (/^[aeiou]/i.test(st.noun) ? 'an ' : 'a ') + st.noun;
      else ctaLabel = !n && (st.optional || st.multi) ? 'Skip'
        : s.ret === 'intro' && !stepHasMods(s, combo, g) ? 'Done' : 'Continue';
    } else {
      ok = !!curEntry && entryMet(st, curEntry);
      var needSize = !!curEntry && isChoice(curOption) && sizeIdOf(curOption, curEntry) == null;
      var midx = modEntryIndexes(s, combo, g);
      var morePicks = midx.indexOf(s.mi) >= 0 && midx.indexOf(s.mi) < midx.length - 1;
      var nx = nextStep(combo, g);
      ctaLabel = !ok ? (needSize ? 'Choose a size' : curMods.some(function (m) { return m.blocked; }) ? 'Unavailable' : 'Make required choices')
        : (morePicks ? 'Continue' : s.ret === 'intro' ? 'Done' : (s.ret || nx >= L ? 'Review combo' : 'Continue to ' + all[nx].name));
    }

    return {
      isIntro: isIntro, notIntro: !isIntro, isReview: isReview, notReview: !isReview,
      addFromIntro: addFromIntro,
      isItem: !!st && ph === 'item', isMods: !!st && ph === 'mods', added: !!s.added,
      available: combo.available !== false && !blockedBy, unavailableGroup: blockedBy, unavailableReason: unavailableReasonOf(combo),
      multi: !!st && st.multi,
      stepLabel: stepLabel,
      steps: idx.map(function (gi, si) {
        var cur = gi === g;
        var done = !cur && hasPick(s, combo, gi) && stepSatisfied(s, combo, gi) || (!cur && !hasPick(s, combo, gi) && !!(s.skipped && s.skipped[gi]));
        var names = stepNames(s, combo, gi), mt = stepModsText(s, combo, gi);
        return {
          index: gi, num: done ? '✓' : String(si + 1), name: all[gi].full,
          label: done && names ? names : all[gi].name,
          summary: names ? names + (mt ? ' · ' + mt : '') : 'Choose ' + quantityLabel(all[gi].min, all[gi].max) + ' ' + all[gi].noun,
          state: done ? 'done' : (cur ? 'current' : 'todo'), reachable: gi <= open,
        };
      }),
      includes: arr(combo.groups).map(function (gr, gi) {
        var grpSteps = all.filter(function (x) { return x.groupIndex === gi; });
        var label = quantityLabel(gr.min, gr.max) + ' × ' + gr.name;
        var sub = selectedGroupText(s, combo, grpSteps);
        var done = grpSteps.length > 0 && grpSteps.every(function (st) {
          return hasPick(s, combo, st.index) && stepSatisfied(s, combo, st.index);
        });
        return { num: gi + 1, index: grpSteps.length ? grpSteps[0].index : null, label: label, sub: sub, done: done };
      }),
      groupTitle: st ? 'Choose your ' + st.noun : '',
      groupName: st ? st.name : '',
      groupHint: st ? 'Pick ' + quantityLabel(st.min, st.max).replace('Up to', 'up to')
        + (st.multi ? ' · ' + picksOf(s, combo, g).length + ' selected' : '') : '',
      items: st ? st.group.options.map(function (o) {
        var count = picksOf(s, combo, g).filter(function (e) { return e.mappingId === o.mappingId; }).length;
        var taken = siblingTaken(s, combo, st, o.mappingId);
        var full = st.multi && !count && picksOf(s, combo, g).length >= st.max;
        return {
          key: slug(o.name) || String(o.mappingId), mappingId: o.mappingId, img: o.image || '', name: o.name, desc: o.description || '',
          price: o.extraPrice ? '+' + money(o.extraPrice) : '', extraPrice: o.extraPrice, included: !o.extraPrice,
          hasMods: hasChoices(o), on: count > 0, count: count,
          disabled: !orderable(o) || taken || full, soldOut: !!o.soldOut, reason: optionProblem(o),
        };
      }) : [],
      curItem: curOption ? curOption.name : '', curKey: curOption ? slug(curOption.name) : '', curImg: curOption && curOption.image || '',
      curFixed: !!st && st.fixed, canChangeItem: !!st && !st.fixed,
      curPosition: st && st.multi && curEntry ? 'Item ' + (s.mi + 1) + ' of ' + picksOf(s, combo, g).length : '',
      curSize: curOption ? sizeLine(curOption, curPick) : null,
      sizes: curEntry && isChoice(curOption) ? sizeGroupView(curOption, curEntry) : null,
      mods: curMods.map(function (m) {
        var sel = curEntry.mods || {};
        var picked = arr(sel[m.id]);
        var taken = m.allowSame ? picked.length : picked.filter(function (x, i) { return picked.indexOf(x) === i; }).length;
        var full = m.max > 1 && taken >= m.max;
        var freeCount = M().freeByGroupOf([m])[m.id] || 0;
        return {
          id: m.id, name: m.name, hint: freeCount > 0 && !m.blocked ? (m.hint ? m.hint + ' · ' : '') + 'First ' + freeCount + ' free' : m.hint,
          badge: M().groupBadge(m, sel), required: m.required,
          radio: m.max === 1, check: m.max > 1 && !m.allowSame, counter: m.allowSame && m.max > 1, full: full, blocked: !!m.blocked,
          opts: m.options.map(function (o) {
            var c = M().count(sel, m.id, o.id);
            var row = curRows.filter(function (r) { return r.groupId === m.id && r.addOnId === o.id; })[0];
            var free = row ? row.free : 0;
            return { id: o.id, label: o.name, price: optPrice(o.price, c, free), on: c > 0, count: c, free: free, radio: m.max === 1, check: m.max > 1, disabled: full && !c };
          }),
        };
      }),
      review: isReview ? all.map(function (x) {
        var p = picksOf(s, combo, x.index);
        var o = p[0] ? optionOf(x, p[0].mappingId) : null;
        var extra = stepExtra(s, combo, x.index);
        var mt = stepModsText(s, combo, x.index);
        return {
          index: x.index, key: o ? slug(o.name) : String(x.index), img: o && o.image || '',
          canChange: !x.fixed || stepHasMods(s, combo, x.index), changeLabel: x.fixed ? 'Customise' : 'Change',
          group: x.full, item: stepNames(s, combo, x.index) || 'None', mods: mt, hasMods: !!mt,
          price: extra ? '+' + money(extra) : '', extraPrice: f.round2(extra),
          items: p.map(function (e, i) {
            var option = optionOf(x, e.mappingId);
            var extra = option ? entryExtra(option, e) : 0;
            return { index: i, name: option ? option.name : '', img: option && option.image || '',
              mods: option ? modsText(option, e) : '', price: extra ? '+' + money(extra) : '' };
          }),
        };
      }) : [],
      unitPrice: unit,
      total: money(unit),
      ctaLabel: ctaLabel,
      ctaEnabled: ok,
      // Actionable missing choices in this phase; the UI may highlight them, but cta() still refuses to advance.
      missing: !ok && st && ph === 'item' ? ['items'] : !ok && curEntry
        ? (isChoice(curOption) && sizeIdOf(curOption, curEntry) == null ? ['size'] : M().validate(curMods, curEntry.mods).missing) : [],
      ctaPrice: isReview ? money(unit * s.qty) : '',
      qty: s.qty,
      decEnabled: s.qty > 1,
      notes: s.notes || '',
    };
  }

  OO.combo = OO.lib.combo = {
    withAvailability: withAvailability,
    init: init,
    fromLine: fromLine,
    steps: steps,
    start: start,
    customiseGroup: customiseGroup,
    pickItem: pickItem,
    removeItem: removeItem,
    pickSize: pickSize,
    toggleMod: toggleMod,
    incMod: incMod,
    decMod: decMod,
    cta: cta,
    back: back,
    goStep: goStep,
    change: change,
    changeItem: changeItem,
    setQty: setQty,
    setNotes: setNotes,
    dismissAdded: dismissAdded,
    entries: entries,
    unitPrice: unitPrice,
    line: line,
    view: view,
  };
})();


// ===================== src/lib/delivery.jsx =====================
// src/lib/delivery.jsx: OO.delivery (alias OO.lib.delivery): the Google Places loader, address autocomplete and
// "use current location" (P1-20), then the Distance Matrix and the delivery zone selection (P1-21). D-16 pulled both
// into Phase 1. No JSX. Nothing runs at load time except creating OO.delivery: window, document, navigator and the
// timers are read at CALL time (the tests run this file in node:vm with fakes).
//
// Google APIs: exactly the ones the REF uses, so the existing key project keeps working (owner decision). That is the
// Maps JavaScript API with the legacy Places library (google.maps.places.Autocomplete) and the reverse Geocoder, not
// the new Places API. The browser key comes from the boot (maps.browserKey, referrer-restricted), never the REF's
// hard-coded key. The loader <script> carries the boot's cspNonce: the storefront CSP (OnlineOrderSpaController)
// allows maps.googleapis.com / maps.gstatic.com scripts with that nonce, and Maps stamps its own scripts with it.
//
// Address = { line, name, street, suburb, city, region, postcode, country, lat, lng, placeId }
//   line      formatted_address, else name
//   street    '{street_number} {route}', trimmed
//   suburb    sublocality_level_1 | sublocality | neighborhood
//   city      locality | postal_town | administrative_area_level_2
//   region    administrative_area_level_1 (short name, e.g. 'VIC')
//   postcode  postal_code
//   country   ISO-2, upper-case ('NZ')
//   lat, lng  numbers from geometry.location, else null
//   placeId   place_id, else ''. The Autocomplete `fields` below request place_id (P1-21), so a pick carries it.
// Text fields are '' when Google sent nothing. A `|` list is a priority order: the first TYPE present wins, wherever
// its component sits.
//
// Quote = { status: 'ok' | 'belowMinimum' | 'outOfZone' | 'unknown', zone, fee, minimum, shortBy, upgrade } (selectZone)
// Point = { lat, lng }: numbers in range, never the 0,0 placeholder (the payload drops it and B10 rejects it).
//
// API:
//   load({timeoutMs = 15000} = {}) → Promise<google.maps>
//       Injects ONE <script async nonce={boot.cspNonce} src="https://maps.googleapis.com/maps/api/js?key={browserKey}
//       &libraries=places&loading=async&callback={unique window callback}&region={COUNTRY}&language=en">, where
//       COUNTRY is country() upper-cased. Memoised: concurrent and later calls share the promise. It resolves at once
//       when window.google.maps.places already exists. It rejects with an Error {kind}:
//         'disabled'     no maps.browserKey in the boot. Nothing is injected, and later calls get the same rejection.
//         'unavailable'  the script failed (onerror), it took longer than timeoutMs ({timeout: true}), or Google called
//                        window.gm_authFailure ({auth: true}: a bad or blocked key).
//       Any rejection except 'disabled' resets the memo, so a later call retries with a fresh script. Every attempt
//       removes its callback global and clears its timer; a failed attempt also removes its <script>. Never throws.
//       A script that arrives after its timeout still defines google.maps.places, so the next call resolves at once.
//       (A retry made while a timed-out script is still loading injects a second copy; Google then warns.)
//       gm_authFailure is sticky. Google usually calls it AFTER the callback, when an Autocomplete first
//       authenticates, and the widget then DISABLES its input. From then on load() rejects {kind:'unavailable',
//       auth:true}, and every live attach() is detached and told through onError: the caller must re-enable or
//       remount that input so the customer can still type an address. Any gm_authFailure the page set is chained.
//   country(store) → 'nz' | 'au' | 'tw'  the lower-case ISO-2 for componentRestrictions: only the countries we
//       serve. It reads store.address.country: names or codes ('New Zealand' / 'NZ' / 'NZL' → 'nz', 'Australia' /
//       'AU' → 'au', 'Taiwan' / 'TW' → 'tw'). Else window.__SIPO_BOOT.region (same rules), else 'nz'. Anything else
//       (another country's code such as 'FJ', or unknown text: staging has 'newziland') falls back to the boot region.
//   attach(input, {country, onPlace(address), onError(err)}) → detach()
//       After load(): new google.maps.places.Autocomplete(input, {componentRestrictions: {country}, fields:
//       ['formatted_address', 'geometry', 'address_components', 'name', 'place_id'], types: ['address']}). country
//       defaults to country() (the same rules). On 'place_changed' → onPlace(parsePlace(getPlace())). A place without
//       a location (Enter on typed text) → onError({code: 'noGeometry'}). If load() fails → onError(err), and
//       detach() is a no-op.
//       detach() clears the listeners on the Autocomplete AND on the input, so re-attaching to the same input never
//       leaves an old widget live. It is safe to call twice, and once it has run (even before load() settles),
//       nothing is created or reported.
//   parsePlace(place) → Address  Pure. A port of the REF parseGoogleAddressParts (utils/addressParts.ts), for an
//       Autocomplete place or a Geocoder result. lat / lng accept LatLng's lat() / lng() methods or plain numbers.
//   locate({timeoutMs = 15000} = {}) → Promise<Address>  "Use current location":
//       navigator.geolocation.getCurrentPosition({enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000})
//       → load() → new google.maps.Geocoder().geocode({location}) → parsePlace(results[0]), with lat / lng = the device
//       position. The Geocoder comes from maps.importLibrary('geocoding') when Maps has it (as under loading=async,
//       P1-21), else the namespace. It rejects with an Error {code}:
//         'unsupported'  no navigator.geolocation
//         'denied' / 'unavailable' / 'timeout'  GeolocationPositionError codes 1 / 2 / 3
//         'unavailable'  also when load() fails ({kind} copied from its error) or the Geocoder errors
//         'noResult'     ZERO_RESULTS, or no results
//   enabled() → bool  P1-21: real stores offer delivery only when the boot has a Maps browser key (maps.browserKey,
//       not blank): without Places and the Distance Matrix there is no address to measure. Read at call time.
//       Fixture stores don't need it (the checkout uses distance 0 for them).
//   pointOf(v) → Point | null  v.lat / v.lng as numbers (numeric strings count) in range, not 0,0; else null. Pure.
//   originOf(store) → Point | string | null  the Distance Matrix origin, as the REF picks it: the store's coordinates
//       (store.lat / store.lng), else its address (store.address.full, or a string address), else null. Pure.
//   distanceKm(origin, dest, {timeoutMs = 15000} = {}) → Promise<number>
//       The DRIVING distance in km, as the REF measures it: load(), then the legacy google.maps.DistanceMatrixService
//       (from maps.importLibrary('routes') when Maps has it, as under loading=async, else the namespace)
//       getDistanceMatrix({origins: [origin], destinations: [dest], travelMode: 'DRIVING', unitSystem: METRIC}) →
//       rows[0].elements[0].distance.value / 1000. origin = a Point or an address string (originOf); dest = a Point.
//       No straight-line fallback (REF): the server's B10 guard has its own. Memoised per (origin, dest) for the
//       page's life: concurrent and later calls share one request. A failure is NOT memoised, so calling again
//       retries. It rejects with an Error {kind} (message: "We couldn't check the distance to your address. Try
//       again."):
//         'disabled'     no maps.browserKey (load()'s 'disabled'), or REQUEST_DENIED (the key can't use the service)
//         'unavailable'  load() failed ({auth} / {timeout} copied from it), a bad origin or dest (nothing is asked),
//                        any other non-OK status ({status}), an unusable answer, no answer within timeoutMs
//                        ({timeout: true}), or a throw
//         'noRoute'      the element status is ZERO_RESULTS, NOT_FOUND or MAX_ROUTE_LENGTH_EXCEEDED ({status})
//       The Promise that current Maps also returns from getDistanceMatrix is handled (the callback decides).
//   cachedKm(origin, dest) → number | null  what distanceKm already measured for that pair in this page's life (no
//       request): the pages read it so a known distance never flashes "checking".
//   selectZone(zones, km, subtotal, {minFee = 0} = {}) → Quote  REF selectDeliveryZone (utils/deliveryZone.ts:10-51;
//       inventory B §1.6b "Zone, fee and minimum"; moved here from checkout.jsx, P1-13). Pure. zones =
//       store.deliveryZones (radiusKm, minimumOrder, fee) or raw deliveryzones rows (zone_area, minimum_amount,
//       delivery_fee), sorted by zone area (ties keep their order); a zone covers the address when its area ≥ km.
//       subtotal = the ITEM subtotal (summary.subtotal: allowance-aware, before discounts and delivery; REF
//       getSubtotal), compared in cents.
//         ok            the covering zone whose minimum ≤ subtotal with the cheapest fee (ties: the smaller zone);
//                       upgrade = the nearest higher minimum of a covering zone with a lower fee, else null:
//                       { zone, fee, addAmount (its minimum − subtotal) }
//         belowMinimum  no covering minimum is met: the entry zone (the covering one with the lowest minimum), its
//                       fee and minimum, shortBy = minimum − subtotal
//         outOfZone     no zone covers the distance (a store with no zones delivers nowhere, as in the REF); fee 0
//         unknown       no usable km (null, '', NaN, < 0); fee 0
//       minFee (P1-21): only zones whose fee is at least this much (half a cent of slack) take part. The checkout
//       sets it to the fee the server's zone guard named for this address (a 202 "costs $X"), so the zone it
//       re-selects is never cheaper than the server's.
//   zoneRejection(body) → { outcome, fee, minimum, itemsTotal, shortBy }  P1-21; G1 fix H. Pure. The B10 zone guard's
//       checkout answer (ecode 202: HTTP 400 { status, ecode, error, message, invalid_type }; DeliveryZoneGuard). The
//       outcome is invalid_type's first (G1 fix G added it: the guard's constant, matched trimmed and upper-cased),
//       even with no text. When invalid_type is absent, null or none of the four (an older backend host), the guard's
//       four fixed phrases (message, else error) name it, as before. The amounts come ONLY out of the text, from that
//       outcome's phrase; no server text is ever shown. Amounts are numbers, else null.
//         'outOfZone'      DELIVERY_OUT_OF_ZONE · "…outside our delivery area…"
//         'belowMinimum'   DELIVERY_BELOW_MINIMUM · "The minimum order for delivery to this address is $M (…). Your
//                          items total $T. …": minimum M, itemsTotal T, shortBy = M − T in cents (null unless both
//                          parse and it is > 0; the key with no such text: all null)
//         'zoneMismatch'   DELIVERY_ZONE_MISMATCH · "Delivery to this address costs $F. …": fee F (without a readable
//                          F it is 'unknown', key or not: the checkout can neither say $F nor set its fee floor)
//         'noCoordinates'  DELIVERY_COORDINATES_REQUIRED · "We couldn't confirm where your delivery address is. …"
//         'unknown'        none of the four keys, and any other text, no text, or not an object
//   __reset() → test hook: forgets the memoised loader, the sticky auth failure and the measured distances.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  var MAPS_URL = 'https://maps.googleapis.com/maps/api/js';
  var TIMEOUT_MS = 15000;
  var CALLBACK_PREFIX = '__ooMapsReady';
  // The REF's Autocomplete fields, plus place_id (P1-21). All Places Basic Data.
  var FIELDS = ['formatted_address', 'geometry', 'address_components', 'name', 'place_id'];

  // ---- small helpers ---------------------------------------------------------------------------
  function text(v) { return v === null || v === undefined ? '' : String(v).trim(); }
  function boot() { return window.__SIPO_BOOT || {}; }
  function timeoutOf(opts) { var n = Number(opts && opts.timeoutMs); return n > 0 && isFinite(n) ? n : TIMEOUT_MS; }
  // A finite number, from a number or a numeric string; null for anything else (Number(null) would be 0).
  function finite(v) {
    if (typeof v === 'string') v = v.trim() === '' ? NaN : Number(v);
    return typeof v === 'number' && isFinite(v) ? v : null;
  }
  function failure(fields, message) {
    var err = new Error(message);
    Object.keys(fields).forEach(function (k) { err[k] = fields[k]; });
    return err;
  }
  function loadFailure(extra) {
    return failure(Object.assign({ kind: 'unavailable' }, extra), "Address search couldn't load. Please type your address.");
  }

  // ---- country -----------------------------------------------------------------------------------
  // Store-address country names and codes → ISO-2, for the countries we serve only (P1-21: another country's code no
  // longer passes through). Keys keep only the letters a-z ('Taiwan (R.O.C.)' → 'taiwanroc').
  var COUNTRIES = {
    nz: 'nz', nzl: 'nz', newzealand: 'nz', aotearoa: 'nz', aotearoanewzealand: 'nz', newzealandaotearoa: 'nz',
    au: 'au', aus: 'au', australia: 'au',
    tw: 'tw', twn: 'tw', taiwan: 'tw', taiwanroc: 'tw',
  };
  function countryCode(v) {
    var k = text(v).toLowerCase().replace(/[^a-z]+/g, '');
    return Object.prototype.hasOwnProperty.call(COUNTRIES, k) ? COUNTRIES[k] : '';
  }
  function country(store) {
    var address = store && store.address;
    return countryCode(address && address.country) || countryCode(boot().region) || 'nz';
  }

  // ---- parsePlace ----------------------------------------------------------------------------------
  // The name of the first component that has one of `types`, by TYPE priority (then component order).
  function component(comps, types, short) {
    for (var t = 0; t < types.length; t++) {
      for (var i = 0; i < comps.length; i++) {
        var c = comps[i];
        if (!c || !Array.isArray(c.types) || c.types.indexOf(types[t]) < 0) continue;
        var name = text(short ? c.short_name || c.long_name : c.long_name || c.short_name);
        if (name) return name;
      }
    }
    return '';
  }
  // geometry.location is a google.maps.LatLng (lat() / lng() methods) or a plain {lat, lng} literal.
  function coord(location, k) {
    if (!location) return null;
    return finite(typeof location[k] === 'function' ? location[k]() : location[k]);
  }

  function parsePlace(place) {
    var p = place && typeof place === 'object' ? place : {};
    var comps = Array.isArray(p.address_components) ? p.address_components : [];
    var location = p.geometry && p.geometry.location;
    var name = text(p.name);
    return {
      line: text(p.formatted_address) || name,
      name: name,
      street: (component(comps, ['street_number']) + ' ' + component(comps, ['route'])).trim(),
      suburb: component(comps, ['sublocality_level_1', 'sublocality', 'neighborhood']),
      city: component(comps, ['locality', 'postal_town', 'administrative_area_level_2']),
      region: component(comps, ['administrative_area_level_1'], true),
      postcode: component(comps, ['postal_code']),
      country: component(comps, ['country'], true).toUpperCase(),
      lat: coord(location, 'lat'),
      lng: coord(location, 'lng'),
      placeId: text(p.place_id),
    };
  }

  // ---- load: the Maps JavaScript API + Places ------------------------------------------------------
  var memo = null;        // the shared load() promise
  var attempt = null;     // the injection in flight: { fail(err) }
  var authFailed = false; // Google called window.gm_authFailure: the key is bad or blocked (sticky)
  var authWatchers = [];  // live attach() handlers, told about an auth failure
  var seq = 0;

  function mapsReady() {
    var g = window.google;
    return g && g.maps && g.maps.places ? g.maps : null;
  }

  function authFailure() { return loadFailure({ auth: true }); }

  // Google calls window.gm_authFailure() when it rejects the key (bad, blocked, or not allowed for this referrer).
  // Hooked once; a handler the page already had still runs.
  function watchAuth() {
    var previous = window.gm_authFailure;
    if (typeof previous === 'function' && previous.__ooDelivery) return;
    var hook = function () {
      authFailed = true;
      memo = null;
      var err = authFailure();
      if (attempt) attempt.fail(err);
      authWatchers.slice().forEach(function (watcher) { watcher(err); });
      if (typeof previous === 'function') return previous.apply(this, arguments);
    };
    hook.__ooDelivery = true;
    window.gm_authFailure = hook;
  }

  function mapsUrl(key, callback) {
    return MAPS_URL + '?key=' + encodeURIComponent(key) + '&libraries=places&loading=async&callback=' +
      encodeURIComponent(callback) + '&region=' + country().toUpperCase() + '&language=en';
  }

  // One attempt: a fresh <script> and callback global. It settles once, then cleans up after itself.
  function inject(key, nonce, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var w = window, el = null, name = null, timer = null, settled = false;
      var self = { fail: function (err) { finish(err); } };
      function finish(err, maps) {
        if (settled) return;
        settled = true;
        if (attempt === self) attempt = null;
        if (timer !== null) clearTimeout(timer);
        if (name) { try { delete w[name]; } catch (e) { w[name] = undefined; } }
        if (!err) { resolve(maps); return; }
        if (el) {
          el.onerror = null;
          if (el.parentNode) el.parentNode.removeChild(el);
        }
        reject(err);
      }
      attempt = self;
      try {
        do { name = CALLBACK_PREFIX + (++seq); } while (w[name] !== undefined);
        w[name] = function () {
          var maps = mapsReady();
          finish(maps ? null : loadFailure(), maps);
        };
        el = document.createElement('script');
        el.async = true;
        if (nonce) { el.setAttribute('nonce', nonce); el.nonce = nonce; }
        el.src = mapsUrl(key, name);
        el.onerror = function () { finish(loadFailure()); };
        timer = setTimeout(function () { finish(loadFailure({ timeout: true })); }, timeoutMs);
        (document.head || document.body || document.documentElement).appendChild(el);
      } catch (e) {
        finish(loadFailure());
      }
    });
  }

  function load(opts) {
    try {
      if (authFailed) return Promise.reject(authFailure());
      if (memo) return memo;
      var maps = mapsReady();
      if (maps) {
        watchAuth();
        memo = Promise.resolve(maps);
        return memo;
      }
      var b = boot();
      var key = text(b.maps && b.maps.browserKey);
      if (!key) {
        memo = Promise.reject(failure({ kind: 'disabled' }, 'Address search is not available. Please type your address.'));
        return memo;
      }
      watchAuth();
      var p = inject(key, text(b.cspNonce), timeoutOf(opts)).then(null, function (err) {
        if (memo === p) memo = null; // before the caller sees it, so a retry from its catch starts fresh
        throw err;
      });
      memo = p;
      return p;
    } catch (e) {
      return Promise.reject(loadFailure());
    }
  }

  // A Maps class, or null: from importLibrary(library) when Maps has it (under loading=async a library outside the
  // bootstrap's `libraries=places` may only arrive that way; P1-21), else the namespace's (the classic bootstrap, and
  // the tests' fakes).
  function libraryClass(maps, library, name) {
    var namespaced = function () { return maps[name]; };
    var found = typeof maps.importLibrary !== 'function' ? Promise.resolve(namespaced())
      : Promise.resolve().then(function () { return maps.importLibrary(library); }).then(function (lib) {
        return (lib && lib[name]) || namespaced();
      }, namespaced);
    return found.then(function (C) { return typeof C === 'function' ? C : null; });
  }

  // ---- attach: address autocomplete --------------------------------------------------------------
  function attach(input, opts) {
    var o = opts || {};
    var onPlace = typeof o.onPlace === 'function' ? o.onPlace : function () {};
    var onError = typeof o.onError === 'function' ? o.onError : function () {};
    var code = countryCode(o.country) || country();
    var detached = false, ac = null, events = null;

    function onAuthFailure(err) { detach(); onError(err); }
    function detach() {
      if (detached) return;
      detached = true;
      var i = authWatchers.indexOf(onAuthFailure);
      if (i >= 0) authWatchers.splice(i, 1);
      if (ac && events) {
        try {
          events.clearInstanceListeners(ac);
          events.clearInstanceListeners(input);
        } catch (e) { /* Google already tore it down */ }
      }
      ac = null;
    }

    load().then(function (maps) {
      if (detached) return;
      try {
        events = maps.event;
        ac = new maps.places.Autocomplete(input, {
          componentRestrictions: { country: code }, fields: FIELDS.slice(), types: ['address'],
        });
        var widget = ac;
        widget.addListener('place_changed', function () {
          if (detached) return;
          var address = parsePlace(widget.getPlace());
          if (address.lat === null || address.lng === null) {
            onError(failure({ code: 'noGeometry' }, 'Choose your address from the list.'));
          } else {
            onPlace(address);
          }
        });
        authWatchers.push(onAuthFailure);
      } catch (e) {
        detach();
        onError(loadFailure());
      }
    }, function (err) {
      if (detached) return;
      detached = true;
      onError(err);
    });
    return detach;
  }

  // ---- locate: "use current location" --------------------------------------------------------------
  var GEO_CODES = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };
  var LOCATE_MESSAGES = {
    unsupported: "This device can't share its location. Please type your address.",
    denied: 'Location access is off. Allow it, or type your address.',
    unavailable: "We couldn't find your location. Please type your address.",
    timeout: 'Finding your location took too long. Please try again.',
    noResult: "We couldn't find an address for your location. Please type it.",
  };
  function locateFailure(code, extra) {
    return failure(Object.assign({ code: code }, extra), LOCATE_MESSAGES[code]);
  }

  function reverseGeocode(maps, lat, lng, resolve, reject) {
    libraryClass(maps, 'geocoding', 'Geocoder').then(function (Geocoder) {
      if (!Geocoder) { reject(locateFailure('unavailable')); return; }
      var ret = new Geocoder().geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
        var first = Array.isArray(results) ? results[0] : null;
        if (first) {
          var address = parsePlace(first);
          address.lat = lat;
          address.lng = lng;
          resolve(address);
        } else {
          reject(locateFailure(!status || status === 'OK' || status === 'ZERO_RESULTS' ? 'noResult' : 'unavailable'));
        }
      });
      // Current Maps also returns a Promise, which rejects on a non-OK status. The callback already handled it.
      if (ret && typeof ret.then === 'function') ret.then(null, function () {});
    }).then(null, function () { reject(locateFailure('unavailable')); });
  }

  function locate(opts) {
    var timeoutMs = timeoutOf(opts);
    return new Promise(function (resolve, reject) {
      var nav = typeof navigator !== 'undefined' ? navigator : window.navigator;
      var geo = nav && nav.geolocation;
      if (!geo || typeof geo.getCurrentPosition !== 'function') { reject(locateFailure('unsupported')); return; }
      function found(position) {
        var c = (position && position.coords) || {};
        var lat = finite(c.latitude), lng = finite(c.longitude);
        if (lat === null || lng === null) { reject(locateFailure('unavailable')); return; }
        load({ timeoutMs: timeoutMs }).then(function (maps) {
          reverseGeocode(maps, lat, lng, resolve, reject);
        }, function (err) {
          reject(locateFailure('unavailable', { kind: (err && err.kind) || 'unavailable' }));
        });
      }
      function failed(e) { reject(locateFailure(GEO_CODES[e && e.code] || 'unavailable')); }
      try {
        geo.getCurrentPosition(found, failed, { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 });
      } catch (e) {
        reject(locateFailure('unavailable'));
      }
    });
  }

  // ---- delivery for real stores: the Maps key, points, the store's origin (P1-21) ------------------------------
  function enabled() {
    var maps = boot().maps;
    return !!text(maps && maps.browserKey);
  }

  function pointOf(v) {
    if (!v || typeof v !== 'object') return null;
    var lat = finite(v.lat), lng = finite(v.lng);
    if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
    return { lat: lat, lng: lng };
  }

  function originOf(store) {
    var s = store && typeof store === 'object' ? store : {};
    var at = pointOf({ lat: s.lat, lng: s.lng });
    if (at) return at;
    var a = s.address;
    return text(a && typeof a === 'object' ? a.full : a) || null;
  }

  // ---- distanceKm: the Distance Matrix (P1-21) ------------------------------------------------------------------
  var NO_ROUTE = { ZERO_RESULTS: true, NOT_FOUND: true, MAX_ROUTE_LENGTH_EXCEEDED: true };
  var measured = {};  // memo key → km: the answers, for the page's life
  var measuring = {}; // memo key → the request in flight
  var memoGen = 0;    // __reset() bumps it: a request from before never writes the memo

  function distanceFailure(kind, extra) {
    return failure(Object.assign({ kind: kind }, extra), "We couldn't check the distance to your address. Try again.");
  }
  // An origin is a Point or a non-blank address string.
  function originPoint(v) { return typeof v === 'string' ? text(v) || null : pointOf(v); }
  function memoKey(from, to) {
    return (typeof from === 'string' ? 'a:' + from : from.lat + ',' + from.lng) + '>' + to.lat + ',' + to.lng;
  }

  // A getDistanceMatrix callback's answer → [error] or [null, km].
  function matrixAnswer(response, status) {
    if (status !== 'OK') {
      return [distanceFailure(status === 'REQUEST_DENIED' ? 'disabled' : 'unavailable', { status: text(status) })];
    }
    var row = response && Array.isArray(response.rows) ? response.rows[0] : null;
    var el = row && Array.isArray(row.elements) ? row.elements[0] : null;
    if (!el || typeof el !== 'object') return [distanceFailure('unavailable')];
    var elStatus = text(el.status);
    if (elStatus && elStatus !== 'OK') {
      var noRoute = Object.prototype.hasOwnProperty.call(NO_ROUTE, elStatus);
      return [distanceFailure(noRoute ? 'noRoute' : 'unavailable', { status: elStatus })];
    }
    var meters = finite(el.distance && el.distance.value);
    if (meters === null || meters < 0) return [distanceFailure('unavailable')];
    return [null, meters / 1000];
  }

  // One request. It settles once: Google's answer, a throw, or timeoutMs without an answer.
  function askMatrix(maps, from, to, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = setTimeout(function () { finish(distanceFailure('unavailable', { timeout: true })); }, timeoutMs);
      function finish(err, km) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (err) reject(err); else resolve(km);
      }
      libraryClass(maps, 'routes', 'DistanceMatrixService').then(function (Service) {
        if (settled) return;
        if (!Service) { finish(distanceFailure('unavailable')); return; }
        var request = {
          origins: [from], destinations: [to],
          travelMode: (maps.TravelMode && maps.TravelMode.DRIVING) || 'DRIVING',
          unitSystem: maps.UnitSystem && typeof maps.UnitSystem.METRIC === 'number' ? maps.UnitSystem.METRIC : 0,
        };
        var ret = new Service().getDistanceMatrix(request, function (response, status) {
          finish.apply(null, matrixAnswer(response, status));
        });
        // Current Maps also returns a Promise, which rejects on a non-OK status. The callback already handled it.
        if (ret && typeof ret.then === 'function') ret.then(null, function () {});
      }).then(null, function () { finish(distanceFailure('unavailable')); });
    });
  }

  function distanceKm(origin, dest, opts) {
    var from = originPoint(origin), to = pointOf(dest);
    if (!from || !to) return Promise.reject(distanceFailure('unavailable'));
    var key = memoKey(from, to);
    if (Object.prototype.hasOwnProperty.call(measured, key)) return Promise.resolve(measured[key]);
    if (measuring[key]) return measuring[key];
    var timeoutMs = timeoutOf(opts), gen = memoGen;
    var p = load({ timeoutMs: timeoutMs }).then(function (maps) {
      return askMatrix(maps, from, to, timeoutMs);
    }, function (err) {
      var e = err || {}, extra = {};
      if (e.auth) extra.auth = true;
      if (e.timeout) extra.timeout = true;
      throw distanceFailure(e.kind === 'disabled' ? 'disabled' : 'unavailable', extra);
    }).then(function (km) {
      if (gen === memoGen) {
        measured[key] = km;
        if (measuring[key] === p) delete measuring[key];
      }
      return km;
    }, function (err) {
      if (gen === memoGen && measuring[key] === p) delete measuring[key]; // not memoised: calling again retries
      throw err;
    });
    measuring[key] = p;
    return p;
  }

  function cachedKm(origin, dest) {
    var from = originPoint(origin), to = pointOf(dest);
    if (!from || !to) return null;
    var key = memoKey(from, to);
    return Object.prototype.hasOwnProperty.call(measured, key) ? measured[key] : null;
  }

  // ---- selectZone: the delivery zone (P1-21; was checkout.jsx's, P1-13) -----------------------------------------
  var MONEY_EPSILON = 0.005; // half a cent, as the server's zone guard
  function num0(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  // OO.format.round2's rule (half-up to cents, float-safe), here so the zone choice needs nothing else.
  function round2(n) {
    var v = num0(n);
    var sign = v < 0 ? -1 : 1;
    return sign * Number(Math.round(Number(Math.abs(v) + 'e2')) + 'e-2') || 0;
  }
  // A zone's numbers: a store.deliveryZones entry (radiusKm, minimumOrder, fee) or a raw deliveryzones row.
  function zoneNums(z) {
    function pick(a, b) { return num0(z[a] !== undefined && z[a] !== null ? z[a] : z[b]); }
    return {
      area: pick('radiusKm', 'zone_area'), min: pick('minimumOrder', 'minimum_amount'), fee: pick('fee', 'delivery_fee'),
    };
  }

  function selectZone(zones, km, subtotal, opts) {
    var d = typeof km === 'number' ? km : (km === null || km === undefined || km === '' ? NaN : Number(km));
    var quote = { status: 'unknown', zone: null, fee: 0, minimum: 0, shortBy: 0, upgrade: null };
    if (!isFinite(d) || d < 0) return quote;
    var sub = round2(subtotal);
    var floor = num0(opts && opts.minFee);
    var covering = (Array.isArray(zones) ? zones : []).filter(function (z) { return z && typeof z === 'object'; })
      .map(function (z, i) { return { z: z, n: zoneNums(z), i: i }; })
      .filter(function (x) { return !(floor > 0) || x.n.fee + MONEY_EPSILON >= floor; })
      .sort(function (a, b) { return a.n.area - b.n.area || a.i - b.i; })
      .filter(function (x) { return x.n.area >= d; });
    if (!covering.length) { quote.status = 'outOfZone'; return quote; }
    var entry = null, matched = null;
    covering.forEach(function (x) {
      if (!entry || x.n.min < entry.n.min) entry = x;
      if (x.n.min <= sub && (!matched || x.n.fee < matched.n.fee)) matched = x;
    });
    if (!matched) {
      return {
        status: 'belowMinimum', zone: entry.z, fee: entry.n.fee, minimum: entry.n.min, shortBy: round2(entry.n.min - sub),
        upgrade: null,
      };
    }
    // The "add $X more" incentive: the nearest minimum still ahead whose (covering) zone delivers for less.
    var up = null;
    covering.forEach(function (x) {
      if (x.n.fee >= matched.n.fee || x.n.min <= sub) return;
      if (!up || x.n.min < up.n.min || (x.n.min === up.n.min && x.n.fee < up.n.fee)) up = x;
    });
    return {
      status: 'ok', zone: matched.z, fee: matched.n.fee, minimum: matched.n.min, shortBy: 0,
      upgrade: up ? { zone: up.z, fee: up.n.fee, addAmount: round2(up.n.min - sub) } : null,
    };
  }

  // ---- zoneRejection: the B10 zone guard's ecode-202 answer (P1-21; G1 fix H) ------------------------------------
  // DeliveryZoneGuard's four outcomes: by the body's invalid_type (G1 fix G: the guard's constant; tests/g1fix-h.test.mjs
  // reads them out of the PHP), else (an older backend host) by its four messages' fixed phrases
  // (tests/delivery-zones.test.mjs reads them out of the PHP, so a change there fails the test).
  var GUARD_TYPES = {
    DELIVERY_OUT_OF_ZONE: 'outOfZone',
    DELIVERY_BELOW_MINIMUM: 'belowMinimum',
    DELIVERY_ZONE_MISMATCH: 'zoneMismatch',
    DELIVERY_COORDINATES_REQUIRED: 'noCoordinates',
  };
  var MONEY_RE = '\\$\\s*([0-9][0-9,]*(?:\\.[0-9]+)?)';
  var GUARD_COORDINATES = /confirm where your delivery address is/i;
  var GUARD_MISMATCH = new RegExp('delivery to this address costs\\s*' + MONEY_RE, 'i');
  var GUARD_MINIMUM = /minimum order for delivery/i;
  var GUARD_MINIMUM_AMOUNTS = new RegExp(
    'minimum order for delivery[^$]*' + MONEY_RE + '[\\s\\S]*?items total[^$]*' + MONEY_RE, 'i');
  var GUARD_OUTSIDE = /outside (?:of )?our delivery area/i;

  function amountOf(s) {
    var n = parseFloat(String(s).replace(/,/g, ''));
    return isFinite(n) && n >= 0 ? n : null;
  }

  // The outcome invalid_type names (trimmed, upper-cased), else null: absent, null, or not one of the guard's.
  function guardType(v) {
    var k = typeof v === 'string' ? v.trim().toUpperCase() : '';
    return Object.prototype.hasOwnProperty.call(GUARD_TYPES, k) ? GUARD_TYPES[k] : null;
  }
  // The fee a zone mismatch names ("costs $F"), else null.
  function mismatchFee(msg) {
    var m = GUARD_MISMATCH.exec(msg);
    return m ? amountOf(m[1]) : null;
  }
  // The below-minimum message's amounts ("… is $M (…). Your items total $T …"), when it has them, onto out.
  function minimumAmounts(msg, out) {
    var m = GUARD_MINIMUM_AMOUNTS.exec(msg);
    if (!m) return;
    out.minimum = amountOf(m[1]);
    out.itemsTotal = amountOf(m[2]);
    var gap = out.minimum !== null && out.itemsTotal !== null ? round2(out.minimum - out.itemsTotal) : 0;
    out.shortBy = gap > 0 ? gap : null;
  }

  function zoneRejection(body) {
    var out = { outcome: 'unknown', fee: null, minimum: null, itemsTotal: null, shortBy: null };
    var b = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
    var msg = typeof b.message === 'string' && b.message.trim() ? b.message : (typeof b.error === 'string' ? b.error : '');
    var keyed = guardType(b.invalid_type);
    if (keyed) {
      // G1 fix H: the body names the outcome, even with no text; the amounts still come from the text. A mismatch
      // needs its fee: without a readable one it stays 'unknown', as on the text path.
      if (keyed === 'zoneMismatch') {
        out.fee = mismatchFee(msg);
        if (out.fee === null) return out;
      } else if (keyed === 'belowMinimum') {
        minimumAmounts(msg, out);
      }
      out.outcome = keyed;
      return out;
    }
    if (!msg) return out;
    var fee;
    if (GUARD_COORDINATES.test(msg)) {
      out.outcome = 'noCoordinates';
    } else if ((fee = mismatchFee(msg)) !== null) {
      out.outcome = 'zoneMismatch';
      out.fee = fee;
    } else if (GUARD_MINIMUM.test(msg)) {
      out.outcome = 'belowMinimum';
      minimumAmounts(msg, out);
    } else if (GUARD_OUTSIDE.test(msg)) {
      out.outcome = 'outOfZone';
    }
    return out;
  }

  function __reset() {
    memo = null;
    attempt = null;
    authFailed = false;
    authWatchers = [];
    measured = {};
    measuring = {};
    memoGen++;
  }

  OO.delivery = OO.lib.delivery = Object.assign(OO.delivery || {}, {
    load: load,
    country: country,
    attach: attach,
    parsePlace: parsePlace,
    locate: locate,
    enabled: enabled,
    pointOf: pointOf,
    originOf: originOf,
    distanceKm: distanceKm,
    cachedKm: cachedKm,
    selectZone: selectZone,
    zoneRejection: zoneRejection,
    __reset: __reset,
  });
})();


// ===================== src/lib/pricetags.jsx =====================
// src/lib/pricetags.jsx: OO.pricetags (alias OO.lib.pricetags): the pricetag engine (Batch 2 P2-01; B §3.5, REF
// shared/pricetag/PricetagContext.tsx, the cart's syncCartItemForPricetag and the checkout wire). A store's pricetags
// (OO.model.normalizePricetags over get_pricetag_order + get_pricetag_prices) list, per DOMAIN, tags in priority order;
// the first that applies (its time rules, a membership) prices products, sizes, modifiers and combos instead of the
// menu. P2-02 (store.jsx) re-prices the cart when the tag changes; P2-17 / P2-21 show tag prices on the menu cards and
// in the item sheet.
// Pure: no JSX, no document or OO.http at load time (the tests and the parity generator run it raw in node:vm).
//
// How a tag reaches the order:
//   1. select(pricetags, { orderType, mode, now, … }) → Selection: the winning tag for the order.
//   2. priceMenu(menu, selection) → the menu at the tag's prices. Everything that prices a line reads the menu
//      (OO.cart.itemLine / comboLine / reprice, OO.modifiers, OO.combo), so the item sheet, new lines and the cart
//      reprice (OO.store.cartReprice(priced menu) → the keys whose price moved) follow it, and the summary, the coupon %
//      and the combo spread run on the tag prices with no pricing hook. priceMenu(pricedMenu, null) restores the base.
//   3. The payload contributor 'pricetags' (OO.payload.contribute, registered at load) claims the tag on the rows it
//      priced, from ctx.pricetag = the priced menu's `pricetag` record (or a Selection): price_tag_id /
//      is_price_tag_applied on a data[] row and on each modifier row. Without ctx.pricetag nothing is claimed (fixture
//      stores, stores without web tags): rows keep price_tag_id null / is_price_tag_applied false, modifier rows get no
//      tag keys. A tag-priced line sent without ctx.pricetag is rejected by the validator (ecode 100 / 101): fail closed.
//
// API:
//   domainOf(orderType, mode = OO.mode, opts) → the pricetag domain: OO.model.domain's with '_' → ' ' (REF
//       resolveDomain; get_pricetag_order's domain_name): 'ONLINE PICKUP' | 'ONLINE DELIVERY' | 'ONLINE APP PICKUP' |
//       'ONLINE APP DELIVERY' | 'TABLE SIDE' (mode 'qr' or opts.table) | 'KIOSK'.
//   memberOf(wallet) → Member | null   (REF extractMembership) wallet = get_customer_wallet_points' body (the raw
//       `wallet` OO.api.getLoyalty resolves); null without one. Member = { priceTagId, membershipId, membershipType }:
//       priceTagId = membership_type_obj.discount_meta (else the root discount_meta; a JSON string encoded up to 3
//       times) .price_tag | .priceTag as a number > 0, else null; membershipId = membership_type_obj.id;
//       membershipType = membership_type (numbers | null).
//   select(pricetags, { orderType, mode, table, now, timeZone, member }) → Selection   (B §3.5)
//       The candidates are the ACTIVE rows of the domain domainOf(orderType, mode, { table }), by sortOrder (stable).
//       The first one that passes wins:
//         - its rules (the tag's availability) hold at `now` — the ORDER's time (the slot of a scheduled order) as a
//           Date | ms | store wall clock | Clock, in timeZone (the store's); without one the device clock, as the
//           OO.model normalisers; read only when a candidate has rules — in the availability domain
//           OO.model.domain(orderType, mode, { table });
//         - a checkMembership row needs `member` (memberOf a signed-in customer's wallet; null when signed out) whose
//           priceTagId is the tag, or whose membershipId (or, failing that, membershipType) is one of the row's
//           membership types; otherwise the row is skipped.
//       A winner with `override` stands for the default prices: the admin's "Default price" anchor sits above it (the
//       POS resolves it the same way), and no later row is tried. No winner, or no prices (pricetags.prices null: the
//       prices read failed) means the default prices too.
//       Selection = { tagId (the winner's, override or not; null without one), name, domain, active (a winner, prices
//       loaded and not override), override, membership (active and the winner checks membership: P2-03's lines with
//       a membership tag price take no membership discount), prices: TagPrices (OO.model.normalizePricetags; the
//       winner's when active, else empty) }. Plain data: tagId and active tell two selections apart.
//   price(pricing, kind, id, sizeId) → number | null   the tag price of an entity, kind 'product' | 'size' (id = the
//       product's, sizeId) | 'modifier' | 'combo'; null when pricing (a Selection or a `pricetag` record) isn't
//       active, has no row for it, or the row is 0 ("no override").
//   priceMenu(menu, selection) → Menu   the OO.model Menu at the selection's prices; the input is never mutated.
//       Priced: product.price, each size's price, each modifier option's price, combo.price and, on each combo option,
//       its sizes, its modifier options and its fixed size's price (option.price when option.sizeId is set). A combo
//       option without a fixed size keeps its sale_price: under V4 the validator prices a combo child's product at
//       sale_price and ignores its product tag (B §3.5); the combo price carries the saving. An entity whose tag price
//       differs from its base becomes { …, price: the tag's, basePrice, pricetagId }; a tag price equal to the base
//       changes nothing. A priced menu is re-priced from basePrice, so priceMenu(priceMenu(m, a), b) equals
//       priceMenu(m, b), and an inactive selection (or null) restores the base prices. The SAME object comes back
//       when nothing changes (a base menu without a tag: every fixture store).
//       A menu the tag changed carries `pricetag` = { tagId, name, domain, active: true, override: false, membership,
//       prices: TagPrices of ONLY the entries whose price the tag changed }: the checkout payload's ctx.pricetag.
//   repriceCart(lines, menu, selection) → { lines, changed }   OO.cart.reprice(lines, priceMenu(menu, selection)).
//   lineUsesTag(line, pricing) → bool   the cart line (an item, or a combo with its picks; modifier rows included)
//       has a price the tag set: REF's is_membership_price_tag_applied when pricing.membership (P2-03).
//
// The payload rows (B §3.5, §4.2; OnlineOrderValidationController::validateCheckoutData L141-315). The validator re-prices
// a row from the tag it claims (checkPriceTagPriceOfEntity: a 0 or missing tag price falls back to the DB price), so a
// row claims only when its price IS the tag's price for that entity (pricing.prices; a `pricetag` record holds only the
// entries the tag changed, so a tag price equal to the base is never claimed, as in REF):
//   item row        sized: the size's tag price ('productId:sizeId'); unsized: the product's → price_tag_id = tagId,
//                   is_price_tag_applied = true. A sized row never claims the product tag: the validator's size branch
//                   replaces the product price.
//   combo child     its size only (size_id): a V4 child's product is checked at sale_price whatever the tag, its size
//                   at the size's tag price when the row claims it.
//   modifier rows   each on its own: { …, price_tag_id, is_price_tag_applied: true } when its price is the modifier's
//                   tag price; otherwise the two keys are left out (REF).
//   combo_price     no claim field: guard Rule L takes the lowest of combo_price and the chain's combopricetags prices.
// Load order (build/concat.mjs): after pricing.jsx / payload.jsx and before store.jsx; the first P2 engine, so its
// line hook runs before the other engines' hooks.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function same(a, b) { return Math.abs(num(a) - num(b)) < 1e-9; }
  function numOrNull(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }
  // A JSON field that may arrive as a string, encoded up to 3 times (discount_meta); null when it doesn't parse.
  function parsed(v) {
    for (var i = 0; i < 3 && typeof v === 'string'; i++) {
      try { v = JSON.parse(v); } catch (e) { return null; }
    }
    return v;
  }
  function emptyPrices() { return { product: {}, size: {}, modifier: {}, combo: {} }; }
  function keyOf(kind, id, sizeId) { return kind === 'size' ? String(id) + ':' + String(sizeId) : String(id); }

  // ---- domain + member ---------------------------------------------------------------------------------------------
  function domainOf(orderType, mode, opts) { return OO.model.domain(orderType, mode, opts).replace(/_/g, ' '); }

  function memberOf(wallet) {
    if (!isRecord(wallet)) return null;
    var obj = isRecord(wallet.membership_type_obj) ? wallet.membership_type_obj : null;
    var meta = parsed(obj && obj.discount_meta != null ? obj.discount_meta : wallet.discount_meta);
    var tag = isRecord(meta) ? (meta.price_tag != null ? meta.price_tag : meta.priceTag) : null;
    var t = numOrNull(tag);
    return {
      priceTagId: t !== null && t > 0 ? t : null,
      membershipId: obj ? numOrNull(obj.id) : null,
      membershipType: numOrNull(wallet.membership_type),
    };
  }

  // A checkMembership row: the member's own price tag, else one of the row's membership types (REF PricetagContext).
  function memberMatches(row, member) {
    if (!member) return false;
    if (member.priceTagId != null && member.priceTagId === row.tagId) return true;
    if (member.membershipId == null) return false;
    return arr(row.membership).some(function (m) {
      return !!m && (m.id === member.membershipId || (m.type != null && m.type === member.membershipType));
    });
  }

  // ---- select (B §3.5) ---------------------------------------------------------------------------------------------
  function select(pricetags, opts) {
    opts = opts || {};
    var where = opts.table ? { table: true } : undefined;
    var domain = domainOf(opts.orderType, opts.mode, where);
    var availability = OO.model.domain(opts.orderType, opts.mode, where);
    var clock = null;
    var rows = arr(pricetags && pricetags.rows)
      .filter(function (r) { return isRecord(r) && r.active === true && r.domain === domain; })
      .map(function (r, i) { return { r: r, i: i }; })
      .sort(function (a, b) { return num(a.r.sortOrder) - num(b.r.sortOrder) || a.i - b.i; })
      .map(function (w) { return w.r; });
    var winner = null;
    for (var i = 0; i < rows.length && !winner; i++) {
      var r = rows[i];
      if (OO.model.parseRules(r.rules).length) {
        clock = clock || OO.model.storeNow({ timezone: opts.timeZone || null }, opts.now);
        if (!OO.model.isAvailable(r.rules, clock, availability)) continue;
      }
      if (r.checkMembership && !memberMatches(r, opts.member)) continue;
      winner = r;
    }
    var prices = pricetags && isRecord(pricetags.prices) ? pricetags.prices : null;
    var active = !!winner && !!prices && !winner.override;
    return {
      tagId: winner ? winner.tagId : null,
      name: winner ? String(winner.name || '') : '',
      domain: domain,
      active: active,
      override: !!(winner && winner.override),
      membership: active && winner.checkMembership === true,
      prices: active && isRecord(prices[winner.tagId]) ? prices[winner.tagId] : emptyPrices(),
    };
  }

  function price(pricing, kind, id, sizeId) {
    if (!pricing || pricing.active !== true || pricing.tagId == null) return null;
    var table = pricing.prices && pricing.prices[kind];
    var key = keyOf(kind, id, sizeId);
    var v = isRecord(table) && own(table, key) ? table[key] : null;
    return typeof v === 'number' && isFinite(v) && v > 0 ? v : null;
  }
  // The entity's price is the tag's price for it: a row may claim the tag.
  function claims(pricing, kind, id, sizeId, value) {
    var t = price(pricing, kind, id, sizeId);
    return t !== null && same(value, t);
  }

  // ---- priceMenu ---------------------------------------------------------------------------------------------------
  // fn over a list: the SAME list when fn returns every element as it was.
  function mapSame(list, fn) {
    if (!Array.isArray(list)) return list;
    var out = null;
    list.forEach(function (x, i) {
      var y = fn(x);
      if (y !== x) { out = out || list.slice(); out[i] = y; }
    });
    return out || list;
  }
  function mapValues(obj, fn) {
    if (!isRecord(obj)) return obj;
    var out = null;
    Object.keys(obj).forEach(function (k) {
      var y = fn(obj[k]);
      if (y !== obj[k]) { out = out || Object.assign({}, obj); out[k] = y; }
    });
    return out || obj;
  }

  function priceMenu(menu, selection) {
    if (!isRecord(menu)) return menu;
    var on = !!(selection && selection.active === true && selection.tagId != null);
    if (!on && !menu.pricetag) return menu; // a base menu and no tag
    var tagId = on ? selection.tagId : null;
    var changed = emptyPrices();
    var any = false;

    // One priceable entity (a product, size, modifier option, combo or fixed-size combo option): the same object when
    // its price stays, else a copy at the tag price (with basePrice / pricetagId) or back at its base.
    function priced(e, kind, id, sizeId) {
      if (!isRecord(e)) return e;
      var base = own(e, 'basePrice') ? num(e.basePrice) : num(e.price);
      var t = on ? price(selection, kind, id, sizeId) : null;
      var tagged = t !== null && !same(t, base);
      if (tagged) { changed[kind][keyOf(kind, id, sizeId)] = t; any = true; }
      if (tagged ? (same(e.price, t) && own(e, 'basePrice') && e.pricetagId === tagId) : (!own(e, 'basePrice') && !own(e, 'pricetagId'))) return e;
      var out = Object.assign({}, e);
      delete out.basePrice;
      delete out.pricetagId;
      if (tagged) { out.price = t; out.basePrice = base; out.pricetagId = tagId; } else out.price = base;
      return out;
    }
    function groupsOf(groups) {
      return mapSame(groups, function (g) {
        if (!isRecord(g)) return g;
        var options = mapSame(g.options, function (o) { return isRecord(o) ? priced(o, 'modifier', o.id) : o; });
        return options === g.options ? g : Object.assign({}, g, { options: options });
      });
    }
    function sizesOf(productId, sizes) {
      return mapSame(sizes, function (s) { return isRecord(s) ? priced(s, 'size', productId, s.id) : s; });
    }
    function product(p) {
      if (!isRecord(p)) return p;
      var q = priced(p, 'product', p.id);
      var sizes = sizesOf(p.id, p.sizes);
      var groups = groupsOf(p.groups);
      return q === p && sizes === p.sizes && groups === p.groups ? p : Object.assign({}, q, { sizes: sizes, groups: groups });
    }
    function comboOption(o) {
      if (!isRecord(o)) return o;
      var q = o.sizeId != null ? priced(o, 'size', o.productId, o.sizeId) : o; // a fixed size: its size's tag price
      var sizes = sizesOf(o.productId, o.sizes);
      var groups = groupsOf(o.groups);
      return q === o && sizes === o.sizes && groups === o.groups ? o : Object.assign({}, q, { sizes: sizes, groups: groups });
    }
    function combo(c) {
      if (!isRecord(c)) return c;
      var q = priced(c, 'combo', c.id);
      var groups = mapSame(c.groups, function (g) {
        if (!isRecord(g)) return g;
        var options = mapSame(g.options, comboOption);
        return options === g.options ? g : Object.assign({}, g, { options: options });
      });
      return q === c && groups === c.groups ? c : Object.assign({}, q, { groups: groups });
    }

    var products = mapValues(menu.products, product);
    var redemptions = menu.redemptionProducts ? mapValues(menu.redemptionProducts, product) : null;
    var combos = mapValues(menu.combos, combo);
    var record = any ? {
      tagId: tagId, name: String(selection.name || ''), domain: String(selection.domain || ''), active: true, override: false,
      membership: selection.membership === true, prices: changed,
    } : null;
    var sameRecord = record ? JSON.stringify(menu.pricetag || null) === JSON.stringify(record) : !own(menu, 'pricetag');
    if (products === menu.products && (!redemptions || redemptions === menu.redemptionProducts) && combos === menu.combos && sameRecord) return menu;
    var out = Object.assign({}, menu, { products: products, combos: combos });
    if (redemptions) out.redemptionProducts = redemptions;
    if (record) out.pricetag = record; else delete out.pricetag;
    return out;
  }

  function repriceCart(lines, menu, selection) { return OO.cart.reprice(lines, priceMenu(menu, selection)); }

  // ---- lines -------------------------------------------------------------------------------------------------------
  function modsUseTag(pricing, mods) {
    return arr(mods).some(function (m) { return !!m && claims(pricing, 'modifier', m.addOnId, null, m.price); });
  }
  function lineUsesTag(line, pricing) {
    if (!isRecord(line) || !pricing || pricing.active !== true) return false;
    if (line.kind === 'combo') {
      return claims(pricing, 'combo', line.comboId, null, line.comboPrice) || arr(line.picks).some(function (p) {
        return !!p && ((p.sizeId != null && claims(pricing, 'size', p.productId, p.sizeId, p.price)) || modsUseTag(pricing, p.modifiers));
      });
    }
    var tagged = line.sizeId != null
      ? claims(pricing, 'size', line.itemId, line.sizeId, line.basePrice)
      : claims(pricing, 'product', line.itemId, null, line.basePrice);
    return tagged || modsUseTag(pricing, line.modifiers);
  }

  // ---- the payload contributor (see the header) ---------------------------------------------------------------------
  // A data[] row's size: a combo child's size_id, an item row's size[0].id; null without one.
  function rowSizeId(row) {
    if (row.size_id !== undefined && row.size_id !== null) return row.size_id;
    var s = arr(row.size)[0];
    return isRecord(s) && s.id !== undefined && s.id !== null ? s.id : null;
  }
  function payloadLine(row, line, ctx) {
    var pricing = ctx && ctx.pricetag;
    if (!isRecord(row) || !pricing || pricing.active !== true || pricing.tagId == null) return null;
    var fields = { price_tag_id: pricing.tagId, is_price_tag_applied: true };
    var sizeId = rowSizeId(row);
    var tagged = sizeId != null
      ? claims(pricing, 'size', row.product_id, sizeId, row.price)
      : !row.is_combo && claims(pricing, 'product', row.product_id, null, row.price);
    var out = tagged ? Object.assign({}, fields) : null;
    var mods = arr(row.modifiers);
    var next = null;
    mods.forEach(function (m, i) {
      if (isRecord(m) && claims(pricing, 'modifier', m.addOnId, null, m.price)) {
        next = next || mods.slice();
        next[i] = Object.assign({}, m, fields);
      }
    });
    if (next) { out = out || {}; out.modifiers = next; }
    return out;
  }
  if (OO.payload && typeof OO.payload.contribute === 'function') OO.payload.contribute('pricetags', { line: payloadLine });

  OO.pricetags = OO.lib.pricetags = {
    domainOf: domainOf,
    memberOf: memberOf,
    select: select,
    price: price,
    priceMenu: priceMenu,
    repriceCart: repriceCart,
    lineUsesTag: lineUsesTag,
  };
})();


// ===================== src/lib/membership.jsx =====================
// src/lib/membership.jsx: OO.membership (alias OO.lib.membership): the membership engine (Batch 2 P2-03 / P2-04; B §3.6,
// REF shared/membership/lib/{membershipBenefits,membershipOffers,membershipPricing}.ts, CartContext's
// membershipPricingActive and the checkout wire). A signed-in customer's membership type (get_customer_wallet_points'
// membership_type_obj) carries discount_meta: a percentage off chosen products or categories (the ITEM saving, per line)
// and a percentage off the order (the CART saving), each with an optional cap. This file prices them (P2-03) and adds
// them to the summary and the checkout payload, with the mismatch guard (P2-04, "The order's membership" below).
// Pure: no JSX, no document or OO.http at load time (the tests and the parity generator run it raw in node:vm). The
// engine functions are pure and read no clock (memberships have no time rules); resolve() and the contributors also
// read the signed-in customer's wallet and the active pricetag from OO.store / OO.data at call time.
//
// The rules the server will honour. The checkout guard (OnlineOrderValidationController::guardMembershipRules /
// guardMembership / guardMembershipLineFindings / guardPayableFinding) decides whether a membership saving is accepted,
// and it parses discount_meta a little differently from REF (membershipBenefits.ts). A rule counts here only where
// BOTH read it the same way, so the SPA never sends a saving the server would refuse (and never one REF wouldn't give):
//   - discount_meta: membership_type_obj.discount_meta only (what the guard reads from customer_membership_type; REF's
//     savings read it there too), a JSON string decoded up to 3 times.
//   - each kind (cart, product, category): a nested object { on, pct, max, items } when meta[kind] is an object, else
//     the legacy flat keys {kind}_discount, {kind}_discount_value, max_discount_value_{kind}, products / categories.
//     A nested object without `on` is OFF (REF falls back to the flat keys there; the guard does not).
//   - on: true, 1, or a string 'true' / 'yes' / 'on' / '1' (REF isEnabled ∩ the guard's guardTruthy).
//   - pct: a number or a numeric string (PHP is_numeric: '10', ' 10 ', '1e1'; never '10%'), > 0. Over 100 saves 100 %.
//   - max: the same numbers; > 0 caps the kind's savings in total, else no cap.
//   - items: a list or a CSV of whole positive ids ('12', 12, ' 12 '). Names, '1.5', '12abc' and { id } objects are
//     dropped (REF reads no { id } object; the guard truncates '1.5' to 1). A product / category rule with no id is off.
//   - a deleted membership type (membership_type_obj.is_delete) or one without a whole id is no membership: the guard
//     rejects it (guardMembership).
//
// API:
//   of(wallet) → Membership | null   (REF resolveMembershipBenefits + resolveCheckoutMembership; built on
//       OO.pricetags.memberOf) wallet = get_customer_wallet_points' body; null without membership_type_obj (or as above).
//       Membership = { id (membership_type_obj.id, else membership_type: the customer's type, a whole number > 0), name
//       (membership_type_obj.name trimmed, '' when none), desc, customerId (the wallet's customer_id | null), priceTagId,
//       membershipId, membershipType (OO.pricetags.memberOf), rules: { cart, product, category } }. Parsed rules are
//       memoised by the discount_meta text.
//   rulesOf(discountMeta) → { cart, product, category }: each Rule = { kind, pct, max (number | null), ids ([number];
//       [] for cart) } or null when off (the rules above).
//   offerFor({ productId, categoryId }, membership) → Offer | null   (REF matchMembershipOffer) the product rule when
//       it names productId, else the category rule when it names categoryId: { kind 'product' | 'category', pct, max }.
//   itemPrice(price, offer) → { offer, originalPrice, discountedPrice, saving } | null   (REF calculateMembershipItemPrice)
//       one unit's member price for the menu card and the item sheet (P2-17 / P2-21): null without an offer, for a price
//       ≤ 0, and when the offer has a max (the cap is shared by the whole order, so no single price can be shown).
//   allocateCap(raws, max) → [saving]   (REF allocateCap) the savings raws (dollars, ≥ 0) take when their total is held
//       to max (null / ≤ 0: no cap): the target min(Σ raws, max) in whole cents, split by largest remainder (equal
//       remainders: the earlier entry first). The guard checks the same spread (GUARD_ROUNDING_PER_UNIT: a cent per
//       unit, GUARD_AMOUNT_TOLERANCE for the cap).
//   eligible(line, pricing) → bool   (REF !isExcludedLine) an item line that can take a membership saving: not a combo,
//       not a reward or free line (line.reward / line.free), and no membership tag price on it (P2-01: with pricing, a
//       Selection or a priced menu's `pricetag` record whose `membership` is true, OO.pricetags.lineUsesTag).
//   discount(lines, membership, { store, pricing, enabled }) → Discount   (REF calculateMembershipCartDiscount; B §3.6)
//       enabled false, no membership, no rule or nothing eligible → EMPTY. Per eligible line (qty = max(1, line.qty)):
//         base   = round2(line.basePrice × qty)        the size price when sized, else the product price (at a tag)
//         mods   = round2(Σ m.price × (m.count − m.free) × qty)   the CHARGED modifiers (free allowance units excluded)
//         a line whose base + mods is 0 takes nothing.
//       ITEM saving: its offer (offerFor) → raw = min(base × pct / 100, base); per kind, allocateCap(raws, rule.max).
//       At a store with applyDiscountToModifiers the validator also takes the line's % (saving / base) off its charged
//       modifiers: that part (modsSaving = round2(mods × saving / base)) is added to the line's item saving (pricing.jsx
//       contract), so the payload's subtotal is the validator's.
//       CART saving: eligible = round2(max(0, base − item saving + mods)) (net of modsSaving at such a store, which
//       keeps the cart saving within the guard's Rule P allowance); raw = min(eligible × pct / 100, eligible);
//       allocateCap(raws, cart.max). Combos, member-tag lines and reward lines are in neither saving.
//       Discount = { name (membership.name, else 'Membership'), item (Σ item savings, modifier part included), cart
//         (Σ cart savings), total (item + cart), cartPercent, cartMaximum, cartEligible (Σ eligible when a cart rule
//         applies, else 0), lines: [{ key, productId, categoryId, kind, pct (the rule's), base, mods, saving (the item
//         saving on the product price), modsSaving, percent (saving / base × 100: the payload's line discount %),
//         cartEligible, cartSaving, total }] (lines with a saving only, in cart order) }. Every amount is round2'd.
//   exclusivity({ coupon, member, points, rewards, signedIn, kiosk, apply }) → Stacking   (REF; see Stacking below)
//   resolve(lines, store, opts) → { membership, pricing, stack, discount }   the order's membership (P2-04, below).
//   EMPTY: the Discount of no membership (frozen). ERRORS: { code: text } of the payload refusals. ROOT_BASE: 'subtotal'.
//
// Stacking (REF develop; the coupon lane P2-09…14 and the wallet lane P2-05…07 call exclusivity; they never re-decide):
//   - Member SAVINGS (item + cart) apply only when signed in, not at a kiosk, with a membership, and with NO coupon
//     on the order (its presence, eligible or not: REF keys on state.appliedCoupon), NO wallet points in use (type 1)
//     and NO reward line (type 2) (CartContext membershipPricingActive). They are never refused or removed: they are
//     simply off while one of those is on, and come back when it goes (REF shows nothing; reason says why).
//   - Member TAG PRICES (P2-01) are prices, not discounts: they stay with a coupon, points or rewards.
//   - A coupon and wallet points never combine; the LAST one applied wins. Applying a coupon clears the points with
//     "Wallet points were removed: they can't be combined with a coupon." (CartContext applyCoupon); using points
//     removes the coupon (OnlineOrderOffersSection choosePoints; before it, the warning "Using wallet points will
//     remove your coupon: they can't be combined.").
//   - A reward line (type 2) and a coupon never combine: a coupon is REFUSED while a reward line is in the cart
//     ("Remove your product rewards before applying a coupon."); adding a reward line removes the coupon ("The coupon
//     was removed: it can't be combined with a product reward.") and clears the points (silently: REF's slider max is
//     0 with a reward line).
//   - Points are refused while a reward line is in the cart (REF: the slider's max is 0).
//   exclusivity inputs: coupon (the applied coupon, any truthy value = present), member (of(wallet) | null), points
//   (type-1 points selected, a number), rewards (the number of reward lines; both may come as wallet: { points,
//   rewards } instead), signedIn, kiosk (bools), apply
//   (null | 'coupon' | 'points' | 'reward': the action about to happen). Stacking = { savings (bool: member savings
//   apply after the action), memberPrices (bool: a member's tag prices apply: signed in with a membership, not at a
//   kiosk), reason (why savings are off: 'SIGNED_OUT' | 'KIOSK' | 'NO_MEMBERSHIP' | 'COUPON' | 'POINTS' | 'REWARDS' |
//   null), refuse (null | { code: 'REWARDS_BLOCK_COUPON' | 'REWARDS_BLOCK_POINTS', text }), drop ([] | ['points'] |
//   ['coupon'] | ['coupon', 'points']: what the action removes), notice (null | { code: 'POINTS_REMOVED' |
//   'COUPON_REMOVED' | 'COUPON_REMOVED_FOR_REWARD', text }: what the customer is told), warning (null | { code:
//   'POINTS_REMOVE_COUPON', text }: before apply 'points' while a coupon is on) }. A refused action changes nothing.
//
// The order's membership (P2-04). resolve(lines, store, opts) → { membership, pricing, stack, discount } is the ONE
// place the summary and the payload get it from, so the two always agree:
//   membership  opts.membership when given (a Membership; null forces it off), else the signed-in customer's: of(
//               OO.store.wallet(store.onlineKey)) (the store's settled get_customer_wallet_points read, P2-02).
//   pricing     opts.pricing when given, else the active pricetag OO.data.selection(store.onlineKey) (P2-02), else the
//               record the store's cart is priced with: what eligible() reads for member-tag lines.
//   stack       exclusivity({ coupon: opts.coupon, member, points: opts.walletAmount (> 0 = points in use), rewards: the
//               lines with line.reward, signedIn: a session (or an explicit opts.membership) }).
//   discount    discount(lines, membership, { store, pricing }) when stack.savings, else EMPTY.
//   (The wallet lane's contract: wallet points in use reach the summary as opts.walletAmount > 0, the one wallet option
//   OO.payload forwards to its own summary, and a reward line carries line.reward; either turns member savings off.)
// Summary contributor 'membership-savings' (OO.pricing.contribute): membershipItem = discount.item (the modifier part at
//   an applyDiscountToModifiers store included), membershipCart = discount.cart, and their rows right after Discount:
//   { key: 'membershipItem', label: '{name} item discount' } and { key: 'membershipCart', label: '{name} order
//   discount' } (REF OnlineOrderPaymentSummary), value = the saving (> 0), text = minus money; none without a saving.
// Payload contributor 'membership-savings' (OO.payload.contribute; B §3.6, REF sipoCheckoutWire): the payload's summary
//   has no opts.membership, so the hooks resolve exactly as it did (the store's membership, with the coupon and
//   walletAmount it got; ctx.membership is never read) and check that its summary contribution carried these amounts
//   (else MEMBERSHIP_CHANGED).
//   line  an item row whose line has an ITEM saving: discount = its percent (saving / base × 100),
//         is_membership_discount_applied: true, discount_reason = the name. A line with only a cart saving keeps 0.
//   root  when the order has a saving or a member-tag line: membership_id (the whole id) and membership_name. With a
//         saving: customer_id = ctx.customerId, else the membership's own (the same store-scoped wallet read); with a
//         CART saving: discount = cart × 100 / ROOT_BASE and discount_reason = the name. ROOT_BASE is the payload's
//         `subtotal` (REF divides by subtotal + delivery): the server's own reading of a membership root % (OnlineOrder-
//         CartDiscount, the emails) and the POS's (online_order_pricing.dart: "explained" = subtotal × rate, the staff
//         check flag) are % of the subtotal. payable_amount already nets both savings (OO.pricing.summary).
//   Mismatch guard: at summary time, what the server won't honour is never priced (rulesOf, of(): a deleted type, no
//         whole id); at payload time the hook REFUSES (it throws; OO.payload reports code CONTRIBUTOR with this error as
//         `cause`: { kind: 'membership', code, text }) when
//           MEMBERSHIP_UNVERIFIED  a saving but no customer_id at all (guardMembership verifies the membership through
//                                  it), or ctx.freshWallet null (the pre-flight read failed): "We couldn't confirm your
//                                  membership. Please sign in again and try again."
//           MEMBERSHIP_CHANGED     ctx.customerId is another customer than the membership's, the summary priced other
//                                  amounts, or ctx.freshWallet (a fresh get_customer_wallet_points body read at Pay,
//                                  REF's pre-flight) gives this order other savings (none where it has some, some where
//                                  it has none) or no longer holds the membership it names: "Your membership discount
//                                  changed. Please review your cart and try again."
//   ERRORS: the codes above with their text (the pages' copy).
//
// Load order (build/concat.mjs): after pricing.jsx / payload.jsx and before store.jsx. Its P2-04 contributors register
// at load time (the summary rows, then the payload hooks), after the pricetags engine's.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  // REF roundMoney (membershipPricing.ts): the savings are rounded exactly as REF rounds them.
  function money(v) { return Math.round((isFinite(v) ? v : 0) * 100) / 100; }
  function positive(v) { var n = Number(v); return isFinite(n) && n > 0 ? n : 0; }
  // A JSON field that may arrive as a string, encoded up to 3 times; null when it doesn't parse.
  function parsed(v) {
    for (var i = 0; i < 3 && typeof v === 'string'; i++) {
      try { v = JSON.parse(v); } catch (e) { return null; }
    }
    return v;
  }

  // ---- rules: what REF and the guard both read (see the header) ----------------------------------------------------
  var KINDS = ['cart', 'product', 'category'];
  var LEGACY_IDS = { cart: null, product: 'products', category: 'categories' };
  var NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/; // PHP is_numeric (after trimming): no '%', no hex

  function enabled(v) {
    if (v === true || v === 1) return true;
    return typeof v === 'string' && ['1', 'true', 'yes', 'on'].indexOf(v.trim().toLowerCase()) >= 0;
  }
  function numeric(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (typeof v === 'boolean') return v ? 1 : 0;
    if (typeof v !== 'string' || !NUMERIC.test(v.trim())) return null;
    var n = Number(v.trim());
    return isFinite(n) ? n : null;
  }
  function wholeId(v) {
    if (typeof v === 'number') return isFinite(v) && v > 0 && Math.floor(v) === v ? v : null;
    if (typeof v !== 'string' || !/^\d+$/.test(v.trim())) return null;
    var n = Number(v.trim());
    return n > 0 ? n : null;
  }
  // Ids from a list or a CSV (REF toTargets ∩ guardIdList): whole positive ids only, each once, in order.
  function idsOf(v) {
    var out = [];
    var list = Array.isArray(v) ? v : (v === null || v === undefined ? [] : [v]);
    list.forEach(function (entry) {
      var parts = typeof entry === 'string' ? entry.split(',') : [entry];
      parts.forEach(function (p) {
        var id = wholeId(p);
        if (id !== null && out.indexOf(id) < 0) out.push(id);
      });
    });
    return out;
  }
  function ruleOf(meta, kind) {
    var nested = isRecord(meta[kind]) ? meta[kind] : null;
    var on = nested ? own(nested, 'on') && enabled(nested.on) : enabled(meta[kind + '_discount']);
    if (!on) return null;
    var pct = numeric(nested ? nested.pct : meta[kind + '_discount_value']);
    if (pct === null || !(pct > 0)) return null;
    var max = numeric(nested ? nested.max : meta['max_discount_value_' + kind]);
    var ids = kind === 'cart' ? [] : idsOf(nested ? nested.items : meta[LEGACY_IDS[kind]]);
    if (kind !== 'cart' && !ids.length) return null;
    return { kind: kind, pct: pct, max: max !== null && max > 0 ? max : null, ids: ids };
  }
  function rulesOf(discountMeta) {
    var meta = parsed(discountMeta);
    var out = { cart: null, product: null, category: null };
    if (!isRecord(meta)) return out;
    KINDS.forEach(function (k) { out[k] = ruleOf(meta, k); });
    return out;
  }
  // Parsed rules by discount_meta text (REF RULES_CACHE_MAX): the summary runs on every render.
  var RULES_CACHE_MAX = 100;
  var rulesCache = {};
  var rulesCacheSize = 0;
  function cachedRules(meta) {
    var key = typeof meta === 'string' ? meta : JSON.stringify(meta === undefined ? null : meta);
    if (own(rulesCache, key)) return rulesCache[key];
    if (rulesCacheSize >= RULES_CACHE_MAX) { rulesCache = {}; rulesCacheSize = 0; }
    rulesCacheSize += 1;
    return (rulesCache[key] = rulesOf(meta));
  }
  function copyRules(r) {
    var out = {};
    KINDS.forEach(function (k) { out[k] = r[k] ? Object.assign({}, r[k], { ids: r[k].ids.slice() }) : null; });
    return out;
  }

  // ---- of ------------------------------------------------------------------------------------------------------------
  function text(v) { return typeof v === 'string' ? v.trim() : ''; }
  function of(wallet) {
    if (!isRecord(wallet)) return null;
    var obj = isRecord(wallet.membership_type_obj) ? wallet.membership_type_obj : null;
    if (!obj || enabled(obj.is_delete)) return null;
    var id = wholeId(typeof obj.id === 'number' ? obj.id : String(obj.id == null ? '' : obj.id));
    if (id === null) id = wholeId(typeof wallet.membership_type === 'number' ? wallet.membership_type : String(wallet.membership_type == null ? '' : wallet.membership_type));
    if (id === null) return null;
    var member = OO.pricetags && typeof OO.pricetags.memberOf === 'function' ? OO.pricetags.memberOf(wallet) : null;
    var name = text(obj.name);
    var desc = text(obj.desc);
    var customerId = text(typeof wallet.customer_id === 'number' ? String(wallet.customer_id) : wallet.customer_id);
    return {
      id: id, name: name, desc: desc.toLowerCase() === name.toLowerCase() ? '' : desc, customerId: customerId || null,
      priceTagId: member ? member.priceTagId : null, membershipId: member ? member.membershipId : null,
      membershipType: member ? member.membershipType : null,
      rules: copyRules(cachedRules(obj.discount_meta)),
    };
  }

  // ---- offers and one item's price -------------------------------------------------------------------------------------
  function covers(rule, id) {
    var n = wholeId(typeof id === 'number' ? id : String(id == null ? '' : id));
    return !!rule && n !== null && rule.ids.indexOf(n) >= 0;
  }
  function offerFor(target, membership) {
    var rules = membership && membership.rules;
    if (!rules || !target) return null;
    var rule = covers(rules.product, target.productId) ? rules.product : (covers(rules.category, target.categoryId) ? rules.category : null);
    return rule ? { kind: rule.kind, pct: rule.pct, max: rule.max } : null;
  }
  function itemPrice(price, offer) {
    var p = money(positive(price));
    if (!offer || p <= 0 || !(offer.pct > 0) || (offer.max !== null && offer.max > 0)) return null;
    var saving = money(Math.min(p * offer.pct / 100, p));
    if (saving <= 0) return null;
    return { offer: offer, originalPrice: p, discountedPrice: money(Math.max(0, p - saving)), saving: saving };
  }

  // ---- allocateCap (REF; largest remainder in cents) ---------------------------------------------------------------
  function allocateCap(raws, max) {
    var list = arr(raws).map(function (r) { return Math.max(0, num(r)); });
    var total = list.reduce(function (t, r) { return t + r; }, 0);
    var cap = max !== null && max !== undefined && num(max) > 0 ? num(max) : Infinity;
    var applied = Math.min(total, cap);
    if (total <= 0 || applied <= 0) return list.map(function () { return 0; });
    var target = Math.round(applied * 100);
    var shares = list.map(function (r, i) {
      var exact = r / total * target;
      var cents = Math.floor(exact);
      return { i: i, cents: cents, rest: exact - cents };
    });
    var left = target - shares.reduce(function (t, s) { return t + s.cents; }, 0);
    shares.slice().sort(function (a, b) { return b.rest - a.rest || a.i - b.i; }).forEach(function (s) {
      if (left <= 0) return;
      s.cents += 1;
      left -= 1;
    });
    return shares.map(function (s) { return s.cents / 100; });
  }

  // ---- lines ---------------------------------------------------------------------------------------------------------
  function eligible(line, pricing) {
    if (!isRecord(line) || line.kind !== 'item' || line.reward || line.free) return false;
    if (pricing && pricing.membership === true && OO.pricetags && OO.pricetags.lineUsesTag(line, pricing)) return false;
    return true;
  }
  // One unit's charged modifiers: Σ price × (count − free) (cart.jsx's allowance-aware rows).
  function chargedMods(line) {
    return arr(line.modifiers).reduce(function (t, m) { return t + num(m && m.price) * Math.max(0, num(m && m.count) - num(m && m.free)); }, 0);
  }

  var EMPTY = Object.freeze({ name: '', item: 0, cart: 0, total: 0, cartPercent: 0, cartMaximum: null, cartEligible: 0, lines: Object.freeze([]) });

  function discount(lines, membership, opts) {
    opts = opts || {};
    if (opts.enabled === false || !membership || !membership.rules) return EMPTY;
    var rules = membership.rules;
    if (!rules.cart && !rules.product && !rules.category) return EMPTY;
    var modsToo = !!(opts.store && opts.store.applyDiscountToModifiers === true);
    var work = arr(lines).filter(function (l) { return eligible(l, opts.pricing); }).map(function (l) {
      var qty = Math.max(1, num(l.qty));
      var base = money(positive(l.basePrice) * qty);
      var mods = money(chargedMods(l) * qty);
      var offer = offerFor({ productId: l.itemId, categoryId: l.categoryId }, membership);
      return {
        line: l, base: base, mods: mods, offer: offer,
        raw: offer ? Math.min(base * offer.pct / 100, base) : 0,
        saving: 0, modsSaving: 0, cartEligible: 0, cartRaw: 0, cartSaving: 0,
      };
    }).filter(function (w) { return w.base + w.mods > 0; });
    if (!work.length) return EMPTY;

    ['product', 'category'].forEach(function (kind) {
      var scoped = work.filter(function (w) { return w.offer && w.offer.kind === kind; });
      var got = allocateCap(scoped.map(function (w) { return w.raw; }), rules[kind] ? rules[kind].max : null);
      scoped.forEach(function (w, i) { w.saving = got[i]; });
    });
    work.forEach(function (w) {
      w.modsSaving = modsToo && w.saving > 0 && w.base > 0 ? money(w.mods * w.saving / w.base) : 0;
      w.cartEligible = money(Math.max(0, w.base - w.saving + w.mods - w.modsSaving));
    });
    var cartRule = rules.cart;
    if (cartRule) {
      work.forEach(function (w) { w.cartRaw = Math.min(w.cartEligible * cartRule.pct / 100, w.cartEligible); });
      var got = allocateCap(work.map(function (w) { return w.cartRaw; }), cartRule.max);
      work.forEach(function (w, i) { w.cartSaving = got[i]; });
    }

    var out = work.map(function (w) {
      var saving = money(w.saving);
      var cartSaving = money(w.cartSaving);
      return {
        key: w.line.key, productId: w.line.itemId, categoryId: w.line.categoryId == null ? null : w.line.categoryId,
        kind: w.offer ? w.offer.kind : null, pct: w.offer ? w.offer.pct : 0, base: w.base, mods: w.mods,
        saving: saving, modsSaving: w.modsSaving, percent: w.base > 0 ? saving / w.base * 100 : 0,
        cartEligible: cartRule ? w.cartEligible : 0, cartSaving: cartSaving, total: money(saving + w.modsSaving + cartSaving),
      };
    }).filter(function (x) { return x.total > 0; });
    var item = money(out.reduce(function (t, x) { return t + x.saving + x.modsSaving; }, 0));
    var cart = money(out.reduce(function (t, x) { return t + x.cartSaving; }, 0));
    var total = money(item + cart);
    if (!(total > 0)) return EMPTY;
    return {
      name: membership.name || 'Membership', item: item, cart: cart, total: total,
      cartPercent: cartRule ? cartRule.pct : 0, cartMaximum: cartRule ? cartRule.max : null,
      cartEligible: cartRule ? money(work.reduce(function (t, w) { return t + w.cartEligible; }, 0)) : 0,
      lines: out,
    };
  }

  // ---- stacking (see the header) -------------------------------------------------------------------------------------
  var TEXT = {
    POINTS_REMOVED: 'Wallet points were removed: they can’t be combined with a coupon.',
    COUPON_REMOVED: 'Your coupon was removed: it can’t be combined with wallet points.',
    COUPON_REMOVED_FOR_REWARD: 'The coupon was removed: it can’t be combined with a product reward.',
    REWARDS_BLOCK_COUPON: 'Remove your product rewards before applying a coupon.',
    REWARDS_BLOCK_POINTS: 'Wallet points can’t be used with a product reward in your cart.',
    POINTS_REMOVE_COUPON: 'Using wallet points will remove your coupon: they can’t be combined.',
  };
  function note(code) { return { code: code, text: TEXT[code] }; }

  function exclusivity(input) {
    var s = input || {};
    var w = isRecord(s.wallet) ? s.wallet : {};
    var coupon = !!s.coupon;
    var points = num(s.points !== undefined ? s.points : w.points) > 0;
    var rewards = num(s.rewards !== undefined ? s.rewards : w.rewards) > 0;
    var apply = s.apply === 'coupon' || s.apply === 'points' || s.apply === 'reward' ? s.apply : null;
    var out = { savings: false, memberPrices: false, reason: null, refuse: null, drop: [], notice: null, warning: null };
    if (apply === 'coupon' && rewards) out.refuse = note('REWARDS_BLOCK_COUPON');
    else if (apply === 'points' && rewards) out.refuse = note('REWARDS_BLOCK_POINTS');
    else if (apply === 'coupon') {
      if (points) { out.drop = ['points']; out.notice = note('POINTS_REMOVED'); points = false; }
      coupon = true;
    } else if (apply === 'points') {
      if (coupon) { out.drop = ['coupon']; out.notice = note('COUPON_REMOVED'); coupon = false; }
      points = true;
    } else if (apply === 'reward') {
      if (coupon) { out.drop.push('coupon'); out.notice = note('COUPON_REMOVED_FOR_REWARD'); coupon = false; }
      if (points) { out.drop.push('points'); points = false; }
      rewards = true;
    }
    if (out.refuse) { // nothing changes: judge the state as it is
      coupon = !!s.coupon;
      points = num(s.points !== undefined ? s.points : w.points) > 0;
      rewards = num(s.rewards !== undefined ? s.rewards : w.rewards) > 0;
    }
    if (!apply && coupon && !points && !rewards) out.warning = note('POINTS_REMOVE_COUPON');
    var signedIn = !!s.signedIn;
    var kiosk = !!s.kiosk;
    out.memberPrices = signedIn && !kiosk && !!s.member;
    if (!signedIn) out.reason = 'SIGNED_OUT';
    else if (kiosk) out.reason = 'KIOSK';
    else if (!s.member) out.reason = 'NO_MEMBERSHIP';
    else if (coupon) out.reason = 'COUPON';
    else if (points) out.reason = 'POINTS';
    else if (rewards) out.reason = 'REWARDS';
    out.savings = out.reason === null;
    return out;
  }

  // ---- the order's membership: resolve, the summary rows, the payload (P2-04; see the header) ----------------------
  var ROOT_BASE = 'subtotal'; // the payload field the root membership % is of (see the header)
  var ERRORS = {
    MEMBERSHIP_UNVERIFIED: 'We couldn’t confirm your membership. Please sign in again and try again.',
    MEMBERSHIP_CHANGED: 'Your membership discount changed. Please review your cart and try again.',
  };
  function membershipError(code) {
    var e = new Error(ERRORS[code]);
    e.kind = 'membership';
    e.code = code;
    e.text = ERRORS[code];
    return e;
  }
  function cents(v) { return Math.round(num(v) * 100); }

  function storeKey(store) { return store && store.onlineKey !== undefined && store.onlineKey !== null ? String(store.onlineKey) : ''; }
  function signedIn() {
    try {
      var s = OO.store && typeof OO.store.session === 'function' ? OO.store.session() : null;
      return !!(s && s.customer && s.customer.uid !== undefined && s.customer.uid !== null && String(s.customer.uid));
    } catch (e) { return false; }
  }
  function storeMembership(key) {
    try { return key && OO.store && typeof OO.store.wallet === 'function' ? of(OO.store.wallet(key)) : null; } catch (e) { return null; }
  }
  function storePricing(key) {
    try {
      var sel = key && OO.data && typeof OO.data.selection === 'function' ? OO.data.selection(key) : null;
      if (sel) return sel;
      var st = OO.store && typeof OO.store.get === 'function' ? OO.store.get() : null;
      // W5-FIX #2: the store key in any spelling (the route's /Bennys is online_key bennys).
      var same = !!st && !!st.hotel && String(st.hotel).toLowerCase() === String(key).toLowerCase();
      return same && st.cart && st.cart.pricing ? st.cart.pricing : null;
    } catch (e) { return null; }
  }
  function resolve(lines, store, opts) {
    opts = opts || {};
    if (store && store.orderingQr) return { membership: null, pricing: opts.pricing || storePricing(storeKey(store)), stack: { savings: false }, discount: EMPTY };
    var key = storeKey(store);
    var explicit = opts.membership !== undefined;
    var membership = explicit ? (opts.membership || null) : storeMembership(key);
    var pricing = opts.pricing !== undefined ? opts.pricing : storePricing(key);
    var stack = exclusivity({
      coupon: opts.coupon, member: membership, signedIn: explicit ? !!membership : signedIn(),
      points: num(opts.walletAmount) > 0 ? 1 : 0,
      rewards: arr(lines).filter(function (l) { return isRecord(l) && l.reward; }).length,
    });
    var d = stack.savings ? discount(lines, membership, { store: store, pricing: pricing }) : EMPTY;
    return { membership: membership, pricing: pricing, stack: stack, discount: d };
  }

  // The summary contributor: the savings and their rows (pricing.jsx contract). Its last answer is kept, so the payload
  // hooks can check they price the very summary the payload was built on.
  var lastSummary = { lines: null, store: null, d: EMPTY };
  function summaryContribution(c) {
    var r = resolve(c.lines, c.store, c.opts);
    var d = r.discount;
    lastSummary = { lines: c.lines, store: c.store, d: d };
    if (!(d.total > 0)) return null;
    var cur = (c.store && c.store.currency) || '$';
    var minus = OO.format && typeof OO.format.minusMoney === 'function' ? OO.format.minusMoney : function (v) { return '-$' + money(v).toFixed(2); };
    var rows = [];
    if (d.item > 0) rows.push({ key: 'membershipItem', label: d.name + ' item discount', value: d.item, text: minus(d.item, cur), after: 'discount' });
    if (d.cart > 0) rows.push({ key: 'membershipCart', label: d.name + ' order discount', value: d.cart, text: minus(d.cart, cur), after: 'discount' });
    return { membershipItem: d.item, membershipCart: d.cart, rows: rows };
  }

  // The payload hooks resolve once per checkoutPayload call (the hook ctx is a new object per call) with exactly the
  // options OO.payload hands its own summary (coupon, walletAmount), so both price the same membership.
  var lastWire = { ctx: null, r: null };
  function forWire(ctx) {
    if (lastWire.ctx === ctx) return lastWire.r;
    var opts = { coupon: ctx.coupon };
    if (ctx.walletAmount !== undefined && ctx.walletAmount !== null) opts.walletAmount = ctx.walletAmount;
    var r = resolve(ctx.lines, ctx.store, opts);
    lastWire = { ctx: ctx, r: r };
    return r;
  }
  function lineOf(d, key) {
    for (var i = 0; i < d.lines.length; i++) if (d.lines[i].key === key) return d.lines[i];
    return null;
  }
  function memberTagLines(lines, pricing) {
    return !!(pricing && pricing.membership === true && OO.pricetags) && arr(lines).some(function (l) {
      return isRecord(l) && OO.pricetags.lineUsesTag(l, pricing);
    });
  }
  function sameMembership(a, b) {
    return !!a && !!b && a.id === b.id && JSON.stringify(a.rules) === JSON.stringify(b.rules);
  }

  function wireLine(row, line, ctx) {
    if (!isRecord(row) || row.is_combo || !isRecord(line)) return null;
    var r = forWire(ctx);
    var x = lineOf(r.discount, line.key);
    if (!x || !(x.saving > 0)) return null;
    return { discount: x.percent, is_membership_discount_applied: true, discount_reason: r.discount.name };
  }
  function wireRoot(body, ctx) {
    var r = forWire(ctx);
    var d = r.discount;
    var mine = lastSummary.lines === ctx.lines && lastSummary.store === ctx.store ? lastSummary.d : null;
    if (mine && (cents(mine.item) !== cents(d.item) || cents(mine.cart) !== cents(d.cart))) throw membershipError('MEMBERSHIP_CHANGED');
    var m = r.membership;
    var sends = !!m && (d.total > 0 || memberTagLines(ctx.lines, r.pricing));
    if (ctx.freshWallet === null && d.total > 0) throw membershipError('MEMBERSHIP_UNVERIFIED'); // the read failed
    if (ctx.freshWallet !== undefined && ctx.freshWallet !== null) {
      // The fresh read's savings on this order (the coupon, points and rewards as they are) must be these ones, and
      // a membership the order names must still be the customer's (REF's pre-flight + membershipAmountsChanged).
      var fresh = of(ctx.freshWallet);
      var allowed = r.stack.reason === null || r.stack.reason === 'NO_MEMBERSHIP';
      var fd = fresh && allowed ? discount(ctx.lines, fresh, { store: ctx.store, pricing: r.pricing }) : EMPTY;
      if (cents(fd.item) !== cents(d.item) || cents(fd.cart) !== cents(d.cart) || (sends && !sameMembership(fresh, m))) {
        throw membershipError('MEMBERSHIP_CHANGED');
      }
    }
    if (!sends) return null;
    var out = { membership_id: m.id, membership_name: m.name || null };
    if (d.total > 0) {
      var sent = body.customer_id === undefined || body.customer_id === null || body.customer_id === '' ? null : String(body.customer_id);
      if (sent && m.customerId && sent !== m.customerId) throw membershipError('MEMBERSHIP_CHANGED');
      var customerId = sent || m.customerId;
      if (!customerId) throw membershipError('MEMBERSHIP_UNVERIFIED');
      out.customer_id = customerId;
    }
    if (d.cart > 0) {
      var base = num(body[ROOT_BASE]);
      out.discount = base > 0 ? d.cart * 100 / base : 0;
      out.discount_reason = d.name;
    }
    return out;
  }

  if (OO.pricing && typeof OO.pricing.contribute === 'function') OO.pricing.contribute('membership-savings', summaryContribution);
  if (OO.payload && typeof OO.payload.contribute === 'function') OO.payload.contribute('membership-savings', { line: wireLine, root: wireRoot });

  OO.membership = OO.lib.membership = {
    of: of,
    rulesOf: rulesOf,
    offerFor: offerFor,
    itemPrice: itemPrice,
    allocateCap: allocateCap,
    eligible: eligible,
    discount: discount,
    exclusivity: exclusivity,
    resolve: resolve,
    EMPTY: EMPTY,
    ERRORS: ERRORS,
    ROOT_BASE: ROOT_BASE,
  };
})();


// ===================== src/lib/wallet.jsx =====================
// src/lib/wallet.jsx: OO.wallet (alias OO.lib.wallet): the wallet engine (Batch 2 P2-05 / P2-06; B §3.7, REF shared/
// utils/wallet.ts, CartContext's wallet slider and addItem, the product modal's loyalty commit and sipoCheckoutWire's
// wallet maps). A signed-in customer's points (get_customer_wallet_points: wallet_points, the redumption_ratio rows) pay
// for part of an order, one of two ways per store: wallet type 1, the cart-level slider (P2-05), or wallet type 2,
// product reward lines (P2-06; the line shape is lib/cart.jsx's, line.reward) at a store whose loyalty is product-level.
// The checkout UI is pages/checkout-wallet.jsx (P2-07; the pages' helpers below); the checkout pre-flight read is P2-08's.
// Pure: no JSX, no document or OO.http at load time (the tests and the parity generator run it raw in node:vm). The
// engine functions read no clock; the contributors also read the store's settled wallet (OO.store.wallet) at call time.
//
// The rules the server will honour. Wherever REF (develop) and the server disagree, the server wins, so the SPA never
// sends a redemption that checkout answers with ecode 700 (WalletPointsRatioRedumptionController::validateWalletPoints)
// or 312 (the checkout guard's Rule W, OnlineOrderValidationController guardWallet* / walletGuard*):
//   - the row: the redemption row of the order's channel (channel() below) with ratio_points > 0, else an ALL row (REF;
//     the server never returns one: its read whitelists the five channels). validateWalletPoints looks up exactly the
//     row named by wallet_order_type, so the payload names the row it priced with (its own order_type spelling).
//   - maximum_consumable_points ≤ 0 means NO points (reason NO_MAX): validateWalletPoints rejects points above the
//     maximum whatever it is, and Rule W's over_max agrees. REF reads ≤ 0 as "no cap" (an order the server refuses).
//   - the server's ratio check: |round(points / amount, 2) − ratio| ≤ 0.5. At a ratio like 30 a few points round to an
//     amount that fails it (5 points → 0.17 → 29.41), so redeem() steps the points down until it passes (REF doesn't).
//   - the minimum: points below min_consumable_points redeem nothing (REF; the server doesn't check it).
//   - the daily cap (usage_limit_over: checkDailyRedeemLimit) and the balance (Rule W's over_balance) bound the points.
//   - a reward needs that same row: validateWalletPoints checks the reward points against it too (REF falls back to
//     get_user_loyalty_products_v4's points_required without one; the server then has no row: ecode 700). Its product
//     must be a loyalty product (checkLoyaltyProduct: ecode 701).
//
// Reading the wallet (wallet = get_customer_wallet_points' body: OO.store.wallet(hotel), the read settled once per page
// load; a checkout passes the fresh pre-flight read instead). A wallet is usable when it is an object whose status isn't
// false / 'false'; anything else is no wallet. ctx below = { wallet, store, orderType 'PICKUP'|'DELIVERY', mode
// ('online'|'app'|'qr'; default OO.mode), isMobileApp (bool; wins over mode, as the payload's), table (a table order:
// truthy), kiosk (bool), coupon (the applied coupon, any truthy value), walletPoints (the points already applied),
// signedIn (bool) }.
//   usable(wallet) → wallet | null.
//   balance(wallet) → number ≥ 0   wallet_points (walletPoints), as sent.
//   usage(wallet) → { over, used, text }   REF isWalletUsageLimitOver / getWalletUsedLimitCount / …Message: over =
//       usage_limit_over (usageLimitOver) on (true, 1, '1', 'true', 'yes'), used = used_limit_count (whole ≥ 0), text =
//       the customer copy (null unless over).
//   rows(wallet) → [{ orderType, ratio, min, max }]   the redemption rows: the first non-empty list of redemption_ratio,
//       redemptionRatios, redumption_ratio (sic, the server's), each row's order_type (orderType) trimmed,
//       ratio_points, min_consumable_points (minimum_…), maximum_consumable_points as numbers; rows without an order type
//       or with ratio ≤ 0 are left out.
//   channel(ctx) → 'dining' (a table order) | 'KIOSK' | 'ONLINE_[APP_]PICKUP' | 'ONLINE_[APP_]DELIVERY'   (REF
//       getWalletOrderType) the redemption row type of the order.
//   rule(wallet, ctx) → { orderType, ratio, min, max } | null   the channel's row (order types compared trimmed and
//       upper-cased), else an ALL row; orderType keeps the row's own spelling (it goes on the wire).
//   productLevel(store) → bool   the store redeems points on product rewards only (type 2), so cart-level points are
//       off, and only there are rewards on (REF isProductLevelLoyaltyEnabled): store.loyaltyProductLevel (the
//       normalised flag), else the first present of REF's raw keys loyalty_enable_product_level,
//       loyalty_product_level_enabled, loyaltyEnableProductLevel, loyality_enable_product_level (sic, getRestaurant's),
//       on as a flag. ⚠ normalizeStore doesn't carry it yet (model-store.jsx patch reported): until it does every store
//       reads as cart-level, so rewards are refused (they fail closed).
//
// Wallet type 1: points off the order (REF CartContext slider + normalizeType1RedeemPoints + buildType1WalletMap).
//   isReward(line) → bool   a product reward line (line.reward; P2-06): never paid with points, and its presence turns
//       the slider off (REF: a type-2 line makes the slider max 0).
//   eligible(line) → bool   an item line points can pay for: not a combo, a coupon's free line (line.free) or a reward
//       line, with basePrice × qty > 0 (REF isType1WalletEligible).
//   cartValue(lines) → round2(Σ basePrice × qty) over the eligible lines: the product prices only (sizes and tag prices
//       included; modifiers, combos and free lines never), what points can cover (REF calculateCartWalletValue).
//   slider(lines, ctx) → Slider = { enabled, reason, text, rule, balance (whole points), cartValue, cap
//       (floor(cartValue × ratio)), min, max, step: 1, warning }   (REF walletSliderMax)
//       max = min(balance, cap, the row's maximum) and min = ceil(the row's minimum), at least 1, while enabled. Off
//       (max 0), the first reason that applies: NO_WALLET (no usable wallet: signed out, not read, a failed read) ·
//       LIMIT (the daily cap is reached; text = usage().text) · PRODUCT_LEVEL (productLevel(store)) · REWARDS (a reward
//       line is in the cart) · NO_RULE (no row for the channel) · NO_MAX (the row's maximum < 1) · NO_POINTS (balance
//       < 1) · NO_ITEMS (cartValue 0) · BELOW_MIN (max < min). text = the customer copy (TEXT). warning = the
//       membership engine's POINTS_REMOVE_COUPON while a coupon is applied (using points will remove it), else null.
//   redeem(points, lines, ctx) → Redemption | null   the points the order can actually take, as the payload sends them:
//       p = min(floor(points), slider.max), then down one point at a time until the server's ratio check passes for
//       amount = round2(min(cartValue, p / ratio)) (header); null when the slider is off or p falls below its min.
//       Redemption = { points (whole), amount (dollars, 2 dp, ≤ cartValue), orderType (the row's), ratio }. The ONE
//       normaliser: the UI shows its result and the payload re-derives it to check nothing moved.
//   spread(lines, redemption) → { [line.key]: { amount, points, base, percent } }   (REF buildType1WalletMap) the
//       redemption over the eligible lines with base = basePrice × qty > 0, in cart order, by base share: amount =
//       min(base, round2(A × base / Σ base)) and points = round2(P × base / Σ base); the LAST line takes what is left
//       (amount clamped to [0, base]); percent = amount / base × 100, the line's payload `discount`. {} without an
//       amount or an eligible line.
//   applyPoints(points, lines, ctx) → { ok, redemption, refuse, drop, notice }   using points, with the stacking rules
//       (OO.membership.exclusivity with apply 'points'; that lane owns them): refuse = exclusivity's refusal (a reward
//       line in the cart: REWARDS_BLOCK_POINTS) or, when redeem() gives nothing, { code: the slider's reason, text }
//       (code BELOW_MIN, 'Use at least N points.', when the slider is on but the points are below its min); drop /
//       notice = what applying them removes (['coupon'] + COUPON_REMOVED). The caller removes what drop names.
//
// Wallet type 2: product reward lines (REF addItem's isLoyaltyAddedProduct, resolveLoyaltyCommit, the rewards page).
//   rewardRows(raw) → [{ id, points }] | null   get_user_loyalty_products_v4's affordable rewards from its body (data.
//       eligible_products, else data.products), a bare data object or a list of rows: id = itemId (id), points =
//       points_required; rows without an id are left out. null for anything else (a failed read).
//   rewardAmount(line) → round2(basePrice × qty)   what the wallet pays for a reward line: its product price (at its
//       size and tag; REF loyaltyLineCoveredAmount). Its modifiers stay the customer's.
//   rewardPoints(line, rule) → round2(rewardAmount × rule.ratio)   (REF calculateLoyaltyPointsForAmount), else (no
//       rule, a zero amount) the points the line was added for (line.reward.points).
//   rewardBudget(lines, ctx) → Budget = { enabled, reason, text, rule, balance, committed, left, max, room }   the points
//       the cart's reward lines take (committed = Σ rewardPoints at the row), left = balance − committed and room =
//       the row maximum − committed (both ≥ 0). Off, the first reason that applies: NO_WALLET · LIMIT · CART_LEVEL (the
//       store isn't product-level: REF "This branch uses cart-level wallet redemption") · KIOSK · NO_RULE · NO_MAX.
//   addReward(product, { sizeId, sel, notes }, row, lines, ctx) → { ok, line, points, refuse, drop, notice }   a reward
//       line for `product` (an OO.model Product from the menu the cart is priced with) chosen from the rewards list:
//       `row` is its rewardRows entry (its id must be the product's). The gates, in order (REF addItem + the product
//       modal's loyalty commit): rewardBudget off → its reason · NOT_REWARD (not a loyalty product, Product.isLoyalty;
//       not the row's) · UNAVAILABLE (sold out / unavailable) · NO_COST (no points: a zero price) · OVER_MAX (committed
//       + points > the row maximum: 'You can redeem at most N points per order.') · SHORT (committed + points >
//       balance: 'This reward needs N more points.'). Then the stacking rules (OO.membership.exclusivity, apply 'reward',
//       the membership lane's): drop = what the reward removes (['coupon'], ['points'] or both) and notice
//       (COUPON_REMOVED_FOR_REWARD). ok: line = OO.cart.rewardLine(product, choice, { points }); the caller removes what
//       drop names and adds the line (OO.store.cartAdd).
//   staleRewards(lines, ctx) → { stale, reason, text, keys }   reward lines the order can no longer take (REF's
//       auto-removal: signed out (ctx.signedIn false), LIMIT, CART_LEVEL, KIOSK), with the copy to show and their keys;
//       { stale: false } with none, or while a signed-in customer's wallet hasn't been read (no wallet yet).
//
// The order (the summary and the payload; how the UI passes the redemption):
//   Summary   points in use reach OO.pricing.summary as opts.walletAmount = the Redemption's amount (the summary takes
//             it off the total and the surcharge base; the membership engine reads it as "points in use" and turns
//             member savings off). Summary contributor 'wallet' (OO.pricing.contribute): at a store with
//             applyDiscountToModifiers (the validator nets each payload row's charged modifiers at the row's `discount`
//             %, with no add-back) it returns that part, Σ charged modifiers × qty × the line's spread percent, as
//             lineSaving (W5-FIX #5: pricing.jsx's neutral field for a saving the server nets from the rows, so it comes
//             off the subtotal sent; pricing.jsx header: "a contributor's saving must include them"), with its own row
//             { key: 'walletModifiers', label: 'Points discount on options', after: 'discount' }, so the rows still add
//             up. Reward lines need no UI input: the contributor returns wallet = Σ rewardAmount (the Discount row shows
//             it) and, at such a store, their charged modifiers in full (100 %) in that same row. null when there is
//             nothing (no points, no reward).
//   Payload   contributor 'wallet' (OO.payload.contribute; REF sipoCheckoutWire buildProductData /
//             calculateWalletTotals). The hook ctx's wallet fields:
//               walletPoints  the Redemption's points; walletAmount its amount (OO.payload forwards it to its summary)
//               wallet        the wallet the page priced with (default OO.store.wallet(store.onlineKey))
//               freshWallet   the pre-flight get_customer_wallet_points read at Pay (REF refreshCheckoutWalletPayload):
//                             undefined = not checked; given, it replaces `wallet` for every check; null = the read failed
//               freshRewards  the pre-flight get_user_loyalty_products_v4 read (anything rewardRows reads) when the
//                             cart has reward lines: undefined = not checked; null = the read failed
//             Nothing when no points are in use and no reward line is in the cart (every fixture and Phase-1 order:
//             the payload is unchanged). Type 1 (points):
//               line  each eligible item row in the spread: discount = walletDiscount = its percent, isWalletRedeemed
//                     true, walletType 1, walletAmount = its amount, walletUsedForProduct = its points. Combo rows keep
//                     the wallet fields off (REF).
//               root  wallet_point_used true · wallet_order_type (the row's) · wallet_applied_type 1 · wallet_amount
//                     (number, 2 dp) · wallet_points (whole) · customer_id (the wallet's). OO.payload already sends
//                     payment_type 'wallet' (gateway 'wallet', ids null, no surcharge) when the points pay it all.
//             Type 2 (reward lines; REF's type-2 row and calculateWalletTotals):
//               line  each reward row: price = its basePrice (the menu price the validator checks), count 1, discount =
//                     walletDiscount = 100, isWalletRedeemed true, walletType 2, walletAmount = rewardAmount,
//                     walletUsedForProduct = rewardPoints at the row. Its modifier rows are charged as on any line.
//               root  as type 1 with wallet_applied_type 2, wallet_amount = Σ rewardAmount, wallet_points = round2(Σ
//                     rewardPoints).
//             It REFUSES (it throws; OO.payload reports code CONTRIBUTOR with this error as `cause`: { kind: 'wallet',
//             code, text }) when
//               POINTS_UNVERIFIED  no usable wallet (signed out, freshWallet null), or the wallet has no customer_id
//               POINTS_WITH_COUPON a coupon is on the order (the two never combine; the UI drops one first)
//               POINTS_LIMIT       the wallet's daily cap is reached
//               POINTS_OFF         the store redeems on product rewards only (productLevel)
//               POINTS_CHANGED     redeem() on this wallet gives other points or another amount than walletPoints /
//                                  walletAmount (REF assertType1SelectionUnchanged), an amount comes without points,
//                                  points come with reward lines, or the order's customer_id is another customer's.
//             and, with reward lines (REF's wire guards and pre-flight):
//               REWARDS_OFF        the store isn't product-level (or a kiosk order)
//               REWARDS_UNVERIFIED freshRewards null (the rewards read failed)
//               REWARD_CHANGED     a reward line that isn't a qty-1 item at a price > 0, a channel without a usable row,
//                                  zero points, a failed ratio check, or (with freshRewards) a product no longer listed
//               REWARDS_OVER       the rewards' points exceed the balance or the row maximum
//               (POINTS_UNVERIFIED, POINTS_WITH_COUPON, POINTS_LIMIT as for points.)
//   ERRORS: { code: text } of those refusals (the pages' copy). TEXT: { reason: text } of the slider's, the budget's and
//   addReward's reasons (a few carry numbers and are built where they apply).
//
// The pages' helpers (P2-07: the checkout's wallet section, pages/checkout-wallet.jsx; the cart's and the checkout's
// summaries, pages/cart.jsx summaryOf). They read OO.store at call time (never at load): the points the customer chose
// (OO.store.walletPoints, memory only) and the settled wallet (OO.store.wallet).
//   ctxOf(store, opts) → ctx   the engine ctx above for the signed-in customer's order at `store`: { wallet:
//       OO.store.wallet(store.onlineKey) (the read the summary contributors use), store, orderType (opts', default
//       'PICKUP'), mode: OO.mode, isMobileApp: OO.mode === 'app' (what checkout.jsx's payloadCtx sends, so the engine
//       and the payload name the same row), table (opts.table, a table order: truthy), kiosk: false, coupon
//       (opts.coupon), walletPoints (OO.store.walletPoints(opts.hotel, else store.onlineKey)), signedIn (a session) }.
//       opts = { hotel, orderType, table, coupon }.
//   inUse(lines, store, opts) → Redemption | null   the points the customer chose, as this order takes them:
//       redeem(walletPoints, lines, ctxOf(store, opts)). null without points chosen, while a coupon is on the order (any
//       coupon, applicable or not: the two never combine, and the last one applied already removed the other) or
//       while the slider is off. The ONE figure the cart's and the checkout's summaries take off (summary
//       opts.walletAmount = its amount: "points in use" for the membership and coupon lanes) and the checkout payload
//       sends (ctx.walletPoints = its points, ctx.walletAmount = its amount).
//   dropPoints(hotel) → bool   forgets the points chosen at that store (OO.store.setWalletPoints(0, hotel)); true when
//       there were some. The coupon lane calls it when applying a coupon drops the points (exclusivity drop
//       ['points']), and so does the wallet section when a reward does.
// Load order (build/concat.mjs): after pricing.jsx / payload.jsx and the pricetags and membership engines, before
// store.jsx. Its contributors register at load time, after the membership engine's.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function r2(n) { return OO.format.round2(n); }
  function cents(v) { return Math.round(num(v) * 100); }
  function text(v) { return typeof v === 'number' && isFinite(v) ? String(v) : (typeof v === 'string' ? v.trim() : ''); }
  // REF isEnabledFlag / isPresentFlag (wallet.ts).
  function flagOn(v) {
    if (v === true || v === 1) return true;
    return typeof v === 'string' && ['1', 'true', 'yes'].indexOf(v.trim().toLowerCase()) >= 0;
  }
  function present(v) {
    if (v === undefined || v === null || v === '') return false;
    if (typeof v !== 'string') return true;
    var s = v.trim().toLowerCase();
    return s !== '' && s !== 'null' && s !== 'undefined';
  }
  function first(o, keys) {
    for (var i = 0; i < keys.length; i++) if (present(o[keys[i]])) return o[keys[i]];
    return undefined;
  }

  // ---- customer copy ---------------------------------------------------------------------------------------------
  var TEXT = {
    NO_WALLET: 'Sign in to use your points.',
    PRODUCT_LEVEL: 'This store takes points as rewards, not off your order.',
    REWARDS: 'Wallet points can’t be used with a product reward in your cart.',
    NO_RULE: 'Points can’t be used for this kind of order.',
    NO_MAX: 'Points can’t be used for this kind of order.',
    NO_POINTS: 'You don’t have any points to use yet.',
    NO_ITEMS: 'Points can’t be used on the items in your cart.',
    BELOW_MIN: 'You need more points to use them on this order.',
    // rewards (P2-06)
    CART_LEVEL: 'This store takes points off your order, not as rewards.',
    KIOSK: 'Rewards can’t be redeemed here.',
    NOT_REWARD: 'This item isn’t a reward.',
    UNAVAILABLE: 'This reward isn’t available right now.',
    NO_COST: 'This reward doesn’t have a valid points cost.',
    OVER_MAX: 'That’s more points than you can redeem on one order.',
    SHORT: 'You don’t have enough points for this reward.',
  };
  // staleRewards' copy, for one reward line or several.
  var STALE = {
    SIGNED_OUT: 'removed: you’re signed out.',
    LIMIT: 'removed: you’ve reached today’s limit for redeeming points.',
    CART_LEVEL: 'removed: this store takes points off your order instead.',
    KIOSK: 'removed: rewards can’t be redeemed here.',
  };
  var ERRORS = {
    POINTS_UNVERIFIED: 'We couldn’t confirm your points. Please sign in again and try again.',
    POINTS_WITH_COUPON: 'Points can’t be combined with a coupon. Please remove one of them and try again.',
    POINTS_LIMIT: 'You’ve reached today’s limit for redeeming points. Please remove your points and try again.',
    POINTS_OFF: 'This store takes points as rewards, not off your order. Please remove your points and try again.',
    POINTS_CHANGED: 'Your points or the store’s points rules changed. Please review your points and try again.',
    REWARDS_OFF: 'Rewards can’t be redeemed at this store right now. Please remove them and try again.',
    REWARDS_UNVERIFIED: 'We couldn’t confirm your rewards. Please try again.',
    REWARD_CHANGED: 'A reward in your cart has changed or is no longer available. Please remove it and choose it again.',
    REWARDS_OVER: 'Your rewards need more points than you can use on this order. Please remove one and try again.',
  };
  function walletError(code) {
    var e = new Error(ERRORS[code]);
    e.kind = 'wallet';
    e.code = code;
    e.text = ERRORS[code];
    return e;
  }

  // ---- reading the wallet -------------------------------------------------------------------------------------------
  function usable(wallet) { return isRecord(wallet) && wallet.status !== false && wallet.status !== 'false' ? wallet : null; }
  function balance(wallet) {
    if (!isRecord(wallet)) return 0;
    return Math.max(0, num(wallet.wallet_points !== undefined && wallet.wallet_points !== null ? wallet.wallet_points : wallet.walletPoints));
  }
  function usage(wallet) {
    var w = isRecord(wallet) ? wallet : {};
    var over = flagOn(first(w, ['usage_limit_over', 'usageLimitOver']));
    var raw = Math.floor(num(w.used_limit_count !== undefined && w.used_limit_count !== null ? w.used_limit_count : w.usedLimitCount));
    var used = raw > 0 ? raw : 0;
    var t = null;
    if (over) {
      t = 'You’ve reached today’s limit for redeeming points.';
      if (used > 0) t += ' Points used ' + used + (used === 1 ? ' time' : ' times') + ' today.';
    }
    return { over: over, used: used, text: t };
  }
  function rows(wallet) {
    if (!isRecord(wallet)) return [];
    var lists = [wallet.redemption_ratio, wallet.redemptionRatios, wallet.redumption_ratio];
    var list = [];
    for (var i = 0; i < lists.length && !list.length; i++) list = arr(lists[i]);
    var out = [];
    list.forEach(function (r) {
      if (!isRecord(r)) return;
      var type = text(r.order_type !== undefined && r.order_type !== null ? r.order_type : r.orderType);
      var ratio = num(r.ratio_points !== undefined && r.ratio_points !== null ? r.ratio_points : r.ratioPoints);
      if (!type || !(ratio > 0)) return;
      var min = first(r, ['min_consumable_points', 'minimum_consumable_points', 'minConsumablePoints']);
      var max = first(r, ['maximum_consumable_points', 'maximumConsumablePoints']);
      out.push({ orderType: type, ratio: ratio, min: num(min), max: num(max) });
    });
    return out;
  }
  function modeOf(ctx) { return ctx.mode !== undefined && ctx.mode !== null ? ctx.mode : OO.mode; }
  function appOf(ctx) {
    if (ctx.isMobileApp !== undefined && ctx.isMobileApp !== null) return !!ctx.isMobileApp;
    return modeOf(ctx) === 'app';
  }
  function channel(ctx) {
    ctx = ctx || {};
    if (ctx.table || modeOf(ctx) === 'qr') return 'dining';
    if (ctx.kiosk) return 'KIOSK';
    var delivery = /DELIVERY/.test(String(ctx.orderType || 'PICKUP').toUpperCase());
    return 'ONLINE_' + (appOf(ctx) ? 'APP_' : '') + (delivery ? 'DELIVERY' : 'PICKUP');
  }
  function rule(wallet, ctx) {
    var list = rows(wallet);
    var want = channel(ctx || {}).toUpperCase();
    var hit = list.filter(function (r) { return r.orderType.toUpperCase() === want; })[0]
      || list.filter(function (r) { return r.orderType.toUpperCase() === 'ALL'; })[0];
    return hit ? Object.assign({}, hit) : null;
  }
  var PRODUCT_LEVEL_KEYS = ['loyaltyProductLevel', 'loyalty_enable_product_level', 'loyalty_product_level_enabled', 'loyaltyEnableProductLevel', 'loyality_enable_product_level'];
  function productLevel(store) { return isRecord(store) && flagOn(first(store, PRODUCT_LEVEL_KEYS)); }

  // ---- wallet type 1: the lines points pay for ------------------------------------------------------------------------
  function isReward(line) { return isRecord(line) && line.kind === 'item' && !!line.reward; }
  function qtyOf(line) { return Math.max(1, Math.floor(num(line.qty)) || 1); }
  function baseOf(line) { return Math.max(0, num(line.basePrice)) * qtyOf(line); }
  function eligible(line) { return isRecord(line) && line.kind === 'item' && !line.free && !line.reward && baseOf(line) > 0; }
  function eligibleLines(lines) { return arr(lines).filter(eligible); }
  function cartValue(lines) { return r2(eligibleLines(lines).reduce(function (t, l) { return t + baseOf(l); }, 0)); }

  // The stacking rules are the membership lane's (OO.membership.exclusivity): called, never re-decided here.
  function stacking(ctx, input) {
    var M = OO.membership;
    if (!M || typeof M.exclusivity !== 'function') return { refuse: null, drop: [], notice: null, warning: null };
    var wallet = usable(ctx.wallet);
    return M.exclusivity(Object.assign({
      member: wallet && typeof M.of === 'function' ? M.of(wallet) : null,
      signedIn: ctx.signedIn !== undefined ? !!ctx.signedIn : !!wallet,
      kiosk: !!ctx.kiosk,
    }, input));
  }
  function rewardCount(lines) { return arr(lines).filter(isReward).length; }

  // A whole number of points from a figure a float may carry (12.000000000000002 → 12; 28.999999999999996 → 29).
  function wholeDown(v) { return Math.floor(num(v) + 1e-9); }

  function slider(lines, ctx) {
    ctx = ctx || {};
    var wallet = usable(ctx.wallet);
    var out = { enabled: false, reason: null, text: null, rule: null, balance: 0, cartValue: 0, cap: 0, min: 0, max: 0, step: 1, warning: null };
    function off(reason, t) { out.reason = reason; out.text = t || TEXT[reason]; out.max = 0; return out; }
    if (ctx.store && ctx.store.orderingQr) return off('NO_WALLET', 'This QR uses its preset offer.');
    if (!wallet) return off('NO_WALLET');
    out.balance = wholeDown(balance(wallet));
    out.cartValue = cartValue(lines);
    out.rule = rule(wallet, ctx);
    var u = usage(wallet);
    if (u.over) return off('LIMIT', u.text);
    if (productLevel(ctx.store)) return off('PRODUCT_LEVEL');
    if (rewardCount(lines)) return off('REWARDS');
    var r = out.rule;
    if (!r) return off('NO_RULE');
    if (wholeDown(r.max) < 1) return off('NO_MAX');
    out.cap = wholeDown(out.cartValue * r.ratio);
    out.min = Math.max(1, Math.ceil(r.min - 1e-9));
    var max = Math.min(out.balance, out.cap, wholeDown(r.max));
    if (out.balance < 1) return off('NO_POINTS');
    if (!(out.cartValue > 0)) return off('NO_ITEMS');
    if (max < out.min) return off('BELOW_MIN', 'You need at least ' + out.min + ' points to use them on this order.');
    out.max = max;
    out.enabled = true;
    out.warning = stacking(ctx, { coupon: ctx.coupon, points: ctx.walletPoints, rewards: 0, apply: null }).warning || null;
    return out;
  }

  // validateWalletPoints' ratio check: |round(points / amount, 2) − ratio| ≤ 0.5 (a hair inside it, for float noise).
  function ratioOk(points, amount, ratio) {
    return points > 0 && amount > 0 && Math.abs(r2(points / amount) - ratio) <= 0.5 - 1e-6;
  }
  var STEP_LIMIT = 1000; // steps down redeem() may take before it gives up (a sane row passes within a few)
  function redeem(points, lines, ctx) {
    var s = slider(lines, ctx);
    if (!s.enabled) return null;
    var p = Math.min(wholeDown(points), s.max);
    for (var n = 0; p >= s.min && n < STEP_LIMIT; p -= 1, n += 1) {
      var amount = r2(Math.min(s.cartValue, p / s.rule.ratio));
      if (ratioOk(p, amount, s.rule.ratio)) return { points: p, amount: amount, orderType: s.rule.orderType, ratio: s.rule.ratio };
    }
    return null;
  }

  function spread(lines, redemption) {
    var out = {};
    var A = num(redemption && redemption.amount);
    var P = Math.max(0, num(redemption && redemption.points));
    if (!(A > 0)) return out;
    var items = eligibleLines(lines).map(function (l) { return { line: l, base: baseOf(l) }; });
    var total = items.reduce(function (t, x) { return t + x.base; }, 0);
    if (!(total > 0)) return out;
    var leftA = A, leftP = P;
    items.forEach(function (x, i) {
      var last = i === items.length - 1;
      var amount = Math.max(0, Math.min(x.base, last ? r2(leftA) : r2(A * x.base / total)));
      var pts = Math.max(0, last ? r2(leftP) : r2(P * x.base / total));
      leftA = r2(leftA - amount);
      leftP = r2(leftP - pts);
      out[x.line.key] = { amount: amount, points: pts, base: x.base, percent: amount / x.base * 100 };
    });
    return out;
  }

  function applyPoints(points, lines, ctx) {
    ctx = ctx || {};
    var x = stacking(ctx, { coupon: ctx.coupon, points: ctx.walletPoints, rewards: rewardCount(lines), apply: 'points' });
    if (x.refuse) return { ok: false, redemption: null, refuse: x.refuse, drop: [], notice: null };
    var r = redeem(points, lines, ctx);
    if (!r) {
      var s = slider(lines, ctx);
      var why = s.enabled ? { code: 'BELOW_MIN', text: 'Use at least ' + s.min + ' points.' } : { code: s.reason, text: s.text };
      return { ok: false, redemption: null, refuse: why, drop: [], notice: null };
    }
    return { ok: true, redemption: r, refuse: null, drop: arr(x.drop).slice(), notice: x.notice || null };
  }

  // ---- wallet type 2: reward lines (P2-06; see the header) -----------------------------------------------------------
  function rewardRows(raw) {
    var list = null;
    if (Array.isArray(raw)) list = raw;
    else if (isRecord(raw)) {
      var d = isRecord(raw.data) ? raw.data : raw;
      if (Array.isArray(d.eligible_products)) list = d.eligible_products;
      else if (Array.isArray(d.products)) list = d.products;
    }
    if (!list) return null;
    var out = [];
    list.forEach(function (r) {
      if (!isRecord(r)) return;
      var id = num(r.itemId !== undefined && r.itemId !== null ? r.itemId : r.id);
      if (!(id > 0)) return;
      out.push({ id: id, points: Math.max(0, num(r.points_required !== undefined && r.points_required !== null ? r.points_required : (r.pointsRequired !== undefined ? r.pointsRequired : r.points))) });
    });
    return out;
  }
  function rewardAmount(line) { return r2(Math.max(0, num(line && line.basePrice)) * qtyOf(line || {})); }
  function rewardPoints(line, r) {
    var amount = rewardAmount(line);
    if (r && r.ratio > 0 && amount > 0) return r2(amount * r.ratio);
    return Math.max(0, num(line && line.reward && line.reward.points));
  }
  function rewardLines(lines) { return arr(lines).filter(isReward); }

  function rewardBudget(lines, ctx) {
    ctx = ctx || {};
    var wallet = usable(ctx.wallet);
    var out = { enabled: false, reason: null, text: null, rule: null, balance: 0, committed: 0, left: 0, max: 0, room: 0 };
    function off(reason, t) { out.reason = reason; out.text = t || TEXT[reason]; return out; }
    if (ctx.store && ctx.store.orderingQr) return off('NO_WALLET', 'This QR uses its preset offer.');
    if (!wallet) return off('NO_WALLET');
    out.balance = balance(wallet);
    var u = usage(wallet);
    if (u.over) return off('LIMIT', u.text);
    if (!productLevel(ctx.store)) return off('CART_LEVEL');
    if (ctx.kiosk) return off('KIOSK');
    var r = out.rule = rule(wallet, ctx);
    if (!r) return off('NO_RULE');
    if (wholeDown(r.max) < 1) return off('NO_MAX');
    out.max = r.max;
    out.committed = r2(rewardLines(lines).reduce(function (t, l) { return t + rewardPoints(l, r); }, 0));
    out.left = Math.max(0, r2(out.balance - out.committed));
    out.room = Math.max(0, r2(out.max - out.committed));
    out.enabled = true;
    return out;
  }

  function addReward(product, choice, row, lines, ctx) {
    ctx = ctx || {};
    var out = { ok: false, line: null, points: 0, refuse: null, drop: [], notice: null };
    function no(code, t) { out.refuse = { code: code, text: t || TEXT[code] }; return out; }
    var b = rewardBudget(lines, ctx);
    if (!b.enabled) return no(b.reason, b.text);
    if (!isRecord(product) || product.kind === 'combo' || product.isLoyalty !== true) return no('NOT_REWARD');
    var rowId = isRecord(row) ? num(row.id !== undefined && row.id !== null ? row.id : row.itemId) : 0;
    if (!(rowId > 0) || rowId !== num(product.id)) return no('NOT_REWARD');
    if (product.soldOut || product.available === false) return no('UNAVAILABLE');
    var line = OO.cart.rewardLine(product, choice || {}, { points: 0 });
    var points = rewardPoints(line, b.rule);
    if (!(points > 0)) return no('NO_COST');
    if (b.committed + points > b.max + 1e-9) return no('OVER_MAX', 'You can redeem at most ' + r2(b.max) + ' points per order.');
    if (b.committed + points > b.balance + 1e-9) return no('SHORT', 'This reward needs ' + r2(b.committed + points - b.balance) + ' more points.');
    var x = stacking(ctx, { coupon: ctx.coupon, points: ctx.walletPoints, rewards: rewardCount(lines), apply: 'reward' });
    if (x.refuse) return no(x.refuse.code, x.refuse.text);
    line.reward = { points: points };
    out.ok = true;
    out.line = line;
    out.points = points;
    out.drop = arr(x.drop).slice();
    out.notice = x.notice || null;
    return out;
  }

  function staleRewards(lines, ctx) {
    ctx = ctx || {};
    var keys = rewardLines(lines).map(function (l) { return l.key; });
    if (!keys.length) return { stale: false, reason: null, text: null, keys: [] };
    var wallet = usable(ctx.wallet);
    var reason = null;
    if (wallet && usage(wallet).over) reason = 'LIMIT';
    else if (ctx.signedIn === false) reason = 'SIGNED_OUT';
    else if (ctx.kiosk) reason = 'KIOSK';
    else if (isRecord(ctx.store) && !productLevel(ctx.store)) reason = 'CART_LEVEL';
    if (!reason) return { stale: false, reason: null, text: null, keys: [] };
    return { stale: true, reason: reason, text: (keys.length === 1 ? 'Your reward was ' : 'Your rewards were ') + STALE[reason], keys: keys };
  }

  // ---- the order: summary + payload (see the header) -----------------------------------------------------------------
  // One unit's charged modifiers: Σ price × (count − free) (cart.jsx's allowance-aware rows).
  function chargedMods(line) {
    return arr(line && line.modifiers).reduce(function (t, m) { return t + num(m && m.price) * Math.max(0, num(m && m.count) - num(m && m.free)); }, 0);
  }

  function summaryContribution(c) {
    var store = isRecord(c.store) ? c.store : {};
    var opts = isRecord(c.opts) ? c.opts : {};
    var lines = arr(c.lines);
    var rewards = rewardLines(lines);
    var wallet = r2(rewards.reduce(function (t, l) { return t + rewardAmount(l); }, 0));
    var mods = 0;
    if (store.applyDiscountToModifiers === true) {
      if (num(opts.walletAmount) > 0) {
        var s = spread(lines, { amount: num(opts.walletAmount), points: 0 });
        lines.forEach(function (l) {
          var x = isRecord(l) ? s[l.key] : null;
          if (x) mods += chargedMods(l) * qtyOf(l) * Math.min(100, Math.max(0, x.percent)) / 100;
        });
      }
      rewards.forEach(function (l) { mods += chargedMods(l) * qtyOf(l); }); // a reward row's 100 % nets them all
    }
    mods = r2(mods);
    if (!(mods > 0) && !(wallet > 0)) return null;
    var out = {};
    if (wallet > 0) out.wallet = wallet;
    if (mods > 0) {
      out.lineSaving = mods; // W5-FIX #5: the neutral field (it travelled as membershipItem)
      out.rows = [{ key: 'walletModifiers', label: 'Points discount on options', value: mods, text: OO.format.minusMoney(mods, store.currency || '$'), after: 'discount' }];
    }
    return out;
  }

  function storeWallet(store) {
    try {
      var key = store && store.onlineKey !== undefined && store.onlineKey !== null ? String(store.onlineKey) : '';
      return key && OO.store && typeof OO.store.wallet === 'function' ? OO.store.wallet(key) : null;
    } catch (e) { return null; }
  }
  // The wallet the order is checked against: the fresh pre-flight read when given, else the one priced with.
  function walletFor(ctx) {
    var w = ctx.freshWallet !== undefined ? ctx.freshWallet : (ctx.wallet !== undefined ? ctx.wallet : storeWallet(ctx.store));
    var ok = usable(w);
    if (!ok) throw walletError('POINTS_UNVERIFIED');
    return ok;
  }

  var NONE = Object.freeze({ used: false });
  // Type 1: the points redeem() gives on this wallet must be the ones the summary priced.
  function pointsWire(ctx, lines, wallet, pts, amt) {
    if (productLevel(ctx.store)) throw walletError('POINTS_OFF');
    if (!(pts > 0)) throw walletError('POINTS_CHANGED');
    var r = redeem(pts, lines, Object.assign({}, ctx, { wallet: wallet, coupon: null }));
    if (!r || r.points !== wholeDown(pts) || cents(r.amount) !== cents(amt)) throw walletError('POINTS_CHANGED');
    return { used: true, type: 1, orderType: r.orderType, amount: r.amount, points: r.points, spread: spread(lines, r), rewards: {} };
  }
  // Type 2: each reward line at the channel's row (REF's wire guards, calculateWalletTotals and the pre-flight).
  function rewardWire(ctx, rewards, wallet, pts, amt) {
    if (pts > 0 || amt > 0) throw walletError('POINTS_CHANGED');
    if (!productLevel(ctx.store) || ctx.kiosk) throw walletError('REWARDS_OFF');
    var r = rule(wallet, ctx);
    if (!r || wholeDown(r.max) < 1) throw walletError('REWARD_CHANGED');
    var listed = null;
    if (ctx.freshRewards !== undefined) {
      listed = rewardRows(ctx.freshRewards);
      if (!listed) throw walletError('REWARDS_UNVERIFIED');
    }
    var per = {};
    var amount = 0, points = 0;
    rewards.forEach(function (l) {
      if (num(l.qty) !== 1 || !(num(l.basePrice) > 0)) throw walletError('REWARD_CHANGED');
      if (listed && !listed.some(function (x) { return x.id === num(l.itemId) && x.points > 0; })) throw walletError('REWARD_CHANGED');
      var a = rewardAmount(l);
      var p = rewardPoints(l, r);
      if (!(p > 0)) throw walletError('REWARD_CHANGED');
      per[l.key] = { amount: a, points: p };
      amount += a;
      points += p;
    });
    amount = r2(amount);
    points = r2(points);
    if (points > balance(wallet) + 1e-9 || points > r.max + 1e-9) throw walletError('REWARDS_OVER');
    if (!ratioOk(points, amount, r.ratio)) throw walletError('REWARD_CHANGED');
    return { used: true, type: 2, orderType: r.orderType, amount: amount, points: points, spread: {}, rewards: per };
  }
  function buildWire(ctx) {
    if (ctx.store && ctx.store.orderingQr) return NONE;
    var lines = arr(ctx.lines);
    var pts = num(ctx.walletPoints);
    var amt = num(ctx.walletAmount);
    var rewards = rewardLines(lines);
    if (!(pts > 0) && !(amt > 0) && !rewards.length) return NONE;
    var wallet = walletFor(ctx);
    if (ctx.coupon) throw walletError('POINTS_WITH_COUPON');
    if (usage(wallet).over) throw walletError('POINTS_LIMIT');
    var w = rewards.length ? rewardWire(ctx, rewards, wallet, pts, amt) : pointsWire(ctx, lines, wallet, pts, amt);
    var customerId = text(wallet.customer_id);
    if (!customerId) throw walletError('POINTS_UNVERIFIED');
    w.customerId = customerId;
    return w;
  }
  // Built once per checkoutPayload call (the hook ctx is a new object per call), on the first row's hook.
  var lastWire = { ctx: null, w: null };
  function wireOf(ctx) {
    if (lastWire.ctx === ctx) return lastWire.w;
    var w = buildWire(ctx);
    lastWire = { ctx: ctx, w: w };
    return w;
  }
  function wireLine(row, line, ctx) {
    var w = wireOf(ctx);
    if (!w.used || !isRecord(row) || row.is_combo || !isRecord(line)) return null;
    var rw = w.rewards[line.key];
    if (rw) return { discount: 100, walletDiscount: 100, isWalletRedeemed: true, walletType: 2, walletAmount: rw.amount, walletUsedForProduct: rw.points };
    var x = w.spread[line.key];
    if (!x) return null;
    return { discount: x.percent, walletDiscount: x.percent, isWalletRedeemed: true, walletType: 1, walletAmount: x.amount, walletUsedForProduct: x.points };
  }
  function wireRoot(body, ctx) {
    var w = wireOf(ctx);
    if (!w.used) return null;
    var sent = text(body.customer_id);
    if (sent && sent !== w.customerId) throw walletError('POINTS_CHANGED');
    return {
      wallet_point_used: true, wallet_order_type: w.orderType, wallet_applied_type: w.type,
      wallet_amount: Number(r2(w.amount).toFixed(2)), wallet_points: w.points, customer_id: w.customerId,
    };
  }

  // ---- the pages' helpers (P2-07; see the header) ---------------------------------------------------------------------
  function storeKeyOf(store) { return isRecord(store) && store.onlineKey !== undefined && store.onlineKey !== null ? String(store.onlineKey) : ''; }
  function chosenPoints(hotel) {
    try { return OO.store && typeof OO.store.walletPoints === 'function' ? num(OO.store.walletPoints(hotel)) : 0; } catch (e) { return 0; }
  }
  function signedInNow() {
    try {
      var s = OO.store && typeof OO.store.session === 'function' ? OO.store.session() : null;
      return !!(s && s.customer && s.customer.uid !== undefined && s.customer.uid !== null && String(s.customer.uid));
    } catch (e) { return false; }
  }
  function ctxOf(store, opts) {
    opts = opts || {};
    var hotel = opts.hotel !== undefined && opts.hotel !== null && opts.hotel !== '' ? String(opts.hotel) : storeKeyOf(store);
    return {
      wallet: storeWallet(store), store: store || null, orderType: opts.orderType || 'PICKUP', mode: OO.mode,
      isMobileApp: OO.mode === 'app', table: opts.table || null, kiosk: false, coupon: opts.coupon || null,
      walletPoints: chosenPoints(hotel), signedIn: signedInNow(),
    };
  }
  function inUse(lines, store, opts) {
    var ctx = ctxOf(store, opts);
    if (!(ctx.walletPoints > 0) || ctx.coupon) return null;
    return redeem(ctx.walletPoints, lines, ctx);
  }
  function dropPoints(hotel) {
    try {
      if (!OO.store || typeof OO.store.walletPoints !== 'function' || !(num(OO.store.walletPoints(hotel)) > 0)) return false;
      OO.store.setWalletPoints(0, hotel);
      return true;
    } catch (e) { return false; }
  }

  if (OO.pricing && typeof OO.pricing.contribute === 'function') OO.pricing.contribute('wallet', summaryContribution);
  if (OO.payload && typeof OO.payload.contribute === 'function') OO.payload.contribute('wallet', { line: wireLine, root: wireRoot });

  OO.wallet = OO.lib.wallet = {
    usable: usable,
    balance: balance,
    usage: usage,
    rows: rows,
    channel: channel,
    rule: rule,
    productLevel: productLevel,
    isReward: isReward,
    eligible: eligible,
    cartValue: cartValue,
    slider: slider,
    redeem: redeem,
    spread: spread,
    applyPoints: applyPoints,
    rewardRows: rewardRows,
    rewardAmount: rewardAmount,
    rewardPoints: rewardPoints,
    rewardBudget: rewardBudget,
    addReward: addReward,
    staleRewards: staleRewards,
    ctxOf: ctxOf,
    inUse: inUse,
    dropPoints: dropPoints,
    TEXT: TEXT,
    ERRORS: ERRORS,
  };
})();


// ===================== src/lib/coupons.jsx =====================
// src/lib/coupons.jsx: OO.coupons (alias OO.lib.coupons): the coupon engine (Batch 2 P2-09 … P2-11). P2-09: coupon v4
// matching (size scopes, the CART amount, coupon_enabled, the D-05 re-check); OO.pricing's coupon section
// (couponEligibility, couponDiscount, _.eligibleLines, _.itemDiscountPercents, _.couponWire) delegates to it, so
// pricing, the summary and the payload share this ONE copy. P2-10: the free-item builders (BOGO, BUY_N, ITEM 100 %
// and the giveaway), the customer's picks and their rebuild in recheck (D-05). P2-11: the payload sends their rows
// (payload.jsx, from OO.pricing._.couponWire) with the item-level root claim (D-06 a), so the flows are online. P2-12 /
// P2-13: the pickers (pages/coupon-pickers.jsx, OO.pages.CouponPicker: the BOGO source, the item, the BUY_N rewards and
// the giveaway) make the choices. Still to come: the offers UI (pages/checkout-offers.jsx, P2-14).
// Pure: no JSX, no document or OO.http at load time (the tests and the parity generator run it raw in node:vm). It
// reads OO.format, OO.model, OO.cart, OO.pricing and OO.mode at CALL time only and registers nothing at load.
// Load order (build/concat.mjs): after pricing.jsx / payload.jsx and before store.jsx. A contributor registered here
// at load time runs in that order (the summary rows, then the payload hooks).
//
// Coupon = OO.model.normalizeCoupon(s)'s shape (model-commerce.jsx); a hand-built one needs { id, type, percent,
// maxDiscount, minOrder, maxOrder, appliesTo, available, itemIds } (+ maxFreeCount, minItemCount, resultScope for the
// free-item types). Money and rule fields are read from the normalised coupon, never re-read from coupon.raw (so a test
// or a picker can set them); only an applied coupon persisted before P2-09 (no `scope`) has its scope derived from raw.
// A coupon may carry the customer's picks (below) as coupon.picks: OO.store persists the applied coupon as it is.
//
// OO.coupons:
//   entries(coupon) → [{ id, sizes: [sizeId…] }]   the coupon's item scope, as the checkout guard and the server's
//       CouponItemMatcher read it: coupon.scope when it is a list (normalizeCoupon: rules.APPLICABLE_ITEMS when it has
//       entries, else the flat items / parseditems with sizes []), else OO.model.normalizeCoupon(coupon.raw).scope,
//       else coupon.itemIds with sizes []. `sizes` [] = every size of that product.
//   lineMatches(line, coupon) → bool   an ITEM line (kind 'item'; a combo line never matches) whose itemId is an
//       entry's id and, when that entry lists sizes, whose sizeId is one of them (a line without a size never matches
//       a sized entry). The guard's guardLineMatches reads the row's size[0].id, the line's sizeId on the wire.
//   eligibleLines(lines, coupon) → the matching item lines (an ITEM_DISCOUNT's lines).
//   isAmount(coupon) → bool   a CART_DISCOUNT read as an AMOUNT, not a %: DISCOUNT (coupon.percent) > 100 (REF
//       getDiscountWithContext), or coupon.amount (normalizeCoupon: a FIXED / FIXED_AMOUNT / AMOUNT DISCOUNT_TYPE). The
//       checkout guard reads both the same way (amount_mode: Rule P allows min(DISCOUNT, its base), then the cap).
//   notOnlineYet(coupon) → bool   what the payload can't carry: a type outside CART_DISCOUNT / ITEM_DISCOUNT /
//       FREE_DELIVERY / BUY_ONE_GET_ONE_FREE / BUY_N_GET_M_FREE (an unknown type) and an ITEM_DISCOUNT with a FIXED
//       DISCOUNT_TYPE (the guard caps its lines at DISCOUNT dollars, which a % line can exceed). A size-scoped
//       ITEM_DISCOUNT and a CART_DISCOUNT over 100 % are honoured since P2-09, the free-item flows (freeFlow below)
//       since P2-11 (each judged by its builder: a choice its picker can't make yet still refuses it).
//   enabled(store) → bool   the store's coupon switch: false only when store.couponsEnabled === false (getRestaurant
//       coupon_enabled › coupon_enable, default on; OO.model.normalizeStore). Off, coupons are hidden (list) and
//       refused (eligibility, so the summary and the payload carry none).
//   appliesTo(coupon, orderType) → bool   coupon.appliesTo is 'ALL' (or empty) or the order type ('PICKUP' |
//       'DELIVERY'; normalizeCoupon maps REF's ONLINE_X / ONLINE_APP_X and APP_X forms to X).
//   eligibility(coupon, ctx) → { ok, reason, code }   ctx = { subtotal, orderType (default 'PICKUP'), lines, currency,
//       paymentMethod, store } (+ the free-line options of freeLines: picks, auto, mode, menu, at). The first refusal
//       that applies, in this order (reason; code):
//         no coupon                          null; null
//         ctx.store with coupons off         'Coupons aren't available at this store'; 'DISABLED'
//         notOnlineYet                       'Not available online yet'; 'NOT_ONLINE'
//         available === false                'This coupon is not available right now'; 'UNAVAILABLE'
//         minOrder > 0 and subtotal below    'Minimum order $X required'; 'MIN_ORDER'
//         maxOrder > 0 and subtotal above    'Maximum order $X exceeded'; 'MAX_ORDER'
//         not for this order type            'Only valid for delivery|pickup orders'; 'ORDER_TYPE'
//         FREE_DELIVERY, not DELIVERY        'Only valid for delivery orders'; 'ORDER_TYPE'
//         FREE_DELIVERY, not ONLINE_PAYMENT  'Free delivery applies when you pay online.'; 'PAYMENT' (the owner,
//                                            2026-10-02: the POS waives no fee on a counter / cash order)
//         ITEM_DISCOUNT / BOGO / BUY_N with no entries   'This coupon has no eligible items'; 'NO_SCOPE'
//         ITEM_DISCOUNT (< 100 %), ctx.lines given and none matches   'Add an eligible item to use this coupon';
//                                            'NO_ITEMS'
//         a free-item flow, ctx.lines given: freeLines' refusal (NO_ITEMS, MIN_ITEMS, NOT_ONLINE or PICK; below)
//       else { ok: true, reason: null, code: null }. Without ctx.store the switch is not checked (OO.pricing's
//       callers that pass none: the cart page's coupon list); the summary passes its store. Without ctx.lines nothing
//       about the cart is (the minimum and maximum read ctx.subtotal).
//   discount(lines, coupon, ctx) → { amount, deliveryDiscount, ok, reason, code, pending }   ctx = { orderType,
//       deliveryFee, currency, paymentMethod, store, subtotal (default Σ OO.pricing.lineTotal(line, store), rounded) }.
//       The subtotal is the cart's own lines (REF calculateCouponSubtotal: free lines never count towards a minimum).
//       Ineligible: amounts 0 with the eligibility's reason and code; pending = (code 'PICK'). Else:
//         CART_DISCOUNT   % : subtotal × DISCOUNT / 100; amount (isAmount): DISCOUNT dollars. Then capped at
//                         MAX_DISCOUNT_VALUE when > 0, never above the subtotal and never below 0 (REF: ≤ cap,
//                         ≤ subtotal). It travels as the root `discount` % = amount × 100 / total (payload.jsx), which
//                         the server and the POS turn back into the amount.
//         ITEM_DISCOUNT   (< 100 %) Σ eligible lines' basePrice × qty × DISCOUNT / 100, capped: the product part only
//                         (the validator's item_wise_discount_by_coupon add-back); modifiers excluded, as in REF.
//         FREE_DELIVERY   deliveryDiscount = min(fee, cap) (cap 0 = the whole fee); DISCOUNT ignored; amount 0.
//         free-item flows Σ over wire(lines, coupon).lines of basePrice × qty × the line's % / 100: the product part
//                         of the free units (BOGO / BUY_N: 100 %, never capped; ITEM ≥ 100 % and the giveaway: the
//                         capped % of ONE unit). Their modifiers stay payable (REF), except where the validator nets
//                         them (applyDiscountToModifiers; OO.pricing's summary).
//       Every amount is OO.format.round2'ed.
//   itemPercents(lines, coupon) → { [line.key]: % }   an applicable ITEM_DISCOUNT's (< 100 %, notOnlineYet false)
//       per-line %: DISCOUNT, scaled down by the cap's share when the raw total exceeds it (REF calculateItemDiscount):
//       each eligible line carries raw_i / Σ raw × cap over its basePrice × qty. {} for any other coupon.
//   list(raw, store, opts) → Coupon[]   the store's coupons to SHOW: [] when the switch is off (enabled), else
//       OO.model.normalizeCoupons(raw, { now: opts.at ?? opts.now, timeZone: store.timezone, domain }) (expired rows
//       dropped). opts = { at, now, orderType, mode, table, domain }; domain = opts.domain, else
//       OO.model.domain(orderType, mode, { table }) (the availability domain of this order: ONLINE_[APP_]PICKUP | …).
//   live(coupon, store, opts) → Coupon | null   the applied coupon re-read from its raw row at opts.at (the selected
//       slot's instant; else opts.now; else the device clock, as the normaliser defaults) in that domain, so its
//       availability follows the time the order is for (validate_coupon_v4 is sent that instant too). The re-read
//       coupon keeps coupon.picks (P2-10). null when the row has expired (normalizeCoupons drops it); the coupon itself
//       when it has no raw (hand-built) or no store.
//   recheck(coupon, lines, ctx) → { keep, ok, coupon, reason, code, message, lines, pending }   D-05 (the owner,
//       2026-10-04: a coupon after a cart edit is RE-CHECKED AND KEPT): the cart and checkout call it on every change of
//       the cart, the order type, the time or the payment method. ctx = { store, orderType, paymentMethod, currency, at,
//       now, mode, table, domain, hold } (+ freeLines' picks, auto, menu). With ctx.store the coupon is first re-read
//       (live; an expired one is removed: 'EXPIRED', 'This coupon has expired'), then judged by eligibility over the
//       cart subtotal (Σ OO.pricing.lineTotal) and, for a free-item flow, rebuilt from its picks (freeLines).
//         eligible                → { keep: true, ok: true, coupon, reason/code/message null, lines, pending: false }:
//                                   `coupon` is the re-read one, a free-item coupon with its picks as freeLines used
//                                   them (a pick that no longer qualifies dropped; a single candidate auto-picked):
//                                   the caller stores it (OO.store.setCoupon), so a second eligible line added later
//                                   doesn't turn the choice back on. `lines` = freeLines' free lines ([] otherwise).
//         'PICK' (a choice is needed and its picker is online)
//                                 → { keep: true, ok: false, coupon (the valid picks kept), reason, code: 'PICK',
//                                   message: null, lines: [], pending: true }: kept, unapplied, until the customer
//                                   chooses (whatever ctx.hold says).
//         a refusal in ctx.hold   → { keep: true, ok: false, coupon, reason, code, message: null, lines: [],
//                                   pending: false }: a checkout choice the customer can still change keeps the
//                                   coupon, unapplied (the summary gives nothing and shows the reason). hold defaults
//                                   to ['PAYMENT'] (FREE_DELIVERY needs online payment).
//         any other refusal       → { keep: false, ok: false, coupon: null, reason, code, message, lines: [],
//                                   pending: false }: the caller removes it (OO.store.setCoupon(null)) and shows
//                                   `message` as a short toast, e.g. 'Coupon OFF10 removed. Minimum order $30.00
//                                   required.' ('… No eligible items are left in your cart.' for NO_ITEMS; 'Coupon
//                                   removed. …' without a code or name).
//       No coupon → { keep: false, ok: false, coupon: null, reason: null, code: null, message: null, lines: [],
//       pending: false }.
//
// Free-item coupons (P2-10; REF CartContext buildCouponFreeItems / getDiscountWithContext, sipoCheckoutWire
// calculateItemDiscount; B §1.5 / §3.8). Free lines are DERIVED: never stored in the cart, rebuilt from the applied
// coupon, its picks and the cart lines wherever the summary, the payload or recheck need them, so they follow every
// cart edit (D-05). A free line is the cart's item-line shape (OO.cart) with a UNIQUE key ('free~…': no cart key starts
// with it; itemPercents and wire's percents key by line.key), qty 1 and free = { couponId, pct, source }.
//   freeFlow(coupon) → 'bogo' (BUY_ONE_GET_ONE_FREE) | 'buyN' (BUY_N_GET_M_FREE) | 'item' (ITEM_DISCOUNT ≥ 100 %, not
//       FIXED: REF's 100 % item coupon; the guard treats any % ≥ 100 as one free unit) | null.
//   The candidates are the cart's purchasable ITEM lines (kind 'item'; no combo, no loyalty reward line `reward`, no free
//   line) that lineMatches the coupon.
//   'bogo'   REF: the free units are clones of paid units. The source lines: the picked one (picks.source); else the
//            only candidate (auto-picked, as REF web does); else, in app mode, every candidate (REF app / kiosk); else a
//            choice (picker 'source'). Their units are sorted by unit basePrice, cheapest first (stable), and the first
//            min(units, max(1, coupon.maxFreeCount)) become free lines: a clone of the unit's line (its size, modifiers,
//            notes and price) at 100 %. The clone's modifiers stay payable (REF: "Extras Remain Payable"). Never capped.
//            No candidate: 'Add an eligible item to use this coupon' (NO_ITEMS).
//   'buyN'   REF: max(1, coupon.minItemCount) paid candidate units unlock max(1, coupon.maxFreeCount) rewards, ONCE
//            whatever the multiple. Fewer units: 'Add N qualifying items to use this coupon' (MIN_ITEMS). The rewards
//            are the customer's (picks.rewards; picker 'rewards'): each an item line in the reward scope
//            (coupon.resultScope = RESULTS_ITEMS; empty → the item scope, as the guard falls back), at 100 %, never
//            capped; fewer valid rewards than the count is a choice. REF app's auto-build (the first result product, no
//            modifiers) is not ported.
//   'item'   REF: ONE free unit. A giveaway pick (picks.rewards[0]: an item line in the item scope; picker
//            'giveaway') is that unit, as its own free line, whatever the cart holds. Else the unit comes off a
//            candidate line: the picked product's lines (picks.product), else the only product among the candidates
//            (auto-picked), else, in app mode, every candidate, else a choice (picker 'item'); the cheapest unit
//            (basePrice; the first line on a tie) is SPLIT off its line (wire). No candidate and no giveaway: a choice
//            (picker 'giveaway'). Its % = 100, or MAX_DISCOUNT_VALUE / price × 100 when a cap below the unit price
//            applies (REF); the split applies to the capped % too (REF sends a capped line whole, which the guard reads
//            as qty free units over the cap).
//   A choice nobody can make yet (its picker isn't online) refuses the coupon: 'Not available online yet'
//   (NOT_ONLINE). Once the picker is online (P2-12: source, item; P2-13: rewards, giveaway) the same state is PENDING:
//   code 'PICK' with a reason ('Choose your free item' …), pending true; the summary and the payload treat it as not
//   applied, recheck keeps it. Online since P2-11 (FLOWS.free): with no choice to make, BOGO (one eligible line, or
//   app mode) and ITEM 100 % (one product in the cart, or app mode); with the customer's valid picks, every flow.
//   Since P2-12 / P2-13 every choice is pending (all four pickers are online); FLOWS keeps the switches (tests).
//
//   Picks (coupon.picks, set by the pickers; each optional, judged against the cart on every build):
//     { source: lineKey }    'bogo': the paid line whose units are copied free (REF selected_bogo_source_cart_item_id).
//     { product: itemId }    'item': the product whose cheapest unit is free (REF selected_item_discount_product_id).
//     { rewards: [ItemLine] } 'buyN': the rewards (≤ the count); 'item': the giveaway (the first one). Item lines as
//                            OO.cart.itemLine builds them from the MENU's product (its menu / tag price, size and
//                            modifiers: the validator checks that price); qty is forced to 1.
//   A pick that no longer qualifies is ignored (dropped from the picks freeLines returns): a source line no longer in
//   the cart or no longer a candidate, a product with no candidate line left, a reward outside its scope or, with
//   opts.menu, one OO.cart.lineIssue flags; a menu also re-prices the rewards (OO.cart.reprice).
//   freeLines(coupon, lines, opts) → { lines, split, pending, reason, code, choices, picks }   opts = { picks (default
//       coupon.picks), auto (default: OO.mode, or opts.mode, is 'app'), menu, at }. lines = the free lines, in build
//       order; split = { key, pct } for 'item' (the cart line one unit of which is free) | null. A refusal or a choice:
//       lines [] and split null, with reason / code (and pending for 'PICK'); choices = what the picker offers, else
//       null: { kind: 'source', lines } | { kind: 'item', products: [itemId…], lines } | { kind: 'rewards', count,
//       missing, scope, products } | { kind: 'giveaway', count: 1, scope, products } (products = the coupon_get_v4 rows,
//       raw.result_products / raw.applicable_products). picks = the picks the build used ({} when none), the
//       auto-picked single candidate included. Not a free-item coupon: lines [], everything else null / false / {}.
//       It judges the cart only: the coupon's own gates are eligibility's.
//   validPick(coupon, pick, lines, opts) → { ok, reason }   one pick ({ source } | { product } | { reward: ItemLine })
//       judged as freeLines would: the right flow, the line / product a candidate, the reward in its scope (and, with
//       opts.menu, orderable).
//   wire(lines, coupon, opts) → { lines, percents: { [line.key]: % } }   the lines as the payload sends them and the %
//       each carries: for a free-item flow that builds (freeLines: no reason), the cart lines with the 'item' unit split
//       off its line (its free unit first: key 'free~…', qty 1, the %; then the line itself with qty − 1, when any is
//       left), then the free lines at their %; else the cart lines with itemPercents (ITEM_DISCOUNT < 100 %; {} for
//       any other coupon). The lines' Σ OO.pricing.lineTotal is the cart's plus the free lines' (a split moves no
//       money). OO.pricing's summary and OO.payload build from it (OO.pricing._.couponWire).
//   __setFlows({ free, source, item, rewards, giveaway }) → the previous flags (tests only): free = the free-item flows
//       are online (notOnlineYet lets them through); the others = that picker is online.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function F() { return OO.format; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function r2(n) { return F().round2(n); }
  function cap(amount, max) { return max > 0 ? Math.min(amount, max) : amount; }
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }

  // Coupon types the online checkout can honour (the free-item ones through freeLines, once FLOWS.free is on).
  var ONLINE_TYPES = ['CART_DISCOUNT', 'ITEM_DISCOUNT', 'FREE_DELIVERY', 'BUY_ONE_GET_ONE_FREE', 'BUY_N_GET_M_FREE'];
  var TYPE_WORD = { DELIVERY: 'delivery', PICKUP: 'pickup', DINEIN: 'dine-in' };
  // recheck: the refusals that keep the coupon (unapplied) instead of removing it.
  var HOLD = ['PAYMENT'];
  // What is online (see the header). free: the payload carries the free-item lines (P2-11). The pickers
  // (pages/coupon-pickers.jsx): P2-12 turned source and item on, P2-13 rewards and giveaway. A picker switched off
  // (__setFlows, tests) turns its choice back into a refusal.
  var FLOWS = { free: true, source: true, item: true, rewards: true, giveaway: true };
  var NOT_ONLINE = 'Not available online yet';

  function refusal(reason, code) { return { ok: false, reason: reason, code: code }; }
  var OK = { ok: true, reason: null, code: null };

  // ---- matching (sizes; the guard's guardCouponEntries / guardLineMatches) ------------------------------------------
  function entries(coupon) {
    if (!coupon) return [];
    if (Array.isArray(coupon.scope)) return coupon.scope;
    if (coupon.raw && typeof coupon.raw === 'object' && OO.model && typeof OO.model.normalizeCoupon === 'function') {
      var n = OO.model.normalizeCoupon(coupon.raw, { now: 0 }); // a fixed clock: the scope doesn't depend on it
      if (n && Array.isArray(n.scope)) return n.scope;
    }
    return arr(coupon.itemIds).map(function (id) { return { id: num(id), sizes: [] }; });
  }

  // An item line against a scope list (entries' shape).
  function inScope(line, list) {
    if (!line || line.kind !== 'item') return false;
    var id = num(line.itemId);
    var size = line.sizeId === null || line.sizeId === undefined || line.sizeId === '' ? null : num(line.sizeId);
    return arr(list).some(function (e) {
      if (!e || num(e.id) !== id) return false;
      var sizes = arr(e.sizes);
      return !sizes.length || (size !== null && sizes.some(function (s) { return num(s) === size; }));
    });
  }

  function lineMatches(line, coupon) { return inScope(line, entries(coupon)); }

  function eligibleLines(lines, coupon) {
    return arr(lines).filter(function (l) { return lineMatches(l, coupon); });
  }

  // ---- what the checkout can carry --------------------------------------------------------------------------------
  function isAmount(coupon) {
    return !!coupon && coupon.type === 'CART_DISCOUNT' && (coupon.amount === true || num(coupon.percent) > 100);
  }

  function freeFlow(coupon) {
    if (!coupon) return null;
    if (coupon.type === 'BUY_ONE_GET_ONE_FREE') return 'bogo';
    if (coupon.type === 'BUY_N_GET_M_FREE') return 'buyN';
    if (coupon.type === 'ITEM_DISCOUNT' && num(coupon.percent) >= 100 && coupon.amount !== true) return 'item';
    return null;
  }

  function notOnlineYet(coupon) {
    if (ONLINE_TYPES.indexOf(coupon.type) < 0) return true;
    if (coupon.type === 'ITEM_DISCOUNT' && coupon.amount === true) return true;
    return !!freeFlow(coupon) && !FLOWS.free;
  }

  function enabled(store) { return !(store && store.couponsEnabled === false); }

  function appliesTo(coupon, orderType) {
    var a = coupon && coupon.appliesTo;
    return !a || a === 'ALL' || a === String(orderType || 'PICKUP').toUpperCase();
  }

  // ---- free-item coupons (P2-10; see the header) --------------------------------------------------------------------
  // The cart's purchasable item lines: no combo, no loyalty reward line, no coupon free line.
  function candidates(lines, coupon, opts) {
    var fresh = opts && opts.menu && Object.prototype.hasOwnProperty.call(opts.menu, 'redemptionProducts');
    return arr(lines).filter(function (l) {
      return lineMatches(l, coupon) && !l.reward && !l.free && (!fresh || !rewardIssue(l, entries(coupon), opts));
    });
  }
  function unitBase(line) { return num(line && line.basePrice); }
  function rewardScope(coupon) {
    var results = arr(coupon && coupon.resultScope);
    if (!results.length && coupon && coupon.raw && !Array.isArray(coupon.resultScope)) {
      var n = OO.model.normalizeCoupon(coupon.raw, { now: 0 });
      results = arr(n && n.resultScope);
    }
    return results.length ? results : entries(coupon); // the guard's fallback: no RESULTS_ITEMS → the item scope
  }
  function rawList(coupon, key) { return arr(coupon && isRecord(coupon.raw) ? coupon.raw[key] : null); }
  // REF: a cap below the unit's price frees MAX_DISCOUNT_VALUE of it, as a %.
  function itemPct(coupon, price) {
    var max = num(coupon.maxDiscount);
    return max > 0 && price > max ? max / price * 100 : 100;
  }
  function freeKey(coupon, tag, i, from) { return 'free~' + coupon.id + '~' + tag + '~' + i + (from ? '~' + from : ''); }
  // A free line: the line's shape (its own rows copied), qty 1, a unique key, its coupon % and source.
  function freeLine(base, key, pct, coupon, source) {
    return Object.assign({}, base, {
      key: key, qty: 1,
      modifiers: arr(base.modifiers).map(function (m) { return Object.assign({}, m); }),
      free: { couponId: coupon.id, pct: pct, source: source || null },
    });
  }

  // The valid reward / giveaway picks (≤ limit): item lines in `scope`, qty 1; with opts.menu orderable and re-priced.
  function rewardIssue(r, scope, opts) {
    if (!isRecord(r) || (r.kind !== undefined && r.kind !== 'item')) return 'That isn’t an item';
    var line = Object.assign({}, r, { kind: 'item', qty: 1 });
    if (!inScope(line, scope)) return 'That item isn’t part of this offer';
    if (opts.menu && OO.cart.lineIssue(line, OO.model.redemptionMenu(opts.menu), { at: opts.at })) return 'That item can’t be ordered right now';
    return null;
  }
  function validRewards(list, scope, limit, opts) {
    var out = [];
    arr(list).forEach(function (r) {
      if (out.length >= limit || rewardIssue(r, scope, opts)) return;
      var line = Object.assign({}, r, { kind: 'item', qty: 1 });
      out.push(opts.menu ? OO.cart.reprice([line], opts.menu).lines[0] : line);
    });
    return out;
  }

  function autoOf(opts) {
    if (opts.auto !== undefined) return !!opts.auto;
    var mode = opts.mode !== undefined ? opts.mode : OO.mode;
    return mode === 'app' || mode === 'kiosk';
  }

  function emptyBuild() {
    return { lines: [], split: null, pending: false, reason: null, code: null, choices: null, picks: {} };
  }
  // One shared size/availability rule for deciding whether to retain a coupon and for its picker.
  function rewardSizes(product, scope) {
    var any = false, ids = [];
    arr(scope).forEach(function (e) {
      if (num(e.id) !== num(product.id)) return;
      if (!arr(e.sizes).length) any = true;
      arr(e.sizes).forEach(function (id) { if (ids.indexOf(num(id)) < 0) ids.push(num(id)); });
    });
    var all = arr(product.sizes);
    var sizes = any ? all : all.filter(function (s) { return ids.indexOf(num(s.id)) >= 0; });
    return { ok: any || sizes.length > 0, sizes: sizes };
  }
  function hasAvailableFreeItem(coupon, opts) {
    if (!opts.menu) return true; // wait for the catalogue before judging a restored cart
    var menu = OO.model.redemptionMenu(opts.menu);
    var scope = freeFlow(coupon) === 'buyN' ? rewardScope(coupon) : entries(coupon);
    return scope.some(function (e) {
      var p = menu.products && menu.products[e.id];
      if (!p || p.soldOut || p.available === false) return false;
      if (opts.at != null && OO.model.availabilityAt) {
        var at = OO.model.availabilityAt(menu, p, {
          now: opts.at, domain: opts.domain || menu.domain, timeZone: menu.timeZone,
        });
        if (at && !at.available) return false;
      }
      var allowed = rewardSizes(p, scope);
      if (!allowed.ok) return false;
      var sizes = allowed.sizes.filter(function (s) { return s.available !== false; });
      if (allowed.sizes.length && !sizes.length) return false;
      return (sizes.length ? sizes : [null]).some(function (s) {
        var sizeId = s ? s.id : null;
        var sel = OO.modifiers.prune(p, sizeId, OO.modifiers.initial(p).sel);
        var view = OO.modifiers.view(p, { sizeId: sizeId, sel: sel, qty: 1 });
        if (view.groups.some(function (g) { return g.blocked; })) return false;
        var line = OO.cart.itemLine(p, { sizeId: sizeId, sel: sel, qty: 1 });
        var issue = OO.cart.lineIssue(line, menu);
        return !issue || issue.code === 'changed'; // the customer can still make required option choices
      });
    });
  }
  function refuseBuild(b, reason, code) { return Object.assign(b, { lines: [], split: null, reason: reason, code: code }); }
  // A choice: pending while its picker is online, else the coupon is refused (not online yet).
  var CHOOSE = {
    source: 'Choose the item you get free',
    item: 'Choose the item you get free',
    rewards: 'Choose your free items',
    giveaway: 'Choose your free item',
  };
  function chooseBuild(b, kind, choices, reason) {
    b.choices = choices;
    if (!FLOWS[kind]) return refuseBuild(b, NOT_ONLINE, 'NOT_ONLINE');
    refuseBuild(b, reason || CHOOSE[kind], 'PICK');
    b.pending = true;
    return b;
  }

  function buildBogo(coupon, lines, picks, opts, b) {
    var cands = candidates(lines, coupon, opts);
    if (!cands.length) return refuseBuild(b, 'Add an eligible item to use this coupon', 'NO_ITEMS');
    var picked = cands.filter(function (l) { return picks.source !== undefined && l.key === picks.source; })[0];
    var sources;
    if (picked) sources = [picked];
    else if (cands.length === 1) sources = cands; // REF web: the one line is selected for the customer
    else if (autoOf(opts)) sources = cands; // REF app / kiosk: every line, cheapest units first
    else return chooseBuild(b, 'source', { kind: 'source', lines: cands });
    if (sources.length === 1) b.picks.source = sources[0].key;
    var units = [];
    sources.forEach(function (l) { for (var i = 0; i < Math.max(1, Math.floor(num(l.qty))); i++) units.push(l); });
    // Cheapest first; a stable sort keeps the cart order on a tie.
    units = units.map(function (l, i) { return { l: l, i: i }; })
      .sort(function (x, y) { return unitBase(x.l) - unitBase(y.l) || x.i - y.i; }).map(function (x) { return x.l; });
    units.slice(0, Math.max(1, num(coupon.maxFreeCount))).forEach(function (l, i) {
      b.lines.push(freeLine(l, freeKey(coupon, 'bogo', i, l.key), 100, coupon, l.key));
    });
    return b;
  }

  function buildBuyN(coupon, lines, picks, opts, b) {
    var units = candidates(lines, coupon, opts).reduce(function (t, l) { return t + Math.max(0, Math.floor(num(l.qty))); }, 0);
    var min = Math.max(1, Math.floor(num(coupon.minItemCount)));
    if (units < min) return refuseBuild(b, 'Add ' + min + ' qualifying item' + (min === 1 ? '' : 's') + ' to use this coupon', 'MIN_ITEMS');
    var count = Math.max(1, Math.floor(num(coupon.maxFreeCount)));
    var scope = rewardScope(coupon);
    var rewards = validRewards(picks.rewards, scope, count, opts);
    if (rewards.length) b.picks.rewards = rewards;
    if (rewards.length < count) {
      return chooseBuild(b, 'rewards', { kind: 'rewards', count: count, missing: count - rewards.length, scope: scope, products: rawList(coupon, 'result_products') },
        count === 1 ? 'Choose your free item' : 'Choose your ' + count + ' free items');
    }
    rewards.forEach(function (r, i) { b.lines.push(freeLine(r, freeKey(coupon, 'reward', i), 100, coupon, null)); });
    return b;
  }

  function buildItem(coupon, lines, picks, opts, b) {
    var gift = validRewards(picks.rewards, entries(coupon), 1, opts)[0];
    if (gift) { // the customer's giveaway: its own free line, whatever the cart holds
      b.picks.rewards = [gift];
      b.lines.push(freeLine(gift, freeKey(coupon, 'gift', 0), itemPct(coupon, unitBase(gift)), coupon, null));
      return b;
    }
    var cands = candidates(lines, coupon, opts);
    if (!cands.length) {
      return chooseBuild(b, 'giveaway', { kind: 'giveaway', count: 1, scope: entries(coupon), products: rawList(coupon, 'applicable_products') });
    }
    var products = [];
    cands.forEach(function (l) { if (products.indexOf(num(l.itemId)) < 0) products.push(num(l.itemId)); });
    var want = picks.product !== undefined && products.indexOf(num(picks.product)) >= 0 ? num(picks.product) : null;
    var from;
    if (want !== null) from = cands.filter(function (l) { return num(l.itemId) === want; });
    else if (products.length === 1) from = cands; // REF web: the one product is selected for the customer
    else if (autoOf(opts)) from = cands; // REF app / kiosk: the cheapest unit of them all
    else return chooseBuild(b, 'item', { kind: 'item', products: products, lines: cands });
    if (want !== null || products.length === 1) b.picks.product = num(from[0].itemId);
    var chosen = from.reduce(function (best, l) { return !best || unitBase(l) < unitBase(best) ? l : best; }, null);
    b.split = { key: chosen.key, pct: itemPct(coupon, unitBase(chosen)) };
    return b;
  }

  function freeLines(coupon, lines, opts) {
    opts = opts || {};
    var b = emptyBuild();
    var flow = freeFlow(coupon);
    if (!flow) return b;
    if (opts.menu && Array.isArray(opts.menu.redemptionCouponIds) && opts.menu.redemptionCouponIds.indexOf(num(coupon.id)) < 0) {
      return refuseBuild(b, 'This coupon is no longer available', 'UNAVAILABLE');
    }
    var available = hasAvailableFreeItem(coupon, opts);
    opts = Object.assign({}, opts, { menu: OO.model.redemptionMenu(opts.menu) });
    var picks = isRecord(opts.picks) ? opts.picks : (opts.picks === undefined && isRecord(coupon.picks) ? coupon.picks : {});
    if (flow === 'bogo') b = buildBogo(coupon, lines, picks, opts, b);
    else if (flow === 'buyN') b = buildBuyN(coupon, lines, picks, opts, b);
    else b = buildItem(coupon, lines, picks, opts, b);
    if (!available) {
      return Object.assign(refuseBuild(b, 'No eligible free items are available for this coupon', 'NO_FREE_ITEMS'), { pending: false, picks: {} });
    }
    return b;
  }

  function validPick(coupon, pick, lines, opts) {
    opts = opts || {};
    var flow = freeFlow(coupon);
    var no = function (reason) { return { ok: false, reason: reason }; };
    if (!flow || !isRecord(pick)) return no('This coupon has no choice to make');
    if (pick.source !== undefined) {
      if (flow !== 'bogo') return no('This coupon has no item to copy');
      return candidates(lines, coupon, opts).some(function (l) { return l.key === pick.source; }) ? { ok: true, reason: null } : no('That item isn’t eligible for this offer');
    }
    if (pick.product !== undefined) {
      if (flow !== 'item') return no('This coupon has no item to choose');
      return candidates(lines, coupon, opts).some(function (l) { return num(l.itemId) === num(pick.product); }) ? { ok: true, reason: null } : no('That item isn’t eligible for this offer');
    }
    if (pick.reward !== undefined) {
      if (flow === 'bogo') return no('This coupon has no free item to choose');
      var why = rewardIssue(pick.reward, flow === 'buyN' ? rewardScope(coupon) : entries(coupon), opts);
      return why ? no(why) : { ok: true, reason: null };
    }
    return no('This coupon has no choice to make');
  }

  function wire(lines, coupon, opts) {
    lines = arr(lines);
    if (!coupon || !freeFlow(coupon)) return { lines: lines, percents: coupon ? itemPercents(lines, coupon) : {} };
    var b = freeLines(coupon, lines, opts);
    if (b.code) return { lines: lines, percents: {} };
    var out = [];
    var pct = {};
    lines.forEach(function (l) {
      if (!b.split || l.key !== b.split.key) { out.push(l); return; }
      var unit = freeLine(l, freeKey(coupon, 'unit', 0, l.key), b.split.pct, coupon, l.key);
      out.push(unit);
      pct[unit.key] = b.split.pct;
      if (Math.floor(num(l.qty)) > 1) out.push(Object.assign({}, l, { qty: Math.floor(num(l.qty)) - 1 }));
    });
    b.lines.forEach(function (f) { out.push(f); pct[f.key] = f.free.pct; });
    return { lines: out, percents: pct };
  }

  // ---- eligibility and amounts --------------------------------------------------------------------------------------
  function eligibility(coupon, ctx) {
    ctx = ctx || {};
    if (!coupon) return refusal(null, null);
    if (ctx.store && !enabled(ctx.store)) return refusal('Coupons aren’t available at this store', 'DISABLED');
    if (notOnlineYet(coupon)) return refusal(NOT_ONLINE, 'NOT_ONLINE');
    var sub = num(ctx.subtotal);
    var type = String(ctx.orderType || 'PICKUP').toUpperCase();
    var cur = ctx.currency || '$';
    if (coupon.available === false) return refusal('This coupon is not available right now', 'UNAVAILABLE');
    if (coupon.minOrder > 0 && sub < coupon.minOrder) return refusal('Minimum order ' + F().money(coupon.minOrder, cur) + ' required', 'MIN_ORDER');
    if (coupon.maxOrder > 0 && sub > coupon.maxOrder) return refusal('Maximum order ' + F().money(coupon.maxOrder, cur) + ' exceeded', 'MAX_ORDER');
    if (!appliesTo(coupon, type)) {
      return refusal('Only valid for ' + (TYPE_WORD[coupon.appliesTo] || String(coupon.appliesTo).toLowerCase()) + ' orders', 'ORDER_TYPE');
    }
    if (coupon.type === 'FREE_DELIVERY' && type !== 'DELIVERY') return refusal('Only valid for delivery orders', 'ORDER_TYPE');
    // Online-only (the owner, 2026-10-02): the POS doesn't waive delivery on a counter / cash order.
    if (coupon.type === 'FREE_DELIVERY' && ctx.paymentMethod !== 'ONLINE_PAYMENT') return refusal('Free delivery applies when you pay online.', 'PAYMENT');
    var flow = freeFlow(coupon);
    if (coupon.type === 'ITEM_DISCOUNT' || flow) {
      if (!entries(coupon).length) return refusal('This coupon has no eligible items', 'NO_SCOPE');
      if (ctx.lines && flow) {
        var b = freeLines(coupon, ctx.lines, ctx);
        if (b.code) return refusal(b.reason, b.code);
      } else if (ctx.lines && !eligibleLines(ctx.lines, coupon).length) {
        return refusal('Add an eligible item to use this coupon', 'NO_ITEMS');
      }
    }
    return OK;
  }

  // Σ the lines' totals for the subtotal, as the summary prices them (OO.pricing.lineTotal: allowances, combos).
  function subtotalOf(lines, store) {
    return r2(arr(lines).reduce(function (t, l) { return t + num(OO.pricing.lineTotal(l, store)); }, 0));
  }

  function discount(lines, coupon, ctx) {
    ctx = ctx || {};
    var none = { amount: 0, deliveryDiscount: 0, ok: false, reason: null, code: null, pending: false };
    if (!coupon) return none;
    var sub = ctx.subtotal !== undefined && ctx.subtotal !== null ? num(ctx.subtotal) : subtotalOf(lines, ctx.store);
    var e = eligibility(coupon, Object.assign({}, ctx, { subtotal: sub, lines: lines }));
    if (!e.ok) return Object.assign({}, none, { reason: e.reason, code: e.code, pending: e.code === 'PICK' });
    var pct = num(coupon.percent);
    var max = num(coupon.maxDiscount);
    function done(amount, deliveryDiscount) {
      return { amount: amount, deliveryDiscount: deliveryDiscount, ok: true, reason: null, code: null, pending: false };
    }
    if (freeFlow(coupon)) { // the free units' product part, each at its % (BOGO / BUY_N 100; an ITEM unit capped)
      var w = wire(lines, coupon, ctx);
      return done(r2(w.lines.reduce(function (t, l) { return t + unitBase(l) * num(l.qty) * num(w.percents[l.key]) / 100; }, 0)), 0);
    }
    switch (coupon.type) {
      case 'CART_DISCOUNT': // a % of the subtotal, or (isAmount) DISCOUNT dollars; ≤ cap, ≤ subtotal
        return done(r2(Math.max(0, Math.min(cap(isAmount(coupon) ? pct : sub * pct / 100, max), sub))), 0);
      case 'ITEM_DISCOUNT': // < 100 % (else a free-item flow): the eligible lines' product part
        var raw = eligibleLines(lines, coupon).reduce(function (t, l) { return t + num(l.basePrice) * num(l.qty) * pct / 100; }, 0);
        return done(r2(cap(raw, max)), 0);
      case 'FREE_DELIVERY': // the fee, up to the cap (0 = no cap); DISCOUNT % is ignored
        return done(0, r2(cap(Math.max(0, num(ctx.deliveryFee)), max)));
      default: // unreachable: eligibility turns the other types away
        return Object.assign({}, none, { reason: NOT_ONLINE, code: 'NOT_ONLINE' });
    }
  }

  // Per-line ITEM_DISCOUNT percent (< 100 %), scaled down when the cap applies (REF calculateItemDiscount). None for a
  // coupon the payload can't honour (notOnlineYet) or a free-item flow (wire carries those), so no row ever carries its
  // %.
  function itemPercents(lines, coupon) {
    var out = {};
    if (!coupon || coupon.type !== 'ITEM_DISCOUNT' || notOnlineYet(coupon) || freeFlow(coupon)) return out;
    var pct = num(coupon.percent);
    var elig = eligibleLines(lines, coupon);
    var rawTotal = elig.reduce(function (t, l) { return t + num(l.basePrice) * num(l.qty) * pct / 100; }, 0);
    var max = num(coupon.maxDiscount);
    elig.forEach(function (l) {
      var price = num(l.basePrice) * num(l.qty);
      var raw = price * pct / 100;
      var applied = max > 0 && rawTotal > max ? raw / rawTotal * max : raw;
      out[l.key] = price > 0 ? applied / price * 100 : 0;
    });
    return out;
  }

  // ---- the store's list, the live coupon, the D-05 re-check ---------------------------------------------------------
  function domainOf(opts) {
    return opts.domain || OO.model.domain(opts.orderType || 'PICKUP', opts.mode, { table: !!opts.table });
  }
  function instantOf(opts) { return opts.at !== undefined && opts.at !== null ? opts.at : opts.now; }

  function list(raw, store, opts) {
    opts = opts || {};
    if (!enabled(store)) return [];
    return OO.model.normalizeCoupons(raw, { now: instantOf(opts), timeZone: (store && store.timezone) || null, domain: domainOf(opts) });
  }

  function live(coupon, store, opts) {
    opts = opts || {};
    if (!coupon || !coupon.raw || typeof coupon.raw !== 'object' || !store) return coupon || null;
    var fresh = OO.model.normalizeCoupons({ data: [coupon.raw] }, { now: instantOf(opts), timeZone: store.timezone || null, domain: domainOf(opts) })[0] || null;
    // The customer's picks ride on the applied coupon (P2-10), not on its row.
    return fresh && coupon.picks !== undefined ? Object.assign(fresh, { picks: coupon.picks }) : fresh;
  }

  // A removal and its toast: 'Coupon OFF10 removed. Minimum order $30.00 required.'
  function removal(coupon, reason, code) {
    var label = (coupon && (coupon.code || coupon.name)) || '';
    var why = String(reason || '');
    var text = code === 'NO_ITEMS' ? 'No eligible items are left in your cart.' : why + (/[.!?]$/.test(why) ? '' : '.');
    var message = (label ? 'Coupon ' + label : 'Coupon') + ' removed. ' + text;
    return { keep: false, ok: false, coupon: null, reason: reason, code: code, message: message, lines: [], pending: false };
  }

  function recheck(coupon, lines, ctx) {
    ctx = ctx || {};
    if (!coupon) return { keep: false, ok: false, coupon: null, reason: null, code: null, message: null, lines: [], pending: false };
    var c = coupon;
    if (ctx.store) {
      c = live(coupon, ctx.store, ctx);
      if (!c) return removal(coupon, 'This coupon has expired', 'EXPIRED');
    }
    var e = eligibility(c, Object.assign({}, ctx, {
      subtotal: subtotalOf(lines, ctx.store), lines: lines,
      currency: ctx.currency || (ctx.store && ctx.store.currency),
    }));
    // A free-item coupon carries the picks its build used (D-05: a pick that no longer qualifies is dropped).
    var b = freeFlow(c) && (e.ok || e.code === 'PICK') ? freeLines(c, lines, ctx) : null;
    if (b) c = Object.assign({}, c, { picks: b.picks });
    if (e.ok) return { keep: true, ok: true, coupon: c, reason: null, code: null, message: null, lines: b ? b.lines : [], pending: false };
    if (e.code === 'PICK') return { keep: true, ok: false, coupon: c, reason: e.reason, code: e.code, message: null, lines: [], pending: true };
    var hold = Array.isArray(ctx.hold) ? ctx.hold : HOLD;
    if (hold.indexOf(e.code) >= 0) return { keep: true, ok: false, coupon: c, reason: e.reason, code: e.code, message: null, lines: [], pending: false };
    return removal(c, e.reason, e.code);
  }

  function setFlows(patch) {
    var before = Object.assign({}, FLOWS);
    Object.keys(FLOWS).forEach(function (k) { if (isRecord(patch) && patch[k] !== undefined) FLOWS[k] = !!patch[k]; });
    return before;
  }

  OO.coupons = OO.lib.coupons = Object.assign(OO.coupons || {}, {
    entries: entries,
    lineMatches: lineMatches,
    eligibleLines: eligibleLines,
    isAmount: isAmount,
    notOnlineYet: notOnlineYet,
    enabled: enabled,
    appliesTo: appliesTo,
    eligibility: eligibility,
    discount: discount,
    itemPercents: itemPercents,
    list: list,
    live: live,
    recheck: recheck,
    freeFlow: freeFlow,
    freeLines: freeLines,
    rewardSizes: rewardSizes,
    hasAvailableFreeItem: hasAvailableFreeItem,
    validPick: validPick,
    wire: wire,
    __setFlows: setFlows,
  });
})();


// ===================== src/lib/schedule.jsx =====================
// src/lib/schedule.jsx: OO.schedule (alias OO.lib.schedule): the scheduling engine (Batch 2 P2-22, with
// model-store.jsx). One pure engine, in the STORE's time zone, for the ordering gates (ASAP allowed?), the
// order-later window, the slot grid (step, shift merge, past-midnight shifts), holidays and closures (Schedule-a-Pause),
// across DST changes. model-store.jsx parses the raw getRestaurant fields into the Store, and OO.model.openStatus /
// pickupOptions / slotInstant are thin wrappers over this file. Callers: pages/checkout-time.jsx (P2-23, the UI),
// P2-16 (availability at the chosen slot), P2-09's follow-up (coupon availability at the slot).
// Pure: no JSX, no document or OO.http at load time (the tests and the parity generator run it raw in node:vm).
// Load order (build/concat.mjs): after pricing.jsx / payload.jsx and before store.jsx; it shares the model core's
// helpers (OO.model._).
//
// REF = the Next app, develop d298a7e: DU = src/shared/utils/dateUtils.ts (unchanged since b52cde9), IDX =
// src/shared/lib/api/index.ts. REF reads the DEVICE clock and time zone; this engine reads the store's (BATCH2-PLAN:
// "keep the store tz, match REF's grid and window semantics").
//
// API. `now` / `at`: a Date | epoch ms | 'Y-m-d H:i:s' store wall clock | Clock (as everywhere in OO.model). orderType:
// 'PICKUP' | 'DELIVERY' | 'DINEIN' (app forms such as 'APP_DELIVERY' map to these; default PICKUP).
//   window(store) → { enabled, reason, asap, from, to }: the order-later window (REF DU:124-168).
//       enabled = is_preorder || order_later (Store.scheduling); reason 'off' (neither), 'empty' (from ≥ to), else null.
//       Days are today + i for from ≤ i < to: from = order_later ? order_later_days : 0, to = is_preorder ?
//       pre_min_day : 7 (pre_min_day is the window LENGTH; 0 = no days). asap = !(order_later && order_later_days ≥ 1)
//       (admin "Future Order Only": no ASAP).
//   asap(store, orderType, now) → { ok, reason, readyAt }: can the order go in ASAP now (REF isAsapAvailableForOrderType
//       DU:588-608, except that "open now" comes from the hours, not the server's pickupAvailable / deliveryAvailable
//       flags, which can be 15 min stale). readyAt = now + the buffer (ms). reason, first that applies: 'offline' (online
//       ordering off) · 'futureOnly' (window().asap false; not for DINEIN) · 'closure' (a closure in the type's scope
//       covers now) · 'holiday' (closed, and today is a holiday) · 'closed' (outside the type's hours) · 'closure'
//       (one covers readyAt: the food would be ready inside it; G1 fix D) · null when ok.
//   slots(store, orderType, now) → days[] (the scheduled times; never ASAP): [] while online ordering is off, while
//       window().enabled is false (REF shows no scheduling then: DU:124, OrderTimePopup) and for DINEIN. One day per
//       window day: { ymd, key, date (= ymd), label ('Today' | 'Tomorrow' | 'Fri 2 Oct'), holiday, holidayLabel
//       (shop_holidays label | null), slots } (holidays and days without times stay listed, with no slots: REF
//       generateAvailableDays). slot = { at (epoch ms), value ('YYYY-MM-DD HH:mm:00', the slot's own store-local wall
//       clock; = deliveryTime), label ('6:30pm'), time ('HH:mm'), ymd (the slot's calendar day), owner (the day it is
//       listed under), dayLabel ('Sat 3' when ymd ≠ owner: a past-midnight slot, REF DU:516; else null), asap: false,
//       deliveryTime }. A slot is also a valid payload `ctx.slot` (OO.payload reads asap / deliveryTime).
//   isOpenAt(store, orderType, at) → bool: the store takes this type at that instant: online on, no closure in the
//       type's scope, inside the type's hours. Holidays and disabled weekdays close that business day's service;
//       yesterday's after-midnight service keeps yesterday's availability. End-exclusive: closing time itself is closed (a slot may still sit AT closing
//       time, as REF's grid includes it). orderType 'OPENING' (or none) = the store's opening hours. Same answer as
//       openStatus(store, at, type).isOpen.
//   findSlot(store, orderType, value, now) → slot | null: the offered slot with this wall-clock value ('YYYY-MM-DD
//       HH:mm[:ss]' or 'YYYY-MM-DDTHH:mm'), judged now (REF findAvailableScheduledSlotWithinWindow DU:399-419: the
//       submit re-check and the stale-selection re-prompt). A time TODAY on a 5-minute mark is also found when today's
//       grid, which moves with the clock (see Grid), offers it laid through that minute (P2-23).
//   openStatus(store, now, type='OPENING') → OO.model.openStatus (its header has the labels).
//   pickupOptions(store, now, {orderType, days=2}) → { days, slots, asapLabel, cookingTimeMins }: the Phase 1 view the
//       current checkout reads (OO.model.pickupOptions; see "Phase 1 view" below).
//   slotInstant(store, slot, now) → ms: ASAP → now; a scheduled slot → its store-local deliveryTime
//       (OO.model.slotInstant).
//   chosen(store, orderType, draft, now) → { day: { key, label }, slot } | null (P2-23): the time a checkout draft
//       ({ slot: 'ASAP' | a wall-clock value }) names, while it is still offered, found as pages/checkout-time.jsx finds
//       it: 'ASAP' while asap() is ok; a value through findSlot, under the day its run starts (day.label 'Today' |
//       'Tomorrow' | 'Fri 2 Oct'). slot has pickupOptions' shape (value, label ('12:30am · Sat 3' past midnight), time,
//       asap, deliveryTime). null when the draft names no time or it is gone: the caller's default applies (the Phase 1
//       view's first slot is the checkout's default). cart.jsx, menu.jsx and store.jsx read the draft's time with it.
//   week(store, orderType, now) → 7 days from today (the store's calendar): [{ ymd, dow (0 = Sunday), today, holiday,
//       holidayLabel (shop_holidays label | null), shifts: [{ start, end }] }] (P3-FIX #30; the About page's hours).
//       shifts: the day's OWN hours for the type as the rest of this engine reads them (Hours below: Store.hoursUtc at
//       that day's own UTC offset, so the days after a DST change show their own wall-clock hours, as isOpenAt and the
//       slots judge them; the type fallback), in local minutes from the day's midnight (end > 1440: into the next
//       morning), by start, overlapping or touching ones as one; none on a holiday. A shift past midnight is listed on
//       the day it starts. orderType defaults to 'OPENING'. Online ordering and closures don't change the hours.
//
// Rules (each REF reference is DU unless named):
//   Hours      Store.hoursUtc (the UTC running_hours as sent) are converted per DAY at the UTC offset in force that day
//              (local noon), so the days after a DST change get their own wall-clock hours. REF and Phase 1 shift every
//              day by the CURRENT offset (IDX:379-411 currentUtcOffsetMinutes; BATCH2-PLAN lists this DST-naive shift
//              under "not to port"). The backend's open check (metadataController timeCheck) and the admin's hours
//              editor read the stored UTC hours at the offset in force at the time, which is what a day's own offset
//              predicts. The answer for an instant never depends on when the Store was normalised. A shift that runs
//              past midnight keeps its start day's offset (on the two DST nights its end can be an hour out). A Store
//              without hoursUtc (built by hand) uses its `hours` (local minutes) as they are. Type hours: PICKUP /
//              DELIVERY use their own hours, else the OPENING hours, else the PICKUP ones; OPENING and DINEIN use
//              OPENING, else PICKUP (Phase 1). REF has no fallback (a type without its own hours is closed:
//              IDX:414-446) and treats an empty workingInfo as open 24 h (DU:325-326): neither is followed (an owner
//              decision; tests pin both). A shift whose start equals its stop is 24 h (Phase 1; REF's 00:00–00:00 has
//              no slots, DU:478).
//   Buffer     The cooking buffer is Store.cookingTimeMins (pickup_buffer_time, default 15), and deliveryTimeMins
//              (delivery_buffer_time, default 30) for a DELIVERY order; DINEIN has none. REF defaults both to 0
//              (DU:309-312, IDX:2269): not followed, so the payload's ASAP deliver_time, the checkout's "Cooking time"
//              and this engine read the same number (an owner decision).
//   Closures   Store.closures, [start, end) in epoch ms (REF inScheduledClosure DU:421-437). Scope: '' / ALL /
//              ONLINE_ORDER block every online type, PICKUP and DELIVERY (ONLINE[_APP]_* already normalised) their own;
//              DINEIN (table orders) is blocked by '' / ALL / TABLE_SIDE only. TABLE_RESERVATION and KIOSK block nothing
//              here (DU:439-452).
//   Holidays   A holiday closes the business day, including its service after midnight on the following date. The
//              previous business day's incoming overnight service remains available. The holiday stays listed with
//              holiday: true; Today may still offer what remains of yesterday's service after midnight.
//   Runs       A day's shifts that overlap or are at most `buffer` min apart become one run (REF mergeContiguousPeriods
//              DU:214-234 merges gaps 0…buffer; overlapping shifts merge here too, where REF lays two grids over each
//              other). A run that ends past midnight belongs to the day it starts (REF DU:243-295, 478-480), and a
//              midnight period paired with an evening period on the SAME weekday belongs to that evening's business
//              day (18:00–23:59 + 00:00–03:00 means 18:00 through next-day 03:00). Their midnight gap must be at most
//              `buffer` min and together span less than 24 h. Other morning periods and 24-hour days keep their own times.
//   Grid       Per run: from run start + buffer; on TODAY from max(that, now + buffer × (ASAP ok ? 2 : 1)); rounded up to
//              5 min; then every 15 min up to and INCLUDING the run's end. Preparation time sets the first slot,
//              independently of the interval between slots.
//              A slot inside a closure, at or before `now`, or at a wall-clock time that does not exist that day (the
//              hour skipped by a DST change) is left out; a repeated wall-clock hour (DST ends) gives one slot.
//              Today's list also offers what is left of yesterday's run past midnight (REF never offers it: yesterday
//              is outside its window). Today's times move with the clock (6:30pm, offered at 3:00pm, isn't at 3:01pm:
//              6:35pm, 6:50pm, …), so findSlot keeps a time chosen from them while it is still bookable: on a 5-minute
//              mark, with at least one preparation buffer remaining, no later than its end, outside closures (P2-23; REF
//              matches the moving grid exactly and re-asks a time chosen today every few minutes).
//   Window     window() above; future days come from the store calendar (today = the store's date now).
//   DINEIN     Provisional, for P4 (qr mode): ASAP only (REF TableSideCheckoutPage forces ASAP), opening hours, no
//              buffer, never in the scheduled window.
//
// Phase 1 view (pickupOptions): the current checkout and cart read it until P2-23 moves them to slots(). It is slots()
// (gate, window, future-only and grid included: a store without is_preorder / order_later offers ASAP only),
// grouped by CALENDAR day (a slot's value date is its day's key, as before), the ASAP slot first ('15–20 mins'
// for a 15-min buffer), days without a slot left out, and the first `days` (2) days kept. { days: [{key, date, label,
// slots}], slots (the first day's), asapLabel, cookingTimeMins (the buffer) }; slot = { value: 'ASAP' |
// 'YYYY-MM-DD HH:mm:00', label: '15–20 mins' | '6:30pm', time: 'HH:mm', asap, deliveryTime: local 'YYYY-MM-DD
// HH:mm:ss' }.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};
  var S = (OO.schedule = OO.lib.schedule = OO.schedule || {});
  var _ = OO.model._, F = _.F, arr = _.arr, resolveNow = _.resolveNow;

  var STEP_MIN = 15;   // Slot interval; preparation buffers do not change it.
  var ROUND_MIN = 5;   // REF DU:495: a run's first slot is rounded up to 5 min
  var WINDOW_DAYS = 7; // REF DU:137: the window length without is_preorder

  function byStart(a, b) { return a.start - b.start; }
  function mod1440(m) { return ((m % 1440) + 1440) % 1440; }
  function whole(v) { var n = Number(v); return isFinite(n) && n > 0 ? Math.floor(n) : 0; }
  function online(store) { return !!(store && store.online && store.online.active); }
  function isHoliday(store, ymd) { return arr(store.holidays).indexOf(ymd) >= 0; }

  // 'PICKUP' | 'DELIVERY' | 'DINEIN' | 'OPENING' from an order type in any of its forms.
  function typeOf(t, dflt) {
    var s = String(t || '').toUpperCase();
    if (!s) return dflt;
    if (s === 'OPENING') return 'OPENING';
    if (s.indexOf('DELIVERY') >= 0) return 'DELIVERY';
    if (s.indexOf('DINE') >= 0 || s.indexOf('TABLE') >= 0) return 'DINEIN';
    return 'PICKUP';
  }
  function bufferOf(store, type) {
    if (type === 'DINEIN') return 0;
    return type === 'DELIVERY' ? (store.deliveryTimeMins || 30) : (store.cookingTimeMins || 15);
  }

  // ---- hours ---------------------------------------------------------------------------------------
  function hasHours(map) { return !!map && Object.keys(map).some(function (k) { return arr(map[k]).length > 0; }); }
  // The weekday table for a type (Phase 1 hoursFor): PICKUP / DELIVERY their own hours, else OPENING, else PICKUP.
  function tableFor(h, type) {
    h = h || {};
    if ((type === 'PICKUP' || type === 'DELIVERY') && hasHours(h[type])) return h[type];
    return hasHours(h.OPENING) ? h.OPENING : (h.PICKUP || {});
  }

  // A per-call cache for one store, by day:
  //   offsetOn(ymd) → the UTC offset (minutes) in force that day, read at local noon (clear of a 2–3am DST change);
  //   instant(ymd, minutes < 1440) → the instant of that store wall clock, or null when it doesn't exist (the hour a
  //     DST change skips: instantOf would move it an hour on). A day with one offset from 00:00 to 23:59:59 (every day
  //     but the two DST changes) is plain arithmetic; a change day goes through instantOf and must read back the same.
  function cacheFor(store) {
    var f = F(), tz = store.timezone, offs = {}, steady = {};
    function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
    function wallUtc(ymd, minutes) { return Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)), 0, minutes); }
    function offsetOn(ymd) {
      if (!has(offs, ymd)) offs[ymd] = f.tzOffsetMinutes(new Date(f.instantOf(ymd, 720, tz)), tz);
      return offs[ymd];
    }
    function instant(ymd, minutes) {
      var off = offsetOn(ymd);
      if (!has(steady, ymd)) {
        var midnight = wallUtc(ymd, 0) - off * 60000;
        steady[ymd] = f.tzOffsetMinutes(new Date(midnight), tz) === off && f.tzOffsetMinutes(new Date(midnight + 86399000), tz) === off;
      }
      if (steady[ymd]) return wallUtc(ymd, minutes) - off * 60000;
      var t = f.instantOf(ymd, minutes, tz);
      var z = f.zoned(new Date(t), tz);
      return z.ymd === ymd && z.minutes === minutes ? t : null;
    }
    return { offsetOn: offsetOn, instant: instant };
  }

  // Day ymd's own shifts for `type`, in local minutes from its midnight (end > 1440: past midnight), by start.
  function shiftsOf(store, type, ymd, cx) {
    var dow = F().weekdayOf(ymd);
    if (store.hoursUtc) {
      var off = cx.offsetOn(ymd);
      return arr(tableFor(store.hoursUtc, type)[dow]).map(function (iv) {
        var s = mod1440(iv.start + off), e = mod1440(iv.end + off);
        if (e <= s) e += 1440;
        return { start: s, end: e };
      }).sort(byStart);
    }
    return arr(tableFor(store.hours, type)[dow]).map(function (iv) { return { start: iv.start, end: iv.end }; }).sort(byStart);
  }

  // A split midnight/evening pair uses this business day's OWN early closing time, even if tomorrow is disabled.
  // Other breaks stay separate for open/ASAP checks; only the slot grid merges those with the preparation buffer.
  function businessShiftsOf(store, type, ymd, buffer, cx) {
    if (isHoliday(store, ymd)) return [];
    var shifts = shiftsOf(store, type, ymd, cx);
    var first = shifts[0], last = shifts[shifts.length - 1];
    if (shifts.length > 1 && continues(last, first, buffer)) {
      last.end = Math.max(last.end, first.end + 1440);
      shifts = shifts.slice(1);
    }
    return shifts;
  }

  // This calendar date can contain yesterday's service even when its own business day is closed.
  function dayIntervals(store, type, ymd, cx) {
    var out = [];
    var buffer = bufferOf(store, type);
    businessShiftsOf(store, type, ymd, buffer, cx).forEach(function (iv) { out.push({ start: iv.start, end: iv.end, spill: false }); });
    var prev = F().addDays(ymd, -1);
    businessShiftsOf(store, type, prev, buffer, cx).forEach(function (iv) { if (iv.end > 1440) out.push({ start: 0, end: iv.end - 1440, spill: true }); });
    return out.sort(byStart);
  }

  // ---- closures ------------------------------------------------------------------------------------
  var CLOSURE_SCOPE = {
    OPENING: ['', 'ALL', 'ONLINE_ORDER'], PICKUP: ['', 'ALL', 'ONLINE_ORDER', 'PICKUP'], DELIVERY: ['', 'ALL', 'ONLINE_ORDER', 'DELIVERY'],
    DINEIN: ['', 'ALL', 'TABLE_SIDE'],
  };
  function closureAt(store, instant, type) {
    var scope = CLOSURE_SCOPE[type] || CLOSURE_SCOPE.OPENING;
    var list = arr(store.closures);
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (scope.indexOf(String(c.domain || '').toUpperCase()) >= 0 && instant >= c.start && instant < c.end) return c;
    }
    return null;
  }

  // ---- open status ---------------------------------------------------------------------------------
  function openAt(store, type, r, cx) {
    var c = r.clock;
    return dayIntervals(store, type, c.ymd, cx).some(function (iv) { return c.minutes >= iv.start && c.minutes < iv.end; });
  }

  function isOpenAt(store, orderType, at) {
    var type = typeOf(orderType, 'OPENING');
    if (!online(store)) return false;
    var r = resolveNow(at, store.timezone);
    if (closureAt(store, r.instant, type)) return false;
    return openAt(store, type, r, cacheFor(store));
  }

  function openStatus(store, now, type) {
    var f = F();
    type = typeOf(type, 'OPENING');
    var cx = cacheFor(store);
    var r = resolveNow(now, store.timezone);
    var c = r.clock;
    if (!online(store)) {
      return { isOpen: false, label: 'Not taking online orders', closesAt: null, opensAt: null, reason: store.online && store.online.reason || null };
    }
    var closure = closureAt(store, r.instant, type);
    if (closure) return { isOpen: false, label: 'Temporarily closed', closesAt: null, opensAt: null, reason: closure.reason || null };
    var today = dayIntervals(store, type, c.ymd, cx);
    for (var i = 0; i < today.length; i++) {
      if (c.minutes >= today[i].start && c.minutes < today[i].end) {
        // Chain straight into an adjacent shift (e.g. 11–15 then 15–22).
        var end = today[i].end;
        for (var j = i + 1; j < today.length; j++) { if (today[j].start <= end) end = Math.max(end, today[j].end); }
        return { isOpen: true, label: 'Open until ' + f.hourLabel(end), closesAt: f.hm(end), opensAt: null, reason: null };
      }
    }
    for (var k = 0; k <= 7; k++) {
      var ymd = f.addDays(c.ymd, k);
      var ivs = dayIntervals(store, type, ymd, cx).filter(function (iv) { return k > 0 || iv.start > c.minutes; });
      if (k > 0) {
        // A later day's first opening is never yesterday's overnight shift running on (Phase 1).
        var wraps = businessShiftsOf(store, type, f.addDays(ymd, -1), bufferOf(store, type), cx).some(function (p) { return p.end > 1440; });
        ivs = ivs.filter(function (iv) { return iv.start > 0 || !wraps; });
      }
      if (ivs.length) {
        var at = ivs[0].start;
        var when = k === 0 ? '' : (k === 1 ? 'tomorrow ' : f.weekdayShort(f.weekdayOf(ymd)) + ' ');
        var label = 'Opens ' + when + f.hourLabel(at);
        return { isOpen: false, label: label, closesAt: null, opensAt: f.hm(at), opensOn: ymd, reason: null };
      }
    }
    return { isOpen: false, label: 'Closed', closesAt: null, opensAt: null, reason: null };
  }

  // ---- the order-later window and ASAP -------------------------------------------------------------
  function windowOf(store) {
    var s = (store && store.scheduling) || {};
    var later = !!s.orderLater, laterDays = later ? whole(s.orderLaterDays) : 0;
    var enabled = !!(s.preorder || later);
    var from = laterDays, to = s.preorder ? whole(s.preorderDays) : WINDOW_DAYS;
    return { enabled: enabled, reason: !enabled ? 'off' : (to <= from ? 'empty' : null), asap: !(later && laterDays >= 1), from: from, to: to };
  }

  function asap(store, orderType, now) {
    var type = typeOf(orderType, 'PICKUP');
    var r = resolveNow(now, store.timezone);
    var buffer = bufferOf(store, type);
    var out = { ok: false, reason: null, readyAt: r.instant + buffer * 60000 };
    function no(reason) { out.reason = reason; return out; }
    if (!online(store)) return no('offline');
    if (type !== 'DINEIN' && !windowOf(store).asap) return no('futureOnly');
    if (closureAt(store, r.instant, type)) return no('closure');
    if (!openAt(store, type, r, cacheFor(store))) return no(isHoliday(store, r.clock.ymd) ? 'holiday' : 'closed');
    if (closureAt(store, out.readyAt, type)) return no('closure');
    out.ok = true;
    return out;
  }

  // ---- runs and the slot grid ----------------------------------------------------------------------
  // Day ymd's own shifts merged for the grid: overlapping or at most `buffer` min apart.
  function mergedOf(store, type, ymd, buffer, cx) {
    var out = [];
    businessShiftsOf(store, type, ymd, buffer, cx).forEach(function (iv) {
      var last = out[out.length - 1];
      if (last && iv.start - last.end <= buffer) last.end = Math.max(last.end, iv.end);
      else out.push({ start: iv.start, end: iv.end });
    });
    return out;
  }
  // Whether an early period, moved after an evening period, forms one overnight business day.
  function continues(prev, next, buffer) {
    if (!prev || !next) return false;
    var start = next.start + 1440;
    return start - prev.end <= buffer && Math.max(prev.end, next.end + 1440) - prev.start < 1440;
  }
  // Runs belong to their configured business day; another weekday cannot extend or close them.
  function runsOf(store, type, ymd, buffer, cx) {
    return mergedOf(store, type, ymd, buffer, cx);
  }

  function dayLabelOf(k, ymd) {
    var f = F();
    return k === 0 ? 'Today' : (k === 1 ? 'Tomorrow' : f.weekdayShort(f.weekdayOf(ymd)) + ' ' + f.shortDate(ymd));
  }

  // The window's days with their slots. o = { stopAfter (stop once that many days have a slot: the Phase 1 view), align
  // ({ ymd, minutes }: findSlot's wall clock; today's runs are laid through it instead of from their own first time) }.
  function daysOf(store, orderType, now, o) {
    o = o || {};
    var f = F();
    var type = typeOf(orderType, 'PICKUP');
    if (type === 'DINEIN' || !online(store)) return [];
    var w = windowOf(store);
    if (!w.enabled) return [];
    var cx = cacheFor(store);
    var r = resolveNow(now, store.timezone), c = r.clock;
    var configuredStep = store.scheduling && store.scheduling.slotIntervalByType && Number(store.scheduling.slotIntervalByType[type]);
    var buffer = bufferOf(store, type), step = configuredStep >= 15 && configuredStep <= 240 ? configuredStep : STEP_MIN;
    // Newly offered times keep space after ASAP. An existing choice only needs its actual preparation buffer,
    // so it does not expire just because the new-time grid moved on.
    var lead = buffer * (!o.existingChoice && asap(store, type, r.instant).ok ? 2 : 1);
    var seen = {};
    var days = [], filled = 0;
    // The slots of `runs` (owned by day `owner`, local minutes from its midnight), from `from` minutes on, listed under
    // `day`.
    function grid(day, owner, runs, from) {
      runs.forEach(function (run) {
        var start = run.start + buffer;
        if (from !== null) start = Math.max(start, from);
        start = Math.ceil(start / ROUND_MIN) * ROUND_MIN;
        // findSlot: today's runs laid through the wanted minute (in the owner day's frame), never before their start.
        if (from !== null && o.align) start += (((f.dayDiff(owner, o.align.ymd) * 1440 + o.align.minutes - start) % step) + step) % step;
        for (var m = start; m <= run.end; m += step) {
          var ymd = f.addDays(owner, Math.floor(m / 1440)), wall = m % 1440;
          var at = cx.instant(ymd, wall);
          if (at === null || at <= r.instant || at - r.instant < buffer * 60000 || seen[at] || closureAt(store, at, type)) continue;
          seen[at] = true;
          if (run.end > 1440 && m >= 1440) day.lateNight = true;
          var value = ymd + ' ' + f.hm(wall) + ':00';
          day.slots.push({
            at: at, value: value, label: f.time12(wall), time: f.hm(wall), ymd: ymd, owner: day.ymd,
            dayLabel: ymd === day.ymd ? null : f.weekdayShort(f.weekdayOf(ymd)) + ' ' + Number(ymd.slice(8, 10)),
            asap: false, deliveryTime: value,
          });
        }
      });
    }
    for (var i = w.from; i < w.to; i++) {
      if (o.stopAfter && filled >= o.stopAfter) break;
      var ymd = f.addDays(c.ymd, i);
      var day = {
        ymd: ymd, key: ymd, date: ymd, label: dayLabelOf(i, ymd), holiday: isHoliday(store, ymd),
        holidayLabel: (store.holidayLabels && store.holidayLabels[ymd]) || null, slots: [],
      };
      if (i === 0) {
        // What is left of yesterday's run past midnight, on today's lead.
        var y = f.addDays(ymd, -1);
        grid(day, y, runsOf(store, type, y, buffer, cx).filter(function (run) { return run.end > 1440; }), c.minutes + 1440 + lead);
      }
      grid(day, ymd, runsOf(store, type, ymd, buffer, cx), i === 0 ? c.minutes + lead : null);
      day.slots.sort(function (a, b) { return a.at - b.at; });
      if (day.slots.length) filled += 1;
      days.push(day);
    }
    return days;
  }

  function slots(store, orderType, now) { return daysOf(store, orderType, now, {}); }

  function slotIn(days, value) {
    for (var i = 0; i < days.length; i++) {
      for (var j = 0; j < days[i].slots.length; j++) { if (days[i].slots[j].value === value) return days[i].slots[j]; }
    }
    return null;
  }
  function findSlot(store, orderType, value, now) {
    var f = F();
    var wc = f.parseWallClock(value);
    if (!wc) return null;
    var want = wc.ymd + ' ' + f.hm(wc.minutes) + ':00';
    var hit = slotIn(slots(store, orderType, now), want);
    if (hit || wc.minutes % ROUND_MIN) return hit;
    // Keep an existing choice off today's moving grid while the kitchen still has its preparation buffer.
    var today = daysOf(store, orderType, now, { stopAfter: 1, existingChoice: true, align: { ymd: wc.ymd, minutes: wc.minutes } })[0];
    return today && today.ymd === resolveNow(now, store.timezone).clock.ymd ? slotIn([today], want) : null;
  }

  // ---- the Phase 1 view (OO.model.pickupOptions) --------------------------------------------------------
  function pickupOptions(store, now, opts) {
    var f = F();
    opts = opts || {};
    var type = typeOf(opts.orderType, 'PICKUP') === 'DELIVERY' ? 'DELIVERY' : 'PICKUP'; // the app's APP_DELIVERY too (P2-30)
    var buffer = bufferOf(store, type);
    var asapLabel = f.rangeMins(buffer, buffer + 5);
    var out = { days: [], slots: [], asapLabel: asapLabel, cookingTimeMins: buffer };
    if (!online(store)) return out;
    var wanted = opts.days || 2;
    var r = resolveNow(now, store.timezone), c = r.clock;
    var lists = {}, order = [];
    function list(ymd) {
      if (!lists[ymd]) { lists[ymd] = []; order.push(ymd); }
      return lists[ymd];
    }
    var a = asap(store, type, r.instant);
    if (a.ok) {
      var z = f.zoned(new Date(a.readyAt), store.timezone);
      list(c.ymd).push({ value: 'ASAP', label: asapLabel, time: f.hm(z.minutes), asap: true, deliveryTime: z.ymd + ' ' + f.hm(z.minutes) + ':00' });
    }
    // One day more than wanted: a day's past-midnight slots land on the next calendar day.
    daysOf(store, type, r.instant, { stopAfter: wanted + 1 }).forEach(function (d) {
      d.slots.forEach(function (s) { list(s.ymd).push({ value: s.value, label: s.label, time: s.time, asap: false, deliveryTime: s.value }); });
    });
    order.sort().forEach(function (ymd) {
      if (out.days.length < wanted) out.days.push({ key: ymd, date: ymd, slots: lists[ymd], label: dayLabelOf(f.dayDiff(c.ymd, ymd), ymd) });
    });
    out.slots = out.days.length ? out.days[0].slots : [];
    return out;
  }

  // The instant (ms) a pickupOptions slot stands for: ASAP → `now`; a scheduled slot → its store-local deliveryTime.
  // Cart / checkout validate the lines against it (and the payload's deliver_time comes from the same deliveryTime).
  function slotInstant(store, slot, now) {
    var n = now == null ? Date.now() : (now instanceof Date ? now.getTime() : Number(now));
    if (!slot || slot.asap || !slot.deliveryTime) return n;
    var wc = F().parseWallClock(slot.deliveryTime);
    return wc ? F().instantOf(wc.ymd, wc.minutes, store && store.timezone) : n;
  }

  // ---- the checkout draft's time (P2-23) ------------------------------------------------------------------
  function chosen(store, orderType, draft, now) {
    var want = draft && draft.slot !== undefined && draft.slot !== null ? String(draft.slot) : '';
    if (!want || !online(store)) return null;
    var f = F();
    var type = typeOf(orderType, 'PICKUP');
    var r = resolveNow(now, store.timezone);
    if (want === 'ASAP') {
      var a = asap(store, type, r.instant);
      if (!a.ok) return null;
      var buffer = bufferOf(store, type);
      var z = f.zoned(new Date(a.readyAt), store.timezone);
      return {
        day: { key: r.clock.ymd, label: dayLabelOf(0, r.clock.ymd) },
        slot: { value: 'ASAP', label: f.rangeMins(buffer, buffer + 5), time: f.hm(z.minutes), asap: true, deliveryTime: z.ymd + ' ' + f.hm(z.minutes) + ':00' },
      };
    }
    var s = findSlot(store, type, want, r.instant);
    if (!s) return null;
    return {
      day: { key: s.owner, label: dayLabelOf(f.dayDiff(r.clock.ymd, s.owner), s.owner) },
      slot: { value: s.value, label: s.dayLabel ? s.label + ' · ' + s.dayLabel : s.label, time: s.time, asap: false, deliveryTime: s.deliveryTime },
    };
  }

  // ---- the week's hours (P3-FIX #30; see week in the header) -------------------------------------------------------
  var WEEK_DAYS = 7;
  // A day's shifts (by start) as a customer reads them: overlapping or touching ones as one (10am–4pm + 4pm–10pm).
  function joined(list) {
    var out = [];
    list.forEach(function (iv) {
      var last = out[out.length - 1];
      if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end);
      else out.push({ start: iv.start, end: iv.end });
    });
    return out;
  }
  function week(store, orderType, now) {
    var f = F();
    var type = typeOf(orderType, 'OPENING');
    var cx = cacheFor(store);
    var today = resolveNow(now, store.timezone).clock.ymd;
    var out = [];
    for (var i = 0; i < WEEK_DAYS; i++) {
      var ymd = f.addDays(today, i);
      var holiday = isHoliday(store, ymd);
      out.push({
        ymd: ymd, dow: f.weekdayOf(ymd), today: i === 0, holiday: holiday,
        holidayLabel: holiday ? (store.holidayLabels && store.holidayLabels[ymd]) || null : null,
        shifts: holiday ? [] : joined(shiftsOf(store, type, ymd, cx)),
      });
    }
    return out;
  }

  S.window = windowOf;
  S.asap = asap;
  S.slots = slots;
  S.isOpenAt = isOpenAt;
  S.findSlot = findSlot;
  S.openStatus = openStatus;
  S.pickupOptions = pickupOptions;
  S.slotInstant = slotInstant;
  S.chosen = chosen;
  S.week = week;
})();


// ===================== src/lib/qrcode.jsx =====================
// src/lib/qrcode.jsx: OO.qrcode (alias OO.lib.qrcode): the vendored barcode encoders (Batch 2 P3-16). The bundle
// imports no packages, so both encoders live here, small and pure: a QR Code encoder (ISO/IEC 18004:2015, byte mode)
// and a Code 128 set B encoder (ISO/IEC 15417), each returning the modules / bars a page draws as ONE SVG path. Both
// are written for this file from the standards (no third-party code is copied, so no licence header applies); the
// tests pin them to independent implementations (tests/qrcode-p316.test.mjs).
// Users: the scannable membership card (app-rewards.jsx, P3-17: customer_code as a QR, with a Code128-B toggle) and
// the booking success QR (app-booking.jsx, P3-19: reservation_id).
// Pure: no JSX, no document or OO.http at load time (the tests run every src/lib file raw in node:vm).
// Load order (build/concat.mjs): after the P2 engines and before store.jsx; it reads nothing at load time.
//
// REF (develop d298a7e): OnlineOrderMembershipCard.tsx draws customer_code (only when there is one) with qrcode.react
// 4.2 at level M, margin 1, and taps over to CustomerCodeBarcode.tsx (Code128-B, the same table as below, quiet zone
// 10); OnlineOrderTableBookingPage.tsx:645 and OnlineOrderBookingDetails.tsx draw reservation_id with qrcode.react's
// defaults (level L, no margin). qrcode.react picks numeric / alphanumeric mode where it can and raises the level while
// the version stays the same; this encoder always uses byte mode at the level asked for. A scanner reads the SAME
// text; only the pattern differs from REF's.
//
// API (pure; every call returns new arrays; text = a string; null / undefined = ''; anything else goes through String()):
//   qrMatrix(text, { ecc: 'L'|'M'|'Q'|'H' = 'M' }) → boolean[][]: the QR symbol, square (21–177 modules a side),
//       true = dark, NO quiet zone. The text is encoded as UTF-8 (a lone surrogate → U+FFFD, as TextEncoder does) in
//       ONE byte-mode segment, with no ECI, in the smallest version (1–40) that holds it at that level (the level is
//       never raised). The mask is the one with the lowest penalty (ISO/IEC 18004 §7.8.3, scored as BaconQrCode
//       2.0.8, a ZXing port, scores it; a tie → the lower mask): the symbol is Bacon's bit for bit, except where
//       Bacon's own table is wrong (version 4-Q). '' is a valid version-1 symbol. ecc: any case. Throws an Error with
//       .kind 'qrcode' and .code:
//         'QR_TOO_LONG' past version 40 (.bytes = the UTF-8 length, .max = 2953 / 2331 / 1663 / 1273 at L / M / Q / H);
//         'QR_ECC' when ecc is not L, M, Q or H (.ecc).
//   qrPath(matrix, { quiet = 4 }) → { size, d }: one SVG path for a viewBox `0 0 size size`, size = matrix.length +
//       2 × quiet (the light margin; 4 modules by the standard, REF's card draws 1 inside its own white padding). One
//       rectangle 'M{x} {y}h{n}v1h-{n}z' per horizontal run of dark modules. Fill it with the dark colour on a light
//       background, shape-rendering="crispEdges".
//   code128B(text) → number[]: the module widths (1–4), alternating bar / space, starting and ending with a bar: start
//       B (104), one symbol per character, the check symbol ((104 + Σ value × position) mod 103) and the stop pattern
//       (2331112). 11 × (characters + 3) + 2 modules; no quiet zone. Set B = ASCII 32–127 (DEL included, as the
//       standard and picqer have it; REF's CustomerCodeBarcode refuses DEL). '' encodes the empty symbol (start, check,
//       stop): show no code where there is none, as REF does. Throws 'CODE128_CHAR' (.char, .index = the UTF-16
//       index) on any other character (control characters, non-ASCII, emoji).
//   barsPath(widths, { height = 60, quiet = 10 }) → { width, d }: one SVG path for a viewBox `0 0 width height`,
//       width = Σ widths + 2 × quiet (Code 128 needs 10 light modules each side). One rectangle
//       'M{x} 0h{w}v{height}h-{w}z' per bar. Draw it with preserveAspectRatio="none" to stretch the bars to a box.
//   Options of both paths: quiet a finite number ≥ 0, height one > 0; anything else (negative, NaN) = the default.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};
  var api = (OO.qrcode = OO.lib.qrcode = OO.qrcode || {});

  function hasOwn(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function codeError(code, message, extra) {
    var e = new Error('Barcode: ' + message);
    e.kind = 'qrcode';
    e.code = code;
    for (var k in extra) if (hasOwn(extra, k)) e[k] = extra[k];
    return e;
  }
  function textOf(text) { return text == null ? '' : String(text); }
  // A size option: a finite number ≥ 0 (> 0 when positive), else the default.
  function sizeOpt(opts, key, fallback, positive) {
    var v = opts && opts[key] != null ? Number(opts[key]) : fallback;
    return isFinite(v) && (positive ? v > 0 : v >= 0) ? v : fallback;
  }

  // ---- UTF-8 ---------------------------------------------------------------------------------------------------------
  function utf8(s) {
    var out = [];
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
        var lo = s.charCodeAt(i + 1);
        if (lo >= 0xdc00 && lo <= 0xdfff) { c = 0x10000 + ((c - 0xd800) << 10) + (lo - 0xdc00); i++; }
      }
      if (c >= 0xd800 && c <= 0xdfff) c = 0xfffd; // a lone surrogate
      if (c < 0x80) out.push(c);
      else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
      else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
      else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    }
    return out;
  }

  // ---- Reed–Solomon over GF(256), x⁸ + x⁴ + x³ + x² + 1 (0x11D), generator roots α⁰ … α^(n−1) ----------------------
  var EXP = [], LOG = [], GEN = {};
  (function () {
    for (var i = 0, x = 1; i < 255; i++) {
      EXP[i] = x;
      LOG[x] = i;
      x <<= 1;
      if (x > 255) x ^= 0x11d;
    }
    for (i = 255; i < 510; i++) EXP[i] = EXP[i - 255];
  })();
  function mul(a, b) { return a && b ? EXP[LOG[a] + LOG[b]] : 0; }
  // The generator polynomial of degree n, highest power first (monic).
  function generator(n) {
    if (GEN[n]) return GEN[n];
    var g = [1];
    for (var i = 0; i < n; i++) {
      var next = [];
      for (var j = 0; j <= g.length; j++) next.push((j < g.length ? g[j] : 0) ^ (j > 0 ? mul(g[j - 1], EXP[i]) : 0));
      g = next;
    }
    return (GEN[n] = g);
  }
  // The n error correction codewords of a block: data(x) · xⁿ mod g(x).
  function eccOf(data, n) {
    var g = generator(n), rem = [], i, j;
    for (i = 0; i < n; i++) rem.push(0);
    for (i = 0; i < data.length; i++) {
      var f = data[i] ^ rem.shift();
      rem.push(0);
      for (j = 0; j < n; j++) rem[j] ^= mul(g[j + 1], f);
    }
    return rem;
  }

  // ---- symbol structure (ISO/IEC 18004:2015 Table 9: per level and version 1–40, index 0 unused) -------------------
  var LEVEL_BITS = { L: 1, M: 0, Q: 3, H: 2 }; // the format information's error correction bits
  var ECC_PER_BLOCK = {
    L: [0, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
    M: [0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
    Q: [0, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
    H: [0, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  };
  var BLOCKS = {
    L: [0, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
    M: [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
    Q: [0, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
    H: [0, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
  };

  // The alignment pattern centres (rows = columns) of a version (Annex E): 6, then evenly spaced up to size − 7.
  function alignments(version) {
    if (version === 1) return [];
    var count = Math.floor(version / 7) + 2, last = version * 4 + 10;
    var step = version === 32 ? 26 : Math.ceil((last - 6) / (count - 1) / 2) * 2; // version 32 breaks the even spacing
    var out = [6];
    for (var i = count - 2; i >= 0; i--) out.push(last - i * step);
    return out;
  }
  // The 15-bit format word (5 data bits + BCH(15,5), masked with 0x5412) and the 18-bit version word (BCH(18,6)).
  function formatWord(level, mask) {
    var data = (LEVEL_BITS[level] << 3) | mask, rem = data;
    for (var i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    return ((data << 10) | rem) ^ 0x5412;
  }
  function versionWord(version) {
    var rem = version;
    for (var i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    return (version << 12) | rem;
  }
  // Where format bit i (0 = least significant) goes: [x, y] for copy 1 (around the top-left finder) at i, copy 2 (under
  // the top-right finder, beside the bottom-left one) at 15 + i.
  function formatCells(size) {
    var a = [], b = [];
    for (var i = 0; i < 15; i++) {
      a.push(i < 6 ? [8, i] : i < 8 ? [8, i + 1] : i === 8 ? [7, 8] : [14 - i, 8]);
      b.push(i < 8 ? [size - 1 - i, 8] : [8, size - 15 + i]);
    }
    return a.concat(b);
  }

  // The function patterns of a version, row-major: dark (0 / 1) and fn (1 = a function module, never data or masked):
  // timing, finders with their separators, alignment patterns, the format areas (light until a mask is chosen), the
  // dark module and, from version 7, the version information.
  function skeleton(version) {
    var size = version * 4 + 17, dark = [], fn = [], i, j;
    for (i = 0; i < size * size; i++) { dark.push(0); fn.push(0); }
    function put(x, y, on) { var k = y * size + x; dark[k] = on ? 1 : 0; fn[k] = 1; }
    // A (2r + 1)² square centred on (cx, cy), dark by Chebyshev distance from the centre; clipped at the edges.
    function square(cx, cy, r, darkAt) {
      for (var dy = -r; dy <= r; dy++) {
        for (var dx = -r; dx <= r; dx++) {
          var x = cx + dx, y = cy + dy;
          if (x >= 0 && y >= 0 && x < size && y < size) put(x, y, darkAt(Math.max(Math.abs(dx), Math.abs(dy))));
        }
      }
    }
    function finder(d) { return d !== 2 && d !== 4; } // 3 × 3 core, light ring, dark ring, light separator
    function alignment(d) { return d !== 1; }
    for (i = 0; i < size; i++) { put(6, i, i % 2 === 0); put(i, 6, i % 2 === 0); }
    square(3, 3, 4, finder);
    square(size - 4, 3, 4, finder);
    square(3, size - 4, 4, finder);
    var pos = alignments(version), last = pos.length - 1;
    for (i = 0; i <= last; i++) {
      for (j = 0; j <= last; j++) {
        if ((i === 0 && (j === 0 || j === last)) || (i === last && j === 0)) continue; // under a finder
        square(pos[i], pos[j], 2, alignment);
      }
    }
    var cells = formatCells(size);
    for (i = 0; i < cells.length; i++) put(cells[i][0], cells[i][1], false);
    put(8, size - 8, true);
    if (version >= 7) {
      var word = versionWord(version);
      for (i = 0; i < 18; i++) {
        var on = (word >>> i) & 1, a = size - 11 + (i % 3), b = Math.floor(i / 3);
        put(a, b, on);
        put(b, a, on);
      }
    }
    return { size: size, dark: dark, fn: fn };
  }

  // Codeword capacity: the data modules a version leaves (counted on its skeleton, once), in whole codewords.
  var RAW = [];
  function rawCodewords(version) {
    if (!RAW[version]) {
      var fn = skeleton(version).fn, free = 0;
      for (var i = 0; i < fn.length; i++) if (!fn[i]) free++;
      RAW[version] = Math.floor(free / 8);
    }
    return RAW[version];
  }
  function dataCodewords(version, level) {
    return rawCodewords(version) - ECC_PER_BLOCK[level][version] * BLOCKS[level][version];
  }
  // The bits one byte segment needs: mode 0100, the count (8 bits to version 9, then 16), the bytes.
  function segmentBits(version, n) { return 4 + (version < 10 ? 8 : 16) + 8 * n; }
  function maxBytes(level) { return Math.floor((dataCodewords(40, level) * 8 - segmentBits(40, 0)) / 8); }

  // The final codeword sequence: the segment, terminator, padding to whole codewords and then 0xEC / 0x11, split into
  // blocks (the short blocks first; the long ones carry one more data codeword), each with its error correction, and
  // interleaved: data column by column, then error correction column by column.
  function codewords(bytes, version, level) {
    var capacity = dataCodewords(version, level), bits = [], data = [], i, j;
    function push(value, n) { for (var b = n - 1; b >= 0; b--) bits.push((value >>> b) & 1); }
    push(4, 4);
    push(bytes.length, version < 10 ? 8 : 16);
    for (i = 0; i < bytes.length; i++) push(bytes[i], 8);
    push(0, Math.min(4, capacity * 8 - bits.length));
    while (bits.length % 8) bits.push(0);
    for (i = 0; i < bits.length; i += 8) {
      var byte = 0;
      for (j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j];
      data.push(byte);
    }
    for (var pad = 0xec; data.length < capacity; pad ^= 0xec ^ 0x11) data.push(pad);

    var count = BLOCKS[level][version], eccLen = ECC_PER_BLOCK[level][version], total = rawCodewords(version);
    var shortData = Math.floor(total / count) - eccLen, shortBlocks = count - (total % count), blocks = [], at = 0;
    for (i = 0; i < count; i++) {
      var block = data.slice(at, at + shortData + (i < shortBlocks ? 0 : 1));
      at += block.length;
      blocks.push({ data: block, ecc: eccOf(block, eccLen) });
    }
    var out = [];
    for (i = 0; i <= shortData; i++) for (j = 0; j < count; j++) if (i < blocks[j].data.length) out.push(blocks[j].data[i]);
    for (i = 0; i < eccLen; i++) for (j = 0; j < count; j++) out.push(blocks[j].ecc[i]);
    return out;
  }

  // The data bits, most significant first, up and down the two-module columns from the bottom-right corner (skipping
  // the vertical timing column and every function module); the remainder modules stay light (they are masked too).
  function place(dark, fn, size, words) {
    var bit = 0, total = words.length * 8, upward = true;
    for (var right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (var v = 0; v < size; v++) {
        var y = upward ? size - 1 - v : v;
        for (var j = 0; j < 2; j++) {
          var k = y * size + right - j;
          if (fn[k]) continue;
          dark[k] = bit < total ? (words[bit >>> 3] >>> (7 - (bit & 7))) & 1 : 0;
          bit++;
        }
      }
      upward = !upward;
    }
  }

  // The eight data masks (x = column, y = row): a module is inverted where its mask is true.
  var MASKS = [
    function (x, y) { return (x + y) % 2 === 0; },
    function (x, y) { return y % 2 === 0; },
    function (x, y) { return x % 3 === 0; },
    function (x, y) { return (x + y) % 3 === 0; },
    function (x, y) { return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; },
    function (x, y) { return ((x * y) % 2) + ((x * y) % 3) === 0; },
    function (x, y) { return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; },
    function (x, y) { return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; },
  ];
  function masked(raw, fn, size, level, mask) {
    var m = raw.slice(), test = MASKS[mask], x, y, k;
    for (y = 0; y < size; y++) {
      for (x = 0; x < size; x++) {
        k = y * size + x;
        if (!fn[k] && test(x, y)) m[k] ^= 1;
      }
    }
    var word = formatWord(level, mask), cells = formatCells(size);
    for (k = 0; k < cells.length; k++) m[cells[k][1] * size + cells[k][0]] = (word >>> (k % 15)) & 1;
    return m;
  }

  // The mask penalty of a whole symbol (format information included), as BaconQrCode 2.0.8 (a ZXing port) scores it:
  //   N1: 3 + (n − 5) per run of n ≥ 5 same-colour modules in a row or column;
  //   N2: 3 per 2 × 2 block of one colour (overlapping blocks all count);
  //   N3: 40 per 1011101 (dark-light-dark-dark-dark-light-dark) in a row or column with 4 light modules right after or
  //       right before it, all 4 inside the symbol (the quiet zone does not count);
  //   N4: 10 per whole 5 % the dark share is away from 50 % (floor(|dark / total − 0.5| × 20), in doubles as Bacon).
  var FINDER_LIKE = [1, 0, 1, 1, 1, 0, 1];
  function penalty(m, size) {
    var score = 0, a, b, k, run, prev, dark = 0;
    for (var dir = 0; dir < 2; dir++) {
      for (a = 0; a < size; a++) {
        run = 0;
        prev = -1;
        for (b = 0; b < size; b++) {
          k = dir === 0 ? a * size + b : b * size + a;
          if (m[k] === prev) run++;
          else {
            if (run >= 5) score += run - 2;
            run = 1;
            prev = m[k];
          }
        }
        if (run >= 5) score += run - 2;
      }
    }
    for (a = 0; a < size - 1; a++) {
      for (b = 0; b < size - 1; b++) {
        k = a * size + b;
        if (m[k] === m[k + 1] && m[k] === m[k + size] && m[k] === m[k + size + 1]) score += 3;
      }
    }
    for (a = 0; a < size; a++) {
      for (b = 0; b < size; b++) {
        if (finderLike(m, size, a * size + b, b, 1)) score += 40; // along row a, from column b
        if (finderLike(m, size, b * size + a, b, size)) score += 40; // along column a, from row b
      }
    }
    for (k = 0; k < size * size; k++) dark += m[k];
    return score + Math.floor(Math.abs(dark / (size * size) - 0.5) * 20) * 10;
  }
  // 1011101 starting at module k (at position `at` along its line, `step` apart), with 4 light modules after or before.
  function finderLike(m, size, k, at, step) {
    if (at + 6 >= size) return false;
    for (var i = 0; i < 7; i++) if (m[k + i * step] !== FINDER_LIKE[i]) return false;
    return lightRun(m, size, k, at, step, 7) || lightRun(m, size, k, at, step, -4);
  }
  function lightRun(m, size, k, at, step, from) {
    if (at + from < 0 || at + from + 3 >= size) return false;
    for (var i = 0; i < 4; i++) if (m[k + (from + i) * step] !== 0) return false;
    return true;
  }

  function levelOf(opts) {
    var ecc = opts && opts.ecc != null ? String(opts.ecc).toUpperCase() : 'M';
    if (!hasOwn(LEVEL_BITS, ecc)) {
      throw codeError('QR_ECC', 'the QR error correction level must be L, M, Q or H, not ' + String(opts.ecc), { ecc: opts.ecc });
    }
    return ecc;
  }

  function qrMatrix(text, opts) {
    var level = levelOf(opts), bytes = utf8(textOf(text)), version = 0;
    for (var v = 1; v <= 40 && !version; v++) if (segmentBits(v, bytes.length) <= dataCodewords(v, level) * 8) version = v;
    if (!version) {
      var max = maxBytes(level);
      throw codeError('QR_TOO_LONG', 'the text is ' + bytes.length + ' bytes in UTF-8; a level ' + level + ' QR code holds at most ' + max, { bytes: bytes.length, max: max });
    }
    var sk = skeleton(version), size = sk.size, raw = sk.dark.slice(), best = null, bestScore = Infinity;
    place(raw, sk.fn, size, codewords(bytes, version, level));
    for (var mask = 0; mask < 8; mask++) {
      var m = masked(raw, sk.fn, size, level, mask), score = penalty(m, size);
      if (score < bestScore) { best = m; bestScore = score; }
    }
    var out = [];
    for (var y = 0; y < size; y++) {
      var row = [];
      for (var x = 0; x < size; x++) row.push(best[y * size + x] === 1);
      out.push(row);
    }
    return out;
  }

  function qrPath(matrix, opts) {
    var quiet = sizeOpt(opts, 'quiet', 4), n = matrix ? matrix.length : 0, d = '';
    for (var y = 0; y < n; y++) {
      var row = matrix[y] || [];
      for (var x = 0; x < n; x++) {
        if (!row[x]) continue;
        var from = x;
        while (x + 1 < n && row[x + 1]) x++;
        var len = x - from + 1;
        d += 'M' + (from + quiet) + ' ' + (y + quiet) + 'h' + len + 'v1h-' + len + 'z';
      }
    }
    return { size: n + 2 * quiet, d: d };
  }

  // ---- Code 128 (ISO/IEC 15417): the 107 symbol patterns, element widths bar first; 103–105 start A / B / C, 106 stop.
  var C128 = (
    '212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 ' +
    '123122 123221 223211 221132 221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 ' +
    '232121 111323 131123 131321 112313 132113 132311 211313 231113 231311 112133 112331 132131 113123 113321 133121 ' +
    '313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 314111 221411 431111 111224 ' +
    '111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 ' +
    '111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 ' +
    '114311 411113 411311 113141 114131 311141 411131 211412 211214 211232 2331112'
  ).split(' ');
  var START_B = 104, STOP = 106;

  function code128B(text) {
    var s = textOf(text), values = [START_B], sum = START_B, i, j;
    for (i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c < 32 || c > 127) {
        var ch = c >= 0xd800 && c <= 0xdbff && i + 1 < s.length ? s.substr(i, 2) : s.charAt(i);
        throw codeError('CODE128_CHAR', 'Code 128 set B has no ' + JSON.stringify(ch) + ' (at ' + i + ')', { char: ch, index: i });
      }
      values.push(c - 32);
      sum += (c - 32) * (i + 1);
    }
    values.push(sum % 103, STOP);
    var widths = [];
    for (i = 0; i < values.length; i++) {
      var p = C128[values[i]];
      for (j = 0; j < p.length; j++) widths.push(p.charCodeAt(j) - 48);
    }
    return widths;
  }

  function barsPath(widths, opts) {
    var height = sizeOpt(opts, 'height', 60, true), quiet = sizeOpt(opts, 'quiet', 10), x = quiet, d = '';
    for (var i = 0; widths && i < widths.length; i++) {
      var w = Number(widths[i]) || 0;
      if (i % 2 === 0 && w > 0) d += 'M' + x + ' 0h' + w + 'v' + height + 'h-' + w + 'z';
      x += w;
    }
    return { width: x + quiet, d: d };
  }

  api.qrMatrix = qrMatrix;
  api.qrPath = qrPath;
  api.code128B = code128B;
  api.barsPath = barsPath;
})();


// ===================== src/lib/platform.jsx =====================
// src/lib/platform.jsx: OO.platform (alias OO.lib.platform): the native app bridge of the customer app (Batch 2 P4-09;
// REF shared/utils/webviewBridge.ts, haptic.ts, context/PlatformContext.tsx and mobile/FlutterUserSync.tsx, inventory D
// §0.2 and the "Mobile app" appendix, A G18), and later the region config (P5-08: dial code, Places country, fallback
// time zone, currency; TWD in whole dollars).
//
// The shell. The SIPO apps are Flutter flutter_inappwebview WebViews (chicking_app, chicking, corianders, novelty-sweets,
// indian_dreams, …); REF also speaks React Native's postMessage. The shells set window.isFlutterWebView = true (at
// document start, or from onLoadStart / onLoadStop in the older ones), register the JavaScript handler 'getUserId' (its
// argument int.parse'd as the customer_code for get_customer_by_id_to_app?customer_code=; none registers
// 'hapticFeedback' yet: flutter_inappwebview 6 answers an unregistered handler with null) and, after every page load and
// on a token refresh, call window.updateFcmToken("<token>"), polling up to 10 s for the function. A shell whose token
// isn't known yet sends the text "null" (Dart's interpolation of a null String).
// Detection runs at CALL time (a shell may set its flag after this file ran) and is never sticky: no localStorage
// `isMobileApp` / `sipo_is_mobile_app` and no `?isMobileApp=` / `?flutter=` flags (REF's sticky app mode; plan §3 P4
// "Mode stays host-decided"; no shell's start URL carries one). OO.mode stays host-decided: the shell adds the bridge,
// the token and the app flag on the wire, never the app UI or the APP_* domains.
//
// OO.platform:
//   bridge() → 'flutter' | 'react-native' | null   window.flutter_inappwebview with a callHandler function first, else
//       window.ReactNativeWebView with a postMessage function (REF getFlutterBridge / getReactNativeBridge).
//   inShell() → bool   the page runs inside a native shell: window.isFlutterWebView === true, or a bridge (REF
//       isLikelyMobileWebView less its localStorage and query flags).
//   isMobileApp() → bool   REF's isMobileApp less the sticky localStorage: app mode (OO.mode 'app': the app host or the
//       /endpoint/customer-app mount) or the shell. What the auth and booking calls send as isMobileApp (the backend
//       records the customer's source 'app' / 'web' from it). Checkout's isMobileApp is the payload ctx's (app mode,
//       checkout.jsx) plus the contributor below.
//   userIdOf(who) → string | null   REF getPreferredUserIdentifier: the first of who.customerCode, customer_code,
//       customerId, customer_id, uid that is a non-blank string or a number, trimmed (a string or number `who` is the
//       id itself); null when none.
//   identity(hotel?) → { customerCode, customerId, uid } | null   the signed-in customer as REF's FlutterUserSync reads
//       it: customerCode = the wallet's customer_code ?? the session customer's customerCode, customerId = the wallet's
//       customer_id ?? the customer's customerId (OO.store.wallet(hotel): the settled get_customer_wallet_points body;
//       default hotel: the current one), uid = the customer's uid. null when signed out (nothing is sent on sign-out).
//   getUserId(who?, { hotel, timeoutMs }?) → Promise<string | null>   tells the shell who is signed in:
//       callHandler('getUserId', id), or (React Native) postMessage(JSON.stringify({ handlerName: 'getUserId',
//       payload: id })), id = userIdOf(who === undefined ? identity(hotel) : who). Resolves the id once the shell took it
//       (the call settled without an error), or null: nothing to send, outside the shell or without a bridge, the
//       shell's handler threw (an old shell int.parse's a non-numeric id), or timeoutMs passed (default 3000; the call
//       keeps going and a late answer still counts). Never rejects. Once per id: the id the shell last took is not sent
//       again and a call in flight for the same id is joined; another id is sent (A → B → A sends three times, where
//       REF's per-id set would leave the shell on B).
//   useUserSync(hotel) → id | null   (a React hook; React is read lazily) REF's <FlutterUserSync/>: the signed-in id
//       (userIdOf(identity(hotel))), re-read on every store change, and getUserId(id) whenever it changes. Mounted once
//       by the app frame (src/app.jsx) in every mode, as REF mounts it in its app and table shells.
//   haptic(kind = 'medium') → bool   REF haptic(): 'selection' | 'light' | 'medium' | 'heavy' | 'success' | 'warning'
//       → the shell's 'hapticFeedback' handler (fire and forget) plus navigator.vibrate(REF's pattern: 5 / 10 / 20 /
//       35 ms, [15, 60, 15], [20, 80, 20]) where the WebView has it. Outside the shell, or an unknown kind: nothing
//       (false). REF vibrates in every browser; the web stays silent here.
//   updateFcmToken(token) → string   keeps the shell's FCM token for this browser session: in memory and in
//       sessionStorage 'oo-fcm-token-v1' (a reload inside the WebView keeps it; storage is first read by fcmToken(),
//       never at load). The token is trimmed; a non-string, a blank, 'null' / 'undefined', more than TOKEN_MAX (255)
//       characters (online_orders.fcm_token is a varchar(255)) or a space or control character inside is NO token, and
//       clears the kept one (REF clears on ''). Returns the token kept ('' for none). Deviations from REF, which keeps
//       any text (in localStorage, for good): the text "null" would reach online_orders.fcm_token, and the server would
//       queue an FCM push that fails on every accept / reject.
//   window.updateFcmToken(token)   installed at load, where the shells look for it: updateFcmToken(token); returns
//       nothing.
//   fcmToken() → string   the token kept this session; '' when none.
//   TOKEN_MAX   255, the longest token kept.
//   __reset()   tests: a page reload: forgets the token in memory (sessionStorage keeps it) and what the shell was told.
// The checkout payload (OO.payload.contribute key 'platform', registered at load; a root hook, after the P2 engines'):
//   - Outside the shell with no token kept it adds nothing: the body stays exactly what it was, byte for byte (the
//     parity fixtures).
//   - In the shell: isMobileApp true (REF: the shell is the app on any host). order_type and wallet_order_type stay the
//     mode's (only app mode sends APP_* / ONLINE_APP_*: the lines, pricetags and wallet ratios were priced in the
//     mode's domain). Checkout and the POS read neither key; the server stores fcmToken.
//   - A token kept: fcmToken = the token (REF's key; payload.jsx sends '' otherwise). The server keeps it in
//     online_orders.fcm_token and pushes "Order Accepted" / "Order Rejected" to it, for a non-table order.
//   It never writes isMobileApp false.
// Pure: no JSX, no document or OO.http at load time (the tests run every src/lib file raw in node:vm); at load it only
// installs window.updateFcmToken and registers the payload contributor. OO.store, OO.useStore and React are read at call
// time (store.jsx loads after this file).
// Load order (build/concat.mjs): after payload.jsx, the P2 engines and qrcode.jsx, before store.jsx.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  function own(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

  // ---- constants ----------------------------------------------------------------------------------------------
  // The shells' JavaScript handler names (REF webviewBridge.ts / haptic.ts; the Flutter shells register getUserId).
  var USER_ID_HANDLER = 'getUserId';
  var HAPTIC_HANDLER = 'hapticFeedback';
  // REF haptic.ts PATTERNS: navigator.vibrate's ms, per kind.
  var HAPTICS = { selection: 5, light: 10, medium: 20, heavy: 35, success: [15, 60, 15], warning: [20, 80, 20] };
  var USER_ID_TIMEOUT_MS = 3000;
  var TOKEN_KEY = 'oo-fcm-token-v1'; // sessionStorage
  var TOKEN_MAX = 255; // online_orders.fcm_token is a varchar(255): a longer token would be cut (non-strict MySQL)
  var TOKEN_TEXT = /^[\x21-\x7e]+$/; // FCM / APNs tokens are printable ASCII without spaces

  // ---- the shell (detected at call time, never sticky) ----------------------------------------------------------
  function flutterBridge() {
    var b = window.flutter_inappwebview;
    return b && typeof b.callHandler === 'function' ? b : null;
  }
  function nativeBridge() {
    var b = window.ReactNativeWebView;
    return b && typeof b.postMessage === 'function' ? b : null;
  }
  function bridge() {
    if (flutterBridge()) return 'flutter';
    return nativeBridge() ? 'react-native' : null;
  }
  function inShell() { return window.isFlutterWebView === true || bridge() !== null; }
  function isMobileApp() { return OO.mode === 'app' || inShell(); }

  // One message to the shell → Promise<boolean>: true once the shell took it (Flutter: callHandler settled without an
  // error; React Native: postMessage returned), false otherwise. Never rejects.
  function send(name, payload) {
    var flutter = flutterBridge();
    if (flutter) {
      return new Promise(function (resolve) {
        var answer;
        try { answer = flutter.callHandler(name, payload); } catch (e) { resolve(false); return; }
        Promise.resolve(answer).then(function () { resolve(true); }, function () { resolve(false); });
      });
    }
    var rn = nativeBridge();
    if (rn) {
      try {
        rn.postMessage(JSON.stringify({ handlerName: name, payload: payload }));
        return Promise.resolve(true);
      } catch (e) {
        return Promise.resolve(false);
      }
    }
    return Promise.resolve(false);
  }

  // ---- who is signed in (REF FlutterUserSync) -------------------------------------------------------------------
  function idText(v) {
    if (typeof v !== 'string' && typeof v !== 'number') return null;
    if (typeof v === 'number' && !isFinite(v)) return null;
    return String(v).trim() || null;
  }
  function userIdOf(who) {
    if (who === null || who === undefined) return null;
    if (typeof who !== 'object') return idText(who);
    var order = [who.customerCode, who.customer_code, who.customerId, who.customer_id, who.uid];
    for (var i = 0; i < order.length; i++) {
      var id = idText(order[i]);
      if (id) return id;
    }
    return null;
  }
  // a ?? b (REF): b only when a is null / undefined.
  function either(a, b) {
    if (a !== null && a !== undefined) return a;
    return b === undefined ? null : b;
  }
  function identity(hotel) {
    var store = OO.store;
    var session = store && typeof store.session === 'function' ? store.session() : null;
    var customer = session && session.customer;
    if (!customer) return null;
    var wallet = typeof store.wallet === 'function' ? store.wallet(hotel) : null;
    return {
      customerCode: either(wallet ? wallet.customer_code : null, customer.customerCode),
      customerId: either(wallet ? wallet.customer_id : null, customer.customerId),
      uid: either(customer.uid, null),
    };
  }

  // What the shell was told: the id it took last, and the call in flight (its id; seq orders the calls, so only the
  // latest one updates `id`).
  var told = { id: null, pendingId: null, pending: null, seq: 0 };

  function within(promise, ms) {
    return new Promise(function (resolve) {
      var timer = setTimeout(function () { resolve(null); }, ms);
      function done(v) { clearTimeout(timer); resolve(v); }
      promise.then(done, function () { done(null); });
    });
  }

  function getUserId(who, opts) {
    var o = opts || {};
    var id = userIdOf(who === undefined ? identity(o.hotel) : who);
    if (!id || !bridge()) return Promise.resolve(null);
    if (told.id === id && told.pendingId === null) return Promise.resolve(id);
    var call = told.pending;
    if (told.pendingId !== id) {
      var seq = ++told.seq;
      call = send(USER_ID_HANDLER, id).then(function (ok) {
        if (seq === told.seq) {
          told.pendingId = null;
          told.pending = null;
          if (ok) told.id = id;
        }
        return ok;
      });
      told.pendingId = id;
      told.pending = call;
    }
    var ms = typeof o.timeoutMs === 'number' && o.timeoutMs >= 0 ? o.timeoutMs : USER_ID_TIMEOUT_MS;
    return within(call.then(function (ok) { return ok ? id : null; }), ms);
  }

  // The hook: the signed-in id is a string (a primitive), so OO.useStore re-renders only when it changes.
  function useUserSync(hotel) {
    var React = window.React;
    var id = OO.useStore(function () { return userIdOf(identity(hotel)); });
    React.useEffect(function () {
      if (id) getUserId(id);
    }, [id]);
    return id;
  }

  // ---- haptics (REF haptic.ts) ----------------------------------------------------------------------------------
  function haptic(kind) {
    if (OO.appPreferences && !OO.appPreferences.haptics()) return false;
    var k = kind === undefined || kind === null ? 'medium' : String(kind);
    if (!own(HAPTICS, k) || !inShell()) return false;
    send(HAPTIC_HANDLER, k);
    var nav = window.navigator;
    if (nav && typeof nav.vibrate === 'function') {
      try { nav.vibrate(HAPTICS[k]); } catch (e) { /* not allowed here */ }
    }
    return true;
  }

  // ---- the FCM token (REF PlatformContext setFcmToken) ----------------------------------------------------------
  var token = null; // null = not read yet; '' = none
  function sessionStore() { try { return window.sessionStorage || null; } catch (e) { return null; } }
  function cleanToken(v) {
    if (typeof v !== 'string') return '';
    var t = v.trim();
    if (!t || t === 'null' || t === 'undefined' || t.length > TOKEN_MAX || !TOKEN_TEXT.test(t)) return '';
    return t;
  }
  function fcmToken() {
    if (token === null) {
      var s = sessionStore();
      var kept = null;
      if (s) {
        try { kept = s.getItem(TOKEN_KEY); } catch (e) { kept = null; }
      }
      token = cleanToken(kept);
    }
    return token;
  }
  function updateFcmToken(v) {
    token = cleanToken(v);
    var s = sessionStore();
    if (s) {
      try {
        if (token) s.setItem(TOKEN_KEY, token);
        else s.removeItem(TOKEN_KEY);
      } catch (e) { /* in memory only */ }
    }
    return token;
  }

  function reset() {
    token = null;
    told = { id: null, pendingId: null, pending: null, seq: 0 };
  }

  var P = (OO.platform = OO.lib.platform = OO.platform || {});
  P.bridge = bridge;
  P.inShell = inShell;
  P.isMobileApp = isMobileApp;
  P.userIdOf = userIdOf;
  P.identity = identity;
  P.getUserId = getUserId;
  P.useUserSync = useUserSync;
  P.haptic = haptic;
  P.updateFcmToken = updateFcmToken;
  P.fcmToken = fcmToken;
  P.TOKEN_MAX = TOKEN_MAX;
  P.__reset = reset;

  // The shells call this after every page load (and on a token refresh).
  window.updateFcmToken = function (t) { updateFcmToken(t); };

  // ---- the checkout payload: isMobileApp and fcmToken, as REF sends them (header) --------------------------------
  if (OO.payload && typeof OO.payload.contribute === 'function') {
    OO.payload.contribute('platform', {
      root: function () {
        var shell = inShell();
        var t = fcmToken();
        if (!shell && !t) return null; // outside the app: the body stays as it was
        var out = {};
        if (shell) out.isMobileApp = true;
        if (t) out.fcmToken = t;
        return out;
      },
    });
  }
})();


// ===================== src/lib/kiosk.jsx =====================
// Kiosk-only device/session rules. Menu, pricing, coupons and checkout remain shared.
// The WebView supplies /{store}?kiosk_id={terminal}. Only that device mapping is
// persistent; customer state belongs to this tab and is cleared between visitors.
(function () {
  'use strict';
  var OO = window.OO;
  var DEVICE_KEY = 'oo-kiosk-device-v1', VISIT_KEY = 'oo-kiosk-visit-v1';
  var IDLE_MS = 60000, WARNING_MS = 30000, SUCCESS_MS = 30000;
  var memoryVisit = null, busy = false, revision = 0, listeners = [];
  function enabled() { return OO.mode === 'kiosk'; }
  function storage(session) { try { return window[session ? 'sessionStorage' : 'localStorage'] || null; } catch (e) { return null; } }
  function read(key, session) {
    try { var s = storage(session); return s ? JSON.parse(s.getItem(key) || 'null') : null; } catch (e) { return null; }
  }
  function write(key, value, session) {
    try { var s = storage(session); if (s) { if (value === null) s.removeItem(key); else s.setItem(key, JSON.stringify(value)); } } catch (e) { /* memory still works */ }
  }
  function keyOf(key) { return String(key || '').trim().toLowerCase(); }
  function cleanId(id) {
    return typeof id === 'string' && id.trim() && id.trim().length <= 255 && !/[\u0000-\u001f\u007f]/.test(id) ? id.trim() : null;
  }
  function route() { try { return OO.router.current(); } catch (e) { return null; } }
  function terminal(hotel) {
    if (!enabled() || !keyOf(hotel)) return null;
    // Studio previews never place orders and must not overwrite the WebView's device mapping.
    if (OO.whitelabel && OO.whitelabel.isPreview()) return 'STUDIO-PREVIEW';
    var r = route(), supplied = r && keyOf(r.hotel) === keyOf(hotel) && r.query && Object.prototype.hasOwnProperty.call(r.query, 'kiosk_id');
    var devices = read(DEVICE_KEY, false) || {};
    if (supplied) {
      var id = cleanId(r.query.kiosk_id);
      // A malformed explicit ID must never fall back to another terminal.
      if (!id) return null;
      devices[keyOf(hotel)] = id;
      write(DEVICE_KEY, devices, false);
      return id;
    }
    return cleanId(devices[keyOf(hotel)]);
  }
  function visit() { return memoryVisit || read(VISIT_KEY, true); }
  function publish(v) {
    memoryVisit = v;
    write(VISIT_KEY, v, true);
    revision++;
    listeners.slice().forEach(function (fn) { fn(); });
  }
  function currentVisit(hotel) {
    var v = visit();
    return v && keyOf(v.hotel) === keyOf(hotel) && v.terminal === terminal(hotel) ? v : null;
  }
  function orderType(hotel) {
    var v = currentVisit(hotel);
    return v && (v.type === 'DINEIN' || v.type === 'TAKEAWAY') ? v.type : null;
  }
  function label(hotel) { return orderType(hotel) === 'DINEIN' ? 'Dine-in' : 'Takeaway'; }
  function presentation(store) {
    var p = store && store.whitelabel && store.whitelabel.kiosk || {};
    return Object.assign({ variant: 'normal', imageFit: 'cover', logoPreset: 'store' }, p);
  }
  function logo(store) {
    var p = presentation(store);
    return p.logo || (p.logoPreset === 'chicking' ? OO.asset('online-order/kiosk/chicking-logo.png') : store && store.logoUrl) || null;
  }
  function style(store) {
    var defaults = { primary: '#000000', onPrimary: '#FFFFFF', header: '#000000', onHeader: '#FFFFFF', canvas: '#EFF4F4',
      surface: '#FFFFFF', ink: '#000000', muted: '#4A5656', accent: '#000000', onAccent: '#FFFFFF', font: 'Poppins', headFont: 'Poppins', headWeight: 700, radius: 'xl' };
    return OO.theme.toCssVars(Object.assign(defaults, store && store.whitelabel ? store.theme : {}), 'kiosk');
  }
  function pending() {
    var lo = OO.store && OO.store.get().lastOrder, v = visit();
    return busy || !!(v && v.unconfirmed) || !!(lo && lo.clearCartOnSuccess && !lo.settledAt && (!v || v.resolvedOrder !== String(lo.orderId)));
  }
  function clearCustomer() {
    if (!OO.store) return;
    OO.store.cartClear();
    OO.store.logout();
    OO.store.clearCheckout({ keepContact: false });
    OO.store.clearLastOrder();
    OO.store.clearTable();
    // The shared payment return context contains no customer details, but belongs
    // to the previous purchase and must not be used by the next visitor.
    try { var s = storage(true); if (s) s.removeItem('sipo_payment_context'); } catch (e) { /* unavailable storage */ }
  }
  function prepare(hotel, now) {
    if (!enabled()) return;
    var v = visit(), id = terminal(hotel), at = now == null ? Date.now() : now;
    if (pending()) return; // even a changed setup link cannot discard an in-flight payment
    if (!v || keyOf(v.hotel) !== keyOf(hotel) || v.terminal !== id || (at - Number(v.activity || 0) >= IDLE_MS && !pending())) {
      // A terminal/store link is configuration. Never carry a customer's cart
      // or login from another terminal into it.
      clearCustomer();
      publish({ hotel: hotel, terminal: id, type: null, activity: at });
    }
  }
  function choose(hotel, type) {
    if (!enabled() || !terminal(hotel) || ['DINEIN', 'TAKEAWAY'].indexOf(type) < 0 || pending()) return false;
    var v = currentVisit(hotel) || { hotel: hotel, terminal: terminal(hotel) };
    publish(Object.assign({}, v, { type: type, activity: Date.now() }));
    // Both kiosk choices are immediate in-store orders. The shared engine's
    // non-delivery draft prices them in domain KIOSK; only the wire type differs.
    OO.store.setCheckout({ orderType: 'PICKUP', day: null, slot: null, chosenAt: null });
    return true;
  }
  function activity(at) {
    var v = visit();
    if (v) { memoryVisit = Object.assign({}, v, { activity: at == null ? Date.now() : at }); write(VISIT_KEY, memoryVisit, true); }
  }
  function reset(hotel) {
    if (pending()) return false;
    clearCustomer();
    publish({ hotel: hotel, terminal: terminal(hotel), type: null, activity: Date.now() });
    OO.router.go(OO.router.href('menu', null, { hotel: hotel, query: { kiosk_id: terminal(hotel) } }), { replace: true });
    return true;
  }
  function hold(value) { busy = !!value; activity(); }
  function submission(value) {
    var v = visit();
    if (v) publish(Object.assign({}, v, { unconfirmed: !!value, activity: Date.now() }));
  }
  function resolved(orderId) {
    var v = visit();
    if (v) publish(Object.assign({}, v, { resolvedOrder: String(orderId), unconfirmed: false, activity: Date.now() }));
    busy = false;
  }
  function preflight(hotel, choice) {
    if (!enabled()) return Promise.resolve(null);
    if (!terminal(hotel) || !orderType(hotel)) return Promise.reject(new Error('Choose dine-in or takeaway on this kiosk first.'));
    return OO.api.getRestaurant(hotel, { fresh: true }).then(function (raw) {
      var store = OO.model.normalizeStore(raw);
      if (!store || !store.kiosk || !store.kiosk.active) throw new Error(store && store.kiosk && store.kiosk.reason || 'This kiosk is not accepting orders. Please ask a staff member.');
      var methods = store.paymentMethods || [], found = !choice;
      methods.forEach(function (m) {
        (m.gateways || m.types || []).forEach(function (p) {
          if (choice && choice.kind === 'gateway' && String(p.gatewayId) === String(choice.gatewayId) && p.gateway === choice.gateway) found = true;
          if (choice && choice.kind === 'counter' && String(p.typeId) === String(choice.typeId)) found = true;
        });
      });
      if (!found) throw new Error('Payment options have changed. Please return to the menu and try again.');
      return store;
    });
  }
  function redirect(r) {
    if (!enabled() || !r) return null;
    var v = visit(), lo = OO.store && OO.store.get().lastOrder;
    if (r.hotel && v && keyOf(v.hotel) === keyOf(r.hotel)) {
      if (v.unconfirmed && r.page !== 'checkout') return OO.router.href('checkout', null, { hotel: r.hotel });
      if (lo && keyOf(lo.hotel) === keyOf(r.hotel) && lo.clearCartOnSuccess && !lo.settledAt && v.resolvedOrder !== String(lo.orderId)
          && ['payment-callback', 'order-status'].indexOf(r.page) < 0) {
        return OO.router.href('payment-callback', null, { hotel: r.hotel, query: { order_id: lo.orderId } });
      }
    }
    if (!r.hotel && r.page === 'payment-callback') {
      var hotel = lo && lo.hotel || v && v.hotel;
      if (hotel) return OO.router.href('payment-callback', null, { hotel: hotel, query: r.query });
    }
    if (r.hotel && ['menu', 'cart', 'checkout', 'upsell', 'offers', 'payment-callback', 'order-status', 'legal', 'ui-gallery'].indexOf(r.page) < 0) {
      return OO.router.href('menu', null, { hotel: r.hotel });
    }
    return null;
  }
  function asap(store, now) {
    var f = OO.format, r = OO.model._.resolveNow(now, store.timezone), minutes = Math.max(0, Number(store.cookingTimeMins) || 0);
    var at = r.instant + minutes * 60000, z = f.zoned(new Date(at), store.timezone);
    return { value: 'ASAP', asap: true, label: f.rangeMins(minutes, minutes + 5), time: f.hm(z.minutes),
      deliveryTime: z.ymd + ' ' + f.hm(z.minutes) + ':00', at: at, ymd: z.ymd, owner: r.clock.ymd, dayLabel: null };
  }
  function idleState(last, now, protectedPayment) {
    var elapsed = Math.max(0, now - last);
    return protectedPayment ? { warning: false, expired: false, remaining: 30 }
      : { warning: elapsed >= WARNING_MS, expired: elapsed >= IDLE_MS, remaining: Math.max(0, Math.ceil((IDLE_MS - elapsed) / 1000)) };
  }
  OO.kiosk = { enabled: enabled, terminal: terminal, cleanId: cleanId, visit: currentVisit, orderType: orderType, label: label, presentation: presentation, logo: logo, style: style,
    prepare: prepare, choose: choose, reset: reset, activity: activity, pending: pending, hold: hold, submission: submission, resolved: resolved,
    redirect: redirect, asap: asap, preflight: preflight, idleState: idleState, IDLE_MS: IDLE_MS, WARNING_MS: WARNING_MS, SUCCESS_MS: SUCCESS_MS,
    subscribe: function (fn) { listeners.push(fn); return function () { listeners = listeners.filter(function (f) { return f !== fn; }); }; },
    revision: function () { return revision; },
  };
})();


// ===================== src/lib/ordering-qr.jsx =====================
// Ordering QRs use the online checkout, with a separate cart/draft and an explicit server-validated context.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function round(v) { return Math.round((v + Number.EPSILON) * 100) / 100; }
  function params(search) { try { return new URLSearchParams(search || ''); } catch (e) { return { get: function () { return null; } }; } }
  var initial = params(window.location && window.location.search);
  var enabled = !OO.mode || OO.mode === 'online';
  var token = enabled ? initial.get('ordering_qr') : null;
  var preview = token ? initial.get('ordering_qr_preview') : null;
  var first = OO.router && OO.router.current ? OO.router.current() : null;
  var hotel = first && first.hotel || initial.get('online_key') || null;
  var seeded = false;
  function active() { return token !== null; }
  function forHotel(key) { return active() && (!hotel || sameStore(hotel, key)); }
  function receiptPage() { var r = OO.router && OO.router.current && OO.router.current(); return r && ['payment-callback', 'order-status', 'track', 'review'].indexOf(r.page) >= 0; }
  function sameStore(a, b) { return String(a || '').toLowerCase() === String(b || '').toLowerCase(); }
  function query(key, q) {
    var out = Object.assign({}, q || {});
    if (!active() || (hotel && key && !sameStore(hotel, key))) return out;
    if (!Object.prototype.hasOwnProperty.call(out, 'ordering_qr')) out.ordering_qr = token;
    if (preview && !Object.prototype.hasOwnProperty.call(out, 'ordering_qr_preview')) out.ordering_qr_preview = preview;
    return out;
  }
  function api(body) { return query(hotel, body); }
  function scopeChanged(to) {
    try {
      var url = new URL(to, window.location.href);
      return String(url.searchParams.get('ordering_qr') || '') !== String(token || '')
        || String(url.searchParams.get('ordering_qr_preview') || '') !== String(preview || '');
    } catch (e) { return false; }
  }
  function decorateStore(store, profile) {
    if (!profile) return store;
    var types = arr(store.orderTypes).filter(function (t) { return arr(profile.order_types).indexOf(t) >= 0; });
    return Object.assign({}, store, {
      orderingQr: profile, orderTypes: types, modesLabel: types.join(' · '), couponsEnabled: false,
      loyaltyProductLevel: false, banners: [], popups: [], appSlides: [],
      settings: Object.assign({}, store.settings, { orderPrompt: types.length > 1 && store.settings && store.settings.orderPrompt }),
    });
  }
  function seedDraft(store) {
    var qr = store && store.orderingQr;
    if (!qr || !OO.store) return;
    var draft = OO.store.get().checkout || {}, patch = {};
    if (arr(store.orderTypes).indexOf(draft.orderType) < 0 && store.orderTypes.length) patch.orderType = store.orderTypes[0];
    var a = qr.delivery_address;
    if (a && (qr.address_mode === 'locked' || (!seeded && !draft.address))) {
      patch.address = a.line; patch.zipcode = a.zipcode || ''; patch.lat = num(a.lat); patch.lng = num(a.lng);
    }
    seeded = true;
    Object.keys(patch).forEach(function (k) { if (patch[k] === draft[k]) delete patch[k]; });
    if (Object.keys(patch).length) OO.store.setCheckout(patch);
  }
  function select(tags, store, orderType) {
    var qr = store && store.orderingQr;
    if (!qr || !qr.pricetag_id) return null;
    var id = Number(qr.pricetag_id);
    var row = arr(tags && tags.rows).filter(function (r) { return Number(r.tagId) === id; })[0];
    var prices = tags && tags.prices && tags.prices[id];
    return { tagId: id, name: row && row.name || 'QR price', domain: OO.pricetags.domainOf(orderType, 'online'),
      active: !!(tags && tags.prices), override: false, membership: false,
      prices: prices || { product: {}, size: {}, modifier: {}, combo: {} } };
  }
  function modifiers(mods, units) {
    return arr(mods).reduce(function (n, m) { return n + num(m.price) * Math.max(0, num(m.count) - num(m.free)) * units; }, 0);
  }
  function tier(rule, subtotal) {
    if (!rule || rule.type !== 'tiers') return null;
    var spend = Math.max(0, Math.round(subtotal * 100)), selected = null, threshold = -1;
    arr(rule.tiers).forEach(function (row) {
      var minimum = Math.round(num(row.minimum_spend) * 100);
      if (minimum <= spend && minimum > threshold) {
        threshold = minimum; selected = { minimum_spend: minimum / 100, percent: num(row.percent) };
      }
    });
    return selected;
  }
  function discount(rule, eligible, subtotal) {
    var base = Math.max(0, Math.round(eligible * 100));
    if (Math.round(subtotal * 100) < Math.round(num(rule && rule.minimum_spend) * 100)) return 0;
    var type = rule && rule.type, selected = tier(rule, subtotal);
    var value = type === 'tiers' ? num(selected && selected.percent) : num(rule && rule.value);
    var amount = type === 'percent' || type === 'tiers' ? Math.round(base * Math.min(100, Math.max(0, value)) / 100)
      : type === 'fixed' ? Math.round(Math.max(0, value) * 100) : 0;
    var cap = Math.round(num(rule && rule.maximum_discount) * 100);
    return Math.min(base, cap > 0 ? Math.min(cap, amount) : amount) / 100;
  }
  function saving(lines, store, subtotal) {
    var qr = store && store.orderingQr;
    if (!qr) return 0;
    var eligible = subtotal;
    if (store.applyDiscountToModifiers !== true) {
      eligible -= arr(lines).reduce(function (n, line) {
        return n + (line.kind === 'combo' ? arr(line.picks).reduce(function (v, p) {
          return v + modifiers(p.modifiers, num(p.count) * num(line.qty));
        }, 0) : modifiers(line.modifiers, num(line.qty)));
      }, 0);
    }
    return discount(qr.discount, round(eligible), subtotal);
  }
  OO.orderingQr = { active: active, token: function () { return token; }, preview: function () { return preview; },
    forHotel: forHotel, receiptPage: receiptPage,
    query: query, api: api, scopeChanged: scopeChanged, decorateStore: decorateStore, seedDraft: seedDraft,
    select: select, discount: discount, saving: saving, tier: tier,
    locked: function (store) { return !!(store && store.orderingQr && store.orderingQr.address_mode === 'locked'); } };
  if (active() && window.addEventListener) window.addEventListener('popstate', function () {
    if (scopeChanged(window.location.href) && window.location.reload) window.location.reload();
  });
})();


// ===================== src/lib/store.jsx =====================
// src/lib/store.jsx: OO.store, the small observable app store, plus the React hooks OO.useStore, OO.useAsync and
// OO.useHotel, and the non-hook data core OO.data (loadHotel / activateHotel, the load-error states, the 15-minute
// store re-read, ensureFreshMenu, the session reconcile). No JSX. React is only read lazily (window.React) inside the
// hooks, so node:vm tests can run everything else.
//
// State = {
//   hotel:    null | 'bennys'                       the store the cart belongs to (OO.store.setHotel)
//   cart:     { lines: CartLine[], coupon: Coupon | null[, pricing] }   persisted PER HOTEL under 'oo-cart-v1-{hotel}';
//             mode qr: per (store, table) in 'oo-table-cart-v1-{hotel}' (P4-04, "Table carts" below); {hotel} is the
//             store's key lower-cased (POLISH-6: one slot whatever the URL's spelling; "Store keys" below)
//             pricing (P2-02): the pricetag record the lines are priced with (OO.pricetags.priceMenu's `pricetag`);
//             the key is absent at the base prices (every fixture store). See "Pricing" below.
//   checkout: { name, phone, email, notes, terms, marketing[, marketingChosen (local choice or preserved old value;
//               see below)], orderType, day, slot, chosenAt (ms: when the customer chose
//               that time; P2-23's 15-minute re-prompt), paymentMethod, paymentChoice (P2-27: the payment choice id,
//               checkout-payment.jsx), address, zoneId, zipcode, lat, lng (its Places pick), hotel (the store this
//               draft is for) }; a sign-out resets all but orderType and hotel ("Sign-out" below)   'oo-checkout-v1'
//   session:  null | { customer (OO.model Customer), customToken }                                  'oo-session-v1'
//   app:      { read: { [hotel]: [ids] } }   (P3-15: the Batch 1 `redeemed` taps are gone; P4-16: so is its device-wide
//             `branch`, the router keeps the branch per chain: "Chains" below)                        'oo-app-v1'
//   lastOrder: null | { orderId, hotel, paymentMethod, placedAt, total, clearCartOnSuccess, couponTxn,
//               couponReleased, settledAt, snapshot }; a sign-out drops it unless its online payment is still in
//               flight ("Sign-out" below)                                                          'oo-last-order-v1'
//   table:    null | { id, name, hotel, guests, excludedCategoryIds, expiresAt } (mode qr only)       'oo-table-v1'
//             the table context (P4-01; "The table context" below)
//   wallets:  { ['{hotel}|{uid}']: wallet | null }   (P2-02, memory only) the signed-in customer's settled
//             get_customer_wallet_points reads (null = failed / module off); OO.store.wallet(hotel) reads it. The hotel
//             part is lower-cased, as everywhere a store's key is compared (W5-FIX #2: "Store keys" below)
//   priceNotice: null | { seq, hotel, keys }          (P2-02, memory only) "Prices updated": raised ONCE per reprice
//             that moved a price (the lines' keys); a page shows it and acknowledges it (OO.store.ackPriceNotice)
//   points:   { ['{hotel}|{uid}']: points }   (P2-07, memory only, never persisted: REF CartContext walletPointsToUse)
//             the wallet points (type 1) the signed-in customer chose to use at that store, a whole number > 0; the
//             hotel part is lower-cased (online keys are case-insensitive). What the order takes is OO.wallet.inUse's
//             (at most the slider's max, nothing while it is off): the choice itself stays as made. Only the account
//             that chose them can use them: logout forgets every entry, a session for another account keeps only its
//             own, and cartClear / settleOrder drop the store's (an order went: REF CLEAR_CART resets the points).
// }
// The checkout draft is stored as { v: 4, …draft } (the version stays out of the state). Terms start ticked and Pay
// needs them. The online offers checkbox starts ticked unless the store sets a different default; the default is never saved
// as a customer's choice. The optional marketingChosen marker records a local checkbox change, or conservatively keeps
// an older stored boolean whose origin is unknown: an old false might be an untick. It does not prove active consent.
// Before v: 2 a saved marketing true could have been the old default, so it still migrates to false. Before v: 3 a saved
// terms false could have been the old default, so it still migrates to true. A v: 3 terms untick remains untouched.
// The marketing value and marker stay with the contact after an order, a store move and reload, but sign-out or a kiosk's
// "start again" clears them. This local marker is not a server-side consent audit record.
// The cart key is computed on EVERY hotel change. That fixes Next's key frozen at module load (§8, gotcha 22).
// The last hotel ('oo-hotel-v1') lets the root /payment-callback find its store. All storage access is
// try/catch'd, and corrupt JSON or bad lines fall back to empty values.
// lastOrder.snapshot is what the order-status page renders before the POS has made the ticket (bug 4; checkout
// builds it at Pay). null | { v: 1, storeName, orderType ('PICKUP' | 'DELIVERY' | 'DINEIN'), when { asap, label,
// at (ISO | null) }, lines [{ name, qty, mods, total }] (≤ 50), rows [{ key, label, text, description? }], total, address | null,
// table | null, paymentLabel }. setLastOrder and the loader sanitise it the same way: only these keys are kept (no
// contact data can ride along), a value of the wrong type is dropped field by field, strings are cut to 200
// characters (charge descriptions to 1,000), lines to 50, and anything but an object with v === 1 becomes null. A record without one keeps none.
// The table context (mode qr only; Batch 2 P4-01; REF TableSideContext, inventory D §1.1). src/pages/table.jsx checks a
// table (it belongs to this store, it is active, the store takes table orders) and keeps the result here with
// setTable; OO.pages.Table.context(hotel) is tableFor(hotel). state.table = null | { id (a whole number > 0), name
// (string | null), hotel (the store's key as given), guests (the party size, 1–999 | null: P4-03), excludedCategoryIds
// (the table group's, whole numbers > 0), expiresAt (device ms) }, persisted as { v: 1, …context } in 'oo-table-v1'.
//   * It lives TABLE_TTL_MS (2 h): setTable stamps expiresAt = now + 2 h when none is given and never keeps a later one
//     (a check renews it). An expired or unusable stored context is dropped when this file loads (memory and storage);
//     one that expires while the page is open reads as none (table(), tableFor()) until table.jsx clears it.
//   * Outside mode qr there is no table (tables are qr-only since P4-14): state.table stays null, setTable and
//     set({ table }) keep nothing, and 'oo-table-v1' is neither read nor written (another mode on the same origin, a
//     dev mount, leaves it alone).
//   * It belongs to ONE store: setHotel to another store drops it (keys compared in any case: "Store keys" below).
//   * tableFor(hotel) is the context that applies to `hotel` now: mode qr, that store, unexpired, and not superseded by
//     the URL: on that store's table entry for ANOTHER table (/{hotel}/table/{id}, OO.router.current()) it is null,
//     so nothing orders for the old table while table.jsx checks the new one.
// Table carts (mode qr, P4-04; REF CartContext: one sipo_cart with its tableId, a table cart lives 2 h, a table change
// clears it). A cart belongs to (store, table): the table of the store's held context (cartTable). It is kept in ONE
// slot per store, 'oo-table-cart-v1-{hotel}' = { v: 1, table, updatedAt (device ms), lines, coupon[, pricing] },
// rewritten on every change (updatedAt with it), and read back only for the same table within TABLE_TTL_MS (2 h) of
// that write (REF checks on load too). Another table brings that table's cart (from the slot: empty, and its first
// write replaces the old table's: REF's clear); no context (none yet, cleared, expired) is an empty cart in memory and
// no write, so a context that comes back for the same table within the 2 h finds its cart again. settleOrder clears
// another store's slot. Modes online and app keep 'oo-cart-v1-{hotel}' exactly as before.
// The menu's channel (P4-02; REF caches its menu per channel, menu:{id}:{table|online}). A hotel's cache entry holds one
// menu at a time, tagged with the channel it was read for: 'online' outside mode qr (OO.api.getMenu(hotel), as ever);
// in mode qr 'table:{id}' for the table context that applies to the store (tableFor): OO.api.getMenu(hotel, { tableId })
// (body table_id + the With-Table-Id header: table prices, the table group's exclusions; the real adapter reads again
// an answer that isn't menu_type 'Table Menu'), in the TABLE_SIDE domain (OO.model.domain in mode qr). Without a
// context mode qr reads NO menu (loadHotel resolves { store, menu: null }): a table menu is read for a checked table
// only, and never while the store's table ordering is off (Store.tableOrdering false; the backend builds a table menu
// per request for such a store): a context kept from before is dropped instead (loadHotel, ensureFreshMenu), no menu.
// loadHotel, peekHotel, ensureFreshMenu and useHotel's key follow the channel that applies at the call: another
// channel's menu is dropped and read again, never served (peekHotel answers null until this channel's menu is in). A
// table menu's excluded_category_ids become the context's (E19's until then) and feed OO.upsell.exclude (P2-19's
// hook). A table menu's 404 (the table stopped being this store's active one) drops the context (table.jsx checks the
// table again and gates) and resolves no menu: never "store not found".
// QR-OFF: a table menu answered "this store takes no table orders" (BE-LIMITS: 200 {status:'False', reason:
// 'table_ordering_off'}; both adapters reject it with kind 'tableOff', tableOffAnswer) by a store read here as taking
// them (a store read up to 15 min ago): the store's table ordering is OFF here at once, before anything else (the cached
// Store gets tableOrdering false and its table-side switches enabled false, its watchers are told: table.jsx's
// "Ordering not available" gate, and no table check or table menu read follows), the context goes, the menu is none
// (loadHotel and ensureFreshMenu resolve null: never a load error), and the store is read again (rereadStore: the
// server's view replaces it, e.g. switched back on) at most once per STORE_REREAD_MS per store for this answer, so a
// store read saying "on" while get_menu says "off" can't loop.
//
// OO.store API:
//   get() → State.   set(patch | fn(state) → patch) → State (shallow merge, persist, notify). A `table` in the
//                                            patch is sanitised like setTable's.
//   subscribe(fn) → unsubscribe.
//   setHotel(hotel, { remember = true })     switches the cart to that hotel's persisted cart and remembers the hotel
//                                            (useHotel switches at once and remembers only after a successful load).
//                                            A different hotel drops the table context. A store that isn't a fixture
//                                            store (OO.api.isFixtureFor) also sheds the fixture adapter's local state:
//                                            a session it made (OO.data.dropFixtureSession).
//   lastHotel() → string | null              the hotel to forward to from the root /payment-callback.
//   cartAdd(line) · cartSetQty(key, qty) · cartInc(key) · cartDec(key) · cartRemove(key) · cartReplace(key, line)
//   cartClear() · setCoupon(coupon | null)   all via OO.cart (pure); each returns the new lines. cartAdd / cartReplace
//                                            price the line at the cart's pricing first (P2-02 "reprice before add":
//                                            an item sheet opened before a tag change adds at today's tag prices; at
//                                            the order's moment, W5-FIX #1: "Order moment" below);
//                                            cartClear keeps the cart's pricing record and drops the store's points
//                                            in use (state.points).
//   cartReprice(menu) → [keys]               OO.cart.reprice on the cart (today's prices from `menu`, AT THE CART'S
//                                            PRICING: OO.pricetags.priceMenu(menu, cart.pricing), so a page reprice
//                                            with a base menu never undoes a tag) and at the order's moment (W5-FIX
//                                            #1: "Order moment" below); the keys whose price changed (the
//                                            cart pages say "Prices updated"; it raises priceNotice too). No write
//                                            when none.
//   syncPricing(hotel?) → [keys]             re-selects the active pricetag (Pricing below) and re-prices the cart when
//                                            the record it is priced with changes; the keys whose price moved (they
//                                            raise priceNotice). [] while the pricing isn't known yet or nothing
//                                            changed. Runs by itself on every input change; pages need not call it.
//   ackPriceNotice(seq?) → bool              clears priceNotice (only that one when seq is given); false when none.
//   wallet(hotel?) → wallet | null           the signed-in customer's settled get_customer_wallet_points body for
//                                            `hotel` (default: the current one); null when signed out, unread, failed.
//                                            Any spelling of the store's key finds it (W5-FIX #2, "Store keys").
//   walletPoints(hotel?) → number            (P2-07) the points the signed-in customer chose to use at `hotel`
//                                            (default: the current one): state.points' entry, else 0 (signed out too).
//   setWalletPoints(points, hotel?) → number (P2-07) chooses them: a whole number (floored); 0 or less forgets the
//                                            choice. Signed out: nothing is kept (0). The number kept is returned.
//   cartLines() · cartCount() · cartSubtotal()
//   setCheckout(patch) · clearCheckout({ keepContact = true })   keepContact keeps name, phone, email, marketing (and
//                                            WL-OPT's marketingChosen when set) and the delivery address + area
//                                            (address, zoneId); the rest resets.
//                                            The draft is stamped with its store: when setHotel moves to ANOTHER store
//                                            its store-specific fields (terms, notes, day, slot, zoneId) reset and the
//                                            contact + address stay — even across a reload. A draft with no stamp
//                                            adopts the current store as it is.
//   setSession({ customer, customToken }) · logout() · session()   logout also flushes OO.http's in-memory read cache
//                                            (wallet, loyalty and the other member reads; C §3.1 "Logout") and
//                                            forgets every wallet read (state.wallets) and every points choice
//                                            (state.points); a session for another account keeps only its own.
//                                            P3-27 (member-cache hygiene): logout, and a session for ANOTHER account
//                                            (A → B; a guest signing in drops nothing), drop every member cache FIRST,
//                                            before the state changes: OO.http's read cache, the wallet reads in flight
//                                            or settled (loadWallet's memo: a read for the old account that lands
//                                            later is never settled, as settleWallet checks the account), the last
//                                            pricetag Selection per store (selection() is null until the new session's
//                                            is known: never the member price of the account that left), the
//                                            post-order refreshes made (walletAfterOrder) and (P3-FIX #10) the order
//                                            lists read (OO.data.ownOrders).
//                                            P3-LOGOUT: the same two (logout; a session for another account or none,
//                                            A → B / A → null) also make this device forget the customer: the checkout
//                                            draft, lastOrder (unless its payment is in flight), the booking hand-off
//                                            and the age confirmations, in the session's own update ("Sign-out" below).
//                                            A guest signing in, or the same account again, clears nothing.
//   markRead(ids) · markAllRead(ids) · readIds()   (per hotel)
//                                            P3-15: the Batch 1 local Redeem taps (redeem(rewardId), redeemedIds(),
//                                            app.redeemed) are gone: Redeem adds a reward line to the cart
//                                            (pages/app-rewards.jsx, OO.wallet.addReward) and checkout redeems it. A
//                                            `redeemed` map still in storage is dropped when the app state loads.
//   chain() · branch() · setBranch(key, chain?) · cartOf(hotel)   mode app's chain and branch (P4-16): "Chains" below.
//   setLastOrder(order) · clearLastOrder()   the order is kept as given; only its snapshot is sanitised (above).
//   settleOrder(orderId) → bool              the payment callback's "payment confirmed" (payment_update 200 / 202).
//                                            When lastOrder is that order and carries clearCartOnSuccess (an online
//                                            payment: checkout kept the cart, coupon and draft for a retry), it clears
//                                            that hotel's cart, its points in use and the checkout draft (contact kept)
//                                            and turns the flag off: exactly once, so a refresh (202 again) never
//                                            empties a cart started since. Anything else is a no-op (false).
//                                            POLISH-6: that store's cart in any spelling ("Store keys").
//   releaseCouponTxn(orderId) → txn | null   a declined / cancelled online payment releases the coupon its order
//                                            claimed (lastOrder.couponTxn, from placeOrder's coupon_txn) with
//                                            OO.api.invalidateCoupon — ONCE: it is marked released here, before the
//                                            call, so a refresh never sends it again. null when there is nothing to send.
//   setTable({ id, name, hotel, guests, excludedCategoryIds, expiresAt }) → the context kept | null (always null outside
//                                            mode qr) · clearTable() · table() → the context unless expired | null ·
//                                            tableFor(hotel) → the context that applies to hotel | null (see "The table
//                                            context" above) · TABLE_TTL_MS = 7200000 (2 h)
//   __reload()                               re-reads storage, and re-arms the once-per-page-load session reconcile
//                                            (tests: a page reload)
//
// Hooks (React 18):
//   OO.useStore(selector?) → selector(state). Re-renders on change. Shallow-equal results are reused, so an inline
//       selector returning an object is safe.
//   OO.useAsync(fn, deps) → { data, error, loading, reload }
//   OO.useHotel(hotel, { withMenu = true }) → { loading, error, kind, notFound, store, menu, reload }
//       store and menu are NORMALISED (OO.model). They come from an in-memory cache per hotel, so moving between
//       pages doesn't refetch. error = loadHotel's rejection and kind = OO.data.errorKind(error) (null while loading
//       or ready); OO.data.loadErrorState(error) names the page to show (src/pages/not-found.jsx renders it).
//       notFound = error.status 404 (or no hotel): an unknown store, and also a store this host doesn't serve (the
//       facade's kind 'notLive' keeps the 404 for Batch-1 callers; check kind first). A store that is only
//       unavailable (network, 408 / 425 / 429, ≥ 500, an unusable 200 = 502) is never notFound. reload() re-runs the
//       load (force). Once loaded, it activates the hotel: applies the theme, loads its Google fonts, remembers the
//       hotel and switches the cart key, then starts the session reconcile (OO.data.reconcileSession: a live store
//       only, once per page load). ?theme=bennys|nonnas|harbour (OO.router.current().query.theme) overrides the store
//       theme with OO.theme.PRESETS. Mode app (P4-16): the theme is the hotel's CHAIN MASTER's ("Chains" below): it
//       reads the master's store too (store only, beside the hotel's: the page never waits for it) and themes with it
//       once it is in.
//       The menu follows the clock: every minute and when the page becomes visible it adopts OO.data.refreshMenu's
//       menu when availability changed (a new object only then).
//       While mounted it watches its hotel (OO.data.watchHotel), which keeps the 15-minute store re-read running and
//       hands it the cache's new store / menu when the re-read or ensureFreshMenu replaced one (the theme follows).
//       menu is AT THE CART'S PRICING (P2-02; OO.data.pricedMenu): the cache's menu priced with the record the cart
//       is priced with, so cards, the item sheet, new lines and checkout's ctx.pricetag (menu.pricetag) all carry the
//       active tag. The SAME object comes back while neither the cached menu nor the record changes; at the base
//       prices (every fixture store) it is the cached menu itself. It re-renders when the cart's pricing or a wallet
//       read changes, and its minute tick also runs syncPricing (a tag's own hours, an ASAP order's clock).
//   OO.useChain(key, { origin, byDistance, theme }) → { loading, error, kind, notFound, chain, master, reload } (mode app,
//       P4-16: "Chains" below). The chain of `key` (the route's chain: any member's key; '' / null: nothing, not loading)
//       through OO.data.loadChain, and its master's store (master: the normalised Store of chain.master.key, store only;
//       null until it is in, when its read failed, or when the master has no key). chain is the cached Chain at once on
//       a later mount; with origin ({ lat, lng }) the stores carry their distances, byDistance (with an origin) sorts
//       them nearest first (OO.model.normalizeChain over the cached body). loading: no chain yet; error / kind /
//       notFound: the read failed and no chain is cached (notFound: kind 'notFound', a key no store has). reload()
//       reads again (fresh). theme: true applies the master's theme while mounted (activateHotel with { cart: false }:
//       the cart and the remembered store stay): the pages with no branch (Home's chain home, Select store) theme
//       themselves this way; a page with a branch gets it from OO.useHotel. Built on OO.useAsync.
//
// Pricing (P2-02; B §1.3 "Pricetag reprice", REF CartContext syncCartItemForPricetag + PricetagContext). The active
// pricetag is OO.pricetags.select over the store's pricetags (get_pricetag_order + _prices, loadPricetags) for:
//   order type  the checkout draft's while this store offers it here (cart.jsx orderTypeOf: PICKUP / DELIVERY, no
//               DELIVERY on a real store without the Maps key), else the first offered one;
//   time        the draft slot's instant (cart.jsx draftSlot: the draft's day / slot while still offered, else the first;
//               ASAP = OO.data.now()), read only when a tag of the domain has hours of its own; a table order's is
//               OO.data.now() whatever the draft names (POLISH-3, p5-followups #18: it is ASAP, with no time to choose);
//   mode, table OO.mode, and a table order for this hotel (mode qr, state.table of this store, any case) → 'TABLE SIDE';
//   member      OO.pricetags.memberOf(the signed-in customer's wallet), null when signed out.
// It is KNOWN only once the hotel's store, menu and pricetags have loaded and, for a signed-in customer, that
// customer's wallet read has settled: a member price is never guessed. Until then the cart keeps the prices it has,
// and new lines / pages are priced with the record the cart carries (persisted with it), so a reload never re-prices
// back and forth. Once known, its record (priceMenu(menu, selection).pricetag) is compared with the cart's: when it
// differs (another tag, other prices, the tag gone), the lines are re-priced with OO.pricetags.repriceCart's menu
// (OO.cart.reprice), the record is stored with the cart, and priceNotice is raised when a line's price moved. An
// unchanged record changes nothing (no write, no notice), so nothing loops. It is re-checked on every change of the
// hotel, the draft's order type / day / slot, the session, the table or a wallet read; when the pricetags or a
// wallet arrive; when the store is re-read or the menu re-fetched; and on useHotel's minute tick.
//
// Order moment (W5-FIX #1). Every re-price of the cart (cartReprice, the Pricing re-price, cartAdd / cartReplace) and
// the free units of a cart read from storage (loadCart sanitises with no menu: free 0; OO.cart.sanitize runs again
// right after setHotel / __reload when the hotel's menu is cached, else as soon as it loads) use the menu at the
// ORDER'S moment, the one the cart's line checks use (cart.jsx slotCheck): OO.data.menuAt for the draft's slot
// (slotInstantOf: OO.schedule.chosen, else the draft's or the first offered slot; ASAP = now; a table order: now,
// POLISH-3, as the cart's and the table checkout's checks at its ASAP table slot) in the order type's
// domain, through OO.cart.atMoment (that moment's availability, the given menu's prices). A modifier group offered only
// at the chosen slot so keeps its free-allowance units after a reload: "(Free)" stays, and the membership's charged
// modifier base isn't overstated. Without the hotel's menu (a hand-built one) the given menu is used as it is.
//
// Store keys (W5-FIX #2). The pages call in with the ROUTE's hotel (/Bennys), the engines with the store's online_key
// (Store.onlineKey 'bennys': OO.wallet, OO.membership); online keys are case-insensitive, so both name one store. The
// pricing and wallet state is keyed and compared by the lower-cased key: the state.wallets and state.points entries,
// the wallet and pricetag reads (loadWallet, loadPricetags, peekPricetags: one read per store whatever the case, sent
// with the key as given), the last Selection (selection), syncPricing's hotel and pricedMenu's cart record (the
// current hotel's whatever the case). The hotel cache and state.hotel itself keep the route's spelling.
// POLISH-6 (p5-followups #22): so do the cart slots ('oo-cart-v1-{key}', 'oo-table-cart-v1-{key}': the key lower-cased,
// as the router compares keys), so ?store=BENNYS-BRUNSWICK and bennys-brunswick share one cart; cartOf and settleOrder
// take the current hotel's cart in any spelling. A slot an earlier build wrote under another spelling MOVES into the
// store's slot as it is read, only while that one holds no lines (the spelling read first, then any other): never
// merged over a cart, so no item is lost, and its old key goes once the move is written. settleOrder also drops the
// slot under the order's own spelling (a cart kept there before is the order's: its paid lines never come back).
//
// Sign-out (P3-LOGOUT, owner decision 2026-10-05; deliberately NOT REF, which keeps the checkout contact draft and the
// last order). A shared device forgets the customer who signs out. logout(), and a session for another account or none
// (setSession A → B or A → null: the signed-in account leaves), also do the following, in the session's own update (one
// write, one emit: no subscriber sees the session gone and the old draft still there):
//   * The checkout draft resets to its defaults except the order type and the store it is stamped with
//     (SIGNED_OUT_KEEP). The contact (name, phone, email), the marketing tick, the delivery address and its area and
//     Places pick (address, zoneId, zipcode, lat, lng), the notes, an untick of the terms (back to the ticked default,
//     TERMS), the chosen time (day, slot, chosenAt) and the payment choice (paymentMethod, paymentChoice) all go.
//   * lastOrder goes, and with it this device's ownership of that order (OO.api.isOwnOrder; the guest's "Your latest
//     order"), UNLESS its online payment is still in flight (paymentInFlight: ONLINE_PAYMENT with clearCartOnSuccess,
//     not settled; payment-callback.jsx orderIdOf's `awaiting`). An order paid at the gateway but not confirmed here
//     yet must not be stranded: the callback's settleOrder still clears its cart, and its order page still opens on
//     this device. So that lastOrder stays as it is and only the rest goes. The cost: a declined, cancelled or
//     abandoned online payment (nothing marks one on lastOrder) survives a sign-out, until the next order replaces it
//     or it settles.
//   * What the pages keep for this tab (sessionStorage) goes: the booking track hand-off (OO.pages.Booking.handoff
//     .clear(): 'oo-booking-track-v1') and every store's age-gate confirmation (OO.pages.ItemSheet.age.forgetAll():
//     'oo-age-v1-*'). Each page owns its clear, reached at call time (those pages load after this file). Before they
//     have loaded (store-api.jsx's landing drop of a fixture session) there is nothing in memory to forget, and an
//     earlier page load's keys in this tab stay until the next sign-out.
// The cart stays (REF keeps it: it isn't personal). Every logout() clears, whoever calls it: the Log out buttons, a
// 401's "Log in again" (profile, addresses), the session reconcile's 404 and the fixture-session drop.
//
// Chains (mode app; Batch 2 P4-16; D-03, D-30 (a); BATCH2-PLAN §3 P4 "Per-chain app": "Data and theme", "Loyalty
// scope"). The app is one per chain (chainedshops: a master store and its branches). The route's path key names the
// chain (route.chain) and route.hotel is the active BRANCH, which glue/router.jsx resolves (P4-11, "Mode app": ?store=
// listed by the chain → the branch saved for the chain → the chain's only store → none). Two things load, apart:
//   * the MASTER: the chain (B31: OO.api.getChain → OO.model.normalizeChain: the master, the listed stores in the
//     server's order with their status, modes and shared_loyalty; loadChain) and the master's store (getRestaurant,
//     store only: theme, logo, name). The theme and the branding always come from it (D-03), with a branch active
//     too: activateHotel themes a hotel's pages with its chain master's store (masterOf); useChain(key, { theme })
//     themes the pages with no branch. The branch names and the list come from the chain, not getRestaurant's
//     related_branches (About's "Other locations" keeps those).
//   * the BRANCH: the ordering store, OO.useHotel(route.hotel) as in every mode (menu, hours, order types, payments,
//     the cart). The wallet and loyalty reads go with the branch's key: get_customer_wallet_points answers the chain's
//     balance for a branch hyperlinked to the master and the store's own otherwise, and the ratios and the daily cap
//     are always the branch's (OO.model.normalizeLoyalty's `scope` labels it: loyaltyScope below).
// Carts stay per store ('oo-cart-v1-{branch}', the key lower-cased: POLISH-6): a branch switch switches the cart
// (setHotel), never merges or drops one, and the other branch's stays in storage for its return (cartOf reads it). As
// for any other store, the switch resets the draft's store fields (its time among them: the order sheet asks again).
// Modes online and qr have no chain: masterOf(hotel) is the hotel, chain() / branch() are null, setBranch is false.
//   OO.store.chain() → Chain | null   the current route's chain as cached (peekChain(route.chain)); null outside mode
//       app, on a store-less route, or until it is read.
//   OO.store.branch() → key | null   the active branch: the current route's hotel (P4-11's resolution, so the branch
//       saved for the chain counts); null with none chosen, or outside mode app.
//   OO.store.setBranch(key, chain?) → bool   saves `key` as the branch of `chain` (default: the current route's chain;
//       a known chain is saved under its master, as the router reads it) in P4-11's per-chain record
//       (OO.router.setBranch: 'oo-chain-v1', kept across visits). A key the chain doesn't list (the router's or the
//       cache's copy, any case) is refused; null clears it. true when it changed. Outside mode app, or with no chain:
//       false. (Batch 1's device-wide app.branch is gone; one still in storage is dropped as the app state loads.)
//   OO.store.cartOf(hotel) → { hotel, lines, count, subtotal }   that store's cart as kept, without switching to it:
//       the current cart for the current hotel, else its persisted one ('oo-cart-v1-{hotel}'; POLISH-6: either in any
//       spelling); empty in mode qr for any other store (its table carts aren't a branch's).
//   OO.data.loadChain(key, { fresh }) → Promise<Chain>   OO.model.normalizeChain(OO.api.getChain(key), { now, current:
//       key }), cached per key (any case) for CHAIN_MAX_AGE_MS (60 s, B31's own cache: the open labels stay fresh); a
//       read in flight is shared; fresh reads again. An unreadable body is the empty chain (source null). A failure
//       rejects (kind stamped) and keeps the chain read before it. POLISH-4 (p5-followups #20): src/app.jsx's chain
//       read (OO.App.ensureChain) goes through it too, so the router's chain and the pages' (OO.useChain) are one read
//       and one cache; the request is sent synchronously (a throw is a rejection).
//   OO.data.peekChain(key, { origin, byDistance }?) → Chain | null   the cached chain of `key` (sync); with origin /
//       byDistance, normalised again from the cached body with them.
//   OO.data.chainStore(chain, key) → ChainStore | null   the store of `chain` whose key is `key` (any case).
//   OO.data.chainFor(hotel) → Chain | null   a cached chain listing `hotel` (the one read for masterOf(hotel) first).
//   OO.data.masterOf(hotel) → key   mode app: the key of the store whose theme and branding `hotel`'s pages wear: the
//       master of its chain as the router knows it (OO.router.chainOf: listed there, or read for it); while the chain
//       isn't known (never read, a read in flight or failed), the current route's chain when the route is that hotel's
//       (its path key: the master once P4-11's child-key redirect has run); else, and for a chain whose master has no
//       key, a key no store has or one not live here, the hotel itself. Other modes: the hotel. It never reads a chain.
//   OO.data.loyaltyScope(hotel) → { chain, master } | {}   what OO.model.normalizeLoyalty needs to label `hotel`'s
//       balance: its cached chain (chainFor) and its master's cached store; {} outside mode app or while unknown.
//
// OO.data (non-hook core, node-testable):
//   now() → Date. The clock for availability and opening hours: OO.api.now() — the facade (P1-02) answers with the
//       fixture adapter's injectable clock (__setNow) for a fixture store, so tests and the fixture "server" share one
//       clock, and with the DEVICE clock when the current route's store is live — else the device clock. Never
//       getRestaurant's current_time, which the backend formats in the request's timezone.
//   domain(orderType, mode = OO.mode, opts) → now lives in OO.model.domain (src/lib/model.jsx); this alias is kept
//       and forwards opts ({ table } → 'TABLE_SIDE').
//   errorKind(err) → 'notFound' | 'notLive' | 'unavailable' | 'network' | 'client' | null. The error's own kind (OO.http
//       v2, the OO.api facade), else its status mapped like OO.http.kindOf: 0 → 'network', 404 → 'notFound', 408 /
//       425 / 429 / ≥ 500 → 'unavailable', another 4xx → 'client'. An error with no status (a normaliser that threw on
//       an unusable body) → 'unavailable'. null for no error.
//   loadErrorState(err) → null | { state, kind, retry }: the page for a failed store load (A R-API-4). state:
//       'notLive' (a store this host doesn't serve, P1-02) · 'notFound' (kind 'notFound': no such store) ·
//       'unavailable' (anything else: network, 408 / 425 / 429, ≥ 500, an unusable 200 = 502, another 4xx), so a
//       store that is only unreachable is never shown as not found. retry: true for 'unavailable' (Try again re-runs
//       the load), except the facade's "Not available yet" (kind 'unavailable', status 0: a method this build lacks,
//       so a retry can't help); false for the other two.
//   loadHotel(hotel, { withMenu = true, force = false, now, domain }) → Promise<{ store, menu | null }>: the cache
//       entry's store and menu as they are when it resolves (the 15-minute re-read, refreshMenu and ensureFreshMenu
//       may have replaced what the first load read; withMenu: false too, with the cached menu or null).
//       It caches per hotel and dedupes in-flight calls. A store-only cache entry never satisfies withMenu. It
//       rejects with the OO.http-style Error { status, message, data, kind } (404 = unknown store): the adapter's own
//       error, with kind stamped on (errorKind) when it carries none. The store and menu are normalised at `now`
//       (default now()), the menu for the PICKUP domain of the mode; the raw menu body is kept. The device time of
//       each read is recorded per hotel (the re-read and ensureFreshMenu measure their ages from it). P4-02: the menu
//       is the channel's that applies at the call (mode qr: the context's table menu; menu null without a context);
//       see "The menu's channel" above.
//   refreshMenu(hotel) → menu | null. Availability moves with the clock (lunch-only items, a deal's day): the cached
//       menu is normalised again from its raw body at now() (at most once a minute) and REPLACED in the cache only
//       when an availability flag changed (a stable object otherwise). OO.useHotel calls it every minute and when the
//       page becomes visible.
//   menuAt(hotel, { now, domain }) → menu | null. A snapshot of the cached menu normalised for another time and / or
//       domain — cart + checkout validate the lines against the selected pickup slot and the order type's domain, as
//       Next does before placing an order. Memoised per hotel + domain + minute; null until the menu has loaded.
//   peekHotel(hotel, withMenu) → { store, menu } | null (sync cache read; with the menu: the channel's that applies,
//       P4-02).   invalidateHotel(hotel?).
//   tableSettings(hotel) → { enabled, guestForm } | null (P4-03): the store's table-side switches from its latest
//       getRestaurant read (OO.model.tableSettings; loadHotel and the 15-minute re-read record them, the Store changed
//       or not); null until the store is cached. The normalised Store carries guestForm too, as
//       settings.tableUserForm (model-store.jsx; table.jsx reads that first, this for a Store without the key).
//   watchHotel(hotel, fn) → unwatch. fn(hotel) runs when the cached store or menu of that hotel is replaced (the
//       re-read below, ensureFreshMenu). While anything is watched, every minute and when the page becomes visible
//       again (visibilitychange), each watched hotel whose store was read STORE_REREAD_MS (15 min) ago or more is
//       re-read (rereadStore), but only while the page is visible (document.visibilityState) and not on the checkout
//       or payment-callback page (OO.router.current().page): REF re-reads its store every 15 min while visible and
//       never there (A F2, F13). Ages use the device clock (Date.now), never the fixture clock.
//   rereadStore(hotel) → Promise<store | null>. One re-read now: OO.api.getRestaurant(hotel, { fresh: true }),
//       normalised at now(). The cache's store is replaced, and the watchers called, only when it changed: the
//       fields normalised from the clock (serverTime, open, pickup) don't count, so a fixture store re-read later the
//       same day keeps its object. A failure keeps the cached store, quietly; the attempt counts as the read, so a
//       failing store is asked again 15 minutes later. null when the store isn't cached. Fixture stores: same path.
//   ensureFreshMenu(hotel, { maxAgeMs = MENU_MAX_AGE_MS }) → Promise<menu>. Checkout calls it at Pay (REF forces a
//       fresh read of a menu older than 15 min, B L1150–1160): the cached menu when it was read ≤ maxAgeMs ago; else
//       get_menu again with { fresh: true }, normalised at now() in the cached menu's domain, which REPLACES the
//       cached menu (menuAt snapshots dropped, watchers called, read time recorded). Nothing cached yet → loadHotel.
//       A failed re-fetch REJECTS (kind stamped) and keeps the cached menu: the caller decides, e.g. it carries on
//       with OO.data.peekHotel(hotel).menu. QR-OFF: a table menu's "this store takes no table orders" resolves null
//       (see "The menu's channel" above). Concurrent calls share one fetch.
//   reconcileSession(hotel) → Promise<'updated' | 'kept' | 'signedOut' | 'skipped'> (BATCH2-PLAN §2 row 53, C §3.1).
//       Once per page load, for a LIVE store (OO.api.isLiveFor) and a session with a uid: OO.api.getCustomer(hotel,
//       uid) → OO.model.normalizeCustomer → the session's customer, the token kept ('updated'). A 404 (kind
//       'notFound') signs out quietly ('signedOut'). 'kept' = the session as is: nothing changed, any other failure
//       (a 500 included: never read as "no such customer"; "Not available yet"), an unusable body, or another account
//       signed in meanwhile. A session the fixture adapter made (a fixture uid, below) is signed out locally WITHOUT a
//       request ('signedOut'): a fixture uid never reaches the real API. A fixture store or a guest: 'skipped', and a
//       later live store may still run it. Never rejects.
//   dropFixtureSession(hotel) → bool. The fixture adapter's members (the demo member 'fx_sam_taylor_812' and its
//       sign-ups, uids 'fx_…'; src/data/store-api.jsx) exist on fixture stores only. On a store that isn't one (a live
//       store, or one this host doesn't serve; OO.api.isFixtureFor, else the fixture keys while the facade hasn't
//       loaded) a session with such a uid is signed out (logout()), locally, with no request: true. Otherwise false
//       (a fixture store, no store, a guest, a real customer). store-api.jsx runs it for the document's landing store
//       before the first render, and setHotel on every later switch, so no member call carries a fixture uid.
//   STORE_REREAD_MS = MENU_MAX_AGE_MS = 900000 (15 min).
//   loadPricetags(hotel, { fresh }) → Promise<Pricetags>: OO.api.getPricetags(hotel) through
//       OO.model.normalizePricetags, once per hotel per page load (fresh re-reads it: a pricetag_refresh_required, and
//       re-prices the cart at once when the tag's prices moved);
//       soft, never rejects: a failed read (or no such method) is { rows: [], prices: null }, i.e. no tag (REF).
//       Started for the current hotel once its menu has loaded. peekPricetags(hotel) → Pricetags | null (sync).
//   walletAfterOrder(hotel, orderId) → Promise<wallet | null> (P3-27; REF refreshWallet() after a counter checkout and
//       on a confirmed payment): the order took points (the server debits them when it takes the order,
//       OnlinePaymentController) or will earn some, so the signed-in customer's wallet is read again: loadWallet(hotel,
//       { fresh: true }). Every page showing points follows the settled read (the rewards and wallet pages, the
//       checkout's slider, the pricing). Once per order id per page load (a repeat returns the first read); signed
//       out: null, with no request. pages/payment-callback.jsx calls it when the order went through.
//   loadWallet(hotel, { fresh }) → Promise<wallet | null>: the signed-in customer's get_customer_wallet_points body
//       (OO.api.getWallet when an adapter has it, else OO.api.getLoyalty's `wallet`), once per hotel + uid per page
//       load (fresh re-reads it: the checkout pre-flight, a membership_refresh_required). Soft: a failure, 403 or a
//       { status: false } body is null. It settles into state.wallets for that session only. Signed out: null.
//   ownOrders(hotel, { fresh }) → Promise<{ uid, ids } | null> (P3-FIX #10): the signed-in customer's own orders at
//       `hotel`, as order ids (strings) newest first: OO.api.getOrders (E16 get_customer_orders: the POS tickets,
//       chain-wide, per_page 1000) through OO.model.normalizeOrders. The order pages' ownership check reads it (B §9
//       Q13: OO.model.orderAccess), so a signed-in visit doesn't ask the server for the whole list again. Read once
//       per store + account per page load (memory only, never stored); `fresh` reads again and replaces it. Calls
//       while a read is on its way share it. A failed read rejects and isn't kept, so the next call reads again (a
//       fresh read that fails leaves the list read before it in place). Signed out: null, with no request. uid is the
//       account the list belongs to; logout and another account drop every list (P3-27, setSession above).
//   pricedMenu(hotel, menu) → menu at the cart's pricing (useHotel's; see Pricing).
//   selection(hotel) → the last known Selection for the current hotel (OO.pricetags.select), or null while it isn't
//       known. P2-03/04 read it for "lines with a membership tag price take no membership discount".
//   loadPricetags, peekPricetags, loadWallet, ownOrders, pricedMenu and selection take the store's key in any spelling
//   (W5-FIX #2: "Store keys" above).
//   activateHotel(hotel, store, { themeKey, mode, cart }) → { theme, vars }. Idempotent per hotel + theme key + store
//       object: a re-read store (a new object) applies its theme again. Online uses only Studio (no legacy/preset theme),
//       with CSS defaults when no document applies. Mode app (P4-16, "Chains" above): the theme is
//       that of masterOf(hotel)'s cached store, idempotent per that store (a switch between branches of one chain
//       applies nothing again); while that store isn't in, a theme of another store comes off (theme null, vars {}),
//       and a master whose read failed leaves the hotel's own. cart: false (useChain's theme) themes with `store`
//       itself and leaves the cart and the remembered store alone.
//   deactivateHotel()   a route with no store (find-store, the root payment-callback, not-found; OO.App calls it):
//       removes the theme vars activateHotel applied and forgets the activation, so the previous store's theme
//       doesn't leak and the next activateHotel applies again. The cart / hotel are untouched.
//   themeKey() → the ?theme= value from the current URL, or ''.
//   reactivate()  applies the last activateHotel again (OO.whitelabel calls it when a white-label document lands or
//       changes; idempotent, so nothing happens when the themed store didn't change). No-op before any activation and
//       after deactivateHotel.
// White label (WL-2; src/lib/whitelabel.jsx). activateHotel / activateApp theme with OO.whitelabel.storeFor(hotel, store)
// (the chain's white-label document laid over the store; the same store object when there is none), load the fonts
// with OO.theme.fontWeights (null for every other theme: loadGoogleFont's own rule) and set the favicon
// (OO.whitelabel.applyHead). OO.useHotel and OO.useChain start the document's read (OO.whitelabel.ensure), re-render on
// its changes (useRevision) and return the overlaid store / master. Without a document every path is as before.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};

  var K = {
    hotel: 'oo-hotel-v1',
    cart: 'oo-cart-v1-',
    checkout: 'oo-checkout-v1',
    session: 'oo-session-v1',
    app: 'oo-app-v1',
    lastOrder: 'oo-last-order-v1',
    table: 'oo-table-v1',
    tableCart: 'oo-table-cart-v1-', // + hotel: mode qr's cart, one table at a time per store (P4-04, "Table carts")
  };

  if (OO.mode === 'kiosk') {
    Object.keys(K).forEach(function (key) { K[key] = K[key].replace(/^oo-/, 'oo-kiosk-'); });
  }
  if (OO.orderingQr && OO.orderingQr.active()) {
    ['cart', 'checkout', 'lastOrder'].forEach(function (key) {
      K[key] = 'oo-ordering-qr-' + OO.orderingQr.token() + (OO.orderingQr.preview() ? '-preview' : '') + '-' + key + '-';
    });
  }

  // ---- storage (every access guarded: private mode, disabled storage, quota) ------------------------
  function storage() {
    try { return (OO.mode === 'kiosk' ? window.sessionStorage : window.localStorage) || null; } catch (e) { return null; }
  }
  function readKey(key) {
    try {
      var s = storage();
      var raw = s ? s.getItem(key) : null;
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function writeKey(key, value) {
    try {
      var s = storage();
      if (!s) return;
      if (value === null || value === undefined) s.removeItem(key);
      else s.setItem(key, JSON.stringify(value));
    } catch (e) { /* quota / disabled storage: the in-memory state still works */ }
  }
  function readString(key) {
    try { var s = storage(); var v = s ? s.getItem(key) : null; return typeof v === 'string' && v ? v : null; } catch (e) { return null; }
  }
  function writeString(key, value) {
    try { var s = storage(); if (s) { if (value) s.setItem(key, value); else s.removeItem(key); } } catch (e) { /* ignore */ }
  }

  function own(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function obj(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : null; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function given(v) { return v !== undefined && v !== null && String(v).trim() !== ''; }
  // A scalar id / key: a non-empty string or a finite number, else null.
  function scalar(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    return typeof v === 'string' && v.trim() ? v : null;
  }

  var ADDRESS_MAX = 300;
  // 2 = marketing started unchecked, 3 = terms started checked, 4 = preserve earlier saved marketing values when the
  // default is checked. Keep the terms migration at v3 so upgrading a v3 draft never changes its terms choice.
  var CHECKOUT_V = 4;
  var MARKETING_OPT_IN_V = 2;
  var TERMS_DEFAULT_V = 3;
  var CHECKOUT_DEFAULTS = {
    // terms: TICKED by default in every ordering option (TERMS, owner 2026-10-05); Pay still needs it ticked.
    name: '', phone: '', email: '', notes: '', terms: true, marketing: false, // no local choice until marketingChosen is set
    orderType: 'PICKUP', day: null, slot: null, chosenAt: null, paymentMethod: null, // chosenAt: checkout-time.jsx (P2-23)
    paymentChoice: null,         // P2-27: the payment choice id (checkout-payment.jsx)
    tipChoice: 'none', tipAmount: '0',
    address: '', zoneId: null,   // delivery address (free text) + delivery area (store.deliveryZones id)
    zipcode: '', lat: null, lng: null, // the Places pick behind the address (P1-21; checkout.jsx clears them on typing)
    hotel: null,                 // the store the draft belongs to
  };
  // What a finished order keeps of the draft with keepContact: the customer, not the order. WL-OPT: marketingChosen (the
  // customer ticked or unticked the offers box) is kept with the tick, and only when the draft has it.
  var CONTACT_KEYS = ['name', 'phone', 'email', 'marketing', 'marketingChosen', 'address', 'zoneId', 'zipcode', 'lat', 'lng', 'hotel'];
  // What belongs to one store's order: reset when the draft moves to another store (terms are accepted per store).
  var STORE_DRAFT_KEYS = ['terms', 'notes', 'day', 'slot', 'chosenAt', 'zoneId', 'paymentChoice', 'tipChoice', 'tipAmount'];
  // P3-LOGOUT: all a sign-out keeps of the draft (see the header, "Sign-out"); every other key resets to its default.
  var SIGNED_OUT_KEEP = ['orderType', 'hotel'];

  // ---- the table context (mode qr; see the header, "The table context") ----
  var TABLE_TTL_MS = 2 * 60 * 60000; // 2 h (REF's table cart and guest session live as long)
  var GUESTS_MAX = 999;              // the party size's cap: the POS decodes guest_count as an int (payload.jsx)
  function qrMode() { return OO.mode === 'qr'; }
  // A whole number > 0 (a number, or a string of digits), else null: table ids, party sizes, category ids.
  function posInt(v) {
    var n = typeof v === 'number' ? v : typeof v === 'string' && /^\s*\d+\s*$/.test(v) ? Number(v) : NaN;
    return isFinite(n) && n > 0 && Math.floor(n) === n && n <= 9007199254740991 ? n : null;
  }
  // The context as kept, or null. The hotel and a whole-number id are required (a table never applies to another
  // store; every POS build decodes table_id as an int). `at` (device ms) stamps a missing expiresAt and caps a later one.
  function sanitizeTable(raw, at) {
    var t = obj(raw);
    if (!t) return null;
    var id = posInt(t.id);
    var hotel = typeof t.hotel === 'string' && t.hotel ? t.hotel : null;
    if (id === null || !hotel) return null;
    var name = typeof t.name === 'string' ? t.name.trim().slice(0, 80) : '';
    var guests = posInt(t.guests);
    var cap = at + TABLE_TTL_MS;
    var exp = typeof t.expiresAt === 'number' && isFinite(t.expiresAt) && t.expiresAt > 0 ? Math.min(t.expiresAt, cap) : cap;
    var ex = [];
    arr(t.excludedCategoryIds).forEach(function (v) { var n = posInt(v); if (n !== null && ex.indexOf(n) < 0) ex.push(n); });
    return {
      id: id, name: name || null, hotel: hotel, guests: guests !== null && guests <= GUESTS_MAX ? guests : null,
      excludedCategoryIds: ex, expiresAt: exp,
    };
  }
  function unexpired(t) { return !!t && t.expiresAt > clockMs(); }

  // The current route (OO.router.current(); glue/router.jsx is concatenated first), or null.
  function currentRoute() {
    try { return OO.router && OO.router.current ? OO.router.current() : null; } catch (e) { return null; }
  }

  // `table`: mode qr's context the cart would belong to (P4-04, "Table carts"; default state.table).
  function loadCart(hotel, table) {
    if (!hotel) return { lines: [], coupon: null };
    if (qrMode()) return loadTableCart(hotel, table === undefined ? state.table : table);
    var raw = readSlot(K.cart, hotel); // POLISH-6: the store's slot, any spelling
    if (!raw) return { lines: [], coupon: null };
    var lines = OO.cart && OO.cart.sanitize ? OO.cart.sanitize(raw.lines) : [];
    return cartWith({ coupon: obj(raw.coupon) }, lines, sanitizePricing(raw.pricing));
  }
  // ---- table carts (mode qr, P4-04; see the header, "Table carts") ----
  // The table a state's cart belongs to (its held context for the cart's store, unexpired), or null: no table, no cart.
  function cartTable(st) {
    var t = st && st.table;
    return qrMode() && st.hotel && t && unexpired(t) && sameStore(t.hotel, st.hotel) ? t.id : null;
  }
  // What the cart belongs to: one scope outside mode qr (the hotel, as ever), (store, held table) in mode qr. The held
  // table, expired or not: clearing an expired context is a change of scope too (its cart leaves memory); the 2 h is
  // judged where the cart is read and written (loadTableCart, saveCart).
  function cartScope(st) {
    if (!qrMode()) return 'online';
    var t = st.table;
    return String(st.hotel || '') + '|' + (t && st.hotel && sameStore(t.hotel, st.hotel) ? t.id : '');
  }
  function loadTableCart(hotel, table) {
    var t = table && unexpired(table) && sameStore(table.hotel, hotel) ? table : null;
    var raw = t ? readSlot(K.tableCart, hotel) : null; // POLISH-6: the store's slot, any spelling
    if (!raw || Number(raw.table) !== t.id || typeof raw.updatedAt !== 'number' || clockMs() - raw.updatedAt > TABLE_TTL_MS) {
      return { lines: [], coupon: null };
    }
    var lines = OO.cart && OO.cart.sanitize ? OO.cart.sanitize(raw.lines) : [];
    return cartWith({ coupon: obj(raw.coupon) }, lines, sanitizePricing(raw.pricing));
  }
  // Keeps st's cart for `hotel` (st.hotel): 'oo-cart-v1-{hotel}' as ever; in mode qr the store's table slot, stamped with
  // its table and the time (its 2 h start again), and nothing without a table. POLISH-6: {hotel} lower-cased (slotOf).
  function saveCart(hotel, cart, st) {
    if (!qrMode()) { writeKey(slotOf(K.cart, hotel), cartRecord(cart)); return; }
    var id = cartTable(st);
    if (id !== null) writeKey(slotOf(K.tableCart, hotel), Object.assign({ v: 1, table: id, updatedAt: clockMs() }, cartRecord(cart)));
  }
  // ---- cart slots (POLISH-6, p5-followups #22; see the header, "Store keys") ----
  // A store's cart slot: the prefix + its key lower-cased, so every spelling the URL brings (/Bennys, ?store=BENNYS)
  // finds the one cart.
  function slotOf(prefix, hotel) { return prefix + storeKey(hotel); }
  // The storage keys of `hotel`'s slot under OTHER spellings (builds before POLISH-6 keyed a cart by the URL's
  // spelling): the spelling given first, then any other in storage (none when storage can't be listed).
  function otherSlots(prefix, hotel) {
    var canon = slotOf(prefix, hotel);
    var mine = prefix + keyOf(hotel);
    var out = mine !== canon ? [mine] : [];
    try {
      var s = storage();
      var n = s && typeof s.length === 'number' ? s.length : 0;
      for (var i = 0; i < n; i++) {
        var k = s.key(i);
        if (typeof k !== 'string' || k === canon || out.indexOf(k) >= 0 || k.indexOf(prefix) !== 0) continue;
        if (k.slice(prefix.length).toLowerCase() === storeKey(hotel)) out.push(k);
      }
    } catch (e) { /* storage that can't be listed: the spelling given only */ }
    return out;
  }
  // The slot's record (raw, untrusted). While the canonical slot holds no lines, a slot written under another spelling
  // MOVES into it (the first with lines, in otherSlots' order): never merged over a cart, so no item is lost, and its
  // old key goes once the move is written (a cart emptied later never brings it back; a failed write keeps it).
  function readSlot(prefix, hotel) {
    var canon = slotOf(prefix, hotel);
    var raw = obj(readKey(canon));
    if (raw && arr(raw.lines).length) return raw;
    var others = otherSlots(prefix, hotel);
    for (var i = 0; i < others.length; i++) {
      var old = obj(readKey(others[i]));
      if (!old || !arr(old.lines).length) continue;
      writeKey(canon, old);
      var back = obj(readKey(canon));
      if (back && arr(back.lines).length) writeKey(others[i], null);
      return old;
    }
    return raw;
  }
  // settleOrder's (POLISH-6): the slot under the order's own spelling, when it isn't the canonical one, goes too: a
  // cart kept there before POLISH-6 is the order's, and its paid lines must never come back.
  function dropSpelling(prefix, hotel) {
    var mine = prefix + keyOf(hotel);
    if (mine !== slotOf(prefix, hotel)) writeKey(mine, null);
  }
  // A cart object: lines, coupon and, only when the lines are priced with a pricetag, its record (P2-02). The key is
  // left out at the base prices, so a cart without a tag is exactly what it was before pricetags.
  function cartWith(cart, lines, pricing) {
    var out = { lines: lines, coupon: (cart && cart.coupon) || null };
    if (pricing) out.pricing = pricing;
    return out;
  }
  // The cart as stored: { v: 1, lines, coupon[, pricing] }.
  function cartRecord(cart) {
    var out = { v: 1, lines: cart.lines, coupon: cart.coupon || null };
    if (cart.pricing) out.pricing = cart.pricing;
    return out;
  }
  // The pricetag record stored with a cart (OO.pricetags.priceMenu's `pricetag`, untrusted when read back): only its
  // keys, each of the right type, prices > 0; anything else is null (the base prices).
  var PRICE_KINDS = ['product', 'size', 'modifier', 'combo'];
  function sanitizePricing(raw) {
    var r = obj(raw);
    if (!r || r.active !== true || scalar(r.tagId) === null || !obj(r.prices)) return null;
    var prices = {};
    PRICE_KINDS.forEach(function (k) {
      var src = obj(r.prices[k]) || {};
      var list = {};
      Object.keys(src).forEach(function (id) {
        var v = src[id];
        if (typeof v === 'number' && isFinite(v) && v > 0) list[id] = v;
      });
      prices[k] = list;
    });
    return {
      tagId: r.tagId, name: typeof r.name === 'string' ? r.name : '', domain: typeof r.domain === 'string' ? r.domain : '',
      active: true, override: false, membership: r.membership === true, prices: prices,
    };
  }
  // Only the known fields, each coerced to its default's type: booleans, strings (the address capped), and the
  // null-default keys (day, slot, chosenAt, paymentMethod, paymentChoice, zoneId) as a string / number id or null. A
  // record from before CHECKOUT_V is migrated once: old defaults are corrected and any saved marketing boolean is
  // retained as a choice, since a prior false cannot safely be distinguished from an explicit untick.
  function loadCheckout() {
    var stored = obj(readKey(K.checkout));
    var raw = stored || {};
    var out = Object.assign({}, CHECKOUT_DEFAULTS);
    Object.keys(CHECKOUT_DEFAULTS).forEach(function (k) {
      if (!Object.prototype.hasOwnProperty.call(raw, k)) return;
      var d = CHECKOUT_DEFAULTS[k], v = raw[k];
      if (typeof d === 'boolean') out[k] = !!v;
      else if (typeof d === 'string') out[k] = typeof v === 'string' ? v : d;
      else out[k] = scalar(v);
    });
    out.address = out.address.slice(0, ADDRESS_MAX);
    if (raw.marketingChosen === true) out.marketingChosen = true; // WL-OPT: only when set (see the header)
    var version = stored ? Number(stored.v) : NaN;
    if (stored && !(version >= CHECKOUT_V)) {
      if (!(version >= MARKETING_OPT_IN_V)) out.marketing = false;
      if (!(version >= TERMS_DEFAULT_V)) out.terms = true;
      if (typeof raw.marketing === 'boolean') out.marketingChosen = true;
      writeKey(K.checkout, checkoutRecord(out));
    }
    return out;
  }
  // The draft as stored: the state's draft stamped with its record version.
  function checkoutRecord(c) { return Object.assign({ v: CHECKOUT_V }, c); }
  function loadSession() {
    var raw = obj(readKey(K.session));
    return raw && obj(raw.customer) ? { customer: raw.customer, customToken: raw.customToken || null } : null;
  }
  function loadApp() {
    var stored = obj(readKey(K.app));
    var raw = stored || {};
    function map(m) {
      var out = {};
      var o = obj(m) || {};
      Object.keys(o).forEach(function (h) { out[h] = arr(o[h]).map(String); });
      return out;
    }
    var app = { read: map(raw.read) };
    // P3-15: the old local Redeem taps leave storage too; P4-16: so does Batch 1's device-wide branch (the router keeps
    // the branch per chain, "Chains" in the header).
    if (stored && (own(stored, 'redeemed') || own(stored, 'branch'))) writeKey(K.app, app);
    return app;
  }
  // ---- the order snapshot (lastOrder.snapshot; see the header) ----------------------------------------------
  // Each field has a check that returns the value to keep, or undefined to drop the field.
  var SNAP_TEXT_MAX = 200;
  var SNAP_LINES_MAX = 50;
  var SNAP_ORDER_TYPES = ['PICKUP', 'DELIVERY', 'DINEIN'];
  function snapText(v) { return typeof v === 'string' ? v.slice(0, SNAP_TEXT_MAX) : undefined; }
  function snapDescription(v) { return typeof v === 'string' ? v.slice(0, 1000) : undefined; }
  function snapTextOrNull(v) { return v === null ? null : snapText(v); }
  function snapBool(v) { return typeof v === 'boolean' ? v : undefined; }
  function snapNumber(v) { return typeof v === 'number' && isFinite(v) ? v : undefined; }
  function snapOrderType(v) { return SNAP_ORDER_TYPES.indexOf(v) >= 0 ? v : undefined; }
  // The keys of `spec` that `src` has and that pass their check (unknown keys never get through).
  function snapPick(src, spec) {
    var out = {};
    Object.keys(spec).forEach(function (k) {
      if (!own(src, k)) return;
      var v = spec[k](src[k]);
      if (v !== undefined) out[k] = v;
    });
    return out;
  }
  function snapObject(spec) {
    return function (v) { var o = obj(v); return o ? snapPick(o, spec) : undefined; };
  }
  // A list of objects (other entries dropped), each picked by `spec`; at most `max` (0 = no cap).
  function snapList(spec, max) {
    return function (v) {
      if (!Array.isArray(v)) return undefined;
      var out = [];
      for (var i = 0; i < v.length && !(max && out.length >= max); i++) {
        var o = obj(v[i]);
        if (o) out.push(snapPick(o, spec));
      }
      return out;
    };
  }
  var SNAPSHOT_SPEC = {
    storeName: snapText,
    orderType: snapOrderType,
    when: snapObject({ asap: snapBool, label: snapText, at: snapTextOrNull }),
    lines: snapList({ name: snapText, qty: snapNumber, mods: snapText, total: snapText }, SNAP_LINES_MAX),
    rows: snapList({ key: snapText, label: snapText, text: snapText, description: snapDescription }, 0),
    total: snapText,
    address: snapTextOrNull,
    table: snapTextOrNull,
    paymentLabel: snapText,
  };
  function sanitizeSnapshot(raw) {
    var s = obj(raw);
    if (!s || s.v !== 1) return null;
    return Object.assign({ v: 1 }, snapPick(s, SNAPSHOT_SPEC));
  }
  // The order as given, with its snapshot (when it has one) sanitised.
  function withSnapshot(order) {
    if (own(order, 'snapshot')) order.snapshot = sanitizeSnapshot(order.snapshot);
    return order;
  }

  function loadLastOrder() {
    var raw = obj(readKey(K.lastOrder));
    return raw && raw.orderId ? withSnapshot(raw) : null;
  }
  // Mode qr only (another mode never touches the key). A stored context must carry its own unexpired expiresAt (a
  // record from before P4-01 has none); anything else is dropped from storage too.
  function loadTable() {
    if (!qrMode()) return null;
    var stored = readString(K.table) !== null; // corrupt JSON reads as null below, and goes too
    var raw = readKey(K.table);
    var t = obj(raw) && typeof raw.expiresAt === 'number' ? sanitizeTable(raw, clockMs()) : null;
    if (!unexpired(t)) t = null;
    if (stored && !t) writeKey(K.table, null);
    return t;
  }
  function tableRecord(t) { return t ? Object.assign({ v: 1 }, t) : null; }

  function fresh() {
    return {
      hotel: null,
      cart: { lines: [], coupon: null },
      checkout: loadCheckout(),
      session: loadSession(),
      app: loadApp(),
      lastOrder: loadLastOrder(),
      table: loadTable(),
      wallets: {},
      priceNotice: null,
      points: {},
    };
  }

  var state = fresh();
  var listeners = [];

  function persist(prev, next) {
    if (next.hotel && (prev.cart !== next.cart || prev.hotel !== next.hotel || cartScope(prev) !== cartScope(next))) {
      saveCart(next.hotel, next.cart, next);
    }
    if (prev.checkout !== next.checkout) writeKey(K.checkout, checkoutRecord(next.checkout));
    if (prev.session !== next.session) writeKey(K.session, next.session);
    if (prev.app !== next.app) writeKey(K.app, next.app);
    if (prev.lastOrder !== next.lastOrder) writeKey(K.lastOrder, next.lastOrder);
    if (prev.table !== next.table) writeKey(K.table, tableRecord(next.table));
  }

  function emit() {
    listeners.slice().forEach(function (fn) {
      try { fn(state); } catch (e) { if (window.console) console.error('[online-order] store listener failed:', e); }
    });
  }

  function get() { return state; }

  // A change of a pricing input re-prices the cart in the SAME update (one write, one emit; see Pricing), then starts
  // the reads the pricing still waits for.
  function set(patch) {
    var p = typeof patch === 'function' ? patch(state) : patch;
    if (!p) return state;
    if (Object.prototype.hasOwnProperty.call(p, 'table') && p.table !== state.table) {
      p = Object.assign({}, p, { table: qrMode() ? sanitizeTable(p.table, clockMs()) : null }); // qr-only (P4-01)
    }
    var prev = state;
    var next = Object.assign({}, state, p);
    // P4-04: a table change that moves the cart's scope (mode qr) brings that table's cart, unless the patch sets one.
    if (!Object.prototype.hasOwnProperty.call(p, 'cart') && cartScope(prev) !== cartScope(next)) {
      next.cart = loadCart(next.hotel, next.table);
      var freed = freePatch(next); // W5-FIX #1: a cart read from storage gets its free units at the order's moment
      if (freed) next.cart = freed.cart;
    }
    var moved = pricingInputsChanged(prev, next);
    var priced = moved ? pricingPatch(next) : null;
    state = priced ? Object.assign(next, priced) : next;
    persist(prev, state);
    emit();
    if (moved) kickLoads(state);
    return state;
  }

  function subscribe(fn) {
    listeners.push(fn);
    return function () { listeners = listeners.filter(function (x) { return x !== fn; }); };
  }

  // ---- the fixture adapter's local state (G1; see the header: dropFixtureSession) -----------------------------------
  var FIXTURE_UID_PREFIX = 'fx_'; // src/data/store-api.jsx's uids; a real one is Firebase's (28 alphanumerics) or temp_…

  // Is `hotel` a fixture store? The facade's answer (no store counts as one) at call time; before the facade has loaded
  // (the landing check store-api.jsx runs), the fixture keys. An error answers "fixture": nothing is dropped.
  function fixtureStore(hotel) {
    var key = hotel === undefined || hotel === null ? '' : String(hotel);
    try {
      if (OO.api && typeof OO.api.isFixtureFor === 'function') return !!OO.api.isFixtureFor(key);
      var fx = OO.fixtures || OO.data.fixtures;
      return !key || !!(fx && typeof fx.has === 'function' && fx.has(key));
    } catch (e) { return true; }
  }
  // A uid the fixture adapter made: 'fx_…', or the demo member's.
  function fixtureUid(uid) {
    var u = uid === undefined || uid === null ? '' : String(uid);
    if (!u) return false;
    if (u.indexOf(FIXTURE_UID_PREFIX) === 0) return true;
    var fx = OO.fixtures || OO.data.fixtures;
    return !!(fx && fx.DEMO_CUSTOMER && u === String(fx.DEMO_CUSTOMER.uid));
  }
  function dropFixtureSession(hotel) {
    var key = hotel === undefined || hotel === null ? '' : String(hotel);
    var s = state.session;
    var uid = s && s.customer && s.customer.uid != null ? String(s.customer.uid) : '';
    if (!key || !uid || !fixtureUid(uid) || fixtureStore(key)) return false;
    logout();
    return true;
  }
  // `h` isn't a fixture store: sign a fixture session out (P3-15: the local Redeem taps it also forgot are gone).
  function shedFixtureState(h) {
    dropFixtureSession(h);
  }

  // opts.remember = false switches the cart without recording the hotel for /payment-callback (useHotel does that
  // before the store is known to exist; activateHotel remembers it after a successful load). Another store's table
  // context is dropped here (memory and storage; the same store under another spelling of its key keeps it, P4-01); a
  // reload (no hotel yet → the table's own hotel) keeps it. A store that isn't a fixture store sheds the fixture
  // adapter's local state first (shedFixtureState).
  function setHotel(hotel, opts) {
    var h = hotel === undefined || hotel === null || hotel === '' ? null : String(hotel);
    if (h && !(opts && opts.remember === false)) writeString(K.hotel, h);
    if (h && !fixtureStore(h)) shedFixtureState(h);
    var dropTable = !!(h && state.table && !sameStore(state.table.hotel, h));
    var draftFor = state.checkout.hotel;
    var moveDraft = !!(h && draftFor !== h);
    if (h === state.hotel && !dropTable && !moveDraft) return state;
    var patch = h === state.hotel ? {} : { hotel: h, cart: loadCart(h, dropTable ? null : state.table) };
    if (dropTable) {
      patch.table = null;
      writeKey(K.table, null);
    }
    if (moveDraft) {
      var draft = Object.assign({}, state.checkout, { hotel: h });
      if (draftFor) STORE_DRAFT_KEYS.forEach(function (k) { draft[k] = CHECKOUT_DEFAULTS[k]; });
      patch.checkout = draft;
      writeKey(K.checkout, checkoutRecord(draft));
    }
    var prev = state;
    state = Object.assign({}, state, patch);
    // W5-FIX #1: a cart just read from storage gets its free units at the order's moment when the menu is cached.
    var freed = patch.cart ? freePatch(state) : null;
    if (freed) state = Object.assign({}, state, freed);
    // Back at a hotel whose pricing is already known (P2-02): its cart follows it now, in this same update.
    var priced = h && pricingInputsChanged(prev, state) ? pricingPatch(state) : null;
    if (priced) state = Object.assign({}, state, priced);
    if (h && (freed || priced)) saveCart(h, state.cart, state);
    emit();
    kickLoads(state);
    return state;
  }

  function lastHotel() { return readString(K.hotel); }

  // ---- cart actions --------------------------------------------------------------------------------
  function cartUpdate(fn) {
    var lines = fn(state.cart.lines);
    if (lines === state.cart.lines) return lines;
    set({ cart: Object.assign({}, state.cart, { lines: lines }) });
    return lines;
  }
  function cartAdd(line) { var l = priceLine(line); return cartUpdate(function (ls) { return OO.cart.add(ls, l); }); }
  function cartSetQty(key, qty) { return cartUpdate(function (l) { return OO.cart.setQty(l, key, qty); }); }
  function cartInc(key) { return cartUpdate(function (l) { return OO.cart.inc(l, key); }); }
  function cartDec(key) { return cartUpdate(function (l) { return OO.cart.dec(l, key); }); }
  function cartRemove(key) { return cartUpdate(function (l) { return OO.cart.remove(l, key); }); }
  function cartReplace(key, line) { var l = priceLine(line); return cartUpdate(function (ls) { return OO.cart.replace(ls, key, l); }); }
  function cartClear() { set({ cart: cartWith({ coupon: null }, [], state.cart.pricing), points: pointsWithout(state.points, state.hotel) }); return []; }
  // `menu` at the cart's pricing (see the header), so a base menu never undoes a tag, and at the order's moment (W5-FIX #1).
  function cartReprice(menu) {
    var r = OO.cart.reprice(state.cart.lines, atMoment(atCartPricing(menu), state.cart.lines, state));
    if (r.lines !== state.cart.lines) {
      var patch = { cart: Object.assign({}, state.cart, { lines: r.lines }) };
      if (r.changed.length) patch.priceNotice = raiseNotice(state.hotel, r.changed);
      set(patch);
    }
    return r.changed;
  }
  function setCoupon(coupon) { set({ cart: Object.assign({}, state.cart, { coupon: coupon || null }) }); return state.cart.coupon; }
  function cartLines() { return state.cart.lines; }
  function cartCount() { return OO.cart.count(state.cart.lines); }
  function cartSubtotal() { return OO.cart.subtotal(state.cart.lines); }

  // ---- checkout draft / session / app state ----------------------------------------------------------
  function setCheckout(patch) {
    set({ checkout: Object.assign({}, state.checkout, patch || {}, { hotel: state.hotel || state.checkout.hotel || null }) });
    return state.checkout;
  }
  function clearedDraft(c, keepContact) {
    var out = Object.assign({}, CHECKOUT_DEFAULTS);
    if (keepContact) CONTACT_KEYS.forEach(function (k) { if (c && c[k] !== undefined) out[k] = c[k]; });
    return out;
  }
  function clearCheckout(opts) {
    set({ checkout: clearedDraft(state.checkout, !opts || opts.keepContact !== false) });
    return state.checkout;
  }
  function setSession(s) {
    var next = s && s.customer ? { customer: s.customer, customToken: s.customToken || null } : null;
    var uid = sessionUid({ session: next });
    var was = sessionUid(state);
    // P3-27: another account, or none (see the header): the account that leaves takes its member caches and (P3-LOGOUT)
    // this device's copy of its personal data with it; a guest signing in drops nothing.
    var leaving = !!was && was !== uid;
    if (leaving) {
      dropMemberCaches();
      forgetPageData();
    }
    set(Object.assign({ session: next, wallets: walletsOf(state.wallets, uid), points: walletsOf(state.points, uid) },
      leaving ? signOutPatch(state) : null));
    return state.session;
  }
  // The in-memory member reads (wallet, loyalty, transactions…) go with the session: OO.http's read cache is flushed
  // FIRST, so what re-renders on the logout never gets the old account's cached bodies. The wallet reads go too, and
  // so do the points chosen (P2-07), the member's pricetag Selection and the post-order refreshes (P3-27). P3-LOGOUT:
  // the pages' tab data is forgotten before the state changes too, and the draft and lastOrder change with the session.
  function logout() {
    dropMemberCaches();
    forgetPageData();
    set(Object.assign({ session: null, wallets: {}, points: {} }, signOutPatch(state)));
  }
  // ---- sign-out: this device forgets the customer (P3-LOGOUT; see the header, "Sign-out") ----
  // The draft a sign-out leaves: the defaults, but for SIGNED_OUT_KEEP (the order type, the store's stamp).
  function signedOutDraft(c) {
    var out = Object.assign({}, CHECKOUT_DEFAULTS);
    SIGNED_OUT_KEEP.forEach(function (k) { if (c && c[k] !== undefined) out[k] = c[k]; });
    return out;
  }
  // An online payment this device still waits on (payment-callback.jsx orderIdOf's `awaiting`): placed to be paid at
  // the gateway (clearCartOnSuccess) and not settled yet. Its order must survive a sign-out.
  function paymentInFlight(lo) {
    if (!lo || String(lo.paymentMethod || '').toUpperCase() !== 'ONLINE_PAYMENT') return false;
    return lo.clearCartOnSuccess === true && !lo.settledAt;
  }
  // What a sign-out changes in `st` besides the session: the draft, and lastOrder unless its payment is in flight.
  function signOutPatch(st) {
    return { checkout: signedOutDraft(st.checkout), lastOrder: paymentInFlight(st.lastOrder) ? st.lastOrder : null };
  }
  // What the pages keep for this tab: each page's own clear, when it has loaded (they load after this file).
  function forgetPageData() {
    var P = OO.pages || {};
    try {
      var handoff = P.Booking && P.Booking.handoff;
      if (handoff && typeof handoff.clear === 'function') handoff.clear();
    } catch (e) { /* a page's storage failing never blocks the sign-out */ }
    try {
      var age = P.ItemSheet && P.ItemSheet.age;
      if (age && typeof age.forgetAll === 'function') age.forgetAll();
    } catch (e) { /* as above */ }
  }
  // P3-27: every member cache this file and OO.http keep, dropped BEFORE the session changes (the set() that follows
  // re-selects the pricing for the new session in the same update when it is known). P3-FIX #10: the order lists too.
  function dropMemberCaches() {
    try { if (OO.http && typeof OO.http.flush === 'function') OO.http.flush(); } catch (e) { /* no transport: nothing cached */ }
    walletReads = {};
    selections = {};
    afterOrder = {};
    orderReads = {};
  }
  function session() { return state.session; }

  function hotelList(map) { return arr(map[state.hotel || '_']); }
  function withHotelList(field, ids) {
    var map = Object.assign({}, state.app[field]);
    map[state.hotel || '_'] = ids;
    var app = Object.assign({}, state.app);
    app[field] = map;
    set({ app: app });
  }
  function readIds() { return hotelList(state.app.read); }
  function markRead(ids) {
    var cur = readIds();
    var add = arr(Array.isArray(ids) ? ids : [ids]).map(String).filter(function (id) { return cur.indexOf(id) < 0; });
    if (add.length) withHotelList('read', cur.concat(add));
    return readIds();
  }
  function markAllRead(ids) { return markRead(ids); }

  function setLastOrder(order) {
    set({ lastOrder: order && order.orderId ? withSnapshot(Object.assign({}, order)) : null });
    return state.lastOrder;
  }
  function clearLastOrder() { set({ lastOrder: null }); }

  // See the header. The cart cleared is the order's hotel's, even when another hotel is current.
  function settleOrder(orderId) {
    var lo = state.lastOrder;
    if (!lo || !given(orderId) || String(lo.orderId) !== String(orderId) || !lo.clearCartOnSuccess) return false;
    var h = lo.hotel ? String(lo.hotel) : state.hotel;
    var patch = {
      lastOrder: Object.assign({}, lo, { clearCartOnSuccess: false, settledAt: Date.now() }),
      checkout: clearedDraft(state.checkout, true),
      points: pointsWithout(state.points, h),
    };
    // P4-04: in mode qr the store's table slot goes whatever the context (a payment return in a fresh tab may have none,
    // and the paid lines must never come back); the current cart is written again empty when a context applies.
    // POLISH-6: the store's slot in any spelling (the current hotel's cart is that store's whatever the case), and the
    // slot under the order's own spelling goes too (dropSpelling).
    if (h && qrMode()) { writeKey(slotOf(K.tableCart, h), null); dropSpelling(K.tableCart, h); }
    if (h && !qrMode()) dropSpelling(K.cart, h);
    if (h && !sameStore(h, state.hotel)) {
      if (!qrMode()) writeKey(slotOf(K.cart, h), { v: 1, lines: [], coupon: null });
    } else patch.cart = cartWith({ coupon: null }, [], state.cart.pricing);
    set(patch);
    return true;
  }

  function releaseCouponTxn(orderId) {
    var lo = state.lastOrder;
    if (!lo || !given(orderId) || String(lo.orderId) !== String(orderId) || !lo.couponTxn || lo.couponReleased) return null;
    set({ lastOrder: Object.assign({}, lo, { couponReleased: true }) });
    return lo.couponTxn;
  }

  // ---- the table context's API (see the header, "The table context") ----
  function setTable(t) { set({ table: t }); return state.table; }
  function clearTable() { if (state.table) set({ table: null }); return null; }
  function table() { return unexpired(state.table) ? state.table : null; }
  function tableFor(hotel) {
    var t = table();
    if (!t || !qrMode() || !sameStore(t.hotel, hotel)) return null;
    // The URL's table wins: on this store's entry for another table, the stored context waits for that table's check.
    var r = currentRoute();
    var entry = r && r.page === 'menu' && r.params && given(r.params.tableId) && sameStore(r.hotel, hotel) ? r.params.tableId : null;
    return entry !== null && posInt(entry) !== t.id ? null : t;
  }

  // ---- pricing: the active pricetag over the cart (P2-02; see the header, "Pricing") --------------------------------
  var DRAFT_TYPES = ['PICKUP', 'DELIVERY']; // the order types a draft prices with (cart.jsx TYPES)
  var tagReads = {};    // hotel → { p: Promise<Pricetags>, data: Pricetags | null (until read) }
  var walletReads = {}; // '{hotel}|{uid}' → Promise<wallet | null>, one read per page load (fresh replaces it)
  var afterOrder = {};  // orderId → walletAfterOrder's read (P3-27: once per order per page load)
  var orderReads = {};  // '{hotel}|{uid}' → Promise<{ uid, ids }>: ownOrders' list, one read per page load (P3-FIX #10)
  var selections = {};  // hotel → the last known Selection (OO.data.selection)
  var pricedMemo = {};  // hotel → { base, rec, out }: pricedMenu's last answer
  var noticeSeq = 0;

  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function sessionUid(st) {
    var c = st && st.session && st.session.customer;
    return c && c.uid !== undefined && c.uid !== null ? String(c.uid) : '';
  }
  // W5-FIX #2 ("Store keys" in the header): the store's key as the pricing and wallet state is keyed, lower-cased, so
  // the route's /Bennys and the engines' online_key bennys find the same entries.
  function storeKey(hotel) { return keyOf(hotel).toLowerCase(); }
  function sameStore(a, b) { var k = storeKey(a); return !!k && k === storeKey(b); }
  function walletKey(hotel, uid) { return storeKey(hotel) + '|' + uid; }
  // The wallet reads of one account only (another account's never linger in memory); none signed out.
  function walletsOf(wallets, uid) {
    var out = {};
    if (!uid) return out;
    Object.keys(wallets || {}).forEach(function (k) { if (k.slice(k.lastIndexOf('|') + 1) === uid) out[k] = wallets[k]; });
    return out;
  }
  // Records compare by content whatever their key order (one read back from storage included).
  function canon(v) {
    if (Array.isArray(v)) return '[' + v.map(canon).join(',') + ']';
    if (isRecord(v)) return '{' + Object.keys(v).sort().map(function (k) { return JSON.stringify(k) + ':' + canon(v[k]); }).join(',') + '}';
    return JSON.stringify(v === undefined ? null : v);
  }
  function sameRecord(a, b) { return canon(a || null) === canon(b || null); }
  function findBy(list, k, v) {
    if (v === undefined || v === null || v === '') return null;
    return arr(list).filter(function (x) { return x && String(x[k]) === String(v); })[0] || null;
  }

  // What changes the active pricetag: the hotel, the draft's order type / day / slot, the session, the table, and a
  // settled wallet read.
  function pricingInputs(st) {
    var c = st.checkout || {};
    return [st.hotel, c.orderType, c.day, c.slot, sessionUid(st), st.table, st.wallets];
  }
  function pricingInputsChanged(prev, next) {
    var a = pricingInputs(prev), b = pricingInputs(next);
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    return false;
  }

  // The order type the cart prices with: the draft's while this store offers it here, else the first offered one
  // (cart.jsx orderTypeOf + checkout-address.jsx offeredTypes: no DELIVERY on a real store without the Maps key).
  function pricingOrderType(hotel, store, draft) {
    var list = arr(store && store.orderTypes).filter(function (t) { return DRAFT_TYPES.indexOf(t) >= 0; });
    if (!list.length) list = ['PICKUP'];
    var maps = false;
    try { maps = !!(OO.delivery && typeof OO.delivery.enabled === 'function' && OO.delivery.enabled()); } catch (e) { maps = false; }
    var here = list.filter(function (t) { return t !== 'DELIVERY' || maps || fixtureStore(hotel); });
    if (here.length) list = here;
    var want = draft && draft.orderType;
    return list.indexOf(want) >= 0 ? want : list[0];
  }
  // The draft slot's instant (cart.jsx draftSlot + slotCheck): the draft's day and slot while still offered, else the
  // first ones; ASAP (or no slot) is `now`.
  function slotInstantOf(store, draft, orderType, at) {
    if (OO.mode === 'kiosk') return at instanceof Date ? at.getTime() : at;
    // P2-23: a chosen time by its value (OO.schedule.chosen: any window day, past midnight too), as the checkout finds it.
    var hit = OO.schedule.chosen(store, orderType, draft, at);
    if (hit) return OO.model.slotInstant(store, hit.slot, at);
    var opts = OO.model.pickupOptions(store, at, { orderType: orderType }) || {};
    var days = arr(opts.days);
    var day = findBy(days, 'key', draft && draft.day) || days[0] || null;
    var slot = day ? (findBy(day.slots, 'value', draft && draft.slot) || arr(day.slots)[0] || null) : null;
    return OO.model.slotInstant(store, slot, at);
  }
  // POLISH-3 (p5-followups #18): a table order (mode qr, a table context of the cart's store) is ASAP: it has no time to
  // choose (the table checkout's CS.time.tableSlot), so its pricetag and its order moment are judged at now, never at
  // the draft's day / slot (left from an online visit).
  function tableOrder(st) {
    var t = st && st.table;
    return qrMode() && !!t && !!st.hotel && sameStore(t.hotel, st.hotel);
  }
  // Does a tag of the order's domain have hours of its own (only then is the slot's instant worth computing)?
  function tagsHaveHours(tags, orderType, table) {
    var domain = OO.pricetags.domainOf(orderType, OO.mode, table ? { table: true } : undefined);
    return arr(tags && tags.rows).some(function (r) {
      return !!r && r.active === true && r.domain === domain && OO.model.parseRules(r.rules).length > 0;
    });
  }

  // ---- the order's moment (W5-FIX #1; see the header, "Order moment") ----
  // The cache's menu normalised for st's draft slot in its order type's domain (cart.jsx slotCheck's menu), or null
  // until the hotel's store and menu are cached. POLISH-3: a table order's at now (its ASAP table slot, as the cart's
  // and the table checkout's line checks judge it).
  function momentMenu(st) {
    var h = st && st.hotel;
    var e = h ? entryOf(h) : null;
    if (!e || !e.store || !e.rawMenu) return null;
    try {
      var orderType = pricingOrderType(h, e.store, st.checkout);
      var t = now();
      return menuAt(h, { now: tableOrder(st) ? t : slotInstantOf(e.store, st.checkout, orderType, t), domain: domain(orderType) });
    } catch (x) { return null; }
  }
  // `menu` with `lines`' products and combos judged at st's order moment: what every re-price of the cart uses.
  function atMoment(menu, lines, st) {
    try { return OO.cart.atMoment(menu, momentMenu(st), lines); } catch (x) { return menu; }
  }
  // Every row's free units of a line, in row order (its picks' rows for a combo).
  function freeOf(line) {
    var rows = line && line.kind === 'combo' ? arr(line.picks).reduce(function (t, p) { return t.concat(arr(p && p.modifiers)); }, []) : arr(line && line.modifiers);
    return rows.map(function (m) { return Number(m && m.free) || 0; }).join(',');
  }
  function sameFree(a, b) {
    return a.length === b.length && a.every(function (l, i) { return !!b[i] && l.key === b[i].key && freeOf(l) === freeOf(b[i]); });
  }
  // The patch giving st's cart the free units of its order moment (OO.cart.sanitize over momentMenu: every row's free
  // re-allocated, prices kept), or null while that menu isn't in or no row's free moves.
  function freePatch(st) {
    var lines = st && st.cart ? arr(st.cart.lines) : [];
    if (!lines.length) return null;
    if (separateRedemptions(st.hotel) && !catalogueReady(entryOf(st.hotel))) return null;
    var snap = momentMenu(st);
    if (!snap) return null;
    var next;
    try { next = OO.cart.sanitize(lines, snap); } catch (x) { return null; }
    return sameFree(next, lines) ? null : { cart: Object.assign({}, st.cart, { lines: next }) };
  }
  function settleFree(hotel) {
    set(function (st) { return st.hotel && sameStore(st.hotel, hotel) ? freePatch(st) : null; });
  }

  // The Selection for state `st` at `at`, or null while it isn't known (see the header): the hotel's store, menu and
  // pricetags not loaded yet, or a signed-in customer whose wallet read hasn't settled.
  function selectionFor(st, at) {
    var h = st.hotel;
    var e = h ? entryOf(h) : null;
    var t = h ? tagReads[storeKey(h)] : null;
    if (!e || !e.store || !e.menu || !t || !t.data || !OO.pricetags) return null;
    if (cartNeedsCatalogue(h, st) && separateRedemptions(h) && !catalogueReady(e)) return null;
    var uid = sessionUid(st);
    var wallets = st.wallets || {};
    var id = uid ? walletKey(h, uid) : null;
    if (id && !e.store.orderingQr && !own(wallets, id)) return null;
    var store = e.store;
    var orderType = pricingOrderType(h, store, st.checkout);
    var qrTag = OO.orderingQr && OO.orderingQr.select(t.data, store, orderType);
    if (qrTag) return qrTag;
    var table = tableOrder(st);
    // POLISH-3: a table order's tag is judged at `at` (now: it is ASAP), whatever the draft's slot.
    var when = !table && tagsHaveHours(t.data, orderType, table) ? slotInstantOf(store, st.checkout, orderType, at) : at;
    return OO.pricetags.select(t.data, {
      orderType: orderType, mode: OO.mode, table: table, now: when, timeZone: store.timezone,
      member: store.orderingQr ? null : OO.pricetags.memberOf(id ? wallets[id] : null),
    });
  }

  function raiseNotice(hotel, keys) { noticeSeq += 1; return { seq: noticeSeq, hotel: hotel, keys: keys.slice() }; }

  // The patch that re-prices `st`'s cart at the active pricetag, or null: not known yet, or the cart is already priced
  // with its record. Never throws (a state change must never fail on pricing).
  function pricingPatch(st) {
    if (!st.hotel) return null;
    var sel;
    try { sel = selectionFor(st, now()); } catch (x) { sel = null; }
    if (!sel) return null;
    selections[storeKey(st.hotel)] = sel;
    try {
      var base = entryOf(st.hotel).menu;
      var priced = OO.pricetags.priceMenu(base, sel);
      var rec = sanitizePricing(priced && priced.pricetag);
      if (sameRecord(rec, st.cart.pricing)) return null;
      // P2-01's contract (OO.pricetags.repriceCart: OO.cart.reprice at the tag), at the order's moment (W5-FIX #1).
      var r = OO.cart.reprice(st.cart.lines, atMoment(priced, st.cart.lines, st));
      var patch = { cart: cartWith(st.cart, r.lines, rec) };
      if (r.changed.length) patch.priceNotice = raiseNotice(st.hotel, r.changed);
      return patch;
    } catch (x) {
      if (window.console) console.error('[online-order] cart reprice failed:', x);
      return null;
    }
  }

  // `menu` at the cart's pricing (the record its lines are priced with): what new lines and page reprices use.
  function atCartPricing(menu) {
    if (!menu || !OO.pricetags) return menu;
    return OO.pricetags.priceMenu(menu, state.cart.pricing || null);
  }
  // A line about to be added, priced at the cart's pricing from the hotel's menu at the order's moment (W5-FIX #1; a line
  // the menu can't rebuild stays).
  function priceLine(line) {
    var e = state.hotel ? entryOf(state.hotel) : null;
    if (!line || !e || !e.menu) return line;
    try { return OO.cart.reprice([line], atMoment(atCartPricing(e.menu), [line], state)).lines[0] || line; } catch (x) { return line; }
  }

  function syncPricing(hotel) {
    var key = hotel === undefined || hotel === null ? null : keyOf(hotel);
    var before = state.priceNotice;
    set(function (st) { return st.hotel && (key === null || sameStore(st.hotel, key)) ? pricingPatch(st) : null; });
    var n = state.priceNotice;
    return n && n !== before ? n.keys.slice() : [];
  }
  function ackPriceNotice(seq) {
    var n = state.priceNotice;
    if (!n || (seq !== undefined && seq !== null && n.seq !== seq)) return false;
    set({ priceNotice: null });
    return true;
  }
  function wallet(hotel) {
    var key = hotel === undefined || hotel === null || hotel === '' ? state.hotel : keyOf(hotel);
    var uid = sessionUid(state);
    var id = key && uid ? walletKey(key, uid) : null;
    return id && own(state.wallets || {}, id) ? state.wallets[id] : null;
  }

  // ---- the wallet points in use (P2-07; see the header: state.points) ----
  // The entry of the signed-in customer at `hotel` (default: the current one), the hotel part lower-cased (walletKey);
  // null signed out. walletsOf (above) keeps one account's entries, as it does for the wallet reads.
  function pointsKey(hotel) {
    var key = hotel === undefined || hotel === null || hotel === '' ? state.hotel : keyOf(hotel);
    var uid = sessionUid(state);
    return key && uid ? walletKey(key, uid) : null;
  }
  function wholePoints(v) { var n = Math.floor(Number(v)); return isFinite(n) && n > 0 ? n : 0; }
  function walletPoints(hotel) {
    var id = pointsKey(hotel);
    return id && own(state.points || {}, id) ? wholePoints(state.points[id]) : 0;
  }
  function setWalletPoints(points, hotel) {
    var id = pointsKey(hotel);
    if (!id) return 0;
    var n = wholePoints(points);
    var cur = state.points || {};
    if ((own(cur, id) ? cur[id] : 0) === n) return n;
    var next = Object.assign({}, cur);
    if (n > 0) next[id] = n; else delete next[id];
    set({ points: next });
    return n;
  }
  // `points` without any entry of `hotel` (every account's): an order went (cartClear, settleOrder).
  function pointsWithout(points, hotel) {
    var key = storeKey(hotel);
    var out = {};
    Object.keys(points || {}).forEach(function (k) { if (k.slice(0, k.lastIndexOf('|')) !== key) out[k] = points[k]; });
    return out;
  }

  // ---- the pricing reads (see the header: loadPricetags, loadWallet) ----
  function loadPricetags(hotel, opts) {
    var key = keyOf(hotel);
    var id = storeKey(key); // one read per store whatever the case (W5-FIX #2); sent with the key as given
    var fresh = !!(opts && opts.fresh);
    var cur = tagReads[id];
    if (cur && cur.p && !fresh) return cur.p;
    var read = { p: null, data: cur ? cur.data : null };
    tagReads[id] = read;
    read.p = Promise.resolve().then(function () {
      var api = OO.api;
      return api && typeof api.getPricetags === 'function' ? api.getPricetags(key, fresh ? { fresh: true } : undefined) : null;
    }).then(function (raw) {
      return OO.model.normalizePricetags(raw && raw.order, raw && raw.prices);
    }).then(null, function () {
      return OO.model.normalizePricetags(null, null); // soft: no tag
    }).then(function (pt) {
      if (tagReads[id] === read) {
        read.data = pt;
        if (fresh) syncPricing(key); // a re-read (pricetag_refresh_required) re-prices the cart at once
      }
      return pt;
    });
    return read.p;
  }
  function peekPricetags(hotel) { var t = tagReads[storeKey(hotel)]; return t ? t.data : null; }

  function usableWallet(w) { return isRecord(w) && w.status !== false && w.status !== 'false' ? w : null; }
  // A settled read joins state.wallets while its account is still signed in (an equal body changes nothing).
  function settleWallet(hotel, uid, w) {
    var id = walletKey(hotel, uid);
    set(function (st) {
      if (sessionUid(st) !== uid) return null;
      var cur = st.wallets || {};
      if (own(cur, id) && canon(cur[id]) === canon(w)) return null;
      var next = Object.assign({}, cur);
      next[id] = w;
      return { wallets: next };
    });
  }
  function loadWallet(hotel, opts) {
    var key = keyOf(hotel);
    var uid = sessionUid(state);
    if (!key || !uid) return Promise.resolve(null);
    var id = walletKey(key, uid);
    var fresh = !!(opts && opts.fresh);
    var p = walletReads[id];
    if (!p || fresh) {
      p = walletReads[id] = Promise.resolve().then(function () {
        var api = OO.api;
        if (typeof api.getWallet === 'function') return api.getWallet(key, { uid: uid, fresh: fresh });
        return api.getLoyalty(key, fresh ? { uid: uid, fresh: true } : { uid: uid }).then(function (r) { return r && r.wallet; });
      }).then(usableWallet, function () { return null; });
    }
    return p.then(function (w) {
      if (walletReads[id] === p) settleWallet(key, uid, w); // a newer (fresh) read wins
      return w;
    });
  }

  // P3-27: see the header. The memo is keyed by the order alone: logout and another account clear it.
  function walletAfterOrder(hotel, orderId) {
    var id = orderId === undefined || orderId === null ? '' : String(orderId).trim();
    if (!id || !keyOf(hotel) || !sessionUid(state)) return Promise.resolve(null);
    if (!own(afterOrder, id)) afterOrder[id] = loadWallet(hotel, { fresh: true });
    return afterOrder[id];
  }

  // P3-FIX #10: see the header (ownOrders). Keyed like the wallet reads; dropMemberCaches empties it.
  function ownOrders(hotel, opts) {
    var key = keyOf(hotel);
    var uid = sessionUid(state);
    if (!key || !uid) return Promise.resolve(null);
    var id = walletKey(key, uid);
    var kept = own(orderReads, id) ? orderReads[id] : null;
    if (kept && !(opts && opts.fresh)) return kept;
    var p = orderReads[id] = Promise.resolve().then(function () {
      return OO.api.getOrders(key, { uid: uid });
    }).then(function (raw) {
      var list = OO.model.normalizeOrders(raw, { now: now() });
      return { uid: uid, ids: list.map(function (o) { return o.id; }) };
    });
    p.then(null, function () {
      if (orderReads[id] !== p) return; // a newer read, or another session's caches since
      if (kept) orderReads[id] = kept;
      else delete orderReads[id];
    });
    return p;
  }

  // Starts the cached pricing reads once their store id is known. Capable shells may overlap them with a pending
  // menu; syncPricing still requires both store and menu, and loadHotel kicks again when the menu lands.
  function kickLoads(st) {
    var h = st.hotel;
    var e = h ? entryOf(h) : null;
    if (!e || !e.store) return;
    if (!e.menu && !(independentMenu() && e.menuP && e.menuChannel === menuChannel(h)
      && e.menuChannel !== null && !(tableChannel(e.menuChannel) && tableOrderingOff(e.store)))) return;
    var waits = [loadPricetags(h)];
    if (sessionUid(st)) waits.push(loadWallet(h));
    Promise.all(waits).then(function () { syncPricing(h); }, function () { /* both reads are soft */ });
  }

  // `menu` at the cart's pricing for the current hotel; the same object while neither changes (useHotel's menu).
  function pricedMenu(hotel, menu) {
    if (!menu || !OO.pricetags) return menu;
    var key = storeKey(hotel);
    var rec = sameStore(state.hotel, key) ? state.cart.pricing || null : null;
    var m = pricedMemo[key];
    if (m && m.base === menu && m.rec === rec) return m.out;
    var out = m && m.base === menu && sameRecord(m.rec, rec) ? m.out : OO.pricetags.priceMenu(menu, rec);
    pricedMemo[key] = { base: menu, rec: rec, out: out };
    return out;
  }
  function selection(hotel) {
    var key = storeKey(hotel === undefined || hotel === null || hotel === '' ? state.hotel : hotel);
    return key && sameStore(state.hotel, key) && own(selections, key) ? selections[key] : null;
  }

  function reload() {
    var h = state.hotel;
    reconciled = false; // a page reload runs the session reconcile again (OO.data.reconcileSession)
    state = fresh();
    if (h) {
      state = Object.assign({}, state, { hotel: h });
      if (state.table && !sameStore(state.table.hotel, h)) { state.table = null; writeKey(K.table, null); }
      state.cart = loadCart(h, state.table); // P4-04: mode qr's for the table that stays
      var freed = freePatch(state); // W5-FIX #1: the free units at the order's moment (a cached menu)
      if (freed) state = Object.assign({}, state, freed);
    }
    emit();
    return state;
  }

  OO.store = {
    get: get,
    set: set,
    subscribe: subscribe,
    setHotel: setHotel,
    lastHotel: lastHotel,
    cartAdd: cartAdd,
    cartSetQty: cartSetQty,
    cartInc: cartInc,
    cartDec: cartDec,
    cartRemove: cartRemove,
    cartReplace: cartReplace,
    cartClear: cartClear,
    cartReprice: cartReprice,
    syncPricing: syncPricing,
    ackPriceNotice: ackPriceNotice,
    wallet: wallet,
    walletPoints: walletPoints,
    setWalletPoints: setWalletPoints,
    setCoupon: setCoupon,
    cartLines: cartLines,
    cartCount: cartCount,
    cartSubtotal: cartSubtotal,
    setCheckout: setCheckout,
    clearCheckout: clearCheckout,
    setSession: setSession,
    logout: logout,
    session: session,
    chain: chain,
    branch: branch,
    setBranch: setBranch,
    cartOf: cartOf,
    readIds: readIds,
    markRead: markRead,
    markAllRead: markAllRead,
    setLastOrder: setLastOrder,
    clearLastOrder: clearLastOrder,
    settleOrder: settleOrder,
    releaseCouponTxn: releaseCouponTxn,
    setTable: setTable,
    clearTable: clearTable,
    table: table,
    tableFor: tableFor,
    TABLE_TTL_MS: TABLE_TTL_MS,
    KEYS: K,
    __reload: reload,
  };

  // ---- hotel data core (non-hook) -------------------------------------------------------------------
  // hotel → { store, menu, storeP, menuP, rawMenu, menuDomain, menuMinute, snapshots, storeReadAt, menuReadAt (device
  // ms of the last store / menu read), storeKey, rereadP, freshMenuP }
  var cache = {};
  var STORE_REREAD_MS = 15 * 60000;
  var MENU_MAX_AGE_MS = 15 * 60000;

  function entry(hotel) { return cache[hotel] || (cache[hotel] = { store: null, menu: null, storeP: null, menuP: null }); }
  function keyOf(hotel) { return String(hotel == null ? '' : hotel); }
  function entryOf(key) { return key && own(cache, key) ? cache[key] : null; }
  // The device clock, for the AGE of what is cached (never OO.data.now(): a fixture clock may stand still).
  function clockMs() { return Date.now(); }

  function domain(orderType, mode, opts) { return OO.model.domain(orderType, mode, opts); }

  // ---- load errors (see the header: errorKind, loadErrorState) -----------------------------------------------
  function errorKind(err) {
    if (!err) return null;
    if (typeof err.kind === 'string' && err.kind) return err.kind;
    var s = err.status === null || err.status === undefined || err.status === '' ? NaN : Number(err.status);
    if (!isFinite(s)) return 'unavailable'; // no status: an unusable body made the normaliser throw (R-API-4)
    if (s === 0) return 'network';
    if (s === 404) return 'notFound';
    if (s >= 400 && s < 500 && s !== 408 && s !== 425 && s !== 429) return 'client';
    return 'unavailable';
  }
  // The rejection itself, with its kind stamped on: callers read status / message / data off the same object. Only a
  // rejection that isn't an object is wrapped.
  function withKind(err) {
    var e = err && typeof err === 'object' ? err : new Error(typeof err === 'string' && err ? err : 'Load failed');
    if (!(typeof e.kind === 'string' && e.kind)) {
      try { e.kind = errorKind(e); } catch (x) { /* a frozen error: errorKind still answers for it */ }
    }
    return e;
  }
  // The facade's "the chosen adapter lacks this method" (src/data/api-select.jsx notYet()).
  function notYet(err) { return !!err && err.message === 'Not available yet' && err.status === 0; }
  function loadErrorState(err) {
    if (!err) return null;
    var kind = errorKind(err);
    if (kind === 'notLive' || kind === 'notFound') return { state: kind, kind: kind, retry: false };
    return { state: 'unavailable', kind: kind, retry: !notYet(err) };
  }

  function now() {
    try {
      var t = OO.api && typeof OO.api.now === 'function' ? OO.api.now() : null;
      if (t != null) return t instanceof Date ? new Date(t.getTime()) : new Date(t);
    } catch (e) { /* the device clock */ }
    return new Date();
  }
  function minuteOf(t) { return Math.floor((t instanceof Date ? t.getTime() : Number(t)) / 60000); }

  // Every availability flag of a menu, in a fixed order (the same raw body always normalises in the same order).
  function availabilityKey(menu) {
    var out = [];
    function flags(groups) {
      arr(groups).forEach(function (g) {
        out.push(g.available === false ? 0 : 1);
        arr(g.options).forEach(function (o) { out.push(o.available === false ? 0 : 1); });
      });
    }
    arr(menu && menu.categories).forEach(function (c) { out.push(c.available ? 1 : 0); });
    Object.keys((menu && menu.products) || {}).forEach(function (k) {
      var p = menu.products[k];
      out.push(p.available ? 1 : 0, p.soldOut ? 1 : 0);
      flags(p.groups);
    });
    Object.keys((menu && menu.combos) || {}).forEach(function (k) {
      var c = menu.combos[k];
      out.push(c.available ? 1 : 0, c.availableToday ? 1 : 0, c.todayOnly ? 1 : 0);
      arr(c.groups).forEach(function (g) { arr(g.options).forEach(function (o) { out.push(o.available === false ? 0 : 1); flags(o.groups); }); });
    });
    return out.join('');
  }
  function normalizeAt(e, t, dom) {
    return OO.model.normalizeMenu(e.rawMenu, { now: t, timeZone: e.store.timezone, domain: dom });
  }

  // The serving backend guarantees get_menu resolves its own store and does not depend on request timeZone.
  // Older shells omit the capability and keep the sequential path. QR still needs its metadata/table gate.
  function independentMenu() {
    return !!(OO.boot && OO.boot.capabilities && OO.boot.capabilities.menuIndependentOfMetadata === true);
  }

  function separateRedemptions(hotel) {
    return !!(OO.boot && OO.boot.capabilities && OO.boot.capabilities.redemptionCatalogueV1 === true)
      && !OO.landingMode && (OO.mode === 'online' || OO.mode === 'app') && !(OO.orderingQr && OO.orderingQr.active())
      && OO.api && OO.api.isLiveFor(hotel);
  }
  function catalogueReady(e) {
    return !!(e && e.rawMenu && Array.isArray(e.rawMenu.redemption_products) && Array.isArray(e.rawMenu.redemption_coupon_ids));
  }
  // A restored cart and every purchasing page keep the complete catalogue. Only an empty-cart browse opts out.
  function cartNeedsCatalogue(hotel, st) {
    return !!(st && sameStore(st.hotel, hotel) && st.cart && (st.cart.coupon || arr(st.cart.lines).length));
  }
  function ensureRedemptions(key) {
    var e = entryOf(key);
    if (!separateRedemptions(key) || catalogueReady(e)) return Promise.resolve(e && e.menu);
    if (!e || !e.store || !e.rawMenu) return loadHotel(key).then(function (r) { return r.menu; });
    if (e.redemptionP) return e.redemptionP;
    var tok = e.menuTok, rawMenu = e.rawMenu;
    var p = Promise.resolve().then(function () { return OO.api.getRedemptionCatalogue(key); }).then(function (raw) {
      // A fresh menu, invalidation or a branch/channel change owns its own catalogue; never attach an old read.
      if (cache[key] !== e || e.menuTok !== tok || e.rawMenu !== rawMenu) {
        if (e.redemptionP === p) e.redemptionP = null;
        return loadHotel(key).then(function (r) { return r.menu; });
      }
      if (!raw || !Array.isArray(raw.redemption_products) || !Array.isArray(raw.redemption_coupon_ids)) throw new Error('Reward catalogue unavailable');
      var merged = Object.assign({}, rawMenu, { redemption_products: raw.redemption_products, redemption_coupon_ids: raw.redemption_coupon_ids });
      var at = now();
      var menu = OO.model.normalizeMenu(merged, { now: at, timeZone: e.store.timezone, domain: e.menuDomain });
      e.rawMenu = merged;
      e.menu = menu;
      e.menuMinute = minuteOf(at);
      e.snapshots = {};
      if (state.hotel === key) { settleFree(key); kickLoads(state); syncPricing(key); }
      notify(key);
      return menu;
    }).catch(function (x) { throw withKind(x); });
    e.redemptionP = p;
    function clear() { if (e.redemptionP === p) e.redemptionP = null; }
    p.then(clear, clear);
    return p;
  }

  function loadHotel(hotel, opts) {
    opts = opts || {};
    var withMenu = opts.withMenu !== false;
    var key = String(hotel == null ? '' : hotel);
    if (!key) {
      var err = new Error('Store not found');
      err.status = 404;
      err.kind = 'notFound';
      err.data = { status: false, error: 'Store not found' };
      return Promise.reject(err);
    }
    if (opts.force) invalidateHotel(key);
    var withRedemptions = opts.withRedemptions !== false || !separateRedemptions(key) || cartNeedsCatalogue(key, state);
    var e = entry(key);
    if (!e.storeP) {
      e.storeP = OO.api.getRestaurant(key).then(function (raw) {
        e.store = OO.model.normalizeStore(raw, { now: opts.now != null ? opts.now : now() });
        if (OO.orderingQr && state.hotel === key) OO.orderingQr.seedDraft(e.store);
        e.tableSettings = tableSettingsOf(raw); // P4-03 (see the header: tableSettings)
        e.storeReadAt = clockMs();
        if (cache[key] === e && state.hotel === key) kickLoads(state);
        return e.store;
      }).catch(function (x) { throw withKind(x); });
      e.storeP.catch(function () { if (cache[key] === e) delete cache[key]; });
    }
    // The entry's CURRENT store (and menu): the 15-minute re-read may have replaced the one storeP resolved with, and a
    // store-only page must never adopt (and re-theme to) the stale one.
    if (!withMenu) return e.storeP.then(function () { return { store: e.store, menu: e.menu }; });
    // P4-02: the menu of the channel that applies now (menuChannel); none in mode qr without a table context. Another
    // channel's menu in the entry is dropped and read again (see the header: "The menu's channel").
    var chan = menuChannel(key);
    if (chan === null) return e.storeP.then(function () { return { store: e.store, menu: null }; });
    if (e.menuP && e.menuChannel !== chan) dropMenu(e);
    if (!e.menuP) {
      var tok = e.menuTok = (e.menuTok || 0) + 1;
      e.menuChannel = chan;
      // Keep an early failure handled while metadata is pending (including a metadata failure). Normalisation,
      // error presentation and every cart/pricing action still wait for the authoritative store response.
      var earlyMenu = independentMenu() && !tableChannel(chan) ? Promise.resolve().then(function () {
        return OO.api.getMenu.apply(OO.api, menuArgs(key, chan, false, withRedemptions));
      }).then(function (raw) { return { ok: true, raw: raw }; }, function (error) { return { ok: false, error: error }; }) : null;
      e.menuP = e.storeP.then(function (store) {
        // P4-02: a store that takes no table orders gets no table menu read (the backend would build one per request;
        // table.jsx's gate says so): the context goes, no menu.
        if (tableChannel(chan) && tableOrderingOff(e.store || store)) {
          if (e.menuTok === tok) { e.menuP = null; e.menuChannel = null; }
          tableGone(key, chan);
          return null;
        }
        var menuRead = earlyMenu ? earlyMenu.then(function (result) {
          if (!result.ok) throw result.error;
          return result.raw;
        }) : OO.api.getMenu.apply(OO.api, menuArgs(key, chan, false, withRedemptions));
        return menuRead.then(function (raw) {
          if (e.menuTok !== tok) return e.menu; // the channel moved on: a newer read owns the entry
          var at = opts.now != null ? opts.now : now(); // a Date / ms (or a wall-clock string in tests)
          e.rawMenu = raw;
          if (raw.ordering_qr && OO.orderingQr) { e.store = OO.orderingQr.decorateStore(e.store, raw.ordering_qr); OO.orderingQr.seedDraft(e.store); }
          e.menuDomain = opts.domain || domain('PICKUP');
          e.menuMinute = minuteOf(at instanceof Date || typeof at === 'number' ? at : now());
          e.snapshots = {};
          e.menu = OO.model.normalizeMenu(raw, { now: at, timeZone: store.timezone, domain: e.menuDomain });
          e.menuReadAt = clockMs();
          tableMenuLanded(key, chan, raw); // P4-02: the table group's exclusions (get_menu's) onto the context
          if (state.hotel === key) {
            settleFree(key); // W5-FIX #1: a cart read before its menu gets its free units at the order's moment
            kickLoads(state); // P2-02: the pricing reads need the store and its menu
          }
          return e.menu;
        }, function (x) {
          // P4-02: a table menu's 404 (the body branch: the table isn't this store's active table any more) is the
          // table's, not the store's: the context goes (table.jsx checks the table again and gates), no menu. QR-OFF:
          // the store takes no table orders (tableOffAnswer): its table ordering goes off here (tableOff), no menu.
          var off = tableChannel(chan) && tableOffAnswer(x);
          if (!off && (!tableChannel(chan) || Number(x && x.status) !== 404)) throw x;
          if (e.menuTok === tok) { e.menuP = null; e.menuChannel = null; }
          if (off) tableOff(key, chan); else tableGone(key, chan);
          return null;
        });
      }).catch(function (x) { throw withKind(x); });
      e.menuP.catch(function () { if (cache[key] === e && e.menuTok === tok) e.menuP = null; });
    }
    // The entry's CURRENT menu: refreshMenu may have replaced the one menuP first resolved with.
    return Promise.all([e.storeP, e.menuP]).then(function () {
      return withRedemptions && e.menu && e.menuChannel === chan && separateRedemptions(key) && !catalogueReady(e) ? ensureRedemptions(key) : null;
    }).then(function () {
      if (cache[key] !== e) return loadHotel(key, { withRedemptions: withRedemptions });
      return { store: e.store, menu: e.menuChannel === chan ? e.menu : null };
    });
  }

  // ---- the menu's channel (P4-02; see the header: "The menu's channel") ----------------------------------------------
  function menuChannel(hotel) {
    if (!qrMode()) return 'online';
    var t = tableFor(hotel);
    return t ? 'table:' + t.id : null;
  }
  function tableChannel(chan) { return typeof chan === 'string' && chan.indexOf('table:') === 0; }
  function tableOrderingOff(store) { return !!store && store.tableOrdering === false; }
  // OO.api.getMenu's arguments for a channel: (hotel) online, as ever; (hotel, { tableId }) for a table; fresh adds
  // { fresh: true }.
  function menuArgs(key, chan, fresh, withRedemptions) {
    var o = {};
    if (tableChannel(chan)) o.tableId = Number(chan.slice(6));
    if (fresh) o.fresh = true;
    if (withRedemptions === false && separateRedemptions(key) && !tableChannel(chan)) o.withRedemptions = false;
    return Object.keys(o).length ? [key, o] : [key];
  }
  function dropMenu(e) {
    e.menuTok = (e.menuTok || 0) + 1;
    e.menuP = null;
    e.freshMenuP = null;
    e.redemptionP = null;
    e.menu = null;
    e.rawMenu = null;
    e.snapshots = {};
    e.menuReadAt = 0;
    e.menuChannel = null;
  }
  // A table menu came (get_menu, body table_id): its excluded_category_ids (when it sends them: since af31ad6e4)
  // become the context's (E19's until now) and feed the upsell (OO.upsell.exclude; P2-19's hook), so the
  // suggestions and Popular drop what the menu dropped.
  function tableMenuLanded(hotel, chan, raw) {
    if (!tableChannel(chan) || !raw || !Array.isArray(raw.excluded_category_ids)) return;
    var id = Number(chan.slice(6));
    var ids = [];
    raw.excluded_category_ids.forEach(function (v) { var n = posInt(v); if (n !== null && ids.indexOf(n) < 0) ids.push(n); });
    var t = state.table;
    if (t && t.id === id && sameStore(t.hotel, hotel) && t.excludedCategoryIds.join(',') !== ids.join(',')) {
      set({ table: Object.assign({}, t, { excludedCategoryIds: ids }) });
    }
    try { if (OO.upsell && typeof OO.upsell.exclude === 'function') OO.upsell.exclude(ids, hotel); } catch (x) { /* the upsell is optional */ }
  }
  // A table menu answered 404: the context of that table goes.
  function tableGone(hotel, chan) {
    var t = state.table;
    if (t && 'table:' + t.id === chan && sameStore(t.hotel, hotel)) set({ table: null });
  }
  // QR-OFF: a table menu read's "this store takes no table orders" (the adapters' kind 'tableOff', else the body's
  // reason 'table_ordering_off'), and what it does (see the header: "The menu's channel").
  function tableOffAnswer(x) {
    if (!x || typeof x !== 'object') return false;
    if (x.kind === 'tableOff') return true;
    var r = x.data && typeof x.data === 'object' ? x.data.reason : null;
    return String(r === undefined || r === null ? '' : r).trim().toLowerCase() === 'table_ordering_off';
  }
  function tableOff(hotel, chan) {
    var e = entryOf(keyOf(hotel));
    if (e && e.store && e.store.tableOrdering !== false) {
      e.store = Object.assign({}, e.store, { tableOrdering: false });
      e.storeKey = storeKeyOf(e.store); // a re-read saying "off" too is no change; one saying "on" replaces it
      if (e.tableSettings) e.tableSettings = Object.assign({}, e.tableSettings, { enabled: false });
      notify(keyOf(hotel));
    }
    tableGone(hotel, chan);
    if (!e || !e.store) return;
    var t = clockMs();
    if (e.tableOffAt && t - e.tableOffAt < STORE_REREAD_MS) return; // read again already: no loop
    e.tableOffAt = t;
    rereadStore(hotel);
  }

  // P4-03: the store's table-side switches from the raw getRestaurant body (OO.model.tableSettings), or null.
  function tableSettingsOf(raw) {
    try { return OO.model.tableSettings ? OO.model.tableSettings(raw) : null; } catch (x) { return null; }
  }
  function tableSettings(hotel) {
    var e = entryOf(keyOf(hotel));
    return e && e.store && e.tableSettings ? e.tableSettings : null;
  }

  function peekHotel(hotel, withMenu, withRedemptions) {
    var key = String(hotel == null ? '' : hotel);
    var e = cache[key];
    if (!e || !e.store) return null;
    if (withMenu !== false && (!e.menu || e.menuChannel !== menuChannel(key))) return null; // P4-02: this channel's only
    if (withMenu !== false && withRedemptions !== false && separateRedemptions(key) && !catalogueReady(e)) return null;
    return { store: e.store, menu: e.menu };
  }

  function invalidateHotel(hotel) {
    if (hotel === undefined) { cache = {}; return; }
    delete cache[String(hotel)];
  }

  function refreshMenu(hotel) {
    var e = cache[String(hotel == null ? '' : hotel)];
    if (!e || !e.menu) return null;
    if (!e.rawMenu || !e.store) return e.menu;
    var t = now();
    var minute = minuteOf(t);
    if (e.menuMinute === minute) return e.menu;
    e.menuMinute = minute;
    var next = normalizeAt(e, t, e.menuDomain);
    if (availabilityKey(next) !== availabilityKey(e.menu)) e.menu = next;
    return e.menu;
  }

  function menuAt(hotel, opts) {
    opts = opts || {};
    var e = cache[String(hotel == null ? '' : hotel)];
    if (!e || !e.rawMenu || !e.store) return null;
    var t = opts.now != null ? new Date(opts.now instanceof Date ? opts.now.getTime() : opts.now) : now();
    var dom = opts.domain || e.menuDomain;
    var k = dom + '|' + minuteOf(t);
    var snaps = e.snapshots || (e.snapshots = {});
    if (!snaps[k]) {
      var keys = Object.keys(snaps);
      if (keys.length >= 12) delete snaps[keys[0]]; // a small LRU-ish bound: slots change rarely
      snaps[k] = normalizeAt(e, t, dom);
    }
    return snaps[k];
  }

  // ---- watchers + the 15-minute store re-read (see the header: watchHotel, rereadStore) ---------------------------
  var REREAD_CHECK_MS = 60000;                              // the check cadence: nothing is fetched until a store is due
  var PAUSED_PAGES = ['checkout', 'payment-callback'];
  var CLOCK_FIELDS = { serverTime: 1, open: 1, pickup: 1 }; // normalised from the clock: never a change on their own
  var watchers = []; // [{ hotel, fn }], in subscription order
  var poll = null;   // { timer, doc, onVisible } while anything is watched

  function pageVisible() {
    var doc = typeof document !== 'undefined' && document ? document : null;
    if (!doc) return true;
    return typeof doc.visibilityState === 'string' ? doc.visibilityState === 'visible' : !doc.hidden;
  }
  function rereadPaused() {
    try {
      var r = OO.router && OO.router.current ? OO.router.current() : null;
      return !!r && PAUSED_PAGES.indexOf(r.page) >= 0;
    } catch (e) { return false; }
  }
  // The store without its clock fields, as text: the same text = an unchanged store.
  function storeKeyOf(store) {
    return JSON.stringify(store, function (k, v) { return this === store && own(CLOCK_FIELDS, k) ? undefined : v; });
  }
  function notify(key) {
    watchers.slice().forEach(function (w) {
      if (w.hotel !== key) return;
      try { w.fn(key); } catch (e) { if (window.console) console.error('[online-order] hotel watcher failed:', e); }
    });
  }
  function checkStores() {
    if (!pageVisible() || rereadPaused()) return;
    var t = clockMs();
    var seen = [];
    watchers.forEach(function (w) {
      if (seen.indexOf(w.hotel) >= 0) return;
      seen.push(w.hotel);
      var e = entryOf(w.hotel);
      if (e && e.store && !e.rereadP && t - (e.storeReadAt || 0) >= STORE_REREAD_MS) rereadStore(w.hotel);
    });
  }
  // setInterval / clearInterval are resolved when called (a page always has them; a node:vm test may not).
  function startPoll() {
    if (poll) return;
    var doc = typeof document !== 'undefined' && document && typeof document.addEventListener === 'function' ? document : null;
    poll = {
      timer: typeof setInterval === 'function' ? setInterval(checkStores, REREAD_CHECK_MS) : null,
      doc: doc,
      onVisible: function () { if (pageVisible()) checkStores(); },
    };
    if (doc) doc.addEventListener('visibilitychange', poll.onVisible);
  }
  function stopPoll() {
    if (!poll) return;
    if (poll.timer !== null && typeof clearInterval === 'function') clearInterval(poll.timer);
    if (poll.doc && typeof poll.doc.removeEventListener === 'function') poll.doc.removeEventListener('visibilitychange', poll.onVisible);
    poll = null;
  }

  function watchHotel(hotel, fn) {
    var key = keyOf(hotel);
    if (!key || typeof fn !== 'function') return function () {};
    var w = { hotel: key, fn: fn };
    watchers.push(w);
    startPoll();
    return function unwatch() {
      var i = watchers.indexOf(w);
      if (i < 0) return;
      watchers.splice(i, 1);
      if (!watchers.length) stopPoll();
    };
  }

  function rereadStore(hotel) {
    var key = keyOf(hotel);
    var e = entryOf(key);
    if (!e || !e.store) return Promise.resolve(null);
    if (e.rereadP) return e.rereadP;
    e.storeReadAt = clockMs(); // the attempt counts as the read: a failing store is asked again in 15 minutes
    var p = Promise.resolve().then(function () {
      return OO.api.getRestaurant(key, { fresh: true });
    }).then(function (raw) {
      var next = OO.model.normalizeStore(raw, { now: now() });
      var nextKey = storeKeyOf(next);
      if (cache[key] === e) e.tableSettings = tableSettingsOf(raw); // P4-03: follows every read, the Store changed or not
      if (e.storeKey === undefined) e.storeKey = storeKeyOf(e.store);
      if (nextKey !== e.storeKey && cache[key] === e) {
        e.store = next;
        e.storeKey = nextKey;
        notify(key);
        syncPricing(key); // its order types or timezone may have moved the pricetag (P2-02)
      }
      return e.store;
    }).catch(function () { return e.store; }); // a failed re-read keeps the cached store, quietly
    e.rereadP = p;
    p.then(function () { if (e.rereadP === p) e.rereadP = null; });
    return p;
  }

  // ---- a fresh menu before submit (see the header: ensureFreshMenu) ------------------------------------------------
  function ensureFreshMenu(hotel, opts) {
    var key = keyOf(hotel);
    var maxAge = opts && opts.maxAgeMs != null && isFinite(Number(opts.maxAgeMs)) ? Math.max(0, Number(opts.maxAgeMs)) : MENU_MAX_AGE_MS;
    var e = entryOf(key);
    var chan = menuChannel(key); // P4-02: the channel that applies now (another one's menu is no fresh menu here)
    if (e && tableChannel(chan) && tableOrderingOff(e.store)) { tableGone(key, chan); return Promise.resolve(null); } // as loadHotel
    if (!e || !e.store || !e.menu || !e.rawMenu || e.menuChannel !== chan) return loadHotel(key).then(function (res) { return res.menu; });
    if (clockMs() - (e.menuReadAt || 0) <= maxAge) return ensureRedemptions(key);
    if (e.freshMenuP) return e.freshMenuP;
    var tok = e.menuTok;
    var p = Promise.resolve().then(function () {
      return OO.api.getMenu.apply(OO.api, menuArgs(key, chan, true));
    }).then(function (raw) {
      var t = now();
      var menu = OO.model.normalizeMenu(raw, { now: t, timeZone: e.store.timezone, domain: e.menuDomain });
      if (e.menuTok !== tok) return menu; // the channel moved on meanwhile: this menu isn't the entry's any more
      e.rawMenu = raw;
      if (raw.ordering_qr && OO.orderingQr) { e.store = OO.orderingQr.decorateStore(e.store, raw.ordering_qr); OO.orderingQr.seedDraft(e.store); }
      e.menu = menu;
      e.menuMinute = minuteOf(t);
      e.snapshots = {};
      e.menuReadAt = clockMs();
      if (cache[key] === e) {
        tableMenuLanded(key, chan, raw); // P4-02
        syncPricing(key); // new base prices may move the tag's record (P2-02), before the watchers adopt the menu
        notify(key);
      }
      return menu;
    }, function (x) {
      // QR-OFF: the store takes no table orders (tableOffAnswer): as loadHotel, its table ordering goes off, no menu.
      if (!tableChannel(chan) || !tableOffAnswer(x)) throw x;
      tableOff(key, chan);
      return null;
    }).catch(function (x) { throw withKind(x); });
    e.freshMenuP = p;
    var done = function () { if (e.freshMenuP === p) e.freshMenuP = null; };
    p.then(done, done);
    return p;
  }

  // ---- the session reconcile (see the header: reconcileSession) ----------------------------------------------------
  var reconciled = false; // once per page load; __reload() re-arms it
  function reconcileSession(hotel) {
    var key = keyOf(hotel);
    var s = state.session;
    var uid = s && s.customer && s.customer.uid ? String(s.customer.uid) : '';
    var live = false;
    try { live = !!key && !!OO.api && typeof OO.api.isLiveFor === 'function' && !!OO.api.isLiveFor(key); } catch (x) { live = false; }
    if (reconciled || !uid || !live || typeof OO.api.getCustomer !== 'function') return Promise.resolve('skipped');
    reconciled = true;
    if (fixtureUid(uid)) { // a session the fixture adapter made: signed out here, never sent (see the header)
      logout();
      return Promise.resolve('signedOut');
    }
    // Still the account the call was made for (a logout or another login may have happened meanwhile).
    function sameAccount() {
      var cur = state.session;
      return !!(cur && cur.customer && String(cur.customer.uid || '') === uid);
    }
    return Promise.resolve().then(function () {
      return OO.api.getCustomer(key, uid);
    }).then(function (raw) {
      var c = OO.model.normalizeCustomer(raw);
      if (!c || !sameAccount()) return 'kept';
      if (!c.uid) c.uid = uid;
      if (String(c.uid) !== uid) return 'kept';
      var cur = state.session;
      if (JSON.stringify(c) === JSON.stringify(cur.customer)) return 'kept'; // nothing new: no write, no re-render
      set({ session: { customer: c, customToken: cur.customToken || null } });
      return 'updated';
    }, function (err) {
      if (errorKind(err) !== 'notFound' || !sameAccount()) return 'kept';
      logout();
      return 'signedOut';
    }).catch(function () { return 'kept'; });
  }

  // ---- chains (mode app, P4-16; see the header: "Chains") ----------------------------------------------------------
  var CHAIN_MAX_AGE_MS = 60000; // B31 caches a chain 60 s per master and judges open-now per request
  var chainReads = {};          // lower-cased key → { key (as asked), p (the read in flight | null), raw, chain, at }
  var masterMiss = {};          // lower-cased master key → true: its store read failed (its branches keep their own theme)

  function appMode() { return OO.mode === 'app'; }

  function loadChain(key, opts) {
    var k = keyOf(key).trim();
    if (!k) return Promise.resolve(null);
    var id = k.toLowerCase();
    var e = own(chainReads, id) ? chainReads[id] : (chainReads[id] = { key: k, p: null, raw: null, chain: null, at: 0 });
    if (e.p) return e.p;
    if (!(opts && opts.fresh) && e.chain && clockMs() - e.at < CHAIN_MAX_AGE_MS) return Promise.resolve(e.chain);
    // POLISH-4: the request goes at once (src/app.jsx's chain read is this one too); a throw is a rejection.
    var sent;
    try { sent = Promise.resolve(OO.api.getChain(k)); } catch (x) { sent = Promise.reject(x); }
    var p = e.p = sent.then(function (raw) {
      var c = OO.model.normalizeChain(raw, { now: now(), current: k });
      if (e.p === p) { e.raw = raw; e.chain = c; e.at = clockMs(); e.p = null; }
      return c;
    }, function (x) {
      if (e.p === p) e.p = null; // the chain read before it stays
      throw withKind(x);
    });
    return p;
  }
  function chainEntry(key) {
    var k = keyOf(key).trim().toLowerCase();
    return k && own(chainReads, k) && chainReads[k].chain ? chainReads[k] : null;
  }
  function peekChain(key, opts) {
    var e = chainEntry(key);
    if (!e) return null;
    if (!opts || !(opts.origin || opts.byDistance)) return e.chain;
    return OO.model.normalizeChain(e.raw, { now: now(), current: e.key, origin: opts.origin || null, byDistance: !!opts.byDistance });
  }
  function chainStore(chain, key) {
    if (!chain || !given(key)) return null;
    return arr(chain.stores).filter(function (s) { return !!s && sameStore(s.key, key); })[0] || null;
  }
  function chainFor(hotel) {
    if (!given(hotel)) return null;
    var first = peekChain(masterOf(hotel));
    if (chainStore(first, hotel)) return first;
    var ids = Object.keys(chainReads);
    for (var i = 0; i < ids.length; i++) {
      var c = chainReads[ids[i]].chain;
      if (chainStore(c, hotel)) return c;
    }
    return null;
  }

  // The current route's chain / branch (mode app; P4-11's route.chain and route.hotel).
  function chain() {
    var r = appMode() ? currentRoute() : null;
    return r && given(r.chain) ? peekChain(r.chain) : null;
  }
  function branch() {
    var r = appMode() ? currentRoute() : null;
    return r && given(r.chain) && given(r.hotel) ? String(r.hotel) : null;
  }
  function setBranch(key, chainKey) {
    if (!appMode() || !OO.router || typeof OO.router.setBranch !== 'function') return false;
    var r = currentRoute();
    var c = given(chainKey) ? String(chainKey).trim() : r && given(r.chain) ? String(r.chain) : null;
    if (!c) return false;
    var k = given(key) ? String(key).trim() : null;
    var known = null;
    try { known = OO.router.chainOf(c); } catch (x) { known = null; }
    known = known && known.state === 'known' ? known : null;
    if (k) {
      var cached = peekChain(c);
      if (known && !known.keys.some(function (x) { return sameStore(x, k); })) return false;
      if (!known && cached && cached.source && !chainStore(cached, k)) return false;
    }
    return !!OO.router.setBranch(known && given(known.master) ? String(known.master) : c, k);
  }
  function cartOf(hotel) {
    var h = keyOf(hotel);
    var c = !h ? null : sameStore(h, state.hotel) ? state.cart : qrMode() ? null : loadCart(h); // POLISH-6: any case
    var lines = c ? arr(c.lines) : [];
    return { hotel: h || null, lines: lines, count: OO.cart.count(lines), subtotal: OO.cart.subtotal(lines) };
  }

  // The store whose theme and branding `hotel`'s pages wear (see the header: masterOf). Never reads a chain.
  function masterOf(hotel) {
    var h = keyOf(hotel);
    if (!h || !appMode() || !OO.router || typeof OO.router.chainOf !== 'function') return h || null;
    var c = null;
    try { c = OO.router.chainOf(h); } catch (x) { c = null; }
    if (c && c.state === 'known') return given(c.master) ? String(c.master) : h;
    if (c && (c.state === 'unknown' || c.state === 'notLive')) return h;
    var r = currentRoute();
    return r && given(r.chain) && sameStore(r.hotel, h) ? String(r.chain) : h;
  }
  // Mode app: the cached Store `hotel`'s pages are themed with (its master's); null while the master's store isn't in;
  // the hotel's own `store` when its master is the hotel itself or the master's read failed.
  function themeStoreOf(hotel, store) {
    var m = masterOf(hotel);
    if (!m || sameStore(m, hotel)) return store;
    var e = entryOf(m);
    if (e && e.store) return e.store;
    return masterMiss[storeKey(m)] ? store : null;
  }
  // The master's store read (store only) for its branches' theme; a failure is remembered (masterMiss), never thrown.
  function loadMaster(master) {
    return loadHotel(master, { withMenu: false }).then(function (res) {
      delete masterMiss[storeKey(master)];
      return res.store;
    }, function () {
      masterMiss[storeKey(master)] = true;
      return null;
    });
  }
  function loyaltyScope(hotel) {
    if (!appMode() || !given(hotel)) return {};
    var c = chainFor(hotel);
    if (!c) return {};
    var mk = c.master && given(c.master.key) ? String(c.master.key) : null;
    var e = mk ? entryOf(mk) : null;
    return { chain: c, master: e && e.store ? e.store : null };
  }

  function themeKey() {
    try {
      var r = OO.router && OO.router.current ? OO.router.current() : null;
      var q = r && r.query ? r.query : {};
      return typeof q.theme === 'string' ? q.theme.toLowerCase().trim() : '';
    } catch (e) { return ''; }
  }

  var activated = null;
  var lastActivation = null; // [hotel, store, opts] of the last activateHotel, for reactivate()
  function wl() { return OO.whitelabel && typeof OO.whitelabel.storeFor === 'function' ? OO.whitelabel : null; }
  function wlStore(hotel, store) { var W = wl(); return W && store ? W.storeFor(hotel, store) : store; }
  function loadFonts(theme) {
    var weights = OO.theme.fontWeights || function () { return null; };
    OO.theme.fontsOf(theme).forEach(function (f) {
      var w = weights(theme, f);
      if (w) OO.theme.loadGoogleFont(f, w, typeof OO.theme.fontItalic === 'function' && OO.theme.fontItalic(theme, f)); else OO.theme.loadGoogleFont(f); // WL-VEL: + italics
    });
  }
  function applyHead(store) { var W = wl(); if (W && typeof W.applyHead === 'function') W.applyHead(store); }
  function activateHotel(hotel, store, opts) {
    opts = opts || {};
    if (!opts.again) lastActivation = [hotel, store, opts];
    store = wlStore(hotel, store);
    var mode = opts.mode || OO.mode || 'online';
    var studioOnly = mode === 'online' || mode === 'landing' || mode === 'kiosk';
    var tk = studioOnly ? '' : (opts.themeKey !== undefined ? String(opts.themeKey || '').toLowerCase() : themeKey());
    if (mode === 'app') return activateApp(hotel, store, opts, tk);
    var marker = String(hotel) + '|' + tk + '|' + mode + '|' + (store && store.id);
    if (opts.cart !== false && !opts.again) setHotel(hotel);
    // The same store OBJECT too: a re-read that replaced the store (rereadStore) brings its theme along.
    if (activated && activated.marker === marker && activated.store === store) return activated.result;
    // Online has one theme authority: Studio. A missing document (or library) never restores the old builder/preset.
    var theme = studioOnly ? (store && store.theme && store.theme.whitelabel ? store.theme : { isConfigured: false }) : OO.theme.resolve(store && store.theme, tk);
    var vars = mode === 'kiosk' && OO.kiosk ? OO.kiosk.style(store) : OO.theme.toCssVars(theme, mode);
    OO.theme.apply(vars);
    loadFonts(theme);
    applyHead(store);
    activated = { marker: marker, store: store, result: { theme: theme, vars: vars } };
    return activated.result;
  }
  // Mode app (P4-16; see the header: activateHotel): the chain master's theme, idempotent per that store object.
  function activateApp(hotel, store, opts, tk) {
    if (opts.cart !== false && !opts.again) setHotel(hotel);
    var themed = opts.cart === false ? store : wlStore(hotel, themeStoreOf(hotel, store));
    if (!themed) { // the master's store is on its way: no other store's theme stays on screen meanwhile
      if (activated) { OO.theme.apply({}); activated = null; }
      return { theme: null, vars: {} };
    }
    var marker = 'app|' + tk + '|' + themed.id;
    if (activated && activated.marker === marker && activated.store === themed) return activated.result;
    var theme = OO.theme.resolve(themed.theme, tk);
    var vars = OO.theme.toCssVars(theme, 'app');
    OO.theme.apply(vars);
    loadFonts(theme);
    applyHead(themed);
    activated = { marker: marker, store: themed, result: { theme: theme, vars: vars } };
    return activated.result;
  }

  function deactivateHotel() {
    if (OO.theme && OO.theme.apply) OO.theme.apply({});
    activated = null;
    lastActivation = null;
    applyHead(null);
  }
  function reactivate() {
    if (!lastActivation) return null;
    // again: the theme only (the cart and the remembered store are the page's business, and may have moved on).
    return activateHotel(lastActivation[0], lastActivation[1], Object.assign({}, lastActivation[2], { again: true }));
  }

  OO.data.now = now;
  OO.data.domain = domain;
  OO.data.errorKind = errorKind;
  OO.data.loadErrorState = loadErrorState;
  OO.data.loadHotel = loadHotel;
  OO.data.refreshMenu = refreshMenu;
  OO.data.menuAt = menuAt;
  OO.data.peekHotel = peekHotel;
  OO.data.tableSettings = tableSettings;
  OO.data.invalidateHotel = invalidateHotel;
  OO.data.watchHotel = watchHotel;
  OO.data.rereadStore = rereadStore;
  OO.data.ensureFreshMenu = ensureFreshMenu;
  OO.data.reconcileSession = reconcileSession;
  OO.data.dropFixtureSession = dropFixtureSession;
  OO.data.STORE_REREAD_MS = STORE_REREAD_MS;
  OO.data.MENU_MAX_AGE_MS = MENU_MAX_AGE_MS;
  OO.data.loadPricetags = loadPricetags;
  OO.data.peekPricetags = peekPricetags;
  OO.data.loadWallet = loadWallet;
  OO.data.walletAfterOrder = walletAfterOrder;
  OO.data.ownOrders = ownOrders;
  OO.data.pricedMenu = pricedMenu;
  OO.data.selection = selection;
  OO.data.activateHotel = activateHotel;
  OO.data.deactivateHotel = deactivateHotel;
  OO.data.reactivate = reactivate;
  OO.data.themeKey = themeKey;
  OO.data.loadChain = loadChain;
  OO.data.peekChain = peekChain;
  OO.data.chainStore = chainStore;
  OO.data.chainFor = chainFor;
  OO.data.masterOf = masterOf;
  OO.data.loyaltyScope = loyaltyScope;
  OO.data.CHAIN_MAX_AGE_MS = CHAIN_MAX_AGE_MS;

  // ---- hooks ------------------------------------------------------------------------------------
  function shallowEqual(a, b) {
    if (Object.is(a, b)) return true;
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) !== Array.isArray(b)) return false;
    var ka = Object.keys(a), kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (var i = 0; i < ka.length; i++) if (!Object.prototype.hasOwnProperty.call(b, ka[i]) || !Object.is(a[ka[i]], b[ka[i]])) return false;
    return true;
  }

  function identity(s) { return s; }

  // getSnapshot must return the SAME value while nothing changed: React 18 re-calls it after commit and compares
  // with Object.is, so a selector that maps to fresh objects would otherwise re-render forever. Cache on (state,
  // selector) identity first; a recomputed value that is shallow-equal to the cached one is reused too.
  OO.useStore = function useStore(selector) {
    var React = window.React;
    var cacheRef = React.useRef(null);
    var sel = typeof selector === 'function' ? selector : identity;
    function snapshot() {
      var c = cacheRef.current;
      if (c && c.state === state && c.sel === sel) return c.v;
      var v = sel(state);
      if (c && shallowEqual(c.v, v)) v = c.v;
      cacheRef.current = { state: state, sel: sel, v: v };
      return v;
    }
    return React.useSyncExternalStore(subscribe, snapshot);
  };

  OO.useAsync = function useAsync(fn, deps) {
    var React = window.React;
    var st = React.useState({ data: undefined, error: null, loading: true });
    var nonce = React.useState(0);
    var fnRef = React.useRef(fn);
    fnRef.current = fn;
    React.useEffect(function () {
      var alive = true;
      st[1](function (s) { return s.loading && !s.error ? s : { data: s.data, error: null, loading: true }; });
      Promise.resolve().then(function () { return fnRef.current(); }).then(
        function (data) { if (alive) st[1]({ data: data, error: null, loading: false }); },
        function (error) { if (alive) st[1]({ data: undefined, error: error, loading: false }); }
      );
      return function () { alive = false; };
    }, (deps || []).concat([nonce[0]]));
    var reloadFn = React.useCallback(function () { nonce[1](function (n) { return n + 1; }); }, []);
    return { data: st[0].data, error: st[0].error, loading: st[0].loading, reload: reloadFn };
  };

  function readyState(key, res) {
    return { key: key, loading: false, error: null, kind: null, notFound: false, store: res.store, menu: res.menu || null };
  }
  function loadingState(key) { return { key: key, loading: true, error: null, kind: null, notFound: false, store: null, menu: null }; }
  // notFound stays "a 404" (see the header): kind tells an unknown store from one that isn't live here.
  function failedState(key, err) {
    return { key: key, loading: false, error: err, kind: errorKind(err), notFound: !!err && err.status === 404, store: null, menu: null };
  }

  OO.useHotel = function useHotel(hotel, opts) {
    var React = window.React;
    var withMenu = !opts || opts.withMenu !== false;
    var hk = String(hotel == null ? '' : hotel);
    var observed = OO.useStore(function (s) {
      return { pricing: s.hotel === hk ? s.cart.pricing || null : null, wallets: s.wallets, table: s.table, catalogue: cartNeedsCatalogue(hk, s) };
    });
    var separated = separateRedemptions(hk);
    var withRedemptions = !opts || opts.withRedemptions !== false || !separated || observed.catalogue;
    var progressive = withMenu && !!(opts && opts.progressive) && OO.mode === 'app' && independentMenu();
    // P4-02: the menu's channel is part of the key (mode qr: the table's; the online key is unchanged), so a context that
    // arrives or moves to another table loads that table's menu.
    var chan = withMenu ? menuChannel(hotel) : null;
    var key = hk + '|' + (withMenu ? 'menu' + (chan === 'online' ? '' : '|' + chan) + (separated ? (withRedemptions ? '|full' : '|browse') : '') : 'store');
    var tk = themeKey();
    var st = React.useState(function () { var c = peekHotel(hotel, withMenu, withRedemptions); return c ? readyState(key, c) : loadingState(key); });
    var nonce = React.useState(0);
    var forcedAt = React.useRef(0);
    var cached = peekHotel(hotel, withMenu, withRedemptions);
    var view = st[0].key === key ? st[0] : (cached ? readyState(key, cached) : loadingState(key));
    // P2-02: re-render when the cart's pricing or a wallet read changes (the menu below follows the pricing; the
    // summaries the pages render follow the wallet); P4-02: and the table context (the menu's channel follows it).
    // WL-2: the chain's white-label document (read beside the store; the page re-renders when it lands or changes).
    var W = wl();
    var wlRev = W ? W.useRevision() : 0;
    React.useEffect(function () { if (W && hotel) W.ensure(hotel); }, [hk]);

    React.useEffect(function () {
      var alive = true;
      var setSt = st[1];
      var force = nonce[0] !== forcedAt.current;
      forcedAt.current = nonce[0];
      if (!hotel) {
        setSt({ key: key, loading: false, error: null, kind: 'notFound', notFound: true, store: null, menu: null });
        return function () { alive = false; };
      }
      // Switch the cart NOW (a branch switch must never show or write the previous store's cart while loading).
      setHotel(hotel, { remember: false });
      var complete = false;
      var pending = loadHotel(hotel, { withMenu: withMenu, withRedemptions: withRedemptions, force: force });
      if (progressive) loadHotel(hotel, { withMenu: false }).then(function (res) {
        if (!alive || complete) return;
        activateHotel(hotel, res.store, { themeKey: themeKey() });
        setSt(Object.assign(readyState(key, { store: res.store, menu: null }), { menuLoading: true }));
      }, function () { /* the full read presents the metadata error */ });
      pending.then(function (res) {
        complete = true;
        if (!alive) return;
        activateHotel(hotel, res.store, { themeKey: themeKey() });
        setSt(readyState(key, res));
        reconcileSession(hotel); // a live store only, once per page load; it never rejects
      }, function (err) {
        complete = true;
        if (!alive) return;
        setSt(failedState(key, err));
      });
      return function () { alive = false; };
    }, [key, nonce[0], progressive]);

    // The 15-minute store re-read and ensureFreshMenu replace the cached store / menu: adopt them (the theme effect
    // below re-applies a new store's theme). Watching also keeps the re-read running while this hotel is on screen.
    React.useEffect(function () {
      if (!hotel) return undefined;
      var setSt = st[1];
      return watchHotel(hotel, function () {
        var c = peekHotel(hotel, false);
        if (!c) return;
        setSt(function (cur) {
          if (cur.key !== key || !cur.store) return cur;
          var completeMenu = !separated || !withRedemptions || catalogueReady(entryOf(hk));
          var menu = withMenu && completeMenu && c.menu ? c.menu : cur.menu;
          return c.store === cur.store && menu === cur.menu ? cur : Object.assign({}, cur, { store: c.store, menu: menu });
        });
      });
    }, [key]);

    // P4-16 (mode app): the chain master's store, read beside the hotel's (store only; the page doesn't wait for it). The
    // theme follows it once it is in (themed: the store activateHotel themes with), or the hotel's own when it failed.
    var master = appMode() && hotel ? masterOf(hotel) : null;
    var mk = master && !sameStore(master, hotel) ? master : null;
    var masterSt = React.useState(0);
    React.useEffect(function () {
      var e = mk ? entryOf(mk) : null;
      if (!mk || (e && e.store)) return undefined;
      var alive = true;
      loadMaster(mk).then(function () { if (alive) masterSt[1](function (n) { return n + 1; }); });
      return function () { alive = false; };
    }, [mk]);
    var themed = mk && view.store ? themeStoreOf(hotel, view.store) : null;

    // ?theme= can change without a refetch (in-app navigation keeps the cache).
    React.useEffect(function () {
      if (view.store) activateHotel(hotel, view.store, { themeKey: tk });
    }, [hotel, tk, view.store, themed, wlRev]);

    // Availability moves with the clock: every minute, and when the page is shown again, re-check the cached menu
    // (refreshMenu) and adopt it when it changed. The store object stays, so the theme effect above doesn't re-run.
    React.useEffect(function () {
      if (!hotel || !withMenu) return undefined;
      var setSt = st[1];
      function check() {
        var m = refreshMenu(hotel);
        if (m) setSt(function (cur) { return cur.key === key && cur.menu && cur.menu !== m ? Object.assign({}, cur, { menu: m }) : cur; });
        syncPricing(hotel); // a tag's own hours, an ASAP order's clock (P2-02)
      }
      var timer = typeof setInterval === 'function' ? setInterval(check, 60000) : null;
      var doc = typeof document !== 'undefined' && document && document.addEventListener ? document : null;
      function onVisible() { if (!doc.hidden) check(); }
      if (doc) doc.addEventListener('visibilitychange', onVisible);
      return function () {
        if (timer) clearInterval(timer);
        if (doc) doc.removeEventListener('visibilitychange', onVisible);
      };
    }, [key]);

    var reloadFn = React.useCallback(function () { nonce[1](function (n) { return n + 1; }); }, []);
    return {
      loading: view.loading, menuLoading: !!view.menuLoading, error: view.error, kind: view.kind || null, notFound: view.notFound,
      store: wlStore(hotel, view.store), menu: pricedMenu(hotel, view.menu), reload: reloadFn,
    };
  };

  // P4-16 (mode app): the chain and its master's store (see the header: OO.useChain, "Chains").
  OO.useChain = function useChain(key, opts) {
    var React = window.React;
    opts = opts || {};
    var k = key === undefined || key === null ? '' : String(key).trim();
    var origin = opts.origin && typeof opts.origin === 'object' ? opts.origin : null;
    var byDistance = !!opts.byDistance && !!origin;
    var freshRef = React.useRef(false);
    var res = OO.useAsync(function () {
      var fresh = freshRef.current;
      freshRef.current = false;
      return k ? loadChain(k, fresh ? { fresh: true } : undefined) : null;
    }, [k]);
    var e = chainEntry(k);
    var base = e ? e.chain : null;
    var lat = origin ? origin.lat : null, lng = origin ? origin.lng : null;
    var chainNow = React.useMemo(function () {
      return base && origin ? peekChain(k, { origin: origin, byDistance: byDistance }) : base;
    }, [base, lat, lng, byDistance]);
    var mk = chainNow && chainNow.master && given(chainNow.master.key) ? String(chainNow.master.key) : '';
    OO.useAsync(function () {
      return mk ? loadHotel(mk, { withMenu: false }).then(function (r) { return r.store; }) : null;
    }, [mk]);
    // WL-2: the chain's white-label document; the master comes back with it laid over (wlStore).
    var W = wl();
    var wlRev = W ? W.useRevision() : 0;
    React.useEffect(function () { if (W && k) W.ensure(k); }, [k]);
    var mc = mk ? peekHotel(mk, false) : null;
    var master = mc ? wlStore(mk, mc.store) : null;
    var tk = themeKey();
    var themeOn = !!opts.theme;
    React.useEffect(function () {
      if (themeOn && master) activateHotel(mk, master, { cart: false, themeKey: tk });
    }, [themeOn, master, tk, wlRev]);
    var reload = res.reload;
    var reloadFn = React.useCallback(function () { freshRef.current = true; reload(); }, [reload]);
    var err = chainNow ? null : res.error || null;
    var kind = err ? errorKind(err) : null;
    return {
      loading: !!k && !chainNow && !err, error: err, kind: kind, notFound: kind === 'notFound',
      chain: chainNow, master: master, reload: reloadFn,
    };
  };
})();


// ===================== src/lib/analytics.jsx =====================
// src/lib/analytics.jsx: OO.analytics (alias OO.lib.analytics): the store's analytics (GTM / GA4 / Meta Pixel) through a
// SANDBOXED tag frame (Batch 2 P5-06b; P5-00 registers the file; design: briefs b2-p5-06-common.md).
//
// Why (owner, 2026-10-05): a Google tag once showed customers a fake payment page in an iframe when they tapped Pay. No
// tag ever runs in this SPA's document, and the SPA's CSP never names a tag host. Tags run only inside ONE invisible
// iframe whose document the backend serves (P5-06a: GET {routeBase}/__tags?store={key}&mode={mode}, itself served with
// `Content-Security-Policy: sandbox allow-scripts`). The element carries sandbox="allow-scripts" and nothing else
// (never allow-same-origin: on a same-origin URL it would let the frame lift its own sandbox; never allow-top-navigation*,
// allow-popups*, allow-modals, allow-forms), so its origin is opaque: it can't touch this document, its storage or
// cookies, can't navigate the top window, and a 0×0 hidden frame can't draw anything a customer sees.
//
// The frame. __SIPO_BOOT.tags = null | { frame: '<absolute URL of the frame document, no query>' } (P5-06a; null while
// analytics is switched off). It is used only when it is an absolute http(s) URL without credentials, query or fragment,
// on THIS page's host (never http under an https page), whose path is exactly routeBase + '/__tags'; anything else is
// no frame (frameSrc). The frame for a store is {frame}?store={encoded key}&mode={OO.mode}. It exists only when ALL hold
// (blocker(route) → null):
//   * tags is on (above) and the page is NOT inside a native app shell: OO.platform.inShell(), or any trace of a shell
//     bridge (window.flutter_inappwebview, window.ReactNativeWebView, a truthy window.isFlutterWebView). Sticky for the
//     page load, checked at every route, event and frame load: flutter_inappwebview may expose its bridge to subframes,
//     and a tag could call the shell's getUserId handler with any id. A shell seen after the frame went up removes it at
//     once.
//   * the route names a store this host serves LIVE (OO.api.availabilityOf(hotel) === 'live': not a fixture store, not a
//     store that isn't live here, never a store-less route) and isn't redirecting (route.redirectTo: e.g. REF's
//     /order-confirmed and /success, whose query may name the customer).
//   * the page is not a no-tags page (NO_TAGS: checkout, payment-callback, and the store-less / dev pages not-found and
//     ui-gallery).
//   * no payment is in flight on this device: OO.store's lastOrder is an ONLINE_PAYMENT still awaiting its gateway
//     (clearCartOnSuccess, no settledAt), placed less than PAY_MS (60 min, payment-callback.jsx RECENT_MS) ago (an
//     unreadable placedAt counts as in flight). The payment callback (200 / a settling 202) or the order page's status
//     poll settles it.
// One frame at a time, appended to document.body: <iframe sandbox="allow-scripts" title="" aria-hidden="true"
// tabindex="-1" referrerpolicy="no-referrer" src="…">, styled position fixed, 0×0, border 0, pointer-events none,
// visibility hidden; `sandbox` is set BEFORE `src`, and a browser without iframe sandbox support gets no frame. Another
// store → a new frame (the old one goes at once, with any events still queued for it). The first frame waits until
// SETTLE_MS (1 s) after the window's load event (a native shell that sets its flag late, from onLoadStop, has done so
// by then); later ones go up at once. Entering a page where it may not exist (a no-tags page, a payment in flight, a
// store that isn't live): nothing more is posted, queued events are dropped, and the element is REMOVED after CLOSE_MS
// (2 s: a begin_checkout posted just before the checkout still gets sent; it is sandboxed meanwhile). A frame that loads
// a second time (a tag navigated it away from the frame document) is removed, and no frame goes up again in this page
// load.
// Messages: parent → frame only. This file never listens for `message` events (nothing in the bundle does; a test greps
// it), and the frame never posts back. postMessage's targetOrigin is '*' (unavoidable for an opaque origin), so no
// message carries PII or a capability:
//   { oo: 1, type: 'init', cid, fbp, fbc, mode }   once, after the frame's (first) load event.
//   { oo: 1, type: 'event', name, params }          per event; queued (QUEUE_MAX 50) until the init went.
// The ids (localStorage 'oo-tags-v1', every access try/catch'd; storage missing or throwing → ids for this page load
// only): cid = the GA client id '<31-bit random>.<unix seconds>', fbp = '_fbp' 'fb.1.<ms>.<31-bit random>', both made
// once; fbc = '_fbc' 'fb.1.<ms>.<fbclid>' from a route's ?fbclid= ([A-Za-z0-9._~-], ≤ 500), kept FBC_MS (90 days), null
// when none. Random numbers come from crypto.getRandomValues (Math.random only where it is missing).
// Events (GA4 names; the frame maps them to gtag, fbq and dataLayer): page_view (a page where the frame may exist, once
// per page: page|store|params), view_item, add_to_cart, begin_checkout, purchase. Every event's params get page_location,
// page_title and page_referrer added here; the caller's params are WHITELISTED (cleanParams): currency (ISO 4217),
// value / tax / shipping (numbers ≥ 0, 2 dp), item_count, items (≤ 50: item_id [A-Za-z0-9_-] ≤ 64, item_name and
// item_category ≤ 100 characters, price, quantity), transaction_id (only an id transactionId() made); anything else is
// dropped. page_location = this page's origin + routeBase + the route's TEMPLATE (pathOf: ids redacted to ':id',
// mode app's path key without its ?store=, an SEO item path as /{kind}/{numeric id}) + the query REDUCED to utm_*
// (values without '@'), gclid, fbclid, gbraid, wbraid — built from the parsed route, never from window.location's
// href (REF's ?email= links sit in the address bar until a page replaces them). page_title = a fixed title per page and
// mode (the document title may name an order). page_referrer = the previous page's page_location, the first page's =
// document.referrer reduced to its origin (none when it is this origin). An event whose page may not have the frame is
// dropped (false).
// Nothing here may break a page: every entry point swallows its own errors. With tags off (no __SIPO_BOOT.tags) every
// entry point returns at once: no storage, no timers, no listeners, no DOM.
//
// OO.analytics:
//   enabled() → bool   tags on (frameSrc's checks on __SIPO_BOOT.tags.frame pass).
//   frameSrc(hotel) → URL | null   the frame URL for a store (null: tags off, or no store).
//   blocker(route) → null (the frame may exist) | 'off' | 'preview' (a white-label studio preview, WL-2: never a frame)
//       | 'shell' | 'redirect' | 'store' | 'page' | 'payment'.
//   pathOf(route, mode?) → the route's redacted path under routeBase (default mode: OO.mode).
//   pageLocation(route) → page_location (above) | null when tags are off.   pageTitle(route) → page_title.
//   track(name, params) → bool   posts (or queues) one event; false when dropped (an unknown name, tags off, no frame here).
//   viewItem(item, store) → bool   view_item for a menu Product or Combo (OO.model): currency, value = its price, items
//       [{ item_id ('{id}', a combo 'combo-{id}'), item_name, price[, item_category: a combo's category] }].
//   addToCart(line, store) → bool   add_to_cart for a cart line (OO.cart): the same + quantity; value = price × qty. A
//       reward line (points) sends nothing.
//   beginCheckout(lines, total, store) → bool   begin_checkout: currency, value = total, item_count (Σ qty), items.
//   purchase(order, store) → bool   purchase for OO.store's lastOrder once it went through (a counter / cash / points
//       order at once; an ONLINE_PAYMENT once settled), ONCE per order on this device: transaction_id = transactionId,
//       value = order.total, currency = store.currencyCode, tax / shipping = order.tax / order.shipping when numbers
//       (checkout's lastOrder carries them). Marked sent when it is posted; one dropped or never posted goes again on a
//       later call.
//   transactionId(orderId) → string | null   a random 32-hex id for the order on this device (made once, kept in
//       'oo-tags-v1', at most TX_MAX 50 orders, the newest kept), never derived from the order id; null when tags are off.
//   ids() → { cid, fbp, fbc } | null (tags off).
//   onRoute(route, { redirecting }?)   a settled route (OO.App's): the frame follows it, and its page_view goes.
//   useRoute(route, { redirecting }?)   the hook OO.App mounts (one React.useEffect, on page|store|params): onRoute.
//   EVENTS, NO_TAGS (lists) · __reset() · __state() (tests).
// Pure apart from the frame: no JSX; no document, storage, timer or listener at load time (the tests run every src/lib
// file raw in node:vm). OO.router, OO.platform, OO.store and OO.api are read at call time.
// Load order (build/concat.mjs): after store.jsx (the store, platform and router pieces it reads), before the data
// layer and the pages that call it.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  // ---- constants ------------------------------------------------------------------------------------------------
  var KEY = 'oo-tags-v1';
  var EVENTS = ['page_view', 'view_item', 'add_to_cart', 'begin_checkout', 'purchase'];
  // The pages where the frame never exists: the payment pages, and the store-less / dev ones.
  var NO_TAGS = { 'checkout': 1, 'payment-callback': 1, 'not-found': 1, 'ui-gallery': 1 };
  var MODES = { online: 1, app: 1, qr: 1 };
  var QUEUE_MAX = 50;
  var CLOSE_MS = 2000;               // a page without the frame: it goes after this long
  var SETTLE_MS = 1000;              // the first frame: this long after the window's load event
  var PAY_MS = 3600000;              // a payment in flight: an awaiting lastOrder placed this recently
  var FBC_MS = 90 * 86400000;        // _fbc: 90 days
  var TX_MAX = 50;                   // orders kept in the transaction map
  var ITEMS_MAX = 50;
  var TEXT_MAX = 100;                // item_name, item_category
  var ID_MAX = 64;                   // item_id; an order id kept in the map
  var CLICK_ID = /^[A-Za-z0-9._~-]{1,500}$/;  // gclid, fbclid, gbraid, wbraid
  var UTM_KEY = /^utm_[A-Za-z0-9_]{1,40}$/;
  var UTM_MAX = 200;
  var CLICK_KEYS = { gclid: 1, fbclid: 1, gbraid: 1, wbraid: 1 };
  var TX_ID = /^[0-9a-f]{32}$/;
  // The fixed page titles (src/app.jsx TITLES and SUFFIX) and the legal pages' (LEGAL_TITLES).
  var TITLES = {
    'find-store': 'Find a store', 'payment-callback': 'Payment', 'not-found': 'Page not found', 'menu': 'Menu', 'cart': 'Cart',
    'checkout': 'Checkout', 'order-status': 'Order status', 'orders': 'Orders', 'profile': 'Profile', 'legal': 'Legal',
    'home': 'Home', 'stores': 'Stores', 'rewards': 'Rewards', 'booking': 'Book a table', 'notifications': 'Notifications',
    'account': 'Account', 'wallet': 'Wallet', 'track': 'Track your order', 'review': 'Rate your order', 'bookings': 'Your bookings',
    'booking-track': 'Track a booking', 'about': 'About', 'coupons': 'Coupons', 'deals': 'Deals', 'scan': 'Scan to order',
    'ui-gallery': 'UI kit',
  };
  var SUFFIX = { online: 'Order online', app: 'SIPO', qr: 'Order at your table' };
  var LEGAL = { terms: 'Terms and conditions', privacy: 'Privacy policy', allergy: 'Allergy information' };
  var LEGAL_SLUG = { terms: 'terms-and-conditions', privacy: 'privacy-policy', allergy: 'allergy-information' };
  var PLAIN = { account: 1, rewards: 1, booking: 1, wallet: 1, about: 1, coupons: 1, deals: 1, stores: 1, notifications: 1 };

  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function given(v) { return v !== undefined && v !== null && v !== ''; }
  function boot() { return window.__SIPO_BOOT || {}; }
  function mode() { return own(MODES, OO.mode) ? OO.mode : 'online'; }
  function lower(s) { return String(s).toLowerCase(); }
  function guard(fn, fallback) { try { return fn(); } catch (e) { return fallback; } }
  function doc() { return typeof document !== 'undefined' && document ? document : null; }
  function now() { return Date.now(); }
  function enc(s) { return encodeURIComponent(String(s)); }
  // '' or '/a/b' (as glue/router.jsx normBase): path only, leading slash, no trailing slash.
  function normBase(base) {
    var b = String(base == null ? '' : base).replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '').replace(/\/+$/, '');
    if (b && b.charAt(0) !== '/') b = '/' + b;
    return b;
  }

  // ---- the frame URL (see the header) --------------------------------------------------------------------------------
  var baseMemo = { raw: null, routeBase: null, host: null, out: null };
  function frameBase() {
    var t = boot().tags;
    if (!t || typeof t !== 'object' || typeof t.frame !== 'string' || typeof URL !== 'function') return null;
    var loc = window.location;
    var host = loc && typeof loc.host === 'string' ? loc.host : null;
    var rb = normBase(boot().routeBase);
    if (baseMemo.raw === t.frame && baseMemo.routeBase === rb && baseMemo.host === host) return baseMemo.out;
    var out = null;
    try {
      var u = new URL(t.frame);
      var https = loc && loc.protocol === 'https:';
      var ok = (u.protocol === 'https:' || (u.protocol === 'http:' && !https)) && !u.username && !u.password
        && !u.search && !u.hash && t.frame.indexOf('?') < 0 && t.frame.indexOf('#') < 0
        && !!host && u.host === host && u.pathname === rb + '/__tags';
      out = ok ? { origin: u.origin, url: u.origin + u.pathname } : null;
    } catch (e) { out = null; }
    baseMemo = { raw: t.frame, routeBase: rb, host: host, out: out };
    return out;
  }
  function frameSrc(hotel) {
    var b = frameBase();
    if (!b || !given(hotel)) return null;
    return b.url + '?store=' + enc(hotel) + '&mode=' + mode();
  }

  // ---- where the frame may exist (see the header) --------------------------------------------------------------------
  var shellSeen = false; // sticky for the page load
  function shellNow() {
    if (shellSeen) return true;
    var yes;
    try {
      var P = OO.platform;
      yes = !!(P && typeof P.inShell === 'function' && P.inShell()) || !!window.flutter_inappwebview
        || !!window.ReactNativeWebView || !!window.isFlutterWebView;
    } catch (e) { yes = true; } // can't tell: as the shell
    if (yes) shellSeen = true;
    return yes;
  }
  function payBusy() {
    var lo = guard(function () { return OO.store.get().lastOrder; }, null);
    if (!lo || typeof lo !== 'object') return false;
    if (String(lo.paymentMethod || '').toUpperCase() !== 'ONLINE_PAYMENT' || lo.clearCartOnSuccess !== true || lo.settledAt) return false;
    var age = now() - Number(lo.placedAt);
    return !(isFinite(age) && age > PAY_MS);
  }
  var broken = false; // a frame navigated away from the frame document: none again this page load
  function blocker(route) {
    if (!frameBase() || broken) return 'off';
    if (guard(function () { return OO.whitelabel.isPreview(); }, false)) return 'preview'; // WL-2: a studio preview
    if (shellNow()) return 'shell';
    if (!route || typeof route !== 'object' || route.redirectTo) return 'redirect';
    if (!given(route.hotel) || !OO.api || typeof OO.api.availabilityOf !== 'function' || OO.api.availabilityOf(route.hotel) !== 'live') return 'store';
    if (own(NO_TAGS, route.page)) return 'page';
    if (payBusy()) return 'payment';
    return null;
  }
  function liveRoute() {
    var R = OO.router;
    return R && typeof R.current === 'function' ? guard(function () { return R.current(); }, null) : null;
  }

  // ---- page_location / page_title / page_referrer (see the header) ---------------------------------------------------
  function pathOf(route, m) {
    var r = route || {};
    var p = r.params || {};
    var md = own(MODES, m) ? m : mode();
    var seg = md === 'app' && given(r.chain) ? r.chain : r.hotel;
    if (!given(seg)) return r.page === 'payment-callback' ? '/payment-callback' : '/';
    var H = '/' + enc(seg);
    switch (r.page) {
      case 'menu':
        if (md === 'qr') return given(p.tableId) ? H + '/table/:id' : H;
        if ((p.item === 'product' || p.item === 'combo') && /^\d{1,18}$/.test(String(p.itemId))) return H + '/' + p.item + '/' + p.itemId;
        return md === 'app' ? H + '/menu' : H;
      case 'cart': return H + '/cart';
      case 'checkout': return H + '/checkout';
      case 'payment-callback': return H + '/payment-callback';
      case 'orders': return H + '/orders';
      case 'order-status': return given(p.orderId) ? H + '/orders/:id' : H + '/orderStatus';
      case 'review': return H + '/orders/:id/review';
      case 'track': return H + '/track' + (given(p.orderId) ? '/:id' : '');
      case 'bookings': return H + '/bookings' + (given(p.reservationId) ? '/:id' : '');
      case 'booking-track': return H + '/booking/track' + (given(p.reservationId) ? '/:id' : '');
      case 'profile': return H + (p.edit ? '/edit-profile' : '/profile');
      case 'legal': return H + '/' + (own(LEGAL_SLUG, p.kind) ? LEGAL_SLUG[p.kind] : LEGAL_SLUG.terms);
      case 'ui-gallery': return H + '/__ui';
      default: return own(PLAIN, r.page) ? H + '/' + r.page : H; // scan, home, not-found → the store's landing
    }
  }
  // The query kept for page_location: utm_* (no '@': never an email), gclid, fbclid, gbraid, wbraid, in their order.
  function reducedQuery(query) {
    var q = query && typeof query === 'object' ? query : {};
    var parts = [];
    Object.keys(q).forEach(function (k) {
      var v = q[k];
      if (typeof v !== 'string') return;
      if (own(CLICK_KEYS, k)) { if (CLICK_ID.test(v)) parts.push(k + '=' + enc(v)); return; }
      if (!UTM_KEY.test(k)) return;
      var s = v.replace(/[\u0000-\u001f\u007f]/g, ' ').trim();
      if (s && s.length <= UTM_MAX && s.indexOf('@') < 0) parts.push(enc(k) + '=' + enc(s));
    });
    return parts.length ? '?' + parts.join('&') : '';
  }
  function pageLocation(route) {
    var b = frameBase();
    if (!b) return null;
    return b.origin + normBase(boot().routeBase) + pathOf(route) + reducedQuery(route && route.query);
  }
  function pageTitle(route) {
    var r = route || {};
    var t = r.page === 'legal' && r.params && own(LEGAL, r.params.kind) ? LEGAL[r.params.kind] : (own(TITLES, r.page) ? TITLES[r.page] : '');
    var s = SUFFIX[mode()];
    return t ? t + ' · ' + s : s;
  }
  var landing = null;
  function landingReferrer() {
    if (landing !== null) return landing;
    landing = guard(function () {
      var d = doc();
      var ref = d && typeof d.referrer === 'string' ? d.referrer : '';
      if (!ref) return '';
      var u = new URL(ref);
      var b = frameBase();
      if ((u.protocol !== 'https:' && u.protocol !== 'http:') || (b && u.origin === b.origin)) return '';
      return u.origin + '/';
    }, '');
    return landing;
  }
  function keyOf(route) {
    var r = route || {};
    return [r.page, r.hotel, r.chain, guard(function () { return JSON.stringify(r.params || {}); }, '')].join('|');
  }

  // ---- the ids (localStorage 'oo-tags-v1'; see the header) -----------------------------------------------------------
  var mem = null; // { cid, fbp, fbc, fbcAt, tx: { [orderId]: { id, at, sent } } }
  function storage() { try { return window.localStorage || null; } catch (e) { return null; } }
  function randomBytes(n) {
    var out = [];
    try {
      var c = window.crypto;
      if (c && typeof c.getRandomValues === 'function') {
        var a = c.getRandomValues(new Uint8Array(n));
        for (var i = 0; i < n; i++) out.push(a[i] & 255);
        return out;
      }
    } catch (e) { out = []; }
    for (var j = 0; j < n; j++) out.push(Math.floor(Math.random() * 256));
    return out;
  }
  function random31() { var b = randomBytes(4); return (b[0] & 127) * 16777216 + b[1] * 65536 + b[2] * 256 + b[3]; }
  function hex(bytes) { return bytes.map(function (b) { return (b < 16 ? '0' : '') + b.toString(16); }).join(''); }
  function cleanTx(raw) {
    var out = {};
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
    Object.keys(raw).slice(0, TX_MAX * 2).forEach(function (k) {
      var e = raw[k];
      if (!k || k.length > ID_MAX || !e || typeof e !== 'object' || !TX_ID.test(e.id)) return;
      out[k] = { id: e.id, at: typeof e.at === 'number' && isFinite(e.at) ? e.at : 0, sent: e.sent === true };
    });
    return out;
  }
  function load() {
    if (mem) return mem;
    var raw = null;
    var s = storage();
    if (s) raw = guard(function () { return JSON.parse(s.getItem(KEY) || 'null'); }, null);
    var r = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    var fbcOk = typeof r.fbc === 'string' && /^fb\.1\.\d{1,16}\.[A-Za-z0-9._~-]{1,500}$/.test(r.fbc) && typeof r.fbcAt === 'number';
    mem = {
      cid: typeof r.cid === 'string' && /^\d{1,10}\.\d{1,12}$/.test(r.cid) ? r.cid : null,
      fbp: typeof r.fbp === 'string' && /^fb\.1\.\d{1,16}\.\d{1,12}$/.test(r.fbp) ? r.fbp : null,
      fbc: fbcOk ? r.fbc : null,
      fbcAt: fbcOk ? r.fbcAt : 0,
      tx: cleanTx(r.tx),
    };
    trimTx();
    return mem;
  }
  function save() {
    var s = storage();
    if (!s || !mem) return;
    try { s.setItem(KEY, JSON.stringify({ v: 1, cid: mem.cid, fbp: mem.fbp, fbc: mem.fbc, fbcAt: mem.fbcAt, tx: mem.tx })); } catch (e) { /* this page load only */ }
  }
  function trimTx() {
    var ks = Object.keys(mem.tx);
    if (ks.length <= TX_MAX) return;
    ks.sort(function (a, b) { return mem.tx[b].at - mem.tx[a].at; }).slice(TX_MAX).forEach(function (k) { delete mem.tx[k]; });
  }
  function makeIds() {
    var m = load();
    var t = now();
    var dirty = false;
    if (!m.cid) { m.cid = random31() + '.' + Math.floor(t / 1000); dirty = true; }
    if (!m.fbp) { m.fbp = 'fb.1.' + t + '.' + random31(); dirty = true; }
    if (m.fbc && !(t - m.fbcAt <= FBC_MS)) { m.fbc = null; m.fbcAt = 0; dirty = true; }
    if (dirty) save();
    return { cid: m.cid, fbp: m.fbp, fbc: m.fbc || null };
  }
  // A route's ?fbclid= becomes _fbc (a new click replaces the old one).
  function noteClick(query) {
    var v = query && typeof query === 'object' && own(query, 'fbclid') ? query.fbclid : null;
    if (typeof v !== 'string' || !CLICK_ID.test(v)) return;
    var m = load();
    if (m.fbc && m.fbc.slice(m.fbc.indexOf('.', 5) + 1) === v) return;
    var t = now();
    m.fbc = 'fb.1.' + t + '.' + v;
    m.fbcAt = t;
    save();
  }
  function makeTx(orderId) {
    var k = typeof orderId === 'string' || typeof orderId === 'number' ? String(orderId).trim() : '';
    if (!k || k.length > ID_MAX) return null;
    var m = load();
    if (!own(m.tx, k)) {
      m.tx[k] = { id: hex(randomBytes(16)), at: now(), sent: false };
      trimTx();
      save();
    }
    return m.tx[k].id;
  }
  function knownTx(id) {
    if (typeof id !== 'string' || !TX_ID.test(id)) return false;
    var tx = load().tx;
    return Object.keys(tx).some(function (k) { return tx[k].id === id; });
  }

  // ---- params (the whitelist; see the header) ------------------------------------------------------------------------
  function amount(v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v < 1e9 ? Math.round(v * 100) / 100 : null; }
  function whole(v) { return typeof v === 'number' && isFinite(v) && v >= 0 && v <= 99999 && Math.floor(v) === v ? v : null; }
  function text(v, max) {
    if (typeof v !== 'string' && typeof v !== 'number') return '';
    return String(v).replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max);
  }
  function cleanItem(it) {
    if (!it || typeof it !== 'object') return null;
    var o = {};
    var id = text(it.item_id, ID_MAX);
    if (/^[A-Za-z0-9_-]+$/.test(id)) o.item_id = id;
    var name = text(it.item_name, TEXT_MAX);
    if (name) o.item_name = name;
    var price = amount(it.price);
    if (price !== null) o.price = price;
    var q = whole(it.quantity);
    if (q) o.quantity = q;
    var cat = text(it.item_category, TEXT_MAX);
    if (cat) o.item_category = cat;
    return o.item_id || o.item_name ? o : null;
  }
  function cleanParams(p) {
    var out = {};
    if (!p || typeof p !== 'object') return out;
    if (typeof p.currency === 'string' && /^[A-Z]{3}$/.test(p.currency)) out.currency = p.currency;
    ['value', 'tax', 'shipping'].forEach(function (k) { var n = amount(p[k]); if (n !== null) out[k] = n; });
    var c = whole(p.item_count);
    if (c !== null) out.item_count = c;
    if (knownTx(p.transaction_id)) out.transaction_id = p.transaction_id;
    if (Array.isArray(p.items)) {
      var items = p.items.slice(0, ITEMS_MAX).map(cleanItem).filter(Boolean);
      if (items.length) out.items = items;
    }
    return out;
  }
  function currencyOf(store) { return store && typeof store.currencyCode === 'string' ? store.currencyCode : undefined; }

  // ---- the frame (see the header) ------------------------------------------------------------------------------------
  var F = null;     // { el, key, ready, closing, timer, loads, onLoad }
  var queue = [];   // [{ msg, key, done, dropped }]
  var view = null;  // the page OO.App settled on: { key, location, title, referrer, viewed (its page_view went) }
  var loadAt = null, loadHooked = false, settleTimer = null;
  var unwatch = null, busyWas = false;

  function clearQueue() {
    var q = queue;
    queue = [];
    q.forEach(function (x) { if (x.dropped) guard(x.dropped); });
  }
  function drop(f) {
    if (!f) return;
    if (f.timer !== null) clearTimeout(f.timer);
    f.timer = null;
    f.closing = true;
    guard(function () { f.el.removeEventListener('load', f.onLoad); });
    guard(function () { if (f.el.parentNode) f.el.parentNode.removeChild(f.el); });
    if (F === f) { F = null; clearQueue(); }
  }
  // At once: tags off, a shell, a frame that navigated away.
  function stop() { drop(F); clearQueue(); }
  // A page where the frame may not exist: nothing more goes; the element goes after CLOSE_MS.
  function leave() {
    clearQueue();
    if (!F || F.closing) return;
    var f = F;
    f.closing = true;
    f.timer = setTimeout(function () { f.timer = null; drop(f); }, CLOSE_MS);
  }
  // When the window's load event was seen: at once when the document is complete, else from the event (hooked once).
  function noteLoad() {
    if (loadAt !== null) return;
    var d = doc();
    if (d && d.readyState === 'complete') { loadAt = now(); return; }
    if (!loadHooked && typeof window.addEventListener === 'function') {
      loadHooked = true;
      window.addEventListener('load', onWindowLoad);
    }
  }
  function settled() {
    noteLoad();
    return loadAt !== null && now() - loadAt >= SETTLE_MS;
  }
  function onWindowLoad() {
    guard(function () { window.removeEventListener('load', onWindowLoad); });
    if (loadAt === null) loadAt = now();
    waitSettle();
  }
  function waitSettle() {
    if (settleTimer !== null) return;
    noteLoad();
    if (loadAt === null) return; // the load event arms it
    settleTimer = setTimeout(function () { settleTimer = null; guard(reconcile); }, Math.max(0, SETTLE_MS - (now() - loadAt)));
  }
  function mount(src, key) {
    var d = doc();
    if (!d || !d.body || typeof d.createElement !== 'function') return null;
    var el = d.createElement('iframe');
    if (!el || !('sandbox' in el)) return null; // no iframe sandbox: never a frame
    el.setAttribute('sandbox', 'allow-scripts'); // before src
    el.setAttribute('title', '');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('tabindex', '-1');
    el.setAttribute('referrerpolicy', 'no-referrer');
    var s = el.style;
    if (s) {
      s.position = 'fixed';
      s.width = '0';
      s.height = '0';
      s.border = '0';
      s.pointerEvents = 'none';
      s.visibility = 'hidden';
    }
    var f = { el: el, key: key, ready: false, closing: false, timer: null, loads: 0, onLoad: null };
    f.onLoad = function () { guard(function () { loaded(f); }); };
    el.addEventListener('load', f.onLoad);
    el.setAttribute('src', src);
    d.body.appendChild(el);
    return f;
  }
  function postTo(f, msg) {
    var w = f.el.contentWindow;
    if (!w || typeof w.postMessage !== 'function') return false;
    w.postMessage(msg, '*');
    return true;
  }
  function loaded(f) {
    f.loads += 1;
    if (f !== F || f.closing) return;
    if (f.loads > 1) { broken = true; stop(); return; } // a tag navigated the frame away from the frame document
    if (shellNow()) { stop(); return; }
    var i = makeIds();
    if (!postTo(f, { oo: 1, type: 'init', cid: i.cid, fbp: i.fbp, fbc: i.fbc, mode: mode() })) { stop(); return; }
    f.ready = true;
    var q = queue;
    queue = [];
    q.forEach(post);
  }
  function post(item) {
    if (!F || !F.ready || F.closing) { if (item.dropped) guard(item.dropped); return false; }
    if (shellNow()) { stop(); if (item.dropped) guard(item.dropped); return false; }
    if (!postTo(F, item.msg)) { if (item.dropped) guard(item.dropped); return false; }
    if (item.done) guard(item.done);
    return true;
  }
  // The frame follows the live route (OO.router.current()): → blocker's answer, or 'wait' (the first frame's settle).
  function sync() {
    var r = liveRoute();
    var why = blocker(r);
    if (why === 'redirect') return why; // the route is about to change: nothing moves
    if (why === 'off' || why === 'shell') { stop(); return why; }
    if (why) { leave(); return why; }
    var key = lower(r.hotel);
    if (F && (F.closing || F.key !== key)) drop(F);
    if (!F) {
      if (!settled()) { waitSettle(); return 'wait'; }
      F = mount(frameSrc(r.hotel), key);
      if (!F) { stop(); return 'off'; }
    }
    return null;
  }
  // The live route's page: OO.App's settled one when it is that page, else built from the route.
  function describe(r) {
    var k = keyOf(r);
    if (view && view.key === k) return view;
    return { key: k, location: pageLocation(r), title: pageTitle(r), referrer: view ? view.location : landingReferrer() };
  }
  function send(name, params, done, dropped) {
    var why = sync();
    if (why && why !== 'wait') { if (dropped) guard(dropped); return false; }
    var d = describe(liveRoute());
    var p = cleanParams(params);
    p.page_location = d.location;
    p.page_title = d.title;
    p.page_referrer = d.referrer;
    var item = { msg: { oo: 1, type: 'event', name: name, params: p }, key: d.key, done: done || null, dropped: dropped || null };
    if (F && F.ready && !F.closing) return post(item);
    if (queue.length >= QUEUE_MAX) { if (dropped) guard(dropped); return false; }
    var at = queue.length;
    if (name === 'page_view') { // ahead of the events its page queued before OO.App settled on it
      for (var i = 0; i < queue.length; i++) if (queue[i].key === d.key) { at = i; break; }
    }
    queue.splice(at, 0, item);
    return true;
  }
  // A route change, a payment that settled, the settle timer: the frame follows, and the settled page's page_view goes
  // (once per visit of the page).
  function reconcile() {
    var why = sync();
    if (why && why !== 'wait') return;
    var r = liveRoute();
    if (!view || !r || view.key !== keyOf(r) || view.viewed) return;
    view.viewed = true;
    send('page_view', {});
  }
  // A payment going in or out of flight moves the frame (OO.store tells; checked after the change has landed).
  function watchStore() {
    if (unwatch || !OO.store || typeof OO.store.subscribe !== 'function') return;
    busyWas = payBusy();
    unwatch = OO.store.subscribe(function () {
      var b = payBusy();
      if (b === busyWas) return;
      busyWas = b;
      setTimeout(function () { guard(reconcile); }, 0);
    });
  }

  // ---- the API ---------------------------------------------------------------------------------------------------
  function track(name, params) {
    return guard(function () {
      if (!frameBase() || EVENTS.indexOf(name) < 0) return false;
      return send(name, params || {});
    }, false);
  }
  function lineItem(line) {
    if (!line || typeof line !== 'object' || line.reward) return null;
    var combo = line.kind === 'combo';
    var id = combo ? line.comboId : line.itemId;
    if (!given(id)) return null;
    var it = { item_id: (combo ? 'combo-' : '') + String(id), item_name: line.name, price: line.unitPrice, quantity: line.qty };
    if (combo) it.item_category = 'Combo';
    return it;
  }
  function viewItem(item, store) {
    return guard(function () {
      if (!frameBase() || !item || typeof item !== 'object' || !given(item.id)) return false;
      var combo = item.kind === 'combo';
      var it = { item_id: (combo ? 'combo-' : '') + String(item.id), item_name: item.name, price: item.price };
      if (combo && item.categoryName) it.item_category = item.categoryName;
      return send('view_item', { currency: currencyOf(store), value: item.price, items: [it] });
    }, false);
  }
  function addToCart(line, store) {
    return guard(function () {
      if (!frameBase()) return false;
      var it = lineItem(line);
      if (!it) return false;
      var value = amount(it.price) !== null && whole(it.quantity) ? it.price * it.quantity : undefined;
      return send('add_to_cart', { currency: currencyOf(store), value: value, items: [it] });
    }, false);
  }
  function beginCheckout(lines, total, store) {
    return guard(function () {
      if (!frameBase()) return false;
      var ls = Array.isArray(lines) ? lines : [];
      var n = ls.reduce(function (t, l) { return t + (l && whole(l.qty) ? l.qty : 0); }, 0);
      return send('begin_checkout', { currency: currencyOf(store), value: total, item_count: n, items: ls.map(lineItem).filter(Boolean) });
    }, false);
  }
  var inFlight = {}; // order id → a purchase posted or queued this page load
  function purchase(order, store) {
    return guard(function () {
      if (!frameBase() || !order || typeof order !== 'object' || !given(order.orderId)) return false;
      var method = String(order.paymentMethod || '').toUpperCase();
      if (method === 'ONLINE_PAYMENT' ? !order.settledAt : !method) return false; // not through yet
      var oid = String(order.orderId).trim();
      if (blocker(liveRoute())) return false; // never a transaction id for a page without the frame
      var tx = makeTx(oid);
      if (!tx || load().tx[oid].sent || own(inFlight, oid)) return false;
      inFlight[oid] = true;
      var params = { transaction_id: tx, value: order.total, currency: currencyOf(store), tax: order.tax, shipping: order.shipping };
      return send('purchase', params, function () {
        var e = load().tx[oid];
        if (e) { e.sent = true; save(); }
      }, function () { delete inFlight[oid]; });
    }, false);
  }
  function transactionId(orderId) { return guard(function () { return frameBase() ? makeTx(orderId) : null; }, null); }
  function ids() { return guard(function () { return frameBase() ? makeIds() : null; }, null); }
  function onRoute(route, opts) {
    guard(function () {
      if (!frameBase() || !route || typeof route !== 'object') return;
      if ((opts && opts.redirecting) || route.redirectTo) return;
      var k = keyOf(route);
      if (!view || view.key !== k) {
        view = { key: k, location: pageLocation(route), title: pageTitle(route), referrer: view ? view.location : landingReferrer(), viewed: false };
      }
      if (shellNow()) { stop(); return; }
      noteClick(route.query);
      noteLoad();
      watchStore();
      reconcile();
    });
  }
  // OO.App's hook: one effect per page (page|store|params) and redirect state.
  function useRoute(route, opts) {
    var React = window.React;
    var o = opts || {};
    var key = keyOf(route) + (o.redirecting ? '|redirecting' : '');
    React.useEffect(function () { onRoute(route, o); }, [key]);
  }

  function reset() {
    guard(function () { if (F) drop(F); });
    F = null;
    queue = [];
    view = null;
    mem = null;
    landing = null;
    shellSeen = false;
    broken = false;
    inFlight = {};
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = null;
    loadAt = null;
    if (loadHooked) guard(function () { window.removeEventListener('load', onWindowLoad); });
    loadHooked = false;
    if (unwatch) guard(unwatch);
    unwatch = null;
    baseMemo = { raw: null, routeBase: null, host: null, out: null };
  }
  function state() {
    return {
      frame: F ? { el: F.el, ready: F.ready, closing: F.closing, key: F.key } : null,
      queue: queue.map(function (x) { return x.msg; }), view: view, loadAt: loadAt, shellSeen: shellSeen, broken: broken,
    };
  }

  var A = (OO.analytics = OO.lib.analytics = OO.analytics || {});
  A.EVENTS = EVENTS.slice();
  A.NO_TAGS = Object.keys(NO_TAGS);
  A.enabled = function enabled() { return guard(function () { return !!frameBase(); }, false); };
  A.frameSrc = function (hotel) { return guard(function () { return frameSrc(hotel); }, null); };
  A.blocker = function (route) { return guard(function () { return blocker(route); }, 'off'); };
  A.pathOf = function (route, m) { return guard(function () { return pathOf(route, m); }, '/'); };
  A.pageLocation = function (route) { return guard(function () { return pageLocation(route); }, null); };
  A.pageTitle = function (route) { return guard(function () { return pageTitle(route); }, ''); };
  A.track = track;
  A.viewItem = viewItem;
  A.addToCart = addToCart;
  A.beginCheckout = beginCheckout;
  A.purchase = purchase;
  A.transactionId = transactionId;
  A.ids = ids;
  A.onRoute = onRoute;
  A.useRoute = useRoute;
  A.__reset = reset;
  A.__state = state;
})();


// ===================== src/lib/whitelabel.jsx =====================
// src/lib/whitelabel.jsx: OO.whitelabel (alias OO.lib.whitelabel): the white-label theme document of a store's chain
// (docs/whitelabel/WHITELABEL-PLAN.md §5, §6, §7.2; lane WL-2) and the studio's preview mode. JSX-free (the tests load it
// raw in node:vm); its one component, PreviewBar, uses React.createElement at render time.
//
// The document. GET {apiBase}/whitelabel/site/{key} → { data: { site_id, master_key, products: { app, web, online },
// version, document } } for the chain of {key} (a master's or a branch's online_key); "none" when the chain has no active
// site with a published version: since WL-3 a cacheable 200 { status: true, data: null } (an older server's 404 reads the
// same). It is read once per key and page load (ensure(key)), never for a fixture store or a
// store this host doesn't serve live, and only applied while the CURRENT SURFACE's product is on (surface(): mode app →
// products.app, online → products.online, landing → products.web, qr → products.qr). A 404, any
// other failure, an unreadable body or a document whose `schema` isn't exactly "sipo.whitelabel.theme/1" uses default
// styling for online ordering. App and QR retain their existing theme fallback.
//
// The kill switch (WL-int). The published site is read only while the server says so, i.e. while the boot's
// `whitelabel.enabled` is exactly true (OnlineOrderSpaController; config whitelabel.spa_enabled, env
// WHITELABEL_SPA_ENABLED, DEFAULT OFF). Anything else (the key absent, as from a server that predates it; false; 'true';
// 1) is off. Then ensure(), the one function that reads the site, sends no request and leaves the cache untouched: it
// resolves null, as for a store with no document, so siteFor() is null. Online ordering uses default styling; app and
// QR retain their existing themes. The studio preview (?wlPreview=, below) is token-gated, not switch-gated: it
// works whatever the switch says.
//
// Online ordering uses only the Studio document for theming. Missing or invalid document fields use CSS defaults,
// never proposed.theme, shopThemes.online_theme or the ?theme= preset. The normalized Store retains legacy data for
// app / QR compatibility; storeFor() removes it from online views before applying the document. Other store settings
// and ?layout= previews keep their existing behavior. App precedence remains document > proposed.theme > legacy theme.
//
// Document → Store (storeFor):
//   theme      colors.primary/onPrimary → primary/onPrimary · headerBg/headerInk → header/onHeader · canvas → canvas
//              (onCanvas derived) · surface → surface (--oo-card) · ink · muted · accent/onAccent (accentBar = accent) ·
//              heroBg/heroInk · typography.body → font, display → headFont (allowlist only: OO.theme.wlFont),
//              headingWeight → headWeight · shape.radius → radius · shape.card → card · layout.header → headerVariant ·
//              brand.name → brand · the logo set → logo (below), logoLight, logoDark, logoMark · isConfigured true (a
//              white-label theme applies in every mode) · whitelabel true (OO.theme.fontWeights) · bodyWeights [800, 900]
//              only in mode landing for landing.design 'bold' (WL-CHK: its labels use the body font at 800 / 900).
//              logo: the variant for the top bar's surface: logo.light on a light bar (dark text reads better), else
//              logo.dark; the other one, then the mark, when it is missing.
//   logoUrl    the document's logo; app falls back to the Store's. Without a document, online may use the store profile
//              logo, but never the old theme builder's header_logo.
//   menuLayout ← layout.menuLayout · categoryStyle ← layout.categoryStyle · homeModules ← layout.homeModules (order
//              kept; a module the document leaves out is appended disabled, as normalizeStore does) · checkout.priceSummary
//              / checkout.loginStyle ← layout.checkout · loyalty.programName / pointsWord ← loyalty.
//   WL-OPT     categoryColumns (2 | 3 | 4) · categoryImage / productImage ('standard' | 'large') ← layout.* (the menu,
//              src/pages/menu.jsx) · checkout.marketingDefault ← layout.checkout.marketingDefault (the checkout offers
//              checkbox's initial state, src/pages/checkout.jsx). Each one, like menuLayout, is the active
//              surface's (surfaces.<active>.layout over the top-level layout), set only when the document has a valid
//              value: a document without them gives the Store exactly as before. brand.poweredBy false hides every
//              OO.ui.PoweredBySipo badge (poweredBy(key) below; no surface override).
//   whitelabel { siteId, masterKey, version, preview, products, greeting, tabBar, favicon, ogImage, tagline, locale,
//              landing } (landing as sent, for the landing page, lane WL-3).
// The result is one object per (Store, document revision), so React effects keyed on it run once per change.
//
// Boot inline (WL-3, WL-PERF). While the kill switch is on, the shell (OnlineOrderSpaController) puts the answer for the
// URL's store key in the same boot key: __SIPO_BOOT.whitelabel = { enabled: true, key, site: <the site endpoint's `data`>
// | null }, from the same cached server lookup as GET whitelabel/site/{key}. That key is then known at load: a site is
// applied with NO request (no theme flash), and null means "no white-label" with no request either. Any other key (a
// client-side move to another chain) is read as before; the endpoint answers "none" with a cacheable 200 { status: true,
// data: null }. { enabled: true } without a key (the root, a reserved name, a fixture store, a failed lookup)
// changes nothing: keys are read as WL-2 reads them. The boot answer goes through the switch like every read: unless the
// same object says exactly enabled: true, nothing is taken from it (switch off: the server sends exactly
// { enabled: false }). Products still gate the surfaces (siteFor), and preview mode ignores the boot answer (the draft
// wins). Mode landing (OO.landingMode, WL-3) is the `web` surface on every page.
//
// Preview mode (?wlPreview=<token> in the URL the page LOADED with; read once, kept in memory only, never written to
// storage, never carried by links): the draft comes from GET {apiBase}/whitelabel/preview/{token}, then is polled every
// POLL_MS (3 s) with If-None-Match: <the last ETag>; a 200 re-applies, a 304 changes nothing, a 429 or 5xx changes
// nothing either (the last good draft stays; the first load keeps waiting) and the next poll tries again, a 404 (a bad
// or expired token) stops the polling and the bar says the link expired. While a token is present:
//   * the preview document applies to every store on the page, whatever the products (the studio previews a surface
//     before its product is ticked);
//   * isPreview() is true: OO.payload.checkoutPayload refuses (code PREVIEW), the checkout's Pay is disabled, the real
//     adapter's placeOrder refuses, no analytics frame (OO.analytics.blocker → 'preview'), no install prompt;
//   * previewAllows(key): the previewed chain (its master's key, or a key its known chain lists) counts as live here even
//     when the boot `live` list doesn't name it (OO.api.availabilityOf), once the draft has loaded;
//   * <OO.whitelabel.PreviewBar /> is the fixed "Preview — orders are disabled" bar OO.App renders on every page.
// Nothing here listens for `message` events (the studio reloads the iframe and the page polls).
//
// WL-5 (landing-only websites). Mode landing only (OO.landingMode: the /endpoint/landing mount and live custom domains;
// every other mode and host behaves exactly as before): the boot's `whitelabel.ordering` ({ online, reason, stores },
// OnlineOrderSpaController + App\Services\Whitelabel\LandingOrdering: the site's Online ordering tick AND whether the
// chain's stores can take online orders; stores: lower-cased online key → can that store take orders) rides on the boot's
// site, and the preview payload's `ordering` on the draft. landingOrdering(key) → { on, reason } for the landing website
// of key's chain: on unless a site applies there and either its products.online isn't true or its ordering says
// online false (no ordering flag: on, as before WL-5). browseOnly(key) → true when key's pages are browse-only there:
// the website's ordering is off, or that store can't take orders (ordering.stores[key] === false). Both are false
// outside mode landing, with the kill switch off (no site), and for a key with no site (the page goes to the menu).
// In mode landing a chain store's key also finds its chain's site through that flag's store list (memberSite: the
// server lists every chain store there), with no request; and a site read later for another key of the same chain
// inherits the boot's flag (remember), so a branch's menu reached by a client-side move keeps the gate.
//
// API:
//   surface() → 'app' | 'online' | 'web' | null   the product the current mode serves.
//   ensure(key) → Promise   reads the key's site (published) when due and the kill switch is on, or starts the preview;
//       never rejects. Switch off: Promise<null>, no request, the cache untouched (the preview still starts).
//   status(key) → 'loading' | 'ready' | 'none' | null   the key's published-site read (null: not asked yet, which is
//       also every key while the switch is off, or one this host doesn't serve live: no read is ever made; WL-3).
//   siteFor(key) → Site | null   the site that applies to key now (preview, else a published site whose product is on).
//   storeFor(key, store) → Store   the effective store for this surface; stable per source store, site and mode.
//   themeOver(theme, doc) → Theme  the theme fields only (doc = a normalised document, parseDocument's).
//   parseDocument(raw) → normalised document | null   client-side checks of every field storeFor reads (the server
//       validates; a value that fails here is ignored, never applied).
//   isPreview() · previewPending() (the draft is loading) · previewState() → 'off' | 'loading' | 'ready' | 'expired'
//   previewAllows(key) → bool · tabBar(key) → [page] | null · greeting(key, name) → text | null
//   poweredBy(key?) → bool   WL-OPT: false when the site that applies to key now says brand.poweredBy false (key
//       omitted: the current route's chain or store, else the cart's store); true otherwise, and with no site.
//   landingOrdering(key) → { on, reason } · browseOnly(key) → bool (WL-5, mode landing; see above)
//   skin(key, page) → 'super' | null (WL-SUPER): the page frame's skin. Mode app: surfaces.app.layout.design; mode online
//       and the landing website's ordering pages: surfaces.online.layout.design; the landing page itself: landing.design.
//       WL-SUPER also keeps typography.script (top level and per surface) and surfaces.{online,app}.layout.design
//       ('standard' | 'super'; never landing's or kiosk's); themeOver sets scriptFont only where a Super design draws it.
//       WL-VEL: the online surface's design may also be 'boutique' (the App's stays standard | super), so skin() is
//       'boutique' on the same pages 'super' would be (mode online and the landing website's ordering pages; never the
//       landing page itself, never the App). The boutique skin's fonts: themeOver asks the display and body families
//       for 800 there (theme.headWeights / bodyWeights).
//   WL-VEL ordering options: layout.orderBar (bool), featuredRail ('popular'), featuredTitle (0..40), featuredBadge
//       (0..16), top level and per surface (surfaces.<active>.layout over the top-level layout, as WL-OPT's menu options;
//       mode landing reads surfaces.landing), kept only when valid → store.whitelabel.ordering { orderBar, featuredRail,
//       featuredTitle, featuredBadge } (only the keys set; absent when none is). orderingOptions(key) reads it.
//   applyHead(store)  the favicon of a white-label store (restored when the next store has none). No-op without a DOM.
//   subscribe(fn) → unsubscribe · revision() → number · useRevision() (a hook: re-renders on every change)
//   PreviewBar   the preview bar component.
//   Test hooks: __reset({ search, boot }) (forget everything; re-read the preview token from `search` and the boot answer
//   from `boot`, default window.__SIPO_BOOT.whitelabel), __seed(key, body)
//   (a site response body, as fetched), __pollOnce() → Promise (one preview poll now), __setFetch(fn | null).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  var SCHEMA = 'sipo.whitelabel.theme/1';
  var POLL_MS = 3000;
  var DEADLINE_MS = 15000;
  var SURFACES = { app: 'app', online: 'online', landing: 'web', kiosk: 'kiosk', qr: 'qr' };
  var DEFAULT_MODULE_KEYS = ['slider', 'loyalty', 'actions', 'reorder', 'popular'];
  var MODULE_KEYS = DEFAULT_MODULE_KEYS.concat(['categories', 'store']);
  var TAB_KEYS = ['home', 'menu', 'rewards', 'account', 'orders', 'stores', 'booking'];
  var COLOR_KEYS = ['primary', 'onPrimary', 'headerBg', 'headerInk', 'canvas', 'surface', 'ink', 'muted', 'accent', 'onAccent', 'heroBg', 'heroInk'];
  // WL-VEL: optional extra palette slots (the boutique design's wash band, hairlines, fields). Parsed only when present, so a
  // document without them parses, themes and emits exactly as before (--oo-surface-alt / --oo-border / --oo-field).
  var EXTRA_COLOR_KEYS = ['surfaceAlt', 'border', 'field'];
  var TOKEN = /^[A-Za-z0-9._~:-]{8,512}$/;
  var GLOBAL = typeof globalThis !== 'undefined' ? globalThis : window;

  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function obj(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : null; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function lower(k) { return String(k).trim().toLowerCase(); }
  function given(k) { return k !== undefined && k !== null && String(k).trim() !== ''; }
  function guard(fn, fallback) { try { return fn(); } catch (e) { return fallback; } }

  // ---- document checks (§5; the server is authoritative, these only keep a bad value off the page) -----------------
  function hex(v) { return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v.trim()) ? v.trim().toUpperCase() : null; }
  // Lengths in code points, as the server counts them (mb_strlen): an emoji is one character, not two UTF-16 units.
  function chars(s) { return Array.from(s).length; }
  function text(v, max) {
    if (typeof v !== 'string') return null;
    var s = v.trim();
    return s && chars(s) <= max ? s : null;
  }
  function oneOf(v, list) { return typeof v === 'string' && list.indexOf(v) >= 0 ? v : null; }
  function httpsUrl(v) {
    if (typeof v !== 'string' || v.length > 500 || /\s/.test(v) || !/^https:\/\/[^\/?#]+/i.test(v)) return null;
    var authority = /^https:\/\/([^\/?#]+)/i.exec(v)[1];
    return authority.indexOf('@') >= 0 ? null : v;
  }

  function parseDocument(raw) {
    var d = obj(raw);
    if (!d || d.schema !== SCHEMA) return null;
    var brand = obj(d.brand) || {}, logo = obj(brand.logo) || {};
    var c = obj(d.colors) || {}, ty = obj(d.typography) || {}, sh = obj(d.shape) || {}, la = obj(d.layout) || {};
    var co = obj(la.checkout) || {}, lo = obj(d.loyalty) || {}, ap = obj(d.app) || {};
    var colors = {};
    COLOR_KEYS.forEach(function (k) { colors[k] = hex(c[k]); });
    EXTRA_COLOR_KEYS.forEach(function (k) { var v = hex(c[k]); if (v) colors[k] = v; }); // WL-VEL
    var weight = [400, 500, 600, 700, 800, 900].indexOf(Number(ty.headingWeight)) >= 0 ? Number(ty.headingWeight) : null;
    var modules = null;
    if (Array.isArray(la.homeModules)) {
      var seen = {};
      modules = [];
      la.homeModules.forEach(function (m) {
        var key = m && m.key;
        if (MODULE_KEYS.indexOf(key) < 0 || seen[key] || typeof m.enabled !== 'boolean') return;
        seen[key] = true;
        modules.push({ key: key, enabled: m.enabled });
      });
      DEFAULT_MODULE_KEYS.forEach(function (k) { if (!seen[k]) modules.push({ key: k, enabled: false }); });
    }
    var tabs = null;
    if (Array.isArray(ap.tabBar)) {
      tabs = [];
      ap.tabBar.forEach(function (t) { if (TAB_KEYS.indexOf(t) >= 0 && tabs.indexOf(t) < 0) tabs.push(t); });
      if (tabs.length < 2 || tabs.length > 6) tabs = null;
    }
    var greeting = typeof ap.greeting === 'string' && chars(ap.greeting.trim()) <= 40 ? ap.greeting.trim() : null;
    var parsed = {
      brand: {
        name: text(brand.name, 60), tagline: text(brand.tagline, 120),
        logo: { light: httpsUrl(logo.light), dark: httpsUrl(logo.dark), mark: httpsUrl(logo.mark) },
        favicon: httpsUrl(brand.favicon), ogImage: httpsUrl(brand.ogImage),
      },
      colors: colors,
      typography: { display: OO.theme.wlFont(ty.display), body: OO.theme.wlFont(ty.body), headingWeight: weight },
      shape: { radius: oneOf(sh.radius, ['none', 'sm', 'md', 'lg', 'xl', 'pill']), card: oneOf(sh.card, ['flat', 'outline', 'shadow']) },
      layout: {
        header: oneOf(la.header, ['solid', 'brand', 'transparent']),
        menuLayout: oneOf(la.menuLayout, ['categories', 'sections']),
        categoryStyle: oneOf(la.categoryStyle, ['image', 'text']),
        homeModules: modules,
        checkout: { priceSummary: oneOf(co.priceSummary, ['inline', 'collapsed']), loginStyle: oneOf(co.loginStyle, ['card', 'solid']) },
      },
      loyalty: { programName: text(lo.programName, 40), pointsWord: text(lo.pointsWord, 20) },
      app: { greeting: greeting, tabBar: tabs },
      landing: obj(d.landing),
      locale: obj(d.locale),
    };
    var script = OO.theme.wlFont(ty.script); // WL-SUPER: the optional script family, kept only when it is on the list
    if (script) parsed.typography.script = script;
    var logoBackground = typeof logo.background === 'string' && logo.background.trim() === 'transparent' ? 'transparent' : hex(logo.background);
    if (logoBackground) parsed.brand.logo.background = logoBackground;
    // WL-OPT: optional keys, kept only when valid (a document without them parses exactly as before).
    if (typeof brand.poweredBy === 'boolean') parsed.brand.poweredBy = brand.poweredBy;
    menuOptions(la, parsed.layout);
    orderingOptions(la, parsed.layout); // WL-VEL
    if (typeof co.marketingDefault === 'boolean') parsed.layout.checkout.marketingDefault = co.marketingDefault;
    if (typeof ap.showStoreName === 'boolean') parsed.app.showStoreName = ap.showStoreName;
    if (typeof ap.tableBooking === 'boolean') parsed.app.tableBooking = ap.tableBooking;
    if (typeof ap.giftCards === 'boolean') parsed.app.giftCards = ap.giftCards;
    var appLayout = oneOf(ap.layout, ['standard', 'market', 'pizzeria', 'pizzaclub', 'zeta', 'kkitchen']);
    if (appLayout) parsed.app.layout = appLayout;
    if (obj(ap.dining)) {
      var dining = {}, dcfg = ap.dining;
      if (oneOf(dcfg.categorySelection, ['all', 'configured'])) dining.categorySelection = dcfg.categorySelection;
      ['title', 'search', 'popularTitle', 'popularEyebrow', 'memberTitle', 'memberEyebrow', 'quickTitle', 'quickEyebrow', 'memberProgram', 'memberLocation', 'memberStore', 'accountBadge', 'accountTitle', 'accountBody', 'rewardsTitle', 'rewardsBody'].forEach(function (key) {
        var max = ['accountBody', 'rewardsBody'].indexOf(key) >= 0 ? 180 : ['title', 'search', 'accountTitle', 'rewardsTitle'].indexOf(key) >= 0 ? 60 : 40;
        if (typeof dcfg[key] === 'string') dining[key] = text(dcfg[key], max);
      });
      ['logo', 'memberLogo'].forEach(function (key) { if (httpsUrl(dcfg[key])) dining[key] = httpsUrl(dcfg[key]); });
      function posId(v) { return Number.isInteger(v) && v > 0 && v <= 2147483647; }
      function slide(v) {
        if (!obj(v) || !text(v.title, 60) || !httpsUrl(v.image) || !oneOf(v.page, ['menu', 'booking', 'rewards', 'deals', 'account'])) return null;
        return { title: text(v.title, 60), image: httpsUrl(v.image), page: v.page, eyebrow: text(v.eyebrow, 40), buttonLabel: text(v.buttonLabel, 30), productId: posId(v.productId) ? v.productId : null };
      }
      if (Array.isArray(dcfg.slides)) dining.slides = dcfg.slides.slice(0, 8).map(slide).filter(Boolean);
      if (slide(dcfg.menuBanner)) dining.menuBanner = slide(dcfg.menuBanner);
      if (Array.isArray(dcfg.featuredProductIds)) dining.featuredProductIds = dcfg.featuredProductIds.filter(posId).slice(0, 24);
      if (Array.isArray(dcfg.categories)) dining.categories = dcfg.categories.slice(0, 16).filter(function (c) { return obj(c) && posId(c.categoryId); }).map(function (c) { return { categoryId: c.categoryId, title: text(c.title, 40), image: httpsUrl(c.image) }; });
      if (Array.isArray(dcfg.productImages)) dining.productImages = dcfg.productImages.slice(0, 48).filter(function (p) { return obj(p) && posId(p.productId) && httpsUrl(p.image); }).map(function (p) { return { productId: p.productId, image: httpsUrl(p.image) }; });
      parsed.app.dining = dining;
    }
    if (obj(ap.hero)) {
      var hero = {};
      ['title', 'body', 'buttonLabel'].forEach(function (key) {
        var value = text(ap.hero[key], key === 'title' ? 60 : key === 'body' ? 180 : 24);
        if (value && !/[\u0000-\u001F\u007F\u2028\u2029]/.test(value)) hero[key] = value;
      });
      var heroImage = httpsUrl(ap.hero.image);
      if (heroImage) hero.image = heroImage;
      var eyebrow = text(ap.hero.eyebrow, 30);
      if (eyebrow) hero.eyebrow = eyebrow;
      if (Object.keys(hero).length) parsed.app.hero = hero;
    }
    ['zoneLabel', 'socialTitle'].forEach(function (key) { var value = text(ap[key], key === 'zoneLabel' ? 24 : 40); if (value) parsed.app[key] = value; });
    if (Array.isArray(ap.cards)) parsed.app.cards = ap.cards.slice(0, 20).filter(function (card) {
      return obj(card) && oneOf(card.group, ['home', 'plan', 'discover', 'app']) && text(card.title, 40) &&
        oneOf(card.icon, ['store', 'heart', 'gift', 'users', 'info', 'mail', 'settings']) && obj(card.target);
    }).map(function (card) {
      return { group: card.group, title: text(card.title, 40), icon: card.icon, target: card.target,
        body: text(card.body, 100), eyebrow: text(card.eyebrow, 30), image: httpsUrl(card.image) };
    });
    if (Array.isArray(ap.categoryTiles)) {
      var seenCategories = {};
      parsed.app.categoryTiles = ap.categoryTiles.slice(0, 6).filter(function (tile) {
        if (!obj(tile) || !Number.isInteger(tile.categoryId) || tile.categoryId < 1 || tile.categoryId > 2147483647 || seenCategories[tile.categoryId]) return false;
        seenCategories[tile.categoryId] = true;
        return true;
      }).map(function (tile) {
        return { categoryId: tile.categoryId, title: text(tile.title, 40), image: httpsUrl(tile.image) };
      });
    }
    var quickActions = obj(ap.quickActions);
    if (quickActions) {
      var actions = {}, actionIcons = obj(quickActions.icons);
      var tileStyle = oneOf(quickActions.tileStyle, ['card', 'transparent']);
      if (tileStyle) actions.tileStyle = tileStyle;
      if (actionIcons) {
        var icons = {};
        ['menu', 'booking', 'rewards', 'stores'].forEach(function (key) {
          var url = httpsUrl(actionIcons[key]);
          if (url) icons[key] = url;
        });
        if (Object.keys(icons).length) actions.icons = icons;
      }
      if (Object.keys(actions).length) parsed.app.quickActions = actions;
    }
    var background = obj(ap.background);
    if (background && oneOf(background.type, ['color', 'gradient', 'image'])) {
      parsed.app.background = { type: background.type };
      ['color', 'endColor'].forEach(function (k) { var color = hex(background[k]); if (color) parsed.app.background[k] = color; });
      ['angle', 'tint'].forEach(function (k) {
        var value = background[k];
        if (Number.isInteger(value) && value >= 0 && value <= (k === 'angle' ? 360 : 100)) parsed.app.background[k] = value;
      });
      // A background URL becomes CSS, so reject CSS punctuation as well as non-HTTPS URLs.
      if (typeof background.image === 'string' && !/["'<>\\`\u0000-\u001F\u007F]/.test(background.image)) {
        var image = httpsUrl(background.image);
        if (image) parsed.app.background.image = image;
      }
    }
    if (obj(d.surfaces)) {
      parsed.surfaces = {};
      ['app', 'landing', 'online', 'kiosk', 'qr'].forEach(function (key) {
        if (obj(d.surfaces[key])) parsed.surfaces[key] = parseSurfaceStyle(d.surfaces[key], key); // WL-SUPER: + key
      });
    }
    return parsed;
  }

  // WL-OPT: the menu's options (layout.* and surfaces.*.layout.*), copied into `into` only when valid: categoryColumns
  // (the integer 2, 3 or 4: the most category cards per desktop row), categoryImage and productImage ('standard' |
  // 'large', trimmed as the server saves them). An absent or invalid key adds nothing.
  var CATEGORY_COLUMNS = [2, 3, 4];
  var IMAGE_SIZES = ['standard', 'large'];
  function menuOptions(source, into) {
    if (!source) return into;
    if (CATEGORY_COLUMNS.indexOf(source.categoryColumns) >= 0) into.categoryColumns = source.categoryColumns;
    if (typeof source.showAddIcon === 'boolean') into.showAddIcon = source.showAddIcon;
    ['categoryImage', 'productImage'].forEach(function (key) {
      var v = typeof source[key] === 'string' ? source[key].trim() : null;
      if (IMAGE_SIZES.indexOf(v) >= 0) into[key] = v;
    });
    return into;
  }

  // WL-VEL: the ordering options (layout.* and surfaces.*.layout.*), copied into `into` only when valid: orderBar (a
  // boolean), featuredRail ('popular'), featuredTitle (1..40 characters; empty means the default) and featuredBadge
  // (1..16). An absent or invalid key adds nothing.
  var FEATURED_RAILS = ['popular'];
  var ORDERING_KEYS = ['orderBar', 'featuredRail', 'featuredTitle', 'featuredBadge'];
  function orderingOptions(source, into) {
    if (!source) return into;
    if (typeof source.orderBar === 'boolean') into.orderBar = source.orderBar;
    if (FEATURED_RAILS.indexOf(source.featuredRail) >= 0) into.featuredRail = source.featuredRail;
    var title = text(source.featuredTitle, 40), badge = text(source.featuredBadge, 16);
    if (title && !/[\u0000-\u001F\u007F\u2028\u2029]/.test(title)) into.featuredTitle = title;
    if (badge && !/[\u0000-\u001F\u007F\u2028\u2029]/.test(badge)) into.featuredBadge = badge;
    return into;
  }

  function parseSurfaceStyle(raw, key) {
    var out = {};
    function group(name, checks) {
      var source = obj(raw[name]);
      if (!source) return;
      var values = {};
      Object.keys(checks).forEach(function (key) {
        var v = checks[key](source[key]);
        if (v !== null && v !== undefined) values[key] = v;
      });
      if (Object.keys(values).length) out[name] = values;
    }
    function choice(list) { return function (v) { return list.indexOf(v) >= 0 ? v : null; }; }
    var colors = {};
    COLOR_KEYS.forEach(function (key) { colors[key] = hex; });
    EXTRA_COLOR_KEYS.forEach(function (key) { colors[key] = hex; }); // WL-VEL (kept only when valid, as every group key)
    group('colors', colors);
    group('typography', { display: OO.theme.wlFont, body: OO.theme.wlFont, headingWeight: choice([400, 500, 600, 700, 800, 900]), script: OO.theme.wlFont }); // WL-SUPER: + script
    group('shape', { radius: choice(['none', 'sm', 'md', 'lg', 'xl', 'pill']), card: choice(['flat', 'outline', 'shadow']) });
    group('layout', { header: choice(['solid', 'brand', 'transparent']), menuLayout: choice(['categories', 'sections']), categoryStyle: choice(['image', 'text']) });
    var options = menuOptions(obj(raw.layout), {}); // WL-OPT
    if (Object.keys(options).length) out.layout = Object.assign(out.layout || {}, options);
    // WL-SUPER: the Super skin of the online-ordering and App surfaces (layout.design); never landing's or kiosk's.
    var design = (raw.layout || {}).design;
    if ((key === 'online' || key === 'app' || key === 'qr') && (design === 'standard' || design === 'super' || design === 'pizzeria')) { out.layout = out.layout || {}; out.layout.design = design; }
    if (key === 'app' && (design === 'pizzaclub' || design === 'kkitchen')) { out.layout = out.layout || {}; out.layout.design = design; }
    if ((key === 'app' || key === 'online') && design === 'zeta') { out.layout = out.layout || {}; out.layout.design = design; }
    if ((key === 'online' || key === 'qr') && design === 'boutique') { out.layout = out.layout || {}; out.layout.design = design; }
    if (key === 'online' && design === 'storefront') { out.layout = out.layout || {}; out.layout.design = design; }
    var ordering = orderingOptions(obj(raw.layout), {}); // WL-VEL
    if (Object.keys(ordering).length) out.layout = Object.assign(out.layout || {}, ordering);
    var kiosk = obj((raw.layout || {}).kiosk);
    if (kiosk) {
      var ks = {};
      ['variant', 'imageFit', 'logoPreset'].forEach(function (key) {
        var choices = key === 'variant' ? ['normal', 'chicking'] : key === 'imageFit' ? ['cover', 'contain'] : ['store', 'chicking'];
        if (choices.indexOf(kiosk[key]) >= 0) ks[key] = kiosk[key];
      });
      if (kiosk.logo === null || httpsUrl(kiosk.logo)) ks.logo = httpsUrl(kiosk.logo);
      if (typeof kiosk.collectionMessage === 'string' && chars(kiosk.collectionMessage.trim()) <= 160 && !/[\u0000-\u001F\u007F\u2028\u2029]/.test(kiosk.collectionMessage)) ks.collectionMessage = kiosk.collectionMessage.trim();
      if (Object.keys(ks).length) { out.layout = out.layout || {}; out.layout.kiosk = ks; }
    }
    var checkout = obj((raw.layout || {}).checkout);
    if (checkout) {
      var co = {};
      if (['inline', 'collapsed'].indexOf(checkout.priceSummary) >= 0) co.priceSummary = checkout.priceSummary;
      if (['card', 'solid'].indexOf(checkout.loginStyle) >= 0) co.loginStyle = checkout.loginStyle;
      if (typeof checkout.marketingDefault === 'boolean') co.marketingDefault = checkout.marketingDefault; // WL-OPT
      if (Object.keys(co).length) { out.layout = out.layout || {}; out.layout.checkout = co; }
    }
    var prompt = obj((raw.layout || {}).orderPrompt);
    if (prompt) {
      var op = {}, layout = oneOf(prompt.layout, ['classic', 'cards', 'banner', 'split', 'minimal']);
      if (layout) op.layout = layout;
      if (['tiles', 'select'].indexOf(prompt.timeStyle) >= 0) op.timeStyle = prompt.timeStyle;
      if (typeof prompt.showBrand === 'boolean') op.showBrand = prompt.showBrand;
      ['title', 'intro'].forEach(function (key) {
        var value = prompt[key];
        if (typeof value === 'string' && chars(value.trim()) <= (key === 'title' ? 60 : 160) && !/[\u0000-\u001F\u007F\u2028\u2029]/.test(value.trim())) op[key] = value.trim();
      });
      if (prompt.image === null || httpsUrl(prompt.image)) op.image = httpsUrl(prompt.image);
      if (Object.keys(op).length) { out.layout = out.layout || {}; out.layout.orderPrompt = op; }
    }
    return out;
  }
  function styleSurface() { return OO.landingMode || OO.mode === 'landing' ? 'landing' : OO.mode === 'app' ? 'app' : OO.mode === 'kiosk' ? 'kiosk' : OO.mode === 'qr' ? 'qr' : 'online'; }
  function forSurface(doc, key) {
    var style = doc.surfaces && doc.surfaces[key || styleSurface()];
    if (!style) return doc;
    var out = Object.assign({}, doc);
    ['colors', 'typography', 'shape', 'layout'].forEach(function (name) { out[name] = Object.assign({}, doc[name], style[name]); });
    out.layout.checkout = Object.assign({}, doc.layout.checkout, (style.layout || {}).checkout);
    return out;
  }

  function parseSite(body, preview) {
    var data = obj(body) && obj(body.data);
    if (!data) return null;
    var doc = parseDocument(data.document);
    if (!doc) return null;
    var p = obj(data.products) || {};
    return {
      siteId: data.site_id === undefined ? null : data.site_id,
      masterKey: given(data.master_key) ? String(data.master_key).trim() : null,
      products: { app: p.app === true, web: p.web === true, online: p.online === true, kiosk: p.kiosk === true, qr: p.qr === true },
      version: data.version === undefined ? null : data.version,
      preview: !!preview,
      doc: doc,
      // WL-5: the landing website's ordering (the preview payload carries it; the boot's rides beside the site).
      ordering: orderingOf(data.ordering),
      reviews: obj(data.reviews) || null,
    };
  }
  // WL-5: { online, reason, stores } as the server sends it, checked; null when absent or unreadable (ordering on).
  function orderingOf(raw) {
    var o = obj(raw);
    if (!o || typeof o.online !== 'boolean') return null;
    var stores = Object.create(null);
    var st = obj(o.stores) || {};
    Object.keys(st).forEach(function (k) { if (typeof st[k] === 'boolean') stores[lower(k)] = st[k]; });
    return { online: o.online, reason: typeof o.reason === 'string' ? o.reason : null, stores: stores };
  }

  // ---- Document → Theme / Store -----------------------------------------------------------------------------------
  function lightSurface(c) {
    var T = OO.theme;
    return !!c && T.contrastRatio('#000000', c) >= T.contrastRatio('#FFFFFF', c);
  }
  function logoFor(doc, theme) {
    var l = doc.brand.logo;
    var hv = theme.headerVariant;
    var bar = hv === 'brand' ? theme.primary : hv === 'transparent' ? theme.canvas : theme.header;
    if (surface() === 'app' && l.background && l.background !== 'transparent') bar = l.background;
    var order = lightSurface(bar) ? [l.light, l.dark] : [l.dark, l.light];
    return order[0] || order[1] || l.mark || null;
  }

  function themeOver(base, doc) {
    doc = forSurface(doc);
    var t = Object.assign({}, base || {});
    var c = doc.colors;
    function set(field, v) { if (v !== null && v !== undefined) t[field] = v; }
    set('primary', c.primary);
    set('onPrimary', c.onPrimary || (c.primary ? OO.theme.contrast(c.primary) : null));
    set('header', c.headerBg);
    set('onHeader', c.headerInk || (c.headerBg ? OO.theme.contrast(c.headerBg) : null));
    set('canvas', c.canvas);
    if (c.canvas) t.onCanvas = OO.theme.contrast(c.canvas);
    set('surface', c.surface);
    set('ink', c.ink);
    set('muted', c.muted);
    set('accent', c.accent);
    set('onAccent', c.onAccent || (c.accent ? OO.theme.contrast(c.accent) : null));
    set('accentBar', c.accent);
    set('heroBg', c.heroBg);
    set('heroInk', c.heroInk || (c.heroBg ? OO.theme.contrast(c.heroBg) : null));
    set('font', doc.typography.body);
    set('headFont', doc.typography.display);
    set('headWeight', doc.typography.headingWeight);
    set('radius', doc.shape.radius);
    set('card', doc.shape.card);
    set('headerVariant', doc.layout.header);
    set('brand', doc.brand.name);
    set('logoLight', doc.brand.logo.light);
    set('logoDark', doc.brand.logo.dark);
    set('logoMark', doc.brand.logo.mark);
    set('logoBackground', doc.brand.logo.background);
    set('showStoreName', doc.app.showStoreName);
    if (surface() === 'app') set('appBackground', doc.app.background);
    set('logo', logoFor(doc, t));
    if (surface() === 'app' && doc.app.layout === 'pizzaclub') { t.bodyWeights = [800, 900]; t.headWeights = [800, 900]; }
    if (surface() === 'app' && surfaceDesign(doc, 'app') === 'kkitchen') { if (t.font === 'Poppins') t.font = 'K Kitchen Body'; if (t.headFont === 'Poppins') t.headFont = 'K Kitchen Body'; }
    if (surfaceDesign(doc, surface()) === 'zeta') { t.bodyWeights = [800, 900]; t.headWeights = [800, 900]; }
    t.isConfigured = true;
    t.whitelabel = true;
    // WL-CHK: the bold landing design's labels use the body font at 800 / 900. Landing website only; any other document
    // or mode has no bodyWeights, so it requests exactly the weights it did before.
    if (OO.landingMode && doc.landing && (doc.landing.design === 'bold' || doc.landing.design === 'super')) t.bodyWeights = [800, 900]; // WL-SUPER: + super
    // WL-SUPER: the script family reaches the theme (and so loads) only where a Super design draws it.
    if (doc.typography.script && superHere(doc)) t.scriptFont = doc.typography.script;
    // WL-VEL: the extra palette slots (only when set) and the boutique design's labels (body 800) and italic display.
    EXTRA_COLOR_KEYS.forEach(function (k) { if (c[k]) t[k] = c[k]; });
    if (OO.landingMode && doc.landing && doc.landing.design === 'boutique') { t.bodyWeights = [800]; t.headItalic = true; }
    // WL-VEL (VEL-C): the boutique ordering skin's section heads (display 800) and labels (body 800), where it is drawn.
    if (boutiqueHere(doc)) {
      t.bodyWeights = (t.bodyWeights || []).concat(t.bodyWeights && t.bodyWeights.indexOf(800) >= 0 ? [] : [800]);
      t.headWeights = [800];
    }
    return t;
  }

  // WL-SUPER: the Super skin's design key for the online-ordering surface ('online') or the App ('app'), or null.
  function surfaceDesign(doc, name) {
    var st = doc && doc.surfaces && doc.surfaces[name];
    if (name === 'app' && ((doc.app && doc.app.layout === 'kkitchen') || (st && st.layout && st.layout.design === 'kkitchen'))) return 'kkitchen';
    if (name === 'app' && ((doc.app && doc.app.layout === 'zeta') || (st && st.layout && st.layout.design === 'zeta'))) return 'zeta';
    if (name === 'online' && st && st.layout && st.layout.design === 'zeta') return 'zeta';
    if (name === 'online' && st && st.layout && st.layout.design === 'storefront') return 'storefront';
    if (name === 'app' && st && st.layout && st.layout.design === 'pizzaclub') return 'pizzaclub';
    if (st && st.layout && st.layout.design === 'pizzeria') return 'pizzeria';
    if (st && st.layout && st.layout.design === 'super') return 'super';
    return (name === 'online' || name === 'qr') && st && st.layout && st.layout.design === 'boutique' ? 'boutique' : null;
  }
  // Is a Super design drawn on the current surface? Mode landing: the landing design or (its ordering pages) the online
  // surface's; mode app: the App's; mode online: the online surface's.
  function superHere(doc) {
    var s = surface();
    if (s === 'web') return !!(doc.landing && doc.landing.design === 'super') || surfaceDesign(doc, 'online') === 'super';
    if (s === 'app') return surfaceDesign(doc, 'app') === 'super';
    if (s === 'online') return surfaceDesign(doc, 'online') === 'super';
    if (s === 'qr') return surfaceDesign(doc, 'qr') === 'super';
    return false;
  }
  // WL-VEL: is the boutique ordering skin drawn on the current surface (mode online, or the landing website's ordering
  // pages)?
  function boutiqueHere(doc) {
    var s = surface();
    return ((s === 'online' || s === 'web') && surfaceDesign(doc, 'online') === 'boutique')
      || (s === 'qr' && surfaceDesign(doc, 'qr') === 'boutique');
  }
  // skin(key, page) → 'super' | 'boutique' (WL-VEL, the online surface only) | null: the skin of the page frame (src/ui/chrome.jsx PageFrame). Mode app: the App
  // surface's layout.design; mode online and the landing website's ordering pages: the online surface's; the landing
  // website's own page ('landing'): its landing.design. Kiosk and a key with no site: null.
  function skin(key, page) {
    var s = surface();
    if (s !== 'app' && s !== 'online' && s !== 'web' && s !== 'qr') return null;
    var site = given(key) ? siteFor(key) : null;
    if (!site) return null;
    if (s === 'web' && page === 'landing') return site.doc.landing && site.doc.landing.design === 'super' ? 'super' : null;
    return surfaceDesign(site.doc, s === 'app' ? 'app' : s === 'qr' ? 'qr' : 'online');
  }

  function overlay(store, site, studioOnly) {
    var doc = forSurface(site.doc), la = doc.layout;
    var theme = themeOver(studioOnly ? null : store.theme, doc);
    var out = Object.assign({}, store, { theme: theme });
    // App-only opt-out: every booking entry uses the selected store's capability.
    // The original store, other surfaces and brands without this option stay unchanged.
    if (surface() === 'app' && doc.app.tableBooking === false) out.booking = false;
    if (studioOnly || doc.brand.logo.light || doc.brand.logo.dark || doc.brand.logo.mark) out.logoUrl = theme.logo || (studioOnly ? null : store.logoUrl) || null;
    if (surfaceDesign(doc, 'online') === 'storefront' && surface() === 'online') {
      out.logoUrl = theme.logo || store.storeLogoUrl || null;
    }
    if (la.menuLayout) out.menuLayout = la.menuLayout;
    if (surfaceDesign(doc, 'online') === 'storefront' && surface() === 'online') out.menuLayout = 'sections';
    if (la.categoryStyle) out.categoryStyle = la.categoryStyle;
    menuOptions(la, out); // WL-OPT: categoryColumns, categoryImage, productImage (the active surface's, as menuLayout)
    if (la.homeModules) out.homeModules = la.homeModules.map(function (m) { return { key: m.key, enabled: m.enabled }; });
    // Both Zeta surfaces display the same preparation-buffer intervals (at least 15 minutes).
    if (surfaceDesign(doc, surface()) === 'zeta') out.scheduling = Object.assign({}, store.scheduling || {}, { slotIntervalByType: { PICKUP: Math.max(15, store.cookingTimeMins || 10), DELIVERY: Math.max(15, store.deliveryTimeMins || 20) } });
    out.checkout = Object.assign({}, store.checkout || {});
    if (la.checkout.priceSummary) out.checkout.priceSummary = la.checkout.priceSummary;
    if (la.checkout.loginStyle) out.checkout.loginStyle = la.checkout.loginStyle;
    if (typeof la.checkout.marketingDefault === 'boolean') out.checkout.marketingDefault = la.checkout.marketingDefault;
    out.loyalty = Object.assign({}, store.loyalty || {});
    if (doc.loyalty.programName) out.loyalty.programName = doc.loyalty.programName;
    if (doc.loyalty.pointsWord) out.loyalty.pointsWord = doc.loyalty.pointsWord;
    out.whitelabel = {
      siteId: site.siteId, masterKey: site.masterKey, version: site.version, preview: site.preview,
      products: Object.assign({}, site.products), greeting: doc.app.greeting, tabBar: doc.app.tabBar ? doc.app.tabBar.slice() : null,
      favicon: doc.brand.favicon, ogImage: doc.brand.ogImage, tagline: doc.brand.tagline, locale: doc.locale, landing: doc.landing,
    };
    if (la.orderPrompt) out.whitelabel.orderPrompt = Object.assign({}, la.orderPrompt);
    var ordering = orderingOptions(la, {}); // WL-VEL: the active surface's ordering options (only the keys set)
    if (Object.keys(ordering).length) out.whitelabel.ordering = ordering;
    if (surface() === 'kiosk' && la.kiosk) out.whitelabel.kiosk = Object.assign({}, la.kiosk);
    if (surface() === 'app' && typeof doc.app.giftCards === 'boolean') out.whitelabel.giftCards = doc.app.giftCards;
    if (surface() === 'app' && doc.app.quickActions) out.whitelabel.quickActions = doc.app.quickActions;
    if (surface() === 'app' && doc.app.categoryTiles) out.whitelabel.categoryTiles = doc.app.categoryTiles;
    return out;
  }

  // ---- state ------------------------------------------------------------------------------------------------------
  var sites, byMaster, preview, rev, subs, made, fetchHook;
  function blank(search) {
    sites = Object.create(null);    // lower key → { state: 'loading' | 'ready' | 'none', p, site }
    byMaster = Object.create(null); // lower master key → site
    preview = { token: tokenFrom(search), state: 'off', site: null, etag: null, timer: null, p: null };
    if (preview.token) preview.state = 'idle';
    rev = 0;
    made = typeof WeakMap === 'function' ? new WeakMap() : null; // raw Store → { site, studioOnly, out }; view → { base }
  }
  // WL-3: the shell's answer for its URL's key (see "Boot inline" above): remembered as if read, so ensure() asks nothing.
  // Behind the kill switch (WL-int), like every read: only an answer that itself says exactly enabled: true is taken, so
  // with the switch off nothing reaches `sites` (and siteFor() stays null) whatever else the boot carries.
  function fromBoot(answer) {
    var b = answer === undefined ? guard(function () { return (window.__SIPO_BOOT || {}).whitelabel; }, null) : answer;
    if (!obj(b) || b.enabled !== true) return;
    if (!given(b.key) || typeof b.key !== 'string' || !own(b, 'site')) return;
    var site = b.site === null ? null : parseSite({ data: b.site }, false);
    if (b.site !== null && !site) return; // an unreadable payload: read it as before
    if (site && own(b, 'ordering')) site.ordering = orderingOf(b.ordering); // WL-5: mode landing's boot only
    remember(lower(b.key), site);
  }
  subs = [];
  fetchHook = null;

  function tokenFrom(search) {
    var s = typeof search === 'string' ? search : guard(function () { return String(window.location.search || ''); }, '');
    var m = /[?&]wlPreview=([^&#]*)/.exec(s);
    if (!m) return null;
    var v = guard(function () { return decodeURIComponent(m[1].replace(/\+/g, ' ')); }, '');
    return TOKEN.test(v) ? v : null;
  }

  function emit() {
    rev += 1;
    subs.slice().forEach(function (fn) { guard(fn); });
    guard(function () { if (OO.data && typeof OO.data.reactivate === 'function') OO.data.reactivate(); });
  }
  function subscribe(fn) {
    subs.push(fn);
    return function () { subs = subs.filter(function (x) { return x !== fn; }); };
  }
  function revision() { return rev; }

  function surface() {
    if (OO.landingMode) return SURFACES.landing;
    return own(SURFACES, OO.mode) ? SURFACES[OO.mode] : (OO.mode ? null : 'online');
  }

  // ---- transport (its own fetch: the preview needs the ETag header) -----------------------------------------------
  function apiBase() { return String((window.__SIPO_BOOT || {}).apiBase || '/api').replace(/\/+$/, ''); }
  function fetchFn() {
    if (fetchHook) return fetchHook;
    var f = typeof window.fetch === 'function' ? window.fetch : GLOBAL.fetch;
    return typeof f === 'function' ? function (u, i) { return f.call(window, u, i); } : null;
  }
  // → Promise<{ status, etag, body }>; rejects on a network failure, the deadline or no fetch.
  function get(path, headers, noStore) {
    var f = fetchFn();
    if (!f) return Promise.reject(new Error('no fetch'));
    var init = { method: 'GET', credentials: 'same-origin', headers: Object.assign({ Accept: 'application/json' }, headers || {}) };
    if (noStore) init.cache = 'no-store';
    var timer = null, ctrl = null;
    var AC = typeof window.AbortController === 'function' ? window.AbortController : GLOBAL.AbortController;
    if (typeof AC === 'function' && typeof setTimeout === 'function') {
      ctrl = new AC();
      init.signal = ctrl.signal;
      timer = setTimeout(function () { ctrl.abort(); }, DEADLINE_MS);
    }
    return Promise.resolve().then(function () { return f(apiBase() + path, init); }).then(function (res) {
      var etag = guard(function () { return res.headers && typeof res.headers.get === 'function' ? res.headers.get('ETag') : null; }, null);
      if (res.status !== 200) return { status: res.status, etag: etag, body: null };
      var parsed = typeof res.json === 'function' ? res.json() : Promise.resolve(res.text()).then(JSON.parse);
      return Promise.resolve(parsed).then(function (body) { return { status: 200, etag: etag, body: body }; },
        function () { return { status: 200, etag: etag, body: null }; });
    }).then(function (r) { if (timer) clearTimeout(timer); return r; }, function (e) { if (timer) clearTimeout(timer); throw e; });
  }

  // ---- the published site ---------------------------------------------------------------------------------------
  // The kill switch (see the header), read per call: on only when the boot says exactly { enabled: true }.
  function liveReadsOn() {
    var w = guard(function () { return (window.__SIPO_BOOT || {}).whitelabel; }, null);
    return !!w && typeof w === 'object' && w.enabled === true;
  }
  function servedLive(key) {
    var A = OO.api;
    return !!A && typeof A.availabilityOf === 'function' && guard(function () { return A.availabilityOf(key) === 'live'; }, false);
  }
  function remember(k, site) {
    // WL-5: the public endpoint's payload carries no ordering flag: the boot's for the same site applies.
    if (site && !site.ordering && site.masterKey) {
      var known = byMaster[lower(site.masterKey)];
      if (known && known.ordering && known.siteId === site.siteId) site.ordering = known.ordering;
    }
    sites[k] = { state: site ? 'ready' : 'none', p: null, site: site };
    if (site && site.masterKey) byMaster[lower(site.masterKey)] = site;
  }
  // WL-5, mode landing: the known site whose ordering flag lists the store key k (lower-cased), or null.
  function memberSite(k) {
    if (!OO.landingMode) return null;
    var hit = null;
    Object.keys(byMaster).forEach(function (m) {
      var st = byMaster[m];
      if (!hit && st && st.ordering && st.ordering.stores && k in st.ordering.stores) hit = st;
    });
    return hit;
  }
  function ensure(key) {
    if (preview.token) return ensurePreview();
    // The kill switch, before anything reads or writes `sites`: off → no request, the cache untouched, no document.
    if (!liveReadsOn()) return Promise.resolve(null);
    if (!given(key) || !surface()) return Promise.resolve(null);
    var k = lower(key);
    var cur = sites[k];
    if (cur) return cur.p || Promise.resolve(cur.site);
    var member = memberSite(k); // WL-5: a chain store of a site the boot brought (mode landing): no request
    if (member) { remember(k, member); return Promise.resolve(member); }
    if (!servedLive(String(key).trim())) return Promise.resolve(null); // a fixture store, or one not served here
    var rec = sites[k] = { state: 'loading', p: null, site: null };
    rec.p = get('/whitelabel/site/' + encodeURIComponent(String(key).trim())).then(function (r) {
      return r.status === 200 ? parseSite(r.body, false) : null;
    }, function () { return null; }).then(function (site) {
      remember(k, site);
      if (site) emit();
      return site;
    });
    return rec.p;
  }

  function status(key) {
    if (!given(key)) return null;
    var cur = sites[lower(key)];
    return cur ? cur.state : null;
  }

  // ---- preview ----------------------------------------------------------------------------------------------------
  function ensurePreview() {
    if (preview.state === 'idle') {
      preview.state = 'loading';
      preview.p = pollOnce().then(function () { schedule(); return preview.site; });
    }
    return preview.p || Promise.resolve(preview.site);
  }
  function schedule() {
    if (preview.state === 'expired' || typeof setTimeout !== 'function') return;
    if (preview.timer) clearTimeout(preview.timer);
    preview.timer = setTimeout(function () {
      preview.timer = null;
      pollOnce().then(schedule);
    }, POLL_MS);
  }
  function pollOnce() {
    if (!preview.token || preview.state === 'expired') return Promise.resolve(null);
    var headers = preview.etag ? { 'If-None-Match': preview.etag } : null;
    var token = preview.token;
    return get('/whitelabel/preview/' + encodeURIComponent(token), headers, true).then(function (r) {
      if (token !== preview.token) return null;
      if (r.status === 304) return null;
      if (r.status === 404 || r.status === 403 || r.status === 410) {
        var was = preview.state;
        preview.state = 'expired';
        if (was !== 'expired') emit();
        return null;
      }
      // Rate-limited (429) or a server hiccup (5xx): transient, like a network blip. The last good draft stays applied
      // (or, on the first load, the page keeps waiting) and the next poll tries again.
      if (r.status === 429 || r.status >= 500) return null;
      if (r.status !== 200) { if (preview.state === 'loading' && !preview.site) { preview.state = 'expired'; emit(); } return null; }
      var site = parseSite(r.body, true);
      if (!site) return null;
      preview.site = site;
      preview.etag = r.etag || null;
      preview.state = 'ready';
      emit();
      return site;
    }, function () {
      return null; // a network blip: the next poll tries again (the first load keeps waiting)
    });
  }
  function isPreview() { return !!preview.token; }
  function previewPending() { return !!preview.token && (preview.state === 'idle' || preview.state === 'loading') && !preview.site; }
  function previewState() { return preview.token ? (preview.state === 'idle' ? 'loading' : preview.state) : 'off'; }
  function chainLists(master, key) {
    var R = OO.router;
    var c = R && typeof R.chainOf === 'function' ? guard(function () { return R.chainOf(master); }, null) : null;
    if (!c || c.state !== 'known') c = R && typeof R.chainOf === 'function' ? guard(function () { return R.chainOf(key); }, null) : null;
    if (!c || c.state !== 'known') return false;
    var listed = arr(c.keys).some(function (x) { return lower(x) === lower(key); });
    return listed && (lower(c.master || '') === lower(master) || arr(c.keys).some(function (x) { return lower(x) === lower(master); }));
  }
  function previewAllows(key) {
    if (!preview.token || !preview.site || preview.state === 'expired' || !given(key)) return false;
    var m = preview.site.masterKey;
    return !!m && (lower(m) === lower(key) || chainLists(m, String(key)));
  }

  // ---- lookups ----------------------------------------------------------------------------------------------------
  function masterKeyOf(key) {
    var D = OO.data;
    return D && typeof D.masterOf === 'function' ? guard(function () { return D.masterOf(key); }, null) : null;
  }
  function siteFor(key) {
    if (preview.token) return preview.state === 'expired' ? null : preview.site;
    var s = surface();
    if (!s || !given(key)) return null;
    var k = lower(key);
    var site = sites[k] && sites[k].site || byMaster[k] || null;
    if (!site) {
      var m = masterKeyOf(key);
      if (given(m)) site = (sites[lower(m)] && sites[lower(m)].site) || byMaster[lower(m)] || null;
    }
    // A non-orderable chain master may have no online key. Its published Studio theme still belongs to each
    // verified member of the cached chain; do not borrow the current route's branding for an unrelated cart/store.
    if (!site && s === 'app' && OO.data && typeof OO.data.chainFor === 'function') {
      var chain = guard(function () { return OO.data.chainFor(key); }, null);
      var ck = chain && given(chain.key) ? lower(chain.key) : null;
      if (ck) site = (sites[ck] && sites[ck].site) || byMaster[ck] || null;
    }
    if (!site) site = memberSite(k); // WL-5 (mode landing only)
    return site && site.products[s] ? site : null;
  }
  function keyOfStore(store) { return store && given(store.onlineKey) ? store.onlineKey : null; }
  function storeFor(key, store) {
    if (!store || typeof store !== 'object') return store;
    var memo = made ? made.get(store) : null;
    if (memo && memo.base) return storeFor(key, memo.base); // re-evaluate a view when its document or mode changes
    var site = siteFor(given(key) ? key : keyOfStore(store)) || (given(key) && keyOfStore(store) ? siteFor(keyOfStore(store)) : null);
    var studioOnly = !OO.mode || OO.mode === 'online' || OO.mode === 'landing' || OO.mode === 'kiosk';
    if (!site && !studioOnly) return store;
    var activeStyle = styleSurface();
    if (memo && memo.site === site && memo.studioOnly === studioOnly && memo.surface === activeStyle) return memo.out;
    var out = site ? overlay(store, site, studioOnly) : Object.assign({}, store, {
      theme: { isConfigured: false }, logoUrl: store.storeLogoUrl || null,
    });
    if (made) { made.set(store, { site: site, studioOnly: studioOnly, surface: activeStyle, out: out }); made.set(out, { base: store }); }
    return out;
  }
  function appDesign(key) {
    if (surface() !== 'app') return null;
    var s = siteFor(key);
    return s && s.doc.app.layout === 'market' ? s.doc.app : null;
  }
  function pizzeriaDesign(key) {
    if (surface() !== 'app') return null;
    var s = siteFor(key);
    return s && s.doc.app.layout === 'pizzeria' ? s.doc.app : null;
  }
  // WL-OPT: false when the site that applies to `key` now hides the "Powered by SIPO" badge (brand.poweredBy false; no
  // surface override), else true. No key: the current route's (mode app: its chain, as OO.App reads the site), else the
  // store the cart is on. OO.ui.PoweredBySipo asks this for every badge.
  function routeKey() {
    var R = OO.router;
    var r = R && typeof R.current === 'function' ? guard(function () { return R.current(); }, null) : null;
    var k = r ? (r.chain || r.hotel) : null;
    if (given(k)) return String(k);
    var S = OO.store;
    var h = S && typeof S.get === 'function' ? guard(function () { return S.get().hotel; }, null) : null;
    return given(h) ? String(h) : null;
  }
  function poweredBy(key) {
    var s = siteFor(given(key) ? key : routeKey());
    return !(s && s.doc.brand.poweredBy === false);
  }
  function tabBar(key) { var s = siteFor(key); return s && s.doc.app.tabBar ? s.doc.app.tabBar.slice() : null; }
  function greeting(key, name) {
    var s = siteFor(key);
    var g = s ? s.doc.app.greeting : null;
    if (g === null || g === undefined) return null;
    var n = given(name) ? String(name).trim() : '';
    // 'Hi {name}' → 'Hi Sam'; signed out → 'Hi' ('Hi {name}, welcome' → 'Hi, welcome').
    return g.split('{name}').join(n).replace(/\s+/g, ' ').replace(/\s+([,!.?])/g, '$1').replace(/^[\s,]+|[\s,]+$/g, '') || null;
  }

  // ---- WL-5: the landing website's ordering ----------------------------------------------------------------------
  function landingOrdering(key) {
    if (!OO.landingMode) return { on: true, reason: null };
    var site = siteFor(key);
    if (!site) return { on: true, reason: null };
    if (site.products.online !== true) return { on: false, reason: 'product' };
    if (site.ordering && site.ordering.online === false) return { on: false, reason: site.ordering.reason || null };
    return { on: true, reason: null };
  }
  function browseOnly(key) {
    if (!OO.landingMode || !given(key)) return false;
    var site = siteFor(key);
    if (!site) return false;
    if (!landingOrdering(key).on) return true;
    var stores = site.ordering ? site.ordering.stores : null;
    return !!stores && stores[lower(key)] === false;
  }

  // ---- the favicon -----------------------------------------------------------------------------------------------
  var head = { el: null, original: null, created: false };
  function applyHead(store) {
    var doc = typeof document !== 'undefined' ? document : null;
    if (!doc || !doc.head || typeof doc.querySelector !== 'function') return;
    var icon = store && store.whitelabel ? store.whitelabel.favicon : null;
    if (icon && OO.ui && OO.ui.iconSource) icon = OO.ui.iconSource(icon, 32);
    guard(function () {
      if (icon) {
        if (!head.el) {
          var el = doc.querySelector('link[rel~="icon"]');
          head.created = !el;
          if (!el) { el = doc.createElement('link'); el.rel = 'icon'; doc.head.appendChild(el); }
          head.el = el;
          head.original = el.getAttribute('href');
        }
        if (head.el.getAttribute('href') !== icon) head.el.setAttribute('href', icon);
      } else if (head.el) {
        if (head.created) { if (head.el.parentNode) head.el.parentNode.removeChild(head.el); } else if (head.original === null) head.el.removeAttribute('href'); else head.el.setAttribute('href', head.original);
        head.el = null;
        head.original = null;
        head.created = false;
      }
    });
  }

  // ---- React -------------------------------------------------------------------------------------------------------
  function useRevision() {
    var React = window.React;
    return React.useSyncExternalStore(subscribe, revision, revision);
  }
  var BAR_H = 32;
  var BAR_STYLE = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001, height: BAR_H, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '0 12px', background: '#1A1A1A', color: '#FFFFFF', fontSize: 13, fontWeight: 600,
    lineHeight: 1.2, textAlign: 'center', fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
  };
  function PreviewBar() {
    var React = window.React;
    useRevision();
    if (!preview.token) return null;
    var expired = preview.state === 'expired';
    var label = expired ? 'Preview link expired — orders are disabled' : 'Preview — orders are disabled';
    return React.createElement(React.Fragment, null,
      React.createElement('div', { className: 'oo-wl-preview-spacer', 'aria-hidden': 'true', style: { height: BAR_H } }),
      React.createElement('div', { className: 'oo-wl-preview-bar', role: 'status', 'data-state': previewState(), style: BAR_STYLE }, label));
  }

  // ---- test hooks -------------------------------------------------------------------------------------------------
  function reset(opts) {
    if (preview && preview.timer && typeof clearTimeout === 'function') clearTimeout(preview.timer);
    blank(opts && typeof opts.search === 'string' ? opts.search : undefined);
    fromBoot(opts && own(opts, 'boot') ? opts.boot : undefined);
    emit();
  }
  function seed(key, body) {
    var site = parseSite(body, false);
    remember(lower(key), site);
    emit();
    return site;
  }

  blank();
  fromBoot();

  OO.whitelabel = OO.lib.whitelabel = {
    SCHEMA: SCHEMA,
    POLL_MS: POLL_MS,
    surface: surface,
    ensure: ensure,
    status: status,
    siteFor: siteFor,
    storeFor: storeFor,
    themeOver: themeOver,
    parseDocument: parseDocument,
    forSurface: forSurface,
    isPreview: isPreview,
    previewPending: previewPending,
    previewState: previewState,
    previewAllows: previewAllows,
    poweredBy: poweredBy,
    tabBar: tabBar,
    appDesign: appDesign,
    kitchenDesign: function (key) { var s = surface() === 'app' ? siteFor(key) : null; return s && surfaceDesign(s.doc, 'app') === 'kkitchen' ? s.doc.app : null; },
    pizzeriaDesign: pizzeriaDesign,
    pizzaClubDesign: function (key) { var s = surface() === 'app' ? siteFor(key) : null; return s && s.doc.app.layout === 'pizzaclub' ? s.doc.app : null; },
    zetaDesign: function (key) { var name = surface(), s = name === 'app' || name === 'online' ? siteFor(key) : null; return s && surfaceDesign(s.doc, name) === 'zeta' ? s.doc.app : null; },
    skin: skin, // WL-SUPER
    greeting: greeting,
    landingOrdering: landingOrdering,
    browseOnly: browseOnly,
    orderingOf: orderingOf,
    applyHead: applyHead,
    subscribe: subscribe,
    revision: revision,
    useRevision: useRevision,
    PreviewBar: PreviewBar,
    __reset: reset,
    __seed: seed,
    __pollOnce: pollOnce,
    __setFetch: function (fn) { fetchHook = typeof fn === 'function' ? fn : null; },
  };
})();


// ===================== src/lib/landing.jsx =====================
// src/lib/landing.jsx: OO.landing (alias OO.lib.landing), the pure logic of the white-label multi-store web landing page
// (docs/whitelabel/WHITELABEL-PLAN.md §5 `landing`, §7.3; lane WL-3). JSX-free (the tests load it raw in node:vm); the
// page itself is src/pages/landing.jsx and its sections src/pages/landing-sections.jsx.
//
// The document's `landing` (as WL-2's OO.whitelabel hands it over: site.doc.landing, the server-validated §5 shape; every
// field is re-checked here before use, and a value that fails is left out, never rendered):
//   nav [{label, target}] · cta {label, target} · hero {autoplay, slides [{image, video, headline, subline, cta}]} ·
//   ticker {enabled, items} · sections [Section] · footer {columns, socials, legal, copyright} · seo (server-side only).
//
// Targets (Target := order | page | section | url) → a link, with ctx { key (the landing's store key), sections (the
// sections shown: sectionsOf) }:
//   {type:'order'} and {type:'page', page:'menu'}   → { kind: 'order' }: the order hand-off (orderPlan, below).
//   {type:'page', page}  offers → the default store's deals · about · track · account → those pages of the landing's key ·
//                        terms / privacy → its legal pages · stores → the stores section when one is shown, else the
//                        order hand-off's store chooser ({ kind: 'order', choose: true }).
//                        → { kind: 'app', href } (in-app, same origin: the online pages below the landing).
//   {type:'section', section}  → { kind: 'section', id, href: '#' + anchorId(id) } when that section is shown; else null.
//   {type:'url', url}    → { kind: 'url', href } for an https URL (§5: ≤ 500, no credentials, no whitespace); else null.
// Anything else → null (the link is left out).
//
// The order hand-off (orderPlan(chain, key, query)): a chain whose list holds exactly one store → { kind: 'go', href:
// /{thatStore}/menu (+ query) }; several → { kind: 'choose' } (the page opens the store chooser: OO.pages.Stores.usePicker's
// list, then /{branchKey}/menu + query); no chain (not read yet, failed, empty) → { kind: 'choose' } while it loads, and
// { kind: 'go', href: /{key}/menu } once it failed or is empty (never a dead button). query: a deep link the menu opens
// ({ type: 'category' | 'product', id }). handoff(branchKey, chainKey, query) → href: the chooser's "Order from":
// OO.store.setBranch(branchKey, chainKey), then OO.router.go(/{branchKey}/menu + query).
//
// Sections (sectionsOf(landing)): the typed registry (TYPES: offers, bestSellers, categories, rewards, stores, stats,
// appPromo, about, franchise, contact, richText), in document order, enabled ones only, each id ^[a-z0-9-]{1,32}$ once.
// anchorId(id) → 'wl-' + id (the section element's id). paragraphs(text) → the non-empty lines of a multi-line body.
// directionsUrl(store) → a Google Maps directions URL (by coordinates, else by name + address), or null.
//
// WL-5 (landing-only websites). ctx.orderingOff (the website's ordering is off: OO.whitelabel.landingOrdering(key).on false)
// turns the page information-only:
//   - sectionsOf(landing, { orderingOff: true }) leaves out offers and rewards (they exist for ordering);
//   - {type:'order'} follows landing.orderingOff (orderingOffOf: { cta: 'menu' | 'call' | 'directions' | 'url' | 'hide',
//     label (default 'View menu'), url }): menu → { kind: 'order', label } (the hand-off to the BROWSE-ONLY menu, the
//     store chooser on a multi-store chain); call → { kind: 'tel', href: 'tel:…', label } from ctx.phone; directions →
//     { kind: 'url', href, label } from ctx.directions; url → { kind: 'url', href, label } from orderingOff.url; hide, or a
//     call / directions / url with nothing to point at → null (the link is left out, never a dead one). A 'menu' fallback
//     whose menu can't be read (ctx.menuUnavailable) is left out too;
//   - {type:'page', page:'menu'} stays the menu hand-off (its own label); offers, track and account pages → null.
//   With ordering on, every target resolves exactly as before WL-5.
// Data sections (dataState({ loading, failed, empty, late }) → 'wait' | 'show' | 'hide'): a skeleton while the read is
// pending, for at most DEADLINE_MS (8 s, `late`); then the section, or nothing when the read failed, came back empty, or
// is still pending. Never a skeleton for good.
// The store directory (loadStores(key) → Promise<[DirectoryStore] | null>, never rejects; once per key and page load, a
// failure retried on the next call): GET {apiBase}/whitelabel/site/{key}/stores (App\Http\Controllers\Whitelabel\
// LandingStoresController: every chain store, orderable or not). DirectoryStore = { key | null, name, address, phone,
// email, lat, lng, image, isMaster, orderable, open, label, hours: [{ day 0 = Sunday … 6, open 'HH:mm', close 'HH:mm' }] }.
// hoursLines(hours) → [{ days: 'Mon–Fri', time: '9am – 5pm' }] (Monday first; identical neighbouring days grouped;
// 'Closed' for a day with no hours); telHref(phone) → 'tel:+6491112222' | null.
// Forms (franchise, contact): validate(fields) → { ok, errors: { name?, email?, phone?, message? } } (the server's rules:
// name 1..100, an email, phone 0..32 of digits + ( ) - . space, message 1..2000); body(sectionId, fields) → the request
// body { section_id, name, email, phone, message, website } (website = the honeypot field as typed: a person leaves it
// empty); submit(key, body) → Promise<{ ok, message, errors }> POSTs {apiBase}/whitelabel/site/{key}/contact (JSON; never
// rejects). The recipient is never sent: the server reads it from the published document. Test hook: __setFetch(fn | null).
//
// WL-CHK (the `bold` design and the opt-in blocks; docs: briefs SPEC §A–§G). Every addition is optional: a document
// without the new keys resolves exactly as before.
//   design(landing) → 'bold' | 'super' | 'standard' (landing.design; absent or unknown → 'standard'; WL-SUPER: + super).
//   {type:'page', page:'allergy'} → the legal page /{key}/allergy-information (router 'legal', kind 'allergy').
//   A section with display 'dialog' is not drawn on the page: a section target that resolves to it (also the stores page
//   when that is the first stores section) carries dialog: true, and the page opens it in a dialog (isDialog(section)).
//   Only the content and form types take a dialog (DIALOG_TYPES: franchise, contact, richText, faq, newsletter, about,
//   banner, features, gallery, testimonials, videos, logos; the studio's list is the same); any other type with
//   display 'dialog' is drawn on the page as if the key were absent (its data, carousels and controls belong there).
//   styleClasses: tone adds 'deep' (near-black), and style.pattern grid | dots | plus | stars → 'oo-lp-pattern--{p}'
//   (WL-SUPER: + grain, a film-grain / chalk texture; styles/landing-super.css draws it for every design).
//   heroInterval(hero) → the auto-advance in ms for hero.interval (whole seconds 3..15), else null (the page's 6 s).
//   eyebrowDot(text) → true when the eyebrow starts with a letter or a digit (the bold pill draws a leading dot; an
//   emoji or ★ is kept as is). Any letter or digit where the engine has Unicode property escapes, ASCII ones elsewhere:
//   the pattern is built on first use (a \p{…} regex literal is a SyntaxError for the whole bundle on older engines).
//   countParts(value) → { prefix, number, decimals, suffix, grouped } | null: a stat value's numeric part ("100+",
//   "10M+", "$1,200", "4.8★"), for the count-up; countText(parts, t) → the value at progress t (0..1, eased), the
//   prefix and suffix kept, t ≥ 1 → the value exactly as written.
//   nearest(stores, origin, limit) → the stores with distanceKm (OO.model.haversineKm from origin {lat, lng}, null without
//   one or without coordinates), nearest first (stores without a distance keep their order, after the others), at most
//   limit; distanceText(km) → '850m' | '4.1km' | null. geoPermission() → Promise<'granted' | 'prompt' | 'denied' |
//   'unsupported'> (navigator.permissions; never prompts). sharedPrefixWords(names) → how many leading words all the
//   names share (the chain list's short-name rule, for the store directory's names).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.lib = OO.lib || {};

  var TYPES = ['offers', 'bestSellers', 'categories', 'rewards', 'stores', 'stats', 'appPromo', 'about', 'franchise', 'contact', 'richText', 'products', 'videos', 'features', 'testimonials', 'gallery', 'faq', 'banner', 'newsletter', 'menu', 'logos', 'marquee',
    'pageHero', 'quote', 'cards', 'contactInfo', 'image', 'composition']; // WL-VEL: the new section types (src/pages/landing-boutique-sections.jsx)
  // WL-CHK: the section types display 'dialog' applies to (see the header).
  var DIALOG_TYPES = ['franchise', 'contact', 'richText', 'faq', 'newsletter', 'about', 'banner', 'features', 'gallery', 'testimonials', 'videos', 'logos'];
  // WL-5: the sections and pages an information-only website leaves out, and the "Order" buttons' fallbacks.
  var OFF_SECTIONS = { offers: 1, rewards: 1 };
  var OFF_PAGES = { offers: 1, track: 1, account: 1 };
  var OFF_CTAS = ['menu', 'call', 'directions', 'url', 'hide'];
  var OFF_LABEL = 'View menu';
  var DEADLINE_MS = 8000;
  var DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var WEEK = [1, 2, 3, 4, 5, 6, 0];
  var PAGES = { offers: 'deals', about: 'about', track: 'track', account: 'account', terms: 'legal', privacy: 'legal', allergy: 'legal', settings: 'settings', orders: 'orders' };
  var SECTION_ID = /^[a-z0-9-]{1,32}$/;
  var EMAIL = /^[^\s@<>()",;:\\\[\]]+@[^\s@<>()",;:\\\[\]]+\.[^\s@<>()",;:\\\[\]]+$/;
  var PHONE = /^[0-9+()\-. ]{0,32}$/;
  var CONTROL = /[\u0000-\u001F\u007F]/;
  var CONTROL_ML = /[\u0000-\u0009\u000B\u000C\u000E-\u001F\u007F]/;
  var GLOBAL = typeof globalThis !== 'undefined' ? globalThis : window;
  var fetchHook = null;

  function obj(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : null; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function str(v) { return typeof v === 'string' ? v.trim() : ''; }
  function given(v) { return v !== undefined && v !== null && String(v).trim() !== ''; }

  function httpsUrl(v) {
    if (typeof v !== 'string' || v.length > 500 || /\s/.test(v) || !/^https:\/\/[^\/?#]+/i.test(v)) return null;
    var authority = /^https:\/\/([^\/?#]+)/i.exec(v)[1];
    return authority.indexOf('@') >= 0 ? null : v;
  }
  function text(v, max) {
    var s = str(v);
    return s && Array.from(s).length <= (max || 1200) ? s : '';
  }

  // ---- sections ---------------------------------------------------------------------------------------------------
  function sectionsOf(landing, opts) {
    var seen = {};
    var off = !!(opts && opts.orderingOff);
    return arr(obj(landing) && landing.sections).filter(function (s) {
      if (!obj(s) || s.enabled !== true || TYPES.indexOf(s.type) < 0 || typeof s.id !== 'string' || !SECTION_ID.test(s.id) || seen[s.id]) return false;
      seen[s.id] = true;
      return !(off && OFF_SECTIONS[s.type]);
    });
  }
  function anchorId(id) { return 'wl-' + String(id); }
  function paragraphs(v) { return str(v).split(/\n+/).map(function (p) { return p.trim(); }).filter(Boolean); }

  // ---- targets ----------------------------------------------------------------------------------------------------
  function shown(ctx, id) {
    return arr(ctx && ctx.sections).some(function (s) { return s && s.id === id; });
  }
  function firstOfType(ctx, type) {
    var hit = arr(ctx && ctx.sections).filter(function (s) { return s && s.type === type; })[0];
    return hit ? hit.id : null;
  }
  // WL-CHK: a section link → { kind: 'section', id, href }, plus dialog: true when that section opens in a dialog.
  function sectionTarget(ctx, id) {
    var out = { kind: 'section', id: id, href: '#' + anchorId(id) };
    var s = arr(ctx && ctx.sections).filter(function (x) { return x && x.id === id; })[0];
    if (isDialog(s)) out.dialog = true;
    return out;
  }
  function isDialog(section) { return !!obj(section) && section.display === 'dialog' && DIALOG_TYPES.indexOf(section.type) >= 0; }
  function design(landing) { return obj(landing) && (landing.design === 'bold' || landing.design === 'super') ? landing.design : 'standard'; } // WL-SUPER: + super
  function appHref(page, params, key) {
    return OO.router.href(page, params || null, { hotel: key, query: OO.appKit && OO.appKit.query ? OO.appKit.query() : undefined });
  }
  // WL-5: landing.orderingOff, its defaults filled in: { cta, label, url }.
  function orderingOffOf(landing) {
    var o = obj(obj(landing) && landing.orderingOff) || {};
    return {
      cta: OFF_CTAS.indexOf(o.cta) >= 0 ? o.cta : 'menu',
      label: text(o.label, 24) || OFF_LABEL,
      url: httpsUrl(o.url),
    };
  }
  function telHref(phone) {
    var digits = str(phone).replace(/[^0-9+]/g, '');
    return /^\+?[0-9]{3,20}$/.test(digits) ? 'tel:' + digits : null;
  }
  // WL-5: an "Order" target while the website's ordering is off (see the header).
  function offTarget(ctx) {
    var o = (ctx && ctx.off) || orderingOffOf(null);
    if (o.cta === 'hide') return null;
    if (o.cta === 'call') { var tel = telHref(ctx && ctx.phone); return tel ? { kind: 'tel', href: tel, label: o.label } : null; }
    if (o.cta === 'directions') { var d = httpsUrl(ctx && ctx.directions); return d ? { kind: 'url', href: d, label: o.label } : null; }
    if (o.cta === 'url') return o.url ? { kind: 'url', href: o.url, label: o.label } : null;
    return ctx && ctx.menuUnavailable ? null : { kind: 'order', label: o.label };
  }
  function targetOf(target, ctx) {
    var t = obj(target);
    var key = ctx && given(ctx.key) ? String(ctx.key) : null;
    var off = !!(ctx && ctx.orderingOff);
    if (!t) return null;
    if (t.type === 'categories') return ctx && ctx.menuUnavailable ? null : { kind: 'categories' };
    if (t.type === 'order') return off ? offTarget(ctx) : { kind: 'order' };
    if (t.type === 'url') { var u = httpsUrl(t.url); return u ? { kind: 'url', href: u } : null; }
    if (t.type === 'section') {
      return typeof t.section === 'string' && shown(ctx, t.section) ? sectionTarget(ctx, t.section) : null;
    }
    if (t.type !== 'page' || typeof t.page !== 'string') return null;
    if (t.page === 'menu') return off && ctx.menuUnavailable ? null : { kind: 'order' };
    if (t.page === 'stores') {
      return key ? { kind: 'app', href: appHref('stores', null, key) } : null;
    }
    if (off && OFF_PAGES[t.page]) return null;
    if (!Object.prototype.hasOwnProperty.call(PAGES, t.page) || !key) return null;
    var page = PAGES[t.page];
    var params = page === 'legal' ? { kind: t.page } : null;
    return { kind: 'app', href: appHref(page, params, key) };
  }

  // ---- the order hand-off ----------------------------------------------------------------------------------------
  function menuHref(key, query) {
    var q = Object.assign({}, OO.appKit && OO.appKit.query ? OO.appKit.query() || {} : {}, query || {});
    return OO.router.href('menu', null, { hotel: key, query: Object.keys(q).length ? q : undefined });
  }
  // chainState: { chain (OO.useChain's Chain | null), loading (bool), failed (bool) }
  function orderPlan(chainState, key, query) {
    var c = chainState && chainState.chain;
    var stores = c ? arr(c.stores) : [];
    if (stores.length === 1 && given(stores[0].key)) return { kind: 'go', href: menuHref(stores[0].key, query) };
    if (stores.length > 1) return { kind: 'choose' };
    if (chainState && chainState.loading) return { kind: 'choose' };
    return { kind: 'go', href: menuHref(key, query) };
  }

  // The store chooser's hand-off: remember the branch for its chain (OO.store.setBranch, as Select store does; it records
  // in mode app only, a no-op here) and go to its menu (+ the deep link). → the href.
  function handoff(branchKey, chainKey, query) {
    if (OO.store && typeof OO.store.setBranch === 'function' && given(branchKey)) OO.store.setBranch(branchKey, chainKey || null);
    var href = menuHref(branchKey, query);
    OO.router.go(href);
    return href;
  }

  function directionsUrl(store) {
    if (!store) return null;
    var lat = Number(store.lat), lng = Number(store.lng);
    if (store.lat !== null && store.lat !== undefined && store.lng !== null && store.lng !== undefined && isFinite(lat) && isFinite(lng)) {
      return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(lat + ',' + lng);
    }
    var where = [str(store.name), str(store.address)].filter(Boolean).join(', ');
    return where ? 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(where) : null;
  }

  // ---- WL-5: data sections, the store directory, opening hours --------------------------------------------------
  function dataState(st) {
    st = st || {};
    if (st.failed) return 'hide';
    if (st.loading) return st.late ? 'hide' : 'wait';
    return st.empty ? 'hide' : 'show';
  }

  function clock(hhmm) {
    var m = /^(\d{1,2}):(\d{2})/.exec(str(hhmm));
    if (!m) return null;
    var h = Number(m[1]) % 24, min = Number(m[2]);
    var suffix = h < 12 ? 'am' : 'pm';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (min ? ':' + (min < 10 ? '0' : '') + min : '') + suffix;
  }
  function hoursLines(hours) {
    var byDay = {};
    arr(hours).forEach(function (h) {
      if (!obj(h) || !(h.day >= 0 && h.day <= 6)) return;
      var a = clock(h.open), b = clock(h.close);
      if (!a || !b) return;
      (byDay[h.day] = byDay[h.day] || []).push(a + ' – ' + b);
    });
    if (!Object.keys(byDay).length) return [];
    var out = [];
    WEEK.forEach(function (d) {
      var time = byDay[d] ? byDay[d].join(', ') : 'Closed';
      var last = out[out.length - 1];
      if (last && last.time === time && last.end === WEEK[WEEK.indexOf(d) - 1]) { last.end = d; return; }
      out.push({ start: d, end: d, time: time });
    });
    return out.map(function (r) {
      return { days: r.start === r.end ? DAY_NAMES[r.start] : DAY_NAMES[r.start] + '–' + DAY_NAMES[r.end], time: r.time };
    });
  }

  function num(v) { var n = Number(v); return v === null || v === undefined || v === '' || !isFinite(n) ? null : n; }
  function directoryStore(r) {
    if (!obj(r) || !text(r.name, 200)) return null;
    var lat = num(r.latitude), lng = num(r.longitude);
    var coords = lat !== null && lng !== null && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);
    var email = str(r.email);
    return {
      key: given(r.key) ? String(r.key).trim() : null,
      name: text(r.name, 200),
      address: text(r.address, 300) || null,
      phone: text(r.phone, 40) || null,
      email: email && EMAIL.test(email) && email.length <= 254 ? email : null,
      lat: coords ? lat : null, lng: coords ? lng : null,
      image: httpsUrl(r.image),
      isMaster: r.is_master === true,
      orderable: r.orderable === true,
      menuAvailable: typeof r.menu_available === 'boolean' ? r.menu_available : r.orderable === true,
      open: r.open_now === true,
      label: text(r.open_label, 80) || null,
      hours: arr(r.hours).filter(function (h) { return obj(h) && h.day >= 0 && h.day <= 6 && typeof h.open === 'string' && typeof h.close === 'string'; }),
    };
  }
  var storesReads = Object.create(null);
  function loadStores(key) {
    var f = fetchFn();
    if (!f || !given(key)) return Promise.resolve(null);
    var k = String(key).trim().toLowerCase();
    if (storesReads[k]) return storesReads[k];
    var p = storesReads[k] = Promise.resolve().then(function () {
      return f(apiBase() + '/whitelabel/site/' + encodeURIComponent(String(key).trim()) + '/stores',
        { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } });
    }).then(function (res) {
      if (!res || res.status !== 200 || typeof res.json !== 'function') return null;
      return Promise.resolve(res.json()).then(function (b) {
        var d = obj(b && b.data);
        return d && Array.isArray(d.stores) ? d.stores.map(directoryStore).filter(Boolean) : null;
      }, function () { return null; });
    }, function () { return null; });
    p.then(function (v) { if (v === null && storesReads[k] === p) delete storesReads[k]; });
    return p;
  }

  // ---- forms --------------------------------------------------------------------------------------------------------
  function validate(f) {
    f = f || {};
    var errors = {};
    var name = str(f.name), email = str(f.email), phone = str(f.phone), message = str(f.message);
    if (!name) errors.name = 'Enter your name.';
    else if (name.length > 100 || CONTROL.test(name)) errors.name = 'Enter a shorter name.';
    if (!email) errors.email = 'Enter your email.';
    else if (email.length > 254 || !EMAIL.test(email)) errors.email = 'Enter a valid email address.';
    if (phone && !PHONE.test(phone)) errors.phone = 'Enter a valid phone number.';
    if (!message) errors.message = 'Enter a message.';
    else if (message.length > 2000 || CONTROL_ML.test(message)) errors.message = 'Keep your message under 2000 characters.';
    return { ok: Object.keys(errors).length === 0, errors: errors };
  }
  function body(sectionId, f) {
    f = f || {};
    return {
      section_id: String(sectionId), name: str(f.name), email: str(f.email), phone: str(f.phone),
      message: str(f.message), website: typeof f.website === 'string' ? f.website : '',
    };
  }
  function apiBase() { return String((window.__SIPO_BOOT || {}).apiBase || '/api').replace(/\/+$/, ''); }
  function fetchFn() {
    if (fetchHook) return fetchHook;
    var f = typeof window.fetch === 'function' ? window.fetch : GLOBAL.fetch;
    return typeof f === 'function' ? function (u, i) { return f.call(window, u, i); } : null;
  }
  var SENT = 'Thanks, your message has been sent.';
  var FAILED = 'We couldn’t send your message. Please try again later.';
  function submit(key, payload, kind) {
    var f = fetchFn();
    if (!f || !given(key)) return Promise.resolve({ ok: false, message: FAILED, errors: {} });
    var init = {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };
    return Promise.resolve().then(function () {
      return f(apiBase() + '/whitelabel/site/' + encodeURIComponent(String(key).trim()) + (kind === 'newsletter' ? '/subscribe' : '/contact'), init);
    }).then(function (res) {
      var parsed = res && typeof res.json === 'function' ? Promise.resolve(res.json()).then(null, function () { return null; }) : Promise.resolve(null);
      return parsed.then(function (b) {
        var status = res ? res.status : 0;
        if (status === 200 && b && b.status === true) {
          var out = { ok: true, message: typeof b.message === 'string' ? b.message : SENT, errors: {} };
          if (kind === 'newsletter') out.code = text(b.code, 24) || null;
          return out;
        }
        if (status === 422) return { ok: false, message: 'Please check the form.', errors: obj(b && b.errors) || {} };
        if (status === 429) return { ok: false, message: 'Too many messages. Please try again in a few minutes.', errors: {} };
        if (status === 404) return { ok: false, message: 'This form isn’t available right now.', errors: {} };
        return { ok: false, message: FAILED, errors: {} };
      });
    }, function () { return { ok: false, message: FAILED, errors: {} }; });
  }


  // WL-5 builder data is drawn from useHotel's cached, normalized customer menu. Hidden items stay hidden.
  function visibleMenu(menu, display) {
    if (!menu || !display) return menu;
    var view = OO.pages.Menu.logic.buildView(menu, [], display), products = {};
    Object.keys(menu.products || {}).forEach(function (id) { if (view.products[id] && menu.products[id].hidden !== true) products[id] = menu.products[id]; });
    return Object.assign({}, menu, { categories: view.categories, products: products });
  }
  function menuProducts(menu) {
    if (!menu || !obj(menu.products)) return [];
    return Object.keys(menu.products).map(function (id) { return menu.products[id]; }).filter(function (p) { return p && p.hidden !== true && p.name; });
  }
  function menuCategories(menu) {
    return arr(menu && menu.categories).filter(function (c) { return c && c.kind !== 'combo' && c.hidden !== true && arr(c.productIds).some(function (id) { return menu.products && menu.products[id] && menu.products[id].hidden !== true; }); });
  }
  function productsOf(section, menu, popular) {
    var s = section || {}, pool = menu && menu.products || {}, source = s.source || 'popular', rows;
    if (source === 'manual') rows = arr(s.productIds).map(function (id) { return pool[id]; });
    else if (source === 'category') {
      var cat = menuCategories(menu).filter(function (c) { return String(c.id) === String(s.categoryId); })[0];
      rows = arr(cat && cat.productIds).map(function (id) { return pool[id]; });
    } else rows = OO.model.popularProducts(popular, menu);
    var seen = {};
    return rows.filter(function (p) { if (!p || p.hidden === true || seen[p.id]) return false; seen[p.id] = true; return true; }).slice(0, Math.min(24, Math.max(1, Number(s.limit) || 8)));
  }
  function searchProducts(menu, query) {
    var q = str(query).toLocaleLowerCase();
    if (!q) return [];
    return menuProducts(menu).filter(function (p) { return [p.name, p.description].concat(arr(p.tags)).join(' ').toLocaleLowerCase().indexOf(q) >= 0; }).slice(0, 12);
  }
  function productPrice(product) {
    var prices = arr(product && product.sizes).map(function (s) { return s.price; }).filter(function (n) { return typeof n === 'number' && isFinite(n); });
    return { amount: prices.length ? Math.min.apply(null, prices) : product && product.price, from: prices.length > 0 };
  }
  var popularReads = Object.create(null);
  function loadPopular(key) {
    var k = lowerKey(key);
    if (!k || !OO.api || !OO.api.getPopular) return Promise.resolve(null);
    if (!popularReads[k]) popularReads[k] = Promise.resolve().then(function () { return OO.api.getPopular(key); }).catch(function () { delete popularReads[k]; return null; });
    return popularReads[k];
  }
  function lowerKey(v) { return str(String(v == null ? '' : v)).toLowerCase(); }
  function newsletterValidate(f) {
    f = f || {};
    var result = validate({ name: str(f.name) || 'Subscriber', email: f.email, message: 'Subscribe' });
    return result;
  }
  function subscribe(key, section, fields) {
    fields = fields || {};
    return submit(key, { section_id: String(section), name: str(fields.name), email: str(fields.email), website: typeof fields.website === 'string' ? fields.website : '' }, 'newsletter');
  }
  function styleClasses(section) {
    var s = obj(section && section.style);
    if (!s) return '';
    // WL-CHK: tone 'deep' and the background pattern (absent → the classes exactly as before).
    return [['tone', ['default', 'muted', 'brand', 'accent', 'dark', 'deep']], ['spacing', ['compact', 'normal', 'roomy']], ['width', ['contained', 'full']], ['align', ['left', 'center']], ['pattern', ['grid', 'dots', 'plus', 'stars', 'grain']]].map(function (row) {
      return row[1].indexOf(s[row[0]]) >= 0 ? 'oo-lp-' + row[0] + '--' + s[row[0]] : '';
    }).filter(Boolean).join(' ');
  }

  // ---- WL-CHK helpers (see the header) ------------------------------------------------------------------------------
  function heroInterval(hero) {
    var n = obj(hero) ? hero.interval : null;
    return typeof n === 'number' && Math.floor(n) === n && n >= 3 && n <= 15 ? n * 1000 : null;
  }
  var leadingWord = null;
  function eyebrowDot(v) {
    if (!leadingWord) {
      try { leadingWord = new RegExp('^[\\p{L}\\p{N}]', 'u'); } catch (e) { leadingWord = /^[A-Za-z0-9]/; }
    }
    return leadingWord.test(str(v));
  }
  var COUNT = /^([^0-9]*?)(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?(.*)$/;
  function countParts(v) {
    var m = COUNT.exec(str(v));
    if (!m) return null;
    var whole = m[2].replace(/,/g, '');
    var number = Number(whole + (m[3] ? '.' + m[3] : ''));
    return isFinite(number) ? { prefix: m[1], number: number, decimals: m[3] ? m[3].length : 0, suffix: m[4], grouped: m[2].indexOf(',') >= 0, text: str(v) } : null;
  }
  function countText(p, t) {
    if (!p) return '';
    if (!(t < 1)) return p.text;
    var k = Math.max(0, t);
    var eased = 1 - Math.pow(1 - k, 3);
    var n = p.number * eased;
    var digits = p.decimals ? n.toFixed(p.decimals) : String(Math.floor(n));
    if (p.grouped) { var parts = digits.split('.'); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); digits = parts.join('.'); }
    return p.prefix + digits + p.suffix;
  }
  function nearest(stores, origin, limit) {
    var M = OO.model;
    var o = obj(origin) && M && M.validCoords(Number(origin.lat), Number(origin.lng)) ? { lat: Number(origin.lat), lng: Number(origin.lng) } : null;
    var rows = arr(stores).map(function (s, i) {
      var lat = s ? Number(s.lat) : NaN, lng = s ? Number(s.lng) : NaN;
      var km = o && s && s.lat != null && s.lng != null && M.validCoords(lat, lng) ? M.haversineKm(o, { lat: lat, lng: lng }) : null;
      return { s: s, km: km, i: i };
    }).filter(function (r) { return !!obj(r.s); });
    if (o) rows.sort(function (a, b) { return (a.km === null) - (b.km === null) || (a.km === null ? 0 : a.km - b.km) || a.i - b.i; });
    var n = Math.floor(Number(limit));
    return rows.slice(0, n >= 1 ? n : rows.length).map(function (r) { return Object.assign({}, r.s, { distanceKm: r.km }); });
  }
  // The leading words every name shares (2+ names; each keeps at least one word): the chain list's short-name rule.
  function sharedPrefixWords(names) {
    var split = arr(names).map(function (n) { return str(n).split(/\s+/).filter(Boolean); });
    if (split.length < 2) return 0;
    var n = 0;
    while (split.every(function (w) { return w.length > n + 1 && w[n] === split[0][n]; })) n += 1;
    return n;
  }
  function distanceText(km) {
    if (typeof km !== 'number' || !isFinite(km) || km < 0) return null;
    return km < 1 ? Math.round(km * 1000) + 'm' : km.toFixed(1) + 'km';
  }
  function geoPermission() {
    var nav = typeof navigator !== 'undefined' ? navigator : window.navigator;
    try {
      if (!nav || !nav.geolocation || typeof nav.geolocation.getCurrentPosition !== 'function') return Promise.resolve('unsupported');
      if (!nav.permissions || typeof nav.permissions.query !== 'function') return Promise.resolve('prompt');
      return Promise.resolve(nav.permissions.query({ name: 'geolocation' })).then(function (st) {
        return st && ['granted', 'prompt', 'denied'].indexOf(st.state) >= 0 ? st.state : 'prompt';
      }, function () { return 'prompt'; });
    } catch (e) { return Promise.resolve('prompt'); }
  }

  OO.landing = OO.lib.landing = {
    visibleMenu: visibleMenu, menuProducts: menuProducts, menuCategories: menuCategories, productsOf: productsOf, searchProducts: searchProducts, productPrice: productPrice, loadPopular: loadPopular, newsletterValidate: newsletterValidate, subscribe: subscribe, styleClasses: styleClasses,
    TYPES: TYPES,
    // WL-CHK
    design: design, isDialog: isDialog, DIALOG_TYPES: DIALOG_TYPES, heroInterval: heroInterval, eyebrowDot: eyebrowDot, countParts: countParts, countText: countText,
    nearest: nearest, distanceText: distanceText, geoPermission: geoPermission, sharedPrefixWords: sharedPrefixWords,
    // WL-5
    OFF_CTAS: OFF_CTAS,
    DEADLINE_MS: DEADLINE_MS,
    orderingOffOf: orderingOffOf,
    telHref: telHref,
    dataState: dataState,
    hoursLines: hoursLines,
    directoryStore: directoryStore,
    loadStores: loadStores,
    sectionsOf: sectionsOf,
    anchorId: anchorId,
    paragraphs: paragraphs,
    httpsUrl: httpsUrl,
    text: text,
    targetOf: targetOf,
    orderPlan: orderPlan,
    menuHref: menuHref,
    handoff: handoff,
    directionsUrl: directionsUrl,
    validate: validate,
    body: body,
    submit: submit,
    __setFetch: function (fn) { fetchHook = typeof fn === 'function' ? fn : null; storesReads = Object.create(null); },
  };
})();


// ===================== src/lib/landing-pages.jsx =====================
// src/lib/landing-pages.jsx: WL-VEL, the white-label website's multi-page keys and the `boutique` design's pure logic
// (scratchpad SPEC §1.2 / §1.3). JSX-free (the tests load it raw in node:vm). It EXTENDS OO.landing (src/lib/landing.jsx,
// loaded right before it): every addition is optional, and a document without the new keys resolves exactly as before.
//
// Design. design(landing) → 'boutique' for landing.design 'boutique'; anything else as before (OO.landing's own rule).
//
// Pages (landing.pages, 0..8 of { id, slug, label?, seo?, sections }). Home stays landing.sections.
//   pagesOf(landing) → the valid pages, in order (slug ^[a-z][a-z0-9-]{1,31}$, not 'page', once each; sections a list).
//   pageOf(landing, slug) → that page | null.   pageLabel(page) → its label, else the slug as words.
//   pageHref(key, slug | null, hash) → the page's URL (OO.router.href('landing', { slug })): /{key}/page/{slug} on the
//   landing mount, /{slug} on a custom domain; slug null → the home page. hash: an anchor without '#'.
//   whereIs(landing, sectionId, opts) → { page: slug | null } for the page (home first) showing that section, or null.
//   menuPage(landing, opts) → { page: slug | null, section } for the first page (home first) with a `menu` section.
//   menuHolder(landing, categoryId, opts) → the same for a menu category's link (VEL-FIX): the first page whose `menu`
//   section draws the #menu-{id} anchors (layout 'accordion' or jumpNav) and lists that category (no categoryIds, or
//   the id among them), as the server's SitePages picks it (tests/Fixtures/whitelabel/boutique-links-cases.json).
//   pageTitle(page, brandName) → the tab title of a site page: its seo.title, else "{label} | {brand}" (the label alone
//   without a brand name), as the server's <title> (OnlineOrderSeo::sitePage).
// Targets (targetOf, wrapping OO.landing.targetOf; ctx as there, plus ctx.landing (the document's landing) and ctx.page
// (the slug shown, null on home)):
//   {type:'sitePage', page}       → { kind: 'site', href, page, current } (null when no such page or no key).
//   {type:'menuCategory', categoryId} → { kind: 'site', href (the menu page + #menu-{id}), page, hash, current } when a
//                                  page holds a `menu` section with that anchor (menuHolder); else { kind: 'order', query:
//                                  { type: 'category', id } }.
//   {type:'section', section} on ANOTHER page (ids are unique across home and the pages) → { kind: 'site', href (that
//                                  page + #wl-{id}), page, hash, dialog? }. A section shown here resolves as before.
//                                  WL-VEL (VEL-C): with ctx.outside (an ordering page) every page, home too, is another
//                                  page (current is never true there).
//   {type:'home'}                 → { kind: 'site', href (the home page), page: null, current } (WL-VEL: current on the
//                                  home page itself; never on a page outside the landing, ctx.outside).
//   Anything else → OO.landing's own targetOf, unchanged.
// Redirects (landing.redirects, 0..10 of { from, to }; `from` canonical: no trailing '/', no '//', never under /page):
// redirectsOf(landing) → the valid ones; redirectFrom(landing,
// path) → the redirect whose `from` is that path (VEL-FIX: in any letter case), or null. The router reads `from` from the
// boot's site (custom domains, the mount's unknown sub-paths) and hands it to the page as route.params.redirect.
// withQuery(href, query) → href with a request's query (an object, OO.router.parseQuery's) before any #fragment, minus
// the PII keys (OO.router.withoutPii) and the keys href already sets (its own win), as the server's
// SitePages::withQuery (VEL-FIX: the landing mount's redirects keep the query).
// redirectTarget(landing, from, ctx, query, master) → { href, external? } | null: where the landing page sends a legacy
// redirect (VEL-FIX; null: no such redirect or no target, the page goes home): an https URL (external); order /
// categories (and a menuCategory without a page holding it) → the chain master's menu, as the server's 301; a section
// here → the home page + its anchor; else the target's href. Each with the request's query (withQuery).
// Chrome keys: statusBarOf(landing) → { countdown, hours, openText, closedText, opensText, offlineText } with the
// defaults filled in, or null (absent / not enabled) · wordmarkOf(landing, brand) → { title, tagline } | null ·
// footerCtaOf(landing) → { label, target } | null · orderingChromeOf(landing) → 'app' | 'site'.
// Status (statusOf(store, now, bar)): OO.schedule.openStatus in the store's time zone (the device clock read as wall
// time there, as the ordering menu does) → { state: 'open' | 'countdown' | 'opens' | 'closed' | 'offline', text (the
// copy with its placeholders filled; {countdown} as it reads now), parts ([{ text } | { countdown: true, text }]),
// targetMs (the next opening, epoch ms) | null }. {countdown} reads "16h 14m 31s" (the hours left out at 0) while the
// next opening is within `hours`; {day} 'today' | 'tomorrow' | 'Wednesday'; {time} '12:00 PM'; {reason} ' · ' + the
// store's offline reason, or ''. countdownText(ms) → "4m 2s". VEL-FIX: closed with no next opening (a temporary closure,
// or nothing in the coming week) reads the configured offlineText with {reason} (the closure's reason), state 'closed'.
// Hours: weekRows(store, now) → Monday first, [{ dow, day ('Mon'), dayLong ('Monday'), today, closed, time
// ('12:00 PM – 10:00 PM' | 'Closed') }] from OO.schedule.week (the store's own wall-clock hours per day, DST-correct);
// directoryRows(hours, now, tz) → the same from the store directory's [{ day, open, close }] (no store read).
// Sections: galleryOf(section) → { items (≤ 32: { image, caption, group }), filters ([{ key, label, count }], 'All'
// first, groups without a filter left out of the chips) } · priceOf(product) → { amount, from } ("from" only when the
// sizes' prices differ) · ruleText(group) → 'choose 1–5 · required' | 'up to 3' | 'optional' · enquiryTypesOf(section),
// enquiryBody(sectionId, fields, section) (OO.landing.body + enquiry_type / preferred_date ONLY when set and valid) and
// enquiryValidate(fields, section, now?, tz?). VEL-FIX: a preferred date is today..today + 365 days in the document's time
// zone (locale.timezone; the server's rule, UTC without one): dateRange(now, tz) → { min, max } (Y-m-d).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var L = OO.landing;
  if (!L) return;

  var SLUG = /^[a-z][a-z0-9-]{1,31}$/;
  var SECTION_ID = /^[a-z0-9-]{1,32}$/;
  var FROM = /^\/[a-z0-9][a-z0-9\/_-]{0,63}$/;
  var GROUP = /^[a-z0-9-]{1,24}$/;
  var YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
  var DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var WEEK = [1, 2, 3, 4, 5, 6, 0];
  var BAR_DEFAULTS = {
    openText: 'Open now · Order online',
    closedText: 'We are currently closed · Opening in {countdown}',
    opensText: 'We are currently closed · Opens {day} {time}',
    offlineText: 'Online ordering is unavailable{reason}',
  };

  function obj(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : null; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function str(v) { return typeof v === 'string' ? v.trim() : ''; }
  function text(v, max) { return L.text(v, max); }

  // ---- design -------------------------------------------------------------------------------------------------------
  var baseDesign = L.design;
  function design(landing) { return obj(landing) && ['boutique', 'canvas'].indexOf(landing.design) >= 0 ? landing.design : baseDesign(landing); }

  // ---- pages --------------------------------------------------------------------------------------------------------
  function pagesOf(landing) {
    var seen = {};
    return arr(obj(landing) && landing.pages).filter(function (p) {
      if (!obj(p) || typeof p.slug !== 'string' || !SLUG.test(p.slug) || p.slug === 'page' || seen[p.slug] || !Array.isArray(p.sections)) return false;
      seen[p.slug] = true;
      return true;
    }).slice(0, 8);
  }
  function pageOf(landing, slug) {
    if (typeof slug !== 'string' || !slug) return null;
    return pagesOf(landing).filter(function (p) { return p.slug === slug; })[0] || null;
  }
  function pageLabel(page) {
    var label = obj(page) ? text(page.label, 24) : '';
    if (label) return label;
    var s = obj(page) ? str(page.slug).replace(/-+/g, ' ') : '';
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }
  function pageHref(key, slug, hash) {
    var params = slug ? { slug: slug } : null;
    return OO.router.href('landing', params, { hotel: key, hash: hash || undefined });
  }
  // The pages in search order: home (slug null) first, then landing.pages.
  function everyPage(landing, opts) {
    var out = [{ page: null, sections: L.sectionsOf(landing, opts) }];
    pagesOf(landing).forEach(function (p) { out.push({ page: p.slug, sections: L.sectionsOf(p, opts) }); });
    return out;
  }
  function whereIs(landing, id, opts) {
    var hit = everyPage(landing, opts).filter(function (pg) { return pg.sections.some(function (s) { return s.id === id; }); })[0];
    return hit ? { page: hit.page, section: hit.sections.filter(function (s) { return s.id === id; })[0] } : null;
  }
  function menuPage(landing, opts) {
    var pages = everyPage(landing, opts);
    for (var i = 0; i < pages.length; i++) {
      var s = pages[i].sections.filter(function (x) { return x.type === 'menu' && !L.isDialog(x); })[0];
      if (s) return { page: pages[i].page, section: s };
    }
    return null;
  }
  // VEL-FIX: the page whose menu section draws the category's #menu-{id} anchor (see the header).
  function anchors(s, id) {
    if (s.type !== 'menu' || L.isDialog(s) || (s.layout !== 'accordion' && s.jumpNav !== true)) return false;
    return !arr(s.categoryIds).length || s.categoryIds.some(function (c) { return String(c) === String(id); });
  }
  function menuHolder(landing, id, opts) {
    var pages = everyPage(landing, opts);
    for (var i = 0; i < pages.length; i++) {
      var s = pages[i].sections.filter(function (x) { return anchors(x, id); })[0];
      if (s) return { page: pages[i].page, section: s };
    }
    return null;
  }
  function pageTitle(page, brandName) {
    var seo = obj(page) && obj(page.seo);
    var own = seo ? text(seo.title, 70) : '';
    if (own) return own;
    var label = pageLabel(page), brand = text(brandName, 120);
    return brand ? label + ' | ' + brand : label;
  }

  // ---- targets ------------------------------------------------------------------------------------------------------
  var baseTarget = L.targetOf;
  function here(ctx) { return ctx && typeof ctx.page === 'string' && ctx.page ? ctx.page : null; }
  // WL-VEL (VEL-C): is `slug` (null: home) the page shown? Never on a page outside the landing (ctx.outside: an ordering
  // page with the site chrome), which shows no landing page at all.
  function onPage(ctx, slug) { return !(ctx && ctx.outside) && here(ctx) === slug; }
  function targetOf(target, ctx) {
    var t = obj(target);
    var landing = ctx && obj(ctx.landing);
    var key = ctx && ctx.key !== undefined && ctx.key !== null && String(ctx.key).trim() !== '' ? String(ctx.key) : null;
    var opts = { orderingOff: !!(ctx && ctx.orderingOff) };
    if (t && t.type === 'sitePage') {
      var p = landing ? pageOf(landing, t.page) : null;
      if (!p || !key) return null;
      return { kind: 'site', href: pageHref(key, p.slug), page: p.slug, current: onPage(ctx, p.slug) };
    }
    if (t && t.type === 'home') { // WL-VEL: the landing's home page
      if (!key) return null;
      return { kind: 'site', href: pageHref(key, null), page: null, current: onPage(ctx, null) };
    }
    if (t && t.type === 'menuCategory') {
      var id = Number(t.categoryId);
      if (!Number.isInteger(id) || id < 1) return null;
      var mp = landing ? menuHolder(landing, id, opts) : null; // VEL-FIX: a menu that draws this category's anchor
      if (mp && key) {
        var hash = 'menu-' + id;
        return { kind: 'site', href: pageHref(key, mp.page, hash), page: mp.page, hash: hash, current: onPage(ctx, mp.page) };
      }
      if (ctx && ctx.menuUnavailable) return null;
      return { kind: 'order', query: { type: 'category', id: String(id) } };
    }
    if (t && t.type === 'section' && typeof t.section === 'string' && landing && key
      && !arr(ctx.sections).some(function (s) { return s && s.id === t.section; })) {
      var w = whereIs(landing, t.section, opts);
      if (!w || onPage(ctx, w.page)) return baseTarget(target, ctx);
      var out = { kind: 'site', href: pageHref(key, w.page, L.anchorId(t.section)), page: w.page, hash: L.anchorId(t.section), current: false };
      if (L.isDialog(w.section)) out.dialog = true;
      return out;
    }
    return baseTarget(target, ctx);
  }

  // ---- redirects ----------------------------------------------------------------------------------------------------
  function redirectsOf(landing) {
    var seen = {};
    return arr(obj(landing) && landing.redirects).filter(function (r) {
      if (!obj(r) || typeof r.from !== 'string' || !FROM.test(r.from) || /\/\/|\/$|^\/page(\/|$)/.test(r.from) || seen[r.from] || !obj(r.to)) return false;
      seen[r.from] = true;
      return true;
    }).slice(0, 10);
  }
  function redirectFrom(landing, path) {
    var p = typeof path === 'string' ? path.replace(/\/+$/, '').toLowerCase() : ''; // VEL-FIX: any letter case
    return redirectsOf(landing).filter(function (r) { return r.from === p; })[0] || null;
  }
  function redirectTarget(landing, from, ctx, query, master) {
    var r = redirectFrom(landing, from);
    var t = r ? targetOf(r.to, ctx) : null;
    if (!t) return null;
    if (t.kind === 'url') return { href: withQuery(t.href, query), external: true };
    if (t.kind === 'order' || t.kind === 'categories') return { href: withQuery(L.menuHref(master || (ctx && ctx.key), t.query || undefined), query) };
    if (t.kind === 'section') return { href: withQuery(OO.router.href('landing', null, { hotel: ctx.key }) + t.href, query) };
    return { href: withQuery(t.href, query) };
  }
  function withQuery(href, query) {
    var R = OO.router;
    var h = String(href == null ? '' : href);
    var at = h.indexOf('#');
    var base = at < 0 ? h : h.slice(0, at), fragment = at < 0 ? '' : h.slice(at);
    var qAt = base.indexOf('?');
    var own = qAt < 0 ? {} : R.parseQuery(base.slice(qAt));
    var rest = {};
    var clean = R.withoutPii ? R.withoutPii(obj(query) || {}) : {};
    Object.keys(clean).forEach(function (k) { if (!Object.prototype.hasOwnProperty.call(own, k)) rest[k] = clean[k]; });
    var qs = R.buildQuery(rest);
    if (!qs) return h;
    return base + (qAt < 0 ? qs : '&' + qs.slice(1)) + fragment;
  }

  // ---- chrome keys --------------------------------------------------------------------------------------------------
  function statusBarOf(landing) {
    var b = obj(obj(landing) && landing.statusBar);
    if (!b || b.enabled !== true) return null;
    var h = Number(b.countdownHours);
    var out = { countdown: b.countdown !== false, hours: Number.isInteger(h) && h >= 1 && h <= 48 ? h : 24 };
    Object.keys(BAR_DEFAULTS).forEach(function (k) { out[k] = text(b[k], 80) || BAR_DEFAULTS[k]; });
    return out;
  }
  function wordmarkOf(landing, brand) {
    var header = obj(obj(landing) && landing.header);
    var w = header && obj(header.wordmark);
    if (!w) return null;
    var b = obj(brand) || {};
    var title = text(w.title, 40) || text(b.name, 60);
    var tagline = text(w.tagline, 40) || text(b.tagline, 120);
    return title || tagline ? { title: title, tagline: tagline } : null;
  }
  function footerCtaOf(landing) {
    var f = obj(obj(landing) && landing.footer);
    var c = f && obj(f.cta);
    return c && text(c.label, 24) && obj(c.target) ? { label: text(c.label, 24), target: c.target } : null;
  }
  function orderingChromeOf(landing) { return obj(landing) && landing.orderingChrome === 'site' ? 'site' : 'app'; }

  // ---- the status bar -----------------------------------------------------------------------------------------------
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function clock12(minutes) {
    var m = ((Math.round(minutes) % 1440) + 1440) % 1440;
    var h = Math.floor(m / 60), mi = m % 60;
    return (h % 12 === 0 ? 12 : h % 12) + ':' + pad(mi) + (h < 12 ? ' AM' : ' PM');
  }
  function toMinutes(hm) {
    var m = /^(\d{1,2}):(\d{2})/.exec(str(hm));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  }
  function countdownText(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return (h ? h + 'h ' : '') + m + 'm ' + sec + 's';
  }
  // The copy with its placeholders: [{ text }, { countdown: true, text }, …].
  function fill(copy, values) {
    var parts = [];
    var re = /\{(countdown|day|time|reason)\}/g, at = 0, m;
    while ((m = re.exec(copy))) {
      if (m.index > at) parts.push({ text: copy.slice(at, m.index) });
      var v = values[m[1]];
      if (m[1] === 'countdown') parts.push({ countdown: true, text: v || '' });
      else if (v) parts.push({ text: v });
      at = re.lastIndex;
    }
    if (at < copy.length) parts.push({ text: copy.slice(at) });
    return parts;
  }
  function joined(parts) { return parts.map(function (p) { return p.text; }).join('').replace(/\s+/g, ' ').trim(); }
  function statusOf(store, now, bar) {
    var S = OO.schedule, F = OO.format;
    if (!store || !bar || !S || typeof S.openStatus !== 'function') return null;
    var n = now == null ? Date.now() : (now instanceof Date ? now.getTime() : Number(now));
    var st;
    try { st = S.openStatus(store, n, 'OPENING'); } catch (e) { return null; }
    var out = function (state, copy, values, target) {
      var parts = fill(copy, values);
      return { state: state, parts: parts, text: joined(parts), targetMs: target };
    };
    if (st.isOpen) return out('open', bar.openText, {}, null);
    var reason = text(st.reason, 120);
    if (st.label === 'Not taking online orders') return out('offline', bar.offlineText, { reason: reason ? ' · ' + reason : '' }, null);
    var mins = toMinutes(st.opensAt);
    if (mins === null || !st.opensOn) return out('closed', bar.offlineText, { reason: reason ? ' · ' + reason : '' }, null); // VEL-FIX: the configured copy
    var tz = store.timezone;
    var target = F.instantOf(st.opensOn, mins, tz);
    var left = target - n;
    if (bar.countdown && left > 0 && left <= bar.hours * 3600000) {
      return out('countdown', bar.closedText, { countdown: countdownText(left) }, target);
    }
    var today = F.zoned(n, tz).ymd;
    var diff = F.dayDiff(today, st.opensOn);
    var day = diff <= 0 ? 'today' : diff === 1 ? 'tomorrow' : DAYS_LONG[F.weekdayOf(st.opensOn)];
    return out('opens', bar.opensText, { day: day, time: clock12(mins) }, target);
  }

  // ---- opening hours ------------------------------------------------------------------------------------------------
  function shiftText(shifts) {
    return shifts.map(function (s) { return clock12(s.start) + ' – ' + clock12(s.end); }).join(', ');
  }
  function weekRows(store, now, includeClosed) {
    var S = OO.schedule;
    if (!store || !S || typeof S.week !== 'function') return [];
    var days;
    try { days = S.week(store, 'OPENING', now == null ? Date.now() : now); } catch (e) { return []; }
    if (!includeClosed && !days.some(function (d) { return d.shifts && d.shifts.length; })) return [];
    var byDow = {};
    days.forEach(function (d) { byDow[d.dow] = d; });
    return WEEK.map(function (dow) {
      var d = byDow[dow] || { shifts: [], today: false };
      var closed = !d.shifts.length;
      return { dow: dow, day: DAYS_SHORT[dow], dayLong: DAYS_LONG[dow], today: !!d.today, closed: closed, time: closed ? 'Closed' : shiftText(d.shifts) };
    });
  }
  function directoryRows(hours, now, tz) {
    var by = {};
    arr(hours).forEach(function (h) {
      if (!obj(h) || !(h.day >= 0 && h.day <= 6)) return;
      var a = toMinutes(h.open), b = toMinutes(h.close);
      if (a === null || b === null) return;
      (by[h.day] = by[h.day] || []).push({ start: a, end: b <= a ? b + 1440 : b });
    });
    if (!Object.keys(by).length) return [];
    var todayDow = null;
    try { todayDow = OO.format.zoned(now == null ? Date.now() : now, tz || undefined).dow; } catch (e) { todayDow = null; }
    return WEEK.map(function (dow) {
      var shifts = by[dow] || [];
      return { dow: dow, day: DAYS_SHORT[dow], dayLong: DAYS_LONG[dow], today: dow === todayDow, closed: !shifts.length, time: shifts.length ? shiftText(shifts) : 'Closed' };
    });
  }

  // ---- sections -----------------------------------------------------------------------------------------------------
  function galleryOf(section) {
    var s = obj(section) || {};
    var filters = [], keys = {};
    arr(s.filters).forEach(function (f) {
      if (!obj(f) || typeof f.key !== 'string' || !GROUP.test(f.key) || keys[f.key] || !text(f.label, 24) || filters.length >= 6) return;
      keys[f.key] = true;
      filters.push({ key: f.key, label: text(f.label, 24), count: 0 });
    });
    var items = arr(s.images).filter(function (v) { return obj(v) && L.httpsUrl(v.image); }).slice(0, 32).map(function (v) {
      var group = typeof v.group === 'string' && GROUP.test(v.group) && keys[v.group] ? v.group : null;
      return { image: L.httpsUrl(v.image), caption: text(v.caption, 80), group: group };
    });
    filters.forEach(function (f) { f.count = items.filter(function (v) { return v.group === f.key; }).length; });
    return { items: items, filters: filters.filter(function (f) { return f.count > 0; }) };
  }
  function priceOf(p) {
    var prices = arr(p && p.sizes).map(function (s) { return s && s.price; }).filter(function (n) { return typeof n === 'number' && isFinite(n); });
    if (!prices.length) return { amount: p && typeof p.price === 'number' ? p.price : null, from: false };
    var min = Math.min.apply(null, prices), max = Math.max.apply(null, prices);
    return { amount: min, from: max > min };
  }
  function ruleText(g) {
    if (!obj(g)) return '';
    var max = Number(g.max) || 0, min = Number(g.min) || 0;
    if (g.required || min > 0) {
      var lo = Math.max(1, min);
      if (g.unlimited || !max) return 'choose ' + lo + '+ · required';
      return (lo === max ? 'choose ' + lo : 'choose ' + lo + '–' + max) + ' · required';
    }
    if (!g.unlimited && max > 0) return 'up to ' + max;
    return 'optional';
  }
  function enquiryTypesOf(section) {
    var seen = {};
    return arr(obj(section) && section.enquiryTypes).map(function (x) { return text(x, 40); }).filter(function (x) {
      if (!x || seen[x]) return false;
      seen[x] = true;
      return true;
    }).slice(0, 8);
  }
  function localYmd(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
  // VEL-FIX: today and today + 365 days in the document's time zone (the server's rule: UTC without one).
  function dateRange(now, tz) {
    var F = OO.format;
    var n = now == null ? Date.now() : (now instanceof Date ? now.getTime() : Number(now));
    var today = F.zoned(n, typeof tz === 'string' && tz ? tz : 'UTC').ymd;
    return { min: today, max: F.addDays(today, 365) };
  }
  function dateOk(v, now, tz) {
    var m = YMD.exec(str(v));
    if (!m) return false;
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (localYmd(d) !== m[0]) return false; // 2026-02-30
    var r = dateRange(now, tz);
    return m[0] >= r.min && m[0] <= r.max;
  }
  function enquiryValidate(fields, section, now, tz) {
    var v = L.validate(fields);
    var f = fields || {};
    var errors = Object.assign({}, v.errors);
    var types = enquiryTypesOf(section);
    if (str(f.enquiryType) && types.indexOf(str(f.enquiryType)) < 0) errors.enquiryType = 'Choose an enquiry type.';
    if (obj(section) && section.askDate === true && str(f.preferredDate) && !dateOk(f.preferredDate, now, tz)) errors.preferredDate = 'Choose a date within the next year.';
    return { ok: Object.keys(errors).length === 0, errors: errors };
  }
  function enquiryBody(sectionId, fields, section) {
    var f = fields || {};
    var out = L.body(sectionId, f);
    var type = str(f.enquiryType);
    if (type && enquiryTypesOf(section).indexOf(type) >= 0) out.enquiry_type = type;
    var date = str(f.preferredDate);
    if (obj(section) && section.askDate === true && date && YMD.test(date)) out.preferred_date = date;
    return out;
  }

  Object.assign(L, {
    design: design, targetOf: targetOf,
    // WL-VEL
    pagesOf: pagesOf, pageOf: pageOf, pageLabel: pageLabel, pageHref: pageHref, whereIs: whereIs, menuPage: menuPage,
    redirectsOf: redirectsOf, redirectFrom: redirectFrom,
    menuHolder: menuHolder, pageTitle: pageTitle, withQuery: withQuery, redirectTarget: redirectTarget, dateRange: dateRange, // VEL-FIX
    statusBarOf: statusBarOf, wordmarkOf: wordmarkOf, footerCtaOf: footerCtaOf, orderingChromeOf: orderingChromeOf,
    statusOf: statusOf, countdownText: countdownText, clock12: clock12, weekRows: weekRows, directoryRows: directoryRows,
    galleryOf: galleryOf, priceOf: priceOf, ruleText: ruleText, enquiryTypesOf: enquiryTypesOf, enquiryValidate: enquiryValidate,
    enquiryBody: enquiryBody, BAR_DEFAULTS: BAR_DEFAULTS, PAGE_SLUG: SLUG,
  });
})();


// ===================== src/data/fixtures.jsx =====================
// src/data/fixtures.jsx: OO.data.fixtures, the Batch-1 demo data as RAW API responses, in the shapes the real API
// sends (the captures in tests/contract/{prod,staging}/**), quirks included: status flag types, JSON-string fields,
// `shop_hoilidays`, UTC running_hours, string money on tickets, product_count "2", double-encoded discount_meta,
// raw-string prod_cat_availability, banner "null", '##' colours, base64 header_logo, numeric gateway ids (STRIPE 4,
// WINDCAVE 3), the theme builder's unset sections ([]), V4 item coupons. tests/contract.test.mjs (P1-17) fails when a
// captured body has a key its fixture twin lacks. The fixture adapter (src/data/store-api.jsx) serves these for the
// fixture stores (OO.api picks it per store), and OO.model normalises them and the real bodies alike.
//
// Time-dependent fields are built per call from ctx.now (the api's injectable clock): current_time, UTC
// running_hours, open/pickup flags, booking dates, order/notification/wallet dates. So "Open until 10pm", "Today"
// and "Last Friday" stay true whenever the demo runs, and tests can pin the clock.
//
// Stores (online_key → data):
//   bennys            Bennys Burger Chapel Street (AU, GST incl. 10%, pickup + dine-in, monochrome theme, menu
//                     = the design's Bennys menu, Wednesday Deal combo, loyalty, orders, booking, notifications)
//   bennys-brunswick  related branch (11am–11pm), shares the Bennys menu
//   bennys-richmond   related branch (lunch only, pickup only), shares the Bennys menu
//   nonnas            Nonna's Pizzeria Carlton (AU, theme B, Pizza Combo + Family Pizza Combo, delivery + 1.5% card
//                     surcharge, menu layout 'sections')
//   harbour           Harbour Coffee Viaduct (NZ, GST 15% = 3/23, theme C, '##' colour, flat $0.50 card
//                     surcharge, a pickup closure today 13:00–14:00, Cheese Scone only 7–11am)
// Menu layout (MENU-LAYOUT): the three Bennys stores and harbour have the admin's "Auto Menu Popup" on (def.menuPopupAuto
// 1 → menu_popup_auto 1: the category tiles), nonnas has it off (0: the full menu, 'sections'). Each store's
// proposed.menu_layout says the same, and wins (OO.model.normalizeStore's precedence).
//
// PROPOSED data (no real endpoint or field yet; see docs/online-order/BATCH2-GAPS.md):
//   data[0].proposed { menu_layout, category_style, home_modules, checkout, theme, loyalty }
//   banner[].proposed { tag, subtitle, cta }, wallet transaction .proposed { description }
//   search rows' hotelName / address_full / shop_image (what the root find-store page reads today)
//   notifications(key), loyaltyCatalogue(key)
//
// API (all builders return FRESH raw objects; ctx = { now: Date }):
//   STORE_KEYS, has(key), def(key)
//   restaurant(key, ctx) → POST getRestaurant              menu(key, ctx) → POST get_menu
//   coupons(key, ctx) → GET coupon_get_v4                  wallet(key, ctx) → POST get_customer_wallet_points
//   loyaltyProducts(key, ctx) → POST get_user_loyalty_products_v4  loyaltyCatalogue(key) → PROPOSED
//   walletTransactions(key, ctx) → GET cust_wallet_points_transactions
//   orders(key, ctx) → POST get_customer_orders (bare paginator)   orderDetail(key, ticketId, ctx) → GET get_order_data
//   tickets(key, ctx) → Ticket[]   ticket(def, spec) → one Ticket row   paginator(rows)   detailOf(def, ticket)
//   popular(key) → GET popular-dishes/{key}       searchStores(query) → GET get_online_store_list_using_name_search
//   notifications(key, ctx) → PROPOSED
//   bookingConfig(key, ctx) → POST get_table_booking_config (null when the store has no reservation settings)
//   bookingSlots(key, { date: 'YYYY-MM-DD', guests }, ctx) → POST check_table_booking_slots
//   table(key, tableId) → GET tables/gettableDetails (raw; { Status:false, error } for unknown/inactive)
//   customerData(key, uid) → GET customer/getdata body | null
//   menuIndex(key) → { products: { id: { label, sale_price, sizes: { id: amount } } }, modifiers: { id: price },
//                      combos: { id: { combo_name, combo_price, description } } } (the fixture "server" DB)
//   DEMO_CUSTOMER (CustomerData), DEMO_ORIGIN ({ lat, lng }, a point the branch distances match), OTP_CODE
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};

  function F() { return OO.format; }
  function clone(v) { return v === undefined ? undefined : JSON.parse(JSON.stringify(v)); }
  function img(name) { return 'online-order/fixtures/img/' + name; }
  function iu(id, w, h) { return 'https://images.unsplash.com/photo-' + id + '?w=' + (w || 240) + '&h=' + (h || 240) + '&fit=crop&q=70'; }

  var TS = '2026-01-15 03:00:00';            // DB::table row timestamps
  var TSZ = '2026-01-15T03:00:00.000000Z';   // Eloquent-serialised timestamps
  var ALL_DOMAINS = ['ONLINE_PICKUP', 'ONLINE_DELIVERY', 'ONLINE_APP_PICKUP', 'ONLINE_APP_DELIVERY', 'KIOSK', 'TABLE_SIDE'];
  var HARBOUR_LOGO = 'iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAIAAABvFaqvAAAAJklEQVR42mMQUbSlCmIYNWjUICzo01JHrGjUoFGDRrPIqEE0MggAHnhaEMLZ0jUAAAAASUVORK5CYII=';
  var NONNAS_LOGO = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAIAAABvFaqvAAAAJklEQVR42mOQi7aiCmIYNWjUICzoSbgeVjRq0KhBo1lk1CAaGQQAMTrAUECMNSoAAAAASUVORK5CYII=';

  // ---- time helpers (store-local wall clock ⇄ UTC, relative to ctx.now) ---------------------------
  function nowOf(ctx) { return ctx && ctx.now instanceof Date ? ctx.now : new Date(ctx && ctx.now != null ? ctx.now : Date.now()); }
  function local(ctx, tz) { return F().zoned(nowOf(ctx), tz); }
  function utcHM(hm, tz, ctx) { return F().hm(F().toMinutes(hm) - F().tzOffsetMinutes(nowOf(ctx), tz)) + ':00'; }
  function localAt(ctx, tz, daysAgo, hm) { return F().instantOf(F().addDays(local(ctx, tz).ymd, -daysAgo), hm, tz); }
  function minutesAgo(ctx, m) { return nowOf(ctx).getTime() - m * 60000; }
  function daysSince(ctx, tz, dow) { var d = (local(ctx, tz).dow - dow + 7) % 7; return d === 0 ? 7 : d; }
  function sqlUtc(ms) { return new Date(ms).toISOString().slice(0, 19).replace('T', ' '); }
  function isoZ(ms) { return new Date(ms).toISOString().replace(/\.(\d{3})Z$/, '.$1000Z'); }
  function wall(ms, tz) { var c = F().zoned(new Date(ms), tz); return c.ymd + ' ' + c.hm + ':' + F().pad2(c.s); }

  // ---- [BE] row builders -----------------------------------------------------------------------------
  function addOn(id, name, price, o) {
    o = o || {};
    return {
      addOnId: id, addOnName: name, mod_image: o.image || null, price: price, preselect: o.preselect ? 1 : 0,
      is_alert: 0, alert_text: null, position: null, availability: o.availability === undefined ? null : o.availability,
    };
  }
  function grp(id, label, o, addOns) {
    return {
      groupId: id, label: label, is_mandatory: o.req ? 1 : 0, min_modifier: o.min != null ? o.min : (o.req ? 1 : 0),
      max_modifier: o.max, max_no_price_count: 0, allow_same: o.same ? 1 : 0, position: o.pos || 1,
      availability: o.availability === undefined ? null : o.availability,
      addOns: addOns.map(function (a, i) { var x = clone(a); x.position = i + 1; return x; }),
    };
  }
  // A groupstoproduct entry: the same group limited to specific modifiers (modifiermaps).
  function gtp(group, ids) {
    var g = clone(group);
    g.modifiermaps = ids.slice();
    g.addOns = g.addOns.filter(function (a) { return ids.indexOf(a.addOnId) >= 0; });
    return g;
  }
  function size(storeId, id, name, amount, pos, excluded) {
    excluded = excluded || [];
    return {
      id: id, size_name: name, size_amount: amount, user_id: storeId, position: pos,
      modifiesxnames: excluded.map(function (a) { return a.addOnName; }), modifiesxids: excluded.map(function (a) { return a.addOnId; }),
    };
  }
  function item(o) {
    return {
      itemId: o.id, sub_cat_id: o.sub == null ? null : o.sub, label: o.name, imageUrl: o.image === undefined ? null : o.image,
      description: o.desc || '', sale_price: o.price, banner: o.banner === undefined ? null : o.banner, banner_price: o.was || 0,
      tags: (o.tags || []).map(function (t, i) {
        return { id: o.id * 10 + i, product_id: String(o.id), tag: t, is_delete: 0, created_at: TSZ, updated_at: TSZ };
      }),
      is_inactive: o.soldOut ? 1 : 0, inactive_reason: o.soldOut || null, special_instruction_hide: o.hideNotes ? 1 : 0,
      is_alert: 0, alert_text: null, is_loyalty_product: o.loyalty ? 1 : 0, loyalty_consumable: 1, loyalty_credit_disable: 0,
      availability: o.availability === undefined ? null : o.availability, weight: 0, av: null,
      sub_cat_name: null, sub_cat_position: null,
      sizes: o.sizes || [], group: o.group || [], groups: o.groups || [], groupstoproduct: o.gtp || [],
    };
  }
  function category(o) {
    var subs = (o.subs || []).map(function (s, i) { return { sub_name: s.name, sub_description: null, is_hidden: 0, sub_id: s.id, position: i + 1 }; });
    o.items.forEach(function (it) {
      var s = subs.filter(function (x) { return x.sub_id === it.sub_cat_id; })[0];
      if (s) { it.sub_cat_name = s.sub_name; it.sub_cat_position = s.position; }
    });
    return {
      categoryId: o.id, label: o.name, description: o.desc || '', imageURL: o.image || null,
      inactive: 0, inactive_reason: null, show_only: 0, availability: o.availability === undefined ? null : o.availability,
      av: null, menus: [1], catAvailability: [], sub_categories: subs, item: o.items,
    };
  }
  function comboProduct(menuItem, o) {
    var sizes = clone(menuItem.sizes || []);
    var sz = o.sizeId != null ? sizes.filter(function (s) { return s.id === o.sizeId; })[0] : null;
    var mods = !o.noMods;
    return {
      mapping_id: o.mapping, product_id: menuItem.itemId, size_id: o.sizeId == null ? null : o.sizeId,
      prod_cat_name: o.catName, prod_cat_availability: '[]', prod_cat_id: o.catId,
      itemId: menuItem.itemId, availability: menuItem.availability || [], label: menuItem.label,
      is_inactive: menuItem.is_inactive, inactive_reason: menuItem.inactive_reason, imageUrl: menuItem.imageUrl,
      sale_price: menuItem.sale_price, is_alert: 0, alert_text: null, extra_price: o.extra || 0, is_default: o.isDefault ? 1 : 0,
      group: mods ? clone(menuItem.group) : [], groups: mods ? clone(menuItem.groups) : [], groupstoproduct: mods ? clone(menuItem.groupstoproduct) : [],
      sizes: sizes,
      size_mode: o.sizeId != null ? 'fixed' : (sizes.length ? 'choice' : 'none'),
      size_required: o.sizeId == null && sizes.length > 0,
      configured_size: sz ? { id: sz.id, size_name: sz.size_name, size_amount: sz.size_amount } : null,
      combo_size_contract_version: 1,
    };
  }
  function combo(o) {
    return {
      combo_id: o.id, combo_name: o.name, banner: null, startdate: '2026-01-01', enddate: '2026-12-31', combo_price: o.price,
      image: o.image || null, availability: o.availability === undefined ? null : o.availability, description: o.desc,
      combo_cat_id: o.catId, category_name: o.catName, category_active: 1, category_availability: null, category_position: o.catPos,
      menus: [1], prod_mappings: [],
      cat_mappings: o.groups.map(function (g, i) {
        return { id: g.id, categoryId: g.id, min: g.min, max: g.max, group_name: g.name, position: i + 1, allow_same: g.same ? 1 : 0, products: g.products };
      }),
    };
  }
  function menuList(storeId, categoryIds, comboIds, comboCatIds) {
    return [{
      id: 1, user_id: storeId, name: 'All day', description: null, availability: [], position: 1, enabled: 1,
      categories: categoryIds.map(function (id, i) { return { category_id: id, position: i + 1 }; }),
      combos: comboIds.map(function (id, i) { return { combo_id: id, position: i + 1 }; }),
      combo_categories: comboCatIds.map(function (id, i) { return { combo_category_id: id, position: i + 1 }; }),
    }];
  }

  // ---- Bennys menu (design CATS / MENU; D02 Fat Boy; 02 Brisket Poutine) ---------------------------------
  function bennysMenu() {
    var S = 4017;
    var bread = grp(201, 'Choice of bread', { max: 1, pos: 3 }, [addOn(9201, 'Brioche Bun', 0), addOn(9202, 'Gluten Free Bun', 2), addOn(9203, 'Lettuce Wrap', 0)]);
    var fatAddOns = grp(202, 'Add ons', { max: 9, pos: 4 }, [addOn(9211, 'Add Beef Patty', 5), addOn(9212, 'Add Chicken Patty', 5), addOn(9213, 'Extra Cheese', 2), addOn(9214, 'Add Bacon', 3)]);
    var mealSide = grp(301, 'Meal side', { req: true, max: 1, pos: 1 }, [addOn(9301, 'Crispy Chips', 0), addOn(9302, 'Onion Rings', 2.5), addOn(9303, 'Sweet Potato Chips', 3)]);
    var mealDrink = grp(302, 'Meal drink', { req: true, max: 1, pos: 2 }, [addOn(9311, 'Coca-Cola', 0), addOn(9312, 'Sprite', 0), addOn(9313, 'Mountain Dew', 2)]);
    var base = grp(211, 'Choice of base', { req: true, max: 1, pos: 1 }, [addOn(9221, 'Crispy Chips', 0), addOn(9222, 'Tater Tots', 2), addOn(9223, 'Sweet Potato Chips', 3)]);
    var poutineAddOns = grp(212, 'Add ons', { max: 3, pos: 2, availability: '[]' }, [addOn(9231, 'Extra Cheese', 2), addOn(9232, 'Extra Gravy', 1.5), addOn(9233, 'Jalapeños', 1)]);
    var mealOptions = mealSide.addOns.concat(mealDrink.addOns);
    var burger = function (o) { return item(Object.assign({ group: [clone(bread)] }, o)); };

    var spicyBeef = burger({
      id: 5001, sub: 1021, name: 'Spicy Beef Burger', price: 16.5, image: img('spicy-beef.jpg'), tags: ['Spicy'],
      desc: 'Prime beef patty, cheese, hot sauce, onion, jalapeño & lettuce',
      sizes: [size(S, 7701, 'Just the Burger', 16.5, 1, mealOptions), size(S, 7702, 'Make it a Meal', 22.5, 2)],
      groups: [clone(mealSide), clone(mealDrink)],
    });
    var fatBoy = burger({
      id: 5003, sub: 1022, name: 'Fat Boy Burger', price: 25.9, image: img('fat-boy.jpg'),
      desc: 'Prime beef patty, eight-hour slow-cooked brisket, double cheese, BBQ sauce & onion',
      sizes: [size(S, 7711, 'Just the Burger', 25.9, 1), size(S, 7712, 'Make it a Meal', 31.9, 2)],
      gtp: [gtp(fatAddOns, [9211, 9212, 9213, 9214])], groups: [clone(fatAddOns)],
    });
    var vego = burger({ id: 5004, sub: 1023, name: 'Vego Cheese Burger', price: 18.9, image: img('vego-cheese.jpg'), tags: ['Vegetarian'], desc: 'Veef patty, cheese, tomato sauce, mustard, onion, pickle, tomato & lettuce' });
    var royal = burger({ id: 5002, sub: 1021, name: 'Royal Beef Burger', price: 18.5, image: img('royal-beef.jpg'), desc: 'Prime beef patty, cheese, BBQ sauce, caramelised onion & slaw' });
    var spicyVegan = burger({ id: 5005, sub: 1023, name: 'Spicy Vegan Burger', price: 18.9, image: img('spicy-vegan.jpg'), tags: ['Spicy', 'Vegan'], desc: 'Veef patty, cheese, hot sauce, onion, jalapeño & lettuce' });
    var buffalo = burger({ id: 5006, sub: 1024, name: 'Buffalo Chicken Burger', price: 15.5, image: img('buffalo-chicken.jpg'), desc: "Chicken patty with buffalo sauce, Benny's sauce, cheese & lettuce" });
    var mountainDew = item({ id: 5201, name: 'Mountain Dew', price: 4, desc: 'Can', image: img('mountain-dew.jpg') });
    var crispyChips = item({ id: 5302, name: 'Crispy Chips', price: 6, image: img('crispy-chips.jpg') });
    var cokeCan = item({ id: 5601, name: 'Coca-Cola (can)', price: 4, image: img('coca-cola-can.jpg') });   // combo-only (hidden from the online menu)
    var spriteCan = item({ id: 5602, name: 'Sprite (can)', price: 4, image: img('sprite-can.jpg') });    // combo-only

    var categories = [
      category({
        id: 101, name: 'ALCOHOL', image: img('dry-martini.jpg'),
        subs: [{ id: 1011, name: 'HARD DRINKS' }, { id: 1012, name: 'WINE' }, { id: 1013, name: 'COCKTAILS' }, { id: 1014, name: 'SHOTS' }, { id: 1015, name: 'TAP BEER' }],
        items: [
          item({ id: 5011, sub: 1011, name: 'Smirnoff', price: 11, image: img('smirnoff.jpg') }), item({ id: 5012, sub: 1011, name: 'Grey Goose', price: 14, image: img('grey-goose.jpg') }),
          item({ id: 5013, sub: 1011, name: 'Absolute Vodka', price: 12, image: img('absolut-vodka.jpg') }), item({ id: 5014, sub: 1012, name: 'House Red', price: 10, desc: 'Glass', image: img('house-red.jpg') }),
          item({ id: 5015, sub: 1012, name: 'House White', price: 10, desc: 'Glass', image: img('house-white.jpg') }), item({ id: 5016, sub: 1013, name: 'Dry Martini', price: 22, image: img('dry-martini.jpg') }),
          item({ id: 5017, sub: 1014, name: 'Tequila Shot', price: 9, image: img('tequila-shot.jpg') }), item({ id: 5018, sub: 1015, name: 'Pale Ale', price: 12, desc: 'Pint', image: img('pale-ale.jpg') }),
        ],
      }),
      category({
        id: 102, name: 'BURGERS', image: img('fat-boy.jpg'),
        subs: [{ id: 1021, name: 'BEEF BURGER' }, { id: 1022, name: "BENNY'S GOURMET BURGERS" }, { id: 1023, name: 'VEGGIE AND VEGAN BURGER' }, { id: 1024, name: 'CHICKEN BURGER' }],
        items: [spicyBeef, fatBoy, vego, royal, spicyVegan, buffalo],
      }),
      category({
        id: 103, name: 'POUTINES/BOXES', image: img('brisket-poutine.jpg'),
        items: [item({ id: 5101, name: 'Brisket Poutine', price: 15.5, image: img('brisket-poutine.jpg'), desc: 'Chips topped with cheese, eight-hour slow-cooked pulled beef brisket & gravy', groups: [base, poutineAddOns] })],
      }),
      category({ id: 104, name: 'TACOS/WRAPS', image: img('beef-taco.jpg'), items: [item({ id: 5111, name: 'Beef Taco', price: 9.5, banner: 'null', image: img('beef-taco.jpg') }), item({ id: 5112, name: 'Chicken Wrap', price: 13.5, image: img('chicken-wrap.jpg') })] }),
      category({ id: 105, name: 'NACHOS/QUESADILLAS', image: img('loaded-nachos.jpg'), items: [item({ id: 5121, name: 'Loaded Nachos', price: 16.5, image: img('loaded-nachos.jpg') })] }),
      category({ id: 106, name: 'SOFT DRINKS/SHAKES', image: img('chocolate-shake.jpg'), items: [mountainDew, item({ id: 5202, name: 'Chocolate Shake', price: 8.5, soldOut: 'Sold out today', image: img('chocolate-shake.jpg') })] }),
      category({ id: 107, name: 'SIDES', image: img('onion-rings.jpg'), items: [item({ id: 5301, name: 'Onion Rings', price: 7, image: img('onion-rings.jpg') }), crispyChips] }),
      category({ id: 108, name: 'KIDS MEALS', image: img('kids-cheeseburger-meal.jpg'), availability: '[]', items: [item({ id: 5401, name: 'Kids Cheeseburger Meal', price: 11, image: img('kids-cheeseburger-meal.jpg') })] }),
      category({ id: 109, name: 'DIPPING SAUCE', image: img('bennys-sauce.jpg'), items: [item({ id: 5501, name: "Benny's Sauce", price: 1.5, image: img('bennys-sauce.jpg') }), item({ id: 5502, name: 'Chipotle Mayo', price: 1.5, image: img('chipotle-mayo.jpg') })] }),
    ];
    var combos = [combo({
      id: 901, name: 'Wednesday Deal', price: 12.9, catId: 41, catName: 'WEEKLY DEALS', catPos: 1,
      image: img('wednesday-deal.jpg'),
      desc: 'Any burger from beef or chicken category, chips and a can of soft drink.',
      availability: [{ DM: ALL_DOMAINS, D: ['3'] }],
      groups: [
        { id: 61, name: 'Burger', min: 1, max: 1, products: [
          comboProduct(spicyBeef, { mapping: 6101, catName: 'BURGERS', catId: 102, sizeId: 7701, noMods: true }),
          comboProduct(royal, { mapping: 6102, catName: 'BURGERS', catId: 102, noMods: true }),
          comboProduct(buffalo, { mapping: 6103, catName: 'BURGERS', catId: 102, noMods: true }),
        ] },
        { id: 62, name: 'Chips', min: 1, max: 1, products: [comboProduct(crispyChips, { mapping: 6111, catName: 'SIDES', catId: 107 })] },
        { id: 63, name: 'Drink', min: 1, max: 1, products: [
          comboProduct(cokeCan, { mapping: 6121, catName: 'HIDDEN', catId: 199 }),
          comboProduct(spriteCan, { mapping: 6122, catName: 'HIDDEN', catId: 199 }),
          comboProduct(mountainDew, { mapping: 6123, catName: 'SOFT DRINKS/SHAKES', catId: 106 }),
        ] },
      ],
    })];
    return {
      status: 'success', menu_type: 'Online Menu', menu_version: '1759190400',
      data: categories, menus: menuList(S, categories.map(function (c) { return c.categoryId; }), [901], [41]), combos: combos,
    };
  }

  // ---- Nonna's menu (design COMBO / FAMILY / MODSETS / IMGS) -----------------------------------------
  function nonnasMenu() {
    var S = 4021;
    var crust = grp(401, 'Crust', { req: true, max: 1, pos: 1 }, [addOn(9401, 'Classic', 0), addOn(9402, 'Thin & crispy', 0), addOn(9403, 'Gluten free', 3)]);
    var toppings = grp(402, 'Extra toppings', { max: 3, pos: 2 }, [addOn(9411, 'Extra cheese', 2), addOn(9412, 'Olives', 1.5), addOn(9413, 'Jalapeños', 1), addOn(9414, 'Mushrooms', 1.5)]);
    var cheesy = grp(411, 'Make it cheesy', { max: 1, pos: 1 }, [addOn(9421, 'Add mozzarella', 1.5)]);
    var seasoning = grp(412, 'Seasoning', { req: true, max: 1, pos: 1 }, [addOn(9431, 'Plain', 0), addOn(9432, 'Chicken salt', 0), addOn(9433, 'Cajun', 0)]);
    var sauce = grp(413, 'Sauce', { req: true, max: 1, pos: 1 }, [addOn(9441, 'BBQ', 0), addOn(9442, 'Buffalo', 0), addOn(9443, 'Honey soy', 0)]);
    // 'Ice' sits at position 1 and 'Size' at 2: Next sorts 'size' groups first regardless.
    var drinkSize = grp(421, 'Size', { req: true, max: 1, pos: 2 }, [addOn(9451, 'Can 375ml', 0), addOn(9452, 'Bottle 600ml', 1)]);
    var ice = grp(422, 'Ice', { max: 1, pos: 1 }, [addOn(9461, 'No ice', 0)]);
    var pizza = function (id, name, desc, price, image, regId, lgId) {
      return item({ id: id, name: name, desc: desc, price: price, image: image, group: [clone(crust), clone(toppings)],
        sizes: [size(S, regId, 'Regular', price, 1), size(S, lgId, 'Large', price + 6, 2)] });
    };
    var drink = function (id, name, image) { return item({ id: id, name: name, price: 4, image: image, groups: [clone(drinkSize), clone(ice)] }); };
    var margherita = pizza(6001, 'Margherita', 'Tomato, mozzarella, basil', 19, iu('1574071318508-1cdbab80d002'), 7801, 7802);
    var pepperoni = pizza(6002, 'Pepperoni', 'Tomato, mozzarella, pepperoni', 22, iu('1628840042765-356cda07504e'), 7803, 7804);
    var bbq = pizza(6003, 'BBQ Chicken', 'BBQ base, chicken, red onion, mozzarella', 24, iu('1565299624946-b28f40a0ae38'), 7805, 7806);
    var veggie = pizza(6004, 'Veggie Supreme', 'Capsicum, mushroom, olives, onion', 21, iu('1511689660979-10d2b1aada49'), 7807, 7808);
    var garlic = item({ id: 6101, name: 'Garlic Bread', price: 7.5, desc: 'Four pieces', image: iu('1619535860434-ba1d8fa12536'), groups: [cheesy] });
    var chips = item({ id: 6102, name: 'Crispy Chips', price: 6, desc: 'Regular', image: iu('1573080496219-bb080dd4f877'), groups: [seasoning] });
    var wings = item({ id: 6103, name: 'Chicken Wings (6)', price: 12.9, desc: 'Tossed in your choice of sauce', image: iu('1608039755401-742074f0548d'), groups: [sauce] });
    var salad = item({ id: 6104, name: 'Side Salad', price: 8, desc: 'Leaves, tomato, cucumber', image: iu('1512621776951-a57141f2eefd') });
    var coke = drink(6201, 'Coca-Cola', iu('1622483767028-3f66f32aef97'));
    var cokeNs = drink(6202, 'Coke No Sugar', iu('1629203851122-3726ecdf080e'));
    var sprite = drink(6203, 'Sprite', iu('1625772299848-391b6a87d7b3'));
    var water = item({ id: 6204, name: 'Water', price: 3.5, desc: '600ml bottle', image: iu('1548839140-29a749e1cf4d') });

    var P = function (it, mapping, o) { return comboProduct(it, Object.assign({ mapping: mapping, catName: o.cat, catId: o.catId }, o)); };
    var pizzas = function (base, same) {
      return { id: base === 7100 ? 71 : 81, name: 'Pizza', min: same ? 2 : 1, max: same ? 2 : 1, same: same, products: [
        P(margherita, base + 1, { cat: 'PIZZAS', catId: 2101, sizeId: 7802 }), P(pepperoni, base + 2, { cat: 'PIZZAS', catId: 2101, sizeId: 7804 }),
        P(bbq, base + 3, { cat: 'PIZZAS', catId: 2101, sizeId: 7806, extra: 2 }), P(veggie, base + 4, { cat: 'PIZZAS', catId: 2101, sizeId: 7808 }),
      ] };
    };
    var drinks = function (base, same) {
      return { id: base === 7120 ? 73 : 82, name: 'Drinks', min: same ? 2 : 1, max: same ? 2 : 1, same: same, products: [
        P(coke, base + 1, { cat: 'DRINKS', catId: 2103 }), P(cokeNs, base + 2, { cat: 'DRINKS', catId: 2103 }),
        P(sprite, base + 3, { cat: 'DRINKS', catId: 2103 }), P(water, base + 4, { cat: 'DRINKS', catId: 2103 }),
      ] };
    };
    var categories = [
      category({ id: 2101, name: 'PIZZAS', items: [margherita, pepperoni, bbq, veggie] }),
      category({ id: 2102, name: 'SIDES', items: [garlic, chips, wings, salad] }),
      category({ id: 2103, name: 'DRINKS', items: [coke, cokeNs, sprite, water] }),
    ];
    var combos = [
      combo({
        id: 911, name: 'Pizza Combo', price: 29.9, catId: 51, catName: 'MEAL DEALS', catPos: 1,
        image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&q=70', desc: '<p>Any large pizza, a side and a drink.</p>',
        groups: [
          pizzas(7100, false),
          { id: 72, name: 'Sides', min: 1, max: 1, products: [
            P(garlic, 7111, { cat: 'SIDES', catId: 2102 }), P(chips, 7112, { cat: 'SIDES', catId: 2102 }),
            P(wings, 7113, { cat: 'SIDES', catId: 2102, extra: 3 }), P(salad, 7114, { cat: 'SIDES', catId: 2102 }),
          ] },
          drinks(7120, false),
        ],
      }),
      combo({
        id: 912, name: 'Family Pizza Combo', price: 54.9, catId: 51, catName: 'MEAL DEALS', catPos: 1,
        image: 'https://images.unsplash.com/photo-1571997478779-2adcbbe9ab2f?w=800&q=70', desc: '<p>Two large pizzas, two drinks and two garlic breads.</p>',
        groups: [
          pizzas(8100, true),
          drinks(8120, true),
          { id: 83, name: 'Sides', min: 2, max: 2, same: true, products: [P(garlic, 8111, { cat: 'SIDES', catId: 2102 })] },
        ],
      }),
    ];
    return {
      status: 'success', menu_type: 'Online Menu', menu_version: '1759190400',
      data: categories, menus: menuList(S, [2101, 2102, 2103], [911, 912], [51]), combos: combos,
    };
  }

  // ---- Harbour menu (small coffee/bakery) --------------------------------------------------------------
  function harbourMenu() {
    var S = 4033;
    var milk = grp(501, 'Milk', { req: true, max: 1, pos: 1 }, [addOn(9501, 'Full cream', 0), addOn(9502, 'Trim', 0), addOn(9503, 'Oat', 0.8), addOn(9504, 'Almond', 0.8)]);
    var milkPre = grp(501, 'Milk', { req: true, max: 1, pos: 1 }, [addOn(9501, 'Full cream', 0, { preselect: true }), addOn(9502, 'Trim', 0), addOn(9503, 'Oat', 0.8), addOn(9504, 'Almond', 0.8)]);
    var extras = grp(502, 'Extras', { max: 4, same: true, pos: 2 }, [addOn(9511, 'Extra shot', 0.8), addOn(9512, 'Vanilla syrup', 0.8), addOn(9513, 'Caramel syrup', 0.8)]);
    var toasted = grp(511, 'Toasted', { max: 1, pos: 1 }, [addOn(9521, 'Toasted with butter', 0.5)]);
    var cupSizes = function (a, b, price) { return [size(S, a, 'Regular', price, 1), size(S, b, 'Large', price + 1, 2)]; };
    var categories = [
      category({ id: 3101, name: 'COFFEE', items: [
        item({ id: 7001, name: 'Flat White', price: 5.5, image: iu('1509042239860-f550ce710b93', 400, 300), sizes: cupSizes(7901, 7902, 5.5), groups: [clone(milk), clone(extras)] }),
        item({ id: 7002, name: 'Latte', price: 5.5, sizes: cupSizes(7903, 7904, 5.5), groups: [clone(milk), clone(extras)] }),
        item({ id: 7003, name: 'Long Black', price: 4.8, sizes: cupSizes(7905, 7906, 4.8), groups: [clone(extras)] }),
        item({ id: 7004, name: 'Iced Latte', price: 6.5, image: iu('1461023058943-07fcbe16d735', 400, 300), groups: [milkPre, clone(extras)] }),
      ] }),
      category({ id: 3102, name: 'BAKERY', items: [
        item({ id: 7101, name: 'Almond Croissant', price: 6, image: iu('1555507036-ab1f4038808a', 400, 300), desc: 'Twice-baked, frangipane, flaked almonds' }),
        item({ id: 7102, name: 'Banana Bread', price: 5.5, desc: 'House-made, with whipped butter', groups: [toasted] }),
        item({ id: 7103, name: 'Cheese Scone', price: 5, desc: 'Served warm until 11am', availability: [{ H: [{ S: '07:00', E: '11:00' }] }] }),
      ] }),
    ];
    return {
      status: 'success', menu_type: 'Online Menu', menu_version: '1759190400',
      data: categories, menus: menuList(S, [3101, 3102], [], []), combos: [],
    };
  }

  var MENUS = { bennys: bennysMenu, nonnas: nonnasMenu, harbour: harbourMenu };

  // ---- store definitions ---------------------------------------------------------------------------------
  function monochromeTheme() {
    return {
      card: { card_border: true, cardHeight: 200, cardHeight2: 150, card_image_position: 0, card_border_color: '#cccccc', card_border_radius: 12, card_transpirancy: 1, card_background_color: '#ffffff', card_vertical: false },
      modal: { modal_border: false, modal_border_radius: 8, modal_background_color: null },
      button: { button_color: '#000000', border_radius: 8, button_border: false, button_border_color: '#000000', button_font_color: '#ffffff' },
      font_family: 'Poppins', font_family_b: 'Poppins', font_variation: 1,
      background_color: '#EFF4F4', background_font_color: '#000000', header_color: '#000000', header_font_color: '#ffffff',
      background_image: null, header_logo: null,
      category_image_on_kiosk: false, kioskCossAxisCount: 3, childAspectRatio: 0.8, crossAxisSpacing: 10, mainAxisSpacing: 10,
      twoLineproductNameInKiosk: false, boldProductTittleInKiosk: true, kioskCategoryFlex: 17,
      // The theme builder's sections, unset, as production serves them (an unset section is [], not an object):
      header: [], hero: { image_url: null, video_url: null }, control_panel: [], search: [], category: [], product_modal: [],
      cart: [], checkout: [], account_drawer: [], footer: { terms_conditions: null, terms_service: null, allergy_info: null },
      legal_modal: [], template: null, alert_message: null, tracking: { google_analytics_code: null, google_tag_manager_code: null },
      share_card_image_url: null, gallery: { interior: [], food: [], exterior: [] }, testimonials: [], version: 1, booking_theme: null,
    };
  }
  function themed(overrides) {
    var t = monochromeTheme();
    Object.keys(overrides).forEach(function (k) { t[k] = k === 'button' ? Object.assign(t.button, overrides.button) : overrides[k]; });
    return t;
  }

  var MODULES = ['slider', 'loyalty', 'actions', 'reorder', 'popular'];
  var TERMS = {
    terms_condition: '<h2>Terms and conditions</h2><p>Orders are prepared fresh once accepted by the store. Prices include GST.</p>',
    terms_of_use: '<h2>Terms of use</h2><p>Use this site to order from the store. Please don’t misuse it or interfere with how it works.</p>',
    terms_of_allergy: '<h2>Allergy information</h2><p>Our kitchen handles gluten, dairy, egg, soy, sesame and nuts. Please ask staff before ordering.</p>',
  };

  var BENNYS_BASE = {
    tz: 'Australia/Melbourne', currency: 'AUD', tax: 10, taxExclusive: 0, surcharge: { enable: 0, percent: 0, amount: 0 },
    phone: '03 9826 1042', website: 'https://example.com/bennys', menu: 'bennys', data: 'bennys',
    description: 'Burgers, poutine and cold drinks.', theme: monochromeTheme(), structured: { enabled: false, accent: '#fe5621', hue: 0 },
    gateway: { gateway: 'STRIPE', gateway_id: 4, label: 'Pay Online' }, holidays: [{ id: 11, date: '2026-12-25', label: 'Christmas Day' }],
    related: ['bennys', 'bennys-brunswick', 'bennys-richmond'],
    menuPopupAuto: 1, // MENU-LAYOUT: Auto Menu Popup on → the category tiles, as proposed.menu_layout says
    proposed: {
      menu_layout: 'categories', category_style: 'image',
      home_modules: MODULES.map(function (k) { return { key: k, enabled: true }; }),
      checkout: { price_summary: 'inline', login_style: 'card' },
      theme: { heading_font: null, heading_weight: null },
      loyalty: { program_name: 'Bennys Rewards', points_word: 'points' },
    },
  };

  var DEFS = {
    bennys: Object.assign({}, BENNYS_BASE, {
      id: 4017, key: 'bennys', name: 'Bennys Burger Chapel Street', shopImage: img('fat-boy-hero.jpg'),
      address: { streetName: '560 Chapel St, South Yarra', postalCode: '3141', city: 'Victoria', country: 'Australia' },
      addressFull: '560 Chapel St, South Yarra VIC 3141', lat: -37.83987, lng: 144.99083,
      modes: { pickup: 1, delivery: 0, dine_in: 1, table_reservation: 1, table_side: 1 },
      hours: { OPENING: [['11:00', '22:00']], PICKUP: [['11:00', '22:00']] },
      banners: [
        { id: 801, image: img('fat-boy-hero.jpg'), text: 'Wednesday Deal $12.90', link: '?type=combo&id=901', tag: 'THIS WEEK', subtitle: 'Any beef or chicken burger, chips & a can', cta: 'Order now' },
        { id: 802, image: img('royal-beef.jpg'), text: 'Double points on burgers', link: 'rewards', tag: 'REWARDS', subtitle: 'Every burger earns 2× until Sunday', cta: 'See rewards' },
        { id: 803, image: img('brisket-poutine.jpg'), text: 'Brisket Poutine', link: 'product&id=5101', tag: 'NEW', subtitle: 'Eight-hour brisket, cheese curds & gravy', cta: 'Try it' },
        { id: 804, image: img('spicy-vegan.jpg'), text: 'Vegan week', link: null, enabled: false },
      ],
      popups: [{ id: 811, image: img('spicy-beef.jpg'), text: 'Try the Spicy Beef Burger', button_name: 'Order now', button_link: 'product&id=5001', enabled: false }],
      cuisines: [{ id: 3, name: 'Burgers' }],
    }),
    'bennys-brunswick': Object.assign({}, BENNYS_BASE, {
      id: 4018, key: 'bennys-brunswick', name: 'Bennys Burger Brunswick St', shopImage: img('royal-beef.jpg'),
      address: { streetName: 'Brunswick St, Fitzroy', postalCode: '3065', city: 'Victoria', country: 'Australia' },
      addressFull: 'Brunswick St, Fitzroy VIC 3065', lat: -37.79885, lng: 144.98401,
      modes: { pickup: 1, delivery: 0, dine_in: 1, table_reservation: 0, table_side: 0 },
      hours: { OPENING: [['11:00', '23:00']], PICKUP: [['11:00', '23:00']] }, banners: [], popups: [], cuisines: [{ id: 3, name: 'Burgers' }],
    }),
    'bennys-richmond': Object.assign({}, BENNYS_BASE, {
      id: 4019, key: 'bennys-richmond', name: 'Bennys Burger Richmond', shopImage: img('buffalo-chicken.jpg'),
      address: { streetName: 'Swan St, Richmond', postalCode: '3121', city: 'Victoria', country: 'Australia' },
      addressFull: 'Swan St, Richmond VIC 3121', lat: -37.81923, lng: 145.01793,
      modes: { pickup: 1, delivery: 0, dine_in: 0, table_reservation: 0, table_side: 0 },
      hours: { OPENING: [['11:00', '15:00']], PICKUP: [['11:00', '15:00']] }, banners: [], popups: [], cuisines: [{ id: 3, name: 'Burgers' }],
    }),
    nonnas: {
      id: 4021, key: 'nonnas', name: "Nonna's Pizzeria", tz: 'Australia/Melbourne', currency: 'AUD', tax: 10, taxExclusive: 0,
      surcharge: { enable: 1, percent: 1.5, amount: 0 }, phone: '03 9347 2210', website: 'https://example.com/nonnas', menu: 'nonnas', data: 'nonnas',
      description: 'Wood-fired pizza on Lygon Street.', shopImage: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1200&q=70',
      address: { streetName: '210 Lygon St, Carlton', postalCode: '3053', city: 'Victoria', country: 'Australia' },
      addressFull: '210 Lygon St, Carlton VIC 3053', lat: -37.7993, lng: 144.9669,
      modes: { pickup: 1, delivery: 1, dine_in: 1, table_reservation: 1, table_side: 0 },
      hours: { OPENING: [['11:00', '23:00']], PICKUP: [['11:00', '23:00']], DELIVERY: [['11:30', '22:30']] },
      theme: themed({ button: { button_color: '#1E5B3A', button_border_color: '#1E5B3A' }, header_color: '#1E5B3A', background_color: '#F7F1E6', header_logo: NONNAS_LOGO }),
      structured: { enabled: true, accent: '#E4572E', hue: 0 },
      gateway: { gateway: 'STRIPE', gateway_id: 4, label: 'Pay Online' }, holidays: [{ id: 12, date: '2026-12-25', label: 'Christmas Day' }],
      related: [],
      deliveryzones: [
        { id: 51, user_id: 4021, name: 'Carlton & Fitzroy', minimum_amount: 20, delivery_fee: 5, type: 'RADIUS', order_outside: 0, created_at: TS, updated_at: TS, is_delete: 0, longitude: null, latitude: null, zone_area: 3 },
        { id: 52, user_id: 4021, name: 'Inner north', minimum_amount: 35, delivery_fee: 8, type: 'RADIUS', order_outside: 0, created_at: TS, updated_at: TS, is_delete: 0, longitude: null, latitude: null, zone_area: 6 },
      ],
      banners: [{ id: 821, image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400&h=300&fit=crop&q=70', text: '2 pizzas, 2 sides, 2 drinks', link: '?type=combo&id=912', tag: 'FAMILY NIGHT', subtitle: 'Family Pizza Combo $54.90', cta: 'Order now' }],
      popups: [], cuisines: [{ id: 5, name: 'Pizza' }],
      proposed: {
        menu_layout: 'sections', category_style: 'image',
        home_modules: [{ key: 'slider', enabled: true }, { key: 'actions', enabled: true }, { key: 'loyalty', enabled: true }, { key: 'popular', enabled: true }, { key: 'reorder', enabled: false }],
        checkout: { price_summary: 'collapsed', login_style: 'solid' },
        theme: { heading_font: 'DM Serif Display', heading_weight: 400, ink: '#1F2A22', hero_bg: '#FFFFFF', hero_ink: '#1F2A22', accent_bar: '#E4572E', on_accent: '#FFFFFF' },
        loyalty: { program_name: "Nonna's Club", points_word: 'stamps' },
      },
    },
    harbour: {
      id: 4033, key: 'harbour', name: 'Harbour Coffee', tz: 'Pacific/Auckland', currency: 'NZD', tax: 15, taxExclusive: 0,
      surcharge: { enable: 1, percent: 0, amount: 0.5 }, phone: '09 377 1233', website: 'https://example.com/harbour', menu: 'harbour', data: 'harbour',
      description: 'Espresso and pastries at the Viaduct.', shopImage: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&q=70',
      address: { streetName: 'Shed 22, 99 Customs St W, Viaduct', postalCode: '1010', city: 'Auckland', country: 'New Zealand' },
      addressFull: 'Shed 22, 99 Customs St W, Auckland 1010', lat: -36.8433, lng: 174.7589,
      modes: { pickup: 1, delivery: 0, dine_in: 0, table_reservation: 0, table_side: 0 },
      hours: { OPENING: [['07:00', '16:00']], PICKUP: [['07:00', '16:00']] },
      theme: themed({ button: { button_color: '#14213D', button_border_color: '#14213D' }, header_color: '#14213D', background_color: '##EEF1F6', font_family: "'Poppins', sans-serif", font_family_b: 'SpaceGrotesk', header_logo: HARBOUR_LOGO }),
      structured: { enabled: true, accent: '#F2A541', hue: 0 },
      gateway: { gateway: 'WINDCAVE', gateway_id: 3, label: 'Pay Online' },
      holidays: [{ id: 21, date: '2026-12-25', label: 'Christmas Day' }, { id: 22, date: '2026-12-26', label: 'Boxing Day' }],
      closureToday: { from: '13:00', to: '14:00', domain: 'PICKUP', reason: 'Barista training' },
      forceLoginForCoupon: true, // force_login_for_coupon: coupons need a logged-in customer
      menuPopupAuto: 1, // MENU-LAYOUT: Auto Menu Popup on → the category tiles, as proposed.menu_layout says
      related: [],
      banners: [{ id: 831, image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=300&fit=crop&q=70', text: 'Skip the queue', link: 'menu', tag: 'MORNING RUSH', subtitle: 'Order ahead, collect at the bar', cta: 'Order now' }],
      popups: [], cuisines: [{ id: 7, name: 'Cafe' }],
      proposed: {
        menu_layout: 'categories', category_style: 'text',
        home_modules: MODULES.map(function (k) { return { key: k, enabled: true }; }),
        checkout: { price_summary: 'inline', login_style: 'card' },
        theme: { heading_font: 'Space Grotesk', heading_weight: 700, ink: '#14213D', hero_bg: '#F2A541', hero_ink: '#14213D', on_accent: '#14213D' },
        loyalty: { program_name: 'Harbour Beans', points_word: 'beans' },
      },
    },
  };
  var STORE_KEYS = Object.keys(DEFS);

  function has(key) { return Object.prototype.hasOwnProperty.call(DEFS, String(key)); }
  function def(key) { return has(key) ? DEFS[key] : null; }

  // ---- getRestaurant ----------------------------------------------------------------------------------
  function inHours(list, minutes) {
    return (list || []).some(function (h) { var s = F().toMinutes(h[0]), e = F().toMinutes(h[1]); return minutes >= s && minutes < e; });
  }
  function workingInfo(d, ctx, hours) {
    var out = [];
    for (var day = 0; day < 7; day++) {
      out.push({
        day: day, is_closed: 0,
        hotelDayInfo: Object.keys(hours).map(function (type) {
          return { type: type, running_hours: hours[type].map(function (h, i) { return { order: i + 1, start: utcHM(h[0], d.tz, ctx), stop: utcHM(h[1], d.tz, ctx) }; }) };
        }),
      });
    }
    return out;
  }
  function branchRow(key) {
    var d = DEFS[key];
    return { id: d.id, name: d.name, address_full: d.addressFull, online_key: d.key, address: [clone(d.address)] };
  }

  function restaurant(key, ctx) {
    var d = def(key);
    if (!d) return null;
    var c = local(ctx, d.tz);
    var holiday = (d.holidays || []).some(function (h) { return h.date === c.ymd; });
    var open = !holiday && inHours(d.hours.OPENING, c.minutes);
    var pickupOpen = !holiday && inHours(d.hours.PICKUP || d.hours.OPENING, c.minutes);
    var deliveryOpen = !holiday && !!d.modes.delivery && inHours(d.hours.DELIVERY, c.minutes);
    var counter = { id: 380, type: 'Pay at Counter', active: 1 };
    var cash = { id: 381, type: 'Cash on Delivery', active: 1 };
    var closures = [];
    if (d.closureToday) {
      closures.push({
        schedule_start_time: F().instantOf(c.ymd, d.closureToday.from, d.tz), schedule_end_time: F().instantOf(c.ymd, d.closureToday.to, d.tz),
        formatted_start_time: c.ymd + ' ' + d.closureToday.from + ':00 ' + d.tz, formatted_end_time: c.ymd + ' ' + d.closureToday.to + ':00 ' + d.tz,
        domain_type: d.closureToday.domain, close_reason: d.closureToday.reason,
      });
    }
    var d0 = {
      id: d.id, shop_logo: null, hotelName: d.name, hotelId: d.id, enable_table_side_ordering: d.modes.table_side,
      currency: d.currency, timezone: d.tz, mobile: d.phone, online_key: d.key, main_terminal: 'TRM' + d.id,
      pickup: d.modes.pickup, delivery: d.modes.delivery, table_reservation: d.modes.table_reservation, order_ahead: 0,
      Opening_hours: null, order_for_later: 0, is_delete: 0, website: d.website, description: d.description, is_preorder: 1,
      pre_min_day: 7, online_order_active: 1, google_analytics_code: null, facebook_pixels_code: null, google_tag_manager_code: null,
      online_inactive_reason: null, pickup_buffer_time: 15, delivery_buffer_time: 30, tax: d.tax, online_payment_enable: 1,
      surcharge_enable: d.surcharge.enable, surcharge_percent: d.surcharge.percent, surcharge_amount: d.surcharge.amount,
      online_theme_color: '#091014', hide_tax_label: 0, alert_info: null, shop_image: d.shopImage, is_apple_login_disabled: 0,
      longitude: d.lng, latitude: d.lat, dine_in: d.modes.dine_in, tax_exclusive: d.taxExclusive, loyality_enable_product_level: 0,
      map_link: 'https://www.google.com/maps?q=' + encodeURIComponent(d.addressFull),
      hotelStatus: [{ active: 1, active_reason: null }],
      deliveryAvailable: deliveryOpen, openingAvailable: open, pickupAvailable: pickupOpen,
      address: [clone(d.address)],
      payment_gateway: [clone(d.gateway)], payment_gateway_kiosk: [], kiosk_slider: null,
      payment: [counter], payment_kiosk: [counter], payment_delivery: d.modes.delivery ? [cash] : [], payment_pickup: [counter],
      payment_table_side: d.modes.table_side ? [counter] : [],
      payment_methods: [
        { id: 380, method: 'payment_type', type: 'Pay at Counter', name: 'Pay at Counter', active: true, position: 1, is_default: false,
          payment: true, payment_kiosk: true, payment_delivery: false, payment_pickup: true, payment_table_side: !!d.modes.table_side },
        { id: 12, method: 'online', gateway: d.gateway.gateway, gateway_id: d.gateway.gateway_id, label: d.gateway.label, type: d.gateway.gateway,
          name: d.gateway.gateway, active: true, position: 2, is_default: true, payment: true, payment_kiosk: false,
          payment_delivery: !!d.modes.delivery, payment_pickup: true, payment_table_side: !!d.modes.table_side },
      ],
      cuisines: (d.cuisines || []).map(function (c) { return { id: c.id, name: c.name, image: null, created_at: TSZ, updated_at: TSZ, is_delete: 0 }; }),
      deliveryzones: clone(d.deliveryzones || []),
      workingInfo: workingInfo(d, ctx, d.hours),
      shop_hoilidays: clone(d.holidays || []),
      items_sorting: 1, powered_by_logo_enable: 1, show_size_price: 0, alert_text: null, is_alert: 0, alert_image: null, dark_theme_active: 0,
      terms_condition: TERMS.terms_condition, terms_of_use: TERMS.terms_of_use, terms_of_allergy: TERMS.terms_of_allergy,
      table_count_popup: 0, menu_popup_auto: d.menuPopupAuto ? 1 : 0, isCategoryBar: 1, is_checkout_alert_pickup: 0, order_prompt_dialog: 0,
      is_checkout_alert_delivery: 0, checkout_alert_pickup: null, checkout_alert_delivery: null, delivery_tip: 0, order_later: 0,
      order_later_hours: null, order_later_days: 1, force_login_for_ordering: 0, force_login_for_coupon: d.forceLoginForCoupon ? 1 : 0, member_login_prompt: 0,
      is_user_form_table_side: 0,
      kiosk_checkout_alert: { takeaway: 0, takeaway_alert: null, dine_in: 0, dine_in_alert: null },
      review_settings: { display_store_rating: true, display_product_rating: true },
      shopThemes: { id: d.id - 4000, user_id: d.id, online_theme: clone(d.theme), theme: null, created_at: TSZ, updated_at: TSZ },
      theme: { online_order: clone(d.structured), kiosk: {}, table_side: {} },
      membership_theme: { enabled: false, preset_id: 'membership_classic_v1', colors: {} },
      popup: (d.popups || []).map(function (p) {
        return { id: p.id, image: p.image, text: p.text, button_name: p.button_name, button_link: p.button_link, enabled: p.enabled !== false, availability: [], is_active: p.enabled !== false };
      }),
      banner: (d.banners || []).map(function (b) {
        var row = { id: b.id, image: b.image, text: b.text, link: b.link, enabled: b.enabled !== false, availability: [], is_active: b.enabled !== false };
        if (b.tag || b.subtitle || b.cta) row.proposed = { tag: b.tag || null, subtitle: b.subtitle || null, cta: b.cta || null }; // PROPOSED fields
        return row;
      }),
      upcomming_scheduled_disable_data: closures,
      proposed: clone(d.proposed), // PROPOSED settings (BATCH2-GAPS.md)
    };
    return {
      status: true,
      data: [d0],
      appinfo: null, // onlineOrderVersionCheck('data', Origin): opaque, unused by the SPA
      related_branches: (d.related || []).filter(has).map(branchRow),
      current_time: wall(nowOf(ctx).getTime(), d.tz),
    };
  }

  function menu(key) {
    var d = def(key);
    return d && MENUS[d.menu] ? MENUS[d.menu]() : null;
  }
  // The store menu's item rows by itemId (the first copy of each): the menu-shaped rows other bodies embed.
  function menuItems(key) {
    var raw = menu(key), out = {};
    (raw ? raw.data : []).forEach(function (c) { c.item.forEach(function (it) { if (!out[it.itemId]) out[it.itemId] = it; }); });
    return out;
  }

  // ---- coupons (coupon_get_v4) -------------------------------------------------------------------------
  // An item coupon is a V4 row: rules.APPLICABLE_ITEMS [{id, sizes: [] (any size)}] and applicable_products (its menu
  // rows, filled in by coupons() from the store's menu) next to the older items / parseditems / products.
  function coupon(o) {
    return {
      id: o.id, name: o.name, description: o.desc, code: o.code,
      rules: {
        type: o.type, APPLICABLE_FOR: o.for || 'ALL', MIN_ORDER_VALUE: o.min || 0, MAX_ORDER_VALUE: o.maxOrder || 0,
        APPLICABLE_ITEMS: (o.items || []).map(function (id) { return { id: id, sizes: [] }; }),
      },
      result: { type: o.type, DISCOUNT: o.discount, MAX_DISCOUNT_VALUE: o.cap, max_free_count: 0, RESULTS_ITEMS: [] },
      is_exposed: 1,
      items: o.items ? JSON.stringify(o.items) : null, // JSON STRING (§3.6)
      availability: o.availability || [],
      parseditems: o.items ? o.items.slice() : null,
      products: (o.products || []).map(function (p) {
        return { id: p[0], name: p[1], description: '', sale_price: p[2], category_id: p[3], subcategory_id: null, photo: p[4] || null, user_id: o.storeId, is_delete: 0 };
      }),
      applicable_products: [], result_products: [],
    };
  }
  var COUPONS = {
    bennys: function () {
      return [
        coupon({ storeId: 4017, id: 819, name: '10% OFF', desc: 'Spend $30 & get 10% off (up to $5)', code: 'OFF10', type: 'CART_DISCOUNT', min: 30, discount: 10, cap: 5 }),
        coupon({ storeId: 4017, id: 820, name: 'Gourmet 20', desc: '20% off the Fat Boy Burger', code: 'GOURMET20', type: 'ITEM_DISCOUNT', discount: 20, cap: 10, items: [5003], products: [[5003, 'Fat Boy Burger', 25.9, 102, img('fat-boy.jpg')]] }),
        coupon({ storeId: 4017, id: 821, name: 'Free delivery', desc: 'Free delivery on orders over $40', code: 'FREEDEL', type: 'FREE_DELIVERY', for: 'DELIVERY', min: 40, discount: 5, cap: 5 }),
      ];
    },
    nonnas: function () {
      return [
        coupon({ storeId: 4021, id: 831, name: '15% OFF FAMILY', desc: 'Spend $50 & get 15% off (up to $10)', code: 'FAMILY15', type: 'CART_DISCOUNT', min: 50, discount: 15, cap: 10 }),
        coupon({ storeId: 4021, id: 832, name: 'Half-price garlic bread', desc: '50% off Garlic Bread', code: 'GARLIC50', type: 'ITEM_DISCOUNT', discount: 50, cap: 5, items: [6101], products: [[6101, 'Garlic Bread', 7.5, 2102]] }),
        coupon({ storeId: 4021, id: 833, name: 'Free delivery over $35', desc: 'We cover delivery up to $6', code: 'FREEDEL35', type: 'FREE_DELIVERY', for: 'DELIVERY', min: 35, discount: 6, cap: 6 }),
      ];
    },
    harbour: function () {
      return [
        coupon({ storeId: 4033, id: 841, name: 'Morning 10', desc: '10% off before 11am (up to $5)', code: 'MORNING10', type: 'CART_DISCOUNT', min: 15, discount: 10, cap: 5, availability: [{ H: [{ S: '07:00', E: '11:00' }] }] }),
        coupon({ storeId: 4033, id: 842, name: '20% off croissants', desc: '20% off the Almond Croissant', code: 'CROISSANT20', type: 'ITEM_DISCOUNT', discount: 20, cap: 3, items: [7101], products: [[7101, 'Almond Croissant', 6, 3102]] }),
        coupon({ storeId: 4033, id: 843, name: 'Free delivery', desc: 'Free delivery on orders over $25', code: 'FREEDEL', type: 'FREE_DELIVERY', for: 'DELIVERY', min: 25, discount: 5, cap: 5 }),
      ];
    },
  };
  function coupons(key) {
    var d = def(key);
    if (!d) return null;
    var rows = COUPONS[d.data] ? COUPONS[d.data]() : [];
    var items = menuItems(key);
    rows.forEach(function (c) {
      c.applicable_products = (c.parseditems || []).filter(function (id) { return items[id]; }).map(function (id) { return clone(items[id]); });
    });
    return { status: true, user_id: d.id, user_name: d.name, data: rows };
  }

  // ---- customer / loyalty / wallet ------------------------------------------------------------------
  var DEMO_CUSTOMER = {
    id: 812, uid: 'fx_sam_taylor_812', name: 'Sam Taylor', phone_no: '+61412345678', email: 'sam.taylor@example.com',
    address: null, birthday: '1992-04-18', otp_code: '123456', otp_expires_at: '2026-09-30 08:40:00', created_at: TSZ, updated_at: TSZ,
  };
  var MEMBERS = {
    bennys: { customer_id: 'cust_66fa1c2b9e', customer_code: '4012345', points: 340, tierId: 2, tier: 'Gold', ratio: 25, credit: 1, max: 500, min: 100 },
    nonnas: { customer_id: 'cust_70bb2d1a44', customer_code: '4021877', points: 6, tierId: null, tier: null, ratio: 1, credit: 1, max: 10, min: 10 },
    harbour: { customer_id: 'cust_81cc5e7f02', customer_code: '4033190', points: 820, tierId: 5, tier: 'Regular', ratio: 100, credit: 10, max: 2000, min: 500 },
  };
  function reward(id, name, price, points, image, storeId) {
    var r = item({ id: id, name: name, price: price, image: image || null, loyalty: true });
    r.catAvailability = [];
    r.points_required = points;
    r.user_id = storeId;
    return r;
  }
  var CATALOGUE = {
    bennys: function () {
      return [
        reward(5901, 'Free Soft Drink', 4, 100, img('spicy-vegan.jpg'), 4017),
        reward(5902, 'Free Crispy Chips', 6, 150, img('brisket-poutine.jpg'), 4017),
        reward(5903, 'Free Burger', 20, 500, img('spicy-beef.jpg'), 4017),
      ];
    },
    nonnas: function () { return [reward(6901, 'Free Margherita', 19, 10, iu('1574071318508-1cdbab80d002'), 4021)]; },
    harbour: function () { return [reward(7901, 'Free coffee', 5.5, 1000, iu('1509042239860-f550ce710b93', 400, 300), 4033)]; },
  };
  function member(key) { var d = def(key); return d ? MEMBERS[d.data] || null : null; }

  function wallet(key, ctx) {
    var d = def(key), m = member(key);
    if (!d || !m) return null;
    var types = ['ONLINE_PICKUP', 'ONLINE_DELIVERY', 'ONLINE_APP_PICKUP', 'ONLINE_APP_DELIVERY'];
    return {
      status: true, name: DEMO_CUSTOMER.name, customer_id: m.customer_id, customer_code: m.customer_code, birthday: null,
      request_time: isoZ(nowOf(ctx).getTime()), email: DEMO_CUSTOMER.email, membership_type: m.tierId,
      membership_type_obj: m.tierId ? {
        id: m.tierId, name: m.tier, desc: m.tier + ' members', restaurant_id: d.id,
        discount_meta: JSON.stringify(JSON.stringify({ price_tag: null })), // ⚠ double-encoded JSON string (§3.12)
        is_delete: 0, created_at: TSZ, updated_at: TSZ,
      } : null,
      wallet_points: m.points,
      redumption_ratio: types.map(function (t) { return { ratio_points: m.ratio, order_type: t, maximum_consumable_points: m.max, min_consumable_points: m.min }; }),
      credit_ratio: types.map(function (t) { return { ratio_points: m.credit, order_type: t }; }),
      usage_limit_over: false, used_limit_count: 0,
    };
  }
  function loyaltyCatalogue(key) {
    var d = def(key);
    return d && CATALOGUE[d.data] ? { status: true, data: CATALOGUE[d.data]() } : { status: true, data: [] };
  }
  function loyaltyProducts(key) {
    var d = def(key), m = member(key);
    if (!d || !m) return null;
    var maxPrice = m.points / m.ratio;
    return {
      status: 'success', client_type: 'web',
      data: {
        // userId is the wallet-master STORE's id (each fixture store is its own), not the customer's
        current_points: m.points, resId: d.id, userId: d.id, price_type: 'sale_price', maxEligiblePrice: maxPrice,
        eligible_products: loyaltyCatalogue(key).data.filter(function (p) { return p.sale_price <= maxPrice; }),
      },
    };
  }
  function walletTransactions(key, ctx) {
    var d = def(key), m = member(key);
    if (!d || !m) return null;
    var tx = function (id, type, points, at, o) {
      return Object.assign({
        id: id, restaurant_id: d.id, customer_id: m.customer_id, ticket_id: null, transaction_type: type, ticket_value: 0,
        wallet_points: points, created_at: isoZ(at), updated_at: isoZ(at), wallet_transaction_id: 'WT' + d.id + id,
        online_consumption_validated: 1, online_order_id: null, created_type: 'ticket', expires_at: isoZ(at + 365 * 86400000),
      }, o || {});
    };
    if (d.data !== 'bennys') {
      return { status: true, data: [tx(9001, 'credit', m.credit * 12, minutesAgo(ctx, 26 * 60), { created_type: 'giveaway', proposed: { description: 'Welcome bonus' } })] };
    }
    var fri = daysSince(ctx, d.tz, 5);
    return {
      status: true,
      data: [
        tx(5003, 'credit', 27, minutesAgo(ctx, 28), { ticket_id: 'OOT40171759001042', ticket_value: 27, proposed: { description: 'Order #1042 · Chapel Street' } }),
        tx(5002, 'credit', 36, localAt(ctx, d.tz, fri, '09:00'), { created_type: 'giveaway', proposed: { description: 'Double points bonus' } }),
        tx(5001, 'debit', 150, localAt(ctx, d.tz, 18, '12:40'), { ticket_id: 'OOT40171759001031', ticket_value: 37.9, proposed: { description: 'Redeemed Free Chips' } }),
      ],
    };
  }

  function customerData(key, uid) {
    var m = member(key);
    if (!def(key) || uid !== DEMO_CUSTOMER.uid) return null;
    return {
      status: true, data: clone(DEMO_CUSTOMER), customerCode: m ? m.customer_code : null, customerId: m ? m.customer_id : null,
      membershipTypeName: m ? m.tier : null, membershipTypeId: m ? m.tierId : null, customerWalletPoints: m ? m.points : 0,
    };
  }

  // ---- orders (get_customer_orders / get_order_data) ------------------------------------------------
  function orderLine(t, n, o) {
    var orderId = t.ticket_id + '-' + n;
    return {
      order_id: orderId, ticket_id: t.ticket_id, user_id: t.user_id, product_id: o.productId, product_count: String(o.qty || 1),
      rate_of_discount: o.discount || 0, temp_rate: o.price, void_item: 0, void_reason: '', kitchen_note: o.note || '', note_price: 0,
      created_at: t.created_at, updated_at: t.created_at, size_id: o.sizeId || null, size: o.sizeName || null,
      combo_id: o.comboId || null, comboBatch: o.batch || '', coupon_id: null, productName: o.name, extra_price: o.extra || 0,
      walletUsedForProduct: o.walletPoints || null, price_tag_id: null, is_price_tag_applied: false, isWalletRedeemed: !!o.wallet,
      walletType: o.wallet ? 2 : null, walletAmount: o.wallet ? Number(o.price).toFixed(2) : null, walletDiscount: o.wallet ? o.price : null,
      discount: o.discount || 0, isCouponApplied: false, couponDetail: null,
      modifiersDetail: (o.mods || []).map(function (m, i) {
        return { ordermodifier_id: orderId + '-' + i, order_id: orderId, product_id: o.productId, modifier_id: m[0], modifier_count: (m[3] || 1) * (o.qty || 1), price: m[2], modifier: m[1], created_at: t.created_at, updated_at: t.created_at };
      }),
      isCombo: !!o.comboId,
    };
  }
  function ticket(d, o) {
    var t = {
      id: o.rowId, ticket_id: o.ticketId, table_id: null, guest: 0, user_id: d.id, customer_id: o.customerId || null,
      ticket_type: 'ONLINE_PICKUP', ticket_status: o.ticketStatus || 'PAID', surcharge: o.surcharge || 0, ...(o.surcharge_label ? { surcharge_label: o.surcharge_label } : {}), discount: 0, gratuity: 0,
      subtotal: o.subtotal, paid: o.paid, created_at: sqlUtc(o.at), updated_at: sqlUtc(o.at + 600000), create_at: o.at,
      pay_at: o.at + 60000, pay_by: null, update_at: 0, discount_reason: o.discountReason || null, void_reason: null,
      short_id: '#' + o.short, 'co_ref-note': '', delivery_time: o.deliveryTime > 0 ? o.deliveryTime : o.at + 15 * 60000, deliver_type: o.deliverType || 'ASAP',
      order_state: o.state === undefined ? null : o.state, payment_type: o.paymentType || 'ONLINE_ORDER_CARD', agreed_delivery_time: 0,
      coupon_id: o.couponId || null, pager_no: null, wallet_points_consumed: o.walletPoints || 0, rating: null, feedback: null,
      orderStatus: o.orderStatus || 'ACCEPTED', onlinePaymentStatus: o.paymentStatus || 'PAID',
      billTotal: Number(o.billTotal).toFixed(2), tax: Number(o.tax).toFixed(2), total: Number(o.total).toFixed(2),
      customer_address: '', customer_name: o.customerName || DEMO_CUSTOMER.name, customer_phone: o.customerPhone || DEMO_CUSTOMER.phone_no,
      order_type: 'PICKUP', delivery_type: o.deliverType || 'ASAP', note: o.note || '', driver_status: '',
      isCouponApplied: !!o.couponId, couponDetail: null, coupon_type: o.couponType || null, coupon_discount_amount: o.couponAmount || 0,
      item_ratings: [],
      ordersDetail: { nonComboOrders: [], comboOrders: [] },
    };
    t.ordersDetail.nonComboOrders = (o.items || []).map(function (it, i) { return orderLine(t, i + 1, it); });
    t.ordersDetail.comboOrders = (o.combos || []).map(function (c, ci) {
      return {
        comboDetails: { combo_name: c.name, description: c.description, comboPrice: c.price },
        orderItems: c.items.map(function (it, i) { return orderLine(t, 'c' + ci + '-' + (i + 1), Object.assign({ comboId: c.id, batch: c.batch }, it)); }),
      };
    });
    return t;
  }
  function bennysTickets(d, ctx) {
    var m = MEMBERS.bennys;
    var meal = [{ productId: 5001, name: 'Spicy Beef Burger', qty: 1, price: 22.5, sizeId: 7702, sizeName: 'Make it a Meal', mods: [[9302, 'Onion Rings', 2.5], [9313, 'Mountain Dew', 2]] }];
    return [
      ticket(d, { rowId: 99142, ticketId: 'OOT40171759001042', short: 1042, at: minutesAgo(ctx, 28), state: 'ready', customerId: m.customer_id,
        subtotal: 27, paid: 27, billTotal: 27, tax: 2.45, total: 27, items: meal }),
      ticket(d, { rowId: 99138, ticketId: 'OOT40171759001038', short: 1038, at: localAt(ctx, d.tz, daysSince(ctx, d.tz, 5), '19:15'), state: 'completed',
        customerId: m.customer_id, subtotal: 27, paid: 27, billTotal: 27, tax: 2.45, total: 27, items: meal }),
      ticket(d, { rowId: 99131, ticketId: 'OOT40171759001031', short: 1031, at: localAt(ctx, d.tz, 18, '12:40'), state: 'completed',
        customerId: m.customer_id, subtotal: 37.9, paid: 31.9, billTotal: 31.9, tax: 3.45, total: 37.9, walletPoints: 150,
        items: [
          { productId: 5101, name: 'Brisket Poutine', qty: 1, price: 15.5, mods: [[9222, 'Tater Tots', 2], [9232, 'Extra Gravy', 1.5]] },
          { productId: 5902, name: 'Free Crispy Chips', qty: 1, price: 6, wallet: true, walletPoints: 150, discount: 100 },
        ],
        combos: [{ id: 901, name: 'Wednesday Deal', description: 'Any burger from beef or chicken category, chips and a can of soft drink.', price: 12.9, batch: '4b1f0c8e-6a3d-4f7e-9c21-0d5e8a7b3c11',
          items: [{ productId: 5002, name: 'Royal Beef Burger', qty: 1, price: 18.5 }, { productId: 5302, name: 'Crispy Chips', qty: 1, price: 6 }, { productId: 5601, name: 'Coca-Cola (can)', qty: 1, price: 4 }] }],
      }),
    ];
  }
  function tickets(key, ctx) {
    var d = def(key);
    if (!d) return [];
    return d.data === 'bennys' ? bennysTickets(d, ctx) : [];
  }
  function paginator(rows) {
    var base = 'https://coreapi.sipocloudpos.com/public/api/get_customer_orders';
    return {
      current_page: 1, data: rows, first_page_url: base + '?page=1', from: rows.length ? 1 : null, last_page: 1, last_page_url: base + '?page=1',
      links: [{ url: null, label: '&laquo; Previous', active: false }, { url: base + '?page=1', label: '1', active: true }, { url: null, label: 'Next &raquo;', active: false }],
      next_page_url: null, path: base, per_page: 1000, prev_page_url: null, to: rows.length ? rows.length : null, total: rows.length,
    };
  }
  function orders(key, ctx) { return def(key) ? paginator(tickets(key, ctx)) : null; }
  function detailOf(d, t) {
    return Object.assign(t, { deliveryBy: null, source: 'SIPO', scheduled_time: null, hotel_address: [d.address.streetName, d.address.city, d.address.country, d.address.postalCode].join(', ') });
  }
  function orderDetail(key, ticketId, ctx) {
    var d = def(key);
    var t = tickets(key, ctx).filter(function (x) { return x.ticket_id === ticketId; })[0];
    return d && t ? { status: true, data: detailOf(d, t) } : null;
  }

  // ---- notifications / popular / search (PROPOSED) --------------------------------------------------
  function notifications(key, ctx) {
    var d = def(key);
    if (!d) return null;
    var n = function (id, type, title, body, at, link) { return { id: id, type: type, title: title, body: body, created_at: isoZ(at), read_at: null, link: link || null }; };
    if (d.data !== 'bennys') {
      return { status: true, data: [n(1, 'offer', 'Welcome to ' + d.name, 'Order ahead and skip the queue.', minutesAgo(ctx, 26 * 60), 'menu')] };
    }
    var sat = daysSince(ctx, d.tz, 6);
    var satYmd = F().addDays(local(ctx, d.tz).ymd, -sat);
    return {
      status: true,
      data: [
        n(1, 'order', 'Order ready for pickup', 'Your order #1042 is ready at Chapel Street.', minutesAgo(ctx, 18), 'orders'),
        n(2, 'points', '+27 points earned', 'Thanks for your order. You have 340 points.', minutesAgo(ctx, 28), 'rewards'),
        n(3, 'offer', 'Wednesday Deal is back', 'Any burger, chips & a can for $12.90 — today only.', localAt(ctx, d.tz, daysSince(ctx, d.tz, 3), '10:00'), '?type=combo&id=901'),
        n(4, 'booking', 'Table booking confirmed', 'Party of 4 · Sat ' + F().shortDate(satYmd) + ', 7:30pm at Chapel Street.', localAt(ctx, d.tz, sat, '11:20'), 'booking'),
        n(5, 'offer', 'Double points weekend', 'Earn 2× points on every burger until Sunday.', localAt(ctx, d.tz, daysSince(ctx, d.tz, 5), '09:00'), 'rewards'),
      ],
    };
  }
  // popular-dishes: the most ordered products and combos (ids, most ordered first).
  var POPULAR = {
    bennys: { products: [5003, 5101, 5006, 5002], combos: [901] },
    nonnas: { products: [6001, 6002, 6003], combos: [912, 911] },
    harbour: { products: [7001, 7004, 7101], combos: [] },
  };
  function popular(key) {
    var d = def(key);
    if (!d) return null;
    var p = POPULAR[d.data] || { products: [], combos: [] };
    var items = menuItems(key);
    var combos = {};
    ((menu(key) || {}).combos || []).forEach(function (c) { combos[c.combo_id] = c; });
    var row = function (id, name, image, price, i) { return { id: id, name: name, image: image, price: price, order_frequency: 40 - i * 7 }; };
    return {
      status: 'success',
      data: {
        products: p.products.filter(function (id) { return items[id]; }).map(function (id, i) { var it = items[id]; return row(id, it.label, it.imageUrl, it.sale_price, i); }),
        combos: p.combos.filter(function (id) { return combos[id]; }).map(function (id, i) { var c = combos[id]; return row(id, c.combo_name, c.image, c.combo_price, i); }),
      },
      meta: { window_days: 30, ranking: 'order_frequency' },
    };
  }
  // get_online_store_list_using_name_search rows (users.name, online_key, user_metadatas.address, lat / lng,
  // users.shop_logo), plus hotelName / address_full / shop_image: the PROPOSED keys the root find-store page reads.
  function searchStores(query) {
    var q = String(query || '').trim().toLowerCase();
    return {
      status: true,
      data: STORE_KEYS.map(function (k) { return DEFS[k]; }).filter(function (d) {
        return !q || d.name.toLowerCase().indexOf(q) >= 0 || d.addressFull.toLowerCase().indexOf(q) >= 0 || d.key.indexOf(q) >= 0;
      }).map(function (d) {
        return {
          name: d.name, online_key: d.key, address: d.addressFull, latitude: d.lat, longitude: d.lng, shop_logo: null,
          hotelName: d.name, address_full: d.addressFull, shop_image: d.shopImage,
        };
      }),
    };
  }

  // ---- table booking ------------------------------------------------------------------------------------
  var BOOKING = {
    bennys: { from: '17:00', to: '20:30', slot: 30, maxSeat: 40, booked: { '17:00': 6, '17:30': 12, '18:00': 20, '18:30': 40, '19:00': 40, '19:30': 34, '20:00': 16, '20:30': 4 } },
    nonnas: { from: '17:30', to: '21:00', slot: 30, maxSeat: 30, booked: { '19:00': 30, '19:30': 22 } },
  };
  function bookingConfig(key, ctx) {
    var d = def(key);
    var b = d && d.key === 'bennys' ? BOOKING.bennys : (d && d.key === 'nonnas' ? BOOKING.nonnas : null);
    if (!d || !b) return null;
    var today = local(ctx, d.tz).ymd;
    var dates = [];
    for (var i = 0; dates.length < 7 && i < 14; i++) {
      var ymd = F().addDays(today, i);
      if (!(d.holidays || []).some(function (h) { return h.date === ymd; })) dates.push(ymd);
    }
    return {
      status: true,
      shop: {
        store_id: d.id, shop_name: d.name, shop_image: d.shopImage, shop_logo: null, address: d.addressFull, mobile: d.phone,
        email: 'hello@example.com', website: d.website, about_us: d.description, country: d.address.country, timezone: d.tz,
        latitude: d.lat, longitude: d.lng, google_tag_manager_code: null, google_analytics_code: null, facebook_pixels_code: null,
        terms_condition: TERMS.terms_condition, privacy_policy: TERMS.terms_of_use, map_link: 'https://www.google.com/maps', online_key: d.key,
      },
      config: { day_inadvance: 7, slot_duration: b.slot, maximum_seat: b.maxSeat, maximum_people_per_booking: 0, minimum_seat: 1, is_active: 1, inactive_reason: null },
      available_dates: dates,
      holidays: [],
      theme: { id: d.id - 4000, user_id: d.id, online_theme: clone(d.theme), theme: null, created_at: TSZ, updated_at: TSZ },
      workingInfo: workingInfo(d, ctx, { TABLE_BOOKING: [[b.from, b.to]] }),
      review_summary: { average_rating: 4.6, total_reviews: 212, show: 1 },
    };
  }
  function bookingSlots(key, q, ctx) {
    var d = def(key);
    var b = d && d.key === 'bennys' ? BOOKING.bennys : (d && d.key === 'nonnas' ? BOOKING.nonnas : null);
    if (!d) return null;
    if (!b) return { status: true, slots: [] };
    var guests = Math.max(1, Number(q && q.guests) || 1);
    var times = [];
    for (var t = F().toMinutes(b.from); t <= F().toMinutes(b.to); t += b.slot) times.push(F().hm(t));
    return {
      status: true,
      slots: times.map(function (hm, i) {
        var booked = b.booked[hm] || 0;
        var start = F().instantOf(q.date, hm, d.tz);
        var next = times[i + 1] || null;
        return {
          stime: hm, stimeEpoch: start, etime: next, etimeEpoch: next ? F().instantOf(q.date, next, d.tz) : null,
          booked_slots: booked, allow_booking: booked + guests <= b.maxSeat, remaining_guests: Math.max(0, b.maxSeat - booked),
        };
      }),
    };
  }

  // ---- tables (tables/gettableDetails) ------------------------------------------------------------------
  var TABLES = {
    bennys: { 15: { name: 'T5', seats: 4, group: 2, status: 'active', excluded: [101] }, 16: { name: 'T6', seats: 2, group: 2, status: 'inactive', excluded: [] } },
  };
  function table(key, tableId) {
    var d = def(key);
    var t = d && TABLES[d.key] ? TABLES[d.key][String(tableId)] : null;
    if (!t) return { Status: false, error: 'Table Not Locked!' };
    if (t.status !== 'active') return { Status: false, error: 'Table is Locked!' };
    var id = Number(tableId);
    return {
      Status: true,
      data: [{
        id: id, table_id: id, user_id: d.id, terminal_id: 'TRM' + d.id, name: t.name, total_seat_count: t.seats, occupied_seat_count: 0,
        occupied_total_price: 0, alerttime: 0, tablesgroup_id: t.group, status: t.status, is_delete: 0, created_at: TS, updated_at: TS,
        position: id - 10, atomic_time: null, split_count: null, original_table_id: null, split: 0, excluded_category_ids: t.excluded.slice(),
      }],
    };
  }

  // ---- the fixture "server" price index (placeOrder validation, ticket rebuild) -------------------------
  function menuIndex(key) {
    var raw = menu(key);
    var idx = { products: {}, modifiers: {}, combos: {} };
    if (!raw) return idx;
    function addProduct(p) {
      var id = p.itemId != null ? p.itemId : p.product_id;
      if (!idx.products[id]) {
        var sizes = {};
        (p.sizes || []).forEach(function (s) { sizes[s.id] = { amount: s.size_amount, name: s.size_name }; });
        idx.products[id] = { label: p.label, sale_price: p.sale_price, sizes: sizes };
      }
      ['group', 'groups', 'groupstoproduct'].forEach(function (k) {
        (p[k] || []).forEach(function (g) { (g.addOns || []).forEach(function (a) { idx.modifiers[a.addOnId] = { price: a.price, name: a.addOnName }; }); });
      });
    }
    raw.data.forEach(function (c) { c.item.forEach(addProduct); });
    raw.combos.forEach(function (c) {
      idx.combos[c.combo_id] = { combo_name: c.combo_name, combo_price: c.combo_price, description: c.description };
      c.cat_mappings.forEach(function (g) { g.products.forEach(addProduct); });
    });
    return idx;
  }

  OO.data.fixtures = {
    STORE_KEYS: STORE_KEYS,
    has: has,
    def: def,
    restaurant: restaurant,
    menu: menu,
    coupons: coupons,
    wallet: wallet,
    loyaltyProducts: loyaltyProducts,
    loyaltyCatalogue: loyaltyCatalogue,
    walletTransactions: walletTransactions,
    customerData: customerData,
    orders: orders,
    orderDetail: orderDetail,
    tickets: tickets,
    ticket: ticket,
    paginator: paginator,
    detailOf: detailOf,
    notifications: notifications,
    popular: popular,
    searchStores: searchStores,
    bookingConfig: bookingConfig,
    bookingSlots: bookingSlots,
    table: table,
    menuIndex: menuIndex,
    DEMO_CUSTOMER: DEMO_CUSTOMER,
    DEMO_ORIGIN: { lat: -37.8505, lng: 144.9932 },
    OTP_CODE: '123456',
  };
  OO.fixtures = OO.data.fixtures;
})();


// ===================== src/data/store-api.jsx =====================
// src/data/store-api.jsx: OO.data.fixtureApi, the FIXTURE adapter. It serves OO.data.fixtures with ~120 ms simulated
// latency. Pages don't call it directly: they call OO.api (src/data/api-select.jsx), which sends the fixture stores
// here and the live ones to OO.data.realApi. The real adapter makes the OO.http call named in each doc comment and
// keeps the signatures and resolved shapes, so pages and OO.model stay untouched.
//
// Every function returns a Promise of the endpoint's RAW body, in the shapes the real API sends (the captures in
// tests/contract/**, which tests/contract.test.mjs holds the fixture bodies to); normalise with OO.model.
// A failure rejects with the same Error shape OO.http throws: err.status (HTTP code), err.message
// (data.message || data.error), err.data (the raw error body).
// Two exceptions to "raw body", the same as the real adapter's:
//   * paymentUpdate resolves { httpStatus, body } for every status: the callback page is driven by the code
//     (200/202/206/409, §3.9).
//   * getLoyalty resolves { wallet, products, catalogue }: the bodies of get_customer_wallet_points and
//     get_user_loyalty_products_v4, plus a PROPOSED catalogue (B19; the real adapter resolves null for it).
//
// Identity: getLoyalty / getWalletTransactions / getOrders / getCustomer take opts.uid. Without it they use
// OO.store's session uid. In FIXTURE mode with neither, they fall back to the demo member (Sam Taylor), so the
// app screens have data. Pages decide "logged in" from OO.store.session(), never from these calls.
// The demo session belongs to the fixture stores. This file decides it for the document's LANDING store
// (OO.router.current() when it runs, i.e. before any in-app navigation and before the first render):
//   * a fixture store, in app mode: the visitor starts signed in as the demo member, once per browser (flag
//     'oo-demo-session-v1'): never again after a sign-out, the drop below included;
//   * a store that isn't a fixture store (a live one, or one this host doesn't serve): a session this adapter made
//     (the demo member, or a sign-up's 'fx_…' uid) is signed out locally, with no request
//     (OO.data.dropFixtureSession, src/lib/store.jsx, which every later store switch runs too);
//   * no store (the root find-store / payment-callback): nothing changes.
//
// Demo hooks (fixture mode only):
//   OTP code '123456' (any other code → 400 {error}). An email starting with 'decline' makes paymentUpdate 409.
//   A placed order progresses PENDING → ACCEPTED (preparing) → ACCEPTED + ticket_state 'ready' after
//   ORDER_PROGRESS_MS.accepted / .ready ms (counted from payment for online orders).
//   Placed orders and registered customers persist in localStorage 'oo-fixture-state-v1', so a full-page
//   payment redirect survives.
// Test hooks: __setLatency(ms), __setNow(Date | ms | () => Date | null), __reset(). OO.api forwards them here.
// now() → Date: this fixture adapter's clock (the device clock unless __setNow injected one). FIXTURE-ONLY: OO.api.now()
// returns it for fixture stores and OO.data.now() reads that, so tests and the fixture "server" share one clock; a
// real adapter defines no now() (the device clock).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};

  function FX() { return OO.data.fixtures; }
  function F() { return OO.format; }
  function clone(v) { return v === undefined ? undefined : JSON.parse(JSON.stringify(v)); }

  var latency = 120;
  var clockFn = null;
  var ORDER_PROGRESS_MS = { accepted: 8000, ready: 45000 };
  var STATE_KEY = 'oo-fixture-state-v1';
  var DEMO_FLAG = 'oo-demo-session-v1';

  function now() {
    var v = typeof clockFn === 'function' ? clockFn() : clockFn;
    if (v == null) return new Date();
    return v instanceof Date ? new Date(v.getTime()) : new Date(v);
  }
  function ctx() { return { now: now() }; }

  // ---- fixture "server" state ---------------------------------------------------------------------
  // signups: { email: uid } of the new emails verify-otp has seen (the backend creates their Firebase user once).
  // demoEdits (P3-09): the demo member's profile fields saved through updateProfile ({name, phone_no, birthday,
  // address}, only those edited), laid over DEMO_CUSTOMER by getCustomer and verifyOtp; {} = the member as shipped.
  function storage() { try { return window.localStorage || null; } catch (e) { return null; } }
  function emptyState() { return { orders: {}, customers: {}, signups: {}, seq: 0, txn: 0, lastOrderAt: {}, bookings: [], demoEdits: {} }; }
  function loadState() {
    try {
      var s = storage();
      var raw = s ? JSON.parse(s.getItem(STATE_KEY) || 'null') : null;
      if (raw && typeof raw === 'object' && raw.orders && raw.customers) return Object.assign(emptyState(), raw);
    } catch (e) { /* corrupt → fresh */ }
    return emptyState();
  }
  var mem = loadState();
  function save() {
    try { var s = storage(); if (s) s.setItem(STATE_KEY, JSON.stringify(mem)); } catch (e) { /* ignore */ }
  }

  // ---- response plumbing ------------------------------------------------------------------------------
  function messageOf(data, status) {
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message) return data.message;
      if (typeof data.error === 'string' && data.error) return data.error;
      if (typeof data.status === 'string' && data.status && data.status !== 'true') return data.status;
    }
    return 'HTTP ' + status;
  }
  function fail(status, data, message) {
    var err = new Error(message || messageOf(data, status));
    err.status = status;
    err.data = data;
    throw err;
  }
  function respond(build) {
    return new Promise(function (resolve, reject) {
      function run() { try { resolve(clone(build())); } catch (e) { reject(e); } }
      if (latency > 0 && typeof setTimeout === 'function') setTimeout(run, latency);
      else Promise.resolve().then(run);
    });
  }
  function storeOr404(hotel, body, message) {
    if (!FX().has(hotel)) fail(404, body, message);
    return FX().def(hotel);
  }

  function sessionUid() {
    try { var s = OO.store && OO.store.session(); return s && s.customer && s.customer.uid ? s.customer.uid : null; } catch (e) { return null; }
  }
  function sessionToken() {
    try { var s = OO.store && OO.store.session(); return s && s.customToken ? String(s.customToken) : null; } catch (e) { return null; }
  }
  function uidOf(opts) { return (opts && opts.uid) || sessionUid() || FX().DEMO_CUSTOMER.uid; }
  function isDemo(uid) { return uid === FX().DEMO_CUSTOMER.uid; }

  // ---- the custom token (verify-otp's customToken) ----------------------------------------------------
  var B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  // A string's UTF-8 bytes as unpadded base64url. Pure (no btoa / Buffer): this file also runs in node:vm.
  function base64url(text) {
    var pct = encodeURIComponent(text), bytes = [], out = '';
    for (var i = 0; i < pct.length; i++) {
      if (pct.charAt(i) === '%') { bytes.push(parseInt(pct.substr(i + 1, 2), 16)); i += 2; } else bytes.push(pct.charCodeAt(i));
    }
    for (var j = 0; j < bytes.length; j += 3) {
      var n = (bytes[j] << 16) | ((bytes[j + 1] || 0) << 8) | (bytes[j + 2] || 0);
      out += B64URL.charAt((n >> 18) & 63) + B64URL.charAt((n >> 12) & 63);
      if (j + 1 < bytes.length) out += B64URL.charAt((n >> 6) & 63);
      if (j + 2 < bytes.length) out += B64URL.charAt(n & 63);
    }
    return out;
  }
  // A Firebase-custom-token-shaped JWT for `uid` (header.payload.signature, base64url; the claims iss, sub, aud,
  // iat, exp, uid of the staging capture), so OO.data.uidFromToken reads it as it reads a real one. The signature
  // is a placeholder: nothing verifies it.
  var TOKEN_ISSUER = 'firebase-adminsdk-fixture@sipo-demo.iam.gserviceaccount.com';
  function customToken(uid) {
    var iat = Math.floor(now().getTime() / 1000);
    return base64url(JSON.stringify({ alg: 'RS256', typ: 'JWT' })) + '.'
      + base64url(JSON.stringify({
        iss: TOKEN_ISSUER, sub: TOKEN_ISSUER, aud: 'https://identitytoolkit.googleapis.com/google.identity.identitytoolkit.v1.IdentityToolkit',
        iat: iat, exp: iat + 3600, uid: String(uid),
      })) + '.' + base64url('fixture-signature');
  }
  // The uid of a new email: made once, then the same on every verify-otp until it registers.
  function signupUidFor(email) {
    if (!mem.signups || typeof mem.signups !== 'object') mem.signups = {};
    if (!mem.signups[email]) mem.signups[email] = 'fx_' + now().getTime().toString(36) + (Object.keys(mem.signups).length + 1).toString(36);
    return mem.signups[email];
  }

  // ---- store, menu, coupons ------------------------------------------------------------------------------
  /** POST getRestaurant {id: hotel, timeZone} (§3.1, metadataController@getRestaurant).
   *  404 {"error":"not found restuarent"} → Error {status: 404, message: 'Store not found'}. */
  function getRestaurant(hotel) {
    return respond(function () {
      var raw = FX().restaurant(hotel, ctx());
      if (!raw) fail(404, { error: 'not found restuarent' }, 'Store not found');
      return raw;
    });
  }

  /** POST get_menu {id: hotel, timeZone[, table_id]} (§3.2, onlineOrderMenu@getMenuToOnlineOrder).
   *  opts.tableId → the table menu (the body table_id branch): excluded categories dropped, menu_type 'Table Menu',
   *  excluded_category_ids; a table that isn't this store's active one → 404 {status:false, error:'Table not available
   *  for this store.'} (P4-02: the real answer; Batch 1's twin served the online menu instead). QR-OFF: then a store
   *  whose table ordering is off (its modes.table_side: getRestaurant's enable_table_side_ordering) → the server's 200
   *  {status:'False', error:'Table ordering is not available.', reason:'table_ordering_off'} (BE-LIMITS), rejected as
   *  the real adapter rejects it: Error { status: 200, kind: 'tableOff', data: that body }.
   *  404 {status:false, error:'Restaurant not found.'}. */
  function getMenu(hotel, opts) {
    return respond(function () {
      var raw = FX().menu(hotel);
      if (!raw) fail(404, { status: false, error: 'Restaurant not found.' });
      if (opts && opts.tableId != null && opts.tableId !== '') {
        var t = FX().table(hotel, opts.tableId);
        if (t.Status !== true) fail(404, { status: false, error: 'Table not available for this store.' });
        var d = FX().def(hotel);
        if (d && d.modes && !d.modes.table_side) {
          var off = { status: 'False', error: 'Table ordering is not available.', reason: 'table_ordering_off' };
          var err = new Error(off.error);
          err.status = 200;
          err.kind = 'tableOff';
          err.data = off;
          throw err;
        }
        var ex = t.data[0].excluded_category_ids;
        raw.data = raw.data.filter(function (c) { return ex.indexOf(c.categoryId) < 0; });
        raw.menu_type = 'Table Menu';
        raw.excluded_category_ids = ex;
      }
      return raw;
    });
  }

  function keyOfStore(idOrHotel) {
    if (FX().has(idOrHotel)) return String(idOrHotel);
    var n = Number(idOrHotel);
    return FX().STORE_KEYS.filter(function (k) { return FX().def(k).id === n; })[0] || null;
  }

  /** GET coupon_get_v4?user_id={data[0].id} (E09, CouponController@get_v4) → {status:true, data:[row], user_id,
   *  user_name}. Takes the numeric store id (the real param) or the online_key. Unknown → 404 (the real API answers
   *  an unknown id with 200 data: []). */
  function getCoupons(storeIdOrHotel) {
    return respond(function () {
      var k = keyOfStore(storeIdOrHotel);
      if (!k) fail(404, { status: false, error: 'Store not found' });
      return FX().coupons(k);
    });
  }

  /** GET get_pricetag_order + get_pricetag_prices ?hotelId= (E05 / E06; P2-01) → { order, prices }, the two raw bodies
   *  (as the real adapter). The fixture stores have no pricetags: no rows and empty price lists, in the real shapes
   *  (tests/contract/prod/get_pricetag_*), so OO.pricetags.select finds no tag and every price stays the menu's. */
  function getPricetags(storeIdOrHotel) {
    return respond(function () {
      return {
        order: { status: true, data: [] },
        prices: { status: 'success', data: { sizepricetags: [], productpricetags: [], modifierpricetags: [], combopricetags: [] } },
      };
    });
  }

  /** POST validate_coupon_v4 {customer_email, code, store_id, time} (E10, CouponController@validateCoupon_v4).
   *  opts = { code, email, time (ISO: the selected pickup time), domain? }. 200 {status:true, data: the coupon_get_v4
   *  row} | 200 {status:false, message:'Invalid coupon' | 'Coupon not available for this time / type'} (HTTP 200 on a
   *  bad code; this twin resolves it, the real adapter rejects it with status 200, kind 'client': the pages handle
   *  both). 400 when the code or email is missing. The fixture checks the code and the coupon's availability at
   *  `time`; the real server also enforces usage caps. */
  function validateCoupon(hotel, opts) {
    opts = opts || {};
    return respond(function () {
      var d = storeOr404(hotel, { status: false, message: 'Invalid store' });
      if (!opts.code || !opts.email) fail(400, { status: false, message: 'Coupon code and email are required' });
      var hit = FX().coupons(hotel).data.filter(function (c) { return c.code.toLowerCase() === String(opts.code).trim().toLowerCase(); })[0];
      if (!hit) return { status: false, message: 'Invalid coupon' };
      var at = opts.time ? new Date(opts.time) : now();
      var c = OO.model.normalizeCoupons({ data: [hit] }, { now: isNaN(at.getTime()) ? now() : at, timeZone: d.tz, domain: opts.domain })[0];
      return c && c.available === false ? { status: false, message: 'Coupon not available for this time / type' } : { status: true, data: hit };
    });
  }

  /** POST invalidate_coupon {transaction_id} (§3.6, CouponController@removeCouponClaimTransactionUnusedByOnlineOrder;
   *  Next goes through /api/invalidate_coupon). Releases the coupon claimed by an order (its base64 coupon_txn) while
   *  that order is PROCESSING and UNPAID (an online payment not completed): 200 {status:true, message}. Otherwise
   *  400 {status:false, message}; an unknown / already released txn → 404. */
  function invalidateCoupon(txn) {
    return respond(function () {
      var id = Object.keys(mem.orders).filter(function (k) { return mem.orders[k].couponTxn && mem.orders[k].couponTxn === txn; })[0];
      if (!txn || !id) fail(404, { status: false, message: 'Coupon transaction not found.' });
      var o = mem.orders[id];
      if (!(o.online && !o.paidAt)) fail(400, { status: false, message: 'The order is not in a state to release its coupon.' });
      o.couponTxn = null;
      save();
      return { status: true, message: 'Unclaimed coupon transaction removed.' };
    });
  }

  /** getRestaurant(hotel).related_branches → POST getRestaurantBulk {ids: [online_key…], timeZone}
   *  (metadataController@getRestaurantBulk). Resolves the raw bulk map { online_key: getRestaurant payload }.
   *  A store with no relation resolves { [hotel]: its own payload }. */
  function getBranches(hotel) {
    return respond(function () {
      var c = ctx();
      var raw = FX().restaurant(hotel, c);
      if (!raw) fail(404, { error: 'not found restuarent' }, 'Store not found');
      var keys = raw.related_branches.length ? raw.related_branches.map(function (b) { return b.online_key; }) : [String(hotel)];
      var out = {};
      keys.forEach(function (k) { out[k] = FX().restaurant(k, c) || { status: false, error: 'not found restuarent' }; });
      return out;
    });
  }

  /** GET online_order/chain/{key} (B31, E47; OnlineOrderChainController@show; P3-24) → {status:true, data:{online_key,
   *  master:{id, online_key, name, image, logo, orderable, shared_loyalty}, stores:[{id, online_key, name, address,
   *  image, logo, latitude, longitude, is_master, online_order_active, online_inactive_reason, pickup, delivery, dine_in,
   *  timezone, shared_loyalty, open_now, open_label, closes_at, opens_at, opens_on, closed_reason}]}}. The fixture chain
   *  is the store's related_branches group, its first key the master (bennys for the three Bennys stores, whose loyalty
   *  is shared; nonnas and harbour are chains of one), in B31's order (the master, then by name). Open now comes from the
   *  fixture clock, as the store pages compute it (OO.model.normalizeStore); the address is the one Batch 1's Select
   *  store showed (the store's own related_branches row's address_full, else its getRestaurant address), so the demo
   *  reads as before; online_key echoes the requested store. An unknown key → 404 {status:false, error:
   *  'STORE_NOT_FOUND', message:'Store not found'}. */
  function chainStoreRow(key, c, masterKey, shared) {
    var body = FX().restaurant(key, c);
    var d0 = body.data[0];
    var s = OO.model.normalizeStore(body, { now: c.now });
    var open = s.open || {};
    var own = (body.related_branches || []).filter(function (b) { return String(b.online_key) === key; })[0];
    return {
      id: d0.id, online_key: d0.online_key, name: d0.hotelName, address: own && own.address_full ? own.address_full : s.address.full, image: d0.shop_image,
      logo: d0.shop_logo, latitude: d0.latitude, longitude: d0.longitude, is_master: key === masterKey,
      online_order_active: Number(d0.online_order_active) === 1, online_inactive_reason: d0.online_inactive_reason,
      pickup: !!d0.pickup, delivery: !!d0.delivery, dine_in: !!d0.dine_in, timezone: d0.timezone, shared_loyalty: shared,
      open_now: !!open.isOpen, open_label: open.label, closes_at: open.closesAt || null, opens_at: open.opensAt || null,
      opens_on: open.opensOn || null, closed_reason: open.reason || null,
    };
  }
  function getChain(hotel) {
    return respond(function () {
      var c = ctx();
      if (!FX().has(hotel)) fail(404, { status: false, error: 'STORE_NOT_FOUND', message: 'Store not found' });
      var d = FX().def(hotel);
      var members = (d.related && d.related.length ? d.related : [d.key]).filter(function (k) { return FX().has(k); });
      if (members.indexOf(d.key) < 0) members.unshift(d.key);
      var masterKey = members[0];
      var shared = members.length > 1;
      var nameOf = function (k) { return FX().def(k).name.toLowerCase(); };
      var rest = members.slice(1).sort(function (a, b) { return nameOf(a) < nameOf(b) ? -1 : (nameOf(a) > nameOf(b) ? 1 : 0); });
      var stores = [masterKey].concat(rest).map(function (k) { return chainStoreRow(k, c, masterKey, shared); });
      var m = FX().def(masterKey);
      return {
        status: true,
        data: {
          online_key: d.key,
          master: { id: m.id, online_key: m.key, name: m.name, image: stores[0].image, logo: stores[0].logo, orderable: true, shared_loyalty: shared },
          stores: stores,
        },
      };
    });
  }

  /** GET popular-dishes/{key} (E03, PopularDishesController@index) → {status:'success', data:{products:[{id, name,
   *  image, price, order_frequency}], combos:[…]}, meta:{window_days, ranking}} (most ordered first). Unknown → 404
   *  (the real adapter resolves an empty body for every failure instead). */
  function getPopular(hotel) {
    return respond(function () { storeOr404(hotel, { status: false, error: 'Restuarent not found' }); return FX().popular(hotel); });
  }

  /** GET get_online_store_list_using_name_search?name= (E34) → {status:true, data:[{name, online_key, address,
   *  latitude, longitude, shop_logo}]}, each row also carrying the keys the root find-store page reads today
   *  (hotelName, address_full, shop_image; PROPOSED). Matches the name, address or key; '' lists every store (the
   *  real adapter sends nothing under 2 characters, B11). */
  function searchStores(query) {
    return respond(function () { return FX().searchStores(query); });
  }

  // A number or a numeric string; anything else is NaN (the real adapter's rule).
  function coord(v) {
    if (typeof v === 'number') return v;
    return typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  }

  /** GET get_online_store_listing?latitude&longitude[&radius] (E33, onlineStoreListingController@getOnlineStoreListing;
   *  P3-23) → {status:true, data:[the search rows above, each plus `distance` (km from the point)]}: the fixture stores
   *  within `radius` km (default 50, the API's) of {lat, lng}, nearest first. A missing or non-finite lat / lng is the
   *  API's 400 {status:false, error:'latitude and longitude are required'}. */
  function nearbyStores(opts) {
    opts = opts || {};
    return respond(function () {
      var lat = coord(opts.lat), lng = coord(opts.lng), radius = coord(opts.radius);
      if (!isFinite(lat) || !isFinite(lng)) fail(400, { status: false, error: 'latitude and longitude are required' });
      var within = isFinite(radius) && radius > 0 ? radius : 50;
      var rows = FX().searchStores('').data.map(function (row) {
        row.distance = OO.model.haversineKm({ lat: lat, lng: lng }, { lat: row.latitude, lng: row.longitude });
        return row;
      }).filter(function (row) { return row.distance <= within; });
      rows.sort(function (a, b) { return a.distance - b.distance; });
      return { status: true, data: rows };
    });
  }

  // ---- loyalty / wallet / customer -----------------------------------------------------------------------
  function zeroWallet(hotel, cust) {
    var w = FX().wallet(hotel, ctx());
    if (!w) return null;
    return Object.assign(w, { name: cust.name, email: cust.email, customer_id: 'cust_fx_' + cust.id, customer_code: null, membership_type: null, membership_type_obj: null, wallet_points: 0 });
  }

  /** POST get_customer_wallet_points {uid, online_key} (E25) then POST get_user_loyalty_products_v4 {uid,
   *  online_key} (E26), + PROPOSED GET loyalty_catalogue_online?online_key= (every reward, locked ones included).
   *  Resolves { wallet, products, catalogue } (raw bodies). Unknown uid → 400 {status:false, error:'Customer Data Not
   *  Found!'} (the real adapter soft-fails both reads to null parts instead). */
  function getLoyalty(hotel, opts) {
    return respond(function () {
      storeOr404(hotel, { status: false, error: 'Store Data Not Found!' });
      var uid = uidOf(opts);
      if (isDemo(uid)) return { wallet: FX().wallet(hotel, ctx()), products: FX().loyaltyProducts(hotel), catalogue: FX().loyaltyCatalogue(hotel) };
      var cust = mem.customers[uid];
      if (!cust) fail(400, { status: false, error: 'Customer Data Not Found!' });
      var w = zeroWallet(hotel, cust);
      var storeId = FX().def(hotel).id;
      return {
        wallet: w,
        // v4's userId is the wallet-master store's id, not the customer's (the staging capture)
        products: { status: 'success', client_type: 'web', data: { current_points: 0, resId: storeId, userId: storeId, price_type: 'sale_price', maxEligiblePrice: 0, eligible_products: [] } },
        catalogue: FX().loyaltyCatalogue(hotel),
      };
    });
  }

  /** POST get_customer_wallet_points alone (P2-02: the store's member read): getLoyalty's `wallet`. */
  function getWallet(hotel, opts) {
    return getLoyalty(hotel, opts).then(function (r) { return r.wallet; });
  }

  /** GET cust_wallet_points_transactions?customer_id=cust_… (§3.12). opts.uid picks the customer (its
   *  customer_id comes from get_customer_wallet_points in Batch 2). */
  function getWalletTransactions(hotel, opts) {
    return respond(function () {
      storeOr404(hotel, { status: false, error: 'Store Data Not Found!' });
      var uid = uidOf(opts);
      if (isDemo(uid)) return FX().walletTransactions(hotel, ctx());
      if (!mem.customers[uid]) fail(400, { status: false, error: 'Customer Data Not Found!' });
      return { status: true, data: [] };
    });
  }

  // P3-09: the demo member as edited through updateProfile (mem.demoEdits over a copy of the shipped record).
  function demoEdits() { return mem.demoEdits && typeof mem.demoEdits === 'object' ? mem.demoEdits : {}; }
  function editedDemo(row) { return Object.assign(row, clone(demoEdits())); }

  /** GET customer/getdata?uid= (§3.7, CustomerController@CustomerDataGet) → {status:true, data: CustomerData,
   *  customerCode, customerId, membershipTypeName, membershipTypeId, customerWalletPoints}. Unknown uid → 400.
   *  The demo member carries its updateProfile edits (P3-09). */
  function getCustomer(hotel, uid) {
    return respond(function () {
      storeOr404(hotel, { status: false, error: 'Store Data Not Found!' });
      var u = uid || sessionUid() || FX().DEMO_CUSTOMER.uid;
      if (isDemo(u)) {
        var body = FX().customerData(hotel, u);
        if (body && body.data) editedDemo(body.data);
        return body;
      }
      var cust = mem.customers[u];
      if (!cust) fail(400, { status: false, error: 'Customer Data Not Found!' });
      return { status: true, data: cust, customerCode: null, customerId: null, membershipTypeName: null, membershipTypeId: null, customerWalletPoints: 0 };
    });
  }

  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** POST send-otp {email, store_id, isMobileApp} (E20, CustomerController@sendOtp).
   *  200 {message:'Verification Code sent to your email.'} | 422 {message:'Validation failed', errors}. */
  function sendOtp(hotel, email) {
    return respond(function () {
      if (!FX().has(hotel)) fail(422, { message: 'Validation failed', errors: { store_id: ['The selected store id is invalid.'] } });
      if (!EMAIL.test(String(email || '').trim())) fail(422, { message: 'Validation failed', errors: { email: ['The email field must be a valid email address.'] } });
      return { message: 'Verification Code sent to your email.' };
    });
  }

  /** POST verify-otp {email, otp (a string), store_id, isMobileApp} (E21, CustomerController@verifyOtp).
   *  Known email → 200 {message:'Login successful', customer: CustomerData, customToken}.
   *  New email → 201 {message:'Signup successful', customer: null, customToken}: the page registers with the uid
   *  claim of the token (OO.data.signupUid). Wrong code → 400 {error:'Invalid or expired Verification Code.'} (key
   *  `error`, not message). customToken is JWT-shaped (customToken above): its uid claim is the customer's, and a new
   *  email's is an 'fx_…' uid made once, so a repeated verify before register carries the same one. The real adapter
   *  also copies that uid to a root `uid`; this twin leaves it in the token. */
  function verifyOtp(hotel, email, code) {
    return respond(function () {
      if (!FX().has(hotel)) fail(422, { message: 'Validation failed', errors: { store_id: ['The selected store id is invalid.'] } });
      if (String(code || '').trim() !== FX().OTP_CODE) fail(400, { error: 'Invalid or expired Verification Code.' });
      var e = String(email || '').trim().toLowerCase();
      if (e === FX().DEMO_CUSTOMER.email) return { message: 'Login successful', customer: editedDemo(clone(FX().DEMO_CUSTOMER)), customToken: customToken(FX().DEMO_CUSTOMER.uid) };
      var known = Object.keys(mem.customers).map(function (k) { return mem.customers[k]; }).filter(function (c) { return c.email === e; })[0];
      if (known) return { message: 'Login successful', customer: known, customToken: customToken(known.uid) };
      var uid = signupUidFor(e);
      save();
      return { message: 'Signup successful', customer: null, customToken: customToken(uid) };
    });
  }

  // customer/register's rules (CustomerController@CustomerRegistration), in its order, on the body as Laravel's
  // TrimStrings + ConvertEmptyStringsToNull leave it (a blank value is a missing one) → { field: [message] }.
  function registerErrors(body) {
    var errors = {};
    function blank(v) { return v === undefined || v === null || String(v).trim() === ''; }
    if (blank(body.uid)) errors.uid = ['The uid field is required.'];
    if (blank(body.name)) errors.name = ['The name field is required.'];
    if (blank(body.email)) errors.email = ['The email field is required.'];
    else if (!EMAIL.test(String(body.email).trim())) errors.email = ['The email field must be a valid email address.'];
    if (String(body.phone_no || '').length > 15) errors.phone_no = ['The phone no field must not be greater than 15 characters.'];
    return errors;
  }
  // Laravel's 422 message: the first error, then "(and N more error[s])".
  function validationMessage(errors) {
    var all = [];
    Object.keys(errors).forEach(function (k) { all = all.concat(errors[k]); });
    var more = all.length - 1;
    return all[0] + (more > 0 ? ' (and ' + more + ' more error' + (more === 1 ? '' : 's') + ')' : '');
  }

  /** POST customer/register {uid, name, email, hotelKey, phone_no, birthday, isMobileApp} (E22,
   *  CustomerController@CustomerRegistration) → 200 {status:true, data: CustomerData}, the name trimmed.
   *  422 {message, errors}, Laravel's shape, with every failing rule (registerErrors): uid, name and email required (a
   *  blank one is missing), a valid email, phone_no ≤ 15 characters. 400 {status:false, error:'Store Data Not Found!'}. */
  function register(hotel, body) {
    body = body || {};
    return respond(function () {
      if (!FX().has(hotel)) fail(400, { status: false, error: 'Store Data Not Found!' });
      var errors = registerErrors(body);
      if (Object.keys(errors).length) fail(422, { message: validationMessage(errors), errors: errors });
      var stamp = now().toISOString();
      var row = {
        id: 900 + Object.keys(mem.customers).length + 1, uid: String(body.uid), name: String(body.name).trim(), phone_no: String(body.phone_no || ''),
        email: String(body.email).trim().toLowerCase(), address: null, birthday: body.birthday || null, otp_code: null, otp_expires_at: null,
        created_at: stamp, updated_at: stamp,
      };
      mem.customers[row.uid] = row;
      save();
      return { status: true, data: row };
    });
  }

  // ---- the profile update (B16, P3-09) -------------------------------------------------------------------
  var PROFILE_FIELDS = ['name', 'phone_no', 'birthday', 'address'];
  var ADDRESS_KEYS = ['uid', 'type', 'address', 'city', 'zipcode', 'selected', 'geoLoc'];
  var ADDRESS_TYPES = ['HOME', 'WORK', 'OTHER'];
  function has(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  // The B16 body the real adapter sends for `fields` (src/data/api-real-auth.jsx profileBody): only the keys given.
  function profileBody(fields) {
    var f = isObj(fields) ? fields : {};
    var out = {};
    var text = function (v) { return v == null ? '' : String(v).trim(); };
    if (has(f, 'name')) out.name = text(f.name);
    if (has(f, 'phone_no')) out.phone_no = text(f.phone_no) || null;
    if (has(f, 'birthday')) {
      var m = /^(\d{4}-\d{2}-\d{2})/.exec(text(f.birthday));
      out.birthday = text(f.birthday) ? (m ? m[1] : text(f.birthday)) : null;
    }
    if (has(f, 'address')) {
      var book = Array.isArray(f.address) ? f.address.filter(isObj).map(function (a) {
        var e = {};
        ADDRESS_KEYS.forEach(function (k) { if (has(a, k)) e[k] = a[k]; });
        return e;
      }) : [];
      out.address = book.length ? book : null;
    }
    return out;
  }
  // CustomerController::profileRules on the body as Laravel's TrimStrings + ConvertEmptyStringsToNull leave it
  // → { field: [message] } (the server's keys: 'name', 'address.0.address', 'profile', …).
  function profileErrors(body) {
    var errors = {};
    function add(k, msg) { (errors[k] = errors[k] || []).push(msg); }
    function blank(v) { return v === undefined || v === null || (typeof v === 'string' && v.trim() === ''); }
    function numOrStr(v, max) { return (typeof v === 'number' && Math.floor(v) === v) || (typeof v === 'string' && v.length <= max); }
    if (!PROFILE_FIELDS.some(function (k) { return has(body, k); })) add('profile', 'Send at least one of name, phone_no, birthday or address.');
    if (has(body, 'name')) {
      if (blank(body.name)) add('name', 'The name field is required.');
      else if (String(body.name).length > 255) add('name', 'The name field must not be greater than 255 characters.');
    }
    if (has(body, 'phone_no') && !blank(body.phone_no)) {
      var p = String(body.phone_no).trim();
      if (p.length > 15) add('phone_no', 'The phone no field must not be greater than 15 characters.');
      if (!/^\+?[0-9 ()\-]+$/.test(p) || p.replace(/\D/g, '').length < 5) {
        add('phone_no', 'The phone number may only contain digits, spaces, brackets, dashes and a leading +, with at least 5 digits.');
      }
    }
    if (has(body, 'birthday') && !blank(body.birthday)) {
      var b = String(body.birthday).trim();
      var dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(b);
      var dt = dm ? new Date(Date.UTC(+dm[1], +dm[2] - 1, +dm[3])) : null;
      var tomorrow = new Date(now().getTime() + 86400000).toISOString().slice(0, 10);
      if (!dt || dt.getUTCMonth() !== +dm[2] - 1 || dt.getUTCDate() !== +dm[3]) add('birthday', 'The birthday field must match the format Y-m-d.');
      else if (b < '1900-01-01') add('birthday', 'The birthday field must be a date after or equal to 1900-01-01.');
      else if (b > tomorrow) add('birthday', 'The birthday field must be a date before or equal to ' + tomorrow + '.');
    }
    if (has(body, 'address') && body.address !== null) {
      if (!Array.isArray(body.address)) add('address', 'The address field must be an array.');
      else {
        if (body.address.length > 20) add('address', 'The address field must not have more than 20 items.');
        body.address.forEach(function (a, i) {
          var k = 'address.' + i + '.';
          if (!isObj(a)) { add('address.' + i, 'The address.' + i + ' field must be an array.'); return; }
          if (blank(a.address)) add(k + 'address', 'The ' + k + 'address field is required.');
          else if (String(a.address).length > 255) add(k + 'address', 'The ' + k + 'address field must not be greater than 255 characters.');
          if (!blank(a.uid) && !numOrStr(a.uid, 64)) add(k + 'uid', 'The ' + k + 'uid field must be a number or a string of at most 64 characters.');
          if (!blank(a.type) && ADDRESS_TYPES.indexOf(a.type) < 0) add(k + 'type', 'The selected ' + k + 'type is invalid.');
          if (!blank(a.city) && String(a.city).length > 100) add(k + 'city', 'The ' + k + 'city field must not be greater than 100 characters.');
          if (!blank(a.zipcode) && !numOrStr(a.zipcode, 20)) add(k + 'zipcode', 'The ' + k + 'zipcode field must be a number or a string of at most 20 characters.');
          if (a.selected != null && [true, false, 0, 1, '0', '1'].indexOf(a.selected) < 0) add(k + 'selected', 'The ' + k + 'selected field must be true or false.');
          if (a.geoLoc != null) {
            var g = a.geoLoc;
            var lat = isObj(g) ? Number(g.latitude) : NaN, lng = isObj(g) ? Number(g.longitude) : NaN;
            if (!isObj(g) || blank(g.latitude) || !isFinite(lat) || Math.abs(lat) > 90) add(k + 'geoLoc.latitude', 'The ' + k + 'geoLoc.latitude field must be between -90 and 90.');
            if (!isObj(g) || blank(g.longitude) || !isFinite(lng) || Math.abs(lng) > 180) add(k + 'geoLoc.longitude', 'The ' + k + 'geoLoc.longitude field must be between -180 and 180.');
          }
        });
      }
    }
    return errors;
  }
  // CustomerController::profileAddressBook: REF's keys and defaults, exactly one entry selected.
  function addressBook(entries) {
    var book = [];
    var selected = null;
    var newUid = now().getTime();
    (entries || []).forEach(function (a, i) {
      var g = isObj(a.geoLoc) ? a.geoLoc : null;
      var geo = !!g && g.latitude != null && g.longitude != null;
      book.push({
        uid: a.uid != null ? a.uid : newUid + i, type: a.type != null ? a.type : 'OTHER', address: String(a.address).trim(),
        city: a.city != null ? String(a.city).trim() : '', zipcode: a.zipcode != null ? String(a.zipcode) : null, selected: 0,
        geoLoc: { latitude: geo ? Number(g.latitude) : 0, longitude: geo ? Number(g.longitude) : 0 },
      });
      if (selected === null && (a.selected === true || a.selected === 1 || a.selected === '1')) selected = i;
    });
    if (book.length) book[selected === null ? 0 : selected].selected = 1;
    return book;
  }
  function profileFail(status, code, message, extra) {
    var data = Object.assign({ status: false, error: code, message: message }, extra || {});
    var err = new Error(message);
    err.status = status;
    err.data = data;
    err.code = status === 404 ? null : code; // a 404 is classified by its status alone (the real adapter's rule)
    err.fields = status === 422 ? data.errors : null;
    return err;
  }

  /** POST customer/profile (B16, CustomerController@updateProfile; P3-09) with the header Authorization: Bearer
   *  <customToken> → 200 {status: true, data: CustomerData} (the OTP fields hidden). The twin of the real adapter's
   *  updateProfile(hotel, fields, opts): the same body from `fields` (only its name / phone_no / birthday / address),
   *  the same opts {uid?, token?} defaulting to the session, the same rejections plus `code` and `fields`. No uid or
   *  no token → 401 TOKEN_REQUIRED at once (no simulated request); 422 VALIDATION_FAILED with the server's rules
   *  (profileErrors); an unknown store 404 STORE_NOT_FOUND; an unknown customer 404 CUSTOMER_NOT_FOUND (it never
   *  creates). Only the fields sent change: name trimmed, phone_no null → '', birthday null → null, address = the
   *  whole book (null → [], profileAddressBook's defaults). The demo member's edits go to mem.demoEdits. A fixture
   *  token is always its own uid's and every fixture customer is linked to every fixture store, so the twin never
   *  answers UID_MISMATCH, STORE_NOT_LINKED or 503. */
  function updateProfile(hotel, fields, opts) {
    var uid = (opts && opts.uid) || sessionUid();
    var token = opts && opts.token !== undefined ? opts.token : sessionToken();
    var body = profileBody(fields);
    if (!uid || !token || !String(token).trim()) {
      return Promise.reject(profileFail(401, 'TOKEN_REQUIRED', 'Please sign in again to update your profile.'));
    }
    return respond(function () {
      var errors = profileErrors(body);
      if (Object.keys(errors).length) throw profileFail(422, 'VALIDATION_FAILED', 'Validation failed', { errors: errors });
      if (!FX().has(hotel)) throw profileFail(404, 'STORE_NOT_FOUND', 'Store not found.');
      var demo = isDemo(String(uid));
      var row = demo ? editedDemo(clone(FX().DEMO_CUSTOMER)) : mem.customers[uid];
      if (!row) throw profileFail(404, 'CUSTOMER_NOT_FOUND', 'Customer not found.');
      var changed = {};
      if (has(body, 'name')) changed.name = body.name;
      if (has(body, 'phone_no')) changed.phone_no = body.phone_no == null ? '' : body.phone_no;
      if (has(body, 'birthday')) changed.birthday = body.birthday;
      if (has(body, 'address')) changed.address = addressBook(body.address || []);
      changed.updated_at = now().toISOString();
      Object.assign(row, changed);
      if (demo) { delete changed.updated_at; mem.demoEdits = Object.assign({}, demoEdits(), changed); }
      save();
      var data = clone(row);
      delete data.otp_code;
      delete data.otp_expires_at;
      return { status: true, data: data };
    });
  }

  // ---- orders ------------------------------------------------------------------------------------------
  function progressOf(o, at) {
    if (o.online && !o.paidAt) return { orderstatus: 'PROCESSING', ticket_state: null, step: 'unpaid' };
    var elapsed = at - (o.online ? o.paidAt : o.placedAt);
    if (elapsed < ORDER_PROGRESS_MS.accepted) return { orderstatus: 'PENDING', ticket_state: null, step: 'received' };
    if (elapsed < ORDER_PROGRESS_MS.ready) return { orderstatus: 'ACCEPTED', ticket_state: null, step: 'preparing' };
    return { orderstatus: 'ACCEPTED', ticket_state: 'ready', step: 'ready' };
  }

  function ticketOf(o) {
    var d = FX().def(o.hotel);
    var idx = FX().menuIndex(o.hotel);
    var p = o.payload;
    var pr = progressOf(o, now().getTime());
    var regular = [], batches = {}, order = [];
    (p.data || []).forEach(function (l) {
      var mods = (l.modifiers || []).map(function (m) { return [m.addOnId, m.addOnName, m.price, Math.max(1, m.count / Math.max(1, l.count))]; });
      var line = {
        productId: l.product_id, name: (idx.products[l.product_id] || {}).label || 'Item', qty: l.count, price: l.price,
        sizeId: l.size && l.size[0] ? l.size[0].id : null, sizeName: l.size && l.size[0] ? l.size[0].size_name : null,
        note: l.kitchen_note, mods: mods, extra: l.extra_price || 0, discount: l.discount || 0,
      };
      if (!l.is_combo) { regular.push(line); return; }
      if (!batches[l.comboBatch]) {
        var c = idx.combos[l.combo_id] || {};
        batches[l.comboBatch] = { id: l.combo_id, name: c.combo_name || 'Combo', description: c.description || '', price: l.combo_price, batch: l.comboBatch, items: [] };
        order.push(l.comboBatch);
      }
      batches[l.comboBatch].items.push(line);
    });
    // As the POS imports it (Batch 2 P3-05): the server adds a delivery order's fee as a product line ('Delivery Fee' +
    // the zone's name), the ticket's subtotal includes it, its surcharge is a % of that subtotal (2 dp, as the column
    // keeps it), and paid is what was paid (0 until then).
    var fee = /DELIVERY/.test(p.order_type) ? Number(p.deliverycharge) || 0 : 0;
    if (fee > 0) {
      var zone = p.deliveryzone && p.deliveryzone.name ? String(p.deliveryzone.name) : '';
      regular.push({ productId: 0, name: 'Delivery Fee' + zone, qty: 1, price: fee, sizeId: null, sizeName: null, note: '', mods: [], extra: 0, discount: 0 });
    }
    var posSubtotal = Number(p.subtotal) + fee;
    var paidNow = o.online && !!o.paidAt;
    var t = FX().ticket(d, {
      rowId: 120000 + o.seq, ticketId: o.orderId, short: o.short, at: o.placedAt, state: pr.ticket_state,
      deliveryTime: Date.parse(p.deliver_time || '') || null, // the chosen slot (ASAP: now + cooking time)
      orderStatus: pr.orderstatus, paymentStatus: o.online && !o.paidAt ? 'UNPAID' : (o.online ? 'PAID' : 'UNPAID'),
      ticketStatus: o.online && o.paidAt ? 'PAID' : 'UNPAID', paymentType: p.payment_type,
      subtotal: posSubtotal, paid: paidNow ? Number(p.payable_amount) + Number(p.tip || 0) : 0, billTotal: p.payable_amount, tax: p.tax, total: p.total,
      surcharge_label: p.surcharge_label,
      surcharge: posSubtotal > 0 ? Math.round((Number(p.surcharge) || 0) / posSubtotal * 10000) / 100 : 0,
      customerId: p.customer_id, customerName: p.customer_name, customerPhone: p.customer_phone,
      note: p.notes, deliverType: p.delivery_type, couponId: p.coupon_id, couponType: p.coupon_type, discountReason: p.discount_reason,
      couponAmount: p.coupon_discount_amount, items: regular, combos: order.map(function (b) { return batches[b]; }),
    });
    t.ticket_type = 'ONLINE_' + (/DELIVERY/.test(p.order_type) ? 'DELIVERY' : 'PICKUP');
    if (Number(p.tip) > 0) t.tip_amount = Number(p.tip);
    return t;
  }

  function placedFor(hotel, uid) {
    return Object.keys(mem.orders).map(function (k) { return mem.orders[k]; }).filter(function (o) {
      return o.hotel === String(hotel) && (o.uid === uid || (!o.uid && isDemo(uid)));
    }).sort(function (a, b) { return b.placedAt - a.placedAt; });
  }

  /** POST get_customer_orders?page=1 {uid, online_key} (E16, CustomerController@getCustomerOrders). Bare
   *  paginator, no status wrapper, per_page 1000. Unknown uid → 400 {status:false, error:'Customer Data Not Found!'}.
   *  Orders placed in this demo are listed first. */
  function getOrders(hotel, opts) {
    return respond(function () {
      storeOr404(hotel, { status: false, error: 'Store Data Not Found!' });
      var uid = uidOf(opts);
      if (!isDemo(uid) && !mem.customers[uid]) fail(400, { status: false, error: 'Customer Data Not Found!' });
      var mine = placedFor(hotel, uid).map(ticketOf);
      var base = isDemo(uid) ? FX().tickets(hotel, ctx()) : [];
      return FX().paginator(mine.concat(base));
    });
  }

  /** GET get_order_data?ticket_id=OOT… (§3.11, onlineOrder@getOrderDetailsByTicketId) → {status:true, data: Ticket
   *  + deliveryBy, source, scheduled_time, hotel_address}. 400 {status:false, error:'ticket_id is required'}. */
  function getOrder(hotel, ticketId) {
    return respond(function () {
      if (!ticketId) fail(400, { status: false, error: 'ticket_id is required' });
      var placed = mem.orders[ticketId];
      if (placed && placed.hotel === String(hotel)) return { status: true, data: FX().detailOf(FX().def(hotel), ticketOf(placed)) };
      var raw = FX().orderDetail(hotel, String(ticketId), ctx());
      if (!raw) fail(404, { status: false, error: 'Order not found' });
      return raw;
    });
  }

  /** PROPOSED GET customer_notifications?online_key=&uid= → {status:true, data:[{id, type, title, body,
   *  created_at, read_at, link}]} (BATCH2-GAPS.md). */
  function getNotifications(hotel) {
    return respond(function () { storeOr404(hotel, { status: false, error: 'Store Data Not Found!' }); return FX().notifications(hotel, ctx()); });
  }

  // ---- table booking ------------------------------------------------------------------------------------
  function bookingDate(date) {
    var s = String(date || '').trim(), m;
    if ((m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s))) return s;
    if ((m = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(s))) return m[3] + '-' + m[2] + '-' + m[1];
    return null;
  }
  function badDate() {
    var msg = 'The date must be a valid calendar date in DD-MM-YYYY, YYYY-MM-DD, or DD/MM/YYYY format.';
    fail(422, { message: msg, errors: { date: [msg] } });
  }

  /** POST get_table_booking_config {id: hotel} (TableBookingController@getTableBookingConfigs) → {status, shop,
   *  config, available_dates, holidays, theme, workingInfo, review_summary}.
   *  400 {status:false, error:'not found restuarent' | 'Reservation Settings Not Found!'}. */
  function getBookingConfig(hotel) {
    return respond(function () {
      if (!FX().has(hotel)) fail(400, { status: false, error: 'not found restuarent' });
      var raw = FX().bookingConfig(hotel, ctx());
      if (!raw) fail(400, { status: false, error: 'Reservation Settings Not Found!' });
      return raw;
    });
  }

  /** POST check_table_booking_slots {id: hotel, date, person_count} (TableBookingController@checkTableBookingSlots)
   *  → {status:true, slots:[{stime 'HH:mm', stimeEpoch, etime, etimeEpoch, booked_slots, allow_booking,
   *  remaining_guests}]}. date: 'YYYY-MM-DD' | 'DD-MM-YYYY' | 'DD/MM/YYYY' (422 otherwise). 404 unknown store. */
  function getBookingSlots(hotel, opts) {
    opts = opts || {};
    return respond(function () {
      if (!FX().has(hotel)) fail(404, { status: false, error: 'Restaurant not found' });
      var ymd = bookingDate(opts.date);
      if (!ymd) badDate();
      var guests = Number(opts.guests);
      if (!(guests >= 1)) fail(422, { message: 'The person count field must be at least 1.', errors: { person_count: ['The person count field must be at least 1.'] } });
      return FX().bookingSlots(hotel, { date: ymd, guests: guests }, ctx());
    });
  }

  /** POST place_table_booking {user_id: hotel (online_key, sic), date 'd-m-Y', time 'HH:mm', person, name, email,
   *  phone, notes, timezone, uid} (TableBookingController@placeTableBooking) → {status:true, data:{reservation_id
   *  'TRE…', customer_email, customer_name, customer_phone, person_count, reservation_date, reservation_time, notes,
   *  reservation_status:'PENDING'}}. 400 {status:false, error, remaining_guests} when the slot can't seat the party. */
  function placeBooking(hotel, body) {
    body = body || {};
    return respond(function () {
      if (!FX().has(hotel)) fail(404, { status: false, error: 'Restaurant not found' });
      if (!FX().bookingConfig(hotel, ctx())) fail(400, { status: false, error: 'Reservation Settings Not Found!' });
      var ymd = bookingDate(body.date);
      if (!ymd) badDate();
      var guests = Math.max(1, Number(body.guests || body.person) || 1);
      var slot = FX().bookingSlots(hotel, { date: ymd, guests: guests }, ctx()).slots.filter(function (s) { return s.stime === String(body.time || '').slice(0, 5); })[0];
      if (!slot) fail(400, { status: false, error: 'The selected time slot is not available.' });
      if (!slot.allow_booking) {
        fail(400, {
          status: false, remaining_guests: slot.remaining_guests,
          error: slot.remaining_guests > 0
            ? 'Only ' + slot.remaining_guests + ' seat(s) left for the selected time slot. Please reduce the party size or pick another slot.'
            : 'The selected time slot is fully booked. Please pick another slot.',
        });
      }
      mem.seq += 1;
      var data = {
        reservation_id: 'TRE' + FX().def(hotel).id + Math.floor(now().getTime() / 1000) + String(100 + mem.seq).slice(-3),
        customer_email: String(body.email || ''), customer_name: String(body.name || ''), customer_phone: String(body.phone || ''),
        person_count: guests, reservation_date: ymd + 'T00:00:00.000000Z', reservation_time: slot.stime, notes: body.notes || null,
        reservation_status: 'PENDING',
      };
      // The row table_booking_status / get_customer_table_bookings serve back (P3-20 / P3-21 twins below).
      mem.bookings = (mem.bookings || []).concat([Object.assign({}, data, {
        user_id: FX().def(hotel).id, hotel: FX().def(hotel).key, reservation_date: ymd, reservation_time: slot.stime + ':00', table_id: null,
        reservation_from: slot.stimeEpoch, reservation_upto: slot.etimeEpoch, sub_status: null, created_at: now().toISOString(),
      })]).slice(-50);
      save();
      return { status: true, data: data };
    });
  }

  function bookingRow(b) {
    var d = FX().def(b.hotel);
    var row = clone(b);
    delete row.hotel;
    return Object.assign(row, { store_name: d ? d.name : '', store_phone: d ? d.phone : '', store_address: d ? d.addressFull : '' });
  }
  /** POST table_booking_status {email, reservation_id} (E39, TableBookingController@getTableBookingStatus) → {status:true,
   *  data: <the table_bookings row>} for a booking placed here (this browser's fixture state); B08's 404 otherwise. */
  function getBookingStatus(hotel, query) {
    query = query || {};
    return respond(function () {
      var id = String(query.reservationId || '').trim(), email = String(query.email || '').trim().toLowerCase();
      var b = id && email ? (mem.bookings || []).filter(function (x) { return x.reservation_id === id && String(x.customer_email).toLowerCase() === email; })[0] : null;
      if (!b) fail(404, { status: false, error: 'Booking not found' }, "We couldn't find a reservation with that ID and email.");
      var row = bookingRow(b);
      delete row.store_name; delete row.store_phone; delete row.store_address;
      return { status: true, data: row };
    });
  }
  /** POST get_customer_table_bookings {uid, online_key} (E40, TableBookingController@getCustomerTableBookingsPublicOfStore)
   *  → {status:true, data:[rows + store_name / store_phone / store_address]}: the chain's bookings placed here with the
   *  customer's email. Unknown uid → 400 'Customer Data Not Found!'. */
  function getCustomerBookings(hotel, opts) {
    return respond(function () {
      var uid = (opts && opts.uid) || sessionUid();
      var cust = !uid ? null : isDemo(uid) ? FX().DEMO_CUSTOMER : mem.customers[uid];
      if (!cust) fail(400, { status: false, error: 'Customer Data Not Found!' });
      var d = FX().def(hotel);
      var chain = d && d.related && d.related.length ? d.related : [String(hotel)];
      var email = String(cust.email || '').toLowerCase();
      return {
        status: true,
        data: (mem.bookings || []).filter(function (b) { return chain.indexOf(b.hotel) >= 0 && String(b.customer_email).toLowerCase() === email; }).map(bookingRow),
      };
    });
  }

  /** GET tables/gettableDetails?tabId={tableId} (§3.13, TableController@tableDetails) → 200 {Status:true, data:[…]}
   *  or 200 {Status:false, error:'Table Not Locked!' | 'Table is Locked!'} (capital S). 400 when no id is given. */
  function getTable(hotel, tableId) {
    return respond(function () {
      if (tableId === undefined || tableId === null || tableId === '') fail(400, { Status: false, error: 'Table ID Not Given!' });
      return FX().table(hotel, tableId);
    });
  }

  // ---- checkout / payment / status -------------------------------------------------------------------
  function apology(code, error, message) {
    fail(400, { status: "Apologies for the inconvenience, but we're currently occupied. Please try again shortly.", ecode: code, error: error, message: message });
  }

  // Server-side validation (§3.8, OnlineOrderValidationController, light): DB prices per line, then the totals
  // within ±0.2.
  function validatePayload(hotel, p) {
    var idx = FX().menuIndex(hotel);
    var batches = {};
    var subtotal = 0;
    (p.data || []).forEach(function (l) {
      var prod = idx.products[l.product_id];
      if (!prod) apology(101, 'PRODUCT_NOT_FOUND', 'Product ' + l.product_id + ' is no longer available.');
      var sizeId = l.size && l.size[0] ? l.size[0].id : null;
      if (sizeId != null && !prod.sizes[sizeId]) apology(102, 'PRODUCT_SIZE_NOT_FOUND', 'A selected size is no longer available.');
      (l.modifiers || []).forEach(function (m) {
        var mod = idx.modifiers[m.addOnId];
        if (!mod || Math.abs(mod.price - Number(m.price)) > 0.001) apology(104, 'MODIFIER_PRICE_MISMATCH', 'A selected option has changed.');
      });
      var modTotal = (l.modifiers || []).reduce(function (t, m) { return t + Number(m.price) * Number(m.count); }, 0);
      if (l.is_combo) {
        var combo = idx.combos[l.combo_id];
        if (!combo || Math.abs(combo.combo_price - Number(l.combo_price)) > 0.001) apology(902, 'COMBO_INVALID', 'This combo has changed.');
        if (!batches[l.comboBatch]) batches[l.comboBatch] = { price: Number(l.combo_price), extra: 0, mods: 0 };
        batches[l.comboBatch].extra += Number(l.extra_price || 0) * Number(l.count);
        batches[l.comboBatch].mods += modTotal;
        return;
      }
      var expected = sizeId != null ? prod.sizes[sizeId].amount : prod.sale_price;
      if (Math.abs(expected - Number(l.price)) > 0.001) apology(300, 'INVALID_TOTAL', 'Prices have changed. Please review your cart.');
      subtotal += Number(l.price) * Number(l.count) + modTotal + Number(l.extra_price || 0);
    });
    // Like OnlinePaymentController: each comboBatch is ONE combo (combo_price once + extras × count + modifiers).
    Object.keys(batches).forEach(function (k) {
      var b = batches[k];
      subtotal += b.price + b.mods + b.extra;
    });
    return F().round2(subtotal);
  }

  /** POST checkout (E12, OnlinePaymentController@checkOutOnlineOrder), body = OO.payload.checkoutPayload(…).
   *  200 {status:"true" (STRING), order_id:'OOT…', data:{…payload, online_order_id, payment_status:'UNPAID', source,
   *  hostUrl}, coupon_txn: base64 | null}, plus {payment_id, payment_url} for ONLINE_ORDER_CARD. In fixture mode
   *  payment_url is this SPA's /{hotel}/payment-callback?success=true&order_id=…
   *  400: {status:'frequent order try again later'} (same store within 5 s) | {status:false, ecode:800,
   *  message:'Oops..!! \n Outlet is not accepting orders at the moment '} (ASAP while closed) | {status:'Apologies…',
   *  ecode:101|102|104|300|902, error, message} (validation) ; 404 {ecode:800}. server_secret is not checked: the SPA
   *  path ignores it (B01); the payload still sends '' because the POS order model needs a non-null one. */
  function placeOrder(hotel, payload) {
    return respond(function () {
      var p = payload || {};
      if (!FX().has(hotel) || (p.restaurant_key && p.restaurant_key !== String(hotel))) {
        fail(404, { status: false, ecode: 800, message: 'Restaurant could not be resolved.' });
      }
      var t = now().getTime();
      var last = mem.lastOrderAt[hotel];
      if (last && t - last < 5000 && t >= last) fail(400, { status: 'frequent order try again later', message: 'frequent order try again later' });
      if (!Array.isArray(p.data) || !p.data.length) apology(300, 'INVALID_TOTAL', 'Your cart is empty.');
      if (String(p.delivery_type || 'ASAP') === 'ASAP') {
        var store = OO.model.normalizeStore(FX().restaurant(hotel, ctx()), { now: new Date(t) });
        if (!OO.model.openStatus(store, new Date(t), 'PICKUP').isOpen) {
          fail(400, { status: false, ecode: 800, message: 'Oops..!! \n Outlet is not accepting orders at the moment ' });
        }
      }
      var subtotal = validatePayload(hotel, p);
      if (Math.abs(subtotal - Number(p.subtotal)) > 0.2) apology(300, 'INVALID_TOTAL', 'Order total mismatch. Please review your cart.');

      mem.seq += 1;
      var d = FX().def(hotel);
      var orderId = 'OOT' + d.id + t + String(100 + mem.seq).slice(-3);
      var online = p.payment_type === 'ONLINE_ORDER_CARD';
      var shortBase = d.data === 'bennys' ? 1042 : 100;
      var prior = Object.keys(mem.orders).filter(function (k) { return FX().def(mem.orders[k].hotel).data === d.data; }).length;
      mem.orders[orderId] = {
        orderId: orderId, hotel: String(hotel), seq: mem.seq, short: shortBase + prior + 1, placedAt: t, paidAt: null, online: online,
        uid: p.fuid || p.userUniqueId || null, declined: /^decline/i.test(String(p.customer_email || '')), payload: clone(p),
      };
      mem.lastOrderAt[hotel] = t;
      var coupon = null;
      if (p.is_coupon_applied) {
        mem.txn += 1;
        coupon = typeof btoa === 'function' ? btoa(String(mem.txn)) : String(mem.txn);
        mem.orders[orderId].couponTxn = coupon; // the claim invalidateCoupon can release
      }
      save();
      var res = {
        status: 'true',
        order_id: orderId,
        data: Object.assign(clone(p), { online_order_id: orderId, payment_status: 'UNPAID', source: 'SIPO', hostUrl: (window.location && window.location.origin) || null, item_wise_discount_by_coupon: [] }),
        coupon_txn: coupon,
      };
      if (online) {
        res.payment_id = 'OPR' + d.id + t + String(100 + mem.seq).slice(-3);
        res.payment_url = OO.router && OO.router.href
          ? OO.router.href('payment-callback', {}, { hotel: String(hotel), query: { success: 'true', order_id: orderId } })
          : '/' + encodeURIComponent(hotel) + '/payment-callback?success=true&order_id=' + encodeURIComponent(orderId);
      }
      return res;
    });
  }

  /** POST payment_update {order_id} (§3.9, OnlinePaymentController@paymentUpdateVerification). The UI keys off the
   *  HTTP code, so this resolves { httpStatus, body } for EVERY status (as the real adapter does):
   *    200 {order_id, payment_id, payment_status:'succeeded', updated_at, status:true, message:''}
   *    202 {status:'Order existing !!'} (already paid, or a pay-at-counter order)
   *    409 {payment_status:'failed', status:false, message} (demo: email starting 'decline')
   *    404 {status:false, code:'ORDER_NOT_FOUND', message}. Don't call it for CASH / PAY_AT_COUNTER / wallet orders. */
  function paymentUpdate(orderId) {
    return respond(function () {
      var o = mem.orders[orderId];
      if (!o) return { httpStatus: 404, body: { status: false, code: 'ORDER_NOT_FOUND', message: 'Order not found.' } };
      var t = now();
      if (!o.online || o.paidAt) return { httpStatus: 202, body: { status: 'Order existing !!' } };
      var payId = 'OPR' + o.orderId.slice(3);
      if (o.declined) return { httpStatus: 409, body: { order_id: o.orderId, payment_id: payId, payment_status: 'failed', updated_at: t.toISOString(), status: false, message: 'Your card was declined.' } };
      o.paidAt = t.getTime();
      save();
      return { httpStatus: 200, body: { order_id: o.orderId, payment_id: payId, payment_status: 'succeeded', updated_at: t.toISOString(), status: true, message: '' } };
    });
  }

  /** POST online_order_status {order_id} (§3.10, OnlinePaymentController@statusCheckAcceptRejectFromWeb) →
   *  {orderid, orderstatus 'PROCESSING'|'PENDING'|'ACCEPTED', response ('Your order #1043 will be ready in 15
   *  minutes' → OO.model.orderProgress reads #1043), deliver_time, delivery_type, updated_time, ticket_created,
   *  ticket_state null|'ready', fulfillment_type, status:true}. Unknown → 400 {orderstatus:'NO_DATA', status:false}.
   *  The demo member's sample tickets of the store the facade names (FX().tickets) answer too, so their Track works
   *  (P3-03): their own orderStatus (ACCEPTED) and ticket_state, ticket_created true. */
  function orderStatus(orderId) {
    var hotel = arguments[1]; // OO.api's trailing hotel (not in the signature: the real twin takes orderId only)
    return respond(function () {
      var o = mem.orders[orderId];
      if (!o) {
        var demo = demoTicket(orderId, hotel);
        if (!demo) fail(400, { orderstatus: 'NO_DATA', status: false }, 'Order not found');
        return {
          orderid: demo.ticket_id, orderstatus: demo.orderStatus || 'ACCEPTED', response: null,
          deliver_time: demo.delivery_time > 0 ? new Date(demo.delivery_time).toISOString() : null,
          delivery_type: demo.deliver_type || 'ASAP', updated_time: now().toISOString(), ticket_created: true,
          ticket_state: demo.order_state || null, fulfillment_type: demo.deliver_type || 'ASAP', status: true,
        };
      }
      var t = now();
      var pr = progressOf(o, t.getTime());
      var response = pr.step === 'preparing' ? 'Your order #' + o.short + ' will be ready in 15 minutes'
        : (pr.step === 'ready' ? 'Your order #' + o.short + ' is ready for pickup' : null);
      return {
        orderid: o.orderId, orderstatus: pr.orderstatus, response: response,
        deliver_time: o.payload.deliver_time || null, delivery_type: o.payload.delivery_type || 'ASAP', updated_time: t.toISOString(),
        ticket_created: pr.step === 'preparing' || pr.step === 'ready', ticket_state: pr.ticket_state,
        fulfillment_type: o.payload.delivery_type || 'ASAP', status: true,
      };
    });
  }

  // A sample ticket of the fixture store `hotel` (FX().tickets: the demo member's), or null.
  function demoTicket(orderId, hotel) {
    if (hotel === undefined || hotel === null || !FX().has(String(hotel))) return null;
    var id = String(orderId);
    return FX().tickets(String(hotel), ctx()).filter(function (t) { return String(t.ticket_id) === id; })[0] || null;
  }

  // ---- demo session: the landing store decides (see the header) ----------------------------------------------
  function seedDemoSession() {
    try {
      var s = storage();
      if (OO.mode !== 'app' || !OO.store || OO.store.session() || (s && s.getItem(DEMO_FLAG))) return;
      OO.store.setSession({ customer: OO.model.normalizeCustomer(clone(FX().DEMO_CUSTOMER)), customToken: customToken(FX().DEMO_CUSTOMER.uid) });
      if (s) s.setItem(DEMO_FLAG, '1');
    } catch (e) { /* storage off: no seeding */ }
  }
  function landingSession() {
    var hotel = null;
    try { hotel = OO.router && OO.router.current ? OO.router.current().hotel : null; } catch (e) { hotel = null; }
    if (!hotel) return;
    if (FX().has(hotel)) seedDemoSession();
    else if (OO.data.dropFixtureSession) OO.data.dropFixtureSession(hotel);
  }
  landingSession();

  OO.data.fixtureApi = {
    isFixture: true,
    now: now,
    getRestaurant: getRestaurant,
    getMenu: getMenu,
    getCoupons: getCoupons,
    getPricetags: getPricetags,
    validateCoupon: validateCoupon,
    invalidateCoupon: invalidateCoupon,
    getBranches: getBranches,
    getChain: getChain,
    getPopular: getPopular,
    searchStores: searchStores,
    nearbyStores: nearbyStores,
    getLoyalty: getLoyalty,
    getWallet: getWallet,
    getWalletTransactions: getWalletTransactions,
    getCustomer: getCustomer,
    sendOtp: sendOtp,
    verifyOtp: verifyOtp,
    register: register,
    updateProfile: updateProfile,
    getOrders: getOrders,
    getOrder: getOrder,
    getNotifications: getNotifications,
    getBookingConfig: getBookingConfig,
    getBookingSlots: getBookingSlots,
    placeBooking: placeBooking,
    getBookingStatus: getBookingStatus,
    getCustomerBookings: getCustomerBookings,
    getTable: getTable,
    placeOrder: placeOrder,
    paymentUpdate: paymentUpdate,
    orderStatus: orderStatus,
    ORDER_PROGRESS_MS: ORDER_PROGRESS_MS,
    __setLatency: function (ms) { latency = Math.max(0, Number(ms) || 0); },
    __setNow: function (v) { clockFn = v === undefined ? null : v; },
    __reset: function () {
      mem = emptyState();
      try { var s = storage(); if (s) s.removeItem(STATE_KEY); } catch (e) { /* ignore */ }
    },
  };
})();


// ===================== src/data/api-real.jsx =====================
// src/data/api-real.jsx: the real (HTTP) adapter, part 1: the shared core (orchestrator, P1-00b), then P1-03's store,
// menu, popular, table, discovery and branches calls. It creates the ONE shared adapter object, OO.data.realApi: every
// api-real*.jsx file attaches its methods to it, and api-select.jsx (P1-02) routes the stores in __SIPO_BOOT.live to
// it. Every method keeps the name, signature and resolved shape of its fixture twin in store-api.jsx and resolves the
// endpoint's REAL raw body (docs/online-order/BATCH2-PLAN.md §5; tests/contract/**). Transport is OO.http v2 only.
//
// Shared core (other api-real*.jsx files use it; P1-03 adds its methods below it):
//   getRestaurant(hotel, { fresh }) → Promise<raw body>   POST getRestaurant {id, timeZone, metadata_format} (E01), cached (OO.http).
//       The timezone rule (§2 row 4): the backend computes the open flags, the day of week and the domain surcharge in
//       the REQUEST's timezone, so send the store's own timezone, remembered per store in localStorage (TZ_KEY). On
//       first contact the device timezone goes out; when the store's `data[0].timezone` (default Pacific/Auckland)
//       differs, it is remembered and the request is repeated once in it. A 2xx whose payload is unusable (status
//       false / 'False', or no data[0]) rejects with unusable() (R-API-4); a 404 is OO.http's kind 'notFound'.
//   _.storeRaw(hotel, opts) → Promise<data[0]>        the store row of getRestaurant.
//   _.storeId(hotel) → Promise<number>               the numeric store id (data[0].id = hotelId): the loaded store's
//       (OO.data.peekHotel) when present, else from getRestaurant.
//   _.tzFor(hotel) → the timezone to send for this store   _.rememberTz(hotel, tz)
//   _.deviceTz() → the device's IANA timezone (tzFor calls it through the bag, so a test can replace it)
//   _.unusable(path, body) → Error { kind: 'unavailable', status: 502, data: body, path }
//   _.TZ_KEY = 'oo-store-tz-v1'   (localStorage: { [onlineKey]: IANA tz })
//   _.tableMenuRetryMs = [400, 1200]   (P4-02: getMenu's pauses before re-reading a table menu; below)
//
// Store, menu and discovery calls (P1-03; twins of store-api.jsx, same signatures and resolved shapes):
//   getMenu(hotel, { fresh, tableId }) → Promise<raw body>   POST get_menu {id, timeZone: _.tzFor(hotel)} (E02), cached;
//       fresh re-reads (P1-16: a fresh menu before submit). {status:'success', data:[category…], menus, menu_type,
//       menu_version, combos}. A 2xx that fails isSuccess or has no category array (`data` not an array, a null or
//       non-JSON body) rejects with unusable() (R-API-4). An empty `data: []` is a usable, empty menu (a table group
//       may exclude every category). A 404 is kind 'notFound'.
//       P4-02, the TABLE menu (mode qr; opts.tableId, a whole number > 0, else a 422-style rejection without a request):
//       body {id, timeZone, table_id} plus the header With-Table-Id: true. The body branch (backend af31ad6e4) checks the
//       table is this store's and active (else 404, by status alone: prod's is nginx HTML), drops the table group's
//       excluded categories and returns excluded_category_ids; the header alone keeps table prices on a host without
//       it. Its own cache entry (the body differs), so the online menu is never reused at a table. It must answer
//       menu_type 'Table Menu': a host without the cold-cache fix answers a table request on a cold cache with the
//       ONLINE menu and no menu_type, so such an answer is read again fresh after _.tableMenuRetryMs (at most two more
//       reads), then rejects with unusable() (its data the last body): online prices never reach a table. A 404, a 5xx,
//       a network failure or an unusable body fail at once.
//       QR-OFF: a store whose table ordering is off (enable_table_side_ordering; BE-LIMITS) answers a table request,
//       after the table check, with 200 {status:'False', error, reason:'table_ordering_off'}: it rejects at once, never
//       read again, with Error { status: 200, kind: 'tableOff', data: the body, message: its message › error } (the
//       reason alone decides; the error text is the server's). Its fixture twin rejects the same way. src/lib/store.jsx
//       turns the store's table ordering off on it ("The menu's channel").
//   getPopular(hotel) → Promise<raw body>   GET popular-dishes/{key} (E03) → {status:'success', data:{products:
//       [{id, name, image, price, order_frequency}], combos:[…]}, meta} (top 5 + 5, server order). It NEVER
//       rejects: the 404 (no data), any other failure or a 2xx without a `data` object resolves the empty body
//       {status:'success', data:{products:[], combos:[]}} (a new object per call), so the menu never fails because of
//       Popular. Not cached.
//   getTable(hotel, tableId) → Promise<raw body>   GET tables/gettableDetails?tabId= (E19): 200 {Status:true,
//       data:[row incl. excluded_category_ids]} or 200 {Status:false, error:'Table Not Locked!' | 'Table is Locked!'},
//       both as is (normalizeTable). Not store-scoped (hotel unused) and not cached. No id → rejects with the API's
//       own 400 {Status:false, error:'Table ID Not Given!'} without a request.
//   searchStores(query) → Promise<raw body>   GET get_online_store_list_using_name_search?name=<trimmed> (E34) →
//       {status:true, data:[{name, online_key, address, latitude, longitude, shop_logo}]}. The B11 rule: fewer than 2
//       characters (code points; the LIKE wildcards % and _ don't count) resolves {status:true, data:[]} without a
//       request (before B11, production answers an empty name with EVERY store).
//   nearbyStores({ lat, lng, radius }) → Promise<raw body>   NEW (E33; its fixture twin came with P3-23's store
//       finder). GET get_online_store_listing?latitude&longitude[&radius] (km; the API defaults to 50) → the search
//       rows plus `distance` (km: the integer 0 at the query point, else a float), nearest first. A missing or
//       non-finite lat / lng rejects with the API's own 400 {status:false, error:'latitude and longitude are required'}
//       without a request. OO.model.normalizeDiscovery reads both bodies.
//   getBranches(hotel) → Promise<{ [online_key]: raw getRestaurant body | {status:false, error} }>   the keys of the
//       store's related_branches (getRestaurant, cached; strings, unique, at most 50) → POST getRestaurantBulk {ids,
//       timeZone: _.tzFor(hotel)} (E35), cached like getRestaurant (full payloads: heavy). A store with no related
//       branches resolves { [hotel]: its getRestaurant body } (the fixture's [hotel] alone) with no bulk request.
//   getChain(hotel) → Promise<raw body>   NEW (P3-24, D-03; fixture twin in store-api.jsx). The store's chain (the
//       master and its branches, chainedshops) for the app's Select store; OO.model.normalizeChain reads both bodies:
//       * B31 (E47): GET online_order/chain/{key} (any member's key, encoded) → {status:true, data:{online_key, master,
//         stores}} as sent. Cached CHAIN_TTL_MS (60 s, the server's own chain cache), so the open labels stay fresh.
//       * The INTERIM, while B31 isn't deployed: its read fails 404 (by status alone: prod's nginx HTML or B31's own
//         STORE_NOT_FOUND), on the network (status 0, the read deadline included), or answers a 2xx without that shape.
//         Then (master keys only, as the plan says) getRestaurant(hotel) (cached; its data[0].id is the master id),
//         GET get_chained_shop_list?id={id} (E45: the children, with chainedshops' linking, plus the master; cached 60
//         s) and POST getRestaurantBulk {ids: those keys, the requested one first, at most 50; timeZone} (E35, cached;
//         no request without keys). It resolves {status:true, interim:true, online_key: hotel, restaurant, list, bulk}
//         (the three raw bodies). E46 get_chained_shops_nearby is not needed: the bulk bodies carry the coordinates
//         and the list carries linking. An unknown key: the interim's getRestaurant 404 (kind 'notFound'), one
//         request more than B31's own 404.
//       Any other B31 failure (429, 5xx, another 4xx) rejects as OO.http does, with no interim requests.
//   getNotifications(hotel) → Promise<{status:true, data:[]}>   no endpoint yet (B20, Phase 3): empty, no request.
// A 2xx can pass isSuccess, so OO.http caches it, and still be unusable here (get_menu's {status:'success', data:null}
// when the server's menu file is corrupt). After such a read, the next read of that key goes out fresh, so a retry
// recovers inside the 15-min TTL.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};
  var R = (OO.data.realApi = OO.data.realApi || {});

  var TZ_KEY = 'oo-store-tz-v1';
  var DEFAULT_TZ = 'Pacific/Auckland';

  function storage() { try { return window.localStorage || null; } catch (e) { return null; } }
  function tzMap() {
    try {
      var s = storage();
      var m = s ? JSON.parse(s.getItem(TZ_KEY) || '{}') : {};
      return m && typeof m === 'object' && !Array.isArray(m) ? m : {};
    } catch (e) { return {}; }
  }
  function deviceTz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ; } catch (e) { return DEFAULT_TZ; }
  }
  function tzFor(hotel) {
    var tz = tzMap()[String(hotel)];
    return typeof tz === 'string' && tz ? tz : R._.deviceTz(); // via the bag, so tests can pin the device timezone
  }
  function rememberTz(hotel, tz) {
    if (!tz) return;
    try {
      var s = storage();
      if (!s) return;
      var m = tzMap();
      if (m[String(hotel)] === tz) return;
      m[String(hotel)] = tz;
      s.setItem(TZ_KEY, JSON.stringify(m));
    } catch (e) { /* private mode / quota: the device timezone is used next time */ }
  }

  function unusable(path, body) {
    var err = new Error('This store is temporarily unavailable. Please try again.');
    err.status = 502;
    err.kind = 'unavailable';
    err.data = body === undefined ? null : body;
    err.path = path;
    return err;
  }

  function storeRowOf(body) {
    if (!body || typeof body !== 'object' || !OO.http.isSuccess(body)) return null;
    var d = Array.isArray(body.data) ? body.data[0] : null;
    return d && typeof d === 'object' ? d : null;
  }

  function getRestaurant(hotel, opts) {
    opts = opts || {};
    var key = String(hotel == null ? '' : hotel);
    var sent = tzFor(key);
    var kioskId = OO.mode === 'kiosk' && OO.kiosk ? OO.kiosk.terminal(key) : null;
    function ask(tz, fresh) {
      // checkedRead: an unusable 2xx (e.g. {status:true, data:[]} passes OO.http.isSuccess and gets cached) marks the
      // key suspect, so the next read goes out fresh instead of replaying the cached body for 15 minutes.
      return checkedRead('getRestaurant', key + ' ' + tz + (kioskId ? ' kiosk ' + kioskId : ''), fresh, function (f) {
        // Explicit opt-in: older backends ignore it and still return the supported inline-image response.
        var body = { id: key, timeZone: tz, metadata_format: 'compact-v1' };
        if (kioskId) body.kiosk_id = kioskId;
        return OO.http.post('getRestaurant', body, { cache: true, fresh: f });
      }, storeRowOf);
    }
    var restaurant = ask(sent, opts.fresh).then(function (body) {
      var tz = String(storeRowOf(body).timezone || '') || DEFAULT_TZ;
      rememberTz(key, tz);
      return tz === sent ? body : ask(tz, opts.fresh);
    });
    if (!OO.orderingQr || !OO.orderingQr.active() || OO.orderingQr.receiptPage()) return restaurant;
    return Promise.all([restaurant, OO.http.get('ordering-qr/profile', {
      query: OO.orderingQr.api({ online_key: key }), fresh: true,
    })]).then(function (results) {
      var body = results[0], profile = results[1];
      if (!profile || !profile.data) throw unusable('ordering-qr/profile', profile);
      return Object.assign({}, body, { data: [Object.assign({}, storeRowOf(body), { ordering_qr: profile.data })] });
    });
  }

  function storeRaw(hotel, opts) { return getRestaurant(hotel, opts).then(storeRowOf); }

  function storeId(hotel) {
    var p = OO.data.peekHotel ? OO.data.peekHotel(hotel, false) : null;
    if (p && p.store && p.store.id) return Promise.resolve(p.store.id);
    return storeRaw(hotel).then(function (d) { return Number(d.id || d.hotelId) || null; });
  }

  R.getRestaurant = getRestaurant;
  // internal: shared by the api-real*.jsx files only; pages never read it
  R._ = { TZ_KEY: TZ_KEY, deviceTz: deviceTz, tzFor: tzFor, rememberTz: rememberTz, unusable: unusable, storeRaw: storeRaw, storeId: storeId };

  // ---- P1-03: menu, popular, table, discovery, branches, notifications ----------------------------------------
  var BULK_MAX = 50;        // getRestaurantBulk validates ids: required|array|min:1|max:50, each a string (else 422)
  var SEARCH_MIN = 2;       // B11 (onlineStoreListingController): mb_strlen of the name without % and _

  function keyOf(hotel) { return String(hotel == null ? '' : hotel); }
  function isObject(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }

  // The Error OO.http throws for this status and JSON body, for a request this adapter knows the API would refuse.
  function apiError(status, data) {
    var err = new Error(data.message || data.error || 'HTTP ' + status);
    err.status = status;
    err.data = data;
    err.kind = OO.http.kindOf(status);
    return err;
  }

  // A read whose 2xx body this adapter checks with usable(body). A body that fails rejects with unusable(path, body)
  // and marks the key suspect: OO.http may have cached it (it passed isSuccess), so the next read goes out fresh.
  var suspect = Object.create(null);
  function checkedRead(path, key, fresh, send, usable, decode) {
    var id = path + ' ' + key;
    return send(!!fresh || !!suspect[id]).then(function (body) {
      if (decode) {
        try { body = decode(body); } catch (error) { suspect[id] = true; throw error; }
      }
      if (!usable(body)) { suspect[id] = true; throw unusable(path, body); }
      delete suspect[id];
      return body;
    });
  }

  function isMenu(body) { return isObject(body) && OO.http.isSuccess(body) && Array.isArray(body.data); }
  function compactMenuEnabled() { return !!(OO.boot && OO.boot.capabilities && OO.boot.capabilities.compactMenuV1 === true); }
  function separateRedemptions() {
    return !!(OO.boot && OO.boot.capabilities && OO.boot.capabilities.redemptionCatalogueV1 === true)
      && !OO.landingMode && (OO.mode === 'online' || OO.mode === 'app') && !(OO.orderingQr && OO.orderingQr.active());
  }
  // Expand before any normaliser, cart or pricing code sees the response. Repeated arrays get independent copies,
  // preserving the old response's ownership as well as every ID, value and array order.
  function expandMenu(body) {
    if (!isObject(body) || !Object.prototype.hasOwnProperty.call(body, 'menu_format')) return body;
    if (body.menu_format !== 'compact-v1' || !isObject(body.menu) || !Array.isArray(body.arrays) || body.arrays.length > 8192) {
      throw unusable('get_menu', null);
    }
    var fields = { group: true, groups: true, groupstoproduct: true, sizes: true };
    function copy(value, depth, references) {
      if (depth > 48) throw unusable('get_menu', null);
      if (Array.isArray(value)) return value.map(function (v) { return copy(v, depth + 1, references); });
      if (!isObject(value)) return value;
      var out = {};
      Object.keys(value).forEach(function (key) {
        var child = value[key];
        if (references && Object.prototype.hasOwnProperty.call(fields, key) && isObject(child)
            && Object.prototype.hasOwnProperty.call(child, '$menuArray')) {
          var index = child.$menuArray;
          if (Object.keys(child).length !== 1 || !Number.isInteger(index) || index < 0 || index >= body.arrays.length
              || !Array.isArray(body.arrays[index])) throw unusable('get_menu', null);
          child = copy(body.arrays[index], depth + 1, false);
        } else child = copy(child, depth + 1, references);
        Object.defineProperty(out, key, { value: child, enumerable: true, writable: true, configurable: true });
      });
      return out;
    }
    return copy(body.menu, 0, true);
  }
  // P4-02: the table menu (see getMenu in the header).
  function isTableMenu(body) { return isMenu(body) && String(body.menu_type || '').trim().toLowerCase() === 'table menu'; }
  // QR-OFF: "this store takes no table orders" (see getMenu in the header), and its rejection.
  function isTableOff(body) {
    var r = isObject(body) ? body.reason : null;
    return String(r === undefined || r === null ? '' : r).trim().toLowerCase() === 'table_ordering_off';
  }
  function tableOffError(body) {
    var text = [body.message, body.error].filter(function (t) { return typeof t === 'string' && t; })[0];
    var err = new Error(text || 'Table ordering is not available.');
    err.status = 200;
    err.kind = 'tableOff';
    err.data = body;
    return err;
  }
  function delay(ms) {
    return new Promise(function (resolve) {
      if (ms > 0 && typeof setTimeout === 'function') setTimeout(resolve, ms); else resolve();
    });
  }

  function getMenu(hotel, opts) {
    var key = keyOf(hotel);
    var given = opts && opts.tableId !== undefined && opts.tableId !== null && opts.tableId !== '';
    if (given) return tableMenu(key, opts.tableId, !!opts.fresh);
    if (OO.orderingQr && OO.orderingQr.active()) {
      return OO.http.post('get_menu', OO.orderingQr.api({ id: key, timeZone: tzFor(key) }), { fresh: true }).then(function (body) {
        if (!isMenu(body) || !body.ordering_qr) throw unusable('get_menu', body);
        return body;
      });
    }
    var rewards = !(separateRedemptions() && opts && opts.withRedemptions === false);
    return checkedRead('get_menu', key + (rewards ? '' : ' browse'), opts && opts.fresh, function (fresh) {
      var body = { id: key, timeZone: tzFor(key), include_redemption_products: rewards };
      if (compactMenuEnabled()) body.menu_format = 'compact-v1';
      return OO.http.post('get_menu', body, { cache: true, fresh: fresh });
    }, isMenu, expandMenu);
  }

  function getRedemptionCatalogue(hotel, opts) {
    var key = keyOf(hotel);
    if (!separateRedemptions()) return getMenu(key, opts);
    return checkedRead('get_redemption_catalogue', key, opts && opts.fresh, function (fresh) {
      var body = { id: key, timeZone: tzFor(key) };
      if (compactMenuEnabled()) body.menu_format = 'compact-v1';
      return OO.http.post('get_redemption_catalogue', body, { cache: true, fresh: fresh });
    }, function (body) {
      return isObject(body) && OO.http.isSuccess(body) && Array.isArray(body.redemption_products) && Array.isArray(body.redemption_coupon_ids);
    }, expandMenu);
  }
  function tableMenu(key, tableId, fresh) {
    var n = typeof tableId === 'number' ? tableId : /^\s*\d+\s*$/.test(String(tableId)) ? Number(tableId) : NaN;
    if (!(n > 0 && Math.floor(n) === n)) {
      return Promise.reject(apiError(422, { message: 'The table id field must be an integer.', errors: { table_id: ['The table id field must be an integer.'] } }));
    }
    var waits = (R._ && Array.isArray(R._.tableMenuRetryMs) ? R._.tableMenuRetryMs : []).slice();
    function read(f) {
      return checkedRead('get_menu', key + ' table ' + n, f, function (ff) {
        return OO.http.post('get_menu', { id: key, timeZone: tzFor(key), table_id: n, include_redemption_products: true }, { cache: true, fresh: ff, headers: { 'With-Table-Id': 'true' } });
      }, function (body) { return isTableMenu(body) || isTableOff(body); }).then(function (body) {
        if (isTableOff(body)) throw tableOffError(body); // QR-OFF: no menu, and nothing to read again (attempt below)
        return body;
      });
    }
    function attempt(f) {
      return read(f).then(null, function (err) {
        // Only "a usable menu, but not the table's" is worth reading again (the cold-cache answer); the rest fails now.
        var body = err && err.status === 502 && err.kind === 'unavailable' ? err.data : null;
        if (!waits.length || !isMenu(body) || isTableMenu(body)) throw err;
        return delay(waits.shift()).then(function () { return attempt(true); });
      });
    }
    return attempt(fresh);
  }

  function emptyPopular() { return { status: 'success', data: { products: [], combos: [] } }; }

  function getPopular(hotel) {
    if (OO.orderingQr && OO.orderingQr.active()) return Promise.resolve(emptyPopular());
    return OO.http.get('popular-dishes/' + encodeURIComponent(keyOf(hotel))).then(function (body) {
      return isObject(body) && OO.http.isSuccess(body) && isObject(body.data) ? body : emptyPopular();
    }, emptyPopular);
  }

  function getTable(hotel, tableId) {
    if (tableId === undefined || tableId === null || tableId === '') {
      return Promise.reject(apiError(400, { Status: false, error: 'Table ID Not Given!' }));
    }
    return OO.http.get('tables/gettableDetails', { query: { tabId: String(tableId) } });
  }

  // Characters as the server counts them: code points, without the LIKE wildcards.
  function searchLength(q) { return q.replace(/[%_]/g, '').replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '.').length; }

  function searchStores(query) {
    var q = String(query == null ? '' : query).trim();
    if (searchLength(q) < SEARCH_MIN) return Promise.resolve({ status: true, data: [] });
    return OO.http.get('get_online_store_list_using_name_search', { query: { name: q } });
  }

  // A number or a numeric string; anything else (null, '', ' ', a boolean, an object) is NaN.
  function coord(v) {
    if (typeof v === 'number') return v;
    return typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  }

  function nearbyStores(opts) {
    opts = opts || {};
    var lat = coord(opts.lat), lng = coord(opts.lng), radius = coord(opts.radius);
    if (!isFinite(lat) || !isFinite(lng)) {
      return Promise.reject(apiError(400, { status: false, error: 'latitude and longitude are required' }));
    }
    var query = { latitude: lat, longitude: lng, radius: isFinite(radius) && radius > 0 ? radius : null };
    return OO.http.get('get_online_store_listing', { query: query });
  }

  // The related_branches online keys: strings, unique, in API order, at most BULK_MAX.
  function branchKeys(body) {
    var keys = [];
    (Array.isArray(body.related_branches) ? body.related_branches : []).forEach(function (b) {
      var k = b && b.online_key != null ? String(b.online_key) : '';
      if (k && keys.length < BULK_MAX && keys.indexOf(k) < 0) keys.push(k);
    });
    return keys;
  }

  function getBranches(hotel) {
    var key = keyOf(hotel);
    return getRestaurant(key).then(function (body) {
      var ids = branchKeys(body);
      if (!ids.length) {
        var own = {};
        own[key] = body;
        return own;
      }
      return checkedRead('getRestaurantBulk', key, false, function (fresh) {
        return OO.http.post('getRestaurantBulk', { ids: ids, timeZone: tzFor(key), metadata_format: 'compact-v1' }, { cache: true, fresh: fresh });
      }, isObject);
    });
  }

  // ---- P3-24: the chain (B31, with the interim E45 + E35) ----------------------------------------------------------
  var CHAIN_TTL_MS = 60000; // B31 caches a chain 60 s per master and judges open-now per request

  function isChainBody(body) {
    return isObject(body) && OO.http.isSuccess(body) && isObject(body.data) && isObject(body.data.master) && Array.isArray(body.data.stores);
  }

  // The interim chain's member keys: the list rows' online keys (strings, unique), the requested key first when listed,
  // at most BULK_MAX.
  function chainKeys(list, first) {
    var keys = [];
    (isObject(list) && Array.isArray(list.data) ? list.data : []).forEach(function (r) {
      var k = r && r.online_key != null ? String(r.online_key).trim() : '';
      if (k && keys.indexOf(k) < 0) keys.push(k);
    });
    var at = keys.indexOf(first);
    if (at > 0) keys.unshift(keys.splice(at, 1)[0]);
    return keys.slice(0, BULK_MAX);
  }

  function interimChain(key) {
    return getRestaurant(key).then(function (restaurant) {
      var d = storeRowOf(restaurant);
      var id = Number(d.id || d.hotelId);
      if (!(id > 0)) throw unusable('getRestaurant', restaurant);
      return OO.http.get('get_chained_shop_list', { query: { id: String(id) }, cache: CHAIN_TTL_MS }).then(function (list) {
        var ids = chainKeys(list, String(d.online_key || key));
        var bulk = ids.length
          ? OO.http.post('getRestaurantBulk', { ids: ids, timeZone: tzFor(key), metadata_format: 'compact-v1' }, { cache: true })
          : Promise.resolve({});
        return bulk.then(function (b) {
          return { status: true, interim: true, online_key: key, restaurant: restaurant, list: list, bulk: isObject(b) ? b : {} };
        });
      });
    });
  }

  function getChain(hotel) {
    var key = keyOf(hotel);
    return OO.http.get('online_order/chain/' + encodeURIComponent(key), { cache: CHAIN_TTL_MS }).then(function (body) {
      return isChainBody(body) ? body : interimChain(key);
    }, function (err) {
      if (err && (err.status === 404 || err.kind === 'network')) return interimChain(key);
      throw err;
    });
  }

  function getNotifications(hotel) { return Promise.resolve({ status: true, data: [] }); }

  R.getChain = getChain;
  R.getMenu = getMenu;
  R.getRedemptionCatalogue = getRedemptionCatalogue;
  R._.expandMenu = expandMenu;
  // P4-02: the pauses before re-reading a table menu that came back without menu_type 'Table Menu' (see getMenu); tests
  // replace it.
  R._.tableMenuRetryMs = [400, 1200];
  R.getPopular = getPopular;
  R.getTable = getTable;
  R.searchStores = searchStores;
  R.nearbyStores = nearbyStores;
  R.getBranches = getBranches;
  R.getNotifications = getNotifications;
})();


// ===================== src/data/api-real-auth.jsx =====================
// src/data/api-real-auth.jsx: the real adapter's auth and account calls (P1-04): email OTP, register, customer, wallet,
// loyalty, transactions (BATCH2-PLAN §5 E20–E27; captures in tests/contract/staging/**), and the profile update (P3-09:
// B16, with E24's full-record customer/create as its UID_MISMATCH fallback). Its methods attach to
// OO.data.realApi (created by api-real.jsx) and keep the names, signatures and resolved shapes of their fixture twins in
// store-api.jsx. Transport is OO.http v2 only. Ratings (E28–E32) are Phase 3, not here.
//
// Identity: getLoyalty / getWalletTransactions take opts.uid, getCustomer its uid argument; without one they use the
// OO.store session's customer uid. A real store has no demo member: with no uid at all nothing is sent.
// isMobileApp (the login source the backend records, app / web) is OO.platform.isMobileApp(): app mode or the shell.
//
//   sendOtp(hotel, email) → Promise<{message}>   POST send-otp {email (trimmed, lower-case), store_id (number,
//       _.storeId), isMobileApp} (E20). A 422 rejects with its first errors.* message (data stays the raw body); a 429
//       (the per-email OTP limiter) rejects kind 'unavailable', 'Too many attempts. Wait a minute and try again.'
//   verifyOtp(hotel, email, code) → Promise<body + uid>   POST verify-otp {email, otp (a string, trimmed), store_id,
//       isMobileApp} (E21). 200 {message, customer, customToken}: an existing customer · 201 {message, customer: null,
//       customToken}: a new email (the page calls register with the uid) · 400 {error} rejects with that message ·
//       422 / 429 as sendOtp. Resolves the raw body plus `uid` (string | null): customer.uid, else the `uid` claim of
//       the customToken (or custom_token) JWT. Any uid format is accepted (temp_…, Firebase): it is the backend's own.
//   register(hotel, body) → Promise<{status: true, data: CustomerData}>   POST customer/register {uid, name, email
//       (trimmed, lower-case), hotelKey: hotel, phone_no (≤ 15 characters, else ''), address (a string, ≤ 255; omitted
//       when empty), birthday (Y-m-d | null), isMobileApp} (E22). body = {uid, name, email, phone_no, address?,
//       birthday}. A 400 {status: false, error: 'Store Data Not Found!'} rejects; 422 as sendOtp.
//   getCustomer(hotel, uid) → Promise<{status, data: CustomerData, customerCode, customerId, membershipTypeName,
//       membershipTypeId, customerWalletPoints}>   GET customer/getdata?uid= (E23; not store-scoped). An unknown uid
//       is B07's 404 (nginx's too, by status alone): it rejects {kind: 'notFound', status: 404, data: null, message:
//       'Customer not found.'}. No uid: that, with no request. A 5xx is never "not found" (the session reconcile signs
//       a customer out on notFound only): it keeps OO.http's status and kind 'unavailable', with its body dropped (a
//       debug body carries PHP internals) and the message ACCOUNT_UNAVAILABLE. A host without B07 answers an unknown
//       uid with a 500, so there the session is kept. Other failures stay OO.http's.
//   getLoyalty(hotel, opts) → Promise<{wallet, products, catalogue: null}>   In SEQUENCE (C §3.2: the wallet call
//       creates the chain's customers row that v4 needs): POST get_customer_wallet_points {uid, online_key: hotel}
//       (E25), then, once it settles, POST get_user_loyalty_products_v4 {uid, online_key} (E26). Both soft: a failure
//       (403 = the loyalty module is off, 400 = unknown customer or store, network) is a null part; it never rejects.
//       catalogue is null (the catalogue endpoint, B19, doesn't exist yet). opts = {uid?, fresh?}.
//   getWallet(hotel, opts) → Promise<wallet | null>   (P2-02) POST get_customer_wallet_points {uid, online_key} (E25)
//       alone: getLoyalty's first step (the same cached, soft read) without get_user_loyalty_products_v4. The store's
//       member read (OO.data.loadWallet). opts = {uid?, fresh?}; no uid, or a failed read → null.
//   getWalletTransactions(hotel, opts) → Promise<{status, data: Row[]} | null>   GET cust_wallet_points_transactions
//       ?customer_id= the WALLET's customer_id (the store-scoped cust_… of get_customer_wallet_points, the same cached
//       read as getLoyalty) (E27), soft (a failure resolves null). No uid, or no wallet customer_id (an unknown
//       customer, a failed wallet read) → {status: true, data: []} (the server's own answer for an unknown customer),
//       with no transactions request. opts = {uid?, fresh?}.
//   The wallet read is cached for WALLET_TTL_MS (60 s; deduped while in flight), so one screen's getLoyalty and
//   getWalletTransactions share one request while a balance never goes stale for long; opts.fresh re-reads it (the
//   checkout pre-flight). Any successful write flushes it (OO.http, R-API-3).
//   updateProfile(hotel, fields, opts) → Promise<{status: true, data: CustomerData}>   (P3-09) B16, the safe partial
//       profile update (briefs/p3-be-contracts.md): POST customer/profile {uid, online_key: hotel, name?, phone_no?,
//       birthday?, address?} with the header Authorization: Bearer <customToken> (verify-otp's custom token). Only the
//       keys `fields` has are sent, and the server leaves every other field alone: name (trimmed), phone_no (trimmed;
//       null or '' → null, which clears it), birthday (its Y-m-d; null or '' → null, which clears it; anything else
//       goes as typed, for the server's 422), address (the WHOLE address book: each entry with REF's keys only, uid,
//       type, address, city, zipcode, selected, geoLoc; null or [] clears it). Never email (the server ignores it).
//       opts = {uid?, token?}: by default the OO.store session's customer uid and customToken.
//       No uid or no token → rejects 401 TOKEN_REQUIRED with no request (the server's answer to a missing token).
//       403 STORE_NOT_LINKED → get_customer_wallet_points {uid, online_key} read FRESH (a cached answer sends nothing,
//       and the request is what creates this store's customer record), then the update is sent ONCE more; another
//       STORE_NOT_LINKED rejects (a store with the loyalty module off answers the wallet read 403, so it never links).
//       403 UID_MISMATCH (the token was minted for another uid than the profile's: a temp_… uid) → the full-record
//       fallback, E24: GET customer/getdata?uid= (fresh), then POST customer/create (a FULL replace) with EVERY field
//       as read (uid, name, phone_no, email, birthday, address) and only the edited ones replaced, so nothing is wiped:
//       an address book that wasn't edited goes back exactly as read (a customer/register string stays a string), an
//       edited empty one is "" (REF's), a cleared phone_no is ''. A failed read, or a record whose uid isn't
//       byte-identical to the session's (the column compares case-insensitively), rejects with no write.
//       Any 404 (STORE_NOT_FOUND, CUSTOMER_NOT_FOUND: classified by status alone) rejects; this route never creates.
//       Rejections keep OO.http's {status, kind, data, message} and add `code` (the B16 `error` string, e.g.
//       'TOKEN_INVALID'; null for a 404 and for a body without one) and `fields` (a 422's errors {field: [message]},
//       keys like 'address.0.address'; else null). A 429's message is the `customer-profile` limiter's own. A 2xx
//       without a customer `data` rejects {status: 502, kind: 'unavailable'}. A successful save flushes the read cache
//       (OO.http, R-API-3), so the next customer/getdata and wallet reads are fresh.
//
// Helpers for the pages:
//   OO.data.uidFromToken(token) → string | null   the `uid` claim of a JWT (header.payload.signature; the payload is
//       base64url JSON). No signature check: it is the server's own token handed back. null on anything malformed.
//   OO.data.signupUid(hotel, verifyResult) → string | null   the uid a new customer registers with: verifyResult.uid,
//       else its token's claim; null without either. Never invented, for any store: the fixture adapter's tokens
//       are JWT-shaped too (P1-17). hotel is unused (kept for the callers).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};
  var R = (OO.data.realApi = OO.data.realApi || {});

  var WALLET_TTL_MS = 60000;
  var TOO_MANY = 'Too many attempts. Wait a minute and try again.';
  var ACCOUNT_UNAVAILABLE = "We couldn't load your account right now. Please try again.";
  var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  // The login source the backend records (CustomerStoreLinkService: app / web): P4-09's app bridge decides it.
  function isMobileApp() { return !!(OO.platform && OO.platform.isMobileApp()); }

  function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function str(v) { return v == null ? '' : String(v); }
  function text(v) { return str(v).trim(); }
  function emailOf(v) { return text(v).toLowerCase(); }
  function keyOf(hotel) { return str(hotel); }

  function sessionUid() {
    try { var s = OO.store && OO.store.session(); return s && s.customer && s.customer.uid ? String(s.customer.uid) : null; } catch (e) { return null; }
  }
  function uidOf(opts) { return (opts && opts.uid ? String(opts.uid) : null) || sessionUid(); }

  // The numeric store id the OTP endpoints take (getRestaurant data[0].id = hotelId).
  function storeIdOf(hotel) { return R._.storeId(hotel).then(function (id) { return Number(id) || null; }); }

  // ---- the custom-token JWT ------------------------------------------------------------------------------
  // base64url → its UTF-8 text, or null. Pure (no atob): the bytes are percent-encoded for decodeURIComponent, which
  // also rejects invalid UTF-8. Padding is optional.
  function base64urlText(seg) {
    var s = str(seg).replace(/-/g, '+').replace(/_/g, '/').replace(/=+$/, '');
    if (!s || s.length % 4 === 1 || /[^A-Za-z0-9+/]/.test(s)) return null;
    var acc = 0, bits = 0, pct = '';
    for (var i = 0; i < s.length; i++) {
      acc = ((acc << 6) | B64.indexOf(s.charAt(i))) & 0xffff;
      bits += 6;
      if (bits >= 8) {
        bits -= 8;
        var b = (acc >> bits) & 0xff;
        pct += (b < 16 ? '%0' : '%') + b.toString(16);
      }
    }
    try { return decodeURIComponent(pct); } catch (e) { return null; }
  }

  function uidFromToken(token) {
    if (typeof token !== 'string') return null;
    var parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    var json = base64urlText(parts[1]);
    var claims = null;
    try { claims = json == null ? null : JSON.parse(json); } catch (e) { claims = null; }
    var uid = isObj(claims) ? claims.uid : null;
    if (typeof uid === 'number' && isFinite(uid)) uid = String(uid);
    return typeof uid === 'string' && uid.trim() ? uid : null;
  }

  // Every store's verify-otp token is JWT-shaped (the fixture adapter's too, P1-17), so no uid is ever invented here.
  function signupUid(hotel, res) {
    var uid = (res && res.uid) || uidFromToken(res && (res.customToken || res.custom_token));
    return uid ? String(uid) : null;
  }

  // ---- errors --------------------------------------------------------------------------------------------
  // The first Laravel validation message of a 422 body ({message, errors: {field: [text, …]}}), or null.
  function firstFieldError(data) {
    var errs = isObj(data) ? data.errors : null;
    if (!isObj(errs)) return null;
    for (var k in errs) {
      if (!Object.prototype.hasOwnProperty.call(errs, k)) continue;
      var v = Array.isArray(errs[k]) ? errs[k][0] : errs[k];
      if (typeof v === 'string' && v.trim()) return v;
    }
    return null;
  }

  // send-otp / verify-otp / register rejections: a 422 carries its first field message, a 429 a fixed one. status,
  // kind and data stay OO.http's (the pages read data.errors).
  function authError(err) {
    if (err && err.status === 422) err.message = firstFieldError(err.data) || err.message;
    else if (err && err.status === 429) err.message = TOO_MANY;
    throw err;
  }

  // customer/getdata's unknown uid (a 404, B07's JSON or nginx's HTML; see the header).
  function customerNotFound() {
    var err = new Error('Customer not found.');
    err.status = 404;
    err.kind = 'notFound';
    err.data = null;
    return err;
  }

  // ---- auth (E20–E22) ------------------------------------------------------------------------------------
  function sendOtp(hotel, email) {
    return storeIdOf(hotel).then(function (storeId) {
      return OO.http.post('send-otp', { email: emailOf(email), store_id: storeId, isMobileApp: isMobileApp() })
        .then(null, authError);
    });
  }

  // verify-otp's body plus `uid`: the customer's (200), else the custom token's claim (201: customer null).
  function withUid(body) {
    var out = isObj(body) ? body : {};
    var c = isObj(out.customer) ? out.customer : null;
    out.uid = (c && c.uid ? String(c.uid) : null) || uidFromToken(out.customToken || out.custom_token);
    return out;
  }

  function verifyOtp(hotel, email, code) {
    return storeIdOf(hotel).then(function (storeId) {
      return OO.http.post('verify-otp', { email: emailOf(email), otp: text(code), store_id: storeId, isMobileApp: isMobileApp() })
        .then(withUid, authError);
    });
  }

  function birthdayOf(v) {
    var m = /^(\d{4}-\d{2}-\d{2})/.exec(text(v));
    return m ? m[1] : null;
  }

  function register(hotel, body) {
    body = body || {};
    var phone = text(body.phone_no);
    var address = typeof body.address === 'string' ? body.address.trim().slice(0, 255) : '';
    var req = { uid: str(body.uid), name: text(body.name), email: emailOf(body.email), hotelKey: keyOf(hotel), phone_no: phone.length <= 15 ? phone : '' };
    if (address) req.address = address;
    req.birthday = birthdayOf(body.birthday);
    req.isMobileApp = isMobileApp();
    return OO.http.post('customer/register', req).then(null, authError);
  }

  // ---- customer (E23) ------------------------------------------------------------------------------------
  function getCustomer(hotel, uid) {
    var u = uid ? String(uid) : sessionUid();
    if (!u) return Promise.reject(customerNotFound());
    return OO.http.get('customer/getdata', { query: { uid: u } }).then(null, function (err) {
      if (err && err.status === 404) throw customerNotFound();
      if (err && err.status >= 500) { // the server's trouble, not "no such customer" (see the header)
        err.data = null;
        err.message = ACCOUNT_UNAVAILABLE;
      }
      throw err;
    });
  }

  // ---- wallet, loyalty, transactions (E25–E27) -----------------------------------------------------------
  // The ONE wallet read both callers share: the same body (key order included) makes the same cache / dedupe key.
  function walletOf(hotel, uid, fresh) {
    return OO.http.post('get_customer_wallet_points', { uid: uid, online_key: keyOf(hotel) },
      { soft: true, cache: WALLET_TTL_MS, fresh: !!fresh });
  }

  function getLoyalty(hotel, opts) {
    var uid = uidOf(opts);
    var wallet = null;
    if (!uid) return Promise.resolve({ wallet: null, products: null, catalogue: null });
    return walletOf(hotel, uid, opts && opts.fresh).then(function (w) {
      wallet = w;
      return OO.http.post('get_user_loyalty_products_v4', { uid: uid, online_key: keyOf(hotel) }, { soft: true });
    }).then(function (products) {
      return { wallet: wallet, products: products, catalogue: null };
    }, function () {
      return { wallet: wallet, products: null, catalogue: null }; // soft reads only reject on a caller abort
    });
  }

  function getWallet(hotel, opts) {
    var uid = uidOf(opts);
    if (!uid) return Promise.resolve(null);
    return walletOf(hotel, uid, opts && opts.fresh).then(function (w) { return w === undefined ? null : w; });
  }

  function emptyTransactions() { return { status: true, data: [] }; }

  function getWalletTransactions(hotel, opts) {
    var uid = uidOf(opts);
    if (!uid) return Promise.resolve(emptyTransactions());
    return walletOf(hotel, uid, opts && opts.fresh).then(function (wallet) {
      var id = isObj(wallet) && OO.http.isSuccess(wallet) ? text(wallet.customer_id) : '';
      if (!id) return emptyTransactions();
      return OO.http.get('cust_wallet_points_transactions', { query: { customer_id: id }, soft: true });
    });
  }

  // ---- profile update (B16, P3-09; see the header) ----------------------------------------------------------
  var PROFILE_FIELDS = ['name', 'phone_no', 'birthday', 'address'];
  var ADDRESS_KEYS = ['uid', 'type', 'label', 'address', 'city', 'zipcode', 'selected', 'geoLoc'];
  var SAVE_FAILED = "We couldn't save your changes. Please try again.";
  var SIGN_IN_AGAIN = 'Please sign in again to update your profile.'; // the server's own TOKEN_* message

  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function sessionToken() {
    try {
      var s = OO.store && OO.store.session();
      return s && typeof s.customToken === 'string' && s.customToken.trim() ? s.customToken.trim() : null;
    } catch (e) { return null; }
  }
  function tokenOf(opts) {
    if (!opts || opts.token === undefined) return sessionToken();
    return typeof opts.token === 'string' && opts.token.trim() ? opts.token.trim() : null;
  }

  // The B16 body's editable part: only the keys `fields` has (see the header).
  function profileBody(fields) {
    var f = isObj(fields) ? fields : {};
    var out = {};
    if (own(f, 'name')) out.name = text(f.name);
    if (own(f, 'phone_no')) out.phone_no = text(f.phone_no) || null;
    if (own(f, 'birthday')) out.birthday = text(f.birthday) ? birthdayOf(f.birthday) || text(f.birthday) : null;
    if (own(f, 'address')) {
      var book = Array.isArray(f.address) ? f.address.filter(isObj).map(function (a) {
        var entry = {};
        ADDRESS_KEYS.forEach(function (k) { if (own(a, k)) entry[k] = a[k]; });
        return entry;
      }) : [];
      out.address = book.length ? book : null;
    }
    return out;
  }

  // Rejections: OO.http's error plus code and fields (see the header). A 404 is classified by its status alone.
  function codeOf(err) {
    return err && Number(err.status) !== 404 && isObj(err.data) && typeof err.data.error === 'string' && err.data.error
      ? err.data.error : null;
  }
  function profileError(err) {
    var e = err instanceof Error ? err : new Error(SAVE_FAILED);
    e.code = codeOf(e);
    e.fields = Number(e.status) === 422 && isObj(e.data) && isObj(e.data.errors) ? e.data.errors : null;
    return e;
  }
  function profileFailure(status, kind, code, message) {
    var e = new Error(message);
    e.status = status;
    e.kind = kind;
    e.data = code ? { status: false, error: code, message: message } : null;
    e.code = code;
    e.fields = null;
    return e;
  }
  // A saved profile: {status: true, data: CustomerData}. Anything else from a 2xx is unusable (a 502).
  function savedBody(body) {
    if (isObj(body) && OO.http.isSuccess(body) && isObj(body.data)) return body;
    throw profileFailure(502, 'unavailable', null, SAVE_FAILED);
  }

  // UID_MISMATCH's full-record fallback (E24, see the header): read the profile fresh, then customer/create it whole.
  function fullRecord(uid, body) {
    return OO.http.get('customer/getdata', { query: { uid: uid }, fresh: true }).then(function (raw) {
      var d = isObj(raw) && isObj(raw.data) ? raw.data : null;
      var readUid = d && (typeof d.uid === 'string' || typeof d.uid === 'number') ? String(d.uid) : null;
      if (readUid !== uid) throw profileFailure(502, 'unavailable', null, SAVE_FAILED); // not this uid, byte for byte
      var rec = {
        uid: uid,
        name: own(body, 'name') ? body.name : d.name,
        phone_no: own(body, 'phone_no') ? (body.phone_no === null ? '' : body.phone_no) : d.phone_no,
        email: d.email,
        birthday: own(body, 'birthday') ? body.birthday : d.birthday,
        address: own(body, 'address') ? (body.address === null ? '' : body.address) : d.address,
      };
      return OO.http.post('customer/create', rec);
    }).then(savedBody, function (err) { throw profileError(err); });
  }

  function updateProfile(hotel, fields, opts) {
    var uid = uidOf(opts);
    var token = tokenOf(opts);
    var body = profileBody(fields);
    if (!uid || !token) return Promise.reject(profileFailure(401, 'client', 'TOKEN_REQUIRED', SIGN_IN_AGAIN));
    function send() {
      return OO.http.post('customer/profile', Object.assign({ uid: uid, online_key: keyOf(hotel) }, body),
        { headers: { Authorization: 'Bearer ' + token } });
    }
    function after(err) {
      if (err && Number(err.status) === 403 && codeOf(err) === 'UID_MISMATCH') return fullRecord(uid, body);
      throw profileError(err);
    }
    return send().then(savedBody, function (err) {
      if (!(err && Number(err.status) === 403 && codeOf(err) === 'STORE_NOT_LINKED')) return after(err);
      // The wallet read links the customer to this store (soft: it never rejects); then once more, and only once.
      return walletOf(hotel, uid, true).then(send).then(savedBody, after);
    });
  }

  R.sendOtp = sendOtp;
  R.verifyOtp = verifyOtp;
  R.register = register;
  R.getCustomer = getCustomer;
  R.getLoyalty = getLoyalty;
  R.getWallet = getWallet;
  R.getWalletTransactions = getWalletTransactions;
  R.updateProfile = updateProfile;
  OO.data.uidFromToken = uidFromToken;
  OO.data.signupUid = signupUid;
})();


// ===================== src/data/api-real-orders.jsx =====================
// src/data/api-real-orders.jsx: the real adapter's order calls (P1-05): coupons v4, checkout, payment, status,
// orders, tracking. Its methods attach to OO.data.realApi (created by api-real.jsx) and keep the name, signature and
// resolved shape of their fixture twins in store-api.jsx: the endpoint's REAL raw body (BATCH2-PLAN §5 E09–E18;
// tests/contract/**), except where noted below. Transport is OO.http v2 only, and no request or response body is
// ever logged (they carry emails, phones and order data). Suggestions came with P2-19; the order's rating and
// rate-order with P3-06; the store and product ratings with P3-07.
//
//   getCoupons(storeIdOrHotel, { fresh }) → Promise<raw>   GET coupon_get_v4?user_id=<store id> (E09), cached (OO.http
//       TTL; opts.fresh re-reads it: the giveaway picker's REF forceFresh, P2-13).
//       A NUMBER is the store id (getRestaurant data[0].id); a STRING is always an online_key (193 stores have
//       numeric keys), resolved with _.storeId. Resolves the body as served: { status, data:[row], user_id,
//       user_name } or a bare array of rows; an unknown store id is a 200 with data: []. The list includes expired
//       but exposed coupons (OO.model.normalizeCoupons drops them).
//   validateCoupon(hotel, { code, email, time, domain }) → Promise<raw>   POST validate_coupon_v4 (E10), never
//       cached: { customer_email (all whitespace stripped, as the checkout payload sends it, so the claim matches),
//       code (trimmed, UPPER; left out when empty), store_id (int), time (ISO: opts.time, else now) }, plus
//       { is_table_side: true, price_type: 'table_side' } when domain is TABLE_SIDE.
//       Resolves 200 { status: true, data: coupon }. A refusal is HTTP 200 + status:false ('Invalid coupon',
//       'Coupon limit reached', …): it REJECTS an Error { status: 200, kind: 'client', data: body, message }, where
//       message is the server's text when it is short and plain, else COUPON_REFUSED. A 400 (no code) rejects with
//       OO.http's error (kind 'client'); a 2xx that isn't a JSON object rejects _.unusable (kind 'unavailable').
//   getPricetags(storeIdOrHotel, { fresh }) → Promise<{ order: raw | null, prices: raw | null }>   (new, P2-01)
//       GET get_pricetag_order?hotelId=<store id> (E05) and GET get_pricetag_prices?hotelId= (E06), in parallel, both
//       SOFT (a failure is a null part; it never rejects) and cached (OO.http TTL; opts.fresh re-reads them: the
//       pre-flight, a pricetag_refresh_required). The store id as getCoupons'; one it can't resolve is { order: null,
//       prices: null } with no request. OO.model.normalizePricetags(order, prices), then OO.pricetags.select.
//   invalidateCoupon(txn) → Promise<raw>   POST invalidate_coupon { transaction_id: txn } (E11): txn is checkout's
//       base64 coupon_txn. Only the claim of a PROCESSING + UNPAID order is released; 400 / 404 reject (OO.http).
//   placeOrder(hotel, payload) → Promise<raw>   POST checkout (E12), withStatus; the payload goes as given. In a
//       white-label studio preview (OO.whitelabel.isPreview(), WL-2) it rejects { kind: 'payload', code: 'PREVIEW' }
//       with no request.
//       Success = a 2xx whose status is true or 'true': resolves the body { status: 'true', order_id, data,
//       coupon_txn[, payment_id, payment_url] }. Use the RESPONSE order_id (the server overwrites the payload's).
//       Anything else rejects an Error { status (HTTP), kind, data (the raw body), ecode (Number | null), message }
//       plus whichever refresh flags the body carries (REFRESH_FLAGS). message and kind come from CHECKOUT_ERRORS;
//       no server text ever reaches the customer. A network failure rejects OO.http's error unchanged (kind
//       'network', status 0): the order MAY exist, so never resubmit blindly.
//   paymentUpdate(orderId) → Promise<{ httpStatus, body }>   POST payment_update { order_id } (E13), withStatus.
//       Resolves for EVERY HTTP status: 200 paid · 202 already ACCEPTED / REJECTED / PAID · 206 in progress ·
//       409 failed · 404 · 422 · 5xx; the callback page decides (P1-15). A prod 404 body is not the controller's
//       JSON. Only a network failure rejects (OO.http, kind 'network').
//   orderStatus(orderId) → Promise<raw>   POST online_order_status { order_id } (E14), never cached: { orderid,
//       orderstatus, response, deliver_time, delivery_type, updated_time, ticket_created, ticket_state,
//       fulfillment_type, status: true }. An unknown order is a 400 { orderstatus: 'NO_DATA' }: OO.http rejects
//       kind 'client'.
//   getOrders(hotel, { uid, page }) → Promise<raw>   POST get_customer_orders?page=N { uid, online_key: hotel }
//       (E16): the bare paginator (per_page 1000, no status key). uid = opts.uid, else the session's; page = opts.page,
//       else 1. An unknown uid is a 400 (rejects). A 200 with an EMPTY body (no customers row in the store's chain)
//       resolves emptyPage(page).
//   getOrder(hotel, ticketId) → Promise<raw>   GET get_order_data?ticket_id= (E15): { status: true, data: Ticket }.
//       It is a 404 until the POS creates the ticket: OO.http's kind 'notFound' (by status; prod 404s are HTML).
//   getAllOrders(hotel, { uid, page }) → Promise<raw>   (new) GET customer/getOrders?cusId&hotelKey&page (E17):
//       { status: true, data: paginator (per_page 10) } of online_orders rows, unpaid and pending included. orderData
//       may be a JSON string (the normaliser's job). uid as getOrders; without one it is a 400 (rejects).
//   getTrackingLink(orderId) → Promise<{ url: string | null }>   (new) GET get_delivery_tracking_link?order_id=
//       (E18): data.tracking_url (B06), else the pre-B06 data.original.tracking_url, else a root tracking_url. Only
//       an http(s) URL is returned. A 400 (no delivery provider on the order) or a 404 (no link yet) resolves
//       { url: null }; any other failure rejects (OO.http).
//   getProductSuggestions(hotel, itemId) → Promise<raw>   (new, P2-19) GET product_suggestion_list_online?online_key=
//       <hotel>&id=<itemId> (E07), cached (OO.http TTL) and SOFT: it never rejects. The body as served ({ status: true,
//       data: [row] }, rows with itemId, category_id, … and groups carrying max_no_price_count); any failure (the
//       unknown key's 404, an nginx page; a network error or the deadline; a 2xx that isn't a JSON object) resolves the
//       empty body { status: true, data: [] }, a new object per call. No hotel or no itemId: that empty body, with no
//       request. OO.upsell (src/pages/upsell.jsx) reads the ids and maps the rows onto menu products by id.
//   getCartSuggestions(hotel) → Promise<raw>   (new, P2-19) GET cart_suggestion_list_online?online_key=<hotel> (E08),
//       the same rules.
//   getOrderRating(hotel, orderId, { storeId, fresh }) → Promise<raw>   (new, P3-06) GET customer/order-rating/<store
//       id>/<orderId> (E30): { status: true, data: null } (not rated) or { status: true, data: { rating, feedback,
//       items: [{ product_id, product_name, rating, feedback }] } } (OO.model.normalizeOrderRating). The store id is
//       opts.storeId, the TICKET's store (its get_customer_orders row's user_id: a chain branch can differ from the
//       route's store), else the route store's (_.storeId). The body holds what the customer wrote: call it for the
//       customer's own order only. Cached (OO.http TTL; rating an order is a write, which flushes the read cache);
//       opts.fresh re-reads it. Rejects OO.http's error, e.g. the 403 { status: false, error:
//       'MODULE_SUBSCRIPTION_DISABLED' } of a store whose review-feedback module is off (kind 'client'). No order id,
//       or no store id: rejects _.unusable without a request.
//   rateOrder(hotel, { orderId, storeId, rating, feedback, items, uid }) → Promise<{ status: true, already, message }>
//       (new, P3-06) POST customer/rate-order (E31), withStatus, with { customer_id (opts.uid, else the session's; never
//       a demo fx_ uid), order_id (a string), store_id (a whole number: the ticket's store, as getOrderRating's),
//       rating (a whole 1–5), feedback (trimmed, at most 1000 characters; '' for none), items? }. items = [{ product_id,
//       rating?, feedback? }] from opts.items' { productId, rating, feedback }: rows without a whole product id are
//       dropped, a rating outside 1–5 and an empty feedback are left out (the backend gives such an item the order's
//       rating); the key is left out when no row remains. Resolves { status: true, already: false, message } for a 2xx
//       whose status isn't false, and { status: true, already: true, message } for the backend's 409 'Order already
//       rated' (or a 4xx body saying "already rated"): REF counts both as sent. Anything else rejects an Error
//       { status, kind, data, message } whose message is RATE_ERRORS' copy for the status (never the server's own
//       text); a network failure rejects OO.http's error (kind 'network', status 0: the rating may have been saved,
//       so re-read getOrderRating before sending it again). No uid, no order id or no store id: rejects { status: 0,
//       kind: 'client', data: null } without a request.
//   getStoreRating(storeIdOrHotel) → Promise<raw>   (new, P3-07) GET customer/store-rating/<store id> (E28): { status:
//       true, data: { average_rating (null with no reviews), total_reviews } } (OO.model.normalizeRatingSummary). A
//       NUMBER is the store id, a STRING the online_key (getCoupons' rule). Cached (OO.http TTL). Rejects OO.http's
//       error, e.g. the 403 { error: 'MODULE_SUBSCRIPTION_DISABLED' } of a store whose review-feedback module is off
//       (kind 'client': "ratings are off"). A store id it can't resolve rejects _.unusable without a request.
//   getProductRating(hotel, productId) → Promise<raw>   (new, P3-07) GET customer/product-rating/<store id>/<productId>
//       (E29): { status: true, data: { average_rating, total_reviews } }, the store's average of that product's item
//       ratings. The store id is the route store's (_.storeId). Cached; a failure isn't (REF: "null is not cached").
//       Rejects like getStoreRating. A productId that isn't a whole number > 0 rejects _.unusable without a request
//       (combos have no product rating; REF's quirk of asking with a combo id isn't copied).
//   TODO(P4): getCoupons sends is_table_order=true in qr mode (table prices).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};
  var R = (OO.data.realApi = OO.data.realApi || {});

  // ---- checkout errors (E12; inventory B §4.3, A §4; P0-AUDIT §7): the ONE map ------------------------------------
  // The first matching row wins. Status rows come first: a 404 is classified by its status alone (a production 404
  // body is nginx HTML, or the SPA vhost's misleading 405 JSON, so neither its ecode nor its text is read), and a 429
  // (the online-checkout limiter, or a route throttle) is a retry whatever its body. ecode is compared as a number
  // (the validator sends '700' and '800' as strings). A row's kind defaults to OO.http.kindOf(status) ('client' for a
  // 400, 'notFound' for a 404, 'unavailable' for a 5xx), and a 2xx failure is 'unavailable'.
  //   http       the HTTP status
  //   frequent   a 400 whose status is the string 'frequent order try again later' (the 5 s repeat-order guard)
  //   ecodes     body.ecode
  //   validator  only the validator's envelope { status: 'Apologies…' (a string), ecode, error, message }: its 800
  //              is the coupon check; every other 800 (status: false) is the store refusing the order
  //   delivery   the message for a delivery order (payload.order_type …DELIVERY), when it differs
  // No row: the outdated-version 400 ({ status: 'You are using an outdated version…' }: an online order without
  // payment_gateway), 'Request Authentication Failed', a 5xx, an unknown ecode → GENERIC.
  var GENERIC = "We couldn't place your order. Please try again.";
  var CHECKOUT_ERRORS = [
    { http: 429, kind: 'retry', message: 'Too many attempts. Wait a minute and try again.' },
    { http: 404, message: "This store can't take online orders right now. Please try again later." },
    { frequent: true, kind: 'retry', message: "We're still placing your last order. Wait a few seconds and try again." },
    { ecodes: [901, 902], kind: 'cart', message: 'Some items in your cart have changed. Please review your cart.' },
    { ecodes: [100, 101, 102, 200, 300], kind: 'cart', message: 'Prices have changed. Please review your cart.' },
    {
      ecodes: [103], message: 'That pickup time is no longer available. Choose another time.',
      delivery: 'That delivery time is no longer available. Choose another time.',
    },
    { ecodes: [201], message: "We couldn't confirm delivery to this address. Check your address and try again." },
    // 202: the B10 zone guard (DeliveryZoneGuard). Its body names the outcome in invalid_type (G1 fix G; an older backend
    // host only in its text), so the checkout page gives each outcome its own copy (OO.delivery.zoneRejection, P1-21,
    // G1 fix H) and this message is the fallback. The kind stays the 400's 'client', never 'cart': nothing in the cart
    // changed on the server.
    { ecodes: [202], message: 'This address is outside our delivery area.' },
    { ecodes: [700, 701, 1000], kind: 'cart', message: 'Your points or rewards have changed. Please review your cart.' },
    { ecodes: [800], validator: true, message: "This coupon can't be used on this order. Remove it and try again." },
    { ecodes: [800], message: "The store isn't accepting this order right now. Please try again later." },
  ];
  // Copied onto the checkout error when the body has them (REF reads them on ecode 701; the checkout pre-flight, P2-08,
  // acts on them). Never read from a 404.
  var REFRESH_FLAGS = ['wallet_refresh_required', 'membership_refresh_required', 'pricetag_refresh_required', 'clear_cart_wallet', 'remove_product_rewards'];
  var COUPON_REFUSED = "This coupon can't be used on this order.";

  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function isObject(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function isTrue(v) { return v === true || (typeof v === 'string' && v.trim().toLowerCase() === 'true'); }
  function ecodeOf(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }
  function isDelivery(payload) { return /DELIVERY$/i.test(String((payload && payload.order_type) || '')); }

  function rowMatches(row, status, body, ecode) {
    if (row.http) return status === row.http;
    if (row.frequent) return status === 400 && typeof body.status === 'string' && /^\s*frequent order/i.test(body.status);
    if (!row.ecodes || ecode === null || row.ecodes.indexOf(ecode) < 0) return false;
    return !row.validator || typeof body.status === 'string';
  }

  // res = OO.http's withStatus outcome { status, data } of a checkout that did not succeed.
  function checkoutError(res, payload) {
    var status = Number(res.status) || 0;
    var body = status !== 404 && isObject(res.data) ? res.data : {};
    var ecode = ecodeOf(body.ecode);
    var row = null;
    for (var i = 0; i < CHECKOUT_ERRORS.length && !row; i++) {
      if (rowMatches(CHECKOUT_ERRORS[i], status, body, ecode)) row = CHECKOUT_ERRORS[i];
    }
    var err = new Error(row ? (row.delivery && isDelivery(payload) ? row.delivery : row.message) : GENERIC);
    err.status = status;
    err.kind = (row && row.kind) || OO.http.kindOf(status) || 'unavailable';
    err.data = res.data === undefined ? null : res.data;
    err.ecode = ecode;
    REFRESH_FLAGS.forEach(function (k) { if (own(body, k)) err[k] = body[k]; });
    return err;
  }

  // ---- shared bits ---------------------------------------------------------------------------------------------
  function sessionUid() {
    try {
      var s = OO.store && OO.store.session ? OO.store.session() : null;
      return s && s.customer && s.customer.uid ? s.customer.uid : null;
    } catch (e) { return null; }
  }
  // Never send a fixture demo uid (fx_…) to the real API (G1 fix A: a demo session left over from a fixture store).
  function uidOf(opts) { var u = (opts && opts.uid) || sessionUid(); return u && String(u).indexOf('fx_') !== 0 ? u : null; }
  function pageOf(opts) {
    var n = Math.floor(Number(opts && opts.page));
    return n >= 1 ? n : 1;
  }
  function keyOf(hotel) { return String(hotel == null ? '' : hotel); }

  // An empty Laravel paginator page (get_customer_orders answers a chain without the customer with an empty body).
  function emptyPage(page) {
    return {
      current_page: page, data: [], first_page_url: null, from: null, last_page: 1, last_page_url: null, links: [],
      next_page_url: null, path: null, per_page: 1000, prev_page_url: null, to: null, total: 0,
    };
  }

  // ---- coupons (E09–E11) ---------------------------------------------------------------------------------------
  function getCoupons(storeIdOrHotel, opts) {
    if (OO.orderingQr && OO.orderingQr.active()) return Promise.resolve({ status: true, data: [] });
    var id = typeof storeIdOrHotel === 'number' ? Promise.resolve(storeIdOrHotel) : R._.storeId(storeIdOrHotel);
    var fresh = !!(opts && opts.fresh);
    return id.then(function (n) {
      if (!(n > 0)) throw R._.unusable('coupon_get_v4', null);
      return OO.http.get(OO.mode === 'kiosk' ? 'coupon_get_kiosk' : 'coupon_get_v4', { query: { user_id: n }, cache: true, fresh: fresh });
    });
  }

  function isoOf(v) {
    var d = v === null || v === undefined || v === '' ? new Date() : new Date(v);
    return (isNaN(d.getTime()) ? new Date() : d).toISOString();
  }
  function tableSide(domain) { return String(domain || '').trim().replace(/[\s-]+/g, '_').toUpperCase() === 'TABLE_SIDE'; }
  // The server's refusal text ('Invalid coupon', 'Coupon limit reached', …) when it is short and plain.
  function plainText(v) {
    var s = typeof v === 'string' ? v.trim() : '';
    return s && s.length <= 120 && !/[<>\r\n]/.test(s) ? s : '';
  }

  function validateCoupon(hotel, opts) {
    opts = opts || {};
    return R._.storeId(hotel).then(function (storeId) {
      var body = { customer_email: String(opts.email == null ? '' : opts.email).replace(/\s+/g, '') };
      var code = String(opts.code == null ? '' : opts.code).trim().toUpperCase();
      if (code) body.code = code;
      body.store_id = storeId;
      body.time = isoOf(opts.time);
      body.check_free_items = true;
      if (opts.domain) body.source = String(opts.domain).trim().replace(/[\s-]+/g, '_').toUpperCase();
      if (OO.mode === 'kiosk') body.source = 'KIOSK';
      if (tableSide(opts.domain)) {
        body.is_table_side = true;
        body.price_type = 'table_side';
      }
      return OO.http.post('validate_coupon_v4', body);
    }).then(function (res) {
      if (!isObject(res)) throw R._.unusable('validate_coupon_v4', res);
      if (OO.http.isSuccess(res)) return res;
      var err = new Error(plainText(res.message) || COUPON_REFUSED);
      err.status = 200;
      err.kind = 'client';
      err.data = res;
      throw err;
    });
  }

  function invalidateCoupon(txn) {
    return OO.http.post('invalidate_coupon', { transaction_id: txn });
  }

  // ---- pricetags (E05, E06; P2-01) -----------------------------------------------------------------------------
  function getPricetags(storeIdOrHotel, opts) {
    var none = { order: null, prices: null };
    var fresh = !!(opts && opts.fresh);
    var id = typeof storeIdOrHotel === 'number' ? Promise.resolve(storeIdOrHotel) : R._.storeId(storeIdOrHotel);
    return id.then(function (n) {
      if (!(n > 0)) return none;
      var q = { query: { hotelId: n }, cache: true, soft: true, fresh: fresh };
      return Promise.all([OO.http.get('get_pricetag_order', q), OO.http.get('get_pricetag_prices', q)]).then(function (r) {
        return { order: r[0] === undefined ? null : r[0], prices: r[1] === undefined ? null : r[1] };
      });
    }, function () { return none; });
  }

  // ---- checkout, payment, status (E12–E14) ---------------------------------------------------------------------
  function placeOrder(hotel, payload) {
    // WL-2: never from a white-label studio preview (the payload refuses first; this is the second lock).
    if ((OO.orderingQr && OO.orderingQr.preview()) || (OO.whitelabel && typeof OO.whitelabel.isPreview === 'function' && OO.whitelabel.isPreview())) {
      var e = new Error('Orders are disabled in preview');
      e.kind = 'payload';
      e.code = 'PREVIEW';
      e.status = 0;
      e.data = null;
      return Promise.reject(e);
    }
    return OO.http.post('checkout', payload, { withStatus: true }).then(function (res) {
      if (res.status >= 200 && res.status < 300 && isObject(res.data) && isTrue(res.data.status)) return res.data;
      throw checkoutError(res, payload);
    });
  }

  function paymentUpdate(orderId) {
    return OO.http.post('payment_update', { order_id: orderId }, { withStatus: true }).then(function (res) {
      return { httpStatus: res.status, body: res.data };
    });
  }

  function orderStatus(orderId) {
    return OO.http.post('online_order_status', { order_id: orderId });
  }

  // ---- orders and tracking (E15–E18) ---------------------------------------------------------------------------
  function getOrders(hotel, opts) {
    var page = pageOf(opts);
    return OO.http.post('get_customer_orders', { uid: uidOf(opts), online_key: keyOf(hotel) }, { query: { page: page } })
      .then(function (body) { return body === null || body === undefined || body === '' ? emptyPage(page) : body; });
  }

  function getOrder(hotel, ticketId) {
    return OO.http.get('get_order_data', { query: { ticket_id: ticketId } });
  }

  function getAllOrders(hotel, opts) {
    return OO.http.get('customer/getOrders', { query: { cusId: uidOf(opts), hotelKey: keyOf(hotel), page: pageOf(opts) } });
  }

  function httpUrl(v) {
    var s = typeof v === 'string' ? v.trim() : '';
    return /^https?:\/\/[^\s]+$/i.test(s) ? s : null;
  }
  function trackingUrlOf(body) {
    var d = isObject(body) ? body : {};
    var data = isObject(d.data) ? d.data : {};
    var original = isObject(data.original) ? data.original : {};
    return httpUrl(data.tracking_url) || httpUrl(original.tracking_url) || httpUrl(d.tracking_url);
  }

  function getTrackingLink(orderId) {
    return OO.http.get('get_delivery_tracking_link', { query: { order_id: orderId } }).then(function (body) {
      return { url: trackingUrlOf(body) };
    }, function (err) {
      if (err && (err.status === 400 || err.status === 404)) return { url: null };
      throw err;
    });
  }

  // ---- suggestions (E07 / E08, P2-19): soft and cached -----------------------------------------------------------
  function noSuggestions() { return { status: true, data: [] }; }
  function suggestions(path, query) {
    return OO.http.get(path, { query: query, cache: true, soft: true }).then(function (body) {
      return isObject(body) ? body : noSuggestions();
    }, noSuggestions);
  }
  function trimmed(v) { return v === undefined || v === null ? '' : String(v).trim(); }
  function getProductSuggestions(hotel, itemId) {
    if (OO.orderingQr && OO.orderingQr.active()) return Promise.resolve(noSuggestions());
    var key = keyOf(hotel), id = trimmed(itemId);
    if (!key || !id) return Promise.resolve(noSuggestions());
    return suggestions('product_suggestion_list_online', { online_key: key, id: id });
  }
  function getCartSuggestions(hotel) {
    if (OO.orderingQr && OO.orderingQr.active()) return Promise.resolve(noSuggestions());
    var key = keyOf(hotel);
    if (!key) return Promise.resolve(noSuggestions());
    if (OO.mode === 'kiosk') return OO.http.post('get_kiosk_cart_suggestion_for_online_kiosk', { online_key: key }, { cache: true, soft: true }).then(function (body) {
      return isObject(body) ? body : noSuggestions();
    }, noSuggestions);
    return suggestions('cart_suggestion_list_online', { online_key: key });
  }

  // ---- ratings (E30 / E31, P3-06) ------------------------------------------------------------------------------
  // The customer-facing copy of a refused rating, by HTTP status (the backend's own text never reaches the customer).
  var RATE_ERRORS = {
    400: "We couldn't find this order in your account.",   // 'Customer not found'
    403: "Ratings aren't available for this store.",        // the review-feedback module is off
    404: "We couldn't find this order in your account.",   // 'Customer not found for this store' | 'Order not found'
    422: 'Check your rating and try again.',
    429: 'Too many attempts. Wait a minute and try again.',
  };
  var RATE_GENERIC = "We couldn't send your rating. Please try again.";
  var ALREADY_RATED = /already\s+rated/i;
  function whole(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return n % 1 === 0 ? n : null;
  }
  function rateFeedback(v) { return (typeof v === 'string' ? v.trim() : '').slice(0, 1000); }
  function localRefusal(message) {
    var err = new Error(message);
    err.status = 0;
    err.kind = 'client';
    err.data = null;
    return err;
  }
  // A ticket's store id (opts.storeId), else the route store's.
  function ratingStoreId(hotel, storeId) {
    var n = whole(storeId);
    return n > 0 ? Promise.resolve(n) : R._.storeId(hotel);
  }

  function getOrderRating(hotel, orderId, opts) {
    opts = opts || {};
    var id = trimmed(orderId);
    if (!id) return Promise.reject(R._.unusable('customer/order-rating', null));
    return ratingStoreId(hotel, opts.storeId).then(function (n) {
      if (!(n > 0)) throw R._.unusable('customer/order-rating', null);
      return OO.http.get('customer/order-rating/' + encodeURIComponent(String(n)) + '/' + encodeURIComponent(id), { cache: true, fresh: !!opts.fresh });
    });
  }

  function rateItems(items) {
    var out = [];
    (Array.isArray(items) ? items : []).forEach(function (it) {
      var pid = it && typeof it === 'object' ? whole(it.productId) : null;
      if (!(pid > 0)) return;
      var row = { product_id: pid };
      var r = whole(it.rating);
      if (r >= 1 && r <= 5) row.rating = r;
      var f = rateFeedback(it.feedback);
      if (f) row.feedback = f;
      out.push(row);
    });
    return out;
  }

  function rateOrder(hotel, opts) {
    opts = opts || {};
    var uid = uidOf(opts);
    var id = trimmed(opts.orderId);
    if (!uid) return Promise.reject(localRefusal('Log in to rate this order.'));
    if (!id) return Promise.reject(localRefusal(RATE_GENERIC));
    return ratingStoreId(hotel, opts.storeId).then(function (n) {
      if (!(n > 0)) throw localRefusal(RATE_GENERIC);
      var body = { customer_id: uid, order_id: id, store_id: n, rating: whole(opts.rating), feedback: rateFeedback(opts.feedback) };
      var items = rateItems(opts.items);
      if (items.length) body.items = items;
      return OO.http.post('customer/rate-order', body, { withStatus: true });
    }).then(function (res) {
      var status = Number(res.status) || 0;
      var data = isObject(res.data) ? res.data : {};
      var text = typeof data.message === 'string' ? data.message : (typeof data.error === 'string' ? data.error : '');
      if (status >= 200 && status < 300 && OO.http.isSuccess(data)) return { status: true, already: false, message: text || null };
      if (status === 409 || (status >= 400 && status < 500 && ALREADY_RATED.test(text))) return { status: true, already: true, message: text || null };
      var err = new Error(RATE_ERRORS[status] || RATE_GENERIC);
      err.status = status;
      err.kind = OO.http.kindOf(status) || 'unavailable';
      err.data = res.data === undefined ? null : res.data;
      throw err;
    });
  }

  // ---- average ratings (E28 / E29, P3-07) ------------------------------------------------------------------------
  function getStoreRating(storeIdOrHotel) {
    var id = typeof storeIdOrHotel === 'number' ? Promise.resolve(storeIdOrHotel) : R._.storeId(storeIdOrHotel);
    return id.then(function (n) {
      if (!(whole(n) > 0)) throw R._.unusable('customer/store-rating', null);
      return OO.http.get('customer/store-rating/' + encodeURIComponent(String(n)), { cache: true });
    });
  }

  function getProductRating(hotel, productId) {
    var pid = whole(productId);
    if (!(pid > 0)) return Promise.reject(R._.unusable('customer/product-rating', null));
    return R._.storeId(hotel).then(function (n) {
      if (!(whole(n) > 0)) throw R._.unusable('customer/product-rating', null);
      return OO.http.get('customer/product-rating/' + encodeURIComponent(String(n)) + '/' + pid, { cache: true });
    });
  }

  R.getCoupons = getCoupons;
  R.getPricetags = getPricetags;
  R.validateCoupon = validateCoupon;
  R.invalidateCoupon = invalidateCoupon;
  R.placeOrder = placeOrder;
  R.paymentUpdate = paymentUpdate;
  R.orderStatus = orderStatus;
  R.getOrders = getOrders;
  R.getOrder = getOrder;
  R.getAllOrders = getAllOrders;
  R.getTrackingLink = getTrackingLink;
  R.getProductSuggestions = getProductSuggestions;
  R.getCartSuggestions = getCartSuggestions;
  R.getOrderRating = getOrderRating;
  R.rateOrder = rateOrder;
  R.getStoreRating = getStoreRating;
  R.getProductRating = getProductRating;
})();


// ===================== src/data/api-real-booking.jsx =====================
// src/data/api-real-booking.jsx: the real adapter's table-booking calls (P1-06): booking config, slots, place,
// status, history (BATCH2-PLAN §5 E36–E40; inventory C §2.C1–C5 and §3.3; captures in tests/contract/**). Its methods
// attach to OO.data.realApi (created by api-real.jsx) and keep the names, signatures and resolved shapes of their
// fixture twins in store-api.jsx: the endpoint's REAL raw body. Transport is OO.http v2 only, and no request or
// response body is ever logged (they carry names, emails and phones). None of these reads is cached (they are not on
// the plan's cached list); each is deduped while in flight and has OO.http's 30 s read deadline. place_table_booking is
// a write. A 2xx body that is not a JSON object rejects _.unusable (kind 'unavailable', 502; R-API-4).
//
//   getBookingConfig(hotel) → Promise<raw>   POST get_table_booking_config { id: hotel } (E36) → { status: true,
//       shop, config, available_dates ['Y-m-d'], holidays, theme, workingInfo, review_summary }, as served: the
//       payload at the root (every capture; REF also reads it under `data`), `config.is_active` null = active, `theme`
//       raw base64 blobs (no data: prefix) or null, `available_dates` not yet filtered to the store's today. All of
//       that is OO.model.normalizeBooking's job. A 400 { status: false, error: 'not found restuarent' | 'Reservation
//       Settings Not Found!' } rejects kind 'client' (OO.http), and so does a 403 (the module is not licensed). A 2xx
//       { status: false, error } object resolves as is (normalizeBooking shows its error as the disabled reason).
//   getBookingSlots(hotel, { date, guests }) → Promise<raw>   POST check_table_booking_slots { id: hotel, date:
//       'Y-m-d', person_count: guests } (E37) → { status: true, slots: [{ stime 'HH:mm', stimeEpoch (ms), etime,
//       etimeEpoch (null on the last slot), booked_slots, allow_booking, remaining_guests }] }. Past slots are NOT
//       filtered server-side (the page / normaliser drops them); `slots: []` when booking is inactive. date:
//       'YYYY-MM-DD', 'DD-MM-YYYY' or 'DD/MM/YYYY' (a real calendar date) goes out as Y-m-d; anything else rejects the
//       API's own 422 { message, errors: { date } } without a request, and so does a party size that isn't an
//       integer ≥ 1 (errors.person_count). An unknown store is a 404: kind 'notFound' (by status: prod sends HTML).
//   placeBooking(hotel, body) → Promise<raw>   POST place_table_booking (E38): a write (no deadline, never deduped;
//       a 2xx flushes the read cache). body = { date, time, guests (or person), name, email, phone, notes, uid?,
//       reservationFrom?, reservationUpto? } goes out as EVERY key below (a host without B09 answers a missing one
//       with a 500):
//         user_id            hotel (the online_key, sic)
//         date               'd-m-Y' (the one format every host parses)
//         time               'HH:mm'
//         reservation_from   the chosen slot's stimeEpoch / etimeEpoch in ms (each ≥ 1e12, upto after from, else
//         reservation_upto   null; without both, the server derives the window from date + time in `timezone`)
//         person             an int
//         name, phone, email trimmed strings; notes: '' when empty
//         uid                body.uid, else the session's, else null
//         timezone           the STORE's: the loaded store's, else getRestaurant's data[0].timezone, else _.tzFor
//         isMobileApp        OO.platform.isMobileApp() (P4-09: app mode or the shell)
//       A date, time or party size the API would refuse rejects B09's own 400 { status: false, error, errors }
//       without a request (a host without B09 can silently book a garbage time). 200 { status: true, data: {
//       reservation_id 'TRE…', customer_*, person_count, reservation_date, reservation_time, notes,
//       reservation_status: 'PENDING' } } resolves (status true or 'true'); any other 2xx rejects _.unusable. A
//       failure keeps OO.http's status, kind and data (the raw body); only `message` is rewritten, since the page
//       shows it (a 500 debug body never reaches it):
//         400 + remaining_guests             'Only N seat(s) left…' | '…fully booked…' (built here), plus
//                                            remainingGuests (a number)
//         400 + maximum_people_per_booking   'Maximum party size is N people per booking.', plus
//                                            maximumPeoplePerBooking (a number)
//         403 (not licensed) · 404           NOT_TAKING
//         429                                TOO_MANY
//         another 4xx                        its `error` when short and plain, else BOOKING_FAILED
//         5xx (and anything else)            BOOKING_FAILED
//       A network failure rejects OO.http's error unchanged (kind 'network'): the booking MAY exist.
//   getBookingStatus(hotel, { email, reservationId }) → Promise<raw>   (new, E39) POST table_booking_status { email,
//       reservation_id } (both trimmed) → { status: true, data: <the table_bookings row: reservation_status
//       PENDING | ACCEPT | REJECT | CANCEL, sub_status SEATED | COMPLETED | NOSHOW | null (only here),
//       reservation_from / upto, …> }. Not store-scoped: hotel only routes OO.api. Never cached (live status). A miss
//       rejects NOT_FOUND { kind: 'notFound', status: 404, data: null, message }: the B08 404, nginx's HTML 404 and a
//       2xx without the row. A missing email or id: that, with no request (a host without B08 matches a missing one
//       with whereNull). A 5xx is a failure, not a miss (also the 500 a host without B08 answers for one): OO.http's
//       status and kind 'unavailable', its body dropped (a debug body carries PHP internals), message STATUS_FAILED.
//       Other failures propagate (OO.http).
//   getCustomerBookings(hotel, { uid }) → Promise<raw>   (new, E40) POST get_customer_table_bookings { uid,
//       online_key: hotel } → { status: true, data: [{ user_id, customer_email, customer_name, customer_phone,
//       person_count, reservation_date, reservation_time, notes, reservation_id, reservation_status, table_id,
//       store_name, store_phone, store_address }] }: the whole chain's bookings, matched by the customer's email; B09
//       adds reservation_from / upto, sub_status and created_at. uid = opts.uid, else the session's. An unknown uid is
//       a 400 { status: false, error: 'Customer Data Not Found!' } (rejects, OO.http); no uid at all rejects that same
//       400 without a request (a null uid would match whereNull).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};
  var R = (OO.data.realApi = OO.data.realApi || {});

  var DEFAULT_TZ = 'Pacific/Auckland';
  var MIN_EPOCH_MS = 1e12;  // 2001-09-09: smaller is a seconds epoch, never a slot's stimeEpoch / etimeEpoch
  var BOOKING_FAILED = "We couldn't book that table. Please try again.";
  var NOT_TAKING = "This store isn't taking table bookings online right now.";
  var TOO_MANY = 'Too many attempts. Wait a minute and try again.';
  var NOT_FOUND = "We couldn't find a reservation with that ID and email.";
  var STATUS_FAILED = "We couldn't check that reservation right now. Please try again.";
  // The API's own validation texts (check_table_booking_slots: Laravel 422; place_table_booking: B09 400).
  var SLOT_DATE = 'The date must be a valid calendar date in DD-MM-YYYY, YYYY-MM-DD, or DD/MM/YYYY format.';
  var SLOT_PARTY = 'The person count field must be at least 1.';
  var PLACE_DATE = 'The date must be a valid date in DD-MM-YYYY or YYYY-MM-DD format.';
  var PLACE_TIME = 'The time must be a valid time (HH:mm).';
  var PLACE_PARTY = 'The person field must be at least 1.';

  // The login source the backend records (customerCreationOnTableBooking: app / web).
  function isMobileApp() { return !!(OO.platform && OO.platform.isMobileApp()); } // P4-09, as for the auth calls

  function isObject(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function isTrue(v) { return v === true || (typeof v === 'string' && v.trim().toLowerCase() === 'true'); }
  function str(v) { return v == null ? '' : String(v); }
  function text(v) { return str(v).trim(); }
  function keyOf(hotel) { return str(hotel); }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function sessionUid() {
    try { var s = OO.store && OO.store.session(); return s && s.customer && s.customer.uid ? String(s.customer.uid) : null; } catch (e) { return null; }
  }
  function uidOf(opts) { return (opts && opts.uid ? String(opts.uid) : null) || sessionUid(); }

  // ---- input --------------------------------------------------------------------------------------------
  // 'YYYY-MM-DD' | 'DD-MM-YYYY' | 'DD/MM/YYYY' (two-digit day and month, one separator) → { y, m, d } of a real
  // calendar date (the API's strict parse), else null.
  function dateOf(v) {
    var s = typeof v === 'string' ? v.trim() : '';
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    var p = m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
    if (!p && (m = /^(\d{2})([-/])(\d{2})\2(\d{4})$/.exec(s))) p = { y: +m[4], m: +m[3], d: +m[1] };
    if (!p) return null;
    var t = new Date(Date.UTC(p.y, p.m - 1, p.d));
    return t.getUTCFullYear() === p.y && t.getUTCMonth() === p.m - 1 && t.getUTCDate() === p.d ? p : null;
  }
  function ymdOf(p) { return p.y + '-' + pad2(p.m) + '-' + pad2(p.d); }
  function dmyOf(p) { return pad2(p.d) + '-' + pad2(p.m) + '-' + p.y; }

  // 'H:mm' | 'HH:mm' | 'HH:mm:ss' → zero-padded 'HH:mm', else null.
  function timeOf(v) {
    var m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(typeof v === 'string' ? v.trim() : '');
    return m && +m[1] <= 23 && +m[2] <= 59 ? pad2(+m[1]) + ':' + m[2] : null;
  }

  // A number or a numeric string; anything else (null, '', a boolean, an object) is NaN.
  function numberOf(v) {
    if (typeof v === 'number') return v;
    return typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  }
  // A party size: an integer ≥ 1, else null.
  function partyOf(v) {
    var n = numberOf(v);
    return isFinite(n) && Math.floor(n) === n && n >= 1 ? n : null;
  }
  // An epoch in ms (≥ MIN_EPOCH_MS), else null.
  function epochOf(v) {
    var n = numberOf(v);
    return isFinite(n) && n >= MIN_EPOCH_MS ? Math.round(n) : null;
  }

  // ---- errors -------------------------------------------------------------------------------------------
  // The Error OO.http throws for this status and JSON body, for a request this adapter knows the API would refuse.
  function apiError(status, data) {
    var err = new Error(data.message || data.error || 'HTTP ' + status);
    err.status = status;
    err.data = data;
    err.kind = OO.http.kindOf(status);
    return err;
  }
  function slotsRefusal(field, message) {
    var errors = {};
    errors[field] = [message];
    return Promise.reject(apiError(422, { message: message, errors: errors }));
  }
  function placeRefusal(field, message) {
    var errors = {};
    errors[field] = [message];
    return Promise.reject(apiError(400, { status: false, error: message, errors: errors }));
  }

  function bookingNotFound() {
    var err = new Error(NOT_FOUND);
    err.status = 404;
    err.kind = 'notFound';
    err.data = null;
    return err;
  }

  // A 2xx body this adapter resolves must at least be a JSON object (R-API-4).
  function objectBody(path) {
    return function (body) {
      if (!isObject(body)) throw R._.unusable(path, body);
      return body;
    };
  }

  // The server's refusal text when it is short and plain.
  function plainText(v) {
    var s = typeof v === 'string' ? v.trim() : '';
    return s && s.length <= 120 && !/[<>\r\n]/.test(s) ? s : '';
  }

  // place_table_booking's rejection (see the header): OO.http's error, with `message` rewritten and the capacity
  // numbers copied on. A 404 body is never read (prod: nginx HTML).
  function placeError(err) {
    var status = err && err.status;
    if (!status) throw err; // network: OO.http's error as is (the booking may exist)
    var body = status !== 404 && isObject(err.data) ? err.data : {};
    var left = numberOf(body.remaining_guests);
    var most = numberOf(body.maximum_people_per_booking);
    if (status === 400 && isFinite(left) && left >= 0) {
      err.remainingGuests = left;
      err.message = left > 0
        ? 'Only ' + left + ' seat(s) left for the selected time slot. '
          + 'Please reduce the party size or pick another slot.'
        : 'The selected time slot is fully booked. Please pick another slot.';
    } else if (status === 400 && isFinite(most) && most > 0) {
      err.maximumPeoplePerBooking = most;
      err.message = 'Maximum party size is ' + most + ' people per booking.';
    } else if (status === 403 || status === 404) {
      err.message = NOT_TAKING;
    } else if (status === 429) {
      err.message = TOO_MANY;
    } else {
      err.message = (status >= 400 && status < 500 && plainText(body.error)) || BOOKING_FAILED;
    }
    throw err;
  }

  // ---- the store's timezone -------------------------------------------------------------------------------
  // The loaded store's (normalizeStore: a zone Intl reads), else getRestaurant's data[0].timezone (cached), else the
  // remembered one (_.tzFor). place_table_booking derives the window from it when no slot epochs come with the body,
  // and the confirmation email shows times in it (REF sends the device's).
  function storeTz(hotel) {
    var p = OO.data.peekHotel ? OO.data.peekHotel(hotel, false) : null;
    if (p && p.store && p.store.timezone) return Promise.resolve(String(p.store.timezone));
    return R._.storeRaw(hotel).then(function (d) {
      return text(d && d.timezone) || DEFAULT_TZ;
    }, function () { return R._.tzFor(hotel); });
  }

  // ---- config and slots (E36, E37) --------------------------------------------------------------------------
  function getBookingConfig(hotel) {
    return OO.http.post('get_table_booking_config', { id: keyOf(hotel) }).then(objectBody('get_table_booking_config'));
  }

  function getBookingSlots(hotel, opts) {
    opts = opts || {};
    var date = dateOf(opts.date);
    if (!date) return slotsRefusal('date', SLOT_DATE);
    var guests = partyOf(opts.guests);
    if (!guests) return slotsRefusal('person_count', SLOT_PARTY);
    return OO.http.post('check_table_booking_slots', { id: keyOf(hotel), date: ymdOf(date), person_count: guests })
      .then(objectBody('check_table_booking_slots'));
  }

  // ---- place (E38) ----------------------------------------------------------------------------------------
  function placeBooking(hotel, body) {
    body = body || {};
    var key = keyOf(hotel);
    var date = dateOf(body.date);
    if (!date) return placeRefusal('date', PLACE_DATE);
    var time = timeOf(body.time);
    if (!time) return placeRefusal('time', PLACE_TIME);
    var person = partyOf(body.guests != null ? body.guests : body.person);
    if (!person) return placeRefusal('person', PLACE_PARTY);
    var from = epochOf(body.reservationFrom);
    var upto = epochOf(body.reservationUpto);
    if (from === null || upto === null || upto <= from) upto = null;
    var uid = (body.uid ? String(body.uid) : null) || sessionUid();
    return storeTz(key).then(function (tz) {
      return OO.http.post('place_table_booking', {
        user_id: key, date: dmyOf(date), time: time, reservation_from: from, reservation_upto: upto, person: person,
        name: text(body.name), phone: text(body.phone), email: text(body.email), notes: text(body.notes), uid: uid,
        timezone: tz, isMobileApp: isMobileApp(),
      }).then(function (res) {
        if (isObject(res) && isTrue(res.status)) return res;
        throw R._.unusable('place_table_booking', res);
      }, placeError);
    });
  }

  // ---- status and history (E39, E40) ------------------------------------------------------------------------
  function getBookingStatus(hotel, query) {
    query = query || {};
    var email = text(query.email);
    var id = text(query.reservationId);
    if (!email || !id) return Promise.reject(bookingNotFound());
    return OO.http.post('table_booking_status', { email: email, reservation_id: id }).then(function (res) {
      if (!isObject(res)) throw R._.unusable('table_booking_status', res);
      if (!OO.http.isSuccess(res) || !isObject(res.data)) throw bookingNotFound();
      return res;
    }, function (err) {
      if (err && err.status === 404) throw bookingNotFound();
      if (err && err.status >= 500) { // the server's trouble, not a miss (see the header)
        err.data = null;
        err.message = STATUS_FAILED;
      }
      throw err;
    });
  }

  function getCustomerBookings(hotel, opts) {
    var uid = uidOf(opts);
    if (!uid) return Promise.reject(apiError(400, { status: false, error: 'Customer Data Not Found!' }));
    return OO.http.post('get_customer_table_bookings', { uid: uid, online_key: keyOf(hotel) })
      .then(objectBody('get_customer_table_bookings'));
  }

  R.getBookingConfig = getBookingConfig;
  R.getBookingSlots = getBookingSlots;
  R.placeBooking = placeBooking;
  R.getBookingStatus = getBookingStatus;
  R.getCustomerBookings = getCustomerBookings;
})();


// ===================== src/data/api-select.jsx =====================
// src/data/api-select.jsx: OO.api (alias OO.data.storeApi), the ONE data-access seam the pages call (P1-02). It is a
// facade that picks the adapter PER STORE, at call time:
//   * a fixture key (OO.fixtures.has: bennys, nonnas, harbour and the two Bennys branches) → OO.data.fixtureApi
//     (src/data/store-api.jsx), the Batch-1 sample data. Fixture keys always win: no real online_key collides with
//     one (P0-AUDIT §5).
//   * a key listed in window.__SIPO_BOOT.live, or any other key when that list holds '*' → OO.data.realApi (the HTTP
//     adapter, src/data/api-real*.jsx).
//   * any other key is not live here: its calls reject with kind 'notLive' (below), and OO.App shows the "not
//     available here yet" card (src/pages/not-found.jsx) instead of the page.
//     Except this device's order (G1 fix F, owner decision). When OO.store's lastOrder was placed at that store, the
//     store was live then, and the kill switch may have taken it out while the customer was at the gateway. So the
//     calls of its payment pages still go to OO.data.realApi: paymentUpdate, orderStatus, getOrder, invalidateCoupon,
//     getTrackingLink, and getRestaurant (both pages load the store through OO.useHotel). Every other method keeps the
//     rejection, OO.App still gates every other route of the store (src/app.jsx), and the store's availability
//     doesn't change (availabilityOf stays 'unavailable').
//   The boot list is read at CALL time. It is the pilot allowlist and the kill switch (ONLINE_ORDER_LIVE_STORES, then
//   config:clear; no rebuild). A white-label preview (?wlPreview=, WL-2) adds the previewed chain while its draft is
//   loaded (OO.whitelabel.previewAllows): its pages can't place an order (OO.payload refuses in preview).
//
// Store API:
//   adapterFor(hotel) → OO.data.fixtureApi | OO.data.realApi | null (not live here).
//   availabilityOf(hotel) → 'fixture' | 'live' | 'unavailable'. No hotel (null or '': the root find-store page, the
//       root payment-callback) counts as 'fixture', because hotel-less calls go to the fixture adapter there.
//   isFixtureFor(hotel) · isLiveFor(hotel) → bool.
//   legacyUrlFor(hotel) → __SIPO_BOOT.legacyBase (trailing '/' dropped) + '/' + encodeURIComponent(hotel), the
//       store's page on the current ordering site; null with no legacyBase or no hotel.
//   isOwnOrder(hotel, orderId?) → bool: OO.store's lastOrder (OO.store.get().lastOrder, read per call) was placed at
//       that store and, when an orderId is given (not undefined / null / ''), it is that order. Ids are compared as
//       strings, because checkout keeps the API's order_id as it came. false with no hotel or no lastOrder. The
//       exemption above uses it, and so does OO.App for the payment pages of a store that isn't live here.
//   isFixture   a getter: isFixtureFor(the current route's hotel). The preview hints of the pages read it.
//   discoveryIsFixture() → bool: the store finder's data (no store at all: the root page) is the sample stores, i.e.
//       this deployment serves no real store (an empty boot `live` list). Else its calls go to OO.data.realApi (P3-23
//       follow-up); the root find-store page's preview bar and sample position follow it.
//   now() → Date: the fixture adapter's clock (its __setNow) unless the current route's store is live, then the
//       device clock. OO.data.now() (src/lib/store.jsx) reads it.
//
// Adapter methods: OO.api carries every method of either adapter, i.e. the UNION of their function names when this file
// loads. Names starting with '_' (internals such as OO.data.realApi._, and the test hooks) are not routed. The facade
// passes every argument through unchanged and returns the adapter's promise. How a call finds its store:
//   * the hotel-less methods paymentUpdate(orderId), orderStatus(orderId), invalidateCoupon(txn), searchStores(query),
//     nearbyStores(opts) and getTrackingLink(orderId) take an optional TRAILING hotel: paymentUpdate(orderId, hotel).
//     Without one they use the current route's hotel, and with no route hotel either, the fixture adapter, except
//     the store finder's searchStores / nearbyStores, which go to the real adapter unless discoveryIsFixture().
//   * every other method names its store with its first argument, the online_key: getMenu(hotel, opts). A first
//     argument that isn't a string is a numeric store id (getCoupons(store.id)). An id can't pick the adapter (a
//     fixture id may equal a real one), so that call routes like a hotel-less call with no trailing hotel.
// Failures. The adapters' own rejections pass through untouched. The facade adds two:
//   * not live here (apart from this device's order, above) → Error { kind: 'notLive', status: 404, message:
//     'Store not found', hotel, legacyUrl, data }.
//     Batch-1 compatibility: getRestaurant, getMenu and getBookingConfig keep the status / message / data the fixture
//     adapter gave an unknown key (getBookingConfig: 400), so the callers that read them behave as before.
//   * the chosen adapter lacks the method (a real method that hasn't landed yet; a real-only method on a fixture
//     store) → Error { kind: 'unavailable', status: 0, message: 'Not available yet', data: null }.
//   A call never throws synchronously: an adapter's synchronous throw becomes a rejection too.
// Fixture-only members forward to the fixture adapter, so tests keep working through OO.api: its test hooks (every
// '__' function: __setLatency, __setNow, __reset) by reference, and its other plain members (ORDER_PROGRESS_MS) as they
// are.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.data = OO.data || {};

  // Hotel-less methods → the index of their optional trailing hotel argument.
  var HOTELLESS = { paymentUpdate: 1, orderStatus: 1, invalidateCoupon: 1, searchStores: 1, nearbyStores: 1, getTrackingLink: 1 };
  // The store finder's calls: with no store at all they follow discoveryIsFixture() (P3-23 follow-up).
  var DISCOVERY = { searchStores: 1, nearbyStores: 1 };
  // The facade's own members: never taken from an adapter.
  var OWN = { now: 1, isFixture: 1, adapterFor: 1, availabilityOf: 1, isFixtureFor: 1, isLiveFor: 1, legacyUrlFor: 1, isOwnOrder: 1, discoveryIsFixture: 1 };
  // What this device's order still needs from a store that has left the live list since (G1 fix F, see the header):
  // the payment callback's and the order page's calls, plus the store itself (OO.useHotel on both pages).
  var OWN_ORDER = { paymentUpdate: 1, orderStatus: 1, getOrder: 1, invalidateCoupon: 1, getTrackingLink: 1, getRestaurant: 1 };
  // A key that isn't live here. Batch 1 sent every key to the fixture adapter, which (like the API) knows no such
  // store; these three keep its answer. Every other method gets the default.
  var NOT_LIVE = {
    getRestaurant: { status: 404, message: 'Store not found', data: { error: 'not found restuarent' } },
    getMenu: { status: 404, message: 'Restaurant not found.', data: { status: false, error: 'Restaurant not found.' } },
    getBookingConfig: { status: 400, message: 'not found restuarent', data: { status: false, error: 'not found restuarent' } },
  };
  var NOT_LIVE_DEFAULT = { status: 404, message: 'Store not found', data: { status: false, error: 'Store not found' } };

  function own(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }
  function boot() { return window.__SIPO_BOOT || {}; }
  function keyOf(hotel) { return hotel === undefined || hotel === null ? '' : String(hotel); }

  // The current route's store. glue/router.jsx keeps its currentHotel() private; this mirrors it. Mode app with no branch
  // chosen (route.hotel null, P4-11): the chain's key stands in, so a live chain's Select store isn't read as a fixture's.
  function currentHotel() {
    try {
      var R = OO.router;
      if (!R || typeof R.current !== 'function' || !window.location) return null;
      var r = R.current();
      return r.hotel || r.chain || null;
    } catch (e) { return null; }
  }

  function hasFixture(key) {
    var fx = OO.fixtures || OO.data.fixtures;
    try { return !!(fx && typeof fx.has === 'function' && fx.has(key)); } catch (e) { return false; }
  }
  // Keys are case-sensitive (P0-AUDIT §5): exact matches only.
  function listedLive(key) {
    var list = boot().live;
    if (!Array.isArray(list)) return false;
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if ((typeof v === 'string' || typeof v === 'number') && (String(v) === '*' || String(v) === key)) return true;
    }
    return false;
  }

  // WL-2: a white-label preview's chain counts as live here once its draft has loaded (OO.whitelabel.previewAllows).
  function previewed(key) {
    var W = OO.whitelabel;
    try { return !!W && typeof W.previewAllows === 'function' && !!W.previewAllows(key); } catch (e) { return false; }
  }
  function availabilityOf(hotel) {
    var key = keyOf(hotel);
    if (key && OO.orderingQr && OO.orderingQr.forHotel(key)) return 'live';
    if (!key || hasFixture(key)) return 'fixture';
    return listedLive(key) || previewed(key) ? 'live' : 'unavailable';
  }
  function isFixtureFor(hotel) { return availabilityOf(hotel) === 'fixture'; }
  function discoveryIsFixture() { var list = boot().live; return !(Array.isArray(list) && list.length); }
  function isLiveFor(hotel) { return availabilityOf(hotel) === 'live'; }
  function adapterFor(hotel) {
    var a = availabilityOf(hotel);
    if (a === 'fixture') return OO.data.fixtureApi || null;
    return a === 'live' ? OO.data.realApi || null : null;
  }

  function legacyUrlFor(hotel) {
    var key = keyOf(hotel);
    var base = String(boot().legacyBase || '').replace(/\/+$/, '');
    return base && key ? base + '/' + encodeURIComponent(key) : null;
  }

  // OO.store's lastOrder (src/lib/store.jsx, concatenated before this file), read per call; null when there is none.
  function lastOrder() {
    try {
      var S = OO.store;
      var lo = S && typeof S.get === 'function' ? S.get().lastOrder : null;
      return lo && typeof lo === 'object' ? lo : null;
    } catch (e) { return null; }
  }
  function given(v) { return v !== undefined && v !== null && v !== ''; }
  function isOwnOrder(hotel, orderId) {
    var key = keyOf(hotel);
    var lo = lastOrder();
    if (!key || !lo || !given(lo.hotel) || String(lo.hotel) !== key) return false;
    return !given(orderId) || (given(lo.orderId) && String(lo.orderId) === String(orderId));
  }

  function now() {
    var fx = OO.data.fixtureApi;
    if (isLiveFor(currentHotel()) || !fx || typeof fx.now !== 'function') return new Date();
    return fx.now();
  }

  // ---- routing ------------------------------------------------------------------------------------------
  function notLive(name, hotel) {
    var c = own(NOT_LIVE, name) ? NOT_LIVE[name] : NOT_LIVE_DEFAULT;
    var err = new Error(c.message);
    err.kind = 'notLive';
    err.status = c.status;
    err.data = JSON.parse(JSON.stringify(c.data)); // a fresh body per rejection: callers may mutate it
    err.hotel = keyOf(hotel);
    err.legacyUrl = legacyUrlFor(hotel);
    return Promise.reject(err);
  }
  function notYet() {
    var err = new Error('Not available yet');
    err.kind = 'unavailable';
    err.status = 0;
    err.data = null;
    return Promise.reject(err);
  }

  function storeOf(name, args) {
    if (own(HOTELLESS, name)) {
      var h = args[HOTELLESS[name]];
      return typeof h === 'string' && h ? h : currentHotel();
    }
    return typeof args[0] === 'string' ? args[0] : currentHotel();
  }

  function route(name) {
    return function () {
      var hotel = storeOf(name, arguments);
      var adapter = !hotel && own(DISCOVERY, name) && !discoveryIsFixture() ? OO.data.realApi || null : adapterFor(hotel);
      // This device's order at a store switched off since it was placed: its payment pages keep these calls.
      if (!adapter && own(OWN_ORDER, name) && availabilityOf(hotel) === 'unavailable' && isOwnOrder(hotel)) {
        adapter = OO.data.realApi || null;
      }
      if (!adapter) return notLive(name, hotel);
      var fn = adapter[name];
      if (typeof fn !== 'function') return notYet();
      try { return fn.apply(adapter, arguments); } catch (e) { return Promise.reject(e); }
    };
  }

  // ---- the facade -----------------------------------------------------------------------------------------
  var api = {};
  [OO.data.fixtureApi, OO.data.realApi].forEach(function (adapter) {
    if (!adapter) return;
    Object.keys(adapter).forEach(function (name) {
      if (name.charAt(0) === '_' || own(OWN, name) || own(api, name) || typeof adapter[name] !== 'function') return;
      api[name] = route(name);
    });
  });

  var fixture = OO.data.fixtureApi || {};
  Object.keys(fixture).forEach(function (name) {
    var v = fixture[name];
    if (name.slice(0, 2) === '__') { if (typeof v === 'function') api[name] = v; return; }
    if (name.charAt(0) !== '_' && !own(OWN, name) && typeof v !== 'function') api[name] = v;
  });

  api.now = now;
  api.adapterFor = adapterFor;
  api.availabilityOf = availabilityOf;
  api.isFixtureFor = isFixtureFor;
  api.isLiveFor = isLiveFor;
  api.legacyUrlFor = legacyUrlFor;
  api.isOwnOrder = isOwnOrder;
  api.discoveryIsFixture = discoveryIsFixture;
  Object.defineProperty(api, 'isFixture', {
    get: function () { return isFixtureFor(currentHotel()); },
    enumerable: true,
    configurable: true,
  });

  OO.api = OO.data.storeApi = api;
})();


// ===================== src/ui/icons.jsx =====================
// src/ui/icons.jsx: OO.icons, the inline SVG icon set. Paths, stroke widths and line caps are copied from the
// design source (docs/online-order/design/*.dc.txt); Clock, Info, ImagePlaceholder and Settings use the same
// 24px / 2px-stroke language. Owner: T2b (UI kit). Also exported as OO.ui.icons.
//
//   <OO.icons.Bag size={20} />                            stroke = currentColor unless `color` is given
//   <OO.icons.Check size={13} strokeWidth={3.5} color="var(--oo-on-primary)" />
//   <OO.ui.Icon name="Bag" size={20} />                   by name (an unknown name renders nothing)
//
// Every icon takes { size = 24, color = 'currentColor', strokeWidth = <design default>, title, className,
// style }. Without `title` the svg is aria-hidden: the button or link around it carries the name. Extra props:
// Home { filled } (the active tab-bar icon is filled), Ticket { dashed } (mobile cart coupon perforation).
//
// Payment brand marks (W5-FIX #6): REF's public/payment-methods logos (paymentGatewayBranding.ts, REF develop d298a7e),
// full colour at their own aspect, unlike the 24px stroke icons: BrandStripe, BrandWindcave, BrandPinPayments (inline
// SVG, the viewBox tightened to the artwork) and BrandAfterpay, BrandLinePay, BrandPayPal, BrandPayrix (the small raster
// files as data URIs, each under 8 KB: the build ships no asset). REF's click-paymark.png (74 KB) and Razorpay (no REF
// logo) have none: the payment card keeps their text wordmark.
//   <OO.icons.BrandStripe height={20} />   { height = 20, className, style }; the width follows the mark's aspect. Always
//                                          aria-hidden (decorative): the caller keeps the brand's name as text.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.ui = OO.ui || {};

  // name → { sw: default stroke width, cap, join, fill: filled glyph, draw: (props) → svg children }
  var SPECS = {
    ArrowLeft: { sw: 2.2, cap: 'round', join: 'round', draw: function () { return <path d="M19 12H5M11 18l-6-6 6-6" />; } },
    ArrowRight: { sw: 2.2, cap: 'round', join: 'round', draw: function () { return <path d="M5 12h14M13 6l6 6-6 6" />; } },
    Table: { sw: 1.8, cap: 'round', join: 'round', draw: function () { return [<rect key="a" x="4" y="3" width="16" height="18" rx="2" />, <path key="b" d="M4 9h16M10 3v18" />]; } },
    Bag: { sw: 2, draw: function () { return [<path key="a" d="M5 8h14l-1 12H6L5 8z" />, <path key="b" d="M9 8V6a3 3 0 0 1 6 0v2" />]; } },
    Utensils: { sw: 2, cap: 'round', join: 'round', draw: function () { return <path d="M4 3v5a3 3 0 0 0 6 0V3M7 3v18M20 3c-3 0-5 4-5 9h5M20 3v18" />; } },
    Search: { sw: 2.2, cap: 'round', draw: function () { return [<circle key="a" cx="11" cy="11" r="7" />, <path key="b" d="M20 20l-4-4" />]; } },
    User: { sw: 2, draw: function () { return [<circle key="a" cx="12" cy="8" r="4" />, <path key="b" d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />]; } },
    Store: { sw: 2, draw: function () { return <path d="M4 9l1.5-5h13L20 9M4 9h16v2a3 3 0 0 1-6 0 3 3 0 0 1-4 0 3 3 0 0 1-6 0V9zM5 13v7h14v-7" />; } },
    Pin: { sw: 2, draw: function () { return [<path key="a" d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />, <circle key="b" cx="12" cy="9.5" r="2.5" />]; } },
    Pencil: { sw: 2, join: 'round', draw: function () { return <path d="M4 20h4L19 9l-4-4L4 16v4z" />; } },
    Settings: { sw: 2, cap: 'round', join: 'round', draw: function () { return [
      <path key="a" d="M19.09 9.56L21.78 9.92L21.78 14.08L19.09 14.44L18.74 15.29L20.39 17.45L17.45 20.39L15.29 18.74L14.44 19.09L14.08 21.78L9.92 21.78L9.56 19.09L8.71 18.74L6.55 20.39L3.61 17.45L5.26 15.29L4.91 14.44L2.22 14.08L2.22 9.92L4.91 9.56L5.26 8.71L3.61 6.55L6.55 3.61L8.71 5.26L9.56 4.91L9.92 2.22L14.08 2.22L14.44 4.91L15.29 5.26L17.45 3.61L20.39 6.55L18.74 8.71Z" />,
      <circle key="b" cx="12" cy="12" r="3" />,
    ]; } },
    Trash: { sw: 2, cap: 'round', draw: function () { return <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />; } },
    Plus: { sw: 2.4, cap: 'round', draw: function () { return <path d="M12 5v14M5 12h14" />; } },
    Minus: { sw: 2.4, cap: 'round', draw: function () { return <path d="M5 12h14" />; } },
    Check: { sw: 3, cap: 'round', join: 'round', draw: function () { return <path d="M5 12l5 5 9-10" />; } },
    ChevronDown: { sw: 2.6, cap: 'round', draw: function () { return <path d="M6 9l6 6 6-6" />; } },
    ChevronUp: { sw: 2.6, cap: 'round', draw: function () { return <path d="M6 15l6-6 6 6" />; } },
    ChevronRight: { sw: 2.4, cap: 'round', join: 'round', draw: function () { return <path d="M9 6l6 6-6 6" />; } },
    ChevronLeft: { sw: 2.4, cap: 'round', join: 'round', draw: function () { return <path d="M15 6l-6 6 6 6" />; } },
    Close: { sw: 2.4, cap: 'round', draw: function () { return <path d="M6 6l12 12M18 6L6 18" />; } },
    DollarCircle: {
      sw: 2, cap: 'round',
      draw: function () {
        return [
          <circle key="a" cx="12" cy="12" r="9.5" />,
          <path key="b" d="M15 9.2c-.5-1-1.6-1.6-3-1.6-1.7 0-2.8.9-2.8 2.1 0 2.9 5.8 1.5 5.8 4.4 0 1.2-1.2 2.2-3 2.2-1.5 0-2.6-.7-3.1-1.7M12 6v1.6M12 16.3V18" />,
        ];
      },
    },
    UserCircle: {
      sw: 2,
      draw: function () {
        return [
          <circle key="a" cx="12" cy="12" r="9.5" />,
          <circle key="b" cx="12" cy="10" r="3" />,
          <path key="c" d="M6.5 18.5c1.3-2 3.2-3 5.5-3s4.2 1 5.5 3" />,
        ];
      },
    },
    Ticket: {
      sw: 2, join: 'round',
      draw: function (p) {
        var out = [<path key="a" d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4V8z" />];
        if (p.dashed) out.push(<path key="b" d="M10 9v6" strokeDasharray="2 2" />);
        return out;
      },
    },
    Bell: { sw: 2, join: 'round', draw: function () { return [<path key="a" d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16z" />, <path key="b" d="M10 20.5a2 2 0 0 0 4 0" />]; } },
    Home: { sw: 2, join: 'round', draw: function () { return <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5z" />; } },
    Book: { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><path d="M12 5c-3-2-6-2-9-1v15c3-1 6-1 9 1 3-2 6-2 9-1V4c-3-1-6-1-9 1z"/><path d="M12 5v15"/></React.Fragment>; } },
    Cart: { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><path d="M3 3h2l2 12h11l3-9H6"/><circle cx="9" cy="20" r="1"/><circle cx="18" cy="20" r="1"/></React.Fragment>; } },
    Lock: { sw: 1.8, cap: 'round', draw: function () { return <React.Fragment><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3M12 15v2"/></React.Fragment>; } },
    Heart: { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"/>; } },
    MenuList: { sw: 2, cap: 'round', draw: function () { return <path d="M4 6h16M4 12h16M4 18h10" />; } },
    Receipt: { sw: 2, join: 'round', draw: function () { return [<path key="a" d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" />, <path key="b" d="M9 8h6M9 12h6" />]; } },
    Star: { sw: 2, join: 'round', draw: function () { return <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3z" />; } },
    Calendar: { sw: 2, cap: 'round', draw: function () { return [<rect key="a" x="3.5" y="5" width="17" height="15" rx="2" />, <path key="b" d="M3.5 10h17M8 3v4M16 3v4" />]; } },
    Gift: {
      sw: 2, join: 'round',
      draw: function () {
        return [
          <rect key="a" x="3.5" y="9" width="17" height="11" rx="1.5" />,
          <path key="b" d="M2.5 9h19M12 9v11M12 9c-2-4-6-4-6-1.5S12 9 12 9zM12 9c2-4 6-4 6-1.5S12 9 12 9z" />,
        ];
      },
    },
    Scan: {
      sw: 2,
      draw: function () {
        return [
          <rect key="a" x="3" y="3" width="7" height="7" rx="1" />,
          <rect key="b" x="14" y="3" width="7" height="7" rx="1" />,
          <rect key="c" x="3" y="14" width="7" height="7" rx="1" />,
          <path key="d" d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" />,
        ];
      },
    },
    Locate: {
      sw: 2.2, cap: 'round',
      draw: function () {
        return [<circle key="a" cx="12" cy="12" r="3" />, <path key="b" d="M12 2v3M12 19v3M2 12h3M19 12h3" />, <circle key="c" cx="12" cy="12" r="7" />];
      },
    },
    // Not in the design (same stroke language):
    Clock: { sw: 2, cap: 'round', join: 'round', draw: function () { return [<circle key="a" cx="12" cy="12" r="9.5" />, <path key="b" d="M12 7.5V12l3 2" />]; } },
    Info: { sw: 2, cap: 'round', draw: function () { return [<circle key="a" cx="12" cy="12" r="9.5" />, <path key="b" d="M12 11v5.5M12 7.6v.01" />]; } },
    ImagePlaceholder: {
      sw: 2, cap: 'round', join: 'round',
      draw: function () {
        return [<rect key="a" x="3.5" y="4.5" width="17" height="15" rx="2" />, <circle key="b" cx="9" cy="10" r="1.75" />, <path key="c" d="M20.5 15.5l-4.5-4.5L6 19.5" />];
      },
    },
    // Filled glyph: the select caret of the Day / Time pickers.
    CaretDown: { fill: true, draw: function () { return <path d="M6 9h12l-6 7z" />; } },
  };
  // Aliases used by the brief and the design.
  SPECS.Sparkles = { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><path d="m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3Z" /><path d="M20 2v4M18 4h4M3 19v3M1.5 20.5h3" /></React.Fragment>; } };
  SPECS.Refresh = { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><path d="M20 7v5h-5M4 17v-5h5M19 11A7 7 0 0 0 7 5L4 8M5 13a7 7 0 0 0 12 6l3-3" /></React.Fragment>; } };
  SPECS.Share = { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m9 10.5 6-4M9 13.5l6 4" /></React.Fragment>; } };
  SPECS.Envelope = { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 6 9 7 9-7" /></React.Fragment>; } };
  SPECS.Phone = { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <path d="m5 3 4 4-2 3a15 15 0 0 0 7 7l3-2 4 4-2 3C10 22 2 14 2 5l3-2Z" />; } };
  SPECS.Shield = { sw: 1.8, cap: 'round', join: 'round', draw: function () { return <React.Fragment><path d="m12 2 9 4v6c0 5-9 10-9 10S3 17 3 12V6l9-4Z" /><path d="m8 12 3 3 5-6" /></React.Fragment>; } };
  var ALIASES = { Account: 'User', Profile: 'User', MapPin: 'Pin', QR: 'Scan', Coupon: 'Ticket', Orders: 'Receipt', Rewards: 'Star', Payment: 'DollarCircle', Contact: 'UserCircle' };

  function makeIcon(name, spec) {
    function Icon(props) {
      var p = props || {};
      var size = p.size == null ? 24 : p.size;
      var color = p.color || 'currentColor';
      var filled = spec.fill || (name === 'Home' && p.filled);
      var attrs = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        className: p.className ? 'oo-icon ' + p.className : 'oo-icon',
        style: p.style,
        focusable: 'false',
        fill: filled ? color : 'none',
      };
      if (!filled) {
        attrs.stroke = color;
        attrs.strokeWidth = p.strokeWidth == null ? spec.sw : p.strokeWidth;
        if (spec.cap) attrs.strokeLinecap = spec.cap;
        if (spec.join) attrs.strokeLinejoin = spec.join;
      }
      if (p.title) {
        attrs.role = 'img';
        attrs['aria-label'] = p.title;
      } else {
        attrs['aria-hidden'] = 'true';
      }
      return React.createElement('svg', attrs, spec.draw(p));
    }
    Icon.displayName = 'Icon' + name;
    return Icon;
  }

  var icons = {};
  Object.keys(SPECS).forEach(function (name) { icons[name] = makeIcon(name, SPECS[name]); });
  Object.keys(ALIASES).forEach(function (alias) { icons[alias] = icons[ALIASES[alias]]; });

  // ---- payment brand marks (W5-FIX #6; see the header) -----------------------------------------------------------
  // name → { box: [x, y, w, h] (the viewBox), draw } (an SVG) | { w, h (its pixels), src (a data URI) } (a raster).
  var BRANDS = {
    BrandStripe: {
      box: [0, 0, 360, 150],
      draw: function () {
        return [
          <path key="0" fill="#533AFD" d="M360 77.4001C360 51.8001 347.6 31.6001 323.9 31.6001C300.1 31.6001 285.7 51.8001 285.7 77.2001C285.7 107.3 302.7 122.5 327.1 122.5C339 122.5 348 119.8 354.8 116V96.0001C348 99.4001 340.2 101.5 330.3 101.5C320.6 101.5 312 98.1001 310.9 86.3001H359.8C359.8 85.0001 360 79.8001 360 77.4001ZM310.6 67.9001C310.6 56.6001 317.5 51.9001 323.8 51.9001C329.9 51.9001 336.4 56.6001 336.4 67.9001H310.6Z" fillRule="evenodd" clipRule="evenodd" />,
          <path key="1" fill="#533AFD" d="M247.1 31.6001C237.3 31.6001 231 36.2001 227.5 39.4001L226.2 33.2001H204.2V149.8L229.2 144.5L229.3 116.2C232.9 118.8 238.2 122.5 247 122.5C264.9 122.5 281.2 108.1 281.2 76.4001C281.1 47.4001 264.6 31.6001 247.1 31.6001ZM241.1 100.5C235.2 100.5 231.7 98.4001 229.3 95.8001L229.2 58.7001C231.8 55.8001 235.4 53.8001 241.1 53.8001C250.2 53.8001 256.5 64.0001 256.5 77.1001C256.5 90.5001 250.3 100.5 241.1 100.5Z" fillRule="evenodd" clipRule="evenodd" />,
          <path key="2" fill="#533AFD" d="M169.8 25.7L194.9 20.3V0L169.8 5.3V25.7Z" fillRule="evenodd" clipRule="evenodd" />,
          <path key="3" fill="#533AFD" d="M194.9 33.3H169.8V120.8H194.9V33.3Z" />,
          <path key="4" fill="#533AFD" d="M142.9 40.7L141.3 33.3H119.7V120.8H144.7V61.5C150.6 53.8 160.6 55.2 163.7 56.3V33.3C160.5 32.1 148.8 29.9 142.9 40.7Z" fillRule="evenodd" clipRule="evenodd" />,
          <path key="5" fill="#533AFD" d="M92.8999 11.6001L68.4999 16.8001L68.3999 96.9001C68.3999 111.7 79.4999 122.6 94.2999 122.6C102.5 122.6 108.5 121.1 111.8 119.3V99.0001C108.6 100.3 92.7999 104.9 92.7999 90.1001V54.6001H111.8V33.3001H92.7999L92.8999 11.6001Z" fillRule="evenodd" clipRule="evenodd" />,
          <path key="6" fill="#533AFD" d="M25.3 58.7001C25.3 54.8001 28.5 53.3001 33.8 53.3001C41.4 53.3001 51 55.6001 58.6 59.7001V36.2001C50.3 32.9001 42.1 31.6001 33.8 31.6001C13.5 31.6001 0 42.2001 0 59.9001C0 87.5001 38 83.1001 38 95.0001C38 99.6001 34 101.1 28.4 101.1C20.1 101.1 9.5 97.7001 1.1 93.1001V116.9C10.4 120.9 19.8 122.6 28.4 122.6C49.2 122.6 63.5 112.3 63.5 94.4001C63.4 64.6001 25.3 69.9001 25.3 58.7001Z" fillRule="evenodd" clipRule="evenodd" />,
        ];
      },
    },
    BrandWindcave: {
      box: [32, 32, 204, 44],
      draw: function () {
        return [
          <polygon key="0" fill="#ff4438" points="107.64 58.14 102.72 42.76 98.67 42.76 93.72 58.14 88.87 42.76 83.65 42.76 91.63 66.01 95.68 66.01 100.63 50.5 105.58 66.01 109.66 66.01 117.64 42.76 112.52 42.76 107.64 58.14" />,
          <path key="1" fill="#ff4438" d="M158.11,51.73c-1.16-1.69-3.16-2.52-5.32-2.52-4.38,0-7.77,3.75-7.77,8.6s3.39,8.6,7.77,8.6c2.16,0,4.15-.9,5.32-2.59v2.19h4.82v-23.25h-4.82v8.97ZM154.06,62.06c-2.36,0-4.22-1.89-4.22-4.25s1.86-4.25,4.22-4.25,4.22,1.89,4.22,4.25-1.86,4.25-4.22,4.25Z" />,
          <polygon key="2" fill="#ff4438" points="208.2 59.97 204.35 49.6 199.24 49.6 205.91 66.01 210.36 66.01 217.04 49.6 212.06 49.6 208.2 59.97" />,
          <path key="3" fill="#ff4438" d="M170.1,57.81c0-2.49,1.89-4.25,4.45-4.25,1.23,0,2.42.4,3.55,1.2v-4.55c-1.2-.7-2.69-1.03-4.12-1.03-5.15,0-8.77,3.69-8.77,8.64s3.65,8.64,8.7,8.64c1.43,0,2.89-.3,4.22-1.06v-4.58c-1.03.76-2.29,1.26-3.59,1.26-2.56,0-4.45-1.76-4.45-4.25Z" />,
          <path key="4" fill="#ff4438" d="M192.82,51.73c-1.16-1.69-3.16-2.52-5.32-2.52-4.38,0-7.77,3.75-7.77,8.6s3.39,8.6,7.77,8.6c2.16,0,4.15-.9,5.32-2.59v2.19h4.75v-16.41h-4.75v2.13ZM188.77,62.06c-2.36,0-4.22-1.89-4.22-4.25s1.86-4.25,4.22-4.25,4.22,1.89,4.22,4.25-1.86,4.25-4.22,4.25Z" />,
          <path key="5" fill="#ff4438" d="M233.98,57.94c0-5.15-3.42-8.77-8.37-8.77s-8.5,3.69-8.5,8.64,3.59,8.64,8.7,8.64c3.59,0,6.25-1.66,7.61-4.35l-4.09-1.63c-.76,1.23-1.69,1.99-3.52,1.99-2.02,0-3.79-1.39-3.95-3.32h12.06c.03-.23.07-.63.07-1.2ZM221.89,55.98c.17-1.56,1.76-3.02,3.69-3.02s3.42,1.26,3.65,3.02h-7.34Z" />,
          <rect key="6" fill="#ff4438" x="118.71" y="49.6" width="4.85" height="16.41" />,
          <path key="7" fill="#ff4438" d="M136.68,49.17c-2.23,0-3.82.93-4.85,2.52v-2.09h-4.85v16.41h4.85v-8.7c0-2.29,1.23-3.69,3.09-3.69s2.96,1.23,2.96,3.39v9h4.85v-10.13c0-4.15-2.29-6.71-6.05-6.71Z" />,
          <path key="8" fill="#ff4438" d="M121.22,42.31c-1.55,0-2.75,1.29-2.75,2.75s1.2,2.75,2.75,2.75,2.75-1.29,2.75-2.75-1.2-2.75-2.75-2.75Z" />,
          <path key="9" fill="#ff4438" d="M53.73,33.98c-10.88,0-19.75,8.83-19.75,19.79s8.98,19.79,19.75,19.79,19.83-9.02,19.83-19.79-8.83-19.79-19.83-19.79ZM40.55,41.98c2.41-1.27,5.11-1.86,8.23-1.86,8.79,0,15.55,6.85,15.55,14.96,0,2.97-.99,6.13-2.89,8.67h-3.8c1.5-2.3,2.37-4.99,2.37-7.84,0-8.03-6.49-14.53-14.84-14.53-1.5,0-3.09.28-4.63.79v-.2ZM38.18,45.15c2.33-1.07,4.75-1.5,6.77-1.5,7.08,0,12.59,5.7,12.59,12.27,0,2.89-1.03,5.66-2.85,7.84h-3.92c1.5-2.14,2.41-4.75,2.41-7.36,0-6.37-5.23-11.48-11.83-11.48-.99,0-1.94.12-3.17.43v-.2ZM37.07,47.88c1.46-.51,2.77-.71,4.16-.71,5.3,0,9.46,4.16,9.46,9.38,0,2.65-1.07,5.22-2.93,7.2h-4.12c1.58-1.62,2.73-3.84,2.73-6.41,0-4.95-3.92-8.9-9.3-9.26v-.2ZM39.76,63.87c-2.02-2.81-3.25-6.29-3.25-10.09,0-1.23.12-2.37.32-3.52,4.2.36,7.12,3.4,7.12,7.08,0,2.69-1.66,5.11-4.2,6.53ZM53.73,71.03c-4.63,0-8.9-1.86-12.03-4.91h24.1c-3.09,3.01-7.36,4.91-12.07,4.91ZM67.82,63.75h-3.4c1.58-2.61,2.45-5.7,2.45-8.67,0-9.34-7.52-17.18-17.73-17.18-1.11,0-2.37.12-4,.48v-.2c2.69-1.23,5.66-1.7,8.39-1.7,9.82,0,17.49,7.84,17.49,17.3,0,3.68-1.19,7.12-3.21,9.97Z" />,
        ];
      },
    },
    BrandPinPayments: {
      box: [48, 48, 466, 90],
      draw: function () {
        return [
          <path key="0" fill="#556677" d="M145.8,70.9c2.7,0,5,0.4,6.9,1.2c1.9,0.8,3.4,1.8,4.6,3.1c1.2,1.3,2.1,2.8,2.6,4.4 c0.5,1.7,0.8,3.4,0.8,5.1c0,1.7-0.3,3.4-0.8,5.1c-0.5,1.7-1.4,3.1-2.6,4.4c-1.2,1.3-2.7,2.3-4.6,3.1c-1.9,0.8-4.2,1.2-6.9,1.2h-10 V114h-9.5V70.9H145.8z M143.2,91.2c1.1,0,2.1-0.1,3.1-0.2c1-0.2,1.9-0.5,2.7-0.9c0.8-0.5,1.4-1.1,1.8-2c0.5-0.8,0.7-2,0.7-3.3 c0-1.4-0.2-2.5-0.7-3.3s-1.1-1.5-1.8-2c-0.8-0.5-1.6-0.8-2.7-0.9c-1-0.2-2.1-0.2-3.1-0.2h-7.4v12.9L143.2,91.2L143.2,91.2z" />,
          <path key="1" fill="#556677" d="M165,70.9h8.6V78H165V70.9z M173.6,114H165V82.8h8.6V114z" />,
          <path key="2" fill="#556677" d="M188.5,82.8v4.3h0.2c1.1-1.8,2.5-3.1,4.2-4c1.7-0.8,3.5-1.2,5.3-1.2c2.3,0,4.2,0.3,5.6,0.9 s2.6,1.5,3.5,2.6c0.8,1.1,1.4,2.5,1.8,4c0.3,1.6,0.5,3.4,0.5,5.3V114H201V96.4c0-2.6-0.4-4.5-1.2-5.8c-0.8-1.3-2.2-1.9-4.3-1.9 c-2.3,0-4,0.7-5.1,2.1c-1,1.4-1.6,3.7-1.6,6.9V114h-8.6V82.8H188.5z" />,
          <path key="3" fill="#556677" d="M253.2,70.9c2.7,0,5,0.4,6.9,1.2c1.9,0.8,3.4,1.8,4.6,3.1c1.2,1.3,2.1,2.8,2.6,4.4 c0.5,1.7,0.8,3.4,0.8,5.1c0,1.7-0.3,3.4-0.8,5.1c-0.5,1.7-1.4,3.1-2.6,4.4c-1.2,1.3-2.7,2.3-4.6,3.1c-1.9,0.8-4.2,1.2-6.9,1.2h-10 V114h-9.5V70.9H253.2z M250.6,91.2c1.1,0,2.1-0.1,3.1-0.2c1-0.2,1.9-0.5,2.7-0.9c0.8-0.5,1.4-1.1,1.8-2c0.5-0.8,0.7-2,0.7-3.3 c0-1.4-0.2-2.5-0.7-3.3c-0.5-0.8-1.1-1.5-1.8-2c-0.8-0.5-1.6-0.8-2.7-0.9c-1-0.2-2.1-0.2-3.1-0.2h-7.4v12.9L250.6,91.2L250.6,91.2 z" />,
          <path key="4" fill="#556677" d="M272.3,87.4c0.9-1.3,2-2.4,3.4-3.2c1.4-0.8,2.9-1.4,4.6-1.7c1.7-0.3,3.4-0.5,5.2-0.5 c1.6,0,3.2,0.1,4.8,0.3c1.6,0.2,3.1,0.7,4.4,1.3s2.4,1.5,3.3,2.7c0.8,1.1,1.3,2.7,1.3,4.6v16.2c0,1.4,0.1,2.8,0.2,4 c0.2,1.3,0.4,2.3,0.8,2.9h-8.7c-0.2-0.5-0.3-1-0.4-1.5c-0.1-0.5-0.2-1-0.2-1.5c-1.4,1.4-3,2.4-4.8,3c-1.9,0.6-3.7,0.8-5.7,0.8 c-1.5,0-2.9-0.2-4.2-0.5c-1.3-0.4-2.4-0.9-3.4-1.7c-1-0.8-1.7-1.7-2.3-2.9c-0.5-1.2-0.8-2.6-0.8-4.2c0-1.8,0.3-3.2,0.9-4.4 c0.6-1.1,1.4-2.1,2.4-2.7c1-0.7,2.1-1.2,3.4-1.5c1.3-0.3,2.5-0.6,3.8-0.8c1.3-0.2,2.6-0.4,3.8-0.5c1.2-0.1,2.4-0.3,3.3-0.5 c1-0.2,1.7-0.6,2.3-1.1c0.6-0.5,0.8-1.1,0.8-2c0-0.9-0.1-1.7-0.5-2.2s-0.7-1-1.2-1.3c-0.5-0.3-1.1-0.5-1.8-0.6s-1.4-0.2-2.1-0.2 c-1.7,0-3,0.4-4,1.1c-1,0.7-1.5,1.9-1.7,3.6h-8.6C270.9,90.4,271.4,88.7,272.3,87.4 M289.2,99.5c-0.5,0.2-1.1,0.3-1.8,0.5 c-0.6,0.1-1.3,0.2-2,0.3c-0.7,0.1-1.4,0.2-2.1,0.3c-0.6,0.1-1.3,0.3-1.9,0.5c-0.6,0.2-1.2,0.5-1.6,0.8s-0.8,0.8-1.1,1.3 c-0.3,0.5-0.4,1.2-0.4,2c0,0.8,0.1,1.4,0.4,1.9c0.3,0.5,0.7,0.9,1.1,1.2c0.5,0.3,1,0.5,1.7,0.6c0.6,0.1,1.3,0.2,2,0.2 c1.7,0,3-0.3,3.9-0.8c0.9-0.6,1.6-1.2,2.1-2c0.4-0.8,0.7-1.6,0.8-2.4c0.1-0.8,0.1-1.4,0.1-1.9v-3.2 C290.2,99.1,289.8,99.3,289.2,99.5" />,
          <path key="5" fill="#556677" d="M317.3,123.3c-1.6,1.1-3.9,1.6-6.9,1.6c-0.9,0-1.8,0-2.6-0.1c-0.9-0.1-1.7-0.1-2.6-0.2v-7.1 c0.8,0.1,1.6,0.2,2.5,0.2c0.8,0.1,1.7,0.1,2.5,0.1c1.1-0.1,2-0.6,2.5-1.3c0.5-0.8,0.8-1.6,0.8-2.5c0-0.7-0.1-1.3-0.4-1.9 l-10.9-29.3h9.1l7.1,21.4h0.1l6.8-21.4h8.9l-13,35.1C320.3,120.4,319,122.2,317.3,123.3" />,
          <path key="6" fill="#556677" d="M345.5,82.8V87h0.1c1.1-1.6,2.5-2.9,4.1-3.7c1.6-0.9,3.4-1.3,5.5-1.3c2,0,3.8,0.4,5.4,1.1 c1.6,0.8,2.9,2.1,3.7,4c0.9-1.4,2.2-2.6,3.8-3.6c1.6-1,3.5-1.6,5.6-1.6c1.6,0,3.2,0.2,4.6,0.6c1.4,0.4,2.6,1,3.6,1.9 c1,0.9,1.8,2,2.4,3.5c0.6,1.4,0.8,3.1,0.8,5.2V114h-8.6V96.3c0-1,0-2-0.1-3c-0.1-0.9-0.3-1.7-0.7-2.4c-0.4-0.7-0.9-1.2-1.6-1.6 c-0.7-0.4-1.7-0.6-2.9-0.6s-2.2,0.2-2.9,0.7c-0.7,0.5-1.3,1.1-1.8,1.8c-0.4,0.7-0.7,1.6-0.8,2.5c-0.1,0.9-0.2,1.9-0.2,2.9V114 h-8.6V96.5c0-0.9,0-1.8-0.1-2.7c0-0.9-0.2-1.7-0.5-2.5c-0.3-0.8-0.8-1.4-1.5-1.8c-0.7-0.5-1.7-0.7-3.1-0.7c-0.4,0-0.9,0.1-1.6,0.3 c-0.7,0.2-1.3,0.5-1.9,1c-0.6,0.5-1.2,1.2-1.6,2.2s-0.7,2.2-0.7,3.7V114h-8.6V82.8H345.5z" />,
          <path key="7" fill="#556677" d="M400.3,106.5c1.3,1.2,3.1,1.9,5.6,1.9c1.7,0,3.2-0.4,4.5-1.3c1.2-0.9,2-1.8,2.3-2.7h7.5 c-1.2,3.7-3.1,6.4-5.6,8c-2.5,1.6-5.5,2.4-9.1,2.4c-2.5,0-4.7-0.4-6.6-1.2c-2-0.8-3.6-1.9-5-3.3c-1.4-1.4-2.4-3.2-3.2-5.2 c-0.7-2-1.1-4.2-1.1-6.6c0-2.3,0.4-4.5,1.1-6.5c0.8-2,1.9-3.8,3.3-5.2c1.4-1.5,3.1-2.6,5-3.5s4.1-1.3,6.5-1.3c2.7,0,5,0.5,6.9,1.5 c2,1,3.6,2.4,4.9,4.1c1.3,1.7,2.2,3.7,2.7,5.9c0.6,2.2,0.8,4.5,0.6,6.9h-22.5C398.3,103.3,399,105.3,400.3,106.5 M409.9,90.1 c-1-1.1-2.6-1.7-4.7-1.7c-1.4,0-2.5,0.2-3.4,0.7c-0.9,0.5-1.6,1-2.2,1.7c-0.5,0.7-0.9,1.4-1.1,2.2c-0.2,0.8-0.4,1.4-0.4,2.1h13.9 C411.7,92.9,411,91.2,409.9,90.1" />,
          <path key="8" fill="#556677" d="M433.6,82.8v4.3h0.2c1.1-1.8,2.5-3.1,4.2-4c1.7-0.8,3.5-1.2,5.3-1.2c2.3,0,4.2,0.3,5.6,0.9 c1.5,0.6,2.6,1.5,3.5,2.6c0.8,1.1,1.4,2.5,1.8,4c0.3,1.6,0.5,3.4,0.5,5.3V114h-8.6V96.4c0-2.6-0.4-4.5-1.2-5.8 c-0.8-1.3-2.2-1.9-4.3-1.9c-2.3,0-4,0.7-5.1,2.1c-1,1.4-1.6,3.7-1.6,6.9V114h-8.6V82.8H433.6z" />,
          <path key="9" fill="#556677" d="M478.4,82.8v5.7h-6.3V104c0,1.4,0.2,2.4,0.7,2.9c0.5,0.5,1.5,0.7,2.9,0.7c0.5,0,0.9,0,1.4-0.1 c0.4,0,0.9-0.1,1.3-0.2v6.6c-0.7,0.1-1.5,0.2-2.4,0.2c-0.9,0-1.8,0.1-2.6,0.1c-1.3,0-2.6-0.1-3.8-0.3c-1.2-0.2-2.2-0.5-3.1-1.1 s-1.6-1.3-2.1-2.2c-0.5-1-0.8-2.2-0.8-3.8V88.5h-5.2v-5.7h5.2v-9.4h8.6v9.4L478.4,82.8z" />,
          <path key="10" fill="#556677" d="M489.7,106.2c0.4,0.7,0.9,1.2,1.5,1.6c0.6,0.4,1.3,0.7,2.1,0.9c0.8,0.2,1.6,0.3,2.4,0.3 c0.6,0,1.2-0.1,1.9-0.2c0.7-0.1,1.3-0.4,1.8-0.7c0.5-0.3,1-0.7,1.4-1.2c0.4-0.5,0.5-1.1,0.5-1.9c0-1.3-0.9-2.3-2.6-2.9 c-1.7-0.6-4.1-1.3-7.2-1.9c-1.2-0.3-2.5-0.6-3.7-1c-1.2-0.4-2.2-0.9-3.2-1.5c-0.9-0.6-1.7-1.4-2.2-2.4c-0.6-0.9-0.8-2.1-0.8-3.5 c0-2,0.4-3.7,1.2-5c0.8-1.3,1.8-2.3,3.1-3c1.3-0.7,2.7-1.3,4.3-1.6c1.6-0.3,3.3-0.5,5-0.5s3.3,0.2,4.9,0.5c1.6,0.3,3,0.9,4.3,1.6 c1.2,0.8,2.3,1.8,3.1,3c0.8,1.3,1.3,2.9,1.5,4.8h-8.1c-0.1-1.6-0.7-2.8-1.9-3.3c-1.1-0.6-2.5-0.9-4-0.9c-0.5,0-1,0-1.6,0.1 c-0.6,0.1-1.1,0.2-1.5,0.4c-0.5,0.2-0.9,0.5-1.2,0.9c-0.3,0.4-0.5,0.9-0.5,1.5c0,0.8,0.3,1.4,0.8,1.9c0.6,0.5,1.3,0.9,2.2,1.2 c0.9,0.3,1.9,0.6,3.1,0.8c1.2,0.2,2.4,0.5,3.6,0.8c1.2,0.3,2.5,0.6,3.7,1c1.2,0.4,2.2,0.9,3.2,1.6c0.9,0.7,1.7,1.5,2.2,2.5 c0.6,1,0.8,2.2,0.8,3.7c0,2.1-0.4,3.8-1.2,5.2c-0.8,1.4-1.9,2.5-3.2,3.3c-1.3,0.8-2.8,1.4-4.6,1.8c-1.7,0.3-3.5,0.5-5.2,0.5 c-1.8,0-3.6-0.2-5.3-0.5c-1.7-0.4-3.3-1-4.6-1.8c-1.3-0.8-2.5-2-3.3-3.3c-0.9-1.4-1.3-3.1-1.4-5.2h8.1 C489.2,104.8,489.3,105.6,489.7,106.2" />,
          <path key="11" fill="#CC1111" d="M76.6,67.1c5.2,0,9.4,4.2,9.4,9.4c0,3.9-1.9,10-4.7,16.7c2.4,5.8,5.5,12.2,8.6,18.2 c6.6-12.9,13.3-27.6,13.3-34.9c0-14.6-11.9-26.5-26.5-26.5S50.1,61.8,50.1,76.5c0,14,24.2,55,26.5,58.8c0.8-1.4,4.3-7.3,8.6-15.1 C77.4,106,67.2,85.6,67.2,76.5C67.2,71.3,71.4,67.1,76.6,67.1z" />,
        ];
      },
    },
    BrandAfterpay: { w: 100, h: 21, src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAAAVCAMAAACHQrsCAAACMVBMVEUAAACx++Ox++Ox++Ox++Ox++Ox++Ox++Ox++Ox++Ox++Ox++Ox++Ox++Ox++MAAACx++OFvKosPjhunY55rJwWHxyw+eEBAQFjjX+b3Mep79iHwK4CAwOn7NYoOTSSz7ur8tuQzbmLxbIPFhSZ2cSo7tdTdmur89whLipYfXGQzLg3TkZii34DBQSMxrOZ2sWm69VEYFeBt6YaJSIMEQ8KDgyY2MOj59FRcmcUHRoxRj8+WE+u998GCQh9saCw+uKt9d4KDw2e4MuV1L8qPDap8NmNyLWV079TdWqIwa5Ma2EYIh8EBgWk6NJ/tKNehXiJwq8aJCEkMy6X18IpOjUqOzWq8dp2p5eT0b1VeG0GCAeGvqwNExE8Vk1plYaCuKZnkoQ6U0tFYlkICwpunI0DBARagHSv+OCf4cwSGhdehnk4T0ea28ZXe28YIR4lNTBFYVgRGBV+sqFnk4UuQTsTHBkoODOOybaFvauEu6l5q5uJw7AcKCRHZFuu9t81S0Rwn5AFBwY2TUVCXlUzSEEfKydDXlWg485vnY6AtqV8sJ9ql4ii5c+KxLGCuacsPzl0pJVObmRyoZJ7rp5DX1ae38qX1sFbgXUIDAstQDo/WlF1pZZdhHcXIB1ZfnILEA5JZ118r55kjoBRc2gdKSUeKial6tQiMCw7VEwvQz2l6dM1TEWn7dcvQjyMx7SU0r5cgnZNbWNmkYMmNjEHCgkMEhCS0LyOyraDuqg5UUmPy7cuuswGAAAAD3RSTlMAxvvWmkBjYfpB15kyMflVkJjUAAACpUlEQVR4Xr3V05MkSxjG4Rr2cN+30HaPbdu2vbZt27aNQ+OvO1PRFXG6evpiZ+PMPNcZ8YusrzJT8IuNjAvn/yw8LjJW+E9CGJdIWIKgiY7hkomJ9jcMDKXH3srFm0t9YqKeQW0krmAIeWuQbspP4eIoAHb4qLMicT4Seh5r8anJinQuTgFOJMFccFU/F0GIol6u3OFU7BfKMaNcgVEhq6/LhfWkXdl6u9SnlGXKl46TZL/sm1/nVXJJlir9VLFwX5uz04GGwwwUJcQzkK0SgAtSBjRsKgKw3U0JJcjIggvA0Tx+971/3W4Ut5DNmKVfHUnvMH7Tfed4IYKBdqIie2IS0rUtqLRswyZLdRHO1x6D0SOhsteeBRTsGcR+noHRkpoDqe4cVvIIdqWR5Ku90hTVCpDHABGC7gymncIBMhsSLcigOpODqDgkil0Yk9BBZqGKdJZgPAducjMknsSA6TSySQ6dBVBiY0r5OowwULjAQFbUkLQHRC7Cb72ETDUyS9KFy2ggOQeJ1RXoM0/b2NLejaS1N9B6cxMct2zUCdpJN9r0OxGRlGy13pGTtchdMmV63T0H7vt3QguAB7Qa4eh0shwPgUfj1AsPmsljGC0Tk1pkI9Y8rX+Gqtrnjhc9WgQvVw5iNavQ9VqdCWkrRkM+3+Ctl+TGdzCOMVhE0N/1vhmAS4uYPgB0GwEkidQiIwA+jvKzC8CwGuEXpJLtKPKRpKlxAxeIDz4ndaKc61TsTFaaSM+UQtYXyrUesjTTo0bE5N4hkjSJ8g/qOv5orikjGwdgXl3Gn/pCNBilnvivpkYYZAY/k2Ta+hoU//Ir3FwgTLu7vj3iNec0UkXrKgC/j1Kjv7sEA7/aH2I+9azin9Twr79X/cMFDMvxnizXy7gsb/y/L/vOcQRjAWkAAAAASUVORK5CYII=' },
    BrandLinePay: { w: 238, h: 69, src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAO4AAABFCAYAAABNNdoPAAAHr0lEQVR42uydT0gcVxzHn8WDlEAtWPAQ4gZyMFCqIYH0ouil6aVBe+hFBXuovQRaD710C7UQTzmYQi/xokV76SFac4m9aLWHFgysUqiHghvooRChFkrx0EPnu7vTTGZn3p/5s/tm3/cDg8nszOzbt/Od35/3e2+7BHGenp2pXu/PrLf1sjesZvN8/JsK/tHFvnBetBDsEkVbKPFOUrhui7bk/TlhTxSO+y+xD5zmI3ZBIZmlcN1mmF1QSHopXEIKCIVLCIVLCKFwCSEULiEULiGEwiWEULiEULiEEAqXEJKSbm8rifqUrjhWva2a0/urppPtNrY4Zhvtj+LM2zZTtj1N3+DcCZHvrBtV/5AOBbOD/lTcXBDAqzm9/463jSmOueZtlYj9KwpR+W2/3Pib5KFyouibauP6UZxIHipZMp5UvD07Uzr9L2Wmf1QM9PQZnfP0/NTbnom9s1+pwBQWt1fjBh7L6ck+pimgOIumc+5G4+Y2ZVijb0oJX8u6D9tidd+4MCCWB+cSn//Xv/+IrdMnYrH6sCZkwhg3fGN/zK86hzin++VU57/inT/TPyKO31wS965M1/5PKNwgnwtOYbOaOxffFt8PlyleCrfJZV7h1203cL0pXgo3KmZd4Fduv3g/K73LjqBwm1zmMX7t9rvNAz2vsSMkdDv4meEyY4jprEM+T9XWhvnDPmGGPKuqcofLntWdO35AhVK4/1NqWN55C9pSSfkAgWg3be3o9T/2xN3qw6b9EO29KzO1jHIct/uuiznqk8INgeGhHyy46fHw2HWt8zF+C2s6dOFSLaaNAuIe7b2qLNLAcbf7boh3PKGjEMS/Hs6Dxd/3/q55DxD/2KGY94vyDuCuxxWXHP79tPY5dDB9XwpX7TLvdpDLXDi++n1bWsBRj3PjhYskFuLhKLcbogew6r7bjX3lmMTXo9Mn4r1fll7Yh7b514kS3OBPeuUB374+H3sdPGBuVRaN+87lSQYcImp7DCy3NHHWDkL9+cZiTYQ6Q0e4zvZwuWaVZVax2dXfl14zToxB4AHIjsMDIwmuzw6aEKyqspazCFcUAsNYb5yLrRKRCVunB1J3eLp/VHkNeAQyfDeewjUHiaoSu6H1qG78Iy+ODJNUtElj8S2JRYQbLhu2qsff1yWi3deOkxnjRrvMmIhwrQ3vnbYMsyosHg6SgVlFsqwyOAwJFzFtq0T7PA5/LG0nXovKnAMkzWSu/CPPoieFwn0uoAXR+sqqpQyucdlW8V7yrFE4voOFGvH2qUR7FMraQgAqtzNoKRE/ZyFytANb3LXgNcQJ987FW5L4/jRxfOuCcHeFfqUUXGYMD1UK9hlnhaWlnBCnSqDxlm67yUKrElGYHgj3M5j0wnlIYpnOGQ63JS77jesi6RUWIYQue3CsJ4xtXYlxMVb7hcHxG4K/E9t2YOHCSRtZRhhgKOduxLxeXOfmwaeR8bIuuIYsFo3yBNRJqX0KV8GCgRUtNSwvaRMQyAfHy037ZUMqsLQytzPumqbijQNtCyapVEkptDXtwgGuZJXfF/qFFhgemqCE2iXaB03WURWr6lgvP1ZN4y7LCMazqqTUWko32SXhVgxd5hW6zK0FyZq3KouRllO10oau9UqTDFKtkTUTGNrKMynlmnDBfaFfFwzRLlFOrbGycHWTxqFprKi51X0c+1p9GZ7R3JNSPq4NB00K9cqNwXi3CFhbay0roIfgYMGwJS1C0HGjg1xKOce3Hpuexmaop/tHxMj51dRuPYUbfZMj3t3okM8DD2LV1sbFTetL8gCQETUcE4VObbHOZ4qbqIDryx5CWSSlXHSVfTYtutmxbGxXim1cODC7CTe76XBM1DFpxnJ1LWbeSSmXhQswD7bKCLM4yGqGYemWBz+MFQ1iTyz/mtVDJElyKauklKuucthl3qEkigFcVFXNMASM4/zsL8ZWpxv7swRJKlVBSFT7s8TlWmXEhxgiYsFFAYAYZTXDdaH21eLPcgvaIktSJXGx6SqbsSCKV5vsLJ/8tm5NW2RDQ2GyTEqZWty008/SLoqWJ77L3I6Ci07u11wsHcZ8yxasu4xEk27cvJaxm2wi3LTFCLi5MN+1auH94FdVtaPgopP7NRcwvITx2KSzjrICWW64v6p24Lgsk1KtdpVhzUoW3w8mVVU24f++sJWcpSiskIGF3+A2mxRuID7OWkA67nIe1pYx7otMCq74aMRho/opiqyHP6JEc/OgrEz6+CWVcXXQwTjUFJ2JC6rJCXm7yi7QaVVVLXEXkywtmhVI+NSt71pt3eLgsA+s/VHowQLrl7UFlK05Vc8+P6NwW4BfVTXLrijWA8Sve24lqlU51jMeAqKrLIdVVUSLaUliqp682qNw2+AyEyJ1kWUVWXmK1gbh2poM2hVmE+/Zh44hmywP8kpKBYXbih++qojoCqUvE54Hvta4oVdTtHlBqIeIVhO+lhVVYfGv9XUyM5LF3PNMSvl08Stwl56dKVSMjbEnzEUr+7GyuePljneVCSkcsplBeSelKFxCEiJLSplMPkgDx3EJMaT/x7m2t4EWl5ACQuESQuESQihcQgiFSwiFSwihcAkhFC4hFC7pWL5jFxSSKoXrNquC60oXkfn/BBgA4x/qQpKXCMsAAAAASUVORK5CYII=' },
    BrandPayPal: { w: 226, h: 142, src: 'data:image/jpeg;base64,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' },
    BrandPayrix: { w: 300, h: 64, src: 'data:image/png;base64,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' },
  };
  function makeBrand(name, spec) {
    function Brand(props) {
      var p = props || {};
      var h = p.height == null ? 20 : p.height;
      var ratio = spec.src ? spec.w / spec.h : spec.box[2] / spec.box[3];
      var attrs = {
        className: p.className ? 'oo-brand ' + p.className : 'oo-brand', style: p.style,
        width: Math.round(h * ratio * 10) / 10, height: h, 'aria-hidden': 'true',
      };
      if (spec.src) return React.createElement('img', Object.assign(attrs, { src: spec.src, alt: '' }));
      return React.createElement('svg', Object.assign(attrs, { viewBox: spec.box.join(' '), focusable: 'false' }), spec.draw());
    }
    Brand.displayName = name;
    return Brand;
  }
  Object.keys(BRANDS).forEach(function (name) { icons[name] = makeBrand(name, BRANDS[name]); });

  OO.icons = icons;
  OO.ui.icons = icons;

  // <OO.ui.Icon name="Bag" size={20} /> — render an icon by name (string). Unknown names render nothing.
  OO.ui.Icon = function Icon(props) {
    var C = props && Object.prototype.hasOwnProperty.call(icons, props.name) ? icons[props.name] : null;
    return C ? React.createElement(C, props) : null;
  };
})();


// ===================== src/ui/primitives.jsx =====================
// src/ui/primitives.jsx: OO.ui primitives, the presentational components built on the --oo-* tokens (styles in
// styles/primitives.css). No data fetching: props in, callbacks out. Owner: T2b (UI kit).
//
// RESPONSIVE CONVENTION (shared with every styles/*.css):
//   Mobile-first. Desktop rules apply at >= 900px AND only under an .oo-wide ancestor:
//       @media (min-width: 900px) { .oo-wide .oo-x { … } }
//   OO.ui.PageFrame puts .oo-wide on the online-mode frame. App mode never carries it, so the app's
//   phone-width column keeps the mobile layout on any screen. In JS the same switch is OO.ui.useWide()
//   (online mode + the media query); <OO.ui.ViewportContext.Provider value="mobile"|"desktop"> overrides it
//   (the UI gallery forces both). CSS utilities: .oo-only-wide / .oo-only-narrow.
//
// Components (each has a prop doc below): Button IconButton Card Chip ChipRow Badge QtyStepper OptionGroup
// OptionRow PriceRows TextInput TextArea SelectField CheckRow InlineNotice Toast StepBar ProductCard
// ProductGrid DealCard CategoryTile CategoryGrid FloatingCartBar Sheet SheetHeading EmptyState Skeleton
// PoweredBySipo Avatar SectionTitle Breadcrumbs SearchField GuestPicker DateStrip TimeGrid ProgressBar
// StatusPill TotalBlock BottomBarCTA. Helpers: OO.ui.cx, OO.ui.useWide, OO.ui.ViewportContext, OO.ui.renderIcon.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.ui = OO.ui || {};
  var ui = OO.ui;

  // ---- helpers ------------------------------------------------------------------------------------------
  function cx() {
    var out = [];
    for (var i = 0; i < arguments.length; i++) if (arguments[i]) out.push(arguments[i]);
    return out.join(' ');
  }
  // icon: an OO.icons name ('Bag') or a ready React element. props go to a named icon.
  function renderIcon(icon, props) {
    if (!icon) return null;
    if (typeof icon === 'string') {
      var C = OO.icons && Object.prototype.hasOwnProperty.call(OO.icons, icon) ? OO.icons[icon] : null;
      return C ? React.createElement(C, props || {}) : null;
    }
    return icon;
  }
  // id, aria-* and data-* props pass through to the root element.
  function passthrough(props) {
    var out = {};
    Object.keys(props).forEach(function (k) {
      if (k === 'id' || k.indexOf('aria-') === 0 || k.indexOf('data-') === 0) out[k] = props[k];
    });
    return out;
  }
  function useDomId(prefix, given) {
    var auto = React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    return given || prefix + auto;
  }

  // Offline image variants are optional and scoped to this page's store. Older shells, unmapped sources,
  // unsupported WebP and failed variants retain the original URL and the caller's existing error fallback.
  function imageEntry(map, src) {
    if (!map || !src) return null;
    if (Object.prototype.hasOwnProperty.call(map, src)) return map[src];
    // Legacy menu URLs may contain spaces. Match the browser's encoded URL without rewriting the menu value.
    try {
      var encoded = new URL(src, window.location && window.location.href || 'https://localhost/').href;
      return Object.prototype.hasOwnProperty.call(map, encoded) ? map[encoded] : null;
    } catch (x) { return null; }
  }
  ui.imageSources = function (src, sizes) {
    var map = OO.boot && OO.boot.images && OO.boot.images.assets;
    var row = imageEntry(map, src);
    if (!row || !Array.isArray(row.variants) || !sizes) return { src: src };
    var list = row.variants.filter(function (v) { return Array.isArray(v) && v[0] > 0 && v[0] <= row.width && /^[a-f0-9]{64}\.webp$/.test(v[2]); });
    if (!list.length) return { src: src };
    var asset = function (v) { return OO.asset('online-order/images/' + v[2]); };
    var set = list.map(function (v) { return asset(v) + ' ' + v[0] + 'w'; });
    var fallback = src;
    if (typeof src === 'string' && /[\s,]/.test(src)) {
      try { fallback = new URL(src, window.location && window.location.href || 'https://localhost/').href.replace(/,/g, '%2C'); } catch (x) { /* keep the existing source */ }
    }
    if (!list.some(function (v) { return v[0] === row.width; })) set.push(fallback + ' ' + row.width + 'w');
    return { src: src, srcSet: set.join(', '), sizes: sizes };
  };
  ui.iconSource = function (src, size) {
    var icons = OO.boot && OO.boot.images && OO.boot.images.icons;
    var sizes = icons && Object.prototype.hasOwnProperty.call(icons, src) ? icons[src] : null;
    var file = sizes && sizes[String(size || 32)];
    return typeof file === 'string' && /^[a-f0-9]{64}\.png$/.test(file) ? OO.asset('online-order/icons/' + file) : src;
  };
  ui.Image = function Image(props) {
    var failed = React.useState(null);
    var measured = React.useState(null), element = React.useRef(null);
    var attrs = Object.assign({}, props);
    var original = props.src;
    var map = OO.boot && OO.boot.images && OO.boot.images.assets;
    var row = imageEntry(map, original);
    // Preserve the already downloaded, full-detail FIRST HERO through the static-to-React handover.
    // A server cannot measure object-fit:cover on a tall phone. Selecting the full-width lossless
    // variant (or the original) avoids a soft first frame and an original + resized-image race.
    // This applies only on the exact published website path whose HTML was pre-rendered.
    var pre = OO.boot && OO.boot.websitePrerender;
    var publicCover = props.cover === true && props.fetchpriority === 'high' && pre && pre.schema === 1 && OO.landingMode
      && window.location && window.location.pathname === pre.path && row && row.width > 0;
    var cover = props.cover === true && !publicCover && row && row.width > 0 && row.height > 0 && typeof document !== 'undefined' && failed[0] !== original;
    // object-fit:cover can enlarge a landscape source well beyond the element's width on a tall phone.
    // Measure before assigning any URL, then let srcset account for device pixel ratio. No original+variant race.
    React.useEffect(function () {
      if (!cover || !element.current) return undefined;
      var active = true;
      function measure() {
        var rect = element.current && element.current.getBoundingClientRect();
        if (!active || !rect || !rect.width || !rect.height) return;
        var width = Math.ceil(Math.max(rect.width, rect.height * row.width / row.height) * (props.zoom ? 1.06 : 1));
        measured[1](function (previous) { return previous && previous.source === original && previous.width === width ? previous : { source: original, width: width }; });
      }
      measure();
      var observer = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(measure) : null;
      if (observer) observer.observe(element.current);
      else if (window.addEventListener) window.addEventListener('resize', measure);
      return function () { active = false; if (observer) observer.disconnect(); else if (window.removeEventListener) window.removeEventListener('resize', measure); };
    }, [original, !!cover, row && row.width, row && row.height, props.zoom]);
    var measuredSize = measured[0] && measured[0].source === original ? measured[0].width + 'px' : null;
    var sources = failed[0] === original ? { src: original } : ui.imageSources(original, publicCover ? row.width + 'px' : cover ? measuredSize : props.sizes);
    delete attrs.cover; delete attrs.zoom;
    if (cover && !measuredSize) { delete attrs.src; delete attrs.srcSet; delete attrs.sizes; return <img {...attrs} ref={element} />; }
    if (!sources.srcSet) { delete attrs.sizes; delete attrs.srcSet; return <img {...attrs} ref={element} />; }
    delete attrs.src; delete attrs.srcSet; delete attrs.sizes; delete attrs.onError;
    return <img {...attrs} ref={element} srcSet={sources.srcSet} sizes={sources.sizes} src={sources.src} onError={function (e) {
      if (sources.srcSet) { failed[1](original); return; }
      if (props.onError) props.onError(e);
    }} />;
  };

  // Hidden slides have no media URL until selected. Keep already visited images for crossfades, and
  // hold the visible slide until the selected image finishes (or fails), avoiding a blank transition.
  ui.useSlideImages = function (slides, requested, sourceOf) {
    var ready = React.useState({}), visited = React.useRef({}), shown = React.useRef(null);
    var keys = slides.map(sourceOf), target = keys[requested];
    if (target) visited.current[target] = true;
    var previous = keys.indexOf(shown.current);
    var index = ready[0][target] || previous < 0 ? requested : previous;
    if (keys[index]) shown.current = keys[index];
    function done(key) { if (!ready[0][key]) ready[1](function (old) { var next = Object.assign({}, old); next[key] = true; return next; }); }
    return { index: index, has: function (k) { return !!visited.current[keys[k]]; },
      done: function (k) { return function () { done(keys[k]); }; } };
  };

  // ---- viewport: OO.ui.useWide() ----------------------------------------------------------------------------
  var WIDE_QUERY = '(min-width: 900px)';
  var ViewportContext = React.createContext(null); // null (auto) | 'mobile' | 'desktop'
  function mql() { return window.matchMedia ? window.matchMedia(WIDE_QUERY) : null; }
  function subscribeWide(cb) {
    var m = mql();
    if (!m) return function () {};
    if (m.addEventListener) {
      m.addEventListener('change', cb);
      return function () { m.removeEventListener('change', cb); };
    }
    m.addListener(cb);
    return function () { m.removeListener(cb); };
  }
  function wideNow() { var m = mql(); return !!(m && m.matches); }
  // true when the desktop layout applies here: online or qr mode (P4-14) at >= 900px, or a forced 'desktop' context.
  function useWide() {
    var forced = React.useContext(ViewportContext);
    var wide = React.useSyncExternalStore(subscribeWide, wideNow, wideNow);
    if (forced === 'mobile') return false;
    if (forced === 'desktop') return true;
    return (OO.mode === 'online' || OO.mode === 'qr' || OO.mode === 'kiosk') && wide;
  }
  // true when this subtree may use the desktop CSS at all (an .oo-wide scope): online or qr mode, or forced 'desktop'.
  function useWideScope() {
    var forced = React.useContext(ViewportContext);
    if (forced) return forced === 'desktop';
    return OO.mode === 'online' || OO.mode === 'qr' || OO.mode === 'kiosk';
  }

  ui.cx = cx;
  ui.renderIcon = renderIcon;
  ui.useWide = useWide;
  ui.useWideScope = useWideScope;
  ui.ViewportContext = ViewportContext;

  // ---- Button --------------------------------------------------------------------------------------------
  // <OO.ui.Button size="lg" block onClick={…}>Checkout</OO.ui.Button>
  //   variant: 'primary' (black, default) | 'outline' (1.5px ink border — Reorder) | 'soft' (canvas fill, muted —
  //            Locked / Redeemed) | 'success' (green — "Request sent") | 'ghost' (text, canvas on hover)
  //            | 'link' (13px underlined text — Change / Customise / Edit / Full menu)
  //   size: 'lg' (50px; 52 desktop — CTAs, default) | 'md' (40 / 42 — Login) | 'sm' (38 / 41 — coupon Select)
  //         | 'xs' (36 — Reorder, Redeem). 'link' has no size.
  //   block: full width. children (or label): the text. price: split layout — label left, price right in 600
  //   ("Add to cart … $25.90"). icon / iconRight: an OO.icons name or an element; iconSize (20 lg, 18 md / sm,
  //   16 xs).
  //   disabled: --oo-disabled-bg / --oo-disabled-on-bg ("Choose a base", "Accept terms to pay").
  //   busy: spinner + aria-busy, not clickable. href: renders <a> (while enabled). type ('button'), form,
  //   onClick, title, className; id / aria-* / data-* pass through.
  ui.Button = function Button(props) {
    var variant = props.variant || 'primary';
    var size = variant === 'link' ? null : props.size || 'lg';
    var split = props.price != null && props.price !== '';
    var disabled = !!props.disabled || !!props.busy;
    var cls = cx('oo-btn', 'oo-btn--' + variant, size && 'oo-btn--' + size, props.block && 'oo-btn--block',
      split && 'oo-btn--split', props.busy && 'is-busy', props.className);
    var label = props.children != null ? props.children : props.label;
    var iconSize = props.iconSize || (size === 'lg' ? 20 : size === 'xs' ? 16 : 18);
    var content = [
      props.icon ? <span key="i" className="oo-btn__icon">{renderIcon(props.icon, { size: iconSize })}</span> : null,
      <span key="l" className="oo-btn__label">{label}</span>,
      split ? <span key="p" className="oo-btn__price">{props.price}</span> : null,
      props.iconRight ? <span key="r" className="oo-btn__icon">{renderIcon(props.iconRight, { size: iconSize })}</span> : null,
      props.busy ? <span key="s" className="oo-btn__spinner" aria-hidden="true" /> : null,
    ];
    var common = passthrough(props);
    if (props.href && !disabled) {
      return (
        <a {...common} className={cls} href={props.href} onClick={props.onClick} title={props.title} target={props.target} rel={props.rel}>
          {content}
        </a>
      );
    }
    return (
      <button {...common} type={props.type || 'button'} form={props.form} className={cls} disabled={disabled}
        aria-busy={props.busy ? 'true' : undefined} onClick={props.onClick} title={props.title}>
        {content}
      </button>
    );
  };

  // ---- IconButton ----------------------------------------------------------------------------------------
  // <OO.ui.IconButton icon="Close" label="Close" onClick={…} /> — an icon-only button.
  //   label: REQUIRED accessible name. icon: an OO.icons name or an element. variant:
  //     'float'  white circle + shadow: 38px (close over the item photo); 40px on desktop modals (default)
  //     'ring'   transparent with a 1.5px currentColor ring: 36px, 42px desktop (header profile / About);
  //              size="lg" keeps 42px everywhere (app bell)
  //     'ghost'  32px rounded square, canvas on hover; 36px desktop (cart-line edit / delete)
  //     'bare'   just the glyph: 22px, 24px desktop (header back arrow)
  //   iconSize / strokeWidth override the glyph defaults. badge: a count bubble top-right (bell unread).
  //   href renders <a>. disabled, onClick, title; id / aria-* / data-* pass through (aria-expanded, …).
  //   The visual size stays as designed; the tap target grows to 44px with an invisible ::after.
  ui.IconButton = function IconButton(props) {
    var variant = props.variant || 'float';
    var glyph = { float: 18, ring: 18, ghost: 17, bare: 22 }[variant] || 18;
    if (variant === 'ring' && props.size === 'lg') glyph = 19;
    var cls = cx('oo-iconbtn', 'oo-iconbtn--' + variant, props.size === 'lg' && 'oo-iconbtn--lg',
      !props.iconSize && 'oo-iconbtn--auto', 'oo-hit', props.className);
    var children = [
      <React.Fragment key="i">{renderIcon(props.icon, { size: props.iconSize || glyph, strokeWidth: props.strokeWidth })}</React.Fragment>,
      props.badge ? <span key="b" className="oo-iconbtn__badge" aria-hidden="true">{props.badge}</span> : null,
    ];
    var common = passthrough(props);
    if (props.href) {
      return <a {...common} className={cls} href={props.href} onClick={props.onClick} aria-label={props.label} title={props.title}>{children}</a>;
    }
    return (
      <button {...common} type="button" className={cls} onClick={props.onClick} aria-label={props.label} title={props.title} disabled={props.disabled}>
        {children}
      </button>
    );
  };

  // ---- Card ----------------------------------------------------------------------------------------------
  // <OO.ui.Card pad="sm">…</OO.ui.Card> — the white surface (radius 14, 16 desktop; hairline shadow).
  //   as: tag or component (default 'div'). pad: 'md' (16px; 20px 22px desktop, default) | 'sm' (14px 16px;
  //   20px 24px desktop — cart lines, coupons) | 'list' (4px 16px; 4px 18px — rows with dividers) | 'summary'
  //   (16px; 22px 20px desktop — "Price summary" / "Your order" cards) | 'none'.
  //   radius: 'md' (default) | 'lg' (16 — app booking / store cards) | 'xl' (18 — app home cards).
  //   flat: no shadow. interactive: pointer + hover lift. onClick, onKeyDown, style, className; role, tabIndex,
  //   id / aria-* / data-* pass through (e.g. role="region" aria-labelledby, or a focusable card). elementRef: DOM ref.
  ui.Card = function Card(props) {
    var Tag = props.as || 'div';
    var cls = cx('oo-card', 'oo-card--pad-' + (props.pad || 'md'), props.radius && props.radius !== 'md' && 'oo-card--r-' + props.radius,
      props.flat && 'oo-card--flat', props.interactive && 'oo-card--interactive', props.className);
    return (
      <Tag {...passthrough(props)} ref={props.elementRef} className={cls} role={props.role} tabIndex={props.tabIndex}
        onClick={props.onClick} onKeyDown={props.onKeyDown} style={props.style}>
        {props.children}
      </Tag>
    );
  };

  // ---- Chip / ChipRow ------------------------------------------------------------------------------------
  // <OO.ui.Chip active={on} onClick={…}>BEEF BURGER</OO.ui.Chip> — outline pill, solid black when active
  //   (aria-pressed). 12px / 8px 14px; 13px / 9px 18px desktop. href: renders <a> with aria-current.
  ui.Chip = function Chip(props) {
    var cls = cx('oo-chip', props.active && 'is-active', props.className);
    if (props.href) {
      return <a {...passthrough(props)} className={cls} href={props.href} onClick={props.onClick} aria-current={props.active ? 'true' : undefined}>{props.children}</a>;
    }
    return (
      <button {...passthrough(props)} type="button" className={cls} onClick={props.onClick} aria-pressed={props.active ? 'true' : 'false'} disabled={props.disabled}>
        {props.children}
      </button>
    );
  };
  // <OO.ui.ChipRow label="Burger types">{chips}</OO.ui.ChipRow> — mobile: one horizontally scrolling row that
  //   bleeds 16px into the page gutter; desktop: wraps. label: the group's accessible name.
  ui.ChipRow = function ChipRow(props) {
    return <div className={cx('oo-chiprow', props.className)} role="group" aria-label={props.label}>{props.children}</div>;
  };

  // ---- Badge ---------------------------------------------------------------------------------------------
  // <OO.ui.Badge tone="required">Required</OO.ui.Badge> — small status pill (11px / 600).
  //   tone: 'optional' (canvas, default) | 'required' (black) | 'done' (green tint) | 'success' (= done, e.g.
  //   "Today only") | 'neutral' (= optional) | 'warn' | 'danger' | 'inverse' (light on the dark loyalty card,
  //   "GOLD"; --oo-card-dark tokens).
  //   size: 'md' (4px 9px, default) | 'sm' (3px 8px; 4px 10px / 12px desktop — "Today only").
  //   upper: uppercase + tracking (GOLD).
  ui.Badge = function Badge(props) {
    return (
      <span {...passthrough(props)} className={cx('oo-badge', 'oo-badge--' + (props.tone || 'optional'), props.size === 'sm' && 'oo-badge--sm',
        props.upper && 'oo-badge--upper', props.className)}>
        {props.children}
      </span>
    );
  };

  // ---- QtyStepper ----------------------------------------------------------------------------------------
  // <OO.ui.QtyStepper value={qty} onChange={setQty} /> — the bordered "− 1 +".
  //   size: 'lg' (50px; 52 desktop — item sheet / combo footer, default) | 'sm' (38px; 40 desktop — cart line).
  //   min (1), max (99). At min the minus greys out (--oo-disabled-fg) and does nothing — unless onRemove is
  //   given: then it stays live and calls onRemove() (the cart drops the line at 0). onChange(next).
  //   bordered={false}: the inline variant without the box. label: the group name ('Quantity');
  //   itemName: names the item in the button labels ("Decrease Fat Boy Burger quantity").
  //   Disabled ends use aria-disabled (not disabled) so keyboard focus is never dropped.
  ui.QtyStepper = function QtyStepper(props) {
    var value = props.value == null ? 1 : props.value;
    var min = props.min == null ? 1 : props.min;
    var max = props.max == null ? 99 : props.max;
    var atMin = value <= min, atMax = value >= max;
    var canRemove = atMin && typeof props.onRemove === 'function';
    var name = props.itemName ? ' ' + props.itemName : '';
    function dec() {
      if (canRemove) props.onRemove();
      else if (!atMin && props.onChange) props.onChange(value - 1);
    }
    function inc() { if (!atMax && props.onChange) props.onChange(value + 1); }
    return (
      <div className={cx('oo-qty', 'oo-qty--' + (props.size || 'lg'), props.bordered === false && 'oo-qty--bare', props.className)}
        role="group" aria-label={props.label || 'Quantity'}>
        <button type="button" className="oo-qty__btn" onClick={dec} aria-disabled={atMin && !canRemove ? 'true' : undefined}
          aria-label={canRemove ? 'Remove' + name : 'Decrease' + name + ' quantity'}>−</button>
        <output className="oo-qty__val" aria-live="polite">{value}</output>
        <button type="button" className="oo-qty__btn" onClick={inc} aria-disabled={atMax ? 'true' : undefined}
          aria-label={'Increase' + name + ' quantity'}>+</button>
      </div>
    );
  };

  // ---- OptionGroup / OptionRow ---------------------------------------------------------------------------
  var OptionCtx = React.createContext(null);
  // <OO.ui.OptionGroup title="Choice of base" hint="Choose 1" type="radio" required done={base >= 0}>
  //   <OO.ui.OptionRow …/>…</OO.ui.OptionGroup> — the modifier-group card: title + hint left, badge right.
  //   type: 'radio' (default) | 'checkbox': the group role, and the default type / name of its OptionRows.
  //   Badge: Required (black) → Done (green) once done; Optional when not required. badge={node|false}
  //   overrides. collapsible: the header becomes a toggle button with a rotating chevron; control it with
  //   open + onToggle(nextOpen), or leave it uncontrolled with defaultOpen. summary: replaces the hint on a
  //   collapsible group (the picked option, or "Choose up to 1"). name: the radio name (auto). id, className.
  ui.OptionGroup = function OptionGroup(props) {
    var id = useDomId('oo-og', props.id);
    var type = props.type || 'radio';
    var collapsible = !!props.collapsible;
    var own = React.useState(!!props.defaultOpen);
    var open = !collapsible || (props.open != null ? !!props.open : own[0]);
    function toggle() {
      if (props.open == null) own[1](!open);
      if (props.onToggle) props.onToggle(!open);
    }
    var badge = props.badge === false ? null
      : props.badge != null ? props.badge
      : props.required ? <ui.Badge tone={props.done ? 'done' : 'required'}>{props.done ? 'Done' : 'Required'}</ui.Badge>
      : <ui.Badge tone="optional">Optional</ui.Badge>;
    var hint = collapsible && props.summary ? props.summary : props.hint;
    var titles = (
      <span className="oo-optgroup__titles">
        <span className="oo-optgroup__title" id={id + '-t'}>{props.title}</span>
        {hint ? <span className="oo-optgroup__hint">{hint}</span> : null}
      </span>
    );
    var ctx = React.useMemo(function () { return { type: type, name: props.name || id }; }, [type, props.name, id]);
    return (
      <div ref={props.elementRef} tabIndex={props.invalid ? -1 : undefined} aria-invalid={props.invalid ? 'true' : undefined}
        className={cx('oo-optgroup', collapsible && 'oo-optgroup--collapsible', collapsible && open && 'is-open', props.invalid && 'is-invalid', props.className)}>
        {collapsible ? (
          <button type="button" className="oo-optgroup__head oo-optgroup__head--toggle" aria-expanded={open ? 'true' : 'false'} aria-controls={id + '-b'} onClick={toggle}>
            {titles}{badge}<OO.icons.ChevronDown size={16} className="oo-optgroup__chev" />
          </button>
        ) : (
          <div className="oo-optgroup__head">{titles}{badge}</div>
        )}
        {open ? (
          <div className="oo-optgroup__body" id={id + '-b'} role={type === 'radio' ? 'radiogroup' : 'group'} aria-labelledby={id + '-t'}
            aria-required={props.required && type === 'radio' ? 'true' : undefined}>
            <OptionCtx.Provider value={ctx}>{props.children}</OptionCtx.Provider>
          </div>
        ) : null}
      </div>
    );
  };
  // <OO.ui.OptionRow label="Tater Tots" price="+$2" checked={on} onToggle={() => pick(i)} /> — one choice: a
  //   real radio / checkbox (visually hidden) + the drawn control + label + muted price.
  //   type / name default from the enclosing OptionGroup. onToggle() fires ONCE per activation (click, Space,
  //   arrow keys): for a radio when it becomes checked, and again when the already-checked radio is clicked
  //   or gets Space (so an optional single-choice group can deselect — the parent decides); for a checkbox
  //   on every toggle. desc: a muted second line. value, disabled, className.
  //   variant 'item' (the combo item picker): 66px rows with a 64px thumb (image; placeholder when absent),
  //   the name in 600 over the desc, a 13px / 500 price (priceTone 'success' → green, e.g. "Included") and
  //   a chevron when `chevron` (the item has options to set next).
  ui.OptionRow = function OptionRow(props) {
    var ctx = React.useContext(OptionCtx) || {};
    var type = props.type || ctx.type || 'radio';
    var checked = !!props.checked;
    var item = props.variant === 'item';
    var spaceRef = React.useRef(false);
    function toggle() { if (!props.disabled && props.onToggle) props.onToggle(); }
    function onChange() { toggle(); }
    function onClick() { if (type === 'radio' && checked && !spaceRef.current) toggle(); }
    // Space on an already-checked radio: browsers fire no click (Chrome) or one on keyup (others). Take it
    // over (keydown + keyup, both prevented) so the keyboard can clear an optional group like the mouse.
    function isSpace(e) { return e.key === ' ' || e.key === 'Spacebar'; }
    function onKeyDown(e) { if (type === 'radio' && checked && isSpace(e)) { e.preventDefault(); spaceRef.current = true; } }
    function onKeyUp(e) {
      if (!spaceRef.current || !isSpace(e)) return;
      e.preventDefault();
      spaceRef.current = false;
      toggle();
    }
    return (
      <label className={cx('oo-optrow', item && 'oo-optrow--item', checked && 'is-on', props.disabled && 'is-disabled', props.className)}>
        <input className="oo-optrow__input" type={type} name={props.name || ctx.name} value={props.value} checked={checked}
          disabled={props.disabled} onChange={onChange} onClick={onClick} onKeyDown={onKeyDown} onKeyUp={onKeyUp}
          onBlur={function () { spaceRef.current = false; }} />
        <span className={cx('oo-ctl', 'oo-ctl--' + type)} aria-hidden="true">
          {type === 'checkbox' ? <OO.icons.Check size={13} strokeWidth={3.5} /> : <span className="oo-ctl__dot" />}
        </span>
        {item ? <span className="oo-optrow__thumb">{props.image ? <img src={props.image} alt="" loading="lazy" /> : null}</span> : null}
        <span className="oo-optrow__text">
          <span className="oo-optrow__label">{props.label}</span>
          {props.desc ? <span className="oo-optrow__desc">{props.desc}</span> : null}
        </span>
        {props.price ? <span className={cx('oo-optrow__price', props.priceTone && 'oo-optrow__price--' + props.priceTone)}>{props.price}</span> : null}
        {item && props.chevron ? <OO.icons.ChevronRight size={14} strokeWidth={2.6} className="oo-optrow__chev" /> : null}
      </label>
    );
  };

  // ---- PriceRows -----------------------------------------------------------------------------------------
  ui.ChargeInfo = function ChargeInfo(props) {
    var open = React.useState(false);
    if (!props.description || !String(props.description).trim()) return null;
    return <React.Fragment>
      <ui.IconButton icon="Info" iconSize={16} variant="bare" label={'About ' + props.name} aria-haspopup="dialog" className="oo-charge-info__button"
        onClick={function (event) { event.stopPropagation(); open[1](true); }} />
      <ui.Sheet open={open[0]} variant="dialog" title={props.name} onClose={function () { open[1](false); }}>
        <p className="oo-charge-info__description">{props.description}</p>
      </ui.Sheet>
    </React.Fragment>;
  };

  // <OO.ui.PriceRows title="Price summary" rows={[{ label: 'Sub-total', value: '$27.00' }, …]} total="$27.00" />
  //   Muted labels with 600 values, a hairline, then the big total (18px; 22px desktop). It is not a card:
  //   wrap it in <OO.ui.Card>. title (omit for none), totalLabel ('Total'), rows: [{ label, value, tone:
  //   'success' | 'muted', key }]. children render after the total (the desktop "Continue to checkout").
  ui.PriceRows = function PriceRows(props) {
    var rows = props.rows || [];
    return (
      <div className={cx('oo-price', props.className)}>
        {props.title ? <h3 className="oo-price__title">{props.title}</h3> : null}
        {rows.length ? (
          <dl className="oo-price__rows">
            {rows.map(function (r, i) {
              return (
                <div key={r.key || r.label || i} className={cx('oo-price__row', r.tone && 'oo-price__row--' + r.tone)}>
                  <dt>{r.label}{r.description ? <ui.ChargeInfo name={r.label} description={r.description} /> : null}</dt>
                  <dd>{r.value}</dd>
                </div>
              );
            })}
          </dl>
        ) : null}
        {props.total != null ? <div className="oo-price__rule" role="presentation" /> : null}
        {props.total != null ? (
          <div className="oo-price__total">
            <span className="oo-price__total-label">{props.totalLabel || 'Total'}</span>
            <span className="oo-price__total-value">{props.total}</span>
          </div>
        ) : null}
        {props.children ? <div className="oo-price__after">{props.children}</div> : null}
      </div>
    );
  };

  // ---- TextInput -----------------------------------------------------------------------------------------
  // <OO.ui.TextInput label="Full name" value={name} onChange={setName} autoComplete="name" /> — the outlined
  //   field (48px; 50 desktop). label is REQUIRED (it is the accessible name). Default look = the design's
  //   placeholder-only field: the label is visually hidden and is also the placeholder unless `placeholder`
  //   is given. stacked: the label sits inside the box above the value (the Day / Time select look).
  //   labelVisible: a visible label above the box. onChange(value, event). type ('text'), name, autoComplete,
  //   inputMode, required, disabled, readOnly, maxLength, error (string: red border + message, aria-invalid),
  //   hint (muted help line), onBlur, onFocus, onKeyDown, inputRef, id, className.
  ui.TextInput = function TextInput(props) {
    var id = useDomId('oo-in', props.id);
    var stacked = !!props.stacked;
    var describedBy = cx(props.error && id + '-err', props.hint && id + '-hint') || undefined;
    var placeholder = props.placeholder != null ? props.placeholder : stacked || props.labelVisible ? undefined : props.label;
    var input = (
      <input
        id={id}
        ref={props.inputRef}
        className="oo-field__input"
        type={props.type || 'text'}
        name={props.name}
        value={props.value == null ? '' : props.value}
        placeholder={placeholder}
        autoComplete={props.autoComplete}
        inputMode={props.inputMode}
        required={props.required}
        disabled={props.disabled}
        readOnly={props.readOnly}
        maxLength={props.maxLength}
        aria-invalid={props.error ? 'true' : undefined}
        aria-describedby={describedBy}
        onChange={function (e) { if (props.onChange) props.onChange(e.target.value, e); }}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
      />
    );
    return (
      <div className={cx('oo-field', props.error && 'is-invalid', props.disabled && 'is-disabled', props.className)}>
        {!stacked ? <label htmlFor={id} className={props.labelVisible ? 'oo-field__label' : 'oo-sr-only'}>{props.label}</label> : null}
        <div className={cx('oo-field__box', stacked && 'oo-field__box--stacked')}>
          {stacked ? <label htmlFor={id} className="oo-field__inlabel">{props.label}</label> : null}
          {input}
        </div>
        {props.error ? <div className="oo-field__msg" id={id + '-err'}>{props.error}</div> : null}
        {props.hint ? <div className="oo-field__hint" id={id + '-hint'}>{props.hint}</div> : null}
      </div>
    );
  };

  // ---- TextArea ------------------------------------------------------------------------------------------
  // <OO.ui.TextArea label="Additional notes" placeholder="Extra comments (optional)" value onChange /> —
  //   outlined, no resize. The label shows as the block title (15px / 600; labelSize="sm" → 14px, the
  //   desktop "Order notes"); labelHidden hides it visually. size (fixed at every width — pick per layout):
  //   'sm' (72px — booking requests) | 'md' (80 — combo notes, desktop item notes) | 'lg' (88 — mobile item
  //   notes, default) | 'xl' (90 — desktop checkout order notes). onChange(value, event), name, maxLength,
  //   disabled, error, id, className.
  ui.TextArea = function TextArea(props) {
    var id = useDomId('oo-ta', props.id);
    return (
      <div className={cx('oo-textarea', props.error && 'is-invalid', props.className)}>
        <label htmlFor={id} className={props.labelHidden ? 'oo-sr-only' : cx('oo-textarea__label', props.labelSize === 'sm' && 'oo-textarea__label--sm')}>{props.label}</label>
        <textarea
          id={id}
          className={cx('oo-textarea__input', 'oo-textarea__input--' + (props.size || 'lg'))}
          name={props.name}
          value={props.value == null ? '' : props.value}
          placeholder={props.placeholder}
          maxLength={props.maxLength}
          disabled={props.disabled}
          aria-invalid={props.error ? 'true' : undefined}
          aria-describedby={props.error ? id + '-err' : undefined}
          onChange={function (e) { if (props.onChange) props.onChange(e.target.value, e); }}
        />
        {props.error ? <div className="oo-field__msg" id={id + '-err'}>{props.error}</div> : null}
      </div>
    );
  };

  // ---- SelectField ---------------------------------------------------------------------------------------
  // <OO.ui.SelectField label="Day" value={day} options={[{ value: 'today', label: 'Today' }]} onChange={setDay} />
  //   The design's outlined picker: small muted label on top, the value in 600, a filled caret. A native
  //   <select> covers the box (transparent), so phones get the OS picker and keyboards / screen readers
  //   get a real select. options: [{ value, label, disabled }] or plain strings. placeholder: shown when the
  //   value matches no option. onChange(value, event). name, disabled, id, className.
  ui.SelectField = function SelectField(props) {
    var id = useDomId('oo-sel', props.id);
    var opts = (props.options || []).map(function (o) { return typeof o === 'object' && o ? o : { value: o, label: String(o) }; });
    var current = null;
    for (var i = 0; i < opts.length; i++) if (String(opts[i].value) === String(props.value)) { current = opts[i]; break; }
    return (
      <div className={cx('oo-select', props.disabled && 'is-disabled', props.className)}>
        <span className="oo-select__text">
          <span className="oo-select__label" id={id + '-l'}>{props.label}</span>
          <span className="oo-select__value" aria-hidden="true">{current ? current.label : props.placeholder || '—'}</span>
        </span>
        <OO.icons.CaretDown size={14} className="oo-select__caret" />
        <select
          id={id}
          className="oo-select__native"
          name={props.name}
          value={current ? current.value : ''}
          disabled={props.disabled}
          aria-labelledby={id + '-l'}
          onChange={function (e) { if (props.onChange) props.onChange(e.target.value, e); }}
        >
          {!current ? <option value="" disabled>{props.placeholder || 'Select'}</option> : null}
          {opts.map(function (o) { return <option key={String(o.value)} value={o.value} disabled={o.disabled}>{o.label}</option>; })}
        </select>
      </div>
    );
  };

  // ---- CheckRow ------------------------------------------------------------------------------------------
  // <OO.ui.CheckRow checked={terms} onChange={setTerms}>I have read and agree to the <a …>terms</a>.</OO.ui.CheckRow>
  //   Checkbox + rich label (13px; 14px desktop). Links inside the label still work. onChange(nextChecked, e).
  //   required (aria-required), invalid (red box + aria-invalid), disabled, name, id, className.
  ui.CheckRow = function CheckRow(props) {
    return (
      <label className={cx('oo-checkrow', props.invalid && 'is-invalid', props.disabled && 'is-disabled', props.className)}>
        <input className="oo-optrow__input" type="checkbox" id={props.id} name={props.name} checked={!!props.checked} disabled={props.disabled}
          aria-required={props.required ? 'true' : undefined} aria-invalid={props.invalid ? 'true' : undefined}
          onChange={function (e) { if (props.onChange) props.onChange(e.target.checked, e); }} />
        <span className="oo-ctl oo-ctl--checkbox" aria-hidden="true"><OO.icons.Check size={13} strokeWidth={3.5} /></span>
        <span className="oo-checkrow__text">{props.children}</span>
      </label>
    );
  };

  // ---- InlineNotice / Toast ------------------------------------------------------------------------------
  // <OO.ui.InlineNotice>Combo added to cart</OO.ui.InlineNotice> — the green in-flow notice (13.5px / 600).
  //   tone: 'success' (default, check icon) | 'warn' | 'danger' | 'info' (white card). icon: an OO.icons name /
  //   element, or false for none. role: 'status' (default, polite) | 'alert'. className.
  var NOTICE_ICON = { success: 'Check', warn: 'Info', danger: 'Info', info: 'Info' };
  ui.InlineNotice = function InlineNotice(props) {
    var tone = props.tone || 'success';
    var icon = props.icon === false ? null : props.icon || NOTICE_ICON[tone];
    return (
      <div className={cx('oo-notice', 'oo-notice--' + tone, props.className)} role={props.role || 'status'}>
        {icon ? renderIcon(icon, tone === 'success' ? { size: 16, strokeWidth: 3 } : { size: 18 }) : null}
        <span className="oo-notice__text">{props.children}</span>
      </div>
    );
  };
  // <OO.ui.Toast open={shown} onClose={() => setShown(false)}>Added to cart</OO.ui.Toast> — a floating
  //   InlineNotice (portal to <body>, aria-live polite). It never covers a bottom bar: it sits 12px above the
  //   highest FIXED bar on screen — the app tab bar, a FloatingCartBar, a BottomBarCTA (whatever its height) —
  //   measured when it opens and on resize (computed bottom + offsetHeight, so entrance transforms don't skew
  //   it); with no bar it floats 24px above the bottom edge. (Unmeasured, the CSS default clears a cart bar.)
  //   It auto-closes after `duration` ms (2600; 0 = stay). tone / icon as InlineNotice. contained: render in
  //   place, unmeasured (gallery).
  var BOTTOM_BARS = '.oo-tabbar, .oo-cartbar, .oo-bottombar';
  function bottomClearance() {
    if (typeof document === 'undefined' || !document.querySelectorAll || !window.getComputedStyle) return null;
    var top = 0;
    var bars = document.querySelectorAll(BOTTOM_BARS);
    for (var i = 0; i < bars.length; i++) {
      var cs = window.getComputedStyle(bars[i]);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var bottom = parseFloat(cs.bottom);
      var h = bars[i].offsetHeight; // 0 when hidden by an ancestor (e.g. the desktop-hidden BottomBarCTA)
      if (h > 0 && isFinite(bottom)) top = Math.max(top, bottom + h);
    }
    return top;
  }
  ui.Toast = function Toast(props) {
    var onCloseRef = React.useRef(props.onClose);
    onCloseRef.current = props.onClose;
    var duration = props.duration == null ? 2600 : props.duration;
    var liftS = React.useState(null); // px above the viewport bottom, or null (CSS default)
    var setLift = liftS[1];
    React.useEffect(function () {
      if (!props.open || !duration) return undefined;
      var t = setTimeout(function () { if (onCloseRef.current) onCloseRef.current(); }, duration);
      return function () { clearTimeout(t); };
    }, [props.open, duration]);
    // Before paint, so the toast never flashes over a bar; re-measured a frame later (bars that mount with it).
    React.useLayoutEffect(function () {
      if (!props.open || props.contained) return undefined;
      function measure() { setLift(bottomClearance()); }
      measure();
      var raf = window.requestAnimationFrame ? window.requestAnimationFrame(measure) : 0;
      window.addEventListener('resize', measure);
      return function () {
        if (raf && window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
        window.removeEventListener('resize', measure);
      };
    }, [props.open, props.contained]);
    var scope = useWideScope();
    var lift = liftS[0];
    var style = lift == null || props.contained ? undefined
      : { bottom: lift > 0 ? lift + 12 + 'px' : 'calc(24px + env(safe-area-inset-bottom, 0px))' };
    var node = (
      <div className={cx('oo-toast-wrap', scope && 'oo-wide', OO.mode === 'app' && 'oo-app')} style={style} aria-live="polite">
        {props.open ? (
          <ui.InlineNotice tone={props.tone} icon={props.icon} className="oo-toast" role="status">{props.children}</ui.InlineNotice>
        ) : null}
      </div>
    );
    return props.contained || !window.ReactDOM || !document.body ? node : ReactDOM.createPortal(node, document.body);
  };

  // ---- StepBar -------------------------------------------------------------------------------------------
  // <OO.ui.StepBar steps={[{ key, label, name, summary, state: 'done' | 'current' | 'todo', disabled }]}
  //   onPick={(i) => …} /> — combo progress. variant 'bars' (default; mobile: a white strip of segment bars
  //   with labels) | 'list' (desktop left column: numbered dots, name + summary, ✓ when done). A step is
  //   clickable unless `disabled` (the design only allows steps up to the first open one). label: the nav
  //   name ('Combo steps').
  ui.StepBar = function StepBar(props) {
    var steps = props.steps || [];
    var list = props.variant === 'list';
    return (
      <nav className={cx(list ? 'oo-steplist' : 'oo-steps', props.className)} aria-label={props.label || 'Combo steps'}>
        {steps.map(function (s, i) {
          var state = s.state || 'todo';
          var cls = cx(list ? 'oo-steplist__item' : 'oo-steps__item', 'is-' + state);
          var pick = function () { if (!s.disabled && props.onPick) props.onPick(i, s); };
          if (list) {
            return (
              <button key={s.key || i} type="button" className={cls} onClick={pick} disabled={s.disabled} aria-current={state === 'current' ? 'step' : undefined}>
                <span className="oo-steplist__dot" aria-hidden="true">{state === 'done' ? <OO.icons.Check size={13} strokeWidth={3} /> : i + 1}</span>
                <span className="oo-steplist__text">
                  <span className="oo-steplist__name">{s.name || s.label}</span>
                  {s.summary ? <span className="oo-steplist__summary">{s.summary}</span> : null}
                </span>
              </button>
            );
          }
          return (
            <button key={s.key || i} type="button" className={cls} onClick={pick} disabled={s.disabled} aria-current={state === 'current' ? 'step' : undefined}
              aria-label={(s.name || s.label) + (state === 'done' ? ', done' : '')}>
              <span className="oo-steps__bar" aria-hidden="true" />
              <span className="oo-steps__label">{s.label}</span>
            </button>
          );
        })}
      </nav>
    );
  };

  // ---- ProductCard / ProductGrid -------------------------------------------------------------------------
  // <OO.ui.ProductCard name price desc image onClick={…} /> — the menu item card. Mobile: 112px photo on the
  //   left, name (15 / 600), 2-line description, price (15 / 700); min-height 124. Desktop: 180px photo,
  //   180px tall, 18px name / price. No image (or a failed load): text only (min-height 108).
  //   soldOut: greyed photo + text, a neutral `soldOutLabel` badge ('Sold out'). unavailable (outside its
  //   availability window): the same greyed look with an `unavailableLabel` badge ('Unavailable now'). Either
  //   one makes the card non-interactive: a <button aria-disabled="true"> (still focusable, so the label is read)
  //   that never calls onClick, no hover lift, no link. A passed aria-disabled is kept otherwise.
  //   badge: a node shown next to the price (when neither state applies). imageAlt (''), href → <a>.
  //   Lay cards out with <OO.ui.ProductGrid> (mobile stack, desktop 2 columns).
  ui.ProductCard = function ProductCard(props) {
    var failed = React.useState(false);
    var hasImg = !!props.image && !failed[0];
    var off = !!props.soldOut || !!props.unavailable;
    var link = props.href && !off;
    var Tag = link ? 'a' : 'button';
    var attrs = link
      ? { href: props.href, onClick: props.onClick }
      : { type: 'button', onClick: off ? undefined : props.onClick, 'aria-disabled': off ? 'true' : props['aria-disabled'] };
    var state = props.soldOut ? props.soldOutLabel || 'Sold out' : props.unavailable ? props.unavailableLabel || 'Unavailable now' : null;
    return (
      <Tag {...passthrough(props)} {...attrs}
        className={cx('oo-pcard', props.showAddIcon && !off && 'oo-card--add-icon', !hasImg && !props.placeholderIcon && 'oo-pcard--noimg', props.soldOut && 'is-soldout', !props.soldOut && props.unavailable && 'is-unavailable', props.className)}>
        {hasImg || props.placeholderIcon ? (
          <span className="oo-pcard__media">
            {hasImg ? <ui.Image cover className="oo-pcard__img" src={props.image} sizes="(min-width: 900px) 220px, 112px" alt={props.imageAlt || ''} loading="lazy" onError={function () { failed[1](true); }} />
              : renderIcon(props.placeholderIcon, { size: 28, 'aria-hidden': true })}
          </span>
        ) : null}
        <span className="oo-pcard__body">
          <span className="oo-pcard__name">{props.name}</span>
          {props.desc ? <span className="oo-pcard__desc">{props.desc}</span> : null}
          <span className="oo-pcard__foot">
            <span className="oo-pcard__price">{props.price}</span>
            {state ? <ui.Badge tone="neutral">{state}</ui.Badge> : props.badge || null}
            {!off && props.actionLabel ? <span className="oo-market-product-action">{props.actionLabel}</span> : null}
          </span>
        </span>
        {props.showAddIcon && !off ? <span className="oo-card__add-icon" aria-hidden="true"><OO.icons.Plus size={20} /></span> : null}
      </Tag>
    );
  };
  // <OO.ui.ProductGrid>{cards}</OO.ui.ProductGrid> — mobile: a 14px-gap stack; desktop: 2 columns, 24px gaps.
  //   Also used for the desktop 2-up deal cards.
  ui.ProductGrid = function ProductGrid(props) {
    return <div className={cx('oo-pgrid', props.className)}>{props.children}</div>;
  };

  // ---- DealCard ------------------------------------------------------------------------------------------
  // <OO.ui.DealCard name="Wednesday Deal" desc="…" price="$12.90" badge="Today only" onClick={…} /> — the weekly
  //   deal card (16px name; 18 desktop) with an optional green badge. href → <a>.
  //   unavailable: greyed, non-interactive (<button aria-disabled="true">, onClick never called, no hover lift)
  //   with a neutral badge: `unavailableLabel` (e.g. 'Not available today'), else the badge text, else
  //   'Unavailable now'. A passed aria-disabled is kept otherwise.
  ui.DealCard = function DealCard(props) {
    var off = !!props.unavailable;
    var Tag = props.href && !off ? 'a' : 'button';
    var attrs = Tag === 'a' ? { href: props.href, onClick: props.onClick }
      : { type: 'button', onClick: off ? undefined : props.onClick, 'aria-disabled': off ? 'true' : props['aria-disabled'] };
    var badge = off ? props.unavailableLabel || props.badge || 'Unavailable now' : props.badge;
    return (
      <Tag {...passthrough(props)} {...attrs} className={cx('oo-deal', props.showAddIcon && !off && 'oo-card--add-icon', off && 'is-unavailable', props.className)}>
        {props.image ? <span className="oo-deal__image"><ui.Image cover src={props.image} sizes="(min-width: 900px) 220px, 112px" alt="" loading="lazy" /></span> : null}
        <span className="oo-deal__body">
          <span className="oo-deal__head">
            <span className="oo-deal__name">{props.name}</span>
            {badge ? <ui.Badge tone={off ? 'neutral' : 'success'} size="sm">{badge}</ui.Badge> : null}
          </span>
          {props.desc ? <span className="oo-deal__desc">{props.desc}</span> : null}
          <span className="oo-deal__price">{props.price}</span>
        </span>
        {props.showAddIcon && !off ? <span className="oo-card__add-icon" aria-hidden="true"><OO.icons.Plus size={20} /></span> : null}
      </Tag>
    );
  };

  // ---- CategoryTile / CategoryGrid -----------------------------------------------------------------------
  // <OO.ui.CategoryTile name="BURGERS" meta="4 categories" image={src} variant="image" onClick={…} />
  //   variant: 'image' (photo on top — placeholder glyph when no image; desktop adds a chevron) | 'text'
  //   (text-only card, meta + chevron at the bottom; desktop puts the chevron in a ring) | 'row' (sub-category
  //   row with an optional 52px thumb — pass image, or withImage for the placeholder). href → <a>.
  //   Lay tiles out with <OO.ui.CategoryGrid variant> (image / text: 2 columns, 4 desktop; row: stack, 3 desktop).
  ui.CategoryTile = function CategoryTile(props) {
    var variant = props.variant || 'image';
    var Tag = props.href ? 'a' : 'button';
    var attrs = props.href ? { href: props.href, onClick: props.onClick } : { type: 'button', onClick: props.onClick };
    var withMedia = variant === 'image' || (variant === 'row' && (props.withImage || !!props.image));
    var media = withMedia ? (
      <span className="oo-ctile__media">
        {props.image ? <ui.Image cover src={props.image} sizes={variant === 'row' ? '52px' : '(min-width: 900px) 240px, 45vw'} alt="" loading="lazy" /> : <OO.icons.ImagePlaceholder size={variant === 'row' ? 20 : 26} className="oo-ctile__ph" />}
      </span>
    ) : null;
    var chev = <OO.icons.ChevronRight size={16} className="oo-ctile__chev" />;
    var name = <span className="oo-ctile__name">{props.name}</span>;
    var meta = props.meta ? <span className="oo-ctile__meta">{props.meta}</span> : null;
    var inner;
    if (variant === 'text') {
      inner = [<React.Fragment key="n">{name}</React.Fragment>, <span key="f" className="oo-ctile__foot">{meta || <span />}<span className="oo-ctile__ring">{chev}</span></span>];
    } else if (variant === 'row') {
      inner = [<React.Fragment key="m">{media}</React.Fragment>, <span key="t" className="oo-ctile__text">{name}{meta}</span>, <React.Fragment key="c">{chev}</React.Fragment>];
    } else {
      inner = [<React.Fragment key="m">{media}</React.Fragment>, <span key="b" className="oo-ctile__body"><span className="oo-ctile__text">{name}{meta}</span>{chev}</span>];
    }
    return <Tag {...passthrough(props)} {...attrs} className={cx('oo-ctile', 'oo-ctile--' + variant, props.className)}>{inner}</Tag>;
  };
  ui.CategoryGrid = function CategoryGrid(props) {
    return <div className={cx('oo-cgrid', 'oo-cgrid--' + (props.variant || 'image'), props.className)}>{props.children}</div>;
  };

  // ---- FloatingCartBar -----------------------------------------------------------------------------------
  // <OO.ui.FloatingCartBar count={3} total="$54.80" onClick={…} /> — the black "View cart (n) … $total" bar
  //   fixed to the bottom; renders NOTHING when count is 0. Mobile: 16px insets, 54px; desktop: centred 420px,
  //   56px. Inside the app column it centres there and clears the tab bar. It also renders an in-flow spacer
  //   so the end of the page isn't hidden behind it. label ('View cart'), href → <a>, className.
  ui.FloatingCartBar = function FloatingCartBar(props) {
    var count = Number(props.count) || 0;
    if (count <= 0) return null;
    var Tag = props.href ? 'a' : 'button';
    var attrs = props.href ? { href: props.href, onClick: props.onClick } : { type: 'button', onClick: props.onClick };
    var label = props.label || 'View cart';
    return (
      <React.Fragment>
        <div className="oo-cartbar-spacer" aria-hidden="true" />
        <Tag {...attrs} className={cx('oo-cartbar', props.className)} aria-label={label + ', ' + count + (count === 1 ? ' item, ' : ' items, ') + props.total}>
          <OO.icons.Bag size={20} />
          <span className="oo-cartbar__label">{label}</span>
          <span className="oo-cartbar__count">{count}</span>
          <span className="oo-cartbar__total">{props.total}</span>
        </Tag>
      </React.Fragment>
    );
  };

  // ---- Sheet ---------------------------------------------------------------------------------------------
  // <OO.ui.Sheet open onClose={…} label="Fat Boy Burger" hero={img} heading={<OO.ui.SheetHeading …/>}
  //   footer={<>{stepper}{cta}</>}>{option groups}</OO.ui.Sheet>
  // variant 'split' (default): mobile = a full-height sheet sliding up (canvas body, hero photo with a
  //   floating close, heading, scroll body, white footer bar). Desktop (OO.ui.useWide) = a centred split
  //   modal, max 1040×720: `aside` (or the hero photo) on the left, header / heading + close / body / footer
  //   on the right, over a dark scrim.
  // variant 'dialog': a small sheet — mobile bottom sheet (auto height, rounded top) with a title row and
  //   close; desktop centred card (520px). Use it for pickers (coupons, payment method, login).
  // Props: open, onClose (ESC, scrim, close button), label | labelledBy (dialog name; REQUIRED one of them),
  //   title (dialog variant title), kind: 'item' (hero 250px, desktop aside 460px; default) | 'combo' (220 /
  //   380), hero (img src) + heroAlt, aside (custom desktop left column; drawn with a right border), header
  //   (a non-scrolling top region; on desktop the close is appended to it; on MOBILE no close is drawn — the
  //   header's own back button exits), heading (top of the scroll body; on desktop it shares a row with the
  //   close), footer, children (scroll body), showClose (true), closeLabel ('Close'), className, contained
  //   (render in place, absolutely positioned inside the nearest positioned ancestor — demos only; no portal,
  //   scroll lock or focus trap).
  // Behaviour: portals to <body>; role="dialog" aria-modal; focuses the panel, traps Tab, restores focus on
  //   close; ESC closes the top-most sheet; locks page scroll; slide-up / pop entrance (reduced-motion safe).
  var sheetStack = [];
  var lock = { count: 0, html: '', body: '' };
  function lockScroll() {
    if (lock.count++ > 0) return;
    lock.html = document.documentElement.style.overflow;
    lock.body = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }
  function unlockScroll() {
    if (lock.count === 0 || --lock.count > 0) return;
    document.documentElement.style.overflow = lock.html;
    document.body.style.overflow = lock.body;
  }
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function cssEsc(s) { return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'); }
  function tabbables(root) {
    if (!root) return [];
    return [].slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      if (!el.getClientRects().length) return false;
      if (el.type === 'radio' && !el.checked && el.name) {
        // Only the checked radio of a group (or the first one when none is) is in the tab order.
        var group = root.querySelectorAll('input[type="radio"][name="' + cssEsc(el.name) + '"]');
        if ([].some.call(group, function (r) { return r.checked; }) || group[0] !== el) return false;
      }
      return true;
    });
  }
  function focusEl(el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }

  function SheetImpl(props) {
    var wide = useWide();
    var scope = useWideScope();
    var panelRef = React.useRef(null);
    var onCloseRef = React.useRef(props.onClose);
    onCloseRef.current = props.onClose;
    var contained = !!props.contained;
    var variant = props.variant === 'dialog' ? 'dialog' : 'split';
    var titleId = useDomId('oo-sheet-t');

    React.useEffect(function () {
      if (contained) return undefined;
      var token = {};
      sheetStack.push(token);
      lockScroll();
      var prev = document.activeElement;
      if (panelRef.current && !panelRef.current.contains(document.activeElement)) focusEl(panelRef.current);
      function isTop() { return sheetStack[sheetStack.length - 1] === token; }
      function onKey(e) {
        if (!isTop() || !panelRef.current) return;
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          if (onCloseRef.current) onCloseRef.current();
        } else if (e.key === 'Tab') {
          var t = tabbables(panelRef.current);
          if (!t.length) { e.preventDefault(); focusEl(panelRef.current); return; }
          var first = t[0], last = t[t.length - 1], active = document.activeElement;
          var inside = panelRef.current.contains(active);
          if (e.shiftKey && (!inside || active === first || active === panelRef.current)) { e.preventDefault(); focusEl(last); }
          else if (!e.shiftKey && (!inside || active === last)) { e.preventDefault(); focusEl(first); }
        }
      }
      function onFocusIn(e) {
        if (!isTop() || !panelRef.current || panelRef.current.contains(e.target)) return;
        var t = tabbables(panelRef.current);
        focusEl(t[0] || panelRef.current);
      }
      document.addEventListener('keydown', onKey);
      document.addEventListener('focusin', onFocusIn);
      return function () {
        document.removeEventListener('keydown', onKey);
        document.removeEventListener('focusin', onFocusIn);
        var i = sheetStack.indexOf(token);
        if (i >= 0) sheetStack.splice(i, 1);
        unlockScroll();
        if (prev && prev.focus && document.contains(prev)) focusEl(prev);
      };
    }, [contained]);

    var showClose = props.showClose !== false;
    function closeBtn(v, cls) {
      return showClose ? <ui.IconButton icon="Close" label={props.closeLabel || 'Close'} variant={v} className={cls} onClick={props.onClose} /> : null;
    }
    var nameProps = props.labelledBy ? { 'aria-labelledby': props.labelledBy }
      : variant === 'dialog' && props.title && !props.label ? { 'aria-labelledby': titleId }
      : { 'aria-label': props.label };
    var kind = props.kind === 'combo' ? 'combo' : 'item';
    var heroImg = props.hero ? <img className="oo-sheet__hero-img" src={props.hero} alt={props.heroAlt || ''} /> : null;
    var footer = props.footer ? <div className="oo-sheet__footer">{props.footer}</div> : null;
    var panelProps = Object.assign({ ref: panelRef, role: 'dialog', 'aria-modal': 'true', tabIndex: -1 }, nameProps);
    var panel;

    if (variant === 'dialog') {
      panel = (
        <div {...panelProps} className={cx('oo-sheet', 'oo-sheet--dialog', props.className)}>
          <div className="oo-sheet__dhead">
            <h2 className="oo-sheet__dtitle" id={titleId}>{props.title || props.label}</h2>
            {closeBtn('ghost', 'oo-sheet__dclose')}
          </div>
          <div className="oo-sheet__scroll oo-sheet__dbody">{props.children}</div>
          {footer}
        </div>
      );
    } else if (wide) {
      var asideNode = props.aside || heroImg;
      panel = (
        <div {...panelProps} className={cx('oo-sheet', 'oo-sheet--modal', 'oo-sheet--' + kind, !asideNode && 'oo-sheet--no-aside', props.className)}>
          {asideNode ? <div className={cx('oo-sheet__aside', props.aside && 'oo-sheet__aside--custom')}>{asideNode}</div> : null}
          <div className="oo-sheet__main">
            {props.header ? <div className="oo-sheet__header">{props.header}{closeBtn('float', 'oo-sheet__header-close')}</div> : null}
            <div className="oo-sheet__scroll">
              {props.heading ? (
                <div className="oo-sheet__heading-row">
                  <div className="oo-sheet__heading">{props.heading}</div>
                  {!props.header ? closeBtn('float') : null}
                </div>
              ) : null}
              <div className="oo-sheet__body">{props.children}</div>
            </div>
            {footer}
            {!props.header && !props.heading ? <span className="oo-sheet__corner">{closeBtn('float')}</span> : null}
          </div>
        </div>
      );
    } else {
      panel = (
        <div {...panelProps} className={cx('oo-sheet', 'oo-sheet--full', 'oo-sheet--' + kind, props.className)}>
          {props.header && !props.headerScroll ? <div className="oo-sheet__header">{props.header}</div> : null}
          {props.headerScroll ? <div className="oo-sheet__scroll">
            {props.header && props.headerScroll ? <div className="oo-sheet__header">{props.header}</div> : null}
            {props.hero ? <div className="oo-sheet__hero">{heroImg}{!props.header ? closeBtn('float', 'oo-sheet__hero-close') : null}</div> : null}
            {props.heading ? <div className="oo-sheet__heading">{props.heading}</div> : null}
            <div className="oo-sheet__body">{props.children}</div>
          </div> : <div className="oo-sheet__scroll">
            {props.hero ? <div className="oo-sheet__hero">{heroImg}{!props.header ? closeBtn('float', 'oo-sheet__hero-close') : null}</div> : null}
            {props.heading ? <div className="oo-sheet__heading">{props.heading}</div> : null}
            <div className="oo-sheet__body">{props.children}</div>
          </div>}
          {footer}
          {!props.hero && !props.header ? <span className="oo-sheet__corner">{closeBtn('float')}</span> : null}
        </div>
      );
    }

    var centred = variant === 'dialog' || wide;
    var root = (
      <div className={cx('oo-sheet-root', 'oo-sheet-root--' + variant, scope && 'oo-wide', OO.mode === 'app' && 'oo-app', OO.mode === 'kiosk' && 'oo-kiosk-overlay', contained && 'oo-sheet-root--contained')}>
        <div className={cx('oo-sheet-scrim', centred && wide && 'oo-sheet-scrim--modal')} onClick={props.onClose} aria-hidden="true" />
        {centred ? <div className="oo-sheet-center">{panel}</div> : panel}
      </div>
    );
    return contained || !window.ReactDOM || !document.body ? root : ReactDOM.createPortal(root, document.body);
  }
  ui.Sheet = function Sheet(props) {
    return props.open ? <SheetImpl {...props} /> : null;
  };

  // <OO.ui.SheetHeading title="Fat Boy Burger" desc="…" price="$25.90" id={headingId} /> — the item / combo
  //   title block for Sheet `heading`: title 21px / 700 (24 desktop; kind="combo" 22), muted 13px
  //   description (14; combo 13.5), price 16 / 600 (17; combo 16). Pass the same id as Sheet labelledBy.
  ui.SheetHeading = function SheetHeading(props) {
    return (
      <div className={cx('oo-sheethead', props.kind === 'combo' && 'oo-sheethead--combo', props.className)}>
        <h2 className="oo-sheethead__title" id={props.id}>{props.title}</h2>
        {props.desc ? <p className="oo-sheethead__desc">{props.desc}</p> : null}
        {props.price ? <div className="oo-sheethead__price">{props.price}</div> : null}
      </div>
    );
  };

  // ---- EmptyState ----------------------------------------------------------------------------------------
  // <OO.ui.EmptyState title="Your cart is empty" /> — centred message card. With only a title it is the
  //   design's single muted line (14px; 15 desktop). icon (OO.icons name, in a canvas circle), body (a
  //   second line) and action (e.g. a Button) make it a fuller empty state. flat: no card background.
  ui.EmptyState = function EmptyState(props) {
    var rich = !!(props.body || props.icon || props.action);
    return (
      <div className={cx('oo-empty', rich && 'oo-empty--rich', props.flat && 'oo-empty--flat', props.className)} role={props.role}>
        {props.icon ? <span className="oo-empty__icon">{renderIcon(props.icon, { size: 22 })}</span> : null}
        {props.title ? <div className="oo-empty__title">{props.title}</div> : null}
        {props.body ? <div className="oo-empty__body">{props.body}</div> : null}
        {props.action ? <div className="oo-empty__action">{props.action}</div> : null}
      </div>
    );
  };

  // ---- Skeleton ------------------------------------------------------------------------------------------
  // <OO.ui.Skeleton variant="product" count={3} /> — shimmering placeholders while data loads (aria-hidden;
  //   mark the loading region aria-busy). variant: 'text' (lines, default; `lines` = 3) | 'product' (the
  //   product-card shape) | 'tile' (category tile) | 'block' (a plain block; `height` px). count repeats it.
  ui.Skeleton = function Skeleton(props) {
    var variant = props.variant || 'text';
    var count = props.count || 1;
    var items = [];
    for (var i = 0; i < count; i++) {
      if (variant === 'product') {
        items.push(
          <div key={i} className="oo-skel-card oo-skel-card--product">
            <span className="oo-skel oo-skel--media" />
            <span className="oo-skel-card__body">
              <span className="oo-skel oo-skel--line oo-skel--w60" />
              <span className="oo-skel oo-skel--line oo-skel--w90" />
              <span className="oo-skel oo-skel--line oo-skel--w70" />
              <span className="oo-skel oo-skel--line oo-skel--w30 oo-skel--push" />
            </span>
          </div>
        );
      } else if (variant === 'tile') {
        items.push(
          <div key={i} className="oo-skel-card oo-skel-card--tile">
            <span className="oo-skel oo-skel--tilemedia" />
            <span className="oo-skel-card__body"><span className="oo-skel oo-skel--line oo-skel--w60" /><span className="oo-skel oo-skel--line oo-skel--w40" /></span>
          </div>
        );
      } else if (variant === 'block') {
        items.push(<span key={i} className="oo-skel oo-skel--block" style={props.height ? { height: props.height } : undefined} />);
      } else {
        var lines = props.lines || 3;
        var ls = [];
        for (var j = 0; j < lines; j++) ls.push(<span key={j} className={cx('oo-skel', 'oo-skel--line', j === lines - 1 && lines > 1 ? 'oo-skel--w60' : 'oo-skel--w100')} />);
        items.push(<div key={i} className="oo-skel-lines">{ls}</div>);
      }
    }
    return <div className={cx('oo-skel-wrap', 'oo-skel-wrap--' + variant, props.className)} aria-hidden="true">{items}</div>;
  };

  // ---- PoweredBySipo -------------------------------------------------------------------------------------
  // <OO.ui.PoweredBySipo /> — "Powered by" + the SIPO logo (110px, multiply), centred. Logo from
  //   OO.asset('online-order/fixtures/img/sipo-logo.png') for now (Batch 2 may move it). WL-CHK: optional `label` (the
  //   line before the logo; default "Powered by") and `logo` (another bundled asset path, e.g. the colour mark the bold
  //   landing footer shows on its dark band); without them the output is unchanged.
  //   WL-OPT: renders NOTHING when the page's white-label site hides the badge (brand.poweredBy false, read with
  //   OO.whitelabel.poweredBy for `hotel` when given, else the current route's chain or store). Every badge goes through
  //   here, so no page checks it itself. OO.ui.PoweredBySipo.shown(hotel?) → bool is the same check, for a layout that
  //   arranges other things around the badge (the bold landing footer's partner row).
  function poweredShown(hotel) {
    var W = OO.whitelabel;
    if (!W || typeof W.poweredBy !== 'function') return true;
    try { return W.poweredBy(hotel) !== false; } catch (e) { return true; }
  }
  ui.PoweredBySipo = function PoweredBySipo(props) {
    if (!poweredShown(props && props.hotel)) return null;
    return (
      <div className={cx('oo-powered', props && props.className)}>
        <span className="oo-powered__by">{(props && props.label) || 'Powered by'}</span>
        <img className="oo-powered__logo" src={OO.asset((props && props.logo) || 'online-order/fixtures/img/sipo-logo.png')} alt="SIPO Cloud POS" width="110" />
      </div>
    );
  };
  ui.PoweredBySipo.shown = poweredShown;

  // ---- Avatar --------------------------------------------------------------------------------------------
  // <OO.ui.Avatar name="Sam Taylor" src={photo} size={42} /> — round avatar: the photo, else initials, else
  //   the User glyph. size: px (36). ring: 1.5px currentColor ring on transparent (for dark headers). alt
  //   defaults to name; decorative (aria-hidden) when neither is given.
  ui.Avatar = function Avatar(props) {
    var size = props.size || 36;
    var initials = props.name ? String(props.name).trim().split(/\s+/).slice(0, 2).map(function (w) { return w.charAt(0).toUpperCase(); }).join('') : '';
    var content = props.src ? <img src={props.src} alt={props.alt != null ? props.alt : props.name || ''} />
      : initials ? <span aria-hidden={props.name ? undefined : 'true'}>{initials}</span>
      : <OO.icons.User size={Math.round(size / 2)} />;
    return (
      <span className={cx('oo-avatar', props.ring && 'oo-avatar--ring', props.className)} style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
        role={!props.src && props.name ? 'img' : undefined} aria-label={!props.src && props.name ? props.name : undefined}
        aria-hidden={!props.src && !props.name ? 'true' : undefined}>
        {content}
      </span>
    );
  };

  // ---- SectionTitle --------------------------------------------------------------------------------------
  // <OO.ui.SectionTitle>WEEKLY DEALS</OO.ui.SectionTitle> — a section / page heading in the heading font.
  //   level: 'category' (20px / 700 / .01em; 26 desktop — WEEKLY DEALS, MENU, category names; default)
  //   | 'page' (20; 26 desktop — "Order summary", the checkout store name) | 'step' (18; 20 desktop — "Review
  //   your combo") | 'section' (17 — app "Order again", "Popular", "Redeem") | 'group' (15 / 600 Poppins —
  //   "What's included"). sub: a muted line under it (13px; 14 desktop). action: a node on the right
  //   (e.g. <Button variant="link">Full menu</Button>). as: the tag ('h2'). upper: CSS uppercase. id.
  ui.SectionTitle = function SectionTitle(props) {
    var Tag = props.as || 'h2';
    return (
      <div className={cx('oo-stitle', 'oo-stitle--' + (props.level || 'category'), props.upper && 'oo-stitle--upper', props.className)}>
        <div className="oo-stitle__main">
          <Tag className="oo-stitle__text" id={props.id}>{props.children}</Tag>
          {props.sub ? <div className="oo-stitle__sub">{props.sub}</div> : null}
        </div>
        {props.action ? <div className="oo-stitle__action">{props.action}</div> : null}
      </div>
    );
  };

  // ---- Breadcrumbs ---------------------------------------------------------------------------------------
  // <OO.ui.Breadcrumbs items={[{ label: 'Menu', onClick }, { label: 'Burgers', href }, { label: 'Beef burger' }]} />
  //   13.5px muted links separated by small chevrons; the last item is the current page (600, ink).
  ui.Breadcrumbs = function Breadcrumbs(props) {
    var items = props.items || [];
    return (
      <nav className={cx('oo-crumbs', props.className)} aria-label={props.label || 'Breadcrumb'}>
        <ol>
          {items.map(function (it, i) {
            var last = i === items.length - 1;
            var node = last ? <span className="oo-crumbs__current" aria-current="page">{it.label}</span>
              : it.href ? <a className="oo-crumbs__link" href={it.href} onClick={it.onClick}>{it.label}</a>
              : <button type="button" className="oo-crumbs__link" onClick={it.onClick}>{it.label}</button>;
            return (
              <li key={it.key || i}>
                {node}
                {!last ? <OO.icons.ChevronRight size={12} strokeWidth={2.6} className="oo-crumbs__sep" /> : null}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  };

  // ---- SearchField ---------------------------------------------------------------------------------------
  // <OO.ui.SearchField value={q} onChange={setQ} placeholder="Search the menu" /> — the search box.
  //   tone: 'light' (46px outlined, on the canvas; default) | 'dark' (42px #1C1C1C field in the black header).
  //   asButton: renders a button that only looks like the field (onClick opens a search view). onSubmit(value)
  //   on Enter. A clear (×) button shows while there is text. label: accessible name (placeholder or
  //   'Search'). autoFocus, inputRef, id, className.
  ui.SearchField = function SearchField(props) {
    var id = useDomId('oo-search', props.id);
    var tone = props.tone === 'dark' ? 'dark' : 'light';
    var placeholder = props.placeholder || 'Search the menu';
    var cls = cx('oo-search', 'oo-search--' + tone, props.asButton && 'oo-search--button', props.className);
    if (props.asButton) {
      return (
        <button type="button" className={cls} onClick={props.onClick} aria-label={props.label || placeholder}>
          <OO.icons.Search size={18} />
          <span className="oo-search__ph">{props.value || placeholder}</span>
        </button>
      );
    }
    var value = props.value == null ? '' : props.value;
    return (
      <div className={cls} role="search">
        <OO.icons.Search size={18} />
        <label htmlFor={id} className="oo-sr-only">{props.label || placeholder}</label>
        <input
          id={id}
          ref={props.inputRef}
          className="oo-search__input"
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          autoFocus={props.autoFocus}
          onChange={function (e) { if (props.onChange) props.onChange(e.target.value, e); }}
          onKeyDown={function (e) { if (e.key === 'Enter' && props.onSubmit) { e.preventDefault(); props.onSubmit(value); } }}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
        />
        {value ? (
          <button type="button" className="oo-search__clear oo-hit" aria-label="Clear search" onClick={function () { if (props.onChange) props.onChange(''); }}>
            <OO.icons.Close size={14} />
          </button>
        ) : null}
      </div>
    );
  };

  // ---- Booking atoms: GuestPicker / DateStrip / TimeGrid -------------------------------------------------
  // All three are native radio groups (arrow keys move the choice), drawn as outlined tiles that turn solid
  // black when picked; a disabled tile is dimmed (TimeGrid's booked-out slots are also struck through).
  // onChange(value).
  function TileGroup(props) {
    var name = useDomId('oo-tg');
    return (
      <div className={props.className} role="radiogroup" aria-label={props.label}>
        {props.items.map(function (it) {
          var on = props.value != null && String(it.value) === String(props.value);
          return (
            <label key={String(it.value)} className={cx('oo-tile', props.tileClass, it.tileClass)}>
              <input className="oo-tile__input" type="radio" name={name} value={String(it.value)} checked={on} disabled={it.disabled}
                aria-label={it.ariaLabel} onChange={function () { if (props.onChange) props.onChange(it.value); }} />
              <span className="oo-tile__face">{it.content}</span>
            </label>
          );
        })}
      </div>
    );
  }
  // <OO.ui.GuestPicker value={2} onChange={setGuests} /> — 44px squares in a scrolling row. options
  //   ([1, 2, 3, 4, 5, 6, 7, '8+']): values or { value, label }. label ('Guests').
  ui.GuestPicker = function GuestPicker(props) {
    var opts = props.options || [1, 2, 3, 4, 5, 6, 7, '8+'];
    var items = opts.map(function (o) {
      var v = typeof o === 'object' && o ? o.value : o;
      var l = typeof o === 'object' && o ? o.label : String(o);
      return { value: v, content: l, ariaLabel: l + (String(l) === '1' ? ' guest' : ' guests') };
    });
    return <TileGroup className={cx('oo-tiles', props.className)} tileClass="oo-tile--guest" label={props.label || 'Guests'} items={items} value={props.value} onChange={props.onChange} />;
  };
  // <OO.ui.DateStrip days={[{ value: '2026-10-01', dow: 'Today', day: 1, label: 'Thursday 1 October' }]}
  //   value onChange /> — 58×66 day tiles in a scrolling row. day.disabled greys one out. label ('Date').
  ui.DateStrip = function DateStrip(props) {
    var items = (props.days || []).map(function (d) {
      return {
        value: d.value, disabled: d.disabled, ariaLabel: d.label || d.dow + ' ' + d.day,
        content: [<span key="w" className="oo-tile__dow">{d.dow}</span>, <span key="d" className="oo-tile__day">{d.day}</span>],
      };
    });
    return <TileGroup className={cx('oo-tiles', props.className)} tileClass="oo-tile--date" label={props.label || 'Date'} items={items} value={props.value} onChange={props.onChange} />;
  };
  // <OO.ui.TimeGrid slots={[{ value: '18:30', label: '6:30pm', reason: 'full' }]} value onChange /> — a 4-column
  //   grid of 40px time pills. A slot can't be picked when it has a reason: 'full' (struck through, the design's
  //   booked-out slot; legacy `full: true` means the same) or 'past' (dimmed, no strike: the time has gone).
  //   disabled alone dims too. label ('Time').
  ui.TimeGrid = function TimeGrid(props) {
    var items = (props.slots || []).map(function (s) {
      var reason = s.reason === 'full' || s.reason === 'past' ? s.reason : (s.full ? 'full' : null);
      return {
        value: s.value, disabled: !!reason || !!s.disabled, content: s.label, tileClass: reason ? 'oo-tile--' + reason : null,
        ariaLabel: reason === 'full' ? s.label + ', fully booked' : reason === 'past' ? s.label + ', time has passed' : undefined,
      };
    });
    return <TileGroup className={cx('oo-timegrid', props.className)} tileClass="oo-tile--time" label={props.label || 'Time'} items={items} value={props.value} onChange={props.onChange} />;
  };

  // ---- ProgressBar ---------------------------------------------------------------------------------------
  // <OO.ui.ProgressBar value={340} max={500} label="Points to Free Burger" /> — loyalty progress: an 8px
  //   rounded track (accent-bar fill). tone 'inverse': the 6px bar of the dark loyalty card (--oo-card-dark
  //   tokens: a light fill on a translucent track, under every theme).
  ui.ProgressBar = function ProgressBar(props) {
    var max = props.max || 100;
    var value = Math.max(0, Math.min(max, Number(props.value) || 0));
    return (
      <div className={cx('oo-progress', props.tone === 'inverse' && 'oo-progress--inverse', props.className)} role="progressbar"
        aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={props.label}>
        <div className="oo-progress__fill" style={{ width: (value / max) * 100 + '%' }} />
      </div>
    );
  };

  // ---- StatusPill ----------------------------------------------------------------------------------------
  // <OO.ui.StatusPill open>Open until 10pm</OO.ui.StatusPill> — store status.
  //   variant 'pill' (default; green tint when open, brown when closed — store cards) | 'dot' (7px dot + text
  //   in the surrounding colour — the header status line). tone overrides open: 'open' | 'closed' |
  //   'neutral' (canvas pill: "1.2 km", "Pickup · Dine-in").
  ui.StatusPill = function StatusPill(props) {
    var tone = props.tone || (props.open ? 'open' : 'closed');
    var dot = props.variant === 'dot';
    return (
      <span className={cx('oo-status', 'oo-status--' + tone, dot && 'oo-status--dot', props.className)}>
        {dot ? <span className="oo-status__dot" aria-hidden="true" /> : null}
        {props.children}
      </span>
    );
  };

  // ---- TotalBlock ----------------------------------------------------------------------------------------
  // <OO.ui.TotalBlock label="Combo total" amount="$29.90" /> — the footer total: a 12px muted label over a
  //   19px / 700 amount (min-width 92px). Used by BottomBarCTA and inside Sheet footers. onLabelClick: the
  //   label becomes a toggle with a chevron (up; down when labelExpanded).
  ui.TotalBlock = function TotalBlock(props) {
    var labelNode = props.onLabelClick ? (
      <button type="button" className="oo-total__label oo-total__label--btn" onClick={props.onLabelClick} aria-expanded={props.labelExpanded ? 'true' : 'false'}>
        {props.label}{props.labelExpanded ? <OO.icons.ChevronDown size={12} /> : <OO.icons.ChevronUp size={12} />}
      </button>
    ) : <span className="oo-total__label">{props.label}</span>;
    return (
      <div className={cx('oo-total', props.className)}>
        {labelNode}
        <span className="oo-total__amount">{props.amount}</span>
      </div>
    );
  };

  // ---- BottomBarCTA --------------------------------------------------------------------------------------
  // <OO.ui.BottomBarCTA label="Total · GST incl." amount="$27.00"><OO.ui.Button block>Checkout</OO.ui.Button>
  //   </OO.ui.BottomBarCTA> — the white mobile footer fixed to the bottom: an optional total block on the left
  //   (12px muted label, 19px / 700 amount) and the CTA (children) filling the rest.
  //   onLabelClick: the label becomes a button with an up chevron (the "collapsed" price-summary tweak);
  //   labelExpanded flips it. note: a centred muted line ABOVE the CTA (booking summary) — the bar stacks.
  //   Renders an in-flow spacer of its own height. Hidden on online desktop (the desktop design puts the
  //   CTA in the summary card) unless showWide.
  ui.BottomBarCTA = function BottomBarCTA(props) {
    var barRef = React.useRef(null);
    var h = React.useState(0);
    React.useLayoutEffect(function () {
      var el = barRef.current;
      if (!el) return undefined;
      function measure() { h[1](el.offsetHeight); }
      measure();
      if (!window.ResizeObserver) return undefined;
      var ro = new ResizeObserver(measure);
      ro.observe(el);
      return function () { ro.disconnect(); };
    }, []);
    var hasSum = props.amount != null || props.label != null;
    var hideWide = !props.showWide && 'oo-bottombar--hide-wide';
    return (
      <React.Fragment>
        <div className={cx('oo-bottombar-spacer', hideWide)} style={{ height: h[0] || undefined }} aria-hidden="true" />
        <div ref={barRef} className={cx('oo-bottombar', props.note && 'oo-bottombar--stack', hideWide, props.className)}>
          {props.note ? <div className="oo-bottombar__note" aria-live="polite">{props.note}</div> : null}
          {hasSum && !props.note ? (
            <ui.TotalBlock label={props.label} amount={props.amount} onLabelClick={props.onLabelClick} labelExpanded={props.labelExpanded} />
          ) : null}
          <div className="oo-bottombar__cta">{props.children}</div>
        </div>
      </React.Fragment>
    );
  };
})();


// ===================== src/ui/chrome.jsx =====================
// src/ui/chrome.jsx: the page chrome on OO.ui — PageFrame, Container, StoreHeader, PageHeader, HeaderLink,
// HeaderCartPill, CategoryTabs, AppTopBar and AppTabBar. Styles in styles/chrome.css (tokens only).
// Owner: T2b (UI kit). Built on src/ui/primitives.jsx (IconButton, SearchField, StatusPill, cx).
// The responsive convention (.oo-wide + @media (min-width: 900px)) is documented in styles/primitives.css.
//
// T1 contract kept: <OO.ui.PageFrame mode={'online'|'app'} withTabBar={bool}>…</OO.ui.PageFrame> and
// <OO.ui.AppTabBar route={route} /> (src/app.jsx renders both).
//
// Ratings (Batch 2 P3-07; REF RatingBadge.tsx, RestaurantContext.tsx, UnifiedProductModalCore ProductRatingPill). The
// average ratings show only where the store's review_settings allow them (getRestaurant; the admin's "Display ratings")
// and the store takes ratings (its review-feedback module: the rating endpoints answer 403 when it's off), and only
// with at least one review. Styles: the "P3-06/07 ratings" section of styles/pages.css.
//   <OO.ui.RatingBadge average={4.6} total={212} variant="store" | "product" tone="light" | "dark" className />
//       store: "4.6 ★ · 212 reviews" ("reviews" on wide screens only; tone dark = on the black store header); product:
//       the item sheet's pill "★ 4.3 1.2k". role img, aria-label 'Rated 4.6 out of 5 from 212 reviews'. Nothing for a
//       total or an average that isn't above 0.
//   <OO.ui.StoreRating hotel store tone className /> the store's badge (useStoreRating), or nothing.
//   useReviewSettings(hotel, store) → { storeRating, productRating } | null (not known yet, or unreadable: show nothing).
//       store.reviewSettings when the Store carries it (the store normaliser's review_settings), else
//       OO.model.restaurantReviewSettings of OO.api.getRestaurant(hotel) (the adapter's cached body), read once per Store
//       object: a store re-read (a new Store) reads it again.
//   useStoreRating(hotel, store) → { average, total } | null   OO.api.getStoreRating(hotel) (E28) when storeRating.
//   useProductRating(hotel, store, productId) → { average, total } | null   OO.api.getProductRating (E29) when
//       productRating. Both: OO.model.normalizeRatingSummary; null while loading, on any failure (a 403, the fixture
//       stores' missing method) and with no reviews.
//   useRatingRead(key, read) → the value read() resolved for this key | null: the shared async read of these hooks (no
//       read while key is null; a new key reads again; a rejection is null).
//   hotel defaults to store.onlineKey in all of them.
//
// The production test (Batch 2 P3-26; D-10: notifications stay hidden in production until the notifications store, B20).
//   OO.ui.liveProduction(hotel) → bool: true when the page runs in production for a LIVE store: OO.boot.env (the blade's
//       __SIPO_BOOT.env, Laravel's app()->environment()) is 'production' and the store is not a fixture store
//       (OO.api.isFixtureFor(hotel); hotel defaults to the current route's, OO.router.current()). Dev, local and staging
//       hosts and the fixture (sample) stores answer false, so they keep the design's notifications and sample copy. The
//       build flags can't tell: npm run online:build always builds with NODE_ENV=production, and the same committed
//       bundle serves the dev server (APP_ENV=local) and the production hosts. Read per call. Its users:
//       OO.pages.Notifications.enabled (src/pages/app-notifications.jsx: the route, the bell below, the account hub's
//       row) and the legal pages' "not available" state (src/pages/legal.jsx, P3-25).
//
// Install prompt (Batch 2 P5-03; REF InstallAppPrompt.tsx) offers to install the store as an app from its web app
// manifest ({routeBase}/{key}/manifest.webmanifest, which the shell links on a store's pages; none in mode qr).
//   OO.ui.useInstallPrompt(route, mode) → 'prompt' | 'ios' | null   a hook OO.App calls on every render: what shows
//       (logic.gate on this page load's state); it re-renders the caller when the browser's event lands or the banner
//       closes, and latches the iOS hint (below).
//   <OO.ui.InstallPrompt kind route mode />   the banner (OO.App puts it in the preview bar's slot, above the page: a
//       store with the sample-data bar is never live, so the two never meet). An unobtrusive inline banner (no overlay:
//       the menu's own prompts and bars stay clear): the store's logo and name ("Install {store}"; mode app: the
//       chain's, as loaded), then for kind
//   'prompt' — Chrome / Android / desktop Chromium: the browser's beforeinstallprompt event, captured from load where the
//       browser has it ('onbeforeinstallprompt' in window: an event that fires before the first render isn't lost; its
//       mini-infobar is suppressed) → an Install button that opens the browser's own dialog (accepted: gone; declined:
//       dismissed);
//   'ios' — iOS Safari (no such event) → a one-time hint "Tap Share, then Add to Home Screen": it counts as dismissed
//       the first time it shows, and stays up for the rest of that page load until closed.
// × dismisses it for 14 days. Never in the native app shell (OO.platform.inShell(), or any shell bridge trace), never
// in mode qr, never when already installed (display-mode standalone, navigator.standalone), never for a store this host
// doesn't serve live (OO.api.availabilityOf) or a store-less page, never while redirecting, never on the cart, checkout
// or payment pages (INSTALL_OFF: cart, checkout, payment-callback, order-status) or the dev / not-found pages.
// OO.ui.InstallPrompt.logic (pure; tests/install-prompt-p503.test.mjs):
//   gate({ mode, page, hotel, live, inShell, standalone, redirecting, dismissedAt, now, prompt, ios, latched, preview }) →
//       'prompt' | 'ios' | null   (preview: a white-label studio preview, WL-2: never)   prompt: a beforeinstallprompt event is held; ios: isIosSafari; latched: the iOS hint
//       already showed in this page load (the dismissal it wrote doesn't hide it).
//   isIosSafari(navigator) → bool   iPhone / iPod / iPad (also iPadOS's desktop "Macintosh" UA with touch points)
//       Safari: not Chrome / Firefox / Edge / Opera / the Google app or the Facebook, Instagram and LINE in-app
//       browsers, nor an app's web view (no "Safari/" in its UA).
//   dismissedAt(storage) → ms | null   localStorage 'oo-install-v1' { at }; missing, unreadable or throwing → null.
//   recent(at, now) → bool   at is a number and now − at < DISMISS_MS (a clock set back counts as recent).
//   dismiss(storage, now) → bool   writes { at: now }; false when storage is missing or throws.
//   KEY 'oo-install-v1', DISMISS_MS (14 days), OFF (the pages it never shows on).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.ui = OO.ui || {};
  var ui = OO.ui;
  var cx = ui.cx;

  // <OO.ui.PageFrame mode="online" | "app" withTabBar> — the page shell.
  //   online: full width, and the .oo-wide scope that switches the desktop layout on at >= 900px.
  //   app: a phone-width column (<= --oo-app-max) centred on the --oo-backdrop on wide screens, with the
  //   .oo-app scope (app header / bar variants); withTabBar adds .oo-has-tabs and pads for the tab bar.
  //   Nothing here may create a containing block (no transform / filter / contain): fixed bars and sheets
  //   inside must stay fixed to the viewport.
  //   WL-SUPER: the white-label skin (OO.whitelabel.skin: 'super' from surfaces.app / surfaces.online layout.design)
  //   adds oo-skin--super to the frame (never on the landing website's own page, which draws its own design; never in
  //   kiosk), and the frame keeps data-oo-skin="super" on <html> for the sheets and toasts portalled to <body> (the
  //   landing page too, for landing.design 'super'). No skin: the frame exactly as before, and no attribute.
  //   WL-VEL: skin 'boutique' (surfaces.online.layout.design, mode online and the landing website's ordering pages) adds
  //   oo-skin--boutique to the frame the same way, keeps data-oo-skin="boutique" on <html>, and ALSO puts the class on
  //   <html> and <body> (ui.skinDocument): its token overrides live on :root, where the derived tokens recompute, and
  //   its sheets, dialogs and toasts portal to <body>. 'super' never gets the <html> / <body> class (unchanged).
  var DOC_SKINS = ['boutique', 'pizzeria', 'pizzaclub', 'zeta', 'kkitchen', 'storefront']; // Include portal sheets in the selected ordering style.
  // ui.skinDocument(html, body, skin): oo-skin--{k} on both elements for the DOC_SKINS value `skin`, off for the others
  // (a missing element or one without classList is skipped). Exported for the tests (the effect below calls it).
  ui.skinDocument = function skinDocument(html, body, skin) {
    [html, body].forEach(function (el) {
      if (!el || !el.classList) return;
      DOC_SKINS.forEach(function (k) { if (k === skin) el.classList.add('oo-skin--' + k); else el.classList.remove('oo-skin--' + k); });
    });
  };
  var useSkinEffect = typeof document !== 'undefined' && React.useLayoutEffect ? React.useLayoutEffect : React.useEffect;
  ui.PageFrame = function PageFrame(props) {
    var route = (OO.router && typeof OO.router.current === 'function' ? OO.router.current() : null) || {};
    var W = OO.whitelabel;
    var skinned = props.mode !== 'kiosk' && W && typeof W.skin === 'function' ? W.skin(route.chain || route.hotel, route.page) : null;
    var skin = route.page === 'landing' ? null : skinned;
    useSkinEffect(function () {
      var el = typeof document !== 'undefined' ? document.documentElement : null;
      if (!el || !el.setAttribute) return undefined;
      if (skinned) el.setAttribute('data-oo-skin', skinned); else el.removeAttribute('data-oo-skin');
      ui.skinDocument(el, document.body, skinned); // WL-VEL
      return undefined;
    }, [skinned]);
    if (props.mode === 'app' || skin === 'zeta') {
      var market = OO.whitelabel && typeof OO.whitelabel.appDesign === 'function' && OO.whitelabel.appDesign(route.chain || route.hotel);
      return (
        <div className={cx('oo-frame', props.mode === 'app' ? 'oo-frame--app' : 'oo-frame--online', 'oo-app', market && 'oo-app--market', skin && 'oo-skin--' + skin, props.withTabBar && 'oo-has-tabs', props.className)}>
          <div className="oo-frame__col">{skin === 'kkitchen' && ui.KitchenHeader ? <React.Fragment><ui.KitchenHeader route={route} />{props.children}</React.Fragment> : skin === 'zeta' && ui.ZetaHeader ? <React.Fragment><ui.ZetaHeader route={route} />{props.children}</React.Fragment> : skin === 'pizzaclub' && ui.PizzaClubHeader ? <React.Fragment><ui.PizzaClubHeader route={route} />{props.children}</React.Fragment> : props.children}</div>
        </div>
      );
    }
    return <div className={cx('oo-frame', 'oo-frame--online', 'oo-wide', props.mode === 'kiosk' && 'oo-kiosk', skin && 'oo-skin--' + skin, props.className)}>{props.children}</div>;
  };

  // <OO.ui.Container>…</OO.ui.Container> — the content column: 16px gutters on mobile; on desktop the design's
  //   1200px content width (120px side margins at 1440) with 24px gutters below that. as ('div'), className.
  ui.Container = function Container(props) {
    var Tag = props.as || 'div';
    return <Tag className={cx('oo-container', props.className)} id={props.id}>{props.children}</Tag>;
  };

  function backButton(props) {
    if (props.showBack === false || (!props.onBack && !props.backHref)) return null;
    return (
      <ui.IconButton icon="ArrowLeft" variant="bare" label={props.backLabel || 'Back'} href={props.backHref}
        onClick={props.onBack} className="oo-head__back" />
    );
  }
  function profileButton(props) {
    if (!props.onProfile && !props.profileHref) return null;
    return (
      <ui.IconButton icon="User" variant="ring" label={props.profileLabel || 'Account'} href={props.profileHref}
        onClick={props.onProfile} className="oo-head__profile" />
    );
  }

  // Keep the store name as the heading's accessible label and as a fallback if its Studio logo cannot load.
  function HeaderTitle(props) {
    var failed = React.useState(null);
    var logo = props.logo && failed[0] !== props.logo ? props.logo : null;
    return <h1 className={cx(props.className, logo && 'oo-head__title--logo')} id={props.id}>
      {logo ? <img className="oo-head__logo" src={logo} alt={props.title} onError={function () { failed[1](props.logo); }} /> : props.title}
    </h1>;
  }

  // <OO.ui.StoreHeader name address onBack onAbout cartCount onCart onProfile search={…} /> — the black store
  //   header of the online menu (not sticky; the CategoryTabs under it are).
  //   Mobile: back · name (16 / 700) over a pin + address line (11.5px) · About ring · profile ring.
  //   Desktop (1200px row, 76px): back · name (20) + address (13) · search field · About pill · "Cart (n)"
  //   pill · profile ring (42).
  //   name, address, addressShort (mobile line; defaults to address), onBack / backHref / backLabel /
  //   showBack, onAbout (About button; aboutLabel), cartCount + onCart / cartHref (desktop cart pill),
  //   onProfile / profileHref / profileLabel, search: { value, onChange, onSubmit, placeholder, inputRef }
  //   (desktop field; inputRef reaches its <input>, e.g. to focus it; omit search for none), className.
  //   logo (optional): the Studio logo replaces the visible name; the address stays below it.
  //   Ratings remain on the store information page.
  ui.StoreHeader = function StoreHeader(props) {
    var addr = props.address;
    var addrShort = props.addressShort || addr;
    var cartTag = props.cartHref ? 'a' : 'button';
    var cartAttrs = props.cartHref ? { href: props.cartHref, onClick: props.onCart } : { type: 'button', onClick: props.onCart };
    var showCart = props.cartCount != null && (props.onCart || props.cartHref);
    var name = props.logo ? <HeaderTitle className="oo-shead__name" title={props.name} logo={props.logo} />
      : <h1 className="oo-shead__name">{props.name}</h1>;
    return (
      <header className={cx('oo-shead', props.logo && 'oo-shead--logo', props.className)}>
        <div className="oo-shead__in">
          {backButton(props)}
          <div className="oo-shead__titles">
            {props.store ? (
              <div className="oo-shead__namerow">
                {name}
              </div>
            ) : name}
            {props.store && props.store.orderingQr ? <p className="oo-shead__addr">{props.store.orderingQr.name}</p> : null}
            {addr ? (
              <p className="oo-shead__addr">
                <OO.icons.Pin size={12} className="oo-shead__pin" />
                <span className="oo-shead__addr-text oo-only-narrow">{addrShort}</span>
                <span className="oo-shead__addr-text oo-only-wide">{addr}</span>
              </p>
            ) : null}
          </div>
          {props.search ? (
            <ui.SearchField tone="dark" className="oo-shead__search oo-only-wide" value={props.search.value} onChange={props.search.onChange}
              onSubmit={props.search.onSubmit} placeholder={props.search.placeholder} inputRef={props.search.inputRef} />
          ) : null}
          {props.onAbout ? (
            <ui.IconButton icon="Store" variant="ring" label={props.aboutLabel || 'About this store'} onClick={props.onAbout} className="oo-only-narrow" />
          ) : null}
          {props.onAbout ? (
            <button type="button" className="oo-shead__pill oo-only-wide" onClick={props.onAbout}>
              <OO.icons.Store size={18} />About
            </button>
          ) : null}
          {showCart ? (
            React.createElement(cartTag, Object.assign({ className: 'oo-shead__pill oo-only-wide' }, cartAttrs),
              <OO.icons.Bag size={18} />, <span className="oo-shead__pill-text">{'Cart (' + props.cartCount + ')'}</span>)
          ) : null}
          {profileButton(props)}
        </div>
      </header>
    );
  };

  // <OO.ui.PageHeader title="Cart" onBack onProfile /> — the black bar of the inner pages (sticky).
  //   Mobile 64px: back · title (18 / 700) · right · profile ring (36). With `subtitle` it is the browse /
  //   combo bar: title 16 / 700 over an 11.5px sub-line (desktop: 20 / 13, 76px). Desktop: a 1200px row,
  //   72px, title 22. In app mode (.oo-app) it is the customer-app header: 18px side padding, 12px sub-line,
  //   status-bar safe area on top.
  //   title, subtitle, onBack / backHref / backLabel / showBack, right (nodes before the profile ring, e.g.
  //   <OO.ui.HeaderLink> or <OO.ui.HeaderCartPill>), onProfile / profileHref / profileLabel, sticky (true),
  //   as ('header'), titleId, className.
  ui.PageHeader = function PageHeader(props) {
    var Tag = props.as || 'header';
    return (
      <Tag className={cx('oo-phead', props.subtitle && 'oo-phead--sub', props.logo && 'oo-phead--logo', props.sticky === false && 'oo-phead--static', props.className)}>
        <div className="oo-phead__in">
          {backButton(props)}
          <div className="oo-phead__titles">
            {props.logo ? <HeaderTitle className="oo-phead__title" id={props.titleId} title={props.title} logo={props.logo} />
              : <h1 className="oo-phead__title" id={props.titleId}>{props.title}</h1>}
            {props.subtitle ? <div className="oo-phead__sub">{props.subtitle}</div> : null}
          </div>
          {props.right ? <div className="oo-phead__right">{props.right}</div> : null}
          {profileButton(props)}
        </div>
      </Tag>
    );
  };

  // <OO.ui.HeaderLink onClick>Mark all read</OO.ui.HeaderLink> — a 13px text action on the dark header
  //   ("How it works", "Mark all read"). href → <a>. disabled: a dimmed, non-clickable button (e.g. "Mark all
  //   read" with nothing unread), even when href is given.
  ui.HeaderLink = function HeaderLink(props) {
    if (props.href && !props.disabled) return <a className={cx('oo-headlink', props.className)} href={props.href} onClick={props.onClick}>{props.children}</a>;
    return <button type="button" className={cx('oo-headlink', props.className)} onClick={props.onClick} disabled={props.disabled}>{props.children}</button>;
  };

  // <OO.ui.HeaderCartPill count={2} onClick /> — the small white bag + count pill of the mobile combo header.
  //   label ('Cart'), href → <a>.
  ui.HeaderCartPill = function HeaderCartPill(props) {
    var Tag = props.href ? 'a' : 'button';
    var attrs = props.href ? { href: props.href } : { type: 'button' };
    return (
      <Tag {...attrs} className={cx('oo-cartpill', 'oo-hit', props.className)} onClick={props.onClick}
        aria-label={(props.label || 'Cart') + ', ' + (props.count || 0) + ((props.count || 0) === 1 ? ' item' : ' items')}>
        <OO.icons.Bag size={14} strokeWidth={2.2} />
        <span aria-hidden="true">{props.count || 0}</span>
      </Tag>
    );
  };

  // <OO.ui.CategoryTabs tabs={[{ id, label }]} active={id} onSelect={(id) => …} onSearch={…} /> — the white
  //   sticky bar under the store header: a search button, then the horizontally scrolling uppercase section
  //   tabs (12.5px; 13.5 desktop), the active one in 600 with a 2.5px --oo-accent-bar underline. The active
  //   tab is scrolled into view when it is outside it. sticky (true), label ('Menu sections'), searchLabel.
  ui.CategoryTabs = function CategoryTabs(props) {
    var listRef = React.useRef(null);
    var tabs = props.tabs || [];
    React.useEffect(function () {
      var list = listRef.current;
      if (!list) return;
      var el = list.querySelector('.is-active');
      if (!el) return;
      // Only when the active tab is (partly) out of view: centre it. The list is its offsetParent.
      var from = el.offsetLeft, to = from + el.offsetWidth;
      if (from >= list.scrollLeft && to <= list.scrollLeft + list.clientWidth) return;
      var left = Math.max(0, from - (list.clientWidth - el.offsetWidth) / 2);
      if (list.scrollTo) list.scrollTo({ left: left, behavior: 'smooth' });
      else list.scrollLeft = left;
    }, [props.active]);
    return (
      <div className={cx('oo-ctabs', props.sticky === false && 'oo-ctabs--static', props.className)}>
        <div className="oo-ctabs__in">
          {props.onSearch ? (
            <button type="button" className="oo-ctabs__search oo-hit" onClick={props.onSearch} aria-label={props.searchLabel || 'Search the menu'}>
              <OO.icons.Search size={20} />
            </button>
          ) : null}
          <nav className="oo-ctabs__list" ref={listRef} aria-label={props.label || 'Menu sections'}>
            {tabs.map(function (t) {
              var on = t.id === props.active;
              return (
                <button key={t.id} type="button" className={cx('oo-ctabs__tab', on && 'is-active')} aria-current={on ? 'true' : undefined}
                  onClick={function () { if (props.onSelect) props.onSelect(t.id, t); }}>
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    );
  };

  // ---- the production test (P3-26; the header above) --------------------------------------------------------------
  function routeHotel() {
    try { return OO.router && typeof OO.router.current === 'function' ? OO.router.current().hotel || null : null; } catch (e) { return null; }
  }
  ui.liveProduction = function liveProduction(hotel) {
    var boot = OO.boot || window.__SIPO_BOOT || {};
    if (boot.env !== 'production') return false;
    var key = hotel === undefined || hotel === null || hotel === '' ? routeHotel() : String(hotel);
    var api = OO.api;
    return !(api && typeof api.isFixtureFor === 'function' && api.isFixtureFor(key));
  };
  // The bell's gate: OO.pages.Notifications.enabled when that page is loaded (it is, in the bundle), else the test itself.
  function notificationsOn(hotel) {
    var N = OO.pages && OO.pages.Notifications;
    return N && typeof N.enabled === 'function' ? !!N.enabled(hotel) : !ui.liveProduction(hotel);
  }

  // <OO.ui.AppTopBar greeting="Good evening, Sam" storeName="Bennys Burger Chapel Street" onStoreClick
  //   open status="Open until 10pm" distance="1.2 km" unread={2} onBell /> — the app Home header in the
  //   header colours (--oo-header-*): greeting (13px), the store switcher (17 / 700 + chevron), the status
  //   line (7px dot + "Open until 10pm · 1.2 km"), and the bell ring (42px) with an unread badge.
  //   Brand variant (pass brand and / or logo): a 52px logo tile, the brand in the theme heading font (22px)
  //   and a store line with a 7px --oo-accent dot (customer-app "T" themes); storeName / status form that line; a
  //   greeting (WL-2: a white-label document's app.greeting) goes above the brand.
  //   bellLabel ('Notifications'), className.
  //   hotel (P3-26): the store the bell belongs to (default: the current route's). The bell and its unread badge show
  //   only while notifications are on for it (notificationsOn above: OO.pages.Notifications.enabled): never for a live
  //   store in production (D-10). Without the bell the row is the main block alone.
  ui.AppTopBar = function AppTopBar(props) {
    var unread = Number(props.unread) || 0;
    var statusText = [props.status, props.distance].filter(Boolean).join(' · ');
    var bell = notificationsOn(props.hotel) ? (
      <ui.IconButton icon="Bell" variant="ring" size="lg" label={(props.bellLabel || 'Notifications') + (unread ? ', ' + unread + ' unread' : '')}
        badge={unread ? (unread > 99 ? '99+' : String(unread)) : null} onClick={props.onBell} href={props.bellHref} />
    ) : null;
    if (props.brand || props.logo) {
      var line = [props.storeName, props.status].filter(Boolean).join(' · ');
      var LineTag = props.onStoreClick ? 'button' : 'div';
      return (
        <header className={cx('oo-atop', 'oo-atop--brand', props.className)}>
          <div className="oo-atop__row">
            <div className="oo-atop__logo">
              {props.logo ? <img src={props.logo} alt={props.brand ? props.brand + ' logo' : 'Logo'} /> : <OO.icons.ImagePlaceholder size={22} />}
            </div>
            <div className="oo-atop__main oo-atop__main--brand">
              {props.greeting ? <div className="oo-atop__greet">{props.greeting}</div> : null}
              {props.showStoreName !== false ? <div className="oo-atop__brand">{props.brand}</div> : null}
              <LineTag className="oo-atop__storeline" type={props.onStoreClick ? 'button' : undefined} onClick={props.onStoreClick}>
                <span className="oo-atop__accentdot" aria-hidden="true" />
                <span className="oo-atop__storeline-text">{line}</span>
              </LineTag>
            </div>
            {bell}
          </div>
        </header>
      );
    }
    return (
      <header className={cx('oo-atop', props.className)}>
        <div className="oo-atop__row">
          <div className="oo-atop__main">
            {props.greeting ? <div className="oo-atop__greet">{props.greeting}</div> : null}
            <button type="button" className="oo-atop__store" onClick={props.onStoreClick} aria-label={'Store: ' + props.storeName + '. Change store'}>
              <span className="oo-atop__store-name">{props.showStoreName === false ? 'Change store' : props.storeName}</span>
              <OO.icons.ChevronDown size={14} strokeWidth={2.8} />
            </button>
            {statusText ? (
              <ui.StatusPill variant="dot" open={props.open !== false} className="oo-atop__status">{statusText}</ui.StatusPill>
            ) : null}
          </div>
          {bell}
        </div>
      </header>
    );
  };

  // The market header uses the selected branch and the existing cart; it adds no ordering or location defaults.
  ui.MarketHeader = function MarketHeader(props) {
    var route = props.route, store = props.store;
    var theme = props.theme || OO.theme.resolve(store.theme);
    var cart = OO.useStore(function (state) { return state.cart; });
    var count = cart && String(cart.hotel) === String(route.hotel) ? OO.cart.count(cart.lines) : 0;
    var link = function (page) { return OO.router.link(page, null, { hotel: route.hotel }); };
    var home = link('home'), stores = link('stores'), basket = link('cart');
    var name = theme.brand || store.name;
    var place = props.place || store.name;
    // Bundled brand assets follow the deployment root, including Studio previews on another host.
    var logo = theme.logo;
    var bundled = /^https:\/\/app\.sipocloudpos\.com\/(online-order\/brands\/[a-z0-9_./-]+\.png)$/.exec(logo || '');
    if (bundled && bundled[1].indexOf('..') < 0) logo = OO.asset(bundled[1]);
    return <header className="oo-market-head">
      <div className="oo-market-head__row">
        {props.onBack ? <ui.IconButton icon="ArrowLeft" variant="bare" label={props.backLabel || 'Back'} onClick={props.onBack} /> : null}
        <a className="oo-market-head__brand" href={home.href} onClick={home.onClick} aria-label={name + ' home'}>
          {logo ? <img src={logo} alt={name} /> : <span>{name}</span>}
        </a>
        <ui.IconButton icon="Bag" variant="ring" size="lg" label={'Cart' + (count ? ', ' + count + ' items' : '')}
          badge={count ? String(count) : null} href={basket.href} onClick={basket.onClick} />
      </div>
      <a className="oo-market-head__location" href={stores.href} onClick={stores.onClick} aria-label={'Store: ' + place + '. Change store'}>
        <OO.icons.Pin size={16} /><span>{place}</span><span className="oo-market-head__change">Change</span>
      </a>
      {props.status ? <p className="oo-market-head__status">{props.status}</p> : null}
    </header>;
  };

  // <OO.ui.AppTabBar route={route} /> — the app's bottom navigation: Home · Menu · Orders · Rewards ·
  //   Account (22px icons, 10.5px labels; the active tab in --oo-primary / 600 with the Home glyph filled).
  //   Fixed to the bottom of the phone column with the safe-area inset. Links go through OO.router.link and
  //   keep the preview query params of the current route (?theme=, ?layout=), like OO.appKit's links.
  //   Sub-pages light up their parent tab (stores / booking / notifications → Home, cart / checkout →
  //   Menu, order-status → Orders, profile → Account).
  //   WL-2: a white-label document's app.tabBar (OO.whitelabel.tabBar(the route's chain or hotel)) picks the tabs and
  //   their order from home · menu · rewards · account · orders · stores (Stores then lights up for itself).
  var TABS = [
    { page: 'home', label: 'Home', icon: 'Home' },
    { page: 'menu', label: 'Menu', icon: 'MenuList' },
    { page: 'orders', label: 'Orders', icon: 'Receipt' },
    { page: 'rewards', label: 'Rewards', icon: 'Star' },
    { page: 'account', label: 'Account', icon: 'User' },
  ];
  var STORES_TAB = { page: 'stores', label: 'Stores', icon: 'Store' };
  function tabsFor(route) {
    var W = OO.whitelabel;
    var keys = W && typeof W.tabBar === 'function' ? W.tabBar(route.chain || route.hotel) : null;
    if (!keys) return TABS;
    var all = TABS.concat([STORES_TAB, { page: 'booking', label: 'Book', icon: 'Calendar' }]);
    return keys.map(function (k) { return all.filter(function (t) { return t.page === k; })[0]; }).filter(Boolean);
  }
  var TAB_OF = {
    home: 'home', stores: 'home', booking: 'home', notifications: 'home',
    menu: 'menu', cart: 'menu', checkout: 'menu',
    orders: 'orders', 'order-status': 'orders',
    rewards: 'rewards',
    account: 'account', profile: 'account',
  };
  var PREVIEW_KEYS = ['theme', 'layout'];
  function previewQuery(query) {
    var out = {};
    PREVIEW_KEYS.forEach(function (k) {
      if (query && typeof query[k] === 'string' && query[k] !== '') out[k] = query[k];
    });
    return Object.keys(out).length ? out : undefined;
  }
  ui.AppTabBar = function AppTabBar(props) {
    var route = props.route || {};
    if (ui.ZetaTabBar && OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign(route.chain||route.hotel)) return <ui.ZetaTabBar route={route}/>;
    var current = Object.prototype.hasOwnProperty.call(TAB_OF, route.page) ? TAB_OF[route.page] : null;
    var query = previewQuery(route.query);
    var tabs = tabsFor(route);
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign(route.chain || route.hotel);
    if (zeta) {
      tabs = tabs.map(function (tab) { return tab.page === 'rewards' ? { page: 'deals', label: zeta.zoneLabel || 'Zeta Zone', icon: 'Sparkles' }
        : tab.page === 'menu' ? { page: 'menu', label: 'Menu', icon: 'Book' } : tab.page === 'stores' ? { page: 'stores', label: 'Stores', icon: 'Pin' }
        : tab.page === 'account' ? { page: 'account', label: 'Account', icon: 'UserCircle' } : tab; });
      if (['deals', 'rewards', 'wallet', 'coupons'].indexOf(route.page) >= 0) current = 'deals';
    }
    if (OO.whitelabel && typeof OO.whitelabel.appDesign === 'function' && OO.whitelabel.appDesign(route.chain || route.hotel)) {
      tabs = tabs.map(function (tab) { return tab.page === 'menu' ? { page: 'menu', label: 'Shop', icon: 'Search' }
        : tab.page === 'stores' ? { page: 'stores', label: 'Stores', icon: 'Pin' } : tab; });
    }
    if (OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(route.chain || route.hotel)) {
      tabs = tabs.map(function (tab) { return tab.page === 'rewards' ? { page: 'rewards', label: 'Club', icon: 'Star' } : tab.page === 'menu' ? { page: 'menu', label: 'Menu', icon: 'Book' } : tab.page === 'stores' ? { page: 'stores', label: 'Stores', icon: 'Pin' } : tab; });
      if (route.page === 'wallet' || route.page === 'coupons' || route.page === 'deals') current = 'rewards';
      if (route.page === 'profile') current = 'account';
    }
    if (OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(route.chain || route.hotel)) {
      tabs = tabs.map(function (tab) { return tab.page === 'menu' ? { page: 'menu', label: 'Menu', icon: 'Book' } : tab; });
      if (['wallet', 'coupons', 'deals'].indexOf(route.page) >= 0) current = 'rewards';
    }
    if (route.page === 'booking' && tabs.some(function (t) { return t.page === 'booking'; })) current = 'booking';
    if (route.page === 'stores' && tabs.some(function (t) { return t.page === 'stores'; })) current = 'stores';
    return (
      <nav className={cx('oo-tabbar', props.className)} aria-label="Main">
        {tabs.map(function (t) {
          var active = current === t.page;
          var link = OO.router.link(t.page, null, { hotel: route.hotel, query: query });
          var Icon = OO.icons[t.icon];
          return (
            <a key={t.page} className="oo-tabbar__item" href={link.href} onClick={link.onClick} aria-current={active ? 'page' : undefined}>
              <Icon size={22} filled={t.page === 'home' && active} />
              <span className="oo-tabbar__label">{t.label}</span>
            </a>
          );
        })}
      </nav>
    );
  };

  // ---- Ratings (P3-07; the header above) ---------------------------------------------------------------------------
  function hotelOf(hotel, store) {
    if (hotel !== undefined && hotel !== null && hotel !== '') return String(hotel);
    return store && store.onlineKey ? String(store.onlineKey) : '';
  }
  // A short tag per Store object, so a re-read store (a new object) makes a new read key.
  var storeTags = typeof WeakMap === 'function' ? new WeakMap() : null;
  var tagSeq = 0;
  function storeTag(store) {
    if (!storeTags || !store || typeof store !== 'object') return '';
    var t = storeTags.get(store);
    if (!t) { t = 's' + (tagSeq += 1); storeTags.set(store, t); }
    return t;
  }

  ui.useRatingRead = function useRatingRead(key, read) {
    var st = React.useState({ key: null, value: null });
    var readRef = React.useRef(read);
    readRef.current = read;
    React.useEffect(function () {
      if (!key) return undefined;
      var alive = true;
      Promise.resolve().then(function () { return readRef.current(); }).then(function (v) {
        if (alive) st[1]({ key: key, value: v === undefined ? null : v });
      }, function () {
        if (alive) st[1]({ key: key, value: null });
      });
      return function () { alive = false; };
    }, [key]);
    return key && st[0].key === key ? st[0].value : null;
  };

  // review_settings: the Store's own (once the store normaliser carries it), else read once per Store object.
  var settingsMemo = typeof WeakMap === 'function' ? new WeakMap() : null;
  function ownSettings(store) {
    var s = store && store.reviewSettings;
    if (!s || typeof s !== 'object') return null;
    return typeof s.storeRating === 'boolean' && typeof s.productRating === 'boolean' ? s : OO.model.normalizeReviewSettings(s);
  }
  function readSettings(hotel, store) {
    var memo = settingsMemo ? settingsMemo.get(store) : null;
    if (memo) return memo;
    var p = Promise.resolve().then(function () { return OO.api.getRestaurant(hotel); }).then(function (raw) {
      return OO.model.restaurantReviewSettings(raw);
    }, function () {
      if (settingsMemo) settingsMemo.delete(store); // unreadable now: nothing shows, and the next mount asks again
      return null;
    });
    if (settingsMemo) settingsMemo.set(store, p);
    return p;
  }
  ui.useReviewSettings = function useReviewSettings(hotel, store) {
    var h = hotelOf(hotel, store);
    var own = ownSettings(store);
    var key = !own && h && store && typeof store === 'object' ? 'settings|' + h + '|' + storeTag(store) : null;
    var read = ui.useRatingRead(key, function () { return readSettings(h, store); });
    return own || read;
  };

  ui.useStoreRating = function useStoreRating(hotel, store) {
    var h = hotelOf(hotel, store);
    var settings = ui.useReviewSettings(h, store);
    var key = settings && settings.storeRating && h ? 'store|' + h + '|' + storeTag(store) : null;
    var raw = ui.useRatingRead(key, function () { return OO.api.getStoreRating(h); });
    return key ? OO.model.normalizeRatingSummary(raw) : null;
  };

  ui.useProductRating = function useProductRating(hotel, store, productId) {
    var h = hotelOf(hotel, store);
    var settings = ui.useReviewSettings(h, store);
    var id = productId === undefined || productId === null ? '' : String(productId);
    var key = settings && settings.productRating && h && id ? 'product|' + h + '|' + id : null;
    var raw = ui.useRatingRead(key, function () { return OO.api.getProductRating(h, productId); });
    return key ? OO.model.normalizeRatingSummary(raw) : null;
  };

  ui.RatingBadge = function RatingBadge(props) {
    var avg = Number(props.average);
    var total = Math.floor(Number(props.total) || 0);
    if (!(avg > 0) || !(total > 0)) return null;
    var score = OO.model.ratingScore(avg);
    var count = OO.model.ratingCount(total);
    var words = total === 1 ? 'review' : 'reviews';
    var label = 'Rated ' + score + ' out of 5 from ' + total + ' ' + words;
    if (props.variant === 'product') {
      return (
        <span className={cx('oo-rating-pill', props.className)} role="img" aria-label={label}>
          <OO.icons.Star size={13} strokeWidth={1.8} />
          <span className="oo-rating-pill__score">{score}</span>
          <span className="oo-rating-pill__count">{count}</span>
        </span>
      );
    }
    return (
      <span className={cx('oo-rating-badge', props.tone === 'dark' && 'oo-rating-badge--dark', props.className)} role="img" aria-label={label}>
        <span className="oo-rating-badge__score">{score}</span>
        <OO.icons.Star size={13} strokeWidth={1.8} />
        <span className="oo-rating-badge__count">{'· ' + count}<span className="oo-only-wide">{' ' + words}</span></span>
      </span>
    );
  };

  ui.StoreRating = function StoreRating(props) {
    var r = ui.useStoreRating(props.hotel, props.store);
    return r ? <ui.RatingBadge average={r.average} total={r.total} tone={props.tone} className={props.className} /> : null;
  };

  // ---- Install prompt (P5-03; the header above) --------------------------------------------------------------------
  var INSTALL_KEY = 'oo-install-v1';
  var INSTALL_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
  // The pages it never shows on: the commerce flow and the payment pages, and the dev / not-found pages.
  var INSTALL_OFF = { cart: 1, checkout: 1, 'payment-callback': 1, 'order-status': 1, 'not-found': 1, 'ui-gallery': 1 };
  var IOS_HINT = 'Tap Share, then Add to Home Screen.';
  var PROMPT_TEXT = 'Add it to your home screen to order again in a tap.';

  function installRecent(at, now) {
    return typeof at === 'number' && isFinite(at) && typeof now === 'number' && now - at < INSTALL_DISMISS_MS;
  }
  function installGate(s) {
    if (!s || s.mode === 'qr' || s.mode === 'kiosk' || s.preview || s.inShell || s.standalone || s.redirecting) return null;
    if (!s.hotel || !s.live || Object.prototype.hasOwnProperty.call(INSTALL_OFF, s.page)) return null;
    if (!s.latched && installRecent(s.dismissedAt, s.now)) return null;
    if (s.prompt) return 'prompt';
    return s.ios ? 'ios' : null;
  }
  function isIosSafari(nav) {
    if (!nav) return false;
    var ua = String(nav.userAgent || '');
    var ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && Number(nav.maxTouchPoints) > 1);
    return ios && /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|FBAN|FBAV|Instagram|Line\//.test(ua);
  }
  function installDismissedAt(storage) {
    try {
      var raw = storage ? storage.getItem(INSTALL_KEY) : null;
      var v = raw ? JSON.parse(raw) : null;
      return v && typeof v.at === 'number' && isFinite(v.at) ? v.at : null;
    } catch (e) { return null; }
  }
  function installDismiss(storage, now) {
    try {
      if (!storage) return false;
      storage.setItem(INSTALL_KEY, JSON.stringify({ at: now }));
      return true;
    } catch (e) { return false; }
  }

  // This page load: the browser's held beforeinstallprompt event, the iOS hint's latch, a close, and who re-renders.
  var install = { event: null, latched: false, closed: false, subs: [] };
  function installChanged() {
    install.subs.slice().forEach(function (f) { try { f(); } catch (e) { /* a gone subscriber */ } });
  }
  // Only where the browser has the event (feature detection: Chromium; Safari and Firefox have neither).
  if (typeof window !== 'undefined' && window && typeof window.addEventListener === 'function') {
    if ('onbeforeinstallprompt' in window) {
      window.addEventListener('beforeinstallprompt', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault(); // no mini-infobar: the banner offers it
        install.event = e || null;
        installChanged();
      });
    }
    if ('onappinstalled' in window) {
      window.addEventListener('appinstalled', function () {
        install.event = null;
        install.closed = true;
        installChanged();
      });
    }
  }
  function installStorage() { try { return window.localStorage || null; } catch (e) { return null; } }
  function inShellNow() {
    var P = OO.platform;
    try { if (P && typeof P.inShell === 'function' && P.inShell()) return true; } catch (e) { return true; }
    return !!(window.flutter_inappwebview || window.ReactNativeWebView || window.isFlutterWebView);
  }
  function standaloneNow() {
    try {
      if (window.navigator && window.navigator.standalone === true) return true;
      return !!(typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
    } catch (e) { return false; }
  }
  // The store the banner names: mode app's chain (its master's branding), else the route's store, as already loaded.
  function installStore(route, mode) {
    var D = OO.data;
    if (!D || typeof D.peekHotel !== 'function') return null;
    var keys = mode === 'app' ? [route.chain, route.hotel] : [route.hotel];
    for (var i = 0; i < keys.length; i++) {
      var hit = keys[i] ? D.peekHotel(keys[i], false) : null;
      if (hit && hit.store) return hit.store;
    }
    return null;
  }
  function installClose() {
    install.closed = true;
    installDismiss(installStorage(), Date.now());
    installChanged();
  }

  // useInstallPrompt(route, mode) → 'prompt' | 'ios' | null: a hook OO.App calls on every render (before its early
  // returns). It re-renders the caller when the browser's event lands or the banner closes, and latches the iOS hint.
  ui.useInstallPrompt = function useInstallPrompt(route, mode) {
    var bump = React.useState(0)[1];
    React.useEffect(function () {
      var sub = function () { bump(function (n) { return n + 1; }); };
      install.subs.push(sub);
      return function () { install.subs = install.subs.filter(function (f) { return f !== sub; }); };
    }, []);
    var r = route || {};
    var api = OO.api;
    var live = !!r.hotel && !!api && typeof api.availabilityOf === 'function' && api.availabilityOf(r.hotel) === 'live';
    var kind = install.closed ? null : installGate({
      mode: mode, page: r.page, hotel: r.hotel, redirecting: !!r.redirectTo, live: live, preview: !!OO.whitelabel && OO.whitelabel.isPreview(),
      inShell: inShellNow(), standalone: standaloneNow(), dismissedAt: installDismissedAt(installStorage()),
      now: Date.now(), prompt: !!install.event, ios: isIosSafari(window.navigator), latched: install.latched,
    });
    // The iOS hint is one-time: it counts as dismissed (14 days) the first time it shows, and stays up this page load.
    React.useEffect(function () {
      if (kind !== 'ios' || install.latched) return;
      install.latched = true;
      installDismiss(installStorage(), Date.now());
    }, [kind]);
    return kind;
  };

  // <OO.ui.InstallPrompt kind route mode /> the banner for useInstallPrompt's kind ('prompt' | 'ios'; else nothing).
  ui.InstallPrompt = function InstallPrompt(props) {
    var kind = props.kind;
    if (kind !== 'prompt' && kind !== 'ios') return null;
    var store = installStore(props.route || {}, props.mode);
    var name = store && store.name ? String(store.name) : '';
    function onInstall() {
      var e = install.event;
      install.event = null; // a prompt event can be used once
      installChanged();
      if (!e || typeof e.prompt !== 'function') return;
      Promise.resolve().then(function () { return e.prompt(); }).then(function () { return e.userChoice; })
        .then(function (choice) {
          if (!choice || choice.outcome !== 'accepted') installClose();
        }, function () { /* the browser refused to show it: the banner is gone until the next event */ });
    }
    return (
      <div className="oo-install" role="region" aria-label="Install the app">
        {store && store.logoUrl ? <img className="oo-install__logo" src={store.logoUrl} alt="" /> : null}
        <div className="oo-install__text">
          <p className="oo-install__title">{name ? 'Install ' + name : 'Install the app'}</p>
          <p className="oo-install__body">{kind === 'ios' ? IOS_HINT : PROMPT_TEXT}</p>
        </div>
        {kind === 'prompt' ? (
          <button type="button" className="oo-install__btn" onClick={onInstall}>Install</button>
        ) : null}
        <ui.IconButton icon="Close" variant="bare" label="Not now" onClick={installClose} className="oo-install__close" />
      </div>
    );
  };
  ui.InstallPrompt.logic = {
    gate: installGate,
    isIosSafari: isIosSafari,
    dismissedAt: installDismissedAt,
    recent: installRecent,
    dismiss: installDismiss,
    KEY: INSTALL_KEY,
    DISMISS_MS: INSTALL_DISMISS_MS,
    OFF: Object.keys(INSTALL_OFF),
  };
})();


// ===================== src/ui/landing-icons.jsx =====================
// WL-5: bundled feature, social and payment symbols. No icon fonts, requests or user-authored SVG.
// WL-CHK: + the `store` feature icon (a storefront), and <Glyph name size /> for the bold design's UI marks (arrows,
// external link, lock, sign-in, plus, book, chevrons; filled: star, apple, play). Glyphs are always decorative
// (aria-hidden): the link or button around them carries the name.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var PATHS = {
    truck: 'M1 5h13v12H1z M14 9h5l4 5v3h-9 M4 17a2 2 0 1 0 4 0 M16 17a2 2 0 1 0 4 0',
    leaf: 'M20 3C9 2 2 8 5 15c3 7 15 4 15-12Z M4 21 16 8',
    star: 'm12 2 3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1Z',
    heart: 'M20 5c-3-3-7-1-8 2C8-1-3 5 3 12l9 9 9-9c2-3 2-5-1-7Z',
    clock: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20 M12 6v6l4 3',
    shield: 'm12 2 9 4v6c0 5-9 10-9 10S3 17 3 12V6Z m-5 10 3 3 6-6',
    card: 'M3 4h18v16H3z M3 9h18 M6 15h4',
    gift: 'M2 8h20v4H2z M4 12v10h16V12 M12 8v14 M12 8C0 8 4-3 12 8c8-11 12 0 0 0Z',
    phone: 'M5 2H2v5c0 8 7 15 15 15h5v-3l-6-4-3 3-7-7 3-3Z',
    'map-pin': 'M12 22S4 14 4 9a8 8 0 0 1 16 0c0 5-8 13-8 13Z M12 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
    chef: 'M6 14C-2 11 4 1 9 5c1-5 9-5 10 1 7 0 6 9-1 9v7H6Z M6 18h12',
    coffee: 'M3 7h13v9a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5Z M16 8h2a4 4 0 0 1 0 8h-2 M6 2v2 M10 1v3 M14 2v2',
    tag: 'M2 2h10l10 10-10 10L2 12Z M7 7h.01',
    'thumbs-up': 'M7 10h3l3-8c3 0 3 3 2 7h6v5l-3 8H7Z M2 10h5v12H2Z',
    users: 'M8 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8 M1 22v-4c0-6 14-6 14 0v4 M16 3c7 0 7 8 0 8 M18 14c5 0 5 4 5 8',
    home: 'm2 11 10-9 10 9 M4 9v13h16V9 M9 22v-9h6v9',
    smile: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20 M7 9h.01 M17 9h.01 M7 14c2 5 8 5 10 0',
    award: 'M12 2a7 7 0 1 0 0 14 7 7 0 0 0 0-14 M7 14 5 23l7-4 7 4-2-9',
    box: 'm12 2 10 5v10l-10 5-10-5V7Z M2 7l10 5 10-5 M12 12v10 M7 4l10 5',
    cart: 'M1 2h4l3 13h11l3-10H6 M9 20h.01 M19 20h.01',
    percent: 'm4 20 16-16 M6 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6 M18 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6',
    bike: 'M5 12a5 5 0 1 0 0 10 5 5 0 0 0 0-10 M19 12a5 5 0 1 0 0 10 5 5 0 0 0 0-10 M5 17l5-9 4 9H5 M10 8h7l2 9 M8 5h4 M17 8V3h3',
    calendar: 'M3 4h18v18H3z M3 9h18 M7 1v6 M17 1v6 M7 13h2 M15 13h2 M7 17h2 M15 17h2',
    sparkle: 'm12 2 3 7 7 3-7 3-3 7-3-7-7-3 7-3Z',
    store: 'M4 10v11h16V10 M2.5 10 4.5 3h15l2 7 M2.5 10a3.2 3.2 0 0 0 6.3 0 3.2 3.2 0 0 0 6.4 0 3.2 3.2 0 0 0 6.3 0 M9.5 21v-6h5v6',
    check: 'M20 6 9 17l-5-5', // WL-VEL: the features checklist
    facebook: 'M14 22V12h4l1-4h-5V6c0-2 1-2 5-2V1h-4c-4 0-5 2-5 5v2H6v4h4v10',
    instagram: 'M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5 M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10 M18 6h.01',
    x: 'M3 3h5l13 18h-5Z M21 3 3 21',
    youtube: 'M3 5h18v14H3Z m7 4 6 3-6 3Z',
    linkedin: 'M3 8v14 M3 2v1 M9 22V8 M9 14c0-8 12-8 12 0v8',
    tiktok: 'M14 2v14a5 5 0 1 1-4-5 M14 2c1 5 3 7 7 7',
  };
  var PAYMENTS = { visa: 'VISA', mastercard: 'Mastercard', amex: 'AMEX', applepay: 'Apple Pay', googlepay: 'Google Pay', paypal: 'PayPal', afterpay: 'Afterpay', eftpos: 'EFTPOS', unionpay: 'UnionPay', alipay: 'Alipay', wechatpay: 'WeChat Pay', laybuy: 'Laybuy', zip: 'Zip', poli: 'POLi' };
  function Icon(props) {
    var path = PATHS[props.name];
    if (!path) return null;
    return <svg width={props.size || 28} height={props.size || 28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={props.strokeWidth || '1.6'} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d={path} /></svg>;
  }
  var GLYPHS = {
    'arrow-right': { d: 'M4 12h15 M13 6l6 6-6 6' },
    external: { d: 'M14 4h6v6 M20 4l-9 9 M18 14v6H4V6h6' },
    lock: { d: 'M6 11h12v10H6Z M8.5 11V8a3.5 3.5 0 0 1 7 0v3' },
    'sign-in': { d: 'M14 4h5v16h-5 M4 12h11 M11 8l4 4-4 4' },
    plus: { d: 'M12 5v14 M5 12h14' },
    book: { d: 'M12 6.5C10 5 7 4.5 3.5 5v13.5c3.5-.5 6.5 0 8.5 1.5 2-1.5 5-2 8.5-1.5V5c-3.5-.5-6.5 0-8.5 1.5Z M12 6.5V20' },
    'chevron-right': { d: 'm9 5 7 7-7 7' },
    'chevron-left': { d: 'm15 5-7 7 7 7' },
    star: { fill: true, d: 'm12 2.6 2.8 5.9 6.4.8-4.7 4.5 1.2 6.4L12 17.1l-5.7 3.1 1.2-6.4-4.7-4.5 6.4-.8Z' },
    apple: { fill: true, d: 'M16.3 12.7c0-2.4 2-3.6 2.1-3.7-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3 0 1.8-.8 3.3-.8s2 .8 3.3.8c1.4 0 2.3-1.2 3.1-2.5.6-.8 1.1-1.8 1.4-2.8-1.7-.6-2.8-2.3-2.8-4.2Z M14 5.3c.7-.8 1.1-2 1-3.1-1 .1-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4Z' },
    play: { fill: true, d: 'M4.5 3.2v17.6c0 .5.5.8 1 .5l15-8.8c.4-.3.4-.8 0-1.1l-15-8.7c-.5-.3-1 0-1 .5Z' },
  };
  function Glyph(props) {
    var g = GLYPHS[props.name];
    if (!g) return null;
    var size = props.size || 16;
    return g.fill
      ? <svg className={props.className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d={g.d} /></svg>
      : <svg className={props.className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={props.strokeWidth || 2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d={g.d} /></svg>;
  }
  function Payment(props) {
    var label = PAYMENTS[props.name];
    if (!label) return null;
    return <svg className="oo-lp-payment" width="78" height="36" viewBox="0 0 78 36" role="img" aria-label={label} focusable="false"><rect x="1" y="1" width="76" height="34" rx="5" fill="none" stroke="currentColor" /><text x="39" y="22" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize={label.length > 9 ? 9 : 11} fill="currentColor">{label}</text></svg>;
  }
  OO.landingIcons = { Icon: Icon, Payment: Payment, Glyph: Glyph, PATHS: PATHS, PAYMENTS: PAYMENTS, GLYPHS: GLYPHS };
})();


// ===================== src/ui/site-chrome.jsx =====================
// src/ui/site-chrome.jsx: WL-VEL, OO.ui.SiteChrome, the white-label website's chrome as standalone components: the live
// status bar, the site header and the site footer (styles/landing-boutique.css, .oo-sc-*). The boutique landing page
// (src/pages/landing-boutique.jsx) draws them, and ordering pages may mount the same ones (landing.orderingChrome
// 'site'). They read NOTHING from the landing page's state: every input is a prop, and every link is a resolved
// descriptor.
//
// Link := { label, href, onClick? (a plain click: preventDefault is the caller's), current? (aria-current="page"),
//           external? (a new tab, rel noopener) }
//
// <StatusBar bar store loading now? />
//   bar      OO.landing.statusBarOf(landing) (null: nothing drawn)
//   store    the Store the hours come from (OO.useHotel(master).store) | null
//   loading  true while that store is still being read ("Checking opening hours…"); false + no store: nothing drawn
//   now      () → epoch ms (tests; default Date.now)
//   The copy comes from OO.landing.statusOf (store time zone, DST-correct). While a countdown shows it ticks every second
//   in the bar's own state (the page never re-renders for it), and recomputes at zero without a reload; otherwise it is
//   re-read every minute. Not a live region (a per-second announcement would flood screen readers); reduced motion only
//   stops the dot's pulse, the text keeps updating.
// <SiteHeader brand nav cta basket menuLabel? />
//   brand   { name, logo (https | null), title, tagline (the wordmark: OO.landing.wordmarkOf; both may be ''), link }
//           link: the home Link (aria-label "{name} home")
//   nav     [Link] (the current page's link carries current: true)
//   cta     Link | null: the cream pill after the nav
//   basket  node | null: a slot after the CTA (the ordering pages' Basket button with its count)
//   Below 900 px the nav, CTA and basket fold into a panel under the bar behind a menu button (aria-expanded,
//   aria-controls; Escape and any link close it).
// <SiteFooter brand columns contact hours socials copyright legal cta powered />
//   brand    { name, logo, tagline }
//   columns  [{ title, kind ('links' | 'text' | 'contact' | 'social' | 'hours'), links: [Link], body }]
//            contact: the address (a Maps search link) and "Get directions"; social: the phone, the email and the
//            round social links; hours: the week (today highlighted). With no 'social' column the phone and email join
//            the 'contact' column, and the social links sit under the brand.
//   contact  { address (multi-line text) | null, mapsHref, directionsHref, phone, telHref, email }
//   hours    OO.landing.weekRows / directoryRows rows (Monday first) | []
//   socials  [{ network, label, href }]
//   copyright text · legal [Link] · cta Link | null (the pill in the bottom row) · powered bool (OO.ui.PoweredBySipo)
// <SiteTop status header sticky? /> the sticky wrapper that pins the status bar and the header together (no measuring);
//   sticky false (landing.header.sticky false): they scroll away with the page.
//
// Builders (shared by the landing page and ordering pages):
//   linkOf(target, label, ctx, opts?) → Link | null   a typed target through OO.landing.targetOf. ctx (the link context):
//     { key, master, landing, page (slug | null), sections (shown here), order(query), openSection?(id), orderingOff?,
//     off?, phone?, directions?, menuUnavailable? }; opts.onNavigate runs first on a plain click.
//   ctxFor({ key, master, landing, order }) → a link context for a page outside the landing (home sections, page null;
//     outside: true, so no nav link is the current page there, not even Home).
//   build(landing, ctx, { brand (doc.brand), logo, footLogo, store, tagline, hoursRows }) → { bar, header, footer }: the
//     three components' props (header without basket; footer with hours = hoursRows).
//   useHours(store, masterKey, tz?) → rows: the store's week (OO.landing.weekRows, in the store's time zone), else the
//     store directory's hours with "today" in tz (VEL-FIX: the document's locale.timezone; the directory has none).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.ui = OO.ui || {};

  var NETWORKS = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', x: 'X', youtube: 'YouTube', linkedin: 'LinkedIn' };
  var TICK_MS = 1000;
  var REREAD_MS = 60000;

  function L() { return OO.landing; }
  function cx() { return OO.ui.cx.apply(null, arguments); }
  function plainClick(e) { return !!e && !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }
  function reducedNow() { try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }
  function arr(v) { return Array.isArray(v) ? v : []; }

  // ---- links ------------------------------------------------------------------------------------------------------------
  function SiteLink(props) {
    var d = props.link;
    if (!d) return null;
    var after = props.after || null;
    if (d.external) return <a className={props.className} href={d.href} target="_blank" rel="noopener noreferrer">{props.children || d.label}{after}</a>;
    return (
      <a className={props.className} href={d.href} data-oo-website-order={d.websiteOrder} aria-current={d.current ? 'page' : undefined}
        onClick={function (e) { if (props.onAny) props.onAny(); if (!d.onClick || !plainClick(e)) return; e.preventDefault(); d.onClick(e); }}>
        {props.children || d.label}{after}
      </a>
    );
  }
  function scrollToId(id) {
    var el = typeof document !== 'undefined' && document.getElementById ? document.getElementById(id) : null;
    if (!el) return false;
    try { el.scrollIntoView({ behavior: reducedNow() ? 'auto' : 'smooth', block: 'start' }); } catch (e) { el.scrollIntoView(); }
    if (el.focus) { try { el.focus({ preventScroll: true }); } catch (x) { /* not focusable */ } }
    return true;
  }
  // A typed target → a Link (see the header).
  function linkOf(target, label, ctx, opts) {
    var t = L().targetOf(target, ctx);
    if (!t) return null;
    var nav = opts && opts.onNavigate ? opts.onNavigate : null;
    var text = t.label || label;
    function first() { if (nav) nav(); }
    if (t.kind === 'url') return { label: text, href: t.href, external: true };
    if (t.kind === 'tel') return { label: text, href: t.href };
    if (t.kind === 'section') {
      return { label: text, href: t.href, onClick: function () {
        first();
        if (t.dialog && ctx && typeof ctx.openSection === 'function') { ctx.openSection(t.id); return; }
        if (scrollToId(L().anchorId(t.id))) { try { window.history.replaceState(window.history.state, '', t.href); } catch (e) { /* no history */ } }
      } };
    }
    if (t.kind === 'order' || t.kind === 'categories') {
      return { label: text, href: L().menuHref(ctx && (ctx.master || ctx.key), t.query || undefined), websiteOrder: ctx && ctx.publicOnly ? JSON.stringify(t.query || null) : undefined, onClick: function () { first(); if (ctx && ctx.order) ctx.order(t.query || null); } };
    }
    if (t.kind === 'site') {
      return { label: text, href: t.href, current: !!t.current && !t.hash, onClick: function () {
        first();
        // Already on that page: scroll to the anchor (and let a menu section open it), else go there.
        if (t.current && t.hash && scrollToId(t.hash)) {
          try { window.history.replaceState(window.history.state, '', '#' + t.hash); } catch (e) { /* no history */ }
          try { window.dispatchEvent(new window.CustomEvent('oo:site-anchor', { detail: { id: t.hash } })); } catch (x) { /* old engine */ }
          return;
        }
        OO.router.go(t.href);
      } };
    }
    return { label: text, href: t.href, onClick: function () { first(); OO.router.go(t.href); } };
  }
  function ctxFor(o) {
    o = o || {};
    return {
      key: o.key, master: o.master || o.key, landing: o.landing || null, page: null,
      sections: L().sectionsOf(o.landing || null, { orderingOff: !!o.orderingOff }), order: o.order || function () {},
      orderingOff: !!o.orderingOff, off: L().orderingOffOf(o.landing || null),
      outside: true, // WL-VEL (VEL-C): an ordering page, so the Home link is never aria-current there
    };
  }

  // ---- status bar -------------------------------------------------------------------------------------------------------
  function StatusBar(props) {
    var bar = props.bar, store = props.store;
    var clock = typeof props.now === 'function' ? props.now : function () { return Date.now(); };
    var tick = React.useState(0);
    var st = bar && store ? L().statusOf(store, clock(), bar) : null;
    var counting = !!st && st.state === 'countdown';
    React.useEffect(function () {
      if (!bar || !store) return undefined;
      var t = setInterval(function () { tick[1](function (n) { return n + 1; }); }, counting ? TICK_MS : REREAD_MS);
      return function () { clearInterval(t); };
    }, [!!bar, store, counting]);
    if (!bar) return null;
    if (!store) {
      if (!props.loading) return null;
      return <div className="oo-sc-status oo-sc-status--loading"><div className="oo-sc-status__in"><span className="oo-sc-status__text">Checking opening hours…</span></div></div>;
    }
    if (!st) return null;
    return (
      <div className={cx('oo-sc-status', 'oo-sc-status--' + st.state)}>
        <div className="oo-sc-status__in">
          <span className="oo-sc-status__dot" aria-hidden="true" />
          <span className="oo-sc-status__text">
            {st.parts.map(function (p, i) { return p.countdown ? <span key={i} className="oo-sc-status__count">{p.text}</span> : <React.Fragment key={i}>{p.text}</React.Fragment>; })}
          </span>
        </div>
      </div>
    );
  }

  // ---- header -----------------------------------------------------------------------------------------------------------
  function SiteHeader(props) {
    var brand = props.brand || {};
    var openS = React.useState(false);
    var open = openS[0];
    var close = function () { openS[1](false); };
    var nav = arr(props.nav).filter(Boolean);
    React.useEffect(function () {
      if (!open) return undefined;
      function onKey(e) { if (e.key === 'Escape' || e.key === 'Esc') { openS[1](false); var b = document.getElementById('oo-sc-burger'); if (b && b.focus) b.focus(); } }
      document.addEventListener('keydown', onKey);
      return function () { document.removeEventListener('keydown', onKey); };
    }, [open]);
    var name = brand.name || '';
    var mark = (
      <>
        {brand.logo ? <OO.ui.Image className="oo-sc-brand__logo" sizes="46px" src={brand.logo} alt="" /> : null}
        {brand.title || brand.tagline ? (
          <span className="oo-sc-brand__words">
            {brand.title ? <span className="oo-sc-brand__name">{brand.title}</span> : null}
            {brand.tagline ? <span className="oo-sc-brand__tag">{brand.tagline}</span> : null}
          </span>
        ) : brand.logo ? null : <span className="oo-sc-brand__name">{name}</span>}
      </>
    );
    var homeLink = brand.link || { href: '#', label: name };
    return (
      <header className={cx('oo-sc-head', open && 'is-open')}>
        <div className="oo-sc-wrap oo-sc-head__row">
          <a className="oo-sc-brand" href={homeLink.href} aria-label={(name || 'Home') + ' home'}
            onClick={function (e) { close(); if (!homeLink.onClick || !plainClick(e)) return; e.preventDefault(); homeLink.onClick(e); }}>{mark}</a>
          <button type="button" id="oo-sc-burger" className="oo-sc-burger" aria-expanded={open ? 'true' : 'false'} aria-controls="oo-sc-nav"
            aria-label={props.menuLabel || 'Menu'} onClick={function () { openS[1](!open); }}>
            <span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
          </button>
          <nav id="oo-sc-nav" className="oo-sc-nav" aria-label="Main">
            <ul className="oo-sc-nav__links">
              {nav.map(function (d, i) { return <li key={i}><SiteLink className="oo-sc-nav__link" link={d} onAny={close} /></li>; })}
            </ul>
            {props.cta || props.basket ? (
              <div className="oo-sc-nav__acts">
                {props.cta ? <SiteLink className="oo-sc-pill oo-sc-pill--cream oo-sc-head__cta" link={props.cta} onAny={close} /> : null}
                {props.basket || null}
              </div>
            ) : null}
          </nav>
        </div>
      </header>
    );
  }
  function SiteTop(props) {
    return <div className={cx('oo-sc-top', props.sticky === false && 'oo-sc-top--static')}>{props.status || null}{props.header || null}</div>;
  }

  // ---- footer -----------------------------------------------------------------------------------------------------------
  function Lines(props) {
    var lines = String(props.text || '').split(/\n+/).map(function (x) { return x.trim(); }).filter(Boolean);
    return lines.map(function (x, i) { return <React.Fragment key={i}>{i ? <br /> : null}{x}</React.Fragment>; });
  }
  function Socials(props) {
    var list = arr(props.items);
    if (!list.length) return null;
    return (
      <ul className="oo-sc-socials" aria-label="Follow us">
        {list.map(function (x, i) { return <li key={i}><a className="oo-sc-social" href={x.href} target="_blank" rel="noopener noreferrer" aria-label={x.label}><OO.landingIcons.Icon name={x.network} size={17} strokeWidth="1.9" /></a></li>; })}
      </ul>
    );
  }
  function HoursList(props) {
    var rows = arr(props.rows);
    if (!rows.length) return null;
    return (
      <ul className="oo-sc-hours">
        {rows.map(function (r) {
          return <li key={r.dow} className={cx(r.today && 'is-today', r.closed && 'is-closed')}><span className="oo-sc-hours__day">{r.day}</span><span className="oo-sc-hours__time">{r.time}</span></li>;
        })}
      </ul>
    );
  }
  function ContactLines(props) {
    var c = props.contact || {};
    return (
      <>
        {c.phone ? <p className="oo-sc-foot__line">{c.telHref ? <a href={c.telHref}>{c.phone}</a> : c.phone}</p> : null}
        {c.email ? <p className="oo-sc-foot__line"><a href={'mailto:' + c.email}>{c.email}</a></p> : null}
      </>
    );
  }
  function SiteFooter(props) {
    var brand = props.brand || {}, c = props.contact || {};
    var cols = arr(props.columns);
    var hasSocialCol = cols.some(function (k) { return k.kind === 'social'; });
    var Glyph = OO.landingIcons.Glyph;
    function body(col) {
      if (col.kind === 'text') return col.body ? <p className="oo-sc-foot__body">{col.body}</p> : null;
      if (col.kind === 'hours') return <HoursList rows={props.hours} />;
      if (col.kind === 'social') return <><ContactLines contact={c} /><Socials items={props.socials} /></>;
      if (col.kind === 'contact') {
        return (
          <>
            {c.address ? <p className="oo-sc-foot__line">{c.mapsHref ? <a href={c.mapsHref} target="_blank" rel="noopener noreferrer"><Lines text={c.address} /></a> : <Lines text={c.address} />}</p> : null}
            {c.directionsHref ? <p className="oo-sc-foot__line"><a className="oo-sc-foot__go" href={c.directionsHref} target="_blank" rel="noopener noreferrer">Get directions<span aria-hidden="true"> →</span></a></p> : null}
            {hasSocialCol ? null : <ContactLines contact={c} />}
          </>
        );
      }
      return <ul className="oo-sc-foot__links">{arr(col.links).map(function (d, i) { return <li key={i}><SiteLink link={d} /></li>; })}</ul>;
    }
    return (
      <footer className="oo-sc-foot">
        <div className="oo-sc-wrap">
          <div className="oo-sc-foot__top">
            <div className="oo-sc-foot__brand">
              {brand.logo ? <OO.ui.Image className="oo-sc-foot__logo" sizes="56px" src={brand.logo} alt={brand.name || 'Logo'} /> : <p className="oo-sc-foot__name">{brand.name}</p>}
              {brand.tagline ? <p className="oo-sc-foot__tag">{brand.tagline}</p> : null}
              {hasSocialCol ? null : <Socials items={props.socials} />}
            </div>
            {cols.map(function (col, i) {
              return <div key={i} className={cx('oo-sc-foot__col', 'oo-sc-foot__col--' + (col.kind || 'links'))}><h2 className="oo-sc-foot__title">{col.title}</h2>{body(col)}</div>;
            })}
          </div>
          <div className="oo-sc-foot__base">
            {props.copyright ? <p className="oo-sc-foot__copy">{props.copyright}</p> : null}
            {arr(props.legal).length ? <ul className="oo-sc-foot__legal">{props.legal.map(function (d, i) { return <li key={i}><SiteLink link={d} /></li>; })}</ul> : null}
            {props.powered ? <OO.ui.PoweredBySipo className="oo-sc-foot__powered" /> : null}
            {props.cta ? <SiteLink className="oo-sc-pill oo-sc-pill--cream oo-sc-foot__cta" link={props.cta} after={<Glyph name="arrow-right" size={14} />} /> : null}
          </div>
        </div>
      </footer>
    );
  }

  // ---- builders ---------------------------------------------------------------------------------------------------------
  function mapsSearch(address) {
    var q = String(address || '').split(/\n+/).map(function (x) { return x.trim(); }).filter(Boolean).join(', ');
    return q ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q) : null;
  }
  function build(landing, ctx, o) {
    o = o || {};
    var LL = L();
    var brand = o.brand || {};
    var name = LL.text(brand.name, 60) || '';
    var words = LL.wordmarkOf(landing, brand);
    var store = o.store || null;
    var header = landing && landing.header ? landing.header : {};
    var homeHref = OO.router.href('landing', null, { hotel: ctx.key });
    var nav = arr(landing && landing.nav).filter(function (n) { return n && LL.text(n.label, 24); }).slice(0, 8).map(function (n) {
      return linkOf(n.target, LL.text(n.label, 24), ctx);
    }).filter(Boolean);
    var cta = landing && landing.cta && LL.text(landing.cta.label, 24) ? linkOf(landing.cta.target, LL.text(landing.cta.label, 24), ctx) : null;
    var f = landing && landing.footer ? landing.footer : {};
    var cols = arr(f.columns).filter(function (k) { return k && LL.text(k.title, 30); }).slice(0, 4).map(function (k) {
      return {
        title: LL.text(k.title, 30), kind: ['text', 'contact', 'social', 'hours'].indexOf(k.kind) >= 0 ? k.kind : 'links', body: LL.text(k.body, 600),
        links: arr(k.links).filter(function (x) { return x && LL.text(x.label, 30); }).slice(0, 8).map(function (x) { return linkOf(x.target, LL.text(x.label, 30), ctx); }).filter(Boolean),
      };
    });
    var lc = landing && landing.contact ? landing.contact : {};
    var address = LL.text(lc.address, 200) || (store && store.address && store.address.full) || null;
    var phone = LL.text(lc.phone, 24) || (store && store.phone) || null;
    var email = typeof lc.email === 'string' && /^[^\s@]+@[^\s@]+$/.test(lc.email.trim()) ? lc.email.trim() : null;
    var directions = LL.directionsUrl(store && store.lat != null ? { lat: store.lat, lng: store.lng, name: store.name, address: address } : { name: name, address: address ? address.replace(/\n+/g, ', ') : '' });
    var socials = arr(f.socials).filter(function (x) { return x && NETWORKS[x.network] && LL.httpsUrl(x.url); }).slice(0, 6).map(function (x) { return { network: x.network, label: NETWORKS[x.network], href: x.url }; });
    var fcta = LL.footerCtaOf(landing);
    return {
      bar: LL.statusBarOf(landing),
      header: {
        brand: { name: name, logo: o.logo || null, title: words ? words.title : '', tagline: words ? words.tagline : '', link: { label: name, href: homeHref, onClick: function () { if (ctx.page) OO.router.go(homeHref); else window.scrollTo(0, 0); } } },
        nav: nav, cta: cta, sticky: header.sticky !== false,
      },
      footer: {
        brand: { name: name, logo: o.footLogo || o.logo || null, tagline: o.tagline || '' },
        columns: cols,
        contact: { address: address, mapsHref: mapsSearch(address), directionsHref: directions, phone: phone, telHref: LL.telHref(phone), email: email },
        hours: o.hoursRows || [],
        socials: socials,
        copyright: LL.text(f.copyright, 120),
        legal: arr(f.legal).filter(function (x) { return x && LL.text(x.label, 30); }).slice(0, 6).map(function (x) { return linkOf(x.target, LL.text(x.label, 30), ctx); }).filter(Boolean),
        cta: fcta ? linkOf(fcta.target, fcta.label, ctx) : null,
        powered: true,
      },
    };
  }
  function useHours(store, masterKey, tz, includeClosed) {
    var need = !store && !!masterKey;
    var q = OO.useAsync(function () { return need ? L().loadStores(masterKey) : Promise.resolve(null); }, [need, masterKey]);
    if (store) return L().weekRows(store, null, includeClosed);
    var rows = Array.isArray(q.data) ? q.data : [];
    var b = rows.filter(function (x) { return x.isMaster; })[0] || rows[0];
    return b ? L().directoryRows(b.hours, null, tz || undefined) : [];
  }

  OO.ui.SiteChrome = {
    StatusBar: StatusBar, SiteHeader: SiteHeader, SiteFooter: SiteFooter, SiteTop: SiteTop, SiteLink: SiteLink,
    linkOf: linkOf, ctxFor: ctxFor, build: build, useHours: useHours, scrollToId: scrollToId, mapsSearch: mapsSearch,
  };
})();


// ===================== src/ui/ordering-site.jsx =====================
// src/ui/ordering-site.jsx: WL-VEL (lane VEL-C), OO.ui.OrderingSite: the white-label website's chrome around its ordering
// pages (scratchpad SPEC §1.3 landing.orderingChrome 'site'). Mode landing only (OO.landingMode: the /endpoint/landing
// mount and live custom domains); the online host, the App, QR and kiosk never mount it, and a website without
// orderingChrome 'site' (absent: 'app') renders its ordering pages exactly as before.
//
// OrderingSite.applies(route) → the Site whose chrome wraps this route, or null: mode landing, a store route on one of
//   PAGES (the menu and its category pages, the cart, checkout, order status / tracking, the account pages, the store's
//   About, coupons and deals, rewards, wallet, bookings, the legal pages and the payment return), a site that applies to
//   the route's store (OO.whitelabel.siteFor) and OO.landing.orderingChromeOf(landing) === 'site'.
// <OrderingSite route site>{page}</OrderingSite> (OO.App mounts it around the page when applies() says so):
//   .oo-sc-ordering (+ .oo-sc-ordering--browse on a browse-only website) > the SiteTop (the site header with the landing's nav, its
//   CTA and a Basket button; VEL-FIX: never the opening-hours strip, which the website's own pages carry: the ordering
//   pages have their own open / closed notices) · .oo-sc-ordering__page (the page, as before) · the SiteFooter. Every input comes from
//   OO.ui.SiteChrome's builders (build / ctxFor / linkOf / useHours), with a link context `outside` the landing (no nav
//   link is the current page; a home section's link goes to the home page + its anchor).
//   Basket: the bag + "Basket" (+ " (n)" with items) → the store's cart route (aria-label "Basket, n items"; aria-current
//   on the cart page); none on a browse-only website (OO.whitelabel.browseOnly), whose cart is off. The header is keyed
//   by the route, so its mobile menu panel closes when a link (the Basket too) changes the page.
//   Stacking: the SiteTop sticks at the top; the page's own sticky bars (the category tabs, the cart / checkout page
//   header, the desktop asides) stick under it: the wrapper carries --oo-site-top, the SiteTop's measured height
//   (ResizeObserver; 0 when the header doesn't stick), which styles/online-boutique.css adds to their `top` and to the
//   sections' scroll margins. The SiteTop paints below sheets, dialogs, toasts and the floating cart bar.
//   The store (the footer's hours) is the route's store from the data cache (OO.data.loadHotel, store only: no activation,
//   the page's own OO.useHotel does that).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.ui = OO.ui || {};

  var PAGES = {
    menu: 1, cart: 1, checkout: 1, 'order-status': 1, orders: 1, profile: 1, account: 1, track: 1, review: 1, wallet: 1,
    coupons: 1, deals: 1, rewards: 1, about: 1, legal: 1, bookings: 1, booking: 1, 'booking-track': 1, 'payment-callback': 1,
  };
  function L() { return OO.landing; }
  function cx() { return OO.ui.cx.apply(null, arguments); }
  function plainClick(e) { return !!e && !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }

  function applies(route) {
    if (!OO.landingMode || !route || !route.hotel || !Object.prototype.hasOwnProperty.call(PAGES, route.page)) return null;
    var W = OO.whitelabel;
    var site = W && typeof W.siteFor === 'function' ? W.siteFor(route.hotel) : null;
    var landing = site && site.doc ? site.doc.landing : null;
    if (!landing || !L() || typeof L().orderingChromeOf !== 'function' || L().orderingChromeOf(landing) !== 'site') return null;
    return site;
  }

  function hasHomeNavigation(route) {
    var site = applies(route), Chrome = OO.ui.SiteChrome;
    if (!site || !Chrome) return false;
    var key = site.masterKey || route.hotel, landing = site.doc.landing;
    var ctx = Chrome.ctxFor({ key: key, master: route.hotel, landing: landing });
    var nav = Chrome.build(landing, ctx, { brand: site.doc.brand }).header.nav;
    var home = OO.router.href('landing', null, { hotel: key });
    return nav.some(function (link) { return !link.external && link.href === home; });
  }

  function Basket(props) {
    var n = props.count;
    var words = 'Basket, ' + n + (n === 1 ? ' item' : ' items');
    return (
      <a className={cx('oo-sc-basket', props.current && 'is-current')} href={props.link.href} aria-label={words}
        aria-current={props.current ? 'page' : undefined}
        onClick={function (e) { if (!plainClick(e)) return; props.link.onClick(e); }}>
        <OO.icons.Bag size={18} />
        <span className="oo-sc-basket__text" aria-hidden="true">{'Basket' + (n > 0 ? ' (' + n + ')' : '')}</span>
      </a>
    );
  }

  var useLayout = typeof document !== 'undefined' && React.useLayoutEffect ? React.useLayoutEffect : React.useEffect;

  function OrderingSite(props) {
    var route = props.route, hotel = route.hotel;
    var site = props.site || applies(route);
    var landing = site.doc.landing;
    var W = OO.whitelabel, Chrome = OO.ui.SiteChrome;
    var siteKey = site.masterKey || hotel;
    var browse = typeof W.browseOnly === 'function' && W.browseOnly(hotel);
    var orderingOff = typeof W.landingOrdering === 'function' ? !W.landingOrdering(hotel).on : false;
    var cached = OO.data && typeof OO.data.peekHotel === 'function' ? OO.data.peekHotel(hotel, false) : null;
    var q = OO.useAsync(function () {
      return OO.data.loadHotel(hotel, { withMenu: false }).then(function (r) { return r ? r.store : null; });
    }, [hotel]);
    var store = q.data || (cached && cached.store) || null;
    var count = OO.useStore(function (s) { return s.hotel === String(hotel) && OO.cart ? OO.cart.count(s.cart.lines) : 0; });
    var hours = Chrome.useHours(store, siteKey, site.doc.locale && site.doc.locale.timezone);
    var ctx = Object.assign(Chrome.ctxFor({
      key: siteKey, master: hotel, landing: landing, orderingOff: orderingOff,
      order: function (query) { OO.router.go(L().menuHref(hotel, query || undefined)); },
    }), { sections: [], store: store, timeZone: site.doc.locale && site.doc.locale.timezone, storeLoading: !store && q.loading });
    var theme = W.themeOver({}, site.doc);
    var logo = L().httpsUrl(landing.header && landing.header.logo) || theme.logo || null;
    var tagline = L().text(landing.footer && landing.footer.tagline, 200) || site.doc.brand.tagline || '';
    var chrome = Chrome.build(landing, ctx, { brand: site.doc.brand, logo: logo, footLogo: logo, store: store, tagline: tagline, hoursRows: hours });
    var cart = OO.router.link('cart', null, { hotel: hotel });
    var basket = browse ? null : <Basket count={count} link={cart} current={route.page === 'cart'} />;
    var canvas = landing.design === 'canvas' && OO.pages.LandingComposition;
    var canvasFooter = canvas && canvas.footerOf(landing);
    var canvasColors = site.doc.surfaces && site.doc.surfaces.landing && site.doc.surfaces.landing.colors;

    // --oo-site-top: the sticky SiteTop's height (see the header).
    var wrapRef = React.useRef(null);
    var topS = React.useState(null);
    var sticky = canvas ? true : chrome.header.sticky !== false;
    useLayout(function () {
      var wrap = wrapRef.current;
      var top = wrap && wrap.querySelector ? wrap.querySelector('.oo-sc-top, .oo-canvas-header') : null;
      if (!top) return undefined;
      function measure() { topS[1](sticky ? Math.round(top.getBoundingClientRect().height) : 0); }
      measure();
      if (typeof window.ResizeObserver !== 'function') return undefined;
      var ro = new window.ResizeObserver(measure);
      ro.observe(top);
      return function () { ro.disconnect(); };
    }, [sticky, !!canvas]);
    var style = topS[0] === null ? undefined : { '--oo-site-top': topS[0] + 'px' };

    return (
      <div className={cx('oo-sc-ordering', canvas && 'oo-sc-ordering--canvas', browse && 'oo-sc-ordering--browse')} ref={wrapRef} style={style}>
        <a className="oo-lp-skip" href="#oo-sc-main">Skip to content</a>
        {canvas ? <canvas.Header key={route.page + '|' + JSON.stringify(route.params || {})} landing={landing} ctx={ctx} colors={canvasColors} basket={basket} /> : <Chrome.SiteTop sticky={sticky}
          header={<Chrome.SiteHeader key={route.page + '|' + JSON.stringify(route.params || {})} brand={chrome.header.brand} nav={chrome.header.nav} cta={chrome.header.cta} basket={basket} />} />
        }
        <div id="oo-sc-main" className="oo-sc-ordering__page" tabIndex={-1}>{props.children}</div>
        {canvasFooter ? <canvas.Composition section={canvasFooter} ctx={ctx} /> : <Chrome.SiteFooter {...chrome.footer} />}
      </div>
    );
  }
  OrderingSite.applies = applies;
  OrderingSite.hasHomeNavigation = hasHomeNavigation;
  OrderingSite.PAGES = Object.keys(PAGES);
  OO.ui.OrderingSite = OrderingSite;
})();


// ===================== src/pages/menu.jsx =====================
// src/pages/menu.jsx: the Menu page (page id 'menu': /{hotel} online, /{hotel}/menu, /{hotel}/table/{tableId}, and
// P5-01's SEO item paths /{hotel}/product|combo/{name}-{id}).
// Owner: T3a (Batch 1 UI on API-shaped fixtures). Styles: styles/menu.css (oo-menu-* classes, tokens only).
// Design: docs/online-order/design/ordering-flow.dc.txt: "01 Menu" / "D01 Menu" (sections layout) and "C01 Browse" /
// "C02 Browse" (category-first layout). Behaviour: renderVals + browseVals in the design's logic script.
//
// Layout = OO.model.menuLayout(store, route.query): store.menuLayout, overridden by ?layout=sections|categories.
//   store.menuLayout (MENU-LAYOUT, model-store.jsx): proposed.menu_layout, else the admin's Online-settings "Auto Menu
//   Popup" toggle (getRestaurant menu_popup_auto): on → categories (the tiles), off / absent → sections (the full menu).
//   sections    StoreHeader · sticky CategoryTabs · one section per category: SectionTitle, a ChipRow (ALL + subs) when
//               the category has sub-categories (it filters that section), DealCards (combos), ProductCards. The
//               active tab follows the scroll (IntersectionObserver); a tab smooth-scrolls to its section below the
//               sticky bar and writes #<category-key> (replace); a #hash on load or hashchange scrolls there.
//   categories  level 0 category grid (CategoryTile image | text from store.categoryStyle) → level 1 matching sub-category grid
//               incl. "ALL {CAT}" (categories with 2+ sub-categories) → level 2 products with sibling chips. The level
//               lives in the URL (?cat=<id>&sub=<id|all>): going down pushes (browser back steps up), sibling chips
//               replace, and the header back arrow steps up one level (history.back when this page pushed the level
//               from that parent, else a replace). Mobile header = PageHeader with browseVals' title / subtitle per
//               level; desktop = StoreHeader + Breadcrumbs.
// Shared:
//   search      mobile: the tabs' search icon (sections) or the field above MENU (categories); desktop: the header
//               field. REF parity (P2-18; menuFilters.ts filterAndSortMenuCategories): the query and every field are
//               normalised (lower case, '-' / '_' runs as one space, spaces collapsed, trimmed; accents folded too,
//               ours: 'jalapeno' finds 'Jalapeño'). A category whose name or description matches brings everything it
//               shows; otherwise a product matches on its name, description, sub-category name or tags (a dietary
//               code's label too, OO.pages.ItemSheet.tagLabel: 'vegetarian' finds 'V'), a combo on its name,
//               description or combo category. Only what the view shows is searched (D-07: what the policy hides
//               never appears); the orderable results come first and the greyed ones after them, shown greyed (REF's
//               available-first partition, over one list); menu order otherwise (category order, a category's combos
//               before its products).
//   landing     ?q=<text> pre-fills the search (REF desktop; the mobile sections layout opens its search bar) and
//               ?orderType=pickup|delivery sets the checkout draft's order type when the cart would price that type
//               here (REF OrderContext readOrderTypeIntent; cart.jsx's rules via orderTypeOf: the store's offered
//               types, no DELIVERY without Maps on a real store). P5-01: the search starts as searchSeed(q) (tags and
//               control characters dropped, spaces collapsed, trimmed, at most SEARCH_MAX characters: the field shows
//               it as text only); mode qr ignores ?orderType= (a table order is dine-in, D-20). Once the store is
//               loaded both are consumed: dropped from the URL (replace, every other key and the #hash kept), so a
//               refresh or Back never re-applies them.
//               With a cart deep link (?type=cart&orderType=delivery) the order type is applied first, then the cart
//               (W3-fix #4: the cart used to leave before the store was in and lost it); no rewrite of its own then.
//               App mode's /{hotel} is Home, which applies ?orderType= itself (app-home.jsx).
//   overlays    <OO.pages.ItemSheet | OO.pages.ComboBuilder hotel store menu productId | comboId moment onClose onAdded>
//               (owned by T3b; looked up at render time, this file is concatenated first). onAdded(line, info) closes
//               the overlay and toasts info.message. moment = the cards' judging opts (useDisplay's moment: now,
//               today, timeZone, orderType, domain, menuId), so a sheet can judge with OO.model.displayState at the same
//               moment (the sheets read the static flags until they use it).
//               <OO.pages.MenuOverlays hotel store menu route mode moment onBusy> (P2-19 / P2-29: the after-add upsell,
//               the store alert, popups, the member-login prompt) is mounted the same way; moment = the cards' judging
//               moment, so suggestions, banners and popups are judged as the cards are. <OO.pages.CouponPrompt hotel
//               store menu mode blocked> (P2-14) waits while anything is up over the menu: the item sheet / combo
//               builder, About, or one of MenuOverlays' own (its onBusy signal, W3-fix #9), so they never stack.
//   deep links  ?type=product|combo|category|cart&id=N (the router canonicalises the shorthand forms). cart → the
//               cart page (replace) at once, or once the page is ready when a landing ?orderType= comes with it (see
//               "landing"); the others once the menu has loaded. type / id are then dropped from the
//               URL (replace, every other param kept but the landing keys q / orderType, which "landing" consumes, so
//               neither rewrite puts back what the other dropped) so refresh and back don't reopen them. A product /
//               combo the view hides (the D-07 policy, a table's excluded category) → "<name> isn’t available here"; a greyed
//               one → its card's toast; one the menu doesn't have → "We couldn’t find that item / deal on the menu".
//   item path   P5-01 (D-19): REF's SEO item path /{hotel}/product|combo/{name}-{id} (route.params item / itemId /
//               itemSlug, glue/router.jsx) opens that item once the menu has loaded, as the deep link above would, and
//               the URL STAYS the item path while its sheet is open (the SEO canonical). Closing the sheet (or adding
//               the item) replaces it with the plain menu URL (the route without the item params, the query without
//               type / id / the landing keys, the #hash kept), once: Back never reopens it, and that URL names no item.
//               An item that can't open toasts as the deep link does and goes to the plain menu URL at once. The
//               router never makes an item path for an open sheet: a card's sheet leaves the URL as it is.
//   table       mode qr only (P4-01; tables are qr-only since P4-14): the table context OO.pages.Table.context(hotel)
//               (src/pages/table.jsx checks the table of /{hotel}/table/{id}, keeps the context on OO.store and shows
//               its gate over this page while the table is checked or can't take orders) → an "Ordering for T5"
//               strip, and the table group's excluded categories are hidden. No context, no table here. POLISH-2
//               (p5-followups #17; D-20: the account is off at a table, the router sends it away): no account ring in
//               the header in mode qr, as the checkout.
//   status      "Closed now · Opens 11am" strip while the store is closed (ordering stays open for later pickup).
//   banners     <OO.pages.MenuBanners hotel store menu moment> ("Featured offers", P2-29: the live banners, a tap opens
//               the full image and its link; src/pages/menu-overlays.jsx) heads the sections and the category grid
//               (level 0), not search results; nothing when no banner is live.
//   chrome      About dialog (StoreHeader's About), FloatingCartBar (count + OO.cart.subtotal), PoweredBySipo. The
//               StoreHeader gets the store (P3-07): its rating badge beside the name (OO.ui.StoreRating).
//   items       D-07 (owner decision; P2-17 on P2-16's OO.model.menuDisplay): every product, combo and category is
//               judged at the moment the order is for (orderMoment below). normal → as before. grey → ProductCard
//               soldOut (reason 'soldOut': its inactive_reason or "Sold out") or ProductCard / DealCard `unavailable`,
//               badged with the policy's label ("Available Today From 5:00 PM", "Available Tomorrow", "Not available",
//               a switch's inactive_reason). hide → not rendered; a category left with nothing to show is skipped, and so
//               are its empty sub-categories (chips, rows); tile and row counts are what shows. A category greyed as a
//               whole (its own switch / window) carries its label under its title. A greyed card is never orderable:
//               the card swallows the tap while it is off, and a Blocked wrapper (display: contents, no box) catches the
//               click as it bubbles and toasts "<name>: <label>". Available items and categories lead; the configured
//               order is kept within the available and greyed groups, including combined lists before pagination.
//               Online and QR table ordering hide every unavailable entry (owner 2026-11-04); their empty
//               categories and sub-categories disappear from browsing, search and direct category links too.
//   moment      orderMoment(store, draft, mode): the draft's chosen slot while it is still offered (found by its value,
//               as the checkout and the cart find it: OO.schedule.chosen, P2-23), else now (OO.data.now()); the order type
//               the cart prices (OO.pages.Cart.shared.pricingCtx, else the draft's offered type) in the mode's domain
//               (no table: the cart and checkout check lines there). The label's day words count from now.
//   named menus OO.model.namedMenus via menuDisplay: with two or more pickable, a "Menus" chip row heads the sections
//               (sections layout) or the category grid (level 0); everything off the active menu is hidden. The pick is
//               kept per hotel while the page lives (REF keeps it in memory), else the first by position.
//   prices      The menu's price (a pricetag's once the menu it is given is priced: OO.pricetags.priceMenu keeps the
//               base as basePrice; nothing selects or applies a tag here). Struck through, as REF's MenuItemPrice: the
//               base price under a membership tag (menu.pricetag.membership, a "Member price" badge), else the banner
//               (was) price when it is above the shown price.
// WL-5 (landing-only websites; mode landing only): when OO.whitelabel.browseOnly(hotel) (the landing website's ordering is
// off for this store) the page is a BROWSE-ONLY menu: the same categories, items, prices, images and search, and the item
// sheet with its description, sizes and options (props.browse: no quantity, notes or add), a combo's details sheet; but
// no cart pill or floating cart bar, no account ring, no promo banners, upsell, popups or coupon prompt, and a cart deep
// link is dropped. Back goes to the landing page. Everywhere else (every other mode and host, and mode landing with
// ordering on) nothing changes.
// WL-OPT (the white-label document's menu options; src/lib/whitelabel.jsx puts them on the Store, the active surface's
// over the top-level layout; a store without them gets no class, so the page renders exactly as before):
//   store.categoryColumns 2 | 3 → oo-menu-cgrid--cols-N on the level-0 category grid (image and text tiles): at most N
//       cards per desktop row; a phone keeps its 2 (never more than N). 4 is today's grid and adds nothing.
//   store.categoryImage 'large' → oo-menu-cgrid--large on that grid with image tiles: a ~35% taller photo.
//   store.productImage 'large' → oo-menu-pgrid--large on the product grids of the sections layout, a category's
//       products (level 2) and the search results: bigger ProductCard and DealCard (combo) thumbnails. Not the kiosk's
//       cards (KioskProductCard) or the market app's (its own design). Sizes: styles/menu.css.
// Query keys owned here: cat, sub, type, id, q, orderType. Every other key (layout, theme, table_id, …) survives the
// page's own navigation, and ?theme= is carried onto the cart / profile links. Route params: tableId (mode qr's table
// path) and item / itemId / itemSlug (an item path) ride along on the page's own URLs (menuHref) until the item path's
// sheet closes.
// OO.pages.Menu.shared (app-home.jsx's Popular reads it):
//   orderMoment(store, draft, mode) → { now (ms: the judged instant), today (ms: now), orderType, domain }
//   picked(hotel) → the named menu id picked on this page, or null (W5-FIX #4: the cart's upsell moment reads it).
//   useDisplay(hotel, store, menu, mode) → { display: MenuDisplay | null, moment: { …orderMoment, timeZone, menuId } |
//       null, pick(menuId), tick } (a hook: re-judged each minute (tick), on a draft change and on a pick; the draft is
//       this hotel's: one stamped for another hotel counts as none).
// OO.pages.Menu.logic (pure; the tests read it): buildView(menu, excludedCategoryIds, display) → the view above;
//   linkTarget(menu, view, display, 'product' | 'combo', id) → { action: 'missing' | 'hidden' | 'grey' | 'open',
//   entity, text (the toast; null for 'open') }; notice(entity, display) → "<name>: <label>";
//   searchView(menu, view, query, display) → null (a blank query) | { query (trimmed), items: [{ kind: 'combo' |
//   'product', entity, grey }] (ranked as "search" says), combos, products (the items' entities by kind) };
//   searchText(text) → the normalised form both sides are compared in; landing(query, liveQuery, store, draft, mode?)
//   → { q (the search to pre-fill: searchSeed(query.q), '' for none), orderType (the draft type to set, or null:
//   absent, unknown, not priced here by the cart, already the draft's, or mode 'qr'), rest (liveQuery without q /
//   orderType), rewrite (liveQuery had one) }: what the landing effect does once the store is loaded (see "landing");
//   searchSeed(value) → the search a ?q= value pre-fills (P5-01; see "landing").
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var OWN_KEYS = ['cat', 'sub', 'type', 'id', 'q', 'orderType'];
  var LANDING_KEYS = ['q', 'orderType']; // consumed once the store is loaded (see "landing" in the header)
  var CARRY_KEYS = ['theme'];
  var ITEM_PARAMS = ['item', 'itemId', 'itemSlug']; // the SEO item path's route params (see "item path" in the header)
  var SEARCH_MAX = 100; // the longest ?q= pre-fill, in characters (P5-01)
  var HEADING_ID = 'oo-menu-h';
  var TOP = { level: 0, cat: null, sub: null };

  // ---- small helpers --------------------------------------------------------------------------------------
  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function same(a, b) { return str(a) === str(b); }
  // P4-01: the table context that applies to this store (OO.pages.Table.context, read at render time: table.jsx loads
  // after this file; OO.store.tableFor without it), or null (always, outside mode qr).
  function tableContext(hotel) {
    var T = OO.pages.Table;
    if (T && typeof T.context === 'function') return T.context(hotel) || null;
    return OO.store && typeof OO.store.tableFor === 'function' ? OO.store.tableFor(hotel) : null;
  }
  function cap(s) { s = str(s); return s.charAt(0) + s.slice(1).toLowerCase(); } // design browseVals cap()
  function slug(s) { return str(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function money(n, store) { return OO.format.money(n, store ? store.currency : undefined); }
  function plural(n) { return OO.format.pluralize(n, 'item'); }
  function hasSubs(cat) { return !!(cat && cat.subcategories && cat.subcategories.length > 1); }
  function pick(obj, keys) {
    var out = {};
    keys.forEach(function (k) { if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') out[k] = obj[k]; });
    return out;
  }
  function omit(obj, keys) {
    var out = {};
    Object.keys(obj || {}).forEach(function (k) { if (keys.indexOf(k) < 0) out[k] = obj[k]; });
    return out;
  }
  function plainClick(e) { return !e || !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }
  function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  // OO.router stamps history.state.ooIdx (in-app depth) on every entry it creates.
  function historyIdx() {
    try { var s = window.history.state; return s && typeof s.ooIdx === 'number' ? s.ooIdx : 0; } catch (e) { return 0; }
  }
  function hashNow() {
    var h = str(window.location && window.location.hash).replace(/^#/, '');
    try { return decodeURIComponent(h); } catch (e) { return h; }
  }
  function focusHeading(id) {
    var el = document.getElementById(id);
    if (!el) return;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  // ---- the orderable view of the menu -------------------------------------------------------------------
  // A table hides its excluded categories, the D-07 display (OO.model.menuDisplay; null: the static flags) hides
  // what can never be ordered here, and a category left with nothing to show is skipped. A category on show is a copy
  // holding only its shown products, combos and sub-categories, its tile meta recounted from them. anchor = the
  // section's #hash (the category key, made unique); label = a menu category greyed as a whole (its own switch /
  // window): its label; products / combos = the ids on show, productOrder / comboOrder = the available-first order
  // across the displayed categories, with shared items included once.
  function availableFirst(rows, unavailable) {
    var ready = [], later = [];
    rows.forEach(function (row) { (unavailable(row) ? later : ready).push(row); });
    return ready.concat(later);
  }
  function unavailable(item, d) {
    return d ? d.show !== 'normal' : !item || item.available === false || !!item.soldOut;
  }
  function itemUnavailable(menu, display, kind, id) {
    var key = kind === 'combo' ? 'combos' : 'products';
    var d = display && display[key] && display[key][id];
    var item = menu[key][id];
    return unavailable(item, d);
  }
  function sortProducts(products, display) {
    return availableFirst(products, function (p) { return unavailable(p, display && display.products && display.products[p.id]); });
  }
  function sortCategories(categories, menu, display) {
    return availableFirst(categories, function (c) {
      var d = display && display.categories && display.categories[c.id];
      return (d ? d.show !== 'normal' : c.available === false) ||
        ((c.productIds || []).every(function (p) { return itemUnavailable(menu, display, 'product', p); }) &&
         (c.comboIds || []).every(function (x) { return itemUnavailable(menu, display, 'combo', x); }));
    });
  }
  function buildView(menu, excluded, display) {
    var ex = {};
    (excluded || []).forEach(function (id) { ex[str(id)] = true; });
    var view = { categories: [], anchor: {}, label: {}, products: {}, combos: {}, productOrder: [], comboOrder: [] };
    if (!menu) return view;
    var used = {};
    menu.categories.forEach(function (c) {
      var id = str(c.id);
      if (ex[id]) return;
      var d = display && display.categories ? display.categories[c.id] : null;
      if (d && d.show === 'hide') return;
      var pids = (d ? d.productIds : c.productIds).filter(function (p) { return !!menu.products[p]; });
      var cids = (d ? d.comboIds : c.comboIds).filter(function (x) { return !!menu.combos[x]; });
      if (!pids.length && !cids.length) return;
      var key = slug(c.key || c.name) || 'menu';
      var anchor = used[key] ? key + '-' + slug(id) : key;
      used[key] = true;
      used[anchor] = true;
      pids = availableFirst(pids, function (p) { return itemUnavailable(menu, display, 'product', p); });
      cids = availableFirst(cids, function (x) { return itemUnavailable(menu, display, 'combo', x); });
      var shown = d ? shownCategory(c, d, pids, cids) : Object.assign({}, c, { productIds: pids, comboIds: cids });
      shown.subcategories = availableFirst((shown.subcategories || []).map(function (sub) {
        return Object.assign({}, sub, { productIds: availableFirst(sub.productIds, function (p) { return itemUnavailable(menu, display, 'product', p); }) });
      }), function (sub) { return sub.productIds.every(function (p) { return itemUnavailable(menu, display, 'product', p); }); });
      view.categories.push(shown);
      view.anchor[id] = anchor;
      if (c.kind !== 'combo' && (d ? d.show === 'grey' : c.available === false)) view.label[id] = d ? d.label : c.inactiveReason || 'Not available right now';
    });
    view.categories = sortCategories(view.categories, menu, display);
    view.categories.forEach(function (c) {
      c.productIds.forEach(function (p) { if (!view.products[p]) { view.products[p] = true; view.productOrder.push(p); } });
      c.comboIds.forEach(function (x) { if (!view.combos[x]) { view.combos[x] = true; view.comboOrder.push(x); } });
    });
    view.productOrder = availableFirst(view.productOrder, function (p) { return itemUnavailable(menu, display, 'product', p); });
    view.comboOrder = availableFirst(view.comboOrder, function (x) { return itemUnavailable(menu, display, 'combo', x); });
    return view;
  }
  // A category as shown: its shown products / combos and the sub-categories that keep one (the display's lists, in
  // menu order), the meta recounted as OO.model counts it ("N categories" with 2+ sub-categories, else "N items").
  function shownCategory(c, d, pids, cids) {
    var keep = {};
    d.subcategories.forEach(function (s) { keep[str(s.id)] = s.productIds; });
    var subs = c.subcategories.filter(function (s) { return !!keep[str(s.id)]; }).map(function (s) { return Object.assign({}, s, { productIds: keep[str(s.id)] }); });
    var n = pids.length + cids.length;
    return Object.assign({}, c, {
      productIds: pids, comboIds: cids, subcategories: subs, itemCount: n,
      meta: subs.length > 1 ? subs.length + ' categories' : plural(n),
    });
  }

  // ---- D-07: the moment the cards are judged at (see "moment" in the header) ----------------------------------------
  // The order type the cart prices (its rules: the store's offered types and checkout's gates), else the draft's when
  // the store offers it, else the first one it offers.
  function orderTypeOf(store, draft) {
    var K = OO.pages.Cart && OO.pages.Cart.shared;
    if (K && typeof K.pricingCtx === 'function') {
      try { return K.pricingCtx(store, draft).orderType; } catch (e) { /* the local rule below */ }
    }
    var list = ((store && store.orderTypes) || []).filter(function (t) { return t === 'PICKUP' || t === 'DELIVERY'; });
    var want = draft && draft.orderType;
    return list.indexOf(want) >= 0 ? want : list[0] || 'PICKUP';
  }
  function orderMoment(store, draft, mode) {
    var now = OO.data.now();
    var orderType = orderTypeOf(store, draft);
    var at = now.getTime();
    if (store && draft && draft.slot !== undefined && draft.slot !== null && draft.slot !== '') {
      try {
        // P2-23: the chosen time by its value (OO.schedule.chosen: any window day, past midnight too), as the checkout.
        var hit = OO.schedule.chosen(store, orderType, draft, now);
        if (hit) at = OO.model.slotInstant(store, hit.slot, now);
      } catch (e) { /* judged at now */ }
    }
    var actualMode = mode === undefined ? OO.mode : mode;
    var moment = { now: at, today: now.getTime(), orderType: orderType, domain: OO.model.domain(orderType, actualMode) };
    if (actualMode === 'online' || actualMode === 'qr') moment.hideUnavailable = true;
    if (OO.model.hideOutsideAvailability(store, actualMode)) moment.hideOutsideAvailability = true;
    return moment;
  }
  var picks = {}; // hotel → the named menu picked on this page (REF keeps the pick in memory: a reload forgets it)
  function selectDraft(s) { return s.checkout; }
  // The display hook (see OO.pages.Menu.shared in the header); tick = the minute counter it re-judges on.
  function useDisplay(hotel, store, menu, mode) {
    var draft = OO.useStore(selectDraft);
    var tick = useMinuteTick();
    var bump = React.useState(0)[1];
    var key = str(hotel);
    var mine = draft && (!draft.hotel || str(draft.hotel) === key) ? draft : null;
    var moment = React.useMemo(function () {
      return store ? orderMoment(store, mine, mode) : null;
    }, [store, mine ? mine.orderType : null, mine ? mine.day : null, mine ? mine.slot : null, mode, tick]);
    var selected = Object.prototype.hasOwnProperty.call(picks, key) ? picks[key] : null;
    var tz = store ? store.timezone : null;
    var todayYmd = moment ? OO.model.storeNow(store, moment.today).ymd : '';
    var display = React.useMemo(function () {
      if (!menu || !moment) return null;
      return OO.model.menuDisplay(menu, Object.assign({}, moment, { timeZone: tz, selected: selected }));
    }, [menu, moment ? Math.floor(moment.now / 60000) : null, moment ? moment.domain : null, moment && moment.hideOutsideAvailability, moment && moment.hideUnavailable, todayYmd, selected, tz]);
    var pick = React.useCallback(function (id) { picks[key] = id; bump(function (n) { return n + 1; }); }, [key]);
    return {
      display: display, pick: pick, tick: tick,
      moment: moment && display ? Object.assign({ timeZone: tz, menuId: display.menuId }, moment) : null,
    };
  }

  // ---- search (P2-18, REF parity; see "search" in the header) ----------------------------------------------------------
  // REF normalizedText (lower case, '-' / '_' runs → ' ', spaces collapsed, trimmed), plus accents folded.
  function searchText(v) {
    var s = str(v).toLowerCase();
    try { s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (e) { /* no String#normalize: as is */ }
    return s.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function anyMatch(fields, needle) {
    for (var i = 0; i < fields.length; i++) if (searchText(fields[i]).indexOf(needle) >= 0) return true;
    return false;
  }
  // A tag as written and as the item sheet spells a dietary code ('V' → 'Vegetarian').
  function tagTexts(tags) {
    var L = OO.pages.ItemSheet && OO.pages.ItemSheet.tagLabel;
    return (tags || []).reduce(function (out, t) { return out.concat(L ? [t, L(t)] : [t]); }, []);
  }
  function productFields(p) { return [p.name, p.description, p.subcategoryName].concat(tagTexts(p.tags)); }
  function comboFields(c) { return [c.name, c.description, c.categoryName]; }
  // Greyed: the D-07 display's 'grey' (else the static flags).
  function greyed(map, e) {
    var d = map ? map[e.id] : null;
    return d ? d.show === 'grey' : !!e.soldOut || e.available === false;
  }
  // The results for `query` over what the view shows (null when the query is blank): items ranked orderable first,
  // greyed after, menu order within each; combos / products = their entities.
  function searchView(menu, view, query, display) {
    var raw = str(query).trim();
    var needle = searchText(raw);
    if (!needle || !menu || !view) return null;
    var seen = {}, items = [];
    function take(kind, e, whole) {
      var k = kind + ':' + e.id;
      if (seen[k] || !(whole || anyMatch(kind === 'combo' ? comboFields(e) : productFields(e), needle))) return;
      seen[k] = true;
      items.push({ kind: kind, entity: e, grey: greyed(display && (kind === 'combo' ? display.combos : display.products), e) });
    }
    view.categories.forEach(function (c) {
      var whole = anyMatch([c.name, c.description], needle);
      (c.comboIds || []).forEach(function (id) { if (view.combos[id] && menu.combos[id]) take('combo', menu.combos[id], whole); });
      (c.productIds || []).forEach(function (id) { if (view.products[id] && menu.products[id]) take('product', menu.products[id], whole); });
    });
    var ranked = items.filter(function (x) { return !x.grey; }).concat(items.filter(function (x) { return x.grey; }));
    return {
      query: raw, items: ranked,
      combos: ranked.filter(function (x) { return x.kind === 'combo'; }).map(function (x) { return x.entity; }),
      products: ranked.filter(function (x) { return x.kind === 'product'; }).map(function (x) { return x.entity; }),
    };
  }
  function resultCount(r) { return r ? r.items.length : 0; }

  // ---- landing params (P2-18; see "landing" in the header) ------------------------------------------------------------
  // ?orderType= → 'PICKUP' | 'DELIVERY' (any case), else null.
  function orderTypeIntent(value) {
    var v = str(value).trim().toUpperCase();
    return v === 'PICKUP' || v === 'DELIVERY' ? v : null;
  }
  // The search a ?q= value pre-fills (P5-01): tags and control characters out, white space collapsed, trimmed, at most
  // SEARCH_MAX characters (code points: an emoji is never cut in half). The field shows it as text only.
  function searchSeed(value) {
    var s = str(value).replace(/<[^>]*>/g, ' ').replace(/[<>\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
    var chars = Array.from(s);
    return chars.length > SEARCH_MAX ? chars.slice(0, SEARCH_MAX).join('').trim() : s;
  }
  // What a landing does (the page's effect runs it once the store is loaded): q = the search to pre-fill ('' for none);
  // orderType = the draft order type to set (null: no ?orderType=, an unknown one, one the cart wouldn't price here, the
  // draft's already, or mode qr: a table order is dine-in); rest = liveQuery (the URL's query as it is now) without the
  // landing keys; rewrite = it had one. draft = this hotel's checkout draft (or null).
  function landing(query, liveQuery, store, draft, mode) {
    var want = mode === 'qr' || mode === 'kiosk' ? null : orderTypeIntent(query && query.orderType);
    var orderType = want && store && orderTypeOf(store, Object.assign({}, draft || {}, { orderType: want })) === want
      && !(draft && draft.orderType === want) ? want : null;
    var live = liveQuery || {};
    var rest = omit(live, LANDING_KEYS);
    return { q: searchSeed(query && query.q), orderType: orderType, rest: rest, rewrite: Object.keys(rest).length !== Object.keys(live).length };
  }

  // ---- category-first levels (URL ?cat=<id>&sub=<id|all>) ---------------------------------------------------
  function findCat(view, id) {
    return view.categories.filter(function (c) { return same(c.id, id); })[0] || null;
  }
  // An unknown cat falls back to level 0, an unknown sub to level 1; a category with fewer than two
  // sub-categories goes straight to its products (level 2), as in the design.
  function resolveLevel(view, query) {
    var cat = query.cat !== undefined && query.cat !== '' ? findCat(view, query.cat) : null;
    if (!cat) return TOP;
    if (!hasSubs(cat)) return { level: 2, cat: cat, sub: null };
    var s = str(query.sub).trim().toLowerCase();
    if (s === 'all') return { level: 2, cat: cat, sub: 'all' };
    var sub = s ? cat.subcategories.filter(function (x) { return same(x.id, s); })[0] : null;
    return sub ? { level: 2, cat: cat, sub: sub } : { level: 1, cat: cat, sub: null };
  }
  function levelQuery(lv) {
    var q = {};
    if (lv.cat) {
      q.cat = str(lv.cat.id);
      if (lv.sub) q.sub = lv.sub === 'all' ? 'all' : str(lv.sub.id);
    }
    return q;
  }
  function levelKey(lv) { var q = levelQuery(lv); return (q.cat || '') + '|' + (q.sub || ''); }
  function parentLevel(lv) { return lv.level === 2 && lv.sub ? { level: 1, cat: lv.cat, sub: null } : TOP; }
  function childLevel(cat) { return { level: hasSubs(cat) ? 1 : 2, cat: cat, sub: null }; }

  // header title / subtitle per level (browseVals: title, subtitle)
  function browseTitles(store, lv) {
    if (!lv.cat) return { title: store.name, subtitle: [store.address.short, store.modesLabel].filter(Boolean).join(' · ') };
    var parts = ['Menu', cap(lv.cat.name)];
    if (lv.sub) parts.push(lv.sub === 'all' ? 'All' : cap(lv.sub.name));
    return { title: lv.sub && lv.sub !== 'all' ? cap(lv.sub.name) : cap(lv.cat.name), subtitle: parts.join(' / ') };
  }
  // WL-VEL: the ordering skin of this page (OO.whitelabel.skin; never in kiosk), or null.
  function skinOf(route, hotel, mode) {
    var W = OO.whitelabel;
    return mode !== 'kiosk' && W && typeof W.skin === 'function' ? W.skin(route.chain || hotel, 'menu') : null;
  }
  // the level heading in the page (browseVals: heading)
  // WL-VEL: 'Menu' / 'All' read in capitals (as always) unless the ordering skin writes them in natural case (ctx.natural:
  // the boutique skin, OO.whitelabel.skin). A store without that skin renders exactly the same text as before.
  function caps(word, ctx) { return ctx && ctx.natural ? word : word.toUpperCase(); }
  function levelHeading(lv, ctx) {
    if (!lv.cat) return caps('Menu', ctx);
    if (lv.sub === 'all') return caps('All', ctx) + ' ' + lv.cat.name;
    return lv.sub ? lv.sub.name : lv.cat.name;
  }

  // ---- navigation ---------------------------------------------------------------------------------------
  function menuHref(route, query, hash) {
    return OO.router.href('menu', route.params, { hotel: route.hotel, query: query, hash: hash || undefined });
  }
  function levelHref(route, lv) {
    return menuHref(route, Object.assign(omit(route.query, OWN_KEYS), levelQuery(lv)));
  }
  // history index → the level key this page pushed it from, so the back arrow can pop instead of stacking entries.
  var pushedFrom = {};
  function goLevel(route, from, to, how) {
    var href = levelHref(route, to);
    var l = window.location;
    if (l && href === l.pathname + l.search) return;
    if (how === 'replace') { OO.router.go(href, { replace: true }); return; }
    OO.router.go(href);
    pushedFrom[historyIdx()] = route.hotel + '#' + levelKey(from);
  }
  function stepUp(route, lv) {
    var to = parentLevel(lv);
    var idx = historyIdx();
    if (idx > 0 && pushedFrom[idx] === route.hotel + '#' + levelKey(to)) { window.history.back(); return; }
    OO.router.go(levelHref(route, to), { replace: true });
  }

  // ---- store status ---------------------------------------------------------------------------------------
  function closedText(store, open) {
    if (!store.online || !store.online.active) {
      return 'Not taking online orders' + (store.online && store.online.reason ? ' · ' + store.online.reason : '');
    }
    if (open.label === 'Temporarily closed') return 'Temporarily closed' + (open.reason ? ' · ' + open.reason : '');
    if (!open.label || open.label === 'Closed') return 'Closed now';
    return 'Closed now · ' + open.label;
  }
  // store.open is the status at load time (server clock). After a minute the page refreshes it from the device clock.
  function useMinuteTick() {
    var s = React.useState(0);
    var set = s[1];
    React.useEffect(function () {
      var t = setInterval(function () { set(function (n) { return n + 1; }); }, 60000);
      return function () { clearInterval(t); };
    }, []);
    return s[0];
  }

  // ---- cards ----------------------------------------------------------------------------------------------
  // ctx = { store, menu, view, display (OO.model.menuDisplay), openProduct, openCombo, tell(entity, display) }
  var NORMAL = { show: 'normal', label: null, reason: null };
  function hasDeviceFavourites(ctx) {
    var W = OO.whitelabel, key = (OO.router.current() || {}).chain || ctx.store.onlineKey;
    return W && W.pizzaClubDesign && W.pizzaClubDesign(key);
  }
  function displayOf(map, id) { return (map && map[id]) || NORMAL; }

  // The price (REF MenuItemPrice): the menu's price, a pricetag's once the menu is priced. Struck through: the base
  // price when a membership tag set the price (memberPriced: REF's "Usual Price" / "Member Price"), else the banner
  // (was) price when it is above the shown one (REF getMenuItemBannerPrice).
  function memberPriced(e, menu) {
    return !!(menu && menu.pricetag && menu.pricetag.membership && e.pricetagId != null && e.basePrice != null && e.price < e.basePrice);
  }
  function priceNode(e, ctx, member) {
    var price = money(e.price, ctx.store);
    var was = member ? e.basePrice : (e.wasPrice != null && e.wasPrice > e.price ? e.wasPrice : null);
    if (was == null) return price;
    return (
      <React.Fragment>
        {member ? <span className="oo-sr-only">Member price </span> : null}
        {price}
        <s className="oo-menu-was"><span className="oo-sr-only">{member ? 'usual price ' : 'was '}</span>{money(was, ctx.store)}</s>
      </React.Fragment>
    );
  }

  // What a greyed entry's tap (or deep link) says: what it is and why it can't be ordered now.
  function notice(e, d) { return e.name + ': ' + d.label; }
  // A deep link's product / combo (kind 'product' | 'combo', id as in the URL) → { action, entity, text }: 'missing'
  // (not on the menu), 'hidden' (the view hides it: the D-07 policy, a table's excluded category), 'grey' (text = its
  // notice) or 'open'. text = the toast (null for 'open').
  function linkTarget(menu, view, display, kind, id) {
    var combo = kind === 'combo';
    var e = menu ? (combo ? menu.combos : menu.products)[id] || null : null;
    if (!e) return { action: 'missing', entity: null, text: combo ? 'We couldn’t find that deal on the menu' : 'We couldn’t find that item on the menu' };
    if (!(combo ? view.combos : view.products)[id]) return { action: 'hidden', entity: e, text: e.name + ' isn’t available here' };
    var d = displayOf(display && (combo ? display.combos : display.products), e.id);
    return d.show === 'grey' ? { action: 'grey', entity: e, text: notice(e, d) } : { action: 'open', entity: e, text: null };
  }

  // A greyed card never opens (D-07). ProductCard / DealCard swallow the tap while off (a <button aria-disabled>, still
  // focusable), so this wrapper (display: contents: no box, the grid lays the card out as before) catches the click of
  // a tap, Enter or Space as it bubbles, and says why.
  function Blocked(props) {
    return <div className="oo-menu-blocked" onClick={props.onTap}>{props.children}</div>;
  }

  function ComboCard(props) {
    var c = props.combo, ctx = props.ctx;
    var d = displayOf(ctx.display && ctx.display.combos, c.id);
    var grey = d.show === 'grey';
    var member = memberPriced(c, ctx.menu);
    var Card = ctx.kiosk ? OO.ui.KioskProductCard : OO.ui.DealCard;
    var card = (
      <Card name={c.name} desc={c.description} image={c.image} price={priceNode(c, ctx, member)}
        showAddIcon={ctx.showAddIcon}
        badge={c.ribbon || (member ? 'Member price' : null) || (c.todayOnly ? 'Today only' : null)}
        unavailable={grey} unavailableLabel={grey ? d.label : undefined}
        onClick={function () { ctx.openCombo(c); }} />
    );
    var PC = OO.appKit && OO.appKit.PizzaClubFavouriteCard;
    if (PC && hasDeviceFavourites(ctx)) card = <PC hotel={ctx.store.onlineKey} kind="combo" item={c}>{card}</PC>;
    return grey ? <Blocked onTap={function () { ctx.tell(c, d); }}>{card}</Blocked> : card;
  }

  function ProductItem(props) {
    var ui = OO.ui;
    var p = props.product, ctx = props.ctx;
    var d = displayOf(ctx.display && ctx.display.products, p.id);
    var grey = d.show === 'grey';
    var soldOut = grey && d.reason === 'soldOut';
    var member = memberPriced(p, ctx.menu);
    var badges = [
      p.ribbon ? <ui.Badge key="r" tone="success" size="sm">{p.ribbon}</ui.Badge> : null,
      member ? <ui.Badge key="m" tone="success" size="sm">Member price</ui.Badge> : null,
    ].filter(Boolean);
    var Card = ctx.kiosk ? ui.KioskProductCard : ui.ProductCard;
    var card = (
      <Card name={ctx.zeta && OO.appKit.Zeta ? OO.appKit.Zeta.displayName(p.name) : p.name} desc={p.description} price={priceNode(p, ctx, member)} image={p.image} actionLabel={ctx.market ? 'View' : undefined} placeholderIcon={ctx.market ? 'Bag' : undefined}
        showAddIcon={ctx.showAddIcon}
        soldOut={soldOut} soldOutLabel={soldOut ? d.label : undefined} unavailable={grey && !soldOut} unavailableLabel={grey && !soldOut ? d.label : undefined}
        badge={badges.length > 1 ? <span className="oo-menu-badges">{badges}</span> : badges[0] || null}
        onClick={function () { ctx.openProduct(p); }} />
    );
    var PC = OO.appKit && OO.appKit.PizzaClubFavouriteCard;
    if (PC && hasDeviceFavourites(ctx)) card = <PC hotel={ctx.store.onlineKey} kind="product" item={p}>{card}</PC>;
    return grey ? <Blocked onTap={function () { ctx.tell(p, d); }}>{card}</Blocked> : card;
  }

  // WL-OPT (see the header): the classes for the white-label document's menu options, undefined when the store has none.
  function categoryGridClass(store, style) {
    var cols = store && (store.categoryColumns === 2 || store.categoryColumns === 3) ? store.categoryColumns : null;
    var large = !!store && store.categoryImage === 'large' && style === 'image';
    return OO.ui.cx(cols ? 'oo-menu-cgrid--cols-' + cols : null, large ? 'oo-menu-cgrid--large' : null) || undefined;
  }
  function productGridClass(ctx) {
    return ctx.store && ctx.store.productImage === 'large' && !ctx.kiosk && !ctx.market ? 'oo-menu-pgrid--large' : undefined;
  }

  function itemCards(ctx, combos, products, limit) {
    var rows = combos.map(function (c) { return { kind: 'combo', item: c }; })
      .concat(products.map(function (p) { return { kind: 'product', item: p }; }));
    rows = availableFirst(rows, function (row) { return itemUnavailable(ctx.menu, ctx.display, row.kind, row.item.id); });
    if (limit) rows = rows.slice(0, limit);
    return rows.map(function (row) {
      return row.kind === 'combo' ? <ComboCard key={'c' + row.item.id} combo={row.item} ctx={ctx} />
        : <ProductItem key={'p' + row.item.id} product={row.item} ctx={ctx} />;
    });
  }

  // The named-menu picker (REF NamedMenuSelector): a "Menus" chip row, one chip per pickable named menu. named = { menus,
  // active, onPick } | null (fewer than two pickable).
  function NamedMenus(props) {
    var ui = OO.ui;
    var n = props.named;
    if (!n) return null;
    return (
      <div className="oo-menu-named">
        <span className="oo-menu-named__label" aria-hidden="true">Menus</span>
        <ui.ChipRow label="Menus">
          {n.menus.map(function (m) {
            return <ui.Chip key={str(m.id)} active={same(m.id, n.active)} onClick={function () { n.onPick(m.id); }}>{m.name}</ui.Chip>;
          })}
        </ui.ChipRow>
      </div>
    );
  }

  // A slim full-width strip under the header ("Ordering for T5", "Closed now · Opens 11am").
  function Strip(props) {
    return (
      <div className={'oo-menu-strip oo-menu-strip--' + props.tone}>
        <OO.ui.Container className="oo-menu-strip__in">
          {OO.ui.renderIcon(props.icon, { size: 16 })}
          <span className="oo-menu-strip__text">{props.children}</span>
        </OO.ui.Container>
      </div>
    );
  }

  function EmptyMenu() {
    return <OO.ui.EmptyState icon="MenuList" title="Nothing on the menu right now" body="Please check back soon." />;
  }

  function Results(props) {
    var ui = OO.ui;
    var r = props.results;
    var n = resultCount(r);
    var word = props.ctx.market ? 'item' : 'dish';
    var items = props.limit ? r.items.slice(0, props.limit) : r.items;
    return (
      <section className="oo-menu-results" aria-labelledby={HEADING_ID}>
        <ui.SectionTitle id={HEADING_ID} level="page" sub={n ? n + (n === 1 ? ' ' + word + ' matches ' : ' ' + word + (word === 'dish' ? 'es' : 's') + ' match ') + '“' + r.query + '”' : null}>
          Search results
        </ui.SectionTitle>
        {n ? (
          <ui.ProductGrid className={productGridClass(props.ctx)}>
            {items.map(function (x) {
              return x.kind === 'combo'
                ? <ComboCard key={'c' + x.entity.id} combo={x.entity} ctx={props.ctx} />
                : <ProductItem key={'p' + x.entity.id} product={x.entity} ctx={props.ctx} />;
            })}
          </ui.ProductGrid>
        ) : (
          <ui.EmptyState icon="Search" title={'No ' + (props.ctx.market ? 'items' : 'dishes') + ' match “' + r.query + '”'} body={props.ctx.market ? 'Check the spelling, or browse the aisles.' : 'Check the spelling, or browse the menu.'}
            action={<ui.Button variant="outline" size="sm" onClick={props.onClear}>Clear search</ui.Button>} />
        )}
      </section>
    );
  }

  // ---- About dialog (StoreHeader's About button) ------------------------------------------------------------
  function AboutSheet(props) {
    var ui = OO.ui;
    var s = props.store, open = props.status;
    if (!props.open) return null;
    var addr = s.address.full || s.address.short;
    var dirs = s.mapLink || (addr ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(s.name + ', ' + addr) : null);
    var ready = s.pickup && s.pickup.asapLabel ? 'Ready in about ' + s.pickup.asapLabel : '';
    var allergy = OO.router.link('legal', { kind: 'allergy' }, { hotel: props.hotel });
    var terms = OO.router.link('legal', { kind: 'terms' }, { hotel: props.hotel });
    var about = OO.router.link('about', null, { hotel: props.hotel }); // P3-22: the full About page
    return (
      <ui.Sheet open onClose={props.onClose} variant="dialog" title={s.name}>
        <div className="oo-menu-about">
          {open ? <ui.StatusPill open={open.isOpen}>{open.isOpen ? open.label : closedText(s, open)}</ui.StatusPill> : null}
          {s.description ? <p className="oo-menu-about__desc">{s.description}</p> : null}
          <dl className="oo-menu-about__dl">
            {addr ? (
              <div className="oo-menu-about__row">
                <dt>Address</dt>
                <dd>
                  <span>{addr}</span>
                  {dirs ? <a className="oo-menu-about__link" href={dirs} target="_blank" rel="noopener noreferrer">Get directions</a> : null}
                </dd>
              </div>
            ) : null}
            {s.phone ? (
              <div className="oo-menu-about__row">
                <dt>Phone</dt>
                <dd><a className="oo-menu-about__link" href={'tel:' + s.phone.replace(/[^\d+]/g, '')}>{s.phone}</a></dd>
              </div>
            ) : null}
            {s.modesLabel ? (
              <div className="oo-menu-about__row">
                <dt>Ordering</dt>
                <dd>
                  <span>{s.modesLabel}</span>
                  {ready ? <span className="oo-menu-about__muted">{ready}</span> : null}
                </dd>
              </div>
            ) : null}
          </dl>
          <div className="oo-menu-about__links">
            <a className="oo-menu-about__link" href={allergy.href} onClick={allergy.onClick}>Allergy information</a>
            <a className="oo-menu-about__link" href={terms.href} onClick={terms.onClick}>Terms and conditions</a>
            <a className="oo-menu-about__link" href={about.href} onClick={about.onClick}>More about this store</a>
          </div>
        </div>
      </ui.Sheet>
    );
  }

  // ---- sections layout (01 / D01) ------------------------------------------------------------------------
  function SectionsView(props) {
    var ui = OO.ui;
    var view = props.view, route = props.route, ctx = props.ctx, menu = ctx.menu;
    var rootRef = props.rootRef;
    var searching = !!props.results;
    var ids = view.categories.map(function (c) { return str(c.id); });
    var idsKey = ids.join('|');
    var activeS = React.useState(ids[0] || '');
    var setActive = activeS[1];
    var active = ids.indexOf(activeS[0]) >= 0 ? activeS[0] : ids[0] || '';
    var chipsS = React.useState({});
    var chips = chipsS[0], setChips = chipsS[1];
    var collapsedS = React.useState({});
    // spy.hold pauses the scroll-spy while a tab / hash scroll runs (until the scroll has been idle for 160ms).
    var spy = React.useRef({ hold: false, last: 0, start: 0, timer: 0 });
    var pending = React.useRef(null); // a section scroll waiting for the sections to render (tab picked from results)
    var hashDone = React.useRef(false);

    function sectionEl(id) {
      var root = rootRef.current;
      if (!root) return null;
      var list = root.querySelectorAll('[data-oo-cat]');
      for (var i = 0; i < list.length; i++) if (list[i].getAttribute('data-oo-cat') === id) return list[i];
      return null;
    }
    function holdSpy() {
      var s = spy.current;
      s.hold = true;
      s.start = s.last = Date.now();
      clearTimeout(s.timer);
      (function wait() {
        s.timer = setTimeout(function () {
          var now = Date.now();
          if (now - s.last >= 160 || now - s.start >= 2500) s.hold = false;
          else wait();
        }, 100);
      })();
    }
    function goSection(id, smooth) {
      var el = sectionEl(id);
      if (!el) return false;
      holdSpy();
      setActive(id);
      try { el.scrollIntoView({ behavior: smooth && !reducedMotion() ? 'smooth' : 'auto', block: 'start' }); } catch (e) { el.scrollIntoView(true); }
      return true;
    }

    React.useEffect(function () {
      function onScroll() { spy.current.last = Date.now(); }
      window.addEventListener('scroll', onScroll, { passive: true });
      return function () { window.removeEventListener('scroll', onScroll); clearTimeout(spy.current.timer); };
    }, []);

    // A pending scroll is served by the first render that has the sections again.
    React.useEffect(function () {
      var p = pending.current;
      if (p && !searching && goSection(p.id, p.smooth)) pending.current = null;
    });

    // Scroll-spy: the IntersectionObserver band starts just under the sticky tabs. Each time a section crosses it,
    // the active tab is the first section whose bottom is still below that line (the last one at the page end).
    React.useEffect(function () {
      var root = rootRef.current;
      if (searching || !root || !window.IntersectionObserver) return undefined;
      var secs = [].slice.call(root.querySelectorAll('[data-oo-cat]'));
      if (!secs.length) return undefined;
      var bar = root.querySelector('.oo-ctabs');
      var line = Math.round((bar ? bar.getBoundingClientRect().height : 0) + 12);
      var storefrontHeader = props.storefront && root.querySelector('.oo-sf-header');
      if (storefrontHeader) line += Math.round(storefrontHeader.getBoundingClientRect().height);
      // WL-VEL: the tabs stick under the website's header when the site chrome wraps the menu (their CSS `top`).
      var stuck = bar && typeof window.getComputedStyle === 'function' ? parseFloat(window.getComputedStyle(bar).top) : 0;
      if (stuck > 0) line += Math.round(stuck);
      var raf = 0;
      function compute() {
        raf = 0;
        if (spy.current.hold) return;
        var cur = secs[secs.length - 1];
        for (var i = 0; i < secs.length; i++) {
          if (secs[i].getBoundingClientRect().bottom > line) { cur = secs[i]; break; }
        }
        var doc = document.documentElement;
        if (window.innerHeight + (window.pageYOffset || doc.scrollTop) >= doc.scrollHeight - 2) cur = secs[secs.length - 1];
        setActive(cur.getAttribute('data-oo-cat'));
      }
      function schedule() { if (!raf) raf = window.requestAnimationFrame(compute); }
      var io = new IntersectionObserver(schedule, { rootMargin: '-' + line + 'px 0px -55% 0px', threshold: [0, 1] });
      secs.forEach(function (s) { io.observe(s); });
      var end = root.querySelector('.oo-menu-end');
      var ioEnd = end ? new IntersectionObserver(schedule) : null;
      if (ioEnd) ioEnd.observe(end);
      return function () {
        io.disconnect();
        if (ioEnd) ioEnd.disconnect();
        if (raf) window.cancelAnimationFrame(raf);
      };
    }, [idsKey, searching, props.wide]);

    // #hash: on load (once the sections exist) and on hashchange.
    React.useEffect(function () {
      function fromHash(smooth) {
        var h = slug(hashNow());
        if (!h) return;
        var c = view.categories.filter(function (x) {
          var id = str(x.id);
          return view.anchor[id] === h || slug(id) === h || slug(x.name) === h;
        })[0];
        if (!c) return;
        if (searching) { pending.current = { id: str(c.id), smooth: false }; props.clearSearch(); }
        else goSection(str(c.id), smooth);
      }
      if (!hashDone.current) { hashDone.current = true; fromHash(false); }
      function onHash() { fromHash(true); }
      window.addEventListener('hashchange', onHash);
      return function () { window.removeEventListener('hashchange', onHash); };
    }, [idsKey]);

    // ?type=category deep link (scrollRequest from the page).
    React.useEffect(function () {
      var r = props.scrollRequest;
      if (!r) return;
      pending.current = { id: r.id, smooth: false };
      if (!searching && goSection(r.id, false)) pending.current = null;
    }, [props.scrollRequest]);

    function onTab(id) {
      if (props.storefront) collapsedS[1](function (old) { var next = Object.assign({}, old); delete next[id]; return next; });
      if (searching) { pending.current = { id: id, smooth: false }; setActive(id); props.clearSearch(); }
      else goSection(id, true);
      if (view.anchor[id]) OO.router.go(menuHref(route, route.query, view.anchor[id]), { replace: true });
    }
    function onSearch() {
      if (props.wide) {
        if (props.searchRef.current) props.searchRef.current.focus(); // the header field (StoreHeader search.inputRef)
        return;
      }
      props.setSearchOpen(true);
      window.scrollTo(0, 0);
    }
    function setChip(id, value) {
      setChips(function (m) { var n = Object.assign({}, m); n[id] = value; return n; });
    }

    var sections = view.categories.map(function (c) {
      var id = str(c.id);
      var subs = hasSubs(c) ? c.subcategories : null;
      var sel = subs ? chips[id] || 'all' : 'all';
      var sub = sel === 'all' ? null : subs.filter(function (s) { return same(s.id, sel); })[0] || null;
      var products = (sub ? sub.productIds : c.productIds).map(function (p) { return menu.products[p]; }).filter(Boolean);
      var combos = c.comboIds.map(function (x) { return menu.combos[x]; }).filter(Boolean);
      var hid = 'oo-menu-sec-' + view.anchor[id];
      var collapsed = props.storefront && collapsedS[0][id];
      var children = <React.Fragment>
        {subs ? <ui.ChipRow label={cap(c.name) + ' types'}>
          <ui.Chip active={!sub} onClick={function () { setChip(id, 'all'); }}>{caps('All', ctx)}</ui.Chip>
          {subs.map(function (s) { return <ui.Chip key={str(s.id)} active={!!sub && same(sub.id, s.id)} onClick={function () { setChip(id, str(s.id)); }}>{s.name}</ui.Chip>; })}
        </ui.ChipRow> : null}
        <ui.ProductGrid className={productGridClass(ctx)}>{itemCards(ctx, combos, products)}</ui.ProductGrid>
      </React.Fragment>;
      if (props.storefront) return <section key={id} className="oo-menu-sec" data-oo-cat={id} aria-labelledby={hid}>
        <h2 id={hid} className="oo-sf-section-title"><button type="button" aria-expanded={!collapsed} aria-controls={hid + '-items'} onClick={function () { collapsedS[1](function (old) { var next = Object.assign({}, old); next[id] = !old[id]; return next; }); }}><span>{c.name.toLowerCase()}</span><OO.ui.Icon name={collapsed ? 'ChevronDown' : 'ChevronUp'} size={15} /></button></h2>
        <div id={hid + '-items'} className="oo-sf-section-items" hidden={!!collapsed}>{children}</div>
      </section>;
      return (
        <section key={id} className="oo-menu-sec" data-oo-cat={id} aria-labelledby={hid}>
          <ui.SectionTitle id={hid} sub={view.label[id] || null}>{c.name}</ui.SectionTitle>
          {subs ? (
            <ui.ChipRow label={cap(c.name) + ' types'}>
              <ui.Chip active={!sub} onClick={function () { setChip(id, 'all'); }}>{caps('All', ctx)}</ui.Chip>
              {subs.map(function (s) {
                return <ui.Chip key={str(s.id)} active={!!sub && same(sub.id, s.id)} onClick={function () { setChip(id, str(s.id)); }}>{s.name}</ui.Chip>;
              })}
            </ui.ChipRow>
          ) : null}
          <ui.ProductGrid className={productGridClass(ctx)}>{itemCards(ctx, combos, products)}</ui.ProductGrid>
        </section>
      );
    });

    var tabs = view.categories.map(function (c) { return { id: str(c.id), label: c.name }; });
    var searchBar = props.searchOpen && !props.wide ? (
      <div className="oo-menu-searchbar">
        <ui.SearchField value={props.q} onChange={props.setQ} placeholder="Search the menu" autoFocus={!props.pizzaClub} />
        {!props.pizzaClub || props.q ? <ui.Button variant="ghost" size="sm" onClick={props.clearSearch}>Cancel</ui.Button> : null}
      </div>
    ) : null;

    if (props.storefront && OO.pages.Storefront) return <OO.pages.Storefront.Layout {...props} tabs={tabs} active={active} onTab={onTab} onSearch={onSearch} sections={sections} namedView={<NamedMenus named={props.named} />} resultsView={props.results ? <Results results={props.results} ctx={ctx} onClear={props.clearSearch} /> : null} emptyView={<EmptyMenu />} />;

    if (props.pizzaClub) return (
      <React.Fragment>
        {props.header}
        {searchBar}
        {props.strips}
        {tabs.length ? <ui.CategoryTabs tabs={tabs} active={active} onSelect={onTab} onSearch={onSearch} /> : null}
        <ui.Container as="main" className="oo-menu__main">
          <div className="oo-menu__body">
            {props.results ? null : props.promo}
            {props.results ? null : <NamedMenus named={props.named} />}
            {props.results ? <Results results={props.results} ctx={ctx} onClear={props.clearSearch} />
              : sections.length ? sections : <EmptyMenu />}
            {props.footer}
          </div>
        </ui.Container>
      </React.Fragment>
    );
    return (
      <React.Fragment>
        {props.header}
        {props.strips}
        {tabs.length ? <ui.CategoryTabs tabs={tabs} active={active} onSelect={onTab} onSearch={onSearch} /> : null}
        <ui.Container as="main" className="oo-menu__main">
          <div className="oo-menu__body">
            {searchBar}
            {props.results ? null : props.promo}
            {props.results ? null : <NamedMenus named={props.named} />}
            {props.results ? <Results results={props.results} ctx={ctx} onClear={props.clearSearch} />
              : sections.length ? sections : <EmptyMenu />}
            {props.footer}
          </div>
        </ui.Container>
      </React.Fragment>
    );
  }

  // The kiosk uses the same judged view, item cards/builders and navigation as the shared menu.
  // Only the category rail/category-first composition differs between the two Studio designs.
  function KioskView(props) {
    var ctx = props.ctx, menu = ctx.menu, cats = props.view.categories, lv = props.level;
    var chicking = OO.kiosk.presentation(ctx.store).variant === 'chicking';
    var cat = lv.cat || (!chicking ? cats[0] : null), sub = lv.sub && lv.sub !== 'all' ? lv.sub : null;
    function choose(c, s) { props.clearSearch(); goLevel(props.route, lv, c ? { level: 2, cat: c, sub: s || 'all' } : TOP); }
    function photo(c) {
      if (c.image) return c.image;
      var ids = c.productIds || [], comboIds = c.comboIds || [];
      var p = ids.map(function (id) { return menu.products[id]; }).concat(comboIds.map(function (id) { return menu.combos[id]; })).filter(function (p) { return p && p.image; })[0];
      return p && p.image;
    }
    function category(c, tile) {
      var img = photo(c), active = cat && same(c.id, cat.id);
      return <button key={c.id} type="button" className={'oo-kiosk-category' + (active ? ' is-active' : '')} aria-pressed={!!active} onClick={function () { choose(c); }}>
        {tile || !chicking ? <span className="oo-kiosk-category__photo">{img ? <img src={img} alt="" loading="lazy" /> : <span>{c.name.split(/\s+/).slice(0, 2).map(function (s) { return s.charAt(0); }).join('')}</span>}</span> : null}
        <span className="oo-kiosk-category__label"><strong>{c.name.split('/').map(function (s, i) { return <React.Fragment key={i}>{i ? <React.Fragment>/<wbr /></React.Fragment> : null}{s}</React.Fragment>; })}</strong>{tile ? <small>{plural(c.productIds.length + c.comboIds.length)}</small> : null}</span>
        {tile ? <span className="oo-kiosk-category__arrow"><OO.icons.ChevronRight size={26} /></span> : null}
      </button>;
    }
    var categoryTiles = chicking && !cat && !props.results;
    var products = cat ? (sub ? sub.productIds : cat.productIds).map(function (id) { return menu.products[id]; }).filter(Boolean) : [];
    var combos = cat ? cat.comboIds.map(function (id) { return menu.combos[id]; }).filter(Boolean) : [];
    return <div className={'oo-kiosk-menu' + (categoryTiles ? ' is-categories' : '') + (!cats.length ? ' is-empty' : '')}>
      {!categoryTiles && cats.length ? <nav className="oo-kiosk-menu__nav" aria-label="Menu categories">
        {chicking ? <button type="button" className="oo-kiosk-menu__all" onClick={function () { choose(null); }}><OO.icons.ArrowLeft size={24} /> All categories</button> : null}
        <div className="oo-kiosk-menu__categories">{cats.map(function (c) { return category(c, false); })}</div>
      </nav> : null}
      <main className="oo-kiosk-menu__content">
        {props.strips}
        <div className="oo-kiosk-menu__heading"><div><h1>{props.results ? 'Search results' : categoryTiles ? 'What are you craving?' : cat ? cat.name : 'Menu'}</h1>
          {categoryTiles ? <p>Choose a category to get started</p> : cat && props.view.label[cat.id] ? <p>{props.view.label[cat.id]}</p> : null}</div>
          <OO.ui.SearchField value={props.q} onChange={props.setQ} placeholder="Search the menu" /></div>
        <NamedMenus named={props.named} />
        {props.results ? <Results results={props.results} ctx={ctx} onClear={props.clearSearch} /> : categoryTiles ? <div className="oo-kiosk-category-grid">{cats.map(function (c) { return category(c, true); })}</div> : <React.Fragment>
          {cat && hasSubs(cat) ? <OO.ui.ChipRow label={cat.name + ' types'}><OO.ui.Chip active={!sub} onClick={function () { choose(cat); }}>All</OO.ui.Chip>
            {cat.subcategories.map(function (s) { return <OO.ui.Chip key={s.id} active={!!sub && same(sub.id, s.id)} onClick={function () { choose(cat, s); }}>{s.name}</OO.ui.Chip>; })}</OO.ui.ChipRow> : null}
          {products.length || combos.length ? <OO.ui.ProductGrid>{itemCards(ctx, combos, products)}</OO.ui.ProductGrid> : <EmptyMenu />}
        </React.Fragment>}
        {props.footer}
      </main>
      <footer className="oo-kiosk-orderbar"><div><span>{plural(props.count)} in your order</span><strong>{money(props.subtotal, ctx.store)}</strong></div>
        <OO.ui.Button size="lg" disabled={!props.count} onClick={props.cartLink.onClick} href={props.count ? props.cartLink.href : undefined}><OO.icons.Bag size={28} /> View order <span className="oo-kiosk-orderbar__count">{props.count}</span></OO.ui.Button></footer>
    </div>;
  }

  // ---- category-first layout (C01 / C02) ------------------------------------------------------------------
  function BrowseView(props) {
    var ui = OO.ui;
    var lv = props.level, view = props.view, route = props.route, ctx = props.ctx, menu = ctx.menu, wide = props.wide;
    var style = props.tileStyle;
    var withImages = style === 'image';

    function nav(to, how) {
      return {
        href: levelHref(route, to),
        onClick: function (e) {
          if (!plainClick(e)) return;
          e.preventDefault();
          props.clearSearch();
          goLevel(route, lv, to, how);
        },
      };
    }
    function firstImage(productIds, comboIds) {
      var i;
      for (i = 0; i < (comboIds || []).length; i++) { var c = menu.combos[comboIds[i]]; if (c && c.image) return c.image; }
      for (i = 0; i < productIds.length; i++) { var p = menu.products[productIds[i]]; if (p && p.image) return p.image; }
      return null;
    }
    function shown(ids) { return ids.filter(function (id) { return !!menu.products[id]; }); }

    var crumbs = null;
    if (wide) {
      if (props.results) crumbs = [{ label: 'Menu', onClick: props.clearSearch }, { label: 'Search results' }];
      else {
        crumbs = [lv.level === 0 ? { label: 'Menu' } : Object.assign({ label: 'Menu' }, nav(TOP))];
        if (lv.cat) crumbs.push(lv.sub ? Object.assign({ label: cap(lv.cat.name) }, nav({ level: 1, cat: lv.cat, sub: null })) : { label: cap(lv.cat.name) });
        if (lv.sub) crumbs.push({ label: lv.sub === 'all' ? 'All' : cap(lv.sub.name) });
      }
    }

    var content;
    var cat = lv.cat;
    var offSub = cat ? view.label[str(cat.id)] || null : null;
    if (props.results) {
      content = <Results results={props.results} ctx={ctx} onClear={props.clearSearch} />;
    } else if (lv.level === 0) {
      content = (
        <React.Fragment>
          {props.promo}
          <NamedMenus named={props.named} />
          <ui.SectionTitle id={HEADING_ID} className="oo-menu-browse__menu">{caps('Menu', ctx)}</ui.SectionTitle>
          {view.categories.length ? (
            <ui.CategoryGrid variant={style} className={categoryGridClass(ctx.store, style)}>
              {view.categories.map(function (c) {
                var link = nav(childLevel(c));
                return (
                  <ui.CategoryTile key={str(c.id)} variant={style} name={c.name} meta={c.meta}
                    image={withImages ? c.image || firstImage(c.productIds, c.comboIds) : null} href={link.href} onClick={link.onClick} />
                );
              })}
            </ui.CategoryGrid>
          ) : <EmptyMenu />}
        </React.Fragment>
      );
    } else if (lv.level === 1) {
      var allLink = nav({ level: 2, cat: cat, sub: 'all' });
      content = (
        <React.Fragment>
          <ui.SectionTitle id={HEADING_ID} sub={offSub}>{levelHeading(lv, ctx)}</ui.SectionTitle>
          <ui.CategoryGrid variant={style} className={categoryGridClass(ctx.store, style)}>
            <ui.CategoryTile variant={style} name={caps('All', ctx) + ' ' + cat.name} meta={plural(shown(cat.productIds).length)}
              image={withImages ? cat.image || firstImage(cat.productIds) : null} href={allLink.href} onClick={allLink.onClick} />
            {cat.subcategories.map(function (s) {
              var link = nav({ level: 2, cat: cat, sub: s });
              return (
                <ui.CategoryTile key={str(s.id)} variant={style} name={s.name} meta={plural(shown(s.productIds).length)}
                  image={withImages ? firstImage(s.productIds) : null} href={link.href} onClick={link.onClick} />
              );
            })}
          </ui.CategoryGrid>
        </React.Fragment>
      );
    } else {
      var subObj = lv.sub && lv.sub !== 'all' ? lv.sub : null;
      var products = shown(subObj ? subObj.productIds : cat.productIds).map(function (id) { return menu.products[id]; });
      var combos = cat.comboIds.map(function (x) { return menu.combos[x]; }).filter(Boolean);
      var cards = itemCards(ctx, combos, products);
      var siblings = hasSubs(cat) ? [{ key: 'all', label: caps('All', ctx), sub: 'all' }].concat(cat.subcategories.map(function (s) {
        return { key: str(s.id), label: s.name, sub: s };
      })) : null;
      content = (
        <React.Fragment>
          <ui.SectionTitle id={HEADING_ID} sub={offSub}>{levelHeading(lv, ctx)}</ui.SectionTitle>
          {siblings ? (
            <ui.ChipRow label={cap(cat.name) + ' types'}>
              {siblings.map(function (o) {
                var on = o.sub === 'all' ? lv.sub === 'all' : !!subObj && same(subObj.id, o.sub.id);
                return (
                  <ui.Chip key={o.key} active={on} onClick={function () {
                    if (on) return;
                    props.clearSearch();
                    goLevel(route, lv, { level: 2, cat: cat, sub: o.sub }, 'replace');
                  }}>{o.label}</ui.Chip>
                );
              })}
            </ui.ChipRow>
          ) : null}
          {cards.length ? <ui.ProductGrid className={productGridClass(ctx)}>{cards}</ui.ProductGrid> : <ui.EmptyState title="Nothing here right now" />}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {props.header}
        {props.strips}
        <ui.Container as="main" className="oo-menu__main">
          <div className="oo-menu__body oo-menu__body--browse">
            {crumbs ? <ui.Breadcrumbs items={crumbs} /> : null}
            {!wide && lv.level === 0 ? <ui.SearchField value={props.q} onChange={props.setQ} placeholder="Search the menu" /> : null}
            {content}
            {props.footer}
          </div>
        </ui.Container>
      </React.Fragment>
    );
  }

  // Market browsing shares every price, modifier and availability decision with the standard menu.
  // A large grocery catalog is rendered one page at a time; category links retain the ordinary cat/sub routes.
  function MarketBrowseView(props) {
    var ui = OO.ui, ctx = props.ctx, menu = ctx.menu, view = props.view, lv = props.level;
    var category = lv.cat || null, sub = lv.sub && lv.sub !== 'all' ? lv.sub : null;
    var limit = React.useState(24);
    var selection = String(category && category.id || '') + ':' + String(sub && sub.id || '') + ':' + props.q;
    React.useEffect(function () { limit[1](24); }, [selection, menu]);
    function unique(ids) {
      var seen = Object.create(null);
      return ids.filter(function (id) { if (seen[id]) return false; seen[id] = true; return true; });
    }
    var selected = category ? [category] : view.categories;
    var productIds = sub ? sub.productIds : unique([].concat.apply([], selected.map(function (cat) { return cat.productIds; })));
    var comboIds = unique([].concat.apply([], selected.map(function (cat) { return cat.comboIds; })));
    var products = productIds.map(function (id) { return menu.products[id]; }).filter(Boolean);
    var combos = comboIds.map(function (id) { return menu.combos[id]; }).filter(Boolean);
    var count = combos.length + products.length, shown = Math.min(count, limit[0]);
    var tabs = [{ id: 'all', label: 'All' }].concat(view.categories.map(function (cat) { return { id: str(cat.id), label: cat.name }; }));
    function selectCategory(id) {
      props.clearSearch();
      var cat = view.categories.filter(function (item) { return str(item.id) === id; })[0];
      goLevel(props.route, lv, cat ? { level: 1, cat: cat } : { level: 0 }, 'replace');
    }
    return <React.Fragment>
      {props.header}
      {props.strips}
      <div className="oo-market-search"><ui.SearchField value={props.q} onChange={props.setQ} placeholder="Search groceries, produce and more" /></div>
      <ui.CategoryTabs tabs={tabs} active={category ? str(category.id) : 'all'} onSelect={selectCategory} sticky={false} label="Shop aisles" />
      <ui.Container as="main" className="oo-menu__main"><div className="oo-menu__body">
        <h1 className="oo-sr-only">Shop groceries</h1>
        {props.results ? <React.Fragment>
          <Results results={props.results} ctx={ctx} onClear={props.clearSearch} limit={limit[0]} />
          {resultCount(props.results) > limit[0] ? <ui.Button variant="outline" size="sm" className="oo-market-more" onClick={function () { limit[1](function (value) { return value + 24; }); }}>Show more results</ui.Button> : null}
        </React.Fragment> : <React.Fragment>
          <NamedMenus named={props.named} />
          <p className="oo-market-count">{shown + ' of ' + count + ' items' + (category ? ' · ' + category.name : '')}</p>
          {shown ? <ui.ProductGrid>{itemCards(ctx, combos, products, limit[0])}</ui.ProductGrid> : <EmptyMenu />}
          {shown < count ? <ui.Button variant="outline" size="sm" className="oo-market-more" onClick={function () { limit[1](function (value) { return value + 24; }); }}>Show more items</ui.Button> : null}
        </React.Fragment>}
        {props.footer}
      </div></ui.Container>
    </React.Fragment>;
  }

  // ---- loading / not found / error --------------------------------------------------------------------------
  function LoadingView(props) {
    var ui = OO.ui;
    var sections = props.layout === 'sections';
    var name = props.name || (
      <React.Fragment>
        <span className="oo-menu-skel-name" aria-hidden="true" />
        <span className="oo-sr-only">Loading the menu</span>
      </React.Fragment>
    );
    return (
      <div className="oo-menu oo-menu--loading" aria-busy="true">
        <ui.StoreHeader name={name} />
        {sections ? <div className="oo-menu-skel-tabs" aria-hidden="true" /> : null}
        <ui.Container as="main" className="oo-menu__main">
          <div className={'oo-menu__body' + (sections ? '' : ' oo-menu__body--browse')}>
            <p className="oo-sr-only" role="status">Loading the menu…</p>
            <ui.Skeleton variant="text" lines={1} className="oo-menu-skel-title" />
            <ui.Skeleton variant={sections ? 'product' : 'tile'} count={sections ? 4 : 6} />
          </div>
        </ui.Container>
      </div>
    );
  }

  function StateView(props) {
    var ui = OO.ui;
    return (
      <div className="oo-menu oo-menu--state">
        <ui.PageHeader title={props.title} onBack={props.onBack} showBack={!!props.onBack} sticky={false} />
        <ui.Container as="main" className="oo-menu__main">
          <div className="oo-menu__body oo-menu__body--state">
            <ui.EmptyState icon={props.icon} title={props.heading} body={props.body} action={props.action} role={props.role} />
          </div>
        </ui.Container>
      </div>
    );
  }

  // ---- the page -----------------------------------------------------------------------------------------
  OO.pages.Menu = function Menu(props) {
    var ui = OO.ui;
    var route = props.route;
    var mode = props.mode || OO.mode;
    var hotel = route.hotel;
    var h = OO.useHotel(hotel, { withRedemptions: false });
    var store = h.store, menu = h.menu;
    // WL-5: the landing website's browse-only menu (see the header); false everywhere but mode landing.
    var browse = !!(OO.whitelabel && typeof OO.whitelabel.browseOnly === 'function' && OO.whitelabel.browseOnly(hotel));
    var wide = ui.useWide();
    var rootRef = React.useRef(null);
    var searchRef = React.useRef(null); // the desktop header search <input>
    var cart = OO.useStore(function (s) { return { hotel: s.hotel, lines: s.cart.lines }; });
    var checkout = OO.useStore(function (s) { return s.checkout; });

    // ---- the table (mode qr: P4-01's context for this store; see "table" in the header) ----
    OO.useStore(function (s) { return s.table; }); // follows the context as table.jsx keeps it
    var table = tableContext(hotel);

    // ---- D-07: every card judged at the moment the order is for (useDisplay; the minute tick drives openNow too) ----
    var dsp = useDisplay(hotel, store, menu, mode);
    var display = dsp.display;

    var excluded = table && table.excludedCategoryIds ? table.excludedCategoryIds : [];
    var kitchen = mode === 'app' && OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(route.chain || hotel);
    if (kitchen && kitchen.dining && kitchen.dining.categorySelection === 'configured' && menu) {
      var kitchenCategories = (kitchen.dining.categories || []).map(function (c) { return str(c.categoryId); });
      excluded = excluded.concat(menu.categories.filter(function (c) { return kitchenCategories.indexOf(str(c.id)) < 0; }).map(function (c) { return c.id; }));
    }
    var exKey = excluded.join(',');
    var view = React.useMemo(function () { return buildView(menu, excluded, display); }, [menu, exKey, display]);
    var ready = !!(store && menu);
    // Entering Menu starts an order in the app. Confirm missing/stale details before consuming an item link.
    var orderKit = OO.appKit && OO.appKit.orderSheet;
    var appOrderState = mode === 'app' && !browse && !table && orderKit && orderKit.ready() && store
      ? orderKit.status(store, checkout, { hotel: hotel, fixture: !!(OO.api && OO.api.isFixtureFor && OO.api.isFixtureFor(hotel)) }) : null;
    var appOrderPending = !!(appOrderState && (appOrderState.need === 'answer' || appOrderState.need === 'review'));
    var market = mode === 'app' && OO.whitelabel && typeof OO.whitelabel.appDesign === 'function' && OO.whitelabel.appDesign(route.chain || hotel);
    var storefront = mode === 'online' && !OO.landingMode && skinOf(route, hotel, mode) === 'storefront';
    var layout = mode === 'kiosk' ? 'categories' : storefront ? 'sections' : OO.model.menuLayout(store, route.query);
    var tileStyle = store && store.categoryStyle === 'text' ? 'text' : 'image';
    var lv = ready && (layout === 'categories' || market) ? resolveLevel(view, route.query) : TOP;

    // ---- search (?q= seeds it on landing: see "landing") ----
    var landingQ = str(route.query.q).trim();
    var seedQ = searchSeed(route.query.q);
    var qS = React.useState(seedQ);
    var q = qS[0], setQ = qS[1];
    var openS = React.useState(!!seedQ);
    var searchOpen = openS[0], setSearchOpen = openS[1];
    var results = React.useMemo(function () { return ready ? searchView(menu, view, q, display) : null; }, [ready, menu, view, q, display]);
    var clearSearch = React.useCallback(function () { setQ(''); setSearchOpen(false); }, []);

    // ---- landing params: ?q= / ?orderType=, applied once the store is loaded, then dropped from the URL ----
    var landingType = str(route.query.orderType).trim();
    var toCart = str(route.query.type).toLowerCase().trim() === 'cart'; // the cart deep link below leaves next
    var landingDone = React.useRef('');
    React.useEffect(function () {
      if (!ready || (!landingQ && !landingType)) return;
      var key = hotel + '|' + landingQ + '|' + landingType;
      if (landingDone.current === key) return;
      landingDone.current = key;
      var draft = OO.store.get().checkout;
      var mine = draft && (!draft.hotel || str(draft.hotel) === str(hotel)) ? draft : null;
      // The LIVE URL's query (not this render's route): a rewrite earlier in this commit is never undone.
      var plan = landing(route.query, (OO.router.current() || {}).query || route.query, store, mine, mode);
      if (plan.q) { setQ(plan.q); setSearchOpen(true); }
      if (plan.orderType) OO.store.setCheckout({ orderType: plan.orderType });
      if (plan.rewrite && !toCart) OO.router.go(menuHref(route, plan.rest, hashNow()), { replace: true });
    }, [hotel, ready, landingQ, landingType]);

    // ---- overlays, toast, About ----
    var sheetS = React.useState(null);
    var sheet = sheetS[0], setSheet = sheetS[1];
    var toastS = React.useState(null);
    var toast = toastS[0], setToast = toastS[1];
    var aboutS = React.useState(false);
    var ovlBusyS = React.useState(false); // one of MenuOverlays' own overlays is up (its onBusy; see "overlays")
    var barBusyS = React.useState(false); // WL-VEL: the order bar's sheet is open (its onBusy)
    var scrollReqS = React.useState(null);
    var seq = React.useRef(0);
    var say = React.useCallback(function (text, tone) {
      seq.current += 1;
      setToast({ id: seq.current, text: text, tone: tone || 'success' });
    }, []);
    var closeSheet = React.useCallback(function () { setSheet(null); }, []);
    var onAdded = React.useCallback(function (line, info) {
      setSheet(null);
      say(info && info.message ? info.message : info && info.kind === 'combo' ? 'Combo added to cart' : 'Added to cart');
    }, [say]);
    var openProduct = React.useCallback(function (p) { setSheet({ kind: 'product', id: p.id }); }, []);
    var openCombo = React.useCallback(function (c) { setSheet({ kind: 'combo', id: c.id }); }, []);
    // A greyed card's tap: what it is and why it can't be ordered now ("Cheese Scone: Available Tomorrow At 7:00 AM").
    var tell = React.useCallback(function (e, d) { say(notice(e, d), 'info'); }, [say]);

    // ---- deep links: ?type=product|combo|category|cart&id= ----
    var dlType = str(route.query.type).toLowerCase().trim();
    var dlId = str(route.query.id).trim();
    var dlDone = React.useRef('');
    React.useEffect(function () {
      if (!dlType) { dlDone.current = ''; return; }
      if (appOrderPending) return;
      var key = hotel + '|' + dlType + '|' + dlId;
      if (dlDone.current === key) return;
      if (dlType === 'cart') {
        // WL-5: no cart on a browse-only menu: the deep link is dropped (the plain menu URL, the rest kept).
        if (browse) {
          dlDone.current = key;
          OO.router.go(menuHref(route, omit(route.query, ['type', 'id']), hashNow()), { replace: true });
          return;
        }
        // A landing ?orderType= is applied first: the landing effect above runs before this one once the page is
        // ready (W3-fix #4). Without one, the cart at once.
        if (landingType && !ready) return;
        dlDone.current = key;
        OO.router.go(OO.router.href('cart', null, { hotel: hotel, query: pick(route.query, CARRY_KEYS) }), { replace: true });
        return;
      }
      if (!ready) return;
      dlDone.current = key;
      var rest = omit(route.query, ['type', 'id'].concat(LANDING_KEYS)); // the landing keys: consumed by their own effect
      var hash = hashNow();
      var found = null;
      if (dlType === 'product' || dlType === 'combo') {
        var t = linkTarget(menu, view, display, dlType, dlId);
        if (t.action !== 'open') say(t.text, 'info');
        else if (dlType === 'combo') openCombo(t.entity);
        else openProduct(t.entity);
      } else if (dlType === 'category') {
        found = findCat(view, dlId);
        if (found && layout === 'categories') {
          rest = omit(rest, ['cat', 'sub']);
          rest.cat = str(found.id);
        } else if (found) {
          hash = view.anchor[str(found.id)];
          scrollReqS[1]({ id: str(found.id) });
        }
      }
      OO.router.go(menuHref(route, rest, hash), { replace: true });
    }, [hotel, dlType, dlId, ready, view, layout, display, browse, appOrderPending]);

    // ---- the SEO item path (P5-01, D-19; see "item path" in the header): open its item, keep the URL while it is open ----
    var pathKind = str(route.params.item);
    var pathId = str(route.params.itemId);
    var pathDone = React.useRef('');
    // The plain menu URL: this route without the item (a table's path stays), its query without the keys the page
    // consumes (type / id, the landing keys), the #hash kept.
    var plainMenu = function () {
      return menuHref({ hotel: hotel, params: omit(route.params, ITEM_PARAMS) }, omit(route.query, ['type', 'id'].concat(LANDING_KEYS)), hashNow());
    };
    React.useEffect(function () {
      if (!pathKind) { pathDone.current = ''; return; }
      if (appOrderPending) return;
      if (!ready) return;
      var key = hotel + '|' + pathKind + '|' + pathId;
      if (pathDone.current === key) return;
      pathDone.current = key;
      var t = linkTarget(menu, view, display, pathKind, pathId);
      if (t.action === 'open') {
        if (pathKind === 'combo') openCombo(t.entity); else openProduct(t.entity);
        return; // the URL stays the item path while the sheet is open
      }
      say(t.text, 'info');
      OO.router.go(plainMenu(), { replace: true });
    }, [hotel, pathKind, pathId, ready, view, display, appOrderPending]);
    // The item path's sheet closed (Close, or added to the cart): the plain menu URL, once. A transition from open to
    // closed only: on the first render the sheet is closed too, before the effect above opens it.
    var prevSheet = React.useRef(null);
    React.useEffect(function () {
      var was = prevSheet.current;
      prevSheet.current = sheet;
      if (was && !sheet && pathKind) OO.router.go(plainMenu(), { replace: true });
    }, [sheet]);

    // ---- category-first: a level change scrolls to the top, clears the search and (on a depth change) moves
    // focus to the new heading. OO.App only scrolls on a page change, and the level is query-only.
    var lvKey = levelKey(lv);
    var depthKey = lv.level + '|' + (lv.cat ? str(lv.cat.id) : '');
    var prevLv = React.useRef(null);
    React.useEffect(function () {
      if (!ready || layout !== 'categories') { prevLv.current = null; return; }
      var prev = prevLv.current;
      prevLv.current = { key: lvKey, depth: depthKey };
      if (!prev || prev.key === lvKey) return;
      setQ('');
      setSearchOpen(false);
      window.scrollTo(0, 0);
      if (prev.depth !== depthKey) focusHeading(HEADING_ID);
    }, [lvKey, depthKey, ready, layout]);

    // A plain effect runs after OO.App's layout effect, so this title wins. P5-01: OO.App sets its own again when the
    // route's params change (an item path's sheet closing to the plain menu URL), so this one runs again then too.
    React.useEffect(function () {
      if (store && store.name) document.title = store.name + ' · ' + (mode === 'app' ? 'Menu' : mode === 'kiosk' ? 'Kiosk' : 'Order online');
    }, [store && store.name, mode, pathKind]);

    var tick = dsp.tick;
    var openNow = store ? (tick > 0 ? OO.model.openStatus(store, new Date()) : store.open) : null;

    // ---- not found / error / loading ----
    // WL-5: a browse-only menu's way back is the landing website's home page.
    function leave() { OO.router.back(browse ? OO.router.href('landing', null, { hotel: hotel }) : OO.router.href('find-store')); }
    if (h.notFound && browse) {
      var home = OO.router.link('landing', null, { hotel: hotel });
      return (
        <StateView title="Menu" onBack={leave} icon="Store" heading="The menu isn’t available right now"
          body="Please check back soon." action={<ui.Button size="md" href={home.href} onClick={home.onClick}>Back to the home page</ui.Button>} />
      );
    }
    if (h.notFound) {
      var find = OO.router.link('find-store');
      return (
        <StateView title="Store not found" onBack={mode === 'app' ? null : leave} icon="Store"
          heading="We couldn’t find this store" body="The link may be out of date. Find your store to start an order."
          action={<ui.Button size="md" href={find.href} onClick={find.onClick}>Find your store</ui.Button>} />
      );
    }
    if (h.error && !store) {
      return (
        <StateView title="Menu" onBack={mode === 'app' ? null : leave} icon="Info" role="alert"
          heading="We couldn’t load the menu" body="Check your connection and try again."
          action={<ui.Button size="md" onClick={h.reload}>Try again</ui.Button>} />
      );
    }
    if (!ready) return <LoadingView layout={layout} name={store ? store.name : null} />;

    // ---- chrome ----
    var keep = pick(route.query, CARRY_KEYS);
    var cartLink = OO.router.link('cart', null, { hotel: hotel, query: keep });
    // P3-12: the account hub. POLISH-2 (D-20): none in mode qr (the account is off at a table). WL-5: none on a browse-only
    // menu.
    var profileLink = mode !== 'qr' && mode !== 'kiosk' && !browse ? OO.router.link('account', null, { hotel: hotel, query: keep }) : null;
    var profileHref = profileLink ? profileLink.href : undefined;
    var onProfile = profileLink ? profileLink.onClick : undefined;
    var lines = cart.hotel === str(hotel) ? cart.lines : [];
    var count = OO.cart.count(lines);
    var subtotal = OO.cart.subtotal(lines);

    var atTop = layout !== 'categories' || lv.level === 0;
    // The website's Home navigation replaces the top-level Back button only when that link is present.
    var siteHome = mode === 'online' && ui.OrderingSite && typeof ui.OrderingSite.hasHomeNavigation === 'function'
      && ui.OrderingSite.hasHomeNavigation(route);
    var qrMenu = mode === 'qr' || (mode === 'online' && store.orderingQr && OO.orderingQr && OO.orderingQr.forHotel(hotel));
    var showBack = !!results || !atTop || (mode !== 'app' && mode !== 'kiosk' && !siteHome && !qrMenu);
    var backLabel = results ? 'Clear search' : atTop ? 'Back' : 'Back to ' + (lv.level === 2 && lv.sub ? cap(lv.cat.name) : 'the menu');
    function onBack() {
      if (results) { clearSearch(); return; }
      if (!atTop) { stepUp(route, lv); return; }
      if (mode === 'online' && store.website && historyIdx() === 0) { window.location.assign(store.website); return; }
      leave();
    }

    var brandLogo = mode === 'online' && !OO.landingMode && store.theme && store.theme.whitelabel ? store.theme.logo : null;
    var header;
    if (layout === 'categories' && !wide) {
      var t = browseTitles(store, lv);
      header = (
        <ui.PageHeader title={t.title} subtitle={t.subtitle} logo={atTop ? brandLogo : null} onBack={showBack ? onBack : undefined} showBack={showBack} backLabel={backLabel}
          profileHref={profileHref} onProfile={onProfile} />
      );
    } else {
      header = (
        <ui.StoreHeader name={store.name} logo={brandLogo} address={store.address.full} addressShort={store.address.short} hotel={hotel} store={store}
          onBack={showBack ? onBack : undefined} showBack={showBack} backLabel={backLabel}
          onAbout={function () { aboutS[1](true); }}
          cartCount={browse ? null : count} cartHref={browse ? undefined : cartLink.href} onCart={browse ? undefined : cartLink.onClick}
          profileHref={profileHref} onProfile={onProfile}
          search={wide ? { value: q, onChange: setQ, placeholder: 'Search the menu', inputRef: searchRef } : null} />
      );
    }

    if (market) header = <ui.MarketHeader route={route} store={store} onBack={showBack ? onBack : undefined} backLabel={backLabel} />;
    var pizzaClub = OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(route.chain || hotel);
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign(route.chain || hotel);
    if (pizzaClub) header = <ui.PageHeader title={results ? 'Search results' : 'Menu'} subtitle={store.name} onBack={showBack ? onBack : undefined} showBack={showBack} sticky={false} />;
    if (kitchen) header = null;
    if (zeta) header = showBack ? <ui.PageHeader title={results ? 'Search results' : 'Menu'} onBack={onBack} backLabel={backLabel} sticky={false} /> : null;
    if (storefront && OO.pages.Storefront) header = <OO.pages.Storefront.Header hotel={hotel} store={store} route={route} q={q} setQ={setQ} searchRef={searchRef} onBack={onBack} profileHref={profileHref} onProfile={onProfile} blocked={!!sheet || aboutS[0] || ovlBusyS[0]} onBusy={barBusyS[1]} />;

    var strips = [];
    if (table) strips.push(<Strip key="table" tone="table" icon="Scan">{'Ordering for ' + (table.name || 'table ' + table.id)}</Strip>);
    if (openNow && !openNow.isOpen) strips.push(<Strip key="closed" tone="warn" icon="Clock">{closedText(store, openNow)}</Strip>);
    var stripNode = strips.length ? <div className="oo-menu-strips">{strips}</div> : null;
    // WL-VEL (VEL-C): the website's ordering options (store.whitelabel.ordering, the active surface's: src/lib/whitelabel.jsx),
    // in mode online only (the online host and the landing website's ordering pages), never browse-only, at a table or in
    // the market layout: the order bar under the header (OO.appKit.OrderPrompt's `bar`; the one-shot on-load prompt stays
    // MenuOverlays') and the "Featured items" rail atop the menu (OO.pages.FeaturedRail, in the promo slot).
    var wlOrdering = mode === 'online' && !browse && !table && !market && store.whitelabel && store.whitelabel.ordering || null;
    var Kit = OO.appKit;
    if (mode === 'app' && Kit && Kit.BranchLine) stripNode = <React.Fragment><Kit.BranchLine route={route} hotel={hotel} />{stripNode}</React.Fragment>;
    if (!storefront && wlOrdering && wlOrdering.orderBar === true && Kit && Kit.orderSheet && Kit.orderSheet.ready()) {
      stripNode = <React.Fragment><Kit.OrderPrompt key={'bar|' + hotel} hotel={hotel} store={store} route={route} bar onBusy={barBusyS[1]} />{stripNode}</React.Fragment>;
    }

    var footer = store.settings && store.settings.poweredBy === false ? null : <ui.PoweredBySipo className="oo-menu__powered" />;
    var ctx = { store: store, menu: menu, view: view, display: display, openProduct: openProduct, openCombo: openCombo, tell: tell, kiosk: mode === 'kiosk', market: !!market,
      showAddIcon: !browse && (mode !== 'app' || !!pizzaClub || !!kitchen) && mode !== 'kiosk' && store.showAddIcon === true };
    if (zeta) ctx.zeta = true;
    if (skinOf(route, hotel, mode) === 'boutique' || pizzaClub || zeta || storefront) ctx.natural = true; // WL-VEL: 'Menu' / 'All' in natural case
    var named = display && display.showSelector ? { menus: display.menus, active: display.menuId, onPick: dsp.pick } : null;
    var Promo = OO.pages.MenuBanners;
    var CouponBanner = OO.pages.MenuCouponBanner;
    var promo = browse ? null : <React.Fragment>
      {CouponBanner ? <CouponBanner hotel={hotel} store={store} mode={mode} moment={dsp.moment} /> : null}
      {Promo ? <Promo hotel={hotel} store={store} menu={menu} moment={dsp.moment} /> : null}
    </React.Fragment>;
    // WL-VEL: the "Featured items" rail heads the promo slot (a new element tree only when it shows: React derives useId
    // values from the tree, so a menu without it keeps exactly its markup).
    var Rail = wlOrdering && wlOrdering.featuredRail === 'popular' ? OO.pages.FeaturedRail : null;
    if (Rail && promo) promo = <React.Fragment><Rail hotel={hotel} ctx={ctx} title={wlOrdering.featuredTitle} badge={wlOrdering.featuredBadge} storefront={storefront} />{promo}</React.Fragment>;
    var shared = {
      route: route, view: view, ctx: ctx, wide: wide, header: header, strips: stripNode, footer: footer,
      q: q, setQ: setQ, results: results, clearSearch: clearSearch, named: named, promo: promo, market: !!market,
      storefront: storefront, browse: browse, profileHref: profileHref, onProfile: onProfile,
    };
    var body = kitchen && OO.appKit.KitchenMenu ? <OO.appKit.KitchenMenu {...shared} design={kitchen} resultsView={results ? <Results results={results} ctx={ctx} onClear={clearSearch} /> : null} namedView={<NamedMenus named={named} />} /> : zeta && OO.appKit.ZetaMenu ? <OO.appKit.ZetaMenu {...shared} resultsView={results ? <Results results={results} ctx={ctx} onClear={clearSearch} /> : null} namedView={<NamedMenus named={named} />} /> : mode === 'kiosk' ? <KioskView {...shared} level={lv} count={count} subtotal={subtotal} cartLink={cartLink} /> : market ? <MarketBrowseView {...shared} level={lv} /> : layout === 'sections'
      ? <SectionsView {...shared} rootRef={rootRef} searchRef={searchRef} searchOpen={searchOpen || !!pizzaClub} pizzaClub={!!pizzaClub} setSearchOpen={setSearchOpen} scrollRequest={scrollReqS[0]} />
      : <BrowseView {...shared} level={lv} tileStyle={tileStyle} />;

    var n = resultCount(results);
    var liveText = results ? (n ? n + (market ? (n === 1 ? ' item found' : ' items found') : (n === 1 ? ' dish found' : ' dishes found')) : (market ? 'No items match ' : 'No dishes match ') + results.query) : '';

    var Item = OO.pages.ItemSheet, Combo = OO.pages.ComboBuilder;
    var overlay = null;
    if (!appOrderPending && sheet && sheet.kind === 'product' && Item && browse) {
      overlay = <Item key={'p' + sheet.id} hotel={hotel} store={store} menu={menu} productId={sheet.id} moment={dsp.moment} onClose={closeSheet} browse />;
    } else if (!appOrderPending && sheet && sheet.kind === 'combo' && browse && Item && Item.ComboInfo) {
      overlay = <Item.ComboInfo key={'c' + sheet.id} store={store} menu={menu} comboId={sheet.id} onClose={closeSheet} />;
    } else if (!appOrderPending && sheet && sheet.kind === 'product' && Item) {
      overlay = <Item key={'p' + sheet.id} hotel={hotel} store={store} menu={menu} productId={sheet.id} moment={dsp.moment} onClose={closeSheet} onAdded={onAdded} />;
    } else if (!appOrderPending && sheet && sheet.kind === 'combo' && Combo) {
      overlay = <Combo key={'c' + sheet.id} hotel={hotel} store={store} menu={menu} comboId={sheet.id} moment={dsp.moment} onClose={closeSheet} onAdded={onAdded} />;
    }

    return (
      <div className={'oo-menu oo-menu--' + layout} ref={rootRef}>
        {body}
        {browse || mode === 'kiosk' ? null : <ui.FloatingCartBar count={count} total={money(subtotal, store)} href={cartLink.href} onClick={cartLink.onClick} />}
        <div className="oo-menu-end" aria-hidden="true" />
        <p className="oo-sr-only" role="status">{liveText}</p>
        {overlay}
        {browse ? null : (OO.pages.MenuOverlays ? <OO.pages.MenuOverlays hotel={hotel} store={store} menu={menu} route={route} mode={mode} moment={dsp.moment} blocked={!!overlay || aboutS[0] || barBusyS[0]} onBusy={ovlBusyS[1]} /> : null)}
        {browse ? null : (OO.pages.CouponPrompt ? <OO.pages.CouponPrompt hotel={hotel} store={store} menu={menu} mode={mode} blocked={!!overlay || aboutS[0] || ovlBusyS[0] || barBusyS[0]} /> : null)}
        <AboutSheet open={aboutS[0]} onClose={function () { aboutS[1](false); }} store={store} status={openNow} hotel={hotel} />
        <ui.Toast key={toast ? toast.id : 'none'} open={!!toast} tone={toast ? toast.tone : undefined} onClose={function () { setToast(null); }}>
          {toast ? toast.text : null}
        </ui.Toast>
      </div>
    );
  };
  // W5-FIX #4: the named menu the customer picked on this page (useDisplay's `selected`), or null: the cart page's
  // upsell judges at the menu's moment without a whole-menu display (Cart.shared.upsellMoment reads it).
  function picked(hotel) { var key = str(hotel); return Object.prototype.hasOwnProperty.call(picks, key) ? picks[key] : null; }
  OO.pages.Menu.shared = { orderMoment: orderMoment, useDisplay: useDisplay, picked: picked, itemCards: itemCards };
  OO.pages.Menu.logic = {
    buildView: buildView, linkTarget: linkTarget, notice: notice, searchView: searchView, searchText: searchText, landing: landing,
    searchSeed: searchSeed, sortProducts: sortProducts, sortCategories: sortCategories,
  };
})();


// ===================== src/pages/menu-overlays.jsx =====================
// src/pages/menu-overlays.jsx: OO.pages.MenuOverlays, the menu's own overlays (Batch 2 P2-19 / P2-29), and
// OO.pages.MenuBanners, its banner strip (below). Not routed pages: the Menu (pages/menu.jsx) mounts
// <OO.pages.MenuOverlays hotel store menu route mode moment? onBusy? /> once its store and menu are in (looked up at
// render time; without this file the Menu renders as before). moment = the menu's judging moment (useDisplay's);
// without it the overlays work it out themselves (OO.upsell.useMoment, src/pages/upsell.jsx). onBusy(open) (W3-fix
// #9): called after each change of whether one of these overlays is up (the store alert, a popup, the member card,
// the after-add dialog or the item sheet it opened: AfterAdd's onOpen), and with false on unmount; the menu adds it to
// its coupon prompt's `blocked`, so the prompt never stacks on them.
// Nothing opens on the first render: every overlay opens from an effect or a later event, so the menu's server-side
// render checks see no overlay markup. At most one modal at a time, in this order (REF StoreAlertModal blocks the
// member and coupon prompts):
//   1. Store alert (REF StoreAlertModal; C §3.4): store.alert = { enabled (is_alert), text, image }. Shown when enabled
//      and it has a text or an image, ONCE per browser session per store (sessionStorage 'oo-alert-seen-v1:{hotel}',
//      written when it opens, as REF's alert_seen_{id}). A "Special note" dialog: the image, the text (line breaks
//      kept), "Got it". Waits for the order type and time prompt when the store enables it.
//      App Home shows it too (W3-fix #7; REF's app shell + Home): <OO.pages.MenuOverlays.AlertHost hotel store /> runs
//      the same check with the same session flag, so the alert shows once, on whichever page comes first.
//   2. Popup (data[0].popup; REF ignores popups, the plan makes them optional): the first popup that passes its
//      availability now (logic.livePopups), at most ONE per session per store ('oo-popup-seen-v1:{hotel}'), after the
//      alert. A dialog: the image, its button (button_name, default "Find out more") following its link, else "Got it".
//   3. Member-login prompt (REF useMembershipLoginPrompt + MembershipLoginPrompt; C §3.1): online mode only, when
//      store.settings.memberLoginPrompt (getRestaurant member_login_prompt) is on, the customer is a guest, this
//      store's cart is empty and neither the alert nor a popup is open: 1.8 s later, unless a dialog is up then (the
//      item sheet, About, the upsell: it waits another 1.8 s), a non-modal card "Are you a member?" opens. Once per
//      session per store and mode ('oo-member-prompt-v1:{mode}:{hotel}' = shown | dismissed | sign-in). Sign in →
//      the profile page's email login (?directLogin=true); the close dismisses it; it also goes when a condition stops
//      holding (an item in the cart, a sign-in).
//   4. The after-add upsell dialog (P2-19): <OO.pages.Upsell.AfterAdd> (src/pages/upsell.jsx), looked up at render time.
// Missing data stays quiet: a store without the alert, popups or the member flag (the normaliser not yet reading one)
// shows none of them, and banners / popups without availability rules are always on.
//
// <OO.pages.MenuBanners hotel store menu? moment? /> the menu's "Featured offers" strip (REF
// OnlineOrderBannerSection, on its desktop menu and mobile home; the Menu decides whether and where to mount it). The
// live banners with an image (REF needs one), in API order, as a scroll-snap row of 16:7 tiles (two per row on
// desktop); a tap opens the full image with its title and subtitle and a button following its link (the PROPOSED cta,
// default "View offer"; REF's preview modal). No link, no button. Nothing when no banner is live.
//
// OO.pages.MenuOverlays.logic (pure; tests/overlays-p229.test.mjs):
//   alertOf(store) → null | { text, image }: the alert to show (enabled, and a text or an image).
//   rulesOk(rules, store, moment) → bool: availability rules (OO.model.parseRules shape, or raw) at moment.now in
//       moment.domain, on the store clock (OO.model.isAvailable). No rules (none sent, or the normaliser not reading
//       them) → true. No moment → now in the pickup domain of the mode.
//   liveBanners(store, moment) → store.banners passing rulesOk(banner.rules) (the normaliser already dropped the
//       disabled and the server-inactive ones: enabled && is_active). REF useAvailableRestaurantBanners.
//   livePopups(store, moment) → store.popups, the same way.
//   memberPromptDue({ store, mode, signedIn, cartCount, blocked }) → bool: the prompt's conditions above, bar the delay.
//   linkTarget(link, hotel, here?) → null | Target: a banner / popup link (REF restaurantBanner.ts, plus this SPA's short
//       forms). here = { host, routeBase, mode } (default: the page's). In order:
//         ''                                        → null
//         'rewards', '/menu', 'cart' (an in-app page) → { kind: 'route', page, params, query } (OO.model.resolveLink)
//         'product&id=5', '?type=combo&id=9', 'type=category&id=2', 'cart&…' → { kind: 'deep', type, id } (cart: id null;
//           a type without a valid id is just the menu: { kind: 'route', page: 'menu', params: {}, query: {} })
//         '//x.co/a'                                → https://x.co/a, then as a URL
//         another scheme (javascript:, mailto:, tel:, data:) → null
//         a bare domain 'shop.co.nz[/…]'            → https://shop.co.nz/…, then as a URL
//         '/{path}' (this host)                     → as a same-host URL
//         a URL on THIS host: a route of this SPA (OO.router.parse under routeBase) → the deep link when it names this
//           store with ?type=…&id=… (or the shorthand ?product&id=…), else { kind: 'internal', href, inApp: true }; a
//           path outside the SPA → { kind: 'internal', href, inApp: false } (a full page load)
//         a URL on another host: this store's deep link (its first path segment, decoded, is the hotel; REF
//           matchesStoreSlug) → { kind: 'deep', … } (opens here); anything else → { kind: 'url', href } (a new tab)
//         anything else (spaces, junk)              → null
//   targetOf(entry, hotel, here?) → linkTarget(entry.linkRaw) when the entry carries the raw link (banners; popups
//       once the normaliser keeps it), else the normalised entry.link ({ kind: 'url', href } | { kind: 'route', … }).
//   hrefOf(target, hotel) → the href (deep: the menu with ?type&id, or the cart; route: OO.router.href).
//   follow(target, hotel) → navigates: in-app for deep / route / internal inApp (OO.router.go), a full load for an
//       internal path outside the SPA, a new tab (noopener) for a URL.
//   __reset() (tests) forgets the in-memory copies of the session flags.
// OO.pages.MenuOverlays.parts = { StoreAlert, Popup, MemberPrompt, BannerPreview, LinkButton } (presentational: the
//   390 / 1440 render checks use them).
// <OO.pages.MenuOverlays.AlertHost hotel store /> the store alert alone (1. above), for app Home: nothing on its first
//   render; the dialog once the check (an effect) finds an alert not yet seen this session for the store.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var PROMPT_DELAY_MS = 1800; // REF PROMPT_DELAY_MS
  var ALERT_KEY = 'oo-alert-seen-v1:';
  var POPUP_KEY = 'oo-popup-seen-v1:';
  var MEMBER_KEY = 'oo-member-prompt-v1:';
  var DEEP_TYPES = ['product', 'combo', 'category', 'cart'];

  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function decodeOr(s) { try { return decodeURIComponent(s); } catch (e) { return s; } }
  function boot() { return window.__SIPO_BOOT || {}; }
  function plainClick(e) { return !e || !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }

  // ---- session flags: sessionStorage, plus an in-memory copy (REF storageFallbackSeenKeys) ---------------------------
  var mem = {};
  function session() { try { return window.sessionStorage || null; } catch (e) { return null; } }
  function seen(key) {
    if (mem[key]) return true;
    try { var s = session(); return !!(s && s.getItem(key) !== null); } catch (e) { return false; }
  }
  function remember(key, value) {
    mem[key] = true;
    try { var s = session(); if (s) s.setItem(key, value || '1'); } catch (e) { /* private mode: the memory copy holds */ }
  }
  // true the first time in this session (and from then on false).
  function once(key) {
    if (seen(key)) return false;
    remember(key, '1');
    return true;
  }

  // ---- logic ------------------------------------------------------------------------------------------------------
  function alertOf(store) {
    var a = store && store.alert;
    if (!a || !a.enabled) return null;
    var text = str(a.text).trim(), image = str(a.image).trim();
    return text || image ? { text: text || null, image: image || null } : null;
  }
  function rulesOk(rules, store, moment) {
    if (rules === undefined || rules === null || (Array.isArray(rules) && !rules.length)) return true;
    var m = moment || {};
    var dom = m.domain || OO.model.domain('PICKUP', OO.mode);
    return OO.model.isAvailable(rules, OO.model.storeNow(store, m.now), dom);
  }
  function live(list, store, moment) {
    return arr(list).filter(function (x) { return x && rulesOk(x.rules, store, moment); });
  }
  function liveBanners(store, moment) { return store ? live(store.banners, store, moment) : []; }
  function liveAppSlides(store, moment) {
    return store ? live(Array.isArray(store.appSlides) ? store.appSlides : store.banners, store, moment) : [];
  }
  function livePopups(store, moment) { return store ? live(store.popups, store, moment) : []; }
  function memberPromptDue(o) {
    o = o || {};
    var s = o.store && o.store.settings;
    return !!(s && s.memberLoginPrompt === true) && o.mode === 'online' && !o.signedIn && !(o.cartCount > 0) && !o.blocked;
  }

  // ---- links (REF restaurantBanner.ts + parseDeepLinkParams) --------------------------------------------------------
  function deepOf(query) {
    var q = query || {};
    var type = str(q.type).toLowerCase().trim();
    if (!type) {
      Object.keys(q).some(function (k) {
        var n = k.toLowerCase().trim();
        if (DEEP_TYPES.indexOf(n) < 0) return false;
        type = n;
        return true;
      });
    }
    if (DEEP_TYPES.indexOf(type) < 0) return null;
    if (type === 'cart') return { kind: 'deep', type: 'cart', id: null };
    var id = Number(str(q.id).trim());
    return isFinite(id) && id > 0 ? { kind: 'deep', type: type, id: str(q.id).trim() } : null;
  }
  // OO.model.resolveLink's route → a deep link (the menu with a valid ?type&id), else the page (the menu without a
  // deep link's leftovers: 'combo&id=0' is just the menu).
  function routeOf(r) {
    if (r.page === 'menu') return deepOf(r.query) || { kind: 'route', page: 'menu', params: {}, query: {} };
    return { kind: 'route', page: r.page, params: r.params || {}, query: r.query || {} };
  }
  function hereOf(here) {
    var h = here || {};
    var l = window.location || {};
    var b = boot();
    return {
      host: h.host !== undefined ? str(h.host) : str(l.host),
      routeBase: h.routeBase !== undefined ? h.routeBase : b.routeBase,
      mode: h.mode !== undefined ? h.mode : b.mode || OO.mode,
    };
  }
  // A path on this host: a route of this SPA (in-app, or this store's deep link), or another page of the site.
  function internalOf(path, hotel, h) {
    var m = /^([^?#]*)(\?[^#]*)?(#.*)?$/.exec(path) || [path, path, '', ''];
    var r = OO.router.parse(m[1] || '/', m[2] || '', h.routeBase, h.mode);
    if (r && r.hotel !== null && r.hotel !== undefined && r.page !== 'not-found') {
      var d = r.hotel === hotel ? deepOf(r.query) : null;
      return d || { kind: 'internal', href: path, inApp: true };
    }
    return { kind: 'internal', href: path, inApp: false };
  }
  function linkTarget(link, hotel, here) {
    var s = str(link).trim();
    if (!s || s.toLowerCase() === 'null') return null;
    var key = str(hotel);
    var h = hereOf(here);
    var href;
    if (s.indexOf('//') === 0) href = 'https:' + s;
    else if (/^[a-z][a-z\d+.-]*:/i.test(s)) {
      if (!/^https?:/i.test(s)) return null; // javascript:, mailto:, tel:, data: … never
      href = s;
    } else {
      // This SPA's short forms first: an in-app page ('rewards') or a deep link ('product&id=5', '?type=combo&id=9').
      var r = OO.model.resolveLink(s);
      if (r && r.kind === 'route') return routeOf(r);
      if (s.charAt(0) === '/') return internalOf(s, key, h);
      if (/^[^\s/?#:@]+\.[a-z]{2,}(?::\d+)?(?:[/?#]\S*)?$/i.test(s)) href = 'https://' + s; // a bare domain (REF)
      else return null;
    }
    var u;
    try { u = new URL(href); } catch (e) { return null; }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (h.host && u.host === h.host) return internalOf(u.pathname + u.search + u.hash, key, h);
    var first = u.pathname.split('/').filter(Boolean)[0];
    if (key && first && decodeOr(first) === key) {
      var dl = deepOf(OO.router.parseQuery(u.search));
      if (dl) return dl;
    }
    return { kind: 'url', href: u.toString() };
  }
  function targetOf(entry, hotel, here) {
    if (!entry) return null;
    if (typeof entry.linkRaw === 'string') return linkTarget(entry.linkRaw, hotel, here);
    var l = entry.link;
    if (!l || typeof l !== 'object') return null;
    if (l.kind === 'url') return linkTarget(l.href, hotel, here);
    return l.kind === 'route' ? routeOf(l) : null;
  }
  function hrefOf(t, hotel) {
    if (!t) return null;
    var o = { hotel: hotel };
    if (t.kind === 'deep') {
      if (t.type === 'cart') return OO.router.href('cart', null, o);
      o.query = { type: t.type, id: t.id };
      return OO.router.href('menu', null, o);
    }
    if (t.kind === 'route') {
      if (t.query && Object.keys(t.query).length) o.query = t.query;
      return OO.router.href(t.page, t.params || {}, o);
    }
    return t.href || null;
  }
  function follow(t, hotel) {
    var href = hrefOf(t, hotel);
    if (!href) return;
    if (t.kind === 'url') { try { window.open(href, '_blank', 'noopener'); } catch (e) { /* a blocked popup: nothing */ } return; }
    if (t.kind === 'internal' && !t.inApp) { window.location.assign(href); return; }
    OO.router.go(href);
  }

  // ---- presentation ---------------------------------------------------------------------------------------------
  // A block button following a link target: a URL opens a new tab (noopener), a path outside the SPA loads it, the rest
  // navigate in-app. onDone runs first (the dialog closes).
  function LinkButton(props) {
    var ui = OO.ui;
    var t = props.target, href = hrefOf(t, props.hotel);
    if (!href) return null;
    if (t.kind === 'url') {
      return <ui.Button block href={href} target="_blank" rel="noopener noreferrer" iconRight="ChevronRight" onClick={props.onDone}>{props.label}</ui.Button>;
    }
    if (t.kind === 'internal' && !t.inApp) return <ui.Button block href={href} onClick={props.onDone}>{props.label}</ui.Button>;
    return (
      <ui.Button block href={href} onClick={function (e) {
        if (!plainClick(e)) return;
        if (e && e.preventDefault) e.preventDefault();
        if (props.onDone) props.onDone();
        OO.router.go(href);
      }}>{props.label}</ui.Button>
    );
  }

  // props: alert ({ text, image }), onClose.
  function StoreAlert(props) {
    var ui = OO.ui, a = props.alert;
    return (
      <ui.Sheet open variant="dialog" title="Special note" onClose={props.onClose} className="oo-ovl-dialog oo-ovl-alert"
        footer={<ui.Button block onClick={props.onClose}>Got it</ui.Button>}>
        {a.image ? <img className="oo-ovl-dialog__img" src={a.image} alt={a.text ? '' : 'A note from the store'} /> : null}
        {a.text ? <p className="oo-ovl-alert__text">{a.text}</p> : null}
      </ui.Sheet>
    );
  }

  // props: popup ({ image, text, buttonName, link, linkRaw? }), hotel, onClose.
  function Popup(props) {
    var ui = OO.ui, p = props.popup;
    var t = targetOf(p, props.hotel);
    var cta = t ? <LinkButton target={t} hotel={props.hotel} label={p.buttonName || 'Find out more'} onDone={props.onClose} /> : null;
    return (
      <ui.Sheet open variant="dialog" title={p.text || 'Featured offer'} onClose={props.onClose} className="oo-ovl-dialog oo-ovl-popup"
        footer={cta || <ui.Button block onClick={props.onClose}>Got it</ui.Button>}>
        {p.image ? <img className="oo-ovl-dialog__img" src={p.image} alt="" /> : null}
      </ui.Sheet>
    );
  }

  // The non-modal member card (REF MembershipLoginPrompt). props: hotel, onSignIn(e), onDismiss.
  function MemberPrompt(props) {
    var ui = OO.ui;
    var id = 'oo-mp' + React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var href = OO.router.href('profile', null, { hotel: props.hotel, query: { directLogin: 'true' } });
    return (
      <aside className="oo-mprompt" aria-labelledby={id + '-t'} aria-describedby={id + '-d'} aria-live="polite" data-oo-member-prompt="">
        <div className="oo-mprompt__text">
          <h2 className="oo-mprompt__title" id={id + '-t'}>Are you a member?</h2>
          <p className="oo-mprompt__body" id={id + '-d'}>Sign in to see your member prices, rewards and exclusive offers.</p>
        </div>
        <ui.IconButton icon="Close" variant="ghost" label="Dismiss the member sign-in prompt" className="oo-mprompt__close" onClick={props.onDismiss} />
        <ui.Button size="sm" icon="User" className="oo-mprompt__btn" href={href} onClick={props.onSignIn}>Sign in</ui.Button>
      </aside>
    );
  }

  // A banner's full image (REF's preview modal). props: banner, hotel, onClose.
  function BannerPreview(props) {
    var ui = OO.ui, b = props.banner;
    var t = targetOf(b, props.hotel);
    return (
      <ui.Sheet open variant="dialog" title={b.title || 'Featured offer'} onClose={props.onClose} className="oo-ovl-dialog oo-ovl-preview"
        footer={t ? <LinkButton target={t} hotel={props.hotel} label={b.cta || 'View offer'} onDone={props.onClose} /> : null}>
        <img className="oo-ovl-dialog__img" src={b.image} alt="" />
        {b.subtitle ? <p className="oo-ovl-preview__sub">{b.subtitle}</p> : null}
      </ui.Sheet>
    );
  }

  function useMoment(hotel, store, menu, given) {
    var U = OO.upsell;
    return U && typeof U.useMoment === 'function' ? U.useMoment(hotel, store, menu, given) : given || null;
  }
  // Any dialog up (the item sheet, About, an upsell, a store dialog)? Read when the prompt's timer fires.
  function sheetOpen() {
    try { return !!(typeof document !== 'undefined' && document.querySelector && document.querySelector('.oo-sheet-root')); } catch (e) { return false; }
  }

  // 1. The store alert, once per session per store (MenuOverlays and AlertHost share it): { checked (the hotel whose
  // alert was checked on this mount: the popup waits for it), alert (on show) | null, close() }.
  function useStoreAlert(hotel, store, blocked) {
    var checkedS = React.useState(null);
    var alertS = React.useState(null);
    React.useEffect(function () {
      if (!store || !hotel || blocked || checkedS[0] === hotel) return;
      checkedS[1](hotel);
      var a = alertOf(store);
      if (a && once(ALERT_KEY + hotel)) alertS[1](a);
    }, [hotel, store, blocked]);
    return { checked: checkedS[0], alert: alertS[0], close: function () { alertS[1](null); } };
  }

  OO.pages.MenuOverlays = function MenuOverlays(props) {
    var hotel = str(props.hotel), store = props.store, menu = props.menu;
    var mode = props.mode || OO.mode;
    var moment = useMoment(props.hotel, store, menu, props.moment);
    var signedIn = OO.useStore(function (s) { return !!(s.session && s.session.customer); });
    var cartCount = OO.useStore(function (s) { return s.hotel === hotel && OO.cart ? OO.cart.count(s.cart.lines) : 0; });
    var kit = OO.appKit, orderKit = kit && kit.orderSheet;
    var appOrdering = mode === 'app';
    var orderEnabled = (appOrdering || (mode === 'online' && !!(store && store.settings && store.settings.orderPrompt)))
      && orderKit && orderKit.ready() && !orderKit.atTable(props.route || OO.router.current(), hotel, OO.store.table());
    var orderBusyS = React.useState(null);
    var onOrderBusy = React.useCallback(function (busy) { orderBusyS[1]({ hotel: hotel, busy: busy }); }, [hotel]);
    // Treat the prompt as pending until its first render reports otherwise: alerts must not race its opening effect.
    var orderBusy = !!orderEnabled && (!orderBusyS[0] || orderBusyS[0].hotel !== hotel || orderBusyS[0].busy);
    var al = useStoreAlert(hotel, store, orderBusy || props.blocked);
    var popupCheckedS = React.useState(null); // the hotel whose popups were checked on this mount
    var popupS = React.useState(null);        // the popup on show
    var promptS = React.useState(false);
    var upsellS = React.useState(false);      // the after-add dialog (or the item sheet it opened) is up

    // 2. Then one popup per session per store, the first that is live now.
    React.useEffect(function () {
      if (!store || !hotel || orderBusy || props.blocked || al.checked !== hotel || al.alert || popupS[0] || popupCheckedS[0] === hotel) return;
      popupCheckedS[1](hotel);
      var list = livePopups(store, moment);
      if (list.length && once(POPUP_KEY + hotel)) popupS[1](list[0]);
    }, [hotel, store, orderBusy, props.blocked, al.checked, al.alert, popupS[0], moment]);
    // 3. The member-login prompt, 1.8 s after its conditions hold (again while a dialog is up), once per session.
    var due = memberPromptDue({ store: store, mode: mode, signedIn: signedIn, cartCount: cartCount, blocked: orderBusy || props.blocked || !!al.alert || !!popupS[0] });
    var promptKey = MEMBER_KEY + mode + ':' + hotel;
    React.useEffect(function () {
      if (!due || seen(promptKey)) return undefined;
      var timer = 0;
      function arm() {
        timer = setTimeout(function () {
          if (sheetOpen()) { arm(); return; }
          remember(promptKey, 'shown');
          promptS[1](true);
        }, PROMPT_DELAY_MS);
      }
      arm();
      return function () { clearTimeout(timer); };
    }, [due, promptKey]);
    // 5. The busy signal (onBusy, see the header): any of the above up.
    var busy = orderBusy || !!al.alert || !!popupS[0] || !!(promptS[0] && due) || upsellS[0];
    var onBusy = React.useRef(props.onBusy);
    onBusy.current = props.onBusy;
    React.useEffect(function () { if (typeof onBusy.current === 'function') onBusy.current(busy); }, [busy]);
    React.useEffect(function () {
      return function () { if (typeof onBusy.current === 'function') onBusy.current(false); };
    }, []);

    function signIn(e) {
      remember(promptKey, 'sign-in');
      promptS[1](false);
      if (!plainClick(e)) return;
      if (e && e.preventDefault) e.preventDefault();
      OO.router.go(OO.router.href('profile', null, { hotel: hotel, query: { directLogin: 'true' } }));
    }
    function dismissPrompt() { remember(promptKey, 'dismissed'); promptS[1](false); }

    var U = OO.pages.Upsell;
    var AfterAdd = U && U.AfterAdd;
    return (
      <React.Fragment>
        {orderEnabled ? <kit.OrderPrompt key={hotel} hotel={hotel} store={store} route={props.route} onLoad={!appOrdering}
          blocked={props.blocked || !!al.alert || !!popupS[0] || upsellS[0]} onBusy={onOrderBusy} /> : null}
        {al.alert ? <StoreAlert alert={al.alert} onClose={al.close} /> : null}
        {!al.alert && popupS[0] ? <Popup popup={popupS[0]} hotel={hotel} onClose={function () { popupS[1](null); }} /> : null}
        {promptS[0] && due ? <MemberPrompt hotel={hotel} onSignIn={signIn} onDismiss={dismissPrompt} /> : null}
        {AfterAdd ? <AfterAdd hotel={props.hotel} store={store} menu={menu} moment={moment} onOpen={upsellS[1]} /> : null}
      </React.Fragment>
    );
  };

  // ---- <OO.pages.MenuOverlays.AlertHost> (app Home, W3-fix #7; see the header) ------------------------------------
  OO.pages.MenuOverlays.AlertHost = function AlertHost(props) {
    var al = useStoreAlert(str(props.hotel), props.store);
    return al.alert ? <StoreAlert alert={al.alert} onClose={al.close} /> : null;
  };

  // ---- <OO.pages.MenuBanners> (the menu's "Featured offers") ------------------------------------------------------
  OO.pages.MenuBanners = function MenuBanners(props) {
    var hotel = str(props.hotel), store = props.store;
    var moment = useMoment(props.hotel, store, props.menu, props.moment);
    var previewS = React.useState(null);
    var headingId = 'oo-ovl-bn' + React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var list = liveBanners(store, moment).filter(function (b) { return !!b.image; });
    if (!list.length) return null;
    return (
      <section className="oo-ovl-banners" aria-labelledby={headingId}>
        <OO.ui.SectionTitle id={headingId} className="oo-ovl-banners__title" sub={list.length > 1 ? list.length + ' offers' : null}>Featured offers</OO.ui.SectionTitle>
        <ul className="oo-ovl-banners__track">
          {list.map(function (b, i) {
            return (
              <li key={b.id || i} className="oo-ovl-banners__item">
                <button type="button" className="oo-ovl-banner" aria-label={'Open ' + (b.title || 'featured offer ' + (i + 1))}
                  aria-haspopup="dialog" onClick={function () { previewS[1](b); }}>
                  <img className="oo-ovl-banner__img" src={b.image} alt="" loading={i ? 'lazy' : undefined} />
                  {b.title ? <span className="oo-ovl-banner__text">{b.title}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
        {previewS[0] ? <BannerPreview banner={previewS[0]} hotel={hotel} onClose={function () { previewS[1](null); }} /> : null}
      </section>
    );
  };

  OO.pages.MenuOverlays.logic = {
    alertOf: alertOf,
    rulesOk: rulesOk,
    liveBanners: liveBanners,
    liveAppSlides: liveAppSlides,
    livePopups: livePopups,
    memberPromptDue: memberPromptDue,
    linkTarget: linkTarget,
    targetOf: targetOf,
    hrefOf: hrefOf,
    follow: follow,
    __reset: function () { mem = {}; },
  };
  OO.pages.MenuOverlays.parts = { StoreAlert: StoreAlert, Popup: Popup, MemberPrompt: MemberPrompt, BannerPreview: BannerPreview, LinkButton: LinkButton };
})();


// ===================== src/pages/menu-featured.jsx =====================
// src/pages/menu-featured.jsx: WL-VEL (lane VEL-C), OO.pages.FeaturedRail: the "Featured items" rail atop the online
// menu (scratchpad SPEC §1.4 layout.featuredRail 'popular'; the active surface's, store.whitelabel.ordering). src/pages/
// menu.jsx mounts it at the head of the menu (the sections and the category grid's level 0, never search results), in
// mode online only (the online host and the landing website's ordering pages), never on a browse-only menu, at a table,
// in the kiosk or the App.
//
// <OO.pages.FeaturedRail hotel ctx title badge />
//   ctx    the menu's card context (src/pages/menu.jsx: store, menu, view, display, openProduct)
//   title  the heading (layout.featuredTitle; default "Featured items") · badge: the pill on every photo
//          (layout.featuredBadge; default "Popular")
// Data: the store's popular dishes (OO.api.getPopular, the source of the App Home's Popular), as products of the menu on
// show (OO.model.popularProducts), kept only when the menu shows them orderable right now: in the judged view (a named
// menu, a table's exclusions) and neither hidden nor greyed by the D-07 display. None (or a failed read): nothing.
// Cards (buttons) open the item sheet (ctx.openProduct); each has the photo (4:3; a placeholder without one), the badge,
// the name and the price ("From $x" when the sizes' prices differ). The track scrolls sideways with snap points; the round
// previous / next buttons scroll it by about a card row and are disabled at either end (smooth unless reduced motion).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var TITLE = 'Featured items';
  var BADGE = 'Popular';
  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function money(n, store) { return OO.format.money(n, store ? store.currency : undefined); }
  function reduced() { try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }

  // The product ids the judged view shows (its categories and their sub-categories).
  function shownIds(view) {
    var ids = Object.create(null);
    ((view && view.categories) || []).forEach(function (c) {
      (c.productIds || []).forEach(function (id) { ids[str(id)] = true; });
      (c.subcategories || []).forEach(function (s) { (s.productIds || []).forEach(function (id) { ids[str(id)] = true; }); });
    });
    return ids;
  }
  // The rail's products: popular, on the view, orderable now (see the header).
  function railProducts(raw, ctx) {
    if (!raw || !ctx || !ctx.menu) return [];
    var shown = shownIds(ctx.view);
    var display = ctx.display && ctx.display.products;
    return OO.model.popularProducts(raw, ctx.menu).filter(function (p) {
      var d = display && display[p.id];
      return !!shown[str(p.id)] && (d ? d.show === 'normal' : p.available !== false && !p.soldOut);
    });
  }
  function priceText(p, store) {
    var L = OO.landing;
    var pr = L && typeof L.priceOf === 'function' ? L.priceOf(p) : { amount: p.price, from: false };
    if (pr.amount === null || pr.amount === undefined) return money(p.price, store);
    return (pr.from ? 'From ' : '') + money(pr.amount, store);
  }

  function Card(props) {
    var p = props.product;
    var failed = React.useState(false);
    var img = p.image && !failed[0];
    return (
      <li className="oo-frail__item">
        <button type="button" className="oo-frail__card" onClick={props.onOpen}>
          <span className="oo-frail__media">
            {img ? <OO.ui.Image cover src={p.image} sizes={props.storefront ? '(min-width: 900px) 300px, 220px' : '220px'} alt="" loading="lazy" onError={function () { failed[1](true); }} /> : props.storefront ? <span className="oo-sf-no-photo">No photo</span> : <OO.icons.ImagePlaceholder size={26} />}
            {props.badge ? <em className="oo-frail__badge">{props.badge}</em> : null}
          </span>
          <strong className="oo-frail__name">{p.name}</strong>
          <small className="oo-frail__price">{props.price}</small>
        </button>
      </li>
    );
  }

  function FeaturedRail(props) {
    var ctx = props.ctx, hotel = props.hotel;
    var q = OO.useAsync(function () {
      return OO.api && typeof OO.api.getPopular === 'function' ? OO.api.getPopular(hotel) : Promise.resolve(null);
    }, [hotel]);
    var list = React.useMemo(function () { return railProducts(q.data, ctx); }, [q.data, ctx.menu, ctx.view, ctx.display]);
    var trackRef = React.useRef(null);
    var endsS = React.useState({ start: true, end: false });
    var ends = endsS[0];
    function measure() {
      var t = trackRef.current;
      if (!t) return;
      var start = t.scrollLeft <= 2, end = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
      endsS[1](function (o) { return o.start === start && o.end === end ? o : { start: start, end: end }; });
    }
    React.useEffect(function () {
      measure();
      if (typeof window.addEventListener !== 'function') return undefined;
      window.addEventListener('resize', measure);
      return function () { window.removeEventListener('resize', measure); };
    }, [list.length]);
    if (!list.length) return null;
    function step(dir) {
      var t = trackRef.current;
      if (!t) return;
      var by = Math.max(200, Math.round(t.clientWidth * 0.8)) * dir;
      try { t.scrollBy({ left: by, behavior: reduced() ? 'auto' : 'smooth' }); } catch (e) { t.scrollLeft += by; }
    }
    var id = 'oo-frail-' + str(hotel).replace(/[^A-Za-z0-9_-]/g, '');
    var title = props.title || TITLE;
    var badge = props.badge || BADGE;
    return (
      <section className="oo-frail" aria-labelledby={id}>
        <div className="oo-frail__head">
          <h2 className="oo-frail__title" id={id}>{title}</h2>
          <div className="oo-frail__arrows">
            <button type="button" className="oo-frail__arrow" aria-label={'Previous ' + title.toLowerCase()} disabled={ends.start} onClick={function () { step(-1); }}>
              <OO.icons.ChevronLeft size={18} />
            </button>
            <button type="button" className="oo-frail__arrow" aria-label={'Next ' + title.toLowerCase()} disabled={ends.end} onClick={function () { step(1); }}>
              <OO.icons.ChevronRight size={18} />
            </button>
          </div>
        </div>
        <ul className="oo-frail__track" ref={trackRef} onScroll={measure}>
          {list.map(function (p) {
            return <Card key={p.id} product={p} badge={badge} price={props.storefront ? money(p.price, ctx.store) : priceText(p, ctx.store)} storefront={props.storefront} onOpen={function () { ctx.openProduct(p); }} />;
          })}
        </ul>
      </section>
    );
  }
  FeaturedRail.logic = { railProducts: railProducts, priceText: priceText, TITLE: TITLE, BADGE: BADGE };
  OO.pages.FeaturedRail = FeaturedRail;
})();


// ===================== src/pages/menu-storefront.jsx =====================
// Optional online Storefront presentation. Menu judging, navigation, item sheets and order details stay shared.
(function () {
  'use strict';
  var OO = window.OO = window.OO || {};
  OO.pages = OO.pages || {};

  function Header(props) {
    var s = props.store, K = OO.appKit;
    var logo = s.theme && s.theme.logo || s.storeLogoUrl || s.logoUrl;
    var failed = React.useState(false);
    var searchOpen = React.useState(false);
    var menu = OO.router.link('menu', null, { hotel: props.hotel });
    function content(p) {
      var status = p && p.status, mine = status && status.mine;
      var type = mine && mine.orderType || status && status.type || 'PICKUP';
      var types = status ? status.types : [];
      var where = type === 'DELIVERY' && mine && mine.address ? mine.address : s.address.full || s.address.short;
      var summary = p && p.summary && p.summary.title || 'Choose order details';
      var when = summary.replace(/^(Pickup|Delivery|Dine-in)\s*·\s*/, '');
      if (status && status.need !== 'fresh') when = status.pickers && status.pickers[type] && status.pickers[type].asap && status.pickers[type].asap.ok ? 'ASAP' : 'Choose a time';
      return <header className={'oo-sf-header' + (searchOpen[0] ? ' is-searching' : '')}>
        {props.onBack ? <button type="button" className="oo-sf-header__back" aria-label="Back to website" onClick={props.onBack}><OO.icons.ArrowLeft size={20} /></button> : null}
        <a className="oo-sf-header__brand" href={menu.href} onClick={menu.onClick} aria-label={s.name + ' menu'}>
          {logo && !failed[0] ? <img src={logo} alt="" onError={function () { failed[1](true); }} /> : <strong>{s.theme && s.theme.brand || s.name}</strong>}
        </a>
        {p ? <React.Fragment>
          <div className="oo-sf-header__types" role="group" aria-label="Order type">
            {['DELIVERY', 'PICKUP', 'DINEIN'].filter(function (t) { return types.indexOf(t) >= 0; }).map(function (t) {
              return <button key={t} type="button" aria-pressed={type === t} onClick={function () { p.onType(t); }}>{t === 'DELIVERY' ? 'Delivery' : t === 'PICKUP' ? 'Pickup' : 'Dine-in'}</button>;
            })}
          </div>
          <button type="button" className="oo-sf-header__details" aria-haspopup="dialog" aria-label={'Change order details, ' + summary} onClick={p.onOpen}>
            <OO.icons.Pin size={17} /><span className="oo-sf-header__address">{where}</span><span className="oo-sf-header__when">· {when}</span>
          </button>
        </React.Fragment> : null}
        <div className="oo-sf-header__search"><OO.ui.SearchField value={props.q} onChange={props.setQ} placeholder="Search the menu" inputRef={props.searchRef} /></div>
        <button type="button" className="oo-sf-header__search-button" aria-label="Search the menu" aria-expanded={searchOpen[0]} onClick={function () { searchOpen[1](!searchOpen[0]); }}><OO.ui.Icon name={searchOpen[0] ? 'Close' : 'Search'} size={20} /></button>
        {props.profileHref ? <a className="oo-sf-header__account" href={props.profileHref} onClick={props.onProfile} aria-label="Sign in"><OO.icons.User size={19} /></a> : null}
      </header>;
    }
    return K && K.orderSheet && K.orderSheet.ready() ? <K.OrderPrompt hotel={props.hotel} store={s} route={props.route} summary manualOnly blocked={props.blocked} onBusy={props.onBusy} renderSummary={content} /> : content(null);
  }

  function Banner(props) {
    var failed = React.useState(false), s = props.store;
    var site = OO.whitelabel && OO.whitelabel.siteFor(s.onlineKey);
    var hero = site && site.doc && site.doc.app && site.doc.app.hero;
    var image = hero && hero.image || s.bannerUrl;
    if (!image || failed[0]) return <h1 className="oo-sr-only">{s.name} menu</h1>;
    return <section className="oo-sf-banner" aria-label={s.name + ' banner'}>
      <h1 className="oo-sr-only">{s.name} menu</h1>
      <img src={image} alt={s.name} fetchpriority="high" onError={function () { failed[1](true); }} />
    </section>;
  }

  function Savings(props) {
    var link = OO.router.link('deals', null, { hotel: props.hotel });
    return <section className="oo-sf-savings" aria-labelledby="oo-sf-savings-title">
      <h2 id="oo-sf-savings-title">Savings and more</h2>
      <a href={link.href} onClick={link.onClick}><span><strong>Explore deals</strong><small>Discover offers from this store</small></span><span className="oo-sf-savings__icon" aria-hidden="true">%</span></a>
    </section>;
  }

  function Footer(props) {
    var s = props.store;
    function link(page, label, params) {
      var l = OO.router.link(page, params && params.kind === 'use' ? { kind: 'terms' } : params || null, { hotel: props.hotel, hash: params && params.kind === 'use' ? 'terms-of-use' : undefined });
      return <a key={label} href={l.href} onClick={l.onClick}>{label}</a>;
    }
    return <footer className="oo-sf-footer">
      <div className="oo-sf-footer__columns">
        <div><h2>{s.name}</h2><p>Online ordering</p></div>
        <div><h3>Visit and contact</h3>{s.address.full ? <a href={s.mapLink || 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(s.address.full)} target="_blank" rel="noopener noreferrer">{s.address.full}</a> : null}{s.phone ? <a href={'tel:' + s.phone.replace(/[^\d+]/g, '')}>{s.phone}</a> : null}{s.website ? <a href={s.website} target="_blank" rel="noopener noreferrer">{s.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a> : null}</div>
        <nav aria-label="Footer navigation"><h3>Explore</h3>{link('menu', 'Menu')}{link('deals', 'Deals')}{link('track', 'Track')}{link('coupons', 'Coupons')}{link('about', 'About')}</nav>
        <div><h3>Information</h3>{link('legal', 'Terms and conditions', { kind: 'terms' })}{link('legal', 'Privacy policy', { kind: 'privacy' })}{link('legal', 'Terms of use', { kind: 'use' })}{link('legal', 'Allergy information', { kind: 'allergy' })}</div>
        {s.settings && s.settings.poweredBy === false ? null : <div className="oo-sf-footer__partner"><OO.ui.PoweredBySipo /></div>}
      </div>
      <div className="oo-sf-footer__copyright">© {new Date().getFullYear()} {s.name}<span>© {new Date().getFullYear()} SIPO</span></div>
    </footer>;
  }

  function Layout(props) {
    var about = OO.router.link('about', null, { hotel: props.ctx.store.onlineKey });
    return <React.Fragment>
      <a className="oo-sf-skip" href="#oo-sf-main">Skip to main content</a>
      {props.header}
      <Banner store={props.ctx.store} />
      <div className="oo-sf-layout">
        <aside className="oo-sf-sidebar" aria-label="Store navigation">
          <strong className="oo-sf-sidebar__title">Menu</strong>
          <nav aria-label="Menu categories">{props.tabs.map(function (t) { return <button type="button" key={t.id} aria-current={props.active === t.id ? 'true' : undefined} onClick={function () { props.onTab(t.id); }}>{t.label}</button>; })}</nav>
          <a className="oo-sf-sidebar__about" href={about.href} onClick={about.onClick}><OO.icons.Info size={15} /> About</a>
          {props.profileHref ? <a className="oo-sf-sidebar__account" href={props.profileHref} onClick={props.onProfile}><span><OO.icons.User size={20} /></span> Sign in <OO.icons.ChevronRight size={16} /></a> : null}
        </aside>
        <div className="oo-sf-content">
          <div className="oo-sf-mobile-tabs">{props.tabs.length ? <OO.ui.CategoryTabs tabs={props.tabs} active={props.active} onSelect={props.onTab} onSearch={props.onSearch} /> : null}</div>
          <main className="oo-sf-main" id="oo-sf-main" tabIndex={-1}>
            {props.results ? props.resultsView : <React.Fragment>{props.namedView}{props.browse ? null : <Savings hotel={props.ctx.store.onlineKey} />}{props.promo}{props.sections.length ? props.sections : props.emptyView}</React.Fragment>}
          </main>
        </div>
      </div>
      <Footer hotel={props.ctx.store.onlineKey} store={props.ctx.store} />
    </React.Fragment>;
  }
  OO.pages.Storefront = { Header: Header, Banner: Banner, Layout: Layout };
})();


// ===================== src/pages/item-sheet.jsx =====================
// src/pages/item-sheet.jsx: OO.pages.ItemSheet, the item options overlay (design "02 Item" mobile and "D02 Item"
// desktop, docs/online-order/design/ordering-flow.dc.txt). It is not a routed page: the Menu opens it for a product
// and the Cart opens it with editLineKey to edit a line. Owner: T3b. Styles: styles/item.css.
//
// <OO.pages.ItemSheet hotel store menu productId editLineKey moment reward onClose onAdded />
//   store, menu   the normalised OO.model Store / Menu. With no `menu` the sheet loads it itself (OO.useHotel(hotel))
//                 and shows a skeleton meanwhile.
//   productId     a menu.products key. With editLineKey it defaults to the edited line's itemId.
//   editLineKey   edit that cart line: size, options, quantity and notes are prefilled, the CTA reads "Update item",
//                 and the line is replaced in place (OO.store.cartReplace; an identical line merges).
//   moment        the menu page's judging opts (D-07, P2-17: the draft's slot or now, the order type's domain, the
//                 active named menu): the item is orderable when OO.model.displayState at that moment says 'normal'
//                 (its label explains a greyed one), and (P2-20) its sizes, groups and options are judged at the same
//                 moment: OO.modifiers.withAvailability over OO.data.menuAt(hotel, { now: moment.now, domain:
//                 moment.domain }) (the menu's raw body normalised then). Without it, an edited line (the cart's edit)
//                 is judged at the order's moment as the menu would judge it (OO.pages.Menu.shared.orderMoment over
//                 the hotel's draft, no named-menu scope), so an option only offered at the chosen slot survives an
//                 edit; a plain mount without one reads the static flags, as before.
//   reward        (Batch 2 P3-17) REWARD MODE: the sheet redeems the product with the customer's points as a wallet
//                 type-2 reward line (OO.wallet, src/lib/wallet.jsx; OO.cart.rewardLine) instead of adding a paid
//                 line. reward = { row (the product's OO.wallet.rewardRows entry: { id, points }), orderType ('PICKUP' |
//                 'DELIVERY'), table (a table order: truthy) }: the opener's engine ctx, OO.wallet.ctxOf(store, {
//                 hotel, orderType, table, coupon: the stored coupon }). The rewards page and the checkout's wallet
//                 section open it for a reward with a size or options to choose. Ignored with editLineKey. See
//                 "Reward mode" below.
//   onClose()     ESC, the scrim, the close button, and after a successful add or update.
//   onAdded(line, info)  called right after the line went into the cart and BEFORE onClose(). The sheet unmounts, so
//                 the parent shows the confirmation (toast). info = { kind: 'item', mode: 'add' | 'update', name,
//                 qty, message: 'Fat Boy Burger added to cart' | 'Cart updated' }; in reward mode { kind: 'reward',
//                 mode: 'add', name, qty: 1, points (what the reward takes), notice (the stacking rules' notice text,
//                 e.g. the coupon was removed | null), message: 'Fat Boy Burger added to your cart as a reward.' (+ ' ' +
//                 notice) }.
//   After an add (not an update), before onAdded: OO.upsell.afterAdd(line, { product, store }) if upsell.jsx has it; an
//   add sheet warms the product's suggestion read on open (OO.upsell.prefetch, P2-19). Neither in reward mode.
//   P5-06b (src/lib/analytics.jsx): an add sheet sends view_item when it opens and add_to_cart after its add
//   (OO.analytics.viewItem / addToCart); neither for an edit or in reward mode.
//   The overlay is open while mounted (open={false} renders nothing).
//   browse        (WL-5) the landing website's BROWSE-ONLY menu (ordering off): the description, badges, sizes and options
//                 with their prices (picking them only shows them), but no quantity, notes, add or age gate; the footer
//                 is a Close button. Nothing goes into the cart, no upsell read, no analytics.
// <OO.pages.ItemSheet.ComboInfo store menu comboId onClose /> (WL-5) the browse-only menu's combo details: the photo,
//   name, description, price ("From …" when a group has extras) and what the deal includes (each group's name and
//   options), with a Close button. No builder, nothing added.
//
// Rules. All selection and price logic is OO.modifiers / OO.cart; this file only renders their view (REF item-sheet
// parity, P2-20: B §1.2 "Sheet").
//   - Sizes: a required radio group whose price REPLACES the base. An available `preselect` takes priority,
//     otherwise the first available size is selected. A size's label is its ABSOLUTE price when the store
//     shows size prices (store.settings.showSizePrice: getRestaurant show_size_price), else none; never a "+$6"
//     difference (REF). A size unavailable at the moment is listed, disabled, "Unavailable"; with none available the
//     item can't be added. Picking another size RESETS the options to that size's defaults (OO.modifiers.defaults:
//     its preselects), as REF does.
//   - Required groups start open with a Required → Done badge at the minimum and auto-collapse only at their
//     configured maximum. All optional groups start collapsed behind a summary + chevron and open on demand. A group
//     unavailable as a whole is skipped; a required group whose options are all unavailable shows its reason and
//     blocks Add (P1-09b, OO.modifiers.visibleGroups).
//   - Multi-choice groups cap at max (the rest disable). allow_same groups are counters.
//   - Options: a 40px photo beside the name when the option has one (mod_image). The price is '+$2'; with the
//     group's free allowance ("First 1 free" in its hint) a picked option reads 'Free' once every picked unit is free
//     and '+$2 · 1 free' when some are (the combo builder's labels). A picked option with an alert (Option.alert:
//     REF is_alert + alert_text, which REF toasts) shows it under its group while picked.
//   - Tags: the ribbon and the product's tags as badges under the heading, REF's dietary codes spelled out
//     (tagLabel: V → Vegetarian, GF → Gluten free, …).
//   - The CTA price is OO.modifiers.view's total: allowance-aware and exactly OO.cart.itemLine's unit price × qty,
//     so what the payload sends for the line. The CTA names the first missing choice ("Choose a size", "Choose a
//     base"); clicking it opens, focuses and shakes that section. Sold-out or unavailable items keep a disabled CTA.
//   - "Additional notes" (at most 200 characters, with a counter; REF's limit) is hidden when the product says so
//     (special_instruction_hide).
//   - Prices (P2-21; the menu cards' rules, menu.jsx priceNode): the heading shows the price at the cart's pricetag
//     (the priced menu). Struck through: the base price under a MEMBERSHIP tag ("Member price" badge); else, when
//     the signed-in customer's membership offers this item a saving (OO.membership.resolve's stack.savings: no
//     coupon on the cart, no reward line; offerFor), the member price with the usual one struck and "10% member
//     discount" (OO.membership.itemPrice; a capped offer can't be priced alone: "Up to 10% member discount · final
//     saving in cart"); else the banner (was) price when it is above the price. The CTA price then nets the line's
//     member saving: OO.membership.discount([the line], …).item, the summary's own figure (0 for a capped offer or a
//     line with a member tag price, OO.membership.eligible). The line itself is unchanged: the saving is the summary's
//     and the payload's (the membership contributor), as before.
//   - Rating pill (P3-07; REF ProductRatingPill): the product's average rating ("★ 4.3 1.2k", OO.ui.RatingBadge variant
//     product) after the heading's price, from OO.ui.useProductRating (E29), only when the store's review_settings show
//     product ratings and the product has reviews; nothing while it loads, on a failure, or on the fixture stores.
//   - Age gate (P2-21; REF OnlineOrderAgeGate / useAgeVerification): an age-restricted product (age.restricted: the
//     item's `av` or its category's) opens "Verify your age" first, until a date of birth old enough for the store
//     (age.minimumAge, default 18) is confirmed. The confirmation is remembered for the browser session per store
//     (sessionStorage; P3-LOGOUT: until a sign-out, age.forgetAll), the signed-in customer's birthday is prefilled, and
//     Cancel (or closing it) closes the item.
//     The confirmed date is the payload's customer_dob (checkout passes age.verifiedDob as ctx.dob).
//   - Reward mode (P3-17, the `reward` prop; REF UnifiedProductModalCore's loyalty product mode + resolveLoyaltyCommit).
//     Sizes, options and notes work as above; the quantity is 1 (no stepper: REF locks a reward line at 1), and the
//     membership's prices stay off (a reward line takes no member saving). The heading shows what the selection costs
//     in points (reward.quote: '120 points'; 'From 60 points' while a sized product has no size yet) over the usual
//     price struck, and a "Reward" badge. An info notice says what the points cover: the product at its size; options
//     are charged as usual, except at an applyDiscountToModifiers store, whose validator nets a reward row's options
//     too (wallet.jsx). Once every required choice is made, OO.wallet.addReward judges the selection against the cart:
//     its refusal (SHORT 'This reward needs 20 more points.', OVER_MAX, LIMIT, UNAVAILABLE, NOT_REWARD, …) shows as a
//     warn notice and disables the CTA (reward.ctaLabel: 'Not enough points' | 'Limit reached' | 'Can’t redeem').
//     The CTA reads "Redeem reward" with the points and what the options add ('120 points + $1.50'). Redeem re-runs
//     addReward on the cart as it is now (OO.store.cartLines, the stored coupon) and, when it passes, removes what it
//     drops (the coupon: OO.store.setCoupon(null); the points chosen: OO.wallet.dropPoints(hotel)) and adds its line
//     with OO.store.cartAdd: a reward line is always a line of its own (its 'reward-…' key), never merged into an
//     identical paid one; the wire shape is P2-06's (OO.cart.rewardLine, as the parity generator builds it). A
//     refusal at that point keeps the sheet open with its text.
//
// Shared with the combo builder (src/pages/combo-builder.jsx) as OO.pages.ItemSheet.parts: ModGroup (one modifier
// group card), CounterRow, useOverlayData, useEditLine, LoadingSheet and MissingSheet. OO.pages.ItemSheet.tagLabel(tag)
// → the display text of a product tag ('V' → 'Vegetarian'; any other tag as it is).
// OO.pages.ItemSheet.age (P2-21; pure but for the session store, which is read and written only inside these calls):
//   restricted(product, menu) → bool: Product.ageRestricted, or its category's Category.ageRestricted (get_menu `av`).
//   minimumAge(store) → store.minimumAge when 0 < n < 130 (REF resolveMinimumAge), else MIN_AGE (18).
//   ageOn(dob, todayYmd) → whole years on that day | null (not a real 'YYYY-MM-DD' date, or after today).
//   check(dob, minimum, todayYmd) → { ok, message }: 'Please enter your date of birth.' | 'Please enter a valid date
//       of birth.' | 'You must be 18 or over to order this item.' | ok with message ''.
//   todayOf(store, now?) → the store's 'YYYY-MM-DD' at now (default OO.data.now()).
//   verifiedDob(hotel, store?, todayYmd?) → this session's confirmed date of birth for the store, or null (none, or no
//       longer old enough for the store's minimum: REF isAgeVerified). Checkout sends it as customer_dob.
//   confirm(hotel, dob) → bool (a valid date was kept) · forget(hotel). Storage key 'oo-age-v1-{hotel}'.
//   forgetAll() (P3-LOGOUT): every store's confirmation, in memory and in the session store. A sign-out calls it
//       (OO.store.logout, or a session for another account: src/lib/store.jsx "Sign-out").
// OO.pages.ItemSheet.reward (P3-17; pure: it reads the wallet only through the ctx it is given):
//   quote(product, { sizeId, sel, notes, complete }, reward, lines, ctx, store) → { points, from, price, extras, refuse,
//       x }: the reward-mode figures of a selection (lines = the cart's, ctx = OO.wallet.ctxOf's). points = what its
//       reward line takes at the order's redemption row (OO.wallet.rewardPoints: 0 for a size priced 0; without a row,
//       reward.row.points); a sized product without a size: the cheapest available size's (a size priced 0 only when
//       none has a price), with from = true when the sizes differ.
//       price = the product price the points pay for (the line's basePrice); extras = what the options still charge
//       (the line's unit price − its basePrice; 0 at an applyDiscountToModifiers store); x = OO.wallet.addReward's
//       result once `complete` (every required choice made), else null; refuse = its refusal ({ code, text }) | null.
//   ctaLabel(quote) → 'Redeem reward' | 'Not enough points' (SHORT) | 'Limit reached' (OVER_MAX, LIMIT) | 'Can’t redeem'.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  function noop() {}
  function arr(v) { return Array.isArray(v) ? v : []; }
  function has(v) { return v !== undefined && v !== null && v !== ''; }
  function domId(prefix) { return prefix + React.useId().replace(/[^a-zA-Z0-9_-]/g, ''); }

  // ---- shared overlay plumbing -------------------------------------------------------------------------
  // The parent passes the menu it already has. Without one (a deep link, a standalone mount) the overlay loads the
  // hotel itself; OO.useHotel(null) is inert, so a passed menu costs nothing.
  function useOverlayData(props) {
    var own = !props.menu;
    var h = OO.useHotel(own ? props.hotel : null);
    return {
      store: props.store || h.store || null,
      menu: props.menu || h.menu || null,
      loading: own && has(props.hotel) && h.loading,
    };
  }

  // The cart line being edited, frozen at first sight. A replace can change the line's key (an item key encodes its
  // options), so the line vanishes from the cart a moment before the parent unmounts the overlay. The frozen copy
  // keeps the form steady instead of flashing back to an empty "add" form.
  function useEditLine(key) {
    var lines = OO.useStore(function (s) { return s.cart.lines; });
    var ref = React.useRef(null);
    if (!has(key)) return null;
    if (!ref.current || ref.current.key !== key) {
      var hit = arr(lines).filter(function (l) { return l.key === key; })[0];
      if (hit) ref.current = hit;
    }
    return ref.current && ref.current.key === key ? ref.current : null;
  }

  function LoadingSheet(props) {
    return (
      <OO.ui.Sheet open onClose={props.onClose} label={props.label || 'Loading'} kind={props.kind}>
        <div className="oo-item__loading" aria-busy="true">
          <span className="oo-sr-only" role="status">Loading…</span>
          <OO.ui.Skeleton variant="block" height={180} />
          <OO.ui.Skeleton lines={3} />
          <OO.ui.Skeleton variant="block" height={150} />
        </div>
      </OO.ui.Sheet>
    );
  }

  function MissingSheet(props) {
    return (
      <OO.ui.Sheet open variant="dialog" title={props.title} onClose={props.onClose}
        footer={<OO.ui.Button block onClick={props.onClose}>Close</OO.ui.Button>}>
        <p className="oo-item__missing">{props.body}</p>
      </OO.ui.Sheet>
    );
  }

  // ---- modifier groups -----------------------------------------------------------------------------------
  // A modifier option's photo (Option.image: mod_image), 40px beside its name (REF); none without one.
  function OptionThumb(props) {
    return props.src ? <span className="oo-item__opt-thumb"><img src={props.src} alt="" loading="lazy" /></span> : null;
  }

  // <CounterRow option={{ id, name, desc, priceLabel, priceTone, count, disabled, optionImage }} full image onInc onDec />
  //   — one allow_same option: label, price and a small stepper (0 … the group's max; `full` / `disabled` stop the
  //   plus). `image` (any value, even null) adds the 64px thumb of the combo item rows. optionImage: a modifier
  //   option's 40px photo before the label (the item sheet's rows). priceTone 'success' = green.
  function CounterRow(props) {
    var o = props.option;
    var count = Number(o.count) || 0;
    var thumb = props.image !== undefined; // item rows: the price moves under the name, beside a photo + stepper
    var price = o.priceLabel ? <span className={OO.ui.cx('oo-item__counter-price', o.priceTone && 'oo-item__counter-price--' + o.priceTone)}>{o.priceLabel}</span> : null;
    return (
      <div className={OO.ui.cx('oo-item__counter', count > 0 && 'is-on', thumb && 'oo-item__counter--thumb')}>
        {thumb ? <span className="oo-item__counter-thumb">{props.image ? <img src={props.image} alt="" loading="lazy" /> : null}</span> : <OptionThumb src={o.optionImage} />}
        <span className="oo-item__counter-text">
          <span className="oo-item__counter-label">{o.name}</span>
          {o.desc ? <span className="oo-item__counter-desc">{o.desc}</span> : null}
          {thumb ? price : null}
        </span>
        {thumb ? null : price}
        <OO.ui.QtyStepper size="sm" value={count} min={0} max={props.full || o.disabled ? count : 99} label={o.name} itemName={o.name}
          onChange={function (n) { if (n > count) props.onInc(o.id); else props.onDec(o.id); }} />
      </div>
    );
  }

  // <ModGroup group onPick onInc onDec collapsible open onToggleOpen /> — one modifier-group card on OO.ui.OptionGroup.
  //   group = { id, name, hint, summary, badge ('Required' | 'Done' | 'Optional'), required, radio, counter, full,
  //             options: [{ id, name, priceLabel, priceTone, optionImage, on, count, disabled }], alerts: [{ id, name,
  //             text }] } — the shape of OO.modifiers.view().groups with priceLabel set (the combo builder maps
  //   OO.combo.view().mods to it). alerts (optional): one warn notice each under the options ("Bacon: contains pork").
  function ModGroup(props) {
    var g = props.group;
    var ui = OO.ui;
    var route=OO.router.current();
    if(OO.whitelabel.zetaDesign(route.chain||route.hotel) && OO.appKit.ZetaModGroup)return <OO.appKit.ZetaModGroup {...props}/>;
    return (
      <ui.OptionGroup title={g.name} hint={g.hint} type={g.radio ? 'radio' : 'checkbox'} required={g.required} done={g.badge === 'Done'}
        collapsible={!!props.collapsible} open={props.collapsible ? !!props.open : undefined} onToggle={props.onToggleOpen} summary={g.summary}
        elementRef={props.elementRef} invalid={props.invalid} className="oo-item__group">
        {arr(g.options).map(function (o) {
          if (g.counter) {
            return <CounterRow key={o.id} option={o} full={g.full} onInc={props.onInc} onDec={props.onDec} />;
          }
          var label = o.optionImage ? <span className="oo-item__opt"><OptionThumb src={o.optionImage} /><span className="oo-item__opt-name">{o.name}</span></span> : o.name;
          return (
            <ui.OptionRow key={o.id} value={String(o.id)} label={label} price={o.priceLabel} priceTone={o.priceTone} checked={!!o.on} disabled={!!o.disabled}
              onToggle={function () { props.onPick(o.id); }} />
          );
        })}
        {arr(g.alerts).map(function (a) {
          return <ui.InlineNotice key={'alert-' + a.id} tone="warn" className="oo-item__alert"><b>{a.name}</b>{': ' + a.text}</ui.InlineNotice>;
        })}
      </ui.OptionGroup>
    );
  }

  // Shared by products and combo choices. Reduced motion keeps the focus/highlight without the shake or smooth scroll.
  function focusInvalidGroup(el) {
    if (!el) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    if (el.focus) el.focus({ preventScroll: true });
    if (!reduce && el.animate) el.animate([
      { transform: 'translateX(0)' }, { transform: 'translateX(-6px)' }, { transform: 'translateX(6px)' },
      { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(0)' },
    ], { duration: 350 });
  }

  // ---- labels ----------------------------------------------------------------------------------------------
  // An option's price on its row: '+$2'; with a free allowance, 'Free' once every picked unit is free and '+$2 · 1
  // free' when some are (OO.combo.view's optPrice); '' for a $0 option. tone 'success' for 'Free'.
  function chargeLabel(o, cur) {
    var f = OO.format;
    if (!o.price) return { text: '', tone: null };
    if (o.count > 0 && o.free >= o.count) return { text: 'Free', tone: 'success' };
    return { text: f.plusMoney(o.price, cur) + (o.free > 0 ? ' · ' + o.free + ' free' : ''), tone: null };
  }

  // REF tagIcons SIPO_TAG_CODES, in our sentence case: a tag that is one of these codes reads as its label.
  var TAG_CODES = {
    H: 'Hot', V: 'Vegetarian', VE: 'Vegan', GF: 'Gluten free', NG: 'No gluten', GFR: 'Gluten friendly (GFR)',
    GFF: 'Gluten friendly (GFF)', HL: 'Halal', NF: 'Nut free', DF: 'Dairy free',
  };
  function tagLabel(tag) {
    var s = String(tag == null ? '' : tag).trim();
    return Object.prototype.hasOwnProperty.call(TAG_CODES, s.toUpperCase()) ? TAG_CODES[s.toUpperCase()] : s;
  }
  // The heading's badges (REF getMenuItemBadges): the ribbon, then each tag's label, without repeats.
  function badgesOf(product) {
    var out = [];
    [product.ribbon].concat(arr(product.tags).map(tagLabel)).forEach(function (b) {
      var s = String(b == null ? '' : b).trim();
      if (s && out.map(function (x) { return x.toLowerCase(); }).indexOf(s.toLowerCase()) < 0) out.push(s);
    });
    return out;
  }

  // ---- prices (P2-21; see "Prices" in the header) ----------------------------------------------------------------
  // A membership tag set this price (menu.jsx memberPriced): its base price is struck through.
  function memberTagged(e, menu) {
    return !!(menu && menu.pricetag && menu.pricetag.membership && e.pricetagId != null && e.basePrice != null && e.price < e.basePrice);
  }
  function pctText(pct) { return String(Math.round(Number(pct) * 100) / 100); }
  function capped(offer) { return offer.max !== null && offer.max !== undefined && offer.max > 0; }
  // The signed-in customer's membership offer on this product while member savings apply to the cart (OO.membership.
  // resolve, the summary's and the payload's own reading: no coupon, no reward line) → { offer, membership, pricing }
  // | null.
  function memberOfferFor(product, store, lines, coupon) {
    var Mb = OO.membership;
    if (!Mb || typeof Mb.resolve !== 'function' || !store) return null;
    try {
      var r = Mb.resolve(arr(lines), store, { coupon: coupon || null });
      if (!r || !r.stack || !r.stack.savings || !r.membership) return null;
      var offer = Mb.offerFor({ productId: product.id, categoryId: product.categoryId }, r.membership);
      return offer ? { offer: offer, membership: r.membership, pricing: r.pricing } : null;
    } catch (e) { return null; }
  }
  // The heading's price → { price, was (struck) | null, badge ('Member price' | null), note (the member offer's text |
  // null) }: a membership tag's price over its base; else the member price over the usual one; else the banner price
  // when it is above the price (REF headerStrikePrice).
  function headingPrice(product, menu, member) {
    if (memberTagged(product, menu)) return { price: product.price, was: product.basePrice, badge: 'Member price', note: null };
    var h = { price: product.price, was: null, badge: null, note: null };
    if (member) {
      var ip = OO.membership.itemPrice(product.price, member.offer);
      if (ip) return { price: ip.discountedPrice, was: ip.originalPrice, badge: null, note: pctText(member.offer.pct) + '% member discount' };
      if (capped(member.offer)) h.note = 'Up to ' + pctText(member.offer.pct) + '% member discount · final saving in cart';
    }
    if (product.wasPrice != null && product.wasPrice > h.price) h.was = product.wasPrice;
    return h;
  }
  // The line's member ITEM saving, the summary's own figure for it (0 for a capped offer, whose cap the whole order
  // shares, and for a line a member tag price excludes).
  function memberSaving(member, line, store) {
    var Mb = OO.membership;
    if (!member || !line || capped(member.offer) || !Mb.eligible(line, member.pricing)) return 0;
    var d = Mb.discount([line], member.membership, { store: store, pricing: member.pricing });
    return d && d.item > 0 ? d.item : 0;
  }

  // ---- reward mode (P3-17; see "Reward mode" and OO.pages.ItemSheet.reward in the header) -------------------------------
  function rewardQuote(product, choice, reward, lines, ctx, store) {
    var W = OO.wallet;
    choice = choice || {};
    var rule = W.rewardBudget(arr(lines), ctx || {}).rule;
    var rowPoints = Math.max(0, Number(reward && reward.row && reward.row.points) || 0);
    function at(sizeId, sel) {
      var l = OO.cart.rewardLine(product, { sizeId: sizeId, sel: sel || {} }, { points: 0 });
      return { line: l, points: rule ? W.rewardPoints(l, rule) : rowPoints };
    }
    var out = { points: 0, from: false, price: 0, extras: 0, refuse: null, x: null };
    var sizes = arr(product.sizes);
    if (sizes.length && !has(choice.sizeId)) {
      // No size yet: the cheapest available size's points (a size priced 0 only when none has a price), as the
      // rewards list judges it (CheckoutSections.wallet.rewardItems).
      var open = sizes.filter(function (z) { return z.available !== false; });
      var quotes = (open.length ? open : sizes).map(function (z) { return at(z.id, {}); });
      var priced = quotes.filter(function (q) { return q.points > 0; });
      var low = (priced.length ? priced : quotes).slice().sort(function (a, b) { return a.points - b.points; })[0];
      out.points = low.points;
      out.price = low.line.basePrice;
      out.from = quotes.some(function (q) { return q.points !== low.points; });
      return out;
    }
    var q = at(has(choice.sizeId) ? choice.sizeId : null, choice.sel);
    out.points = q.points;
    out.price = q.line.basePrice;
    out.extras = store && store.applyDiscountToModifiers === true ? 0 : OO.format.round2(Math.max(0, q.line.unitPrice - q.line.basePrice));
    if (choice.complete) {
      var x = W.addReward(product, { sizeId: has(choice.sizeId) ? choice.sizeId : null, sel: choice.sel || {}, notes: choice.notes || '' }, reward && reward.row, arr(lines), ctx || {});
      out.x = x;
      if (x.ok) out.points = x.points;
      else out.refuse = x.refuse || { code: null, text: W.TEXT.UNAVAILABLE };
    }
    return out;
  }
  function rewardCta(quote) {
    var code = quote && quote.refuse ? quote.refuse.code : null;
    if (!code && !(quote && quote.refuse)) return 'Redeem reward';
    if (code === 'SHORT') return 'Not enough points';
    if (code === 'OVER_MAX' || code === 'LIMIT') return 'Limit reached';
    return 'Can’t redeem';
  }
  // '120 points' · '1 point' · 'From 60 points' (the store's word); '' for no points (a size priced 0: refused).
  function pointsLabel(n, word, from) {
    var w = word || 'points';
    var v = Math.round((Number(n) || 0) * 100) / 100;
    if (!(v > 0)) return '';
    return (from ? 'From ' : '') + v.toLocaleString('en-NZ') + ' ' + (v === 1 ? w.replace(/s$/i, '') : w);
  }
  // The CTA's price in reward mode: the points and what the options add ('120 points + $1.50').
  function rewardPrice(q, word, cur) {
    var parts = [];
    var pts = pointsLabel(q.points, word, q.from);
    if (pts) parts.push(pts);
    if (q.extras > 0) parts.push(OO.format.money(q.extras, cur));
    return parts.join(' + ');
  }

  // ---- the age gate (P2-21; see OO.pages.ItemSheet.age in the header) ----------------------------------------------
  var MIN_AGE = 18;
  var AGE_KEY = 'oo-age-v1-';
  var ageMemo = {}; // hotel → the confirmed date of birth this page load (sessionStorage keeps it for the session)

  function ageRestricted(product, menu) {
    if (!product) return false;
    if (product.ageRestricted === true) return true;
    var cat = product.categoryId != null ? arr(menu && menu.categories).filter(function (c) { return c.id === product.categoryId; })[0] : null;
    return !!(cat && cat.ageRestricted === true);
  }
  function minimumAge(store) {
    var n = Number(store && store.minimumAge);
    return isFinite(n) && n > 0 && n < 130 ? n : MIN_AGE;
  }
  // A real calendar date 'YYYY-MM-DD' (a longer ISO string's date part) → its yyyymmdd number, else null.
  function ymdNumber(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s == null ? '' : s).trim());
    if (!m) return null;
    var y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
    var days = [31, (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mo - 1];
    return mo >= 1 && mo <= 12 && d >= 1 && d <= days ? y * 10000 + mo * 100 + d : null;
  }
  function ageOn(dob, todayYmd) {
    var b = ymdNumber(dob), t = ymdNumber(todayYmd);
    if (b === null || t === null || b > t) return null;
    return Math.floor((t - b) / 10000); // whole years: the birthday counts from its own day (29 Feb: from 1 Mar)
  }
  function checkAge(dob, minimum, todayYmd) {
    var min = minimum > 0 ? minimum : MIN_AGE;
    if (!String(dob == null ? '' : dob).trim()) return { ok: false, message: 'Please enter your date of birth.' };
    var age = ageOn(dob, todayYmd);
    if (age === null) return { ok: false, message: 'Please enter a valid date of birth.' };
    if (age < min) return { ok: false, message: 'You must be ' + min + ' or over to order this item.' };
    return { ok: true, message: '' };
  }
  function todayOf(store, now) { return OO.model.storeNow(store || null, now != null ? now : OO.data.now()).ymd; }
  function ageKey(hotel) { return String(hotel == null ? '' : hotel); }
  function sessionStore() { try { return window.sessionStorage || null; } catch (e) { return null; } }
  function storedDob(hotel) {
    var k = ageKey(hotel);
    if (!k) return null;
    if (!Object.prototype.hasOwnProperty.call(ageMemo, k)) {
      var v = null;
      try { var s = sessionStore(); v = s ? s.getItem(AGE_KEY + k) : null; } catch (e) { v = null; }
      ageMemo[k] = ymdNumber(v) !== null ? String(v).trim().slice(0, 10) : null;
    }
    return ageMemo[k];
  }
  function verifiedDob(hotel, store, todayYmd) {
    var dob = storedDob(hotel);
    if (!dob) return null;
    var age = ageOn(dob, todayYmd || todayOf(store));
    return age !== null && age >= minimumAge(store) ? dob : null;
  }
  function confirmAge(hotel, dob) {
    var k = ageKey(hotel);
    if (!k || ymdNumber(dob) === null) return false;
    ageMemo[k] = String(dob).trim().slice(0, 10);
    try { var s = sessionStore(); if (s) s.setItem(AGE_KEY + k, ageMemo[k]); } catch (e) { /* this page load still has it */ }
    return true;
  }
  function forgetAge(hotel) {
    var k = ageKey(hotel);
    delete ageMemo[k];
    try { var s = sessionStore(); if (s) s.removeItem(AGE_KEY + k); } catch (e) { /* nothing kept */ }
  }
  // P3-LOGOUT: every store's confirmation, in memory and in the session store (a sign-out: OO.store.logout). The keys
  // are collected first, because removing while walking key(i) would skip some.
  function forgetAllAges() {
    ageMemo = {};
    try {
      var s = sessionStore();
      if (!s || typeof s.key !== 'function') return;
      var keys = [];
      for (var i = 0; i < s.length; i++) {
        var k = s.key(i);
        if (typeof k === 'string' && k.indexOf(AGE_KEY) === 0) keys.push(k);
      }
      keys.forEach(function (key) { s.removeItem(key); });
    } catch (e) { /* nothing kept */ }
  }

  // REF OnlineOrderAgeGate: "Verify your age" for the item, the date of birth (prefilled with the signed-in
  // customer's birthday), the store's minimum age; Cancel / closing it closes the item.
  function AgeGate(props) {
    var ui = OO.ui;
    var min = minimumAge(props.store);
    var dobS = React.useState(props.defaultDob || '');
    var errS = React.useState('');
    function confirm() {
      var r = checkAge(dobS[0], min, todayOf(props.store));
      if (!r.ok) { errS[1](r.message); return; }
      props.onConfirm(String(dobS[0]).trim().slice(0, 10));
    }
    return (
      <ui.Sheet open variant="dialog" title="Verify your age" onClose={props.onDecline} className="oo-item-age"
        footer={[
          <ui.Button key="cancel" variant="outline" className="oo-item-age__btn" onClick={props.onDecline}>Cancel</ui.Button>,
          <ui.Button key="ok" className="oo-item-age__btn" onClick={confirm}>Confirm</ui.Button>,
        ]}>
        <p className="oo-item-age__text"><b>{props.name}</b>{' is an age restricted item. You must be ' + min + ' or over to continue.'}</p>
        <ui.TextInput label="Date of birth" labelVisible type="date" autoComplete="bday" value={dobS[0]} error={errS[0] || undefined}
          onChange={function (v) { dobS[1](v); if (errS[0]) errS[1](''); }} />
        <p className="oo-item-age__note">Photo ID may be required on delivery or collection.</p>
      </ui.Sheet>
    );
  }

  // ---- the sheet -------------------------------------------------------------------------------------------
  // A cart line's modifiers → a selection ({ [groupId]: [optionId × count] }). A modifier whose group id no longer
  // matches is matched by option id; whatever the product (or its size) no longer offers is pruned.
  function selFromLine(product, sizeId, line) {
    var groups = OO.modifiers.visibleGroups(product, sizeId);
    var sel = {};
    arr(line.modifiers).forEach(function (m) {
      var g = groups.filter(function (x) { return x.id === Number(m.groupId) && x.options.some(function (o) { return o.id === Number(m.addOnId); }); })[0]
        || groups.filter(function (x) { return x.options.some(function (o) { return o.id === Number(m.addOnId); }); })[0];
      if (!g) return;
      var n = Math.max(1, Math.floor(Number(m.count)) || 1);
      sel[g.id] = (sel[g.id] || []).concat(Array.apply(null, Array(n)).map(function () { return Number(m.addOnId); }));
    });
    return OO.modifiers.prune(product, sizeId, sel);
  }

  // An edited line keeps its saved size while the product still has it, else the customer must choose again.
  function initialState(product, line) {
    if (line) {
      var sizes = arr(product.sizes);
      var sizeId = has(line.sizeId) && sizes.some(function (z) { return z.id === Number(line.sizeId); }) ? Number(line.sizeId) : null;
      return { sizeId: sizeId, sel: selFromLine(product, sizeId, line), qty: Math.max(1, Math.floor(Number(line.qty)) || 1), notes: clipNotes(line.notes), open: {} };
    }
    var init = OO.modifiers.initial(product);
    return { sizeId: init.sizeId, sel: init.sel, qty: 1, notes: '', open: {} };
  }

  // The size the groups use: the chosen one while it is available, else none (as OO.modifiers.view's sizeId).
  function liveSize(product, sizeId) {
    var z = arr(product.sizes).filter(function (x) { return x.id === sizeId; })[0];
    return z && z.available !== false ? z.id : null;
  }
  function findGroup(product, sizeId, groupId) {
    return OO.modifiers.visibleGroups(product, liveSize(product, sizeId)).filter(function (g) { return g.id === groupId; })[0] || null;
  }

  var NOTES_MAX = 200; // REF's Special Instructions limit
  function clipNotes(t) { return String(t == null ? '' : t).slice(0, NOTES_MAX); }
  // The notes a line carries: none when the product hides the field.
  function notesOf(product, s) { return product.hideNotes ? '' : clipNotes(s.notes); }
  // P3-17: the engine ctx of a reward (the opener's order type and table; the coupon the cart holds).
  function rewardCtx(reward, store, hotel, coupon) {
    return OO.wallet.ctxOf(store || null, { hotel: has(hotel) ? String(hotel) : undefined, orderType: reward.orderType, table: reward.table || null, coupon: coupon || null });
  }

  // P2-20: the cart's edit passes no moment. An edited line is judged where it was added and where the cart checks
  // it: at this hotel's draft (its slot while still offered, else now; the order type the cart prices) through
  // OO.pages.Menu.shared.orderMoment, with no named-menu scope (as the cart's and checkout's re-checks). null when it
  // can't be built: the static flags then.
  function selectDraft(s) { return s.checkout; }
  function editMoment(store, draft, hotel) {
    var S = OO.pages.Menu && OO.pages.Menu.shared;
    if (!store || !S || typeof S.orderMoment !== 'function') return null;
    var mine = draft && (!draft.hotel || String(draft.hotel) === String(hotel)) ? draft : null;
    try { return Object.assign({ timeZone: store.timezone, menuId: null }, S.orderMoment(store, mine, OO.mode)); } catch (e) { return null; }
  }

  // P2-20 (D-07): the product with its sizes, groups and options judged at the sheet's moment (see `moment` above).
  // The menu's own product (prices at the cart's tag) when there is no moment or the snapshot isn't there.
  function useProductAt(product, hotel, moment, reward) {
    var at = null;
    if (moment && has(hotel) && OO.data && typeof OO.data.menuAt === 'function') {
      var snap = OO.data.menuAt(hotel, { now: moment.now, domain: moment.domain });
      if (reward) snap = OO.model.redemptionMenu(snap);
      at = snap && snap.products ? snap.products[product.id] || null : null;
    }
    return React.useMemo(function () { return OO.modifiers.withAvailability(product, at); }, [product, at]);
  }

  // What the member price reads (P2-21): the cart (lines, coupon) and the session / wallet reads behind the membership.
  function selectPricing(s) { return { lines: s.cart.lines, coupon: s.cart.coupon, session: s.session, wallets: s.wallets }; }

  // P3-07: the product's rating pill. Its own component, so its reads (hooks) never shift the sheet's hook order.
  function ProductRating(props) {
    var r = OO.ui.useProductRating(props.hotel, props.store, props.productId);
    return r ? <OO.ui.RatingBadge variant="product" average={r.average} total={r.total} className="oo-item__rating" /> : null;
  }

  function ItemSheetBody(props) {
    var ui = OO.ui, f = OO.format, M = OO.modifiers;
    var line = props.line;
    var draft = OO.useStore(selectDraft);
    var moment = props.moment || (line ? editMoment(props.store, draft, props.hotel) : null);
    var product = useProductAt(props.product, props.hotel, moment, props.reward || (line && line.reward));
    var cartSt = OO.useStore(selectPricing);
    var store = props.store || {};
    var cur = store.currency || '$';
    var editing = !!line;
    var rw = !editing && props.reward && props.reward.row ? props.reward : null; // P3-17: reward mode
    var browse = !editing && !rw && props.browse === true; // WL-5: the browse-only menu
    var wide = ui.useWide();
    var headingId = domId('oo-item-t');
    var st = React.useState(function () { return initialState(product, line); });
    var s = st[0], setS = st[1];
    var doneRef = React.useRef(false);
    var errS = React.useState(null); // P3-17: a refusal at Redeem ({ code, text }), until the selection changes
    var invalidS = React.useState({ id: null, attempt: 0 });
    var groupRefs = React.useRef({});

    // P2-19: warm the product's suggestion read, so the after-add upsell comes up at once (REF prefetches on open).
    React.useEffect(function () {
      if (browse) return; // WL-5: nothing to order on a browse-only menu
      if (!editing && !rw && OO.upsell && typeof OO.upsell.prefetch === 'function') OO.upsell.prefetch(OO.store.get().hotel, product.id);
      if (!editing && !rw && OO.analytics) OO.analytics.viewItem(product, store); // P5-06b: view_item
    }, [product.id, editing, !!rw]);

    var v = M.view(product, { sizeId: s.sizeId, sel: s.sel, qty: s.qty });
    var judged = moment && props.menu ? OO.model.displayState(props.menu, product, moment) : null;
    var unavailable = judged ? judged.show !== 'normal' : !!product.soldOut || product.available === false;
    // P3-17: reward mode's figures, judged on the cart as it is (see "Reward mode" in the header).
    var word = (store.loyalty && store.loyalty.pointsWord) || 'points';
    var rq = rw ? rewardQuote(product, { sizeId: v.sizeId, sel: s.sel, notes: notesOf(product, s), complete: v.ok && !unavailable }, rw, cartSt.lines,
      rewardCtx(rw, props.store, props.hotel, cartSt.coupon), store) : null;
    var refusal = rw ? errS[0] || rq.refuse : null;
    var canAdd = v.ctaEnabled && !unavailable && !refusal;
    var canValidate = !unavailable && !v.blocked.length && v.missing.length > 0;
    var ctaLabel = unavailable ? (product.soldOut ? 'Sold out' : 'Not available right now')
      : !v.ok ? v.ctaLabel : rw ? rewardCta({ refuse: refusal }) : editing ? 'Update item' : 'Add to cart';

    function patch(fn) {
      if (errS[0]) errS[1](null);
      if (invalidS[0].id !== null) invalidS[1]({ id: null, attempt: invalidS[0].attempt });
      setS(function (prev) { return Object.assign({}, prev, fn(prev)); });
    }
    // A size change resets the options to that size's defaults (REF), never a re-pick of the same size.
    function pickSize(id) {
      patch(function (p) {
        if (p.sizeId === id) return {};
        // Reset the other sections along with their size-dependent choices.
        return { sizeId: id, sel: M.defaults(product, id), open: { size: false } };
      });
    }
    function withGroup(groupId, op, optionId) {
      patch(function (p) {
        var g = findGroup(product, p.sizeId, groupId);
        if (!g) return {};
        var sel = M[op](g, p.sel, optionId);
        var open = Object.assign({}, p.open);
        if (g.required) open[groupId] = g.unlimited || M.count(sel, groupId) < g.max;
        return { sel: sel, open: open };
      });
    }
    function toggleOpen(groupId, next) {
      patch(function (p) { var o = Object.assign({}, p.open); o[groupId] = !!next; return { open: o }; });
    }
    React.useEffect(function () {
      focusInvalidGroup(groupRefs.current[invalidS[0].id]);
    }, [invalidS[0]]);

    // P3-17: Redeem (reward mode): addReward again on the cart as it is now; its drops, then the reward line.
    function redeem() {
      var W = OO.wallet;
      var x = W.addReward(product, { sizeId: v.sizeId, sel: s.sel, notes: notesOf(product, s) }, rw.row, OO.store.cartLines(),
        rewardCtx(rw, props.store, props.hotel, OO.store.get().cart.coupon));
      if (!x.ok) { doneRef.current = false; errS[1](x.refuse || { code: null, text: W.TEXT.UNAVAILABLE }); return; }
      if (arr(x.drop).indexOf('coupon') >= 0) OO.store.setCoupon(null);
      if (arr(x.drop).indexOf('points') >= 0) W.dropPoints(has(props.hotel) ? String(props.hotel) : store.onlineKey);
      OO.store.cartAdd(x.line); // a reward line: its own 'reward-…' line, never merged into a paid one
      OO.platform.haptic('medium'); // P4-09: REF addItem (a no-op outside the app shell)
      var notice = x.notice && x.notice.text ? x.notice.text : null;
      if (props.onAdded) {
        props.onAdded(x.line, {
          kind: 'reward', mode: 'add', name: product.name, qty: 1, points: x.points, notice: notice,
          message: product.name + ' added to your cart as a reward.' + (notice ? ' ' + notice : ''),
        });
      }
      props.onClose();
    }

    function submit() {
      if (browse) return;
      if (!canAdd && canValidate) {
        setS(function (prev) {
          var open = Object.assign({}, prev.open);
          v.missing.forEach(function (id) { open[id] = true; });
          return Object.assign({}, prev, { open: open });
        });
        invalidS[1]({ id: v.missing[0], attempt: invalidS[0].attempt + 1 });
        return;
      }
      if (!canAdd || doneRef.current) return;
      doneRef.current = true;
      if (rw) { redeem(); return; }
      // The judged product: an option only available at the moment stays on the line.
      var next = OO.cart.itemLine(product, { sizeId: v.sizeId, sel: s.sel, qty: s.qty, notes: notesOf(product, s) });
      if (editing) OO.store.cartReplace(line.key, next);
      else OO.store.cartAdd(next);
      if (!editing && OO.analytics) OO.analytics.addToCart(next, store); // P5-06b: add_to_cart
      if (!editing) OO.platform.haptic('medium'); // P4-09: REF addItem (a no-op outside the app shell)
      if (!editing && OO.upsell && typeof OO.upsell.afterAdd === 'function') OO.upsell.afterAdd(next, { product: product, store: store });
      if (props.onAdded) {
        props.onAdded(next, {
          kind: 'item', mode: editing ? 'update' : 'add', name: product.name, qty: next.qty,
          message: editing ? 'Cart updated' : product.name + ' added to cart',
        });
      }
      props.onClose();
    }

    var showSizePrice = !!(store.settings && store.settings.showSizePrice);
    // P2-21: the member / tag price in the heading, and the line's member saving off the CTA price (never for a reward).
    var member = rw ? null : memberOfferFor(product, props.store, cartSt.lines, cartSt.coupon);
    var hp = headingPrice(product, props.menu, member);
    var saving = member ? memberSaving(member, OO.cart.itemLine(product, { sizeId: v.sizeId, sel: s.sel, qty: s.qty }), store) : 0;
    var ctaPrice = rw ? rewardPrice(rq, word, cur) : f.money(f.round2(v.total - saving), cur);
    var usual = hp.badge || hp.note;
    var heading = (
      <ui.SheetHeading id={headingId} title={product.name} desc={product.description}
        price={rw ? (
          <span className="oo-item__price">
            {pointsLabel(rq.points, word, rq.from) || null}
            {!rq.from && rq.price > 0 ? <s className="oo-item__was"><span className="oo-sr-only">usual price </span>{f.money(rq.price, cur)}</s> : null}
            <ui.Badge tone="success" size="sm">Reward</ui.Badge>
            <ProductRating hotel={props.hotel} store={props.store} productId={product.id} />
          </span>
        ) : (
          <span className="oo-item__price">
            {hp.badge ? <span className="oo-sr-only">Member price </span> : null}
            {f.money(hp.price, cur)}
            {hp.was != null ? <s className="oo-item__was"><span className="oo-sr-only">{usual ? 'usual price ' : 'was '}</span>{f.money(hp.was, cur)}</s> : null}
            {hp.badge ? <ui.Badge tone="success" size="sm">{hp.badge}</ui.Badge> : null}
            {hp.note ? <span className="oo-item__member">{hp.note}</span> : null}
            <ProductRating hotel={props.hotel} store={props.store} productId={product.id} />
          </span>
        )} />
    );
    // Mobile without a photo: the close sits in the heading row and scrolls away with it, as it does with the
    // photo (the Sheet's own corner button would float over the options).
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign((OO.router.current() || {}).chain || props.hotel) && OO.appKit.ZetaItemHeader;
    var kitchen = OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign((OO.router.current() || {}).chain || props.hotel);
    var itemImage = kitchen && OO.appKit.Kitchen ? OO.appKit.Kitchen.artwork(kitchen.dining || {}, product) : product.image;
    var ownClose = !zeta && !wide && !itemImage;
    if (ownClose) {
      heading = (
        <div className="oo-item__headrow">
          {heading}
          <ui.IconButton icon="Close" label="Close" onClick={props.onClose} className="oo-item__close" />
        </div>
      );
    }

    var body = [];
    var badges = badgesOf(product);
    if (badges.length) {
      body.push(
        <div key="tags" className="oo-item__tags">
          {badges.map(function (b) { return <ui.Badge key={b} tone="neutral" size="sm">{b}</ui.Badge>; })}
        </div>
      );
    }
    if (product.alert && product.alert.text) {
      body.push(<ui.InlineNotice key="alert" tone="info">{product.alert.text}</ui.InlineNotice>);
    }
    if (rw) {
      // P3-17: what the points cover (see "Reward mode"), then why this selection can't be redeemed.
      var covers = store.applyDiscountToModifiers === true ? ' cover this item and its options.'
        : v.groups.length ? ' cover this item. Options you add are charged as usual.' : ' cover this item.';
      body.push(<ui.InlineNotice key="reward" tone="info" icon="Gift">{'Redeemed with your ' + word + ': they' + covers}</ui.InlineNotice>);
      if (refusal && !unavailable) body.push(<ui.InlineNotice key="refuse" tone="warn">{refusal.text}</ui.InlineNotice>);
    }
    if (unavailable) {
      body.push(
        <ui.InlineNotice key="na" tone="warn">
          {product.soldOut ? (product.inactiveReason || 'Sold out right now. Check back soon.') : judged && judged.label ? judged.label : 'This item isn’t available right now.'}
        </ui.InlineNotice>
      );
    }
    var SizeGroup=zeta&&OO.appKit.ZetaOptionGroup||ui.OptionGroup;
    if (v.sizes) {
      // REF: the size's own price when the store shows size prices (never a difference), else none.
      body.push(
        <SizeGroup key="size" min={1} max={1} count={v.sizes.chosen?1:0} type={zeta?"radio":undefined} title={v.sizes.name} hint={v.sizes.hint} required done={v.sizes.badge === 'Done'} className="oo-item__group"
          collapsible open={s.open.size !== false} onToggle={function (next) { toggleOpen('size', next); }}
          summary={v.sizes.options.filter(function (o) { return o.on; }).map(function (o) { return o.name; }).join(', ')}
          elementRef={function (el) { groupRefs.current.size = el; }} invalid={invalidS[0].id === 'size'}>
          {v.sizes.options.map(function (o) {
            return (
              <ui.OptionRow key={o.id} type={zeta?"radio":undefined} name={zeta?'zeta-size-'+product.id:undefined} value={String(o.id)} label={o.name} checked={o.on} disabled={o.disabled}
                price={o.disabled ? o.reason : showSizePrice && o.price > 0 ? f.money(o.price, cur) : null}
                onToggle={function () { if (!o.on && !o.disabled) pickSize(o.id); }} />
            );
          })}
        </SizeGroup>
      );
    }
    v.groups.forEach(function (g) {
      var bounds = (product.groups || []).filter(function(raw){return raw.id===g.id;})[0] || {};
      var group = Object.assign({}, g, { min: bounds.min, max: bounds.max,
        options: g.options.map(function (o) {
          var c = chargeLabel(o, cur);
          return Object.assign({}, o, { priceLabel: c.text, priceTone: c.tone, optionImage: o.image || null });
        }),
      });
      body.push(
        <ModGroup key={'g' + g.id} group={group} currency={cur} collapsible open={g.required ? s.open[g.id] !== false : !!s.open[g.id]}
          elementRef={function (el) { groupRefs.current[g.id] = el; }} invalid={invalidS[0].id === g.id}
          onToggleOpen={function (next) { toggleOpen(g.id, next); }}
          onPick={function (id) { withGroup(g.id, 'toggle', id); }}
          onInc={function (id) { withGroup(g.id, 'increment', id); }}
          onDec={function (id) { withGroup(g.id, 'decrement', id); }} />
      );
    });
    if (!product.hideNotes && !browse) {
      body.push(
        <div key="notes" className="oo-item__notes">
          <ui.TextArea size={wide ? 'md' : 'lg'} label={zeta ? 'Anything to add?' : 'Additional notes'} maxLength={NOTES_MAX}
            placeholder={zeta ? 'e.g. no onions, extra sauce' : 'Extra comments (optional)'} value={s.notes} onChange={function (t) { patch(function () { return { notes: clipNotes(t) }; }); }} />
          <span className="oo-item__notes-count" aria-hidden="true">{s.notes.length + '/' + NOTES_MAX}</span>
        </div>
      );
    }

    var footer = browse ? [<ui.Button key="b" block variant="outline" onClick={props.onClose}>Close</ui.Button>] : [
      // P3-17: a reward is one (REF locks a reward line at 1): no stepper.
      rw ? null : <ui.QtyStepper key="q" value={s.qty} onChange={function (n) { patch(function () { return { qty: n }; }); }} itemName={product.name} />,
      <ui.Button key="b" block price={ctaPrice} disabled={!canAdd && !canValidate} onClick={submit}>{zeta && ctaLabel === 'Add to cart' ? 'Add to order' : ctaLabel}</ui.Button>,
    ].filter(Boolean);

    return (
      <ui.Sheet open onClose={props.onClose} labelledBy={headingId} kind="item" hero={zeta ? null : itemImage || null} showClose={!zeta && !ownClose}
        headerScroll={!!zeta} header={zeta ? <OO.appKit.ZetaItemHeader hotel={props.hotel} product={product} onClose={props.onClose} /> : null}
        className={ui.cx('oo-item', !itemImage && 'oo-item--noimg')} heading={heading} footer={footer}>
        {body}
      </ui.Sheet>
    );
  }

  function selectSession(s) { return s.session; }
  // The signed-in customer's birthday as 'YYYY-MM-DD' (the age gate's prefill), else ''.
  function birthdayOf(session) {
    var b = session && session.customer ? session.customer.birthday : null;
    return ymdNumber(b) !== null ? String(b).trim().slice(0, 10) : '';
  }

  OO.pages.ItemSheet = function ItemSheet(props) {
    var data = useOverlayData(props);
    var line = useEditLine(props.editLineKey);
    var session = OO.useStore(selectSession);
    var okS = React.useState(null); // the date of birth confirmed here (kept for this sheet even when storage fails)
    var itemLine = line && line.kind !== 'combo' ? line : null;
    if (props.reward || (itemLine && itemLine.reward)) data = Object.assign({}, data, { menu: OO.model.redemptionMenu(data.menu) });
    var pid = has(props.productId) ? props.productId : (itemLine ? itemLine.itemId : null);
    var product = data.menu && has(pid) ? data.menu.products[pid] || null : null;
    var onClose = props.onClose || noop;
    if (props.open === false) return null;
    if (data.loading) return <LoadingSheet onClose={onClose} label="Loading item" kind="item" />;
    if (!product) {
      return <MissingSheet onClose={onClose} title="Item unavailable" body="We couldn’t find this item. It may have been removed from the menu." />;
    }
    // P2-21: an age-restricted item asks for the date of birth first (once per browser session and store). WL-5: not on
    // a browse-only menu, where nothing can be ordered.
    var ageHotel = has(props.hotel) ? props.hotel : (data.store && data.store.onlineKey) || '';
    if (props.browse !== true && ageRestricted(product, data.menu) && !okS[0] && !verifiedDob(ageHotel, data.store)) {
      return (
        <AgeGate key={'age-' + product.id} name={product.name} store={data.store} defaultDob={birthdayOf(session)} onDecline={onClose}
          onConfirm={function (dob) { confirmAge(ageHotel, dob); okS[1](dob); }} />
      );
    }
    var seed = product.id + '|' + (itemLine ? itemLine.key : '');
    return <ItemSheetBody key={seed} product={product} hotel={props.hotel} store={data.store} menu={data.menu} moment={props.moment} line={itemLine} reward={props.reward} onClose={onClose} onAdded={props.onAdded} browse={props.browse === true} />;
  };

  // WL-5: the browse-only menu's combo details (see the header). Read-only: nothing here can add to the cart.
  function ComboInfo(props) {
    var ui = OO.ui, f = OO.format;
    var headingId = domId('oo-combo-info');
    var combo = props.menu && props.menu.combos ? props.menu.combos[props.comboId] || null : null;
    var onClose = props.onClose || noop;
    if (!combo) {
      return <MissingSheet onClose={onClose} title="Deal unavailable" body="We couldn’t find this deal. It may have been removed from the menu." />;
    }
    var cur = (props.store && props.store.currency) || '$';
    var extras = arr(combo.groups).some(function (g) { return arr(g.options).some(function (o) { return Number(o.price) > 0; }); });
    var heading = <ui.SheetHeading kind="combo" id={headingId} title={combo.name} desc={combo.description}
      price={(extras ? 'From ' : '') + f.money(combo.price, cur)} />;
    var body = arr(combo.groups).map(function (g) {
      var names = arr(g.options).map(function (o) { return o && o.name; }).filter(Boolean);
      return (
        <div key={'g' + g.id} className="oo-combo-info__group">
          <p className="oo-combo-info__name">{g.name}</p>
          {names.length ? <p className="oo-combo-info__opts">{names.join(' · ')}</p> : null}
        </div>
      );
    });
    return (
      <ui.Sheet open onClose={onClose} labelledBy={headingId} kind="combo" hero={combo.image || null} className="oo-item oo-combo-info"
        heading={heading} footer={[<ui.Button key="b" block variant="outline" onClick={onClose}>Close</ui.Button>]}>
        {body}
      </ui.Sheet>
    );
  }
  OO.pages.ItemSheet.ComboInfo = ComboInfo;

  OO.pages.ItemSheet.parts = {
    ModGroup: ModGroup,
    CounterRow: CounterRow,
    focusInvalidGroup: focusInvalidGroup,
    useOverlayData: useOverlayData,
    useEditLine: useEditLine,
    LoadingSheet: LoadingSheet,
    MissingSheet: MissingSheet,
  };
  OO.pages.ItemSheet.tagLabel = tagLabel;
  OO.pages.ItemSheet.reward = { quote: rewardQuote, ctaLabel: rewardCta }; // P3-17
  OO.pages.ItemSheet.age = {
    MIN_AGE: MIN_AGE,
    restricted: ageRestricted,
    minimumAge: minimumAge,
    ageOn: ageOn,
    check: checkAge,
    todayOf: todayOf,
    verifiedDob: verifiedDob,
    confirm: confirmAge,
    forget: forgetAge,
    forgetAll: forgetAllAges, // P3-LOGOUT
  };
})();


// ===================== src/pages/upsell.jsx =====================
// src/pages/upsell.jsx: OO.upsell and OO.pages.Upsell, the upsell (Batch 2 P2-19; REF CartContext
// fetchAndShowProductSuggestions + ProductSuggestionModal, useCartSuggestions + OnlineOrderCartDrawer; inventory B §1.3
// "Suggestions"): product suggestions after an add (E07 GET product_suggestion_list_online?online_key&id) and cart
// suggestions (E08 GET cart_suggestion_list_online?online_key, merged with E07 for each product in the cart).
//
// Sources. OO.api.getProductSuggestions(hotel, itemId) and OO.api.getCartSuggestions(hotel), when an adapter has them
// (the real adapter, BATCH2-PLAN §5 E07 / E08: soft and cached). The fixture adapter has neither, so a fixture store's
// upsell stays empty and the Batch-1 demo is unchanged. Any failure is an empty list: the upsell never blocks an order.
// Rows → menu products BY ID (P2-15: a suggestion row is no menu entity). A row whose itemId isn't a product of the
// menu is dropped (it can't be ordered here). The menu product, never the row, is what is shown, judged, priced and
// added: its sizes, its groups with their max_no_price_count allowance (Group.freeCount; OO.modifiers prices it) and
// the menu's price (a pricetag's once the menu is priced). The row's own groups, sizes and sale_price are never read.
// D-07 (owner decision; REF d298a7e hides instead). Every product is judged with OO.model.displayState at the menu's
// moment (useMoment below: the draft's slot or now, the order type's domain, the active named menu): hide → dropped;
// grey → listed greyed with its label ("Available Tomorrow At 7:00 AM", "Sold out") and never addable; normal →
// addable. The addable ones come first (stable), then the greyed ones.
// Category exclusions (B-Δ2; REF useSuggestionAvailability + isExcludedCategory). A row is dropped when its
// category_id, or its product's categoryId, is excluded: the ids fed through OO.upsell.exclude plus the held table
// context's excludedCategoryIds for this hotel (the menu already hides those categories). Positive whole numbers only,
// as REF. The feed (P4-02, mode qr): src/lib/store.jsx calls OO.upsell.exclude(ids, hotel) with the table group's
// excluded_category_ids each time a table menu arrives with them (get_menu's body table_id answer); the context then
// carries the same ids (E19's before the menu came). Outside mode qr nothing feeds it and there is no table context.
//
// After an add. The item sheet calls OO.upsell.afterAdd(line, { product, store }) right after an ADD (never an update).
// A combo, a coupon's free line or a reward line never asks. Once per product per store and page session (REF): a
// product whose list came back non-empty is "seen" and never asks again, one in flight isn't asked twice, and another
// store clears both (REF seenSuggestionItemIds, inFlightSuggestionItemIds, suggestionScopeRef). The answer becomes the
// pending offer. <OO.pages.Upsell.AfterAdd> (OO.pages.MenuOverlays mounts it on the menu) shows it as a dialog, "You
// might also like", when one or more suggestions can be added now, and never an offer asked for before it mounted. It
// lists every suggestion, without the cart check (REF). Add puts a product with nothing to choose straight in the cart
// (quickLine, priced by OO.store.cartAdd) and the row then reads "Added". Customise dismisses the offer, marks that
// product seen (so its own add asks nothing: REF fromSuggestion) and opens the item sheet. "No thanks, continue" (or
// "Done" after an add), the close button, ESC and the scrim dismiss it.
// Age gate (W3-fix #1; P2-21's OO.pages.ItemSheet.age). Add never skips it: an age-restricted product (ageGated: its
// own `av` or its category's) while this session's age isn't verified for the store goes the Customise way, so the item
// sheet asks "Verify your age" first; once verified, Add adds it as any other.
// The cart. <OO.pages.Upsell hotel store menu lines /> merges the cart list with each in-cart product's list, in that
// order (the first copy wins), without the products already in the cart (REF useCartSuggestions). Combo lines ask
// nothing: REF fetches with a combo id as a product id, a quirk not copied. An empty cart lists at most 4 under
// "Popular right now", any other at most 8 under "You might also like" (REF OnlineOrderCartDrawer). Add and Customise
// as above (Customise opens the item sheet over the cart; Add says so in a toast; an age-gated Add opens the sheet).
// Nothing left → the section is absent. While a new merge loads, the previous one stays (minus what is in the cart now).
// P5-06b: every Add (the dialog's and the cart section's) sends add_to_cart (OO.analytics.addToCart, src/lib/analytics.jsx).
//
// OO.upsell (pure helpers first; tests/upsell-p219.test.mjs):
//   rowsOf(body) → [{ id, categoryId }]: an E07 / E08 body ({ status: true | 'true' | 1, data: [row] }, or a bare row
//       array). Any other body (status false, a 404 page, null) is []. id = itemId › product_id › id, categoryId =
//       category_id › categoryId (positive whole numbers, else null); a row without an id is dropped, and so is a
//       repeat of one (the first copy wins).
//   resolve(rows, menu, { moment, excluded, inCart, limit }) → [Suggestion]: the rows mapped onto menu.products by id,
//       without the excluded categories and the inCart ids ({ [id]: true }), judged at `moment` (displayState's opts),
//       hidden ones dropped, addable first, at most `limit` (when > 0). Suggestion = { product, display, addable,
//       label (the grey label; null when addable), options (hasOptions) }.
//   hasOptions(product) → bool: a size or a visible option group to choose (REF suggestionHasOptions) → Customise.
//   quickLine(product) → the item line of one unit with nothing chosen (OO.cart.itemLine) | null when hasOptions.
//   ageGated(product, menu, hotel, store) → bool: the product is age-restricted (OO.pages.ItemSheet.age.restricted)
//       and this session has no verified date of birth for the store (age.verifiedDob(hotel, store)): Add then opens
//       the item sheet instead (see "Age gate"). false without the item sheet's age helpers.
//   exclude(ids, hotel?) → the excluded category ids now fed (positive whole numbers, unique): the category-exclusion
//       hook store.jsx feeds in qr mode (P4-02, see "Category exclusions"). With a hotel they apply to that store
//       only; [] or null clears them.
//   excluded(hotel, table?) → [ids]: the fed ids that apply to hotel, plus table.excludedCategoryIds when that table
//       context (default OO.store.table()) belongs to hotel.
//   cartIds(lines) → [itemId]: the distinct item ids of the non-combo lines, in line order.
//   productRows(hotel, itemId) · cartRows(hotel) → Promise<rows> (never rejects; [] without the adapter method).
//   cartSuggestions(hotel, lines) → Promise<rows>: the cart merge above (not yet judged: resolve does that).
//   afterAdd(line, { product, store }) → undefined (see "After an add"). The store is the cart's (OO.store.get().hotel).
//   prefetch(hotel, itemId) → Promise<rows>: warms the adapter's read cache (an item sheet may call it on open, as
//       REF prefetches when its product modal opens, so the dialog comes up at once after the add).
//   offer() → null | { seq, hotel, productId, name, rows }: the pending offer (a stable object until it changes).
//   subscribe(fn) → unsubscribe (fn runs when the offer changes).   dismiss(seq?) → clears the offer (only that one
//   when seq is given).   markSeen(hotel, itemId).   seq() → the number of the latest after-add request.
//   useMoment(hotel, store, menu, moment?) (a hook) → the judging moment: the given one (the menu passes its own),
//       else OO.pages.Menu.shared.useDisplay's, which the menu page judges its cards with. With a moment given it calls
//       useDisplay with no menu, so no second whole-menu display is computed.
//   __reset() (tests) forgets the store scope, the seen / in-flight ids, the offer, its numbering and the exclusions.
// Components:
//   <OO.pages.Upsell hotel store menu lines moment? /> the cart's section (cart.jsx mounts it).
//   <OO.pages.Upsell.AfterAdd hotel store menu moment? since? onOpen? /> the after-add dialog host (OO.pages.MenuOverlays
//       mounts it). since: the offer number it shows from (default: the latest when it mounted). onOpen(open) (W3-fix
//       #9): called after each change of whether the dialog, or the item sheet its Customise opened, is up, and with
//       false on unmount (MenuOverlays' busy signal: the menu's coupon prompt waits meanwhile).
//   OO.pages.Upsell.parts = { Row, List, Dialog } (presentational: the 390 / 1440 render checks use them).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var U = (OO.upsell = OO.upsell || {});

  var CART_LIMIT = 8;   // REF: visibleSuggestions.slice(0, 8) under the cart lines
  var EMPTY_LIMIT = 4;  // REF: .slice(0, 4) "Popular Right Now" in an empty cart

  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  // A positive whole number (REF isExcludedCategory: Number.isSafeInteger(id) && id > 0), else null.
  function posInt(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(typeof v === 'string' ? v.trim() : v);
    return isFinite(n) && n > 0 && Math.floor(n) === n && n <= 9007199254740991 ? n : null;
  }
  function firstOf(o, keys) {
    for (var i = 0; i < keys.length; i++) if (o[keys[i]] !== undefined && o[keys[i]] !== null && o[keys[i]] !== '') return o[keys[i]];
    return null;
  }
  // REF isSipoSuccessStatus: true, 'true' or 1.
  function okStatus(s) { return s === true || s === 1 || s === '1' || (typeof s === 'string' && s.trim().toLowerCase() === 'true'); }

  // ---- rows (E07 / E08 bodies) ----------------------------------------------------------------------------------
  function rowsOf(body) {
    var list = Array.isArray(body) ? body : body && typeof body === 'object' && okStatus(body.status) ? arr(body.data) : [];
    var seen = {};
    var out = [];
    list.forEach(function (r) {
      if (!r || typeof r !== 'object') return;
      var id = posInt(firstOf(r, ['itemId', 'product_id', 'id']));
      if (!id || seen[id]) return;
      seen[id] = true;
      out.push({ id: id, categoryId: posInt(firstOf(r, ['category_id', 'categoryId'])) });
    });
    return out;
  }

  // ---- menu products, judged ------------------------------------------------------------------------------------
  function hasOptions(product) {
    if (!product) return false;
    if (arr(product.sizes).length) return true;
    return !!(OO.modifiers && OO.modifiers.visibleGroups(product, null).length);
  }
  function quickLine(product) {
    if (!product || hasOptions(product)) return null;
    return OO.cart.itemLine(product, { sizeId: null, sel: {}, qty: 1, notes: '' });
  }
  // W3-fix #1: a quick add must not skip P2-21's age gate (see "Age gate" in the header).
  function ageGated(product, menu, hotel, store) {
    var A = OO.pages.ItemSheet && OO.pages.ItemSheet.age;
    if (!product || !A || typeof A.restricted !== 'function' || !A.restricted(product, menu)) return false;
    return !A.verifiedDob(str(hotel), store || null);
  }
  function resolve(rows, menu, opts) {
    opts = opts || {};
    var products = menu && menu.products;
    if (!products) return [];
    var ex = {};
    arr(opts.excluded).forEach(function (id) { var n = posInt(id); if (n) ex[n] = true; });
    var inCart = opts.inCart || {};
    var ready = [], greyed = [], seen = {};
    arr(rows).forEach(function (r) {
      var id = r && posInt(r.id);
      if (!id || seen[id] || own(inCart, id)) return;
      seen[id] = true;
      var p = own(products, id) ? products[id] : null;
      if (!p || p.kind === 'combo') return; // not a product of this menu (P2-15)
      var pc = posInt(p.categoryId);
      if ((r.categoryId && ex[r.categoryId]) || (pc && ex[pc])) return;
      var d = OO.model.displayState(menu, p, opts.moment || {}) || { show: 'normal', label: null };
      if (d.show === 'hide') return;
      var item = { product: p, display: d, addable: d.show === 'normal', label: d.show === 'grey' ? d.label || 'Not available' : null, options: hasOptions(p) };
      (item.addable ? ready : greyed).push(item);
    });
    var out = ready.concat(greyed);
    return opts.limit > 0 ? out.slice(0, opts.limit) : out;
  }

  // ---- the category-exclusion hook (P4-02) ----------------------------------------------------------------------
  var fed = { hotel: null, ids: [] };
  function uniqIds(list) {
    var out = [];
    arr(list).forEach(function (v) { var n = posInt(v); if (n && out.indexOf(n) < 0) out.push(n); });
    return out;
  }
  function exclude(ids, hotel) {
    fed = { hotel: hotel === undefined || hotel === null || hotel === '' ? null : str(hotel), ids: uniqIds(ids) };
    return fed.ids.slice();
  }
  function excluded(hotel, table) {
    var key = str(hotel);
    var t = table === undefined ? (OO.store && OO.store.table ? OO.store.table() : null) : table;
    var out = fed.hotel === null || fed.hotel === key ? fed.ids.slice() : [];
    if (t && typeof t === 'object' && str(t.hotel) === key) out = uniqIds(out.concat(arr(t.excludedCategoryIds)));
    return out;
  }

  // ---- reads ------------------------------------------------------------------------------------------------------
  function read(method, args) {
    var api = OO.api;
    if (!api || typeof api[method] !== 'function') return Promise.resolve([]);
    try {
      return Promise.resolve(api[method].apply(api, args)).then(rowsOf, function () { return []; });
    } catch (e) { return Promise.resolve([]); }
  }
  function productRows(hotel, itemId) {
    return str(hotel) && posInt(itemId) ? read('getProductSuggestions', [str(hotel), posInt(itemId)]) : Promise.resolve([]);
  }
  function cartRows(hotel) { return str(hotel) ? read('getCartSuggestions', [str(hotel)]) : Promise.resolve([]); }
  function cartIds(lines) {
    var out = [];
    arr(lines).forEach(function (l) {
      var id = l && l.kind !== 'combo' ? posInt(l.itemId) : null;
      if (id && out.indexOf(id) < 0) out.push(id);
    });
    return out;
  }
  function cartSuggestions(hotel, lines) {
    var ids = cartIds(lines);
    var inCart = {};
    ids.forEach(function (id) { inCart[id] = true; });
    return Promise.all([cartRows(hotel)].concat(ids.map(function (id) { return productRows(hotel, id); }))).then(function (lists) {
      var seen = {};
      var out = [];
      lists.forEach(function (list) {
        list.forEach(function (r) {
          if (inCart[r.id] || seen[r.id]) return;
          seen[r.id] = true;
          out.push(r);
        });
      });
      return out;
    });
  }

  // ---- after an add: the session's offer -----------------------------------------------------------------------
  var S = { hotel: null, seen: {}, inflight: {}, offer: null, seq: 0, subs: [] };
  function notify() { S.subs.slice().forEach(function (fn) { try { fn(); } catch (e) { /* a broken listener stays alone */ } }); }
  function setOffer(o) { S.offer = o; notify(); }
  // Another store: a fresh scope (REF clears its seen / in-flight sets when the restaurant changes).
  function scope(hotel) {
    if (S.hotel === hotel) return;
    S.hotel = hotel;
    S.seen = {};
    S.inflight = {};
    if (S.offer) setOffer(null);
  }
  function cartHotel() {
    try { return str(OO.store && OO.store.get ? OO.store.get().hotel : ''); } catch (e) { return ''; }
  }
  function afterAdd(line, info) {
    if (!line || typeof line !== 'object' || line.kind === 'combo' || line.free || line.reward) return;
    var product = info && info.product;
    var id = posInt(product && product.id != null ? product.id : line.itemId);
    var hotel = cartHotel();
    if (!id || !hotel) return;
    scope(hotel);
    if (S.seen[id] || S.inflight[id]) return;
    S.inflight[id] = true;
    var seq = ++S.seq;
    var name = str(product && product.name) || str(line.name);
    productRows(hotel, id).then(function (rows) {
      if (S.hotel !== hotel) return; // the store changed meanwhile: that scope is gone
      delete S.inflight[id];
      if (!rows.length) return;
      S.seen[id] = true;
      setOffer({ seq: seq, hotel: hotel, productId: id, name: name, rows: rows });
    });
  }
  function markSeen(hotel, itemId) {
    var id = posInt(itemId);
    if (!id || !str(hotel)) return;
    scope(str(hotel));
    S.seen[id] = true;
  }
  function dismiss(seq) {
    if (!S.offer || (seq !== undefined && seq !== null && S.offer.seq !== seq)) return;
    setOffer(null);
  }
  function subscribe(fn) {
    S.subs.push(fn);
    return function () { var i = S.subs.indexOf(fn); if (i >= 0) S.subs.splice(i, 1); };
  }
  function offer() { return S.offer; }

  // ---- the judging moment ---------------------------------------------------------------------------------------
  // Without the menu page's hook (a test loading this file alone): now, the pickup domain of the mode, no named menu.
  function plainDisplay(hotel, store, menu, mode) {
    if (!store || !menu) return { moment: null };
    var now = OO.data && OO.data.now ? OO.data.now().getTime() : Date.now();
    return { moment: { now: now, today: now, orderType: 'PICKUP', domain: OO.model.domain('PICKUP', mode), timeZone: store.timezone, menuId: null } };
  }
  function useMoment(hotel, store, menu, given) {
    var shared = OO.pages.Menu && OO.pages.Menu.shared;
    var hook = shared && typeof shared.useDisplay === 'function' ? shared.useDisplay : plainDisplay;
    var d = hook(hotel, store, given ? null : menu, OO.mode);
    return given || (d && d.moment) || null;
  }

  // ---- presentation ---------------------------------------------------------------------------------------------
  // The price as the menu card shows it (menu.jsx priceNode): the base price struck through under a membership tag,
  // else the banner (was) price when it is above the price.
  function priceNode(p, menu, cur) {
    var f = OO.format;
    var member = !!(menu && menu.pricetag && menu.pricetag.membership && p.pricetagId != null && p.basePrice != null && p.price < p.basePrice);
    var was = member ? p.basePrice : (p.wasPrice != null && p.wasPrice > p.price ? p.wasPrice : null);
    if (was == null) return f.money(p.price, cur);
    return (
      <React.Fragment>
        {member ? <span className="oo-sr-only">Member price </span> : null}
        {f.money(p.price, cur)}
        <s className="oo-ups-row__was"><span className="oo-sr-only">{member ? 'usual price ' : 'was '}</span>{f.money(was, cur)}</s>
      </React.Fragment>
    );
  }

  // One suggestion: photo, name, one line of description, price, then Add / Customise / Added, or the grey label.
  // props: item (Suggestion), menu, currency, added, onAdd(item), onCustomise(item).
  function Row(props) {
    var ui = OO.ui;
    var it = props.item, p = it.product;
    var action;
    if (!it.addable) action = <ui.Badge tone="neutral" size="sm" className="oo-ups-row__label">{it.label}</ui.Badge>;
    else if (props.added) {
      action = <span className="oo-ups-row__added" role="status"><OO.icons.Check size={14} strokeWidth={3} />Added</span>;
    } else if (it.options) {
      action = (
        <ui.Button size="xs" variant="outline" className="oo-ups-row__btn" aria-label={'Customise ' + p.name}
          onClick={function () { props.onCustomise(it); }}>Customise</ui.Button>
      );
    } else {
      action = (
        <ui.Button size="xs" icon="Plus" iconSize={14} className="oo-ups-row__btn" aria-label={'Add ' + p.name + ' to cart'}
          onClick={function () { props.onAdd(it); }}>Add</ui.Button>
      );
    }
    return (
      <li className={ui.cx('oo-ups-row', !it.addable && 'is-off')}>
        <span className={ui.cx('oo-ups-row__media', !p.image && 'oo-ups-row__media--none')} aria-hidden="true">
          {p.image ? <img className="oo-ups-row__img" src={p.image} alt="" loading="lazy" /> : <OO.icons.ImagePlaceholder size={22} />}
        </span>
        <span className="oo-ups-row__body">
          <span className="oo-ups-row__name">{p.name}</span>
          {p.description ? <span className="oo-ups-row__desc">{p.description}</span> : null}
          <span className="oo-ups-row__price">{priceNode(p, props.menu, props.currency)}</span>
        </span>
        <span className="oo-ups-row__act">{action}</span>
      </li>
    );
  }

  // props: items, menu, currency, added ({ [productId]: true }), onAdd, onCustomise, label (the list's name).
  function List(props) {
    var added = props.added || {};
    return (
      <ul className="oo-ups-list" aria-label={props.label}>
        {props.items.map(function (it) {
          return (
            <Row key={it.product.id} item={it} menu={props.menu} currency={props.currency} added={!!added[it.product.id]}
              onAdd={props.onAdd} onCustomise={props.onCustomise} />
          );
        })}
      </ul>
    );
  }

  // The after-add dialog (REF ProductSuggestionModal). props: name (the product just added), items, menu, currency,
  // added, onAdd, onCustomise, onClose.
  function Dialog(props) {
    var ui = OO.ui;
    var any = Object.keys(props.added || {}).length > 0;
    return (
      <ui.Sheet open variant="dialog" title="You might also like" onClose={props.onClose} className="oo-ups-dialog"
        footer={<ui.Button block onClick={props.onClose}>{any ? 'Done' : 'No thanks, continue'}</ui.Button>}>
        <p className="oo-ups-dialog__lead">
          <span className="oo-ups-dialog__added"><OO.icons.Check size={12} strokeWidth={3} />Added to cart</span>
          {props.name ? <span className="oo-ups-dialog__with">People often add these with <b>{props.name}</b></span> : null}
        </p>
        <List items={props.items} menu={props.menu} currency={props.currency} added={props.added} label="Suggested items"
          onAdd={props.onAdd} onCustomise={props.onCustomise} />
      </ui.Sheet>
    );
  }

  function selectTable(s) { return s.table || null; }
  function addedMessage(p) { return p.name + ' added to cart'; }

  // The item sheet for a suggestion with options, and the toast both hosts use. (A hook: ids from useState.)
  function useCustomise(hotel, store, menu, moment) {
    var sheetS = React.useState(null);  // the product id being customised
    var toastS = React.useState(null);  // { id, text }
    var seq = React.useRef(0);
    function say(text) { seq.current += 1; toastS[1]({ id: seq.current, text: text }); }
    var Item = OO.pages.ItemSheet;
    var sheet = sheetS[0] != null && Item ? (
      <Item key={'ups' + sheetS[0]} hotel={hotel} store={store} menu={menu} productId={sheetS[0]} moment={moment}
        onClose={function () { sheetS[1](null); }}
        onAdded={function (line, info) { sheetS[1](null); say(info && info.message ? info.message : 'Added to cart'); }} />
    ) : null;
    var toast = toastS[0] ? (
      <OO.ui.Toast key={toastS[0].id} open onClose={function () { toastS[1](null); }}>{toastS[0].text}</OO.ui.Toast>
    ) : null;
    return {
      open: function (product) { markSeen(hotel, product.id); sheetS[1](product.id); },
      say: say,
      busy: !!sheet, // the item sheet is up
      nodes: sheet || toast ? <React.Fragment>{sheet}{toast}</React.Fragment> : null,
    };
  }

  // ---- <OO.pages.Upsell.AfterAdd> (the menu's dialog host) --------------------------------------------------------
  function AfterAdd(props) {
    var hotel = str(props.hotel), store = props.store, menu = props.menu;
    var cur = store ? store.currency : undefined;
    var since = React.useState(function () { return props.since != null ? props.since : S.seq; })[0];
    var pending = React.useSyncExternalStore(subscribe, offer, offer);
    var table = OO.useStore(selectTable);
    var moment = useMoment(props.hotel, store, menu, props.moment);
    var addedS = React.useState({ seq: 0, ids: {} });
    var c = useCustomise(hotel, store, menu, moment);
    var live = pending && pending.seq > since && pending.hotel === hotel ? pending : null;
    var items = React.useMemo(function () {
      return live && menu ? resolve(live.rows, menu, { moment: moment, excluded: excluded(hotel, table) }) : [];
    }, [live, menu, moment, hotel, table]);
    var show = items.some(function (it) { return it.addable; });
    // W3-fix #9: tell the host whether the dialog, or the sheet its Customise opened, is up (see the header).
    var open = show || c.busy;
    var onOpen = React.useRef(props.onOpen);
    onOpen.current = props.onOpen;
    React.useEffect(function () { if (typeof onOpen.current === 'function') onOpen.current(open); }, [open]);
    React.useEffect(function () {
      return function () { if (typeof onOpen.current === 'function') onOpen.current(false); };
    }, []);
    var added = live && addedS[0].seq === live.seq ? addedS[0].ids : {};
    function close() { if (live) dismiss(live.seq); }
    function add(it) {
      if (live && ageGated(it.product, menu, hotel, store)) { customise(it); return; } // the sheet's age gate first
      var line = quickLine(it.product);
      if (!line || !live) return;
      OO.store.cartAdd(line);
      if (OO.analytics) OO.analytics.addToCart(line, store); // P5-06b: add_to_cart
      var ids = Object.assign({}, added);
      ids[it.product.id] = true;
      addedS[1]({ seq: live.seq, ids: ids });
    }
    function customise(it) { close(); c.open(it.product); }
    return (
      <React.Fragment>
        {show ? <Dialog name={live.name} items={items} menu={menu} currency={cur} added={added} onAdd={add} onCustomise={customise} onClose={close} /> : null}
        {c.nodes}
      </React.Fragment>
    );
  }

  // ---- <OO.pages.Upsell> (the cart's section) ---------------------------------------------------------------------
  OO.pages.Upsell = function Upsell(props) {
    var ui = OO.ui;
    var hotel = str(props.hotel), store = props.store, menu = props.menu, lines = arr(props.lines);
    var cur = store ? store.currency : undefined;
    var headingId = 'oo-ups-h' + React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var table = OO.useStore(selectTable);
    var moment = useMoment(props.hotel, store, menu, props.moment);
    var key = hotel + '|' + cartIds(lines).join(',');
    // OO.useAsync keeps the last merge while the next one loads (a new product in the cart).
    var q = OO.useAsync(function () { return hotel ? cartSuggestions(hotel, lines) : []; }, [key]);
    var c = useCustomise(hotel, store, menu, moment);
    var rows = arr(q.data);
    var limit = lines.length ? CART_LIMIT : EMPTY_LIMIT;
    var items = React.useMemo(function () {
      var inCart = {};
      lines.forEach(function (l) { var id = l && posInt(l.itemId); if (id) inCart[id] = true; });
      return resolve(rows, menu, { moment: moment, excluded: excluded(hotel, table), inCart: inCart, limit: limit });
    }, [rows, menu, moment, hotel, table, lines, limit]);
    function add(it) {
      if (ageGated(it.product, menu, hotel, store)) { c.open(it.product); return; } // the sheet's age gate first
      var line = quickLine(it.product);
      if (!line) return;
      OO.store.cartAdd(line);
      if (OO.analytics) OO.analytics.addToCart(line, store); // P5-06b: add_to_cart
      c.say(addedMessage(it.product));
    }
    var title = lines.length ? 'You might also like' : 'Popular right now';
    var section = items.length ? (
      <section className={ui.cx('oo-ups', !lines.length && 'oo-ups--empty-cart')} aria-labelledby={headingId}>
        <ui.SectionTitle id={headingId} className="oo-ups__title">{title}</ui.SectionTitle>
        <List items={items} menu={menu} currency={cur} label={title} onAdd={add} onCustomise={function (it) { c.open(it.product); }} />
      </section>
    ) : null;
    if (!section && !c.nodes) return null;
    return <React.Fragment>{section}{c.nodes}</React.Fragment>;
  };
  OO.pages.Upsell.AfterAdd = AfterAdd;
  OO.pages.Upsell.parts = { Row: Row, List: List, Dialog: Dialog };

  U.rowsOf = rowsOf;
  U.resolve = resolve;
  U.hasOptions = hasOptions;
  U.quickLine = quickLine;
  U.ageGated = ageGated;
  U.exclude = exclude;
  U.excluded = excluded;
  U.cartIds = cartIds;
  U.productRows = productRows;
  U.cartRows = cartRows;
  U.cartSuggestions = cartSuggestions;
  U.afterAdd = afterAdd;
  U.prefetch = productRows;
  U.offer = offer;
  U.subscribe = subscribe;
  U.dismiss = dismiss;
  U.markSeen = markSeen;
  U.seq = function () { return S.seq; };
  U.useMoment = useMoment;
  U.__reset = function () {
    S.hotel = null;
    S.seen = {};
    S.inflight = {};
    S.offer = null;
    S.seq = 0;
    fed = { hotel: null, ids: [] };
    notify();
  };
})();


// ===================== src/pages/combo-builder.jsx =====================
// src/pages/combo-builder.jsx: OO.pages.ComboBuilder, the combo builder overlay (design "K01 Combo mobile", "K03
// Family combo mobile" and "K02 Combo desktop", docs/online-order/design/ordering-flow.dc.txt). It is not a routed
// page: the Menu opens it for a combo / deal and the Cart opens it with editLineKey. Owner: T3b. Styles:
// styles/combo.css (plus the shared modifier-group parts of src/pages/item-sheet.jsx and styles/item.css).
//
// <OO.pages.ComboBuilder hotel store menu comboId editLineKey moment onClose onAdded />
//   store, menu   the normalised OO.model Store / Menu. With no `menu` the builder loads it itself (OO.useHotel(hotel)).
//   comboId       a menu.combos key. With editLineKey it defaults to the edited line's comboId.
//   editLineKey   edit that combo line: the picks (with their sizes), quantity and notes are rebuilt from it
//                 (OO.combo.fromLine) and the builder opens at Review (or at the first step that is no longer
//                 complete). The CTA reads "Update combo"; it replaces the line in place (OO.store.cartReplace) and
//                 closes.
//   moment        the menu page's judging opts (D-07, P2-17): the deal is orderable when OO.model.displayState at that
//                 moment says 'normal'. Choices, sizes and modifiers use the same time and channel, retaining the
//                 menu's prices. Without it (the cart's edit), derive the moment from the store's checkout draft.
//   onClose()     the back arrow on the intro, ESC, the scrim / close button, and after an update.
//   onAdded(line, info)  after a combo went into the cart. info = { kind: 'combo', mode: 'add' | 'update', name, qty,
//                 message: 'Combo added to cart' | 'Cart updated' }. After an ADD the builder stays open: it resets
//                 to its intro with the green "Combo added to cart" notice (the design's `added` state), so the
//                 parent may close it from onAdded or leave it open.
//   The overlay is open while mounted (open={false} renders nothing).
//   P5-06b (src/lib/analytics.jsx): an add builder sends view_item when it opens and add_to_cart after each add
//   (OO.analytics.viewItem / addToCart); an edit sends neither.
//
// Rendering is pure OO.combo: new combos open at OO.combo.init(combo), every action returns a new state, and OO.combo.view()
// says what to draw: intro → for each step an item pick → that item's options → … → review → add to cart. An intro
// with all required choices preselected can add directly; each group's gear opens that group and returns here. Items
// with nothing to choose skip straight on; one-option groups are added automatically; Review rows Change / Customise
// and come back to Review.
//   Sizes (OO.combo's header): a 'choice' pick's options phase starts with a required "Size" group (view().sizes; no
//     preselection, no prices: the combo price stands) and shows its option groups once a size is chosen; a 'fixed'
//     size is a read-only line in the current-item card ("Size · Set By Combo: Large").
//   An item that can't be ordered (a blocked required group, no usable size) is disabled in the list with
//     "Unavailable" and its reason, and a required group with no orderable item makes the whole deal unavailable
//     (view().available / unavailableGroup: the warning notice and a disabled CTA).
//   Free modifier units (OO.modifiers allowances) read "Free" on the option rows and "(Free)" in the review text.
//   Option photos and alerts (W3-fix #2; the item sheet's P2-20 rows): an option with a photo (Option.image:
//     mod_image) shows it beside its name, and a picked option with an alert (Option.alert: REF is_alert + alert_text)
//     shows it under its group, read from the current pick's own option groups (OO.combo.view() carries neither).
//   Mobile (K01 / K03): a full-height sheet. The black header (back · name over the step label · cart pill), the
//     segment step bar once started, the phase content, and a white footer (Combo total, or the quantity stepper
//     at Review, + the CTA).
//   Desktop (K02): a split modal. Left: combo photo, name / description / price and the numbered step list. Right:
//     the back · step label · close row, the phase content and the footer.
// The back arrow steps back (OO.combo.back); on the intro it closes the builder. Arrow keys only move focus through
// the item list (a native radio would pick, and so advance, on every arrow press); Space picks.
// An incomplete required product count, size or modifier selection leaves the CTA clickable: it focuses, highlights
// and shakes the first missing group, as the product sheet does. It never advances or writes an incomplete combo.
//
// OO.pages.ComboBuilder.logic (pure; tests/w3fix-combo.test.mjs):
//   modGroup(viewGroup, option) → the item sheet's ModGroup shape (OO.pages.ItemSheet.parts.ModGroup) of one
//       OO.combo.view() group (.mods[i], or .sizes): { id, name, hint, badge, required, radio, counter, full, options:
//       [{ id, name, priceLabel, on, count, disabled, optionImage }], alerts: [{ id, name, text }] }. optionImage and
//       the alerts (the picked options' only) come from `option` (the current pick's ComboOption, OO.combo.entries:
//       the group of the same id, its option of the same id); none for a size group or without `option`.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  function noop() {}
  function arr(v) { return Array.isArray(v) ? v : []; }
  function has(v) { return v !== undefined && v !== null && v !== ''; }
  function domId(prefix) { return prefix + React.useId().replace(/[^a-zA-Z0-9_-]/g, ''); }
  function parts() { return OO.pages.ItemSheet.parts; }

  // "From $29.90" when any pick or option can add to the price (K02 aside), else the plain price.
  function hasExtras(combo) {
    return arr(combo.groups).some(function (g) {
      return arr(g.options).some(function (o) {
        return Number(o.extraPrice) > 0 || arr(o.groups).some(function (gr) { return arr(gr.options).some(function (x) { return Number(x.price) > 0; }); });
      });
    });
  }

  // ---- the options phase's groups (W3-fix #2; see OO.pages.ComboBuilder.logic in the header) ------------------------
  // An option's alert text (Option.alert = { text }), as OO.modifiers reads it; '' for none.
  function alertText(o) {
    return o && o.alert && typeof o.alert === 'object' ? String(o.alert.text == null ? '' : o.alert.text).trim() : '';
  }
  function modGroup(m, option) {
    var src = arr(option && option.groups).filter(function (g) { return g.id === m.id; })[0];
    var rows = {};
    arr(src && src.options).forEach(function (o) { rows[o.id] = o; });
    return {
      id: m.id, name: m.name, hint: m.hint, badge: m.badge, required: m.required, radio: m.radio, counter: m.counter, full: !!(m.counter && m.full),
      options: arr(m.opts).map(function (o) {
        var row = rows[o.id];
        return { id: o.id, name: o.label, priceLabel: o.price, on: o.on, count: o.count, disabled: o.disabled, optionImage: (row && row.image) || null };
      }),
      alerts: arr(m.opts).filter(function (o) { return o.on && alertText(rows[o.id]); }).map(function (o) {
        return { id: o.id, name: o.label, text: alertText(rows[o.id]) };
      }),
    };
  }

  function selectDraft(s) { return s.checkout; }
  function editMoment(store, draft, hotel) {
    var S = OO.pages.Menu && OO.pages.Menu.shared;
    if (!store || !S || typeof S.orderMoment !== 'function') return null;
    var mine = draft && (!draft.hotel || String(draft.hotel) === String(hotel)) ? draft : null;
    try { return Object.assign({ timeZone: store.timezone, menuId: null }, S.orderMoment(store, mine, OO.mode)); } catch (e) { return null; }
  }
  function useComboAt(combo, hotel, moment) {
    var at = null;
    if (moment && has(hotel) && OO.data && typeof OO.data.menuAt === 'function') {
      var snap = OO.data.menuAt(hotel, { now: moment.now, domain: moment.domain });
      at = snap && snap.combos ? snap.combos[combo.id] || null : null;
    }
    return React.useMemo(function () { return OO.combo.withAvailability(combo, at); }, [combo, at]);
  }

  // ---- the builder ---------------------------------------------------------------------------------------------
  function ComboBody(props) {
    var ui = OO.ui, C = OO.combo, f = OO.format, P = parts();
    var line = props.line;
    var draft = OO.useStore(selectDraft);
    var moment = props.moment || editMoment(props.store, draft, props.hotel);
    var combo = useComboAt(props.combo, props.hotel, moment);
    var store = props.store || {};
    var cur = store.currency || '$';
    var editing = !!line;
    var wide = ui.useWide();
    var titleId = domId('oo-combo-h');      // heading of the current phase: the focus target after a step change
    var headTitleId = domId('oo-combo-n');  // mobile header title (dialog name)
    var asideTitleId = domId('oo-combo-a'); // desktop aside title (dialog name)
    var radioName = domId('oo-combo-pick');
    var st = React.useState(function () { return line ? C.fromLine(combo, line) : C.init(combo); });
    var s = st[0], setS = st[1];
    var cartCount = OO.useStore(function (x) { return OO.cart.count(x.cart.lines); });
    var flowRef = React.useRef(null);
    var doneRef = React.useRef(false);
    var invalidS = React.useState({ id: null, attempt: 0 });
    var groupRefs = React.useRef({});
    var v = C.view(s, combo, { currency: cur });
    var judged = moment && props.menu ? OO.model.displayState(props.menu, combo, moment) : null;
    var available = judged ? judged.show === 'normal' : v.available; // the deal's own rules, and a required group with no orderable item (OO.combo)
    var canValidate = available && !v.ctaEnabled && v.missing.length > 0 && !v.mods.some(function (m) { return m.blocked; });
    var step = !v.isIntro && !v.isReview ? C.steps(combo)[s.g] : null;
    var stepLabel = v.stepLabel; // 'Customise · Chips' for an auto-added step (OO.combo.view)

    React.useEffect(function () { if (!editing && OO.analytics) OO.analytics.viewItem(combo, store); }, [combo.id]); // P5-06b: view_item

    // A new phase starts at its top, with focus on its heading (keyboard and screen-reader users keep their place).
    var phaseKey = [s.g, s.ph, s.mi, s.added ? 1 : 0].join('|');
    var firstRun = React.useRef(true);
    React.useEffect(function () {
      if (firstRun.current) { firstRun.current = false; return; }
      var flow = flowRef.current;
      var scroller = flow && flow.closest ? flow.closest('.oo-sheet__scroll') : null;
      if (scroller) scroller.scrollTop = 0;
      var h = document.getElementById(titleId);
      if (h) {
        h.setAttribute('tabindex', '-1');
        try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
      }
    }, [phaseKey]);

    React.useEffect(function () {
      P.focusInvalidGroup(groupRefs.current[invalidS[0].id]);
    }, [invalidS[0]]);

    // Pure reducer actions (safe to re-run); the cart is only touched in onCta, from the state on screen.
    // run(fn, …args) → fn(state, combo, …args); setQty / setNotes take that shape too.
    function run(fn) {
      var args = [].slice.call(arguments, 1);
      if (invalidS[0].id !== null) invalidS[1]({ id: null, attempt: invalidS[0].attempt });
      setS(function (prev) { return fn.apply(null, [prev, combo].concat(args)); });
    }
    function setQty(n) { run(C.setQty, n); }
    function setNotes(text) { run(C.setNotes, text); }
    function notify(added, mode) {
      if (!props.onAdded || !added) return;
      props.onAdded(added, { kind: 'combo', mode: mode, name: combo.name, qty: added.qty, message: mode === 'update' ? 'Cart updated' : 'Combo added to cart' });
    }
    function onCta() {
      if (!available) return;
      if (!v.ctaEnabled) {
        if (canValidate) invalidS[1]({ id: v.missing[0], attempt: invalidS[0].attempt + 1 });
        return;
      }
      if (!v.isReview && !v.addFromIntro) { run(C.cta); return; }
      if (editing) {
        if (doneRef.current) return;
        doneRef.current = true;
        var updated = Object.assign(C.line(s, combo), { key: line.key }); // a replaced combo keeps its key
        OO.store.cartReplace(line.key, updated);
        notify(updated, 'update');
        props.onClose();
        return;
      }
      var next = C.cta(s, combo);
      if (next.addedLine) { OO.store.cartAdd(next.addedLine); OO.platform.haptic('medium'); } // P4-09: REF addItem
      if (next.addedLine && OO.analytics) OO.analytics.addToCart(next.addedLine, store); // P5-06b: add_to_cart
      setS(next);
      notify(next.addedLine, 'add');
    }
    function onBack() {
      if (v.isIntro) props.onClose();
      else run(C.back);
    }
    function pickStep(i) {
      var x = v.steps[i];
      if (x && x.reachable) run(C.goStep, x.index);
    }

    // Item list keyboard: arrows move focus only (see the header); Space / click picks.
    function onListKey(e) {
      if (v.multi) return;
      var k = e.key;
      var dir = k === 'ArrowDown' || k === 'ArrowRight' ? 1 : k === 'ArrowUp' || k === 'ArrowLeft' ? -1 : 0;
      var t = e.target;
      if (!dir || !t || t.type !== 'radio') return;
      e.preventDefault();
      var radios = [].slice.call(e.currentTarget.querySelectorAll('input[type="radio"]')).filter(function (r) { return !r.disabled; });
      var i = radios.indexOf(t);
      var nextRadio = radios[(i + dir + radios.length) % radios.length];
      if (nextRadio && nextRadio !== t) nextRadio.focus();
    }

    // ---- phase content ----
    var content;
    if (v.isIntro) {
      var includedEntries = C.entries(s, combo);
      content = [
        <ui.SectionTitle key="t" level="group" id={titleId}>What’s included</ui.SectionTitle>,
        <ui.Card key="l" pad="list" flat className="oo-combo__includes">
          <ol className="oo-combo__incl-list">
            {v.includes.map(function (i) {
              var selected = includedEntries.filter(function (e) { return e.groupId === combo.groups[i.num - 1].id; });
              var image = selected.length ? selected[0].option.image : null;
              var action = selected.length ? 'Change' : 'Add';
              return (
                <li key={i.num} className="oo-combo__incl">
                  <span className="oo-combo__incl-media" aria-hidden="true">
                    <span className={ui.cx('oo-combo__incl-image', !image && 'is-placeholder', !selected.length && 'is-empty')}>
                      {selected.length ? <OO.icons.ImagePlaceholder size={22} /> : <OO.icons.Plus size={22} />}
                      {image ? <img key={image} src={image} alt="" onError={function (e) { e.currentTarget.hidden = true; }} /> : null}
                    </span>
                    {i.done ? <span className="oo-combo__incl-check"><OO.icons.Check size={12} strokeWidth={3} /></span> : null}
                  </span>
                  <span className="oo-combo__incl-text">
                    <span className="oo-combo__incl-label">{i.label}</span>
                    {i.done ? <span className="oo-sr-only">Selected</span> : null}
                    <span className="oo-combo__incl-sub">{i.sub}</span>
                  </span>
                  <ui.Button variant="outline" size="sm" className="oo-combo__incl-action"
                    aria-label={action + ' ' + i.label} disabled={i.index == null}
                    onClick={function () { run(C.customiseGroup, i.index); }}>{action}</ui.Button>
                </li>
              );
            })}
          </ol>
        </ui.Card>,
        <p key="n" className="oo-combo__note">{v.addFromIntro
          ? <>Your required choices are selected. Tap <strong>Change</strong> to swap any item.</>
          : <>Tap <strong>Add</strong> to choose the required items, or <strong>Change</strong> to swap a selection.</>}</p>,
      ];
    } else if (v.isItem) {
      var counters = v.multi && step && step.group.allowSame;
      var picked = v.items.reduce(function (t, it) { return t + (it.count || 0); }, 0);
      content = [
        <ui.SectionTitle key="t" level="step" id={titleId} sub={v.groupHint}>{v.groupTitle}</ui.SectionTitle>,
        <ui.Card key="l" pad="list" flat className={ui.cx('oo-combo__choices', invalidS[0].id === 'items' && 'is-invalid')}
          elementRef={function (el) { groupRefs.current.items = el; }} tabIndex={-1}
          role="group" aria-labelledby={titleId} aria-invalid={invalidS[0].id === 'items' ? true : undefined}>
          <div role={v.multi ? 'group' : 'radiogroup'} aria-labelledby={titleId} onKeyDown={onListKey}>
            {v.items.map(function (it) {
              // An item that can't be completed (a blocked required group, no usable size) says why instead of its
              // description.
              var price = it.soldOut ? 'Sold out' : it.reason ? 'Unavailable' : it.price;
              var tone = !it.soldOut && !it.reason && it.included ? 'success' : undefined;
              var desc = it.reason || it.desc;
              if (counters) {
                return (
                  <P.CounterRow key={it.mappingId} image={it.img || null} full={picked >= step.max}
                    option={{ id: it.mappingId, name: it.name, desc: desc, priceLabel: price, priceTone: tone, count: it.count, disabled: it.disabled }}
                    onInc={function (id) { run(C.pickItem, id); }} onDec={function (id) { run(C.removeItem, id); }} />
                );
              }
              return (
                <ui.OptionRow key={it.mappingId} variant="item" type={v.multi ? 'checkbox' : 'radio'} name={radioName} value={String(it.mappingId)}
                  image={it.img || null} label={it.name} desc={desc} price={price} priceTone={tone} chevron={it.hasMods}
                  checked={it.on} disabled={it.disabled} onToggle={function () { run(C.pickItem, it.mappingId); }} />
              );
            })}
          </div>
        </ui.Card>,
      ];
    } else if (v.isMods) {
      // A view group (OO.combo.view().sizes / .mods) → the item sheet's ModGroup shape (modGroup), with the photos and
      // alerts of the current pick's option (OO.combo.entries: step s.g, pick s.mi).
      var curEntry = C.entries(s, combo).filter(function (e) { return e.step === s.g; })[s.mi] || null;
      var groupOf = function (m) { return modGroup(m, curEntry && curEntry.option); };
      // A 'choice' pick asks for its size first; its option groups follow once one is chosen.
      var sizeGroup = v.sizes ? [
        <P.ModGroup key={s.mi + '-size'} group={groupOf(v.sizes)} onPick={function (id) { run(C.pickSize, id); }}
          elementRef={function (el) { groupRefs.current.size = el; }} invalid={invalidS[0].id === 'size'} />,
      ] : [];
      content = [
        <ui.Card key="c" pad="none" flat className="oo-combo__cur">
          <span className="oo-combo__thumb">{v.curImg ? <img src={v.curImg} alt="" /> : null}</span>
          <span className="oo-combo__cur-text">
            <span className="oo-combo__cur-group">{v.groupName + (v.curPosition ? ' · ' + v.curPosition : '')}</span>
            <h2 className="oo-combo__cur-name" id={titleId}>{v.curItem}</h2>
            {v.curFixed ? <span className="oo-combo__cur-fixed">Only option</span> : null}
            {v.curSize && v.curSize.locked ? <span className="oo-combo__cur-fixed">{v.curSize.label + ': ' + v.curSize.name}</span> : null}
          </span>
          {v.canChangeItem ? (
            <ui.Button variant="link" aria-label={'Change ' + v.groupName.toLowerCase()} onClick={function () { run(C.changeItem); }}>Change</ui.Button>
          ) : null}
        </ui.Card>,
      ].concat(sizeGroup, v.mods.map(function (m) {
        return (
          <P.ModGroup key={s.mi + '-' + m.id} group={groupOf(m)}
            elementRef={function (el) { groupRefs.current[m.id] = el; }} invalid={invalidS[0].id === m.id}
            onPick={function (id) { run(C.toggleMod, m.id, id); }}
            onInc={function (id) { run(C.incMod, m.id, id); }}
            onDec={function (id) { run(C.decMod, m.id, id); }} />
        );
      }));
    } else {
      content = [
        <ui.SectionTitle key="t" level="step" id={titleId}>Review your combo</ui.SectionTitle>,
      ].concat(v.review.map(function (r) {
        if (r.items.length > 1) return (
          <ui.Card key={'r' + r.index} pad="none" flat className="oo-combo__review-group">
            <div className="oo-combo__review-head">
              <span className="oo-combo__row-group">{r.group}</span>
              {r.canChange ? <ui.Button variant="link" aria-label={r.changeLabel + ' ' + r.group}
                onClick={function () { run(C.change, r.index); }}>{r.changeLabel}</ui.Button> : null}
            </div>
            {r.items.map(function (item) { return (
              <div key={item.index} className="oo-combo__row">
                <span className="oo-combo__thumb">{item.img ? <img src={item.img} alt="" /> : null}</span>
                <span className="oo-combo__row-text">
                  <span className="oo-combo__row-item">{item.name}</span>
                  {item.mods ? <span className="oo-combo__row-mods">{item.mods}</span> : null}
                </span>
                {item.price ? <span className="oo-combo__row-price">{item.price}</span> : null}
              </div>
            ); })}
          </ui.Card>
        );
        return (
          <ui.Card key={'r' + r.index} pad="none" flat className="oo-combo__row">
            <span className="oo-combo__thumb">{r.img ? <img src={r.img} alt="" /> : null}</span>
            <span className="oo-combo__row-text">
              <span className="oo-combo__row-group">{r.group}</span>
              <span className="oo-combo__row-item">{r.item}</span>
              {r.hasMods ? <span className="oo-combo__row-mods">{r.mods}</span> : null}
            </span>
            <span className="oo-combo__row-side">
              {r.price ? <span className="oo-combo__row-price">{r.price}</span> : null}
              {r.canChange ? (
                <ui.Button variant="link" aria-label={r.changeLabel + ' ' + r.group} onClick={function () { run(C.change, r.index); }}>{r.changeLabel}</ui.Button>
              ) : null}
            </span>
          </ui.Card>
        );
      })).concat([
        <ui.TextArea key="n" className="oo-combo__notes" size="md" label="Additional notes" placeholder="Extra comments (optional)"
          value={v.notes} onChange={setNotes} />,
      ]);
    }

    var notices = [];
    if (v.added) notices.push(<ui.InlineNotice key="added">Combo added to cart</ui.InlineNotice>);
    if (!available) {
      notices.push(
        <ui.InlineNotice key="na" tone="warn">
          {v.unavailableGroup ? v.unavailableGroup + ' isn’t available right now, so this deal can’t be ordered.'
            : combo.availableToday ? 'This deal isn’t available at this time of day.' : 'This deal isn’t available today.'}
        </ui.InlineNotice>
      );
    }

    // ---- chrome ----
    var price = f.money(combo.price, cur);
    var steps = v.steps.map(function (x) {
      return { key: String(x.index), label: x.label, name: x.name, summary: x.summary, state: x.state, disabled: !x.reachable || !available };
    });
    var header, aside = null;
    if (wide) {
      header = [
        v.notIntro ? <ui.IconButton key="b" icon="ArrowLeft" label="Back" strokeWidth={2.4} onClick={onBack} /> : null,
        <span key="l" className="oo-combo__steplabel">{stepLabel}</span>,
      ];
      aside = (
        <div className="oo-combo__aside">
          {combo.image ? <div className="oo-combo__hero"><img src={combo.image} alt="" /></div> : null}
          <div className="oo-combo__intro">
            <ui.SheetHeading kind="combo" id={asideTitleId} title={combo.name} desc={combo.description} price={(hasExtras(combo) ? 'From ' : '') + price} />
          </div>
          <div className="oo-combo__steps">
            <ui.StepBar variant="list" steps={steps} onPick={pickStep} />
          </div>
        </div>
      );
    } else {
      var cartLink = OO.router.link('cart', {}, has(props.hotel) ? { hotel: props.hotel } : {});
      header = [
        <ui.PageHeader key="h" as="div" sticky={false} titleId={headTitleId} title={combo.name} subtitle={stepLabel}
          onBack={onBack} backLabel={v.isIntro ? 'Close' : 'Back'}
          right={editing
            ? <ui.HeaderCartPill count={cartCount} onClick={props.onClose} />
            : <ui.HeaderCartPill count={cartCount} href={cartLink.href} onClick={function (e) { cartLink.onClick(e); if (e.defaultPrevented) props.onClose(); }} />} />,
        v.notIntro ? <ui.StepBar key="s" steps={steps} onPick={pickStep} /> : null,
      ];
    }

    var ctaLabel = !available ? 'Not available right now' : (v.isReview || v.addFromIntro) && editing ? 'Update combo' : v.ctaLabel;
    var ctaButton = <ui.Button key="c" block className="oo-combo__cta" variant={v.isIntro ? 'success' : 'primary'}
      price={v.isIntro ? v.total : v.ctaPrice || null} disabled={(!v.ctaEnabled && !canValidate) || !available} onClick={onCta}>{ctaLabel}</ui.Button>;
    var footer = v.isIntro ? null : [
      v.isReview
        ? <ui.QtyStepper key="q" value={v.qty} onChange={setQty} itemName={combo.name} />
        : <ui.TotalBlock key="t" label="Combo total" amount={v.total} />,
      ctaButton,
    ];

    return (
      <ui.Sheet open onClose={props.onClose} labelledBy={wide ? asideTitleId : headTitleId} kind="combo" className={ui.cx('oo-combo', v.isIntro && 'oo-combo--overview')}
        header={header} aside={aside}
        hero={!wide && v.isIntro ? combo.image || null : null}
        heading={!wide && v.isIntro ? <ui.SheetHeading kind="combo" title={combo.name} desc={combo.description} price={price} /> : null}
        footer={footer}>
        <div ref={flowRef} className="oo-combo__flow">
          <div className="oo-sr-only" aria-live="polite">{stepLabel}</div>
          {notices}
          {content}
          {v.isIntro ? <div className="oo-combo__intro-action">{ctaButton}</div> : null}
        </div>
      </ui.Sheet>
    );
  }

  OO.pages.ComboBuilder = function ComboBuilder(props) {
    var P = parts();
    var data = P.useOverlayData(props);
    var line = P.useEditLine(props.editLineKey);
    var comboLine = line && line.kind === 'combo' ? line : null;
    var cid = has(props.comboId) ? props.comboId : (comboLine ? comboLine.comboId : null);
    var combo = data.menu && has(cid) ? data.menu.combos[cid] || null : null;
    var onClose = props.onClose || noop;
    if (props.open === false) return null;
    if (data.loading) return <P.LoadingSheet onClose={onClose} label="Loading combo" kind="combo" />;
    if (!combo || !OO.combo.steps(combo).length) {
      return <P.MissingSheet onClose={onClose} title="Deal unavailable" body="We couldn’t find this deal. It may have been removed from the menu." />;
    }
    var seed = combo.id + '|' + (comboLine ? comboLine.key : '');
    return <ComboBody key={seed} combo={combo} store={data.store} menu={data.menu} moment={props.moment} line={comboLine} hotel={props.hotel} onClose={onClose} onAdded={props.onAdded} />;
  };
  OO.pages.ComboBuilder.logic = { modGroup: modGroup };
})();


// ===================== src/pages/cart.jsx =====================
// src/pages/cart.jsx: the Cart page (page id 'cart', /{hotel}/cart). Owner: T3c (Batch 1 UI on API-shaped fixtures).
// Screens: mobile "03 Cart" and desktop "D03 Cart" in docs/online-order/design/ordering-flow.dc.txt (lines 792–859 and
// 1123–1196; behaviour in renderVals, lines 1459–1497). Styles: styles/cart.css.
//
// Data: OO.useHotel (normalised store + menu) and the live cart in OO.store (lines + applied coupon). Every number on
// the page comes from OO.pricing.summary; nothing is re-priced here.
// Pieces: PageHeader "Cart" · "Order summary" + store line · line cards (OO.cart.displayMods, QtyStepper — minus at 1
// removes —, line total, pencil → <OO.pages.ItemSheet editLineKey> / <OO.pages.ComboBuilder editLineKey>, trash)
// and the applied coupon's free lines (P2-14: FreeLine; see Coupons) · "+ Add more items" → menu · Price summary
// (PriceRows). Coupon selection and free-item choices live at checkout; the shared coupon helpers remain here.
// Desktop "Continue to
// checkout" · mobile BottomBarCTA "Total … Checkout" · PoweredBySipo. store.checkout.priceSummary 'collapsed' hides
// the mobile summary card behind the bar's "Price summary" toggle (design tweak priceSummary inline | collapsed).
// P5-06b: both Checkout buttons send begin_checkout (OO.analytics.beginCheckout: the lines, the summary's total) BEFORE
// they navigate: the checkout has no tag frame (src/lib/analytics.jsx).
// Upsell (P2-19): <OO.pages.Upsell hotel store menu lines moment /> (src/pages/upsell.jsx, looked up at render time)
// under the lines, or under the empty state: "You might also like" (≤ 8) / "Popular right now" (≤ 4); nothing when none.
// W5-FIX #4: the page mounts it through UpsellHost, which hands it the moment the menu page judges its cards at
// (useUpsellMoment), worked out without the whole-menu display (OO.model.menuDisplay over every product and combo, each
// minute) the upsell's own useMoment builds: the upsell then judges only its suggested products. Same output: the same
// orderMoment, time zone and active named menu (OO.model.namedMenus, judged lazily; nothing to judge without named
// menus). The moment's minute tick lives in UpsellHost, so it re-renders that subtree only, never the whole page.
// States: loading (skeleton until the store has loaded AND OO.store has switched to this hotel's cart), store not
// found, load error (retry), empty cart (Browse menu), lines that can't be ordered (OO.cart.issues, checked at the
// draft's pickup slot — the checkout's default slot when there is none): gone / sold out / changed options block the
// way to checkout until removed or edited; an "unavailable" (time-bound) line keeps its badge but doesn't block,
// because the cure is another time and the time picker is on checkout, which validates the slot it shows. Not at a
// table (POLISH-1, p5-followups #16): see "At a table".
//
// Coupons (Batch 2 P2-14). D-05 (the owner, 2026-10-04: a coupon after a cart edit is RE-CHECKED AND KEPT): the page
// re-checks the applied coupon with OO.coupons.recheck on every change of the lines, the order type, the time (the
// draft's slot) and the payment method (useCouponSync, below): still valid → kept, and stored again only when its
// picks changed (a free-item coupon's resolved choice); no longer valid → removed, with recheck's message as a short
// toast ('Coupon OFF10 removed. Minimum order $30.00 required.'); waiting for the customer's choice (pending) → kept
// for checkout's "Choose your free item" prompt (P2-12 / P2-13; PickerHost). The line list shows the applied coupon's
// free lines after the cart's own (couponLines: a BOGO clone, a
// BUY_N reward, a giveaway; marked "Free", or the capped "N% off"), and a "1 free" badge on the line one unit of
// which an ITEM 100 % coupon frees (that split moves no money), so the visible line totals add up to the Sub-total.
// One menu for the free items (couponMenu): the picker, the recheck after its pick, the D-05 plan and apply all judge
// and price them with the menu the page checks its lines with at the order's slot, at the cart's pricing record, with
// the same lines, mode, instant and order type (the orchestrator's decision, 2026-10-04).
// Applying a coupon (useCouponApply; REF CartContext.applyCoupon's order): force_login_for_coupon (a guest: "Please
// sign in to use this coupon.") → OO.membership.exclusivity({ apply: 'coupon' }) (a reward line refuses it; wallet
// points in use are dropped with its notice) → the email it is checked with (the session's, else the draft's when
// valid, else a guest's contact sheet: name, mobile, email, written to the checkout draft) → OO.api.validateCoupon
// (code, email, the order's instant; a typed code may name a coupon the list doesn't show) → the local gates
// (OO.coupons.recheck: any refusal but a choice to make is shown, not applied) → stored; a coupon that waits for a
// choice opens its picker.
//
// Reward lines (W5-FIX #3; wallet type 2, a loyalty product the points pay for: OO.cart's line.reward). Their own card
// (RewardLine): no stepper (REF locks them at 1: "Qty 1") and no edit pencil, a "Reward" mark and what they take, as
// the checkout shows them (rewardNote: '40 points · $4.00 off' at the order's redemption row, OO.wallet.rewardPoints /
// rewardAmount), and Remove. Their price comes off in the Discount row (the wallet contributor). REF's auto-removal of
// reward lines the order can no longer take (OO.wallet.staleRewards: signed out, the daily cap, a cart-level store) runs
// here too (useStaleRewards), with the same rule and copy as the checkout's wallet section: the lines go once, with
// that text as a toast, on whichever page sees them first (the other then finds none; nothing re-adds them).
//
// Delivery fee: pricingCtx takes the zone checkout last synced into the draft (zoneId). P1-21: when the draft carries a
// picked address (lat / lng), the cart chooses the zone itself, as checkout does: OO.delivery.selectZone on the cart's
// item subtotal and the driving distance (useDeliveryDistance; 0 for a fixture store), through checkout's zoneQuote
// (its fee floor for that address, scoped to the zones and item subtotal it was named for, never turning a covered
// address "outside"), so its fee matches checkout's. While a real store's distance is unknown (being measured, or it
// failed: checkout says so) the synced zone stands.
//
// At a table (Batch 2 QR-CART, p4-followups #25; mode qr with the table context the checkout orders for: Checkout.logic
// tableContextOf, read per render): the cart prices the order exactly as the table checkout does before a pick, with
// the checkout's own helpers and the same table object (Checkout.logic tablePc(null) and summarize: one copy of the
// maths). A dine-in counter order: no card surcharge until a method is picked at checkout (whatever method the draft
// names, or the store's default), no delivery fee or Delivery row, no coupon and no points (D-20); the lines keep the
// prices the menu shows (the TABLE SIDE pricetag record the store prices the cart with). Its lines are checked at the
// checkout's ASAP table slot (CS.time.tableSlot), never the draft's day / slot. No coupon card, offers sheet or
// free-item prompt, no D-05 re-check, no reward auto-removal and no delivery distance: a coupon or points the draft
// carries are ignored at the table, never removed. Online / app carts are unchanged.
// POLISH-1 (p5-followups #16): the lines are judged as the table checkout judges them (Checkout.logic issueState at its
// ASAP slot): EVERY issue blocks the way to checkout, a time-bound ("unavailable") line too, since a table has no time to
// choose. The notice then reads "Some items aren’t available right now. Remove them to continue." when every issue is
// time-bound, else the blocked copy; never "Choose another time at checkout".
// POLISH-2 (p5-followups #17; D-20: the account is off at a table): no account link in the header in mode qr, as the
// checkout.
//
// OO.pages.Cart.shared: the helpers checkout.jsx reuses (useCartPage, pricingCtx, summaryOf, useDeliveryDistance, …)
// so the cart, the checkout and the checkout payload use the same order type, payment method and delivery fee —
// identical totals.
//   summaryOf(lines, store, coupon, pc, table) → OO.pricing.summary for the order: pc's payment method, order type and
//       delivery fee, the coupon, the table, and (P2-07) the wallet points in use: opts.walletAmount = pointsOf's amount
//       when there is one (none: the summary as before), so the cart, the checkout and the menu's coupon prompt all see
//       "points in use" as summary.wallet > 0 (the coupon lane's contract).
//   pointsOf(lines, store, coupon, pc, table) → Redemption | null (P2-07): OO.wallet.inUse for that order (the points
//       the customer chose at the checkout, as the order takes them; none with a coupon). checkout.jsx sends its points
//       and amount with the same arguments as its summary.
//   useCartPage(hotel) re-renders on a change of the points chosen (OO.store's state.points) too.
//   useDeliveryDistance(store, draft, on) → { status: 'off' | 'pending' | 'ok' | 'error', km, error, retry() } (P1-21,
//       a hook): the driving distance from the store (OO.delivery.originOf) to the draft's picked point, through
//       OO.delivery.distanceKm (memoised for the page's life, so the two pages share one request). 'off' unless `on`
//       (the caller's delivery order), a real store, the Maps key (OO.delivery.enabled()) and a picked point. A
//       distance already measured is 'ok' at once. A failure stays until retry(): nothing re-asks on its own.
// The coupon helpers (P2-14; checkout-offers.jsx uses them too):
//   couponCtx(v) → cc: the context every coupon flow reads (the D-05 re-check, the picker). v = { hotel, store, menu
//       (couponMenu's: the order's slot menu at the cart's pricing), lines, coupon (the STORED coupon: OO.store's
//       cart.coupon, never liveCoupon's copy),
//       orderType, paymentMethod, at (ms: the order's instant), table, session, draft, sum (the summary shown) }.
//       cc = v plus { email (the session customer's, else the draft's when it is a valid address, else ''), signedIn,
//       points (sum.wallet: wallet points in use reach the summary as its wallet), rewards (the number of reward lines,
//       line.reward), rc (OO.coupons' ctx: { store, orderType, paymentMethod, currency, at, menu, mode: OO.mode,
//       table }) }.
//   couponPlan(cc) → { action: 'none' | 'store' | 'remove', from, coupon, message, reason, pending, choices, sig }
//       (pure; the D-05 decision): OO.coupons.recheck(cc.coupon, cc.lines, cc.rc). Kept → 'store' when the kept
//       coupon's picks differ from the stored ones (canonical: sorted keys, none = {}), else 'none'; not kept →
//       'remove' with recheck's message. from = the stored coupon judged; coupon = the kept one (recheck's: re-read at
//       `at`, its picks resolved) | null; reason = recheck's ('Choose the item you get free' while pending); pending =
//       it waits for the customer's choice; choices = OO.coupons.freeLines(coupon, lines, cc.rc).choices while
//       pending, else null; sig = the decision's identity (the sync's effect key). No coupon (or a recheck that
//       throws) → 'none'.
//   useCouponSync(cc, onRemoved) → plan (a hook): couponPlan on every render, applied in an effect only while OO.store
//       still holds the coupon it judged: 'store' writes the kept coupon, 'remove' clears it and calls
//       onRemoved(message). The coupon it stores is judged unchanged on the next render, so nothing loops. cc null →
//       the 'none' plan.
//   slotAt(store, draft, orderType) → ms: the instant of the slot checkout would show (draftSlot; ASAP = now).
//   tableOf(table, hotel) → { id, name } | null: OO.store's table context when it belongs to this hotel.
//   couponMenu(hotel, slotMenu) → Menu: the menu every coupon flow judges and prices free items with (the
//       orchestrator's decision, 2026-10-04: D-07 is judged at the order's moment): the slot menu the page checks its
//       lines with (slotCheck's: OO.data.menuAt at the order's instant and domain, else the page's menu), at the cart's
//       pricing record (OO.pricetags.priceMenu over OO.store's cart.pricing, as OO.data.pricedMenu prices useHotel's
//       menu; the same object back while neither changes). The picker, its recheck, the D-05 plan and apply all get
//       this one menu with the same lines, mode, `at` and order type.
//   couponMoment(hotel, store, draft, orderType, menu) → { at, menu }: the instant of the slot checkout would show
//       (draftSlot) and couponMenu over that slot's slotCheck menu (`menu`, the page's, is slotCheck's fallback).
//   couponLines(lines, coupon, store, ok) → [{ key, line, part, free }]: the lines as the order shows them. Each cart
//       line (part = { qty: 1, pct, label } when the applied ITEM 100 % coupon frees one of its units), then the
//       coupon's free lines (free = { pct, label }), from OO.coupons.freeLines(coupon, lines, { store }): the
//       summary's own options, so the rows are the ones it priced. Only when ok (the summary applied the coupon:
//       sum.coupon.ok); else the cart's lines. label: 'Free' at 100 %, else the capped 'N% off' (part: '1 free' |
//       '1 at N% off').
//   snapshotLines(lines, coupon, store, ok) → couponLines' lines for the order snapshot (checkout's snapshotOf), a
//       free or part line's name followed by its label in brackets ('Fat Boy Burger (Free)').
//   <PickerHost cc plan open onClose say /> (component): OO.pages.CouponPicker (P2-12 / P2-13's contract: store, menu,
//       lines, coupon, choices, mode, at, currency, orderType, onPick, onClose; menu = cc.menu, couponMenu's) while open
//       and the plan is pending. onPick(picks) → OO.coupons.recheck(the coupon with those picks, cc.lines, cc.rc: the
//       picker's own lines, menu, mode, at and order type): kept → stored (a coupon still pending keeps the picker
//       open), not kept → removed and say(message). onClose only closes it: the pending coupon stays (D-05).
//   <PickPrompt plan onChoose onRemove? /> (component): "Choose your free item" (the plan's reason) + Choose (and
//       Remove when onRemove is given), while the plan is pending; nothing otherwise.
//   useCouponApply(cc, say, { onApplied(coupon), onPending(coupon) }) → { busy, error, contact, apply(coupon),
//       applyCode(code), submitContact({ name, phone, email }), closeContact(), clearError(), remove() } (a hook; see
//       "Applying a coupon" above). One check at a time: busy = 'code' | the coupon id while the server checks it; error
//       = { id ('code' | a coupon id), text, login (true: the sign-in refusal) } | null; contact = { target } while a
//       guest's contact sheet is open (submitContact writes name / phone / email to the checkout draft and checks the
//       coupon with that email). The texts: 'Please sign in to use this coupon.'; exclusivity's refusal; the server's
//       refusal (validate_coupon_v4's message, else 'Invalid or expired promo code.'); recheck's reason ('Minimum order
//       $30.00 required'); a typed code the server couldn't check (not a refusal) applies only when the store lists it,
//       else 'Coupon validation is temporarily unavailable. Please try again.' (a listed coupon applies: Pay re-checks
//       it). A typed code's coupon is the server's row (validate_coupon_v4's data, normalised at the order's instant); a
//       listed one's too when it is the same coupon (its applicable / result products are the pickers'). Success:
//       OO.store.setCoupon(recheck's coupon), say('Coupon OFF10 applied' | 'Coupon BOGO1 added. Choose the item you get
//       free.', + exclusivity's notice), onApplied, and onPending while it waits for a choice. remove() clears it.
//       Points exclusivity: exclusivity's drop ['points'] calls OO.wallet.dropPoints(hotel) when the wallet lane (P2-07)
//       provides it (until then no points can be in use).
//   useCouponList(store, cc, on) → { list, loading, error, reload } (a hook): OO.api.getCoupons(store.id) while `on`
//       (no request otherwise), through OO.coupons.list at cc's instant, order type and table.
//   couponWords(coupon, currency) → { type, offer }: REF's words ('Order discount' · '10% off your order (up to
//       $5.00)', 'Buy 1 get 1' · 'Buy 1, get 1 free', …; a 1,000,000 cap is none).
//   offerRows(list, cc, applied, base) → [{ coupon, e (OO.coupons.eligibility), on (the applied one), canApply (eligible
//       or waiting for a choice), addMore (> 0: short of MIN_ORDER by that, and meeting it is all it takes; a coupon
//       that would still be refused at its minimum carries that refusal instead, e.g. 'Only valid for delivery
//       orders'), rank, i }]: applied first, then the ones that can apply, then those short of a minimum (by the
//       shortfall), then the rest; by name. base = the cart's own subtotal.
//   offerCount(rows) → the rows checkout's "N offers available" counts (not applied; can apply or short of a minimum).
//   rewardNote(line, store, rule) → '40 points · $4.00 off' (W5-FIX #3): what a reward line takes, as the checkout's
//       wallet section words it: OO.wallet.rewardPoints at `rule` (the order's redemption row, OO.wallet.rewardBudget's;
//       none: the points it was added for) in the store's points word, and OO.wallet.rewardAmount.
//   useStaleRewards(ctx, lines, say) (W5-FIX #3, a hook): OO.wallet.staleRewards(lines, ctx) (ctx = OO.wallet.ctxOf's;
//       null: nothing) removes the stale reward lines in an effect and says its text, once per set of lines.
//   upsellMoment(hotel, store, menu, draft) → moment | null (W5-FIX #4): OO.pages.Menu.shared.useDisplay's moment
//       ({ timeZone, menuId, now, today, orderType, domain }: its orderMoment over this hotel's draft, the store's time
//       zone, the active named menu) without its whole-menu display: the named menu from OO.model.namedMenus. null
//       without a store, a menu or the menu page's helpers, and when two or more named menus can be picked but the
//       customer's pick can't be read (Menu.shared.picked(hotel), when the menu page offers it): the upsell then
//       judges with its own display, as before.
//   useUpsellMoment(hotel, store, menu) → upsellMoment (a hook), re-judged as the menu's moment is: on the minute
//       (Menu.shared.useDisplay's tick, called with no menu, so it builds no display) and on a draft change. Call it
//       from a small component (the page's UpsellHost): its tick re-renders the component that calls it.
//   <OffersSheet cc api applied withCode needLogin onRemove onClose /> · <CodeForm api hotel /> · <ContactSheet cc code
//       busy onSubmit onClose /> (components; see "the offers sheet" below).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;

  // ---- shared helpers (also used by checkout.jsx via OO.pages.Cart.shared) ------------------------------------
  var TYPES = ['PICKUP', 'DELIVERY']; // online checkout order types (DINEIN is table / kiosk only)
  var TYPE_LABEL = { PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
  // POLISH-1: the notice at a table when every issue is time-bound (no time to choose there; see "At a table").
  var TABLE_UNAVAILABLE = 'Some items aren’t available right now. Remove them to continue.';

  function money(n, cur) { return OO.format.money(n, cur); }

  // The table context the menu keeps on OO.store (OO.store.table(): { id, name, hotel, excludedCategoryIds }, persisted
  // per hotel). Only this hotel's table applies; read defensively.
  function normTable(t) {
    if (t === undefined || t === null || t === '' || t === false) return null;
    if (typeof t === 'object') return t.id !== undefined && t.id !== null && t.id !== '' ? { id: t.id, name: t.name || null } : null;
    return { id: t, name: null };
  }

  // The page's data: the hotel (store + menu) and this hotel's cart. `ready` only once the store has loaded AND
  // OO.store has switched to this hotel's cart: useHotel switches it inside an effect, so on the first render the
  // store may still hold another hotel's cart (or none) and the page must not flash "empty" or the wrong lines.
  // Lines added earlier carry the prices of that day: once the menu is in, the cart is re-priced from it
  // (OO.store.cartReprice) and `repriced` turns true when a price changed (the pages say "Prices updated"); so it does
  // when the store re-priced the cart itself for a pricetag or membership change (OO.store priceNotice, P2-02): shown
  // once, the store's notice is acknowledged.
  function useCartPage(hotel) {
    var h = OO.useHotel(hotel);
    var s = OO.useStore(function (st) {
      // points (P2-07): the summaries take the points in use off, so a new choice re-renders the page.
      return { hotel: st.hotel, cart: st.cart, checkout: st.checkout, session: st.session, table: st.table, priceNotice: st.priceNotice, points: st.points };
    });
    var ready = !!h.store && s.hotel === String(hotel);
    var table = tableOf(s.table, hotel);
    var repricedS = React.useState(false);
    React.useEffect(function () {
      if (!ready || !h.menu) return;
      if (OO.store.cartReprice(h.menu).length) repricedS[1](true);
    }, [ready, h.menu]);
    var notice = ready && s.priceNotice && s.priceNotice.hotel === String(hotel) ? s.priceNotice : null;
    React.useEffect(function () {
      if (!notice) return;
      repricedS[1](true);
      OO.store.ackPriceNotice(notice.seq);
    }, [notice ? notice.seq : 0]);
    return {
      h: h, store: h.store, menu: h.menu, ready: ready,
      lines: ready ? s.cart.lines : [], coupon: ready ? s.cart.coupon : null,
      draft: s.checkout, session: s.session, table: table, repriced: repricedS[0] || !!notice,
    };
  }

  function orderTypesOf(store) {
    var list = ((store && store.orderTypes) || []).filter(function (t) { return TYPES.indexOf(t) >= 0; });
    return list.length ? list : ['PICKUP'];
  }
  // checkout.jsx's rules (OO.pages.Checkout.logic, read at call time), so the cart prices the order the checkout will
  // place: no DELIVERY where it isn't offered (a real store without the Maps key; a store left with none keeps its
  // list, which the checkout blocks), and no ONLINE_PAYMENT without a gateway the payload can send.
  function checkoutLogic() { return OO.pages.Checkout && OO.pages.Checkout.logic; }
  function fixtureStore(store) { return !!(store && OO.api && OO.api.isFixtureFor && OO.api.isFixtureFor(store.onlineKey)); }
  function offeredHere(list, store) {
    var L = checkoutLogic();
    var out = L ? L.offeredTypes(list, fixtureStore(store)) : list;
    return out.length ? out : list;
  }
  function orderTypeOf(store, draft) {
    var list = offeredHere(orderTypesOf(store), store);
    var want = draft && draft.orderType;
    return list.indexOf(want) >= 0 ? want : list[0];
  }
  function methodsFor(store, orderType) {
    return ((store && store.paymentMethods) || []).filter(function (m) { return (m.orderTypes || []).indexOf(orderType) >= 0; });
  }
  // The draft's method when the store offers it for this order type, else the store's default (is_default, REF's
  // order), else Pay Online, else the first offered one.
  function methodOf(store, orderType, draft) {
    var L = checkoutLogic();
    var list = L ? L.usableMethods(methodsFor(store, orderType), fixtureStore(store)) : methodsFor(store, orderType);
    var want = draft && draft.paymentMethod;
    if (list.some(function (m) { return m.key === want; })) return want;
    var def = list.filter(function (m) { return m.isDefault; })[0];
    if (def) return def.key;
    if (list.some(function (m) { return m.key === 'ONLINE_PAYMENT'; })) return 'ONLINE_PAYMENT';
    return list.length ? list[0].key : null;
  }
  function zoneOf(store, draft) {
    var id = draft && draft.zoneId;
    if (id === undefined || id === null || id === '') return null;
    return ((store && store.deliveryZones) || []).filter(function (z) { return String(z.id) === String(id); })[0] || null;
  }
  // QR-CART: the table this cart orders for (mode qr; see "At a table" in the header): checkout.jsx's tableContextOf,
  // the context the table checkout prices and places with; null without one (or without checkout.jsx's table pricing).
  function tableHere(hotel) {
    var L = checkoutLogic();
    var ok = !!L && typeof L.tableContextOf === 'function' && typeof L.tablePc === 'function' && typeof L.summarize === 'function';
    return ok ? L.tableContextOf(hotel) : null;
  }
  // The pricing context the checkout will use: order type, payment method (surcharge) and delivery fee.
  function pricingCtx(store, draft) {
    var orderType = orderTypeOf(store, draft);
    var zone = orderType === 'DELIVERY' ? zoneOf(store, draft) : null;
    return { orderType: orderType, paymentMethod: methodOf(store, orderType, draft), zone: zone, deliveryFee: zone ? zone.fee : 0, tipAmount: draft.tipAmount };
  }
  // The applied coupon as of the time the order is for (`at`: the chosen slot's instant, as validate_coupon_v4 judges
  // it; default now). The cart keeps the normalised coupon from when it was applied, and its `available` flag
  // (availability rules: hours / days) would stay frozen at that moment; re-derive it from the raw row on every render
  // (OO.coupons.live, P2-09) so a time-limited coupon follows the chosen time.
  function liveCoupon(coupon, store, orderType, at) {
    if (!coupon || !coupon.raw || !store) return coupon || null;
    try {
      // An expired coupon (normalizeCoupons drops it, P1-05) stops discounting instead of falling back to the frozen one.
      return OO.coupons.live(coupon, store, { orderType: orderType, at: at === undefined || at === null ? new Date() : at })
        || Object.assign({}, coupon, { available: false });
    } catch (e) { return coupon; }
  }
  function summaryOf(lines, store, coupon, pc, table) {
    var opts = { paymentMethod: pc.paymentMethod, coupon: coupon, orderType: pc.orderType, deliveryFee: pc.deliveryFee, table: table || undefined };
    opts.tipAmount = pc.tipAmount;
    if (pc.feePaymentKey !== undefined) opts.feePaymentKey = pc.feePaymentKey;
    else if (store.orderFees && OO.pages.CheckoutSections) {
      var methods = methodsFor(store, pc.orderType);
      var choice = OO.pages.CheckoutSections.payment.choiceOf(OO.pages.CheckoutSections.payment.choices(methods, pc.orderType, fixtureStore(store)), pc.paymentMethod, OO.store.getDraft ? OO.store.getDraft().paymentChoice : null);
      if (choice) opts.feePaymentKey = choice.kind === 'gateway' ? OO.pricing.gatewayFeeKey(choice.gateway, choice.gatewayId) : 'type:' + choice.typeId;
    }
    var points = pointsOf(lines, store, coupon, pc, table);
    if (points) opts.walletAmount = points.amount; // P2-07: the points in use (see the header)
    return OO.pricing.summary(lines, store, opts);
  }
  // P2-07: the wallet points in use for this order (see the header). Never throws: no points then.
  function pointsOf(lines, store, coupon, pc, table) {
    if (!store || !pc || !OO.wallet || typeof OO.wallet.inUse !== 'function') return null;
    try { return OO.wallet.inUse(lines, store, { orderType: pc.orderType, coupon: coupon || null, table: table || null }); } catch (e) { return null; }
  }

  // ---- coupons: the D-05 re-check, the free lines, the picker (P2-14; see the header) ----------------------------
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function numOr0(v) { var n = Number(v); return isFinite(n) ? n : 0; }

  function tableOf(t, hotel) { return t && (!t.hotel || t.hotel === String(hotel)) ? normTable(t) : null; }
  function slotAt(store, draft, orderType) { return OO.model.slotInstant(store, draftSlot(store, draft, orderType).slot, OO.data.now()); }
  // The menu a coupon's free items are judged and priced with (see the header): `slotMenu` at the cart's pricing record,
  // memoised on its own (the last few bases) so OO.data.pricedMenu's one-per-hotel memo (useHotel's menu) is never
  // handed another base.
  var couponMenus = []; // [{ base, rec, out }], the latest first
  function couponMenu(hotel, slotMenu) {
    if (!slotMenu || !OO.pricetags || typeof OO.pricetags.priceMenu !== 'function') return slotMenu || null;
    var st = OO.store.get();
    var rec = st.hotel === String(hotel) ? st.cart.pricing || null : null;
    for (var i = 0; i < couponMenus.length; i++) {
      if (couponMenus[i].base === slotMenu && couponMenus[i].rec === rec) return couponMenus[i].out;
    }
    var out = OO.pricetags.priceMenu(slotMenu, rec);
    couponMenus.unshift({ base: slotMenu, rec: rec, out: out });
    if (couponMenus.length > 4) couponMenus.length = 4;
    return out;
  }
  function couponMoment(hotel, store, draft, orderType, menu) {
    var ds = draftSlot(store, draft, orderType);
    var check = slotCheck(hotel, store, ds.day, ds.slot, orderType, menu || null);
    return { at: OO.model.slotInstant(store, ds.slot, OO.data.now()), menu: couponMenu(hotel, check.menu) };
  }

  function couponCtx(v) {
    var store = v.store || null;
    var lines = v.lines || [];
    var customer = v.session && v.session.customer;
    var own = customer && typeof customer.email === 'string' ? customer.email.trim() : '';
    var typed = String((v.draft && v.draft.email) || '').trim();
    var at = v.at === undefined || v.at === null ? OO.data.now().getTime() : v.at;
    return {
      hotel: v.hotel, store: store, menu: v.menu || null, lines: lines, coupon: v.coupon || null,
      orderType: v.orderType, paymentMethod: v.paymentMethod || null, at: at, table: v.table || null,
      session: v.session || null, draft: v.draft || {}, sum: v.sum || null,
      email: own && EMAIL.test(own) ? own : (EMAIL.test(typed) ? typed : ''),
      signedIn: !!customer,
      points: v.sum ? numOr0(v.sum.wallet) : 0,
      rewards: lines.filter(function (l) { return l && l.reward; }).length,
      rc: {
        store: store, orderType: v.orderType, paymentMethod: v.paymentMethod || null, currency: store ? store.currency : undefined,
        at: at, menu: v.menu || null, mode: OO.mode, table: v.table || null,
      },
    };
  }

  // Canonical JSON (sorted keys) of a picks object; none (undefined / null / not an object) reads as {}.
  function canon(v) {
    if (Array.isArray(v)) return '[' + v.map(canon).join(',') + ']';
    if (isRecord(v)) {
      return '{' + Object.keys(v).sort().filter(function (k) { return v[k] !== undefined; }).map(function (k) {
        return JSON.stringify(k) + ':' + canon(v[k]);
      }).join(',') + '}';
    }
    return v === undefined ? 'null' : JSON.stringify(v);
  }
  function samePicks(a, b) { return canon(isRecord(a) ? a : {}) === canon(isRecord(b) ? b : {}); }

  var NO_PLAN = { action: 'none', from: null, coupon: null, message: null, reason: null, pending: false, choices: null, sig: '' };
  function couponPlan(cc) {
    var c = cc && cc.coupon;
    if (!c || !cc.store) return NO_PLAN;
    var r;
    try { r = OO.coupons.recheck(c, cc.lines, cc.rc); } catch (e) { return NO_PLAN; }
    if (!r.keep) {
      return { action: 'remove', from: c, coupon: null, message: r.message, reason: r.reason, pending: false, choices: null, sig: 'remove|' + c.id + '|' + r.code };
    }
    var choices = null;
    if (r.pending) {
      try { choices = OO.coupons.freeLines(r.coupon, cc.lines, cc.rc).choices || null; } catch (e) { choices = null; }
    }
    var changed = !samePicks(c.picks, r.coupon && r.coupon.picks);
    return {
      action: changed ? 'store' : 'none', from: c, coupon: r.coupon, message: null, reason: r.reason, pending: !!r.pending,
      choices: choices, sig: changed ? 'store|' + canon(r.coupon.picks) : '',
    };
  }

  function useCouponSync(cc, onRemoved) {
    var plan = cc ? couponPlan(cc) : NO_PLAN;
    var planRef = React.useRef(plan);
    var cbRef = React.useRef(onRemoved);
    planRef.current = plan;
    cbRef.current = onRemoved;
    React.useEffect(function () {
      var p = planRef.current;
      if (p.action === 'none' || OO.store.get().cart.coupon !== p.from) return; // changed since this render: the next decides
      if (p.action === 'store') { OO.store.setCoupon(p.coupon); return; }
      OO.store.setCoupon(null);
      if (p.message && typeof cbRef.current === 'function') cbRef.current(p.message);
    }, [plan.action, plan.sig, plan.from]);
    return plan;
  }

  // The coupon's mark on a line: 'Free' at 100 %, else the capped % ('38.8% off').
  function freeLabel(pct) { var p = numOr0(pct); return p >= 100 ? 'Free' : Math.round(p * 10) / 10 + '% off'; }
  function couponLines(lines, coupon, store, ok) {
    var rows = (lines || []).map(function (l) { return { key: l.key, line: l, part: null, free: null }; });
    if (!ok || !coupon || !OO.coupons.freeFlow(coupon)) return rows;
    var b;
    try { b = OO.coupons.freeLines(coupon, lines || [], { store: store }); } catch (e) { return rows; }
    if (b.code) return rows;
    if (b.split) {
      rows.forEach(function (r) {
        if (r.key === b.split.key) r.part = { qty: 1, pct: b.split.pct, label: b.split.pct >= 100 ? '1 free' : '1 at ' + freeLabel(b.split.pct) };
      });
    }
    b.lines.forEach(function (f) { rows.push({ key: f.key, line: f, part: null, free: { pct: f.free.pct, label: freeLabel(f.free.pct) } }); });
    return rows;
  }
  function snapshotLines(lines, coupon, store, ok) {
    return couponLines(lines, coupon, store, ok).map(function (r) {
      var mark = r.free ? r.free.label : r.part ? r.part.label : null;
      return mark ? Object.assign({}, r.line, { name: r.line.name + ' (' + mark + ')' }) : r.line;
    });
  }

  // "Choose your free item" while the coupon waits for the customer's choice (the plan's reason, recheck's).
  function PickPrompt(props) {
    if (!props.plan || !props.plan.pending) return null;
    return (
      <div className="oo-coupon-pick" role="status">
        <OO.icons.Ticket size={18} className="oo-coupon-pick__icon" />
        <span className="oo-coupon-pick__text">{props.plan.reason || 'Choose your free item'}</span>
        <span className="oo-coupon-pick__acts">
          {props.onRemove ? <ui.Button variant="link" onClick={props.onRemove}>Remove</ui.Button> : null}
          <ui.Button size="xs" aria-haspopup="dialog" onClick={props.onChoose}>Choose</ui.Button>
        </span>
      </div>
    );
  }

  // The coupon's picker (OO.pages.CouponPicker, P2-12 / P2-13; it renders nothing until they merge).
  function PickerHost(props) {
    var Picker = OO.pages.CouponPicker;
    var plan = props.plan;
    var cc = props.cc;
    if (!props.open || !Picker || !cc || !plan || !plan.pending || !plan.choices || !plan.coupon) return null;
    var coupon = plan.coupon;
    function onPick(picks) {
      var r = OO.coupons.recheck(Object.assign({}, coupon, { picks: isRecord(picks) ? picks : {} }), cc.lines, cc.rc);
      if (!r.keep) {
        OO.store.setCoupon(null);
        props.onClose();
        if (r.message && props.say) props.say(r.message);
        return;
      }
      OO.store.setCoupon(r.coupon);
      OO.platform.haptic('medium'); // P4-09: REF commits a coupon's chosen item with a medium haptic
      if (!r.pending) props.onClose();
    }
    return (
      <Picker store={cc.store} menu={cc.menu} lines={cc.lines} coupon={coupon} choices={plan.choices} mode={OO.mode}
        at={cc.at} currency={cc.store.currency} orderType={cc.orderType} onPick={onPick} onClose={props.onClose} />
    );
  }

  // ---- coupons: applying one (P2-14; see the header) ---------------------------------------------------------------
  var LOGIN_TEXT = 'Please sign in to use this coupon.';
  var INVALID_TEXT = 'Invalid or expired promo code.';
  var UNCHECKED_TEXT = 'Coupon validation is temporarily unavailable. Please try again.';
  var CANT_TEXT = 'This coupon can’t be used on this order';

  function memberOf(cc) {
    try { return OO.membership.of(OO.store.wallet(cc.hotel)); } catch (e) { return null; }
  }
  function dropPoints(hotel) {
    if (OO.wallet && typeof OO.wallet.dropPoints === 'function') OO.wallet.dropPoints(hotel);
  }
  // validate_coupon_v4's coupon row (REF: data[0] | data | coupon), when it is an object.
  function rowOf(res) {
    var d = res ? (Array.isArray(res.data) ? res.data[0] : res.data) : null;
    if (isRecord(d)) return d;
    return res && isRecord(res.coupon) ? res.coupon : null;
  }
  function textOr(v, fallback) { var s = typeof v === 'string' ? v.trim() : ''; return s || fallback; }
  function domainOf(cc) { return OO.model.domain(cc.orderType || 'PICKUP', OO.mode, { table: !!cc.table }); }

  function useCouponApply(cc, say, opts) {
    var busyS = React.useState(null);     // 'code' | a coupon id: the server check in flight
    var errS = React.useState(null);      // { id, text, login }
    var contactS = React.useState(null);  // { target }: the email capture sheet is open for it
    var alive = React.useRef(true);
    var busyRef = React.useRef(null);
    var ref = React.useRef(null);
    ref.current = { cc: cc, say: say, opts: opts || {} };
    React.useEffect(function () { alive.current = true; return function () { alive.current = false; }; }, []);

    function idOf(target) { return target.code !== undefined ? 'code' : target.coupon.id; }
    function settle() { busyRef.current = null; busyS[1](null); }
    function fail(id, text, login) { settle(); contactS[1](null); errS[1]({ id: id, text: text, login: !!login }); }
    // The local gates (recheck: REF's checks and the picker), then the store, the toast and the caller's hooks.
    function finish(id, coupon, x) {
      var c = ref.current.cc;
      var r;
      try { r = OO.coupons.recheck(coupon, c.lines, c.rc); } catch (e) { fail(id, CANT_TEXT); return; }
      if (!r.keep || (!r.ok && !r.pending)) { fail(id, r.reason || CANT_TEXT); return; }
      settle();
      contactS[1](null);
      errS[1](null);
      if (x && x.drop.indexOf('points') >= 0) dropPoints(c.hotel);
      OO.store.setCoupon(r.coupon);
      OO.platform.haptic('medium'); // P4-09: REF applyCoupon (a no-op outside the app shell)
      var o = ref.current.opts;
      var label = r.coupon.code || r.coupon.name || '';
      var text = r.pending ? 'Coupon ' + label + ' added. ' + (r.reason || 'Choose your free item') + '.' : 'Coupon ' + label + ' applied';
      if (x && x.notice) text += (/\.$/.test(text) ? ' ' : '. ') + x.notice.text;
      if (typeof ref.current.say === 'function') ref.current.say(text);
      if (typeof o.onApplied === 'function') o.onApplied(r.coupon);
      if (r.pending && typeof o.onPending === 'function') o.onPending(r.coupon);
    }
    // A typed code the server couldn't check (not a refusal): only a coupon the store lists under it may apply.
    function listed(id, code, x) {
      var c = ref.current.cc;
      Promise.resolve().then(function () { return OO.api.getCoupons(c.store.id); }).then(function (raw) {
        if (!alive.current) return;
        var hit = OO.coupons.list(raw, c.store, { at: c.at, orderType: c.orderType, table: c.table }).filter(function (k) {
          return String(k.code || '').trim().toUpperCase() === code;
        })[0];
        if (hit) finish(id, hit, x); else fail(id, UNCHECKED_TEXT);
      }, function () { if (alive.current) fail(id, UNCHECKED_TEXT); });
    }
    function start(target) {
      var c = ref.current.cc;
      if (!c || !c.store || busyRef.current) return;
      var id = idOf(target);
      errS[1](null);
      if (!OO.coupons.enabled(c.store)) { fail(id, 'Coupons aren’t available at this store'); return; }
      if (c.store.settings && c.store.settings.forceLoginForCoupon && !c.signedIn) { fail(id, LOGIN_TEXT, true); return; }
      var x = OO.membership.exclusivity({
        coupon: c.coupon, member: memberOf(c), points: c.points, rewards: c.rewards, signedIn: c.signedIn, kiosk: false, apply: 'coupon',
      });
      if (x.refuse) { fail(id, x.refuse.text); return; }
      var code = String(target.code !== undefined ? target.code : (target.coupon.code || '')).trim().toUpperCase();
      if (!code) { finish(id, target.coupon, x); return; } // a listed coupon without a code: Pay's checkout checks it by id
      var email = String(target.email || c.email || '').trim();
      if (!email) { contactS[1]({ target: target }); return; } // REF: an email is needed to validate
      busyRef.current = id;
      busyS[1](id);
      OO.api.validateCoupon(c.hotel, { code: code, email: email, time: new Date(c.at).toISOString(), domain: OO.data.domain(c.orderType) }).then(function (res) {
        if (!alive.current) return;
        if (res && (res.status === false || res.status === 'false')) { fail(id, textOr(res.message, INVALID_TEXT)); return; }
        var cur = ref.current.cc;
        var row = rowOf(res);
        var fresh = row ? OO.model.normalizeCoupon(row, { now: cur.at, timeZone: cur.store.timezone || null, domain: domainOf(cur) }) : null;
        var coupon = target.code !== undefined ? fresh : (fresh && fresh.id === target.coupon.id ? fresh : target.coupon);
        if (!coupon) { fail(id, INVALID_TEXT); return; }
        finish(id, coupon, x);
      }, function (e) {
        if (!alive.current) return;
        // The real adapter rejects a refusal (HTTP 200, kind 'client', the server's text when plain); any other client
        // answer is a refusal too.
        if (e && e.kind === 'client') { fail(id, textOr(e.message, INVALID_TEXT)); return; }
        if (target.code === undefined) { finish(id, target.coupon, x); return; } // a listed coupon: Pay re-validates it
        listed(id, code, x);
      });
    }
    return {
      busy: busyS[0],
      error: errS[0],
      contact: contactS[0],
      apply: function (coupon) { if (coupon) start({ coupon: coupon }); },
      applyCode: function (code) { var s = String(code || '').trim(); if (s) start({ code: s.toUpperCase() }); },
      submitContact: function (details) {
        var t = contactS[0] && contactS[0].target;
        if (!t || !details) return;
        OO.store.setCheckout({ name: details.name, phone: details.phone, email: details.email });
        start(Object.assign({}, t, { email: details.email }));
      },
      closeContact: function () { contactS[1](null); },
      clearError: function () { errS[1](null); },
      remove: function () {
        errS[1](null);
        OO.store.setCoupon(null);
        if (typeof ref.current.say === 'function') ref.current.say('Coupon removed');
      },
    };
  }

  // The store's coupons to list (OO.coupons.list at the order's instant and type), read only while `on` (a request
  // that would show nothing never goes out).
  function useCouponList(store, cc, on) {
    var q = OO.useAsync(function () {
      return on && store ? OO.api.getCoupons(store.id) : Promise.resolve(null);
    }, [store ? store.id : null, !!on]);
    var list = on && q.data && store ? OO.coupons.list(q.data, store, { at: cc ? cc.at : undefined, orderType: cc ? cc.orderType : 'PICKUP', table: cc ? cc.table : null }) : [];
    return { list: list, loading: !!on && q.loading, error: on ? q.error : null, reload: q.reload };
  }

  // REF's words for a coupon (getCouponTypeLabel / getCouponDiscountLabel): its kind and what it gives. A cap the admin
  // left blank (1,000,000) is no cap.
  var TYPE_WORDS = {
    CART_DISCOUNT: 'Order discount', ITEM_DISCOUNT: 'Item discount', FREE_DELIVERY: 'Free delivery',
    BUY_ONE_GET_ONE_FREE: 'Buy 1 get 1', BUY_N_GET_M_FREE: 'Bundle deal',
  };
  function couponWords(c, cur) {
    var pct = numOr0(c.percent);
    var max = numOr0(c.maxDiscount);
    var cap = max > 0 && max < 100000 ? max : 0;
    var upTo = cap ? ' (up to ' + money(cap, cur) + ')' : '';
    var offer;
    if (c.type === 'FREE_DELIVERY') offer = cap ? 'Free delivery up to ' + money(cap, cur) : 'Free delivery';
    else if (c.type === 'BUY_ONE_GET_ONE_FREE') offer = 'Buy 1, get 1 free';
    else if (c.type === 'BUY_N_GET_M_FREE') offer = 'Buy more, get free items';
    else if (c.type === 'CART_DISCOUNT') offer = OO.coupons.isAmount(c) ? money(pct, cur) + ' off your order' + upTo : (pct > 0 ? pct + '% off your order' + upTo : 'Order discount');
    else if (c.type === 'ITEM_DISCOUNT') offer = pct >= 100 ? 'Free selected item' + upTo : pct + '% off selected items' + upTo;
    else offer = 'Special offer';
    return { type: TYPE_WORDS[c.type] || 'Special offer', offer: offer };
  }

  // The offers sheet's rows: each coupon with its eligibility for this order (OO.coupons.eligibility over the cart's own
  // subtotal, `base`), applicable first (eligible, or waiting for a choice), then the ones short of a minimum (by the
  // shortfall), then the rest; by name within each. The applied one (shown even when no longer listed) leads.
  var APPLY_CODES = ['PICK'];
  function offerRows(list, cc, applied, base) {
    var all = (list || []).slice();
    if (applied && !all.some(function (c) { return c.id === applied.id; })) all.unshift(applied);
    var cur = cc.store.currency;
    function judge(c, subtotal) {
      return OO.coupons.eligibility(c, {
        subtotal: subtotal, orderType: cc.orderType, lines: cc.lines, currency: cur, paymentMethod: cc.paymentMethod, store: cc.store,
        at: cc.at, menu: cc.menu, mode: OO.mode,
      });
    }
    var rows = all.map(function (c, i) {
      var e = judge(c, base);
      var short = 0;
      if (e.code === 'MIN_ORDER') {
        // "Add $X more" only when reaching the minimum is all it takes; else the refusal that would remain is the reason.
        var met = judge(c, Math.max(base, numOr0(c.minOrder)));
        if (met.ok || APPLY_CODES.indexOf(met.code) >= 0) short = Math.max(0, Math.round((numOr0(c.minOrder) - base) * 100) / 100);
        else e = met;
      }
      var on = !!applied && applied.id === c.id;
      var rank = on ? 0 : e.ok || APPLY_CODES.indexOf(e.code) >= 0 ? 1 : (short > 0 || e.code === 'MIN_ITEMS') ? 2 : 3;
      return { coupon: c, e: e, on: on, canApply: e.ok || APPLY_CODES.indexOf(e.code) >= 0, addMore: short, rank: rank, i: i };
    });
    return rows.sort(function (a, b) {
      return a.rank - b.rank || a.addMore - b.addMore || String(a.coupon.name || a.coupon.code).localeCompare(String(b.coupon.name || b.coupon.code)) || a.i - b.i;
    });
  }
  // How many of the rows the checkout's "N offers available" counts (REF: the exposed coupons this cart can use).
  function offerCount(rows) { return rows.filter(function (r) { return !r.on && r.rank <= 2; }).length; }

  // ---- the delivery distance (P1-21; see the header) -------------------------------------------------------------
  function draftPoint(draft) { return draft ? OO.delivery.pointOf({ lat: draft.lat, lng: draft.lng }) : null; }
  function useDeliveryDistance(store, draft, on) {
    var D = OO.delivery;
    var dest = on && store ? draftPoint(draft) : null;
    var origin = dest && !fixtureStore(store) && D.enabled() ? D.originOf(store) : null;
    var key = origin ? JSON.stringify([origin, dest]) : '';
    var known = key ? D.cachedKm(origin, dest) : null;
    var st = React.useState({ key: '', status: 'off', km: null, error: null });
    var tries = React.useState(0);
    React.useEffect(function () {
      if (!key || known !== null) return undefined;
      var alive = true;
      st[1]({ key: key, status: 'pending', km: null, error: null });
      D.distanceKm(origin, dest).then(function (km) {
        if (alive) st[1]({ key: key, status: 'ok', km: km, error: null });
      }, function (err) {
        if (alive) st[1]({ key: key, status: 'error', km: null, error: err || null });
      });
      return function () { alive = false; };
    }, [key, tries[0]]);
    var retry = React.useCallback(function () { tries[1](function (n) { return n + 1; }); }, []);
    var now = !key ? { status: 'off', km: null, error: null }
      : known !== null ? { status: 'ok', km: known, error: null }
      : st[0].key === key ? st[0] : { status: 'pending', km: null, error: null };
    return { status: now.status, km: now.km, error: now.error, retry: retry };
  }
  // The distance the cart prices delivery with: 0 for a fixture store with a picked address (checkout's distance), a
  // real store's measured one, else null (the synced zone stands).
  function deliveryKm(store, draft, dist) {
    if (!draftPoint(draft)) return null;
    if (fixtureStore(store)) return 0;
    return dist.status === 'ok' ? dist.km : null;
  }
  // The cart's order as checkout will price it (see the header): pricingCtx, the draft's slot and its instant `at`
  // (P2-09: the coupon's availability at the chosen time), the applied coupon at `at` (liveCoupon) and the summary;
  // P1-21: a picked delivery address re-selects the zone; check = slotCheck's (the lines' menu at that slot; P2-14: the
  // coupon flows' menu, couponMenu). P = useCartPage's (ready). QR-CART: at a table (tableHere's), tablePricing's.
  function cartPricing(P, hotel, dist, table) {
    if (table) return tablePricing(P, hotel, table);
    var store = P.store;
    var pc = pricingCtx(store, P.draft);
    var ds = draftSlot(store, P.draft, pc.orderType);
    var at = OO.model.slotInstant(store, ds.slot, OO.data.now());
    var cp = liveCoupon(P.coupon, store, pc.orderType, at);
    var sum = summaryOf(P.lines, store, cp, pc, P.table);
    var km = pc.orderType === 'DELIVERY' ? deliveryKm(store, P.draft, dist) : null;
    if (km !== null) {
      var L = checkoutLogic();
      var q = L && L.zoneQuote ? L.zoneQuote(hotel, draftPoint(P.draft), store.deliveryZones, km, sum.subtotal)
        : OO.delivery.selectZone(store.deliveryZones, km, sum.subtotal);
      pc = Object.assign({}, pc, { zone: q.zone, deliveryFee: q.fee });
      sum = summaryOf(P.lines, store, cp, pc, P.table);
    }
    return { pc: pc, ds: ds, at: at, cp: cp, sum: sum, check: slotCheck(hotel, store, ds.day, ds.slot, pc.orderType, P.menu) };
  }
  // QR-CART: the order as the table checkout prices it before a pick (see "At a table" in the header): its pricing
  // context and summary (Checkout.logic tablePc(null), summarize with the table), its ASAP slot (CheckoutSections
  // time.tableSlot; none without it: now) and that slot's line check; no coupon (cp null).
  function tablePricing(P, hotel, table) {
    var L = checkoutLogic();
    var store = P.store;
    var now = OO.data.now();
    var T = OO.pages.CheckoutSections && OO.pages.CheckoutSections.time;
    var slot = T && typeof T.tableSlot === 'function' ? T.tableSlot(store, now) : null;
    var pc = L.tablePc(null);
    return {
      pc: pc, ds: { day: null, slot: slot }, at: OO.model.slotInstant(store, slot, now), cp: null,
      sum: L.summarize(OO.pages.Cart.shared, P.lines, store, null, pc, table),
      check: slotCheck(hotel, store, null, slot, pc.orderType, P.menu),
    };
  }

  // PriceRows rows: every summary row but the total (PriceRows draws the total itself). Savings read as such: the
  // coupon / wallet Discount, the membership's item and order discounts (P2-04) and the wallet lines' options at an
  // applyDiscountToModifiers store (P2-05 / P2-06).
  var SAVING_ROWS = ['discount', 'membershipItem', 'membershipCart', 'walletModifiers'];
  function priceRows(sum) {
    return sum.rows.filter(function (r) { return r.key !== 'total'; }).map(function (r) {
      return { key: r.key, label: r.label, value: r.text, ...(r.description ? {description: r.description} : {}), tone: SAVING_ROWS.indexOf(r.key) >= 0 && r.value > 0 ? 'success' : undefined };
    });
  }
  function totalLabel(sum, store) { return sum.taxInclusive && !(store && store.hideTaxLabel) ? 'Total · GST incl.' : 'Total'; }

  // Why cart lines can't be ordered: OO.cart.issues → { [key]: { code, text } } (empty while the menu loads).
  function lineIssues(lines, menu, opts) { return OO.cart.issues(lines, menu, opts); }

  function findBy(list, key, value) {
    if (value === undefined || value === null || value === '') return null;
    return (list || []).filter(function (x) { return String(x[key]) === String(value); })[0] || null;
  }
  // What to validate the lines against, as Next does before placing an order: the menu snapshot for the pickup slot
  // (OO.data.menuAt: its time, ASAP = now) and the order type's domain. `at` names a scheduled slot for the message
  // ('6:30pm' today, 'Tomorrow, 12:15pm'); null for ASAP. Falls back to `menu` until the snapshot can be built.
  function slotCheck(hotel, store, day, slot, orderType, menu) {
    var t = OO.model.slotInstant(store, slot, OO.data.now());
    var snap = OO.data.menuAt(hotel, { now: t, domain: OO.data.domain(orderType) }) || menu;
    var at = slot && !slot.asap ? (day && day.label && day.label !== 'Today' ? day.label + ', ' : '') + slot.label : null;
    return { menu: snap, at: at };
  }
  // The cart has no time picker: it checks the slot checkout would show — the draft's while it is still offered,
  // else the first offered one (checkout's default), else now.
  function draftSlot(store, draft, orderType) {
    if (OO.mode === 'kiosk') return { day: null, slot: OO.kiosk.asap(store, OO.data.now()) };
    // P2-23: a chosen time by its value (OO.schedule.chosen: any window day, past midnight too), as the checkout finds it.
    var hit = OO.schedule.chosen(store, orderType, draft, OO.data.now());
    if (hit) return hit;
    var opts = OO.model.pickupOptions(store, OO.data.now(), { orderType: orderType });
    var day = findBy(opts.days, 'key', draft && draft.day) || opts.days[0] || null;
    var slot = day ? (findBy(day.slots, 'value', draft && draft.slot) || day.slots[0] || null) : null;
    return { day: slot ? day : null, slot: slot };
  }

  function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  // Scroll an element into view (scroll-margin in the CSS keeps it clear of the sticky header / fixed bottom bar).
  function reveal(el, block) {
    if (!el || !el.scrollIntoView) return;
    try { el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: block || 'nearest' }); } catch (e) { el.scrollIntoView(false); }
  }
  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  // Store not found (getRestaurant 404) / failed to load. Rendered under the page's own header.
  function StoreProblem(props) {
    var h = props.h;
    var home = OO.router.link('find-store');
    var body = h.notFound ? (
      <ui.EmptyState icon="Store" title="We couldn't find this store"
        body="The link may be out of date. Find your store to start a new order."
        action={<ui.Button size="md" href={home.href} onClick={home.onClick}>Find a store</ui.Button>} />
    ) : (
      <ui.EmptyState icon="Info" role="alert" title="We couldn't load this store"
        body="Check your connection and try again."
        action={<ui.Button size="md" onClick={h.reload}>Try again</ui.Button>} />
    );
    return <ui.Container><div className="oo-cart__problem">{body}</div></ui.Container>;
  }

  // Loading placeholder in the two-column page shape (cart and checkout).
  function PageSkeleton(props) {
    return (
      <ui.Container>
        <div className="oo-cart__body" aria-busy="true">
          <span className="oo-sr-only" role="status">{props.label || 'Loading'}</span>
          <div className="oo-cart__col oo-cart__col--main">
            <ui.Skeleton variant="text" lines={2} className="oo-cart__skel-title" />
            <ui.Skeleton variant="block" count={props.blocks || 2} height={props.blockHeight || 112} />
          </div>
          <div className="oo-cart__col oo-cart__col--side">
            <ui.Skeleton variant="block" height={72} />
            <ui.Skeleton variant="block" height={232} />
          </div>
        </div>
      </ui.Container>
    );
  }

  // ---- cart line ------------------------------------------------------------------------------------------------
  // Mobile: name + mods with edit / delete top-right, stepper + line total below (03). Desktop: one row — text,
  // stepper, total (84px, right), edit / delete (D03). part (P2-14): the applied ITEM 100 % coupon frees one unit of
  // this line ('1 free' | '1 at 38.8% off'); the line's total stays its full price, as the Sub-total counts it.
  function CartLine(props) {
    var l = props.line;
    var mods = OO.cart.displayMods(l);
    return (
      <ui.Card as="li" pad="sm" className={cx('oo-cart__line', props.issue && 'is-unavailable')}>
        <div className="oo-cart__line-text">
          <div className="oo-cart__line-name">{l.name}</div>
          {mods ? <div className="oo-cart__line-mods">{mods}</div> : null}
          {l.notes ? <div className="oo-cart__line-note">{'Note: ' + l.notes}</div> : null}
          {props.part ? <ui.Badge tone="done" className="oo-cart__line-mark">{props.part}</ui.Badge> : null}
          {props.issue ? <ui.Badge tone="warn" className="oo-cart__line-issue">{props.issue}</ui.Badge> : null}
        </div>
        <ui.QtyStepper size="sm" value={l.qty} itemName={l.name} label={'Quantity of ' + l.name}
          onChange={props.onQty} onRemove={props.onRemove} />
        <div className="oo-cart__line-total">{money(OO.cart.lineTotal(l), props.currency)}</div>
        <div className="oo-cart__line-actions">
          <ui.IconButton variant="ghost" icon="Pencil" label={'Edit ' + l.name} onClick={props.onEdit} disabled={!props.canEdit} />
          <ui.IconButton variant="ghost" icon="Trash" label={'Remove ' + l.name} onClick={props.onRemove} />
        </div>
      </ui.Card>
    );
  }

  // A coupon's free line (P2-14: couponLines' free row). The coupon builds it, so it has no stepper, edit or remove
  // (removing the coupon drops it). Its total is the full price the Sub-total counts; the badge says what the coupon
  // takes off it (the Discount row): 'Free', or the capped '38.8% off'.
  function FreeLine(props) {
    var l = props.line;
    var mods = OO.cart.displayMods(l);
    return (
      <ui.Card as="li" pad="sm" className="oo-cart__line oo-cart__line--free">
        <div className="oo-cart__line-text">
          <div className="oo-cart__line-name">{l.name}</div>
          {mods ? <div className="oo-cart__line-mods">{mods}</div> : null}
          {l.notes ? <div className="oo-cart__line-note">{'Note: ' + l.notes}</div> : null}
          <ui.Badge tone="done" className="oo-cart__line-mark">{props.label}</ui.Badge>
        </div>
        <div className="oo-cart__line-qty">{'Qty ' + l.qty}</div>
        <div className="oo-cart__line-total">{money(OO.cart.lineTotal(l), props.currency)}</div>
      </ui.Card>
    );
  }

  // ---- reward lines (W5-FIX #3; see the header) ----------------------------------------------------------------------
  function pointsWord(store) { return (store && store.loyalty && store.loyalty.pointsWord) || 'points'; }
  function pointsText(n, word) { return Number(n).toLocaleString('en-NZ') + ' ' + (Number(n) === 1 ? word.replace(/s$/, '') : word); }
  function rewardNote(line, store, rule) {
    var W = OO.wallet;
    return pointsText(W.rewardPoints(line, rule || null), pointsWord(store)) + ' · ' + money(W.rewardAmount(line), store && store.currency) + ' off';
  }
  // The engine ctx the checkout's wallet section judges reward lines with (OO.wallet.ctxOf), or null without a store.
  function rewardCtx(hotel, store, orderType, table, coupon) {
    if (!store || !OO.wallet || typeof OO.wallet.ctxOf !== 'function') return null;
    return OO.wallet.ctxOf(store, { hotel: hotel, orderType: orderType, table: table || null, coupon: coupon || null });
  }
  function useStaleRewards(ctx, lines, say) {
    var stale = ctx ? OO.wallet.staleRewards(lines || [], ctx) : null;
    var sig = stale && stale.stale ? stale.reason + ':' + stale.keys.join(',') : '';
    var ref = React.useRef(null);
    ref.current = { stale: stale, say: say };
    React.useEffect(function () {
      if (!sig) return;
      var s = ref.current.stale;
      s.keys.forEach(function (k) { OO.store.cartRemove(k); });
      if (typeof ref.current.say === 'function') ref.current.say(s.text);
    }, [sig]);
  }
  // ---- the upsell's judging moment (W5-FIX #4; see "Upsell" in the header) ----------------------------------------
  function selectDraft(s) { return s.checkout; }
  function upsellMoment(hotel, store, menu, draft) {
    var S = OO.pages.Menu && OO.pages.Menu.shared;
    if (!store || !menu || !S || typeof S.orderMoment !== 'function') return null;
    var mine = draft && (!draft.hotel || String(draft.hotel) === String(hotel)) ? draft : null;
    var m = S.orderMoment(store, mine, OO.mode);
    var canPick = typeof S.picked === 'function';
    var nm = OO.model.namedMenus(menu, Object.assign({}, m, {
      timeZone: store.timezone, selected: canPick ? S.picked(hotel) : null,
    }));
    if (nm.showSelector && !canPick) return null; // the pick decides and can't be read here: the upsell's own display
    return Object.assign({ timeZone: store.timezone, menuId: nm.activeId }, m);
  }
  function useUpsellMoment(hotel, store, menu) {
    var S = OO.pages.Menu && OO.pages.Menu.shared;
    var d = S && typeof S.useDisplay === 'function' ? S.useDisplay(hotel, store, null, OO.mode) : null;
    var draft = OO.useStore(selectDraft);
    var tick = d ? d.tick : 0;
    return React.useMemo(function () { return upsellMoment(hotel, store, menu, draft); }, [hotel, store, menu, draft, tick]);
  }
  // The upsell with that moment, in a component of its own: the moment's minute tick re-renders this subtree only, never
  // the whole cart page (its pricing, slot check and coupon re-check).
  function UpsellHost(props) {
    var at = useUpsellMoment(props.hotel, props.store, props.menu);
    return <OO.pages.Upsell hotel={props.hotel} store={props.store} menu={props.menu} lines={props.lines} moment={at || undefined} />;
  }

  // A reward line: no stepper (REF locks it at 1) and no edit pencil; the "Reward" mark, what it takes (rewardNote), its
  // issue, and Remove. Its total is the price the Sub-total counts; the Discount row takes it off.
  function RewardLine(props) {
    var l = props.line;
    var mods = OO.cart.displayMods(l);
    return (
      <ui.Card as="li" pad="sm" className={cx('oo-cart__line oo-cart__line--reward', props.issue && 'is-unavailable')}>
        <div className="oo-cart__line-text">
          <div className="oo-cart__line-name">{l.name}</div>
          {mods ? <div className="oo-cart__line-mods">{mods}</div> : null}
          {l.notes ? <div className="oo-cart__line-note">{'Note: ' + l.notes}</div> : null}
          <ui.Badge tone="done" className="oo-cart__line-mark">Reward</ui.Badge>
          <div className="oo-cart__line-points">{props.note}</div>
          {props.issue ? <ui.Badge tone="warn" className="oo-cart__line-issue">{props.issue}</ui.Badge> : null}
        </div>
        <div className="oo-cart__line-qty">{'Qty ' + l.qty}</div>
        <div className="oo-cart__line-total">{money(OO.cart.lineTotal(l), props.currency)}</div>
        <div className="oo-cart__line-actions">
          <ui.IconButton variant="ghost" icon="Trash" label={'Remove reward ' + l.name} onClick={props.onRemove} />
        </div>
      </ui.Card>
    );
  }

  // ---- the offers sheet, the typed code, the contact sheet (P2-14; see the header) ---------------------------------
  // The store's exposed coupons (fetched when the sheet opens: useCouponList), each with its eligibility for this order
  // (offerRows): an eligible one (or one waiting for a choice) → Apply; one short of its minimum → "Add $X more"
  // (disabled; REF), any other refusal → its reason and a disabled Apply; the applied one → Remove. An applied coupon
  // that is no longer listed still shows, so it can be removed. withCode: the typed code field on top (CodeForm). needLogin
  // (store.settings.forceLoginForCoupon, logged out): a "Log in to use coupons" prompt and no Apply. api: useCouponApply's
  // (busy: the coupon the server is checking; error: { id, text } its refusal).
  function OffersSheet(props) {
    var cc = props.cc;
    var api = props.api;
    var store = cc.store;
    var cur = store.currency;
    var q = useCouponList(store, cc, true);
    // The cart's own subtotal (free lines never count towards a minimum), as the coupon engine reads it.
    var base = OO.pricing.summary(cc.lines, store, { orderType: cc.orderType, paymentMethod: cc.paymentMethod, table: cc.table || undefined }).subtotal;
    var rows = q.loading || q.error ? [] : offerRows(q.list, cc, props.applied, base);
    var body;
    if (q.loading) {
      body = (
        <div className="oo-coupons__loading" aria-busy="true">
          <span className="oo-sr-only" role="status">Loading coupons</span>
          <ui.Skeleton variant="block" count={3} height={84} />
        </div>
      );
    } else if (q.error) {
      body = (
        <ui.EmptyState flat icon="Info" role="alert" title="We couldn't load coupons"
          body="Check your connection and try again." action={<ui.Button size="md" onClick={q.reload}>Try again</ui.Button>} />
      );
    } else if (!rows.length) {
      body = <ui.EmptyState flat icon="Ticket" title="No coupons right now" body={props.withCode ? 'Have a promo code? Enter it above.' : 'Check back soon for new deals.'} />;
    } else {
      body = (
        <ul className="oo-coupons" aria-label="Available coupons">
          {rows.map(function (r) {
            var c = r.coupon;
            var words = couponWords(c, cur);
            var err = api.error && api.error.id === c.id ? api.error : null;
            var why = err ? err.text : (!r.e.ok && r.e.reason ? r.e.reason : null);
            var label = c.code || c.name;
            var off = !r.on && (!r.canApply || props.needLogin);
            return (
              <li key={c.id} className={cx('oo-coupons__item', r.on && 'is-applied', off && 'is-off')}>
                <div className="oo-coupons__text">
                  <div className="oo-coupons__head">
                    <span className="oo-coupons__name">{c.name || c.code}</span>
                    <ui.Badge size="sm">{words.type}</ui.Badge>
                    {r.on ? <ui.Badge tone="done" size="sm">Applied</ui.Badge> : null}
                  </div>
                  <div className="oo-coupons__offer">{words.offer}</div>
                  {c.description ? <div className="oo-coupons__desc">{c.description}</div> : null}
                  <div className="oo-coupons__meta">
                    {c.code ? <span className="oo-coupons__code">{c.code}</span> : null}
                    {c.minOrder > 0 ? <span>{'Min order ' + money(c.minOrder, cur)}</span> : null}
                  </div>
                  {why ? <div className={cx('oo-coupons__why', err && 'is-error')} role={err ? 'alert' : undefined}>{why}</div> : null}
                </div>
                {r.on ? (
                  <ui.Button size="xs" variant="outline" aria-label={'Remove coupon ' + label} onClick={props.onRemove}>Remove</ui.Button>
                ) : (
                  <ui.Button size="xs" aria-label={(r.addMore > 0 ? 'Add ' + money(r.addMore, cur) + ' more for ' : 'Apply coupon ') + label}
                    disabled={off || !!api.busy} busy={api.busy === c.id} onClick={function () { api.apply(c); }}>
                    {r.addMore > 0 ? 'Add ' + money(r.addMore, cur) + ' more' : 'Apply'}
                  </ui.Button>
                )}
              </li>
            );
          })}
        </ul>
      );
    }
    var login = props.needLogin ? OO.router.link('profile', null, { hotel: cc.hotel, query: { directLogin: 'true' } }) : null;
    return (
      <ui.Sheet open variant="dialog" title="Choose a coupon" onClose={props.onClose} className="oo-offers-sheet">
        {login ? (
          <div className="oo-coupons__login">
            <ui.InlineNotice tone="info" icon="User">Log in to use coupons at {store.name}.</ui.InlineNotice>
            <ui.Button size="sm" href={login.href} onClick={login.onClick}>Log in</ui.Button>
          </div>
        ) : null}
        {props.withCode ? <CodeForm api={api} hotel={cc.hotel} /> : null}
        {body}
      </ui.Sheet>
    );
  }

  // The typed code (P2-14; REF's "Promo code"): upper-cased as typed, Enter or Apply sends it through useCouponApply
  // (validate_coupon_v4 accepts a code the list doesn't show: a personal or offer coupon). Its refusal shows under the
  // field ("Please sign in to use this coupon." gets a Log in link).
  function CodeForm(props) {
    var api = props.api;
    var textS = React.useState('');
    var err = api.error && api.error.id === 'code' ? api.error : null;
    var busy = api.busy === 'code';
    var login = err && err.login ? OO.router.link('profile', null, { hotel: props.hotel, query: { directLogin: 'true' } }) : null;
    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (!textS[0].trim() || api.busy) return;
      api.applyCode(textS[0]);
    }
    if (props.zeta) return <form className="oo-offers-code" onSubmit={submit} noValidate><div className="oo-offers-code__row"><input className="zeta-checkout-coupon-input" aria-label="Coupon code" placeholder="Coupon code" name="promo" autoComplete="off" maxLength={64} value={textS[0]} onChange={function(e){textS[1](String(e.target.value).toUpperCase());if(err)api.clearError();}}/><ui.Button type="submit" className="zeta-checkout-coupon-button" busy={busy} disabled={!textS[0].trim()||!!api.busy}>Apply</ui.Button></div>{err?<p role="alert" className="zeta-checkout-error">{err.text}</p>:null}{login?<a href={login.href} onClick={login.onClick}>Log in</a>:null}</form>;
    return (
      <form className="oo-offers-code" onSubmit={submit} noValidate>
        <div className="oo-offers-code__row">
          <ui.TextInput label="Promo code" name="promo" autoComplete="off" maxLength={64} value={textS[0]} placeholder={props.zeta ? "Coupon code" : "Enter promo code"}
            className="oo-offers-code__field" error={err ? err.text : null}
            onChange={function (v) { textS[1](String(v).toUpperCase()); if (err) api.clearError(); }} />
          <ui.Button type="submit" size="sm" busy={busy} disabled={!textS[0].trim() || (!!api.busy && !busy)}>Apply</ui.Button>
        </div>
        {login ? <a className="oo-offers-code__login" href={login.href} onClick={login.onClick}>Log in</a> : null}
      </form>
    );
  }

  // The contact details a guest's coupon is checked with (P2-14; REF OnlineOrderCouponContactModal: name ≥ 2
  // characters, a mobile number, an email). Prefilled from the checkout draft; on submit they fill the draft (checkout
  // shows them) and the coupon is checked with that email.
  function contactErrors(v) {
    var e = {};
    if (String(v.name || '').trim().length < 2) e.name = 'Enter your full name';
    var phone = String(v.phone || '').trim();
    var digits = phone.replace(/\D/g, '');
    if (!phone) e.phone = 'Enter your mobile number';
    else if (!/^[+\d\s().-]+$/.test(phone) || digits.length < 6 || digits.length > 15) e.phone = 'Enter a valid mobile number';
    var email = String(v.email || '').trim();
    if (!email) e.email = 'Enter your email address';
    else if (!EMAIL.test(email) || email.length > 254) e.email = 'Enter a valid email address';
    return e;
  }
  function ContactSheet(props) {
    var d = (props.cc && props.cc.draft) || {};
    var valS = React.useState({ name: String(d.name || ''), phone: String(d.phone || ''), email: String(d.email || '') });
    var shownS = React.useState(false);
    var v = valS[0];
    var errs = shownS[0] ? contactErrors(v) : {};
    function set(k) { return function (x) { var n = Object.assign({}, valS[0]); n[k] = x; valS[1](n); }; }
    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      shownS[1](true);
      if (Object.keys(contactErrors(v)).length || props.busy) return;
      props.onSubmit({ name: v.name.trim(), phone: v.phone.trim(), email: v.email.trim() });
    }
    var code = String(props.code || '').trim().toUpperCase();
    return (
      <ui.Sheet open variant="dialog" title="Complete your contact details" onClose={props.onClose} className="oo-coupon-contact">
        <form className="oo-coupon-contact__form" onSubmit={submit} noValidate>
          <p className="oo-coupon-contact__lead">
            Enter your contact details to confirm this offer and prefill your checkout.
            {code ? <React.Fragment>{' '}<span className="oo-coupons__code">{code}</span></React.Fragment> : null}
          </p>
          <ui.TextInput label="Full name" labelVisible name="name" autoComplete="name" required value={v.name} onChange={set('name')} error={errs.name} />
          <ui.TextInput label="Mobile number" labelVisible type="tel" name="tel" autoComplete="tel" inputMode="tel" required value={v.phone}
            onChange={set('phone')} error={errs.phone} />
          <ui.TextInput label="Email" labelVisible type="email" name="email" autoComplete="email" inputMode="email" required value={v.email}
            onChange={set('email')} error={errs.email} />
          <p className="oo-coupon-contact__note">We’ll use these details to check your coupon and for updates about this order.</p>
          <ui.Button type="submit" block busy={!!props.busy}>Apply coupon</ui.Button>
        </form>
      </ui.Sheet>
    );
  }

  // ---- page -------------------------------------------------------------------------------------------------------
  OO.pages.Cart = function Cart(props) {
    var route = props.route;
    var hotel = route.hotel;
    var o = { hotel: hotel };
    var P = useCartPage(hotel);
    var wide = ui.useWide();
    var editState = React.useState(null);      // { key, kind, itemId, comboId } of the line being edited
    var expState = React.useState(false);      // collapsed price summary opened (mobile)
    var toastState = React.useState(null);     // { id, text }
    var summaryRef = React.useRef(null);
    var listRef = React.useRef(null);
    var emptyRef = React.useRef(null);
    var editing = editState[0];
    var expanded = expState[0];
    // QR-CART: the table this cart orders for (mode qr), read per render as the checkout reads it ("At a table").
    var table = tableHere(hotel);
    // P1-21: the driving distance to the draft's picked address, for a delivery order (see the header). QR-CART: never
    // at a table (a dine-in order).
    var dist = useDeliveryDistance(P.store, P.draft, P.ready && !!P.store && !table && orderTypeOf(P.store, P.draft) === 'DELIVERY');

    React.useEffect(function () {
      if (expanded) reveal(summaryRef.current);
    }, [expanded]);

    function say(text) { toastState[1]({ id: Date.now() + Math.random(), text: text }); }

    var problem = P.h.notFound || (!!P.h.error && !P.store);
    // The order as priced (cartPricing; P1-21: a picked address re-selects the zone), before the early returns: the
    // coupon hooks read it. P2-14 (D-05): the applied coupon is re-checked on every change of the lines, the order
    // type, the time and the payment method (useCouponSync: kept / stored with its picks / removed with a toast), and
    // a coupon waiting for the customer's choice opens its picker (PickerHost) from "Choose" or right after Apply.
    // QR-CART (D-20): at a table the table checkout's pricing, and no coupon flows: no coupon context, so the D-05 sync
    // judges nothing (a stored coupon stays, unapplied) and nothing can be applied.
    var priced = !problem && P.ready ? cartPricing(P, hotel, dist, table) : null;
    var cc = priced && !table ? couponCtx({
      hotel: hotel, store: P.store, menu: couponMenu(hotel, priced.check.menu), lines: P.lines, coupon: P.coupon, orderType: priced.pc.orderType,
      paymentMethod: priced.pc.paymentMethod, at: priced.at, table: P.table, session: P.session, draft: P.draft, sum: priced.sum,
    }) : null;
    // Keep an already-applied coupon in sync with the cart. Coupon selection lives at checkout.
    useCouponSync(cc, say);
    // W5-FIX #3: REF's auto-removal of reward lines the order can no longer take ("Reward lines" in the header).
    // QR-CART: not at a table (D-20: the checkout's wallet section, the other place that removes them, is off there).
    var rctx = priced && !table ? rewardCtx(hotel, P.store, priced.pc.orderType, P.table, P.coupon) : null;
    useStaleRewards(rctx, P.lines, say);

    function goBack() { OO.router.back(problem ? OO.router.href('find-store') : OO.router.href('menu', null, o)); }
    // P3-12: the account hub. POLISH-2 (D-20): no account link in mode qr, as the checkout (see "At a table").
    var prof = OO.mode !== 'qr' && OO.mode !== 'kiosk' ? OO.router.link('account', null, o) : null;
    var header = (
      <ui.PageHeader title="Cart" onBack={goBack} backLabel={problem ? 'Back' : 'Back to menu'}
        profileHref={prof ? prof.href : undefined} onProfile={prof ? prof.onClick : undefined} profileLabel={prof ? 'Your account' : undefined} />
    );
    if (problem) return <div className="oo-cart">{header}<StoreProblem h={P.h} /></div>;
    if (!P.ready) return <div className="oo-cart">{header}<PageSkeleton label="Loading your cart" /></div>;

    var store = P.store;
    var menu = P.menu;
    var lines = P.lines;
    var cur = store.currency;
    var pc = priced.pc;
    var ds = priced.ds;
    var cp = priced.cp;
    var sum = priced.sum;
    var check = priced.check;
    var issues = lineIssues(lines, check.menu, { at: check.at });
    var issueKeys = Object.keys(issues);
    // POLISH-1: at a table the table checkout's verdict (Checkout.logic issueState: every issue blocks, see "At a table");
    // elsewhere a time-bound line doesn't block (see the header).
    var CL = table ? checkoutLogic() : null;
    var verdict = CL && typeof CL.issueState === 'function' ? CL.issueState(issues, check.at) : null;
    var blocked = verdict ? verdict.blocked : issueKeys.some(function (k) { return issues[k].code !== 'unavailable'; });
    var timeBound = !blocked && issueKeys.length > 0;
    var onlyTimeBound = issueKeys.length > 0 && issueKeys.every(function (k) { return issues[k].code === 'unavailable'; });
    var empty = !lines.length;
    var collapsed = !wide && store.checkout.priceSummary === 'collapsed';
    var menuLink = OO.router.link('menu', null, o);
    var checkoutLink = OO.router.link('checkout', null, o);
    // P5-06b: begin_checkout as the customer heads to the checkout, before it (the tag frame goes there).
    var toCheckout = function (e) { if (OO.analytics) OO.analytics.beginCheckout(lines, sum.total, store); checkoutLink.onClick(e); };
    var storeLine = wide
      ? [store.name, store.address && store.address.full].filter(Boolean).join(' · ')
      : store.name + ' · ' + (OO.mode === 'kiosk' ? OO.kiosk.label(hotel) : TYPE_LABEL[pc.orderType]);

    // After a line is removed (or re-keyed by an edit) its buttons are gone: park focus on the list (or the empty
    // state) unless something else already took it.
    function refocus(onlyIfLost) {
      var run = function () {
        var a = document.activeElement;
        if (onlyIfLost && a && a !== document.body && document.contains(a)) return;
        focusEl(listRef.current || emptyRef.current);
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 0);
    }
    function removeLine(l) { OO.store.cartRemove(l.key); OO.platform.haptic('light'); say('Removed ' + l.name); refocus(); }
    // W5-FIX #3: a reward line says it as the checkout's wallet section does; its points at the order's row.
    function removeReward(l) { OO.store.cartRemove(l.key); OO.platform.haptic('light'); say(l.name + ' removed from your rewards'); refocus(); }
    var rewardRule = rctx && lines.some(OO.wallet.isReward) ? OO.wallet.rewardBudget(lines, rctx).rule : null;
    // Editable unless the line has a problem other than changed options (which the editor resolves).
    function canEdit(l) {
      var why = issues[l.key];
      if (!menu || (why && why.code !== 'changed')) return false;
      var itemMenu = l.reward ? OO.model.redemptionMenu(menu) : menu;
      return l.kind === 'combo' ? !!(menu.combos && menu.combos[l.comboId]) : !!(itemMenu.products && itemMenu.products[l.itemId]);
    }
    function closeEdit() { editState[1](null); refocus(true); }
    // The overlays (T3b) call onAdded(line, info) once the line is replaced, then (or not) onClose: both just close;
    // one confirmation, info.message ('Cart updated').
    function edited(line, info) {
      editState[1](null);
      say((info && info.message) || (typeof line === 'string' && line) || 'Cart updated');
      refocus(true);
    }
    var summaryCard = !collapsed || expanded ? (
      <div ref={summaryRef} className="oo-cart__summary">
        <ui.Card pad="summary">
          <ui.PriceRows title="Price summary" rows={priceRows(sum)} total={money(sum.total, cur)}>
            {wide ? (
              <ui.Button block href={checkoutLink.href} onClick={toCheckout} disabled={blocked}>Continue to checkout</ui.Button>
            ) : null}
          </ui.PriceRows>
        </ui.Card>
      </div>
    ) : null;

    var editor = null;
    if (editing) {
      var common = { hotel: hotel, store: store, menu: menu, editLineKey: editing.key, onClose: closeEdit, onAdded: edited };
      var Overlay = editing.kind === 'combo' ? OO.pages.ComboBuilder : OO.pages.ItemSheet;
      if (Overlay) {
        editor = editing.kind === 'combo'
          ? <Overlay key={'edit-' + editing.key} {...common} comboId={editing.comboId} />
          : <Overlay key={'edit-' + editing.key} {...common} productId={editing.itemId} />;
      }
    }

    return (
      <div className={cx('oo-cart', empty && 'oo-cart--empty')}>
        {header}
        <ui.Container>
          <div className="oo-cart__body">
            <div className="oo-cart__col oo-cart__col--main">
              <ui.SectionTitle level="page" className="oo-cart__title"
                sub={<React.Fragment>{wide ? <OO.icons.Pin size={14} /> : null}<span>{storeLine}</span></React.Fragment>}>
                Order summary
              </ui.SectionTitle>
              {empty ? (
                <div ref={emptyRef} tabIndex={-1} className="oo-cart__empty">
                  <ui.EmptyState icon="Bag" title="Your cart is empty" body="Add something tasty from the menu to get started."
                    action={<ui.Button size="md" href={menuLink.href} onClick={menuLink.onClick}>Browse menu</ui.Button>} />
                </div>
              ) : (
                <React.Fragment>
                  {P.repriced ? <ui.InlineNotice tone="info" icon="Info">Prices updated to today’s menu.</ui.InlineNotice> : null}
                  {blocked ? (
                    <ui.InlineNotice tone="warn">{verdict && onlyTimeBound ? TABLE_UNAVAILABLE : 'Some items can’t be ordered as they are. Edit or remove them to continue.'}</ui.InlineNotice>
                  ) : null}
                  {timeBound ? (
                    <ui.InlineNotice tone="warn">
                      {(check.at ? 'Some items aren’t available at ' + check.at + '.' : 'Some items aren’t available right now.') + ' Choose another time at checkout, or remove them.'}
                    </ui.InlineNotice>
                  ) : null}
                  <ul ref={listRef} tabIndex={-1} className="oo-cart__lines" aria-label="Items in your cart">
                    {couponLines(lines, cp, store, sum.coupon.ok).map(function (row) {
                      var l = row.line;
                      if (row.free) return <FreeLine key={row.key} line={l} label={row.free.label} currency={cur} />;
                      if (l.reward) {
                        return (
                          <RewardLine key={l.key} line={l} currency={cur} note={rewardNote(l, store, rewardRule)}
                            issue={issues[l.key] ? issues[l.key].text : null} onRemove={function () { removeReward(l); }} />
                        );
                      }
                      return (
                        <CartLine key={l.key} line={l} currency={cur} part={row.part ? row.part.label : null}
                          issue={issues[l.key] ? issues[l.key].text : null} canEdit={canEdit(l)}
                          onQty={function (q) { OO.store.cartSetQty(l.key, q); OO.platform.haptic('selection'); }}
                          onRemove={function () { removeLine(l); }}
                          onEdit={function () { editState[1]({ key: l.key, kind: l.kind, itemId: l.itemId, comboId: l.comboId }); }} />
                      );
                    })}
                  </ul>
                  <a className="oo-cart__more" href={menuLink.href} onClick={menuLink.onClick}>
                    <OO.icons.Plus size={16} />Add more items
                  </a>
                </React.Fragment>
              )}
              {OO.pages.Upsell ? <UpsellHost hotel={hotel} store={store} menu={menu} lines={lines} /> : null}
            </div>
            {empty ? <ui.PoweredBySipo className="oo-cart__powered" /> : (
              <div className="oo-cart__col oo-cart__col--side">
                {summaryCard}
                <ui.PoweredBySipo />
              </div>
            )}
          </div>
        </ui.Container>
        {!empty && !wide ? (
          <ui.BottomBarCTA label={collapsed ? 'Price summary' : 'Total'} amount={money(sum.total, cur)}
            onLabelClick={collapsed ? function () { expState[1](!expanded); } : undefined} labelExpanded={expanded}>
            <ui.Button block href={checkoutLink.href} onClick={toCheckout} disabled={blocked}>Checkout</ui.Button>
          </ui.BottomBarCTA>
        ) : null}
        {editor}
        {toastState[0] ? (
          <ui.Toast key={toastState[0].id} open onClose={function () { toastState[1](null); }}>{toastState[0].text}</ui.Toast>
        ) : null}
      </div>
    );
  };

  OO.pages.Cart.shared = {
    TYPE_LABEL: TYPE_LABEL,
    money: money,
    useCartPage: useCartPage,
    orderTypesOf: orderTypesOf,
    methodsFor: methodsFor,
    pricingCtx: pricingCtx,
    liveCoupon: liveCoupon,
    summaryOf: summaryOf,
    pointsOf: pointsOf, // P2-07
    useDeliveryDistance: useDeliveryDistance,
    priceRows: priceRows,
    totalLabel: totalLabel,
    lineIssues: lineIssues,
    slotCheck: slotCheck,
    reveal: reveal,
    focusEl: focusEl,
    StoreProblem: StoreProblem,
    PageSkeleton: PageSkeleton,
    // P2-14: the coupon helpers (see the header)
    couponCtx: couponCtx,
    couponPlan: couponPlan,
    useCouponSync: useCouponSync,
    slotAt: slotAt,
    tableOf: tableOf,
    couponMenu: couponMenu,
    couponMoment: couponMoment,
    couponLines: couponLines,
    snapshotLines: snapshotLines,
    PickerHost: PickerHost,
    PickPrompt: PickPrompt,
    useCouponApply: useCouponApply,
    useCouponList: useCouponList,
    couponWords: couponWords,
    offerRows: offerRows,
    offerCount: offerCount,
    rewardNote: rewardNote, // W5-FIX #3
    useStaleRewards: useStaleRewards, // W5-FIX #3
    upsellMoment: upsellMoment, // W5-FIX #4
    useUpsellMoment: useUpsellMoment, // W5-FIX #4
    OffersSheet: OffersSheet,
    CodeForm: CodeForm,
    ContactSheet: ContactSheet,
  };
})();


// ===================== src/pages/coupon-pickers.jsx =====================
// src/pages/coupon-pickers.jsx: OO.pages.CouponPicker, the coupon item pickers (Batch 2 P2-12 / P2-13): the item
// picker and the BOGO source (P2-12), the BUY_N two-step and the giveaway list (forceFresh; P2-13). They build the free
// lines with OO.coupons (lib/coupons.jsx). Not a routed page: the offers UI (pages/checkout-offers.jsx, pages/cart.jsx)
// opens it while a free-item coupon waits for a choice (OO.coupons: code 'PICK', pending). It stores nothing itself:
// the picks go back to the caller, which rechecks and stores the coupon (OO.coupons.recheck → OO.store.setCoupon).
// REF: GuidedBogoLinePicker, GuidedItemDiscountPicker, GuidedBuyNGetMModal, GuidedCouponGiveawayPicker (and their
// CartContext handlers: select*, beginCouponGiveaway, applyCouponWithFreeItems). Styles: styles/offers.css (its "coupon
// pickers" section) plus the item sheet's option groups (styles/item.css).
//
// <OO.pages.CouponPicker store menu lines coupon choices mode at currency orderType onPick onClose />
//   store      the normalised Store (timezone, orderTypes, applyDiscountToModifiers, id; currency when `currency` is
//              absent).
//   menu       the priced menu (useHotel().menu: the cart's pricetag prices): rewards are built from ITS products.
//   lines      the cart lines.
//   coupon     the Coupon being applied or already applied, with its current picks (coupon.picks).
//   choices    OO.coupons.freeLines(coupon, lines, …).choices, the choice that made it pending. null / absent → derived
//              here the same way (logic.choicesOf; recheck() returns no choices).
//   mode       'online' | 'app' | 'qr' (default OO.mode): freeLines' auto-pick rule (REF app / kiosk pick a BOGO source
//              and an ITEM product for the customer, so those two never reach the app) and the domain reward lists are
//              judged in.
//   at         the order's instant (ms | Date; default the app clock, OO.data.now()): reward lists are judged at it.
//   currency   default store.currency.
//   orderType  optional (not in the P2-14 contract; pass it when known): 'PICKUP' | 'DELIVERY'. Default: the order type
//              the cart prices (OO.pages.Cart.shared.pricingCtx over OO.store's checkout draft), else the draft's when the
//              store offers it, else the store's first. With mode it names the availability domain
//              (OO.model.domain(orderType, mode): ONLINE_[APP_]PICKUP | … | TABLE_SIDE in qr mode).
//   onPick(picks)  the COMPLETE new picks object (coupon.picks merged with the new pick), already checked with
//              OO.coupons.validPick, and with freeLines (the build no longer asks for that choice). The caller rechecks
//              and stores the coupon. Called once per pick: a second tap is ignored until the caller renders again with
//              a coupon (a new object, the same picks or not: a caller that didn't take the pick gets the retry). The
//              reward steps a customer has taken are kept by the coupon's id and picks, not its identity (the cart
//              re-reads the applied coupon on every render): they survive re-renders and restart when the picks change.
//   onClose()  dismissed without a pick (ESC, the scrim, the close button).
//   Renders null without a coupon, or when there is no choice it can make (none pending; an unknown kind).
//
// The pickers: a dialog Sheet (OO.ui.Sheet variant 'dialog': a bottom sheet on mobile, a 520px card on desktop; focus
// trap, ESC, scrim). The coupon's code, a line of copy, then one full-width button per choice; a tap picks it.
//   'source'   BOGO with several eligible lines (choices = { kind: 'source', lines }): "Which item should we copy?". A
//              row per cart line: name × qty, its size and options (OO.cart.displayMods), "Base $12.00 free" (or
//              "2 × base $12.00 free" when max_free_count copies more than one unit) and, unless the store applies
//              discounts to modifiers, "Extras $2.50 payable" (REF: the copy's extras remain payable). Pick → { source:
//              line.key }.
//   'item'     ITEM 100 % with several products in the cart (choices = { kind: 'item', products, lines }): "Which item
//              should be free?". A row per product: the unit that would be free (its cheapest unit, its options) with
//              "Free" or, under a cap below its price, "$5.00 off", the unit's base price struck. Pick → { product: id }.
//   The savings come from OO.coupons.freeLines run with that pick (the clone count, the split unit and its %), never
//   re-derived here. A pick the engine turns down shows its reason in a warning notice and nothing is picked.
//   'rewards'  BUY_N (choices = { kind: 'rewards', count, missing, scope, products }), REF's two steps: "1 · Qualifying
//              items" (the cart's qualifying lines, "This offer needs 2 qualifying items. You have 3 in your cart.",
//              CTA "Choose your free item" | "Choose 2 free items", disabled with a notice when no reward can be
//              picked), then "2 · Pick free items": "Free item 1 of 2" with a progress bar, the rewards chosen so far
//              (the coupon's valid ones are kept: freeLines' picks), and the reward list; a product with something to
//              choose opens the configure step first. The last one → onPick({ …, rewards: [ItemLine × count] }).
//              Back: from the configure step to the list; from the list, "Change previous item" drops the last reward,
//              else back to step 1. REF: one reward to pick from and one to go → its configure step at once.
//   'giveaway' ITEM 100 % with nothing eligible in the cart (choices = { kind: 'giveaway', count: 1, scope, products }):
//              "Choose your free item" ("Pick one: it’s on the house."), the list, then the configure step when there
//              is something to choose; a single reward with something to choose opens it at once (REF). Pick →
//              onPick({ …, rewards: [ItemLine] }). forceFresh (REF beginCouponGiveaway): when the coupon carries no
//              product rows (choices.products []), the store's coupon list is read again with OO.api.getCoupons(
//              store.id, { fresh: true }) and the row of this coupon (same id, else the same upper-cased code) gives
//              them; meanwhile the list is a skeleton.
//   The reward lists (logic.candidates): the coupon's product rows (choices.products, or the fresh ones) that are in
//   its scope (choices.scope: the guard's truth, never widened), in the rows' order; no rows at all → the scope's ids.
//   Each is the MENU's product (not the row: its price is the menu's / pricetag's, which the validator checks); one the
//   menu doesn't have, or whose sizes the scope can never match, is left out. D-07 via OO.model.displayState at `at` in
//   the order's domain (no named menu, as the cart's checks): 'hide' → left out; 'grey' → listed greyed with its label
//   ("Available Tomorrow At 7:00 AM", "Sold out today"), never pickable; so is one the menu's static flags turn down
//   (OO.cart.lineIssue: 'Sold out', 'Not available right now') or whose required group is blocked. "Free" (or the
//   giveaway's capped "$5.00 off") with the price struck; "Choose a size and options next" when it has some.
//   The configure step (the item sheet's rules: OO.modifiers, OO.pages.ItemSheet.parts.ModGroup): the sizes the scope
//   allows (no prices: the base is free; the first allowed size to start, its options pruned as a size change prunes
//   them), then every visible option group with the preselects (required ones open, optional single-choice ones
//   collapsed, a counter for allow_same), the CTA naming the first missing required group ("Choose a milk") until done,
//   then "Add free item" | "Confirm free item 1 of 2". Footer: "You pay $1.50" (the reward's payable part: options,
//   and a capped giveaway's rest; at an applyDiscountToModifiers store the options come off at the same %) or "Free".
//   The reward = OO.cart.itemLine(menu product, { sizeId, sel, qty: 1 }), checked with OO.coupons.validPick (menu).
//
// OO.pages.CouponPicker.logic (pure; the tests read it):
//   choicesOf(coupon, lines, { choices, menu, mode }) → Choices | null   `choices` when it is one (an object with a
//       kind), else OO.coupons.freeLines(coupon, lines, { menu, mode }).choices.
//   merge(coupon, patch) → picks   coupon.picks (when an object) with `patch` laid over it.
//   commit(coupon, kind, patch, lines, { menu, mode }) → { ok, picks, reason }   the pick patch ({ source } | { product }
//       | { rewards: [ItemLine] }, each reward as { reward }) checked with OO.coupons.validPick (with the menu), merged,
//       then freeLines over the merged picks: refused when a pick is invalid (validPick's reason) or the build still
//       asks for the same kind of choice (its reason); else { ok: true, picks (complete), reason: null }.
//   sourceRows(coupon, choices, lines, { store, mode, currency }) → [{ key, name, details, image, qty, base, free (the
//       copies), saving, extras (payable; 0 at an applyDiscountToModifiers store), save, extra, pick: { source } }]
//   itemRows(coupon, choices, lines, { store, menu, mode, currency }) → [{ key, id, name, details, image, unitKey,
//       base, pct, saving, save, was, pick: { product } }]
//   orderTypeOf(store, draft) → 'PICKUP' | 'DELIVERY'   the default order type (see the orderType prop).
//   judgeAt(store, { at, now, mode, orderType }) → { now (ms: at), today (ms: now, default OO.data.now()), timeZone,
//       domain }   displayState's opts for the reward lists.
//   freshRows(raw, coupon) → [row]   the coupon's applicable_products in a coupon_get_v4 body ({ data } or a bare array):
//       its row by id, else by upper-cased code; [] when none.
//   productIds(scope, rows) → [id]   the rows' ids (itemId | id | product_id) that are in the scope, in the rows' order;
//       no rows → the scope's ids.
//   candidates(coupon, kind, choices, lines, { menu, store, mode, at, now, orderType, rows, currency }) → [{ key, id,
//       name, image, details, sizes (allowed), sizeId, sel (the start), needsConfig, base, pct, saving, save, was, extra,
//       off (the greyed label | null), label }]   rows: the fresh rows (undefined / null → choices.products).
//   qualifying(coupon, lines) → { lines, units, min }   BUY_N's qualifying cart lines (no reward / free line).
//   seedOf(coupon, kind, lines, { menu, mode }) → [ItemLine]   the rewards the coupon keeps (freeLines' picks).
//   configView(ctx, state) → { product, candidate, sizes, groups, ok, line, pct, payable, cta } | null
//   flow (a pure reducer; ctx = { coupon, kind, count, cands, lines, menu, mode, store }; state = { step: 'qualify' |
//       'pick' | 'configure' | 'done', staged: [ItemLine], cfg: { id, sizeId, sel } | null, error }): start(ctx, seed),
//       next(s, ctx), pick(s, ctx, id), size(s, ctx, sizeId), option(s, ctx, 'toggle' | 'increment' | 'decrement',
//       groupId, optionId), confirm(s, ctx), back(s, ctx). 'done' = every reward staged: the component commits.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  function arr(v) { return Array.isArray(v) ? v : []; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function r2(n) { return OO.format.round2(n); }
  function money(n, cur) { return OO.format.money(n, cur); }
  function noop() {}

  var NOT_NOW = 'Not available right now';

  // The coupon as far as the reward steps care: its id and picks (a re-read coupon is a new object with the same ones).
  function pickSig(coupon) {
    try { return String(coupon && coupon.id) + '|' + JSON.stringify((coupon && coupon.picks) || null); } catch (e) { return String(coupon && coupon.id); }
  }

  // ---- logic (pure; see the header) ----------------------------------------------------------------------------------
  var KINDS = ['source', 'item', 'rewards', 'giveaway'];

  function choicesOf(coupon, lines, opts) {
    opts = opts || {};
    if (isRecord(opts.choices) && opts.choices.kind) return opts.choices;
    if (!coupon || !OO.coupons.freeFlow(coupon)) return null;
    return OO.coupons.freeLines(coupon, arr(lines), { menu: opts.menu || undefined, mode: opts.mode }).choices || null;
  }

  function merge(coupon, patch) {
    return Object.assign({}, coupon && isRecord(coupon.picks) ? coupon.picks : {}, patch || {});
  }

  function commit(coupon, kind, patch, lines, opts) {
    opts = opts || {};
    var C = OO.coupons;
    var menu = opts.menu || undefined;
    var picks = [];
    if (patch && patch.rewards !== undefined) picks = arr(patch.rewards).map(function (r) { return { reward: r }; });
    else if (patch && patch.source !== undefined) picks = [{ source: patch.source }];
    else if (patch && patch.product !== undefined) picks = [{ product: patch.product }];
    if (!picks.length) return { ok: false, picks: null, reason: 'This coupon has no choice to make' };
    for (var i = 0; i < picks.length; i++) {
      var check = C.validPick(coupon, picks[i], lines, { menu: menu });
      if (!check.ok) return { ok: false, picks: null, reason: check.reason };
    }
    var all = merge(coupon, patch);
    var b = C.freeLines(coupon, arr(lines), { picks: all, menu: menu, mode: opts.mode });
    if (b.code === 'PICK' && b.choices && b.choices.kind === kind) return { ok: false, picks: null, reason: b.reason };
    return { ok: true, picks: all, reason: null };
  }

  // A preview build with one pick laid over the coupon's (the clones / the split unit the engine would make).
  function preview(coupon, lines, patch, opts) {
    return OO.coupons.freeLines(coupon, arr(lines), { picks: merge(coupon, patch), mode: opts.mode });
  }

  function sourceRows(coupon, choices, lines, opts) {
    opts = opts || {};
    var cur = opts.currency;
    var net = !!(opts.store && opts.store.applyDiscountToModifiers); // the validator nets the copy's options too
    return arr(choices && choices.lines).map(function (l) {
      var copies = preview(coupon, lines, { source: l.key }, opts).lines.filter(function (f) { return f.free && f.free.source === l.key; });
      var n = copies.length;
      var base = num(l.basePrice);
      var extras = net ? 0 : r2(copies.reduce(function (t, f) { return t + num(f.unitPrice) - num(f.basePrice); }, 0));
      var qty = Math.max(1, Math.floor(num(l.qty)));
      return {
        key: l.key, name: l.name + (qty > 1 ? ' × ' + qty : ''), details: OO.cart.displayMods(l), image: l.image || null, qty: qty,
        base: base, free: n, saving: r2(base * n), extras: extras,
        save: (n > 1 ? n + ' × base ' : 'Base ') + money(base, cur) + ' free',
        extra: extras > 0 ? 'Extras ' + money(extras, cur) + ' payable' : null,
        pick: { source: l.key },
      };
    });
  }

  function itemRows(coupon, choices, lines, opts) {
    opts = opts || {};
    var cur = opts.currency;
    var products = opts.menu && opts.menu.products ? opts.menu.products : {};
    var own = arr(choices && choices.lines);
    return arr(choices && choices.products).map(function (id) {
      var mine = own.filter(function (l) { return num(l.itemId) === num(id); });
      var b = preview(coupon, lines, { product: id }, opts);
      var unit = b.split ? arr(lines).filter(function (l) { return l.key === b.split.key; })[0] || null : null;
      var pct = b.split ? num(b.split.pct) : 100;
      var first = unit || mine[0] || {};
      var base = num(first.basePrice);
      var saving = r2(base * pct / 100);
      var p = products[id] || null;
      return {
        key: 'p' + id, id: num(id), name: first.name || (p && p.name) || '', details: unit ? OO.cart.displayMods(unit) : '',
        image: first.image || (p && p.image) || null, unitKey: unit ? unit.key : null,
        base: base, pct: pct, saving: saving,
        save: pct >= 100 ? 'Free' : money(saving, cur) + ' off', was: base > 0 ? money(base, cur) : null,
        pick: { product: num(id) },
      };
    });
  }

  // ---- P2-13: the reward lists (BUY_N rewards, the giveaway) ---------------------------------------------------------
  function msOf(v, fallback) {
    if (v instanceof Date) return v.getTime();
    var n = typeof v === 'number' ? v : typeof v === 'string' && v ? Date.parse(v) : NaN;
    return isFinite(n) ? n : fallback;
  }

  function orderTypeOf(store, draft) {
    var K = OO.pages.Cart && OO.pages.Cart.shared;
    if (store && K && typeof K.pricingCtx === 'function') {
      try {
        var t = K.pricingCtx(store, draft || null).orderType;
        if (t) return t;
      } catch (e) { /* the store's own list below */ }
    }
    var types = arr(store && store.orderTypes).filter(function (x) { return x === 'PICKUP' || x === 'DELIVERY'; });
    var want = draft && draft.orderType;
    return types.indexOf(want) >= 0 ? want : types[0] || 'PICKUP';
  }

  function judgeAt(store, opts) {
    opts = opts || {};
    var today = msOf(opts.now, null);
    if (today === null) today = OO.data.now().getTime();
    var moment = {
      now: msOf(opts.at, today), today: today, timeZone: store && store.timezone ? store.timezone : undefined,
      domain: OO.model.domain(opts.orderType || 'PICKUP', opts.mode),
    };
    if (OO.model.hideOutsideAvailability(store, opts.mode)) moment.hideOutsideAvailability = true;
    return moment;
  }

  function freshRows(raw, coupon) {
    var list = Array.isArray(raw) ? raw : arr(raw && raw.data);
    var id = num(coupon && coupon.id);
    var code = String((coupon && coupon.code) || '').trim().toUpperCase();
    var hit = list.filter(function (r) { return isRecord(r) && id > 0 && num(r.id) === id; })[0]
      || list.filter(function (r) { return isRecord(r) && !!code && String(r.code || '').trim().toUpperCase() === code; })[0];
    return hit ? arr(hit.applicable_products) : [];
  }

  function rowId(r) {
    var v = !isRecord(r) ? r : r.itemId !== undefined && r.itemId !== null ? r.itemId : r.id !== undefined && r.id !== null ? r.id : r.product_id;
    var n = Math.floor(num(v));
    return n > 0 ? n : null;
  }

  function productIds(scope, rows) {
    var inScope = {};
    arr(scope).forEach(function (e) { var id = rowId(e); if (id !== null) inScope[id] = true; });
    var out = [];
    (arr(rows).length ? arr(rows) : arr(scope)).forEach(function (r) {
      var id = rowId(r);
      if (id !== null && inScope[id] && out.indexOf(id) < 0) out.push(id);
    });
    return out;
  }

  // The product's sizes the scope allows: all of them when an entry for it lists none; ok false when it can never
  // match (only sized entries and none of those sizes, or sizes asked of a product that has none).
  function scopeSizes(product, scope) {
    return OO.coupons.rewardSizes(product, scope);
  }

  // The configure step's start, as the item sheet starts (OO.modifiers.initial) and then picks the first allowed size.
  function startCfg(product, sizes) {
    var M = OO.modifiers;
    var sizeId = sizes.length ? sizes[0].id : null;
    return { sizeId: sizeId, sel: M.prune(product, sizeId, M.initial(product).sel) };
  }

  // A giveaway unit's % (the engine's: 100, or MAX_DISCOUNT_VALUE of its price under a lower cap); BUY_N rewards: 100.
  function rewardPct(coupon, kind, line, lines, opts) {
    if (kind !== 'giveaway') return 100;
    var b = OO.coupons.freeLines(coupon, arr(lines), { picks: merge(coupon, { rewards: [line] }), mode: opts.mode });
    return b.lines.length && b.lines[0].free ? num(b.lines[0].free.pct) : 100;
  }

  // What the reward leaves to pay: its options (allowance-aware) and a capped unit's rest; at an applyDiscountToModifiers
  // store the validator nets the options at the same % (OO.pricing's summary).
  function payableOf(line, pct, store) {
    var unit = num(line.unitPrice);
    var off = (store && store.applyDiscountToModifiers ? unit : num(line.basePrice)) * pct / 100;
    return r2(Math.max(0, unit - off));
  }

  function candidates(coupon, kind, choices, lines, opts) {
    opts = opts || {};
    var menu = OO.model.redemptionMenu(opts.menu);
    if (!coupon || !choices || !menu || !menu.products) return [];
    var M = OO.modifiers;
    var cur = opts.currency;
    var scope = arr(choices.scope);
    var when = judgeAt(opts.store, opts);
    var rows = opts.rows !== undefined && opts.rows !== null ? opts.rows : choices.products;
    var out = [];
    // Saved coupon rows may predate a hidden product or a catalogue refresh. The opt-in catalogue is authoritative;
    // use every configured reward it contains, even when the old coupon response omitted that product's detail row.
    productIds(scope, Object.prototype.hasOwnProperty.call(menu, 'redemptionProducts') ? [] : rows).forEach(function (id) {
      var p = menu.products[id];
      if (!p) return; // not on this menu: never orderable here
      var sz = scopeSizes(p, scope);
      if (!sz.ok) return;
      var shown = OO.model.displayState(menu, p, when) || { show: 'normal', label: null };
      if (shown.show === 'hide') return; // D-07: never orderable here (the domain, nothing within 7 days)
      var cfg = startCfg(p, sz.sizes);
      var v = M.view(p, { sizeId: cfg.sizeId, sel: cfg.sel, qty: 1 });
      var line = OO.cart.itemLine(p, { sizeId: cfg.sizeId, sel: cfg.sel, qty: 1 });
      var issue = OO.cart.lineIssue(line, menu); // the menu's static flags, as validPick reads them ('changed' = a choice)
      var blocked = v.groups.filter(function (g) { return g.blocked; })[0];
      var off = shown.show === 'grey' ? shown.label || NOT_NOW
        : issue && issue.code !== 'changed' ? issue.text
        : blocked ? blocked.reason || NOT_NOW : null;
      var pct = rewardPct(coupon, kind, line, lines, opts);
      var base = num(line.basePrice);
      var saving = r2(base * pct / 100);
      var choose = [sz.sizes.length > 1 ? 'a size' : null, v.groups.length ? 'options' : null].filter(Boolean);
      out.push({
        key: 'p' + id, id: num(p.id), name: p.name, image: p.image || null,
        details: sz.sizes.length === 1 ? sz.sizes[0].name : '',
        sizes: sz.sizes, sizeId: cfg.sizeId, sel: cfg.sel, needsConfig: choose.length > 0,
        base: base, pct: pct, saving: saving,
        save: pct >= 100 ? 'Free' : money(saving, cur) + ' off', was: base > 0 ? money(base, cur) : null,
        extra: choose.length ? 'Choose ' + choose.join(' and ') + ' next' : null,
        off: off, label: off,
      });
    });
    return out;
  }

  function qualifying(coupon, lines) {
    var own = OO.coupons.eligibleLines(lines, coupon).filter(function (l) { return !l.reward && !l.free; });
    return {
      lines: own,
      units: own.reduce(function (t, l) { return t + Math.max(0, Math.floor(num(l.qty))); }, 0),
      min: Math.max(1, Math.floor(num(coupon && coupon.minItemCount))),
    };
  }

  function seedOf(coupon, kind, lines, opts) {
    if (kind !== 'rewards' || !coupon) return [];
    opts = opts || {};
    var b = OO.coupons.freeLines(coupon, arr(lines), { menu: opts.menu || undefined, mode: opts.mode });
    return arr(b.picks && b.picks.rewards);
  }

  // ---- the reward flow (a pure reducer; see the header) --------------------------------------------------------------
  function candOf(ctx, id) { return arr(ctx.cands).filter(function (c) { return c.id === num(id); })[0] || null; }
  function productOf(ctx, id) {
    var menu = OO.model.redemptionMenu(ctx.menu);
    return menu && menu.products ? menu.products[id] || null : null;
  }
  function patch(s, p) { return Object.assign({}, s, p); }
  function configureOf(s, cand) { return patch(s, { step: 'configure', cfg: { id: cand.id, sizeId: cand.sizeId, sel: cand.sel }, error: null }); }
  // REF: a single reward to pick from with one to go → its configure step at once (when it has something to choose).
  function only(s, ctx) {
    var open = arr(ctx.cands).filter(function (c) { return !c.off; });
    return open.length === 1 && ctx.count - s.staged.length === 1 && open[0].needsConfig ? configureOf(s, open[0]) : s;
  }
  function start(ctx, seed) {
    var s = { step: ctx.kind === 'rewards' ? 'qualify' : 'pick', staged: arr(seed).slice(0, Math.max(0, ctx.count - 1)), cfg: null, error: null };
    return ctx.kind === 'rewards' ? s : only(s, ctx);
  }
  function next(s, ctx) { return s.step === 'qualify' ? only(patch(s, { step: 'pick', error: null }), ctx) : s; }
  function stage(s, ctx, line) {
    var check = OO.coupons.validPick(ctx.coupon, { reward: line }, ctx.lines, { menu: ctx.menu || undefined });
    if (!check.ok) return patch(s, { error: check.reason });
    var staged = s.staged.concat([line]);
    return patch(s, { step: staged.length >= ctx.count ? 'done' : 'pick', staged: staged, cfg: null, error: null });
  }
  function pick(s, ctx, id) {
    var cand = candOf(ctx, id);
    var p = productOf(ctx, id);
    if (!cand || cand.off || !p || s.step !== 'pick') return s;
    if (cand.needsConfig) return configureOf(s, cand);
    return stage(s, ctx, OO.cart.itemLine(p, { sizeId: cand.sizeId, sel: cand.sel, qty: 1 }));
  }
  function size(s, ctx, sizeId) {
    var p = s.cfg ? productOf(ctx, s.cfg.id) : null;
    var cand = s.cfg ? candOf(ctx, s.cfg.id) : null;
    if (!p || !cand || !cand.sizes.some(function (z) { return z.id === sizeId; })) return s;
    return patch(s, { cfg: { id: s.cfg.id, sizeId: sizeId, sel: OO.modifiers.prune(p, sizeId, s.cfg.sel) }, error: null });
  }
  function option(s, ctx, op, groupId, optionId) {
    var p = s.cfg ? productOf(ctx, s.cfg.id) : null;
    var M = OO.modifiers;
    if (!p || ['toggle', 'increment', 'decrement'].indexOf(op) < 0) return s;
    var g = M.visibleGroups(p, s.cfg.sizeId).filter(function (x) { return x.id === groupId; })[0];
    return g ? patch(s, { cfg: Object.assign({}, s.cfg, { sel: M[op](g, s.cfg.sel, optionId) }), error: null }) : s;
  }
  function confirm(s, ctx) {
    var p = s.cfg ? productOf(ctx, s.cfg.id) : null;
    if (!p || !OO.modifiers.view(p, { sizeId: s.cfg.sizeId, sel: s.cfg.sel, qty: 1 }).ok) return s;
    return stage(s, ctx, OO.cart.itemLine(p, { sizeId: s.cfg.sizeId, sel: s.cfg.sel, qty: 1 }));
  }
  function back(s, ctx) {
    if (s.step === 'configure') return patch(s, { step: 'pick', cfg: null, error: null });
    if (s.step === 'pick' && s.staged.length) return patch(s, { staged: s.staged.slice(0, -1), error: null });
    if (s.step === 'pick' && ctx.kind === 'rewards') return patch(s, { step: 'qualify', error: null });
    return s;
  }

  function configView(ctx, s) {
    var cand = s.cfg ? candOf(ctx, s.cfg.id) : null;
    var p = s.cfg ? productOf(ctx, s.cfg.id) : null;
    if (!cand || !p) return null;
    var v = OO.modifiers.view(p, { sizeId: s.cfg.sizeId, sel: s.cfg.sel, qty: 1 });
    var allowed = cand.sizes.map(function (z) { return z.id; });
    var sizes = v.sizes && allowed.length > 1
      ? Object.assign({}, v.sizes, { options: v.sizes.options.filter(function (o) { return allowed.indexOf(o.id) >= 0; }) }) : null;
    var line = OO.cart.itemLine(p, { sizeId: s.cfg.sizeId, sel: s.cfg.sel, qty: 1 });
    var pct = rewardPct(ctx.coupon, ctx.kind, line, ctx.lines, ctx);
    return {
      product: p, candidate: cand, sizes: sizes, groups: v.groups, ok: v.ok, line: line, pct: pct, payable: payableOf(line, pct, ctx.store),
      cta: !v.ok ? v.ctaLabel : ctx.count > 1 ? 'Confirm free item ' + (s.staged.length + 1) + ' of ' + ctx.count : 'Add free item',
    };
  }

  // ---- view --------------------------------------------------------------------------------------------------------
  // One choice: a full-width button (a greyed one, `off`, is aria-disabled and never picks).
  function Row(props) {
    var ui = OO.ui;
    var r = props.row;
    var off = !!r.off;
    return (
      <li className="oo-cpick__item">
        <button type="button" className={ui.cx('oo-cpick__row', off && 'is-off')} aria-disabled={off ? 'true' : undefined}
          onClick={off ? undefined : props.onPick}>
          <span className="oo-cpick__thumb" aria-hidden="true">
            {r.image ? <img src={r.image} alt="" loading="lazy" /> : ui.renderIcon(props.icon || 'Gift', { size: 20 })}
          </span>
          <span className="oo-cpick__text">
            <span className="oo-cpick__name">{r.name}</span>
            {r.details ? <span className="oo-cpick__details">{r.details}</span> : null}
            {off ? <span className="oo-cpick__off">{r.label}</span> : (
              <span className="oo-cpick__save">{r.save}{r.was ? <s className="oo-cpick__was">{r.was}</s> : null}</span>
            )}
            {!off && r.extra ? <span className="oo-cpick__extra">{r.extra}</span> : null}
          </span>
        </button>
      </li>
    );
  }

  // The dialog: the coupon's code and a line of copy over the body.
  function Shell(props) {
    var ui = OO.ui;
    return (
      <ui.Sheet open variant="dialog" title={props.title} onClose={props.onClose} footer={props.footer || null}
        className={ui.cx('oo-cpick', props.className)}>
        <div className="oo-cpick__intro">
          {props.code ? <span className="oo-cpick__code">{props.code}</span> : null}
          {props.desc ? <p className="oo-cpick__desc">{props.desc}</p> : null}
        </div>
        {props.children}
      </ui.Sheet>
    );
  }

  var COPY = {
    source: {
      title: 'Which item should we copy?', list: 'Items you can copy', icon: 'Gift',
      desc: 'We copy its size and options. The copy’s base price is free; its extras stay payable.',
      net: 'We copy its size and options, and the copy is free.',
    },
    item: {
      title: 'Which item should be free?', list: 'Items that can be free', icon: 'Ticket',
      desc: 'One unit’s base price comes off. Its options stay payable.',
      net: 'One unit comes off, its options included.',
    },
  };

  // The source and item pickers: one list, a tap picks.
  function ListPicker(props) {
    var ui = OO.ui;
    var coupon = props.coupon;
    var kind = props.kind;
    var errS = React.useState(null);
    var doneRef = React.useRef(null); // the coupon object onPick was called for: the caller's next render allows a retry
    var opts = { store: props.store, menu: props.menu, mode: props.mode, currency: props.currency };
    var rows = kind === 'source' ? sourceRows(coupon, props.choices, props.lines, opts) : itemRows(coupon, props.choices, props.lines, opts);
    var copy = COPY[kind];
    var net = !!(props.store && props.store.applyDiscountToModifiers);
    function choose(row) {
      if (doneRef.current === coupon) return;
      var r = commit(coupon, kind, row.pick, props.lines, opts);
      if (!r.ok) { errS[1](r.reason || 'That item can’t be chosen right now'); return; }
      doneRef.current = coupon;
      props.onPick(r.picks);
    }
    return (
      <Shell title={copy.title} code={coupon.code || coupon.name} desc={net ? copy.net : copy.desc} onClose={props.onClose}
        className={'oo-cpick--' + kind}>
        {errS[0] ? <ui.InlineNotice tone="warn" role="alert">{errS[0]}</ui.InlineNotice> : null}
        <ul className="oo-cpick__list" aria-label={copy.list}>
          {rows.map(function (row) {
            return <Row key={row.key} row={row} icon={copy.icon} onPick={function () { choose(row); }} />;
          })}
        </ul>
      </Shell>
    );
  }

  function selectDraft(x) { return x.checkout; }

  // The BUY_N two-step and the giveaway (see the header).
  function RewardPicker(props) {
    var ui = OO.ui;
    var cx = ui.cx;
    var coupon = props.coupon;
    var kind = props.kind;
    var choices = props.choices;
    var store = props.store;
    var cur = props.currency;
    var draft = OO.useStore(selectDraft);
    var orderType = props.orderType || orderTypeOf(store, draft);
    // forceFresh (REF beginCouponGiveaway): a giveaway coupon without product rows re-reads the store's coupon list.
    var needFresh = kind === 'giveaway' && !arr(choices.products).length && store.id !== undefined && store.id !== null && store.id !== '';
    var fresh = OO.useAsync(function () {
      if (!needFresh) return null;
      return OO.api.getCoupons(store.id, { fresh: true }).then(function (raw) { return freshRows(raw, coupon); });
    }, [needFresh ? String(store.id) : '', coupon.id]);
    var loading = needFresh && fresh.loading;
    var opts = {
      store: store, menu: props.menu, mode: props.mode, at: props.at, orderType: orderType, currency: cur,
      rows: needFresh ? fresh.data || [] : undefined,
    };
    var cands = loading ? [] : candidates(coupon, kind, choices, props.lines, opts);
    var count = kind === 'rewards' ? Math.max(1, Math.floor(num(choices.count))) : 1;
    var ctx = { coupon: coupon, kind: kind, count: count, cands: cands, lines: props.lines, menu: props.menu, mode: props.mode, store: store };
    // The flow state, held with the coupon (pickSig) it was taken for; until the first action, or once the picks change,
    // the start state follows the props.
    var sig = pickSig(coupon);
    var st = React.useState(null);
    var s = st[0] && st[0].sig === sig ? st[0].s : start(ctx, seedOf(coupon, kind, props.lines, opts));
    var openS = React.useState({}); // optional single-choice groups opened in the configure step
    var doneRef = React.useRef(null); // the coupon object onPick was called for (see ListPicker)
    var headId = 'oo-cpick-h' + React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var step = s.step === 'done' ? 'pick' : s.step;

    // A new step starts at its top, with focus on its heading (as the combo builder does).
    var phase = [step, s.staged.length, s.cfg ? s.cfg.id : ''].join('|');
    var firstRun = React.useRef(true);
    React.useEffect(function () {
      if (firstRun.current) { firstRun.current = false; return; }
      var h = document.getElementById(headId);
      if (!h) return;
      var scroller = h.closest ? h.closest('.oo-sheet__scroll') : null;
      if (scroller) scroller.scrollTop = 0;
      h.setAttribute('tabindex', '-1');
      try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
    }, [phase]);

    function save(nextState) { st[1]({ sig: sig, s: nextState }); }
    function finish(done) {
      if (doneRef.current === coupon) return;
      var r = commit(coupon, kind, { rewards: done.staged }, props.lines, opts);
      if (!r.ok) { save(patch(done, { step: 'pick', staged: [], error: r.reason || 'Please choose again.' })); return; }
      doneRef.current = coupon;
      save(done);
      props.onPick(r.picks);
    }
    // run(fn, …args): fn(state, ctx, …args), then store it (or, every reward staged, commit).
    function run(fn) {
      var nextState = fn.apply(null, [s, ctx].concat([].slice.call(arguments, 1)));
      if (nextState.step === 'done') finish(nextState);
      else save(nextState);
    }

    var open = cands.filter(function (c) { return !c.off; });
    var nth = Math.min(count, s.staged.length + 1);
    var none = kind === 'rewards' ? 'No free items are available for this offer right now.' : 'The free item for this offer isn’t available right now.';
    var steps = kind === 'rewards' ? (
      <ol key="steps" className="oo-cpick__steps">
        <li className={cx('oo-cpick__step', step === 'qualify' && 'is-current')} aria-current={step === 'qualify' ? 'step' : undefined}>1 · Qualifying items</li>
        <li className={cx('oo-cpick__step', step !== 'qualify' && 'is-current')} aria-current={step !== 'qualify' ? 'step' : undefined}>
          {'2 · Pick free items' + (s.staged.length ? ' (' + s.staged.length + '/' + count + ')' : '')}
        </li>
      </ol>
    ) : null;
    var progress = count > 1 ? (
      <div className="oo-cpick__progress">
        <span className="oo-cpick__eyebrow">{'Free item ' + nth + ' of ' + count}</span>
        <span className="oo-cpick__segs" aria-hidden="true">
          {Array.apply(null, Array(count)).map(function (x, i) {
            return <span key={i} className={cx('oo-cpick__seg', i < s.staged.length && 'is-done')} />;
          })}
        </span>
      </div>
    ) : null;
    var error = s.error ? <ui.InlineNotice tone="warn" role="alert">{s.error}</ui.InlineNotice> : null;
    var body;
    var footer = null;
    var live;

    if (loading) {
      live = 'Loading the free items';
      body = (
        <div className="oo-cpick__loading" aria-busy="true">
          <span className="oo-sr-only" role="status">Loading the free items…</span>
          <ui.Skeleton variant="block" count={3} height={72} />
        </div>
      );
    } else if (step === 'qualify') {
      var q = qualifying(coupon, props.lines);
      live = 'Step 1 of 2: qualifying items';
      body = [
        steps,
        <h3 key="h" className="oo-cpick__h" id={headId}>Your qualifying items</h3>,
        <p key="n" className="oo-cpick__hint">{'This offer needs ' + q.min + ' qualifying item' + (q.min === 1 ? '' : 's') + '. You have ' + q.units + ' in your cart.'}</p>,
        <ul key="q" className="oo-cpick__qual" aria-label="Qualifying items">
          {q.lines.map(function (l) {
            var n = Math.max(1, Math.floor(num(l.qty)));
            return (
              <li key={l.key} className="oo-cpick__qual-row">
                <span className="oo-cpick__qual-name">{l.name + (n > 1 ? ' × ' + n : '')}</span>
                <span className="oo-cpick__qual-ok">{ui.renderIcon('Check', { size: 14, strokeWidth: 3 })}Qualifies</span>
              </li>
            );
          })}
        </ul>,
        !open.length ? <ui.InlineNotice key="none" tone="warn">{none}</ui.InlineNotice> : null,
      ];
      footer = (
        <ui.Button block disabled={!open.length} onClick={function () { run(next); }}>
          {count > 1 ? 'Choose ' + count + ' free items' : 'Choose your free item'}
        </ui.Button>
      );
    } else if (step === 'configure') {
      var cv = configView(ctx, s);
      var P = OO.pages.ItemSheet && OO.pages.ItemSheet.parts;
      if (!cv || !P) {
        body = <ui.InlineNotice tone="warn">{NOT_NOW}</ui.InlineNotice>;
      } else {
        var c = cv.candidate;
        live = 'Choose the options for ' + c.name;
        body = [
          steps,
          <ui.Button key="b" variant="link" className="oo-cpick__back" onClick={function () { run(back); }}>Back</ui.Button>,
          progress ? <React.Fragment key="p">{progress}</React.Fragment> : null,
          error ? <React.Fragment key="e">{error}</React.Fragment> : null,
          <div key="prod" className="oo-cpick__product">
            <span className="oo-cpick__thumb" aria-hidden="true">
              {c.image ? <img src={c.image} alt="" loading="lazy" /> : ui.renderIcon('Gift', { size: 20 })}
            </span>
            <div className="oo-cpick__text">
              <h3 className="oo-cpick__name oo-cpick__h" id={headId}>{c.name}</h3>
              <span className="oo-cpick__save">{c.save}{c.was ? <s className="oo-cpick__was">{c.was}</s> : null}</span>
            </div>
          </div>,
          cv.sizes ? (
            <ui.OptionGroup key="size" title={cv.sizes.name} hint={cv.sizes.hint} required done className="oo-item__group">
              {cv.sizes.options.map(function (o) {
                return <ui.OptionRow key={o.id} value={String(o.id)} label={o.name} checked={o.on} onToggle={function () { if (!o.on) run(size, o.id); }} />;
              })}
            </ui.OptionGroup>
          ) : null,
        ].concat(cv.groups.map(function (g) {
          var group = Object.assign({}, g, {
            options: g.options.map(function (o) { return Object.assign({}, o, { priceLabel: OO.format.plusMoney(o.price, cur) }); }),
          });
          var collapsible = !g.required && g.radio;
          return (
            <P.ModGroup key={'g' + g.id} group={group} collapsible={collapsible} open={!!openS[0][g.id]}
              onToggleOpen={function (nextOpen) { var o = Object.assign({}, openS[0]); o[g.id] = !!nextOpen; openS[1](o); }}
              onPick={function (id) { run(option, 'toggle', g.id, id); }}
              onInc={function (id) { run(option, 'increment', g.id, id); }}
              onDec={function (id) { run(option, 'decrement', g.id, id); }} />
          );
        }));
        footer = [
          <ui.TotalBlock key="t" label={cv.payable > 0 ? 'You pay' : 'Your price'} amount={cv.payable > 0 ? money(cv.payable, cur) : 'Free'} />,
          <ui.Button key="c" block disabled={!cv.ok} onClick={function () { run(confirm); }}>{cv.cta}</ui.Button>,
        ];
      }
    } else {
      live = count > 1 ? 'Pick free item ' + nth + ' of ' + count : 'Pick your free item';
      var backLabel = s.staged.length ? 'Change previous item' : kind === 'rewards' ? 'Back' : null;
      body = [
        steps,
        backLabel ? <ui.Button key="b" variant="link" className="oo-cpick__back" onClick={function () { run(back); }}>{backLabel}</ui.Button> : null,
        progress ? <React.Fragment key="p">{progress}</React.Fragment> : null,
        <h3 key="h" className="oo-cpick__h" id={headId}>{count > 1 ? 'Pick free item ' + nth : 'Pick your free item'}</h3>,
        s.staged.length ? (
          <div key="s" className="oo-cpick__staged">
            <span className="oo-cpick__eyebrow">Chosen</span>
            <ul className="oo-cpick__staged-list">
              {s.staged.map(function (r, i) {
                var mods = OO.cart.displayMods(r);
                return (
                  <li key={i} className="oo-cpick__staged-row">
                    {ui.renderIcon('Check', { size: 14, strokeWidth: 3 })}
                    <span className="oo-cpick__staged-name">{r.name}{mods ? <span className="oo-cpick__details">{' · ' + mods}</span> : null}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null,
        error ? <React.Fragment key="e">{error}</React.Fragment> : null,
        !open.length ? <ui.InlineNotice key="none" tone="warn">{none}</ui.InlineNotice> : null,
        <ul key="l" className="oo-cpick__list" aria-label="Free items you can choose">
          {cands.map(function (row) {
            return <Row key={row.key} row={row} icon="Gift" onPick={function () { run(pick, row.id); }} />;
          })}
        </ul>,
      ];
    }

    var title = kind === 'giveaway' ? 'Choose your free item' : coupon.name || 'Buy and get free';
    var desc = kind === 'giveaway' ? 'Pick one: it’s on the house.' : coupon.description || null;
    return (
      <Shell title={title} code={coupon.code || (kind === 'giveaway' ? coupon.name : null)} desc={desc} onClose={props.onClose}
        footer={footer} className={'oo-cpick--' + kind}>
        <div className="oo-sr-only" aria-live="polite">{live}</div>
        {body}
      </Shell>
    );
  }

  OO.pages.CouponPicker = function CouponPicker(props) {
    var coupon = props.coupon || null;
    if (!coupon) return null;
    var lines = arr(props.lines);
    var store = props.store || {};
    var mode = props.mode || OO.mode;
    var choices = choicesOf(coupon, lines, { choices: props.choices, menu: props.menu, mode: mode });
    var kind = choices && choices.kind;
    if (KINDS.indexOf(kind) < 0) return null;
    var common = {
      store: store, menu: props.menu || null, lines: lines, coupon: coupon, choices: choices, mode: mode, at: props.at,
      currency: props.currency || store.currency || '$', orderType: props.orderType || null,
      onPick: props.onPick || noop, onClose: props.onClose || noop,
    };
    if (kind === 'source' || kind === 'item') return <ListPicker key={coupon.id + '|' + kind} kind={kind} {...common} />;
    return <RewardPicker key={coupon.id + '|' + kind} kind={kind} {...common} />;
  };

  OO.pages.CouponPicker.logic = {
    choicesOf: choicesOf,
    merge: merge,
    commit: commit,
    sourceRows: sourceRows,
    itemRows: itemRows,
    orderTypeOf: orderTypeOf,
    judgeAt: judgeAt,
    freshRows: freshRows,
    productIds: productIds,
    candidates: candidates,
    qualifying: qualifying,
    seedOf: seedOf,
    configView: configView,
    flow: { start: start, next: next, pick: pick, size: size, option: option, confirm: confirm, back: back },
  };
})();


// ===================== src/pages/offers.jsx =====================
// src/pages/offers.jsx: the offers pages (Batch 2): OO.pages.Coupons, the coupons page (P2-31; /{hotel}/coupons), and
// OO.pages.Deals, the deals page (P2-32; /{hotel}/deals: the catalogue, the limit pre-check, recovery). Routed by P3-01
// in both modes (D-18); the router's table gate sends both to the table menu while a table applies (D-20). Styles:
// styles/offers.css (the pages section).
//
// Both pages judge the order the cart and the checkout will place (OO.pages.Cart.shared, read at render time; useOffers
// below): the hotel (OO.useHotel: the store and the menu at the cart's pricing), this hotel's cart once OO.store has
// switched to it, the order type and payment method the cart prices (pricingCtx), the order's moment (couponMoment: the
// draft's slot or now, and that slot's menu at the cart's pricing), the applied coupon at that moment (liveCoupon), the
// summary (summaryOf: wallet points in use included) and the coupon context over them (couponCtx, the STORED coupon). On
// it they run the D-05 re-check (useCouponSync: an applied coupon the cart no longer qualifies for is removed with
// recheck's message as a toast; a free-item coupon's resolved picks are stored) and the apply flow (useCouponApply:
// coupon_enabled, force_login_for_coupon, OO.membership.exclusivity, the email (a guest's contact sheet,
// Cart.shared.ContactSheet), validate_coupon_v4 at the order's instant, the local gates; a coupon that waits for the
// customer's choice opens its picker, Cart.shared.PickerHost on the sync's plan).
//
// <OO.pages.Coupons route mode /> (P2-31; REF OnlineOrderCouponsPage, plus this cart's eligibility and Apply):
//   acct.Page "Coupons" (back → the menu; the profile ring online), acct.gate's store states (loading, not found, not
//   live, unavailable), then "Save on your order" (the store's name under it) and:
//   - store.couponsEnabled false (coupon_enabled): "Coupons aren’t available at {store}" + Browse menu, no request.
//   - a guest at a force_login_for_coupon store: "Log in to use coupons at {store}." + Log in (profile, directLogin);
//     the coupons stay listed (copy works) and Apply stays off.
//   - "Have a promo code?": the typed code (Cart.shared.CodeForm: upper-cased; validate_coupon_v4 accepts a code the list
//     doesn't show).
//   - "Choose your free item" (Cart.shared.PickPrompt) while the applied coupon waits for the customer's choice.
//   - "Available coupons" with its count, over the line the eligibility is judged on: '2 items in your cart · $16.00 ·
//     Pickup' (the cart's own subtotal), or 'Your cart is empty.'.
//   - the store's exposed coupons (coupon_get_v4 serves is_exposed = 1 only; Cart.shared.useCouponList: OO.coupons.list
//     at the order's instant, type and table), as Cart.shared.offerRows ranks them over the cart's own subtotal: the
//     applied one first (even when no longer listed), then the ones this cart can use, the ones short of a minimum (by
//     the shortfall), the rest. One card each (cardOf below): its kind, what it gives (couponWords), REF's subtitle and
//     scope line, the minimum, the end date, the code (copy to the clipboard; REF "Tap code to copy") and the action:
//     Apply (eligible, or waiting for a choice) | "Add $X more" (disabled; reaching the minimum is all it takes) |
//     a disabled Apply under the refusal's reason | Remove (the applied one: "You save $X", or why it doesn't apply).
//   - loading (skeleton), a failed read ("We couldn't load coupons" + Try again), none ("No coupons right now").
//   - FloatingCartBar (the cart's count and OO.cart.subtotal → the cart page) while the cart has lines.
//   REF splits public / member-only coupons by is_private, which coupon_get_v4 never selects (no such column): not
//   ported. REF offers no Apply on this page ("Apply At Checkout"); the plan asks for it.
//
// OO.pages.Coupons.logic (pure; tests/coupons-page-p231.test.mjs):
//   scopeLabel(coupon) → REF's scope line: 'Applies to your whole order' (CART_DISCOUNT, FREE_DELIVERY) | 'Free item with
//       N qualifying item(s)' (BOGO, BUY_N) | 'Valid on N eligible item(s)' (ITEM_DISCOUNT) — N = the distinct product
//       ids of OO.coupons.entries — | 'Enter this code to unlock your saving' (no item scope).
//   subtitleOf(coupon, offer) → REF's subtitle: the name, else the description, whichever doesn't just restate the code
//       or the offer text (case-insensitive); '' when both do.
//   endYmd(coupon) → 'YYYY-MM-DD' | null: the raw row's end_date (REF: expiry › enddate › end_date; '0000-00-00' = none).
//   endsLabel(coupon, todayYmd) → 'Ends 30 Sep' ('Ends 30 Sep 2027' in another year) | null. The day is the store's
//       whole local day (the dates rule, model-commerce.jsx).
//   cardOf(row, v) → the card's view: { id, code, type, offer, subtitle, scope, minOrder, ends, applied, action
//       ('remove' | 'apply' | 'more'), label, disabled, why, tone ('ok' | 'warn' | 'error' | null) }. row = offerRows';
//       v = { currency, today (ymd), needLogin, error (useCouponApply's), saving (the applied coupon's: summary discount
//       + delivery discount), reason (the summary's refusal of the applied one), pending + pendingReason (the plan's) }.
//       The applied one: Remove, and why = the choice it waits for (warn) | 'You save $X' (ok) | the summary's refusal
//       (warn). Another: 'more' ("Add $X more", disabled; no reason line: the minimum pill says it) | a disabled Apply
//       when it can't apply or a guest must log in first | Apply; why = this coupon's refusal by the server or the
//       local gates (error), else its eligibility reason (warn: 'Only valid for delivery orders', or the choice an
//       applicable one will ask for).
// OO.pages.Coupons.parts = { OfferCard, CopyCode } (presentational; the 390 / 1440 render checks use them).
//   <OfferCard view busy onAction /> one coupon (cardOf's view). <CopyCode code /> the code button: the clipboard, then
//   "Copied" for 1.6 s; without a clipboard "Couldn’t copy automatically. Note the code and enter it at checkout.".
//
// <OO.pages.Deals route mode /> (P2-32; REF OnlineOrderDealsPage + dealCatalog + useCouponLimitAvailability +
// dealRecovery): acct.Page "Deals", acct.gate's store states, then "Deals and offers" and:
//   - the login gate (force_login_for_coupon, a guest; coupon deals stay off, specials and combos don't), "Choose your
//     free item" while the applied coupon waits for a choice, and "We couldn’t load this store’s coupons." + Try again
//     when coupon_get_v4 failed (the menu's deals still show);
//   - the catalogue (logic.catalog over Cart.shared.useCouponList's coupons and the menu at the cart's pricing), each
//     product judged with OO.model.displayState at the menu cards' moment (Cart.shared.useUpsellMoment: the draft's slot
//     or now, the order type's domain, the active named menu): hidden → left out; greyed (D-07, not REF's hide) →
//     listed with its label and never orderable. One section per group, in REF's order: Free items · Buy one, get one
//     free · Buy more, get more free · Discounted items · Menu specials · Combos · Order coupons; each with its count
//     and REF's hint; cards ranked by sortDeals. Product cards (DealCard): the deal's badge, name, price (Free · the
//     capped deal price · the item's), the struck base / banner price and a saving of 5 % or more, the description,
//     the grey label, REF's conditions (sizes the coupon takes, the free choices, the minimum, "1 of N items on this
//     offer", the end date; two at most) and the CTA (Get it free · Add & get one free · Start this deal · Add & save ·
//     Add to order). Order coupons: the coupons page's card (OfferCard, dealView): Apply to order | Remove (applied) |
//     Not available under the coupon's own refusal. "No deals right now" + Browse menu when nothing is listed;
//   - the limit pre-check while the customer's email is known (cc.email: the session's, else a valid draft email):
//     one validate_coupon_v4 per listed code at the order's instant (the apply flow's request), every code at once;
//     a code whose answer is a claim-limit message is left out of the catalogue. Answers are cached for the page load
//     (a limit 30 min, any other answer 15 min; REF's) and a code in flight is asked once; a failure (network, 5xx, an
//     unusable answer) counts as not reached, is not cached, and is not asked again on this page. The page shows its
//     skeleton until every code is answered (REF); answers are read from the cache on every render, so a warm cache
//     adds no wait (and renders on the server);
//   - Apply: an order coupon applies through useCouponApply (as the coupons page); a product deal opens the item sheet
//     (OO.pages.ItemSheet with the page's moment; a combo, OO.pages.ComboBuilder) and, once its line is in the cart
//     (onAdded), its coupon is applied on the next commit (REF add_then_apply), so the check sees that line; an 'item'
//     coupon (ITEM 100 %) then takes that product as its pick (REF selected_item_discount_product_id), any other choice
//     opens the picker. Specials and combos just add (the sheet's own toast);
//   - a failed deal (the apply's refusal of the deal just tried): a claim-limit message hides the deal (cached 30 min)
//     with "This deal has reached its claim limit and is no longer available."; a refusal more items can fix (the local
//     gates' MIN_ORDER, NO_ITEMS or MIN_ITEMS, with the very text the apply refused it with: logic.recoveryOf) opens
//     "Complete this deal" (RecoveryDialog); anything else is a toast (the server's text);
//   - "Complete this deal" (REF OnlineOrderDealRecoveryModal): the refusal, "Your item is in your cart …" after an add,
//     the basket minimum (its progress and bar), the eligible items (when more than one is needed, or one is still
//     short), the sizes the coupon takes, then up to six suggestions to add (a product deal: its coupon's other
//     orderable products, siblingsOf; an order coupon: the cart's suggestions, OO.upsell.cartSuggestions resolved at
//     the moment, addable only), each "Add" → the item sheet → the deal is tried again; Review cart · Browse full menu;
//   - FloatingCartBar, the guest's contact sheet, the picker and the toast, as the coupons page.
//   No REF "member deals" lock (no is_private); no deal-condition panel inside the item sheet (it takes no header).
//
// OO.pages.Deals.logic (pure but for the limit cache; tests/deals-p232.test.mjs):
//   GROUPS → [{ key, title, hint }] in REF's order.   badgeOf(coupon, currency) → REF dealBadge ('Buy 1 get 1 free', 'Buy 2
//       get 1 free', 'Free item', '20% off', '10% off your order', '$5.00 off your order', 'Free delivery', …).
//   basePriceOf(product, sizeIds) → { price, from }: the cheapest of the sizes the coupon names (their price replaces the
//       base), else the menu price (the cheapest size when it has none); from = those prices differ.
//   itemDeal(coupon, base) → { price, saving } | null: an ITEM_DISCOUNT on one unit, its % capped at MAX_DISCOUNT_VALUE
//       (a 1,000,000 cap is none); null for the other types and a FIXED one.
//   catalog({ coupons, menu, store, currency, orderType, paymentMethod, today, excluded ({ CODE: true }), applied,
//       judge(entity) → Display | null }) → { groups: [{ key, title, hint, entries }], deals, specials, combos, coupons,
//       counts: { deals, specials, combos, coupons, total } }. A coupon whose code is excluded is left out. A coupon
//       naming products (ITEM / BOGO / BUY_N with an item scope, OO.coupons.entries) becomes one card per product on
//       the menu (not on it: left out), only while its own gates pass (gate: OO.coupons.eligibility without lines, at
//       its minimum: coupons off, not online, not available at the order's moment, this order type, the payment it
//       needs); with none left it is not listed at all (REF). Any other coupon is an order coupon, greyed with its
//       gate's reason. Specials: menu products with a banner price above the price, or a banner text; combos: every
//       combo; in menu order. Deal = { key, kind: 'coupon_product' | 'special' | 'combo' | 'coupon', group, title,
//       description, image, badge, base, price, was, from, saving, free, orderable, label (the grey label or the
//       refusal), applied, … }: product deals add coupon, code, product, sizes (names), rewards (BUY_N's reward names),
//       freeCount, minItems, minOrder, ends, siblings; order coupons add coupon, code, type, offer, subtitle, scope,
//       minOrder, ends.
//   isLimitMessage(text) → bool: REF's claim-limit texts — the server's 'Coupon limit reached', 'Coupon usage limit
//       reached for this user', 'Coupon already claimed' — and REF's "already used / claimed / redeemed this coupon".
//   limitKey(storeId, email, table, code) → the cache key (email lower-cased, code upper-cased).
//   limits = { peek(key, now), set(key, reached, now), check({ hotel, storeId, email, table, code, time, domain }) →
//       Promise<true | false | null>, TTL_OK, TTL_REACHED, __setClock(fn), __reset() } (the cache; tests only for __).
//   recoverable(code) → MIN_ORDER | NO_ITEMS | MIN_ITEMS.   recoveryOf(deal, text, cc) → recheck's removal | null.
//   progressOf(deal, lines, base) → { subtotal, minOrder, remainingValue, units, minItems, remainingItems } (base = the
//       cart's own subtotal; units = the paid lines OO.coupons.lineMatches; an order coupon needs no items).
//   siblingsOf(deals, deal, progress) → ≤ 6 suggestions { key, name, image, price, from, sibling, kind, id }.
//   helperOf(suggestion, progress, currency) → 'Counts as one eligible item' | 'Adds $X toward the minimum' | 'Adds toward
//       the minimum' | 'Eligible for this deal'.   ctaOf(deal) · metaOf(deal, currency) · dealView(deal, v).
// OO.pages.Deals.parts = { DealCard, RecoveryDialog } (presentational): <DealCard deal currency busy disabled onAction />;
//   <RecoveryDialog recovery progress suggestions currency cart menu onPick onClose /> (cart / menu: OO.router.link's).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  // The kits are read at render / call time only (cart.jsx and not-found.jsx: tests load this file without them).
  function Kit() { return OO.pages.Cart.shared; }
  function Acct() { return OO.pages.acct; }
  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function numOr0(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function money(n, cur) { return OO.format.money(n, cur); }
  function plural(n, one) { return OO.format.pluralize(n, one); }

  // ---- logic (pure; see the header) ----------------------------------------------------------------------------------
  var ITEM_TYPES = ['ITEM_DISCOUNT', 'BUY_ONE_GET_ONE_FREE', 'BUY_N_GET_M_FREE'];
  function distinctIds(entries) {
    var seen = {};
    var out = [];
    (entries || []).forEach(function (e) {
      var id = e && e.id !== undefined ? String(e.id) : '';
      if (id && !seen[id]) { seen[id] = true; out.push(id); }
    });
    return out;
  }
  function scopeLabel(coupon) {
    var type = coupon ? coupon.type : null;
    if (type === 'CART_DISCOUNT' || type === 'FREE_DELIVERY') return 'Applies to your whole order';
    var n = ITEM_TYPES.indexOf(type) >= 0 ? distinctIds(OO.coupons.entries(coupon)).length : 0;
    if (n > 0 && type !== 'ITEM_DISCOUNT') return 'Free item with ' + n + ' qualifying ' + (n === 1 ? 'item' : 'items');
    if (n > 0) return 'Valid on ' + n + ' eligible ' + (n === 1 ? 'item' : 'items');
    return 'Enter this code to unlock your saving';
  }
  function restates(text, coupon, offer) {
    var t = str(text).trim().toLowerCase();
    return !t || t === str(coupon.code).trim().toLowerCase() || t === str(offer).trim().toLowerCase();
  }
  function subtitleOf(coupon, offer) {
    if (!coupon) return '';
    if (!restates(coupon.name, coupon, offer)) return str(coupon.name).trim();
    if (!restates(coupon.description, coupon, offer)) return str(coupon.description).trim();
    return '';
  }
  function endYmd(coupon) {
    var raw = coupon && coupon.raw && typeof coupon.raw === 'object' ? coupon.raw : {};
    var v = raw.expiry !== undefined && raw.expiry !== null && raw.expiry !== '' ? raw.expiry
      : raw.enddate !== undefined && raw.enddate !== null && raw.enddate !== '' ? raw.enddate : raw.end_date;
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str(v).trim());
    if (!m || m[1] === '0000') return null;
    return m[1] + '-' + ('0' + m[2]).slice(-2) + '-' + ('0' + m[3]).slice(-2);
  }
  function endsLabel(coupon, todayYmd) {
    var ymd = endYmd(coupon);
    if (!ymd) return null;
    var label = OO.format.shortDate(ymd);
    return 'Ends ' + label + (todayYmd && str(todayYmd).slice(0, 4) !== ymd.slice(0, 4) ? ' ' + ymd.slice(0, 4) : '');
  }
  function cardOf(row, v) {
    v = v || {};
    var c = row.coupon;
    var words = Kit().couponWords(c, v.currency);
    var err = v.error && v.error.id === c.id ? v.error : null;
    var view = {
      id: c.id, code: str(c.code).trim(), type: words.type, offer: words.offer, subtitle: subtitleOf(c, words.offer),
      scope: scopeLabel(c), minOrder: numOr0(c.minOrder), ends: endsLabel(c, v.today), applied: !!row.on,
      action: 'apply', label: 'Apply', disabled: false, why: null, tone: null,
    };
    if (row.on) {
      view.action = 'remove';
      view.label = 'Remove';
      if (v.pending) { view.why = v.pendingReason || 'Choose your free item'; view.tone = 'warn'; }
      else if (numOr0(v.saving) > 0) { view.why = 'You save ' + money(v.saving, v.currency); view.tone = 'ok'; }
      else if (v.reason) { view.why = v.reason; view.tone = 'warn'; }
      return view;
    }
    if (row.addMore > 0) {
      // The minimum pill and "Add $X more" say it all: no reason line.
      view.action = 'more';
      view.label = 'Add ' + money(row.addMore, v.currency) + ' more';
      view.disabled = true;
    } else if (!row.canApply || v.needLogin) {
      view.disabled = true;
    }
    view.why = err ? err.text : (view.action !== 'more' && !row.e.ok && row.e.reason ? row.e.reason : null);
    view.tone = err ? 'error' : view.why ? 'warn' : null;
    return view;
  }

  // ---- the order the offers flows judge (see the header) ------------------------------------------------------------
  function selectOrder(st) {
    return { hotel: st.hotel, lines: st.cart.lines, coupon: st.cart.coupon, draft: st.checkout, session: st.session, table: st.table, points: st.points };
  }
  function useToast() {
    var s = React.useState(null); // { id, text }
    return {
      say: function (text) { if (text) s[1]({ id: Date.now() + Math.random(), text: text }); },
      node: s[0] ? <OO.ui.Toast key={s[0].id} open onClose={function () { s[1](null); }}>{s[0].text}</OO.ui.Toast> : null,
    };
  }
  // useOffers(hotel, say, handlers?) → { h, store, menu, ready, lines, coupon (stored), applied (its live copy), draft,
  //   session, table, pc, sum, cc, plan, api, picker: { open, set } }. handlers = { onApplied(coupon), onPending(coupon)
  //   → true when it handled the choice itself (else the picker opens) }, read at call time.
  function useOffers(hotel, say, handlers) {
    var K = Kit();
    var h = OO.useHotel(hotel);
    var s = OO.useStore(selectOrder);
    var store = h.store || null;
    var ready = !!store && s.hotel === String(hotel);
    var lines = ready ? s.lines : [];
    var table = ready ? K.tableOf(s.table, hotel) : null;
    var pc = ready ? K.pricingCtx(store, s.draft) : null;
    var moment = ready ? K.couponMoment(hotel, store, s.draft, pc.orderType, h.menu || null) : null;
    var cp = ready ? K.liveCoupon(s.coupon, store, pc.orderType, moment.at) : null;
    var sum = ready ? K.summaryOf(lines, store, cp, pc, table) : null;
    var cc = ready ? K.couponCtx({
      hotel: hotel, store: store, menu: moment.menu, lines: lines, coupon: s.coupon, orderType: pc.orderType,
      paymentMethod: pc.paymentMethod, at: moment.at, table: table, session: s.session, draft: s.draft, sum: sum,
    }) : null;
    var plan = K.useCouponSync(cc, say);
    var pickS = React.useState(false); // the coupon's picker open
    React.useEffect(function () { if (!plan.pending && pickS[0]) pickS[1](false); }, [plan.pending]);
    var hs = React.useRef(null);
    hs.current = handlers || {};
    var api = K.useCouponApply(cc, say, {
      onApplied: function (c) { if (typeof hs.current.onApplied === 'function') hs.current.onApplied(c); },
      onPending: function (c) {
        if (typeof hs.current.onPending === 'function' && hs.current.onPending(c)) return;
        pickS[1](true);
      },
    });
    return {
      h: h, store: store, menu: h.menu || null, ready: ready, lines: lines, coupon: ready ? s.coupon : null,
      applied: ready && s.coupon ? cp || s.coupon : null, draft: s.draft, session: s.session, table: table, pc: pc, sum: sum,
      cc: cc, plan: plan, api: api, picker: { open: pickS[0], set: pickS[1] },
    };
  }
  // The cart's own subtotal (no coupon: free lines never count towards a minimum), as the coupon engine reads it.
  function baseOf(o) {
    return OO.pricing.summary(o.lines, o.store, { orderType: o.pc.orderType, paymentMethod: o.pc.paymentMethod, table: o.table || undefined }).subtotal;
  }
  function needLoginOf(o) { return !!(o.store.settings && o.store.settings.forceLoginForCoupon) && !o.cc.signedIn; }
  function todayOf(o) { return OO.model.storeNow(o.store, OO.data.now()).ymd; }
  // cardOf's v: the page's currency and day, the login gate, the apply flow's error and the applied coupon's state.
  function cardCtx(o, needLogin) {
    var sc = o.sum.coupon || {};
    return {
      currency: o.store.currency, today: todayOf(o), needLogin: needLogin, error: o.api.error,
      saving: sc.ok ? numOr0(o.sum.discount) + numOr0(o.sum.deliveryDiscount) : 0,
      reason: !sc.ok ? sc.reason || null : null,
      pending: o.plan.pending, pendingReason: o.plan.reason,
    };
  }
  // force_login_for_coupon, a guest: why nothing applies here, and the way to sign in (the profile's email login).
  function loginGate(hotel, store) {
    var ui = OO.ui;
    var login = OO.router.link('profile', null, { hotel: hotel, query: { directLogin: 'true' } });
    return (
      <div className="oo-offers__login">
        <ui.InlineNotice tone="info" icon="User">{'Log in to use coupons at ' + store.name + '.'}</ui.InlineNotice>
        <ui.Button size="sm" href={login.href} onClick={login.onClick}>Log in</ui.Button>
      </div>
    );
  }

  // The overlays every offers page hosts (plain functions, inlined in the page's tree): the guest's contact sheet, the
  // coupon's picker (on the sync's plan) and the toast; the cart bar while the cart has lines.
  function overlays(o, toast) {
    var K = Kit();
    var api = o.api;
    return (
      <React.Fragment>
        {api.contact ? (
          <K.ContactSheet cc={o.cc} code={api.contact.target.code || (api.contact.target.coupon && api.contact.target.coupon.code)}
            busy={!!api.busy} onSubmit={api.submitContact} onClose={api.closeContact} />
        ) : null}
        {o.cc ? <K.PickerHost cc={o.cc} plan={o.plan} open={o.picker.open} onClose={function () { o.picker.set(false); }} say={toast.say} /> : null}
        {toast.node}
      </React.Fragment>
    );
  }
  function cartBar(o, hotel) {
    if (!o.lines.length) return null;
    var link = OO.router.link('cart', null, { hotel: hotel });
    return <OO.ui.FloatingCartBar count={OO.cart.count(o.lines)} total={money(OO.cart.subtotal(o.lines), o.store.currency)} href={link.href} onClick={link.onClick} />;
  }
  function cartLine(o) {
    if (!o.lines.length) return 'Your cart is empty.';
    var type = Kit().TYPE_LABEL[o.pc.orderType] || '';
    return [plural(OO.cart.count(o.lines), 'item') + ' in your cart', money(baseOf(o), o.store.currency), type].filter(Boolean).join(' · ');
  }

  // ---- parts ---------------------------------------------------------------------------------------------------------
  // The copy glyph (not in the UI kit; drawn in its 24px / 2px-stroke language).
  function CopyIcon(props) {
    var size = props.size || 18;
    return (
      <svg className="oo-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
      </svg>
    );
  }
  var COPY_FAILED = 'Couldn’t copy automatically. Note the code and enter it at checkout.';
  function CopyCode(props) {
    var st = React.useState(null); // null | 'copied' | 'failed'
    var timer = React.useRef(null);
    var alive = React.useRef(true);
    React.useEffect(function () {
      alive.current = true;
      return function () { alive.current = false; if (timer.current) clearTimeout(timer.current); };
    }, []);
    var code = str(props.code);
    function copy() {
      var nav = window.navigator;
      var clip = nav && nav.clipboard;
      var p = null;
      try { p = clip && typeof clip.writeText === 'function' ? clip.writeText(code) : null; } catch (e) { p = null; }
      if (!p) { st[1]('failed'); return; }
      Promise.resolve(p).then(function () {
        if (!alive.current) return;
        st[1]('copied');
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(function () { if (alive.current) st[1](null); }, 1600);
      }, function () { if (alive.current) st[1]('failed'); });
    }
    var copied = st[0] === 'copied';
    return (
      <div className="oo-offer__codebox">
        <button type="button" className={OO.ui.cx('oo-offer__code', copied && 'is-copied')} aria-label={'Copy promo code ' + code} onClick={copy}>
          <span className="oo-offer__code-text">
            <span className="oo-offer__code-label">{copied ? 'Copied' : 'Promo code'}</span>
            <span className="oo-offer__code-value">{code}</span>
          </span>
          {copied ? <OO.icons.Check size={18} /> : <CopyIcon size={18} />}
        </button>
        <span className="oo-sr-only" role="status" aria-live="polite">{copied ? 'Code ' + code + ' copied' : ''}</span>
        {st[0] === 'failed' ? <p className="oo-offer__code-err" role="alert">{COPY_FAILED}</p> : null}
      </div>
    );
  }

  // One coupon (cardOf's view). busy: the server is checking this one; onAction(view.action).
  function OfferCard(props) {
    var ui = OO.ui;
    var v = props.view;
    var cur = props.currency;
    var label = v.code || v.offer;
    return (
      <li className={ui.cx('oo-offer', v.applied && 'is-applied', !v.applied && v.disabled && 'is-off')}>
        <div className="oo-offer__body">
          <div className="oo-offer__top">
            <span className="oo-offer__icon" aria-hidden="true"><OO.icons.Ticket size={18} /></span>
            <span className="oo-offer__type">{v.type}</span>
            {v.applied ? <ui.Badge tone="done" size="sm">Applied</ui.Badge> : null}
            {v.ends ? <span className="oo-offer__ends">{v.ends}</span> : null}
          </div>
          <h3 className="oo-offer__title">{v.offer}</h3>
          {v.subtitle ? <p className="oo-offer__sub">{v.subtitle}</p> : null}
          <div className="oo-offer__meta">
            {v.minOrder > 0 ? <span className="oo-offer__pill">{'Min order ' + money(v.minOrder, cur)}</span> : null}
            <span className="oo-offer__scope"><OO.icons.Bag size={14} />{v.scope}</span>
          </div>
          {v.why ? (
            <p className={ui.cx('oo-offer__why', v.tone && 'is-' + v.tone)} role={v.tone === 'error' ? 'alert' : undefined}>{v.why}</p>
          ) : null}
        </div>
        <div className="oo-offer__foot">
          {v.code ? <CopyCode code={v.code} /> : <span className="oo-offer__nocode">No code needed</span>}
          {v.action === 'remove' ? (
            <ui.Button size="sm" variant="outline" aria-label={'Remove coupon ' + label} onClick={function () { props.onAction('remove'); }}>Remove</ui.Button>
          ) : (
            <ui.Button size="sm" aria-label={(v.action === 'more' ? v.label + ' for ' : 'Apply coupon ') + label}
              disabled={v.disabled || (!!props.busy && props.busy !== v.id)} busy={props.busy === v.id}
              onClick={function () { props.onAction(v.action); }}>{v.label}</ui.Button>
          )}
        </div>
      </li>
    );
  }

  function listSkeleton(label, count, height) {
    return (
      <div className="oo-offers__loading" aria-busy="true">
        <span className="oo-sr-only" role="status">{label || 'Loading'}</span>
        <OO.ui.Skeleton variant="block" count={count || 3} height={height || 168} />
      </div>
    );
  }

  // ---- the coupons page (P2-31; see the header) ----------------------------------------------------------------------
  OO.pages.Coupons = function Coupons(props) {
    var route = props.route;
    var hotel = route.hotel;
    var ui = OO.ui;
    var A = Acct();
    var K = Kit();
    var toast = useToast();
    var o = useOffers(hotel, toast.say, null);
    var enabled = o.ready && OO.coupons.enabled(o.store);
    var q = K.useCouponList(o.store, o.cc, enabled);
    var header = A.headerProps({ hotel: hotel, mode: props.mode, back: OO.router.href('menu', null, { hotel: hotel }), profile: true });
    var skeleton = (
      <div className="oo-acct__skel">
        <ui.Skeleton lines={2} className="oo-acct__skel-title" />
        {listSkeleton('Loading coupons')}
      </div>
    );
    var blocked = A.gate(o.h, { title: 'Coupons', header: header, skeleton: skeleton });
    if (blocked) return blocked;
    if (!o.ready) return <A.Page title="Coupons" {...header} busy>{skeleton}</A.Page>;

    var store = o.store;
    var cur = store.currency;
    var api = o.api;
    var menuLink = OO.router.link('menu', null, { hotel: hotel });
    var title = <ui.SectionTitle level="page" sub={store.name}>Save on your order</ui.SectionTitle>;
    if (!enabled) {
      return (
        <React.Fragment>
          <A.Page title="Coupons" {...header} className="oo-offers">
            {title}
            <ui.EmptyState icon="Ticket" title={'Coupons aren’t available at ' + store.name} body="You can still order from the menu."
              action={<ui.Button size="md" href={menuLink.href} onClick={menuLink.onClick}>Browse menu</ui.Button>} />
            <ui.PoweredBySipo />
          </A.Page>
          {cartBar(o, hotel)}
          {overlays(o, toast)}
        </React.Fragment>
      );
    }

    var needLogin = needLoginOf(o);
    var rows = q.loading || q.error ? [] : K.offerRows(q.list, o.cc, o.applied, baseOf(o));
    var v = cardCtx(o, needLogin);
    function act(row, action) {
      if (action === 'remove') { api.remove(); return; }
      if (action === 'apply') api.apply(row.coupon);
    }
    var body;
    if (q.loading) body = listSkeleton('Loading coupons');
    else if (q.error) {
      body = (
        <ui.EmptyState icon="Info" role="alert" title="We couldn't load coupons" body="Check your connection and try again."
          action={<ui.Button size="md" onClick={q.reload}>Try again</ui.Button>} />
      );
    } else if (!rows.length) {
      body = <ui.EmptyState icon="Ticket" title="No coupons right now" body="Offers appear here when this store publishes them. Have a promo code? Enter it above." />;
    } else {
      body = (
        <ul className="oo-offers__grid" aria-label="Available coupons">
          {rows.map(function (row) {
            return <OfferCard key={row.coupon.id} view={cardOf(row, v)} currency={cur} busy={api.busy} onAction={function (a) { act(row, a); }} />;
          })}
        </ul>
      );
    }
    return (
      <React.Fragment>
        <A.Page title="Coupons" {...header} className="oo-offers">
          {title}
          {needLogin ? loginGate(hotel, store) : null}
          <ui.Card pad="sm" className="oo-offers__promo">
            <h2 className="oo-offers__promo-title">Have a promo code?</h2>
            <K.CodeForm api={api} hotel={hotel} />
          </ui.Card>
          <K.PickPrompt plan={o.plan} onChoose={function () { o.picker.set(true); }} onRemove={api.remove} />
          <ui.SectionTitle level="section" className="oo-offers__head" sub={cartLine(o)}
            action={rows.length ? <span className="oo-offers__count" aria-label={plural(rows.length, 'coupon')}>{rows.length}</span> : null}>
            Available coupons
          </ui.SectionTitle>
          {body}
          <ui.PoweredBySipo />
        </A.Page>
        {cartBar(o, hotel)}
        {overlays(o, toast)}
      </React.Fragment>
    );
  };
  OO.pages.Coupons.logic = { scopeLabel: scopeLabel, subtitleOf: subtitleOf, endYmd: endYmd, endsLabel: endsLabel, cardOf: cardOf };
  OO.pages.Coupons.parts = { OfferCard: OfferCard, CopyCode: CopyCode };

  // ---- the deals page: the catalogue (pure; see the header) ----------------------------------------------------------
  var GROUPS = [
    { key: 'free_item', title: 'Free items', hint: 'Claim one free. Extras are still charged.' },
    { key: 'bogo', title: 'Buy one, get one free', hint: 'Add one of these and the second one is free.' },
    { key: 'bngm', title: 'Buy more, get more free', hint: 'Add the qualifying items, then choose your free ones.' },
    { key: 'item_discount', title: 'Discounted items', hint: 'Money off these items, taken off at checkout.' },
    { key: 'specials', title: 'Menu specials', hint: 'Prices already reduced by the store.' },
    { key: 'combos', title: 'Combos', hint: 'Set meals priced as a bundle.' },
    { key: 'coupons', title: 'Order coupons', hint: 'Taken off your whole order at checkout.' },
  ];
  var NO_CAP = 100000; // couponWords' rule: a cap the admin left blank (1,000,000) is no cap
  var SHOWN = { show: 'normal', label: null };
  function arr(v) { return Array.isArray(v) ? v : []; }
  function round2(n) { return OO.format.round2(n); }
  function codeKey(code) { return str(code).trim().toUpperCase(); }
  function capOf(c) { var m = numOr0(c && c.maxDiscount); return m > 0 && m < NO_CAP ? m : 0; }
  function pctText(p) { return String(Math.round(p * 100) / 100); }
  function badgeOf(c, cur) {
    var pct = numOr0(c.percent);
    switch (c.type) {
      case 'BUY_ONE_GET_ONE_FREE': return 'Buy 1 get 1 free';
      case 'BUY_N_GET_M_FREE': return 'Buy ' + Math.max(1, numOr0(c.minItemCount)) + ' get ' + Math.max(1, numOr0(c.maxFreeCount)) + ' free';
      case 'ITEM_DISCOUNT': return pct >= 100 ? 'Free item' : pct > 0 ? pctText(pct) + '% off' : 'Item deal';
      case 'FREE_DELIVERY': return 'Free delivery';
      case 'CART_DISCOUNT':
        return OO.coupons.isAmount(c) ? money(pct, cur) + ' off your order' : pct > 0 ? pctText(pct) + '% off your order' : 'Order discount';
      default: return 'Special offer';
    }
  }
  function judgeOf(input, e) {
    var d = typeof input.judge === 'function' ? input.judge(e) : null;
    return d && d.show ? d : SHOWN;
  }
  // The coupon's products, one per product id in scope order, with the sizes it names ([] = any size).
  function scopeProducts(c) {
    var out = [];
    var byId = {};
    OO.coupons.entries(c).forEach(function (e) {
      var id = String(e.id);
      var sizes = arr(e.sizes).map(Number);
      if (!byId[id]) { byId[id] = { id: Number(e.id), sizes: sizes.slice(), any: !sizes.length }; out.push(byId[id]); return; }
      if (!sizes.length) byId[id].any = true;
      sizes.forEach(function (s) { if (byId[id].sizes.indexOf(s) < 0) byId[id].sizes.push(s); });
    });
    out.forEach(function (x) { if (x.any) x.sizes = []; });
    return out;
  }
  // The price a product deal starts from: the cheapest of the sizes the coupon names (a size's price replaces the base),
  // else the menu's price (the cheapest size when the product has no price of its own). from: those prices differ.
  function basePriceOf(p, sizeIds) {
    var sizes = arr(p.sizes);
    var named = sizeIds && sizeIds.length ? sizes.filter(function (z) { return sizeIds.indexOf(Number(z.id)) >= 0; }) : [];
    var pool = named.length ? named : numOr0(p.price) > 0 ? [] : sizes;
    var prices = pool.map(function (z) { return numOr0(z.price); }).filter(function (n) { return n > 0; });
    if (!prices.length) return { price: numOr0(p.price), from: false };
    var lo = Math.min.apply(null, prices);
    return { price: lo, from: prices.some(function (n) { return n !== lo; }) };
  }
  // An ITEM_DISCOUNT on one unit at `base`: the capped % (REF prices base × (1 − %); a cap below it is what the coupon
  // engine takes, OO.coupons itemPercents / freeLines). null for the other types (their card shows the item's price).
  function itemDeal(c, base) {
    if (c.type !== 'ITEM_DISCOUNT' || c.amount === true || !(base > 0)) return null;
    var pct = Math.min(100, numOr0(c.percent));
    if (pct <= 0) return null;
    var off = base * pct / 100;
    var cap = capOf(c);
    if (cap > 0) off = Math.min(off, cap);
    off = round2(off);
    return { price: round2(Math.max(0, base - off)), saving: off };
  }
  function rewardNames(c, menu) {
    var seen = {};
    return arr(c.resultScope).map(function (e) { return menu.products[e.id]; }).filter(function (p) {
      if (!p || seen[p.id]) return false;
      seen[p.id] = true;
      return true;
    }).map(function (p) { return p.name; });
  }
  // The coupon's own gates, whatever the cart holds (OO.coupons.eligibility without lines, at its minimum): coupons off,
  // not online, not available at the order's moment, this order type, the payment it needs, no item scope.
  function gateOf(c, input) {
    return OO.coupons.eligibility(c, {
      subtotal: Math.max(0, numOr0(c.minOrder)), orderType: input.orderType || 'PICKUP', currency: input.currency,
      paymentMethod: input.paymentMethod || null, store: input.store || null,
    });
  }
  function isApplied(c, input) { return !!input.applied && input.applied.id === c.id; }
  function productDeals(c, input) {
    var menu = input.menu;
    var cur = input.currency;
    var flow = OO.coupons.freeFlow(c);
    var group = flow === 'item' ? 'free_item' : c.type === 'BUY_ONE_GET_ONE_FREE' ? 'bogo' : c.type === 'BUY_N_GET_M_FREE' ? 'bngm' : 'item_discount';
    var buyN = c.type === 'BUY_N_GET_M_FREE';
    var out = [];
    scopeProducts(c).forEach(function (s) {
      var p = menu && menu.products ? menu.products[s.id] : null;
      if (!p) return; // not on this menu: it can't be ordered here (the upsell's rule)
      var d = judgeOf(input, p);
      if (d.show === 'hide') return;
      var base = basePriceOf(p, s.sizes);
      var deal = itemDeal(c, base.price);
      out.push({
        key: 'deal-' + c.id + '-' + p.id, kind: 'coupon_product', group: group, coupon: c, code: str(c.code).trim(), product: p,
        title: p.name, description: p.description || '', image: p.image || null,
        badge: deal && flow === 'item' && deal.price > 0 ? money(deal.saving, cur) + ' off' : badgeOf(c, cur),
        base: base.price, price: deal ? deal.price : base.price, was: deal && deal.saving > 0 ? base.price : null, from: base.from,
        saving: deal ? deal.saving : null, free: !!deal && deal.price === 0,
        sizes: s.sizes.length ? arr(p.sizes).filter(function (z) { return s.sizes.indexOf(Number(z.id)) >= 0; }).map(function (z) { return z.name; }) : [],
        rewards: buyN ? rewardNames(c, menu) : [], freeCount: buyN ? Math.max(1, numOr0(c.maxFreeCount)) : 0,
        minItems: buyN ? Math.max(1, numOr0(c.minItemCount)) : 1, minOrder: numOr0(c.minOrder), ends: endsLabel(c, input.today),
        siblings: 0, orderable: d.show === 'normal', label: d.show === 'grey' ? d.label || 'Not available' : null, applied: isApplied(c, input),
      });
    });
    out.forEach(function (x) { x.siblings = out.length; });
    return out;
  }
  function couponDeal(c, gate, input) {
    var cur = input.currency;
    var words = Kit().couponWords(c, cur);
    return {
      key: 'coupon-' + c.id, kind: 'coupon', group: 'coupons', coupon: c, code: str(c.code).trim(),
      title: str(c.name).trim() || badgeOf(c, cur), badge: badgeOf(c, cur), type: words.type, offer: words.offer,
      subtitle: subtitleOf(c, words.offer), scope: scopeLabel(c), minOrder: numOr0(c.minOrder), ends: endsLabel(c, input.today),
      base: 0, price: 0, saving: null, free: false, orderable: !!gate.ok, label: gate.ok ? null : gate.reason || 'Not available',
      applied: isApplied(c, input),
    };
  }
  // The menu's specials (a banner price above the price, or a banner) and combos, in menu order, judged (D-07).
  function menuDeals(input) {
    var menu = input.menu;
    var specials = [];
    var combos = [];
    var seen = {};
    function special(p) {
      if (!p || seen['p' + p.id]) return;
      var was = p.wasPrice !== null && p.wasPrice !== undefined && numOr0(p.wasPrice) > numOr0(p.price) ? numOr0(p.wasPrice) : null;
      if (was === null && !p.ribbon) return;
      seen['p' + p.id] = true;
      var d = judgeOf(input, p);
      if (d.show === 'hide') return;
      specials.push({
        key: 'special-' + p.id, kind: 'special', group: 'specials', product: p, title: p.name, description: p.description || '',
        image: p.image || null, badge: p.ribbon || 'Special price', base: numOr0(p.price), price: numOr0(p.price), was: was,
        saving: was !== null ? round2(was - numOr0(p.price)) : null, from: false, free: false,
        orderable: d.show === 'normal', label: d.show === 'grey' ? d.label || 'Not available' : null,
      });
    }
    function combo(c) {
      if (!c || seen['c' + c.id]) return;
      seen['c' + c.id] = true;
      var d = judgeOf(input, c);
      if (d.show === 'hide') return;
      combos.push({
        key: 'combo-' + c.id, kind: 'combo', group: 'combos', product: c, title: c.name, description: c.description || '',
        image: c.image || null, badge: c.ribbon || 'Combo deal', base: numOr0(c.price), price: numOr0(c.price), was: null,
        saving: null, from: false, free: false, orderable: d.show === 'normal', label: d.show === 'grey' ? d.label || 'Not available' : null,
      });
    }
    arr(menu && menu.categories).forEach(function (cat) {
      arr(cat.comboIds).forEach(function (id) { combo(menu.combos ? menu.combos[id] : null); });
      var ids = arr(cat.productIds).slice();
      arr(cat.subcategories).forEach(function (sub) { ids = ids.concat(arr(sub.productIds)); });
      ids.forEach(function (id) { special(menu.products ? menu.products[id] : null); });
    });
    return { specials: specials, combos: combos };
  }
  // REF sortDeals: orderable first, then the most value (a free item; the saving, else the price; a special's saving),
  // then by title; stable.
  function valueOf(x) {
    if (x.kind === 'coupon_product') return x.free ? Number.MAX_SAFE_INTEGER : x.saving !== null ? x.saving : x.base;
    if (x.kind === 'special' || x.kind === 'combo') return x.saving || 0;
    return 0;
  }
  function sortDeals(list) {
    return arr(list).map(function (x, i) { return { x: x, i: i }; }).sort(function (a, b) {
      return (a.x.orderable === b.x.orderable ? 0 : a.x.orderable ? -1 : 1) || valueOf(b.x) - valueOf(a.x)
        || str(a.x.title).localeCompare(str(b.x.title)) || a.i - b.i;
    }).map(function (w) { return w.x; });
  }
  function catalog(input) {
    input = input || {};
    var deals = [];
    var coupons = [];
    arr(input.coupons).forEach(function (c) {
      if (!c) return;
      var code = codeKey(c.code);
      if (code && input.excluded && input.excluded[code]) return; // its claim limit is reached (the pre-check)
      var gate = gateOf(c, input);
      if (ITEM_TYPES.indexOf(c.type) >= 0 && scopeProducts(c).length) {
        if (gate.ok) deals = deals.concat(productDeals(c, input));
        return; // a coupon that names products shows them, or nothing (REF)
      }
      coupons.push(couponDeal(c, gate, input));
    });
    var m = menuDeals(input);
    deals = sortDeals(deals);
    var by = { free_item: [], bogo: [], bngm: [], item_discount: [], specials: sortDeals(m.specials), combos: sortDeals(m.combos), coupons: sortDeals(coupons) };
    deals.forEach(function (x) { by[x.group].push(x); });
    var groups = GROUPS.filter(function (g) { return by[g.key].length > 0; }).map(function (g) {
      return { key: g.key, title: g.title, hint: g.hint, entries: by[g.key] };
    });
    return {
      groups: groups, deals: deals, specials: by.specials, combos: by.combos, coupons: by.coupons,
      counts: {
        deals: deals.length, specials: by.specials.length, combos: by.combos.length, coupons: by.coupons.length,
        total: deals.length + by.specials.length + by.combos.length + by.coupons.length,
      },
    };
  }

  // ---- the deals page: the limit pre-check (REF useCouponLimitAvailability; see the header) --------------------------
  var LIMIT_OK_MS = 15 * 60000;
  var LIMIT_HIT_MS = 30 * 60000;
  var LIMIT_MAX = 300;
  var limitCache = {}; // key → { reached, until }
  var limitSize = 0;
  var limitFlight = {}; // key → Promise<reached | null>
  var limitClock = function () { return Date.now(); };
  function isLimitMessage(text) {
    var s = str(text).trim().toLowerCase().replace(/[.!]+$/g, '');
    if (!s) return false;
    return /\bcoupon\s+(?:usage\s+)?limit\s+(?:has\s+been\s+)?reached\b/.test(s)
      || /\bcoupon\s+already\s+(?:been\s+)?claimed\b/.test(s)
      || /\b(?:you\s+(?:have|'ve)\s+)?already\s+(?:used|claimed|redeemed)\s+(?:this\s+)?coupon\b/.test(s)
      || /\bthis\s+coupon\s+(?:has\s+)?already\s+been\s+(?:used|claimed|redeemed)\b/.test(s);
  }
  function limitKey(storeId, email, table, code) {
    return [str(storeId), str(email).trim().toLowerCase(), table ? 'table' : 'online', codeKey(code)].join(':');
  }
  function limitPeek(key, now) {
    var e = Object.prototype.hasOwnProperty.call(limitCache, key) ? limitCache[key] : null;
    return e && e.until > now ? e.reached : undefined;
  }
  function limitSet(key, reached, now) {
    if (limitSize >= LIMIT_MAX) {
      Object.keys(limitCache).forEach(function (k) { if (limitCache[k].until <= now) { delete limitCache[k]; limitSize -= 1; } });
      if (limitSize >= LIMIT_MAX) { limitCache = {}; limitSize = 0; }
    }
    if (!Object.prototype.hasOwnProperty.call(limitCache, key)) limitSize += 1;
    limitCache[key] = { reached: !!reached, until: now + (reached ? LIMIT_HIT_MS : LIMIT_OK_MS) };
  }
  // One code's check → Promise<true (reached) | false | null (not checked: a failure, never cached)>. Shared while in
  // flight; an answer is cached (reached 30 min, else 15 min).
  function limitCheck(o) {
    var key = limitKey(o.storeId, o.email, o.table, o.code);
    var hit = limitPeek(key, limitClock());
    if (hit !== undefined) return Promise.resolve(hit);
    if (limitFlight[key]) return limitFlight[key];
    function answer(reached) { limitSet(key, reached, limitClock()); return reached; }
    var p = Promise.resolve().then(function () {
      return OO.api.validateCoupon(o.hotel, { code: codeKey(o.code), email: o.email, time: o.time, domain: o.domain });
    }).then(function (res) {
      return answer(!!res && (res.status === false || res.status === 'false') && isLimitMessage(res.message));
    }, function (e) {
      // The real adapter rejects a refusal (HTTP 200, kind 'client', the server's text); anything else: unknown.
      return e && e.kind === 'client' ? answer(isLimitMessage(e.message)) : null;
    });
    limitFlight[key] = p;
    p.then(function () { delete limitFlight[key]; });
    return p;
  }
  // useLimits(hotel, o, codes) → { reached: { CODE: true }, checking, mark(code, message) → bool } (a hook). Read from
  // the cache on every render (a warm cache answers at once); the misses are asked in an effect, all at once.
  function useLimits(hotel, o, codes) {
    var bump = React.useState(0)[1];
    var failed = React.useRef({}); // keys whose check failed on this page: not reached, not asked again here
    var atRef = React.useRef(null);
    var cc = o.cc;
    atRef.current = cc ? cc.at : null;
    var on = !!(o.ready && cc && cc.email && codes.length);
    var now = limitClock();
    var reached = {};
    var missing = [];
    if (on) {
      codes.forEach(function (code) {
        var key = limitKey(o.store.id, cc.email, !!cc.table, code);
        var hit = limitPeek(key, now);
        if (hit === undefined) { if (!failed.current[key]) missing.push(code); } else if (hit) reached[code] = true;
      });
    }
    var sig = missing.length ? [o.store.id, cc.email.toLowerCase(), cc.table ? 'table' : 'online', missing.join('|')].join(':') : '';
    var req = sig ? { storeId: o.store.id, email: cc.email, table: !!cc.table, domain: OO.data.domain(cc.orderType) } : null;
    React.useEffect(function () {
      if (!sig) return undefined;
      var alive = true;
      var time = new Date(atRef.current === null ? OO.data.now().getTime() : atRef.current).toISOString();
      Promise.all(missing.map(function (code) {
        var q = Object.assign({ hotel: hotel, code: code, time: time }, req);
        return limitCheck(q).then(function (r) {
          if (r === null) failed.current[limitKey(q.storeId, q.email, q.table, code)] = true;
        });
      })).then(function () { if (alive) bump(function (n) { return n + 1; }); });
      return function () { alive = false; };
    }, [sig]);
    return {
      reached: reached,
      checking: missing.length > 0,
      mark: function (code, message) {
        var c = o.cc;
        if (!isLimitMessage(message) || !codeKey(code) || !c || !c.email || !o.store) return false;
        limitSet(limitKey(o.store.id, c.email, !!c.table, code), true, limitClock());
        bump(function (n) { return n + 1; });
        return true;
      },
    };
  }
  function codesOf(list) {
    var out = [];
    arr(list).forEach(function (c) { var k = codeKey(c && c.code); if (k && out.indexOf(k) < 0) out.push(k); });
    return out;
  }

  // ---- the deals page: recovery (REF dealRecovery + OnlineOrderDealRecoveryModal; see the header) --------------------
  var RECOVERABLE = ['MIN_ORDER', 'NO_ITEMS', 'MIN_ITEMS'];
  function recoverable(code) { return RECOVERABLE.indexOf(code) >= 0; }
  // A failure of the deal's apply that more items can fix: the local gates' own refusal (recheck at the page's order)
  // with a recoverable code, and the very text the apply refused it with. null otherwise (the server's refusal, …).
  function recoveryOf(deal, text, cc) {
    if (!deal || !cc) return null;
    var r;
    try { r = OO.coupons.recheck(deal.coupon, cc.lines, cc.rc); } catch (e) { return null; }
    return !r.keep && recoverable(r.code) && r.reason === text ? r : null;
  }
  function progressOf(deal, lines, base) {
    var c = deal.coupon;
    var item = deal.kind === 'coupon_product';
    var minOrder = Math.max(0, numOr0(c.minOrder));
    var minItems = item ? Math.max(1, numOr0(deal.minItems) || 1) : 0;
    var units = 0;
    if (item) {
      arr(lines).forEach(function (l) {
        if (l && !l.reward && !l.free && OO.coupons.lineMatches(l, c)) units += Math.max(0, numOr0(l.qty));
      });
    }
    var subtotal = round2(Math.max(0, numOr0(base)));
    return {
      subtotal: subtotal, minOrder: minOrder, remainingValue: round2(Math.max(0, minOrder - subtotal)),
      units: units, minItems: minItems, remainingItems: Math.max(0, minItems - units),
    };
  }
  // The deal's sibling products (its coupon's other orderable cards), REF's order: cheapest first while items are short;
  // while value is short, the ones that reach it (cheapest first) before the rest (dearest first); ≤ 6.
  function siblingsOf(deals, deal, prog) {
    if (!deal || deal.kind !== 'coupon_product') return [];
    var seen = {};
    var list = arr(deals).filter(function (x) {
      if (!x.orderable || x.coupon.id !== deal.coupon.id || seen[x.product.id]) return false;
      seen[x.product.id] = true;
      return true;
    });
    var need = prog.remainingValue;
    list.sort(function (a, b) {
      if (prog.remainingItems > 0 || !(need > 0)) return a.base - b.base;
      var ao = a.base >= need;
      var bo = b.base >= need;
      if (ao !== bo) return ao ? -1 : 1;
      return ao ? a.base - b.base : b.base - a.base;
    });
    return list.slice(0, 6).map(function (x) {
      return { key: x.key, name: x.title, image: x.image, price: x.base, from: x.from, sibling: true, kind: 'product', id: x.product.id };
    });
  }
  function helperOf(s, prog, cur) {
    if (prog.remainingItems > 0 && s.sibling) return 'Counts as one eligible item';
    if (prog.remainingValue > 0) return s.from ? 'Adds toward the minimum' : 'Adds ' + money(s.price, cur) + ' toward the minimum';
    return 'Eligible for this deal';
  }

  // ---- the deals page: parts ----------------------------------------------------------------------------------------
  function ctaOf(x) {
    if (!x.orderable) return 'Not available';
    if (x.kind !== 'coupon_product') return 'Add to order';
    if (x.free) return 'Get it free';
    if (x.coupon.type === 'BUY_ONE_GET_ONE_FREE') return 'Add & get one free';
    if (x.coupon.type === 'BUY_N_GET_M_FREE') return 'Start this deal';
    return 'Add & save';
  }
  // REF's condition lines (at most two; one when the card is greyed, under its label).
  function metaOf(x, cur) {
    var out = [];
    if (arr(x.sizes).length) out.push((x.sizes.length === 1 ? 'Size: ' : 'Sizes: ') + x.sizes.join(', '));
    if (x.freeCount > 0) {
      var names = arr(x.rewards).slice(0, 2).join(', ');
      out.push('Choose ' + x.freeCount + ' free ' + (x.freeCount === 1 ? 'item' : 'items') + (names ? ': ' + names + (x.rewards.length > 2 ? '…' : '') : ''));
    }
    if (x.minOrder > 0) out.push('Minimum order ' + money(x.minOrder, cur));
    if (x.siblings > 1) out.push('1 of ' + x.siblings + ' items on this offer');
    if (x.ends) out.push(x.ends);
    return out.slice(0, x.orderable ? 2 : 1);
  }
  function priceText(x, cur) {
    if (x.free) return 'Free';
    return (x.from ? 'From ' : '') + money(x.price, cur);
  }
  // A product, special or combo deal. busy: its coupon is being checked; disabled: a guest must log in for a coupon deal.
  function DealCard(props) {
    var ui = OO.ui;
    var x = props.deal;
    var cur = props.currency;
    var cta = ctaOf(x);
    var meta = metaOf(x, cur);
    var savePct = x.was > 0 && x.saving > 0 ? Math.round((x.saving / x.was) * 100) : 0;
    return (
      <li className={ui.cx('oo-dcard', !x.image && 'oo-dcard--noimg', !x.orderable && 'is-off', x.applied && 'is-applied')}>
        <div className="oo-dcard__body">
          <div className="oo-dcard__badges">
            <ui.Badge tone="success" size="sm">{x.badge}</ui.Badge>
            {x.applied ? <ui.Badge tone="done" size="sm">Applied</ui.Badge> : null}
          </div>
          <h3 className="oo-dcard__name">{x.title}</h3>
          <div className="oo-dcard__price">
            <span className="oo-dcard__now">{priceText(x, cur)}</span>
            {x.was ? <s className="oo-dcard__was"><span className="oo-sr-only">was </span>{money(x.was, cur)}</s> : null}
            {savePct >= 5 ? <span className="oo-dcard__save">{'-' + savePct + '%'}</span> : null}
          </div>
          {x.description ? <p className="oo-dcard__desc">{x.description}</p> : null}
          {!x.orderable && x.label ? <p className="oo-dcard__off">{x.label}</p> : null}
          {meta.length ? <ul className="oo-dcard__meta">{meta.map(function (m) { return <li key={m}>{m}</li>; })}</ul> : null}
          <div className="oo-dcard__foot">
            <ui.Button size="xs" variant={x.orderable ? 'primary' : 'soft'} disabled={!x.orderable || !!props.disabled}
              busy={!!props.busy} aria-label={cta + ': ' + x.title} onClick={function () { props.onAction(x); }}>{cta}</ui.Button>
          </div>
        </div>
        {x.image ? <span className="oo-dcard__media"><img className="oo-dcard__img" src={x.image} alt="" loading="lazy" /></span> : null}
      </li>
    );
  }
  // An order coupon as OfferCard's view (the coupons page's card): Apply to order | Remove (applied) | its gate's reason.
  function dealView(x, v) {
    var view = {
      id: x.coupon.id, code: x.code, type: x.type, offer: x.offer, subtitle: x.subtitle, scope: x.scope, minOrder: x.minOrder,
      ends: x.ends, applied: !!x.applied, action: 'apply', label: 'Apply to order', disabled: false, why: null, tone: null,
    };
    if (x.applied) {
      var on = cardOf({ coupon: x.coupon, on: true, e: {}, canApply: true, addMore: 0 }, v);
      return Object.assign(view, { action: on.action, label: on.label, why: on.why, tone: on.tone });
    }
    if (!x.orderable) return Object.assign(view, { label: 'Not available', disabled: true, why: x.label, tone: 'warn' });
    if (v.needLogin) view.disabled = true;
    return view;
  }
  // "Complete this deal" (REF OnlineOrderDealRecoveryModal): the refusal, what is still short (the basket minimum with
  // its bar; the eligible items; the sizes the coupon takes), up to six things to add (Add → the item sheet, then the
  // deal is tried again), Review cart and Browse full menu.
  function RecoveryDialog(props) {
    var ui = OO.ui;
    var r = props.recovery;
    var p = props.progress;
    var cur = props.currency;
    var deal = r.deal;
    var valueMet = p.remainingValue <= 0;
    var itemsMet = p.remainingItems <= 0;
    var showItems = p.minItems > 1 || !itemsMet;
    var footer = (
      <div className="oo-deal-help__foot">
        <ui.Button size="md" variant="outline" href={props.cart.href} onClick={props.cart.onClick}>Review cart</ui.Button>
        <ui.Button size="md" href={props.menu.href} onClick={props.menu.onClick}>Browse full menu</ui.Button>
      </div>
    );
    return (
      <ui.Sheet open variant="dialog" title="Complete this deal" onClose={props.onClose} footer={footer} className="oo-deal-help">
        <div className="oo-deal-help__lead">
          <div className="oo-deal-help__tags">
            <span className="oo-deal-help__tag">Deal needs one more step</span>
            {deal.code ? <span className="oo-coupons__code">{deal.code}</span> : null}
          </div>
          <p className="oo-deal-help__msg">{r.message}</p>
        </div>
        {r.itemWasAdded ? <ui.InlineNotice tone="success">Your item is in your cart. Add only what’s still needed below.</ui.InlineNotice> : null}
        <ul className="oo-deal-help__progress" aria-label="Deal progress">
          {p.minOrder > 0 ? (
            <li className={ui.cx('oo-deal-help__row', valueMet && 'is-met')}>
              <span className="oo-deal-help__mark" aria-hidden="true">{valueMet ? <OO.icons.Check size={14} /> : <OO.icons.Info size={14} />}</span>
              <span className="oo-deal-help__text">
                <span className="oo-deal-help__label">Basket minimum</span>
                <span className="oo-deal-help__value">{valueMet ? money(p.minOrder, cur) + ' minimum reached'
                  : money(p.subtotal, cur) + ' of ' + money(p.minOrder, cur) + ' · Add ' + money(p.remainingValue, cur) + ' more'}</span>
              </span>
            </li>
          ) : null}
          {showItems ? (
            <li className={ui.cx('oo-deal-help__row', itemsMet && 'is-met')}>
              <span className="oo-deal-help__mark" aria-hidden="true">{itemsMet ? <OO.icons.Check size={14} /> : <OO.icons.Info size={14} />}</span>
              <span className="oo-deal-help__text">
                <span className="oo-deal-help__label">Eligible items</span>
                <span className="oo-deal-help__value">{itemsMet ? plural(p.minItems, 'eligible item') + ' added'
                  : p.units + ' of ' + p.minItems + ' · Add ' + p.remainingItems + ' more'}</span>
              </span>
            </li>
          ) : null}
          {arr(deal.sizes).length ? (
            <li className="oo-deal-help__row oo-deal-help__row--note">
              <span className="oo-deal-help__text"><span className="oo-deal-help__label">{deal.sizes.length === 1 ? 'Eligible size' : 'Eligible sizes'}</span>
                <span className="oo-deal-help__value">{deal.sizes.join(', ')}</span></span>
            </li>
          ) : null}
        </ul>
        {p.minOrder > 0 ? <ui.ProgressBar value={Math.min(p.subtotal, p.minOrder)} max={p.minOrder} label="Basket minimum progress" /> : null}
        <div className="oo-deal-help__h">
          <h3 className="oo-deal-help__title">Add an item to unlock the deal</h3>
          <p className="oo-deal-help__sub">We’ll try the deal again once it’s in your cart.</p>
        </div>
        {arr(props.suggestions).length ? (
          <ul className="oo-deal-help__list" aria-label="Suggestions">
            {props.suggestions.map(function (s) {
              return (
                <li key={s.key} className="oo-deal-help__item">
                  <span className="oo-deal-help__thumb" aria-hidden="true">{s.image ? <img src={s.image} alt="" loading="lazy" /> : <OO.icons.Gift size={20} />}</span>
                  <span className="oo-deal-help__itext">
                    <span className="oo-deal-help__iname">{s.name}</span>
                    <span className="oo-deal-help__ihelp">{helperOf(s, p, cur)}</span>
                    <span className="oo-deal-help__iprice">{(s.from ? 'From ' : '') + money(s.price, cur)}</span>
                  </span>
                  <ui.Button size="xs" aria-label={'Add ' + s.name + ' and try the deal again'} onClick={function () { props.onPick(s); }}>Add</ui.Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="oo-deal-help__none">Browse the menu to add something toward this deal.</p>
        )}
      </ui.Sheet>
    );
  }

  // ---- the deals page (P2-32; see the header) ------------------------------------------------------------------------
  var GONE_TEXT = 'This deal has reached its claim limit and is no longer available.';
  OO.pages.Deals = function Deals(props) {
    var route = props.route;
    var hotel = route.hotel;
    var ui = OO.ui;
    var A = Acct();
    var K = Kit();
    var toast = useToast();
    var sheetS = React.useState(null);  // { kind: 'product' | 'combo', id, deal }: the item sheet / combo builder open
    var recS = React.useState(null);    // { deal, message, itemWasAdded }: the recovery dialog open
    var seqS = React.useState(0);       // bumped once a deal's item is in the cart: its coupon is applied after that render
    var attempt = React.useRef(null);   // { deal, productId, itemWasAdded, sent, applied }: the deal being applied
    var ccRef = React.useRef(null);
    // A coupon waiting for a choice right after its deal's item was added: an 'item' coupon takes that product (REF
    // selected_item_discount_product_id); anything else opens the picker.
    function autoPick(c) {
      var a = attempt.current;
      var cc = ccRef.current;
      if (!a || a.productId === null || a.productId === undefined || !cc || OO.coupons.freeFlow(c) !== 'item') return false;
      var r;
      try { r = OO.coupons.recheck(Object.assign({}, c, { picks: Object.assign({}, c.picks || {}, { product: a.productId }) }), cc.lines, cc.rc); } catch (e) { return false; }
      if (!r.keep || !r.ok) return false;
      OO.store.setCoupon(r.coupon);
      toast.say('Coupon ' + (r.coupon.code || r.coupon.name || '') + ' applied');
      return true;
    }
    var o = useOffers(hotel, toast.say, {
      onApplied: function (c) {
        var a = attempt.current;
        if (a && a.deal.coupon.id === c.id) a.applied = true;
        recS[1](null);
      },
      onPending: autoPick,
    });
    ccRef.current = o.cc;
    var enabled = o.ready && OO.coupons.enabled(o.store);
    var q = K.useCouponList(o.store, o.cc, enabled);
    var list = enabled && !q.loading && !q.error ? q.list : [];
    var lim = useLimits(hotel, o, codesOf(list));
    var moment = K.useUpsellMoment(hotel, o.store, o.menu);
    var rec = recS[0];
    // A whole-order deal's recovery suggests the cart's suggestions (REF useCartSuggestions; read while it is open).
    var sugQ = OO.useAsync(function () {
      return rec && rec.deal.kind === 'coupon' && OO.upsell && typeof OO.upsell.cartSuggestions === 'function'
        ? OO.upsell.cartSuggestions(hotel, OO.store.get().cart.lines) : Promise.resolve(null);
    }, [rec ? rec.deal.key : null]);
    var applied = o.applied;
    // The catalogue is re-built when what it shows can change: the menu, the judging moment, the coupons listed (their
    // availability at the order's instant), the codes the pre-check hid, the applied coupon, the order type, the payment.
    var listSig = list.map(function (c) { return c.id + (c.available === false ? '-' : '+'); }).join(',');
    var reachedSig = Object.keys(lim.reached).sort().join('|');
    var cat = React.useMemo(function () {
      if (!o.ready || !o.menu) return null;
      return catalog({
        coupons: list, menu: o.menu, store: o.store, currency: o.store.currency, orderType: o.pc.orderType, paymentMethod: o.pc.paymentMethod,
        today: todayOf(o), excluded: lim.reached, applied: applied,
        judge: moment ? function (e) { return OO.model.displayState(o.menu, e, moment); } : null,
      });
    }, [o.ready, o.store, o.menu, listSig, moment, reachedSig, applied ? applied.id : null, o.pc ? o.pc.orderType : null, o.pc ? o.pc.paymentMethod : null]);
    // A deal's item is in the cart (the item sheet's onAdded): its coupon is applied on the commit that holds the line.
    React.useEffect(function () {
      var a = attempt.current;
      if (!seqS[0] || !a || a.sent) return;
      a.sent = true;
      o.api.apply(a.deal.coupon);
    }, [seqS[0]]);
    // The deal's apply failed: its limit is reached (hidden, toast), more items can fix it (recovery), else the toast.
    React.useEffect(function () {
      var err = o.api.error;
      var a = attempt.current;
      if (!err || !a || a.applied || err.id !== a.deal.coupon.id) return;
      attempt.current = null;
      o.api.clearError();
      if (lim.mark(a.deal.coupon.code, err.text)) { recS[1](null); toast.say(GONE_TEXT); return; }
      if (recoveryOf(a.deal, err.text, ccRef.current)) { recS[1]({ deal: a.deal, message: err.text, itemWasAdded: !!a.itemWasAdded }); return; }
      toast.say(err.text);
    }, [o.api.error]);

    var header = A.headerProps({ hotel: hotel, mode: props.mode, back: OO.router.href('menu', null, { hotel: hotel }), profile: true });
    var skeleton = (
      <div className="oo-acct__skel">
        <ui.Skeleton lines={2} className="oo-acct__skel-title" />
        {listSkeleton('Loading deals', 4, 140)}
      </div>
    );
    if (props.preview && K && OO.appKit.PizzaClubDealsPreview) return <OO.appKit.PizzaClubDealsPreview route={route} hotel={hotel} store={o.store || {}} catalog={cat} variant={props.preview} />;
    var blocked = A.gate(o.h, { title: 'Deals', header: header, skeleton: skeleton });
    if (blocked) return blocked;
    if (!o.ready || !cat) return <A.Page title="Deals" {...header} busy>{skeleton}</A.Page>;

    var store = o.store;
    var cur = store.currency;
    var api = o.api;
    var needLogin = needLoginOf(o);
    var v = cardCtx(o, needLogin);
    var menuLink = OO.router.link('menu', null, { hotel: hotel });
    var cartLink = OO.router.link('cart', null, { hotel: hotel });
    var busyKey = api.busy && attempt.current && api.busy === attempt.current.deal.coupon.id ? attempt.current.deal.key : null;
    function act(x) {
      if (!x.orderable || api.busy) return;
      if (x.kind === 'coupon') {
        if (x.applied) { api.remove(); return; }
        attempt.current = { deal: x, productId: null, itemWasAdded: false };
        api.apply(x.coupon);
        return;
      }
      sheetS[1]({ kind: x.kind === 'combo' ? 'combo' : 'product', id: x.product.id, deal: x.kind === 'coupon_product' ? x : null });
    }
    function closeSheet() { sheetS[1](null); }
    function onAdded(line, info) {
      var s = sheetS[0];
      sheetS[1](null);
      toast.say((info && info.message) || 'Added to cart');
      if (!s || !s.deal) return;
      attempt.current = { deal: s.deal, productId: line && line.itemId !== undefined ? line.itemId : null, itemWasAdded: true };
      seqS[1](function (n) { return n + 1; });
    }
    function pickSuggestion(s) {
      var deal = rec && rec.deal;
      recS[1](null);
      sheetS[1]({ kind: s.kind === 'combo' ? 'combo' : 'product', id: s.id, deal: deal });
    }

    var body;
    var loading = (enabled && q.loading) || lim.checking;
    if (loading) body = listSkeleton('Loading deals', 4, 140);
    else if (!cat.counts.total) {
      body = (
        <ui.EmptyState icon="Ticket" title="No deals right now" body="Offers appear here as soon as this store publishes them."
          action={<ui.Button size="md" href={menuLink.href} onClick={menuLink.onClick}>Browse menu</ui.Button>} />
      );
    } else {
      body = cat.groups.map(function (g) {
        var id = 'oo-deals-' + g.key;
        return (
          <section key={g.key} className="oo-deals__group" aria-labelledby={id}>
            <ui.SectionTitle level="section" id={id} className="oo-offers__head" sub={g.hint}
              action={<span className="oo-offers__count" aria-label={plural(g.entries.length, 'deal')}>{g.entries.length}</span>}>{g.title}</ui.SectionTitle>
            <ul className="oo-offers__grid" aria-label={g.title}>
              {g.entries.map(function (x) {
                if (x.kind === 'coupon') {
                  return <OfferCard key={x.key} view={dealView(x, v)} currency={cur} busy={api.busy} onAction={function () { act(x); }} />;
                }
                return (
                  <DealCard key={x.key} deal={x} currency={cur} busy={busyKey === x.key}
                    disabled={!!api.busy || (x.kind === 'coupon_product' && needLogin)} onAction={act} />
                );
              })}
            </ul>
          </section>
        );
      });
    }

    var overlay = null;
    var sheet = sheetS[0];
    if (sheet && o.menu) {
      var Overlay = sheet.kind === 'combo' ? OO.pages.ComboBuilder : OO.pages.ItemSheet;
      if (Overlay) {
        overlay = sheet.kind === 'combo'
          ? <Overlay key={'c' + sheet.id} hotel={hotel} store={store} menu={o.menu} comboId={sheet.id} moment={moment || undefined} onClose={closeSheet} onAdded={onAdded} />
          : <Overlay key={'p' + sheet.id} hotel={hotel} store={store} menu={o.menu} productId={sheet.id} moment={moment || undefined} onClose={closeSheet} onAdded={onAdded} />;
      }
    }
    var help = null;
    if (rec) {
      var prog = progressOf(rec.deal, o.lines, baseOf(o));
      var sugs = rec.deal.kind === 'coupon_product' ? siblingsOf(cat.deals, rec.deal, prog) : cartPicks(sugQ.data, o, hotel, moment);
      help = (
        <RecoveryDialog recovery={rec} progress={prog} suggestions={sugs} currency={cur} cart={cartLink} menu={menuLink}
          onPick={pickSuggestion} onClose={function () { recS[1](null); }} />
      );
    }
    return (
      <React.Fragment>
        <A.Page title="Deals" {...header} className={ui.cx("oo-offers oo-deals", props.embedded && "oo-pc-embedded-deals")}>
          <ui.SectionTitle level="page" sub={store.name}>Deals and offers</ui.SectionTitle>
          {needLogin ? loginGate(hotel, store) : null}
          <K.PickPrompt plan={o.plan} onChoose={function () { o.picker.set(true); }} onRemove={api.remove} />
          {q.error ? (
            <div className="oo-deals__err">
              <ui.InlineNotice tone="warn">We couldn’t load this store’s coupons.</ui.InlineNotice>
              <ui.Button size="sm" variant="outline" onClick={q.reload}>Try again</ui.Button>
            </div>
          ) : null}
          {body}
          <ui.PoweredBySipo />
        </A.Page>
        {cartBar(o, hotel)}
        {overlay}
        {help}
        {overlays(o, toast)}
      </React.Fragment>
    );
  };
  // The cart's suggestions for a whole-order deal's recovery: the addable ones (D-07), ≤ 6 (REF), as dialog rows.
  function cartPicks(rows, o, hotel, moment) {
    var U = OO.upsell;
    if (!rows || !U || typeof U.resolve !== 'function' || !o.menu) return [];
    var inCart = {};
    U.cartIds(o.lines).forEach(function (id) { inCart[id] = true; });
    return U.resolve(rows, o.menu, { moment: moment || undefined, excluded: U.excluded(hotel), inCart: inCart, limit: 0 }).filter(function (s) {
      return s.addable;
    }).slice(0, 6).map(function (s) {
      var b = basePriceOf(s.product, []);
      return { key: 'sug-' + s.product.id, name: s.product.name, image: s.product.image || null, price: b.price, from: b.from || arr(s.product.sizes).length > 0, sibling: false, kind: 'product', id: s.product.id };
    });
  }
  OO.pages.Deals.logic = {
    GROUPS: GROUPS, badgeOf: badgeOf, basePriceOf: basePriceOf, itemDeal: itemDeal, catalog: catalog,
    isLimitMessage: isLimitMessage, limitKey: limitKey, recoverable: recoverable, recoveryOf: recoveryOf, progressOf: progressOf,
    siblingsOf: siblingsOf, helperOf: helperOf, ctaOf: ctaOf, metaOf: metaOf, dealView: dealView,
    limits: {
      peek: limitPeek, set: limitSet, check: limitCheck, TTL_OK: LIMIT_OK_MS, TTL_REACHED: LIMIT_HIT_MS,
      __setClock: function (fn) { var before = limitClock; limitClock = typeof fn === 'function' ? fn : function () { return Date.now(); }; return before; },
      __reset: function () { limitCache = {}; limitSize = 0; limitFlight = {}; },
    },
  };
  OO.pages.Deals.parts = { DealCard: DealCard, RecoveryDialog: RecoveryDialog };
})();


// ===================== src/pages/checkout-time.jsx =====================
// src/pages/checkout-time.jsx: the checkout's time section (Batch 2 P2-00b: moved out of checkout.jsx; P2-23: the
// scheduling UI on OO.schedule and the 15-minute stale re-prompt): the order type card: a Pickup / Delivery toggle when
// the store offers both here (else the type's title), the Day + Time selects, the re-prompt, the line on why ASAP isn't
// offered, and the delivery address part inside the card (checkout-address.jsx). Next owners: P4-06, P4-10 (the app
// order sheet: the same picker and freshness rule). Styles: styles/checkout.css.
//
// P2-23. REF = the Next app, develop d298a7e: OTP = src/online-order/ui/OrderTimePopup.tsx, OC =
// src/shared/orders/OrderContext.tsx, CF = src/shared/checkout/components/CheckoutForm.tsx. Our checkout keeps the
// choice inline (REF locks its checkout and edits the time in the menu's popup).
//   Times    OO.schedule (src/lib/schedule.jsx) at ONE instant, re-read every minute and when the page is shown or
//            focused again: OO.data.now(), so a fixture store's clock counts. ASAP comes first on Today while asap().ok
//            (the type's hours, closures, holidays, "Future Order Only"); then the order-later window's days (window():
//            only with is_preorder || order_later; without it, ASAP is the only time), each with the times of the runs
//            that START on it (slots()' owner days: a past-midnight time reads '12:30am · Sat 3', REF's dayLabel). The
//            engine never checks the store's order types: a type missing from store.orderTypes has no times here
//            (cart.jsx's list falls back to PICKUP).
//   Days     The Day select lists every window day (REF generateAvailableDays): a holiday without times as 'Fri 25 Dec
//            · Closed: Christmas Day' ('· Closed for holiday' without a label; never REF's "noReasonProvided."), another
//            day without times as '· No times'; both disabled.
//   Default  ASAP while it is offered, else the first time (the first day with times). It isn't written to the draft;
//            the one the customer saw (the first shown for this store and type) stays shown while it is offered.
//   Choice   Picking a day (its first time) or a time, or the re-prompt's button, writes { day (the owner day), slot
//            ('ASAP' | 'YYYY-MM-DD HH:mm:00'), chosenAt (OO.data.now(), ms) } to the draft; a type change clears all
//            three. A chosen time is found again with OO.schedule.findSlot (never isOpenAt, which is end-exclusive);
//            'ASAP' while asap().ok. Today's times move with the clock (OO.schedule's Grid: 6:30pm at 3:00pm, 6:35pm at
//            3:01pm), and findSlot keeps a time chosen from them while it is bookable: such a time is listed in its
//            day. store.jsx keeps only its listed draft keys over a reload, so a chosen time restored after a reload
//            has no known age.
//   Stale    (REF evaluateOrderSelectionFreshness, OC:136-201; ORDER_SELECTION_FRESHNESS_MS = 15 min) a chosen time
//            that is no longer offered, or was chosen 15 min ago or more (or at an unknown time); the untouched default
//            the customer saw, once it is no longer offered (ASAP at 9:55pm: "Tomorrow, 11:30am" at 10:01; it never
//            ages). Never while there is nothing to choose (online ordering off, the type not offered, no time at all):
//            the card says why.
//            A stale time is asked again: "Review your order time" (OTP's review title), REF's reason line (CF:813-834;
//            "from the menu" dropped: the time is chosen here) and "Update order details" (OTP:1297), which takes the
//            time shown. checkout.jsx asks (time.ask) instead of paying while it is stale.
//
// OO.pages.CheckoutSections: the checkout's sections (the established OO.pages.* kit pattern, cf. OO.pages.acct). This
// file loads first of the checkout*.jsx files and creates it; checkout.jsx (loaded last) calls every section INLINE on
// each render, never as a component, so the page's element tree, its hooks and its state stay in the Checkout
// component: use(…) runs a section's hooks (before Checkout's early returns), card / fields(…) returns its markup.
//   _ = { byKey, numOr0, centsOf } (internal: shared by the checkout*.jsx files only; pages never read it).
//   time.use({ store, orderType }) → T (hooks; the draft is read from OO.store) = { opts, openNow, picker, day, slot,
//       stale, ask, confirm, reviewRef }; without a store every field is null (ask / confirm do nothing useful).
//       opts       the shape checkout.jsx reads (Phase 1's): { days (the days WITH times, the default first), slots (the
//                  first day's), asapLabel, cookingTimeMins (the buffer) } plus list (every window day: picker.list).
//       openNow    OO.model.openStatus(store, now, orderType).
//       picker     time.picker(store, orderType, now).
//       day, slot  time.resolve's for the draft, or for the untouched default the customer saw: what the order is for
//                  (while it is stale: what the re-prompt proposes).
//       stale      null | { reason, title, text } (time.freshness; text is null for 'expired').
//       ask()      shows the re-prompt: scrolls it into view and focuses it, after the render.
//       confirm(day, slot)  takes that time as the customer's choice (see Choice).
//       reviewRef  the re-prompt's element.
//   time.card(st, { K, store, types, orderType, isDelivery, typeBlocked, day, slot, deliveryFields }) → the order type
//       card (st = time.use's; K = OO.pages.Cart.shared; day / slot = the ones checkout.jsx orders with). Picking a
//       type clears the draft's day, slot and chosenAt; a day picks its first time.
//   Pure (tests/time-p223.test.mjs). now: a Date | epoch ms | 'Y-m-d H:i:s' store wall clock (as OO.schedule's).
//   time.picker(store, orderType, now) → Picker = { type ('DELIVERY' for any form of it, the app's APP_DELIVERY too, as
//       OO.schedule reads it; else 'PICKUP'), at (ms), today (ymd), online, offered (store.orderTypes has the type),
//       gate (OO.schedule.window), asap { ok, reason ('notOffered', else OO.schedule.asap's), slot (the ASAP Slot |
//       null) }, list [Day] (every window day; a Today holding only ASAP when there is no window), days [Day] (those
//       with times), first (the default Slot | null), asapLabel ('15–20 mins'), cookingTimeMins }. Day = { key (= ymd),
//       ymd, date, label ('Today' | 'Tomorrow' | 'Fri 2 Oct'), holiday, holidayLabel, slots, pickable, text (its Day
//       select label) }. Slot = { value, label ('6:30pm', '12:30am · Sat 3'), time, asap, deliveryTime, at (ms), ymd,
//       owner (its Day's key), dayLabel }; the ASAP Slot: value 'ASAP', label asapLabel, at = when it would be ready.
//       checkout.jsx and OO.payload read value, label, asap and deliveryTime, as they read pickupOptions' slots.
//   time.resolve(picker, store, draft) → { day, slot, chosen (the draft names a time), found (… still offered) }: the
//       chosen time while offered (one findSlot offers off today's moving grid is listed in a copy of its day); else
//       the chosen day's first time while that day has times (REF's review popup keeps the day: OTP:503-517); else the
//       default. day / slot are null when there is no time at all.
//   time.freshness(picker, store, draft, { now, seen }) → { fresh, reason, expiresAt }: reason null | 'asapGone' |
//       'schedulingOff' | 'gone' | 'expired' (REF's order: whether the time is still offered, then its age); expiresAt
//       = chosenAt + 15 min | null. seen: the untouched default the customer saw ('ASAP' | a value): 'asapGone' /
//       'gone' once it is no longer offered.
//   time.note(picker, openNow, store) → the line under the selects when ASAP isn't offered but later times are, else
//       null: 'Closed now · Opens 5pm. Choose a time for later.' (Phase 1's), 'Closed today for a holiday · Christmas
//       Day. …', 'Temporarily closed · {reason}. …', 'ASAP isn’t available right now. …' (a closure starts within the
//       buffer), 'This store takes orders for later only.'
//   time.REVIEW (the re-prompt's copy) · time.FRESH_MS (15 min).
//   P4-06 (dine-in, mode qr; checkout.jsx "Dine-in"). REF TableSideCheckoutPage: no order type and no time picker; a
//   table order is ASAP, ready at now + pickup_buffer_time.
//   time.tableSlot(store, now) → the ASAP Slot of a table order (pure): value 'ASAP', label the cooking range ('15–20
//       mins'), asap true, at = now + store.cookingTimeMins (ms; the payload's deliver_time), deliveryTime / time / ymd
//       at that instant in the store's zone, owner = today. checkout.jsx orders with it in place of the picker's.
//   time.tableCard({ store, table }) → the dine-in card in place of the order type card: "Dine-in", the table (its name,
//       else 'Table {id}') and the party size when the context has one ('Table 7 · 3 guests'), and "Cooking time ~15
//       mins". Nothing to choose.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;
  var CS = (OO.pages.CheckoutSections = OO.pages.CheckoutSections || {});

  // ---- helpers shared by the checkout*.jsx files (CS._) ---------------------------------------------------------
  function byKey(list, key, value) {
    if (value === undefined || value === null || value === '') return null;
    return (list || []).filter(function (x) { return String(x[key]) === String(value); })[0] || null;
  }
  function numOr0(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function centsOf(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return isFinite(n) ? Math.round(n * 100) : null;
  }
  CS._ = { byKey: byKey, numOr0: numOr0, centsOf: centsOf }; // internal: the checkout*.jsx files only (see the header)

  // ---- the picker (pure; see the header) ---------------------------------------------------------------------------
  var FRESH_MS = 15 * 60000; // REF ORDER_SELECTION_FRESHNESS_MS (OC:34)
  var REVIEW = {
    title: 'Review your order time',
    asapGone: 'ASAP is no longer available. Please update your order time.',
    schedulingOff: 'Scheduled ordering is no longer available. Please update your order time.',
    gone: 'That time is no longer available. Please update your order time.',
    button: 'Update order details',
  };
  var LATER = 'Choose a time for later.';

  // 'PICKUP' | 'DELIVERY' from an order type in any form: the app's APP_DELIVERY is DELIVERY, as OO.schedule reads it (P2-30).
  function typeOf(t) { return String(t || '').toUpperCase().indexOf('DELIVERY') >= 0 ? 'DELIVERY' : 'PICKUP'; }
  function draftSlot(draft) { var s = draft ? draft.slot : null; return s === undefined || s === null ? '' : String(s); }
  // An engine slot as the selects show it: a past-midnight time names its day (REF: '1:00 AM · Sat 4').
  function slotView(s) {
    return {
      value: s.value, label: s.dayLabel ? s.label + ' · ' + s.dayLabel : s.label, time: s.time, asap: false,
      deliveryTime: s.deliveryTime, at: s.at, ymd: s.ymd, owner: s.owner, dayLabel: s.dayLabel || null,
    };
  }
  function dayText(d) {
    if (d.pickable) return d.label;
    if (d.holiday) return d.label + ' · ' + (d.holidayLabel ? 'Closed: ' + d.holidayLabel : 'Closed for holiday');
    return d.label + ' · No times';
  }
  function slotIn(days, value) {
    for (var i = 0; i < days.length; i++) {
      var s = byKey(days[i].slots, 'value', value);
      if (s) return s;
    }
    return null;
  }

  function picker(store, orderType, now) {
    var f = OO.format, S = OO.schedule;
    var type = typeOf(orderType);
    var r = OO.model._.resolveNow(now, store.timezone);
    var at = r.instant, today = r.clock.ymd;
    var a = S.asap(store, type, at);
    var buffer = Math.max(0, Math.round((a.readyAt - at) / 60000));
    var out = {
      type: type, at: at, today: today, online: !!(store.online && store.online.active),
      offered: (store.orderTypes || []).indexOf(type) >= 0, gate: S.window(store),
      asap: { ok: false, reason: a.reason, slot: null }, list: [], days: [], first: null,
      asapLabel: f.rangeMins(buffer, buffer + 5), cookingTimeMins: buffer,
    };
    if (!out.offered) {
      if (out.online) out.asap.reason = 'notOffered';
      return out;
    }
    var list = S.slots(store, type, at).map(function (d) {
      return { key: d.key, ymd: d.ymd, date: d.date, label: d.label, holiday: !!d.holiday, holidayLabel: d.holidayLabel || null, lateNight: !!d.lateNight, slots: d.slots.map(slotView) };
    });
    if (a.ok) {
      var z = f.zoned(new Date(a.readyAt), store.timezone);
      out.asap.ok = true;
      out.asap.slot = {
        value: 'ASAP', label: out.asapLabel, time: f.hm(z.minutes), asap: true, deliveryTime: z.ymd + ' ' + f.hm(z.minutes) + ':00',
        at: a.readyAt, ymd: z.ymd, owner: today, dayLabel: null,
      };
      if (!list.length || list[0].ymd !== today) {
        // No window (scheduling is off) or an empty one: Today holds ASAP alone.
        var hol = (store.holidays || []).indexOf(today) >= 0;
        list.unshift({ key: today, ymd: today, date: today, label: 'Today', holiday: hol, holidayLabel: hol ? (store.holidayLabels || {})[today] || null : null, slots: [] });
      }
      list[0].slots.unshift(out.asap.slot);
    }
    list.forEach(function (d) { d.pickable = d.slots.length > 0; d.text = dayText(d); });
    out.list = list;
    out.days = list.filter(function (d) { return d.pickable; });
    out.first = out.days.length ? out.days[0].slots[0] : null;
    return out;
  }

  // Day d with slot s listed (a copy; by time).
  function withSlot(d, s) {
    var out = Object.assign({}, d, { slots: d.slots.concat([s]).sort(function (a, b) { return a.at - b.at; }), pickable: true });
    out.text = dayText(out);
    return out;
  }
  function resolve(p, store, draft) {
    var want = draftSlot(draft);
    var out = { day: null, slot: null, chosen: want !== '', found: false };
    if (!p || !p.first) return out;
    var hit = null, day = null;
    if (want === 'ASAP') hit = p.asap.slot;
    else if (want) {
      var s = OO.schedule.findSlot(store, p.type, want, p.at);
      hit = s ? slotIn(p.days, s.value) : null;
      if (s && !hit) {
        // Still offered, but off today's grid (it moves with the clock): listed in a copy of its day.
        hit = slotView(s);
        day = withSlot(byKey(p.list, 'key', hit.owner), hit);
      }
    }
    if (hit) {
      out.found = true;
      out.slot = hit;
      out.day = day || byKey(p.days, 'key', hit.owner);
      return out;
    }
    var day = (want && byKey(p.days, 'key', draft.day)) || p.days[0];
    out.day = day;
    out.slot = day.slots[0];
    return out;
  }

  function freshness(p, store, draft, o) {
    o = o || {};
    var d = draft || {};
    var want = draftSlot(d);
    var stamp = d.chosenAt === null || d.chosenAt === undefined || d.chosenAt === '' ? NaN : Number(d.chosenAt);
    var expiresAt = isFinite(stamp) && stamp > 0 ? stamp + FRESH_MS : null;
    var now = o.now === undefined || o.now === null ? (p ? p.at : NaN) : (o.now instanceof Date ? o.now.getTime() : Number(o.now));
    function out(reason) { return { fresh: !reason, reason: reason || null, expiresAt: expiresAt }; }
    // Nothing to choose (REF: online off, no bookable time; OC:163-171): never asked; the card says why.
    if (!p || !p.online || !p.offered || !p.first) return out(null);
    if (want) {
      if (want === 'ASAP') {
        if (!p.asap.ok) return out('asapGone');
      } else if (!p.gate.enabled) {
        return out('schedulingOff');
      } else if (!OO.schedule.findSlot(store, p.type, want, p.at)) {
        return out('gone');
      }
      return out(expiresAt !== null && now < expiresAt ? null : 'expired');
    }
    // An untouched default never ages; it asks once the time the customer saw is no longer offered.
    if (o.seen === 'ASAP') return out(p.asap.ok ? null : 'asapGone');
    if (o.seen !== undefined && o.seen !== null && o.seen !== '') return out(OO.schedule.findSlot(store, p.type, o.seen, p.at) ? null : 'gone');
    return out(null);
  }

  function note(p, openNow, store) {
    if (!p || !p.first || p.asap.ok) return null;
    var why = p.asap.reason;
    if (why === 'futureOnly') return 'This store takes orders for later only.';
    if (why === 'holiday') {
      var label = (store && store.holidayLabels && store.holidayLabels[p.today]) || '';
      return 'Closed today for a holiday' + (label ? ' · ' + label : '') + '. ' + LATER;
    }
    if (why === 'closure') {
      if (openNow && !openNow.isOpen) return 'Temporarily closed' + (openNow.reason ? ' · ' + openNow.reason : '') + '. ' + LATER;
      return 'ASAP isn’t available right now. ' + LATER;
    }
    if (openNow && !openNow.isOpen) return 'Closed now' + (openNow.label && openNow.label !== 'Closed' ? ' · ' + openNow.label : '') + '. ' + LATER;
    return null;
  }

  // ---- time.use: the clock, the picker, the draft's time and its freshness (hooks) ----------------------------------
  function selectDraft(s) { return s.checkout; }
  function use(c) {
    var store = c.store, orderType = c.orderType;
    var draft = OO.useStore(selectDraft) || {};
    var tickS = React.useState(0);          // re-reads the times every minute and when the page is shown again
    var askS = React.useState(0);           // ask(): reveal and focus the re-prompt after the render
    var reviewRef = React.useRef(null);
    var seenRef = React.useRef(null);       // the untouched default the customer saw: { key (store|type), value }
    React.useEffect(function () {
      function bump() { tickS[1](function (n) { return n + 1; }); }
      function onShow() { if (typeof document === 'undefined' || document.visibilityState !== 'hidden') bump(); }
      var doc = typeof document !== 'undefined' ? document : null;
      var t = setInterval(bump, 60000);
      if (doc && doc.addEventListener) doc.addEventListener('visibilitychange', onShow);
      if (window.addEventListener) window.addEventListener('focus', onShow);
      return function () {
        clearInterval(t);
        if (doc && doc.removeEventListener) doc.removeEventListener('visibilitychange', onShow);
        if (window.removeEventListener) window.removeEventListener('focus', onShow);
      };
    }, []);
    React.useEffect(function () {
      var K = OO.pages.Cart && OO.pages.Cart.shared;
      if (askS[0] && reviewRef.current && K) { K.reveal(reviewRef.current, 'center'); K.focusEl(reviewRef.current); }
    }, [askS[0]]);
    var read = React.useMemo(function () {
      if (!store) return null;
      var p = picker(store, orderType, OO.data.now());
      return { p: p, openNow: OO.model.openStatus(store, p.at, orderType) };
    }, [store, orderType, tickS[0]]);
    var p = read ? read.p : null;
    var chosen = draftSlot(draft) !== '';
    // The untouched default the customer saw (the first one shown for this store and type): shown while it is offered.
    var key = p ? String(store.onlineKey || store.id || '') + '|' + p.type : '';
    if (p && p.first && !chosen && (!seenRef.current || seenRef.current.key !== key)) seenRef.current = { key: key, value: p.first.value };
    var seen = p && !chosen && seenRef.current && seenRef.current.key === key ? seenRef.current.value : null;
    var res = React.useMemo(function () {
      return p ? Object.assign(resolve(p, store, chosen ? draft : { slot: seen }), { chosen: chosen }) : null;
    }, [p, chosen, draft.day, draft.slot, seen]);
    var fr = p ? freshness(p, store, draft, { now: OO.data.now(), seen: seen }) : null;
    function ask() { askS[1](function (n) { return n + 1; }); }
    function confirm(day, slot) {
      if (!slot) return;
      OO.store.setCheckout({ day: day ? day.key : slot.owner || null, slot: slot.value, chosenAt: OO.data.now().getTime() });
    }
    return {
      opts: p ? { days: p.days, slots: p.days.length ? p.days[0].slots : [], list: p.list, asapLabel: p.asapLabel, cookingTimeMins: p.cookingTimeMins } : null,
      openNow: read ? read.openNow : null, picker: p, day: res ? res.day : null, slot: res ? res.slot : null,
      stale: fr && !fr.fresh ? { reason: fr.reason, title: REVIEW.title, text: REVIEW[fr.reason] || null } : null,
      ask: ask, confirm: confirm, reviewRef: reviewRef,
    };
  }

  // The store setting applies to the online website; app, kiosk and QR experiences retain their own layouts.
  function websiteSlotDisplay(store) {
    if (OO.mode !== 'online' || (OO.orderingQr && OO.orderingQr.active())) return null;
    return store && store.settings && store.settings.timeSlotDisplay === 'slots' ? 'slots' : 'dropdown';
  }

  // ---- time.card: the order type card --------------------------------------------------------------------------
  function card(st, v) {
    var opts = st.opts, openNow = st.openNow, p = st.picker, stale = st.stale;
    var K = v.K, store = v.store, types = v.types, orderType = v.orderType, isDelivery = v.isDelivery, typeBlocked = v.typeBlocked;
    var day = v.day, slot = v.slot, deliveryFields = v.deliveryFields;
    var individualSlots = websiteSlotDisplay(store) === 'slots';
    var word = isDelivery ? 'delivery' : 'pickup';
    function setType(t) { OO.store.setCheckout({ orderType: t, day: null, slot: null, chosenAt: null }); }
    function setDay(key) {
      var d = byKey(opts.days, 'key', key);
      if (d) st.confirm(d, d.slots[0]);
    }
    function setSlot(value) {
      var s = day ? byKey(day.slots, 'value', value) : null;
      if (s) st.confirm(day, s);
    }
    var times = !typeBlocked && opts.days.length > 0;
    var line = times ? note(p, openNow, store) : null;
    var whenCard = (
      <ui.Card className="oo-checkout__card">
        <div className="oo-checkout__head">
          {isDelivery ? <OO.icons.Pin size={22} /> : <OO.icons.Bag size={22} />}
          {types.length > 1 ? (
            <div className="oo-checkout__seg" role="radiogroup" aria-label="Order type">
              {types.map(function (t) {
                var on = t === orderType;
                return (
                  <label key={t} className={cx('oo-checkout__seg-opt', on && 'is-on')}>
                    <input className="oo-checkout__seg-input" type="radio" name="oo-checkout-type" value={t} checked={on}
                      onChange={function () { setType(t); }} />
                    <span className="oo-checkout__seg-label">{K.TYPE_LABEL[t]}</span>
                  </label>
                );
              })}
            </div>
          ) : <h3 className="oo-checkout__title">{K.TYPE_LABEL[orderType]}</h3>}
          <span className="oo-checkout__aside">{(isDelivery ? 'Delivery time ~' : 'Cooking time ~') + opts.cookingTimeMins + ' mins'}</span>
        </div>
        {typeBlocked ? (
          <ui.InlineNotice tone="warn">This store only delivers, and delivery isn’t available online here yet.</ui.InlineNotice>
        ) : times ? (
          <React.Fragment>
            {stale ? (
              <div ref={st.reviewRef} tabIndex={-1} className="oo-checkout__review">
                <ui.InlineNotice tone="warn" role="alert">
                  <span className="oo-checkout__review-title">{stale.title}</span>
                  {stale.text ? <span className="oo-checkout__review-text">{stale.text}</span> : null}
                </ui.InlineNotice>
              </div>
            ) : null}
            <div className={cx('oo-checkout__pair', individualSlots && 'oo-checkout__pair--single')}>
              <ui.SelectField label="Day" name="day" value={day ? day.key : ''}
                options={opts.list.map(function (d) { var x = day && day.key === d.key ? day : d; return { value: x.key, label: x.text, disabled: !x.pickable }; })} onChange={setDay} />
              {!individualSlots ? <ui.SelectField label="Time" name="time" value={slot ? slot.value : ''}
                options={(day ? day.slots : []).map(function (s) { return { value: s.value, label: s.label }; })} onChange={setSlot} /> : <div className="oo-checkout__slots">
                  <span className="oo-checkout__slots-label">Time</span>
                  <div className="oo-checkout__slots-list"><ui.TimeGrid label="Time" value={slot ? slot.value : null} onChange={setSlot}
                    slots={(day ? day.slots : []).map(function (s) { return { value: s.value, label: s.asap ? 'ASAP · ' + s.label : s.label }; })} /></div>
                </div>}
            </div>
            {stale ? (
              <div className="oo-checkout__review-actions">
                <ui.Button variant="outline" size="md" onClick={function () { st.confirm(day, slot); }}>{REVIEW.button}</ui.Button>
              </div>
            ) : null}
          </React.Fragment>
        ) : (
          <ui.InlineNotice tone="warn">
            {store.online && store.online.active === false ? 'This store isn’t taking online orders right now.'
              : p && !p.offered ? 'This store isn’t taking ' + word + ' orders right now.'
              : 'No ' + word + ' times are available right now. Please check back later.'}
          </ui.InlineNotice>
        )}
        {day && day.lateNight ? <p className="oo-checkout__note"><ui.Badge tone="required">Late night orders available</ui.Badge>{line ? ' ' + line : null}</p>
          : line ? <p className="oo-checkout__note">{line}</p> : null}
        {deliveryFields}
      </ui.Card>
    );
    return whenCard;
  }

  // ---- P4-06: dine-in (see the header) -------------------------------------------------------------------------------
  function tableSlot(store, now) {
    var f = OO.format;
    var r = OO.model._.resolveNow(now, store.timezone);
    var buffer = Math.max(0, Math.round(Number(store.cookingTimeMins) || 0));
    var at = r.instant + buffer * 60000;
    var z = f.zoned(new Date(at), store.timezone);
    return {
      value: 'ASAP', label: f.rangeMins(buffer, buffer + 5), time: f.hm(z.minutes), asap: true, deliveryTime: z.ymd + ' ' + f.hm(z.minutes) + ':00',
      at: at, ymd: z.ymd, owner: r.clock.ymd, dayLabel: null,
    };
  }
  function tableCard(v) {
    var store = v.store;
    var table = v.table || {};
    var mins = Math.max(0, Math.round(Number(store.cookingTimeMins) || 0));
    var guests = Math.floor(Number(table.guests));
    var where = (table.name ? String(table.name).trim() : '') || 'Table ' + table.id;
    if (guests >= 1) where += ' · ' + OO.format.pluralize(guests, 'guest');
    return (
      <ui.Card className="oo-checkout__card oo-checkout__dinein">
        <div className="oo-checkout__head">
          <OO.icons.Pin size={22} />
          <div className="oo-checkout__titles">
            <h3 className="oo-checkout__title">Dine-in</h3>
            <p className="oo-checkout__sub">{where}</p>
          </div>
          <span className="oo-checkout__aside">{'Cooking time ~' + mins + ' mins'}</span>
        </div>
      </ui.Card>
    );
  }

  CS.time = {
    use: use, card: card, picker: picker, resolve: resolve, freshness: freshness, note: note, websiteSlotDisplay: websiteSlotDisplay, REVIEW: REVIEW, FRESH_MS: FRESH_MS,
    tableSlot: tableSlot, tableCard: tableCard, // P4-06
  };
})();


// ===================== src/pages/checkout-address.jsx =====================
// src/pages/checkout-address.jsx: the checkout's delivery address section (Batch 2 P2-00b: moved out of checkout.jsx,
// behaviour unchanged): who gets delivery, the automatic zone and its fee floors, the address field (Places, "Use my
// current location") and the zone text and blocks under it. P3-11 added the saved-address default preselect (use).
// Styles: styles/checkout.css. The Delivery notes below moved here from checkout.jsx's header:
//
// Delivery (bug 5: the customer no longer picks the zone; there is no dropdown). OO.delivery.selectZone (REF
// selectDeliveryZone) chooses it from the driving distance and the item subtotal (summary.subtotal): its fee is the
// delivery fee, shown as text with the "Add $X more for …" incentive (zoneTexts). Below the zone minimum or outside
// every zone, Pay is blocked with that message. A FIXTURE store uses distance 0 (every zone covers it) and a plain
// address field. A REAL store offers delivery only when the boot has a Maps browser key (realDeliveryEnabled; without
// it a draft still on DELIVERY falls back to the first type offered). Then (P1-21; deliveryState folds it together):
//   - the address field gets Places suggestions (OO.delivery.attach, the store's country), plus "Use my current
//     location" (OO.delivery.locate). A pick stores { address: line, zipcode, lat, lng } in the draft. Typing clears
//     zipcode / lat / lng, and Pay is blocked ("Choose your address from the suggestions so we can check delivery.")
//     until a pick: B10 needs the coordinates. Enter on typed text asks for a pick.
//   - the distance is the Distance Matrix's, from the store (its lat / lng, else its address) to the pick: cart.jsx
//     useDeliveryDistance (OO.delivery.distanceKm, memoised for the page's life). While it is measured, Pay waits
//     ("Checking delivery…"). A failure blocks Pay with "We couldn't check the distance to your address. Try again."
//     ("Try again." is the link that asks again). There is no straight-line fallback (REF).
//   - a bad Maps key (gm_authFailure; Google disables the field) re-enables the field and says the address search is
//     unavailable and delivery needs it. A script that didn't load says so, with Try again (attach afresh).
// The address state is { line, zipcode, lat, lng } (addressOf); the payload gets it with the zone (delivery_lat /
// delivery_lng). The draft follows the page: an order type not offered here and the chosen zone id are written back
// (not while a pick is still being measured), so the cart page (cart.jsx reads the draft) prices the same order after
// a checkout visit. The zone is zoneQuote's: selectZone with the address's fee floor (the zone guard's "costs $X"),
// scoped to the zones and item subtotal it was named for, and never turning a covered address "outside".
//
// OO.pages.CheckoutSections.address (checkout-time.jsx creates the kit; see its header):
//   realDeliveryEnabled, deliveryOffered, offeredTypes, distanceKm, selectZone, zoneTexts, deliveryState, feeFloor,
//       setFeeFloor, zoneQuote, addressOf: the pure helpers OO.pages.Checkout.logic re-exports (checkout.jsx's header
//       has their contracts; FEE_FLOORS, the fee floors kept for the page's life, live here).
//   guardText(r, cur) → the zone guard's ecode-202 outcome (OO.delivery.zoneRejection) as copy, null for 'unknown'
//       (checkout.jsx errorState).
//   use({ hotel, store, P, lines, places, askAddress, addressRef, aliveRef }) → { searchS, attachS, locatingS }
//       (hooks): the address search's trouble, the "Try again" counter, "Use my current location" in flight, and the
//       Places autocomplete effect on the field (a real store's delivery only). P3-11: also the saved-address
//       preselect: while delivery is asked (askAddress, a fixture store too: P3-FIX #27; a caller without it: places, a
//       real store's) and the page is ready, a signed-in customer's default saved address (preselect, from P.session's
//       customer.addresses) fills an EMPTY delivery address in the draft, at most once per customer per visit, so an
//       address typed or picked is never replaced and a field the customer cleared stays clear. The Distance Matrix
//       then measures it like a pick.
//   preselect(draft, addresses) → { address, zipcode, lat, lng } | null (P3-11, pure; REF OrderTimePopup): null when
//       the draft already has a line or a point; else the default among the saved addresses WITH coordinates
//       (OO.pages.Addresses.logic.defaultOf(book, { needPoint: true }): the selected one, else the first; a saved
//       address typed without a Places pick has none and is never preselected), null when there is none.
//   fields(st, { draft, cur, askAddress, places, point, dist, quote, ds, checking, errors, shown, submittedS,
//       addressRef, zoneRef, aliveRef }) → the address part of the order type card, null unless askAddress (st =
//       use's; ds = deliveryState's; errors / shown = checkout.jsx validate's).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;
  var CS = (OO.pages.CheckoutSections = OO.pages.CheckoutSections || {});
  var _ = CS._, numOr0 = _.numOr0, centsOf = _.centsOf;

  // ---- delivery: who gets it, and which zone (bug 5; P1-21) -------------------------------------------------------
  // A REAL store offers delivery only when the boot has a Maps browser key (OO.delivery.enabled()): its address comes
  // from Places and is measured with the Distance Matrix, which B10 re-checks. A fixture store keeps its delivery, at
  // distance 0.
  function realDeliveryEnabled() { return !!(OO.delivery && typeof OO.delivery.enabled === 'function' && OO.delivery.enabled()); }
  function deliveryOffered(fixture) { return !!fixture || realDeliveryEnabled(); }
  // The order types this checkout offers: cart.jsx orderTypesOf's list, without DELIVERY where it isn't offered.
  function offeredTypes(types, fixture) {
    return (types || []).filter(function (t) { return t !== 'DELIVERY' || deliveryOffered(fixture); });
  }
  // The driving distance to the address in km: 0 for a fixture store (every zone covers it, so the smallest zone's
  // terms apply); for a real store the measured one (the distance hook), null until it is known.
  function distanceKm(fixture, measured) {
    if (fixture) return 0;
    return typeof measured === 'number' && isFinite(measured) && measured >= 0 ? measured : null;
  }
  // OO.delivery.selectZone (P1-21 moved it there; the contract is in its header), kept here as an alias.
  function selectZone(zones, km, subtotal, opts) { return OO.delivery.selectZone(zones, km, subtotal, opts); }

  // P1-21: what a real store's address part says.
  var ADDRESS_LABEL = 'Delivery address';
  var PICK_ADDRESS = 'Choose your address from the suggestions so we can check delivery.';
  var PICK_HINT = 'Choose your address from the suggestions.';
  var DISTANCE_FAILED = "We couldn't check the distance to your address. Try again.";
  var SEARCH_DOWN = "Address search is unavailable, and we need it to check delivery to your address. Choose pickup, or try again later.";
  var SEARCH_RETRY = "Address search didn't load, and we need it to check delivery to your address. Try again.";
  // A block with a retry ends in "Try again.": the page shows that phrase as the Try again link.
  var TRY_AGAIN = 'Try again.';
  function retryLead(text) {
    var s = String(text || '');
    return s.slice(-TRY_AGAIN.length) === TRY_AGAIN ? s.slice(0, -TRY_AGAIN.length).trim() : s;
  }

  // P1-21: the address part's state and what stops Pay (see the header). v = { real (a real store: Places and the
  // Distance Matrix; false = a fixture at distance 0), point (the draft's picked OO.delivery Point, or null), search
  // (null | { kind: 'auth' | 'unavailable' | 'disabled' | 'noGeometry' | 'locate' }: the address search's trouble),
  // dist ({ status: 'off' | 'pending' | 'ok' | 'error', error }: the distance hook), quote (selectZone's), cur }.
  function deliveryState(v) {
    var out = { block: null, live: false, checking: false, retry: null, searchDown: false };
    var q = v.quote || null;
    if (!v.real) {
      out.block = zoneTexts(q, v.cur).block;
      out.live = !!out.block && !!q && q.status !== 'unknown';
      return out;
    }
    var s = v.search || {}, d = v.dist || {};
    var auth = s.kind === 'auth' || (d.status === 'error' && !!d.error && !!d.error.auth);
    var down = auth || s.kind === 'unavailable' || s.kind === 'disabled';
    out.searchDown = down;
    if (!v.point) {
      // Typed by hand (or nothing yet): B10 needs coordinates, so only a pick can be sent. A search that didn't load
      // can be tried again; a bad key (auth) or no key can't.
      out.retry = down && !auth && s.kind === 'unavailable' ? 'search' : null;
      out.block = !down ? PICK_ADDRESS : out.retry ? SEARCH_RETRY : SEARCH_DOWN;
      out.live = down;
    } else if (d.status === 'pending') {
      out.checking = true;
    } else if (d.status !== 'ok') {
      // The Distance Matrix failed (no haversine fallback: REF), or the store has nothing to measure from ('off').
      out.block = auth ? SEARCH_DOWN : DISTANCE_FAILED;
      out.live = true;
      out.retry = auth ? null : 'distance';
    } else {
      out.block = zoneTexts(q, v.cur).block;
      out.live = !!out.block;
    }
    return out;
  }

  // P1-21: the zone guard's "Delivery to this address costs $X" (its zone is dearer than the one chosen here) makes $X
  // the fee floor for that address (selectZone's minFee) for the page's life, per store and point: the zone is
  // re-selected and the total shown is what the server charges. The cart prices with it too.
  // G1 fix B: the server's answer was about one zone set and one item subtotal (its zone choice depends on the
  // minimums), so a floor set with opts { zones, subtotal } is kept with them: read with opts, a different zone set
  // (the store's zones were edited) DROPS it, and a different item subtotal (in cents) ignores it (0) while it lasts:
  // the server may now choose a cheaper zone, and it accepts an overcharge, so the floor must not outlive what it was
  // about. Without opts both calls behave as before (the cart's feeFloor(hotel, point)).
  var FEE_FLOORS = {}; // key → { fee, zones (zoneSig | null), sub (cents | null) }
  function floorKey(hotel, point) {
    var p = OO.delivery.pointOf(point);
    return p ? String(hotel) + '|' + p.lat + ',' + p.lng : null;
  }
  // The zone set as one string: each zone's id, area, minimum and fee (store.deliveryZones or raw deliveryzones rows),
  // in a stable order. Anything but an array is the empty set.
  function zoneSig(zones) {
    function pick(z, a, b) { var v = z[a] !== undefined && z[a] !== null ? z[a] : z[b]; return numOr0(v); }
    return JSON.stringify((Array.isArray(zones) ? zones : []).map(function (z) {
      var n = z && typeof z === 'object' ? z : {};
      var id = String(n.id === undefined || n.id === null ? '' : n.id);
      return JSON.stringify([id, pick(n, 'radiusKm', 'zone_area'), pick(n, 'minimumOrder', 'minimum_amount'), pick(n, 'fee', 'delivery_fee')]);
    }).sort());
  }
  function feeFloor(hotel, point, opts) {
    var k = floorKey(hotel, point);
    var f = k && Object.prototype.hasOwnProperty.call(FEE_FLOORS, k) ? FEE_FLOORS[k] : null;
    if (!f) return 0;
    if (opts && opts.zones !== undefined && f.zones !== null && zoneSig(opts.zones) !== f.zones) {
      delete FEE_FLOORS[k];
      return 0;
    }
    var sub = opts ? centsOf(opts.subtotal) : null;
    return sub !== null && f.sub !== null && sub !== f.sub ? 0 : f.fee;
  }
  function setFeeFloor(hotel, point, fee, opts) {
    var k = floorKey(hotel, point);
    var n = Number(fee);
    if (!k || !isFinite(n) || !(n > 0)) return;
    var zones = opts && opts.zones !== undefined ? zoneSig(opts.zones) : null;
    var sub = opts ? centsOf(opts.subtotal) : null;
    var f = Object.prototype.hasOwnProperty.call(FEE_FLOORS, k) ? FEE_FLOORS[k] : null;
    // The dearest answer about the same zone set and subtotal stands; an answer about others replaces it.
    FEE_FLOORS[k] = { fee: f && f.zones === zones && f.sub === sub ? Math.max(n, f.fee) : n, zones: zones, sub: sub };
  }
  // G1 fix B: the zone for a picked address as checkout (and, with the patch in its report, the cart) chooses it:
  // selectZone with the address's fee floor for these zones and this item subtotal. The floor can only make the fee
  // dearer, never move an address that a zone covers outside the delivery area: when only the floor does that, it is
  // dropped and the zone is chosen without it.
  function zoneQuote(hotel, point, zones, km, subtotal) {
    var floor = feeFloor(hotel, point, { zones: zones, subtotal: subtotal });
    var q = selectZone(zones, km, subtotal, { minFee: floor });
    if (floor > 0 && q.status === 'outOfZone') {
      var plain = selectZone(zones, km, subtotal);
      if (plain.status !== 'outOfZone') {
        var k = floorKey(hotel, point);
        if (k) delete FEE_FLOORS[k];
        return plain;
      }
    }
    return q;
  }

  // P1-21: the zone guard's ecode-202 outcome (OO.delivery.zoneRejection) as this page's copy; null for 'unknown' (the
  // adapter's own message stands).
  function guardText(r, cur) {
    var money = function (n) { return OO.format.money(n, cur); };
    var outcome = r && r.outcome;
    if (outcome === 'outOfZone') return 'This address is outside our delivery area.';
    if (outcome === 'belowMinimum') {
      return r.shortBy > 0 ? 'Add ' + money(r.shortBy) + ' more to get delivery to this address.' : 'Add more items to get delivery to this address.';
    }
    if (outcome === 'zoneMismatch') return 'Delivery to this address costs ' + money(r.fee) + ' — your total has been updated. Check it and pay again.';
    if (outcome === 'noCoordinates') return PICK_ADDRESS;
    return null;
  }

  // The zone as text (no picker): fee and upgrade are information; block is the message that stops Pay.
  function zoneTexts(q, cur) {
    var money = function (n) { return OO.format.money(n, cur); };
    var out = { fee: null, upgrade: null, block: null };
    if (!q) return out;
    if (q.status === 'ok') {
      out.fee = q.fee > 0 ? 'Delivery fee ' + money(q.fee) : 'Free delivery';
      if (q.upgrade) out.upgrade = 'Add ' + money(q.upgrade.addAmount) + ' more for ' + (q.upgrade.fee > 0 ? money(q.upgrade.fee) + ' delivery.' : 'free delivery.');
    } else if (q.status === 'belowMinimum') {
      out.block = 'Minimum order for delivery is ' + money(q.minimum) + ' (excluding the delivery fee). Add ' + money(q.shortBy) + ' more to continue.';
    } else if (q.status === 'outOfZone') {
      out.block = 'This address is outside our delivery area.';
    } else {
      out.block = "We can't check delivery to this address yet. Please choose another address or pickup.";
    }
    return out;
  }

  function coordOf(v, limit) {
    var n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() ? Number(v) : NaN);
    return isFinite(n) && Math.abs(n) <= limit ? n : null;
  }
  // The delivery address state (see the header): the draft's free-text line plus the Places pick's zipcode / lat / lng.
  function addressOf(draft) {
    var d = draft || {};
    return { line: String(d.address || '').trim(), zipcode: String(d.zipcode || '').trim(), lat: coordOf(d.lat, 90), lng: coordOf(d.lng, 180) };
  }

  // Google disables the address field when it rejects the Maps key (gm_authFailure) and stamps its own placeholder and
  // warning icon on it; React never undoes that (its props didn't change). Undo it, so the customer can still type.
  function reenableField(el) {
    if (!el) return;
    try {
      el.disabled = false;
      el.removeAttribute('disabled');
      if (el.classList) el.classList.remove('gm-err-autocomplete');
      if (el.style) el.style.backgroundImage = '';
      el.setAttribute('placeholder', ADDRESS_LABEL);
    } catch (e) { /* a detached field: nothing to undo */ }
  }
  // P3-11: the saved address that fills an empty delivery address (see the header).
  function preselect(draft, addresses) {
    var d = draft || {};
    if (String(d.address || '').trim() || OO.delivery.pointOf({ lat: d.lat, lng: d.lng })) return null;
    var A = OO.pages.Addresses && OO.pages.Addresses.logic; // addresses.jsx loads after this file: read at call time
    var pick = A ? A.defaultOf(A.book(addresses), { needPoint: true }) : null;
    var p = pick ? A.pointOf(pick) : null;
    return p ? { address: pick.address, zipcode: pick.zipcode || '', lat: p.lat, lng: p.lng } : null;
  }

  // ---- address.use: the address search state and the Places autocomplete (hooks) ------------------------------
  function use(c) {
    var hotel = c.hotel, store = c.store, P = c.P, lines = c.lines, places = c.places, addressRef = c.addressRef, aliveRef = c.aliveRef;
    // P3-FIX #27: the preselect follows "delivery is asked" (the shell's askAddress, a fixture store too); a caller
    // without it keeps P3-11's gate (places: a real store's delivery).
    var asked = c.askAddress === undefined ? !!places : !!c.askAddress;
    var searchS = React.useState(null);     // P1-21: the address search's trouble: null | { kind, text } (deliveryState)
    var attachS = React.useState(0);        // P1-21: bumped by "Try again" after the address search failed to load
    var locatingS = React.useState(false);  // P1-21: "Use my current location" in flight
    // P3-11: the default saved address fills an empty delivery address, once per signed-in customer per visit.
    var seededFor = React.useRef(null);
    var who = P.session && P.session.customer && P.session.customer.uid ? String(P.session.customer.uid) : '';
    React.useEffect(function () {
      if (OO.orderingQr && store && store.orderingQr) { OO.orderingQr.seedDraft(store); return; }
      if (!asked || !P.ready || !who || seededFor.current === who) return;
      seededFor.current = who;
      var s = OO.store.session();
      var cust = s && s.customer && String(s.customer.uid || '') === who ? s.customer : null;
      var patch = cust ? preselect(OO.store.get().checkout, cust.addresses) : null;
      if (patch) OO.store.setCheckout(patch);
    }, [asked, P.ready, who]);
    // P1-21: Places autocomplete on the address field (a real store's delivery only). The field exists once the page
    // renders past its loading states; a store or country change, or "Try again" after a failed load, re-attaches.
    var placesCountry = places ? OO.delivery.country(store) : '';
    var fieldShown = P.ready && lines.length > 0;
    React.useEffect(function () {
      var el = addressRef.current;
      if (OO.orderingQr && OO.orderingQr.locked(store)) return undefined;
      if (!fieldShown || !places || !el) return undefined;
      return OO.delivery.attach(el, {
        country: placesCountry,
        onPlace: function (a) {
          if (!aliveRef.current) return;
          searchS[1](null);
          OO.store.setCheckout({ address: a.line, zipcode: a.postcode, lat: a.lat, lng: a.lng });
        },
        onError: function (err) { if (aliveRef.current) searchFailed(el, err); },
      });
    }, [fieldShown, places, hotel, placesCountry, attachS[0]]);
    // The address search failed (attach's onError): Enter on typed text (noGeometry) only asks for a pick; a bad key
    // (auth) leaves Google's disabled field behind, re-enabled here so the customer can still type.
    function searchFailed(el, err) {
      var e = err || {};
      if (e.code === 'noGeometry') { searchS[1]({ kind: 'noGeometry', text: PICK_HINT }); return; }
      if (e.auth) reenableField(el);
      searchS[1]({ kind: e.auth ? 'auth' : (e.kind === 'disabled' ? 'disabled' : 'unavailable'), text: null });
    }
    return { searchS: searchS, attachS: attachS, locatingS: locatingS, store: store };
  }

  // ---- address.fields: the address field and the zone text --------------------------------------------------------
  function fields(st, v) {
    var searchS = st.searchS, attachS = st.attachS, locatingS = st.locatingS;
    var draft = v.draft, cur = v.cur, askAddress = v.askAddress, places = v.places, point = v.point, dist = v.dist, quote = v.quote;
    var ds = v.ds, checking = v.checking, errors = v.errors, shown = v.shown, submittedS = v.submittedS;
    var addressRef = v.addressRef, zoneRef = v.zoneRef, aliveRef = v.aliveRef;
    // Typing changes the address: a Places pick's zipcode and coordinates no longer describe it, so a real store's
    // delivery waits for a new pick (B10 needs coordinates). A "choose from the list" or location note goes with them.
    function setAddress(v) {
      OO.store.setCheckout({ address: v, zipcode: '', lat: null, lng: null });
      var s = searchS[0];
      if (s && (s.kind === 'noGeometry' || s.kind === 'locate')) searchS[1](null);
    }
    // "Use my current location" (P1-21; OO.delivery.locate): the device's position, reverse-geocoded, is the pick.
    function locateMe() {
      if (locatingS[0]) return;
      locatingS[1](true);
      searchS[1](null);
      OO.delivery.locate().then(function (a) {
        if (!aliveRef.current) return;
        locatingS[1](false);
        OO.store.setCheckout({ address: a.line, zipcode: a.postcode, lat: a.lat, lng: a.lng });
      }, function (err) {
        if (!aliveRef.current) return;
        locatingS[1](false);
        searchS[1]({ kind: 'locate', text: (err && err.message) || "We couldn't find your location. Please type your address." });
      });
    }
    // "Try again" after the address search failed to load: attach afresh (load() retries a failed load).
    function retrySearch() {
      searchS[1](null);
      attachS[1](function (n) { return n + 1; });
    }
    // The zone is text, never a picker (bug 5): its fee and the "add $X more" incentive, or the block that stops Pay
    // (deliveryState: live, or once Pay was pressed). P1-21, a real store: "Checking delivery…" while the distance is
    // measured, Try again when it (or the address search) failed, and the search's own notes.
    var zt = askAddress ? zoneTexts(quote, cur) : null;
    var zoneBlock = !!errors.zone && (ds.live || submittedS[0]);
    var searchNote = places && searchS[0] && searchS[0].text ? searchS[0].text : null;
    var retry = ds && ds.retry === 'distance' ? dist.retry : ds && ds.retry === 'search' ? retrySearch : null;
    var zoneInfo = zt && (zt.fee || zt.upgrade || zoneBlock || checking || searchNote) ? (
      <div ref={zoneRef} className="oo-checkout__zone">
        {checking ? <p className="oo-checkout__hint" role="status">Checking delivery to your address…</p> : null}
        {zt.fee ? <p className="oo-checkout__hint">{zt.fee}</p> : null}
        {zt.upgrade ? <p className="oo-checkout__hint">{zt.upgrade}</p> : null}
        {searchNote ? <p className="oo-checkout__hint" role="status">{searchNote}</p> : null}
        {zoneBlock ? (
          <ui.InlineNotice tone="warn">
            {retry ? (
              <React.Fragment>
                {retryLead(errors.zone)}{' '}<ui.Button variant="link" className="oo-checkout__inlink" onClick={retry}>{TRY_AGAIN}</ui.Button>
              </React.Fragment>
            ) : errors.zone}
          </ui.InlineNotice>
        ) : null}
      </div>
    ) : null;
    // A real store's field: Places suggestions as the customer types (the hint asks for a pick once typed), and the
    // device's location as the other way to pick.
    var addressHint = places && !point && !ds.searchDown && String(draft.address || '').trim() && !searchNote ? PICK_HINT : null;
    var qr = st.store && st.store.orderingQr;
    if (askAddress && qr && qr.address_mode === 'locked') return <div className="oo-checkout__delivery">
      <p><strong>Delivery location</strong></p><p>{draft.address}</p>
      {qr.delivery_address && qr.delivery_address.instructions ? <p>{qr.delivery_address.instructions}</p> : null}
      <p className="oo-checkout__hint">This QR delivers to this location.</p>{zoneInfo}
    </div>;
    var deliveryFields = askAddress ? (
      <div className="oo-checkout__delivery">
        <ui.TextInput label={ADDRESS_LABEL} name="address" autoComplete="street-address" value={draft.address || ''}
          onChange={setAddress} error={shown.address} hint={addressHint} inputRef={addressRef} />
        {places && !ds.searchDown ? (
          <p className="oo-checkout__hint">
            <ui.Button variant="link" busy={locatingS[0]} onClick={locateMe}>Use my current location</ui.Button>
          </p>
        ) : null}
        {zoneInfo}
      </div>
    ) : null;
    return deliveryFields;
  }

  CS.address = {
    realDeliveryEnabled: realDeliveryEnabled,
    deliveryOffered: deliveryOffered,
    offeredTypes: offeredTypes,
    distanceKm: distanceKm,
    selectZone: selectZone,
    zoneTexts: zoneTexts,
    deliveryState: deliveryState,
    feeFloor: feeFloor,
    setFeeFloor: setFeeFloor,
    zoneQuote: zoneQuote,
    addressOf: addressOf,
    guardText: guardText,
    preselect: preselect,
    use: use,
    fields: fields,
  };
})();


// ===================== src/pages/checkout-payment.jsx =====================
// src/pages/checkout-payment.jsx: the checkout's payment method section (Batch 2 P2-00b: moved out of checkout.jsx;
// P2-27: unified payment methods): which methods this page offers (an ONLINE_PAYMENT only with a gateway the payload
// can send), the choices they open into (every counter type and every gateway of the order type, in the server's
// order), which one is chosen, the store the payload is built on, and the payment method card. P4-05 (dine-in, mode
// qr): the table's choices (REF getTableSidePaymentOptions), the configured default or sole choice selected
// automatically (manual picks live in this visit's page state, never in the draft), and the table's card.
// Styles: styles/checkout.css.
// OO.pages.CheckoutSections.payment (checkout-time.jsx creates the kit; see its header):
//   usableMethods(methods, fixture), methodKey(methods, want): the pure helpers OO.pages.Checkout.logic re-exports
//       (checkout.jsx's header has their contracts) · gatewayOk(m, fixture) → bool (a gateway name and a numeric id
//       > 0; a fixture's demo id passes) · methodNote(m, store) → the method's one-line description.
//   choices(methods, orderType, fixture) → [Choice] (P2-27; pure): what the card lists for the order type, from the
//       methods offered (usableMethods of cart.jsx methodsFor: one per key). Each method opens into its options
//       (OO.model Store.paymentMethods): ONLINE_PAYMENT into its gateways, PAY_AT_COUNTER / CASH into their types,
//       each kept when enabled for the order type (its orderTypes; a gateway also needs gatewayOk). A method whose
//       options all drop out keeps its own one (the one the payload would send), so every method offered has a
//       choice. Sorted by position (null last), then the server's order (`order`: the unified payment_methods
//       index), then as given; one per id. P4-05: 'DINEIN' (a table order) lists only what the store enables at
//       tables (REF getTableSidePaymentOptions): a counter type of payment_table_side (DINEIN in its orderTypes) with
//       an id > 0, a gateway whose channels include table side (DINEIN in its orderTypes); a method whose options all
//       drop out offers NOTHING at a table (REF reads the table-side lists as they are), never its own option.
//   pickOf(choices, id[, automatic]) → Choice | null (P4-05; pure): the table's pick while it is offered, else the
//       configured default, else the sole available choice; several choices without a default require a manual pick.
//       automatic=false suppresses automatic selection after a gateway refusal until the customer picks again.
//       Choice = { id ('gateway:{NAME}:{gatewayId}' | 'counter:{typeId}': a gateway is its name + id pair, never the
//       id alone: production keeps WINDCAVE configs under STRIPE's id 4), key (the method's: ONLINE_PAYMENT |
//       PAY_AT_COUNTER | CASH), kind ('gateway' | 'counter'), label, gateway, gatewayId, typeId, position, isDefault,
//       order, own (the method's own option: what the payload sends for the key), brand (brandOf(gateway) | null) }.
//   choiceOf(choices, key, want) → Choice | null: the chosen one of the key (cart.jsx methodOf / methodKey's key, so
//       the cart and checkout price with the same method): `want` (the draft's paymentChoice id) when it is one of
//       the key's, else the method's own option (is_default › first: the payload's default), else the key's first;
//       null when the key has none.
//   payStore(store, choice) → the store to build the payload on: `store` itself unless the choice is a gateway other
//       than ONLINE_PAYMENT's own; then a copy whose ONLINE_PAYMENT entry names that gateway (label, gateway,
//       gatewayId), so payment_gateway / payment_gateway_id / terminal_id are the chosen config's, as before for the
//       default. A gateway the store doesn't list THROWS Error { kind: 'payload', code: 'NO_GATEWAY' } (nothing is
//       sent; never another gateway than the one shown). A counter choice changes nothing on the wire (REF web sends
//       'Pay at Counter' for every counter type).
//   brandOf(gateway) → { key, name, logo } | null: the gateway's brand mark (REF paymentGatewayBranding: STRIPE / STRIPE_V2
//       Stripe, WINDCAVE / WINDCAVE_EFTPOS Windcave, PIN_PAYMENT Pin Payments, AFTERPAY Afterpay, PAYPAL PayPal, CLICK
//       Paymark Click, LINEPAY LINE Pay, PAYRIX Payrix, RAZORPAY Razorpay; the name normalised as REF does: trimmed,
//       [\s-]+ → _, upper-case). logo (W5-FIX #6) = the OO.icons name of REF's logo (src/ui/icons.jsx: BrandStripe,
//       BrandWindcave, BrandPinPayments, BrandAfterpay, BrandPayPal, BrandLinePay, BrandPayrix), or null for Paymark
//       Click (REF's file is too large to embed) and Razorpay (REF has none): those keep the text wordmark.
//   use() → { payOpenS, pointerPick, pickS, autoPickS, missS, tableRef } (hooks): the list's open state, and whether the last pick
//       was a pointer's; P4-05: the table's pick (pickS: a Choice id | null, this visit only), whether Pay asked for one
//       (missS: the card shows "Choose a payment method"), whether automatic selection is allowed (autoPickS),
//       and the table card's element (the shell reveals it).
//   tableCard(st, { store, choices, choice }) → the table's card (P4-05; REF TableSidePaymentChoices): "Payment
//       method" with the pick (or "Choose one"), the choices always listed (a radio each; a gateway "Pay securely
//       online now" with its brand mark and surcharge, a counter type "Pay in person"), the default or sole choice checked
//       automatically; a manual pick sets st.pickS (never the draft) and clears the ask. No choices: "No active table-side
//       payment methods are configured. Please ask a staff member for help." (REF). st.missS: "Choose a payment
//       method" under the list. TABLE_NONE / TABLE_PICK: those two lines.
//   card(st, { store, methods, method, pc[, choices, choice] }) → the card: the chosen method (method | null: "Not
//       available"); with more than one row, its head opens the radio list (a pointer pick closes it; arrow keys keep
//       it open). st = use's. Two modes:
//       - with `choices` (an array; the shell computes choices / choiceOf and builds the payload on payStore(store,
//         choice)): one row per choice, each gateway with its brand mark; the head shows the chosen one; a pick sets
//         the draft's paymentMethod (its key) and paymentChoice (its id).
//       - without: one row per method (the Batch-1 card, unchanged; the payload sends each key's own option); a pick
//         sets paymentMethod.
//       The card never offers a gateway the payload won't send: the choice rows need the shell to pass `choices`
//       only together with payStore (tests/payments-p227.test.mjs pins it on the page).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;
  var CS = (OO.pages.CheckoutSections = OO.pages.CheckoutSections || {});

  // ---- payment methods ------------------------------------------------------------------------------------------
  // P1-12 sends an online order only with the method's gateway name and a numeric gateway id > 0 (a digit string
  // counts); a fixture store's string demo id passes, as payload.jsx allows. Anything else isn't offered at all, so a
  // store with online_payment_enable but no usable gateway shows only its counter / cash methods.
  function gatewayOk(m, fixture) {
    if (!m.gateway || !String(m.gateway).trim()) return false;
    var id = m.gatewayId;
    if (typeof id === 'number') return isFinite(id) && id > 0 && Math.floor(id) === id;
    var s = String(id === undefined || id === null ? '' : id).trim();
    if (/^\d+$/.test(s)) return Number(s) > 0;
    return !!fixture && !!s;
  }
  function usableMethods(methods, fixture) {
    return (methods || []).filter(function (m) { return !!m && (m.key !== 'ONLINE_PAYMENT' || gatewayOk(m, fixture)); });
  }
  // cart.jsx methodOf's choice on that list: the wanted key when offered, else the store's default (isDefault), else
  // Pay Online, else the first; null when nothing is offered.
  function methodKey(methods, want) {
    var list = methods || [];
    function has(k) { return list.some(function (m) { return m.key === k; }); }
    if (want && has(want)) return want;
    var def = list.filter(function (m) { return m.isDefault; })[0];
    if (def) return def.key;
    if (has('ONLINE_PAYMENT')) return 'ONLINE_PAYMENT';
    return list.length ? list[0].key : null;
  }

  function methodNote(m, store) {
    if (m.key === 'ONLINE_PAYMENT') {
      var sc = store.surcharge || {};
      var parts = [];
      if (sc.enabled && sc.amount > 0) parts.push(OO.format.money(sc.amount, store.currency));
      if (sc.enabled && sc.percent > 0) parts.push(sc.percent + '%');
      return 'Pay securely online now' + (parts.length ? ' · ' + parts.join(' + ') + ' card surcharge' : '');
    }
    if (m.key === 'CASH') return 'Pay the driver when your order arrives';
    return 'Pay when you collect your order';
  }

  // ---- choices (P2-27; see the header) ------------------------------------------------------------------------------
  // REF paymentGatewayBranding.ts's gateways: [name, the OO.icons name of REF's logo | null (the text wordmark)].
  var BRANDS = {
    STRIPE: ['Stripe', 'BrandStripe'], STRIPE_V2: ['Stripe', 'BrandStripe'], WINDCAVE: ['Windcave', 'BrandWindcave'],
    WINDCAVE_EFTPOS: ['Windcave', 'BrandWindcave'], PIN_PAYMENT: ['Pin Payments', 'BrandPinPayments'],
    AFTERPAY: ['Afterpay', 'BrandAfterpay'], PAYPAL: ['PayPal', 'BrandPayPal'], CLICK: ['Paymark Click', null],
    LINEPAY: ['LINE Pay', 'BrandLinePay'], PAYRIX: ['Payrix', 'BrandPayrix'], RAZORPAY: ['Razorpay', null],
  };
  var UNORDERED = 1e9; // an option without `order` (a hand-built method): after the ranked ones, as given
  function text(v) { return v === undefined || v === null ? '' : String(v).trim(); }
  function gatewayName(v) { return text(v).replace(/[\s-]+/g, '_').toUpperCase(); }
  function brandOf(gateway) {
    var k = gatewayName(gateway);
    if (!Object.prototype.hasOwnProperty.call(BRANDS, k)) return null;
    return { key: k.toLowerCase().replace(/_/g, '-'), name: BRANDS[k][0], logo: BRANDS[k][1] };
  }
  function numOrNull(v) { return typeof v === 'number' && isFinite(v) ? v : null; }
  function gatewayId(g) { return 'gateway:' + gatewayName(g.gateway) + ':' + text(g.gatewayId); }
  function sameGateway(a, b) {
    return !!a && !!b && gatewayName(a.gateway) === gatewayName(b.gateway) && text(a.gatewayId) === text(b.gatewayId);
  }
  function forType(o, orderType) {
    return !orderType || !Array.isArray(o.orderTypes) || o.orderTypes.indexOf(orderType) >= 0;
  }
  function orderOf(o) { return typeof o.order === 'number' && isFinite(o.order) ? o.order : UNORDERED; }

  function gatewayChoice(m, g) {
    return {
      id: gatewayId(g), key: m.key, kind: 'gateway', label: text(g.label) || text(m.label) || 'Pay Online',
      gateway: text(g.gateway), gatewayId: g.gatewayId, typeId: null, position: numOrNull(g.position),
      isDefault: g.isDefault === true, order: orderOf(g), own: g === m || sameGateway(g, m), brand: brandOf(g.gateway),
    };
  }
  function counterChoice(m, t) {
    var typeId = t.typeId === undefined ? null : t.typeId;
    return {
      id: 'counter:' + (typeId === null ? m.key : text(typeId)), key: m.key, kind: 'counter',
      label: text(t.label) || text(m.label), gateway: null, gatewayId: null, typeId: typeId, position: numOrNull(t.position),
      isDefault: t.isDefault === true, order: orderOf(t), own: t === m || text(t.typeId) === text(m.typeId), brand: null,
    };
  }
  function byRank(list) {
    return list.map(function (c, i) { return { c: c, i: i }; }).sort(function (a, b) {
      var pa = a.c.position, pb = b.c.position;
      if (pa !== pb) {
        if (pa === null || pb === null) return pa === null ? 1 : -1;
        return pa - pb;
      }
      return a.c.order - b.c.order || a.i - b.i;
    }).map(function (x) { return x.c; });
  }
  // P4-05: a counter type REF lists at a table (isTableSideCounterPayment: active, which the model already keeps, and an
  // id > 0).
  function typeIdOk(o) {
    var s = text(o.typeId);
    return /^\d+$/.test(s) && Number(s) > 0;
  }
  function choices(methods, orderType, fixture) {
    var out = [];
    var seen = {};
    var table = orderType === 'DINEIN'; // P4-05: the table-side lists only (see the header)
    function push(c) {
      if (seen[c.id]) return;
      seen[c.id] = true;
      out.push(c);
    }
    (methods || []).forEach(function (m) {
      if (!m || !m.key) return;
      var online = m.key === 'ONLINE_PAYMENT';
      var list = online ? m.gateways : m.types;
      var opts = Array.isArray(list) && list.length ? list : [m];
      var kept = opts.filter(function (o) {
        return !!o && forType(o, orderType) && (online ? gatewayOk(o, fixture) : !table || typeIdOk(o));
      });
      if (!kept.length && !table) kept = [m]; // the method was offered for the order type: its own option stays (see the header)
      kept.forEach(function (o) { push(online ? gatewayChoice(m, o) : counterChoice(m, o)); });
    });
    return byRank(out);
  }
  // Keep an available table pick; otherwise use the configured default, then the sole available choice.
  function pickOf(list, id, automatic) {
    var offered = list || [];
    var w = text(id);
    var picked = w && offered.filter(function (c) { return !!c && c.id === w; })[0];
    if (picked || automatic === false) return picked || null;
    return offered.filter(function (c) { return !!c && c.isDefault; })[0] || (offered.length === 1 ? offered[0] : null);
  }
  function choiceOf(list, key, want) {
    var mine = (list || []).filter(function (c) { return !!c && c.key === key; });
    if (!mine.length) return null;
    var w = text(want);
    return (w && mine.filter(function (c) { return c.id === w; })[0]) || mine.filter(function (c) { return c.own; })[0] || mine[0];
  }
  function payStore(store, choice) {
    if (store && store.orderFees && choice) store = Object.assign({}, store, {
      orderFeePaymentKey: choice.kind === 'gateway' ? OO.pricing.gatewayFeeKey(choice.gateway, choice.gatewayId) : 'type:' + choice.typeId,
      paymentMethods: (store.paymentMethods || []).map(function (m) {
        return m.key === choice.key && choice.kind === 'counter' ? Object.assign({}, m, { typeId: choice.typeId }) : m;
      }),
    });
    if (!store || !choice || choice.kind !== 'gateway' || choice.own) return store;
    var found = false;
    var methods = (store.paymentMethods || []).map(function (m) {
      if (!m || m.key !== 'ONLINE_PAYMENT') return m;
      var g = (m.gateways || []).filter(function (x) { return sameGateway(x, choice); })[0];
      if (!g) return m;
      found = true;
      return Object.assign({}, m, { label: g.label || m.label, gateway: g.gateway, gatewayId: g.gatewayId });
    });
    if (!found) {
      var err = new Error('the chosen payment gateway (' + choice.id + ') is not one of this store\'s');
      err.kind = 'payload';
      err.code = 'NO_GATEWAY';
      throw err;
    }
    return Object.assign({}, store, { paymentMethods: methods });
  }

  // ---- payment.use: the list's state (hooks) -------------------------------------------------------------------
  function use() {
    var payOpenS = React.useState(false);   // payment methods expanded
    var pointerPick = React.useRef(false);
    var pickS = React.useState(null);       // P4-05: the table's pick (a Choice id), this visit only
    var autoPickS = React.useState(true);   // a refused gateway must not be selected again automatically
    var missS = React.useState(false);      // P4-05: Pay asked for a pick ("Choose a payment method")
    var tableRef = React.useRef(null);      // P4-05: the table card (revealed when Pay asks)
    return { payOpenS: payOpenS, pointerPick: pointerPick, pickS: pickS, autoPickS: autoPickS, missS: missS, tableRef: tableRef };
  }

  // ---- payment.card: the payment method card -----------------------------------------------------------------------
  // A gateway's brand mark: REF's logo (W5-FIX #6; OO.icons[brand.logo], decorative) with the brand's name kept for
  // screen readers, else (no logo: Paymark Click, Razorpay) the text wordmark.
  function Mark(props) {
    var b = props.brand;
    if (!b) return null;
    var Logo = b.logo && OO.icons && OO.icons[b.logo] ? OO.icons[b.logo] : null;
    if (!Logo) return <span className={'oo-checkout__paymark oo-checkout__paymark--' + b.key} title={b.name}>{b.name}</span>;
    return (
      <span className={'oo-checkout__paymark oo-checkout__paymark--' + b.key + ' has-logo'} title={b.name}>
        <Logo height={20} /><span className="oo-sr-only">{b.name}</span>
      </span>
    );
  }
  function card(st, v) {
    var payOpenS = st.payOpenS, pointerPick = st.pointerPick;
    var store = v.store, methods = v.methods, method = v.method, pc = v.pc;
    var picking = Array.isArray(v.choices); // P2-27: the choice rows (see the header)
    var rows = picking ? v.choices : methods;
    var chosen = picking ? v.choice || null : null;
    // A pointer pick closes the list; arrow keys change the method and keep it open.
    function picked() {
      if (pointerPick.current) { pointerPick.current = false; payOpenS[1](false); }
    }
    function pickMethod(key) {
      OO.store.setCheckout({ paymentMethod: key });
      picked();
    }
    function pickChoice(c) {
      OO.store.setCheckout({ paymentMethod: c.key, paymentChoice: c.id });
      picked();
    }
    var multi = rows.length > 1;
    var payOpen = multi && payOpenS[0];
    var shown = picking ? (chosen && method ? chosen.label : null) : (method ? method.label : null);
    var payHead = [
      <OO.icons.DollarCircle key="i" size={22} />,
      <span key="t" className="oo-checkout__title oo-checkout__pay-title">Payment method</span>,
      <span key="v" className={cx('oo-checkout__pay-val', !shown && 'is-none')}>{shown || 'Not available'}</span>,
      multi ? <OO.icons.ChevronDown key="c" size={16} className="oo-checkout__chev" /> : null,
    ];
    var payCard = (
      <ui.Card pad="none" className={cx('oo-checkout__pay', payOpen && 'is-open')}>
        {multi ? (
          <button type="button" className="oo-checkout__pay-head" aria-expanded={payOpen ? 'true' : 'false'} aria-controls="oo-checkout-pay-list"
            onClick={function () { payOpenS[1](!payOpenS[0]); }}>
            {payHead}
          </button>
        ) : <div className="oo-checkout__pay-head">{payHead}</div>}
        {payOpen ? (
          <div id="oo-checkout-pay-list" className="oo-checkout__pay-list" role="radiogroup" aria-label="Payment method"
            onPointerUp={function () { pointerPick.current = true; }} onKeyDown={function () { pointerPick.current = false; }}>
            {picking ? rows.map(function (c) {
              return (
                <ui.OptionRow key={c.id} type="radio" name="oo-checkout-pay" value={c.id} label={c.label} desc={methodNote(c, store)}
                  price={c.brand ? <Mark brand={c.brand} /> : null}
                  checked={!!chosen && c.id === chosen.id} onToggle={function () { pickChoice(c); }} />
              );
            }) : methods.map(function (m) {
              return (
                <ui.OptionRow key={m.key} type="radio" name="oo-checkout-pay" value={m.key} label={m.label} desc={methodNote(m, store)}
                  checked={m.key === pc.paymentMethod} onToggle={function () { pickMethod(m.key); }} />
              );
            })}
          </div>
        ) : null}
      </ui.Card>
    );
    return payCard;
  }

  // ---- payment.tableCard: the table's card (P4-05; see the header) ---------------------------------------------------
  var TABLE_NONE = 'No active table-side payment methods are configured. Please ask a staff member for help.';
  var TABLE_PICK = 'Choose a payment method';
  // A choice's line at a table (REF: "Pay Online" / "Pay In Person"): a gateway keeps the online note (its surcharge).
  function tableNote(c, store) { return c.kind === 'gateway' ? methodNote(c, store) : 'Pay in person'; }
  function tableCard(st, v) {
    var store = v.store;
    var list = Array.isArray(v.choices) ? v.choices : [];
    var chosen = v.choice || null;
    var miss = !!st.missS[0] && !chosen && list.length > 0;
    function pick(c) {
      st.pickS[1](c.id);
      st.missS[1](false);
    }
    return (
      <div ref={st.tableRef} tabIndex={-1} className="oo-checkout__paywrap">
        <ui.Card pad="none" className={cx('oo-checkout__pay', 'oo-checkout__pay--table', miss && 'is-invalid')}>
          <div className="oo-checkout__pay-head">
            <OO.icons.DollarCircle size={22} />
            <span className="oo-checkout__title oo-checkout__pay-title">Payment method</span>
            <span className={cx('oo-checkout__pay-val', !chosen && 'is-none')}>{chosen ? chosen.label : list.length ? 'Choose one' : 'Not available'}</span>
          </div>
          {list.length ? (
            <div id="oo-checkout-pay-list" className="oo-checkout__pay-list" role="radiogroup" aria-label="Payment method"
              aria-describedby={miss ? 'oo-checkout-pay-err' : undefined}>
              {list.map(function (c) {
                return (
                  <ui.OptionRow key={c.id} type="radio" name="oo-checkout-pay" value={c.id} label={c.label} desc={tableNote(c, store)}
                    price={c.brand ? <Mark brand={c.brand} /> : null}
                    checked={!!chosen && c.id === chosen.id} onToggle={function () { pick(c); }} />
                );
              })}
            </div>
          ) : (
            <p className="oo-checkout__pay-none" role="status">{TABLE_NONE}</p>
          )}
          {miss ? <p id="oo-checkout-pay-err" className="oo-checkout__pay-err" role="alert">{TABLE_PICK}</p> : null}
        </ui.Card>
      </div>
    );
  }

  CS.payment = {
    gatewayOk: gatewayOk,
    usableMethods: usableMethods,
    methodKey: methodKey,
    methodNote: methodNote,
    choices: choices,
    choiceOf: choiceOf,
    pickOf: pickOf, // P4-05
    payStore: payStore,
    brandOf: brandOf,
    use: use,
    card: card,
    tableCard: tableCard, // P4-05
    TABLE_NONE: TABLE_NONE, // P4-05
    TABLE_PICK: TABLE_PICK, // P4-05
  };
})();


// ===================== src/pages/checkout-offers.jsx =====================
// src/pages/checkout-offers.jsx: the checkout's offers section (Batch 2 P2-00b: moved out of checkout.jsx; P2-14 fills
// it) and the menu's coupon prompt (P2-14). It holds the coupon re-check at Pay, and the offers slot: the D-05 re-check
// of the applied coupon on every change of the cart, the order type, the time and the payment method
// (OO.pages.Cart.shared.useCouponSync, the cart page's own), REF's "Offers" (OnlineOrderOffersSection): a typed promo
// code, "View coupons · N offers available" (the offers sheet), the applied coupon with Remove, the picker a coupon
// waiting for the customer's choice opens; applying goes through Cart.shared.useCouponApply (force_login_for_coupon,
// exclusivity, a guest's contact sheet, validate_coupon_v4, the local gates). Styles: styles/checkout.css (the
// section) and styles/cart.css (the shared coupon pieces and the prompt).
// OO.pages.CheckoutSections.offers (checkout-time.jsx creates the kit; see its header):
//   couponCheck(hotel, coupon, useCoupon, email, when, orderType) → Promise<{ status, message? }>, never rejects: an
//       applied coupon with a code (useCoupon) is checked by OO.api.validateCoupon(hotel, { code, email, time: when,
//       domain: OO.data.domain(orderType) }); the real adapter's refusal (HTTP 200, kind 'client') → { status: false,
//       message }; no coupon, or any other failure → { status: true } (the checkout itself re-validates the coupon).
//       checkout.jsx pay() keeps the rest: useCoupon (coupon && sum.coupon.ok), the stop for a coupon still waiting for
//       the customer's choice (P2-14), the forceLoginForCoupon stop (the login sheet opens) and a refusal's handling
//       (OO.store.setCoupon(null), an Error with couponRejected).
//   card(v) → <OffersSection v /> (P2-14): ONE element of this file's own component, so Checkout's render tree (and
//       the shallow-render tests that read it) gains no button or notice of its own, and the section's hooks run in
//       the section, never in Checkout. Checkout places Coupons between Contact details and Payment method.
//       v = { hotel, store, lines, coupon, sum, pc, orderType, draft, customer }
//       (coupon = the applied one as of now, sum = the summary shown).
// OffersSection: the coupon context (Cart.shared.couponCtx) over the STORED coupon (OO.store's cart.coupon, never
//   v.coupon: liveCoupon's copy), v's order type, payment method, lines and summary, the order's moment
//   (Cart.shared.couponMoment over v.draft: checkout's own choice of day and slot, its instant, and the slot's menu at
//   the cart's pricing — the one menu the picker, its recheck and the D-05 plan judge free items with) and this hotel's
//   table; useCouponSync on it (a removal toasts recheck's message). Coupons switched off
//   (coupon_enabled): no card (the re-check still runs, and removes an applied coupon). Otherwise the "Coupons" card:
//   none applied → (a guest at a force_login_for_coupon store: "Log in to use coupons at {store}.") the typed code
//   (Cart.shared.CodeForm) and, when the store lists coupons this cart can use (offerCount over offerRows),
//   "View coupons · N offers available" → the offers sheet (Cart.shared.OffersSheet, no code field: it is on the card);
//   one applied → its code, "Applied" + "{name} · You save $X" (a "Saving $X" pill in the head), its reason when it
//   doesn't apply, Remove, REF's note for an item coupon (base price only), and "Choose your free item" (PickPrompt)
//   while it waits for a choice; the guest's contact sheet (Cart.shared.ContactSheet) and the picker (PickerHost).
//
// OO.pages.CouponPrompt (P2-14; REF online-order/ui/CouponPrompt.tsx + useCouponPrompt): <OO.pages.CouponPrompt hotel
//   store menu mode blocked /> — the best coupon this cart can use, proposed PROMPT_DELAY_MS (900 ms) after the menu
//   shows it, online mode only (REF's online channel shell; the app has none). Not while blocked (the menu's item
//   sheet / combo builder or About is open, or one of MenuOverlays' own: the store alert, a popup, the member card, the
//   after-add upsell; its onBusy signal, W3-fix #9), the cart is empty, a coupon is applied, wallet points are in
//   use or a reward line is in the cart, coupons are off, or the prompt was dismissed or used this session (per store:
//   sessionStorage 'oo-coupon-prompt-{hotel}', in memory too when storage is blocked). The coupons are only fetched
//   while it may show (useCouponList: no request otherwise). Best = offerRows' applicable ones (eligible, or waiting for
//   a choice) by the saving OO.coupons.discount gives, then their order. Apply goes through useCouponApply (a guest's
//   contact sheet; "Please sign in to use this coupon." with a Log in link; a picker when it waits for a choice) and
//   ends the prompt for the session; the close button dismisses it. Mounted by the menu (pages/menu.jsx, a patch).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;
  var CS = (OO.pages.CheckoutSections = OO.pages.CheckoutSections || {});

  function Kit() { return OO.pages.Cart.shared; }
  function numOr0(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  var ITEM_TYPES = ['ITEM_DISCOUNT', 'BUY_ONE_GET_ONE_FREE', 'BUY_N_GET_M_FREE'];
  function useToast() {
    var s = React.useState(null); // { id, text }
    return {
      say: function (text) { s[1]({ id: Date.now() + Math.random(), text: text }); },
      node: s[0] ? <ui.Toast key={s[0].id} open onClose={function () { s[1](null); }}>{s[0].text}</ui.Toast> : null,
    };
  }
  // The cart's own subtotal (no coupon: free lines never count towards a minimum), as the coupon engine reads it.
  function baseOf(cc) {
    return OO.pricing.summary(cc.lines, cc.store, { orderType: cc.orderType, paymentMethod: cc.paymentMethod, table: cc.table || undefined }).subtotal;
  }

  // ---- the offers section (P2-14; see the header) -------------------------------------------------------------------
  function OffersSection(props) {
    var v = props.v;
    var K = Kit();
    var hk = String(v.hotel);
    var s = OO.useStore(function (st) { return { coupon: st.hotel === hk ? st.cart.coupon : null, table: st.table }; });
    var toast = useToast();
    var pickS = React.useState(false);      // the coupon's picker open
    var sheetS = React.useState(false);     // the offers sheet open
    // The order's moment: the slot the page shows and its menu at the cart's pricing (couponMenu), the fallback the
    // cached menu.
    var cached = v.store ? OO.data.peekHotel(v.hotel, true) : null;
    var moment = v.store ? K.couponMoment(v.hotel, v.store, v.draft, v.orderType, cached ? cached.menu : null) : null;
    var cc = v.store ? K.couponCtx({
      hotel: v.hotel, store: v.store, menu: moment.menu, lines: v.lines, coupon: s.coupon, orderType: v.orderType,
      paymentMethod: v.pc ? v.pc.paymentMethod : null, at: moment.at,
      table: K.tableOf(s.table, v.hotel), session: v.customer ? { customer: v.customer } : null, draft: v.draft, sum: v.sum,
    }) : null;
    var plan = K.useCouponSync(cc, toast.say);
    React.useEffect(function () { if (!plan.pending && pickS[0]) pickS[1](false); }, [plan.pending]);
    var api = K.useCouponApply(cc, toast.say, {
      onApplied: function () { sheetS[1](false); },
      onPending: function () { pickS[1](true); },
    });
    var enabled = !!cc && OO.coupons.enabled(v.store);
    var applied = s.coupon ? (v.coupon || s.coupon) : null; // the summary's (live) copy of the stored coupon
    var q = K.useCouponList(v.store, cc, enabled && !applied);
    var rows = enabled && !applied && !q.loading && !q.error ? K.offerRows(q.list, cc, null, baseOf(cc)) : [];
    var count = K.offerCount(rows);
    var extras = (
      <React.Fragment>
        {sheetS[0] && enabled ? (
          <K.OffersSheet cc={cc} api={api} applied={applied} needLogin={!!(v.store.settings && v.store.settings.forceLoginForCoupon) && !cc.signedIn}
            onRemove={function () { sheetS[1](false); api.remove(); }} onClose={function () { sheetS[1](false); api.clearError(); }} />
        ) : null}
        {api.contact ? (
          <K.ContactSheet cc={cc} code={api.contact.target.code || (api.contact.target.coupon && api.contact.target.coupon.code)}
            busy={!!api.busy} onSubmit={api.submitContact} onClose={api.closeContact} />
        ) : null}
        <K.PickerHost cc={cc} plan={plan} open={pickS[0]} onClose={function () { pickS[1](false); }} say={toast.say} />
        {toast.node}
      </React.Fragment>
    );
    // No card while coupons are off (the re-check above still runs); no wrapper either, so the main column's gap
    // never grows for nothing (the toast portals to <body>).
    if (!enabled) return extras;

    var store = v.store;
    var cur = store.currency;
    var money = function (n) { return OO.format.money(n, cur); };
    var sum = v.sum || { coupon: {} };
    var ok = !!applied && !!sum.coupon.ok;
    var saving = ok ? numOr0(sum.discount) + numOr0(sum.deliveryDiscount) : 0;
    var label = applied ? applied.code || applied.name || 'Coupon' : '';
    var sub = !applied ? null
      : ok ? (applied.name ? applied.name + ' · ' : '') + 'You save ' + money(saving)
      : plan.pending ? applied.name || 'Coupon added'
      : sum.coupon.reason || 'This coupon can’t be used on this order';
    var needLogin = !!(store.settings && store.settings.forceLoginForCoupon) && !cc.signedIn;
    if (v.zeta) return <React.Fragment><section className="zeta-checkout-coupon"><p>Promo code</p><p className="zeta-checkout-coupon-sub">Add a valid Zeta coupon before you confirm.</p>{applied ? <div><strong>{label}</strong><p>{sub}</p><ui.Button variant="link" onClick={api.remove}>Remove</ui.Button></div> : <K.CodeForm hotel={v.hotel} api={api} zeta/>}<K.PickPrompt plan={plan} onChoose={function(){pickS[1](true);}}/></section>{extras}</React.Fragment>;
    return (
      <React.Fragment>
        <ui.Card className="oo-checkout__card oo-checkout__offers">
          <div className="oo-checkout__head">
            <OO.icons.Ticket size={22} />
            <h3 className="oo-checkout__title">Coupons</h3>
            {saving > 0 ? <ui.Badge tone="done" size="sm">{'Saving ' + money(saving)}</ui.Badge> : null}
          </div>
          {applied ? (
            <div className={cx('oo-checkout__coupon', ok ? 'is-ok' : !plan.pending && 'is-warn')}>
              <div className="oo-checkout__coupon-text">
                <div className="oo-checkout__coupon-head">
                  <span className="oo-checkout__coupon-code">{label}</span>
                  {ok ? <ui.Badge tone="done" size="sm">Applied</ui.Badge> : null}
                </div>
                <span className="oo-checkout__coupon-sub">{sub}</span>
              </div>
              <ui.Button variant="link" aria-label={'Remove coupon ' + label} onClick={api.remove}>Remove</ui.Button>
            </div>
          ) : (
            <React.Fragment>
              {needLogin ? <ui.InlineNotice tone="info" icon="User">{'Log in to use coupons at ' + store.name + '.'}</ui.InlineNotice> : null}
              <K.CodeForm api={api} hotel={v.hotel} />
              {count > 0 ? (
                <button type="button" className="oo-checkout__offers-view" aria-haspopup="dialog" onClick={function () { api.clearError(); sheetS[1](true); }}>
                  <span className="oo-checkout__offers-view-text">
                    <span className="oo-checkout__offers-view-title">View coupons</span>
                    <span className="oo-checkout__offers-view-sub">{count + (count === 1 ? ' offer' : ' offers') + ' available'}</span>
                  </span>
                  <OO.icons.ChevronRight size={16} />
                </button>
              ) : null}
            </React.Fragment>
          )}
          {applied && ITEM_TYPES.indexOf(applied.type) >= 0 ? (
            <p className="oo-checkout__note">This discount applies to the item’s base price only — options and combos aren’t discounted.</p>
          ) : null}
          <K.PickPrompt plan={plan} onChoose={function () { pickS[1](true); }} />
        </ui.Card>
        {extras}
      </React.Fragment>
    );
  }

  // Exposed coupons lead the menu as "Deals", one card per swipe. The same list normalizer as checkout
  // filters expired coupons and applies the selected order time and channel.
  OO.pages.MenuCouponBanner = function MenuCouponBanner(props) {
    var K = Kit();
    var store = props.store;
    var moment = props.moment;
    var enabled = (props.mode || OO.mode) === 'online' && !!store && OO.coupons.enabled(store);
    var cc = moment ? { at: moment.now, orderType: moment.orderType, table: null } : null;
    var q = K.useCouponList(store, cc, enabled);
    var orderType = cc ? cc.orderType : 'PICKUP';
    var coupons = q.list.filter(function (c) {
      return c.raw && Number(c.raw.is_exposed) === 1 && c.available !== false &&
        (c.appliesTo === 'ALL' || c.appliesTo === orderType);
    });
    var trackRef = React.useRef(null);
    var indexS = React.useState(0);
    var trackId = 'oo-menu-deals-' + React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var couponKey = String(store && store.id) + '|' + orderType + '|' + coupons.map(function (c) { return c.id; }).join(',');
    var index = Math.min(indexS[0], Math.max(0, coupons.length - 1));
    React.useEffect(function () {
      indexS[1](0);
      if (trackRef.current) trackRef.current.scrollLeft = 0;
    }, [couponKey]);
    function goTo(next) {
      var track = trackRef.current;
      next = Math.max(0, Math.min(next, coupons.length - 1));
      if (!track || !track.children[next]) return;
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: track.children[next].offsetLeft - track.children[0].offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
    }
    function onScroll() {
      var track = trackRef.current;
      if (!track || track.children.length < 2) return;
      var step = track.children[1].offsetLeft - track.children[0].offsetLeft;
      if (step > 0) indexS[1](Math.max(0, Math.min(coupons.length - 1, Math.round(track.scrollLeft / step))));
    }
    function onKeyDown(event) {
      var next;
      if (event.key === 'ArrowLeft') next = index - 1;
      else if (event.key === 'ArrowRight') next = index + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = coupons.length - 1;
      else return;
      event.preventDefault();
      goTo(next);
    }
    if (!enabled || q.loading || q.error || !coupons.length) return null;
    return (
      <section className="oo-menu-coupons" aria-label="Deals">
        <ui.SectionTitle>Deals</ui.SectionTitle>
        <div id={trackId} ref={trackRef} className="oo-menu-coupons__track" role="region" aria-roledescription="carousel"
          aria-label="Available coupons" tabIndex={coupons.length > 1 ? 0 : undefined} onScroll={onScroll} onKeyDown={onKeyDown}>
        {coupons.map(function (c, i) {
          var words = K.couponWords(c, store.currency);
          return <div key={c.id} className="oo-menu-coupons__offer" role="group" aria-roledescription="slide" aria-label={(i + 1) + ' of ' + coupons.length}>
            <OO.icons.Ticket size={22} />
            <div className="oo-menu-coupons__text">
              <strong>{c.name || words.offer}</strong>
              {c.name ? <span>{words.offer}</span> : null}
              {c.minOrder > 0 ? <span>{'Minimum order ' + OO.format.money(c.minOrder, store.currency)}</span> : null}
              <span>Apply at checkout</span>
            </div>
            {c.code ? <span className="oo-coupons__code">{c.code}</span> : null}
          </div>;
        })}
        </div>
        {coupons.length > 1 ? <div className="oo-menu-coupons__controls">
          <button type="button" aria-label="Previous deal" aria-controls={trackId} disabled={index === 0} onClick={function () { goTo(index - 1); }}><OO.icons.ChevronLeft size={18} /></button>
          <span className="oo-menu-coupons__position" aria-live="polite" aria-atomic="true">{(index + 1) + ' / ' + coupons.length}</span>
          <button type="button" aria-label="Next deal" aria-controls={trackId} disabled={index === coupons.length - 1} onClick={function () { goTo(index + 1); }}><OO.icons.ChevronRight size={18} /></button>
        </div> : null}
      </section>
    );
  };

  // ---- the menu's coupon prompt (P2-14; see the header) ---------------------------------------------------------
  var PROMPT_DELAY_MS = 900;
  var seenMem = {}; // hotel → 'dismissed' | 'applied': this page load's answers (storage may be blocked)
  function promptKey(hotel) { return 'oo-coupon-prompt-' + hotel; }
  function promptSeen(hotel) {
    if (seenMem[hotel]) return true;
    try { return !!(window.sessionStorage && window.sessionStorage.getItem(promptKey(hotel))); } catch (e) { return false; }
  }
  function markSeen(hotel, how) {
    seenMem[hotel] = how;
    try { if (window.sessionStorage) window.sessionStorage.setItem(promptKey(hotel), how); } catch (e) { /* in memory only */ }
  }
  // The best coupon to propose: offerRows' applicable ones by the saving they give now (a choice still to make gives 0).
  function bestOf(list, cc, fee) {
    var base = baseOf(cc);
    var best = null;
    Kit().offerRows(list, cc, null, base).forEach(function (r) {
      if (!r.canApply) return;
      var d = OO.coupons.discount(cc.lines, r.coupon, {
        orderType: cc.orderType, deliveryFee: fee, currency: cc.store.currency, paymentMethod: cc.paymentMethod, store: cc.store, subtotal: base,
      });
      var saving = numOr0(d.amount) + numOr0(d.deliveryDiscount);
      if (!best || saving > best.saving) best = { coupon: r.coupon, saving: saving };
    });
    return best;
  }

  OO.pages.CouponPrompt = function CouponPrompt(props) {
    var K = Kit();
    var hotel = String(props.hotel);
    var store = props.store || null;
    var online = (props.mode || OO.mode) === 'online';
    var s = OO.useStore(function (st) {
      return { hotel: st.hotel, lines: st.cart.lines, coupon: st.cart.coupon, draft: st.checkout, session: st.session, table: st.table };
    });
    var toast = useToast();
    var openS = React.useState(false);      // shown (after the delay)
    var doneS = React.useState(false);      // dismissed or applied on this page
    var pickS = React.useState(false);      // the coupon's picker open
    var mine = !!store && s.hotel === hotel;
    // The coupon context (the order's slot, its menu, the summary) only while the prompt may show, or its picker is open:
    // the menu re-renders often.
    var pre = online && mine && OO.coupons.enabled(store) && s.lines.length > 0 && !s.coupon && !doneS[0] && !promptSeen(hotel);
    var need = pre || (mine && pickS[0]);
    // A browse menu may omit hidden redemption products. The prompt uses the same full catalogue as checkout.
    var full = OO.useHotel(need ? hotel : null);
    need = need && !!full.menu && !full.loading && !full.error;
    var table = need ? K.tableOf(s.table, hotel) : null;
    var pc = need ? K.pricingCtx(store, s.draft) : null;
    var moment = need ? K.couponMoment(hotel, store, s.draft, pc.orderType, full.menu) : null;
    var cc = need ? K.couponCtx({
      hotel: hotel, store: store, menu: moment.menu, lines: s.lines, coupon: s.coupon, orderType: pc.orderType,
      paymentMethod: pc.paymentMethod, at: moment.at, table: table, session: s.session, draft: s.draft,
      sum: K.summaryOf(s.lines, store, null, pc, table),
    }) : null;
    var may = pre && !!cc && cc.points <= 0 && cc.rewards === 0;
    var q = K.useCouponList(store, cc, may);
    var best = may && !q.loading && !q.error ? bestOf(q.list, cc, pc.deliveryFee) : null;
    var show = !!best && !props.blocked;
    var bestId = best ? best.coupon.id : null;
    React.useEffect(function () {
      if (!show) { openS[1](false); return undefined; }
      var t = setTimeout(function () { openS[1](true); }, PROMPT_DELAY_MS);
      return function () { clearTimeout(t); };
    }, [show, bestId]);
    var api = K.useCouponApply(cc, toast.say, {
      onApplied: function () { markSeen(hotel, 'applied'); doneS[1](true); openS[1](false); },
      onPending: function () { pickS[1](true); },
    });
    // The applied coupon's picker (a coupon that waits for a choice), after the prompt has gone.
    var plan = cc && s.coupon && pickS[0] ? K.couponPlan(cc) : null;
    function dismiss() { markSeen(hotel, 'dismissed'); doneS[1](true); openS[1](false); api.clearError(); }

    var card = null;
    if (openS[0] && show) {
      var c = best.coupon;
      var words = K.couponWords(c, store.currency);
      var err = api.error && api.error.id === c.id ? api.error : null;
      var login = err && err.login ? OO.router.link('profile', null, { hotel: hotel, query: { directLogin: 'true' } }) : null;
      card = (
        <div className="oo-coupon-prompt" role="status" aria-live="polite">
          <span className="oo-coupon-prompt__icon"><OO.icons.Ticket size={20} /></span>
          <div className="oo-coupon-prompt__body">
            <div className="oo-coupon-prompt__head">
              <div className="oo-coupon-prompt__titles">
                <p className="oo-coupon-prompt__name">{c.name || 'Coupon available'}</p>
                <p className="oo-coupon-prompt__offer">{words.offer}</p>
              </div>
              <ui.IconButton variant="ghost" icon="Close" label="Dismiss coupon" onClick={dismiss} />
            </div>
            {err ? (
              <p className="oo-coupon-prompt__err" role="alert">
                {err.text}
                {login ? <React.Fragment>{' '}<a className="oo-coupon-prompt__login" href={login.href} onClick={login.onClick}>Log in</a></React.Fragment> : null}
              </p>
            ) : null}
            <div className="oo-coupon-prompt__foot">
              {c.code ? <span className="oo-coupons__code">{c.code}</span> : null}
              <ui.Button size="sm" busy={api.busy === c.id} disabled={!!api.busy} onClick={function () { api.apply(c); }}>Apply</ui.Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <React.Fragment>
        {card}
        {api.contact ? (
          <K.ContactSheet cc={cc} code={api.contact.target.coupon && api.contact.target.coupon.code}
            busy={!!api.busy} onSubmit={api.submitContact} onClose={api.closeContact} />
        ) : null}
        {plan ? <K.PickerHost cc={cc} plan={plan} open={pickS[0]} onClose={function () { pickS[1](false); }} say={toast.say} /> : null}
        {toast.node}
      </React.Fragment>
    );
  };

  CS.offers = {
    couponCheck: function (hotel, coupon, useCoupon, email, when, orderType) {
      // The coupon is checked by the server for this email and time; a network failure doesn't block the order (the
      // checkout itself re-validates the coupon).
      var couponCheck = useCoupon && coupon.code
        ? OO.api.validateCoupon(hotel, { code: coupon.code, email: email, time: when, domain: OO.data.domain(orderType) }).catch(function (e) {
          // The real adapter REJECTS a refusal (HTTP 200, kind 'client', a sanitised message); any other failure doesn't block.
          return e && e.kind === 'client' && e.status === 200 ? { status: false, message: e.message } : { status: true };
        })
        : Promise.resolve({ status: true });
      return couponCheck;
    },
    card: function (v) {
      return <OffersSection v={v} />;
    },
  };
})();


// ===================== src/pages/checkout-wallet.jsx =====================
// src/pages/checkout-wallet.jsx: the checkout's wallet section (Batch 2 P2-00b: moved out of checkout.jsx; P2-07: the
// wallet UI). It holds the store-scoped customer_id from the wallet call, and the wallet slot: the points slider
// (wallet type 1), the reward lines and the rewards list (wallet type 2), on the wallet engine (src/lib/wallet.jsx,
// OO.wallet) and the membership lane's stacking rules (OO.membership.exclusivity, called through the engine). Styles:
// styles/checkout.css.
// REF = the Next app, develop: OS = src/online-order/pages/checkout/OnlineOrderOffersSection.tsx (WalletRedeemBlock and
// the wallet notice), CC = src/shared/cart/CartContext.tsx (the slider state, the reward lines' auto-removal), RP =
// src/online-order/pages/rewards/OnlineOrderRewardsPage.tsx (the rewards list REF shows on its Rewards page: here it is
// on the checkout, where P2 adds reward lines).
// OO.pages.CheckoutSections.wallet (checkout-time.jsx creates the kit; see its header):
//   use({ hotel, P }) → { walletRef, sessionUid, walletCustomerId, preflight } (hooks): walletCustomerId() →
//       Promise<customer_id | null>: OO.api.getLoyalty → get_customer_wallet_points (store-scoped), fetched once per
//       customer, prefetched once the page is ready; a failure is null. sessionUid = the signed-in customer's uid | null.
//       checkout.jsx sets walletRef.current = { uid: null, p: null, fresh: true } when a refusal re-reads the wallet,
//       so that read is made again past the 60 s cache.
//       preflight({ rewards }) → Promise<Preflight> (P2-08; REF refreshCheckoutWalletPayload, never rejects): what can
//       move under the order, read again at Pay for a signed-in customer (REF: any signed-in customer's order). A fresh
//       get_customer_wallet_points (OO.api.getWallet { fresh: true }) or, when the cart has reward lines (rewards
//       true), OO.api.getLoyalty { fresh: true } (the wallet, then get_user_loyalty_products_v4). It never touches
//       OO.store's settled read: a failed read there would wipe the page's wallet; the page re-reads it
//       (OO.data.loadWallet) only after a refusal. Preflight = { checked (false for a guest: nothing read),
//       freshWallet (the read's body when usable, else null: a failed read, a { status: false } body, the module
//       off), freshRewards (with rewards only: get_user_loyalty_products_v4's body, null when that read failed),
//       customerId (the fresh read's customer_id, else walletCustomerId()'s, else null) }. checkout.jsx hands
//       freshWallet / freshRewards to the payload (payloadCtx): the wallet and membership engines check the order
//       against them and refuse what moved (null: the read failed → they refuse what needs it).
//   card(st, v) → <WalletSection st v /> (P2-07): ONE element of this file's own component (as the offers slot's), so
//       Checkout's render tree gains no button of its own and the section's hooks run in the section. Placed right
//       after the offers slot. v = the offers slot's v ({ hotel, store, lines, coupon (the applied one as of the
//       order's time), sum, pc, orderType, draft, customer }) plus { menu (the page's menu), table, moment: { at (ms:
//       the instant the order is for), menu (the slot's menu at the cart's pricing: Cart.shared.couponMenu over the
//       page's slotCheck menu), today (ms: now) } }. Without a store: null.
//   Pure (tests/wallet-p207.test.mjs):
//   pointsView(lines, store, opts) → PointsView: what the points block shows (opts = OO.wallet.ctxOf's: { hotel,
//       orderType, table, coupon }) = { show: 'slider' | 'notice' | null, slider (OO.wallet.slider at the points
//       chosen), redemption (OO.wallet.inUse: what the order takes, null for none), applied (bool), requested (the
//       points chosen, OO.store.walletPoints), selected (min(requested, slider.max); 0 while off), balance, ratio,
//       min, max, belowMin (0 < selected < min: chosen, not applied: REF), warning (the engine's "Using wallet points
//       will remove your coupon…" while a coupon is on and no points apply; REF), notice (the copy of a slider that
//       is off for a reason the customer can act on, below) }. null show: signed out or the wallet not read (nothing
//       to show), no points at all, or a store that takes points as rewards (product level: the rewards instead).
//       REF walletNoticeVisible: the daily cap reached (usage text + "You can still place this order without
//       points."), or points but not on this order (no row for the order type, a row maximum of 0, no item the
//       points can pay for, fewer points than the row's minimum, a reward line): the slider's own text.
//   rewardItems(raw, menu, lines, ctx, moment) → [RewardItem] | null: the rewards list (REF RP:243-451), from
//       get_user_loyalty_products_v4's body (OO.wallet.rewardRows; null for a failed read). Each row is the menu
//       product of its id (rows the menu doesn't have, combos and duplicates are left out), judged with D-07
//       (OO.model.displayState at moment = { now (ms), today, timeZone, orderType, domain }): hidden ones are left
//       out, greyed ones listed with their label. RewardItem = { id, product, row, name, image, points (what it
//       costs at the order's row: OO.wallet.rewardPoints, else the row's points_required), addable, reason (why it
//       can't be added: the D-07 label, or OO.wallet.addReward's refusal: 'This reward needs 40 more points.', the
//       per-order maximum, …), code (why, as a code: null when addable · 'GREY' (D-07) · addReward's refusal code:
//       SHORT, OVER_MAX, LIMIT, UNAVAILABLE, …), options (P3-17: a size or options to choose: the item sheet's
//       reward mode adds it, OO.pages.ItemSheet `reward`), from (P3-17: its sizes cost different points; points is
//       the cheapest's) }. A product with options is judged at its cheapest way to redeem: each available size (or
//       none) with no options chosen (options never change the points: a reward's points are its product price at
//       its size; a size priced 0 only when no other has a price), through addReward; no available size, or a
//       required choice none of whose options is available (OO.modifiers.view's blocked), is UNAVAILABLE ('This
//       reward isn't available right now.'). Addable first, then the rest, each in the list's order. ctx =
//       OO.wallet.ctxOf's (+ the stored coupon).
// WalletSection, per store (OO.wallet.productLevel):
//   Cart level (type 1; REF OS WalletRedeemBlock): "Wallet points" with pointsView. The slider (0 … max, step 1)
//     chooses points, through OO.wallet.applyPoints (the stacking rules: a reward line refuses; a coupon is removed
//     with exclusivity's notice, on the first choice only, as the coupon is then gone). A choice below the row's
//     minimum is kept as chosen but not applied ("Use at least N points.") and, as REF's choosePoints, removes the
//     coupon all the same (OO.membership.exclusivity, apply 'points'): the slider can be dragged up through it. "Use
//     max" chooses slider.max; "Remove" (once points apply) forgets the choice (OO.store.setWalletPoints(0)). The
//     choice lives in OO.store (memory only): the cart's and the checkout's summaries take OO.wallet.inUse's amount off
//     (Cart.shared.summaryOf), and the payload sends the same points.
//   Product level (type 2): "Rewards": the balance, the reward lines in the cart (P3-17: their size and options as
//     OO.cart.displayMods words them and their note; their points at the order's row; Remove) and the rewards list
//     (OO.api.getLoyalty → products, read while signed in with a wallet): Add → OO.wallet.addReward (its gates, then
//     the stacking rules: a coupon and the points are removed with the notice) → OO.store.cartAdd(the reward line) and
//     a toast. A reward with a size or options (RewardItem.options; "From N points" when its sizes differ) opens the
//     item sheet in reward mode instead (P3-17: <OO.pages.ItemSheet reward={{ row, orderType, table }}> over the list's
//     own menu and moment), which makes the same checks and adds the line; its toast is the same. A rewards budget that
//     is off for a reason the customer can act on shows its text (the daily cap, no row for the order type).
//   Both: REF CC's auto-removal of reward lines the order can no longer take (OO.wallet.staleRewards: signed out, the
//     daily cap, a cart-level store): they are removed with its text as a toast, once per set of lines.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;
  var CS = (OO.pages.CheckoutSections = OO.pages.CheckoutSections || {});

  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function r2(n) { return OO.format.round2(n); }
  function wordOf(store) { return (store && store.loyalty && store.loyalty.pointsWord) || 'points'; }

  var LIMIT_MORE = 'You can still place this order without points.';
  // Reasons the points block explains (REF walletNoticeVisible); the others show nothing.
  var NOTICE_REASONS = ['NO_RULE', 'NO_MAX', 'NO_ITEMS', 'BELOW_MIN', 'REWARDS'];
  // Reasons the rewards block explains; NO_WALLET (signed out / not read) and CART_LEVEL show nothing.
  var BUDGET_NOTICE = ['LIMIT', 'NO_RULE', 'NO_MAX', 'KIOSK'];

  // ---- wallet.use: the wallet customer_id (hooks) ------------------------------------------------------------------
  function use(c) {
    var hotel = c.hotel, P = c.P;
    // The store-scoped customer_id from the wallet call (see the header): fetched once per customer, awaited at Pay.
    // A rejection's wallet refresh flag drops it with fresh: true, so the next Pay re-reads it past the 60 s cache.
    var walletRef = React.useRef({ uid: null, p: null, fresh: false });
    var sessionUid = P.session && P.session.customer ? P.session.customer.uid : null;
    function walletCustomerId() {
      if (!sessionUid) return Promise.resolve(null);
      var w = walletRef.current;
      if (w.uid !== sessionUid || !w.p) {
        var q = w.fresh ? { uid: sessionUid, fresh: true } : { uid: sessionUid };
        w = walletRef.current = {
          uid: sessionUid,
          fresh: false,
          p: OO.api.getLoyalty(hotel, q).then(function (raw) {
            return OO.model.normalizeLoyalty(raw, {}).customerId || null;
          }, function () { return null; }),
        };
      }
      return w.p;
    }
    React.useEffect(function () { if (P.ready && sessionUid) walletCustomerId(); }, [P.ready, sessionUid]);
    // P2-08: the pre-flight read at Pay (see the header).
    function preflight(o) {
      if (!sessionUid) return Promise.resolve({ checked: false, customerId: null });
      var rewards = !!(o && o.rewards);
      var q = { uid: sessionUid, fresh: true };
      var read = rewards
        ? Promise.resolve().then(function () { return OO.api.getLoyalty(hotel, q); }).then(function (r) { return { wallet: r ? r.wallet : null, products: r ? r.products : null }; })
        : Promise.resolve().then(function () {
          return typeof OO.api.getWallet === 'function' ? OO.api.getWallet(hotel, q) : OO.api.getLoyalty(hotel, q).then(function (r) { return r ? r.wallet : null; });
        }).then(function (w) { return { wallet: w, products: null }; });
      return read.then(null, function () { return { wallet: null, products: null }; }).then(function (r) {
        var fresh = OO.wallet.usable(r.wallet);
        var out = { checked: true, freshWallet: fresh, customerId: fresh && fresh.customer_id ? String(fresh.customer_id).trim() || null : null };
        if (rewards) out.freshRewards = r.products === undefined ? null : r.products;
        if (out.customerId) return out;
        return walletCustomerId().then(function (id) { out.customerId = id || null; return out; }, function () { return out; });
      });
    }
    return { walletRef: walletRef, sessionUid: sessionUid, walletCustomerId: walletCustomerId, preflight: preflight };
  }

  // ---- the points (type 1; see the header) ---------------------------------------------------------------------------
  function pointsView(lines, store, opts) {
    var W = OO.wallet;
    var ctx = W.ctxOf(store, opts);
    var s = W.slider(lines, ctx);
    var r = W.inUse(lines, store, opts);
    var out = {
      show: null, slider: s, redemption: r, applied: !!r, requested: ctx.walletPoints, selected: 0, balance: s.balance,
      ratio: s.rule ? s.rule.ratio : 0, min: s.min, max: s.max, belowMin: false, warning: null, notice: null,
    };
    if (!W.usable(ctx.wallet) || W.productLevel(store)) return out;
    if (s.enabled) {
      out.show = 'slider';
      // With a coupon on the order no points apply (the two never combine): the slider reads 0 and warns.
      var free = ctx.coupon ? W.slider(lines, Object.assign({}, ctx, { walletPoints: 0 })) : s;
      out.selected = ctx.coupon ? 0 : Math.min(ctx.walletPoints, s.max);
      out.belowMin = out.selected > 0 && out.selected < s.min;
      out.warning = !r && free.warning ? free.warning.text : null;
      return out;
    }
    if (s.reason === 'LIMIT') {
      out.show = 'notice';
      out.notice = s.text + ' ' + LIMIT_MORE;
    } else if (s.balance >= 1 && NOTICE_REASONS.indexOf(s.reason) >= 0) {
      out.show = 'notice';
      out.notice = s.text;
    }
    return out;
  }

  // ---- the rewards (type 2; see the header) --------------------------------------------------------------------------
  function hasOptions(product) {
    if (arr(product && product.sizes).length) return true;
    return !!(OO.modifiers && OO.modifiers.visibleGroups(product, null).length);
  }
  // P3-17: the cheapest way to redeem a product with options (see rewardItems in the header) → { x (addReward's result),
  // points, from } | null (nothing can be chosen: no available size, or a required choice with no available option).
  function cheapest(p, row, lines, ctx, rule) {
    var W = OO.wallet, M = OO.modifiers;
    var all = arr(p.sizes);
    var open = all.filter(function (z) { return z.available !== false; });
    if (all.length && !open.length) return null;
    var picks = (open.length ? open : [null]).map(function (z) {
      var sizeId = z ? z.id : null;
      return { sizeId: sizeId, points: rule ? W.rewardPoints(OO.cart.rewardLine(p, { sizeId: sizeId, sel: {} }, { points: 0 }), rule) : 0 };
    });
    var priced = picks.filter(function (x) { return x.points > 0; });
    var low = (priced.length ? priced : picks).slice().sort(function (a, b) { return a.points - b.points; })[0];
    var v = M && typeof M.view === 'function' ? M.view(p, { sizeId: low.sizeId, sel: {}, qty: 1 }) : null;
    if (v && arr(v.blocked).length) return null;
    return {
      x: W.addReward(p, { sizeId: low.sizeId, sel: {} }, row, lines, ctx), points: low.points,
      from: picks.some(function (x) { return x.points !== low.points; }),
    };
  }
  function rewardItems(raw, menu, lines, ctx, moment) {
    var W = OO.wallet;
    var rows = W.rewardRows(raw);
    if (!rows) return null;
    menu = OO.model.redemptionMenu(menu);
    var products = (menu && menu.products) || {};
    var budget = W.rewardBudget(lines, ctx);
    var seen = {};
    var ready = [], rest = [];
    rows.forEach(function (row) {
      if (seen[row.id]) return;
      seen[row.id] = true;
      var p = own(products, row.id) ? products[row.id] : null;
      if (!p || p.kind === 'combo') return; // not a product of this menu
      var d = OO.model.displayState(menu, p, moment || {}) || { show: 'normal', label: null };
      if (d.show === 'hide') return;
      var cost = budget.rule ? W.rewardPoints(OO.cart.rewardLine(p, {}, { points: 0 }), budget.rule) : 0;
      var item = {
        id: row.id, product: p, row: row, name: p.name, image: p.image || null, points: cost > 0 ? cost : row.points, addable: false,
        reason: null, code: null, options: false, from: false,
      };
      if (d.show === 'grey') { item.reason = d.label || 'Not available'; item.code = 'GREY'; } else {
        // P3-17: a size or options to choose → the item sheet's reward mode; judged at its cheapest way to redeem.
        var opt = hasOptions(p);
        var c = opt ? cheapest(p, row, lines, ctx, budget.rule) : null;
        var x = opt ? (c ? c.x : null) : W.addReward(p, { sizeId: null, sel: {} }, row, lines, ctx);
        item.options = opt;
        if (c) { item.from = c.from; if (c.points > 0) item.points = c.points; }
        if (!x) { item.reason = W.TEXT.UNAVAILABLE; item.code = 'UNAVAILABLE'; }
        else if (x.ok) { item.addable = true; item.points = x.points; }
        else { item.reason = x.refuse ? x.refuse.text : 'Not available'; item.code = x.refuse ? x.refuse.code : null; }
      }
      (item.addable ? ready : rest).push(item);
    });
    return ready.concat(rest);
  }

  // What choosing points removes, by the membership lane's stacking rules (OO.membership.exclusivity, apply 'points'),
  // for a choice applyPoints doesn't apply (below the row's minimum): REF's choosePoints removes the coupon all the same.
  function pointsStack(c, lines) {
    var M = OO.membership;
    if (!M || typeof M.exclusivity !== 'function') return { drop: [], notice: null };
    return M.exclusivity({
      coupon: c.coupon, member: typeof M.of === 'function' ? M.of(OO.wallet.usable(c.wallet)) : null, points: c.walletPoints,
      rewards: arr(lines).filter(OO.wallet.isReward).length, signedIn: c.signedIn, kiosk: false, apply: 'points',
    });
  }

  // ---- the section --------------------------------------------------------------------------------------------------
  function useToast() {
    var s = React.useState(null); // { id, text }
    return {
      say: function (text) { if (text) s[1]({ id: Date.now() + Math.random(), text: text }); },
      node: s[0] ? <ui.Toast key={s[0].id} open onClose={function () { s[1](null); }}>{s[0].text}</ui.Toast> : null,
    };
  }
  function pointsText(n, word) { return Number(n).toLocaleString('en-NZ') + ' ' + (Number(n) === 1 ? word.replace(/s$/, '') : word); }

  function WalletSection(props) {
    var v = props.v;
    var W = OO.wallet;
    var hk = String(v.hotel);
    var store = v.store;
    var toast = useToast();
    var sheetS = React.useState(null); // P3-17: the reward open in the item sheet's reward mode ({ id, row }) | null
    // The STORED coupon (exclusivity reads its presence; v.coupon is the summary's live copy) and the points chosen.
    var s = OO.useStore(function (st) { return { coupon: st.hotel === hk ? st.cart.coupon : null, points: st.points }; });
    var coupon = s.coupon || v.coupon || null;
    var opts = { hotel: hk, orderType: v.orderType, table: v.table || null, coupon: coupon };
    var ctx = W.ctxOf(store, opts);
    var lines = v.lines || [];
    var word = wordOf(store);
    var cur = store.currency;
    function money(n) { return OO.format.money(n, cur); }

    // REF CC's auto-removal of reward lines the order can no longer take (see the header): once per set of lines.
    var stale = W.staleRewards(lines, ctx);
    var staleSig = stale.stale ? stale.reason + ':' + stale.keys.join(',') : '';
    React.useEffect(function () {
      if (!staleSig) return;
      stale.keys.forEach(function (k) { OO.store.cartRemove(k); });
      toast.say(stale.text);
    }, [staleSig]);

    var productLevel = W.productLevel(store);
    var wallet = W.usable(ctx.wallet);
    var uid = v.customer && v.customer.uid ? String(v.customer.uid) : '';
    var wantRewards = productLevel && !!wallet && !!uid;
    var q = OO.useAsync(function () { return wantRewards ? OO.api.getLoyalty(hk, { uid: uid }) : null; }, [hk, uid, wantRewards]);

    var body = null;
    var title = '';
    var saving = 0;
    if (!productLevel) {
      // ---- wallet type 1: the points slider (REF OS WalletRedeemBlock) ----
      var pv = pointsView(lines, store, opts);
      if (pv.show) {
        title = 'Wallet points';
        saving = pv.applied ? pv.redemption.amount : 0;
        var choose = function (value) {
          var n = Math.max(0, Math.min(pv.max, Math.floor(num(value))));
          if (!(n > 0)) { OO.store.setWalletPoints(0, hk); return; }
          var c = W.ctxOf(store, opts);
          var res = W.applyPoints(n, lines, c);
          if (!res.ok && !(res.refuse && res.refuse.code === 'BELOW_MIN')) { toast.say(res.refuse ? res.refuse.text : null); return; }
          // Below the row's minimum the choice is kept but not applied ("Use at least N points.") and, as REF's
          // choosePoints does, it removes what applying points removes (a coupon): the membership lane's rules.
          var x = res.ok ? res : pointsStack(c, lines);
          if (arr(x.drop).indexOf('coupon') >= 0) OO.store.setCoupon(null);
          OO.store.setWalletPoints(n, hk);
          if (x.notice) toast.say(x.notice.text);
        };
        var value = pv.applied ? pv.redemption.amount : (pv.ratio > 0 ? r2(pv.selected / pv.ratio) : 0);
        body = pv.show === 'notice' ? (
          <ui.InlineNotice tone="info" icon="Info">{pv.notice}</ui.InlineNotice>
        ) : (
          <div className={cx('oo-wallet', pv.applied && 'is-on')}>
            <div className="oo-wallet__top">
              <div className="oo-wallet__text">
                <span className="oo-wallet__title">
                  {pv.applied ? pointsText(pv.redemption.points, word) + ' applied · You save ' + money(pv.redemption.amount) : 'Use your ' + word}
                </span>
                <span className="oo-wallet__sub">{pointsText(pv.balance, word) + ' available · ' + pointsText(pv.ratio, word) + ' = ' + money(1)}</span>
              </div>
              {pv.applied ? (
                <ui.Button variant="link" aria-label={'Remove your ' + word} onClick={function () { choose(0); }}>Remove</ui.Button>
              ) : (
                <ui.Button variant="link" onClick={function () { choose(pv.max); }}>Use max</ui.Button>
              )}
            </div>
            {pv.warning ? <ui.InlineNotice tone="warn">{pv.warning}</ui.InlineNotice> : null}
            <div className="oo-wallet__range">
              <span className="oo-wallet__end" aria-hidden="true">0</span>
              <input className="oo-wallet__slider" type="range" min={0} max={pv.max} step={1} value={pv.selected}
                aria-label={'Wallet ' + word + ' to use'} aria-valuetext={pointsText(pv.selected, word)}
                onChange={function (e) { choose(e.target.value); }} />
              <span className="oo-wallet__end oo-wallet__end--max" aria-hidden="true">{pointsText(pv.max, word)}</span>
            </div>
            <p className={cx('oo-wallet__value', pv.belowMin && 'is-warn')}>
              {pv.selected > 0 ? pointsText(pv.selected, word) + ' selected · ' + money(value)
                : 'Up to ' + money(pv.ratio > 0 ? r2(pv.max / pv.ratio) : 0) + ' off · ' + pointsText(pv.max, word) + ' max'}
            </p>
            {pv.belowMin ? <p className="oo-wallet__min" role="status">{'Use at least ' + pointsText(pv.min, word) + '.'}</p> : null}
            <p className="oo-checkout__note">{'Points apply to item prices only — options and combos aren’t covered.'}</p>
          </div>
        );
      }
    } else if (wallet) {
      // ---- wallet type 2: reward lines and the rewards list ----
      var budget = W.rewardBudget(lines, ctx);
      var mine = lines.filter(W.isReward);
      var showBudget = !budget.enabled && BUDGET_NOTICE.indexOf(budget.reason) >= 0;
      if (budget.enabled || mine.length || showBudget) {
        title = 'Rewards';
        saving = r2(mine.reduce(function (t, l) { return t + W.rewardAmount(l); }, 0));
        var items = budget.enabled && q.data !== undefined && q.data !== null ? rewardItems(q.data.products, v.moment && v.moment.menu || v.menu, lines, ctx, momentOf(v)) : undefined;
        var addItem = function (item) {
          // P3-17: a size or options to choose → the item sheet's reward mode (below), which adds the line itself.
          if (item.options) {
            if (OO.pages.ItemSheet) sheetS[1]({ id: item.id, row: item.row });
            else toast.say('This reward can’t be added right now.');
            return;
          }
          var x = W.addReward(item.product, { sizeId: null, sel: {} }, item.row, lines, W.ctxOf(store, opts));
          if (!x.ok) { toast.say(x.refuse ? x.refuse.text : 'This reward can’t be added right now.'); return; }
          if (x.drop.indexOf('coupon') >= 0) OO.store.setCoupon(null);
          if (x.drop.indexOf('points') >= 0) W.dropPoints(hk);
          OO.store.cartAdd(x.line);
          toast.say(item.name + ' added as a reward' + (x.notice ? '. ' + x.notice.text : ''));
        };
        var removeLine = function (l) { OO.store.cartRemove(l.key); toast.say(l.name + ' removed from your rewards'); };
        body = (
          <React.Fragment>
            <p className="oo-wallet__sub">
              {pointsText(budget.enabled ? budget.balance : W.balance(wallet), word) + (budget.enabled && budget.committed > 0 ? ' · ' + pointsText(budget.left, word) + ' left after your rewards' : '')}
            </p>
            {showBudget ? <ui.InlineNotice tone="info" icon="Info">{budget.reason === 'LIMIT' ? budget.text + ' ' + LIMIT_MORE : budget.text}</ui.InlineNotice> : null}
            {mine.length ? (
              <ul className="oo-wallet__list" aria-label="Rewards in your order">
                {mine.map(function (l) {
                  // P3-17: a reward with a size or options (the item sheet's reward mode) shows them and its note, as
                  // the order list does: the options it charges are on the order.
                  var mods = OO.cart.displayMods(l);
                  return (
                    <li key={l.key} className="oo-wallet__row is-mine">
                      <span className="oo-wallet__row-text">
                        <span className="oo-wallet__row-name">{l.name}</span>
                        {mods ? <span className="oo-checkout__oline-mods">{mods}</span> : null}
                        {l.notes ? <span className="oo-checkout__oline-mods">{'Note: ' + l.notes}</span> : null}
                        <span className="oo-wallet__row-sub">{pointsText(W.rewardPoints(l, budget.rule), word) + ' · ' + money(W.rewardAmount(l)) + ' off'}</span>
                      </span>
                      <ui.Button variant="link" aria-label={'Remove reward ' + l.name} onClick={function () { removeLine(l); }}>Remove</ui.Button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {!budget.enabled ? null : items === undefined ? (
              q.error || (q.data === null && !q.loading) ? null : <p className="oo-checkout__note" role="status">Loading your rewards…</p>
            ) : items === null ? (
              <p className="oo-checkout__note">We couldn’t load your rewards. You can still place this order.</p>
            ) : items.length ? (
              <ul className="oo-wallet__list" aria-label="Rewards you can add">
                {items.map(function (it) {
                  return (
                    <li key={it.id} className={cx('oo-wallet__row', !it.addable && 'is-off')}>
                      {it.image ? <img className="oo-wallet__row-img" src={it.image} alt="" loading="lazy" /> : <span className="oo-wallet__row-img" aria-hidden="true"><OO.icons.Gift size={18} /></span>}
                      <span className="oo-wallet__row-text">
                        <span className="oo-wallet__row-name">{it.name}</span>
                        <span className="oo-wallet__row-sub">{it.points > 0 ? (it.from ? 'From ' : '') + pointsText(it.points, word) : 'Reward'}</span>
                        {it.reason ? <span className="oo-wallet__row-why">{it.reason}</span> : null}
                      </span>
                      <ui.Button size="xs" variant={it.addable ? 'outline' : 'soft'} disabled={!it.addable} aria-label={'Add reward ' + it.name}
                        aria-haspopup={it.options ? 'dialog' : undefined} onClick={function () { addItem(it); }}>Add</ui.Button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="oo-checkout__note">No rewards to add yet.</p>
            )}
          </React.Fragment>
        );
      }
    }
    // P3-17: the item sheet in reward mode for a reward with options, over the list's own menu and moment.
    var Sheet = OO.pages.ItemSheet;
    var open = sheetS[0];
    var sheet = open && Sheet ? (
      <Sheet key={'reward-' + open.id} hotel={hk} store={store} menu={v.moment && v.moment.menu || v.menu} productId={open.id} moment={momentOf(v)}
        reward={{ row: open.row, orderType: v.orderType, table: v.table || null }}
        onClose={function () { sheetS[1](null); }}
        onAdded={function (line, info) { toast.say(info.name + ' added as a reward' + (info.notice ? '. ' + info.notice : '')); }} />
    ) : null;
    if (store.orderingQr) return null;
    if (!body) return sheet ? <React.Fragment>{sheet}{toast.node}</React.Fragment> : toast.node;
    return (
      <React.Fragment>
        <ui.Card className="oo-checkout__card oo-checkout__wallet">
          <div className="oo-checkout__head">
            <OO.icons.Star size={22} />
            <h3 className="oo-checkout__title">{title}</h3>
            {saving > 0 ? <ui.Badge tone="done" size="sm">{'Saving ' + money(saving)}</ui.Badge> : null}
          </div>
          {body}
        </ui.Card>
        {sheet}
        {toast.node}
      </React.Fragment>
    );
  }
  // D-07's judging opts for the rewards list: the order's instant, the store's clock and the order type's domain.
  function momentOf(v) {
    var m = v.moment || {};
    var now = OO.data.now().getTime();
    var moment = {
      now: m.at !== undefined && m.at !== null ? m.at : now, today: m.today !== undefined && m.today !== null ? m.today : now,
      timeZone: v.store ? v.store.timezone : null, orderType: v.orderType,
      domain: OO.data.domain(v.orderType, OO.mode, v.table ? { table: true } : undefined),
    };
    if (!v.table && OO.model.hideOutsideAvailability(v.store, OO.mode)) moment.hideOutsideAvailability = true;
    return moment;
  }

  function card(st, v) {
    return v && v.store ? <WalletSection st={st} v={v} /> : null;
  }

  CS.wallet = { use: use, card: card, pointsView: pointsView, rewardItems: rewardItems };
})();


// ===================== src/pages/checkout.jsx =====================
// src/pages/checkout.jsx: the Checkout page (page id 'checkout', /{hotel}/checkout). Owner: T3c (Batch 1 UI on
// API-shaped fixtures); Batch 2 P1-13 (bug 5, delivery gate, lat/lng, order snapshot, error kinds), P1-21 (delivery
// for real stores: Places address, Distance Matrix, automatic zone, the zone guard's 202 copy), the G1 fix B ("the
// order may exist", no payment method, the sign-up name, bfcache Back, the fee floor's scope, the online pay label), the
// G1 fix H (a payment that couldn't start) and P2-34 (the remaining generic placeOrder error copy).
// Screens: mobile "04 Checkout" and desktop "D04 Checkout" in docs/online-order/design/ordering-flow.dc.txt (lines
// 861–964 and 1198–1275; pay states from renderVals.payLabel). Store tweaks: store.checkout.loginStyle ('card' |
// 'solid') and store.checkout.priceSummary ('inline' | 'collapsed': on mobile the price card folds into an expandable
// "Price summary" row). Styles: styles/checkout.css.
//
// Sections: store name + address · order type card (Pickup / Delivery toggle when the store offers both HERE, see
// Delivery) with Day + Time selects on OO.schedule (checkout-time.jsx, P2-23: ASAP, then the order-later window's days,
// holidays listed; re-read every minute; a chosen time that is no longer offered or 15 min old is asked again before
// Pay, never switched silently); delivery adds the address (a real store: Places suggestions and "Use my current
// location") and the delivery zone's fee / minimum as text · login
// prompt → OTP sheet (OO.api.sendOtp / verifyOtp, then getCustomer, or register for a new email; fixture code 123456;
// G1 fix B: a new email whose contact has no name yet gets a "Your name" step before register, on the same code)
// or the signed-in card · Contact details (validated on Pay; the draft lives in OO.store.checkout) · Payment method
// (expands to the store's methods for the order type: usableMethods; none → "Not available", and Pay reads "No payment
// method available", disabled: nothing is sent; P2-27: each method opens into its choices, every counter type and
// every gateway of the order type, CS.payment.choices / choiceOf, the draft's paymentChoice) · terms (required, and
// ticked by default in every ordering option, a table too: TERMS, owner 2026-10-05; links to the terms page) +
// marketing checkbox (ticked by default on online and website ordering; an explicit white-label
// layout.checkout.marketingDefault false starts it unticked). App and kiosk retain their previous unchecked default.
// A customer's choice (draft.marketingChosen) wins over the store's default. None at a table, and no
// consent is sent there; Checkout.logic.marketingOn) · Order notes · price summary.
// Desktop: right column "Your order" (lines, Edit → cart, PriceRows, Pay) + Order notes + PoweredBySipo. Mobile:
// BottomBarCTA "Total · GST incl." + Pay.
// Totals: OO.pages.Cart.shared (cart.jsx) summaryOf on this page's own context { orderType, paymentMethod, zone,
// deliveryFee }: cart.jsx's order type and payment rules, minus what this page doesn't offer (delivery on a real store
// without the Maps key, an ONLINE_PAYMENT without a usable gateway), with the zone chosen here. The payload gets the
// same context (payloadCtx), so the charged total is the one shown. P2-07: summaryOf takes the wallet points in use
// off (Cart.shared.pointsOf, chosen in the wallet section), and the payload gets the same points and amount
// (walletPoints / walletAmount); points or rewards that pay it all read "Place order · $0.00" (no online payment: the
// payload sends payment_type 'wallet'), and a reward line is marked "Reward" in "Your order".
//
// Gates (P2-24; REF CheckoutForm handleSubmit, OrderTimePopup, PhoneInput):
//   force_login_for_ordering (store.settings.forceLoginForOrdering) and no customer: the login card reads "Log in to
//     place your order at {store}.", Pay reads "Log in to order" and opens the login sheet with "Log in to place your
//     order." (checked before the contact details; the notice goes once logged in). Fixture stores keep it off.
//   The checkout alert: a store's pickup / delivery text (store.checkoutAlert, see alertText; normalizeStore carries
//     it once the model-store.jsx patch lands, until then none shows) opens "Confirm your order" with that text and
//     one "Place order" as the last stop before anything is sent; closing it sends nothing (REF useCheckoutAlertGate).
//   The mobile number: a country code picker (PhoneField: REF's countries, the store's code by default — the one the
//     payload's e164 gives a number without one) beside the number; the draft gets E.164 ('+64211234567'), which the
//     payload sends as it is. Validation: REF's 8–15 digits with one leading '+' at most (not its national NZ / AU
//     mobile patterns: the picker shows the code a number goes with).
//   The paused store (store.online.active false): "Online ordering is taking a short pause", the store's note (its
//     online reason) and a way back to the menu; Pay reads "Ordering is paused" (disabled). A refusal saying the store
//     stopped taking these orders (the checkout's own ecode 800) re-reads the store (OO.data.rereadStore), so a pause
//     made while the customer was here shows.
//
// Dine-in (Batch 2 P4-05; mode qr only: tables are qr-only since P4-14; REF qr-ordering TableSideCheckoutPage, inventory
// D §1.1 "Checkout"). The table is the store's table context, read per render (tableContextOf): OO.pages.Table
// .context(hotel) (P4-01's contract: { id (a whole number), name (string | null), hotel, guests (number | null),
// excludedCategoryIds, expiresAt }), else (a bundle without table.jsx) OO.store.tableFor(hotel), the same context: P4-01
// keeps it on OO.store, where it also picks the page's menu (the table's, P4-02) and the cart ((store, table), P4-04).
// Modes online and app never place a table order (OO.store keeps no table there). In mode qr without a valid context
// (its 2 h ran out while the page was open, or Pay's re-check below found the table gone) the page shows the table
// problem and no form, ahead of the empty cart's redirect (the context going empties the cart in memory, P4-04): "We
// couldn't verify your table" (REF's copy), or the re-check's own words. A table checkout never turns into a pickup
// order. At a table:
//   Order type 'DINEIN' (the payload's 'dining'); no delivery, no zone; the summary is OO.pricing.summary with the
//     table, no coupon and no points (D-20; membership prices still apply, as REF), and the payload gets the same.
//     That pricing is tablePc + summarize (QR-CART: on Checkout.logic, so the cart prices a table order with it too).
//   Payment: CS.payment.choices(methods for DINEIN, 'DINEIN') (the table-side lists only) in CS.payment.tableCard,
//     the configured default or sole choice selected automatically; otherwise a manual pick is required. Manual picks
//     live in this visit's page state (CS.payment.use's pickS), never the draft. Until a choice is selected, Pay
//     reads "Choose a payment method": a tap opens nothing new, it shows that line under the list and brings the card
//     into view. No choices at all: "No payment method available" (disabled) and REF's "ask a staff member" line.
//     The payload is built on CS.payment.payStore(store, pick).
//   Contact (REF): required when the store asks for it (is_user_form_table_side: store.settings.tableUserForm, a
//     model-store.jsx key; see tableUserForm) or a gateway is picked ("Needed for online payment."); otherwise
//     optional ("Contact details (optional)"), and only what is filled in is checked. The payload's customer_name
//     falls back to the table's name, else 'Guest' (P4-07).
//   D-20: no coupons, points, rewards list or member prompt (the optional login card), and no account link in the
//     header. force_login_for_ordering is still honoured (REF), and a signed-in customer keeps the signed-in card.
//   P4-06: no order type and no time (REF: ASAP dine-in only). The order type card is CS.time.tableCard ("Dine-in",
//     the table and its party size, the cooking time) and the order is for CS.time.tableSlot (ASAP, ready at now +
//     pickup_buffer_time, as the payload's deliver_time); the time section's stale re-prompt and the store's checkout
//     alert (pickup / delivery text) don't apply. "Kitchen notes (optional)" ("Notes for the kitchen") are the order's
//     notes. No marketing opt-in (REF's table checkout has none; marketing_consent is false). TERMS (owner 2026-10-05,
//     not REF, whose table checkout has none): the terms checkbox shows here too, ticked by default, and Pay needs it.
//     Paused = the store's table ordering is off (enable_table_side_ordering; the server skips online_order_active for
//     a table): "Ordering not available" / "{store} isn’t taking orders at the table right now. Please ask a staff
//     member for help." (QR-OFF, owner 2026-10-05: table.jsx's paused gate says the same; REF "Dine-In Paused"), Pay
//     "Ordering is paused". In mode qr that gate stands in this page's place for such a store (QR-OFF), so the page
//     shows it only where table.jsx doesn't run.
//   P4-06, Pay re-checks the table (REF canOrder, with its pre-submit revalidate): OO.pages.Table.revalidate(hotel)
//     (recheckTable) runs beside the fresh menu, the coupon and the wallet pre-flight, and is judged first. null (the
//     table is gone, locked or disabled) → "Table unavailable" / "Please scan the table QR again or ask staff to
//     reopen this table." (REF), nothing sent: P4-01 clears the context on such an answer, so the page shows it as its
//     table problem (REF re-renders its landing), or as a notice over the form when the context stays (a store the
//     check couldn't find); a failed check → "We couldn't check your table. Check your connection and try again."
//     (never sent unchecked); a fresh context → the order goes with it (its name and party size; the snapshot names it
//     too). A table.jsx without revalidate → the rendered context. Refusals at a table: the payload's NO_TABLE /
//     PRICETAG_DOMAIN / TABLE_UNSUPPORTED (P4-07's copy; TABLE_UNSUPPORTED re-reads the menu and PRICETAG_DOMAIN the
//     pricetags, so the next Pay can go), a NO_GATEWAY ("That payment method is no longer available. Choose another
//     option.", REF; the pick goes), the server's "not accepting" 800 and the 403 licence refusal ("{store} isn’t
//     taking orders at the table right now. Please ask a staff member for help."; the 800 re-reads the store, so its
//     paused state shows).
//   P4-08, the return: a counter order's in-app payment-callback URL carries the table (?t_id=), and before a table
//     order's gateway redirect REF's payment context is written (saveRefPayment → payment-callback.jsx
//     acct.paycb.saveRefContext: sessionStorage 'sipo_payment_context', localStorage 'sipo_last_restaurant_slug'), so
//     the root /payment-callback can put the table back on the return (and the Next app's forwarder could too, if the
//     qr.sipo.nz flip is rolled back mid-payment). The context never carries the customer's name.
//
// Sections (Batch 2 P2-00b): this file keeps the page shell (header, layout, notices, login, contact, terms, notes, the
// price summaries), the pay flow and the wiring. Each section's hooks, handlers and markup live in its own file on
// OO.pages.CheckoutSections (concatenated before this one; checkout-time.jsx's header has the kit's API), called inline
// so the page's element tree and state are as before: checkout-time.jsx (order type, Day / Time), checkout-address.jsx
// (the delivery address and zone; the Delivery notes moved there), checkout-payment.jsx (the payment method),
// checkout-offers.jsx (the coupon re-check at Pay; the offers slot) and checkout-wallet.jsx (the wallet customer_id;
// the wallet slot, P2-07: the points slider, the reward lines and the rewards list, given the slot's v plus the page's
// menu, the table and the order's moment).
//
// Before Pay: a coupon still waiting for the customer's choice (P2-14: a free item to pick, sum.coupon.pending) stops
// Pay with "Choose your free item to use your coupon, or remove the coupon." (the offers section has both); an applied
// coupon needs a logged-in customer when store.settings.forceLoginForCoupon (the login sheet
// opens), and is re-validated with OO.api.validateCoupon(hotel, { code, email, time = the slot's instant }); a
// rejected coupon is removed and the customer presses Pay again on the new total (never charged a total they didn't
// see). customer_id is the wallet's (get_customer_wallet_points, store-scoped: P2-08's fresh read at Pay, else the one
// prefetched on load; a failure sends null), never customer/getdata's (matched by email across stores). P2-08 (REF's
// pre-flight, refreshCheckoutWalletPayload): for a signed-in customer the wallet is read again at Pay (CS.wallet's
// preflight: get_customer_wallet_points fresh, and get_user_loyalty_products_v4 when the cart has reward lines), and the
// payload gets it as freshWallet / freshRewards: the wallet and membership engines check the order against it and
// refuse what moved (the balance, a ratio row, the daily cap, a reward, the membership; null: the read failed), and
// nothing is sent at a total the customer didn't see (STALE_TOTAL). At the same time
// the menu is made fresh (freshMenu: OO.data.ensureFreshMenu, P1-16, when store.jsx has it, re-fetches a menu older
// than 15 min; a failed re-fetch falls back to the cached menu and never blocks) and the lines are checked against it
// at the slot (cart.jsx slotCheck + lineIssues): any issue stops the order and shows the unavailable-items state, kept
// for the lines, order type, day and time it was found for.
// Pay: OO.payload.checkoutPayload(lines, CS.payment.payStore(store, choice) (P2-27: the chosen gateway's config),
// payloadCtx(…, expectedPayable = the total on the Pay button)) →
// OO.api.placeOrder → OO.store.setLastOrder({ orderId, hotel, paymentMethod, placedAt, total, clearCartOnSuccess,
// couponTxn, snapshot, tax, shipping (P5-06b: the summary's tax and delivery fee after its waiver, for the order page's
// purchase event; never on the wire) }) → payment_url (fixture: this SPA's own /{hotel}/payment-callback) or
// /{hotel}/payment-callback?order_id=…&success=true for counter orders (P4-08: &t_id=… at a table). paymentMethod is the method the body went with
// (sentMethod: P1-12b sends an ONLINE_PAYMENT with $0.00 to pay as a counter order, which then takes the counter path).
// Counter / cash orders clear the checkout draft (contact kept) and the cart at once (on success only: a failure,
// "may exist" included, clears nothing); a card payment keeps them (clearCartOnSuccess: true) until the payment
// callback confirms the payment, so a declined / cancelled payment can retry. Back from the gateway may restore this
// page from the bfcache as it was left (G1 fix B, pageshow persisted): Pay's lock is reset, and a page whose order went
// to its gateway reloads (the callback may have cleared the cart in storage since).
// snapshot (snapshotOf, from what the page showed at Pay; order-status renders it until the POS ticket exists)
// = { v: 1, storeName, orderType ('DINEIN' for a table order), when { asap, label ('ASAP · about 15 min' | 'Today,
// 6:30pm'), at (ISO; null for ASAP) }, lines [{ name, qty, mods, total }] (≤ 50), rows [{ key, label, text }] (the
// price rows shown at Pay; the total is `total`, not a row), total, address (delivery only, else null), table (its
// name, else 'Table {id}'; null without a table), paymentLabel ('Online payment' (G1 fix B: never 'Paid online', it is
// written before any payment; the live status says whether it is paid) | 'Pay at the counter' | 'Pay on delivery' |
// 'Paid with points' | 'Nothing to pay' for $0.00) }. No contact data; strings cut to 200 characters.
// placeOrder rejections (errorState): kind 'paymentStart' (G1 fix H: the body's failure_code is PAYMENT_START_FAILED, on
// any status but a 404: the payment page couldn't be created (Windcave 503 / 502, Windcave EFTPOS 409, Payrix, Afterpay
// and LINE Pay 400s, the outdated-version 400), and the server has already released the coupon claim and the points
// debit and failed the order, with no payment row: nothing can charge it; P5-06b: also a payment_url that isn't an
// http(s) URL, refused before anything navigates (payTarget)) → "We couldn't start your payment, and nothing
// was charged. Please try again, or choose another way to pay.", Pay back at once; the cart and the coupon stay (the
// next Pay re-validates the coupon as always), and a lastOrder of that order id is cleared · kind 'unconfirmed' (G1 fix
// B: the order MAY exist — no answer (network), a 500 / 502 / 503 / 504 without that failure_code (a 500 can follow the
// order write; incl. a payment provider that didn't answer; an older backend host, and Stripe / PayPal / Click /
// Razorpay start exceptions, which are still 500s), or the server's 5 s repeat-order 400) → "We couldn't
// confirm your order — it may have gone through. Check your email (or Orders) before ordering again.", Pay disabled
// for 30 s, then "Place it again" (one explicit tap); a logged-in customer's orders are looked at (OO.api.getAllOrders,
// now and at 20 s; missing or failing is fine) and a matching one (findPlacedOrder) makes the button, and a link in the
// notice, "View your order" (its status page) instead · kind 'cart' (P1-05's map: prices, items or points changed) →
// what changed where the ecode says it (P2-34, CART_TEXT: 100 an item's price, 101 an option's price, 102 a size, 200
// an item, 300 the cart total, 700 the points), else "Some items in your cart have changed. Please review your cart."
// (701, 1000, 901, 902); every one ends "Please review your cart." and gets a Review cart link; the coupon list
// (OO.http.flush) and the menu (freshMenu, maxAgeMs 0: the cart then says "Prices updated") are re-read · kind 'retry'
// (a 429) → its message, Pay disabled for 5 s · kind 'payload' (P1-12 / P1-12b: nothing was sent) → own copy per code
// (STALE_TOTAL: the total moved since the render; Pay comes back on the re-rendered total) · ecode 202 (P1-21: the
// B10 zone guard; kind 'client' in P1-05's map, never 'cart') →
// its own copy per outcome (OO.delivery.zoneRejection, money in the store's currency): out of zone → "This address is
// outside our delivery area." · below the zone minimum → "Add $X more to get delivery to this address." · a zone
// dearer than the one chosen ("costs $X") → "Delivery to this address costs $X — your total has been updated. Check it
// and pay again.", and $X becomes that address's fee floor (setFeeFloor, with the zones and item subtotal the order
// went with: the zone is re-selected, never cheaper) · no coordinates → "Choose your address from the suggestions so
// we can check delivery.", and the pick is dropped · an unreadable outcome keeps the adapter's message · kind 'client'
// answers the adapter leaves generic or gives the store-closed line (P2-34, clientText; the kind and the rest of the
// state as before): a 403 (LicenseEnforcementService / StoreSubscriptions: the store's online ordering is unlicensed
// or switched off, any reason code) → "This store isn't taking online orders right now. Please contact the store to
// order." · a 413 → "Your order is too large to send. …" · the 400 { error: 'Request Authentication Failed' } (B01:
// neither the secret nor an allowed Origin) → "We couldn't verify this page with the store. Please refresh the page and
// try again." · ecode 310 / 311 / 312 (the checkout guards, enforce mode only: SEC-2 the amount to pay and a line
// discount, SEC-3 the points) → the server's `message`, a customer sentence by design, shown as sent when it is plain
// text of at most 200 characters and not the technical `error` (a backend without that wording sent the reason in
// both), else our copy per code (GUARD_TEXT) · the 800 'Delivery Service Not Available Right Now' (mandatory external
// delivery without a courier quote; not the validator's coupon 800) → "Delivery isn't available for this order right
// now. Please try again later." · any other kind (a 500 included) → the adapter's mapped message · a fixture-adapter
// rejection (no kind) → the Batch-1 ecode copy. No other server text is shown, apart from a coupon's refusal at Pay
// (see Before Pay).
// Refresh after a refusal (P2-08: refreshPlan; REF placeCheckoutOrder's *_refresh_required, clear_cart_wallet and
// remove_product_rewards, which REF derives from what the order carried: the server sends none, and a body that
// carries them is honoured too). A membership or wallet refusal of the payload (OO.membership.ERRORS /
// OO.wallet.ERRORS, their own copy) and any refused order of a signed-in customer, or with points or rewards, re-read
// the wallet (OO.data.loadWallet fresh: the store settles it and re-prices); a line priced with a pricetag re-reads the
// pricetags (OO.data.loadPricetags fresh): the page shows the new total (P2-02), never silently re-priced and paid. A
// refused order with points takes them off (REF clear_cart_wallet; a 429, "may exist" or a payment that couldn't start
// keeps them: the next Pay checks them again), with WALLET_REVIEW's copy for a wallet refusal (the engine's codes;
// ecode 700 / 1000 / 312) and POINTS_TAKEN_OFF after the copy of any other; a reward that can't be redeemed (ecode 701,
// the engine's reward codes, the daily cap) removes the reward lines with WALLET_REVIEW's copy. A read that failed
// (POINTS_UNVERIFIED / REWARDS_UNVERIFIED) keeps the choice and reads again.
// States: loading, store not found, load error, empty cart (→ redirect to the cart), unavailable items, no pickup times,
// closed now (later slots only), delivery-only store without delivery here, no payment method, validation errors, zone
// blocks, checking the distance / it failed / the address search is down (P1-21), placeOrder rejections (closed
// store, price change, double submit, the zone guard; P2-34: no licence, too large, not verified, the checkout guards,
// no courier), the order may exist (wait / Place it again / View your order); P2-24: the paused store, the login gate
// (force_login_for_ordering), the checkout alert.
//
// OO.pages.Checkout.logic: the page's pure helpers (tests/checkout-logic.test.mjs, tests/delivery-zones.test.mjs,
// tests/g1fix-b.test.mjs):
//   realDeliveryEnabled() → bool (OO.delivery.enabled(): the boot has a Maps browser key) · deliveryOffered(fixture) →
//       bool · offeredTypes(types, fixture) → types without DELIVERY where it isn't offered · distanceKm(fixture,
//       measured) → 0 (fixture) | measured (a number ≥ 0) | null (unknown).
//   selectZone(zones, km, subtotal, opts) → Quote: OO.delivery.selectZone (an alias; the contract is in its header).
//   zoneTexts(quote, currency) → { fee, upgrade, block } copy ('Delivery fee $7.00' | 'Free delivery', 'Add $X more
//       for …', the message that blocks Pay) · deliveryState({ real, point, search, dist, quote, cur }) → { block (stops
//       Pay | null), live (show it before Pay is pressed), checking (Pay waits for the distance), retry ('distance' |
//       'search' | null), searchDown } (see Delivery).
//   feeFloor(hotel, point, opts?) → the address's fee floor (0 when none) · setFeeFloor(hotel, point, fee, opts?) (kept
//       for the page's life, per store and point; the cart reads it too). G1 fix B: opts = { zones, subtotal } (the
//       store's zones, the item subtotal). A floor set with them and read with them: another zone set drops it,
//       another subtotal (in cents) reads 0 while it differs. Without opts either call behaves as before (the dearest
//       answer stands; the read ignores zones and subtotal).
//   zoneQuote(hotel, point, zones, km, subtotal) → Quote (G1 fix B): selectZone with feeFloor(hotel, point, { zones,
//       subtotal }) as minFee; when only the floor makes the address 'outOfZone', the floor is dropped and the quote
//       is the one without it.
//   usableMethods(methods, fixture) → methods without an ONLINE_PAYMENT
//       whose gateway the payload can't send (a gateway name and a numeric id > 0; a fixture's demo id passes) ·
//       methodKey(methods, want) → cart.jsx methodOf's choice on that list (want, isDefault, ONLINE_PAYMENT, first).
//   payState(v) → { label, enabled, canPay, view } (G1 fix B): the Pay button, v = { count, typeBlocked, blocked,
//       timeBound, slot, isDelivery, checking, method (the usable method chosen | null), terms, placing, waiting,
//       unconfirmed (null | { phase: 'wait' | 'again' | 'found' }), online, total (text), paused, login, choose }. No
//       method → 'No payment method available' (disabled; it outranks 'Accept terms to pay'); 'wait' → disabled; 'again'
//       → 'Place it again'; 'found' → 'View your order' (enabled, view: true; canPay false). P2-24: paused → 'Ordering is
//       paused' (disabled; right after an empty cart); login → 'Log in to order' where Pay would be (enabled: pay()
//       opens the login sheet). P4-05: choose (a table order with choices and no pick yet; method null) → 'Choose a
//       payment method' where 'No payment method available' would be (enabled: pay() asks for the pick). WL-2: preview
//       (a white-label studio preview) → 'Preview — orders are disabled', disabled, before anything else but 'found'.
//   P4-05 (see "Dine-in"): tableContextOf(hotel) → the table context | null (mode qr only; not pure: it reads
//       OO.pages.Table.context, else OO.store.tableFor) · tableUserForm(store) → bool (is_user_form_table_side) ·
//       contactRequired(store, pick) → bool (the flag, or a gateway pick) · validate(draft, delivery, zoneBlock,
//       optional) → { field: message } (optional: the contact is checked only where it is filled in).
//   P4-06 (see "Dine-in"): recheckTable(hotel) → Promise<{ ok, table, why }> (never rejects): OO.pages.Table
//       .revalidate's answer: ok with the fresh context; { ok: false, why: 'gone' } for null; { ok: false, why:
//       'unchecked' } when it rejected; { ok: true, table: null } when table.jsx has no revalidate (the rendered context
//       stands).
//   QR-CART (see "Dine-in"; p4-followups #25): tablePc(pick) → a table order's pricing context { orderType 'DINEIN',
//       paymentMethod (the pick's key; before a pick 'PAY_AT_COUNTER': a counter order, no card surcharge), zone null,
//       deliveryFee 0 } · summarize(K, lines, store, coupon, pc, table) → the page's summary (K = OO.pages.Cart.shared;
//       with a table: OO.pricing.summary with it, no coupon, no points, no delivery fee; else K.summaryOf). The cart
//       prices a table order with summarize(K, lines, store, null, tablePc(null), table): this page's before a pick.
//   POLISH-1 (p5-followups #16): issueState(issues, at) → { blocked, timeBound }: the page's verdict on its line checks
//       (issues = Cart.shared.lineIssues', at = slotCheck's: the later time named, null for ASAP): any issue stops the
//       order (blocked); timeBound = a later time is named and some line is time-bound (code 'unavailable': another
//       time cures it). A table order is ASAP (at null): every issue blocks, none is time-bound. The cart at a table
//       judges its lines with it too.
//   P2-24 (see "Gates"): loginRequired(store, customer) → bool · pausedOf(store, table) → bool (P4-06: with a table, the
//       store's table ordering is off) · alertText(store, orderType)
//       → text | null · PHONE_COUNTRIES [{ iso, name, dial }] · phoneDefault(store) → iso · phoneParts(value, fallbackIso,
//       store) → { iso, national } · phoneValue(iso, national) → E.164 | '' · phoneValid(value) → bool.
//   addressOf(draft) → { line, zipcode, lat, lng } · payloadCtx(v) → the checkoutPayload ctx (no `now`: the payload's
//       summary is the rendered one; expectedPayable when v has a number; dob, P2-21, when v has one; walletPoints /
//       walletAmount, P2-07, when walletAmount > 0; wallet, freshWallet and freshRewards (P2-08) when v has them;
//       P4-07, with a table: menuType (null without one) and guestCount) ·
//       sentMethod(body, chosen) →
//       'ONLINE_PAYMENT' (payment_type ONLINE_ORDER_CARD) | 'WALLET' | 'CASH' | 'PAY_AT_COUNTER' · whenOf(store, day,
//       slot, asapMins, now) → snapshot.when · snapshotOf(v) → the snapshot · errorState(err, cur = '$', at) → { kind:
//       'menu' | 'coupon' | 'paymentStart' | 'unconfirmed' | 'cart' | 'retry' | 'table' | 'error', text, action ('cart'
//       | null), retryMs (5000 for 'retry', 30000 for 'unconfirmed'), refreshWallet, refreshCoupons[, zone: the zone
//       guard's outcome (OO.delivery.zoneRejection), ecode 202 only][, orderId: the failed order's id (string | null),
//       'paymentStart' only][, title: 'Table unavailable', kind 'table' (the re-check found the table gone) only] }. P4-06:
//       at = { table: bool, store: the store's name } (a table order): kind 'table' for the re-check's stops (err.tableGone
//       / err.tableUnchecked: nothing was sent), and the table copy for a NO_GATEWAY, the server's "not accepting" 800 and a
//       403 (see "Dine-in") · placeErrorText(err) → copy.
//   refreshPlan(err, intent) → { wallet, pricetags, clearPoints, removeRewards, text, note } (P2-08; see "Refresh after
//       a refusal"): intent = { points, rewards, member, tag } (what the order carried: type-1 points, reward lines, a
//       signed-in customer, a line priced with a pricetag). wallet / pricetags: read them again; clearPoints /
//       removeRewards: take the points / the reward lines off; text: the copy that replaces errorState's (WALLET_REVIEW);
//       note: the sentence appended to errorState's (POINTS_TAKEN_OFF). Nothing for no error, the page's own stops
//       (menuChanged, couponRejected; P4-06: the table re-check's, tableGone / tableUnchecked), a payload refusal other
//       than a membership / wallet one, or a 429.
//   findPlacedOrder(raw, { total, paymentType, now }) → { orderId, createdAt (ms) } | null (G1 fix B; pure): in
//       customer/getOrders' answer (E17), the newest row created within 10 minutes of now (either side) with that
//       payment_type and payable (billTotal, else total; in cents); created_at is the server's UTC
//       'YYYY-MM-DD HH:MM:SS'.
//   freshMenu(hotel, opts) → Promise<menu | null> (never rejects; null when store.jsx has no ensureFreshMenu).
//   signIn(hotel, res, email, contact) → Promise<Customer> (not pure: the session): after verify-otp, see LoginSheet.
//       Rejects { signupFailed } (no uid) or (G1 fix B) { needName } (a new email whose contact name is blank or
//       longer than 191 characters) without calling register.
//   P5-06b (see goTo's comment): payTarget(url, { href, routeBase, mode }) → { kind: 'refuse' } (not an http(s) URL) |
//       { kind: 'app', to } (this origin, a route the SPA serves: in-app) | { kind: 'page', to } (a full-page
//       navigation: another origin, or this origin outside the SPA) · goTo(url, gateway) → bool (not pure: navigates;
//       false = refused, nothing happened).
//   WL-OPT: marketingOn(store, draft, table) → bool: the offers opt-in shown and sent (see "marketing opt-in" above).
//       Pay sends the draft with that value as its marketing (payloadCtx: marketing_consent only when it is true).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;
  var cx = ui.cx;

  // cart.jsx registers the shared helpers; read them at render time.
  function Kit() { return OO.pages.Cart.shared; }

  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var FIELD_ORDER = ['name', 'phone', 'email', 'address'];
  var SNAP_TEXT_MAX = 200;   // the snapshot contract's string cap
  var SNAP_LINES_MAX = 50;   // … and line cap
  var RETRY_MS = 5000;       // a kind 'retry' rejection (a 429) keeps Pay disabled this long
  // G1 fix B: a placeOrder failure after which the order MAY exist (errorState kind 'unconfirmed'): Pay stays disabled
  // this long, then reads PLACE_AGAIN. A logged-in customer's orders are looked at now and again at LOOKUP_AGAIN_MS
  // (before the wait ends); an order created within ORDER_MATCH_MS of now (either side) with this payable counts.
  var UNCONFIRMED_MS = 30000;
  var LOOKUP_AGAIN_MS = 20000;
  var ORDER_MATCH_MS = 600000;
  var NAME_MAX = 191;        // customer/register's name (a varchar(191) column)
  var GENERIC = "We couldn't place your order. Please try again.";
  var NETWORK = "We couldn't reach the store. Check your connection and try again.";
  var UNCONFIRMED = "We couldn't confirm your order — it may have gone through. Check your email (or Orders) before ordering again.";
  // G1 fix H: the checkout answer's failure_code for a payment that couldn't start (startFailed), and its copy.
  var START_FAILED = 'PAYMENT_START_FAILED';
  var PAY_NOT_STARTED = "We couldn't start your payment, and nothing was charged. Please try again, or choose another way to pay.";
  var PLACE_AGAIN = 'Place it again';
  var VIEW_ORDER = 'View your order';
  var PREVIEW_LABEL = 'Preview — orders are disabled'; // WL-2
  var NO_METHOD = 'No payment method available';
  var CHOOSE_METHOD = 'Choose a payment method'; // P4-05: several table choices, no configured default or pick
  // P4-05: mode qr without a valid table context (REF TableSideLanding 'error').
  var TABLE_LOST_TITLE = "We couldn't verify your table";
  var TABLE_LOST_TEXT = 'Scan the QR code on your table again, or ask a staff member for help.';
  // P4-06: the table's own stops and refusals (see the header, "Dine-in"; REF TableSideCheckoutPage and TableSideLanding).
  var TABLE_GONE_TITLE = 'Table unavailable';
  var TABLE_GONE = 'Please scan the table QR again or ask staff to reopen this table.';
  var TABLE_UNCHECKED = "We couldn't check your table. Check your connection and try again.";
  var TABLE_METHOD_GONE = 'That payment method is no longer available. Choose another option.';
  // QR-OFF (owner 2026-10-05): the store's table ordering off says what table.jsx's paused gate says.
  var TABLE_PAUSED_TITLE = 'Ordering not available';
  function tableOffText(name) {
    var n = String(name === undefined || name === null ? '' : name).trim();
    return (n || 'This store') + ' isn’t taking orders at the table right now. Please ask a staff member for help.';
  }
  var CART_CHANGED = 'Some items in your cart have changed. Please review your cart.';
  // P2-34: a kind 'cart' rejection whose ecode says what changed (the validator's 100 PRODUCT_PRICE, 101 MODIFIER_PRICE,
  // 102 PRODUCT_SIZE_NOT_FOUND, 200 PRODUCT_NOT_FOUND, 300 INVALID_TOTAL, '700' the wallet points). Any other ecode of
  // that kind keeps CART_CHANGED.
  var CART_TEXT = {
    100: 'The price of an item in your cart has changed. Please review your cart.',
    101: 'The price of an option in your cart has changed. Please review your cart.',
    102: 'A size in your cart is no longer available. Please review your cart.',
    200: 'An item in your cart is no longer available. Please review your cart.',
    300: 'Your cart total has changed. Please review your cart.',
    700: "Your points couldn't be applied to this order. Please review your cart.",
  };
  // P2-34: the checkout guards' ecodes (enforce mode only): the fallback when the body's `message` isn't the customer
  // sentence the server puts there (guardSentence), worded as the server's own default for the code.
  var GUARD_TEXT = {
    310: "We couldn't confirm the amount to pay for this order. Please refresh your cart and try again.",
    311: "A discount in your cart couldn't be confirmed. Please refresh your cart and try again.",
    312: "We couldn't apply your points to this order. Please refresh and try again.",
  };
  var GUARD_TEXT_MAX = 200; // the longest guard sentence the server sends is under 100 characters
  // P2-34: kind 'client' rejections the adapter leaves generic (clientText).
  var STORE_OFF = "This store isn't taking online orders right now. Please contact the store to order.";
  var TOO_LARGE = 'Your order is too large to send. Remove some items and try again.';
  var REFRESH_PAGE = "We couldn't verify this page with the store. Please refresh the page and try again.";
  var NO_DELIVERY = "Delivery isn't available for this order right now. Please try again later.";
  // snapshot.paymentLabel (G1 fix B: an online order's is 'Online payment', never 'Paid online': it is written at Pay,
  // before any payment, and order-status shows it while the live status is unknown).
  var PAY_LABEL = { ONLINE_PAYMENT: 'Online payment', PAY_AT_COUNTER: 'Pay at the counter', CASH: 'Pay on delivery', WALLET: 'Paid with points' };
  // P2-08: what a refused order says when it took the points off or removed reward lines (refreshPlan): by the wallet
  // engine's refusal code, and for the server's wallet ecodes (700 the points, 1000 the wallet type, 312 the points
  // guard; 701 a reward's product).
  var WALLET_REVIEW = {
    POINTS: 'Your points balance or the store’s points rules changed, so your points were taken off this order. Choose them again or pay the new total.',
    POINTS_LIMIT: 'You’ve reached today’s limit for redeeming points, so your points were taken off this order. Check your new total and pay again.',
    POINTS_OFF: 'This store takes points as rewards, not off your order, so your points were taken off. Check your new total and pay again.',
    POINTS_WITH_COUPON: 'Points can’t be combined with a coupon, so your points were taken off this order. Check your new total and pay again.',
    REWARDS: 'A reward in your cart can no longer be redeemed, so it was removed. Check your order and pay again.',
    REWARDS_LIMIT: 'You’ve reached today’s limit for redeeming points, so your rewards were removed. Check your order and pay again.',
    REWARDS_OVER: 'Your rewards need more points than you can use on this order, so they were removed. Check your order and pay again.',
  };
  // … and the sentence added to a refusal about something else while points were on the order (REF clears them).
  var POINTS_TAKEN_OFF = 'Your points were taken off this order: choose them again before you pay.';
  var WALLET_ECODES = { 700: true, 1000: true, 312: true };
  // OO.payload refusals (Error { kind: 'payload', code }; P1-12 / P1-12b): nothing was sent.
  var PAYLOAD_TEXT = {
    PREVIEW: 'This is a preview. Orders are disabled.', // WL-2: a white-label studio preview
    NO_GATEWAY: "Online payment isn't set up for this store yet. Please choose another way to pay.",
    STALE_TOTAL: 'Prices have changed. Check your total and tap Pay again.',
    // P4-07: a table order's own refusals (payload.jsx "Table orders").
    TABLE_UNSUPPORTED: "Your table's menu didn't finish loading. Refresh the page and try again.",
    NO_TABLE: "We couldn't find your table. Scan the QR code on your table again.",
    PRICETAG_DOMAIN: 'Prices have changed. Refresh the page and try again.',
    NO_ZONE: 'Choose a delivery address in our delivery area.',
    COMBO_UNPRICEABLE: "One of the deals in your cart can't be ordered online right now. Remove it and try again.",
  };

  // The sections (concatenated before this file) and the helpers this file shares with them (P2-00b).
  var CS = OO.pages.CheckoutSections, byKey = CS._.byKey, numOr0 = CS._.numOr0, centsOf = CS._.centsOf;
  var AD = CS.address, realDeliveryEnabled = AD.realDeliveryEnabled, deliveryOffered = AD.deliveryOffered, offeredTypes = AD.offeredTypes;
  var distanceKm = AD.distanceKm, selectZone = AD.selectZone, zoneTexts = AD.zoneTexts, deliveryState = AD.deliveryState;
  var feeFloor = AD.feeFloor, setFeeFloor = AD.setFeeFloor, zoneQuote = AD.zoneQuote, addressOf = AD.addressOf, guardText = AD.guardText;
  var usableMethods = CS.payment.usableMethods, methodKey = CS.payment.methodKey;
  // A server flag that may come as true / 1 / '1' / 'true'.
  function flagOn(v) { return v === true || v === 1 || v === '1' || (typeof v === 'string' && v.trim().toLowerCase() === 'true'); }

  // ---- the order: address, payload ctx, snapshot ------------------------------------------------------------------
  // The OO.payload.checkoutPayload ctx (payload.jsx header) for what the page shows. v = { orderType, slot,
  // paymentMethod, draft, customer, customerId, coupon, table, isMobileApp, zone, fee, expectedPayable, pricetag, dob,
  // walletPoints, walletAmount, wallet, freshWallet, freshRewards, menuType, guestCount }. The
  // gateway name and id come from store.paymentMethods by paymentMethod (P1-12). expectedPayable = the total the Pay
  // button shows (a number): the payload refuses with code STALE_TOTAL when its own payable differs (P1-12b). No `now`:
  // the payload's summary must be the one the page rendered (summaryOf passes none), so payable_amount is the total
  // shown. pricetag (P2-01) = the `pricetag` record of the menu the lines were priced with (OO.pricetags.priceMenu):
  // the pricetags contributor claims the tag on exactly the rows it priced; none → nothing is claimed. dob (P2-21) = the
  // date of birth the item sheet's age gate confirmed this session (OO.pages.ItemSheet.age.verifiedDob; REF sends
  // getVerifiedDobFor(store) whenever there is one): the payload's customer_dob; none → no customer_dob.
  // P2-07: walletPoints / walletAmount = the points in use as the summary took them off (Cart.shared.pointsOf: the
  // Redemption's points and amount, the same arguments as the page's summary), passed only when walletAmount > 0 (no
  // points: the ctx as before); wallet (the wallet the page priced with) only when given (default: the payload's own
  // read of OO.store.wallet). P2-08: freshWallet / freshRewards = the pre-flight read at Pay (CS.wallet's preflight),
  // passed when v has them (undefined = not checked; null = the read failed: the engines refuse what needs it).
  // The offers checkbox as the page shows it and the order sends it. A customer's saved choice wins over the store's
  // default; a legacy true without a choice marker also remains selected. An explicit store setting follows, otherwise
  // online and website ordering start checked and the app and kiosk remain unchecked. Never at a table.
  function marketingOn(store, draft, table) {
    if (table) return false;
    var d = draft || {};
    if (d.marketingChosen === true) return d.marketing === true;
    if (d.marketing === true) return true;
    var configured = store && store.checkout && store.checkout.marketingDefault;
    if (typeof configured === 'boolean') return configured;
    return OO.mode === 'online' || OO.mode === 'landing';
  }
  function payloadCtx(v) {
    var d = v.draft || {};
    var delivery = v.orderType === 'DELIVERY';
    var ctx = {
      orderType: v.orderType, slot: v.slot, paymentMethod: v.paymentMethod || 'PAY_AT_COUNTER',
      contact: { name: String(d.name || '').trim(), phone: String(d.phone || '').trim(), email: String(d.email || '').trim() },
      customer: v.customer || null, customerId: v.customerId || null, notes: String(d.notes || '').trim(), coupon: v.coupon || null,
      marketing: d.marketing === true && !v.table, table: v.table || null, isMobileApp: !!v.isMobileApp, // P4-06: no opt-in at a table
      deliveryFee: delivery ? numOr0(v.fee) : 0, deliveryZone: delivery ? v.zone || null : null,
      address: delivery ? addressOf(d) : null,
      tipAmount: d.tipAmount == null ? '0' : d.tipAmount,
    };
    if (typeof v.expectedPayable === 'number' && isFinite(v.expectedPayable)) ctx.expectedPayable = v.expectedPayable;
    if (v.pricetag) ctx.pricetag = v.pricetag;
    if (v.dob) ctx.dob = v.dob;
    if (numOr0(v.walletAmount) > 0) { ctx.walletPoints = numOr0(v.walletPoints); ctx.walletAmount = numOr0(v.walletAmount); } // P2-07
    if (v.wallet !== undefined) ctx.wallet = v.wallet;
    if (v.freshWallet !== undefined) ctx.freshWallet = v.freshWallet; // P2-08: the pre-flight read (null: it failed)
    if (v.freshRewards !== undefined) ctx.freshRewards = v.freshRewards;
    // P4-07: a table order's menu type (the lines were priced from P.menu: 'Table Menu' lifts TABLE_UNSUPPORTED on a
    // live store) and its party size (P4-03, on the table context).
    if (v.table) { ctx.menuType = v.menuType || null; ctx.guestCount = v.guestCount; }
    return ctx;
  }
  // P4-08 (see the header, "Dine-in"): REF's payment context before a table order's gateway redirect, through
  // payment-callback.jsx's acct.paycb.saveRefContext (read at call time). Best effort: missing or throwing storage
  // writes nothing. v: saveRefContext's (orderId, hotel, total, itemsCount, gateway, gatewayId, estimatedTime,
  // placedAt, currency, storeName, storePhone, table).
  function saveRefPayment(v) {
    var paycb = OO.pages.acct && OO.pages.acct.paycb;
    if (!paycb || typeof paycb.saveRefContext !== 'function') return false;
    var session = null, local = null;
    try { session = window.sessionStorage || null; } catch (e) { session = null; }
    try { local = window.localStorage || null; } catch (e) { local = null; }
    return paycb.saveRefContext(session, local, v);
  }
  // The age gate's confirmed date of birth for this store this session, or null (P2-21).
  function verifiedDob(hotel, store) {
    var age = OO.pages.ItemSheet && OO.pages.ItemSheet.age;
    try { return age && typeof age.verifiedDob === 'function' ? age.verifiedDob(hotel, store) : null; } catch (e) { return null; }
  }
  // The payment method the order actually went with, from the body sent: a card payment only for payment_type
  // 'ONLINE_ORDER_CARD'. P1-12b sends an ONLINE_PAYMENT whose payable is $0.00 as a counter order (no payment_url,
  // nothing to confirm), and a fully wallet-paid one is 'wallet'. The payment callback skips payment_update for
  // PAY_AT_COUNTER / CASH / WALLET.
  function sentMethod(body, chosen) {
    var type = String((body && body.payment_type) || '');
    if (type === 'ONLINE_ORDER_CARD') return 'ONLINE_PAYMENT';
    if (type === 'wallet') return 'WALLET';
    return chosen === 'CASH' ? 'CASH' : 'PAY_AT_COUNTER';
  }
  // snapshot.paymentLabel: how the order is paid, by the method it went with ('Nothing to pay' for a $0.00 total).
  function payLabelOf(method, total) {
    if (method === 'WALLET') return PAY_LABEL.WALLET;
    if (!(numOr0(total) > 0)) return 'Nothing to pay';
    return PAY_LABEL[method] || PAY_LABEL.PAY_AT_COUNTER;
  }

  function clip(v) { return String(v === undefined || v === null ? '' : v).trim().slice(0, SNAP_TEXT_MAX); }
  // snapshot.when for the slot ordered: ASAP → 'ASAP · about {asapMins} min' (at null); a time → 'Today, 6:30pm' and
  // the slot's store-local instant as ISO.
  function whenOf(store, day, slot, asapMins, now) {
    if (!slot || slot.asap) return { asap: true, label: clip('ASAP · about ' + Math.max(0, Math.round(numOr0(asapMins))) + ' min'), at: null };
    var t = new Date(OO.model.slotInstant(store, slot, now));
    return { asap: false, label: clip((day && day.label ? day.label + ', ' : '') + (slot.label || '')), at: isFinite(t.getTime()) ? t.toISOString() : null };
  }
  // The lastOrder snapshot (the contract in the header; OO.store sanitises it and DROPS a field of the wrong type, so
  // every field is built with its type). v = { store, orderType, table (the table context | null), when (whenOf),
  // lines, sum (the summary shown), address (the delivery line), paymentMethod (the method the order went with:
  // sentMethod) }.
  function snapshotOf(v) {
    var store = v.store || {};
    var money = function (n) { return OO.format.money(n, store.currency); };
    var table = v.table || null;
    var delivery = !table && v.orderType === 'DELIVERY';
    var when = v.when || {};
    var address = delivery ? clip(v.address) : '';
    return {
      v: 1,
      storeName: clip(store.name),
      orderType: table ? 'DINEIN' : (delivery ? 'DELIVERY' : 'PICKUP'),
      when: { asap: !!when.asap, label: clip(when.label), at: typeof when.at === 'string' && when.at ? when.at : null },
      lines: (v.lines || []).slice(0, SNAP_LINES_MAX).map(function (l) {
        return { name: clip(l.name), qty: numOr0(l.qty), mods: clip(OO.cart.displayMods(l)), total: clip(money(OO.cart.lineTotal(l))) };
      }),
      rows: ((v.sum && v.sum.rows) || []).filter(function (r) { return r && r.key !== 'total'; }).map(function (r) {
        return { key: clip(r.key), label: clip(r.label), text: clip(r.text !== undefined && r.text !== null ? r.text : money(r.value)),
          ...(r.description ? {description: String(r.description).slice(0, 1000)} : {}) };
      }),
      total: clip(money(v.sum ? v.sum.total : 0)),
      address: address || null,
      table: table ? clip(table.name || 'Table ' + table.id) : null,
      paymentLabel: payLabelOf(v.paymentMethod, v.sum ? v.sum.total : 0),
    };
  }

  // ---- Pay: a fresh menu, and what a rejection means ----------------------------------------------------------------
  // A menu at most 15 min old for the re-check at Pay (OO.data.ensureFreshMenu, P1-16; opts { maxAgeMs }). Its failure
  // never blocks the order: the cached menu instead (OO.data.peekHotel). null when store.jsx has no ensureFreshMenu (the
  // render-time check stands). Never rejects.
  function freshMenu(hotel, opts) {
    var D = OO.data || {};
    if (typeof D.ensureFreshMenu !== 'function') return Promise.resolve(null);
    return Promise.resolve().then(function () { return D.ensureFreshMenu(hotel, opts); }).then(function (menu) {
      return menu || null;
    }, function () {
      var cached = typeof D.peekHotel === 'function' ? D.peekHotel(hotel) : null;
      return (cached && cached.menu) || null;
    });
  }

  // ---- P2-34: the placeOrder answers the adapter leaves generic ----------------------------------------------------
  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  // The raw body of a rejection (err.data) when it is a JSON object, else {}.
  function bodyOf(e) {
    var d = e ? e.data : null;
    return d && typeof d === 'object' && !Array.isArray(d) ? d : {};
  }
  // A checkout guard's customer sentence: the body's `message` (SEC-2 / SEC-3 put one there for 310 / 311 / 312 and keep
  // the technical reason in `error`) when it is non-empty plain text of at most GUARD_TEXT_MAX characters and isn't that
  // `error` (a backend without the customer wording, 87be3ed54, sent the reason as both); else ''.
  function guardSentence(body) {
    var s = typeof body.message === 'string' ? body.message.trim() : '';
    if (!s || s.length > GUARD_TEXT_MAX || /[<>\r\n]/.test(s)) return '';
    return typeof body.error === 'string' && body.error.trim() === s ? '' : s;
  }
  // Our copy for a real-adapter kind 'client' rejection that the adapter leaves generic or gives the store-closed line
  // (see the header), by status, ecode and body; null for any other rejection, whose copy stays as it was. Only a 400's
  // body is read (never a 404's).
  function clientText(e) {
    if (!e || e.kind !== 'client') return null;
    var status = Number(e.status);
    if (status === 403) return STORE_OFF;
    if (status === 413) return TOO_LARGE;
    if (status !== 400) return null;
    var body = bodyOf(e);
    var code = Number(e.ecode);
    if (has(GUARD_TEXT, code)) return guardSentence(body) || GUARD_TEXT[code];
    if (/^\s*request authentication failed\s*$/i.test(typeof body.error === 'string' ? body.error : '')) return REFRESH_PAGE;
    // The external-delivery refusal is a bare { status: false, ecode: 800, message }; the validator's coupon 800 (its
    // status is a string) is never it, whatever its text.
    var text = typeof body.message === 'string' ? body.message : '';
    if (code === 800 && typeof body.status !== 'string' && /delivery\s+service\s+not\s+available/i.test(text)) return NO_DELIVERY;
    return null;
  }
  // The copy for a kind 'cart' rejection: what changed, when its ecode says it.
  function cartText(e) {
    var code = Number(e.ecode);
    return has(CART_TEXT, code) ? CART_TEXT[code] : CART_CHANGED;
  }

  // Copy for a placeOrder rejection that isn't a 'cart' / 'retry' kind.
  function placeErrorText(err) {
    // OO.payload refused to build the body (nothing was sent): no gateway, a total that moved since the page rendered
    // (STALE_TOTAL: the re-render shows the new one), no scheduled time, a broken contributor, …
    if (err && err.kind === 'payload') {
      // P2-04: the membership contributor refused (OO.membership.ERRORS): its own customer sentence.
      if (err.code === 'CONTRIBUTOR' && err.cause && err.cause.kind === 'membership' && typeof err.cause.text === 'string') return err.cause.text;
      if (err.code === 'CONTRIBUTOR' && err.cause && err.cause.kind === 'wallet' && typeof err.cause.text === 'string') return err.cause.text; // P2-05 / P2-06
      return PAYLOAD_TEXT[err.code] || "We couldn't prepare your order. Please refresh the page and try again.";
    }
    if (err && err.kind === 'network') return NETWORK;
    // The real adapter (and the OO.api facade) reject with a kind and a customer-safe message (api-real-orders.jsx
    // CHECKOUT_ERRORS) that never carries server text. P2-34: our own copy comes first for the answers it leaves generic
    // (clientText); of those, only a checkout guard's customer sentence is the server's.
    if (err && err.kind) {
      return clientText(err) || (typeof err.message === 'string' && err.message.trim() ? err.message.trim() : GENERIC);
    }
    // A fixture-adapter rejection carries the raw body: the Batch-1 copy (API-CONTRACT §3.8 errors).
    var d = (err && err.data) || {};
    var code = Number(d.ecode);
    var text = String(d.message || (typeof d.status === 'string' ? d.status : '') || d.error || (err && err.message) || '');
    if (code === 800) {
      return /resolved/i.test(text) ? "We couldn't find this store. Please refresh the page and try again."
        : "The store isn't accepting orders for that time right now. Please choose a later time.";
    }
    if (code === 300) return 'Prices have changed since you added these items. Please review your cart and try again.';
    if (code === 101 || code === 102 || code === 104 || code === 902) {
      return (d.message ? String(d.message).trim() + ' ' : 'Something in your cart has changed. ') + 'Please review your cart.';
    }
    if (/frequent/i.test(text)) return 'An order was just placed. Please wait a few seconds and try again.';
    if (err && !err.status) return NETWORK;
    return GENERIC;
  }

  // G1 fix B: a placeOrder failure after which the order MAY exist, so Pay must never simply come back: no answer (kind
  // 'network': the request may have reached the server), a 502 / 503 / 504 (a proxy timeout while the order was being
  // written, or a payment provider that didn't answer after it was: Windcave's 503 PAYMENT_PROVIDER_* says "No new
  // payment attempt should be started until this order is checked"), and the server's 5 s repeat-order guard (kind
  // 'retry' on a 400: an order from this customer was just placed). Only the real adapter's / OO.http's errors (they
  // carry a kind): a 429 (the checkout limiter; nothing was written), a 500 and a fixture rejection are not.
  // G1 fix H: unless the body says the payment couldn't start (startFailed, checked first): that order is dead.
  var MAY_EXIST_STATUS = { 500: true, 502: true, 503: true, 504: true }; // 500 too: it can follow the order write (e.g. during the POS push)
  function mayExist(e) {
    if (!e || !e.kind || e.kind === 'payload') return false;
    var status = Number(e.status);
    return e.kind === 'network' || MAY_EXIST_STATUS[status] === true || (e.kind === 'retry' && status === 400);
  }
  // G1 fix H: the payment couldn't START (the body's failure_code is PAYMENT_START_FAILED, on any status): the server had
  // created the order, then its gateway refused or didn't answer (Windcave 503 / 502, Windcave EFTPOS 409, Payrix,
  // Afterpay and LINE Pay 400s, the outdated-version 400). It has already released the coupon claim and the points
  // debit and marked the order FAILED + UNPAID with no payment row, so nothing can charge or revive it: not "may exist",
  // and Pay may come back at once. Read from the raw body (err.data), never a 404's (a 404 is classified by its status
  // alone) nor a payload refusal's (nothing was sent). Without the key (an older backend host; Stripe / PayPal / Click /
  // Razorpay start exceptions, still 500s) the status decides as before.
  function startFailed(e) {
    if (!e || e.kind === 'payload' || Number(e.status) === 404) return false;
    var d = e.data;
    return !!d && typeof d === 'object' && !Array.isArray(d) && d.failure_code === START_FAILED;
  }
  // The order id of that answer (its order_id: a string or a number), else null.
  function failedOrderId(d) {
    var id = d ? d.order_id : null;
    var s = typeof id === 'string' || (typeof id === 'number' && isFinite(id)) ? String(id).trim() : '';
    return s || null;
  }

  // What Pay's failure shows (see the header). menuChanged / couponRejected are this page's own stops. cur: the store's
  // currency symbol for the zone guard's amounts (default '$').
  function errorState(err, cur, at) {
    var e = err || {};
    var table = !!(at && at.table); // P4-06: a table order (see the header)
    var out = { kind: 'error', text: GENERIC, action: null, retryMs: 0, refreshWallet: false, refreshCoupons: false };
    if (e.menuChanged) { out.kind = 'menu'; out.text = null; return out; }
    // P4-06: the table re-check's stops (nothing was sent).
    if (e.tableGone) { out.kind = 'table'; out.title = TABLE_GONE_TITLE; out.text = TABLE_GONE; return out; }
    if (e.tableUnchecked) { out.kind = 'table'; out.text = TABLE_UNCHECKED; return out; }
    if (e.couponRejected) {
      out.kind = 'coupon';
      out.text = 'Your coupon was removed: ' + e.message + ' Check your new total and pay again.';
      return out;
    }
    out.refreshWallet = flagOn(e.wallet_refresh_required) || flagOn(e.clear_cart_wallet);
    if (startFailed(e)) {
      // G1 fix H: nothing was charged and nothing is pending: Pay comes back at once (no wait, no lookup), and the page
      // forgets that order (orderId).
      out.kind = 'paymentStart';
      out.text = PAY_NOT_STARTED;
      out.orderId = failedOrderId(e.data);
    } else if (mayExist(e)) {
      out.kind = 'unconfirmed';
      out.text = UNCONFIRMED;
      out.retryMs = UNCONFIRMED_MS;
    } else if (e.kind === 'cart') {
      out.kind = 'cart';
      out.text = cartText(e); // P2-34: what changed, by ecode
      out.action = 'cart';
      out.refreshCoupons = true;
    } else if (e.kind === 'retry') {
      out.kind = 'retry';
      out.text = typeof e.message === 'string' && e.message.trim() ? e.message.trim() : GENERIC;
      out.retryMs = RETRY_MS;
    } else {
      out.text = placeErrorText(err);
      // P1-21: the B10 zone guard (ecode 202; kind 'client' in P1-05's map) gets its own copy per outcome, and `zone`
      // (OO.delivery.zoneRejection) tells the page what to change. An outcome without copy keeps the adapter's text.
      if (Number(e.ecode) === 202) {
        out.zone = OO.delivery.zoneRejection(e.data);
        out.text = guardText(out.zone, cur) || out.text;
      }
      // P4-06: a table order's refusals in REF's words (a payment method that went away; the store not taking table
      // orders: the checkout's 800, the TABLE_SIDE_ORDERING licence's 403).
      if (table && e.kind === 'payload' && e.code === 'NO_GATEWAY') out.text = TABLE_METHOD_GONE;
      if (table && (storeOff(e) || (e.kind === 'client' && Number(e.status) === 403))) out.text = tableOffText(at.store);
    }
    return out;
  }

  // P2-08: what a refused order re-reads and removes (see the header, "Refresh after a refusal"). REF placeCheckoutOrder
  // derives its *_refresh_required / clear_cart_wallet / remove_product_rewards flags from what the order carried (the
  // server sends none; a body that carries them is honoured too). intent = { points (type-1 points were on the order),
  // rewards (reward lines), member (a signed-in customer: REF's account / membership review), tag (a line priced with a
  // pricetag) }. Pure.
  var UNVERIFIED = { POINTS_UNVERIFIED: true, REWARDS_UNVERIFIED: true };
  function refreshPlan(err, intent) {
    var e = err || {};
    var i = intent || {};
    var out = { wallet: false, pricetags: false, clearPoints: false, removeRewards: false, text: null, note: null };
    if (!err || e.menuChanged || e.couponRejected || e.tableGone || e.tableUnchecked) return out;
    var cause = e.cause || {};
    if (e.kind === 'payload') {
      // Nothing was sent: the engines refused what moved since the page priced it (the pre-flight read included).
      if (e.code !== 'CONTRIBUTOR') return out;
      if (cause.kind === 'membership') { out.wallet = true; return out; }
      if (cause.kind !== 'wallet') return out;
      out.wallet = true;
      if (UNVERIFIED[cause.code]) return out; // the read failed: keep the choice, read again (REF keeps it too)
      out.removeRewards = !!i.rewards;
      out.clearPoints = !!i.points && !i.rewards;
      if (out.removeRewards) out.text = cause.code === 'POINTS_LIMIT' ? WALLET_REVIEW.REWARDS_LIMIT : cause.code === 'REWARDS_OVER' ? WALLET_REVIEW.REWARDS_OVER : WALLET_REVIEW.REWARDS;
      else if (out.clearPoints) out.text = WALLET_REVIEW[cause.code] || WALLET_REVIEW.POINTS;
      return out;
    }
    var involved = !!(i.points || i.rewards || i.member);
    if (startFailed(e) || mayExist(e)) {
      // The order may exist (the repeat-order 400 included), or its payment couldn't start (the server released the
      // points): re-read the balance, keep the choices (the next Pay's pre-flight checks them again).
      out.wallet = involved;
      return out;
    }
    if (e.kind === 'retry') return out; // a 429: nothing was processed
    // Refused: REF's flags.
    var code = Number(e.ecode);
    out.wallet = involved || flagOn(e.wallet_refresh_required) || flagOn(e.membership_refresh_required) || flagOn(e.clear_cart_wallet);
    out.pricetags = !!i.tag || flagOn(e.pricetag_refresh_required);
    out.clearPoints = !!i.points && !i.rewards;
    out.removeRewards = !!i.rewards && (code === 701 || flagOn(e.remove_product_rewards));
    if (out.removeRewards) out.text = WALLET_REVIEW.REWARDS;
    else if (out.clearPoints && WALLET_ECODES[code]) out.text = WALLET_REVIEW.POINTS;
    else if (out.clearPoints) out.note = POINTS_TAKEN_OFF;
    return out;
  }

  // ---- P2-24: the checkout gates (see the header, "Gates") ----------------------------------------------------------
  var LOGIN_TO_ORDER = 'Log in to place your order.';
  var LOGIN_LABEL = 'Log in to order';
  var PAUSED_LABEL = 'Ordering is paused';
  var PAUSED_TITLE = 'Online ordering is taking a short pause';
  var PAUSED_TEXT = 'You can still browse the menu.';
  var ALERT_TITLE = 'Confirm your order';
  // pay()'s argument when the checkout alert was confirmed: an object no click event can ever be.
  var ALERT_CONFIRMED = {};
  // force_login_for_ordering (REF getRestaurantLoginRequirement 'ordering'): a guest logs in before Pay.
  function loginRequired(store, customer) { return !!(store && store.settings && store.settings.forceLoginForOrdering) && !customer; }
  // The paused store (REF OrderTimePopup "Online Ordering Is Taking A Short Pause"): online ordering is switched off
  // (store.online.active false: hotelStatus[0].active › online_order_active).
  // P4-06: at a table (table: truthy) the store's table ordering decides (enable_table_side_ordering, store.tableOrdering):
  // the server skips online_order_active for a table order (REF's "Dine-In Paused").
  function pausedOf(store, table) {
    if (table) return !!store && store.tableOrdering === false;
    return !!(store && store.online && store.online.active === false);
  }
  // The checkout alert (REF useCheckoutAlertGate + CheckoutForm L929-937): the store's text for the order type
  // (store.checkoutAlert = { PICKUP, DELIVERY }: checkout_alert_pickup | _delivery while is_checkout_alert_pickup |
  // _delivery is on; normalizeStore's, a model-store.jsx patch), trimmed; null without one.
  function alertText(store, orderType) {
    if (OO.mode === 'kiosk' && store && store.kiosk) return store.kiosk.alerts[OO.kiosk.orderType(store.onlineKey)] || null;
    var a = store && store.checkoutAlert && typeof store.checkoutAlert === 'object' ? store.checkoutAlert : null;
    var t = a ? a[orderType === 'DELIVERY' ? 'DELIVERY' : 'PICKUP'] : null;
    return typeof t === 'string' && t.trim() ? t.trim() : null;
  }
  // A refusal that says the store stopped taking these orders (the checkout's own ecode 800, "Outlet is not accepting
  // orders at the moment"; never the validator's coupon 800, nor the external-delivery one): the page re-reads the
  // store so a pause shows (OO.data.rereadStore).
  function storeOff(e) {
    if (!e || e.kind === 'payload' || Number(e.ecode) !== 800 || Number(e.status) === 404) return false;
    var body = bodyOf(e);
    return typeof body.status !== 'string' && !/delivery\s+service\s+not\s+available/i.test(String(body.message || ''));
  }

  // ---- P4-05: the table (see the header, "Dine-in") ------------------------------------------------------------------
  // The table this checkout orders for: mode qr only. P4-01's context (OO.pages.Table.context: valid, unexpired, this
  // store's), else (a bundle without table.jsx) OO.store.tableFor(hotel), the same context: P4-01 keeps it on OO.store.
  // It must have an id. null otherwise. Read per render: the context can expire (or a re-check clear it) while the page
  // is open.
  function given(v) { return v !== undefined && v !== null && String(v).trim() !== ''; }
  function tableContextOf(hotel) {
    if (OO.mode !== 'qr' || !given(hotel)) return null;
    var T = OO.pages.Table;
    var t = null;
    try {
      if (T && typeof T.context === 'function') t = T.context(hotel) || null;
      else if (OO.store && typeof OO.store.tableFor === 'function') t = OO.store.tableFor(hotel) || null; // P4-INT: P4-01's store API
    } catch (e) { t = null; }
    return t && typeof t === 'object' && given(t.id) ? t : null;
  }
  // is_user_form_table_side (REF: the guest form AND mandatory contact at a table): store.settings.tableUserForm, the
  // boolean model-store.jsx reads from it (flag(d.is_user_form_table_side)); P4-03's guest welcome reads the same key.
  function tableUserForm(store) { return !!(store && store.settings && store.settings.tableUserForm === true); }
  // REF's contact rule at a table: required when the store asks for it or the pick is a gateway (pick: a Choice | null).
  function contactRequired(store, pick) { return tableUserForm(store) || !!(pick && pick.kind === 'gateway'); }
  // The page's summary (K = OO.pages.Cart.shared). At a table (D-20): OO.pricing.summary with the table, no coupon and
  // no points (Cart.shared.summaryOf would take OO.store's points in use off), exactly the options the payload's own
  // summary gets from payloadCtx (no walletAmount, no now), so the total shown is the one charged. Else summaryOf.
  // QR-CART: the cart's table pricing too (Checkout.logic), with tablePc(null).
  function summarize(K, lines, store, coupon, pc, table) {
    if (!table) return K.summaryOf(lines, store, coupon, pc, null);
    return OO.pricing.summary(lines, store, { paymentMethod: pc.paymentMethod, feePaymentKey: pc.feePaymentKey, coupon: null, orderType: pc.orderType, deliveryFee: 0, table: table });
  }
  // P4-05: a table order's pricing context (pick: the table's Choice | null): 'DINEIN', no zone, no delivery fee, the
  // pick's method; before the pick a counter order (no card surcharge; REF prices it as cash). QR-CART: the cart's too.
  function tablePc(pick) {
    return { orderType: 'DINEIN', paymentMethod: pick ? pick.key : 'PAY_AT_COUNTER', zone: null, deliveryFee: 0 };
  }
  // POLISH-1: the verdict on the line checks (see the header): any issue blocks; time-bound only with a later time named.
  function issueState(issues, at) {
    var keys = Object.keys(issues || {});
    return {
      blocked: keys.length > 0,
      timeBound: !!at && keys.some(function (k) { return !!issues[k] && issues[k].code === 'unavailable'; }),
    };
  }
  // P4-06: Pay's re-check of the table (see the header): P4-01's revalidate (it re-reads the table; null = gone, locked
  // or disabled). Never rejects.
  function recheckTable(hotel) {
    var T = OO.pages.Table;
    if (!T || typeof T.revalidate !== 'function') return Promise.resolve({ ok: true, table: null, why: null });
    return Promise.resolve().then(function () { return T.revalidate(hotel); }).then(function (t) {
      return t && typeof t === 'object' && given(t.id) ? { ok: true, table: t, why: null } : { ok: false, table: null, why: 'gone' };
    }, function () { return { ok: false, table: null, why: 'unchecked' }; });
  }

  // The E.164 phone picker (P2-24; REF shared/components/ui/PhoneInput.tsx + utils/phoneCountry.ts, REF's countries).
  var PHONE_COUNTRIES = [
    ['NZ', 'New Zealand', '64'], ['AU', 'Australia', '61'], ['US', 'United States', '1'], ['GB', 'United Kingdom', '44'],
    ['CA', 'Canada', '1'], ['IE', 'Ireland', '353'], ['IN', 'India', '91'], ['SG', 'Singapore', '65'],
    ['AE', 'United Arab Emirates', '971'], ['AR', 'Argentina', '54'], ['AT', 'Austria', '43'], ['BD', 'Bangladesh', '880'],
    ['BE', 'Belgium', '32'], ['BR', 'Brazil', '55'], ['CH', 'Switzerland', '41'], ['CL', 'Chile', '56'], ['CN', 'China', '86'],
    ['CO', 'Colombia', '57'], ['CZ', 'Czechia', '420'], ['DE', 'Germany', '49'], ['DK', 'Denmark', '45'], ['EG', 'Egypt', '20'],
    ['ES', 'Spain', '34'], ['FI', 'Finland', '358'], ['FJ', 'Fiji', '679'], ['FR', 'France', '33'], ['GR', 'Greece', '30'],
    ['HK', 'Hong Kong', '852'], ['HU', 'Hungary', '36'], ['ID', 'Indonesia', '62'], ['IL', 'Israel', '972'], ['IT', 'Italy', '39'],
    ['JP', 'Japan', '81'], ['KE', 'Kenya', '254'], ['KR', 'South Korea', '82'], ['KW', 'Kuwait', '965'], ['LK', 'Sri Lanka', '94'],
    ['MX', 'Mexico', '52'], ['MY', 'Malaysia', '60'], ['NG', 'Nigeria', '234'], ['NL', 'Netherlands', '31'], ['NO', 'Norway', '47'],
    ['NP', 'Nepal', '977'], ['PH', 'Philippines', '63'], ['PK', 'Pakistan', '92'], ['PL', 'Poland', '48'], ['PT', 'Portugal', '351'],
    ['QA', 'Qatar', '974'], ['RO', 'Romania', '40'], ['RU', 'Russia', '7'], ['SA', 'Saudi Arabia', '966'], ['SE', 'Sweden', '46'],
    ['TH', 'Thailand', '66'], ['TR', 'Türkiye', '90'], ['TW', 'Taiwan', '886'], ['UA', 'Ukraine', '380'], ['VN', 'Vietnam', '84'],
    ['ZA', 'South Africa', '27'], ['WS', 'Samoa', '685'], ['TO', 'Tonga', '676'], ['PG', 'Papua New Guinea', '675'],
    ['VU', 'Vanuatu', '678'], ['SB', 'Solomon Islands', '677'], ['CK', 'Cook Islands', '682'], ['BH', 'Bahrain', '973'],
    ['OM', 'Oman', '968'], ['JO', 'Jordan', '962'], ['LB', 'Lebanon', '961'], ['MA', 'Morocco', '212'], ['GH', 'Ghana', '233'],
    ['MM', 'Myanmar', '95'], ['KH', 'Cambodia', '855'], ['BN', 'Brunei', '673'], ['MV', 'Maldives', '960'], ['BT', 'Bhutan', '975'],
  ].map(function (c) { return { iso: c[0], name: c[1], dial: c[2] }; });
  function phoneCountry(iso) {
    for (var i = 0; i < PHONE_COUNTRIES.length; i++) if (PHONE_COUNTRIES[i].iso === iso) return PHONE_COUNTRIES[i];
    return PHONE_COUNTRIES[0];
  }
  // The picker's default country: the one whose code OO.payload.e164 gives a number typed without one (store.address
  // .country, else the currency, else the region, else NZ), so an untouched number shows the code it will go with.
  function phoneDefault(store) {
    var plus = OO.payload && typeof OO.payload.e164 === 'function' ? String(OO.payload.e164('1', store || {})) : '+641';
    var dial = plus.replace(/^\+/, '').slice(0, -1);
    for (var i = 0; i < PHONE_COUNTRIES.length; i++) if (PHONE_COUNTRIES[i].dial === dial) return PHONE_COUNTRIES[i].iso;
    return 'NZ';
  }
  // REF splitInternationalDigits: the country whose code the digits start with (the fallback's first, else the longest).
  function splitDigits(digits, fallback) {
    var fb = phoneCountry(fallback);
    if (digits.indexOf(fb.dial) === 0) return { iso: fb.iso, national: digits.slice(fb.dial.length) };
    var best = null;
    PHONE_COUNTRIES.forEach(function (c) { if (digits.indexOf(c.dial) === 0 && (!best || c.dial.length > best.dial.length)) best = c; });
    return best ? { iso: best.iso, national: digits.slice(best.dial.length) } : { iso: fb.iso, national: digits };
  }
  // A phone as the picker shows it → { iso, national }: the number as the payload would send it (OO.payload.e164 at
  // `store`), split at its country code; nothing → { fallback, '' }.
  function phoneParts(value, fallback, store) {
    var s = String(value === undefined || value === null ? '' : value).trim();
    if (!s) return { iso: fallback, national: '' };
    var e = OO.payload && typeof OO.payload.e164 === 'function' ? String(OO.payload.e164(s, store || {})) : s;
    if (e.charAt(0) === '+') return splitDigits(e.slice(1).replace(/\D/g, ''), fallback);
    return { iso: fallback, national: s.replace(/\D/g, '') };
  }
  // What the picker writes: '+{code}{digits}' (REF emit; NZ / AU leading zeros dropped), '' without digits.
  function phoneValue(iso, national) {
    var digits = String(national === undefined || national === null ? '' : national).replace(/\D/g, '');
    if ((iso === 'NZ' || iso === 'AU') && /^0+[1-9]/.test(digits)) digits = digits.replace(/^0+/, '');
    return digits ? '+' + phoneCountry(iso).dial + digits : '';
  }
  // REF isValidMobileNumber: digits, spaces and ( ) . - with one leading '+' at most, 8–15 digits. Its national NZ / AU
  // mobile patterns for a number without its code are not used: the picker shows the code a number goes with.
  function phoneValid(value) {
    var raw = String(value === undefined || value === null ? '' : value).trim();
    if (!/^\+?[0-9\s().-]+$/.test(raw)) return false;
    var digits = raw.replace(/\D/g, '');
    return digits.length >= 8 && digits.length <= 15;
  }

  // Contact + delivery checks, run on Pay (field messages show after the first attempt, then live). zoneBlock: the
  // message that stops a delivery order (deliveryState's block), or null. optional (P4-05: a table order whose contact
  // the store doesn't ask for and no gateway needs, REF): nothing is required, and a phone or email that is filled in
  // must still be valid.
  function validate(draft, delivery, zoneBlock, optional) {
    var e = {};
    if (!optional && String(draft.name || '').trim().length < 2) e.name = 'Enter your full name';
    var phone = String(draft.phone || '').trim();
    if (!phone) { if (!optional) e.phone = 'Enter your mobile number'; }
    else if (!phoneValid(phone)) e.phone = 'Enter a valid mobile number'; // P2-24: REF's rule (8–15 digits)
    var email = String(draft.email || '').trim();
    if (!email) { if (!optional) e.email = 'Enter your email address'; }
    else if (!EMAIL.test(email)) e.email = 'Enter a valid email address';
    if (delivery) {
      if (!String(draft.address || '').trim()) e.address = 'Enter your delivery address';
      if (zoneBlock) e.zone = zoneBlock;
    }
    return e;
  }

  // The Pay button (see the header): { label, enabled, canPay (pay() may run), view (the button opens the order a
  // "may exist" lookup found, instead of paying) }. v = { count, typeBlocked, blocked, timeBound, slot, isDelivery,
  // checking, method (the usable method chosen | null), terms, placing, waiting (a kind 'retry' wait), unconfirmed
  // (null | { phase: 'wait' | 'again' | 'found' }), online, total (the total as text) }. G1 fix B: no usable method
  // means no Pay ("No payment method available", never a Pay at Counter the store doesn't offer); 'wait' keeps Pay
  // disabled, 'again' reads "Place it again" (one explicit tap places a new order), 'found' is "View your order".
  function payState(v) {
    var u = v.unconfirmed || null;
    if (u && u.phase === 'found') return { label: VIEW_ORDER, enabled: true, canPay: false, view: true };
    // WL-2: a white-label studio preview never pays (OO.payload refuses too).
    if (v.preview) return { label: PREVIEW_LABEL, enabled: false, canPay: false, view: false };
    var label = !(v.count > 0) ? 'Cart is empty'
      : v.paused ? PAUSED_LABEL // P2-24
      : v.typeBlocked ? 'Delivery not available'
      : v.blocked ? (v.timeBound ? 'Choose another time' : 'Review your cart')
      : !v.slot ? (v.isDelivery ? 'No delivery times available' : 'No pickup times available')
      : v.checking ? 'Checking delivery…'
      : !v.method && v.choose ? CHOOSE_METHOD // P4-05: Pay asks for the table's pick
      : !v.method ? NO_METHOD
      : !v.terms ? 'Accept terms to pay'
      : u && u.phase === 'again' ? PLACE_AGAIN
      : v.login ? LOGIN_LABEL // P2-24: Pay opens the login sheet
      : v.online ? 'Pay ' + v.total : 'Place order · ' + v.total;
    var canPay = v.count > 0 && !v.paused && !v.typeBlocked && !v.blocked && !!v.slot && !v.checking && (!!v.method || !!v.choose) &&
      (!!v.terms || (!v.method && !!v.choose)) && !v.placing && !v.waiting && !(u && u.phase === 'wait');
    return { label: label, enabled: canPay, canPay: canPay, view: false };
  }

  // G1 fix B: the server's time of an online_orders row: its UTC 'YYYY-MM-DD HH:MM:SS' (the app timezone is UTC), or
  // an ISO string with a zone. ms, else null.
  function serverMs(v) {
    var m = typeof v === 'string' ? /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i.exec(v.trim()) : null;
    if (!m) return null;
    var ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
    var z = m[7] && m[7].toUpperCase() !== 'Z' ? /^([+-])(\d{2}):?(\d{2})$/.exec(m[7]) : null;
    if (z) ms -= (z[1] === '-' ? -1 : 1) * (Number(z[2]) * 60 + Number(z[3])) * 60000;
    return isFinite(ms) ? ms : null;
  }
  // G1 fix B: the order a "may exist" failure may have created, in customer/getOrders' answer (E17: { status, data:
  // paginator }; its rows are online_orders, pending and unpaid ones included). opts = { total (the payable sent),
  // paymentType (the body's payment_type), now (Date | ms) }. A row matches when it was created within 10 minutes of
  // now (either side: the device clock may be off), carries the payment_type sent, and its payable (billTotal = the
  // stored payable_amount; its total when it has none) is that total in cents. → { orderId, createdAt (ms) } of the
  // newest match | null. Pure.
  function findPlacedOrder(raw, opts) {
    var o = opts || {};
    var body = raw && typeof raw === 'object' ? raw : {};
    var page = body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : body;
    var rows = Array.isArray(page.data) ? page.data : (Array.isArray(body.data) ? body.data : (Array.isArray(raw) ? raw : []));
    var want = centsOf(o.total);
    var nowMs = o.now === undefined || o.now === null ? NaN : Number(o.now);
    var type = String(o.paymentType === undefined || o.paymentType === null ? '' : o.paymentType).trim().toLowerCase();
    if (want === null || !isFinite(nowMs)) return null;
    var best = null;
    rows.forEach(function (r) {
      if (!r || typeof r !== 'object' || r.order_id === undefined || r.order_id === null || r.order_id === '') return;
      var at = serverMs(r.created_at);
      if (at === null || Math.abs(nowMs - at) > ORDER_MATCH_MS) return;
      if (type && String(r.payment_type === undefined || r.payment_type === null ? '' : r.payment_type).trim().toLowerCase() !== type) return;
      var paid = r.billTotal !== undefined && r.billTotal !== null && r.billTotal !== '' ? r.billTotal : r.total;
      paid = Number(paid) + Number(r.tip_amount || 0);
      if (centsOf(paid) !== want) return;
      if (!best || at > best.createdAt) best = { orderId: String(r.order_id), createdAt: at };
    });
    return best;
  }

  // payment_url: the gateway (full page) or, in fixture mode, this SPA's own payment-callback (in-app, replacing
  // the checkout entry so Back doesn't return to a spent checkout).
  // P5-06b (payTarget; a tag once showed customers a fake payment page): only an http(s) URL is ever opened. Any other
  // scheme (javascript:, data:, blob:, vbscript:, file:, … in any case, after leading spaces or control characters, split
  // by tabs or newlines: what the URL parser itself reads), a blank or a non-string is REFUSED: pay() shows the payment
  // that couldn't start (G1 fix H's state) and nothing navigates. On this origin, a route the SPA serves under routeBase
  // stays in-app (OO.router.go); a path outside routeBase (the dev server's PIN page) or one the router reads as no page
  // of its own (a store-less not-found: a reserved first segment such as /api, a machine route) is a full-page navigation,
  // as another origin is. No host allowlist (owner, 2026-10-05).
  // Embedded storefront (a store's site frames this SPA): a cross-origin gateway mustn't load inside the frame (most
  // refuse to be framed, and the return URL must land in the top window), so the TOP window goes there. A browser that
  // blocks that (sandboxed frame, no user activation left) throws, so a new tab opens instead; failing both, the frame
  // itself navigates. (Batch 2 gaps: BATCH2-GAPS.md "Embedded storefronts".) P2-28: where a framed checkout opens the
  // gateway is the gateway's (OO.pages.acct.paycb.navigation: the top window for every hosted gateway page today); a
  // full-page navigation on this origin goes the same way.
  function framed() { try { return window.top !== window.self; } catch (e) { return true; } }
  // A root-relative path the URL parser reads exactly as written: no '//' or '\' after the first '/', no tab or newline.
  var ROOT_PATH = /^\/(?![\/\\])[^\t\n\r\\]*$/;
  // The page's URL, the route base and the mode payTarget judges against (read at call time, as the router does).
  function payEnv() {
    var l = window.location || {};
    var href = typeof l.href === 'string' && l.href ? l.href : String(l.origin || '') + String(l.pathname || '/') + String(l.search || '');
    return { href: href, routeBase: (window.__SIPO_BOOT || {}).routeBase, mode: OO.mode };
  }
  // P5-06b: payTarget(url, { href, routeBase, mode }) → { kind: 'refuse' } | { kind: 'app', to (path + query + hash) } |
  // { kind: 'page', to (the URL as parsed: absolute when it can be) } (see above). Pure.
  function payTarget(url, env) {
    var e = env || {};
    if (typeof url !== 'string' || !url.trim()) return { kind: 'refuse' };
    var served = function (path) {
      var r = OO.router.parse(path.split('#')[0], '', e.routeBase, e.mode);
      return !(r.page === 'not-found' && !r.hotel);
    };
    var hasURL = typeof URL === 'function';
    if (ROOT_PATH.test(url)) {
      if (served(url)) return { kind: 'app', to: url };
      var abs = url;
      if (hasURL && e.href) { try { abs = new URL(url, e.href).href; } catch (x) { abs = url; } }
      return { kind: 'page', to: abs };
    }
    if (!hasURL) return { kind: 'refuse' };
    var u;
    try { u = e.href ? new URL(url, e.href) : new URL(url); } catch (x) { return { kind: 'refuse' }; }
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return { kind: 'refuse' };
    var here = null;
    try { here = e.href ? new URL(e.href).origin : null; } catch (x) { here = null; }
    if (here && u.origin === here && served(u.pathname + u.search)) return { kind: 'app', to: u.pathname + u.search + u.hash };
    return { kind: 'page', to: u.href };
  }
  // Opens a payment_url (above) → true, or false when payTarget refused it (nothing navigates).
  function goTo(url, gateway) {
    var t = payTarget(url, payEnv());
    if (t.kind === 'refuse') return false;
    if (t.kind === 'app') { OO.router.go(t.to, { replace: true }); return true; }
    var s = t.to;
    var paycb = OO.pages.acct && OO.pages.acct.paycb;
    if ((paycb ? paycb.navigation(gateway, { framed: framed() }) : (framed() ? 'top' : 'self')) === 'top') {
      try { window.top.location.href = s; return true; } catch (e) { /* cross-origin top navigation blocked */ }
      var w = null;
      try { w = window.open(s, '_blank'); } catch (e) { w = null; }
      if (w) { try { w.opener = null; } catch (e) { /* ignore */ } return true; }
    }
    window.location.assign(s);
    return true;
  }
  // P5-06b: a refused payment_url, as the payment that couldn't start (errorState's kind 'paymentStart').
  function notStarted(orderId) {
    var err = new Error(PAY_NOT_STARTED);
    err.kind = 'client';
    err.status = 200;
    err.data = { failure_code: START_FAILED, order_id: orderId === undefined ? null : orderId };
    return err;
  }

  // G1 fix B: the name a new customer is registered with: the contact's, trimmed, 1–191 characters; null when it has
  // none (customer/register refuses an empty name with a 422, after the single-use code is spent).
  function signupName(contact) {
    var name = String((contact && contact.name) || '').trim();
    return name && name.length <= NAME_MAX ? name : null;
  }

  // After verify-otp: an existing customer is signed in and refreshed from customer/getdata (phone, ids, points); a
  // new email (customer: null) is registered with the backend's uid (OO.data.signupUid: the custom token's `uid` claim,
  // never invented for a real store) and the contact typed so far. No uid: rejects { signupFailed: true, message:
  // 'Sign up failed. Please try again.' } without calling register. G1 fix B: no usable name in the contact
  // (signupName) rejects { needName: true } without calling register either: the login sheet asks for the name and
  // calls signIn again with the same verify-otp answer (the code is single-use; the uid is in the answer).
  function signIn(hotel, res, email, contact) {
    var token = (res && res.customToken) || null;
    if (res && res.customer) {
      var c = OO.model.normalizeCustomer(res);
      if (!c) return Promise.reject(new Error('Login failed'));
      OO.store.setSession({ customer: c, customToken: token });
      return OO.api.getCustomer(hotel, c.uid).then(function (raw) {
        var full = OO.model.normalizeCustomer(raw);
        if (!full) return c;
        OO.store.setSession({ customer: full, customToken: token });
        return full;
      }, function () { return c; });
    }
    var uid = OO.data.signupUid(hotel, res);
    if (!uid) {
      var failed = new Error('Sign up failed. Please try again.');
      failed.signupFailed = true;
      return Promise.reject(failed);
    }
    var name = signupName(contact);
    if (!name) {
      var ask = new Error('Enter your name to finish signing up.');
      ask.needName = true;
      return Promise.reject(ask);
    }
    var phone = String((contact && contact.phone) || '').replace(/[^\d+]/g, '');
    return OO.api.register(hotel, {
      uid: uid, name: name, email: email,
      phone_no: phone.length <= 15 ? phone : '', birthday: null,
    }).then(function (r) {
      var c = OO.model.normalizeCustomer(r);
      if (!c) throw new Error('Sign up failed');
      OO.store.setSession({ customer: c, customToken: token }); // P3-09: a uid is no token (B16 would answer 401)
      return c;
    });
  }

  // ---- login (email OTP) sheet -------------------------------------------------------------------------------
  // Steps: 'email' → 'code' → (G1 fix B: a new email whose contact has no usable name) 'name' ("Your name", required,
  // ≤ 191 characters) → register. The name step keeps verify-otp's answer, so the customer never asks for a new code; a
  // register failure stays on it (Create account again).
  // P3-08: the profile login's OTP rules (OO.pages.Profile.logic, read at render: profile.jsx comes later in the
  // bundle). The email is lower-cased once, at send, and verified as sent; "Resend code" opens 60 s after a send ("Resend
  // code in 42s"), and the same address inside that minute goes back to its code without a new mail; the sixth digit
  // verifies once (one request in flight; never the code just refused); a 429 shows the backend limiter's words.
  function LoginSheet(props) {
    var PL = OO.pages.Profile.logic;
    var hotel = props.hotel;
    var stepS = React.useState('email');
    var emailS = React.useState(props.email || '');
    var codeS = React.useState('');
    var nameS = React.useState('');
    var busyS = React.useState(false);
    var errS = React.useState(null);    // { field: 'email' | 'code' | 'name' | null, text }
    var infoS = React.useState(null);   // status line after a resend
    var sentS = React.useState(null);   // P3-08: the last code mailed { email (lower-cased), at (ms) }
    var nowS = React.useState(function () { return Date.now(); }); // the countdown's clock
    var inflightRef = React.useRef(false); // a send / verify in flight: nothing else starts
    var refusedRef = React.useRef(null);   // the code verify-otp last refused (400): not auto-sent again
    var emailRef = React.useRef(null);
    var codeRef = React.useRef(null);
    var nameRef = React.useRef(null);
    var pendingRef = React.useRef(null); // the name step's { res (verify-otp's answer), email }
    var alive = React.useRef(true);
    var step = stepS[0];
    var err = errS[0];
    var sentAt = sentS[0] ? sentS[0].at : null;
    var left = PL.cooldownLeft(sentAt, nowS[0]);

    React.useEffect(function () { alive.current = true; return function () { alive.current = false; }; }, []);
    // P3-08: the countdown (code step only) re-reads the clock when its next second is due.
    React.useEffect(function () {
      if (!left || step !== 'code') return undefined;
      var id = setTimeout(function () { if (alive.current) nowS[1](Date.now()); }, (sentAt + PL.COOLDOWN_S * 1000 - nowS[0]) % 1000 || 1000);
      return function () { clearTimeout(id); };
    }, [sentAt, nowS[0], step]);
    // Runs after the Sheet (a child) has focused its panel: move focus to the step's field.
    React.useEffect(function () {
      Kit().focusEl(step === 'code' ? codeRef.current : step === 'name' ? nameRef.current : emailRef.current);
    }, [step]);

    function sendCode(e, resend) {
      if (e && e.preventDefault) e.preventDefault();
      var last = sentS[0];
      var email = resend && last ? last.email : PL.otpEmail(emailS[0]);
      if (!EMAIL.test(email)) {
        errS[1]({ field: 'email', text: 'Enter a valid email address' });
        stepS[1]('email');
        Kit().focusEl(emailRef.current);
        return;
      }
      if (inflightRef.current) return;
      var cooling = last && last.email === email && PL.cooldownLeft(last.at, Date.now()) > 0;
      if (cooling && resend) return;
      if (cooling) { nowS[1](Date.now()); errS[1](null); infoS[1](null); stepS[1]('code'); return; } // that code still works
      inflightRef.current = true;
      busyS[1](true); errS[1](null); infoS[1](null);
      OO.api.sendOtp(hotel, email).then(function () {
        inflightRef.current = false;
        if (!alive.current) return;
        var t = Date.now();
        busyS[1](false);
        sentS[1]({ email: email, at: t });
        nowS[1](t);
        refusedRef.current = null;
        codeS[1]('');
        stepS[1]('code');
        if (resend) { infoS[1]('We sent a new code.'); Kit().focusEl(codeRef.current); }
      }, function (x) {
        inflightRef.current = false;
        if (!alive.current) return;
        busyS[1](false);
        var limited = x && Number(x.status) === 429;
        if (limited && resend) { var t = Date.now(); sentS[1]({ email: email, at: t }); nowS[1](t); } // don't reopen at once
        var bad = x && x.status === 422;
        var fail = "We couldn't send a code. Please try again.";
        errS[1]({ field: bad && step === 'email' ? 'email' : null, text: bad ? 'Enter a valid email address' : limited ? PL.limitText(x, fail) : fail });
      });
    }
    // `typed`: the code the sixth digit completed (autosubmit); else the field's.
    function verify(e, typed) {
      if (e && e.preventDefault) e.preventDefault();
      if (inflightRef.current) return;
      var code = typed != null ? typed : codeS[0].trim();
      if (!/^\d{6}$/.test(code)) {
        errS[1]({ field: 'code', text: 'Enter the 6-digit code' });
        Kit().focusEl(codeRef.current);
        return;
      }
      var email = sentS[0] ? sentS[0].email : PL.otpEmail(emailS[0]);
      var isNew = false;
      var answer = null;
      inflightRef.current = true;
      busyS[1](true); errS[1](null); infoS[1](null);
      OO.api.verifyOtp(hotel, email, code).then(function (res) {
        answer = res;
        isNew = !(res && res.customer);
        return signIn(hotel, res, email, props.contact);
      }).then(function (c) {
        inflightRef.current = false;
        if (alive.current) props.onDone(c, isNew);
      }, function (x) {
        inflightRef.current = false;
        if (!alive.current) return;
        busyS[1](false);
        if (x && x.needName) {
          // A new email and no name to register with yet: ask for it (the code is spent; keep its answer).
          pendingRef.current = { res: answer, email: email };
          nameS[1](String((props.contact && props.contact.name) || '').trim());
          errS[1](null);
          stepS[1]('name');
          return;
        }
        var bad = x && x.status === 400;
        if (bad) refusedRef.current = code;
        var fail = "We couldn't log you in. Please try again.";
        var text = bad ? 'That code is invalid or has expired. Check it and try again.' : (x && x.signupFailed ? x.message : x && Number(x.status) === 429 ? PL.limitText(x, fail) : fail);
        errS[1]({ field: bad ? 'code' : null, text: text });
        if (bad) Kit().focusEl(codeRef.current);
      });
    }
    // The name step: register with the name typed here (signIn again, with the kept verify-otp answer).
    function finishSignup(e) {
      if (e && e.preventDefault) e.preventDefault();
      var name = nameS[0].trim();
      var bad = !name ? 'Enter your name' : name.length > NAME_MAX ? 'Use ' + NAME_MAX + ' characters or fewer' : null;
      if (bad) {
        errS[1]({ field: 'name', text: bad });
        Kit().focusEl(nameRef.current);
        return;
      }
      var p = pendingRef.current;
      if (!p) { errS[1]({ field: null, text: 'Sign up failed. Please try again.' }); return; }
      busyS[1](true); errS[1](null);
      signIn(hotel, p.res, p.email, { name: name, phone: props.contact && props.contact.phone }).then(function (c) {
        if (alive.current) props.onDone(c, true);
      }, function (x) {
        if (!alive.current) return;
        busyS[1](false);
        errS[1]({ field: null, text: x && x.signupFailed ? x.message : "We couldn't create your account. Please try again." });
      });
    }
    function changeEmail() { errS[1](null); infoS[1](null); codeS[1](''); pendingRef.current = null; stepS[1]('email'); }

    var general = err && !err.field ? <ui.InlineNotice tone="danger" role="alert">{err.text}</ui.InlineNotice> : null;
    return (
      <ui.Sheet open variant="dialog" title={step === 'name' ? 'Create your account' : 'Log in'} onClose={props.onClose} className="oo-login-sheet">
        {step === 'name' ? (
          <form className="oo-login" onSubmit={finishSignup} noValidate>
            <p className="oo-login__lead">Welcome! Add your name to finish signing up.</p>
            <ui.TextInput label="Your name" labelVisible name="name" autoComplete="name" required maxLength={NAME_MAX}
              value={nameS[0]} inputRef={nameRef}
              onChange={function (v) { nameS[1](v); if (err) errS[1](null); }}
              error={err && err.field === 'name' ? err.text : null} />
            {general}
            <ui.Button type="submit" block busy={busyS[0]}>Create account</ui.Button>
            <div className="oo-login__links">
              <ui.Button variant="link" disabled={busyS[0]} onClick={changeEmail}>Use a different email</ui.Button>
            </div>
          </form>
        ) : step === 'email' ? (
          <form className="oo-login" onSubmit={sendCode} noValidate>
            <p className="oo-login__lead">Enter your email and we’ll send you a 6-digit code to log in.</p>
            <ui.TextInput label="Email" labelVisible type="email" name="email" autoComplete="email" inputMode="email"
              value={emailS[0]} inputRef={emailRef}
              onChange={function (v) { emailS[1](v); if (err) errS[1](null); }}
              error={err && err.field === 'email' ? err.text : null} />
            {general}
            <ui.Button type="submit" block busy={busyS[0]}>Send code</ui.Button>
          </form>
        ) : (
          <form className="oo-login" onSubmit={verify} noValidate>
            <p className="oo-login__lead">We sent a code to <strong>{sentS[0] ? sentS[0].email : emailS[0].trim()}</strong>.</p>
            <ui.TextInput label="6-digit code" labelVisible name="otp" inputMode="numeric" autoComplete="one-time-code"
              value={codeS[0]} inputRef={codeRef}
              onChange={function (v) {
                var c = PL.otpCode(v);
                var was = codeS[0];
                codeS[1](c);
                if (err) errS[1](null);
                if (c.length === 6 && c !== was && c !== refusedRef.current) verify(null, c); // P3-08: the sixth digit
              }}
              error={err && err.field === 'code' ? err.text : null}
              hint={OO.api.isFixture ? 'Preview: use code 123456' : null} />
            {infoS[0] ? <p className="oo-login__info" role="status">{infoS[0]}</p> : null}
            {general}
            <ui.Button type="submit" block busy={busyS[0]}>Verify and log in</ui.Button>
            <div className="oo-login__links">
              <ui.Button variant="link" onClick={changeEmail}>Use a different email</ui.Button>
              {left > 0 ? <span className="oo-login__wait">{'Resend code in ' + left + 's'}</span> : (
                <ui.Button variant="link" disabled={busyS[0]} onClick={function () { sendCode(null, true); }}>Resend code</ui.Button>
              )}
            </div>
          </form>
        )}
      </ui.Sheet>
    );
  }

  // ---- P2-24: the mobile number with its country code, and the checkout alert ------------------------------------------
  // <PhoneField store value onChange error inputRef required /> (REF PhoneInput): a country code select (PHONE_COUNTRIES, the
  // store's by default: phoneDefault) beside the number. What is typed stays as typed; the draft gets phoneValue's
  // E.164 ('+64211234567'; '' with no digits). Typing a number with its code ('+61 …', '0061 …') picks that country. A
  // value changed from outside (the login fills it, a cleared draft) is read again (phoneParts). required: default true
  // (P4-05: false at a table whose contact is optional).
  var PHONE_ID = 'oo-checkout-tel';
  function PhoneField(props) {
    var store = props.store;
    var fallback = phoneDefault(store);
    var start = phoneParts(props.value, fallback, store);
    var isoS = React.useState(start.iso);
    var natS = React.useState(start.national);
    var sentRef = React.useRef(props.value); // the value this field wrote last
    React.useEffect(function () {
      if (props.value === sentRef.current) return;
      var p = phoneParts(props.value, fallback, store);
      sentRef.current = props.value;
      isoS[1](p.iso);
      natS[1](p.national);
    }, [props.value, fallback]);
    function emit(iso, national) {
      var v = phoneValue(iso, national);
      sentRef.current = v;
      props.onChange(v);
    }
    function typed(raw) {
      if (/^\s*(\+\d|00\d)/.test(raw)) {
        var p = phoneParts(raw, isoS[0], store);
        isoS[1](p.iso);
        natS[1](p.national);
        emit(p.iso, p.national);
        return;
      }
      var kept = raw.replace(/[^\d\s().-]/g, '');
      natS[1](kept);
      emit(isoS[0], kept);
    }
    var c = phoneCountry(isoS[0]);
    var err = props.error || null;
    return (
      <div className={cx('oo-field oo-phone', err && 'is-invalid')}>
        <label htmlFor={PHONE_ID} className="oo-sr-only">Mobile number</label>
        <div className="oo-field__box oo-phone__box">
          <span className="oo-phone__cc">
            <span className="oo-phone__dial" aria-hidden="true">{'+' + c.dial}</span>
            <OO.icons.CaretDown size={12} className="oo-phone__caret" />
            <select className="oo-phone__select" name="tel-country" aria-label="Country code" value={c.iso}
              onChange={function (e) { isoS[1](e.target.value); emit(e.target.value, natS[0]); }}>
              {PHONE_COUNTRIES.map(function (x) { return <option key={x.iso} value={x.iso}>{x.name + ' (+' + x.dial + ')'}</option>; })}
            </select>
          </span>
          <input id={PHONE_ID} ref={props.inputRef} className="oo-field__input oo-phone__input" type="tel" name="tel"
            autoComplete={OO.mode === 'kiosk' ? 'off' : 'tel-national'} inputMode="tel" required={props.required !== false} placeholder="Mobile number" value={natS[0]}
            aria-invalid={err ? 'true' : undefined} aria-describedby={err ? PHONE_ID + '-err' : undefined}
            onChange={function (e) { typed(e.target.value); }} />
        </div>
        {err ? <div className="oo-field__msg" id={PHONE_ID + '-err'}>{err}</div> : null}
      </div>
    );
  }

  // <CheckoutAlert text busy onPlace onClose /> (REF OnlineOrderCheckoutAlert): "Confirm your order", the store's text
  // (its line breaks kept) and one "Place order", which places the order; closing it places nothing.
  function CheckoutAlert(props) {
    return (
      <ui.Sheet open variant="dialog" title={ALERT_TITLE} onClose={props.onClose} className="oo-checkout-alert-sheet">
        <div className="oo-checkout-alert">
          <p className="oo-checkout-alert__text">{props.text}</p>
          <ui.Button block disabled={!!props.busy} onClick={props.onPlace}>Place order</ui.Button>
        </div>
      </ui.Sheet>
    );
  }

  // ---- page -------------------------------------------------------------------------------------------------------
  OO.pages.Checkout = function Checkout(props) {
    var K = Kit();
    var route = props.route;
    var hotel = route.hotel;
    var kiosk = OO.mode === 'kiosk';
    var o = { hotel: hotel };
    var P = K.useCartPage(hotel);
    var wide = ui.useWide();
    var submittedS = React.useState(false); // Pay was pressed: show field errors
    var placingS = React.useState(false);
    var uncertainVisit = kiosk && OO.kiosk.visit(hotel) && OO.kiosk.visit(hotel).unconfirmed;
    var noticeS = React.useState(uncertainVisit ? { kind: 'unconfirmed', text: 'The last order could not be confirmed. Please ask a staff member to check before placing it again.' } : null);
    var loginS = React.useState(false);
    var paymentUse = CS.payment.use();      // P2-00b: the payment method list's state (checkout-payment.jsx)
    var foldS = React.useState(false);      // collapsed price summary expanded (mobile)
    var toastS = React.useState(null);      // { id, text }
    var freshS = React.useState(null);      // { sig, issues }: what the fresh menu at Pay can't sell (see pay())
    var waitS = React.useState(false);      // a kind 'retry' rejection: Pay disabled for RETRY_MS
    var unconfS = React.useState(uncertainVisit ? { phase: 'again', orderId: null } : null); // survives a kiosk reload
    React.useEffect(function () {
      if (!kiosk) return undefined;
      OO.kiosk.hold(placingS[0] || !!unconfS[0]);
      return function () { OO.kiosk.hold(false); };
    }, [kiosk, placingS[0], unconfS[0]]);
    var alertS = React.useState(null);      // P2-24: the checkout alert's text while it asks for confirmation
    var nameRef = React.useRef(null);
    var phoneRef = React.useRef(null);
    var emailRef = React.useRef(null);
    var addressRef = React.useRef(null);
    var zoneRef = React.useRef(null);
    var noticeRef = React.useRef(null);
    var blockedRef = React.useRef(null);
    var foldRef = React.useRef(null);
    var loginRef = React.useRef(null);
    var placedRef = React.useRef(false);
    var busyRef = React.useRef(false);      // a placeOrder in flight (sync guard against a double click)
    var aliveRef = React.useRef(true);
    var waitRef = React.useRef(null);       // the retry timer
    var unconfRef = React.useRef({ episode: null, timers: [] }); // G1 fix B: the "may exist" episode and its timers
    var prefilled = React.useRef(false);

    React.useEffect(function () {
      aliveRef.current = true;
      return function () {
        aliveRef.current = false;
        clearTimeout(waitRef.current);
        unconfRef.current.timers.forEach(function (t) { clearTimeout(t); });
      };
    }, []);
    // G1 fix B: Back from a payment gateway can restore this page from the bfcache exactly as it was left: "Placing
    // your order…" with Pay locked. The lock is reset. An order that went to its gateway also reloads the page: the
    // payment callback may have settled it in another document since (the cart and the draft in storage moved on;
    // this page's copy didn't), so only a fresh load shows the truth.
    React.useEffect(function () {
      if (typeof window.addEventListener !== 'function') return undefined;
      function onShow(e) {
        if (!e || !e.persisted) return;
        var placed = placedRef.current;
        busyRef.current = false;
        placedRef.current = false;
        placingS[1](false);
        if (placed) {
          try { window.location.reload(); } catch (x) { /* the reset above stands */ }
        }
      }
      window.addEventListener('pageshow', onShow);
      return function () { window.removeEventListener('pageshow', onShow); };
    }, []);
    var walletUse = CS.wallet.use({ hotel: hotel, P: P }); // P2-00b / P2-08: the wallet customer_id and the pre-flight read
    var walletRef = walletUse.walletRef, sessionUid = walletUse.sessionUid;
    // An empty cart has nothing to check out: back to the cart (never after a successful order, which clears it).
    // P4-INT: nor in mode qr while no table context applies: the context going (its 2 h ran out, or Pay's re-check
    // cleared it) empties the cart in memory (P4-04), and the page shows the table problem instead (see "Dine-in").
    var tableHeld = OO.mode !== 'qr' || !!tableContextOf(hotel);
    React.useEffect(function () {
      if (!P.ready || placedRef.current || P.lines.length || !tableHeld) return;
      OO.router.go(OO.router.href('cart', null, o), { replace: true });
    }, [P.ready, P.lines.length, tableHeld]);
    // Fill empty contact fields once from the signed-in customer (app mode starts signed in as the demo member).
    React.useEffect(function () {
      if (!P.ready || prefilled.current) return;
      prefilled.current = true;
      var c = P.session && P.session.customer;
      if (!c) return;
      var d = OO.store.get().checkout;
      var patch = {};
      if (!String(d.name || '').trim() && c.name) patch.name = c.name;
      if (!String(d.phone || '').trim() && c.phone) patch.phone = c.phone;
      if (!String(d.email || '').trim() && c.email) patch.email = c.email;
      if (Object.keys(patch).length) OO.store.setCheckout(patch);
    }, [P.ready]);
    React.useEffect(function () { if (foldS[0]) K.reveal(foldRef.current); }, [foldS[0]]);
    React.useEffect(function () { if (noticeS[0] && noticeS[0].reveal) K.reveal(noticeRef.current, 'center'); }, [noticeS[0]]);
    React.useEffect(function () { if (freshS[0]) K.reveal(blockedRef.current, 'center'); }, [freshS[0]]);

    // This page's pricing context (see the header "Totals"): cart.jsx's order type unless this page doesn't offer it
    // (then the first type offered; a store left with none here keeps it, blocked), the usable payment method, and the
    // delivery zone chosen from the item subtotal.
    var store = P.store;
    var draft = P.draft;
    var lines = P.lines;
    var fixture = !!(OO.api && typeof OO.api.isFixtureFor === 'function' && OO.api.isFixtureFor(hotel));
    // P4-05: the table this order is for (mode qr; see the header, "Dine-in"), and mode qr without one (the page shows
    // the table problem instead of the form: never a pickup order).
    var table = tableContextOf(hotel);
    var atTable = !!table;
    var lostTable = OO.mode === 'qr' && !table;
    var types = store ? offeredTypes(K.orderTypesOf(store), fixture) : [];
    var typeBlocked = !!store && !types.length && !atTable; // e.g. a delivery-only real store without the Maps key
    var pc0 = store ? K.pricingCtx(store, draft) : null;
    var orderType = atTable ? 'DINEIN' : !pc0 ? 'PICKUP' : (typeBlocked || types.indexOf(pc0.orderType) >= 0 ? pc0.orderType : types[0]);
    var isDelivery = orderType === 'DELIVERY';
    var askAddress = isDelivery && !typeBlocked;
    // P1-21: a real store's address comes from Places (or the device's location) and is measured with the Distance
    // Matrix (the distance hook, shared with the cart); a fixture store keeps a plain field and distance 0.
    var places = askAddress && !fixture;
    var point = OO.delivery.pointOf({ lat: draft.lat, lng: draft.lng });
    var dist = K.useDeliveryDistance(store, draft, places);
    var methods = store ? usableMethods(K.methodsFor(store, orderType), fixture) : [];
    var choices = store ? CS.payment.choices(methods, orderType, fixture) : []; // P2-27: every counter type and gateway
    // P4-05: the table's available pick, configured default, or sole choice selected automatically.
    var pick = atTable ? CS.payment.pickOf(choices, paymentUse.pickS[0], paymentUse.autoPickS[0]) : null;
    // Availability re-checked now, as on the cart. P4-05 (D-20): no coupon at a table.
    var coupon = store && !atTable ? K.liveCoupon(P.coupon, store, orderType) : null;
    var quote = null;
    var pc = null;
    var sum = null;
    if (store) {
      // P4-05: a table order before its pick is priced as a counter order (no card surcharge; REF prices it as cash).
      pc = atTable ? tablePc(pick) : { orderType: orderType, paymentMethod: methodKey(methods, draft.paymentMethod), zone: null, deliveryFee: 0, tipAmount: draft.tipAmount };
      if (store.orderFees) {
        var feeChoice = atTable ? pick : CS.payment.choiceOf(choices, pc.paymentMethod, draft.paymentChoice);
        pc.feePaymentKey = feeChoice ? (feeChoice.kind === 'gateway' ? OO.pricing.gatewayFeeKey(feeChoice.gateway, feeChoice.gatewayId) : 'type:' + feeChoice.typeId) : '';
      }
      sum = summarize(K, lines, store, coupon, pc, table);
      if (askAddress) {
        quote = zoneQuote(hotel, point, store.deliveryZones, distanceKm(fixture, dist.km), sum.subtotal);
        pc = Object.assign({}, pc, { zone: quote.zone, deliveryFee: quote.fee });
        sum = summarize(K, lines, store, coupon, pc, table);
      }
    }
    // The draft follows the page (see the header), so the cart page prices the same order after a checkout visit: the
    // order type when cart.jsx would pick DELIVERY that this page doesn't offer, and the chosen zone. P1-21: not while
    // a picked address is still being measured (or failed to be): the zone synced last stands until it is known.
    // P4-05: never at a table (its order type isn't the draft's).
    var zoneWant = quote && quote.zone ? quote.zone.id : null;
    var zoneKnown = !!quote && (quote.status !== 'unknown' || !point);
    var dropType = !atTable && !!pc0 && pc0.orderType === 'DELIVERY' && orderType !== 'DELIVERY';
    React.useEffect(function () {
      if (!P.ready || !store || !lines.length || placedRef.current) return;
      var patch = {};
      if (dropType) { patch.orderType = orderType; patch.day = null; patch.slot = null; }
      if (askAddress && zoneKnown && String(draft.zoneId === null || draft.zoneId === undefined ? '' : draft.zoneId) !== String(zoneWant === null ? '' : zoneWant)) {
        patch.zoneId = zoneWant;
      }
      if (Object.keys(patch).length) OO.store.setCheckout(patch);
    }, [P.ready, store, dropType, orderType, askAddress, zoneKnown, zoneWant, draft.zoneId]);
    // P2-00b: the address search and the Places autocomplete (checkout-address.jsx), then the minute tick and the
    // pickup options (checkout-time.jsx). P3-FIX #27: askAddress tells the section delivery is asked, on a fixture
    // store too (places is a real store's only), for its saved-address preselect.
    var addressUse = CS.address.use({ hotel: hotel, store: store, P: P, lines: lines, places: places, askAddress: askAddress, addressRef: addressRef, aliveRef: aliveRef });
    var searchS = addressUse.searchS;
    var timeUse = CS.time.use({ store: store, orderType: orderType });
    var opts = timeUse.opts;

    var problem = P.h.notFound || (!!P.h.error && !store);
    function goBack() { OO.router.back(problem ? OO.router.href('find-store') : OO.router.href('cart', null, o)); }
    // P3-12: the account hub. P4-05 (D-20): no account link in mode qr.
    var prof = OO.mode !== 'qr' && !kiosk ? OO.router.link('account', null, o) : null;
    var header = (
      <ui.PageHeader title="Checkout" onBack={goBack} backLabel={problem ? 'Back' : 'Back to cart'}
        profileHref={prof ? prof.href : undefined} onProfile={prof ? prof.onClick : undefined} profileLabel={prof ? 'Your account' : undefined} />
    );
    if (problem) return <div className="oo-checkout">{header}<K.StoreProblem h={P.h} /></div>;
    // Loading, or an empty cart about to redirect: never flash an empty checkout. P4-INT: in mode qr without a table
    // context the table problem comes first (the context going empties the cart in memory, P4-04: no redirect then).
    if (!P.ready || (!P.lines.length && !lostTable)) return <div className="oo-checkout">{header}<K.PageSkeleton label="Loading checkout" blocks={3} /></div>;
    // P4-05: mode qr without a valid table context: the table problem (REF's "error" landing), never a pickup order.
    // P4-INT: when Pay's re-check found the table gone (P4-01's revalidate clears the context on that answer), the
    // re-check's own words (P4-06, REF canOrder: "Table unavailable"), else REF's "We couldn't verify your table".
    if (lostTable) {
      var gone = noticeS[0] && noticeS[0].kind === 'table' && noticeS[0].title ? noticeS[0] : null;
      return (
        <div className="oo-checkout">
          {header}
          <ui.Container>
            <div className="oo-checkout__lost">
              <ui.EmptyState icon="Scan" role="alert" title={gone ? gone.title : TABLE_LOST_TITLE} body={gone ? gone.text : TABLE_LOST_TEXT} />
            </div>
          </ui.Container>
        </div>
      );
    }

    var cur = store.currency;
    var money = function (n) { return OO.format.money(n, cur); };
    // P2-23: the time the order is for, as the time section resolves it (a chosen one found again with
    // OO.schedule.findSlot; while it is stale, the one its re-prompt proposes, and Pay asks first: see pay()).
    // P4-06: a table order is ASAP (CS.time.tableSlot), with no day.
    var day = atTable || kiosk ? null : timeUse.day;
    var slot = kiosk ? OO.kiosk.asap(store, OO.data.now()) : atTable ? CS.time.tableSlot(store, OO.data.now()) : timeUse.slot;
    // P2-09: the applied coupon's availability at the time the order is for (the slot shown), as validate_coupon_v4
    // judges it at Pay; the summary follows it.
    if (P.coupon && !atTable) {
      coupon = K.liveCoupon(P.coupon, store, orderType, OO.model.slotInstant(store, slot, OO.data.now()));
      sum = K.summaryOf(lines, store, coupon, pc, null);
    }
    // P2-07: the wallet points in use, as that summary took them off (the same arguments): the payload sends them.
    // P4-05 (D-20): none at a table.
    var walletIn = !atTable && typeof K.pointsOf === 'function' ? K.pointsOf(lines, store, coupon, pc, null) : null;
    // Points or rewards that pay it all (payment_type 'wallet', no surcharge: P2-05's wire): nothing to pay online.
    var walletPaid = numOr0(sum.wallet) > 0 && !(sum.total > 0);
    // Validated for the slot shown (it may be a fallback) and the order type's domain, as Next does before placing.
    var check = K.slotCheck(hotel, store, day, slot, orderType, P.menu);
    var issues = K.lineIssues(lines, check.menu, { at: check.at });
    // What the fresh menu at Pay found stays shown for the order it was found for (these lines, type, day and time),
    // even before the page's own menu catches up.
    var sig = [orderType, day ? day.key : '', slot ? slot.value : '', lines.map(function (l) { return l.key; }).join(',')].join('|');
    if (freshS[0] && freshS[0].sig === sig) issues = Object.assign({}, freshS[0].issues, issues);
    var verdict = issueState(issues, check.at); // POLISH-1: the cart at a table judges with it too
    var timeBound = verdict.timeBound;
    var blocked = verdict.blocked;
    // P2-27: what the payload sends. P4-05: at a table, the resolved choice (automatic for a default or sole choice).
    var method = atTable && !pick ? null : byKey(methods, 'key', pc.paymentMethod);
    var choice = atTable ? pick : CS.payment.choiceOf(choices, pc.paymentMethod, draft.paymentChoice);
    var online = pc.paymentMethod === 'ONLINE_PAYMENT' && !walletPaid; // P2-07: a wallet-paid order is placed, not paid
    var customer = P.session && P.session.customer;
    var optIn = marketingOn(store, draft, table); // WL-OPT: the offers opt-in shown and sent
    var paused = pausedOf(store, atTable); // P2-24: online ordering switched off (P4-06: at a table, table ordering)
    var loginNeeded = loginRequired(store, customer) || (!customer && OO.mode === 'app' && !!OO.whitelabel.zetaDesign(route.chain || hotel)); // P2-24: force_login_for_ordering and no customer yet
    // The delivery address part (P1-21; deliveryState): the block that stops Pay, and whether Pay waits for the distance.
    var ds = askAddress ? deliveryState({ real: places, point: point, search: searchS[0], dist: dist, quote: quote, cur: cur }) : null;
    var checking = !!ds && ds.checking;
    // P4-05: the contact at a table is required only when the store asks for it or a gateway is picked (REF).
    var contactNeeded = !kiosk && (!atTable || contactRequired(store, pick));
    var errors = validate(draft, askAddress, ds ? ds.block : null, !contactNeeded);
    var shown = submittedS[0] ? errors : {};
    var placing = placingS[0];
    var count = OO.cart.count(lines);
    var totalText = money(sum.total);
    var unconf = unconfS[0];
    var payNow = payState({
      count: count, typeBlocked: typeBlocked, blocked: blocked, timeBound: timeBound, slot: slot, isDelivery: isDelivery,
      checking: checking, method: method, terms: !!draft.terms, placing: placing, waiting: waitS[0], unconfirmed: unconf, // TERMS: at a table too
      online: online, total: totalText, paused: paused, login: loginNeeded,
      choose: atTable && !pick && choices.length > 0, // P4-05: Pay asks for the table's pick
      preview: !!(OO.orderingQr && OO.orderingQr.preview()) || (!!OO.whitelabel && OO.whitelabel.isPreview()),
    });
    var canPay = payNow.canPay;
    var cartLink = OO.router.link('cart', null, o);
    var foundLink = unconf && unconf.orderId ? OO.router.link('order-status', { orderId: unconf.orderId }, o) : null;
    var termsLink = OO.router.link('legal', { kind: 'terms' }, o);

    function setter(k) { return function (v) { var p = {}; p[k] = v; OO.store.setCheckout(p); }; }
    function say(text) { toastS[1]({ id: Date.now() + Math.random(), text: text }); }
    // Logging in / out swaps the login block, so the button that had focus is gone: hand focus to the new block.
    function refocusLogin() {
      var run = function () {
        var a = document.activeElement;
        if (a && a !== document.body && document.contains(a)) return;
        K.focusEl(loginRef.current);
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 0);
    }
    function loggedIn(c, isNew) {
      var d = OO.store.get().checkout;
      OO.store.setCheckout({ name: c.name || d.name, phone: c.phone || d.phone, email: c.email || d.email });
      loginS[1](false);
      if (noticeS[0] && noticeS[0].kind === 'login') noticeS[1](null); // P2-24: the login gate's notice is answered
      say(isNew ? 'Account created. Welcome!' : 'Logged in as ' + (c.firstName || c.name || c.email));
      refocusLogin();
    }
    function logout() { OO.store.logout(); say('Logged out'); refocusLogin(); }
    function fieldEl(k) { return { name: nameRef, phone: phoneRef, email: emailRef, address: addressRef }[k].current; }
    // P1-21: what the zone guard's 202 changes here (errorState's zone). "Costs $X": $X becomes the fee floor for the
    // address the order went with (pt), so the zone is re-selected and the total updates on the re-render the
    // rejection's notice causes. No coordinates: the pick is dropped, so Pay waits for a new one. G1 fix B: the floor
    // is kept with the zones and the item subtotal (sub) the order went with (setFeeFloor's opts).
    function zoneRejected(z, pt, sub) {
      if (z.outcome === 'zoneMismatch' && pt) setFeeFloor(hotel, pt, z.fee, { zones: store.deliveryZones, subtotal: sub });
      else if (z.outcome === 'noCoordinates') OO.store.setCheckout({ zipcode: '', lat: null, lng: null });
    }
    // A kind 'retry' rejection: Pay comes back after `ms`.
    function waitThenRetry(ms) {
      clearTimeout(waitRef.current);
      waitS[1](true);
      waitRef.current = setTimeout(function () { if (aliveRef.current) waitS[1](false); }, ms);
    }
    // G1 fix B: a "may exist" failure (errorState kind 'unconfirmed'). Pay stays disabled for `ms`, then reads
    // "Place it again": one explicit tap places a new order. look = { uid, total (the payable sent), type (its
    // payment_type) } for a logged-in customer (else null): their orders (OO.api.getAllOrders, E17: pending and unpaid
    // included) are looked at now and again at LOOKUP_AGAIN_MS (a network failure usually fails the first look too);
    // an order that matches (findPlacedOrder) turns the button into "View your order" instead, even after "Place it
    // again" showed. A missing or failing lookup changes nothing. The cart, coupon and draft stay as they are: nothing
    // is cleared.
    function endUnconfirmed() {
      var u = unconfRef.current;
      u.timers.forEach(function (t) { clearTimeout(t); });
      unconfRef.current = { episode: null, timers: [] };
      unconfS[1](null);
    }
    function unconfirmed(ms, look) {
      endUnconfirmed();
      var episode = { orderId: null };
      var u = unconfRef.current = { episode: episode, timers: [] };
      function mine() { return aliveRef.current && unconfRef.current.episode === episode; }
      function later(fn, delay) { u.timers.push(setTimeout(function () { if (mine()) fn(); }, delay)); }
      function lookUp() {
        if (!look || episode.orderId || typeof OO.api.getAllOrders !== 'function') return;
        Promise.resolve().then(function () { return OO.api.getAllOrders(hotel, { uid: look.uid }); }).then(function (raw) {
          if (!mine() || episode.orderId) return;
          var hit = findPlacedOrder(raw, { total: look.total, paymentType: look.type, now: Date.now() });
          if (!hit) return;
          episode.orderId = hit.orderId;
          unconfS[1]({ phase: 'found', orderId: hit.orderId });
        }, function () { /* no lookup: the copy and the wait stand */ });
      }
      unconfS[1]({ phase: 'wait', orderId: null });
      later(function () {
        unconfS[1](function (s) { return s && s.phase === 'wait' ? { phase: 'again', orderId: null } : s; });
      }, ms);
      lookUp();
      later(lookUp, Math.min(LOOKUP_AGAIN_MS, ms));
    }
    // "View your order": the order a lookup found (its status page polls online_order_status, so a pending one shows).
    function viewOrder() {
      var u = unconfS[0];
      if (u && u.orderId) OO.router.go(OO.router.href('order-status', { orderId: u.orderId }, o));
    }
    // G1 fix H: a payment that couldn't start leaves a dead order (FAILED + UNPAID, no payment row). This page writes
    // lastOrder only once an order is placed, so this is a guard: a lastOrder of THAT order goes, so no page polls or
    // shows it; any other (an earlier order) stays.
    function forgetOrder(orderId) {
      var lo = OO.store.get().lastOrder;
      if (orderId && lo && String(lo.orderId) === String(orderId)) OO.store.clearLastOrder();
    }

    function pay(arg) {
      if (!canPay || busyRef.current) return;
      if (OO.pricing.tipsEnabled(store, { table: table }) && !OO.pricing.validTip(draft.tipAmount == null ? '0' : draft.tipAmount)) {
        noticeS[1]({ kind: 'tip', text: 'Enter a valid tip with at most two decimal places.', reveal: true });
        return;
      }
      var confirmed = arg === ALERT_CONFIRMED; // P2-24: the checkout alert's "Place order" (never a click event)
      // P2-23: a time that is no longer offered, or chosen 15 min ago or more, is asked again first (checkout-time.jsx).
      if (timeUse.stale && !atTable && !kiosk) { timeUse.ask(); return; } // immediate at a table or kiosk
      // P2-24: force_login_for_ordering (REF: "Please Sign In To Place Your Order." and the account drawer).
      if (loginNeeded) {
        noticeS[1]({ kind: 'login', text: LOGIN_TO_ORDER, reveal: !wide });
        loginS[1](true);
        return;
      }
      // P4-05: several table-side choices without a default require a pick; Pay asks for it under the list.
      if (atTable && !pick) {
        paymentUse.missS[1](true);
        K.reveal(paymentUse.tableRef.current, 'center');
        K.focusEl(paymentUse.tableRef.current);
        return;
      }
      submittedS[1](true);
      var first = FIELD_ORDER.filter(function (k) { return errors[k]; })[0];
      if (first || errors.zone) {
        noticeS[1]({ kind: 'form', text: first ? 'Please check the highlighted details.' : errors.zone });
        if (first) {
          var el = fieldEl(first);
          if (el) { K.reveal(el, 'center'); K.focusEl(el); }
        } else {
          // A zone block: show its message. The address is what to change, unless the order is below the minimum, the
          // distance couldn't be checked (Try again), or the address search is down.
          K.reveal(zoneRef.current || addressRef.current, 'center');
          if (!ds.retry && !ds.searchDown && !(quote && quote.status === 'belowMinimum')) K.focusEl(addressRef.current);
        }
        return;
      }
      var useCoupon = !!(coupon && sum.coupon.ok);
      // P2-14: a coupon still waiting for the customer's choice (its free item) would go unapplied: stop and say so (the
      // offers section's "Choose" opens its picker; Remove drops it).
      if (coupon && sum.coupon.pending) {
        noticeS[1]({ text: 'Choose your free item to use your coupon, or remove the coupon.', reveal: !wide });
        return;
      }
      if (useCoupon && store.settings.forceLoginForCoupon && !customer) {
        noticeS[1]({ text: 'Log in to use your coupon, or remove it from your cart.', reveal: !wide });
        loginS[1](true);
        return;
      }
      // P2-24: the checkout alert (REF useCheckoutAlertGate): the store's text for the order type, confirmed with "Place
      // order" before anything is sent; closing it sends nothing.
      var alertMsg = atTable ? null : alertText(store, orderType); // P4-06: REF has none at a table
      if (alertMsg && !confirmed) {
        alertS[1](alertMsg);
        return;
      }
      var email = String(draft.email).trim();
      var when = new Date(OO.model.slotInstant(store, slot, OO.data.now())).toISOString();
      var asOf = whenOf(store, day, slot, atTable || kiosk ? store.cookingTimeMins : opts.cookingTimeMins, OO.data.now());
      var paySig = sig;
      var payPoint = point; // the address the order goes with (the zone guard's answer is about it)
      var paySub = sum.subtotal; // … and its item subtotal
      var payUid = sessionUid; // the customer the order goes with (a "may exist" lookup looks at their orders)
      var sent = null; // the body placeOrder gets
      // P2-08: what the order carries that can move under it (refreshPlan's intent): points, reward lines, a signed-in
      // customer (the membership), a line priced with a pricetag.
      var payRewards = !atTable && lines.some(function (l) { return OO.wallet.isReward(l); }); // P4-05 (D-20): none at a table
      var payTag = (P.menu && P.menu.pricetag) || null;
      var payIntent = {
        points: !!walletIn, rewards: payRewards, member: !!sessionUid,
        tag: !!payTag && !!OO.pricetags && lines.some(function (l) { return OO.pricetags.lineUsesTag(l, payTag); }),
      };
      noticeS[1](null);
      freshS[1](null);
      endUnconfirmed(); // "Place it again" was tapped: a new attempt
      placingS[1](true);
      busyRef.current = true;
      var couponCheck = CS.offers.couponCheck(hotel, coupon, useCoupon, email, when, orderType); // checkout-offers.jsx
      // The lines are checked again on a menu at most 15 min old (freshMenu: a failure falls back to the cached one), and
      // P2-08: the wallet is read again (CS.wallet's preflight: a signed-in customer's fresh read, with the rewards list
      // when the cart has reward lines), which the payload's wallet and membership engines check the order against.
      // P4-06: at a table, the table is checked again too (recheckTable; REF canOrder), and judged first.
      var tableCheck = atTable ? recheckTable(hotel) : Promise.resolve(null);
      var payTable = table; // the table the order goes with (the re-check's fresh context, when it has one)
      Promise.all([freshMenu(hotel), couponCheck, walletUse.preflight({ rewards: payRewards }), tableCheck, kiosk ? OO.kiosk.preflight(hotel, choice) : null]).then(function (res) {
        var tc = res[3];
        if (tc && !tc.ok) {
          var gone = new Error(tc.why === 'gone' ? TABLE_GONE : TABLE_UNCHECKED);
          if (tc.why === 'gone') gone.tableGone = true; else gone.tableUnchecked = true;
          throw gone;
        }
        if (tc && tc.table) payTable = tc.table;
        if (res[0]) {
          var again = K.slotCheck(hotel, store, day, slot, orderType, res[0]);
          var found = K.lineIssues(lines, again.menu, { at: again.at });
          if (Object.keys(found).length) {
            var changed = new Error('Some items in your cart can’t be ordered as they are.');
            changed.menuChanged = true;
            changed.issues = found;
            throw changed;
          }
        }
        var cv = res[1];
        if (cv && (cv.status === false || cv.status === 'false')) {
          var why = String(cv.message || 'This coupon can’t be used on this order.').replace(/\.?\s*$/, '.');
          OO.store.setCoupon(null);
          var stop = new Error(why);
          stop.couponRejected = true;
          throw stop;
        }
        var pre = res[2] || {};
        sent = OO.payload.checkoutPayload(lines, CS.payment.payStore(store, choice), payloadCtx({
          orderType: orderType, slot: slot, paymentMethod: pc.paymentMethod, draft: Object.assign({}, draft, { marketing: optIn }), // WL-OPT: the opt-in shown
          customer: customer, customerId: pre.customerId || null,
          coupon: coupon, table: payTable, isMobileApp: OO.mode === 'app', zone: pc.zone, fee: pc.deliveryFee, expectedPayable: sum.total,
          pricetag: (P.menu && P.menu.pricetag) || null, dob: verifiedDob(hotel, store),
          menuType: P.menu ? P.menu.type : null, guestCount: payTable ? payTable.guests : undefined, // P4-07 (guests: P4-03's count)
          walletPoints: walletIn ? walletIn.points : 0, walletAmount: walletIn ? walletIn.amount : 0, // P2-07
          freshWallet: pre.checked ? pre.freshWallet : undefined, freshRewards: pre.freshRewards, // P2-08
        }));
        if (kiosk) OO.kiosk.submission(true);
        return OO.api.placeOrder(hotel, sent);
      }).then(function (res) {
        if (!res || String(res.status) !== 'true' || !res.order_id) {
          var bad = new Error((res && (res.message || res.error)) || 'Order not placed');
          bad.status = 200;
          bad.data = res;
          throw bad;
        }
        // P5-06b: a payment_url that isn't an http(s) URL is never opened (payTarget): the payment couldn't start.
        if (res.payment_url && payTarget(res.payment_url, payEnv()).kind === 'refuse') throw notStarted(res.order_id);
        if (kiosk) OO.kiosk.submission(false);
        placedRef.current = true;
        // An online payment isn't done yet: keep the cart, coupon and draft so a declined / cancelled payment can go
        // straight back to checkout. The payment callback clears them once paymentUpdate confirms (200 / 202) a
        // lastOrder carrying clearCartOnSuccess. Counter / cash orders have no payment step: they are done now, and so
        // is an online order the payload sent as a counter one ($0.00 to pay; sentMethod).
        var method = sentMethod(sent, pc.paymentMethod);
        var awaitPayment = method === 'ONLINE_PAYMENT';
        // What the order-status page shows until the POS ticket exists: the order as this page showed it at Pay.
        // P2-14: the coupon's free lines too, marked in their names (Cart.shared.snapshotLines), so they add up to the
        // Sub-total the snapshot's rows show.
        var snapshot = snapshotOf({ // P4-INT: the table the order went with (the re-check's fresh context: its name)
          store: store, orderType: orderType, table: payTable, lines: K.snapshotLines(lines, coupon, store, sum.coupon.ok), sum: sum,
          paymentMethod: method, when: asOf,
          address: askAddress ? addressOf(draft).line : null,
        });
        OO.store.setLastOrder({
          orderId: res.order_id, hotel: String(hotel), paymentMethod: method,
          placedAt: Date.now(), total: sum.total, clearCartOnSuccess: awaitPayment, couponTxn: res.coupon_txn || null,
          snapshot: snapshot,
          tax: sum.tax, shipping: Math.max(0, Math.round(((Number(sum.delivery) || 0) - (Number(sum.deliveryDiscount) || 0)) * 100) / 100), // P5-06b
        });
        // P4-08: a table order's return keeps its table (t_id), and REF's payment context goes before its gateway.
        var back = { order_id: res.order_id, success: 'true' };
        if (payTable) back.t_id = String(payTable.id);
        if (payTable && res.payment_url) {
          var est = res.estimated_time || (res.data && res.data.estimated_time) || (numOr0(store.cookingTimeMins) || 15) + ' min';
          saveRefPayment({
            orderId: res.order_id, hotel: String(hotel), total: sum.total, itemsCount: lines.length, gateway: sent.payment_gateway,
            gatewayId: sent.payment_gateway_id, estimatedTime: typeof est === 'string' || typeof est === 'number' ? est : '',
            placedAt: new Date().toISOString(), currency: store.currency, storeName: store.name, storePhone: store.phone, table: payTable,
          });
        }
        goTo(res.payment_url || OO.router.href('payment-callback', null, { hotel: hotel, query: back }), sent.payment_gateway);
        if (!awaitPayment) {
          OO.store.clearCheckout();
          OO.store.cartClear();
        }
      }).catch(function (x) {
        busyRef.current = false;
        if (!aliveRef.current || placedRef.current) return;
        placingS[1](false);
        var st = errorState(x, cur, atTable ? { table: true, store: store.name } : null);
        // P4-06: a table order's payload refusals: the pick that went away goes; a menu that wasn't the table's is read
        // again, and a pricetag of the wrong channel (the next Pay can go).
        if (atTable && x && x.kind === 'payload') {
          if (x.code === 'NO_GATEWAY') { paymentUse.pickS[1](null); paymentUse.autoPickS[1](false); }
          if (x.code === 'TABLE_UNSUPPORTED') freshMenu(hotel, { maxAgeMs: 0 });
          if (x.code === 'PRICETAG_DOMAIN') OO.data.loadPricetags(hotel, { fresh: true });
        }
        if (st.kind === 'menu') { freshS[1]({ sig: paySig, issues: x.issues }); return; } // the unavailable-items state
        // G1 fix B: only an order that was sent can exist (a failure before placeOrder keeps its own copy).
        if (st.kind === 'unconfirmed' && !sent) st = Object.assign({}, st, { kind: 'error', text: placeErrorText(x), retryMs: 0 });
        if (kiosk && st.kind !== 'unconfirmed') OO.kiosk.submission(false);
        if (st.zone) zoneRejected(st.zone, payPoint, paySub);
        // G1 fix H: the payment couldn't start: no wait and no lookup (retryMs 0); the cart and coupon stay as they are.
        if (st.kind === 'paymentStart') forgetOrder(st.orderId);
        // P2-08 (refreshPlan; see the header): the wallet (its points, rows, rewards, membership) and the pricetags are
        // read again after a refusal, the store re-prices and settles them, and the page shows the new state; the points
        // or the reward lines the refusal was about are taken off, and the notice says so.
        var plan = refreshPlan(x, payIntent);
        if (plan.wallet || st.refreshWallet) {
          walletRef.current = { uid: null, p: null, fresh: true };
          OO.data.loadWallet(hotel, { fresh: true });
        }
        if (plan.pricetags) OO.data.loadPricetags(hotel, { fresh: true });
        if (plan.clearPoints) OO.wallet.dropPoints(hotel);
        if (plan.removeRewards) lines.forEach(function (l) { if (OO.wallet.isReward(l)) OO.store.cartRemove(l.key); });
        if (st.refreshCoupons && OO.http && typeof OO.http.flush === 'function') OO.http.flush();
        // The cart changed on the server's side: re-read the menu now, so the cart re-prices ("Prices updated").
        if (st.kind === 'cart') freshMenu(hotel, { maxAgeMs: 0 });
        // P2-24: the store stopped taking these orders: re-read it, so a pause shows (the paused-store state).
        if (storeOff(x) && typeof OO.data.rereadStore === 'function') OO.data.rereadStore(hotel);
        var note = { text: plan.text || (plan.note ? st.text + ' ' + plan.note : st.text), title: st.title || null, action: st.action, reveal: !wide };
        if (st.kind === 'table') note.kind = 'table'; // P4-INT: the re-check's stop (the lost table state reads it)
        // The order may exist: no Pay for a while, then "Place it again" (or "View your order"); nothing is cleared.
        if (st.kind === 'unconfirmed') {
          note.kind = 'unconfirmed';
          unconfirmed(st.retryMs, payUid ? { uid: payUid, total: Number(sent.payable_amount) + Number(sent.tip || 0), type: sent.payment_type } : null);
        } else if (st.retryMs > 0) {
          waitThenRetry(st.retryMs);
        }
        noticeS[1](note);
      });
    }

    // ---- sections ----
    var storeBlock = (
      <ui.SectionTitle level="page" className="oo-checkout__store"
        sub={store.address && store.address.full ? <React.Fragment>{wide ? <OO.icons.Pin size={14} /> : null}<span>{store.address.full}</span></React.Fragment> : null}>
        {store.name}
      </ui.SectionTitle>
    );

    var deliveryFields = CS.address.fields(addressUse, {
      draft: draft, cur: cur, askAddress: askAddress, places: places, point: point, dist: dist, quote: quote, ds: ds, checking: checking,
      errors: errors, shown: shown, submittedS: submittedS, addressRef: addressRef, zoneRef: zoneRef, aliveRef: aliveRef,
    });

    // P4-06: at a table, the dine-in card (no order type, no time).
    var whenCard = kiosk ? <OO.pages.KioskOrderType hotel={hotel} /> : atTable ? CS.time.tableCard({ store: store, table: table }) : CS.time.card(timeUse, {
      K: K, store: store, types: types, orderType: orderType, isDelivery: isDelivery, typeBlocked: typeBlocked,
      day: day, slot: slot, deliveryFields: deliveryFields,
    });

    var openLogin = function () { loginS[1](true); };
    var loginBlock;
    if (customer) {
      loginBlock = (
        <ui.Card className="oo-checkout__signed">
          <span className="oo-checkout__avatar" aria-hidden="true"><ui.Avatar name={customer.name || customer.email} size={36} /></span>
          <div className="oo-checkout__signed-text">
            <span className="oo-checkout__signed-name">{'Logged in as ' + (customer.name || customer.email)}</span>
            {customer.name && customer.email ? <span className="oo-checkout__signed-sub">{customer.email}</span> : null}
          </div>
          <ui.Button variant="link" onClick={logout}>Log out</ui.Button>
        </ui.Card>
      );
    } else if (kiosk || (atTable && !loginNeeded)) {
      loginBlock = null; // P4-05 (D-20): no member prompt at a table; force_login_for_ordering still asks (REF)
    } else if (store.checkout.loginStyle === 'solid') {
      loginBlock = <ui.Button block aria-haspopup="dialog" onClick={openLogin}>Login</ui.Button>;
    } else {
      loginBlock = (
        <ui.Card className="oo-checkout__login">
          <p className="oo-checkout__login-text">{loginNeeded ? 'Log in to place your order at ' + store.name + '.' : 'Have an account? Log in to fill your details.'}</p>
          <ui.Button size="md" aria-haspopup="dialog" onClick={openLogin}>Login</ui.Button>
        </ui.Card>
      );
    }

    // P4-05: at a table the contact is REF's: why it is needed (a gateway; the store asks for it), else optional.
    var contactSub = kiosk ? 'Optional: add your name or email for your order.' : !atTable ? "We'll send your order confirmation here"
      : pick && pick.kind === 'gateway' ? 'Needed for online payment.'
        : contactNeeded ? 'Required by the store.'
          : 'Optional: add your details to get a confirmation email.';
    var contactCard = (
      <ui.Card className="oo-checkout__card">
        <div className="oo-checkout__head oo-checkout__head--top">
          <OO.icons.UserCircle size={22} />
          <div className="oo-checkout__titles">
            <h3 className="oo-checkout__title">{!contactNeeded ? 'Contact details (optional)' : 'Contact details'}</h3>
            <p className="oo-checkout__sub">{contactSub}</p>
          </div>
        </div>
        <ui.TextInput label="Full name" name="name" autoComplete={kiosk ? 'off' : 'name'} required={contactNeeded} value={draft.name}
          onChange={setter('name')} error={shown.name} inputRef={nameRef} />
        <div className="oo-checkout__duo">
          <PhoneField store={store} value={draft.phone} onChange={setter('phone')} error={shown.phone} inputRef={phoneRef} required={contactNeeded} />
          <ui.TextInput label="Email" type="email" name="email" autoComplete={kiosk ? 'off' : 'email'} inputMode="email" required={contactNeeded} value={draft.email}
            onChange={setter('email')} error={shown.email} inputRef={emailRef} />
        </div>
      </ui.Card>
    );

    // P4-05: the table's card (the default or sole choice selected automatically) at a table.
    var payCard = atTable ? CS.payment.tableCard(paymentUse, { store: store, choices: choices, choice: pick })
      : CS.payment.card(paymentUse, { store: store, methods: methods, method: method, pc: pc, choices: choices, choice: choice });
    // The offers and wallet slots (P2-14, P2-07), placed after the order notes in the main column. The wallet slot also
    // gets the page's menu, the table and the order's moment (the slot's instant and its menu at the cart's pricing:
    // the rewards list judges and prices its products with it, as the coupon flows do).
    // P4-05 (D-20): neither at a table.
    var slotCtx = { hotel: hotel, store: store, lines: lines, coupon: coupon, sum: sum, pc: pc, orderType: orderType, draft: draft, customer: customer };
    if (OO.whitelabel.zetaDesign(route.chain || hotel)) slotCtx.zeta = true;
    var offersCard = atTable ? null : CS.offers.card(slotCtx);
    var walletCard = atTable ? null : CS.wallet.card(walletUse, Object.assign({}, slotCtx, {
      menu: P.menu, table: null,
      moment: { at: OO.model.slotInstant(store, slot, OO.data.now()), menu: K.couponMenu(hotel, check.menu), today: OO.data.now().getTime() },
    }));

    // The terms in every ordering option (TERMS, owner 2026-10-05: at a table too, where REF has none; ticked by
    // default, Pay needs them). P4-06: no marketing opt-in at a table (REF's table checkout has none).
    var checks = (
      <div className="oo-checkout__checks">
        <ui.CheckRow name="terms" required checked={!!draft.terms} onChange={setter('terms')}>
          I have read and agree to the <a className="oo-checkout__terms" href={termsLink.href} onClick={termsLink.onClick}>terms and conditions</a>.
        </ui.CheckRow>
        {atTable ? null : (
          <ui.CheckRow name="marketing" checked={optIn} onChange={function (v) { OO.store.setCheckout({ marketing: v, marketingChosen: true }); }}>
            Send me email and push notifications about exclusive offers
          </ui.CheckRow>
        )}
      </div>
    );

    // P4-06: at a table they are the kitchen's (REF "Kitchen Notes (Optional)" / "Notes For Kitchen").
    var notes = (
      <ui.TextArea label={atTable ? 'Kitchen notes (optional)' : 'Order notes'} name="notes" labelSize={wide ? 'sm' : undefined} size={wide ? 'xl' : 'lg'} maxLength={250}
        placeholder={atTable ? 'Notes for the kitchen' : 'Extra comments (optional)'} value={draft.notes} onChange={setter('notes')} className="oo-checkout__notes" />
    );

    var tipping = OO.pricing.tipsEnabled(store, { table: table });
    var tipInvalid = tipping && !OO.pricing.validTip(draft.tipAmount == null ? '0' : draft.tipAmount);
    var tipCard = tipping ? (
      <ui.Card className="oo-checkout__card oo-checkout__tips">
        <h3 className="oo-checkout__title">Add a tip (optional)</h3>
        <div className="oo-checkout__tip-choices" role="group" aria-label="Tip amount">
          {['none', '5', '10', '15', 'other'].map(function (value) {
            var selected = (draft.tipChoice || 'none') === value;
            return <button key={value} type="button" aria-pressed={selected} disabled={placing}
              className={cx('oo-checkout__tip-choice', selected && 'is-selected')}
              onClick={function () { OO.store.setCheckout({ tipChoice: value, tipAmount: value === 'other' ? '' : value === 'none' ? '0' : value }); }}>
              {value === 'none' ? 'No tip' : value === 'other' ? 'Other' : (store.currency || '$') + value}
            </button>;
          })}
        </div>
        {draft.tipChoice === 'other' ? <div className="oo-checkout__tip-custom">
          <label htmlFor="oo-custom-tip">Tip amount ({store.currency || '$'})</label>
          <input id="oo-custom-tip" type="text" inputMode="decimal" value={draft.tipAmount || ''} disabled={placing}
            maxLength={9} aria-invalid={tipInvalid} aria-describedby={tipInvalid ? 'oo-tip-error' : undefined}
            onChange={function (e) { OO.store.setCheckout({ tipAmount: e.target.value }); }} />
          {tipInvalid ? <p id="oo-tip-error" role="alert">Enter an amount with up to two decimal places.</p> : null}
        </div> : null}
      </ui.Card>
    ) : null;
    var rows = K.priceRows(sum);
    var preview = OO.api.isFixture ? <p className="oo-checkout__preview">Preview — no real payment is taken</p> : null;
    // G1 fix B: payState decides (no method: no Pay; "may exist": wait, "Place it again", or "View your order").
    var payBtn = (
      <ui.Button block busy={placing} disabled={!payNow.enabled} onClick={payNow.view ? viewOrder : OO.whitelabel.zetaDesign(route.chain || hotel) && loginNeeded ? function(){OO.router.go(OO.router.href('profile',null,{hotel:hotel,query:{returnTo:OO.router.href('checkout',null,o)}}));} : pay}>
        {placing ? 'Placing your order…' : OO.whitelabel.zetaDesign(route.chain || hotel) && loginNeeded ? 'Log in to continue' : payNow.label}
      </ui.Button>
    );
    // A validation notice goes away by itself once every field is fixed; an order error stays until the next Pay.
    var stillInvalid = FIELD_ORDER.some(function (k) { return errors[k]; }) || !!errors.zone;
    var notice = noticeS[0] && (noticeS[0].kind !== 'form' || stillInvalid) ? (
      <div ref={noticeRef} className="oo-checkout__notice">
        <ui.InlineNotice tone="danger" role="alert">
          {noticeS[0].title ? <span className="oo-checkout__notice-title">{noticeS[0].title}</span> : null}
          {noticeS[0].text}
          {noticeS[0].action === 'cart' ? (
            <React.Fragment>{' '}<a className="oo-checkout__inlink" href={cartLink.href} onClick={cartLink.onClick}>Review cart</a></React.Fragment>
          ) : null}
          {noticeS[0].kind === 'unconfirmed' && foundLink ? (
            <React.Fragment>{' '}<a className="oo-checkout__inlink" href={foundLink.href} onClick={foundLink.onClick}>{VIEW_ORDER}</a></React.Fragment>
          ) : null}
        </ui.InlineNotice>
      </div>
    ) : null;
    var blockedNotice = blocked ? (
      <div ref={blockedRef} className="oo-checkout__notice">
        <ui.InlineNotice tone="warn">
          {timeBound ? 'Some items aren’t available at ' + check.at + '. Choose another time, or ' : 'Some items in your cart can’t be ordered as they are. '}
          <a className="oo-checkout__inlink" href={cartLink.href} onClick={cartLink.onClick}>{timeBound ? 'review your cart' : 'Review your cart'}</a>
        </ui.InlineNotice>
      </div>
    ) : null;
    var repricedNotice = P.repriced ? <ui.InlineNotice tone="info" icon="Info">Prices updated to today’s menu.</ui.InlineNotice> : null;
    // P2-24: the paused store (REF's browse-only "short pause" with its store note): Pay reads PAUSED_LABEL. P4-06: at a
    // table, its table ordering is off (REF "Dine-In Paused"): ask a staff member.
    var menuLink = OO.router.link('menu', null, o);
    var pausedNotice = paused && atTable ? (
      <ui.InlineNotice tone="warn" className="oo-checkout__paused">
        <span className="oo-checkout__paused-title">{TABLE_PAUSED_TITLE}</span>
        <span className="oo-checkout__paused-text">{tableOffText(store.name)}</span>
      </ui.InlineNotice>
    ) : paused ? (
      <ui.InlineNotice tone="warn" className="oo-checkout__paused">
        <span className="oo-checkout__paused-title">{PAUSED_TITLE}</span>
        {store.online.reason ? <span className="oo-checkout__paused-text">{'Store note: ' + store.online.reason}</span> : null}
        <span className="oo-checkout__paused-text">{PAUSED_TEXT + ' '}<a className="oo-checkout__inlink" href={menuLink.href} onClick={menuLink.onClick}>Back to the menu</a></span>
      </ui.InlineNotice>
    ) : null;

    // Mobile price summary: the inline card, or (collapsed) a row that expands to the same rows.
    var collapsed = store.checkout.priceSummary === 'collapsed';
    var mobileSummary = collapsed ? (
      <div ref={foldRef} className="oo-checkout__foldwrap">
        <ui.Card pad="none" className={cx('oo-checkout__fold', foldS[0] && 'is-open')}>
          <button type="button" className="oo-checkout__fold-head" aria-expanded={foldS[0] ? 'true' : 'false'} aria-controls="oo-checkout-fold"
            onClick={function () { foldS[1](!foldS[0]); }}>
            <span className="oo-checkout__title">Price summary</span>
            <span className="oo-checkout__fold-total">{totalText}</span>
            <OO.icons.ChevronDown size={16} className="oo-checkout__chev" />
          </button>
          {foldS[0] ? <div id="oo-checkout-fold" className="oo-checkout__fold-body"><ui.PriceRows rows={rows} total={totalText} /></div> : null}
        </ui.Card>
      </div>
    ) : (
      <ui.Card pad="summary"><ui.PriceRows title="Price summary" rows={rows} total={totalText} /></ui.Card>
    );

    var orderCard = (
      <ui.Card pad="summary" className="oo-checkout__order">
        <div className="oo-checkout__order-head">
          <h3 className="oo-checkout__order-title">Your order</h3>
          <a className="oo-checkout__edit" href={cartLink.href} onClick={cartLink.onClick} aria-label="Edit your order">Edit</a>
        </div>
        <ul className="oo-checkout__olines">
          {K.couponLines(lines, coupon, store, sum.coupon.ok).map(function (row) {
            // P2-14: the cart's lines, then the coupon's free lines; a coupon's mark ('Free', '1 at 38.8% off'), so the
            // lines add up to the Sub-total (Cart.shared.couponLines).
            var l = row.line;
            var mods = OO.cart.displayMods(l);
            // P2-07: a reward line (wallet type 2) is marked too; its price comes off in the Discount row.
            var mark = row.free ? row.free.label : row.part ? row.part.label : l.reward ? 'Reward' : null;
            return (
              <li key={row.key} className="oo-checkout__oline">
                <span className="oo-checkout__oline-qty"><span aria-hidden="true">{l.qty + '×'}</span><span className="oo-sr-only">{l.qty + ' ×'}</span></span>
                <span className="oo-checkout__oline-text">
                  <span className="oo-checkout__oline-name">{l.name}</span>
                  {mods ? <span className="oo-checkout__oline-mods">{mods}</span> : null}
                  {l.notes ? <span className="oo-checkout__oline-mods">{'Note: ' + l.notes}</span> : null}
                  {mark ? <ui.Badge tone="done" size="sm" className="oo-checkout__oline-mark">{mark}</ui.Badge> : null}
                  {!row.free && issues[l.key] ? <span className="oo-checkout__oline-issue">{issues[l.key].text}</span> : null}
                </span>
                <span className="oo-checkout__oline-total">{money(OO.cart.lineTotal(l))}</span>
              </li>
            );
          })}
        </ul>
        <div className="oo-checkout__rule" role="presentation" />
        <ui.PriceRows rows={rows} total={totalText}>
          {payBtn}
          {preview}
          {notice}
        </ui.PriceRows>
      </ui.Card>
    );
    // P4-17 (mode app, a chain of several stores): "Ordering from {branch} · Change store" (OO.appKit.BranchLine).
    var branchLine = OO.appKit && OO.appKit.BranchLine ? <OO.appKit.BranchLine route={route} hotel={hotel} /> : null;

    if (OO.whitelabel.zetaDesign(route.chain || hotel) && OO.appKit && OO.appKit.ZetaCheckout) return <OO.appKit.ZetaCheckout route={route} store={store} draft={draft} setter={setter} shown={shown} timeStale={!!timeUse.stale} required={contactNeeded} nameRef={nameRef} phoneRef={phoneRef} emailRef={emailRef} delivery={isDelivery} address={deliveryFields} day={day} slot={slot} choices={choices} choice={choice} back={goBack} paused={pausedNotice} repriced={repricedNotice} blocked={blockedNotice} notice={notice} login={loginBlock ? <div ref={loginRef}>{loginBlock}</div> : null} order={orderCard} lines={K.couponLines(lines,coupon,store,sum.coupon.ok)} offers={offersCard} wallet={walletCard} tip={tipCard} checks={checks} sum={sum} pay={payBtn} preview={preview} dialogs={<React.Fragment>{loginS[0] ? <LoginSheet hotel={hotel} email={draft.email} contact={{name:draft.name,phone:draft.phone}} onDone={loggedIn} onClose={function(){loginS[1](false);}}/> : null}{alertS[0] ? <CheckoutAlert text={alertS[0]} busy={placing} onClose={function(){alertS[1](null);}} onPlace={function(){alertS[1](null);pay(ALERT_CONFIRMED);}}/> : null}{toastS[0] ? <ui.Toast key={toastS[0].id} open onClose={function(){toastS[1](null);}}>{toastS[0].text}</ui.Toast> : null}</React.Fragment>}/>;

    return (
      <div className="oo-checkout">
        {header}
        <ui.Container>
          <div className="oo-checkout__body">
            <div className="oo-checkout__col oo-checkout__col--main">
              {branchLine}
              {storeBlock}
              {pausedNotice}
              {repricedNotice}
              {blockedNotice}
              {whenCard}
              {loginBlock ? <div ref={loginRef} tabIndex={-1} className="oo-checkout__loginwrap">{loginBlock}</div> : null}
              {contactCard}
              {offersCard}
              {tipCard ? <React.Fragment>{payCard}{tipCard}</React.Fragment> : payCard}
              {checks}
              {!wide ? notes : null}
              {walletCard}
              {!wide ? mobileSummary : null}
              {!wide ? notice : null}
              {!wide ? <ui.PoweredBySipo /> : null}
            </div>
            {wide ? (
              <div className="oo-checkout__col oo-checkout__col--side">
                {orderCard}
                {notes}
                <ui.PoweredBySipo />
              </div>
            ) : null}
          </div>
        </ui.Container>
        {!wide ? (
          <ui.BottomBarCTA label={K.totalLabel(sum, store)} amount={totalText}>
            <div className="oo-checkout__paycol">
              {payBtn}
              {preview}
            </div>
          </ui.BottomBarCTA>
        ) : null}
        {loginS[0] ? (
          <LoginSheet hotel={hotel} email={draft.email} contact={{ name: draft.name, phone: draft.phone }}
            onDone={loggedIn} onClose={function () { loginS[1](false); }} />
        ) : null}
        {alertS[0] ? (
          <CheckoutAlert text={alertS[0]} busy={placing} onClose={function () { alertS[1](null); }}
            onPlace={function () { alertS[1](null); pay(ALERT_CONFIRMED); }} />
        ) : null}
        {toastS[0] ? <ui.Toast key={toastS[0].id} open onClose={function () { toastS[1](null); }}>{toastS[0].text}</ui.Toast> : null}
      </div>
    );
  };

  // The page's pure helpers (see the header), for tests/checkout-logic.test.mjs and tests/delivery-zones.test.mjs.
  // selectZone is OO.delivery's (P1-21).
  OO.pages.Checkout.logic = {
    realDeliveryEnabled: realDeliveryEnabled,
    deliveryOffered: deliveryOffered,
    offeredTypes: offeredTypes,
    distanceKm: distanceKm,
    selectZone: selectZone,
    zoneTexts: zoneTexts,
    deliveryState: deliveryState,
    feeFloor: feeFloor,
    setFeeFloor: setFeeFloor,
    zoneQuote: zoneQuote,
    usableMethods: usableMethods,
    methodKey: methodKey,
    payState: payState,
    addressOf: addressOf,
    payloadCtx: payloadCtx,
    sentMethod: sentMethod,
    whenOf: whenOf,
    snapshotOf: snapshotOf,
    errorState: errorState,
    placeErrorText: placeErrorText,
    refreshPlan: refreshPlan, // P2-08
    // P2-24
    loginRequired: loginRequired,
    pausedOf: pausedOf,
    alertText: alertText,
    PHONE_COUNTRIES: PHONE_COUNTRIES,
    phoneDefault: phoneDefault,
    phoneParts: phoneParts,
    phoneValue: phoneValue,
    phoneValid: phoneValid,
    findPlacedOrder: findPlacedOrder,
    freshMenu: freshMenu,
    signIn: signIn,
    // P4-05
    tableContextOf: tableContextOf,
    tableUserForm: tableUserForm,
    contactRequired: contactRequired,
    validate: validate,
    recheckTable: recheckTable, // P4-06
    // QR-CART: the table pricing, which the cart reuses (one copy of the maths)
    tablePc: tablePc,
    summarize: summarize,
    issueState: issueState, // POLISH-1: the line checks' verdict (the cart's at a table too)
    // P5-06b
    payTarget: payTarget,
    goTo: goTo,
    marketingOn: marketingOn, // WL-OPT
  };
})();


// ===================== src/pages/order-status.jsx =====================
// src/pages/order-status.jsx: the order status / detail page (page id 'order-status': /{hotel}/orderStatus, and
// /{hotel}/profile/orders/{id} or REF's /{hotel}/orders/{id} with params.orderId; order emails and the payment callback
// link to the former). Styles: styles/pages.css (.oo-ostatus__*). Not in the design: built from its parts (black
// PageHeader, canvas, white cards, PriceRows).
//
// Which order: params.orderId, else (/orderStatus) the query's order_id (REF's confirmation pages /order-confirmed and
// /success arrive here with their query, minus the contact fields: glue/router.jsx), else the last order placed on this
// device for this store (OO.store lastOrder), else the newest order in the history when logged in (OO.data.ownOrders,
// read fresh: the newest order may have been placed elsewhere since the list was read), else an empty state.
// Who sees what (Batch 2 P3-02; B §9 Q13: get_order_data returns the customer's name, phone and address to anyone holding
// an id): OO.model.orderAccess decides. The OWNER (this device placed the order, OO.api.isOwnOrder; or it is on the
// signed-in customer's own order list, OO.data.ownOrders: read once per session and store, so a later visit asks the
// server for nothing (P3-FIX #10); read again, fresh and once per order, when the live status says the ticket exists
// while the list in hand doesn't hold the order, never once it does (P3-FIX #21)) gets
// the ticket, the snapshot, the receipt and "Order again". Anyone else gets the public view: the live status
// (online_order_status: no contact data), the confirmation link's display params, and a note that the items and the
// receipt are the owner's (Log in when signed out; Try again when the list couldn't be read: with no live status
// either, "We couldn't load this order" and its Try again reads the list again, never "not found"). get_order_data is
// never asked for someone else's order (orderState 'private').
// Confirmation links: their display params (OO.model.orderLinkSnapshot: type, payment method, total, items, placed,
// estimated time, pickup number, table) fill the status-only view when there is no ticket and no snapshot; the payment
// row shows payment_method as REF's pages do ('online' | 'cash'; REF defaults it by the alias: online for
// /order-confirmed, cash for /success; a link without one shows no payment row).
// Receipt: the owner's ticket view is the receipt. "Print receipt" (window.print) and ?print=1 (REF's Download link,
// the order history's Receipt action) print it, the latter once per order on this page, 350 ms after the ticket shows
// (REF); the print styles (styles/pages.css, `.oo-ostatus--receipt`) leave out the header, the live status, the help card
// and the buttons. No print for anyone else, or before the ticket exists.
// Data (bug 4: get_order_data 404s until the POS creates the ticket, so it can't drive the page on its own):
//   * Live status: OO.api.orderStatus(orderId, hotel) → OO.model.orderProgress, from the first render (it doesn't wait
//     for the ticket). Polled on D-17's backoff of the visible tab's time (P3-FIX #20: the track page's schedule,
//     OO.pages.Track.logic.pollDelay, read when a poll falls due: every 10 s for 2 minutes, every 30 s to 10 minutes,
//     every 60 s to 30 minutes; a bundle without the track page polls every 10 s for the same 30 minutes) as a
//     setTimeout chain (polls never overlap), paused while the tab is hidden (document.visibilityState; it polls at
//     once when the tab is visible again); then, or after 6 failures in a row, the card says "Refresh for the latest
//     status" with a Refresh button (asks for the order again and starts a new run). It stops at a FINAL state
//     (completed, rejected or cancelled; READY is not final: Collected / Delivered follows) and on a 400 / 404 / 422 (an
//     order this device didn't place, or none: the page keeps the ticket's own status). A network failure or a 5xx (the
//     backend's retryable 503) is retried on the same schedule.
//   * "Track live status" (P3-FIX #20): while the status updates automatically, its row links the public track page
//     (/{hotel}/track/{id}, OO.router.link('track'): the live status, a share link and, for this device's delivery,
//     the courier's map). Not at a table (D-20: the track page is off there) and not for a store this host doesn't
//     serve (OO.api.availabilityOf 'unavailable': the track route is gated there, only this page stays open).
//   * The ticket (the owner only): OO.api.getOrder → OO.model.normalizeOrder (header, lines, totals, status,
//     pickupLabel). A 404 (or any failure) while the status poll runs is not an error: it is asked for again every 30 s
//     (same visibility rule) until it exists or the order is final.
//   * The snapshot: OO.store lastOrder.snapshot (checkout's copy of the order at Pay) via OO.model.orderSnapshot, only
//     when lastOrder is this order of this store. OO.store sanitises it, so any field can be absent.
// What shows is OO.model.orderStatusView (pure; tests/order-status.test.mjs): the ticket once it exists, else the
// snapshot (its lines and price rows; no "Order again": its lines carry no ids), else the live status card alone (no
// lines; the confirmation link's details when it has some), else a skeleton, "We couldn't find this order" (no ticket
// and the status call 400s) or "We couldn't load this order" (Try again). Awaiting payment (PROCESSING: an unpaid online
// order, not a failure) is its own state, before "Received" on the stepper; until the first status answer the card says
// "Checking your order".
// Order number: the ticket's short id, else the live pickup number ('#1043' in the status text), else the link's.
// Pickup / delivery time: the live deliver_time, else the ticket's pickupLabel ('Today, 6:30pm') or the snapshot's
// time, else ASAP / Scheduled. Stepper: Received → Preparing → Ready for pickup → Collected (delivery: On its way →
// Delivered; a type nothing tells, someone else's order without a link naming it, as online_order_status has none:
// Ready → Completed, and the headlines "Your order is ready" / "Order complete"). The owner's price summary (P3-05; OO.model's Order.totals, REF's rows; they add up to the total): Sub-total,
// Delivery, Surcharge ('Surcharge (1.5%)'), Tip, Tax ('Tax (GST inclusive)' | 'Tax (GST Exclusive)', as the cart's
// summary labels it), the coupon ('Coupon SAVE10 (10% off)': −amount, else 'Free item' | 'Applied'), the points ('Paid
// with points (190)'), the rest of the savings (discount_reason, else 'Discount'), and Paid / Remaining for a part
// payment; Delivery, Surcharge, Tip and the savings only when non-zero. Its Payment row is Order.payment's label
// ('Paid online', 'Pay at the counter', 'Paid at the counter', 'Paid with points', …; 'Not paid yet' while the live
// status awaits the payment). Under the price summary: the ratings lane's <OO.pages.Review.OrderRating hotel store
// order> (P3-06/07) when the bundle has it, in .oo-ostatus__rating (left out of the receipt print).
// Analytics (P5-06b): for this device's order (OO.api.isOwnOrder) of a loaded store, OO.analytics.purchase(lastOrder,
// store) on every change of lastOrder: it sends purchase once per order, once it went through (a counter order at once,
// an online payment once settled; src/lib/analytics.jsx), with a random transaction id, never the order id.
//
// At a table (Batch 2 P4-08; REF TableSideOrderConfirmedPage, inventory D §1.1 gap T12: no pickup-style "ready for
// collection" words). A dine-in order (the ticket's, the snapshot's or the link's order type DINEIN; in mode qr every
// order: the qr hosts only place table orders, whatever type a ticket reports) reads as one, its type 'Dine-in'. Its
// status reads like a normal order being prepared (owner decision 2026-10-05, P4-INT: nothing about who brings the
// food, so no "bring it to your table" / "Served"): the neutral stepper Received → Preparing → Ready → Completed and
// the neutral headlines ("Order received", "Preparing your order", "Your order is ready", "Order complete", awaiting
// payment, checking), except that a declined / cancelled order, and one without live updates, say to ask a staff
// member; the ticket view names the table too (this device's snapshot's). In mode qr the page keeps to the table
// (D-20): its table is the URL's (OO.router.tableIdOf: the payment callback hands it on as ?t_id=), else the store's
// live table context (P4-01's OO.pages.Table.context, else OO.store.tableFor); back and
// "Order more" (always shown, under the status) go to the table menu (/{hotel}/table/{id}; without a table the store's
// landing, the scan landing); no account link in the header, no "Log in", "Your orders" or "Order again"; "We couldn't
// find this order" says "Please ask a staff member for your order."; Need help: "Ask a staff member, or call the store
// on {phone}."; the title ends "Order at your table".
//
// Also adds to OO.pages.acct (the T3d kit, see not-found.jsx):
//   useReorder(hotel) → { state, run(order), dismiss() }: loads the menu (OO.data.loadHotel), rebuilds the order with
//       OO.cart.fromOrder (rewards left out; missing, unavailable or sold-out items skipped), adds the lines via
//       OO.store.cartAdd, then goes to the cart, or keeps a notice in `state` when something was left out. Age gate
//       (W3-fix #1; P2-21's, OO.pages.ItemSheet.age): when the rebuilt lines hold an age-restricted product and this
//       session's age isn't verified for the store (reorderGated), nothing is added yet: `state.gate` = { names, store,
//       confirm(dob), decline() } asks "Verify your age" (ReorderNotice renders ReorderAgeGate). Confirm keeps the date
//       for the session (age.confirm: checkout's customer_dob) and adds every line; Cancel adds the rest and names
//       the restricted items in the notice. Combos are never gated (REF).
//   ReorderNotice { notice, hotel }: the notice, or the age gate while state.gate is set.
//   reorderGated(lines, menu, hotel, store) → [line]: the item lines (never a combo) whose menu product is
//       age-restricted (age.restricted: its own `av` or its category's), when the session has no verified date of
//       birth for the store (age.verifiedDob); else [].
//   reorderText(lines, skipped, declined) → the notice: 'Added 2 items to your cart. Fries isn't available right
//       now. Corona wasn't added (age restricted).' (skipped = fromOrder's [{ name }], declined = names).
//   ReorderAgeGate { names, store, onConfirm(dob), onDecline }: "Verify your age" for the named items (the item
//       sheet's gate: the store's minimum age, the signed-in customer's birthday prefilled, age.check's messages).
//   orderModsText(line)    orderWhen(order) → 'Today, 6:02pm'.
// And OO.pages.OrderStatus.logic (the page's pure helpers, for tests): linkOrderId(query) → the confirmation link's
// order_id (trimmed, ≤ 64 characters) | null · wantsPrint(query) → ?print=1 · ticketMade(rawOnlineOrderStatus) → its
// ticket_created flag · PRINT_DELAY_MS (350) · pollDelay(elapsedMs) → ms | null (the poll's next wait, see Live status:
// OO.pages.Track.logic.pollDelay's answer when the bundle has the track page, else 10000 below 30 minutes, then null) ·
// P4-08: stepsFor(key, kind) → [{ key, label, state }] and headline(key, kind, store, progress) → { title, body } (kind
// 'delivery' | 'pickup' | 'dinein' | null; 'dinein' reads as null but for the staff-help lines; see "At a table").
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var POLL_MS = 10000;         // without the track page's schedule: a poll every 10 s …
  var POLL_RUN_MS = 30 * 60000; // … for 30 minutes of a visible tab (D-17's cap); then "Refresh for the latest status"
  var POLL_ERRORS_MAX = 6;     // failed polls in a row before the run stops (Refresh)
  var ORDER_RETRY_MS = 30000;  // get_order_data again while the ticket doesn't exist yet
  var PRINT_DELAY_MS = 350;    // ?print=1: the print dialog once the receipt (the ticket) shows (REF's delay)
  // The stepper's order. 'payment' (awaiting payment) comes before Received: every step is still to do.
  var RANK = { payment: -1, received: 0, preparing: 1, ready: 2, completed: 3 };

  function arr(v) { return Array.isArray(v) ? v : []; }
  // A query value as trimmed text ('' for none).
  function qtext(v) { return typeof v === 'string' ? v.trim() : ''; }

  // ---- reorder (OO.cart.fromOrder rebuilds the lines on today's menu) -------------------------------------------
  function uniq(names) { return names.filter(function (x, i) { return x && names.indexOf(x) === i; }); }
  function listText(names) {
    var n = uniq(names);
    if (n.length <= 1) return n[0] || '';
    return n.slice(0, -1).join(', ') + ' and ' + n[n.length - 1];
  }
  function reorderText(lines, skipped, declined) {
    var out = [];
    if (lines.length) out.push('Added ' + OO.format.pluralize(OO.cart.count(lines), 'item') + ' to your cart.');
    if (skipped.length) {
      var what = listText(skipped.map(function (x) { return x.name; }));
      out.push((lines.length ? '' : 'Sorry, ') + what + (skipped.length > 1 ? " aren't" : " isn't") + ' available right now.');
    }
    if (declined.length) out.push(listText(declined) + (uniq(declined).length > 1 ? " weren't" : " wasn't") + ' added (age restricted).');
    return out.length ? out.join(' ') : 'There is nothing in this order to add again.';
  }

  // ---- the reorder's age gate (W3-fix #1; see useReorder in the header) ------------------------------------------
  function ageKit() { return OO.pages.ItemSheet && OO.pages.ItemSheet.age; }
  function reorderGated(lines, menu, hotel, store) {
    var A = ageKit();
    if (!A || !menu || !menu.products) return [];
    var hit = arr(lines).filter(function (l) { return !!l && l.kind !== 'combo' && A.restricted(menu.products[l.itemId] || null, menu); });
    return hit.length && !A.verifiedDob(String(hotel == null ? '' : hotel), store || null) ? hit : [];
  }
  function selectSession(s) { return s.session; }
  // The signed-in customer's birthday as 'YYYY-MM-DD' (the gate's prefill, as the item sheet's), else ''.
  function birthdayOf(session) {
    var b = session && session.customer && session.customer.birthday != null ? String(session.customer.birthday).trim() : '';
    return /^\d{4}-\d{2}-\d{2}/.test(b) ? b.slice(0, 10) : '';
  }
  // The item sheet's "Verify your age" (P2-21 AgeGate) for the order's restricted items. Cancel or closing it declines.
  function ReorderAgeGate(props) {
    var ui = OO.ui, A = ageKit();
    var session = OO.useStore(selectSession);
    var min = A.minimumAge(props.store);
    var dobS = React.useState(function () { return birthdayOf(session); });
    var errS = React.useState('');
    var names = uniq(arr(props.names));
    var many = names.length > 1;
    function confirm() {
      var r = A.check(dobS[0], min, A.todayOf(props.store));
      if (!r.ok) { errS[1](many ? r.message.replace('this item', 'these items') : r.message); return; }
      props.onConfirm(String(dobS[0]).trim().slice(0, 10));
    }
    return (
      <ui.Sheet open variant="dialog" title="Verify your age" onClose={props.onDecline} className="oo-item-age"
        footer={[
          <ui.Button key="cancel" variant="outline" className="oo-item-age__btn" onClick={props.onDecline}>Cancel</ui.Button>,
          <ui.Button key="ok" className="oo-item-age__btn" onClick={confirm}>Confirm</ui.Button>,
        ]}>
        <p className="oo-item-age__text">
          <b>{listText(names)}</b>{(many ? ' are age restricted items' : ' is an age restricted item') + '. You must be ' + min + ' or over to continue.'}
        </p>
        <ui.TextInput label="Date of birth" labelVisible type="date" autoComplete="bday" value={dobS[0]} error={errS[0] || undefined}
          onChange={function (v) { dobS[1](v); if (errS[0]) errS[1](''); }} />
        <p className="oo-item-age__note">Photo ID may be required on delivery or collection.</p>
      </ui.Sheet>
    );
  }

  function useReorder(hotel) {
    var st = React.useState(null); // null | { orderId, busy, tone, text, cart } | { orderId, busy: false, gate }
    var alive = React.useRef(true);
    React.useEffect(function () {
      alive.current = true;
      return function () { alive.current = false; };
    }, []);
    // Adds the lines, then the cart (the whole order went in) or the notice (something was left out).
    function finish(orderId, lines, skipped, declined) {
      lines.forEach(function (l) { OO.store.cartAdd(l); });
      if (lines.length && !skipped.length && !declined.length) {
        st[1](null);
        OO.router.go(OO.router.href('cart', null, { hotel: hotel }));
        return;
      }
      st[1]({ orderId: orderId, busy: false, tone: 'warn', text: reorderText(lines, skipped, declined), cart: lines.length > 0 });
    }
    function run(order) {
      if (!order || (st[0] && st[0].busy)) return;
      st[1]({ orderId: order.id, busy: true });
      OO.data.loadHotel(hotel).then(function (res) {
        if (!alive.current) return;
        // The menu as of now (this page loads the store only, so nothing else re-checks its availability).
        var menu = OO.data.refreshMenu(hotel) || res.menu;
        var r = OO.cart.fromOrder(order, menu, { now: new Date() });
        var gated = reorderGated(r.lines, menu, hotel, res.store);
        if (!gated.length) { finish(order.id, r.lines, r.skipped, []); return; }
        st[1]({
          orderId: order.id, busy: false,
          gate: {
            names: gated.map(function (l) { return l.name; }), store: res.store,
            confirm: function (dob) {
              ageKit().confirm(hotel, dob);
              if (alive.current) finish(order.id, r.lines, r.skipped, []);
            },
            decline: function () {
              var rest = r.lines.filter(function (l) { return gated.indexOf(l) < 0; });
              if (alive.current) finish(order.id, rest, r.skipped, gated.map(function (l) { return l.name; }));
            },
          },
        });
      }, function () {
        if (!alive.current) return;
        st[1]({ orderId: order.id, busy: false, tone: 'danger', text: "We couldn't load the menu. Please try again.", cart: false });
      });
    }
    return { state: st[0], run: run, dismiss: function () { st[1](null); } };
  }

  function ReorderNotice(props) {
    var n = props.notice;
    if (n && n.gate) {
      return <ReorderAgeGate key={'age-' + n.orderId} names={n.gate.names} store={n.gate.store} onConfirm={n.gate.confirm} onDecline={n.gate.decline} />;
    }
    if (!n || n.busy || !n.text) return null;
    var ui = OO.ui;
    var cart = OO.router.link('cart', null, { hotel: props.hotel });
    return (
      <div className={ui.cx('oo-acct__reorder', props.className)}>
        <ui.InlineNotice tone={n.tone} icon="Info">{n.text}</ui.InlineNotice>
        {n.cart ? <ui.Button variant="link" href={cart.href} onClick={cart.onClick}>View cart</ui.Button> : null}
      </div>
    );
  }

  // 'Make it a Meal · 1 × Onion Rings, 1 × Mountain Dew' (the cart's displayMods format).
  function modsText(sizeName, mods) {
    var parts = [];
    if (sizeName) parts.push(sizeName);
    var mt = arr(mods).map(function (m) { return (m.count || 1) + ' × ' + m.name; }).join(', ');
    if (mt) parts.push(mt);
    return parts.join(' · ');
  }
  function orderModsText(line) { return line && line.kind !== 'combo' ? modsText(line.sizeName, line.modifiers) : ''; }
  function orderWhen(order) {
    if (!order) return '';
    return [order.placedLabelLong, order.placedTime].filter(Boolean).join(', ');
  }

  acct.useReorder = useReorder;
  acct.ReorderNotice = ReorderNotice;
  acct.reorderGated = reorderGated;
  acct.reorderText = reorderText;
  acct.ReorderAgeGate = ReorderAgeGate;
  acct.orderModsText = orderModsText;
  acct.orderWhen = orderWhen;

  // ---- status ----------------------------------------------------------------------------------------------
  // kind: 'delivery' | 'pickup' | null. null = the order type is unknown (P3-02: someone else's order without a link
  // naming it; online_order_status carries no order type): neutral words, never a guess.
  // P4-08: kind 'dinein' (a table order; see the header). P4-INT (owner wording, 2026-10-05): it reads like a normal
  // order being prepared, so its steps are the neutral ones: Received → Preparing → Ready → Completed.
  function stepsFor(key, kind) {
    var neutral = !kind || kind === 'dinein';
    var labels = ['Received', 'Preparing',
      kind === 'delivery' ? 'On its way' : (neutral ? 'Ready' : 'Ready for pickup'),
      kind === 'delivery' ? 'Delivered' : (neutral ? 'Completed' : 'Collected')];
    var at = RANK[key]; // 'payment' (-1), 'checking' and 'unknown' (none): every step is still to do
    return labels.map(function (label, i) {
      return { key: i, label: label, state: i < at || key === 'completed' ? 'done' : (i === at ? 'current' : 'todo') };
    });
  }

  // P4-08 + P4-INT (owner wording, 2026-10-05): a dine-in order's headlines are the neutral ones (the type nothing
  // tells: "Order received", "Your order is ready", "Order complete"; no words about who brings the food), except where
  // those send the customer to the store for help: at a table it's a staff member (see the header).
  var DINE_HELP = {
    unknown: "We can't show live updates for this order. If you need help, please ask a staff member.",
    rejected: "The store couldn't take this order. If you were charged, please ask a staff member.",
    cancelled: 'This order was cancelled. If you were charged, please ask a staff member.',
  };
  function headline(key, kind, store, progress) {
    if (kind === 'dinein') {
      var h = headline(key, null, store, progress);
      return Object.prototype.hasOwnProperty.call(DINE_HELP, key) ? { title: h.title, body: DINE_HELP[key] } : h;
    }
    switch (key) {
      case 'payment': return { title: 'Awaiting payment', body: 'Your order goes to the store as soon as your payment is confirmed.' };
      case 'checking': return { title: 'Checking your order', body: 'Getting the latest status from the store.' };
      case 'unknown': return { title: 'Order placed', body: "We can't show live updates for this order. If you need help, please contact the store." };
      case 'preparing': return { title: 'Preparing your order', body: (progress && progress.message) || 'The kitchen is working on it now.' };
      case 'ready': return kind === 'delivery'
        ? { title: 'On its way', body: 'Your order is out for delivery.' }
        : (kind ? { title: 'Ready for pickup', body: 'Head to the counter at ' + store.name + '.' }
          : { title: 'Your order is ready', body: 'The store has finished your order.' });
      case 'completed': return {
        title: kind === 'delivery' ? 'Delivered' : (kind ? 'Collected' : 'Order complete'),
        body: 'Thanks for ordering with ' + store.name + '.',
      };
      case 'rejected': return { title: 'Order declined', body: "The store couldn't take this order. If you were charged, please call the store." };
      case 'cancelled': return { title: 'Order cancelled', body: 'This order was cancelled. If you were charged, please call the store.' };
      default: return { title: 'Order received', body: 'The store has your order and will start on it shortly.' };
    }
  }

  // The pickup / delivery time: the live deliver_time ('Today, 6:47pm'), else `fallback`.
  function timeText(progress, store, fallback) {
    var dt = progress && progress.deliverTime;
    if (dt) {
      var t = Date.parse(dt);
      if (!isNaN(t)) {
        var c = OO.format.zoned(new Date(t), store.timezone);
        var today = OO.format.zoned(new Date(), store.timezone).ymd;
        return OO.format.relativeDay(c.ymd, today) + ', ' + OO.format.time12(c.minutes);
      }
    }
    return fallback || '';
  }

  // ---- the owner's price summary (P3-05: OO.model's Order.totals, REF's rows) ----------------------------------------
  // '13%' · '1.5%' · '13.05%'.
  function percentText(n) { return String(Math.round(n * 100) / 100) + '%'; }
  // 'Coupon SAVE10 (10% off)' · 'Coupon SAVE10' · 'Coupon (Free burger)' · 'Coupon'.
  function couponLabel(c) {
    var name = c.name && c.name !== c.code ? c.name : '';
    if (c.code) return 'Coupon ' + c.code + (name ? ' (' + name + ')' : '');
    return name ? 'Coupon (' + name + ')' : 'Coupon';
  }
  // The PriceRows rows of a ticket, in checkout's order (Sub-total, Delivery, Surcharge, Tax, the savings), plus a tip and
  // a part payment's Paid / Remaining. Delivery, surcharge, tip and the savings only when there are some.
  function totalRows(order, store) {
    var T = order.totals;
    var cur = store.currency;
    var money = function (n) { return acct.money(n, store); };
    var minus = function (n) { return OO.format.minusMoney(n, cur); };
    var word = (store.loyalty && store.loyalty.pointsWord) || 'points';
    var rows = [{ key: 'subtotal', label: 'Sub-total', value: money(T.subtotal) }];
    if (T.delivery > 0) rows.push({ key: 'delivery', label: 'Delivery', value: money(T.delivery) });
    if (T.surcharge > 0) rows.push({ key: 'surcharge', label: (T.surchargeLabel || 'Surcharge') + (T.surchargePercent > 0 ? ' (' + percentText(T.surchargePercent) + ')' : ''), value: money(T.surcharge) });
    if (T.tip > 0) rows.push({ key: 'tip', label: 'Tip', value: money(T.tip) });
    if (!store.hideTaxLabel) rows.push({ key: 'tax', label: store.taxInclusive ? 'Tax (GST inclusive)' : 'Tax (GST Exclusive)', value: money(T.tax) });
    if (T.coupon) {
      rows.push({ key: 'coupon', label: couponLabel(T.coupon), value: T.coupon.amount > 0 ? minus(T.coupon.amount) : (T.coupon.free ? 'Free item' : 'Applied') });
    }
    if (T.points && T.points.amount > 0) {
      var pts = Math.round(T.points.points);
      rows.push({ key: 'points', label: 'Paid with ' + word + (pts > 0 ? ' (' + pts + ')' : ''), value: minus(T.points.amount) });
    }
    if (T.discount > 0) rows.push({ key: 'discount', label: T.discountLabel, value: minus(T.discount) });
    if (T.paid > 0 && T.remaining > 0) {
      rows.push({ key: 'paid', label: 'Paid', value: money(T.paid) });
      rows.push({ key: 'remaining', label: 'Remaining', value: money(T.remaining) });
    }
    return rows;
  }

  function telHref(phone) {
    var digits = String(phone || '').replace(/[^\d+]/g, '');
    return digits ? 'tel:' + digits : null;
  }

  // ---- polling helpers -------------------------------------------------------------------------------------
  function hidden() { return typeof document !== 'undefined' && document.visibilityState === 'hidden'; }

  // D-17 (P3-FIX #20): the wait before the next poll once the run's schedule has used `elapsed` ms → ms | null (the run
  // is over). The track page's schedule, read when a poll falls due (track.jsx loads after this file); a bundle without
  // it polls every POLL_MS for POLL_RUN_MS.
  function pollDelay(elapsed) {
    var T = OO.pages.Track && OO.pages.Track.logic;
    if (T && typeof T.pollDelay === 'function') return T.pollDelay(elapsed);
    return (Number(elapsed) || 0) < POLL_RUN_MS ? POLL_MS : null;
  }

  // setTimeout for a visible tab: when the delay ends while the tab is hidden, fn runs as soon as the tab is visible
  // again (one catch-up call). → cancel().
  function visibleTimeout(fn, ms) {
    var timer = null;
    var waiting = false;
    var over = false;
    function fire() { if (!over) { over = true; fn(); } }
    function stopWaiting() {
      if (!waiting) return;
      waiting = false;
      document.removeEventListener('visibilitychange', onVisible);
    }
    function onVisible() { if (!hidden()) { stopWaiting(); fire(); } }
    timer = setTimeout(function () {
      timer = null;
      if (!hidden()) { fire(); return; }
      waiting = true;
      document.addEventListener('visibilitychange', onVisible);
    }, ms);
    return function cancel() { over = true; if (timer) clearTimeout(timer); stopWaiting(); };
  }

  // A failure a retry won't change: the order isn't there. By STATUS (fixture errors carry no kind; production 404
  // bodies are nginx HTML): online_order_status answers a miss with 400 NO_DATA, get_order_data with 404 (no ticket
  // yet) or 400 (no id).
  function isGone(err) {
    var code = err && err.status;
    return code === 400 || code === 404 || code === 422 || !!(err && err.kind === 'notFound');
  }

  // ---- parts -----------------------------------------------------------------------------------------------
  function Stepper(props) {
    return (
      <ol className="oo-ostatus__steps" aria-label="Order progress">
        {props.steps.map(function (s) {
          return (
            <li key={s.key} className={'oo-ostatus__step is-' + s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
              <span className="oo-ostatus__dot" aria-hidden="true">{s.state === 'done' ? <OO.icons.Check size={12} strokeWidth={3.4} /> : null}</span>
              <span className="oo-ostatus__label">
                {s.label}
                <span className="oo-sr-only">{s.state === 'done' ? ' (done)' : (s.state === 'current' ? ' (now)' : '')}</span>
              </span>
            </li>
          );
        })}
      </ol>
    );
  }

  function Lines(props) {
    var store = props.store;
    return (
      <ul className="oo-ostatus__lines">
        {props.lines.map(function (l, i) {
          var mods = orderModsText(l);
          return (
            <li key={i} className="oo-ostatus__line">
              <span className="oo-ostatus__qty">{l.qty + '×'}</span>
              <span className="oo-ostatus__lmain">
                <span className="oo-ostatus__lname">
                  {l.name}
                  {l.feeDescription ? <OO.ui.ChargeInfo name={l.name} description={l.feeDescription} /> : null}
                  {l.reward ? <OO.ui.Badge tone="success" size="sm">Reward</OO.ui.Badge> : null}
                </span>
                {mods ? <span className="oo-ostatus__lmods">{mods}</span> : null}
                {l.kind === 'combo' && arr(l.items).length ? (
                  <span className="oo-ostatus__citems">
                    {l.items.map(function (it, j) {
                      var im = modsText(it.sizeName, it.modifiers);
                      return <span key={j} className="oo-ostatus__citem">{(it.qty > 1 ? it.qty + ' × ' : '') + it.name + (im ? ' (' + im + ')' : '')}</span>;
                    })}
                  </span>
                ) : null}
                {l.notes ? <span className="oo-ostatus__lnote">{'Note: ' + l.notes}</span> : null}
              </span>
              <span className="oo-ostatus__lprice">{acct.money(l.total, store)}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  // The snapshot's lines (OO.model.orderSnapshot): mods and totals are the text checkout showed.
  function SnapshotLines(props) {
    return (
      <ul className="oo-ostatus__lines">
        {props.lines.map(function (l, i) {
          return (
            <li key={i} className="oo-ostatus__line">
              <span className="oo-ostatus__qty">{l.qty + '×'}</span>
              <span className="oo-ostatus__lmain">
                <span className="oo-ostatus__lname">{l.name}</span>
                {l.mods ? <span className="oo-ostatus__lmods">{l.mods}</span> : null}
              </span>
              {l.total ? <span className="oo-ostatus__lprice">{l.total}</span> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  function Skeleton() {
    var ui = OO.ui;
    return (
      <div className="oo-acct__skel">
        <ui.Skeleton lines={2} className="oo-acct__skel-title" />
        <ui.Skeleton variant="block" height={168} />
        <ui.Skeleton variant="block" height={220} />
      </div>
    );
  }

  // ---- page ------------------------------------------------------------------------------------------------
  // The order a confirmation link names (REF's /order-confirmed and /success, sent here with their query): order_id.
  function linkOrderId(query) {
    var id = qtext(query && query.order_id);
    return id && id.length <= 64 ? id : null;
  }
  // ?print=1: REF's "Download" link and the order history's Receipt action.
  function wantsPrint(query) { return qtext(query && query.print) === '1'; }
  // The browser's print dialog (the receipt's print styles apply); nothing where there is none.
  function printReceipt() {
    if (typeof window !== 'undefined' && typeof window.print === 'function') window.print();
  }
  // A live status body that says the POS ticket exists (online_order_status ticket_created).
  function ticketMade(raw) {
    var v = raw && raw.ticket_created;
    return v === true || v === 1 || v === '1' || v === 'true';
  }

  // The public view's note (not the owner, OO.model.orderAccess): why there are no items, and what to do about it.
  function PrivateNote(props) {
    var ui = OO.ui;
    var failed = props.access.state === 'error';
    var login = OO.router.link('profile', null, { hotel: props.hotel, query: { directLogin: 'true', tab: 'orders' } });
    var text = failed ? "We couldn't check your orders, so this order's items and receipt aren't shown."
      : props.signedIn ? "This order isn't on your account, so its items and receipt aren't shown."
        : 'The items and the receipt are only shown to the customer who placed this order.';
    // P4-08: noLogin (mode qr, D-20): no "Log in" at a table.
    var action = failed ? <ui.Button variant="outline" size="xs" onClick={props.onRetry}>Try again</ui.Button>
      : (props.signedIn || props.noLogin ? null : <ui.Button variant="outline" size="xs" href={login.href} onClick={login.onClick}>Log in</ui.Button>);
    return (
      <div className="oo-ostatus__private">
        <ui.InlineNotice tone="info" icon="Info">{text}</ui.InlineNotice>
        {action}
      </div>
    );
  }

  // P4-08: the store's live table context's id (P4-01's OO.pages.Table.context, else OO.store.tableFor: the same
  // context), or null. The page subscribes to OO.store's table, so it renders again when the context changes.
  function liveTableId(hotel) {
    var T = OO.pages.Table;
    var t = null;
    try {
      t = T && typeof T.context === 'function' ? T.context(hotel)
        : (OO.store && typeof OO.store.tableFor === 'function' ? OO.store.tableFor(hotel) : null); // P4-INT: P4-01's store API
    } catch (e) { t = null; }
    if (!t || typeof t !== 'object' || (t.hotel && String(t.hotel).toLowerCase() !== String(hotel).toLowerCase())) return null;
    var id = t.id === undefined || t.id === null ? '' : String(t.id).trim();
    return id || null;
  }

  OO.pages.OrderStatus = function OrderStatus(props) {
    var route = props.route;
    var mode = props.mode;
    var hotel = route.hotel;
    var query = route.query || {};
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var storeKey = store ? store.id : null;
    var tz = store ? store.timezone : null;
    var lastOrder = OO.useStore(function (s) { return s.lastOrder; });
    var session = OO.useStore(function (s) { return s.session; });
    var table = OO.useStore(function (s) { return s.table; }); // D-20: the track link's gate
    var uid = session && session.customer ? session.customer.uid : null;
    // P4-08: mode qr keeps to the table (see the header): the URL's table (the callback's ?t_id=), else the live one's.
    var qr = mode === 'qr';
    var tableId = qr ? (OO.router.tableIdOf(query) || liveTableId(hotel)) : null;
    var tableMenu = OO.router.link('menu', tableId ? { tableId: tableId } : null, { hotel: hotel });
    var directId = route.params.orderId ? String(route.params.orderId) : null;
    var linkId = directId ? null : linkOrderId(query);
    var lastId = !directId && !linkId && lastOrder && lastOrder.hotel === hotel && lastOrder.orderId ? String(lastOrder.orderId) : null;

    // /orderStatus with nothing on this device: the newest order in the history (logged in only). Read fresh (one
    // request per such visit), which also renews the list the ownership check below reads.
    var latestQ = OO.useAsync(function () {
      if (directId || linkId || lastId || !uid || !storeKey) return null;
      return OO.data.ownOrders(hotel, { fresh: true }).then(function (r) {
        return r && r.ids.length ? r.ids[0] : null;
      });
    }, [hotel, directId, linkId, lastId, uid, storeKey]);
    var orderId = directId || linkId || lastId || (latestQ.data || null);
    var resolving = !directId && !linkId && !lastId && !!uid && (latestQ.loading || !storeKey);
    var ready = !!(orderId && storeKey);

    // The live status: st = { id, raw (the last online_order_status body) | null, state 'loading' | 'ok' | 'retrying'
    // | 'gone' | 'off' | 'stale' }. Progress is derived at render, so the order type (ticket, else snapshot) is current.
    var st = React.useState(null);
    var pollRun = React.useState(0);
    var stCur = st[0] && st[0].id === orderId ? st[0] : null;
    var made = !!(stCur && ticketMade(stCur.raw));

    // Who may see the detail (OO.model.orderAccess; see the header): this device's order, the newest order of the
    // signed-in customer's own history, or one on their list (OO.data.ownOrders: the session's list, so no request once
    // it was read). againS holds the order whose list was asked for again (fresh): see recheck below.
    var device = !!orderId && typeof OO.api.isOwnOrder === 'function' && !!OO.api.isOwnOrder(hotel, orderId);
    var account = !directId && !linkId && !lastId && !!orderId && orderId === latestQ.data;
    var againS = React.useState(null);
    var again = !!orderId && againS[0] === orderId;
    var ownQ = OO.useAsync(function () {
      if (!orderId || device || account || !uid || !storeKey) return null;
      var at = hotel;
      return OO.data.ownOrders(hotel, again ? { fresh: true } : undefined).then(function (r) {
        return r ? { uid: r.uid, hotel: at, ids: r.ids } : null;
      });
    }, [hotel, orderId, device, account, uid, storeKey, again]);
    // The list as last read for THIS customer at this store (kept while it is read again, so the page doesn't flicker;
    // never another customer's).
    var read = ownQ.data && ownQ.data.uid === uid && ownQ.data.hotel === hotel ? ownQ.data.ids : null;
    var access = OO.model.orderAccess({
      orderId: orderId, device: device, account: account, signedIn: !!uid, list: read || (ownQ.error ? 'error' : 'loading'),
    });
    var owner = access.owner;
    // P3-FIX #21: the live status says the ticket exists, and the list (as read, not while a read is on its way) doesn't
    // hold the order: a ticket made since the list was read can be theirs, so it is read again, fresh, once per order.
    // Once the order is on the list (the owner) nothing is read again.
    var recheck = made && !!read && !owner && !again && !ownQ.loading;
    React.useEffect(function () { if (recheck) againS[1](orderId); }, [recheck, orderId]);

    // The ticket (get_order_data; the owner only): tk = { id, state 'ok' | 'missing' | 'error', order }. Kept while it
    // is asked for again (orderRun), so a retry never flashes the skeleton and a failed reload keeps a loaded ticket.
    var tk = React.useState(null);
    var orderRun = React.useState(0);
    React.useEffect(function () {
      if (!ready || !owner) return undefined;
      var alive = true;
      var id = orderId;
      OO.api.getOrder(hotel, id).then(function (raw) {
        if (!alive) return;
        var o = OO.model.normalizeOrder(raw, { now: new Date(), timeZone: tz });
        tk[1](function (cur) { return o ? { id: id, state: 'ok', order: o } : { id: id, state: 'missing', order: cur && cur.id === id ? cur.order : null }; });
      }, function (err) {
        if (!alive) return;
        tk[1](function (cur) { return { id: id, state: isGone(err) ? 'missing' : 'error', order: cur && cur.id === id ? cur.order : null }; });
      });
      return function () { alive = false; };
    }, [hotel, orderId, ready, owner, orderRun[0]]);
    var tkCur = owner && tk[0] && tk[0].id === orderId ? tk[0] : null;
    var order = tkCur ? tkCur.order : null;

    var snapshot = React.useMemo(function () {
      return owner ? OO.model.orderSnapshot(lastOrder, { orderId: orderId, hotel: hotel, now: new Date(), timeZone: tz }) : null;
    }, [owner, lastOrder, orderId, hotel, tz]);
    // A confirmation link's display params (OO.model.orderLinkSnapshot): the status-only view's details.
    var link = React.useMemo(function () {
      return OO.model.orderLinkSnapshot(query, { orderId: orderId, now: new Date(), timeZone: tz });
    }, [query, orderId, tz]);
    var type = order ? order.orderType : (snapshot ? snapshot.orderType : ((link && link.orderType) || 'PICKUP'));
    var progress = stCur && stCur.raw ? OO.model.orderProgress(stCur.raw, { orderType: type }) : null;
    var view = OO.model.orderStatusView({
      order: order,
      orderState: owner ? (order ? 'ok' : (tkCur ? tkCur.state : 'loading')) : (access.state === 'checking' ? 'loading' : 'private'),
      progress: progress, statusState: stCur ? stCur.state : 'loading', snapshot: snapshot,
    });
    var final = view.final;

    // The poll: a setTimeout chain on D-17's backoff (pollDelay; cleared on unmount, order change, a final state or
    // Refresh), paused while hidden.
    React.useEffect(function () {
      if (!ready || final) return undefined;
      var alive = true;
      var id = orderId;
      var elapsed = 0; // the schedule used so far this run (the waits, in ms)
      var errors = 0;
      var cancel = null;
      function put(state, raw) {
        st[1](function (cur) { return { id: id, raw: raw !== undefined ? raw : (cur && cur.id === id ? cur.raw : null), state: state }; });
      }
      function later() {
        var wait = pollDelay(elapsed);
        if (wait === null) { put('stale'); return; }
        elapsed += wait;
        cancel = visibleTimeout(tick, wait);
      }
      function tick() {
        cancel = null;
        OO.api.orderStatus(id, hotel).then(function (raw) {
          // P1-15: a 202 the payment callback couldn't settle (still PROCESSING) clears the kept cart once the order is
          // past PROCESSING. A no-op unless lastOrder is this order with clearCartOnSuccess (and only once).
          var paycb = OO.pages.acct && OO.pages.acct.paycb;
          if (paycb && paycb.settlesOn202(raw)) OO.store.settleOrder(id);
          if (!alive) return;
          errors = 0;
          put('ok', raw);
          if (!OO.model.orderProgress(raw).done) later();
        }, function (err) {
          if (!alive) return;
          if (isGone(err)) { put('gone'); return; }
          errors += 1;
          if (errors >= POLL_ERRORS_MAX) { put('off'); return; }
          put('retrying');
          later();
        });
      }
      // A (re)start keeps the last answer on screen while its first poll runs.
      st[1](function (cur) {
        var raw = cur && cur.id === id ? cur.raw : null;
        if (cur && cur.id === id && (cur.state === 'ok' || cur.state === 'loading')) return cur;
        return { id: id, raw: raw, state: raw ? 'ok' : 'loading' };
      });
      tick();
      return function () { alive = false; if (cancel) cancel(); };
    }, [hotel, orderId, ready, final, pollRun[0]]);

    // No ticket yet while the status poll runs: ask get_order_data again in 30 s (each answer re-arms it).
    var retryOrder = view.retryOrder;
    React.useEffect(function () {
      if (!retryOrder) return undefined;
      return visibleTimeout(function () { orderRun[1](function (n) { return n + 1; }); }, ORDER_RETRY_MS);
    }, [retryOrder, orderId, tk[0]]);

    var number = order && order.shortId ? order.shortId
      : (view.pickupNumber ? '#' + view.pickupNumber : (link && link.pickupNumber ? '#' + link.pickupNumber : null));
    React.useEffect(function () {
      if (number) document.title = 'Order ' + number + ' · ' + (mode === 'app' ? 'SIPO' : mode === 'qr' ? 'Order at your table' : 'Order online');
    }, [number, mode]);

    // The receipt (the owner's ticket view): ?print=1 opens the print dialog once per order, PRINT_DELAY_MS after it
    // shows (REF); Print receipt any time.
    var receipt = owner && view.show === 'ticket';
    var printWanted = wantsPrint(query);
    var printedRef = React.useRef(null);
    React.useEffect(function () {
      if (!printWanted || !receipt || printedRef.current === orderId) return undefined;
      var timer = setTimeout(function () {
        printedRef.current = orderId;
        printReceipt();
      }, PRINT_DELAY_MS);
      return function () { clearTimeout(timer); };
    }, [printWanted, receipt, orderId]);

    // P5-06b: purchase, once per order this device placed, once it went through (OO.analytics decides: src/lib/analytics.jsx).
    React.useEffect(function () { if (device && store && OO.analytics) OO.analytics.purchase(lastOrder, store); }, [device, lastOrder, storeKey]);

    var re = useReorder(hotel);

    var back = qr ? tableMenu.href // P4-08: the table menu
      : directId ? OO.router.href('orders', null, { hotel: hotel }) : OO.router.href(mode === 'app' ? 'home' : 'menu', null, { hotel: hotel });
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: back, profile: !qr }); // P4-08 (D-20): no account link at a table
    var title = 'Order status';
    var blocked = acct.gate(h, { title: title, header: header, skeleton: <Skeleton /> });
    if (blocked) return blocked;

    var menuLink = qr ? tableMenu : OO.router.link('menu', null, { hotel: hotel });
    var ordersLink = OO.router.link('orders', null, { hotel: hotel });
    // The signed-in customer's list couldn't be read: whether this is their order is unknown (never "not found").
    var unsure = !owner && access.state === 'error';
    function askAgain() {
      orderRun[1](function (n) { return n + 1; });
      pollRun[1](function (n) { return n + 1; });
      if (unsure) ownQ.reload();
    }

    // ---- no order to show ----
    if (!orderId) {
      if (resolving || latestQ.loading) return <acct.Page title={title} {...header} busy><Skeleton /></acct.Page>;
      var login = OO.router.link('profile', null, { hotel: hotel, query: { directLogin: 'true', tab: 'orders' } });
      return (
        <acct.Page title={title} {...header} className="oo-ostatus">
          <ui.EmptyState icon="Receipt" title="No recent orders"
            body={latestQ.error ? "We couldn't load your orders. Please try again." : 'Orders you place on this device will show up here.'}
            action={(
              <div className="oo-acct__actions">
                <ui.Button size="md" href={menuLink.href} onClick={menuLink.onClick}>Start an order</ui.Button>
                {!uid && !qr ? <ui.Button size="md" variant="outline" href={login.href} onClick={login.onClick}>Log in</ui.Button> : null}
                {latestQ.error ? <ui.Button size="md" variant="outline" onClick={latestQ.reload}>Try again</ui.Button> : null}
              </div>
            )} />
          <ui.PoweredBySipo />
        </acct.Page>
      );
    }
    if (view.show === 'loading') {
      return <acct.Page title={title} {...header} busy><span className="oo-sr-only" role="status">Loading your order</span><Skeleton /></acct.Page>;
    }
    if (view.show === 'missing' || view.show === 'error') {
      var missing = view.show === 'missing' && !unsure;
      return (
        <acct.Page title={title} {...header} className="oo-ostatus">
          <ui.EmptyState icon="Receipt"
            title={missing ? "We couldn't find this order" : "We couldn't load this order"}
            body={missing ? (qr ? 'Please ask a staff member for your order.' : 'Check the link in your confirmation email, or look in your orders.') : 'Check your connection and try again.'}
            action={(
              <div className="oo-acct__actions">
                {missing
                  ? (qr ? null : <ui.Button size="md" href={ordersLink.href} onClick={ordersLink.onClick}>Your orders</ui.Button>)
                  : <ui.Button size="md" onClick={askAgain}>Try again</ui.Button>}
                <ui.Button size="md" variant="outline" href={menuLink.href} onClick={menuLink.onClick}>Back to menu</ui.Button>
              </div>
            )} />
          <ui.PoweredBySipo />
        </acct.Page>
      );
    }

    // ---- the order: the ticket, else the snapshot, else the live status alone (with a confirmation link's details) ----
    var snap = view.show === 'snapshot' ? snapshot : null;
    var lnk = view.show === 'status' ? link : null;
    var key = view.key;
    var bad = key === 'rejected' || key === 'cancelled';
    var delivery = type === 'DELIVERY';
    // The order type comes from the ticket, this device's snapshot or the link; else it is unknown: neutral words.
    // P4-08: a dine-in order reads as one ('dinein'); in mode qr every order does (see the header).
    var dine = qr || type === 'DINEIN';
    var kind = dine ? 'dinein' : order || snapshot || (link && link.orderType) ? (delivery ? 'delivery' : 'pickup') : null;
    var hl = headline(key, kind, store, progress);
    var label = number ? 'Order ' + number : 'Your order';
    var typeLabel = dine ? 'Dine-in' : order ? order.typeLabel : (snap ? snap.typeLabel : (lnk ? lnk.typeLabel : null)); // P4-08
    var tel = telHref(store.phone);
    // P3-FIX #20: the public track page, while the status updates automatically (see the header): not at a table (D-20)
    // and not for a store this host doesn't serve (its track route is gated). The row takes the track page's own
    // live-row layout (styles/track.css: the text, then the button at the row's end).
    var served = !(OO.api && typeof OO.api.availabilityOf === 'function' && OO.api.availabilityOf(hotel) === 'unavailable');
    var atTable = !!OO.router.tableGate({ hotel: hotel, page: 'track', params: {}, query: query }, table);
    var trackLink = view.live && served && !atTable ? OO.router.link('track', { orderId: orderId }, { hotel: hotel }) : null;

    var statusCard = (
      <ui.Card className={ui.cx('oo-ostatus__status', bad && 'is-bad')}>
        <div className="oo-ostatus__headline" role="status" aria-live="polite" aria-atomic="true">
          {bad ? <span className="oo-ostatus__badicon" aria-hidden="true"><acct.icons.Alert size={22} /></span> : null}
          <span className="oo-ostatus__htext">
            <span className="oo-ostatus__state">{hl.title}</span>
            <span className="oo-ostatus__msg">{hl.body}</span>
          </span>
        </div>
        {bad ? null : <Stepper steps={stepsFor(key, kind)} />}
        {view.live && trackLink ? (
          <div className="oo-ostatus__live oo-track__live">
            <span className="oo-ostatus__livedot" aria-hidden="true" />
            <span className="oo-track__livetext">Updates automatically</span>
            <ui.Button variant="outline" size="xs" className="oo-track__refresh" href={trackLink.href} onClick={trackLink.onClick}>Track live status</ui.Button>
          </div>
        ) : null}
        {view.live && !trackLink ? (
          <div className="oo-ostatus__live"><span className="oo-ostatus__livedot" aria-hidden="true" />Updates automatically</div>
        ) : null}
        {view.refresh ? (
          <div className="oo-ostatus__live oo-ostatus__live--stale">
            <span>Refresh for the latest status</span>
            <ui.Button variant="outline" size="xs" onClick={askAgain}>Refresh</ui.Button>
          </div>
        ) : null}
      </ui.Card>
    );
    // Not the owner (or the check failed): the public view says why it has no items (signed in or not), never PII.
    var privateNote = !owner && (access.state === 'guest' || access.state === 'error')
      ? <PrivateNote access={access} signedIn={!!uid} noLogin={qr} hotel={hotel} onRetry={ownQ.reload} /> : null;

    var storeRow = (
      <div className="oo-acct__dlrow"><dt>Store</dt><dd>{store.name}<span className="oo-acct__dlsub">{(order && order.storeAddress) || store.address.full}</span></dd></div>
    );
    var detailsCard, itemsCard = null, summaryCard = null;
    if (order) {
      var dtType = String(order.deliveryType || 'ASAP').toUpperCase();
      var reState = re.state && re.state.orderId === order.id ? re.state : null;
      var rows = totalRows(order, store);
      detailsCard = (
        <ui.Card pad="list" className="oo-ostatus__meta">
          <dl className="oo-acct__dl">
            <div className="oo-acct__dlrow"><dt>Placed</dt><dd>{orderWhen(order) || '—'}</dd></div>
            <div className="oo-acct__dlrow"><dt>{typeLabel || 'Pickup'}</dt><dd>{timeText(progress, store, order.pickupLabel || (dtType === 'ASAP' ? 'ASAP' : 'Scheduled'))}</dd></div>
            <div className="oo-acct__dlrow"><dt>Payment</dt><dd>{view.awaitingPayment ? 'Not paid yet' : order.payment.label}</dd></div>
            {delivery && order.address ? <div className="oo-acct__dlrow"><dt>Deliver to</dt><dd>{order.address}</dd></div> : null}
            {dine && snapshot && snapshot.table ? <div className="oo-acct__dlrow"><dt>Table</dt><dd>{snapshot.table}</dd></div> : null}
            {storeRow}
            {order.notes ? <div className="oo-acct__dlrow"><dt>Notes</dt><dd>{order.notes}</dd></div> : null}
          </dl>
        </ui.Card>
      );
      itemsCard = (
        <ui.Card pad="summary" className="oo-ostatus__items">
          <h3 className="oo-acct__cardtitle">Items<span className="oo-acct__cardsub">{OO.format.pluralize(order.itemCount, 'item')}</span></h3>
          <Lines lines={order.lines} store={store} />
        </ui.Card>
      );
      summaryCard = (
        <ui.Card pad="summary" className="oo-ostatus__sum">
          <ui.PriceRows title="Price summary" rows={rows} total={acct.money(order.totals.total, store)}>
            <div className="oo-ostatus__actions">
              {qr ? null : <ui.Button block icon="Bag" busy={!!(reState && reState.busy)} onClick={function () { re.run(order); }}>Order again</ui.Button>}
              <ui.Button block variant="outline" icon="Receipt" onClick={printReceipt}>Print receipt</ui.Button>
            </div>
          </ui.PriceRows>
          <ReorderNotice notice={reState} hotel={hotel} />
        </ui.Card>
      );
    } else if (snap) {
      detailsCard = (
        <ui.Card pad="list" className="oo-ostatus__meta">
          <dl className="oo-acct__dl">
            <div className="oo-acct__dlrow"><dt>Placed</dt><dd>{snap.placedLabel || '—'}</dd></div>
            <div className="oo-acct__dlrow"><dt>{typeLabel}</dt><dd>{timeText(progress, store, snap.whenLabel) || '—'}</dd></div>
            <div className="oo-acct__dlrow"><dt>Payment</dt><dd>{view.awaitingPayment ? 'Not paid yet' : (snap.paymentLabel || '—')}</dd></div>
            {delivery && snap.address ? <div className="oo-acct__dlrow"><dt>Deliver to</dt><dd>{snap.address}</dd></div> : null}
            {snap.table ? <div className="oo-acct__dlrow"><dt>Table</dt><dd>{snap.table}</dd></div> : null}
            {storeRow}
          </dl>
        </ui.Card>
      );
      itemsCard = (
        <ui.Card pad="summary" className="oo-ostatus__items">
          <h3 className="oo-acct__cardtitle">Items<span className="oo-acct__cardsub">{OO.format.pluralize(snap.itemCount, 'item')}</span></h3>
          <SnapshotLines lines={snap.lines} />
        </ui.Card>
      );
      summaryCard = snap.rows.length || snap.total ? (
        <ui.Card pad="summary" className="oo-ostatus__sum">
          <ui.PriceRows title="Price summary" rows={snap.rows} total={snap.total} />
        </ui.Card>
      ) : null;
    } else {
      // The live status alone; a confirmation link adds what its URL says (REF's display: type, time, payment, items,
      // total), never as fact: the payment row says how it was to be paid, the status card whether it is.
      var when = timeText(progress, store, lnk && lnk.eta ? lnk.eta : '');
      var payNote = view.awaitingPayment ? 'Not paid yet' : (lnk && lnk.paymentLabel) || null;
      detailsCard = (
        <ui.Card pad="list" className="oo-ostatus__meta">
          <dl className="oo-acct__dl">
            {lnk && lnk.placedLabel ? <div className="oo-acct__dlrow"><dt>Placed</dt><dd>{lnk.placedLabel}</dd></div> : null}
            {when ? <div className="oo-acct__dlrow"><dt>{typeLabel || 'Time'}</dt><dd>{when}</dd></div> : null}
            {payNote ? <div className="oo-acct__dlrow"><dt>Payment</dt><dd>{payNote}</dd></div> : null}
            {lnk && lnk.itemCount ? <div className="oo-acct__dlrow"><dt>Items</dt><dd>{OO.format.pluralize(lnk.itemCount, 'item')}</dd></div> : null}
            {lnk && lnk.total != null ? <div className="oo-acct__dlrow"><dt>Total</dt><dd>{acct.money(lnk.total, store)}</dd></div> : null}
            {lnk && lnk.table ? <div className="oo-acct__dlrow"><dt>Table</dt><dd>{lnk.table}</dd></div> : null}
            {storeRow}
          </dl>
        </ui.Card>
      );
    }

    // The order's rating (the ratings lane, P3-06/07: OO.pages.Review.OrderRating; owner views only, so the ticket view
    // only), when this bundle has it. The wrapper (display: contents) lets the receipt print leave it out.
    var Rating = order && OO.pages.Review && OO.pages.Review.OrderRating;
    var ratingCard = Rating ? <div className="oo-ostatus__rating"><Rating hotel={hotel} store={store} order={order} /></div> : null;

    var helpCard = (
      <ui.Card className="oo-ostatus__help">
        <span className="oo-ostatus__helpicon" aria-hidden="true"><acct.icons.Phone size={20} /></span>
        <span className="oo-ostatus__helptext">
          <span className="oo-acct__cardtitle">Need help?</span>
          <span className="oo-acct__muted">{qr ? (tel ? 'Ask a staff member, or call the store on ' + store.phone + '.' : 'Please ask a staff member.')
            : tel ? 'Call the store on ' + store.phone + '.' : 'Please contact ' + store.name + ' directly.'}</span>
        </span>
        {tel ? <ui.Button variant="outline" size="xs" href={tel} aria-label={'Call the store, ' + store.phone}>Call the store</ui.Button> : null}
      </ui.Card>
    );

    return (
      <acct.Page title={title} {...header} className={ui.cx('oo-ostatus', receipt && 'oo-ostatus--receipt')}>
        <div className="oo-acct__grid">
          <div className="oo-acct__col">
            <ui.SectionTitle level="page" sub={[store.name, typeLabel].filter(Boolean).join(' · ')}>{label}</ui.SectionTitle>
            {statusCard}
            {qr ? (
              <div className="oo-ostatus__actions">
                <ui.Button block icon="Bag" href={tableMenu.href} onClick={tableMenu.onClick}>Order more</ui.Button>
              </div>
            ) : null}
            {privateNote}
            {detailsCard}
            {itemsCard}
          </div>
          <div className="oo-acct__col">
            {summaryCard}
            {ratingCard}
            {helpCard}
            <ui.PoweredBySipo />
          </div>
        </div>
      </acct.Page>
    );
  };

  // For tests (tests/order-p302.test.mjs, tests/p3fix-b-track.test.mjs): the page's pure helpers.
  OO.pages.OrderStatus.logic = {
    linkOrderId: linkOrderId, wantsPrint: wantsPrint, ticketMade: ticketMade, PRINT_DELAY_MS: PRINT_DELAY_MS, pollDelay: pollDelay,
    stepsFor: stepsFor, headline: headline, // P4-08
  };
})();


// ===================== src/pages/orders.jsx =====================
// src/pages/orders.jsx: the order history page (page id 'orders': /{hotel}/orders and /{hotel}/profile/orders). In app
// mode it is a tab page (bottom tab bar, no back arrow). Styles: styles/pages.css (.oo-orders__* cards) and
// styles/track.css (P3-04: the tabs, the actions row, Load more). Not in the design: built from its parts (PageHeader,
// canvas, white cards, the outline "Reorder" button of the app's "Order again").
//
// Logged out (OO.store session null): a "Log in to see your orders" state (→ /{hotel}/profile?directLogin=true&tab=orders)
// with "Track an order" (→ /{hotel}/track, the public lookup; not at a table, D-20), plus a card for the last order
// placed on this device (OO.store lastOrder) so guests can still follow it.
// Logged in (owner-only, as before: the signed-in customer's uid), two tabs (Batch 2 P3-04; REF OrdersPageClient):
//   Accepted   OO.api.getOrders (E16 get_customer_orders: the POS tickets, per_page 1000) → OO.model.normalizeOrders,
//              plus each ticket's rating and whether it is an online order (its online_orders status) from the rows.
//   All        OO.api.getAllOrders (E17 customer/getOrders: the online_orders rows across the chain, pending and unpaid
//              included, 10 a page) → OO.model.normalizeOnlineOrders (src/lib/model-account.jsx since P3-FIX #22). A
//              store whose adapter has no customer/getOrders (the fixture stores: the facade's "Not available yet")
//              lists its tickets here too.
//   The tab lives in the URL as ?view=all (replace; absent = Accepted), a key the profile page's ?tab= never uses, so
//   Back from an order returns to the same tab. "Load more orders" asks for the next page (current_page / last_page of
//   the answer) and adds it, de-duplicated by order id, newest first. A tab or account change, a reload or an
//   unmount drops the answers still on their way.
// Cards: the title links to /{hotel}/profile/orders/{id} (the owner's order page); the status pill; when, type and the
// branch when it isn't this store; the summary; total, item count and the stars once rated. Actions (REF): Track (a
// live online order → /{hotel}/track/{id}) · Details (live) | Receipt (→ /{hotel}/profile/orders/{id}?print=1, which
// the order page prints; REF's /orders/{id} is the same page) · Rate (completed, not rated) | Your review (rated) →
// /{hotel}/orders/{id}/review · Reorder (the cards with ticket lines, OO.pages.acct.useReorder in order-status.jsx;
// REF has none in the list). At a table (D-20) Track, Rate and Your review are left out: those pages are off there.
// When the build has the ratings lane's OO.pages.Review.OrderRating (P3-07), each card mounts its 'line' variant in
// the actions row, before Reorder (no table): it reads the order's rating from order.rating (an OrderRating or null,
// so no per-card E30 read; one cached E28 probe per store tells whether ratings are on) and draws the stars + "My
// review", or Rate for a completed order. Then this card's own Rate / Your review and stars stand down.
//
// OO.pages.Orders.logic (pure; tests/orders-p304.test.mjs). `now` is a Date | ms (pass OO.data.now()).
//   TABS [{ key 'accepted' | 'all', label }] · LIVE_MS (24 h) · AFTER_PICKUP_MS (12 h)
//   viewOf(query) → 'all' | 'accepted'   the tab a URL query asks for.
//   pageInfo(raw) → { page, last, total | null }   a bare paginator (E16) or { status, data: paginator } (E17).
//   acceptedOrders(raw, { now, timeZone }) → Order[]   normalizeOrders plus { rating: OrderRating | null, online: bool
//       (the ticket has an online_orders status), source: 'ticket' }. OrderRating = { rating (a whole 1–5), label,
//       feedback, items }: OO.model.normalizeOrderRating(row) when the build has it (P3-07), else { rating, label
//       ('Poor' … 'Excellent'), feedback (trimmed) | null, items: [] } from the row; null = not rated (a whole 1–5 or
//       nothing). It is what OO.pages.Review.OrderRating reads from order.rating.
//   normalizeOnlineOrders(raw, { now, timeZone, menu? }) → OnlineOrder[]   an alias of OO.model.normalizeOnlineOrders
//       (read at call time), where it moved with its OnlineOrder contract (src/lib/model-account.jsx; P3-FIX #22, as
//       BATCH2-PLAN §5 E17 names it).
//   mergeOrders(list, more) → the two lists de-duplicated by id (the first kept), newest first.
//   isLive(order, now) → bool   not final (completed, rejected, cancelled) and placed within LIVE_MS of now (either
//       side), or its pickup time is less than AFTER_PICKUP_MS behind now (a scheduled order still ahead).
//   actionsFor(order, { now, table }) → { live, track, details 'details' | 'receipt', rate, review }   track: live and
//       an online order; rate: completed and not rated; review: rated (order.rating is an OrderRating). table (D-20):
//       no track, rate or review.
//   orderName(order) → 'Order #1042' (its short id) | 'Order #345678' (REF: the id's last 6) | 'Order'.
//
// Adds to OO.pages.acct (the T3d kit, see not-found.jsx):
//   OrderList { hotel, store, uid }   the tabs and the fetched list with its loading / error / empty states and Load
//                                     more (also the profile page's Orders tab).
//   OrderCard { order, hotel, store, reorder, now, table }   reorder: acct.useReorder's result, or null for no Reorder.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var BADGE = { neutral: 'neutral', warn: 'warn', success: 'success', danger: 'danger' };
  var TABS = [{ key: 'accepted', label: 'Accepted' }, { key: 'all', label: 'All orders' }];
  var LIVE_MS = 24 * 3600000;
  var AFTER_PICKUP_MS = 12 * 3600000;
  var FINAL = { completed: true, rejected: true, cancelled: true };

  function arr(v) { return Array.isArray(v) ? v : []; }
  function text(v) { return typeof v === 'string' ? v.trim() : (typeof v === 'number' && isFinite(v) ? String(v) : ''); }
  function num(v) { var n = typeof v === 'string' ? parseFloat(v) : Number(v); return isFinite(n) ? n : 0; }
  function msOf(now) {
    if (now instanceof Date) return now.getTime();
    var n = Number(now);
    return isFinite(n) ? n : Date.parse(String(now));
  }
  // A history row's rating as an OrderRating ({ rating (a whole 1–5), label, feedback, items }) | null (not rated): the
  // shape OO.pages.Review.OrderRating reads from order.rating. OO.model.normalizeOrderRating (the ratings lane, P3-07)
  // when it is there (it reads the rows' rating, feedback and item_ratings), else the same from the row's rating and
  // feedback (no items).
  var RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent']; // as model-account's (P3-07)
  function ratingOf(row) {
    var M = OO.model;
    if (M && typeof M.normalizeOrderRating === 'function') return M.normalizeOrderRating(row) || null;
    var v = row ? row.rating : null;
    var n = v === null || v === undefined || v === '' || typeof v === 'boolean' ? NaN : Number(v);
    if (!(isFinite(n) && n % 1 === 0 && n >= 1 && n <= 5)) return null;
    var said = row && typeof row.feedback === 'string' ? row.feedback.trim() : '';
    return { rating: n, label: RATING_LABELS[n], feedback: said || null, items: [] };
  }
  function starsOf(order) { var r = order && order.rating; return r && typeof r === 'object' && r.rating >= 1 ? r.rating : null; }

  // ---- pure logic (OO.pages.Orders.logic; see the header) --------------------------------------------------------
  function viewOf(query) { return query && String(query.view || '').toLowerCase().trim() === 'all' ? 'all' : 'accepted'; }

  function pageInfo(raw) {
    var body = raw && typeof raw === 'object' ? raw : {};
    var p = body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : body;
    var page = Math.max(1, Math.floor(num(p.current_page)) || 1);
    var last = Math.max(page, Math.floor(num(p.last_page)) || 1);
    return { page: page, last: last, total: p.total === null || p.total === undefined || p.total === '' ? null : num(p.total) };
  }

  function acceptedOrders(raw, opts) {
    var rows = Object.create(null);
    arr(raw && raw.data).forEach(function (t) { if (t && typeof t === 'object' && t.ticket_id != null) rows[String(t.ticket_id)] = t; });
    return OO.model.normalizeOrders(raw, opts || {}).map(function (o) {
      var t = rows[o.id] || {};
      return Object.assign({}, o, { rating: ratingOf(t), online: text(t.orderStatus) !== '', source: 'ticket' });
    });
  }

  // P3-FIX #22: the All tab's rows are OO.model.normalizeOnlineOrders (src/lib/model-account.jsx, BATCH2-PLAN §5 E17);
  // this alias reads it at call time.
  function normalizeOnlineOrders(raw, opts) { return OO.model.normalizeOnlineOrders(raw, opts); }

  function mergeOrders(list, more) {
    var seen = Object.create(null);
    var out = [];
    arr(list).concat(arr(more)).forEach(function (o) {
      if (!o || seen[o.id]) return;
      seen[o.id] = true;
      out.push(o);
    });
    return out.sort(function (a, b) { return (b.placedAt || 0) - (a.placedAt || 0); });
  }

  function isLive(order, now) {
    if (!order || !order.status || FINAL[order.status.key]) return false;
    var t = msOf(now);
    if (!isFinite(t)) return false;
    if (typeof order.placedAt === 'number' && Math.abs(t - order.placedAt) < LIVE_MS) return true;
    return typeof order.pickupAt === 'number' && t < order.pickupAt + AFTER_PICKUP_MS;
  }

  function actionsFor(order, opts) {
    var o = opts || {};
    var live = isLive(order, o.now);
    var key = order && order.status ? order.status.key : null;
    var rated = starsOf(order) !== null;
    return {
      live: live,
      track: live && !o.table && !!order && order.online !== false,
      details: live ? 'details' : 'receipt',
      rate: !o.table && key === 'completed' && !rated,
      review: !o.table && rated,
    };
  }

  function orderName(order) {
    if (!order) return 'Order';
    if (order.shortId) return 'Order ' + (String(order.shortId).charAt(0) === '#' ? order.shortId : '#' + order.shortId);
    var id = text(order.id);
    return id ? 'Order #' + id.slice(-6).toUpperCase() : 'Order';
  }

  // ---- the list's data: one tab, page by page ----------------------------------------------------------------
  function menuOf(hotel) {
    try { var c = OO.data.peekHotel(hotel, true); return c ? c.menu : null; } catch (e) { return null; }
  }
  function fetchOrders(hotel, uid, tab, page, opts) {
    function accepted() {
      return Promise.resolve().then(function () { return OO.api.getOrders(hotel, { uid: uid, page: page }); }).then(function (raw) {
        return { orders: acceptedOrders(raw, opts), page: pageInfo(raw) };
      });
    }
    if (tab !== 'all') return accepted();
    return Promise.resolve().then(function () { return OO.api.getAllOrders(hotel, { uid: uid, page: page }); }).then(function (raw) {
      return { orders: normalizeOnlineOrders(raw, opts), page: pageInfo(raw) };
    }, function (err) {
      // No customer/getOrders for this store's adapter (the facade's "Not available yet": the fixture stores): the
      // preview lists its tickets under All too. Any other failure is the tab's error.
      if (err && err.kind === 'unavailable' && !(Number(err.status) > 0)) return accepted();
      throw err;
    });
  }
  function blank(key) {
    return { key: key, orders: [], page: 0, last: 1, total: null, loading: true, error: null, more: false, moreError: false };
  }
  // → { orders, loading, error, more (a next page is loading), moreError, hasMore, loadMore(), reload() }
  function useOrderPages(hotel, uid, tab, store) {
    var key = String(hotel) + '|' + String(uid) + '|' + tab;
    var st = React.useState(function () { return blank(key); });
    var nonce = React.useState(0);
    var req = React.useRef(0);
    var cur = st[0].key === key ? st[0] : blank(key);
    var tz = store ? store.timezone : null;

    function run(page, append) {
      req.current += 1;
      var id = req.current;
      st[1](function (s) {
        var base = s.key === key ? s : blank(key);
        return append ? Object.assign({}, base, { more: true, moreError: false }) : blank(key);
      });
      fetchOrders(hotel, uid, tab, page, { now: OO.data.now(), timeZone: tz, menu: menuOf(hotel) }).then(function (res) {
        if (req.current !== id) return; // a newer request (another tab / account, a reload, an unmount) replaced it
        st[1](function (s) {
          var base = s.key === key ? s : blank(key);
          return Object.assign({}, base, {
            orders: append ? mergeOrders(base.orders, res.orders) : res.orders, page: res.page.page, last: res.page.last,
            total: res.page.total, loading: false, error: null, more: false, moreError: false,
          });
        });
      }, function (err) {
        if (req.current !== id) return;
        st[1](function (s) {
          var base = s.key === key ? s : blank(key);
          return append ? Object.assign({}, base, { more: false, moreError: true }) : Object.assign({}, base, { loading: false, error: err || new Error('Failed') });
        });
      });
    }
    React.useEffect(function () {
      run(1, false);
      return function () { req.current += 1; };
    }, [key, nonce[0]]);

    return {
      orders: cur.orders, loading: cur.loading, error: cur.error, more: cur.more, moreError: cur.moreError,
      hasMore: !cur.loading && !cur.error && cur.page >= 1 && cur.page < cur.last,
      loadMore: function () { if (!cur.loading && !cur.more && cur.page >= 1 && cur.page < cur.last) run(cur.page + 1, true); },
      reload: function () { nonce[1](function (n) { return n + 1; }); },
    };
  }

  // ---- parts -----------------------------------------------------------------------------------------------
  function OrderCard(props) {
    var ui = OO.ui;
    var o = props.order;
    var re = props.reorder;
    var hotel = props.hotel;
    var acts = actionsFor(o, { now: props.now, table: !!props.table });
    // The ratings lane's own line (P3-07; owner views only), when this build has it and no table applies (D-20: the
    // review page is off there): its stars + "My review" or its Rate take the place of ours (one Rate per card).
    var Rating = !props.table && OO.pages.Review && typeof OO.pages.Review.OrderRating === 'function' ? OO.pages.Review.OrderRating : null;
    var stars = Rating ? null : starsOf(o);
    var link = OO.router.link('order-status', { orderId: o.id }, { hotel: hotel });
    var receipt = acts.details === 'receipt' ? OO.router.link('order-status', { orderId: o.id }, { hotel: hotel, query: { print: '1' } }) : link;
    var track = acts.track ? OO.router.link('track', { orderId: o.id }, { hotel: hotel }) : null;
    var review = !Rating && (acts.rate || acts.review) ? OO.router.link('review', { orderId: o.id }, { hotel: hotel }) : null;
    var mine = re && re.state && re.state.orderId === o.id ? re.state : null;
    var name = orderName(o);
    var lower = name.toLowerCase();
    var storeName = props.store && props.store.name ? String(props.store.name).trim().toLowerCase() : '';
    var branch = o.branch && String(o.branch).trim().toLowerCase() !== storeName ? 'at ' + o.branch : null;
    var when = [acct.orderWhen(o), o.typeLabel, branch].filter(Boolean).join(' · ');
    return (
      <ui.Card className="oo-orders__card">
        <div className="oo-orders__top">
          <h3 className="oo-orders__name">
            <a className="oo-orders__link" href={link.href} onClick={link.onClick}>{name}</a>
          </h3>
          <ui.Badge tone={BADGE[o.status.tone] || 'neutral'}>{o.status.label}</ui.Badge>
        </div>
        {when ? <div className="oo-orders__when">{when}</div> : null}
        {o.summary ? <div className="oo-orders__summary">{o.summary}</div> : null}
        <div className="oo-orders__foot">
          <span className="oo-orders__total">
            <span className="oo-orders__amount">{acct.money(o.total, props.store)}</span>
            <span className="oo-orders__count">{OO.format.pluralize(o.itemCount, 'item')}</span>
          </span>
          {stars ? (
            <span className="oo-orders__rated" aria-label={'Rated ' + stars + ' out of 5'}>
              <span aria-hidden="true"><OO.icons.Star size={14} /></span>
              <span aria-hidden="true">{stars}</span>
            </span>
          ) : null}
        </div>
        <div className="oo-orders__actions">
          {track ? <ui.Button size="xs" href={track.href} onClick={track.onClick} aria-label={'Track ' + lower}>Track</ui.Button> : null}
          <ui.Button variant="outline" size="xs" href={receipt.href} onClick={receipt.onClick}
            aria-label={(acts.details === 'details' ? 'Details of ' : 'Receipt for ') + lower}>{acts.details === 'details' ? 'Details' : 'Receipt'}</ui.Button>
          {Rating ? <Rating variant="line" hotel={hotel} store={props.store} order={o} /> : null}
          {review && acts.rate ? <ui.Button variant="outline" size="xs" href={review.href} onClick={review.onClick} aria-label={'Rate ' + lower}>Rate</ui.Button> : null}
          {review && acts.review ? <ui.Button variant="outline" size="xs" href={review.href} onClick={review.onClick} aria-label={'Your review of ' + lower}>Your review</ui.Button> : null}
          {re ? (
            <ui.Button variant="outline" size="xs" className="oo-orders__reorder" busy={!!(mine && mine.busy)}
              aria-label={'Reorder ' + lower} onClick={function () { re.run(o); }}>Reorder</ui.Button>
          ) : null}
        </div>
        {re ? <acct.ReorderNotice notice={mine} hotel={hotel} className="oo-orders__notice" /> : null}
      </ui.Card>
    );
  }

  function ListSkeleton() {
    return (
      <div className="oo-orders__list" aria-hidden="true">
        {[0, 1, 2].map(function (i) { return <OO.ui.Skeleton key={i} variant="block" height={132} />; })}
      </div>
    );
  }

  // WAI-ARIA tabs: roving tabindex, arrow keys / Home / End (as the profile page's).
  function Tabs(props) {
    var refs = React.useRef({});
    function onKey(e, i) {
      var n = TABS.length;
      var j = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
      if (j < 0) return;
      e.preventDefault();
      props.onSelect(TABS[j].key);
      var el = refs.current[TABS[j].key];
      if (el && el.focus) el.focus();
    }
    return (
      <div className="oo-orders__tabs" role="tablist" aria-label="Which orders">
        {TABS.map(function (t, i) {
          var on = t.key === props.value;
          return (
            <button key={t.key} ref={function (el) { refs.current[t.key] = el; }} type="button" role="tab"
              id={'oo-orders-tab-' + t.key} aria-selected={on ? 'true' : 'false'} aria-controls="oo-orders-panel" tabIndex={on ? 0 : -1}
              className={OO.ui.cx('oo-orders__tab', on && 'is-active')}
              onClick={function () { props.onSelect(t.key); }} onKeyDown={function (e) { onKey(e, i); }}>
              {t.label}
            </button>
          );
        })}
      </div>
    );
  }

  function currentQuery() {
    try { return OO.router.current().query || {}; } catch (e) { return {}; }
  }
  // D-20: does a table apply to this store here (the URL's ?table_id, else OO.store's table context)?
  function atTable(hotel) {
    try {
      var table = OO.store && typeof OO.store.table === 'function' ? OO.store.table() : null;
      return !!OO.router.tableGate({ hotel: hotel, page: 'track', params: {}, query: currentQuery() }, table);
    } catch (e) { return false; }
  }

  function OrderList(props) {
    var ui = OO.ui;
    var hotel = props.hotel;
    var store = props.store;
    var tabS = React.useState(function () { return viewOf(currentQuery()); });
    var tab = tabS[0];
    var list = useOrderPages(hotel, props.uid, tab, store);
    var re = acct.useReorder(hotel);
    var menu = OO.router.link('menu', null, { hotel: hotel });
    var now = OO.data.now();
    var table = atTable(hotel);

    function select(key) {
      if (key === tab) return;
      tabS[1](key);
      try {
        var r = OO.router.current();
        var q = Object.assign({}, r.query);
        if (key === 'all') q.view = 'all'; else delete q.view;
        OO.router.go(OO.router.href(r.page, r.params, { hotel: r.hotel, query: q }), { replace: true });
      } catch (e) { /* no router location (tests): the tab still switches */ }
    }

    var panel;
    if (list.loading && !list.orders.length) {
      panel = <div aria-busy="true"><span className="oo-sr-only" role="status">Loading your orders</span><ListSkeleton /></div>;
    } else if (list.error) {
      panel = (
        <ui.EmptyState icon="Info" title="We couldn't load your orders" body="Check your connection and try again."
          action={<ui.Button size="md" onClick={list.reload}>Try again</ui.Button>} />
      );
    } else if (!list.orders.length) {
      panel = tab === 'all' ? (
        <ui.EmptyState icon="Receipt" title="No orders yet" body={'When you order from ' + store.name + ', it will show up here.'}
          action={<ui.Button size="md" href={menu.href} onClick={menu.onClick}>Start an order</ui.Button>} />
      ) : (
        <ui.EmptyState icon="Receipt" title="No accepted orders yet"
          body="Orders show here once the store accepts them. All orders also lists the ones still waiting."
          action={(
            <div className="oo-acct__actions">
              <ui.Button size="md" href={menu.href} onClick={menu.onClick}>Start an order</ui.Button>
              <ui.Button size="md" variant="outline" onClick={function () { select('all'); }}>See all orders</ui.Button>
            </div>
          )} />
      );
    } else {
      panel = (
        <React.Fragment>
          <ul className="oo-orders__list" aria-label={tab === 'all' ? 'All orders' : 'Accepted orders'}>
            {list.orders.map(function (o) {
              return (
                <li key={o.id} className="oo-orders__item">
                  <OrderCard order={o} hotel={hotel} store={store} reorder={o.source === 'ticket' ? re : null} now={now} table={table} />
                </li>
              );
            })}
          </ul>
          {list.hasMore || list.more ? (
            <div className="oo-orders__more">
              {list.moreError ? <ui.InlineNotice tone="danger" icon="Info">We couldn't load more orders.</ui.InlineNotice> : null}
              <ui.Button variant="outline" size="md" block busy={list.more} onClick={list.loadMore}>
                {list.moreError ? 'Try again' : 'Load more orders'}
              </ui.Button>
            </div>
          ) : null}
        </React.Fragment>
      );
    }

    return (
      <div className="oo-orders__history">
        <Tabs value={tab} onSelect={select} />
        <div className="oo-orders__panel" role="tabpanel" id="oo-orders-panel" aria-labelledby={'oo-orders-tab-' + tab}>
          {panel}
        </div>
      </div>
    );
  }

  acct.OrderCard = OrderCard;
  acct.OrderList = OrderList;

  // A guest's last order on this device (OO.store lastOrder: { orderId, hotel, paymentMethod, placedAt, total }).
  function RecentCard(props) {
    var ui = OO.ui;
    var last = props.last;
    var link = OO.router.link('order-status', { orderId: last.orderId }, { hotel: props.hotel });
    var at = Number(last.placedAt);
    var when = '';
    if (at > 0) {
      var c = OO.format.zoned(new Date(at), props.store.timezone);
      var today = OO.format.zoned(new Date(), props.store.timezone).ymd;
      when = OO.format.relativeDay(c.ymd, today) + ', ' + OO.format.time12(c.minutes);
    }
    return (
      <ui.Card className="oo-orders__recent">
        <span className="oo-orders__recent-icon" aria-hidden="true"><OO.icons.Receipt size={22} /></span>
        <span className="oo-orders__recent-text">
          <span className="oo-acct__cardtitle">Your latest order</span>
          <span className="oo-acct__muted">
            {[when ? 'Placed ' + when : null, last.total != null && last.total !== '' ? acct.money(last.total, props.store) : null].filter(Boolean).join(' · ') || 'Placed on this device'}
          </span>
        </span>
        <ui.Button size="xs" href={link.href} onClick={link.onClick}>Track order</ui.Button>
      </ui.Card>
    );
  }

  OO.pages.Orders = function Orders(props) {
    var route = props.route;
    var mode = props.mode;
    var hotel = route.hotel;
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var session = OO.useStore(function (s) { return s.session; });
    var lastOrder = OO.useStore(function (s) { return s.lastOrder; });
    var app = mode === 'app';
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: app ? null : OO.router.href('menu', null, { hotel: hotel }), profile: true });
    var blocked = acct.gate(h, {
      title: 'Orders', header: header,
      skeleton: <div className="oo-acct__skel"><ui.Skeleton lines={2} className="oo-acct__skel-title" /><ListSkeleton /></div>,
    });
    if (blocked) return blocked;

    var store = h.store;
    var uid = session && session.customer ? session.customer.uid : null;
    var last = lastOrder && lastOrder.hotel === hotel && lastOrder.orderId ? lastOrder : null;
    var login = OO.router.link('profile', null, { hotel: hotel, query: { directLogin: 'true', tab: 'orders' } });
    var track = atTable(hotel) ? null : OO.router.link('track', null, { hotel: hotel });

    return (
      <acct.Page title="Orders" {...header} className="oo-orders">
        <ui.SectionTitle level="page" sub={store.name}>Your orders</ui.SectionTitle>
        {uid ? <OrderList hotel={hotel} store={store} uid={uid} /> : (
          <React.Fragment>
            {last ? <RecentCard last={last} hotel={hotel} store={store} /> : null}
            <ui.EmptyState icon="User" title="Log in to see your orders" body="Your past orders, receipts and reorders live here."
              action={(
                <div className="oo-acct__actions">
                  <ui.Button size="md" href={login.href} onClick={login.onClick}>Log in</ui.Button>
                  {track ? <ui.Button size="md" variant="outline" href={track.href} onClick={track.onClick}>Track an order</ui.Button> : null}
                </div>
              )} />
          </React.Fragment>
        )}
        <ui.PoweredBySipo />
      </acct.Page>
    );
  };

  OO.pages.Orders.logic = {
    TABS: TABS, LIVE_MS: LIVE_MS, AFTER_PICKUP_MS: AFTER_PICKUP_MS,
    viewOf: viewOf, pageInfo: pageInfo, acceptedOrders: acceptedOrders, normalizeOnlineOrders: normalizeOnlineOrders,
    mergeOrders: mergeOrders, isLive: isLive, actionsFor: actionsFor, orderName: orderName,
  };
})();


// ===================== src/pages/profile.jsx =====================
// src/pages/profile.jsx: the profile page (page id 'profile': /{hotel}/profile, and /{hotel}/edit-profile with
// params.edit = true). Query: ?directLogin=true opens the email login straight away, ?tab=orders|points selects a tab.
// Styles: styles/pages.css (.oo-profile__*; the sign-up's phone picker also wears checkout.css's global .oo-phone__*),
// plus the Batch 2 P3-09…12 section of styles/app-pages.css (the save's "Log in again" link).
// Not in the design: built from its parts (PageHeader, canvas, white cards, black actions; the Points tab reuses the
// dark loyalty-card look and the "Activity" list of the app's Rewards screen).
//
// Logged out (OO.store session null): email OTP login (P3-08: REF AuthModal's rules and words, in our sentence case).
//   Email: trimmed and lower-cased ONCE, at send (logic.otpEmail); the code step, verify-otp and customer/register all
//     use that sent-to address. OO.api.sendOtp → "Check your email" ("We sent a 6-digit code to …"; fixture 123456).
//   Resend: "Resend verification code" opens COOLDOWN_S (60 s) after each send; until then "Resend code in 42s" counts
//     down (the clock re-read each second on the code step; never a live region). The cooldown lives in the panel, so
//     "Use a different email" → the same address inside the minute goes back to the code without a new mail; another
//     address sends at once.
//     A 429 shows the backend's own limiter message (logic.limitText; send-otp: 5 codes per address per 10 minutes,
//     config/customer_auth.php; verify-otp: 10 tries per 10 minutes); a refused resend restarts the countdown.
//   Code: digits only, six at most; a paste replaces the field (REF). The sixth digit (typed, pasted, autofilled)
//     submits it ONCE: one send / verify / register in flight at a time (a ref, not the busy state), and never again
//     the code verify-otp just refused (400) — "Verify code" still sends it. Any other failure (5xx, network, 429, sign
//     up failed) keeps the code: the backend restores a code it consumed but couldn't finish with, so it can be retried.
//   A known email resolves a customer → OO.store.setSession. A new email resolves customer: null → "Complete your
//     profile": full name, mobile number (REQUIRED, REF: the checkout's E.164 picker from P2-24, built on
//     OO.pages.Checkout.logic's PHONE_COUNTRIES / phoneDefault / phoneParts / phoneValue / phoneValid, the store's
//     country first; ≤ 15 characters, customer/register's limit, which the real adapter would otherwise send as ''),
//     optional birthday, the email read-only → OO.api.register with the backend's uid (OO.data.signupUid: the custom
//     token's `uid` claim; never invented for a real store) → setSession. No uid → "Sign up failed. Please try again."
//     The optional sign-up address of REF waits for the address book (P3-10).
//   OO.pages.Profile.logic (pure): COOLDOWN_S · otpEmail(v) → trimmed, lower-case · otpCode(v) → its digits, six at most
//     · cooldownLeft(sentAt, now) → whole seconds until Resend opens (COOLDOWN_S right after a send, 0 once it has
//     passed or with no send) · limitText(err, fallback) → a 429's own JSON message (error / message), else
//     acct.errorText(err, fallback) · signupPhoneError(value) → null | 'Enter your mobile number.' | 'Enter a valid
//     mobile number.'
// Logged in: a profile card (name, email, phone, points and tier from OO.api.getCustomer → OO.model.normalizeCustomer)
// and ARIA tabs Profile / Orders (OO.pages.acct.OrderList) / Points (OO.api.getWalletTransactions →
// OO.model.normalizeActivity, fetched when the tab opens). The tab lives in the URL (?tab=, replace). The Profile tab:
// the details, the address book (P3-10: <OO.pages.Addresses>, src/pages/addresses.jsx; its "Address added." etc. in
// this page's toast), in online mode the account hub's other pages (P3-12: Wallet, Rewards, and Bookings and Track a
// booking at a store that takes bookings; OO.pages.Account.logic), the store information links and Log out. Back
// (with no in-app history to go back to) goes to the account hub, /{hotel}/account, in both modes (P3-12); from
// /edit-profile, to the profile.
// Edit (/edit-profile; P3-09): name (required), mobile and birthday (optional), validated, then saved through B16
//   (OO.api.updateProfile: the adapter's STORE_NOT_LINKED link-and-retry and UID_MISMATCH full-record fallback). The
//   name is always sent; the mobile and the birthday only when changed (an untouched one is left as stored, whatever
//   its format; a cleared one is sent null, which clears it). The email is shown read-only and never sent. While it
//   saves, Save is busy and a second submit does nothing. Saved → the session's customer is reconciled with the saved
//   record (logic.reconcile) and the page leaves edit mode ("Profile saved."). Refused → saveError's words in an alert
//   above the actions, plus field errors from a 422; a 401 (the sign-in token is missing, invalid or older than 30
//   days) offers "Log in again" (→ /edit-profile?directLogin=true, then the session is dropped); a 503 keeps the
//   session ("try again shortly").
//   OO.pages.Profile.saveProfile(hotel, fields) → Promise<{customer, body}>   OO.api.updateProfile for the session's
//       customer (its uid and customToken), then, when that customer is still the one signed in, OO.store.setSession
//       with logic.reconcile's customer (the token kept); customer is null when the session moved on meanwhile. The
//       address book (addresses.jsx) saves through it too. Rejects with the adapter's error (no session: a 401
//       TOKEN_REQUIRED shape without a call).
//   OO.pages.Profile.logic (P3-09, pure): saveError(err) → { text: string | null, fields: { name?, phone?, birthday?,
//       address? }, signIn: bool } · reconcile(prevCustomer, savedBody) → Customer | null: OO.model.normalizeCustomer
//       of the saved record, keeping prev's customerCode, tier and points (customer/profile and customer/create don't
//       carry them; customer/getdata does), its addresses with each entry's uid and default (P3-10:
//       OO.pages.Addresses.logic.sessionAddresses), null unless it is prev's uid · editFields(customer, {name, phone,
//       birthday}) → the updateProfile fields of the edit form (name always; phone_no / birthday when changed).
// Log out → OO.store.logout().
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var TABS = [{ key: 'profile', label: 'Profile' }, { key: 'orders', label: 'Orders' }, { key: 'points', label: 'Points' }];
  var LEGAL = [['terms', 'Terms and conditions'], ['privacy', 'Privacy policy'], ['allergy', 'Allergy information']];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function compactPhone(s) { return String(s || '').replace(/[\s()-]/g, ''); }
  function phoneError(s) {
    var v = compactPhone(s);
    if (!v) return null;
    if (v.length > 15) return 'Use 15 characters or fewer.';
    return /^\+?\d{6,}$/.test(v) ? null : 'Enter a valid mobile number.';
  }
  function todayYmd() {
    var d = new Date();
    return d.getFullYear() + '-' + OO.format.pad2(d.getMonth() + 1) + '-' + OO.format.pad2(d.getDate());
  }
  function birthdayError(s) {
    if (!s) return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return 'Enter a valid date.';
    var y = +m[1], mo = +m[2], d = +m[3];
    var dt = new Date(Date.UTC(y, mo - 1, d));
    if (y < 1900 || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return 'Enter a valid date.';
    if (s > todayYmd()) return "Your birthday can't be in the future.";
    return null;
  }
  function birthdayText(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
    return m ? Number(m[3]) + ' ' + MONTHS[Number(m[2]) - 1] + ' ' + m[1] : '';
  }

  function useAlive() {
    var alive = React.useRef(true);
    React.useEffect(function () {
      alive.current = true;
      return function () { alive.current = false; };
    }, []);
    return alive;
  }

  // ---- login (email OTP; P3-08) ----------------------------------------------------------------------------
  var SIGNUP_FAILED = 'Sign up failed. Please try again.';
  var CODE_LEN = 6;
  var COOLDOWN_S = 60;  // REF AuthModal RESEND_COOLDOWN
  var PHONE_MAX = 15;   // customer/register's phone_no rule (the real adapter sends '' for a longer one)
  var CODE_ID = 'oo-profile-code';
  var PHONE_ID = 'oo-profile-tel';
  var BODY_ID = 'oo-profile-login-body';

  // The device clock: the cooldown measures real time on every store (a fixture store's API clock may stand still).
  function clock() { return Date.now(); }
  // The address a code goes to (B§5 E20; REF's drawer): trimmed and lower-cased, once, at send.
  function otpEmail(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  // The code as typed, pasted or autofilled: its digits, the first six.
  function otpCode(v) { return String(v == null ? '' : v).replace(/\D/g, '').slice(0, CODE_LEN); }
  // Whole seconds until "Resend" opens: COOLDOWN_S right after the send at `sentAt` (ms), 0 once that has passed.
  function cooldownLeft(sentAt, now) {
    if (sentAt == null || !isFinite(sentAt) || !isFinite(now)) return 0;
    var ms = sentAt + COOLDOWN_S * 1000 - now;
    return ms > 0 ? Math.min(COOLDOWN_S, Math.ceil(ms / 1000)) : 0;
  }
  // The ms until cooldownLeft next drops a second.
  function nextSecond(sentAt, now) {
    var ms = (sentAt + COOLDOWN_S * 1000 - now) % 1000;
    return ms > 0 ? ms : 1000;
  }
  // A rejected send-otp / verify-otp → its text. A 429 is the backend's own limiter answer ({status: false, error,
  // message}, RouteServiceProvider otp-send / otp-verify): its words say how long to wait, the adapter's generic ones
  // don't. Anything else (an nginx 429 page included) → acct.errorText.
  function limitText(err, fallback) {
    var d = err && Number(err.status) === 429 && err.data && typeof err.data === 'object' ? err.data : null;
    var msg = d ? [d.message, d.error].filter(function (m) { return typeof m === 'string' && m.trim(); })[0] : null;
    return msg && msg.length < 200 && msg.indexOf('<') < 0 ? msg.trim() : acct.errorText(err, fallback);
  }
  // The checkout's phone kit (P2-24, src/pages/checkout.jsx: REF's countries and rules), read when used.
  function phoneKit() { return OO.pages.Checkout.logic; }
  // Sign-up's mobile number, the picker's E.164 (REF: required).
  function signupPhoneError(value) {
    var v = String(value == null ? '' : value).trim();
    if (!v) return 'Enter your mobile number.';
    return phoneKit().phoneValid(v) && v.length <= PHONE_MAX ? null : 'Enter a valid mobile number.';
  }

  // <CodeField label value onChange(raw) onPaste(event) error hint inputRef /> — TextInput's markup (visible label,
  // outlined box) plus a paste handler, with the card's "We sent a 6-digit code to …" line in its description. No
  // maxLength: it would cut a pasted "123 456" before onChange; the panel keeps six digits.
  function CodeField(props) {
    var describedBy = OO.ui.cx(BODY_ID, props.error && CODE_ID + '-err', props.hint && CODE_ID + '-hint');
    return (
      <div className={OO.ui.cx('oo-field', 'oo-profile__code', props.error && 'is-invalid')}>
        <label htmlFor={CODE_ID} className="oo-field__label">{props.label}</label>
        <div className="oo-field__box">
          <input id={CODE_ID} ref={props.inputRef} className="oo-field__input oo-profile__code-input" type="text" name="otp"
            inputMode="numeric" autoComplete="one-time-code" required value={props.value}
            aria-invalid={props.error ? 'true' : undefined} aria-describedby={describedBy}
            onChange={function (e) { props.onChange(e.target.value); }} onPaste={props.onPaste} />
        </div>
        {props.error ? <div className="oo-field__msg" id={CODE_ID + '-err'}>{props.error}</div> : null}
        {props.hint ? <div className="oo-field__hint" id={CODE_ID + '-hint'}>{props.hint}</div> : null}
      </div>
    );
  }

  // <PhoneInput store label value onChange(e164) error inputRef /> — the sign-up's mobile number (REF PhoneInput, the
  // checkout's P2-24 picker): a country code select (Checkout.logic PHONE_COUNTRIES, the store's code first:
  // phoneDefault) beside the national number, under a visible label. onChange gets phoneValue's E.164
  // ('+61412345678'; '' with no digits); typing a number with its code ('+64 …', '0064 …') picks that country.
  function PhoneInput(props) {
    var kit = phoneKit();
    var store = props.store;
    var start = React.useState(function () { return kit.phoneParts(props.value, kit.phoneDefault(store), store); })[0];
    var iso = React.useState(start.iso);
    var national = React.useState(start.national);
    function emit(i, n) { props.onChange(kit.phoneValue(i, n)); }
    function typed(raw) {
      if (/^\s*(\+\d|00\d)/.test(raw)) {
        var p = kit.phoneParts(raw, iso[0], store);
        iso[1](p.iso);
        national[1](p.national);
        emit(p.iso, p.national);
        return;
      }
      var kept = raw.replace(/[^\d\s().-]/g, '');
      national[1](kept);
      emit(iso[0], kept);
    }
    var c = kit.PHONE_COUNTRIES.filter(function (x) { return x.iso === iso[0]; })[0] || kit.PHONE_COUNTRIES[0];
    var err = props.error || null;
    return (
      <div className={OO.ui.cx('oo-field', 'oo-phone', err && 'is-invalid')}>
        <label htmlFor={PHONE_ID} className="oo-field__label">{props.label}</label>
        <div className="oo-field__box oo-phone__box">
          <span className="oo-phone__cc">
            <span className="oo-phone__dial" aria-hidden="true">{'+' + c.dial}</span>
            <OO.icons.CaretDown size={12} className="oo-phone__caret" />
            <select className="oo-phone__select" name="tel-country" aria-label="Country code" value={c.iso}
              onChange={function (e) { iso[1](e.target.value); emit(e.target.value, national[0]); }}>
              {kit.PHONE_COUNTRIES.map(function (x) { return <option key={x.iso} value={x.iso}>{x.name + ' (+' + x.dial + ')'}</option>; })}
            </select>
          </span>
          <input id={PHONE_ID} ref={props.inputRef} className="oo-field__input oo-phone__input" type="tel" name="tel"
            autoComplete="tel-national" inputMode="tel" required value={national[0]}
            aria-invalid={err ? 'true' : undefined} aria-describedby={err ? PHONE_ID + '-err' : undefined}
            onChange={function (e) { typed(e.target.value); }} />
        </div>
        {err ? <div className="oo-field__msg" id={PHONE_ID + '-err'}>{err}</div> : null}
      </div>
    );
  }

  function LoginPanel(props) {
    var ui = OO.ui;
    var hotel = props.hotel;
    var kitchen = OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign((OO.router.current() || {}).chain || hotel);
    var step = React.useState(props.direct ? 'email' : 'intro');
    var email = React.useState('');
    var code = React.useState('');
    var name = React.useState('');
    var phone = React.useState('');      // the picker's E.164 ('' with no digits)
    var birthday = React.useState('');
    var busy = React.useState(false);
    var err = React.useState(null); // { field: 'email' | 'code' | 'name' | 'phone' | 'birthday' | null, text }
    var info = React.useState(null);
    var sent = React.useState(null);     // the last code mailed: { email (lower-cased), at (ms) }
    var now = React.useState(clock);     // the countdown's clock: set at a send, re-read every second while it runs
    var pending = React.useRef(null);    // after a new email's verify: { token, uid, email }
    var inflight = React.useRef(false);  // a send / verify / register in flight: nothing else starts (no double submit)
    var refused = React.useRef(null);    // the code verify-otp last refused (400): never auto-submitted again
    var focusRef = React.useRef(null);
    var phoneRef = React.useRef(null);
    var birthRef = React.useRef(null);
    var mounted = React.useRef(false);
    var alive = useAlive();
    var at = sent[0] ? sent[0].at : null;
    var left = cooldownLeft(at, now[0]);

    // Move focus to the step's first field (on mount only for ?directLogin=true).
    React.useEffect(function () {
      var first = !mounted.current;
      mounted.current = true;
      if (first && (!props.direct || kitchen)) return;
      focus(focusRef);
    }, [step[0]]);

    // The countdown (code step only): re-read the clock when its next second is due, while it runs.
    React.useEffect(function () {
      if (!left || step[0] !== 'code') return undefined;
      var id = setTimeout(function () { if (alive.current) now[1](clock()); }, nextSecond(at, now[0]));
      return function () { clearTimeout(id); };
    }, [at, now[0], step[0]]);

    function focus(ref) { var el = ref.current; if (el && el.focus) el.focus(); }
    function goStep(s) { err[1](null); info[1](null); step[1](s); }
    function fieldErr(f) { return err[0] && err[0].field === f ? err[0].text : undefined; }
    function clearErr(f) { if (err[0] && (err[0].field === f || !err[0].field)) err[1](null); }
    function markSent(to) { var t = clock(); sent[1]({ email: to, at: t }); now[1](t); }

    function sendCode(e) {
      if (e && e.preventDefault) e.preventDefault();
      var v = otpEmail(email[0]);
      if (!v) { err[1]({ field: 'email', text: 'Enter your email address.' }); return; }
      if (!EMAIL.test(v)) { err[1]({ field: 'email', text: 'Enter a valid email address.' }); return; }
      var s = sent[0];
      if (s && s.email === v && cooldownLeft(s.at, clock()) > 0) { now[1](clock()); goStep('code'); return; } // that code still works
      mail(v, false);
    }
    function resend() {
      var s = sent[0];
      if (!s || cooldownLeft(s.at, clock()) > 0) return;
      mail(s.email, true);
    }
    // send-otp to `to` (lower-cased). Sent: the cooldown restarts; a first send opens the code step, a resend says so.
    function mail(to, again) {
      if (inflight.current) return;
      inflight.current = true;
      busy[1](true);
      err[1](null);
      info[1](null);
      OO.api.sendOtp(hotel, to).then(function () {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        markSent(to);
        code[1]('');
        refused.current = null;
        if (again) { info[1]('New code sent!'); focus(focusRef); } else step[1]('code');
      }, function (x) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        if (again && x && Number(x.status) === 429) markSent(to); // the cap: don't reopen Resend at once
        var bad = !again && x && x.status === 422 && x.data && x.data.errors && x.data.errors.email;
        err[1](bad ? { field: 'email', text: 'Enter a valid email address.' } : { field: null, text: limitText(x, "We couldn't send the code. Please try again.") });
      });
    }

    // verify-otp with the six digits `c` (autosubmit, Enter and the button all come here; never two at once).
    function verify(c) {
      if (inflight.current) return;
      if (!/^\d{6}$/.test(c)) { err[1]({ field: 'code', text: 'Enter the 6-digit code from your email.' }); focus(focusRef); return; }
      var to = sent[0] && sent[0].email;
      if (!to) { goStep('email'); return; }
      inflight.current = true;
      busy[1](true);
      err[1](null);
      info[1](null);
      OO.api.verifyOtp(hotel, to, c).then(function (res) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        // An existing customer (200). Branch on res.customer: a new email's body (201, customer null) carries the
        // adapter's root `uid`, which normalizeCustomer would read as a customer.
        var cust = res && res.customer ? OO.model.normalizeCustomer(res) : null;
        if (cust && cust.uid) {
          OO.store.setSession({ customer: cust, customToken: (res && res.customToken) || null });
          if (props.onDone) props.onDone(cust, false);
          return;
        }
        // A new email (201): sign up with the backend's uid. A real store never invents one.
        var uid = OO.data.signupUid(hotel, res);
        if (!uid) { err[1]({ field: null, text: SIGNUP_FAILED }); return; }
        pending.current = { token: (res && res.customToken) || null, uid: uid, email: to };
        goStep('register');
      }, function (x) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        if (x && x.status === 400) {
          refused.current = c;
          err[1]({ field: 'code', text: 'That code is invalid or has expired. Check it, or send a new one.' });
          focus(focusRef);
          return;
        }
        err[1]({ field: null, text: limitText(x, "We couldn't check the code. Please try again.") });
      });
    }
    function submitCode(e) {
      if (e && e.preventDefault) e.preventDefault();
      verify(code[0]);
    }
    // The field's digits; the sixth one submits them (once; not the code just refused).
    function typeCode(raw) {
      var c = otpCode(raw);
      var was = code[0];
      code[1](c);
      clearErr('code');
      if (c.length === CODE_LEN && c !== was && c !== refused.current) verify(c);
    }
    // A paste replaces the code with the clipboard's digits (REF); with none, the browser pastes and typeCode filters.
    function pasteCode(e) {
      var data = e && e.clipboardData && e.clipboardData.getData ? e.clipboardData.getData('text') : '';
      if (!otpCode(data)) return;
      if (e.preventDefault) e.preventDefault();
      typeCode(data);
    }

    function create(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (inflight.current) return;
      var p = pending.current;
      if (!p || !p.uid) { err[1]({ field: null, text: SIGNUP_FAILED }); return; }
      var nm = name[0].trim();
      if (!nm) { err[1]({ field: 'name', text: 'Enter your full name.' }); focus(focusRef); return; }
      var pe = signupPhoneError(phone[0]);
      if (pe) { err[1]({ field: 'phone', text: pe }); focus(phoneRef); return; }
      var be = birthdayError(birthday[0]);
      if (be) { err[1]({ field: 'birthday', text: be }); focus(birthRef); return; }
      inflight.current = true;
      busy[1](true);
      err[1](null);
      OO.api.register(hotel, { uid: p.uid, name: nm, email: p.email, phone_no: phone[0].trim(), birthday: birthday[0] || null }).then(function (res) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        var cust = OO.model.normalizeCustomer(res);
        if (!cust) { err[1]({ field: null, text: "We couldn't create your account. Please try again." }); return; }
        OO.store.setSession({ customer: cust, customToken: p.token });
        if (props.onDone) props.onDone(cust, true);
      }, function (x) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        err[1]({ field: null, text: acct.errorText(x, "We couldn't create your account. Please try again.") });
      });
    }

    var s = step[0];
    var to = (sent[0] && sent[0].email) || otpEmail(email[0]);
    var head = {
      intro: ['Log in or sign up', 'See your orders and points, and check out faster.'],
      email: ['Log in or sign up', 'Enter your email to receive a verification code.'],
      code: ['Check your email', 'We sent a 6-digit code to ' + to + '.'],
      register: ['Complete your profile', 'Just a couple more details to get you started.'],
    }[s];
    var MailIcon = acct.icons.Mail;
    return (
      <ui.Card className="oo-profile__login" data-kk-step={kitchen ? s : undefined} data-pc-step={OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(hotel) ? s : undefined}>
        <span className="oo-profile__login-icon" aria-hidden="true">{s === 'code' ? <MailIcon size={24} /> : <OO.icons.User size={24} />}</span>
        <h2 className="oo-profile__login-title">{head[0]}</h2>
        <p className="oo-profile__login-body" id={BODY_ID}>{head[1]}</p>
        {err[0] && !err[0].field ? <ui.InlineNotice tone="danger" icon="Info" role="alert">{err[0].text}</ui.InlineNotice> : null}

        {s === 'intro' ? (
          <ui.Button block icon={<MailIcon size={20} />} onClick={function () { goStep('email'); }}>Continue with email</ui.Button>
        ) : null}

        {s === 'email' ? (
          <form className="oo-profile__form" onSubmit={sendCode} noValidate>
            <ui.TextInput label={kitchen ? "Email address" : "Email"} placeholder={kitchen ? "you@example.com" : undefined} labelVisible type="email" inputMode="email" autoComplete="email" required inputRef={focusRef}
              value={email[0]} onChange={function (v) { email[1](v); clearErr('email'); }} error={fieldErr('email')} />
            <ui.Button type="submit" block busy={busy[0]} disabled={!!kitchen && !email[0].trim()}>{kitchen ? "Send my code" : "Send verification code"}</ui.Button>
            {kitchen ? <p className="oo-kk-email-note"><MailIcon size={14} />We email a six-digit code. No password to remember.</p> : null}
          </form>
        ) : null}

        {s === 'code' ? (
          <form className="oo-profile__form" onSubmit={submitCode} noValidate>
            {info[0] ? <ui.InlineNotice tone="success">{info[0]}</ui.InlineNotice> : null}
            <CodeField label="Verification code" inputRef={focusRef} value={code[0]} onChange={typeCode} onPaste={pasteCode}
              error={fieldErr('code')} hint={OO.api && OO.api.isFixture ? 'Preview: the code is 123456.' : undefined} />
            <ui.Button type="submit" block busy={busy[0]}>Verify code</ui.Button>
            <div className="oo-profile__login-links">
              <ui.Button variant="link" disabled={busy[0]} onClick={function () { goStep('email'); }}>Use a different email</ui.Button>
              {left > 0 ? (
                <span className="oo-profile__resend-wait">Resend code in <span className="oo-profile__resend-num">{left + 's'}</span></span>
              ) : (
                <ui.Button variant="link" disabled={busy[0]} onClick={resend}>Resend verification code</ui.Button>
              )}
            </div>
          </form>
        ) : null}

        {s === 'register' ? (
          <form className="oo-profile__form" onSubmit={create} noValidate>
            <ui.TextInput label="Full name" labelVisible autoComplete="name" required inputRef={focusRef}
              value={name[0]} onChange={function (v) { name[1](v); clearErr('name'); }} error={fieldErr('name')} />
            <PhoneInput store={props.store} label="Mobile number" inputRef={phoneRef}
              value={phone[0]} onChange={function (v) { phone[1](v); clearErr('phone'); }} error={fieldErr('phone')} />
            <ui.TextInput label="Birthday (optional)" labelVisible type="date" autoComplete="bday" inputRef={birthRef}
              value={birthday[0]} onChange={function (v) { birthday[1](v); clearErr('birthday'); }} error={fieldErr('birthday')} />
            <ui.TextInput label="Email" labelVisible type="email" readOnly className="oo-profile__readonly" value={to} />
            <ui.Button type="submit" block busy={busy[0]}>Create account</ui.Button>
          </form>
        ) : null}
      </ui.Card>
    );
  }

  // ---- tabs (WAI-ARIA tabs: roving tabindex, arrow keys / Home / End) ----------------------------------------
  function Tabs(props) {
    var refs = React.useRef({});
    function onKey(e, i) {
      var n = TABS.length;
      var j = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
      if (j < 0) return;
      e.preventDefault();
      props.onSelect(TABS[j].key);
      var el = refs.current[TABS[j].key];
      if (el && el.focus) el.focus();
    }
    return (
      <div className="oo-profile__tabs" role="tablist" aria-label="Your account">
        {TABS.map(function (t, i) {
          var on = t.key === props.value;
          return (
            <button key={t.key} ref={function (el) { refs.current[t.key] = el; }} type="button" role="tab"
              id={'oo-profile-tab-' + t.key} aria-selected={on ? 'true' : 'false'} aria-controls="oo-profile-panel" tabIndex={on ? 0 : -1}
              className={OO.ui.cx('oo-profile__tab', on && 'is-active')}
              onClick={function () { props.onSelect(t.key); }} onKeyDown={function (e) { onKey(e, i); }}>
              {t.label}
            </button>
          );
        })}
      </div>
    );
  }

  // P3-12, online mode: the account hub's other pages (OO.pages.Account.logic.sections less Orders, a tab here, and
  // Profile, this page): Wallet, Rewards, and at a store that takes bookings Bookings.
  // app-account.jsx loads after this file.
  function AccountLinks(props) {
    var ui = OO.ui;
    var S = OO.pages.Account && OO.pages.Account.logic;
    var opts = { except: ['orders', 'profile'], booking: !!(props.store && props.store.booking) };
    var rows = S ? [].concat.apply([], S.sections('online', opts).map(function (s) { return s.rows; })) : [];
    if (!rows.length) return null;
    return (
      <ui.Card pad="list">
        <nav aria-label="More in your account">
          <ul className="oo-acct__links">
            {rows.map(function (r) {
              var link = OO.router.link(r.page, null, { hotel: props.hotel });
              return (
                <li key={r.key}>
                  <a className="oo-acct__linkrow" href={link.href} onClick={link.onClick}>
                    <span>{r.label}</span><OO.icons.ChevronRight size={16} />
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </ui.Card>
    );
  }

  function DetailsPanel(props) {
    var ui = OO.ui;
    var c = props.customer;
    var LogOut = acct.icons.LogOut;
    var Book = OO.pages.Addresses; // P3-10 (addresses.jsx loads after this file: read at render time)
    return (
      <div className="oo-acct__stack">
        <ui.Card pad="list">
          <dl className="oo-acct__dl">
            <div className="oo-acct__dlrow"><dt>Name</dt><dd>{c.name || 'Not added'}</dd></div>
            <div className="oo-acct__dlrow"><dt>Email</dt><dd>{c.email || 'Not added'}</dd></div>
            <div className="oo-acct__dlrow"><dt>Mobile</dt><dd>{c.phone || 'Not added'}</dd></div>
            <div className="oo-acct__dlrow"><dt>Birthday</dt><dd>{birthdayText(c.birthday) || 'Not added'}</dd></div>
          </dl>
        </ui.Card>
        {Book ? <Book hotel={props.hotel} store={props.store} onSaved={props.onNotice} /> : null}
        {props.mode !== 'app' ? <AccountLinks hotel={props.hotel} store={props.store} /> : null}
        <ui.Card pad="list">
          <nav aria-label="Store information">
            <ul className="oo-acct__links">
              {LEGAL.map(function (l) {
                var link = OO.router.link('legal', { kind: l[0] }, { hotel: props.hotel });
                return (
                  <li key={l[0]}>
                    <a className="oo-acct__linkrow" href={link.href} onClick={link.onClick}>
                      <span>{l[1]}</span><OO.icons.ChevronRight size={16} />
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </ui.Card>
        <ui.Button variant="outline" block className="oo-profile__logout" icon={<LogOut size={20} />} onClick={props.onLogout}>Log out</ui.Button>
      </div>
    );
  }

  function PointsPanel(props) {
    var ui = OO.ui;
    var store = props.store;
    var q = props.activity;
    var word = (store.loyalty && store.loyalty.pointsWord) || 'points';
    var rewards = props.mode === 'app' ? OO.router.link('rewards', null, { hotel: props.hotel }) : null;
    var list = q.data || [];
    var body;
    if (q.loading && !q.data) {
      body = <div aria-busy="true"><span className="oo-sr-only" role="status">Loading activity</span><ui.Skeleton variant="block" height={168} /></div>;
    } else if (q.error) {
      body = <ui.EmptyState icon="Info" title="We couldn't load your activity" body="Check your connection and try again." action={<ui.Button size="md" onClick={q.reload}>Try again</ui.Button>} />;
    } else if (!list.length) {
      body = <ui.EmptyState icon="Star" title="No points activity yet" body={'Points you earn and spend at ' + store.name + ' will show up here.'} />;
    } else {
      body = (
        <ui.Card pad="list">
          <ul className="oo-profile__acts">
            {list.map(function (a) {
              return (
                <li key={a.id} className="oo-profile__act">
                  <span className="oo-profile__act-text">
                    <span className="oo-profile__act-title">{a.title}</span>
                    <span className="oo-profile__act-when">{a.when}</span>
                  </span>
                  <span className={'oo-profile__act-pts is-' + a.kind}>{a.pointsLabel}<span className="oo-sr-only">{' ' + word}</span></span>
                </li>
              );
            })}
          </ul>
        </ui.Card>
      );
    }
    return (
      <div className="oo-acct__stack">
        <div className="oo-profile__wallet">
          <div className="oo-profile__wallet-top">
            <span className="oo-profile__wallet-prog">{(store.loyalty && store.loyalty.programName) || 'Rewards'}</span>
            {props.tier ? <ui.Badge tone="inverse" upper>{props.tier}</ui.Badge> : null}
          </div>
          <div className="oo-profile__wallet-pts">
            <span className="oo-profile__wallet-num">{props.points == null ? '—' : props.points}</span>
            <span className="oo-profile__wallet-word">{word}</span>
          </div>
          {rewards ? (
            <a className="oo-profile__wallet-link" href={rewards.href} onClick={rewards.onClick}>See rewards<OO.icons.ChevronRight size={14} /></a>
          ) : <p className="oo-profile__wallet-note">{'Your balance at ' + store.name + '.'}</p>}
        </div>
        <ui.SectionTitle level="section" as="h3">Activity</ui.SectionTitle>
        {body}
      </div>
    );
  }

  function CustomerCode(props) {
    var code = props.code == null ? '' : String(props.code).trim();
    var qr = React.useMemo(function () {
      if (!code) return null;
      try { return OO.qrcode.qrPath(OO.qrcode.qrMatrix(code, { ecc: 'M' }), { quiet: 4 }); }
      catch (e) { return null; }
    }, [code]);
    if (!qr) return null;
    return (
      <div className="oo-profile__code">
        <svg viewBox={'0 0 ' + qr.size + ' ' + qr.size} shapeRendering="crispEdges" role="img"
          aria-label={'QR code for customer code ' + code} focusable="false">
          <rect width={qr.size} height={qr.size} fill="#fff" />
          <path d={qr.d} fill="#000" />
        </svg>
        <span className="oo-profile__code-label">Customer code</span>
        <strong>{code}</strong>
      </div>
    );
  }

  function ProfileView(props) {
    var ui = OO.ui;
    var hotel = props.hotel;
    var store = props.store;
    var route = props.route;
    var base = props.session.customer || {};
    var uid = base.uid || null;
    var tab = TABS.some(function (t) { return t.key === route.query.tab; }) ? route.query.tab : 'profile';
    var custQ = OO.useAsync(function () {
      if (!uid) return null;
      return OO.api.getCustomer(hotel, uid).then(function (raw) { return OO.model.normalizeCustomer(raw); });
    }, [hotel, uid]);
    // The wallet's code belongs to this store; customer/getdata can return another store's customer code.
    var codeQ = OO.useAsync(function () {
      if (!uid) return null;
      return OO.api.getWallet(hotel, { uid: uid }).then(function (wallet) {
        return { hotel: hotel, uid: uid, code: OO.model.normalizeLoyalty({ wallet: wallet }, { store: store }).memberCode };
      });
    }, [hotel, uid]);
    var actQ = OO.useAsync(function () {
      if (tab !== 'points' || !uid) return null;
      return OO.api.getWalletTransactions(hotel, { uid: uid }).then(function (raw) {
        return OO.model.normalizeActivity(raw, { now: new Date(), timeZone: store.timezone });
      });
    }, [hotel, uid, tab === 'points', store.timezone]);
    // Identity fields come from the session (edits are saved there); points and tier from customer/getdata.
    var fresh = custQ.data || null;
    var points = fresh && fresh.points != null ? fresh.points : (base.points != null ? base.points : null);
    var tier = (fresh && fresh.tier) || base.tier || null;
    var word = (store.loyalty && store.loyalty.pointsWord) || 'points';
    var edit = OO.router.link('profile', { edit: true }, { hotel: hotel });

    function select(key) {
      var q = Object.assign({}, route.query);
      delete q.directLogin;
      if (key === 'profile') delete q.tab; else q.tab = key;
      OO.router.go(OO.router.href('profile', null, { hotel: hotel, query: q }), { replace: true });
    }

    var panel;
    if (tab === 'orders') panel = <acct.OrderList hotel={hotel} store={store} uid={uid} />;
    else if (tab === 'points') panel = <PointsPanel store={store} hotel={hotel} mode={props.mode} activity={actQ} points={points} tier={tier} />;
    else panel = <DetailsPanel customer={base} hotel={hotel} store={store} mode={props.mode} onLogout={props.onLogout} onNotice={props.onNotice} />;

    return (
      <div className="oo-profile__grid">
        <div className="oo-profile__side">
          <ui.Card className="oo-profile__card">
            <div className="oo-profile__who">
              <span aria-hidden="true"><ui.Avatar name={base.name || base.email} size={56} className="oo-profile__avatar" /></span>
              <div className="oo-profile__id">
                <h2 className="oo-profile__name">{base.name || 'Your account'}</h2>
                {base.email ? <div className="oo-profile__contact">{base.email}</div> : null}
                {base.phone ? <div className="oo-profile__contact">{base.phone}</div> : null}
              </div>
            </div>
            <div className="oo-profile__ptsrow">
              <span className="oo-profile__pts">
                <span className="oo-profile__ptsnum">{points == null ? '—' : points}</span>{' ' + word}
              </span>
              {tier ? <ui.Badge tone="required" upper>{tier}</ui.Badge> : null}
            </div>
            <CustomerCode code={codeQ.data && codeQ.data.hotel === hotel && codeQ.data.uid === uid ? codeQ.data.code : null} />
            <ui.Button variant="outline" size="sm" block icon="Pencil" href={edit.href} onClick={edit.onClick}>Edit profile</ui.Button>
          </ui.Card>
        </div>
        <div className="oo-profile__main">
          <Tabs value={tab} onSelect={select} />
          <div className="oo-profile__panel" role="tabpanel" id="oo-profile-panel" aria-labelledby={'oo-profile-tab-' + tab}>{panel}</div>
        </div>
      </div>
    );
  }

  // ---- saving (P3-09: B16 through OO.api.updateProfile; see the header) ----------------------------------
  var SAVE_FAILED = "We couldn't save your changes. Please try again.";
  var SAVE_SIGN_IN = 'For your security, please log in again to save your changes.';
  var SAVE_LATER = 'Profile updates are unavailable right now. Please try again shortly.';
  var SAVE_NOT_LINKED = "We couldn't set up your account at this store yet. Please try again later.";
  var FIELD_TEXT = {
    name: 'Check your name and try again.', phone: 'Enter a valid mobile number.', birthday: 'Enter a valid date.',
    address: 'Check this address and try again.',
  };
  var FIELD_OF = { name: 'name', phone_no: 'phone', birthday: 'birthday', address: 'address' };

  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  // A 422's errors {field: [message]} → this page's field errors ('address.0.address' is the address).
  function fieldErrors(errors) {
    var out = {};
    Object.keys(errors && typeof errors === 'object' ? errors : {}).forEach(function (k) {
      var f = own(FIELD_OF, String(k).split('.')[0]) ? FIELD_OF[String(k).split('.')[0]] : null;
      if (f) out[f] = FIELD_TEXT[f];
    });
    return out;
  }
  // A refused save (OO.api.updateProfile's rejection: status, code, fields) → what the page says.
  function saveError(err) {
    var e = err || {};
    var status = Number(e.status);
    var code = typeof e.code === 'string' ? e.code : null;
    var view = { text: SAVE_FAILED, fields: {}, signIn: false };
    if (status === 401) {
      view.text = SAVE_SIGN_IN;
      view.signIn = true;
    } else if (status === 422) {
      view.fields = fieldErrors(e.fields || (e.data && typeof e.data === 'object' ? e.data.errors : null));
      if (Object.keys(view.fields).length) view.text = null;
    } else if (status === 429) view.text = limitText(e, SAVE_FAILED);
    else if (status === 503 && code === 'VERIFIER_UNAVAILABLE') view.text = SAVE_LATER;
    else if (status === 403 && code === 'STORE_NOT_LINKED') view.text = SAVE_NOT_LINKED;
    return view;
  }
  // The session's customer after a save (see the header).
  function reconcile(prev, body) {
    var c = OO.model.normalizeCustomer(body);
    if (!c || !prev || !prev.uid || String(c.uid || '') !== String(prev.uid)) return null;
    c.customerCode = c.customerCode || prev.customerCode || null;
    c.tier = c.tier || prev.tier || null;
    c.points = c.points != null ? c.points : (prev.points != null ? prev.points : null);
    // P3-10: each address keeps its uid and default (normalizeCustomer drops both), so the checkout knows the default.
    var A = OO.pages.Addresses && OO.pages.Addresses.logic;
    if (A && body && body.data && typeof body.data === 'object') c.addresses = A.sessionAddresses(A.book(body.data.address));
    return c;
  }
  // The edit form's updateProfile fields: the name always, the mobile and the birthday only when changed.
  function editFields(c, v) {
    c = c || {};
    v = v || {};
    var out = { name: String(v.name == null ? '' : v.name).trim() };
    var phone = String(v.phone == null ? '' : v.phone);
    var birthday = String(v.birthday == null ? '' : v.birthday);
    if (phone !== String(c.phone || '')) out.phone_no = compactPhone(phone) || null;
    if (birthday !== (c.birthday ? String(c.birthday).slice(0, 10) : '')) out.birthday = birthday || null;
    return out;
  }
  function saveProfile(hotel, fields) {
    var s = OO.store.session();
    var uid = s && s.customer && s.customer.uid ? String(s.customer.uid) : '';
    return OO.api.updateProfile(hotel, fields, { uid: uid || null, token: (s && s.customToken) || null }).then(function (body) {
      var cur = OO.store.session();
      var next = uid && cur && cur.customer && String(cur.customer.uid || '') === uid ? reconcile(cur.customer, body) : null;
      if (next) OO.store.setSession({ customer: next, customToken: cur.customToken || null });
      return { customer: next, body: body };
    });
  }

  // ---- edit ----------------------------------------------------------------------------------------------
  function EditProfile(props) {
    var ui = OO.ui;
    var c = props.session.customer || {};
    var name = React.useState(c.name || '');
    var phone = React.useState(c.phone || '');
    var birthday = React.useState(c.birthday ? String(c.birthday).slice(0, 10) : '');
    var errs = React.useState({});
    var failed = React.useState(null);  // the last refusal: { text, signIn } (saveError)
    var busy = React.useState(false);
    var inflight = React.useRef(false); // one save at a time (a ref, not the busy state)
    var alive = useAlive();
    var refs = { name: React.useRef(null), phone: React.useRef(null), birthday: React.useRef(null) };

    function focusFirst(next) {
      var first = ['name', 'phone', 'birthday'].filter(function (k) { return next[k]; })[0];
      if (first) { var el = refs[first].current; if (el && el.focus) el.focus(); }
      return !!first;
    }
    function submit(e) {
      e.preventDefault();
      if (inflight.current) return;
      var fields = editFields(c, { name: name[0], phone: phone[0], birthday: birthday[0] });
      var next = {};
      if (!fields.name) next.name = 'Enter your name.';
      var pe = own(fields, 'phone_no') ? phoneError(phone[0]) : null;
      if (pe) next.phone = pe;
      var be = own(fields, 'birthday') ? birthdayError(birthday[0]) : null;
      if (be) next.birthday = be;
      errs[1](next);
      failed[1](null);
      if (focusFirst(next)) return;
      inflight.current = true;
      busy[1](true);
      saveProfile(props.hotel, fields).then(function () {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        props.onSaved();
      }, function (err) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        var v = saveError(err);
        errs[1](v.fields);
        failed[1](v.text ? { text: v.text, signIn: v.signIn } : null);
        focusFirst(v.fields);
      });
    }
    function clear(k) { if (errs[0][k]) { var n = Object.assign({}, errs[0]); delete n[k]; errs[1](n); } }
    // A 401: the sign-in has to be renewed. The login opens on this page, and then the old session goes.
    function signInAgain() {
      OO.router.go(OO.router.href('profile', { edit: true }, { hotel: props.hotel, query: { directLogin: 'true' } }), { replace: true });
      OO.store.logout();
    }

    return (
      <form className="oo-profile__edit" onSubmit={submit} noValidate>
        <ui.Card className="oo-profile__form">
          <ui.TextInput label="Full name" labelVisible autoComplete="name" required inputRef={refs.name}
            value={name[0]} onChange={function (v) { name[1](v); clear('name'); }} error={errs[0].name} />
          <ui.TextInput label="Email" labelVisible type="email" readOnly className="oo-profile__readonly" value={c.email || ''}
            hint="You log in with this email, so it can't be changed here." />
          <ui.TextInput label="Mobile number" labelVisible type="tel" inputMode="tel" autoComplete="tel" inputRef={refs.phone}
            value={phone[0]} onChange={function (v) { phone[1](v); clear('phone'); }} error={errs[0].phone} />
          <ui.TextInput label="Birthday" labelVisible type="date" autoComplete="bday" inputRef={refs.birthday}
            value={birthday[0]} onChange={function (v) { birthday[1](v); clear('birthday'); }} error={errs[0].birthday} hint="Optional" />
        </ui.Card>
        {failed[0] ? (
          <ui.InlineNotice tone="danger" icon="Info" role="alert" className="oo-profile__save-error">
            {failed[0].text}
            {failed[0].signIn ? <React.Fragment>{' '}<ui.Button variant="link" className="oo-profile__inlink" onClick={signInAgain}>Log in again</ui.Button></React.Fragment> : null}
          </ui.InlineNotice>
        ) : null}
        <div className="oo-profile__edit-actions">
          <ui.Button type="submit" block busy={busy[0]}>Save changes</ui.Button>
          <ui.Button variant="ghost" block onClick={props.onCancel}>Cancel</ui.Button>
        </div>
        {OO.api && OO.api.isFixture ? <p className="oo-acct__note">Preview: changes are saved on this device only.</p> : null}
      </form>
    );
  }

  // ---- page ----------------------------------------------------------------------------------------------
  OO.pages.Profile = function Profile(props) {
    var route = props.route;
    var mode = props.mode;
    var hotel = route.hotel;
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var session = OO.useStore(function (s) { return s.session; });
    var toast = React.useState(null);
    var edit = !!route.params.edit;
    var loggedIn = !!(session && session.customer);
    var title = !loggedIn ? 'Log in' : (edit ? 'Edit profile' : 'Profile');
    var profileHref = OO.router.href('profile', null, { hotel: hotel });
    // P3-12: the account hub is the profile's parent in both modes (a fallback: Back follows the in-app history first).
    var back = edit && loggedIn ? profileHref : OO.router.href('account', null, { hotel: hotel });
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: back });
    var blocked = acct.gate(h, { title: title, header: header });
    if (blocked) return blocked;
    var store = h.store;

    function onLoggedIn(cust, isNew) {
      var who = (cust && (cust.firstName || cust.name)) || '';
      toast[1]((isNew ? 'Welcome' : 'Welcome back') + (who ? ', ' + who : '') + '!');
      if (route.query.directLogin != null) {
        var q = Object.assign({}, route.query);
        delete q.directLogin;
        OO.router.go(OO.router.href('profile', edit ? { edit: true } : null, { hotel: hotel, query: q }), { replace: true });
      }
    }
    function onLogout() {
      OO.store.logout();
      toast[1]("You're logged out.");
    }
    function leaveEdit() { OO.router.back(profileHref); }

    var body;
    if (!loggedIn && mode === 'app' && !props.kitchenEmbedded && OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(route.chain || hotel) && OO.appKit.KitchenAccount) return <OO.appKit.KitchenAccount route={route} />;
    if (!loggedIn) body = <LoginPanel key="login" hotel={hotel} store={store} direct={route.query.directLogin === 'true'} onDone={onLoggedIn} />;
    else if (edit) body = <EditProfile key="edit" hotel={hotel} session={session} onSaved={function () { toast[1]('Profile saved.'); leaveEdit(); }} onCancel={leaveEdit} />;
    else body = <ProfileView key="view" hotel={hotel} store={store} route={route} mode={mode} session={session} onLogout={onLogout} onNotice={function (t) { if (t) toast[1](t); }} />;

    return (
      <acct.Page title={title} {...header} className={ui.cx('oo-profile', !loggedIn && 'oo-profile--login', edit && loggedIn && 'oo-profile--edit')}>
        {body}
        <ui.PoweredBySipo />
        <ui.Toast open={!!toast[0]} onClose={function () { toast[1](null); }}>{toast[0]}</ui.Toast>
      </acct.Page>
    );
  };

  // P3-08: the login's pure rules; P3-09: the save's (see the header).
  OO.pages.Profile.logic = {
    COOLDOWN_S: COOLDOWN_S,
    otpEmail: otpEmail,
    otpCode: otpCode,
    cooldownLeft: cooldownLeft,
    limitText: limitText,
    signupPhoneError: signupPhoneError,
    saveError: saveError,
    reconcile: reconcile,
    editFields: editFields,
  };
  // P3-09: the profile save (B16) and the session reconcile; the address book saves through it too.
  OO.pages.Profile.saveProfile = saveProfile;
})();


// ===================== src/pages/legal.jsx =====================
// src/pages/legal.jsx: the legal pages (page id 'legal', params.kind = terms | privacy | allergy):
// /{hotel}/terms-and-conditions, /privacy-policy, /allergy-information. Styles: styles/pages.css (.oo-legal__*).
// Not in the design: a readable text page in its system (PageHeader, canvas, one white card).
//
// Content: the normalised store's terms fields (OO.model Store.terms.{terms, privacy, allergy}: back-office HTML, plain
// text or a web address; terms = terms_condition › terms_of_use). The HTML is never injected: it is parsed with
// DOMParser and rebuilt as React elements from a small whitelist (headings, paragraphs, lists, emphasis, links with
// http(s) / mailto / tel hrefs, tables, line breaks). Scripts, styles, embeds, images, forms, event handlers and every
// other attribute are dropped. A leading heading that repeats the page title is dropped too.
//
// Batch 2 P3-25 (REF develop: OnlineOrderAccountDrawer openPolicyItem, RestaurantPolicyModal, the About page's policies;
// C §1.1 "Policies", §3.4):
//   Web addresses. A value that is a bare web address (REF isSimpleUrl: no white space, an optional http(s)://, a dotted
//     host, an optional plain path) is a link, never text: the card says the policy is on the store's website and offers
//     "Open …" in a new tab (rel noopener noreferrer). https:// is added when it has no scheme (REF formatUrl). Only
//     http(s) ever becomes an href this way. No automatic redirect.
//   "Not available" in production. A page without content (empty, "null", or HTML that keeps no readable text) shows
//     REF's "… is not available for this store." plus a line to contact the store (its phone, when it has one) for a live
//     store in production: OO.ui.liveProduction, the one production test (src/ui/chrome.jsx). Dev, local and staging
//     hosts and the fixture stores keep the sample copy marked "Sample text". getRestaurant never sends a privacy
//     policy, so a live store's Privacy page always says "not available" there (REF parity).
//   Terms of use (REF's fourth policy, terms_of_use). The terms page adds a "Terms of use" section (#terms-of-use) when
//     terms_of_use has content that isn't the body already shown (compared trimmed: with no terms_condition, the body IS
//     terms_of_use). The text: Store.terms.use when the Store carries it, else the cached getRestaurant body
//     (OO.api.getRestaurant(hotel): the read useHotel already made, so no request), read once per hotel through
//     OO.ui.useRatingRead on every legal page (the list below names it). A visit to /terms-and-conditions#terms-of-use
//     scrolls to the section once it shows.
//   The "Other policies" list (logic.policies): terms and conditions, terms of use (only with its own content), privacy
//     policy, allergy information, without the page's own. A policy that is a web address opens it in a new tab; the
//     others are their pages (terms of use: the terms page's section).
//
// OO.pages.Legal.logic (pure; tests/legal-p325.test.mjs):
//   KINDS ['terms', 'privacy', 'allergy'] (the routed kinds) · TITLES { terms, use, privacy, allergy } · USE_ID
//       'terms-of-use' · UNAVAILABLE { kind: the "not available" sentence }.
//   policyUrl(text) → 'https://…' | 'http://…' | null: the web address a policy value stands for (rules above).
//   hostOf(url) → the address without its scheme and trailing '/': the link's visible text.
//   termsOfUse(raw) → string: a getRestaurant body's terms_of_use (data[0], else data.restaurant), trimmed; '' for none.
//   useOf(store, raw) → string: Store.terms.use when the Store has that key, else termsOfUse(raw).
//   hasContent(text) → bool: a web address, or text / HTML with readable text.
//   distinct(use, shown) → bool: use has content and isn't the shown body (both trimmed).
//   policies(store, use) → [{ kind ('terms' | 'use' | 'privacy' | 'allergy'), title, url (policyUrl | null), page
//       (the legal kind its page is: 'terms' for use), hash (USE_ID for use, else null) }]: every policy, in REF's
//       order; use only when distinct from the terms.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var TITLES = { terms: 'Terms and conditions', use: 'Terms of use', privacy: 'Privacy policy', allergy: 'Allergy information' };
  var KINDS = ['terms', 'privacy', 'allergy'];
  var USE_ID = 'terms-of-use';
  // REF: "Terms And Conditions Are Not Available For This Store." and its siblings, in sentence case.
  var UNAVAILABLE = {
    terms: 'Terms and conditions are not available for this store.',
    use: 'Terms of use are not available for this store.',
    privacy: 'Privacy policy is not available for this store.',
    allergy: 'Allergy information is not available for this store.',
  };

  // Tag → rendered tag ('' = keep the children only). Headings drop a level: the page h1 is the header and the
  // body title is an h2.
  var TAGS = {
    P: 'p', DIV: 'div', SECTION: 'div', ARTICLE: 'div', H1: 'h3', H2: 'h3', H3: 'h3', H4: 'h4', H5: 'h4', H6: 'h4',
    UL: 'ul', OL: 'ol', LI: 'li', STRONG: 'strong', B: 'strong', EM: 'em', I: 'em', U: 'u', SMALL: 'small', SUP: 'sup', SUB: 'sub',
    BR: 'br', HR: 'hr', BLOCKQUOTE: 'blockquote', A: 'a', TABLE: 'table', THEAD: 'thead', TBODY: 'tbody', TR: 'tr', TH: 'th', TD: 'td',
    SPAN: '', FONT: '', CENTER: '', MAIN: '', HEADER: '', FOOTER: '', LABEL: '',
  };
  var VOID = { br: true, hr: true };
  var STRUCT = { ul: true, ol: true, table: true, thead: true, tbody: true, tr: true }; // no text children
  var SAFE_HREF = /^(https?:|mailto:|tel:)/i;
  // REF isSimpleUrl (shared/utils/index.ts): an optional scheme, a dotted host, an optional plain path.
  var SIMPLE_URL = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/i;

  function str(v) { return v === null || v === undefined ? '' : String(v); }
  function clean(v) { var s = str(v).trim(); return s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined' ? '' : s; }

  // ---- logic (the header) -----------------------------------------------------------------------------------------
  function policyUrl(text) {
    var s = clean(text);
    if (!s || /\s/.test(s) || !SIMPLE_URL.test(s)) return null;
    return /^https?:\/\//i.test(s) ? s : 'https://' + s;
  }
  function hostOf(url) { return str(url).replace(/^https?:\/\//i, '').replace(/\/+$/, ''); }
  function rowOf(raw) {
    var data = raw && typeof raw === 'object' ? raw.data : null;
    var d = (Array.isArray(data) ? data[0] : null) || (data && typeof data === 'object' && !Array.isArray(data) ? data.restaurant : null);
    return d && typeof d === 'object' && !Array.isArray(d) ? d : {};
  }
  function termsOfUse(raw) { return clean(rowOf(raw).terms_of_use); }
  function useOf(store, raw) {
    var t = store && store.terms;
    if (t && typeof t === 'object' && Object.prototype.hasOwnProperty.call(t, 'use')) return clean(t.use);
    return termsOfUse(raw);
  }
  function hasContent(text) {
    var s = clean(text);
    // What a script / style / template holds is never shown (renderDoc drops those elements with their content).
    return !!s && (!!policyUrl(s) || !!OO.format.stripHtml(s.replace(/<(script|style|template)\b[\s\S]*?<\/\1\s*>/gi, ' ')));
  }
  function distinct(use, shown) { return hasContent(use) && clean(use) !== clean(shown); }
  function policies(store, use) {
    var t = (store && store.terms) || {};
    var out = [];
    [['terms', t.terms], ['use', use], ['privacy', t.privacy], ['allergy', t.allergy]].forEach(function (p) {
      if (p[0] === 'use' && !distinct(use, t.terms)) return;
      out.push({ kind: p[0], title: TITLES[p[0]], url: policyUrl(p[1]), page: p[0] === 'use' ? 'terms' : p[0], hash: p[0] === 'use' ? USE_ID : null });
    });
    return out;
  }

  // ---- HTML → React ----------------------------------------------------------------------------------------------
  function convert(node, key) {
    if (node.nodeType === 3) return node.nodeValue;
    if (node.nodeType !== 1 || !Object.prototype.hasOwnProperty.call(TAGS, node.nodeName)) return null; // dropped with its content
    var tag = TAGS[node.nodeName];
    var kids = [];
    for (var i = 0; i < node.childNodes.length; i++) {
      var c = convert(node.childNodes[i], i);
      if (c === null || c === '' || (STRUCT[tag] && typeof c === 'string' && !c.trim())) continue;
      kids.push(c);
    }
    if (tag === '') return kids.length ? React.createElement(React.Fragment, { key: key }, kids) : null;
    var props = { key: key };
    if (tag === 'a') {
      var href = String(node.getAttribute('href') || '').trim();
      if (!SAFE_HREF.test(href)) return kids.length ? React.createElement(React.Fragment, { key: key }, kids) : null;
      props.href = href;
      if (/^https?:/i.test(href)) { props.target = '_blank'; props.rel = 'noopener noreferrer'; }
    }
    if (VOID[tag]) return React.createElement(tag, props);
    return React.createElement(tag, props, kids.length ? kids : null);
  }

  function textOf(el) { return String(el && el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase(); }

  // HTML (or plain text) → React nodes, or null when there's nothing readable.
  function renderDoc(html, title) {
    var src = String(html || '').trim();
    if (!src || !OO.format.stripHtml(src)) return null;
    if (!/<[a-z!/]/i.test(src)) {
      return src.split(/\n{2,}/).map(function (para, i) {
        var lines = para.split('\n');
        return <p key={i}>{lines.map(function (l, j) { return j ? <React.Fragment key={j}><br />{l}</React.Fragment> : l; })}</p>;
      });
    }
    if (typeof window.DOMParser !== 'function') return <p>{OO.format.stripHtml(src)}</p>;
    var doc = new window.DOMParser().parseFromString(src, 'text/html');
    var body = doc && doc.body;
    if (!body) return null;
    var firstEl = body.firstElementChild;
    if (firstEl && /^H[1-6]$/.test(firstEl.nodeName) && textOf(firstEl) === String(title || '').toLowerCase()) body.removeChild(firstEl);
    var out = [];
    for (var i = 0; i < body.childNodes.length; i++) {
      var c = convert(body.childNodes[i], i);
      if (c !== null && !(typeof c === 'string' && !c.trim())) out.push(c);
    }
    return out.length ? out : null;
  }

  // ---- the states of a policy's card ------------------------------------------------------------------------------
  function Sample(props) {
    var ui = OO.ui;
    var name = props.store.name;
    var copy = {
      terms: [
        'Orders are prepared once ' + name + ' accepts them. Pickup times are estimates and can change when the store is busy.',
        'Prices shown include GST where it applies. The total you pay is shown before you place your order.',
        'If something is wrong with your order, please contact the store as soon as you can so they can help.',
      ],
      privacy: [
        'We use your name, email and phone number to process your order and to keep you updated about it.',
        'If you opt in to offers, we may send you news and promotions. You can opt out at any time.',
        'We don’t sell your personal information.',
      ],
      allergy: [
        'Our kitchen handles common allergens, so we can’t guarantee any item is free from them.',
        'If you have an allergy or intolerance, please contact ' + name + ' before you order.',
      ],
    }[props.kind] || [];
    return (
      <div className="oo-legal__sample">
        <ui.Badge tone="warn">Sample text</ui.Badge>
        <p className="oo-legal__samplenote">{name + ' hasn’t added its ' + TITLES[props.kind].toLowerCase() + ' yet. This is sample text, not the store’s own policy.'}</p>
        <div className="oo-legal__doc">{copy.map(function (p, i) { return <p key={i}>{p}</p>; })}</div>
      </div>
    );
  }

  // A policy kept on the store's website (P3-25): never shown as text, opened in a new tab.
  function External(props) {
    var ui = OO.ui;
    var what = TITLES[props.kind].toLowerCase();
    return (
      <div className="oo-legal__sample oo-legal__external">
        <div className="oo-legal__doc"><p>{props.store.name + ' keeps its ' + what + ' on its website.'}</p></div>
        <ui.Button variant="outline" size="md" href={props.url} target="_blank" rel="noopener noreferrer">
          {'Open ' + what}<span className="oo-sr-only"> (opens in a new tab)</span>
        </ui.Button>
        <p className="oo-legal__samplenote">{hostOf(props.url)}</p>
      </div>
    );
  }

  // No content for a live store in production (P3-25): REF's sentence, and how to ask the store.
  function Unavailable(props) {
    var ui = OO.ui;
    var s = props.store;
    var phone = clean(s.phone);
    var tel = phone ? 'tel:' + phone.replace(/[^\d+]/g, '') : null;
    var hint = props.kind === 'allergy'
      ? 'If you have an allergy or intolerance, please contact ' + s.name + ' before you order.'
      : 'Please contact ' + s.name + ' if you have a question.';
    return (
      <div className="oo-legal__sample oo-legal__unavailable">
        <div className="oo-legal__doc"><p>{UNAVAILABLE[props.kind]}</p></div>
        <p className="oo-legal__samplenote">{hint}</p>
        {tel ? <ui.Button variant="outline" size="md" href={tel}>{'Call ' + phone}</ui.Button> : null}
      </div>
    );
  }

  // One policy's card body: a web address, the document, else "not available" (live, production) or the sample.
  function Doc(props) {
    var url = policyUrl(props.text);
    if (url) return <External url={url} kind={props.kind} store={props.store} />;
    if (props.nodes) return <div className="oo-legal__doc">{props.nodes}</div>;
    if (props.live) return <Unavailable kind={props.kind} store={props.store} />;
    return <Sample kind={props.kind} store={props.store} />;
  }

  function safeDoc(text, title) {
    try { return renderDoc(text, title); } catch (e) { return null; }
  }

  OO.pages.Legal = function Legal(props) {
    var route = props.route;
    var mode = props.mode;
    var hotel = route.hotel;
    var ui = OO.ui;
    var kind = KINDS.indexOf(route.params.kind) >= 0 ? route.params.kind : 'terms';
    var title = TITLES[kind];
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var html = store && store.terms ? store.terms[kind] || '' : '';
    // Terms of use: the Store's own key, else the cached getRestaurant body (the header). Every legal page reads it (the
    // Other policies list names it); only the terms page shows it.
    var ownUse = !!(store && store.terms && Object.prototype.hasOwnProperty.call(store.terms, 'use'));
    var raw = ui.useRatingRead(store && !ownUse && hotel ? 'legal-use|' + hotel : null, function () {
      return OO.api.getRestaurant(hotel);
    });
    var use = store ? useOf(store, raw) : '';
    var onTerms = kind === 'terms' && distinct(use, html);
    var nodes = React.useMemo(function () { return safeDoc(html, title); }, [html, title]);
    var useNodes = React.useMemo(function () { return onTerms ? safeDoc(use, TITLES.use) : null; }, [use, onTerms]);
    var showUse = onTerms && !!(policyUrl(use) || useNodes); // a section only when it shows something
    React.useEffect(function () {
      if (!showUse || !window.location || window.location.hash !== '#' + USE_ID || typeof document === 'undefined') return;
      var el = document.getElementById(USE_ID);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    }, [showUse]);
    var back = OO.router.href(mode === 'app' ? 'account' : 'menu', null, { hotel: hotel });
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: back, profile: true });
    var blocked = acct.gate(h, { title: title, header: header });
    if (blocked) return blocked;
    var live = !!(ui.liveProduction && ui.liveProduction(hotel));
    var more = policies(store, use).filter(function (p) { return p.kind !== kind && !(kind === 'terms' && p.kind === 'use'); });

    return (
      <acct.Page title={title} {...header} className="oo-legal">
        <ui.SectionTitle level="page" sub={store.name}>{title}</ui.SectionTitle>
        <ui.Card className="oo-legal__card">
          <Doc text={html} nodes={nodes} kind={kind} store={store} live={live} />
        </ui.Card>
        {showUse ? (
          <section className="oo-legal__use" id={USE_ID} aria-labelledby="oo-legal-use-title">
            <ui.SectionTitle level="section" id="oo-legal-use-title">{TITLES.use}</ui.SectionTitle>
            <ui.Card className="oo-legal__card">
              <Doc text={use} nodes={useNodes} kind="use" store={store} live={live} />
            </ui.Card>
          </section>
        ) : null}
        <nav className="oo-legal__more" aria-label="Other policies">
          {more.map(function (p) {
            if (p.url) {
              return (
                <a key={p.kind} className="oo-legal__morelink" href={p.url} target="_blank" rel="noopener noreferrer">
                  <span>{p.title}<span className="oo-sr-only"> (opens in a new tab)</span></span><OO.icons.ChevronRight size={16} />
                </a>
              );
            }
            var link = OO.router.link('legal', { kind: p.page }, { hotel: hotel, hash: p.hash || undefined });
            return (
              <a key={p.kind} className="oo-legal__morelink" href={link.href} onClick={link.onClick}>
                <span>{p.title}</span><OO.icons.ChevronRight size={16} />
              </a>
            );
          })}
        </nav>
        <ui.PoweredBySipo />
      </acct.Page>
    );
  };

  OO.pages.Legal.logic = {
    KINDS: KINDS, TITLES: TITLES, USE_ID: USE_ID, UNAVAILABLE: UNAVAILABLE,
    policyUrl: policyUrl, hostOf: hostOf, termsOfUse: termsOfUse, useOf: useOf, hasContent: hasContent, distinct: distinct,
    policies: policies,
  };
})();


// ===================== src/pages/payment-callback.jsx =====================
// src/pages/payment-callback.jsx: the payment gateway return page (page id 'payment-callback': /{hotel}/payment-callback,
// and the root /payment-callback with hotel null). Styles: styles/pages.css (.oo-paycb__*). Not in the design: one
// centred result card in its system (PageHeader, canvas, white card, black actions).
//
// Params (acct.paycb.parseParams; inventory B §5): the order is order_id, else `amp;order_id` (WINDCAVE's return URL
// carries a literal &amp;), else LINE Pay's own orderId (the SIPO order_id wins when both exist). Also success,
// canceled / cancelled, gateway (LINEPAY) and online_key (B05: the store), each read with the `amp;` prefix too. The
// gateways' extras (Afterpay orderToken / status, LINE Pay transactionId, Stripe kiosk's kisok, …) are kept untouched
// in `extras` and never interpreted. Stripe / PayPal cancel URLs carry `&&`: an empty pair, skipped.
//
// Every gateway's return path (P2-28; acct.paycb.GATEWAYS, and tests/payments-p228.test.mjs builds each URL as the
// backend's own builders do: OnlinePaymentController, OnlinePaymentGatewayController, PinPaymentController, each plus
// B05's online_key):
//   STRIPE / STRIPE_V2  success_url ?success=true&order_id=… · cancel_url ?canceled=true&&order_id=… (APP_TYPE=DEV
//                       servers: both add kisok=true and the cancel URL has one &). A declined card stays on Stripe
//                       Checkout. payment_update has no STRIPE_V2 branch (its 409; no store uses STRIPE_V2).
//   WINDCAVE            PxPay UrlSuccess / UrlFail with XML-escaped separators (?success=true&amp;order_id=…) plus
//                       PxPay's result / userid. A decline returns on UrlFail: canceled=true. online_key only when the
//                       key needs no encoding and the URL fits 255 bytes.
//   PIN_PAYMENT         our hosted page (pin-payment?online_order_id=…, open 5 minutes) redirects ?success=true…
//                       after its charge and ?canceled=true… after a failed 3-D Secure; a declined card stays on it.
//   AFTERPAY            redirectConfirmUrl / redirectCancelUrl plus Afterpay's orderToken / status; a decline returns on
//                       the cancel URL. payment_update captures (idempotent on the token).
//   PAYPAL              return_url / cancel_url (?canceled=true&&…) plus PayPal's token / PayerID.
//   CLICK, PAYRIX       ONE return URL (?success=true…): every outcome returns as success=true and payment_update
//                       decides (a decline or a cancel is its 409: "Payment didn't go through").
//   LINEPAY             ?success=true&gateway=LINEPAY&order_id=… / ?canceled=true&gateway=LINEPAY&… plus LINE Pay's
//                       transactionId / orderId. payment_update confirms (not idempotent: B02's lock and re-query).
//   RAZORPAY            our hosted page: its success URL after a payment, its cancel URL on a dismiss or a failure.
// A return whose URL lost the order id (a gateway that dropped the query) verifies this device's order instead
// (orderIdOf): lastOrder of this store, an online payment still awaiting it (clearCartOnSuccess, not settled), placed
// within RECENT_MS (60 min) — never for a bare /payment-callback (no success / canceled). REF reads its session
// context's order id even over the URL's; here the URL's wins.
// Navigation (navigation): REF opens every gateway page in the same window (window.location.href = payment_url), as
// checkout does here when not framed. A FRAMED checkout (a store's site embeds this SPA) opens every hosted gateway page
// in the TOP window (GATEWAYS[g].nav 'top' for all, unverified per gateway: Stripe Checkout, PayPal, Afterpay and LINE
// Pay refuse to be framed; 3-D Secure and Apple / Google Pay want a top-level page). Its return then loads this page
// top-level, in the browser's first-party storage: the framed checkout's lastOrder and cart (partitioned under the
// store's site) aren't visible here, so the payment is verified by the URL's order id alone and that cart isn't cleared
// (a follow-up, as is a "Continue to payment" button for a frame whose top navigation is blocked).
//
// Root (no hotel): forwards (replace) to /{key}/payment-callback + location.search, verbatim, so every gateway param
// survives (mode app: OO.router.paymentReturn's /{masterKey}/payment-callback?store={key}&…, as OO.App forwards first,
// P4-11). key = the URL's online_key (B05: a new tab, another browser or a WebView remembers no store), else
// OO.store.lastHotel() (as Next's app/payment-callback does with localStorage hotelKey). Neither → a card linking to
// the store finder.
// With a hotel:
//   no order id (orderIdOf)             → "We couldn't find this order".
//   canceled=true                       → "Payment cancelled": cancelled or declined, nothing charged, Try again (a
//                                         declined card returns here too: Windcave's UrlFail is the canceled=true
//                                         URL). payment_update is NOT called.
//   the order is this device's last order paid at the counter / in cash / with points (OO.store lastOrder.paymentMethod)
//                                       → "Order placed" (payment_update is skipped, as in Next).
//   else OO.api.paymentUpdate(order_id, hotel) in a retry loop (bug 7), driven by the HTTP code (R-API-7, B02):
//     200 → "Payment received".
//     202 (the order is already ACCEPTED / REJECTED / PAID) is NOT announced as success: "Taking you to your order…"
//         while OO.api.orderStatus(order_id, hotel) is read for the cart (below), then (replace) the order-status page,
//         which decides from online_order_status.
//     206 (B02: another verification of this order is running; a crashed one holds the server lock up to 180 s) and a
//         network failure → "Confirming your payment…" and payment_update again after 2 s, 4 s, 8 s, then every 10 s,
//         until another status, while the next attempt still starts within 200 s of the first (longer than the 180 s
//         server lock). Budget spent → "We're still checking your payment".
//     409 → "Payment didn't go through" (Try again → checkout) · 404 / 422 → order not found.
//     anything else (500, 502, 429, …) → "We're still checking your payment" with a link to the order. No retry: the
//         server's PROCESSING cron re-verifies the payment.
// Cross-tab: one tab per order runs the loop. It holds the localStorage lock `oo-paycb-lock-<orderId>` = { tab, until },
// renewed every 5 s, ignored once `until` has passed (20 s). Another tab shows "Confirming your payment…", re-checks
// every 2 s and on `storage` events, adopts the result the running tab writes (`oo-paycb-result-<orderId>` =
// { httpStatus, at }: 200 / 202 / 409 / 404 / 422 only, read for 10 min), and takes the loop over when the lock lapses
// without a result. A running tab that finds another tab's live lock when it renews (every 5 s, and before each
// retry) gives way to it and waits. Only a result written after a tab opened is adopted: a page opened later (a
// refresh, a second return) asks the server itself, so a stored failure never hides a payment that went through
// later (B02 makes the repeat safe: it answers 202 once paid). The tab id is kept in sessionStorage
// (`oo-paycb-tab`): it is per tab and survives a reload, so a refreshed page finds the lock it left as its own and
// runs at once instead of waiting for it to lapse. The lock also goes on pagehide (a reload, leaving the site, the
// bfcache), since an unloading page never runs its React cleanup. A duplicated tab (the browser copies sessionStorage)
// shares the id, so both tabs run: safe, B02 answers the second 206, which retries and gets 202. localStorage or
// sessionStorage that is missing or throws (private mode) means no lock: every tab just runs. Expired keys of every
// order are swept on load.
// After "Payment received" or "Order placed" it continues to /{hotel}/profile/orders/{order_id} after 2 s (replace,
// so Back doesn't return here); the "View your order" button goes there at once.
// Coupon: a declined (409) or cancelled payment releases the coupon its order claimed (lastOrder.couponTxn →
// OO.api.invalidateCoupon(txn, hotel), once via OO.store.releaseCouponTxn; failures are ignored: best effort, as in
// Next). The trailing hotel routes it to this store's adapter even when the outcome lands after the page was left:
// without it the facade would follow the route the customer is on by then (the store finder, another store).
// Cart: an ONLINE_PAYMENT checkout keeps the cart, coupon and draft (lastOrder.clearCartOnSuccess) so a declined /
// cancelled payment can "Try again". A 200 clears them via OO.store.settleOrder(order_id) (= cartClear +
// clearCheckout({ keepContact: true }), once: a refresh never clears again). A 202 clears them only when
// online_order_status shows the order went through (acct.paycb.settlesOn202: any status but PROCESSING, FAILED,
// NO_DATA or none, i.e. PENDING, ACCEPTED, REJECTED, CANCELLED, …); an unpaid (PROCESSING) order or a failed status
// read keeps the cart. Declined, cancelled, unconfirmed and still-confirming payments leave the cart as it is. These
// cart and coupon effects run even if the page was left meanwhile, and also in a tab that adopts another tab's result
// (each is once per tab; a repeated invalidate_coupon just 404s).
// At a table (Batch 2 P4-08; mode qr, where every order is a table order; REF TableSidePaymentStatusPage +
// TableSideOrderConfirmedPage, inventory D §1.1 "Payment return + confirmation"):
//   The table. REF re-attaches t_id to the return, and so does this page: the root forward adds `t_id` from REF's payment
//     context (below) when the URL has no table key and the context is this store's (and, when both name one, this
//     order's), and the store's page reads its table from the URL (OO.router.tableIdOf: t_id › table_id › tableId), else
//     REF's context for this order, else the store's live table context (OO.pages.Table.context, else OO.store.tableFor:
//     P4-01's). Every link it writes keeps the table: "Back to menu" → /{hotel}/table/{id} (the table menu's entry,
//     which checks the table again), the order page (the 2 s continue, "View your order", the 202 path) carries ?t_id=,
//     and "Try again" goes to the checkout while the live table context is this table's (its cart is that table's,
//     P4-04), else to the table menu (a checkout URL with t_id would be sent to the table menu by the router anyway). No
//     table known: the store's landing ('/{hotel}', the scan landing).
//   Copy (dine-in; no pickup or delivery words): "Payment received" — "Thanks! Your order has gone to the kitchen.";
//     "Order placed" — "Your order has gone to the kitchen. You’ll pay in person."; "We couldn't find this order" —
//     "Please ask a staff member for your order." (REF), with no "Your orders" (off at a table); "We're still checking
//     your payment" — "Your payment is being processed. If you need help, please ask a staff member.".
//   REF's payment context (so a return that lands on the Next app still works if the qr.sipo.nz flip is rolled back
//     mid-payment, BATCH2-PLAN §3 P4): before a table order's gateway redirect, checkout.jsx writes REF's keys through
//     acct.paycb.saveRefContext: sessionStorage 'sipo_payment_context' (REF's PaymentContext, every value a string) and
//     localStorage 'sipo_last_restaurant_slug' (the store key). customer_name is always '' (REF copies it into its
//     confirmation URL; this SPA never puts a contact field in a URL). The root forward reads REF's slug too (after
//     online_key and OO.store.lastHotel), so a return started on the Next app before the flip also lands. A confirmed
//     payment (200, or a 202) clears the context when it is this order's (REF clearPaymentContext on CONFIRMED).
// Wallet (P3-27; REF refreshWallet() on a confirmed payment and after a counter checkout): when the order went through
// ("Order placed", 200, or a 202 whose order settles the cart: the cart rule above) the signed-in customer's wallet is
// read again, OO.data.walletAfterOrder(hotel, order_id) (once per order; signed out: nothing), so every page showing
// points (rewards, wallet, the checkout's slider) shows what the order took. Like the cart, it runs even if the page
// was left meanwhile. A declined or cancelled payment reads nothing (the server releases the points later).
//
// acct.paycb: the callback's pure logic (no React; tests/payment-callback.test.mjs). `storage` is a localStorage-like
// object or null; every access is try/catch'd. `now` is a ms timestamp.
//   parseParams(query) → { orderId, success, canceled, gateway, onlineKey, extras }   query = OO.router's parsed query.
//       orderId / gateway are trimmed ('' when absent; gateway upper-cased), onlineKey is as given (null when absent).
//   nextStep({ httpStatus } | { network: true }) → { phase, retry, decisive }   the status table: phase 'paid' (200) |
//       'opening' (202) | 'confirming' (206, network: retry) | 'failed' (409) | 'missing' (404, 422) | 'pending'.
//       decisive = written to, and adopted from, the shared result.
//   retryDelay(n, elapsedMs) → ms | null   the wait before retry n (0-based: 2000, 4000, 8000, then 10000), or null when
//       that retry would start more than BUDGET_MS (200 s) after the first attempt.
//   tabId(session, make?) → string | null   this tab's id, kept in the sessionStorage-like `session` under TAB_KEY:
//       per tab, and a reload in the same tab gets it back. A missing or malformed one (not 1–64 of [A-Za-z0-9_-]) is
//       replaced by make() (default: a random 'tab-…' id) and stored. null when `session` is missing or throws: the
//       caller then runs without a lock.
//   acquireLock(storage, orderId, tab, now) → bool   true = this tab runs: it holds the lock now, or storage is unusable
//       (no lock, just run). false = another tab holds a live lock, or won the write race.
//   renewLock(storage, orderId, tab, now) → bool   the same claim: extends this tab's lock; false once another holds it.
//   releaseLock(storage, orderId, tab)   removes the lock if it is this tab's.
//   readResult(storage, orderId, now) → { httpStatus, at } | null   a decisive result under 10 min old (an expired or
//       malformed one is removed).   writeResult(storage, orderId, httpStatus, now)   decisive statuses only.
//   sweep(storage, now) → number removed   expired / malformed `oo-paycb-*` keys of every order.
//   settlesOn202(rawOnlineOrderStatus) → bool   reads orderstatus / orderStatus / order_status.
//   run({ orderId, tab, call, storage, now, setTimeout, clearTimeout, events, onUpdate, onFinal }) → { stop() }
//       The loop. It takes the lock and calls call() (→ Promise<{ httpStatus, body }>; a rejection with kind 'network'
//       is a network failure, any other is classified by its status) per nextStep, or waits for the running tab (a
//       2 s check, plus events' 'storage' when given: window). It adopts a result written since run() was called (when
//       waiting, and before each retry). events' 'pagehide' releases this tab's lock at once (the loop goes on: a page
//       back from the bfcache takes it again at its next renewal, or gives way to the tab that took it).
//       onUpdate({ phase: 'confirming', waiting }) when it retries, waits or takes over.
//       onFinal({ phase, httpStatus, body, network, adopted, spent, late }) at most once: when the loop ends (spent:
//       the budget ran out, phase 'pending'), or after stop() when the call then in flight settles with a non-retry
//       outcome (late: true). stop() clears every timer and listener and releases the lock. now / setTimeout /
//       clearTimeout default to the globals.
//   GATEWAYS → { [gateway]: { returns, cancel, decline, extras, confirm, nav } } (P2-28, frozen):
//       returns   'both' (a success and a cancel URL) | 'success' (one URL for every outcome)
//       cancel    the cancel URL's query up to the order id (e.g. 'canceled=true&&order_id=') | null
//       decline   where a declined payment ends: 'gateway' (the hosted page shows it, no return) | 'cancel'
//                 (canceled=true) | 'verify' (success=true, then payment_update's 409)
//       extras    what the gateway appends (parseParams' extras)
//       confirm   what payment_update does: 'read' (the provider's status) | 'capture' (AFTERPAY) | 'confirm'
//                 (LINEPAY) | 'none' (STRIPE_V2: no branch, its 409)
//       nav       'top' (see Navigation)
//   orderIdOf(params, lastOrder, hotel, now) → string ('' when none): params.orderId, else the fallback above.
//   navigation(gateway, { framed }) → 'self' | 'top': where checkout opens the gateway page. Not framed: 'self' (REF);
//       framed: the gateway's nav, 'top' for every one (an unknown gateway too).
//   P4-08, REF's payment context (see "At a table"; `session` / `local` are sessionStorage- / localStorage-like objects
//   or null; every access is try/catch'd):
//   refContext(v) → REF's PaymentContext: v = { orderId, hotel, total (a number), itemsCount, gateway, gatewayId,
//       estimatedTime, placedAt (ISO), currency, storeName, storePhone, table: { id, name, guests } } → { order_id,
//       restaurant_id (the store key), total ('12.50'), items_count, customer_name: '', order_type: 'dining',
//       payment_method: 'online', payment_gateway, payment_gateway_id, estimated_time, placed_at, currency_symbol,
//       restaurant_name, restaurant_phone, table_id, table_name, guest_count ('' unless ≥ 1) }, every value a string.
//   saveRefContext(session, local, v) → bool: writes refContext(v) to session REF_CONTEXT_KEY and v.hotel to local
//       REF_SLUG_KEY (each write on its own; true when the context was written).
//   readRefContext(session) → PaymentContext | null (an object with a string order_id or restaurant_id, else null).
//   refSlug(session, local) → string | null: REF getLastRestaurantSlug (the context's restaurant_id, else the slug).
//   refTableFor(ctx, hotel, orderId) → string | null: ctx.table_id when ctx is this store's (restaurant_id, any case)
//       and, when both name an order, this order's.
//   clearRefContext(session, orderId) → removes the context when it is that order's (or names none).
//   REF_CONTEXT_KEY ('sipo_payment_context'), REF_SLUG_KEY ('sipo_last_restaurant_slug').
//   Constants: LOCK_PREFIX, RESULT_PREFIX, TAB_KEY, LOCK_TTL_MS, LOCK_RENEW_MS, WAIT_CHECK_MS, RESULT_TTL_MS, BUDGET_MS,
//       BACKOFF_MS, RECENT_MS.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var CONTINUE_MS = 2000;
  var SKIP_UPDATE = { PAY_AT_COUNTER: 'counter', CASH: 'cash', WALLET: 'wallet' };
  var AUTO = { paid: true, placed: true };

  // ---- acct.paycb: params, the status table, backoff, the cross-tab lock and the loop (pure; see the header) ---------
  var LOCK_PREFIX = 'oo-paycb-lock-';
  var RESULT_PREFIX = 'oo-paycb-result-';
  var TAB_KEY = 'oo-paycb-tab'; // sessionStorage
  var TAB_ID = /^[A-Za-z0-9_-]{1,64}$/;
  var LOCK_TTL_MS = 20000;
  var LOCK_RENEW_MS = 5000;
  var WAIT_CHECK_MS = 2000;
  var RESULT_TTL_MS = 600000;
  var BUDGET_MS = 200000; // > config online_order.checkout.lock_seconds (180 s)
  var BACKOFF_MS = [2000, 4000, 8000];
  var STEADY_MS = 10000;
  var DECISIVE = { 200: true, 202: true, 404: true, 409: true, 422: true };
  var OWN_PARAMS = ['order_id', 'success', 'canceled', 'cancelled', 'gateway', 'online_key'];
  var NOT_THROUGH = ['PROCESSING', 'FAILED', 'NO_DATA'];
  var RECENT_MS = 3600000; // P2-28: how old this device's order may be to stand in for a return that lost its id

  // P2-28: every gateway's return path (see the header: "Every gateway's return path").
  function gw(returns, cancel, decline, extras, confirm) {
    return Object.freeze({
      returns: returns, cancel: cancel, decline: decline, extras: Object.freeze(extras), confirm: confirm, nav: 'top',
    });
  }
  var GATEWAYS = Object.freeze({
    STRIPE: gw('both', 'canceled=true&&order_id=', 'gateway', ['kisok'], 'read'),
    STRIPE_V2: gw('both', 'canceled=true&&order_id=', 'gateway', [], 'none'),
    WINDCAVE: gw('both', 'canceled=true&amp;order_id=', 'cancel', ['result', 'userid'], 'read'),
    PIN_PAYMENT: gw('both', 'canceled=true&order_id=', 'gateway', [], 'read'),
    AFTERPAY: gw('both', 'canceled=true&order_id=', 'cancel', ['orderToken', 'status'], 'capture'),
    PAYPAL: gw('both', 'canceled=true&&order_id=', 'gateway', ['token', 'PayerID'], 'read'),
    CLICK: gw('success', null, 'verify', [], 'read'),
    LINEPAY: gw('both', 'canceled=true&gateway=LINEPAY&order_id=', 'gateway', ['transactionId', 'orderId'], 'confirm'),
    PAYRIX: gw('success', null, 'verify', [], 'read'),
    RAZORPAY: gw('both', 'canceled=true&order_id=', 'cancel', [], 'read'),
  });

  function has(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function text(v) { return v === undefined || v === null ? '' : String(v).trim(); }
  function yes(v) { return text(v).toLowerCase() === 'true'; }
  function finite(v) { return typeof v === 'number' && isFinite(v); }
  function decisive(status) { return finite(status) && DECISIVE[status] === true; }

  // The raw value of the first non-blank name / `amp;`name, in the order given; '' when none.
  function first(q, names) {
    for (var i = 0; i < names.length; i++) {
      var forms = [names[i], 'amp;' + names[i]];
      for (var j = 0; j < forms.length; j++) {
        if (has(q, forms[j]) && text(q[forms[j]]) !== '') return String(q[forms[j]]);
      }
    }
    return '';
  }

  function parseParams(query) {
    var q = query && typeof query === 'object' ? query : {};
    var extras = {};
    Object.keys(q).forEach(function (k) {
      if (OWN_PARAMS.indexOf(k.indexOf('amp;') === 0 ? k.slice(4) : k) < 0) extras[k] = q[k];
    });
    return {
      orderId: text(first(q, ['order_id', 'orderId'])),
      success: yes(first(q, ['success'])),
      canceled: yes(first(q, ['canceled'])) || yes(first(q, ['cancelled'])),
      gateway: text(first(q, ['gateway'])).toUpperCase(),
      onlineKey: first(q, ['online_key']) || null,
      extras: extras,
    };
  }

  // The order to verify: the URL's, else this device's online payment of this store that still awaits it, placed within
  // RECENT_MS — only for a gateway's return (success / canceled), never a bare /payment-callback.
  function orderIdOf(params, lastOrder, hotel, now) {
    var p = params || {};
    if (p.orderId) return String(p.orderId);
    var lo = lastOrder && typeof lastOrder === 'object' ? lastOrder : null;
    if (!(p.success || p.canceled) || !lo || !text(lo.orderId) || !text(hotel)) return '';
    var age = Number(now) - Number(lo.placedAt);
    var online = String(lo.paymentMethod || '').toUpperCase() === 'ONLINE_PAYMENT';
    var awaiting = online && lo.clearCartOnSuccess === true && !lo.settledAt;
    var recent = isFinite(age) && age >= 0 && age <= RECENT_MS;
    return text(lo.hotel) === text(hotel) && awaiting && recent ? text(lo.orderId) : '';
  }

  // Where checkout opens a gateway's page (see the header: "Navigation").
  function navigation(gateway, env) {
    if (!env || !env.framed) return 'self';
    var key = text(gateway).replace(/[\s-]+/g, '_').toUpperCase();
    return has(GATEWAYS, key) ? GATEWAYS[key].nav : 'top';
  }

  function nextStep(outcome) {
    var o = outcome || {};
    var s = o.network ? 'network' : o.httpStatus;
    if (s === 'network' || s === 206) return { phase: 'confirming', retry: true, decisive: false };
    if (s === 200) return { phase: 'paid', retry: false, decisive: true };
    if (s === 202) return { phase: 'opening', retry: false, decisive: true };
    if (s === 409) return { phase: 'failed', retry: false, decisive: true };
    if (s === 404 || s === 422) return { phase: 'missing', retry: false, decisive: true };
    return { phase: 'pending', retry: false, decisive: false };
  }

  function retryDelay(n, elapsedMs) {
    var i = Math.max(0, Math.floor(Number(n)) || 0);
    var d = i < BACKOFF_MS.length ? BACKOFF_MS[i] : STEADY_MS;
    return Number(elapsedMs) + d <= BUDGET_MS ? d : null;
  }

  function parseJson(raw) {
    try {
      var v = typeof raw === 'string' ? JSON.parse(raw) : null;
      return v && typeof v === 'object' ? v : null;
    } catch (e) { return null; }
  }
  function lockOf(raw) {
    var v = parseJson(raw);
    return v && typeof v.tab === 'string' && finite(v.until) ? { tab: v.tab, until: v.until } : null;
  }
  function resultOf(raw) {
    var v = parseJson(raw);
    return v && decisive(v.httpStatus) && finite(v.at) ? { httpStatus: v.httpStatus, at: v.at } : null;
  }

  function newTabId() { return 'tab-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10); }
  // sessionStorage is per tab and survives a reload in it: a refreshed page gets its id back, so the lock it left (its
  // unloading page never ran stop()) is its own. No usable session storage → null: no stable id, so no lock.
  function tabId(session, make) {
    if (!session) return null;
    try {
      var id = session.getItem(TAB_KEY);
      if (typeof id === 'string' && TAB_ID.test(id)) return id;
      id = String((typeof make === 'function' ? make : newTabId)());
      session.setItem(TAB_KEY, id);
      return id;
    } catch (e) { return null; }
  }

  function acquireLock(storage, orderId, tab, now) {
    if (!storage) return true;
    var key = LOCK_PREFIX + orderId;
    try {
      var held = lockOf(storage.getItem(key));
      if (held && held.tab !== tab && held.until > now) return false;
    } catch (e) { return true; }
    try {
      storage.setItem(key, JSON.stringify({ tab: tab, until: now + LOCK_TTL_MS }));
      // Read back: when two tabs claim at once, the later write wins and the other tab waits.
      var back = lockOf(storage.getItem(key));
      return !back || back.tab === tab;
    } catch (e) { return true; }
  }
  function renewLock(storage, orderId, tab, now) { return acquireLock(storage, orderId, tab, now); }
  function releaseLock(storage, orderId, tab) {
    if (!storage) return;
    var key = LOCK_PREFIX + orderId;
    try {
      var held = lockOf(storage.getItem(key));
      if (held && held.tab === tab) storage.removeItem(key);
    } catch (e) { /* it lapses on its own */ }
  }

  function readResult(storage, orderId, now) {
    if (!storage) return null;
    var key = RESULT_PREFIX + orderId;
    try {
      var raw = storage.getItem(key);
      if (raw === null || raw === undefined) return null;
      var r = resultOf(raw);
      if (r && now - r.at < RESULT_TTL_MS) return r;
      storage.removeItem(key);
    } catch (e) { /* unreadable: no result */ }
    return null;
  }
  function writeResult(storage, orderId, httpStatus, now) {
    if (!storage || !decisive(httpStatus)) return;
    try { storage.setItem(RESULT_PREFIX + orderId, JSON.stringify({ httpStatus: httpStatus, at: now })); } catch (e) { /* the other tab checks for itself */ }
  }

  function sweep(storage, now) {
    var removed = 0;
    try {
      if (!storage || typeof storage.key !== 'function' || !(storage.length >= 0)) return 0;
      var keys = [];
      for (var i = 0; i < storage.length; i++) {
        var k = storage.key(i);
        if (typeof k === 'string' && (k.indexOf(LOCK_PREFIX) === 0 || k.indexOf(RESULT_PREFIX) === 0)) keys.push(k);
      }
      keys.forEach(function (k) {
        var raw = storage.getItem(k);
        var lock = k.indexOf(LOCK_PREFIX) === 0 ? lockOf(raw) : null;
        var res = k.indexOf(RESULT_PREFIX) === 0 ? resultOf(raw) : null;
        var live = lock ? lock.until > now : (res ? now - res.at < RESULT_TTL_MS : false);
        if (!live) { storage.removeItem(k); removed += 1; }
      });
    } catch (e) { /* best effort */ }
    return removed;
  }

  function settlesOn202(raw) {
    var r = raw && typeof raw === 'object' ? raw : {};
    var s = text(r.orderstatus != null ? r.orderstatus : (r.orderStatus != null ? r.orderStatus : r.order_status)).toUpperCase();
    return s !== '' && NOT_THROUGH.indexOf(s) < 0;
  }

  function outcomeOfResponse(res) {
    return { httpStatus: res && finite(res.httpStatus) ? res.httpStatus : 0, body: res && res.body !== undefined ? res.body : null, network: false };
  }
  function outcomeOfError(err) {
    if (err && err.kind === 'network') return { httpStatus: 0, body: null, network: true };
    return { httpStatus: err && finite(err.status) ? err.status : 0, body: err && err.data !== undefined ? err.data : null, network: false };
  }

  function run(opts) {
    var orderId = String(opts.orderId);
    var tab = String(opts.tab);
    var storage = opts.storage || null;
    var now = opts.now || Date.now;
    var later = opts.setTimeout || setTimeout;
    var cancel = opts.clearTimeout || clearTimeout;
    var events = opts.events || null;
    var keys = [LOCK_PREFIX + orderId, RESULT_PREFIX + orderId];
    var stopped = false, done = false, waiting = false, inflight = false, yielded = false;
    var bornAt = now();
    var startedAt = 0, retries = 0;
    var timer = { retry: null, renew: null, check: null };
    var unlisten = null, unhide = null;

    function clear(name) {
      if (timer[name] !== null) { cancel(timer[name]); timer[name] = null; }
    }
    function quiet() {
      clear('retry'); clear('renew'); clear('check');
      if (unlisten) { unlisten(); unlisten = null; }
      if (unhide) { unhide(); unhide = null; }
    }
    // The ONE way out: onFinal runs at most once, whichever path gets here first.
    function finish(outcome, extra) {
      if (done) return;
      done = true;
      quiet();
      releaseLock(storage, orderId, tab);
      opts.onFinal(Object.assign({
        phase: nextStep(outcome).phase, httpStatus: outcome.httpStatus, body: outcome.body === undefined ? null : outcome.body,
        network: !!outcome.network, adopted: false, spent: false, late: false,
      }, extra));
    }
    function progress(isWaiting) {
      if (!stopped && !done && opts.onUpdate) opts.onUpdate({ phase: 'confirming', waiting: isWaiting });
    }
    // Only a result written since this tab started: an older one (a refresh, a second return) is asked again, so a
    // stored failure can never hide a payment that went through later.
    function adoptStored() {
      var r = readResult(storage, orderId, now());
      if (!r || r.at < bornAt) return false;
      finish({ httpStatus: r.httpStatus, body: null, network: false }, { adopted: true });
      return true;
    }

    // Running: this tab holds the lock (or storage is unusable) and calls payment_update.
    function startRun() {
      var tookOver = waiting; // its card stops saying "another window"
      waiting = false;
      yielded = false;
      clear('check');
      if (unlisten) { unlisten(); unlisten = null; }
      startedAt = now();
      retries = 0;
      renewSoon();
      if (tookOver) progress(false);
      attempt();
    }
    function renewSoon() {
      clear('renew');
      timer.renew = later(function () {
        timer.renew = null;
        if (stopped || done || waiting) return;
        if (renewLock(storage, orderId, tab, now())) renewSoon();
        else giveWay();
      }, LOCK_RENEW_MS);
    }
    function attempt() {
      inflight = true;
      var p;
      try { p = Promise.resolve(opts.call()); } catch (e) { p = Promise.reject(e); }
      p.then(outcomeOfResponse, outcomeOfError).then(settle);
    }
    function settle(outcome) {
      inflight = false;
      var step = nextStep(outcome);
      // Before the lock goes: a waiting tab reads the result first, then the lock.
      if (step.decisive) writeResult(storage, orderId, outcome.httpStatus, now());
      if (done) return;
      if (stopped) {
        if (!step.retry) finish(outcome, { late: true });
        return;
      }
      if (yielded) {
        if (step.decisive) finish(outcome);
        else startWait();
        return;
      }
      if (!step.retry) { finish(outcome); return; }
      var delay = retryDelay(retries, now() - startedAt);
      if (delay === null) { finish(outcome, { phase: 'pending', spent: true }); return; }
      retries += 1;
      progress(false);
      timer.retry = later(function () {
        timer.retry = null;
        if (stopped || done || adoptStored()) return;
        // Still ours? A tab that was frozen past the lock's expiry finds the tab that took over, and waits.
        if (renewLock(storage, orderId, tab, now())) attempt();
        else giveWay();
      }, delay);
    }
    // Another tab took the lock (ours lapsed): stop calling; the call in flight, if any, still lands in settle().
    function giveWay() {
      yielded = true;
      clear('retry');
      if (!inflight) startWait();
    }

    // Waiting: another tab holds a live lock.
    function startWait() {
      waiting = true;
      clear('retry');
      clear('renew');
      progress(true);
      if (events && !unlisten) listen();
      checkSoon();
    }
    function listen() {
      var on = function (e) {
        var k = e ? e.key : null;
        if (k === null || k === undefined || keys.indexOf(k) >= 0) check();
      };
      try {
        events.addEventListener('storage', on);
        unlisten = function () {
          try { events.removeEventListener('storage', on); } catch (e) { /* gone */ }
        };
      } catch (e) { unlisten = null; }
    }
    function checkSoon() {
      clear('check');
      timer.check = later(function () { timer.check = null; check(); }, WAIT_CHECK_MS);
    }
    function check() {
      if (stopped || done || !waiting) return;
      if (adoptStored()) return;
      if (acquireLock(storage, orderId, tab, now())) startRun();
      else checkSoon();
    }

    // pagehide (a reload, leaving the site, the bfcache): an unloading page never runs stop(), so the lock goes now
    // rather than lapsing 20 s later, and the reload or another tab runs at once. The loop itself goes on: a page back
    // from the bfcache takes the lock again at its next renewal, or gives way to the tab that took it meanwhile.
    function watchHide() {
      var on = function () {
        if (!stopped && !done) releaseLock(storage, orderId, tab);
      };
      try {
        events.addEventListener('pagehide', on);
        unhide = function () {
          try { events.removeEventListener('pagehide', on); } catch (e) { /* gone */ }
        };
      } catch (e) { unhide = null; }
    }

    if (events) watchHide(); // first: a loop that ends at once (an adopted result) drops it again
    if (!adoptStored()) {
      if (acquireLock(storage, orderId, tab, now())) startRun();
      else startWait();
    }

    return {
      stop: function () {
        if (stopped) return;
        stopped = true;
        quiet();
        if (!done) releaseLock(storage, orderId, tab);
      },
    };
  }

  // ---- P4-08: REF's payment context (see the header: "At a table") ------------------------------------------------------
  var REF_CONTEXT_KEY = 'sipo_payment_context';     // sessionStorage (REF shared/checkout/utils/paymentContext.ts)
  var REF_SLUG_KEY = 'sipo_last_restaurant_slug';   // localStorage
  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function refContext(v) {
    var o = v || {};
    var t = o.table && typeof o.table === 'object' ? o.table : {};
    var total = Number(o.total);
    var guests = Math.floor(Number(t.guests));
    return {
      order_id: str(o.orderId), restaurant_id: str(o.hotel), total: isFinite(total) ? total.toFixed(2) : '0.00',
      items_count: str(o.itemsCount), customer_name: '', order_type: 'dining', payment_method: 'online',
      payment_gateway: str(o.gateway), payment_gateway_id: str(o.gatewayId), estimated_time: str(o.estimatedTime),
      placed_at: str(o.placedAt), currency_symbol: str(o.currency) || '$', restaurant_name: str(o.storeName),
      restaurant_phone: str(o.storePhone), table_id: str(t.id), table_name: str(t.name), guest_count: guests >= 1 ? String(guests) : '',
    };
  }
  function saveRefContext(session, local, v) {
    var ctx = refContext(v);
    var saved = false;
    try { if (session) { session.setItem(REF_CONTEXT_KEY, JSON.stringify(ctx)); saved = true; } } catch (e) { saved = false; }
    try { if (local && ctx.restaurant_id) local.setItem(REF_SLUG_KEY, ctx.restaurant_id); } catch (e) { /* REF logs and goes on */ }
    return saved;
  }
  function readRefContext(session) {
    try {
      var v = session ? parseJson(session.getItem(REF_CONTEXT_KEY)) : null;
      return v && (typeof v.order_id === 'string' || typeof v.restaurant_id === 'string') ? v : null;
    } catch (e) { return null; }
  }
  function refSlug(session, local) {
    var ctx = readRefContext(session);
    var s = ctx && typeof ctx.restaurant_id === 'string' ? ctx.restaurant_id.trim() : '';
    if (s) return s;
    try { var l = local ? local.getItem(REF_SLUG_KEY) : null; return typeof l === 'string' && l.trim() ? l.trim() : null; } catch (e) { return null; }
  }
  function refTableFor(ctx, hotel, orderId) {
    if (!ctx || typeof ctx !== 'object' || !text(hotel)) return null;
    if (text(ctx.restaurant_id).toLowerCase() !== text(hotel).toLowerCase()) return null;
    if (text(orderId) && text(ctx.order_id) && text(ctx.order_id) !== text(orderId)) return null;
    return text(ctx.table_id) || null;
  }
  function clearRefContext(session, orderId) {
    try {
      var ctx = readRefContext(session);
      if (ctx && (!text(ctx.order_id) || !text(orderId) || text(ctx.order_id) === text(orderId))) session.removeItem(REF_CONTEXT_KEY);
    } catch (e) { /* best effort */ }
  }

  var paycb = (acct.paycb = {
    LOCK_PREFIX: LOCK_PREFIX, RESULT_PREFIX: RESULT_PREFIX, TAB_KEY: TAB_KEY, LOCK_TTL_MS: LOCK_TTL_MS,
    LOCK_RENEW_MS: LOCK_RENEW_MS, WAIT_CHECK_MS: WAIT_CHECK_MS, RESULT_TTL_MS: RESULT_TTL_MS, BUDGET_MS: BUDGET_MS,
    BACKOFF_MS: BACKOFF_MS.slice(), RECENT_MS: RECENT_MS, GATEWAYS: GATEWAYS,
    parseParams: parseParams,
    orderIdOf: orderIdOf,
    navigation: navigation,
    nextStep: nextStep,
    retryDelay: retryDelay,
    tabId: tabId,
    acquireLock: acquireLock,
    renewLock: renewLock,
    releaseLock: releaseLock,
    readResult: readResult,
    writeResult: writeResult,
    sweep: sweep,
    settlesOn202: settlesOn202,
    run: run,
    // P4-08
    REF_CONTEXT_KEY: REF_CONTEXT_KEY,
    REF_SLUG_KEY: REF_SLUG_KEY,
    refContext: refContext,
    saveRefContext: saveRefContext,
    readRefContext: readRefContext,
    refSlug: refSlug,
    refTableFor: refTableFor,
    clearRefContext: clearRefContext,
  });

  // The getters themselves throw when site data is blocked (SecurityError), so each read is guarded.
  function storageOrNull() {
    try { return window.localStorage || null; } catch (e) { return null; }
  }
  function sessionOrNull() {
    try { return window.sessionStorage || null; } catch (e) { return null; }
  }

  function ResultCard(props) {
    var ui = OO.ui;
    var tone = props.tone || 'neutral';
    var icon = props.icon;
    return (
      <ui.Card className={'oo-paycb__card oo-paycb__card--' + tone}>
        <span className="oo-paycb__icon" aria-hidden="true">{icon === 'spinner' ? <span className="oo-paycb__spinner" /> : icon}</span>
        <div className="oo-paycb__text" role="status" aria-live="polite" aria-atomic="true">
          <h2 className="oo-paycb__title">{props.title}</h2>
          {props.body ? <p className="oo-paycb__body">{props.body}</p> : null}
        </div>
        {props.meta ? <div className="oo-paycb__meta">{props.meta}</div> : null}
        {props.actions ? <div className="oo-paycb__actions">{props.actions}</div> : null}
      </ui.Card>
    );
  }

  // ---- P4-08: the table of a return (mode qr; see the header: "At a table") --------------------------------------------
  // The root forward's query: as it came; in mode qr, plus t_id from REF's context when the URL has no table key.
  function forwardSearch(search, query, key, qr) {
    if (!qr || OO.router.tableIdOf(query)) return search;
    var t = refTableFor(readRefContext(sessionOrNull()), key, paycb.parseParams(query).orderId);
    return t ? search + (search ? '&' : '?') + 't_id=' + encodeURIComponent(t) : search;
  }
  // The store's live table context (P4-01's OO.pages.Table.context, else OO.store.tableFor: the same context), or null.
  function liveTable(hotel) {
    var T = OO.pages.Table;
    try {
      var t = T && typeof T.context === 'function' ? T.context(hotel)
        : (OO.store && typeof OO.store.tableFor === 'function' ? OO.store.tableFor(hotel) : null); // P4-INT: P4-01's store API
      if (t && t.hotel && text(t.hotel).toLowerCase() !== text(hotel).toLowerCase()) return null;
      return t && text(t.id) ? t : null;
    } catch (e) { return null; }
  }
  // The table this return belongs to: the URL's, else REF's context for this order, else the live context.
  function tableIdFor(query, hotel, orderId) {
    var id = OO.router.tableIdOf(query) || refTableFor(readRefContext(sessionOrNull()), hotel, orderId);
    if (id) return String(id);
    var t = liveTable(hotel);
    return t ? text(t.id) : null;
  }

  // ---- root: forward to the URL's store, else the remembered one ------------------------------------------------
  // P4-08 (mode qr): REF's slug after those, and the table carried on (forwardSearch).
  function RootCallback(props) {
    var ui = OO.ui;
    var qr = OO.mode === 'qr';
    var key = React.useState(function () {
      return paycb.parseParams(props.route.query).onlineKey || OO.store.lastHotel() || (qr ? refSlug(sessionOrNull(), storageOrNull()) : null);
    })[0];
    React.useLayoutEffect(function () {
      if (!key) return;
      var search = forwardSearch(window.location.search || '', props.route.query, key, qr);
      // Mode app (P4-11): the branch's chain path with store={key} (OO.App forwards first; this is the fallback), never
      // href() + search: href writes store= there, so a second '?' would break the query.
      var to = OO.mode === 'app' && typeof OO.router.paymentReturn === 'function'
        ? OO.router.paymentReturn(key, search, { fallback: true })
        : OO.router.href('payment-callback', null, { hotel: key }) + search;
      OO.router.go(to, { replace: true });
    }, [key]);
    var home = OO.router.link('find-store', null, { hotel: null });
    return (
      <acct.Page title="Payment" className="oo-acct--center oo-paycb">
        {key ? (
          <ResultCard icon="spinner" title="Taking you back to the store…" />
        ) : (
          <ResultCard tone="warn" icon={<OO.icons.Store size={26} />} title="We couldn't find your store"
            body="If you've just paid, your confirmation email has your order details."
            actions={<ui.Button block href={home.href} onClick={home.onClick}>Find your store</ui.Button>} />
        )}
        <ui.PoweredBySipo />
      </acct.Page>
    );
  }

  // ---- with a store: verify the payment ---------------------------------------------------------------------------
  function StoreCallback(props) {
    var ui = OO.ui;
    var route = props.route;
    var hotel = route.hotel;
    var params = paycb.parseParams(route.query);
    var canceled = params.canceled;
    var h = OO.useHotel(hotel, { withMenu: false });
    // Read once: checkout has just written it, and later writes must not change this page's verdict.
    var last = React.useState(function () { return OO.store.get().lastOrder; })[0];
    // P2-28: the URL's order, else (a return that lost it) this device's recent order of this store awaiting payment.
    var orderId = React.useState(function () { return paycb.orderIdOf(params, last, hotel, Date.now()); })[0];
    var method = last && String(last.orderId) === orderId ? String(last.paymentMethod || '').toUpperCase() : '';
    // P4-08 (mode qr: every order is a table order): the table this return belongs to (read once; see the header).
    var qr = OO.mode === 'qr';
    var tableId = React.useState(function () { return qr ? tableIdFor(route.query, hotel, orderId) : null; })[0];
    var tableQuery = tableId ? { t_id: tableId } : undefined;
    var skip = SKIP_UPDATE[method] || null;
    var st = React.useState(function () {
      if (!orderId) return { phase: 'missing' };
      if (canceled) return { phase: 'canceled' };
      if (skip) return { phase: 'placed', how: skip };
      return { phase: 'checking' };
    });
    var state = st[0];

    // P3-27: the order went through: the wallet is read again (see the header).
    function refreshWallet() {
      if (OO.data && typeof OO.data.walletAfterOrder === 'function') OO.data.walletAfterOrder(hotel, orderId);
    }
    // The order's claimed coupon goes back (invalidate_coupon) when its payment failed or was abandoned. The hotel goes
    // with it: a late outcome (the page was left meanwhile) must still reach this store's adapter.
    function releaseCoupon() {
      var txn = OO.store.releaseCouponTxn(orderId);
      if (txn && OO.api.invalidateCoupon) OO.api.invalidateCoupon(txn, hotel).catch(function () { /* best effort */ });
    }

    React.useEffect(function () {
      if (!orderId) { st[1]({ phase: 'missing' }); return undefined; }
      if (canceled) { releaseCoupon(); st[1]({ phase: 'canceled' }); return undefined; }
      if (skip) { st[1]({ phase: 'placed', how: skip }); refreshWallet(); return undefined; }
      var alive = true;
      // This tab's id from sessionStorage: a refresh finds its own lock instead of waiting for it to lapse. Without
      // one (sessionStorage missing or throwing), no lock at all, as with an unusable localStorage: this tab just runs.
      var tab = paycb.tabId(sessionOrNull());
      var storage = tab ? storageOrNull() : null;
      var toOrder = OO.router.href('order-status', { orderId: orderId }, { hotel: hotel, query: tableQuery });
      st[1]({ phase: 'checking' });
      paycb.sweep(storageOrNull(), Date.now());
      var loop = paycb.run({
        orderId: orderId, tab: tab || newTabId(), storage: storage, now: Date.now, events: window,
        call: function () { return OO.api.paymentUpdate(orderId, hotel); },
        onUpdate: function (u) { if (alive) st[1]({ phase: 'confirming', waiting: !!u.waiting }); },
        onFinal: function (f) {
          // Cart and coupon effects run even if the page was left meanwhile (no-ops unless it's this device's order).
          if (f.phase === 'paid') { OO.store.settleOrder(orderId); refreshWallet(); }
          if (qr && (f.phase === 'paid' || f.phase === 'opening')) clearRefContext(sessionOrNull(), orderId); // P4-08 (REF)
          if (f.phase === 'failed') releaseCoupon();
          if (f.phase === 'opening') {
            // 202: the cart goes only once online_order_status shows the order went through; the status page decides.
            OO.api.orderStatus(orderId, hotel).then(function (raw) {
              if (paycb.settlesOn202(raw)) {
                OO.store.settleOrder(orderId); refreshWallet();
                if (OO.mode === 'kiosk' && alive) st[1]({ phase: 'paid' });
                return true;
              }
              return false;
            }, function () { return false; /* unknown: the cart stays */ }).then(function (settled) {
              if (alive && !(OO.mode === 'kiosk' && settled)) OO.router.go(toOrder, { replace: true });
            });
          }
          if (!alive) return;
          if (f.phase === 'failed') st[1]({ phase: 'failed', message: f.body && typeof f.body.message === 'string' ? f.body.message : '' });
          else st[1]({ phase: f.phase });
        },
      });
      return function () { alive = false; loop.stop(); };
    }, [hotel, orderId, canceled, skip]);

    var orderLink = OO.router.link('order-status', { orderId: orderId }, { hotel: hotel, replace: true, query: tableQuery });
    React.useEffect(function () {
      if (OO.mode === 'kiosk' || !AUTO[state.phase] || !orderId) return undefined;
      var t = setTimeout(function () { OO.router.go(orderLink.href, { replace: true }); }, CONTINUE_MS);
      return function () { clearTimeout(t); };
    }, [state.phase, orderId, orderLink.href]);

    React.useEffect(function () {
      if (OO.mode === 'kiosk' && orderId && ['paid', 'placed', 'canceled', 'failed'].indexOf(state.phase) >= 0) OO.kiosk.resolved(orderId);
    }, [state.phase, orderId]);

    if (OO.mode === 'kiosk' && (state.phase === 'paid' || state.phase === 'placed')) {
      return <OO.pages.KioskSuccess hotel={hotel} orderId={orderId} paid={state.phase === 'paid'} how={state.how} />;
    }

    if (h.notFound) return <acct.Page title="Payment" className="oo-acct--center"><acct.StoreNotFound /></acct.Page>;

    var storeName = h.store ? h.store.name : 'the store';
    // P4-08: at a table, back to the table menu (its entry checks the table again); "Try again" goes to the checkout
    // while the table context is valid, else there (see the header). P4-INT: the context of THIS table (its id a
    // number, the URL's a string): another table's context has another table's cart.
    var menu = OO.router.link('menu', tableId ? { tableId: tableId } : null, { hotel: hotel });
    var live = qr && tableId ? liveTable(hotel) : null;
    var checkout = qr && tableId && !(live && text(live.id) === tableId) ? menu : OO.router.link('checkout', null, { hotel: hotel });
    var orders = OO.router.link('orders', null, { hotel: hotel });
    var viewOrder = <ui.Button block href={orderLink.href} onClick={orderLink.onClick}>View your order</ui.Button>;
    var meta = orderId ? <span>{'Order reference '}<span className="oo-paycb__ref">{orderId}</span></span> : null;
    var moving = 'Taking you to your order…';
    var check = <OO.icons.Check size={26} strokeWidth={3} />;
    var cross = <OO.icons.Close size={24} strokeWidth={2.6} />;
    var card;
    switch (state.phase) {
      case 'paid':
        card = (
          <ResultCard tone="success" icon={check} title="Payment received" meta={meta} actions={viewOrder}
            body={(qr ? 'Thanks! Your order has gone to the kitchen. ' : 'Thanks! Your order is on its way to ' + storeName + '. ') + moving} />
        );
        break;
      case 'opening':
        card = <ResultCard icon="spinner" title={moving} meta={meta} actions={viewOrder} />;
        break;
      case 'placed':
        card = (
          <ResultCard tone="success" icon={check} title="Order placed" meta={meta} actions={viewOrder}
            body={(qr ? 'Your order has gone to the kitchen. ' : '') + (state.how === 'wallet' ? 'Paid with your points. ' : state.how === 'cash' ? 'You’ll pay in cash. '
              : qr ? 'You’ll pay in person. ' : 'You’ll pay at the counter. ') + moving} />
        );
        break;
      case 'confirming':
        card = (
          <ResultCard icon="spinner" title="Confirming your payment…" meta={meta}
            body={state.waiting ? 'Another window is confirming this payment. This page will update by itself.'
              : 'This can take a few minutes. Please keep this page open.'} />
        );
        break;
      case 'pending':
        card = (
          <ResultCard tone="neutral" icon={<OO.icons.Clock size={26} />} title="We're still checking your payment" meta={meta} actions={viewOrder}
            body={qr ? 'Your payment is being processed. If you need help, please ask a staff member.'
              : "Your payment is being processed. We'll email your confirmation, and you can check your order at any time."} />
        );
        break;
      case 'failed':
        card = (
          <ResultCard tone="danger" icon={cross} title="Payment didn't go through" meta={meta}
            body={(state.message ? state.message.replace(/\.?\s*$/, '. ') : 'Your payment was declined. ') + 'Please try again, or use another card.'}
            actions={[
              <ui.Button key="a" block href={checkout.href} onClick={checkout.onClick}>Try again</ui.Button>,
              <ui.Button key="b" block variant="outline" href={menu.href} onClick={menu.onClick}>Back to menu</ui.Button>,
            ]} />
        );
        break;
      case 'canceled':
        card = (
          <ResultCard tone="warn" icon={cross} title="Payment cancelled" meta={meta}
            body="Your payment was cancelled or declined. Nothing was charged."
            actions={[
              <ui.Button key="a" block href={checkout.href} onClick={checkout.onClick}>Try again</ui.Button>,
              <ui.Button key="b" block variant="outline" href={menu.href} onClick={menu.onClick}>Back to menu</ui.Button>,
            ]} />
        );
        break;
      case 'missing':
        card = (
          <ResultCard tone="warn" icon={<OO.icons.Info size={26} />} title="We couldn't find this order" meta={meta}
            body={qr ? 'Please ask a staff member for your order.' : 'Check your email for your order confirmation, or look in your orders.'}
            actions={qr ? <ui.Button block href={menu.href} onClick={menu.onClick}>Back to menu</ui.Button> : [
              <ui.Button key="a" block href={orders.href} onClick={orders.onClick}>Your orders</ui.Button>,
              <ui.Button key="b" block variant="outline" href={menu.href} onClick={menu.onClick}>Back to menu</ui.Button>,
            ]} />
        );
        break;
      default:
        card = <ResultCard icon="spinner" title="Checking your payment…" body="This only takes a moment. Please don't close this page." meta={meta} />;
    }

    return (
      <acct.Page title="Payment" className="oo-acct--center oo-paycb">
        {card}
        <ui.PoweredBySipo />
      </acct.Page>
    );
  }

  OO.pages.PaymentCallback = function PaymentCallback(props) {
    var route = props.route;
    if (!route.hotel) return <RootCallback route={route} />;
    return <StoreCallback key={route.hotel} route={route} mode={props.mode} />;
  };
})();


// ===================== src/pages/find-store.jsx =====================
// src/pages/find-store.jsx: the store finder at the site root (page id 'find-store', '/', hotel null). Styles:
// styles/pages.css (.oo-find__*). Not in the design: a SIPO-branded landing in its system (black PageHeader, canvas,
// the light SearchField, white store cards). No store theme applies here, so it shows the default monochrome tokens.
//
// Discovery (P3-23; REF develop OnlineOrderDiscoveryPage + useRestaurantDiscovery, inventory C-Δ1): list first, no map
// (D-27). REF's states and copy, in our sentence case (FindStore.logic.TEXT):
//   search   a query of 2 characters or more (OO.model.discoveryQuery: code points, % and _ not counted): the list says
//            "Finding restaurants…" at once, and OO.api.searchStores(q) (E34) goes out DEBOUNCE_MS (320) after the
//            last keystroke. One character: "Enter at least 2 characters to search." Heading "Search results".
//   nearby   no searchable query and a known position: OO.api.nearbyStores({lat, lng}) (E33; the API's 50 km). Heading
//            "Restaurants near you". Typing a single character doesn't ask again; dropping back below 2 characters
//            does.
//   Rows: OO.model.normalizeDiscovery (REF's row rules, at most 30), with distances from the known position (search
//   rows too), nearest first. A response to an older query or position is ignored (nothing is aborted). The list keeps
//   the last rows (dimmed) while the next request runs; a failure empties it.
//   The position: in online mode the page locates on arrival (REF autoLocate; app mode waits for the button): the
//   cached fix (FindStore.geo, 15 min), else navigator.geolocation. "Use my location" clears the query and asks again.
//   A refusal, no location or a timeout shows its own message, unless a searchable query is typed (the search keeps
//   its message). With the sample data (OO.api.discoveryIsFixture(): the root's discovery calls go to the fixture
//   adapter) the fix is replaced by the fixtures' DEMO_ORIGIN, as the app's Select store does (OO.appKit.demoOrigin).
//   The count badge: "Searching" / "N results" / "0 results" / "Unavailable" / "Explore". VISIBLE (3) rows, then
//   "View all N results" / "Show fewer results". Empty states: "Unable to load restaurants" (Try again: the same
//   request again), "No restaurants found" and "Find your next meal" (Clear search / Search restaurants: clears the
//   query and focuses the field).
// Each card links to /{online_key} (the store landing in both modes). A "last visited" card (ours, not REF's) uses
// OO.store.lastHotel() and OO.data.loadHotel(key, { withMenu: false }) for its name, while nothing is typed; an unknown
// key is ignored.
//
// OO.pages.FindStore.logic (pure; the tests read it):
//   DEBOUNCE_MS 320 · VISIBLE 3 · TEXT (every string above) · locationMessage(code) → the message for 'denied' |
//   'unavailable' | 'timeout'
//   view(s) → { mode ('search' | 'nearby' | 'empty'), busy, waiting, status, heading, badge, skeleton, list, empty:
//       null | { kind: 'error' | 'none' | 'idle', title, body, action: 'retry' | 'clear' | 'focus' } } for the state
//       s = { query, origin ({lat, lng} | null), loc: { status: 'idle' | 'loading' | 'success' | 'error', error (the
//       failed locate's code) }, res: { status: 'idle' | 'loading' | 'success' | 'error', list: Discovered[], key (the
//       request it belongs to) }, message (that request's: none found / failed), key (the current request: query or
//       position; when given and res.key differs, res is an older request's, see view()) }. The status line, first
//       that applies: searching › locating (no searchable query) › one character › the failed locate's message (no
//       searchable query) › message › the default.
// OO.pages.FindStore.geo (the app's Select store reads it too, src/pages/app-stores.jsx):
//   TTL_MS 900000 (15 min) · TIMEOUT_MS 6500 · KEY 'oo-discovery-geo-v1' (sessionStorage { lat, lng, at })
//   read(nowMs?) → { lat, lng } | null   the cached fix while it is at most TTL_MS old
//   write(pos, nowMs?)                   caches a fix
//   locate() → Promise<{ lat, lng }>     navigator.geolocation once ({ enableHighAccuracy: false, timeout: TIMEOUT_MS,
//       maximumAge: TTL_MS }); the fix is cached; rejects with { code: 'denied' | 'unavailable' | 'timeout' }.
//   Storage failures (private mode, a test without sessionStorage) are ignored: no cache.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var DEBOUNCE_MS = 320;
  var VISIBLE = 3;
  var TEXT = {
    searching: 'Finding restaurants…',
    locating: 'Finding restaurants near you…',
    tooShort: 'Enter at least 2 characters to search.',
    idle: 'Search by name or area to find your next meal.',
    nearbyNone: 'No nearby ordering stores found.',
    nearbyError: 'Nearby stores could not be loaded.',
    searchNone: 'No stores match that search.',
    searchError: 'Search could not be loaded.',
    denied: 'Location is off. Search by restaurant, suburb or city.',
    unavailable: 'Your location isn’t available on this device. Search by restaurant, suburb or city.',
    timeout: 'We couldn’t find your location in time. Search by restaurant, suburb or city.',
    searchHeading: 'Search results',
    nearbyHeading: 'Restaurants near you',
    badgeBusy: 'Searching',
    badgeError: 'Unavailable',
    badgeIdle: 'Explore',
    errorTitle: 'Unable to load restaurants',
    errorBody: 'Please try searching again in a moment.',
    noneTitle: 'No restaurants found',
    noneBody: 'Try a different restaurant, suburb or city to explore more places to order.',
    idleTitle: 'Find your next meal',
    idleBody: 'Search for a restaurant or use your location to discover what’s nearby.',
    retry: 'Try again',
    clear: 'Clear search',
    focus: 'Search restaurants',
    locate: 'Use my location',
    locatingButton: 'Finding you…',
    placeholder: 'Search restaurants, suburbs or cities',
    lastVisited: 'Continue where you left off',
    showFewer: 'Show fewer results',
  };
  function results(n) { return n === 1 ? '1 result' : n + ' results'; }
  function locationMessage(code) { return TEXT[code === 'denied' || code === 'timeout' ? code : 'unavailable']; }

  function view(s) {
    var qi = OO.model.discoveryQuery(s.query);
    var mode = qi.searchable ? 'search' : (s.origin ? 'nearby' : 'empty');
    var res = s.res || { status: 'idle', list: [] };
    // The results of an older request (s.key moved on; the effect is about to run): a search or nearby list is
    // already "loading" (REF shows it at once), anything else is idle.
    var stale = s.key !== undefined && res.key !== s.key;
    var status = stale ? (mode === 'empty' ? 'idle' : 'loading') : res.status;
    var list = stale && mode === 'empty' ? [] : res.list || [];
    var busy = status === 'loading';
    var waiting = !!s.loc && s.loc.status === 'loading' && mode !== 'search';
    var has = list.length > 0;
    // A failed locate speaks while no searchable query is typed (REF: it never overwrites a search's message).
    var located = !!s.loc && s.loc.status === 'error' && !qi.searchable ? locationMessage(s.loc.error) : '';
    var message = stale ? '' : s.message;
    var empty = null;
    if (!has && !busy && !waiting) {
      if (status === 'error') empty = { kind: 'error', title: TEXT.errorTitle, body: TEXT.errorBody, action: 'retry' };
      else if (status === 'success') empty = { kind: 'none', title: TEXT.noneTitle, body: TEXT.noneBody, action: qi.q ? 'clear' : 'focus' };
      else empty = { kind: 'idle', title: TEXT.idleTitle, body: TEXT.idleBody, action: qi.q ? 'clear' : 'focus' };
    }
    return {
      mode: mode, busy: busy, waiting: waiting,
      status: busy ? TEXT.searching : waiting ? TEXT.locating : (qi.q && !qi.searchable ? TEXT.tooShort : located || message || TEXT.idle),
      heading: mode === 'search' ? TEXT.searchHeading : TEXT.nearbyHeading,
      badge: busy || waiting ? TEXT.badgeBusy : has ? results(list.length)
        : status === 'error' ? TEXT.badgeError : status === 'success' ? results(0) : TEXT.badgeIdle,
      skeleton: (busy || waiting) && !has,
      list: list,
      empty: empty,
    };
  }

  // ---- the position (FindStore.geo) ----------------------------------------------------------------------------
  var GEO_KEY = 'oo-discovery-geo-v1';
  var GEO_TTL_MS = 900000;
  var GEO_TIMEOUT_MS = 6500;
  function session() { try { return window.sessionStorage || null; } catch (e) { return null; } }
  function msOf(at) { return typeof at === 'number' && isFinite(at) ? at : Date.now(); }
  var geo = {
    TTL_MS: GEO_TTL_MS, TIMEOUT_MS: GEO_TIMEOUT_MS, KEY: GEO_KEY,
    read: function (at) {
      try {
        var s = session();
        var raw = s ? s.getItem(GEO_KEY) : null;
        var p = raw ? JSON.parse(raw) : null;
        var age = p && typeof p.at === 'number' ? msOf(at) - p.at : NaN;
        return age >= 0 && age <= GEO_TTL_MS && OO.model.validCoords(p.lat, p.lng) ? { lat: p.lat, lng: p.lng } : null;
      } catch (e) { return null; }
    },
    write: function (pos, at) {
      try {
        var s = session();
        if (s && pos && OO.model.validCoords(pos.lat, pos.lng)) s.setItem(GEO_KEY, JSON.stringify({ lat: pos.lat, lng: pos.lng, at: msOf(at) }));
      } catch (e) { /* private mode / quota: no cache */ }
    },
    locate: function () {
      return new Promise(function (resolve, reject) {
        var g = null;
        try { g = typeof navigator !== 'undefined' && navigator ? navigator.geolocation : null; } catch (e) { g = null; }
        if (!g || typeof g.getCurrentPosition !== 'function') { reject({ code: 'unavailable' }); return; }
        try {
          g.getCurrentPosition(function (p) {
            var c = p && p.coords ? p.coords : {};
            var pos = { lat: c.latitude, lng: c.longitude };
            if (!OO.model.validCoords(pos.lat, pos.lng)) { reject({ code: 'unavailable' }); return; }
            geo.write(pos);
            resolve(pos);
          }, function (err) {
            reject({ code: err && err.code === 1 ? 'denied' : (err && err.code === 3 ? 'timeout' : 'unavailable') });
          }, { enableHighAccuracy: false, timeout: GEO_TIMEOUT_MS, maximumAge: GEO_TTL_MS });
        } catch (e) { reject({ code: 'unavailable' }); }
      });
    },
  };

  // The sample data's simulated position (the root's discovery calls go to the fixture adapter), else null.
  function demoOrigin() {
    var A = OO.api;
    var sample = A && (typeof A.discoveryIsFixture === 'function' ? A.discoveryIsFixture() : A.isFixture);
    return sample && OO.data && OO.data.fixtures ? OO.data.fixtures.DEMO_ORIGIN || null : null;
  }

  function StoreCard(props) {
    var s = props.store;
    var link = OO.router.link('home', null, { hotel: s.key });
    var failed = React.useState(false);
    return (
      <a className="oo-find__store" href={link.href} onClick={link.onClick}>
        <span className="oo-find__thumb" aria-hidden="true">
          {s.image && !failed[0] ? <img src={s.image} alt="" loading="lazy" onError={function () { failed[1](true); }} /> : <OO.icons.Store size={22} />}
        </span>
        <span className="oo-find__text">
          {props.eyebrow ? <span className="oo-find__eyebrow">{props.eyebrow}</span> : null}
          <span className="oo-find__name">{s.name}</span>
          {s.address ? <span className="oo-find__addr">{s.address}</span> : null}
          {s.distance ? <span className="oo-find__dist">{s.distance + ' away'}</span> : null}
        </span>
        <OO.icons.ChevronRight size={18} className="oo-find__chev" />
      </a>
    );
  }
  // OO.api[name](arg) as a promise, whatever happens (a build without the call rejects instead of throwing).
  function ask(name, arg) {
    try { return Promise.resolve(OO.api[name](arg)); } catch (e) { return Promise.reject(e); }
  }
  // A Discovered row as the card reads it.
  function cardOf(d) { return { key: d.key, name: d.name, address: d.address, image: d.logo, distance: d.distanceLabel }; }

  OO.pages.FindStore = function FindStore() {
    var ui = OO.ui;
    var auto = OO.mode !== 'app';
    var queryState = React.useState('');
    // A cached fix is used at once; otherwise online mode starts locating (the effect below asks).
    var locState = React.useState(function () {
      var cached = auto ? geo.read() : null;
      return cached ? { status: 'success', pos: cached, error: null } : { status: auto ? 'loading' : 'idle', pos: null, error: null };
    });
    // The last request's outcome: { status, list, key (its runKey), message (none found / failed) }.
    var resState = React.useState({ status: 'idle', list: [], key: null, message: '' });
    var allState = React.useState(false);
    var nonce = React.useState(0);
    var seq = React.useRef(0);
    var locSeq = React.useRef(0);
    var alive = React.useRef(true);
    var inputRef = React.useRef(null);
    var lastKey = React.useState(function () { return OO.store.lastHotel(); })[0];
    var lastQ = OO.useAsync(function () {
      if (!lastKey) return null;
      return OO.data.loadHotel(lastKey, { withMenu: false }).then(function (r) {
        var s = r && r.store;
        return s ? { key: lastKey, name: s.name, address: s.address ? s.address.full : '', image: s.bannerUrl || null } : null;
      }, function () { return null; });
    }, [lastKey]);

    var loc = locState[0];
    var demo = demoOrigin();
    var origin = loc.pos ? demo || loc.pos : null;
    var originKey = origin ? origin.lat + ',' + origin.lng : '';
    var qi = OO.model.discoveryQuery(queryState[0]);
    var runKey = (qi.searchable ? 's:' + qi.q : 'n') + '|' + originKey + '|' + nonce[0];

    React.useEffect(function () {
      alive.current = true;
      return function () { alive.current = false; locSeq.current += 1; seq.current += 1; };
    }, []);

    function locate() {
      var my = ++locSeq.current;
      locState[1](function (l) { return { status: 'loading', pos: l.pos, error: null }; });
      geo.locate().then(function (pos) {
        if (!alive.current || my !== locSeq.current) return;
        locState[1]({ status: 'success', pos: pos, error: null });
      }, function (err) {
        if (!alive.current || my !== locSeq.current) return;
        // An earlier fix stays (REF): its rows stay listed, and the status line says why the new one failed.
        locState[1](function (l) { return { status: 'error', pos: l.pos, error: err && err.code ? err.code : 'unavailable' }; });
      });
    }
    // Online mode, no cached fix: locate on arrival (REF autoLocate).
    React.useEffect(function () { if (auto && loc.status === 'loading' && !loc.pos) locate(); }, []);

    // The request for the current query / position (runKey); an answer to an older one is ignored.
    React.useEffect(function () {
      var my = ++seq.current;
      var key = runKey;
      var setRes = resState[1];
      function settle(none, failed) {
        return [function (raw) {
          if (!alive.current || my !== seq.current) return;
          var list = OO.model.normalizeDiscovery(raw, { origin: origin });
          setRes({ status: 'success', list: list, key: key, message: list.length ? '' : none });
        }, function () {
          if (!alive.current || my !== seq.current) return;
          setRes({ status: 'error', list: [], key: key, message: failed });
        }];
      }
      function loading(r) { return { status: 'loading', list: r.list, key: key, message: '' }; }
      if (!qi.searchable && !origin) {
        // REF: a search's message goes with it (view() still says why locating failed).
        setRes({ status: 'idle', list: [], key: key, message: '' });
        return undefined;
      }
      setRes(loading);
      if (!qi.searchable) {
        var near = settle(TEXT.nearbyNone, TEXT.nearbyError);
        ask('nearbyStores', { lat: origin.lat, lng: origin.lng }).then(near[0], near[1]);
        return undefined;
      }
      var found = settle(TEXT.searchNone, TEXT.searchError);
      var q = qi.q;
      var t = setTimeout(function () { ask('searchStores', q).then(found[0], found[1]); }, DEBOUNCE_MS);
      return function () { clearTimeout(t); };
    }, [runKey]);

    function setQuery(v) { allState[1](false); queryState[1](v); }
    function clearSearch() { setQuery(''); }
    function focusField() {
      clearSearch();
      var el = inputRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    }
    function locateMe() { clearSearch(); locate(); }
    function retry() { nonce[1](function (n) { return n + 1; }); }

    var v = view({ query: queryState[0], origin: origin, loc: loc, res: resState[0], message: resState[0].message, key: runKey });
    var last = lastQ.data && !qi.q ? lastQ.data : null;
    var rows = allState[0] ? v.list : v.list.slice(0, VISIBLE);
    var locating = loc.status === 'loading';

    var body;
    if (v.skeleton) {
      body = <div aria-hidden="true" className="oo-find__list">{[0, 1, 2].map(function (i) { return <ui.Skeleton key={i} variant="block" height={80} />; })}</div>;
    } else if (v.list.length) {
      body = (
        <ul className={ui.cx('oo-find__list', v.busy && 'is-busy')} aria-label={v.heading}>
          {rows.map(function (d) { return <li key={d.key}><StoreCard store={cardOf(d)} /></li>; })}
        </ul>
      );
    } else if (v.empty) {
      var e = v.empty;
      var action = e.action === 'retry'
        ? <ui.Button size="md" onClick={retry}>{TEXT.retry}</ui.Button>
        : <ui.Button size="md" variant="outline" icon="Search" onClick={focusField}>{e.action === 'clear' ? TEXT.clear : TEXT.focus}</ui.Button>;
      body = <ui.EmptyState icon={e.kind === 'error' ? 'Info' : 'Search'} title={e.title} body={e.body} action={action} role={e.kind === 'error' ? 'alert' : undefined} />;
    }

    return (
      <acct.Page title="Find your store" className="oo-find">
        <div className="oo-find__col">
          <div className="oo-find__hero">
            <img className="oo-find__logo" src={OO.asset('online-order/fixtures/img/sipo-logo.png')} alt="SIPO Cloud POS" width="120" />
            <h2 className="oo-find__title">Order from your local favourites</h2>
            <p className="oo-find__lead">Scan the QR code at your table or use your store's link. You can also search for your store here.</p>
          </div>
          <div className="oo-find__controls">
            <ui.SearchField value={queryState[0]} onChange={setQuery} inputRef={inputRef} placeholder={TEXT.placeholder} label={TEXT.placeholder} />
            <ui.Button className="oo-find__locate" variant="outline" size="md" icon="Locate" busy={locating} onClick={locateMe}>
              {locating ? TEXT.locatingButton : TEXT.locate}
            </ui.Button>
          </div>
          <p className="oo-find__status" role="status">{v.status}</p>
          {last ? (
            <div className="oo-find__group">
              <h3 className="oo-find__h">{TEXT.lastVisited}</h3>
              <StoreCard store={last} />
            </div>
          ) : null}
          <section className="oo-find__group" aria-labelledby="oo-find-results" aria-busy={v.busy || v.waiting ? 'true' : undefined}>
            <div className="oo-find__head">
              <h2 className="oo-find__rh" id="oo-find-results">{v.heading}</h2>
              <ui.StatusPill tone="neutral">{v.badge}</ui.StatusPill>
            </div>
            {body}
            {v.list.length > VISIBLE ? (
              <ui.Button className="oo-find__more" variant="link" onClick={function () { allState[1](function (x) { return !x; }); }}>
                {allState[0] ? TEXT.showFewer : 'View all ' + results(v.list.length)}
              </ui.Button>
            ) : null}
          </section>
        </div>
      </acct.Page>
    );
  };
  OO.pages.FindStore.logic = { DEBOUNCE_MS: DEBOUNCE_MS, VISIBLE: VISIBLE, TEXT: TEXT, view: view, locationMessage: locationMessage };
  OO.pages.FindStore.geo = geo;
})();


// ===================== src/pages/not-found.jsx =====================
// src/pages/not-found.jsx: the not-found page (page id 'not-found'). It is only for URLs that name no store: a malformed
// store segment, a reserved first segment, or a path outside the mount. Unknown sub-paths redirect. OO.App also
// falls back to it for an unknown page id (then route.hotel may be set, and the page links back to that store).
// OO.pages.NotFound({ route, error, onRetry }) has three states. The error's page state is OO.data.loadErrorState
// (src/lib/store.jsx: A R-API-4), checked in this order:
//   * not live here: route.hotel is a store this host doesn't serve (OO.api.availabilityOf → 'unavailable'; OO.App
//     renders this page for every route under such a store, P1-02), or the state is 'notLive' → "This store isn't
//     available here yet" (acct.StoreNotLive).
//   * temporarily unavailable: the state is 'unavailable' (network, 408 / 425 / 429, ≥ 500, an unusable 200 = 502,
//     any failure that isn't a 404) → "This store is temporarily unavailable" + Try again = onRetry, else a full
//     reload, which reloads the store (acct.StoreUnavailable). No Try again for the facade's "Not available yet" (a
//     method this build lacks: retrying can't help).
//   * otherwise (no error, or a 404) the 404 card: "We couldn't find that page". Never for an unavailable store.
//
// This file also holds the base of OO.pages.acct, the small kit shared by the account & info pages (T3d:
// order-status, orders, profile, legal, payment-callback, find-store, not-found). Members are only read inside
// components (render time), never at a file's top level, so the concat order between these files doesn't matter.
//   acct.Page { title, subtitle, onBack, showBack, profileHref + onProfile, profileLabel, right, busy, className }
//       PageHeader (black bar) + <main> + Container; the body is a column with the design's 12px / 16px gaps.
//   acct.headerProps({ hotel, mode, back: fallbackHref | null, profile }) → PageHeader props: back = OO.router.back
//       to the in-app previous entry, else to the fallback; profile ring → /{hotel}/profile (online mode only: never in
//       app mode, nor in mode qr, POLISH-2: the account is off at a table, D-20; mode defaults to OO.mode for that).
//   acct.gate(useHotelResult, { title, header, skeleton }) → a whole page while the store isn't ready, or null once
//       it is. Pages call every hook first, then `var blocked = acct.gate(…)`. In this order: not live here (the
//       error's state 'notLive' → acct.StoreNotLive), store not found (h.notFound / state 'notFound' →
//       acct.StoreNotFound; neither has back / profile buttons, both would lead to the same store), temporarily
//       unavailable (any other load error → acct.StoreUnavailable under the page's title + header, Try again =
//       h.reload unless the state says no retry), then the loading skeleton.
//   acct.StoreNotFound    "We couldn't find this store" card with a link to the root store finder.
//   acct.StoreNotLive({ hotel, back })   "This store isn't available here yet" card. Its primary link "Order on the
//       current site" goes to OO.api.legacyUrlFor(hotel) in the same tab; no link when there is no legacy base.
//       back: an optional extra action (the page's "Go back").
//   acct.StoreUnavailable({ onRetry, back })   "This store is temporarily unavailable" card with Try again (onRetry);
//       without an onRetry function there is no Try again and the copy says to check back soon.
//   acct.icons            Phone, LogOut, Mail, Alert (24px, 2px stroke, same API as OO.icons).
//   acct.money(n, store)  OO.format.money in the store currency.
//   acct.errorText(err, fallback) → a short customer-facing message, in this order: a status ≥ 500 (whatever its
//       kind: a 500 body may carry PHP internals), kind 'network', or kind 'unavailable' with no HTTP status (the
//       facade's "Not available yet") → the generic copy (fallback, else "Something went wrong. Please try again.");
//       an error carrying a kind (OO.http / a real adapter, which map their messages: P1-04's 429 is kind
//       'unavailable' + "Too many attempts. Wait a minute and try again.") → err.message; anything else (the fixture
//       adapter) → data.message / data.error. A message is used only when it is a non-empty string under 140
//       characters and not 'HTTP <status>'; otherwise the generic copy.
// Other files add: acct.reorder / useReorder / ReorderNotice / reorderGated / reorderText / ReorderAgeGate /
// orderModsText / orderWhen (order-status.jsx) and
// acct.OrderList / OrderCard (orders.jsx).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  // ---- icons (not in the UI kit; drawn in its 24px / 2px-stroke language) ------------------------------------
  function makeIcon(name, draw) {
    function Icon(props) {
      var p = props || {};
      var size = p.size == null ? 24 : p.size;
      var attrs = {
        width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: p.color || 'currentColor',
        strokeWidth: p.strokeWidth == null ? 2 : p.strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round',
        className: p.className ? 'oo-icon ' + p.className : 'oo-icon', style: p.style, focusable: 'false',
      };
      if (p.title) { attrs.role = 'img'; attrs['aria-label'] = p.title; } else { attrs['aria-hidden'] = 'true'; }
      return React.createElement('svg', attrs, draw());
    }
    Icon.displayName = 'AcctIcon' + name;
    return Icon;
  }
  acct.icons = {
    Phone: makeIcon('Phone', function () {
      return <path d="M5.5 3.5h3l1.6 4.4-2.1 1.4a11.5 11.5 0 0 0 6.7 6.7l1.4-2.1 4.4 1.6v3a2 2 0 0 1-2.2 2A17.5 17.5 0 0 1 3.5 5.7a2 2 0 0 1 2-2.2z" />;
    }),
    LogOut: makeIcon('LogOut', function () {
      return [<path key="a" d="M9.5 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3.5" />, <path key="b" d="M15 16.5l4.5-4.5L15 7.5M19.5 12H9.5" />];
    }),
    Mail: makeIcon('Mail', function () {
      return [<rect key="a" x="3.5" y="5.5" width="17" height="13" rx="2" />, <path key="b" d="M4 7.5l8 6 8-6" />];
    }),
    Alert: makeIcon('Alert', function () {
      return [<circle key="a" cx="12" cy="12" r="9.5" />, <path key="b" d="M12 7.5v5.5M12 16.4v.01" />];
    }),
  };

  // ---- small helpers ---------------------------------------------------------------------------------------
  acct.money = function money(n, store) { return OO.format.money(n, store && store.currency); };

  // A short customer-facing message for a rejected OO.api call (the rules are in the header).
  function shownText(msg) { return typeof msg === 'string' && msg && msg.length < 140 && !/^HTTP \d+/.test(msg) ? msg : null; }
  acct.errorText = function errorText(err, fallback) {
    var generic = fallback || 'Something went wrong. Please try again.';
    if (!err) return generic;
    var kind = typeof err.kind === 'string' ? err.kind : '';
    var status = Number(err.status);
    if (status >= 500 || kind === 'network' || (kind === 'unavailable' && !(status >= 400))) return generic;
    if (kind) return shownText(err.message) || generic;
    var d = err.data;
    return shownText(d && typeof d === 'object' ? (d.message || d.error) : null) || generic;
  };

  // OO.data.loadErrorState(err) (src/lib/store.jsx), read at render time; null for no error.
  function loadState(err) {
    if (!err) return null;
    var f = OO.data && OO.data.loadErrorState;
    return typeof f === 'function' ? f(err) : { state: 'unavailable', kind: null, retry: true };
  }

  acct.headerProps = function headerProps(o) {
    o = o || {};
    var p = {};
    if (o.back) {
      var to = o.back;
      p.onBack = function () { OO.router.back(to); };
    }
    var qr = (o.mode || OO.mode) === 'qr'; // POLISH-2 (D-20): no account link at a table
    if (o.profile && o.mode !== 'app' && !qr && o.hotel) {
      var l = OO.router.link('account', null, { hotel: o.hotel }); // P3-12: the account hub
      p.profileHref = l.href;
      p.onProfile = l.onClick;
      p.profileLabel = 'Your account';
    }
    return p;
  };

  // ---- Page ------------------------------------------------------------------------------------------------
  acct.Page = function AcctPage(props) {
    var ui = OO.ui;
    return (
      <React.Fragment>
        <ui.PageHeader title={props.title} subtitle={props.subtitle} onBack={props.onBack} showBack={props.showBack} right={props.right}
          profileHref={props.profileHref} onProfile={props.onProfile} profileLabel={props.profileLabel} />
        <main className={ui.cx('oo-acct', props.className)} aria-busy={props.busy ? 'true' : undefined}>
          <ui.Container className="oo-acct__in">{props.children}</ui.Container>
        </main>
      </React.Fragment>
    );
  };

  acct.StoreNotFound = function StoreNotFound() {
    var ui = OO.ui;
    var home = OO.router.link('find-store', null, { hotel: null });
    return (
      <ui.EmptyState icon="Store" title="We couldn't find this store"
        body="The link may be out of date. Search for your store to keep ordering."
        action={<ui.Button size="md" href={home.href} onClick={home.onClick}>Find a store</ui.Button>} />
    );
  };

  acct.gate = function gate(h, o) {
    o = o || {};
    var ui = OO.ui;
    var header = o.header || {};
    var st = loadState(h.error);
    // Not live here / not found: no back / profile buttons, both would lead to the same store. notLive first: the
    // facade keeps a 404 status for it, so h.notFound is true for it too.
    if (st && st.state === 'notLive') {
      return (
        <acct.Page title="Store not available" className="oo-acct--center">
          <acct.StoreNotLive hotel={h.error.hotel || null} />
        </acct.Page>
      );
    }
    if (h.notFound || (st && st.state === 'notFound')) {
      return <acct.Page title="Store not found" className="oo-acct--center"><acct.StoreNotFound /></acct.Page>;
    }
    if (st) {
      return (
        <acct.Page title={o.title} {...header} className="oo-acct--center">
          <acct.StoreUnavailable onRetry={st.retry ? h.reload : null} />
        </acct.Page>
      );
    }
    if (h.loading || !h.store) {
      return (
        <acct.Page title={o.title} {...header} busy>
          <span className="oo-sr-only" role="status">Loading</span>
          {o.skeleton || (
            <div className="oo-acct__skel">
              <ui.Skeleton lines={2} className="oo-acct__skel-title" />
              <ui.Skeleton variant="block" count={2} />
            </div>
          )}
        </acct.Page>
      );
    }
    return null;
  };

  // ---- the not-found cards -----------------------------------------------------------------------------------
  // The not-found page's card: a faint code or icon, a title, one line of copy, then the actions (children).
  function NfCard(props) {
    var ui = OO.ui;
    var actions = React.Children.toArray(props.children);
    return (
      <ui.Card className="oo-nf__card">
        <span className="oo-nf__code" aria-hidden="true">{props.code || ui.renderIcon(props.icon, { size: 32 })}</span>
        <h2 className="oo-nf__title">{props.title}</h2>
        <p className="oo-nf__body">{props.body}</p>
        {actions.length ? <div className="oo-nf__actions">{actions}</div> : null}
      </ui.Card>
    );
  }

  acct.StoreNotLive = function StoreNotLive(props) {
    var ui = OO.ui;
    var url = OO.api && OO.api.legacyUrlFor ? OO.api.legacyUrlFor(props.hotel) : null;
    // A plain link, no router onClick and no target: the current site opens in this tab.
    return (
      <NfCard icon="Store" title="This store isn't available here yet"
        body={url ? 'You can still order from this store on its current ordering site.'
          : "This store doesn't take orders on this site yet. Please check back soon."}>
        {url ? <ui.Button href={url}>Order on the current site</ui.Button> : null}
        {props.back || null}
      </NfCard>
    );
  };

  acct.StoreUnavailable = function StoreUnavailable(props) {
    var ui = OO.ui;
    var retry = typeof props.onRetry === 'function' ? props.onRetry : null;
    return (
      <NfCard icon="Clock" title="This store is temporarily unavailable"
        body={retry ? "We couldn't reach it just now. Please try again in a moment." : 'Please check back soon.'}>
        {retry ? <ui.Button onClick={retry}>Try again</ui.Button> : null}
        {props.back || null}
      </NfCard>
    );
  };

  // ---- the not-found page ------------------------------------------------------------------------------------
  OO.pages.NotFound = function NotFound(props) {
    var route = props.route || {};
    var err = props.error;
    var ls = loadState(err);
    var ui = OO.ui;
    var st = window.history && window.history.state;
    var canBack = !!(st && typeof st.ooIdx === 'number' && st.ooIdx > 0);
    var back = canBack ? <ui.Button variant="outline" onClick={function () { window.history.back(); }}>Go back</ui.Button> : null;

    var hotel = route.hotel || (err && err.hotel) || null;
    if ((ls && ls.state === 'notLive') || (route.hotel && OO.api && OO.api.availabilityOf && OO.api.availabilityOf(route.hotel) === 'unavailable')) {
      return (
        <acct.Page title="Store not available" className="oo-acct--center oo-nf">
          <acct.StoreNotLive hotel={hotel} back={back} />
          <ui.PoweredBySipo />
        </acct.Page>
      );
    }
    if (ls && ls.state === 'unavailable') {
      var retry = ls.retry ? (props.onRetry || function () { window.location.reload(); }) : null;
      return (
        <acct.Page title="Temporarily unavailable" className="oo-acct--center oo-nf">
          <acct.StoreUnavailable onRetry={retry} back={back} />
          <ui.PoweredBySipo />
        </acct.Page>
      );
    }

    var home = OO.router.link('find-store', null, { hotel: null });
    var store = route.hotel ? OO.router.link('home', null, { hotel: route.hotel }) : null;
    var primary = store || home;
    return (
      <acct.Page title="Page not found" className="oo-acct--center oo-nf">
        <NfCard code="404" title="We couldn't find that page"
          body="The link may be broken, or the page may have moved. Let's get you back to ordering.">
          <ui.Button href={primary.href} onClick={primary.onClick}>{store ? 'Back to the store' : 'Find a store'}</ui.Button>
          {back}
        </NfCard>
        <ui.PoweredBySipo />
      </acct.Page>
    );
  };
})();


// ===================== src/pages/app-home.jsx =====================
// src/pages/app-home.jsx: the customer app Home (page id 'home'; app mode only: /{hotel} and /{hotel}/home), plus
// OO.appKit, the helpers the six app pages share (app-home, app-stores, app-rewards, app-booking, app-notifications,
// app-account). OO.appKit lives here because app-home.jsx is the first of those files in the concat; the other pages
// only read it at render time, so file order never matters. Owner: T4 (Batch 1 app pages).
// Design: docs/online-order/design/customer-app.dc.txt "01 Home" + the three white-label "T Theme" variants (logic
// at lines 431–513). Styles: styles/app-home.css (Home) and styles/app-pages.css (the shared .oo-app-* cards).
//
// Home renders store.homeModules in order (enabled ones only): slider (the store banners live now, P2-29's
// OO.pages.MenuOverlays.logic.liveBanners at the menu's order moment: auto-advance 3.5s, dots, swipe,
// pause on hover / focus, no auto-advance under prefers-reduced-motion; a slide's CTA follows K.bannerLink), loyalty
// (points, tier, progress to the next
// reward, "Scan card" → rewards), actions (Order now · Book a table (only when store.booking) · Rewards · Stores),
// reorder ("Order again": the last completed order; Reorder rebuilds its lines with OO.cart.fromOrder and goes to the cart;
// W3-fix #1: an age-restricted item asks the item sheet's age gate first, OO.pages.acct.reorderGated / ReorderAgeGate
// from order-status.jsx: confirmed, every line goes in; declined, the rest do, or a toast names what was left out),
// popular (OO.api.getPopular; tap → menu ?type=product&id=; the menu's D-07 display: a dish that can never be ordered
// here drops out, a greyed one shows its label and isn't a link; P4-02: at a table, the table group's excluded categories
// drop out too, OO.pages.Table.context). Optional Studio modules add categories (visible menu category shortcuts)
// and store (the selected branch's address, physical opening hours and directions). The header is OO.ui.AppTopBar: the greeting variant for
// the default look, the brand variant (logo + brand in the heading font) when the theme is a white-label one (a
// ?theme= preset, or a store theme with a logo).
// Landing ?orderType= (W3-fix #4; REF OrderContext readOrderTypeIntent): Home is the app's /{hotel} landing, so it
// applies the order type as the menu's landing does (OO.pages.Menu.logic.landing: the draft takes it when the cart
// would price it here) once the store is loaded, then drops it from the URL (replace; every other key and the #hash
// kept), so a refresh or Back never re-applies it. P4-10: a type it changes clears the draft's time with it (day, slot,
// chosenAt: the checkout's own rule for a type change), so the order sheet asks again with that type preselected (REF:
// an intent re-prompts, OrderContext.tsx:261-266).
// Store alert (W3-fix #7; REF's app shell + Home): <OO.pages.MenuOverlays.AlertHost> shows the store's "Special note"
// here too, once per browser session per store (the menu's own flag: whichever page comes first shows it).
//
// The chain (Batch 2 P4-17; D-03, D-30 (a); BATCH2-PLAN §3 P4 "Per-chain app"). The app is one per chain: route.chain is
// the path key and route.hotel the active branch (glue/router.jsx "Mode app"). Home reads the chain with
// OO.useChain(route.chain) (P4-16: B31; a hand-built route without `chain` reads none) for the branch names and
// distances (K.shortName / K.placeName over the chain's stores; p3-followups #35: no more related_branches), the
// master's branding and the loyalty scope.
//   No branch (route.hotel null: several stores, none chosen; OO.pages.Home.withoutBranch, so OO.App mounts Home itself
//          rather than Select store): the chain home, ChainHome: the chain master's branding (logo, name: OO.model
//          chainName; its theme, OO.useChain's) over "Choose a store", the chain's stores to pick from
//          (OO.pages.Stores.usePicker) and the CTA "Order from {short name}" above the tab bar, which saves the branch
//          and opens Home with it. No order sheet, summary row or modules until a store is chosen.
//   A branch: the header's brand (logo, brand) is the chain master's (the branch's own until the master's store is
//          in); in a chain of two or more stores the summary row says "Ordering from {short name}" (summary's branch),
//          and the top bar's store button / line still opens Select store; the loyalty card shows its scope line
//          (Loyalty.scope.text: the balance shared across the chain, or this store's only).
//
// OO.appKit.BranchLine (P4-17) is "Ordering from {branch} · Change store" for the checkout (src/pages/checkout.jsx mounts
// it above its store block): mode app, a chain of two or more stores listing the branch; nothing otherwise.
//
// Order sheet (Batch 2 P4-10; REF = the Next app, develop d298a7e: MAOS = src/mobile/ui/order/MobileAppOrderSheet.tsx,
// OC = src/shared/orders/OrderContext.tsx; inventory D appendix "Mobile app", B §2 "Selection freshness"). Home mounts
// <OO.appKit.OrderPrompt hotel store summary manualOnly /> at the top of its body, once the checkout kits it is built on are
// loaded (OO.pages.CheckoutSections time / address, OO.pages.Cart.shared; read at render time). The sheet asks the
// order type (the store's offered types, as the checkout offers them), the time (ASAP, or a later day and time: P2-23's
// picker on OO.schedule, so holidays stay listed and closed and a future-only store has no ASAP) and, for delivery, the
// address (Places and "Use my current location" on a real store, a plain field on a fixture store; the saved default
// preselected; the zone checked for COVERAGE only, as REF's sheet does: the minimum is the checkout's). Continue writes them to the checkout draft, the keys the checkout reads: orderType, day, slot,
// chosenAt and, for delivery, address / zipcode / lat / lng (zoneId stays the checkout's to sync). Until then the
// choice is the sheet's own (REF keeps its draft local, MAOS:129-136).
//   When   Home opens the dismissible sheet only from its summary action. Entering the app Menu starts ordering and
//          status() (below) decides whether details are needed. FORCED (no close) there while this store's
//          draft names no time (another store's draft counts as none), its delivery has no usable address, its type is
//          no longer offered here or has no time left, or its time is stale (CS.time.freshness, P2-23's rule: gone, ASAP
//          closed, scheduling off, or chosen 15 min ago or more). It is re-judged every minute and when the page is shown
//          again (CS.time.use's clock), so it re-prompts in Menu. Where nothing can be ordered (online ordering paused, no
//          type offered here, no time in the window), the app shows neither the sheet nor its summary row, so customers
//          can browse immediately. The online menu keeps its dismissible "Browse for later" sheet, once per store and
//          reason per page load. Otherwise it opens from the summary row only, dismissible. Never
//          at a table (D-20; REF bypasses its prompt there): neither the sheet nor the row while the route names a table
//          or this store's table context applies (atTable).
//   Summary  the row it renders when `summary`: the order ("Pickup · ASAP · 15–20 mins" and where), with Change; else
//          what is missing, with Choose / Details. It opens the sheet.
//   Home links still go where they say. Menu holds product/combo links until the required details are answered,
//   then opens the requested item; fresh choices are reused.
//
// OO.appKit:
//   query(extra) → extra plus the preview keys of the current URL, ?theme= and ?layout= (so in-app links keep the
//       preview; OO.ui.AppTabBar keeps the same two).
//   href(hotel, page, params, query) · link(hotel, page, params, query) → OO.router.link · go(hotel, …, opts)
//   back(hotel) → OO.router.back with the store's Home as the fallback.
//   bannerLink(banner, hotel) → null | { kind: 'url' (a new tab) | 'load' (a path outside the SPA: a full page load) |
//       'app' (in-app), href, onClick (app only: navigates in-app on a plain click) } (W3-fix #6): the banner's link by
//       the menu overlays' resolution (OO.pages.MenuOverlays.logic.targetOf: its raw link first, so a bare domain or
//       another host's link to this store resolves as in the banner preview); in-app pages and deep links carry the
//       preview keys (href). Without menu-overlays.jsx, the normalised link (a URL or an in-app page).
//   useSession() · useReadIds(hotel)  (re-render on store changes)
//   useLoyalty(hotel, store, enabled, scope?) → { signedIn, loading, error, data: Loyalty | null, reload }
//       scope (P4-17): { chain, master } (OO.useChain's) → OO.model.normalizeLoyalty's opts.chain / opts.master (the
//       loyalty scope, P4-16). No hotel (no branch): nothing is read.
//   useNotifications(hotel, store) → { signedIn, loading, error, data: { items, unread } | null, reload }   (no hotel:
//       nothing is read)
//   useBranches(hotel, { origin, byDistance }) → { loading, error, data: Branch[] | null, reload }
//       API order (the design) unless byDistance; distances only when there is an origin. Batch 1's related_branches
//       read: Home lists the chain since P4-17 (OO.useChain).
//   BranchLine({ route, hotel, className }) (component, P4-17: see "The chain" above).
//   demoOrigin() → fixture mode only: OO.data.fixtures.DEMO_ORIGIN, the simulated device location the fixture branch
//       distances are measured from ('1.2 km'). Batch 2 has no origin until the customer shares their location.
//   shortName(store, branches) → 'Chapel Street' (a branch's short name) | the store name (single store).
//   placeName(store, branches) → 'Chapel Street' | 'Carlton' (the suburb of a single store; the brand header line).
//   pointsText(n, word) → '160 points' | '1 point' | '4 stamps'.
//   NotFound() · ErrorCard({ title, body, onRetry }) · LoginPrompt({ hotel, title, body })
//   ListSkeleton({ count, height })
//   The order sheet (P4-10; the contracts sit with the code, "the order sheet" below):
//   orderSheet (pure; tests/app-sheet-p410.test.mjs): ready() · atTable(route, hotel, table) · types(store, fixture) ·
//       status(store, draft, o) · usable(address, real) · initial(store, st, draft, addresses) · view(store, sel, st) ·
//       addressState(store, address, o) · commit(v, address, now) · whenText(day, slot) · browseText(st, store, now) ·
//       summary(st, store, { branch }) · TEXT · COVER_SUBTOTAL
//   OrderPrompt({ hotel, store, route, summary, branch, manualOnly }) · OrderSheet({ hotel, store, st, draft, session, fixture, forced,
//       onDone, onClose, onBrowse })   (components)
//   WL-VEL (VEL-C): OrderPrompt's `bar` (the online menu's order bar, layout.orderBar; src/pages/menu.jsx mounts it):
//       a full-width button with SH.barText's words that opens the sheet, dismissible; with `bar` the prompt never forces
//       or opens the sheet by itself (the menu's one-shot on-load prompt, MenuOverlays' own OrderPrompt, is unchanged).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  // ===================================== OO.appKit =====================================
  var K = (OO.appKit = OO.appKit || {});
  var EMPTY = [];

  function themeKey() { return OO.data && OO.data.themeKey ? OO.data.themeKey() : ''; }
  function layoutKey() {
    try { var q = OO.router.current().query; return typeof q.layout === 'string' ? q.layout : ''; } catch (e) { return ''; }
  }

  K.query = function (extra) {
    var q = Object.assign({}, extra || {});
    var t = themeKey(), l = layoutKey();
    if (t && q.theme === undefined) q.theme = t;
    if (l && q.layout === undefined) q.layout = l;
    return Object.keys(q).length ? q : undefined;
  };
  K.href = function (hotel, page, params, query) {
    return OO.router.href(page, params || null, { hotel: hotel, query: K.query(query) });
  };
  K.link = function (hotel, page, params, query) {
    return OO.router.link(page, params || null, { hotel: hotel, query: K.query(query) });
  };
  K.go = function (hotel, page, params, query, opts) { OO.router.go(K.href(hotel, page, params, query), opts); };
  K.back = function (hotel) { OO.router.back(K.href(hotel, 'home')); };

  function plainClick(e) { return !e || !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }
  // The normalised link as the slider read it before (no menu-overlays.jsx): a URL, or an in-app page.
  function legacyTarget(link) {
    if (!link || typeof link !== 'object') return null;
    if (link.kind === 'url') return { kind: 'url', href: link.href };
    return link.kind === 'route' ? { kind: 'route', page: link.page, params: link.params, query: link.query } : null;
  }
  K.bannerLink = function (banner, hotel) {
    var L = OO.pages.MenuOverlays && OO.pages.MenuOverlays.logic;
    var t = L && typeof L.targetOf === 'function' ? L.targetOf(banner, hotel) : legacyTarget(banner && banner.link);
    if (!t) return null;
    if (t.kind === 'url') return t.href ? { kind: 'url', href: t.href } : null;
    if (t.kind === 'internal' && !t.inApp) return t.href ? { kind: 'load', href: t.href } : null;
    var href = t.kind === 'deep' ? (t.type === 'cart' ? K.href(hotel, 'cart') : K.href(hotel, 'menu', null, { type: t.type, id: t.id }))
      : t.kind === 'route' ? K.href(hotel, t.page, t.params, t.query) : t.href;
    if (!href) return null;
    return {
      kind: 'app', href: href,
      onClick: function (e) {
        if (!plainClick(e)) return;
        if (e && e.preventDefault) e.preventDefault();
        OO.router.go(href);
      },
    };
  };

  function selectSession(s) { return s.session; }
  K.useSession = function () { return OO.useStore(selectSession); };
  function uidOf(session) { return session && session.customer && session.customer.uid ? session.customer.uid : null; }
  K.useReadIds = function (hotel) {
    return OO.useStore(function (s) { var m = s.app && s.app.read; return (m && m[hotel]) || EMPTY; });
  };

  // The member endpoints fall back to the demo member when no uid is sent (fixture mode), so every member call is
  // gated on a session: a logged-out customer must never see someone else's points or orders. P4-17: and on a store
  // (the branch: mode app's chain home has none); scope labels the balance (P4-16's loyalty scope).
  K.useLoyalty = function (hotel, store, enabled, scope) {
    var uid = uidOf(K.useSession());
    var on = enabled !== false && !!hotel;
    var res = OO.useAsync(function () {
      return uid && on ? OO.api.getLoyalty(hotel, { uid: uid }) : Promise.resolve(null);
    }, [hotel, uid, on]);
    var chain = scope && scope.chain ? scope.chain : null;
    var master = scope && scope.master ? scope.master : null;
    var data = React.useMemo(function () {
      if (!res.data || !store) return null;
      return OO.model.normalizeLoyalty(res.data, chain ? { store: store, chain: chain, master: master } : { store: store });
    }, [res.data, store, chain, master]);
    return { signedIn: !!uid, loading: res.loading, error: res.error, data: data, reload: res.reload };
  };

  K.useNotifications = function (hotel, store) {
    var uid = uidOf(K.useSession());
    var read = K.useReadIds(hotel);
    var res = OO.useAsync(function () {
      return uid && hotel ? OO.api.getNotifications(hotel) : Promise.resolve(null);
    }, [hotel, uid]);
    var data = React.useMemo(function () {
      return res.data && store ? OO.model.normalizeNotifications(res.data, { timeZone: store.timezone, read: read }) : null;
    }, [res.data, store, read]);
    return { signedIn: !!uid, loading: res.loading, error: res.error, data: data, reload: res.reload };
  };

  K.useBranches = function (hotel, opts) {
    opts = opts || {};
    var origin = opts.origin || null;
    var byDistance = !!opts.byDistance && !!origin;
    var res = OO.useAsync(function () { return OO.api.getBranches(hotel); }, [hotel]);
    var data = React.useMemo(function () {
      if (!res.data) return null;
      var list = OO.model.normalizeBranches(res.data, { origin: origin, current: hotel });
      if (byDistance) return list;
      var order = Object.keys(res.data); // normalizeBranches sorts by distance whenever it gets an origin
      return list.slice().sort(function (a, b) { return order.indexOf(a.key) - order.indexOf(b.key); });
    }, [res.data, origin, byDistance, hotel]);
    return { loading: res.loading, error: res.error, data: data, reload: res.reload };
  };

  K.demoOrigin = function () {
    return OO.api && OO.api.isFixture && OO.data && OO.data.fixtures ? OO.data.fixtures.DEMO_ORIGIN || null : null;
  };

  function branchOf(store, branches) {
    if (!store || !branches || branches.length < 2) return null;
    return branches.filter(function (b) { return b.key === store.onlineKey; })[0] || null;
  }
  K.shortName = function (store, branches) {
    var b = branchOf(store, branches);
    return b && b.shortName ? b.shortName : (store ? store.name : '');
  };
  K.placeName = function (store, branches) {
    var b = branchOf(store, branches);
    if (b && b.shortName && b.shortName !== b.name) return b.shortName;
    var parts = String(store && store.address ? store.address.street : '').split(',');
    var suburb = parts.length > 1 ? parts[parts.length - 1].trim() : '';
    return suburb || (store && store.address && store.address.city) || (store ? store.name : '');
  };

  K.pointsText = function (n, word) {
    var w = word || 'points';
    var one = /s$/i.test(w) ? w.slice(0, -1) : w;
    return n + ' ' + (Number(n) === 1 ? one : w);
  };

  K.NotFound = function NotFound() {
    var l = OO.router.link('find-store', null, {});
    return (
      <OO.ui.EmptyState className="oo-app-state" icon="Store" title="We couldn't find this store"
        body="The link may be out of date. Find your store to keep going."
        action={<OO.ui.Button size="sm" href={l.href} onClick={l.onClick}>Find a store</OO.ui.Button>} />
    );
  };

  K.ErrorCard = function ErrorCard(props) {
    return (
      <OO.ui.EmptyState className={OO.ui.cx('oo-app-state', props.className)} icon="Info" role="alert"
        title={props.title || 'Something went wrong'} body={props.body || 'Check your connection and try again.'}
        action={props.onRetry ? <OO.ui.Button variant="outline" size="sm" onClick={props.onRetry}>Try again</OO.ui.Button> : null} />
    );
  };

  K.LoginPrompt = function LoginPrompt(props) {
    var l = K.link(props.hotel, 'profile', null, { directLogin: 'true' });
    return (
      <OO.ui.Card radius="xl" className={OO.ui.cx('oo-app-login', props.className)}>
        <div className="oo-app-login__text">
          <div className="oo-app-login__title">{props.title || 'Log in to your account'}</div>
          {props.body ? <div className="oo-app-login__body">{props.body}</div> : null}
        </div>
        <OO.ui.Button size="md" className="oo-app-login__btn" href={l.href} onClick={l.onClick}>Log in</OO.ui.Button>
      </OO.ui.Card>
    );
  };

  K.ListSkeleton = function ListSkeleton(props) {
    var n = props.count || 3;
    var out = [];
    for (var i = 0; i < n; i++) out.push(<OO.ui.Skeleton key={i} variant="block" height={props.height || 88} />);
    return <div className={OO.ui.cx('oo-app-skel', props.className)} aria-hidden="true">{out}</div>;
  };

  // P4-17: "Ordering from {branch} · Change store" (see "The chain" in the header).
  K.BranchLine = function BranchLine(props) {
    var route = props.route || {};
    var hotel = props.hotel || route.hotel || null;
    var res = OO.useChain(OO.mode === 'app' && route.chain ? route.chain : null);
    var c = res.chain;
    var b = c && hotel && c.stores.length > 1 ? OO.data.chainStore(c, hotel) : null;
    if (!b) return null;
    var change = K.link(hotel, 'stores');
    return (
      <div className={OO.ui.cx('oo-app-branch', props.className)} role="group" aria-label="Store">
        <OO.icons.Pin size={16} className="oo-app-branch__icon" />
        <span className="oo-app-branch__text">Ordering from <strong>{b.shortName || b.name}</strong></span>
        <a className="oo-app-branch__change" href={change.href} onClick={change.onClick}>Change store</a>
      </div>
    );
  };

  // ============================== the order sheet (Batch 2 P4-10; the header has the behaviour) ==============================
  // Built on P2-23's time section (OO.pages.CheckoutSections.time: picker, resolve, freshness, note, use) and the checkout's
  // delivery helpers (CheckoutSections.address: offeredTypes, deliveryState, preselect, realDeliveryEnabled; Cart.shared:
  // orderTypesOf, useDeliveryDistance), all read at call time: they load before this file in the bundle, but a test may
  // load only some pages. `now` is a Date | epoch ms, as OO.schedule takes it.
  //
  // OO.appKit.orderSheet (pure):
  //   ready() → bool: those kits are loaded. Home renders OrderPrompt only then.
  //   atTable(route, hotel, table) → bool (D-20): a table applies, so nothing is asked: the route names one
  //       (params.tableId, ?table_id, REF's ?t_id / ?tableId) or `table` (OO.store.table()) is this hotel's.
  //   types(store, fixture) → ['PICKUP' | 'DELIVERY']: the order types the sheet offers, the checkout's (Cart.shared
  //       orderTypesOf, then CheckoutSections.address.offeredTypes: no DELIVERY on a real store without the Maps key); []
  //       when none is left (a delivery-only real store without the key: the checkout's typeBlocked).
  //   status(store, draft, { hotel, now, fixture, types, pickers }) → St | null (no store, or the kits missing). types /
  //       pickers: computed when not given. St = { need, reason, types, pickers ({ [type]: CS.time.picker(store, type,
  //       now) }), mine (the draft when it is this hotel's: no stamp or this hotel's, as the menu's useDisplay reads it;
  //       else null), type (the type the sheet starts on: the draft's while offered here with a time, else the first
  //       type with a time), expiresAt (chosenAt + 15 min | null) }. need / reason, the first that applies:
  //         'browse'  'paused' (online ordering off: store.online.active false) · 'blocked' (no type offered here) ·
  //                   'noTimes' (no offered type has a time in the window)
  //         'answer'  'unanswered' (the draft names no time: none chosen yet, or another store's draft)
  //         'review'  'typeGone' (its type isn't offered here, or has no time left; REF order_type_unavailable) · then
  //                   CS.time.freshness's: 'asapGone' · 'schedulingOff' · 'gone' · 'expired' (15 min old or more)
  //         'answer'  'address' (a delivery whose address isn't usable)
  //         'fresh'   null
  //   usable(address, real) → bool: a line and, for a real store, the Places pick's coordinates (OO.delivery.pointOf;
  //       the checkout's rule: B10 needs them). A fixture store's line is enough (it is measured at distance 0).
  //   initial(store, st, draft, addresses) → Sel, the sheet's own choice when it opens = { type, mode ('asap' | 'later'),
  //       day (a Day key | null), slot (a value, 'ASAP' | null), address { address, zipcode, lat, lng } }. type = st.type;
  //       the time = CS.time.resolve's for the draft when it names that type (the chosen time while offered, else its
  //       day's first time, else the default), else the default (ASAP while offered, else the first later time). The
  //       address = the draft's (kept across stores, as the checkout keeps it); an empty one takes the saved default when
  //       delivery is offered (CS.address.preselect over the session customer's addresses: one with coordinates; P3-11).
  //   view(store, sel, st) → V, what the sheet shows and Continue commits (sel judged against now's times) = { type,
  //       picker, asap { ok, slot, label }, later { ok, days (every window day as a later Day: its slots without ASAP,
  //       pickable when it has one; the resolved day in place) }, mode ('asap' | 'later' | null: no time for this type),
  //       day, slot (the order's Day and slot | null), note (CS.time.note: why ASAP isn't offered | null), closed (the
  //       window's holidays, their Day texts) }. A mode without a time falls back to the other; a later time no longer
  //       offered falls back to its day's first later time, else the first later day's (a time CS.time.resolve still
  //       finds off today's moving grid stays).
  //   addressState(store, address, { real, search, dist, cur }) → { block, live, checking, retry, searchDown, ok }: the
  //       address part, by CS.address.deliveryState on a COVERAGE quote (OO.delivery.selectZone at COVER_SUBTOTAL, REF's
  //       MAX_SAFE_INTEGER, MAOS:396: does a zone cover the distance). A real store needs the pick and its driving distance
  //       (dist: Cart.shared.useDeliveryDistance's; search: the address search's trouble); a fixture store a line
  //       (distance 0). block: what stops Continue (TEXT.enterAddress for an empty fixture field, else deliveryState's);
  //       ok: Continue may go.
  //   commit(v, address, now) → the draft patch, null without a slot: { orderType, day (the Day's key, else the slot's
  //       owner), slot (its value, 'ASAP' for ASAP), chosenAt (now, ms) } as CS.time's confirm() writes it, plus for
  //       DELIVERY { address, zipcode, lat, lng } as the checkout's Places pick writes them (lat / lng null for a typed
  //       line). zoneId stays the checkout's to sync.
  //   whenText(day, slot) → 'ASAP · 15–20 mins' | 'Tomorrow, 6:30pm' | 'Sat 3, 12:30am' (a past-midnight time names its
  //       own day) | ''.
  //   browseText(st, store, now) → { text, reason } | null: the "Browse for later" sheet's lines ('paused': the store's
  //       online.reason; 'noTimes': its opening label when closed, 'Opens tomorrow 11am').
  //   summary(st, store, { branch }) → { title, sub, action } | null: the summary row's words. branch (P4-17: the active
  //       branch's short name, in a chain of two or more stores): the sub-line says "Ordering from {branch}" (a delivery:
  //       'To {address} · from {branch}') while ordering is possible; the browse-only lines stay.
  //   TEXT (the copy) · COVER_SUBTOTAL.
  // <OO.appKit.OrderPrompt hotel store route summary branch /> (component; render it only while ready(), keyed per
  //   store): the summary row (with `summary`, its sub-line naming `branch`) and, while it is open (the header's "When"),
  //   <OrderSheet>; nothing at a table
  //   (atTable over `route`, else OO.router.current(), and OO.store.table(), read per render).
  // <OO.appKit.OrderSheet hotel store st draft session fixture forced onDone(patch) onClose() onBrowse() /> (component):
  //   the sheet (OO.ui.Sheet, dialog variant) for St `st`. forced: no close button, and ESC / the scrim do nothing.
  //   onDone gets commit()'s patch (the caller writes it); onBrowse: "Browse the menu".
  var SH = (K.orderSheet = K.orderSheet || {});
  var COVER_SUBTOTAL = 9007199254740991; // REF's Number.MAX_SAFE_INTEGER (Infinity would not do: OO.delivery's round2 reads it as 0)
  var TYPE_WORD = { PICKUP: 'Pickup', DELIVERY: 'Delivery' };
  var TEXT = {
    title: 'How would you like it?',
    intro: 'Choose your service and when you want your order.',
    // REF's review lines (MAOS getReviewMessage) by reason; 15 = CS.time.FRESH_MS (REF ORDER_SELECTION_FRESHNESS_MS)
    review: {
      typeGone: 'That service is no longer available. Choose an available option.',
      asapGone: 'ASAP has closed for this service. Pick a scheduled time.',
      schedulingOff: 'That scheduled slot is no longer available. Pick a new time.',
      gone: 'That scheduled slot is no longer available. Pick a new time.',
      expired: 'Please confirm your order details again. Choices refresh every 15 minutes.',
    },
    browseTitle: 'Browse for later',
    browse: {
      paused: 'Ordering is paused right now. You can still browse the menu and plan for later.',
      noTimes: 'We aren’t taking orders right now. You can still browse the menu.',
      blocked: 'This store only delivers, and delivery isn’t available online here yet. You can still browse the menu.',
    },
    browseLine: { paused: 'Ordering is paused right now', noTimes: 'Not taking orders right now', blocked: 'Delivery isn’t available online here yet' },
    proceed: 'Continue',
    update: 'Update order details', // = CS.time.REVIEW.button (REF MAOS:796)
    browseMenu: 'Browse the menu',
    typeLabel: 'Order type',
    whenLabel: 'When',
    asap: 'ASAP',
    later: 'Later',
    laterCaption: 'Choose a time',
    notAvailable: 'Not available',
    noTimes: 'No times',
    noTypeTimes: function (type) { return 'No ' + (type === 'DELIVERY' ? 'delivery' : 'pickup') + ' times are available right now. Please check back later.'; },
    addressLabel: 'Delivery address',
    enterAddress: 'Enter your delivery address to continue.',
    pickHint: 'Choose your address from the suggestions.', // = checkout-address.jsx's
    locate: 'Use my current location',
    locateFailed: 'We couldn’t find your location. Please type your address.',
    checking: 'Checking delivery to your address…',
    covered: 'We deliver to this address.',
    tryAgain: 'Try again.',
    orderingFrom: function (branch) { return 'Ordering from ' + branch; }, // P4-17: the summary row in a chain
    barChoose: 'Choose a time', // WL-VEL: the order bar before a time is chosen
  };
  SH.TEXT = TEXT;
  SH.COVER_SUBTOTAL = COVER_SUBTOTAL;

  function sheetKits() {
    var CS = OO.pages.CheckoutSections, C = OO.pages.Cart && OO.pages.Cart.shared;
    var ok = !!CS && !!CS.time && !!CS.address && !!C && typeof CS.time.use === 'function' && typeof C.useDeliveryDistance === 'function';
    return ok ? { CS: CS, C: C } : null;
  }
  function txt(v) { return v === undefined || v === null ? '' : String(v); }
  function byKeyOf(list, key, value) {
    if (value === undefined || value === null || value === '') return null;
    return (list || []).filter(function (x) { return String(x[key]) === String(value); })[0] || null;
  }
  // The draft when it is this hotel's (no stamp: it adopts the store, as OO.store does; the menu's useDisplay rule).
  function draftFor(draft, hotel) { return draft && (!draft.hotel || txt(draft.hotel) === txt(hotel)) ? draft : null; }
  // A picker Day as the later mode lists it: its scheduled times only (ASAP is the other mode).
  function laterOf(d) {
    var slots = d.slots.filter(function (s) { return !s.asap; });
    return Object.assign({}, d, { slots: slots, pickable: slots.length > 0 });
  }

  SH.ready = function () { return !!sheetKits(); };

  // D-20 (REF bypasses its order prompt at a table: OC bypassOrderPrompt = isTableSide || isKiosk): a table applies when
  // the route names one (/{hotel}/table/{id}, ?table_id, REF's ?t_id / ?tableId) or OO.store's table context is this
  // store's (as OO.router.tableGate reads it).
  var TABLE_KEYS = ['table_id', 't_id', 'tableId'];
  SH.atTable = function (route, hotel, table) {
    var r = route || {}, q = r.query || {}, p = r.params || {};
    if (txt(p.tableId).trim()) return true;
    for (var i = 0; i < TABLE_KEYS.length; i++) if (txt(q[TABLE_KEYS[i]]).trim()) return true;
    return !!table && txt(table.hotel) === txt(hotel) && txt(table.id).trim() !== '';
  };

  SH.types = function (store, fixture) {
    var k = sheetKits();
    return k && store ? k.CS.address.offeredTypes(k.C.orderTypesOf(store), !!fixture) : [];
  };

  SH.usable = function (a, real) {
    if (!a || !txt(a.address).trim()) return false;
    return !real || !!OO.delivery.pointOf({ lat: a.lat, lng: a.lng });
  };

  SH.status = function (store, draft, o) {
    o = o || {};
    var k = sheetKits();
    if (!k || !store) return null;
    var T = k.CS.time;
    var now = o.now === undefined || o.now === null ? OO.data.now() : o.now;
    var list = o.types || SH.types(store, o.fixture);
    var pickers = {};
    list.forEach(function (t) { pickers[t] = (o.pickers && o.pickers[t]) || T.picker(store, t, now); });
    var mine = draftFor(draft, o.hotel);
    var timed = list.filter(function (t) { return !!pickers[t].first; });
    var own = mine && list.indexOf(mine.orderType) >= 0 ? mine.orderType : null;
    var out = {
      need: 'fresh', reason: null, types: list, pickers: pickers, mine: mine, expiresAt: null,
      type: own && pickers[own].first ? own : (timed[0] || own || list[0] || null),
    };
    function is(need, reason) { out.need = need; out.reason = reason; return out; }
    if (store.online && store.online.active === false) return is('browse', 'paused');
    if (!list.length) return is('browse', 'blocked');
    if (!timed.length) return is('browse', 'noTimes');
    if (!mine || !txt(mine.slot)) return is('answer', 'unanswered');
    if (!own || !pickers[own].first) return is('review', 'typeGone');
    var fr = T.freshness(pickers[own], store, mine, { now: now });
    out.expiresAt = fr.expiresAt;
    if (fr.reason) return is('review', fr.reason);
    if (own === 'DELIVERY' && !SH.usable(mine, !o.fixture)) return is('answer', 'address');
    return out;
  };

  SH.initial = function (store, st, draft, addresses) {
    var k = sheetKits();
    var type = st.type;
    var p = type ? st.pickers[type] : null;
    var mine = st.mine && st.mine.orderType === type ? st.mine : null;
    var r = p && p.first ? k.CS.time.resolve(p, store, mine) : { day: null, slot: null };
    var d = draft || {};
    var address = { address: txt(d.address), zipcode: txt(d.zipcode), lat: d.lat === undefined ? null : d.lat, lng: d.lng === undefined ? null : d.lng };
    if (st.types.indexOf('DELIVERY') >= 0 && !address.address.trim() && !OO.delivery.pointOf(address)) {
      var seed = k.CS.address.preselect({ address: '', lat: null, lng: null }, addresses);
      if (seed) address = { address: txt(seed.address), zipcode: txt(seed.zipcode), lat: seed.lat, lng: seed.lng };
    }
    return {
      type: type, mode: r.slot && !r.slot.asap ? 'later' : 'asap', day: r.day ? r.day.key : null, slot: r.slot ? r.slot.value : null,
      address: address,
    };
  };

  SH.view = function (store, sel, st) {
    var k = sheetKits();
    var T = k.CS.time;
    // A type no longer offered (the store was re-read while the sheet was open): the one the status starts on.
    var type = sel && st.types.indexOf(sel.type) >= 0 ? sel.type : st.type;
    var p = type ? st.pickers[type] || null : null;
    var out = {
      type: type, picker: p, asap: { ok: false, slot: null, label: null }, later: { ok: false, days: [] },
      mode: null, day: null, slot: null, note: null, closed: [],
    };
    if (!p) return out;
    var asap = p.asap.ok && p.asap.slot ? p.asap.slot : null;
    var days = p.list.map(laterOf);
    var open = days.filter(function (d) { return d.pickable; });
    out.asap = { ok: !!asap, slot: asap, label: p.asapLabel };
    out.mode = sel.mode === 'later' ? (open.length ? 'later' : asap ? 'asap' : null) : (asap ? 'asap' : open.length ? 'later' : null);
    if (out.mode === 'asap') {
      out.slot = asap;
      out.day = byKeyOf(p.days, 'key', asap.owner) || p.days[0] || null;
    } else if (out.mode === 'later') {
      var want = txt(sel.slot);
      var r = want && want !== 'ASAP' ? T.resolve(p, store, { day: sel.day, slot: want }) : null;
      if (r && r.found && r.slot && !r.slot.asap) {
        // Still offered (off today's moving grid too: resolve lists it in a copy of its day).
        out.day = laterOf(r.day);
        out.slot = r.slot;
        days = days.map(function (d) { return d.key === out.day.key ? out.day : d; });
      } else {
        out.day = byKeyOf(open, 'key', sel.day) || open[0];
        out.slot = out.day.slots[0];
      }
    }
    out.later = { ok: open.length > 0, days: days };
    out.note = T.note(p, OO.model.openStatus(store, p.at, type), store);
    out.closed = days.filter(function (d) { return d.holiday; }).map(function (d) { return d.text; });
    return out;
  };

  SH.addressState = function (store, a, o) {
    o = o || {};
    var k = sheetKits();
    var D = OO.delivery;
    var line = txt(a && a.address).trim();
    var point = a ? D.pointOf({ lat: a.lat, lng: a.lng }) : null;
    var zones = store ? store.deliveryZones : [];
    var ds;
    if (!o.real) {
      if (!line) return { block: TEXT.enterAddress, live: false, checking: false, retry: null, searchDown: false, ok: false };
      ds = k.CS.address.deliveryState({ real: false, point: point, quote: D.selectZone(zones, 0, COVER_SUBTOTAL), cur: o.cur });
      return Object.assign({}, ds, { ok: !ds.block });
    }
    var dist = o.dist || { status: 'off' };
    var km = point && dist.status === 'ok' && typeof dist.km === 'number' ? dist.km : null;
    var quote = km === null ? null : D.selectZone(zones, km, COVER_SUBTOTAL);
    ds = k.CS.address.deliveryState({ real: true, point: point, search: o.search || null, dist: dist, quote: quote, cur: o.cur });
    return Object.assign({}, ds, { ok: !ds.block && !ds.checking && !!line && km !== null });
  };

  SH.commit = function (v, a, now) {
    if (!v || !v.slot) return null;
    var at = now instanceof Date ? now.getTime() : Number(now);
    var patch = { orderType: v.type, day: v.day ? v.day.key : v.slot.owner || null, slot: v.slot.value, chosenAt: at };
    if (v.type === 'DELIVERY') {
      var p = OO.delivery.pointOf({ lat: a && a.lat, lng: a && a.lng });
      patch.address = txt(a && a.address).trim();
      patch.zipcode = txt(a && a.zipcode).trim();
      patch.lat = p ? p.lat : null;
      patch.lng = p ? p.lng : null;
    }
    return patch;
  };

  // Review the choice visible in the sheet after the customer edits it. The persisted draft is only replaced on
  // Continue; its old warning should not describe the valid replacement meanwhile.
  SH.reviewReason = function (v, sel, st) {
    if (sel.timeEdited) {
      if (sel.mode === 'later' && sel.slot && (!v.slot || v.slot.value !== sel.slot)) return 'gone';
      return null;
    }
    return st.need === 'review' ? st.reason : null;
  };

  SH.whenText = function (day, slot) {
    if (!slot) return '';
    if (slot.asap) return TEXT.asap + ' · ' + slot.label;
    if (slot.dayLabel) return slot.dayLabel + ', ' + OO.format.time12(slot.time);
    return (day && day.label ? day.label : 'Today') + ', ' + slot.label;
  };

  SH.browseText = function (st, store, now) {
    if (!st || st.need !== 'browse' || !store) return null;
    var reason = null;
    if (st.reason === 'paused') reason = store.online && store.online.reason ? String(store.online.reason) : null;
    if (st.reason === 'noTimes') {
      var open = OO.model.openStatus(store, now === undefined || now === null ? OO.data.now() : now);
      reason = !open.isOpen && open.label && open.label !== 'Closed' ? open.label : null;
    }
    return { text: TEXT.browse[st.reason], reason: reason };
  };

  SH.summary = function (st, store, opts) {
    if (!st || !store) return null;
    var branch = opts && opts.branch ? txt(opts.branch).trim() : ''; // P4-17: the active branch in a chain
    if (st.need === 'browse') return { title: TEXT.browseTitle, sub: TEXT.browseLine[st.reason], action: 'Details' };
    if (st.need !== 'fresh') {
      return { title: TEXT.title, sub: branch ? TEXT.orderingFrom(branch) : TEXT.intro, action: 'Choose' };
    }
    var mine = st.mine, type = mine.orderType;
    var r = sheetKits().CS.time.resolve(st.pickers[type], store, mine);
    var where = type === 'DELIVERY' ? 'To ' + txt(mine.address).trim() + (branch ? ' · from ' + branch : '')
      : branch ? TEXT.orderingFrom(branch) : 'From ' + store.name;
    return { title: TYPE_WORD[type] + ' · ' + SH.whenText(r.day, r.slot), sub: where, action: 'Change' };
  };

  // WL-VEL (VEL-C): the online menu's order bar (layout.orderBar): { text, action } | null. A chosen order (fresh):
  // 'Pickup at {store} · {whenText}' ('Delivery to {address} · …'); none yet, or one to review: '{type} at {store} ·
  // ASAP' while the sheet's picker offers ASAP for that type now (VEL-FIX), else '… · Choose a time' (the type the sheet
  // would start on); browse-only: the browse line. action: 'Change' | 'Details'.
  SH.barText = function (st, store) {
    if (!st || !store) return null;
    if (st.need === 'browse') return { text: TEXT.browseLine[st.reason] || TEXT.browseTitle, action: 'Details' };
    var name = txt(store.name).trim();
    if (st.need !== 'fresh' || !st.mine) {
      var picker = st.pickers && st.pickers[st.type];
      var when = picker && picker.asap && picker.asap.ok === true ? TEXT.asap : TEXT.barChoose;
      return { text: (TYPE_WORD[st.type] || TYPE_WORD.PICKUP) + ' at ' + name + ' · ' + when, action: 'Change' };
    }
    var type = st.mine.orderType;
    var r = sheetKits().CS.time.resolve(st.pickers[type], store, st.mine);
    var when = SH.whenText(r.day, r.slot);
    var at = type === 'DELIVERY' && txt(st.mine.address).trim() ? 'Delivery to ' + txt(st.mine.address).trim() : (TYPE_WORD[type] || TYPE_WORD.PICKUP) + ' at ' + name;
    return { text: at + (when ? ' · ' + when : ''), action: 'Change' };
  };

  // Google disables the address field when it rejects the Maps key (gm_authFailure) and stamps its own placeholder and
  // icon on it; React never undoes that. Undo it, so the customer can still type (as checkout-address.jsx does).
  function reenableField(el) {
    if (!el) return;
    try {
      el.disabled = false;
      el.removeAttribute('disabled');
      if (el.classList) el.classList.remove('gm-err-autocomplete');
      if (el.style) el.style.backgroundImage = '';
      el.setAttribute('placeholder', TEXT.addressLabel);
    } catch (e) { /* a detached field: nothing to undo */ }
  }
  // A Places pick or the device's location (OO.delivery Address) as the sheet's address.
  function pickedAddress(a) {
    var p = a || {};
    return {
      address: txt(p.line).trim(), zipcode: txt(p.postcode).trim(),
      lat: typeof p.lat === 'number' ? p.lat : null, lng: typeof p.lng === 'number' ? p.lng : null,
    };
  }
  // A later Day as a DateStrip tile.
  function dayTile(d) {
    var f = OO.format;
    return {
      value: d.key, dow: d.label === 'Today' ? 'Today' : f.weekdayShort(f.weekdayOf(d.ymd)), day: Number(String(d.ymd).slice(8, 10)),
      label: d.text || d.label, disabled: !d.pickable,
    };
  }
  // A block that ends in "Try again." without it (the page draws that phrase as the link), as the checkout does.
  function retryLead(text) {
    var s = txt(text);
    return s.slice(-TEXT.tryAgain.length) === TEXT.tryAgain ? s.slice(0, -TEXT.tryAgain.length).trim() : s;
  }
  function noop() {}

  // Presentation only. Old documents have no orderPrompt and keep the original sheet exactly.
  SH.appearance = function (store) {
    var p = store.whitelabel && store.whitelabel.orderPrompt;
    if (!p) return null;
    var theme = store.theme || {}, brand = theme.brand || store.name;
    return { layout: p.layout || 'classic', timeStyle: p.timeStyle || 'tiles', showBrand: p.showBrand === true,
      title: p.title || TEXT.title, intro: p.intro === undefined ? TEXT.intro : p.intro, image: p.image || null,
      brand: brand, branch: store.name !== brand ? store.name : null,
      logo: theme.logoLight || theme.logoMark || theme.logo || store.logoUrl || null };
  };

  function OrderPromptBrand(props) {
    var p = props.appearance;
    return <div className="oo-osheet__brand">
      {p.image ? <img className="oo-osheet__brand-image" src={p.image} alt="" /> : null}
      <div className="oo-osheet__identity">
        <span className="oo-osheet__brand-logo">{p.logo ? <img src={p.logo} alt="" /> : p.brand.slice(0, 1)}</span>
        <div className="oo-osheet__brand-copy"><strong>{p.brand}</strong><span>{p.branch || 'Online ordering'}</span></div>
      </div>
    </div>;
  }

  K.OrderSheet = function OrderSheet(props) {
    var k = sheetKits();
    var ui = OO.ui;
    var cx = ui.cx;
    var store = props.store, st = props.st;
    var appearance = SH.appearance(store);
    var websiteTimeStyle = k.CS.time.websiteSlotDisplay(store);
    var timeStyle = websiteTimeStyle || (appearance && appearance.timeStyle === 'select' ? 'dropdown' : 'slots');
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign((props.route || OO.router.current() || {}).chain || props.hotel);
    var customer = props.session && props.session.customer;
    var uid = React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var selS = React.useState(function () { return SH.initial(store, st, props.draft, customer ? customer.addresses : null); });
    var sel = selS[0];
    function setAddress(a) { selS[1](function (s) { return Object.assign({}, s, { address: a }); }); }
    var v = SH.view(store, sel, st);
    var delivery = v.type === 'DELIVERY';
    var real = !props.fixture;
    var places = delivery && real && k.CS.address.realDeliveryEnabled();

    // The address search (P1-21 / P3-11, as the address book's sheet runs it): the field's element is kept through a
    // callback ref, so the Places autocomplete attaches when the field mounts and detaches when it goes.
    var fieldS = React.useState(null);
    var searchS = React.useState(null);   // the search's trouble: null | { kind: 'noGeometry' | 'locate' | 'auth' | 'disabled' | 'unavailable', text }
    var attachS = React.useState(0);      // "Try again" after the search failed to load
    var locatingS = React.useState(false);
    var aliveRef = React.useRef(true);
    React.useEffect(function () { aliveRef.current = true; return function () { aliveRef.current = false; }; }, []);
    var fieldRef = React.useCallback(function (el) { fieldS[1](function (cur) { return cur === el ? cur : el; }); }, []);
    var country = places ? OO.delivery.country(store) : '';
    React.useEffect(function () {
      var el = fieldS[0];
      if (!places || !el) return undefined;
      return OO.delivery.attach(el, {
        country: country,
        onPlace: function (a) { if (!aliveRef.current) return; searchS[1](null); setAddress(pickedAddress(a)); },
        onError: function (err) {
          if (!aliveRef.current) return;
          var e = err || {};
          if (e.code === 'noGeometry') { searchS[1]({ kind: 'noGeometry', text: TEXT.pickHint }); return; }
          if (e.auth) reenableField(el);
          searchS[1]({ kind: e.auth ? 'auth' : (e.kind === 'disabled' ? 'disabled' : 'unavailable'), text: null });
        },
      });
    }, [places, country, fieldS[0], attachS[0]]);
    // The driving distance to the pick (Cart.shared's hook: its memo is the cart's and the checkout's, so they never ask again).
    var dist = k.C.useDeliveryDistance(store, sel.address, places);
    var as = delivery ? SH.addressState(store, sel.address, { real: real, search: searchS[0], dist: dist, cur: store.currency }) : null;

    // Typing changes the address: a pick's postcode and coordinates no longer describe it (the checkout's rule).
    function typeAddress(value) {
      setAddress({ address: value, zipcode: '', lat: null, lng: null });
      var n = searchS[0];
      if (n && (n.kind === 'noGeometry' || n.kind === 'locate')) searchS[1](null);
    }
    function locate() {
      if (locatingS[0]) return;
      locatingS[1](true);
      searchS[1](null);
      OO.delivery.locate().then(function (a) {
        if (!aliveRef.current) return;
        locatingS[1](false);
        setAddress(pickedAddress(a));
      }, function (err) {
        if (!aliveRef.current) return;
        locatingS[1](false);
        searchS[1]({ kind: 'locate', text: (err && err.message) || TEXT.locateFailed });
      });
    }
    function retrySearch() { searchS[1](null); attachS[1](function (n) { return n + 1; }); }
    // A type keeps the mode, day and time while it offers them (view() resolves the rest); a day takes its first time.
    function chooseTime(patch) {
      selS[1](function (s) {
        var next = Object.assign({}, s, patch, { timeEdited: true });
        var choice = SH.view(store, next, st);
        return Object.assign({}, next, { day: choice.day ? choice.day.key : null, slot: choice.slot ? choice.slot.value : null });
      });
    }
    function pickType(t) { chooseTime({ type: t }); }
    function pickMode(m) { chooseTime({ mode: m }); }
    function pickDay(key) { chooseTime({ mode: 'later', day: key, slot: null }); }
    function pickSlot(value) {
      var s = v.day ? byKeyOf(v.day.slots, 'value', value) : null;
      if (s) chooseTime({ mode: 'later', day: v.day.key, slot: s.value });
    }
    var canGo = !!v.slot && (!delivery || (!!as && as.ok));
    function proceed() { if (canGo) props.onDone(SH.commit(v, sel.address, OO.data.now())); }

    var forced = !!props.forced;
    var browse = st.need === 'browse';
    var review = st.need === 'review';
    var reviewReason = SH.reviewReason(v, sel, st);
    var footer = browse
      ? <ui.Button block onClick={props.onBrowse}>{TEXT.browseMenu}</ui.Button>
      : <ui.Button block disabled={!canGo} onClick={proceed}>{zeta ? 'Continue to menu' : review ? TEXT.update : TEXT.proceed}</ui.Button>;
    var sheet = function (body) {
      return (
        <ui.Sheet open variant="dialog" title={browse ? TEXT.browseTitle : zeta && (!appearance || appearance.title === TEXT.title) ? 'How would you like to order?' : appearance ? appearance.title : TEXT.title}
          className={cx('oo-osheet', zeta && 'oo-osheet--zeta', appearance && 'oo-osheet--' + appearance.layout, appearance && 'oo-osheet--custom', appearance && appearance.showBrand && 'oo-osheet--branded')} showClose={!forced}
          onClose={forced ? noop : props.onClose} footer={footer}>
          {!zeta ? <K.BranchLine route={props.route || OO.router.current()} hotel={props.hotel} /> : null}
          {appearance ? <React.Fragment>
            {appearance.showBrand ? <OrderPromptBrand appearance={appearance} /> : null}
            <div className="oo-osheet__fields">{body}</div>
          </React.Fragment> : body}
        </ui.Sheet>
      );
    };

    if (browse) {
      var b = SH.browseText(st, store);
      return sheet(
        <div className="oo-osheet__notice" role="status">
          <OO.icons.Info size={20} />
          <div className="oo-osheet__notice-text">
            <p className="oo-osheet__lead">{b.text}</p>
            {b.reason ? <p className="oo-osheet__reason">{b.reason}</p> : null}
          </div>
        </div>
      );
    }

    function option(group, value, on, off, icon, name, sub) {
      return (
        <label key={value} className={cx('oo-osheet__opt', off && 'is-off')}>
          <input className="oo-osheet__input" type="radio" name={uid + '-' + group} value={value} checked={on} disabled={off}
            onChange={function () { if (group === 'type') pickType(value); else pickMode(value); }} />
          <span className="oo-osheet__face">
            {ui.renderIcon(icon, { size: 18 })}
            <span className="oo-osheet__optname">{name}</span>
            {sub ? <span className="oo-osheet__optsub">{sub}</span> : null}
          </span>
        </label>
      );
    }

    var typeSec = st.types.length > 1 ? (
      <div className="oo-osheet__sec">
        <div className="oo-osheet__label" id={uid + '-type'}>{TEXT.typeLabel}</div>
        <div className="oo-osheet__seg" role="radiogroup" aria-labelledby={uid + '-type'}>
          {st.types.map(function (t) {
            var off = !st.pickers[t] || !st.pickers[t].first;
            return option('type', t, t === v.type, off, t === 'DELIVERY' ? 'Pin' : 'Bag', TYPE_WORD[t], off ? TEXT.noTimes : null);
          })}
        </div>
      </div>
    ) : (
      <p className="oo-osheet__only">
        {ui.renderIcon(v.type === 'DELIVERY' ? 'Pin' : 'Bag', { size: 18 })}
        <span>{(TYPE_WORD[v.type] || TYPE_WORD.PICKUP) + (v.type === 'DELIVERY' ? ' from ' : ' at ') + store.name}</span>
      </p>
    );

    var addressSec = null;
    if (delivery) {
      var note = searchS[0] && searchS[0].text ? searchS[0].text : null;
      var retry = as.retry === 'distance' ? dist.retry : as.retry === 'search' ? retrySearch : null;
      var line = as.checking ? <p className="oo-osheet__hint" role="status">{TEXT.checking}</p>
        : as.ok ? <p className="oo-osheet__ok" role="status"><OO.icons.Check size={16} strokeWidth={3} /><span>{TEXT.covered}</span></p>
        : as.block && as.live ? (
          <ui.InlineNotice tone="warn" role="alert">
            {retry ? (
              <React.Fragment>
                {retryLead(as.block)}{' '}<ui.Button variant="link" className="oo-osheet__inlink" onClick={retry}>{TEXT.tryAgain}</ui.Button>
              </React.Fragment>
            ) : as.block}
          </ui.InlineNotice>
        )
        : as.block ? <p className="oo-osheet__hint" role="status">{as.block}</p> : null;
      addressSec = (
        <div className="oo-osheet__sec oo-osheet__addr">
          <ui.TextInput label={TEXT.addressLabel} labelVisible name="address" autoComplete="street-address" value={sel.address.address}
            inputRef={fieldRef} hint={note || undefined} onChange={typeAddress}
            onKeyDown={places ? function (e) { if (e.key === 'Enter') e.preventDefault(); } : undefined} />
          {places && !as.searchDown ? (
            <p className="oo-osheet__locate">
              <ui.Button variant="link" busy={locatingS[0]} onClick={locate}>{TEXT.locate}</ui.Button>
            </p>
          ) : null}
          {line}
        </div>
      );
    }

    var whenSec = (
      <div className="oo-osheet__sec">
        <div className="oo-osheet__label" id={uid + '-when'}>{TEXT.whenLabel}</div>
        {v.day && v.day.lateNight ? <div><ui.Badge tone="required">Late night orders available</ui.Badge></div> : null}
        {v.mode ? (
          <div className="oo-osheet__seg" role="radiogroup" aria-labelledby={uid + '-when'}>
            {option('mode', 'asap', v.mode === 'asap', !v.asap.ok, 'Clock', TEXT.asap, v.asap.ok ? v.asap.label : TEXT.notAvailable)}
            {option('mode', 'later', v.mode === 'later', !v.later.ok, 'Calendar', TEXT.later, v.later.ok ? TEXT.laterCaption : TEXT.notAvailable)}
          </div>
        ) : <ui.InlineNotice tone="warn">{TEXT.noTypeTimes(v.type)}</ui.InlineNotice>}
        {v.note ? <p className="oo-osheet__note">{v.note}</p> : null}
        {v.mode === 'later' ? (
          <React.Fragment>
            {appearance && appearance.timeStyle === 'select' ? <ui.SelectField label="Day" name="day" value={v.day.key} onChange={pickDay}
              options={v.later.days.map(function (day) { return { value: day.key, label: day.text || day.label, disabled: !day.pickable }; })} />
              : <ui.DateStrip label="Day" value={v.day.key} onChange={pickDay} days={v.later.days.map(dayTile)} className="oo-osheet__days" />}
            {v.closed.length ? (
              <ul className="oo-osheet__closed">{v.closed.map(function (c) { return <li key={c}>{c}</li>; })}</ul>
            ) : null}
            {timeStyle === 'dropdown' ? <ui.SelectField label="Time" name="time" value={v.slot.value} onChange={pickSlot}
              options={v.day.slots.map(function (s) { return { value: s.value, label: s.label }; })} /> : <div className="oo-osheet__times">
              <ui.TimeGrid label="Time" value={v.slot.value} onChange={pickSlot}
                slots={v.day.slots.map(function (s) { return { value: s.value, label: s.label }; })} />
            </div>}
          </React.Fragment>
        ) : null}
      </div>
    );

    if (zeta && K.ZetaOrderChoices) return sheet(<K.ZetaOrderChoices store={store} route={props.route || OO.router.current()} status={st} view={v}
      onType={pickType} onMode={pickMode} onDay={pickDay} onSlot={pickSlot} address={addressSec}
      review={reviewReason ? TEXT.review[reviewReason] || TEXT.review.expired : null} />);

    return sheet(
      <React.Fragment>
        {reviewReason ? (
          <ui.InlineNotice tone="warn" role="alert" className="oo-osheet__review">{TEXT.review[reviewReason] || TEXT.review.expired}</ui.InlineNotice>
        ) : (appearance ? appearance.intro : TEXT.intro) ? <p className="oo-osheet__lead">{appearance ? appearance.intro : TEXT.intro}</p> : null}
        {typeSec}
        {addressSec}
        {whenSec}
      </React.Fragment>
    );
  };

  // The summary row (see the header).
  function OrderSummary(props) {
    var s = props.sum, st = props.st;
    var icon = st.need === 'browse' ? 'Info' : st.need === 'fresh' && st.mine && st.mine.orderType === 'DELIVERY' ? 'Pin' : 'Bag';
    return (
      <section className="oo-home-order" aria-label="Your order">
        <button type="button" className="oo-home-order__btn" aria-haspopup="dialog" onClick={props.onOpen}>
          <span className="oo-home-order__icon">{OO.ui.renderIcon(icon, { size: 20 })}</span>
          <span className="oo-home-order__text">
            <span className="oo-home-order__title">{s.title}</span>
            {s.sub ? <span className="oo-home-order__sub">{s.sub}</span> : null}
          </span>
          <span className="oo-home-order__act">{s.action}</span>
        </button>
      </section>
    );
  }

  // WL-VEL (VEL-C): the order bar (OrderPrompt's `bar`): full width under the menu's header; it opens the sheet.
  function OrderBar(props) {
    var b = props.bar;
    if (!b) return null;
    return (
      <div className="oo-obar">
        <button type="button" className="oo-obar__btn" aria-haspopup="dialog" onClick={props.onOpen}>
          <OO.icons.Clock size={17} className="oo-obar__icon" />
          <span className="oo-obar__text">{b.text}</span>
          <span className="oo-obar__act">{b.action}</span>
        </button>
      </div>
    );
  }

  // The online menu's "Browse for later" sheets dismissed on this page load: '{hotel}|{reason}' → true.
  var browsed = {};
  // The online menu honours the store's on-load prompt once per store in this page load.
  // Returning from the cart keeps the answer; reloading the site asks again.
  var answeredOnLoad = {};
  function selectCheckout(s) { return s.checkout; }

  K.OrderPrompt = function OrderPrompt(props) {
    var hotel = props.hotel, store = props.store;
    var k = sheetKits();
    if (!k || !store) return null; // fixed for the page's life: the hooks below always run, or never
    var draft = OO.useStore(selectCheckout) || {};
    var session = K.useSession();
    var fixture = !!(OO.api && typeof OO.api.isFixtureFor === 'function' && OO.api.isFixtureFor(hotel));
    var list = SH.types(store, fixture);
    var mine = draftFor(draft, hotel);
    // P2-23's clock: this re-renders every minute and when the page is shown or focused again, so a time that goes
    // stale while Home is open asks again (the section's own picker for the draft's type is not read here).
    k.CS.time.use({ store: store, orderType: mine && list.indexOf(mine.orderType) >= 0 ? mine.orderType : list[0] || 'PICKUP' });
    var now = OO.data.now();
    var minute = Math.floor(now.getTime() / 60000);
    var pickers = React.useMemo(function () {
      var out = {};
      list.forEach(function (t) { out[t] = k.CS.time.picker(store, t, now); });
      return out;
    }, [store, list.join('|'), minute]);
    var st = SH.status(store, draft, { hotel: hotel, now: now, fixture: fixture, types: list, pickers: pickers });
    var manualS = React.useState(false);
    var manualTypeS = React.useState(null);
    React.useEffect(function(){if(props.openSignal > 0)manualS[1](true);},[props.openSignal]);
    var bump = React.useState(0)[1];
    var loadDue = !!props.onLoad && !answeredOnLoad[hotel];
    // `bar` (the online order bar) and `manualOnly` (app Home) open only from their explicit order action.
    var manualOnly = props.manualOnly === true;
    var forced = !props.bar && !manualOnly && (st.need === 'answer' || st.need === 'review' || (loadDue && st.need !== 'browse'));
    var browseKey = txt(hotel) + '|' + txt(st.reason);
    var browseOnlyApp = OO.mode === 'app' && st.need === 'browse';
    var open = !browseOnlyApp && (props.bar || manualOnly ? manualS[0] : forced || manualS[0] || loadDue || (st.need === 'browse' && !browsed[browseKey]));
    var atTable = SH.atTable(props.route || OO.router.current(), hotel, OO.store.table());
    var busy = open && !atTable;
    var onBusy = React.useRef(props.onBusy);
    onBusy.current = props.onBusy;
    React.useEffect(function () { if (onBusy.current) onBusy.current(busy); }, [busy]);
    React.useEffect(function () { return function () { if (onBusy.current) onBusy.current(false); }; }, []);
    function close() {
      if (props.onLoad) answeredOnLoad[hotel] = true;
      if (st.need === 'browse') { browsed[browseKey] = true; bump(function (n) { return n + 1; }); }
      manualS[1](false);
      manualTypeS[1](null);
    }
    function done(patch) {
      if (props.onLoad) answeredOnLoad[hotel] = true;
      if (patch) OO.store.setCheckout(patch);
      manualS[1](false);
      manualTypeS[1](null);
    }
    function browse() { close(); K.go(hotel, 'menu'); }
    // At a table nothing is asked (D-20): the route (props.route, else the live one) and the table context, per render.
    // Browse-only apps go straight to Home; keep the hooks above active if ordering becomes available again.
    if (atTable || browseOnlyApp) return null;
    var sum = props.summary ? SH.summary(st, store, { branch: props.branch }) : null;
    // The sheet keeps its own choice while open; it starts afresh when it turns from browse-only to ordering (or back)
    // and when the draft's type changes from outside (Home's ?orderType= landing applies it after the first render).
    var requestedType = manualS[0] && list.indexOf(manualTypeS[0]) >= 0 ? manualTypeS[0] : null;
    var sheetStatus = requestedType ? Object.assign({}, st, { type: requestedType }) : st;
    var sheetKey = (st.need === 'browse' ? 'browse' : 'order') + '|' + txt(mine && mine.orderType) + (requestedType ? '|' + requestedType : '');
    // WL-VEL: the order bar's own element tree (React derives useId values from the tree: the other prompts keep theirs).
    if (props.bar) {
      return (
        <React.Fragment>
          <OrderBar bar={SH.barText(st, store)} onOpen={function () { manualS[1](true); }} />
          {open && !props.blocked ? (
            <K.OrderSheet key={sheetKey} hotel={hotel} store={store} st={st} draft={draft} session={session} fixture={fixture} forced={forced}
              onDone={done} onClose={close} onBrowse={browse} />
          ) : null}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        {sum ? props.renderSummary ? props.renderSummary({ summary: sum, status: st, onOpen: function () { manualTypeS[1](null); manualS[1](true); }, onType: function (type) { if (list.indexOf(type) >= 0) { manualTypeS[1](type); manualS[1](true); } } }) : <OrderSummary sum={sum} st={st} onOpen={function () { manualS[1](true); }} /> : null}
        {open && !props.blocked ? (
          <K.OrderSheet key={sheetKey} hotel={hotel} store={store} st={sheetStatus} draft={draft} session={session} fixture={fixture} forced={forced}
            onDone={done} onClose={close} onBrowse={browse} />
        ) : null}
      </React.Fragment>
    );
  };

  // ===================================== Home =====================================
  var RM = '(prefers-reduced-motion: reduce)';
  function subscribeRM(cb) {
    var m = window.matchMedia ? window.matchMedia(RM) : null;
    if (!m) return function () {};
    if (m.addEventListener) { m.addEventListener('change', cb); return function () { m.removeEventListener('change', cb); }; }
    m.addListener(cb);
    return function () { m.removeListener(cb); };
  }
  function reducedNow() { return !!(window.matchMedia && window.matchMedia(RM).matches); }
  function useReducedMotion() { return React.useSyncExternalStore(subscribeRM, reducedNow); }

  function greetingFor(name) {
    var h = new Date().getHours();
    var part = h >= 5 && h < 12 ? 'Good morning' : (h >= 12 && h < 17 ? 'Good afternoon' : 'Good evening');
    return name ? part + ', ' + name : part;
  }

  function money(n, store) { return OO.format.money(n, store && store.currency); }
  function displayPrice(p) { return p.sizes && p.sizes.length ? p.sizes[0].price : p.price; }

  function orderImage(order, menu) {
    var l = (order.lines || []).filter(function (x) { return !x.reward; })[0];
    if (!l || !menu) return null;
    var src = l.kind === 'combo' ? menu.combos[l.comboId] : menu.products[l.productId];
    return src && src.image ? src.image : null;
  }

  // ---- promo slider -----------------------------------------------------------------------------------
  // The CTA follows K.bannerLink (W3-fix #6): a URL in a new tab, a path outside the SPA by a full load, the rest in-app.
  function SlideCta(props) {
    var b = props.banner;
    var l = K.bannerLink(b, props.hotel);
    if (!l) return null;
    var label = b.cta || 'Order now';
    var common = { className: 'oo-home-slide__cta', tabIndex: props.active ? undefined : -1, 'aria-label': label + (b.title ? ': ' + b.title : '') };
    if (l.kind === 'url') return <a {...common} href={l.href} target="_blank" rel="noopener noreferrer">{label}</a>;
    return <a {...common} href={l.href} onClick={l.onClick}>{label}</a>;
  }

  function PromoSlider(props) {
    var slides = props.banners;
    var n = slides.length;
    var idxState = React.useState(0);
    var hoverState = React.useState(false);
    var focusState = React.useState(false);
    var stoppedState = React.useState(false);
    var reduced = useReducedMotion();
    var touch = React.useRef(null);
    var requested = n ? idxState[0] % n : 0;
    var media = OO.ui.useSlideImages(slides, requested, function (s) { return s.image || String(s.id); });
    var i = slides[requested] && !slides[requested].image ? requested : media.index;
    var rotating = n > 1 && !reduced && !hoverState[0] && !focusState[0] && !stoppedState[0];

    React.useEffect(function () {
      if (!rotating) return undefined;
      var t = setTimeout(function () { idxState[1](function (x) { return (x + 1) % n; }); }, 3500);
      return function () { clearTimeout(t); };
    }, [rotating, i, n]);

    function pick(k) { stoppedState[1](true); idxState[1](((k % n) + n) % n); }
    function onTouchStart(e) { var t = e.touches && e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; }
    function onTouchEnd(e) {
      var s = touch.current, t = e.changedTouches && e.changedTouches[0];
      touch.current = null;
      if (!s || !t || n < 2) return;
      var dx = t.clientX - s.x, dy = t.clientY - s.y;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) pick(i + (dx < 0 ? 1 : -1));
    }
    function onBlur(e) { if (!e.currentTarget.contains(e.relatedTarget)) focusState[1](false); }

    return (
      <section className="oo-home-promo" aria-roledescription="carousel" aria-label="Offers"
        onMouseEnter={function () { hoverState[1](true); }} onMouseLeave={function () { hoverState[1](false); }}
        onFocus={function () { focusState[1](true); }} onBlur={onBlur}>
        <div className="oo-home-promo__viewport" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="oo-home-promo__track" aria-live={rotating ? 'off' : 'polite'}
            style={{ transform: 'translateX(calc(' + (-100 * i) + '% - ' + (12 * i) + 'px))' }}>
            {slides.map(function (b, k) {
              var on = k === i;
              var imageOnly = !!b.image && !b.title && !b.tag && !b.subtitle;
              return (
                <div key={b.id || k} className={OO.ui.cx('oo-home-slide', !b.image && 'oo-home-slide--noimg', imageOnly && 'oo-home-slide--imageonly')} role="group"
                  aria-roledescription="slide" aria-label={(k + 1) + ' of ' + n} aria-hidden={on ? undefined : 'true'}>
                  <div className="oo-home-slide__text">
                    {b.tag ? <div className="oo-home-slide__tag">{b.tag}</div> : null}
                    {b.title ? <div className="oo-home-slide__title">{b.title}</div> : null}
                    {b.subtitle ? <div className="oo-home-slide__sub">{b.subtitle}</div> : null}
                    <SlideCta banner={b} hotel={props.hotel} active={on} />
                  </div>
                  {b.image && media.has(k) ? <OO.ui.Image cover className="oo-home-slide__img" src={b.image} sizes="(max-width: 540px) 100vw, 540px" alt="" loading="eager" fetchpriority={k === 0 ? 'high' : 'auto'} onLoad={media.done(k)} onError={media.done(k)} /> : null}
                </div>
              );
            })}
          </div>
        </div>
        {n > 1 ? (
          <div className="oo-home-promo__dots" role="group" aria-label="Choose an offer">
            {slides.map(function (b, k) {
              return (
                <button key={b.id || k} type="button" className={OO.ui.cx('oo-home-dot', k === i && 'is-on')}
                  aria-label={'Offer ' + (k + 1) + ' of ' + n} aria-current={k === i ? 'true' : undefined}
                  onClick={function () { pick(k); }}>
                  <span className="oo-home-dot__pill" />
                </button>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  }

  // ---- modules -------------------------------------------------------------------------------------------
  function LoyaltyModule(props) {
    var hotel = props.hotel, store = props.store, res = props.loyalty;
    var ui = OO.ui;
    if (!res.signedIn) {
      return (
        <K.LoginPrompt hotel={hotel} className="oo-home-card"
          title={'Join ' + store.loyalty.programName}
          body={'Log in to earn ' + store.loyalty.pointsWord + ' on every order.'} />
      );
    }
    if (res.error && !res.data) {
      return (
        <ui.Card radius="xl" className="oo-home-card oo-home-loyal oo-home-loyal--msg">
          <span className="oo-home-loyal__msg" role="alert">We couldn't load your {store.loyalty.pointsWord}.</span>
          <ui.Button variant="link" onClick={res.reload}>Try again</ui.Button>
        </ui.Card>
      );
    }
    var L = res.data;
    if (!L) return <div className="oo-home-card" aria-hidden="true"><ui.Skeleton variant="block" height={128} /></div>;
    if (!L.enabled) return null;
    var scan = K.link(hotel, 'rewards');
    return (
      <ui.Card radius="xl" className="oo-home-card oo-home-loyal">
        <div className="oo-home-loyal__row">
          <div className="oo-home-loyal__text">
            <div className="oo-home-loyal__name">{L.programName + (L.tier ? ' · ' + L.tier : '')}</div>
            <div className="oo-home-loyal__pts">
              <span className="oo-home-loyal__num">{L.points}</span>
              <span className="oo-home-loyal__word">{L.pointsWord}</span>
            </div>
            {L.scope ? <div className="oo-home-loyal__scope">{L.scope.text}</div> : null}
          </div>
          <ui.Button size="md" icon="Scan" iconSize={16} className="oo-home-loyal__scan" href={scan.href} onClick={scan.onClick}>Scan card</ui.Button>
        </div>
        {L.goal ? (
          <ui.ProgressBar value={L.points} max={L.goal.cost || 1} label={'Progress to ' + L.goal.name} />
        ) : null}
        {L.goal ? (
          <div className="oo-home-loyal__togo">
            {L.goal.toGo > 0
              ? <React.Fragment>{K.pointsText(L.goal.toGo, L.pointsWord)} to a <strong>{L.goal.name}</strong></React.Fragment>
              : <React.Fragment>You have enough for a <strong>{L.goal.name}</strong></React.Fragment>}
          </div>
        ) : null}
      </ui.Card>
    );
  }

  function MarketHero(props) {
    var hero = props.design.hero || {};
    var shop = K.link(props.hotel, 'menu');
    return <section className="oo-market-hero" aria-label="Welcome">
      {hero.image ? <OO.ui.Image cover className="oo-market-hero__image" src={hero.image} sizes="(max-width: 540px) 100vw, 540px" alt="" loading="eager" fetchpriority="high" />
        : props.banners.length ? <PromoSlider banners={props.banners} hotel={props.hotel} /> : null}
      <div className="oo-market-hero__body">
        {hero.title ? <h2>{hero.title}</h2> : null}
        {hero.body ? <p>{hero.body}</p> : null}
        <a className="oo-market-hero__cta" href={shop.href} onClick={shop.onClick}>{hero.buttonLabel || 'Start shopping'}</a>
      </div>
    </section>;
  }

  // The same values and checkout used by VoucherOnlineController. Food carts and order planning stay separate.
  var GIFT_CARD_VALUES = [25, 50, 100, 200];
  function giftCardUrl(hotel, value) {
    return 'https://voucher.sipocloudpos.com/' + encodeURIComponent(String(hotel)) + '/app?value=' + value;
  }
  function GiftCardsModule(props) {
    var preview = !!(OO.whitelabel && OO.whitelabel.isPreview());
    // Voucher checkout uses the store/gateway currency, independent of the website's branding locale.
    var currency = props.store.currency;
    if (!/^[A-Z]{3}$/.test(String(currency || ''))) currency = 'NZD';
    var money = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return <section className="oo-home-gifts" aria-label="Gift cards">
      <h2>Gift cards</h2>
      <p>A thoughtful gift, delivered by email.</p>
      <div className="oo-home-gifts__grid">{GIFT_CARD_VALUES.map(function (value) {
        var amount = money.format(value);
        var face = <React.Fragment>
          <span className="oo-home-gift__label">E-gift card</span>
          <span className="oo-home-gift__brand">{props.theme.brand || props.store.name}</span>
          <strong className="oo-home-gift__value">{amount}<small>{currency}</small></strong>
          <span className="oo-home-gift__choose">Choose gift card <OO.icons.ChevronRight size={16} /></span>
        </React.Fragment>;
        return preview ? <div key={value} className="oo-home-gift" aria-label={amount + ' gift card — preview only'}>{face}</div>
          : <a key={value} className="oo-home-gift" href={giftCardUrl(props.hotel, value)} target="_blank" rel="noopener noreferrer"
            aria-label={'Choose ' + amount + ' gift card for ' + props.place}>{face}</a>;
      })}</div>
      <p className="oo-home-gifts__store">Redeem at {props.place}. {preview ? 'Purchases are disabled in preview.' : 'Choose a card to continue to gift-card checkout.'}</p>
    </section>;
  }
  K.GiftCardsModule = GiftCardsModule;
  K.giftCardValues = GIFT_CARD_VALUES.slice();

  function MarketActions(props) {
    var actions = [
      { page: 'menu', label: 'Shop groceries', icon: 'Bag' },
      { page: 'stores', label: 'Stores & collect', icon: 'Pin' },
      { page: 'orders', label: 'Track & reorder', icon: 'Receipt' },
    ];
    return <nav className="oo-market-actions" aria-label="Quick actions">{actions.map(function (action) {
      var link = K.link(props.hotel, action.page), Icon = OO.icons[action.icon];
      return <a key={action.page} className="oo-market-action" href={link.href} onClick={link.onClick}>
        <Icon size={22} /><span>{action.label}</span>
      </a>;
    })}</nav>;
  }

  function ActionIcon(props) {
    var failed = React.useState(false);
    var Icon = OO.icons[props.icon];
    return props.src && !failed[0]
      ? <OO.ui.Image className="oo-home-action__image" src={props.src} sizes="64px" alt="" aria-hidden="true" onError={function () { failed[1](true); }} />
      : <Icon size={props.blend ? 40 : 24} />;
  }

  function ActionsModule(props) {
    var hotel = props.hotel;
    var settings = (props.brandStore.whitelabel || {}).quickActions || {};
    var images = settings.icons || {}, blend = settings.tileStyle === 'transparent';
    var list = [{ page: 'menu', label: 'Order now', icon: 'Bag' }];
    if (props.store.booking) list.push({ page: 'booking', label: 'Book a table', icon: 'Calendar' });
    list.push({ page: 'rewards', label: 'Rewards', icon: 'Gift' }, { page: 'stores', label: 'Stores', icon: 'Pin' });
    return (
      <nav className={OO.ui.cx('oo-home-actions', blend && 'oo-home-actions--transparent')} aria-label="Quick actions">
        {list.map(function (a) {
          var l = K.link(hotel, a.page);
          return (
            <a key={a.page} className="oo-home-action" href={l.href} onClick={l.onClick}>
              <span className="oo-home-action__tile"><ActionIcon key={images[a.page] || a.icon} src={images[a.page]} icon={a.icon} blend={blend} /></span>
              <span className="oo-home-action__label">{a.label}</span>
            </a>
          );
        })}
      </nav>
    );
  }

  function ReorderModule(props) {
    var ui = OO.ui;
    var res = props.orders, store = props.store, menu = props.menu;
    if (!props.signedIn) return null;
    var order = res.data;
    if (res.loading && !order) {
      return (
        <section className="oo-home-sec" aria-busy="true" aria-label="Order again">
          <div className="oo-home-card" aria-hidden="true"><ui.Skeleton variant="block" height={84} /></div>
        </section>
      );
    }
    if (!order) return null; // no completed order yet, or the list failed (quietly: it's a shortcut, not content)
    var img = orderImage(order, menu);
    var sub = [order.placedLabelLong, props.place, money(order.total, store)].filter(Boolean).join(' · ');
    return (
      <section className="oo-home-sec" aria-labelledby="oo-home-again-t">
        <ui.SectionTitle level="section" id="oo-home-again-t" className="oo-home-sec__title">Order again</ui.SectionTitle>
        <ui.Card radius="lg" pad="none" className="oo-home-card oo-home-again">
          <span className="oo-home-again__img">{img ? <img src={img} alt="" /> : <OO.icons.Receipt size={22} />}</span>
          <span className="oo-home-again__text">
            <span className="oo-home-again__name">{order.summary || order.shortId}</span>
            <span className="oo-home-again__sub">{sub}</span>
          </span>
          <ui.Button variant="outline" size="xs" disabled={!menu} onClick={function () { props.onReorder(order); }}
            aria-label={'Reorder ' + (order.summary || 'your last order')}>Reorder</ui.Button>
        </ui.Card>
      </section>
    );
  }

  // D-07 (P2-17): Popular follows the menu's display policy, judged at the same moment (OO.pages.Menu.shared.useDisplay
  // on the cached menu): a dish that can never be ordered here drops out, a greyed one shows greyed with its label and
  // is never a link. Until the display exists (no menu cached yet), the static sold-out flag greys as before.
  function PopularModule(props) {
    var ui = OO.ui;
    var res = props.popular, hotel = props.hotel, store = props.store;
    var shared = OO.pages.Menu && OO.pages.Menu.shared;
    var cached = OO.data.peekHotel(hotel, true);
    var dsp = shared ? shared.useDisplay(hotel, store, cached ? cached.menu : null) : null;
    var display = dsp && dsp.display;
    var full = K.link(hotel, 'menu');
    var title = props.pizzaClub ? 'Popular right now' : props.pizzeria ? 'Popular picks' : props.brand ? 'Popular' : 'Popular at ' + props.place;
    var action = <a className="oo-home-full" href={full.href} onClick={full.onClick}>Full menu</a>;
    var body;
    if (res.loading && !res.data) {
      var sk = [];
      for (var i = 0; i < 3; i++) sk.push(<span key={i} className="oo-home-pop__skel"><ui.Skeleton variant="block" height={168} /></span>);
      body = <div className="oo-home-pop" aria-hidden="true">{sk}</div>;
    } else if (res.error && !res.data) {
      body = (
        <div className="oo-home-card oo-home-pop__msg" role="alert">
          <span>We couldn't load popular items.</span>
          <ui.Button variant="link" onClick={res.reload}>Try again</ui.Button>
        </div>
      );
    } else {
      var products = (res.data || []).filter(function (p) { var d = display && display.products[p.id]; return !d || d.show !== 'hide'; });
      products = OO.pages.Menu.logic.sortProducts(products, display);
      if (!products.length) return null;
      body = (
        <ul className="oo-home-pop">
          {products.map(function (p) {
            var d = display ? display.products[p.id] : null;
            var off = d ? d.show === 'grey' : !!p.soldOut || p.available === false;
            var price = money(displayPrice(p), store);
            var inner = [
              <span key="m" className="oo-home-pop__media">{p.image ? <OO.ui.Image cover src={p.image} sizes="220px" alt="" loading="lazy" /> : <OO.icons.ImagePlaceholder size={24} />}</span>,
              <span key="b" className="oo-home-pop__body">
                <span className="oo-home-pop__name">{p.name}</span>
                <span className="oo-home-pop__price">{off ? <ui.Badge tone="neutral" size="sm">{d ? d.label : 'Sold out'}</ui.Badge> : price}</span>
              </span>,
            ];
            if (off) return <li key={p.id}><span className={'oo-home-pop__card ' + (!d || d.reason === 'soldOut' ? 'is-soldout' : 'is-unavailable')}>{inner}</span></li>;
            var l = K.link(hotel, 'menu', null, { type: 'product', id: String(p.id) });
            return <li key={p.id}><a className="oo-home-pop__card" href={l.href} onClick={l.onClick}>{inner}</a></li>;
          })}
        </ul>
      );
    }
    return (
      <section className="oo-home-sec" aria-labelledby="oo-home-pop-t">
        <ui.SectionTitle level="section" id="oo-home-pop-t" className="oo-home-sec__title" action={action}>{title}</ui.SectionTitle>
        {body}
      </section>
    );
  }

  function CategoryArtwork(props) {
    var failed = React.useState(false);
    React.useEffect(function () { failed[1](false); }, [props.image]);
    return <span className="oo-home-category__media">
      {props.image && !failed[0] ? <OO.ui.Image cover src={props.image} sizes="220px" alt="" loading="lazy" onError={function () { failed[1](true); }} /> : <OO.icons.Bag size={32} />}
    </span>;
  }

  // The home shortcuts use the same selected menu, display policy and table exclusions as Menu.
  function CategoriesModule(props) {
    var store = props.store, menu = props.menu, hotel = props.hotel, page = OO.pages.Menu;
    var dsp = page.shared.useDisplay(hotel, store, menu, OO.mode);
    var table = OO.pages.Table && typeof OO.pages.Table.context === 'function' ? OO.pages.Table.context(hotel) : null;
    var view = page.logic.buildView(menu, table ? table.excludedCategoryIds : [], dsp.display);
    var configured = store.whitelabel && store.whitelabel.categoryTiles;
    var tiles = configured && configured.length ? configured : view.categories.slice(0, 4).map(function (c) { return { categoryId: c.id }; });
    var shown = tiles.map(function (tile) {
      var cat = view.categories.filter(function (c) { return String(c.id) === String(tile.categoryId); })[0];
      if (!cat) return null;
      var product = cat.productIds.map(function (id) { return menu.products[id]; }).filter(function (p) { return p && p.image; })[0];
      return { category: cat, title: tile.title || cat.name, image: tile.image || cat.image || (product && product.image) || null };
    }).filter(Boolean);
    if (!shown.length) return null;
    var full = K.link(hotel, 'menu');
    var sections = OO.model.menuLayout(store, props.route.query) === 'sections';
    var market = OO.whitelabel && typeof OO.whitelabel.appDesign === 'function' && OO.whitelabel.appDesign(props.route.chain || hotel);
    if (market) sections = false;
    return <section className="oo-home-sec" aria-labelledby="oo-home-categories-t">
      <OO.ui.SectionTitle level="section" id="oo-home-categories-t" className="oo-home-sec__title"
        action={<a className="oo-home-full" href={full.href} onClick={full.onClick}>View all</a>}>{market ? 'Shop by aisle' : 'Shop by category'}</OO.ui.SectionTitle>
      <ul className="oo-home-categories">{shown.map(function (tile) {
        var id = tile.category.id;
        var link = OO.router.link('menu', null, { hotel: hotel, query: K.query(sections ? {} : { cat: String(id) }), hash: sections ? view.anchor[String(id)] : null });
        return <li key={id}><a className="oo-home-category" href={link.href} onClick={link.onClick}>
          <CategoryArtwork image={tile.image} />
          <span className="oo-home-category__label"><span>{tile.title}</span><OO.icons.ChevronRight size={16} /></span>
        </a></li>;
      })}</ul>
    </section>;
  }

  function StoreModule(props) {
    var store = props.store, about = OO.pages.About && OO.pages.About.logic;
    var clock = React.useState(function () { return new Date(); });
    React.useEffect(function () {
      function refresh() { clock[1](new Date()); }
      var timer = window.setInterval(refresh, 60000);
      window.addEventListener('focus', refresh);
      return function () { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
    }, []);
    var address = store.address && (store.address.full || store.address.short);
    var opening = store.hoursUtc && store.hoursUtc.OPENING || store.hours && store.hours.OPENING || {};
    var hasHours = Object.keys(opening).some(function (day) { return opening[day].length; });
    var today = about && hasHours ? about.weekRows(store, 'OPENING', clock[0]).filter(function (day) { return day.today; })[0] : null;
    var hours = today ? (today.closed ? 'Closed today' + (today.holiday ? ' · ' + today.holiday : '') : today.ranges.join(', ')) : '';
    var directions = about && about.directionsUrl(store);
    var details = K.link(props.hotel, 'about');
    if (!address && !hours) return null;
    return <section className="oo-home-visit" aria-labelledby="oo-home-visit-t">
      <div className="oo-home-visit__head"><span className="oo-home-visit__icon"><OO.icons.Store size={24} /></span>
        <div><h2 id="oo-home-visit-t">Visit us</h2><p className="oo-home-visit__name">{store.name}</p></div>
      </div>
      {address ? <p className="oo-home-visit__row"><OO.icons.Pin size={16} /><span>{address}</span></p> : null}
      {hours ? <p className="oo-home-visit__row"><OO.icons.Clock size={16} /><span><strong>Today</strong> · {hours}</span></p> : null}
      <div className="oo-home-visit__links">
        {directions ? <a className="oo-home-visit__directions" href={directions} target="_blank" rel="noopener noreferrer">Get directions<OO.icons.ChevronRight size={16} /></a> : null}
        <a href={details.href} onClick={details.onClick}>Store details</a>
      </div>
    </section>;
  }

  function HomeSkeleton() {
    var ui = OO.ui;
    return (
      <div className="oo-home" aria-busy="true">
        <header className="oo-home-skelhead" aria-hidden="true">
          <span className="oo-home-skelhead__line oo-home-skelhead__line--sm" />
          <span className="oo-home-skelhead__line" />
          <span className="oo-home-skelhead__line oo-home-skelhead__line--xs" />
        </header>
        <main className="oo-home__body" aria-hidden="true">
          <div className="oo-home-card"><ui.Skeleton variant="block" height={168} /></div>
          <div className="oo-home-card"><ui.Skeleton variant="block" height={128} /></div>
          <div className="oo-home-card"><ui.Skeleton variant="block" height={96} /></div>
        </main>
      </div>
    );
  }

  // P4-17: Home with no branch, the chain home (see "The chain" in the header). Its own component: Home renders it in
  // place of its body, so the branch view's hooks never change order.
  function ChainHome(props) {
    var route = props.route, ui = OO.ui;
    var P = OO.pages.Stores.usePicker({ route: route, theme: true, pick: props.pick, ctaClass: 'oo-home-pickbar' });
    if (P.loading) return <HomeSkeleton />;
    var master = P.master;
    var theme = (master && OO.theme.resolve(master.theme, themeKey())) || {};
    var logo = theme.logo || (master && master.logoUrl) || null;
    var name = theme.brand || OO.model.chainName(P.chain, master);
    var count = P.chain ? P.chain.stores.length : 0;
    return (
      <div className={ui.cx('oo-home', 'oo-home--chain', !!theme.accent && 'oo-home--themed', 'oo-home--brand')}>
        <header className="oo-atop oo-atop--brand oo-chome">
          <div className="oo-atop__row">
            <div className={ui.cx('oo-atop__logo', logo && 'oo-chome__logo')}>{logo ? <OO.ui.Image src={logo} sizes="180px" alt={name ? name + ' logo' : 'Logo'} /> : <OO.icons.Store size={22} />}</div>
            <div className="oo-atop__main oo-atop__main--brand">
              {theme.showStoreName !== false ? <div className="oo-atop__brand">{name}</div> : null}
              {count > 1 ? (
                <div className="oo-atop__storeline">
                  <span className="oo-atop__accentdot" aria-hidden="true" />
                  <span className="oo-atop__storeline-text">{count + ' stores'}</span>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <main className="oo-home__body oo-chome__body">
          <h1 className="oo-sr-only">{name || 'Choose a store'}</h1>
          <section className="oo-home-sec oo-chome__pick" aria-labelledby="oo-chome-t">
            <ui.SectionTitle level="section" id="oo-chome-t" className="oo-home-sec__title" sub="Pick the store you’d like to order from.">
              Choose a store
            </ui.SectionTitle>
            <div className="oo-chome__list">{P.body}</div>
          </section>
        </main>
        {P.cta}
      </div>
    );
  }

  OO.pages.Home = function Home(props) {
    var route = props.route;
    var hotel = route.hotel;
    var chainKey = route.chain || null; // P4-17: mode app's chain (the router's path key); a hand-built route has none
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withRedemptions: false, progressive: true });
    var store = h.store, menu = h.menu;
    var session = K.useSession();
    var uid = uidOf(session);
    var mods = store ? store.homeModules.filter(function (m) { return m.enabled; }) : EMPTY;
    if (OO.mode === 'app' && store && (store.whitelabel || {}).giftCards === true) {
      mods = mods.slice();
      var giftPosition = mods.findIndex(function (m) { return m.key === 'actions'; });
      if (giftPosition < 0) giftPosition = mods.findIndex(function (m) { return m.key === 'slider'; });
      mods.splice(giftPosition + 1, 0, { key: 'gift-cards', enabled: true });
    }
    var on = function (key) { return mods.some(function (m) { return m.key === key; }); };
    // P4-17: the chain (B31): the branch names and distances (p3-followups #35), the master's branding, the loyalty scope.
    var chainRes = OO.useChain(chainKey, { origin: K.demoOrigin() });
    var loyalty = K.useLoyalty(hotel, store, !store || on('loyalty'), { chain: chainRes.chain, master: chainRes.master });
    var notes = K.useNotifications(hotel, store);
    var wantOrders = !!uid && !!store && on('reorder');
    var ordersRes = OO.useAsync(function () {
      return wantOrders ? OO.api.getOrders(hotel, { uid: uid }) : Promise.resolve(null);
    }, [hotel, uid, wantOrders]);
    var wantPopular = !!store && on('popular');
    var popularRes = OO.useAsync(function () {
      return wantPopular ? OO.api.getPopular(hotel) : Promise.resolve(null);
    }, [hotel, wantPopular]);
    var toastState = React.useState(null);
    var gateState = React.useState(null); // { lines, gated } while the reorder's age gate is up (W3-fix #1)

    // Landing ?orderType= (see the header), once the store is in.
    var landingType = String(route.query.orderType == null ? '' : route.query.orderType).trim();
    var landingDone = React.useRef('');
    React.useEffect(function () {
      if (!store || !landingType) return;
      var key = hotel + '|' + landingType;
      if (landingDone.current === key) return;
      landingDone.current = key;
      var L = OO.pages.Menu && OO.pages.Menu.logic;
      var draft = OO.store.get().checkout;
      var mine = draft && (!draft.hotel || String(draft.hotel) === String(hotel)) ? draft : null;
      // The LIVE URL's query (not this render's route), as the menu reads it.
      var live = (OO.router.current() || {}).query || route.query;
      var plan = L && typeof L.landing === 'function' ? L.landing({ orderType: landingType }, live, store, mine) : null;
      // P4-10: a new type clears the time with it, so the order sheet asks again (see the header).
      if (plan && plan.orderType) OO.store.setCheckout({ orderType: plan.orderType, day: null, slot: null, chosenAt: null });
      if (!Object.prototype.hasOwnProperty.call(live, 'orderType')) return;
      var rest = {};
      Object.keys(live).forEach(function (k) { if (k !== 'orderType') rest[k] = live[k]; });
      var hash = String((window.location && window.location.hash) || '').replace(/^#/, '');
      OO.router.go(OO.router.href('home', null, { hotel: hotel, query: rest, hash: hash || undefined }), { replace: true });
    }, [hotel, store, landingType]);

    var lastOrder = React.useMemo(function () {
      if (!ordersRes.data || !store) return null;
      return OO.model.lastCompletedOrder(OO.model.normalizeOrders(ordersRes.data, { timeZone: store.timezone }));
    }, [ordersRes.data, store]);
    // P4-02 (develop parity, B-Δ2): at a table, Popular drops the table group's excluded categories, as the menu does
    // (the table context's excludedCategoryIds: get_menu's, else E19's). Only mode qr has a table, so elsewhere this
    // drops nothing.
    var tableCtx = OO.pages.Table && typeof OO.pages.Table.context === 'function' ? OO.pages.Table.context(hotel) : null;
    var exKey = tableCtx ? tableCtx.excludedCategoryIds.join(',') : '';
    var popular = React.useMemo(function () {
      var list = popularRes.data && menu ? OO.model.popularProducts(popularRes.data, menu) : null;
      var ex = exKey ? exKey.split(',') : [];
      return list && ex.length ? list.filter(function (p) { return ex.indexOf(String(p.categoryId)) < 0; }) : list;
    }, [popularRes.data, menu, exKey]);

    // P4-17: no branch (several stores, none chosen): the chain home (every hook above has run: their order never moves).
    if (!hotel && chainKey) return <ChainHome route={route} pick={props.pick} />;
    if (h.notFound) return <div className="oo-home"><main className="oo-home__body oo-home__body--state"><K.NotFound /></main></div>;
    if (!store) {
      if (h.error) {
        return (
          <div className="oo-home">
            <main className="oo-home__body oo-home__body--state">
              <K.ErrorCard title="We couldn't load this store" onRetry={h.reload} />
            </main>
          </div>
        );
      }
      return <HomeSkeleton />;
    }

    // P4-17: the branding is the chain master's (D-03; the branch's own until the master's store is in).
    var brandStore = chainRes.master || store;
    var theme = OO.theme.resolve(brandStore.theme, themeKey()) || {};
    var themed = !!theme.accent;
    var brandHeader = !!(theme.brand || theme.logo);
    var accentOnHero = themed && !!theme.heroBg &&
      OO.theme.normalizeColor(theme.accent) === OO.theme.normalizeColor(theme.heroBg);
    var open = OO.model.openStatus(store, new Date());
    var list = chainRes.chain ? chainRes.chain.stores : null;
    var me = list ? list.filter(function (b) { return b.key === store.onlineKey; })[0] : null;
    var short = K.shortName(store, list);
    // P4-17: "Ordering from {branch}" in the summary row, in a chain of two or more stores.
    var branchName = me && list.length > 1 ? me.shortName || me.name : null;
    var unread = notes.data ? notes.data.unread : 0;
    var bell = K.link(hotel, 'notifications');
    var toStores = function () { K.go(hotel, 'stores'); };
    var name = session && session.customer ? session.customer.firstName : '';

    // Rebuilt on today's menu by OO.cart.fromOrder (rewards and unavailable items are left out). Age-restricted items
    // ask the age gate first (see the header).
    var acct = OO.pages.acct || {};
    function addAll(lines) {
      lines.forEach(function (l) { OO.store.cartAdd(l); });
      K.go(hotel, 'cart');
    }
    function onReorder(order) {
      var lines = menu ? OO.cart.fromOrder(order, menu, { now: new Date() }).lines : [];
      if (!lines.length) {
        toastState[1]('Sorry, those items aren’t available right now.');
        return;
      }
      var gated = typeof acct.reorderGated === 'function' ? acct.reorderGated(lines, menu, hotel, store) : [];
      if (gated.length) { gateState[1]({ lines: lines, gated: gated }); return; }
      addAll(lines);
    }
    function onGate(ok, dob) {
      var g = gateState[0];
      gateState[1](null);
      if (!g) return;
      if (ok) {
        OO.pages.ItemSheet.age.confirm(hotel, dob);
        addAll(g.lines);
        return;
      }
      var rest = g.lines.filter(function (l) { return g.gated.indexOf(l) < 0; });
      if (rest.length) addAll(rest);
      else toastState[1](acct.reorderText([], [], g.gated.map(function (l) { return l.name; })));
    }
    var Gate = gateState[0] ? acct.ReorderAgeGate : null;
    var AlertHost = OO.pages.MenuOverlays && OO.pages.MenuOverlays.AlertHost; // the store alert (see the header)
    // P4-10: the order sheet and its summary row (see the header); never at a table (D-20).
    var Prompt = SH.ready() && !SH.atTable(route, hotel, OO.store.table()) ? K.OrderPrompt : null;

    // WL-2: a white-label document's app.greeting ('Hi {name}'), in either header variant.
    var wlGreet = brandStore.whitelabel && OO.whitelabel ? OO.whitelabel.greeting(chainKey || hotel, name) : null;
    var market = OO.whitelabel && typeof OO.whitelabel.appDesign === 'function' && OO.whitelabel.appDesign(chainKey || hotel);
    var pizzeria = OO.whitelabel && OO.whitelabel.pizzeriaDesign && OO.whitelabel.pizzeriaDesign(chainKey || hotel);
    var pizzaClub = OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(chainKey || hotel);
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign(chainKey || hotel);
    var kitchen = OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(chainKey || hotel);
    var top = pizzaClub || zeta || kitchen ? null : pizzeria && K.PizzeriaHeader ? <K.PizzeriaHeader hotel={hotel} theme={theme} store={store} /> : market ? <ui.MarketHeader route={route} store={store} theme={theme} place={K.placeName(store, list)} status={open.label} /> : brandHeader ? (
      <ui.AppTopBar brand={theme.brand || brandStore.name} logo={theme.logo || brandStore.logoUrl || null} greeting={wlGreet}
        showStoreName={theme.showStoreName}
        storeName={K.placeName(store, list)} status={open.label} onStoreClick={toStores}
        unread={unread} bellHref={bell.href} onBell={bell.onClick} hotel={hotel} />
    ) : (
      <ui.AppTopBar greeting={wlGreet || greetingFor(name)} storeName={store.name} onStoreClick={toStores}
        showStoreName={theme.showStoreName}
        open={open.isOpen} status={open.label} distance={me ? me.distanceLabel : null}
        unread={unread} bellHref={bell.href} onBell={bell.onClick} hotel={hotel} />
    );

    var render = {
      'gift-cards': function () { return <GiftCardsModule hotel={hotel} store={store} theme={theme} place={K.placeName(store, list)} />; },
      slider: function () {
        // App slides have their own list; older stores keep using banners until first configured.
        var L = OO.pages.MenuOverlays && OO.pages.MenuOverlays.logic, S = OO.pages.Menu && OO.pages.Menu.shared;
        var banners = L && S ? L.liveAppSlides(store, Object.assign({ timeZone: store.timezone }, S.orderMoment(store, null, OO.mode)))
          : (Array.isArray(store.appSlides) ? store.appSlides : store.banners);
        return market ? <MarketHero design={market} banners={banners} hotel={hotel} /> : banners.length ? <PromoSlider banners={banners} hotel={hotel} /> : null;
      },
      loyalty: function () { return <LoyaltyModule hotel={hotel} store={store} loyalty={loyalty} />; },
      actions: function () { return market ? <MarketActions hotel={hotel} /> : <ActionsModule hotel={hotel} store={store} brandStore={brandStore} />; },
      categories: function () { return <CategoriesModule hotel={hotel} store={store} menu={menu} route={route} />; },
      store: function () { return <StoreModule hotel={hotel} store={store} />; },
      reorder: function () {
        return (
          <ReorderModule signedIn={!!uid} store={store} menu={menu} place={short} onReorder={onReorder}
            orders={{ loading: ordersRes.loading, data: lastOrder }} />
        );
      },
      popular: function () {
        return (
          <PopularModule hotel={hotel} store={store} place={short} brand={brandHeader} pizzeria={pizzeria} pizzaClub={pizzaClub}
            popular={{ loading: popularRes.loading || (!!popularRes.data && !menu), error: popularRes.error, data: popular, reload: popularRes.reload }} />
        );
      },
    };

    var homeContent = kitchen && K.KitchenHome ? <K.KitchenHome hotel={hotel} store={store} menu={menu} route={route} design={kitchen} loyalty={loyalty} signedIn={!!uid} modules={mods} renderModule={render} lastOrder={lastOrder} onReorder={onReorder} /> : zeta && K.ZetaHome ? <K.ZetaHome hotel={hotel} store={store} menu={menu} route={route} design={zeta} branch={short}
            loyalty={loyalty} signedIn={!!uid} modules={mods} renderModule={render}
            popular={{ loading: popularRes.loading || (!!popularRes.data && !menu), error: popularRes.error, data: popular, reload: popularRes.reload }}
            orders={ordersRes} lastOrder={lastOrder} onReorder={onReorder} /> : pizzaClub && K.PizzaClubHome ? <K.PizzaClubHome hotel={hotel} store={store} menu={menu} route={route} design={pizzaClub} chain={chainRes.chain}
            loyalty={loyalty} signedIn={!!uid} modules={mods} renderModule={render} /> : pizzeria && K.PizzeriaHome ? <K.PizzeriaHome hotel={hotel} store={store} menu={menu} route={route} design={pizzeria}
            loyalty={loyalty} modules={mods} renderModule={render} /> : mods.map(function (m) {
            var node = render[m.key] ? render[m.key]() : null;
            return node ? <React.Fragment key={m.key}>{node}</React.Fragment> : null;
          });
    if ((pizzaClub || pizzeria || zeta || kitchen) && mods.some(function (m) { return m.key === 'gift-cards'; })) {
      homeContent = <React.Fragment>{homeContent}{render['gift-cards']()}</React.Fragment>;
    }

    return (
      <div className={ui.cx('oo-home', themed && 'oo-home--themed', brandHeader && 'oo-home--brand', accentOnHero && 'oo-home--accent-on-hero')}>
        {top}
        <main className="oo-home__body">
          <h1 className="oo-sr-only">{store.name}</h1>
          {!pizzaClub && !zeta && !kitchen && Prompt ? <Prompt key={hotel} hotel={hotel} store={store} route={route} summary manualOnly branch={branchName} /> : null}
          {homeContent}
        </main>
        <ui.Toast open={!!toastState[0]} onClose={function () { toastState[1](null); }} tone="warn">{toastState[0]}</ui.Toast>
        {AlertHost ? <AlertHost hotel={hotel} store={store} /> : null}
        {Gate ? (
          <Gate names={gateState[0].gated.map(function (l) { return l.name; })} store={store}
            onConfirm={function (dob) { onGate(true, dob); }} onDecline={function () { onGate(false); }} />
        ) : null}
      </div>
    );
  };
  // P4-17: Home renders with no branch (the chain home; src/app.jsx branchGate hands it the route as it is).
  OO.pages.Home.withoutBranch = true;
})();


// ===================== src/pages/app-stores.jsx =====================
// src/pages/app-stores.jsx: the customer app store picker (page id 'stores'; app mode only: /{chain}/stores), and the
// chain home's list (Batch 2 P4-17). Design: docs/online-order/design/customer-app.dc.txt "02 Select store". Owner: T4
// (Batch 1 app pages); the chain data P3-24 / P4-16; the chain screens P4-17. Styles: styles/app-pages.css (.oo-st-*).
// Shared helpers: OO.appKit (src/pages/app-home.jsx).
//
// Lists the CHAIN (D-03: the master and its branches, chainedshops; not getRestaurant's related_branches, which the About
// page keeps for "Other locations"): OO.useChain(route.chain) (P4-16: B31 through OO.data.loadChain; a hand-built route
// with no chain lists its store's chain, route.hotel), in the chain's order (the master first), with status (the open
// label as B31 sends it), distance and order-mode pills, filtered by the search box (name, suburb or postcode). A
// distance needs a position: the fixtures' simulated one in fixture mode (OO.appKit.demoOrigin, whatever the device
// reports), else the customer's own fix when it is known (OO.pages.FindStore.geo: one taken here or on the store finder
// in the last 15 minutes). "Use my current location" asks again (FindStore.geo.locate: 6.5 s, the fix cached 15
// minutes); on success the list is sorted nearest first; a refusal, no fix or a timeout only shows a short note. The
// cards are one native radio group (arrow keys move the choice).
// P4-17 (D-30 (a)): the page renders with or without a branch (OO.pages.Stores.withoutBranch: OO.App mounts it, with the
// route as it is, in place of a branch page while the chain's app has no branch). The active branch (route.hotel) is
// picked by default when the chain lists it; with no branch nothing is picked. With no branch it themes itself with the
// chain master's theme (OO.useChain's theme). Notices: a route store the chain doesn't list (deleted, inactive, hidden
// from listings, no POS: "{store} isn't taking orders here. Choose another store."); no branch while the branch saved
// for the chain is no longer listed (a branch that disappeared: GONE_TEXT); and, as the CTA's note, the switch notice
// (logic.switchNotice) while another store is picked and the active branch's cart holds items (that cart stays with its
// store: OO.store carts are per store).
// The bottom CTA "Order from {short name}" saves the branch for the chain (OO.store.setBranch: P4-11's per-chain record)
// and goes where the customer was going (p5-followups #3; logic.target): from a page the gate stood in for
// (route.page isn't 'stores': Home's chain home, the menu with a deep link or an SEO item path, the cart…) that same
// page with its params and query under the chosen branch, replacing the gate's URL; from Select store itself, the
// branch's menu (pushed). The chain's path is its master's key (B31), else route.chain.
//
// OO.pages.Stores.useChain(hotel, { origin, byDistance, theme }) → { loading, error, notFound, data: Chain | null, master,
//   reload }: OO.useChain's read (P4-16) under P3-24's names (data = the chain, normalised with the position).
// OO.pages.Stores.usePicker({ route, theme, pick, ctaClass, label }) → { chain, master, notFound, loading, selected,
//   body, cta }: the search, the location button and note, the notices and the list (body) and the bottom CTA (cta), as
//   Select store and Home's chain home (src/pages/app-home.jsx) render them. A hook: call it, don't mount it. pick: the
//   store picked at first (else the active branch when the chain lists it); ctaClass: a class for the CTA bar (Home's sits
//   above the tab bar); label: the CTA's words before a pick (default 'Choose a store').
// OO.pages.Stores.logic (pure):
//   switchNotice({ current: { key, name } | null, picked: key, count }) → text | null: the note while `picked` isn't
//     the active branch `current` and its cart holds `count` (> 0) items.
//   target(route, key, chain) → { href, replace }: where "Order from" goes for store `key` under the chain path `chain`.
//   GONE_TEXT: the notice for a saved branch the chain no longer lists.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var LOCATION_TEXT = {
    denied: 'Location access is off. Search by suburb or postcode instead.',
    failed: 'We couldn’t find your location. Search by suburb or postcode instead.',
    none: 'Location isn’t available on this device. Search by suburb or postcode instead.',
    done: 'Showing the closest stores first.',
  };
  // P4-17: no branch while the branch saved for the chain is no longer listed (see the header).
  var GONE_TEXT = 'The store you ordered from before isn’t taking orders here now. Choose another store.';
  var automaticAttempts = Object.create(null);

  // Only listed branches with valid coordinates can be selected from a location fix.
  function nearest(stores, origin) {
    if (!origin || !OO.model.validCoords(origin.lat, origin.lng)) return null;
    var found = null, distance = Infinity;
    (stores || []).forEach(function (store) {
      if (!OO.model.validCoords(store.lat, store.lng)) return;
      var km = OO.model.haversineKm(origin, { lat: store.lat, lng: store.lng });
      if (km < distance) { distance = km; found = store; }
    });
    return found;
  }

  function same(a, b) { return !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase(); }
  function matches(b, q) {
    if (!q) return true;
    var hay = (b.name + ' ' + b.shortName + ' ' + b.address).toLowerCase();
    return q.toLowerCase().split(/\s+/).filter(Boolean).every(function (w) { return hay.indexOf(w) >= 0; });
  }

  // The store finder's position helper (src/pages/find-store.jsx, earlier in the bundle), else null.
  function geo() { return OO.pages.FindStore && OO.pages.FindStore.geo ? OO.pages.FindStore.geo : null; }
  function deviceCanLocate() {
    try { return typeof navigator !== 'undefined' && !!navigator && !!navigator.geolocation && typeof navigator.geolocation.getCurrentPosition === 'function'; } catch (e) { return false; }
  }

  function useChain(hotel, opts) {
    opts = opts || {};
    var r = OO.useChain(hotel, { origin: opts.origin || null, byDistance: !!opts.byDistance, theme: !!opts.theme });
    return { loading: r.loading, error: r.error, notFound: r.notFound, data: r.chain, master: r.master, reload: r.reload };
  }

  // ---- logic (pure; see the header) ----
  function switchNotice(o) {
    o = o || {};
    var cur = o.current;
    var n = Math.floor(Number(o.count)) || 0;
    if (!cur || !cur.key || !o.picked || same(cur.key, o.picked) || n < 1) return null;
    return 'Your cart at ' + (cur.name || cur.key) + ' (' + n + (n === 1 ? ' item' : ' items') + ') is kept for that store.';
  }
  function target(route, key, chain) {
    var r = route || {};
    var opts = { hotel: key };
    if (chain) opts.chain = chain;
    if (r.page && r.page !== 'stores') {
      opts.query = r.query;
      return { href: OO.router.href(r.page, r.params || {}, opts), replace: true };
    }
    opts.query = OO.appKit.query();
    return { href: OO.router.href('menu', null, opts), replace: false };
  }

  function StoreCard(props) {
    var b = props.branch, on = props.on, ui = OO.ui;
    var descId = props.name + '-' + b.key.replace(/[^a-zA-Z0-9_-]/g, '_') + '-d';
    return (
      <label className={ui.cx('oo-st-card', on && 'is-on')}>
        <input className="oo-st-card__input" type="radio" name={props.name} value={b.key} checked={on}
          aria-label={b.name} aria-describedby={descId} onChange={function () { props.onPick(b.key); }} />
        <span className="oo-st-card__head">
          <span className="oo-st-card__text">
            <span className="oo-st-card__name">{b.name}</span>
            {b.address ? <span className="oo-st-card__addr" aria-hidden="true">{b.address}</span> : null}
          </span>
          <span className="oo-st-card__radio" aria-hidden="true" />
        </span>
        <span className="oo-sr-only" id={descId}>
          {[b.address, b.status.label, b.distanceLabel ? b.distanceLabel + ' away' : null, b.modesLabel].filter(Boolean).join(', ')}
        </span>
        <span className="oo-st-card__pills" aria-hidden="true">
          <ui.StatusPill open={b.open}>{b.status.label}</ui.StatusPill>
          {b.distanceLabel ? <ui.StatusPill tone="neutral">{b.distanceLabel}</ui.StatusPill> : null}
          {b.modesLabel ? <ui.StatusPill tone="neutral">{b.modesLabel}</ui.StatusPill> : null}
        </span>
      </label>
    );
  }

  function usePicker(o) {
    o = o || {};
    var route = o.route || {};
    var hotel = route.hotel || null;                  // the active branch, when there is one
    var key = route.chain || route.hotel || null;     // the chain to list (a hand-built route: its store's)
    var K = OO.appKit, ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var qState = React.useState('');
    var pickState = React.useState(o.pick || null);
    var locState = React.useState({ status: 'idle', origin: null, message: '' });
    var known = React.useState(function () { var g = geo(); return g ? g.read() : null; })[0];
    var alive = React.useRef(true);
    var manualPick = React.useRef(!!o.pick);
    var autoStarted = React.useRef(false);
    React.useEffect(function () { alive.current = true; return function () { alive.current = false; }; }, []);
    var loc = locState[0];
    var located = loc.status === 'done';
    var chain = useChain(key, { origin: loc.origin || K.demoOrigin() || known, byDistance: located, theme: !!o.theme });
    var groupName = 'oo-st-' + React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
    var locationMessageId = groupName + '-location';

    var c = chain.data;
    var list = c ? c.stores : [];
    var active = hotel && c ? OO.data.chainStore(c, hotel) : null;
    var selected = list.filter(function (b) { return b.key === pickState[0]; })[0] || active;
    var q = qState[0].trim();
    var shown = list.filter(function (b) { return matches(b, q); });
    var notFound = hotel ? h.notFound : chain.notFound;
    var chainPath = c && c.master && c.master.key ? c.master.key : route.chain || null;

    function pick(key) { manualPick.current = true; pickState[1](key); }
    function selectBranch(branch) {
      OO.store.setBranch(branch.key, chainPath);
      var t = target(route, branch.key, chainPath);
      OO.router.go(t.href, t.replace ? { replace: true } : undefined);
    }

    React.useEffect(function () {
      if (!c || list.length < 2 || hotel || manualPick.current || autoStarted.current || OO.mode !== 'app') return;
      autoStarted.current = true;
      // A prior choice, including a removed branch, requires a deliberate manual replacement.
      if (OO.router.branchOf && OO.router.branchOf(key)) return;
      if (automaticAttempts[key]) {
        locState[1]({ status: 'error', origin: null, message: 'Choose the branch you would like to order from.' });
        return;
      }
      var g = geo();
      if (!g || !deviceCanLocate()) {
        locState[1]({ status: 'error', origin: null, message: LOCATION_TEXT.none });
        return;
      }
      locState[1]({ status: 'locating', origin: null, message: 'Finding your nearest branch…' });
      // Share one permission request across remounts; each picker checks its own active/manual state.
      var request = automaticAttempts[key] = g.locate();
      request.then(function (pos) {
        if (!alive.current || manualPick.current) return;
        var branch = nearest(list, pos);
        locState[1]({ status: branch ? 'done' : 'error', origin: pos,
          message: branch ? LOCATION_TEXT.done : 'Choose a branch. We could not compare these store locations.' });
        if (branch) selectBranch(branch);
      }, function (err) {
        if (!alive.current || manualPick.current) return;
        locState[1]({ status: 'error', origin: null,
          message: (err && err.code === 'denied' ? LOCATION_TEXT.denied : LOCATION_TEXT.failed) + ' Choose a branch below.' });
      });
    }, [c, key, hotel]);

    function locate() {
      var g = geo();
      if (!g || !deviceCanLocate()) {
        locState[1]({ status: 'error', origin: null, message: LOCATION_TEXT.none });
        return;
      }
      locState[1]({ status: 'locating', origin: loc.origin, message: '' });
      g.locate().then(function (pos) {
        if (!alive.current) return;
        locState[1]({ status: 'done', origin: K.demoOrigin() || pos, message: LOCATION_TEXT.done });
      }, function (err) {
        if (!alive.current) return;
        locState[1]({ status: 'error', origin: null, message: err && err.code === 'denied' ? LOCATION_TEXT.denied : LOCATION_TEXT.failed });
      });
    }

    function orderFrom() {
      if (!selected) return;
      manualPick.current = true;
      selectBranch(selected);
    }

    var family = React.useState('all'), mapView = React.useState(false);
    var pizzaClub = OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(route.chain || key);
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign(route.chain || key);
    if (pizzaClub && family[0] !== 'all') shown = shown.filter(function (b) { return /\bmax\b/i.test(b.name) === (family[0] === 'max'); });
    var listNode;
    if (notFound) listNode = <K.NotFound />;
    else if (chain.error && !c) {
      listNode = <K.ErrorCard title="We couldn't load the stores" onRetry={chain.reload} />;
    } else if (!c) {
      listNode = <K.ListSkeleton count={3} height={112} />;
    } else if (!list.length) {
      listNode = (
        <ui.EmptyState className="oo-app-state" icon="Store" title="No stores to choose from"
          body="None of these stores can take online orders right now." />
      );
    } else if (!shown.length) {
      listNode = (
        <ui.EmptyState className="oo-app-state" icon="Search" title={'No stores match “' + q + '”'}
          body="Try another suburb, postcode or store name." />
      );
    } else {
      listNode = (
        <div className="oo-st-list" role={zeta ? 'list' : 'radiogroup'} aria-label="Stores">
          {shown.map(function (b) {
            if (zeta && K.ZetaStoreCard) return <K.ZetaStoreCard key={b.key} branch={b} selected={!!active && active.key === b.key} route={route} onSelect={function () { pick(b.key); selectBranch(b); }} />;
            return pizzaClub ? <article key={b.key} className="oo-pc-store-card"><StoreCard branch={b} name={groupName} on={!!selected && b.key === selected.key} onPick={pick} /><div className="oo-pc-store-actions"><button type="button" onClick={function () { pick(b.key); selectBranch(b); }}>Order from here</button>{OO.model.validCoords(b.lat,b.lng) ? <a href={'https://www.google.com/maps/dir/?api=1&destination='+b.lat+','+b.lng} target="_blank" rel="noopener noreferrer" aria-label={'Directions to '+b.name}><OO.icons.Pin size={17}/></a> : null}</div></article> : <StoreCard key={b.key} branch={b} name={groupName} on={!!selected && b.key === selected.key} onPick={pick} />;
          })}
        </div>
      );
    }
    // The route's store isn't in its chain's list: it can't be ordered from here, so the customer chooses.
    var away = !!hotel && !!c && !active && list.length > 0 && !notFound;
    // P4-17: no branch while the one saved for the chain has left its list (see the header).
    var saved = !hotel && c && key && OO.router.branchOf ? OO.router.branchOf(route.chain || key) : null;
    var gone = !!saved && list.length > 0 && !OO.data.chainStore(c, saved);
    // P4-17: the switch notice: the active branch's cart stays with it (OO.store.cartOf: kept per store).
    var note = selected && hotel ? switchNotice({
      current: { key: hotel, name: active ? active.shortName || active.name : h.store ? h.store.name : hotel },
      picked: selected.key, count: OO.store.cartOf(hotel).count,
    }) : null;

    var body = (
      <React.Fragment>
        <ui.SearchField value={qState[0]} onChange={qState[1]} placeholder="Search suburb or postcode"
          label="Search stores by name, suburb or postcode" />
        <button type="button" className="oo-st-locate" onClick={locate} disabled={loc.status === 'locating'}
          aria-describedby={locationMessageId}>
          <OO.icons.Locate size={16} />
          <span>{loc.status === 'locating' ? 'Finding your location…' : 'Use my current location'}</span>
        </button>
        <p id={locationMessageId} className={ui.cx('oo-st-locmsg', loc.status === 'error' && 'is-error')} aria-live="polite">
          {loc.message}
        </p>
        {away ? (
          <ui.InlineNotice tone="warn">
            {(h.store && h.store.name ? h.store.name : 'This store') + ' isn’t taking orders here. Choose another store.'}
          </ui.InlineNotice>
        ) : null}
        {gone ? <ui.InlineNotice tone="warn">{GONE_TEXT}</ui.InlineNotice> : null}
        {pizzaClub ? <React.Fragment><div className="oo-pc-store-filters"><div role="group" aria-label="Filter by store family">{[['all','All'],['club','Pizza Club'],['max','Max']].map(function (f) { return <button key={f[0]} type="button" aria-pressed={family[0] === f[0]} onClick={function () { family[1](f[0]); }}>{f[1]}</button>; })}</div><div role="group" aria-label="Store view"><button type="button" aria-pressed={!mapView[0]} onClick={function () { mapView[1](false); }}>List</button><button type="button" aria-pressed={mapView[0]} onClick={function () { mapView[1](true); }}>Map</button></div></div><div className="oo-pc-store-count"><h2>All stores</h2><span>{shown.length} of {list.length}</span></div>{note ? <ui.InlineNotice tone="warn">{note}</ui.InlineNotice> : null}</React.Fragment> : null}
        {pizzaClub && mapView[0] && K.PizzaClubStoreMap ? <K.PizzaClubStoreMap branches={shown} selected={selected} onPick={pick} /> : listNode}
      </React.Fragment>
    );
    var short = selected ? selected.shortName || selected.name : '';
    var cta = notFound ? null : (
      <ui.BottomBarCTA className={o.ctaClass} note={note || undefined}>
        <ui.Button block disabled={!selected} onClick={orderFrom}>
          {selected ? 'Order from ' + short : o.label || 'Choose a store'}
        </ui.Button>
      </ui.BottomBarCTA>
    );
    return {
      pizzaClub: pizzaClub, zeta: zeta, mapView: mapView[0], chain: c, master: chain.master, notFound: notFound, loading: !c && !chain.error && !notFound,
      selected: selected, body: body, cta: cta,
    };
  }

  OO.pages.Stores = function Stores(props) {
    var route = props.route;
    var K = OO.appKit, ui = OO.ui;
    var P = usePicker({ route: route, theme: !route.hotel, pick: props.pick });
    if (P.zeta) return <main className="oo-app-content oo-zeta-stores"><div className="oo-app-content__hero"><small className="oo-app-content__eyebrow">Find your local</small><h1>Stores</h1><p>Find a store, check opening hours and order your Greek favourites.</p></div>{P.body}</main>;
    return (
      <div className="oo-apg oo-st">
        <ui.PageHeader title={P.pizzaClub ? "Find your local club" : "Select store"} subtitle={P.pizzaClub && P.chain ? P.chain.stores.length + " stores ready for online ordering." : undefined} onBack={function () { K.back(route.hotel); }} />
        <main className="oo-apg__body">{P.body}</main>
        {P.pizzaClub && !P.mapView ? null : P.cta}
      </div>
    );
  };
  // P4-17: renders with no branch (src/app.jsx branchGate hands it the route as it is).
  OO.pages.Stores.withoutBranch = true;
  OO.pages.Stores.useChain = useChain;
  OO.pages.Stores.usePicker = usePicker;
  OO.pages.Stores.logic = { switchNotice: switchNotice, target: target, nearest: nearest, GONE_TEXT: GONE_TEXT };
})();


// ===================== src/pages/app-pizzeria.jsx =====================
// Bee's presentation on the common store, menu, availability, membership and checkout engines.
(function () {
  'use strict';
  var OO = window.OO, K = OO.appKit;
  function Link(props) {
    var l = props.hash ? OO.router.link(props.page, null, { hotel: props.hotel, query: K.query(props.query), hash: props.hash }) : K.link(props.hotel, props.page, null, props.query);
    return <a className={props.className} href={l.href} onClick={l.onClick} aria-label={props.ariaLabel}>{props.children}</a>;
  }
  K.PizzeriaHeader = function PizzeriaHeader(props) {
    var theme = props.theme, logo = (props.store.theme && props.store.theme.logoMark) || theme.logo || props.store.logoUrl;
    var name = theme.brand || props.store.name;
    return <header className="oo-pizza-header"><Link hotel={props.hotel} page="home" className="oo-pizza-brand" ariaLabel={name + ' home'}>
      {logo ? <img src={logo} alt="" /> : <OO.icons.Store size={28} />}{theme.showStoreName !== false ? <strong>{name}</strong> : null}
      </Link><Link hotel={props.hotel} page="cart" className="oo-pizza-basket" ariaLabel="Open basket"><OO.icons.Bag size={24} /></Link>
    </header>;
  };
  K.PizzeriaHome = function PizzeriaHome(props) {
    var hotel = props.hotel, store = props.store, menu = props.menu, ui = OO.ui;
    var page = OO.pages.Menu;
    var dsp = page.shared.useDisplay(hotel, store, menu, OO.mode);
    var view = page.logic.buildView(menu, [], dsp.display);
    var sections = OO.model.menuLayout(store, props.route.query) === 'sections';
    var search = React.useState('');
    var hero = props.design.hero || {}, brand = store.theme.brand || store.name;
    var enabled = function (key) { return props.modules.some(function (m) { return m.key === key && m.enabled; }); };
    var combos = view.comboOrder.map(function (id) { return menu && menu.combos[id]; }).filter(Boolean).slice(0, 8);
    var ctx = { store: store, menu: menu, view: view, display: dsp.display, natural: true,
      openCombo: function (c) { K.go(hotel, 'menu', null, { type: 'combo', id: String(c.id) }); },
      openProduct: function (p) { K.go(hotel, 'menu', null, { type: 'product', id: String(p.id) }); },
      tell: function () {} };
    return <div className="oo-pizza-home">
      <section className="oo-pizza-discover" aria-labelledby="oo-pizza-title">
        <h1 id="oo-pizza-title">What are you craving?</h1><p>Your favourites, made your way.</p>
        <ui.SearchField value={search[0]} onChange={search[1]} onSubmit={function (q) { K.go(hotel, 'menu', null, { q: q }); }}
          placeholder="Search pizzas, sides & drinks" className="oo-pizza-search" />
        <nav className="oo-pizza-chips" aria-label="Browse food categories">
          <Link hotel={hotel} page="menu" className="is-all">All items</Link>
          {view.categories.map(function (c) { return <Link key={c.id} hotel={hotel} page="menu" query={sections ? null : { cat: String(c.id) }} hash={sections ? view.anchor[String(c.id)] : null}>{c.name}</Link>; })}
        </nav>
      </section>
      {enabled('slider') ? <Link hotel={hotel} page="menu" className="oo-pizza-hero">
        <span><small>Fresh from the oven</small><strong>{hero.title || 'Make it a pizza night.'}</strong>
          <span>{hero.buttonLabel || 'Order your favourites'} <OO.icons.ChevronRight size={16} /></span></span>
        {hero.image ? <img src={hero.image} alt="" /> : null}
      </Link> : null}
      {enabled('loyalty') ? <section className="oo-pizza-rewards" aria-labelledby="oo-pizza-rewards-title">
        <div className="oo-pizza-section-head"><h2 id="oo-pizza-rewards-title">Rewards Zone</h2><Link hotel={hotel} page="rewards">Explore <OO.icons.ChevronRight size={16} /></Link></div>
        {props.loyalty.signedIn ? props.renderModule.loyalty() : <div className="oo-pizza-member">
          <div className="oo-pizza-member-head"><span><OO.icons.Gift size={19} /> My {brand}</span><small>Membership</small></div>
          <h3>Your next slice starts here.</h3><p>Sign in to see your member card, points and available rewards.</p>
          <Link hotel={hotel} page="account" className="oo-btn oo-btn--primary">Sign in or create account <OO.icons.ChevronRight size={18} /></Link>
        </div>}
        <nav className="oo-pizza-reward-links" aria-label="Rewards Zone">
          {[['rewards', 'Rewards', 'Your reward catalogue', 'Gift'], ['wallet', 'Wallet', 'Points & member card', 'Scan'],
            ['coupons', 'Coupons', 'Your available offers', 'Ticket'], ['deals', 'Store offers', 'Explore current specials', 'Bag']].map(function (item) {
            return <Link key={item[0]} hotel={hotel} page={item[0]}><ui.Icon name={item[3]} size={20} /><strong>{item[1]}</strong><span>{item[2]}</span></Link>;
          })}
        </nav>
      </section> : null}
      {combos.length ? <section className="oo-pizza-combos" aria-labelledby="oo-pizza-combos-title">
        <div className="oo-pizza-section-head"><h2 id="oo-pizza-combos-title">Offers &amp; combos</h2><Link hotel={hotel} page="menu">View all <OO.icons.ChevronRight size={16} /></Link></div>
        <div className="oo-pizza-combo-list">{page.shared.itemCards(ctx, combos, [])}</div>
      </section> : null}
      {enabled('popular') ? props.renderModule.popular() : null}
      {enabled('reorder') ? props.renderModule.reorder() : null}
      {enabled('store') ? props.renderModule.store() : null}
    </div>;
  };
})();


// ===================== src/pages/app-pizzaclub.jsx =====================
// Pizza Club's app presentation. Ordering, offer eligibility, rewards and branch selection use the common engines.
(function () {
  'use strict';
  var OO = window.OO, K = OO.appKit, ui = OO.ui;
  function Link(p) {
    var l = OO.router.link(p.page, null, { hotel: p.hotel, query: K.query(p.query), hash: p.hash });
    return <a className={p.className} href={l.href} onClick={l.onClick} aria-label={p.label}>{p.children}</a>;
  }
  function design(route) { return OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign((route || {}).chain || (route || {}).hotel); }
  function Artwork(p) {
    var failed = React.useState(false);
    return p.src && !failed[0] ? <ui.Image cover src={p.src} sizes={p.sizes || '54px'} alt={p.alt || ''} loading={p.priority ? 'eager' : 'lazy'} fetchpriority={p.priority ? 'high' : undefined} onError={function () { failed[1](true); }} /> : <OO.icons.ImagePlaceholder size={28} />;
  }
  function Section(p) { return <section className={'oo-pc-section ' + (p.className || '')} id={p.id}>
    <div className="oo-pc-section-head"><h2>{p.title}</h2>{p.page ? <Link hotel={p.hotel} page={p.page} hash={p.hash}>See all</Link> : null}</div>{p.children}
  </section>; }
  function OrderStrip(p) {
    var st = p.status, type = st.mine ? st.mine.orderType : st.type;
    var when = st.need === 'fresh' ? p.summary.title.split(' · ')[1] : 'Choose time';
    if (when && when.indexOf('ASAP') === 0) when = 'ASAP';
    return <div className="oo-pc-order-strip">
      <Link hotel={p.hotel} page="stores" className="oo-pc-order-store" label={'Change store, currently ' + p.store.name}>
        <OO.icons.Bag size={17} /><span><small>{type === 'DELIVERY' ? 'Delivery from' : 'Pickup from'}</small><strong>{p.store.name}</strong></span><OO.icons.ChevronDown size={16} />
      </Link><button type="button" className="oo-pc-order-time" onClick={p.onOpen} aria-label={'Change order time, currently ' + when}>
        <OO.icons.Clock size={17} /><span><small>Ready</small><strong>{when}</strong></span><OO.icons.ChevronDown size={13} />
      </button>
    </div>;
  }
  ui.PizzaClubHeader = function PizzaClubHeader(p) {
    var route = p.route, h = OO.useHotel(route.hotel, { withMenu: false }), chain = OO.useChain(route.chain || route.hotel, { theme: false });
    var drawer = React.useState(false), store = h.store, brand = chain.master || store;
    var theme = brand ? OO.theme.resolve(brand.theme) || {} : {};
    var logo = theme.logo || (brand && brand.logoUrl), name = theme.brand || 'Pizza Club';
    var Prompt = K.OrderPrompt, ready = K.orderSheet && K.orderSheet.ready && K.orderSheet.ready();
    var hotel = route.hotel || route.chain;
    var strip = ['stores', 'account', 'profile'].indexOf(route.page) < 0;
    var nav = [['home','Home','Home'],['menu','Menu','Book'],['rewards','Club','Star'],['stores','Stores','Pin'],['account','Account','User'],['orders','Orders','Receipt'],['wallet','Wallet','Scan'],['coupons','Coupons','Ticket'],['deals','Deals','Gift'],['about','About this store','Info']];
    return <React.Fragment><header className="oo-pc-header">
      <div className="oo-pc-header-row"><button type="button" aria-label="Open navigation" aria-expanded={drawer[0]} onClick={function () { drawer[1](true); }}><OO.icons.MenuList size={21} /></button>
        <Link hotel={hotel} page="home" className="oo-pc-logo" label={name + ' app home'}>{logo ? <ui.Image src={logo} sizes="94px" alt={name} /> : <strong>{name}</strong>}</Link>
        <Link hotel={hotel} page="cart" className="oo-pc-cart" label="Open cart"><OO.icons.Cart size={21} /></Link></div>
      {strip ? store && ready ? <Prompt key={route.hotel} hotel={route.hotel} store={store} route={route} summary manualOnly
        renderSummary={function (state) { return <OrderStrip {...state} hotel={route.hotel} store={store} />; }} /> : <div className="oo-pc-order-strip oo-pc-order-strip--empty"><Link hotel={hotel} page="stores"><OO.icons.Pin size={17} /> Choose your store</Link></div> : null}
    </header>
    <ui.Sheet open={drawer[0]} title={name} className="oo-pc-navigation" onClose={function () { drawer[1](false); }}>
      <nav aria-label="Pizza Club navigation">{nav.map(function (item) { var l = K.link(hotel, item[0]), Icon = OO.icons[item[2]]; return <a key={item[0]} href={l.href} onClick={function (e) { drawer[1](false); l.onClick(e); }}><Icon size={20} />{item[1]}<OO.icons.ChevronRight size={16} /></a>; })}</nav>
    </ui.Sheet></React.Fragment>;
  };

  // Device favourites are separate from carts, pricing and customer accounts, and remain scoped to the branch.
  var favouriteRevision = 0, favouriteSubscribers = [];
  function favKey(hotel) { return 'oo-pizzaclub-favourites-v1:' + String(hotel || '').toLowerCase(); }
  function readFavourites(hotel) {
    try { var list = JSON.parse(window.localStorage.getItem(favKey(hotel)) || '[]'); return Array.isArray(list) ? list.filter(function (id) { return /^product:\d+$|^combo:\d+$/.test(id); }).slice(0,100) : []; } catch (e) { return []; }
  }
  function toggleFavourite(hotel, id) {
    var list = readFavourites(hotel), index = list.indexOf(id);
    if (index >= 0) list.splice(index,1); else list.push(id);
    try { window.localStorage.setItem(favKey(hotel), JSON.stringify(list.slice(-100))); } catch (e) { return; }
    favouriteRevision++; favouriteSubscribers.slice().forEach(function (fn) { fn(); });
  }
  function useFavourites(hotel) {
    React.useSyncExternalStore(function (fn) { favouriteSubscribers.push(fn); return function () { favouriteSubscribers = favouriteSubscribers.filter(function (f) { return f !== fn; }); }; }, function () { return favouriteRevision; }, function () { return favouriteRevision; });
    return readFavourites(hotel);
  }
  K.PizzaClubFavouriteCard = function FavouriteCard(p) {
    var ids = useFavourites(p.hotel), active = ids.indexOf(p.kind + ':' + p.item.id) >= 0;
    return <div className="oo-pc-favourite-card">{p.children}<button type="button" className={ui.cx('oo-pc-heart',active && 'is-saved')} aria-label={(active ? 'Remove ' : 'Save ') + p.item.name + (active ? ' from favourites' : ' to favourites')} aria-pressed={active}
      onClick={function (e) { e.stopPropagation(); toggleFavourite(p.hotel,p.kind + ':' + p.item.id); }}><OO.icons.Heart size={17} filled={active} /></button></div>;
  };
  K.pizzaClubFavourites = { read: readFavourites, toggle: toggleFavourite };
  K.useDeviceFavourites = useFavourites;
  K.DeviceFavouriteCard = K.PizzaClubFavouriteCard;

  K.PizzaClubDealsPreview = function DealsPreview(p) {
    var selected = React.useState(0), reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var doc = design(p.route) || {}, hero = doc.hero || {};
    var deals = p.catalog ? p.catalog.deals.filter(function (d) { return d.orderable !== false; }).slice(0,3) : [];
    var slides = deals.map(function (d) { return {title:d.title,eyebrow:d.badge || d.offer || 'Club deal',body:d.minOrder ? 'Min. order ' + OO.format.money(d.minOrder,p.store.currency) : d.description,image:d.image,page:'deals',button:'View deal'}; });
    slides.push({title:'Join free.',eyebrow:'The Club',body:'Earn points and unlock member benefits.',image:hero.image,page:'rewards',button:'See benefits'});
    React.useEffect(function () { if (reduced || slides.length < 2 || p.variant !== 'hero') return; var timer = window.setInterval(function () { selected[1](function (i) { return (i+1)%slides.length; }); },6500); return function () { window.clearInterval(timer); }; },[slides.length,reduced,p.variant]);
    if (p.variant === 'rail') return !deals.length ? null : <Section hotel={p.hotel} title="Deals right now" page="deals"><div className="oo-pc-deals-rail">{deals.map(function (d) { return <Link key={d.key} hotel={p.hotel} page="deals"><Artwork src={d.image} /><strong>{d.title}</strong><span>{d.badge || d.offer}</span></Link>; })}</div></Section>;
    var slide = slides[selected[0]%slides.length];
    return <section className="oo-pc-promos" aria-label="Pizza Club highlights"><Link hotel={p.hotel} page={slide.page} className="oo-pc-banner">
      <div><small>{slide.eyebrow}</small><h2>{slide.title}</h2><p>{slide.body}</p><span className="oo-pc-banner-button">{slide.button}</span></div><Artwork src={slide.image} priority sizes="240px" />
      </Link><div className="oo-pc-dots" role="group" aria-label="Choose banner">{slides.map(function (s,i) { return <button key={i} type="button" aria-label={'Show banner ' + (i+1)} aria-pressed={selected[0]%slides.length === i} onClick={function () { selected[1](i); }} />; })}</div></section>;
  };
  K.PizzaClubClub = function Club(p) {
    return <div className="oo-pc-club"><div className="oo-pc-club-title"><h1>The Club</h1><p>Points, rewards and member-only deals</p>{p.signedIn ? <ui.HeaderLink onClick={p.onHow}>How it works</ui.HeaderLink> : null}</div>
      {!p.signedIn ? <div className="oo-pc-unlock"><OO.icons.Lock size={24} /><span><strong>Unlock Club benefits</strong><small>Sign in for points, rewards and coupons.</small></span><Link hotel={p.hotel} page="profile" query={{directLogin:'true'}} className="oo-pc-signin">Sign in</Link></div> : null}
      <nav className="oo-pc-club-links" aria-label="Club benefits">{[['wallet','Wallet','Member card','Scan'],['rewards','Rewards','Earn points','Gift'],['coupons','Coupons','Member deals','Ticket']].map(function (x) { return <Link key={x[0]} hotel={p.hotel} page={x[0]} query={x[0] === 'rewards' ? {catalogue:'true'} : null}><ui.Icon name={x[3]} size={18} /><strong>{x[1]}</strong><small>{x[2]}</small></Link>; })}</nav>
      <p className="oo-pc-club-note"><OO.icons.Gift size={16} /> Earn points when you order signed in. New rewards appear here as soon as you qualify.</p>
      {p.signedIn ? p.children : null}<section className="oo-pc-club-deals"><h2>Club deals</h2><p><OO.icons.Pin size={14} />{p.store.name}</p><OO.pages.Deals route={p.route} mode="app" embedded /></section>
    </div>;
  };
  function mapPoint(branch,zoom) {
    var scale = 256*Math.pow(2,zoom), lat = Math.max(-85,Math.min(85,branch.lat))*Math.PI/180;
    return { x:(branch.lng+180)/360*scale, y:(1-Math.log(Math.tan(lat)+1/Math.cos(lat))/Math.PI)/2*scale };
  }
  K.PizzaClubStoreMap = function StoreMap(p) {
    var zoom = React.useState(7), pan = React.useState({x:0,y:0}), drag = React.useRef(null);
    var width = Math.min(window.innerWidth || 480,480)-32, height = 280;
    var branches = p.branches.filter(function(b){return OO.model.validCoords(b.lat,b.lng);});
    var centerBranch = p.selected && OO.model.validCoords(p.selected.lat,p.selected.lng) ? p.selected : branches[0];
    if (!centerBranch) return <ui.EmptyState icon="Pin" title="No store locations available" />;
    var center = mapPoint(centerBranch,zoom[0]);center.x -= pan[0].x;center.y -= pan[0].y;
    var tiles = [], n = Math.pow(2,zoom[0]);
    for (var x=Math.floor((center.x-width/2)/256);x<=Math.floor((center.x+width/2)/256);x++) for(var y=Math.floor((center.y-height/2)/256);y<=Math.floor((center.y+height/2)/256);y++) if(y>=0 && y<n) tiles.push({x:x,y:y});
    function changeZoom(delta){var next=Math.max(4,Math.min(16,zoom[0]+delta));pan[1]({x:pan[0].x*Math.pow(2,next-zoom[0]),y:pan[0].y*Math.pow(2,next-zoom[0])});zoom[1](next);}
    return <div className="oo-pc-store-map" role="region" aria-label="Pizza Club store map" style={{height:height}}
      onPointerDown={function(e){if(e.target.closest('button,a'))return;drag.current={x:e.clientX,y:e.clientY,pan:pan[0]};e.currentTarget.setPointerCapture(e.pointerId);}}
      onPointerMove={function(e){if(drag.current)pan[1]({x:drag.current.pan.x+e.clientX-drag.current.x,y:drag.current.pan.y+e.clientY-drag.current.y});}}
      onPointerUp={function(){drag.current=null;}} onPointerCancel={function(){drag.current=null;}}>
      {tiles.map(function(t){return <img key={zoom[0]+':'+t.x+':'+t.y} src={'https://tile.openstreetmap.org/'+zoom[0]+'/'+((t.x%n+n)%n)+'/'+t.y+'.png'} alt="" draggable="false" referrerPolicy="strict-origin-when-cross-origin" style={{left:t.x*256-center.x+width/2,top:t.y*256-center.y+height/2}}/>;})}
      {branches.map(function(b){var point=mapPoint(b,zoom[0]),x=point.x-center.x+width/2,y=point.y-center.y+height/2;if(x < 0 || x > width || y < 0 || y > height)return null;return <button key={b.key} type="button" className={ui.cx('oo-pc-map-pin',p.selected && p.selected.key===b.key && 'is-selected')} style={{left:x,top:y}} aria-label={'Select '+b.name} title={b.name} onClick={function(){pan[1]({x:0,y:0});p.onPick(b.key);}}><OO.icons.Pin size={23}/></button>;})}
      <div className="oo-pc-map-zoom"><button type="button" aria-label="Zoom in" disabled={zoom[0]===16} onClick={function(){changeZoom(1);}}>+</button><button type="button" aria-label="Zoom out" disabled={zoom[0]===4} onClick={function(){changeZoom(-1);}}>−</button></div>
      <a className="oo-pc-map-attribution" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>
    </div>;
  };
  K.PizzaClubAccount = function Account(p) {
    var route = Object.assign({}, p.route, { query: Object.assign({}, p.route.query, { directLogin: 'true' }) });
    return <div className="oo-pc-account"><small>Your Pizza Club</small><h1>Account</h1><div className="oo-pc-account-welcome"><OO.icons.User size={22}/><small>Member access</small><h2>Good pizza, made personal.</h2><p>Use your email to sign in or create a Pizza Club account.</p><div className="oo-pc-account-progress"><span/><span/><span/></div></div>
      <OO.pages.Profile route={route} mode="app"/><nav className="oo-pc-account-benefits" aria-label="Member benefits">{[['rewards','Club rewards','Star'],['orders','Quick reorder','Bag'],['deals','Member offers','Gift']].map(function(x){return <Link key={x[0]} hotel={p.route.hotel} page={x[0]}><ui.Icon name={x[2]} size={17}/><strong>{x[1]}</strong></Link>;})}</nav>
    </div>;
  };
  K.PizzaClubHome = function PizzaClubHome(p) {
    var hotel = p.hotel, store = p.store, menu = p.menu, M = OO.pages.Menu;
    var search = React.useState(''), favourites = useFavourites(hotel);
    var dsp = M.shared.useDisplay(hotel,store,menu,OO.mode), view = M.logic.buildView(menu,[],dsp.display);
    var sections = OO.model.menuLayout(store,p.route.query) === 'sections';
    var enabled = function (key) { return p.modules.some(function (m) { return m.key === key && m.enabled; }); };
    var open = OO.model.openStatus(store,OO.data.now());
    var ctx = {store:store,menu:menu,view:view,display:dsp.display,natural:true,showAddIcon:true,
      openCombo:function(c){ K.go(hotel,'menu',null,{type:'combo',id:String(c.id)}); },openProduct:function(i){ K.go(hotel,'menu',null,{type:'product',id:String(i.id)}); },tell:function(){} };
    var favProducts = menu ? favourites.filter(function (id) { return id.indexOf('product:') === 0; }).map(function(id){return menu.products[id.split(':')[1]];}).filter(function(i){return i && !!view.products[i.id];}) : [];
    var favCombos = menu ? favourites.filter(function (id) { return id.indexOf('combo:') === 0; }).map(function(id){return menu.combos[id.split(':')[1]];}).filter(function(i){return i && !!view.combos[i.id];}) : [];
    var branches = p.chain ? p.chain.stores.slice() : [];
    var selectedBranch = branches.filter(function(b){return b.key === store.onlineKey;})[0];
    if (selectedBranch) branches.sort(function(a,b){ if(a.key===selectedBranch.key)return -1;if(b.key===selectedBranch.key)return 1;return OO.model.haversineKm(selectedBranch,a)-OO.model.haversineKm(selectedBranch,b); });
    return <div className="oo-pc-home">
      <section className="oo-pc-discover"><small>{open.isOpen ? 'Welcome back' : 'Still hungry'}</small><h1>{open.isOpen ? 'What sounds good today?' : 'Plan something delicious.'}</h1><p>{open.isOpen ? 'Fresh pizza, ready your way.' : 'Pre-orders are open for later.'}</p>
        <ui.SearchField value={search[0]} onChange={search[1]} onSubmit={function(q){K.go(hotel,'menu',null,{q:q});}} placeholder="What are you craving?" label="Search the Pizza Club menu" />
      </section>
      {enabled('slider') ? <OO.pages.Deals route={p.route} mode="app" preview="hero" /> : null}
      {enabled('categories') ? <nav className="oo-pc-categories" aria-label="Browse all Pizza Club categories"><Link hotel={hotel} page="deals"><Artwork src={(p.design.hero || {}).image} /><strong>Deals</strong></Link>
        {view.categories.map(function(c){ var first=menu && c.productIds && menu.products[c.productIds[0]];return <Link key={c.id} hotel={hotel} page="menu" query={sections ? null : {cat:String(c.id)}} hash={sections ? view.anchor[String(c.id)] : null}><Artwork src={c.image || (first && first.image) || (p.design.hero || {}).image} /><strong>{c.name}</strong></Link>; })}
      </nav> : null}
      {enabled('loyalty') ? <section className="oo-pc-membership"><Link hotel={hotel} page="deals" className="oo-pc-exclusive"><small>App exclusive</small><strong>Deals you only get in the app.</strong><span>Member-only pricing and faster checkout.</span><OO.icons.ChevronRight size={19} /></Link>
        {p.loyalty.signedIn ? p.renderModule.loyalty() : <div className="oo-pc-join"><span><strong>Join free. Earn points.</strong><small>Members unlock rewards and coupons.</small></span><Link hotel={hotel} page="profile" query={{directLogin:'true'}}>Sign in</Link></div>}
      </section> : null}
      {enabled('popular') ? <div className="oo-pc-popular">{p.renderModule.popular()}</div> : null}
      <Section title="Your favourites" hotel={hotel} page="home" hash="pc-favourites" id="pc-favourites">{favProducts.length || favCombos.length ? <div className="oo-pc-favourites">{M.shared.itemCards(ctx,favCombos,favProducts)}</div> : <Link hotel={hotel} page="menu" className="oo-pc-empty-favourites"><OO.icons.Heart size={25} /><span><strong>Keep favourites close</strong><small>Tap the heart on any pizza or combo.</small></span><OO.icons.ChevronRight size={16} /></Link>}</Section>
      {enabled('reorder') ? <Section title="Reorder your last meal" hotel={hotel}>{p.signedIn ? p.renderModule.reorder() : <Link hotel={hotel} page="profile" query={{directLogin:'true'}} className="oo-pc-reorder"><OO.icons.Receipt size={30}/><span><strong>Your favourites, one tap away</strong><small>Sign in to reorder a recent meal.</small><b>Sign in</b></span></Link>}</Section> : null}
      {enabled('slider') ? <OO.pages.Deals route={p.route} mode="app" preview="rail" /> : null}
      {enabled('store') ? <Section title="Nearest stores" hotel={hotel} page="stores"><div className="oo-pc-nearby">{branches.slice(0,2).map(function(b){return <Link key={b.key} hotel={hotel} page="stores"><OO.icons.Pin size={20}/><span>{b.key===store.onlineKey?<small>Selected</small>:null}<strong>{b.shortName || b.name}</strong><small>{b.openLabel || ''}{b.distanceLabel ? ' · '+b.distanceLabel : ''}</small><small>{b.address}</small></span><OO.icons.ChevronRight size={16}/></Link>;})}</div></Section> : null}
    </div>;
  };
})();


// ===================== src/pages/app-kkitchen.jsx =====================
// Dining-app presentation. All selections, availability, member prices, authentication and payments use SIPO's shared engines.
(function () {
  'use strict';
  var OO = window.OO, K = OO.appKit, ui = OO.ui;
  function config(route) { var app = OO.whitelabel.kitchenDesign(route.chain || route.hotel); return app && app.dining || {}; }
  function Link(p) {
    var link = K.link(p.hotel, p.page || 'menu', p.params || null, p.query);
    return <a href={link.href} className={p.className} aria-label={p.label} onClick={function (e) { if (p.onNavigate) p.onNavigate(); link.onClick(e); }}>{p.children}</a>;
  }
  function title(name) { return name && name === name.toUpperCase() ? name.toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : name; }
  function artwork(d, item) {
    var row = (d.productImages || []).find(function (p) { return String(p.productId) === String(item.id); });
    return row ? row.image : item.image;
  }
  function products(d, menu, view) {
    var ids = d.featuredProductIds && d.featuredProductIds.length ? d.featuredProductIds : view.productOrder;
    return (ids || []).map(function (id) { return view.products[String(id)] && menu && menu.products[id]; }).filter(Boolean);
  }
  function categories(d, menu, view) {
    var order = d.categories || [], result = [];
    order.forEach(function (spec) {
      var c = view.categories.find(function (c) { return String(c.id) === String(spec.categoryId); });
      if (c && !result.some(function (r) { return r.id === c.id; })) result.push(Object.assign({}, c, { name: spec.title || title(c.name), image: spec.image || c.image }));
    });
    if (d.categorySelection !== 'configured') view.categories.forEach(function (c) { if (!result.some(function (r) { return r.id === c.id; })) result.push(Object.assign({}, c, { name: title(c.name) })); });
    return result.map(function (c) {
      var first = (c.productIds || []).map(function (id) { return menu && menu.products[id]; }).find(Boolean);
      return Object.assign({}, c, { image: c.image || first && artwork(d, first) });
    });
  }
  function items(ctx, d, combos, list) {
    return OO.pages.Menu.shared.itemCards(Object.assign({}, ctx, { natural: true, showAddIcon: true }), combos || [], list.map(function (p) {
      return Object.assign({}, p, { image: artwork(d, p) });
    }));
  }
  function SectionHead(p) {
    return <div className="oo-kk-section-head"><div>{p.eyebrow ? <small className="oo-kk-eyebrow">{p.eyebrow}</small> : null}<h2>{p.title}</h2></div>
      {p.page ? <Link hotel={p.hotel} page={p.page}>{p.cta || 'See all'}<OO.icons.ChevronRight size={13} /></Link> : p.aside}</div>;
  }
  function OrderStrip(p) {
    var type = p.status.mine && p.status.mine.orderType || p.status.type;
    var when = p.status.need === 'fresh' ? p.summary.title.split(' · ').slice(1).join(' · ') : 'Choose time';
    if (/^ASAP/.test(when)) when = 'ASAP';
    var open = OO.model.openStatus(p.store, OO.data.now());
    return <button type="button" className={ui.cx('oo-kk-order', p.compact && 'oo-kk-order--compact')} aria-label="Change your order plan" onClick={p.onOpen}>
      <span className="oo-kk-order-pin"><OO.icons.Pin size={16} /></span><span><strong>{type === 'DELIVERY' ? 'Delivery' : type === 'DINE_IN' ? 'Dine in' : 'Pickup'} · {when}</strong>
        {!p.compact ? <small>{open.isOpen ? 'Open now' : open.label} · {p.store.address.short || p.store.address.street}</small> : null}</span><b>Change <OO.icons.ChevronDown size={14} /></b>
    </button>;
  }
  ui.KitchenHeader = function KitchenHeader(p) {
    var route = p.route, d = config(route), h = OO.useHotel(route.hotel, { withMenu: false }), drawer = React.useState(false);
    var site = OO.whitelabel.siteFor(route.chain || route.hotel), brand = site && site.doc.brand || {};
    var name = brand.name || h.store && h.store.name || 'Our restaurant';
    var logo = d.logo || brand.logo && (brand.logo.light || brand.logo.mark);
    var count = OO.useStore(function (s) { return OO.cart.count(s.cart.lines); });
    var pages = [['home', 'Home'], ['menu', 'Order menu'], ['booking', 'Book a table'], ['bookings', 'My bookings'], ['orders', 'Orders', 'Receipt'], ['rewards', 'Member rewards', 'Star'], ['stores', 'Find us'], ['about', 'Help & contact']];
    var shortcuts = [['account', 'Account', 'User'], ['wallet', 'Member', 'Scan'], ['rewards', 'Rewards', 'Gift'], ['bookings', 'Bookings', 'Calendar']];
    return <React.Fragment><header className="oo-kk-header"><div>
      <Link hotel={route.hotel} page="home" className="oo-kk-logo" label={name + ' home'}>{logo ? <ui.Image src={logo} alt="" sizes="80px" loading="eager" /> : <strong>{name}</strong>}</Link>
      <Link hotel={route.hotel} page="cart" className="oo-kk-basket" label="Open basket"><OO.icons.Cart size={23} />{count ? <span>{count}</span> : null}</Link>
      <button type="button" aria-label={drawer[0] ? "Close navigation" : "Open navigation"} aria-expanded={drawer[0]} onClick={function () { drawer[1](!drawer[0]); }}><OO.icons.MenuList size={21} /></button>
    </div></header>
      {h.store && ['home', 'menu'].indexOf(route.page) >= 0 && K.orderSheet.ready() ? <K.OrderPrompt key={route.hotel} hotel={route.hotel} store={h.store} route={route} summary manualOnly
        renderSummary={function (state) { return <OrderStrip {...state} store={h.store} compact={route.page === 'menu'} />; }} /> : null}
      <ui.Sheet open={drawer[0]} variant="dialog" title={logo ? <ui.Image src={logo} alt={name} sizes="115px" /> : name} closeLabel="Close navigation" className="oo-kk-navigation" onClose={function () { drawer[1](false); }} footer={<div className="oo-kk-navigation-foot">{h.store ? <p>{h.store.address.short || h.store.address.street}<br />{h.store.phone}</p> : null}<ui.PoweredBySipo /></div>}>
        <div className="oo-kk-navigation-shortcuts">{shortcuts.map(function (row) { return <Link key={row[0]} hotel={route.hotel} page={row[0]} onNavigate={function () { drawer[1](false); }}><ui.Icon name={row[2]} size={18} /><span>{row[1]}</span></Link>; })}</div>
        <nav aria-label="App navigation">{pages.map(function (row) { return <Link key={row[0]} hotel={route.hotel} page={row[0]} onNavigate={function () { drawer[1](false); }}><span>{row[1]}</span>{row[2] ? <ui.Icon name={row[2]} size={16} /> : null}</Link>; })}</nav>
      </ui.Sheet>
    </React.Fragment>;
  };
  function Banner(p) {
    var s = p.slide;
    return <Link hotel={p.hotel} page={s.productId ? 'menu' : s.page} query={s.productId ? { type: 'product', id: String(s.productId) } : undefined} className={ui.cx('oo-kk-banner', p.compact && 'oo-kk-banner--compact')}>
      <ui.Image src={s.image} alt={s.title} sizes="(min-width:768px) 720px, 440px" loading={p.eager ? 'eager' : 'lazy'} />
      <span className="oo-kk-banner-copy"><small>{s.eyebrow}</small><strong>{s.title}</strong>{s.buttonLabel ? <span>{s.buttonLabel}</span> : null}</span>
      {!p.compact ? <span className="oo-kk-banner-arrow"><OO.icons.ArrowRight size={16} /></span> : null}
    </Link>;
  }
  function Carousel(p) {
    var current = React.useState(0), rail = React.useRef(null), paused = React.useState(false);
    var reduced = ui.useReducedMotion ? ui.useReducedMotion() : !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var slides = p.slides || [];
    function select(i) { current[1](i); if (rail.current) rail.current.scrollTo({ left: rail.current.clientWidth * i, behavior: reduced ? 'auto' : 'smooth' }); }
    React.useEffect(function () {
      if (reduced || paused[0] || slides.length < 2) return;
      var timer = window.setTimeout(function () { select((current[0] + 1) % slides.length); }, 8000);
      return function () { window.clearTimeout(timer); };
    }, [current[0], slides.length, reduced, paused[0]]);
    if (!slides.length) return null;
    return <section className="oo-kk-carousel oo-kk-gutter" aria-label="Featured food and offers" onMouseEnter={function () { paused[1](true); }} onMouseLeave={function () { paused[1](false); }} onFocus={function () { paused[1](true); }} onBlur={function (e) { if (!e.currentTarget.contains(e.relatedTarget)) paused[1](false); }}>
      <div className="oo-kk-slides" ref={rail} onScroll={function (e) { if (e.currentTarget.clientWidth) current[1](Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth)); }}>{slides.map(function (s, i) { return <Banner key={i} hotel={p.hotel} slide={s} eager={i === 0} />; })}</div>
      <div className="oo-kk-dots" aria-label={'Feature ' + (current[0] + 1) + ' of ' + slides.length}>{slides.map(function (s, i) { return <button type="button" key={i} aria-label={'Show feature ' + (i + 1)} aria-current={i === current[0] ? 'true' : undefined} onClick={function () { select(i); }} />; })}</div>
    </section>;
  }
  function MemberCard(p) {
    var session = K.useSession(), customer = session && session.customer, L = p.loyalty && p.loyalty.data;
    var d = p.design || {}, site = OO.whitelabel.siteFor(p.route.chain || p.hotel), brand = site && site.doc.brand || {};
    var code = customer && L && L.memberCode, name = customer ? L && L.memberName || customer.name || 'Member' : 'Future member';
    return <div className="oo-kk-member-card" role="group" aria-label={customer ? 'Your membership card' : 'Inactive membership card. Sign in to activate.'}>
      <div className="oo-kk-member-main"><div className="oo-kk-member-top">{d.memberLogo || d.logo ? <ui.Image src={d.memberLogo || d.logo} alt={brand.name || ''} sizes="130px" /> : <strong>{brand.name}</strong>}<small>{d.memberLocation}</small></div>
        <span className="oo-kk-member-program">{d.memberProgram || 'Restaurant loyalty'}</span>
        <div className="oo-kk-member-middle"><div className="oo-kk-member-qr">{code && OO.pages.Booking ? <OO.pages.Booking.Qr value={code} label="Membership QR code" /> : <OO.icons.Lock size={23} />}</div><div><span className="oo-kk-member-chip" aria-hidden="true"><svg viewBox="0 0 32 20" width="32" height="20" fill="none" stroke="currentColor" strokeWidth=".7"><rect x="5" y="3" width="22" height="14" rx="2"/><path d="M12 3v14m8-14v14M5 7h7m-7 6h7m8-6h7m-7 6h7M12 7h8v6h-8"/></svg></span><small>MEMBER NUMBER</small><b>{code || '•••• ••••'}</b><span>{customer ? p.loyalty.loading ? 'Updating your card' : 'Scan in store' : 'Sign in to activate this card'}</span></div></div>
        <div className="oo-kk-member-bottom"><div><small>MEMBER NAME</small><b>{name}</b></div><div><small>POINTS</small><b>{customer && L ? L.points : '—'}</b></div><div><small>STORE</small><b>{d.memberStore || p.store.address.city || p.store.name}</b></div></div>
      </div><div className="oo-kk-member-edge">{customer ? brand.name || 'Member' : 'Member'}</div>
    </div>;
  }
  K.KitchenHome = function KitchenHome(p) {
    var d = p.design.dining || {}, M = OO.pages.Menu, dsp = M.shared.useDisplay(p.hotel, p.store, p.menu, OO.mode), view = M.logic.buildView(p.menu, [], dsp.display);
    var enabled = function (key) { return p.modules.some(function (m) { return m.key === key && m.enabled; }); };
    var cats = categories(d, p.menu, view), popular = products(d, p.menu, view).slice(0, 6);
    var ctx = { store: p.store, menu: p.menu, view: view, display: dsp.display, natural: true, showAddIcon: true,
      openProduct: function (item) { K.go(p.hotel, 'menu', null, { type: 'product', id: String(item.id) }); },
      openCombo: function (item) { K.go(p.hotel, 'menu', null, { type: 'combo', id: String(item.id) }); }, tell: function () {} };
    return <div className="oo-kk-home"><section className="oo-kk-discover oo-kk-gutter"><h1>{d.title || 'What are you craving?'}</h1>
      <Link hotel={p.hotel} className="oo-kk-search" label="Search the menu"><OO.icons.Search size={19} /><span>{d.search || 'Search the menu'}</span></Link>
    </section>
      {enabled('categories') ? <nav className="oo-kk-categories oo-kk-rail" aria-label="Browse menu categories">{cats.map(function (c) { return <Link key={c.id} hotel={p.hotel} query={{ cat: String(c.id) }}><ui.Image src={c.image} alt="" sizes="64px" /><span>{c.name}</span></Link>; })}</nav> : null}
      {enabled('slider') ? <Carousel hotel={p.hotel} slides={d.slides} /> : null}
      {enabled('popular') && popular.length ? <section className="oo-kk-popular"><div className="oo-kk-gutter"><SectionHead title={d.popularTitle || 'Popular right now'} eyebrow={d.popularEyebrow || 'From the pass'} hotel={p.hotel} page="menu" /></div><div className="oo-kk-product-rail oo-kk-rail">{items(ctx, d, [], popular)}</div></section> : null}
      {enabled('loyalty') ? <section className="oo-kk-membership oo-kk-gutter"><SectionHead title={d.memberTitle || 'Member card'} eyebrow={d.memberEyebrow || 'Your membership'} hotel={p.hotel} page={p.signedIn ? 'wallet' : 'account'} cta={p.signedIn ? 'Wallet' : 'Sign in'} />
        <MemberCard {...p} design={d} />{!p.signedIn ? <Link hotel={p.hotel} page="account" className="oo-kk-activate"><span>Sign in to reveal your QR, balance and rewards.</span><b>Activate <OO.icons.ChevronRight size={12} /></b></Link> : null}
      </section> : null}
      {enabled('actions') || enabled('reorder') ? <section className="oo-kk-quick oo-kk-gutter"><SectionHead title={d.quickTitle || 'Quick picks'} eyebrow={d.quickEyebrow || 'Made for you'} /><div className="oo-kk-quick-grid">
        {enabled('reorder') ? p.signedIn && p.lastOrder ? <button type="button" onClick={function () { p.onReorder(p.lastOrder); }}><OO.icons.Refresh size={18} /><strong>Reorder</strong><small>{p.lastOrder.summary || 'Your last meal'}</small></button> : <Link hotel={p.hotel} page={p.signedIn ? 'orders' : 'account'} label="Sign in for one-tap reorders"><OO.icons.Refresh size={18} /><strong>Reorder</strong><small>{p.signedIn ? 'Your past orders' : 'Sign in to start'}</small></Link> : null}
        {enabled('actions') ? <React.Fragment><Link hotel={p.hotel} page="rewards" className="oo-kk-quick-dark"><OO.icons.Gift size={18} /><strong>Rewards &amp; member</strong><small>{p.signedIn ? 'Your points & rewards' : 'Join free & earn'}</small></Link><Link hotel={p.hotel} page="deals"><OO.icons.Ticket size={18} /><strong>Deals &amp; offers</strong><small>See what’s available today</small></Link><Link hotel={p.hotel} page="booking"><OO.icons.Calendar size={18} /><strong>Book a table</strong><small>Plan your next visit.</small></Link></React.Fragment> : null}
      </div></section> : null}
      {enabled('store') ? p.renderModule.store() : null}
    </div>;
  };
  K.KitchenMenu = function KitchenMenu(p) {
    var d = p.design.dining || {}, ctx = p.ctx, cats = categories(d, ctx.menu, p.view), featured = products(d, ctx.menu, p.view).slice(0, 12);
    var initial = p.route.query.cat || (p.route.query.type === 'category' ? p.route.query.id : null), selected = React.useState(initial || 'popular'), sub = React.useState(null);
    React.useEffect(function () { if (initial) { selected[1](initial); sub[1](null); } }, [initial]);
    var cat = cats.find(function (c) { return String(c.id) === String(selected[0]); });
    var group = cat && (cat.subcategories || []).find(function (s) { return String(s.id) === String(sub[0]); });
    var list = cat ? (group ? group.productIds : cat.productIds).map(function (id) { return ctx.menu.products[id]; }).filter(Boolean) : featured;
    var combos = cat ? cat.comboIds.map(function (id) { return ctx.menu.combos[id]; }).filter(Boolean) : [];
    function choose(id) { p.clearSearch(); selected[1](id); sub[1](null); }
    var cards = items(ctx, d, combos, list);
    return <main className="oo-kk-menu"><div className="oo-kk-menu-controls"><div className="oo-kk-gutter"><ui.SearchField value={p.q} onChange={p.setQ} placeholder="Search dishes" label="Search the restaurant menu" /></div>
      <nav className="oo-kk-tabs oo-kk-rail" aria-label="Menu categories"><button type="button" aria-pressed={!cat} onClick={function () { choose('popular'); }}><OO.icons.Star size={14} />Popular</button>{cats.map(function (c) { return <button key={c.id} type="button" aria-pressed={!!cat && cat.id === c.id} onClick={function () { choose(c.id); }}>{c.name}</button>; })}</nav></div>
      {p.strips}{p.namedView}
      {!p.q && d.menuBanner ? <div className="oo-kk-menu-banner oo-kk-gutter"><Banner hotel={p.route.hotel} slide={d.menuBanner} compact /></div> : null}
      <div className="oo-kk-menu-body oo-kk-gutter">{p.results ? p.resultsView : <React.Fragment><SectionHead title={cat ? cat.name : 'Popular picks'} eyebrow={cat ? 'Made to order' : 'Chef’s picks'} aside={<small>{list.length + combos.length} dishes</small>} />
        {cat && cat.subcategories && cat.subcategories.length > 1 ? <ui.ChipRow label={cat.name + ' types'}><ui.Chip active={!group} onClick={function () { sub[1](null); }}>All</ui.Chip>{cat.subcategories.map(function (s) { return <ui.Chip key={s.id} active={group && group.id === s.id} onClick={function () { sub[1](s.id); }}>{title(s.name)}</ui.Chip>; })}</ui.ChipRow> : null}
        <div className="oo-kk-menu-items">{cards.map(function (card, i) { return <React.Fragment key={card.key || i}>{card}{i === 1 ? <Link hotel={p.route.hotel} page="booking" className="oo-kk-book-row"><OO.icons.Calendar size={18} /><strong>Book a table for later</strong><OO.icons.ChevronRight size={16} /></Link> : null}</React.Fragment>; })}</div>
        {!cards.length ? <ui.EmptyState icon="Book" title="No dishes available" body="Try another category or check again later." /> : null}
      </React.Fragment>}</div>
    </main>;
  };
  K.KitchenAccount = function KitchenAccount(p) {
    var d = config(p.route), route = Object.assign({}, p.route, { query: Object.assign({}, p.route.query, { directLogin: 'true' }) });
    return <main className="oo-kk-account oo-kk-gutter"><section className="oo-kk-access"><div className="oo-kk-access-intro"><small><OO.icons.User size={11} /> {d.accountBadge || 'Member access'}</small><h1>{d.accountTitle || 'Your restaurant account'}</h1><p>{d.accountBody || 'Sign in to collect points, reorder in a tap and keep your details ready at checkout.'}</p></div>
      <OO.pages.Profile route={route} mode="app" kitchenEmbedded />
    </section><section className="oo-kk-guest-order"><small className="oo-kk-eyebrow">Ordering today?</small><p>You can order as a guest without an account. Members earn points on every order.</p><Link hotel={p.route.hotel} className="oo-kk-outline">Browse the menu</Link></section></main>;
  };
  K.KitchenRewardsGuest = function KitchenRewardsGuest(p) {
    var d = p.design.dining || {};
    return <main className="oo-kk-rewards-guest oo-kk-gutter"><section><div className="oo-kk-rewards-intro"><OO.icons.Lock size={23} /><h1>{d.rewardsTitle || 'Restaurant rewards'}</h1><p>{d.rewardsBody || 'Collect points every time you order and turn them into food.'}</p></div>
      <ul><li>Earn points on every order you place</li><li>Redeem points against real menu items</li><li>Your member card, ready to scan in store</li></ul>
      <div className="oo-kk-rewards-actions"><Link hotel={p.route.hotel} page="account" className="oo-kk-solid">Sign in or join</Link><Link hotel={p.route.hotel} className="oo-kk-outline">Browse the menu <OO.icons.ChevronRight size={15} /></Link></div></section></main>;
  };
  K.KitchenBookingUnavailable = function KitchenBookingUnavailable(p) {
    var name = p.store.theme && p.store.theme.brand || p.store.name, phone = p.store.phone;
    if (phone && /^64\d/.test(phone)) phone = '+' + phone;
    return <main className="oo-kk-booking oo-kk-gutter"><section className="oo-kk-booking-intro"><small className="oo-kk-eyebrow">{name}</small><h1>Reserve a table</h1><p>Online reservations are not switched on for {name} yet, so the restaurant takes tables by phone.</p></section>
      <section className="oo-kk-booking-contact"><small className="oo-kk-eyebrow">Book by phone</small><h2>Call the restaurant</h2><p>Online table reservations have not been enabled in the restaurant system yet. Please contact the restaurant to arrange your table.</p>
        {phone ? <a href={'tel:' + phone.replace(/[^+\d]/g, '')} className="oo-kk-solid"><OO.icons.Phone size={16} /> Call {phone}</a> : null}<Link hotel={p.hotel} className="oo-kk-outline">Order online instead</Link><Link hotel={p.hotel} page="booking-track" className="oo-kk-outline">Track an existing booking</Link></section>
    </main>;
  };
  K.Kitchen = { config: config, artwork: artwork, products: products, categories: categories, MemberCard: MemberCard, Carousel: Carousel, Banner: Banner };
})();


// ===================== src/pages/app-zeta.jsx =====================
// Zeta's app composition. All food, availability, order choices, authentication and checkout use the shared engines.
(function () {
  'use strict';
  var OO = window.OO, K = OO.appKit, ui = OO.ui;
  var ICONS = { store: 'Store', heart: 'Heart', gift: 'Gift', users: 'UserCircle', info: 'Info', mail: 'Envelope', settings: 'Settings' };
  function displayName(name) { return name && name === name.toUpperCase() ? name.toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : name; }
  function RadioRow(p) {
    function move(e) {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].indexOf(e.key) < 0) return;
      var items = Array.prototype.slice.call(e.currentTarget.querySelectorAll('button[role="radio"]:not(:disabled)'));
      var at = items.indexOf(e.target);
      if (at < 0 || !items.length) return;
      e.preventDefault();
      var next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (at + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
      items[next].focus(); items[next].click();
    }
    return <div className={p.className} role="radiogroup" aria-label={p.label} onKeyDown={move}>{p.children}</div>;
  }
  function config(route) { return OO.whitelabel.zetaDesign(route.chain || route.hotel) || {}; }
  function site(route) { return OO.whitelabel.siteFor(route.chain || route.hotel); }
  function branchName(store, branches, brand) {
    var name = K.shortName(store, branches || []), full = brand && brand.name || '';
    if (full && name.toLowerCase().indexOf(full.toLowerCase()) === 0) name = name.slice(full.length).trim() || name;
    else if (full && name.toLowerCase().indexOf(full.split(' ')[0].toLowerCase() + ' ') === 0) name = name.slice(full.split(' ')[0].length).trim();
    return name.replace(/^\((.*)\)$/, '$1').replace(/,\s*CBD$/i, ' (CBD)');
  }
  function Link(p) {
    var l = OO.router.link(p.page || 'menu', p.params || null, { hotel: p.hotel, query: K.query(p.query), hash: p.hash });
    return <a className={p.className} href={l.href} onClick={function (e) { if (p.onNavigate) p.onNavigate(); l.onClick(e); }} aria-label={p.label}>{p.children}</a>;
  }
  function Artwork(p) {
    var failed = React.useState(null);
    return p.src && failed[0] !== p.src ? <ui.Image src={K.ZetaAsset?K.ZetaAsset(p.src):p.src} alt={p.alt || ''} sizes={p.sizes || '420px'} loading={p.eager ? 'eager' : 'lazy'}
      onError={function () { failed[1](p.src); }} /> : p.icon ? <ui.Icon name={ICONS[p.icon] || p.icon} size={p.size || 30} /> : null;
  }
  function Section(p) {
    return <section className={'oo-zeta-section ' + (p.className || '')} aria-label={p.title}>
      <div className="oo-zeta-section__heading"><h2>{p.title}</h2>{p.menu ? <Link hotel={p.hotel}>Full menu<OO.icons.ChevronRight size={13} /></Link> : null}</div>{p.children}
    </section>;
  }
  function TargetLink(p) {
    var target = p.target || {};
    if (target.type === 'sitePage') return <Link hotel={p.hotel} page="app-content" params={{ slug: target.page }} className={p.className} onNavigate={p.onNavigate}>{p.children}</Link>;
    if (target.type === 'page') {
      var legal = ['terms', 'privacy', 'allergy'].indexOf(target.page) >= 0;
      return <Link hotel={p.hotel} page={legal ? 'legal' : target.page === 'offers' ? 'deals' : target.page} params={legal ? { kind: target.page } : null}
        className={p.className} onNavigate={p.onNavigate}>{p.children}</Link>;
    }
    if (target.type === 'home') return <Link hotel={p.hotel} page="home" className={p.className} onNavigate={p.onNavigate}>{p.children}</Link>;
    if (target.type === 'order' || target.type === 'categories' || target.type === 'menuCategory') return <Link hotel={p.hotel}
      query={target.type === 'menuCategory' ? { type: 'category', id: String(target.categoryId) } : null} className={p.className} onNavigate={p.onNavigate}>{p.children}</Link>;
    var s = site(p.route), doc = s && s.doc, landing = doc && doc.landing;
    var t = OO.landing.targetOf(p.target, { key: p.route.chain || p.hotel, landing: landing, sections: landing && landing.sections || [] });
    if (!t) return null;
    var href = t.href || (OO.landing.pageHref(p.route.chain || p.hotel, null) + '#' + OO.landing.anchorId(target.section));
    return <a className={p.className} href={href} onClick={p.onNavigate}>{p.children}</a>;
  }
  function OrderStrip(p) {
    var st = p.status, type = (st.mine && st.mine.orderType) || st.type;
    var when = st.need === 'fresh' ? (K.ZetaSlotLabel&&st.mine&&K.ZetaSlotLabel(st.mine.slot,false)) || p.summary.title.split(' · ').slice(1).join(' · ') : 'ASAP';
    return <button type="button" className="oo-zeta-order" onClick={p.onOpen}>
      <small>{type === 'DELIVERY' ? 'Delivery from' : 'Pickup from'}</small><strong>{p.branch}</strong><span>{when}</span><OO.icons.ChevronRight size={16} />
    </button>;
  }
  ui.ZetaHeader = function ZetaHeader(p) {
    var route = p.route, hotel = route.hotel || route.chain, d = config(route), s = site(route);
    var h = OO.useHotel(route.hotel), chain = OO.useChain(route.chain || route.hotel, { theme: false });
    var finder = React.useState(false), orderRequest = React.useState(0);
    var drawer = React.useState(false), cartOpen=React.useState(false), editing=React.useState(null), store = h.store, master = chain.master || store;
    var session=K.useSession(),signedIn=!!(session && session.customer);
    React.useEffect(function(){function edit(e){cartOpen[1](false);editing[1](e.detail);}function cart(e){cartOpen[1](!!e.detail);}window.addEventListener('oo-zeta-edit-item',edit);window.addEventListener('oo-zeta-cart-open',cart);return function(){window.removeEventListener('oo-zeta-edit-item',edit);window.removeEventListener('oo-zeta-cart-open',cart);};},[]);
    var brand = s && s.doc.brand || {}, theme = master && OO.theme.resolve(master.theme) || {};
    var logo = brand.logo && (brand.logo.light || brand.logo.mark) || theme.logo || master && master.logoUrl;
    var name = brand.name || theme.brand || master && master.name || 'Our app', short = name.split(' ')[0];
    var branch = store ? branchName(store, chain.chain && chain.chain.stores || [], brand) : '';
    React.useEffect(function(){
      if(route.hotel || !chain.chain || !chain.chain.stores)return;
      var first=chain.chain.stores.find(function(b){return (b.onlineKey||b.key)==='zeta-greek-kitchen';});
      if(!first)return;
      var active=OO.router.current();if(active.hotel)return;
      var key=first.onlineKey||first.key;
      OO.store.setBranch(key,active.chain||active.hotel);
      OO.router.go(OO.router.href(active.page,active.params,{hotel:key,chain:active.chain,query:Object.assign({},active.query,{store:key})}),{replace:true});
    },[route.hotel,chain.chain]);

    var ready = K.orderSheet && K.orderSheet.ready();
    var close = function () { drawer[1](false); };
    var member = [['menu', 'Favourites', 'Heart', { saved: 'true' }], ['wallet', 'Member card', 'Scan'], ['rewards', 'Rewards', 'Gift'], ['coupons', 'Coupons', 'Ticket']];
    var strip = route.page !== 'checkout';
    var cartCount=OO.useStore(function(st){return OO.cart.count(st.cart.lines);});
    return <React.Fragment>
      <header className="oo-zeta-header" hidden={route.page==='checkout'}><div className="oo-zeta-header__row">
        <button type="button" aria-label="Open menu" aria-expanded={drawer[0]} onClick={function () { drawer[1](true); }}><K.ZetaHeaderIcons.Menu size={21} /></button>
        <Link hotel={hotel} page="home" className="oo-zeta-logo" label={name + ' home'}><Artwork src={logo} eager />{!logo ? <strong>{name}</strong> : null}</Link>
        <button type="button" className="oo-zeta-bag" aria-label="Open cart" onClick={function(){cartOpen[1](true);}}><K.ZetaHeaderIcons.Bag size={23} />{cartCount>0?<span className="oo-zeta-bag-count">{OO.store.cartCount()}</span>:null}</button>
      </div>{strip ? store && ready ? <K.OrderPrompt key={route.hotel} hotel={route.hotel} store={store} route={route} summary manualOnly={route.page!=='menu'||route.query.saved==='true'} branch={branch} openSignal={orderRequest[0]} onLoad={route.page==='menu'&&route.query.saved!=='true'}
        renderSummary={function (state) { return <OrderStrip {...state} branch={branch} onOpen={function(){finder[1](true);}} />; }} />
        : <button type="button" className="oo-zeta-order oo-zeta-order--choose" onClick={function(){finder[1](true);}}><OO.icons.Pin size={15} /><strong>Choose your store</strong><OO.icons.ChevronRight size={16} /></button> : null}</header>
      <K.ZetaStoreFinder open={finder[0]} onClose={function(order){finder[1](false);if(order===true)orderRequest[1](function(n){return n+1;});}}/>
      {K.ZetaNavigationDrawer?<K.ZetaNavigationDrawer route={route} open={drawer[0]} onClose={close}/>:null}{K.ZetaCartDrawer ? <K.ZetaCartDrawer route={route} open={cartOpen[0]} onClose={function(){cartOpen[1](false);}}/>:null}
      {editing[0] && h.store && h.menu ? editing[0].kind==='combo' ? <OO.pages.ComboBuilder hotel={hotel} store={h.store} menu={h.menu} comboId={editing[0].id} editLineKey={editing[0].line} onClose={function(){editing[1](null);}}/> : <OO.pages.ItemSheet hotel={hotel} store={h.store} menu={h.menu} productId={editing[0].id} editLineKey={editing[0].line} onClose={function(){editing[1](null);}}/> : null}
      {K.AppWelcome ? <K.AppWelcome route={route} /> : null}
    </React.Fragment>;
  };
  function Hero(p) {
    var hero = p.design.hero || {};
    if (!hero.image && !hero.title) return p.fallback ? p.fallback() : null;
    return <div className="oo-zeta-welcome"><Link hotel={p.hotel} className="oo-zeta-hero">
      <Artwork src={hero.image} eager /><span className="oo-zeta-hero__shade" /><span className="oo-zeta-hero__copy">
        {hero.eyebrow ? <small>{hero.eyebrow}</small> : null}<strong>{hero.title}</strong>{hero.body ? <span>{hero.body}</span> : null}
      </span></Link></div>;
  }
  function ActionRow(p) { return <div className="oo-zeta-gutter oo-zeta-action-wrap"><Link hotel={p.hotel} className="oo-zeta-action"><OO.icons.Utensils size={20} />{(p.design.hero || {}).buttonLabel || 'Order now'}</Link></div>; }
  function Reorder(p) {
    var order = p.lastOrder;
    if (p.signedIn && order) return <div className="oo-zeta-gutter"><button type="button" className="oo-zeta-reorder" onClick={function () { p.onReorder(order); }}>
      <OO.icons.Refresh size={24} /><span><strong>Reorder your last meal</strong><small>{order.summary || order.shortId}</small></span><b>Reorder</b>
    </button></div>;
    return <div className="oo-zeta-gutter"><Link hotel={p.hotel} page={p.signedIn ? 'menu' : 'profile'} query={p.signedIn ? null : { directLogin: 'true' }} className="oo-zeta-reorder">
      <OO.icons.Refresh size={24} /><span><strong>Reorder your last meal</strong><small>{p.signedIn ? 'Find your next favourite meal' : 'Sign in to see your previous orders'}</small></span><b>{p.signedIn ? 'View menu' : 'Sign in'}</b>
    </Link></div>;
  }
  function RewardsTeaser(p) {
    var name = p.store.loyalty && p.store.loyalty.programName || 'Rewards';
    if (p.signedIn) return <div className="oo-zeta-rewards-balance">{p.renderModule.loyalty()}</div>;
    return <div className="oo-zeta-gutter"><Link hotel={p.hotel} page="rewards" className="oo-zeta-rewards">
      <span className="oo-zeta-rewards__icon"><OO.icons.Gift size={22} /></span><span><small>{name}</small><strong>Join {name}</strong><span>Earn points on every order</span></span><OO.icons.ChevronRight size={18} />
    </Link></div>;
  }
  function ProductRail(p) {
    var res = p.popular || {}, data = res.data;
    var products = (Array.isArray(data) ? data : []).map(function (item) { return p.menu && p.menu.products[item.id] || item; }).filter(function (item) {
      return p.view.products[String(item.id)] && (!p.display || !p.display.products[item.id] || p.display.products[item.id].show !== 'hide');
    });
    products = OO.pages.Menu.logic.sortProducts(products, p.display);
    if (!products.length && !res.loading && !res.error) return null;
    return <Section title="Featured for you" hotel={p.hotel} menu>
      {res.error && !data ? <div className="oo-zeta-gutter"><p role="alert">We couldn't load featured items.</p><ui.Button variant="link" onClick={res.reload}>Try again</ui.Button></div>
        : <div className="oo-zeta-rail" aria-busy={res.loading && !data || undefined}>{res.loading && !data ? [0, 1, 2].map(function (n) { return <ui.Skeleton key={n} variant="block" width={118} height={138} />; }) : products.slice(0, 8).map(function (item) {
          var d = p.display && p.display.products[item.id], off = d ? d.show === 'grey' : !!item.soldOut || item.available === false;
          var open = function () { K.go(p.hotel, 'menu', null, { type: 'product', id: String(item.id) }); };
          var price = item.sizes && item.sizes.length ? item.sizes[0].price : item.price;
          return <article key={item.id} className={'oo-zeta-food' + (!item.image ? ' oo-zeta-food--plain' : '') + (off ? ' is-off' : '')}>
            {item.image ? <button type="button" className="oo-zeta-food__photo" aria-label={'View ' + item.name} disabled={off} onClick={open}><Artwork src={item.image} sizes="160px" /></button> : null}
            <div className="oo-zeta-food__body"><button type="button" disabled={off} onClick={open}>{displayName(item.name)}</button><strong>{off ? d && d.label || 'Unavailable' : price > 0 ? OO.format.money(price, p.store.currency) : 'View'}</strong></div>
            {!off ? <button type="button" className="oo-zeta-food__add" aria-label={'Add ' + item.name} onClick={open}><OO.icons.Plus size={17} /></button> : null}
          </article>;
        })}</div>}
    </Section>;
  }
  function CategoryRail(p) {
    var configured = p.design.categoryTiles || [], list = configured.length ? configured.map(function (tile) {
      var cat = p.view.categories.filter(function (c) { return String(c.id) === String(tile.categoryId); })[0];
      return cat && Object.assign({}, cat, { name: tile.title || cat.name, image: tile.image || cat.image });
    }).filter(Boolean) : p.view.categories;
    list = OO.pages.Menu.logic.sortCategories(list, p.menu, p.display);
    if (!list.length) return null;
    var sections = OO.model.menuLayout(p.store, p.route.query) === 'sections';
    return <Section title="Browse by category" hotel={p.hotel} menu><nav className="oo-zeta-rail" aria-label="Browse categories">{list.map(function (tile) {
      var c = tile, count = c.productIds.length + c.comboIds.length;
      return <Link key={c.id} hotel={p.hotel} query={sections ? null : { cat: String(c.id) }} hash={sections ? p.view.anchor[String(c.id)] : null} className={'oo-zeta-category' + (!c.image ? ' oo-zeta-category--plain' : '')}>
        {c.image ? <span><Artwork src={c.image} sizes="160px" /></span> : null}<strong>{displayName(c.name)}</strong><small>{count} {count === 1 ? 'item' : 'items'}</small>
      </Link>;
    })}</nav></Section>;
  }
  function BranchCallout(p) {
    var open = OO.model.openStatus(p.store, OO.data.now());
    var s = site(p.route), name = branchName(p.store, [], s && s.doc.brand);
    return <div className="oo-zeta-gutter"><Link hotel={p.hotel} page="stores" className="oo-zeta-branch"><span><OO.icons.Pin size={22} /></span>
      <span><small>Nearest branch</small><strong>{name}</strong><span>{open.label || (open.isOpen ? 'Open now' : 'Closed')}</span></span><OO.icons.ChevronRight size={18} />
    </Link></div>;
  }
  K.ZetaStoreCard = function ZetaStoreCard(p) {
    var b = p.branch, s = site(p.route), name = branchName(b, [], s && s.doc.brand);
    var directions = OO.landing.directionsUrl({ lat: b.lat, lng: b.lng, name: b.name, address: b.address });
    return <article className="oo-zeta-store" role="listitem"><div className="oo-zeta-store__head"><span><OO.icons.Store size={25} /></span><div><h2>{name}</h2><small>{b.status && b.status.label}</small></div>{p.selected ? <b>Selected</b> : null}</div>
      {b.address ? <p><OO.icons.Pin size={16} />{b.address}</p> : null}<div className="oo-zeta-store__actions"><button type="button" onClick={p.onSelect}>{p.selected ? 'Order now' : 'Order here'}<OO.icons.ChevronRight size={16} /></button>{directions ? <a href={directions} target="_blank" rel="noopener noreferrer">Directions<OO.icons.Pin size={15} /></a> : null}</div>
    </article>;
  };
  function PromoCards(p) {
    var cards = (p.design.cards || []).filter(function (c) { return c.group === 'home'; });
    if (!cards.length) return null;
    return <section className="oo-zeta-promos" aria-label="Discover more">{cards.map(function (card, i) {
      return <TargetLink key={i} route={p.route} hotel={p.hotel} target={card.target} className="oo-zeta-promo">
        <span className="oo-zeta-promo__media"><Artwork src={card.image} icon={card.icon} /></span>{card.eyebrow ? <small>{card.eyebrow}</small> : null}<strong>{card.title}</strong><span>{card.body}</span><OO.icons.ChevronRight size={16} />
      </TargetLink>;
    })}</section>;
  }
  function SocialIcon(p) {
    if (p.network === 'instagram') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>;
    if (p.network === 'facebook') return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.8V12h2.6V9.8c0-2.6 1.5-4 3.9-4 1.1 0 2.3.2 2.3.2v2.5h-1.3c-1.3 0-1.7.8-1.7 1.6V12h2.9l-.5 2.9h-2.4v7A10 10 0 0 0 22 12Z" /></svg>;
    return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15 2h3c.3 2.3 1.7 3.9 4 4.2v3a9 9 0 0 1-4-1.3V16a6 6 0 1 1-6-6h1v3h-1a3 3 0 1 0 3 3Z" /></svg>;
  }
  function SocialLinks(p) {
    var s = site(p.route), footer = s && s.doc.landing && s.doc.landing.footer;
    var socials = footer && footer.socials || [];
    if (!socials.length) return null;
    return <section className="oo-zeta-socials oo-zeta-gutter" aria-label="Follow us"><h2>{p.design.socialTitle || 'Follow us'}</h2><div>{socials.map(function (social) {
      return <a key={social.network} href={social.url} target="_blank" rel="noopener noreferrer" className={'oo-zeta-social oo-zeta-social--' + social.network}><SocialIcon network={social.network} /><span>{social.network.charAt(0).toUpperCase() + social.network.slice(1).replace('iktok', 'ikTok')}</span></a>;
    })}</div></section>;
  }
  K.ZetaHome = function ZetaHome(p) {
    var dsp = OO.pages.Menu.shared.useDisplay(p.hotel, p.store, p.menu, OO.mode);
    var view = OO.pages.Menu.logic.buildView(p.menu, [], dsp.display);
    var state = Object.assign({}, p, { view: view, display: dsp.display });
    var render = { slider: function () { return <Hero {...state} fallback={p.renderModule.slider} />; }, actions: function () { return <ActionRow {...state} />; },
      reorder: function () { return <Reorder {...state} />; }, loyalty: function () { return <RewardsTeaser {...state} />; },
      popular: function () { return <ProductRail {...state} />; }, categories: function () { return <CategoryRail {...state} />; }, store: function () { return <BranchCallout {...state} />; } };
    return <div className="oo-zeta-home">{p.modules.map(function (m) { return render[m.key] ? <React.Fragment key={m.key}>{render[m.key]()}</React.Fragment> : null; })}
      <PromoCards {...state} /><SocialLinks {...state} />
    </div>;
  };
  K.ZetaZone = function ZetaZone(p) {
    var d = config(p.route), hotel = p.route.hotel;
    return <main className="oo-zeta-zone"><h1>{d.zoneLabel || 'Zeta Zone'}</h1><p>Rewards, member benefits and offers, all in one place.</p>
      <nav aria-label="Member benefits" className="oo-zeta-zone-links">{[['wallet', 'Member card', 'Scan'], ['rewards', 'Rewards', 'Gift'], ['coupons', 'Coupons', 'Ticket']].map(function (item) {
        return <Link key={item[0]} hotel={hotel} page={item[0]}><ui.Icon name={item[2]} size={24} /><strong>{item[1]}</strong><OO.icons.ChevronRight size={15} /></Link>;
      })}</nav><OO.pages.Deals route={p.route} mode="app" embedded />
    </main>;
  };
  K.ZetaFavourites = function ZetaFavourites(p) {
    var hotel = p.route.hotel, h = OO.useHotel(hotel), ids = K.useDeviceFavourites(hotel), M = OO.pages.Menu;
    var dsp = M.shared.useDisplay(hotel, h.store, h.menu, OO.mode), view = M.logic.buildView(h.menu, [], dsp.display);
    var products = h.menu ? ids.filter(function (id) { return id.indexOf('product:') === 0 && view.products[id.split(':')[1]]; }).map(function (id) { return h.menu.products[id.split(':')[1]]; }).filter(Boolean) : [];
    var combos = h.menu ? ids.filter(function (id) { return id.indexOf('combo:') === 0 && view.combos[id.split(':')[1]]; }).map(function (id) { return h.menu.combos[id.split(':')[1]]; }).filter(Boolean) : [];
    var ctx = { store: h.store, menu: h.menu, view: view, display: dsp.display, natural: true,
      openProduct: function (item) { K.go(hotel, 'menu', null, { type: 'product', id: String(item.id) }); },
      openCombo: function (item) { K.go(hotel, 'menu', null, { type: 'combo', id: String(item.id) }); }, tell: function () {} };
    return <main className="oo-zeta-zone"><h1>Favourites</h1><p>Your saved dishes at this store, on this device.</p>
      {h.loading ? <ui.Skeleton variant="block" height={120} /> : h.error && !h.store ? <K.ErrorCard title="We couldn't load this store" onRetry={h.reload} />
        : products.length || combos.length ? <ui.ProductGrid>{M.shared.itemCards(ctx, combos, products)}</ui.ProductGrid>
        : <div className="oo-zeta-empty"><OO.icons.Heart size={32} /><h2>Keep your favourites close</h2><p>Tap the heart on a dish to save it here.</p><Link hotel={hotel} className="oo-zeta-action">Browse the menu</Link></div>}
    </main>;
  };
  K.ZetaOptionGroup = function ZetaOptionGroup(p) {
    var own=React.useState(!!p.defaultOpen), id='zeta-group-'+React.useId().replace(/:/g,'');
    var open=!p.collapsible || (p.open==null?own[0]:p.open), max=p.max||1, count=p.count||0;
    var hint=p.constraint || (p.min>0 ? (p.min===max?'Choose '+max:'Choose '+p.min+'–'+max) : 'Up to '+max);
    function toggle(){if(p.open==null)own[1](!open);if(p.onToggle)p.onToggle(!open);}
    return <section ref={p.elementRef} tabIndex={p.invalid?-1:undefined} aria-invalid={p.invalid||undefined} className={ui.cx('oo-zeta-mod',p.invalid&&'is-invalid',p.className)}>
      <button type="button" className="oo-zeta-mod__head" aria-expanded={open} aria-controls={id+'-body'} onClick={toggle}>
        <span className="oo-zeta-mod__titles"><span className="oo-zeta-mod__line"><strong id={id}>{p.title}</strong><span>{p.required?'Required':'Optional'}</span></span><small>{hint}</small></span>
        <span className="oo-zeta-mod__count">{count+'/'+max}</span><OO.icons.ChevronDown size={14} className={open?'is-open':''}/>
      </button>
      {open?<div id={id+'-body'} className="oo-zeta-mod__body" role={p.type==='radio'?'radiogroup':'group'} aria-labelledby={id} aria-required={p.required||undefined}>{p.children}</div>:null}
    </section>;
  };
  K.ZetaModGroup = function ZetaModGroup(p) {
    var g=p.group, options=g.options||[], taken=options.reduce(function(n,o){return n+(o.count||(+!!o.on));},0);
    var bounds=String(g.hint||'').split('·')[0].match(/\d+/g)||[], max=g.max || (g.radio?1:Number(bounds[bounds.length-1])||Math.max(1,options.length));
    var currency=p.currency || '$';
    return <K.ZetaOptionGroup title={g.name} required={g.required} min={g.min||0} max={max} count={taken} type={g.radio?'radio':'checkbox'}
      constraint={g.blocked?g.reason:null} collapsible={p.collapsible} open={p.open} onToggle={p.onToggleOpen} elementRef={p.elementRef} invalid={p.invalid}>
      {options.map(function(o){var count=o.count||(+!!o.on);return <div key={o.id} className={ui.cx('oo-zeta-mod__option',o.on&&'is-on',o.disabled&&'is-disabled')}
        onClick={function(e){if(!o.disabled&&!e.target.closest('label,.oo-qty'))p.onPick(o.id);}}>
        <label className="oo-zeta-mod__pick"><input type={g.radio?'radio':'checkbox'} name={'zeta-mod-'+g.id} aria-label={o.name} checked={!!o.on} disabled={!!o.disabled} onChange={function(){p.onPick(o.id);}}/>
          <span className={ui.cx('oo-zeta-mod__control',g.radio&&'is-radio')} aria-hidden="true">{o.on?<OO.icons.Check size={13}/>:null}</span>
          {o.optionImage?<img src={o.optionImage} alt=""/>:null}<span>{o.name}</span>
        </label>
        {o.price>0?<span className="oo-zeta-mod__price">{'+'+OO.format.money(o.price,currency)}</span>:null}
        {g.counter&&count>0?<ui.QtyStepper size="sm" value={count} min={0} max={g.full||o.disabled?count:99} label={o.name} itemName={o.name}
          onChange={function(n){if(n>count)p.onInc(o.id);else p.onDec(o.id);}}/>:null}
      </div>;})}
      {(g.alerts||[]).map(function(a){return <ui.InlineNotice key={a.id} tone="warn"><b>{a.name}</b>{': '+a.text}</ui.InlineNotice>;})}
    </K.ZetaOptionGroup>;
  };
  K.ZetaItemHeader = function ZetaItemHeader(p) {
    var favourites=K.useZetaFavourites(), id=String(p.product.id), saved=favourites.some(function(f){return f.id===id;});
    var notice = React.useState('');
    function share() {
      var target = new URL(OO.router.href('menu', null, { hotel: p.hotel, query: { type: 'product', id: String(p.product.id) } }), window.location.origin);
      target.searchParams.delete('wlPreview'); target.searchParams.delete('wlcb');
      var url = target.href;
      if (navigator.share) navigator.share({ title: p.product.name, url: url }).catch(function () {});
      else if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(function () { notice[1]('Link copied'); }, function () { notice[1]('Use your browser to copy this page’s address.'); });
      else notice[1]('Use your browser to copy this page’s address.');
    }
    return <div className={'oo-zeta-item-header' + (!p.product.image ? ' oo-zeta-item-header--plain' : '')}>
      <Artwork src={p.product.image} eager /><div className="oo-zeta-item-actions"><button type="button" aria-label={'Share ' + p.product.name} onClick={share}><OO.icons.Share size={20} /></button>
        <button type="button" aria-pressed={saved} aria-label={(saved ? 'Remove ' : 'Add ') + p.product.name + (saved ? ' from favourites' : ' to favourites')}
          onClick={function () { OO.zetaReference.require(154484).toggleFavoriteProduct({id:id,label:displayName(p.product.name),image:p.product.image,price:p.product.price,type:'item',slug:OO.format.slugify ? OO.format.slugify(p.product.name):''}); }}><OO.icons.Heart size={20} filled={saved} /></button>
        <button type="button" aria-label="Close" onClick={p.onClose}><OO.icons.Close size={20} /></button></div>
      {notice[0] ? <span className="oo-zeta-share-notice" role="status">{notice[0]}</span> : null}
    </div>;
  };
  K.ZetaOrderChoices = function ZetaOrderChoices(p) {
    var st = p.status, v = p.view, expandedDays = React.useState(false), expandedTimes = React.useState(false);
    var s = site(p.route), name = branchName(p.store, [], s && s.doc.brand);
    var types = ['PICKUP', 'DELIVERY'].concat(st.types.filter(function (t) { return t !== 'PICKUP' && t !== 'DELIVERY'; }));
    function choice(key, label, icon, on, off, sub, click) { return <button key={key} type="button" role="radio" aria-checked={on} disabled={off} className={ui.cx('oo-zeta-choice', on && 'is-on')} onClick={click}>
      <K.ZetaOrderIcon name={icon} size={20} /><strong>{label}</strong>{sub ? <small>{sub}</small> : null}</button>; }
    var days = v.later && v.later.days || [], slots = v.day && v.day.slots || [];
    return <div className="oo-zeta-order-fields"><p className="oo-zeta-order-lead">{name} · we'll show what's available</p>
      {p.review ? <ui.InlineNotice tone="warn">{p.review}</ui.InlineNotice> : null}
      <section><h3>Order type</h3><RadioRow className="oo-zeta-choices" label="Order type">{types.map(function (type) {
        var off = st.types.indexOf(type) < 0 || !st.pickers[type] || !st.pickers[type].first;
        return choice(type, type === 'DELIVERY' ? 'Delivery' : type === 'PICKUP' ? 'Pickup' : 'Dine in', type === 'DELIVERY' ? 'Pin' : 'Bag', v.type === type, off, off ? 'Unavailable' : null, function () { p.onType(type); });
      })}</RadioRow></section>{p.address}
      <section><h3>{v.type === 'DELIVERY' ? 'Delivery time' : 'Pickup time'}</h3><RadioRow className="oo-zeta-choices" label="Order time">
        {choice('asap', 'ASAP', 'Clock', v.mode === 'asap', !v.asap.ok, !v.asap.ok ? 'Unavailable' : '~' + (v.type === 'DELIVERY' ? p.store.deliveryTimeMins : p.store.cookingTimeMins) + ' mins', function () { p.onMode('asap'); })}
        {choice('later', 'Schedule', 'Calendar', v.mode === 'later', !v.later.ok, v.later.ok ? 'Pick a time' : 'Unavailable', function () { p.onMode('later'); })}
      </RadioRow></section>
      {v.mode === 'later' ? <React.Fragment><section><h3>{v.type === 'DELIVERY' ? 'Delivery date' : 'Pickup date'}</h3><RadioRow className="oo-zeta-dates" label={v.type === 'DELIVERY' ? 'Delivery date' : 'Pickup date'}>{days.slice(0, expandedDays[0] ? days.length : 3).map(function (day) {
        return <button key={day.key} type="button" role="radio" aria-checked={v.day && v.day.key === day.key} disabled={!day.pickable} onClick={function () { expandedTimes[1](false); p.onDay(day.key); }}>{(day.text || day.label).replace(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) /, '$1, ')}</button>;
      })}{days.length > 3 ? <button type="button" aria-expanded={expandedDays[0]} onClick={function () { expandedDays[1](!expandedDays[0]); }}>{expandedDays[0] ? 'Show less' : '+' + (days.length - 3) + ' more'}</button> : null}</RadioRow></section>
      <section className="oo-zeta-time-section"><h3>Time</h3><RadioRow className="oo-zeta-times" label="Time">{slots.slice(0, expandedTimes[0] ? slots.length : 6).map(function (slot) {
        return <button key={slot.value} type="button" role="radio" aria-checked={v.slot && v.slot.value === slot.value} onClick={function () { p.onSlot(slot.value); }}>{slot.label.replace(/([ap]m)$/i, ' $1').toUpperCase()}</button>;
      })}{slots.length > 6 ? <button type="button" aria-expanded={expandedTimes[0]} onClick={function () { expandedTimes[1](!expandedTimes[0]); }}>{expandedTimes[0] ? 'Show less' : '+' + (slots.length - 6) + ' more'}</button> : null}</RadioRow></section></React.Fragment> : null}
    </div>;
  };
  K.ZetaMenu = function ZetaMenu(p) {
    var cats = p.view.categories, initial = p.route.query.cat || (p.route.query.type === 'category' ? p.route.query.id : null);
    var hash = String(p.route.hash || '').replace(/^#/, '');
    if (!initial && hash) { var linked = cats.filter(function (c) { return p.view.anchor[String(c.id)] === hash; })[0]; if (linked) initial = linked.id; }
    var selected = React.useState(initial || cats[0] && cats[0].id), sub = React.useState(null);
    React.useEffect(function () { if (initial) { selected[1](initial); sub[1](null); } }, [initial]);
    var cat = cats.filter(function (c) { return String(c.id) === String(selected[0]); })[0] || cats[0];
    var subgroup = cat && sub[0] && cat.subcategories.filter(function (c) { return String(c.id) === String(sub[0]); })[0];
    var products = cat ? (subgroup ? subgroup.productIds : cat.productIds).map(function (id) { return p.ctx.menu.products[id]; }).filter(Boolean) : [];
    var combos = cat ? cat.comboIds.map(function (id) { return p.ctx.menu.combos[id]; }).filter(Boolean) : [];
    function choose(c) { p.clearSearch(); selected[1](c.id); sub[1](null); }
    return <main className="oo-zeta-menu"><div className="oo-zeta-menu-search"><ui.SearchField value={p.q} onChange={p.setQ} placeholder="Search the menu" /></div>
      <ui.CategoryTabs tabs={cats.map(function (c) { return { id: c.id, label: displayName(c.name) }; })} active={cat && cat.id} onSelect={function (id) { var c = cats.filter(function (c) { return c.id === id; })[0]; if (c) choose(c); }} />
      <div className="oo-zeta-menu-body">{p.strips}{p.namedView}{p.results ? p.resultsView : cat ? <React.Fragment><h1>{displayName(cat.name)}</h1><p className="oo-zeta-menu-count">{cat.productIds.length + cat.comboIds.length} items</p>
        {cat.subcategories && cat.subcategories.length ? <ui.ChipRow label={displayName(cat.name) + ' types'}><ui.Chip active={!subgroup} onClick={function () { sub[1](null); }}>All</ui.Chip>{cat.subcategories.map(function (c) { return <ui.Chip key={c.id} active={subgroup && subgroup.id === c.id} onClick={function () { sub[1](c.id); }}>{displayName(c.name)}</ui.Chip>; })}</ui.ChipRow> : null}
        <ui.ProductGrid>{OO.pages.Menu.shared.itemCards(p.ctx, combos, products)}</ui.ProductGrid></React.Fragment> : <p>No dishes are available right now.</p>}{p.footer}
      </div></main>;
  };
  K.Zeta = { Hero: Hero, ActionRow: ActionRow, Reorder: Reorder, RewardsTeaser: RewardsTeaser, ProductRail: ProductRail,
    CategoryRail: CategoryRail, BranchCallout: BranchCallout, PromoCards: PromoCards, SocialLinks: SocialLinks, TargetLink: TargetLink, displayName: displayName, branchName: branchName };
})();


// ===================== src/pages/app-rewards.jsx =====================
// src/pages/app-rewards.jsx: the rewards page / loyalty card (page id 'rewards': /{hotel}/rewards; both modes since
// P3-01 (D-18), off at a table (D-20: the router sends a table to its menu)). Design: docs/online-order/design/
// customer-app.dc.txt "03 Loyalty card"; REF OnlineOrderRewardsPage (C §1.2). Owner: T4 (Batch 1 app pages); Batch 2
// P3-15, P3-17. Styles: styles/app-pages.css (.oo-rw-*, the design's phone column) and styles/account.css (P3-15: the
// reward states, and the online desktop layout; P3-17: the code slot and the membership tiles). Shared helpers:
// OO.appKit (src/pages/app-home.jsx).
//
// Data (P3-15): the page shows the wallet the engine judges with, so the card, the Redeem gates, the checkout's slider
// and the wallet page (src/pages/wallet-page.jsx) always agree:
//   the wallet   OO.store's settled get_customer_wallet_points read (OO.store.wallet(hotel); the page re-renders on
//                state.wallets) through OO.data.loadWallet(hotel): the store's own read, shared with its pricing (the
//                menu loads, so the store reads the wallet anyway: a fresh read here would be a second request on
//                landing). It is the session's balance, as REF's: P3-27 reads it again after a paid order, the wallet
//                page reads it fresh, and Try again here reads it fresh;
//   then         OO.api.getLoyalty (get_user_loyalty_products_v4, after its wallet step, which reads the cached body;
//                the fixtures' PROPOSED catalogue rides along) and OO.api.getWalletTransactions (the activity).
//   Loyalty      OO.model.normalizeLoyalty({ wallet, products, catalogue }, { store, orderType } + POLISH-5's
//                OO.data.loyaltyScope(hotel)) (P3-13) at the order type the cart prices with (OO.pages.Cart.shared
//                .pricingCtx over the checkout draft), in OO.mode.
// The card (--oo-card-dark): the program, member, tier, the member's code, the balance, the earn rate (the channel's
// credit row) and, at a store that redeems rewards, the progress to the next one; "Wallet and history" → the wallet page.
//   The scope line (POLISH-5, p5-followups #21; mode app, a chain of two or more stores: Loyalty.scope, as Home's card
//   says it): "Points shared across the {master} group" for a branch linked to the master (its customers and points
//   live on the master: B31 shared_loyalty, chainedshops.linking), else "Points for {branch} only". OO.data
//   .loyaltyScope reads the chain and the master's store as cached (OO.App's chain read, OO.useHotel's master read): none
//   until they are in, then the line shows. Elsewhere none.
//   The code (P3-17; REF OnlineOrderMembershipCard + CustomerCodeBarcode): what the POS / staff scanners read is the
//   wallet's customer_code (Loyalty.memberCode), exactly. A QR code (OO.qrcode.qrMatrix at level M, qrPath with a
//   quiet zone of 1 inside the design's white 132px box) that a tap turns into a Code 128-B barcode (code128B +
//   barsPath, quiet 10) drawn across the card's width (preserveAspectRatio none: in the 132px box a 7-digit code
//   would be under a pixel a module, too thin to scan), and back. Both are drawn black on white by their own SVG
//   fills, so no theme reaches them. The code's digits and "Tap for barcode" / "Tap for QR code" sit under it. An
//   encoder that fails (a code Code 128-B can't hold; OO.qrcode missing) shows "Barcode not available" / "QR code
//   not available" in the box. Without a customer_code there is no code slot at all (REF falls back to customer_id
//   for the member number; nothing scans that, so it isn't shown).
// The membership (P3-17; REF RP:131-232 "Active membership", membershipBenefits.ts): under the card, the signed-in
// customer's membership type as the membership lane reads it (OO.membership.of(the wallet): never parsed here) — its
// name, its description and one tile per benefit the engine applies, in REF's order: "Order discount" (the cart
// rule), "Product discount", "Category discount": "10% off", "Up to $5.00 per order" when the rule is capped, and the
// products / categories it names that are on the menu as chips → the menu's deep link (?type=product|category&id=;
// products hidden by D-07 and ids the menu doesn't have are left out; categories are the top-level ones, as
// OO.membership.offerFor matches a product's categoryId). A note says the discounts come off automatically, but not
// alongside a coupon, points or a reward (OO.membership.exclusivity). No membership (none, a deleted type, no name):
// no section. A membership with no benefit the engine applies shows its name and description only (REF).
// The rewards (REF RP:243-451), on the checkout's own logic (P2-07, OO.pages.CheckoutSections.wallet):
//   the list is rewardItems(the v4 body, the slot's menu at the cart's pricing (OO.pages.Cart.shared.couponMoment), the
//   cart's lines, the engine ctx (OO.wallet.ctxOf: the settled wallet, the order type, the stored coupon), the order's
//   moment (D-07)): rewards the menu has, addable ones first. Each row's state (logic.rewardView, from the list's own
//   judgement: its addable flag and refusal code):
//     'redeem'       addable → Redeem: OO.wallet.addReward (its gates, then the membership lane's stacking rules) → what
//                    it drops (the coupon: OO.store.setCoupon(null); the points: OO.wallet.dropPoints) →
//                    OO.store.cartAdd(the reward line) → a toast. Checkout redeems it (wallet type 2). Never a local
//                    fake: the Batch 1 OO.store.redeem is gone. P3-17: a reward with a size or options to choose
//                    (RewardItem.options; "From N points" when its sizes differ: judged at its cheapest) opens the
//                    item sheet in reward mode instead (<OO.pages.ItemSheet reward={{ row, orderType, table: null }}>
//                    over the list's menu and moment), which makes the same checks on the selection and adds the line;
//                    the same toast.
//     'locked'       not enough points left after the cart's rewards (addReward SHORT): 'N of M points' + 'K more
//                    needed' and a progress bar (REF).
//     'limit'        the daily cap (the whole list, as REF), or more points than one order may take (OVER_MAX).
//     'unavailable'  greyed by D-07 (its label), sold out, no size or required choice available, or no points cost.
//   The whole list (logic.listState, from OO.wallet.rewardBudget at the cart's lines): 'cart' (a cart-level store: REF's
//   "uses cart-level wallet redemption" copy + Order now) · 'off' (no row for this kind of order: NO_RULE / NO_MAX /
//   KIOSK) · 'loading' · 'failed' (the v4 read failed: Try again) · 'empty' (none listed, or v4's "Redemption ratio not
//   set": 'No rewards yet' with the balance + Order now) · 'list'. The daily cap shows its notice above the list.
//   Reward lines already in the cart: a line with their points, the points left, and View cart. Stale reward lines
//   (signed out, the cap, a cart-level store) go with OO.pages.Cart.shared.useStaleRewards' text (REF CC), as on the
//   cart and checkout pages.
// Activity: the latest ACTIVITY_MAX (5) rows (OO.model.normalizeActivity) and "See all" → the wallet page.
// "How it works" opens a small dialog. Signed out: a log-in card. No usable wallet (the loyalty module off, a failed
// read: the soft reads can't tell them apart): "Points aren't available right now" + Try again. Online, the header
// has back (→ account) and the profile ring; in app mode it is a tab page (no back).
// OO.pages.Rewards.logic (pure; tests/rewards-p315.test.mjs, tests/card-p317.test.mjs):
//   rewardView(item, budget, word) → { id, product, row, name, image, points, options, from, state, label, why,
//       progress: { have, need, short, text, more } | null }   item = a rewardItems row; budget = rewardBudget(lines,
//       ctx); word = the store's points word (P3-17: the state is the list's own judgement, item.addable / code).
//   listState(L, budget, items) → 'cart' | 'off' | 'loading' | 'failed' | 'empty' | 'list'   items undefined while
//       loading, null for a failed read.
//   cartNote(lines, budget, word) → { count, text } | null   the reward lines in the cart and the points left.
//   codeView(code) → { value, qr: { size, d } | null, bars: { width, height, d } | null } | null   (P3-17) the card's
//       code: value = the code trimmed (null for none: no code slot); qr = qrPath(qrMatrix(value, { ecc: 'M' }),
//       { quiet: 1 }), bars = barsPath(code128B(value), { height: BARS_H, quiet: 10 }); each null when its encoder
//       throws or OO.qrcode lacks it.   BARS_H = 60 (the barcode's viewBox height).
//   membershipView(wallet, menu, judge, currency) → { name, desc, tiles: [{ kind 'cart'|'product'|'category', label,
//       value ('10% off'), note ('Up to $5.00 per order' | null), targets: [{ kind 'product'|'category', id, name }]
//       }] } | null   (P3-17) the membership section; menu = the menu the list is judged with, judge = its D-07 opts
//       (null: nothing hidden).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var EMPTY = [];
  var ACTIVITY_MAX = 5;

  // ---- the member's code (P3-17; see "The code" in the header) --------------------------------------------------
  var BARS_H = 60; // the barcode's viewBox height; the box stretches it (preserveAspectRatio none)
  function codeView(code) {
    var value = code === null || code === undefined ? '' : String(code).trim();
    if (!value) return null;
    var Q = OO.qrcode || {};
    var out = { value: value, qr: null, bars: null };
    if (typeof Q.qrMatrix === 'function' && typeof Q.qrPath === 'function') {
      try {
        var p = Q.qrPath(Q.qrMatrix(value, { ecc: 'M' }), { quiet: 1 });
        if (p && Number(p.size) > 0 && typeof p.d === 'string' && p.d) out.qr = { size: Number(p.size), d: p.d };
      } catch (e) { out.qr = null; }
    }
    if (typeof Q.code128B === 'function' && typeof Q.barsPath === 'function') {
      try {
        var b = Q.barsPath(Q.code128B(value), { height: BARS_H, quiet: 10 });
        if (b && Number(b.width) > 0 && typeof b.d === 'string' && b.d) out.bars = { width: Number(b.width), height: BARS_H, d: b.d };
      } catch (e) { out.bars = null; }
    }
    return out;
  }

  // ---- the membership (P3-17; see "The membership" in the header) -----------------------------------------------
  var BENEFITS = [['cart', 'Order discount'], ['product', 'Product discount'], ['category', 'Category discount']];
  function pctText(pct) { return String(Math.round(Math.min(100, num(pct)) * 100) / 100); } // 12.5 → '12.5'; over 100 saves 100 %
  function membershipView(wallet, menu, judge, currency) {
    var Mb = OO.membership;
    var m = Mb && typeof Mb.of === 'function' ? Mb.of(wallet) : null;
    if (!m || !m.name) return null;
    var products = (menu && menu.products) || {};
    var cats = (menu && Array.isArray(menu.categories) ? menu.categories : []).filter(function (c) { return c && c.kind !== 'combo'; });
    var tiles = [];
    BENEFITS.forEach(function (b) {
      var r = m.rules ? m.rules[b[0]] : null;
      if (!r) return;
      var targets = [];
      (Array.isArray(r.ids) ? r.ids : []).forEach(function (id) {
        if (b[0] === 'product') {
          var p = Object.prototype.hasOwnProperty.call(products, id) ? products[id] : null;
          if (!p || p.kind === 'combo') return;
          var d = judge && menu ? OO.model.displayState(menu, p, judge) : null;
          if (d && d.show === 'hide') return;
          targets.push({ kind: 'product', id: p.id, name: p.name });
        } else if (b[0] === 'category') {
          var c = cats.filter(function (x) { return num(x.id) === id; })[0];
          if (c) targets.push({ kind: 'category', id: c.id, name: c.name });
        }
      });
      tiles.push({
        kind: b[0], label: b[1], value: pctText(r.pct) + '% off',
        note: r.max !== null && r.max > 0 ? 'Up to ' + OO.format.money(r.max, currency || '$') + ' per order' : null, targets: targets,
      });
    });
    return { name: m.name, desc: m.desc || '', tiles: tiles };
  }

  function earnText(L, store) {
    var n = L.earnPerDollar;
    if (!n) return 'Show at the counter to earn ' + L.pointsWord;
    var unit = L.pointsWord === 'points' ? (n === 1 ? 'pt' : 'pts') : OO.appKit.pointsText(n, L.pointsWord).replace(/^\S+\s/, '');
    return 'Show at the counter to earn ' + n + ' ' + unit + ' per ' + ((store && store.currency) || '$') + '1';
  }
  function shortPoints(n, word) {
    return word === 'points' ? n + ' pts' : OO.appKit.pointsText(n, word);
  }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  // '1,250 points' · '1 point' (the store's word).
  function pointsText(n, word) {
    var w = word || 'points';
    var v = Math.abs(num(n));
    return v.toLocaleString('en-NZ') + ' ' + (v === 1 ? w.replace(/s$/i, '') : w);
  }
  function wordOf(store) { return (store && store.loyalty && store.loyalty.pointsWord) || 'points'; }

  // ---- logic (pure; see the header) ------------------------------------------------------------------------------
  var LABEL = { redeem: 'Redeem', locked: 'Locked', limit: 'Limit reached', unavailable: 'Unavailable' };
  var UNAVAILABLE = 'Not available right now';
  // P3-17: from the list's own judgement (rewardItems' addable / code / reason), never a second addReward.
  function rewardView(item, budget, word) {
    var out = {
      id: item.id, product: item.product, row: item.row, name: item.name, image: item.image || null, points: item.points,
      options: !!item.options, from: !!item.from, state: 'redeem', label: '', why: null, progress: null,
    };
    if (item.addable) out.state = 'redeem';
    else if (budget && budget.reason === 'LIMIT') out.state = 'limit'; // the whole list (the notice above says why)
    else if (item.code === 'SHORT') {
      var need = num(item.points), have = Math.min(need, Math.max(0, num(budget && budget.left)));
      out.state = 'locked';
      out.progress = {
        have: have, need: need, short: Math.max(0, need - have),
        text: have.toLocaleString('en-NZ') + ' of ' + pointsText(need, word), more: Math.max(0, need - have).toLocaleString('en-NZ') + ' more needed',
      };
    } else if (item.code === 'OVER_MAX' || item.code === 'LIMIT') { out.state = 'limit'; out.why = item.reason || null; } else {
      out.state = 'unavailable'; // greyed by D-07 (its label), sold out, no points cost, …
      out.why = item.reason || UNAVAILABLE;
    }
    out.label = LABEL[out.state];
    return out;
  }
  function listState(L, budget, items) {
    var reason = budget ? budget.reason : null;
    if (reason === 'CART_LEVEL') return 'cart';
    if (reason === 'NO_RULE' || reason === 'NO_MAX' || reason === 'KIOSK' || reason === 'NO_WALLET') return 'off';
    if (items === undefined) return 'loading';
    if (items === null) return L && L.rewardsRead === 'off' ? 'empty' : 'failed';
    return items.length ? 'list' : 'empty';
  }
  function cartNote(lines, budget, word) {
    var mine = (lines || []).filter(function (l) { return OO.wallet.isReward(l); });
    if (!mine.length) return null;
    var used = budget && budget.enabled ? budget.committed : mine.reduce(function (t, l) { return t + OO.wallet.rewardPoints(l, null); }, 0);
    var text = (mine.length === 1 ? '1 reward in your cart uses ' : mine.length + ' rewards in your cart use ') + pointsText(used, word) + '.';
    if (budget && budget.enabled) text += ' ' + pointsText(budget.left, word) + ' left.';
    return { count: mine.length, text: text };
  }

  // ---- the parts -------------------------------------------------------------------------------------------------
  // The code slot (see "The code" in the header): a toggle over the white box, the digits and the hint under it.
  // Hook-free: MemberCard holds the QR / barcode choice.
  function MemberCode(props) {
    var c = props.code, bars = props.bars;
    var art;
    if (bars) {
      art = c.bars ? (
        <svg className="oo-rw-qr__svg" viewBox={'0 0 ' + c.bars.width + ' ' + c.bars.height} preserveAspectRatio="none"
          shapeRendering="crispEdges" role="img" aria-label={'Barcode of membership number ' + c.value} focusable="false">
          <rect width={c.bars.width} height={c.bars.height} fill="#fff" />
          <path d={c.bars.d} fill="#000" />
        </svg>
      ) : <span className="oo-rw-qr__none">Barcode not available</span>;
    } else {
      art = c.qr ? (
        <svg className="oo-rw-qr__svg" viewBox={'0 0 ' + c.qr.size + ' ' + c.qr.size} shapeRendering="crispEdges" role="img"
          aria-label={'QR code of membership number ' + c.value} focusable="false">
          <rect width={c.qr.size} height={c.qr.size} fill="#fff" />
          <path d={c.qr.d} fill="#000" />
        </svg>
      ) : <span className="oo-rw-qr__none">QR code not available</span>;
    }
    return (
      <div className="oo-rw-code">
        <button type="button" className={OO.ui.cx('oo-rw-qr', bars && 'oo-rw-qr--bars')} aria-pressed={bars ? 'true' : 'false'}
          aria-label="Show as a barcode" onClick={props.onToggle}>
          {art}
        </button>
        <span className="oo-rw-code__num">{c.value}</span>
        <span className="oo-rw-code__hint" aria-hidden="true">{bars ? 'Tap for QR code' : 'Tap for barcode'}</span>
      </div>
    );
  }

  function MemberCard(props) {
    var L = props.loyalty, ui = OO.ui;
    var goal = props.goal;
    var code = React.useMemo(function () { return codeView(L.memberCode); }, [L.memberCode]);
    var barsS = React.useState(false); // REF: the QR first; a tap shows the barcode
    var bars = !!code && barsS[0];
    return (
      <section className="oo-rw-card" aria-label={L.programName + ' card'}>
        <div className="oo-rw-card__top">
          <div className="oo-rw-card__who">
            <div className="oo-rw-card__sub">{L.programName}</div>
            <div className="oo-rw-card__name">{L.memberName || 'Member'}</div>
          </div>
          {L.tier ? <ui.Badge tone="inverse" upper>{L.tier}</ui.Badge> : null}
        </div>
        <div className={ui.cx('oo-rw-card__mid', bars && 'oo-rw-card__mid--bars')}>
          {code ? <MemberCode code={code} bars={bars} onToggle={function () { barsS[1](function (b) { return !b; }); }} /> : null}
          <div className="oo-rw-card__pts">
            <div className="oo-rw-card__num">{L.points}</div>
            <div className="oo-rw-card__sub">{L.pointsWord}</div>
            <div className="oo-rw-card__earn">{earnText(L, props.store)}</div>
            {L.scope ? <div className="oo-rw-card__scope">{L.scope.text}</div> : null}
          </div>
        </div>
        {goal ? (
          <div className="oo-rw-card__goal">
            <ui.ProgressBar tone="inverse" value={L.points} max={goal.cost || 1} label={'Progress to ' + goal.name} />
            <div className="oo-rw-card__sub">
              {goal.toGo > 0 ? shortPoints(goal.toGo, L.pointsWord) + ' to ' + goal.name : 'Enough for ' + goal.name}
            </div>
          </div>
        ) : null}
        {props.wallet ? (
          <a className="oo-rw-card__link" href={props.wallet.href} onClick={props.wallet.onClick}>Wallet and history<OO.icons.ChevronRight size={14} /></a>
        ) : null}
      </section>
    );
  }

  // The membership section (see "The membership" in the header; view = membershipView's). Hook-free.
  function Membership(props) {
    var mv = props.view;
    return (
      <section className="oo-rw-tier" aria-labelledby="oo-rw-tier-t">
        <div className="oo-rw-tier__head">
          <span className="oo-rw-tier__icon" aria-hidden="true"><OO.icons.Star size={20} /></span>
          <div className="oo-rw-tier__who">
            <h2 className="oo-rw-tier__kicker" id="oo-rw-tier-t">Your membership</h2>
            <p className="oo-rw-tier__name">{mv.name}</p>
            {mv.desc ? <p className="oo-rw-tier__desc">{mv.desc}</p> : null}
          </div>
        </div>
        {mv.tiles.length ? (
          <ul className="oo-rw-tiles" aria-label="Your member benefits">
            {mv.tiles.map(function (t) {
              return (
                <li key={t.kind} className="oo-rw-tile">
                  <span className="oo-rw-tile__label">{t.label}</span>
                  <span className="oo-rw-tile__value">{t.value}</span>
                  {t.note ? <span className="oo-rw-tile__note">{t.note}</span> : null}
                  {t.targets.length ? (
                    <span className="oo-rw-tile__chips" role="group" aria-label={t.label + ' applies to'}>
                      {t.targets.map(function (x) {
                        var l = OO.router.link('menu', null, { hotel: props.hotel, query: { type: x.kind, id: String(x.id) } });
                        return <a key={x.kind + '-' + x.id} className="oo-rw-tile__chip" href={l.href} onClick={l.onClick}>{x.name}</a>;
                      })}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {mv.tiles.length ? (
          <p className="oo-rw-tier__note">Member discounts come off eligible items in your cart and at checkout, but not alongside a coupon, points or a reward.</p>
        ) : null}
      </section>
    );
  }

  function RewardRow(props) {
    var r = props.reward, ui = OO.ui;
    var word = props.pointsWord;
    var on = r.state === 'redeem';
    var action = on
      ? <ui.Button size="xs" onClick={function () { props.onRedeem(r); }} aria-label={'Redeem ' + r.name + (r.from ? ' from ' : ' for ') + pointsText(r.points, word)}
        aria-haspopup={r.options ? 'dialog' : undefined}>Redeem</ui.Button>
      : <ui.Button size="xs" variant="soft" disabled>{r.label}</ui.Button>;
    return (
      <li className={ui.cx('oo-rw-reward', 'is-' + r.state)}>
        <span className="oo-rw-reward__img">{r.image ? <img src={r.image} alt="" loading="lazy" /> : <OO.icons.Gift size={22} />}</span>
        <span className="oo-rw-reward__text">
          <span className="oo-rw-reward__name">{r.name}</span>
          <span className="oo-rw-reward__cost">{r.points > 0 ? (r.from ? 'From ' : '') + pointsText(r.points, word) : 'Reward'}</span>
          {r.why ? <span className="oo-rw-reward__why">{r.why}</span> : null}
          {r.progress ? (
            <span className="oo-rw-reward__prog">
              <ui.ProgressBar value={r.progress.have} max={r.progress.need || 1} label={'Progress to ' + r.name} />
              <span className="oo-rw-reward__progtext">{r.progress.text + ' · ' + r.progress.more}</span>
            </span>
          ) : null}
        </span>
        {action}
      </li>
    );
  }

  function HowItWorks(props) {
    var L = props.loyalty, store = props.store, ui = OO.ui;
    var name = (L && L.programName) || store.loyalty.programName;
    var word = (L && L.pointsWord) || store.loyalty.pointsWord;
    var cheapest = L && L.rewards.length ? L.rewards[0].cost : null;
    var cartLevel = !!L && !L.productLevel;
    var steps = [
      { icon: 'Scan', title: 'Show your card', body: 'Scan your code at the counter before you pay. Online orders made while you’re logged in count too.' },
      { icon: 'Star', title: 'Earn ' + word, body: L && L.earnPerDollar ? 'Get ' + OO.appKit.pointsText(L.earnPerDollar, word) + ' for every ' + (store.currency || '$') + '1 you spend.' : 'Every order earns ' + word + '.' },
      cartLevel ? {
        icon: 'Gift', title: 'Use them at checkout',
        body: L.pointsPerDollar ? 'Take your ' + word + ' off your order: ' + OO.appKit.pointsText(L.pointsPerDollar, word) + ' = ' + (store.currency || '$') + '1.' : 'Take your ' + word + ' off your order at checkout.',
      } : { icon: 'Gift', title: 'Redeem rewards', body: cheapest ? 'Swap your ' + word + ' for free food and drinks, from ' + OO.appKit.pointsText(cheapest, word) + '.' : 'Swap your ' + word + ' for free food and drinks.' },
    ];
    return (
      <ui.Sheet open={props.open} onClose={props.onClose} variant="dialog" title={'How ' + name + ' works'}>
        <ol className="oo-rw-how">
          {steps.map(function (s) {
            var Icon = OO.icons[s.icon];
            return (
              <li key={s.title} className="oo-rw-how__step">
                <span className="oo-rw-how__icon"><Icon size={20} /></span>
                <span className="oo-rw-how__text">
                  <span className="oo-rw-how__title">{s.title}</span>
                  <span className="oo-rw-how__body">{s.body}</span>
                </span>
              </li>
            );
          })}
        </ol>
        {L && L.tier ? <p className="oo-rw-how__tier">{'You’re a ' + L.tier + ' member.'}</p> : null}
      </ui.Sheet>
    );
  }

  // The list's judging: the slot's menu at the cart's pricing (couponMoment, the checkout's) and D-07's opts.
  function momentOf(hotel, store, draft, orderType, menu) {
    var CK = OO.pages.Cart && OO.pages.Cart.shared;
    var now = OO.data.now().getTime();
    var cm = null;
    try { cm = CK && typeof CK.couponMoment === 'function' ? CK.couponMoment(hotel, store, draft || {}, orderType, menu) : null; } catch (e) { cm = null; }
    var judge = { now: cm && cm.at != null ? cm.at : now, today: now, timeZone: store.timezone, orderType: orderType, domain: OO.data.domain(orderType, OO.mode) };
    if (OO.model.hideOutsideAvailability(store, OO.mode)) judge.hideOutsideAvailability = true;
    return {
      menu: (cm && cm.menu) || menu,
      judge: judge,
    };
  }
  function orderTypeOf(store, draft) {
    var CK = OO.pages.Cart && OO.pages.Cart.shared;
    try { if (CK && typeof CK.pricingCtx === 'function') return CK.pricingCtx(store, draft || {}).orderType || 'PICKUP'; } catch (e) { /* PICKUP */ }
    return 'PICKUP';
  }
  function selectWallets(s) { return s.wallets; }
  function selectDraft(s) { return s.checkout; }

  OO.pages.Rewards = function Rewards(props) {
    var route = props.route;
    var hotel = route.hotel;
    var hk = String(hotel);
    var mode = props.mode || OO.mode;
    var K = OO.appKit, ui = OO.ui;
    var CK = OO.pages.Cart.shared;
    var CSW = OO.pages.CheckoutSections.wallet;
    var h = OO.useHotel(hotel); // with the menu: rewards are menu products (D-07, their prices)
    var store = h.store;
    var session = K.useSession();
    var uid = session && session.customer && session.customer.uid ? String(session.customer.uid) : null;
    OO.useStore(selectWallets); // the settled wallet (below) re-renders the page
    var cart = OO.useStore(function (s) { return s.hotel === hk ? { lines: s.cart.lines, coupon: s.cart.coupon || null } : { lines: EMPTY, coupon: null }; });
    var draft = OO.useStore(selectDraft);
    var howState = React.useState(false);
    var toastState = React.useState(null); // { id, text }
    var sheetState = React.useState(null); // P3-17: the reward open in the item sheet's reward mode ({ id, row }) | null
    function say(text) { if (text) toastState[1]({ id: Date.now() + Math.random(), text: text }); }
    // The store's wallet read, then the rewards and the activity (see the header). Try again reads the wallet fresh:
    // the store keeps a failed read for the page load.
    var freshRef = React.useRef(false);
    var read = OO.useAsync(function () {
      if (!uid || !store) return null;
      var o = freshRef.current ? { fresh: true } : undefined;
      freshRef.current = false;
      return OO.data.loadWallet(hotel, o).then(function (w) {
        if (!OO.wallet.usable(w)) return { products: null, catalogue: null, tx: null };
        return Promise.all([
          OO.api.getLoyalty(hotel, { uid: uid }).then(function (r) { return r || {}; }, function () { return {}; }),
          OO.api.getWalletTransactions(hotel, { uid: uid }).then(function (t) { return t || null; }, function () { return null; }),
        ]).then(function (x) { return { products: x[0].products || null, catalogue: x[0].catalogue || null, tx: x[1] }; });
      });
    }, [hotel, uid, !!store]);
    function retry() { freshRef.current = true; read.reload(); }
    var data = read.data || null;
    var wallet = uid ? OO.wallet.usable(OO.store.wallet(hotel)) : null;
    var mine = draft && (!draft.hotel || String(draft.hotel) === hk) ? draft : null;
    var orderType = store ? orderTypeOf(store, mine) : 'PICKUP';
    var scope = OO.data.loyaltyScope ? OO.data.loyaltyScope(hotel) : {}; // POLISH-5: { chain, master } in mode app, else {}
    var L = React.useMemo(function () {
      if (!wallet || !store) return null;
      var raw = { wallet: wallet };
      if (data) { raw.products = data.products; raw.catalogue = data.catalogue; }
      return OO.model.normalizeLoyalty(raw, Object.assign({ store: store, orderType: orderType }, scope));
    }, [wallet, store, orderType, data, scope.chain, scope.master]);
    var ctx = store ? OO.wallet.ctxOf(store, { hotel: hk, orderType: orderType, table: null, coupon: cart.coupon }) : null;
    CK.useStaleRewards(ctx, cart.lines, say); // REF CC's auto-removal (see the header)
    var budget = ctx && wallet ? OO.wallet.rewardBudget(cart.lines, ctx) : null;
    var moment = store && h.menu ? momentOf(hotel, store, mine, orderType, h.menu) : null;
    var items = data && moment ? CSW.rewardItems(data.products, moment.menu, cart.lines, ctx, moment.judge) : undefined;
    var views = items ? items.map(function (it) { return rewardView(it, budget, store ? wordOf(store) : 'points'); }) : items;
    var activity = React.useMemo(function () {
      return data && data.tx && store ? OO.model.normalizeActivity(data.tx, { now: OO.data.now(), timeZone: store.timezone, pointsPerDollar: L ? L.pointsPerDollar : null }) : null;
    }, [data, store, L ? L.pointsPerDollar : null]);

    function redeem(r) {
      // P3-17: a size or options to choose → the item sheet's reward mode (below), which adds the line itself.
      if (r.options) {
        if (OO.pages.ItemSheet) sheetState[1]({ id: r.id, row: r.row });
        else say('This reward can’t be added right now.');
        return;
      }
      var x = OO.wallet.addReward(r.product, { sizeId: null, sel: {} }, r.row, OO.store.cartLines(), OO.wallet.ctxOf(store, { hotel: hk, orderType: orderType, table: null, coupon: OO.store.get().cart.coupon || null }));
      if (!x.ok) { say(x.refuse ? x.refuse.text : 'This reward can’t be added right now.'); return; }
      if (x.drop.indexOf('coupon') >= 0) OO.store.setCoupon(null);
      if (x.drop.indexOf('points') >= 0) OO.wallet.dropPoints(hk);
      OO.store.cartAdd(x.line);
      say(r.name + ' added to your cart as a reward.' + (x.notice ? ' ' + x.notice.text : ''));
    }

    var word = store ? wordOf(store) : 'points';
    var body;
    if (h.notFound) body = <K.NotFound />;
    else if (!store) body = h.error ? <K.ErrorCard title="We couldn't load this store" onRetry={h.reload} /> : <RewardsSkeleton />;
    else if (!uid) {
      body = (
        <K.LoginPrompt hotel={hotel} title={'Log in to see your ' + store.loyalty.programName + ' card'}
          body={'Earn ' + store.loyalty.pointsWord + ' on every order and swap them for free food.'} />
      );
    } else if (!wallet && (read.loading || read.data === undefined)) body = <RewardsSkeleton />;
    else if (!wallet || !L) {
      body = (
        <ui.EmptyState className="oo-app-state" icon="Gift" title={'Points aren’t available right now'}
          body={'We couldn’t load your ' + word + ' for ' + store.name + '. Try again, or ask the team in store.'}
          action={<ui.Button size="sm" variant="outline" onClick={retry}>Try again</ui.Button>} />
      );
    } else {
      var walletLink = OO.router.link('wallet', null, { hotel: hotel });
      var cartLink = OO.router.link('cart', null, { hotel: hotel });
      var note = cartNote(cart.lines, budget, word);
      var mv = membershipView(wallet, moment ? moment.menu : h.menu, moment ? moment.judge : null, store.currency); // P3-17
      body = (
        <React.Fragment>
          <div className="oo-rw__side">
            <MemberCard loyalty={L} store={store} goal={L.productLevel ? L.goal : null} wallet={walletLink} />
            {L.usageLimitOver ? (
              <ui.InlineNotice tone="warn" role="alert" className="oo-rw__notice">
                {(L.usageText || 'You’ve reached today’s limit for redeeming points.') + ' Rewards are still shown, but can’t be redeemed today.'}
              </ui.InlineNotice>
            ) : null}
            {mv ? <Membership view={mv} hotel={hotel} /> : null}
          </div>
          <div className="oo-rw__main">
            <section className="oo-rw-sec" aria-labelledby="oo-rw-redeem-t">
              <ui.SectionTitle level="section" id="oo-rw-redeem-t">Redeem</ui.SectionTitle>
              {note ? (
                <p className="oo-rw-incart" role="status">
                  <span>{note.text}</span>
                  <a className="oo-rw-incart__link" href={cartLink.href} onClick={cartLink.onClick}>View cart</a>
                </p>
              ) : null}
              <RewardList state={listState(L, budget, views)} views={views} L={L} word={word} hotel={hotel} onRedeem={redeem} onRetry={retry} />
            </section>
            <section className="oo-rw-sec" aria-labelledby="oo-rw-activity-t">
              <ui.SectionTitle level="section" id="oo-rw-activity-t"
                action={activity && activity.length > ACTIVITY_MAX ? <ui.Button variant="link" href={walletLink.href} onClick={walletLink.onClick}>See all</ui.Button> : null}>Activity</ui.SectionTitle>
              <ActivityList read={read} activity={activity} onRetry={retry} />
            </section>
          </div>
        </React.Fragment>
      );
    }

    // P3-17: the item sheet in reward mode for a reward with options, over the list's own menu and moment.
    var Sheet = OO.pages.ItemSheet;
    var open = sheetState[0];
    var sheet = open && Sheet && store && moment ? (
      <Sheet key={'reward-' + open.id} hotel={hotel} store={store} menu={moment.menu} productId={open.id} moment={moment.judge}
        reward={{ row: open.row, orderType: orderType, table: null }}
        onClose={function () { sheetState[1](null); }} onAdded={function (line, info) { say(info.message); }} />
    ) : null;
    var kitchen = mode === 'app' && OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(props.route.chain || hotel);
    if (kitchen && store && !uid && K.KitchenRewardsGuest) return <K.KitchenRewardsGuest route={props.route} store={store} design={kitchen} />;
    var pizzaClub = mode === 'app' && OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(props.route.chain || hotel);
    if (pizzaClub && store && props.route.query.catalogue !== 'true' && K.PizzaClubClub) return <React.Fragment>
      <K.PizzaClubClub hotel={hotel} store={store} route={props.route} signedIn={!!uid} onHow={function () { howState[1](true); }}>{body}</K.PizzaClubClub>
      <HowItWorks open={howState[0]} onClose={function () { howState[1](false); }} loyalty={L} store={store} />
      {sheet}
      {toastState[0] ? <ui.Toast key={toastState[0].id} open onClose={function () { toastState[1](null); }}>{toastState[0].text}</ui.Toast> : null}
    </React.Fragment>;
    var head = mode === 'app' ? {} : OO.pages.acct.headerProps({ hotel: hotel, mode: mode, back: OO.router.href('account', null, { hotel: hotel }), profile: true });
    return (
      <div className="oo-apg oo-rw">
        <ui.PageHeader title="Rewards" {...head}
          right={store ? <ui.HeaderLink onClick={function () { howState[1](true); }}>How it works</ui.HeaderLink> : null} />
        <main className="oo-apg__body oo-rw__body" aria-busy={!store || (!!uid && !wallet && read.loading) ? 'true' : undefined}>
          {body}
        </main>
        {store ? <HowItWorks open={howState[0]} onClose={function () { howState[1](false); }} loyalty={L} store={store} /> : null}
        {sheet}
        {toastState[0] ? <ui.Toast key={toastState[0].id} open onClose={function () { toastState[1](null); }}>{toastState[0].text}</ui.Toast> : null}
      </div>
    );
  };

  function RewardList(props) {
    var ui = OO.ui, L = props.L, word = props.word;
    var menu = OO.router.link('menu', null, { hotel: props.hotel });
    var order = <ui.Button size="sm" variant="outline" href={menu.href} onClick={menu.onClick}>Order now</ui.Button>;
    switch (props.state) {
      case 'cart':
        return <ui.EmptyState className="oo-app-state" icon="Gift" title="Use your points at checkout" body={'This store takes ' + word + ' off your order instead of swapping them for rewards.'} action={order} />;
      case 'off':
        return <ui.EmptyState className="oo-app-state" icon="Gift" title="Rewards aren’t available for this kind of order yet" body="Check back soon, or ask the team in store." action={order} />;
      case 'loading':
        return <div aria-busy="true"><span className="oo-sr-only" role="status">Loading your rewards</span><ui.Skeleton variant="block" height={80} count={2} /></div>;
      case 'failed':
        return (
          <ui.EmptyState className="oo-app-state" icon="Info" role="alert" title="We couldn’t load your rewards" body="Check your connection and try again."
            action={<ui.Button size="sm" variant="outline" onClick={props.onRetry}>Try again</ui.Button>} />
        );
      case 'empty':
        return <ui.EmptyState className="oo-app-state" icon="Gift" title="No rewards yet" body={'You have ' + pointsText(L.walletPoints, word) + '. Every order earns more.'} action={order} />;
      default:
        return (
          <ul className="oo-rw-rewards">
            {props.views.map(function (r) { return <RewardRow key={r.id} reward={r} pointsWord={word} onRedeem={props.onRedeem} />; })}
          </ul>
        );
    }
  }

  function ActivityList(props) {
    var read = props.read, list = props.activity, ui = OO.ui;
    if (!list) {
      if (read.loading || read.data === undefined) return <ui.Skeleton variant="block" height={168} />;
      return (
        <div className="oo-rw-activity oo-rw-activity--msg" role="alert">
          <span>We couldn’t load your activity.</span>
          <ui.Button variant="link" onClick={props.onRetry}>Try again</ui.Button>
        </div>
      );
    }
    if (!list.length) return <ui.EmptyState className="oo-app-state" title="No activity yet. Your points history shows up here." />;
    return (
      <ul className="oo-rw-activity">
        {list.slice(0, ACTIVITY_MAX).map(function (a) {
          return (
            <li key={a.id} className="oo-rw-act">
              <span className="oo-rw-act__text">
                <span className="oo-rw-act__title">{a.title}</span>
                <span className="oo-rw-act__when">{a.when}</span>
              </span>
              <span className={OO.ui.cx('oo-rw-act__pts', a.points >= 0 && 'is-credit')}>{a.pointsLabel}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  function RewardsSkeleton() {
    return (
      <div className="oo-app-skel" aria-hidden="true">
        <OO.ui.Skeleton variant="block" height={252} />
        <OO.ui.Skeleton variant="block" height={80} count={3} />
      </div>
    );
  }

  OO.pages.Rewards.logic = {
    rewardView: rewardView, listState: listState, cartNote: cartNote, ACTIVITY_MAX: ACTIVITY_MAX,
    codeView: codeView, membershipView: membershipView, BARS_H: BARS_H, // P3-17
  };
})();


// ===================== src/pages/app-booking.jsx =====================
// src/pages/app-booking.jsx: table booking (page id 'booking': /{hotel}/booking, in both modes since P3-01: D-18; off at a
// table, D-20). Design: docs/online-order/design/customer-app.dc.txt "04 Book a table" (state machine: lines 500–506).
// Owner: T4 (Batch 1 app pages); Batch 2 P3-18 (wire fixes, online mode, PENDING copy) and P3-19 (the request's QR and
// track link, the guest-limit dialog, dietary chips). Styles: styles/app-pages.css (.oo-bk-*) and styles/bookings.css
// (the online desktop layout, the QR, the dialog, the chips). Shared helpers: OO.appKit (app-home.jsx).
//
// Gate: a store whose getRestaurant table_reservation is off never asks for the config (REF) and shows "not available".
// Config: OO.api.getBookingConfig → OO.model.normalizeBooking at the store's day (OO.data.now(), store.timezone): the
// party sizes (REF's bounds), the bookable dates. A 400 / 403 / 404 (no reservation settings, e.g. the harbour fixture;
// not licensed) or a paused config (is_active 0: its inactive_reason, with links to the store's hours and the menu)
// shows a friendly "not available" state; any other failure can be retried. Guests (GuestPicker: a tile per size, or
// min … min + 6 and an '8+' tile under which a stepper picks the exact party up to the maximum), Date (DateStrip of the
// dates from today in the store's time zone), Time (TimeGrid from OO.api.getBookingSlots → OO.model.normalizeSlots,
// refetched per date + party size: a slot is bookable when the server allows it AND its remaining seats fit the party;
// 'full' slots are struck through, slots already started (their stimeEpoch before now) are dimmed: past wins), Your
// details, Special requests with REF's dietary chips.
// Guest limit (P3-19; REF OnlineOrderBookingGuestLimitDialog): only when the store sets a per-booking maximum
// (booking.perBookingCap) a line says so, and its "Booking for more?" opens a dialog to contact the restaurant (phone,
// email, address with its map link: the config's shop details, else the store's). A refusal naming the server's
// maximumPeoplePerBooking opens it too.
// Dietary chips (P3-19; REF TABLE_BOOKING_DIETARY_REQUIREMENTS): toggles; the two "no …" options exclude the others.
// What goes is the typed notes (any line starting "Dietary requirements:" dropped) plus, on its own line, REF's line
// with REF's labels, which is what staff read on the POS: 'Dietary requirements: Vegan, Nut Allergy'.
// Length: table_bookings.notes is a VARCHAR(255) (create_table_bookings, 2021), which MySQL cuts (non-strict) or refuses
// (strict) past 255 characters. So the whole value stays within NOTES_MAX (255): the field takes at most TYPED_MAX
// characters (what the longest dietary line leaves, 189), and notesWith cuts the typed part, never the dietary line.
// Your details: name, mobile and email for everyone (REF): signed in, they start from the session and the email is
// the account's (read-only, REF's locked email). Checked on Confirm by OO.model.validBookingPhone (REF's rule, by the
// STORE's time zone) and validBookingEmail; the first invalid field takes the focus.
// Confirm → OO.api.placeBooking with the chosen slot's window (reservationFrom / reservationUpto: its stimeEpoch /
// etimeEpoch; the adapter adds d-m-Y, the store time zone, the session uid). The booking is a REQUEST: the restaurant
// accepts it later (PENDING), so success reads "Request sent" and says we'll email once the restaurant confirms. A
// refusal with remainingGuests (the slot filled up meanwhile, REF) records that slot's seats left, so it greys out
// for this party, and drops the chosen time when the party no longer fits; the slots are read again. Changing
// guests / date / time starts a new booking, like the design.
// After the request (P3-19; REF's success step): the summary card shows the reservation id and, when the vendored QR
// encoder is there (OO.qrcode.qrMatrix + qrPath, P3-16; feature-checked, any encoder error = no QR), a QR of the
// reservation_id exactly (what staff scan). The CTA becomes "Track reservation" → /{hotel}/booking/track/{id}: the
// email that lookup needs never goes in the URL (B §9 Q13); this tab remembers it (handoff below) and bookings.jsx
// reads it there. "Book another table" starts over (date and party kept). No reservation id in the answer: no
// reference, no QR and no track link (REF made up a BK-#### reference; nothing could track it).
// CTA: the design's sticky bottom bar ("Select a time" (disabled) → "Confirm booking"); on the online desktop
// (OO.ui.useWide) the bar is hidden by design, so the CTA sits in a "Your booking" summary card beside the form.
//
// OO.pages.Booking.logic (pure; tests/booking-p318.test.mjs):
//   tileValue(options, guests) → the tile the GuestPicker shows picked: the '8+' tile (more: true) for any party
//       from its value up, else guests.
//   moreTile(options) → that tile | null.
//   defaultGuests(booking) → 2 when the party bounds allow it, else the minimum.
//   contactOf(customer) → { name, phone, email } from the session's customer ('' each when absent).
//   contactErrors(contact, timeZone) → { name?, phone?, email? } messages (empty when the details can go).
//   slotView(slots, caps, date, guests) → TimeGrid slots [{ value, label, reason: 'past' | 'full' | null, from, upto }]
//       from normalizeSlots' (past wins); caps = { 'YYYY-MM-DD|HH:mm': seats left } from refused requests: a capped
//       slot is 'full' for a party above it.
//   guestsText(n) → '1 guest' | '4 guests'.
//   DIETARY [{ id, label (REF's, for the notes line), text (the chip), none (the two "no …" options) }] ·
//       toggleDietary(ids, id) → ids (a "no …" option alone; any other drops them) · notesWith(text, ids) → the notes
//       that go, at most NOTES_MAX characters (see "Dietary chips") · NOTES_MAX (255) · TYPED_MAX (189).
// OO.pages.Booking.handoff (P3-19): the email a reservation's track page needs, kept for THIS TAB only (sessionStorage
//   'oo-booking-track-v1' = [{ id, email, hotel }], newest first, at most 10, plus an in-memory copy when storage is
//   blocked); never in a URL. remember(id, email, hotel) → bool · recall(id) → { email, hotel } | null (ids compared
//   trimmed, case-insensitively). Written after a request (here) and by the track lookup (bookings.jsx, P3-20).
//   clear() (P3-LOGOUT): forgets every entry, stored and in memory. A sign-out calls it (OO.store.logout, or a session
//   for another account: src/lib/store.jsx "Sign-out").
// OO.pages.Booking.Qr { value, label?, className? } (P3-19): the reservation's QR as an inline SVG (black on a white
//   quiet zone, whatever the theme: it must scan), or nothing without the encoder / when it throws. qrOf(text) →
//   { size, d } | null is the same check, on OO.pages.Booking.logic.
// OO.pages.Booking.Summary { result (place_table_booking's data), sent (what went: date, time, label, guests, name,
//   email, phone, notes), store, when, onAnother? }: the request summary card shown after a booking (renders no contact
//   details); onAnother adds its "Book another table".
// OO.pages.Booking.GuestLimit { open, max, store, contact (booking.contact), onClose } (P3-19): the guest-limit dialog.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var FAILED = 'We couldn’t book that table. Please try again.';
  var DETAILS = 'Add your details so the venue can confirm your table.';

  function str(v) { return v === null || v === undefined ? '' : String(v); }
  function guestsText(n) { return n + (Number(n) === 1 ? ' guest' : ' guests'); }
  function dayText(d) { return d ? (d.dow === 'Today' ? 'Today' : d.dow + ' ' + d.day) : ''; }
  function telOf(phone) { var p = str(phone).replace(/[^\d+]/g, ''); return p ? 'tel:' + p : null; }

  // ---- pure logic (see the header) -------------------------------------------------------------------------------
  function moreTile(options) { return (options || []).filter(function (o) { return o && o.more; })[0] || null; }
  function tileValue(options, guests) {
    var more = moreTile(options);
    return more && guests >= more.value ? more.value : guests;
  }
  function defaultGuests(booking) {
    if (!booking) return null;
    return booking.minGuests <= 2 && booking.maxGuests >= 2 ? 2 : booking.minGuests;
  }
  function contactOf(customer) {
    var c = customer || {};
    return { name: str(c.name).trim(), phone: str(c.phone).trim(), email: str(c.email).trim() };
  }
  function contactErrors(c, timeZone) {
    var M = OO.model, e = {};
    if (!str(c.name).trim()) e.name = 'Enter your name';
    if (!str(c.phone).trim()) e.phone = 'Enter your mobile number';
    else if (!M.validBookingPhone(c.phone, timeZone)) e.phone = 'Enter a valid mobile number';
    if (!str(c.email).trim()) e.email = 'Enter your email';
    else if (!M.validBookingEmail(c.email)) e.email = 'Enter a valid email';
    return e;
  }
  function slotView(slots, caps, date, guests) {
    return (slots || []).map(function (s) {
      var key = date + '|' + s.time;
      var left = caps && Object.prototype.hasOwnProperty.call(caps, key) ? caps[key] : null;
      var full = s.full || (left !== null && left < guests);
      return { value: s.time, label: s.label, from: s.from, upto: s.upto, reason: s.past ? 'past' : (full ? 'full' : null) };
    });
  }

  // ---- dietary chips (P3-19; REF's ids and labels: the labels go to the POS as written) -----------------------------
  var DIETARY = [
    { id: 'no-specific', label: 'No Specific Dietary Requirements.', text: 'No specific requirements', none: true },
    { id: 'vegetarian', label: 'Vegetarian', text: 'Vegetarian' },
    { id: 'vegan', label: 'Vegan', text: 'Vegan' },
    { id: 'nut-allergy', label: 'Nut Allergy', text: 'Nut allergy' },
    { id: 'gluten-free', label: 'Gluten-Free', text: 'Gluten-free' },
    { id: 'no-allergies', label: 'No Allergies Or Preferences.', text: 'No allergies or preferences', none: true },
  ];
  var DIETARY_PREFIX = 'Dietary requirements:';
  function dietaryOf(id) { return DIETARY.filter(function (d) { return d.id === id; })[0] || null; }
  function toggleDietary(ids, id) {
    var cur = (ids || []).filter(dietaryOf);
    var d = dietaryOf(id);
    if (!d) return cur;
    if (cur.indexOf(id) >= 0) return cur.filter(function (x) { return x !== id; });
    if (d.none) return [id];
    return cur.filter(function (x) { return !dietaryOf(x).none; }).concat([id]);
  }
  function dietaryLine(ids) {
    var labels = DIETARY.filter(function (d) { return (ids || []).indexOf(d.id) >= 0; }).map(function (d) { return d.label; });
    return labels.length ? DIETARY_PREFIX + ' ' + labels.join(', ') : '';
  }
  var NOTES_MAX = 255; // table_bookings.notes: VARCHAR(255)
  // The longest line the chips can make: every option but the two "no …" ones, or one of those alone.
  var LINE_MAX = Math.max.apply(null, [dietaryLine(DIETARY.filter(function (d) { return !d.none; }).map(function (d) { return d.id; }))]
    .concat(DIETARY.filter(function (d) { return d.none; }).map(function (d) { return dietaryLine([d.id]); })).map(function (l) { return l.length; }));
  var TYPED_MAX = NOTES_MAX - LINE_MAX - 1;
  function notesWith(text, ids) {
    var base = str(text).split(/\r?\n/).filter(function (line) { return line.trim().indexOf(DIETARY_PREFIX) !== 0; }).join('\n').trim();
    var line = dietaryLine(ids);
    var room = NOTES_MAX - (line ? line.length + 1 : 0);
    if (base.length > room) base = base.slice(0, room).trim();
    return [base, line].filter(Boolean).join('\n');
  }

  // ---- the track page's email, for this tab only (P3-19; see the header) ---------------------------------------------
  var HANDOFF_KEY = 'oo-booking-track-v1';
  var HANDOFF_MAX = 10;
  var handoffMemo = [];
  function sessionStore() { try { return window.sessionStorage || null; } catch (e) { return null; } }
  function idKey(id) { return str(id).trim().toUpperCase(); }
  function handoffList() {
    var s = sessionStore();
    if (s) {
      try {
        var v = JSON.parse(s.getItem(HANDOFF_KEY) || 'null');
        if (Array.isArray(v)) {
          return v.filter(function (x) { return x && typeof x === 'object' && idKey(x.id) && str(x.email).trim(); }).slice(0, HANDOFF_MAX);
        }
      } catch (e) { /* in memory only */ }
    }
    return handoffMemo.slice();
  }
  function remember(id, email, hotel) {
    var k = idKey(id), e = str(email).trim();
    if (!k || !e) return false;
    var list = [{ id: str(id).trim(), email: e, hotel: str(hotel) }].concat(handoffList().filter(function (x) { return idKey(x.id) !== k; }))
      .slice(0, HANDOFF_MAX);
    handoffMemo = list;
    var s = sessionStore();
    try { if (s) s.setItem(HANDOFF_KEY, JSON.stringify(list)); } catch (x) { /* in memory only */ }
    return true;
  }
  function recall(id) {
    var k = idKey(id);
    if (!k) return null;
    var hit = handoffList().filter(function (x) { return idKey(x.id) === k; })[0] || handoffMemo.filter(function (x) { return idKey(x.id) === k; })[0];
    return hit ? { email: str(hit.email).trim(), hotel: str(hit.hotel) } : null;
  }
  // P3-LOGOUT: a sign-out (OO.store.logout) forgets them all, in this tab's storage and in memory (recall reads both).
  function clearHandoff() {
    handoffMemo = [];
    var s = sessionStore();
    try { if (s) s.removeItem(HANDOFF_KEY); } catch (x) { /* nothing kept */ }
  }

  // ---- the reservation QR (P3-16's encoders, feature-checked) ---------------------------------------------------------
  function qrOf(text) {
    var Q = OO.qrcode, t = str(text).trim();
    if (!t || !Q || typeof Q.qrMatrix !== 'function' || typeof Q.qrPath !== 'function') return null;
    try {
      var p = Q.qrPath(Q.qrMatrix(t, { ecc: 'M' }), { quiet: 4 });
      return p && Number(p.size) > 0 && typeof p.d === 'string' && p.d ? { size: Number(p.size), d: p.d } : null;
    } catch (e) { return null; }
  }
  function Qr(props) {
    var p = React.useMemo(function () { return qrOf(props.value); }, [props.value]);
    if (!p) return null;
    return (
      <svg className={OO.ui.cx('oo-bk-qr__svg', props.className)} viewBox={'0 0 ' + p.size + ' ' + p.size} role="img"
        aria-label={props.label || 'QR code for reservation ' + str(props.value).trim()} shapeRendering="crispEdges" focusable="false">
        <rect width={p.size} height={p.size} fill="#fff" />
        <path d={p.d} fill="#000" />
      </svg>
    );
  }

  // ---- "Need a table for more guests?" (P3-19; REF OnlineOrderBookingGuestLimitDialog) ------------------------------
  function GuestLimit(props) {
    var ui = OO.ui, store = props.store, c = props.contact || {};
    var acctIcons = (OO.pages.acct && OO.pages.acct.icons) || {};
    var phone = c.phone || store.phone;
    var tel = telOf(phone);
    var email = c.email;
    var address = c.address || (store.address && store.address.full) || '';
    var map = c.mapLink || store.mapLink;
    function row(icon, small, value) {
      return [
        <span key="i" className="oo-bk-limit__icon" aria-hidden="true">{icon}</span>,
        <span key="t" className="oo-bk-limit__text"><small>{small}</small><strong>{value}</strong></span>,
      ];
    }
    var Phone = acctIcons.Phone, Mail = acctIcons.Mail;
    return (
      <ui.Sheet open={props.open} variant="dialog" title="Need a table for more guests?" onClose={props.onClose} className="oo-bk-limit"
        footer={<ui.Button block variant="outline" onClick={props.onClose}>Close</ui.Button>}>
        <p className="oo-bk-limit__lead">
          <strong>{'A single online booking can include up to ' + props.max + ' guests.'}</strong>
          {' Contact ' + store.name + ' directly to arrange a bigger booking.'}
        </p>
        <ul className="oo-bk-limit__list">
          {tel ? <li><a className="oo-bk-limit__row" href={tel}>{row(Phone ? <Phone size={18} /> : null, 'Phone', phone)}</a></li> : null}
          {email ? <li><a className="oo-bk-limit__row" href={'mailto:' + email}>{row(Mail ? <Mail size={18} /> : null, 'Email', email)}</a></li> : null}
          {address ? (
            <li>
              {map ? <a className="oo-bk-limit__row" href={map} target="_blank" rel="noopener noreferrer">{row(<OO.icons.Pin size={18} />, 'Address · Directions', address)}</a>
                : <div className="oo-bk-limit__row">{row(<OO.icons.Pin size={18} />, 'Address', address)}</div>}
            </li>
          ) : null}
        </ul>
      </ui.Sheet>
    );
  }

  function Unavailable(props) {
    var ui = OO.ui, K = OO.appKit, store = props.store, hotel = props.hotel, app = props.mode === 'app';
    var home = K.link(hotel, 'home'); // online mode: the store landing, i.e. the menu
    var about = props.about ? K.link(hotel, 'about') : null;
    var tel = telOf(store.phone);
    return (
      <ui.EmptyState className="oo-app-state" icon="Calendar" title={props.title || 'Table booking isn’t available here'}
        body={props.reason || (store.name + ' isn’t taking table bookings ' + (app ? 'in the app' : 'online') + ' right now.' + (tel ? ' Give them a call to book.' : ''))}
        action={
          <span className="oo-bk-na__actions">
            {tel ? <ui.Button size="sm" href={tel}>{'Call ' + store.phone}</ui.Button> : null}
            {about ? <ui.Button size="sm" variant="outline" href={about.href} onClick={about.onClick}>Hours and directions</ui.Button> : null}
            <ui.Button size="sm" variant="outline" href={home.href} onClick={home.onClick}>{app ? 'Back to home' : 'Back to menu'}</ui.Button>
          </span>
        } />
    );
  }

  function Summary(props) {
    var r = props.result || {}, sent = props.sent || {}, ui = OO.ui, store = props.store;
    var ref = React.useRef(null);
    React.useEffect(function () {
      var el = ref.current;
      if (!el) return;
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, []);
    var id = str(r.reservation_id).trim();
    var rows = [
      { label: 'When', value: props.when },
      { label: 'Guests', value: guestsText(r.person_count || sent.guests) },
      r.customer_name || sent.name ? { label: 'Name', value: r.customer_name || sent.name } : null,
      id ? { label: 'Reservation ID', value: id } : null,
      r.notes || sent.notes ? { label: 'Requests', value: r.notes || sent.notes } : null,
    ].filter(Boolean);
    var tel = telOf(store.phone);
    var qr = id && qrOf(id) ? (
      <div className="oo-bk-qr">
        <div className="oo-bk-qr__copy">
          <span className="oo-bk-qr__title">Show this at the restaurant</span>
          <span className="oo-bk-qr__sub">{'Reservation ' + id}</span>
        </div>
        <Qr value={id} />
      </div>
    ) : null;
    return (
      <ui.Card radius="lg" className="oo-bk-done">
        <div className="oo-bk-done__head">
          <span className="oo-bk-done__icon"><OO.icons.Check size={18} strokeWidth={3} /></span>
          <h2 className="oo-bk-done__title" tabIndex={-1} ref={ref}>Request sent</h2>
          <ui.Badge tone="warn" size="sm" className="oo-bk-done__status">Pending</ui.Badge>
        </div>
        <p className="oo-bk-done__store">{'We’ll email you as soon as ' + store.name + ' confirms your table.'}</p>
        <dl className="oo-bk-done__rows">
          {rows.map(function (x) {
            return <div key={x.label} className="oo-bk-done__row"><dt>{x.label}</dt><dd>{x.value}</dd></div>;
          })}
        </dl>
        {qr}
        {tel ? <p className="oo-bk-done__note">Need to change it? <a href={tel}>{'Call ' + store.phone}</a></p> : null}
        {props.onAnother ? (
          <ui.Button variant="outline" size="sm" className="oo-bk-done__again" onClick={props.onAnother}>Book another table</ui.Button>
        ) : null}
      </ui.Card>
    );
  }

  OO.pages.Booking = function Booking(props) {
    var route = props.route;
    var hotel = route.hotel;
    var mode = props.mode;
    var K = OO.appKit, ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var tz = store ? store.timezone : null;
    var bookable = !!(store && store.booking);
    var session = K.useSession();
    var customer = session && session.customer ? session.customer : null;
    var wide = ui.useWide();

    // The config only for a store that takes bookings (REF: no request otherwise); null until then.
    var cfgRes = OO.useAsync(function () {
      return bookable ? OO.api.getBookingConfig(hotel) : Promise.resolve(null);
    }, [hotel, bookable]);
    var booking = React.useMemo(function () {
      return cfgRes.data ? OO.model.normalizeBooking(cfgRes.data, { now: OO.data.now(), timeZone: tz }) : null;
    }, [cfgRes.data, tz]);

    var form = React.useState({ guests: null, date: null, time: null, notes: '' });
    var contact = React.useState(function () { return contactOf(customer); });
    var errs = React.useState({});
    var caps = React.useState({}); // 'YYYY-MM-DD|HH:mm' → seats left, from a refused request (REF)
    var diet = React.useState([]); // the dietary chips picked (ids)
    var limit = React.useState(null); // the guest-limit dialog: null | the maximum it names
    var send = React.useState({ status: 'idle', result: null, error: null, sent: null }); // idle | busy | done
    var f = form[0], st = send[0];
    var nameRef = React.useRef(null), phoneRef = React.useRef(null), emailRef = React.useRef(null);

    // Signed in (here or meanwhile): empty fields start from the session (REF); the email is the account's.
    var uid = customer ? customer.uid : null;
    React.useEffect(function () {
      if (!customer) return;
      var from = contactOf(customer);
      contact[1](function (cur) {
        return { name: cur.name || from.name, phone: cur.phone || from.phone, email: cur.email || from.email };
      });
    }, [uid]);
    var emailLocked = !!(customer && str(customer.email).trim());
    var c = contact[0];
    var details = { name: c.name, phone: c.phone, email: emailLocked ? str(customer.email).trim() : c.email };

    var options = booking ? booking.guestOptions : [];
    var more = moreTile(options);
    var fallback = defaultGuests(booking);
    var guests = f.guests != null && booking ? Math.min(booking.maxGuests, Math.max(booking.minGuests, f.guests)) : fallback;
    var dates = booking ? booking.dates : [];
    var date = f.date && dates.some(function (d) { return d.ymd === f.date; }) ? f.date : (dates[0] ? dates[0].ymd : null);
    var day = dates.filter(function (d) { return d.ymd === date; })[0] || null;

    var slotsRes = OO.useAsync(function () {
      return booking && booking.enabled && date && guests ? OO.api.getBookingSlots(hotel, { date: date, guests: guests }) : Promise.resolve(null);
    }, [hotel, !!(booking && booking.enabled), date, guests]);
    var now = OO.data.now();
    var minute = Math.floor(now.getTime() / 60000);
    var slots = React.useMemo(function () {
      if (!slotsRes.data) return null;
      var list = OO.model.normalizeSlots(slotsRes.data, { guests: guests, now: now, date: date, timeZone: tz });
      return slotView(list, caps[0], date, guests);
    }, [slotsRes.data, guests, date, tz, minute, caps[0]]);
    var open = slots ? slots.filter(function (s) { return !s.reason; }) : [];
    var timeSlot = f.time ? open.filter(function (s) { return s.value === f.time; })[0] || null : null;
    var time = timeSlot ? timeSlot.value : null;
    var booked = st.status === 'done';

    // A new guests / date / time pick starts a new booking (the design resets `booked` on every pick).
    function patch(p) {
      form[1](function (cur) { return Object.assign({}, cur, p); });
      if (st.status !== 'busy') send[1]({ status: 'idle', result: null, error: null, sent: null });
    }
    function setNotes(v) { form[1](function (cur) { return Object.assign({}, cur, { notes: v }); }); }
    function setContact(k, v) {
      contact[1](function (cur) { var n = Object.assign({}, cur); n[k] = v; return n; });
      if (errs[0][k]) errs[1](function (cur) { var n = Object.assign({}, cur); delete n[k]; return n; });
    }
    function focusFirst(e) {
      var el = (e.name ? nameRef : e.phone ? phoneRef : emailRef).current;
      if (!el || !el.focus) return;
      try { el.focus({ preventScroll: true }); } catch (x) { el.focus(); }
      if (el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    function confirm() {
      if (!timeSlot || st.status === 'busy' || booked) return;
      var e = contactErrors(details, tz);
      errs[1](e);
      if (Object.keys(e).length) {
        send[1]({ status: 'idle', result: null, error: DETAILS, sent: null });
        focusFirst(e);
        return;
      }
      var sent = {
        date: date, time: time, label: timeSlot.label, guests: guests, name: details.name.trim(), phone: details.phone.trim(),
        email: details.email.trim(), notes: notesWith(f.notes, diet[0]),
      };
      send[1]({ status: 'busy', result: null, error: null, sent: null });
      OO.api.placeBooking(hotel, {
        date: date, time: time, guests: guests, name: sent.name, email: sent.email, phone: sent.phone, notes: sent.notes,
        reservationFrom: timeSlot.from, reservationUpto: timeSlot.upto,
      }).then(function (res) {
        var result = (res && res.data) || {};
        // The track page needs this email; it stays in this tab, never in the URL (see the header).
        if (result.reservation_id) remember(result.reservation_id, result.customer_email || sent.email, hotel);
        caps[1]({});
        send[1]({ status: 'done', result: result, error: null, sent: sent });
      }, function (err) {
        var left = err && typeof err.remainingGuests === 'number' && isFinite(err.remainingGuests) ? err.remainingGuests : null;
        if (left !== null) {
          caps[1](function (cur) { var n = Object.assign({}, cur); n[date + '|' + time] = left; return n; });
          if (left < guests) form[1](function (cur) { return Object.assign({}, cur, { time: null }); });
        }
        var most = err && typeof err.maximumPeoplePerBooking === 'number' && err.maximumPeoplePerBooking > 0 ? err.maximumPeoplePerBooking : null;
        if (most !== null) limit[1](most);
        send[1]({ status: 'idle', result: null, error: (err && err.message) || FAILED, sent: null });
        slotsRes.reload();
      });
    }
    // "Book another table": the next request starts with the same date and party, no time, notes or chips.
    function another() {
      form[1](function (cur) { return Object.assign({}, cur, { time: null, notes: '' }); });
      diet[1]([]);
      send[1]({ status: 'idle', result: null, error: null, sent: null });
    }

    var acct = OO.pages.acct;
    var ring = mode !== 'app' && acct && acct.headerProps ? acct.headerProps({ hotel: hotel, mode: mode, profile: true }) : {};
    var header = (
      <ui.PageHeader title="Book a table" subtitle={store ? store.name : undefined} onBack={function () { K.back(hotel); }}
        profileHref={ring.profileHref} onProfile={ring.onProfile} profileLabel={ring.profileLabel} />
    );
    var cfgStatus = cfgRes.error ? cfgRes.error.status : null;
    var cfgMissing = cfgStatus === 400 || cfgStatus === 403 || cfgStatus === 404;

    var body;
    if (h.notFound) body = <K.NotFound />;
    else if (!store && h.error) body = <K.ErrorCard title="We couldn't load this store" onRetry={h.reload} />;
    else if (!store) body = <K.ListSkeleton count={4} height={104} />;
    else if (!bookable || cfgMissing) body = <Unavailable store={store} hotel={hotel} mode={mode} />;
    else if (!booking && !cfgRes.error) body = <K.ListSkeleton count={4} height={104} />;
    else if (booking && !booking.enabled) {
      body = <Unavailable store={store} hotel={hotel} mode={mode} about title="Bookings are paused" reason={booking.reason} />;
    } else if (cfgRes.error || !booking) body = <K.ErrorCard title="We couldn't load booking times" onRetry={cfgRes.reload} />;
    else if (!dates.length) body = <Unavailable store={store} hotel={hotel} mode={mode} about reason="There are no dates open for booking right now. Please check back soon." />;

    if (store && (!bookable || cfgMissing) && mode === 'app' && OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(route.chain || hotel) && K.KitchenBookingUnavailable) return <K.KitchenBookingUnavailable store={store} hotel={hotel} />;
    if (body) {
      return (
        <div className="oo-apg oo-bk">
          {header}
          <main className="oo-apg__body oo-bk__body oo-bk__body--state">{body}</main>
        </div>
      );
    }

    var summary = timeSlot ? [guestsText(guests), dayText(day), timeSlot.label].join(' · ') : 'Pick a time to continue';
    var errorNote = st.error ? <span className="oo-bk-err" role="alert">{st.error}</span> : null;
    var timesBody;
    if (slotsRes.error && !slots) {
      timesBody = (
        <div className="oo-bk-times__msg" role="alert">
          <span>We couldn’t load times for this day.</span>
          <ui.Button variant="link" onClick={slotsRes.reload}>Try again</ui.Button>
        </div>
      );
    } else if (!slots) {
      timesBody = <ui.Skeleton variant="block" height={88} />;
    } else if (!slots.length || !open.length) {
      timesBody = (
        <React.Fragment>
          {slots.length ? <ui.TimeGrid slots={slots} value={null} /> : null}
          <p className="oo-bk-times__none">No tables left on this day. Try another date.</p>
        </React.Fragment>
      );
    } else {
      timesBody = <ui.TimeGrid slots={slots} value={time} onChange={function (v) { patch({ time: v }); }} />;
    }

    var reservationId = booked && st.result ? str(st.result.reservation_id).trim() : '';
    var track = reservationId ? K.link(hotel, 'booking-track', { reservationId: reservationId }) : null;
    var cta;
    if (track) cta = <ui.Button block icon="Ticket" href={track.href} onClick={track.onClick}>Track reservation</ui.Button>;
    else if (booked) cta = <ui.Button block onClick={another}>Book another table</ui.Button>;
    else {
      cta = (
        <ui.Button block disabled={!time} busy={st.status === 'busy'} onClick={confirm}>
          {time ? 'Confirm booking' : 'Select a time'}
        </ui.Button>
      );
    }
    var sentDay = st.sent ? dates.filter(function (d) { return d.ymd === st.sent.date; })[0] || null : null;
    var when = st.sent ? dayText(sentDay) + ' · ' + st.sent.label : '';
    var dietIds = diet[0];

    return (
      <div className="oo-apg oo-bk">
        {header}
        <main className="oo-apg__body oo-bk__body">
          <div className="oo-bk__main">
            {booked ? <Summary result={st.result} sent={st.sent} store={store} when={when} onAnother={reservationId ? another : null} /> : null}
            <ui.Card radius="lg" className="oo-bk-card">
              <h2 className="oo-bk-card__title" id="oo-bk-guests-t">Guests</h2>
              <ui.GuestPicker options={options} value={tileValue(options, guests)} label="Number of guests"
                onChange={function (v) { patch({ guests: v }); }} />
              {more && guests >= more.value ? (
                <div className="oo-bk-more">
                  <span className="oo-bk-more__label">Party size</span>
                  <ui.QtyStepper size="sm" value={guests} min={more.value} max={booking.maxGuests} label="Number of guests"
                    onChange={function (v) { patch({ guests: v }); }} />
                </div>
              ) : null}
              {booking.perBookingCap ? (
                <p className="oo-bk-cap">
                  {'Up to ' + booking.maxGuests + ' guests per online booking. '}
                  <ui.Button variant="link" aria-haspopup="dialog" onClick={function () { limit[1](booking.maxGuests); }}>Booking for more?</ui.Button>
                </p>
              ) : null}
            </ui.Card>
            <ui.Card radius="lg" className="oo-bk-card">
              <h2 className="oo-bk-card__title">Date</h2>
              <ui.DateStrip label="Date" value={date} onChange={function (v) { patch({ date: v }); }}
                days={dates.map(function (d) { return { value: d.ymd, dow: d.dow, day: d.day, label: d.label }; })} />
            </ui.Card>
            <ui.Card radius="lg" className="oo-bk-card">
              <div className="oo-bk-card__head">
                <h2 className="oo-bk-card__title">Time</h2>
                <span className="oo-bk-card__aside">Tables held for 15 mins</span>
              </div>
              <div aria-busy={slotsRes.loading ? 'true' : undefined}>{timesBody}</div>
            </ui.Card>
            <ui.Card radius="lg" className="oo-bk-card">
              <h2 className="oo-bk-card__title">Your details</h2>
              <div className="oo-bk-fields">
                <ui.TextInput label="Full name" value={details.name} onChange={function (v) { setContact('name', v); }} autoComplete="name"
                  error={errs[0].name} inputRef={nameRef} />
                <ui.TextInput label="Mobile" type="tel" inputMode="tel" value={details.phone} onChange={function (v) { setContact('phone', v); }}
                  autoComplete="tel" error={errs[0].phone} inputRef={phoneRef} />
                <ui.TextInput label="Email" type="email" inputMode="email" value={details.email} autoComplete="email" error={errs[0].email}
                  inputRef={emailRef} readOnly={emailLocked} hint={emailLocked ? 'From your account' : undefined}
                  onChange={function (v) { if (!emailLocked) setContact('email', v); }} />
              </div>
            </ui.Card>
            <ui.Card radius="lg" className="oo-bk-card">
              <div className="oo-bk-diet">
                <h2 className="oo-bk-diet__title" id="oo-bk-diet-t">Dietary requirements</h2>
                <ui.ChipRow label="Dietary requirements" className="oo-bk-diet__chips">
                  {DIETARY.map(function (d) {
                    return (
                      <ui.Chip key={d.id} active={dietIds.indexOf(d.id) >= 0} onClick={function () { diet[1](function (cur) { return toggleDietary(cur, d.id); }); }}>
                        {d.text}
                      </ui.Chip>
                    );
                  })}
                </ui.ChipRow>
              </div>
              <ui.TextArea label="Special requests" size="sm" placeholder="Birthday, high chair, allergies… (optional)" maxLength={TYPED_MAX}
                value={f.notes} onChange={setNotes} />
            </ui.Card>
          </div>
          {wide ? (
            <aside className="oo-bk__aside" aria-label="Your booking">
              <ui.Card radius="lg" className="oo-bk-sum">
                <h2 className="oo-bk-card__title">Your booking</h2>
                <dl className="oo-bk-sum__rows">
                  <div className="oo-bk-sum__row"><dt>Guests</dt><dd>{guestsText(guests)}</dd></div>
                  <div className="oo-bk-sum__row"><dt>Date</dt><dd>{day ? day.label : '—'}</dd></div>
                  <div className="oo-bk-sum__row"><dt>Time</dt><dd>{timeSlot ? timeSlot.label : 'Pick a time'}</dd></div>
                </dl>
                {errorNote}
                {cta}
              </ui.Card>
            </aside>
          ) : null}
        </main>
        {wide ? null : <ui.BottomBarCTA note={errorNote || (booked ? 'Request sent · we’ll email you once it’s confirmed' : summary)}>{cta}</ui.BottomBarCTA>}
        {limit[0] ? (
          <GuestLimit open max={limit[0]} store={store} contact={booking.contact} onClose={function () { limit[1](null); }} />
        ) : null}
      </div>
    );
  };

  OO.pages.Booking.logic = {
    tileValue: tileValue, moreTile: moreTile, defaultGuests: defaultGuests, contactOf: contactOf,
    contactErrors: contactErrors, slotView: slotView, guestsText: guestsText,
    DIETARY: DIETARY, toggleDietary: toggleDietary, notesWith: notesWith, qrOf: qrOf, NOTES_MAX: NOTES_MAX, TYPED_MAX: TYPED_MAX,
  };
  OO.pages.Booking.handoff = { remember: remember, recall: recall, clear: clearHandoff };
  OO.pages.Booking.Qr = Qr;
  OO.pages.Booking.Summary = Summary;
  OO.pages.Booking.GuestLimit = GuestLimit;
})();


// ===================== src/pages/app-notifications.jsx =====================
// src/pages/app-notifications.jsx: the customer app notifications (page id 'notifications'; app mode only:
// /{hotel}/notifications). Design: docs/online-order/design/customer-app.dc.txt "05 Notifications".
// Owner: T4 (Batch 1 app pages). Styles: styles/app-pages.css (.oo-nt-*). Shared helpers: OO.appKit (app-home.jsx).
//
// OO.api.getNotifications → OO.model.normalizeNotifications (read state = the server's read_at + the ids read in this
// browser, OO.store app.read per hotel). Groups Today / Earlier, typed icons (order · points · offer · booking), an
// unread dot. Tapping a notification marks it read (OO.store.markRead) and, when it carries a link, opens it (orders,
// rewards, a menu deep link, …); "Mark all read" in the header marks every one. Logged out: a log-in card.
//
// Hidden in production (Batch 2 P3-26, D-10 (a)) until the notifications store exists (B20: today the real adapter has
// no endpoint and answers an empty list).
//   OO.pages.Notifications.enabled(hotel) → bool: false for a live store in production, true on dev, local and staging
//       hosts and for the fixture stores: !OO.ui.liveProduction(hotel), the one production test (src/ui/chrome.jsx);
//       hotel defaults to the current route's. Every way in asks it: this route (off: a replace-redirect to the store's
//       home before paint; nothing renders and nothing is read), the app header's bell and unread badge
//       (OO.ui.AppTopBar) and the account hub's Notifications row (src/pages/app-account.jsx, the account lane's). A
//       banner or deep link naming the page lands on this route, so it goes home too.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var ICONS = { order: 'Bag', points: 'Star', offer: 'Ticket', booking: 'Calendar' };

  function enabled(hotel) {
    var ui = OO.ui;
    return !(ui && typeof ui.liveProduction === 'function' && ui.liveProduction(hotel));
  }

  // Off (P3-26): to the store's home, replacing this entry. A layout effect, as OO.App's redirects: before paint.
  function NotificationsOff(props) {
    var to = OO.router.href('home', null, { hotel: props.hotel });
    React.useLayoutEffect(function () { OO.router.go(to, { replace: true }); }, [to]);
    return null;
  }

  function groupsOf(items) {
    var out = [];
    items.forEach(function (n) {
      var last = out[out.length - 1];
      if (!last || last.name !== n.group) out.push(last = { name: n.group, items: [] });
      last.items.push(n);
    });
    return out;
  }

  function Item(props) {
    var n = props.item, hotel = props.hotel, K = OO.appKit;
    var Icon = OO.icons[ICONS[n.type] || 'Bell'];
    function read() { if (n.unread) OO.store.markRead([n.id]); }
    var inner = [
      <span key="i" className="oo-nt-item__icon"><Icon size={20} /></span>,
      <span key="t" className="oo-nt-item__text">
        <span className="oo-nt-item__top">
          <span className="oo-nt-item__title">{n.unread ? <span className="oo-sr-only">Unread: </span> : null}{n.title}</span>
          {n.time ? <span className="oo-nt-item__time">{n.time}</span> : null}
        </span>
        {n.body ? <span className="oo-nt-item__body">{n.body}</span> : null}
      </span>,
      <span key="d" className="oo-nt-item__dot" aria-hidden="true" />,
    ];
    var cls = OO.ui.cx('oo-nt-item', n.unread && 'is-unread');
    if (n.link && n.link.kind === 'url') {
      return <a className={cls} href={n.link.href} target="_blank" rel="noopener noreferrer" onClick={read}>{inner}</a>;
    }
    if (n.link && n.link.kind === 'route') {
      var l = K.link(hotel, n.link.page, n.link.params, n.link.query);
      return <a className={cls} href={l.href} onClick={function (e) { read(); l.onClick(e); }}>{inner}</a>;
    }
    return <button type="button" className={cls} onClick={read}>{inner}</button>;
  }

  // The page while notifications are on. Its own component, so the gate below never changes its hooks.
  function NotificationsPage(props) {
    var route = props.route;
    var hotel = route.hotel;
    var K = OO.appKit, ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var notes = K.useNotifications(hotel, h.store);
    var data = notes.data;
    var unread = data ? data.unread : 0;

    function markAll() { if (data) OO.store.markAllRead(data.items.map(function (n) { return n.id; })); }

    var body;
    if (h.notFound) body = <K.NotFound />;
    else if (!h.store && h.error) body = <K.ErrorCard title="We couldn't load this store" onRetry={h.reload} />;
    else if (h.store && !notes.signedIn) {
      body = <K.LoginPrompt hotel={hotel} title="Log in to see your notifications" body="Order updates, points and offers will show up here." />;
    } else if (notes.error && !data) body = <K.ErrorCard title="We couldn't load your notifications" onRetry={notes.reload} />;
    else if (!data) body = <K.ListSkeleton count={4} height={84} />;
    else if (!data.items.length) {
      body = <ui.EmptyState className="oo-app-state" icon="Bell" title="No notifications yet" body="Order updates, points and offers will show up here." />;
    } else {
      body = groupsOf(data.items).map(function (g, gi) {
        var id = 'oo-nt-g' + gi;
        return (
          <section key={g.name + gi} className="oo-nt-group" aria-labelledby={id}>
            <h2 className="oo-nt-group__title" id={id}>{g.name}</h2>
            <ul className="oo-nt-list">
              {g.items.map(function (n) { return <li key={n.id}><Item item={n} hotel={hotel} /></li>; })}
            </ul>
          </section>
        );
      });
    }

    return (
      <div className="oo-apg oo-nt">
        <ui.PageHeader title="Notifications" onBack={function () { K.back(hotel); }}
          right={data && data.items.length ? <ui.HeaderLink onClick={markAll} disabled={!unread}>Mark all read</ui.HeaderLink> : null} />
        <main className="oo-apg__body oo-nt__body" aria-busy={!data && !notes.error && notes.signedIn ? 'true' : undefined}>
          <p className="oo-sr-only" aria-live="polite">{data ? (unread ? unread + ' unread' : 'All caught up') : ''}</p>
          {body}
        </main>
      </div>
    );
  }

  OO.pages.Notifications = function Notifications(props) {
    var hotel = props.route ? props.route.hotel : undefined;
    return enabled(hotel) ? <NotificationsPage route={props.route} mode={props.mode} /> : <NotificationsOff hotel={hotel} />;
  };
  OO.pages.Notifications.enabled = enabled;
})();


// ===================== src/pages/app-account.jsx =====================
// src/pages/app-account.jsx: the account hub (page id 'account': /{hotel}/account), in both modes since P3-01 (D-18).
// App mode: the customer app's Account tab (Batch 1 T4; not in the design: built in the app's system: black header,
// canvas, white cards, black actions, Poppins). Online mode (Batch 2 P3-12): REF's account drawer as a page
// (OnlineOrderAccountDrawer: "My Account" with its bookings links, the policies, Sign out) inside the account pages'
// shell (OO.pages.acct.Page with its back arrow to the menu; acct.gate's store states). Styles: styles/app-pages.css
// (.oo-ac-*; the P3-12 rules sit in its Batch 2 P3-09…12 section). Shared helpers: OO.appKit (app-home.jsx).
//
// Signed in: a profile summary (name, email, mobile, tier · points; "Edit" → /{hotel}/edit-profile). Signed out: a
// log-in card (→ /{hotel}/profile?directLogin=true); the links stay, and each page asks for a login itself.
// POLISH-5 (p5-followups #21): under the points, the scope line Home's loyalty card shows (mode app, a chain of two or
// more stores: "Points shared across the {master} group" | "Points for {branch} only"): OO.appKit.useLoyalty with
// OO.data.loyaltyScope(hotel), the chain and the master's store as cached.
// The links (logic.sections), "Your account": Orders, Wallet, Rewards, then at a store that takes bookings
// (store.booking) Bookings, then Profile. App mode adds its app-only pages: Notifications (the
// unread count) and Stores. The Notifications row shows (and the unread count is read) only while the store's
// notifications are on: OO.pages.Notifications.enabled(hotel), the one rule of P3-26 that the route and the app
// header's bell follow too (P3-FIX #29: off for a live store in production until B20, D-10; dev, local and staging
// hosts and the fixture stores keep it).
// Online mode titles the list (REF's "My Account"); app mode keeps its untitled card. Then the store information: About
// this store (/{hotel}/about; P3-FIX #29: app mode has no other way there), Terms, Privacy, Allergy information; Log out
// (OO.store.logout) when signed in; the app version (the build id, OO.boot.buildId) in app mode.
// OO.pages.Account.logic (pure):
//   sections(mode, { notifications, booking, except }) → [{ key: 'account', label, rows: [{ key, page, label, icon }] }]
//       the hub's lists (one today); notifications = the store's notifications are on (default true; P3-12's
//       `production: true` turns them off too); booking = the store takes bookings; except = row keys to leave out (the
//       profile page shows the rest of them; P3-12). An emptied list is left out.
//   notificationsOn(hotel) → OO.pages.Notifications.enabled(hotel) (read when called; without that page, the same test:
//       !OO.ui.liveProduction(hotel)).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  // ---- the lists (pure; see the header) ---------------------------------------------------------------------------
  function sections(mode, opts) {
    var o = opts || {};
    var app = mode === 'app';
    var except = Array.isArray(o.except) ? o.except : [];
    var account = [
      { key: 'orders', page: 'orders', label: 'Orders', icon: 'Receipt' },
      { key: 'wallet', page: 'wallet', label: 'Wallet', icon: 'DollarCircle' },
      { key: 'rewards', page: 'rewards', label: 'Rewards', icon: 'Star' },
    ];
    // Only at a store that takes bookings (store.booking: table_reservation), right after Rewards (orchestrator).
    if (o.booking) {
      account.push({ key: 'bookings', page: 'bookings', label: 'Bookings', icon: 'Calendar' });
    }
    account.push({ key: 'profile', page: 'profile', label: 'Profile', icon: 'User' });
    if (app && o.notifications !== false && !o.production) account.push({ key: 'notifications', page: 'notifications', label: 'Notifications', icon: 'Bell' });
    if (app) account.push({ key: 'stores', page: 'stores', label: 'Stores', icon: 'Pin' });
    function keep(r) { return except.indexOf(r.key) < 0; }
    return [{ key: 'account', label: 'Your account', rows: account.filter(keep) }].filter(function (s) { return s.rows.length > 0; });
  }
  // P3-FIX #29: the store's notifications on or off, by P3-26's one rule (app-notifications.jsx loads before this file;
  // read when called, as OO.ui.AppTopBar's bell reads it).
  function notificationsOn(hotel) {
    var N = OO.pages.Notifications;
    if (N && typeof N.enabled === 'function') return !!N.enabled(hotel);
    return !(OO.ui && typeof OO.ui.liveProduction === 'function' && OO.ui.liveProduction(hotel));
  }

  // ---- the page ---------------------------------------------------------------------------------------------------
  function Row(props) {
    var Icon = props.icon ? OO.icons[props.icon] : null;
    return (
      <li>
        <a className="oo-ac-row" href={props.link.href} onClick={props.link.onClick}>
          {Icon ? <span className="oo-ac-row__icon"><Icon size={20} /></span> : null}
          <span className="oo-ac-row__label">{props.label}</span>
          {props.badge ? <span className="oo-ac-row__badge" aria-label={props.badgeLabel}>{props.badge}</span> : null}
          <OO.icons.ChevronRight size={16} className="oo-ac-row__chev" />
        </a>
      </li>
    );
  }

  // The Notifications row's unread count: fetched only while the row shows (app mode, notifications on: D-10).
  function useUnread(hotel, store, on) {
    var K = OO.appKit;
    var s = K.useSession();
    var uid = s && s.customer && s.customer.uid ? s.customer.uid : null;
    var read = K.useReadIds(hotel);
    var res = OO.useAsync(function () {
      return on && uid ? OO.api.getNotifications(hotel) : Promise.resolve(null);
    }, [hotel, uid, on]);
    return React.useMemo(function () {
      return on && res.data && store ? OO.model.normalizeNotifications(res.data, { timeZone: store.timezone, read: read }).unread : 0;
    }, [on, res.data, store, read]);
  }

  OO.pages.Account = function Account(props) {
    var route = props.route;
    var hotel = route.hotel;
    var app = props.mode === 'app';
    var K = OO.appKit, ui = OO.ui, acct = OO.pages.acct;
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var session = K.useSession();
    var customer = session && session.customer ? session.customer : null;
    // POLISH-5: the loyalty scope ({ chain, master } in mode app, else {}), as Home's card reads it.
    var loyalty = K.useLoyalty(hotel, store, true, OO.data.loyaltyScope ? OO.data.loyaltyScope(hotel) : null);
    var notesOn = notificationsOn(hotel); // P3-FIX #29: P3-26's rule, as the bell and the route
    var unread = useUnread(hotel, store, app && notesOn);
    var L = loyalty.data;
    var buildId = (OO.boot && OO.boot.buildId) || '';

    var profile;
    if (customer) {
      var edit = K.link(hotel, 'profile', { edit: true });
      var pts = L && L.enabled ? (L.tier ? L.tier + ' · ' : '') + K.pointsText(L.points, L.pointsWord) : null;
      profile = (
        <ui.Card radius="lg" className="oo-ac-profile">
          <ui.Avatar name={customer.name || customer.email} size={52} />
          <div className="oo-ac-profile__text">
            <div className="oo-ac-profile__name">{customer.name || 'Member'}</div>
            {customer.email ? <div className="oo-ac-profile__meta">{customer.email}</div> : null}
            {customer.phone ? <div className="oo-ac-profile__meta">{customer.phone}</div> : null}
            {pts ? <div className="oo-ac-profile__pts">{pts}</div> : null}
            {pts && L.scope ? <div className="oo-ac-profile__scope">{L.scope.text}</div> : null}
          </div>
          <ui.Button variant="link" href={edit.href} onClick={edit.onClick} aria-label="Edit profile">Edit</ui.Button>
        </ui.Card>
      );
    } else {
      profile = <K.LoginPrompt hotel={hotel} title="You’re not logged in" body="Log in to see your orders, points and rewards." />;
    }

    var lists = sections(props.mode, { notifications: notesOn, booking: !!(store && store.booking) }).map(function (s) {
      var titleId = 'oo-ac-sec-' + s.key;
      return (
        <nav key={s.key} className="oo-ac-sec" aria-label={app ? s.label : undefined} aria-labelledby={app ? undefined : titleId}>
          {app ? null : <h2 className="oo-ac-sec__title" id={titleId}>{s.label}</h2>}
          <ui.Card radius="lg" pad="none" className="oo-ac-card">
            <ul className="oo-ac-list">
              {s.rows.map(function (r) {
                var count = r.key === 'notifications' && unread ? String(unread > 99 ? '99+' : unread) : null;
                return <Row key={r.key} icon={r.icon} label={r.label} link={K.link(hotel, r.page)} badge={count} badgeLabel={unread + ' unread'} />;
              })}
            </ul>
          </ui.Card>
        </nav>
      );
    });
    var info = (
      <nav className="oo-ac-sec" aria-label="Store information">
        <ui.Card radius="lg" pad="none" className="oo-ac-card">
          <ul className="oo-ac-list">
            <Row label="About this store" link={K.link(hotel, 'about')} />
            <Row label="Terms and conditions" link={K.link(hotel, 'legal', { kind: 'terms' })} />
            <Row label="Privacy policy" link={K.link(hotel, 'legal', { kind: 'privacy' })} />
            <Row label="Allergy information" link={K.link(hotel, 'legal', { kind: 'allergy' })} />
          </ul>
        </ui.Card>
      </nav>
    );
    var logout = customer ? (
      <ui.Button variant="outline" block className="oo-ac-logout" onClick={function () { OO.store.logout(); }}>Log out</ui.Button>
    ) : null;

    if (app && store && !customer && OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(props.route.chain || hotel) && K.KitchenAccount) return <K.KitchenAccount route={props.route} />;
    if (app && store && !customer && OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(props.route.chain || hotel) && K.PizzaClubAccount) return <K.PizzaClubAccount route={props.route} />;

    // Online mode (P3-12): the account pages' shell, back to the menu.
    if (!app) {
      var header = acct.headerProps({ hotel: hotel, mode: props.mode, back: OO.router.href('menu', null, { hotel: hotel }) });
      var blocked = acct.gate(h, { title: 'Account', header: header });
      if (blocked) return blocked;
      return (
        <acct.Page title="Account" {...header} className="oo-ac oo-ac-hub">
          {profile}
          {lists}
          {info}
          {logout}
          <ui.PoweredBySipo />
        </acct.Page>
      );
    }

    var body;
    if (h.notFound) body = <K.NotFound />;
    else if (!store && h.error) body = <K.ErrorCard title="We couldn't load this store" onRetry={h.reload} />;
    else {
      body = (
        <React.Fragment>
          {profile}
          {lists}
          {info}
          {logout}
          <footer className="oo-ac-foot">
            <ui.PoweredBySipo />
            <p className="oo-ac-version">{buildId ? 'App version ' + buildId : 'App version (development build)'}</p>
          </footer>
        </React.Fragment>
      );
    }

    return (
      <div className="oo-apg oo-ac">
        <ui.PageHeader title="Account" />
        <main className="oo-apg__body oo-ac__body">{body}</main>
      </div>
    );
  };

  OO.pages.Account.logic = { sections: sections, notificationsOn: notificationsOn };
})();


// ===================== src/pages/track.jsx =====================
// src/pages/track.jsx: OO.pages.Track, order tracking (Batch 2 P3-03; REF online-order/pages/track, B §1.8 "Track").
//   /{hotel}/track        the lookup (REF OnlineOrderTrackLookupPage): an order id, no sign-in. The id is kept as REF
//                         keeps it (every space removed, upper-cased) and needs 6 to 64 characters; Track goes to
//                         /{hotel}/track/{id}. The order this device placed last at the store gets a shortcut card. A
//                         store that takes table bookings (store.booking) also links "Track a table booking" (REF's
//                         mode switch): /{hotel}/booking/track, the booking lookup (bookings.jsx).
//   /{hotel}/track/{id}   the public track (route.params.orderId; REF OnlineOrderTrackResultPage): the live status
//                         (headline, stepper, the store's message, the pickup number, the time), polled per D-17; a
//                         share / copy link to this page; and, for a delivery order this device placed, the courier's
//                         tracking link (E18) once the store has accepted it.
// Privacy (B §9 Q13, D-14): get_order_data returns name, phone and address to anyone holding an order id, so this page
// reads ONLY online_order_status (E14: status, the store's message, times; no contact data) and this device's lastOrder,
// never get_order_data, get_customer_orders or customer/getOrders. Anyone with the id sees the status. Only the device
// that placed the order (OO.store lastOrder is this order of this store) also sees what it holds: the items, prices and
// total as checkout showed them (never the snapshot's address), the placed time, the payment label, and the delivery
// link (a courier's tracking page shows the drop-off, so it stays with the owner). Only the owner's device links the
// order page (/{hotel}/profile/orders/{id}, which reads get_order_data); anyone else gets "Log in", or, signed in,
// "Your orders" (their own uid-scoped history), never a link to that order. The share link is /{hotel}/track/{id}
// with no query at all, and an incoming contact field in the URL (REF links) is replaced out of the address bar at once.
// Polling (D-17): online_order_status at once, then on a backoff of the visible tab's time: every 10 s for 2 minutes,
// every 30 s to 10 minutes, every 60 s to 30 minutes (49 polls), then "Refresh for the latest status". It is a
// setTimeout chain (never two polls at once), paused while the tab is hidden (a poll that falls due then runs as soon as
// the tab is visible again). It stops at a final state (completed, rejected, cancelled), on a 400 / 404 / 422 (no such
// order: "We couldn't find this order", unless this device holds its snapshot), and after 6 failed polls in a row; a
// network failure or a 5xx is retried on the same schedule. Refresh asks at once and starts a new run (a tap while a
// poll is on its way waits for that answer instead). Each answer also settles this device's kept cart once the order is
// past PROCESSING (acct.paycb.settlesOn202 → OO.store.settleOrder, as the order-status page does).
// Delivery link (E18, B06): OO.api.getTrackingLink once when the store accepts (Preparing), and again at Ready if the
// first found none: at most two calls per page. Any failure (400 no courier, 404 no link yet, 429, 5xx, the network,
// a fixture store's "Not available yet") is "no link". Only an http(s) URL is shown, opened in a new tab.
// Page id 'track' (routed by P3-01: glue/router.jsx, src/app.jsx PAGES); /{hotel}/track?mode=booking is the booking
// lookup instead (page 'booking-track', bookings.jsx). Off at a table (D-20: OO.App redirects). Styles:
// styles/track.css (.oo-track__*); the status card reuses the order-status parts in styles/pages.css (.oo-ostatus__*).
//
// OO.pages.Track.logic (pure; tests/track-p303.test.mjs):
//   ID_MIN 6 · ID_MAX 64 · POLL = { steps: [[untilMs, everyMs] …] (D-17), errorsMax: 6 }
//   normalizeId(v) → the lookup's id: every space removed, upper-cased ('oot 12 3' → 'OOT123').
//   idError(id) → '' | 'Enter your order ID.' | 'That ID looks too short. Check your confirmation email.' |
//       'That ID looks too long. Check your confirmation email.'   (id as normalizeId gives it)
//   pollDelay(elapsedMs) → ms | null   the wait before the next poll once the run's schedule has used elapsedMs:
//       10000 below 2 min, 30000 below 10 min, 60000 below 30 min, then null (the run is over: 12 + 16 + 20 waits).
//   isGone(err) → bool   status 400 / 404 / 422, or kind 'notFound': the order isn't there (classified by STATUS: a
//       production 404 body is nginx HTML).
//   isOwn(lastOrder, hotel, orderId) → bool   this device placed this order at this store (ids compared as strings).
//   view({ raw, statusState, snapshot }) → View   OO.model.orderStatusView with no ticket (this page never asks for one:
//       orderState 'loading' while the poll still asks, so a failed first poll keeps the skeleton, else 'missing') for
//       the latest online_order_status body `raw` (null before the first answer), the poll's statusState ('loading' |
//       'ok' | 'retrying' | 'gone' | 'off' | 'stale') and this device's snapshot (OO.model.orderSnapshot, or null), plus
//       { type: the snapshot's orderType 'PICKUP' | 'DELIVERY' | 'DINEIN', else null (nothing public tells it),
//       typeLabel, message: the store's text | null }. show 'missing' = gone without a snapshot; 'error' = stopped
//       after repeated failures without any answer.
//   steps(key, type) → [{ key, label, state 'done' | 'current' | 'todo' }]   Received → Preparing → Ready for pickup →
//       Collected (PICKUP) · … → On its way → Delivered (DELIVERY) · … → Ready → Completed (type unknown). 'payment',
//       'checking' and 'unknown' have every step still to do; 'completed' has them all done.
//   headline(key, type, storeName, message) → { title, body }   the status card's words (type-neutral without a type).
//   when(raw, { now, timeZone }) → { at, text 'Today, 6:47pm' | 'Tomorrow, …' | '12 Oct, …', scheduled } | null
//       online_order_status' deliver_time (ISO, or the server's UTC 'YYYY-MM-DD HH:MM:SS') in the store's zone;
//       scheduled = its delivery_type (else fulfillment_type) is SCHEDULED.
//   driverStep(view, own) → 'preparing' | 'ready' | null   when E18 may be asked: this device's order (own), a DELIVERY
//       snapshot, and the order at Preparing or Ready.
//   shareHref(hotel, orderId, { routeBase, mode }) → '/{hotel}/track/{id}' (OO.router.href; never a query or hash).
//   hasPii(query) → bool   the query holds a key OO.router.href leaves out (a contact field: email, phone, …).
//   httpUrl(v) → the trimmed http(s) URL | null.
// OO.pages.Track.View(props) → the result page for a computed state (no hooks, no data: the container below feeds it;
//   the render checks use it directly): { hotel, store, mode, header, orderId, view, raw, now, snapshot, own,
//   signedIn, driverUrl, poll: { state, at, manual }, onRefresh, shareUrl, canShare, onShare, onCopy, toast, onToast }.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var acct = (OO.pages.acct = OO.pages.acct || {});

  var ID_MIN = 6;
  var ID_MAX = 64;
  // D-17: [the run's schedule so far is below this (ms), wait this long (ms)].
  var POLL = { steps: [[2 * 60000, 10000], [10 * 60000, 30000], [30 * 60000, 60000]], errorsMax: 6 };
  var TYPE_LABEL = { PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
  // The stepper's order. 'payment' (awaiting payment) comes before Received: every step is still to do.
  var RANK = { payment: -1, received: 0, preparing: 1, ready: 2, completed: 3 };
  var STEP_LABELS = {
    PICKUP: ['Received', 'Preparing', 'Ready for pickup', 'Collected'],
    DELIVERY: ['Received', 'Preparing', 'On its way', 'Delivered'],
    other: ['Received', 'Preparing', 'Ready', 'Completed'],
  };

  function own(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }
  function text(v) { return typeof v === 'string' ? v.trim() : (typeof v === 'number' && isFinite(v) ? String(v) : ''); }

  // ---- pure logic (OO.pages.Track.logic; see the header) ---------------------------------------------------------
  function normalizeId(v) { return String(v == null ? '' : v).replace(/\s+/g, '').toUpperCase(); }
  function idError(id) {
    var s = String(id == null ? '' : id);
    if (!s) return 'Enter your order ID.';
    if (s.length < ID_MIN) return 'That ID looks too short. Check your confirmation email.';
    if (s.length > ID_MAX) return 'That ID looks too long. Check your confirmation email.';
    return '';
  }
  function pollDelay(elapsed) {
    var e = Number(elapsed) || 0;
    for (var i = 0; i < POLL.steps.length; i++) if (e < POLL.steps[i][0]) return POLL.steps[i][1];
    return null;
  }
  function isGone(err) {
    var code = err && err.status;
    return code === 400 || code === 404 || code === 422 || !!(err && err.kind === 'notFound');
  }
  function isOwn(lastOrder, hotel, orderId) {
    var lo = lastOrder && typeof lastOrder === 'object' ? lastOrder : null;
    if (!lo || hotel == null || hotel === '' || orderId == null || orderId === '') return false;
    return lo.hotel != null && lo.orderId != null && String(lo.hotel) === String(hotel) && String(lo.orderId) === String(orderId);
  }
  function view(input) {
    var i = input || {};
    var snap = i.snapshot || null;
    var ss = i.statusState || 'loading';
    var type = snap && own(TYPE_LABEL, snap.orderType) ? snap.orderType : null;
    // The step itself doesn't depend on the type (orderProgress only words its labels with it); this page words them.
    var progress = i.raw ? OO.model.orderProgress(i.raw, { orderType: type || 'PICKUP' }) : null;
    // No ticket is ever asked for: 'loading' while the poll still asks (a failed first poll keeps the skeleton until
    // the run stops), then 'missing', so a stop without an answer is "couldn't find" (gone) or "couldn't load" (off).
    var v = OO.model.orderStatusView({
      order: null, orderState: ss === 'loading' || ss === 'retrying' ? 'loading' : 'missing', progress: progress, statusState: ss, snapshot: snap,
    });
    return Object.assign({}, v, { type: type, typeLabel: type ? TYPE_LABEL[type] : null, message: progress ? progress.message : null });
  }
  function steps(key, type) {
    var labels = STEP_LABELS[type === 'PICKUP' || type === 'DELIVERY' ? type : 'other'];
    var at = own(RANK, key) ? RANK[key] : null;
    return labels.map(function (label, i) {
      var state = key === 'completed' || (at !== null && i < at) ? 'done' : (at === i ? 'current' : 'todo');
      return { key: i, label: label, state: state };
    });
  }
  function headline(key, type, storeName, message) {
    var pickup = type === 'PICKUP', delivery = type === 'DELIVERY';
    switch (key) {
      case 'payment': return { title: 'Awaiting payment', body: 'Your order goes to the store as soon as your payment is confirmed.' };
      case 'checking': return { title: 'Checking your order', body: 'Getting the latest status from the store.' };
      case 'unknown': return { title: 'Order placed', body: "We can't show live updates for this order right now. If you need help, please contact the store." };
      case 'preparing': return { title: 'Preparing your order', body: message || 'The kitchen is working on it now.' };
      case 'ready':
        if (delivery) return { title: 'On its way', body: 'Your order is out for delivery.' };
        if (pickup) return { title: 'Ready for pickup', body: 'Head to the counter at ' + storeName + '.' };
        return { title: 'Your order is ready', body: message || 'The store has marked your order as ready.' };
      case 'completed': return { title: delivery ? 'Delivered' : (pickup ? 'Collected' : 'Order complete'), body: 'Thanks for ordering with ' + storeName + '.' };
      case 'rejected': return { title: 'Order declined', body: "The store couldn't take this order. If you were charged, please call the store." };
      case 'cancelled': return { title: 'Order cancelled', body: 'This order was cancelled. If you were charged, please call the store.' };
      default: return { title: 'Order received', body: 'The store has your order and will start on it shortly.' };
    }
  }
  function when(raw, opts) {
    var r = raw && typeof raw === 'object' ? raw : {};
    var o = opts || {};
    var at = OO.model._.parseInstant(text(r.deliver_time));
    if (at === null || !(at > 0)) return null;
    var f = OO.format;
    var tz = o.timeZone || null;
    var c = f.zoned(new Date(at), tz);
    var today = OO.model._.resolveNow(o.now, tz).clock.ymd;
    var day = f.dayDiff(c.ymd, today) === -1 ? 'Tomorrow' : f.relativeDay(c.ymd, today);
    var kind = text(r.delivery_type || r.fulfillment_type).toUpperCase();
    return { at: at, text: day + ', ' + f.time12(c.minutes), scheduled: kind === 'SCHEDULED' };
  }
  function driverStep(v, isOwnOrder) {
    if (!isOwnOrder || !v || v.type !== 'DELIVERY') return null;
    return v.key === 'preparing' || v.key === 'ready' ? v.key : null;
  }
  function shareHref(hotel, orderId, opts) {
    var o = opts || {};
    return OO.router.href('track', { orderId: String(orderId) }, { hotel: hotel, routeBase: o.routeBase, mode: o.mode });
  }
  function hasPii(query) {
    var keys = Object.keys(query || {});
    if (!keys.length) return false;
    var out = OO.router.href('track', null, { hotel: 'x', routeBase: '', mode: 'online', query: query });
    var at = out.indexOf('?');
    return Object.keys(at < 0 ? {} : OO.router.parseQuery(out.slice(at))).length < keys.length;
  }
  function httpUrl(v) {
    var s = typeof v === 'string' ? v.trim() : '';
    return /^https?:\/\/[^\s]+$/i.test(s) ? s : null;
  }

  // ---- timers and the platform (guarded: node:vm tests and react-dom/server have no document / navigator) --------
  function hidden() { return typeof document !== 'undefined' && !!document && document.visibilityState === 'hidden'; }
  // setTimeout for a visible tab: when the delay ends while the tab is hidden, fn runs as soon as the tab is visible
  // again (one catch-up call). → cancel().
  function visibleTimeout(fn, ms) {
    var timer = null;
    var waiting = false;
    var over = false;
    function fire() { if (!over) { over = true; fn(); } }
    function stopWaiting() {
      if (!waiting) return;
      waiting = false;
      document.removeEventListener('visibilitychange', onVisible);
    }
    function onVisible() { if (!hidden()) { stopWaiting(); fire(); } }
    timer = setTimeout(function () {
      timer = null;
      if (!hidden()) { fire(); return; }
      waiting = true;
      document.addEventListener('visibilitychange', onVisible);
    }, ms);
    return function cancel() { over = true; if (timer) clearTimeout(timer); stopWaiting(); };
  }
  function nav() { return typeof navigator !== 'undefined' && navigator ? navigator : null; }
  function copyText(value) {
    try {
      var n = nav();
      var c = n && n.clipboard;
      if (c && typeof c.writeText === 'function') {
        return Promise.resolve(c.writeText(value)).then(function () { return true; }, function () { return false; });
      }
    } catch (e) { /* no clipboard */ }
    return Promise.resolve(false);
  }
  function absoluteUrl(href) {
    var origin = '';
    try { origin = window.location && window.location.origin ? String(window.location.origin) : ''; } catch (e) { origin = ''; }
    return origin + href;
  }

  // ---- hooks -----------------------------------------------------------------------------------------------
  function selectLastOrder(s) { return s.lastOrder; }
  function selectSession(s) { return s.session; }

  // The D-17 status poll for one order → { raw, state, at (ms of the last answer) | null, manual, refresh() }.
  // state: 'loading' (no answer yet) | 'ok' | 'retrying' | 'gone' | 'off' | 'stale' (orderStatusView's statusState).
  function useStatusPoll(orderId, hotel) {
    var st = React.useState(null); // { id, raw, state, at, manual }
    var runS = React.useState(0);
    var flight = React.useRef(false);
    var cur = st[0] && st[0].id === orderId ? st[0] : null;
    var final = !!(cur && cur.raw && OO.model.orderProgress(cur.raw).done);

    React.useEffect(function () {
      if (!orderId || final) return undefined;
      var alive = true;
      var id = orderId;
      var elapsed = 0;
      var errors = 0;
      var cancel = null;
      function put(patch) {
        st[1](function (c) {
          var base = c && c.id === id ? c : { id: id, raw: null, state: 'loading', at: null, manual: false };
          return Object.assign({}, base, patch);
        });
      }
      function later() {
        var wait = pollDelay(elapsed);
        if (wait === null) { put({ state: 'stale', manual: false }); return; }
        elapsed += wait;
        cancel = visibleTimeout(tick, wait);
      }
      function tick() {
        cancel = null;
        flight.current = true;
        Promise.resolve().then(function () { return OO.api.orderStatus(id, hotel); }).then(function (raw) {
          // P1-15 (as order-status.jsx): a 202 the payment callback couldn't settle clears the kept cart once the order
          // is past PROCESSING. A no-op unless lastOrder is this order with clearCartOnSuccess (and only once).
          var paycb = OO.pages.acct && OO.pages.acct.paycb;
          if (paycb && typeof paycb.settlesOn202 === 'function' && paycb.settlesOn202(raw)) OO.store.settleOrder(id);
          if (!alive) return;
          flight.current = false;
          errors = 0;
          put({ raw: raw, state: 'ok', at: OO.data.now().getTime(), manual: false });
          if (!OO.model.orderProgress(raw).done) later();
        }, function (err) {
          if (!alive) return;
          flight.current = false;
          if (isGone(err)) { put({ state: 'gone', manual: false }); return; }
          errors += 1;
          if (errors >= POLL.errorsMax) { put({ state: 'off', manual: false }); return; }
          put({ state: 'retrying', manual: false });
          later();
        });
      }
      // A (re)start keeps the last answer on screen while its first poll runs.
      st[1](function (c) {
        if (!c || c.id !== id) return { id: id, raw: null, state: 'loading', at: null, manual: false };
        if (c.state === 'ok' || c.state === 'loading') return c;
        return Object.assign({}, c, { state: c.raw ? 'ok' : 'loading' });
      });
      tick();
      return function () { alive = false; flight.current = false; if (cancel) cancel(); };
    }, [orderId, hotel, final, runS[0]]);

    function refresh() {
      if (flight.current) return; // the answer on its way is as fresh as a new one
      st[1](function (c) { return c && c.id === orderId ? Object.assign({}, c, { manual: true }) : c; });
      runS[1](function (n) { return n + 1; });
    }
    return { raw: cur ? cur.raw : null, state: cur ? cur.state : 'loading', at: cur ? cur.at : null, manual: !!(cur && cur.manual), refresh: refresh };
  }

  // E18 for this device's delivery order: asked once per step ('preparing', then 'ready' while there is no link yet).
  function useDriverLink(orderId, hotel, step) {
    var urlS = React.useState(null); // { id, url }
    var asked = React.useRef({});
    var mounted = React.useRef(true);
    React.useEffect(function () {
      mounted.current = true;
      return function () { mounted.current = false; };
    }, []);
    var url = urlS[0] && urlS[0].id === orderId ? urlS[0].url : null;
    React.useEffect(function () {
      if (!step || !orderId || url) return;
      var k = orderId + '|' + step;
      if (asked.current[k]) return;
      asked.current[k] = true;
      var id = orderId;
      Promise.resolve().then(function () { return OO.api.getTrackingLink(id, hotel); }).then(function (r) {
        var u = httpUrl(r && r.url);
        if (u && mounted.current) urlS[1]({ id: id, url: u });
      }, function () { /* any failure: no link */ });
    }, [orderId, hotel, step, url]);
    return url;
  }

  // navigator.share is read after mount only (the server render and the first client render agree: no Share button).
  function useCanShare() {
    var s = React.useState(false);
    React.useEffect(function () {
      var n = nav();
      s[1](!!n && typeof n.share === 'function');
    }, []);
    return s[0];
  }

  // ---- icons (drawn in the kit's 24px / 2px-stroke language; OO.icons has none of these) -------------------------
  function makeIcon(name, draw) {
    function Icon(props) {
      var p = props || {};
      var size = p.size == null ? 24 : p.size;
      return React.createElement('svg', {
        width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
        strokeLinecap: 'round', strokeLinejoin: 'round', className: 'oo-icon', focusable: 'false', 'aria-hidden': 'true',
      }, draw());
    }
    Icon.displayName = 'TrackIcon' + name;
    return Icon;
  }
  var icons = {
    Share: makeIcon('Share', function () {
      return [<path key="a" d="M12 15V3.5M7.5 8L12 3.5 16.5 8" />, <path key="b" d="M5 12.5v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />];
    }),
    Copy: makeIcon('Copy', function () {
      return [<rect key="a" x="8.5" y="8.5" width="12" height="12" rx="2" />, <path key="b" d="M15.5 8.5V5.5a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" />];
    }),
    Truck: makeIcon('Truck', function () {
      return [
        <path key="a" d="M2.5 6.5h11v9h-11z" />, <path key="b" d="M13.5 9.5h4l3 3.2v2.8h-7" />,
        <circle key="c" cx="7" cy="17.5" r="1.8" />, <circle key="d" cx="17" cy="17.5" r="1.8" />,
      ];
    }),
    Lock: makeIcon('Lock', function () {
      return [<rect key="a" x="5" y="10.5" width="14" height="10" rx="2" />, <path key="b" d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />];
    }),
  };

  // ---- parts -----------------------------------------------------------------------------------------------
  function Stepper(props) {
    return (
      <ol className="oo-ostatus__steps" aria-label="Order progress">
        {props.steps.map(function (s) {
          return (
            <li key={s.key} className={'oo-ostatus__step is-' + s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
              <span className="oo-ostatus__dot" aria-hidden="true">{s.state === 'done' ? <OO.icons.Check size={12} strokeWidth={3.4} /> : null}</span>
              <span className="oo-ostatus__label">
                {s.label}
                <span className="oo-sr-only">{s.state === 'done' ? ' (done)' : (s.state === 'current' ? ' (now)' : '')}</span>
              </span>
            </li>
          );
        })}
      </ol>
    );
  }

  function Skeleton() {
    var ui = OO.ui;
    return (
      <div className="oo-acct__skel">
        <ui.Skeleton lines={2} className="oo-acct__skel-title" />
        <ui.Skeleton variant="block" height={168} />
        <ui.Skeleton variant="block" height={140} />
      </div>
    );
  }

  function clockText(ms, store) {
    if (!(ms > 0)) return '';
    return OO.format.time12(OO.format.zoned(new Date(ms), store.timezone).minutes);
  }
  function telHref(phone) {
    var digits = String(phone || '').replace(/[^\d+]/g, '');
    return digits ? 'tel:' + digits : null;
  }

  // This device's last order at the store: a shortcut on the lookup.
  function LatestCard(props) {
    var ui = OO.ui;
    var last = props.last;
    var store = props.store;
    var link = OO.router.link('track', { orderId: String(last.orderId) }, { hotel: props.hotel });
    var at = Number(last.placedAt);
    var placed = '';
    if (at > 0) {
      var c = OO.format.zoned(new Date(at), store.timezone);
      var today = OO.format.zoned(OO.data.now(), store.timezone).ymd;
      placed = OO.format.relativeDay(c.ymd, today) + ', ' + OO.format.time12(c.minutes);
    }
    var meta = [placed ? 'Placed ' + placed : null, last.total != null && last.total !== '' ? acct.money(last.total, store) : null].filter(Boolean).join(' · ');
    return (
      <ui.Card className="oo-orders__recent oo-track__latest">
        <span className="oo-orders__recent-icon" aria-hidden="true"><OO.icons.Receipt size={22} /></span>
        <span className="oo-orders__recent-text">
          <span className="oo-acct__cardtitle">Your latest order</span>
          <span className="oo-acct__muted">{meta || 'Placed on this device'}</span>
        </span>
        <ui.Button size="xs" href={link.href} onClick={link.onClick} aria-label="Track your latest order">Track</ui.Button>
      </ui.Card>
    );
  }

  // ---- the lookup ------------------------------------------------------------------------------------------
  function Lookup(props) {
    var ui = OO.ui;
    var hotel = props.hotel;
    var store = props.store;
    var lastOrder = OO.useStore(selectLastOrder);
    var valS = React.useState('');
    var errS = React.useState('');
    var inputRef = React.useRef(null);
    var last = lastOrder && String(lastOrder.hotel) === String(hotel) && lastOrder.orderId != null && lastOrder.orderId !== '' ? lastOrder : null;
    // REF's lookup also tracks table bookings (its mode switch): here a link to the booking lookup (bookings.jsx).
    var booking = store && store.booking ? OO.router.link('booking-track', null, { hotel: hotel }) : null;

    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      var id = normalizeId(valS[0]);
      var msg = idError(id);
      if (msg) {
        errS[1](msg);
        if (inputRef.current && inputRef.current.focus) inputRef.current.focus();
        return;
      }
      OO.router.go(OO.router.href('track', { orderId: id }, { hotel: hotel }));
    }

    return (
      <acct.Page title="Track your order" {...props.header} className="oo-track oo-track--lookup oo-acct--center">
        <ui.SectionTitle level="page" sub="Live status updates. No sign-in needed.">Track your order</ui.SectionTitle>
        <ui.Card className="oo-track__form-card">
          <form className="oo-track__form" onSubmit={submit} noValidate aria-label="Track an order">
            <ui.TextInput label="Order ID" labelVisible placeholder="e.g. OOT123456789" autoComplete="off" maxLength={80}
              value={valS[0]} inputRef={inputRef} error={errS[0] || undefined}
              hint={errS[0] ? undefined : "It's in your order confirmation email."}
              onChange={function (v) { valS[1](normalizeId(v)); if (errS[0]) errS[1](''); }} />
            <ui.Button type="submit" block icon="Search">Track order</ui.Button>
          </form>
          {booking ? (
            <div className="oo-track__alt">
              <span className="oo-acct__muted">Booked a table?</span>
              <ui.Button variant="link" href={booking.href} onClick={booking.onClick}>Track a table booking</ui.Button>
            </div>
          ) : null}
        </ui.Card>
        {last ? <LatestCard last={last} hotel={hotel} store={store} /> : null}
        <ui.Card className="oo-track__how-card">
          <ol className="oo-track__how" aria-label="How tracking works">
            <li className="oo-track__how-step"><span className="oo-track__how-num" aria-hidden="true">1</span><span><b>Find your order ID</b> in your confirmation email.</span></li>
            <li className="oo-track__how-step"><span className="oo-track__how-num" aria-hidden="true">2</span><span><b>Enter it above</b> to see where your order is.</span></li>
            <li className="oo-track__how-step"><span className="oo-track__how-num" aria-hidden="true">3</span><span><b>Keep the page open</b>: the status updates on its own.</span></li>
          </ol>
        </ui.Card>
        <ui.PoweredBySipo />
      </acct.Page>
    );
  }

  // ---- the result: a view of a computed state (OO.pages.Track.View) ------------------------------------------
  function TrackView(p) {
    var ui = OO.ui;
    var store = p.store;
    var hotel = p.hotel;
    var v = p.view;
    var title = 'Track order';
    var menuLink = OO.router.link(p.mode === 'app' ? 'home' : 'menu', null, { hotel: hotel });
    var lookupLink = OO.router.link('track', null, { hotel: hotel });

    if (v.show === 'loading') {
      return (
        <acct.Page title={title} {...p.header} busy>
          <span className="oo-sr-only" role="status">Loading the order status</span>
          <Skeleton />
        </acct.Page>
      );
    }
    if (v.show === 'missing' || v.show === 'error') {
      var missing = v.show === 'missing';
      return (
        <acct.Page title={title} {...p.header} className="oo-track">
          <ui.EmptyState icon="Receipt"
            title={missing ? "We couldn't find this order" : "We couldn't load this order"}
            body={missing ? 'Check the order ID in your confirmation email and try again.' : 'Check your connection and try again.'}
            action={(
              <div className="oo-acct__actions">
                {missing
                  ? <ui.Button size="md" href={lookupLink.href} onClick={lookupLink.onClick}>Try another ID</ui.Button>
                  : <ui.Button size="md" onClick={p.onRefresh}>Try again</ui.Button>}
                <ui.Button size="md" variant="outline" href={menuLink.href} onClick={menuLink.onClick}>{p.mode === 'app' ? 'Back to home' : 'Back to menu'}</ui.Button>
              </div>
            )} />
          <ui.PoweredBySipo />
        </acct.Page>
      );
    }

    var key = v.key;
    var bad = key === 'rejected' || key === 'cancelled';
    var hl = headline(key, v.type, store.name, v.message);
    var number = v.pickupNumber;
    var label = number ? 'Order #' + number : 'Your order';
    var snap = p.snapshot || null;
    var time = when(p.raw, { now: p.now, timeZone: store.timezone });
    var timeLabel = v.typeLabel || (time && time.scheduled ? 'Scheduled for' : 'Estimated time');
    var updated = clockText(p.poll.at, store);
    var details = OO.router.link('order-status', { orderId: p.orderId }, { hotel: hotel });
    var tel = telHref(store.phone);

    var statusCard = (
      <ui.Card className={ui.cx('oo-ostatus__status', 'oo-track__status', bad && 'is-bad')}>
        <div className="oo-ostatus__headline" role="status" aria-live="polite" aria-atomic="true">
          {bad ? <span className="oo-ostatus__badicon" aria-hidden="true"><acct.icons.Alert size={22} /></span> : null}
          <span className="oo-ostatus__htext">
            <span className="oo-ostatus__state">{hl.title}</span>
            <span className="oo-ostatus__msg">{hl.body}</span>
          </span>
        </div>
        {bad ? null : <Stepper steps={steps(key, v.type)} />}
        {v.live ? (
          <div className="oo-ostatus__live oo-track__live">
            <span className="oo-ostatus__livedot" aria-hidden="true" />
            <span className="oo-track__livetext">{'Updates automatically' + (updated ? ' · Updated ' + updated : '')}</span>
            <ui.Button variant="outline" size="xs" className="oo-track__refresh" busy={!!p.poll.manual} onClick={p.onRefresh}>Refresh</ui.Button>
          </div>
        ) : null}
        {v.refresh ? (
          <div className="oo-ostatus__live oo-ostatus__live--stale">
            <span>Refresh for the latest status</span>
            <ui.Button variant="outline" size="xs" onClick={p.onRefresh}>Refresh</ui.Button>
          </div>
        ) : null}
      </ui.Card>
    );

    var driverCard = p.own && p.driverUrl && !v.final ? (
      <ui.Card className="oo-track__driver">
        <span className="oo-track__badge-icon" aria-hidden="true"><icons.Truck size={22} /></span>
        <span className="oo-track__cardtext">
          <span className="oo-acct__cardtitle">Track your delivery</span>
          <span className="oo-acct__muted">See where your driver is, live.</span>
        </span>
        <ui.Button size="xs" href={p.driverUrl} target="_blank" rel="noopener noreferrer"
          aria-label="Open live delivery tracking (opens in a new tab)">Open map</ui.Button>
      </ui.Card>
    ) : null;

    var metaCard = (
      <ui.Card pad="list" className="oo-track__meta">
        <dl className="oo-acct__dl">
          {time ? <div className="oo-acct__dlrow"><dt>{timeLabel}</dt><dd>{time.text}</dd></div> : null}
          {snap && snap.placedLabel ? <div className="oo-acct__dlrow"><dt>Placed</dt><dd>{snap.placedLabel}</dd></div> : null}
          {v.awaitingPayment
            ? <div className="oo-acct__dlrow"><dt>Payment</dt><dd>Not paid yet</dd></div>
            : (snap && snap.paymentLabel ? <div className="oo-acct__dlrow"><dt>Payment</dt><dd>{snap.paymentLabel}</dd></div> : null)}
          <div className="oo-acct__dlrow"><dt>Order ID</dt><dd className="oo-track__id">{p.orderId}</dd></div>
          <div className="oo-acct__dlrow"><dt>Store</dt><dd>{store.name}{store.address && store.address.full ? <span className="oo-acct__dlsub">{store.address.full}</span> : null}</dd></div>
        </dl>
      </ui.Card>
    );

    // What this device knows about its own order (never its address); for anyone else, why there is no more.
    var orderCard;
    if (snap) {
      orderCard = (
        <ui.Card pad="summary" className="oo-track__order">
          <h3 className="oo-acct__cardtitle">Your order<span className="oo-acct__cardsub">{OO.format.pluralize(snap.itemCount, 'item')}</span></h3>
          <ul className="oo-ostatus__lines">
            {snap.lines.map(function (l, i) {
              return (
                <li key={i} className="oo-ostatus__line">
                  <span className="oo-ostatus__qty">{l.qty + '×'}</span>
                  <span className="oo-ostatus__lmain">
                    <span className="oo-ostatus__lname">{l.name}</span>
                    {l.mods ? <span className="oo-ostatus__lmods">{l.mods}</span> : null}
                  </span>
                  {l.total ? <span className="oo-ostatus__lprice">{l.total}</span> : null}
                </li>
              );
            })}
          </ul>
          {snap.rows && snap.rows.some(function (row) { return row.key.indexOf('fee:') === 0; })
            ? <ui.PriceRows rows={snap.rows.filter(function (row) { return row.key.indexOf('fee:') === 0; })} /> : null}
          {snap.total ? <div className="oo-track__total"><span>Total</span><span className="oo-track__total-value">{snap.total}</span></div> : null}
          <ui.Button variant="outline" size="md" block href={details.href} onClick={details.onClick}>View order details</ui.Button>
        </ui.Card>
      );
    } else if (p.own) {
      orderCard = (
        <ui.Card className="oo-track__note">
          <span className="oo-track__badge-icon" aria-hidden="true"><OO.icons.Receipt size={20} /></span>
          <span className="oo-track__cardtext">
            <span className="oo-acct__cardtitle">Placed on this device</span>
            <span className="oo-acct__muted">Your items, totals and receipt are on the order page.</span>
          </span>
          <ui.Button variant="outline" size="xs" href={details.href} onClick={details.onClick}>View order</ui.Button>
        </ui.Card>
      );
    } else {
      // Never a link to the order page from here: anyone may hold this id, and that page reads get_order_data. A
      // signed-in viewer gets their own (uid-scoped) history instead, whose cards open their own orders.
      var login = OO.router.link('profile', null, { hotel: hotel, query: { directLogin: 'true', tab: 'orders' } });
      var yours = OO.router.link('orders', null, { hotel: hotel });
      orderCard = (
        <ui.Card className="oo-track__note">
          <span className="oo-track__badge-icon" aria-hidden="true"><icons.Lock size={20} /></span>
          <span className="oo-track__cardtext">
            <span className="oo-acct__cardtitle">Order details stay private</span>
            <span className="oo-acct__muted">
              {p.signedIn
                ? 'Items, totals and receipts show on the device that placed the order, or under your orders if it is one of yours.'
                : 'Items, totals and receipts show on the device that placed the order, or in your orders once you log in.'}
            </span>
          </span>
          {p.signedIn
            ? <ui.Button variant="outline" size="xs" href={yours.href} onClick={yours.onClick}>Your orders</ui.Button>
            : <ui.Button variant="outline" size="xs" href={login.href} onClick={login.onClick}>Log in</ui.Button>}
        </ui.Card>
      );
    }

    var shareCard = (
      <ui.Card className="oo-track__share">
        <h3 className="oo-acct__cardtitle">Share tracking</h3>
        <p className="oo-acct__muted oo-track__share-note">Anyone with this link can follow the order's status. It never shows names, phone numbers or addresses.</p>
        <div className="oo-track__url" aria-label="Tracking link">{p.shareUrl}</div>
        <div className="oo-track__share-actions">
          <ui.Button variant="outline" size="sm" icon={<icons.Copy size={18} />} onClick={p.onCopy}>Copy link</ui.Button>
          {p.canShare ? <ui.Button size="sm" icon={<icons.Share size={18} />} onClick={p.onShare}>Share</ui.Button> : null}
        </div>
      </ui.Card>
    );

    var helpCard = (
      <ui.Card className="oo-ostatus__help">
        <span className="oo-ostatus__helpicon" aria-hidden="true"><acct.icons.Phone size={20} /></span>
        <span className="oo-ostatus__helptext">
          <span className="oo-acct__cardtitle">Need help?</span>
          <span className="oo-acct__muted">{tel ? 'Call the store on ' + store.phone + '.' : 'Please contact ' + store.name + ' directly.'}</span>
        </span>
        {tel ? <ui.Button variant="outline" size="xs" href={tel} aria-label={'Call the store, ' + store.phone}>Call the store</ui.Button> : null}
      </ui.Card>
    );

    return (
      <acct.Page title={title} {...p.header} className="oo-track">
        <div className="oo-acct__grid">
          <div className="oo-acct__col">
            <ui.SectionTitle level="page" sub={[store.name, v.typeLabel].filter(Boolean).join(' · ')}>{label}</ui.SectionTitle>
            {statusCard}
            {driverCard}
            {metaCard}
          </div>
          <div className="oo-acct__col">
            {orderCard}
            {shareCard}
            {helpCard}
            <ui.PoweredBySipo />
          </div>
        </div>
        {p.toast ? <ui.Toast key={p.toast.id} open tone={p.toast.tone} onClose={p.onToast}>{p.toast.text}</ui.Toast> : null}
      </acct.Page>
    );
  }

  // ---- the result: data (the poll, this device's order, the delivery link, share) ----------------------------
  function Result(props) {
    var hotel = props.hotel;
    var store = props.store;
    var orderId = props.orderId;
    var tz = store.timezone;
    var lastOrder = OO.useStore(selectLastOrder);
    var session = OO.useStore(selectSession);
    var poll = useStatusPoll(orderId, hotel);
    var mine = isOwn(lastOrder, hotel, orderId);
    var snapshot = React.useMemo(function () {
      return mine ? OO.model.orderSnapshot(lastOrder, { orderId: orderId, hotel: hotel, now: OO.data.now(), timeZone: tz }) : null;
    }, [lastOrder, mine, orderId, hotel, tz]);
    var v = view({ raw: poll.raw, statusState: poll.state, snapshot: snapshot });
    var driverUrl = useDriverLink(orderId, hotel, driverStep(v, mine));
    var canShare = useCanShare();
    var toastS = React.useState(null);
    var seq = React.useRef(0);
    var shareUrl = absoluteUrl(shareHref(hotel, orderId));
    var number = v.pickupNumber;

    React.useEffect(function () {
      if (number && typeof document !== 'undefined' && document) document.title = 'Track order #' + number + ' · ' + (props.mode === 'app' ? 'SIPO' : 'Order online');
    }, [number, props.mode]);

    function say(textValue, tone) {
      seq.current += 1;
      toastS[1]({ id: seq.current, text: textValue, tone: tone });
    }
    function onCopy() {
      copyText(shareUrl).then(function (ok) {
        say(ok ? 'Tracking link copied' : "Couldn't copy the link. Select it above to copy it.", ok ? 'success' : 'warn');
      });
    }
    function onShare() {
      var n = nav();
      if (!n || typeof n.share !== 'function') { onCopy(); return; }
      try {
        var pr = n.share({ title: number ? 'Track order #' + number : 'Track my order', url: shareUrl });
        if (pr && typeof pr.then === 'function') pr.then(null, function () { /* dismissed */ });
      } catch (e) { onCopy(); }
    }

    return (
      <TrackView hotel={hotel} store={store} mode={props.mode} header={props.header} orderId={orderId} view={v} raw={poll.raw}
        now={OO.data.now()} snapshot={snapshot} own={mine} signedIn={!!(session && session.customer && session.customer.uid)}
        driverUrl={driverUrl} poll={poll} onRefresh={poll.refresh} shareUrl={shareUrl} canShare={canShare}
        onShare={onShare} onCopy={onCopy} toast={toastS[0]} onToast={function () { toastS[1](null); }} />
    );
  }

  // ---- page ------------------------------------------------------------------------------------------------
  OO.pages.Track = function Track(props) {
    var route = props.route;
    var mode = props.mode;
    var hotel = route.hotel;
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var orderId = route.params.orderId != null && String(route.params.orderId) !== '' ? String(route.params.orderId) : null;

    // A contact field in the URL (REF's links carry ?email= and friends): out of the address bar at once.
    React.useEffect(function () {
      if (hasPii(route.query)) OO.router.go(OO.router.href('track', route.params, { hotel: hotel, query: route.query }), { replace: true });
    }, [route]);

    var back = orderId ? OO.router.href('track', null, { hotel: hotel }) : OO.router.href(mode === 'app' ? 'home' : 'menu', null, { hotel: hotel });
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: back, profile: true });
    var blocked = acct.gate(h, {
      title: orderId ? 'Track order' : 'Track your order', header: header,
      skeleton: orderId ? <Skeleton /> : <div className="oo-acct__skel"><ui.Skeleton lines={2} className="oo-acct__skel-title" /><ui.Skeleton variant="block" height={176} /></div>,
    });
    if (blocked) return blocked;

    if (!orderId) return <Lookup hotel={hotel} store={h.store} mode={mode} header={header} />;
    return <Result key={orderId} hotel={hotel} store={h.store} mode={mode} header={header} orderId={orderId} />;
  };

  OO.pages.Track.View = TrackView;
  OO.pages.Track.logic = {
    ID_MIN: ID_MIN, ID_MAX: ID_MAX, POLL: POLL,
    normalizeId: normalizeId, idError: idError, pollDelay: pollDelay, isGone: isGone, isOwn: isOwn, view: view,
    steps: steps, headline: headline, when: when, driverStep: driverStep, shareHref: shareHref, hasPii: hasPii,
    httpUrl: httpUrl,
  };
})();


// ===================== src/pages/review.jsx =====================
// src/pages/review.jsx: OO.pages.Review, the review page (Batch 2 P3-06; REF shared/orders/components/RatingForm.tsx,
// online-order/pages/orders/OnlineOrderReviewPage.tsx, B §1.8 "Ratings"): /{hotel}/orders/{id}/review
// (route.params.orderId; page id 'review', routed by P3-01: href('review', { orderId }); off at a table, D-20). It is the
// rating form while the order isn't rated and "Your review" once it is. Styles: the "P3-06/07 ratings" section of
// styles/pages.css (.oo-review, .oo-stars, .oo-rate).
//
// Owner only (B§9 Q13, D-14). The page needs the signed-in customer (customer/rate-order takes the customer's uid), and
// the order must be in that customer's own list: OO.api.getOrders(hotel, { uid }) (get_customer_orders, E16; the
// chain's tickets of the customer, each with rating, feedback, item_ratings and user_id). The page never reads
// get_order_data (it returns name, phone and address to anyone holding an id). Only then does it read the order's
// review: OO.api.getOrderRating(hotel, orderId, { storeId, fresh: true }) (E30), with the TICKET's store id (the row's
// user_id; the route store's id when the row has none): rate-order and the review are keyed by the ticket's store.
// States (OO.pages.Review.logic.stateOf), in this order:
//   login     signed out (or a demo session's fx_ uid): "Log in to rate this order" → /{hotel}/profile?directLogin=true
//             &tab=orders. A guest's own order (this device's lastOrder) can't be rated without an account either.
//   thanks    this page just sent the rating: "Your review" under "Thanks for your rating! We've shared it with …" (or
//             "You've already rated this order." for the 409).
//   error     the list couldn't be read: "We couldn't load this order" + Try again.
//   loading   the list or the review is being read.
//   missing   the order isn't in the customer's list (another account's, or not a ticket yet: the store hasn't accepted
//             it), or the route names no order: "We couldn't find this order in your account".
//   off       ratings are off for the store: E30 answers 403 (MODULE_SUBSCRIPTION_DISABLED: the store's review-feedback
//             module) or the store's adapter has no rating calls (the facade's kind 'unavailable' with no HTTP status:
//             the fixture stores). No form.
//   rated     the review E30 holds (OO.model.normalizeOrderRating): "Your review". When E30 fails any other way (network,
//             5xx) the list row's own rating, feedback and item_ratings decide instead.
//   closed    a rejected or cancelled order: "This order can't be rated".
//   early     an order the store hasn't accepted yet (status received): "You can rate this order soon".
//   form      OO.model.canRateOrder(order) (preparing, ready, completed: REF's ACCEPTED / READY / COMPLETED).
// The review_settings flags don't gate this page: they only choose whether the store's and the products' AVERAGE
// ratings are shown (the admin's "Display ratings"); whether a store takes ratings is its review-feedback module (the
// 403 above), as in REF.
// The form (REF RatingForm with the completion card's options: independentItemRatings, clearableRatings): the overall
// stars (required; 'Poor' … 'Excellent'), then "Rate the items" (optional: one row per product of the order,
// OO.model.ratingItems; tapping an item's chosen star again clears it; "Add a comment" opens that item's comment box),
// then "Tell us more" (optional, at most 1000 characters, with a counter). Submit sends OO.api.rateOrder with the rated
// or commented items only: the backend gives every other item the order's rating. A sent rating (200, or the 409 "already
// rated" REF also counts as sent) re-reads E30 fresh (a 409 doesn't flush the read cache) and shows the stored review
// (state thanks); when that read fails, the review is shown as sent (logic.draftReview), except after a 409 (what is
// stored isn't this draft: the form says so instead). A refusal keeps the draft and says why (logic.submitText).
// "Your review": the score, the stars and the label, what the customer wrote, and the items with their stars and
// comments (OO.model.orderRatingRows); then "Back to your order" (order-status) and "Your orders".
//
// OO.pages.Review.logic (pure; tests/review-p306.test.mjs):
//   FEEDBACK_MAX 1000.  OFF_TEXT, GENERIC (the copy below).
//   findTicket(rawOrders, orderId) → the get_customer_orders row (a paginator's data[], a bare array, or { data:
//       paginator }) whose ticket_id is orderId (compared as trimmed strings), else null.
//   storeIdOf(row, store) → the row's user_id when it is a whole number > 0, else store.id, else null.
//   ratingsOff(err) → true for a 403, or the facade's missing method (kind 'unavailable' and no HTTP status).
//   stateOf({ uid, orderId, loading, error, data, sent }) → one of the states above. data = load()'s result; sent =
//       the rating this page sent for this order ({ orderId, uid, already, review }) | null.
//   nextStars(current, picked, clearable) → the new rating: picked (a whole 1–5; anything else keeps current); with
//       clearable, picking the current rating again clears it to 0 (REF getNextRatingSelection).
//   emptyDraft(items) → { rating: 0, feedback: '', items: { [productId]: { rating: 0, feedback: '', open: false } } }.
//   itemsPayload(items, draft) → [{ productId, rating?, feedback? }]: the items with a rating or a non-blank comment,
//       in the items' order; the comment trimmed. [] when none.
//   canSubmit(draft) → a whole 1–5 overall rating.
//   draftReview(draft, items) → OrderRating: the draft as the backend stores it (an item without its own rating gets
//       the order's).
//   submitText(err) → the refusal's copy: OFF_TEXT when ratingsOff; the error's own message when it carries a kind
//       (OO.http and the adapter map theirs) and is short; else GENERIC.
//   load(hotel, orderId, uid, store) → Promise<{ found: false } | { found: true, order, storeId, items, review,
//       checked, off }>: order = OO.model.normalizeOrder(row), items = OO.model.ratingItems(order), review = E30's
//       OrderRating | null (checked: true), or on any other E30 failure the row's own (checked: false); off: ratings
//       are off (ratingsOff). Rejects only when the list can't be read.
// <OO.pages.Review.OrderRating hotel store order rating storeId variant /> (P3-07): the order's own rating, for the
//   order pages to mount on an OWNER view (the detail page, the history cards; it never shows what was written).
//   order = the normalised Order (normalizeOrder / normalizeOrders); its rating comes from `rating` (an OrderRating |
//   null the host already has, e.g. OO.model.normalizeOrderRating(the get_customer_orders row)), else order.rating when
//   the Order carries one, else the order's own E30 read (OO.api.getOrderRating, cached; store id = storeId, else
//   order.storeId, else the route store's). Hidden while unknown and when ratings are off for the store (E30's 403;
//   with a given rating, one cached E28 read per store, OO.api.getStoreRating, says so). Rated: its stars + label and
//   "My review" (→ the review page). Not rated: "How was your order?" + Rate (→ the review page) when
//   OO.model.canRateOrder(order); variant 'line' (a history card's foot: just the stars + "My review", or an outline
//   Rate) offers Rate for a completed order only (REF's list: not while the order is live).
// OO.pages.Review.parts (render-time): StarRow { value, size: 'sm' | 'md' | 'lg', label } (display), StarInput { value,
//   onChange(n), label, itemName, size: 'md' | 'lg', clearable, disabled } (5 toggle buttons, aria-pressed), ReviewView
//   { review (OrderRating), order, orderId, store, hotel, notice }, RatingForm { items, draft, onDraft(draft), onSubmit,
//   busy, error, order, store }, Notice { icon, title, body, action }.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var FEEDBACK_MAX = 1000;
  var VALUES = [1, 2, 3, 4, 5];
  var OFF_TEXT = "Ratings aren't available for this store.";
  var GENERIC = "We couldn't send your rating. Please try again.";

  // ---- the pure rules ------------------------------------------------------------------------------------------------
  function text(v) { return typeof v === 'string' ? v.trim() : (typeof v === 'number' && isFinite(v) ? String(v) : ''); }
  function whole(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
    var n = Number(v);
    return n % 1 === 0 ? n : null;
  }
  function stars(v) { var n = whole(v); return n >= 1 && n <= 5 ? n : null; }

  function rowsOf(raw) {
    if (Array.isArray(raw)) return raw;
    if (!raw || typeof raw !== 'object') return [];
    if (Array.isArray(raw.data)) return raw.data;
    return raw.data && typeof raw.data === 'object' && Array.isArray(raw.data.data) ? raw.data.data : [];
  }
  function findTicket(raw, orderId) {
    var id = text(orderId);
    if (!id) return null;
    return rowsOf(raw).filter(function (t) { return t && typeof t === 'object' && text(t.ticket_id) === id; })[0] || null;
  }
  function storeIdOf(row, store) {
    var n = whole(row && row.user_id);
    if (n > 0) return n;
    var s = whole(store && store.id);
    return s > 0 ? s : null;
  }
  function ratingsOff(err) {
    if (!err) return false;
    var status = Number(err.status);
    return status === 403 || (err.kind === 'unavailable' && !(status > 0));
  }

  function stateOf(i) {
    i = i || {};
    if (!i.uid) return 'login';
    if (i.sent) return 'thanks';
    if (!text(i.orderId)) return 'missing';
    if (i.error) return 'error';
    var d = i.data;
    if (i.loading || !d) return 'loading';
    if (!d.found) return 'missing';
    if (d.off) return 'off';
    if (d.review) return 'rated';
    var key = d.order && d.order.status ? d.order.status.key : null;
    if (key === 'rejected' || key === 'cancelled') return 'closed';
    return OO.model.canRateOrder(d.order) ? 'form' : 'early';
  }

  function nextStars(current, picked, clearable) {
    var p = stars(picked);
    if (!p) return current;
    return clearable && current === p ? 0 : p;
  }
  function emptyDraft(items) {
    var map = {};
    (items || []).forEach(function (it) { map[it.productId] = { rating: 0, feedback: '', open: false }; });
    return { rating: 0, feedback: '', items: map };
  }
  function itemsPayload(items, draft) {
    var d = (draft && draft.items) || {};
    var out = [];
    (items || []).forEach(function (it) {
      var row = d[it.productId] || {};
      var r = stars(row.rating);
      var f = text(row.feedback).slice(0, FEEDBACK_MAX);
      if (!r && !f) return;
      var p = { productId: it.productId };
      if (r) p.rating = r;
      if (f) p.feedback = f;
      out.push(p);
    });
    return out;
  }
  function canSubmit(draft) { return !!(draft && stars(draft.rating)); }
  function draftReview(draft, items) {
    var rating = stars(draft && draft.rating);
    if (!rating) return null;
    var d = draft.items || {};
    return {
      rating: rating, label: OO.model.ratingLabel(rating), feedback: text(draft.feedback).slice(0, FEEDBACK_MAX) || null,
      items: (items || []).map(function (it) {
        var row = d[it.productId] || {};
        return { productId: it.productId, name: it.name, rating: stars(row.rating) || rating, feedback: text(row.feedback).slice(0, FEEDBACK_MAX) || null };
      }),
    };
  }
  function submitText(err) {
    if (ratingsOff(err)) return OFF_TEXT;
    var m = err && typeof err.message === 'string' ? err.message.trim() : '';
    return err && err.kind && m && m.length < 140 && !/^HTTP \d/.test(m) ? m : GENERIC;
  }

  function nowOf() {
    var d = OO.data && typeof OO.data.now === 'function' ? OO.data.now() : null;
    return d instanceof Date ? d : new Date();
  }
  function load(hotel, orderId, uid, store) {
    return OO.api.getOrders(hotel, { uid: uid }).then(function (raw) {
      var row = findTicket(raw, orderId);
      if (!row) return { found: false };
      var order = OO.model.normalizeOrder(row, { now: nowOf(), timeZone: store ? store.timezone : null });
      var storeId = storeIdOf(row, store);
      var base = { found: true, order: order, storeId: storeId, items: OO.model.ratingItems(order) };
      return OO.api.getOrderRating(hotel, orderId, { storeId: storeId, fresh: true }).then(function (body) {
        return Object.assign(base, { review: OO.model.normalizeOrderRating(body), checked: true, off: false });
      }, function (err) {
        if (ratingsOff(err)) return Object.assign(base, { review: null, checked: false, off: true });
        return Object.assign(base, { review: OO.model.normalizeOrderRating(row), checked: false, off: false });
      });
    });
  }

  // ---- stars ---------------------------------------------------------------------------------------------------------
  var STAR_PX = { sm: 14, md: 18, lg: 24 };
  function StarRow(props) {
    var value = stars(props.value) || 0;
    var size = STAR_PX[props.size] ? props.size : 'md';
    return (
      <span className={OO.ui.cx('oo-stars', 'oo-stars--' + size, props.className)} role="img"
        aria-label={props.label || value + ' out of 5 stars'}>
        {VALUES.map(function (n) {
          return <span key={n} className={OO.ui.cx('oo-stars__star', n <= value && 'is-on')}><OO.icons.Star size={STAR_PX[size]} strokeWidth={1.8} /></span>;
        })}
      </span>
    );
  }

  var INPUT_PX = { md: 22, lg: 32 };
  function StarInput(props) {
    var value = stars(props.value) || 0;
    var size = INPUT_PX[props.size] ? props.size : 'lg';
    var who = props.itemName ? props.itemName + ' ' : 'this order ';
    return (
      <div className={OO.ui.cx('oo-rate', 'oo-rate--' + size, props.className)} role="group" aria-label={props.label}>
        {VALUES.map(function (n) {
          return (
            <button key={n} type="button" className={OO.ui.cx('oo-rate__star', n <= value && 'is-on')} aria-pressed={value === n ? 'true' : 'false'}
              aria-label={'Rate ' + who + n + ' out of 5'} disabled={props.disabled}
              onClick={function () { props.onChange(nextStars(value, n, props.clearable)); }}>
              <OO.icons.Star size={INPUT_PX[size]} strokeWidth={1.8} />
            </button>
          );
        })}
      </div>
    );
  }

  // ---- shared bits ---------------------------------------------------------------------------------------------------
  function orderLine(order, store) {
    if (!order) return store ? store.name : '';
    var acct = OO.pages.acct || {};
    var when = typeof acct.orderWhen === 'function' ? acct.orderWhen(order) : '';
    return [order.shortId ? 'Order ' + order.shortId : 'Your order', when, order.typeLabel].filter(Boolean).join(' · ');
  }
  function Notice(props) {
    return <OO.ui.EmptyState icon={props.icon} title={props.title} body={props.body} action={props.action} />;
  }
  function backLinks(hotel, orderId) {
    return {
      order: OO.router.link('order-status', { orderId: orderId }, { hotel: hotel }),
      orders: OO.router.link('orders', null, { hotel: hotel }),
    };
  }

  // ---- "Your review" ---------------------------------------------------------------------------------------------------
  function ReviewView(props) {
    var ui = OO.ui;
    var r = props.review;
    var rows = OO.model.orderRatingRows(r, props.order);
    var rated = rows.filter(function (x) { return x.rating != null; }).length;
    var links = backLinks(props.hotel, props.order ? props.order.id : props.orderId);
    return (
      <div className="oo-review__col">
        {props.notice ? <ui.InlineNotice tone="success">{props.notice}</ui.InlineNotice> : null}
        <ui.SectionTitle level="page" sub={orderLine(props.order, props.store)}>Your review</ui.SectionTitle>
        <ui.Card className="oo-review__card">
          <h2 className="oo-acct__cardtitle">Your rating</h2>
          <div className="oo-review__score">
            <span className="oo-review__num" aria-hidden="true">{r.rating}</span>
            <span className="oo-review__scoretext">
              <StarRow value={r.rating} size="lg" label={'You rated this order ' + r.rating + ' out of 5'} />
              <span className="oo-review__label">{r.label}</span>
            </span>
          </div>
          {r.feedback ? (
            <figure className="oo-review__said">
              <figcaption className="oo-review__saidcap">What you said</figcaption>
              <blockquote className="oo-review__quote">{'“' + r.feedback + '”'}</blockquote>
            </figure>
          ) : null}
        </ui.Card>
        {rows.length ? (
          <ui.Card pad="list" className="oo-review__card">
            <div className="oo-review__itemshead">
              <h2 className="oo-acct__cardtitle">Items</h2>
              <span className="oo-acct__muted">{rated ? OO.format.pluralize(rated, 'item') + ' rated' : OO.format.pluralize(rows.length, 'comment')}</span>
            </div>
            <ul className="oo-review__items">
              {rows.map(function (x) {
                return (
                  <li key={x.key} className="oo-review__item">
                    <div className="oo-review__itemrow">
                      <span className="oo-review__itemname">{x.name}{x.qty > 1 ? <span className="oo-review__qty">{' ×' + x.qty}</span> : null}</span>
                      {x.rating != null ? <StarRow value={x.rating} size="sm" label={x.name + ': ' + x.rating + ' out of 5'} /> : null}
                    </div>
                    {x.feedback ? <p className="oo-review__itemsaid">{'“' + x.feedback + '”'}</p> : null}
                  </li>
                );
              })}
            </ul>
          </ui.Card>
        ) : null}
        {/* On a white card: an outline button straight on the canvas would need the page text tokens. */}
        <ui.Card pad="sm" className="oo-review__actions">
          <ui.Button variant="outline" size="md" href={links.order.href} onClick={links.order.onClick}>Back to your order</ui.Button>
          <ui.Button size="md" href={links.orders.href} onClick={links.orders.onClick}>Your orders</ui.Button>
        </ui.Card>
      </div>
    );
  }

  // ---- the form --------------------------------------------------------------------------------------------------------
  function RatingForm(props) {
    var ui = OO.ui;
    var draft = props.draft;
    var items = props.items || [];
    function set(patch) { props.onDraft(Object.assign({}, draft, patch)); }
    function setItem(id, patch) {
      var all = Object.assign({}, draft.items);
      all[id] = Object.assign({ rating: 0, feedback: '', open: false }, all[id], patch);
      set({ items: all });
    }
    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (canSubmit(draft) && !props.busy) props.onSubmit();
    }
    var label = draft.rating ? OO.model.ratingLabel(draft.rating) : 'Tap a star to rate';
    var name = props.store ? props.store.name : 'the store';
    return (
      <form className="oo-review__col oo-review__form" onSubmit={submit} noValidate>
        <ui.SectionTitle level="page" sub={orderLine(props.order, props.store)}>How was your order?</ui.SectionTitle>
        <ui.Card className="oo-review__card oo-review__overall">
          <h2 className="oo-acct__cardtitle">Your rating</h2>
          <p className="oo-acct__muted">{'A quick rating helps ' + name + ' keep every order great.'}</p>
          <StarInput value={draft.rating} size="lg" label="Your rating" disabled={props.busy}
            onChange={function (n) { set({ rating: n }); }} />
          <p className={ui.cx('oo-review__ratelabel', draft.rating && 'is-set')} aria-live="polite">{label}</p>
        </ui.Card>
        {items.length ? (
          <ui.Card pad="list" className="oo-review__card">
            <div className="oo-review__itemshead">
              <h2 className="oo-acct__cardtitle">Rate the items</h2>
              <ui.Badge tone="optional" size="sm">Optional</ui.Badge>
            </div>
            <p className="oo-acct__muted oo-review__hint">Items you don’t rate get your overall rating.</p>
            <ul className="oo-review__items">
              {items.map(function (it) {
                var row = draft.items[it.productId] || { rating: 0, feedback: '', open: false };
                var open = row.open || !!row.feedback;
                return (
                  <li key={it.productId} className="oo-review__item">
                    <div className="oo-review__itemrow">
                      <span className="oo-review__itemname">{it.name}{it.qty > 1 ? <span className="oo-review__qty">{' ×' + it.qty}</span> : null}</span>
                      <StarInput value={row.rating} size="md" clearable itemName={it.name} label={'Rate ' + it.name} disabled={props.busy}
                        onChange={function (n) { setItem(it.productId, { rating: n }); }} />
                    </div>
                    {open ? (
                      <ui.TextArea size="sm" labelHidden label={'Comment on ' + it.name} placeholder="Add a comment (optional)" maxLength={FEEDBACK_MAX}
                        className="oo-review__itemnote" value={row.feedback} disabled={props.busy}
                        onChange={function (v) { setItem(it.productId, { feedback: String(v).slice(0, FEEDBACK_MAX), open: true }); }} />
                    ) : (
                      <ui.Button variant="link" className="oo-review__addnote" disabled={props.busy}
                        onClick={function () { setItem(it.productId, { open: true }); }}>Add a comment</ui.Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </ui.Card>
        ) : null}
        <ui.Card className="oo-review__card">
          <div className="oo-review__notes">
            <ui.TextArea size="md" label="Tell us more" labelSize="sm" placeholder="What did you enjoy? What could be better? (optional)"
              maxLength={FEEDBACK_MAX} value={draft.feedback} disabled={props.busy}
              onChange={function (v) { set({ feedback: String(v).slice(0, FEEDBACK_MAX) }); }} />
            <span className="oo-review__count" aria-hidden="true">{draft.feedback.length + '/' + FEEDBACK_MAX}</span>
          </div>
        </ui.Card>
        {props.error ? <ui.InlineNotice tone="danger" role="alert">{props.error}</ui.InlineNotice> : null}
        <ui.Button type="submit" block busy={props.busy} disabled={!canSubmit(draft)}>{draft.rating ? 'Send rating' : 'Choose a rating'}</ui.Button>
      </form>
    );
  }

  // ---- the page ----------------------------------------------------------------------------------------------------------
  // The form's state; a sent rating goes up to the page (props.onSent), which then shows "Your review".
  function ReviewBody(props) {
    var d = props.data;
    var draftS = React.useState(function () { return emptyDraft(d.items); });
    var sendS = React.useState({ busy: false, error: null });
    var alive = React.useRef(true);
    React.useEffect(function () { alive.current = true; return function () { alive.current = false; }; }, []);
    var send = sendS[0];
    var setSend = sendS[1];

    function submit() {
      var draft = draftS[0];
      if (!canSubmit(draft) || send.busy) return;
      setSend({ busy: true, error: null });
      var hotel = props.hotel;
      OO.api.rateOrder(hotel, {
        orderId: props.orderId, storeId: d.storeId, uid: props.uid, rating: draft.rating, feedback: draft.feedback,
        items: itemsPayload(d.items, draft),
      }).then(function (res) {
        // A 409 isn't a 2xx, so the read cache wasn't flushed: read the stored review fresh either way.
        return OO.api.getOrderRating(hotel, props.orderId, { storeId: d.storeId, fresh: true }).then(function (body) {
          return OO.model.normalizeOrderRating(body);
        }, function () { return null; }).then(function (stored) {
          if (!alive.current) return;
          // A 409 whose review can't be read: what was stored isn't this draft, so don't show the draft as it.
          var review = stored || (res.already ? null : draftReview(draft, d.items));
          if (!review) { setSend({ busy: false, error: "You've already rated this order. Refresh the page to see your review." }); return; }
          setSend({ busy: false, error: null });
          props.onSent({ orderId: props.orderId, uid: props.uid, already: !!res.already, review: review });
        });
      }, function (err) {
        if (alive.current) setSend({ busy: false, error: submitText(err) });
      });
    }

    return (
      <RatingForm items={d.items} draft={draftS[0]} onDraft={draftS[1]} onSubmit={submit} busy={send.busy} error={send.error}
        order={d.order} store={props.store} />
    );
  }

  function Skeleton() {
    var ui = OO.ui;
    return (
      <div className="oo-review__col" aria-busy="true">
        <span className="oo-sr-only" role="status">Loading your order</span>
        <ui.Skeleton lines={2} className="oo-acct__skel-title" />
        <ui.Skeleton variant="block" height={150} />
        <ui.Skeleton variant="block" height={210} />
      </div>
    );
  }

  OO.pages.Review = function Review(props) {
    var route = props.route || {};
    var mode = props.mode;
    var hotel = route.hotel;
    var orderId = text(route.params && route.params.orderId);
    var acct = OO.pages.acct;
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var session = OO.useStore(function (s) { return s.session; });
    var raw = session && session.customer ? text(session.customer.uid) : '';
    var uid = raw && raw.indexOf('fx_') !== 0 ? raw : '';
    var store = h.store;
    var ready = !!(store && uid && orderId);
    var q = OO.useAsync(function () {
      return ready ? load(hotel, orderId, uid, store) : Promise.resolve(null);
    }, [hotel, orderId, uid, ready]);
    var sentS = React.useState(null);
    var sent = sentS[0] && sentS[0].orderId === orderId && sentS[0].uid === uid ? sentS[0] : null;

    var back = orderId ? OO.router.href('order-status', { orderId: orderId }, { hotel: hotel }) : OO.router.href('orders', null, { hotel: hotel });
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: back, profile: true });
    var blocked = acct.gate(h, { title: 'Rate your order', header: header, skeleton: <Skeleton /> });
    if (blocked) return blocked;

    var state = stateOf({ uid: uid, orderId: orderId, loading: q.loading, error: q.error, data: q.data, sent: sent });
    var links = backLinks(hotel, orderId);
    var toOrder = <ui.Button size="md" href={links.order.href} onClick={links.order.onClick}>Back to your order</ui.Button>;
    var body;
    if (state === 'login') {
      var login = OO.router.link('profile', null, { hotel: hotel, query: { directLogin: 'true', tab: 'orders' } });
      body = <Notice icon="Star" title="Log in to rate this order" body="Only the account that placed an order can rate it."
        action={<ui.Button size="md" href={login.href} onClick={login.onClick}>Log in</ui.Button>} />;
    } else if (state === 'loading') {
      body = <Skeleton />;
    } else if (state === 'error') {
      body = <Notice icon="Info" title="We couldn't load this order" body={acct.errorText(q.error, 'Check your connection and try again.')}
        action={<ui.Button size="md" onClick={q.reload}>Try again</ui.Button>} />;
    } else if (state === 'missing') {
      body = <Notice icon="Receipt" title="We couldn't find this order in your account"
        body="You can rate an order from the account that placed it, once the store has accepted it."
        action={<ui.Button size="md" href={links.orders.href} onClick={links.orders.onClick}>Your orders</ui.Button>} />;
    } else if (state === 'off') {
      body = <Notice icon="Star" title={OFF_TEXT} body="This store isn't taking ratings online at the moment." action={toOrder} />;
    } else if (state === 'thanks') {
      body = <ReviewView review={sent.review} order={q.data ? q.data.order : null} orderId={orderId} store={store} hotel={hotel}
        notice={sent.already ? "You've already rated this order." : 'Thanks for your rating! We’ve shared it with ' + store.name + '.'} />;
    } else if (state === 'rated') {
      body = <ReviewView review={q.data.review} order={q.data.order} orderId={orderId} store={store} hotel={hotel} />;
    } else if (state === 'closed') {
      body = <Notice icon="Info" title="This order can't be rated"
        body={q.data.order.status.key === 'rejected' ? "The store didn't accept this order." : 'This order was cancelled.'} action={toOrder} />;
    } else if (state === 'early') {
      body = <Notice icon="Clock" title="You can rate this order soon" body="Ratings open once the store has accepted your order." action={toOrder} />;
    } else {
      body = <ReviewBody key={orderId + '|' + uid} data={q.data} hotel={hotel} orderId={orderId} uid={uid} store={store} onSent={sentS[1]} />;
    }
    return (
      <acct.Page title={state === 'rated' || state === 'thanks' ? 'Your review' : 'Rate your order'} {...header} className="oo-review" busy={state === 'loading'}>
        {body}
        <ui.PoweredBySipo />
      </acct.Page>
    );
  };

  // ---- P3-07: the order's own rating (the header's OrderRating) -----------------------------------------------------
  function givenRating(props) {
    if (props.rating !== undefined) return props.rating;
    return props.order && props.order.rating !== undefined ? props.order.rating : undefined;
  }
  function OrderRating(props) {
    var ui = OO.ui;
    var order = props.order || null;
    var hotel = text(props.hotel) || (props.store && props.store.onlineKey ? String(props.store.onlineKey) : '');
    var id = order ? text(order.id) : '';
    var given = givenRating(props);
    var sid = whole(props.storeId) > 0 ? whole(props.storeId) : (order && whole(order.storeId) > 0 ? whole(order.storeId) : undefined);
    // No rating given: the order's own E30 read, whose 403 says ratings are off.
    var own = ui.useRatingRead(given === undefined && hotel && id ? 'order|' + hotel + '|' + id + '|' + (sid || '') : null, function () {
      return OO.api.getOrderRating(hotel, id, { storeId: sid }).then(function (body) {
        return { on: true, rating: OO.model.normalizeOrderRating(body) };
      }, function (err) { return { on: ratingsOff(err) ? false : null, rating: null }; });
    });
    // A rating given: is the store still taking ratings? One cached E28 read per store.
    var probe = ui.useRatingRead(given !== undefined && hotel ? 'ratings-on|' + hotel : null, function () {
      return OO.api.getStoreRating(hotel).then(function () { return { on: true }; }, function (err) { return { on: ratingsOff(err) ? false : null }; });
    });
    var st = given !== undefined ? (probe ? { on: probe.on, rating: given } : null) : own;
    if (!order || !id || !st || st.on === false) return null;
    var line = props.variant === 'line';
    var link = OO.router.link('review', { orderId: id }, { hotel: hotel });
    var r = st.rating;
    if (r && stars(r.rating)) {
      var said = 'You rated this order ' + r.rating + ' out of 5';
      if (line) {
        return (
          <span className="oo-order-rating oo-order-rating--line">
            <StarRow value={r.rating} size="sm" label={said} />
            <a className="oo-order-rating__link" href={link.href} onClick={link.onClick}>My review</a>
          </span>
        );
      }
      return (
        <ui.Card className="oo-order-rating">
          <div className="oo-order-rating__row">
            <span className="oo-order-rating__text">
              <span className="oo-acct__cardtitle">Your rating</span>
              <span className="oo-order-rating__stars">
                <StarRow value={r.rating} size="md" label={said} />
                <span className="oo-order-rating__label">{OO.model.ratingLabel(r.rating)}</span>
              </span>
            </span>
            <ui.Button variant="outline" size="xs" href={link.href} onClick={link.onClick}>My review</ui.Button>
          </div>
        </ui.Card>
      );
    }
    var can = line ? !!(order.status && order.status.key === 'completed') : OO.model.canRateOrder(order);
    if (!can) return null;
    var label = 'Rate ' + (order.shortId ? 'order ' + order.shortId : 'this order');
    if (line) {
      return (
        <ui.Button variant="outline" size="xs" icon="Star" className="oo-order-rating oo-order-rating--line" href={link.href}
          onClick={link.onClick} aria-label={label}>Rate</ui.Button>
      );
    }
    var name = props.store && props.store.name ? props.store.name : 'the store';
    return (
      <ui.Card className="oo-order-rating">
        <div className="oo-order-rating__row">
          <span className="oo-order-rating__text">
            <span className="oo-acct__cardtitle">How was your order?</span>
            <span className="oo-acct__muted">{'Rate it to tell ' + name + ' how it went.'}</span>
          </span>
          <ui.Button size="xs" icon="Star" href={link.href} onClick={link.onClick} aria-label={label}>Rate</ui.Button>
        </div>
      </ui.Card>
    );
  }

  OO.pages.Review.OrderRating = OrderRating;
  OO.pages.Review.logic = {
    FEEDBACK_MAX: FEEDBACK_MAX, OFF_TEXT: OFF_TEXT, GENERIC: GENERIC,
    findTicket: findTicket, storeIdOf: storeIdOf, ratingsOff: ratingsOff, stateOf: stateOf, nextStars: nextStars,
    emptyDraft: emptyDraft, itemsPayload: itemsPayload, canSubmit: canSubmit, draftReview: draftReview,
    submitText: submitText, load: load,
  };
  OO.pages.Review.parts = { StarRow: StarRow, StarInput: StarInput, ReviewView: ReviewView, RatingForm: RatingForm, Notice: Notice };
})();


// ===================== src/pages/wallet-page.jsx =====================
// src/pages/wallet-page.jsx: OO.pages.Wallet, the wallet page (Batch 2 P3-14; REF OnlineOrderWalletPage, C §1.2):
// /{hotel}/wallet, in both modes (D-18); off at a table (D-20: the router sends a table to its menu). Named
// wallet-page.jsx because src/lib/wallet.jsx is the P2 wallet engine (OO.wallet). Page id 'wallet' (routed by P3-01).
// Styles: styles/account.css (.oo-wl-*). The wallet holds points only: there is no top-up (REF).
//
// Data: the page shows the wallet the engine judges with, so the balance here, the checkout's slider and the rewards
// page always agree:
//   the wallet   OO.store's settled get_customer_wallet_points read for this store (OO.store.wallet(hotel); the page
//                re-renders on state.wallets), read again with OO.data.loadWallet(hotel, { fresh: true }) once per
//                mount and customer, so the page opens on the balance as it is now (a read P3-27 also makes after a
//                paid order). A wallet settled earlier shows at once while that read runs.
//   the history  OO.api.getWalletTransactions(hotel, { uid }) (E27: the wallet's customer_id), once the wallet read
//                has settled (it then reads the wallet from OO.http's cache: one wallet request per visit).
//   Loyalty      OO.model.normalizeLoyalty({ wallet }, { store, orderType } + POLISH-5's OO.data.loyaltyScope(hotel))
//                (P3-13) for the order type the cart prices with (OO.pages.Cart.shared.pricingCtx over the checkout draft,
//                else PICKUP), in OO.mode: the channel's redemption row, the ratio table, the daily cap. Activity:
//                OO.model.normalizeActivity at OO.data.now() in the store's zone, valued at that channel's ratio; stats:
//                OO.model.activityStats.
// States, in order: the store (OO.pages.acct.gate) · signed out → a log-in card (OO.appKit.LoginPrompt) · no wallet
// settled and the read pending → a skeleton · no usable wallet (the loyalty module off, an unknown customer, a failed
// read: the soft reads can't tell them apart) → "Points aren't available right now" with Try again (these two single
// cards sit in the account pages' centred column, oo-acct--center) · the wallet:
//   the balance card (dark, as the Rewards card): the program and tier, the balance, the scope line (POLISH-5,
//     p5-followups #21; mode app, a chain of two or more stores: Loyalty.scope as Home's and the Rewards card say it,
//     "Points shared across the {master} group" | "Points for {branch} only"), its worth at the channel's ratio
//     ('Worth about $13.60'), how points are used here (logic.view's `how`), and "How points convert": a toggle
//     (aria-expanded) for the ratio table: every redemption row (label, 'Your orders' on the channel's, its limits and
//     its rate; a row the server takes no points on reads 'Not available', never REF's "No cap");
//   two links: Spend ('Redeem rewards', 'View rewards' at the daily cap or a cart-level store) → rewards, and Earn
//     ('Place an order') → menu;
//   the daily cap (usage_limit_over): its copy + 'You can still order without points.' (REF);
//   stats: Earned · Redeemed · This month, + Expired when any (REF counts expiries as redeemed);
//   Points history, newest first: the title, its day and time, 'Order $27.50' (ticket_value), ±points and '≈ $1.08'
//     (only with a ratio). A failed read: "We couldn't load your history" + Try again; none: "No points history yet".
// Header: back (OO.router.back; the rewards page in app mode, the account page online) and, online, the profile ring.
// OO.pages.Wallet.logic (pure; tests/wallet-page-p314.test.mjs):
//   orderTypeOf(store, draft) → 'PICKUP' | 'DELIVERY': Cart.shared.pricingCtx(store, draft).orderType, else PICKUP.
//   pointsText(n, word) → '1,250 points' | '1 point' (the store's word: '4 stamps', '1 bean').
//   rateText(row, word, currency) → '25 points = $1.00' | 'Not available' (a row that isn't usable).
//   limitsText(row, word) → 'Min 100 · Max 500 points' | 'No minimum · Max 500 points' | 'Not set up' (ratio 0) |
//       'No points on these orders' (a maximum below 1 point).
//   view(L, activity, { store }) → View | null (no L): { points ('340'), word, value ('Worth about $13.60' | null),
//       how (string | null), rows: [{ key, label, current, usable, rate, limits }], spend ({ label }), notice (the cap's
//       copy | null), stats ([{ key, label, value }] | null without activity), history ([{ id, title, when, meta,
//       points, kind, value, sr }] | null without activity) }.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function wordOf(store) { return (store && store.loyalty && store.loyalty.pointsWord) || 'points'; }
  function count(n) { return Math.abs(num(n)).toLocaleString('en-NZ'); }
  function pointsText(n, word) {
    var w = word || 'points';
    var one = /s$/i.test(w) ? w.slice(0, -1) : w;
    return count(n) + ' ' + (Math.abs(num(n)) === 1 ? one : w);
  }
  function money(n, cur) { return OO.format.money(n, cur); }

  // ---- logic (pure; see the header) ------------------------------------------------------------------------------
  function orderTypeOf(store, draft) {
    var K = OO.pages.Cart && OO.pages.Cart.shared;
    try { if (K && typeof K.pricingCtx === 'function' && store) return K.pricingCtx(store, draft || {}).orderType || 'PICKUP'; } catch (e) { /* PICKUP */ }
    return 'PICKUP';
  }
  function rateText(row, word, cur) {
    return row && row.usable ? pointsText(row.ratio, word) + ' = ' + money(1, cur) : 'Not available';
  }
  function limitsText(row, word) {
    if (!row || !(row.ratio > 0)) return 'Not set up';
    if (!row.usable) return 'No points on these orders';
    return (row.min > 0 ? 'Min ' + count(row.min) : 'No minimum') + ' · Max ' + pointsText(row.max, word);
  }
  function howOf(L, word, cur) {
    if (L.redeemOff === 'NO_RULE' || L.redeemOff === 'NO_MAX') return L.redeemText;
    if (!L.rule) return null;
    var rate = pointsText(L.rule.ratio, word) + ' = ' + money(1, cur);
    return L.productLevel ? 'Swap them for rewards: ' + rate + '.' : 'Use them at checkout: ' + rate + ' off.';
  }
  function historyRow(a, word, cur) {
    var meta = [a.whenLong || a.when];
    if (a.orderValue) meta.push('Order ' + money(a.orderValue, cur));
    return {
      id: a.id, title: a.title, when: a.whenLong || a.when, meta: meta.filter(Boolean).join(' · '), points: a.pointsLabel, kind: a.kind,
      value: a.value != null ? '≈ ' + money(a.value, cur) : null,
      sr: pointsText(a.points, word) + (a.kind === 'debit' ? (a.source === 'expiry' ? ' expired' : ' used') : ' added'),
    };
  }
  function view(L, activity, opts) {
    if (!L) return null;
    var store = (opts && opts.store) || {};
    var word = L.pointsWord || wordOf(store);
    var cur = store.currency;
    var stats = null;
    if (activity) {
      var s = OO.model.activityStats(activity, { now: opts && opts.now, timeZone: store.timezone || null });
      stats = [{ key: 'earned', label: 'Earned', value: s.earned }, { key: 'redeemed', label: 'Redeemed', value: s.redeemed }, { key: 'month', label: 'This month', value: s.thisMonth }];
      if (s.expired > 0) stats.push({ key: 'expired', label: 'Expired', value: s.expired });
    }
    return {
      points: count(L.walletPoints), word: Math.abs(num(L.walletPoints)) === 1 ? word.replace(/s$/i, '') : word,
      value: L.pointsPerDollar && L.walletPoints > 0 ? 'Worth about ' + money(L.walletValue, cur) : null,
      how: howOf(L, word, cur),
      rows: L.ratioRows.map(function (r, i) {
        return { key: r.orderType + '|' + i, label: r.label, current: r.current, usable: r.usable, rate: rateText(r, word, cur), limits: limitsText(r, word) };
      }),
      spend: { label: L.productLevel && !L.usageLimitOver ? 'Redeem rewards' : 'View rewards' },
      notice: L.usageLimitOver ? (L.usageText || 'You’ve reached today’s limit for redeeming points.') + ' You can still order without points.' : null,
      stats: stats,
      history: activity ? activity.map(function (a) { return historyRow(a, word, cur); }) : null,
    };
  }

  // ---- the page ---------------------------------------------------------------------------------------------------
  function selectSession(s) { return s.session; }
  function selectWallets(s) { return s.wallets; }
  function selectDraft(s) { return s.checkout; }
  function uidOf(session) { return session && session.customer && session.customer.uid ? String(session.customer.uid) : null; }

  OO.pages.Wallet = function Wallet(props) {
    var ui = OO.ui, acct = OO.pages.acct, K = OO.appKit;
    var route = props.route;
    var hotel = route.hotel;
    var mode = props.mode || OO.mode;
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var uid = uidOf(OO.useStore(selectSession));
    OO.useStore(selectWallets); // the settled wallet (below) re-renders the page
    var draft = OO.useStore(selectDraft);
    var openS = React.useState(false);
    // The wallet as it is now, then the history (see the header).
    var read = OO.useAsync(function () {
      if (!uid || !store) return null;
      return OO.data.loadWallet(hotel, { fresh: true }).then(function (w) {
        if (!OO.wallet.usable(w)) return { tx: null };
        return OO.api.getWalletTransactions(hotel, { uid: uid }).then(function (tx) { return { tx: tx || null }; }, function () { return { tx: null }; });
      });
    }, [hotel, uid, !!store]);
    var wallet = uid ? OO.wallet.usable(OO.store.wallet(hotel)) : null;
    var orderType = store ? orderTypeOf(store, draft && (!draft.hotel || String(draft.hotel) === String(hotel)) ? draft : null) : 'PICKUP';
    var scope = OO.data.loyaltyScope ? OO.data.loyaltyScope(hotel) : {}; // POLISH-5: { chain, master } in mode app, else {}
    var L = React.useMemo(function () {
      return wallet && store ? OO.model.normalizeLoyalty({ wallet: wallet }, Object.assign({ store: store, orderType: orderType }, scope)) : null;
    }, [wallet, store, orderType, scope.chain, scope.master]);
    var tx = read.data && read.data.tx;
    var activity = React.useMemo(function () {
      return tx && store ? OO.model.normalizeActivity(tx, { now: OO.data.now(), timeZone: store.timezone, pointsPerDollar: L ? L.pointsPerDollar : null }) : null;
    }, [tx, store, L ? L.pointsPerDollar : null]);
    var v = React.useMemo(function () { return view(L, activity, { store: store, now: OO.data.now() }); }, [L, activity, store]);

    var header = acct.headerProps({ hotel: hotel, mode: mode, back: OO.router.href(mode === 'app' ? 'rewards' : 'account', null, { hotel: hotel }), profile: true });
    var blocked = acct.gate(h, { title: 'Wallet', header: header });
    if (blocked) return blocked;

    var programName = (store.loyalty && store.loyalty.programName) || 'Rewards';
    var body;
    var narrow = false; // a single card (log in, unavailable): the account pages' centred column
    if (!uid) {
      narrow = true;
      body = <K.LoginPrompt hotel={hotel} title="Log in to see your wallet" body={'Your ' + wordOf(store) + ' and their history live here.'} />;
    } else if (!wallet && (read.loading || read.data === undefined)) {
      body = <WalletSkeleton />;
    } else if (!wallet) {
      narrow = true;
      body = (
        <ui.EmptyState icon="Gift" title={'Points aren’t available right now'}
          body={'We couldn’t load your ' + wordOf(store) + ' for ' + store.name + '. Try again, or ask the team in store.'}
          action={<ui.Button size="md" variant="outline" onClick={read.reload}>Try again</ui.Button>} />
      );
    } else {
      var rewards = OO.router.link('rewards', null, { hotel: hotel });
      var menu = OO.router.link('menu', null, { hotel: hotel });
      body = (
        <div className="oo-wl">
          <div className="oo-wl__side">
            <BalanceCard v={v} L={L} store={store} open={openS[0]} onToggle={function () { openS[1](function (o) { return !o; }); }} />
            <nav className="oo-wl-links" aria-label={programName}>
              <a className="oo-wl-link" href={rewards.href} onClick={rewards.onClick}>
                <span className="oo-wl-link__text"><span className="oo-wl-link__kicker">Spend</span><span className="oo-wl-link__label">{v.spend.label}</span></span>
                <OO.icons.ChevronRight size={16} />
              </a>
              <a className="oo-wl-link" href={menu.href} onClick={menu.onClick}>
                <span className="oo-wl-link__text"><span className="oo-wl-link__kicker">Earn</span><span className="oo-wl-link__label">Place an order</span></span>
                <OO.icons.ChevronRight size={16} />
              </a>
            </nav>
            {v.notice ? <ui.InlineNotice tone="warn" role="alert" className="oo-wl__notice">{v.notice}</ui.InlineNotice> : null}
            {v.stats ? (
              <ul className="oo-wl-stats" aria-label="Your points at a glance">
                {v.stats.map(function (s) {
                  return (
                    <li key={s.key} className={'oo-wl-stat oo-wl-stat--' + s.key}>
                      <span className="oo-wl-stat__label">{s.label}</span>
                      <span className="oo-wl-stat__num">{count(s.value)}</span>
                      <span className="oo-wl-stat__word">{wordOf(store)}</span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
          <section className="oo-wl__main" aria-labelledby="oo-wl-history-t">
            <ui.SectionTitle level="section" id="oo-wl-history-t" sub="Every point you earn and use"
              action={v.history && v.history.length ? <ui.Badge tone="neutral">{String(v.history.length)}</ui.Badge> : null}>Points history</ui.SectionTitle>
            <History v={v} read={read} store={store} />
          </section>
        </div>
      );
    }
    return (
      <acct.Page title="Wallet" {...header} className={OO.ui.cx('oo-wl-page', narrow && 'oo-acct--center')} busy={!!uid && !wallet && read.loading}>
        {body}
      </acct.Page>
    );
  };

  function BalanceCard(props) {
    var v = props.v, L = props.L, store = props.store, ui = OO.ui;
    var panelId = 'oo-wl-rates';
    return (
      <section className="oo-wl-card" aria-label={'Your ' + ((store.loyalty && store.loyalty.pointsWord) || 'points') + ' balance'}>
        <div className="oo-wl-card__top">
          <span className="oo-wl-card__prog">{L.programName}</span>
          {L.tier ? <ui.Badge tone="inverse" upper>{L.tier}</ui.Badge> : null}
        </div>
        <div className="oo-wl-card__pts">
          <span className="oo-wl-card__num">{v.points}</span>
          <span className="oo-wl-card__word">{v.word}</span>
        </div>
        {L.scope ? <p className="oo-wl-card__scope">{L.scope.text}</p> : null}
        {v.value ? <p className="oo-wl-card__value">{v.value}</p> : null}
        {v.how ? <p className="oo-wl-card__how">{v.how}</p> : null}
        {v.rows.length ? (
          <button type="button" className="oo-wl-card__toggle" aria-expanded={props.open ? 'true' : 'false'} aria-controls={panelId} onClick={props.onToggle}>
            How points convert
            {props.open ? <OO.icons.ChevronUp size={14} /> : <OO.icons.ChevronDown size={14} />}
          </button>
        ) : null}
        {v.rows.length && props.open ? (
          <ul className="oo-wl-rates" id={panelId}>
            {v.rows.map(function (r) {
              return (
                <li key={r.key} className={ui.cx('oo-wl-rate', r.current && 'is-current', !r.usable && 'is-off')}>
                  <span className="oo-wl-rate__text">
                    <span className="oo-wl-rate__label">{r.label}{r.current ? <span className="oo-wl-rate__mine">Your orders</span> : null}</span>
                    <span className="oo-wl-rate__limits">{r.limits}</span>
                  </span>
                  <span className="oo-wl-rate__rate">{r.rate}</span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    );
  }

  function History(props) {
    var v = props.v, read = props.read, store = props.store, ui = OO.ui;
    if (!v.history) {
      if (read.loading || read.data === undefined) {
        return <div aria-busy="true"><span className="oo-sr-only" role="status">Loading your history</span><ui.Skeleton variant="block" height={168} /></div>;
      }
      return (
        <ui.EmptyState icon="Info" role="alert" title="We couldn’t load your history" body="Check your connection and try again."
          action={<ui.Button size="md" variant="outline" onClick={read.reload}>Try again</ui.Button>} />
      );
    }
    if (!v.history.length) {
      return <ui.EmptyState icon="Star" title="No points history yet" body={'Points you earn and use at ' + store.name + ' show up here.'} />;
    }
    return (
      <ui.Card pad="list">
        <ul className="oo-wl-hist">
          {v.history.map(function (r) {
            return (
              <li key={r.id} className={'oo-wl-row is-' + r.kind}>
                <span className="oo-wl-row__text">
                  <span className="oo-wl-row__title">{r.title}</span>
                  {r.meta ? <span className="oo-wl-row__meta">{r.meta}</span> : null}
                </span>
                <span className="oo-wl-row__nums">
                  <span className="oo-wl-row__pts" aria-hidden="true">{r.points}</span>
                  <span className="oo-sr-only">{r.sr}</span>
                  {r.value ? <span className="oo-wl-row__value">{r.value}</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
      </ui.Card>
    );
  }

  function WalletSkeleton() {
    var ui = OO.ui;
    return (
      <div className="oo-wl oo-wl--skel" aria-hidden="true">
        <div className="oo-wl__side">
          <ui.Skeleton variant="block" height={196} className="oo-wl__skel-card" />
          <ui.Skeleton variant="block" height={64} />
        </div>
        <div className="oo-wl__main"><ui.Skeleton variant="block" height={56} count={4} /></div>
      </div>
    );
  }

  OO.pages.Wallet.logic = {
    orderTypeOf: orderTypeOf,
    pointsText: pointsText,
    rateText: rateText,
    limitsText: limitsText,
    view: view,
  };
})();


// ===================== src/pages/addresses.jsx =====================
// src/pages/addresses.jsx: OO.pages.Addresses, the customer's address book (Batch 2 P3-10 / P3-11; REF
// AccountPageClient "Saved addresses", C §1.1 and §3.1 "Address book rules"): list, add, edit, remove (confirmed), the
// default and the address type (P3-10); Places autocomplete in the form and the checkout's default preselect (P3-11,
// with checkout-address.jsx). Saved through the profile update (B16, else the full-record customer/create; P3-09).
// Not a routed page: the profile page mounts it on its Profile tab (both modes), where REF's account page has it. It
// loads after the pages that mount it, so they reach it at render time only. Styles: the Batch 2 P3-09…12 section of
// styles/app-pages.css (.oo-addr*).
//
// <OO.pages.Addresses hotel store onSaved(text)? />: the signed-in customer's book (OO.store's session); null for a guest.
//   The book is read FRESH (OO.api.getCustomer → its raw data.address) on mount, and after a save it is the saved
//   record's. The session's addresses never feed a save: OO.model.normalizeCustomer drops each entry's uid and
//   `selected`, so a book built from them would silently move the default. While the read runs: a skeleton; a failed
//   read: "We couldn't load your addresses" + Try again, and nothing to edit. A read that lands after a save is ignored.
//   Every change re-sends the WHOLE book (logic.wire, REF's shape) through OO.pages.Profile.saveProfile (B16 with its
//   fallbacks; the session reconciled with the saved record), and the list changes only once it is saved. While one
//   save runs, every action waits. A refusal shows Profile.logic.saveError's words (a 401 with "Log in again", as on
//   the edit page). Saved → onSaved('Address added.' | 'Address updated.' | 'Address removed.' | 'Default address
//   updated.') (the profile page's toast).
//   Rows: the type (Home / Work / Other), "Default" on the selected one, the line and city · postcode, then "Set as
//   default" (not on the default), Edit and Remove. Remove asks first ("Remove this address?"); removing the default
//   makes the first remaining one the default (REF). Empty: "No saved addresses" + "Add an address for faster
//   checkout." At MAX (20, B16's cap) "Add address" is off, with a note.
//   Add / Edit: a dialog sheet with the type (Home / Work / Other radios; a new address starts Home, REF) and the
//   address (required). Typing a new line drops the city, postcode and coordinates of the line it replaces (REF);
//   typing an edited address's own line again brings them back. The same address twice (logic.key) is refused:
//   "You've already saved this address." An edit that changes nothing just closes.
//   P3-11, the address search: on a real store with the boot's Maps key (OO.delivery.enabled(); a fixture store keeps
//   a plain field, as the checkout does) the field carries the Places suggestions (OO.delivery.attach, the store's
//   country) and "Use my current location" (OO.delivery.locate) sits under it; a pick is the line with its city,
//   postcode and coordinates. Enter in the field picks a suggestion and never saves. A typed line can still be saved
//   (REF: geoLoc 0, 0), with the hint "Choose your address from the suggestions so we can deliver to it." (the
//   checkout preselects only addresses with coordinates). A search that didn't load, or a rejected key (Google's
//   disabled field is re-enabled), says the suggestions aren't available and the address can still be typed; a
//   failed location says so.
// OO.pages.Addresses.logic (pure):
//   TYPES ['HOME', 'WORK', 'OTHER'] · TYPE_LABEL · MAX 20
//   book(raw) → Entry[]   Entry = { uid, type, address, city, zipcode (string | null), selected (bool), lat, lng (numbers
//       | null) }. REF's mapRawAddress + deduplicateAddresses. raw: a stored address value (a list; a JSON string; a
//       customer/register plain string → one entry, the default; a single object; '' / null → []) or a session
//       customer's addresses ({lat, lng}). uid: the stored one when it is a number, else a stable hash of the line
//       (REF); type upper-cased, HOME when missing, OTHER when unknown; blank lines dropped; a duplicate (key) dropped,
//       the first kept; exactly one selected: the first marked one, else the first.
//   key(entry) → REF's duplicate key: line, city, postcode, lat, lng (0 when missing), trimmed and lower-cased.
//   wire(entries) → the `address` list B16 / customer/create store, in REF's shape: { uid, type, address, city, zipcode
//       (| null), selected (1 | 0), geoLoc { latitude, longitude } (0, 0 = not picked from Places; an out-of-range
//       coordinate goes as 0, 0) }; blank lines dropped, exactly one selected, line / city / postcode cut to B16's
//       255 / 100 / 20 characters.
//   add(entries, entry, now) → entries + the entry (uid = now's ms, unique in the book; the default when it is the first)
//   update(entries, uid, patch) → the entry with that uid patched (its uid and default kept)
//   remove(entries, uid) → without it; removing the default makes the first remaining one the default
//   setDefault(entries, uid) → that one selected, every other not
//   duplicateOf(entries, entry, exceptUid) → the saved entry with the same key (other than exceptUid) | null
//   label(entry) → 'Home' | 'Work' | 'Other' · meta(entry) → 'Wellington · 6011' (city · postcode, REF) | ''
//   sessionAddresses(entries) → the session customer's `addresses`: OO.model.normalizeCustomer's entry ({ address,
//       city, zipcode, type, lat, lng }) plus uid and selected, so the checkout knows the default (P3-11)
//   pointOf(entry) → OO.delivery's Point (real coordinates in range, never 0, 0) | null
//   defaultOf(entries, { needPoint }) → the selected entry, else the first; with needPoint, among the entries with a
//       point only (REF OrderTimePopup's "usable saved addresses"); null when there is none.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var TYPES = ['HOME', 'WORK', 'OTHER'];
  var TYPE_LABEL = { HOME: 'Home', WORK: 'Work', OTHER: 'Other' };
  var MAX = 20;

  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
  function str(v) { return v === null || v === undefined ? '' : String(v); }
  function numOrNull(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }

  // ---- the book (pure; see the header) ---------------------------------------------------------------------
  // REF stableAddressUid: a positive integer from the line and its position.
  function stableUid(text, index) {
    var hash = 0;
    for (var i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
    return Math.abs(hash) + index + 1;
  }
  // REF unpackAddressSource: a stored address value → its raw entries.
  function unpack(source) {
    if (!source) return [];
    if (typeof source === 'string') {
      var s = source.trim();
      if (!s) return [];
      var parsed;
      try { parsed = JSON.parse(s); } catch (e) { return [{ address: s, selected: true }]; }
      return typeof parsed === 'string' ? (parsed.trim() ? [{ address: parsed.trim(), selected: true }] : []) : unpack(parsed);
    }
    if (Array.isArray(source)) return source;
    return isObj(source) ? [source] : [];
  }
  function markedSelected(a) { var v = a.selected !== undefined ? a.selected : a.isSelected; return v === true || v === 1 || v === '1' || v === 'true'; }
  function typeOf(v) {
    var t = str(v).trim().toUpperCase();
    if (!t) return 'HOME';
    return TYPES.indexOf(t) >= 0 ? t : 'OTHER';
  }
  function coordIn(v, limit) { var n = numOrNull(v); return n !== null && Math.abs(n) <= limit ? n : null; }
  function key(e) {
    var line = str(e && e.address).trim();
    if (!line) return 'uid:' + str(e && e.uid);
    return [line, e.city, e.zipcode, e.lat == null ? 0 : e.lat, e.lng == null ? 0 : e.lng].map(function (v) {
      return str(v).trim().toLowerCase();
    }).join('|');
  }
  // Exactly one selected: the first marked one, else the first (REF).
  function oneSelected(list) {
    var at = -1;
    for (var i = 0; i < list.length; i++) if (list[i].selected) { at = i; break; }
    return list.map(function (e, i) { return Object.assign({}, e, { selected: i === (at < 0 ? 0 : at) }); });
  }
  function book(raw) {
    var rows = unpack(raw).filter(isObj);
    var explicit = rows.some(function (a) { return own(a, 'selected') || own(a, 'isSelected'); });
    var seen = {};
    var out = [];
    rows.forEach(function (a, i) {
      var line = str(a.address !== undefined && a.address !== null ? a.address : (a.line1 !== undefined ? a.line1 : a.formatted_address)).trim();
      if (!line) return;
      var geo = isObj(a.geoLoc) ? a.geoLoc : {};
      var lat = numOrNull(geo.latitude !== undefined ? geo.latitude : (a.latitude !== undefined ? a.latitude : a.lat));
      var lng = numOrNull(geo.longitude !== undefined ? geo.longitude : (a.longitude !== undefined ? a.longitude : a.lng));
      var uid = Number(a.uid !== undefined && a.uid !== null ? a.uid : (a.addressUid !== undefined ? a.addressUid : a.id));
      var e = {
        uid: isFinite(uid) && uid ? uid : stableUid(line, i),
        type: typeOf(a.type),
        ...(a.label ? {label:str(a.label).trim().slice(0,80)} : {}),
        address: line,
        city: str(a.city).trim(),
        zipcode: a.zipcode === null || a.zipcode === undefined || str(a.zipcode).trim() === '' ? null : str(a.zipcode).trim(),
        selected: markedSelected(a) || (!explicit && i === 0),
        lat: lat,
        lng: lng,
      };
      var k = key(e);
      if (seen[k]) return;
      seen[k] = true;
      out.push(e);
    });
    return oneSelected(out);
  }
  function wire(entries) {
    var list = oneSelected((entries || []).filter(function (e) { return isObj(e) && str(e.address).trim(); }));
    return list.map(function (e) {
      var lat = coordIn(e.lat, 90), lng = coordIn(e.lng, 180);
      var picked = lat !== null && lng !== null;
      var zip = str(e.zipcode).trim();
      return {
        uid: e.uid, type: typeOf(e.type), ...(e.label ? {label:str(e.label).trim().slice(0,80)} : {}), address: str(e.address).trim().slice(0, 255), city: str(e.city).trim().slice(0, 100),
        zipcode: zip ? zip.slice(0, 20) : null, selected: e.selected ? 1 : 0,
        geoLoc: { latitude: picked ? lat : 0, longitude: picked ? lng : 0 },
      };
    });
  }
  function entryOf(fields) {
    var f = fields || {};
    var zip = str(f.zipcode).trim();
    return {
      type: typeOf(f.type), address: str(f.address).trim(), city: str(f.city).trim(), zipcode: zip || null,
      lat: numOrNull(f.lat), lng: numOrNull(f.lng),
    };
  }
  function nowMs(now) {
    var t = now instanceof Date ? now.getTime() : Number(now);
    return isFinite(t) && t > 0 ? Math.floor(t) : 1;
  }
  function add(entries, fields, now) {
    var list = (entries || []).slice();
    var uid = nowMs(now);
    var taken = {};
    list.forEach(function (e) { taken[e.uid] = true; });
    while (taken[uid]) uid += 1;
    list.push(Object.assign(entryOf(fields), { uid: uid, selected: list.length === 0 }));
    return list;
  }
  function update(entries, uid, fields) {
    return (entries || []).map(function (e) {
      return e.uid === uid ? Object.assign(entryOf(Object.assign({}, e, fields)), { uid: e.uid, selected: !!e.selected }) : e;
    });
  }
  function remove(entries, uid) {
    var gone = null;
    var rest = (entries || []).filter(function (e) { if (e.uid === uid) { gone = e; return false; } return true; });
    if (gone && gone.selected && rest.length) return rest.map(function (e, i) { return Object.assign({}, e, { selected: i === 0 }); });
    return rest;
  }
  function setDefault(entries, uid) {
    return (entries || []).map(function (e) { return Object.assign({}, e, { selected: e.uid === uid }); });
  }
  // The same saved details, exactly (the edit sheet's "nothing changed").
  function sameEntry(a, b) {
    return ['type', 'address', 'city', 'zipcode', 'lat', 'lng'].every(function (k) {
      var x = a[k] === undefined || a[k] === '' ? null : a[k];
      var y = b[k] === undefined || b[k] === '' ? null : b[k];
      return x === y;
    });
  }
  function duplicateOf(entries, fields, exceptUid) {
    var k = key(entryOf(fields));
    return (entries || []).filter(function (e) { return e.uid !== exceptUid && key(e) === k; })[0] || null;
  }
  function label(e) { return TYPE_LABEL[typeOf(e && e.type)]; }
  function meta(e) { return [str(e && e.city).trim(), str(e && e.zipcode).trim()].filter(Boolean).join(' · '); }
  function sessionAddresses(entries) {
    return (entries || []).map(function (e) {
      return { address: e.address, city: e.city || '', zipcode: e.zipcode || '', type: e.type || null, ...(e.label ? {label:e.label} : {}), lat: e.lat, lng: e.lng, uid: e.uid, selected: !!e.selected };
    });
  }
  function pointOf(e) {
    var D = OO.delivery;
    return e && D && typeof D.pointOf === 'function' ? D.pointOf({ lat: e.lat, lng: e.lng }) : null;
  }
  function defaultOf(entries, opts) {
    var list = (entries || []).filter(function (e) { return isObj(e) && str(e.address).trim() && (!(opts && opts.needPoint) || pointOf(e)); });
    return list.filter(function (e) { return e.selected; })[0] || list[0] || null;
  }

  var logic = {
    TYPES: TYPES, TYPE_LABEL: TYPE_LABEL, MAX: MAX,
    book: book, key: key, wire: wire, add: add, update: update, remove: remove, setDefault: setDefault,
    duplicateOf: duplicateOf, label: label, meta: meta, sessionAddresses: sessionAddresses, pointOf: pointOf, defaultOf: defaultOf,
  };

  // ---- the component ------------------------------------------------------------------------------------------
  var DONE = { add: 'Address added.', edit: 'Address updated.', remove: 'Address removed.', default: 'Default address updated.' };
  var DUPLICATE = "You've already saved this address.";
  var NEED_LINE = 'Enter an address.';

  // The raw stored book of a customer/getdata body ({status, data: CustomerData}).
  function rawBookOf(body) { return isObj(body) && isObj(body.data) ? body.data.address : null; }
  function useAlive() {
    var alive = React.useRef(true);
    React.useEffect(function () {
      alive.current = true;
      return function () { alive.current = false; };
    }, []);
    return alive;
  }

  // A save's refusal (Profile.logic.saveError) as a notice; a 401 offers "Log in again" (→ the login on /profile).
  function Refusal(props) {
    var ui = OO.ui;
    if (!props.view || !props.view.text) return null;
    function signInAgain() {
      OO.router.go(OO.router.href('profile', null, { hotel: props.hotel, query: { directLogin: 'true' } }), { replace: true });
      OO.store.logout();
    }
    return (
      <ui.InlineNotice tone="danger" icon="Info" role="alert" className="oo-addr__error">
        {props.view.text}
        {props.view.signIn ? <React.Fragment>{' '}<ui.Button variant="link" className="oo-profile__inlink" onClick={signInAgain}>Log in again</ui.Button></React.Fragment> : null}
      </ui.InlineNotice>
    );
  }

  // P3-11: the address search (OO.delivery, as on the checkout: checkout-address.jsx).
  var PICK_HINT = 'Choose your address from the suggestions so we can deliver to it.';
  var SEARCH_DOWN = "Address suggestions aren't available right now. You can still type your address.";
  var LOCATE_FAILED = "We couldn't find your location. Please type your address.";
  // Google disables the field when it rejects the Maps key (gm_authFailure) and stamps its own placeholder on it;
  // React never undoes that (its props didn't change), so it is undone here and the address can still be typed.
  function reenableField(el) {
    if (!el) return;
    try {
      el.disabled = false;
      el.removeAttribute('disabled');
      if (el.classList) el.classList.remove('gm-err-autocomplete');
      if (el.style) el.style.backgroundImage = '';
      el.setAttribute('placeholder', '');
    } catch (e) { /* a detached field: nothing to undo */ }
  }

  // The Home / Work / Other radios.
  function TypePicker(props) {
    var cx = OO.ui.cx;
    return (
      <div className="oo-addr-type" role="radiogroup" aria-label="Address type">
        {TYPES.map(function (t) {
          var on = t === props.value;
          return (
            <label key={t} className={cx('oo-addr-type__opt', on && 'is-on')}>
              <input className="oo-addr-type__input" type="radio" name="oo-addr-type" value={t} checked={on}
                disabled={props.disabled} onChange={function () { props.onChange(t); }} />
              <span className="oo-addr-type__label">{TYPE_LABEL[t]}</span>
            </label>
          );
        })}
      </div>
    );
  }

  // The add / edit sheet (no hooks: the address search's state lives in Addresses). props: { form, busy, refusal,
  // hotel, places, search (null | { kind, text }), locating, fieldRef, onChange(patch), onLocate(), onSubmit(),
  // onClose() }. With places (P3-11): the field carries the Places suggestions (attached by Addresses through
  // fieldRef), Enter in it never submits (it picks a suggestion), and "Use my current location" sits under it while
  // the search works.
  function AddressSheet(props) {
    var ui = OO.ui;
    var f = props.form;
    var editing = f.mode === 'edit';
    var places = !!props.places;
    var note = props.search || null;
    var down = !!note && (note.kind === 'auth' || note.kind === 'disabled' || note.kind === 'unavailable');
    var point = OO.delivery && OO.delivery.pointOf ? OO.delivery.pointOf({ lat: f.lat, lng: f.lng }) : null;
    var hint = note ? note.text : (places && String(f.address || '').trim() && !point ? PICK_HINT : undefined);
    var footer = (
      <div className="oo-addr-sheet__actions">
        <ui.Button block busy={props.busy} onClick={props.onSubmit}>{editing ? 'Save address' : 'Add address'}</ui.Button>
        <ui.Button variant="ghost" block disabled={props.busy} onClick={props.onClose}>Cancel</ui.Button>
      </div>
    );
    return (
      <ui.Sheet open variant="dialog" title={editing ? 'Edit address' : 'Add an address'} onClose={props.onClose} footer={footer}
        className="oo-addr-sheet">
        <form className="oo-addr-form" noValidate onSubmit={function (e) { e.preventDefault(); props.onSubmit(); }}>
          <TypePicker value={f.type} disabled={props.busy} onChange={function (t) { props.onChange({ type: t }); }} />
          <div className="oo-addr-form__field">
            <ui.TextInput label="Address" labelVisible name="address" autoComplete="street-address" required value={f.address}
              inputRef={props.fieldRef} error={f.error || undefined} hint={f.error ? undefined : hint}
              onKeyDown={places ? function (e) { if (e.key === 'Enter') e.preventDefault(); } : undefined}
              onChange={function (v) { props.onChange({ address: v }); }} />
            {places && !down ? (
              <p className="oo-addr-form__locate">
                <ui.Button variant="link" busy={!!props.locating} disabled={props.busy} onClick={props.onLocate}>Use my current location</ui.Button>
              </p>
            ) : null}
          </div>
          <Refusal view={props.refusal} hotel={props.hotel} />
        </form>
      </ui.Sheet>
    );
  }

  // The remove confirmation.
  function RemoveSheet(props) {
    var ui = OO.ui;
    var footer = (
      <div className="oo-addr-sheet__actions">
        <ui.Button block busy={props.busy} onClick={props.onConfirm}>Remove</ui.Button>
        <ui.Button variant="ghost" block disabled={props.busy} onClick={props.onClose}>Cancel</ui.Button>
      </div>
    );
    return (
      <ui.Sheet open variant="dialog" title="Remove this address?" onClose={props.onClose} footer={footer} className="oo-addr-sheet">
        <p className="oo-addr-sheet__body">{props.entry.address + ' will be removed from your saved addresses. You can add it again at any time.'}</p>
        <Refusal view={props.refusal} hotel={props.hotel} />
      </ui.Sheet>
    );
  }

  function Row(props) {
    var ui = OO.ui;
    var e = props.entry;
    var sub = meta(e);
    return (
      <li className="oo-addr__row">
        <span className="oo-addr__icon" aria-hidden="true"><OO.icons.Pin size={20} /></span>
        <div className="oo-addr__text">
          <div className="oo-addr__pills">
            <ui.Badge size="sm">{label(e)}</ui.Badge>
            {e.selected ? <ui.Badge size="sm" tone="required">Default</ui.Badge> : null}
          </div>
          <p className="oo-addr__line">{e.address}</p>
          {sub ? <p className="oo-addr__meta">{sub}</p> : null}
          {!e.selected ? (
            <ui.Button variant="link" className="oo-addr__act" disabled={props.busy} aria-label={'Set ' + e.address + ' as your default address'}
              onClick={props.onDefault}>Set as default</ui.Button>
          ) : null}
        </div>
        <div className="oo-addr__acts">
          <ui.Button variant="link" className="oo-addr__act" disabled={props.busy} aria-label={'Edit address: ' + e.address} onClick={props.onEdit}>Edit</ui.Button>
          <ui.Button variant="link" className="oo-addr__act" disabled={props.busy} aria-label={'Remove address: ' + e.address} onClick={props.onRemove}>Remove</ui.Button>
        </div>
      </li>
    );
  }

  OO.pages.Addresses = function Addresses(props) {
    var ui = OO.ui;
    var hotel = props.hotel;
    var session = OO.useStore(function (s) { return s.session; });
    var uid = session && session.customer && session.customer.uid ? String(session.customer.uid) : null;
    var readQ = OO.useAsync(function () { return uid ? OO.api.getCustomer(hotel, uid) : Promise.resolve(null); }, [hotel, uid]);
    var saved = React.useState(null);     // { uid, entries }: the last saved book (it wins over any read)
    var form = React.useState(null);      // the add / edit sheet: { mode, uid, type, address, city, zipcode, lat, lng, error }
    var removing = React.useState(null);  // the entry the remove confirmation is about
    var busy = React.useState(false);
    var refusal = React.useState(null);   // { where: 'list' | 'sheet' | 'remove', view }
    var inflight = React.useRef(false);
    var addressRef = React.useRef(null);
    var alive = useAlive();
    // P3-11: the address search. Places on a real store with the boot's Maps key (a fixture store keeps a plain field,
    // as the checkout does), attached to the open sheet's field (a stable callback ref: mounted → attached, gone →
    // detached).
    var fieldEl = React.useState(null);
    var search = React.useState(null);    // the search's trouble: null | { kind: 'noGeometry' | 'locate' | 'auth' | 'disabled' | 'unavailable', text }
    var locating = React.useState(false); // "Use my current location" in flight
    var places = !!(OO.delivery && typeof OO.delivery.enabled === 'function' && OO.delivery.enabled())
      && !(OO.api && typeof OO.api.isFixtureFor === 'function' && OO.api.isFixtureFor(hotel));
    var country = places ? OO.delivery.country(props.store) : '';
    var fieldRef = React.useCallback(function (el) {
      addressRef.current = el;
      fieldEl[1](function (cur) { return cur === el ? cur : el; });
    }, []);
    React.useEffect(function () {
      var el = fieldEl[0];
      if (!places || !el) return undefined;
      return OO.delivery.attach(el, {
        country: country,
        onPlace: function (a) { if (!alive.current) return; search[1](null); pickForm(a); },
        onError: function (err) {
          if (!alive.current) return;
          var e = err || {};
          if (e.code === 'noGeometry') { search[1]({ kind: 'noGeometry', text: PICK_HINT }); return; }
          if (e.auth) reenableField(el);
          search[1]({ kind: e.auth ? 'auth' : (e.kind === 'disabled' ? 'disabled' : 'unavailable'), text: SEARCH_DOWN });
        },
      });
    }, [places, country, fieldEl[0]]);
    if (!uid) return null;

    var entries = saved[0] && saved[0].uid === uid ? saved[0].entries : (readQ.data ? book(rawBookOf(readQ.data)) : null);
    function refusalAt(where) { return refusal[0] && refusal[0].where === where ? refusal[0].view : null; }
    // "Use my current location" (OO.delivery.locate): the device's position, reverse-geocoded, is the pick.
    function locate() {
      if (locating[0]) return;
      locating[1](true);
      search[1](null);
      OO.delivery.locate().then(function (a) {
        if (!alive.current) return;
        locating[1](false);
        pickForm(a);
      }, function (err) {
        if (!alive.current) return;
        locating[1](false);
        search[1]({ kind: 'locate', text: (err && err.message) || LOCATE_FAILED });
      });
    }

    // Every change: the whole book through B16; the list follows only once it is saved.
    function save(next, done, where, after) {
      if (inflight.current) return;
      inflight.current = true;
      busy[1](true);
      refusal[1](null);
      OO.pages.Profile.saveProfile(hotel, { address: wire(next) }).then(function (res) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        saved[1]({ uid: uid, entries: book(rawBookOf(res.body)) });
        if (after) after();
        if (props.onSaved) props.onSaved(DONE[done]);
      }, function (err) {
        inflight.current = false;
        if (!alive.current) return;
        busy[1](false);
        var view = OO.pages.Profile.logic.saveError(err);
        var onLine = view.fields && view.fields.address;
        if (onLine && where === 'sheet') {
          form[1](function (f) { return f ? Object.assign({}, f, { error: onLine }) : f; }); // a 422 on the address: its field says it
          view = Object.assign({}, view, { text: null });
        } else if (onLine && !view.text) view = Object.assign({}, view, { text: onLine });
        refusal[1]({ where: where, view: view });
      });
    }

    function openAdd() {
      refusal[1](null);
      search[1](null);
      form[1]({ mode: 'add', uid: null, type: 'HOME', address: '', city: '', zipcode: null, lat: null, lng: null, error: null });
    }
    function openEdit(e) {
      refusal[1](null);
      search[1](null);
      form[1]({ mode: 'edit', uid: e.uid, type: e.type, address: e.address, city: e.city, zipcode: e.zipcode, lat: e.lat, lng: e.lng, error: null, orig: e });
    }
    function closeForm() { if (!inflight.current) { form[1](null); refusal[1](null); } }
    // Typing a new line drops what described the old one (REF handleAddressLineChange); typing an edited address's
    // own line again brings its details back.
    function changeForm(patch) {
      var n = search[0];
      if (own(patch, 'address') && n && (n.kind === 'noGeometry' || n.kind === 'locate')) search[1](null);
      form[1](function (f) {
        if (!f) return f;
        var next = Object.assign({}, f, patch, { error: null });
        if (own(patch, 'address')) {
          var back = f.orig && patch.address === f.orig.address ? f.orig : null;
          next.city = back ? back.city : '';
          next.zipcode = back ? back.zipcode : null;
          next.lat = back ? back.lat : null;
          next.lng = back ? back.lng : null;
        }
        return next;
      });
    }
    // P3-11: a Places pick or the device's location: the line with its city, postcode and coordinates.
    function pickForm(a) {
      var p = a || {};
      form[1](function (f) {
        if (!f) return f;
        return Object.assign({}, f, {
          address: String(p.line || '').trim() || f.address, city: String(p.city || '').trim(), zipcode: String(p.postcode || '').trim() || null,
          lat: typeof p.lat === 'number' ? p.lat : null, lng: typeof p.lng === 'number' ? p.lng : null, error: null,
        });
      });
    }
    function submitForm() {
      var f = form[0];
      if (!f || inflight.current) return;
      var fields = { type: f.type, address: f.address, city: f.city, zipcode: f.zipcode, lat: f.lat, lng: f.lng };
      var err = !String(f.address || '').trim() ? NEED_LINE : (duplicateOf(entries, fields, f.mode === 'edit' ? f.uid : null) ? DUPLICATE : null);
      if (err) {
        form[1](Object.assign({}, f, { error: err }));
        var el = addressRef.current;
        if (el && el.focus) el.focus();
        return;
      }
      if (f.mode === 'edit' && f.orig && sameEntry(entryOf(fields), f.orig)) {
        closeForm(); // nothing changed: nothing to save
        return;
      }
      var next = f.mode === 'edit' ? update(entries, f.uid, fields) : add(entries, fields, OO.data.now());
      save(next, f.mode, 'sheet', function () { form[1](null); });
    }
    function confirmRemove() {
      var e = removing[0];
      if (!e) return;
      save(remove(entries, e.uid), 'remove', 'remove', function () { removing[1](null); });
    }

    var head = (
      <div className="oo-addr__head">
        <h3 className="oo-addr__title" id="oo-addr-title">Saved addresses</h3>
        {entries ? (
          <ui.Button variant="link" className="oo-addr__add" icon="Plus" iconSize={16} disabled={busy[0] || entries.length >= MAX}
            onClick={openAdd}>Add address</ui.Button>
        ) : null}
      </div>
    );
    var body;
    if (!entries && readQ.error) {
      body = (
        <div className="oo-addr__state" role="alert">
          <p className="oo-addr__state-title">We couldn't load your addresses</p>
          <p className="oo-addr__state-body">Check your connection and try again.</p>
          <ui.Button variant="outline" size="sm" onClick={readQ.reload}>Try again</ui.Button>
        </div>
      );
    } else if (!entries) {
      body = <div aria-busy="true"><span className="oo-sr-only" role="status">Loading your addresses</span><ui.Skeleton lines={2} /></div>;
    } else if (!entries.length) {
      body = (
        <div className="oo-addr__state">
          <p className="oo-addr__state-title">No saved addresses</p>
          <p className="oo-addr__state-body">Add an address for faster checkout.</p>
        </div>
      );
    } else {
      body = (
        <ul className="oo-addr__list" aria-labelledby="oo-addr-title">
          {entries.map(function (e) {
            return (
              <Row key={e.uid} entry={e} busy={busy[0]}
                onDefault={function () { save(setDefault(entries, e.uid), 'default', 'list'); }}
                onEdit={function () { openEdit(e); }}
                onRemove={function () { refusal[1](null); removing[1](e); }} />
            );
          })}
        </ul>
      );
    }
    return (
      <ui.Card className="oo-addr" aria-labelledby="oo-addr-title" role="region">
        {head}
        <Refusal view={refusalAt('list')} hotel={hotel} />
        {body}
        {entries && entries.length >= MAX ? <p className="oo-addr__note">{'You can save up to ' + MAX + ' addresses.'}</p> : null}
        {form[0] ? (
          <AddressSheet form={form[0]} busy={busy[0]} refusal={refusalAt('sheet')} hotel={hotel} places={places} search={search[0]}
            locating={locating[0]} fieldRef={fieldRef} onChange={changeForm} onLocate={locate} onSubmit={submitForm} onClose={closeForm} />
        ) : null}
        {removing[0] ? (
          <RemoveSheet entry={removing[0]} busy={busy[0]} refusal={refusalAt('remove')} hotel={hotel}
            onConfirm={confirmRemove} onClose={function () { if (!inflight.current) { removing[1](null); refusal[1](null); } }} />
        ) : null}
      </ui.Card>
    );
  };

  OO.pages.Addresses.logic = logic;
})();


// ===================== src/pages/about.jsx =====================
// src/pages/about.jsx: OO.pages.About, the store's about page (Batch 2 P3-22; REF OnlineOrderAboutPage, C §1.4):
// /{hotel}/about, in both modes (D-18; page id 'about', routed by P3-01). It stays at a table (D-20; REF's table side
// keeps its About page) without the extras: no booking button, rewards, other locations, ready times or delivery zones
// there, and the table's own (dine-in) payment methods; its back button goes to the table's menu. No map (D-27):
// directions open the store's map link, else a Google Maps search. Not in the design: a page in its system (PageHeader,
// canvas, white cards; the account pages' kit OO.pages.acct). Styles: styles/about.css.
//
// Sections (REF's; each only when it has something to show), in reading order; on desktop the cards sit in two columns
// (about, hours, contact, delivery | booking, services, payments, rewards, policies), closures and locations below:
//   hero       the store's logo, name, address, today's opening status (OO.schedule.openStatus, OPENING; the reason when
//              closed) and its rating badge while review_settings show it (OO.ui.useStoreRating, E28, P3-07); quick
//              actions Directions · Call · Website.
//   update     the store alert's text (is_alert; tags stripped), as REF's "Store update".
//   about      the description (about_us is never sent), the cuisines, and how soon an order is usually ready per order
//              type (OO.schedule.pickupOptions(…).asapLabel: the buffers checkout uses).
//   hours      a tab per order type with hours of its own (Opening; Pickup / Delivery when the store offers them), that
//              type's status now (OO.schedule.openStatus(store, now, type)), then the 7 days from today in the store's time
//              zone: holidays closed with their label, overlapping shifts as one, a shift past midnight ending the next
//              morning, a 24-hour day as "Open 24 hours". On a phone: today, plus "View the full week". The rows are
//              OO.schedule.week's (P3-FIX #30): each day's hours at that day's own UTC offset, as the open status and
//              checkout's times read them, so in a week that crosses a DST change the days after it show their own
//              wall-clock hours (Phase 1 and REF shift every day by the load-time offset: those days an hour out).
//   contact    address (+ Get directions), phone (tel:), website. getRestaurant sends no email and no social links (REF
//              shows none either): the website is the store's only web link.
//   delivery   the delivery zones (minimum order, fee or "Free") while the store delivers.
//   booking    "Book a table" → /{hotel}/booking while the store takes bookings (store.booking), not at a table.
//   services   Pickup, Delivery, Dine-in, Table booking, Order ahead (the order-later window, OO.schedule.window).
//   payments   per order type, the choices checkout offers (OO.pages.CheckoutSections.payment.choices over
//              OO.pages.Cart.shared.methodsFor): never something checkout won't take. At a table: the dine-in ones.
//   rewards    a link to /{hotel}/rewards (the loyalty lane owns its rates), not at a table.
//   policies   OO.pages.Legal.logic.policies (src/pages/legal.jsx): a web address opens in a new tab, the others open the
//              legal pages (terms of use: the terms page's section).
//   closures   the scheduled closures that haven't ended, in the store's time zone, with their scope.
//   locations  the store's related_branches without itself → each branch's menu (name and address only, as REF), not at
//              a table.
// Data: the Store (OO.useHotel, no menu) and, for what the Store doesn't carry yet, the cached getRestaurant body
// (OO.api.getRestaurant: the read useHotel made, so no request; OO.ui.useRatingRead): cuisines, a website sent without a
// scheme (the captured live stores send "www.…"; Store.website keeps http(s) only), terms_of_use. now = OO.data.now()
// (the fixture clock on the sample stores).
//
// OO.pages.About.logic (pure; tests/about-p322.test.mjs). now: a Date | epoch ms.
//   directionsUrl(store) → store.mapLink, else a Google Maps search of lat,lng, else of "name, address"; null without any.
//   websiteOf(store, raw) → { href, label } | null: Store.website, else the body's website as a web address
//       (OO.pages.Legal.logic.policyUrl: https added; anything else is no link). label: without scheme or trailing '/'.
//   cuisinesOf(raw) → [name]: the body's cuisines (data[0]), trimmed, unique, deleted ones and "null" left out.
//   hourTabs(store) → [{ type, label }]: OPENING with hours; PICKUP / DELIVERY offered (store.orderTypes) with their own.
//   weekRows(store, type, now) → 7 × { ymd, day ('Wednesday'), today, holiday (its label | ''), closed, ranges }: the
//       days of OO.schedule.week(store, type, now) (per-day offsets; the type's hours as the engine reads them).
//   dayShifts(list) → the day's shifts by start, overlapping or touching ones merged.
//   rangeText({ start, end }) → '11:00am – 10:00pm' (local minutes; end past 1440 = the next morning) | 'Open 24 hours'.
//   readyTimes(store, now) → [{ type, label, text ('15–20 mins') }] for the offered PICKUP / DELIVERY.
//   servicesOf(store) → ['Pickup', 'Delivery', 'Dine-in', 'Table booking', 'Order ahead'] (those that apply).
//   zonesOf(store) → [{ id, name, minimum, fee }] while DELIVERY is offered.
//   paymentGroups(store, { table, fixture }) → [{ type, label, items: [label] }] (no group without items).
//   closuresOf(store, now) → [{ key, reason, scope, when }]: not ended, by start; when = 'Fri 25 Dec, 9:00am – 5:00pm',
//       across days 'Fri 25 Dec, 9:00am – Sat 26 Dec, 5:00pm', already started 'Until Sat 26 Dec, 5:00pm'.
//   branchesOf(store) → [{ key, name, address }]: related_branches with a key, not this store, once each.
//   alertText(store) → the store alert's text ('' when off).
// OO.pages.About.parts (render-time, for the tests): Hours { store, tabs (hourTabs), now } (the hours card: the type tabs,
//   the status, the week; its own state), Section { name, title, end, children } (a card with an h2 heading).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var TYPE_LABEL = { OPENING: 'Opening', PICKUP: 'Pickup', DELIVERY: 'Delivery', DINEIN: 'Dine-in' };
  var SCOPE = { ONLINE_ORDER: 'Online orders', PICKUP: 'Pickup', DELIVERY: 'Delivery', TABLE_RESERVATION: 'Table bookings', TABLE_SIDE: 'Table orders', KIOSK: 'Kiosk' };
  var MAPS = 'https://www.google.com/maps/search/?api=1&query=';

  function str(v) { return v === null || v === undefined ? '' : String(v); }
  function clean(v) { var s = str(v).trim(); var l = s.toLowerCase(); return l === 'null' || l === 'undefined' ? '' : s; }
  function arr(v) { return Array.isArray(v) ? v : []; }
  function ms(now) { return now instanceof Date ? now.getTime() : Number(now); }
  function offers(store, type) { return arr(store && store.orderTypes).indexOf(type) >= 0; }
  function hasHours(map) { return !!map && Object.keys(map).some(function (k) { return arr(map[k]).length > 0; }); }
  function legal() { return (OO.pages.Legal && OO.pages.Legal.logic) || null; }
  // getRestaurant's store row: data[0], else data.restaurant (OO.model's storeRow).
  function rowOf(raw) {
    var data = raw && typeof raw === 'object' ? raw.data : null;
    var d = (Array.isArray(data) ? data[0] : null) || (data && typeof data === 'object' && !Array.isArray(data) ? data.restaurant : null);
    return d && typeof d === 'object' && !Array.isArray(d) ? d : {};
  }

  // ---- logic (the header) -----------------------------------------------------------------------------------------
  function directionsUrl(store) {
    if (!store) return null;
    if (store.mapLink) return store.mapLink;
    var lat = store.lat, lng = store.lng;
    if (typeof lat === 'number' && typeof lng === 'number' && isFinite(lat) && isFinite(lng) && !(lat === 0 && lng === 0)) return MAPS + lat + ',' + lng;
    var addr = clean(store.address && (store.address.full || store.address.short));
    return addr ? MAPS + encodeURIComponent((clean(store.name) ? clean(store.name) + ', ' : '') + addr) : null;
  }
  function websiteOf(store, raw) {
    var L = legal();
    var href = store && store.website ? store.website : (L ? L.policyUrl(rowOf(raw).website) : null);
    if (!href) return null;
    return { href: href, label: str(href).replace(/^https?:\/\//i, '').replace(/\/+$/, '') };
  }
  function cuisinesOf(raw) {
    var seen = {}, out = [];
    arr(rowOf(raw).cuisines).forEach(function (c) {
      if (!c || typeof c !== 'object' || Number(c.is_delete) === 1) return;
      var name = clean(c.name);
      if (!name || seen[name.toLowerCase()]) return;
      seen[name.toLowerCase()] = true;
      out.push(name);
    });
    return out;
  }
  function hourTabs(store) {
    var h = (store && store.hours) || {};
    var out = [];
    if (hasHours(h.OPENING)) out.push({ type: 'OPENING', label: TYPE_LABEL.OPENING });
    ['PICKUP', 'DELIVERY'].forEach(function (t) { if (offers(store, t) && hasHours(h[t])) out.push({ type: t, label: TYPE_LABEL[t] }); });
    return out;
  }
  function rangeText(iv) {
    var f = OO.format;
    if (iv.end - iv.start >= 1440) return 'Open 24 hours';
    return f.time12(iv.start) + ' – ' + f.time12(iv.end);
  }
  // A day's shifts by start, overlapping or touching ones shown as one (10am–4pm + 10am–11pm = 10am–11pm).
  function dayShifts(list) {
    var out = [];
    arr(list).filter(function (iv) { return iv && isFinite(iv.start) && isFinite(iv.end); }).slice()
      .sort(function (a, b) { return a.start - b.start || a.end - b.end; }).forEach(function (iv) {
        var last = out[out.length - 1];
        if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end);
        else out.push({ start: iv.start, end: iv.end });
      });
    return out;
  }
  // P3-FIX #30: the days of OO.schedule.week (each at its own UTC offset), worded.
  function weekRows(store, type, now) {
    return OO.schedule.week(store, type, now).map(function (d) {
      var shifts = dayShifts(d.shifts);
      return {
        ymd: d.ymd, day: DAYS[d.dow], today: d.today, holiday: d.holiday ? clean(d.holidayLabel) || 'Holiday' : '',
        closed: !shifts.length, ranges: shifts.map(rangeText),
      };
    });
  }
  function readyTimes(store, now) {
    var S = OO.schedule;
    if (!store || !S || typeof S.pickupOptions !== 'function') return [];
    return ['PICKUP', 'DELIVERY'].filter(function (t) { return offers(store, t); }).map(function (t) {
      return { type: t, label: TYPE_LABEL[t], text: S.pickupOptions(store, now, { orderType: t, days: 1 }).asapLabel };
    });
  }
  function servicesOf(store) {
    var out = [];
    if (offers(store, 'PICKUP')) out.push('Pickup');
    if (offers(store, 'DELIVERY')) out.push('Delivery');
    if (offers(store, 'DINEIN')) out.push('Dine-in');
    if (store && store.booking) out.push('Table booking');
    var w = store && OO.schedule && typeof OO.schedule.window === 'function' ? OO.schedule.window(store) : null;
    if (w && w.enabled && !w.reason) out.push('Order ahead');
    return out;
  }
  function zonesOf(store) {
    if (!offers(store, 'DELIVERY')) return [];
    return arr(store.deliveryZones).map(function (z) {
      return { id: z.id, name: clean(z.name) || 'Delivery zone', minimum: Number(z.minimumOrder) || 0, fee: Number(z.fee) || 0 };
    });
  }
  function paymentGroups(store, opts) {
    opts = opts || {};
    var K = OO.pages.Cart && OO.pages.Cart.shared;
    var P = OO.pages.CheckoutSections && OO.pages.CheckoutSections.payment;
    if (!store || !K || !P || typeof K.methodsFor !== 'function' || typeof P.choices !== 'function') return [];
    var fixture = !!opts.fixture;
    var types = opts.table ? ['DINEIN'] : ['PICKUP', 'DELIVERY'].filter(function (t) { return offers(store, t); });
    return types.map(function (type) {
      var methods = typeof P.usableMethods === 'function' ? P.usableMethods(K.methodsFor(store, type), fixture) : K.methodsFor(store, type);
      var seen = {}, items = [];
      P.choices(methods, type, fixture).forEach(function (c) {
        var label = clean(c && c.label);
        if (!label || seen[label.toLowerCase()]) return;
        seen[label.toLowerCase()] = true;
        items.push(label);
      });
      return { type: type, label: TYPE_LABEL[type], items: items };
    }).filter(function (g) { return g.items.length > 0; });
  }
  function whenText(t, tz) {
    var f = OO.format;
    var z = f.zoned(new Date(t), tz);
    return f.weekdayShort(z.dow) + ' ' + f.shortDate(z.ymd) + ', ' + f.time12(z.minutes);
  }
  function closuresOf(store, now) {
    var f = OO.format;
    var t = ms(now);
    var tz = store && store.timezone;
    return arr(store && store.closures).filter(function (c) {
      return c && typeof c.end === 'number' && isFinite(c.end) && c.end > t;
    }).slice().sort(function (a, b) { return (a.start || 0) - (b.start || 0); }).map(function (c, i) {
      var started = !(typeof c.start === 'number' && c.start > t);
      var when;
      if (started) when = 'Until ' + whenText(c.end, tz);
      else {
        var sameDay = f.zoned(new Date(c.start), tz).ymd === f.zoned(new Date(c.end), tz).ymd;
        when = whenText(c.start, tz) + ' – ' + (sameDay ? f.time12(f.zoned(new Date(c.end), tz).minutes) : whenText(c.end, tz));
      }
      var d = str(c.domain).toUpperCase();
      return { key: (c.start || 0) + '-' + c.end + '-' + i, reason: clean(c.reason) || 'Temporarily closed', scope: SCOPE[d] || '', when: when };
    });
  }
  function branchesOf(store) {
    var me = str(store && store.onlineKey);
    var seen = {};
    return arr(store && store.relatedBranches).filter(function (b) {
      var k = clean(b && b.onlineKey);
      if (!k || k === me || seen[k]) return false;
      seen[k] = true;
      return true;
    }).map(function (b) { return { key: clean(b.onlineKey), name: clean(b.name) || 'Branch', address: clean(b.addressFull) }; });
  }
  function alertText(store) {
    var a = store && store.alert;
    return a && a.enabled && a.text ? OO.format.stripHtml(a.text) : '';
  }

  // ---- parts ------------------------------------------------------------------------------------------------------
  // A globe (not in the UI kit), drawn in its 24px / 2px-stroke language.
  function Globe(props) {
    var size = props.size || 18;
    return (
      <svg className="oo-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="9.5" /><path d="M2.5 12h19M12 2.5c2.6 2.6 3.9 5.8 3.9 9.5s-1.3 6.9-3.9 9.5c-2.6-2.6-3.9-5.8-3.9-9.5s1.3-6.9 3.9-9.5z" />
      </svg>
    );
  }

  // A white card with a heading (h2 + id for aria-labelledby) and an optional node at the heading's end.
  function Section(props) {
    var ui = OO.ui;
    var id = 'oo-about-' + props.name;
    return (
      <ui.Card as="section" className={ui.cx('oo-about__card', 'oo-about__card--' + props.name)} aria-labelledby={id}>
        <div className="oo-about__head">
          <h2 className="oo-acct__cardtitle" id={id}>{props.title}</h2>
          {props.end || null}
        </div>
        {props.children}
      </ui.Card>
    );
  }

  function Hours(props) {
    var ui = OO.ui;
    var store = props.store, tabs = props.tabs, now = props.now;
    var tabS = React.useState(tabs[0].type);
    var weekS = React.useState(false);
    var tab = tabs.filter(function (t) { return t.type === tabS[0]; })[0] || tabs[0];
    var status = OO.schedule.openStatus(store, now, tab.type);
    var rows = weekRows(store, tab.type, now);
    var open = weekS[0];
    var picker = tabs.length > 1 ? (
      <div className="oo-about__tabs" role="group" aria-label="Hours for">
        {tabs.map(function (t) {
          return <ui.Chip key={t.type} active={t.type === tab.type} onClick={function () { tabS[1](t.type); }}>{t.label}</ui.Chip>;
        })}
      </div>
    ) : null;
    return (
      <Section name="hours" title="Opening hours" end={picker}>
        <div className="oo-about__status">
          <ui.StatusPill open={status.isOpen}>{(tabs.length > 1 ? tab.label + ': ' : '') + status.label}</ui.StatusPill>
        </div>
        <ul className={ui.cx('oo-about__week', open && 'is-open')} id="oo-about-week">
          {rows.map(function (r) {
            return (
              <li key={r.ymd} className={ui.cx('oo-about__day', r.today && 'is-today', r.closed && 'is-closed')}>
                <span className="oo-about__dayname">
                  {r.day}{r.today ? <span className="oo-about__today">Today</span> : null}
                </span>
                <span className="oo-about__times">
                  {r.closed ? <span>{r.holiday ? 'Closed · ' + r.holiday : 'Closed'}</span>
                    : r.ranges.map(function (x, i) { return <span key={i}>{x}</span>; })}
                </span>
              </li>
            );
          })}
        </ul>
        <button type="button" className="oo-about__weektoggle" aria-expanded={open ? 'true' : 'false'} aria-controls="oo-about-week"
          onClick={function () { weekS[1](!open); }}>
          {open ? 'Hide the full week' : 'View the full week'}
          <OO.icons.ChevronDown size={16} className={open ? 'is-flipped' : undefined} />
        </button>
      </Section>
    );
  }

  OO.pages.About = function About(props) {
    var route = props.route || {};
    var mode = props.mode;
    var hotel = route.hotel;
    var acct = OO.pages.acct;
    var ui = OO.ui;
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var table = OO.useStore(function (s) { return s.table; });
    var raw = ui.useRatingRead(store && hotel ? 'about|' + hotel : null, function () { return OO.api.getRestaurant(hotel); });
    var rating = ui.useStoreRating(hotel, store);

    // D-20: a table applies (the URL's ?table_id, else this store's table context) → the table's menu, as for booking.
    var toTable = OO.router.tableGate({ hotel: hotel, page: 'booking', params: {}, query: route.query || {} }, table);
    var atTable = !!toTable;
    var back = toTable || OO.router.href(mode === 'app' ? 'home' : 'menu', null, { hotel: hotel });
    var header = acct.headerProps({ hotel: hotel, mode: mode, back: back, profile: true });
    var blocked = acct.gate(h, { title: 'About', header: header });
    if (blocked) return blocked;

    var now = OO.data && typeof OO.data.now === 'function' ? OO.data.now() : new Date();
    var fixture = !!(OO.api && typeof OO.api.isFixtureFor === 'function' && OO.api.isFixtureFor(hotel));
    var address = clean(store.address && (store.address.full || store.address.short));
    var status = OO.schedule.openStatus(store, now);
    var statusText = status.label + (!status.isOpen && clean(status.reason) ? ' · ' + clean(status.reason) : '');
    var dirs = directionsUrl(store);
    var phone = clean(store.phone);
    var tel = phone ? 'tel:' + phone.replace(/[^\d+]/g, '') : null;
    var site = websiteOf(store, raw);
    var update = alertText(store);
    var cuisines = cuisinesOf(raw);
    var ready = atTable ? [] : readyTimes(store, now); // pickup / delivery facts: not at a table (REF's table About)
    var tabs = hourTabs(store);
    var services = servicesOf(store);
    var zones = atTable ? [] : zonesOf(store);
    var payments = paymentGroups(store, { table: atTable, fixture: fixture });
    var L = legal();
    var policies = L ? L.policies(store, L.useOf(store, raw)) : [];
    var closures = closuresOf(store, now);
    var branches = atTable ? [] : branchesOf(store);
    var booking = store.booking && !atTable ? OO.router.link('booking', null, { hotel: hotel }) : null;
    var rewards = atTable ? null : OO.router.link('rewards', null, { hotel: hotel });
    var description = clean(store.description);
    var NEW_TAB = { target: '_blank', rel: 'noopener noreferrer' };

    var actions = [];
    if (dirs) actions.push(<ui.Button key="d" variant="outline" size="sm" icon="Pin" href={dirs} {...NEW_TAB}>Directions</ui.Button>);
    if (tel) actions.push(<ui.Button key="c" variant="outline" size="sm" icon={<acct.icons.Phone size={18} />} href={tel}>Call</ui.Button>);
    if (site) actions.push(<ui.Button key="w" variant="outline" size="sm" icon={<Globe size={18} />} href={site.href} {...NEW_TAB}>Website</ui.Button>);

    var hero = (
      <ui.Card className="oo-about__hero">
        <div className="oo-about__id">
          {store.logoUrl ? <img className="oo-about__logo" src={store.logoUrl} alt="" /> : null}
          <div className="oo-about__idtext">
            <h2 className="oo-about__name">{store.name}</h2>
            {address ? <p className="oo-about__addr">{address}</p> : null}
            <div className="oo-about__badges">
              <ui.StatusPill open={status.isOpen}>{statusText}</ui.StatusPill>
              {rating ? <ui.RatingBadge average={rating.average} total={rating.total} /> : null}
            </div>
          </div>
        </div>
        {actions.length ? <div className="oo-about__actions">{actions}</div> : null}
      </ui.Card>
    );

    var main = [];
    if (update) {
      main.push(
        <Section key="update" name="update" title="Store update">
          <p className="oo-about__text">{update}</p>
        </Section>
      );
    }
    if (description || cuisines.length || ready.length) {
      main.push(
        <Section key="about" name="about" title={'About ' + store.name}>
          {description ? <p className="oo-about__text">{description}</p> : null}
          {cuisines.length || ready.length ? (
            <ul className="oo-about__chips" aria-label="Food and ready times">
              {cuisines.map(function (c) { return <li key={'c' + c} className="oo-about__chip">{c}</li>; })}
              {ready.map(function (r) {
                return <li key={'r' + r.type} className="oo-about__chip"><OO.icons.Clock size={14} />{r.label + ' in about ' + r.text}</li>;
              })}
            </ul>
          ) : null}
        </Section>
      );
    }
    if (tabs.length) main.push(<Hours key="hours" store={store} tabs={tabs} now={now} />);
    if (address || phone || site) {
      main.push(
        <Section key="contact" name="contact" title="Contact and location">
          <dl className="oo-acct__dl">
            {address ? (
              <div className="oo-acct__dlrow">
                <dt>Address</dt>
                <dd>
                  <span>{address}</span>
                  {dirs ? <a className="oo-about__link" href={dirs} {...NEW_TAB}>Get directions<span className="oo-sr-only"> (opens in a new tab)</span></a> : null}
                </dd>
              </div>
            ) : null}
            {phone ? (
              <div className="oo-acct__dlrow"><dt>Phone</dt><dd><a className="oo-about__link" href={tel}>{phone}</a></dd></div>
            ) : null}
            {site ? (
              <div className="oo-acct__dlrow">
                <dt>Website</dt>
                <dd><a className="oo-about__link" href={site.href} {...NEW_TAB}>{site.label}<span className="oo-sr-only"> (opens in a new tab)</span></a></dd>
              </div>
            ) : null}
          </dl>
        </Section>
      );
    }
    if (zones.length) {
      main.push(
        <Section key="delivery" name="delivery" title="Delivery fees and minimums">
          <ul className="oo-about__rows">
            {zones.map(function (z, i) {
              return (
                <li key={str(z.id) + i} className="oo-about__row">
                  <span className="oo-about__rowmain">
                    <span className="oo-about__rowtitle">{z.name}</span>
                    <span className="oo-about__rowsub">{'Minimum order ' + acct.money(z.minimum, store)}</span>
                  </span>
                  <span className="oo-about__rowend">{z.fee > 0 ? acct.money(z.fee, store) : 'Free'}</span>
                </li>
              );
            })}
          </ul>
          <p className="oo-acct__muted">Your delivery fee is confirmed at checkout, from your address and order total.</p>
        </Section>
      );
    }

    var side = [];
    if (booking) {
      side.push(
        <Section key="booking" name="booking" title="Book a table">
          <p className="oo-acct__muted">Choose a date, time and party size for your visit.</p>
          <ui.Button size="md" icon="Calendar" href={booking.href} onClick={booking.onClick}>Book a table</ui.Button>
        </Section>
      );
    }
    if (services.length) {
      side.push(
        <Section key="services" name="services" title="Available here">
          <ul className="oo-about__chips">
            {services.map(function (s) { return <li key={s} className="oo-about__chip">{s}</li>; })}
          </ul>
        </Section>
      );
    }
    side.push(
      <Section key="payments" name="payments" title="Payment methods">
        {payments.length ? (
          <ul className="oo-about__rows">
            {payments.map(function (g) {
              return (
                <li key={g.type} className="oo-about__row oo-about__row--top">
                  <span className="oo-about__rowtitle">{g.label}</span>
                  <ul className="oo-about__chips oo-about__chips--end" aria-label={g.label + ' payment methods'}>
                    {g.items.map(function (x) { return <li key={x} className="oo-about__chip">{x}</li>; })}
                  </ul>
                </li>
              );
            })}
          </ul>
        ) : <p className="oo-acct__muted">Your payment choices are shown when you check out.</p>}
      </Section>
    );
    if (rewards) {
      side.push(
        <Section key="rewards" name="rewards" title="Rewards">
          <p className="oo-acct__muted">Earn points on every order and redeem them on your next one.</p>
          <ui.Button variant="outline" size="md" icon="Star" href={rewards.href} onClick={rewards.onClick}>See your rewards</ui.Button>
        </Section>
      );
    }
    if (policies.length) {
      side.push(
        <Section key="policies" name="policies" title="Terms and policies">
          <ul className="oo-acct__links">
            {policies.map(function (p) {
              if (p.url) {
                return (
                  <li key={p.kind}>
                    <a className="oo-acct__linkrow" href={p.url} {...NEW_TAB}>
                      <span>{p.title}<span className="oo-sr-only"> (opens in a new tab)</span></span><OO.icons.ChevronRight size={16} />
                    </a>
                  </li>
                );
              }
              var l = OO.router.link('legal', { kind: p.page }, { hotel: hotel, hash: p.hash || undefined });
              return (
                <li key={p.kind}>
                  <a className="oo-acct__linkrow" href={l.href} onClick={l.onClick}><span>{p.title}</span><OO.icons.ChevronRight size={16} /></a>
                </li>
              );
            })}
          </ul>
        </Section>
      );
    }

    var more = [];
    if (closures.length) {
      more.push(
        <Section key="closures" name="closures" title="Upcoming closures">
          <ul className="oo-about__rows">
            {closures.map(function (c) {
              return (
                <li key={c.key} className="oo-about__row oo-about__row--top">
                  <span className="oo-about__rowmain">
                    <span className="oo-about__rowtitle">{c.reason}</span>
                    {c.scope ? <span className="oo-about__rowsub">{c.scope}</span> : null}
                  </span>
                  <span className="oo-about__rowend">{c.when}</span>
                </li>
              );
            })}
          </ul>
        </Section>
      );
    }
    // WL-5: on a landing website with ordering off, a location's menu is browse-only.
    var browseOnly = !!(OO.whitelabel && typeof OO.whitelabel.browseOnly === 'function' && OO.whitelabel.browseOnly(hotel));
    if (branches.length) {
      more.push(
        <Section key="locations" name="locations" title="Other locations">
          <ul className="oo-acct__links">
            {branches.map(function (b) {
              var l = OO.router.link('menu', null, { hotel: b.key });
              return (
                <li key={b.key}>
                  <a className="oo-acct__linkrow oo-about__branch" href={l.href} onClick={l.onClick}>
                    <span className="oo-about__rowmain">
                      <span className="oo-about__rowtitle">{b.name}</span>
                      {b.address ? <span className="oo-about__rowsub">{b.address}</span> : null}
                    </span>
                    <span className="oo-about__order">{browseOnly ? 'Menu' : 'Order'}<OO.icons.ChevronRight size={16} /></span>
                  </a>
                </li>
              );
            })}
          </ul>
        </Section>
      );
    }

    return (
      <acct.Page title="About" {...header} className="oo-about">
        {hero}
        <div className="oo-about__grid">
          <div className="oo-about__col">{main}</div>
          <div className="oo-about__col">{side}</div>
        </div>
        {more.length ? <div className="oo-about__more">{more}</div> : null}
        <ui.PoweredBySipo />
      </acct.Page>
    );
  };

  OO.pages.About.logic = {
    directionsUrl: directionsUrl, websiteOf: websiteOf, cuisinesOf: cuisinesOf, hourTabs: hourTabs, weekRows: weekRows,
    dayShifts: dayShifts, rangeText: rangeText, readyTimes: readyTimes, servicesOf: servicesOf, zonesOf: zonesOf,
    paymentGroups: paymentGroups, closuresOf: closuresOf, branchesOf: branchesOf, alertText: alertText,
  };
  OO.pages.About.parts = { Hours: Hours, Section: Section };
})();


// ===================== src/pages/bookings.jsx =====================
// src/pages/bookings.jsx: OO.pages.Bookings, the customer's table bookings once made (Batch 2 P3-20 / P3-21; REF
// online-order/pages/bookings and booking/track, C §1.3), in both modes (D-18). Making a booking stays OO.pages.Booking
// (app-booking.jsx).
//   /{hotel}/booking/track          the track lookup: the reservation id and the email (P3-20). REF's unified
//                                   /{hotel}/track?mode=booking is this page too.
//   /{hotel}/booking/track/{id}     the reservation's detail with its live status (E39 table_booking_status, sub_status),
//                                   polled until it ends (P3-20).
//   /{hotel}/bookings               the signed-in history: Upcoming / All (E40 get_customer_table_bookings; P3-21).
//   /{hotel}/bookings/{id}          the same detail, signed in (P3-21).
// Page ids 'bookings' and 'booking-track', both this component (routed by P3-01): route.page tells them apart, and
// route.params.reservationId is set when the URL names one. Off at a table (D-20). Styles: styles/bookings.css. The
// pages use the account kit (OO.pages.acct: Page, headerProps, gate) like the other account pages.
//
// Privacy (B §9 Q13; D-14): table_booking_status answers whoever sends a reservation id AND its booking's email, so
// that pair is the key (REF). The email is typed, or found in this order: an incoming ?email= (REF's links and B15's
// email link; read, remembered for this tab, then dropped from the address bar with a replace), this tab's hand-off
// (OO.pages.Booking.handoff: the booking page and the lookup remember the pair), the signed-in customer's email. It is
// never written into a URL. The detail never shows the row's email or phone.
//
// Lookup (REF BookingTrackPageClient): the id (upper-cased, spaces dropped as typed, at least 6 characters) and the
// email (prefilled from ?email= or the session); on submit the pair is remembered and the page goes to
// /booking/track/{id}. It also explains the statuses and offers the store's phone.
// Detail (REF OnlineOrderBookingDetails): the first read is OO.useAsync(OO.api.getBookingStatus) → OO.model
// .normalizeReservation (store time zone). The status card (REF's steps: Requested → Confirmed → At the restaurant;
// sub_status Seated / Completed / No-show, which REF never reads), the booking's details, the reservation QR (OO.pages
// .Booking.Qr, when the encoder is there) and actions (book again, track another, call, the menu).
// Live status (a product call; REF reads it at most every 15 minutes): once the first read has the row and it isn't
// final, it is read again every POLL.ms (60 s) as a setTimeout chain (never overlapping), paused while the tab is hidden
// (document.visibilityState; it reads at once when the tab shows again), at most POLL.max reads (30: half an hour of a
// visible tab) and POLL.errorsMax failures in a row (5) per run; then "Refresh for the latest status". It stops at a
// final status (completed, no-show, declined, cancelled). The newest good answer stays on screen (a failed poll never
// blanks it). Refresh starts a new run: a new first read and a new chain. "Updated 6:02pm" tells when it was last read.
// Failures of the first read: a miss (kind 'notFound': B08's 404, a 2xx without the row, or no email / id) asks for the
// email again (it may be a typo) with "Track another booking"; this build without the call (the facade's 'unavailable'
// with no HTTP status: the fixture stores) says tracking isn't available here; anything else can be retried.
// History (P3-21; REF OnlineOrderBookingHistory), signed in only (signed out: the log-in card): OO.useAsync(OO.api
// .getCustomerBookings(hotel, { uid })) → OO.model.bookingHistory at OO.data.now() in the store's zone. Tabs Upcoming
// (soonest first, the first marked "Next up") and All (newest first), each with its count; Refresh reads again. A
// card: the date block, the relative day ('Tomorrow, Sat 3 Oct · 6:30pm'), the status, the party, the name, the
// store when it is another one of the chain (the history is chain-wide), the requests, the reservation id; "View"
// (/bookings/{id}) and, until it ends, "Track live" (/booking/track/{id}). Both are the detail above; the session's
// email reads it, so no email goes in a URL. A 400 (the API's "Customer Data Not Found!": no customer record in this
// chain yet) is an empty history; this build without the call says it isn't available here; anything else retries.
// /bookings/{id}: the detail, signed in only (REF), its back link to the history.
//
// OO.pages.Bookings.logic (pure; tests/bookings-p320.test.mjs, tests/bookings-p321.test.mjs):
//   lookupError(id, email) → null | { field: 'id' | 'email', text } (REF's checks, our words).
//   cleanId(text) → the id as the lookup keeps it: upper-cased, no whitespace.
//   queryEmail(query) → the incoming ?email= (any case of the key) when it is a valid email, else ''.
//   hasContact(query) → bool: the query holds a contact field the router never writes (email, phone, mobile,
//       customer_name, customer_email, customer_phone, address; any case), i.e. the page must replace its URL.
//   emailFor(id, incoming, customer) → the email the detail starts with (see "Privacy"), '' when none.
//   steps(reservation) → [{ key, label, state: 'done' | 'current' | 'todo' | 'failed' }] (the status card's).
//   whenText(reservation) → 'Tomorrow, Sat 3 Oct · 6:30pm' (a history card's heading; '' parts dropped).
// OO.pages.Bookings.POLL = { ms, max, errorsMax }; OO.pages.Bookings.Lookup / Detail / History: the views (tests mount
// them).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var POLL = { ms: 60000, max: 30, errorsMax: 5 };
  var MIN_ID = 6;
  var CONTACT = /^(email|phone|mobile|customer_name|customer_email|customer_phone|address)$/i;

  function str(v) { return v === null || v === undefined ? '' : String(v); }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function telOf(phone) { var p = str(phone).replace(/[^\d+]/g, ''); return p ? 'tel:' + p : null; }
  function guestsText(n) { return n + (Number(n) === 1 ? ' guest' : ' guests'); }
  function selectSession(s) { return s.session; }
  function handoff() { return OO.pages.Booking && OO.pages.Booking.handoff ? OO.pages.Booking.handoff : null; }

  // ---- pure logic (see the header) ---------------------------------------------------------------------------------
  function cleanId(text) { return str(text).toUpperCase().replace(/\s+/g, ''); }
  function lookupError(id, email) {
    var i = cleanId(id), e = str(email).trim();
    if (!i) return { field: 'id', text: 'Enter your reservation ID' };
    if (i.length < MIN_ID) return { field: 'id', text: 'That ID looks too short. Check your booking email.' };
    if (!e) return { field: 'email', text: 'Enter the email you booked with' };
    if (!OO.model.validBookingEmail(e)) return { field: 'email', text: 'That email address doesn’t look right' };
    return null;
  }
  function queryEmail(query) {
    var q = query || {};
    for (var k in q) {
      if (own(q, k) && k.toLowerCase() === 'email') {
        var v = str(q[k]).trim();
        return OO.model.validBookingEmail(v) ? v : '';
      }
    }
    return '';
  }
  function hasContact(query) {
    var q = query || {};
    for (var k in q) if (own(q, k) && CONTACT.test(k)) return true;
    return false;
  }
  function emailFor(id, incoming, customer) {
    if (incoming) return incoming;
    var H = handoff();
    var hand = H ? H.recall(id) : null;
    if (hand && hand.email) return hand.email;
    return customer ? str(customer.email).trim() : '';
  }
  // REF's steps (Requested → Accepted → Visit), with sub_status and the ways a booking ends early.
  function steps(r) {
    var s = r ? r.status : 'checking';
    var third = s === 'seated' ? 'Seated' : s === 'completed' ? 'Completed' : s === 'noshow' ? 'No-show' : 'At the restaurant';
    var second = s === 'declined' ? 'Declined' : s === 'cancelled' ? 'Cancelled' : 'Confirmed';
    var states = {
      checking: ['current', 'todo', 'todo'],
      pending: ['done', 'current', 'todo'],
      confirmed: ['done', 'done', 'current'],
      seated: ['done', 'done', 'current'],
      completed: ['done', 'done', 'done'],
      noshow: ['done', 'done', 'failed'],
      declined: ['done', 'failed', 'todo'],
      cancelled: ['done', 'failed', 'todo'],
    }[s] || ['current', 'todo', 'todo'];
    return [
      { key: 'requested', label: 'Requested', state: states[0] },
      { key: 'confirmed', label: second, state: states[1] },
      { key: 'visit', label: third, state: states[2] },
    ];
  }
  function whenText(r) {
    var day = [r.relative, r.dayLabel].filter(Boolean).join(', ');
    return [day, r.timeLabel].filter(Boolean).join(' · ');
  }

  // ---- shared bits -------------------------------------------------------------------------------------------------
  // An incoming URL with a contact field (REF's ?email=): `remember` keeps what the page needs, then the URL is
  // replaced by the same route without it (OO.router.href drops contact fields; the #hash stays). OO.App keys pages by
  // page and params, not the query, so the page's state survives the replace.
  function useDropContact(route, remember) {
    var has = hasContact(route.query);
    React.useEffect(function () {
      if (!has) return;
      if (remember) remember();
      var l = window.location;
      OO.router.go(OO.router.href(route.page, route.params, { hotel: route.hotel, query: route.query }) + ((l && l.hash) || ''), { replace: true });
    }, [has]);
  }
  function headerFor(hotel, mode, back) {
    var acct = OO.pages.acct;
    return acct.headerProps({ hotel: hotel, mode: mode, back: back, profile: true });
  }
  function homeHref(hotel, mode) { return OO.router.href(mode === 'app' ? 'home' : 'menu', null, { hotel: hotel }); }

  function StatusGuide() {
    var ui = OO.ui;
    var rows = [
      { key: 'pending', label: 'Pending', text: 'The restaurant is reviewing your request.' },
      { key: 'confirmed', label: 'Confirmed', text: 'Your table is booked.' },
      { key: 'declined', label: 'Declined', text: 'The restaurant can’t take this booking. Call them to find another time.' },
    ];
    return (
      <ui.Card className="oo-bkt-guide">
        <h2 className="oo-acct__cardtitle">What the statuses mean</h2>
        <dl className="oo-bkt-guide__list">
          {rows.map(function (r) {
            return (
              <div key={r.key} className={'oo-bkt-guide__row oo-bkt-guide__row--' + r.key}>
                <dt>{r.label}</dt>
                <dd>{r.text}</dd>
              </div>
            );
          })}
        </dl>
      </ui.Card>
    );
  }

  function CallCard(props) {
    var ui = OO.ui, store = props.store;
    var tel = telOf(store.phone);
    if (!tel) return null;
    var Phone = OO.pages.acct && OO.pages.acct.icons ? OO.pages.acct.icons.Phone : null;
    return (
      <ui.Card className="oo-bkt-call">
        <a className="oo-bkt-call__link" href={tel}>
          <span className="oo-bkt-call__icon" aria-hidden="true">{Phone ? <Phone size={20} /> : null}</span>
          <span className="oo-bkt-call__text">
            <span className="oo-bkt-call__title">{'Call ' + store.name}</span>
            <span className="oo-acct__muted">{store.phone + ' · to change or cancel a booking'}</span>
          </span>
        </a>
      </ui.Card>
    );
  }

  // ---- the lookup (P3-20) ------------------------------------------------------------------------------------------
  function Lookup(props) {
    var route = props.route, hotel = route.hotel, mode = props.mode, ui = OO.ui, acct = OO.pages.acct;
    var h = OO.useHotel(hotel, { withMenu: false });
    var session = OO.useStore(selectSession);
    var customer = session && session.customer ? session.customer : null;
    var incoming = queryEmail(route.query);
    var idS = React.useState('');
    var emailS = React.useState(function () { return incoming || (customer ? str(customer.email).trim() : ''); });
    var errS = React.useState(null);
    var idRef = React.useRef(null), emailRef = React.useRef(null);
    useDropContact(route, null);

    var header = headerFor(hotel, mode, homeHref(hotel, mode));
    var blocked = acct.gate(h, { title: 'Track a booking', header: header });
    if (blocked) return blocked;
    var store = h.store;
    var err = errS[0];
    var mine = customer ? OO.router.link('bookings', null, { hotel: hotel }) : null;

    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      var id = cleanId(idS[0]);
      var email = str(emailS[0]).trim();
      var bad = lookupError(id, email);
      errS[1](bad);
      if (bad) {
        var el = (bad.field === 'id' ? idRef : emailRef).current;
        if (el && el.focus) el.focus();
        return;
      }
      var H = handoff();
      if (H) H.remember(id, email, hotel);
      OO.router.go(OO.router.href('booking-track', { reservationId: id }, { hotel: hotel }));
    }
    return (
      <acct.Page title="Track a booking" subtitle={store.name} {...header} className="oo-bkt">
        <ui.Card className="oo-bkt-form">
          <form className="oo-bkt-form__in" onSubmit={submit} noValidate>
            <h2 className="oo-acct__cardtitle">Find your booking</h2>
            <p className="oo-acct__muted">Enter the reservation ID from your booking request and the email you booked with.</p>
            <ui.TextInput label="Reservation ID" labelVisible value={idS[0]} autoComplete="off" placeholder="e.g. TRE4017…"
              onChange={function (v) { idS[1](cleanId(v)); if (err && err.field === 'id') errS[1](null); }}
              error={err && err.field === 'id' ? err.text : undefined} inputRef={idRef} />
            <ui.TextInput label="Email" labelVisible type="email" inputMode="email" autoComplete="email" value={emailS[0]}
              onChange={function (v) { emailS[1](v); if (err && err.field === 'email') errS[1](null); }}
              error={err && err.field === 'email' ? err.text : undefined} inputRef={emailRef} />
            <ui.Button type="submit" block>Track booking</ui.Button>
            <p className="oo-acct__note">We ask for your email so only the person who booked can see the booking.</p>
          </form>
        </ui.Card>
        <StatusGuide />
        <CallCard store={store} />
        {mine ? <ui.Button variant="outline" href={mine.href} onClick={mine.onClick}>Your bookings</ui.Button> : null}
      </acct.Page>
    );
  }

  // ---- the detail, with its live status (P3-20) ----------------------------------------------------------------------
  function EmailGate(props) {
    var ui = OO.ui;
    var vS = React.useState(props.email || '');
    var errS = React.useState(null);
    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      var v = str(vS[0]).trim();
      if (!OO.model.validBookingEmail(v)) { errS[1](v ? 'That email address doesn’t look right' : 'Enter the email you booked with'); return; }
      props.onEmail(v);
    }
    return (
      <ui.Card className="oo-bkt-form">
        <form className="oo-bkt-form__in" onSubmit={submit} noValidate>
          <h2 className="oo-acct__cardtitle">{props.title || 'Confirm your booking email'}</h2>
          <p className="oo-acct__muted">{props.body || 'Enter the email you used for this booking at ' + props.store.name + '.'}</p>
          <ui.TextInput label="Email" labelVisible type="email" inputMode="email" autoComplete="email" value={vS[0]}
            onChange={function (v) { vS[1](v); if (errS[0]) errS[1](null); }} error={errS[0] || undefined} />
          <ui.Button type="submit" block>View status</ui.Button>
        </form>
      </ui.Card>
    );
  }

  function Detail(props) {
    var route = props.route, hotel = route.hotel, mode = props.mode, id = props.reservationId, ui = OO.ui, acct = OO.pages.acct;
    var h = OO.useHotel(hotel, { withMenu: false });
    var tz = h.store ? h.store.timezone : null;
    var session = OO.useStore(selectSession);
    var customer = session && session.customer ? session.customer : null;
    var incoming = queryEmail(route.query);
    var emailS = React.useState(function () { return emailFor(id, incoming, customer); });
    var email = emailS[0];
    useDropContact(route, function () { var H = handoff(); if (H && incoming) H.remember(id, incoming, hotel); });

    var runS = React.useState(0);     // Refresh: a new first read and a new polling run
    var liveS = React.useState(null); // { run, email, res, at }: this run's newest poll answer
    var stopS = React.useState(null); // { run, email }: this run stopped early (POLL.max reads or POLL.errorsMax failures)
    var run = runS[0];
    var first = OO.useAsync(function () {
      return email ? OO.api.getBookingStatus(hotel, { email: email, reservationId: id }) : Promise.resolve(null);
    }, [hotel, id, email, run]);
    var firstAt = React.useMemo(function () { return first.data ? OO.data.now().getTime() : null; }, [first.data]);
    var live = liveS[0] && liveS[0].run === run && liveS[0].email === email ? liveS[0] : null;
    var res = live ? live.res : first.data;
    var row = React.useMemo(function () { return res ? OO.model.normalizeReservation(res, { timeZone: tz }) : null; }, [res, tz]);
    var stopped = !!(stopS[0] && stopS[0].run === run && stopS[0].email === email);
    var polling = !!first.data && !first.loading && !!row && !row.terminal && !stopped;

    React.useEffect(function () {
      if (!polling) return undefined;
      var alive = true, timer = null, busy = false, reads = 0, fails = 0;
      var doc = typeof document !== 'undefined' ? document : null;
      function hidden() { return !!doc && doc.visibilityState === 'hidden'; }
      function later() { timer = setTimeout(tick, POLL.ms); }
      function stop() { stopS[1]({ run: run, email: email }); }
      function tick() {
        timer = null;
        if (!alive || busy) return;
        if (hidden()) return; // onShow reads at once when the tab is visible again
        busy = true;
        reads += 1;
        OO.api.getBookingStatus(hotel, { email: email, reservationId: id }).then(function (r) {
          busy = false;
          if (!alive) return;
          fails = 0;
          liveS[1]({ run: run, email: email, res: r, at: OO.data.now().getTime() });
          if (OO.model.normalizeReservation(r, { timeZone: tz }).terminal) return; // the effect ends with the final status
          if (reads >= POLL.max) stop(); else later();
        }, function () {
          busy = false;
          if (!alive) return;
          fails += 1;
          if (fails >= POLL.errorsMax || reads >= POLL.max) stop(); else later();
        });
      }
      function onShow() {
        if (!alive || hidden() || busy) return;
        if (timer !== null) { clearTimeout(timer); timer = null; }
        tick();
      }
      later();
      if (doc && doc.addEventListener) doc.addEventListener('visibilitychange', onShow);
      return function () {
        alive = false;
        if (timer !== null) clearTimeout(timer);
        if (doc && doc.removeEventListener) doc.removeEventListener('visibilitychange', onShow);
      };
    }, [polling, hotel, id, email, run]);

    var back = props.backTo || OO.router.href('booking-track', null, { hotel: hotel });
    var header = headerFor(hotel, mode, back);
    var blocked = acct.gate(h, { title: 'Booking status', header: header });
    if (blocked) return blocked;
    var store = h.store;

    function chooseEmail(v) {
      var H = handoff();
      if (H) H.remember(id, v, hotel);
      liveS[1](null);
      stopS[1](null);
      emailS[1](v);
    }
    function refresh() { liveS[1](null); stopS[1](null); runS[1](function (n) { return n + 1; }); }
    var another = OO.router.link('booking-track', null, { hotel: hotel });
    var page = function (children, busy) {
      return <acct.Page title="Booking status" subtitle={store.name} {...header} busy={busy} className="oo-bkd">{children}</acct.Page>;
    };

    if (!email) {
      return page(<EmailGate store={store} onEmail={chooseEmail} />);
    }
    var err = first.error;
    if (err && !row) {
      if (err.kind === 'notFound') {
        return page([
          <ui.EmptyState key="nf" className="oo-bkd-miss" icon="Calendar" role="alert" title="We couldn’t find that booking"
            body={'No booking ' + id + ' matches that email. Check the email you booked with, or the reservation ID.'} />,
          <EmailGate key="gate" store={store} email={email} onEmail={chooseEmail} title="Try another email"
            body={'The email you used for booking ' + id + '.'} />,
          <ui.Button key="another" variant="outline" href={another.href} onClick={another.onClick}>Track another booking</ui.Button>,
        ]);
      }
      var notHere = err.kind === 'unavailable' && !(Number(err.status) > 0);
      return page(
        <ui.EmptyState className="oo-bkd-miss" icon="Info" role="alert"
          title={notHere ? 'Booking tracking isn’t available here yet' : 'We couldn’t check that booking'}
          body={notHere ? 'Call ' + store.name + (store.phone ? ' on ' + store.phone : '') + ' to check on your booking.' : acct.errorText(err, 'Check your connection and try again.')}
          action={notHere ? null : <ui.Button variant="outline" size="sm" onClick={first.reload}>Try again</ui.Button>} />
      );
    }
    if (!row) {
      return page([
        <span key="sr" className="oo-sr-only" role="status">Loading</span>,
        <div key="sk" className="oo-acct__skel"><ui.Skeleton lines={2} className="oo-acct__skel-title" /><ui.Skeleton variant="block" count={2} /></div>,
      ], true);
    }

    var at = live ? live.at : firstAt;
    var clock = at !== null && at !== undefined ? OO.format.time12(OO.format.zoned(new Date(at), tz).hm) : '';
    var liveLine = row.terminal ? null : stopped ? 'Refresh for the latest status.' : clock ? 'Live · updated ' + clock : 'Live';
    var book = store.booking ? OO.router.link('booking', null, { hotel: hotel }) : null;
    var menu = OO.router.link(mode === 'app' ? 'home' : 'menu', null, { hotel: hotel });
    var tel = telOf(store.phone);
    var Qr = OO.pages.Booking && OO.pages.Booking.Qr;
    var qrOk = !!(Qr && OO.pages.Booking.logic && OO.pages.Booking.logic.qrOf(row.id || id));
    var rows = [
      { label: 'When', value: [row.dayLabel, row.timeLabel].filter(Boolean).join(' · ') || '—' },
      { label: 'Guests', value: guestsText(row.guests) },
      row.name ? { label: 'Name', value: row.name } : null,
      row.notes ? { label: 'Requests', value: row.notes } : null,
      { label: 'Reservation ID', value: row.id || id },
    ].filter(Boolean);

    return page(
      <div className="oo-acct__grid">
        <div className="oo-acct__col">
          <ui.Card className={'oo-bkd-status oo-bkd-status--' + row.tone}>
            <div className="oo-bkd-status__top">
              <span className="oo-bkd-status__id">{'Booking ' + (row.id || id)}</span>
              <ui.Badge tone={row.tone} size="sm">{row.label}</ui.Badge>
            </div>
            <div role="status" aria-live="polite" className="oo-bkd-status__now">
              <h2 className="oo-bkd-status__state">{row.label}</h2>
              <p className="oo-bkd-status__text">{row.text}</p>
            </div>
            <ol className="oo-bkd-steps">
              {steps(row).map(function (s) {
                return (
                  <li key={s.key} className={'oo-bkd-steps__step is-' + s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
                    <span className="oo-bkd-steps__dot" aria-hidden="true" />
                    <span className="oo-bkd-steps__label">{s.label}</span>
                  </li>
                );
              })}
            </ol>
            {liveLine ? (
              <div className="oo-bkd-status__live">
                <span>{liveLine}</span>
                <ui.Button variant="link" onClick={refresh}>Refresh</ui.Button>
              </div>
            ) : null}
          </ui.Card>
          <ui.Card className="oo-bkd-info">
            <h2 className="oo-acct__cardtitle">Your booking</h2>
            <dl className="oo-acct__dl">
              {rows.map(function (x) {
                return <div key={x.label} className="oo-acct__dlrow"><dt>{x.label}</dt><dd className={x.label === 'Requests' ? 'oo-bkd-info__notes' : undefined}>{x.value}</dd></div>;
              })}
            </dl>
          </ui.Card>
        </div>
        <div className="oo-acct__col">
          {qrOk ? (
            <ui.Card className="oo-bkd-qr">
              <div className="oo-bk-qr__copy">
                <span className="oo-bk-qr__title">Show this at the restaurant</span>
                <span className="oo-bk-qr__sub">{'Booking ' + (row.id || id)}</span>
              </div>
              <Qr value={row.id || id} className="oo-bkd-qr__svg" />
            </ui.Card>
          ) : null}
          <ui.Card className="oo-bkd-actions">
            {book ? <ui.Button block href={book.href} onClick={book.onClick}>Book again</ui.Button> : null}
            <ui.Button block variant="outline" href={another.href} onClick={another.onClick}>Track another booking</ui.Button>
            {tel ? <ui.Button block variant="outline" href={tel}>{'Call ' + store.name}</ui.Button> : null}
            <ui.Button block variant="ghost" href={menu.href} onClick={menu.onClick}>{mode === 'app' ? 'Back to home' : 'Back to menu'}</ui.Button>
          </ui.Card>
        </div>
      </div>
    );
  }

  // ---- the signed-in history (P3-21) -------------------------------------------------------------------------------
  function HistoryCard(props) {
    var ui = OO.ui, r = props.item, hotel = props.hotel, store = props.store;
    var view = OO.router.link('bookings', { reservationId: r.id }, { hotel: hotel });
    var track = r.terminal ? null : OO.router.link('booking-track', { reservationId: r.id }, { hotel: hotel });
    var day = r.date ? OO.format.shortDate(r.date).split(' ') : null;
    var dow = r.date ? OO.format.weekdayShort(OO.format.weekdayOf(r.date)) : '';
    var other = r.storeName && r.storeName !== store.name ? r.storeName : '';
    var meta = [guestsText(r.guests), r.name, other].filter(Boolean).join(' · ');
    return (
      <ui.Card className={'oo-bkh-card' + (props.next ? ' is-next' : '')}>
        <div className="oo-bkh-card__date" aria-hidden="true">
          {day ? [<span key="w">{dow}</span>, <strong key="d">{day[0]}</strong>, <span key="m">{day[1]}</span>] : <strong>—</strong>}
        </div>
        <div className="oo-bkh-card__main">
          {props.next ? <span className="oo-bkh-card__next">Next up</span> : null}
          <div className="oo-bkh-card__top">
            <h3 className="oo-bkh-card__when">{whenText(r) || 'Date to be confirmed'}</h3>
            <ui.Badge tone={r.tone} size="sm">{r.label}</ui.Badge>
          </div>
          {meta ? <p className="oo-bkh-card__meta">{meta}</p> : null}
          {r.notes ? <p className="oo-bkh-card__notes">{r.notes}</p> : null}
          <p className="oo-bkh-card__id">{'Reservation ' + r.id}</p>
          <div className="oo-bkh-card__actions">
            <ui.Button size="xs" variant="outline" href={view.href} onClick={view.onClick} aria-label={'View booking ' + r.id}>View</ui.Button>
            {track ? <ui.Button size="xs" href={track.href} onClick={track.onClick} aria-label={'Track booking ' + r.id + ' live'}>Track live</ui.Button> : null}
          </div>
        </div>
      </ui.Card>
    );
  }

  function History(props) {
    var route = props.route, hotel = route.hotel, mode = props.mode, ui = OO.ui, acct = OO.pages.acct, K = OO.appKit;
    var h = OO.useHotel(hotel, { withMenu: false });
    var tz = h.store ? h.store.timezone : null;
    var session = OO.useStore(selectSession);
    var uid = session && session.customer && session.customer.uid ? String(session.customer.uid) : null;
    var tabS = React.useState('upcoming');
    var res = OO.useAsync(function () {
      return uid ? OO.api.getCustomerBookings(hotel, { uid: uid }) : Promise.resolve(null);
    }, [hotel, uid]);
    var empty = !!res.error && Number(res.error.status) === 400; // "Customer Data Not Found!": no bookings in this chain
    var hist = React.useMemo(function () {
      return res.data || empty ? OO.model.bookingHistory(res.data || {}, { now: OO.data.now(), timeZone: tz }) : null;
    }, [res.data, empty, tz]);

    var header = headerFor(hotel, mode, homeHref(hotel, mode));
    var blocked = acct.gate(h, { title: 'Your bookings', header: header });
    if (blocked) return blocked;
    var store = h.store;
    var page = function (children, extra) {
      extra = extra || {};
      return <acct.Page title="Your bookings" subtitle={store.name} {...header} right={extra.right} busy={extra.busy} className="oo-bkh">{children}</acct.Page>;
    };
    var book = store.booking ? OO.router.link('booking', null, { hotel: hotel }) : null;
    var lookup = OO.router.link('booking-track', null, { hotel: hotel });
    var links = (
      <div key="links" className="oo-bkh__links">
        {book ? <ui.Button variant="outline" href={book.href} onClick={book.onClick}>Book a table</ui.Button> : null}
        <ui.Button variant="ghost" href={lookup.href} onClick={lookup.onClick}>Track a booking</ui.Button>
      </div>
    );

    if (!uid) {
      return page([
        <K.LoginPrompt key="login" hotel={hotel} title="Log in to see your bookings" body="Your table bookings at every one of our stores, in one place." />,
        links,
      ]);
    }
    if (res.error && !empty) {
      var notHere = res.error.kind === 'unavailable' && !(Number(res.error.status) > 0);
      return page([
        <ui.EmptyState key="err" icon="Info" role="alert"
          title={notHere ? 'Your bookings aren’t available here yet' : 'We couldn’t load your bookings'}
          body={notHere ? 'Use “Track a booking” with your reservation ID and email.' : acct.errorText(res.error, 'Check your connection and try again.')}
          action={notHere ? null : <ui.Button variant="outline" size="sm" onClick={res.reload}>Try again</ui.Button>} />,
        links,
      ]);
    }
    if (!hist) {
      return page([
        <span key="sr" className="oo-sr-only" role="status">Loading</span>,
        <div key="sk" className="oo-acct__skel"><ui.Skeleton lines={2} className="oo-acct__skel-title" /><ui.Skeleton variant="block" count={2} /></div>,
      ], { busy: true });
    }

    var tab = tabS[0];
    var items = tab === 'all' ? hist.all : hist.upcoming;
    var tabs = [
      { key: 'upcoming', label: 'Upcoming', count: hist.upcoming.length },
      { key: 'all', label: 'All', count: hist.all.length },
    ];
    var refresh = <ui.HeaderLink onClick={res.reload} disabled={res.loading}>Refresh</ui.HeaderLink>;
    var none = tab === 'all'
      ? <ui.EmptyState key="none" icon="Calendar" title="No bookings yet" body={'Your table bookings at ' + store.name + ' will show here.'} />
      : <ui.EmptyState key="none" icon="Calendar" title="No upcoming bookings" body={hist.all.length ? 'Your past bookings are under All.' : 'Book a table and it will show here.'} />;
    return page([
      <div key="tabs" className="oo-bkh__tabs" role="group" aria-label="Show bookings">
        {tabs.map(function (t) {
          return (
            <ui.Chip key={t.key} active={tab === t.key} onClick={function () { tabS[1](t.key); }}>
              {t.label + ' (' + t.count + ')'}
            </ui.Chip>
          );
        })}
      </div>,
      items.length ? (
        <ul key="list" className="oo-bkh__list" aria-label={tab === 'all' ? 'All bookings' : 'Upcoming bookings'} aria-busy={res.loading ? 'true' : undefined}>
          {items.map(function (r) {
            return <li key={r.id} className="oo-bkh__item"><HistoryCard item={r} hotel={hotel} store={store} next={tab === 'upcoming' && r.id === hist.next} /></li>;
          })}
        </ul>
      ) : none,
      links,
    ], { right: refresh });
  }

  // /bookings/{id}: the detail, signed in only (REF BookingDetailPageClient).
  function SignedInDetail(props) {
    var route = props.route, hotel = route.hotel, mode = props.mode, acct = OO.pages.acct, K = OO.appKit;
    var h = OO.useHotel(hotel, { withMenu: false });
    var session = OO.useStore(selectSession);
    var signedIn = !!(session && session.customer && session.customer.uid);
    var back = OO.router.href('bookings', null, { hotel: hotel });
    if (signedIn) return <Detail route={route} mode={mode} reservationId={props.reservationId} backTo={back} />;
    var header = headerFor(hotel, mode, back);
    var blocked = acct.gate(h, { title: 'Booking status', header: header });
    if (blocked) return blocked;
    return (
      <acct.Page title="Booking status" subtitle={h.store.name} {...header} className="oo-bkd">
        <K.LoginPrompt hotel={hotel} title="Log in to see this booking" body="Or track it with your reservation ID and email." />
        <OO.ui.Button variant="outline" {...OO.router.link('booking-track', { reservationId: props.reservationId }, { hotel: hotel })}>Track with your email</OO.ui.Button>
      </acct.Page>
    );
  }

  OO.pages.Bookings = function Bookings(props) {
    var route = props.route;
    var id = route.params && route.params.reservationId != null ? String(route.params.reservationId) : '';
    if (route.page === 'booking-track') {
      return id ? <Detail key={'track|' + id} route={route} mode={props.mode} reservationId={id} />
        : <Lookup route={route} mode={props.mode} />;
    }
    return id ? <SignedInDetail key={'mine|' + id} route={route} mode={props.mode} reservationId={id} />
      : <History route={route} mode={props.mode} />;
  };
  OO.pages.Bookings.logic = {
    lookupError: lookupError, cleanId: cleanId, queryEmail: queryEmail, hasContact: hasContact, emailFor: emailFor, steps: steps,
    whenText: whenText,
  };
  OO.pages.Bookings.POLL = POLL;
  OO.pages.Bookings.Lookup = Lookup;
  OO.pages.Bookings.Detail = Detail;
  OO.pages.Bookings.History = History;
  OO.pages.Bookings.SignedInDetail = SignedInDetail;
})();


// ===================== src/pages/kiosk.jsx =====================
// The kiosk shell owns the customer session. All ordering pages inside it are shared.
(function () {
  'use strict';
  var OO = window.OO, ui = OO.ui, K = OO.kiosk;
  function preview() { return !!(OO.whitelabel && OO.whitelabel.isPreview()); }
  function Brand(props) {
    var logo = K.logo(props.store), name = props.store.name;
    return logo ? <img className="oo-kiosk__logo" src={logo} alt={name} /> : <span className="oo-kiosk__monogram" aria-label={name}>{name.split(/\s+/).slice(0, 2).map(function (w) { return w.charAt(0); }).join('')}</span>;
  }
  ui.KioskProductCard = function KioskProductCard(props) {
    var off = props.soldOut || props.unavailable, label = props.soldOutLabel || props.unavailableLabel || 'Unavailable now';
    var failed = React.useState(false), initial = props.name.split(/\s+/).slice(0, 2).map(function (w) { return w.charAt(0); }).join('');
    return <button type="button" className={'oo-kiosk-product' + (off ? ' is-unavailable' : '')} aria-disabled={!!off} onClick={off ? undefined : props.onClick}>
      <span className="oo-kiosk-product__photo">{props.image && !failed[0] ? <img src={props.image} alt="" loading="lazy" onError={function () { failed[1](true); }} /> : <span className="oo-kiosk-product__initials">{initial}</span>}
        <span className="oo-kiosk-product__price">{props.price}</span>{off ? <span className="oo-kiosk-product__badge">{label}</span> : props.badge ? <span className="oo-kiosk-product__badge">{props.badge}</span> : null}
      </span>
      <span className="oo-kiosk-product__body"><strong>{props.name}</strong>{props.desc ? <span className="oo-kiosk-product__desc">{props.desc}</span> : null}
        <span className="oo-kiosk-product__foot"><span className="oo-kiosk-product__normal-price">{props.price}</span>
          <span className="oo-kiosk-product__add" aria-hidden="true"><OO.icons.Plus size={28} /><span>{off ? label : 'Add to cart'}</span></span></span>
      </span>
    </button>;
  };
  function Choices(props) {
    var selected = K.orderType(props.hotel);
    return <div className="oo-kiosk__choices" aria-label="Order type">
      {[['DINEIN', 'Dine-in', 'Enjoy your meal here', 'Utensils'], ['TAKEAWAY', 'Takeaway', 'Take your favourites with you', 'Bag']].map(function (v) {
        var Icon = OO.icons[v[3]] || OO.icons.Check;
        return <button key={v[0]} type="button" className={'oo-kiosk__choice' + (selected === v[0] ? ' is-selected' : '')}
          aria-pressed={selected === v[0]} disabled={props.disabled} onClick={function () { if (K.choose(props.hotel, v[0]) && props.onChoose) props.onChoose(); }}>
          <Icon size={36} /><strong>{v[1]}</strong><span>{v[2]}</span>
        </button>;
      })}
    </div>;
  }
  function Message(props) {
    return <main className="oo-kiosk__message"><h1>{props.title}</h1><p>{props.children}</p>{props.actions}</main>;
  }
  function Welcome(props) {
    var store = props.store, prompt = store.whitelabel && store.whitelabel.orderPrompt || {};
    var slides = store.kiosk && store.kiosk.slides || [], slide = React.useState(0);
    React.useEffect(function () {
      if (slides.length < 2 || prompt.image) return undefined;
      var timer = setInterval(function () { slide[1](function (v) { return (v + 1) % slides.length; }); }, 7000);
      return function () { clearInterval(timer); };
    }, [slides.length, prompt.image]);
    var photo = prompt.image || (slides[slide[0] % Math.max(1, slides.length)] || {}).image;
    return <main className={'oo-kiosk__welcome' + (photo ? ' has-photo' : '')}>
      <div className="oo-kiosk__photo">{photo ? <img src={photo} alt="" /> : <div className="oo-kiosk__photo-fallback">{store.name}</div>}<div className="oo-kiosk__photo-brand"><Brand store={store} /></div></div>
      <div className="oo-kiosk__welcome-content">
        <h1>{prompt.title || 'Order here'}</h1>
        <p>{prompt.intro || 'Freshly made. Just the way you like it.'}</p>
        <h2>Where will you eat today?</h2>
        <Choices hotel={props.hotel} onChoose={props.onChoose} />
        <div className="oo-kiosk__welcome-footer"><ui.PoweredBySipo /><button type="button" className="oo-kiosk__text-size" aria-pressed={props.large} onClick={props.onLargerText}><b>Aa</b> {props.large ? 'Standard text' : 'Larger text'}</button></div>
      </div>
    </main>;
  }
  OO.pages.KioskOrderType = function KioskOrderType(props) {
    React.useSyncExternalStore(K.subscribe, K.revision, K.revision);
    return <ui.Card className="oo-checkout__card"><h3 className="oo-checkout__title">Your order</h3><Choices hotel={props.hotel} /></ui.Card>;
  };
  OO.pages.KioskSuccess = function KioskSuccess(props) {
    var hotel = OO.useHotel(props.hotel, { withMenu: false });
    var left = React.useState(Math.ceil(K.SUCCESS_MS / 1000));
    React.useEffect(function () {
      K.resolved(props.orderId);
      var end = Date.now() + K.SUCCESS_MS;
      var timer = setInterval(function () {
        var seconds = Math.max(0, Math.ceil((end - Date.now()) / 1000));
        left[1](seconds);
        if (!seconds) K.reset(props.hotel);
      }, 1000);
      return function () { clearInterval(timer); };
    }, [props.hotel, props.orderId]);
    return <main className="oo-kiosk__success">
      <div className="oo-kiosk__check"><OO.icons.Check size={54} /></div>
      <h1>{props.paid ? 'Thank you!' : 'Order placed'}</h1>
      <p>{props.paid || props.how === 'wallet' ? 'Your order has been sent to the store.' : 'Please pay at the counter to complete your order.'}</p>
      <p className={'oo-kiosk__reference' + (String(props.orderId).length > 12 ? ' is-long' : '')}>Order reference <strong>{props.orderId}</strong></p>
      <p>{K.presentation(hotel.store).collectionMessage || 'Please keep this reference for collection.'}</p>
      <ui.Button size="lg" onClick={function () { K.reset(props.hotel); }}>Done</ui.Button>
      <p className="oo-kiosk__countdown">Ready for the next order in {left[0]} seconds</p>
    </main>;
  };
  OO.pages.KioskHost = function KioskHost(props) {
    var route = props.route, hotel = route.hotel, h = OO.useHotel(hotel, { withMenu: false });
    React.useSyncExternalStore(K.subscribe, K.revision, K.revision);
    var state = OO.useStore(function (s) { return s; });
    var tick = React.useState(Date.now()), restart = React.useState(false), large = React.useState(false), isPreview = preview();
    var id = K.terminal(hotel), visit = K.visit(hotel), paymentPage = route.page === 'payment-callback' || route.page === 'order-status';
    React.useLayoutEffect(function () { if (hotel) K.prepare(hotel); }, [hotel, id]);
    React.useEffect(function () {
      if (!hotel || isPreview) return undefined;
      function active() { K.activity(); }
      ['pointerdown', 'keydown', 'scroll'].forEach(function (e) { window.addEventListener(e, active, { passive: true, capture: true }); });
      var timer = setInterval(function () { tick[1](Date.now()); }, 1000);
      return function () { clearInterval(timer); ['pointerdown', 'keydown', 'scroll'].forEach(function (e) { window.removeEventListener(e, active, true); }); };
    }, [hotel, isPreview]);
    React.useEffect(function () {
      if (!hotel || !id || isPreview) return undefined;
      // An unattended kiosk should pick up closures and terminal configuration changes.
      var timer = setInterval(function () {
        if (!K.pending()) OO.data.loadHotel(hotel, { withMenu: false, force: true }).catch(function () {});
      }, 30000);
      return function () { clearInterval(timer); };
    }, [hotel, id, isPreview]);
    var idle = K.idleState(visit && visit.activity || tick[0], tick[0], isPreview || K.pending());
    React.useEffect(function () { if (visit && visit.type && idle.expired) { restart[1](false); K.reset(hotel); } }, [hotel, idle.expired]);
    if (!hotel || !id) return <Message title="Kiosk setup needed">Open the store link configured in your kiosk app, including its terminal.</Message>;
    if (!visit) return <Message title={K.pending() ? 'Payment needs checking' : 'Please wait'}>{K.pending() ? 'Please ask a staff member to check the previous order before changing this kiosk.' : 'Preparing this kiosk…'}</Message>;
    if (h.error || h.notFound) return <Message title="Unable to load this store" actions={<ui.Button onClick={h.reload}>Try again</ui.Button>}>Please ask a staff member for help.</Message>;
    if (!h.store) return <Message title="Welcome">Loading the menu…</Message>;
    var presentation = K.presentation(h.store);
    function shell(content) { return <div className={'oo-kiosk-shell oo-kiosk--' + presentation.variant + (large[0] ? ' oo-kiosk--large' : '')} data-image-fit={presentation.imageFit} style={K.style(h.store)}>{content}</div>; }
    if (!paymentPage && h.store.kiosk && !h.store.kiosk.active && !K.pending()) {
      return shell(<Message title="Ordering is paused">{h.store.kiosk.reason || 'Please order with a member of staff.'}</Message>);
    }
    // A confirmed order reached through a status link has the same short, private finish as a callback.
    var last = state.lastOrder;
    if (route.page === 'order-status' && last && String(last.hotel).toLowerCase() === String(hotel).toLowerCase() && last.settledAt) {
      return shell(<OO.pages.KioskSuccess hotel={hotel} orderId={last.orderId} paid={last.paymentMethod === 'ONLINE_PAYMENT' || Number(last.total) === 0} />);
    }
    if (!visit.type && !paymentPage) return shell(<Welcome hotel={hotel} store={h.store} large={large[0]} onLargerText={function () { large[1](!large[0]); }} onChoose={function () { OO.router.go(OO.router.href('menu', null, { hotel: hotel })); }} />);
    return shell(<React.Fragment>
      <header className="oo-kiosk__session"><Brand store={h.store} /><div className="oo-kiosk__session-title"><strong>{h.store.name}</strong>{visit.type ? <span>{K.label(hotel)}</span> : null}</div>
        {!paymentPage ? <ui.Button variant="outline" disabled={K.pending()} onClick={function () { restart[1](true); }}>Start again</ui.Button> : null}</header>
      {props.page}
      <ui.Sheet open={!!visit.type && idle.warning} variant="dialog" title="Still ordering?" onClose={function () { K.activity(); tick[1](Date.now()); }}>
        <p>Your order will be cleared in {idle.remaining} seconds.</p>
        <ui.Button block onClick={function () { K.activity(); tick[1](Date.now()); }}>Continue ordering</ui.Button>
        <ui.Button block variant="outline" onClick={function () { K.reset(hotel); }}>Start again</ui.Button>
      </ui.Sheet>
      <ui.Sheet open={restart[0]} variant="dialog" title="Start a new order?" onClose={function () { restart[1](false); }}>
        <p>This will clear your cart and contact details.</p>
        <ui.Button block onClick={function () { if (K.reset(hotel)) restart[1](false); }}>Start again</ui.Button>
        <ui.Button block variant="outline" onClick={function () { restart[1](false); }}>Keep ordering</ui.Button>
      </ui.Sheet>
    </React.Fragment>);
  };
})();


// ===================== src/pages/qr-start.jsx =====================
// A presentation-only entry screen for table-side and Ordering QR menus. Acknowledgements live only in this
// page load, scoped by store and table/QR, so returning from the cart keeps the menu and scanning anew starts here.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var started = Object.create(null), listeners = [], revision = 0;
  function str(value) { return value == null ? '' : String(value).trim(); }
  function sameStore(a, b) { return str(a).toLowerCase() === str(b).toLowerCase(); }
  function scopeOf(route, mode, store, table) {
    if (!route || route.page !== 'menu' || !route.hotel || !store) return null;
    var hotel = str(route.hotel).toLowerCase();
    if (mode === 'qr' && table && sameStore(table.hotel, hotel)) {
      var name = str(table.name) || str(table.id);
      return { key: JSON.stringify(['table', hotel, str(table.id)]), table: true,
        destination: /^table(?:\s|$)/i.test(name) ? name : 'Table ' + name };
    }
    var qr = store.orderingQr;
    if (mode === 'online' && qr && OO.orderingQr && OO.orderingQr.forHotel(route.hotel)) {
      return { key: JSON.stringify(['qr', hotel, str(qr.id || OO.orderingQr.token()), str(qr.revision)]), table: false,
        destination: str(qr.name) || 'Ordering QR' };
    }
    return null;
  }
  function pending(scope) { return !!scope && !started[scope.key]; }
  function subscribe(fn) {
    listeners.push(fn);
    return function () { listeners = listeners.filter(function (other) { return other !== fn; }); };
  }
  function version() { return revision; }
  function usePending(scope) {
    React.useSyncExternalStore(subscribe, version, version);
    return pending(scope);
  }
  function complete(scope) {
    if (!pending(scope)) return;
    started[scope.key] = true;
    revision += 1;
    listeners.slice().forEach(function (fn) { fn(); });
    if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
  }
  function Start(props) {
    var ui = OO.ui, store = props.store, scope = props.scope;
    var logo = store.theme && store.theme.whitelabel ? store.theme.logo : null;
    return (
      <div className="oo-qr-start">
        <ui.StoreHeader name={store.name} logo={logo} address={store.address.full} addressShort={store.address.short} showBack={false} />
        <main className="oo-qr-start__main" aria-labelledby="oo-qr-start-title">
          <div className="oo-qr-start__card">
            <p className="oo-qr-start__label">{scope.table ? 'Table side ordering' : 'QR ordering'}</p>
            <span className="oo-qr-start__icon" aria-hidden="true">{ui.renderIcon(scope.table ? 'Table' : 'Scan', { size: 28 })}</span>
            <h1 className="oo-qr-start__title" id="oo-qr-start-title"><span className="oo-qr-start__prefix">Order from</span>{' '}{scope.destination}</h1>
            <p className="oo-qr-start__intro">Browse the menu and place your order.</p>
            <ui.Button size="lg" block iconRight="ArrowRight" onClick={function () { complete(scope); }}>Start ordering</ui.Button>
          </div>
        </main>
        {store.settings && store.settings.poweredBy === false ? null : <ui.PoweredBySipo className="oo-qr-start__powered" />}
      </div>
    );
  }
  function OrderingHost(props) {
    var route = props.route;
    var h = OO.useHotel(route.hotel, { withMenu: false });
    var scope = scopeOf(route, props.mode, h.store, null);
    var showStart = usePending(scope);
    // Keep item/cart deep links untouched while metadata loads; the existing Menu consumes them after Start.
    if (h.notFound || h.error) return props.page;
    if (!h.store) return <div className="oo-qr-start__loading" role="status">Loading the menu…</div>;
    return showStart ? <Start store={h.store} scope={scope} /> : props.page;
  }
  OO.pages.QrStart = Start;
  Start.Host = OrderingHost;
  Start.scopeOf = scopeOf;
  Start.pending = pending;
  Start.usePending = usePending;
  Start.complete = complete;
})();


// ===================== src/pages/table.jsx =====================
// src/pages/table.jsx: OO.pages.Table, ordering at a table in mode 'qr' (Batch 2 P4-01 / P4-03; REF qr-ordering:
// TableSideContext, TableSideGate, TableSideLanding, QrRootGate, TableWelcome; inventory D §1.1). One component, two jobs
// (styles: styles/table.css):
//   the scan landings   route.page 'scan' (it is the page): '/' (route.hotel null) is the generic "Scan the QR code on
//                       your table"; '/{key}' (and '/{key}/menu') with no table context is the store's: its branding,
//                       "Scan the code on your table", and a secondary "Order pickup or delivery" link to the online
//                       host's '/{key}' (onlineHref: __SIPO_BOOT.onlineBase, when the store takes pickup or delivery
//                       orders online). A store whose table ordering is off shows "Ordering not available" instead
//                       (QR-OFF, below: the gate's panel, with its [Check again] re-reading the store).
//   the table host      any other page of a store this host serves (OO.App mounts it in the page's place and hands it
//                       the page, `page`; P4-14): the table entry '/{key}/table/{id}' (route.params.tableId), cart,
//                       checkout, about, the legal pages and the payment pages. It renders the page, then the
//                       full-screen gate while the table is being checked or can't take orders, then (P4-03) the guest
//                       welcome where the store asks for the party size; nothing after the page otherwise. Where the
//                       store's table ordering is off it renders the gate WITHOUT the page (QR-OFF, below).
// Routed by P4-14 (src/app.jsx; glue/router.jsx "Mode qr"): OO.router.qrGate sends '/{key}' on to the table menu while
// a context applies, and cart / checkout / about / the legal pages to the scan landing while none does.
//
// The table context (the contract in briefs/b2-p4-common.md; other lanes read it at render time):
//   OO.pages.Table.context(hotel) → null | { id: number, name: string | null, hotel: string, guests: number | null,
//       excludedCategoryIds: number[], expiresAt: ms }: this store's valid, unexpired context (OO.store.tableFor: kept
//       in OO.store, 'oo-table-v1', 2 h; mode qr only; null on this store's table entry for another table).
//   OO.pages.Table.revalidate(hotel) → Promise<context | null>: re-reads the table (the one of the table entry the URL
//       is on, else the context's) and judges it again: the fresh context (its 2 h start again), or null when the table
//       is gone, locked, inactive, another store's, the store's table ordering is off, or there is no table to check (a
//       definite answer: the store's context is cleared). It REJECTS (the request's Error) when the table couldn't be
//       checked: a network or server failure (venue Wi-Fi), so the checkout can say "We couldn't check your table"
//       rather than "Table unavailable"; the context is kept and the status reads 'error' until a check succeeds.
//   OO.pages.Table.status(hotel) → null | 'validating' | 'open' | 'closed' | 'disabled' | 'error': the latest check of
//       the table in question (the entry's, else the context's, else the store's latest failed check), null when none
//       ran this page load.
// The check (REF TableSideContext): the store (OO.data.loadHotel, store only; a fresh re-read for "Check again" on the
// paused gate) and E19 (OO.api.getTable: GET tables/gettableDetails, not store-scoped) together, judged by
// OO.model.judgeTable: ownership (the row's user_id is this Store.id: a table id from the URL is never trusted alone),
// the store's table ordering (enable_table_side_ordering; it wins over everything), the table's status. Once per
// (store, table) per page load: the entry with no context shows "Checking your table…" until it answers; a context kept
// from before (a reload, another page) is used at once and checked in the background. 'open' keeps the context
// (OO.store.setTable: the row's id, name and the table group's excluded categories, a new 2 h). A definite failure
// clears any context of this store, so a later '/{key}' never goes back to a table the customer has left.
// The gate (REF TableSideGate; its copy in our sentence case), over every page of the store but checkout and the payment
// pages (REF's commerce flow: there the page decides; checkout re-checks at Pay with revalidate):
//   validating  "Checking your table…"                       the entry while its table is checked
//   closed      "{Table} isn't available"  [Check table again]  E19's "Table is Locked!" (tables.status inactive) or a
//                                                             row that isn't active. REF shows "We couldn't verify your
//                                                             table" for the locked answer; we say what it is (P4-01).
//   disabled    "Ordering not available"   [Check again]      the store's table ordering is off (QR-OFF, below)
//   error       "We couldn't verify your table"    [Try again]     no such table, another store's, a malformed id in
//                                                             the URL, or the check failed
// A store the API doesn't know (its load answers 404) gets no gate: the page says "Store not found" itself.
// QR-OFF (owner 2026-10-05: "when table ordering switched off dont show them table ordering show ordering not
// available"). The store's table ordering off (enable_table_side_ordering, Store.tableOrdering false; also when get_menu
// said so for a store read as on: store.jsx then turns the cached Store off, "The menu's channel") is the 'disabled'
// state: eyebrow "Ordering paused", title "Ordering not available", "{store} isn’t taking orders at the table right now.
// Please ask a staff member for help.", [Check again] (re-reads the store, with the table in question when there is one)
// and, where the store takes pickup or delivery orders online, "Not at a table?" [Order pickup or delivery] (onlineHref).
// It shows at once from the loaded Store, context or not (store.jsx reads no table menu for it and drops the context),
// over every page of the store but the payment pages (checkout included: nothing is ordered at a table meanwhile), and
// IN PLACE of the page: no menu, cart, table strip, guest welcome or checkout renders under it. The store's scan landing
// shows the same panel.
// The guest welcome (P4-03; REF TableWelcome + TableSideContext's guest count): where the store asks for the party size
// (Store.settings.tableUserForm: getRestaurant's is_user_form_table_side, the key the dine-in checkout reads too and the
// flag REF also makes contact mandatory at checkout with; for a Store normalised without that key, the same raw flag
// through OO.data.tableSettings(hotel).guestForm), a full-screen "Welcome to {store}" with the party size (a − / +
// stepper starting at 2, quick picks 1–6, at least 1 and at most the table's seats, 20 when the seat count is unknown)
// and "See the menu" ("Continue" on the cart and checkout), over the menu, cart and checkout (REF's ordering routes)
// once the table's check this page load is 'open' and the context has no party size yet. The count is remembered ON the context
// (context.guests: the context's 2 h; P4-07 sends it as guest_count). A check keeps it for the same table (capped at the
// seats), forgets it for another, a single-seat table gets 1 without asking (REF), and a store that doesn't ask keeps
// none (null).
//   OO.pages.Table.setGuests(hotel, n) → context | null: the party size for this store's context (a whole number ≥ 1,
//       capped at the table's seats when the check knows them, and at 999); null without a context or a usable n.
// OO.pages.Table.logic (pure; tests/table-p401.test.mjs, table-p403.test.mjs): onlineHref(hotel), offersOnline(store),
//   storeLabel(name), copy(state, { store, table }) → { eyebrow, title, message, retry }, gateState({ page, entry, idOk,
//   context, check }) → null | 'validating' | 'closed' | 'disabled' | 'error', entryId(route) → the entry's table id (a
//   whole number) | NaN (an entry whose id isn't one) | null (not an entry); welcomeOf({ page, context, check, guestForm })
//   → null | { max, seats, initial } (the welcome to show: seats 0 = unknown), quickPicks(max) → [1–6 up to max],
//   clampGuests(n, max) → a whole number in [1, max], pausedOn(page, store) → the store's table ordering is off and
//   `page` is one the paused gate covers (QR-OFF). OO.pages.Table.parts = { Gate, Welcome } (the dialogs).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  // REF isCommerceFlowRoute: no gate here, the page decides (checkout refuses at Pay; a payment return confirms).
  var NO_GATE = { checkout: 1, 'payment-callback': 1, 'order-status': 1 };
  // QR-OFF: the pages the paused gate (the store's table ordering off) never covers: the payment pages only (a payment
  // return still confirms its order); checkout is covered.
  var PAY_PAGES = { 'payment-callback': 1, 'order-status': 1 };
  var FAILED = { closed: 1, disabled: 1, error: 1 };
  // P4-03 (REF TableWelcome): the pages that ask for the party size (REF's ordering routes), its stepper's default and
  // its maximum when the table's seat count is unknown; the quick picks; the cap a POS takes (payload.jsx GUESTS_MAX).
  var WELCOME_PAGES = { menu: 1, cart: 1, checkout: 1 };
  var DEFAULT_GUESTS = 2;
  var DEFAULT_MAX_GUESTS = 20;
  var QUICK_PICKS = [1, 2, 3, 4, 5, 6];
  var GUESTS_MAX = 999;
  // REF TableSideLanding STEPS, in our sentence case.
  var STEPS = [
    { title: 'Find the code', text: 'Look for the QR card on your table.' },
    { title: 'Scan it', text: 'Open your phone camera and point it at the code.' },
    { title: 'Order', text: 'Browse the menu and send it to the kitchen.' },
  ];

  function str(v) { return v === undefined || v === null ? '' : String(v); }
  function own(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }
  function sameKey(a, b) { var x = str(a).toLowerCase(); return x !== '' && x === str(b).toLowerCase(); }
  // A whole number > 0 (a number, or a string of digits), else null.
  function posInt(v) {
    var n = typeof v === 'number' ? v : typeof v === 'string' && /^\s*\d+\s*$/.test(v) ? Number(v) : NaN;
    return isFinite(n) && n > 0 && Math.floor(n) === n && n <= 9007199254740991 ? n : null;
  }
  function boot() { return window.__SIPO_BOOT || {}; }

  // ---- pure logic (OO.pages.Table.logic) ------------------------------------------------------------------------------
  // The store's page on the online host ({onlineBase}/{key}), or null without an absolute http(s) onlineBase or a key.
  function onlineHref(hotel) {
    var base = str(boot().onlineBase).trim().replace(/\/+$/, '');
    if (!str(hotel) || !/^https?:\/\/[^\s/?#]+(\/[^\s?#]*)?$/i.test(base)) return null;
    return base + '/' + encodeURIComponent(str(hotel));
  }
  // The store takes pickup or delivery orders online now (the landing's "Order pickup or delivery" link).
  function offersOnline(store) {
    var types = store && Array.isArray(store.orderTypes) ? store.orderTypes : [];
    var active = !(store && store.online && store.online.active === false);
    return !!store && active && (types.indexOf('PICKUP') >= 0 || types.indexOf('DELIVERY') >= 0);
  }
  // REF: a store's name without a trailing "(…)" qualifier ("Bennys (Fitzroy)" → "Bennys").
  function storeLabel(name) { var s = str(name).trim(); return s.split('(')[0].trim() || s; }
  function copy(state, names) {
    var store = (names && names.store) || 'This restaurant';
    var table = names && names.table ? names.table : 'This table';
    switch (state) {
      case 'validating':
        return { eyebrow: 'Table-side ordering', title: 'Checking your table…', message: 'One moment while we get your table ready to order.', retry: null };
      case 'closed':
        return { eyebrow: 'Table unavailable', title: table + ' isn’t available', message: 'This table is closed for ordering right now. Please ask a staff member for help.', retry: 'Check table again' };
      case 'disabled': // QR-OFF (see the header)
        return { eyebrow: 'Ordering paused', title: 'Ordering not available', message: store + ' isn’t taking orders at the table right now. Please ask a staff member for help.', retry: 'Check again' };
      case 'error':
        return { eyebrow: 'Couldn’t verify', title: 'We couldn’t verify your table', message: 'Scan the QR code on your table again, or ask a staff member for help.', retry: 'Try again' };
      default:
        return { eyebrow: 'Table-side ordering', title: 'Scan the QR code on your table', message: 'Open your phone camera and scan the QR code on your table to start your dine-in order.', retry: null };
    }
  }
  // QR-OFF: the store's table ordering is off and `page` is one the paused gate covers (see the header).
  function pausedOn(page, store) { return !!store && store.tableOrdering === false && !own(PAY_PAGES, page); }
  // The gate to show over a store page (see the header), or null. check = the latest check of the table in question.
  function gateState(o) {
    if (!o || own(NO_GATE, o.page)) return null;
    if (o.entry && !o.idOk) return 'error';
    var st = o.check ? o.check.status : null;
    if (st && own(FAILED, st)) return st;
    if (st === 'nostore' || o.context) return null;
    return o.entry ? 'validating' : null;
  }
  // The table entry's id: a whole number, NaN for an entry whose id isn't one, null when the route isn't an entry.
  function entryId(route) {
    if (!route || route.page !== 'menu' || !route.params || !str(route.params.tableId).trim()) return null;
    var id = posInt(route.params.tableId);
    return id === null ? NaN : id;
  }
  // P4-03: the guest welcome to show (see the header), or null. check = this page load's check of the context's table.
  function welcomeOf(o) {
    if (!o || !own(WELCOME_PAGES, o.page) || !o.guestForm || !o.context || o.context.guests !== null) return null;
    if (!o.check || o.check.status !== 'open' || o.check.id !== o.context.id) return null;
    var seats = o.check.seats > 0 ? o.check.seats : 0;
    if (seats === 1) return null; // a single seat: 1 guest, never asked (REF)
    var max = Math.min(seats || DEFAULT_MAX_GUESTS, GUESTS_MAX);
    return { max: max, seats: seats, initial: Math.min(DEFAULT_GUESTS, max) };
  }
  function quickPicks(max) { return QUICK_PICKS.filter(function (n) { return n <= max; }); }
  function clampGuests(n, max) {
    var v = Math.floor(Number(n));
    if (!isFinite(v) || v < 1) v = 1;
    return Math.min(v, Math.max(1, Math.floor(Number(max)) || 1));
  }

  // ---- the checks: one per (store, table) per page load; revalidate re-runs (see the header) -------------------------
  var checks = {};   // '{store key, lower case}|{id}' → Check { hotel, id, status, reason, seq, p }
  var latest = {};   // store key (lower case) → its latest Check (a failed one keeps its gate up once the context is gone)
  var listeners = [];
  var seq = 0;
  function checkKey(hotel, id) { return str(hotel).toLowerCase() + '|' + id; }
  function peek(hotel, id) { var k = checkKey(hotel, id); return id !== null && own(checks, k) ? checks[k] : null; }
  function emit() {
    seq += 1;
    listeners.slice().forEach(function (fn) { try { fn(); } catch (e) { /* a listener never breaks a check */ } });
  }
  function subscribe(fn) {
    listeners.push(fn);
    return function () { listeners = listeners.filter(function (x) { return x !== fn; }); };
  }
  function version() { return seq; }

  function storeFor(hotel, fresh) {
    var D = OO.data;
    function load() { return D.loadHotel(hotel, { withMenu: false }).then(function (r) { return r.store; }); }
    if (!fresh || typeof D.rereadStore !== 'function') return load();
    return D.rereadStore(hotel).then(function (s) { return s || load(); });
  }
  // P4-03: the party size a fresh 'open' context keeps (see the header): none where the store doesn't ask; the same
  // table's (capped at its seats); 1 at a single-seat table.
  // Does the store ask for the party size? Its Store.settings.tableUserForm (model-store.jsx: is_user_form_table_side,
  // the key the dine-in checkout reads too), else, for a Store normalised without that key, the raw body's flag
  // (OO.data.tableSettings, the same field).
  function guestFormOf(store, hotel) {
    var s = store && store.settings;
    if (s && typeof s.tableUserForm === 'boolean') return s.tableUserForm;
    var t = OO.data && typeof OO.data.tableSettings === 'function' ? OO.data.tableSettings(hotel) : null;
    return !!(t && t.guestForm);
  }
  function guestsFor(c, v, kept, store) {
    if (!guestFormOf(store, c.hotel)) return null;
    if (v.seats === 1) return 1;
    var g = posInt(kept);
    return g === null ? null : Math.min(g, v.seats > 0 ? v.seats : GUESTS_MAX);
  }
  function setGuests(hotel, n) {
    var ctx = str(hotel) ? context(hotel) : null;
    var g = posInt(n);
    if (!ctx || g === null) return null;
    var c = peek(hotel, ctx.id);
    var cap = c && c.status === 'open' && c.seats > 0 ? c.seats : GUESTS_MAX;
    return OO.store.setTable(Object.assign({}, ctx, { guests: Math.min(g, cap, GUESTS_MAX) }));
  }

  // Settles a check with a verdict (OO.model.judgeTable's, or a failed read's): keeps or clears OO.store's context.
  function settle(c, v, store) {
    if (checks[checkKey(c.hotel, c.id)] !== c) return OO.store.tableFor(c.hotel); // a newer check of this table answers
    var ctx = null;
    var held = OO.store.get().table;
    var heldHere = !!held && sameKey(held.hotel, c.hotel);
    c.status = v.status;
    c.reason = v.reason || null;
    c.name = v.name || (heldHere && held.id === c.id ? held.name : null) || null; // the closed gate's "{Table} isn't available"
    c.seats = v.seats > 0 ? v.seats : 0;
    if (v.status === 'open') {
      ctx = OO.store.setTable({
        id: v.id, name: v.name, hotel: c.hotel, guests: guestsFor(c, v, heldHere && held.id === v.id ? held.guests : null, store),
        excludedCategoryIds: v.excludedCategoryIds, expiresAt: Date.now() + OO.store.TABLE_TTL_MS,
      });
    } else if (heldHere && own(FAILED, v.status) && v.reason !== 'network') {
      OO.store.clearTable(); // a definite answer: no table of this store keeps ordering (see the header)
    }
    emit();
    return ctx;
  }
  // check(hotel, id, { fresh, freshStore }) → Promise<context | null>. fresh runs it again even when one ran.
  function check(hotel, id, opts) {
    opts = opts || {};
    var k = checkKey(hotel, id);
    var cur = own(checks, k) ? checks[k] : null;
    if (cur && cur.p && !opts.fresh) return cur.p;
    var c = { hotel: str(hotel), id: id, status: 'validating', reason: null, p: null };
    checks[k] = c;
    latest[str(hotel).toLowerCase()] = c;
    emit();
    var table = Promise.resolve().then(function () { return OO.api.getTable(hotel, id); });
    c.p = storeFor(hotel, !!opts.freshStore).then(function (store) {
      return table.then(function (raw) {
        return settle(c, OO.model.judgeTable(raw, store, id), store);
      }, function (err) {
        settle(c, { status: 'error', reason: 'network' }, store); // the table couldn't be read: no verdict, it rejects
        throw err;
      });
    }, function (err) {
      var kind = OO.data.errorKind(err);
      table.then(null, function () { /* the store's failure answers */ });
      if (kind === 'notFound' || kind === 'notLive') return settle(c, { status: 'nostore', reason: 'store' }, null);
      settle(c, { status: 'error', reason: 'network' }, null);
      throw err;
    });
    c.p.then(null, function () { /* a caller that doesn't wait on it (the host's check) never leaves it unhandled */ });
    return c.p;
  }

  function context(hotel) { return OO.store && typeof OO.store.tableFor === 'function' ? OO.store.tableFor(hotel) : null; }
  function latestOf(hotel) { var k = str(hotel).toLowerCase(); return k && own(latest, k) ? latest[k] : null; }
  // The table in question for `hotel` on `route`: the table entry's (a whole number; null for a malformed one), else
  // the context's, else (withFailed) the store's latest check when it failed, so its gate stays once the context is gone.
  function subjectOf(hotel, route, withFailed) {
    var r = route && sameKey(route.hotel, hotel) ? route : null;
    var eid = entryId(r);
    if (eid !== null) return isNaN(eid) ? null : eid;
    var ctx = context(hotel);
    if (ctx) return ctx.id;
    var last = withFailed ? latestOf(hotel) : null;
    return last && own(FAILED, last.status) ? last.id : null;
  }
  function currentRoute() { try { return OO.router && OO.router.current ? OO.router.current() : null; } catch (e) { return null; } }
  function revalidate(hotel) {
    var id = str(hotel) ? subjectOf(hotel, currentRoute(), false) : null;
    return id === null ? Promise.resolve(null) : check(hotel, id, { fresh: true });
  }
  function status(hotel) {
    var c = str(hotel) ? peek(hotel, subjectOf(hotel, currentRoute(), true)) : null;
    if (!c) return null;
    return c.status === 'nostore' ? 'error' : c.status;
  }

  // ---- presentation ---------------------------------------------------------------------------------------------------
  // The store's mark above the card: its logo (else a glyph) and its name; SIPO's on the generic landing.
  function Brand(props) {
    var ui = OO.ui;
    var store = props.store;
    var logo = store && store.logoUrl ? store.logoUrl : null;
    return (
      <div className="oo-tbl-brand">
        {logo ? <img className="oo-tbl-brand__logo" src={logo} alt="" /> : <span className="oo-tbl-brand__mark" aria-hidden="true">{ui.renderIcon(store ? 'Store' : 'Scan', { size: 20 })}</span>}
        <span className="oo-tbl-brand__name">{store ? storeLabel(store.name) : 'SIPO table ordering'}</span>
      </div>
    );
  }
  // A dialog takes the focus when it opens (its container, tabIndex -1): keyboards and screen readers start there.
  function useFocusOnOpen() {
    var ref = React.useRef(null);
    React.useEffect(function () {
      var el = ref.current;
      if (!el || typeof el.focus !== 'function') return;
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }, []);
    return ref;
  }
  // The panel the landings and the gate show: the brand, then the card (an icon or a spinner, the eyebrow, title and
  // message, then the body).
  function Panel(props) {
    var ui = OO.ui;
    return (
      <div className={ui.cx('oo-tbl-panel', props.tone && 'oo-tbl-panel--' + props.tone)}>
        <Brand store={props.store} />
        <div className="oo-tbl-card">
          {props.busy
            ? <span className="oo-tbl-card__spin" aria-hidden="true" />
            : <span className="oo-tbl-card__icon" aria-hidden="true">{ui.renderIcon(props.icon || 'Scan', { size: 26 })}</span>}
          <p className="oo-tbl-card__eyebrow">{props.copy.eyebrow}</p>
          <h1 className="oo-tbl-card__title" id={props.titleId}>{props.copy.title}</h1>
          {props.copy.message ? <p className="oo-tbl-card__message" id={props.messageId}>{props.copy.message}</p> : null}
          {props.children}
        </div>
      </div>
    );
  }
  function Steps() {
    return (
      <ol className="oo-tbl-steps">
        {STEPS.map(function (s, i) {
          return (
            <li key={s.title} className="oo-tbl-steps__item">
              <span className="oo-tbl-steps__n" aria-hidden="true">{i + 1}</span>
              <span className="oo-tbl-steps__text"><strong>{s.title}</strong> {s.text}</span>
            </li>
          );
        })}
      </ol>
    );
  }
  var STATE_ICON = { closed: 'Clock', disabled: 'Clock', error: 'Info' };
  // "Not at a table?" → the store's page on the online host (the landings; the paused gate too, QR-OFF).
  function OnlineLink(props) {
    var ui = OO.ui;
    return (
      <div className="oo-tbl-online">
        <span className="oo-tbl-online__or">Not at a table?</span>
        <ui.Button variant="outline" size="md" block href={props.href} icon="Bag">Order pickup or delivery</ui.Button>
      </div>
    );
  }

  // The scan landings (page 'scan').
  function GenericLanding() {
    return (
      <main className="oo-tbl-landing">
        <Panel copy={copy('scan')} icon="Scan" titleId="oo-tbl-title"><Steps /></Panel>
      </main>
    );
  }
  function StoreLanding(props) {
    var ui = OO.ui;
    var hotel = props.hotel;
    var h = OO.useHotel(hotel, { withMenu: false });
    var store = h.store;
    var busyS = React.useState(false); // QR-OFF: [Check again] re-reading the store
    var c;
    var tone = null;
    if (h.notFound) {
      c = { eyebrow: 'Table-side ordering', title: 'We couldn’t find this store', message: 'Scan the QR code on your table to start your order, or ask a staff member for help.' };
    } else if (h.error && !store) {
      c = { eyebrow: 'Table-side ordering', title: 'We couldn’t load this store', message: 'Check your connection and try again.' };
    } else if (!store) {
      return (
        <main className="oo-tbl-landing" aria-busy="true">
          <Panel copy={{ eyebrow: 'Table-side ordering', title: 'Loading…', message: '' }} busy titleId="oo-tbl-title" />
        </main>
      );
    } else if (store.tableOrdering === false) {
      c = copy('disabled', { store: storeLabel(store.name) }); // QR-OFF: the paused gate's panel
      tone = 'paused';
    } else {
      c = copy('scan');
      c = { eyebrow: c.eyebrow, title: 'Scan the code on your table', message: c.message };
    }
    var online = store && offersOnline(store) ? onlineHref(hotel) : null;
    // QR-OFF: [Check again] re-reads the store (OO.useHotel adopts it when it changed: back on, the scan copy).
    function recheck() {
      if (typeof OO.data.rereadStore !== 'function') return;
      var done = function () { busyS[1](false); };
      busyS[1](true);
      OO.data.rereadStore(hotel).then(done, done);
    }
    return (
      <main className="oo-tbl-landing">
        <Panel store={store} copy={c} tone={tone} icon={tone ? 'Clock' : h.notFound || h.error ? 'Info' : 'Scan'} titleId="oo-tbl-title">
          {store && !tone ? <Steps /> : null}
          {tone ? <ui.Button size="md" block onClick={recheck} busy={busyS[0]}>{c.retry}</ui.Button> : null}
          {h.error && !store && !h.notFound ? <ui.Button size="md" block onClick={h.reload}>Try again</ui.Button> : null}
          {online ? <OnlineLink href={online} /> : null}
        </Panel>
      </main>
    );
  }

  // The gate over a store page (see the header): a full-screen dialog while the table is checked or can't order.
  function Gate(props) {
    var ui = OO.ui;
    var state = props.state;
    var ref = useFocusOnOpen();
    var c = copy(state, { store: props.store ? storeLabel(props.store.name) : null, table: props.tableName });
    var busy = state === 'validating';
    return (
      <div className="oo-tbl-gate" role="dialog" aria-modal="true" aria-labelledby="oo-tbl-gate-title"
        aria-describedby="oo-tbl-gate-msg" aria-busy={busy ? 'true' : undefined} ref={ref} tabIndex={-1}>
        <Panel store={props.store} copy={c} busy={busy} icon={STATE_ICON[state]} tone={state} titleId="oo-tbl-gate-title" messageId="oo-tbl-gate-msg">
          {c.retry ? <ui.Button size="md" block onClick={props.onRetry} busy={props.retrying}>{c.retry}</ui.Button> : null}
          {state === 'disabled' && props.online ? <OnlineLink href={props.online} /> : null}
        </Panel>
      </div>
    );
  }

  // P4-03: the guest welcome (see the header; REF TableWelcome, in our sentence case). plan = welcomeOf's.
  function Welcome(props) {
    var ui = OO.ui;
    var plan = props.plan;
    var ref = useFocusOnOpen();
    var countS = React.useState(plan.initial);
    var count = clampGuests(countS[0], plan.max);
    var table = props.table && props.table.name ? props.table.name : 'your table';
    var store = props.store ? storeLabel(props.store.name) : 'the restaurant';
    function guests(n) { return n + (n === 1 ? ' guest' : ' guests'); }
    function pick(n) { countS[1](clampGuests(n, plan.max)); }
    function done() { setGuests(props.hotel, count); }
    return (
      <div className="oo-tbl-gate oo-tbl-welcome" role="dialog" aria-modal="true" aria-labelledby="oo-tbl-welcome-title"
        aria-describedby="oo-tbl-welcome-msg" ref={ref} tabIndex={-1}>
        <div className="oo-tbl-panel">
          <Brand store={props.store} />
          <div className="oo-tbl-card">
            <span className="oo-tbl-card__icon" aria-hidden="true">{ui.renderIcon('User', { size: 26 })}</span>
            <p className="oo-tbl-card__eyebrow">Table-side ordering</p>
            <h1 className="oo-tbl-card__title" id="oo-tbl-welcome-title">{'Welcome to ' + store}</h1>
            <p className="oo-tbl-card__message" id="oo-tbl-welcome-msg">{'Your dine-in order is linked to ' + table + '.'}</p>
            <section className="oo-tbl-party" aria-labelledby="oo-tbl-party-h">
              <div className="oo-tbl-party__head">
                <div className="oo-tbl-party__titles">
                  <h2 className="oo-tbl-party__title" id="oo-tbl-party-h">Party size</h2>
                  <p className="oo-tbl-party__sub">{'Guests at ' + table}</p>
                </div>
                <span className="oo-tbl-party__count">{guests(count)}</span>
              </div>
              <div className="oo-tbl-party__stepper">
                <button type="button" className="oo-tbl-party__step" aria-label="Fewer guests" disabled={count <= 1}
                  onClick={function () { pick(count - 1); }}>{ui.renderIcon('Minus', { size: 20 })}</button>
                <output className="oo-tbl-party__value" aria-live="polite" aria-label={guests(count)}>{count}</output>
                <button type="button" className="oo-tbl-party__step" aria-label="More guests" disabled={count >= plan.max}
                  onClick={function () { pick(count + 1); }}>{ui.renderIcon('Plus', { size: 20 })}</button>
              </div>
              {plan.seats ? <p className="oo-tbl-party__seats">{'Up to ' + plan.seats + ' seats at this table'}</p> : null}
              <div className="oo-tbl-party__picks" role="group" aria-label="Quick picks">
                {quickPicks(plan.max).map(function (n) {
                  return (
                    <button key={n} type="button" className={ui.cx('oo-tbl-party__pick', n === count && 'is-on')} aria-label={guests(n)}
                      aria-pressed={n === count ? 'true' : 'false'} onClick={function () { pick(n); }}>{n}</button>
                  );
                })}
              </div>
            </section>
            <ui.Button size="lg" block iconRight="ChevronRight" onClick={done}>{props.menu ? 'See the menu' : 'Continue'}</ui.Button>
            <p className="oo-tbl-party__note">You can let your server know if this changes.</p>
          </div>
        </div>
      </div>
    );
  }

  // The host on a store's pages (see the header).
  function Host(props) {
    var route = props.route || {};
    var hotel = route.hotel;
    React.useSyncExternalStore(subscribe, version, version);
    OO.useStore(function (s) { return s.table; });
    var h = OO.useHotel(hotel, { withMenu: false });
    var ctx = context(hotel);
    var eid = entryId(route);
    var entry = eid !== null;
    var idOk = entry && !isNaN(eid);
    var id = subjectOf(hotel, route, true); // the table in question (see subjectOf)
    var c = peek(hotel, id);
    var ctxHere = ctx && ctx.id === id ? ctx : null;
    var retryS = React.useState(false);
    var Start = OO.pages.QrStart;
    var startScope = Start ? Start.scopeOf(route, props.mode || OO.mode, h.store, ctxHere) : null;
    var startPending = Start ? Start.usePending(startScope) : false;

    // Check the table once per page load (again when an 'open' check's context has gone, e.g. expired).
    React.useEffect(function () {
      if (!hotel || id === null) return;
      var cur = peek(hotel, id);
      if (!cur || (cur.status === 'open' && !ctxHere)) check(hotel, id); // a failed read shows on the gate (handled there)
    }, [hotel, id, !!ctxHere]);
    // The context ends at its expiry (2 h; a check renews it), also while the page stays open.
    var held = OO.store.get().table;
    var exp = held && sameKey(held.hotel, hotel) ? held.expiresAt : null;
    React.useEffect(function () {
      if (!exp) return undefined;
      var t = setTimeout(function () {
        var cur = OO.store.get().table;
        if (cur && cur.expiresAt === exp) OO.store.clearTable();
      }, Math.max(0, Math.min(exp - Date.now(), 2147483647)));
      return function () { clearTimeout(t); };
    }, [exp]);

    // The store's table ordering off: the paused gate at once, from the loaded store, context or not (nothing is ordered
    // at a table meanwhile; store.jsx reads no table menu for it and drops the context), on checkout too (QR-OFF).
    var paused = pausedOn(route.page, h.store);
    var state = paused ? 'disabled' : gateState({ page: route.page, entry: entry, idOk: idOk, context: ctxHere, check: c });
    var over = null;
    if (!state) {
      // P4-03: the guest welcome, where the store asks and the table is open (see the header).
      var plan = welcomeOf({ page: route.page, context: ctxHere, check: c, guestForm: guestFormOf(h.store, hotel) });
      if (plan && !startPending) over = <Welcome key={ctxHere.hotel + '|' + ctxHere.id} hotel={hotel} store={h.store} table={ctxHere} plan={plan} menu={route.page === 'menu'} />;
    } else {
      var retry = function () {
        var done = function () { retryS[1](false); };
        // "Check again" on the paused gate re-reads the store (with the table when there is one in question).
        var p = id !== null ? check(hotel, id, { fresh: true, freshStore: state === 'disabled' })
          : state === 'disabled' && typeof OO.data.rereadStore === 'function' ? OO.data.rereadStore(hotel) : null;
        if (!p) return;
        retryS[1](true);
        p.then(done, done);
      };
      var online = state === 'disabled' && offersOnline(h.store) ? onlineHref(hotel) : null;
      over = <Gate state={state} store={h.store} tableName={c ? c.name : null} onRetry={retry} retrying={retryS[0] && state !== 'validating'} online={online} />;
    }
    // The page, then the gate or the welcome over it; QR-OFF: the 'disabled' gate stands in the page's place (the page
    // isn't rendered at all: no menu, cart, table or checkout under it).
    var page = props.page;
    if (Start && route.page === 'menu' && (state || startPending)) {
      // Validation still wins. Menu effects (including item/cart deep links) begin only after the starting step.
      page = !state && startPending ? <Start store={h.store} scope={startScope} /> : null;
    }
    return <React.Fragment>{state === 'disabled' ? null : page}{over}</React.Fragment>;
  }

  // OO.App mounts it as the page on 'scan', and on a store's other pages in mode qr in the page's place with the page as
  // `page` (the host renders it; see the header).
  OO.pages.Table = function Table(props) {
    var route = (props && props.route) || {};
    if (route.page === 'scan') return route.hotel ? <StoreLanding hotel={route.hotel} /> : <GenericLanding />;
    if (!route.hotel) return props.page || null;
    return <Host route={route} mode={props.mode} page={props.page} />;
  };
  OO.pages.Table.context = context;
  OO.pages.Table.revalidate = revalidate;
  OO.pages.Table.status = status;
  OO.pages.Table.setGuests = setGuests;
  OO.pages.Table.logic = {
    onlineHref: onlineHref, offersOnline: offersOnline, storeLabel: storeLabel, copy: copy, gateState: gateState, entryId: entryId,
    welcomeOf: welcomeOf, quickPicks: quickPicks, clampGuests: clampGuests, pausedOn: pausedOn,
  };
  // The dialogs, presentational (the render and interaction checks use them): <Gate state store tableName onRetry
  // retrying online /> (online: the 'disabled' state's "Order pickup or delivery" href, or null), <Welcome hotel store
  // table plan menu /> (plan = welcomeOf's; Continue calls setGuests).
  OO.pages.Table.parts = { Gate: Gate, Welcome: Welcome };
  // Tests: forget every check (a page reload).
  OO.pages.Table.__reset = function () { checks = {}; latest = {}; emit(); };
})();


// ===================== src/pages/landing-sections.jsx =====================
// src/pages/landing-sections.jsx: the white-label landing page's typed section registry (docs/whitelabel/
// WHITELABEL-PLAN.md §5 Section types, §7.3; lane WL-3), its forms and its store chooser. The page (src/pages/landing.jsx)
// renders them in document order (OO.landing.sectionsOf). Styles: styles/landing.css (.oo-lp-*). Text from the document is
// rendered as text (React escapes it), never as HTML; URLs only when https (OO.landing.httpsUrl).
//
// <Section section ctx />, ctx = { key (the landing's store key), master (the chain master's key: the default store),
//   store (its Store, white-label overlaid) | null, menu | null, chain ({ chain, loading, failed }), order(query) (the
//   order hand-off: OO.landing.orderPlan, the chooser on a multi-store chain), sections (the ones shown), wide }:
//   offers       the default store's coupons (OO.api.getCoupons → OO.coupons.list, as the Deals page lists them; at most
//                `limit`), each with its words (OO.pages.Cart.shared.couponWords) → the default store's Deals page.
//   bestSellers  OO.api.getPopular(default store) → OO.model.popularProducts over its menu (at most `limit`) → the order
//                hand-off with the dish as the menu's deep link (?type=product&id=).
//   categories   the document's tiles (OO.ui.CategoryTile) → the order hand-off with ?type=category&id={categoryId}.
//   rewards      title, body, "Join {programme}" → the landing key's rewards page.
//   stores       the chain's list (OO.useChain: B31; the master first): name, address, open status, Directions (Google
//                Maps, a new tab), "Order here" → /{store}/menu. `map` is not drawn in phase 1 (no map component today).
//   stats · appPromo (App Store / Google Play links, https only) · about (paragraphs, image) · richText (paragraphs).
//   franchise    title, body and the form; contact: title, email (mailto) and phone (tel) links and the form (only when
//                the section has an email: that is its recipient, server side).
// Forms (<ContactForm sectionId siteKey />): name, email, phone, message, plus a honeypot `website` field people never
// see (off-screen, aria-hidden, tabIndex -1, autocomplete off). OO.landing.validate first, then OO.landing.submit; the
// server's 422 errors show per field. No reCAPTCHA. The recipient is never in the page or the request.
// <StoreChooser open onClose master query /> the order hand-off's store chooser: OO.pages.Stores.usePicker's list (search,
// location, open status) in a dialog, then "Order from {store}" → OO.store.setBranch + /{branchKey}/menu (+ query).
// WL-5: every data section (offers, bestSellers, categories, stores) follows OO.landing.dataState: a skeleton while its
// read is pending, for at most OO.landing.DEADLINE_MS (useLate), then the section, or nothing when the read failed, came
// back empty or never answered (the chain master's store unreadable: ctx.storeFailed). Never a skeleton for good.
// Ordering off (ctx.orderingOff, the information-only website): stores lists EVERY chain store from the store directory
// (OO.landing.loadStores: address, phone, opening hours, open now; "View menu" for a store with a menu) and never
// "Order here"; the store chooser offers "View menu at {store}". With ordering on, a store that can't take online
// orders (ctx.ordering.stores[key] false) shows "View menu" instead of "Order here".
// OO.pages.LandingSections = { Frame: Frame, Paras: Paras, REGISTRY: type → component, Section, ContactForm, StoreChooser, ChooserBody, go, useLate }.
//
// WL-CHK (opt-in; a section without the new keys renders exactly as before). Every Frame: `eyebrow` (a label above the
// title), the title line "title accent trail" (TitleText: `accent` and `trail` as their own spans), `watermark` (a giant
// aria-hidden background word), style.tone 'deep' and style.pattern (classes, OO.landing.styleClasses). Frame options for
// the variants: aside (the head row's right-hand content), noMore (the variant places `more` itself), headless (the
// variant draws <Head> inside its own layout), backdrop (a full-bleed layer under the content). A section with display
// 'dialog' (a content or form type: OO.landing.isDialog) is drawn by <SectionDialog> (OO.ui.Sheet, variant dialog: the
// Sheet carries the title; Frame draws the eyebrow, the subtitle, the body and the section's own `more` link,
// ctx.inDialog; a link in it closes the dialog first, ctx.closeDialog). Variants: stores layout 'nearest'
// (NearestStores: the chain's stores, nearest first when the browser already shares the location; "Use my location"
// asks only on a click), offers layout 'cards' (a carousel of deal cards), rewards layout 'showcase' (steps, CTA and a
// member card), about under the bold design (AboutBold: stats, CTA, background image), stats layout 'cards' (+ countUp,
// item icon/body), appPromo layout 'showcase' (a phone mock-up), and the new type `marquee` (a scrolling band with a
// Pause button). Under the bold design the about and the rewards showcase are dark bands when their tone is absent or
// 'default' (boldTone); an explicit tone wins and their text follows it. <Rail> is the bold carousels' scroller
// (scroll-snap, arrow keys, round arrow buttons at its sides, which follow the row's overflow); <CountUp> counts a stat
// up once it scrolls into view (the final value always in the accessible text; reduced motion: the final value).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  function L() { return OO.landing; }
  function cx() { return OO.ui.cx.apply(null, arguments); }
  function money(n, store) { return OO.format.money(n, store && store.currency); }
  function displayPrice(p) { return p.sizes && p.sizes.length ? p.sizes[0].price : p.price; }
  function words(c, cur) {
    var K = OO.pages.Cart && OO.pages.Cart.shared;
    try { return K && typeof K.couponWords === 'function' ? K.couponWords(c, cur) : null; } catch (e) { return null; }
  }

  // WL-CHK: the title line, "title accent trail"; without accent and trail it is the title text alone (as before).
  function TitleText(props) {
    var s = props.section;
    var title = L().text(s.title, 80), accent = L().text(s.accent, 60), trail = L().text(s.trail, 60);
    if (!accent && !trail) return title;
    return <>{title}{accent ? <>{title ? ' ' : null}<span className="oo-lp-accent">{accent}</span></> : null}{trail ? <>{' '}<span className="oo-lp-trail">{trail}</span></> : null}</>;
  }
  function Eyebrow(props) {
    var text = L().text(props.text, 40);
    if (!text) return null;
    return <p className="oo-lp-eyebrow">{L().eyebrowDot(text) ? <span className="oo-lp-eyebrow__dot" aria-hidden="true" /> : null}{text}</p>;
  }
  function Head(props) {
    var s = props.section;
    var title = L().text(s.title, 80);
    return <>{s.eyebrow ? <Eyebrow text={s.eyebrow} /> : null}{title ? <h2 className="oo-lp-sec__title" id={L().anchorId(s.id) + '-t'}><TitleText section={s} /></h2> : null}{L().text(s.subtitle, 160) ? <p className="oo-lp-sec__subtitle">{L().text(s.subtitle, 160)}</p> : null}</>;
  }
  function Frame(props) {
    var s = props.section;
    var titled = !!L().text(s.title, 80);
    var watermark = L().text(s.watermark, 24);
    var more = s.more && props.ctx && !props.noMore ? <div className="oo-lp-more"><OO.pages.Landing.parts.TargetLink className="oo-lp-link" target={s.more.target} label={s.more.label} ctx={props.ctx} /></div> : null;
    // WL-CHK: inside a section dialog the Sheet carries the title (drawn once there): the eyebrow, the subtitle, the
    // section's own controls and body, and its `more` link.
    if (props.ctx && props.ctx.inDialog) {
      var subtitle = L().text(s.subtitle, 160);
      return <div className={cx('oo-lp-dlg', 'oo-lp-dlg--' + s.type, props.className)}><Eyebrow text={s.eyebrow} />{subtitle ? <p className="oo-lp-sec__subtitle">{subtitle}</p> : null}{props.aside || null}{props.children}{more}</div>;
    }
    var head = props.headless ? null : props.aside ? <div className="oo-lp-sec__head"><div className="oo-lp-sec__heading"><Head section={s} /></div>{props.aside}</div> : <Head section={s} />;
    var wrap = (
      <div className="oo-lp-wrap">
        {head}
        {props.children}
        {more}
      </div>
    );
    // The backdrop and the watermark only add children when present: a section without them keeps exactly the element
    // tree it had (React derives useId values, e.g. the form fields', from that tree).
    var decor = props.backdrop || watermark;
    return (
      <section id={L().anchorId(s.id)} tabIndex={-1} className={cx('oo-lp-sec', 'oo-lp-sec--' + s.type, props.className, L().styleClasses(s), watermark && 'has-watermark')}
        aria-labelledby={titled ? L().anchorId(s.id) + '-t' : undefined} aria-label={titled ? undefined : props.label}>
        {decor ? <>{props.backdrop || null}{watermark ? <span className="oo-lp-watermark" aria-hidden="true"><span>{watermark}</span></span> : null}{wrap}</> : wrap}
      </section>
    );
  }

  // ---- WL-CHK shared pieces -----------------------------------------------------------------------------------------
  function reducedNow() { try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }
  function useReduced() { return OO.pages.Landing.logic.useReducedMotion(); }
  function Glyph(props) { return <OO.landingIcons.Glyph name={props.name} size={props.size} className={props.className} strokeWidth={props.strokeWidth} />; }
  // <Rail id label className>{<li>…}</Rail>: the bold carousels. A scroll-snap list (Tab-focusable, ← / → scroll it) with
  // round arrow buttons at its sides; an arrow is disabled (and hidden) at its end of the row. The arrows are measured
  // again after every render (the skeleton swapped for the cards changes the overflow without a scroll or resize
  // event), on scroll, on window resizes and, where ResizeObserver exists, whenever the track's own size changes.
  function Rail(props) {
    var ref = React.useRef(null);
    var edges = React.useState({ prev: false, next: true });
    var id = L().anchorId(props.id) + '-rail';
    function sync() {
      var el = ref.current;
      if (!el) return;
      var prev = el.scrollLeft > 8, next = el.scrollLeft < el.scrollWidth - el.clientWidth - 8;
      edges[1](function (cur) { return cur.prev === prev && cur.next === next ? cur : { prev: prev, next: next }; });
    }
    React.useLayoutEffect(function () { sync(); });
    React.useEffect(function () {
      var el = ref.current;
      var ro = el && typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(function () { sync(); }) : null;
      if (ro) ro.observe(el);
      window.addEventListener('resize', sync);
      return function () { window.removeEventListener('resize', sync); if (ro) ro.disconnect(); };
    }, []);
    function move(dir) {
      var el = ref.current;
      if (!el) return;
      el.scrollBy({ left: dir * Math.max(240, el.clientWidth * .8), behavior: reducedNow() ? 'auto' : 'smooth' });
    }
    return (
      <div className={cx('oo-lp-rail', props.className)} role="region" aria-roledescription="carousel" aria-label={props.label}>
        <ul className="oo-lp-rail__track" ref={ref} id={id} tabIndex={0} aria-label={props.label} onScroll={sync}
          onKeyDown={function (e) { if (e.target !== e.currentTarget || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return; e.preventDefault(); move(e.key === 'ArrowLeft' ? -1 : 1); }}>
          {props.children}
        </ul>
        <button type="button" className="oo-lp-rail__arrow oo-lp-rail__arrow--prev" aria-controls={id} aria-label={'Previous ' + props.label} disabled={!edges[0].prev} onClick={function () { move(-1); }}><Glyph name="chevron-left" size={22} strokeWidth={2.6} /></button>
        <button type="button" className="oo-lp-rail__arrow oo-lp-rail__arrow--next" aria-controls={id} aria-label={'Next ' + props.label} disabled={!edges[0].next} onClick={function () { move(1); }}><Glyph name="chevron-right" size={22} strokeWidth={2.6} /></button>
      </div>
    );
  }
  // <CountUp value on /> a stat value that counts up from 0 once it scrolls into view (about 1.5 s), when `on` and motion
  // isn't reduced; the accessible text is always the final value. Without IntersectionObserver: the final value.
  var COUNT_MS = 1500;
  function CountUp(props) {
    var ref = React.useRef(null);
    var parts = props.on ? L().countParts(props.value) : null;
    var shown = React.useState(null);
    React.useLayoutEffect(function () {
      var el = ref.current;
      if (!parts || !el || reducedNow() || typeof window.IntersectionObserver !== 'function' || typeof window.requestAnimationFrame !== 'function') return undefined;
      var raf = 0, t0 = 0;
      shown[1](L().countText(parts, 0));
      function tick(ts) {
        if (!t0) t0 = ts;
        var t = (ts - t0) / COUNT_MS;
        shown[1](t >= 1 ? null : L().countText(parts, t));
        if (t < 1) raf = window.requestAnimationFrame(tick);
      }
      var io = new window.IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        raf = window.requestAnimationFrame(tick);
      }, { threshold: .4 });
      io.observe(el);
      return function () { io.disconnect(); if (raf) window.cancelAnimationFrame(raf); };
    }, [props.value, !!props.on]);
    var value = L().text(props.value, 12);
    if (!parts) return value;
    return <span ref={ref} className="oo-lp-count"><span aria-hidden="true">{shown[0] === null ? value : shown[0]}</span><span className="oo-sr-only">{value}</span></span>;
  }
  // WL-CHK: a section with display 'dialog', opened by a link to it (OO.ui.Sheet, variant dialog). A link inside it
  // closes it before it goes anywhere (ctx.closeDialog: OO.pages.Landing.parts.TargetLink).
  function SectionDialog(props) {
    var s = props.section, ctx = props.ctx;
    var title = s ? L().text(s.title, 80) : '';
    return (
      <OO.ui.Sheet open={!!s} onClose={props.onClose} variant="dialog" title={s && title ? <TitleText section={s} /> : undefined}
        label={s && !title ? 'More' : undefined} className={cx('oo-lp-dialog', s && 'oo-lp-dialog--' + s.type, ctx && ctx.bold && 'oo-lp-dialog--bold', ctx && ctx.superDesign && 'oo-lp-dialog--super', ctx && ctx.boutique && 'oo-lp-dialog--boutique' /* WL-VEL */)}>
        {s ? <Section section={s} ctx={Object.assign({}, ctx, { inDialog: true, closeDialog: props.onClose })} /> : null}
      </OO.ui.Sheet>
    );
  }
  // WL-CHK: the bold about and rewards showcase are dark bands by design: with no tone, or 'default' (the studio shows the
  // two alike), they take the dark tone; any other tone wins, and the CSS lets their text follow it.
  var OWN_TONES = ['muted', 'brand', 'accent', 'dark', 'deep'];
  function boldTone(s) {
    var style = s.style && typeof s.style === 'object' && !Array.isArray(s.style) ? s.style : {};
    return OWN_TONES.indexOf(style.tone) >= 0 ? s : Object.assign({}, s, { style: Object.assign({}, style, { tone: 'dark' }) });
  }
  function Paras(props) {
    return L().paragraphs(props.text).map(function (p, i) { return <p key={i} className="oo-lp-p">{p}</p>; });
  }
  function limitOf(s, n) { var x = Math.floor(Number(s.limit)); return x >= 1 && x <= 20 ? x : n; }

  // WL-5: true once `active` has stayed true for OO.landing.DEADLINE_MS (8 s): a read that never answers. The sections
  // call it through OO.pages.LandingSections.useLate (late()), so a test can stand in for the clock.
  function late(active) { return OO.pages.LandingSections.useLate(active); }
  function useLate(active) {
    var st = React.useState(false);
    React.useEffect(function () {
      if (!active) { st[1](false); return undefined; }
      var t = setTimeout(function () { st[1](true); }, L().DEADLINE_MS);
      return function () { clearTimeout(t); };
    }, [!!active]);
    return !!active && st[0];
  }
  function lowerKey(k) { return String(k == null ? '' : k).trim().toLowerCase(); }

  // ---- offers ---------------------------------------------------------------------------------------------------
  function OffersSection(props) {
    var s = props.section, ctx = props.ctx, store = ctx.store;
    var on = !!store && !!OO.coupons && OO.coupons.enabled(store);
    var q = OO.useAsync(function () { return on ? OO.api.getCoupons(store.id) : Promise.resolve(null); }, [store ? store.id : null, on]);
    var list = on && q.data ? OO.coupons.list(q.data, store, { orderType: 'PICKUP' }).filter(function (c) { return c.available !== false; }) : [];
    list = list.slice(0, limitOf(s, 10));
    var deals = L().targetOf({ type: 'page', page: 'offers' }, { key: ctx.master });
    // WL-5: a skeleton while the store or its coupons load (at most 8 s); nothing when they fail or there is no offer.
    var loading = !!ctx.storeLoading || (on && q.loading);
    var isLate = late(loading);
    var state = L().dataState({ loading: loading, failed: !!ctx.storeFailed || (on && !!q.error), empty: !list.length, late: isLate });
    if (state === 'hide') return null;
    if (s.layout === 'cards') return <OfferCards section={s} ctx={ctx} list={list} wait={state === 'wait'} deals={deals} />;
    var body;
    if (state === 'wait') body = <div className="oo-lp-grid oo-lp-grid--offers" aria-hidden="true">{[0, 1, 2].map(function (i) { return <span key={i} className="oo-lp-skel" />; })}</div>;
    else {
      body = (
        <ul className="oo-lp-grid oo-lp-grid--offers">
          {list.map(function (c) {
            var w = words(c, store.currency) || {};
            return (
              <li key={c.id} className="oo-lp-offer">
                {w.type ? <span className="oo-lp-offer__tag">{w.type}</span> : null}
                <span className="oo-lp-offer__name">{c.name || w.offer || 'Special offer'}</span>
                {w.offer && c.name ? <span className="oo-lp-offer__words">{w.offer}</span> : null}
                {c.code ? <span className="oo-lp-offer__code">{'Code ' + c.code}</span> : null}
              </li>
            );
          })}
        </ul>
      );
    }
    return (
      <Frame section={s} ctx={props.ctx} label="Offers">
        {body}
        {deals ? <div className="oo-lp-more"><a className="oo-lp-link" href={deals.href} onClick={go(deals.href)}>See all deals</a></div> : null}
      </Frame>
    );
  }

  // WL-CHK: offers layout 'cards'. A carousel of deal cards: the image of a product the coupon names (the default store's
  // menu), else the section's `image`, else a tinted block; the deal's tag (primary for a free item, accent for money or
  // percent off), its name and "View deal" → the Deals page. "View all deals" sits in the head row.
  function offerTag(c, cur) {
    var w = words(c, cur) || {};
    var pct = Number(c.percent) || 0;
    if (c.type === 'ITEM_DISCOUNT' && pct >= 100) return { text: 'Free item', tone: 'primary' };
    if (c.type === 'CART_DISCOUNT' || c.type === 'ITEM_DISCOUNT') {
      if (OO.coupons && OO.coupons.isAmount && OO.coupons.isAmount(c)) return { text: money(pct, { currency: cur }) + ' off', tone: 'accent' };
      if (pct > 0) return { text: pct + '% off', tone: 'accent' };
    }
    return w.type ? { text: w.type, tone: 'primary' } : null;
  }
  function OfferCards(props) {
    var s = props.section, ctx = props.ctx, store = ctx.store, deals = props.deals;
    var fallback = L().httpsUrl(s.image);
    var menu = ctx.builderMenu || ctx.menu;
    var aside = deals ? <a className="oo-lp-sec__aside-link" href={deals.href} onClick={go(deals.href)}>View all deals<Glyph name="arrow-right" size={16} /></a> : null;
    var cards = props.wait ? [0, 1, 2].map(function (i) { return <li key={i} aria-hidden="true"><span className="oo-lp-skel oo-lp-deal oo-lp-deal--skel" /></li>; }) : props.list.map(function (c) {
      var tag = offerTag(c, store.currency);
      var hit = (Array.isArray(c.itemIds) ? c.itemIds : []).map(function (id) { return menu && menu.products ? menu.products[id] : null; }).filter(function (p) { return p && p.image; })[0];
      var image = hit ? hit.image : fallback;
      var inner = (
        <>
          <span className={cx('oo-lp-deal__media', !image && 'is-empty')}>{image ? <img src={image} alt="" loading="lazy" /> : null}</span>
          <span className="oo-lp-deal__body">
            {tag ? <span className={cx('oo-lp-deal__tag', 'oo-lp-deal__tag--' + tag.tone)}>{tag.text}</span> : null}
            <span className="oo-lp-deal__name">{c.name || (words(c, store.currency) || {}).offer || 'Special offer'}</span>
            {deals ? <span className="oo-lp-deal__more">View deal<Glyph name="arrow-right" size={14} /></span> : null}
          </span>
        </>
      );
      return <li key={c.id}>{deals ? <a className="oo-lp-deal" href={deals.href} onClick={go(deals.href)}>{inner}</a> : <div className="oo-lp-deal">{inner}</div>}</li>;
    });
    return <Frame section={s} ctx={ctx} label="Offers" className="oo-lp-offers" aside={aside} noMore><Rail id={s.id} label={L().text(s.title, 80) || 'Offers'} className="oo-lp-rail--deals">{cards}</Rail></Frame>;
  }

  // ---- best sellers ----------------------------------------------------------------------------------------------
  function BestSellersSection(props) {
    var s = props.section, ctx = props.ctx, store = ctx.store;
    var q = OO.useAsync(function () { return ctx.master ? OO.api.getPopular(ctx.master) : Promise.resolve(null); }, [ctx.master]);
    var list = q.data && ctx.menu ? OO.model.popularProducts(q.data, ctx.menu) : null;
    list = list ? list.filter(function (p) { return !p.soldOut; }).slice(0, limitOf(s, 8)) : null;
    // WL-5: a skeleton while the popular dishes or the menu load (at most 8 s); nothing when either fails or is empty.
    var failed = !!ctx.storeFailed || !!q.error || (!q.loading && !q.data);
    var loading = !failed && !list;
    var isLate = late(loading);
    var state = L().dataState({ loading: loading, failed: failed, empty: !!list && !list.length, late: isLate });
    var body;
    if (state === 'hide') return null;
    if (state === 'wait') body = <div className="oo-lp-grid oo-lp-grid--dishes" aria-hidden="true">{[0, 1, 2, 3].map(function (i) { return <span key={i} className="oo-lp-skel oo-lp-skel--tall" />; })}</div>;
    else {
      body = (
        <ul className="oo-lp-grid oo-lp-grid--dishes">
          {list.map(function (p) {
            var price = displayPrice(p);
            return (
              <li key={p.id}>
                <button type="button" className="oo-lp-dish" onClick={function () { ctx.order({ type: 'product', id: String(p.id) }); }}>
                  <span className="oo-lp-dish__media">{p.image ? <img src={p.image} alt="" loading="lazy" /> : <OO.icons.ImagePlaceholder size={26} />}</span>
                  <span className="oo-lp-dish__name">{p.name}</span>
                  {typeof price === 'number' ? <span className="oo-lp-dish__price">{money(price, store)}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      );
    }
    return <Frame section={s} ctx={props.ctx} label="Best sellers">{body}</Frame>;
  }

  // ---- categories -----------------------------------------------------------------------------------------------
  function CategoriesSection(props) {
    var s = props.section, ctx = props.ctx, ui = OO.ui;
    var tiles = (Array.isArray(s.tiles) ? s.tiles : []).filter(function (t) {
      return t && L().text(t.label, 24) && Number.isInteger(t.categoryId) && t.categoryId > 0;
    }).slice(0, 12);
    // WL-5: the tiles open the default store's menu: none when it can't be read (failed, or still loading after 8 s).
    var isLate = late(!ctx.menu && !ctx.storeFailed);
    if (!tiles.length || ctx.storeFailed || isLate || !ctx.menu && ctx.storeLoading === false || ctx.menu && !L().menuCategories(ctx.menu).length) return null;
    if (OO.pages.LandingBlocks && (s.style || s.variant || s.columns)) return <OO.pages.LandingBlocks.Categories section={s} ctx={ctx} />;
    return (
      <Frame section={s} ctx={props.ctx} label="Categories">
        <ui.CategoryGrid variant="image" className="oo-lp-cats">
          {tiles.map(function (t, i) {
            return (
              <ui.CategoryTile key={t.categoryId + '-' + i} name={L().text(t.label, 24)} image={L().httpsUrl(t.image)} variant="image"
                onClick={function () { ctx.order({ type: 'category', id: String(t.categoryId) }); }} />
            );
          })}
        </ui.CategoryGrid>
      </Frame>
    );
  }

  // ---- rewards ----------------------------------------------------------------------------------------------------
  function RewardsSection(props) {
    var s = props.section, ctx = props.ctx, ui = OO.ui;
    if (s.layout === 'showcase') return <RewardsShowcase section={s} ctx={ctx} />;
    var program = ctx.store && ctx.store.loyalty ? ctx.store.loyalty.programName : '';
    var href = OO.router.href('rewards', null, { hotel: ctx.key });
    // WL-CHK: a section `cta` replaces "Join {programme}" (absent: as before).
    var cta = s.cta && L().text(s.cta.label, 24) ? <OO.pages.Landing.parts.TargetLink className="oo-btn oo-btn--primary oo-btn--md" target={s.cta.target} label={L().text(s.cta.label, 24)} ctx={ctx} button /> : null;
    return (
      <Frame section={s} ctx={props.ctx} label="Rewards" className="oo-lp-band">
        <div className="oo-lp-band__body">
          <span className="oo-lp-band__icon" aria-hidden="true"><OO.icons.Gift size={28} /></span>
          <Paras text={s.body} />
          {cta || <ui.Button size="md" href={href} onClick={go(href)}>{program ? 'Join ' + program : 'Join rewards'}</ui.Button>}
        </div>
      </Frame>
    );
  }
  // WL-CHK: rewards layout 'showcase'. Left: eyebrow, title (the accent on its own line), body, the steps as numbered
  // tiles and the CTA (`cta`, else "Join {programme}" → rewards). Right: a member card: the programme (the store's loyalty
  // name, else the brand), GUEST / MEMBER, the points balance (masked signed out; signed in, the balance of a wallet read
  // this page load has already settled, OO.store.wallet: the landing never asks for one itself; none, no row), "Members
  // only", the featured `reward`, a bar to sign in (signed in: "View rewards", the rewards page); the `bonus` chip and a
  // coin float over its corners (a few seconds of motion; static under reduced motion).
  function RewardsShowcase(props) {
    var ctx = props.ctx, s = ctx.bold ? boldTone(props.section) : props.section;
    var st = OO.useStore(function (x) { return { session: x.session, wallets: x.wallets }; });
    var signedIn = !!st.session;
    var wallet = signedIn && OO.store && typeof OO.store.wallet === 'function' && OO.wallet ? OO.wallet.usable(OO.store.wallet(ctx.key)) : null;
    var balance = wallet ? OO.wallet.balance(wallet) : null;
    var program = (ctx.store && ctx.store.loyalty && L().text(ctx.store.loyalty.programName, 40)) || L().text(ctx.brand, 60) || 'Rewards';
    var rewards = OO.router.href('rewards', null, { hotel: ctx.key });
    var account = OO.router.href('account', null, { hotel: ctx.key });
    var steps = (Array.isArray(s.steps) ? s.steps : []).map(function (x) { return L().text(x, 24); }).filter(Boolean).slice(0, 4);
    var reward = L().text(s.reward, 40), bonus = L().text(s.bonus, 16);
    var Link = OO.pages.Landing.parts.TargetLink;
    var cta = s.cta && L().text(s.cta.label, 24)
      ? <Link className="oo-lp-show__cta" target={s.cta.target} label={L().text(s.cta.label, 24)} ctx={ctx} icon={<Glyph name="arrow-right" size={16} />} />
      : <a className="oo-lp-show__cta" href={rewards} onClick={go(rewards)}>{'Join ' + program}<Glyph name="arrow-right" size={16} /></a>;
    var bar = signedIn ? rewards : account;
    return (
      <Frame section={s} ctx={ctx} label="Rewards" className="oo-lp-show" headless>
        <div className="oo-lp-show__grid">
          <div className="oo-lp-show__text">
            <Head section={s} />
            <Paras text={s.body} />
            {steps.length ? <ol className="oo-lp-show__steps">{steps.map(function (x, i) { return <li key={i}><span className="oo-lp-show__num" aria-hidden="true">{i + 1}</span><span>{x}</span></li>; })}</ol> : null}
            {cta}
          </div>
          <div className="oo-lp-show__card-wrap">
            <div className="oo-lp-member">
              <span className="oo-lp-member__shadow" aria-hidden="true" />
              <div className="oo-lp-member__card">
                <div className="oo-lp-member__head">
                  <span className="oo-lp-member__badge" aria-hidden="true"><Glyph name="star" size={18} /></span>
                  <span className="oo-lp-member__who"><span className="oo-lp-member__name">{program}</span><span className="oo-lp-member__role">Rewards member</span></span>
                  <span className="oo-lp-member__status"><span className="oo-lp-member__live" aria-hidden="true" />{signedIn ? 'Member' : 'Guest'}</span>
                </div>
                {signedIn && typeof balance !== 'number' ? null : (
                  <div className="oo-lp-member__balance">
                    <span className="oo-lp-member__k">Points balance</span>
                    <span className="oo-lp-member__pts">
                      {signedIn ? <span className="oo-lp-member__value">{balance.toLocaleString()}</span> : <span className="oo-lp-member__value oo-lp-member__value--masked"><span aria-hidden="true">••••</span><span className="oo-sr-only">Hidden until you sign in</span></span>}
                      <span className="oo-lp-member__unit">pts</span>
                      {signedIn ? null : <span className="oo-lp-member__lock" aria-hidden="true"><Glyph name="lock" size={14} />Sign in</span>}
                    </span>
                  </div>
                )}
                <p className="oo-lp-member__note"><span aria-hidden="true" className="oo-lp-member__dot" />Members only</p>
                <span className="oo-lp-member__rule" aria-hidden="true" />
                {reward ? <p className="oo-lp-member__reward"><span className="oo-lp-member__k">Featured reward</span><span className="oo-lp-member__reward-name">{reward}</span></p> : null}
                <a className="oo-lp-member__bar" href={bar} onClick={go(bar)}>
                  <span>{signedIn ? null : <Glyph name="sign-in" size={14} />}{signedIn ? 'View rewards' : 'Sign in to unlock'}</span><Glyph name="arrow-right" size={16} />
                </a>
              </div>
              {bonus ? <span className="oo-lp-member__bonus" aria-hidden="true"><Glyph name="star" size={12} />{bonus}</span> : null}
              <span className="oo-lp-member__coin" aria-hidden="true"><span>$</span></span>
            </div>
          </div>
        </div>
      </Frame>
    );
  }

  // ---- stores -----------------------------------------------------------------------------------------------------
  function StoresSkeleton() {
    return <div className="oo-lp-grid oo-lp-grid--stores" aria-hidden="true">{[0, 1, 2].map(function (i) { return <span key={i} className="oo-lp-skel" />; })}</div>;
  }
  // Ordering on: the chain's stores a customer can order from (OO.useChain), each with Directions and "Order here" (WL-5:
  // "View menu" where that store can't take online orders).
  function ChainStores(props) {
    var s = props.section, ctx = props.ctx, ui = OO.ui;
    var c = ctx.chain.chain;
    var list = c ? c.stores : [];
    var loading = !c && ctx.chain.loading;
    var isLate = late(loading);
    var state = L().dataState({ loading: loading, failed: !c && !ctx.chain.loading, empty: !list.length, late: isLate });
    if (state === 'hide') return null;
    var stores = ctx.ordering && ctx.ordering.stores ? ctx.ordering.stores : null;
    var body;
    if (state === 'wait') body = <StoresSkeleton />;
    else {
      body = (
        <ul className="oo-lp-grid oo-lp-grid--stores">
          {list.map(function (b) {
            var dir = L().directionsUrl(b);
            var menu = L().menuHref(b.key);
            var canOrder = !(stores && stores[lowerKey(b.key)] === false);
            return (
              <li key={b.key} className="oo-lp-store">
                <span className="oo-lp-store__name">{b.name}</span>
                {b.address ? <span className="oo-lp-store__addr">{b.address}</span> : null}
                {b.status && b.status.label ? <ui.StatusPill open={b.open}>{b.status.label}</ui.StatusPill> : null}
                <span className="oo-lp-store__acts">
                  {dir ? <a className="oo-lp-link" href={dir} target="_blank" rel="noopener noreferrer" aria-label={'Directions to ' + b.name}>Directions</a> : null}
                  {canOrder
                    ? <ui.Button size="sm" href={menu} onClick={go(menu)} aria-label={'Order here: ' + b.name}>Order here</ui.Button>
                    : <ui.Button size="sm" variant="outline" href={menu} onClick={go(menu)} aria-label={'View the menu: ' + b.name}>View menu</ui.Button>}
                </span>
              </li>
            );
          })}
        </ul>
      );
    }
    return <Frame section={s} ctx={props.ctx} label="Stores">{body}</Frame>;
  }
  // WL-5, ordering off: EVERY chain store from the store directory (orderable or not): address and Directions, phone,
  // opening hours, open now, and "View menu" for a store with a menu. No "Order here".
  function DirectoryStores(props) {
    var s = props.section, ctx = props.ctx, ui = OO.ui;
    var key = ctx.master || ctx.key;
    var q = OO.useAsync(function () { return L().loadStores(key); }, [key]);
    var list = Array.isArray(q.data) ? q.data : [];
    var loading = !!q.loading;
    var isLate = late(loading);
    var state = L().dataState({ loading: loading, failed: !!q.error || (!q.loading && !Array.isArray(q.data)), empty: !list.length, late: isLate });
    if (state === 'hide') return null;
    var body;
    if (state === 'wait') body = <StoresSkeleton />;
    else {
      body = (
        <ul className="oo-lp-grid oo-lp-grid--stores">
          {list.map(function (b, i) {
            var dir = L().directionsUrl(b);
            var tel = L().telHref(b.phone);
            var lines = L().hoursLines(b.hours);
            var menu = b.key && (b.menuAvailable || b.orderable) ? L().menuHref(b.key) : null;
            var fallback = L().targetOf({ type: 'order' }, Object.assign({}, ctx, { phone: b.phone, directions: dir, menuUnavailable: !menu }));
            return (
              <li key={(b.key || 'store') + '-' + i} className="oo-lp-store">
                <span className="oo-lp-store__name">{b.name}</span>
                {b.address ? <span className="oo-lp-store__addr">{b.address}</span> : null}
                {b.label ? <ui.StatusPill open={b.open}>{b.label}</ui.StatusPill> : null}
                {b.phone ? <span className="oo-lp-store__line">{tel ? <a className="oo-lp-link" href={tel}>{b.phone}</a> : b.phone}</span> : null}
                {lines.length ? (
                  <dl className="oo-lp-hours" aria-label={'Opening hours: ' + b.name}>
                    {lines.map(function (l) {
                      return <div key={l.days} className="oo-lp-hours__row"><dt>{l.days}</dt><dd>{l.time}</dd></div>;
                    })}
                  </dl>
                ) : null}
                <span className="oo-lp-store__acts">
                  {dir ? <a className="oo-lp-link" href={dir} target="_blank" rel="noopener noreferrer" aria-label={'Directions to ' + b.name}>Directions</a> : null}
                  {fallback && fallback.kind === 'order' && menu ? <ui.Button size="sm" variant="outline" href={menu} onClick={go(menu)} aria-label={'View the menu: ' + b.name}>{fallback.label || 'View menu'}</ui.Button> : fallback ? <OO.pages.Landing.parts.TargetLink className="oo-lp-link" target={{ type: 'order' }} label="View menu" ctx={Object.assign({}, ctx, { phone: b.phone, directions: dir, menuUnavailable: !menu })} /> : null}
                </span>
              </li>
            );
          })}
        </ul>
      );
    }
    return <Frame section={s} ctx={props.ctx} label="Stores">{body}</Frame>;
  }
  // WL-CHK: stores layout 'nearest'. Up to `limit` (default 4) cards: the chain's stores (ordering on; OO.useChain's list)
  // or the store directory (ordering off). Location: only when the browser already shares it (permissions 'granted': the
  // cached fix, else one silent read) are the stores sorted nearest first, with their distance and the first one marked
  // NEAREST; otherwise a "Use my location" button asks, on a click only. Each card: the open status, the name, the address,
  // the distance (else Directions) and "Order now" → that store's menu ("View menu" where it can't take orders; ordering
  // off: landing.orderingOff, as the directory does). `more` sits in the head row; one that points back at this section
  // opens the store chooser (the full list).
  function lowerOf(k) { return String(k == null ? '' : k).trim().toLowerCase(); }
  function NearestStores(props) {
    var s = props.section, ctx = props.ctx;
    var Link = OO.pages.Landing.parts.TargetLink;
    var geo = OO.pages.FindStore && OO.pages.FindStore.geo ? OO.pages.FindStore.geo : null;
    var originS = React.useState(null), permS = React.useState(null), busyS = React.useState(false);
    React.useEffect(function () {
      var on = true;
      L().geoPermission().then(function (st) {
        if (!on) return;
        permS[1](st);
        if (st !== 'granted' || !geo) return;
        var cached = geo.read();
        if (cached) { originS[1](cached); return; }
        geo.locate().then(function (pos) { if (on) originS[1](pos); }, function () {});
      });
      return function () { on = false; };
    }, []);
    var off = !!ctx.orderingOff;
    var key = ctx.master || ctx.key;
    var q = OO.useAsync(function () { return off ? L().loadStores(key) : Promise.resolve(null); }, [off, key]);
    var c = ctx.chain && ctx.chain.chain;
    var source = off ? (Array.isArray(q.data) ? q.data : []) : (c ? c.stores : []);
    var loading = off ? !!q.loading : (!c && !!(ctx.chain && ctx.chain.loading));
    var failed = off ? (!!q.error || (!q.loading && !Array.isArray(q.data))) : (!c && !(ctx.chain && ctx.chain.loading));
    var isLate = late(loading);
    var state = L().dataState({ loading: loading, failed: failed, empty: !source.length, late: isLate });
    if (state === 'hide') return null;
    var n = Math.floor(Number(s.limit));
    var list = L().nearest(source, originS[0], n >= 1 && n <= 12 ? n : 4);
    var cut = off ? L().sharedPrefixWords(source.map(function (b) { return b.name; })) : 0; // the directory's short names
    var located = list.some(function (b) { return typeof b.distanceKm === 'number'; });
    var orderable = ctx.ordering && ctx.ordering.stores ? ctx.ordering.stores : null;
    function locate() {
      if (!geo || busyS[0]) return;
      busyS[1](true);
      geo.locate().then(function (pos) { busyS[1](false); permS[1]('granted'); originS[1](pos); }, function (e) { busyS[1](false); if (e && e.code === 'denied') permS[1]('denied'); });
    }
    var mt = s.more ? L().targetOf(s.more.target, ctx) : null;
    var moreLabel = L().text(s.more && s.more.label, 24) || 'View all';
    var more = !mt ? null : mt.kind === 'section' && mt.id === s.id
      ? <button type="button" className="oo-lp-near__all" onClick={ctx.chooseStore}>{moreLabel}<span aria-hidden="true"> →</span></button>
      : <Link className="oo-lp-near__all" target={s.more.target} ctx={ctx} label={moreLabel} icon={<span aria-hidden="true"> →</span>} />;
    var asks = !originS[0] && permS[0] === 'prompt' && !!geo;
    var aside = more || asks ? <div className="oo-lp-near__aside">{asks ? <button type="button" className="oo-lp-near__locate" onClick={locate} aria-busy={busyS[0] ? 'true' : undefined}><OO.landingIcons.Icon name="map-pin" size={14} />Use my location</button> : null}{more}</div> : null;
    var body = state === 'wait'
      ? <ul className="oo-lp-near" aria-hidden="true">{[0, 1, 2, 3].slice(0, n >= 1 && n <= 12 ? Math.min(n, 4) : 4).map(function (i) { return <li key={i} className="oo-lp-near__card oo-lp-near__card--skel"><span className="oo-lp-skel" /></li>; })}</ul>
      : (
        <ul className="oo-lp-near">
          {list.map(function (b, i) {
            var first = i === 0 && located;
            var dir = L().directionsUrl(b);
            var menu = b.key ? L().menuHref(b.key) : null;
            var label = off ? b.label : b.status && b.status.label;
            var canOrder = !off && !(orderable && orderable[lowerOf(b.key)] === false);
            var action = null;
            if (!off && menu) {
              action = <a className="oo-lp-near__go" href={menu} onClick={go(menu)} aria-label={(canOrder ? 'Order now: ' : 'View the menu: ') + b.name}>{canOrder ? 'Order now' : 'View menu'}<span aria-hidden="true"> →</span></a>;
            } else if (off) {
              var dctx = Object.assign({}, ctx, { phone: b.phone, directions: dir, menuUnavailable: !(b.key && (b.menuAvailable || b.orderable)) });
              var fb = L().targetOf({ type: 'order' }, dctx);
              var dmenu = b.key && (b.menuAvailable || b.orderable) ? menu : null;
              if (fb && fb.kind === 'order' && dmenu) action = <a className="oo-lp-near__go" href={dmenu} onClick={go(dmenu)} aria-label={'View the menu: ' + b.name}>{fb.label || 'View menu'}<span aria-hidden="true"> →</span></a>;
              else if (fb && fb.kind !== 'order') action = <Link className="oo-lp-near__go" target={{ type: 'order' }} label="View menu" ctx={dctx} icon={<span aria-hidden="true"> →</span>} />;
            }
            return (
              <li key={(b.key || 'store') + '-' + i} className={cx('oo-lp-near__card', first && 'is-first')}>
                <span className="oo-lp-near__bar" aria-hidden="true" />
                <div className="oo-lp-near__body">
                  <div className="oo-lp-near__top">
                    {first ? <span className="oo-lp-near__nearest"><Glyph name="star" size={10} />Nearest</span> : null}
                    <span className="oo-lp-near__pills">
                      {label ? <span className={cx('oo-lp-pill', b.open ? 'oo-lp-pill--open' : 'oo-lp-pill--closed')} title={label}><span className="oo-lp-pill__dot" aria-hidden="true" />{b.open ? 'Open' : 'Closed'}</span> : null}
                      {b.preorder === true ? <span className="oo-lp-pill oo-lp-pill--pre">Pre-order</span> : null}
                    </span>
                  </div>
                  <div className="oo-lp-near__info">
                    <h3 className="oo-lp-near__name">{b.shortName || (cut ? b.name.split(/\s+/).filter(Boolean).slice(cut).join(' ') : b.name)}</h3>
                    {b.address ? <p className="oo-lp-near__addr">{b.address}</p> : null}
                  </div>
                  <div className="oo-lp-near__foot">
                    {typeof b.distanceKm === 'number'
                      ? <span className="oo-lp-near__dist"><span aria-hidden="true">📍 </span>{L().distanceText(b.distanceKm)}<span className="oo-sr-only"> away</span></span>
                      : dir ? <a className="oo-lp-near__dir" href={dir} target="_blank" rel="noopener noreferrer" aria-label={'Directions to ' + b.name}>Directions</a> : <span />}
                    {action}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      );
    return <Frame section={s} ctx={ctx} label="Stores" className="oo-lp-nearest" aside={aside} noMore>{body}</Frame>;
  }
  function StoresSection(props) {
    var s = props.section;
    if (s._listing) return <section className="oo-lp-store-page oo-lp-wrap" aria-labelledby="oo-store-list-title">
      <h1 id="oo-store-list-title">Choose a store</h1>
      <ChooserBody master={props.ctx.master} browse={props.ctx.orderingOff} onClose={function () {}} />
    </section>;
    if (s.layout === 'nearest') return <NearestStores section={s} ctx={props.ctx} />;
    if (OO.pages.LandingBlocks && ['layout', 'showHours', 'showPhone', 'showEmail', 'photos'].some(function (k) { return s[k] !== undefined; })) return <OO.pages.LandingBlocks.Stores section={s} ctx={props.ctx} />;
    return props.ctx.orderingOff ? <DirectoryStores section={props.section} ctx={props.ctx} /> : <ChainStores section={props.section} ctx={props.ctx} />;
  }

  // ---- stats, app promo, about, rich text ------------------------------------------------------------------------
  function StatsSection(props) {
    var s = props.section;
    var items = (Array.isArray(s.items) ? s.items : []).filter(function (x) { return x && L().text(x.value, 12) && L().text(x.label, 30); }).slice(0, 6);
    if (!items.length) return null;
    if (s.layout === 'cards') return <StatCards section={s} ctx={props.ctx} items={items} />;
    return (
      <Frame section={s} ctx={props.ctx} label="In numbers" className="oo-lp-stats">
        <dl className="oo-lp-stats__list">
          {items.map(function (x, i) {
            return (
              <div key={i} className="oo-lp-stat">
                <dt className="oo-lp-stat__label">{L().text(x.label, 30)}</dt>
                <dd className="oo-lp-stat__value">{s.countUp === true ? <CountUp value={x.value} on /> : L().text(x.value, 12)}</dd>
              </div>
            );
          })}
        </dl>
      </Frame>
    );
  }
  // WL-CHK: stats layout 'cards': up to 3 per row, each with its icon in a pale primary disc, the value (counting up
  // with `countUp`), the label and the item's `body`.
  function StatCards(props) {
    var s = props.section;
    return (
      <Frame section={s} ctx={props.ctx} label="In numbers" className="oo-lp-statcards">
        <ul className="oo-lp-statcards__list">
          {props.items.map(function (x, i) {
            var body = L().text(x.body, 120);
            return (
              <li key={i} className="oo-lp-statcard">
                {x.icon && OO.landingIcons.PATHS[x.icon] ? <span className="oo-lp-statcard__icon" aria-hidden="true"><OO.landingIcons.Icon name={x.icon} size={30} /></span> : null}
                <p className="oo-lp-statcard__value">{s.countUp === true ? <CountUp value={x.value} on /> : L().text(x.value, 12)}</p>
                <h3 className="oo-lp-statcard__label">{L().text(x.label, 30)}</h3>
                {body ? <p className="oo-lp-statcard__body">{body}</p> : null}
              </li>
            );
          })}
        </ul>
      </Frame>
    );
  }
  function AppPromoSection(props) {
    var s = props.section;
    var ios = L().httpsUrl(s.ios), android = L().httpsUrl(s.android);
    if (s.layout === 'showcase') return <AppShowcase section={s} ctx={props.ctx} ios={ios} android={android} />;
    return (
      <Frame section={s} ctx={props.ctx} label="Get the app" className="oo-lp-band oo-lp-band--hero">
        <div className="oo-lp-band__body">
          <Paras text={s.body} />
          {ios || android ? (
            <div className="oo-lp-badges">
              {ios ? <a className="oo-lp-badge" href={ios} target="_blank" rel="noopener noreferrer">Download on the App Store</a> : null}
              {android ? <a className="oo-lp-badge" href={android} target="_blank" rel="noopener noreferrer">Get it on Google Play</a> : null}
            </div>
          ) : null}
        </div>
      </Frame>
    );
  }
  // WL-CHK: the app promo's 'showcase' layout. Left: the eyebrow pill, the title with the accent on its own line, the body
  // and the store badges (only for a set URL). Right (decorative, aria-hidden): a phone mock-up over a tilted brand block,
  // its screen the `screen` image with screenTitle / screenBody / screenButton, and up to 3 `floats` drifting around it.
  function AppShowcase(props) {
    var s = props.section;
    var screen = L().httpsUrl(s.screen);
    var floats = (Array.isArray(s.floats) ? s.floats : []).map(function (u) { return L().httpsUrl(u); }).filter(Boolean).slice(0, 3);
    var st = L().text(s.screenTitle, 30), sb = L().text(s.screenBody, 60), sbtn = L().text(s.screenButton, 20);
    return (
      <Frame section={s} ctx={props.ctx} label="Get the app" className="oo-lp-app" headless>
        <div className="oo-lp-app__grid">
          <div className="oo-lp-app__text">
            <Head section={s} />
            <Paras text={s.body} />
            {props.ios || props.android ? (
              <div className="oo-lp-app__badges">
                {props.ios ? <a className="oo-lp-app__badge" href={props.ios} target="_blank" rel="noopener noreferrer"><Glyph name="apple" size={20} />App Store</a> : null}
                {props.android ? <a className="oo-lp-app__badge" href={props.android} target="_blank" rel="noopener noreferrer"><Glyph name="play" size={18} />Google Play</a> : null}
              </div>
            ) : null}
          </div>
          <div className="oo-lp-app__visual" aria-hidden="true">
            <span className="oo-lp-app__block" />
            <span className="oo-lp-app__phone">
              <span className="oo-lp-app__screen">
                {screen ? <img src={screen} alt="" loading="lazy" /> : null}
                <span className="oo-lp-app__notch" />
                {st || sb || sbtn ? <span className="oo-lp-app__screen-text">{st ? <span className="oo-lp-app__st">{st}</span> : null}{sb ? <span className="oo-lp-app__sb">{sb}</span> : null}{sbtn ? <span className="oo-lp-app__sbtn">{sbtn}</span> : null}</span> : null}
              </span>
            </span>
            {floats.map(function (u, i) { return <span key={i} className={'oo-lp-app__float oo-lp-app__float--' + (i + 1)}><img src={u} alt="" loading="lazy" /></span>; })}
          </div>
        </div>
      </Frame>
    );
  }
  function AboutSection(props) {
    var s = props.section, ctx = props.ctx;
    var img = L().httpsUrl(s.image);
    if (ctx && ctx.bold && !ctx.inDialog) return <AboutBold section={s} ctx={ctx} />;
    // WL-CHK: `stats`, `cta` and imageSide 'left' render plainly under the standard design (absent: as before).
    var stats = aboutStats(s), cta = s.cta && L().text(s.cta.label, 24) ? s.cta : null;
    return (
      <Frame section={s} ctx={props.ctx} label="About" className={cx(img && 'oo-lp-split', img && s.imageSide === 'left' && 'oo-lp-split--media-left')}>
        <div className="oo-lp-split__text">{stats.length || cta ? <><Paras text={s.body} />{stats.length ? <AboutStats stats={stats} /> : null}{cta ? <OO.pages.Landing.parts.TargetLink className="oo-btn oo-btn--primary oo-btn--md oo-lp-about__cta" target={cta.target} label={L().text(cta.label, 24)} ctx={ctx} button /> : null}</> : <Paras text={s.body} />}</div>
        {img ? <div className="oo-lp-split__media"><img src={img} alt="" loading="lazy" /></div> : null}
      </Frame>
    );
  }
  function aboutStats(s) {
    return (Array.isArray(s.stats) ? s.stats : []).filter(function (x) { return x && L().text(x.value, 12) && L().text(x.label, 30); }).slice(0, 4);
  }
  function AboutStats(props) {
    return <dl className="oo-lp-about__stats">{props.stats.map(function (x, i) { return <div key={i} className="oo-lp-about__stat"><dt>{L().text(x.label, 30)}</dt><dd>{L().text(x.value, 12)}</dd></div>; })}</dl>;
  }
  // WL-CHK: about under the bold design: a dark band (boldTone), the `background` image full-bleed and faint under a
  // primary glow (a few glinting dots over it), the heading (accent inline, trail on its own line), body, `stats`, `cta`;
  // the image in a framed card on the `imageSide` (default right) with the website's logo in its corner.
  function AboutBold(props) {
    var s = boldTone(props.section), ctx = props.ctx;
    var img = L().httpsUrl(s.image), bg = L().httpsUrl(s.background);
    var stats = aboutStats(s), cta = s.cta && L().text(s.cta.label, 24) ? s.cta : null;
    var backdrop = bg ? <span className="oo-lp-about__bg" aria-hidden="true"><img src={bg} alt="" loading="lazy" /><span className="oo-lp-about__dots">{[0, 1, 2, 3, 4, 5].map(function (i) { return <span key={i} />; })}</span></span> : null;
    return (
      <Frame section={s} ctx={ctx} label="About" className={cx('oo-lp-about', bg && 'has-bg', img && 'has-media', s.imageSide === 'left' && 'media-left')} headless backdrop={backdrop}>
        <div className="oo-lp-about__grid">
          <div className="oo-lp-about__text">
            <Head section={s} />
            <Paras text={s.body} />
            {stats.length ? <AboutStats stats={stats} /> : null}
            {cta ? <OO.pages.Landing.parts.TargetLink className="oo-lp-about__cta" target={cta.target} label={L().text(cta.label, 24)} ctx={ctx} /> : null}
          </div>
          {img ? <div className="oo-lp-about__media"><span className="oo-lp-about__frame"><img src={img} alt="" loading="lazy" />{ctx.logo ? <img className="oo-lp-about__logo" src={ctx.logo} alt="" /> : null}</span></div> : null}
        </div>
      </Frame>
    );
  }
  function RichTextSection(props) {
    var s = props.section;
    var ps = (Array.isArray(s.paragraphs) ? s.paragraphs : []).map(function (p) { return L().text(p, 1200); }).filter(Boolean);
    return <Frame section={s} ctx={props.ctx} label="More">{ps.map(function (p, i) { return <p key={i} className="oo-lp-p">{p}</p>; })}</Frame>;
  }
  // WL-CHK: the `marquee` section type, a scrolling band of short messages between sections (no heading drawn; the title,
  // when given, names the region). size large (the bold ticker's look; also when absent, as the studio shows it) |
  // small (spaced-out caps); speed slow | normal | fast. Screen readers get the items as a list; the moving copy is
  // aria-hidden. A Pause / Play button stops it (WCAG 2.2.2); under reduced motion it stands still and the button is left
  // out. Nothing without items. Never a dialog (OO.landing.isDialog).
  function MarqueeSection(props) {
    var s = props.section;
    var items = (Array.isArray(s.items) ? s.items : []).map(function (x) { return L().text(x, 80); }).filter(Boolean).slice(0, 8);
    var reduced = useReduced();
    var paused = React.useState(false);
    if (!items.length) return null;
    var sep = L().text(s.separator, 3) || '•';
    var still = reduced || paused[0];
    function run(copy) {
      return items.map(function (x, k) { return <React.Fragment key={copy + '-' + k}><span className="oo-lp-marquee__item">{x}</span><span className="oo-lp-marquee__sep">{sep}</span></React.Fragment>; });
    }
    return (
      <section id={L().anchorId(s.id)} tabIndex={-1} aria-label={L().text(s.title, 80) || 'Highlights'}
        className={cx('oo-lp-sec', 'oo-lp-sec--marquee', 'oo-lp-marquee', 'oo-lp-marquee--' + (s.size === 'small' ? 'small' : 'large'), ['slow', 'fast'].indexOf(s.speed) >= 0 && 'oo-lp-marquee--' + s.speed, still && 'is-still', L().styleClasses(s))}>
        <ul className="oo-sr-only">{items.map(function (x, k) { return <li key={k}>{x}</li>; })}</ul>
        <div className="oo-lp-marquee__track" aria-hidden="true">{run('a')}{still ? null : run('b')}{still ? null : run('c')}{still ? null : run('d')}</div>
        {reduced ? null : <button type="button" className="oo-lp-marquee__pause" onClick={function () { paused[1](!paused[0]); }} aria-label={paused[0] ? 'Play the scrolling messages' : 'Pause the scrolling messages'}>{paused[0] ? 'Play' : 'Pause'}</button>}
      </section>
    );
  }

  // ---- forms ------------------------------------------------------------------------------------------------------
  var EMPTY = { name: '', email: '', phone: '', message: '', website: '' };
  function ContactForm(props) {
    var ui = OO.ui;
    var f = React.useState(EMPTY);
    var errs = React.useState({});
    var st = React.useState({ state: 'idle', message: '' }); // idle | sending | sent | failed
    var uid = 'oo-lp-f-' + String(props.sectionId);
    function set(k) { return function (v) { f[1](function (cur) { var n = Object.assign({}, cur); n[k] = v; return n; }); }; }
    function onSubmit(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (st[0].state === 'sending') return;
      var v = L().validate(f[0]);
      errs[1](v.errors);
      if (!v.ok) { st[1]({ state: 'idle', message: '' }); return; }
      st[1]({ state: 'sending', message: '' });
      L().submit(props.siteKey, L().body(props.sectionId, f[0])).then(function (r) {
        if (r.ok) { f[1](EMPTY); errs[1]({}); st[1]({ state: 'sent', message: r.message }); return; }
        errs[1](r.errors || {});
        st[1]({ state: 'failed', message: r.message });
      });
    }
    var e = errs[0];
    var s = st[0];
    return (
      <form className="oo-lp-form" onSubmit={onSubmit} noValidate aria-describedby={uid + '-status'}>
        <div className="oo-lp-form__row">
          <ui.TextInput label="Name" labelVisible name="name" autoComplete="name" value={f[0].name} onChange={set('name')} error={e.name} maxLength={100} required />
          <ui.TextInput label="Email" labelVisible name="email" type="email" autoComplete="email" value={f[0].email} onChange={set('email')} error={e.email} maxLength={254} required />
        </div>
        <ui.TextInput label="Phone (optional)" labelVisible name="phone" type="tel" autoComplete="tel" value={f[0].phone} onChange={set('phone')} error={e.phone} maxLength={32} />
        <ui.TextArea label="Message" name="message" value={f[0].message} onChange={set('message')} error={e.message} maxLength={2000} size="xl" />
        <div className="oo-lp-hp" aria-hidden="true">
          <label htmlFor={uid + '-website'}>Website</label>
          <input id={uid + '-website'} name="website" type="text" tabIndex={-1} autoComplete="off" value={f[0].website}
            onChange={function (ev) { set('website')(ev.target.value); }} />
        </div>
        <div className="oo-lp-form__foot">
          <ui.Button type="submit" size="md" busy={s.state === 'sending'}>Send message</ui.Button>
          <p id={uid + '-status'} className={cx('oo-lp-form__status', s.state === 'failed' && 'is-error')} role="status" aria-live="polite">{s.message}</p>
        </div>
      </form>
    );
  }
  function FranchiseSection(props) {
    var s = props.section;
    return (
      <Frame section={s} ctx={props.ctx} label="Franchise" className="oo-lp-split">
        <div className="oo-lp-split__text"><Paras text={s.body} /></div>
        <div className="oo-lp-split__form"><ContactForm sectionId={s.id} siteKey={props.ctx.key} /></div>
      </Frame>
    );
  }
  function ContactSection(props) {
    var s = props.section;
    var email = typeof s.email === 'string' && /^[^\s@]+@[^\s@]+$/.test(s.email.trim()) ? s.email.trim() : null;
    var phone = L().text(s.phone, 24);
    var tel = phone ? phone.replace(/[^0-9+]/g, '') : '';
    return (
      <Frame section={s} ctx={props.ctx} label="Contact" className="oo-lp-split">
        <div className="oo-lp-split__text">
          <ul className="oo-lp-contact">
            {email ? <li><span className="oo-lp-contact__k">Email</span><a className="oo-lp-link" href={'mailto:' + email}>{email}</a></li> : null}
            {phone ? <li><span className="oo-lp-contact__k">Phone</span>{tel ? <a className="oo-lp-link" href={'tel:' + tel}>{phone}</a> : phone}</li> : null}
          </ul>
        </div>
        {email ? <div className="oo-lp-split__form"><ContactForm sectionId={s.id} siteKey={props.ctx.key} /></div> : null}
      </Frame>
    );
  }

  // ---- the store chooser -------------------------------------------------------------------------------------------
  function ChooserBody(props) {
    var ui = OO.ui;
    var route = { chain: props.master, hotel: null, page: 'stores', params: {}, query: {} };
    var P = OO.pages.Stores.usePicker({ route: route, theme: false });
    var sel = P.selected;
    function orderFrom() {
      if (!sel) return;
      props.onClose();
      L().handoff(sel.key, P.chain && P.chain.master ? P.chain.master.key : props.master, props.query);
    }
    return (
      <div className="oo-lp-chooser">
        {P.body}
        <div className="oo-lp-chooser__cta">
          <ui.Button block disabled={!sel} onClick={orderFrom}>{sel ? (props.browse ? 'View menu at ' : 'Order from ') + (sel.shortName || sel.name) : 'Choose a store'}</ui.Button>
        </div>
      </div>
    );
  }
  function StoreChooser(props) {
    var ui = OO.ui;
    return (
      <ui.Sheet open={!!props.open} onClose={props.onClose} variant="dialog" className="oo-lp-chooser-sheet" label="Choose a store" title="Choose a store">
        {props.open ? <ChooserBody master={props.master} query={props.query} onClose={props.onClose} browse={!!props.browse} /> : null}
      </ui.Sheet>
    );
  }

  // An in-app link's onClick: a plain primary click navigates without a page load.
  function go(href) {
    return function (e) {
      if (!e || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.preventDefault) e.preventDefault();
      OO.router.go(href);
    };
  }

  var REGISTRY = {
    offers: OffersSection, bestSellers: BestSellersSection, categories: CategoriesSection, rewards: RewardsSection,
    stores: StoresSection, stats: StatsSection, appPromo: AppPromoSection, about: AboutSection, franchise: FranchiseSection,
    contact: ContactSection, richText: RichTextSection, marquee: MarqueeSection,
  };
  function Section(props) {
    // WL-SUPER: under the Super design a type with a Super variant (OO.pages.LandingSuper.REGISTRY) draws that one.
    var sup = props.ctx && props.ctx.superDesign && OO.pages.LandingSuper ? OO.pages.LandingSuper.REGISTRY : null;
    var C = (sup && props.section && Object.prototype.hasOwnProperty.call(sup, props.section.type) ? sup[props.section.type] : null) || REGISTRY[props.section && props.section.type];
    return C ? <C section={props.section} ctx={props.ctx} /> : null;
  }

  OO.pages.LandingSections = {
    Frame: Frame, Paras: Paras, REGISTRY: REGISTRY, Section: Section, ContactForm: ContactForm, StoreChooser: StoreChooser, ChooserBody: ChooserBody, go: go, useLate: useLate,
    // WL-CHK
    Head: Head, TitleText: TitleText, Eyebrow: Eyebrow, Rail: Rail, CountUp: CountUp, SectionDialog: SectionDialog, offerTag: offerTag,
  };
})();


// ===================== src/pages/landing.jsx =====================
// src/pages/landing.jsx: OO.pages.Landing, the white-label multi-store web landing page (page 'landing': /{key} on the
// /endpoint/landing mount, boot mode 'landing'; docs/whitelabel/WHITELABEL-PLAN.md §7.3 phase 1; lane WL-3). Desktop
// first and responsive. Styles: styles/landing.css (.oo-lp-*). Logic: src/lib/landing.jsx (OO.landing); sections and
// forms: src/pages/landing-sections.jsx.
//
// The document: OO.whitelabel.siteFor(key) (WL-2: the boot's inline answer, a read for a key the boot didn't cover, or
// the studio's draft in preview), on the `web` surface (products.web; the preview ignores products). The chain's master
// (site.masterKey, else the key) is the default store: OO.useHotel(master) loads it (and applies the white-label theme,
// as every page does) and OO.useChain(master) lists the chain. No site for the key once its read has settled
// (OO.whitelabel.ensure(key) resolved: no site, the web product off, an expired preview, a key not served live here, or
// the white-label kill switch off, WL-int) → the key's menu (/{key}/menu, replace): ordering is never blocked.
//
// Layout, top to bottom:
//   header   the logo (theme.logo: the variant for the bar's surface, WL-2) and brand name, nav from landing.nav (typed
//            targets, OO.landing.targetOf: order / page / section / url; a target that resolves to nothing is left
//            out), the CTA (landing.cta), the cart (the last store's cart; "Cart (n)") and the account (/{key}/account).
//            Below 900 px the nav folds into a menu button (aria-expanded). layout.header 'transparent' lays the bar
//            over the hero in hero colours.
//   hero     <HeroSlider>: the desktop variant of app Home's PromoSlider (src/pages/app-home.jsx): the same carousel
//            rules (pauses on hover and focus, dots, swipe, no auto-advance under prefers-reduced-motion), full-bleed
//            slides with the headline, subline and CTA. A slide with a video plays it muted, looping and inline, with
//            the slide image as its poster, only while autoplay is on and motion isn't reduced (else the poster image
//            alone). Auto-advance (6 s) only when hero.autoplay; a Pause / Play button stops it (WCAG 2.2.2).
//   ticker   landing.ticker when enabled (a marquee that stops under reduced motion; the items as a list for screen
//            readers).
//   sections OO.landing.sectionsOf(landing), in document order (OO.pages.LandingSections.Section).
//   footer   landing.footer: columns of links, socials (https), legal links, copyright; then OO.ui.PoweredBySipo.
// "Order" targets (the CTA, an order nav item, a hero CTA, a dish or category tile) go through the order hand-off
// (OO.landing.orderPlan): one store → /{store}/menu; several → the store chooser (OO.pages.LandingSections.StoreChooser:
// OO.pages.Stores.usePicker), then /{branchKey}/menu, with the tile's deep link (?type=category|product&id=) kept.
// Section links scroll to the section (smoothly unless motion is reduced) and keep #wl-{id} in the URL.
// The page sets document.title to landing.seo.title (else the brand name). Nothing here listens for `message` events.
// WL-5 (landing-only websites): when the website's ordering is off (OO.whitelabel.landingOrdering(key).on false: the
// site's Online ordering tick off, or no chain store can take online orders) the page is information-only: no cart and
// no account in the header; offers and rewards left out; every "Order" target follows landing.orderingOff (the
// browse-only menu, a call, directions, a web address, or nothing; OO.landing.targetOf) with its label; the store
// chooser offers "View menu". The logo and name come from the document (brand.*): with the chain master's store
// unreadable (getRestaurant 404s without a main terminal) the header still wears the brand, and the page applies the
// document's theme itself. With ordering on everything renders exactly as before WL-5.
// WL-CHK (all opt-in; a document without the new keys renders exactly as before):
//   landing.design 'bold' → the root carries oo-lp--bold (styles/landing-bold.css), and the header and the footer are the
//            bold ones (BoldHeader: a solid bar, the nav centred, then the CTA pill, the cart and the account; below 900 px
//            the menu button on the left, the logo centred and the cart on the right, the nav and the CTA / account in a
//            drop-down panel. BoldFooter: a dark band, the logo and footer.tagline, the columns, round social buttons, the
//            partner row "Online order partner [SIPO]" + footer.credits, then copyright and legal links). Sections reveal on
//            scroll (a data-reveal attribute set only once JS runs; never under reduced motion).
//   landing.header.logo  the website's logo in the header and the footer (both designs).
//   landing.footer.tagline (the footer's line under the logo) · footer.credits (partner credits after PoweredBySipo).
//   hero.interval (seconds) · hero.scrim 'left' (a gradient from the left and the bottom; the flat `overlay` colour is
//   not applied with it) · slides[].badge, lead, trail (the h1 reads "lead headline trail"), logo (drawn in place of the
//   headline, which stays in the h1 for screen readers), rotate + rotatePrefix (a line of phrases changing every 3 s
//   while the slideshow plays; screen readers and reduced motion get the first), videoPortrait (below 768 px).
//   ticker.separator (after each item, in place of the bullet) · ticker.tone accent | brand | dark.
//   A section with display 'dialog' (a content or form type: OO.landing.isDialog; any other type stays on the page) is
//   drawn in a dialog (OO.pages.LandingSections.SectionDialog) opened by any link to it; the URL keeps #wl-{id} while it
//   is open, and arriving with that hash opens it.
// WL-OPT: brand.poweredBy false hides the SIPO badge in both footers (OO.ui.PoweredBySipo decides; footer.credits stay).
//   The bold partner row then starts with the first credit (no rule before it), and is left out with no credits.
// WL-SUPER (opt-in): landing.design 'super' → the root carries oo-lp--super (+ oo-lp--script with a script font,
//   typography.script; styles/landing-super.css), the header and the footer are OO.pages.LandingSuper's, the hero slide
//   shows `cta` and `more` as two pills (LandingSuper.SlideCtas), ctx.superDesign routes a section type to
//   LandingSuper.REGISTRY when it has a Super variant, and sections reveal on scroll as in bold. ctx.bold stays false.
// WL-VEL (every design; a document without the new keys renders exactly as before): route.params.slug names a site page
//   (landing.pages; OO.landing.pageOf): its sections replace the home sections, there is no hero or ticker, the title is
//   its seo.title, and an unknown slug replaces the URL with the home page's. route.params.redirect (a landing.redirects
//   `from`, glue/router.jsx) resolves its `to` like any link and replaces the URL with it. A section link to another
//   page (ids are unique across the pages) goes to that page and its #wl-{id}, which is scrolled to (or opened, for a
//   dialog section) once the page is drawn. landing.design 'boutique' draws its own chrome around the same sections
//   (OO.pages.LandingBoutique, src/pages/landing-boutique.jsx).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var ROTATE_MS = 6000;
  var EMPTY = [];

  function L() { return OO.landing; }
  function cx() { return OO.ui.cx.apply(null, arguments); }
  function plainClick(e) { return !!e && !(e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey); }

  // prefers-reduced-motion, as app Home reads it (useSyncExternalStore over matchMedia).
  var RM_QUERY = '(prefers-reduced-motion: reduce)';
  function rmList() { try { return window.matchMedia ? window.matchMedia(RM_QUERY) : null; } catch (e) { return null; } }
  function reducedNow() { var m = rmList(); return !!(m && m.matches); }
  function subscribeRM(cb) {
    var m = rmList();
    if (!m) return function () {};
    if (m.addEventListener) { m.addEventListener('change', cb); return function () { m.removeEventListener('change', cb); }; }
    if (m.addListener) { m.addListener(cb); return function () { m.removeListener(cb); }; }
    return function () {};
  }
  function useReducedMotion() { return React.useSyncExternalStore(subscribeRM, reducedNow, reducedNow); }

  function scrollToSection(id) {
    var el = typeof document !== 'undefined' && document.getElementById ? document.getElementById(L().anchorId(id)) : null;
    if (!el) return;
    try { el.scrollIntoView({ behavior: reducedNow() ? 'auto' : 'smooth', block: 'start' }); } catch (e) { el.scrollIntoView(); }
    if (el.focus) { try { el.focus({ preventScroll: true }); } catch (x) { /* not focusable */ } }
    try { window.history.replaceState(window.history.state, '', '#' + L().anchorId(id)); } catch (e2) { /* no history */ }
  }

  // <TargetLink target label button ctx className /> an <a> for a typed target, or nothing when it resolves to nothing.
  // button: the label sits in the button's label span. WL-5: an "Order" target while ordering is off carries its own
  // label (landing.orderingOff.label), and a call is a tel: link. WL-CHK: inside a section dialog (ctx.closeDialog) an
  // in-page link closes the dialog first; a page section is scrolled to once the dialog is gone (its focus has gone
  // back to the opener by then).
  function TargetLink(props) {
    var t = L().targetOf(props.target, props.ctx);
    if (!t) return null;
    var text = t.label || props.label;
    var label = props.children || (props.button ? <span className="oo-btn__label">{text}</span> : text);
    if (props.icon && !props.children) label = <>{label}{props.icon}</>; // WL-CHK: a trailing glyph
    var cls = props.className;
    var shut = props.ctx && typeof props.ctx.closeDialog === 'function' ? props.ctx.closeDialog : null;
    function leave() { if (props.onNavigate) props.onNavigate(); if (shut) shut(); }
    if (t.kind === 'url') return <a className={cls} href={t.href} target="_blank" rel="noopener noreferrer">{label}</a>;
    if (t.kind === 'tel') return <a className={cls} href={t.href}>{label}</a>;
    if (t.kind === 'section') {
      // WL-CHK: a section shown in a dialog opens it (ctx.openSection), else the page scrolls to the section.
      var open = t.dialog && props.ctx && typeof props.ctx.openSection === 'function' ? props.ctx.openSection : null;
      return <a className={cls} href={t.href} onClick={function (e) { if (!plainClick(e)) return; e.preventDefault(); leave(); if (open) open(t.id); else if (shut) setTimeout(function () { scrollToSection(t.id); }, 0); else scrollToSection(t.id); }}>{label}</a>;
    }
    if (t.kind === 'order' || t.kind === 'categories') {
      var menu = L().menuHref(props.ctx.key);
      return <a className={cls} href={menu} data-oo-website-order={props.ctx.publicOnly ? JSON.stringify(t.query || null) : undefined} onClick={function (e) { if (!plainClick(e)) return; e.preventDefault(); leave(); props.ctx.order(t.query || null); }}>{label}</a>; // WL-VEL: t.query (menuCategory)
    }
    return <a className={cls} href={t.href} onClick={function (e) { if (!plainClick(e)) return; e.preventDefault(); leave(); OO.router.go(t.href); }}>{label}</a>;
  }


  function NavItem(props) {
    var n = props.item, ctx = props.ctx, categories = n.target && n.target.type === 'categories';
    var children = Array.isArray(n.children) ? n.children.slice(0, 8) : [];
    var cats = categories ? L().menuCategories(ctx.builderMenu || ctx.menu) : [];
    if (categories && !cats.length) return null;
    if (!children.length && !categories) return <TargetLink className="oo-lp-head__link" target={n.target} label={n.label} ctx={ctx} onNavigate={props.close} />;
    function close(e) { var details = e.currentTarget.closest('details'); if (details) details.open = false; props.close(); }
    return <details className={cx('oo-lp-dropdown', categories && 'oo-lp-dropdown--mega')} onKeyDown={function (e) { if (e.key === 'Escape') { e.currentTarget.open = false; var summary = e.currentTarget.querySelector('summary'); if (summary) summary.focus(); } }} onBlur={function (e) { if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) e.currentTarget.open = false; }}>
      <summary className="oo-lp-head__link">{n.label}<span aria-hidden="true">⌄</span></summary>
      <ul className="oo-lp-dropdown__items">
        {!categories ? <li><TargetLink className="oo-lp-head__link" target={n.target} label={'Explore ' + n.label} ctx={ctx} onNavigate={props.close} /></li> : null}
        {cats.map(function (c) { return <li key={c.id}><a href={L().menuHref(ctx.master, { type: 'category', id: String(c.id) })} onClick={function (e) { if (!plainClick(e)) return; e.preventDefault(); close(e); ctx.order({ type: 'category', id: String(c.id) }); }}>{c.name}</a></li>; })}
        {children.map(function (child, i) { return <li key={i}><TargetLink className="oo-lp-head__link" target={child.target} label={child.label} ctx={ctx} onNavigate={props.close} /></li>; })}
      </ul>
    </details>;
  }
  function ProductSearch(props) {
    var ctx = props.ctx, query = React.useState(''), active = React.useState(-1), open = React.useState(false), root = React.useRef(null);
    var list = L().searchProducts(ctx.builderMenu || ctx.menu, query[0]), id = 'oo-lp-product-search';
    function choose(p) { open[1](false); query[1](''); active[1](-1); ctx.order({ type: 'product', id: String(p.id) }); }
    if (!L().menuProducts(ctx.builderMenu || ctx.menu).length) return null;
    return <div className="oo-lp-search" ref={root} onBlur={function (e) { if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) open[1](false); }}>
      <label className="oo-sr-only" htmlFor={id}>Search products</label><input id={id} type="search" role="combobox" autoComplete="off" placeholder="Search products" value={query[0]} aria-expanded={!!(open[0] && query[0])} aria-controls={id + '-results'} aria-autocomplete="list" aria-activedescendant={open[0] && active[0] >= 0 && list[active[0]] ? id + '-result-' + active[0] : undefined}
        onFocus={function () { open[1](true); }} onChange={function (e) { query[1](e.target.value); active[1](-1); open[1](true); }} onKeyDown={function (e) {
          if (e.key === 'Escape') { open[1](false); active[1](-1); }
          if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && list.length) { e.preventDefault(); open[1](true); active[1](active[0] < 0 ? (e.key === 'ArrowDown' ? 0 : list.length - 1) : (active[0] + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length); }
          if (e.key === 'Enter' && list.length) { e.preventDefault(); choose(list[Math.max(0, active[0])]); }
        }} />
      {open[0] && query[0] ? <div className="oo-lp-search__results"><ul id={id + '-results'} role="listbox" aria-label="Search results">{list.map(function (p, i) { return <li id={id + '-result-' + i} role="option" aria-selected={i === active[0]} key={p.id}><button type="button" tabIndex={-1} onMouseDown={function (e) { e.preventDefault(); }} onClick={function () { choose(p); }}>{p.image ? <img src={p.image} alt="" loading="lazy" /> : null}<span>{p.name}</span></button></li>; })}</ul>{!list.length ? <p role="status">No matching products.</p> : null}</div> : null}
    </div>;
  }
  function Utility(props) {
    var u = props.header.utility;
    if (!u || !u.enabled) return null;
    return <div className="oo-lp-utility"><div className="oo-lp-wrap"><span>{L().text(u.text, 120)}</span>{u.storePicker ? <button type="button" onClick={props.ctx.chooseStore}>Change location</button> : null}</div></div>;
  }

  // ---- header -------------------------------------------------------------------------------------------------------
  // The public website renderer uses the same header without reading a customer's saved cart.
  // The live wrapper keeps the existing subscription and branch selection.
  function headerCart(ctx) {
    var cartKey = (OO.store && typeof OO.store.lastHotel === 'function' && OO.store.lastHotel()) || ctx.master;
    OO.useStore(function (s) { return s.cart; });
    var count = cartKey && OO.store && typeof OO.store.cartOf === 'function' ? OO.store.cartOf(cartKey).count || 0 : 0;
    return { key: cartKey, count: count };
  }
  // WL-LOGO: the optional larger header logo (landing.header.logoHeight: large | xlarge; absent or 'standard' = the design's default size).
  function logoClass(header) { var h = header && header.logoHeight; return h === 'large' || h === 'xlarge' ? 'oo-lp-head__logo--' + h : null; }
  function Header(props) { return HeaderView(props, headerCart(props.ctx)); }
  function HeaderView(props, cartState) {
    var ctx = props.ctx, landing = props.landing, theme = props.theme || {};
    var openS = React.useState(false);
    var settings = landing.header || {};
    var nav = (Array.isArray(landing.nav) ? landing.nav : EMPTY).filter(function (n) { return n && L().text(n.label, 24); }).slice(0, 8);
    var cta = landing.cta && L().text(landing.cta.label, 24) ? landing.cta : null;
    var logo = L().httpsUrl(settings.logo) || theme.logo || null; // WL-CHK: landing.header.logo first
    var name = theme.brand || props.name || '';
    var home = OO.router.href('landing', null, { hotel: ctx.key });
    var account = OO.router.href('account', null, { hotel: ctx.key });
    var cartKey = cartState.key, count = cartState.count;
    var cart = OO.router.href('cart', null, { hotel: cartKey });
    var close = function () { openS[1](false); };
    function inApp(href) { return function (e) { if (!plainClick(e)) return; e.preventDefault(); close(); OO.router.go(href); }; }
    return (
      <header className={cx('oo-lp-head', props.overHero && 'oo-lp-head--over', openS[0] && 'is-open', settings.layout && 'oo-lp-head--' + settings.layout, settings.sticky === true && 'oo-lp-head--sticky', settings.sticky === false && 'oo-lp-head--static')}>
        <Utility header={settings} ctx={ctx} />
        <div className="oo-lp-wrap oo-lp-head__row">
          <a className={cx('oo-lp-head__brand', logo && logoClass(settings) && 'oo-lp-head__brand--sized')} href={home} onClick={function (e) { if (!plainClick(e)) return; e.preventDefault(); close(); window.scrollTo(0, 0); }}>
            {logo ? <img className={cx('oo-lp-head__logo', logoClass(settings))} src={logo} alt={name ? name + ' home' : 'Home'} /> : <span className="oo-lp-head__name">{name}</span>}
          </a>
          {settings.search ? <ProductSearch ctx={ctx} /> : null}
          <button type="button" className="oo-lp-head__toggle" aria-expanded={openS[0] ? 'true' : 'false'} aria-controls="oo-lp-nav"
            onClick={function () { openS[1](!openS[0]); }}>
            {openS[0] ? <OO.icons.Close size={22} /> : <OO.icons.MenuList size={22} />}
            <span className="oo-sr-only">Menu</span>
          </button>
          <nav id="oo-lp-nav" className="oo-lp-head__nav" aria-label="Main">
            <ul className="oo-lp-head__links">
              {nav.map(function (n, i) {
                return <li key={i}><NavItem item={n} ctx={ctx} close={close} /></li>;
              })}
            </ul>
            <div className="oo-lp-head__acts">
              {ctx.orderingOff || ctx.publicOnly ? null : <a className="oo-lp-head__icon" href={account} onClick={inApp(account)} aria-label="Account"><OO.icons.User size={20} /></a>}
              {ctx.orderingOff || ctx.publicOnly ? null : (
                <a className="oo-lp-head__icon oo-lp-head__cart" href={cart} onClick={inApp(cart)} aria-label={'Cart, ' + count + (count === 1 ? ' item' : ' items')}>
                  <OO.icons.Bag size={20} /><span className="oo-lp-head__count" aria-hidden="true">{count}</span>
                </a>
              )}
              {cta ? <TargetLink className="oo-btn oo-btn--primary oo-btn--md oo-lp-head__cta" target={cta.target} ctx={ctx} onNavigate={close} label={L().text(cta.label, 24)} button /> : null}
            </div>
          </nav>
        </div>
      </header>
    );
  }

  // ---- WL-CHK: the bold header ----------------------------------------------------------------------------------------
  // A solid bar (72 px; 56 below 900): the logo, the nav centred, then the CTA (a light pill with an arrow), the cart and
  // the account. Below 900 px: the menu button on the left, the logo centred, the cart on the right; the nav, the CTA and
  // the account drop down under the bar (aria-expanded). Ordering off: no cart, no account (WL-5), the CTA as orderingOff.
  function BoldHeader(props) { return BoldHeaderView(props, headerCart(props.ctx)); }
  function BoldHeaderView(props, cartState) {
    var ctx = props.ctx, landing = props.landing, theme = props.theme || {};
    var openS = React.useState(false);
    var settings = landing.header || {};
    var nav = (Array.isArray(landing.nav) ? landing.nav : EMPTY).filter(function (n) { return n && L().text(n.label, 24); }).slice(0, 8);
    var cta = landing.cta && L().text(landing.cta.label, 24) ? landing.cta : null;
    var logo = L().httpsUrl(settings.logo) || theme.logo || null;
    var name = theme.brand || props.name || '';
    var home = OO.router.href('landing', null, { hotel: ctx.key });
    var account = OO.router.href('account', null, { hotel: ctx.key });
    var cartKey = cartState.key, count = cartState.count;
    var cart = OO.router.href('cart', null, { hotel: cartKey });
    var close = function () { openS[1](false); };
    function inApp(href) { return function (e) { if (!plainClick(e)) return; e.preventDefault(); close(); OO.router.go(href); }; }
    var Glyph = OO.landingIcons.Glyph;
    var hasSearch = settings.search && L().menuProducts(ctx.builderMenu || ctx.menu).length > 0;
    var cartLink = ctx.orderingOff || ctx.publicOnly ? null : (
      <a className="oo-lp-head__icon oo-lp-head__cart" href={cart} onClick={inApp(cart)} aria-label={'Cart, ' + count + (count === 1 ? ' item' : ' items')}>
        <OO.icons.Bag size={24} />{count ? <span className="oo-lp-head__count" aria-hidden="true">{count}</span> : null}
      </a>
    );
    return (
      <header className={cx('oo-lp-head', 'oo-lp-head--bold', hasSearch && 'oo-lp-head--search', openS[0] && 'is-open', settings.sticky === false && 'oo-lp-head--static')}>
        <Utility header={settings} ctx={ctx} />
        <div className="oo-lp-wrap oo-lp-head__row">
          <button type="button" className="oo-lp-head__toggle" aria-expanded={openS[0] ? 'true' : 'false'} aria-controls="oo-lp-nav"
            onClick={function () { openS[1](!openS[0]); }}>
            {openS[0] ? <OO.icons.Close size={24} /> : <OO.icons.MenuList size={24} />}
            <span className="oo-sr-only">Menu</span>
          </button>
          <a className="oo-lp-head__brand" href={home} onClick={function (e) { if (!plainClick(e)) return; e.preventDefault(); close(); window.scrollTo(0, 0); }}>
            {logo ? <img className={cx('oo-lp-head__logo', logoClass(settings))} src={logo} alt={name ? name + ' home' : 'Home'} /> : <span className="oo-lp-head__name">{name}</span>}
          </a>
          {settings.search ? <ProductSearch ctx={ctx} /> : null}
          <nav id="oo-lp-nav" className="oo-lp-head__nav" aria-label="Main">
            <ul className="oo-lp-head__links">
              {nav.map(function (n, i) { return <li key={i}><NavItem item={n} ctx={ctx} close={close} /></li>; })}
            </ul>
            {cta || (!ctx.orderingOff && !ctx.publicOnly) ? (
              <div className="oo-lp-head__panel">
                {cta ? <TargetLink className="oo-lp-head__pcta" target={cta.target} ctx={ctx} onNavigate={close} label={L().text(cta.label, 24)} /> : null}
                {ctx.orderingOff || ctx.publicOnly ? null : <a className="oo-lp-head__plink" href={account} onClick={inApp(account)}><OO.icons.User size={18} />Account</a>}
              </div>
            ) : null}
          </nav>
          <div className="oo-lp-head__acts">
            {cta ? <TargetLink className="oo-lp-head__cta" target={cta.target} ctx={ctx} onNavigate={close} label={L().text(cta.label, 24)} icon={<Glyph name="external" size={12} strokeWidth={2.6} />} /> : null}
            {cartLink}
            {ctx.orderingOff || ctx.publicOnly ? null : <a className="oo-lp-head__icon oo-lp-head__account" href={account} onClick={inApp(account)} aria-label="Account"><OO.icons.User size={20} /></a>}
          </div>
        </div>
      </header>
    );
  }

  // ---- hero slider ------------------------------------------------------------------------------------------------
  // WL-CHK: a media query as a live boolean (useSyncExternalStore over matchMedia), e.g. the portrait-video breakpoint.
  function useMedia(q) {
    var sub = React.useCallback(function (cb) {
      var m = null;
      try { m = window.matchMedia ? window.matchMedia(q) : null; } catch (e) { m = null; }
      if (!m) return function () {};
      if (m.addEventListener) { m.addEventListener('change', cb); return function () { m.removeEventListener('change', cb); }; }
      if (m.addListener) { m.addListener(cb); return function () { m.removeListener(cb); }; }
      return function () {};
    }, [q]);
    var get = function () { try { return !!(window.matchMedia && window.matchMedia(q).matches); } catch (e) { return false; } };
    return React.useSyncExternalStore(sub, get, get);
  }
  var PORTRAIT_Q = '(max-width: 767px)';
  var PHRASE_MS = 3000;
  // WL-CHK: a slide's line of rotating phrases (slides[].rotate, after rotatePrefix). The phrase changes every 3 s while
  // `active` (the slide is showing and the slideshow plays); screen readers get the prefix and the first phrase only.
  function Rotator(props) {
    var phrases = props.phrases, n = phrases.length;
    var k = React.useState(0);
    React.useEffect(function () {
      if (!props.active || n < 2) return undefined;
      var t = setInterval(function () { k[1](function (x) { return (x + 1) % n; }); }, PHRASE_MS);
      return function () { clearInterval(t); };
    }, [props.active, n]);
    var at = props.active ? k[0] % n : 0;
    return (
      <p className="oo-lp-slide__rotate">
        <span className="oo-sr-only">{(props.prefix ? props.prefix + ' ' : '') + phrases[0]}</span>
        <span className="oo-lp-slide__rot" aria-hidden="true">
          {props.prefix ? <span className="oo-lp-slide__rprefix">{props.prefix}</span> : null}
          <span className="oo-lp-slide__rwin"><span key={at} className="oo-lp-slide__rword">{phrases[at]}</span></span>
        </span>
      </p>
    );
  }
  // WL-CHK: the h1 / title content: "lead headline trail" (each optional part its own span); a slide logo is drawn in place
  // of the headline, which stays in the text for screen readers. Without lead, trail and logo: the headline text alone.
  function SlideTitle(props) {
    var s = props.slide, headline = L().text(s.headline, 80);
    var lead = L().text(s.lead, 40), trail = L().text(s.trail, 40), logo = L().httpsUrl(s.logo);
    if (!lead && !trail && !logo) return headline;
    return (
      <>
        {lead ? <span className="oo-lp-slide__lead">{lead}</span> : null}
        {lead ? ' ' : null}
        {logo ? <><span className="oo-sr-only">{headline}</span><OO.ui.Image className="oo-lp-slide__logo" src={props.deferImage ? undefined : logo} sizes="(max-width: 457px) 70vw, 320px" alt="" /></> : <span className="oo-lp-slide__head">{headline}</span>}
        {trail ? ' ' : null}
        {trail ? <span className="oo-lp-slide__trail">{trail}</span> : null}
      </>
    );
  }
  function HeroSlider(props) {
    var hero = props.hero || {}, ctx = props.ctx;
    var slides = (Array.isArray(hero.slides) ? hero.slides : EMPTY).filter(function (s) { return s && L().httpsUrl(s.image) && L().text(s.headline, 80); }).slice(0, 6);
    var n = slides.length;
    var idxState = React.useState(0);
    var hoverState = React.useState(false);
    var focusState = React.useState(false);
    var stoppedState = React.useState(false);
    var reduced = useReducedMotion();
    var portrait = useMedia(PORTRAIT_Q);
    var touch = React.useRef(null);
    var requested = n ? idxState[0] % n : 0;
    var loadedMedia = OO.ui.useSlideImages(slides, requested, function (s) { return L().httpsUrl(s.image); });
    var i = loadedMedia.index;
    var autoplay = hero.autoplay === true && !reduced;
    var rotating = autoplay && n > 1 && !hoverState[0] && !focusState[0] && !stoppedState[0];
    var interval = L().heroInterval(hero) || ROTATE_MS;
    var leftScrim = hero.scrim === 'left';
    // A slide with rotating phrases keeps the Pause button even alone (WCAG 2.2.2).
    var phrasing = slides.some(function (s) { return Array.isArray(s.rotate) && s.rotate.some(function (x) { return L().text(x, 30); }); });
    React.useEffect(function () {
      if (!rotating) return undefined;
      var t = setTimeout(function () { idxState[1](function (x) { return (x + 1) % n; }); }, interval);
      return function () { clearTimeout(t); };
    }, [rotating, i, n, interval]);
    if (!n) return null;
    function pick(k) { stoppedState[1](true); idxState[1](((k % n) + n) % n); }
    function onTouchStart(e) { var t = e.touches && e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; }
    function onTouchEnd(e) {
      var s = touch.current, t = e.changedTouches && e.changedTouches[0];
      touch.current = null;
      if (!s || !t || n < 2) return;
      var dx = t.clientX - s.x, dy = t.clientY - s.y;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) pick(i + (dx < 0 ? 1 : -1));
    }
    function onBlur(e) { if (!e.currentTarget.contains(e.relatedTarget)) focusState[1](false); }
    return (
      <section className={cx('oo-lp-hero', props.overHero && 'oo-lp-hero--under', hero.height && 'oo-lp-hero--' + hero.height, hero.align && 'oo-lp-hero-align--' + hero.align)} aria-roledescription="carousel" aria-label="Highlights"
        onMouseEnter={function () { hoverState[1](true); }} onMouseLeave={function () { hoverState[1](false); }}
        onFocus={function () { focusState[1](true); }} onBlur={onBlur} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="oo-lp-hero__track" aria-live={rotating ? 'off' : 'polite'}>
          {slides.map(function (s, k) {
            var on = k === i;
            var image = L().httpsUrl(s.image);
            // WL-CHK: videoPortrait below 768 px (absent: `video` everywhere, as before).
            var video = L().httpsUrl(portrait && s.videoPortrait ? s.videoPortrait : s.video);
            var cta = s.cta && L().text(s.cta.label, 24) ? s.cta : null;
            var badge = L().text(s.badge, 40);
            var phrases = (Array.isArray(s.rotate) ? s.rotate : EMPTY).map(function (x) { return L().text(x, 30); }).filter(Boolean).slice(0, 6);
            var media = !loadedMedia.has(k) ? null : video && autoplay
              ? <video className="oo-lp-hero__media" src={on || k === requested ? video : undefined} poster={image} autoPlay={on || k === requested} muted loop playsInline preload={on || k === requested ? 'auto' : 'none'} aria-hidden="true" onLoadedData={loadedMedia.done(k)} onError={loadedMedia.done(k)} />
              : <OO.ui.Image cover className="oo-lp-hero__media" src={image} sizes="100vw" alt="" loading="eager" fetchpriority={k === 0 ? 'high' : 'auto'} onLoad={loadedMedia.done(k)} onError={loadedMedia.done(k)} />;
            return (
              <div key={k} className={cx('oo-lp-slide', on && 'is-on')} role="group" aria-roledescription="slide"
                aria-label={(k + 1) + ' of ' + n} aria-hidden={on ? undefined : 'true'}>
                {media}
                {leftScrim
                  ? <div className="oo-lp-slide__scrim oo-lp-slide__scrim--left" aria-hidden="true" />
                  : <div className="oo-lp-slide__scrim" aria-hidden="true" style={typeof hero.overlay === 'number' ? { background: 'rgba(0, 0, 0, ' + Math.min(.8, Math.max(0, hero.overlay / 100)) + ')' } : undefined} />}
                <div className="oo-lp-wrap oo-lp-slide__text">
                  {badge ? <p className="oo-lp-slide__badge">{badge}</p> : null}
                  {k === 0 ? <h1 className="oo-lp-slide__title"><SlideTitle slide={s} deferImage={!loadedMedia.has(k)} /></h1> : <p className="oo-lp-slide__title"><SlideTitle slide={s} deferImage={!loadedMedia.has(k)} /></p>}
                  {phrases.length ? <Rotator phrases={phrases} prefix={L().text(s.rotatePrefix, 30)} active={on && autoplay && !stoppedState[0]} /> : null}
                  {L().text(s.subline, 160) ? <p className="oo-lp-slide__sub">{L().text(s.subline, 160)}</p> : null}
                  {ctx.superDesign && OO.pages.LandingSuper ? (on ? <OO.pages.LandingSuper.SlideCtas slide={s} ctx={ctx} /> : null) /* WL-SUPER: cta + more as pills */
                    : cta && on ? <TargetLink className="oo-btn oo-btn--primary oo-btn--lg oo-lp-slide__cta" target={cta.target} ctx={ctx} label={L().text(cta.label, 24)} button /> : null}
                </div>
              </div>
            );
          })}
        </div>
        {n > 1 || (phrasing && autoplay) ? (
          <div className="oo-lp-wrap oo-lp-hero__ctrl">
            {n > 1 ? <div className="oo-lp-hero__dots" role="group" aria-label="Choose a slide">
              {slides.map(function (s, k) {
                return (
                  <button key={k} type="button" className={cx('oo-lp-dot', k === i && 'is-on')} aria-label={'Slide ' + (k + 1) + ' of ' + n}
                    aria-current={k === i ? 'true' : undefined} onClick={function () { pick(k); }}><span className="oo-lp-dot__pill" /></button>
                );
              })}
            </div> : null}
            {autoplay ? (
              <button type="button" className="oo-lp-hero__pause" onClick={function () { stoppedState[1](!stoppedState[0]); }}
                aria-label={stoppedState[0] ? 'Play slideshow' : 'Pause slideshow'}>{stoppedState[0] ? 'Play' : 'Pause'}</button>
            ) : null}
          </div>
        ) : null}
      </section>
    );
  }

  // ---- ticker -----------------------------------------------------------------------------------------------------
  function Ticker(props) {
    var t = props.ticker;
    var items = t && t.enabled === true && Array.isArray(t.items) ? t.items.map(function (x) { return L().text(x, 80); }).filter(Boolean).slice(0, 8) : EMPTY;
    var reduced = useReducedMotion();
    var paused = React.useState(false);
    var still = reduced || t && t.style === 'static' || paused[0];
    if (!items.length) return null;
    // WL-CHK: `separator` follows each item in place of the leading bullet; `tone` picks the band's colour.
    var sep = L().text(t.separator, 3);
    var tone = ['accent', 'brand', 'dark'].indexOf(t.tone) >= 0 ? t.tone : null;
    var run = items.map(function (x, k) { return sep ? <React.Fragment key={k}><span className="oo-lp-ticker__item">{x}</span><span className="oo-lp-ticker__sep">{sep}</span></React.Fragment> : <span key={k} className="oo-lp-ticker__item">{x}</span>; });
    return (
      <div className={cx('oo-lp-ticker', still && 'is-still', t.speed && 'oo-lp-ticker--' + t.speed, sep && 'oo-lp-ticker--sep', tone && 'oo-lp-ticker--tone-' + tone)} role="region" aria-label="News">
        <ul className="oo-sr-only">{items.map(function (x, k) { return <li key={k}>{x}</li>; })}</ul>
        <div className="oo-lp-ticker__track" aria-hidden="true">{run}{still ? null : run}</div>
        {t.style === 'marquee' && !reduced ? <button type="button" className="oo-lp-ticker__pause" onClick={function () { paused[1](!paused[0]); }} aria-label={paused[0] ? 'Play announcements' : 'Pause announcements'}>{paused[0] ? 'Play' : 'Pause'}</button> : null}
      </div>
    );
  }

  // ---- footer -----------------------------------------------------------------------------------------------------
  var NETWORKS = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', x: 'X', youtube: 'YouTube', linkedin: 'LinkedIn' };

  function SocialLinks(props) { return <ul className="oo-lp-foot__socials" aria-label="Follow us">{props.items.map(function (x, i) { return <li key={i}><a className="oo-lp-foot__social" href={x.url} target="_blank" rel="noopener noreferrer"><OO.landingIcons.Icon name={x.network} size={22} /><span>{NETWORKS[x.network]}</span></a></li>; })}</ul>; }
  function FooterHours(props) { return props.ctx.publicOnly ? null : <LiveFooterHours {...props} />; }
  function LiveFooterHours(props) {
    var ctx = props.ctx, q = OO.useAsync(function () { return L().loadStores(ctx.master); }, [ctx.master]);
    var rows = Array.isArray(q.data) ? q.data : [], store = rows.filter(function (b) { return b.isMaster; })[0] || rows[0];
    var lines = L().hoursLines(store && store.hours);
    if (!lines.length) return null;
    return <dl className="oo-lp-hours">{lines.map(function (r) { return <div className="oo-lp-hours__row" key={r.days}><dt>{r.days}</dt><dd>{r.time}</dd></div>; })}</dl>;
  }
  function FooterContact(props) {
    var c = props.ctx.contact || {}, tel = L().telHref(c.phone);
    return <address className="oo-lp-foot__contact">{c.address ? <span>{L().text(c.address, 200)}</span> : null}{tel ? <a href={tel}>{c.phone}</a> : null}{typeof c.email === 'string' && /^[^\s@]+@[^\s@]+$/.test(c.email) ? <a href={'mailto:' + c.email}>{c.email}</a> : null}</address>;
  }

  // WL-CHK: footer.credits (at most 3): [{ label, image (https), url (https | null) }], drawn after PoweredBySipo.
  function creditsOf(f) {
    return (Array.isArray(f && f.credits) ? f.credits : EMPTY).filter(function (c) { return c && L().text(c.label, 30) && L().httpsUrl(c.image); }).slice(0, 3);
  }
  function Credit(props) {
    var c = props.credit, url = L().httpsUrl(c.url);
    var mark = <img className="oo-lp-credit__logo" src={L().httpsUrl(c.image)} alt={L().text(c.label, 30)} />;
    return <span className="oo-lp-credit"><span className="oo-lp-credit__label" aria-hidden="true">{L().text(c.label, 30)}</span>{url ? <a href={url} target="_blank" rel="noopener noreferrer">{mark}</a> : mark}</span>;
  }
  function Footer(props) {
    var f = props.footer || {}, ctx = props.ctx;
    var cols = (Array.isArray(f.columns) ? f.columns : EMPTY).filter(function (c) { return c && L().text(c.title, 30); }).slice(0, 4);
    var socials = (Array.isArray(f.socials) ? f.socials : EMPTY).filter(function (x) { return x && NETWORKS[x.network] && L().httpsUrl(x.url); }).slice(0, 6);
    var legal = (Array.isArray(f.legal) ? f.legal : EMPTY).filter(function (x) { return x && L().text(x.label, 30); }).slice(0, 6);
    var copyright = L().text(f.copyright, 120);
    var credits = creditsOf(f);
    return (
      <footer className={cx('oo-lp-foot', cols.some(function (c) { return !!c.kind; }) && 'oo-lp-foot--builder')}>
        <div className="oo-lp-wrap">
          <div className="oo-lp-foot__top">
            <div className="oo-lp-foot__brand">
              {props.logo ? <img className="oo-lp-foot__logo" src={props.logo} alt={props.name || 'Logo'} /> : <span className="oo-lp-foot__name">{props.name}</span>}
              {props.tagline ? <span className="oo-lp-foot__tag">{props.tagline}</span> : null}
            </div>
            {cols.map(function (c, k) {
              var links = (Array.isArray(c.links) ? c.links : EMPTY).filter(function (x) { return x && L().text(x.label, 30); }).slice(0, 8);
              return (
                <nav key={k} className="oo-lp-foot__col" aria-label={L().text(c.title, 30)}>
                  <h2 className="oo-lp-foot__title">{L().text(c.title, 30)}</h2>
                  {c.kind === 'text' ? <p className="oo-lp-foot__body">{L().text(c.body, 600)}</p> : c.kind === 'contact' ? <FooterContact ctx={ctx} /> : c.kind === 'social' ? <SocialLinks items={socials} /> : c.kind === 'hours' ? <FooterHours ctx={ctx} /> :
                  <ul>{links.map(function (x, j) { return <li key={j}><TargetLink className="oo-lp-foot__link" target={x.target} label={L().text(x.label, 30)} ctx={ctx} /></li>; })}</ul>}
                </nav>
              );
            })}
          </div>
          {socials.length && !cols.some(function (c) { return c.kind === 'social'; }) ? (
            <ul className="oo-lp-foot__socials" aria-label="Follow us">
              {socials.map(function (x, k) {
                return <li key={k}><a className="oo-lp-foot__social" href={L().httpsUrl(x.url)} target="_blank" rel="noopener noreferrer">{NETWORKS[x.network]}</a></li>;
              })}
            </ul>
          ) : null}
          {Array.isArray(f.payments) && f.payments.length ? <ul className="oo-lp-payments" aria-label="Accepted payment methods">{f.payments.map(function (p) { return <li key={p}><OO.landingIcons.Payment name={p} /></li>; })}</ul> : null}
          <div className="oo-lp-foot__base">
            {copyright ? <span className="oo-lp-foot__copy">{copyright}</span> : null}
            {legal.length ? (
              <ul className="oo-lp-foot__legal">
                {legal.map(function (x, k) { return <li key={k}><TargetLink className="oo-lp-foot__link" target={x.target} label={L().text(x.label, 30)} ctx={ctx} /></li>; })}
              </ul>
            ) : null}
          </div>
          {credits.length ? <><OO.ui.PoweredBySipo className="oo-lp-foot__powered" /><div className="oo-lp-foot__credits">{credits.map(function (c, k) { return <Credit key={k} credit={c} />; })}</div></> : <OO.ui.PoweredBySipo className="oo-lp-foot__powered" />}
        </div>
      </footer>
    );
  }
  // WL-CHK: the bold footer. A dark band: the logo (landing.header.logo, else the brand logo made for a dark surface) and
  // the tagline; the link columns (a 'social' column shows round icon buttons); the partner row ("Online order partner"
  // + the SIPO mark, then footer.credits, divided by rules); copyright on the left, legal links on the right.
  function BoldFooter(props) {
    var f = props.footer || {}, ctx = props.ctx;
    var cols = (Array.isArray(f.columns) ? f.columns : EMPTY).filter(function (c) { return c && L().text(c.title, 30); }).slice(0, 4);
    var socials = (Array.isArray(f.socials) ? f.socials : EMPTY).filter(function (x) { return x && NETWORKS[x.network] && L().httpsUrl(x.url); }).slice(0, 6);
    var legal = (Array.isArray(f.legal) ? f.legal : EMPTY).filter(function (x) { return x && L().text(x.label, 30); }).slice(0, 6);
    var copyright = L().text(f.copyright, 120);
    var credits = creditsOf(f);
    // WL-OPT: whether the SIPO badge shows (the site's brand.poweredBy, OO.ui.PoweredBySipo's own check). Without it the
    // partner row drops the rule before the first credit, and the whole row (its top rule) when there are no credits.
    var badge = typeof OO.ui.PoweredBySipo.shown === 'function' ? OO.ui.PoweredBySipo.shown() : true;
    var socialList =<ul className="oo-lp-foot__socials" aria-label="Follow us">{socials.map(function (x, i) { return <li key={i}><a className="oo-lp-foot__social" href={x.url} target="_blank" rel="noopener noreferrer" aria-label={NETWORKS[x.network]}><OO.landingIcons.Icon name={x.network} size={17} strokeWidth="2.1" /></a></li>; })}</ul>;
    return (
      <footer className="oo-lp-foot oo-lp-foot--bold">
        <div className="oo-lp-wrap">
          <div className="oo-lp-foot__top">
            <div className="oo-lp-foot__brand">
              {props.logo ? <img className="oo-lp-foot__logo" src={props.logo} alt={props.name || 'Logo'} /> : <span className="oo-lp-foot__name">{props.name}</span>}
              {props.tagline ? <p className="oo-lp-foot__tag">{props.tagline}</p> : null}
            </div>
            {cols.map(function (c, k) {
              var links = (Array.isArray(c.links) ? c.links : EMPTY).filter(function (x) { return x && L().text(x.label, 30); }).slice(0, 8);
              return (
                <nav key={k} className="oo-lp-foot__col" aria-label={L().text(c.title, 30)}>
                  <h2 className="oo-lp-foot__title">{L().text(c.title, 30)}</h2>
                  {c.kind === 'text' ? <p className="oo-lp-foot__body">{L().text(c.body, 600)}</p> : c.kind === 'contact' ? <FooterContact ctx={ctx} /> : c.kind === 'social' ? socialList : c.kind === 'hours' ? <FooterHours ctx={ctx} /> :
                  <ul>{links.map(function (x, j) { return <li key={j}><TargetLink className="oo-lp-foot__link" target={x.target} label={L().text(x.label, 30)} ctx={ctx} /></li>; })}</ul>}
                </nav>
              );
            })}
            {socials.length && !cols.some(function (c) { return c.kind === 'social'; }) ? <nav className="oo-lp-foot__col" aria-label="Follow us"><h2 className="oo-lp-foot__title">Follow us</h2>{socialList}</nav> : null}
          </div>
          {Array.isArray(f.payments) && f.payments.length ? <ul className="oo-lp-payments" aria-label="Accepted payment methods">{f.payments.map(function (p) { return <li key={p}><OO.landingIcons.Payment name={p} /></li>; })}</ul> : null}
          {badge || credits.length ? (
            <div className="oo-lp-foot__partners">
              <OO.ui.PoweredBySipo className="oo-lp-foot__powered" label="Online order partner" logo="online-order/fixtures/img/sipo-logo-color.svg" />
              {credits.map(function (c, k) { return <React.Fragment key={k}>{badge || k ? <span className="oo-lp-foot__rule" aria-hidden="true" /> : null}<Credit credit={c} /></React.Fragment>; })}
            </div>
          ) : null}
          <div className="oo-lp-foot__base">
            {copyright ? <span className="oo-lp-foot__copy">{copyright}</span> : null}
            {legal.length ? (
              <ul className="oo-lp-foot__legal">
                {legal.map(function (x, k) { return <li key={k}><TargetLink className="oo-lp-foot__link" target={x.target} label={L().text(x.label, 30)} ctx={ctx} /></li>; })}
              </ul>
            ) : null}
          </div>
        </div>
      </footer>
    );
  }

  // WL-CHK: the brand logo variant made for a surface colour: logo.light on a light surface, logo.dark on a dark one (the
  // other one, then the mark, when it is missing).
  function logoOn(theme, surface) {
    var T = OO.theme, light = false;
    try { light = !!surface && T.contrastRatio('#000000', surface) >= T.contrastRatio('#FFFFFF', surface); } catch (e) { light = false; }
    var order = light ? [theme.logoLight, theme.logoDark] : [theme.logoDark, theme.logoLight];
    return order[0] || order[1] || theme.logoMark || null;
  }

  function Skeleton() {
    return (
      <div className="oo-lp oo-lp--loading" aria-busy="true">
        <div className="oo-lp-head"><div className="oo-lp-wrap oo-lp-head__row"><span className="oo-lp-skel oo-lp-skel--logo" /></div></div>
        <div className="oo-lp-hero oo-lp-hero--skel" aria-hidden="true" />
      </div>
    );
  }

  OO.pages.Landing = function Landing(props) {
    var route = props.route;
    var key = route.hotel;
    var W = OO.whitelabel;
    W.useRevision();
    var site = W.siteFor(key);
    var master = site && site.masterKey ? site.masterKey : key;
    var h = OO.useHotel(master);
    var chainRes = OO.useChain(master);
    var chooserS = React.useState(null); // null | { query }
    var dialogS = React.useState(null); // WL-CHK: the id of the section open in a dialog | null
    // The key whose read has settled (W.ensure(key) resolved; it never rejects). That covers every "no site": none
    // published, the web product off, an expired preview, a key this host doesn't serve live, and (WL-int) the kill
    // switch off, where ensure() resolves null with no request and records nothing (status(key) stays null for good).
    var settledS = React.useState(null);
    React.useEffect(function () {
      var on = true;
      var done = function () { if (on) settledS[1](key); };
      Promise.resolve(W.ensure(key)).then(done, done);
      return function () { on = false; };
    }, [key]);
    var settled = settledS[0] === key && !(W.isPreview() && W.previewPending());
    // No site for this key once its read has settled: the menu (ordering is never blocked).
    var away = !site && settled;
    React.useEffect(function () {
      if (away) OO.router.go(OO.router.href('menu', null, { hotel: key }), { replace: true });
    }, [away, key]);
    var landing = site && site.doc.landing ? site.doc.landing : null;
    // WL-VEL: the site page this route shows (null: the home page) and a legacy redirect to follow.
    var slug = route.params && typeof route.params.slug === 'string' ? route.params.slug : null;
    var storesPage = !!(route.params && route.params.stores);
    var sitePage = storesPage ? { slug: 'stores', label: 'Stores', sections: [] }
      : landing && slug && L().pageOf ? L().pageOf(landing, slug) : null;
    var redirectFrom = route.params && typeof route.params.redirect === 'string' ? route.params.redirect : null;
    var ctxRef = React.useRef(null);
    // An Order click made on the public HTML uses this same orderPlan / chooser once the page mounts.
    React.useEffect(function () {
      var intent = window.__OO_WEBSITE_ORDER;
      if (!intent || !ctxRef.current || intent.path !== window.location.pathname) return;
      delete window.__OO_WEBSITE_ORDER;
      ctxRef.current.order(intent.query || null);
    }, [key]);
    React.useEffect(function () {
      if (!landing || !settled) return;
      if (slug && !sitePage) { OO.router.go(OO.router.href('landing', null, { hotel: key }), { replace: true }); return; }
      if (!redirectFrom || !ctxRef.current) return;
      // VEL-FIX: OO.landing.redirectTarget (the query kept without the PII keys, as the server's 301 keeps it).
      var dest = L().redirectTarget ? L().redirectTarget(landing, redirectFrom, ctxRef.current, route.query, master) : null;
      var home = OO.router.href('landing', null, { hotel: key });
      if (!dest) { OO.router.go(home, { replace: true }); return; }
      if (dest.external) { try { window.location.replace(dest.href); } catch (e) { OO.router.go(home, { replace: true }); } return; }
      OO.router.go(dest.href, { replace: true });
    }, [!!landing, settled, slug, !!sitePage, redirectFrom]);
    // Builder collections and search use the same hidden-item policy as the actual menu. Keep the v1 section data intact.
    var hasBuilderData = !!(landing && (landing.header && landing.header.search || (landing.nav || []).some(function (n) { return n.target && n.target.type === 'categories'; }) || (landing.sections || []).some(function (s) { return s.enabled && ['products', 'menu', 'categories'].indexOf(s.type) >= 0 && (s.type !== 'categories' || s.variant || s.style || s.columns); })));
    var pageBuilder = !!(sitePage && sitePage.sections.some(function (s) { return s && s.enabled && ['products', 'menu', 'categories'].indexOf(s.type) >= 0; })); // WL-VEL
    var builderMenu = React.useMemo(function () {
      if (!(hasBuilderData || pageBuilder) || !h.store || !h.menu) return h.menu;
      var moment = OO.pages.Menu.shared.orderMoment(h.store, null, 'online');
      var display = OO.model.menuDisplay(h.menu, Object.assign({}, moment, { timeZone: h.store.timezone }));
      return L().visibleMenu(h.menu, display);
    }, [hasBuilderData, pageBuilder, h.store, h.menu]);
    var brandName = site ? site.doc.brand.name : '';
    // WL-VEL: a site page's own title (VEL-FIX: else "{label} | {brand}", as the server's <title>), before the landing's.
    var seoTitle = (sitePage && L().pageTitle ? L().pageTitle(sitePage, brandName) : '') || (landing && landing.seo ? L().text(landing.seo.title, 70) : '');
    React.useEffect(function () {
      if (landing) document.title = seoTitle || brandName || document.title;
    }, [landing, seoTitle, brandName]);
    // A #wl-{id} in the URL on arrival scrolls to that section once it is drawn (WL-CHK: or opens it, when it is a
    // section shown in a dialog).
    React.useEffect(function () {
      var hash = String((window.location && window.location.hash) || '');
      if (!landing || !/^#wl-[a-z0-9-]{1,32}$/.test(hash)) return;
      var id = hash.slice(4);
      var shownHere = L().sectionsOf(sitePage || landing, { orderingOff: !W.landingOrdering(key).on });
      if (shownHere.some(function (s) { return s.id === id && L().isDialog(s); })) dialogS[1](id);
      else scrollToSection(id);
    }, [!!landing, slug, !!sitePage]); // WL-VEL: also on arriving at another page
    // WL-CHK: the bold design reveals each section as it scrolls into view. The hidden state is a data-reveal attribute
    // set here (never in the markup), only on sections below the fold, and never under reduced motion or without
    // IntersectionObserver: the page reads fully without it.
    var bold = !!landing && L().design(landing) === 'bold';
    var superD = !!landing && L().design(landing) === 'super'; // WL-SUPER: the Super design (src/pages/landing-super.jsx)
    React.useLayoutEffect(function () {
      if (!(bold || superD) || typeof document === 'undefined' || reducedNow() || typeof window.IntersectionObserver !== 'function') return undefined;
      var main = document.getElementById('oo-lp-main');
      if (!main) return undefined;
      var vh = window.innerHeight || 0;
      var io = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { e.target.setAttribute('data-reveal', 'in'); io.unobserve(e.target); } });
      }, { rootMargin: '0px 0px -8% 0px' });
      Array.prototype.forEach.call(main.children, function (el) {
        if (!el.classList || !el.classList.contains('oo-lp-sec')) return;
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', el.getBoundingClientRect().top < vh ? 'in' : 'out');
        if (el.getAttribute('data-reveal') === 'out') io.observe(el);
      });
      return function () { io.disconnect(); };
    });

    // WL-5: the chain master's store could not be read (getRestaurant 404s without a main terminal): the page wears the
    // document's theme itself (activateHotel never ran), with nothing to order there.
    var storeFailed = !h.store && !!(h.error || h.notFound);
    React.useEffect(function () {
      if (!site || !storeFailed || !OO.data || typeof OO.data.activateHotel !== 'function') return;
      OO.data.activateHotel(master, { id: null, onlineKey: master, name: site.doc.brand.name || '', theme: {} }, { cart: false });
    }, [site, storeFailed, master]);

    if (!site || !landing) return <Skeleton />;

    var store = h.store;
    // WL-5: the website's ordering (see the header).
    var orderingOff = !W.landingOrdering(key).on;
    // The built-in directory reuses the chooser, with the website's branding and a full-page list.
    var sections = storesPage ? [{ id: 'store-listing', type: 'stores', enabled: true, _listing: true }]
      : L().sectionsOf(sitePage || landing, { orderingOff: orderingOff }); // WL-VEL: a site page's own
    var chainState = { chain: chainRes.chain, loading: chainRes.loading, failed: !!chainRes.error };
    var chainStores = chainRes.chain ? chainRes.chain.stores : [];
    var contactSection = (Array.isArray(landing.sections) ? landing.sections : []).filter(function (s) { return s && s.type === 'contact' && s.enabled === true; })[0];
    var masterInChain = chainStores.filter(function (b) { return b.isMaster; })[0] || chainStores[0] || null;
    // WL-CHK: the website's logo (landing.header.logo), the logo made for the dark footer band, and the dialog sections.
    var theme = (store && store.theme) || W.themeOver({}, site.doc);
    var headerLogo = L().httpsUrl(landing.header && landing.header.logo);
    var darkLogo = headerLogo || logoOn(theme, theme.heroBg);
    function openSection(id) {
      dialogS[1](id);
      try { window.history.replaceState(window.history.state, '', '#' + L().anchorId(id)); } catch (e) { /* no history */ }
    }
    function closeSection() {
      dialogS[1](null);
      try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search); } catch (e) { /* no history */ }
    }
    var ctx = {
      key: key, master: master, store: store, menu: h.menu, builderMenu: builderMenu, chain: chainState, sections: sections,
      reviews: site.reviews, contact: landing.contact || {},
      chooseStore: function () { chooserS[1]({ query: null }); },
      // WL-CHK
      bold: bold, brand: brandName, logo: darkLogo || null, openSection: openSection,
      superDesign: superD, // WL-SUPER
      landing: landing, page: sitePage ? sitePage.slug : null, storesPage: storesPage, // WL-VEL: cross-page links (OO.landing.targetOf)
      timeZone: site.doc.locale && typeof site.doc.locale.timezone === 'string' ? site.doc.locale.timezone : null, // VEL-FIX: the document's (forms, hours)
      // WL-5: the information-only website's context (OO.landing.targetOf, the sections).
      orderingOff: orderingOff, off: L().orderingOffOf(landing), storeFailed: storeFailed,
      storeLoading: !store && !storeFailed, ordering: site.ordering || null,
      phone: (landing.contact && L().text(landing.contact.phone, 24)) || (contactSection && L().text(contactSection.phone, 24))
        || (store && store.phone) || null,
      directions: L().directionsUrl(store ? { lat: store.lat, lng: store.lng, name: store.name, address: store.address && store.address.full } : masterInChain) || L().directionsUrl({ name: brandName, address: landing.contact && landing.contact.address }),
      // No menu anywhere: the master's store is unreadable and the chain lists no store with a menu.
      menuUnavailable: storeFailed && !chainState.loading && !chainStores.length,
      // The order hand-off: one store → its menu; several (or the chain still loading) → the store chooser.
      order: function (query) {
        var plan = L().orderPlan(chainState, master, query || null);
        if (plan.kind === 'go') OO.router.go(plan.href);
        else chooserS[1]({ query: query || null });
      },
    };
    // WL-5: the logo and name come from the document (brand.*), with or without the store.
    var overHero = !bold && !superD && theme.headerVariant === 'transparent' && !sitePage; // WL-VEL (VEL-FIX): a site page has no hero
    var Section = OO.pages.LandingSections.Section;
    var chooser = OO.pages.LandingSections.StoreChooser;
    var footTagline = L().text(landing.footer && landing.footer.tagline, 200) || site.doc.brand.tagline;
    var Super = superD ? OO.pages.LandingSuper : null; // WL-SUPER
    var Head = Super ? Super.Header : bold ? BoldHeader : Header;
    var dialogSection = dialogS[0] ? sections.filter(function (s) { return s.id === dialogS[0] && L().isDialog(s); })[0] || null : null;
    // The dialog host only joins the tree when the document has dialog sections (the same element tree as before
    // otherwise: React derives useId values from it).
    var hasDialogs = sections.some(function (s) { return L().isDialog(s); });
    var chooserEl = React.createElement(chooser, {
      open: !!chooserS[0], master: master, query: chooserS[0] ? chooserS[0].query : null, browse: orderingOff,
      onClose: function () { chooserS[1](null); },
    });
    ctxRef.current = ctx; // WL-VEL: the redirect effect resolves its target with it
    // WL-VEL: the boutique design draws its own chrome around the same sections (src/pages/landing-boutique.jsx).
    if (L().design(landing) === 'canvas' && OO.pages.LandingComposition) {
      return <OO.pages.LandingComposition.CanvasPage ctx={ctx} landing={landing} sections={sections} chooser={chooserEl}
        dialog={dialogSection ? <OO.pages.LandingSections.SectionDialog section={dialogSection} ctx={ctx} onClose={closeSection} /> : null} />;
    }
    if (L().design(landing) === 'boutique' && OO.pages.LandingBoutique) {
      return <OO.pages.LandingBoutique ctx={ctx} landing={landing} site={site} theme={theme} name={brandName} tagline={footTagline}
        logo={headerLogo || theme.logo || null} footLogo={darkLogo || null} page={sitePage} sections={sections} chooser={chooserEl}
        dialog={dialogSection ? <OO.pages.LandingSections.SectionDialog section={dialogSection} ctx={Object.assign({}, ctx, { boutique: true })} onClose={closeSection} /> : null} />;
    }
    return (
      <div className={cx('oo-lp', overHero && 'oo-lp--over', bold && 'oo-lp--bold', Super && 'oo-lp--super', Super && theme.scriptFont && 'oo-lp--script')}>
        <a className="oo-lp-skip" href="#oo-lp-main">Skip to content</a>
        {landing.header && landing.header.layout === 'commerce' ? <><Ticker ticker={landing.ticker} /><Head ctx={ctx} landing={landing} theme={theme} name={brandName} overHero={overHero} /></> : <Head ctx={ctx} landing={landing} theme={theme} name={brandName} overHero={overHero} />}
        <main id="oo-lp-main" className="oo-lp-main" tabIndex={-1}>
          <HeroSlider hero={sitePage ? null : landing.hero} ctx={ctx} overHero={overHero} />
          {landing.header && landing.header.layout === 'commerce' ? null : <Ticker ticker={sitePage ? null : landing.ticker} />}
          {sections.map(function (s) { return L().isDialog(s) ? null : <Section key={s.id} section={s} ctx={ctx} />; })}
        </main>
        {Super ? <Super.Footer footer={landing.footer} ctx={ctx} name={brandName} tagline={footTagline} logo={darkLogo} /> : bold
          ? <BoldFooter footer={landing.footer} ctx={ctx} name={brandName} tagline={footTagline} logo={darkLogo} />
          : <Footer footer={landing.footer} ctx={ctx} name={brandName} tagline={footTagline} logo={headerLogo} />}
        {hasDialogs ? <>{chooserEl}{dialogSection ? <OO.pages.LandingSections.SectionDialog section={dialogSection} ctx={ctx} onClose={closeSection} /> : null}</> : chooserEl}
      </div>
    );
  };
  Header.Public = function PublicHeader(props) { return HeaderView(props, { key: props.ctx.master, count: 0 }); };
  BoldHeader.Public = function PublicBoldHeader(props) { return BoldHeaderView(props, { key: props.ctx.master, count: 0 }); };
  OO.pages.Landing.logic = { useReducedMotion: useReducedMotion, ROTATE_MS: ROTATE_MS };
  OO.pages.Landing.parts = { Header: Header, HeroSlider: HeroSlider, Ticker: Ticker, Footer: Footer, TargetLink: TargetLink, ProductSearch: ProductSearch, NavItem: NavItem, Utility: Utility, BoldHeader: BoldHeader, BoldFooter: BoldFooter, logoClass: logoClass,
    // WL-SUPER: shared with src/pages/landing-super.jsx
    plainClick: plainClick, creditsOf: creditsOf, Credit: Credit, FooterContact: FooterContact, FooterHours: FooterHours, NETWORKS: NETWORKS };
})();


// ===================== src/pages/landing-blocks.jsx =====================
// WL-5: optional page-builder blocks. Registers additively after the v1 landing components.
// WL-CHK: products layout 'ranked' (Ranked: numbered cards, the dish's description else the section `note`, "Order now"
// → the dish's deep link; with `more`, an ink pill with its label in the head row and a dashed "See everything" tail
// card, whose link reads "View full menu" when `more` leads to the menu, the order hand-off, else the label) and
// categories variant / style 'spotlight' (Spotlight: tall cards in a four-colour cycle under round photos, the label one
// word per line, an "Order now" pill on hover / focus; `more` → a centred ink button). Both on the bold carousel
// (OO.pages.LandingSections.Rail).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var L = OO.landing, ui = OO.ui, old = OO.pages.LandingSections;
  var Frame = old.Frame, Paras = old.Paras, Link = OO.pages.Landing.parts.TargetLink;
  var Icon = OO.landingIcons.Icon;
  function arr(v) { return Array.isArray(v) ? v : []; }
  function cx() { return ui.cx.apply(null, arguments); }
  function builderCtx(ctx) { return ctx.builderMenu ? Object.assign({}, ctx, { menu: ctx.builderMenu }) : ctx; }
  function money(n, ctx) { return OO.format.money(n, ctx.store && ctx.store.currency); }
  function useMenuState(ctx, empty) {
    var pending = !ctx.menu && !ctx.storeFailed && ctx.storeLoading !== false;
    return L.dataState({ loading: pending, failed: ctx.storeFailed, empty: empty, late: old.useLate(pending) });
  }
  function Loading() { return <div className="oo-lp-block-loading" aria-busy="true" aria-label="Loading"><span className="oo-lp-skel" /></div>; }
  function Carousel(props) {
    var ref = React.useRef(null), id = L.anchorId(props.id) + '-carousel';
    function move(dir) { if (ref.current) ref.current.scrollBy({ left: dir * ref.current.clientWidth * .85, behavior: OO.pages.Landing.logic.useReducedMotion ? (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth') : 'auto' }); }
    return <div className="oo-lp-carousel" role="region" aria-roledescription="carousel" aria-label={props.label}>
      <div className="oo-lp-carousel__controls"><button type="button" className="oo-lp-carousel__arrow" onClick={function () { move(-1); }} aria-controls={id} aria-label={'Previous ' + props.label}>‹</button><button type="button" className="oo-lp-carousel__arrow" onClick={function () { move(1); }} aria-controls={id} aria-label={'Next ' + props.label}>›</button></div>
      <ul className={cx('oo-lp-carousel__track', props.className)} ref={ref} id={id} tabIndex={0} aria-label={props.label} onKeyDown={function (e) { if (e.target !== e.currentTarget) return; if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); move(e.key === 'ArrowLeft' ? -1 : 1); } }}>{props.children}</ul>
    </div>;
  }
  function ProductCard(props) {
    var p = props.product, ctx = props.ctx, price = L.productPrice(p);
    return <button type="button" className={cx('oo-lp-product', props.compact && 'oo-lp-product--compact')} onClick={function () { ctx.order({ type: 'product', id: String(p.id) }); }} aria-label={'View ' + p.name}>
      {props.images !== false ? <span className="oo-lp-product__media">{p.image ? <img src={p.image} alt="" loading="lazy" /> : <OO.icons.ImagePlaceholder size={28} />}{props.badge ? <span className="oo-lp-product__badge">{props.badge}</span> : null}</span> : null}
      <span className="oo-lp-product__name">{p.name}</span>
      {props.prices !== false && typeof price.amount === 'number' ? <span className="oo-lp-product__price">{price.from ? 'From ' : ''}{money(price.amount, ctx)}</span> : null}
      {!ctx.orderingOff ? <span className="oo-lp-product__add" aria-hidden="true">Choose options <span>＋</span></span> : null}
    </button>;
  }
  function Products(props) {
    var s = props.section, ctx = builderCtx(props.ctx), popular = !s.source || s.source === 'popular';
    var q = OO.useAsync(function () { return popular ? L.loadPopular(ctx.master) : Promise.resolve(true); }, [ctx.master, popular]);
    var list = L.productsOf(s, ctx.menu, q.data);
    var pending = !ctx.storeFailed && (!ctx.menu && ctx.storeLoading !== false || popular && q.loading);
    var state = L.dataState({ loading: pending, failed: ctx.storeFailed || popular && (!!q.error || !q.loading && !q.data), empty: !list.length, late: old.useLate(pending) });
    if (state === 'hide') return null;
    if (s.layout === 'ranked') return <Ranked section={s} ctx={ctx} list={list} wait={state === 'wait'} />;
    var cards = list.map(function (p) { return <li key={p.id}><ProductCard product={p} ctx={ctx} prices={s.showPrices} badge={L.text(s.badge, 24)} /></li>; });
    return <Frame section={s} ctx={ctx} label="Products">{state === 'wait' ? <Loading /> : s.layout === 'grid' ? <ul className="oo-lp-products-grid">{cards}</ul> : <Carousel id={s.id} label={s.title || 'Products'}>{cards}</Carousel>}</Frame>;
  }
  function Ranked(props) {
    var s = props.section, ctx = props.ctx, Rail = old.Rail, Glyph = OO.landingIcons.Glyph;
    var note = L.text(s.note, 40), more = s.more && L.text(s.more.label, 24) ? s.more : null;
    var aside = more ? <Link className="oo-lp-sec__aside-pill" target={more.target} label={L.text(more.label, 24)} ctx={ctx} icon={<Glyph name="arrow-right" size={16} strokeWidth={2.8} />} /> : null;
    var cards = props.wait ? [0, 1, 2, 3].map(function (i) { return <li key={i} aria-hidden="true"><span className="oo-lp-skel oo-lp-rank oo-lp-rank--skel" /></li>; }) : props.list.map(function (p, i) {
      var price = L.productPrice(p), desc = L.text(p.description, 400) || note;
      return (
        <li key={p.id}>
          <div className="oo-lp-rank">
            <span className="oo-lp-rank__media">{p.image ? <img src={p.image} alt="" loading="lazy" /> : <OO.icons.ImagePlaceholder size={28} />}<span className="oo-lp-rank__chip" aria-hidden="true">{i + 1}</span></span>
            <span className="oo-lp-rank__body">
              <span className="oo-lp-rank__name">{p.name}</span>
              {desc ? <span className="oo-lp-rank__note">{desc}</span> : null}
              {s.showPrices === true && typeof price.amount === 'number' ? <span className="oo-lp-rank__price">{price.from ? 'From ' : ''}{money(price.amount, ctx)}</span> : null}
              <button type="button" className="oo-lp-rank__go" onClick={function () { ctx.order({ type: 'product', id: String(p.id) }); }} aria-label={(ctx.orderingOff ? 'View ' : 'Order ') + p.name}>{ctx.orderingOff ? 'View dish' : 'Order now'}<Glyph name="arrow-right" size={14} strokeWidth={2.8} /></button>
            </span>
          </div>
        </li>
      );
    });
    if (more && !props.wait) {
      var to = L.targetOf(more.target, ctx);
      cards.push(
        <li key="more">
          <Link className="oo-lp-rank__tail" target={more.target} ctx={ctx} label={L.text(more.label, 24)}>
            <span className="oo-lp-rank__plus" aria-hidden="true"><Glyph name="plus" size={22} /></span>
            <span className="oo-lp-rank__tail-title">See everything</span>
            <span className="oo-lp-rank__tail-text">More dishes are waiting in the full menu</span>
            <span className="oo-lp-rank__tail-link">{to && to.kind === 'order' && !to.choose ? 'View full menu' : L.text(more.label, 24)}<span aria-hidden="true"> →</span></span>
          </Link>
        </li>
      );
    }
    return <Frame section={s} ctx={ctx} label="Products" className="oo-lp-ranked" aside={aside} noMore><Rail id={s.id} label={L.text(s.title, 80) || 'Products'} className="oo-lp-rail--ranked">{cards}</Rail></Frame>;
  }
  function Spotlight(props) {
    var s = props.section, ctx = props.ctx, Rail = old.Rail, Glyph = OO.landingIcons.Glyph;
    var more = s.more && L.text(s.more.label, 24) ? s.more : null;
    var cards = props.wait ? [0, 1, 2, 3].map(function (i) { return <li key={i} aria-hidden="true"><span className="oo-lp-skel oo-lp-spot oo-lp-spot--skel" /></li>; }) : props.tiles.map(function (v, i) {
      var words = L.text(v.label, 24).split(/\s+/);
      return (
        <li key={i}>
          <button type="button" className={cx('oo-lp-spot', 'oo-lp-spot--' + (i % 4))} onClick={function () { ctx.order({ type: 'category', id: String(v.categoryId) }); }}>
            <span className="oo-lp-spot__img"><img src={v.image} alt="" loading="lazy" /></span>
            <span className="oo-lp-spot__panel">
              <Glyph name="book" size={44} className="oo-lp-spot__icon" />
              <span className="oo-lp-spot__label">{words.map(function (w, k) { return <React.Fragment key={k}>{k ? ' ' : null}<span>{w}</span></React.Fragment>; })}</span>
              <span className="oo-lp-spot__cta" aria-hidden="true">{ctx.orderingOff ? 'View menu' : 'Order now'}</span>
            </span>
          </button>
        </li>
      );
    });
    return (
      <Frame section={s} ctx={ctx} label="Categories" className="oo-lp-spotlight" noMore>
        <Rail id={s.id} label={L.text(s.title, 80) || 'Categories'} className="oo-lp-rail--spot">{cards}</Rail>
        {more ? <div className="oo-lp-spotlight__more"><Link className="oo-lp-btn-ink" target={more.target} label={L.text(more.label, 24)} ctx={ctx} /></div> : null}
      </Frame>
    );
  }
  function Videos(props) {
    var s = props.section, items = arr(s.items).filter(function (v) { return v && L.httpsUrl(v.video) && /\.mp4(?:[?#]|$)/i.test(v.video) && L.httpsUrl(v.poster); }).slice(0, 8);
    if (!items.length) return null;
    return <Frame section={s} ctx={props.ctx} label="Videos"><Carousel id={s.id} label={s.title || 'Videos'} className="oo-lp-video-list">{items.map(function (v, i) { return <li key={i}><figure className="oo-lp-video"><video src={L.httpsUrl(v.video)} poster={L.httpsUrl(v.poster)} controls playsInline preload="none" aria-label={L.text(v.caption, 80) || 'Video ' + (i + 1)} />{v.caption ? <figcaption>{L.text(v.caption, 80)}</figcaption> : null}{v.target ? <Link target={v.target} label="Explore more" className="oo-lp-link" ctx={props.ctx} /> : null}</figure></li>; })}</Carousel></Frame>;
  }
  function Features(props) {
    var s = props.section, image = L.httpsUrl(s.image), items = arr(s.items).slice(0, 6);
    if (!items.length) return null;
    return <Frame section={s} ctx={props.ctx} label="Features"><div className={cx('oo-lp-features', image && 'has-image', s.imageSide === 'left' && 'image-left')}>
      <ul className="oo-lp-features__items">{items.map(function (v, i) { return <li key={i}><Icon name={v.icon} size={32} /><h3>{L.text(v.title, 40)}</h3><p>{L.text(v.body, 200)}</p></li>; })}</ul>
      {image ? <img className="oo-lp-features__image" src={image} alt="" loading="lazy" /> : null}</div></Frame>;
  }
  function Stars(props) { var n = Math.max(0, Math.min(5, Number(props.rating) || 0)); return <span className="oo-lp-stars" role="img" aria-label={n + ' out of 5 stars'}><span aria-hidden="true">{'★'.repeat(Math.round(n)) + '☆'.repeat(5 - Math.round(n))}</span></span>; }
  function Testimonials(props) {
    var s = props.section, reviews = props.ctx.reviews || {}, summary = reviews.summary || {};
    var items = arr(s.items).map(function (v) { return { display_name: v.name, rating: v.rating, text: v.text }; });
    if (s.ratings === true) items = items.concat(arr(reviews.items));
    items = items.filter(function (v) { return v && L.text(v.display_name, 40) && L.text(v.text, 400) && Number(v.rating) >= 1 && Number(v.rating) <= 5; });
    var showSummary = s.showSummary !== false && Number(summary.count) > 0 && Number(summary.average) >= 0;
    if (!items.length && !showSummary) return null;
    return <Frame section={s} ctx={props.ctx} label="Customer reviews">{showSummary ? <div className="oo-lp-review-summary"><Stars rating={summary.average} /><b>{Number(summary.average).toFixed(1)} / 5</b><span>{Number(summary.count)} reviews</span></div> : null}
      {items.length ? <Carousel id={s.id} label={s.title || 'Customer reviews'}>{items.map(function (v, i) { return <li key={i}><figure className="oo-lp-testimonial"><Stars rating={v.rating} /><blockquote>{v.text}</blockquote><figcaption>{v.display_name}{v.date ? <time dateTime={v.date}>{String(v.date).slice(0, 10)}</time> : null}</figcaption></figure></li>; })}</Carousel> : null}</Frame>;
  }
  function Lightbox(props) {
    var items = props.items, index = props.index, item = items[index];
    return <ui.Sheet open={index !== null} onClose={props.close} variant="dialog" label="Image gallery" title={item && item.caption || 'Image gallery'}>
      {item ? <div className="oo-lp-lightbox" onKeyDown={function (e) { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); props.pick((index + (e.key === 'ArrowLeft' ? -1 : 1) + items.length) % items.length); } }}><img src={item.image} alt={item.caption || 'Gallery image ' + (index + 1)} /><p role="status">{index + 1} of {items.length}{item.caption ? ' · ' + item.caption : ''}</p>{items.length > 1 ? <div className="oo-lp-lightbox__controls"><ui.Button variant="outline" onClick={function () { props.pick((index - 1 + items.length) % items.length); }}>Previous image</ui.Button><ui.Button variant="outline" onClick={function () { props.pick((index + 1) % items.length); }}>Next image</ui.Button></div> : null}</div> : null}
    </ui.Sheet>;
  }
  function Gallery(props) {
    var s = props.section, items = arr(s.images).filter(function (v) { return v && L.httpsUrl(v.image); }).slice(0, 16), picked = React.useState(null);
    if (!items.length) return null;
    var cards = items.map(function (v, i) { return <li key={i}><button type="button" className="oo-lp-gallery__item" aria-label={'Open image: ' + (L.text(v.caption, 80) || (i + 1))} onClick={function () { picked[1](i); }}><img src={v.image} alt={L.text(v.caption, 80)} loading="lazy" />{v.caption ? <span>{v.caption}</span> : null}</button></li>; });
    return <Frame section={s} ctx={props.ctx} label="Gallery">{s.layout === 'carousel' ? <Carousel id={s.id} label={s.title || 'Gallery'}>{cards}</Carousel> : <ul className="oo-lp-gallery" style={{ '--wl-columns': Math.min(6, Math.max(2, s.columns || 3)), '--wl-tablet-columns': Math.min(3, Math.max(2, s.columns || 3)) }}>{cards}</ul>}<Lightbox items={items} index={picked[0]} pick={picked[1]} close={function () { picked[1](null); }} /></Frame>;
  }
  function accordionKey(e) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key) || e.target.tagName !== 'SUMMARY') return;
    var list = Array.from(e.currentTarget.querySelectorAll('summary')), i = list.indexOf(e.target);
    var next = e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1 : (i + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length;
    if (list[next]) { e.preventDefault(); list[next].focus(); }
  }
  function FAQ(props) {
    var s = props.section, items = arr(s.items).slice(0, 12);
    if (!items.length) return null;
    return <Frame section={s} ctx={props.ctx} label="Questions and answers"><div className="oo-lp-accordion" onKeyDown={accordionKey}>{items.map(function (v, i) { return <details key={i}><summary>{L.text(v.q, 160)}<span aria-hidden="true">＋</span></summary><div><Paras text={L.text(v.a, 600)} /></div></details>; })}</div></Frame>;
  }
  function Banner(props) {
    var s = props.section, image = L.httpsUrl(s.image);
    return <Frame section={s} ctx={props.ctx} label="Announcement"><div className={cx('oo-lp-banner', image && 'has-image')}><div><Paras text={L.text(s.body, 300)} />{s.cta ? <Link className="oo-btn oo-btn--primary oo-btn--md" target={s.cta.target} label={s.cta.label} button ctx={props.ctx} /> : null}</div>{image ? <img src={image} alt="" loading="lazy" /> : null}</div></Frame>;
  }
  function Newsletter(props) {
    var s = props.section, state = React.useState({ name: '', email: '', website: '', consent: false }), result = React.useState(null), busy = React.useState(false), errors = React.useState({});
    var id = L.anchorId(s.id) + '-signup', form = React.useRef(null);
    function set(k, v) { state[1](function (f) { var n = Object.assign({}, f); n[k] = v; return n; }); }
    function submit(e) {
      e.preventDefault(); if (busy[0] || OO.whitelabel && OO.whitelabel.isPreview()) return;
      var checked = L.newsletterValidate(state[0]);
      if (s.consent && !state[0].consent) { checked.errors.consent = 'Please agree before subscribing.'; checked.ok = false; }
      errors[1](checked.errors);
      if (!checked.ok) { setTimeout(function () { var el = form.current && form.current.querySelector('[aria-invalid="true"]'); if (el) el.focus(); }, 0); return; }
      busy[1](true); result[1](null);
      L.subscribe(props.ctx.key, s.id, state[0]).then(function (r) { busy[1](false); result[1](r); errors[1](r.errors || {}); });
    }
    var sent = result[0] && result[0].ok, error = errors[0];
    return <Frame section={s} ctx={props.ctx} label="Newsletter"><div className="oo-lp-newsletter"><Paras text={L.text(s.body, 300)} />
      {sent ? <div className="oo-lp-newsletter__success" role="status"><p>{L.text(s.success, 200) || result[0].message}</p>{result[0].code ? <p>Your code: <strong className="oo-lp-newsletter__code">{result[0].code}</strong></p> : null}</div> : <form ref={form} className="oo-lp-form" onSubmit={submit} noValidate>
        <div className="oo-lp-form__row"><ui.TextInput label="Name (optional)" labelVisible name="name" autoComplete="name" value={state[0].name} onChange={function (v) { set('name', v); }} error={error.name} maxLength={100} /><ui.TextInput label="Email" labelVisible name="email" type="email" autoComplete="email" value={state[0].email} onChange={function (v) { set('email', v); }} error={error.email} maxLength={254} required /></div>
        <div className="oo-lp-hp" aria-hidden="true"><label htmlFor={id + '-website'}>Website</label><input id={id + '-website'} name="website" tabIndex={-1} autoComplete="off" value={state[0].website} onChange={function (e) { set('website', e.target.value); }} /></div>
        {s.consent ? <div><label className="oo-lp-consent"><input type="checkbox" checked={state[0].consent} aria-invalid={error.consent ? 'true' : undefined} aria-describedby={error.consent ? id + '-consent-error' : undefined} onChange={function (e) { set('consent', e.target.checked); }} />{L.text(s.consent, 200)}</label>{error.consent ? <p id={id + '-consent-error'} className="oo-lp-form__status is-error">{error.consent}</p> : null}</div> : null}
        <ui.Button type="submit" busy={busy[0]} disabled={!!(OO.whitelabel && OO.whitelabel.isPreview())}>{L.text(s.button, 24) || 'Sign me up'}</ui.Button>
        {OO.whitelabel && OO.whitelabel.isPreview() ? <p>Signup is available on the published website.</p> : null}
        {result[0] ? <p className="oo-lp-form__status is-error" role="alert">{result[0].message}</p> : null}</form>}</div></Frame>;
  }
  function Menu(props) {
    var s = props.section, ctx = builderCtx(props.ctx), cats = L.menuCategories(ctx.menu).filter(function (c) { return !arr(s.categoryIds).length || s.categoryIds.some(function (id) { return String(id) === String(c.id); }); });
    var state = useMenuState(ctx, !cats.length);
    if (state === 'hide') return null;
    return <Frame section={s} ctx={ctx} label="Menu">{state === 'wait' ? <Loading /> : <div className="oo-lp-menu">{cats.map(function (c) { var list = arr(c.productIds).map(function (id) { return ctx.menu.products[id]; }).filter(function (p) { return p && p.hidden !== true; }).slice(0, Math.min(30, Math.max(1, Number(s.limitPerCategory) || 10))); return <div key={c.id} className="oo-lp-menu__category"><h3>{c.name}</h3><ul className={s.layout === 'grid' ? 'oo-lp-products-grid' : 'oo-lp-menu__list'}>{list.map(function (p) { return <li key={p.id}><ProductCard product={p} ctx={Object.assign({}, ctx, { orderingOff: true })} images={s.showImages !== false} compact={s.layout !== 'grid'} /></li>; })}</ul></div>; })}<Link target={{ type: 'page', page: 'menu' }} label="Explore the full menu" className="oo-lp-link" ctx={ctx} /></div>}</Frame>;
  }
  function Logos(props) {
    var s = props.section, items = arr(s.images).filter(function (v) { return v && L.httpsUrl(v.image); }).slice(0, 12);
    if (!items.length) return null;
    return <Frame section={s} ctx={props.ctx} label="Our partners"><ul className="oo-lp-logos">{items.map(function (v, i) { var image = <img src={v.image} alt={L.text(v.alt, 40)} loading="lazy" />; return <li key={i}>{L.httpsUrl(v.url) ? <a href={v.url} target="_blank" rel="noopener noreferrer" aria-label={v.alt || 'Partner website'}>{image}</a> : image}</li>; })}</ul></Frame>;
  }
  function Categories(props) {
    var s = props.section, ctx = builderCtx(props.ctx), cats = L.menuCategories(ctx.menu), tiles = arr(s.tiles).filter(function (v) { return v && L.httpsUrl(v.image) && cats.some(function (c) { return String(c.id) === String(v.categoryId); }); });
    var state = useMenuState(ctx, !tiles.length), variant = typeof s.style === 'string' ? s.style : s.variant || 'tiles';
    if (state === 'hide') return null;
    if (variant === 'spotlight') return <Spotlight section={s} ctx={ctx} tiles={tiles} wait={state === 'wait'} />;
    return <Frame section={s} ctx={ctx} label="Categories">{state === 'wait' ? <Loading /> : <ul className={cx('oo-lp-category-tiles', 'oo-lp-category-tiles--' + variant)} style={{ '--wl-columns': Math.min(6, Math.max(2, s.columns || 4)), '--wl-tablet-columns': Math.min(3, Math.max(2, s.columns || 4)) }}>{tiles.map(function (v, i) { return <li key={i}><button type="button" onClick={function () { ctx.order({ type: 'category', id: String(v.categoryId) }); }}><img src={v.image} alt="" loading="lazy" /><span>{L.text(v.label, 24)}</span></button></li>; })}</ul>}</Frame>;
  }
  function StoreContent(props) {
    var s = props.section, b = props.store, ctx = props.ctx, tel = L.telHref(b.phone), hours = L.hoursLines(b.hours), dir = L.directionsUrl(b);
    var photo = arr(s.photos).filter(function (p) { return p && String(p.storeKey).toLowerCase() === String(b.key).toLowerCase(); })[0];
    var image = L.httpsUrl(photo && photo.image), menu = b.key && (b.menuAvailable !== false && (b.menuAvailable || b.orderable)) ? L.menuHref(b.key) : null;
    var off = ctx.orderingOff || !b.orderable || !!(ctx.ordering && ctx.ordering.stores && ctx.ordering.stores[String(b.key).toLowerCase()] === false), button = off ? L.targetOf({ type: 'order' }, Object.assign({}, ctx, { orderingOff: true, phone: b.phone, directions: dir, menuUnavailable: !menu })) : null;
    return <div className={cx('oo-lp-store-details', image && 'has-image')}><div>
      {b.address ? <p>{b.address}</p> : null}{b.label ? <ui.StatusPill open={b.open}>{b.label}</ui.StatusPill> : null}
      {s.showPhone !== false && tel ? <p><a className="oo-lp-link" href={tel}>{b.phone}</a></p> : null}
      {s.showEmail !== false && b.email ? <p><a className="oo-lp-link" href={'mailto:' + b.email}>{b.email}</a></p> : null}
      {s.showHours !== false && hours.length ? <dl className="oo-lp-hours" aria-label={'Opening hours: ' + b.name}>{hours.map(function (r) { return <div key={r.days} className="oo-lp-hours__row"><dt>{r.days}</dt><dd>{r.time}</dd></div>; })}</dl> : null}
      <div className="oo-lp-store__acts">{s.map && dir ? <a className="oo-lp-link" href={dir} target="_blank" rel="noopener noreferrer">Open in Maps</a> : null}
        {!off && menu ? <a className="oo-btn oo-btn--primary oo-btn--sm" href={menu} onClick={old.go(menu)}>Order here</a> : button ? (button.kind === 'order' ? <a className="oo-lp-link" href={menu} onClick={old.go(menu)}>{button.label}</a> : <Link target={{ type: 'order' }} label="View menu" className="oo-lp-link" ctx={Object.assign({}, ctx, { orderingOff: true, phone: b.phone, directions: dir, menuUnavailable: !menu })} />) : null}</div>
    </div>{image ? <img src={image} alt={b.name} loading="lazy" /> : null}</div>;
  }
  function Stores(props) {
    var s = props.section, ctx = props.ctx, q = OO.useAsync(function () { return L.loadStores(ctx.master || ctx.key); }, [ctx.master || ctx.key]), list = arr(q.data);
    var state = L.dataState({ loading: q.loading, failed: !!q.error || !q.loading && !Array.isArray(q.data), empty: !list.length, late: old.useLate(q.loading) });
    if (state === 'hide') return null;
    return <Frame section={s} ctx={ctx} label="Stores">{state === 'wait' ? <Loading /> : s.layout === 'accordion' ? <div className="oo-lp-accordion" onKeyDown={accordionKey}>{list.map(function (b, i) { return <details key={b.key || i} open={i === 0}><summary>{b.name}<span aria-hidden="true">＋</span></summary><StoreContent store={b} section={s} ctx={ctx} /></details>; })}</div> : <ul className="oo-lp-grid oo-lp-grid--stores">{list.map(function (b, i) { return <li className="oo-lp-store" key={b.key || i}><h3 className="oo-lp-store__name">{b.name}</h3><StoreContent store={b} section={s} ctx={ctx} /></li>; })}</ul>}</Frame>;
  }
  Object.assign(old.REGISTRY, { products: Products, videos: Videos, features: Features, testimonials: Testimonials, gallery: Gallery, faq: FAQ, banner: Banner, newsletter: Newsletter, menu: Menu, logos: Logos });
  OO.pages.LandingBlocks = { Carousel: Carousel, ProductCard: ProductCard, Lightbox: Lightbox, Stars: Stars, Categories: Categories, Stores: Stores, Newsletter: Newsletter, accordionKey: accordionKey };
})();


// ===================== src/pages/landing-super.jsx =====================
// src/pages/landing-super.jsx: OO.pages.LandingSuper, the white-label landing page's `super` design (landing.design
// 'super'; lane WL-SUPER): a dark poster look with giant tight display caps, script accents (typography.script), pill
// CTAs and grain textures. Generic: every colour and font is a theme token (styles/landing-super.css, scoped under
// .oo-lp--super). src/pages/landing.jsx uses it only for landing.design 'super'; nothing here changes the other designs.
//   Header     a solid bar: the logo on the left, the nav centred (small letter-spaced caps), then the account, the cart
//              and the CTA pill (landing.cta). Below 900 px the menu button sits on the right and opens the nav, the
//              account and the CTA in a panel under the bar (aria-expanded). Ordering off: no cart, no account (WL-5).
//   Footer     the logo large and centred, the tagline, the links of every link column in one centred row (each column
//              its own labelled list), the text / contact / hours columns as small blocks, round social buttons, the
//              payment marks, copyright and legal links, then the partner row (PoweredBySipo + footer.credits).
//   SlideCtas  a hero slide's `cta` (the accent pill) and `more` (the primary pill; slides[].more {label, target}).
//   REGISTRY   the Super variants, read by OO.pages.LandingSections.Section when ctx.superDesign:
//     about     with an image: a split, the image on imageSide (default right) drawn as it is (a cut-out reads best);
//               without one: a centred band. `background` is a full-bleed photo under it. The heading, `stats` as huge
//               display figures (accent / primary alternating, the label in display caps below), the body, then `cta`
//               (a primary pill) and `more` (an outline pill). In a section dialog: the standard about.
//     appPromo  layout 'showcase': the heading, the body, the App Store / Google Play badges (only for a set https URL),
//               and the `screen` image drawn as it is (a phone mock-up or a screenshot) over a soft glow
//               (decorative). Any other layout: the standard band.
//   Pills      <Pills first second firstTone secondTone ctx className /> two link pills (a link that resolves to nothing
//              is left out, OO.pages.Landing.parts.TargetLink).
// Every other section type keeps its standard markup; the CSS restyles it.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var L = OO.landing, ui = OO.ui, S = OO.pages.LandingSections, P = OO.pages.Landing.parts;
  var EMPTY = [];
  var BaseAbout = S.REGISTRY.about, BaseApp = S.REGISTRY.appPromo;
  function cx() { return ui.cx.apply(null, arguments); }
  function Glyph(props) { return <OO.landingIcons.Glyph name={props.name} size={props.size} className={props.className} strokeWidth={props.strokeWidth} />; }
  function linkOf(v) { return v && typeof v === 'object' && L.text(v.label, 24) ? v : null; }

  function Pills(props) {
    var a = linkOf(props.first), b = linkOf(props.second), Link = P.TargetLink;
    if (!a && !b) return null;
    return (
      <div className={cx('oo-lp-spills', props.className)}>
        {a ? <Link className={'oo-lp-spill oo-lp-spill--' + (props.firstTone || 'primary')} target={a.target} label={L.text(a.label, 24)} ctx={props.ctx} /> : null}
        {b ? <Link className={'oo-lp-spill oo-lp-spill--' + (props.secondTone || 'ghost')} target={b.target} label={L.text(b.label, 24)} ctx={props.ctx} /> : null}
      </div>
    );
  }
  function SlideCtas(props) {
    return <Pills first={props.slide.cta} second={props.slide.more} firstTone="accent" secondTone="primary" className="oo-lp-slide__ctas" ctx={props.ctx} />;
  }

  // ---- header -------------------------------------------------------------------------------------------------------
  function Header(props) {
    var ctx = props.ctx;
    var cartKey = (OO.store && typeof OO.store.lastHotel === 'function' && OO.store.lastHotel()) || ctx.master;
    OO.useStore(function (s) { return s.cart; });
    var count = cartKey && OO.store && typeof OO.store.cartOf === 'function' ? OO.store.cartOf(cartKey).count || 0 : 0;
    return HeaderView(props, { key: cartKey, count: count });
  }
  function HeaderView(props, cartState) {
    var ctx = props.ctx, landing = props.landing, theme = props.theme || {};
    var openS = React.useState(false);
    var settings = landing.header || {};
    var nav = (Array.isArray(landing.nav) ? landing.nav : EMPTY).filter(function (n) { return n && L.text(n.label, 24); }).slice(0, 8);
    var cta = linkOf(landing.cta);
    var logo = L.httpsUrl(settings.logo) || theme.logo || null;
    var name = theme.brand || props.name || '';
    var home = OO.router.href('landing', null, { hotel: ctx.key });
    var account = OO.router.href('account', null, { hotel: ctx.key });
    var cartKey = cartState.key, count = cartState.count;
    var cart = OO.router.href('cart', null, { hotel: cartKey });
    var close = function () { openS[1](false); };
    function inApp(href) { return function (e) { if (!P.plainClick(e)) return; e.preventDefault(); close(); OO.router.go(href); }; }
    var Link = P.TargetLink, NavItem = P.NavItem;
    var ctaLabel = cta ? L.text(cta.label, 24) : '';
    var hasSearch = settings.search && L.menuProducts(ctx.builderMenu || ctx.menu).length > 0;
    return (
      <header className={cx('oo-lp-head', 'oo-lp-head--super', hasSearch && 'oo-lp-head--search', openS[0] && 'is-open', settings.sticky === false && 'oo-lp-head--static')}>
        <P.Utility header={settings} ctx={ctx} />
        <div className="oo-lp-wrap oo-lp-head__row">
          <a className={cx('oo-lp-head__brand', logo && P.logoClass(settings) && 'oo-lp-head__brand--sized')} href={home} onClick={function (e) { if (!P.plainClick(e)) return; e.preventDefault(); close(); window.scrollTo(0, 0); }}>
            {logo ? <img className={cx('oo-lp-head__logo', P.logoClass(settings))} src={logo} alt={name ? name + ' home' : 'Home'} /> : <span className="oo-lp-head__name">{name}</span>}
          </a>
          {settings.search ? <P.ProductSearch ctx={ctx} /> : null}
          <nav id="oo-lp-nav" className="oo-lp-head__nav" aria-label="Main">
            <ul className="oo-lp-head__links">
              {nav.map(function (n, i) { return <li key={i}><NavItem item={n} ctx={ctx} close={close} /></li>; })}
            </ul>
            {cta || (!ctx.orderingOff && !ctx.publicOnly) ? (
              <div className="oo-lp-head__panel">
                {ctx.orderingOff || ctx.publicOnly ? null : <a className="oo-lp-head__plink" href={account} onClick={inApp(account)}><OO.icons.User size={18} />Account</a>}
                {cta ? <Link className="oo-lp-spill oo-lp-spill--primary oo-lp-head__pcta" target={cta.target} ctx={ctx} onNavigate={close} label={ctaLabel} /> : null}
              </div>
            ) : null}
          </nav>
          <div className="oo-lp-head__acts">
            {ctx.orderingOff || ctx.publicOnly ? null : <a className="oo-lp-head__icon oo-lp-head__account" href={account} onClick={inApp(account)} aria-label="Account"><OO.icons.User size={20} /></a>}
            {ctx.orderingOff || ctx.publicOnly ? null : (
              <a className="oo-lp-head__icon oo-lp-head__cart" href={cart} onClick={inApp(cart)} aria-label={'Cart, ' + count + (count === 1 ? ' item' : ' items')}>
                <OO.icons.Bag size={20} />{count ? <span className="oo-lp-head__count" aria-hidden="true">{count}</span> : null}
              </a>
            )}
            {cta ? <Link className="oo-lp-spill oo-lp-spill--primary oo-lp-head__cta" target={cta.target} ctx={ctx} onNavigate={close} label={ctaLabel} /> : null}
            <button type="button" className="oo-lp-head__toggle" aria-expanded={openS[0] ? 'true' : 'false'} aria-controls="oo-lp-nav"
              onClick={function () { openS[1](!openS[0]); }}>
              {openS[0] ? <OO.icons.Close size={22} /> : <OO.icons.MenuList size={22} />}
              <span className="oo-sr-only">Menu</span>
            </button>
          </div>
        </div>
      </header>
    );
  }

  Header.Public = function PublicSuperHeader(props) { return HeaderView(props, { key: props.ctx.master, count: 0 }); };

  // ---- footer -------------------------------------------------------------------------------------------------------
  var BLOCK_KINDS = { text: 1, contact: 1, hours: 1 };
  function Footer(props) {
    var f = props.footer || {}, ctx = props.ctx, N = P.NETWORKS, Link = P.TargetLink;
    var cols = (Array.isArray(f.columns) ? f.columns : EMPTY).filter(function (c) { return c && L.text(c.title, 30); }).slice(0, 4);
    var socials = (Array.isArray(f.socials) ? f.socials : EMPTY).filter(function (x) { return x && N[x.network] && L.httpsUrl(x.url); }).slice(0, 6);
    var legal = (Array.isArray(f.legal) ? f.legal : EMPTY).filter(function (x) { return x && L.text(x.label, 30); }).slice(0, 6);
    var copyright = L.text(f.copyright, 120);
    var credits = P.creditsOf(f);
    // WL-OPT parity (as the bold footer): brand.poweredBy false drops the SIPO badge (OO.ui.PoweredBySipo's own check);
    // the partner row then has no rule before its first credit, and is left out with no credits.
    var badge = typeof ui.PoweredBySipo.shown === 'function' ? ui.PoweredBySipo.shown() : true;
    var linkCols = cols.filter(function (c) { return !BLOCK_KINDS[c.kind] && c.kind !== 'social'; }).map(function (c) {
      return { title: L.text(c.title, 30), links: (Array.isArray(c.links) ? c.links : EMPTY).filter(function (x) { return x && L.text(x.label, 30); }).slice(0, 8) };
    }).filter(function (c) { return c.links.length; });
    var blocks = cols.filter(function (c) { return BLOCK_KINDS[c.kind]; });
    return (
      <footer className="oo-lp-foot oo-lp-foot--super">
        <div className="oo-lp-wrap">
          <div className="oo-lp-foot__brand">
            {props.logo ? <img className="oo-lp-foot__logo" src={props.logo} alt={props.name || 'Logo'} /> : <span className="oo-lp-foot__name">{props.name}</span>}
            {props.tagline ? <p className="oo-lp-foot__tag">{props.tagline}</p> : null}
          </div>
          {linkCols.length ? (
            <nav className="oo-lp-foot__nav" aria-label="Footer">
              {linkCols.map(function (c, k) {
                return <ul key={k} aria-label={c.title}>{c.links.map(function (x, j) { return <li key={j}><Link className="oo-lp-foot__link" target={x.target} label={L.text(x.label, 30)} ctx={ctx} /></li>; })}</ul>;
              })}
            </nav>
          ) : null}
          {blocks.length ? (
            <div className="oo-lp-foot__blocks">
              {blocks.map(function (c, k) {
                return (
                  <div key={k} className="oo-lp-foot__col">
                    <h2 className="oo-lp-foot__title">{L.text(c.title, 30)}</h2>
                    {c.kind === 'text' ? <p className="oo-lp-foot__body">{L.text(c.body, 600)}</p> : c.kind === 'contact' ? <P.FooterContact ctx={ctx} /> : <P.FooterHours ctx={ctx} />}
                  </div>
                );
              })}
            </div>
          ) : null}
          {socials.length ? (
            <ul className="oo-lp-foot__socials" aria-label="Follow us">
              {socials.map(function (x, i) { return <li key={i}><a className="oo-lp-foot__social" href={x.url} target="_blank" rel="noopener noreferrer" aria-label={N[x.network]}><OO.landingIcons.Icon name={x.network} size={18} strokeWidth="2.1" /></a></li>; })}
            </ul>
          ) : null}
          {Array.isArray(f.payments) && f.payments.length ? <ul className="oo-lp-payments" aria-label="Accepted payment methods">{f.payments.map(function (p) { return <li key={p}><OO.landingIcons.Payment name={p} /></li>; })}</ul> : null}
          {copyright || legal.length ? (
            <div className="oo-lp-foot__base">
              {copyright ? <span className="oo-lp-foot__copy">{copyright}</span> : null}
              {legal.length ? <ul className="oo-lp-foot__legal">{legal.map(function (x, k) { return <li key={k}><Link className="oo-lp-foot__link" target={x.target} label={L.text(x.label, 30)} ctx={ctx} /></li>; })}</ul> : null}
            </div>
          ) : null}
          {badge || credits.length ? (
            <div className="oo-lp-foot__partners">
              <OO.ui.PoweredBySipo className="oo-lp-foot__powered" logo="online-order/fixtures/img/sipo-logo-color.svg" />
              {credits.map(function (c, k) { return <React.Fragment key={k}>{badge || k ? <span className="oo-lp-foot__rule" aria-hidden="true" /> : null}<P.Credit credit={c} /></React.Fragment>; })}
            </div>
          ) : null}
        </div>
      </footer>
    );
  }

  // ---- about ----------------------------------------------------------------------------------------------------------
  function AboutSuper(props) {
    var s = props.section, ctx = props.ctx;
    if (!ctx || ctx.inDialog) return <BaseAbout section={s} ctx={ctx} />;
    var img = L.httpsUrl(s.image), bg = L.httpsUrl(s.background);
    var stats = (Array.isArray(s.stats) ? s.stats : EMPTY).filter(function (x) { return x && L.text(x.value, 12) && L.text(x.label, 30); }).slice(0, 4);
    var backdrop = bg ? <span className="oo-lp-sabout__bg" aria-hidden="true"><img src={bg} alt="" loading="lazy" /></span> : null;
    return (
      <S.Frame section={s} ctx={ctx} label="About" headless noMore backdrop={backdrop}
        className={cx('oo-lp-sabout', img ? 'oo-lp-sabout--split' : 'oo-lp-sabout--band', bg && 'has-bg', img && s.imageSide === 'left' && 'media-left')}>
        <div className="oo-lp-sabout__grid">
          <div className="oo-lp-sabout__text">
            <S.Head section={s} />
            {stats.length ? (
              <dl className="oo-lp-sstats">
                {stats.map(function (x, i) { return <div key={i} className="oo-lp-sstat"><dt>{L.text(x.label, 30)}</dt><dd>{L.text(x.value, 12)}</dd></div>; })}
              </dl>
            ) : null}
            <div className="oo-lp-sabout__body"><S.Paras text={s.body} /></div>
            <Pills first={s.cta} second={s.more} firstTone="primary" secondTone="ghost" ctx={ctx} />
          </div>
          {img ? <div className="oo-lp-sabout__media"><img src={img} alt="" loading="lazy" /></div> : null}
        </div>
      </S.Frame>
    );
  }

  // ---- app promo ------------------------------------------------------------------------------------------------------
  function AppSuper(props) {
    var s = props.section;
    if (s.layout !== 'showcase' || (props.ctx && props.ctx.inDialog)) return <BaseApp section={s} ctx={props.ctx} />;
    var ios = L.httpsUrl(s.ios), android = L.httpsUrl(s.android), screen = L.httpsUrl(s.screen);
    return (
      <S.Frame section={s} ctx={props.ctx} label="Get the app" className={cx('oo-lp-sapp', screen && 'has-visual')} headless>
        <div className="oo-lp-sapp__grid">
          <div className="oo-lp-sapp__text">
            <S.Head section={s} />
            <S.Paras text={s.body} />
            {ios || android ? (
              <div className="oo-lp-sapp__badges">
                {ios ? <a className="oo-lp-sbadge" href={ios} target="_blank" rel="noopener noreferrer"><Glyph name="apple" size={22} /><span className="oo-lp-sbadge__text"><span className="oo-lp-sbadge__small">Download on the</span><span className="oo-lp-sbadge__big">App Store</span></span></a> : null}
                {android ? <a className="oo-lp-sbadge" href={android} target="_blank" rel="noopener noreferrer"><Glyph name="play" size={20} /><span className="oo-lp-sbadge__text"><span className="oo-lp-sbadge__small">Get it on</span><span className="oo-lp-sbadge__big">Google Play</span></span></a> : null}
              </div>
            ) : null}
          </div>
          {screen ? <div className="oo-lp-sapp__visual" aria-hidden="true"><span className="oo-lp-sapp__glow" /><img src={screen} alt="" loading="lazy" /></div> : null}
        </div>
      </S.Frame>
    );
  }

  OO.pages.LandingSuper = {
    Header: Header, Footer: Footer, SlideCtas: SlideCtas, Pills: Pills,
    REGISTRY: { about: AboutSuper, appPromo: AppSuper },
  };
})();


// ===================== src/pages/landing-boutique-sections.jsx =====================
// src/pages/landing-boutique-sections.jsx: WL-VEL, the website's new section types and the upgraded ones (scratchpad SPEC
// §1.3), registered into OO.pages.LandingSections.REGISTRY after src/pages/landing-blocks.jsx. Every design draws them;
// styles/landing-boutique.css gives them a base look (new classes only) and the boutique design's refinements (scoped
// under .oo-lp--boutique). A section WITHOUT the new keys keeps its own renderer, except under the boutique design
// (ctx.boutique) where about, banner, contact, gallery and menu take the boutique markup.
//   pageHero     an inner page's hero band: the image faint under a deep tone, eyebrow, title (h1 on a site page),
//                subtitle.
//   quote        a large italic quote with an optional caption and CTA.
//   cards        image cards (4:3) with a title, text and up to two links (the first an outline pill with an arrow, the
//                second a filled one); columns 2–4 (default 3).
//   contactInfo  info cards from landing.contact, footer.socials and the store's hours: Visit (address + directions),
//                Opening hours (full day names, today highlighted, closed days), Contact (phone, email, socials).
//   image        one wide photo (aspect 21:9 | 16:9 | 3:2 | 4:3 | 1:1 | 21:10) with a caption, optionally a link.
//   about        + badge: a round seal over the photo's bottom-left corner (BoutiqueAbout, any design); ctaStyle 'filled':
//                the button is the primary pill (absent / 'outline': the outline pill).
//   features     + layout 'list': a checklist (icon + text rows) beside the optional image.
//   gallery      + images[].group, filters (chips with counts, 'All' first; aria-pressed), layout 'masonry', up to
//                32 images; a lightbox that steps within the filtered set (← / →, Esc closes, focus returns).
//   menu         + layout 'accordion' (collapsible categories: image, title, description, "N items"), jumpNav (chips
//                to #menu-{id}), showOptions (sizes and modifier groups with their rules and prices), orderLinks
//                ("Order now →" into ordering, only while ordering is on). The #menu-{id} anchors exist only with
//                accordion or jumpNav; arriving with one (or a site link to it, 'oo:site-anchor') opens and scrolls to
//                that category.
//   contact      + enquiryTypes (a select) and askDate (a date field): OO.landing.enquiryBody sends enquiry_type /
//                preferred_date only when set.
//   banner       (boutique only) the photo band: the image faint under a deep gradient, centred head, body and CTA.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  var L = OO.landing, ui = OO.ui, S = OO.pages.LandingSections;
  var Frame = S.Frame, Head = S.Head, REGISTRY = S.REGISTRY;
  var base = Object.assign({}, REGISTRY); // the renderers the upgraded types fall back to
  var Chrome = OO.ui.SiteChrome;

  function arr(v) { return Array.isArray(v) ? v : []; }
  function obj(v) { return v && typeof v === 'object' && !Array.isArray(v) ? v : null; }
  function cx() { return ui.cx.apply(null, arguments); }
  function Glyph(props) { return <OO.landingIcons.Glyph name={props.name} size={props.size} strokeWidth={props.strokeWidth} />; }
  function reducedNow() { try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }
  function money(n, ctx) { return OO.format.money(n, ctx.store && ctx.store.currency); }
  function builderCtx(ctx) { return ctx.builderMenu ? Object.assign({}, ctx, { menu: ctx.builderMenu }) : ctx; }
  function linkOf(link, ctx) { return link && obj(link) && L.text(link.label, 24) ? Chrome.linkOf(link.target, L.text(link.label, 24), ctx) : null; }
  function Pill(props) {
    if (!props.link) return null;
    return <Chrome.SiteLink className={cx('oo-lpb-pill', 'oo-lpb-pill--' + (props.tone || 'primary'), props.className)} link={props.link} after={props.arrow === false ? null : <Glyph name="arrow-right" size={props.arrowSize || 16} />} />;
  }
  function section(s, props, children) {
    var titled = !!L.text(s.title, 80);
    return (
      <section id={L.anchorId(s.id)} tabIndex={-1} className={cx('oo-lp-sec', 'oo-lp-sec--' + s.type, props.className, L.styleClasses(s))}
        aria-labelledby={titled ? L.anchorId(s.id) + '-t' : undefined} aria-label={titled ? undefined : props.label}>
        {children}
      </section>
    );
  }

  // ---- pageHero -------------------------------------------------------------------------------------------------------
  function PageHero(props) {
    var s = props.section, ctx = props.ctx;
    var title = L.text(s.title, 80);
    var img = L.httpsUrl(s.image), eyebrow = L.text(s.eyebrow, 40), sub = L.text(s.subtitle, 200);
    if (!title && !eyebrow && !sub) return null; // every field is optional; a band with no words is left out
    var H = ctx && ctx.page ? 'h1' : 'h2';
    return section(s, { className: cx('oo-lp-phero', img && 'has-image'), label: 'Page heading' }, (
      <>
        {img ? <img className="oo-lp-phero__bg" src={img} alt="" /> : null}
        <div className="oo-lp-wrap oo-lp-phero__in oo-rv">
          {eyebrow ? <p className="oo-lp-phero__eyebrow">{eyebrow}</p> : null}
          {title ? <H className="oo-lp-phero__title" id={L.anchorId(s.id) + '-t'}>{title}</H> : null}
          {sub ? <p className="oo-lp-phero__sub">{sub}</p> : null}
        </div>
      </>
    ));
  }

  // ---- quote ----------------------------------------------------------------------------------------------------------
  function Quote(props) {
    var s = props.section, ctx = props.ctx;
    var q = L.text(s.quote, 160);
    var cap = L.text(s.caption, 160), cta = linkOf(s.cta, ctx);
    if (!q && !cap) return null;
    return section(s, { className: 'oo-lp-quote', label: 'Quote' }, (
      <div className="oo-lp-wrap oo-lp-quote__in">
        <figure className="oo-lp-quote__fig">
          {q ? <blockquote className="oo-lp-quote__text oo-rv"><p>{q}</p></blockquote> : null}
          {cap ? <figcaption className="oo-lp-quote__cap oo-rv">{cap}</figcaption> : null}
        </figure>
        {cta ? <div className="oo-lp-quote__cta oo-rv"><Pill link={cta} tone="cream" arrow={false} /></div> : null}
      </div>
    ));
  }

  // ---- cards ----------------------------------------------------------------------------------------------------------
  function Cards(props) {
    var s = props.section, ctx = props.ctx;
    var items = arr(s.items).filter(function (it) { return obj(it) && L.text(it.title, 40); }).slice(0, 12);
    if (!items.length) return null;
    var n = [2, 3, 4].indexOf(s.columns) >= 0 ? s.columns : 3;
    return (
      <Frame section={s} ctx={ctx} label="Highlights" className="oo-lp-cardsec">
        <ul className={cx('oo-lp-cards', 'oo-lp-cards--' + n)}>
          {items.map(function (it, i) {
            var img = L.httpsUrl(it.image), body = L.text(it.body, 240);
            var links = arr(it.links).slice(0, 2).map(function (l) { return linkOf(l, ctx); }).filter(Boolean);
            return (
              <li key={i} className="oo-lp-card oo-rv">
                {img ? <img className="oo-lp-card__img" src={img} alt="" loading="lazy" /> : null}
                <div className="oo-lp-card__body">
                  <h3 className="oo-lp-card__title">{L.text(it.title, 40)}</h3>
                  {body ? <p className="oo-lp-card__text">{L.paragraphs(body).map(function (x, k) { return <React.Fragment key={k}>{k ? <br /> : null}{x}</React.Fragment>; })}</p> : null}
                  {links.length ? (
                    <div className="oo-lp-card__links">
                      {links.map(function (l, k) { return <Chrome.SiteLink key={k} className={cx('oo-lp-card__link', k ? 'oo-lp-card__link--fill' : 'oo-lp-card__link--line')} link={l} after={k ? null : <Glyph name="arrow-right" size={14} />} />; })}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </Frame>
    );
  }

  // ---- contactInfo ----------------------------------------------------------------------------------------------------
  var NETWORKS = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', x: 'X', youtube: 'YouTube', linkedin: 'LinkedIn' };
  function ContactInfo(props) {
    var s = props.section, ctx = props.ctx;
    var landing = obj(ctx.landing) || {};
    var lc = obj(landing.contact) || {}, store = ctx.store || null;
    var rows = Chrome.useHours(store, ctx.master, ctx.timeZone); // VEL-FIX: "today" in the document's time zone
    var address = L.text(lc.address, 200) || (store && store.address && store.address.full) || null;
    var phone = L.text(lc.phone, 24) || (store && store.phone) || null, tel = L.telHref(phone);
    var email = typeof lc.email === 'string' && /^[^\s@]+@[^\s@]+$/.test(lc.email.trim()) ? lc.email.trim() : null;
    var maps = Chrome.mapsSearch(address);
    var socials = arr(obj(landing.footer) && landing.footer.socials).filter(function (x) { return x && NETWORKS[x.network] && L.httpsUrl(x.url); }).slice(0, 6);
    var show = function (k) { return s[k] !== false; };
    var cards = [];
    if (show('showAddress') && address) {
      cards.push(
        <li key="visit" className="oo-lp-info oo-rv">
          <h3 className="oo-lp-info__title"><OO.landingIcons.Icon name="map-pin" size={20} strokeWidth="2" />Visit Us</h3>
          <p className="oo-lp-info__addr">{maps ? <a href={maps} target="_blank" rel="noopener noreferrer">{address.split(/\n+/).map(function (x, i) { return <React.Fragment key={i}>{i ? <br /> : null}{x.trim()}</React.Fragment>; })}</a> : address}</p>
          {maps ? <p><a className="oo-lp-info__go" href={maps} target="_blank" rel="noopener noreferrer">Get directions<span aria-hidden="true"> →</span></a></p> : null}
        </li>
      );
    }
    if (show('showHours') && rows.length) {
      cards.push(
        <li key="hours" className="oo-lp-info oo-rv">
          <h3 className="oo-lp-info__title"><OO.landingIcons.Icon name="clock" size={20} strokeWidth="2" />Opening Hours</h3>
          <dl className="oo-lp-info__hours">
            {rows.map(function (r) { return <div key={r.dow} className={cx('oo-lp-info__row', r.today && 'is-today', r.closed && 'is-closed')}><dt>{r.dayLong}</dt><dd>{r.time}</dd></div>; })}
          </dl>
        </li>
      );
    }
    if (show('showContact') && (phone || email || (show('showSocials') && socials.length))) {
      cards.push(
        <li key="contact" className="oo-lp-info oo-rv">
          <h3 className="oo-lp-info__title"><OO.landingIcons.Icon name="phone" size={20} strokeWidth="2" />Contact</h3>
          {phone ? <p className="oo-lp-info__tel">{tel ? <a href={tel}>{phone}</a> : phone}</p> : null}
          {email ? <p className="oo-lp-info__mail"><a href={'mailto:' + email}>{email}</a></p> : null}
          {show('showSocials') && socials.length ? (
            <>
              <p className="oo-lp-info__k">Follow us</p>
              <ul className="oo-lp-info__socials" aria-label="Follow us">{socials.map(function (x, i) { return <li key={i}><a href={x.url} target="_blank" rel="noopener noreferrer" aria-label={NETWORKS[x.network]}><OO.landingIcons.Icon name={x.network} size={18} strokeWidth="1.9" /></a></li>; })}</ul>
            </>
          ) : null}
        </li>
      );
    } else if (show('showSocials') && socials.length) {
      cards.push(
        <li key="social" className="oo-lp-info oo-rv">
          <h3 className="oo-lp-info__title">Follow us</h3>
          <ul className="oo-lp-info__socials" aria-label="Follow us">{socials.map(function (x, i) { return <li key={i}><a href={x.url} target="_blank" rel="noopener noreferrer" aria-label={NETWORKS[x.network]}><OO.landingIcons.Icon name={x.network} size={18} strokeWidth="1.9" /></a></li>; })}</ul>
        </li>
      );
    }
    if (!cards.length) return null;
    return <Frame section={s} ctx={ctx} label="Contact details" className="oo-lp-infosec"><ul className={cx('oo-lp-infos', 'oo-lp-infos--' + cards.length)}>{cards}</ul></Frame>;
  }

  // ---- image ----------------------------------------------------------------------------------------------------------
  var ASPECTS = { '21:9': '21 / 9', '16:9': '16 / 9', '3:2': '3 / 2', '4:3': '4 / 3', '1:1': '1 / 1', '21:10': '21 / 10' };
  function ImageBlock(props) {
    var s = props.section, ctx = props.ctx;
    var img = L.httpsUrl(s.image);
    if (!img) return null;
    var cap = L.text(s.caption, 160), link = linkOf(s.link, ctx);
    var pic = <img src={img} alt={cap || ''} loading="lazy" />;
    return (
      <Frame section={s} ctx={ctx} label="Photo" className="oo-lp-imagesec">
        <figure className="oo-lp-figure oo-rv" style={{ '--wl-aspect': ASPECTS[s.aspect] || ASPECTS['21:9'] }}>
          {link ? <Chrome.SiteLink className="oo-lp-figure__link" link={link}><span className="oo-sr-only">{link.label}</span>{pic}</Chrome.SiteLink> : pic}
          {cap ? <figcaption>{cap}</figcaption> : null}
        </figure>
      </Frame>
    );
  }

  // ---- about (+ badge) ------------------------------------------------------------------------------------------------
  function SplitMedia(props) {
    if (!props.image) return null;
    return (
      <div className="oo-lp-bsplit__media oo-rv">
        <img src={props.image} alt="" loading="lazy" />
        {props.badge ? <img className="oo-lp-bsplit__badge" src={props.badge} alt="" /> : null}
      </div>
    );
  }
  function About(props) {
    var s = props.section, ctx = props.ctx;
    var badge = L.httpsUrl(s.badge);
    if (ctx.inDialog || (!badge && !ctx.boutique) || (ctx.bold && !badge)) return React.createElement(base.about, props);
    var img = L.httpsUrl(s.image), cta = linkOf(s.cta, ctx);
    var stats = arr(s.stats).filter(function (x) { return x && L.text(x.value, 12) && L.text(x.label, 30); }).slice(0, 4);
    return section(s, { className: cx('oo-lp-bsplit', img && 'has-media', s.imageSide === 'left' && 'media-left'), label: 'About' }, (
      <div className="oo-lp-wrap oo-lp-bsplit__grid">
        <div className="oo-lp-bsplit__text oo-rv">
          <Head section={s} />
          {L.paragraphs(s.body).map(function (p, i) { return <p key={i} className="oo-lp-lead">{p}</p>; })}
          {stats.length ? <dl className="oo-lp-about__stats">{stats.map(function (x, i) { return <div key={i} className="oo-lp-about__stat"><dt>{L.text(x.label, 30)}</dt><dd>{L.text(x.value, 12)}</dd></div>; })}</dl> : null}
          {cta ? <div className="oo-lp-bsplit__cta"><Pill link={cta} tone={s.ctaStyle === 'filled' ? 'primary' : 'ghost'} arrow={false} /></div> : null}
        </div>
        <SplitMedia image={img} badge={badge} />
      </div>
    ));
  }

  // ---- features (+ list) ----------------------------------------------------------------------------------------------
  function Features(props) {
    var s = props.section, ctx = props.ctx;
    if (s.layout !== 'list' || ctx.inDialog) return React.createElement(base.features, props);
    var items = arr(s.items).filter(function (x) { return obj(x) && L.text(x.title, 40); }).slice(0, 6);
    if (!items.length) return null;
    var img = L.httpsUrl(s.image);
    return section(s, { className: cx('oo-lp-bsplit', 'oo-lp-checks', img && 'has-media', s.imageSide === 'left' && 'media-left'), label: 'Features' }, (
      <div className="oo-lp-wrap oo-lp-bsplit__grid">
        <div className="oo-lp-bsplit__text oo-rv">
          <Head section={s} />
          <ul className="oo-lp-checklist">
            {items.map(function (x, i) {
              var body = L.text(x.body, 200);
              return <li key={i}><span className="oo-lp-checklist__icon" aria-hidden="true"><OO.landingIcons.Icon name={OO.landingIcons.PATHS[x.icon] ? x.icon : 'check'} size={24} strokeWidth="2" /></span><span className="oo-lp-checklist__text"><span className="oo-lp-checklist__title">{L.text(x.title, 40)}</span>{body ? <span className="oo-lp-checklist__body">{body}</span> : null}</span></li>;
            })}
          </ul>
          {s.more && L.text(s.more.label, 24) ? <div className="oo-lp-bsplit__cta"><Pill link={linkOf(s.more, ctx)} tone="primary" /></div> : null}
        </div>
        <SplitMedia image={img} />
      </div>
    ));
  }

  // ---- gallery (+ groups, filters, masonry, 32) -----------------------------------------------------------------------
  function upgradedGallery(s) {
    return (Array.isArray(s.filters) && s.filters.length > 0) || s.layout === 'masonry' || arr(s.images).length > 16 || arr(s.images).some(function (v) { return v && v.group; }); // VEL-FIX: an empty list is no filter
  }
  function Lightbox(props) {
    var items = props.items, i = props.index, item = i === null ? null : items[i];
    var n = items.length;
    function step(d) { props.pick((i + d + n) % n); }
    // ← / → step while it is open, wherever the focus is inside the dialog (the Sheet focuses its panel first).
    var stepRef = React.useRef(step);
    stepRef.current = step;
    var open = !!item;
    React.useEffect(function () {
      if (!open || n < 2 || typeof document === 'undefined') return undefined;
      function onKey(e) {
        if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.altKey || e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        stepRef.current(e.key === 'ArrowLeft' ? -1 : 1);
      }
      document.addEventListener('keydown', onKey);
      return function () { document.removeEventListener('keydown', onKey); };
    }, [open, n]);
    return (
      <ui.Sheet open={!!item} onClose={props.close} variant="dialog" label={item ? (item.caption || 'Photo ' + (i + 1) + ' of ' + n) : 'Photo'} className={cx('oo-lp-lbox', props.boutique && 'oo-lp-lbox--boutique')}>
        {item ? (
          <div className="oo-lp-lbox__stage">
            {n > 1 ? <button type="button" className="oo-lp-lbox__nav oo-lp-lbox__nav--prev" aria-label="Previous photo" onClick={function () { step(-1); }}><span aria-hidden="true">‹</span></button> : null}
            <figure className="oo-lp-lbox__fig">
              <img src={item.image} alt={item.caption || 'Photo ' + (i + 1)} />
              <figcaption><span className="oo-sr-only">{(i + 1) + ' of ' + n + (item.caption ? ': ' : '')}</span>{item.caption || null}</figcaption>
            </figure>
            {n > 1 ? <button type="button" className="oo-lp-lbox__nav oo-lp-lbox__nav--next" aria-label="Next photo" onClick={function () { step(1); }}><span aria-hidden="true">›</span></button> : null}
          </div>
        ) : null}
      </ui.Sheet>
    );
  }
  function Gallery(props) {
    var s = props.section, ctx = props.ctx;
    var upgraded = upgradedGallery(s);
    var picked = React.useState(null), filter = React.useState('all');
    if (ctx.inDialog || (!upgraded && !ctx.boutique)) return React.createElement(base.gallery, props);
    var g = L.galleryOf(s);
    if (!g.items.length) return null;
    var shown = filter[0] === 'all' ? g.items : g.items.filter(function (v) { return v.group === filter[0]; });
    var masonry = s.layout === 'masonry';
    var cols = Math.min(6, Math.max(2, Number(s.columns) || (masonry ? 4 : 3)));
    var chips = g.filters.length ? [{ key: 'all', label: 'All', count: g.items.length }].concat(g.filters) : [];
    return (
      <Frame section={s} ctx={ctx} label="Gallery" className="oo-lp-bgal">
        {chips.length ? (
          <div className="oo-lp-filters" role="group" aria-label="Filter gallery">
            {chips.map(function (c) {
              var on = filter[0] === c.key;
              return <button key={c.key} type="button" className={cx('oo-lp-filter', on && 'is-active')} aria-pressed={on ? 'true' : 'false'} onClick={function () { filter[1](c.key); picked[1](null); }}>{c.label}<span className="oo-lp-filter__n" aria-hidden="true">{c.count}</span><span className="oo-sr-only">{', ' + c.count + (c.count === 1 ? ' photo' : ' photos')}</span></button>;
            })}
          </div>
        ) : null}
        <ul className={cx('oo-lp-shots', masonry ? 'oo-lp-shots--masonry' : 'oo-lp-shots--grid', 'oo-rv')} style={{ '--wl-columns': cols }}>
          {shown.map(function (v, i) {
            return <li key={v.image + '-' + i}><button type="button" className="oo-lp-shot" aria-label={'View larger: ' + (v.caption || 'photo ' + (i + 1))} onClick={function () { picked[1](i); }}><img src={v.image} alt="" loading="lazy" /></button></li>;
          })}
        </ul>
        <Lightbox items={shown} index={picked[0] !== null && picked[0] < shown.length ? picked[0] : null} pick={picked[1]} close={function () { picked[1](null); }} boutique={!!ctx.boutique} />
      </Frame>
    );
  }

  // ---- menu (+ accordion, jump nav, options, order links) -----------------------------------------------------------
  function upgradedMenu(s) { return s.layout === 'accordion' || s.jumpNav === true || s.showOptions === true || s.orderLinks === true; }
  function MenuItem(props) {
    var p = props.product, ctx = props.ctx, s = props.section;
    var price = L.priceOf(p);
    var groups = arr(p.groups).filter(function (g) { return g && g.available !== false && arr(g.options).length; });
    var sizes = arr(p.sizes).filter(function (z) { return z && z.available !== false && typeof z.price === 'number'; });
    var optCount = (sizes.length > 1 ? 1 : 0) + groups.length;
    var desc = L.text(p.description, 400);
    var img = s.showImages !== false ? p.image : null;
    return (
      <li className="oo-lp-mi">
        {img ? <OO.ui.Image cover className="oo-lp-mi__img" sizes="(min-width: 900px) 360px, (min-width: 600px) 45vw, 90vw" src={img} alt="" loading="lazy" /> : null}
        <div className="oo-lp-mi__body">
          <div className="oo-lp-mi__top">
            <h4 className="oo-lp-mi__name">{p.name}</h4>
            {typeof price.amount === 'number' ? <p className="oo-lp-mi__price">{price.from ? <em>from</em> : null}{price.from ? ' ' : null}{money(price.amount, ctx)}</p> : null}
          </div>
          {desc ? <p className="oo-lp-mi__desc">{desc}</p> : null}
          {s.orderLinks === true && !ctx.orderingOff ? (
            <a className="oo-lp-mi__order" href={L.menuHref(ctx.master || ctx.key, { type: 'product', id: String(p.id) })} onClick={function (e) { if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); ctx.order({ type: 'product', id: String(p.id) }); }}
              aria-label={'Order now: ' + p.name}>Order now<span aria-hidden="true"> →</span></a>
          ) : null}
          {s.showOptions === true && optCount ? (
            <details className="oo-lp-mi__opts">
              <summary>View details <span className="oo-lp-mi__n">{optCount}</span></summary>
              {sizes.length > 1 ? <ul className="oo-lp-mi__sizes" aria-label="Sizes">{sizes.map(function (z) { return <li key={z.id}><span>{z.name}</span> {money(z.price, ctx)}</li>; })}</ul> : null}
              {groups.map(function (g) {
                var opts = arr(g.options).filter(function (o) { return o && o.available !== false; });
                return (
                  <div key={g.id} className="oo-lp-mi__group">
                    <h5>{g.name}<span className="oo-lp-mi__rule">{L.ruleText(g)}</span></h5>
                    <ul>{opts.map(function (o) { return <li key={o.id}><span>{o.name}</span>{typeof o.price === 'number' && o.price > 0 ? <b>{'+' + money(o.price, ctx)}</b> : <b className="is-free">Free</b>}</li>; })}</ul>
                  </div>
                );
              })}
            </details>
          ) : null}
        </div>
      </li>
    );
  }
  function anchorOf(c) { return 'menu-' + String(c.id); }
  function Menu(props) {
    var s = props.section, ctx = builderCtx(props.ctx);
    var upgraded = upgradedMenu(s);
    var openS = React.useState({});
    var ref = React.useRef(null);
    var cats = L.menuCategories(ctx.menu).filter(function (c) { return !arr(s.categoryIds).length || s.categoryIds.some(function (id) { return String(id) === String(c.id); }); });
    var accordion = s.layout === 'accordion', anchors = accordion || s.jumpNav === true;
    var ids = cats.map(anchorOf).join('|');
    function openAt(id, scroll) {
      if (!id || ('|' + ids + '|').indexOf('|' + id + '|') < 0) return false;
      if (accordion) openS[1](function (cur) { var n = Object.assign({}, cur); n[id] = true; return n; });
      if (scroll) setTimeout(function () { Chrome.scrollToId(id); }, 0);
      return true;
    }
    React.useEffect(function () {
      if (!anchors || !ids) return undefined;
      var h = String((window.location && window.location.hash) || '').replace(/^#/, '');
      if (h) openAt(h, true);
      function onAnchor(e) { openAt(e && e.detail && e.detail.id, false); }
      function onHash() { openAt(String(window.location.hash || '').replace(/^#/, ''), true); }
      window.addEventListener('oo:site-anchor', onAnchor);
      window.addEventListener('hashchange', onHash);
      return function () { window.removeEventListener('oo:site-anchor', onAnchor); window.removeEventListener('hashchange', onHash); };
    }, [anchors, ids]);
    // Every hook runs on every render (the studio may switch a section between the two renderers live).
    var pending = !ctx.menu && !ctx.storeFailed && ctx.storeLoading !== false;
    var isLate = S.useLate(pending);
    if (ctx.inDialog || (!upgraded && !ctx.boutique)) return React.createElement(base.menu, props);
    var state = L.dataState({ loading: pending, failed: ctx.storeFailed, empty: !cats.length, late: isLate });
    if (state === 'hide') return null;
    if (state === 'wait') return <Frame section={s} ctx={ctx} label="Menu"><div className="oo-lp-block-loading" aria-busy="true" aria-label="Loading"><span className="oo-lp-skel" /></div></Frame>;
    var limit = Math.min(30, Math.max(1, Number(s.limitPerCategory) || 30));
    function itemsOf(c) { return arr(c.productIds).map(function (id) { return ctx.menu.products[id]; }).filter(function (p) { return p && p.hidden !== true; }).slice(0, limit); }
    function jump(e, id) {
      if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openAt(id, true);
      try { window.history.replaceState(window.history.state, '', '#' + id); } catch (x) { /* no history */ }
    }
    return (
      <Frame section={s} ctx={ctx} label="Menu" className="oo-lp-bmenu">
        {s.jumpNav === true && cats.length > 1 ? (
          <nav className="oo-lp-mjump" aria-label="Jump to a menu category">
            {cats.map(function (c) { return <a key={c.id} className="oo-lp-mjump__chip" href={'#' + anchorOf(c)} onClick={function (e) { jump(e, anchorOf(c)); }}>{c.name}</a>; })}
          </nav>
        ) : null}
        <div className="oo-lp-mcats" ref={ref}>
          {cats.map(function (c) {
            var list = itemsOf(c);
            var head = (
              <>
                {s.showImages !== false && c.image ? <OO.ui.Image cover className="oo-lp-mcat__img" sizes="(min-width: 900px) 88px, 52px" src={c.image} alt="" loading="lazy" /> : null}
                <span className="oo-lp-mcat__words">
                  <h3 className="oo-lp-mcat__name">{c.name}</h3>
                  {L.text(c.description, 300) ? <span className="oo-lp-mcat__desc">{L.text(c.description, 300)}</span> : null}
                  <span className="oo-lp-mcat__count">{list.length + (list.length === 1 ? ' item' : ' items')}</span>
                </span>
              </>
            );
            var grid = <ul className="oo-lp-mitems">{list.map(function (p) { return <MenuItem key={p.id} product={p} ctx={ctx} section={s} />; })}</ul>;
            if (accordion) {
              var id = anchorOf(c);
              return (
                <details key={c.id} id={id} className="oo-lp-mcat" open={!!openS[0][id]}
                  onToggle={function (e) { var on = e.currentTarget.open; if (!!openS[0][id] !== on) openS[1](function (cur) { var n = Object.assign({}, cur); n[id] = on; return n; }); }}>
                  <summary className="oo-lp-mcat__head">{head}</summary>
                  {grid}
                </details>
              );
            }
            return <div key={c.id} id={anchors ? anchorOf(c) : undefined} className="oo-lp-mcat oo-lp-mcat--flat"><div className="oo-lp-mcat__head">{head}</div>{grid}</div>;
          })}
        </div>
      </Frame>
    );
  }

  // ---- contact (+ enquiry type, preferred date) -----------------------------------------------------------------------
  var EMPTY = { name: '', email: '', phone: '', enquiryType: '', preferredDate: '', message: '', website: '' };
  function clockNow() { return OO.data && typeof OO.data.now === 'function' ? OO.data.now() : Date.now(); }
  function EnquiryForm(props) {
    var s = props.section;
    var f = React.useState(EMPTY), errs = React.useState({}), st = React.useState({ state: 'idle', message: '' });
    var uid = 'oo-lp-e-' + String(s.id);
    var types = L.enquiryTypesOf(s), askDate = s.askDate === true;
    function set(k) { return function (e) { var v = e.target.value; f[1](function (cur) { var n = Object.assign({}, cur); n[k] = v; return n; }); }; }
    function onSubmit(e) {
      e.preventDefault();
      if (st[0].state === 'sending') return;
      var v = L.enquiryValidate(f[0], s, clockNow(), props.timeZone); // VEL-FIX: today..+365 in the document's time zone
      errs[1](v.errors);
      if (!v.ok) {
        st[1]({ state: 'idle', message: '' });
        var form = e.currentTarget;
        setTimeout(function () { var el = form && form.querySelector('[aria-invalid="true"]'); if (el && el.focus) el.focus(); }, 0);
        return;
      }
      st[1]({ state: 'sending', message: 'Sending your message…' });
      L.submit(props.siteKey, L.enquiryBody(s.id, f[0], s)).then(function (r) {
        if (r.ok) { f[1](EMPTY); errs[1]({}); st[1]({ state: 'sent', message: r.message }); return; }
        errs[1](r.errors || {});
        st[1]({ state: 'failed', message: r.message });
      });
    }
    var e = errs[0];
    function field(k, label, input, wide) {
      var err = e[k] || (k === 'enquiryType' ? e.enquiry_type : k === 'preferredDate' ? e.preferred_date : null);
      if (Array.isArray(err)) err = err[0];
      return (
        <div className={cx('oo-lp-ef', wide && 'oo-lp-ef--wide', err && 'is-invalid')}>
          <label htmlFor={uid + '-' + k}>{label}</label>
          {React.cloneElement(input, { id: uid + '-' + k, 'aria-invalid': err ? 'true' : undefined, 'aria-describedby': err ? uid + '-' + k + '-err' : undefined })}
          {err ? <p className="oo-lp-ef__err" id={uid + '-' + k + '-err'}>{err}</p> : null}
        </div>
      );
    }
    var enquiry = types.length || askDate;
    var range = askDate ? L.dateRange(clockNow(), props.timeZone) : null; // VEL-FIX: the server's rule
    return (
      <form className="oo-lp-eform oo-rv" onSubmit={onSubmit} noValidate aria-describedby={uid + '-status'}>
        <div className="oo-lp-eform__grid">
          {field('name', 'Full name', <input type="text" name="name" autoComplete="name" maxLength={100} required value={f[0].name} onChange={set('name')} />)}
          {field('email', 'Email address', <input type="email" name="email" autoComplete="email" maxLength={254} required value={f[0].email} onChange={set('email')} />)}
          {field('phone', 'Phone number', <input type="tel" name="phone" autoComplete="tel" maxLength={32} value={f[0].phone} onChange={set('phone')} />)}
          {types.length ? field('enquiryType', 'Enquiry type', <select name="enquiry_type" value={f[0].enquiryType} onChange={set('enquiryType')}><option value="">Choose…</option>{types.map(function (t) { return <option key={t} value={t}>{t}</option>; })}</select>) : null}
          {askDate ? field('preferredDate', <>Preferred date <span className="oo-lp-ef__opt">(if relevant)</span></>, <input type="date" name="preferred_date" min={range.min} max={range.max} value={f[0].preferredDate} onChange={set('preferredDate')} />) : null}
          {field('message', 'Message', <textarea name="message" rows={5} maxLength={2000} required value={f[0].message} onChange={set('message')} />, true)}
        </div>
        <div className="oo-lp-hp" aria-hidden="true">
          <label htmlFor={uid + '-website'}>Website</label>
          <input id={uid + '-website'} name="website" type="text" tabIndex={-1} autoComplete="off" value={f[0].website} onChange={set('website')} />
        </div>
        <div className="oo-lp-eform__foot">
          <button type="submit" className="oo-lpb-pill oo-lpb-pill--primary oo-lp-eform__send" aria-busy={st[0].state === 'sending' ? 'true' : undefined}>{enquiry ? 'Send enquiry' : 'Send message'}<Glyph name="arrow-right" size={16} /></button>
          <p id={uid + '-status'} className={cx('oo-lp-eform__status', st[0].state === 'failed' && 'is-error')} role="status" aria-live="polite">{st[0].message}</p>
        </div>
      </form>
    );
  }
  function Contact(props) {
    var s = props.section, ctx = props.ctx;
    var upgraded = arr(s.enquiryTypes).length > 0 || s.askDate === true;
    if (!upgraded && !ctx.boutique) return React.createElement(base.contact, props);
    var email = typeof s.email === 'string' && /^[^\s@]+@[^\s@]+$/.test(s.email.trim()) ? s.email.trim() : null;
    if (!email) return React.createElement(base.contact, props);
    return <Frame section={s} ctx={ctx} label="Contact" className="oo-lp-bcontact"><EnquiryForm section={s} siteKey={ctx.key} timeZone={ctx.timeZone} /></Frame>;
  }

  // ---- banner (boutique) ----------------------------------------------------------------------------------------------
  function Banner(props) {
    var s = props.section, ctx = props.ctx;
    if (!ctx.boutique || ctx.inDialog) return React.createElement(base.banner, props);
    var img = L.httpsUrl(s.image), cta = linkOf(s.cta, ctx);
    return section(s, { className: cx('oo-lp-band2', img && 'has-image'), label: 'Announcement' }, (
      <>
        {img ? <img className="oo-lp-band2__bg" src={img} alt="" loading="lazy" /> : null}
        <div className="oo-lp-wrap oo-lp-band2__in oo-rv">
          <Head section={s} />
          {L.paragraphs(L.text(s.body, 300)).map(function (p, i) { return <p key={i} className="oo-lp-lead">{p}</p>; })}
          {cta ? <div className="oo-lp-band2__cta"><Pill link={cta} tone="cream" /></div> : null}
        </div>
      </>
    ));
  }

  Object.assign(REGISTRY, {
    pageHero: PageHero, quote: Quote, cards: Cards, contactInfo: ContactInfo, image: ImageBlock,
    about: About, features: Features, gallery: Gallery, menu: Menu, contact: Contact, banner: Banner,
  });
  OO.pages.LandingBoutiqueSections = { base: base, PageHero: PageHero, Quote: Quote, Cards: Cards, ContactInfo: ContactInfo, ImageBlock: ImageBlock, About: About, Features: Features, Gallery: Gallery, Menu: Menu, Contact: Contact, Banner: Banner, EnquiryForm: EnquiryForm, Lightbox: Lightbox, upgradedGallery: upgradedGallery, upgradedMenu: upgradedMenu };
})();


// ===================== src/pages/landing-boutique.jsx =====================
// src/pages/landing-boutique.jsx: WL-VEL, OO.pages.LandingBoutique: the white-label website's `boutique` design
// (landing.design 'boutique'; scratchpad SPEC §2). OO.pages.Landing (src/pages/landing.jsx) reads the site, the store
// and the chain, builds the link context and picks the page's sections (home, or a site page); this page draws them in
// the boutique chrome (OO.ui.SiteChrome: the status bar, the header with the wordmark, the footer with its CTA).
// Styles: styles/landing-boutique.css (.oo-lp--boutique, .oo-sc-*, .oo-lpb-*).
//
// Props: ctx (the landing's link context), landing, site, theme, name (brand name), tagline (the footer line), logo (the
// header logo), footLogo (the logo for the deep footer), page (the site page shown | null), sections (shown, in order),
// chooser (the store chooser element), dialog (the open section dialog | null).
//
// The hero (home only; BoutiqueHero): every slide's photo stacked, crossfading (1.4 s); hero.motion 'zoom' adds a slow
// zoom-out (1.06 → 1 over about 7 s) on the slide coming in. The copy is the current slide's: a slide logo above the
// headline (logoPlacement 'above') or in its place (the default, as the other designs), the headline with `trail` on its
// own italic line, the subline and one pill CTA; identical copy on every slide stays still while the photos change.
// Dots (the current one a pill) and a Pause / Play button while it plays (WCAG 2.2.2); it pauses while focus is inside.
// Auto-advance only with hero.autoplay, every hero.interval seconds (default 6), never under reduced motion (the
// crossfade then is short and there is no zoom).
// Reveal on scroll (useReveal): blocks marked .oo-rv (cards, split halves, quotes, forms, info cards, …) and sections
// without any rise and fade in once as they enter (threshold .14 of the block or half the viewport, bottom margin −8 %);
// the hidden state is a data-reveal attribute set only by JS, only below the fold, never under reduced motion or without
// IntersectionObserver.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};

  var ROTATE_MS = 6000;
  function L() { return OO.landing; }
  function cx() { return OO.ui.cx.apply(null, arguments); }
  function reducedNow() { try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; } }

  // ---- reveal on scroll -------------------------------------------------------------------------------------------------
  var HEAD_SEL = ':scope > .oo-lp-wrap > .oo-lp-eyebrow, :scope > .oo-lp-wrap > .oo-lp-sec__title, :scope > .oo-lp-wrap > .oo-lp-sec__subtitle, :scope > .oo-lp-wrap > .oo-lp-more';
  function useReveal(dep) {
    React.useLayoutEffect(function () {
      if (typeof document === 'undefined' || reducedNow() || typeof window.IntersectionObserver !== 'function') return undefined;
      var main = document.getElementById('oo-lp-main');
      if (!main) return undefined;
      var vh = window.innerHeight || 0;
      var io = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          if (e.intersectionRatio >= 0.14 || e.intersectionRect.height >= vh * 0.5) { e.target.setAttribute('data-reveal', 'in'); io.unobserve(e.target); }
        });
      }, { threshold: [0, 0.14, 0.3, 0.6], rootMargin: '0px 0px -8% 0px' });
      var targets = [];
      Array.prototype.forEach.call(main.children, function (sec) {
        if (!sec.classList || !sec.classList.contains('oo-lp-sec')) return;
        var marked = sec.querySelectorAll('.oo-rv');
        if (!marked.length) { targets.push(sec); return; }
        Array.prototype.forEach.call(marked, function (el) { targets.push(el); });
        try { Array.prototype.forEach.call(sec.querySelectorAll(HEAD_SEL), function (el) { targets.push(el); }); } catch (x) { /* no :scope */ }
      });
      targets.forEach(function (el) {
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', el.getBoundingClientRect().top < vh ? 'in' : 'out');
        if (el.getAttribute('data-reveal') === 'out') io.observe(el);
      });
      return function () { io.disconnect(); };
    });
    // A new page starts its own reveal (the elements are new; nothing to reset).
    return dep;
  }

  // ---- hero -------------------------------------------------------------------------------------------------------------
  function copyKey(s) { return [s.headline, s.trail, s.lead, s.subline, s.logo, s.logoPlacement, s.cta && s.cta.label].join('|'); }
  function BoutiqueHero(props) {
    var hero = props.hero || {}, ctx = props.ctx;
    var slides = (Array.isArray(hero.slides) ? hero.slides : []).filter(function (s) { return s && L().httpsUrl(s.image) && L().text(s.headline, 80); }).slice(0, 6);
    var n = slides.length;
    var idx = React.useState(0), focus = React.useState(false), stopped = React.useState(false);
    var reduced = OO.pages.Landing.logic.useReducedMotion();
    var touch = React.useRef(null);
    var requested = n ? idx[0] % n : 0;
    var media = OO.ui.useSlideImages(slides, requested, function (s) { return L().httpsUrl(s.image); });
    var i = media.index;
    var autoplay = hero.autoplay === true && !reduced;
    var rotating = autoplay && n > 1 && !focus[0] && !stopped[0];
    var interval = L().heroInterval(hero) || ROTATE_MS;
    React.useEffect(function () {
      if (!rotating) return undefined;
      var t = setTimeout(function () { idx[1](function (x) { return (x + 1) % n; }); }, interval);
      return function () { clearTimeout(t); };
    }, [rotating, i, n, interval]);
    if (!n) return null;
    var s = slides[i];
    var headline = L().text(s.headline, 80), trail = L().text(s.trail, 40), lead = L().text(s.lead, 40);
    var logo = L().httpsUrl(s.logo), above = !!logo && s.logoPlacement === 'above';
    var sub = L().text(s.subline, 160);
    var cta = s.cta && L().text(s.cta.label, 24) ? OO.ui.SiteChrome.linkOf(s.cta.target, L().text(s.cta.label, 24), ctx) : null;
    var Glyph = OO.landingIcons.Glyph;
    function pick(k) { stopped[1](true); idx[1](((k % n) + n) % n); }
    var Title = ctx.page ? 'p' : 'h1';
    return (
      <section className={cx('oo-lpb-hero', hero.motion === 'zoom' && !reduced && 'oo-lpb-hero--zoom', hero.align && 'oo-lpb-hero--' + hero.align)} aria-roledescription="carousel" aria-label="Highlights"
        onFocus={function () { focus[1](true); }} onBlur={function (e) { if (!e.currentTarget.contains(e.relatedTarget)) focus[1](false); }}
        onTouchStart={function (e) { var t = e.touches && e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; }}
        onTouchEnd={function (e) { var a = touch.current, t = e.changedTouches && e.changedTouches[0]; touch.current = null; if (!a || !t || n < 2) return; var dx = t.clientX - a.x; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(t.clientY - a.y)) pick(i + (dx < 0 ? 1 : -1)); }}>
        <div className="oo-lpb-hero__slides" aria-hidden="true">
          {slides.map(function (x, k) { return media.has(k) ? <OO.ui.Image key={k} cover zoom={hero.motion === 'zoom' && !reduced} className={cx('oo-lpb-hero__slide', k === i && 'is-on')} src={L().httpsUrl(x.image)} sizes="100vw" alt="" loading="eager" fetchpriority={k === 0 ? 'high' : 'auto'} onLoad={media.done(k)} onError={media.done(k)} /> : null; })}
        </div>
        <div className="oo-lpb-hero__in" key={copyKey(s)}>
          {above ? <OO.ui.Image className="oo-lpb-hero__logo" src={logo} sizes="78px" alt="" /> : null}
          {lead ? <p className="oo-lpb-hero__lead">{lead}</p> : null}
          <Title className="oo-lpb-hero__title">
            {logo && !above ? <><span className="oo-sr-only">{headline}</span><OO.ui.Image className="oo-lpb-hero__mark" src={logo} sizes="(max-width: 650px) 80vw, 520px" alt="" /></> : <span className="oo-lpb-hero__line">{headline}</span>}
            {trail ? <>{' '}<em className="oo-lpb-hero__trail">{trail}</em></> : null}
          </Title>
          {sub ? <p className="oo-lpb-hero__sub">{sub}</p> : null}
          {cta ? <div className="oo-lpb-hero__cta"><OO.ui.SiteChrome.SiteLink className="oo-lpb-pill oo-lpb-pill--cream oo-lpb-pill--lg" link={cta} after={<Glyph name="arrow-right" size={16} />} /></div> : null}
        </div>
        {n > 1 ? (
          <div className="oo-lpb-hero__ctrl">
            <div className="oo-lpb-hero__dots" role="group" aria-label="Choose a slide">
              {slides.map(function (x, k) { return <button key={k} type="button" className={cx('oo-lpb-hero__dot', k === i && 'is-on')} aria-label={'Show slide ' + (k + 1) + ' of ' + n} aria-current={k === i ? 'true' : undefined} onClick={function () { pick(k); }} />; })}
            </div>
            {autoplay ? <button type="button" className="oo-lpb-hero__pause" onClick={function () { stopped[1](!stopped[0]); }} aria-label={stopped[0] ? 'Play slideshow' : 'Pause slideshow'}>{stopped[0] ? 'Play' : 'Pause'}</button> : null}
          </div>
        ) : null}
      </section>
    );
  }

  // ---- the page -----------------------------------------------------------------------------------------------------------
  OO.pages.LandingBoutique = function LandingBoutique(props) {
    var landing = props.landing, site = props.site;
    var ctx = Object.assign({}, props.ctx, { boutique: true });
    var Chrome = OO.ui.SiteChrome;
    var hours = Chrome.useHours(ctx.store, ctx.master, ctx.timeZone);
    var chrome = Chrome.build(landing, ctx, { brand: site.doc.brand, logo: props.logo, footLogo: props.footLogo || props.logo, store: ctx.store, tagline: props.tagline, hoursRows: hours });
    useReveal(props.page ? props.page.slug : '');
    var Section = OO.pages.LandingSections.Section;
    var Ticker = OO.pages.Landing.parts.Ticker;
    return (
      <div className={cx('oo-lp', 'oo-lp--boutique', props.page && 'oo-lp--page')}>
        <a className="oo-lp-skip" href="#oo-lp-main">Skip to content</a>
        <Chrome.SiteTop sticky={chrome.header.sticky}
          status={<Chrome.StatusBar bar={chrome.bar} store={ctx.store} loading={!!ctx.storeLoading} />}
          header={<Chrome.SiteHeader brand={chrome.header.brand} nav={chrome.header.nav} cta={chrome.header.cta} basket={null} />} />
        <main id="oo-lp-main" className="oo-lp-main" tabIndex={-1}>
          {props.page ? null : <BoutiqueHero hero={landing.hero} ctx={ctx} />}
          {props.page ? null : <Ticker ticker={landing.ticker} />}
          {props.sections.map(function (s) { return L().isDialog(s) ? null : <Section key={s.id} section={s} ctx={ctx} />; })}
        </main>
        <Chrome.SiteFooter {...chrome.footer} />
        {props.chooser}
        {props.dialog}
      </div>
    );
  };
  OO.pages.LandingBoutique.parts = { BoutiqueHero: BoutiqueHero, useReveal: useReveal };
})();


// ===================== src/pages/landing-composition.jsx =====================
// Editable, data-only artboards. The Canvas page and the public prerender share this renderer.
(function () {
  'use strict';
  var OO = window.OO, L = OO.landing;
  var FONTS = ['Berthold Block', 'Bernoru SemiCondensed', 'Cubao', 'Gasoek One', 'Telegraf', 'Costa Rica', 'Poppins'];
  function number(value, fallback, min, max) { return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback; }
  // Keep matching weekdays together, while letting the store API own every time and closed day.
  function hoursLines(rows) {
    if (!Array.isArray(rows) || !rows.length) return [];
    var groups = [], lines = rows.length === 7 && rows.every(function (r) { return !r.closed; }) ? ['OPEN ALL DAYS'] : [];
    rows.forEach(function (row, i) {
      var time = row.closed ? 'Closed' : row.time;
      var group = groups.find(function (g) { return g.time === time; });
      if (!group) { group = { time: time, days: [] }; groups.push(group); }
      group.days.push({ index: i, name: row.dayLong, short: row.day });
    });
    groups.forEach(function (g) {
      var runs = [];
      g.days.forEach(function (day) {
        var last = runs[runs.length - 1];
        if (last && last[last.length - 1].index + 1 === day.index) last.push(day);
        else runs.push([day]);
      });
      function label(short) {
        return runs.map(function (run) {
          var names = run.map(function (d) { return short ? d.short : d.name; });
          return names.length > 2 ? names[0] + '–' + names[names.length - 1] : names.join(' & ');
        }).join(' & ');
      }
      var days = label(false);
      lines.push(days.length > 30 ? label(true) : days);
      String(g.time || '').split(', ').forEach(function (time) { lines.push(time.replace(/:00(?=\s*(?:am|pm)\b)/gi, '').replace(/\s*(am|pm)\b/gi, function (_, part) { return part.toUpperCase(); })); });
    });
    return lines;
  }
  // If a changed schedule needs more rows, extend the artboard and move the content below it.
  // The original positions, fonts and spacing remain identical whenever the hours fit their reserved frame.
  function hoursSection(section, lines) {
    var items = section.items || [], out = Object.assign({}, section), expansions = { desktop: [], mobile: [] };
    [false, true].forEach(function (mobile) {
      var list = expansions[mobile ? 'mobile' : 'desktop'];
      items.forEach(function (item) {
        if (item.element !== 'hours') return;
        var f = Object.assign({}, item, mobile ? item.mobile : null);
        var needed = Math.ceil(lines.length * (f.fontSize || 240) * (item.lineHeight || 120) / 100);
        if (needed > f.height) list.push({ at: f.y + f.height, amount: needed - f.height });
      });
    });
    function expanded(frame, list, hours) {
      var f = Object.assign({}, frame), shift = list.filter(function (e) { return e.at <= f.y; }).reduce(function (sum, e) { return sum + e.amount; }, 0);
      f.y += shift;
      if (hours) f.height = Math.max(f.height, Math.ceil(lines.length * (f.fontSize || 240) * (f.lineHeight || 120) / 100));
      return f;
    }
    out.items = items.map(function (item) {
      var next = expanded(item, expansions.desktop, item.element === 'hours');
      if (item.mobile) next.mobile = expanded(Object.assign({}, item, item.mobile, { mobile: undefined }), expansions.mobile, item.element === 'hours');
      return next;
    });
    out.height += expansions.desktop.reduce(function (sum, e) { return sum + e.amount; }, 0);
    out.mobileHeight = (section.mobileHeight || section.height) + expansions.mobile.reduce(function (sum, e) { return sum + e.amount; }, 0);
    return out;
  }
  function frame(item, width, height, mobile) {
    var m = mobile ? item.mobile || {} : {}, x = m.x === undefined ? item.x : m.x, y = m.y === undefined ? item.y : m.y;
    var w = m.width === undefined ? item.width : m.width, h = m.height === undefined ? item.height : m.height;
    return { x: 100 * number(x, 0, -20000, 40000) / width + '%', y: 100 * number(y, 0, -20000, 40000) / height + '%',
      w: 100 * number(w, 4000, 1, 40000) / width + '%', h: 100 * number(h, 1000, 1, 40000) / height + '%',
      size: 100 * number(m.fontSize === undefined ? item.fontSize : m.fontSize, 240, 1, 3000) / width,
      display: m.hidden === true ? 'none' : 'block' };
  }
  function Element(props) {
    var item = props.item, s = props.section, w = number(s.width, 13660, 100, 40000), h = number(s.height, 7680, 100, 40000);
    var desktop = frame(item, w, h, false), mobile = item.mobile ? frame(item, number(s.mobileWidth, w, 100, 10000), number(s.mobileHeight, h, 100, 40000), true) : desktop;
    var style = { '--comp-x': desktop.x, '--comp-y': desktop.y, '--comp-w': desktop.w, '--comp-h': desktop.h, '--comp-font': desktop.size,
      '--comp-mx': mobile.x, '--comp-my': mobile.y, '--comp-mw': mobile.w, '--comp-mh': mobile.h, '--comp-mfont': mobile.size, '--comp-mdisplay': mobile.display,
      borderStyle: item.borderWidth ? 'solid' : undefined, borderWidth: item.borderWidth ? (item.borderWidth / (item.fontSize || 240)) + 'em' : undefined, borderColor: item.borderColor || undefined,
      textShadow: item.shadowOffset ? (item.shadowOffset / (item.fontSize || 240)) + 'em ' + (item.shadowOffset / (item.fontSize || 240)) + 'em 0 rgba(0,0,0,.4)' : undefined,
      // WL-COMPOSITION-TYPE: optional metrics leave existing compositions unchanged.
      '--comp-letter-spacing': Number.isInteger(item.letterSpacing) ? number(item.letterSpacing, 0, -200, 500) / 1000 + 'em' : undefined,
      borderRadius: item.radius ? number(item.radius, 0, 0, 1000) / (item.fontSize || 240) + 'em' : undefined,
      '--comp-mwhitespace': item.mobile && typeof item.mobile.nowrap === 'boolean' ? (item.mobile.nowrap ? 'nowrap' : 'pre-wrap') : undefined,
      '--comp-whitespace': item.nowrap ? 'nowrap' : 'pre-wrap', color: item.color || 'inherit', backgroundColor: item.background || undefined,
      fontFamily: FONTS.indexOf(item.font) >= 0 ? '"' + (item.font === 'Poppins' ? 'Composition Poppins' : item.font) + '", sans-serif' : 'inherit',
      fontWeight: number(item.weight, 400, 400, 900), lineHeight: number(item.lineHeight, 120, 50, 220) / 100,
      textAlign: ['left', 'center', 'right', 'justify'].indexOf(item.align) >= 0 ? item.align : 'left', textTransform: item.uppercase ? 'uppercase' : undefined };
    var content;
    if (item.element === 'image') {
      var image = L.httpsUrl(item.image);
      if (!image) return null;
      var crop = item.crop, cropStyle = crop ? { position: 'absolute', left: 100 * crop.x / item.width + '%', top: 100 * crop.y / item.height + '%',
        width: 100 * crop.width / item.width + '%', height: 100 * crop.height / item.height + '%', maxWidth: 'none' } : { objectFit: item.fit === 'cover' ? 'cover' : 'contain' };
      content = <img src={image} alt={item.alt || ''} style={cropStyle} loading={props.eager ? 'eager' : 'lazy'} decoding="async" />;
    } else if (item.element === 'dots') content = <div className="oo-composition__dots" aria-hidden="true">{[0, 1, 2, 3, 4].map(function (i) { return <span key={i} style={{ animationDelay: i * 0.15 + 's' }} />; })}</div>;
    else if (item.element === 'rule') content = null;
    else if (item.element === 'hours') content = <div aria-label="Opening hours">{(props.hours || ['Hours unavailable']).map(function (line, i) { return <p key={i}>{line}</p>; })}</div>;
    else {
      var Tag = ['h1', 'h2', 'h3'].indexOf(item.tag) >= 0 ? item.tag : 'p';
      content = <Tag>{Array.isArray(item.runs) && item.runs.length ? item.runs.map(function (run, i) { return <React.Fragment key={i}>{i ? ' ' : null}{run.target ? <OO.pages.Landing.parts.TargetLink target={run.target} label={run.text} ctx={props.ctx} /> : run.text}</React.Fragment>; }) : item.text || ''}</Tag>;
    }
    if (item.phone && /^\+?[0-9 ()-]{1,24}$/.test(item.phone)) content = <a href={'tel:' + item.phone.replace(/[ ()-]/g, '')}>{content}</a>;
    else if (item.target) content = <OO.pages.Landing.parts.TargetLink target={item.target} label={item.text || item.alt || 'View'} ctx={props.ctx}>{content}</OO.pages.Landing.parts.TargetLink>;
    return <div className={'oo-composition__element oo-composition__element--' + item.element} style={style}>{content}</div>;
  }
  function Composition(props) {
    if ((props.section.items || []).some(function (item) { return item.element === 'hours'; }) && !(props.ctx && props.ctx.publicOnly)) return <LiveComposition {...props} />;
    return <CompositionView {...props} hours={['Loading hours…']} />;
  }
  function LiveComposition(props) {
    var ctx = props.ctx || {};
    var rows = OO.ui.SiteChrome.useHours(ctx.store, ctx.master || ctx.key, ctx.timeZone, true);
    var lines = hoursLines(rows);
    if (!lines.length) lines = [ctx.storeLoading ? 'Loading hours…' : 'Hours unavailable'];
    return <CompositionView {...props} section={hoursSection(props.section, lines)} hours={lines} />;
  }
  function CompositionView(props) {
    var s = props.section, w = number(s.width, 13660, 100, 40000), h = number(s.height, 7680, 100, 40000);
    var mw = number(s.mobileWidth, w, 100, 10000), mh = number(s.mobileHeight, h, 100, 40000), bg = L.httpsUrl(s.image);
    return <section id={L.anchorId(s.id)} className="oo-composition" aria-label={s.title || 'Website section'} tabIndex={-1}
      style={{ '--comp-ratio': w + ' / ' + h, '--comp-mobile-ratio': mw + ' / ' + mh, backgroundColor: s.background || '#FFFFFF' }}>
      {bg ? <img className="oo-composition__background" src={bg} alt="" style={{ opacity: number(s.imageOpacity, 100, 0, 100) / 100, transform: s.imageFlip ? 'scaleX(-1)' : undefined, objectPosition: Number.isInteger(s.imagePositionY) ? 'center ' + number(s.imagePositionY, 50, 0, 100) + '%' : undefined }} loading={props.eager ? 'eager' : 'lazy'} /> : null}
      <div className="oo-composition__board" style={{ maxWidth: s.maxWidth ? s.maxWidth + 'px' : undefined }}>
      {(Array.isArray(s.items) ? s.items : []).slice(0, 40).map(function (item, i) { return <Element key={i} item={item} section={s} ctx={props.ctx} eager={props.eager} hours={props.hours} />; })}</div>
    </section>;
  }
  function Header(props) {
    var landing = props.landing, ctx = props.ctx, colors = props.colors || {}, header = landing.header || {};
    var navFont = FONTS.indexOf(header.navFont) >= 0 ? '"' + (header.navFont === 'Poppins' ? 'Composition Poppins' : header.navFont) + '", sans-serif' : undefined;
    return (landing.nav || []).length ? <header className="oo-canvas-header" style={{ '--oo-header-bg': colors.headerBg, '--oo-header-ink': colors.headerInk, '--canvas-nav-font': navFont, '--canvas-nav-weight': Number.isInteger(header.navWeight) ? number(header.navWeight, 400, 400, 900) : undefined }}>
        <nav className="oo-canvas-nav" aria-label="Main navigation">{landing.nav.map(function (link, i) {
          return <OO.pages.Landing.parts.TargetLink key={i} target={link.target} label={link.label} ctx={ctx} />;
        })}{props.basket || null}</nav>
        {props.basket ? <div className="oo-canvas-mobile-basket">{props.basket}</div> : null}
        <details className="oo-canvas-menu" onClick={function (e) { if (e.target.closest('a')) e.currentTarget.open = false; }}><summary aria-label="Menu"><span className="oo-canvas-menu__icon" aria-hidden="true" /></summary>
          <nav aria-label="Mobile navigation">{landing.nav.map(function (link, i) {
            return <OO.pages.Landing.parts.TargetLink key={i} target={link.target} label={link.label} ctx={ctx} />;
          })}</nav></details>
      </header> : null;
  }
  function footerOf(landing) {
    var id = landing.footer && landing.footer.section;
    return id ? L.sectionsOf(landing).find(function (section) { return section.id === id && section.type === 'composition'; }) || null : null;
  }
  function CanvasPage(props) {
    var landing = props.landing, sections = props.sections || [], ctx = props.ctx;
    return <div className="oo-lp oo-lp--canvas">
      <a className="oo-lp-skip" href="#oo-lp-main">Skip to content</a>
      <Header landing={landing} ctx={ctx} />
      <main id="oo-lp-main" tabIndex={-1}>{sections.map(function (s, i) {
        return s.type === 'composition' ? <Composition key={s.id} section={s} ctx={ctx} eager={i === 0} /> : <OO.pages.LandingSections.Section key={s.id} section={s} ctx={ctx} />;
      })}</main>
      {props.chooser || null}{props.dialog || null}
    </div>;
  }
  OO.pages.LandingSections.REGISTRY.composition = Composition;
  OO.pages.LandingComposition = { Composition: Composition, CanvasPage: CanvasPage, Header: Header, footerOf: footerOf, hoursLines: hoursLines, hoursSection: hoursSection };
})();


// ===================== src/pages/app-content.jsx =====================
// Hosted brand pages use Studio's existing page/section contract and the published form service.
(function () {
  'use strict';
  var OO = window.OO, ui = OO.ui;
  var BUDGETS = ['Under $100k', '$100k - $250k', '$250k - $500k', '$500k+'];
  function site(route) { return OO.whitelabel.siteFor(route.chain || route.hotel); }
  function pageOf(route, slug) {
    var s = site(route), pages = s && s.doc.landing && s.doc.landing.pages || [];
    return pages.filter(function (p) { return p && p.slug === slug; })[0] || null;
  }
  function Link(p) {
    var T = OO.appKit.Zeta.TargetLink;
    return <T hotel={p.route.hotel || p.route.chain} route={p.route} target={p.target} className={p.className}>{p.children}</T>;
  }
  function Paras(p) { return <React.Fragment>{(Array.isArray(p.text) ? p.text : String(p.text || '').split(/\n\s*\n/)).filter(Boolean).map(function (text, i) { return <p key={i}>{text}</p>; })}</React.Fragment>; }
  function Heading(p) { return <React.Fragment>{p.section.eyebrow ? <small className="oo-app-content__eyebrow">{p.section.eyebrow}</small> : null}{p.section.title ? <h2>{p.section.title}</h2> : null}{p.section.subtitle ? <p>{p.section.subtitle}</p> : null}</React.Fragment>; }
  function Buttons(p) { return <div className="oo-app-content__buttons">{p.links.map(function (link, i) { return <Link key={i} route={p.route} target={link.target}>{link.label}<OO.icons.ChevronRight size={16} /></Link>; })}</div>; }
  function ContactInfo(p) {
    var s = p.section, contact = p.context.contact, socials = p.context.socials;
    if (s.style && s.style.tone === 'brand') return <section className="oo-app-content__section oo-app-content__callout"><Heading section={s} /><div className="oo-app-content__buttons">{contact.email ? <a href={'mailto:' + contact.email + (p.context.page === 'catering' ? '?subject=' + encodeURIComponent(p.context.short + ' catering enquiry') : '')}>Email {p.context.short}<OO.icons.ChevronRight size={16} /></a> : null}{s.more ? <Link route={p.route} target={s.more.target}>{s.more.label}<OO.icons.ChevronRight size={16} /></Link> : null}</div></section>;
    return <section className="oo-app-content__section"><Heading section={s} /><div className="oo-app-content__rows">
      {s.showContact !== false && contact.email ? <a href={'mailto:' + contact.email}><OO.icons.Envelope size={20} /><span><strong>{contact.email}</strong><small>General enquiries, catering and booking questions.</small></span><OO.icons.ChevronRight size={16} /></a> : null}
      {s.showContact !== false && contact.phone ? <a href={'tel:' + contact.phone.replace(/[^+0-9]/g, '')}><OO.icons.Phone size={20} /><span><strong>{contact.phone}</strong><small>New Zealand enquiries.</small></span><OO.icons.ChevronRight size={16} /></a> : null}
      {s.showAddress !== false ? <Link route={p.route} target={{ type: 'page', page: 'stores' }}><OO.icons.Pin size={20} /><span><strong>Find a {p.context.short} store</strong><small>Addresses, live status and directions.</small></span><OO.icons.ChevronRight size={16} /></Link> : null}
      {s.showSocials ? socials.map(function (social) { return <a key={social.network} href={social.url} target="_blank" rel="noopener noreferrer"><span>{social.network.charAt(0).toUpperCase() + social.network.slice(1)}</span><OO.icons.ChevronRight size={16} /></a>; }) : null}
    </div></section>;
  }
  function FranchiseForm(p) {
    var s = p.section, f = React.useState({ name: '', email: '', phone: '', location: '', budget: '', message: '', website: '' });
    var errors = React.useState({}), state = React.useState({ busy: false, sent: false, message: '' });
    var extended = s.formLayout === 'franchise', preview = OO.whitelabel.isPreview();
    function set(k) { return function (value) { f[1](function (old) { var next = Object.assign({}, old); next[k] = value; return next; }); }; }
    function submit(e) {
      e.preventDefault(); if (state[0].busy || preview) return;
      var fields = f[0], message = extended ? 'Preferred location: ' + fields.location.trim() + '\nInvestment budget: ' + (fields.budget || 'Not specified') + '\n\nHospitality or business experience:\n' + (fields.message.trim() || 'Not provided') : fields.message;
      var values = Object.assign({}, fields, { message: message });
      var result = OO.landing.validate(values), errs = Object.assign({}, result.errors);
      if (extended && !fields.phone.trim()) errs.phone = 'Enter your phone number.';
      if (extended && !fields.location.trim()) errs.location = 'Enter your preferred location.';
      errors[1](errs); if (Object.keys(errs).length) return;
      state[1]({ busy: true, sent: false, message: '' });
      OO.landing.submit(p.context.key, Object.assign(OO.landing.body(s.id, values), { surface: OO.mode === 'app' ? 'app' : 'online' })).then(function (r) {
        errors[1](r.errors || {}); state[1]({ busy: false, sent: r.ok, message: r.message });
        if (r.ok) f[1]({ name: '', email: '', phone: '', location: '', budget: '', message: '', website: '' });
      });
    }
    var errs = errors[0], status = state[0];
    return <section className="oo-app-content__form"><Heading section={s} /><Paras text={s.body} />
      {status.sent ? <p role="status" className="oo-app-content__success">{status.message}</p> : <form onSubmit={submit} noValidate>
        <ui.TextInput label={extended ? 'Full name *' : 'Name *'} labelVisible name="name" autoComplete="name" value={f[0].name} onChange={set('name')} error={errs.name} maxLength={100} required />
        <ui.TextInput label={extended ? 'Phone *' : 'Phone (optional)'} labelVisible name="phone" type="tel" autoComplete="tel" value={f[0].phone} onChange={set('phone')} error={errs.phone} maxLength={32} required={extended} />
        <ui.TextInput label="Email *" labelVisible name="email" type="email" autoComplete="email" value={f[0].email} onChange={set('email')} error={errs.email} maxLength={254} required />
        {extended ? <React.Fragment><ui.TextInput label="Preferred location *" labelVisible name="location" value={f[0].location} onChange={set('location')} error={errs.location} maxLength={120} required />
          <label className="oo-app-content__select">Investment budget<select name="budget" value={f[0].budget} onChange={function (e) { set('budget')(e.target.value); }}><option value="">Select a range</option>{BUDGETS.map(function (b) { return <option key={b}>{b}</option>; })}</select></label></React.Fragment> : null}
        <ui.TextArea label={extended ? 'Hospitality or business experience' : 'Message *'} name="message" value={f[0].message} onChange={set('message')} error={errs.message} maxLength={extended ? 1600 : 2000} size="xl" />
        <div className="oo-lp-hp" aria-hidden="true"><label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={f[0].website} onChange={function (e) { set('website')(e.target.value); }} /></label></div>
        <ui.Button type="submit" block busy={status.busy} disabled={preview}>{extended ? 'Submit enquiry' : 'Send message'}</ui.Button>
        {preview ? <p>Enquiries are available in the published app.</p> : null}{status.message ? <p role="alert">{status.message}</p> : null}
      </form>}
    </section>;
  }
  function ContentSection(p) {
    var s = p.section, route = p.route, cls = 'oo-app-content__section', muted = s.style && s.style.tone === 'muted';
    if (s.type === 'pageHero') return <section className={'oo-app-content__hero' + (s.image ? ' has-photo' : '')}>
      {s.image ? <ui.Image src={s.image} alt="" loading="eager" /> : null}<div>{s.eyebrow ? <small className="oo-app-content__eyebrow">{s.eyebrow}</small> : null}{p.first ? <h1>{s.title}</h1> : <h2>{s.title}</h2>}{s.subtitle ? <p>{s.subtitle}</p> : null}</div>
    </section>;
    if (s.type === 'franchise') return <FranchiseForm {...p} />;
    if (s.type === 'contactInfo') return <ContactInfo {...p} />;
    if (s.type === 'faq') return <section className={cls}><Heading section={s} /><div className="oo-app-content__faq">{(s.items || []).map(function (item, i) { return <details key={i}><summary>{item.q}<OO.icons.ChevronDown size={18} /></summary><p>{item.a}</p></details>; })}</div></section>;
    if (s.type === 'features') return <section className={cls}><Heading section={s} /><div className={'oo-app-content__features' + (s.layout === 'list' ? ' is-list' : '')}>{(s.items || []).map(function (item, i) { return <article key={i}>{item.icon ? <ui.Icon name={item.icon === 'users' ? 'UserCircle' : item.icon === 'shield' ? 'Shield' : 'Info'} size={23} /> : null}<h3>{item.title}</h3><p>{item.body}</p></article>; })}</div></section>;
    if (s.type === 'cards') {
      var shortcuts = s.items && s.items.length && s.items.every(function (item) { return !item.image && !item.body && item.links && item.links.length === 1; });
      if (shortcuts) return <section className={cls} aria-label={s.title}><nav className="oo-app-content__shortcuts">{s.items.map(function (item, i) { var target = item.links[0].target; return <Link key={i} route={route} target={target}><span><ui.Icon name={target.page === 'orders' ? 'Receipt' : target.page === 'menu' ? 'Utensils' : target.page === 'stores' ? 'Pin' : 'Info'} size={20} /></span>{item.links[0].label}</Link>; })}</nav></section>;
      return <section className={cls}><Heading section={s} /><div className="oo-app-content__cards">{(s.items || []).map(function (item, i) { return <article key={i}>{item.image ? <ui.Image src={item.image} alt="" /> : null}<h3>{item.title}</h3>{item.body ? <p>{item.body}</p> : null}<Buttons route={route} links={item.links || []} /></article>; })}</div></section>;
    }
    if (s.type === 'image') return <figure className={cls}><ui.Image src={s.image} alt={s.caption || ''} />{s.caption ? <figcaption>{s.caption}</figcaption> : null}</figure>;
    if (s.type === 'richText' || s.type === 'about' || s.type === 'banner') return <section className={cls + (muted ? ' is-muted' : '')}><Heading section={s} />{s.image ? <ui.Image src={s.image} alt="" /> : null}<Paras text={s.paragraphs || s.body} />{s.cta ? <Buttons route={route} links={[s.cta]} /> : null}{s.more ? <Buttons route={route} links={[s.more]} /> : null}</section>;
    return <OO.pages.LandingSections.Section section={s} ctx={Object.assign({}, p.context, { route: route })} />;
  }
  OO.pages.AppContent = function AppContent(p) {
    var route = p.route, s = site(route), page = pageOf(route, route.params.slug), landing = s && s.doc.landing || {};
    OO.useChain(route.chain || route.hotel, { theme: !route.hotel });
    React.useEffect(function () { if (page && typeof document !== 'undefined') document.title = page.seo && page.seo.title || (page.label || page.slug) + ' · ' + s.doc.brand.name; }, [page, s]);
    if (!page) return <ui.EmptyState icon="Info" title="Page not found" body="This page is not available." />;
    var context = { key: route.chain || route.hotel, landing: landing, page: page.slug, brand: s.doc.brand, short: s.doc.brand.name.split(' ')[0], contact: landing.contact || {}, socials: landing.footer && landing.footer.socials || [], sections: page.sections || [] };
    return <main className="oo-app-content">{(page.sections || []).filter(function (section) { return section.enabled === true; }).map(function (section, i) { return <div key={section.id} id={'wl-' + section.id}><ContentSection section={section} first={i === 0} route={route} context={context} /></div>; })}</main>;
  };
  OO.pages.AppContent.withoutBranch = true;
  OO.pages.AppContent.hasPage = function (route, slug) { return !!pageOf(route, slug); };
  OO.pages.AppContent.FranchiseForm = FranchiseForm;
  OO.pages.AppContent.ContentSection = ContentSection;

  function preferenceKey() { var r = OO.router.current(); return 'oo-app-preferences-v1:' + (r.chain || r.hotel || 'app'); }
  function preferences() { try { return JSON.parse(window.localStorage.getItem(preferenceKey()) || '{}') || {}; } catch (e) { return {}; } }
  function savePreference(key, value) { var p = preferences(); p[key] = value; try { window.localStorage.setItem(preferenceKey(), JSON.stringify(p)); } catch (e) { /* Device storage can be unavailable. */ } }
  OO.appPreferences = { read: preferences, set: savePreference, haptics: function () { var r = OO.router.current(); return OO.mode !== 'app' || !OO.whitelabel.zetaDesign(r.chain || r.hotel) || preferences().haptics !== false && window.localStorage.getItem('oo-zeta-haptics-v1') !== '0'; } };
  OO.appKit.AppWelcome = function AppWelcome(p) {
    var open = React.useState(false), s = site(p.route), key = p.route.chain || p.route.hotel;
    React.useEffect(function () {
      if (!key) return;
      var seen = 'oo-app-welcome-seen:' + key;
      try { if (window.sessionStorage.getItem(seen)) return; window.sessionStorage.setItem(seen, '1'); } catch (e) { return; }
      if (preferences().welcome === true) open[1](true);
    }, [key]);
    return <ui.Sheet open={open[0]} onClose={function () { open[1](false); }} title={'Welcome to ' + (s && s.doc.brand.name || 'our app')} label="Welcome" variant="dialog"><div className="oo-app-content__welcome"><OO.appKit.Zeta.Hero hotel={p.route.hotel || key} design={s && s.doc.app || {}} /><p>{s && s.doc.brand.tagline}</p><ui.Button block onClick={function () { open[1](false); }}>Let's go</ui.Button></div></ui.Sheet>;
  };
  OO.pages.AppSettings = function AppSettings(p) {
    var route = p.route, s = site(route), brand = s && s.doc.brand.name || 'Our app', prefs = React.useState(preferences), intro = React.useState(false), update = React.useState('');
    var h = OO.useHotel(route.hotel, { withMenu: false }), notes = OO.pages.Notifications.enabled(route.hotel);
    OO.useChain(route.chain || route.hotel, { theme: !route.hotel });
    function toggle(k, title, body, fallback) { var on = prefs[0][k] === undefined ? fallback : !!prefs[0][k]; return <button type="button" className="oo-app-setting" role="switch" aria-checked={on} onClick={function () { savePreference(k, !on); prefs[1](preferences()); }}><span><strong>{title}</strong><small>{body}</small></span><span className="oo-app-setting__switch" aria-hidden="true" /></button>; }
    return <main className="oo-app-content oo-app-settings"><div className="oo-app-content__hero"><h1>Settings</h1><p>Preferences for this device.</p></div>
      <section className="oo-app-content__section"><h2>Notifications</h2><Link route={route} target={{ type: 'page', page: 'account' }} className="oo-app-setting"><span><strong>Order updates</strong><small>{notes ? 'Order progress and rewards news in your account.' : 'Order updates are available through your order receipt.'}</small></span><OO.icons.ChevronRight size={18} /></Link></section>
      <section className="oo-app-content__section"><h2>App experience</h2>{toggle('haptics', 'Haptic feedback', 'Vibrate on taps and adding items.', true)}{toggle('welcome', 'Welcome screens', 'Show the intro next time the app opens.', false)}<button type="button" className="oo-app-setting" onClick={function () { intro[1](true); }}><span><strong>Replay welcome screens now</strong><small>Run the intro straight away.</small></span><OO.icons.ChevronRight size={18} /></button></section>
      <section className="oo-app-content__section"><h2>Ordering</h2><Link route={route} target={{ type: 'page', page: 'stores' }} className="oo-app-setting"><span><strong>Your store</strong><small>{h.store ? h.store.name : 'Choose a store'}</small></span><OO.icons.ChevronRight size={18} /></Link><Link route={route} target={{ type: 'page', page: 'stores' }} className="oo-app-setting"><span><strong>Location</strong><small>Turn on to sort stores by distance.</small></span><OO.icons.ChevronRight size={18} /></Link></section>
      <section className="oo-app-content__section"><h2>Help and legal</h2>{[['help', 'Help and FAQ'], ['about', 'About ' + brand.split(' ')[0]], ['privacy', 'Privacy'], ['terms', 'Terms and conditions']].filter(function (x) { return pageOf(route, x[0]); }).map(function (x) { return <Link key={x[0]} route={route} target={{ type: 'sitePage', page: x[0] }} className="oo-app-setting"><strong>{x[1]}</strong><OO.icons.ChevronRight size={18} /></Link>; })}</section>
      <section className="oo-app-content__section"><h2>Account</h2><Link route={route} target={{ type: 'page', page: 'account' }} className="oo-app-setting"><span><strong>Profile and account</strong><small>Details, addresses, sign out.</small></span><OO.icons.ChevronRight size={18} /></Link></section>
      <section className="oo-app-content__section"><h2>About this app</h2><button type="button" className="oo-app-setting" onClick={function () { if (!window.SipoVersion) return; update[1]('Checking for updates…'); window.SipoVersion.check().then(function () { update[1](window.SipoVersion.stale() ? 'An update is available.' : 'Update check complete.'); }); }}><strong>Check for updates</strong><OO.icons.Refresh size={18} /></button>{update[0] ? <p role="status">{update[0]}</p> : null}{window.SipoVersion && window.SipoVersion.buildId() ? <p>Version {window.SipoVersion.buildId()}</p> : null}<p>{brand}</p></section>
      <ui.Sheet open={intro[0]} onClose={function () { intro[1](false); }} label={'Welcome to ' + brand} title={'Welcome to ' + brand} variant="dialog"><div className="oo-app-content__welcome"><OO.appKit.Zeta.Hero hotel={route.hotel || route.chain} design={s && s.doc.app || {}} /><p>{s && s.doc.brand.tagline}</p><ui.Button block onClick={function () { intro[1](false); }}>Let's go</ui.Button></div></ui.Sheet>
    </main>;
  };
  OO.pages.AppSettings.withoutBranch = true;
})();


// ===================== src/pages/zeta-runtime.jsx =====================
// Native service boundary for Zeta's reference screens. Pricing, cart lines, auth tokens and checkout stay in OO.
(function () {
  'use strict';

  var OO = window.OO,
    K = OO.appKit,
    ui = OO.ui;
  var factories = {},
    cache = {},
    adapters = {},
    aliases = {};
  function requireView(id) {
    if (aliases[id]) return requireView(aliases[id]);
    if (adapters[id]) return adapters[id];
    if (cache[id]) return cache[id];
    if (!factories[id]) throw new Error('Missing Zeta view dependency ' + id);
    var exports = cache[id] = {};
    factories[id]({
      i: requireView,
      A: function (id) {
        return OO.zetaReference.importView(id);
      },
      n: function (value) {
        Object.assign(exports, value);
      },
      s: function (entries) {
        for (var n = 0; n < entries.length;) {
          var name = entries[n++],
            value = entries[n++];
          if (value === 0) exports[name] = entries[n++];else Object.defineProperty(exports, name, {
            enumerable: true,
            get: value
          });
        }
      }
    });
    return exports;
  }
  OO.zetaReference = {
    define: function (id, factory) {
      factories[id] = factory;
    },
    alias: function (id, target) {
      aliases[id] = target;
    },
    require: requireView,
    adapters: adapters
  };
  OO.zetaReference.productName = function (id) {
    var route = OO.router.current(),
      h = OO.data.peekHotel(route.hotel, true),
      product = h && h.menu && h.menu.products[String(id)];
    return product && product.name || '';
  };
  var Context = React.createContext(null),
    branches = [];
  function current() {
    return OO.router.current();
  }
  function key() {
    var r = current();
    return r.hotel || r.chain;
  }
  function brand() {
    var r = current(),
      s = OO.whitelabel.siteFor(r.chain || r.hotel);
    return s && s.doc.brand || {};
  }
  function rawStore(store) {
    return store && (store.raw || store._raw) || {};
  }
  function shortName(store) {
    return K.Zeta.branchName ? K.Zeta.branchName(store, branches, brand()) : (store && store.name || '').replace(/^Zeta Greek Kitchen\s*/i, '').replace(/^\((.*)\)$/, '$1');
  }
  function sourceCustomer(c) {
    return c ? Object.assign({}, c, {
      phone_no: c.phone,
      phone: c.phone,
      addresses: (c.addresses || []).map(function (a) {
        return {
          addressUid: a.uid,
          label: a.label || (a.type === 'HOME' ? 'Home' : a.type === 'WORK' ? 'Work' : 'Other'),
          line1: a.address,
          city: a.city,
          zipcode: a.zipcode,
          latitude: a.lat,
          longitude: a.lng,
          isSelected: !!a.selected
        };
      })
    }) : null;
  }
  function sourceBranch(b, meta) {
    return Object.assign({
      id: String(b.id),
      storeId: Number(b.id),
      onlineKey: b.onlineKey || b.key,
      name: shortName(b),
      address: b.address,
      lat: b.lat,
      lng: b.lng,
      timezone: b.timezone,
      status: 'open',
      tags: (b.orderTypes || []).map(function (t) {
        return t === 'PICKUP' ? 'Pickup' : t === 'DELIVERY' ? 'Delivery' : 'Dine in';
      })
    }, meta || {}, {
      address: meta && meta.address || (typeof b.address === 'string' ? b.address : b.address && b.address.full) || ''
    });
  }
  function useBranch() {
    var r = current(),
      h = OO.useHotel(r.hotel, {
        withMenu: false
      }),
      cr = OO.useChain(r.chain || r.hotel, {
        theme: false
      });
    var draft = OO.useStore(function (s) {
        return s.checkout;
      }) || {},
      store = h.store;
    var raw = OO.useAsync(function () {
      return r.hotel ? OO.api.getRestaurant(r.hotel) : Promise.resolve(null);
    }, [r.hotel]);
    var record = raw.data && (Array.isArray(raw.data.data) ? raw.data.data[0] : raw.data.data) || {};
    var list = cr.chain && cr.chain.stores || [];
    var liveBranches = React.useMemo(function () {
      var seed = OO.zetaReference.require(901002).locations;
      // The live chain decides eligibility; reference entries only supply presentation details.
      return list.map(function (b) {
        var meta = seed.filter(function (meta) {
          return (b.onlineKey || b.key) === meta.onlineKey;
        })[0];
        return sourceBranch(b, meta);
      });
    }, [cr.chain]);
    branches.splice.apply(branches, [0, branches.length].concat(liveBranches));
    var selected = React.useMemo(function () {
      var meta = liveBranches.filter(function (b) {
        return store && b.onlineKey === store.onlineKey;
      })[0];
      return store ? sourceBranch(store, meta) : null;
    }, [store, liveBranches]);
    var config = React.useMemo(function () {
      return Object.assign({}, record, store || {});
    }, [store, raw.data]);
    config.isPreorder = !!(store && store.scheduling && store.scheduling.preorder);
    config.isPaused = !!(store && store.online && store.online.active === false);
    config.pauseReason = store && store.online && store.online.reason;
    config.onlineOrderActive = !!(store && store.online && store.online.active !== false);
    config.id = store && store.id;
    config.hotelId = config.id;
    config.pickupEnabled = !!(store && (store.orderTypes || []).indexOf('PICKUP') >= 0);
    config.deliveryEnabled = !!(store && (store.orderTypes || []).indexOf('DELIVERY') >= 0);
    config.timezone = store && store.timezone;
    config.payment_pickup = (store && store.paymentMethods || []).filter(function (p) {
      return p.orderTypes && p.orderTypes.indexOf('PICKUP') >= 0;
    });
    config.payment_delivery = (store && store.paymentMethods || []).filter(function (p) {
      return p.orderTypes && p.orderTypes.indexOf('DELIVERY') >= 0;
    });
    var branchStatuses = {};
    list.forEach(function (b) {
      branchStatuses[b.key] = {
        isOpen: !!b.open,
        isPreorder: !b.open && !!b.opensOn,
        isPaused: b.onlineActive === false,
        pauseReason: b.inactiveReason
      };
    });
    if (store) {
      var os = OO.model.openStatus(store, OO.data.now());
      branchStatuses[store.onlineKey] = {
        isOpen: os.isOpen,
        isPreorder: !os.isOpen && os.canOrder !== false,
        isPaused: !!store.paused,
        pauseReason: store.pauseReason
      };
    }
    var chooseBranch = React.useCallback(function (b) {
      var active = current();
      OO.store.setBranch(b.onlineKey, active.chain || active.hotel);
      OO.router.go(OO.router.href(active.page, active.params, {
        hotel: b.onlineKey, chain: active.chain,
        query: Object.assign({}, active.query, { store: b.onlineKey })
      }), { replace: true });
    }, []);
    return {
      branchStatuses: branchStatuses,
      currentBranch: selected,
      allBranches: liveBranches,
      branches: liveBranches,
      isDetecting: cr.loading,
      allConfigs: store ? Object.fromEntries([[store.onlineKey, config]]) : {},
      setBranch: chooseBranch,
      restaurantConfig: config,
      orderType: draft.orderType || 'PICKUP',
      scheduleType: draft.slot && draft.slot !== 'ASAP' ? 'SCHEDULED' : 'ASAP',
      scheduledTime: draft.slot && draft.slot !== 'ASAP' ? draft.slot : null,
      setOrderType: function (type) {
        OO.store.setCheckout(Object.assign({}, draft, {
          orderType: type
        }));
      }
    };
  }
  function Provider(p) {
    var r = p.route,
      hotel = r.hotel || r.chain;
    var native = OO.useHotel(r.hotel);
    var moment = OO.pages.Cart.shared.useUpsellMoment(r.hotel, native.store, native.menu);
    var session = OO.useStore(function (s) {
      return s.session;
    });
    var customer = session && session.customer;
    var pending = React.useRef(null),
      inflight = React.useRef(false),
      sent = React.useRef(null);
    var signup = React.useState(false),
      pendingEmail = React.useState(''),
      toast = React.useState(null);
    var alive = React.useRef(true);
    React.useEffect(function () {
      alive.current = true;
      return function () {
        alive.current = false;
      };
    }, []);
    var refresh = React.useCallback(function (uid) {
      return OO.api.getCustomer(hotel, uid).then(function (raw) {
        var c = OO.model.normalizeCustomer(raw);
        if (c && OO.store.session() && OO.store.session().customer.uid === c.uid) OO.store.setSession(Object.assign({}, OO.store.session(), {
          customer: c
        }));
        return c;
      });
    }, [hotel]);
    var auth = React.useMemo(function () {
      async function login(email) {
        if (inflight.current) return;
        var to = OO.pages.Profile.logic.otpEmail(email),
          now = Date.now();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) throw new Error('Enter a valid email address.');
        if (sent.current && sent.current.email === to && now - sent.current.at < 60000) {
          pendingEmail[1](to);
          return;
        }
        inflight.current = true;
        try {
          await OO.api.sendOtp(hotel, to);
          sent.current = {
            email: to,
            at: Date.now()
          };
          if (alive.current) {
            pendingEmail[1](to);
            signup[1](false);
          }
        } finally {
          inflight.current = false;
        }
      }
      async function verify(email, code) {
        if (inflight.current) return;
        inflight.current = true;
        try {
          var to = sent.current && sent.current.email || OO.pages.Profile.logic.otpEmail(email);
          var raw = await OO.api.verifyOtp(hotel, to, OO.pages.Profile.logic.otpCode(code));
          var c = raw && raw.customer ? OO.model.normalizeCustomer(raw) : null;
          if (c && c.uid) {
            OO.store.setSession({
              customer: c,
              customToken: raw.customToken || null
            });
            signup[1](false);
            pendingEmail[1]('');
            return {
              success: true
            };
          }
          var uid = OO.data.signupUid(hotel, raw);
          if (!uid) throw new Error('We could not complete sign-in. Please try again.');
          pending.current = {
            uid: uid,
            email: to,
            token: raw.customToken || null
          };
          signup[1](true);
          return {
            needsSignup: true
          };
        } finally {
          inflight.current = false;
        }
      }
      async function register(fields, phone) {
        if (inflight.current) return;
        var args = typeof fields === 'string' ? {
            name: fields,
            phone: phone
          } : fields || {},
          q = pending.current;
        if (!q) throw new Error('Please verify your email again.');
        var value = args.phone || args.phone_no || '';
        if (value && value.charAt(0) !== '+') {
          var kit = OO.pages.Checkout.logic;
          var parts = kit.phoneParts(value, 'NZ', null);
          value = kit.phoneValue(parts.iso, parts.national);
        }
        var error = OO.pages.Profile.logic.signupPhoneError(value);
        if (error) throw new Error(error);
        inflight.current = true;
        try {
          var raw = await OO.api.register(hotel, {
            uid: q.uid,
            name: String(args.name || '').trim(),
            email: q.email,
            phone_no: value,
            birthday: null
          });
          var c = OO.model.normalizeCustomer(raw);
          if (!c) throw new Error('We could not create your account. Please try again.');
          OO.store.setSession({
            customer: c,
            customToken: q.token
          });
          signup[1](false);
          pendingEmail[1]('');
          pending.current = null;
          return {
            success: true
          };
        } finally {
          inflight.current = false;
        }
      }
      return {
        user: customer ? {
          uid: customer.uid,
          displayName: customer.name,
          email: customer.email
        } : null,
        customerData: sourceCustomer(customer),
        loading: false,
        loginWithOTP: login,
        verifyAndLogin: verify,
        signupAndLogin: register,
        needsSignup: signup[0],
        pendingEmail: pendingEmail[0],
        logout: function () {
          OO.store.logout();
          return Promise.resolve();
        },
        refreshCustomerData: refresh,
        updateCustomerDataLocally: function () {},
        openAccountDrawer: function () {
          navigate('/profile');
        }
      };
    }, [hotel, customer, signup[0], pendingEmail[0], refresh]);
    var notify = React.useMemo(function () {
      var api = {};
      ['success', 'error', 'warning', 'info'].forEach(function (name) {
        api[name] = function (body, options) {
          toast[1]([options && options.title, body].filter(Boolean).join(' · '));
        };
      });
      return api;
    }, []);
    return <Context.Provider value={{
      auth: auth,
      toast: notify,
      native: native,
      moment: moment
    }}><div className="oo-zeta-native">{p.children}</div><ui.Toast open={!!toast[0]} onClose={function () {
        toast[1](null);
      }}>{toast[0]}</ui.Toast></Context.Provider>;
  }
  function useAuth() {
    var ctx = React.useContext(Context);
    if (!ctx) throw new Error('Zeta screen provider missing');
    return ctx.auth;
  }
  function useToast() {
    var ctx = React.useContext(Context);
    return ctx.toast;
  }
  function targetOf(href) {
    var r = current(),
      u = new URL(href || '/', 'https://zeta.invalid'),
      path = u.pathname,
      parts = path.split('/').filter(Boolean),
      q = {};
    u.searchParams.forEach(function (v, k) {
      q[k] = v;
    });
    if (q.returnTo && q.returnTo.charAt(0) === '/' && q.returnTo.indexOf('//') !== 0) q.returnTo = nativeHref(q.returnTo);
    var page = {
      '': 'home',
      offers: 'deals',
      profile: 'profile',
      addresses: 'zeta-addresses',
      favourites: 'menu',
      'orders/track': 'track',
      checkout: 'checkout',
      menu: 'menu',
      wallet: 'wallet',
      rewards: 'rewards',
      coupons: 'coupons',
      orders: 'orders',
      stores: 'stores',
      settings: 'settings'
    }[parts.join('/')];
    var params = null;
    if (path === '/favourites') q.saved = 'true';
    if (path === '/track' || path === '/order-details') {
      page = 'track';
      if (q.id) {
        params = {
          orderId: OO.pages.Track.logic.normalizeId(q.id)
        };
        delete q.id;
      }
    }
    if (!page && parts[0] === 'track') {
      page = 'track';
      params = {
        orderId: parts[1]
      };
    }
    if (!page && parts[0] === 'orders' && parts[1]) {
      page = 'order-status';
      params = {
        orderId: parts[1]
      };
    }
    if (!page && ['about', 'help', 'contact', 'catering', 'franchise', 'allergen', 'terms', 'privacy'].indexOf(parts[0]) >= 0) {
      page = 'app-content';
      params = {
        slug: parts[0]
      };
    }
    if (page === 'menu') {
      if (q.item || q.combo) {
        q.type = q.combo ? 'combo' : 'product';
        delete q.item;
        delete q.combo;
      }
    }
    if (!page) return null;
    return {
      page: page,
      params: params,
      options: {
        hotel: r.hotel || r.chain,
        query: Object.assign({}, r.hotel ? {
          store: r.hotel
        } : {}, q),
        hash: u.hash
      }
    };
  }
  function nativeHref(href) {
    var target = targetOf(href);
    return target ? OO.router.href(target.page, target.params, target.options) : href;
  }
  function navigate(href, replace) {
    var r = current(),
      prefix = ((OO.boot || {}).routeBase || '') + '/' + encodeURIComponent(r.chain || r.hotel);
    if (href === prefix || href.indexOf(prefix + '/') === 0 || href.indexOf(prefix + '?') === 0) {
      OO.router.go(href, {
        replace: !!replace
      });
      return;
    }
    var target = targetOf(href);
    if (target) OO.router.go(OO.router.href(target.page, target.params, target.options), {
      replace: !!replace
    });
  }
  var routerObject = {
    push: function (href) {
      navigate(href);
    },
    replace: function (href) {
      navigate(href, true);
    },
    back: function () {
      OO.router.back(nativeHref('/'));
    },
    prefetch: function () {}
  };
  function Link(p) {
    var target = typeof p.href === 'string' && p.href.charAt(0) === '/' ? targetOf(p.href) : null;
    var link = target ? OO.router.link(target.page, target.params, target.options) : null;
    return <a {...p} href={link ? link.href : p.href} onClick={function (e) {
      if (p.onClick) p.onClick(e);
      if (link && !e.defaultPrevented) link.onClick(e);
    }}>{p.children}</a>;
  }
  function Image(p) {
    var props = Object.assign({}, p);
    delete props.fill;
    delete props.priority;
    delete props.context;
    delete props.meta;
    if (props.src) props.src = K.ZetaAsset ? K.ZetaAsset(props.src) : props.src;
    if (props.src && props.src.charAt(0) === '/' && !props.src.includes('/online-order/')) props.src = 'https://app.zeta.sipo.nz' + props.src;
    if (p.fill) props.style = Object.assign({
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%'
    }, props.style);
    return <ui.Image {...props} loading={p.priority ? 'eager' : 'lazy'} />;
  }
  var motion = {};
  ['div', 'section', 'article', 'header', 'main', 'aside', 'button', 'span', 'p', 'a', 'nav', 'ul', 'li', 'footer'].forEach(function (tag) {
    motion[tag] = React.forwardRef(function (p, ref) {
      var props = Object.assign({}, p);
      ['initial', 'animate', 'exit', 'transition', 'layout', 'layoutId', 'whileTap', 'whileHover', 'variants'].forEach(function (k) {
        delete props[k];
      });
      props.ref = ref;
      return React.createElement(tag, props);
    });
  });
  function createIcon(name, paths) {
    return React.forwardRef(function (p, ref) {
      var props = Object.assign({
        xmlns: 'http://www.w3.org/2000/svg',
        width: 24,
        height: 24,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': 'true'
      }, p);
      props.ref = ref;
      if (p.size) props.width = props.height = p.size;
      delete props.size;
      return React.createElement('svg', props, paths.map(function (path) {
        return React.createElement(path[0], path[1]);
      }));
    });
  }
  function jsx(type, props, k) {
    return React.createElement(type, k === undefined ? props : Object.assign({}, props, {
      key: k
    }));
  }
  function useLoyalty() {
    var r = current(),
      hotel = r.hotel,
      auth = useAuth(),
      uid = auth.user && auth.user.uid;
    var request = OO.useAsync(function () {
      if (!uid || !hotel) return Promise.resolve(null);
      return OO.data.loadWallet(hotel, {
        fresh: true
      }).then(function () {
        return Promise.all([OO.api.getLoyalty(hotel, {
          uid: uid
        }), OO.api.getWalletTransactions(hotel, {
          uid: uid
        })]);
      }).then(function (res) {
        return {
          loyalty: res[0],
          tx: res[1]
        };
      });
    }, [hotel, uid]);
    var refresh = React.useCallback(function () {
      request.reload();
      return Promise.resolve();
    }, [hotel, uid]);
    var data = request.data,
      wallet = data && data.loyalty && data.loyalty.wallet;
    var mapped = wallet ? Object.assign({}, wallet, {
      walletPoints: Number(wallet.wallet_points || wallet.walletPoints || 0),
      sipoCustomerId: wallet.customer_id,
      customerCode: wallet.customer_code,
      membershipType: wallet.membership_type,
      membershipTypeObj: wallet.membership_type_obj,
      redemptionRatios: (wallet.redemption_ratio || wallet.redumption_ratio || []).map(function (row) {
        return Object.assign({}, row, {
          orderType: row.order_type,
          ratioPoints: Number(row.ratio_points),
          minConsumablePoints: Number(row.min_consumable_points),
          maximumConsumablePoints: Number(row.maximum_consumable_points)
        });
      })
    }) : null;
    var products = data && data.loyalty && data.loyalty.products,
      rows = products && products.data;
    if (rows && !Array.isArray(rows)) rows = rows.eligible_products || rows.products || [];
    var tx = data && data.tx,
      transactions = tx && (Array.isArray(tx) ? tx : tx.data) || [];
    return {
      walletData: mapped,
      loyaltyProducts: (Array.isArray(rows) ? rows : []).map(function (p) {
        return Object.assign({}, p, {
          productItemId: p.itemId || p.product_id || p.id,
          name: p.label || p.name,
          imageUrl: p.image || p.imageUrl,
          pointsRequired: Number(p.points_required)
        });
      }),
      walletTransactions: transactions.map(function (t) {
        return Object.assign({}, t, {
          walletPoints: t.wallet_points,
          transactionType: t.transaction_type,
          createdAt: t.created_at,
          orderTrackingId: t.online_order_id || t.ticket_id,
          storePosOwnerId: t.restaurant_id
        });
      }),
      isLoading: !!uid && request.loading,
      refreshLoyalty: refresh
    };
  }
  function QR(p) {
    var code;
    try {
      code = OO.qrcode.qrPath(OO.qrcode.qrMatrix(String(p.value || ''), {
        ecc: 'M'
      }), {
        quiet: 1
      });
    } catch (e) {
      return null;
    }
    return <svg width={p.size || 96} height={p.size || 96} viewBox={'0 0 ' + code.size + ' ' + code.size} role="img" aria-label="Member QR code"><rect width="100%" height="100%" fill={p.bgColor || 'white'} /><path d={code.d || code.path} fill={p.fgColor || '#131316'} /></svg>;
  }
  function ItemBridge(p) {
    var r = current(),
      hotel = r.hotel,
      h = OO.useHotel(hotel),
      item = p.item || p.combo;
    if (!item || !h.store || !h.menu) return null;
    var id = String(item.productItemId || item.itemId || item.product_id || item.id || item.combo_id),
      reward = item.isLoyaltyAddedProduct || item.isWalletRedeemed;
    var body = p.combo ? <OO.pages.ComboBuilder hotel={hotel} store={h.store} menu={h.menu} comboId={id} onClose={p.onClose} onAdded={p.onSuccess} /> : <OO.pages.ItemSheet hotel={hotel} store={h.store} menu={h.menu} productId={id} reward={reward ? {
      row: item,
      orderType: (OO.store.get().checkout || {}).orderType || 'PICKUP',
      table: null
    } : undefined} onClose={p.onClose} onAdded={p.onSuccess} />;
    return <div className="oo-zeta-native">{body}</div>;
  }
  function walletScope(wallet, isApp) {
    var store = OO.data.peekHotel(key(), false),
      st = store && store.store;
    var rows = OO.wallet.rows(wallet),
      active = rows.filter(function (r) {
        return r.usable && r.orderType.indexOf(isApp ? 'MobileApp' : 'OnlineOrder') >= 0;
      });
    return {
      enabled: active.length > 0,
      available: active.length > 0,
      canRedeem: active.length > 0,
      reason: active.length ? null : 'NO_RULE',
      mode: OO.wallet.productLevel(st) ? 'product' : 'cart',
      productLevel: OO.wallet.productLevel(st),
      channels: active
    };
  }
  adapters[843476] = {
    jsx: jsx,
    jsxs: jsx,
    Fragment: React.Fragment
  };
  adapters[271645] = React;
  adapters[522016] = {
    default: Link
  };
  adapters[618566] = {
    useRouter: function () {
      return routerObject;
    },
    useSearchParams: function () {
      var r = current(),
        q = Object.assign({}, r.query || {});
      if ((q.type === 'product' || q.type === 'combo') && q.id) q[q.type === 'combo' ? 'combo' : 'item'] = 'view';
      if (r.params && r.params.orderId) q.id = r.params.orderId;
      if (r.params && r.params.itemId) {
        q.id = r.params.itemId;
        q[r.params.item === 'combo' ? 'combo' : 'item'] = r.params.itemSlug || 'view';
      }
      return new URLSearchParams(q);
    },
    usePathname: function () {
      return nativeHref('/' + current().page);
    }
  };
  adapters[88653] = {
    AnimatePresence: function (p) {
      return p.children || null;
    }
  };
  adapters[846932] = {
    motion: motion
  };
  adapters[772328] = {
    useReducedMotion: function () {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
  };
  adapters[456420] = {
    default: createIcon
  };
  adapters[990746] = {
    useAuth: useAuth
  };
  adapters[652348] = {
    useBranch: useBranch
  };
  adapters[238325] = {
    useLoyalty: useLoyalty
  };
  adapters[309808] = {
    useToast: useToast
  };
  adapters[651164] = {};
  adapters[247167] = {};
  adapters[273341] = {
    haptic: OO.platform.haptic,
    HAPTICS_PREFERENCE_KEY: 'oo-zeta-haptics-v1'
  };
  adapters[394556] = {
    default: {
      get name() { return (brand().name || 'Zeta').split(' ')[0]; },
      get displayName() {
        return brand().name || 'Zeta Greek Kitchen';
      },
      get storagePrefix() {
        return 'oo-zeta:' + (current().chain || key());
      },
      branches: branches,
      get socials() {
        var r = current(),
          s = OO.whitelabel.siteFor(r.chain || r.hotel),
          links = s && s.doc.landing && s.doc.landing.footer.socials || [];
        var out = {};
        links.forEach(function (l) {
          out[l.network] = l.url;
        });
        return out;
      }
    }
  };
  adapters[351969] = {
    get BUILD_LABEL() {
      return window.SipoVersion && window.SipoVersion.buildId();
    },
    checkForBuildUpdate: function () {
      return window.SipoVersion ? window.SipoVersion.check().then(function () {
        return {
          updateAvailable: window.SipoVersion.stale()
        };
      }) : Promise.reject(new Error('Version unavailable'));
    }
  };
  adapters[515746] = {
    getCurrentPosition: function () {
      return new Promise(function (resolve, reject) {
        navigator.geolocation ? navigator.geolocation.getCurrentPosition(function (p) {
          resolve({
            latitude: p.coords.latitude,
            longitude: p.coords.longitude
          });
        }, reject, { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }) : reject(new Error('Location unavailable'));
      });
    },
    haversineDistance: function (a, b, c, d) {
      return OO.model.haversineKm({
        lat: a,
        lng: b
      }, {
        lat: c,
        lng: d
      });
    }
  };
  adapters[999627] = {
    hasCompletedZetaOnboarding: function () {
      return OO.appPreferences.read().welcome !== true;
    },
    completeZetaOnboarding: function () {
      OO.appPreferences.set('welcome', false);
    },
    resetZetaOnboarding: function () {
      OO.appPreferences.set('welcome', true);
    },
    replayZetaOnboarding: function () {
      window.dispatchEvent(new Event('oo-zeta-replay-welcome'));
    }
  };
  adapters[657688] = {
    default: Image
  };
  adapters[977617] = {
    default: Image
  };
  adapters[377572] = {
    default: QR
  };
  adapters[528526] = {
    useZetaAppSurface: function () {
      return OO.mode === 'app';
    }
  };
  adapters[798909] = {
    useFlutterWebView: function () {
      return OO.platform.inShell();
    }
  };
  adapters[675378] = {
    summarizeRedemptionScope: walletScope,
    getDisplayWalletRatio: function (wallet, isApp, type) {
      var rule = OO.wallet.rule(wallet, {
        mode: OO.mode,
        isMobileApp: OO.mode === 'app',
        orderType: type || 'PICKUP'
      });
      return rule ? {
        ratioPoints: rule.ratio,
        maximumConsumablePoints: rule.max,
        minConsumablePoints: rule.min
      } : null;
    }
  };
  adapters[343699] = {
    default: ItemBridge
  };
  adapters[899907] = {
    default: ItemBridge
  };
  adapters[519957] = {
    branches: branches,
    resolveBranchForOrder: function (order) {
      return branches.filter(function (b) {
        return b.onlineKey === order.onlineKey || b.storeId === Number(order.storeId || order.hotelId);
      })[0] || null;
    }
  };
  adapters[98828] = {
    RatingForm: function (p) {
      return <OO.pages.Review route={Object.assign({}, current(), {
        params: {
          orderId: p.orderId
        }
      })} mode={OO.mode} />;
    }
  };
  adapters[987664] = {
    useCart: function () {
      var r = current(),
        h = OO.useHotel(r.hotel);
      return {
        addToCart: function (item) {
          navigate('/menu?' + (item.is_combo ? 'combo' : 'item') + '=view&id=' + encodeURIComponent(item.itemId || item.id));
        },
        isSuggestionPopupVisible: function () {
          return false;
        }
      };
    }
  };
  adapters[174080] = {
    createPortal: function (children, target) {
      return ReactDOM.createPortal(<div className="oo-zeta-native">{children}</div>, target);
    }
  };
  adapters[341861] = {
    calculateCartLineTotal: function (line) {
      return line._oo ? OO.cart.lineTotal(line._oo) : 0;
    }
  };
  adapters[498624] = {
    autocompleteAddress: function (text) {
      return OO.delivery.load().then(function (g) {
        return new Promise(function (resolve, reject) {
          new g.places.AutocompleteService().getPlacePredictions({
            input: text,
            componentRestrictions: {
              country: (OO.data.peekHotel(key(), false)?.store?.timezone || '').indexOf('Australia/') === 0 ? 'au' : 'nz'
            },
            types: ['address']
          }, function (rows, status) {
            if (status !== g.places.PlacesServiceStatus.OK && status !== g.places.PlacesServiceStatus.ZERO_RESULTS) {
              reject(new Error('Address search is unavailable right now.'));
              return;
            }
            resolve({
              status: 'success',
              predictions: (rows || []).map(function (p) {
                var f = p.structured_formatting || {};
                return Object.assign({}, p, {
                  main_text: f.main_text || p.description,
                  secondary_text: f.secondary_text || ''
                });
              })
            });
          });
        });
      });
    },
    getPlaceDetails: function (id) {
      return OO.delivery.load().then(function (g) {
        return new Promise(function (resolve, reject) {
          new g.places.PlacesService(document.createElement('div')).getDetails({
            placeId: id,
            fields: ['place_id', 'formatted_address', 'geometry', 'address_components']
          }, function (place, status) {
            if (status !== g.places.PlacesServiceStatus.OK) {
              reject(new Error('Address could not be loaded.'));
              return;
            }
            var value = OO.delivery.parsePlace(place);
            resolve({
              status: 'success',
              place: {
                formatted_address: value.line,
                lat: value.lat,
                lng: value.lng,
                city: value.city,
                postcode: value.postcode
              }
            });
          });
        });
      });
    }
  };
  adapters[987664] = {
    useCart: function () {
      var r = current(),
        h = OO.useHotel(r.hotel),
        cart = OO.useStore(function (s) {
          return s.cart;
        }),
        lines = cart.lines || [],
        ctx = React.useContext(Context),
        moment = ctx.moment || {};
      var suggestions = OO.useAsync(function () {
        return r.hotel ? OO.upsell.cartSuggestions(r.hotel, lines) : Promise.resolve([]);
      }, [r.hotel, OO.upsell.cartIds(lines).join(',')]);
      var inCart = {};
      lines.forEach(function (l) {
        if (l.itemId) inCart[l.itemId] = true;
      });
      var extras = OO.upsell.resolve(suggestions.data || [], h.menu, {
        moment: moment,
        inCart: inCart,
        excluded: OO.upsell.excluded(r.hotel, null),
        limit: 8
      }).filter(function (x) {
        return x.addable;
      }).map(function (x) {
        var p = x.product;
        return {
          productItemId: p.id,
          itemId: p.id,
          name: p.name,
          label: p.name,
          salePrice: p.sizes && p.sizes[0] ? p.sizes[0].price : p.price,
          imageUrl: p.image,
          sizes: p.sizes || [],
          groups: x.options ? [{
            addOns: [{
              id: 'native-options'
            }]
          }] : []
        };
      });
      var issues = h.menu ? OO.pages.Cart.shared.lineIssues(lines, h.menu, {
        at: moment.at || moment.now
      }) : {};
      function pick(item) {
        window.dispatchEvent(new CustomEvent('oo-zeta-edit-item', {
          detail: {
            id: item.itemId || item.productItemId || item.id,
            kind: item.is_combo ? 'combo' : 'item'
          }
        }));
      }
      var rows = lines.map(function (line) {
        var product = h.menu && (line.kind === 'combo' ? h.menu.combos[line.comboId] : h.menu.products[line.itemId]);
        return {
          _oo: line,
          cart_item_id: line.key,
          itemId: line.itemId,
          combo_id: line.comboId,
          is_combo: line.kind === 'combo',
          label: line.name,
          name: line.name,
          combo_name: line.kind === 'combo' ? line.name : null,
          image: line.image || product && product.image,
          count: line.qty,
          size: line.sizeId,
          sizes: product && product.sizes || [],
          modifiers: String(OO.cart.displayMods(line) || '').split(' · ').filter(Boolean).map(function (m) {
            return {
              addOnName: m,
              count: 1
            };
          }),
          isLoyaltyAddedProduct: !!line.reward,
          points_required: line.reward && line.reward.points,
          special_instruction_hide: product && product.notesHidden ? 1 : 0
        };
      });
      return {
        cart: rows,
        cartTotal: OO.cart.subtotal(lines),
        cartCount: OO.cart.count(lines),
        cartSuggestions: extras,
        unavailableCartItems: rows.filter(function (row) {
          return !!issues[row.cart_item_id];
        }).map(function (row) {
          return {
            line: row,
            reason: issues[row.cart_item_id].text
          };
        }),
        removeFromCart: OO.store.cartRemove,
        updateQuantity: OO.store.cartSetQty,
        markSuggestionSeen: function () {},
        openCustomizer: pick,
        startEditingCartItem: function (item) {
          window.dispatchEvent(new CustomEvent('oo-zeta-edit-item', {
            detail: {
              line: item.cart_item_id,
              kind: item.is_combo ? 'combo' : 'item',
              id: item.itemId || item.combo_id
            }
          }));
        },
        clearCart: function () {
          OO.store.cartClear();
        },
        setIsCartOpen: function (on) {
          window.dispatchEvent(new CustomEvent('oo-zeta-cart-open', {
            detail: !!on
          }));
        },
        addToCart: function (item) {
          if (item._ooLine) {
            OO.store.cartAdd(item._ooLine);
            return;
          }
          var product = h.menu && h.menu.products[String(item.itemId || item.productItemId || item.product_id || item.id)];
          if (!product) {
            pick(item);
            return;
          }
          var initial = OO.modifiers.initial(product),
            sid = initial && initial.sizeId !== undefined ? initial.sizeId : product.sizes[0] && product.sizes[0].id || null,
            sel = OO.modifiers.defaults(product, sid);
          if (OO.upsell.ageGated(product, h.menu, r.hotel, h.store) || !OO.modifiers.validate(OO.modifiers.visibleGroups(product, sid), sel).ok) {
            pick(item);
            return;
          }
          OO.store.cartAdd(OO.cart.itemLine(product, {
            sizeId: sid,
            sel: sel,
            qty: item.count || 1
          }));
        },
        isSuggestionPopupVisible: function () {
          return false;
        }
      };
    }
  };
  adapters[84122] = {
    reorderItems: function (order, rawMenu, collect) {
      var h = OO.data.peekHotel(key(), true),
        menu = h && h.menu,
        raw = order._native || order.rawData || order,
        native = OO.model.normalizeOrders({
          data: [raw]
        }, {
          now: OO.data.now()
        })[0] || OO.model.normalizeOnlineOrders({
          status: true,
          data: {
            data: [raw]
          }
        }, {
          now: OO.data.now()
        })[0];
      if (!native || !menu) return {
        added: 0,
        skipped: order.items || []
      };
      var result = OO.cart.fromOrder(native, menu, {
        now: OO.data.now()
      });
      result.lines.forEach(function (line) {
        collect({
          _ooLine: line
        }, {});
      });
      return {
        added: result.lines.length,
        skipped: result.skipped
      };
    }
  };
  K.useZetaFavourites = function () {
    var f = requireView(154484),
      s = React.useState(f.getFavoriteProducts);
    React.useEffect(function () {
      var reload = function () {
        s[1](f.getFavoriteProducts());
      };
      window.addEventListener(f.ZETA_FAVORITES_CHANGED_EVENT, reload);
      window.addEventListener('storage', reload);
      return function () {
        window.removeEventListener(f.ZETA_FAVORITES_CHANGED_EVENT, reload);
        window.removeEventListener('storage', reload);
      };
    }, []);
    return s[0];
  };
  K.ZetaCartDrawer = function (p) {
    var Component = requireView(901100).default;
    return <Provider route={p.route}><Component isOpen={p.open} onClose={p.onClose} /></Provider>;
  };
  adapters[116160] = {
    usePricetag: function () {
      var ctx = React.useContext(Context),
        h = ctx.native;
      return {
        getProductPrice: function (id) {
          var p = h.menu && h.menu.products[String(id)];
          return p ? Number(p.sizes && p.sizes[0] ? p.sizes[0].price : p.price) : null;
        },
        getComboPrice: function (id) {
          var p = h.menu && h.menu.combos[String(id)];
          return p ? Number(p.price) : null;
        },
        activePricetag: null
      };
    }
  };
  adapters[153755] = {
    useAvailability: function () {
      var ctx = React.useContext(Context),
        h = ctx.native,
        m = ctx.moment || {},
        r = current(),
        draft = OO.store.get().checkout || {};
      function status(item, kind) {
        if (!h.menu) return {
          show: 'normal'
        };
        var id = String(item.itemId || item.product_id || item.combo_id || item.categoryId || item.id),
          entity = kind === 'combo' ? h.menu.combos[id] : kind === 'category' ? h.menu.categories.filter(function (c) {
            return String(c.id) === id;
          })[0] : h.menu.products[id];
        return entity ? OO.model.displayState(h.menu, entity, m) : {
          show: 'normal'
        };
      }
      return {
        isItemAvailable: function (item) {
          return status(item).show === 'normal';
        },
        isCategoryAvailable: function (item) {
          return status(item, 'category').show === 'normal';
        },
        isComboAvailable: function (item) {
          return status(item, 'combo').show === 'normal';
        },
        isComboCategoryAvailable: function (category) {
          return status(category, 'category').show === 'normal';
        },
        nextAvailableTime: function (item) {
          return status(item).label || null;
        },
        unavailabilityReason: function (item) {
          return status(item).label || null;
        },
        orderType: draft.orderType || 'PICKUP',
        nzNow: m.now || OO.data.now(),
        timeZone: h.store && h.store.timezone,
        isKiosk: false,
        isTableSide: false
      };
    }
  };
  var ZETA_INFO = {
    catering: 604388,
    franchise: 709696,
    about: 807420,
    help: 555125,
    contact: 747764,
    allergen: 196148,
    terms: 357217,
    privacy: 803929
  };
  ui.ZetaTabBar = function (p) {
    var r = p.route,
      s = OO.useStore(function (st) {
        return st.session;
      }),
      signedIn = !!(s && s.customer),
      R = requireView;
    var tabs = [['home', 'Home', 902434, 'default'], ['menu', 'Menu', 856423, 'BookOpen'], ['deals', 'Zeta Zone', 328623, 'Sparkles'], ['orders', 'Orders', 253682, 'Receipt'], ['stores', 'Stores', 248510, 'MapPinned'], ['profile', 'Account', 508619, 'CircleUserRound']];
    var active = ['wallet', 'rewards', 'coupons'].indexOf(r.page) >= 0 ? 'deals' : ['account', 'zeta-addresses'].indexOf(r.page) >= 0 ? 'profile' : r.page;
    return <nav className="oo-tabbar oo-zeta-tabs" aria-label="Main">{tabs.map(function (t) {
        var Icon = R(t[2])[t[3]],
          page = !signedIn && t[0] === 'orders' ? 'profile' : t[0],
          query = page === 'profile' && t[0] === 'orders' ? {
            returnTo: OO.router.href('orders', null, {
              hotel: r.hotel
            })
          } : {};
        var link = OO.router.link(page, null, {
          hotel: r.hotel,
          query: query
        });
        return <a key={t[0]} className="oo-tabbar__item" href={link.href} onClick={link.onClick} aria-current={active === t[0] ? 'page' : undefined} aria-label={t[1] + (!signedIn && ['orders', 'profile'].indexOf(t[0]) >= 0 ? ' — sign in required' : '')}><Icon size={22} /><span className="oo-tabbar__label">{t[1]}</span></a>;
      })}</nav>;
  };
  K.ZetaReferenceScreen = function ZetaReferenceScreen(p) {
    var Component = requireView(p.id).default;
    React.useEffect(function () {
      var title = {
        home: 'Home',
        menu: 'Menu',
        deals: 'Zeta Zone',
        profile: 'Your account',
        settings: 'Settings',
        wallet: 'Member card',
        rewards: 'Rewards',
        coupons: 'Coupons',
        orders: 'Your orders',
        stores: 'Our stores',
        track: 'Track your order',
        'zeta-addresses': 'Saved addresses'
      }[p.route.page] || p.route.params && p.route.params.slug || 'Zeta';
      var themed = OO.whitelabel.siteFor(p.route.chain || p.route.hotel);
      var page = p.route.page === 'app-content' && themed && (themed.doc.landing.pages || []).find(function(page){return page.slug === p.route.params.slug;});
      document.title = page && page.seo && page.seo.title || (p.route.page === 'home' ? (brand().name || 'Zeta Greek Kitchen') + ' | Souvlaki, Gyros & Greek Street Food' : title + ' | ' + (brand().name || 'Zeta Greek Kitchen'));
    }, [p.route.page, p.route.params && p.route.params.slug]);
    return <Provider route={p.route}><Component /></Provider>;
  };
  K.ZetaReferenceScreen.info = ZETA_INFO;
  K.ZetaReferenceScreen.Provider = Provider;
  K.ZetaReferenceScreen.Link = Link;
  K.ZetaReferenceScreen.nativeHref = nativeHref;
})();


// ===================== src/pages/zeta-reference-brand.jsx =====================
// Source Zeta store presentation; branch keys are always verified against the live chain.
(function () {
  'use strict';

  window.OO.zetaReference.define(901002, function (e) {
    e.s(["locations", 0, [{
      id: "mission-bay",
      name: "Mission Bay",
      address: "Eastridge shopping centre Shop SU03G, Ground Floor / Kepa Road Mission Bay",
      lat: -36.8608069,
      lng: 174.8296347,
      status: "open",
      onlineKey: "zeta-greek-kitchen",
      storeId: 1142,
      phone: "09 281 0278",
      countryCode: "+64",
      mapLink: "https://maps.app.goo.gl/Y7TEyi5WHk7Lh1q67",
      tags: ["Pickup", "Catering"]
    }, {
      id: "elliott-stables",
      name: "Elliott Stables (CBD)",
      address: "Elliott Stables, Elliot Street, Auckland",
      lat: -36.8503609,
      lng: 174.7635609,
      status: "open",
      onlineKey: "zeta-greek",
      storeId: 1368,
      phone: "09 930 4352",
      countryCode: "+64",
      tags: ["Pickup", "Catering"]
    }, {
      id: "dress-smart",
      name: "Dresssmart",
      address: "151 Arthur Street, Onehunga, Auckland 1061",
      lat: -36.9207849,
      lng: 174.7865561,
      status: "open",
      onlineKey: "zeta-dress-mart",
      storeId: 1791,
      phone: "09 971 2474",
      countryCode: "+64",
      tags: ["Pickup"]
    }, {
      id: "newmarket",
      name: "Westfield Newmarket",
      address: "level 3/309 Broadway, Newmarket, Auckland 1023, New Zealand",
      lat: -36.8721531,
      lng: 174.7767527,
      status: "open",
      onlineKey: "zeta-greek-newmarket",
      storeId: 2016,
      phone: "021 426 155",
      countryCode: "+64",
      tags: ["Pickup", "Catering"],
      notes: "Parking: Mon–Fri 3h free; Sat–Sun 2h free with Westfield Plus app"
    }, {
      id: "town-hall-sydney",
      name: "Town Hall (Sydney)",
      address: "shop 23a Town Hall arcade 464-480 Kent Street Sydney CBD NSW 2000",
      status: "open",
      onlineKey: "zeta-greek-town-hall",
      storeId: 2105,
      phone: "+64 21 426 155",
      timezone: "Australia/Sydney",
      countryCode: "+61",
      tags: ["Pickup"]
    }, {
      id: "beaumont-st",
      name: "Beaumont Street (CBD)",
      address: "100 Beaumont Street, Auckland CBD, Auckland 1010, New Zealand",
      lat: -36.8443535,
      lng: 174.7530668,
      status: "open",
      onlineKey: "zeta-greek-beaumount",
      storeId: 2111,
      phone: "+64 21 426 155",
      countryCode: "+64",
      tags: ["Pickup"]
    }]]);
  });
})();


// ===================== src/pages/zeta-native-services.jsx =====================
// Adapters for the imported read-only Zeta view models. All writes go through native profile/order engines.
(function () {
  'use strict';

  var OO = window.OO,
    Z = OO.zetaReference,
    A = Z.adapters;
  function pathOf(path) {
    return String(path).replace(/^https?:\/\/[^/]+\/.*?\/api\//, '').replace(/^\/+/, '');
  }
  async function read(path, body, method) {
    var path = pathOf(path),
      endpoint = path.split('?')[0];
    if ((endpoint === 'get_customer_orders' || endpoint === 'customer/getOrders') && OO.router.current().hotel) await OO.data.loadHotel(OO.router.current().hotel);
    if (endpoint === 'get_menu') return OO.api.getMenu(body.id, {
      withRedemptions: false
    });
    return method === 'GET' ? OO.http.get(path, {
      query: body || {},
      fresh: true
    }) : OO.http.post(path, body || {}, {
      fresh: true
    });
  }
  A[917375] = {
    sipoFetch: read,
    sipoFetchWithReadFallback: read,
    sipoReadCacheKey: function (path, body) {
      return path + JSON.stringify(body || {});
    },
    readThroughSipoCache: function (k, fn) {
      return Promise.resolve().then(fn);
    }
  };
  A[887262] = {
    ORDERS_API_BASE_URL: null,
    READ_FALLBACK_API_BASE_URLS: [],
    SIPO_READ_FALLBACK_API_BASE_URLS: []
  };
  A[109258] = {
    isActiveTableSideSession: function () {
      return false;
    }
  };
  A[98823] = {
    isKioskHost: function () { return false; },
    isKioskMode: function () {
      return false;
    },
    getKioskOrderType: function () {
      return null;
    }
  };
  Z.submitEnquiry = function (path, options) {
    if (path !== '/api/leads') return Promise.reject(new Error('Unknown form'));
    var r = OO.router.current(),
      site = OO.whitelabel.siteFor(r.chain || r.hotel),
      page = site && site.doc.landing && site.doc.landing.pages.filter(function (p) {
        return p.slug === 'franchise';
      })[0],
      section = page && page.sections.filter(function (s) {
        return s.enabled && s.type === 'franchise';
      })[0];
    if (!section || OO.whitelabel.isPreview()) return Promise.resolve({
      ok: false,
      json: function () {
        return Promise.resolve({
          ok: false
        });
      }
    });
    var value = JSON.parse(options.body || '{}'),
      fields = {
        name: value.name,
        email: value.email,
        phone: value.phone,
        message: 'Preferred location: ' + String(value.location || '').trim() + '\nInvestment budget: ' + String(value.budget || 'Not specified') + '\n\nHospitality or business experience:\n' + String(value.experience || 'Not provided')
      };
    return OO.landing.submit(r.chain || r.hotel, Object.assign(OO.landing.body(section.id, fields), {
      surface: 'app'
    })).then(function (result) {
      return {
        ok: result.ok,
        json: function () {
          return Promise.resolve({
            ok: result.ok,
            message: result.message,
            errors: result.errors
          });
        }
      };
    });
  };
  A[910000] = {
    getCoupons: function (id) {
      return OO.api.getCoupons(Number(id));
    },
    createCustomer: function (fields) {
      var customer = OO.store.session() && OO.store.session().customer;
      if (!customer || customer.uid !== fields.firebaseUID) return Promise.resolve({
        success: false,
        error: 'Please sign in again.'
      });
      var body = {
        name: String(fields.name || '').trim(),
        phone_no: fields.phone || null,
        birthday: fields.birthday || null
      };
      if (fields.addresses) body.address = OO.pages.Addresses.logic.wire(fields.addresses.map(function (a) {
        return {
          uid: a.addressUid,
          type: ['HOME', 'WORK'].indexOf(String(a.label).toUpperCase()) >= 0 ? String(a.label).toUpperCase() : 'OTHER',
          label: ['HOME', 'WORK', 'OTHER'].indexOf(String(a.label).toUpperCase()) < 0 ? a.label : null,
          address: a.line1,
          city: a.city,
          zipcode: a.zipcode,
          lat: a.latitude,
          lng: a.longitude,
          selected: a.isSelected
        };
      }));
      return OO.pages.Profile.saveProfile(OO.router.current().hotel, body).then(function (result) {
        return {
          success: true,
          status: true,
          data: result.customer
        };
      }, function (e) {
        return {
          success: false,
          error: OO.pages.Profile.logic.saveError(e).text || 'Could not save your profile.'
        };
      });
    },
    deleteAccount: function () {
      return Promise.resolve({
        success: false,
        error: 'Account deletion is not available from this public SIPO endpoint. Please contact Zeta to request account deletion.'
      });
    }
  };
})();


// ===================== src/pages/zeta-native-reads.jsx =====================
// Read-only Zeta catalogue and order presentation mapping, captured 2026-10-19.
// Network calls route through the existing OO HTTP adapter. No checkout or account-write implementation is copied.
(function () {
  'use strict';var Z=window.OO.zetaReference; Z.define(917848, function(e){
var t = e.i(917375);
var r = e.i(887262);
var n = e.i(566450);
var d = e.i(98823);
var c = e.i(109258);
var p = "Pacific/Auckland";
var f = "/coupon_get_v4";
function y(e) {
  return String(e || "").trim() || p;
}
function g(e) {
  let t = Number(e?.httpStatus);
  return 404 === t || 405 === t || 501 === t;
}
function b(e) {
  return !0 === e || 1 === e || "1" === e || "true" === e || "success" === e;
}
function h(e) {
  if (null == e || "" === e) return 0;
  let t = Number(e);
  return Number.isFinite(t) ? t : 0;
}
function v(e) {
  if (e && "object" == typeof e) return e;
  if ("string" == typeof e) try {
    return JSON.parse(e);
  } catch {}
  return e;
}
function A(e) {
  if (!e || "object" != typeof e) return null;
  let t = v(e.rules),
    r = v(e.result),
    i = t && "object" == typeof t && !Array.isArray(t) ? {
      ...t
    } : {},
    a = r && "object" == typeof r && !Array.isArray(r) ? {
      ...r
    } : {},
    o = e => {
      let t = v(e);
      return Array.isArray(t) ? t : [];
    },
    l = o(e.applicable_products),
    s = o(e.result_products),
    u = e => {
      let t = e && "object" == typeof e ? e.itemId ?? e.product_id ?? e.productId ?? e.id : e;
      if (null == t || "" === t) return null;
      let r = Number(t);
      return Number.isFinite(r) ? r : String(t);
    },
    d = e => e.map(e => {
      let t = u(e);
      if (null === t) return null;
      let r = o(e?.sizes).map(e => u(e?.size_id ?? e?.sizeId ?? e?.id ?? e)).filter(e => null !== e);
      return r.length ? {
        id: t,
        sizes: r
      } : {
        id: t
      };
    }).filter(Boolean),
    c = v(e.items),
    m = v(e.parseditems),
    p = (Array.isArray(m) ? m : Array.isArray(c) ? c : l).map(u).filter(e => null !== e);
  return Array.isArray(i.APPLICABLE_ITEMS) && 0 !== i.APPLICABLE_ITEMS.length || (i.APPLICABLE_ITEMS = d(l)), Array.isArray(a.RESULTS_ITEMS) && 0 !== a.RESULTS_ITEMS.length || (a.RESULTS_ITEMS = d(s)), {
    ...e,
    id: Number(e.id || 0),
    code: String(e.code ?? e.coupon_code ?? "").trim(),
    rules: i,
    result: a,
    items: Array.isArray(c) ? c : null,
    parseditems: p,
    availability: (0, n.normalizeAvailabilityRules)(v(e.availability) ?? e.availability),
    products: l.length ? l : o(e.products),
    applicable_products: l,
    result_products: s
  };
}
function P(e) {
  let t = v(e);
  return Array.isArray(t) ? t.flatMap(e => Array.isArray(e) ? e : e ? [e] : []) : t && "object" == typeof t ? ["orderItems", "comboDetails", "product_id", "productItemId", "itemId", "productName", "productname"].some(e => Object.prototype.hasOwnProperty.call(t, e)) ? [t] : Object.values(t).flatMap(e => Array.isArray(e) ? e : e && "object" == typeof e ? [e] : []) : [];
}
function N(e) {
  return !0 === e || 1 === e || "1" === e || "true" === e;
}
function C(e, t, r) {
  let i = parseFloat(String(t.discount ?? 0)),
    n = t.coupon_type || t.couponType || t.couponDetail?.rules?.type || t.couponDetail?.type || (i > 0 ? "CART_DISCOUNT" : null),
    a = String(n || "").toUpperCase(),
    o = "FREE_DELIVERY" === a,
    l = "CART_DISCOUNT" === a,
    s = P(e).map(e => {
      let t = Math.max(1, parseFloat(String(e.product_count ?? e.count ?? 1)) || 1),
        i = P(e.modifiersDetail || e.modifiers).map(e => {
          let t = Math.max(1, parseFloat(String(e.modifier_count ?? e.count ?? 1)) || 1),
            r = parseFloat(String(e.price ?? 0)) || 0;
          return {
            addOnId: e.addOnId ?? e.modifier_id ?? e.id ?? null,
            groupId: e.groupId ?? e.group_id ?? null,
            addOnName: e.addOnName || e.modifier || e.name || "",
            price: r,
            count: t
          };
        }),
        n = parseFloat(String(e.temp_rate ?? e.price ?? 0)) || 0,
        a = N(e.isWalletRedeemed ?? e.is_wallet_redeemed),
        s = N(e.isCouponApplied ?? e.is_coupon_applied) && !a,
        u = parseFloat(String(e.rate_of_discount ?? e.discount ?? 0)) || 0,
        d = +(n * t).toFixed(2),
        c = +i.reduce((e, t) => e + t.price * t.count, 0).toFixed(2),
        m = +(d + c).toFixed(2),
        p = s && u > 0 ? +(d * u / 100).toFixed(2) : null,
        _ = String(e.productName || e.product_name || e.productname || ""),
        f = o && /\bdelivery\s*fee/i.test(_) && !a,
        y = N(e.is_free_item ?? e.isFreeItem) || s && null != p && p >= d && d > 0 || f,
        g = e.coupon_discount_amount ?? e.couponDiscountAmount,
        b = (null == g ? null : Math.max(0, parseFloat(String(g)) || 0)) ?? (f || y ? d : p),
        h = !l && (s || y) ? Math.min(m, Math.max(0, Number(b) || 0)) : 0,
        S = +Math.max(0, m - h).toFixed(2),
        I = {
          productItemId: e.product_id ?? e.productItemId ?? e.productId ?? e.product_item_id ?? e.item_id ?? e.itemId ?? e.id ?? null,
          productName: e.productName || e.product_name || e.productname || window.OO.zetaReference.productName(e.product_id ?? e.productItemId ?? e.itemId ?? e.id) || "",
          count: t,
          price: n,
          baseTotal: d,
          modifierTotal: c,
          lineTotal: S,
          size: Array.isArray(e.size) ? e.size[0]?.size_name ?? null : "string" == typeof e.size ? e.size : e.size ?? null,
          modifiers: i,
          kitchen_note: e.kitchen_note || "",
          extraPrice: parseFloat(e.extra_price || 0),
          isWalletRedeemed: a,
          walletAmount: parseFloat(e.walletAmount || 0),
          walletDiscount: parseFloat(e.walletDiscount || 0),
          walletType: e.walletType ?? null,
          walletPointsUsed: parseFloat(e.walletUsedForProduct || 0) || 0,
          priceTagApplied: e.is_price_tag_applied || !1,
          priceTagId: e.price_tag_id ?? null,
          isCouponApplied: s || f,
          isFreeItem: y,
          couponDetail: e.couponDetail || null,
          couponDiscountAmount: b,
          originalPrice: e.original_price ?? e.originalPrice ?? n
        };
      return "ordersDetail" === r ? {
        ...I,
        lineItemUid: e.order_id,
        kitchenNote: e.kitchen_note || "",
        sizeId: null != e.size_id && Number(e.size_id) > 0 ? Number(e.size_id) : null,
        isCombo: N(e.isCombo ?? e.is_combo) || null != e.combo_id,
        comboId: e.combo_id ?? e.comboId ?? null
      } : I;
    }),
    u = s.filter(e => e.isCouponApplied),
    d = s.filter(e => !e.isWalletRedeemed);
  return u.length > 0 && u.length === d.length && s.forEach(e => {
    e.isCouponApplied = !1;
  }), {
    items: s,
    couponType: n
  };
}
function k(e, t, r) {
  var i;
  let n,
    a,
    o,
    l = P(e?.nonComboOrders),
    {
      flatItems: s,
      nestedGroups: u,
      rawChildren: d
    } = (i = e?.comboOrders, n = [], a = [], o = [], P(i).forEach(e => {
      if (!Object.prototype.hasOwnProperty.call(e || {}, "orderItems")) {
        n.push(e), o.push(e);
        return;
      }
      let t = v(e.comboDetails) || {},
        r = P(e.orderItems),
        i = Math.max(1, parseFloat(String(t.count ?? e.count ?? 1)) || 1);
      a.push({
        comboName: t.combo_name ?? t.comboName ?? e.combo_name ?? e.comboName ?? "Combo",
        comboPrice: parseFloat(String(t.comboPrice ?? t.combo_price ?? e.comboPrice ?? e.combo_price ?? 0)) || 0,
        comboDescription: t.description ?? e.description ?? null,
        comboId: t.combo_id ?? e.combo_id ?? r[0]?.combo_id ?? null,
        count: i,
        children: r
      }), o.push(...r);
    }), {
      flatItems: n,
      nestedGroups: a,
      rawChildren: o
    }),
    c = [...l, ...s, ...d],
    {
      items: m,
      couponType: p
    } = C([...l, ...s], t, r);
  return {
    rawItems: c,
    items: [...m, ...u.map(e => {
      let i = C(e.children, t, r).items,
        n = +(e.comboPrice * e.count).toFixed(2);
      return {
        lineItemUid: e.children[0]?.order_id ?? null,
        productItemId: null,
        productName: e.comboName,
        count: e.count,
        price: e.comboPrice,
        baseTotal: n,
        modifierTotal: 0,
        lineTotal: n,
        size: null,
        modifiers: [],
        kitchenNote: "",
        kitchen_note: "",
        extraPrice: 0,
        isCombo: !0,
        comboId: e.comboId,
        comboName: e.comboName,
        comboDescription: e.comboDescription,
        comboItems: i,
        isWalletRedeemed: !1,
        walletAmount: 0,
        walletDiscount: 0,
        walletType: null,
        walletPointsUsed: 0,
        priceTagApplied: !1,
        priceTagId: null,
        isCouponApplied: !1,
        isFreeItem: !1,
        couponDetail: null,
        couponDiscountAmount: null,
        originalPrice: e.comboPrice
      };
    })],
    couponType: p
  };
}
function O(e) {
  return String(e?.deliveryType ?? e?.delivery_type ?? e?.deliver_type ?? "").trim();
}
function D(e) {
  return "SCHEDULED" === O(e).toUpperCase() ? e?.scheduledTime ?? e?.scheduled_time ?? e?.deliver_time ?? null : null;
}
function R(e) {
  return String((e?.timezone ?? e?.timeZone ?? e?.hotel_timezone ?? e?.store_timezone ?? e?.restaurant_timezone) || "").trim() || null;
}
function L(e, t) {
  let r = P(e),
    i = r.filter(e => N(e.isWalletRedeemed ?? e.is_wallet_redeemed)),
    n = +i.reduce((e, t) => e + parseFloat(String(t.walletAmount || 0)), 0).toFixed(2),
    a = parseFloat(String(t.discount ?? 0)),
    o = t.billTotal ?? t.bill_total,
    l = null != o && "" !== o ? parseFloat(String(o)) : parseFloat(String(t.subtotal || t.total || 0)),
    s = parseFloat(String(t.surcharge ?? 0)) || 0,
    u = s > 0 ? +(l - l / (1 + s / 100)).toFixed(2) : 0,
    d = +(+(l - u).toFixed(2) + n).toFixed(2),
    c = a > 0 && a < 100 ? +(d / (1 - a / 100)).toFixed(2) : d,
    m = +(c - d).toFixed(2);
  return {
    walletAmount: n,
    isWalletRedeemed: n > 0,
    walletType: i.length ? i[0].walletType ?? null : null,
    walletPointsUsed: r.reduce((e, t) => e + (parseFloat(String(t.walletUsedForProduct || 0)) || 0), 0),
    computedTotal: l,
    displaySubtotal: c,
    discountAmount: m,
    discountPercent: a,
    tax: parseFloat(String(t.tax || 0)),
    surcharge: u,
    surchargePercent: s,
    surchargeLabel: t.surcharge_label || "Surcharge",
    deliveryCharge: parseFloat(String(t.deliverycharge ?? t.delivery_charge ?? t.deliveryCharge ?? 0)) || 0,
    gratuity: parseFloat(String(t.gratuity || 0))
  };
}
async function U(e, i, n = 1) {
  if (!e || !i) return {
    success: !1,
    error: "firebaseUID and storeOnlineKey are required"
  };
  let a = await (0, t.sipoFetch)(`/get_customer_orders?page=${n}`, {
    uid: e,
    online_key: i
  }, "POST", {}, r.ORDERS_API_BASE_URL);
  if (!Array.isArray(a.data)) return {
    success: !1,
    error: "Failed to fetch order history"
  };
  let o = R(a);
  return {
    success: !0,
    data: {
      data: a.data.map(e => function (e) {
        let {
            rawItems: t,
            items: r,
            couponType: i
          } = k(e.ordersDetail || {}, e, "ordersDetail"),
          n = L(t, e),
          a = O(e),
          o = R(e);
        return {
          _native: e,
          orderTrackingId: e.ticket_id,
          createdAt: e.created_at,
          orderStatus: e.orderStatus || "",
          orderState: e.order_state || null,
          ticketStatus: e.ticket_status || "",
          onlinePaymentStatus: e.paymentStatus || e.payment_status || "",
          paymentType: e.payment_type || "",
          subtotal: String(n.displaySubtotal),
          total: String(n.computedTotal),
          discount: n.discountAmount,
          discountPercent: n.discountPercent,
          discountReason: e.discount_reason || null,
          walletAmount: n.walletAmount,
          isWalletRedeemed: n.isWalletRedeemed,
          walletType: n.walletType,
          walletPointsUsed: n.walletPointsUsed,
          cashDue: n.computedTotal,
          tax: n.tax,
          surcharge: n.surcharge,
          surchargePercent: n.surchargePercent,
          surchargeLabel: n.surchargeLabel,
          deliveryCharge: n.deliveryCharge,
          gratuity: n.gratuity,
          shortId: e.short_id || null,
          pagerNo: e.pager_no ?? null,
          orderType: e.ticket_type || e.order_type || "",
          deliveryType: a,
          scheduledTime: D(e),
          scheduledTimeRaw: e.scheduledTime || e.scheduled_time || e.deliver_time || null,
          customerAddress: e.customer_address || null,
          customerName: e.customer_name || null,
          customerPhone: e.customer_phone || null,
          instructions: e.note || null,
          storeUserId: e.user_id ?? null,
          onlineKey: e.hotelKey || e.online_key || e.onlineKey || null,
          hotelName: e.hotelName || e.name || e.hotel_name || null,
          hotelAddress: e.address || e.hotel_address || null,
          timezone: o,
          timeZone: o,
          couponDetail: e.couponDetail || null,
          isCouponApplied: e.isCouponApplied || !1,
          couponType: i,
          couponDiscountAmount: e.coupon_discount_amount ?? e.couponDiscountAmount ?? null,
          rating: e.rating ?? null,
          feedback: e.feedback ?? null,
          items: r,
          id: e.ticket_id,
          order_id: e.ticket_id,
          lineItems: r,
          orderData: r
        };
      }(o && !R(e) ? {
        ...e,
        timezone: o
      } : e)),
      currentPage: a.current_page,
      lastPage: a.last_page,
      total: a.total,
      perPage: a.per_page
    }
  };
}
function G(e) {
  if (Array.isArray(e)) return {
    usable: !0,
    rows: e
  };
  if (!e || "object" != typeof e) return {
    usable: !1,
    rows: []
  };
  let t = Array.isArray(e.data) ? e.data : null,
    r = e.status;
  return {
    usable: null !== t && (void 0 === r || b(r)),
    rows: t || []
  };
}
async function Y(e, r = {}) {
  let i = {
      user_id: Number(e),
      ...(r.tableSide ? {
        is_table_order: !0
      } : {})
    },
    n = r.kiosk ? ["/coupon_get_kiosk"] : [f, "/coupon_get"],
    a = (0, t.sipoReadCacheKey)(n[0], {
      ...i,
      kiosk: !!r.kiosk
    }),
    o = await (0, t.readThroughSipoCache)(a, async () => {
      for (let e = 0; e < n.length; e += 1) {
        let r = n[e];
        try {
          return await (0, t.sipoFetch)(r, i, "GET");
        } catch (e) {
          if (r !== f || !g(e)) throw e;
        }
      }
      throw Error("Coupon catalogue unavailable");
    }, {
      canCache: e => G(e).usable
    }),
    l = G(o);
  return l.usable ? l.rows : [];
}
function j(e) {
  return !!(Array.isArray(e?.data) && e.data.length > 0 || Array.isArray(e?.categories) && e.categories.length > 0);
}
async function W(e, r = {}) {
  let i = y(r.timeZone),
    n = r.tableSide ?? (0, c.isActiveTableSideSession)(),
    a = {
      id: e,
      timeZone: i
    },
    o = (0, t.sipoReadCacheKey)("/get_menu", {
      ...a,
      ...(n ? {
        tableSide: !0
      } : {})
    });
  return (0, t.readThroughSipoCache)(o, async () => {
    let e = await (0, t.sipoFetchWithReadFallback)("/get_menu", a, "POST", j, n ? {
      "With-Table-Id": "true"
    } : {});
    if (!e || !1 === e.status || !1 === e.success || void 0 !== e.status && !b(e.status) && "success" !== e.status || !Array.isArray(e.data) && !Array.isArray(e.categories)) throw Error(e?.message || "Menu unavailable");
    let r = Array.isArray(e.data) ? e.data : e.categories || [],
      i = e => !0 === e || 1 === e || "string" == typeof e && ["1", "true", "yes"].includes(e.trim().toLowerCase()),
      o = (e, t = !1) => {
        let r = i(e.av) || t,
          n = Array.isArray(e.items) ? e.items : Array.isArray(e.item) ? e.item : [],
          a = Array.isArray(e.sub_categories) ? e.sub_categories : [],
          l = new Set(a.filter(e => i(e.av)).map(e => String(e.sub_cat_id ?? e.subCatId ?? e.id ?? e.categoryId)));
        return {
          ...e,
          is_age_restricted: r,
          items: n.map(t => ({
            ...t,
            categoryId: t.prod_cat_id ?? t.category_id ?? t.categoryId ?? e.categoryId ?? e.id,
            is_age_restricted: i(t.av) || r || l.has(String(t.sub_cat_id ?? t.subCatId ?? ""))
          })),
          ...(a.length ? {
            sub_categories: a.map(e => o(e, r))
          } : {})
        };
      },
      l = r.map(e => o(e)),
      s = Array.isArray(e.combos) ? e.combos : e.combos && "object" == typeof e.combos ? Object.values(e.combos) : [];
    return {
      ...e,
      status: "success",
      categories: l,
      combos: s,
      data: e.data || {}
    };
  }, {
    force: r.force
  });
}
async function en(e, i, n = 1) {
  let a = await (0, t.sipoFetch)(`/customer/getOrders?cusId=${encodeURIComponent(e)}&hotelKey=${encodeURIComponent(String(i))}&page=${n}`, {}, "GET", {}, r.ORDERS_API_BASE_URL);
  if (!a.status || !a.data) return {
    success: !1,
    error: "Failed to fetch order history"
  };
  let o = a.data || {},
    l = R(o) || R(a);
  return {
    success: !0,
    data: {
      data: (o.data || []).map(e => function (e) {
        let t = v(e.orderData) || [],
          {
            rawItems: r,
            items: i,
            couponType: n
          } = k(t && "object" == typeof t && !Array.isArray(t) && (Object.prototype.hasOwnProperty.call(t, "nonComboOrders") || Object.prototype.hasOwnProperty.call(t, "comboOrders")) ? t : {
            nonComboOrders: t,
            comboOrders: e.comboOrders
          }, e, "orderData"),
          a = L(r, e),
          o = e.order_id || e.ticket_id || e.id,
          l = O(e),
          s = R(e);
        return {
          orderTrackingId: o,
          createdAt: e.created_at || e.createdAt,
          orderStatus: e.order_status || e.orderStatus || "",
          orderState: e.order_state || null,
          ticketStatus: e.ticket_status || "",
          onlinePaymentStatus: e.payment_status || e.paymentStatus || "",
          paymentType: e.payment_type || "",
          subtotal: String(a.displaySubtotal),
          total: String(a.computedTotal),
          discount: a.discountAmount,
          discountPercent: a.discountPercent,
          discountReason: e.discount_reason || null,
          walletAmount: a.walletAmount,
          isWalletRedeemed: a.isWalletRedeemed,
          walletType: a.walletType,
          walletPointsUsed: a.walletPointsUsed,
          cashDue: a.computedTotal,
          tax: a.tax,
          surcharge: a.surcharge,
          surchargePercent: a.surchargePercent,
          surchargeLabel: a.surchargeLabel,
          deliveryCharge: a.deliveryCharge,
          gratuity: a.gratuity,
          shortId: e.short_id || null,
          pagerNo: e.pager_no ?? null,
          orderType: e.order_type || e.ticket_type || "",
          deliveryType: l,
          scheduledTime: D(e),
          scheduledTimeRaw: e.scheduledTime || e.scheduled_time || e.deliver_time || null,
          onlineKey: e.hotelKey || e.online_key || e.onlineKey || null,
          hotelName: e.hotelName || e.name || e.hotel_name || "",
          hotelAddress: e.address || e.hotel_address || null,
          timezone: s,
          timeZone: s,
          customerAddress: e.customer_address || "",
          customerName: e.customer_name || null,
          customerPhone: e.customer_phone || null,
          instructions: e.note || null,
          couponDetail: e.couponDetail || null,
          isCouponApplied: e.isCouponApplied || !1,
          couponType: n,
          couponDiscountAmount: e.coupon_discount_amount ?? e.couponDiscountAmount ?? null,
          rating: e.rating ?? null,
          feedback: e.feedback ?? null,
          items: i,
          id: o,
          order_id: o,
          lineItems: i,
          orderData: i
        };
      }(l && !R(e) ? {
        ...e,
        timezone: l
      } : e)),
      currentPage: o.current_page,
      lastPage: o.last_page,
      total: o.total,
      perPage: o.per_page
    }
  };
}
async function ed(e) {
  let r = `/popular-dishes/${e}`,
    i = (0, c.isActiveTableSideSession)(),
    [n, a] = await Promise.all([(0, t.readThroughSipoCache)((0, t.sipoReadCacheKey)(r, {}), () => (0, t.sipoFetchWithReadFallback)(r, {}, "GET", e => !!e?.data)), W(e, {
      tableSide: i
    }).catch(() => null)]);
  if (!n.data || !Array.isArray(n.data.products)) return {
    success: !1,
    error: "Popular dishes not found"
  };
  let o = new Map(),
    l = e => e?.is_available !== !1 && e?.is_available !== 0 && e?.is_inactive !== !0 && e?.is_inactive !== 1 && e?.is_inactive !== "1",
    s = (e, t = !0) => {
      e.forEach(e => {
        let r = t && l(e);
        (Array.isArray(e?.items) ? e.items : Array.isArray(e?.item) ? e.item : []).forEach(e => {
          let t = e?.itemId ?? e?.product_id ?? e?.id;
          r && l(e) && null != t && o.set(String(t), e);
        });
        let i = e?.sub_categories ?? e?.subCategories ?? e?.children;
        Array.isArray(i) && s(i, r);
      });
    },
    u = new Map();
  return a && (s(Array.isArray(a.categories) ? a.categories : []), (Array.isArray(a.combos) ? a.combos : []).forEach(e => {
    let t = e?.combo_id ?? e?.comboId ?? e?.id;
    l(e) && null != t && u.set(String(t), e);
  })), {
    success: !0,
    data: [...n.data.products.map(e => ({
      ...e,
      _itemType: "product"
    })), ...(n.data.combos || []).map(e => ({
      ...e,
      _itemType: "combo"
    }))].flatMap(e => {
      let t = e.itemId ?? e.combo_id ?? e.id,
        r = "combo" === e._itemType ? u.get(String(t)) : o.get(String(t));
      return r ? [{
        ...e,
        ...r,
        _itemType: e._itemType
      }] : [];
    }).map((e, t) => {
      let r = (i ? e.table_price ?? e.tablePrice : null) ?? e.sale_price ?? e.combo_price ?? e.price ?? 0;
      return {
        ...e,
        productItemId: e.itemId ?? e.product_id ?? e.combo_id ?? e.id,
        name: e.label ?? e.combo_name ?? e.name,
        imageUrl: e.image_url ?? e.imageUrl ?? e.image,
        sale_price: r,
        salePrice: r,
        orderFrequency: e.order_frequency || 0,
        rank: t + 1,
        itemType: e._itemType
      };
    })
  };
}
function eN(e, t) {
  return e && "null" !== e && "NULL" !== e && "undefined" !== e ? e : {
    product: "SPECIAL",
    coupon: "PROMO",
    loyalty: "LOYALTY REWARD"
  }[t] || "DEAL";
}
function eC(e) {
  let t = v(e);
  return t && "object" == typeof t ? t : {};
}
async function ek(e, r, i, a, o = p) {
  var l;
  let s,
    u = (0, d.isKioskHost)(),
    m = !u && (0, c.isActiveTableSideSession)(),
    _ = y(o),
    [f, g, b] = await Promise.allSettled([(0, t.readThroughSipoCache)((0, t.sipoReadCacheKey)("/get_website", {
      id: e
    }), () => (0, t.sipoFetch)("/get_website", {
      id: e
    }, "GET")), r ? Y(Number(r), {
      kiosk: u,
      tableSide: m
    }) : Promise.resolve([]), W(e, {
      timeZone: _,
      tableSide: m
    })]),
    S = "fulfilled" === b.status ? (l = b.value, s = new Map(), (Array.isArray(l?.data) ? l.data : l?.categories || []).forEach(e => {
      (e.items || e.item || []).forEach(t => {
        let r = Number(t.itemId ?? t.id ?? t.product_id);
        r && s.set(r, {
          ...t,
          categoryId: e.categoryId
        });
      });
    }), s) : new Map(),
    I = [];
  if ("fulfilled" === f.status) {
    let e = f.value?.data ?? f.value;
    Array.isArray(e?.special_product_list) && e.special_product_list.forEach(e => {
      let t = e.itemId ?? e.id,
        r = m ? S.get(Number(t)) : null;
      if (m && !r) return;
      let o = r ? {
          ...e,
          ...r
        } : e,
        l = o.imageUrl || o.image || o.photo || o.photo_original || null,
        s = o.end_date || (o.expiry_date && "0000-00-00" !== o.expiry_date ? o.expiry_date : null),
        d = h((m ? o.table_price ?? o.tablePrice : null) ?? o.sale_price ?? o.price);
      I.push({
        id: `sp-${t}`,
        type: "product",
        title: o.label || o.name || o.title,
        description: o.description || "",
        image: l,
        badge: eN(o.banner, "product"),
        originalPrice: h(o.banner_price ?? o.original_price) || null,
        offerPrice: d,
        category: "special",
        validUntil: s,
        isAvailable: (0, n.isEntityAvailable)(o, i, new Date(), a, !1, u, m, _),
        ctaAction: {
          type: "add_to_cart",
          value: String(t)
        },
        rawData: {
          ...o,
          itemId: t,
          label: o.label || o.name,
          imageUrl: l,
          sale_price: d
        }
      });
    });
  }
  if ("fulfilled" === g.status) {
    let e = g.value;
    (Array.isArray(e) ? e : e.data || []).map(A).filter(Boolean).forEach(e => {
      var t, r;
      let o, l, s, d, c;
      if (e?.is_exposed !== void 0 && 1 !== e.is_exposed) return;
      let p = eC(e?.rules),
        f = eC(e?.result),
        y = String(p.type || f.type || "").toUpperCase(),
        g = (t = p, r = f, s = t.type || r.type || "", d = Number(r.DISCOUNT || 0), c = Number(r.MAX_DISCOUNT_VALUE || 0), "FREE_DELIVERY" === s ? c > 0 ? `Free delivery (up to $${c.toFixed(2)})` : "Free delivery" : "BUY_ONE_GET_ONE_FREE" === s ? "Buy 1 Get 1 Free" : "BUY_N_GET_M_FREE" === s ? "Buy N Get M Free" : "CART_DISCOUNT" === s ? c > 0 ? `${d}% off order (up to $${c.toFixed(2)})` : `${d}% off your order` : "ITEM_DISCOUNT" === s ? 100 === d ? "Free item" : c > 0 ? `${d}% off items (up to $${c.toFixed(2)})` : `${d}% off selected items` : "Special offer"),
        b = (o = p.type || f.type || "", l = Number(f.DISCOUNT || 0), "FREE_DELIVERY" === o ? "FREE DELIVERY" : "BUY_ONE_GET_ONE_FREE" === o ? "BOGO" : "BUY_N_GET_M_FREE" === o ? "BUY N GET M" : "CART_DISCOUNT" === o && l ? `${l}% OFF` : "ITEM_DISCOUNT" === o ? 100 === l ? "FREE ITEM" : l ? `${l}% OFF ITEMS` : "ITEM DISCOUNT" : "PROMO"),
        h = Number(p.MIN_ORDER_VALUE ?? f.MIN_ORDER_VALUE ?? 0) || 0,
        v = Number(p.MAX_ORDER_VALUE ?? f.MAX_ORDER_VALUE ?? 0) || 0,
        A = p.APPLICABLE_FOR || "ALL",
        E = (Array.isArray(e.products) ? e.products : []).map(e => e?.label || e?.name || e?.product_name).filter(Boolean),
        w = ["BUY_ONE_GET_ONE_FREE", "BUY_N_GET_M_FREE", "ITEM_DISCOUNT"].includes(y),
        T = w ? function (e, t, r) {
          let i = String(e).toUpperCase(),
            n = Array.isArray(r.applicable_products) ? r.applicable_products.map(e => {
              let t = Number(e?.itemId ?? e?.product_id ?? e?.id);
              if (!t) return null;
              let r = (Array.isArray(e?.sizes) ? e.sizes : []).map(e => Number(e?.size_id ?? e?.sizeId ?? e?.id ?? e)).filter(e => Number.isFinite(e) && e > 0);
              return r.length ? {
                id: t,
                sizes: r
              } : {
                id: t
              };
            }).filter(Boolean) : [];
          if (n.length > 0 && ["BUY_ONE_GET_ONE_FREE", "BUY_N_GET_M_FREE", "ITEM_DISCOUNT"].includes(i)) return n;
          if ("BUY_ONE_GET_ONE_FREE" === i || "BUY_N_GET_M_FREE" === i) return Array.isArray(t.APPLICABLE_ITEMS) ? t.APPLICABLE_ITEMS : [];
          if ("ITEM_DISCOUNT" === i) {
            let e = Array.isArray(r.parseditems) ? r.parseditems : [];
            return e.length > 0 ? e.map(e => ({
              id: Number(e)
            })) : (Array.isArray(r.products) ? r.products : []).map(e => ({
              id: Number(e.id)
            })).filter(e => e.id);
          }
          return [];
        }(y, p, e) : [],
        P = new Map((Array.isArray(e.applicable_products) ? e.applicable_products : []).map(e => {
          let t = Number(e?.itemId ?? e?.product_id ?? e?.id);
          return t ? [t, e] : null;
        }).filter(Boolean)),
        N = T.map(e => {
          let t = Number(e.id);
          if (!t) return null;
          let r = P.get(t) || S.get(t);
          return r ? {
            entry: e,
            menuItem: r
          } : null;
        }).filter(Boolean);
      if (w && N.length > 0) {
        let t = N.length,
          r = Number(f.DISCOUNT || 0),
          o = "ITEM_DISCOUNT" === y && 100 === r,
          l = [],
          s = 0;
        if ("BUY_N_GET_M_FREE" === y) {
          s = Number(f.max_free_count) || 1;
          let t = Array.isArray(e.result_products) ? e.result_products : [];
          l = (t.length > 0 ? t : Array.isArray(f.RESULTS_ITEMS) ? f.RESULTS_ITEMS : []).map(e => {
            let r = Number(e.itemId ?? e.product_id ?? e.id),
              i = r ? t.length > 0 ? e : S.get(r) : null;
            if (!i) return null;
            let n = (Array.isArray(e.sizes) ? e.sizes : []).map(e => Number(e?.size_id ?? e?.sizeId ?? e?.id ?? e)).filter(e => Number.isFinite(e) && e > 0);
            return {
              itemId: r,
              label: i.label || i.name || `Item ${r}`,
              imageUrl: i.imageUrl || i.image || i.photo || null,
              price: parseFloat(i.sale_price || i.price || 0) || 0,
              sizes: n.length > 0 ? n : null
            };
          }).filter(Boolean);
        }
        N.forEach(({
          entry: r,
          menuItem: d
        }) => {
          let c = Array.isArray(r.sizes) ? r.sizes : [],
            p = Array.isArray(d.sizes) ? d.sizes : [],
            f = c.length > 0 ? p.filter(e => c.includes(Number(e.id ?? e.size_id ?? e.sizeId))) : p,
            S = d.itemId ?? d.product_id ?? d.id,
            E = d.imageUrl || d.image || d.photo || null,
            w = parseFloat(d.sale_price || d.price || 0);
          I.push({
            id: `coupon-${e.id}-item-${S}`,
            type: "coupon_product",
            title: d.label || d.name || `Item ${S}`,
            description: d.description || "",
            image: E,
            badge: eN(b, "coupon"),
            originalPrice: o ? w : null,
            offerPrice: o ? 0 : w,
            category: "coupon_deals",
            validUntil: e.enddate || e.end_date || null,
            validFrom: e.startdate || e.start_date || null,
            isAvailable: (0, n.isEntityAvailable)(d, i, new Date(), a, !1, u, m, _) && (0, n.isEntityAvailable)(e, i, new Date(), a, !1, u, m, _),
            couponCode: e.code,
            couponId: e.id,
            couponName: e.name,
            couponType: y,
            couponAvailability: e.availability ?? null,
            discountText: g,
            couponBadge: eN(b, "coupon"),
            minOrder: h,
            maxOrder: v,
            applicableFor: A,
            terms: e.terms_conditions || e.terms || null,
            siblingCount: t,
            isFreeItem: o,
            freeRewards: l,
            maxFreeCount: s,
            ctaAction: {
              type: "add_to_cart",
              value: String(S)
            },
            rawData: {
              ...d,
              itemId: S,
              label: d.label || d.name,
              imageUrl: E,
              sizes: f
            }
          });
        });
        return;
      }
      I.push({
        id: `coupon-${e.id}`,
        type: "coupon",
        title: e.name,
        description: e.description || g,
        image: e.image || null,
        badge: eN(b, "coupon"),
        code: e.code,
        originalPrice: null,
        offerPrice: null,
        category: "coupons",
        validUntil: e.enddate || e.end_date || null,
        validFrom: e.startdate || e.start_date || null,
        isAvailable: !0,
        terms: e.terms_conditions || e.terms || null,
        couponType: y,
        discountText: g,
        minOrder: h,
        maxOrder: v,
        applicableFor: A,
        productNames: E,
        rules: p,
        result: f,
        ctaAction: {
          type: "apply_coupon",
          value: e.code
        },
        rawData: e
      });
    });
  }
  let v = new Set(),
    E = I.filter(e => !v.has(e.id) && (v.add(e.id), !0));
  return {
    status: "success",
    offers: E,
    total: E.length
  };
}
async function eO(e, t, r, i, n = p) {
  return ek(e, t, r, i, y(n));
}
async function eD(e, t, r = 1) {
  return "string" == typeof t && t.trim() ? en(e, t.trim(), r) : {
    success: !1,
    error: "storeOnlineKey is required"
  };
}
async function eR(e, t, r = 1) {
  return U(e, t, r);
}
async function eL(e, t, r) {
  let i = Array(e.length),
    n = 0,
    a = Math.max(1, Math.min(t, e.length));
  return await Promise.all(Array.from({
    length: a
  }, async () => {
    for (; n < e.length;) {
      let t = n;
      n += 1, i[t] = await r(e[t], t);
    }
  })), i;
}
async function eU(e, t = [], r = {}) {
  if (!t || !Array.isArray(t) || !t.length) return {
    orders: [],
    acceptedOrders: [],
    historyOrders: [],
    errors: [],
    error: "No branches available"
  };
  let i = new Map(),
    n = new Map();
  t.forEach(e => {
    let t = String(e?.onlineKey || "").trim();
    if (!t || i.has(t)) return;
    let r = {
      ...e,
      onlineKey: t,
      storeId: Number(e?.storeId) || 0,
      name: String(e?.name || e?.hotelName || t)
    };
    i.set(t, r);
    let a = Number(r.storeId);
    Number.isFinite(a) && a > 0 && n.set(a, r);
  });
  let a = Array.from(i.values());
  if (!a.length) return {
    orders: [],
    acceptedOrders: [],
    historyOrders: [],
    errors: [],
    error: "No branch online keys available"
  };
  try {
    let t = Math.max(1, Math.floor(Number(r.page) || 1)),
      o = Math.max(1, Math.min(8, Math.floor(Number(r.concurrency) || 4))),
      l = Array.from(new Set((r.sources?.length ? r.sources : ["history", "accepted"]).filter(e => "history" === e || "accepted" === e))),
      s = a.flatMap(e => l.map(t => ({
        branch: e,
        source: t
      }))),
      u = await eL(s, o, async r => {
        try {
          let i = "history" === r.source ? await eD(e, r.branch.onlineKey, t) : await eR(e, r.branch.onlineKey, t);
          if (!i?.success) return {
            task: r,
            response: null,
            error: String(i?.error || "Order request failed")
          };
          return {
            task: r,
            response: i,
            error: null
          };
        } catch (e) {
          return {
            task: r,
            response: null,
            error: String(e?.message || "Order request failed")
          };
        }
      }),
      d = new Map(),
      c = new Map(),
      m = new Map(),
      p = [];
    u.forEach(({
      task: e,
      response: t,
      error: r
    }) => {
      var a, o, l;
      r || !t?.data ? p.push({
        onlineKey: e.branch.onlineKey,
        source: e.source,
        error: r || "Order request failed"
      }) : (a = Array.isArray(t.data) ? t.data : t.data.data || [], o = e.branch, l = e.source, Array.isArray(a) && a.forEach(e => {
        let t = String(e.ticket_id || e.order_id || e.orderTrackingId || e.id || "");
        if (!t) return;
        let r = m.get(t),
          a = Number(e.storeId ?? e.store_id ?? e.hotelId ?? e.hotel_id ?? e.restaurant_id),
          s = String(e.hotelKey || e.online_key || e.onlineKey || "").trim(),
          u = i.get(s) || n.get(a) || o,
          p = u?.onlineKey || s || o.onlineKey,
          _ = R(e) || R(u) || r?.timezone || null,
          f = O(e) || r?.deliveryType || "",
          y = e.lineItems || e.orderData || e.items || [],
          g = {
            ...e,
            id: t,
            onlineKey: p,
            ticket_id: e.ticket_id || e.order_id || t,
            orderTrackingId: e.orderTrackingId || e.order_id || e.ticket_id || t,
            orderStatus: e.orderstatus || e.orderStatus || e.order_status || r?.orderStatus || "PROCESSING",
            orderState: e.orderState ?? e.order_state ?? r?.orderState ?? null,
            ticketStatus: e.ticketStatus || e.ticket_status || r?.ticketStatus || "",
            onlinePaymentStatus: e.onlinePaymentStatus || e.paymentStatus || e.payment_status || r?.onlinePaymentStatus || "",
            created_at: e.created_at || e.createdAt || e.created || r?.created_at || "",
            createdAt: e.created_at || e.createdAt || e.created || r?.createdAt || "",
            subtotal: String(e.subtotal || r?.subtotal || "0"),
            total: String(e.total || e.paid || e.billTotal || r?.total || "0"),
            billTotal: String(e.billTotal || e.total || e.paid || r?.billTotal || "0"),
            items: y,
            orderData: y,
            lineItems: y,
            orderType: e.orderType || e.ticket_type || e.deliver_type || r?.orderType || "",
            deliveryType: f,
            deliver_type: f,
            scheduledTime: D(e),
            scheduledTimeRaw: e.scheduledTimeRaw || e.scheduled_time || e.deliver_time || null,
            hotelName: e.hotelName || e.restaurant_name || u?.name || r?.hotelName || "",
            hotelId: e.hotelId || e.restaurant_id || u?.storeId || r?.hotelId || 0,
            storeId: e.storeId || u?.storeId || r?.storeId || 0,
            branchName: u?.name || e.hotelName || r?.branchName || "",
            timezone: _,
            timeZone: _,
            rating: e.rating ?? r?.rating ?? null,
            feedback: e.feedback ?? r?.feedback ?? null,
            source: l,
            sources: [l]
          },
          b = "accepted" === l ? d : c,
          h = b.get(t);
        if (b.set(t, h ? {
          ...h,
          ...g,
          items: g.items.length >= h.items.length ? g.items : h.items,
          orderData: g.orderData.length >= h.orderData.length ? g.orderData : h.orderData,
          lineItems: g.lineItems.length >= h.lineItems.length ? g.lineItems : h.lineItems
        } : g), r) {
          let e = "accepted" === l;
          m.set(t, {
            ...(e ? r : g),
            ...(e ? g : r),
            onlineKey: g.onlineKey || r.onlineKey,
            orderStatus: e ? g.orderStatus || r.orderStatus : r.orderStatus || g.orderStatus,
            items: g.items.length >= r.items.length ? g.items : r.items,
            orderData: g.orderData.length >= r.orderData.length ? g.orderData : r.orderData,
            lineItems: g.lineItems.length >= r.lineItems.length ? g.lineItems : r.lineItems,
            rating: g.rating ?? r.rating,
            feedback: g.feedback ?? r.feedback,
            sources: Array.from(new Set([...r.sources, l]))
          });
        } else m.set(t, g);
      }));
    });
    let _ = e => Array.from(e).sort((e, t) => {
        let r = new Date(e.created_at?.replace(" ", "T") || 0).getTime();
        return new Date(t.created_at?.replace(" ", "T") || 0).getTime() - r;
      }),
      f = _(m.values()),
      y = _(d.values()),
      g = _(c.values()),
      b = p.length === s.length ? "Failed to load orders for every branch" : null;
    return {
      orders: f,
      acceptedOrders: y,
      historyOrders: g,
      errors: p,
      error: b
    };
  } catch (e) {
    return console.error("[aggregateAllOrders]", e), {
      orders: [],
      acceptedOrders: [],
      historyOrders: [],
      errors: [],
      error: e.message || "Failed to load order history"
    };
  }
}
var native=e.i(910000);e.s(["fetchMenu",0,W,"fetchOffers",0,eO,"getCustomerOrders",0,U,"getOrderHistory",0,en,"aggregateAllOrders",0,eU,"getPopularDishes",0,ed,"getCoupons",0,native.getCoupons,"createCustomer",0,native.createCustomer,"deleteAccount",0,native.deleteAccount]);
});})();


// ===================== src/pages/zeta-reference-views.jsx =====================
// Zeta reference views, captured from the public Zeta app on 2026-10-19.
// Layout and copy are isolated to the Zeta app. Services are supplied by zeta-runtime.jsx.
(function () {
  'use strict';
  var Z = window.OO.zetaReference;
  Z.alias(566450,376151);
  Z.define(24071, e => {
  "use strict";

  let t = (0, e.i(456420).default)("chevron-left", [["path", {
    d: "m15 18-6-6 6-6",
    key: "1wnfg3"
  }]]);
  e.s(["ChevronLeft", 0, t], 24071);
});

  Z.define(75183, e => {
  "use strict";

  let t = (0, e.i(456420).default)("store", [["path", {
    d: "M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5",
    key: "slp6dd"
  }], ["path", {
    d: "M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244",
    key: "o0xfot"
  }], ["path", {
    d: "M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05",
    key: "wn3emo"
  }]]);
  e.s(["Store", 0, t], 75183);
});

  Z.define(77071, e => {
  "use strict";

  let t = (0, e.i(456420).default)("plus", [["path", {
    d: "M5 12h14",
    key: "1ays0h"
  }], ["path", {
    d: "M12 5v14",
    key: "s699le"
  }]]);
  e.s(["Plus", 0, t], 77071);
});

  Z.define(96315, e => {
  "use strict";

  let t = (0, e.i(456420).default)("mail", [["path", {
    d: "m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7",
    key: "132q7q"
  }], ["rect", {
    x: "2",
    y: "4",
    width: "20",
    height: "16",
    rx: "2",
    key: "izxlao"
  }]]);
  e.s(["Mail", 0, t], 96315);
});

  Z.define(98828, e => {
  "use strict";

  var r = e.i(843476),
    t = e.i(271645);
  e.i(651164);
  var a = e.i(917848),
    n = e.i(844644);
  let i = ({
    className: e = "h-8 w-8",
    filled: t = !1
  }) => (0, r.jsx)("svg", {
    className: e,
    viewBox: "0 0 24 24",
    fill: t ? "currentColor" : "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    children: (0, r.jsx)("path", {
      d: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
    })
  });
  e.s(["RatingForm", 0, function ({
    orderId: e,
    firebaseUid: s,
    storePosOwnerId: l,
    onSuccess: o,
    initialRating: d,
    initialFeedback: c,
    items: u
  }) {
    let [m, p] = (0, t.useState)(d || 0),
      [h, x] = (0, t.useState)(c || ""),
      [f, b] = (0, t.useState)(0),
      [g, y] = (0, t.useState)(!1),
      [j, N] = (0, t.useState)(""),
      [k, v] = (0, t.useState)(!!d),
      [S, C] = (0, t.useState)(!!d),
      [w, _] = (0, t.useState)(!d),
      [E, T] = (0, t.useState)(!1),
      [O, P] = (0, t.useState)({}),
      I = (0, t.useMemo)(() => (0, n.normalizeZetaRateableItems)(u), [u]);
    async function D(r) {
      if (r && r.preventDefault(), !m) return void N("Please select a star rating.");
      N(""), y(!0);
      try {
        let r = await (0, a.submitOrderRating)({
          firebaseUID: s,
          orderTrackingId: String(e),
          storePosOwnerId: Number(l),
          rating: m,
          feedback: h,
          items: I.flatMap(e => O[e.productItemId] ? [{
            productItemId: e.productItemId,
            rating: O[e.productItemId]
          }] : [])
        });
        if (r?.success === !1) throw Error(r.error || "Something went wrong. Please try again.");
        v(!0), o && o(m, h);
      } catch (e) {
        N(e.message || "Something went wrong. Please try again.");
      } finally {
        y(!1);
      }
    }
    if ((0, t.useEffect)(() => {
      let r = Number(l || 0),
        t = String(e || "").trim();
      if (d || !r || !t) return void _(!1);
      let n = !1;
      return (0, a.getOrderRating)(r, t).then(e => {
        n || (_(!1), e.success && e.data?.rating != null && (p(e.data.rating), x(e.data.feedback || ""), P(Object.fromEntries(e.data.items.filter(e => null != e.rating).map(e => [e.productItemId, Number(e.rating)]))), C(!0), v(!0)));
      }), () => {
        n = !0;
      };
    }, [d, e, l]), k) return (0, r.jsxs)("div", {
      className: "flex flex-col items-center justify-center py-8 text-center",
      children: [(0, r.jsx)("div", {
        className: "w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4",
        children: (0, r.jsx)("svg", {
          className: "w-7 h-7 text-emerald-500",
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor",
          strokeWidth: 2.5,
          children: (0, r.jsx)("path", {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M4.5 12.75l6 6 9-13.5"
          })
        })
      }), (0, r.jsx)("p", {
        className: "font-display font-black text-lg text-brand-ink uppercase tracking-tight mb-1",
        children: S ? "Already rated" : "Thank you!"
      }), (0, r.jsx)("p", {
        className: "text-sm text-gray-400",
        children: S ? `You rated this order ${m}/5.` : "Your feedback helps us serve you better."
      })]
    });
    if (w) return (0, r.jsxs)("div", {
      className: "flex min-h-24 items-center justify-center gap-2 text-xs font-semibold text-gray-400",
      children: [(0, r.jsx)("svg", {
        className: "h-4 w-4 animate-spin",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 2,
        children: (0, r.jsx)("path", {
          d: "M21 12a9 9 0 1 1-6.219-8.56",
          strokeLinecap: "round"
        })
      }), "Checking your rating…"]
    });
    let R = f || m;
    return (0, r.jsxs)("form", {
      onSubmit: D,
      className: "space-y-5",
      children: [(0, r.jsxs)("div", {
        className: "flex flex-col items-center gap-2",
        children: [(0, r.jsx)("div", {
          className: "flex gap-1.5",
          children: [1, 2, 3, 4, 5].map(e => (0, r.jsx)("button", {
            type: "button",
            onMouseEnter: () => b(e),
            onMouseLeave: () => b(0),
            onClick: () => {
              p(e), N("");
            },
            className: "p-0.5 transition-transform hover:scale-110 active:scale-95 focus:outline-none",
            children: (0, r.jsx)(i, {
              filled: R >= e,
              className: `h-9 w-9 transition-colors duration-150 ${R >= e ? "text-amber-400" : "text-gray-200"}`
            })
          }, e))
        }), (0, r.jsx)("p", {
          className: `text-xs font-black uppercase tracking-widest transition-colors duration-150 h-4 ${R ? "text-amber-500" : "text-gray-300"}`,
          children: ["", "Poor", "Fair", "Good", "Great", "Excellent"][R] || ""
        })]
      }), (0, r.jsxs)("div", {
        className: "space-y-1.5",
        children: [(0, r.jsxs)("label", {
          htmlFor: "order-feedback",
          className: "block text-[10px] font-black uppercase tracking-widest text-gray-400",
          children: ["Feedback ", (0, r.jsx)("span", {
            className: "text-gray-300 normal-case font-semibold tracking-normal",
            children: "— optional"
          })]
        }), (0, r.jsx)("textarea", {
          id: "order-feedback",
          rows: 3,
          maxLength: 1e3,
          value: h,
          onChange: e => x(e.target.value),
          placeholder: "How was your meal? Any suggestions?",
          className: "w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3 text-sm text-brand-ink placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-400/20 focus:border-amber-400/40 transition-all resize-none"
        }), (0, r.jsxs)("p", {
          className: "text-right text-[10px] font-bold text-gray-300 uppercase tracking-widest",
          children: [h.length, "/1000"]
        })]
      }), I.length ? (0, r.jsxs)("div", {
        className: "rounded-2xl border border-gray-100 bg-gray-50",
        children: [(0, r.jsxs)("button", {
          type: "button",
          onClick: () => T(e => !e),
          "aria-expanded": E,
          className: "flex min-h-12 w-full items-center justify-between gap-3 px-4 text-left",
          children: [(0, r.jsxs)("span", {
            children: [(0, r.jsx)("span", {
              className: "block text-xs font-bold text-brand-ink",
              children: "Rate individual items"
            }), (0, r.jsx)("span", {
              className: "mt-0.5 block text-[10px] text-gray-400",
              children: "Optional · helps the kitchen improve each dish"
            })]
          }), (0, r.jsx)("span", {
            className: "text-lg text-brand-primary",
            "aria-hidden": "true",
            children: E ? "−" : "+"
          })]
        }), E ? (0, r.jsx)("div", {
          className: "divide-y divide-gray-100 border-t border-gray-100 bg-white",
          children: I.map(e => (0, r.jsxs)("div", {
            className: "px-4 py-3",
            children: [(0, r.jsx)("p", {
              className: "truncate text-xs font-semibold text-brand-ink",
              children: e.productName
            }), (0, r.jsx)("div", {
              className: "mt-2 flex gap-1",
              role: "radiogroup",
              "aria-label": `Rate ${e.productName}`,
              children: [1, 2, 3, 4, 5].map(t => {
                let a = Number(O[e.productItemId] || 0);
                return (0, r.jsx)("button", {
                  type: "button",
                  role: "radio",
                  "aria-checked": a === t,
                  "aria-label": `${t} star${1 === t ? "" : "s"}`,
                  onClick: () => P(r => ({
                    ...r,
                    [e.productItemId]: t
                  })),
                  className: "grid h-9 w-9 place-items-center rounded-full bg-gray-50",
                  children: (0, r.jsx)(i, {
                    filled: t <= a,
                    className: `h-5 w-5 ${t <= a ? "text-brand-primary" : "text-gray-200"}`
                  })
                }, t);
              })
            })]
          }, e.productItemId))
        }) : null]
      }) : null, j && (0, r.jsxs)("div", {
        className: "flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs font-bold",
        children: [(0, r.jsx)("svg", {
          className: "w-4 h-4 shrink-0",
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor",
          strokeWidth: 2,
          children: (0, r.jsx)("path", {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          })
        }), j]
      }), (0, r.jsx)("button", {
        type: "submit",
        disabled: g || !m,
        className: "w-full h-12 rounded-2xl bg-brand-ink text-white text-xs font-black uppercase tracking-widest hover:bg-black transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2",
        children: g ? (0, r.jsxs)(r.Fragment, {
          children: [(0, r.jsx)("svg", {
            className: "w-4 h-4 animate-spin",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: 2,
            children: (0, r.jsx)("path", {
              d: "M21 12a9 9 0 1 1-6.219-8.56",
              strokeLinecap: "round"
            })
          }), "Submitting…"]
        }) : "Submit Feedback"
      })]
    });
  }]);
});

  Z.define(104673, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    r = e.i(522016),
    s = e.i(618566),
    n = e.i(88653),
    i = e.i(846932),
    l = e.i(199383),
    d = e.i(867927),
    c = e.i(508619),
    o = e.i(226091),
    x = e.i(576248),
    u = e.i(611568),
    m = e.i(367784),
    p = e.i(96315),
    b = e.i(420865),
    h = e.i(515281),
    f = e.i(575387),
    g = e.i(256102);
  let j = (0, e.i(456420).default)("save", [["path", {
    d: "M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z",
    key: "1c8476"
  }], ["path", {
    d: "M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7",
    key: "1ydtos"
  }], ["path", {
    d: "M7 3v4a1 1 0 0 0 1 1h7",
    key: "t51u73"
  }]]);
  var y = e.i(166794),
    k = e.i(584026),
    N = e.i(328623),
    v = e.i(573474),
    w = e.i(763566),
    C = e.i(263676),
    S = e.i(911358),
    _ = e.i(772328),
    z = e.i(399847),
    P = e.i(768877),
    E = e.i(689664),
    A = e.i(282625),
    D = e.i(994004),
    L = e.i(972764),
    R = e.i(765649),
    T = e.i(913734),
    M = e.i(990746),
    F = e.i(273341);
  let U = ["EMAIL", "OTP", "SIGNUP"],
    $ = "h-12 w-full rounded-[14px] border border-brand-ink/10 bg-[#F5F5F7] px-3.5 text-[16px] font-medium text-brand-ink outline-none transition-colors placeholder:text-brand-ink/35 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/12";
  function O({
    icon: e,
    label: a
  }) {
    return (0, t.jsxs)("span", {
      className: "flex min-w-0 items-center gap-1.5 rounded-[12px] bg-white/80 px-2 py-2",
      children: [(0, t.jsx)(e, {
        className: "h-4 w-4 shrink-0 text-brand-primary",
        strokeWidth: 2,
        "aria-hidden": "true"
      }), (0, t.jsx)("span", {
        className: "whitespace-nowrap text-[9px] font-semibold text-brand-ink",
        children: a
      })]
    });
  }
  function W() {
    let {
        user: e,
        loading: r,
        loginWithOTP: l,
        verifyAndLogin: d,
        signupAndLogin: c,
        needsSignup: o,
        pendingEmail: x
      } = (0, M.useAuth)(),
      u = (0, s.useRouter)(),
      m = (0, s.useSearchParams)(),
      b = (0, _.useReducedMotion)(),
      h = (0, a.useMemo)(() => {
        var e;
        return !(e = m.get("returnTo")) || !e.startsWith("/") || e.startsWith("//") || e.startsWith("/account") ? "/profile" : e;
      }, [m]),
      [g, j] = (0, a.useState)("EMAIL"),
      [y, N] = (0, a.useState)(""),
      [v, w] = (0, a.useState)(""),
      [C, W] = (0, a.useState)(""),
      [I, B] = (0, a.useState)(""),
      [H, q] = (0, a.useState)(""),
      [Y, Z] = (0, a.useState)(!1);
    async function G(e) {
      e.preventDefault();
      let t = y.trim().toLowerCase();
      if (t) {
        (0, F.haptic)("light"), Z(!0), q("");
        try {
          await l(t), N(t), j("OTP");
        } catch (e) {
          q((0, S.getAuthErrorMessage)(e, "We could not send your code. Please try again."));
        } finally {
          Z(!1);
        }
      }
    }
    async function K(e) {
      if (e.preventDefault(), 6 === v.length) {
        (0, F.haptic)("light"), Z(!0), q("");
        try {
          let e = await d(x || y, v);
          if (e?.needsSignup) return void j("SIGNUP");
          "/profile" !== h && u.replace(h);
        } catch (e) {
          q((0, S.getAuthErrorMessage)(e, "We could not complete sign-in. Please try again."));
        } finally {
          Z(!1);
        }
      }
    }
    async function V(e) {
      if (e.preventDefault(), C.trim() && I.trim()) {
        (0, F.haptic)("light"), Z(!0), q("");
        try {
          await c({
            name: C.trim(),
            phone: I.trim(),
            email: x || y
          }), "/profile" !== h && u.replace(h);
        } catch (e) {
          q((0, S.getAuthErrorMessage)(e, "We could not create your account. Please try again."));
        } finally {
          Z(!1);
        }
      }
    }
    async function Q() {
      let e = x || y;
      if (!e) return void j("EMAIL");
      (0, F.haptic)("light"), Z(!0), q("");
      try {
        await l(e);
      } catch (e) {
        q((0, S.getAuthErrorMessage)(e, "We could not resend your code. Please try again."));
      } finally {
        Z(!1);
      }
    }
    if ((0, a.useEffect)(() => {
      o ? j("SIGNUP") : x && (N(x), j("OTP"));
    }, [o, x]), (0, a.useEffect)(() => {
      !r && e && "/profile" !== h && u.replace(h);
    }, [r, h, u, e]), r || e) return (0, t.jsx)(T.default, {
      kind: "profile"
    });
    let J = U.indexOf(g),
      X = {
        EMAIL: {
          eyebrow: "Zeta account",
          title: "Let’s get you signed in",
          body: "Enter your email and we’ll send a secure six-digit code. No password needed."
        },
        OTP: {
          eyebrow: "Check your inbox",
          title: "Enter your code",
          body: `We sent a six-digit code to ${x || y}.`
        },
        SIGNUP: {
          eyebrow: "Almost there",
          title: "Finish your account",
          body: "Add your details once, then enjoy faster ordering and member rewards."
        }
      }[g];
    return (0, t.jsxs)("div", {
      className: "mx-auto flex min-h-full w-full max-w-5xl flex-col bg-white",
      children: [(0, t.jsx)("header", {
        className: "px-4 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-7 lg:px-8",
        children: (0, t.jsxs)("div", {
          className: "mx-auto max-w-3xl",
          children: [(0, t.jsxs)("div", {
            className: "flex items-start justify-between gap-4",
            children: [(0, t.jsxs)("div", {
              children: [(0, t.jsx)("p", {
                className: "text-[10px] font-bold uppercase tracking-[0.14em] text-brand-primary",
                children: X.eyebrow
              }), (0, t.jsx)("h1", {
                className: "mt-1.5 text-[24px] font-extrabold leading-tight tracking-[-0.03em] text-brand-ink sm:text-[30px]",
                children: X.title
              })]
            }), (0, t.jsxs)("span", {
              className: "shrink-0 rounded-full bg-[#EAF0FF] px-2.5 py-1.5 text-[10px] font-bold text-brand-primary",
              children: [J + 1, " of 3"]
            })]
          }), (0, t.jsx)("p", {
            className: "mt-2 max-w-xl text-[12px] leading-relaxed text-brand-ink-light sm:text-[13px]",
            children: X.body
          }), (0, t.jsx)("div", {
            className: "mt-4 grid grid-cols-3 gap-1.5",
            "aria-label": `Account step ${J + 1} of 3`,
            children: U.map((e, a) => (0, t.jsx)("span", {
              className: `h-1 rounded-full transition-colors ${a <= J ? "bg-brand-primary" : "bg-brand-ink/10"}`
            }, e))
          })]
        })
      }), (0, t.jsx)("main", {
        className: "flex-1 border-t border-brand-ink/7 px-4 py-5 sm:px-6 sm:py-7 lg:px-8",
        children: (0, t.jsxs)("div", {
          className: "mx-auto grid max-w-3xl gap-5 md:grid-cols-[0.88fr_1.12fr] md:items-start md:gap-6",
          children: [(0, t.jsxs)("section", {
            className: "rounded-[18px] bg-[#EAF0FF] p-3.5 sm:p-4",
            children: [(0, t.jsx)("span", {
              className: "grid h-10 w-10 place-items-center rounded-[12px] bg-brand-primary text-white",
              children: (0, t.jsx)("span", {
                className: "text-[18px] font-extrabold",
                "aria-hidden": "true",
                children: "Z"
              })
            }), (0, t.jsx)("h2", {
              className: "mt-3 text-[16px] font-bold tracking-[-0.02em] text-brand-ink",
              children: "One account, more Zeta"
            }), (0, t.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
              children: "Your rewards, orders and favourites stay together across every Zeta visit."
            }), (0, t.jsxs)("div", {
              className: "mt-3 grid grid-cols-3 gap-1.5 md:grid-cols-1",
              children: [(0, t.jsx)(O, {
                icon: A.Gift,
                label: "Member rewards"
              }), (0, t.jsx)(O, {
                icon: D.History,
                label: "Faster reorders"
              }), (0, t.jsx)(O, {
                icon: k.ShieldCheck,
                label: "Secure access"
              })]
            })]
          }), (0, t.jsxs)("section", {
            "aria-live": "polite",
            children: [(0, t.jsx)(n.AnimatePresence, {
              mode: "wait",
              initial: !1,
              children: (0, t.jsxs)(i.motion.div, {
                initial: !b && {
                  opacity: 0,
                  x: 10
                },
                animate: {
                  opacity: 1,
                  x: 0
                },
                exit: b ? void 0 : {
                  opacity: 0,
                  x: -8
                },
                transition: {
                  duration: .18 * !b
                },
                children: [H ? (0, t.jsxs)("div", {
                  role: "alert",
                  className: "mb-3 flex items-start gap-2.5 rounded-[14px] bg-red-50 px-3.5 py-3 text-[12px] font-medium leading-relaxed text-red-700",
                  children: [(0, t.jsx)(z.AlertCircle, {
                    className: "mt-0.5 h-4 w-4 shrink-0",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  }), (0, t.jsx)("span", {
                    children: H
                  })]
                }) : null, "EMAIL" === g ? (0, t.jsxs)("form", {
                  onSubmit: G,
                  children: [(0, t.jsx)("label", {
                    htmlFor: "zeta-account-email",
                    className: "text-[11px] font-semibold text-brand-ink",
                    children: "Email address"
                  }), (0, t.jsxs)("div", {
                    className: "relative mt-1.5",
                    children: [(0, t.jsx)(p.Mail, {
                      className: "pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-brand-ink-light",
                      strokeWidth: 1.9,
                      "aria-hidden": "true"
                    }), (0, t.jsx)("input", {
                      id: "zeta-account-email",
                      type: "email",
                      value: y,
                      onChange: e => N(e.target.value),
                      placeholder: "you@example.com",
                      autoComplete: "email",
                      inputMode: "email",
                      enterKeyHint: "send",
                      autoCapitalize: "none",
                      spellCheck: !1,
                      required: !0,
                      className: `${$} pl-11`
                    })]
                  }), (0, t.jsxs)("button", {
                    type: "submit",
                    disabled: Y || !y.trim(),
                    className: "mt-3 flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white transition-colors active:bg-brand-primary-dark disabled:bg-brand-ink/10 disabled:text-brand-ink/35",
                    children: [Y ? "Sending code…" : "Continue with email", Y ? null : (0, t.jsx)(P.ArrowRight, {
                      className: "h-[18px] w-[18px]",
                      strokeWidth: 2,
                      "aria-hidden": "true"
                    })]
                  })]
                }) : null, "OTP" === g ? (0, t.jsxs)("form", {
                  onSubmit: K,
                  children: [(0, t.jsxs)("div", {
                    className: "flex items-center justify-between gap-3",
                    children: [(0, t.jsx)("label", {
                      htmlFor: "zeta-account-code",
                      className: "text-[11px] font-semibold text-brand-ink",
                      children: "Six-digit code"
                    }), (0, t.jsx)("button", {
                      type: "button",
                      onClick: function () {
                        (0, F.haptic)("selection"), w(""), q(""), j("EMAIL");
                      },
                      className: "min-h-11 touch-manipulation px-1 text-[11px] font-semibold text-brand-primary",
                      children: "Change email"
                    })]
                  }), (0, t.jsx)("input", {
                    id: "zeta-account-code",
                    type: "text",
                    value: v,
                    onChange: e => w(e.target.value.replace(/\D/g, "").slice(0, 6)),
                    placeholder: "000000",
                    autoComplete: "one-time-code",
                    inputMode: "numeric",
                    enterKeyHint: "done",
                    maxLength: 6,
                    required: !0,
                    autoFocus: !0,
                    className: `${$} h-14 text-center font-mono text-[24px] font-semibold tracking-[0.34em]`
                  }), (0, t.jsxs)("button", {
                    type: "submit",
                    disabled: Y || 6 !== v.length,
                    className: "mt-3 flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white transition-colors active:bg-brand-primary-dark disabled:bg-brand-ink/10 disabled:text-brand-ink/35",
                    children: [Y ? "Checking code…" : "Verify and continue", Y ? null : (0, t.jsx)(E.Check, {
                      className: "h-[18px] w-[18px]",
                      strokeWidth: 2.2,
                      "aria-hidden": "true"
                    })]
                  }), (0, t.jsx)("button", {
                    type: "button",
                    onClick: Q,
                    disabled: Y,
                    className: "mt-1 min-h-11 w-full touch-manipulation text-[11px] font-semibold text-brand-ink-light disabled:opacity-50",
                    children: "Didn’t receive it? Send a new code"
                  })]
                }) : null, "SIGNUP" === g ? (0, t.jsxs)("form", {
                  onSubmit: V,
                  className: "space-y-3",
                  children: [(0, t.jsxs)("label", {
                    className: "block",
                    children: [(0, t.jsx)("span", {
                      className: "text-[11px] font-semibold text-brand-ink",
                      children: "Full name"
                    }), (0, t.jsxs)("span", {
                      className: "relative mt-1.5 block",
                      children: [(0, t.jsx)(R.UserRound, {
                        className: "pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-brand-ink-light",
                        strokeWidth: 1.9,
                        "aria-hidden": "true"
                      }), (0, t.jsx)("input", {
                        type: "text",
                        value: C,
                        onChange: e => W(e.target.value),
                        placeholder: "Your name",
                        autoComplete: "name",
                        enterKeyHint: "next",
                        required: !0,
                        autoFocus: !0,
                        className: `${$} pl-11`
                      })]
                    })]
                  }), (0, t.jsxs)("label", {
                    className: "block",
                    children: [(0, t.jsx)("span", {
                      className: "text-[11px] font-semibold text-brand-ink",
                      children: "Phone number"
                    }), (0, t.jsxs)("span", {
                      className: "relative mt-1.5 block",
                      children: [(0, t.jsx)(f.Phone, {
                        className: "pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-brand-ink-light",
                        strokeWidth: 1.9,
                        "aria-hidden": "true"
                      }), (0, t.jsx)("input", {
                        type: "tel",
                        value: I,
                        onChange: e => B(e.target.value),
                        placeholder: "021 000 0000",
                        autoComplete: "tel",
                        inputMode: "tel",
                        enterKeyHint: "done",
                        required: !0,
                        className: `${$} pl-11`
                      })]
                    })]
                  }), (0, t.jsxs)("button", {
                    type: "submit",
                    disabled: Y || !C.trim() || !I.trim(),
                    className: "flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white transition-colors active:bg-brand-primary-dark disabled:bg-brand-ink/10 disabled:text-brand-ink/35",
                    children: [Y ? "Creating account…" : "Create my account", Y ? null : (0, t.jsx)(P.ArrowRight, {
                      className: "h-[18px] w-[18px]",
                      strokeWidth: 2,
                      "aria-hidden": "true"
                    })]
                  })]
                }) : null]
              }, g)
            }), (0, t.jsxs)("div", {
              className: "mt-4 flex items-start gap-2 border-t border-brand-ink/8 pt-3 text-[10px] leading-relaxed text-brand-ink-light",
              children: [(0, t.jsx)(L.LockKeyhole, {
                className: "mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), (0, t.jsx)("p", {
                children: "We use your details only to manage your Zeta account, orders and rewards."
              })]
            })]
          })]
        })
      })]
    });
  }
  var I = e.i(238325),
    B = e.i(309808);
  e.i(651164);
  var H = e.i(917848);
  let q = "h-12 w-full rounded-[14px] border border-brand-ink/10 bg-[#F5F5F7] px-3.5 text-[14px] font-medium text-brand-ink outline-none transition-colors placeholder:text-brand-ink/35 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/12";
  function Y() {
    return (0, t.jsx)(T.default, {
      kind: "profile"
    });
  }
  function Z({
    icon: e,
    label: a,
    value: r,
    children: s
  }) {
    return (0, t.jsxs)("label", {
      className: "block",
      children: [(0, t.jsxs)("span", {
        className: "mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-brand-ink-light",
        children: [(0, t.jsx)(e, {
          className: "h-3.5 w-3.5",
          strokeWidth: 2,
          "aria-hidden": "true"
        }), a]
      }), s || (0, t.jsx)("span", {
        className: "flex min-h-12 items-center rounded-[14px] bg-[#F5F5F7] px-3.5 text-[14px] font-medium text-brand-ink",
        children: r || "Not set"
      })]
    });
  }
  function G({
    href: e,
    icon: a,
    label: s,
    detail: n,
    onClick: i,
    trailing: l,
    destructive: c = !1
  }) {
    let o = (0, t.jsxs)(t.Fragment, {
        children: [(0, t.jsx)("span", {
          className: `flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] ${c ? "bg-red-50 text-red-600" : "bg-[#EAF0FF] text-brand-primary"}`,
          children: (0, t.jsx)(a, {
            className: "h-[18px] w-[18px]",
            strokeWidth: 1.9,
            "aria-hidden": "true"
          })
        }), (0, t.jsxs)("span", {
          className: "min-w-0 flex-1",
          children: [(0, t.jsx)("span", {
            className: `block text-[14px] font-semibold ${c ? "text-red-600" : "text-brand-ink"}`,
            children: s
          }), n ? (0, t.jsx)("span", {
            className: "mt-0.5 block text-[11px] leading-snug text-brand-ink-light",
            children: n
          }) : null]
        }), l || (0, t.jsx)(d.ChevronRight, {
          className: "h-5 w-5 shrink-0 text-brand-ink/25",
          strokeWidth: 2,
          "aria-hidden": "true"
        })]
      }),
      x = "flex min-h-[58px] w-full touch-manipulation items-center gap-3 px-3.5 py-2.5 text-left transition-colors active:bg-[#F5F5F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary/40";
    return e ? (0, t.jsx)(r.default, {
      href: e,
      className: x,
      onClick: () => (0, F.haptic)("selection"),
      children: o
    }) : l ? (0, t.jsx)("div", {
      className: x,
      children: o
    }) : (0, t.jsx)("button", {
      type: "button",
      onClick: () => {
        (0, F.haptic)("selection"), i?.();
      },
      className: x,
      children: o
    });
  }
  e.s(["default", 0, function () {
    let {
        user: e,
        customerData: S,
        loading: _,
        updateCustomerDataLocally: z,
        refreshCustomerData: P,
        logout: E
      } = (0, M.useAuth)(),
      {
        walletData: A,
        refreshLoyalty: D
      } = (0, I.useLoyalty)(),
      L = (0, s.useRouter)(),
      R = (0, B.useToast)(),
      T = (0, a.useRef)(!1),
      U = (0, a.useRef)(null),
      [$, O] = (0, a.useState)(!1),
      [K, V] = (0, a.useState)(!1),
      [Q, J] = (0, a.useState)({
        name: "",
        phone: "",
        birthday: ""
      }),
      [X, ee] = (0, a.useState)(!1),
      [et, ea] = (0, a.useState)(""),
      [er, es] = (0, a.useState)(!1),
      en = S?.addresses || [],
      ei = Number(A?.walletPoints ?? A?.wallet_points ?? 0) || 0,
      el = String(A?.membershipTypeObj?.name || A?.membershipType || "Zeta Rewards");
    if ((0, a.useEffect)(() => {
      _ || !e || T.current || (T.current = !0, P(e.uid), D());
    }, [_, P, D, L, e]), (0, a.useEffect)(() => {
      S && !$ && J({
        name: S.name || "",
        phone: S.phone || S.phone_no || "",
        birthday: S.birthday || ""
      });
    }, [S, $]), (0, a.useEffect)(() => {
      if (!X) return;
      let e = document.body.style.overflow,
        t = window.requestAnimationFrame(() => U.current?.focus()),
        a = e => {
          "Escape" !== e.key || er || ee(!1);
        };
      return document.body.style.overflow = "hidden", window.addEventListener("keydown", a), () => {
        window.cancelAnimationFrame(t), window.removeEventListener("keydown", a), document.body.style.overflow = e;
      };
    }, [er, X]), _) return (0, t.jsx)(Y, {});
    if (!e) return (0, t.jsx)(W, {});
    let ed = Q.name || S?.name || e.displayName || "Zeta member",
      ec = S?.email || e.email || "No email on file",
      eo = ed.trim().charAt(0).toUpperCase() || "Z";
    async function ex(t) {
      t.preventDefault();
      let a = Q.name.trim(),
        r = Q.phone.trim();
      if (!a) return void R.error("Name is required.");
      if (!r) return void R.error("Phone number is required.");
      V(!0);
      try {
        z?.({
          name: a,
          phone_no: r,
          birthday: Q.birthday || null
        });
        let t = await (0, H.createCustomer)({
          firebaseUID: e.uid,
          name: a,
          phone: r,
          email: "No email on file" === ec ? "" : ec,
          birthday: Q.birthday || null,
          addresses: en
        });
        t.success || t.status ? (t.data && z?.(t.data), O(!1), R.success("Your details are up to date.", {
          title: "Profile saved"
        })) : R.error(t.error || "Failed to save. Please try again.", {
          title: "Save failed"
        });
      } catch {
        R.error("Failed to save changes. Please try again.", {
          title: "Save failed"
        });
      } finally {
        V(!1);
      }
    }
    async function eu() {
      (0, F.haptic)("selection"), await E(), L.replace("/");
    }
    async function em() {
      let t = S?.email || e.email;
      if (!t) return void R.error("No email on file. Please contact support.", {
        title: "Cannot delete"
      });
      if ("DELETE" !== et.trim().toUpperCase()) return void R.error("Type DELETE to confirm.", {
        title: "Confirmation required"
      });
      es(!0);
      try {
        let e = await (0, H.deleteAccount)(t);
        if (!e?.success) {
          R.error(e?.error || "Failed to delete account. Please try again.", {
            title: "Delete failed"
          }), es(!1);
          return;
        }
        R.success("Account deleted. Sorry to see you go."), await E(), L.replace("/");
      } catch (e) {
        R.error(e?.message || "Failed to delete account. Please try again.", {
          title: "Delete failed"
        }), es(!1);
      }
    }
    let ep = [{
      href: "/orders",
      label: "Orders",
      detail: "Receipts",
      Icon: g.ReceiptText
    }, {
      href: "/addresses",
      label: "Addresses",
      detail: `${en.length} saved`,
      Icon: b.MapPin
    }, {
      href: "/wallet",
      label: "Wallet",
      detail: `${ei.toLocaleString()} points`,
      Icon: w.WalletCards
    }, {
      href: "/offers",
      label: "Zeta Zone",
      detail: "Deals & rewards",
      Icon: N.Sparkles
    }];
    return (0, t.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, t.jsxs)("header", {
        className: "mx-auto w-full max-w-[760px] px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6",
        children: [(0, t.jsxs)("div", {
          className: "flex items-center gap-2 text-brand-primary",
          children: [(0, t.jsx)(c.CircleUserRound, {
            className: "h-4 w-4",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), (0, t.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.12em]",
            children: "Your account"
          })]
        }), (0, t.jsx)("h1", {
          className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.025em] sm:text-[30px]",
          children: "Account"
        }), (0, t.jsx)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light sm:text-[13px]",
          children: "Details, rewards and app preferences."
        })]
      }), (0, t.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-4 px-4 sm:px-6",
        children: [(0, t.jsxs)("section", {
          className: "rounded-[16px] border border-brand-primary/18 bg-[#EAF0FF] p-3.5",
          children: [(0, t.jsxs)("div", {
            className: "flex items-center gap-3",
            children: [(0, t.jsx)("span", {
              className: "grid h-12 w-12 shrink-0 place-items-center rounded-[14px] bg-brand-primary text-[20px] font-extrabold text-white",
              children: eo
            }), (0, t.jsxs)("span", {
              className: "min-w-0 flex-1",
              children: [(0, t.jsx)("span", {
                className: "block truncate text-[18px] font-bold leading-tight",
                children: ed
              }), (0, t.jsx)("span", {
                className: "mt-0.5 block truncate text-[11px] text-brand-ink-light",
                children: ec
              })]
            }), (0, t.jsxs)(r.default, {
              href: "/wallet",
              onClick: () => (0, F.haptic)("selection"),
              className: "min-h-11 shrink-0 touch-manipulation rounded-[10px] bg-white px-2.5 py-2 text-right focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40",
              children: [(0, t.jsx)("span", {
                className: "block text-[16px] font-extrabold leading-none tabular-nums text-brand-primary",
                children: ei.toLocaleString()
              }), (0, t.jsx)("span", {
                className: "mt-1 block text-[8px] font-bold uppercase tracking-[0.1em] text-brand-ink-light",
                children: "Points"
              })]
            })]
          }), (0, t.jsxs)("div", {
            className: "mt-3 flex items-center justify-between border-t border-brand-primary/12 pt-2.5 text-[10px]",
            children: [(0, t.jsx)("span", {
              className: "font-semibold text-brand-primary",
              children: el
            }), (0, t.jsxs)(r.default, {
              href: "/wallet",
              onClick: () => (0, F.haptic)("selection"),
              className: "flex min-h-11 touch-manipulation items-center gap-1 font-semibold text-brand-ink",
              children: ["View member card", (0, t.jsx)(d.ChevronRight, {
                className: "h-3.5 w-3.5",
                strokeWidth: 2.2,
                "aria-hidden": "true"
              })]
            })]
          })]
        }), (0, t.jsx)("section", {
          className: "grid grid-cols-2 gap-2 md:grid-cols-4",
          "aria-label": "Account shortcuts",
          children: ep.map(({
            href: e,
            label: a,
            detail: s,
            Icon: n
          }) => (0, t.jsxs)(r.default, {
            href: e,
            onClick: () => (0, F.haptic)("selection"),
            className: "flex min-h-[64px] touch-manipulation items-center gap-2.5 rounded-[15px] bg-[#F5F5F7] px-3 transition-transform active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40",
            children: [(0, t.jsx)("span", {
              className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
              children: (0, t.jsx)(n, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), (0, t.jsxs)("span", {
              className: "min-w-0",
              children: [(0, t.jsx)("span", {
                className: "block text-[13px] font-semibold",
                children: a
              }), (0, t.jsx)("span", {
                className: "mt-0.5 block truncate text-[10px] text-brand-ink-light",
                children: s
              })]
            })]
          }, e))
        }), (0, t.jsxs)("section", {
          className: "overflow-hidden rounded-[16px] border border-brand-ink/8 bg-white",
          children: [(0, t.jsxs)("div", {
            className: "flex items-center justify-between border-b border-brand-ink/8 px-3.5 py-3",
            children: [(0, t.jsxs)("div", {
              children: [(0, t.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: "Personal details"
              }), (0, t.jsx)("p", {
                className: "mt-0.5 text-[11px] text-brand-ink-light",
                children: "Used for your Zeta orders."
              })]
            }), $ ? null : (0, t.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, F.haptic)("selection"), O(!0);
              },
              className: "flex min-h-11 touch-manipulation items-center gap-1.5 rounded-[11px] bg-[#F5F5F7] px-3 text-[12px] font-semibold text-brand-primary",
              children: [(0, t.jsx)(h.Pencil, {
                className: "h-3.5 w-3.5",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), "Edit"]
            })]
          }), (0, t.jsxs)("form", {
            onSubmit: ex,
            className: "grid gap-3 p-3.5 sm:grid-cols-2",
            children: [(0, t.jsx)(Z, {
              icon: c.CircleUserRound,
              label: "Full name",
              value: Q.name,
              children: $ ? (0, t.jsx)("input", {
                value: Q.name,
                onChange: e => J({
                  ...Q,
                  name: e.target.value
                }),
                className: q,
                autoComplete: "name"
              }) : void 0
            }), (0, t.jsx)(Z, {
              icon: f.Phone,
              label: "Phone number",
              value: Q.phone,
              children: $ ? (0, t.jsx)("input", {
                type: "tel",
                value: Q.phone,
                onChange: e => J({
                  ...Q,
                  phone: e.target.value
                }),
                className: q,
                autoComplete: "tel"
              }) : void 0
            }), (0, t.jsx)(Z, {
              icon: p.Mail,
              label: "Email address",
              value: ec
            }), (0, t.jsx)(Z, {
              icon: l.Cake,
              label: "Birthday",
              value: Q.birthday ? new Date(Q.birthday).toLocaleDateString("en-NZ") : "Not set",
              children: $ ? (0, t.jsx)("input", {
                type: "date",
                value: Q.birthday ? Q.birthday.substring(0, 10) : "",
                onChange: e => J({
                  ...Q,
                  birthday: e.target.value
                }),
                className: q
              }) : void 0
            }), $ ? (0, t.jsxs)("div", {
              className: "grid grid-cols-2 gap-2 pt-1 sm:col-span-2",
              children: [(0, t.jsx)("button", {
                type: "button",
                onClick: function () {
                  O(!1), J({
                    name: S?.name || "",
                    phone: S?.phone || S?.phone_no || "",
                    birthday: S?.birthday || ""
                  });
                },
                disabled: K,
                className: "h-11 touch-manipulation rounded-[13px] bg-[#F5F5F7] text-[13px] font-semibold text-brand-ink disabled:opacity-50",
                children: "Cancel"
              }), (0, t.jsxs)("button", {
                type: "submit",
                disabled: K,
                className: "flex h-11 touch-manipulation items-center justify-center gap-1.5 rounded-[13px] bg-brand-primary text-[13px] font-semibold text-white disabled:opacity-50",
                children: [K ? (0, t.jsx)(u.LoaderCircle, {
                  className: "h-4 w-4 animate-spin",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }) : (0, t.jsx)(j, {
                  className: "h-4 w-4",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), K ? "Saving" : "Save changes"]
              })]
            }) : null]
          })]
        }), (0, t.jsxs)("section", {
          children: [(0, t.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "More"
          }), (0, t.jsxs)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8 bg-white",
            children: [(0, t.jsx)(G, {
              href: "/settings",
              icon: y.Settings,
              label: "Settings",
              detail: "Notifications, haptics, welcome screens and updates."
            }), (0, t.jsx)(G, {
              href: "/contact",
              icon: x.HelpCircle,
              label: "Help and contact",
              detail: "Get help from the Zeta team."
            }), (0, t.jsx)(G, {
              href: "/privacy",
              icon: k.ShieldCheck,
              label: "Privacy"
            }), (0, t.jsx)(G, {
              href: "/terms",
              icon: o.FileText,
              label: "Terms and conditions"
            })]
          })]
        }), (0, t.jsxs)("section", {
          children: [(0, t.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Session"
          }), (0, t.jsxs)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8 bg-white",
            children: [(0, t.jsx)(G, {
              icon: m.LogOut,
              label: "Sign out",
              detail: "Sign out on this device.",
              onClick: eu
            }), (0, t.jsx)(G, {
              icon: v.Trash2,
              label: "Delete account",
              detail: "Permanently remove your Zeta account.",
              destructive: !0,
              onClick: () => {
                ea(""), ee(!0);
              }
            })]
          })]
        })]
      }), (0, t.jsx)(n.AnimatePresence, {
        children: X ? (0, t.jsxs)(t.Fragment, {
          children: [(0, t.jsx)(i.motion.button, {
            type: "button",
            "aria-label": "Close delete account confirmation",
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            onClick: () => {
              er || ee(!1);
            },
            className: "fixed inset-0 z-[200] bg-brand-ink/45"
          }), (0, t.jsxs)(i.motion.section, {
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "delete-account-title",
            initial: {
              y: "100%"
            },
            animate: {
              y: 0
            },
            exit: {
              y: "100%"
            },
            transition: {
              type: "spring",
              stiffness: 380,
              damping: 38
            },
            className: "fixed bottom-0 left-0 right-0 z-[201] mx-auto max-h-[calc(100dvh-20px)] max-w-[520px] overflow-y-auto overscroll-contain rounded-t-[20px] bg-white px-4 pb-[calc(20px+env(safe-area-inset-bottom,0px))] pt-3 shadow-2xl sm:bottom-6 sm:rounded-[20px] sm:px-5 sm:pb-5",
            children: [(0, t.jsx)("div", {
              className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/12"
            }), (0, t.jsxs)("div", {
              className: "mt-3 flex items-start justify-between gap-3",
              children: [(0, t.jsxs)("div", {
                children: [(0, t.jsx)("h2", {
                  id: "delete-account-title",
                  className: "text-[20px] font-bold",
                  children: "Delete your account?"
                }), (0, t.jsx)("p", {
                  className: "mt-1 text-[12px] leading-relaxed text-brand-ink-light",
                  children: "This permanently removes your account, saved addresses, wallet balance and order history. It cannot be undone."
                })]
              }), (0, t.jsx)("button", {
                ref: U,
                type: "button",
                onClick: () => ee(!1),
                disabled: er,
                "aria-label": "Close",
                className: "grid h-11 w-11 shrink-0 touch-manipulation place-items-center rounded-[11px] bg-[#F5F5F7] text-brand-ink",
                children: (0, t.jsx)(C.X, {
                  className: "h-4 w-4",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              })]
            }), (0, t.jsxs)("label", {
              className: "mt-4 block",
              children: [(0, t.jsx)("span", {
                className: "text-[11px] font-semibold text-red-600",
                children: "Type DELETE to confirm"
              }), (0, t.jsx)("input", {
                value: et,
                onChange: e => ea(e.target.value),
                disabled: er,
                placeholder: "DELETE",
                className: `${q} mt-1.5 border-red-200 focus:border-red-500 focus:ring-red-500/10`
              })]
            }), (0, t.jsxs)("div", {
              className: "mt-4 grid grid-cols-2 gap-2",
              children: [(0, t.jsx)("button", {
                type: "button",
                onClick: () => ee(!1),
                disabled: er,
                className: "h-12 touch-manipulation rounded-[14px] bg-[#F5F5F7] text-[14px] font-semibold text-brand-ink disabled:opacity-50",
                children: "Keep account"
              }), (0, t.jsxs)("button", {
                type: "button",
                onClick: em,
                disabled: er || "DELETE" !== et.trim().toUpperCase(),
                className: "flex h-12 touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-red-600 text-[14px] font-semibold text-white disabled:opacity-35",
                children: [er ? (0, t.jsx)(u.LoaderCircle, {
                  className: "h-4 w-4 animate-spin",
                  "aria-hidden": "true"
                }) : null, er ? "Deleting" : "Delete account"]
              })]
            })]
          })]
        }) : null
      })]
    });
  }], 104673);
});

  Z.define(106501, e => {
  "use strict";

  let t = (0, e.i(456420).default)("calendar-clock", [["path", {
    d: "M16 14v2.2l1.6 1",
    key: "fo4ql5"
  }], ["path", {
    d: "M16 2v4",
    key: "4m81vk"
  }], ["path", {
    d: "M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5",
    key: "1osxxc"
  }], ["path", {
    d: "M3 10h5",
    key: "r794hk"
  }], ["path", {
    d: "M8 2v4",
    key: "1cmpym"
  }], ["circle", {
    cx: "16",
    cy: "16",
    r: "6",
    key: "qoo3c4"
  }]]);
  e.s(["CalendarClock", 0, t], 106501);
});

  Z.define(116579, e => {
  "use strict";

  let t = [{
      title: "1. Acceptance of Terms",
      content: `By accessing and using the Zeta Greek Kitchen website (zeta.sipo.nz), mobile applications, and any related services (collectively, the "Services"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Services.

These Terms apply to all visitors, users, customers, and others who access or use the Services. We reserve the right to modify these Terms at any time, and such modifications shall be effective immediately upon posting on the website.`
    }, {
      title: "2. Use of Services",
      content: `You agree to use our Services only for lawful purposes and in accordance with these Terms. You agree not to:

• Use the Services in any way that violates any applicable local, national, or international law or regulation
• Impersonate or attempt to impersonate Zeta, a Zeta employee, another user, or any other person or entity
• Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Services
• Attempt to gain unauthorised access to any portion of the Services or any systems or networks connected to the Services
• Use any robot, spider, or other automatic device to access the Services for any purpose`
    }, {
      title: "3. Online Ordering",
      content: `When placing an order through our website or third-party ordering platforms, you agree to provide accurate, current, and complete information. All orders are subject to acceptance and product availability.

• Prices displayed are in New Zealand Dollars (NZD) and include GST unless otherwise stated
• We reserve the right to refuse or cancel any order for any reason, including product unavailability, errors in pricing, or suspected fraudulent activity
• Online orders may have a minimum order value and delivery fees may apply depending on your location
• Estimated delivery times are approximate and may vary due to factors such as order volume, weather, and traffic conditions
• Payment must be made at the time of ordering through accepted payment methods`
    }, {
      title: "4. Menu & Pricing",
      content: `• All menu items displayed on our website are subject to availability at your selected store
• Prices are subject to change without prior notice
• Images of menu items are for illustration purposes only and may differ from the actual product
• Nutritional and allergen information is provided as a guide only and may not reflect recent recipe or supplier changes
• We reserve the right to modify our menu items, ingredients, portions, and packaging at any time`
    }, {
      title: "5. Intellectual Property",
      content: `The Zeta\xae brand, logo, and all related trademarks, service marks, trade names, and trade dress are the property of Zeta Greek Kitchen and its licensees. All content on this website including but not limited to text, graphics, logos, images, audio clips, digital downloads, and data compilations is the property of Zeta Greek Kitchen or its content suppliers and is protected by New Zealand and international copyright laws.

You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any content from the Services without explicit written permission from Zeta Greek Kitchen.`
    }, {
      title: "6. Loyalty & Reward Programs",
      content: "If applicable, loyalty points and rewards earned through our programs are subject to the specific terms and conditions of each program. Points have no cash value and cannot be exchanged for cash. We reserve the right to modify, suspend, or discontinue any loyalty program at any time without prior notice. Abuse or fraudulent accumulation of points may result in forfeiture of points and termination of membership."
    }, {
      title: "7. Limitation of Liability",
      content: `To the maximum extent permitted by law, Zeta Greek Kitchen, its directors, employees, partners, agents, suppliers, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.

Nothing in these Terms limits or excludes any consumer rights under the New Zealand Consumer Guarantees Act 1993 that cannot be lawfully limited or excluded.`
    }, {
      title: "8. Food Safety & Quality",
      content: `We are committed to food safety and quality. Zeta states that its products are halal and prepared in accordance with applicable food safety regulations. However, please note:

• Our kitchens handle multiple allergens — we cannot guarantee an allergen-free environment
• If you have specific dietary requirements or food allergies, please inform our staff before ordering
• Please consume products within the recommended timeframe
• We are not responsible for any adverse reactions resulting from non-disclosed allergies`
    }, {
      title: "9. Privacy",
      content: "Your use of the Services is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices regarding the collection, use, and disclosure of your personal information."
    }, {
      title: "10. Governing Law",
      content: "These Terms shall be governed by and construed in accordance with the laws of New Zealand, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of New Zealand."
    }, {
      title: "11. Contact Us",
      content: `If you have any questions about these Terms and Conditions, please contact Zeta using the source details below:

• Email: zetagreekkitchen@gmail.com
• Phone: +64 9 600 2598
• Store help: use the Contact or Stores page for current Auckland location details.`
    }],
    a = [{
      title: "1. Information We Collect",
      content: `We may collect and process the following types of personal information:

Information you provide directly:
• Name, email address, phone number, and delivery address when placing an order
• Payment information (processed securely through our payment providers)
• Feedback, reviews, and communications you send to us
• Account registration details and preferences
• Franchise enquiry information

Information collected automatically:
• Device information (browser type, operating system, device identifiers)
• IP address and approximate location
• Website usage data (pages visited, time spent, click patterns)
• Cookies and similar tracking technologies
• Order history and preferences`
    }, {
      title: "2. How We Use Your Information",
      content: `We use the information we collect for the following purposes:

• To process and fulfil your orders, including delivery and pickup
• To create and manage your account
• To communicate with you about orders, promotions, and updates
• To personalise your experience and recommend products
• To improve our website, services, and customer experience
• To process payments and prevent fraud
• To respond to your inquiries, feedback, and support requests
• To comply with legal obligations and enforce our Terms and Conditions
• To conduct analytics and understand usage patterns
• To send marketing communications (with your consent)`
    }, {
      title: "3. Sharing Your Information",
      content: `We do not sell your personal information. We may share your data with:

• Service Providers: Third-party companies that help us deliver our services, including payment processors (e.g., Stripe, Windcave), delivery partners, cloud hosting providers, and email services
• Franchise Partners: Your selected Zeta store for order fulfilment
• Analytics Partners: Tools like Google Analytics to understand website usage
• Legal Requirements: When required by law, regulation, court order, or to protect our rights, safety, or property
• Business Transfers: In connection with a merger, acquisition, or sale of assets

All third parties are contractually obligated to handle your data securely and in accordance with applicable privacy laws.`
    }, {
      title: "4. Cookies & Tracking",
      content: `We use cookies and similar technologies to:

• Remember your preferences and settings
• Understand how you interact with our website
• Deliver targeted advertising and promotions
• Analyse website traffic and performance

Types of cookies we use:
• Essential Cookies: Required for the website to function properly
• Performance Cookies: Help us understand how visitors use our website
• Functional Cookies: Remember your preferences and choices
• Marketing Cookies: Used to deliver relevant advertisements

You can control cookies through your browser settings. However, disabling certain cookies may affect the functionality of our website.`
    }, {
      title: "5. Data Security",
      content: `We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. These measures include:

• SSL/TLS encryption for data in transit
• Secure payment processing through PCI-compliant providers
• Regular security assessments and updates
• Access controls and employee training
• Secure data storage on cloud infrastructure

While we strive to protect your data, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your information.`
    }, {
      title: "6. Your Rights",
      content: `Under the New Zealand Privacy Act 2020, you have the right to:

• Access: Request a copy of the personal information we hold about you
• Correction: Request correction of inaccurate or incomplete data
• Deletion: Request deletion of your personal information (subject to legal obligations)
• Objection: Object to the processing of your data for certain purposes
• Data Portability: Request your data in a commonly used format
• Withdraw Consent: Withdraw consent for marketing communications at any time

To exercise any of these rights, please contact us using the details provided below. We will respond to your request within 20 working days as required by New Zealand law.`
    }, {
      title: "7. Data Retention",
      content: `We retain your personal information only for as long as necessary to fulfil the purposes for which it was collected, including:

• Active account data: Retained while your account is active
• Order history: Retained for 7 years for tax and accounting purposes
• Marketing data: Retained until you unsubscribe or request deletion
• Website analytics: Retained for up to 26 months

When your data is no longer needed, we will securely delete or anonymise it.`
    }, {
      title: "8. Children's Privacy",
      content: "Our Services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children under 16. If you become aware that a child has provided us with personal information, please contact us immediately and we will take steps to delete such information."
    }, {
      title: "9. Third-Party Links",
      content: "Our website may contain links to third-party websites, including our online ordering platform (powered by SIPO Cloud POS), social media pages, and delivery partners. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party services you visit."
    }, {
      title: "10. Changes to This Policy",
      content: 'We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on our website with a revised "Last Updated" date. Your continued use of our Services after such changes constitutes your acceptance of the updated Privacy Policy.'
    }, {
      title: "11. Contact Us",
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact Zeta using the source details below:

• Email: zetagreekkitchen@gmail.com
• Phone: +64 9 600 2598
• Store help: use the Contact or Stores page for current Auckland location details.

If you are not satisfied with our response, you may lodge a complaint with the Office of the Privacy Commissioner of New Zealand at www.privacy.org.nz.`
    }];
  e.s(["PRIVACY_SECTIONS", 0, a, "TERMS_SECTIONS", 0, t]);
});

  Z.define(118635, e => {
  "use strict";

  var t = e.i(902434);
  e.s(["House", () => t.default]);
});

  Z.define(126486, e => {
  "use strict";

  let t = (0, e.i(456420).default)("utensils-crossed", [["path", {
    d: "m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8",
    key: "n7qcjb"
  }], ["path", {
    d: "M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7",
    key: "d0u48b"
  }], ["path", {
    d: "m2.1 21.8 6.4-6.3",
    key: "yn04lh"
  }], ["path", {
    d: "m19 5-7 7",
    key: "194lzd"
  }]]);
  e.s(["UtensilsCrossed", 0, t], 126486);
});

  Z.define(129768, e => {
  "use strict";

  let t = (0, e.i(456420).default)("calendar-days", [["path", {
    d: "M8 2v4",
    key: "1cmpym"
  }], ["path", {
    d: "M16 2v4",
    key: "4m81vk"
  }], ["rect", {
    width: "18",
    height: "18",
    x: "3",
    y: "4",
    rx: "2",
    key: "1hopcy"
  }], ["path", {
    d: "M3 10h18",
    key: "8toen8"
  }], ["path", {
    d: "M8 14h.01",
    key: "6423bh"
  }], ["path", {
    d: "M12 14h.01",
    key: "1etili"
  }], ["path", {
    d: "M16 14h.01",
    key: "1gbofw"
  }], ["path", {
    d: "M8 18h.01",
    key: "lrp35t"
  }], ["path", {
    d: "M12 18h.01",
    key: "mhygvu"
  }], ["path", {
    d: "M16 18h.01",
    key: "kzsmim"
  }]]);
  e.s(["CalendarDays", 0, t], 129768);
});

  Z.define(134461, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    s = e.i(652348),
    l = e.i(174080),
    i = e.i(977617),
    r = e.i(846932),
    n = e.i(88653),
    o = e.i(701688);
  function c({
    isOpen: e,
    title: s = "Important Notice",
    message: d,
    imageUrl: x,
    branchName: p,
    branchAddress: m,
    onClose: h
  }) {
    let [u, b] = (0, a.useState)(!1);
    return ((0, o.useBodyScrollLock)(e), (0, a.useEffect)(() => {
      b(!0);
    }, []), u) ? (0, l.createPortal)((0, t.jsx)(n.AnimatePresence, {
      children: e && (0, t.jsxs)(t.Fragment, {
        children: [(0, t.jsx)(r.motion.div, {
          initial: {
            opacity: 0
          },
          animate: {
            opacity: 1
          },
          exit: {
            opacity: 0
          },
          transition: {
            duration: .1
          },
          className: "fixed inset-0 bg-brand-ink/70 z-[10000]",
          onClick: h
        }, "backdrop"), (0, t.jsx)("div", {
          className: "fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none",
          children: (0, t.jsxs)(r.motion.div, {
            initial: {
              opacity: 0,
              scale: .96
            },
            animate: {
              opacity: 1,
              scale: 1
            },
            exit: {
              opacity: 0,
              scale: .96,
              transition: {
                duration: .12
              }
            },
            transition: {
              duration: .14,
              ease: "easeOut"
            },
            role: "alertdialog",
            "aria-modal": "true",
            "aria-labelledby": "restaurant-alert-title",
            className: "pointer-events-auto w-full max-w-sm rounded-md border border-brand-ink/10 bg-white shadow-2xl overflow-hidden",
            children: [(0, t.jsx)("div", {
              className: "h-1 w-full bg-brand-primary"
            }), !!x && "null" !== x && "NULL" !== x && (0, t.jsx)("div", {
              className: "relative w-full bg-gray-100",
              style: {
                aspectRatio: "16/9"
              },
              children: (0, t.jsx)(i.default, {
                src: x,
                alt: s,
                fill: !0,
                className: "object-cover",
                sizes: "(max-width: 768px) 100vw, 400px",
                context: "RestaurantAlertModal",
                meta: {
                  title: s
                }
              })
            }), (0, t.jsxs)("div", {
              className: "flex items-start gap-3 px-4 pt-4 pb-3",
              children: [(0, t.jsx)("span", {
                className: "text-brand-primary mt-0.5",
                children: (0, t.jsx)("svg", {
                  className: "w-5 h-5 flex-shrink-0",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor",
                  strokeWidth: 2,
                  children: (0, t.jsx)("path", {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    d: "M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                  })
                })
              }), (0, t.jsxs)("div", {
                className: "flex-1 min-w-0",
                children: [p && (0, t.jsxs)("div", {
                  className: "mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-brand-primary/10 text-brand-primary max-w-full",
                  children: [(0, t.jsx)("svg", {
                    className: "w-3 h-3 flex-shrink-0",
                    fill: "currentColor",
                    viewBox: "0 0 20 20",
                    children: (0, t.jsx)("path", {
                      fillRule: "evenodd",
                      d: "M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z",
                      clipRule: "evenodd"
                    })
                  }), (0, t.jsx)("span", {
                    className: "font-display font-black text-[10px] uppercase tracking-[0.08em] truncate",
                    children: p
                  })]
                }), (0, t.jsx)("p", {
                  id: "restaurant-alert-title",
                  className: "font-black text-sm text-gray-900 mb-1 font-display uppercase tracking-tight",
                  children: s
                }), (0, t.jsx)("p", {
                  className: "text-sm text-gray-600 leading-snug whitespace-pre-line",
                  children: d
                }), m && (0, t.jsxs)("p", {
                  className: "mt-2 text-[11px] text-gray-400 leading-snug flex items-start gap-1",
                  children: [(0, t.jsxs)("svg", {
                    className: "w-3 h-3 flex-shrink-0 mt-0.5",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor",
                    strokeWidth: 2,
                    children: [(0, t.jsx)("path", {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      d: "M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z"
                    }), (0, t.jsx)("path", {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      d: "M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    })]
                  }), (0, t.jsx)("span", {
                    className: "truncate",
                    children: m
                  })]
                })]
              }), (0, t.jsx)("button", {
                onClick: h,
                "aria-label": "Close",
                className: "flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors mt-0.5",
                children: (0, t.jsx)("svg", {
                  className: "w-4 h-4",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor",
                  strokeWidth: 2.5,
                  children: (0, t.jsx)("path", {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    d: "M6 18L18 6M6 6l12 12"
                  })
                })
              })]
            }), (0, t.jsx)("div", {
              className: "px-4 pb-4 pt-1",
              children: (0, t.jsx)("button", {
                onClick: h,
                className: "w-full bg-brand-primary text-white py-2.5 rounded-md font-display font-black text-xs uppercase tracking-wider hover:bg-brand-primary-dark transition-all active:scale-[0.98]",
                children: "I Understand"
              })
            })]
          }, "card")
        })]
      })
    }), document.body) : null;
  }
  var d = e.i(394556);
  e.s(["default", 0, function () {
    let {
        currentBranch: e,
        restaurantConfig: l
      } = (0, s.useBranch)(),
      [i, r] = (0, a.useState)(!1);
    if ((0, a.useEffect)(() => {
      if (!l) return;
      let t = e?.onlineKey || l.online_key;
      if (!t || 1 !== l.is_alert || "string" != typeof l.alert_text || "" === l.alert_text.trim() || "null" === l.alert_text.trim().toLowerCase()) return;
      let a = `${d.default.storagePrefix}-restaurant-alert-shown:${t}`;
      try {
        if ("1" === sessionStorage.getItem(a)) return;
        sessionStorage.setItem(a, "1");
      } catch {}
      r(!0);
    }, [l, e?.onlineKey]), !l) return null;
    let n = l.alert_image,
      o = e?.name || l.hotelName || l.hotel_name || l.name || "",
      x = Array.isArray(l.address) ? l.address[0] : null,
      p = [x?.streetName, x?.city].filter(Boolean),
      m = p.length > 0 ? p.join(", ") : e?.address || "";
    return (0, t.jsx)(c, {
      isOpen: i,
      title: "Important Notice",
      message: l.alert_text || "",
      imageUrl: n && "null" !== n && "NULL" !== n ? n : null,
      branchName: o,
      branchAddress: m,
      onClose: () => r(!1)
    });
  }], 134461);
});

  Z.define(154484, e => {
  "use strict";

  var t = e.i(394556);
  let a = `${t.default.storagePrefix}-favorite-products-v1`,
    r = `${t.default.storagePrefix}:favorites:changed`;
  function n() {
    try {
      let e = window.localStorage.getItem(a);
      if (!e) return [];
      let t = JSON.parse(e);
      return Array.isArray(t) ? t.filter(e => e && e.id) : [];
    } catch {
      return [];
    }
  }
  function i(e) {
    try {
      window.localStorage.setItem(a, JSON.stringify(e)), window.dispatchEvent(new Event(r));
    } catch {}
  }
  e.s(["ZETA_FAVORITES_CHANGED_EVENT", 0, r, "getFavoriteProducts", 0, function () {
    return n().sort((e, t) => (t.savedAt || 0) - (e.savedAt || 0));
  }, "isFavoriteProduct", 0, function (e) {
    return null != e && "" !== e && n().some(t => t.id === String(e));
  }, "removeFavoriteProduct", 0, function (e) {
    let t = String(e ?? "");
    t && i(n().filter(e => e.id !== t));
  }, "toggleFavoriteProduct", 0, function (e) {
    let t = String(e.id ?? "");
    if (!t) return !1;
    let a = n(),
      r = a.findIndex(e => e.id === t);
    return r >= 0 ? (a.splice(r, 1), i(a), !1) : (a.push({
      ...e,
      id: t,
      savedAt: Date.now()
    }), i(a), !0);
  }]);
});

  Z.define(166794, e => {
  "use strict";

  let t = (0, e.i(456420).default)("settings", [["path", {
    d: "M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915",
    key: "1i5ecw"
  }], ["circle", {
    cx: "12",
    cy: "12",
    r: "3",
    key: "1v7zrd"
  }]]);
  e.s(["Settings", 0, t], 166794);
});

  Z.define(179490, e => {
  "use strict";

  function r(e) {
    if ("string" != typeof e) return "";
    let r = e.replace(/\s+/g, " ").trim();
    return "null" === r || "undefined" === r ? "" : r;
  }
  function t(e) {
    if (!e || null == e.lat || null == e.lng || "" === e.lat || "" === e.lng) return !1;
    let r = Number(e.lat),
      t = Number(e.lng);
    return Number.isFinite(r) && Number.isFinite(t) && r >= -90 && r <= 90 && t >= -180 && t <= 180 && (0 !== r || 0 !== t);
  }
  e.s(["getBranchDirectionsUrl", 0, function (e) {
    if (!e) return "https://www.google.com/maps";
    let n = function (e) {
      let t = r(e);
      if (!t) return "";
      try {
        let e = new URL(t);
        return "https:" === e.protocol || "http:" === e.protocol ? e.toString() : "";
      } catch {
        return "";
      }
    }(e.mapLink ?? e.map_link);
    if (n) return n;
    if (t(e)) {
      let r = `${Number(e.lat)},${Number(e.lng)}`;
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r)}`;
    }
    let a = r(e.address) || r(e.address_full) || r(e.name);
    return a ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a)}` : "https://www.google.com/maps";
  }, "hasBranchCoordinates", 0, t]);
});

  Z.define(188947, e => {
  "use strict";

  var r = e.i(843476),
    t = e.i(271645),
    a = e.i(522016),
    n = e.i(88653),
    i = e.i(846932),
    s = e.i(689664),
    l = e.i(716327),
    o = e.i(867927),
    d = e.i(304729),
    c = e.i(994004),
    u = e.i(611568),
    m = e.i(420865),
    p = e.i(475577),
    h = e.i(256102),
    x = e.i(741120),
    f = e.i(263448),
    b = e.i(786563),
    g = e.i(75183),
    y = e.i(263676),
    j = e.i(394556),
    N = e.i(913734),
    k = e.i(98828),
    v = e.i(922386),
    S = e.i(990746),
    C = e.i(652348),
    w = e.i(309808),
    _ = e.i(519957),
    E = e.i(509182);
  e.i(651164);
  var T = e.i(917848),
    O = e.i(273341);
  let P = j.default.branches,
    I = {
      ACCEPTED: {
        label: "Preparing",
        dot: "bg-emerald-500",
        badge: "bg-emerald-50 text-emerald-700"
      },
      COMPLETED: {
        label: "Completed",
        dot: "bg-brand-ink/55",
        badge: "bg-[#F5F5F7] text-brand-ink-light"
      },
      READY: {
        label: "Ready",
        dot: "bg-emerald-500",
        badge: "bg-emerald-50 text-emerald-700"
      },
      PENDING: {
        label: "Received",
        dot: "bg-brand-primary",
        badge: "bg-[#EAF0FF] text-brand-primary"
      },
      PROCESSING: {
        label: "Received",
        dot: "bg-brand-primary",
        badge: "bg-[#EAF0FF] text-brand-primary"
      },
      PLACED: {
        label: "Received",
        dot: "bg-brand-primary",
        badge: "bg-[#EAF0FF] text-brand-primary"
      },
      REJECTED: {
        label: "Rejected",
        dot: "bg-red-500",
        badge: "bg-red-50 text-red-600"
      },
      CANCELLED: {
        label: "Cancelled",
        dot: "bg-red-500",
        badge: "bg-red-50 text-red-600"
      },
      FAILED: {
        label: "Payment failed",
        dot: "bg-red-500",
        badge: "bg-red-50 text-red-600"
      }
    };
  function D(e, r) {
    let t = String(e || "").toUpperCase().trim(),
      a = String(r || "").toLowerCase().trim();
    return "REJECTED" === t || "CANCELLED" === t || "FAILED" === t ? t : "completed" === a ? "COMPLETED" : "ready" === a ? "READY" : t || "PROCESSING";
  }
  function R(e) {
    if (!e) return null;
    let r = String(e).replace(" ", "T");
    /[zZ]|[+-]\d{2}:?\d{2}$/.test(r) || (r += "Z");
    let t = new Date(r);
    return Number.isNaN(t.getTime()) ? null : t;
  }
  function A(e) {
    return e.startsWith("Australia/") ? "en-AU" : "en-NZ";
  }
  function L(e, r = "Pacific/Auckland") {
    let t = R(e);
    return t ? t.toLocaleDateString(A(r), {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: r
    }) : null;
  }
  function $(e) {
    return [...e].sort((e, r) => {
      let t = R(e.createdAt)?.getTime() || 0;
      return (R(r.createdAt)?.getTime() || 0) - t;
    });
  }
  function M(e, r, t) {
    let a = String(e.orderTrackingId || e.id || ""),
      n = (0, _.resolveBranchForOrder)(e, r, t);
    return {
      ...e,
      id: a,
      orderStatus: e.orderStatus || "",
      orderState: e.orderState ?? null,
      createdAt: e.createdAt || null,
      branchName: n?.name || e.hotelName || "Zeta",
      onlineKey: n?.onlineKey || r || null,
      storeId: n?.storeId || e.storeUserId || null,
      timezone: e.timezone || e.timeZone || n?.timezone || "Pacific/Auckland",
      items: e.items || []
    };
  }
  async function F(e, r) {
    let t = await (0, T.aggregateAllOrders)(e, r, {
        page: 1,
        sources: ["accepted"]
      }),
      a = t.errors.filter(e => "accepted" === e.source).length;
    if (t.error || !t.acceptedOrders.length && a >= r.length) throw Error(t.error || "Unable to load active orders.");
    return {
      orders: $(t.acceptedOrders.map(e => M(e, e.onlineKey, r)).filter(e => e.id).filter(e => !["COMPLETED", "COLLECTED", "DELIVERED", "REJECTED", "CANCELLED", "CANCELED", "FAILED"].includes(D(e.orderStatus, e.orderState)))),
      failedBranches: a
    };
  }
  async function z(e, r, t, a = 1) {
    let n = await (0, T.getOrderHistory)(e, r, a);
    if (!n?.success) return {
      orders: [],
      currentPage: a,
      lastPage: a
    };
    let i = Array.isArray(n.data) ? n.data : n.data?.data || [],
      s = n.data?.currentPage || a,
      l = n.data?.lastPage || s;
    return {
      orders: i.map(e => M(e, r, t)).filter(e => e.id),
      currentPage: s,
      lastPage: l
    };
  }
  function B() {
    return (0, r.jsx)("div", {
      className: "grid gap-2.5 md:grid-cols-2",
      role: "status",
      "aria-label": "Loading orders",
      children: [0, 1, 2].map(e => (0, r.jsxs)("div", {
        className: "animate-pulse rounded-[16px] border border-brand-ink/6 bg-white p-3.5",
        children: [(0, r.jsxs)("div", {
          className: "flex justify-between gap-4",
          children: [(0, r.jsxs)("div", {
            className: "space-y-2",
            children: [(0, r.jsx)("div", {
              className: "h-3.5 w-24 rounded bg-[#EBEBEF]"
            }), (0, r.jsx)("div", {
              className: "h-3 w-36 rounded bg-[#F5F5F7]"
            }), (0, r.jsx)("div", {
              className: "h-3 w-28 rounded bg-[#F5F5F7]"
            })]
          }), (0, r.jsxs)("div", {
            className: "space-y-2",
            children: [(0, r.jsx)("div", {
              className: "ml-auto h-5 w-16 rounded-full bg-[#EBEBEF]"
            }), (0, r.jsx)("div", {
              className: "ml-auto h-4 w-12 rounded bg-[#F5F5F7]"
            })]
          })]
        }), (0, r.jsx)("div", {
          className: "mt-3 h-11 rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7]"
        })]
      }, e))
    });
  }
  function K({
    favorites: e
  }) {
    return e.length ? (0, r.jsxs)("section", {
      children: [(0, r.jsxs)("div", {
        className: "mb-2 flex items-center justify-between",
        children: [(0, r.jsx)("h2", {
          className: "text-[13px] font-semibold text-brand-ink-light",
          children: "Saved for next time"
        }), (0, r.jsxs)("span", {
          className: "text-[10px] font-semibold text-brand-primary",
          children: [e.length, " saved"]
        })]
      }), (0, r.jsx)("div", {
        className: "hide-scrollbar -mx-4 flex snap-x snap-proximity scroll-pl-4 gap-2 overscroll-x-contain overflow-x-auto px-4 pb-1 scroll-smooth sm:-mx-6 sm:scroll-pl-6 sm:px-6 lg:-mx-8 lg:scroll-pl-8 lg:px-8",
        children: e.slice(0, 6).map(e => {
          let t = Number(e.total);
          return (0, r.jsxs)(a.default, {
            href: `/order-details?id=${e.id}&storeOnlineKey=${e.onlineKey || ""}`,
            onClick: () => (0, O.haptic)("selection"),
            className: "w-[220px] shrink-0 snap-start touch-manipulation rounded-[15px] border border-brand-primary/14 bg-[#EAF0FF] p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 sm:w-[252px]",
            children: [(0, r.jsxs)("span", {
              className: "flex items-center justify-between gap-2",
              children: [(0, r.jsxs)("span", {
                className: "truncate text-[12px] font-bold",
                children: ["#", String(e.id).slice(-6).toUpperCase()]
              }), (0, r.jsx)(b.Star, {
                className: "h-3.5 w-3.5 fill-brand-primary text-brand-primary",
                strokeWidth: 1.8,
                "aria-hidden": "true"
              })]
            }), (0, r.jsx)("span", {
              className: "mt-1 block truncate text-[10px] text-brand-ink-light",
              children: e.branchName || "Zeta"
            }), (0, r.jsx)("span", {
              className: "mt-2 block truncate text-[11px] font-medium",
              children: e.itemSummary || "Past Zeta order"
            }), (0, r.jsxs)("span", {
              className: "mt-2 flex items-center justify-between text-[11px] font-semibold text-brand-primary",
              children: [Number.isFinite(t) ? `$${t.toFixed(2)}` : "View receipt", (0, r.jsx)(o.ChevronRight, {
                className: "h-4 w-4",
                strokeWidth: 2.2,
                "aria-hidden": "true"
              })]
            })]
          }, e.id);
        })
      })]
    }) : null;
  }
  function Y({
    order: e,
    expanded: t,
    favorite: s,
    onToggle: c,
    onToggleFavorite: u,
    onRate: p
  }) {
    var h;
    let x = String(e.id),
      f = x.length > 6 ? x.slice(-6).toUpperCase() : x.toUpperCase(),
      g = D(e.orderStatus, e.orderState),
      y = I[D(h = e.orderStatus, e.orderState)] || {
        label: String(h || "Updating").toLowerCase(),
        dot: "bg-brand-ink/30",
        badge: "bg-[#F5F5F7] text-brand-ink-light"
      },
      j = Array.isArray(e.items) ? e.items : [],
      N = Number(e.total),
      k = e.timezone || "Pacific/Auckland",
      S = L(e.createdAt, k),
      C = function (e, r = "Pacific/Auckland") {
        let t = R(e);
        return t ? t.toLocaleTimeString(A(r), {
          hour: "numeric",
          minute: "2-digit",
          hour12: !0,
          timeZone: r
        }) : null;
      }(e.createdAt, k),
      w = j.reduce((e, r) => e + Math.max(1, Math.round(Number(r.product_count || r.count || 1))), 0),
      _ = e.orderType ? String(e.orderType).replace(/^ONLINE_/, "").replace(/_/g, " ").toLowerCase() : "",
      T = "FAILED" === g,
      P = ["ACCEPTED", "COMPLETED", "READY"].includes(g);
    return (0, r.jsxs)("article", {
      className: `overflow-hidden rounded-[16px] border bg-white ${t ? "border-brand-primary/24" : "border-brand-ink/8"}`,
      children: [(0, r.jsxs)("div", {
        className: "p-3.5",
        children: [(0, r.jsxs)("div", {
          className: "flex items-start justify-between gap-3",
          children: [(0, r.jsxs)("div", {
            className: "min-w-0 flex-1",
            children: [(0, r.jsxs)("div", {
              className: "flex flex-wrap items-center gap-1.5",
              children: [(0, r.jsxs)("h3", {
                className: "text-[15px] font-bold tracking-[-0.01em]",
                children: ["Order #", f]
              }), e.shortId ? (0, r.jsx)("span", {
                className: "rounded-[5px] bg-brand-ink px-1.5 py-0.5 text-[8px] font-bold text-white",
                children: e.shortId
              }) : null]
            }), (0, r.jsxs)("p", {
              className: "mt-1.5 flex items-center gap-1.5 text-[11px] text-brand-ink-light",
              children: [(0, r.jsx)(m.MapPin, {
                className: "h-3.5 w-3.5 shrink-0",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), (0, r.jsx)("span", {
                className: "truncate",
                children: e.branchName || "Zeta"
              })]
            }), S ? (0, r.jsxs)("p", {
              className: "mt-1 flex items-center gap-1.5 text-[11px] text-brand-ink-light",
              children: [(0, r.jsx)(d.Clock3, {
                className: "h-3.5 w-3.5 shrink-0",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), S, C ? ` \xb7 ${C}` : ""]
            }) : null]
          }), (0, r.jsxs)("div", {
            className: "shrink-0 text-right",
            children: [(0, r.jsxs)("span", {
              className: `inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-semibold ${y.badge}`,
              children: [(0, r.jsx)("span", {
                className: `h-1.5 w-1.5 rounded-full ${y.dot}`,
                "aria-hidden": "true"
              }), y.label]
            }), Number.isFinite(N) ? (0, r.jsxs)("p", {
              className: "mt-2 text-[17px] font-extrabold leading-none tabular-nums",
              children: ["$", N.toFixed(2)]
            }) : null]
          })]
        }), (0, r.jsxs)("button", {
          type: "button",
          onClick: () => {
            (0, O.haptic)("selection"), c();
          },
          "aria-expanded": t,
          className: "mt-3 flex min-h-12 w-full touch-manipulation items-center gap-2 rounded-[12px] bg-[#F5F5F7] px-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40",
          children: [(0, r.jsx)("span", {
            className: "grid h-6 min-w-6 shrink-0 place-items-center rounded-[8px] bg-white px-1.5 text-[10px] font-bold text-brand-primary",
            children: w || j.length || "—"
          }), (0, r.jsx)("span", {
            className: "min-w-0 flex-1 truncate text-[11px] font-medium text-brand-ink-light",
            children: (0, v.summarizeOrderItems)(e)
          }), (0, r.jsx)(l.ChevronDown, {
            className: `h-4 w-4 shrink-0 text-brand-ink/35 transition-transform ${t ? "rotate-180" : ""}`,
            strokeWidth: 2,
            "aria-hidden": "true"
          })]
        })]
      }), (0, r.jsx)(n.AnimatePresence, {
        initial: !1,
        children: t ? (0, r.jsx)(i.motion.div, {
          initial: {
            height: 0,
            opacity: 0
          },
          animate: {
            height: "auto",
            opacity: 1
          },
          exit: {
            height: 0,
            opacity: 0
          },
          transition: {
            duration: .18,
            ease: "easeOut"
          },
          className: "overflow-hidden",
          children: (0, r.jsxs)("div", {
            className: "border-t border-brand-ink/8 px-3.5 py-3",
            children: [j.length ? (0, r.jsx)("div", {
              className: "divide-y divide-brand-ink/8",
              children: j.map((e, t) => {
                var a;
                let n,
                  i = Math.max(1, Math.round(Number(e.product_count || e.count || 1))),
                  s = Number(e.price),
                  l = (a = Number.isFinite(s) ? s * i : null, null != (n = e?.lineTotal) && "" !== n && Number.isFinite(Number(n)) ? Number(n) : a),
                  o = (e.modifiers || []).filter(e => e.addOnName).map(e => e.addOnName).join(", ");
                return (0, r.jsxs)("div", {
                  className: "flex gap-3 py-2.5 first:pt-0 last:pb-0",
                  children: [(0, r.jsxs)("span", {
                    className: "grid h-7 min-w-7 shrink-0 place-items-center rounded-[9px] bg-[#F5F5F7] px-1 text-[10px] font-bold text-brand-ink-light",
                    children: [i, "×"]
                  }), (0, r.jsxs)("span", {
                    className: "min-w-0 flex-1",
                    children: [(0, r.jsx)("span", {
                      className: "block text-[12px] font-semibold",
                      children: e.productName || "Order item"
                    }), [e.size, o].filter(Boolean).length ? (0, r.jsx)("span", {
                      className: "mt-0.5 block text-[10px] leading-snug text-brand-ink-light",
                      children: [e.size, o].filter(Boolean).join(" · ")
                    }) : null]
                  }), null !== l ? (0, r.jsxs)("span", {
                    className: "shrink-0 text-[12px] font-semibold tabular-nums",
                    children: ["$", l.toFixed(2)]
                  }) : null]
                }, `${e.productItemId || e.productName || "item"}-${t}`);
              })
            }) : (0, r.jsx)("p", {
              className: "text-[11px] text-brand-ink-light",
              children: "Open the receipt for the complete order breakdown."
            }), (0, r.jsxs)("div", {
              className: "mt-3 flex flex-wrap gap-1.5",
              children: [_ ? (0, r.jsx)("span", {
                className: "rounded-full bg-[#F5F5F7] px-2.5 py-1 text-[10px] font-medium capitalize text-brand-ink-light",
                children: _
              }) : null, e.scheduledTime ? (0, r.jsxs)("span", {
                className: "rounded-full bg-[#F5F5F7] px-2.5 py-1 text-[10px] font-medium text-brand-ink-light",
                children: ["Scheduled ", L(e.scheduledTime, k)]
              }) : null, (0, E.getPaymentPills)(e).map(e => (0, r.jsxs)("span", {
                className: `rounded-full px-2.5 py-1 text-[10px] font-medium ${e.paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`,
                children: [e.label, ": ", e.value]
              }, e.key))]
            }), (0, r.jsxs)("div", {
              className: "mt-3 grid grid-cols-[44px_minmax(0,1fr)_minmax(0,1fr)] gap-2",
              children: [(0, r.jsx)("button", {
                type: "button",
                onClick: () => {
                  (0, O.haptic)("selection"), u();
                },
                "aria-label": s ? "Remove favourite order" : "Save favourite order",
                "aria-pressed": s,
                className: `grid h-12 touch-manipulation place-items-center rounded-[12px] border ${s ? "border-brand-primary bg-[#EAF0FF] text-brand-primary" : "border-brand-ink/10 bg-white text-brand-ink-light"}`,
                children: (0, r.jsx)(b.Star, {
                  className: `h-[18px] w-[18px] ${s ? "fill-current" : ""}`,
                  strokeWidth: 1.9,
                  "aria-hidden": "true"
                })
              }), T ? (0, r.jsx)("span", {
                className: "flex h-12 items-center justify-center rounded-[12px] bg-red-50 text-[11px] font-semibold text-red-600",
                children: "Payment failed"
              }) : (0, r.jsxs)(a.default, {
                href: `/track?id=${x}&storeOnlineKey=${e.onlineKey || ""}`,
                onClick: () => (0, O.haptic)("selection"),
                className: "flex h-12 touch-manipulation items-center justify-center gap-1.5 rounded-[12px] bg-[#F5F5F7] text-[12px] font-semibold text-brand-ink",
                children: [(0, r.jsx)(m.MapPin, {
                  className: "h-4 w-4 text-brand-primary",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), "Track"]
              }), (0, r.jsxs)(a.default, {
                href: `/order-details?id=${x}&storeOnlineKey=${e.onlineKey || ""}`,
                onClick: () => (0, O.haptic)("selection"),
                className: "flex h-12 touch-manipulation items-center justify-center gap-1.5 rounded-[12px] bg-brand-primary text-[12px] font-semibold text-white",
                children: ["Receipt", (0, r.jsx)(o.ChevronRight, {
                  className: "h-4 w-4",
                  strokeWidth: 2.2,
                  "aria-hidden": "true"
                })]
              })]
            }), P ? (0, r.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, O.haptic)("selection"), p();
              },
              disabled: null != e.rating,
              className: "mt-2 flex min-h-11 w-full touch-manipulation items-center justify-center gap-1.5 rounded-[12px] bg-amber-50 text-[11px] font-semibold text-amber-700 disabled:opacity-100",
              children: [(0, r.jsx)(b.Star, {
                className: `h-4 w-4 ${null != e.rating ? "fill-current" : ""}`,
                strokeWidth: 1.9,
                "aria-hidden": "true"
              }), null != e.rating ? `Rated ${e.rating}/5` : "Rate this order"]
            }) : null]
          })
        }) : null
      })]
    });
  }
  e.s(["default", 0, function () {
    let {
        user: e,
        loading: l,
        openAccountDrawer: o
      } = (0, S.useAuth)(),
      {
        currentBranch: d,
        isDetecting: b,
        setBranch: j,
        branches: E
      } = (0, C.useBranch)(),
      T = (0, w.useToast)(),
      [I, D] = (0, t.useState)([]),
      [R, A] = (0, t.useState)(!0),
      [L, M] = (0, t.useState)(""),
      G = (0, t.useRef)(!1),
      U = (0, t.useRef)(0),
      [Z, H] = (0, t.useState)([]),
      [W, V] = (0, t.useState)(!1),
      [q, J] = (0, t.useState)(!1),
      [X, Q] = (0, t.useState)(""),
      [ee, er] = (0, t.useState)(1),
      [et, ea] = (0, t.useState)(1),
      en = (0, t.useRef)(!1),
      ei = (0, t.useRef)(0),
      [es, el] = (0, t.useState)("branch"),
      [eo, ed] = (0, t.useState)(null),
      [ec, eu] = (0, t.useState)(null),
      [em, ep] = (0, t.useState)(!1),
      [eh, ex] = (0, t.useState)([]),
      ef = (0, t.useMemo)(() => E?.length ? E : P, [E]),
      eb = (0, t.useMemo)(() => {
        let e = new Set();
        return ef.filter(r => {
          let t = String(r?.onlineKey || "").trim();
          return !(!t || r?.status === "coming-soon" || e.has(t)) && (e.add(t), !0);
        });
      }, [ef]),
      eg = (0, t.useMemo)(() => eb.map(e => String(e.onlineKey)).sort().join("|"), [eb]),
      ey = d?.onlineKey;
    async function ej() {
      if (!e?.uid || !eb.length) return;
      G.current = !0;
      let r = ++U.current;
      A(!0), M("");
      try {
        let t = await F(e.uid, eb);
        if (r !== U.current) return;
        D(t.orders), t.failedBranches > 0 && M(`Orders from ${t.failedBranches} ${1 === t.failedBranches ? "store are" : "stores are"} temporarily unavailable. Results may be incomplete.`);
      } catch {
        if (r !== U.current) return;
        M("Orders could not be loaded."), T.error("Unable to load orders. Please try again.", {
          title: "Load failed"
        });
      } finally {
        r === U.current && A(!1);
      }
    }
    async function eN(r) {
      if (!e?.uid) return;
      en.current = !0;
      let t = ++ei.current;
      V(!0), Q("");
      try {
        let a = await z(e.uid, r, ef, 1);
        if (t !== ei.current) return;
        H($(a.orders)), er(a.currentPage), ea(a.lastPage);
      } catch {
        if (t !== ei.current) return;
        Q("Order history could not be loaded."), T.error("Unable to load order history. Please try again.", {
          title: "Load failed"
        });
      } finally {
        t === ei.current && V(!1);
      }
    }
    async function ek() {
      if (!e?.uid || !ey || W || q || ee >= et) return;
      let r = ++ei.current;
      J(!0);
      try {
        let t = await z(e.uid, ey, ef, ee + 1);
        if (r !== ei.current) return;
        H(e => {
          let r = new Set(e.map(e => String(e.id)));
          return $([...e, ...t.orders.filter(e => !r.has(String(e.id)))]);
        }), er(t.currentPage), ea(t.lastPage);
      } catch {
        if (r !== ei.current) return;
        T.error("Couldn’t load more orders. Try again.", {
          title: "Load failed"
        });
      } finally {
        r === ei.current && J(!1);
      }
    }
    function ev() {
      ey && ((0, O.haptic)("selection"), "history" === es ? (er(1), ea(1), eN(ey)) : ej());
    }
    if ((0, t.useEffect)(() => {
      ex((0, v.readFavoriteOrders)());
    }, []), (0, t.useEffect)(() => {
      U.current += 1, G.current = !1, D([]), M(""), e?.uid || A(!1);
    }, [eg, e?.uid]), (0, t.useEffect)(() => {
      ei.current += 1, en.current = !1, H([]), Q(""), er(1), ea(1), J(!1), e?.uid || V(!1);
    }, [ey, e?.uid]), (0, t.useEffect)(() => {
      l || b || !e?.uid || !eb.length || G.current || ej();
    }, [l, b, eg, e?.uid]), (0, t.useEffect)(() => {
      "history" === es && !en.current && e?.uid && ey && eN(ey);
    }, [es, ey, e?.uid]), l || b) return (0, r.jsx)(N.default, {
      kind: "orders"
    });
    let eS = I.filter(e => e.onlineKey === ey),
      eC = "history" === es ? Z : "recent" === es ? I : eS,
      ew = "history" === es ? W : R,
      e_ = "history" === es ? X : L,
      eE = "history" === es ? "Full history" : "recent" === es ? "Recent across Zeta" : d?.name || "This store",
      eT = [...I, ...Z].find(e => String(e.id) === ec),
      eO = [{
        key: "branch",
        label: "This store",
        count: eS.length,
        Icon: g.Store
      }, {
        key: "recent",
        label: "Recent",
        count: I.length,
        Icon: p.PackageCheck
      }, {
        key: "history",
        label: "History",
        count: Z.length,
        Icon: c.History
      }];
    return (0, r.jsxs)("div", {
      className: "mx-auto min-h-[70vh] w-full max-w-5xl bg-white pb-6 text-brand-ink",
      children: [(0, r.jsx)("header", {
        className: "px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6 lg:px-8",
        children: (0, r.jsxs)("div", {
          className: "flex items-start justify-between gap-3",
          children: [(0, r.jsxs)("div", {
            children: [(0, r.jsxs)("div", {
              className: "flex items-center gap-2 text-brand-primary",
              children: [(0, r.jsx)(h.ReceiptText, {
                className: "h-4 w-4",
                strokeWidth: 2.1,
                "aria-hidden": "true"
              }), (0, r.jsx)("p", {
                className: "text-[10px] font-bold uppercase tracking-[0.12em]",
                children: "Receipts and updates"
              })]
            }), (0, r.jsx)("h1", {
              className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.025em]",
              children: "Orders"
            }), (0, r.jsx)("p", {
              className: "mt-1.5 text-[12px] text-brand-ink-light",
              children: e ? "Track current orders and revisit past favourites." : "Sign in to see your Zeta orders."
            })]
          }), e ? (0, r.jsx)("button", {
            type: "button",
            onClick: ev,
            disabled: ew || !ey,
            "aria-label": "Refresh orders",
            className: "grid h-11 w-11 shrink-0 touch-manipulation place-items-center rounded-[13px] bg-[#F5F5F7] text-brand-primary disabled:opacity-45",
            children: (0, r.jsx)(x.RefreshCw, {
              className: `h-[18px] w-[18px] ${ew ? "animate-spin" : ""}`,
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          }) : null]
        })
      }), e ? (0, r.jsxs)("main", {
        className: "space-y-4 px-4 sm:px-6 lg:px-8",
        children: [(0, r.jsx)("section", {
          className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] p-1",
          "aria-label": "Order history filters",
          children: (0, r.jsx)("div", {
            className: "grid grid-cols-3 gap-1",
            children: eO.map(({
              key: e,
              label: t,
              count: a,
              Icon: n
            }) => {
              let i = es === e;
              return (0, r.jsxs)("button", {
                type: "button",
                onClick: () => {
                  (0, O.haptic)("selection"), el(e), ed(null);
                },
                "aria-pressed": i,
                className: `flex min-h-12 touch-manipulation items-center justify-center gap-1.5 rounded-[12px] px-1 text-[11px] font-semibold transition-colors ${i ? "bg-white text-brand-primary shadow-sm" : "text-brand-ink-light"}`,
                children: [(0, r.jsx)(n, {
                  className: "h-4 w-4 shrink-0",
                  strokeWidth: i ? 2.2 : 1.8,
                  "aria-hidden": "true"
                }), (0, r.jsx)("span", {
                  children: t
                }), a ? (0, r.jsx)("span", {
                  className: `grid min-w-4 place-items-center rounded-full px-1 text-[8px] leading-4 ${i ? "bg-[#EAF0FF] text-brand-primary" : "bg-white text-brand-ink-light"}`,
                  children: a
                }) : null]
              }, e);
            })
          })
        }), "recent" !== es ? (0, r.jsxs)("button", {
          type: "button",
          onClick: () => {
            (0, O.haptic)("selection"), ep(!0);
          },
          className: "flex min-h-[56px] w-full touch-manipulation items-center gap-3 rounded-[15px] border border-brand-ink/8 bg-white px-3.5 text-left",
          children: [(0, r.jsx)("span", {
            className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-[#EAF0FF] text-brand-primary",
            children: (0, r.jsx)(m.MapPin, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          }), (0, r.jsxs)("span", {
            className: "min-w-0 flex-1",
            children: [(0, r.jsx)("span", {
              className: "block text-[10px] font-medium text-brand-ink-light",
              children: "history" === es ? "History for" : "Current store"
            }), (0, r.jsx)("span", {
              className: "mt-0.5 block truncate text-[13px] font-semibold",
              children: d?.name || "Choose a store"
            })]
          }), (0, r.jsx)("span", {
            className: "text-[11px] font-semibold text-brand-primary",
            children: "Change"
          })]
        }) : null, (0, r.jsx)(K, {
          favorites: eh
        }), (0, r.jsxs)("section", {
          children: [(0, r.jsx)("div", {
            className: "mb-2 flex items-center justify-between",
            children: (0, r.jsxs)("div", {
              children: [(0, r.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: eE
              }), ew ? null : (0, r.jsxs)("p", {
                className: "mt-0.5 text-[10px] text-brand-ink-light",
                children: [eC.length, " order", 1 === eC.length ? "" : "s"]
              })]
            })
          }), e_ ? (0, r.jsxs)("div", {
            className: "mb-2 flex items-center justify-between rounded-[14px] bg-red-50 px-3.5 py-3",
            children: [(0, r.jsx)("p", {
              className: "text-[12px] font-medium text-red-600",
              children: e_
            }), (0, r.jsx)("button", {
              type: "button",
              onClick: ev,
              className: "min-h-11 touch-manipulation rounded-xl px-2 text-[12px] font-semibold text-red-700",
              children: "Retry"
            })]
          }) : null, ew ? (0, r.jsx)(B, {}) : 0 !== eC.length || e_ ? (0, r.jsxs)("div", {
            className: "grid gap-2.5 md:grid-cols-2",
            children: [eC.map(e => (0, r.jsx)(Y, {
              order: e,
              expanded: eo === String(e.id),
              favorite: (0, v.isFavoriteOrder)(eh, e.id),
              onToggle: () => ed(r => r === String(e.id) ? null : String(e.id)),
              onToggleFavorite: () => {
                let r;
                ex((r = (0, v.toggleFavoriteOrder)(e)).favorites), T.success(r.isFavorite ? "Order saved for next time." : "Order removed from saved orders.", {
                  title: r.isFavorite ? "Order saved" : "Order removed"
                });
              },
              onRate: () => eu(String(e.id))
            }, String(e.id))), "history" === es && ee < et ? (0, r.jsxs)("button", {
              type: "button",
              onClick: ek,
              disabled: q,
              className: "flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-[#F5F5F7] text-[13px] font-semibold text-brand-ink disabled:opacity-50 md:col-span-2",
              children: [q ? (0, r.jsx)(u.LoaderCircle, {
                className: "h-4 w-4 animate-spin text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              }) : null, q ? "Loading more" : "Load more orders"]
            }) : null]
          }) : (0, r.jsxs)("div", {
            className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-7 text-center",
            children: [(0, r.jsx)("span", {
              className: "mx-auto grid h-11 w-11 place-items-center rounded-[13px] bg-white text-brand-primary",
              children: (0, r.jsx)(f.ShoppingBag, {
                className: "h-5 w-5",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), (0, r.jsx)("h3", {
              className: "mt-3 text-[16px] font-bold",
              children: "branch" === es ? "No orders at this store" : "No orders yet"
            }), (0, r.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
              children: "branch" === es ? "Try Recent for orders across Zeta, or start something new." : "Your Zeta receipts will appear here after your first order."
            }), (0, r.jsx)(a.default, {
              href: "/menu",
              className: "mt-4 inline-flex h-11 items-center justify-center rounded-[13px] bg-brand-primary px-5 text-[13px] font-semibold text-white",
              children: "Browse menu"
            })]
          })]
        })]
      }) : (0, r.jsxs)("main", {
        className: "px-4 sm:px-6 lg:px-8",
        children: [(0, r.jsxs)("section", {
          className: "mx-auto max-w-xl rounded-[18px] border border-brand-primary/16 bg-[#EAF0FF] p-5 text-center sm:p-7",
          children: [(0, r.jsx)("span", {
            className: "mx-auto grid h-12 w-12 place-items-center rounded-[14px] bg-white text-brand-primary",
            children: (0, r.jsx)(f.ShoppingBag, {
              className: "h-6 w-6",
              strokeWidth: 1.8,
              "aria-hidden": "true"
            })
          }), (0, r.jsx)("h2", {
            className: "mt-3 text-[18px] font-bold",
            children: "Your orders, all together"
          }), (0, r.jsx)("p", {
            className: "mx-auto mt-1 max-w-[280px] text-[12px] leading-relaxed text-brand-ink-light",
            children: "Sign in to track live orders, open receipts and save favourites for next time."
          }), (0, r.jsx)("button", {
            type: "button",
            onClick: () => {
              (0, O.haptic)("selection"), o();
            },
            className: "mt-4 min-h-12 w-full touch-manipulation rounded-[14px] bg-brand-primary text-[14px] font-semibold text-white",
            children: "Sign in to Zeta"
          })]
        }), (0, r.jsx)(a.default, {
          href: "/menu",
          className: "mx-auto mt-2 flex min-h-12 max-w-xl touch-manipulation items-center justify-center rounded-[14px] bg-[#F5F5F7] text-[14px] font-semibold text-brand-ink",
          children: "Browse the menu"
        })]
      }), (0, r.jsx)(n.AnimatePresence, {
        children: em ? (0, r.jsxs)(r.Fragment, {
          children: [(0, r.jsx)(i.motion.button, {
            type: "button",
            "aria-label": "Close store picker",
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            onClick: () => ep(!1),
            className: "fixed inset-0 z-[200] bg-brand-ink/45"
          }), (0, r.jsxs)(i.motion.section, {
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "order-store-picker-title",
            initial: {
              y: "100%"
            },
            animate: {
              y: 0
            },
            exit: {
              y: "100%"
            },
            transition: {
              type: "spring",
              stiffness: 380,
              damping: 38
            },
            className: "fixed bottom-0 left-0 right-0 z-[201] mx-auto w-full max-w-xl overflow-hidden rounded-t-[20px] bg-white pb-[env(safe-area-inset-bottom,0px)] shadow-2xl sm:bottom-4 sm:left-6 sm:right-6 sm:w-auto sm:rounded-[20px]",
            children: [(0, r.jsxs)("div", {
              className: "px-4 pb-2 pt-3",
              children: [(0, r.jsx)("div", {
                className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/12"
              }), (0, r.jsxs)("div", {
                className: "mt-3 flex items-center justify-between",
                children: [(0, r.jsxs)("div", {
                  children: [(0, r.jsx)("h2", {
                    id: "order-store-picker-title",
                    className: "text-[19px] font-bold",
                    children: "Choose a store"
                  }), (0, r.jsx)("p", {
                    className: "mt-0.5 text-[11px] text-brand-ink-light",
                    children: "See receipts for another Zeta branch."
                  })]
                }), (0, r.jsx)("button", {
                  type: "button",
                  onClick: () => ep(!1),
                  "aria-label": "Close",
                  className: "grid h-11 w-11 touch-manipulation place-items-center rounded-[11px] bg-[#F5F5F7]",
                  children: (0, r.jsx)(y.X, {
                    className: "h-4 w-4",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  })
                })]
              })]
            }), (0, r.jsx)("div", {
              className: "max-h-[55vh] overflow-y-auto border-t border-brand-ink/8 px-2 py-2",
              children: ef.filter(e => "open" === e.status && e.onlineKey).map(e => {
                let t = e.onlineKey === ey;
                return (0, r.jsxs)("button", {
                  type: "button",
                  onClick: () => {
                    ep(!1), e.onlineKey !== ey && ((0, O.haptic)("selection"), j(e), en.current = !1, H([]), er(1), ea(1), el("branch"), ed(null));
                  },
                  className: `flex min-h-[60px] w-full touch-manipulation items-center gap-3 rounded-[14px] px-3 text-left ${t ? "bg-[#EAF0FF]" : "bg-white active:bg-[#F5F5F7]"}`,
                  children: [(0, r.jsx)("span", {
                    className: `grid h-9 w-9 shrink-0 place-items-center rounded-[11px] ${t ? "bg-brand-primary text-white" : "bg-[#F5F5F7] text-brand-ink-light"}`,
                    children: t ? (0, r.jsx)(s.Check, {
                      className: "h-4 w-4",
                      strokeWidth: 2.5,
                      "aria-hidden": "true"
                    }) : (0, r.jsx)(g.Store, {
                      className: "h-4 w-4",
                      strokeWidth: 2,
                      "aria-hidden": "true"
                    })
                  }), (0, r.jsxs)("span", {
                    className: "min-w-0 flex-1",
                    children: [(0, r.jsx)("span", {
                      className: `block text-[14px] font-semibold ${t ? "text-brand-primary" : "text-brand-ink"}`,
                      children: e.name
                    }), (0, r.jsx)("span", {
                      className: "mt-0.5 block truncate text-[10px] text-brand-ink-light",
                      children: e.address
                    })]
                  })]
                }, e.id || e.onlineKey);
              })
            })]
          })]
        }) : null
      }), (0, r.jsx)(n.AnimatePresence, {
        children: ec && eT ? (0, r.jsxs)(r.Fragment, {
          children: [(0, r.jsx)(i.motion.button, {
            type: "button",
            "aria-label": "Close rating",
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            onClick: () => eu(null),
            className: "fixed inset-0 z-[210] bg-brand-ink/45"
          }), (0, r.jsxs)(i.motion.section, {
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "rate-order-title",
            initial: {
              y: "100%"
            },
            animate: {
              y: 0
            },
            exit: {
              y: "100%"
            },
            transition: {
              type: "spring",
              stiffness: 380,
              damping: 38
            },
            className: "fixed bottom-0 left-0 right-0 z-[211] mx-auto w-full max-w-xl rounded-t-[20px] bg-white px-4 pb-[calc(20px+env(safe-area-inset-bottom,0px))] pt-3 shadow-2xl sm:bottom-4 sm:left-6 sm:right-6 sm:w-auto sm:rounded-[20px] sm:px-6",
            children: [(0, r.jsx)("div", {
              className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/12"
            }), (0, r.jsxs)("div", {
              className: "mb-4 mt-3 flex items-start justify-between",
              children: [(0, r.jsxs)("div", {
                children: [(0, r.jsx)("h2", {
                  id: "rate-order-title",
                  className: "text-[19px] font-bold",
                  children: "How was your order?"
                }), (0, r.jsxs)("p", {
                  className: "mt-0.5 text-[11px] text-brand-ink-light",
                  children: ["Order #", String(ec).slice(-6).toUpperCase(), " · ", eT.branchName]
                })]
              }), (0, r.jsx)("button", {
                type: "button",
                onClick: () => eu(null),
                "aria-label": "Close",
                className: "grid h-11 w-11 touch-manipulation place-items-center rounded-[11px] bg-[#F5F5F7]",
                children: (0, r.jsx)(y.X, {
                  className: "h-4 w-4",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              })]
            }), (0, r.jsx)("div", {
              className: "[&_button]:min-h-11 [&_button]:touch-manipulation [&_textarea]:min-h-24 [&_textarea]:text-base",
              children: (0, r.jsx)(k.RatingForm, {
                orderId: ec,
                firebaseUid: e?.uid,
                storePosOwnerId: (0, _.resolveBranchForOrder)(eT, eT.onlineKey, ef)?.storeId || eT.storeId || eT.hotelId,
                items: eT.lineItems || eT.orderData || eT.items || [],
                onSuccess: (e, r) => {
                  let t;
                  return t = t => String(t.id) === ec ? {
                    ...t,
                    rating: e,
                    feedback: r
                  } : t, void (D(e => e.map(t)), H(e => e.map(t)), window.setTimeout(() => eu(null), 1e3));
                }
              })
            })]
          })]
        }) : null
      })]
    });
  }]);
});

  Z.define(196148, e => {
  "use strict";

  var a = e.i(843476),
    t = e.i(522016),
    r = e.i(853138),
    s = e.i(447061),
    n = e.i(867927),
    i = e.i(626102),
    l = e.i(621999),
    o = e.i(584026),
    c = e.i(799552),
    d = e.i(273341);
  let m = [{
    Icon: o.ShieldCheck,
    title: "Zeta states its food is fully halal",
    body: "No current allergen document has been supplied for this ordering app. Ask the selected store if you need current preparation or supplier information."
  }, {
    Icon: i.Leaf,
    title: "Dietary choices vary by item",
    body: "The live menu includes vegetarian and falafel options, seafood choices, salads, dips, warm pita and slow-cooked proteins."
  }, {
    Icon: l.MessageCircleMore,
    title: "Ask before ordering",
    body: "For allergies, cross-contact or preparation questions, contact the selected Zeta store before placing an order."
  }];
  e.s(["default", 0, function () {
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsx)(c.MobilePageHeader, {
        icon: s.BadgeInfo,
        kicker: "Food information",
        title: "Allergens",
        description: "Check ingredients and preparation with the selected store before ordering."
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[720px] space-y-4 px-4 sm:px-6",
        children: [(0, a.jsxs)("section", {
          className: "flex gap-3 rounded-[16px] bg-amber-50 p-3.5 text-amber-900",
          children: [(0, a.jsx)(r.AlertTriangle, {
            className: "mt-0.5 h-5 w-5 shrink-0",
            strokeWidth: 2,
            "aria-hidden": "true"
          }), (0, a.jsxs)("div", {
            children: [(0, a.jsx)("h2", {
              className: "text-[13px] font-bold",
              children: "Allergen-free preparation cannot be guaranteed"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-amber-900/72",
              children: "Zeta kitchens handle multiple ingredients and allergens. Product recipes and suppliers can also change."
            })]
          })]
        }), (0, a.jsx)("section", {
          className: "grid gap-2 md:grid-cols-3",
          children: m.map(({
            Icon: e,
            title: t,
            body: r
          }) => (0, a.jsxs)("article", {
            className: "flex gap-3 rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3.5",
            children: [(0, a.jsx)("span", {
              className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
              children: (0, a.jsx)(e, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), (0, a.jsxs)("div", {
              children: [(0, a.jsx)("h2", {
                className: "text-[13px] font-semibold leading-snug",
                children: t
              }), (0, a.jsx)("p", {
                className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                children: r
              })]
            })]
          }, t))
        }), (0, a.jsx)("section", {
          children: (0, a.jsxs)(t.default, {
            href: "/contact",
            onClick: () => (0, d.haptic)("selection"),
            className: "flex min-h-[52px] w-full touch-manipulation items-center justify-between rounded-[14px] bg-brand-primary px-3.5 text-[13px] font-semibold text-white",
            children: ["Contact Zeta", (0, a.jsx)(n.ChevronRight, {
              className: "h-4 w-4",
              strokeWidth: 2.2,
              "aria-hidden": "true"
            })]
          })
        })]
      })]
    });
  }]);
});

  Z.define(196699, e => {
  "use strict";

  let t = (0, e.i(456420).default)("moon", [["path", {
    d: "M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401",
    key: "kfwtm"
  }]]);
  e.s(["Moon", 0, t], 196699);
});

  Z.define(199383, e => {
  "use strict";

  let t = (0, e.i(456420).default)("cake", [["path", {
    d: "M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8",
    key: "1w3rig"
  }], ["path", {
    d: "M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1",
    key: "n2jgmb"
  }], ["path", {
    d: "M2 21h20",
    key: "1nyx9w"
  }], ["path", {
    d: "M7 8v3",
    key: "1qtyvj"
  }], ["path", {
    d: "M12 8v3",
    key: "hwp4zt"
  }], ["path", {
    d: "M17 8v3",
    key: "1i6e5u"
  }], ["path", {
    d: "M7 4h.01",
    key: "1bh4kh"
  }], ["path", {
    d: "M12 4h.01",
    key: "1ujb9j"
  }], ["path", {
    d: "M17 4h.01",
    key: "1upcoc"
  }]]);
  e.s(["Cake", 0, t], 199383);
});

  Z.define(200416, e => {
  "use strict";

  let t = (0, e.i(456420).default)("ticket-percent", [["path", {
    d: "M2 9a3 3 0 1 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 1 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z",
    key: "1l48ns"
  }], ["path", {
    d: "M9 9h.01",
    key: "1q5me6"
  }], ["path", {
    d: "m15 9-6 6",
    key: "1uzhvr"
  }], ["path", {
    d: "M15 15h.01",
    key: "lqbp3k"
  }]]);
  e.s(["TicketPercent", 0, t], 200416);
});

  Z.define(226091, e => {
  "use strict";

  let t = (0, e.i(456420).default)("file-text", [["path", {
    d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z",
    key: "1oefj6"
  }], ["path", {
    d: "M14 2v5a1 1 0 0 0 1 1h5",
    key: "wfsgrz"
  }], ["path", {
    d: "M10 9H8",
    key: "b1mrlr"
  }], ["path", {
    d: "M16 13H8",
    key: "t4e002"
  }], ["path", {
    d: "M16 17H8",
    key: "z1uh3a"
  }]]);
  e.s(["FileText", 0, t], 226091);
});

  Z.define(235056, e => {
  "use strict";

  var t = e.i(275043);
  e.s(["CircleHelp", () => t.default]);
});

  Z.define(248510, e => {
  "use strict";

  let t = (0, e.i(456420).default)("map-pinned", [["path", {
    d: "M18 8c0 3.613-3.869 7.429-5.393 8.795a1 1 0 0 1-1.214 0C9.87 15.429 6 11.613 6 8a6 6 0 0 1 12 0",
    key: "11u0oz"
  }], ["circle", {
    cx: "12",
    cy: "8",
    r: "2",
    key: "1822b1"
  }], ["path", {
    d: "M8.714 14h-3.71a1 1 0 0 0-.948.683l-2.004 6A1 1 0 0 0 3 22h18a1 1 0 0 0 .948-1.316l-2-6a1 1 0 0 0-.949-.684h-3.712",
    key: "q8zwxj"
  }]]);
  e.s(["MapPinned", 0, t], 248510);
});

  Z.define(253682, e => {
  "use strict";

  let t = (0, e.i(456420).default)("receipt", [["path", {
    d: "M12 17V7",
    key: "pyj7ub"
  }], ["path", {
    d: "M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8",
    key: "1elt7d"
  }], ["path", {
    d: "M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z",
    key: "ycz6yz"
  }]]);
  e.s(["Receipt", 0, t], 253682);
});

  Z.define(256102, e => {
  "use strict";

  let t = (0, e.i(456420).default)("receipt-text", [["path", {
    d: "M13 16H8",
    key: "wsln4y"
  }], ["path", {
    d: "M14 8H8",
    key: "1l3xfs"
  }], ["path", {
    d: "M16 12H8",
    key: "1fr5h0"
  }], ["path", {
    d: "M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z",
    key: "ycz6yz"
  }]]);
  e.s(["ReceiptText", 0, t], 256102);
});

  Z.define(263448, e => {
  "use strict";

  let t = (0, e.i(456420).default)("shopping-bag", [["path", {
    d: "M16 10a4 4 0 0 1-8 0",
    key: "1ltviw"
  }], ["path", {
    d: "M3.103 6.034h17.794",
    key: "awc11p"
  }], ["path", {
    d: "M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z",
    key: "o988cm"
  }]]);
  e.s(["ShoppingBag", 0, t], 263448);
});

  Z.define(263676, e => {
  "use strict";

  let t = (0, e.i(456420).default)("x", [["path", {
    d: "M18 6 6 18",
    key: "1bl5f8"
  }], ["path", {
    d: "m6 6 12 12",
    key: "d8bk6v"
  }]]);
  e.s(["X", 0, t], 263676);
});

  Z.define(274588, e => {
  "use strict";

  let t = (0, e.i(456420).default)("party-popper", [["path", {
    d: "M5.8 11.3 2 22l10.7-3.79",
    key: "gwxi1d"
  }], ["path", {
    d: "M4 3h.01",
    key: "1vcuye"
  }], ["path", {
    d: "M22 8h.01",
    key: "1mrtc2"
  }], ["path", {
    d: "M15 2h.01",
    key: "1cjtqr"
  }], ["path", {
    d: "M22 20h.01",
    key: "1mrys2"
  }], ["path", {
    d: "m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10",
    key: "hbicv8"
  }], ["path", {
    d: "m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17",
    key: "1i94pl"
  }], ["path", {
    d: "m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7",
    key: "1cofks"
  }], ["path", {
    d: "M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z",
    key: "4kbmks"
  }]]);
  e.s(["PartyPopper", 0, t], 274588);
});

  Z.define(274779, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    s = e.i(618566),
    r = e.i(867927),
    n = e.i(611568),
    i = e.i(420865),
    l = e.i(578344),
    o = e.i(566595),
    d = e.i(263676),
    c = e.i(977617),
    m = e.i(652348),
    x = e.i(515746),
    p = e.i(273341),
    h = e.i(179490);
  function u(e, t) {
    let a = e?.onlineKey ? t?.[e.onlineKey]?.shop_image : "";
    if ("string" != typeof a) return "";
    let s = a.trim();
    return s && "null" !== s && "undefined" !== s && s.startsWith("http") ? s : "";
  }
  function b(e, t) {
    let a = t.toLowerCase().trim();
    if (!a) return !0;
    let s = String(e.address || "").match(/\b\d{4}\b/g) || [],
      r = [e.name, e.address, e.notes, ...s, ...(e.tags || [])].filter(Boolean).map(e => String(e).toLowerCase()).join(" ");
    return a.split(/\s+/).filter(Boolean).every(e => r.includes(e));
  }
  function g(e, t) {
    if ("coming-soon" === e.status) return {
      label: "Coming soon",
      detail: "Ordering opens when this store goes live.",
      tone: "quiet"
    };
    let a = e.onlineKey ? t?.[e.onlineKey] : null;
    return a ? a.isOpen && !a.isPaused ? {
      label: "Open",
      detail: "Taking online orders.",
      tone: "live"
    } : {
      label: "Closed",
      detail: "Opens again in the next service window.",
      tone: "quiet"
    } : {
      label: "Checking",
      detail: "Loading the current order window.",
      tone: "quiet"
    };
  }
  let f = {
      live: "bg-emerald-500",
      alert: "bg-red-500",
      quiet: "bg-brand-ink/25"
    },
    j = {
      live: "text-emerald-600",
      alert: "text-red-600",
      quiet: "text-brand-ink-light"
    };
  function y({
    store: e,
    status: a,
    isSelected: s,
    onOrder: r,
    shopImage: n
  }) {
    let l = "open" === e.status && !!e.onlineKey;
    return (0, t.jsxs)("article", {
      className: `overflow-hidden rounded-[16px] border transition-colors ${s ? "border-brand-primary bg-[#EAF0FF]" : "border-brand-ink/8 bg-white"}`,
      children: [n ? (0, t.jsx)("div", {
        className: "relative h-[104px] w-full bg-[#ECECEF] sm:h-[132px]",
        children: (0, t.jsx)(c.default, {
          src: n,
          alt: "",
          fill: !0,
          sizes: "(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 460px",
          className: "object-cover",
          context: "StoresPage.shopImage",
          meta: {
            branch: e.id
          }
        })
      }) : null, (0, t.jsxs)("div", {
        className: "p-3.5",
        children: [(0, t.jsxs)("div", {
          className: "flex items-start justify-between gap-3",
          children: [(0, t.jsxs)("div", {
            className: "min-w-0",
            children: [(0, t.jsx)("h3", {
              className: "text-[16px] font-semibold leading-tight tracking-[-0.01em] text-brand-ink",
              children: e.name
            }), (0, t.jsxs)("p", {
              className: `mt-1 flex items-center gap-1.5 text-[12px] font-medium ${j[a.tone]}`,
              children: [(0, t.jsx)("span", {
                className: `h-1.5 w-1.5 shrink-0 rounded-full ${f[a.tone]}`,
                "aria-hidden": "true"
              }), a.label]
            })]
          }), (0, t.jsxs)("div", {
            className: "flex shrink-0 items-center gap-1.5",
            children: [void 0 !== e.distance ? (0, t.jsxs)("span", {
              className: "rounded-full bg-[#F5F5F7] px-2 py-0.5 text-[11px] font-medium text-brand-ink-light",
              children: [e.distance.toFixed(1), " km"]
            }) : null, s ? (0, t.jsx)("span", {
              className: "rounded-full bg-brand-primary px-2 py-0.5 text-[10px] font-semibold text-white",
              children: "Selected"
            }) : null]
          })]
        }), (0, t.jsxs)("p", {
          className: "mt-2 flex gap-1.5 text-[12px] leading-[1.45] text-brand-ink-light",
          children: [(0, t.jsx)(i.MapPin, {
            className: "mt-[2px] h-[14px] w-[14px] shrink-0",
            strokeWidth: 2,
            "aria-hidden": "true"
          }), (0, t.jsx)("span", {
            children: e.address
          })]
        }), e.notes ? (0, t.jsx)("p", {
          className: "mt-1.5 text-[11px] leading-[1.45] text-brand-ink-light",
          children: e.notes
        }) : null, (0, t.jsxs)("div", {
          className: "mt-3 flex items-center gap-2",
          children: [l ? (0, t.jsx)("button", {
            type: "button",
            onClick: () => r(e),
            className: "flex min-h-12 flex-1 touch-manipulation items-center justify-center rounded-[12px] bg-brand-primary px-3 text-[14px] font-semibold text-white transition-transform active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
            children: s ? "Order now" : "Order here"
          }) : (0, t.jsx)("span", {
            className: "flex min-h-12 flex-1 items-center justify-center rounded-[12px] bg-[#F5F5F7] px-3 py-2 text-center text-[12px] font-medium leading-snug text-brand-ink-light",
            children: a.detail
          }), (0, t.jsx)("a", {
            href: (0, h.getBranchDirectionsUrl)(e),
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: () => (0, p.haptic)("selection"),
            className: "flex min-h-12 shrink-0 touch-manipulation items-center justify-center rounded-[12px] border border-brand-ink/12 px-4 text-[14px] font-semibold text-brand-ink transition-colors active:bg-[#F5F5F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
            children: "Directions"
          })]
        })]
      })]
    });
  }
  e.s(["default", 0, function () {
    let e = (0, s.useRouter)(),
      {
        branches: c,
        currentBranch: f,
        setBranch: j,
        branchStatuses: N,
        allConfigs: k
      } = (0, m.useBranch)(),
      [v, w] = (0, a.useState)(""),
      [_, S] = (0, a.useState)(null),
      [C, P] = (0, a.useState)(!1),
      [L, $] = (0, a.useState)("");
    (0, a.useEffect)(() => {
      let e = !1;
      return (0, x.getCurrentPosition)().then(t => {
        e || S({
          latitude: t.latitude,
          longitude: t.longitude
        });
      }).catch(() => {}), () => {
        e = !0;
      };
    }, []);
    let M = (0, a.useMemo)(() => c.map(e => _ && (0, h.hasBranchCoordinates)(e) ? {
        ...e,
        distance: Math.round(10 * (0, x.haversineDistance)(_.latitude, _.longitude, Number(e.lat), Number(e.lng))) / 10
      } : e), [c, _]),
      z = (0, a.useMemo)(() => {
        let e = M.filter(e => "open" === e.status).filter(e => b(e, v));
        return _ ? e.sort((e, t) => (e.distance ?? 1 / 0) - (t.distance ?? 1 / 0)) : e;
      }, [v, M, _]),
      O = (0, a.useMemo)(() => M.filter(e => "coming-soon" === e.status).filter(e => b(e, v)), [v, M]),
      B = v.trim().length > 0,
      I = z.length + O.length > 0,
      F = (0, a.useCallback)(async () => {
        (0, p.haptic)("selection"), P(!0), $("");
        try {
          let e = await (0, x.getCurrentPosition)();
          S({
            latitude: e.latitude,
            longitude: e.longitude
          });
        } catch {
          $("Location unavailable. Allow location access and try again.");
        } finally {
          P(!1);
        }
      }, []),
      D = (0, a.useCallback)(t => {
        "open" === t.status && t.onlineKey && ((0, p.haptic)("medium"), j(t), e.push("/menu"));
      }, [e, j]);
    return (0, t.jsxs)("div", {
      className: "mx-auto min-h-full w-full max-w-5xl bg-white",
      children: [(0, t.jsxs)("section", {
        className: "px-4 pb-3 pt-5 sm:px-6 sm:pb-5 sm:pt-6 lg:px-8",
        children: [(0, t.jsx)("h1", {
          className: "text-[28px] font-semibold leading-none tracking-[-0.03em] text-brand-ink sm:text-[30px]",
          children: "Stores"
        }), (0, t.jsxs)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light",
          children: [c.length, " connected location", 1 === c.length ? "" : "s"]
        }), (0, t.jsxs)("div", {
          className: "mt-4 flex items-center gap-2",
          children: [(0, t.jsxs)("label", {
            className: "flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-[14px] bg-[#F5F5F7] px-3 focus-within:ring-2 focus-within:ring-brand-primary/40",
            children: [(0, t.jsx)(o.Search, {
              className: "h-[18px] w-[18px] shrink-0 text-brand-ink-light",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), (0, t.jsx)("input", {
              type: "search",
              value: v,
              onChange: e => w(e.target.value),
              "aria-label": "Search stores",
              placeholder: "Suburb, store or postcode",
              autoComplete: "off",
              spellCheck: !1,
              enterKeyHint: "search",
              className: "min-w-0 flex-1 bg-transparent text-base font-medium text-brand-ink outline-none placeholder:text-brand-ink-light sm:text-[14px]"
            }), v ? (0, t.jsx)("button", {
              type: "button",
              onClick: () => w(""),
              "aria-label": "Clear store search",
              className: "flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-[11px] bg-white text-brand-ink",
              children: (0, t.jsx)(d.X, {
                className: "h-4 w-4",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            }) : null]
          }), (0, t.jsx)("button", {
            type: "button",
            onClick: F,
            disabled: C,
            "aria-label": "Sort stores by distance from me",
            className: "flex h-12 w-12 shrink-0 touch-manipulation items-center justify-center rounded-[14px] bg-[#F5F5F7] text-brand-primary transition-colors active:bg-[#EAF0FF] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
            children: C ? (0, t.jsx)(n.LoaderCircle, {
              className: "h-[18px] w-[18px] animate-spin",
              strokeWidth: 2,
              "aria-hidden": "true"
            }) : (0, t.jsx)(l.Navigation, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          })]
        }), L ? (0, t.jsx)("p", {
          className: "mt-2 text-[12px] leading-[1.4] text-red-600",
          children: L
        }) : _ ? (0, t.jsx)("p", {
          className: "mt-2 text-[12px] text-brand-ink-light",
          children: "Open stores sorted by distance from you."
        }) : null]
      }), (0, t.jsxs)("main", {
        className: "px-4 pb-6 sm:px-6 lg:px-8",
        children: [B && !I ? (0, t.jsxs)("div", {
          className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-8 text-center",
          children: [(0, t.jsx)("h2", {
            className: "text-[17px] font-semibold text-brand-ink",
            children: "No stores found"
          }), (0, t.jsx)("p", {
            className: "mt-2 text-[13px] leading-relaxed text-brand-ink-light",
            children: "Try another suburb, store name or postcode."
          }), (0, t.jsx)("button", {
            type: "button",
            onClick: () => w(""),
            className: "mt-5 h-11 rounded-[14px] bg-brand-primary px-5 text-[14px] font-semibold text-white",
            children: "Show all stores"
          })]
        }) : null, z.length > 0 ? (0, t.jsxs)("section", {
          "aria-label": "Connected Zeta stores",
          children: [(0, t.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Connected stores"
          }), (0, t.jsx)("div", {
            className: "grid gap-3 sm:grid-cols-2",
            children: z.map(e => (0, t.jsx)(y, {
              store: e,
              status: g(e, N),
              isSelected: f?.id === e.id,
              onOrder: D,
              shopImage: u(e, k)
            }, e.id))
          })]
        }) : null, O.length > 0 ? (0, t.jsxs)("section", {
          className: "mt-5",
          "aria-label": "Zeta stores opening soon",
          children: [(0, t.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Opening soon"
          }), (0, t.jsx)("div", {
            className: "grid gap-3 sm:grid-cols-2",
            children: O.map(e => (0, t.jsx)(y, {
              store: e,
              status: g(e, N),
              isSelected: !1,
              onOrder: D,
              shopImage: u(e, k)
            }, e.id))
          })]
        }) : null]
      })]
    });
  }]);
});

  Z.define(275043, e => {
  "use strict";

  let t = (0, e.i(456420).default)("circle-question-mark", [["circle", {
    cx: "12",
    cy: "12",
    r: "10",
    key: "1mglay"
  }], ["path", {
    d: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3",
    key: "1u773s"
  }], ["path", {
    d: "M12 17h.01",
    key: "p32p05"
  }]]);
  e.s(["default", 0, t]);
});

  Z.define(280277, e => {
  "use strict";

  let r = "CART_DISCOUNT",
    t = "FREE_DELIVERY",
    a = "BUY_ONE_GET_ONE_FREE",
    n = "BUY_N_GET_M_FREE";
  function i(e) {
    if (!e) return null;
    let t = e.couponType || e.coupon_type || e.couponDetail?.rules?.type || e.couponDetail?.result?.type || e.couponDetail?.type || null;
    return t ? String(t).toUpperCase() : parseFloat(String(e.discountPercent ?? e.discount ?? 0)) > 0 ? r : null;
  }
  function s(e) {
    let r = i(e);
    return r === a || r === n;
  }
  e.s(["getCouponSummary", 0, function (e) {
    if (!e || !(e.isCouponApplied || e.is_coupon_applied || e.couponDetail)) return null;
    let l = e.couponDetail || {},
      o = l.code || e.couponCode || e.coupon_code || null,
      d = l.name || l.description || null;
    if (!o && !d) return null;
    let c = null;
    if (i(e) === r) {
      let r = parseFloat(String(e.couponDiscountAmount ?? e.coupon_discount_amount ?? NaN)),
        t = parseFloat(String(e.discount ?? 0));
      c = Number.isFinite(r) && r > 0 ? r : t > 0 ? t : null;
    }
    return {
      code: o,
      name: d,
      typeLabel: function (e) {
        switch (i(e)) {
          case r:
            return "Cart discount";
          case "ITEM_DISCOUNT":
            return "Item discount";
          case t:
            return "Free delivery";
          case a:
            return "Buy one get one";
          case n:
            return "Buy & get free";
          default:
            return "Coupon";
        }
      }(e),
      iconKey: s(e) ? "gift" : i(e) === t ? "truck" : "ticket",
      amount: c,
      isFreeItem: s(e)
    };
  }, "getFreeItemView", 0, function (e) {
    if (!e || !(e.isFreeItem || e.is_free_item)) return null;
    let r = parseFloat(String(e.originalPrice ?? e.original_price ?? e.price ?? 0));
    return {
      isFree: !0,
      originalPrice: r > 0 ? r : null
    };
  }, "getLineCouponAmount", 0, function (e) {
    if (!e) return null;
    let r = parseFloat(String(e.couponDiscountAmount ?? e.coupon_discount_amount ?? NaN));
    return Number.isFinite(r) && r > 0 ? r : null;
  }, "isCartLevelCoupon", 0, function (e) {
    return i(e) === r;
  }]);
});

  Z.define(282625, e => {
  "use strict";

  let t = (0, e.i(456420).default)("gift", [["path", {
    d: "M12 7v14",
    key: "1akyts"
  }], ["path", {
    d: "M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8",
    key: "1sqzm4"
  }], ["path", {
    d: "M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5",
    key: "kc0143"
  }], ["rect", {
    x: "3",
    y: "7",
    width: "18",
    height: "4",
    rx: "1",
    key: "1hberx"
  }]]);
  e.s(["Gift", 0, t], 282625);
});

  Z.define(298007, e => {
  "use strict";

  let t = (0, e.i(456420).default)("chef-hat", [["path", {
    d: "M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589 5 5 0 0 0-9.186 0 4 4 0 0 0-2.134 7.588c.411.198.727.585.727 1.041V20a1 1 0 0 0 1 1Z",
    key: "1qvrer"
  }], ["path", {
    d: "M6 17h12",
    key: "1jwigz"
  }]]);
  e.s(["ChefHat", 0, t], 298007);
});

  Z.define(304729, e => {
  "use strict";

  let t = (0, e.i(456420).default)("clock-3", [["circle", {
    cx: "12",
    cy: "12",
    r: "10",
    key: "1mglay"
  }], ["path", {
    d: "M12 6v6h4",
    key: "135r8i"
  }]]);
  e.s(["Clock3", 0, t], 304729);
});

  Z.define(306555, e => {
  "use strict";

  let t = (0, e.i(456420).default)("arrow-down-left", [["path", {
    d: "M17 7 7 17",
    key: "15tmo1"
  }], ["path", {
    d: "M17 17H7V7",
    key: "1org7z"
  }]]);
  e.s(["ArrowDownLeft", 0, t], 306555);
});

  Z.define(328623, e => {
  "use strict";

  let t = (0, e.i(456420).default)("sparkles", [["path", {
    d: "M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z",
    key: "1s2grr"
  }], ["path", {
    d: "M20 2v4",
    key: "1rf3ol"
  }], ["path", {
    d: "M22 4h-4",
    key: "gwowj6"
  }], ["circle", {
    cx: "4",
    cy: "20",
    r: "2",
    key: "6kqj1y"
  }]]);
  e.s(["Sparkles", 0, t], 328623);
});

  Z.define(330084, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    i = e.i(522016),
    s = e.i(618566),
    r = e.i(867927),
    n = e.i(668109),
    l = e.i(875969),
    o = e.i(573474),
    d = e.i(977617),
    c = e.i(652348),
    u = e.i(309808);
  e.i(651164);
  var m = e.i(917848),
    h = e.i(273341),
    p = e.i(376151),
    f = e.i(754675),
    x = e.i(154484);
  e.s(["default", 0, function () {
    let e = (0, s.useRouter)(),
      b = (0, u.useToast)(),
      {
        currentBranch: y,
        restaurantConfig: g
      } = (0, c.useBranch)(),
      [j, v] = (0, a.useState)([]),
      [N, _] = (0, a.useState)({}),
      [w, S] = (0, a.useState)(!1),
      C = (0, a.useCallback)(() => v((0, x.getFavoriteProducts)()), []);
    (0, a.useEffect)(() => (C(), window.addEventListener(x.ZETA_FAVORITES_CHANGED_EVENT, C), () => window.removeEventListener(x.ZETA_FAVORITES_CHANGED_EVENT, C)), [C]);
    let q = y?.onlineKey,
      $ = String(g?.timezone || g?.timeZone || y?.timezone || p.DEFAULT_TIME_ZONE);
    (0, a.useEffect)(() => {
      if (!q) return;
      let e = !1;
      return (0, m.fetchMenu)(q, {
        timeZone: $
      }).then(t => {
        if (e) return;
        let a = {};
        (t?.categories || []).forEach(e => {
          (e.items || []).forEach(e => {
            let t = String(e.itemId ?? e.product_id ?? "");
            if (!t) return;
            let i = Number(e.sale_price ?? e.salePrice);
            a[t] = Number.isFinite(i) ? i : null;
          });
        }), _(a), S(!0);
      }).catch(() => {
        e || S(!0);
      }), () => {
        e = !0;
      };
    }, [q, $]);
    let A = j.length,
      z = (0, a.useMemo)(() => y?.name || "", [y?.name]);
    return (0, t.jsxs)("div", {
      className: "min-h-full bg-white",
      children: [(0, t.jsxs)("header", {
        className: "px-4 pb-2 pt-4",
        children: [(0, t.jsx)("h1", {
          className: "text-[24px] font-semibold leading-none tracking-[-0.03em] text-brand-ink",
          children: "Favourites"
        }), (0, t.jsx)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light",
          children: 0 === A ? "Dishes you save appear here." : `${A} saved${z ? ` \xb7 prices from ${z}` : ""}`
        })]
      }), (0, t.jsxs)("main", {
        className: "px-4 pb-6",
        children: [0 === A ? (0, t.jsxs)("div", {
          className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-10 text-center",
          children: [(0, t.jsx)("span", {
            className: "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-brand-primary",
            children: (0, t.jsx)(l.HeartOff, {
              className: "h-6 w-6",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            })
          }), (0, t.jsx)("h2", {
            className: "text-[16px] font-semibold text-brand-ink",
            children: "No favourites yet"
          }), (0, t.jsx)("p", {
            className: "mx-auto mt-2 max-w-[260px] text-[13px] leading-relaxed text-brand-ink-light",
            children: "Tap the heart on any dish to save it here for next time."
          }), (0, t.jsx)(i.default, {
            href: "/menu",
            className: "mt-5 inline-flex h-11 items-center rounded-[14px] bg-brand-primary px-5 text-[14px] font-semibold text-white",
            children: "Browse the menu"
          })]
        }) : (0, t.jsx)("ul", {
          className: "space-y-2.5",
          children: j.map(a => {
            let i = N[a.id],
              s = void 0 !== i ? i : a.price ?? null,
              r = w && void 0 === i;
            return (0, t.jsx)("li", {
              children: (0, t.jsx)("article", {
                className: `overflow-hidden rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] ${r ? "opacity-70" : ""}`,
                children: (0, t.jsxs)("div", {
                  className: "flex items-center gap-3 p-3",
                  children: [(0, t.jsxs)("button", {
                    type: "button",
                    onClick: () => {
                      let t;
                      return (0, h.haptic)("light"), t = "combo" === a.type ? "combo" : "item", void e.push(`/menu?${t}=${a.slug || ""}&id=${encodeURIComponent(a.id)}`);
                    },
                    "aria-label": `View ${(0, f.sentenceLabel)(a.label)}`,
                    className: "flex min-w-0 flex-1 items-center gap-3 text-left",
                    children: [(0, t.jsxs)("span", {
                      className: "min-w-0 flex-1",
                      children: [(0, t.jsx)("span", {
                        className: "block text-[15px] font-semibold leading-tight text-brand-ink",
                        children: (0, f.sentenceLabel)(a.label)
                      }), (0, t.jsx)("span", {
                        className: "mt-1 block text-[13px] font-semibold text-brand-ink",
                        children: r ? "Not on this store’s menu" : null != s ? `$${Number(s).toFixed(2)}` : "View"
                      })]
                    }), !function (e) {
                      if ("string" != typeof e) return !1;
                      let t = e.trim();
                      return !!t && "null" !== t && "undefined" !== t && (t.startsWith("http") || t.startsWith("/"));
                    }(a.image) ? null : (0, t.jsx)("span", {
                      className: "relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-[13px] bg-white",
                      children: (0, t.jsx)(d.default, {
                        src: a.image,
                        alt: "",
                        fill: !0,
                        sizes: "72px",
                        className: "object-cover",
                        context: "FavouritesPage",
                        meta: {
                          id: a.id
                        }
                      })
                    })]
                  }), (0, t.jsx)("button", {
                    type: "button",
                    onClick: () => {
                      (0, h.haptic)("light"), (0, x.removeFavoriteProduct)(a.id), C(), b.success("Removed from favourites", {
                        title: (0, f.sentenceLabel)(a.label)
                      });
                    },
                    "aria-label": `Remove ${(0, f.sentenceLabel)(a.label)} from favourites`,
                    className: "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-brand-ink-light active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
                    children: (0, t.jsx)(o.Trash2, {
                      className: "h-[17px] w-[17px]",
                      strokeWidth: 2,
                      "aria-hidden": "true"
                    })
                  })]
                })
              })
            }, a.id);
          })
        }), A > 0 ? (0, t.jsxs)(i.default, {
          href: "/menu",
          onClick: () => (0, h.haptic)("selection"),
          className: "mt-4 flex min-h-[56px] w-full items-center gap-3 rounded-[16px] border border-brand-ink/8 bg-white px-3.5 text-left active:scale-[0.99]",
          children: [(0, t.jsx)("span", {
            className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-[#EAF0FF] text-brand-primary",
            children: (0, t.jsx)(n.Heart, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          }), (0, t.jsxs)("span", {
            className: "min-w-0 flex-1",
            children: [(0, t.jsx)("span", {
              className: "block text-[14px] font-semibold text-brand-ink",
              children: "Find more to love"
            }), (0, t.jsx)("span", {
              className: "mt-0.5 block text-[12px] text-brand-ink-light",
              children: "Browse the full Zeta menu"
            })]
          }), (0, t.jsx)(r.ChevronRight, {
            className: "h-5 w-5 shrink-0 text-brand-ink/28",
            strokeWidth: 2,
            "aria-hidden": "true"
          })]
        }) : null]
      })]
    });
  }]);
});

  Z.define(341987, e => {
  "use strict";

  let t = (0, e.i(456420).default)("share-2", [["circle", {
    cx: "18",
    cy: "5",
    r: "3",
    key: "gq8acd"
  }], ["circle", {
    cx: "6",
    cy: "12",
    r: "3",
    key: "w7nqdw"
  }], ["circle", {
    cx: "18",
    cy: "19",
    r: "3",
    key: "1xt0gg"
  }], ["line", {
    x1: "8.59",
    x2: "15.42",
    y1: "13.51",
    y2: "17.49",
    key: "47mynk"
  }], ["line", {
    x1: "15.41",
    x2: "8.59",
    y1: "6.51",
    y2: "10.49",
    key: "1n3mei"
  }]]);
  e.s(["Share2", 0, t], 341987);
});

  Z.define(357217, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(226091),
    r = e.i(799552),
    i = e.i(116579);
  e.s(["default", 0, function () {
    return (0, t.jsx)(r.MobileReadingPage, {
      icon: a.FileText,
      kicker: "Legal",
      title: "Terms",
      description: "Terms for using Zeta ordering, membership and related services.",
      updated: "April 2026",
      intro: "Welcome to Zeta Greek Kitchen. These Terms and Conditions govern your use of our website, app, services and products. Please read them carefully before using our services.",
      sections: i.TERMS_SECTIONS
    });
  }]);
});

  Z.define(367784, e => {
  "use strict";

  let t = (0, e.i(456420).default)("log-out", [["path", {
    d: "m16 17 5-5-5-5",
    key: "1bji2h"
  }], ["path", {
    d: "M21 12H9",
    key: "dn1m92"
  }], ["path", {
    d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4",
    key: "1uf3rs"
  }]]);
  e.s(["LogOut", 0, t], 367784);
});

  Z.define(370812, e => {
  "use strict";

  let t = (0, e.i(456420).default)("bell", [["path", {
    d: "M10.268 21a2 2 0 0 0 3.464 0",
    key: "vwvbt9"
  }], ["path", {
    d: "M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326",
    key: "11g9vi"
  }]]);
  e.s(["Bell", 0, t], 370812);
});

  Z.define(375775, e => {
  "use strict";

  let t = (0, e.i(456420).default)("arrow-up-right", [["path", {
    d: "M7 7h10v10",
    key: "1tivn9"
  }], ["path", {
    d: "M7 17 17 7",
    key: "1vkiza"
  }]]);
  e.s(["ArrowUpRight", 0, t], 375775);
});

  Z.define(376151, e => {
  "use strict";

  let t = "Pacific/Auckland";
  function r(e) {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: e
      }).format(new Date(0)), !0;
    } catch {
      return !1;
    }
  }
  function i(e, n = t) {
    let a = String(e || "").trim();
    return a && r(a) ? a : r(n) ? n : "UTC";
  }
  function n(e, r) {
    return i(r || e?.timezone || e?.timeZone || t);
  }
  function a(e, r = t) {
    let n = i(r),
      o = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
        timeZone: n,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: !1
      }).formatToParts(e).map(e => [e.type, e.value]));
    return {
      y: Number(o.year),
      mo: Number(o.month),
      d: Number(o.day),
      h: Number(o.hour) % 24,
      min: Number(o.minute)
    };
  }
  function o(e, r = t) {
    let i = a(e, r);
    return `${i.y}-${String(i.mo).padStart(2, "0")}-${String(i.d).padStart(2, "0")}`;
  }
  function l(e, r = t) {
    let i = a(e, r);
    return `${i.y}-${String(i.mo).padStart(2, "0")}-${String(i.d).padStart(2, "0")}T${String(i.h).padStart(2, "0")}:${String(i.min).padStart(2, "0")}`;
  }
  function s(e) {
    let [t, r, i] = e.split("-").map(Number);
    return new Date(Date.UTC(t, r - 1, i)).getUTCDay();
  }
  function u(e, t) {
    let [r, i, n] = e.split("-").map(Number),
      a = new Date(Date.UTC(r, i - 1, n + t));
    return `${a.getUTCFullYear()}-${String(a.getUTCMonth() + 1).padStart(2, "0")}-${String(a.getUTCDate()).padStart(2, "0")}`;
  }
  function d(e, t, r) {
    return `${e}T${String(t).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  }
  function c(e, r = t) {
    if (!e || e.length < 16) return new Date().toISOString();
    let [n, o] = e.split("T"),
      [l, s, u] = n.split("-").map(Number),
      [d, m] = o.split(":").map(Number),
      p = new Date(Date.UTC(l, s - 1, u, d, m)),
      _ = a(p, i(r)),
      f = Date.UTC(l, s - 1, u, d, m),
      y = Date.UTC(_.y, _.mo - 1, _.d, _.h, _.min);
    return new Date(p.getTime() + (f - y)).toISOString();
  }
  function m(e, r = t) {
    if (e) {
      let t = String(e).trim(),
        i = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(t) ? t : c(t.replace(" ", "T").slice(0, 16), r));
      if (!isNaN(i.getTime())) return i;
    }
    return new Date();
  }
  function p(e) {
    let t = String(e || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
    return t.includes("DELIVERY") ? "DELIVERY" : t.includes("TABLE") ? "TABLE_BOOKING" : "OPENING" === t ? "OPENING" : "PICKUP";
  }
  function _(e, t) {
    if (Array.isArray(e)) return e;
    let r = p(t),
      i = e?.workingInfoByType;
    return i?.[r] || e?.workingInfo || null;
  }
  function f(e, t) {
    if (!Array.isArray(e)) return null;
    let r = p(t),
      i = e.find(e => String(e.type || "").trim().toUpperCase() === r);
    return i?.running_hours?.length ? i : null;
  }
  function y(e) {
    let t = String(e || "").trim().match(/^(\d{1,2}):(\d{2})/);
    if (!t) return null;
    let r = Number(t[1]),
      i = Number(t[2]);
    return r < 0 || r > 23 || i < 0 || i > 59 ? null : 60 * r + i;
  }
  function g(e, t, r) {
    let i,
      n = _(e, r);
    if (!n?.length) return [];
    let a = n.find(e => Number(e.day) === s(t));
    if (!a || !0 === (i = a?.is_closed) || 1 === Number(i)) return [];
    let o = f(a.hotelDayInfo || [], r);
    return o?.running_hours?.length ? o.running_hours.flatMap(e => {
      let t = y(e.start),
        r = y(e.stop);
      return null == t || null == r ? [] : [{
        start: String(e.start).slice(0, 5),
        stop: String(e.stop).slice(0, 5),
        startMinutes: t,
        stopMinutes: r
      }];
    }).sort((e, t) => e.startMinutes - t.startMinutes) : [];
  }
  function b(e, t) {
    let r = [];
    for (let i of e) {
      let e = {
          ...i
        },
        n = r[r.length - 1],
        a = n && n.stopMinutes < n.startMinutes,
        o = e.stopMinutes < e.startMinutes,
        l = n ? e.startMinutes - n.stopMinutes : 1 / 0;
      if (n && !a && !o && l >= 0 && l <= t) {
        e.stopMinutes > n.stopMinutes && (n.stop = e.stop, n.stopMinutes = e.stopMinutes);
        continue;
      }
      r.push(e);
    }
    return r;
  }
  function h(e, t) {
    return !e || !t || e.stopMinutes < e.startMinutes || t.stopMinutes < t.startMinutes ? 1 / 0 : 1440 - e.stopMinutes + t.startMinutes;
  }
  function S(e, t, r) {
    if (!Array.isArray(e) && E(t, e)) return [];
    let i = function (e, t) {
        if (Array.isArray(e) || !e) return 5;
        let r = Number("DELIVERY" === p(t) ? e.deliveryBufferTime ?? e.delivery_buffer_time : e.pickupBufferTime ?? e.pickup_buffer_time);
        return Math.max(5, Number.isFinite(r) && r >= 0 ? r : 0);
      }(e, r),
      n = b(g(e, t, r), i);
    if (!n.length) return [];
    let a = b(g(e, u(t, -1), r), i);
    if (h(a[a.length - 1], n[0]) <= i && (n = n.slice(1)), !n.length) return [];
    let o = b(g(e, u(t, 1), r), i),
      l = n[n.length - 1],
      s = o[0];
    return h(l, s) <= i && s && (n = n.map((e, t) => t === n.length - 1 ? {
      ...e,
      stop: s.stop,
      stopMinutes: s.stopMinutes
    } : e)), n;
  }
  function I(e, t, r) {
    let i = _(r, t);
    if (!i?.length || !e || e.length < 16) return null;
    let n = e.slice(0, 10),
      a = e.slice(11, 16);
    for (let e of [n, u(n, -1)]) if (function (e, t, r, i, n) {
      let a = S(e, t, n),
        o = t !== r;
      return a.some(e => {
        let t = e.start,
          r = e.stop,
          n = r < t;
        return o ? n && i <= r : n ? i >= t : i >= t && i <= r;
      });
    }(r, e, n, a, t)) return e;
    return null;
  }
  function v(e) {
    if (null == e) return null;
    if ("number" == typeof e) return Number.isFinite(e) ? new Date(e < 1e12 ? 1e3 * e : e) : null;
    let t = String(e).trim();
    if (!t) return null;
    if (/^\d+$/.test(t)) {
      let e = Number(t);
      return Number.isFinite(e) ? new Date(e < 1e12 ? 1e3 * e : e) : null;
    }
    let r = new Date(t);
    return Number.isNaN(r.getTime()) ? null : r;
  }
  function A(e, t, r) {
    let i = r?.scheduledClosures ?? r?.scheduled_closures ?? r?.upcoming_scheduled_disable_data;
    if (!Array.isArray(i) || 0 === i.length) return !1;
    let n = e.getTime();
    for (let e of i) {
      if (!function (e, t) {
        let r = String(e?.order_type ?? e?.domain_type ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_"),
          i = p(t);
        return !r || "ALL" === r || "ONLINE_ORDER" === r || ("PICKUP" === r || "ONLINE_PICKUP" === r || "ONLINE_APP_PICKUP" === r ? "PICKUP" === i : "DELIVERY" === r || "ONLINE_DELIVERY" === r || "ONLINE_APP_DELIVERY" === r ? "DELIVERY" === i : "KIOSK" !== r && ("TABLE_RESERVATION" === r || r.includes("TABLE"), !1));
      }(e, t)) continue;
      let r = v(e.schedule_start_time ?? e.start),
        i = v(e.schedule_end_time ?? e.end);
      if (r && i && n >= r.getTime() && n < i.getTime()) return !0;
    }
    return !1;
  }
  function E(e, t) {
    return !!w(e, t);
  }
  function w(e, t) {
    let r = t?.shopHolidays ?? t?.shop_holidays ?? t?.shop_hoilidays ?? [];
    return Array.isArray(r) && 0 !== r.length && r.find(t => {
      let r = "string" == typeof t ? t : t.date || t.holiday_date;
      return "string" == typeof r && r.slice(0, 10) === e;
    }) || null;
  }
  function T(e, t) {
    return "DELIVERY" === p(e) ? Number(t?.deliveryBufferTime ?? t?.delivery_buffer_time) || 20 : Number(t?.pickupBufferTime ?? t?.pickup_buffer_time) || 10;
  }
  function P(e) {
    if (!0 === e || 1 === e) return !0;
    if ("string" != typeof e) return !1;
    let t = e.trim().toLowerCase();
    return "1" === t || "true" === t || "yes" === t;
  }
  e.s(["DEFAULT_TIME_ZONE", 0, t, "NZ_TZ", 0, t, "addDays", 0, u, "buildScheduleDays", 0, function (e, t, r) {
    let i = t?.isPreorder === !0 || t?.is_preorder === 1,
      n = Number(t?.preMinDay ?? t?.pre_min_day ?? 0) || 0,
      a = (t?.orderLater === !0 || t?.order_later === 1) && Number(t?.orderLaterDays ?? t?.order_later_days ?? 0) >= 1 && Number(t?.orderLaterDays ?? t?.order_later_days ?? 0) || 0,
      o = 7;
    i && (1 === n ? o = 1 : 2 === n ? o = 2 : n > 2 && (o = n));
    let l = r ? _(t, r) : Array.isArray(t?.workingInfo) ? t.workingInfo : null,
      s = [];
    for (let i = a; i < o; i++) {
      let n = u(e, i);
      !E(n, t) && (r && l && !S(t, n, r).length || s.push(n));
    }
    return s;
  }, "buildScheduleSlots", 0, function ({
    selectedDay: e,
    orderType: t,
    restaurantConfig: r,
    isAsapAvailable: i = !1,
    nowUTC: o,
    timeZone: s
  }) {
    let m = _(r, t);
    if (!e || !m) return [];
    let p = n(r, s),
      f = T(t, r),
      y = S(r, e, t);
    if (!y.length) return [];
    let g = Math.max(15, f),
      b = e === l(o, p).slice(0, 10),
      h = a(o, p),
      I = 60 * h.h + h.min,
      v = [],
      E = new Set();
    y.forEach(t => {
      let [r, n] = t.start.split(":").map(Number),
        [a, o] = t.stop.split(":").map(Number),
        l = 60 * r + n,
        s = 60 * a + o;
      s <= l && (0 !== l || 0 !== s) && (s += 1440);
      let c = b ? Math.max(I + f * (i ? 2 : 1), l + f) : l + f,
        m = c = 5 * Math.ceil(c / 5);
      for (; m <= s;) {
        let t = m % 1440,
          r = Math.floor(t / 60),
          i = t % 60;
        String(r).padStart(2, "0"), String(i).padStart(2, "0");
        let n = Math.floor(m / 1440),
          a = d(n > 0 ? u(e, n) : e, r, i);
        E.has(a) || (E.add(a), v.push(a)), m += g;
      }
    });
    let w = r?.scheduledClosures ?? r?.scheduled_closures ?? r?.upcoming_scheduled_disable_data;
    return Array.isArray(w) && w.length > 0 ? v.filter(e => !A(new Date(c(e, p)), t, r)) : v;
  }, "dateStringDow", 0, s, "findTimeSlotOwnerDate", 0, I, "fmtSlot", 0, function (e) {
    if (!e || e.length < 16) return "";
    let t = Number(e.slice(11, 13)),
      r = e.slice(14, 16);
    return `${t % 12 || 12}:${r} ${t >= 12 ? "PM" : "AM"}`;
  }, "fmtSlotFull", 0, function (e) {
    if (!e || e.length < 16) return "";
    let [t, r] = e.split("T"),
      [i, n, a] = t.split("-").map(Number),
      o = Number(r.slice(0, 2)),
      l = r.slice(3, 5),
      s = new Date(Date.UTC(i, n - 1, a)).toLocaleDateString("en-NZ", {
        weekday: "short",
        day: "numeric",
        month: "short",
        timeZone: "UTC"
      });
    return `${s}, ${o % 12 || 12}:${l} ${o >= 12 ? "PM" : "AM"}`;
  }, "getAvailabilityEvaluationTime", 0, function (e, r, i = t, n = new Date()) {
    if ("SCHEDULED" === String(e || "").trim().toUpperCase() && r) {
      let e = new Date(c(r, i));
      if (!Number.isNaN(e.getTime())) return e;
    }
    return n;
  }, "getHolidayEntry", 0, w, "getHolidayReason", 0, function (e) {
    if (!e || "object" != typeof e) return null;
    let t = e.label ?? e.reason ?? e.holiday_reason ?? e.holiday_name ?? e.name ?? e.description ?? e.message ?? null;
    return "string" != typeof t ? null : t.trim() || null;
  }, "getHoursEntry", 0, f, "getNZNow", 0, function (e) {
    return m(e, t);
  }, "getOrderBuffer", 0, T, "getScheduleTimeZone", 0, n, "getTimeZoneNow", 0, m, "getWorkingInfoForOrderType", 0, _, "isAsapAvailableForOrderType", 0, function (e, t, r = new Date(), i) {
    if (!e || function (e) {
      if (!1 === e || 0 === e) return !0;
      if ("string" != typeof e) return !1;
      let t = e.trim().toLowerCase();
      return "0" === t || "false" === t || "no" === t;
    }(e.onlineOrderActive ?? e.online_order_active) || P(e.onlineInactive ?? e.online_inactive) || P(e.isPaused ?? e.is_paused) || P(e.orderLater ?? e.order_later) && Number(e.orderLaterDays ?? e.order_later_days ?? 0) >= 1 || E(o(r, n(e, i)), e) || A(r, t, e)) return !1;
    let a = T(t, e);
    return !(a > 0 && A(new Date(r.getTime() + 6e4 * a), t, e)) && P("DELIVERY" === p(t) ? e.deliveryAvailable ?? e.delivery_available : e.pickupAvailable ?? e.pickup_available);
  }, "isHolidayDate", 0, E, "isInScheduledClosure", 0, A, "isTimeSlotValid", 0, function (e, t, r) {
    return null !== I(e, t, r);
  }, "nzDow", 0, function (e) {
    return s(e);
  }, "nzISOStr", 0, function (e) {
    return l(e, t);
  }, "nzParts", 0, function (e) {
    return a(e, t);
  }, "nzSlot", 0, function (e, t, r) {
    return d(e, t, r);
  }, "nzSlotToUTC", 0, function (e) {
    return c(e, t);
  }, "resolveTimeZone", 0, i, "resolveWorkingInfoType", 0, p, "tzDateStr", 0, o, "tzISOStr", 0, l, "tzParts", 0, a, "tzSlotToUTC", 0, c, "wallClockSlot", 0, d], 376151);
  let N = new Set(["DM", "SD", "ED", "D", "H", "dm", "sd", "ed", "d", "h", "domains", "days", "timings", "from", "to"]),
    C = {
      sun: "0",
      sunday: "0",
      mon: "1",
      monday: "1",
      tue: "2",
      tues: "2",
      tuesday: "2",
      wed: "3",
      wednesday: "3",
      thu: "4",
      thur: "4",
      thurs: "4",
      thursday: "4",
      fri: "5",
      friday: "5",
      sat: "6",
      saturday: "6"
    };
  function k(e) {
    return !!e && "object" == typeof e && !Array.isArray(e);
  }
  function O(e) {
    let t = e.trim();
    if (!t || "null" === t || "undefined" === t || "false" === t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  function D(e) {
    if (null == e) return;
    let t = e;
    if ("string" == typeof t && (t = O(t) ?? t), Array.isArray(t)) {
      let e = t.map(e => String(e).trim()).filter(Boolean);
      return e.length ? e : void 0;
    }
    if ("string" == typeof t) {
      let e = t.split(",").map(e => e.trim()).filter(Boolean);
      return e.length ? e : void 0;
    }
    if ("number" == typeof t || "boolean" == typeof t) return [String(t)];
    if (k(t)) {
      let e = Object.values(t).map(e => String(e).trim()).filter(Boolean);
      return e.length ? e : void 0;
    }
  }
  function R(e) {
    if (null == e) return "";
    let t = String(e).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!t) return "";
    let r = Number(t[1]),
      i = Number(t[2]);
    return r < 0 || r > 24 || i < 0 || i > 59 || 24 === r && 0 !== i ? "" : `${String(r).padStart(2, "0")}:${String(i).padStart(2, "0")}`;
  }
  function L(e) {
    let t;
    if (null == e) return;
    let r = e;
    "string" == typeof r && (r = O(r) ?? r);
    let i = (Array.isArray(r) ? r : k(r) && (t = r, ["S", "E", "s", "e", "start", "end", "start_time", "end_time", "startTime", "endTime", "from", "to"].some(e => e in t)) ? [r] : k(r) ? Object.values(r) : []).filter(k).map(e => ({
      S: R(e.S ?? e.s ?? e.start ?? e.start_time ?? e.startTime ?? e.from),
      E: R(e.E ?? e.e ?? e.end ?? e.end_time ?? e.endTime ?? e.to)
    })).filter(e => e.S && e.E);
    return i.length ? i : void 0;
  }
  function U(e) {
    let t = D(e);
    if (!t) return;
    let r = t.map(e => C[e.toLowerCase()] ?? e).filter(e => /^[0-6]$/.test(e));
    return r.length ? r : void 0;
  }
  function M(e) {
    return "string" == typeof e && /^\d{4}-\d{2}-\d{2}/.test(e.trim());
  }
  function F(e) {
    return "unavailable" === String(e || "").trim().toLowerCase() ? "unavailable" : void 0;
  }
  function x(e) {
    return "unavailable" === F(e.type);
  }
  function z(e) {
    return Object.keys(e).some(e => N.has(e)) || "unavailable" === F(e.type);
  }
  function B(e) {
    if (null == e) return [];
    let t = e;
    if ("string" == typeof t && null == (t = O(t))) return [];
    if (Array.isArray(t)) return t.flatMap(e => B(e));
    if (k(t)) {
      if (z(t)) {
        let e = function (e) {
          if (!k(e) || !z(e)) return null;
          let t = {
              ...e
            },
            r = [e.DM, e.dm, e.domains].map(D).find(e => e?.length),
            i = [e.D, e.d, e.days].map(U).find(e => e?.length),
            n = [e.H, e.h, e.timings].map(L).find(e => e?.length),
            a = e.from,
            o = e.to,
            l = [e.SD, e.sd, M(a) ? a : void 0].find(e => null != e && String(e).trim()),
            s = [e.ED, e.ed, M(o) ? o : void 0].find(e => null != e && String(e).trim());
          n || !a || !o || M(a) || M(o) || (n = L([{
            from: a,
            to: o
          }])), r && (t.DM = r), i && (t.D = i), n && (t.H = n), null != l && (t.SD = String(l)), null != s && (t.ED = String(s));
          let u = F(e.type);
          return u ? t.type = u : delete t.type, t;
        }(t);
        return e ? [e] : [];
      }
      return Object.values(t).flatMap(e => B(e));
    }
    return [];
  }
  function K(e) {
    if (!0 === e || 1 === e) return !0;
    if ("string" != typeof e) return !1;
    let t = e.trim().toLowerCase();
    return "1" === t || "true" === t || "yes" === t;
  }
  function $(e) {
    return !(!(function (e) {
      if (!1 === e || 0 === e) return !0;
      if ("string" != typeof e) return !1;
      let t = e.trim().toLowerCase();
      return "0" === t || "false" === t || "no" === t;
    }(e.is_available ?? e.isAvailable) || K(e.is_inactive ?? e.isInactive ?? e.inactive)) && (void 0 === e.category_active || K(e.category_active)) && (void 0 === e.combo_active || K(e.combo_active)));
  }
  function G(e) {
    return String(e || "").trim().replace(/[\s-]+/g, "_").toUpperCase();
  }
  function Y(e, t, r, i) {
    let n = a(e, t),
      o = `${n.y}-${String(n.mo).padStart(2, "0")}-${String(n.d).padStart(2, "0")}`,
      l = u(o, -1),
      d = (i.isKiosk ? ["KIOSK"] : i.isTableSide ? ["TABLE_SIDE"] : "DELIVERY" === r ? [i.isMobileApp ? "ONLINE_APP_DELIVERY" : "ONLINE_DELIVERY"] : [i.isMobileApp ? "ONLINE_APP_PICKUP" : "ONLINE_PICKUP"]).map(G),
      c = !i.isKiosk && !i.isMobileApp && !i.isTableSide;
    return {
      today: o,
      dow: s(o),
      previousDate: l,
      previousDow: s(l),
      cur: `${String(n.h).padStart(2, "0")}:${String(n.min).padStart(2, "0")}`,
      domainMatches: e => Array.isArray(e) && e.some(e => {
        let t = G(e);
        return d.includes(t) || c && ("WEB" === t || "ONLINE" === t);
      })
    };
  }
  function V(e, t, r) {
    return !(e.SD && t < String(e.SD).slice(0, 10) || e.ED && t > String(e.ED).slice(0, 10) || Array.isArray(e.D) && e.D.length > 0 && !e.D.includes(String(r)));
  }
  function j(e, t) {
    if (Array.isArray(e.DM) && e.DM.length > 0 && !t.domainMatches(e.DM)) return !1;
    if (Array.isArray(e.H) && e.H.length > 0) {
      for (let r of e.H) {
        let i = R(r.S),
          n = R(r.E);
        if (i && n) {
          if (!(n < i)) {
            if (t.cur >= i && t.cur <= n && V(e, t.today, t.dow)) return !0;
            continue;
          }
          if (t.cur >= i && V(e, t.today, t.dow) || t.cur <= n && V(e, t.previousDate, t.previousDow)) return !0;
        }
      }
      return !1;
    }
    return V(e, t.today, t.dow);
  }
  function W(e, t = !1) {
    return B(t ? e.catAvailability ?? e.cat_availability : e.availability);
  }
  function Z(e, r = "PICKUP", i = new Date(), n = {}) {
    if (!e) return !0;
    if ($(e)) return !1;
    let a = W(e, n.source);
    if (0 === a.length) return !0;
    let o = Y(i, n.timeZone || t, r, n);
    if (a.filter(x).some(e => j(e, o))) return !1;
    let l = a.filter(e => !x(e));
    if (0 === l.length) return !0;
    let s = l.filter(e => Array.isArray(e.DM) && e.DM.length > 0);
    return (!(s.length > 0) || !!s.some(e => o.domainMatches(e.DM))) && l.some(e => j(e, o));
  }
  function H(e, t = "PICKUP", r = new Date(), i = {}) {
    return !e || !!Z(e, t, r, i) && Z(e, t, r, {
      ...i,
      source: !0
    });
  }
  function q(e) {
    let [t, r] = e.split(":"),
      i = Number(t);
    return `${i % 12 || 12}:${r} ${i >= 12 ? "PM" : "AM"}`;
  }
  function J(e) {
    let [t, r, i] = e.split("-").map(Number);
    return new Date(Date.UTC(t, r - 1, i)).toLocaleDateString("en-NZ", {
      weekday: "long",
      timeZone: "UTC"
    });
  }
  function X(e, r = "PICKUP", i = new Date(), n = {}) {
    if (!e || $(e)) return null;
    let l = W(e);
    if (0 === l.length) return null;
    let s = n.timeZone || t,
      d = o(i, s),
      m = new Map();
    for (let e = 0; e <= 7; e += 1) {
      let t = u(d, e);
      for (let e of l) {
        let r = Array.isArray(e.H) ? e.H : [];
        if (0 === r.length) {
          let e = new Date(c(`${t}T00:00`, s));
          m.set(e.getTime(), {
            at: e,
            timed: !1
          });
          continue;
        }
        for (let e of r) {
          let r = R(e.S),
            i = R(e.E);
          if (!r || !i) continue;
          let n = new Date(c(`${t}T${r}`, s));
          m.set(n.getTime(), {
            at: n,
            timed: !0
          });
          let a = i < r ? u(t, 1) : t,
            o = new Date(new Date(c(`${a}T${i}`, s)).getTime() + 6e4);
          m.set(o.getTime(), {
            at: o,
            timed: !0
          });
        }
      }
    }
    for (let t of [...m.values()].sort((e, t) => e.at.getTime() - t.at.getTime())) if (!(t.at.getTime() < i.getTime()) && Z(e, r, t.at, n)) {
      let e = o(t.at, s),
        r = a(t.at, s),
        i = `${String(r.h).padStart(2, "0")}:${String(r.min).padStart(2, "0")}`,
        n = u(d, 1);
      if (!t.timed) {
        if (e === d) return "Available now";
        if (e === n) return "Available tomorrow";
        return `Available ${J(e)}`;
      }
      if (e === d) return `Available today from ${q(i)}`;
      if (e === n) return `Available tomorrow at ${q(i)}`;
      return `Available ${J(e)} at ${q(i)}`;
    }
    return null;
  }
  function Q(e, r = "PICKUP", i = new Date(), n = {}) {
    if (!e) return "Not available";
    if ($(e)) {
      let t = e.inactive_reason ?? e.inactiveReason;
      return "string" == typeof t && t.trim() ? t.trim() : "Not available";
    }
    let a = W(e);
    if (0 === a.length) return "Not available";
    let o = Y(i, n.timeZone || t, r, n);
    if (a.filter(x).some(e => j(e, o))) return n.isKiosk || n.isTableSide ? "Not available at the moment" : "Temporarily unavailable";
    let l = a.filter(e => !x(e)),
      s = l.filter(e => Array.isArray(e.DM) && e.DM.length > 0);
    if (s.length > 0 && !s.some(e => o.domainMatches(e.DM))) return n.isKiosk ? "Not available at this kiosk" : n.isTableSide ? "Not available for dine-in" : "DELIVERY" === r ? "Not available for delivery" : "Not available for pickup";
    let u = l.filter(e => !Array.isArray(e.DM) || 0 === e.DM.length || o.domainMatches(e.DM)),
      d = u.filter(e => e.SD || e.ED);
    return d.length > 0 && d.length === u.length && !d.some(e => (!e.SD || o.today >= String(e.SD).slice(0, 10)) && (!e.ED || o.today <= String(e.ED).slice(0, 10))) ? "Outside available date range" : X(e, r, i, n) || "Not available at this time";
  }
  e.s(["getNextAvailableTime", 0, function (e, r = "PICKUP", i = new Date(), n = !1, a = !1, o = !1, l = t) {
    return X(e, r, i, {
      timeZone: l,
      isMobileApp: n,
      isKiosk: a,
      isTableSide: o
    });
  }, "getNextAvailableTimeInTimeZone", 0, X, "getUnavailabilityReason", 0, function (e, r = "PICKUP", i = new Date(), n = !1, a = !1, o = !1, l = t) {
    return Q(e, r, i, {
      timeZone: l,
      isMobileApp: n,
      isKiosk: a,
      isTableSide: o
    });
  }, "getUnavailabilityReasonInTimeZone", 0, Q, "isCategoryAvailable", 0, function (e, r = "PICKUP", i = new Date(), n = !1, a = !1, o = !1, l = t) {
    return H(e, r, i, {
      timeZone: l,
      isMobileApp: n,
      isKiosk: a,
      isTableSide: o
    });
  }, "isCategoryAvailableInTimeZone", 0, H, "isComboCategoryAvailable", 0, function (e, r = "PICKUP", i = new Date(), n = !1, a = !1, o = !1, l = t) {
    return function (e, t = "PICKUP", r = new Date(), i = {}) {
      return !e || Z({
        ...e,
        availability: e.category_availability ?? e.categoryAvailability ?? e.availability
      }, t, r, i);
    }(e, r, i, {
      timeZone: l,
      isMobileApp: n,
      isKiosk: a,
      isTableSide: o
    });
  }, "isEntityAvailable", 0, function (e, r = "PICKUP", i = new Date(), n = !1, a = !1, o = !1, l = !1, s = t) {
    return Z(e, r, i, {
      timeZone: s,
      isMobileApp: n,
      source: a,
      isKiosk: o,
      isTableSide: l
    });
  }, "isEntityAvailableInTimeZone", 0, Z, "normalizeAvailabilityRules", 0, function (e) {
    return B(e);
  }], 566450);
});

  Z.define(395925, e => {
  "use strict";

  let t = (0, e.i(456420).default)("rotate-ccw", [["path", {
    d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8",
    key: "1357e3"
  }], ["path", {
    d: "M3 3v5h5",
    key: "1xhq8a"
  }]]);
  e.s(["RotateCcw", 0, t], 395925);
});

  Z.define(399847, e => {
  "use strict";

  var t = e.i(724983);
  e.s(["AlertCircle", () => t.default]);
});

  Z.define(399901, e => {
  "use strict";

  var a = e.i(843476),
    s = e.i(271645),
    t = e.i(88653),
    r = e.i(846932),
    d = e.i(782924),
    l = e.i(689664),
    i = e.i(24071),
    n = e.i(118635),
    c = e.i(611568),
    o = e.i(420865),
    u = e.i(515281),
    m = e.i(77071),
    p = e.i(573474),
    x = e.i(263676),
    h = e.i(618566),
    b = e.i(913734),
    f = e.i(799552),
    y = e.i(990746),
    g = e.i(309808),
    j = e.i(498624),
    N = e.i(273341);
  e.i(651164);
  var v = e.i(917848);
  let k = [{
    value: "Home",
    Icon: n.House
  }, {
    value: "Work",
    Icon: d.BriefcaseBusiness
  }, {
    value: "Other",
    Icon: o.MapPin
  }];
  function _({
    initialValue: e,
    onSelect: t
  }) {
    let [r, d] = (0, s.useState)(e),
      [i, n] = (0, s.useState)([]),
      [u, m] = (0, s.useState)(null),
      [p, h] = (0, s.useState)(""),
      [b, f] = (0, s.useState)(""),
      [y, g] = (0, s.useState)(!1),
      [v, S] = (0, s.useState)(""),
      w = (0, s.useRef)(null);
    function C(e) {
      (d(e), m(null), S(""), w.current && window.clearTimeout(w.current), e.trim().length < 2) ? n([]) : w.current = window.setTimeout(async () => {
        g(!0);
        try {
          let a = await (0, j.autocompleteAddress)(e);
          n(a?.status === "success" && a.predictions || []), a?.status !== "success" && S("Address search is unavailable right now.");
        } catch {
          n([]), S("Address search is unavailable right now.");
        } finally {
          g(!1);
        }
      }, 300);
    }
    async function P(e) {
      (0, N.haptic)("selection"), n([]), d(e.description || e.main_text || ""), g(!0), S("");
      try {
        let a = await (0, j.getPlaceDetails)(e.place_id),
          s = a?.status === "success" ? a.place : null;
        if (!s) throw Error("No place");
        m({
          addressUid: Date.now(),
          label: "",
          line1: s.formatted_address,
          city: s.city || s.suburb || "",
          zipcode: s.postcode || null,
          isSelected: !1,
          latitude: s.lat,
          longitude: s.lng
        });
      } catch {
        S("That address could not be loaded. Please choose another result.");
      } finally {
        g(!1);
      }
    }
    return (0, s.useEffect)(() => () => {
      w.current && window.clearTimeout(w.current);
    }, []), (0, a.jsxs)("div", {
      className: "space-y-4",
      children: [(0, a.jsxs)("label", {
        className: "block",
        children: [(0, a.jsx)("span", {
          className: "mb-1.5 block text-[11px] font-semibold text-brand-ink-light",
          children: "Search delivery address"
        }), (0, a.jsxs)("span", {
          className: "flex min-h-12 items-center rounded-[14px] border border-brand-ink/10 bg-[#F5F5F7] px-3.5 focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-brand-primary/12",
          children: [y ? (0, a.jsx)(c.LoaderCircle, {
            className: "h-5 w-5 shrink-0 animate-spin text-brand-primary",
            strokeWidth: 2,
            "aria-hidden": "true"
          }) : (0, a.jsx)(o.MapPin, {
            className: "h-5 w-5 shrink-0 text-brand-primary",
            strokeWidth: 1.9,
            "aria-hidden": "true"
          }), (0, a.jsx)("input", {
            value: r,
            onChange: e => C(e.target.value),
            autoComplete: "off",
            placeholder: "Start typing an address",
            className: "min-h-12 min-w-0 flex-1 bg-transparent px-3 text-[14px] font-medium text-brand-ink outline-none placeholder:text-brand-ink/35"
          }), r ? (0, a.jsx)("button", {
            type: "button",
            onClick: () => C(""),
            "aria-label": "Clear address search",
            className: "grid h-11 w-11 shrink-0 touch-manipulation place-items-center rounded-[12px] text-brand-ink-light",
            children: (0, a.jsx)(x.X, {
              className: "h-4 w-4",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          }) : null]
        })]
      }), i.length ? (0, a.jsx)("div", {
        className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[15px] border border-brand-ink/8 bg-white",
        children: i.slice(0, 6).map(e => (0, a.jsxs)("button", {
          type: "button",
          onClick: () => void P(e),
          className: "flex min-h-[58px] w-full touch-manipulation items-center gap-3 px-3.5 py-2.5 text-left active:bg-[#F5F5F7]",
          children: [(0, a.jsx)("span", {
            className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-[#EAF0FF] text-brand-primary",
            children: (0, a.jsx)(o.MapPin, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            })
          }), (0, a.jsxs)("span", {
            className: "min-w-0",
            children: [(0, a.jsx)("span", {
              className: "block truncate text-[13px] font-semibold text-brand-ink",
              children: e.main_text
            }), (0, a.jsx)("span", {
              className: "mt-0.5 block truncate text-[10px] text-brand-ink-light",
              children: e.secondary_text
            })]
          })]
        }, e.place_id))
      }) : null, v ? (0, a.jsx)("p", {
        className: "rounded-[12px] bg-red-50 px-3 py-2.5 text-[11px] font-medium text-red-600",
        role: "status",
        children: v
      }) : null, u ? (0, a.jsxs)("div", {
        className: "space-y-4",
        children: [(0, a.jsxs)("div", {
          className: "flex gap-3 rounded-[15px] bg-[#EAF0FF] p-3.5",
          children: [(0, a.jsx)("span", {
            className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
            children: (0, a.jsx)(l.Check, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 2.2,
              "aria-hidden": "true"
            })
          }), (0, a.jsxs)("div", {
            className: "min-w-0",
            children: [(0, a.jsx)("p", {
              className: "text-[10px] font-semibold text-brand-primary",
              children: "Address selected"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[13px] font-semibold leading-snug text-brand-ink",
              children: u.line1
            }), (0, a.jsx)("p", {
              className: "mt-0.5 text-[10px] text-brand-ink-light",
              children: [u.city, u.zipcode].filter(Boolean).join(" · ")
            })]
          })]
        }), (0, a.jsxs)("fieldset", {
          children: [(0, a.jsx)("legend", {
            className: "mb-2 text-[11px] font-semibold text-brand-ink-light",
            children: "Label this address"
          }), (0, a.jsx)("div", {
            className: "grid grid-cols-3 gap-2",
            children: k.map(({
              value: e,
              Icon: s
            }) => (0, a.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, N.haptic)("selection"), h(p === e ? "" : e);
              },
              "aria-pressed": p === e,
              className: `flex min-h-12 touch-manipulation items-center justify-center gap-1.5 rounded-[13px] border px-2 text-[11px] font-semibold ${p === e ? "border-brand-primary bg-[#EAF0FF] text-brand-primary" : "border-brand-ink/8 bg-white text-brand-ink"}`,
              children: [(0, a.jsx)(s, {
                className: "h-4 w-4",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              }), e]
            }, e))
          }), "Other" === p ? (0, a.jsx)("input", {
            value: b,
            onChange: e => f(e.target.value),
            placeholder: "e.g. Gym or family",
            className: "mt-2 h-12 w-full rounded-[14px] border border-brand-ink/10 bg-[#F5F5F7] px-3.5 text-[14px] font-medium outline-none placeholder:text-brand-ink/35 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/12"
          }) : null]
        }), (0, a.jsx)("button", {
          type: "button",
          onClick: function () {
            if (!u) return;
            let e = "Other" === p ? b.trim() : p;
            (0, N.haptic)("medium"), t({
              ...u,
              label: e
            });
          },
          className: "flex min-h-12 w-full touch-manipulation items-center justify-center rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white",
          children: "Save address"
        })]
      }) : null]
    });
  }
  e.s(["default", 0, function () {
    let e = (0, h.useRouter)(),
      d = (0, g.useToast)(),
      {
        user: l,
        customerData: n,
        loading: j,
        updateCustomerDataLocally: k,
        refreshCustomerData: S
      } = (0, y.useAuth)(),
      [w, C] = (0, s.useState)(!1),
      [P, A] = (0, s.useState)(null),
      [z, L] = (0, s.useState)(null),
      [E, D] = (0, s.useState)(!1),
      B = (0, s.useRef)(!1),
      M = (0, s.useRef)(null),
      U = (0, s.useRef)(null),
      H = n?.addresses || [];
    if ((0, s.useEffect)(() => {
      j || (l ? B.current || (B.current = !0, S(l.uid)) : e.replace("/"));
    }, [j, S, e, l]), (0, s.useEffect)(() => {
      if (!w && !z) return;
      let e = document.body.style.overflow,
        a = window.requestAnimationFrame(() => {
          z ? U.current?.focus() : M.current?.focus();
        }),
        s = e => {
          "Escape" !== e.key || E || (z ? L(null) : (C(!1), A(null)));
        };
      return document.body.style.overflow = "hidden", window.addEventListener("keydown", s), () => {
        window.cancelAnimationFrame(a), window.removeEventListener("keydown", s), document.body.style.overflow = e;
      };
    }, [w, z, E]), j || !l) return (0, a.jsx)(b.default, {
      kind: "profile"
    });
    async function R(e) {
      if (!l?.uid) return !1;
      D(!0), k?.({
        addresses: e
      });
      try {
        let a = await (0, v.createCustomer)({
          firebaseUID: l.uid,
          name: n?.name || l.displayName || "",
          phone: n?.phone || n?.phone_no || "",
          email: n?.email || l.email || "",
          birthday: n?.birthday || null,
          addresses: e
        });
        if (!a?.success && !a?.status) throw Error(a?.error || "Address save failed");
        return a.data && k?.(a.data), !0;
      } catch {
        return d.error("The address book could not be saved. Please try again.", {
          title: "Save failed"
        }), S(l.uid), !1;
      } finally {
        D(!1);
      }
    }
    async function T(e) {
      let a = P ? H.map(a => a.addressUid === P.addressUid ? {
        ...e,
        addressUid: P.addressUid,
        isSelected: P.isSelected
      } : a) : [...H, 0 === H.length ? {
        ...e,
        isSelected: !0
      } : e];
      (await R(a)) && (d.success(P ? "Address updated." : "Address added.", {
        title: "Address book saved"
      }), C(!1), A(null));
    }
    async function I(e) {
      (0, N.haptic)("selection"), (await R(H.map(a => ({
        ...a,
        isSelected: a.addressUid === e
      })))) && d.success("Default delivery address updated.");
    }
    async function F() {
      if (!z) return;
      (0, N.haptic)("medium");
      let e = H.filter(e => e.addressUid !== z.addressUid),
        a = z.isSelected && e.length > 0 ? e.map((e, a) => ({
          ...e,
          isSelected: 0 === a
        })) : e;
      (await R(a)) && (d.success("Address removed."), L(null));
    }
    function $(e = null) {
      (0, N.haptic)("selection"), A(e), C(!0);
    }
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsx)(f.MobilePageHeader, {
        icon: o.MapPin,
        kicker: "Delivery book",
        title: "Saved addresses",
        description: "Keep regular delivery stops ready for checkout."
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-4 px-4 sm:px-6",
        children: [(0, a.jsxs)("button", {
          type: "button",
          onClick: () => $(),
          className: "flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white",
          children: [(0, a.jsx)(m.Plus, {
            className: "h-4 w-4",
            strokeWidth: 2.2,
            "aria-hidden": "true"
          }), "Add address"]
        }), H.length ? (0, a.jsx)("section", {
          className: "grid gap-3 md:grid-cols-2",
          "aria-label": "Saved delivery addresses",
          children: H.map((e, s) => (0, a.jsxs)("article", {
            className: `flex min-h-[156px] flex-col overflow-hidden rounded-[16px] border bg-white ${e.isSelected ? "border-brand-primary/28" : "border-brand-ink/8"}`,
            children: [(0, a.jsxs)("div", {
              className: "flex flex-1 items-start gap-3 p-3.5",
              children: [(0, a.jsx)("span", {
                className: `grid h-10 w-10 shrink-0 place-items-center rounded-[12px] ${e.isSelected ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
                children: (0, a.jsx)(o.MapPin, {
                  className: "h-[18px] w-[18px]",
                  strokeWidth: 1.9,
                  "aria-hidden": "true"
                })
              }), (0, a.jsxs)("div", {
                className: "min-w-0 flex-1",
                children: [(0, a.jsxs)("div", {
                  className: "flex flex-wrap items-center gap-2",
                  children: [(0, a.jsx)("p", {
                    className: "text-[10px] font-semibold text-brand-ink-light",
                    children: e.label || "Saved address"
                  }), e.isSelected ? (0, a.jsx)("span", {
                    className: "rounded-full bg-[#EAF0FF] px-2 py-1 text-[8px] font-bold text-brand-primary",
                    children: "Default"
                  }) : null]
                }), (0, a.jsx)("p", {
                  className: "mt-1.5 text-[13px] font-semibold leading-snug text-brand-ink",
                  children: e.line1 || "Address"
                }), (0, a.jsx)("p", {
                  className: "mt-1 text-[10px] text-brand-ink-light",
                  children: [e.city, e.zipcode].filter(Boolean).join(" · ") || "Delivery details saved"
                })]
              })]
            }), (0, a.jsxs)("div", {
              className: "grid grid-cols-3 border-t border-brand-ink/8",
              children: [(0, a.jsxs)("button", {
                type: "button",
                onClick: () => $(e),
                disabled: E,
                className: "flex min-h-11 touch-manipulation items-center justify-center gap-1 border-r border-brand-ink/8 text-[10px] font-semibold text-brand-ink disabled:opacity-40",
                children: [(0, a.jsx)(u.Pencil, {
                  className: "h-3.5 w-3.5",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), "Edit"]
              }), (0, a.jsx)("button", {
                type: "button",
                onClick: () => void I(e.addressUid),
                disabled: E || e.isSelected,
                className: "min-h-11 touch-manipulation border-r border-brand-ink/8 px-1 text-[10px] font-semibold text-brand-primary disabled:text-brand-ink/32",
                children: e.isSelected ? "Default" : "Make default"
              }), (0, a.jsxs)("button", {
                type: "button",
                onClick: () => {
                  (0, N.haptic)("selection"), L(e);
                },
                disabled: E,
                className: "flex min-h-11 touch-manipulation items-center justify-center gap-1 px-1 text-[10px] font-semibold text-red-600 disabled:opacity-40",
                children: [(0, a.jsx)(p.Trash2, {
                  className: "h-3.5 w-3.5",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), "Remove"]
              })]
            })]
          }, e.addressUid || `${e.line1}-${s}`))
        }) : (0, a.jsxs)("section", {
          className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-8 text-center",
          children: [(0, a.jsx)("span", {
            className: "mx-auto grid h-12 w-12 place-items-center rounded-[14px] bg-white text-brand-primary",
            children: (0, a.jsx)(o.MapPin, {
              className: "h-6 w-6",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            })
          }), (0, a.jsx)("h2", {
            className: "mt-3 text-[16px] font-bold",
            children: "No saved addresses yet"
          }), (0, a.jsx)("p", {
            className: "mx-auto mt-1 max-w-[18rem] text-[11px] leading-relaxed text-brand-ink-light",
            children: "Add home, work or another regular delivery stop for a faster checkout."
          })]
        }), (0, a.jsxs)("button", {
          type: "button",
          onClick: () => {
            (0, N.haptic)("selection"), e.push("/profile");
          },
          className: "flex min-h-11 touch-manipulation items-center gap-1 text-[12px] font-semibold text-brand-primary",
          children: [(0, a.jsx)(i.ChevronLeft, {
            className: "h-4 w-4",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), "Back to Account"]
        })]
      }), (0, a.jsx)(t.AnimatePresence, {
        children: w ? (0, a.jsxs)(a.Fragment, {
          children: [(0, a.jsx)(r.motion.button, {
            type: "button",
            "aria-label": "Close address editor",
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            onClick: () => {
              E || (C(!1), A(null));
            },
            className: "fixed inset-0 z-[200] bg-brand-ink/45"
          }), (0, a.jsxs)(r.motion.section, {
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "zeta-address-editor-title",
            initial: {
              y: "100%"
            },
            animate: {
              y: 0
            },
            exit: {
              y: "100%"
            },
            transition: {
              type: "spring",
              stiffness: 380,
              damping: 38
            },
            className: "fixed bottom-0 left-0 right-0 z-[201] mx-auto max-h-[88dvh] max-w-[560px] overflow-hidden rounded-t-[20px] bg-white shadow-2xl sm:bottom-6 sm:rounded-[20px]",
            children: [(0, a.jsxs)("div", {
              className: "border-b border-brand-ink/8 bg-white px-4 pb-3 pt-2.5 sm:px-5",
              children: [(0, a.jsx)("div", {
                className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/12"
              }), (0, a.jsxs)("div", {
                className: "mt-2.5 flex items-center justify-between gap-3",
                children: [(0, a.jsxs)("div", {
                  children: [(0, a.jsx)("p", {
                    className: "text-[10px] font-semibold text-brand-primary",
                    children: "Delivery address"
                  }), (0, a.jsx)("h2", {
                    id: "zeta-address-editor-title",
                    className: "mt-0.5 text-[20px] font-bold",
                    children: P ? "Edit address" : "Add address"
                  })]
                }), (0, a.jsx)("button", {
                  ref: M,
                  type: "button",
                  onClick: () => {
                    C(!1), A(null);
                  },
                  disabled: E,
                  "aria-label": "Close",
                  className: "grid h-11 w-11 shrink-0 touch-manipulation place-items-center rounded-[12px] bg-[#F5F5F7] text-brand-ink disabled:opacity-40",
                  children: (0, a.jsx)(x.X, {
                    className: "h-4 w-4",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  })
                })]
              })]
            }), (0, a.jsxs)("div", {
              className: "max-h-[calc(88dvh-84px)] overflow-y-auto overscroll-contain px-4 pb-[calc(20px+env(safe-area-inset-bottom,0px))] pt-4 sm:px-5 sm:pb-5",
              children: [(0, a.jsx)(_, {
                initialValue: P?.line1 || "",
                onSelect: e => void T(e)
              }, P?.addressUid || "new"), E ? (0, a.jsxs)("p", {
                className: "mt-4 flex min-h-11 items-center justify-center gap-2 rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7] text-[11px] font-semibold text-brand-ink-light",
                role: "status",
                children: [(0, a.jsx)(c.LoaderCircle, {
                  className: "h-4 w-4 animate-spin",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), "Saving address"]
              }) : null]
            })]
          })]
        }) : null
      }), (0, a.jsx)(t.AnimatePresence, {
        children: z ? (0, a.jsxs)(a.Fragment, {
          children: [(0, a.jsx)(r.motion.button, {
            type: "button",
            "aria-label": "Keep saved address",
            initial: {
              opacity: 0
            },
            animate: {
              opacity: 1
            },
            exit: {
              opacity: 0
            },
            onClick: () => {
              E || L(null);
            },
            className: "fixed inset-0 z-[210] bg-brand-ink/45"
          }), (0, a.jsxs)(r.motion.section, {
            role: "alertdialog",
            "aria-modal": "true",
            "aria-labelledby": "zeta-delete-address-title",
            initial: {
              y: "100%"
            },
            animate: {
              y: 0
            },
            exit: {
              y: "100%"
            },
            transition: {
              type: "spring",
              stiffness: 380,
              damping: 38
            },
            className: "fixed bottom-0 left-0 right-0 z-[211] mx-auto max-w-[520px] rounded-t-[20px] bg-white px-4 pb-[calc(20px+env(safe-area-inset-bottom,0px))] pt-3 shadow-2xl sm:bottom-6 sm:rounded-[20px] sm:px-5 sm:pb-5",
            children: [(0, a.jsx)("div", {
              className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/12"
            }), (0, a.jsx)("p", {
              className: "mt-4 text-[10px] font-semibold text-red-600",
              children: "Remove saved address"
            }), (0, a.jsx)("h2", {
              id: "zeta-delete-address-title",
              className: "mt-1 text-[20px] font-bold",
              children: "Remove this address?"
            }), (0, a.jsx)("p", {
              className: "mt-1.5 text-[12px] leading-relaxed text-brand-ink-light",
              children: "It will no longer be available as a delivery option at checkout."
            }), (0, a.jsx)("div", {
              className: "mt-3 rounded-[13px] border border-brand-ink/8 bg-[#F5F5F7] p-3 text-[12px] font-medium leading-relaxed",
              children: z.line1
            }), (0, a.jsxs)("div", {
              className: "mt-4 grid grid-cols-2 gap-2",
              children: [(0, a.jsx)("button", {
                ref: U,
                type: "button",
                onClick: () => L(null),
                disabled: E,
                className: "min-h-12 touch-manipulation rounded-[14px] bg-[#F5F5F7] text-[13px] font-semibold disabled:opacity-40",
                children: "Keep address"
              }), (0, a.jsx)("button", {
                type: "button",
                onClick: () => void F(),
                disabled: E,
                className: "min-h-12 touch-manipulation rounded-[14px] bg-red-600 text-[13px] font-semibold text-white disabled:opacity-40",
                children: E ? "Removing" : "Remove"
              })]
            })]
          })]
        }) : null
      })]
    });
  }]);
});

  Z.define(401993, e => {
  "use strict";

  e.s(["slugify", 0, function (e) {
    return e ? String(e).toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") : "";
  }]);
});

  Z.define(414716, e => {
  "use strict";

  var r = e.i(843476),
    t = e.i(271645),
    n = e.i(179490);
  e.s(["default", 0, function ({
    branches: a,
    selectedBranch: i,
    onSelectBranch: o,
    userLocation: s,
    compact: l = !1,
    showPopupActions: d = !0
  }) {
    let c = (0, t.useRef)(null),
      u = (0, t.useRef)(null),
      p = (0, t.useRef)({}),
      m = (0, t.useRef)(null),
      [f, g] = (0, t.useState)(!1);
    return (0, t.useEffect)(() => {
      let r = !0;
      return async function () {
        let t = (await e.A(871400)).default;
        if (!document.getElementById("leaflet-css")) {
          let e = document.createElement("link");
          e.id = "leaflet-css", e.rel = "stylesheet", e.href = (window.OO.boot.assetRoot||"/")+"online-order/vendor/leaflet.css", document.head.appendChild(e), await new Promise(e => setTimeout(e, 400));
        }
        if (!r || !c.current || u.current) return;
        let n = t.map(c.current, {
          center: [-39, 175.5],
          zoom: 6,
          zoomControl: !0,
          scrollWheelZoom: !l
        });
        t.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap",
          maxZoom: 18
        }).addTo(n), u.current = n, m.current = t, g(!0);
        let a = () => {
            u.current && u.current.invalidateSize();
          },
          i = () => {
            "visible" === document.visibilityState && u.current && setTimeout(() => u.current.invalidateSize(), 100);
          };
        window.addEventListener("resize", a), document.addEventListener("visibilitychange", i), setTimeout(() => {
          u.current && u.current.invalidateSize();
        }, 250), u.current._cleanup = () => {
          window.removeEventListener("resize", a), document.removeEventListener("visibilitychange", i);
        };
      }(), () => {
        r = !1, u.current && (u.current._cleanup && u.current._cleanup(), u.current.remove(), u.current = null);
      };
    }, [l]), (0, t.useEffect)(() => {
      if (!u.current || !m.current || !f) return;
      let e = u.current,
        r = m.current;
      Object.entries(p.current).forEach(([r, t]) => {
        "userLoc" !== r && !r.startsWith("_") && t && t.marker && e.removeLayer(t.marker);
      });
      let t = p.current.userLoc ? {
          userLoc: p.current.userLoc
        } : {},
        s = r.latLngBounds();
      a.filter(e => (0, n.hasBranchCoordinates)(e)).forEach((a, i) => {
        let c = void 0 !== a.distance,
          u = c ? `<span style="font-weight:700; margin-right:6px; border-right:1px solid rgba(var(--brand-surface-rgb),0.55); padding-right:6px;">${a.distance}km</span>` : "",
          p = l ? `<div style="position:absolute; transform:translate(-50%, -50%); display:grid; place-items:center; width:34px; height:34px; background-color:var(--brand-primary); color:var(--brand-surface-soft); border:3px solid var(--brand-surface-soft); border-radius:50% 50% 50% 8px; box-shadow:0 5px 14px rgba(0,64,240,0.28); font-size:11px; font-weight:900; rotate:-45deg;"><span style="rotate:45deg;">${i + 1}</span></div>` : `<div style="position:absolute; transform:translate(-50%, -50%); display:flex; align-items:center; background-color:var(--brand-primary); color:var(--brand-surface-soft); border:2px solid var(--brand-surface-soft); border-radius:6px; padding:5px 12px; font-size:12px; font-weight:800; white-space:nowrap; transition:all 0.2s ease;">${u}<span>${a.name}</span></div>`,
          m = `<div style="position:absolute; transform:translate(-50%, -50%); display:flex; align-items:center; background-color:var(--brand-surface-soft); color:var(--brand-primary); border:2px solid var(--brand-primary); border-radius:${l ? "999px" : "6px"}; padding:${l ? "6px 11px 6px 7px" : "6px 14px"}; font-size:${l ? "11px" : "13px"}; font-weight:900; white-space:nowrap; box-shadow:${l ? "0 7px 18px rgba(19,19,22,0.18)" : "none"}; z-index:1000;">${l ? `<span style="display:grid; place-items:center; width:22px; height:22px; margin-right:6px; border-radius:50%; background-color:var(--brand-primary); color:var(--brand-surface-soft);">${i + 1}</span>` : ""}${c ? `<span style="font-weight:800; margin-right:6px; border-right:1px solid rgba(var(--brand-primary-rgb),0.3); padding-right:6px;">${a.distance}km</span>` : ""}<span>${a.name}</span></div>`,
          f = r.divIcon({
            className: "",
            html: p,
            iconSize: null
          }),
          g = r.divIcon({
            className: "",
            html: m,
            iconSize: null
          }),
          b = d ? "open" === a.status && a.onlineKey ? '<a href="/menu" style="background-color:var(--brand-primary); color:var(--brand-surface-soft); padding:8px 12px; border-radius:6px; font-size:13px; font-weight:800; text-decoration:none; display:block;">Browse Menu</a>' : '<p style="border:1px solid rgba(var(--brand-primary-rgb),0.28); color:var(--brand-ink); background-color:var(--brand-surface); padding:8px 12px; border-radius:6px; font-size:12px; font-weight:700; margin:0;">Ordering opens when this store is live.</p>' : '<p style="border:1px solid rgba(var(--brand-primary-rgb),0.22); color:var(--brand-primary); background-color:rgba(var(--brand-primary-rgb),0.07); padding:8px 12px; border-radius:8px; font-size:12px; font-weight:700; margin:0;">Selected for ordering</p>',
          x = d ? `<a href="${(0, n.getBranchDirectionsUrl)(a)}" target="_blank" rel="noopener noreferrer" style="font-size:12px; font-weight:600; color:var(--brand-primary); text-decoration:none; display:inline-block; margin-top:2px;">
                Get Directions
              </a>` : "",
          h = r.marker([a.lat, a.lng], {
            icon: f,
            title: `Select ${a.name}`,
            alt: `Select ${a.name}`,
            keyboard: !0
          }).addTo(e);
        (!l || d) && h.bindPopup(`
          <div style="text-align:center; min-width:180px; font-family: system-ui, sans-serif;">
            <p style="font-weight:800; font-size:15px; margin:0 0 4px; color:var(--brand-ink);">${a.name}</p>
            <p style="font-size:12px; color:rgba(var(--brand-ink-rgb),0.65); margin:0 0 12px; line-height:1.4;">${a.address}</p>
            <div style="display:flex; flex-direction:column; gap:8px;">
              ${b}
              ${x}
            </div>
          </div>
        `), h.getElement()?.setAttribute("aria-label", `Select ${a.name}`), h.on("click", () => o(a.id)), t[a.id] = {
          marker: h,
          defaultIcon: f,
          activeMarkerIcon: g
        }, s.extend([a.lat, a.lng]);
      }), p.current = t, i && t[i.id] && t[i.id].marker.setIcon(t[i.id].activeMarkerIcon);
    }, [a, l, f, o, i, d]), (0, t.useEffect)(() => {
      if (!u.current || !i || !(0, n.hasBranchCoordinates)(i)) return;
      let e = !p.current._hasPanned,
        r = () => {
          u.current && (u.current.invalidateSize(), e ? (u.current.setView([i.lat, i.lng], l ? 10 : 11), p.current._hasPanned = !0) : u.current.flyTo([i.lat, i.lng], l ? 10 : 11, {
            duration: .8
          }));
        };
      e ? setTimeout(r, 100) : r(), Object.entries(p.current).forEach(([e, r]) => {
        if ("userLoc" === e || e.startsWith("_") || !r || !r.marker) return;
        let {
          marker: t,
          defaultIcon: n,
          activeMarkerIcon: a
        } = r;
        t.setIcon(e === i.id ? a : n), e !== i.id || l || t.openPopup();
      });
    }, [l, i, f]), (0, t.useEffect)(() => {
      let e = u.current,
        r = m.current;
      if (!e || !r || !f || i) return;
      let t = a.filter(e => (0, n.hasBranchCoordinates)(e));
      if (t.length > 0) {
        let n = t.slice(0, 8),
          a = r.latLngBounds(n.map(e => [e.lat, e.lng]));
        s && a.extend([s.latitude, s.longitude]);
        let i = window.innerWidth < 768;
        e.fitBounds(a, {
          padding: i ? [30, 30] : [100, 100],
          maxZoom: 11,
          duration: 1.2
        });
      }
    }, [a, s, f, i]), (0, t.useEffect)(() => {
      let e = u.current,
        r = m.current;
      if (e && r && s && f) if (p.current.userLoc) p.current.userLoc.marker.setLatLng([s.latitude, s.longitude]);else {
        let t = r.divIcon({
            className: "",
            html: `
          <div style="position: absolute; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center;">
            <div style="width:18px; height:18px; background-color:var(--brand-primary); border:3px solid var(--brand-surface-soft); border-radius:6px; outline:2px solid rgba(var(--brand-primary-rgb),0.35);"></div>
          </div>
        `,
            iconSize: null
          }),
          n = r.marker([s.latitude, s.longitude], {
            icon: t,
            zIndexOffset: 900
          }).addTo(e).bindPopup('<div style="font-weight:bold; text-align:center;">Your Current Location</div>');
        p.current.userLoc = {
          marker: n
        };
      }
    }, [s, f]), (0, r.jsxs)("div", {
      className: "w-full h-full relative",
      children: [(0, r.jsx)("div", {
        ref: c,
        className: "h-full w-full",
        style: {
          minHeight: l ? "220px" : "300px"
        }
      }), !f && (0, r.jsx)("div", {
        className: "absolute inset-0 flex items-center justify-center bg-brand-surface",
        children: (0, r.jsxs)("div", {
          className: "text-center",
          children: [(0, r.jsx)("div", {
            className: "mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-brand-primary border-t-transparent"
          }), (0, r.jsx)("span", {
            className: "text-sm font-semibold text-brand-ink/60",
            children: "Loading map…"
          })]
        })
      })]
    });
  }]);
});

  Z.define(420865, e => {
  "use strict";

  let t = (0, e.i(456420).default)("map-pin", [["path", {
    d: "M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0",
    key: "1r0f0z"
  }], ["circle", {
    cx: "12",
    cy: "10",
    r: "3",
    key: "ilqhr7"
  }]]);
  e.s(["MapPin", 0, t], 420865);
});

  Z.define(422031, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    i = e.i(618566),
    n = e.i(304729),
    r = e.i(566595),
    s = e.i(263676);
  e.i(651164);
  var l = e.i(917848),
    o = e.i(401993),
    c = e.i(652348),
    d = e.i(987664),
    u = e.i(309808),
    m = e.i(671753),
    p = e.i(153755),
    x = e.i(273341),
    b = e.i(376151),
    h = e.i(343699),
    f = e.i(899907),
    g = e.i(468179),
    y = e.i(754675),
    j = e.i(977617),
    v = e.i(116160);
  function N(e) {
    if ("string" != typeof e) return null;
    let t = e.trim();
    return t && "NULL" !== t && "null" !== t ? t.startsWith("//") ? `https:${t}` : t.startsWith("http://") || t.startsWith("https://") || t.startsWith("/") ? t : null : null;
  }
  function w(e) {
    if ("string" != typeof e) return null;
    let t = e.trim();
    return t && "null" !== t.toLowerCase() ? t : null;
  }
  function k({
    value: e
  }) {
    return (0, t.jsxs)("span", {
      className: "block text-[15px] font-semibold leading-none text-brand-ink sm:text-[16px]",
      children: ["$", e.toFixed(2)]
    });
  }
  function _({
    item: e,
    category: i,
    onSelect: n
  }) {
    let {
        getProductPrice: r
      } = (0, v.usePricetag)(),
      {
        isItemAvailable: s,
        isCategoryAvailable: l,
        nextAvailableTime: o
      } = (0, p.useAvailability)(),
      c = r(e.itemId),
      d = null !== c ? c : Number(e.sale_price) || 0,
      u = N(e.imageUrl ?? e.image_url ?? e.image),
      m = 1 !== e.is_inactive && s(e) && l(i),
      x = (0, a.useMemo)(() => m ? null : w(i?.inactive_reason) || w(e.inactive_reason) || (1 === e.is_inactive ? "Sold out" : null) || o(e) || "Not available right now", [m, i?.inactive_reason, e, o]),
      b = w(e.description),
      h = Array.isArray(e.groups) && e.groups.length > 0;
    return (0, t.jsx)("article", {
      id: `menu-item-${e.itemId}`,
      className: `overflow-hidden rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] ${m ? "" : "opacity-60"}`,
      children: (0, t.jsxs)("button", {
        type: "button",
        disabled: !m,
        onClick: () => n(e, i),
        "aria-label": m ? `View ${(0, y.sentenceLabel)(e.label)}` : `${(0, y.sentenceLabel)(e.label)} unavailable: ${x}`,
        className: "flex min-h-[100px] w-full touch-manipulation items-center gap-3 p-3 text-left active:bg-brand-ink/[0.035] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary/50 disabled:cursor-not-allowed sm:min-h-[118px] sm:gap-3.5 sm:p-3.5",
        children: [(0, t.jsxs)("span", {
          className: "min-w-0 flex-1",
          children: [(0, t.jsx)("span", {
            className: "block text-[16px] font-semibold leading-[1.22] text-brand-ink sm:text-[17px]",
            children: (0, y.sentenceLabel)(e.label)
          }), b && (0, t.jsx)("span", {
            className: "mt-1 line-clamp-2 block text-[12px] leading-[1.35] text-brand-ink-light",
            children: b
          }), (0, t.jsxs)("span", {
            className: "mt-2 flex flex-wrap items-center gap-2",
            children: [(0, t.jsx)(k, {
              value: d
            }), h && m && (0, t.jsx)("span", {
              className: "text-[10px] font-medium text-brand-primary",
              children: "Customisable"
            }), !m && (0, t.jsx)("span", {
              className: "text-[10px] font-medium text-brand-ink-light",
              children: x
            })]
          })]
        }), (0, t.jsx)("span", {
          className: "flex shrink-0 items-center gap-2",
          children: u && (0, t.jsx)("span", {
            className: "relative h-[96px] w-[96px] shrink-0 overflow-hidden rounded-[14px] bg-white sm:h-[112px] sm:w-[112px]",
            children: (0, t.jsx)(j.default, {
              src: u,
              alt: "",
              fill: !0,
              sizes: "(max-width: 639px) 96px, 112px",
              className: "object-cover",
              context: "MobileMenuCard",
              meta: {
                itemId: e.itemId,
                label: e.label
              }
            })
          })
        })]
      })
    });
  }
  function C({
    combo: e,
    onSelect: a
  }) {
    let {
        getComboPrice: i
      } = (0, v.usePricetag)(),
      {
        isItemAvailable: n,
        isComboCategoryAvailable: r,
        nextAvailableTime: s
      } = (0, p.useAvailability)(),
      l = i(e.combo_id),
      o = null !== l ? l : Number(e.combo_price) || 0,
      c = N(e.image ?? e.imageUrl ?? e.image_url),
      d = 1 !== e.is_inactive && n(e) && r(e),
      u = w(e.inactive_reason) || (1 === e.is_inactive ? "Sold out" : null) || s(e) || "Not available right now";
    return (0, t.jsx)("article", {
      id: `combo-item-${e.combo_id}`,
      className: `overflow-hidden rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] ${d ? "" : "opacity-60"}`,
      children: (0, t.jsxs)("button", {
        type: "button",
        disabled: !d,
        onClick: () => a(e),
        "aria-label": d ? `View ${(0, y.sentenceLabel)(e.combo_name)}` : `${(0, y.sentenceLabel)(e.combo_name)} unavailable: ${u}`,
        className: "flex min-h-[100px] w-full touch-manipulation items-center gap-3 p-3 text-left active:bg-brand-ink/[0.035] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary/50 disabled:cursor-not-allowed sm:min-h-[118px] sm:gap-3.5 sm:p-3.5",
        children: [(0, t.jsxs)("span", {
          className: "min-w-0 flex-1",
          children: [(0, t.jsx)("span", {
            className: "text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-primary",
            children: "Combo"
          }), (0, t.jsx)("span", {
            className: "mt-0.5 block text-[16px] font-semibold leading-[1.22] text-brand-ink sm:text-[17px]",
            children: (0, y.sentenceLabel)(e.combo_name)
          }), w(e.description) && (0, t.jsx)("span", {
            className: "mt-1 line-clamp-2 block text-[12px] leading-[1.35] text-brand-ink-light",
            children: e.description
          }), (0, t.jsxs)("span", {
            className: "mt-2 flex items-center gap-2",
            children: [(0, t.jsx)(k, {
              value: o
            }), !d && (0, t.jsx)("span", {
              className: "text-[10px] font-medium text-brand-ink-light",
              children: u
            })]
          })]
        }), (0, t.jsx)("span", {
          className: "flex shrink-0 items-center gap-2",
          children: c && (0, t.jsx)("span", {
            className: "relative h-[96px] w-[96px] shrink-0 overflow-hidden rounded-[14px] bg-white sm:h-[112px] sm:w-[112px]",
            children: (0, t.jsx)(j.default, {
              src: c,
              alt: "",
              fill: !0,
              sizes: "(max-width: 639px) 96px, 112px",
              className: "object-cover",
              context: "MobileComboCard",
              meta: {
                comboId: e.combo_id,
                name: e.combo_name
              }
            })
          })
        })]
      })
    });
  }
  e.s(["default", 0, function () {
    let {
        currentBranch: e,
        isDetecting: j,
        restaurantConfig: v,
        branchStatuses: N,
        orderType: w
      } = (0, c.useBranch)(),
      {
        isCategoryAvailable: k,
        isComboCategoryAvailable: S
      } = (0, p.useAvailability)(),
      {
        isSuggestionPopupVisible: M
      } = (0, d.useCart)(),
      $ = (0, u.useToast)(),
      A = (0, i.useSearchParams)(),
      z = (0, i.useRouter)(),
      I = (0, i.usePathname)(),
      [q, O] = (0, a.useState)([]),
      [P, L] = (0, a.useState)([]),
      [F, E] = (0, a.useState)("__loading"),
      [T, R] = (0, a.useState)("all"),
      [B, U] = (0, a.useState)(""),
      [D, W] = (0, a.useState)(null),
      [K, G] = (0, a.useState)(null),
      [Z, H] = (0, a.useState)(!0),
      [V, Y] = (0, a.useState)(null),
      X = (0, a.useRef)(null),
      J = (0, a.useRef)(""),
      Q = String(v?.timezone || v?.timeZone || e?.timezone || b.DEFAULT_TIME_ZONE),
      ee = (0, a.useCallback)(async () => {
        if (!j && e) {
          H(!0), Y(null);
          try {
            let t = await (0, l.fetchMenu)(e.onlineKey || "", {
              timeZone: Q
            });
            if ("success" !== t.status || !Array.isArray(t.categories)) throw Error(t.message || "Failed to load menu");
            let a = t.categories,
              i = Array.isArray(t.combos) ? t.combos : [];
            O(a), L(i), E(e => "__loading" !== e ? e : a.length ? String(a[0].categoryId) : i.length ? `combo_${i[0].combo_cat_id}` : "empty");
          } catch (e) {
            Y(e?.message || "Something went wrong. Please try again.");
          } finally {
            H(!1);
          }
        }
      }, [e, j, w, Q]);
    (0, a.useEffect)(() => {
      ee();
    }, [ee]);
    let et = (0, a.useMemo)(() => {
        let e = new Map();
        for (let t of P) {
          let a = String(t.combo_cat_id);
          e.has(a) || e.set(a, {
            id: a,
            label: t.category_name || "Combos",
            position: Number(t.category_position || 0),
            combos: []
          }), e.get(a)?.combos.push(t);
        }
        return Array.from(e.values()).sort((e, t) => e.position - t.position);
      }, [P]),
      ea = (0, a.useCallback)((e, t) => {
        let a = e?.sub_cat_id != null && Array.isArray(t?.sub_categories) ? t.sub_categories.find(t => String(t.sub_cat_id) === String(e.sub_cat_id)) : null;
        W({
          ...e,
          _categoryCtx: t ? {
            categoryId: t.categoryId,
            label: t.label,
            availability: t.availability ?? null,
            catAvailability: t.catAvailability ?? null
          } : null,
          _subCategoryCtx: a ? {
            sub_cat_id: a.sub_cat_id,
            sub_cat_name: a.sub_cat_name,
            availability: a.availability ?? null
          } : null
        });
      }, []);
    (0, a.useEffect)(() => {
      if (Z || V || !q.length && !P.length) return;
      let e = A.get("category"),
        t = A.get("id"),
        a = A.get("item"),
        i = A.get("combo"),
        n = A.toString();
      if (!e && !t && !a && !i) {
        J.current = "";
        return;
      }
      if (J.current !== n) {
        if (e) {
          let n = q.find(n => (0, o.slugify)(n.label) === e || String(n.categoryId) === (!t || a || i ? e : t));
          n && E(String(n.categoryId));
        }
        if (a || t && !e && !i) {
          let e = t ? Number(t) : null;
          for (let t of q) {
            let i = (t.items || []).find(t => e && t.itemId === e || a && (0, o.slugify)(t.label) === a);
            if (i) {
              E(String(t.categoryId)), ea(i, t), J.current = n;
              return;
            }
          }
        }
        if (i) {
          let e = t ? Number(t) : null,
            a = P.find(t => e && t.combo_id === e || (0, o.slugify)(t.combo_name) === i);
          a && (E(`combo_${a.combo_cat_id}`), G(a));
        }
        J.current = n;
      }
    }, [q, P, V, Z, A, ea]);
    let ei = (0, a.useCallback)(() => {
        let e = new URLSearchParams(A.toString()),
          t = !1;
        for (let a of ["item", "combo", "id"]) e.has(a) && (e.delete(a), t = !0);
        if (t) {
          let t = e.toString();
          z.replace(t ? `${I}?${t}` : I, {
            scroll: !1
          });
        }
      }, [I, z, A]),
      en = (0, m.normalizeMenuSearchQuery)(B).length > 0,
      er = (0, a.useMemo)(() => (0, m.searchMenu)({
        categories: q,
        combos: P,
        query: B
      }), [q, P, B]),
      es = (0, a.useMemo)(() => q.find(e => String(e.categoryId) === F) || null, [F, q]),
      el = (0, a.useMemo)(() => F.startsWith("combo_") && et.find(e => `combo_${e.id}` === F) || null, [F, et]),
      eo = (0, a.useMemo)(() => {
        if (!es || en) return [];
        let e = new Map(),
          t = !1;
        for (let a of es.items || []) {
          if (null == a.sub_cat_id || "" === a.sub_cat_id) {
            t = !0;
            continue;
          }
          let i = String(a.sub_cat_id);
          e.has(i) || e.set(i, {
            id: i,
            name: (0, y.sentenceLabel)(a.sub_cat_name || "Other"),
            position: Number(a.sub_cat_position || 0)
          });
        }
        let a = Array.from(e.values()).sort((e, t) => e.position - t.position);
        return t && a.length && a.push({
          id: "__none",
          name: "Other",
          position: 9999
        }), a;
      }, [en, es]);
    (0, a.useEffect)(() => {
      "all" !== T && (eo.some(e => e.id === T) || R("all"));
    }, [T, eo]);
    let ec = (0, a.useMemo)(() => {
        if (en) return er.items;
        let e = es?.items || [];
        return "all" !== T && eo.length ? "__none" === T ? e.filter(e => null == e.sub_cat_id || "" === e.sub_cat_id) : e.filter(e => String(e.sub_cat_id) === T) : e;
      }, [T, en, er.items, es, eo.length]),
      ed = en ? er.combos : el?.combos || [],
      eu = (0, a.useMemo)(() => q.reduce((e, t) => e + (t.items?.length || 0), 0), [q]),
      em = ec.length + ed.length,
      ep = (0, a.useMemo)(() => {
        if (!v) return null;
        if (!0 === v.isPaused) return {
          label: "Ordering paused",
          reason: v.pauseReason || "This branch has paused online orders."
        };
        if (!0 === v.onlineInactive || !1 === v.onlineOrderActive) return {
          label: "Ordering unavailable",
          reason: v.onlineInactiveReason || "This branch is not taking online orders right now."
        };
        let t = e?.onlineKey ? N?.[e.onlineKey] : null;
        return !0 === v.isPreorder && t?.isOpen === !1 ? {
          label: "Pre-order available",
          reason: "Orders are queued for the next service window."
        } : null;
      }, [N, e?.onlineKey, v]),
      ex = e => {
        (0, x.haptic)("selection"), E(e), R("all"), U(""), requestAnimationFrame(() => {
          let t = X.current?.querySelector(`[data-category-id="${e}"]`);
          t?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center"
          });
        });
      },
      eb = en ? `Results for “${B.trim()}”` : (0, y.sentenceLabel)(es?.label || el?.label || "Menu");
    return (0, t.jsxs)("div", {
      className: "mx-auto min-h-full w-full max-w-5xl bg-white",
      children: [(0, t.jsx)(g.default, {}), (0, t.jsx)("section", {
        className: "px-4 pb-2 pt-3 sm:px-6 sm:pb-4 sm:pt-6 lg:px-8",
        children: (0, t.jsxs)("div", {
          className: "sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(320px,440px)] sm:items-end sm:gap-6",
          children: [(0, t.jsxs)("div", {
            className: "flex flex-wrap items-baseline gap-x-2 gap-y-0.5",
            children: [(0, t.jsx)("h1", {
              className: "text-[22px] font-semibold leading-none tracking-[-0.03em] text-brand-ink sm:text-[28px]",
              children: "Menu"
            }), (0, t.jsx)("p", {
              className: "text-[12px] text-brand-ink-light",
              children: Z ? "Loading fresh favourites…" : `${q.length} categories \xb7 ${eu} items`
            })]
          }), (0, t.jsxs)("label", {
            className: "mt-2.5 flex min-h-11 items-center gap-3 rounded-[14px] border border-brand-ink/8 bg-[#F5F5F7] px-3 focus-within:ring-2 focus-within:ring-brand-primary/40 sm:mt-0 sm:min-h-12",
            children: [(0, t.jsx)(r.Search, {
              className: "h-[18px] w-[18px] shrink-0 text-brand-ink-light",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), (0, t.jsx)("input", {
              type: "search",
              value: B,
              onChange: e => U(e.target.value),
              "aria-label": "Search menu",
              placeholder: "Search the menu",
              autoComplete: "off",
              spellCheck: !1,
              enterKeyHint: "search",
              className: "min-w-0 flex-1 bg-transparent text-base font-medium text-brand-ink outline-none placeholder:text-brand-ink-light sm:text-[14px]"
            }), B && (0, t.jsx)("button", {
              type: "button",
              onClick: () => U(""),
              "aria-label": "Clear search",
              className: "flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-[11px] bg-white text-brand-ink",
              children: (0, t.jsx)(s.X, {
                className: "h-4 w-4",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            })]
          })]
        })
      }), (0, t.jsx)("div", {
        className: "sticky top-[92px] z-30 border-y border-brand-ink/8 bg-white",
        children: (0, t.jsxs)("div", {
          ref: X,
          className: "hide-scrollbar mx-auto flex max-w-5xl snap-x snap-proximity gap-2 overscroll-x-contain overflow-x-auto px-4 py-1.5 scroll-px-4 scroll-smooth sm:px-6 sm:py-2 sm:scroll-px-6 lg:px-8 lg:scroll-px-8",
          "aria-label": "Menu categories",
          children: [et.map(e => {
            let a = `combo_${e.id}`,
              i = !en && F === a;
            return (0, t.jsx)("button", {
              type: "button",
              "data-category-id": a,
              onClick: () => ex(a),
              className: `min-h-11 shrink-0 snap-start touch-manipulation rounded-[12px] px-3 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${i ? "bg-brand-primary text-white" : "bg-[#F5F5F7] text-brand-ink"}`,
              children: (0, y.sentenceLabel)(e.label)
            }, a);
          }), q.map(e => {
            let a = String(e.categoryId),
              i = !en && F === a;
            return (0, t.jsx)("button", {
              type: "button",
              "data-category-id": a,
              onClick: () => ex(a),
              className: `min-h-11 shrink-0 snap-start touch-manipulation rounded-[12px] px-3 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${i ? "bg-brand-primary text-white" : "bg-[#F5F5F7] text-brand-ink"}`,
              children: (0, y.sentenceLabel)(e.label)
            }, a);
          })]
        })
      }), (0, t.jsxs)("main", {
        className: "px-4 pb-6 pt-4 sm:px-6 sm:pt-5 lg:px-8",
        children: [ep && (0, t.jsxs)("div", {
          className: "mb-4 flex items-start gap-3 rounded-[14px] bg-[#EAF0FF] px-3 py-3",
          children: [(0, t.jsx)(n.Clock3, {
            className: "mt-0.5 h-[18px] w-[18px] shrink-0 text-brand-primary",
            strokeWidth: 2,
            "aria-hidden": "true"
          }), (0, t.jsxs)("p", {
            className: "text-[12px] leading-[1.4] text-brand-ink",
            children: [(0, t.jsxs)("span", {
              className: "font-semibold",
              children: [ep.label, "."]
            }), " ", ep.reason]
          })]
        }), Z && (0, t.jsx)("div", {
          "aria-label": "Loading menu",
          className: "grid gap-3 sm:grid-cols-2",
          children: [0, 1, 2, 3, 4].map(e => (0, t.jsx)("div", {
            className: "h-[116px] animate-pulse rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] sm:h-[136px]"
          }, e))
        }), !Z && V && (0, t.jsxs)("div", {
          className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-8 text-center",
          children: [(0, t.jsx)("h2", {
            className: "text-[18px] font-semibold text-brand-ink",
            children: "Couldn't load the menu"
          }), (0, t.jsx)("p", {
            className: "mt-2 text-[13px] leading-relaxed text-brand-ink-light",
            children: V
          }), (0, t.jsx)("button", {
            type: "button",
            onClick: ee,
            className: "mt-5 h-11 rounded-[14px] bg-brand-primary px-5 text-[14px] font-semibold text-white",
            children: "Try again"
          })]
        }), !Z && !V && (0, t.jsxs)(t.Fragment, {
          children: [(0, t.jsxs)("div", {
            className: "mb-3 flex items-center justify-between gap-3",
            children: [(0, t.jsxs)("div", {
              className: "min-w-0",
              children: [(0, t.jsx)("h2", {
                className: "truncate text-[20px] font-semibold tracking-[-0.02em] text-brand-ink",
                children: eb
              }), (0, t.jsxs)("p", {
                className: "mt-0.5 text-[11px] text-brand-ink-light",
                children: [em, " ", 1 === em ? "item" : "items"]
              })]
            }), en && (0, t.jsx)("button", {
              type: "button",
              onClick: () => U(""),
              className: "min-h-11 shrink-0 touch-manipulation rounded-[12px] bg-[#F5F5F7] px-3 text-[12px] font-semibold text-brand-primary",
              children: "Clear"
            })]
          }), !en && eo.length > 1 && (0, t.jsxs)("div", {
            className: "hide-scrollbar -mx-4 mb-3 flex snap-x snap-proximity gap-2 overscroll-x-contain overflow-x-auto px-4 scroll-px-4 scroll-smooth sm:-mx-6 sm:px-6 sm:scroll-px-6 lg:-mx-8 lg:px-8 lg:scroll-px-8",
            children: [(0, t.jsx)("button", {
              type: "button",
              onClick: () => R("all"),
              className: `min-h-11 shrink-0 snap-start touch-manipulation rounded-[11px] px-3 text-[11px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${"all" === T ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
              children: "All"
            }), eo.map(e => (0, t.jsx)("button", {
              type: "button",
              onClick: () => R(e.id),
              className: `min-h-11 shrink-0 snap-start touch-manipulation rounded-[11px] px-3 text-[11px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${T === e.id ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
              children: e.name
            }, e.id))]
          }), 0 === em ? (0, t.jsxs)("div", {
            className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-8 text-center",
            children: [(0, t.jsx)("h3", {
              className: "text-[17px] font-semibold text-brand-ink",
              children: en ? "No matches found" : "Nothing here right now"
            }), (0, t.jsx)("p", {
              className: "mt-2 text-[13px] leading-relaxed text-brand-ink-light",
              children: en ? "Try another product name or choose a category." : "Choose another category to keep browsing."
            })]
          }) : (0, t.jsxs)("div", {
            className: "grid gap-3 sm:grid-cols-2",
            children: [ed.map(e => (0, t.jsx)(C, {
              combo: e,
              onSelect: G
            }, e.combo_id)), ec.map(e => {
              let a = en ? q.find(t => String(t.categoryId) === String(e._categoryId)) || q.find(t => t.label === e._categoryLabel) || null : es;
              return (0, t.jsx)(_, {
                item: e,
                category: a,
                onSelect: ea
              }, `${e.itemId}-${a?.categoryId || "unknown"}`);
            })]
          }), !en && es && !k(es) && (0, t.jsx)("p", {
            className: "mt-4 text-center text-[11px] text-brand-ink-light",
            children: "This category is not available for the selected time."
          }), !en && el && el.combos[0] && !S(el.combos[0]) && (0, t.jsx)("p", {
            className: "mt-4 text-center text-[11px] text-brand-ink-light",
            children: "These combos are not available for the selected time."
          })]
        })]
      }), (0, t.jsx)(h.default, {
        item: D,
        onClose: () => {
          W(null), ei();
        },
        onSuccess: () => {
          M() || $.success("Added to cart", {
            title: (0, y.sentenceLabel)(D?.label || D?.name),
            duration: 800
          });
        }
      }), (0, t.jsx)(f.default, {
        combo: K,
        onClose: () => {
          G(null), ei();
        },
        onSuccess: () => $.success("Added to cart", {
          title: (0, y.sentenceLabel)(K?.combo_name),
          duration: 800
        })
      })]
    });
  }], 422031);
});

  Z.define(447061, e => {
  "use strict";

  let a = (0, e.i(456420).default)("badge-info", [["path", {
    d: "M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z",
    key: "3c2336"
  }], ["line", {
    x1: "12",
    x2: "12",
    y1: "16",
    y2: "12",
    key: "1y1yb1"
  }], ["line", {
    x1: "12",
    x2: "12.01",
    y1: "8",
    y2: "8",
    key: "110wyk"
  }]]);
  e.s(["BadgeInfo", 0, a], 447061);
});

  Z.define(466787, e => {
  "use strict";

  var t = e.i(755566);
  e.s(["TriangleAlert", () => t.default]);
});

  Z.define(468179, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    r = e.i(652348),
    i = e.i(174080),
    s = e.i(846932),
    n = e.i(88653),
    l = e.i(701688);
  function o({
    isOpen: e,
    title: r = "Online Ordering Unavailable",
    reason: d,
    branchName: c,
    branchAddress: m,
    onClose: u
  }) {
    let [p, x] = (0, a.useState)(!1);
    if ((0, l.useBodyScrollLock)(e), (0, a.useEffect)(() => {
      x(!0);
    }, []), !p) return null;
    let h = d && "null" !== d && ("string" != typeof d || "" !== d.trim()) ? d : "Online ordering is currently unavailable. Please try again later.";
    return (0, i.createPortal)((0, t.jsx)(n.AnimatePresence, {
      children: e && (0, t.jsxs)(t.Fragment, {
        children: [(0, t.jsx)(s.motion.div, {
          initial: {
            opacity: 0
          },
          animate: {
            opacity: 1
          },
          exit: {
            opacity: 0
          },
          transition: {
            duration: .1
          },
          className: "fixed inset-0 bg-brand-ink/70 z-[10000]",
          onClick: u
        }, "backdrop"), (0, t.jsx)("div", {
          className: "fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none",
          children: (0, t.jsxs)(s.motion.div, {
            initial: {
              opacity: 0,
              scale: .96
            },
            animate: {
              opacity: 1,
              scale: 1
            },
            exit: {
              opacity: 0,
              scale: .96,
              transition: {
                duration: .12
              }
            },
            transition: {
              duration: .14,
              ease: "easeOut"
            },
            role: "alertdialog",
            "aria-modal": "true",
            "aria-labelledby": "online-inactive-title",
            className: "pointer-events-auto w-full max-w-sm rounded-md border border-brand-ink/10 bg-white shadow-2xl overflow-hidden",
            children: [(0, t.jsx)("div", {
              className: "h-1 w-full bg-brand-primary"
            }), (0, t.jsxs)("div", {
              className: "flex items-start gap-3 px-4 pt-4 pb-3",
              children: [(0, t.jsx)("span", {
                className: "text-brand-primary mt-0.5",
                children: (0, t.jsx)("svg", {
                  className: "w-5 h-5 flex-shrink-0",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor",
                  strokeWidth: 2,
                  children: (0, t.jsx)("path", {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    d: "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728A9 9 0 015.636 5.636"
                  })
                })
              }), (0, t.jsxs)("div", {
                className: "flex-1 min-w-0",
                children: [c && (0, t.jsxs)("div", {
                  className: "mb-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-brand-primary/10 text-brand-primary max-w-full",
                  children: [(0, t.jsx)("svg", {
                    className: "w-3 h-3 flex-shrink-0",
                    fill: "currentColor",
                    viewBox: "0 0 20 20",
                    children: (0, t.jsx)("path", {
                      fillRule: "evenodd",
                      d: "M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z",
                      clipRule: "evenodd"
                    })
                  }), (0, t.jsx)("span", {
                    className: "font-display font-black text-[10px] uppercase tracking-[0.08em] truncate",
                    children: c
                  })]
                }), (0, t.jsx)("p", {
                  id: "online-inactive-title",
                  className: "font-black text-sm text-gray-900 mb-1 font-display uppercase tracking-tight",
                  children: r
                }), (0, t.jsx)("p", {
                  className: "text-sm text-gray-600 leading-snug whitespace-pre-line",
                  children: h
                }), m && (0, t.jsxs)("p", {
                  className: "mt-2 text-[11px] text-gray-400 leading-snug flex items-start gap-1",
                  children: [(0, t.jsxs)("svg", {
                    className: "w-3 h-3 flex-shrink-0 mt-0.5",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor",
                    strokeWidth: 2,
                    children: [(0, t.jsx)("path", {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      d: "M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z"
                    }), (0, t.jsx)("path", {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      d: "M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    })]
                  }), (0, t.jsx)("span", {
                    className: "truncate",
                    children: m
                  })]
                })]
              }), (0, t.jsx)("button", {
                onClick: u,
                "aria-label": "Close",
                className: "flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors mt-0.5",
                children: (0, t.jsx)("svg", {
                  className: "w-4 h-4",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor",
                  strokeWidth: 2.5,
                  children: (0, t.jsx)("path", {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    d: "M6 18L18 6M6 6l12 12"
                  })
                })
              })]
            }), (0, t.jsx)("div", {
              className: "px-4 pb-4 pt-1",
              children: (0, t.jsx)("button", {
                onClick: u,
                className: "w-full bg-brand-primary text-white py-2.5 rounded-md font-display font-black text-xs uppercase tracking-wider hover:bg-brand-primary-dark transition-all active:scale-[0.98]",
                children: "OK"
              })
            })]
          }, "card")
        })]
      })
    }), document.body);
  }
  var d = e.i(394556);
  e.s(["default", 0, function () {
    let {
        currentBranch: e,
        restaurantConfig: i
      } = (0, r.useBranch)(),
      [s, n] = (0, a.useState)(!1);
    if ((0, a.useEffect)(() => {
      if (!i) return;
      let t = e?.onlineKey || i.online_key;
      if (!t || !0 !== i.isPaused && !0 !== i.onlineInactive && !1 !== i.onlineOrderActive) return;
      let a = `${d.default.storagePrefix}-online-inactive-shown:${t}`;
      try {
        if ("1" === sessionStorage.getItem(a)) return;
        sessionStorage.setItem(a, "1");
      } catch {}
      n(!0);
    }, [i, e?.onlineKey]), !i) return null;
    let l = i.pauseReason || i.onlineInactiveReason || null,
      c = e?.name || i.hotelName || i.hotel_name || i.name || "",
      m = Array.isArray(i.address) ? i.address[0] : null,
      u = [m?.streetName, m?.city].filter(Boolean),
      p = u.length > 0 ? u.join(", ") : e?.address || "";
    return (0, t.jsx)(o, {
      isOpen: s,
      title: i.isPaused ? "Ordering Paused" : "Online Ordering Unavailable",
      reason: l,
      branchName: c,
      branchAddress: p,
      onClose: () => n(!1)
    });
  }], 468179);
});

  Z.define(475577, e => {
  "use strict";

  let r = (0, e.i(456420).default)("package-check", [["path", {
    d: "M12 22V12",
    key: "d0xqtd"
  }], ["path", {
    d: "m16 17 2 2 4-4",
    key: "uh5qu3"
  }], ["path", {
    d: "M21 11.127V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.729l7 4a2 2 0 0 0 2 .001l1.32-.753",
    key: "kpkbpo"
  }], ["path", {
    d: "M3.29 7 12 12l8.71-5",
    key: "19ckod"
  }], ["path", {
    d: "m7.5 4.27 8.997 5.148",
    key: "9yrvtv"
  }]]);
  e.s(["PackageCheck", 0, r], 475577);
});

  Z.define(482022, e => {
  "use strict";

  let t = (0, e.i(456420).default)("external-link", [["path", {
    d: "M15 3h6v6",
    key: "1q9fwt"
  }], ["path", {
    d: "M10 14 21 3",
    key: "gplh6r"
  }], ["path", {
    d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
    key: "a6xqqp"
  }]]);
  e.s(["ExternalLink", 0, t], 482022);
});

  Z.define(486935, e => {
  "use strict";

  var r = e.i(843476),
    t = e.i(271645),
    a = e.i(522016),
    n = e.i(618566),
    i = e.i(853138),
    s = e.i(689664),
    l = e.i(551757),
    d = e.i(298007),
    c = e.i(304729),
    o = e.i(482022),
    u = e.i(420865),
    m = e.i(475577),
    x = e.i(256102),
    p = e.i(263448),
    b = e.i(786563),
    h = e.i(98828),
    f = e.i(990746),
    g = e.i(652348),
    y = e.i(519957),
    k = e.i(280277),
    j = e.i(509182);
  e.i(651164);
  var N = e.i(917848),
    v = e.i(273341);
  let w = [{
      key: "PENDING",
      label: "Received",
      Icon: x.ReceiptText
    }, {
      key: "ACCEPTED",
      label: "Preparing",
      Icon: d.ChefHat
    }, {
      key: "READY",
      label: "Ready",
      Icon: m.PackageCheck
    }, {
      key: "COMPLETED",
      label: "Done",
      Icon: l.CheckCircle2
    }],
    C = ["PENDING", "ACCEPTED", "READY", "COMPLETED"],
    E = {
      PROCESSING: {
        label: "Connecting",
        badge: "bg-[#EAF0FF] text-brand-primary",
        dot: "bg-brand-primary"
      },
      PENDING: {
        label: "Received",
        badge: "bg-[#EAF0FF] text-brand-primary",
        dot: "bg-brand-primary"
      },
      ACCEPTED: {
        label: "Preparing",
        badge: "bg-amber-50 text-amber-700",
        dot: "bg-amber-500"
      },
      READY: {
        label: "Ready",
        badge: "bg-emerald-50 text-emerald-700",
        dot: "bg-emerald-500"
      },
      COMPLETED: {
        label: "Completed",
        badge: "bg-[#F5F5F7] text-brand-ink-light",
        dot: "bg-brand-ink/45"
      },
      REJECTED: {
        label: "Cancelled",
        badge: "bg-red-50 text-red-700",
        dot: "bg-red-500"
      }
    };
  function S(e) {
    let r = Number(e);
    return Number.isFinite(r) ? r : 0;
  }
  function T(e, r = "Pacific/Auckland") {
    let t = function (e) {
      if (!e) return null;
      let r = String(e).replace(" ", "T");
      /[zZ]|[+-]\d{2}:?\d{2}$/.test(r) || (r += "Z");
      let t = new Date(r);
      return Number.isNaN(t.getTime()) ? null : t;
    }(e);
    return t ? t.toLocaleString(r.startsWith("Australia/") ? "en-AU" : "en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: !0,
      timeZone: r
    }) : "";
  }
  function _(e, r) {
    let t = String(e || "").toUpperCase().trim(),
      a = String(r || "").toLowerCase().trim();
    return ["REJECTED", "CANCELLED", "FAILED"].includes(t) ? "REJECTED" : "completed" === a || "COMPLETED" === t ? "COMPLETED" : "ready" === a || "READY" === t ? "READY" : "ACCEPTED" === t ? "ACCEPTED" : "PENDING" === t || "PLACED" === t ? "PENDING" : "PROCESSING";
  }
  function D() {
    return (0, r.jsxs)("main", {
      className: "mx-auto min-h-[70vh] w-full max-w-5xl bg-white px-4 pb-8 pt-5 sm:px-6 sm:pt-6 lg:px-8",
      "aria-label": "Loading order tracker",
      children: [(0, r.jsx)("div", {
        className: "zeta-app-skeleton h-3 w-24 rounded"
      }), (0, r.jsx)("div", {
        className: "zeta-app-skeleton mt-3 h-8 w-48 rounded-lg"
      }), (0, r.jsx)("div", {
        className: "zeta-app-skeleton mt-3 h-4 w-72 max-w-full rounded"
      }), (0, r.jsxs)("div", {
        className: "mt-5 grid gap-4 md:grid-cols-2",
        children: [(0, r.jsx)("div", {
          className: "zeta-app-skeleton h-[260px] rounded-[18px]"
        }), (0, r.jsx)("div", {
          className: "zeta-app-skeleton h-[360px] rounded-[18px]"
        })]
      })]
    });
  }
  function P({
    label: e,
    value: t,
    strong: a = !1
  }) {
    return null == t || "" === t ? null : (0, r.jsxs)("div", {
      className: "min-w-0",
      children: [(0, r.jsx)("dt", {
        className: "text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-ink/42",
        children: e
      }), (0, r.jsx)("dd", {
        className: `mt-1 break-words text-[12px] leading-snug ${a ? "font-bold text-brand-primary" : "font-semibold text-brand-ink"}`,
        children: t
      })]
    });
  }
  function O() {
    let e = (0, n.useSearchParams)(),
      l = (0, n.useRouter)(),
      {
        user: d
      } = (0, f.useAuth)(),
      {
        branches: m,
        currentBranch: O
      } = (0, g.useBranch)(),
      I = e.get("id") || e.get("order_id") || "",
      R = e.get("storeOnlineKey") || e.get("onlineKey") || "",
      [L, A] = (0, t.useState)(null),
      [$, M] = (0, t.useState)(null),
      [F, Y] = (0, t.useState)(!0),
      [G, z] = (0, t.useState)(""),
      [U, B] = (0, t.useState)(null),
      [Z, H] = (0, t.useState)(""),
      [K, W] = (0, t.useState)(null),
      [V, J] = (0, t.useState)(""),
      [q, X] = (0, t.useState)(!1),
      Q = (0, t.useRef)(null),
      ee = (0, t.useRef)(!1);
    if ((0, t.useEffect)(() => {
      if (!I) return void l.replace("/orders/track");
      let e = !0;
      async function r(t = !1) {
        try {
          let r = await (0, N.fetchOrderStatus)(I);
          if (!e) return;
          if (!r?.success) {
            t && z(r?.error || "Order not found.");
            return;
          }
          M(r), B(new Date());
          let a = _(r.orderstatus, r.order_state ?? r.ticket_state);
          "ACCEPTED" !== a || ee.current || (ee.current = !0, (0, N.getDeliveryTrackingLink)(I).then(r => {
            e && r?.success && r.data?.url && H(r.data.url);
          }).catch(() => {})), ["COMPLETED", "REJECTED"].includes(a) && Q.current && clearInterval(Q.current);
        } catch {
          t && z("Could not connect to the kitchen. Please try again.");
        } finally {
          t && e && Y(!1);
        }
      }
      return Y(!0), z(""), H(""), ee.current = !1, (0, N.getOrderData)(I, R || void 0).then(async r => {
        if (!e || !r?.success || !r.data) return;
        A(r.data), W(r.data.rating ?? null), J(r.data.feedback ?? "");
        let t = Number(r.data.storeId || r.data.hotelId || 0);
        if (null == r.data.rating && t) {
          let r = await (0, N.getOrderRating)(t, String(I));
          e && r.success && r.data?.rating != null && (W(r.data.rating), J(r.data.feedback || ""));
        }
      }).catch(() => {}), r(!0), Q.current = setInterval(() => void r(!1), 2e4), () => {
        e = !1, Q.current && clearInterval(Q.current);
      };
    }, [R, I, l]), F && !$) return (0, r.jsx)(D, {});
    if (G && !$) return (0, r.jsx)("main", {
      className: "mx-auto flex min-h-[70vh] w-full max-w-xl items-center bg-white px-4 py-8 text-brand-ink sm:px-6",
      children: (0, r.jsxs)("section", {
        className: "w-full rounded-[18px] border border-red-100 bg-red-50/60 p-5 text-center",
        children: [(0, r.jsx)("span", {
          className: "mx-auto grid h-12 w-12 place-items-center rounded-[14px] bg-white text-red-600",
          children: (0, r.jsx)(i.AlertTriangle, {
            className: "h-6 w-6",
            strokeWidth: 2,
            "aria-hidden": "true"
          })
        }), (0, r.jsx)("h1", {
          className: "mt-3 text-[20px] font-bold",
          children: "Order not found"
        }), (0, r.jsx)("p", {
          className: "mt-1 text-[12px] leading-relaxed text-brand-ink-light",
          children: G
        }), (0, r.jsxs)("div", {
          className: "mt-4 grid grid-cols-2 gap-2",
          children: [(0, r.jsx)(a.default, {
            href: "/orders/track",
            className: "flex min-h-12 touch-manipulation items-center justify-center rounded-[13px] bg-brand-primary px-3 text-[12px] font-semibold text-white",
            children: "Try another ID"
          }), (0, r.jsx)(a.default, {
            href: "/orders",
            className: "flex min-h-12 touch-manipulation items-center justify-center rounded-[13px] bg-white px-3 text-[12px] font-semibold text-brand-ink",
            children: "My orders"
          })]
        })]
      })
    });
    let er = _($?.orderstatus || L?.orderStatus, $?.order_state ?? $?.ticket_state ?? L?.orderState),
      et = E[er],
      ea = "COMPLETED" === er ? 100 : "READY" === er ? 76 : "ACCEPTED" === er ? 44 : "REJECTED" === er ? 100 : 12,
      en = "REJECTED" === er,
      ei = "PROCESSING" === er ? 0 : Math.max(0, C.indexOf(er)),
      es = L?.lineItems || L?.orderData || L?.items || [],
      el = String(L?.orderType || L?.ticket_type || "").toUpperCase().includes("DELIVERY"),
      ed = (0, y.resolveBranchForOrder)(L, R, m),
      ec = String(L?.timezone || L?.timeZone || $?.timezone || ed?.timezone || "Pacific/Auckland"),
      eo = String($?.delivery_type || L?.deliveryType || "").toUpperCase(),
      eu = "SCHEDULED" === eo ? T($?.deliver_time || L?.scheduledTime, ec) : "",
      em = String($?.pickup_number || $?.pickupNumber || L?.pickup_number || L?.pickupNumber || "").trim(),
      ex = L?.hotelName || L?.hotel_name || L?.branchName || ed?.name || O?.name || "Zeta",
      ep = L?.hotelAddress || ed?.address || "",
      eb = L?.createdAt || L?.created_at,
      eh = S(L?.subtotal || L?.total),
      ef = S(L?.billTotal || L?.total),
      eg = S(L?.walletAmount),
      ey = S(L?.discount),
      ek = S(L?.surcharge),
      ej = S(L?.service_fee),
      eN = S(L?.gratuity),
      ev = (0, k.getCouponSummary)(L),
      ew = (0, j.getPaymentPills)(L),
      eC = ["ACCEPTED", "READY", "COMPLETED"].includes(er),
      eE = ed?.storeId || L?.storeId || L?.hotelId || O?.storeId;
    return (0, r.jsxs)("main", {
      className: "mx-auto min-h-[70vh] w-full max-w-5xl bg-white px-4 pb-8 pt-5 text-brand-ink sm:px-6 sm:pt-6 lg:px-8",
      children: [(0, r.jsxs)("header", {
        className: "flex items-start justify-between gap-3",
        children: [(0, r.jsxs)("div", {
          className: "min-w-0",
          children: [(0, r.jsxs)("div", {
            className: "flex items-center gap-2 text-brand-primary",
            children: [(0, r.jsx)(x.ReceiptText, {
              className: "h-4 w-4",
              strokeWidth: 2.1,
              "aria-hidden": "true"
            }), (0, r.jsx)("p", {
              className: "text-[10px] font-bold uppercase tracking-[0.12em]",
              children: "Live order"
            })]
          }), (0, r.jsxs)("h1", {
            className: "mt-1 truncate text-[27px] font-extrabold leading-none tracking-[-0.03em] sm:text-[30px]",
            children: ["#", I.slice(-8).toUpperCase()]
          }), (0, r.jsx)("p", {
            className: "mt-1.5 text-[11px] text-brand-ink-light",
            children: U ? `Updated ${U.toLocaleTimeString("en-NZ", {
              hour: "numeric",
              minute: "2-digit"
            })}` : "Connecting to the kitchen"
          })]
        }), (0, r.jsxs)("span", {
          className: `inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[10px] font-semibold ${et.badge}`,
          children: [(0, r.jsx)("span", {
            className: `h-1.5 w-1.5 rounded-full ${et.dot}`,
            "aria-hidden": "true"
          }), et.label]
        })]
      }), (0, r.jsxs)("div", {
        className: "mt-5 grid items-start gap-4 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]",
        children: [(0, r.jsxs)("div", {
          className: "space-y-4",
          children: [(0, r.jsxs)("section", {
            className: "overflow-hidden rounded-[18px] border border-brand-ink/8 bg-white",
            children: [(0, r.jsxs)("div", {
              className: `p-4 ${en ? "bg-red-50/60" : "bg-[#EAF0FF]"}`,
              children: [(0, r.jsx)("p", {
                className: "text-[14px] font-semibold leading-relaxed",
                children: function (e, {
                  isDelivery: r,
                  responseText: t,
                  pickupNumber: a,
                  scheduledFor: n
                }) {
                  return "PENDING" === e ? t || "Your order has reached the selected Zeta kitchen." : "ACCEPTED" === e ? n ? `The kitchen will prepare your order for ${n}.` : t || "The kitchen is preparing your Zeta order now." : "READY" === e ? r ? "Your order is on its way. Please be ready to receive it." : a ? `Your order is ready. Show pickup number ${a} at the counter.` : "Your order is hot and ready at the counter." : "COMPLETED" === e ? r ? "Delivery complete. Enjoy your meal." : "Order collected. Enjoy your meal." : "REJECTED" === e ? t || "This order was cancelled. Contact the store if you need help." : "Connecting to the kitchen for the latest update.";
                }(er, {
                  isDelivery: el,
                  responseText: $?.response_text,
                  pickupNumber: em,
                  scheduledFor: eu
                })
              }), (0, r.jsxs)("div", {
                className: "mt-4 flex items-center gap-3",
                children: [(0, r.jsx)("div", {
                  className: "h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/80",
                  children: (0, r.jsx)("div", {
                    className: `h-full rounded-full transition-[width] duration-500 ${en ? "bg-red-500" : "bg-brand-primary"}`,
                    style: {
                      width: `${ea}%`
                    }
                  })
                }), (0, r.jsxs)("span", {
                  className: "text-[11px] font-bold tabular-nums",
                  children: [ea, "%"]
                })]
              })]
            }), (0, r.jsx)("ol", {
              className: "grid grid-cols-4 border-t border-brand-ink/8",
              children: w.map(({
                key: e,
                label: t,
                Icon: a
              }, n) => {
                let i = !en && n <= ei;
                return (0, r.jsxs)("li", {
                  className: "border-r border-brand-ink/8 px-1 py-3 text-center last:border-r-0",
                  children: [(0, r.jsx)("span", {
                    className: `mx-auto grid h-9 w-9 place-items-center rounded-[11px] ${i ? "bg-brand-primary text-white" : en && 0 === n ? "bg-red-100 text-red-600" : "bg-[#F5F5F7] text-brand-ink/28"}`,
                    children: (0, r.jsx)(a, {
                      className: "h-4 w-4",
                      strokeWidth: 2,
                      "aria-hidden": "true"
                    })
                  }), (0, r.jsx)("span", {
                    className: `mt-1.5 block text-[8px] font-semibold ${i ? "text-brand-ink" : "text-brand-ink/35"}`,
                    children: t
                  })]
                }, e);
              })
            }), Z && el && !["COMPLETED", "REJECTED"].includes(er) ? (0, r.jsx)("div", {
              className: "border-t border-brand-ink/8 p-3",
              children: (0, r.jsxs)("a", {
                href: Z,
                target: "_blank",
                rel: "noopener noreferrer",
                onClick: () => (0, v.haptic)("selection"),
                className: "flex min-h-12 touch-manipulation items-center justify-center gap-2 rounded-[13px] bg-brand-ink px-4 text-[12px] font-semibold text-white",
                children: [(0, r.jsx)(u.MapPin, {
                  className: "h-4 w-4",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), "Open courier map", (0, r.jsx)(o.ExternalLink, {
                  className: "h-3.5 w-3.5",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })]
              })
            }) : null]
          }), L ? (0, r.jsxs)("section", {
            className: "rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
            children: [(0, r.jsx)("h2", {
              className: "text-[15px] font-bold",
              children: "Order details"
            }), (0, r.jsxs)("dl", {
              className: "mt-3 grid grid-cols-2 gap-x-4 gap-y-4",
              children: [(0, r.jsx)(P, {
                label: "Store",
                value: ex
              }), (0, r.jsx)(P, {
                label: "Placed",
                value: T(eb, ec)
              }), (0, r.jsx)(P, {
                label: "Service",
                value: el ? "Delivery" : "Pickup"
              }), (0, r.jsx)(P, {
                label: "Timing",
                value: eu || ("ASAP" === eo ? "ASAP" : eo)
              }), (0, r.jsx)(P, {
                label: "Payment",
                value: (0, j.getPaymentMethodLabel)(L)
              }), (0, r.jsx)(P, {
                label: "Total",
                value: `$${ef.toFixed(2)}`,
                strong: !0
              }), (0, r.jsx)(P, {
                label: "Address",
                value: L?.customerAddress || ep
              }), (0, r.jsx)(P, {
                label: "Customer",
                value: [L?.customerName, L?.customerPhone].filter(Boolean).join(" · ")
              })]
            }), L?.instructions ? (0, r.jsxs)("div", {
              className: "mt-4 border-t border-brand-ink/8 pt-3",
              children: [(0, r.jsx)("p", {
                className: "text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-ink/42",
                children: "Special instructions"
              }), (0, r.jsx)("p", {
                className: "mt-1 text-[12px] leading-relaxed text-brand-ink-light",
                children: L.instructions
              })]
            }) : null]
          }) : null, (0, r.jsxs)("div", {
            className: "grid grid-cols-2 gap-2",
            children: [(0, r.jsx)(a.default, {
              href: "/orders",
              onClick: () => (0, v.haptic)("selection"),
              className: "flex min-h-12 touch-manipulation items-center justify-center rounded-[13px] bg-[#F5F5F7] px-3 text-[12px] font-semibold",
              children: "All orders"
            }), (0, r.jsxs)(a.default, {
              href: "/menu",
              onClick: () => (0, v.haptic)("selection"),
              className: "flex min-h-12 touch-manipulation items-center justify-center gap-2 rounded-[13px] bg-brand-primary px-3 text-[12px] font-semibold text-white",
              children: [(0, r.jsx)(p.ShoppingBag, {
                className: "h-4 w-4",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), "Order again"]
            })]
          })]
        }), (0, r.jsxs)("div", {
          className: "space-y-4",
          children: [(0, r.jsxs)("section", {
            className: "overflow-hidden rounded-[18px] border border-brand-ink/8 bg-white",
            children: [(0, r.jsxs)("header", {
              className: "flex items-start justify-between gap-3 border-b border-brand-ink/8 px-4 py-3.5",
              children: [(0, r.jsxs)("div", {
                className: "min-w-0",
                children: [(0, r.jsx)("p", {
                  className: "text-[9px] font-semibold uppercase tracking-[0.1em] text-brand-primary",
                  children: "Receipt"
                }), (0, r.jsx)("h2", {
                  className: "mt-0.5 truncate text-[16px] font-bold",
                  children: ex
                })]
              }), (0, r.jsxs)("span", {
                className: "shrink-0 text-[11px] font-semibold text-brand-ink-light",
                children: [es.length, " ", 1 === es.length ? "line" : "lines"]
              })]
            }), ew.length ? (0, r.jsx)("div", {
              className: "flex flex-wrap gap-1.5 border-b border-brand-ink/8 bg-[#F5F5F7] px-4 py-2.5",
              children: ew.map(e => (0, r.jsxs)("span", {
                className: `inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-semibold ${e.paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`,
                children: [e.paid ? (0, r.jsx)(s.Check, {
                  className: "h-3 w-3",
                  strokeWidth: 2.4,
                  "aria-hidden": "true"
                }) : null, e.label, ": ", e.value]
              }, e.key))
            }) : null, (0, r.jsx)("div", {
              className: "divide-y divide-brand-ink/8",
              children: es.length ? es.map((e, t) => {
                let a = Math.max(1, Math.round(S(e.product_count || e.count || 1))),
                  n = S(e.temp_rate ?? e.price),
                  i = e.productName || e.productname || e.label || e.name || "Zeta item",
                  s = "string" == typeof e.size ? e.size : "",
                  l = e.modifiersDetail || e.modifiers || e.modifier || [],
                  d = Array.isArray(l) ? l : [],
                  c = d.reduce((e, r) => e + S(r.price) * Math.max(1, S(r.count || r.modifier_count || 1)), 0),
                  o = Number(e.lineTotal),
                  u = Number.isFinite(o) ? o : n * a + c;
                return (0, r.jsxs)("article", {
                  className: "flex gap-3 px-4 py-3.5",
                  children: [(0, r.jsxs)("span", {
                    className: "grid h-8 min-w-8 shrink-0 place-items-center rounded-[10px] bg-[#EAF0FF] px-1 text-[10px] font-bold text-brand-primary",
                    children: [a, "×"]
                  }), (0, r.jsxs)("div", {
                    className: "min-w-0 flex-1",
                    children: [(0, r.jsxs)("h3", {
                      className: "text-[13px] font-semibold leading-snug",
                      children: [i, s ? (0, r.jsxs)("span", {
                        className: "font-medium text-brand-ink-light",
                        children: [" · ", s]
                      }) : null]
                    }), d.length ? (0, r.jsx)("p", {
                      className: "mt-1 line-clamp-2 text-[10px] leading-relaxed text-brand-ink-light",
                      children: d.map(e => e.addOnName || e.modifier || e.modifier_name || e.modifierName).filter(Boolean).join(", ")
                    }) : null]
                  }), (0, r.jsxs)("span", {
                    className: "shrink-0 text-[12px] font-bold tabular-nums",
                    children: ["$", u.toFixed(2)]
                  })]
                }, `${e.productItemId || e.id || i}-${t}`);
              }) : (0, r.jsxs)("div", {
                className: "px-4 py-8 text-center",
                children: [(0, r.jsx)(c.Clock3, {
                  className: "mx-auto h-5 w-5 text-brand-primary",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), (0, r.jsx)("p", {
                  className: "mt-2 text-[11px] text-brand-ink-light",
                  children: "Receipt details are still loading."
                })]
              })
            }), L ? (0, r.jsxs)("div", {
              className: "space-y-2 border-t border-brand-ink/8 bg-[#F5F5F7] px-4 py-3.5 text-[11px]",
              children: [eh > 0 && eh !== ef ? (0, r.jsxs)("div", {
                className: "flex justify-between gap-3 text-brand-ink-light",
                children: [(0, r.jsx)("span", {
                  children: "Subtotal"
                }), (0, r.jsxs)("span", {
                  className: "font-semibold",
                  children: ["$", eh.toFixed(2)]
                })]
              }) : null, ek > 0 ? (0, r.jsxs)("div", {
                className: "flex justify-between gap-3 text-brand-ink-light",
                children: [(0, r.jsx)("span", {
                  children: L?.surchargeLabel || L?.surcharge_label || "Surcharge"
                }), (0, r.jsxs)("span", {
                  className: "font-semibold",
                  children: ["$", ek.toFixed(2)]
                })]
              }) : null, ej > 0 ? (0, r.jsxs)("div", {
                className: "flex justify-between gap-3 text-brand-ink-light",
                children: [(0, r.jsx)("span", {
                  children: "Service fee"
                }), (0, r.jsxs)("span", {
                  className: "font-semibold",
                  children: ["$", ej.toFixed(2)]
                })]
              }) : null, eN > 0 ? (0, r.jsxs)("div", {
                className: "flex justify-between gap-3 text-brand-ink-light",
                children: [(0, r.jsx)("span", {
                  children: "Gratuity"
                }), (0, r.jsxs)("span", {
                  className: "font-semibold",
                  children: ["$", eN.toFixed(2)]
                })]
              }) : null, ey > 0 && !ev ? (0, r.jsxs)("div", {
                className: "flex justify-between gap-3 font-semibold text-emerald-700",
                children: [(0, r.jsx)("span", {
                  children: "Discount"
                }), (0, r.jsxs)("span", {
                  children: ["−$", ey.toFixed(2)]
                })]
              }) : null, ev ? (0, r.jsxs)("div", {
                className: "flex items-start justify-between gap-3 font-semibold text-emerald-700",
                children: [(0, r.jsxs)("span", {
                  className: "min-w-0",
                  children: ["Coupon", ev.code ? ` \xb7 ${ev.code}` : "", ev.name ? ` — ${ev.name}` : ""]
                }), (0, r.jsx)("span", {
                  className: "shrink-0",
                  children: S(ev.amount) > 0 ? `−$${S(ev.amount).toFixed(2)}` : ev.isFreeItem ? "Free item" : "Applied"
                })]
              }) : null, eg > 0 ? (0, r.jsxs)("div", {
                className: "flex justify-between gap-3 font-semibold text-violet-700",
                children: [(0, r.jsx)("span", {
                  children: "Paid with wallet"
                }), (0, r.jsxs)("span", {
                  children: ["−$", eg.toFixed(2)]
                })]
              }) : null, (0, r.jsxs)("div", {
                className: "flex items-end justify-between gap-3 border-t border-brand-ink/8 pt-3",
                children: [(0, r.jsx)("span", {
                  className: "text-[13px] font-bold",
                  children: eg > 0 && ef > 0 ? "Balance due" : "Total"
                }), (0, r.jsxs)("span", {
                  className: "text-[20px] font-extrabold tabular-nums",
                  children: ["$", ef.toFixed(2)]
                })]
              })]
            }) : null, L ? (0, r.jsxs)("div", {
              className: "grid grid-cols-2 gap-2 border-t border-brand-ink/8 p-3",
              children: [(0, r.jsx)(a.default, {
                href: "/orders",
                onClick: () => (0, v.haptic)("selection"),
                className: "flex min-h-12 touch-manipulation items-center justify-center rounded-[13px] bg-[#F5F5F7] px-3 text-[12px] font-semibold",
                children: "All orders"
              }), eC && null == K ? (0, r.jsxs)("button", {
                type: "button",
                onClick: () => {
                  (0, v.haptic)("selection"), X(e => !e);
                },
                className: "flex min-h-12 touch-manipulation items-center justify-center gap-1.5 rounded-[13px] bg-amber-50 px-3 text-[12px] font-semibold text-amber-700",
                children: [(0, r.jsx)(b.Star, {
                  className: "h-4 w-4",
                  strokeWidth: 1.9,
                  "aria-hidden": "true"
                }), "Rate order"]
              }) : (0, r.jsxs)("span", {
                className: "flex min-h-12 items-center justify-center gap-1.5 rounded-[13px] bg-[#F5F5F7] px-3 text-[12px] font-semibold text-brand-ink-light",
                children: [(0, r.jsx)(b.Star, {
                  className: `h-4 w-4 ${null != K ? "fill-amber-400 text-amber-400" : ""}`,
                  strokeWidth: 1.9,
                  "aria-hidden": "true"
                }), null != K ? `Rated ${K}/5` : "Rating available later"]
              })]
            }) : null]
          }), q && L && null == K ? (0, r.jsxs)("section", {
            className: "rounded-[18px] border border-brand-ink/8 bg-white p-4",
            children: [(0, r.jsx)("h2", {
              className: "text-[15px] font-bold",
              children: "How was your order?"
            }), (0, r.jsx)("p", {
              className: "mt-1 text-[11px] text-brand-ink-light",
              children: "Your feedback goes to the selected Zeta store."
            }), (0, r.jsx)("div", {
              className: "mt-4 [&_button]:min-h-11 [&_button]:touch-manipulation [&_textarea]:min-h-24 [&_textarea]:text-base",
              children: (0, r.jsx)(h.RatingForm, {
                orderId: I,
                firebaseUid: d?.uid,
                storePosOwnerId: eE,
                items: L?.lineItems || L?.orderData || L?.items || [],
                onSuccess: (e, r) => {
                  W(e), J(r), X(!1);
                }
              })
            })]
          }) : null, null != K ? (0, r.jsxs)("section", {
            className: "rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
            children: [(0, r.jsxs)("div", {
              className: "flex items-center gap-2 text-amber-500",
              children: [(0, r.jsx)(b.Star, {
                className: "h-5 w-5 fill-current",
                strokeWidth: 1.8,
                "aria-hidden": "true"
              }), (0, r.jsxs)("h2", {
                className: "text-[14px] font-bold text-brand-ink",
                children: ["Your rating · ", K, "/5"]
              })]
            }), V ? (0, r.jsxs)("p", {
              className: "mt-2 text-[11px] leading-relaxed text-brand-ink-light",
              children: ["“", V, "”"]
            }) : null]
          }) : null]
        })]
      })]
    });
  }
  e.s(["default", 0, function () {
    return (0, r.jsx)(t.Suspense, {
      fallback: (0, r.jsx)(D, {}),
      children: (0, r.jsx)(O, {})
    });
  }]);
});

  Z.define(508619, e => {
  "use strict";

  let t = (0, e.i(456420).default)("circle-user-round", [["path", {
    d: "M17.925 20.056a6 6 0 0 0-11.851.001",
    key: "z69sun"
  }], ["circle", {
    cx: "12",
    cy: "11",
    r: "4",
    key: "1gt34v"
  }], ["circle", {
    cx: "12",
    cy: "12",
    r: "10",
    key: "1mglay"
  }]]);
  e.s(["CircleUserRound", 0, t], 508619);
});

  Z.define(508734, e => {
  "use strict";

  let a = (0, e.i(456420).default)("copy", [["rect", {
    width: "14",
    height: "14",
    x: "8",
    y: "8",
    rx: "2",
    ry: "2",
    key: "17jyea"
  }], ["path", {
    d: "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2",
    key: "zix9uf"
  }]]);
  e.s(["Copy", 0, a], 508734);
});

  Z.define(509182, e => {
  "use strict";

  function r(e) {
    return "wallet" === String(e || "").trim().toLowerCase();
  }
  e.s(["getPaymentMethodLabel", 0, function (e) {
    let t = e?.paymentType || e?.payment_type || "",
      a = parseFloat(String(e?.walletAmount || 0)) || 0,
      n = parseFloat(String(e?.cashDue ?? e?.total ?? 0)) || 0;
    return r(t) ? a > 0 ? `Wallet $${a.toFixed(2)}` : "Wallet" : t ? a > 0 && n > 0 ? `Wallet $${a.toFixed(2)} + ${t} $${n.toFixed(2)}` : t : a > 0 ? `Wallet $${a.toFixed(2)}` : null;
  }, "getPaymentPills", 0, function (e) {
    var t;
    let a,
      n = String(e?.onlinePaymentStatus || "").toUpperCase(),
      i = String(e?.ticketStatus || "").toUpperCase(),
      s = e?.paymentType || e?.payment_type || "",
      l = parseFloat(String(e?.cashDue ?? e?.total ?? 0)) || 0,
      o = [];
    return r(s) ? l <= 0 ? o.push({
      key: "wallet",
      label: "Wallet",
      value: "PAID",
      paid: !0
    }) : i && o.push({
      key: "store",
      label: "In-Store",
      value: i,
      paid: "PAID" === i
    }) : (a = String((t = s) || "").toLowerCase()).includes("counter") || a.includes("store") || r(t) ? i && o.push({
      key: "store",
      label: "In-Store",
      value: i,
      paid: "PAID" === i
    }) : (n && o.push({
      key: "online",
      label: "Online",
      value: n,
      paid: "PAID" === n
    }), i && i !== n && o.push({
      key: "store",
      label: "In-Store",
      value: i,
      paid: "PAID" === i
    })), o;
  }]);
});

  Z.define(515281, e => {
  "use strict";

  let t = (0, e.i(456420).default)("pencil", [["path", {
    d: "M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z",
    key: "1a8usu"
  }], ["path", {
    d: "m15 5 4 4",
    key: "1mk7zo"
  }]]);
  e.s(["Pencil", 0, t], 515281);
});

  Z.define(516306, e => {
  "use strict";

  let t = (0, e.i(456420).default)("shield-alert", [["path", {
    d: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z",
    key: "oel41y"
  }], ["path", {
    d: "M12 8v4",
    key: "1got3b"
  }], ["path", {
    d: "M12 16h.01",
    key: "1drbdi"
  }]]);
  e.s(["ShieldAlert", 0, t], 516306);
});

  Z.define(551757, e => {
  "use strict";

  var t = e.i(616933);
  e.s(["CheckCircle2", () => t.default]);
});

  Z.define(555125, e => {
  "use strict";

  var a = e.i(843476),
    r = e.i(522016),
    s = e.i(447061),
    t = e.i(716327),
    n = e.i(226091),
    l = e.i(576248),
    i = e.i(96315),
    o = e.i(248510),
    d = e.i(256102),
    c = e.i(584026),
    u = e.i(956094),
    h = e.i(799552),
    p = e.i(273341);
  let m = "zetagreekkitchen@gmail.com",
    x = [{
      q: "How do I track my order?",
      a: "Open Orders from the bottom navigation, select the receipt, then choose Track. Live status appears when the store supplies it."
    }, {
      q: "Can I modify or cancel my order?",
      a: "An order can only be changed before the store accepts it. Contact the selected store as soon as possible."
    }, {
      q: "What payment methods are accepted?",
      a: "The payment methods currently available for your store and order are shown securely at checkout."
    }, {
      q: "Is Zeta halal?",
      a: "Zeta states that its food is fully halal. Ask the selected store if you need current preparation or supplier information."
    }, {
      q: "How do I report an order problem?",
      a: `Email ${m} with your order number and a clear description, or contact the selected store for an active order.`
    }, {
      q: "Does every store deliver?",
      a: "Delivery availability is store-specific. The order strip and checkout show only the fulfilment options enabled by the selected store."
    }, {
      q: "How do I create an account?",
      a: "Tap Account in the bottom navigation, then continue with an available sign-in or registration method."
    }, {
      q: "Where is allergen information?",
      a: "Open Allergen information below and contact the selected store before ordering if you have a food allergy or dietary requirement."
    }];
  e.s(["default", 0, function () {
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsx)(h.MobilePageHeader, {
        icon: l.HelpCircle,
        kicker: "Support",
        title: "Help",
        description: "Quick answers, order support and useful Zeta information."
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-5 px-4 sm:px-6",
        children: [(0, a.jsx)("section", {
          className: "grid grid-cols-2 gap-2 md:grid-cols-4",
          children: [{
            href: "/orders",
            Icon: d.ReceiptText,
            label: "My orders"
          }, {
            href: "/menu",
            Icon: u.Utensils,
            label: "Browse menu"
          }, {
            href: "/stores",
            Icon: o.MapPinned,
            label: "Store help"
          }, {
            href: "/allergen",
            Icon: s.BadgeInfo,
            label: "Allergens"
          }].map(({
            href: e,
            Icon: s,
            label: t
          }) => (0, a.jsxs)(r.default, {
            href: e,
            onClick: () => (0, p.haptic)("selection"),
            className: "flex min-h-[58px] touch-manipulation items-center gap-2.5 rounded-[15px] bg-[#F5F5F7] px-3 text-[13px] font-semibold",
            children: [(0, a.jsx)("span", {
              className: "grid h-9 w-9 place-items-center rounded-[11px] bg-white text-brand-primary",
              children: (0, a.jsx)(s, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), t]
          }, e))
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[16px] font-bold",
            children: "Frequently asked questions"
          }), (0, a.jsx)("div", {
            className: "space-y-2",
            children: x.map(({
              q: e,
              a: r
            }) => (0, a.jsxs)("details", {
              className: "group overflow-hidden rounded-[15px] border border-brand-ink/8 bg-white",
              children: [(0, a.jsxs)("summary", {
                className: "flex min-h-[52px] cursor-pointer touch-manipulation list-none items-center justify-between gap-3 px-3.5 py-3 text-[13px] font-semibold",
                children: [e, (0, a.jsx)(t.ChevronDown, {
                  className: "h-4 w-4 shrink-0 text-brand-ink-light transition-transform group-open:rotate-180",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })]
              }), (0, a.jsx)("p", {
                className: "border-t border-brand-ink/8 px-3.5 py-3 text-[11px] leading-relaxed text-brand-ink-light",
                children: r
              })]
            }, e))
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Useful information"
          }), (0, a.jsxs)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8",
            children: [(0, a.jsx)(h.MobileLinkRow, {
              href: "/allergen",
              icon: s.BadgeInfo,
              label: "Allergen information"
            }), (0, a.jsx)(h.MobileLinkRow, {
              href: "/terms",
              icon: n.FileText,
              label: "Terms and conditions"
            }), (0, a.jsx)(h.MobileLinkRow, {
              href: "/privacy",
              icon: c.ShieldCheck,
              label: "Privacy policy"
            })]
          })]
        }), (0, a.jsxs)("section", {
          className: "rounded-[16px] bg-[#EAF0FF] p-4 text-center",
          children: [(0, a.jsx)(i.Mail, {
            className: "mx-auto h-6 w-6 text-brand-primary",
            strokeWidth: 1.9,
            "aria-hidden": "true"
          }), (0, a.jsx)("h2", {
            className: "mt-2 text-[15px] font-bold",
            children: "Still need help?"
          }), (0, a.jsx)("p", {
            className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
            children: "Email the Zeta team with your order number and the store involved."
          }), (0, a.jsx)("a", {
            href: `mailto:${m}`,
            className: "mt-3 inline-flex min-h-11 touch-manipulation items-center justify-center rounded-[13px] bg-brand-primary px-5 text-[13px] font-semibold text-white",
            children: "Email Zeta"
          })]
        })]
      })]
    });
  }]);
});

  Z.define(566595, e => {
  "use strict";

  let t = (0, e.i(456420).default)("search", [["path", {
    d: "m21 21-4.34-4.34",
    key: "14j7rj"
  }], ["circle", {
    cx: "11",
    cy: "11",
    r: "8",
    key: "4ej97u"
  }]]);
  e.s(["Search", 0, t], 566595);
});

  Z.define(573474, e => {
  "use strict";

  let t = (0, e.i(456420).default)("trash-2", [["path", {
    d: "M10 11v6",
    key: "nco0om"
  }], ["path", {
    d: "M14 11v6",
    key: "outv1u"
  }], ["path", {
    d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
    key: "miytrc"
  }], ["path", {
    d: "M3 6h18",
    key: "d0wm0j"
  }], ["path", {
    d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
    key: "e791ji"
  }]]);
  e.s(["Trash2", 0, t], 573474);
});

  Z.define(575387, e => {
  "use strict";

  let t = (0, e.i(456420).default)("phone", [["path", {
    d: "M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384",
    key: "9njp5v"
  }]]);
  e.s(["Phone", 0, t], 575387);
});

  Z.define(576248, e => {
  "use strict";

  var t = e.i(275043);
  e.s(["HelpCircle", () => t.default]);
});

  Z.define(578344, e => {
  "use strict";

  let t = (0, e.i(456420).default)("navigation", [["polygon", {
    points: "3 11 22 2 13 21 11 13 3 11",
    key: "1ltx0t"
  }]]);
  e.s(["Navigation", 0, t], 578344);
});

  Z.define(578727, e => {
  "use strict";

  let t = (0, e.i(456420).default)("bike", [["circle", {
    cx: "18.5",
    cy: "17.5",
    r: "3.5",
    key: "15x4ox"
  }], ["circle", {
    cx: "5.5",
    cy: "17.5",
    r: "3.5",
    key: "1noe27"
  }], ["circle", {
    cx: "15",
    cy: "5",
    r: "1",
    key: "19l28e"
  }], ["path", {
    d: "M12 17.5V14l-3-3 4-3 2 3h2",
    key: "1npguv"
  }]]);
  e.s(["Bike", 0, t], 578727);
});

  Z.define(584026, e => {
  "use strict";

  let t = (0, e.i(456420).default)("shield-check", [["path", {
    d: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z",
    key: "oel41y"
  }], ["path", {
    d: "m9 12 2 2 4-4",
    key: "dzmm74"
  }]]);
  e.s(["ShieldCheck", 0, t], 584026);
});

  Z.define(593583, e => {
  "use strict";

  let t = (0, e.i(456420).default)("menu", [["path", {
    d: "M4 5h16",
    key: "1tepv9"
  }], ["path", {
    d: "M4 12h16",
    key: "1lakjw"
  }], ["path", {
    d: "M4 19h16",
    key: "1djgab"
  }]]);
  e.s(["Menu", 0, t], 593583);
});

  Z.define(598457, e => {
  "use strict";

  let t = (0, e.i(456420).default)("vibrate", [["path", {
    d: "m2 8 2 2-2 2 2 2-2 2",
    key: "sv1b1"
  }], ["path", {
    d: "m22 8-2 2 2 2-2 2 2 2",
    key: "101i4y"
  }], ["rect", {
    width: "8",
    height: "14",
    x: "8",
    y: "5",
    rx: "1",
    key: "1oyrl4"
  }]]);
  e.s(["Vibrate", 0, t], 598457);
});

  Z.define(604388, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(657688),
    r = e.i(522016),
    n = e.i(768877),
    i = e.i(106501),
    s = e.i(96315),
    l = e.i(274588),
    o = e.i(988771),
    d = e.i(799552);
  e.s(["default", 0, function () {
    return (0, t.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, t.jsx)(d.MobilePageHeader, {
        icon: l.PartyPopper,
        kicker: "Events and groups",
        title: "Zeta Catering",
        description: "A dedicated catering experience is on the way."
      }), (0, t.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-4 px-4 sm:px-6",
        children: [(0, t.jsxs)("section", {
          className: "relative aspect-[16/10] overflow-hidden rounded-[20px] border border-brand-ink/8 bg-[#F5F5F7] sm:aspect-[2/1]",
          children: [(0, t.jsx)(a.default, {
            src: "/brands/zeta/catering-platter.webp",
            alt: "Zeta Greek catering platter",
            fill: !0,
            priority: !0,
            sizes: "(max-width: 520px) calc(100vw - 32px), 488px",
            className: "object-cover"
          }), (0, t.jsxs)("div", {
            className: "absolute inset-x-0 bottom-0 bg-white p-3",
            children: [(0, t.jsx)("span", {
              className: "inline-flex rounded-full bg-[#EAF0FF] px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-brand-primary",
              children: "Coming soon"
            }), (0, t.jsx)("h2", {
              className: "mt-1.5 text-[18px] font-extrabold tracking-[-0.02em]",
              children: "Catering requests, built properly."
            })]
          })]
        }), (0, t.jsx)("section", {
          className: "rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
          children: (0, t.jsxs)("div", {
            className: "flex gap-3",
            children: [(0, t.jsx)("span", {
              className: "grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-white text-brand-primary",
              children: (0, t.jsx)(i.CalendarClock, {
                className: "h-5 w-5",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            }), (0, t.jsxs)("div", {
              children: [(0, t.jsx)("h2", {
                className: "text-[14px] font-bold",
                children: "We're preparing the booking flow"
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                children: "Catering is different from reserving a table. This page will become a dedicated enquiry experience for event menus, dates, group sizes and pickup details."
              })]
            })]
          })
        }), (0, t.jsxs)("section", {
          className: "grid grid-cols-2 gap-2",
          "aria-label": "Catering occasions",
          children: [(0, t.jsxs)("div", {
            className: "rounded-[16px] border border-brand-primary/14 bg-white p-3",
            children: [(0, t.jsx)(o.UsersRound, {
              className: "h-5 w-5 text-brand-primary",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            }), (0, t.jsx)("h3", {
              className: "mt-3 text-[13px] font-bold",
              children: "Work and school"
            }), (0, t.jsx)("p", {
              className: "mt-1 text-[10px] leading-relaxed text-brand-ink-light",
              children: "Office lunches, school events and corporate functions."
            })]
          }), (0, t.jsxs)("div", {
            className: "rounded-[16px] border border-brand-primary/14 bg-white p-3",
            children: [(0, t.jsx)(l.PartyPopper, {
              className: "h-5 w-5 text-brand-primary",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            }), (0, t.jsx)("h3", {
              className: "mt-3 text-[13px] font-bold",
              children: "Celebrations"
            }), (0, t.jsx)("p", {
              className: "mt-1 text-[10px] leading-relaxed text-brand-ink-light",
              children: "Family gatherings, parties and community events."
            })]
          })]
        }), (0, t.jsxs)("section", {
          className: "rounded-[18px] bg-[#EAF0FF] p-4",
          children: [(0, t.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.1em] text-brand-primary",
            children: "Need to plan now?"
          }), (0, t.jsx)("h2", {
            className: "mt-1 text-[16px] font-bold",
            children: "Talk to the Zeta team directly."
          }), (0, t.jsx)("p", {
            className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
            children: "Send the date, location, estimated guest count and the kind of event you're planning."
          }), (0, t.jsxs)("div", {
            className: "mt-3 grid grid-cols-2 gap-2",
            children: [(0, t.jsxs)("a", {
              href: "mailto:zetagreekkitchen@gmail.com?subject=Zeta%20catering%20enquiry",
              className: "flex min-h-11 touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-3 text-[12px] font-bold text-white",
              children: [(0, t.jsx)(s.Mail, {
                className: "h-4 w-4",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), "Email Zeta"]
            }), (0, t.jsxs)(r.default, {
              href: "/contact",
              className: "flex min-h-11 touch-manipulation items-center justify-center gap-1.5 rounded-[14px] bg-white px-3 text-[12px] font-bold text-brand-ink",
              children: ["Contact options", (0, t.jsx)(n.ArrowRight, {
                className: "h-4 w-4 text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              })]
            })]
          })]
        })]
      })]
    });
  }]);
});

  Z.define(611568, e => {
  "use strict";

  var t = e.i(758379);
  e.s(["LoaderCircle", () => t.default]);
});

  Z.define(616933, e => {
  "use strict";

  let t = (0, e.i(456420).default)("circle-check", [["circle", {
    cx: "12",
    cy: "12",
    r: "10",
    key: "1mglay"
  }], ["path", {
    d: "m9 12 2 2 4-4",
    key: "dzmm74"
  }]]);
  e.s(["default", 0, t]);
});

  Z.define(621999, e => {
  "use strict";

  let a = (0, e.i(456420).default)("message-circle-more", [["path", {
    d: "M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719",
    key: "1sd12s"
  }], ["path", {
    d: "M8 12h.01",
    key: "czm47f"
  }], ["path", {
    d: "M12 12h.01",
    key: "1mp3jc"
  }], ["path", {
    d: "M16 12h.01",
    key: "1l6xoz"
  }]]);
  e.s(["MessageCircleMore", 0, a], 621999);
});

  Z.define(626102, e => {
  "use strict";

  let t = (0, e.i(456420).default)("leaf", [["path", {
    d: "M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z",
    key: "nnexq3"
  }], ["path", {
    d: "M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12",
    key: "mt58a7"
  }]]);
  e.s(["Leaf", 0, t], 626102);
});

  Z.define(632496, e => {
  "use strict";

  var r = e.i(843476),
    t = e.i(271645),
    a = e.i(522016),
    n = e.i(618566),
    s = e.i(768877),
    i = e.i(551757),
    l = e.i(298007),
    d = e.i(96315),
    o = e.i(475577),
    c = e.i(256102),
    u = e.i(566595),
    m = e.i(263448),
    p = e.i(273341);
  let h = [{
    label: "Received",
    detail: "Your order reaches the selected Zeta kitchen.",
    Icon: c.ReceiptText
  }, {
    label: "Preparing",
    detail: "The kitchen accepts and starts your order.",
    Icon: l.ChefHat
  }, {
    label: "Ready",
    detail: "We let you know when it is ready to collect.",
    Icon: o.PackageCheck
  }, {
    label: "Complete",
    detail: "The order is collected or delivered.",
    Icon: i.CheckCircle2
  }];
  e.s(["default", 0, function () {
    let e = (0, n.useRouter)(),
      [i, l] = (0, t.useState)(""),
      [o, x] = (0, t.useState)(!1);
    return (0, r.jsxs)("main", {
      className: "mx-auto min-h-[70vh] w-full max-w-5xl bg-white px-4 pb-8 pt-5 text-brand-ink sm:px-6 sm:pt-6 lg:px-8",
      children: [(0, r.jsxs)("header", {
        children: [(0, r.jsxs)("div", {
          className: "flex items-center gap-2 text-brand-primary",
          children: [(0, r.jsx)(u.Search, {
            className: "h-4 w-4",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), (0, r.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.12em]",
            children: "Live order lookup"
          })]
        }), (0, r.jsx)("h1", {
          className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.03em] sm:text-[31px]",
          children: "Track an order"
        }), (0, r.jsx)("p", {
          className: "mt-2 max-w-xl text-[12px] leading-relaxed text-brand-ink-light sm:text-[13px]",
          children: "Enter the order ID from your confirmation email or text message."
        })]
      }), (0, r.jsxs)("div", {
        className: "mt-5 grid items-start gap-5 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]",
        children: [(0, r.jsxs)("section", {
          className: "rounded-[18px] border border-brand-primary/16 bg-[#EAF0FF] p-4 sm:p-5",
          children: [(0, r.jsx)("span", {
            className: "grid h-11 w-11 place-items-center rounded-[13px] bg-white text-brand-primary",
            children: (0, r.jsx)(c.ReceiptText, {
              className: "h-5 w-5",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          }), (0, r.jsx)("h2", {
            className: "mt-3 text-[18px] font-bold",
            children: "Find your live order"
          }), (0, r.jsx)("p", {
            className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
            children: "Order IDs usually begin with OOT. Spaces and letter case are handled automatically."
          }), (0, r.jsxs)("form", {
            onSubmit: function (r) {
              r.preventDefault();
              let t = i.trim();
              t && !o && ((0, p.haptic)("medium"), x(!0), e.push(`/track?id=${encodeURIComponent(t)}`));
            },
            className: "mt-4",
            children: [(0, r.jsx)("label", {
              htmlFor: "zeta-track-order-id",
              className: "text-[10px] font-semibold text-brand-ink-light",
              children: "Order ID"
            }), (0, r.jsx)("div", {
              className: "mt-1.5 flex min-h-[52px] items-center rounded-[14px] border border-brand-ink/10 bg-white px-3 focus-within:border-brand-primary/35 focus-within:ring-2 focus-within:ring-brand-primary/15",
              children: (0, r.jsx)("input", {
                id: "zeta-track-order-id",
                type: "text",
                value: i,
                onChange: e => l(e.target.value.toUpperCase()),
                placeholder: "e.g. OOT123456789",
                autoComplete: "off",
                autoCapitalize: "characters",
                spellCheck: !1,
                enterKeyHint: "go",
                className: "min-w-0 flex-1 bg-transparent font-mono text-base font-semibold uppercase text-brand-ink outline-none placeholder:font-sans placeholder:font-medium placeholder:normal-case placeholder:text-brand-ink/28"
              })
            }), (0, r.jsxs)("button", {
              type: "submit",
              disabled: o || !i.trim(),
              className: "mt-3 flex min-h-[52px] w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white disabled:bg-brand-ink/12",
              children: [o ? "Opening tracker…" : "Track order", o ? null : (0, r.jsx)(s.ArrowRight, {
                className: "h-4 w-4",
                strokeWidth: 2.2,
                "aria-hidden": "true"
              })]
            })]
          }), (0, r.jsxs)("a", {
            href: "mailto:info@zetagreekkitchen.co.nz",
            className: "mt-3 flex min-h-11 touch-manipulation items-center justify-center gap-2 rounded-[12px] text-[12px] font-semibold text-brand-primary",
            children: [(0, r.jsx)(d.Mail, {
              className: "h-4 w-4",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), "Need help finding your ID?"]
          })]
        }), (0, r.jsxs)("section", {
          "aria-labelledby": "tracking-steps-title",
          children: [(0, r.jsxs)("div", {
            className: "flex items-center justify-between gap-3",
            children: [(0, r.jsx)("h2", {
              id: "tracking-steps-title",
              className: "text-[16px] font-bold",
              children: "What you will see"
            }), (0, r.jsx)("span", {
              className: "text-[10px] font-medium text-brand-ink-light",
              children: "Updates automatically"
            })]
          }), (0, r.jsx)("ol", {
            className: "mt-2 grid gap-2 sm:grid-cols-2",
            children: h.map(({
              label: e,
              detail: t,
              Icon: a
            }, n) => (0, r.jsxs)("li", {
              className: "flex min-h-[92px] items-start gap-3 rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
              children: [(0, r.jsx)("span", {
                className: "grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-white text-brand-primary",
                children: (0, r.jsx)(a, {
                  className: "h-[19px] w-[19px]",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              }), (0, r.jsxs)("span", {
                className: "min-w-0",
                children: [(0, r.jsx)("span", {
                  className: "text-[9px] font-bold text-brand-primary",
                  children: String(n + 1).padStart(2, "0")
                }), (0, r.jsx)("span", {
                  className: "block text-[13px] font-bold",
                  children: e
                }), (0, r.jsx)("span", {
                  className: "mt-0.5 block text-[10px] leading-relaxed text-brand-ink-light",
                  children: t
                })]
              })]
            }, e))
          }), (0, r.jsxs)("div", {
            className: "mt-3 grid grid-cols-2 gap-2",
            children: [(0, r.jsxs)(a.default, {
              href: "/orders",
              onClick: () => (0, p.haptic)("selection"),
              className: "flex min-h-12 touch-manipulation items-center justify-center gap-2 rounded-[13px] bg-[#F5F5F7] px-3 text-[12px] font-semibold",
              children: [(0, r.jsx)(c.ReceiptText, {
                className: "h-4 w-4 text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), "My orders"]
            }), (0, r.jsxs)(a.default, {
              href: "/menu",
              onClick: () => (0, p.haptic)("selection"),
              className: "flex min-h-12 touch-manipulation items-center justify-center gap-2 rounded-[13px] bg-[#F5F5F7] px-3 text-[12px] font-semibold",
              children: [(0, r.jsx)(m.ShoppingBag, {
                className: "h-4 w-4 text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), "Browse menu"]
            })]
          })]
        })]
      })]
    });
  }]);
});

  Z.define(632781, e => {
  "use strict";

  var t = e.i(758379);
  e.s(["Loader2", () => t.default]);
});

  Z.define(668109, e => {
  "use strict";

  let t = (0, e.i(456420).default)("heart", [["path", {
    d: "M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5",
    key: "mvr1a0"
  }]]);
  e.s(["Heart", 0, t], 668109);
});

  Z.define(671753, e => {
  "use strict";

  let t = ["label", "name", "product_name", "productName", "title"],
    a = ["description", "desc", "product_description", "productDescription", "details", "ingredients"],
    i = ["tags", "tag", "keywords", "category_tags"],
    n = ["label", "name", "category_name", "categoryName", "title"],
    r = ["categoryId", "category_id", "id", "cat_id"],
    s = ["position", "categoryPosition", "category_position", "sort_order", "sortOrder"],
    l = ["position", "itemPosition", "item_position", "sort_order", "sortOrder"],
    o = ["combo_name", "comboName", "label", "name", "title"],
    c = ["combo_description", "comboDescription", "description", "desc", "details"],
    d = ["category_name", "categoryName", "combo_category_name", "comboCategoryName"],
    u = ["position", "combo_position", "comboPosition", "sort_order", "sortOrder"];
  function m(e, t) {
    return e ? t.map(t => function e(t) {
      return null == t ? "" : "string" == typeof t || "number" == typeof t || "boolean" == typeof t ? String(t) : Array.isArray(t) ? t.map(e).filter(Boolean).join(" ") : "object" == typeof t ? [t.label, t.name, t.title, t.value].map(e).filter(Boolean).join(" ") : "";
    }(e[t])).filter(Boolean).join(" ") : "";
  }
  function p(e) {
    return String(e ?? "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/&/g, " and ").replace(/[^0-9a-z]+/gi, " ").trim().toLowerCase();
  }
  function x(e, t, a) {
    for (let a of t) {
      let t = Number(e[a]);
      if (Number.isFinite(t)) return t;
    }
    return a;
  }
  function b(e, t) {
    return !!e && !!t && (!!e.includes(t) || e.split(/\s+/).some(e => e.startsWith(t)));
  }
  function h(e, t, a) {
    let i = e.map(({
        field: e,
        text: t,
        weights: a
      }) => ({
        field: e,
        text: p(t),
        rawText: t,
        weights: a
      })),
      n = i.map(({
        text: e
      }) => e).filter(Boolean).join(" ");
    if (!n || !a.every(e => b(n, e)) && !n.includes(t)) return null;
    let r = i.map(({
        field: e,
        rawText: i,
        weights: n
      }) => ({
        field: e,
        score: function (e, t, a, i) {
          let n = p(e);
          if (!n) return 0;
          let r = 0;
          n === t && (r += i.exact), n.startsWith(t) && (r += i.startsWith), n.includes(t) && (r += i.includes);
          let s = p(n).split(/\s+/).filter(Boolean);
          for (let e of a) s.some(t => t.startsWith(e)) ? r += i.wordStartsWith : b(n, e) && (r += i.token);
          return r;
        }(i, t, a, n)
      })).filter(({
        score: e
      }) => e > 0),
      s = r.reduce((e, t) => e + t.score, 0);
    return s <= 0 ? null : {
      score: s,
      matchFields: r.map(({
        field: e
      }) => e)
    };
  }
  e.s(["normalizeMenuSearchQuery", 0, p, "searchMenu", 0, function ({
    categories: e = [],
    combos: b = [],
    query: f,
    limit: g
  }) {
    let y = p(f),
      j = y.split(/\s+/).map(e => e.trim()).filter(Boolean);
    if (!y || 0 === j.length) return {
      query: "",
      itemResults: [],
      comboResults: [],
      items: [],
      combos: [],
      totalCount: 0
    };
    let v = [],
      N = [];
    for (let [o, c] of (e || []).entries()) {
      let e = m(c, n) || "Menu",
        d = m(c, r) || void 0,
        u = x(c, s, o);
      for (let [n, r] of (Array.isArray(c.items) ? c.items : Array.isArray(c.item) ? c.item : []).entries()) {
        let s = h([{
          field: "name",
          text: m(r, t),
          weights: {
            exact: 140,
            startsWith: 110,
            wordStartsWith: 34,
            includes: 70,
            token: 18
          }
        }, {
          field: "category",
          text: e,
          weights: {
            exact: 50,
            startsWith: 40,
            wordStartsWith: 18,
            includes: 28,
            token: 9
          }
        }, {
          field: "tags",
          text: m(r, i),
          weights: {
            exact: 44,
            startsWith: 34,
            wordStartsWith: 18,
            includes: 25,
            token: 9
          }
        }, {
          field: "description",
          text: m(r, a),
          weights: {
            exact: 28,
            startsWith: 22,
            wordStartsWith: 10,
            includes: 16,
            token: 6
          }
        }], y, j);
        if (!s) continue;
        let o = x(r, l, n);
        v.push({
          type: "item",
          item: {
            ...r,
            _categoryId: d,
            _categoryLabel: e
          },
          category: c,
          score: s.score - .001 * u - 1e-4 * o,
          matchFields: s.matchFields
        });
      }
    }
    for (let [e, t] of (b || []).entries()) {
      let a = h([{
        field: "name",
        text: m(t, o),
        weights: {
          exact: 135,
          startsWith: 105,
          wordStartsWith: 32,
          includes: 68,
          token: 18
        }
      }, {
        field: "category",
        text: m(t, d),
        weights: {
          exact: 50,
          startsWith: 40,
          wordStartsWith: 18,
          includes: 28,
          token: 9
        }
      }, {
        field: "description",
        text: m(t, c),
        weights: {
          exact: 28,
          startsWith: 22,
          wordStartsWith: 10,
          includes: 16,
          token: 6
        }
      }], y, j);
      if (!a) continue;
      let i = x(t, u, e);
      N.push({
        type: "combo",
        combo: t,
        score: a.score - 1e-4 * i,
        matchFields: a.matchFields
      });
    }
    v.sort((e, t) => t.score - e.score), N.sort((e, t) => t.score - e.score);
    let w = "number" == typeof g && g > 0 ? v.slice(0, g) : v,
      k = "number" == typeof g && g > 0 ? Math.max(0, g - w.length) : void 0,
      _ = "number" == typeof k ? N.slice(0, k) : N;
    return {
      query: y,
      itemResults: w,
      comboResults: _,
      items: w.map(({
        item: e
      }) => e),
      combos: _.map(({
        combo: e
      }) => e),
      totalCount: w.length + _.length
    };
  }]);
});

  Z.define(675378, e=>{var p = ["DINING", "TABLE_SIDE"];
function _(e, t, r = !1, i = !1) {
  return i ? "KIOSK" : r ? "dining" : "DELIVERY" === e ? t ? "ONLINE_APP_DELIVERY" : "ONLINE_DELIVERY" : t ? "ONLINE_APP_PICKUP" : "ONLINE_PICKUP";
}
function f(e, t) {
  let r = String(e?.orderType ?? e?.order_type ?? "").trim().toUpperCase(),
    i = t.trim().toUpperCase();
  return r === i || p.includes(i) && p.includes(r);
}
function y(e, t) {
  var r = e.find(e => f(e, t));
  if (!r) return null;
  let i = Number(r.ratioPoints ?? r.ratio_points ?? 0);
  return i > 0 ? {
    orderType: r.orderType || r.order_type,
    ratioPoints: i,
    minConsumablePoints: Number(r.minConsumablePoints ?? r.min_consumable_points ?? 0) || 0,
    maximumConsumablePoints: Number(r.maximumConsumablePoints ?? r.maximum_consumable_points ?? 0) || 0
  } : null;
};e.s(["getDisplayWalletRatio", 0, function (e, t, r = "PICKUP") {
  let i = e?.redemptionRatios || e?.redumption_ratio || [];
  if (!Array.isArray(i) || 0 === i.length) return null;
  let n = "PICKUP" === r ? "DELIVERY" : "PICKUP";
  for (let e of [_(r, t), _(n, t), _(r, !t), _(n, !t), "ALL"]) {
    let t = y(i, e);
    if (t) return t;
  }
  return null;
}, "summarizeRedemptionScope", 0, function (e, t) {
  let r = e?.redemptionRatios || e?.redumption_ratio || [],
    i = e => {
      let t = r.find(t => f(t, e));
      return Number(t?.ratioPoints ?? t?.ratio_points ?? 0) > 0;
    },
    n = i("ALL"),
    a = n || i("ONLINE_PICKUP"),
    o = n || i("ONLINE_APP_PICKUP"),
    l = n || i("ONLINE_DELIVERY"),
    s = n || i("ONLINE_APP_DELIVERY"),
    u = n || i("dining") || i("TABLE_SIDE"),
    d = t ? o : a,
    c = t ? s : l,
    m = d || c,
    p = a || o || l || s || u,
    _ = null;
  return p && !m ? _ = t ? "Available on website only" : "Available in the mobile app only" : d && !c ? _ = "Pickup orders only" : c && !d && (_ = "Delivery orders only"), {
    hasAny: p,
    hasForContext: m,
    pickupWeb: a,
    pickupApp: o,
    deliveryWeb: l,
    deliveryApp: s,
    pickupForCtx: d,
    deliveryForCtx: c,
    scopeLabel: _
  };
}]);});

  Z.define(678162, e => {
  "use strict";

  let t = (0, e.i(456420).default)("locate-fixed", [["line", {
    x1: "2",
    x2: "5",
    y1: "12",
    y2: "12",
    key: "bvdh0s"
  }], ["line", {
    x1: "19",
    x2: "22",
    y1: "12",
    y2: "12",
    key: "1tbv5k"
  }], ["line", {
    x1: "12",
    x2: "12",
    y1: "2",
    y2: "5",
    key: "11lu5j"
  }], ["line", {
    x1: "12",
    x2: "12",
    y1: "19",
    y2: "22",
    key: "x3vr5v"
  }], ["circle", {
    cx: "12",
    cy: "12",
    r: "7",
    key: "fim9np"
  }], ["circle", {
    cx: "12",
    cy: "12",
    r: "3",
    key: "1v7zrd"
  }]]);
  e.s(["LocateFixed", 0, t], 678162);
});

  Z.define(689664, e => {
  "use strict";

  let t = (0, e.i(456420).default)("check", [["path", {
    d: "M20 6 9 17l-5-5",
    key: "1gmf2c"
  }]]);
  e.s(["Check", 0, t], 689664);
});

  Z.define(692801, e => {
  "use strict";

  var t = e.i(843476),
    n = e.i(271645),
    i = e.i(618566),
    a = e.i(522016),
    r = e.i(370812),
    s = e.i(867927),
    l = e.i(235056),
    o = e.i(741120),
    c = e.i(226091),
    d = e.i(810818),
    u = e.i(420865),
    h = e.i(196699);
  let p = (0, e.i(456420).default)("radio", [["path", {
    d: "M16.247 7.761a6 6 0 0 1 0 8.478",
    key: "1fwjs5"
  }], ["path", {
    d: "M19.075 4.933a10 10 0 0 1 0 14.134",
    key: "ehdyv1"
  }], ["path", {
    d: "M4.925 19.067a10 10 0 0 1 0-14.134",
    key: "1q22gi"
  }], ["path", {
    d: "M7.753 16.239a6 6 0 0 1 0-8.478",
    key: "r2q7qm"
  }], ["circle", {
    cx: "12",
    cy: "12",
    r: "2",
    key: "1c9p78"
  }]]);
  var x = e.i(395925),
    b = e.i(584026),
    f = e.i(986206),
    m = e.i(75183),
    g = e.i(598457),
    j = e.i(394556),
    w = e.i(652348),
    y = e.i(309808),
    k = e.i(351969),
    N = e.i(273341),
    v = e.i(515746),
    C = e.i(999627);
  let P = `${j.default.storagePrefix}-notifications-enabled`;
  function S({
    enabled: e,
    label: n
  }) {
    return (0, t.jsx)("span", {
      role: "switch",
      "aria-checked": e,
      "aria-label": n,
      className: `relative inline-flex h-[26px] w-[44px] shrink-0 items-center rounded-full transition-colors ${e ? "bg-brand-primary" : "bg-brand-ink/15"}`,
      children: (0, t.jsx)("span", {
        className: `absolute h-[20px] w-[20px] rounded-full bg-white shadow-[0_1px_3px_rgba(19,19,22,0.25)] transition-transform ${e ? "translate-x-[21px]" : "translate-x-[3px]"}`
      })
    });
  }
  function E({
    icon: e,
    label: n,
    detail: i,
    trailing: r,
    onClick: l,
    href: o,
    destructive: c
  }) {
    let d = (0, t.jsxs)(t.Fragment, {
        children: [(0, t.jsx)("span", {
          className: `flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] ${c ? "bg-red-50 text-red-600" : "bg-[#EAF0FF] text-brand-primary"}`,
          children: (0, t.jsx)(e, {
            className: "h-[18px] w-[18px]",
            strokeWidth: 2,
            "aria-hidden": "true"
          })
        }), (0, t.jsxs)("span", {
          className: "min-w-0 flex-1",
          children: [(0, t.jsx)("span", {
            className: `block text-[14px] font-semibold ${c ? "text-red-600" : "text-brand-ink"}`,
            children: n
          }), i ? (0, t.jsx)("span", {
            className: "mt-0.5 block text-[12px] leading-[1.4] text-brand-ink-light",
            children: i
          }) : null]
        }), r ?? (0, t.jsx)(s.ChevronRight, {
          className: "h-5 w-5 shrink-0 text-brand-ink/28",
          strokeWidth: 2,
          "aria-hidden": "true"
        })]
      }),
      u = "flex min-h-[60px] w-full touch-manipulation items-center gap-3 px-3.5 py-2.5 text-left transition-colors active:bg-[#F5F5F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary/45";
    return o ? (0, t.jsx)(a.default, {
      href: o,
      className: u,
      onClick: () => (0, N.haptic)("selection"),
      children: d
    }) : (0, t.jsx)("button", {
      type: "button",
      className: u,
      onClick: l,
      children: d
    });
  }
  function _({
    title: e,
    children: n
  }) {
    return (0, t.jsxs)("section", {
      children: [(0, t.jsx)("h2", {
        className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
        children: e
      }), (0, t.jsx)("div", {
        className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8 bg-white",
        children: n
      })]
    });
  }
  e.s(["default", 0, function () {
    let e = (0, i.useRouter)(),
      a = (0, y.useToast)(),
      {
        currentBranch: s,
        restaurantConfig: R,
        orderType: I,
        setOrderType: z
      } = (0, w.useBranch)(),
      [A, O] = (0, n.useState)(!1),
      [T, L] = (0, n.useState)(!0),
      [F, B] = (0, n.useState)(!1),
      [H, Y] = (0, n.useState)("unknown"),
      M = k.BUILD_LABEL,
      [U, $] = (0, n.useState)(!1),
      V = (0, n.useRef)(!1),
      W = (0, n.useRef)(null);
    (0, n.useEffect)(() => (V.current = !0, () => {
      V.current = !1, null !== W.current && clearTimeout(W.current);
    }), []), (0, n.useEffect)(() => {
      try {
        O("1" === window.localStorage.getItem(P)), L("0" !== window.localStorage.getItem(N.HAPTICS_PREFERENCE_KEY)), B(!(0, C.hasCompletedZetaOnboarding)());
      } catch {}
      let e = navigator?.permissions;
      e?.query && e.query({
        name: "geolocation"
      }).then(e => Y(e.state)).catch(() => Y("unknown"));
    }, []);
    let q = (0, n.useCallback)((e, t) => {
      try {
        window.localStorage.setItem(e, t);
      } catch {}
    }, []);
    async function D() {
      (0, N.haptic)("selection"), $(!0);
      try {
        let {
          updateAvailable: e
        } = await (0, k.checkForBuildUpdate)();
        if (!V.current) return;
        if (e) {
          a.success("Update found", {
            title: "Reloading Zeta…"
          }), W.current = setTimeout(() => window.location.reload(), 600);
          return;
        }
        a.success("You're on the latest version");
      } catch {
        if (!V.current) return;
        a.warning("Could not check for updates", {
          title: "Check your connection and try again"
        });
      } finally {
        V.current && $(!1);
      }
    }
    async function K() {
      (0, N.haptic)("selection");
      try {
        await (0, v.getCurrentPosition)(), Y("granted"), a.success("Location enabled", {
          title: "We can sort stores by distance"
        });
      } catch {
        Y("denied"), a.warning("Location unavailable", {
          title: "Allow location for this site in your browser or device settings"
        });
      }
    }
    let G = R?.pickupEnabled !== !1,
      Z = !!R?.deliveryEnabled,
      Q = (("DELIVERY" === I ? R?.payment_delivery : R?.payment_pickup) || []).filter(e => e?.active !== 0);
    return (0, t.jsxs)("div", {
      className: "min-h-full bg-white",
      children: [(0, t.jsxs)("header", {
        className: "px-4 pb-2 pt-4",
        children: [(0, t.jsx)("h1", {
          className: "text-[24px] font-semibold leading-none tracking-[-0.03em] text-brand-ink",
          children: "Settings"
        }), (0, t.jsx)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light",
          children: "Preferences for this device."
        })]
      }), (0, t.jsxs)("main", {
        className: "space-y-4 px-4 pb-6",
        children: [(0, t.jsx)(_, {
          title: "Notifications",
          children: (0, t.jsx)(E, {
            icon: r.Bell,
            label: "Order updates",
            detail: "Order progress and Zeta Rewards news.",
            onClick: function () {
              let e = !A;
              (0, N.haptic)("selection"), O(e), q(P, e ? "1" : "0"), a.success(e ? "Notifications turned on" : "Notifications turned off");
            },
            trailing: (0, t.jsx)(S, {
              enabled: A,
              label: "Order and rewards notifications"
            })
          })
        }), (0, t.jsxs)(_, {
          title: "App experience",
          children: [(0, t.jsx)(E, {
            icon: g.Vibrate,
            label: "Haptic feedback",
            detail: "Vibrate on taps and adding items.",
            onClick: function () {
              let e = !T;
              e ? (q(N.HAPTICS_PREFERENCE_KEY, "1"), (0, N.haptic)("medium")) : ((0, N.haptic)("light"), q(N.HAPTICS_PREFERENCE_KEY, "0")), L(e);
            },
            trailing: (0, t.jsx)(S, {
              enabled: T,
              label: "Haptic feedback"
            })
          }), (0, t.jsx)(E, {
            icon: f.Smartphone,
            label: "Welcome screens",
            detail: "Show the intro next time the app opens.",
            onClick: function () {
              let e = !F;
              (0, N.haptic)("selection"), B(e), e ? (0, C.resetZetaOnboarding)() : (0, C.completeZetaOnboarding)(), a.success(e ? "Welcome screens will show next launch" : "Welcome screens turned off");
            },
            trailing: (0, t.jsx)(S, {
              enabled: F,
              label: "Show welcome screens"
            })
          }), (0, t.jsx)(E, {
            icon: x.RotateCcw,
            label: "Replay welcome screens now",
            detail: "Run the intro straight away.",
            onClick: () => {
              (0, N.haptic)("medium"), (0, C.resetZetaOnboarding)(), (0, C.replayZetaOnboarding)(), B(!0), e.push("/");
            }
          })]
        }), (0, t.jsxs)(_, {
          title: "Ordering",
          children: [(0, t.jsx)(E, {
            icon: m.Store,
            label: "Your store",
            detail: s?.name || "Choose a Zeta store",
            href: "/stores"
          }), G && Z ? (0, t.jsx)(E, {
            icon: p,
            label: "Default service",
            detail: "DELIVERY" === I ? "Delivery" : "Pickup",
            onClick: () => {
              (0, N.haptic)("selection");
              let e = "DELIVERY" === I ? "PICKUP" : "DELIVERY";
              z(e), a.success(`Default set to ${"DELIVERY" === e ? "delivery" : "pickup"}`);
            }
          }) : null, Q.length > 1 ? (0, t.jsx)(E, {
            icon: c.FileText,
            label: "Payment",
            detail: `${Q.length} methods available at checkout.`,
            href: "/checkout"
          }) : null, (0, t.jsx)(E, {
            icon: u.MapPin,
            label: "Location",
            detail: {
              granted: "On — stores are sorted by distance.",
              denied: "Blocked. Allow location in your browser or device settings.",
              prompt: "Off — turn on to sort stores by distance.",
              unknown: "Turn on to sort stores by distance."
            }[H],
            onClick: "granted" === H ? void 0 : K,
            trailing: "granted" === H ? (0, t.jsx)(S, {
              enabled: !0,
              label: "Location access"
            }) : void 0
          })]
        }), (0, t.jsxs)(_, {
          title: "Help and legal",
          children: [(0, t.jsx)(E, {
            icon: l.CircleHelp,
            label: "Help and FAQ",
            href: "/help"
          }), (0, t.jsx)(E, {
            icon: d.Info,
            label: "About Zeta",
            href: "/about"
          }), (0, t.jsx)(E, {
            icon: b.ShieldCheck,
            label: "Privacy",
            href: "/privacy"
          }), (0, t.jsx)(E, {
            icon: c.FileText,
            label: "Terms and conditions",
            href: "/terms"
          })]
        }), (0, t.jsx)(_, {
          title: "Account",
          children: (0, t.jsx)(E, {
            icon: h.Moon,
            label: "Profile and account",
            detail: "Details, addresses, sign out.",
            href: "/profile"
          })
        }), (0, t.jsx)(_, {
          title: "About this app",
          children: (0, t.jsx)(E, {
            icon: o.RefreshCw,
            label: U ? "Checking for updates…" : "Check for updates",
            detail: M ? `Version ${M}` : "Version unavailable",
            onClick: U ? void 0 : D
          })
        }), (0, t.jsx)("p", {
          className: "pt-1 text-center text-[11px] text-brand-ink-light",
          children: j.default.displayName
        })]
      })]
    });
  }], 692801);
});

  Z.define(700240, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    r = e.i(174080),
    i = e.i(482022),
    s = e.i(273341);
  let n = {
    instagram: {
      label: "Instagram",
      tint: "text-[#E1306C]",
      appUrl: "instagram://user?username=zeta_greek_kitchen"
    },
    facebook: {
      label: "Facebook",
      tint: "text-[#1877F2]",
      appUrl: "fb://profile/100054830416083"
    },
    tiktok: {
      label: "TikTok",
      tint: "text-brand-ink",
      appUrl: "tiktok://user?username=zetagreekkitchen1"
    }
  };
  function l({
    network: e,
    ...a
  }) {
    return "instagram" === e ? (0, t.jsx)("svg", {
      viewBox: "0 0 24 24",
      fill: "currentColor",
      "aria-hidden": "true",
      ...a,
      children: (0, t.jsx)("path", {
        d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98C.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
      })
    }) : "facebook" === e ? (0, t.jsx)("svg", {
      viewBox: "0 0 24 24",
      fill: "currentColor",
      "aria-hidden": "true",
      ...a,
      children: (0, t.jsx)("path", {
        d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      })
    }) : (0, t.jsx)("svg", {
      viewBox: "0 0 24 24",
      fill: "currentColor",
      "aria-hidden": "true",
      ...a,
      children: (0, t.jsx)("path", {
        d: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
      })
    });
  }
  e.s(["ZetaSocialMark", 0, l, "default", 0, function ({
    network: e,
    href: o,
    children: d,
    className: c,
    ariaLabel: m
  }) {
    let [u, p] = (0, a.useState)(!1),
      [x, h] = (0, a.useState)(!1),
      b = (0, a.useRef)(null),
      f = (0, a.useRef)(null),
      g = n[e];
    return (0, a.useEffect)(() => (p(!0), () => {
      b.current && window.clearTimeout(b.current), f.current && window.clearTimeout(f.current);
    }), []), (0, t.jsxs)(t.Fragment, {
      children: [(0, t.jsx)("button", {
        type: "button",
        onClick: () => {
          x || ((0, s.haptic)("selection"), h(!0), b.current = window.setTimeout(() => {
            (0, s.openExternalAppLink)({
              platform: e,
              appUrl: g.appUrl,
              webUrl: o
            });
          }, 900), f.current = window.setTimeout(() => h(!1), 2300));
        },
        "aria-label": m || `Open ${g.label}`,
        disabled: x,
        className: c,
        children: d
      }), u && x ? (0, r.createPortal)((0, t.jsx)("div", {
        role: "status",
        "aria-live": "assertive",
        "aria-label": `Opening ${g.label}`,
        className: "fixed inset-0 z-[2800] grid place-items-center bg-brand-ink/25 px-5 backdrop-blur-[3px]",
        children: (0, t.jsxs)("div", {
          className: "zeta-error-art-enter w-full max-w-[320px] overflow-hidden rounded-[24px] border border-brand-primary/10 bg-white px-6 pb-5 pt-6 text-center shadow-[0_24px_70px_rgba(8,18,45,0.22)]",
          children: [(0, t.jsxs)("div", {
            className: "relative mx-auto grid h-16 w-16 place-items-center rounded-[20px] bg-[#EEF3FF]",
            children: [(0, t.jsx)(l, {
              network: e,
              className: `h-7 w-7 ${g.tint}`
            }), (0, t.jsx)("span", {
              className: "absolute -bottom-1.5 -right-1.5 grid h-7 w-7 place-items-center rounded-full border-[3px] border-white bg-brand-primary text-white",
              children: (0, t.jsx)(i.ExternalLink, {
                className: "h-3.5 w-3.5",
                strokeWidth: 2.2,
                "aria-hidden": "true"
              })
            })]
          }), (0, t.jsx)("p", {
            className: "mt-5 text-[10px] font-bold uppercase tracking-[0.24em] text-brand-primary",
            children: "Leaving Zeta"
          }), (0, t.jsxs)("h2", {
            className: "mt-1.5 text-[21px] font-bold tracking-[-0.02em] text-brand-ink",
            children: ["Opening ", g.label]
          }), (0, t.jsx)("p", {
            className: "mx-auto mt-2 max-w-[250px] text-[13px] leading-5 text-brand-ink-light",
            children: "We'll open the app when it's installed, otherwise the website."
          }), (0, t.jsx)("div", {
            className: "mx-auto mt-5 h-1.5 w-28 overflow-hidden rounded-full bg-brand-primary/10",
            children: (0, t.jsx)("div", {
              className: "h-full w-2/3 animate-pulse rounded-full bg-brand-primary"
            })
          })]
        })
      }), document.body) : null]
    });
  }, "getZetaSocialMeta", 0, function (e) {
    return n[e];
  }]);
});

  Z.define(701688, e => {
  "use strict";

  var t = e.i(271645);
  let r = 0;
  e.s(["useBodyScrollLock", 0, function (e) {
    (0, t.useEffect)(() => {
      if (e && "u" > typeof document) return 1 === (r += 1) && (document.body.classList.add("drawer-open"), document.documentElement.classList.add("drawer-open")), () => {
        0 === (r = Math.max(0, r - 1)) && (document.body.classList.remove("drawer-open"), document.documentElement.classList.remove("drawer-open"));
      };
    }, [e]);
  }]);
});

  Z.define(709696, e => {
  "use strict"; var fetch=window.OO.zetaReference.submitEnquiry;

  var a = e.i(843476),
    r = e.i(271645),
    t = e.i(551757),
    i = e.i(611568);
  let n = (0, e.i(456420).default)("send", [["path", {
    d: "M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z",
    key: "1ffxy3"
  }], ["path", {
    d: "m21.854 2.147-10.94 10.939",
    key: "12cjpa"
  }]]);
  var s = e.i(75183),
    l = e.i(988771),
    d = e.i(977617),
    o = e.i(799552);
  let c = [{
      title: "Proven concept",
      body: "A modern take on traditional Greek cuisine built around fresh Mediterranean food and warm hospitality."
    }, {
      title: "Fresh and authentic",
      body: "Warm pita, house sauces, dips, salads, seafood, falafel and slow rotisserie-cooked meats."
    }, {
      title: "Operational support",
      body: "Support across site selection, training, marketing and ongoing operations."
    }, {
      title: "Scalable model",
      body: "A fast-casual Greek kitchen model designed for repeatable operations and future growth."
    }],
    m = ["Under $100k", "$100k - $250k", "$250k - $500k", "$500k+"],
    h = "h-12 w-full rounded-[14px] border border-brand-ink/10 bg-[#F5F5F7] px-3.5 text-[14px] font-medium text-brand-ink outline-none placeholder:text-brand-ink/35 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/12",
    p = "mb-1.5 block text-[11px] font-semibold text-brand-ink-light";
  e.s(["default", 0, function () {
    let [e, x] = (0, r.useState)("idle");
    async function u(e) {
      e.preventDefault();
      let a = new FormData(e.currentTarget),
        r = {
          type: "franchise",
          name: a.get("name"),
          email: a.get("email"),
          phone: a.get("phone"),
          location: a.get("location"),
          budget: a.get("budget"),
          experience: a.get("experience")
        };
      x("sending");
      try {
        let e = await fetch("/api/leads", {
            method: "POST",
            headers: {
              "content-type": "application/json"
            },
            body: JSON.stringify(r)
          }),
          a = await e.json();
        x(a.ok ? "sent" : "error");
      } catch {
        x("error");
      }
    }
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsx)(o.MobilePageHeader, {
        icon: s.Store,
        kicker: "Grow with Zeta",
        title: "Franchise",
        description: "Share the flavours and hospitality of Zeta with your community."
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[800px] space-y-5 px-4 sm:px-6",
        children: [(0, a.jsxs)("section", {
          className: "relative h-[176px] overflow-hidden rounded-[20px] bg-[#EBEBEF] sm:h-[220px]",
          children: [(0, a.jsx)(d.default, {
            src: "/brands/zeta/hero-gyros.webp",
            alt: "Zeta souvlaki wrap",
            fill: !0,
            priority: !0,
            sizes: "(max-width: 520px) calc(100vw - 32px), 752px",
            className: "object-cover",
            context: "MobileFranchise.hero"
          }), (0, a.jsx)("div", {
            className: "absolute inset-0 bg-gradient-to-t from-black/65 via-black/12 to-transparent"
          }), (0, a.jsxs)("div", {
            className: "absolute bottom-0 left-0 right-0 p-4 text-white",
            children: [(0, a.jsx)("p", {
              className: "text-[10px] font-semibold uppercase tracking-[0.12em] text-white/75",
              children: "Be part of the story"
            }), (0, a.jsx)("h2", {
              className: "mt-1 text-[20px] font-bold",
              children: "Bring Zeta to your area"
            })]
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[16px] font-bold",
            children: "Why Zeta"
          }), (0, a.jsx)("div", {
            className: "grid grid-cols-2 gap-2 md:grid-cols-4",
            children: c.map(e => (0, a.jsxs)("article", {
              className: "rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
              children: [(0, a.jsx)(l.UsersRound, {
                className: "h-5 w-5 text-brand-primary",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              }), (0, a.jsx)("h3", {
                className: "mt-2 text-[13px] font-semibold leading-tight",
                children: e.title
              }), (0, a.jsx)("p", {
                className: "mt-1 text-[10px] leading-relaxed text-brand-ink-light",
                children: e.body
              })]
            }, e.title))
          })]
        }), (0, a.jsx)("section", {
          className: "overflow-hidden rounded-[16px] border border-brand-ink/8",
          children: "sent" === e ? (0, a.jsxs)("div", {
            className: "px-5 py-9 text-center",
            children: [(0, a.jsx)("span", {
              className: "mx-auto grid h-12 w-12 place-items-center rounded-[14px] bg-emerald-50 text-emerald-600",
              children: (0, a.jsx)(t.CheckCircle2, {
                className: "h-6 w-6",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), (0, a.jsx)("h2", {
              className: "mt-3 text-[18px] font-bold",
              children: "Enquiry received"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
              children: "Thanks for your interest in franchising with Zeta. The team will be in touch."
            })]
          }) : (0, a.jsxs)(a.Fragment, {
            children: [(0, a.jsxs)("div", {
              className: "border-b border-brand-ink/8 px-3.5 py-3",
              children: [(0, a.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: "Franchise enquiry"
              }), (0, a.jsx)("p", {
                className: "mt-0.5 text-[11px] text-brand-ink-light",
                children: "Tell us about your preferred location, budget and experience."
              })]
            }), (0, a.jsxs)("form", {
              onSubmit: u,
              className: "grid gap-3 p-3.5 sm:grid-cols-2",
              children: [(0, a.jsxs)("label", {
                className: "block",
                htmlFor: "mobile-fr-name",
                children: [(0, a.jsx)("span", {
                  className: p,
                  children: "Full name *"
                }), (0, a.jsx)("input", {
                  id: "mobile-fr-name",
                  name: "name",
                  required: !0,
                  autoComplete: "name",
                  className: h,
                  placeholder: "Full name"
                })]
              }), (0, a.jsxs)("label", {
                className: "block",
                htmlFor: "mobile-fr-phone",
                children: [(0, a.jsx)("span", {
                  className: p,
                  children: "Phone *"
                }), (0, a.jsx)("input", {
                  id: "mobile-fr-phone",
                  name: "phone",
                  required: !0,
                  type: "tel",
                  autoComplete: "tel",
                  className: h,
                  placeholder: "021 ..."
                })]
              }), (0, a.jsxs)("label", {
                className: "block",
                htmlFor: "mobile-fr-email",
                children: [(0, a.jsx)("span", {
                  className: p,
                  children: "Email *"
                }), (0, a.jsx)("input", {
                  id: "mobile-fr-email",
                  name: "email",
                  required: !0,
                  type: "email",
                  autoComplete: "email",
                  className: h,
                  placeholder: "you@example.com"
                })]
              }), (0, a.jsxs)("label", {
                className: "block",
                htmlFor: "mobile-fr-location",
                children: [(0, a.jsx)("span", {
                  className: p,
                  children: "Preferred location *"
                }), (0, a.jsx)("input", {
                  id: "mobile-fr-location",
                  name: "location",
                  required: !0,
                  className: h,
                  placeholder: "City or suburb"
                })]
              }), (0, a.jsxs)("label", {
                className: "block",
                htmlFor: "mobile-fr-budget",
                children: [(0, a.jsx)("span", {
                  className: p,
                  children: "Investment budget"
                }), (0, a.jsxs)("select", {
                  id: "mobile-fr-budget",
                  name: "budget",
                  defaultValue: "",
                  className: h,
                  children: [(0, a.jsx)("option", {
                    value: "",
                    disabled: !0,
                    children: "Select a range"
                  }), m.map(e => (0, a.jsx)("option", {
                    value: e,
                    children: e
                  }, e))]
                })]
              }), (0, a.jsxs)("label", {
                className: "block",
                htmlFor: "mobile-fr-experience",
                children: [(0, a.jsx)("span", {
                  className: p,
                  children: "Hospitality or business experience"
                }), (0, a.jsx)("input", {
                  id: "mobile-fr-experience",
                  name: "experience",
                  className: h,
                  placeholder: "Cafe owner, operator, investor..."
                })]
              }), "error" === e ? (0, a.jsx)("p", {
                className: "rounded-[12px] bg-red-50 px-3 py-2.5 text-[11px] font-medium text-red-600 sm:col-span-2",
                children: "Something went wrong. Please try again."
              }) : null, (0, a.jsxs)("button", {
                type: "submit",
                disabled: "sending" === e,
                className: "flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary text-[14px] font-semibold text-white disabled:opacity-50 sm:col-span-2",
                children: ["sending" === e ? (0, a.jsx)(i.LoaderCircle, {
                  className: "h-4 w-4 animate-spin",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }) : (0, a.jsx)(n, {
                  className: "h-4 w-4",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                }), "sending" === e ? "Sending enquiry" : "Submit enquiry"]
              })]
            })]
          })
        })]
      })]
    });
  }], 709696);
});

  Z.define(716327, e => {
  "use strict";

  let t = (0, e.i(456420).default)("chevron-down", [["path", {
    d: "m6 9 6 6 6-6",
    key: "qrunsl"
  }]]);
  e.s(["ChevronDown", 0, t], 716327);
});

  Z.define(724983, e => {
  "use strict";

  let t = (0, e.i(456420).default)("circle-alert", [["circle", {
    cx: "12",
    cy: "12",
    r: "10",
    key: "1mglay"
  }], ["line", {
    x1: "12",
    x2: "12",
    y1: "8",
    y2: "12",
    key: "1pkeuh"
  }], ["line", {
    x1: "12",
    x2: "12.01",
    y1: "16",
    y2: "16",
    key: "4dfq90"
  }]]);
  e.s(["default", 0, t]);
});

  Z.define(741120, e => {
  "use strict";

  let t = (0, e.i(456420).default)("refresh-cw", [["path", {
    d: "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8",
    key: "v9h5vc"
  }], ["path", {
    d: "M21 3v5h-5",
    key: "1q7to0"
  }], ["path", {
    d: "M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16",
    key: "3uifl3"
  }], ["path", {
    d: "M8 16H3v5",
    key: "1cv678"
  }]]);
  e.s(["RefreshCw", 0, t], 741120);
});

  Z.define(747764, e => {
  "use strict";

  var a = e.i(843476),
    t = e.i(129768),
    r = e.i(96315),
    s = e.i(248510),
    n = e.i(621999),
    l = e.i(575387),
    i = e.i(75183),
    o = e.i(394556),
    d = e.i(799552),
    c = e.i(700240);
  let m = "+64 9 600 2598",
    u = "zetagreekkitchen@gmail.com";
  e.s(["default", 0, function () {
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsx)(d.MobilePageHeader, {
        icon: n.MessageCircleMore,
        kicker: "Get in touch",
        title: "Contact Zeta",
        description: "General questions, catering, franchise interest and store help."
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[720px] space-y-5 px-4 sm:px-6",
        children: [(0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Contact details"
          }), (0, a.jsxs)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8",
            children: [(0, a.jsx)(d.MobileLinkRow, {
              href: `mailto:${u}`,
              icon: r.Mail,
              label: u,
              detail: "General enquiries, catering and booking questions.",
              external: !0
            }), (0, a.jsx)(d.MobileLinkRow, {
              href: `tel:${m.replace(/\s+/g, "")}`,
              icon: l.Phone,
              label: m,
              detail: "New Zealand enquiries.",
              external: !0
            }), (0, a.jsx)(d.MobileLinkRow, {
              href: "/stores",
              icon: s.MapPinned,
              label: "Find a Zeta store",
              detail: "Addresses, live status and directions."
            })]
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Plan with Zeta"
          }), (0, a.jsxs)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8",
            children: [(0, a.jsx)(d.MobileLinkRow, {
              href: "/catering",
              icon: t.CalendarDays,
              label: "Catering and events",
              detail: "Office lunches, functions, events and group catering."
            }), (0, a.jsx)(d.MobileLinkRow, {
              href: "/franchise",
              icon: i.Store,
              label: "Franchise enquiries",
              detail: "Share your location, budget and experience with the Zeta team."
            })]
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
            children: "Follow Zeta"
          }), (0, a.jsxs)("div", {
            className: "grid grid-cols-2 gap-2",
            children: [o.default.socials?.instagram ? (0, a.jsxs)(c.default, {
              network: "instagram",
              href: o.default.socials.instagram,
              ariaLabel: "Open Zeta on Instagram",
              className: "flex min-h-[56px] touch-manipulation items-center gap-2.5 rounded-[15px] bg-[#F5F5F7] px-3 text-[13px] font-semibold",
              children: [(0, a.jsx)("span", {
                className: "grid h-9 w-9 place-items-center rounded-[11px] bg-white text-[#E1306C]",
                children: (0, a.jsx)(c.ZetaSocialMark, {
                  network: "instagram",
                  className: "h-[18px] w-[18px]"
                })
              }), "Instagram"]
            }) : null, o.default.socials?.facebook ? (0, a.jsxs)(c.default, {
              network: "facebook",
              href: o.default.socials.facebook,
              ariaLabel: "Open Zeta on Facebook",
              className: "flex min-h-[56px] touch-manipulation items-center gap-2.5 rounded-[15px] bg-[#F5F5F7] px-3 text-[13px] font-semibold",
              children: [(0, a.jsx)("span", {
                className: "grid h-9 w-9 place-items-center rounded-[11px] bg-white text-[#1877F2]",
                children: (0, a.jsx)(c.ZetaSocialMark, {
                  network: "facebook",
                  className: "h-[18px] w-[18px]"
                })
              }), "Facebook"]
            }) : null]
          })]
        }), (0, a.jsxs)("section", {
          className: "flex gap-3 rounded-[16px] bg-[#EAF0FF] p-3.5",
          children: [(0, a.jsx)("span", {
            className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
            children: (0, a.jsx)(i.Store, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            })
          }), (0, a.jsxs)("div", {
            children: [(0, a.jsx)("h2", {
              className: "text-[13px] font-semibold",
              children: "Need help with an active order?"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
              children: "Contact the selected store as soon as possible. Store details are kept current in the directory."
            })]
          })]
        })]
      })]
    });
  }]);
});

  Z.define(753004, e => {
  "use strict";

  let t = (0, e.i(456420).default)("book-open-text", [["path", {
    d: "M12 7v14",
    key: "1akyts"
  }], ["path", {
    d: "M16 12h2",
    key: "7q9ll5"
  }], ["path", {
    d: "M16 8h2",
    key: "msurwy"
  }], ["path", {
    d: "M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z",
    key: "ruj8y"
  }], ["path", {
    d: "M6 12h2",
    key: "32wvfc"
  }], ["path", {
    d: "M6 8h2",
    key: "30oboj"
  }]]);
  e.s(["BookOpenText", 0, t], 753004);
});

  Z.define(754675, e => {
  "use strict";

  let t = new Set(["BBQ", "NZ", "XL", "GF", "DF", "VG"]);
  function a(e) {
    let a = String(e ?? "").trim();
    if (!a) return "";
    let r = a.replace(/[^A-Za-z]/g, "");
    return r.length > 1 && r === r.toUpperCase() ? a.toLowerCase().replace(/[A-Za-z][A-Za-z']*/g, e => {
      let a = e.toUpperCase();
      return t.has(a) ? a : e.charAt(0).toUpperCase() + e.slice(1);
    }).replace(/(\d)\s?(Ml|L|G|Kg|Pc|Pcs)\b/g, (e, t, a) => `${t}${a.toLowerCase()}`) : a;
  }
  e.s(["displayLabel", 0, a, "sentenceLabel", 0, function (e) {
    let t = a(e);
    return t && /^[a-z]/.test(t) ? t.charAt(0).toUpperCase() + t.slice(1) : t;
  }]);
});

  Z.define(755566, e => {
  "use strict";

  let t = (0, e.i(456420).default)("triangle-alert", [["path", {
    d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3",
    key: "wmoenq"
  }], ["path", {
    d: "M12 9v4",
    key: "juzpu7"
  }], ["path", {
    d: "M12 17h.01",
    key: "p32p05"
  }]]);
  e.s(["default", 0, t]);
});

  Z.define(758379, e => {
  "use strict";

  let t = (0, e.i(456420).default)("loader-circle", [["path", {
    d: "M21 12a9 9 0 1 1-6.219-8.56",
    key: "13zald"
  }]]);
  e.s(["default", 0, t]);
});

  Z.define(763566, e => {
  "use strict";

  let t = (0, e.i(456420).default)("wallet-cards", [["path", {
    d: "M3 11h3.75a2 2 0 0 1 1.6.8l.45.6a4 4 0 0 0 6.4 0l.45-.6a2 2 0 0 1 1.6-.8H21",
    key: "1vwh6y"
  }], ["path", {
    d: "M3 7h18",
    key: "1uiuf2"
  }], ["rect", {
    x: "3",
    y: "3",
    width: "18",
    height: "18",
    rx: "2",
    key: "h1oib"
  }]]);
  e.s(["WalletCards", 0, t], 763566);
});

  Z.define(764679, e => {
  "use strict";

  var a = e.i(843476),
    t = e.i(271645),
    s = e.i(522016),
    r = e.i(618566),
    l = e.i(689664),
    n = e.i(716327),
    i = e.i(508734),
    o = e.i(972764),
    d = e.i(328623),
    c = e.i(200416),
    u = e.i(528526),
    m = e.i(990746),
    p = e.i(652348),
    _ = e.i(913734),
    x = e.i(566450),
    f = e.i(273341),
    h = e.i(376151);
  e.i(651164);
  var b = e.i(917848);
  function g(e) {
    if (e && "object" == typeof e) return e;
    if ("string" == typeof e) try {
      return JSON.parse(e);
    } catch {}
    return {};
  }
  function y(e) {
    return g(e?.rules);
  }
  function N(e) {
    return g(e?.result);
  }
  function j(e) {
    return y(e).type || N(e).type || "";
  }
  function v(e, a) {
    if (!e) return null;
    let t = new Date(e);
    return Number.isNaN(t.getTime()) ? null : t.toLocaleDateString("Australia/Sydney" === a ? "en-AU" : "en-NZ", {
      timeZone: a,
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  }
  function k({
    coupon: e,
    exclusive: t,
    copied: s,
    onCopy: r,
    orderType: u,
    now: m,
    isMobileApp: p,
    timeZone: _
  }) {
    var f;
    let h,
      b,
      g,
      w,
      V,
      C = String(e.coupon_code || e.code || "").trim(),
      A = y(e),
      P = N(e),
      T = (0, x.isEntityAvailable)(e, u, m, p, !1, !1, !1, _),
      I = Number(A.MIN_ORDER_VALUE ?? P.MIN_ORDER_VALUE ?? 0),
      S = Number(A.MAX_ORDER_VALUE ?? P.MAX_ORDER_VALUE ?? 0),
      E = A.APPLICABLE_FOR || "ALL",
      M = v(e.expiry || e.enddate || e.end_date, _),
      L = v(e.startdate || e.start_date, _),
      F = e.terms_conditions || e.terms || "",
      U = Array.isArray(e.products) ? e.products : [],
      R = Array.isArray(e.parseditems) ? e.parseditems : [],
      O = s === C;
    return (0, a.jsxs)("article", {
      className: `overflow-hidden rounded-[16px] border bg-white ${t ? "border-brand-primary/20" : "border-brand-ink/8"} ${T ? "" : "opacity-60"}`,
      children: [(0, a.jsxs)("div", {
        className: "p-3.5",
        children: [(0, a.jsxs)("div", {
          className: "flex items-start justify-between gap-3",
          children: [(0, a.jsxs)("div", {
            className: "min-w-0",
            children: [(0, a.jsxs)("div", {
              className: "flex flex-wrap items-center gap-1.5",
              children: [(0, a.jsx)("span", {
                className: `rounded-full px-2 py-1 text-[9px] font-semibold ${t ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
                children: t ? "Exclusive" : function (e) {
                  switch (j(e)) {
                    case "CART_DISCOUNT":
                      return "Cart discount";
                    case "ITEM_DISCOUNT":
                      return "Item discount";
                    case "FREE_DELIVERY":
                      return "Free delivery";
                    case "BUY_ONE_GET_ONE_FREE":
                      return "Buy one, get one";
                    case "BUY_N_GET_M_FREE":
                      return "Multi-buy";
                    default:
                      return e.discount_label || "Special offer";
                  }
                }(e)
              }), T ? null : (0, a.jsx)("span", {
                className: "rounded-full bg-red-50 px-2 py-1 text-[9px] font-semibold text-red-600",
                children: "Not available now"
              })]
            }), (0, a.jsx)("h3", {
              className: "mt-2 text-[16px] font-bold leading-tight",
              children: e.name || "Zeta coupon"
            }), e.description ? (0, a.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
              children: e.description
            }) : null]
          }), (0, a.jsx)("span", {
            className: "grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-[#EAF0FF] text-brand-primary",
            children: t ? (0, a.jsx)(d.Sparkles, {
              className: "h-5 w-5",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            }) : (0, a.jsx)(c.TicketPercent, {
              className: "h-5 w-5",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            })
          })]
        }), (0, a.jsxs)("div", {
          className: "mt-3 rounded-[13px] border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2.5",
          children: [(0, a.jsx)("p", {
            className: "text-[9px] font-medium text-brand-ink-light",
            children: "Your saving"
          }), (0, a.jsx)("p", {
            className: "mt-1 text-[13px] font-bold text-brand-primary",
            children: (h = y(f = e), b = N(f), g = j(f), w = Number(b.DISCOUNT || 0), V = Number(b.MAX_DISCOUNT_VALUE || 0), "FREE_DELIVERY" === g ? V > 0 ? `Free delivery up to $${V.toFixed(2)}` : "Free delivery" : "BUY_ONE_GET_ONE_FREE" === g ? "Buy one, get one free" : "BUY_N_GET_M_FREE" === g ? "Buy N, get M free" : "CART_DISCOUNT" === g ? V > 0 ? `${w}% off, up to $${V.toFixed(2)}` : `${w}% off your order` : "ITEM_DISCOUNT" === g ? 100 === w ? "Free item" : V > 0 ? `${w}% off items, up to $${V.toFixed(2)}` : `${w}% off selected items` : f.discount_label || h.discount_label || "Special offer")
          })]
        }), I > 0 || S > 0 || "ALL" !== E || L || M ? (0, a.jsxs)("dl", {
          className: "mt-3 grid grid-cols-2 gap-2 text-[10px]",
          children: [I > 0 ? (0, a.jsxs)("div", {
            className: "rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2",
            children: [(0, a.jsx)("dt", {
              className: "text-brand-ink-light",
              children: "Minimum order"
            }), (0, a.jsxs)("dd", {
              className: "mt-1 font-semibold",
              children: ["$", I.toFixed(2)]
            })]
          }) : null, S > 0 ? (0, a.jsxs)("div", {
            className: "rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2",
            children: [(0, a.jsx)("dt", {
              className: "text-brand-ink-light",
              children: "Maximum order"
            }), (0, a.jsxs)("dd", {
              className: "mt-1 font-semibold",
              children: ["$", S.toFixed(2)]
            })]
          }) : null, "ALL" !== E ? (0, a.jsxs)("div", {
            className: "rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2",
            children: [(0, a.jsx)("dt", {
              className: "text-brand-ink-light",
              children: "Valid for"
            }), (0, a.jsx)("dd", {
              className: "mt-1 font-semibold capitalize",
              children: String(E).toLowerCase()
            })]
          }) : null, L || M ? (0, a.jsxs)("div", {
            className: "rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2",
            children: [(0, a.jsx)("dt", {
              className: "text-brand-ink-light",
              children: M ? "Expires" : "Starts"
            }), (0, a.jsx)("dd", {
              className: "mt-1 font-semibold",
              children: M || L
            })]
          }) : null]
        }) : null, U.length || R.length ? (0, a.jsxs)("div", {
          className: "mt-3 text-[10px] leading-relaxed text-brand-ink-light",
          children: [(0, a.jsx)("span", {
            className: "font-semibold text-brand-ink",
            children: "Applies to: "
          }), U.length ? U.map(e => e.label || e.name || e.product_name).filter(Boolean).join(", ") : `${R.length} selected ${1 === R.length ? "item" : "items"}`]
        }) : null, F ? (0, a.jsxs)("details", {
          className: "mt-3 rounded-[12px] border border-brand-ink/8 bg-[#F5F5F7] px-3",
          children: [(0, a.jsxs)("summary", {
            className: "flex min-h-11 cursor-pointer touch-manipulation list-none items-center justify-between text-[10px] font-semibold",
            children: ["Terms and conditions", (0, a.jsx)(n.ChevronDown, {
              className: "h-4 w-4 text-brand-ink-light",
              strokeWidth: 2,
              "aria-hidden": "true"
            })]
          }), (0, a.jsx)("p", {
            className: "pb-3 whitespace-pre-line text-[10px] leading-relaxed text-brand-ink-light",
            children: F
          })]
        }) : null]
      }), (0, a.jsx)("div", {
        className: "border-t border-dashed border-brand-ink/12 p-3.5",
        children: (0, a.jsxs)("button", {
          type: "button",
          onClick: () => r(C),
          disabled: !T || !C,
          className: `flex min-h-12 w-full touch-manipulation items-center justify-between rounded-[13px] px-3.5 ${O ? "bg-emerald-50 text-emerald-700" : T ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
          children: [(0, a.jsx)("span", {
            className: "font-mono text-[13px] font-bold tracking-[0.08em]",
            children: O ? "Copied" : C || "No code"
          }), O ? (0, a.jsx)(l.Check, {
            className: "h-4 w-4",
            strokeWidth: 2.3,
            "aria-hidden": "true"
          }) : T ? (0, a.jsx)(i.Copy, {
            className: "h-4 w-4",
            strokeWidth: 2,
            "aria-hidden": "true"
          }) : (0, a.jsx)(o.LockKeyhole, {
            className: "h-4 w-4",
            strokeWidth: 2,
            "aria-hidden": "true"
          })]
        })
      })]
    });
  }
  e.s(["default", 0, function () {
    let e = (0, r.useRouter)(),
      l = (0, u.useZetaAppSurface)(),
      {
        user: n,
        loading: i
      } = (0, m.useAuth)(),
      {
        currentBranch: o,
        restaurantConfig: d,
        orderType: x,
        scheduleType: g,
        scheduledTime: y
      } = (0, p.useBranch)(),
      [N, j] = (0, t.useState)([]),
      [v, w] = (0, t.useState)(!0),
      [V, C] = (0, t.useState)(null),
      A = String(d?.timezone || d?.timeZone || o?.timezone || h.DEFAULT_TIME_ZONE),
      P = (0, t.useMemo)(() => {
        if ("SCHEDULED" === g && y) try {
          let e = new Date((0, h.tzSlotToUTC)(y, A));
          if (!Number.isNaN(e.getTime())) return e;
        } catch {}
        return (0, h.getTimeZoneNow)(d?.serverTime, A);
      }, [d?.serverTime, g, y, A]);
    (0, t.useEffect)(() => {
      let e = !1;
      w(!0);
      let a = Number(d?.id || d?.hotelId || o?.storeId || 349);
      return (0, b.getCoupons)(a).then(a => {
        e || j(a?.data || []);
      }).catch(() => {
        e || j([]);
      }).finally(() => {
        e || w(!1);
      }), () => {
        e = !0;
      };
    }, [o?.storeId, i, d?.hotelId, d?.id, e, n]);
    let T = (0, t.useMemo)(() => N.filter(e => e?.is_exposed === void 0 || e?.is_exposed === 1), [N]);
    if (v && 0 === N.length) return (0, a.jsx)(_.default, {
      kind: "member"
    });
    let I = T.filter(e => e.is_private),
      S = T.filter(e => !e.is_private),
      E = "DELIVERY" === x ? "DELIVERY" : "PICKUP";
    function M(e) {
      e && ((0, f.haptic)("selection"), navigator.clipboard?.writeText(e), C(e), window.setTimeout(() => C(null), 1800));
    }
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsxs)("header", {
        className: "mx-auto w-full max-w-[760px] px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6",
        children: [(0, a.jsxs)("div", {
          className: "flex items-center gap-2 text-brand-primary",
          children: [(0, a.jsx)(c.TicketPercent, {
            className: "h-4 w-4",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), (0, a.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.12em]",
            children: "Member savings"
          })]
        }), (0, a.jsx)("h1", {
          className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.025em] sm:text-[30px]",
          children: "Coupons"
        }), (0, a.jsxs)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light sm:text-[13px]",
          children: ["Live codes for ", o?.name || "your selected Zeta store", "."]
        })]
      }), (0, a.jsx)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-5 px-4 sm:px-6",
        children: v ? (0, a.jsx)("div", {
          className: "grid gap-2.5 md:grid-cols-2",
          "aria-label": "Loading coupons",
          children: [0, 1, 2].map(e => (0, a.jsx)("div", {
            className: "zeta-app-skeleton h-[180px] rounded-[16px]"
          }, e))
        }) : T.length ? (0, a.jsxs)(a.Fragment, {
          children: [I.length ? (0, a.jsxs)("section", {
            children: [(0, a.jsxs)("div", {
              className: "mb-2 flex items-center justify-between",
              children: [(0, a.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: "Just for you"
              }), (0, a.jsxs)("span", {
                className: "rounded-full bg-[#EAF0FF] px-2.5 py-1 text-[10px] font-semibold text-brand-primary",
                children: [I.length, " exclusive"]
              })]
            }), (0, a.jsx)("div", {
              className: "grid gap-2.5 md:grid-cols-2",
              children: I.map(e => (0, a.jsx)(k, {
                coupon: e,
                exclusive: !0,
                copied: V,
                onCopy: M,
                orderType: E,
                now: P,
                isMobileApp: l,
                timeZone: A
              }, e.id || e.coupon_code))
            })]
          }) : null, S.length ? (0, a.jsxs)("section", {
            children: [(0, a.jsxs)("div", {
              className: "mb-2 flex items-center justify-between",
              children: [(0, a.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: "Available deals"
              }), (0, a.jsxs)("span", {
                className: "text-[10px] text-brand-ink-light",
                children: [S.length, " live"]
              })]
            }), (0, a.jsx)("div", {
              className: "grid gap-2.5 md:grid-cols-2",
              children: S.map(e => (0, a.jsx)(k, {
                coupon: e,
                exclusive: !1,
                copied: V,
                onCopy: M,
                orderType: E,
                now: P,
                isMobileApp: l,
                timeZone: A
              }, e.id || e.coupon_code))
            })]
          }) : null]
        }) : (0, a.jsxs)("section", {
          className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-8 text-center",
          children: [(0, a.jsx)("span", {
            className: "mx-auto grid h-12 w-12 place-items-center rounded-[14px] bg-white text-brand-primary",
            children: (0, a.jsx)(c.TicketPercent, {
              className: "h-6 w-6",
              strokeWidth: 1.8,
              "aria-hidden": "true"
            })
          }), (0, a.jsx)("h2", {
            className: "mt-3 text-[16px] font-bold",
            children: "No coupons right now"
          }), (0, a.jsx)("p", {
            className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
            children: "New member coupons will appear here automatically when they are live."
          }), (0, a.jsx)(s.default, {
            href: "/offers",
            className: "mt-4 inline-flex h-11 items-center justify-center rounded-[13px] bg-brand-primary px-5 text-[13px] font-semibold text-white",
            children: "Back to Zeta Zone"
          })]
        })
      })]
    });
  }]);
});

  Z.define(765649, e => {
  "use strict";

  let t = (0, e.i(456420).default)("user-round", [["circle", {
    cx: "12",
    cy: "8",
    r: "5",
    key: "1hypcn"
  }], ["path", {
    d: "M20 21a8 8 0 0 0-16 0",
    key: "rfgkzh"
  }]]);
  e.s(["UserRound", 0, t], 765649);
});

  Z.define(768877, e => {
  "use strict";

  let t = (0, e.i(456420).default)("arrow-right", [["path", {
    d: "M5 12h14",
    key: "1ays0h"
  }], ["path", {
    d: "m12 5 7 7-7 7",
    key: "xquz4c"
  }]]);
  e.s(["ArrowRight", 0, t], 768877);
});

  Z.define(782924, e => {
  "use strict";

  let t = (0, e.i(456420).default)("briefcase-business", [["path", {
    d: "M12 12h.01",
    key: "1mp3jc"
  }], ["path", {
    d: "M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2",
    key: "1ksdt3"
  }], ["path", {
    d: "M22 13a18.15 18.15 0 0 1-20 0",
    key: "12hx5q"
  }], ["rect", {
    width: "20",
    height: "14",
    x: "2",
    y: "6",
    rx: "2",
    key: "i6l2r4"
  }]]);
  e.s(["BriefcaseBusiness", 0, t], 782924);
});

  Z.define(786563, e => {
  "use strict";

  let t = (0, e.i(456420).default)("star", [["path", {
    d: "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z",
    key: "r04s7s"
  }]]);
  e.s(["Star", 0, t], 786563);
});

  Z.define(799552, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(522016),
    r = e.i(867927),
    n = e.i(273341);
  function i({
    icon: e,
    kicker: a,
    title: r,
    description: n
  }) {
    return (0, t.jsxs)("header", {
      className: "mx-auto w-full max-w-[760px] px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6",
      children: [(0, t.jsxs)("div", {
        className: "flex items-center gap-2 text-brand-primary",
        children: [(0, t.jsx)(e, {
          className: "h-4 w-4",
          strokeWidth: 2.1,
          "aria-hidden": "true"
        }), (0, t.jsx)("p", {
          className: "text-[10px] font-bold uppercase tracking-[0.12em]",
          children: a
        })]
      }), (0, t.jsx)("h1", {
        className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.025em] text-brand-ink sm:text-[30px]",
        children: r
      }), (0, t.jsx)("p", {
        className: "mt-1.5 max-w-[42rem] text-[12px] leading-relaxed text-brand-ink-light sm:text-[13px]",
        children: n
      })]
    });
  }
  e.s(["MobileLinkRow", 0, function ({
    href: e,
    icon: i,
    label: s,
    detail: l,
    external: o = !1
  }) {
    let d = (0, t.jsxs)(t.Fragment, {
        children: [(0, t.jsx)("span", {
          className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-[#EAF0FF] text-brand-primary",
          children: (0, t.jsx)(i, {
            className: "h-[18px] w-[18px]",
            strokeWidth: 1.9,
            "aria-hidden": "true"
          })
        }), (0, t.jsxs)("span", {
          className: "min-w-0 flex-1",
          children: [(0, t.jsx)("span", {
            className: "block text-[14px] font-semibold text-brand-ink",
            children: s
          }), l ? (0, t.jsx)("span", {
            className: "mt-0.5 block text-[11px] leading-snug text-brand-ink-light",
            children: l
          }) : null]
        }), (0, t.jsx)(r.ChevronRight, {
          className: "h-5 w-5 shrink-0 text-brand-ink/25",
          strokeWidth: 2,
          "aria-hidden": "true"
        })]
      }),
      c = "flex min-h-[58px] w-full touch-manipulation items-center gap-3 px-3.5 py-2.5 text-left active:bg-[#F5F5F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary/40";
    return o ? (0, t.jsx)("a", {
      href: e,
      target: e.startsWith("http") ? "_blank" : void 0,
      rel: e.startsWith("http") ? "noopener noreferrer" : void 0,
      onClick: () => (0, n.haptic)("selection"),
      className: c,
      children: d
    }) : (0, t.jsx)(a.default, {
      href: e,
      onClick: () => (0, n.haptic)("selection"),
      className: c,
      children: d
    });
  }, "MobilePageHeader", 0, i, "MobileReadingPage", 0, function ({
    icon: e,
    kicker: a,
    title: r,
    description: n,
    updated: s,
    intro: l,
    sections: o
  }) {
    return (0, t.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, t.jsx)(i, {
        icon: e,
        kicker: a,
        title: r,
        description: n
      }), (0, t.jsxs)("main", {
        className: "mx-auto w-full max-w-[680px] px-4 sm:px-6",
        children: [(0, t.jsxs)("div", {
          className: "rounded-[14px] border border-brand-ink/8 bg-[#F5F5F7] px-3.5 py-3",
          children: [(0, t.jsx)("p", {
            className: "text-[10px] font-semibold text-brand-primary",
            children: "Last updated"
          }), (0, t.jsx)("p", {
            className: "mt-1 text-[12px] font-medium text-brand-ink",
            children: s
          })]
        }), (0, t.jsx)("p", {
          className: "mt-4 text-[13px] leading-[1.7] text-brand-ink-light sm:text-[14px]",
          children: l
        }), (0, t.jsx)("div", {
          className: "mt-5 divide-y divide-brand-ink/8 border-y border-brand-ink/8",
          children: o.map(e => (0, t.jsxs)("section", {
            className: "py-4",
            children: [(0, t.jsx)("h2", {
              className: "text-[15px] font-bold leading-snug text-brand-ink sm:text-[16px]",
              children: e.title
            }), (0, t.jsx)("div", {
              className: "mt-2 whitespace-pre-line text-[12px] leading-[1.7] text-brand-ink-light sm:text-[13px]",
              children: e.content.replace(/\*\*/g, "")
            })]
          }, e.title))
        })]
      })]
    });
  }]);
});

  Z.define(803929, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(584026),
    r = e.i(799552),
    o = e.i(116579);
  e.s(["default", 0, function () {
    return (0, t.jsx)(r.MobileReadingPage, {
      icon: a.ShieldCheck,
      kicker: "Legal",
      title: "Privacy",
      description: "How Zeta collects, uses and protects personal information.",
      updated: "April 2026",
      intro: "Zeta Greek Kitchen is committed to protecting your privacy. This policy explains how personal information is collected, used, disclosed and safeguarded across Zeta websites, apps and related services under the New Zealand Privacy Act 2020.",
      sections: o.PRIVACY_SECTIONS
    });
  }]);
});

  Z.define(806807, e => {
  "use strict";

  var a = e.i(843476),
    t = e.i(522016),
    r = e.i(271645),
    s = e.i(618566),
    n = e.i(867927),
    i = e.i(282625),
    l = e.i(972764),
    o = e.i(420865),
    d = e.i(328623),
    c = e.i(200416),
    u = e.i(763566),
    m = e.i(977617),
    p = e.i(343699),
    x = e.i(899907),
    h = e.i(528526),
    b = e.i(990746),
    f = e.i(652348),
    g = e.i(987664),
    y = e.i(238325),
    _ = e.i(309808),
    j = e.i(566450),
    N = e.i(376151);
  e.i(651164);
  var v = e.i(917848),
    w = e.i(273341),
    k = e.i(754675);
  function C(e) {
    return Number(e?.pointsRequired ?? e?.points_required ?? 0) || 0;
  }
  function S(e) {
    if (e && "object" == typeof e) return e;
    if ("string" == typeof e) try {
      return JSON.parse(e);
    } catch {}
    return {};
  }
  function P({
    title: e,
    href: r,
    action: s
  }) {
    return (0, a.jsxs)("div", {
      className: "flex items-end justify-between gap-3",
      children: [(0, a.jsx)("h2", {
        className: "text-[17px] font-bold leading-none tracking-[-0.02em]",
        children: e
      }), r && s ? (0, a.jsxs)(t.default, {
        href: r,
        onClick: () => (0, w.haptic)("selection"),
        className: "flex min-h-11 touch-manipulation items-center gap-0.5 px-1 text-[10px] font-bold text-brand-primary",
        children: [s, (0, a.jsx)(n.ChevronRight, {
          className: "h-3.5 w-3.5",
          strokeWidth: 2.4,
          "aria-hidden": "true"
        })]
      }) : null]
    });
  }
  e.s(["default", 0, function () {
    let e = (0, s.useRouter)(),
      I = (0, _.useToast)(),
      M = (0, h.useZetaAppSurface)(),
      {
        currentBranch: A,
        orderType: V,
        restaurantConfig: $,
        scheduleType: z,
        scheduledTime: O
      } = (0, f.useBranch)(),
      {
        user: T,
        customerData: E,
        openAccountDrawer: U
      } = (0, b.useAuth)(),
      {
        walletData: R,
        loyaltyProducts: L,
        isLoading: F,
        refreshLoyalty: D
      } = (0, y.useLoyalty)(),
      {
        addToCart: B,
        isSuggestionPopupVisible: G
      } = (0, g.useCart)(),
      [Z, q] = (0, r.useState)([]),
      [W, K] = (0, r.useState)(!0),
      [Y, H] = (0, r.useState)(""),
      [Q, J] = (0, r.useState)([]),
      [X, ee] = (0, r.useState)(!1),
      [ea, et] = (0, r.useState)(null),
      [er, es] = (0, r.useState)(null),
      [en, ei] = (0, r.useState)(""),
      el = (0, r.useRef)(!1),
      eo = String($?.timezone || $?.timeZone || A?.timezone || N.DEFAULT_TIME_ZONE),
      ed = (0, r.useMemo)(() => {
        if ("SCHEDULED" === z && O) try {
          let e = new Date((0, N.tzSlotToUTC)(O, eo));
          if (!Number.isNaN(e.getTime())) return e;
        } catch {}
        return (0, N.getTimeZoneNow)($?.serverTime, eo);
      }, [$?.serverTime, z, O, eo]);
    (0, r.useEffect)(() => {
      if (!A?.onlineKey) {
        q([]), K(!1);
        return;
      }
      let e = !1;
      return K(!0), H(""), (0, v.fetchOffers)(A.onlineKey, A.storeId, V || "PICKUP", M, eo).then(a => {
        e || (a?.status === "success" ? q(a.offers || []) : (q([]), H(a?.message || "Deals could not be loaded.")));
      }).catch(() => {
        e || (q([]), H("Deals could not be loaded."));
      }).finally(() => {
        e || K(!1);
      }), () => {
        e = !0;
      };
    }, [A?.onlineKey, A?.storeId, M, V, eo]), (0, r.useEffect)(() => {
      T && !el.current && (el.current = !0, D());
    }, [D, T]), (0, r.useEffect)(() => {
      if (!T) {
        J([]), ee(!1);
        return;
      }
      let e = !1;
      ee(!0);
      let a = Number($?.id || $?.hotelId || A?.storeId || 349);
      return (0, v.getCoupons)(a).then(a => {
        e || J(a?.data || []);
      }).catch(() => {
        e || J([]);
      }).finally(() => {
        e || ee(!1);
      }), () => {
        e = !0;
      };
    }, [A?.storeId, $?.hotelId, $?.id, T]);
    let ec = (0, r.useMemo)(() => Z.map(e => {
        let a = e?.rawData?.availability;
        return !a || Array.isArray(a) && 0 === a.length ? e : {
          ...e,
          isAvailable: (0, j.isEntityAvailable)(e.rawData, V || "PICKUP", ed, M, !1, !1, !1, eo)
        };
      }), [ed, M, Z, V, eo]),
      eu = (0, r.useMemo)(() => [...(L || [])].filter(e => 1 !== Number(e?.is_inactive ?? e?.inactive ?? 0)).sort((e, a) => C(e) - C(a)), [L]),
      em = (0, r.useMemo)(() => Q.filter(e => e?.is_exposed === void 0 || 1 === Number(e?.is_exposed)).filter(e => (0, j.isEntityAvailable)(e, V || "PICKUP", ed, M, !1, !1, !1, eo)), [Q, ed, M, V, eo]),
      ep = Number(R?.walletPoints ?? R?.wallet_points ?? 0) || 0,
      ex = E?.name || T?.displayName || "Zeta member",
      eh = String(R?.membershipTypeObj?.name || R?.membershipType || "Zeta Rewards"),
      eb = eu.filter(e => {
        let a = C(e);
        return a > 0 && ep >= a;
      }),
      ef = eu.find(e => C(e) > ep) || null,
      eg = C(ef),
      ey = eg > 0 ? Math.min(100, Math.round(ep / eg * 100)) : 100 * (eu.length > 0);
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsxs)("header", {
        className: "mx-auto w-full max-w-[800px] px-4 pb-3 pt-4 sm:px-6 sm:pb-4 sm:pt-6",
        children: [(0, a.jsxs)("div", {
          className: "flex items-center gap-2 text-brand-primary",
          children: [(0, a.jsx)(d.Sparkles, {
            className: "h-4 w-4",
            strokeWidth: 2.2,
            "aria-hidden": "true"
          }), (0, a.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.12em]",
            children: "Member benefits"
          })]
        }), (0, a.jsx)("h1", {
          className: "mt-1 text-[27px] font-extrabold leading-tight tracking-[-0.025em] sm:text-[30px]",
          children: "Zeta Zone"
        }), (0, a.jsx)("p", {
          className: "mt-1 max-w-[42rem] text-[12px] leading-relaxed text-brand-ink-light sm:text-[13px]",
          children: "Rewards progress, member coupons and live app deals."
        })]
      }), (0, a.jsxs)("main", {
        className: "mx-auto grid w-full max-w-[800px] gap-5 px-4 sm:px-6 md:grid-cols-2",
        children: [T ? (0, a.jsxs)("section", {
          className: "overflow-hidden rounded-[18px] border border-brand-primary/16 bg-[#EAF0FF] md:col-span-2",
          children: [(0, a.jsxs)("div", {
            className: "flex items-center gap-3 p-3.5",
            children: [(0, a.jsx)("span", {
              className: "grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-white text-[18px] font-extrabold text-brand-primary",
              children: ex.trim().charAt(0).toUpperCase() || "Z"
            }), (0, a.jsxs)("div", {
              className: "min-w-0 flex-1",
              children: [(0, a.jsx)("p", {
                className: "text-[9px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                children: "Welcome back"
              }), (0, a.jsx)("p", {
                className: "mt-0.5 truncate text-[15px] font-bold",
                children: ex
              }), (0, a.jsx)("p", {
                className: "mt-0.5 truncate text-[10px] text-brand-ink-light",
                children: eh
              })]
            }), (0, a.jsxs)("div", {
              className: "shrink-0 text-right",
              children: [(0, a.jsx)("p", {
                className: "text-[22px] font-extrabold leading-none tabular-nums",
                children: ep.toLocaleString()
              }), (0, a.jsx)("p", {
                className: "mt-1 text-[9px] text-brand-ink-light",
                children: "points"
              })]
            })]
          }), (0, a.jsx)("div", {
            className: "grid grid-cols-3 border-t border-brand-primary/12 bg-white/65",
            children: [{
              label: "Rewards",
              value: F ? "Loading" : eb.length ? `${eb.length} ready` : `${eu.length} live`,
              href: "/rewards",
              Icon: i.Gift
            }, {
              label: "Coupons",
              value: X ? "Loading" : `${em.length} active`,
              href: "/coupons",
              Icon: c.TicketPercent
            }, {
              label: "Wallet",
              value: "Card & activity",
              href: "/wallet",
              Icon: u.WalletCards
            }].map(({
              label: t,
              value: r,
              href: s,
              Icon: n
            }) => (0, a.jsxs)("button", {
              type: "button",
              onClick: () => {
                ((0, w.haptic)("selection"), !T) ? U() : e.push(s);
              },
              className: "flex min-h-[66px] touch-manipulation flex-col items-center justify-center border-r border-brand-primary/10 px-1.5 text-center last:border-r-0 active:bg-[#EAF0FF]",
              children: [(0, a.jsx)(n, {
                className: "h-[18px] w-[18px] text-brand-primary",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              }), (0, a.jsx)("span", {
                className: "mt-1 text-[10px] font-bold",
                children: t
              }), (0, a.jsx)("span", {
                className: "mt-0.5 max-w-full truncate text-[8px] text-brand-ink-light",
                children: r
              })]
            }, s))
          })]
        }) : (0, a.jsxs)("section", {
          className: "flex items-center gap-3 rounded-[18px] border border-brand-primary/16 bg-[#EAF0FF] p-3.5 md:col-span-2",
          children: [(0, a.jsx)("span", {
            className: "grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-white text-brand-primary",
            children: (0, a.jsx)(l.LockKeyhole, {
              className: "h-5 w-5",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            })
          }), (0, a.jsxs)("div", {
            className: "min-w-0 flex-1",
            children: [(0, a.jsx)("h2", {
              className: "text-[14px] font-bold",
              children: "Unlock your Zeta benefits"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[10px] leading-relaxed text-brand-ink-light",
              children: "Sign in to see your points, reward progress and account coupons."
            })]
          }), (0, a.jsx)("button", {
            type: "button",
            onClick: () => {
              (0, w.haptic)("selection"), U();
            },
            className: "min-h-11 shrink-0 touch-manipulation rounded-[12px] bg-brand-primary px-3 text-[11px] font-bold text-white",
            children: "Sign in"
          })]
        }), T ? (0, a.jsxs)("section", {
          children: [(0, a.jsx)(P, {
            title: "Rewards progress",
            href: "/rewards",
            action: "See rewards"
          }), (0, a.jsx)("div", {
            className: "mt-2.5 rounded-[16px] border border-brand-ink/8 bg-white p-3.5",
            children: F ? (0, a.jsx)("div", {
              className: "zeta-app-skeleton h-[62px] rounded-[13px]",
              role: "status",
              "aria-label": "Loading reward progress"
            }) : ef ? (0, a.jsxs)(a.Fragment, {
              children: [(0, a.jsxs)("div", {
                className: "flex items-start justify-between gap-3",
                children: [(0, a.jsxs)("div", {
                  className: "min-w-0",
                  children: [(0, a.jsx)("p", {
                    className: "text-[9px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                    children: "Next reward"
                  }), (0, a.jsx)("p", {
                    className: "mt-1 truncate text-[14px] font-bold",
                    children: ef.name || ef.label || "Zeta reward"
                  })]
                }), (0, a.jsxs)("span", {
                  className: "shrink-0 rounded-full bg-[#EAF0FF] px-2.5 py-1 text-[9px] font-bold text-brand-primary",
                  children: [Math.max(0, eg - ep).toLocaleString(), " to go"]
                })]
              }), (0, a.jsx)("div", {
                className: "mt-3 h-2 overflow-hidden rounded-full bg-[#F0F0F3]",
                children: (0, a.jsx)("span", {
                  className: "block h-full rounded-full bg-brand-primary",
                  style: {
                    width: `${ey}%`
                  }
                })
              })]
            }) : eu.length > 0 ? (0, a.jsxs)("div", {
              className: "flex items-center gap-3",
              children: [(0, a.jsx)("span", {
                className: "grid h-10 w-10 place-items-center rounded-[12px] bg-[#EAF0FF] text-brand-primary",
                children: (0, a.jsx)(i.Gift, {
                  className: "h-5 w-5",
                  strokeWidth: 1.9,
                  "aria-hidden": "true"
                })
              }), (0, a.jsxs)("div", {
                children: [(0, a.jsx)("p", {
                  className: "text-[13px] font-bold",
                  children: "Your balance reaches every live reward"
                }), (0, a.jsx)("p", {
                  className: "mt-1 text-[10px] text-brand-ink-light",
                  children: "Open Rewards to check store redemption rules."
                })]
              })]
            }) : (0, a.jsx)("p", {
              className: "text-[11px] leading-relaxed text-brand-ink-light",
              children: "No reward products are enabled for this store right now."
            })
          }), !F && eu.length > 0 ? (0, a.jsx)("div", {
            className: "mt-2 divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7]",
            children: eu.slice(0, 2).map((e, r) => {
              let s = C(e),
                n = Math.max(0, s - ep),
                l = e.name || e.label || "Zeta reward";
              return (0, a.jsxs)(t.default, {
                href: "/rewards",
                onClick: () => (0, w.haptic)("selection"),
                className: "flex min-h-[58px] items-center gap-3 px-3 py-2",
                children: [(0, a.jsx)("span", {
                  className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
                  children: (0, a.jsx)(i.Gift, {
                    className: "h-[18px] w-[18px]",
                    strokeWidth: 1.9,
                    "aria-hidden": "true"
                  })
                }), (0, a.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, a.jsx)("span", {
                    className: "block truncate text-[12px] font-semibold",
                    children: l
                  }), (0, a.jsxs)("span", {
                    className: "mt-0.5 block text-[9px] text-brand-ink-light",
                    children: [s.toLocaleString(), " points"]
                  })]
                }), (0, a.jsx)("span", {
                  className: `shrink-0 rounded-full px-2 py-1 text-[8px] font-bold ${0 === n ? "bg-brand-primary text-white" : "bg-white text-brand-ink-light"}`,
                  children: 0 === n ? "Ready" : `${n.toLocaleString()} to go`
                })]
              }, e.productItemId || e.itemId || `${l}-${r}`);
            })
          }) : null]
        }) : null, T ? (0, a.jsxs)("section", {
          children: [(0, a.jsx)(P, {
            title: "Member coupons",
            href: "/coupons",
            action: "See all"
          }), (0, a.jsx)("div", {
            className: "mt-2.5 overflow-hidden rounded-[16px] border border-brand-ink/8",
            children: X ? (0, a.jsxs)("div", {
              className: "space-y-2 p-3",
              role: "status",
              "aria-label": "Loading member coupons",
              children: [(0, a.jsx)("div", {
                className: "zeta-app-skeleton h-14 rounded-[12px]"
              }), (0, a.jsx)("div", {
                className: "zeta-app-skeleton h-14 rounded-[12px]"
              })]
            }) : 0 === em.length ? (0, a.jsxs)("div", {
              className: "p-4 text-center",
              children: [(0, a.jsx)("p", {
                className: "text-[12px] font-semibold",
                children: "No active member coupons right now."
              }), (0, a.jsx)("p", {
                className: "mt-1 text-[9px] text-brand-ink-light",
                children: "New coupons will appear automatically."
              })]
            }) : (0, a.jsx)("div", {
              className: "divide-y divide-brand-ink/8",
              children: em.slice(0, 2).map((e, r) => {
                let s, i, l, o;
                return (0, a.jsxs)(t.default, {
                  href: "/coupons",
                  onClick: () => (0, w.haptic)("selection"),
                  className: "flex min-h-[64px] items-center gap-3 p-2.5",
                  children: [(0, a.jsx)("span", {
                    className: "grid h-[44px] w-[58px] shrink-0 place-items-center rounded-[11px] bg-brand-primary px-1 text-center text-[9px] font-bold leading-tight text-white",
                    children: (s = S(e?.rules), i = S(e?.result), l = String(s.type || i.type || "").toUpperCase(), o = Number(i.DISCOUNT || 0), "FREE_DELIVERY" === l ? "Free delivery" : "BUY_ONE_GET_ONE_FREE" === l ? "2 for 1" : "BUY_N_GET_M_FREE" === l ? "Free item" : ("CART_DISCOUNT" === l || "ITEM_DISCOUNT" === l) && o > 0 ? `${o}% off` : e?.discount_label || "Member deal")
                  }), (0, a.jsxs)("span", {
                    className: "min-w-0 flex-1",
                    children: [(0, a.jsx)("span", {
                      className: "block truncate text-[12px] font-semibold",
                      children: e.name || "Zeta coupon"
                    }), (0, a.jsx)("span", {
                      className: "mt-1 block truncate text-[9px] text-brand-ink-light",
                      children: e.description || "View conditions before checkout"
                    })]
                  }), (0, a.jsx)(n.ChevronRight, {
                    className: "h-4 w-4 shrink-0 text-brand-primary",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  })]
                }, e.id || e.coupon_code || e.code || r);
              })
            })
          })]
        }) : null, (0, a.jsxs)("section", {
          className: "md:col-span-2",
          children: [(0, a.jsx)(P, {
            title: "App deals"
          }), (0, a.jsxs)("div", {
            className: "mt-1 flex items-center gap-2 text-[9px] text-brand-ink-light",
            children: [(0, a.jsx)(o.MapPin, {
              className: "h-3.5 w-3.5 text-brand-primary",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), A?.name || "Choose a Zeta store", ec.length > 0 ? (0, a.jsxs)("span", {
              className: "ml-auto rounded-full bg-[#EAF0FF] px-2 py-1 font-bold text-brand-primary",
              children: [ec.length, " live"]
            }) : null]
          }), W ? (0, a.jsx)("div", {
            className: "mt-2 grid gap-2 md:grid-cols-2",
            "aria-label": "Loading Zeta Zone deals",
            children: [0, 1, 2].map(e => (0, a.jsx)("div", {
              className: "zeta-app-skeleton h-[96px] rounded-[16px]"
            }, e))
          }) : A ? Y ? (0, a.jsxs)("div", {
            className: "mt-2 rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] p-4 text-center",
            children: [(0, a.jsx)("p", {
              className: "text-[13px] font-bold",
              children: Y
            }), (0, a.jsx)(t.default, {
              href: "/menu",
              className: "mt-2 inline-flex min-h-11 touch-manipulation items-center text-[11px] font-bold text-brand-primary",
              children: "Browse menu"
            })]
          }) : 0 === ec.length ? (0, a.jsxs)("div", {
            className: "mt-2 rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] p-4 text-center",
            children: [(0, a.jsx)("p", {
              className: "text-[13px] font-bold",
              children: "No live app deals right now"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[10px] text-brand-ink-light",
              children: "New Zeta Zone offers will appear here automatically."
            }), (0, a.jsx)(t.default, {
              href: "/menu",
              className: "mt-3 inline-flex min-h-11 items-center justify-center rounded-[13px] bg-brand-primary px-5 text-[12px] font-bold text-white",
              children: "Browse menu"
            })]
          }) : (0, a.jsx)("div", {
            className: "mt-2 grid gap-2 md:grid-cols-2",
            children: ec.map(t => {
              let r,
                s = function (e) {
                  if ("string" != typeof e) return !1;
                  let a = e.trim().toLowerCase();
                  return !!(a && "null" !== a && "undefined" !== a);
                }(t.image),
                n = Number.isFinite(r = Number(t?.offerPrice)) && r >= 0 ? `$${r.toFixed(2)}` : null;
              return (0, a.jsxs)("article", {
                className: `flex min-h-[96px] gap-3 overflow-hidden rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] p-2 ${!1 === t.isAvailable ? "opacity-60" : ""}`,
                children: [s ? (0, a.jsx)("span", {
                  className: "relative h-20 w-20 shrink-0 overflow-hidden rounded-[13px] bg-[#ECECEF]",
                  children: (0, a.jsx)(m.default, {
                    src: t.image,
                    alt: "",
                    fill: !0,
                    sizes: "80px",
                    className: "object-cover",
                    context: "ZetaZone.offer",
                    meta: {
                      id: t.id
                    }
                  })
                }) : (0, a.jsx)("span", {
                  className: "grid h-11 w-11 shrink-0 place-items-center rounded-[12px] bg-white text-brand-primary",
                  children: (0, a.jsx)(d.Sparkles, {
                    className: "h-5 w-5",
                    strokeWidth: 1.9,
                    "aria-hidden": "true"
                  })
                }), (0, a.jsxs)("span", {
                  className: "flex min-w-0 flex-1 flex-col justify-center py-0.5",
                  children: [(0, a.jsxs)("span", {
                    className: "flex items-start justify-between gap-2",
                    children: [(0, a.jsx)("span", {
                      className: "line-clamp-2 text-[13px] font-bold leading-tight",
                      children: (0, k.displayLabel)(t.title || "Zeta deal")
                    }), n ? (0, a.jsx)("span", {
                      className: "shrink-0 text-[12px] font-extrabold text-brand-primary",
                      children: n
                    }) : null]
                  }), t.description ? (0, a.jsx)("span", {
                    className: "mt-1 line-clamp-2 text-[9px] leading-snug text-brand-ink-light",
                    children: (0, k.displayLabel)(t.description)
                  }) : null, (0, a.jsx)("button", {
                    type: "button",
                    onClick: () => function (a) {
                      if (a?.isAvailable === !1) return;
                      if ((0, w.haptic)("light"), a?.type === "coupon_product") {
                        ei(String(a.couponCode || "")), et(a.rawData);
                        return;
                      }
                      let t = a?.ctaAction;
                      if (!t) return void e.push("/menu");
                      if ("apply_coupon" === t.type) return void e.push(`/checkout?coupon=${encodeURIComponent(t.value)}`);
                      if ("add_to_cart" !== t.type) return void e.push("/menu");
                      if ("combo" === a.type) return void es(a.rawData);
                      let r = a.rawData,
                        s = [r?.group, r?.groups, r?.groupstoproduct].flatMap(e => e || []);
                      (r?.sizes || []).length > 1 || s.some(e => e.is_mandatory || Number(e.min_modifier) > 0) ? et(r) : (B({
                        ...r,
                        itemId: r.itemId,
                        label: r.label,
                        sale_price: r.sale_price,
                        size: r.sizes?.[0]?.id || "",
                        sizes: r.sizes || [],
                        count: 1,
                        modifiers: [],
                        is_combo: !1
                      }), I.success("Added to cart", {
                        title: (0, k.displayLabel)(r.label || a.title)
                      }));
                    }(t),
                    disabled: !1 === t.isAvailable,
                    className: "mt-2 min-h-11 touch-manipulation self-start rounded-[12px] bg-brand-primary px-3 text-[10px] font-bold text-white disabled:bg-brand-ink/20",
                    children: t?.isAvailable === !1 ? "Not available" : t?.type === "coupon_product" ? "Choose item" : t?.ctaAction?.type === "apply_coupon" ? "Use offer" : t?.ctaAction?.type === "add_to_cart" ? "Add deal" : "Browse menu"
                  })]
                })]
              }, t.id);
            })
          }) : (0, a.jsxs)("div", {
            className: "mt-2 rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] p-4 text-center",
            children: [(0, a.jsx)("p", {
              className: "text-[13px] font-bold",
              children: "Choose a store to see local deals"
            }), (0, a.jsx)(t.default, {
              href: "/stores",
              className: "mt-3 inline-flex min-h-11 items-center justify-center rounded-[13px] bg-brand-primary px-5 text-[12px] font-bold text-white",
              children: "Find a store"
            })]
          })]
        })]
      }), (0, a.jsx)(p.default, {
        item: ea,
        onClose: () => {
          et(null), ei("");
        },
        onSuccess: () => {
          if (en) {
            ei(""), e.push(`/checkout?coupon=${encodeURIComponent(en)}`);
            return;
          }
          G() || I.success("Added to cart", {
            title: (0, k.displayLabel)(ea?.label || ea?.name)
          });
        }
      }), (0, a.jsx)(x.default, {
        combo: er,
        onClose: () => es(null),
        onSuccess: () => {
          I.success("Added to cart", {
            title: (0, k.displayLabel)(er?.combo_name)
          });
        }
      })]
    });
  }]);
});

  Z.define(807420, e => {
  "use strict";

  var a = e.i(843476),
    t = e.i(522016),
    s = e.i(753004),
    i = e.i(867927);
  let r = (0, e.i(456420).default)("heart-handshake", [["path", {
    d: "M19.414 14.414C21 12.828 22 11.5 22 9.5a5.5 5.5 0 0 0-9.591-3.676.6.6 0 0 1-.818.001A5.5 5.5 0 0 0 2 9.5c0 2.3 1.5 4 3 5.5l5.535 5.362a2 2 0 0 0 2.879.052 2.12 2.12 0 0 0-.004-3 2.124 2.124 0 1 0 3-3 2.124 2.124 0 0 0 3.004 0 2 2 0 0 0 0-2.828l-1.881-1.882a2.41 2.41 0 0 0-3.409 0l-1.71 1.71a2 2 0 0 1-2.828 0 2 2 0 0 1 0-2.828l2.823-2.762",
    key: "17lmqv"
  }]]);
  var n = e.i(75183),
    l = e.i(956094),
    o = e.i(977617),
    d = e.i(799552),
    c = e.i(273341);
  let m = [{
      title: "A Greek kitchen made for Auckland",
      body: "Zeta brings Mediterranean flavours into a fast, fresh format: souva wraps, burgers, salad bowls, sharing plates, dips, pickles and warm pita made to order."
    }, {
      title: "A generous table feel",
      body: "The menu moves through thick-cut chips, hummus, tzatziki, zesty salads, slow rotisserie-cooked meats, falafel, seafood and bright lemon-dressed flavours."
    }, {
      title: "Growing from Auckland",
      body: "Zeta is growing across Auckland and into Australia while keeping the promise direct: fresh Greek food, served with energy."
    }],
    h = [{
      Icon: l.Utensils,
      title: "Fresh Mediterranean flavour",
      body: "Warm pita, house dips, olives, pomegranate, lemon dressing, salads and slow-cooked proteins."
    }, {
      Icon: r,
      title: "Fully halal",
      body: "Zeta states that its food is fully halal across its wraps, plates, salads, seafood, falafel and proteins."
    }, {
      Icon: n.Store,
      title: "Catering and events",
      body: "Functions, office lunches, school events, corporate catering and family gatherings."
    }];
  e.s(["default", 0, function () {
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsx)(d.MobilePageHeader, {
        icon: s.BookOpenText,
        kicker: "Zeta Greek Kitchen",
        title: "About Zeta",
        description: "Fresh Greek food, generous flavours and warm hospitality."
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-5 px-4 sm:px-6",
        children: [(0, a.jsxs)("section", {
          className: "relative h-[196px] overflow-hidden rounded-[20px] bg-[#EBEBEF] sm:h-[240px]",
          children: [(0, a.jsx)(o.default, {
            src: "/brands/zeta/catering-platter.webp",
            alt: "Zeta catering platter",
            fill: !0,
            priority: !0,
            sizes: "(max-width: 520px) calc(100vw - 32px), 712px",
            className: "object-cover",
            context: "MobileAbout.hero"
          }), (0, a.jsx)("div", {
            className: "absolute inset-0 bg-gradient-to-t from-black/62 via-black/10 to-transparent"
          }), (0, a.jsxs)("div", {
            className: "absolute bottom-0 left-0 right-0 p-4 text-white",
            children: [(0, a.jsx)("p", {
              className: "text-[10px] font-semibold uppercase tracking-[0.12em] text-white/75",
              children: "Made fresh"
            }), (0, a.jsx)("h2", {
              className: "mt-1 text-[21px] font-bold leading-tight",
              children: "Eat like a Greek"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[11px] text-white/80",
              children: "Wraps, plates, bowls, dips and mezze."
            })]
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[16px] font-bold",
            children: "Our story"
          }), (0, a.jsx)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8 md:grid md:grid-cols-3 md:divide-x md:divide-y-0",
            children: m.map(e => (0, a.jsxs)("article", {
              className: "px-3.5 py-3.5",
              children: [(0, a.jsx)("h3", {
                className: "text-[14px] font-semibold leading-snug",
                children: e.title
              }), (0, a.jsx)("p", {
                className: "mt-1.5 text-[11px] leading-relaxed text-brand-ink-light",
                children: e.body
              })]
            }, e.title))
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("h2", {
            className: "mb-2 text-[16px] font-bold",
            children: "What Zeta stands for"
          }), (0, a.jsx)("div", {
            className: "grid gap-2 md:grid-cols-3",
            children: h.map(({
              Icon: e,
              title: t,
              body: s
            }) => (0, a.jsxs)("article", {
              className: "flex gap-3 rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
              children: [(0, a.jsx)("span", {
                className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
                children: (0, a.jsx)(e, {
                  className: "h-[18px] w-[18px]",
                  strokeWidth: 1.9,
                  "aria-hidden": "true"
                })
              }), (0, a.jsxs)("div", {
                children: [(0, a.jsx)("h3", {
                  className: "text-[13px] font-semibold",
                  children: t
                }), (0, a.jsx)("p", {
                  className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                  children: s
                })]
              })]
            }, t))
          })]
        }), (0, a.jsxs)("section", {
          className: "grid grid-cols-2 gap-2",
          children: [(0, a.jsxs)(t.default, {
            href: "/menu",
            onClick: () => (0, c.haptic)("selection"),
            className: "flex min-h-[52px] touch-manipulation items-center justify-between rounded-[14px] bg-brand-primary px-3.5 text-[13px] font-semibold text-white",
            children: ["Explore menu", (0, a.jsx)(i.ChevronRight, {
              className: "h-4 w-4",
              strokeWidth: 2.2,
              "aria-hidden": "true"
            })]
          }), (0, a.jsxs)(t.default, {
            href: "/catering",
            onClick: () => (0, c.haptic)("selection"),
            className: "flex min-h-[52px] touch-manipulation items-center justify-between rounded-[14px] bg-[#F5F5F7] px-3.5 text-[13px] font-semibold text-brand-ink",
            children: ["Catering", (0, a.jsx)(i.ChevronRight, {
              className: "h-4 w-4 text-brand-primary",
              strokeWidth: 2.2,
              "aria-hidden": "true"
            })]
          })]
        })]
      })]
    });
  }], 807420);
});

  Z.define(810818, e => {
  "use strict";

  let t = (0, e.i(456420).default)("info", [["circle", {
    cx: "12",
    cy: "12",
    r: "10",
    key: "1mglay"
  }], ["path", {
    d: "M12 16v-4",
    key: "1dtifu"
  }], ["path", {
    d: "M12 8h.01",
    key: "e9boi3"
  }]]);
  e.s(["Info", 0, t], 810818);
});

  Z.define(819536, e => {
  "use strict";

  var t = e.i(271645);
  e.s(["useDialogFocus", 0, function ({
    open: e,
    onClose: i,
    focusKey: r
  }) {
    let a = (0, t.useRef)(null),
      s = (0, t.useRef)(i);
    return (0, t.useEffect)(() => {
      s.current = i;
    }, [i]), (0, t.useEffect)(() => {
      let t = a.current;
      if (!e || !t) return;
      let i = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      function r(e) {
        if (e.defaultPrevented || !t) return;
        let i = document.activeElement;
        if (!(i instanceof Element && i.closest('[role="dialog"]') !== t && t.contains(i))) {
          if ("Escape" === e.key) e.preventDefault(), e.stopPropagation(), s.current();else if ("Tab" === e.key) {
            let r = Array.from(t.querySelectorAll('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]')).filter(e => e.tabIndex >= 0 && !e.matches(":disabled") && !e.closest('[hidden], [inert], [aria-hidden="true"]') && e.getClientRects().length > 0 && "hidden" !== getComputedStyle(e).visibility),
              a = r[0],
              s = r[r.length - 1];
            a && t.contains(i) && i !== t && (!e.shiftKey || i !== a) && (e.shiftKey || i !== s) || (e.preventDefault(), (e.shiftKey ? s || t : a || t).focus());
          }
        }
      }
      return document.addEventListener("keydown", r), () => {
        document.removeEventListener("keydown", r), i?.isConnected && !i.closest("[inert]") && i.focus({
          preventScroll: !0
        });
      };
    }, [e]), (0, t.useEffect)(() => {
      if (!e) return;
      let t = requestAnimationFrame(() => a.current?.focus({
        preventScroll: !0
      }));
      return () => cancelAnimationFrame(t);
    }, [e, r]), a;
  }]);
});

  Z.define(844644, e => {
  "use strict";

  e.s(["normalizeZetaRateableItems", 0, function (e) {
    let t = new Map();
    return (Array.isArray(e) ? e : !e || "object" != typeof e ? [] : [e.lineItems, e.orderData, e.items, e.receiptItems, e.ordersDetail?.nonComboOrders].flatMap(e => Array.isArray(e) ? e : [])).forEach(e => {
      let a = Number(e?.productItemId ?? e?.product_id ?? e?.itemId ?? e?.productId ?? e?.id);
      !Number.isInteger(a) || a <= 0 || t.has(a) || t.set(a, {
        productItemId: a,
        productName: String(e?.productName ?? e?.product_name ?? e?.productname ?? e?.label ?? e?.name ?? "Zeta item")
      });
    }), Array.from(t.values());
  }]);
});

  Z.define(849862, e => {
  "use strict";

  let t = (0, e.i(456420).default)("map", [["path", {
    d: "M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z",
    key: "169xi5"
  }], ["path", {
    d: "M15 5.764v15",
    key: "1pn4in"
  }], ["path", {
    d: "M9 3.236v15",
    key: "1uimfh"
  }]]);
  e.s(["Map", 0, t], 849862);
});

  Z.define(853138, e => {
  "use strict";

  var t = e.i(755566);
  e.s(["AlertTriangle", () => t.default]);
});

  Z.define(856423, e => {
  "use strict";

  let t = (0, e.i(456420).default)("book-open", [["path", {
    d: "M12 7v14",
    key: "1akyts"
  }], ["path", {
    d: "M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z",
    key: "ruj8y"
  }]]);
  e.s(["BookOpen", 0, t], 856423);
});

  Z.define(859871, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(522016),
    s = e.i(271645),
    l = e.i(618566),
    i = e.i(88653),
    r = e.i(846932);
  let n = (0, e.i(456420).default)("award", [["path", {
    d: "m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526",
    key: "1yiouv"
  }], ["circle", {
    cx: "12",
    cy: "8",
    r: "6",
    key: "1vp47v"
  }]]);
  var o = e.i(867927),
    c = e.i(870387),
    d = e.i(282625),
    x = e.i(420865),
    p = e.i(77071),
    m = e.i(256102),
    h = e.i(741120),
    u = e.i(263448),
    b = e.i(75183),
    f = e.i(126486),
    g = e.i(977617),
    j = e.i(394556),
    y = e.i(700240);
  let w = [{
    key: "instagram"
  }, {
    key: "facebook"
  }, {
    key: "tiktok"
  }];
  function N() {
    let e = j.default.socials || {},
      a = w.filter(t => !!e[t.key]);
    return 0 === a.length ? null : (0, t.jsxs)("section", {
      className: "px-4 sm:px-6 lg:px-8",
      children: [(0, t.jsx)("h2", {
        className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
        children: "Follow Zeta"
      }), (0, t.jsx)("div", {
        className: "grid grid-cols-3 gap-2",
        children: a.map(({
          key: a
        }) => {
          let s = (0, y.getZetaSocialMeta)(a);
          return (0, t.jsxs)(y.default, {
            network: a,
            href: e[a],
            ariaLabel: `Open Zeta on ${s.label}`,
            className: "flex min-h-[74px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-brand-ink/8 bg-white transition-transform active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
            children: [(0, t.jsx)(y.ZetaSocialMark, {
              network: a,
              className: `h-[22px] w-[22px] ${s.tint}`
            }), (0, t.jsx)("span", {
              className: "text-[12px] font-semibold text-brand-ink",
              children: s.label
            })]
          }, a);
        })
      })]
    });
  }
  var k = e.i(154484),
    v = e.i(468179),
    C = e.i(134461),
    F = e.i(652348),
    $ = e.i(990746),
    A = e.i(238325),
    E = e.i(987664),
    _ = e.i(309808),
    L = e.i(528526),
    R = e.i(675378);
  e.i(651164);
  var S = e.i(917848),
    M = e.i(84122),
    I = e.i(401993),
    z = e.i(273341),
    P = e.i(754675);
  let T = ["appOnly", "isAppOnly", "appExclusive", "app_exclusive", "app_only", "is_app_only", "mobileAppOnly", "mobile_app_only", "onlyForApp", "only_for_app"];
  function O(e) {
    return [e, e?.rawData, e?.rules, e?.result].filter(Boolean).some(e => T.some(t => {
      var a;
      return !0 === (a = e[t]) || 1 === a || "string" == typeof a && ["1", "true", "yes", "app", "mobile_app"].includes(a.toLowerCase());
    }));
  }
  function Z(e) {
    if ("string" != typeof e) return !1;
    let t = e.trim().toLowerCase();
    return !!(t && "null" !== t && "undefined" !== t);
  }
  function B({
    title: e,
    href: s,
    action: l = "See all"
  }) {
    return (0, t.jsxs)("div", {
      className: "mb-2 flex items-center justify-between px-4 sm:px-6 lg:px-8",
      children: [(0, t.jsx)("h2", {
        className: "text-[17px] font-bold leading-tight text-brand-ink sm:text-[19px]",
        children: e
      }), s ? (0, t.jsxs)(a.default, {
        href: s,
        onClick: () => (0, z.haptic)("selection"),
        className: "flex min-h-11 items-center gap-0.5 rounded-xl px-1 text-[12px] font-semibold text-brand-primary active:opacity-60",
        children: [l, (0, t.jsx)(o.ChevronRight, {
          className: "h-3.5 w-3.5",
          "aria-hidden": "true"
        })]
      }) : null]
    });
  }
  e.s(["default", 0, function () {
    let e,
      j,
      y = (0, l.useRouter)(),
      w = (0, _.useToast)(),
      {
        branches: T,
        currentBranch: U,
        orderType: W,
        branchStatuses: H,
        allConfigs: V,
        restaurantConfig: D
      } = (0, F.useBranch)(),
      {
        user: G,
        customerData: K,
        openAccountDrawer: Y
      } = (0, $.useAuth)(),
      {
        walletData: q,
        loyaltyProducts: J
      } = (0, A.useLoyalty)(),
      {
        addToCart: Q,
        clearCart: X,
        openCustomizer: ee,
        setIsCartOpen: et,
        cartCount: ea,
        cartTotal: es
      } = (0, E.useCart)(),
      el = (0, L.useZetaAppSurface)(),
      [ei, er] = (0, s.useState)(null),
      [en, eo] = (0, s.useState)(null),
      [ec, ed] = (0, s.useState)(null),
      [ex, ep] = (0, s.useState)(!1),
      [em, eh] = (0, s.useState)(0),
      [eu, eb] = (0, s.useState)(null),
      [ef, eg] = (0, s.useState)([]),
      ej = (0, s.useRef)(null),
      ey = (0, s.useRef)(new Map());
    (0, s.useEffect)(() => {
      let e = () => eg((0, k.getFavoriteProducts)().slice(0, 8));
      return e(), window.addEventListener(k.ZETA_FAVORITES_CHANGED_EVENT, e), () => window.removeEventListener(k.ZETA_FAVORITES_CHANGED_EVENT, e);
    }, []);
    let ew = U?.onlineKey,
      eN = U?.storeId,
      ek = "DELIVERY" === W ? "DELIVERY" : "PICKUP",
      ev = D?.timezone || D?.timeZone || U?.timezone || "Pacific/Auckland";
    (0, s.useEffect)(() => {
      if (!ew) {
        ey.current = new Map();
        return;
      }
      let e = !1;
      return (0, S.fetchMenu)(ew, {
        timeZone: ev
      }).then(t => {
        if (e) return;
        eb((t?.categories || []).filter(e => (e.items || []).length > 0).map(e => ({
          id: String(e.categoryId),
          label: e.label,
          count: (e.items || []).length,
          image: e.imageUrl || e.imageURL || ""
        })));
        let a = new Map();
        (t?.categories || []).forEach(e => {
          (e.items || []).forEach(t => {
            let s = {
              ...t,
              _categoryCtx: {
                label: e.label,
                availability: e.availability,
                catAvailability: e.catAvailability
              }
            };
            null != t.itemId && a.set(String(t.itemId), s), null != t.product_id && a.set(String(t.product_id), s);
          });
        }), ey.current = a;
      }).catch(() => {
        e || (ey.current = new Map(), eb([]));
      }), () => {
        e = !0;
      };
    }, [ew, ev]), (0, s.useEffect)(() => {
      if (!ew) return void er([]);
      let e = !1;
      return (0, S.fetchOffers)(ew, eN, ek, el, ev).then(t => {
        e || er(t?.status === "success" ? (t.offers || []).slice(0, 8) : []);
      }).catch(() => {
        e || er([]);
      }), () => {
        e = !0;
      };
    }, [ek, el, ew, eN, ev]), (0, s.useEffect)(() => {
      if (!ew) return void eo([]);
      let e = !1;
      return (0, S.getPopularDishes)(ew).then(t => {
        e || eo(t?.success ? (t.data || []).slice(0, 8) : []);
      }).catch(() => {
        e || eo([]);
      }), () => {
        e = !0;
      };
    }, [ew]), (0, s.useEffect)(() => {
      if (!G?.uid || !ew) return void ed(null);
      let e = !1;
      return (0, S.getOrderHistory)(G.uid, ew, 1).then(t => {
        e || ed((Array.isArray(t?.data?.data) ? t.data.data : [])[0] || null);
      }).catch(() => {
        e || ed(null);
      }), () => {
        e = !0;
      };
    }, [ew, G?.uid]);
    let eC = (0, s.useMemo)(() => (ei || []).find(e => Z(e?.image)), [ei]),
      eF = (0, s.useMemo)(() => T.find(e => "coming-soon" === e.status), [T]),
      e$ = (0, s.useMemo)(() => {
        let e = [];
        return eC && e.push({
          key: `offer-${eC.id}`,
          eyebrow: eC.badge && "null" !== eC.badge ? eC.badge : "Limited-time offer",
          title: (0, P.displayLabel)(eC.title || "A little something extra"),
          subtitle: (0, P.displayLabel)(eC.description || "Available from your selected Zeta store."),
          href: "/offers",
          image: eC.image
        }), eF && e.push({
          key: `store-${eF.id}`,
          eyebrow: "New store opening",
          title: `We’re coming to ${eF.name}`,
          subtitle: "Fresh Greek favourites are on the way.",
          href: "/stores",
          image: window.OO.appKit.ZetaHero().image || "/brands/zeta/hero-gyros.webp"
        }), e.length || e.push({
          key: "zeta-favourites",
          eyebrow: window.OO.appKit.ZetaHero().eyebrow || "Made fresh",
          title: window.OO.appKit.ZetaHero().title || "Your Greek favourites, ready when you are",
          subtitle: window.OO.appKit.ZetaHero().body || "Souvlaki, gyros, bowls and more.",
          href: "/menu",
          image: window.OO.appKit.ZetaHero().image || "/brands/zeta/hero-gyros.webp"
        }), e;
      }, [eF, eC]),
      eA = Number(q?.walletPoints ?? q?.wallet_points ?? 0) || 0,
      eE = (0, R.getDisplayWalletRatio)(q, el, ek),
      e_ = eE?.ratioPoints ?? 0,
      eL = e_ > 0 ? eA / e_ : 0,
      eR = (J || [])[0],
      eS = eR?.name || eR?.label || "reward",
      eM = Number(eR?.pointsRequired ?? eR?.points_required) || 0,
      eI = eM > 0 ? Math.min(100, Math.round(eA / eM * 100)) : 0,
      ez = eL > 0 ? `$${eL.toFixed(2)} reward value` : eM > eA ? `${eM - eA} pts to a ${eS}` : eA > 0 ? "A reward is ready to redeem" : "Earn points on every order",
      eP = K?.name || G?.displayName || "Zeta member",
      eT = G ? String(q?.customerCode || G.uid) : "",
      eO = (ei || []).filter(O),
      eZ = (ei || []).filter(e => !O(e) && e?.id !== eC?.id).slice(0, 3),
      eB = function (e, t) {
        if (e?.status === "coming-soon") return "Coming soon";
        let a = e?.onlineKey ? t?.[e.onlineKey] : null;
        return a ? a.isPaused ? a.pauseReason || "Temporarily paused" : a.isOpen ? a.isPreorder ? "Open · Pre-order available" : "Open now" : a.isPreorder ? "Pre-order available" : "Closed now" : "Checking hours";
      }(U, H),
      eU = ew && (V?.[ew]?.shop_image || D?.shop_image) || "";
    function eW(e) {
      let t = "combo" === e.itemType ? "combo" : "item";
      y.push(`/menu?${t}=${(0, I.slugify)(e.name)}&id=${e.productItemId}`);
    }
    function eH(e) {
      if ((0, z.haptic)("light"), "combo" === e.itemType) return void eW(e);
      let t = ey.current.get(String(e.productItemId));
      if (!t) return void eW(e);
      if (1 === Number(t.is_inactive)) return void w.info("That item is sold out right now.");
      let a = Array.isArray(t.sizes) ? t.sizes : [],
        s = t.groups || t.group || [];
      a.length > 1 || Array.isArray(s) && s.length > 0 ? ee(t) : (Q({
        ...t,
        itemId: t.itemId ?? t.product_id,
        size: 1 === a.length ? String(a[0].id) : "",
        sizes: a,
        count: 1,
        modifiers: [],
        is_combo: !1
      }), w.success(`${(0, P.displayLabel)(t.label || t.name || "Item")} added to cart.`));
    }
    async function eV() {
      if (ec && ew && !ex) {
        (0, z.haptic)("medium"), ep(!0);
        try {
          let e = await (0, S.fetchMenu)(ew, {
              timeZone: ev
            }),
            t = [],
            a = (0, M.reorderItems)(ec, e, (e, a) => {
              t.push({
                item: e,
                options: a
              });
            });
          a.added > 0 ? (X(), t.forEach(({
            item: e,
            options: t
          }) => Q(e, t)), et(!0), w.success(`${a.added} item${1 === a.added ? "" : "s"} added to your cart.`, {
            title: "Last order rebuilt"
          }), a.skipped.length > 0 && w.info(`${a.skipped.length} unavailable item${1 === a.skipped.length ? " was" : "s were"} skipped.`)) : w.warning("None of the previous items are available right now.", {
            title: "Could not reorder"
          });
        } catch {
          w.error("Could not rebuild your last order. Please try again.", {
            title: "Reorder failed"
          });
        } finally {
          ep(!1);
        }
      }
    }
    return (0, t.jsxs)(t.Fragment, {
      children: [(0, t.jsx)(v.default, {}), (0, t.jsx)(C.default, {}), (0, t.jsxs)("main", {
        className: "mx-auto min-h-[70vh] w-full max-w-5xl bg-white pb-5 text-brand-ink",
        style: {
          paddingBottom: ea > 0 ? 112 : void 0
        },
        children: [(0, t.jsxs)("section", {
          className: "px-4 pt-3 sm:px-6 sm:pt-5 lg:px-8",
          children: [(0, t.jsx)("div", {
            ref: ej,
            onScroll: e => {
              let t = e.currentTarget;
              t.clientWidth > 0 && eh(Math.round(t.scrollLeft / t.clientWidth));
            },
            className: "flex snap-x snap-proximity overscroll-x-contain overflow-x-auto rounded-[22px] scroll-smooth hide-scrollbar sm:rounded-[24px]",
            children: e$.map(e => (0, t.jsxs)(a.default, {
              href: e.href,
              onClick: () => (0, z.haptic)("light"),
              className: "relative h-[140px] min-w-full snap-start overflow-hidden rounded-[22px] bg-brand-ink sm:h-[196px] sm:rounded-[24px]",
              children: [(0, t.jsx)(g.default, {
                src: e.image,
                alt: "",
                fill: !0,
                priority: e.key === e$[0]?.key,
                sizes: "(max-width: 639px) 100vw, (max-width: 1023px) 90vw, 960px",
                className: "object-cover",
                context: "MobileAppHome.hero",
                meta: {
                  key: e.key
                }
              }), (0, t.jsx)("span", {
                className: "absolute inset-0 bg-gradient-to-r from-black/80 via-black/48 to-black/5"
              }), (0, t.jsxs)("span", {
                className: "absolute inset-y-0 left-0 flex w-[78%] max-w-xl flex-col justify-end p-3.5 text-white sm:p-5",
                children: [(0, t.jsx)("span", {
                  className: "mb-auto w-fit rounded-full bg-brand-primary px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.12em]",
                  children: e.eyebrow
                }), (0, t.jsx)("span", {
                  className: "line-clamp-2 text-[20px] font-extrabold leading-[1.02] tracking-[-0.025em] sm:text-[27px]",
                  children: e.title
                }), (0, t.jsx)("span", {
                  className: "mt-1.5 line-clamp-1 text-[11px] font-medium text-white/78 sm:text-[13px]",
                  children: e.subtitle
                })]
              })]
            }, e.key))
          }), e$.length > 1 ? (0, t.jsx)("div", {
            className: "mt-2 flex justify-center gap-1.5",
            "aria-label": "Featured slides",
            children: e$.map((e, a) => (0, t.jsx)("button", {
              type: "button",
              onClick: () => {
                let e;
                return (0, z.haptic)("selection"), eh(a), void ((e = ej.current) && e.scrollTo({
                  left: e.clientWidth * a,
                  behavior: "smooth"
                }));
              },
              className: "flex h-11 min-w-11 touch-manipulation items-center justify-center rounded-full",
              "aria-label": `Show featured slide ${a + 1}`,
              "aria-current": em === a,
              children: (0, t.jsx)("span", {
                className: `h-1.5 rounded-full transition-all ${em === a ? "w-5 bg-brand-primary" : "w-1.5 bg-brand-ink/18"}`,
                "aria-hidden": "true"
              })
            }, e.key))
          }) : null, (0, t.jsxs)(a.default, {
            href: "/menu",
            onClick: () => (0, z.haptic)("medium"),
            className: "mt-2 flex min-h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[14px] bg-brand-primary text-[15px] font-bold text-white shadow-[0_7px_20px_rgba(0,64,240,0.2)] active:scale-[0.99]",
            children: [(0, t.jsx)(f.UtensilsCrossed, {
              className: "h-[18px] w-[18px]",
              "aria-hidden": "true"
            }), "Order now"]
          })]
        }), (0, t.jsxs)("div", {
          className: "space-y-5 pt-2.5",
          children: [(0, t.jsx)("section", {
            className: "px-4 sm:px-6 lg:px-8",
            children: G && ec ? (0, t.jsxs)("button", {
              type: "button",
              onClick: eV,
              disabled: ex,
              className: "flex min-h-[58px] w-full touch-manipulation items-center gap-2.5 rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] p-2.5 text-left active:scale-[0.99] disabled:opacity-65 sm:min-h-[64px] sm:px-4",
              children: [(0, t.jsx)("span", {
                className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-brand-primary shadow-sm",
                children: (0, t.jsx)(h.RefreshCw, {
                  className: `h-5 w-5 ${ex ? "animate-spin" : ""}`,
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("span", {
                className: "min-w-0 flex-1",
                children: [(0, t.jsx)("span", {
                  className: "block text-[11px] font-semibold text-brand-ink/48",
                  children: "Reorder last order"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block truncate text-[14px] font-bold",
                  children: (j = (e = Array.isArray(ec?.lineItems) ? ec.lineItems : Array.isArray(ec?.orderData) ? ec.orderData : Array.isArray(ec?.items) ? ec.items : []).map(e => `${Number(e.count) > 1 ? `${e.count}\xd7 ` : ""}${e.productName || e.name || ""}`).filter(Boolean).slice(0, 2)).length ? `${j.join(", ")}${e.length > j.length ? ` +${e.length - j.length} more` : ""}` : "Your last Zeta order"
                })]
              }), (0, t.jsx)("span", {
                className: "shrink-0 rounded-full bg-brand-primary px-3.5 py-2 text-[12px] font-bold text-white",
                children: ex ? "Adding…" : "Reorder"
              })]
            }) : G ? (0, t.jsxs)(a.default, {
              href: "/menu",
              className: "flex min-h-[58px] touch-manipulation items-center gap-2.5 rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] p-2.5 active:scale-[0.99] sm:min-h-[64px] sm:px-4",
              children: [(0, t.jsx)("span", {
                className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-brand-primary",
                children: (0, t.jsx)(m.ReceiptText, {
                  className: "h-5 w-5",
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("span", {
                className: "min-w-0 flex-1",
                children: [(0, t.jsx)("span", {
                  className: "block text-[14px] font-bold",
                  children: "Your next favourite starts here"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block text-[11px] text-brand-ink/52",
                  children: "No previous orders yet"
                })]
              }), (0, t.jsx)(o.ChevronRight, {
                className: "h-5 w-5 text-brand-ink/32",
                "aria-hidden": "true"
              })]
            }) : (0, t.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, z.haptic)("light"), Y();
              },
              className: "flex min-h-[58px] w-full touch-manipulation items-center gap-2.5 rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] p-2.5 text-left active:scale-[0.99] sm:min-h-[64px] sm:px-4",
              children: [(0, t.jsx)("span", {
                className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-brand-primary",
                children: (0, t.jsx)(h.RefreshCw, {
                  className: "h-5 w-5",
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("span", {
                className: "min-w-0 flex-1",
                children: [(0, t.jsx)("span", {
                  className: "block text-[14px] font-bold",
                  children: "Reorder your last meal"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block text-[11px] text-brand-ink/52",
                  children: "Sign in to see your previous orders"
                })]
              }), (0, t.jsx)("span", {
                className: "shrink-0 text-[12px] font-bold text-brand-primary",
                children: "Sign in"
              })]
            })
          }), (0, t.jsx)("section", {
            className: "px-4 sm:px-6 lg:px-8",
            children: G ? (0, t.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, z.haptic)("light"), y.push("/wallet");
              },
              className: "w-full touch-manipulation rounded-2xl bg-[#EAF0FF] px-3.5 py-3 text-left active:scale-[0.99] sm:px-5 sm:py-4",
              "aria-label": `View membership card for ${eP}, member code ${eT}`,
              children: [(0, t.jsxs)("span", {
                className: "flex items-center gap-3",
                children: [(0, t.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, t.jsxs)("span", {
                    className: "flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                    children: [(0, t.jsx)(n, {
                      className: "h-3.5 w-3.5",
                      "aria-hidden": "true"
                    }), "Zeta Rewards"]
                  }), (0, t.jsx)("span", {
                    className: "mt-0.5 block truncate text-[15px] font-extrabold leading-tight",
                    children: eP
                  }), (0, t.jsxs)("span", {
                    className: "block truncate font-mono text-[10px] font-semibold tracking-[0.08em] text-brand-ink/48",
                    children: ["#", eT]
                  })]
                }), (0, t.jsxs)("span", {
                  className: "flex shrink-0 items-baseline gap-1",
                  children: [(0, t.jsx)("span", {
                    className: "text-[20px] font-extrabold leading-none",
                    children: eA.toLocaleString()
                  }), (0, t.jsx)("span", {
                    className: "text-[11px] font-semibold text-brand-ink/48",
                    children: "points"
                  })]
                })]
              }), eM > 0 ? (0, t.jsx)("span", {
                className: "mt-2 block h-1.5 overflow-hidden rounded-full bg-brand-primary/12",
                children: (0, t.jsx)("span", {
                  className: "block h-full rounded-full bg-brand-primary",
                  style: {
                    width: `${eI}%`
                  }
                })
              }) : null, (0, t.jsxs)("span", {
                className: "mt-1.5 flex items-center gap-2",
                children: [(0, t.jsx)("span", {
                  className: "min-w-0 flex-1 truncate text-[10px] text-brand-ink/55",
                  children: ez
                }), (0, t.jsxs)("span", {
                  className: "flex shrink-0 items-center gap-1 text-[11px] font-bold text-brand-primary",
                  children: [(0, t.jsx)(c.CreditCard, {
                    className: "h-3.5 w-3.5",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  }), "Card", (0, t.jsx)(o.ChevronRight, {
                    className: "h-3.5 w-3.5",
                    strokeWidth: 2.2,
                    "aria-hidden": "true"
                  })]
                })]
              })]
            }) : (0, t.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, z.haptic)("light"), Y();
              },
              className: "flex min-h-[62px] w-full touch-manipulation items-center gap-2.5 rounded-2xl bg-[#EAF0FF] p-2.5 text-left active:scale-[0.99] sm:min-h-[68px] sm:px-4",
              children: [(0, t.jsx)("span", {
                className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white",
                children: (0, t.jsx)(d.Gift, {
                  className: "h-[21px] w-[21px]",
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("span", {
                className: "min-w-0 flex-1",
                children: [(0, t.jsx)("span", {
                  className: "block text-[11px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                  children: "Zeta Rewards"
                }), (0, t.jsx)("span", {
                  className: "mt-1 block text-[15px] font-bold",
                  children: "Join Zeta Rewards"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block text-[11px] text-brand-ink/55",
                  children: "Earn points on every order"
                })]
              }), (0, t.jsx)(o.ChevronRight, {
                className: "h-5 w-5 shrink-0 text-brand-primary/55",
                "aria-hidden": "true"
              })]
            })
          }), (0, t.jsxs)("section", {
            children: [(0, t.jsx)(B, {
              title: "Featured for you",
              href: "/menu",
              action: "Full menu"
            }), (0, t.jsx)("div", {
              className: "flex snap-x snap-proximity scroll-pl-4 gap-2.5 overscroll-x-contain overflow-x-auto px-4 pb-1 scroll-smooth hide-scrollbar sm:scroll-pl-6 sm:px-6 lg:scroll-pl-8 lg:px-8",
              children: null === en ? [0, 1, 2].map(e => (0, t.jsx)("div", {
                className: "h-24 w-[118px] shrink-0 rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] sm:h-[132px] sm:w-[168px]"
              }, e)) : en.length > 0 ? en.map(e => {
                let s,
                  l = Z(e.imageUrl);
                return (0, t.jsxs)("article", {
                  className: "flex w-[118px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] sm:w-[168px]",
                  children: [l ? (0, t.jsxs)("div", {
                    className: "relative h-[70px] shrink-0 sm:h-[92px]",
                    children: [(0, t.jsx)(a.default, {
                      href: `/menu?${"combo" === e.itemType ? "combo" : "item"}=${(0, I.slugify)(e.name)}&id=${e.productItemId}`,
                      onClick: () => (0, z.haptic)("light"),
                      className: "relative block h-full bg-[#ECECEF]",
                      children: (0, t.jsx)(g.default, {
                        src: e.imageUrl,
                        alt: (0, P.displayLabel)(e.name),
                        fill: !0,
                        sizes: "(max-width: 639px) 118px, 168px",
                        className: "object-cover",
                        context: "MobileAppHome.featured",
                        meta: {
                          id: e.productItemId
                        }
                      })
                    }), (0, t.jsx)("button", {
                      type: "button",
                      onClick: () => eH(e),
                      className: "absolute -bottom-0.5 -right-0.5 flex h-11 w-11 touch-manipulation items-center justify-center active:scale-90",
                      "aria-label": `Add ${(0, P.displayLabel)(e.name)}`,
                      children: (0, t.jsx)("span", {
                        className: "flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white shadow-[0_2px_8px_rgba(0,64,240,0.28)]",
                        children: (0, t.jsx)(p.Plus, {
                          className: "h-[15px] w-[15px]",
                          strokeWidth: 2.6,
                          "aria-hidden": "true"
                        })
                      })
                    })]
                  }) : null, (0, t.jsxs)("div", {
                    className: `flex flex-1 flex-col p-2 ${l ? "" : "min-h-[76px] justify-end"}`,
                    children: [(0, t.jsx)("button", {
                      type: "button",
                      onClick: () => eW(e),
                      className: "line-clamp-2 touch-manipulation text-left text-[12px] font-bold leading-[1.2]",
                      children: (0, P.displayLabel)(e.name)
                    }), (0, t.jsxs)("div", {
                      className: "mt-0.5 flex items-center justify-between gap-1",
                      children: [(0, t.jsx)("span", {
                        className: "truncate text-[12px] font-extrabold",
                        children: (Number.isFinite(s = Number(e?.salePrice ?? e?.sale_price ?? e?.price)) && s > 0 ? `$${s.toFixed(2)}` : null) || "View"
                      }), !l && (0, t.jsx)("button", {
                        type: "button",
                        onClick: () => eH(e),
                        className: "-m-1.5 flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center active:scale-90",
                        "aria-label": `Add ${(0, P.displayLabel)(e.name)}`,
                        children: (0, t.jsx)("span", {
                          className: "flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white",
                          children: (0, t.jsx)(p.Plus, {
                            className: "h-[15px] w-[15px]",
                            strokeWidth: 2.6,
                            "aria-hidden": "true"
                          })
                        })
                      })]
                    })]
                  })]
                }, `${e.itemType}-${e.productItemId}`);
              }) : (0, t.jsxs)(a.default, {
                href: "/menu",
                className: "flex h-24 w-[118px] shrink-0 snap-start flex-col justify-end rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] p-3 sm:h-[132px] sm:w-[168px]",
                children: [(0, t.jsx)("span", {
                  className: "text-[12px] font-bold leading-tight",
                  children: "Explore the full menu"
                }), (0, t.jsx)("span", {
                  className: "mt-2 text-[11px] font-semibold text-brand-primary",
                  children: "View menu"
                })]
              })
            })]
          }), ef.length > 0 ? (0, t.jsxs)("section", {
            children: [(0, t.jsx)(B, {
              title: "Your favourites",
              href: "/favourites"
            }), (0, t.jsx)("div", {
              className: "flex snap-x scroll-pl-4 gap-2.5 overflow-x-auto px-4 pb-1 hide-scrollbar sm:scroll-pl-6 sm:px-6 lg:scroll-pl-8 lg:px-8",
              children: ef.map(e => (0, t.jsxs)(a.default, {
                href: `/menu?${"combo" === e.type ? "combo" : "item"}=${e.slug || ""}&id=${encodeURIComponent(e.id)}`,
                onClick: () => (0, z.haptic)("light"),
                className: "flex w-[118px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] active:scale-[0.98] sm:w-[168px]",
                children: [Z(e.image) ? (0, t.jsx)("span", {
                  className: "relative block h-[70px] bg-[#ECECEF] sm:h-[92px]",
                  children: (0, t.jsx)(g.default, {
                    src: e.image,
                    alt: "",
                    fill: !0,
                    sizes: "(max-width: 639px) 118px, 168px",
                    className: "object-cover",
                    context: "MobileAppHome.favourite",
                    meta: {
                      id: e.id
                    }
                  })
                }) : null, (0, t.jsxs)("span", {
                  className: `flex flex-1 flex-col p-2 ${Z(e.image) ? "" : "min-h-[76px] justify-end"}`,
                  children: [(0, t.jsx)("span", {
                    className: "line-clamp-2 text-[12px] font-bold leading-[1.2] text-brand-ink",
                    children: (0, P.displayLabel)(e.label)
                  }), Number.isFinite(Number(e.price)) && Number(e.price) > 0 ? (0, t.jsxs)("span", {
                    className: "mt-0.5 truncate text-[12px] font-extrabold text-brand-ink",
                    children: ["$", Number(e.price).toFixed(2)]
                  }) : null]
                })]
              }, e.id))
            })]
          }) : null, null === eu ? (0, t.jsxs)("section", {
            children: [(0, t.jsx)(B, {
              title: "Browse by category",
              href: "/menu",
              action: "Full menu"
            }), (0, t.jsx)("div", {
              className: "flex gap-2 px-4 sm:px-6 lg:px-8",
              children: [0, 1, 2, 3].map(e => (0, t.jsx)("div", {
                className: "h-[62px] w-[112px] shrink-0 animate-pulse rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] sm:h-[76px] sm:w-[148px]"
              }, e))
            })]
          }) : eu.length > 0 ? (0, t.jsxs)("section", {
            children: [(0, t.jsx)(B, {
              title: "Browse by category",
              href: "/menu",
              action: "Full menu"
            }), (0, t.jsx)("div", {
              className: "flex snap-x snap-proximity scroll-pl-4 gap-2 overscroll-x-contain overflow-x-auto px-4 pb-1 scroll-smooth hide-scrollbar sm:scroll-pl-6 sm:px-6 lg:scroll-pl-8 lg:px-8",
              children: eu.map(e => (0, t.jsxs)(a.default, {
                href: `/menu?category=${(0, I.slugify)(e.label)}&id=${e.id}`,
                onClick: () => (0, z.haptic)("light"),
                className: "flex min-h-[62px] w-[112px] shrink-0 snap-start touch-manipulation flex-col justify-center overflow-hidden rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2 active:scale-[0.98] sm:min-h-[76px] sm:w-[148px]",
                children: [Z(e.image) ? (0, t.jsx)("span", {
                  className: "relative -mx-3 -mt-2 mb-1.5 block h-[52px] bg-[#ECECEF]",
                  children: (0, t.jsx)(g.default, {
                    src: e.image,
                    alt: "",
                    fill: !0,
                    sizes: "104px",
                    className: "object-cover",
                    context: "MobileAppHome.category",
                    meta: {
                      id: e.id
                    }
                  })
                }) : null, (0, t.jsx)("span", {
                  className: "line-clamp-2 text-[12px] font-bold leading-[1.2] text-brand-ink",
                  children: (0, P.displayLabel)(e.label)
                }), (0, t.jsxs)("span", {
                  className: "mt-0.5 text-[10px] font-semibold text-brand-ink/45",
                  children: [e.count, " ", 1 === e.count ? "item" : "items"]
                })]
              }, e.id))
            })]
          }) : null, eZ.length > 0 ? (0, t.jsxs)("section", {
            children: [(0, t.jsx)(B, {
              title: "Promotions",
              href: "/offers"
            }), (0, t.jsx)("div", {
              className: "grid gap-2 px-4 sm:grid-cols-2 sm:px-6 lg:px-8",
              children: eZ.map(e => (0, t.jsxs)(a.default, {
                href: "/offers",
                onClick: () => (0, z.haptic)("light"),
                className: `flex min-h-[64px] items-center gap-3 rounded-2xl border border-brand-ink/8 bg-[#F5F5F7] p-2 active:scale-[0.99] ${Z(e.image) ? "" : "pl-4"}`,
                children: [Z(e.image) ? (0, t.jsx)("span", {
                  className: "relative h-12 w-16 shrink-0 overflow-hidden rounded-xl bg-[#ECECEF]",
                  children: (0, t.jsx)(g.default, {
                    src: e.image,
                    alt: (0, P.displayLabel)(e.title),
                    fill: !0,
                    sizes: "72px",
                    className: "object-cover",
                    context: "MobileAppHome.promotion",
                    meta: {
                      id: e.id
                    }
                  })
                }) : null, (0, t.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, t.jsx)("span", {
                    className: "block text-[10px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                    children: "Promotion"
                  }), (0, t.jsx)("span", {
                    className: "mt-0.5 line-clamp-2 block text-[14px] font-bold leading-tight",
                    children: (0, P.displayLabel)(e.title)
                  })]
                }), (0, t.jsx)(o.ChevronRight, {
                  className: "h-5 w-5 shrink-0 text-brand-ink/28",
                  "aria-hidden": "true"
                })]
              }, e.id))
            })]
          }) : null, eO.length > 0 ? (0, t.jsxs)("section", {
            children: [(0, t.jsx)(B, {
              title: "App-exclusive deals",
              href: "/offers"
            }), (0, t.jsx)("div", {
              className: "grid gap-2 px-4 sm:grid-cols-2 sm:px-6 lg:px-8",
              children: eO.slice(0, 2).map(e => (0, t.jsxs)(a.default, {
                href: "/offers",
                onClick: () => (0, z.haptic)("light"),
                className: "flex min-h-[64px] items-center gap-3 rounded-2xl bg-[#EAF0FF] p-2.5 active:scale-[0.99]",
                children: [Z(e.image) ? (0, t.jsx)("span", {
                  className: "relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-white",
                  children: (0, t.jsx)(g.default, {
                    src: e.image,
                    alt: (0, P.displayLabel)(e.title),
                    fill: !0,
                    sizes: "48px",
                    className: "object-cover",
                    context: "MobileAppHome.appExclusive",
                    meta: {
                      id: e.id
                    }
                  })
                }) : (0, t.jsx)("span", {
                  className: "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-brand-primary",
                  children: (0, t.jsx)(d.Gift, {
                    className: "h-5 w-5",
                    "aria-hidden": "true"
                  })
                }), (0, t.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, t.jsx)("span", {
                    className: "block text-[10px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                    children: "Only in the app"
                  }), (0, t.jsx)("span", {
                    className: "mt-0.5 line-clamp-2 block text-[14px] font-bold leading-tight",
                    children: (0, P.displayLabel)(e.title)
                  })]
                }), (0, t.jsx)(o.ChevronRight, {
                  className: "h-5 w-5 shrink-0 text-brand-primary/45",
                  "aria-hidden": "true"
                })]
              }, e.id))
            })]
          }) : null, (0, t.jsx)("section", {
            className: "px-4 sm:px-6 lg:px-8",
            children: (0, t.jsxs)(a.default, {
              href: "/stores",
              onClick: () => (0, z.haptic)("selection"),
              className: "flex min-h-14 items-center gap-2.5 rounded-2xl border border-brand-ink/8 bg-white p-2 shadow-[0_6px_22px_rgba(19,19,22,0.06)] active:scale-[0.99]",
              children: [Z(eU) ? (0, t.jsx)("span", {
                className: "relative h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-[#ECECEF]",
                children: (0, t.jsx)(g.default, {
                  src: eU,
                  alt: "",
                  fill: !0,
                  sizes: "44px",
                  className: "object-cover",
                  context: "MobileAppHome.branchShopImage",
                  meta: {
                    branch: U?.id
                  }
                })
              }) : (0, t.jsx)("span", {
                className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EAF0FF] text-brand-primary",
                children: (0, t.jsx)(x.MapPin, {
                  className: "h-[22px] w-[22px]",
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("span", {
                className: "min-w-0 flex-1",
                children: [(0, t.jsx)("span", {
                  className: "block text-[10px] font-bold uppercase tracking-[0.1em] text-brand-ink/42",
                  children: "Nearest branch"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block truncate text-[15px] font-bold",
                  children: U?.name || "Choose a Zeta store"
                }), (0, t.jsx)("span", {
                  className: `mt-0.5 block text-[11px] font-semibold ${eB.startsWith("Open") ? "text-emerald-600" : "text-brand-ink/48"}`,
                  children: eB
                })]
              }), (0, t.jsx)(o.ChevronRight, {
                className: "h-5 w-5 shrink-0 text-brand-ink/28",
                "aria-hidden": "true"
              })]
            })
          }), (0, t.jsxs)("section", {
            className: "grid grid-cols-2 gap-2.5 px-4 sm:gap-4 sm:px-6 lg:px-8",
            children: [(0, t.jsxs)(a.default, {
              href: "/catering",
              onClick: () => (0, z.haptic)("light"),
              className: "group relative flex min-h-[158px] touch-manipulation flex-col overflow-hidden rounded-2xl border border-brand-primary/35 bg-white p-2.5 shadow-[0_6px_18px_rgba(0,64,240,0.07)] active:scale-[0.99] sm:min-h-[188px] sm:p-3.5",
              children: [(0, t.jsx)("span", {
                className: "relative h-[66px] w-full shrink-0 overflow-hidden rounded-xl bg-[#ECECEF]",
                children: (0, t.jsx)(g.default, {
                  src: "/brands/zeta/catering-platter.webp",
                  alt: "Zeta catering platter",
                  fill: !0,
                  sizes: "180px",
                  className: "object-cover",
                  context: "MobileAppHome.catering"
                })
              }), (0, t.jsxs)("span", {
                className: "mt-2 min-w-0 pr-4",
                children: [(0, t.jsx)("span", {
                  className: "block text-[9px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                  children: "Feed the crew"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block text-[14px] font-bold leading-tight",
                  children: "Catering by Zeta"
                }), (0, t.jsx)("span", {
                  className: "mt-1 line-clamp-2 block text-[10px] leading-snug text-brand-ink/50",
                  children: "Fresh Greek food for your next event"
                })]
              }), (0, t.jsx)(o.ChevronRight, {
                className: "absolute bottom-3 right-2.5 h-4 w-4 text-brand-primary/55",
                "aria-hidden": "true"
              })]
            }), (0, t.jsxs)(a.default, {
              href: "/franchise",
              onClick: () => (0, z.haptic)("light"),
              className: "relative flex min-h-[158px] touch-manipulation flex-col rounded-2xl border border-brand-primary/35 bg-white p-2.5 shadow-[0_6px_18px_rgba(0,64,240,0.07)] active:scale-[0.99] sm:min-h-[188px] sm:p-3.5",
              children: [(0, t.jsx)("span", {
                className: "flex h-[66px] w-full shrink-0 items-center justify-center rounded-xl bg-[#EAF0FF] text-brand-primary",
                children: (0, t.jsx)(b.Store, {
                  className: "h-8 w-8",
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("span", {
                className: "mt-2 min-w-0 pr-4",
                children: [(0, t.jsx)("span", {
                  className: "block text-[9px] font-bold uppercase tracking-[0.1em] text-brand-primary",
                  children: "Grow with us"
                }), (0, t.jsx)("span", {
                  className: "mt-0.5 block text-[14px] font-bold leading-tight",
                  children: "Own a Zeta franchise"
                }), (0, t.jsx)("span", {
                  className: "mt-1 line-clamp-2 block text-[10px] leading-snug text-brand-ink/50",
                  children: "Bring Zeta to your community"
                })]
              }), (0, t.jsx)(o.ChevronRight, {
                className: "absolute bottom-3 right-2.5 h-4 w-4 text-brand-primary/55",
                "aria-hidden": "true"
              })]
            })]
          }), (0, t.jsx)(N, {})]
        })]
      }), (0, t.jsx)(i.AnimatePresence, {
        children: ea > 0 ? (0, t.jsxs)(r.motion.button, {
          initial: {
            y: 90,
            opacity: 0
          },
          animate: {
            y: 0,
            opacity: 1
          },
          exit: {
            y: 90,
            opacity: 0
          },
          transition: {
            type: "spring",
            stiffness: 380,
            damping: 32
          },
          type: "button",
          onClick: () => {
            (0, z.haptic)("medium"), et(!0);
          },
          className: "fixed left-3 right-3 z-[88] mx-auto flex max-w-3xl touch-manipulation items-center justify-between gap-3 rounded-2xl bg-brand-primary py-3 pl-3.5 pr-4 text-white shadow-lg active:scale-[0.98] sm:left-6 sm:right-6 sm:min-h-14 sm:px-5",
          style: {
            bottom: "calc(68px + env(safe-area-inset-bottom, 0px))"
          },
          "aria-label": `View cart, ${ea} item${1 === ea ? "" : "s"}, total $${es.toFixed(2)}`,
          children: [(0, t.jsxs)("span", {
            className: "flex min-w-0 items-center gap-2.5",
            children: [(0, t.jsxs)("span", {
              className: "relative shrink-0",
              children: [(0, t.jsx)(u.ShoppingBag, {
                className: "h-[22px] w-[22px]",
                "aria-hidden": "true"
              }), (0, t.jsx)("span", {
                className: "absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-brand-primary",
                children: ea
              })]
            }), (0, t.jsx)("span", {
              className: "text-[15px] font-semibold",
              children: "View cart"
            })]
          }), (0, t.jsxs)("span", {
            className: "flex items-center gap-1.5 text-[15px] font-semibold",
            children: ["$", es.toFixed(2), (0, t.jsx)(o.ChevronRight, {
              className: "h-4 w-4 opacity-80",
              "aria-hidden": "true"
            })]
          })]
        }, "cart-bar") : null
      })]
    });
  }], 859871);
});

  Z.define(867927, e => {
  "use strict";

  let t = (0, e.i(456420).default)("chevron-right", [["path", {
    d: "m9 18 6-6-6-6",
    key: "mthhwq"
  }]]);
  e.s(["ChevronRight", 0, t], 867927);
});

  Z.define(870387, e => {
  "use strict";

  let t = (0, e.i(456420).default)("credit-card", [["rect", {
    width: "20",
    height: "14",
    x: "2",
    y: "5",
    rx: "2",
    key: "ynyp8z"
  }], ["line", {
    x1: "2",
    x2: "22",
    y1: "10",
    y2: "10",
    key: "1b3vmo"
  }]]);
  e.s(["CreditCard", 0, t], 870387);
});

  Z.define(875969, e => {
  "use strict";

  let t = (0, e.i(456420).default)("heart-off", [["path", {
    d: "M10.5 4.893a5.5 5.5 0 0 1 1.091.931.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 1.872-1.002 3.356-2.187 4.655",
    key: "1inpfl"
  }], ["path", {
    d: "m16.967 16.967-3.459 3.346a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5a5.5 5.5 0 0 1 2.747-4.761",
    key: "vbc6x7"
  }], ["path", {
    d: "m2 2 20 20",
    key: "1ooewy"
  }]]);
  e.s(["HeartOff", 0, t], 875969);
});

  Z.define(882505, e => {
  "use strict";

  let t = (0, e.i(456420).default)("list", [["path", {
    d: "M3 5h.01",
    key: "18ugdj"
  }], ["path", {
    d: "M3 12h.01",
    key: "nlz23k"
  }], ["path", {
    d: "M3 19h.01",
    key: "noohij"
  }], ["path", {
    d: "M8 5h13",
    key: "1pao27"
  }], ["path", {
    d: "M8 12h13",
    key: "1za7za"
  }], ["path", {
    d: "M8 19h13",
    key: "m83p4d"
  }]]);
  e.s(["List", 0, t], 882505);
});

  Z.define(901100, e=>{var t = e.i(843476);
var a = e.i(271645);
var i = e.i(618566);
var r = e.i(88653);
var l = e.i(846932);
var c = e.i(867927);
var p = e.i(263448);
var A = e.i(263676);
var P = e.i(701688);
var L = e.i(273341);
var R = e.i(174080);
var U = e.i(652348);
var V = e.i(987664);
var et = e.i(328623);
var eo = e.i(341861);
var ed = e.i(853138);
var ec = e.i(935829);
var ex = e.i(515281);
var em = e.i(77071);
var ep = e.i(573474);
var eu = e.i(977617);
var eh = e.i(973438);
function eb({
  item: e,
  onChoose: a
}) {
  let i = "string" == typeof e.imageUrl && e.imageUrl.trim(),
    n = (0, eh.isCustomizable)(e);
  return (0, t.jsxs)("button", {
    type: "button",
    onClick: () => a(e),
    className: "flex min-h-[68px] w-full touch-manipulation items-center gap-3 rounded-[14px] bg-[#F5F5F7] p-2 text-left active:scale-[0.99]",
    "aria-label": `${n ? "Customise" : "Add"} ${e.name}`,
    children: [i ? (0, t.jsx)("span", {
      className: "relative h-[52px] w-[52px] shrink-0 overflow-hidden rounded-[11px] bg-[#EBEBEF]",
      children: (0, t.jsx)(eu.default, {
        src: e.imageUrl,
        alt: "",
        fill: !0,
        sizes: "52px",
        className: "object-cover",
        context: "AppCartSheet.suggestion",
        meta: {
          id: e.productItemId
        }
      })
    }) : (0, t.jsx)("span", {
      className: "grid h-[52px] w-[52px] shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
      children: (0, t.jsx)(et.Sparkles, {
        className: "h-5 w-5",
        strokeWidth: 1.9,
        "aria-hidden": "true"
      })
    }), (0, t.jsxs)("span", {
      className: "min-w-0 flex-1",
      children: [(0, t.jsx)("span", {
        className: "line-clamp-2 text-[13px] font-semibold leading-tight text-brand-ink",
        children: e.name
      }), (0, t.jsxs)("span", {
        className: "mt-1 block text-[12px] font-bold text-brand-primary",
        children: ["$", Number(e.salePrice || 0).toFixed(2)]
      })]
    }), (0, t.jsx)("span", {
      className: "grid h-11 w-11 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
      children: n ? (0, t.jsx)(c.ChevronRight, {
        className: "h-4 w-4",
        strokeWidth: 2.2,
        "aria-hidden": "true"
      }) : (0, t.jsx)(em.Plus, {
        className: "h-4 w-4",
        strokeWidth: 2.4,
        "aria-hidden": "true"
      })
    })]
  });
}
function ef({
  isOpen: e,
  onClose: n
}) {
  let s = (0, i.useRouter)(),
    {
      currentBranch: o,
      orderType: d,
      scheduleType: x,
      scheduledTime: m
    } = (0, U.useBranch)(),
    {
      cart: u,
      removeFromCart: h,
      updateQuantity: b,
      cartTotal: f,
      cartCount: g,
      cartSuggestions: y,
      addToCart: j,
      markSuggestionSeen: k,
      openCustomizer: w,
      startEditingCartItem: v,
      unavailableCartItems: N
    } = (0, V.useCart)(),
    [F, C] = (0, a.useState)(!1);
  (0, a.useEffect)(() => C(!0), []), (0, P.useBodyScrollLock)(e);
  let S = (0, a.useMemo)(() => new Set((N || []).map(e => e.line.cart_item_id)), [N]);
  function z(e) {
    if ((0, L.haptic)("light"), (0, eh.isCustomizable)(e)) {
      k(e.productItemId), n(), w((0, eh.suggestionToMenuItem)(e));
      return;
    }
    j((0, eh.suggestionToCartItem)(e), {
      fromSuggestion: !0
    });
  }
  if (!F) return null;
  let E = [o?.name || "Choose a store", String(d || "PICKUP").toLowerCase(), "SCHEDULED" === x && m ? "scheduled" : "ASAP"].join(" · ");
  return (0, R.createPortal)((0, t.jsx)(r.AnimatePresence, {
    children: e ? (0, t.jsxs)(t.Fragment, {
      children: [(0, t.jsx)(l.motion.button, {
        type: "button",
        "aria-label": "Close cart",
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        },
        onClick: n,
        className: "fixed inset-0 z-[110] bg-brand-ink/48"
      }), (0, t.jsxs)(l.motion.section, {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "zeta-cart-title",
        initial: {
          y: "100%"
        },
        animate: {
          y: 0
        },
        exit: {
          y: "100%"
        },
        transition: {
          type: "spring",
          stiffness: 380,
          damping: 38
        },
        className: "fixed bottom-0 left-0 right-0 z-[120] mx-auto flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[20px] bg-white shadow-2xl sm:bottom-4 sm:left-6 sm:right-6 sm:max-h-[calc(100dvh-32px)] sm:w-auto sm:rounded-[22px]",
        children: [(0, t.jsxs)("header", {
          className: "shrink-0 border-b border-brand-ink/8 px-4 pb-3 pt-3 sm:px-6 sm:pt-4",
          children: [(0, t.jsx)("div", {
            className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/12"
          }), (0, t.jsxs)("div", {
            className: "mt-3 flex items-start justify-between gap-3",
            children: [(0, t.jsxs)("div", {
              children: [(0, t.jsxs)("div", {
                className: "flex items-center gap-1.5 text-brand-primary",
                children: [(0, t.jsx)(p.ShoppingBag, {
                  className: "h-4 w-4",
                  strokeWidth: 2.1,
                  "aria-hidden": "true"
                }), (0, t.jsx)("p", {
                  className: "text-[10px] font-bold uppercase tracking-[0.12em]",
                  children: "Your order"
                })]
              }), (0, t.jsx)("h2", {
                id: "zeta-cart-title",
                className: "mt-1 text-[23px] font-extrabold leading-none tracking-[-0.02em]",
                children: u.length ? "Ready to review" : "Your cart"
              }), (0, t.jsx)("p", {
                className: "mt-1.5 text-[11px] capitalize text-brand-ink-light",
                children: E
              })]
            }), (0, t.jsx)("button", {
              type: "button",
              onClick: n,
              "aria-label": "Close",
              className: "grid h-11 w-11 shrink-0 touch-manipulation place-items-center rounded-[13px] bg-[#F5F5F7] text-brand-ink",
              children: (0, t.jsx)(A.X, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            })]
          })]
        }), (0, t.jsxs)("div", {
          className: "min-h-0 flex-1 overscroll-y-contain overflow-y-auto px-4 py-3 sm:px-6 sm:py-4",
          children: [u.length && N?.length ? (0, t.jsxs)("div", {
            className: "mb-3 flex items-start gap-2.5 rounded-[14px] bg-red-50 px-3 py-2.5 text-red-700",
            children: [(0, t.jsx)(ed.AlertTriangle, {
              className: "mt-0.5 h-4 w-4 shrink-0",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), (0, t.jsxs)("p", {
              className: "text-[11px] font-medium leading-snug",
              children: [1 === N.length ? "One item is" : `${N.length} items are`, " unavailable for this order type or time. Remove or edit before checkout."]
            })]
          }) : null, u.length ? (0, t.jsxs)("div", {
            className: "space-y-3",
            children: [(0, t.jsxs)("section", {
              "aria-label": "Items in your cart",
              children: [(0, t.jsxs)("div", {
                className: "mb-2 flex items-center justify-between",
                children: [(0, t.jsx)("h3", {
                  className: "text-[15px] font-bold",
                  children: "Your items"
                }), (0, t.jsxs)("span", {
                  className: "text-[11px] text-brand-ink-light",
                  children: [g, " ", 1 === g ? "item" : "items"]
                })]
              }), (0, t.jsx)("div", {
                className: "space-y-2.5",
                children: u.map(e => {
                  let a = S.has(e.cart_item_id),
                    i = !e.isFreeItem && !e.isLoyaltyAddedProduct && (e.is_combo ? Array.isArray(e.cat_mappings) && e.cat_mappings.length > 0 : Array.isArray(e.sizes) && e.sizes.length > 0 || Array.isArray(e.modifiers) && e.modifiers.length > 0 || 1 !== e.special_instruction_hide || Array.isArray(e.group) && e.group.length > 0 || Array.isArray(e.groups) && e.groups.length > 0 || Array.isArray(e.groupstoproduct) && e.groupstoproduct.length > 0),
                    s = e.image_url || e.imageUrl || e.image,
                    r = "string" == typeof s && s.trim(),
                    l = e.size && e.sizes?.length ? e.sizes.find(t => String(t.id) === String(e.size)) : null,
                    o = l?.size_name || l?.label || l?.name,
                    d = e.isLoyaltyAddedProduct || e.isFreeItem;
                  return (0, t.jsxs)("article", {
                    className: `overflow-hidden rounded-[16px] border ${a ? "border-red-200 bg-red-50/40" : "border-brand-ink/8 bg-white"}`,
                    children: [a ? (0, t.jsx)("div", {
                      className: "border-b border-red-100 px-3 py-1.5 text-[10px] font-semibold text-red-600",
                      children: "Not available for this order"
                    }) : null, (0, t.jsxs)("div", {
                      className: `p-3 ${a ? "opacity-65" : ""}`,
                      children: [(0, t.jsxs)("div", {
                        className: "flex gap-3",
                        children: [r ? (0, t.jsx)("span", {
                          className: "relative h-16 w-16 shrink-0 overflow-hidden rounded-[12px] bg-[#EBEBEF]",
                          children: (0, t.jsx)(eu.default, {
                            src: s,
                            alt: "",
                            fill: !0,
                            sizes: "64px",
                            className: "object-cover",
                            context: "AppCartSheet.item",
                            meta: {
                              id: e.cart_item_id
                            }
                          })
                        }) : null, (0, t.jsxs)("div", {
                          className: "min-w-0 flex-1",
                          children: [(0, t.jsxs)("div", {
                            className: "flex items-start justify-between gap-2",
                            children: [(0, t.jsxs)("div", {
                              className: "min-w-0",
                              children: [(0, t.jsx)("h4", {
                                className: "line-clamp-2 text-[14px] font-semibold leading-tight",
                                children: e.combo_name || e.label || e.name || "Item"
                              }), e.isLoyaltyAddedProduct ? (0, t.jsxs)("span", {
                                className: "mt-1 inline-flex rounded-full bg-[#EAF0FF] px-2 py-0.5 text-[9px] font-semibold text-brand-primary",
                                children: ["Reward · ", e.points_required, " points"]
                              }) : null]
                            }), (0, t.jsxs)("span", {
                              className: "shrink-0 text-[14px] font-bold tabular-nums",
                              children: ["$", (0, eo.calculateCartLineTotal)(e).toFixed(2)]
                            })]
                          }), o ? (0, t.jsx)("p", {
                            className: "mt-1 text-[10px] font-medium text-brand-ink-light",
                            children: o
                          }) : null, e.modifiers?.length ? (0, t.jsx)("p", {
                            className: "mt-1 line-clamp-2 text-[10px] leading-snug text-brand-ink-light",
                            children: e.modifiers.map(e => `${e.addOnName || e.name}${e.count > 1 ? ` \xd7${e.count}` : ""}`).join(", ")
                          }) : null]
                        })]
                      }), (0, t.jsxs)("div", {
                        className: "mt-3 flex items-center justify-between gap-2",
                        children: [(0, t.jsxs)("div", {
                          className: "flex min-h-11 items-center rounded-[11px] border border-brand-ink/8 bg-[#F5F5F7]",
                          children: [(0, t.jsx)("button", {
                            type: "button",
                            onClick: () => {
                              (0, L.haptic)("light"), b(e.cart_item_id, e.count - 1);
                            },
                            disabled: d,
                            "aria-label": `Decrease ${e.combo_name || e.label || e.name || "item"} quantity`,
                            className: "grid h-11 w-11 touch-manipulation place-items-center rounded-[10px] bg-white text-brand-ink disabled:bg-transparent disabled:opacity-35",
                            children: (0, t.jsx)(ec.Minus, {
                              className: "h-3.5 w-3.5",
                              strokeWidth: 2.3,
                              "aria-hidden": "true"
                            })
                          }), (0, t.jsx)("span", {
                            className: "w-8 text-center text-[12px] font-bold tabular-nums",
                            children: e.count
                          }), (0, t.jsx)("button", {
                            type: "button",
                            onClick: () => {
                              (0, L.haptic)("light"), b(e.cart_item_id, e.count + 1);
                            },
                            disabled: d,
                            "aria-label": `Increase ${e.combo_name || e.label || e.name || "item"} quantity`,
                            className: "grid h-11 w-11 touch-manipulation place-items-center rounded-[10px] bg-white text-brand-primary disabled:bg-transparent disabled:opacity-35",
                            children: (0, t.jsx)(em.Plus, {
                              className: "h-3.5 w-3.5",
                              strokeWidth: 2.3,
                              "aria-hidden": "true"
                            })
                          })]
                        }), (0, t.jsxs)("div", {
                          className: "flex items-center gap-1.5",
                          children: [i ? (0, t.jsxs)("button", {
                            type: "button",
                            onClick: () => {
                              (0, L.haptic)("selection"), v(e), n();
                            },
                            className: "flex h-11 touch-manipulation items-center gap-1.5 rounded-[11px] bg-[#F5F5F7] px-3 text-[11px] font-semibold",
                            children: [(0, t.jsx)(ex.Pencil, {
                              className: "h-3.5 w-3.5 text-brand-primary",
                              strokeWidth: 2,
                              "aria-hidden": "true"
                            }), "Edit"]
                          }) : null, (0, t.jsx)("button", {
                            type: "button",
                            onClick: () => {
                              (0, L.haptic)("selection"), h(e.cart_item_id);
                            },
                            className: "grid h-11 w-11 touch-manipulation place-items-center rounded-[11px] bg-red-50 text-red-600",
                            "aria-label": `Remove ${e.combo_name || e.label || e.name || "item"}`,
                            children: (0, t.jsx)(ep.Trash2, {
                              className: "h-4 w-4",
                              strokeWidth: 2,
                              "aria-hidden": "true"
                            })
                          })]
                        })]
                      })]
                    })]
                  }, e.cart_item_id);
                })
              })]
            }), y?.length ? (0, t.jsxs)("section", {
              children: [(0, t.jsx)("h3", {
                className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
                children: "Add something extra"
              }), (0, t.jsx)("div", {
                className: "hide-scrollbar -mx-4 flex snap-x snap-proximity scroll-pl-4 gap-2 overscroll-x-contain overflow-x-auto px-4 pb-1 scroll-smooth sm:-mx-6 sm:scroll-pl-6 sm:px-6",
                children: y.map(e => (0, t.jsxs)("button", {
                  type: "button",
                  onClick: () => z(e),
                  className: "min-h-[76px] w-[148px] shrink-0 snap-start touch-manipulation rounded-[14px] bg-[#F5F5F7] p-2.5 text-left sm:w-[168px]",
                  children: [(0, t.jsx)("span", {
                    className: "line-clamp-2 min-h-[32px] text-[12px] font-semibold leading-tight",
                    children: e.name
                  }), (0, t.jsxs)("span", {
                    className: "mt-2 flex items-center justify-between text-[11px] font-bold text-brand-primary",
                    children: ["$", Number(e.salePrice || 0).toFixed(2), (0, eh.isCustomizable)(e) ? (0, t.jsx)(c.ChevronRight, {
                      className: "h-4 w-4",
                      strokeWidth: 2.2,
                      "aria-hidden": "true"
                    }) : (0, t.jsx)(em.Plus, {
                      className: "h-4 w-4",
                      strokeWidth: 2.3,
                      "aria-hidden": "true"
                    })]
                  })]
                }, e.productItemId))
              })]
            }) : null]
          }) : (0, t.jsxs)("div", {
            className: "flex min-h-[360px] flex-col items-center justify-center text-center",
            children: [(0, t.jsx)("span", {
              className: "grid h-14 w-14 place-items-center rounded-[16px] bg-[#EAF0FF] text-brand-primary",
              children: (0, t.jsx)(p.ShoppingBag, {
                className: "h-7 w-7",
                strokeWidth: 1.8,
                "aria-hidden": "true"
              })
            }), (0, t.jsx)("h3", {
              className: "mt-3 text-[18px] font-bold",
              children: "Nothing in your cart yet"
            }), (0, t.jsx)("p", {
              className: "mt-1 max-w-[270px] text-[12px] leading-relaxed text-brand-ink-light",
              children: "Add a souva, plate, bowl or side and it will appear here."
            }), (0, t.jsx)("button", {
              type: "button",
              onClick: () => {
                n(), s.push("/menu");
              },
              className: "mt-4 min-h-12 touch-manipulation rounded-[13px] bg-brand-primary px-5 text-[13px] font-semibold text-white",
              children: "Browse menu"
            }), y?.length ? (0, t.jsxs)("div", {
              className: "mt-7 w-full text-left",
              children: [(0, t.jsx)("h4", {
                className: "mb-2 text-[13px] font-semibold text-brand-ink-light",
                children: "Popular right now"
              }), (0, t.jsx)("div", {
                className: "space-y-2",
                children: y.slice(0, 3).map(e => (0, t.jsx)(eb, {
                  item: e,
                  onChoose: z
                }, e.productItemId))
              })]
            }) : null]
          })]
        }), (0, t.jsxs)("footer", {
          className: "shrink-0 border-t border-brand-ink/8 bg-white px-4 pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-3 sm:px-6 sm:pb-5",
          children: [u.some(e => e.isLoyaltyAddedProduct) ? (0, t.jsxs)("div", {
            className: "mb-2 flex items-center justify-between rounded-[12px] bg-[#EAF0FF] px-3 py-2 text-[11px] font-semibold text-brand-primary",
            children: [(0, t.jsx)("span", {
              children: "Loyalty rewards applied"
            }), (0, t.jsx)(et.Sparkles, {
              className: "h-4 w-4",
              strokeWidth: 2,
              "aria-hidden": "true"
            })]
          }) : null, (0, t.jsxs)("div", {
            className: "mb-2 flex items-end justify-between",
            children: [(0, t.jsxs)("div", {
              children: [(0, t.jsx)("p", {
                className: "text-[11px] text-brand-ink-light",
                children: "Subtotal · GST included"
              }), (0, t.jsx)("p", {
                className: "mt-0.5 text-[10px] text-brand-ink/38",
                children: "Final fees and discounts appear at checkout."
              })]
            }), (0, t.jsxs)("p", {
              className: "text-[20px] font-extrabold tabular-nums",
              children: ["$", f.toFixed(2)]
            })]
          }), (0, t.jsxs)("button", {
            type: "button",
            onClick: function () {
              (0, L.haptic)("medium"), n(), s.push("/checkout");
            },
            disabled: !u.length,
            className: "flex min-h-12 w-full touch-manipulation items-center justify-between rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white disabled:bg-brand-ink/12",
            children: [(0, t.jsx)("span", {
              children: "Continue to checkout"
            }), (0, t.jsxs)("span", {
              className: "flex items-center gap-1.5",
              children: ["$", f.toFixed(2), (0, t.jsx)(c.ChevronRight, {
                className: "h-4 w-4",
                strokeWidth: 2.2,
                "aria-hidden": "true"
              })]
            })]
          })]
        })]
      })]
    }) : null
  }), document.body);
};e.s(["default",0,ef]);});

  Z.define(901101, e=>{var t = e.i(843476);
var a = e.i(271645);
var i = e.i(618566);
var n = e.i(657688);
var r = e.i(88653);
var l = e.i(846932);
var o = e.i(772328);
var f = e.i(282625);
var F = e.i(75183);
var C = e.i(200416);
var z = e.i(126486);
var E = e.i(763566);
var P = e.i(701688);
var I = e.i(990746);
var L = e.i(273341);
var Z = e.i(678162);
var O = e.i(420865);
var U = e.i(652348);
var q = e.i(515746);
var et = e.i(328623);
var eu = e.i(977617);
var eA = e.i(770703);
var eP = e.i(768877);
var eI = e.i(689664);
var eL = e.i(882505);
var e_ = e.i(632781);
var eW = e.i(849862);
var eT = e.i(819536);
var e$ = e.i(238325);
var eR = e.i(999627);
var eZ = (0, eA.default)(() => e.A(493239), {
  loadableGenerated: {
    modules: [254803]
  },
  ssr: !1,
  loading: () => (0, t.jsx)("div", {
    className: "flex h-full min-h-[220px] items-center justify-center bg-[#F5F5F7]",
    children: (0, t.jsxs)("div", {
      className: "text-center",
      children: [(0, t.jsx)(e_.Loader2, {
        className: "mx-auto h-6 w-6 animate-spin text-brand-primary",
        strokeWidth: 2,
        "aria-hidden": "true"
      }), (0, t.jsx)("p", {
        className: "mt-2 text-[11px] font-medium text-brand-ink/48",
        children: "Loading store map…"
      })]
    })
  })
});
function eD({
  className: e = ""
}) {
  return (0, t.jsx)(n.default, {
    src: "/brands/zeta/zeta-logo-horizontal.png",
    alt: "Zeta Greek Kitchen",
    width: 602,
    height: 92,
    className: `h-auto object-contain ${e}`,
    priority: !0
  });
}
function eO({
  onSkip: e
}) {
  return (0, t.jsxs)("header", {
    className: "mx-auto flex w-full max-w-[960px] shrink-0 items-center justify-between px-5 pb-3 pt-[max(18px,env(safe-area-inset-top))] md:px-7",
    children: [(0, t.jsx)(eD, {
      className: "w-[148px]"
    }), (0, t.jsx)("button", {
      type: "button",
      onClick: e,
      className: "min-h-11 px-1 text-[13px] font-semibold text-brand-ink/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
      children: "Skip"
    })]
  });
}
function eB({
  active: e
}) {
  return (0, t.jsxs)("div", {
    className: "flex items-center justify-between",
    children: [(0, t.jsx)("ol", {
      className: "flex items-center gap-2",
      "aria-label": `Onboarding step ${e + 1} of 3`,
      children: Array.from({
        length: 3
      }, (a, i) => (0, t.jsx)("li", {
        "aria-current": i === e ? "step" : void 0,
        className: `h-1.5 rounded-full transition-all ${i === e ? "w-7 bg-brand-primary" : "w-1.5 bg-brand-ink/14"}`
      }, i))
    }), (0, t.jsxs)("span", {
      className: "text-[10px] font-semibold tabular-nums text-brand-ink/38",
      children: [e + 1, " / ", 3]
    })]
  });
}
function eM({
  children: e,
  disabled: a = !1,
  onClick: i
}) {
  return (0, t.jsx)("button", {
    type: "button",
    disabled: a,
    onClick: i,
    className: "flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[14px] bg-brand-primary px-5 text-[16px] font-semibold text-white shadow-[0_14px_28px_-20px_rgba(0,64,240,0.8)] transition-transform active:scale-[0.99] disabled:opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45 focus-visible:ring-offset-2",
    children: e
  });
}
function eU(e) {
  if ("string" != typeof e) return "";
  let t = e.trim();
  return t.startsWith("http") ? t : "";
}
function eq(e, t) {
  let a = String(e.onlineKey || ""),
    i = a ? t?.[a] : null;
  return {
    image: eU(i?.shop_image || e.shop_image),
    logo: eU(i?.shop_logo || e.shop_logo) || "/brands/zeta/zeta-logo-horizontal.png"
  };
}
function eY(e, t) {
  let a = String(e.onlineKey || ""),
    i = a ? t?.[a] : null;
  return i ? i.isPaused ? {
    label: "Orders paused",
    tone: "quiet"
  } : i.isOpen ? {
    label: "Open now",
    tone: "open"
  } : {
    label: "Closed now",
    tone: "quiet"
  } : {
    label: "Checking hours",
    tone: "quiet"
  };
}
function eG({
  branch: e,
  allConfigs: a,
  branchStatuses: i
}) {
  let n = eq(e, a),
    s = eY(e, i),
    r = Array.isArray(e.tags) && e.tags.length > 0 ? e.tags.slice(0, 2) : ["Pickup"];
  return (0, t.jsx)("div", {
    className: "mt-2.5 overflow-hidden rounded-[16px] border border-brand-primary bg-[#EAF0FF]",
    children: (0, t.jsxs)("div", {
      className: "flex min-h-[158px]",
      children: [(0, t.jsxs)("div", {
        className: "relative w-[148px] shrink-0 overflow-hidden bg-brand-primary/8 sm:w-[164px]",
        style: n.image ? void 0 : {
          backgroundImage: 'url("/brands/zeta/pattern-meander.webp")',
          backgroundPosition: "center",
          backgroundSize: "220px auto"
        },
        children: [n.image ? (0, t.jsx)(eu.default, {
          src: n.image,
          alt: `${e.name} store`,
          fill: !0,
          sizes: "126px",
          className: "object-cover",
          context: "ZetaOnboarding.selectedStoreImage",
          meta: {
            branch: e.id
          },
          fallbackSrc: "/brands/zeta/pattern-meander.webp"
        }) : (0, t.jsx)("div", {
          className: "absolute inset-0 bg-brand-primary/75",
          "aria-hidden": "true"
        }), (0, t.jsx)("span", {
          className: "absolute bottom-3 left-3 right-3 h-11 overflow-hidden rounded-[10px] bg-white/95 px-2 shadow-[0_6px_18px_rgba(19,19,22,0.18)]",
          children: (0, t.jsx)(eu.default, {
            src: n.logo,
            alt: "",
            fill: !0,
            sizes: "136px",
            className: "object-contain p-2",
            context: "ZetaOnboarding.selectedStoreLogo",
            meta: {
              branch: e.id
            },
            fallbackSrc: "/brands/zeta/zeta-logo-horizontal.png"
          })
        })]
      }), (0, t.jsxs)("div", {
        className: "min-w-0 flex-1 p-3.5 sm:p-4",
        children: [(0, t.jsxs)("div", {
          className: "flex items-center justify-between gap-2",
          children: [(0, t.jsxs)("span", {
            className: `flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.09em] ${"open" === s.tone ? "text-emerald-600" : "text-brand-ink/48"}`,
            children: [(0, t.jsx)("span", {
              className: `h-1.5 w-1.5 rounded-full ${"open" === s.tone ? "bg-emerald-500" : "bg-brand-ink/24"}`,
              "aria-hidden": "true"
            }), s.label]
          }), (0, t.jsx)("span", {
            className: "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white",
            children: (0, t.jsx)(eI.Check, {
              className: "h-3 w-3",
              strokeWidth: 3,
              "aria-hidden": "true"
            })
          })]
        }), (0, t.jsx)("p", {
          className: "mt-2 line-clamp-2 text-[16px] font-semibold leading-tight",
          children: e.name
        }), (0, t.jsxs)("p", {
          className: "mt-2 flex gap-1.5 text-[11px] leading-[1.4] text-brand-ink/50",
          children: [(0, t.jsx)(O.MapPin, {
            className: "mt-px h-3.5 w-3.5 shrink-0",
            strokeWidth: 2,
            "aria-hidden": "true"
          }), (0, t.jsx)("span", {
            className: "line-clamp-2",
            children: e.address
          })]
        }), (0, t.jsx)("div", {
          className: "mt-3 flex flex-wrap gap-1.5",
          children: r.map(e => (0, t.jsx)("span", {
            className: "rounded-full bg-white/85 px-2.5 py-1 text-[9px] font-semibold text-brand-primary",
            children: String(e)
          }, String(e)))
        })]
      })]
    })
  });
}
function eH({
  reducedMotion: e,
  onNext: a,
  onSkip: i
}) {
  let s = [{
    icon: z.UtensilsCrossed,
    label: "Order fresh",
    detail: "Pickup or delivery"
  }, {
    icon: et.Sparkles,
    label: "Zeta Zone",
    detail: "Deals and rewards"
  }, {
    icon: F.Store,
    label: "Find a Zeta",
    detail: "Connected stores near you"
  }];
  return (0, t.jsxs)(l.motion.section, {
    "data-onboarding-step": "overview",
    className: "absolute inset-0 flex flex-col overflow-hidden bg-white text-brand-ink",
    initial: !e && {
      opacity: 0,
      y: 14
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: e ? void 0 : {
      opacity: 0,
      y: -10
    },
    transition: {
      duration: .28 * !e,
      ease: [.2, .8, .2, 1]
    },
    children: [(0, t.jsx)(eO, {
      onSkip: i
    }), (0, t.jsxs)("div", {
      className: "zeta-onboarding-content mx-auto grid min-h-0 w-full max-w-[960px] flex-1 overflow-y-auto px-5 pb-4 md:grid-cols-2 md:content-center md:gap-x-7 md:gap-y-4 md:px-7",
      children: [(0, t.jsxs)(l.motion.div, {
        initial: e ? void 0 : {
          opacity: 0,
          y: 18
        },
        animate: e ? void 0 : {
          opacity: 1,
          y: 0
        },
        transition: {
          duration: .42,
          delay: .06
        },
        children: [(0, t.jsx)("p", {
          className: "text-[10px] font-bold uppercase tracking-[0.18em] text-brand-primary",
          children: "Welcome to the Zeta app"
        }), (0, t.jsxs)("h1", {
          className: "mt-2 max-w-[350px] text-[38px] font-extrabold leading-[0.98] tracking-[-0.045em]",
          children: ["Everything Zeta,", (0, t.jsx)("br", {}), "in one place."]
        }), (0, t.jsx)("p", {
          className: "mt-3 max-w-[345px] text-[14px] leading-[1.5] text-brand-ink/58",
          children: "Get to your favourites faster, keep your rewards close and always know where to find us."
        })]
      }), (0, t.jsxs)(l.motion.div, {
        className: "relative mt-5 h-[214px] overflow-hidden rounded-[18px] border border-brand-primary/18 bg-[#F5F5F7] md:row-span-2 md:mt-0 md:h-full md:min-h-[370px] md:rounded-[24px]",
        initial: e ? void 0 : {
          opacity: 0,
          scale: .97
        },
        animate: e ? void 0 : {
          opacity: 1,
          scale: 1
        },
        transition: {
          duration: .48,
          delay: .12
        },
        children: [(0, t.jsx)(n.default, {
          src: "/brands/zeta/mezze.webp",
          alt: "A spread of Zeta Greek favourites",
          fill: !0,
          priority: !0,
          sizes: "(max-width: 480px) calc(100vw - 40px), 440px",
          className: "object-cover object-[50%_48%]"
        }), (0, t.jsxs)("div", {
          className: "absolute bottom-3 left-3 right-3 flex items-center gap-3 rounded-[13px] bg-white px-3 py-2.5 shadow-[0_8px_24px_rgba(19,19,22,0.16)]",
          children: [(0, t.jsx)("span", {
            className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-brand-primary text-white",
            children: (0, t.jsx)(z.UtensilsCrossed, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          }), (0, t.jsxs)("span", {
            className: "min-w-0 flex-1",
            children: [(0, t.jsx)("span", {
              className: "block text-[13px] font-semibold",
              children: "Your favourites are closer"
            }), (0, t.jsx)("span", {
              className: "mt-0.5 block text-[10px] text-brand-ink/48",
              children: "Menu, order history and quick reorder"
            })]
          })]
        })]
      }), (0, t.jsx)("div", {
        className: "mt-3 grid grid-cols-3 gap-2 md:self-start",
        children: s.map(({
          icon: a,
          label: i,
          detail: n
        }, s) => (0, t.jsxs)(l.motion.div, {
          className: "min-h-[92px] rounded-[14px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
          initial: e ? void 0 : {
            opacity: 0,
            y: 12
          },
          animate: e ? void 0 : {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: .34,
            delay: .17 + .05 * s
          },
          children: [(0, t.jsx)(a, {
            className: "h-[18px] w-[18px] text-brand-primary",
            strokeWidth: 2,
            "aria-hidden": "true"
          }), (0, t.jsx)("p", {
            className: "mt-2 text-[11px] font-semibold leading-tight",
            children: i
          }), (0, t.jsx)("p", {
            className: "mt-1 text-[9px] leading-tight text-brand-ink/45",
            children: n
          })]
        }, i))
      })]
    }), (0, t.jsx)("div", {
      className: "shrink-0 border-t border-brand-ink/8 bg-white px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-4 md:px-7",
      children: (0, t.jsxs)("div", {
        className: "mx-auto w-full max-w-[906px] md:flex md:items-center md:justify-between md:gap-8",
        children: [(0, t.jsx)("div", {
          className: "md:flex-1",
          children: (0, t.jsx)(eB, {
            active: 0
          })
        }), (0, t.jsx)("div", {
          className: "mt-4 md:mt-0 md:w-[360px]",
          children: (0, t.jsxs)(eM, {
            onClick: a,
            children: ["Set up my app", (0, t.jsx)(eP.ArrowRight, {
              className: "h-5 w-5",
              strokeWidth: 2.3,
              "aria-hidden": "true"
            })]
          })
        })]
      })
    })]
  }, "overview");
}
function eV({
  branches: e,
  selectedBranch: i,
  allConfigs: n,
  branchStatuses: s,
  userLocation: o,
  locationStatus: d,
  reducedMotion: c,
  onSelect: x,
  onLocate: m,
  onNext: p,
  onSkip: u
}) {
  let h = (0, a.useRef)(null),
    [b, f] = (0, a.useState)("map");
  return (0, a.useEffect)(() => {
    let e = window.requestAnimationFrame(() => {
      h.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView({
        block: "nearest",
        inline: "nearest"
      });
    });
    return () => window.cancelAnimationFrame(e);
  }, [i?.id]), (0, t.jsxs)(l.motion.section, {
    "data-onboarding-step": "store",
    className: "absolute inset-0 flex flex-col overflow-hidden bg-white text-brand-ink",
    initial: !c && {
      opacity: 0,
      x: "4%"
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: c ? void 0 : {
      opacity: 0,
      x: "-4%"
    },
    transition: {
      duration: .28 * !c,
      ease: [.2, .8, .2, 1]
    },
    children: [(0, t.jsx)(eO, {
      onSkip: u
    }), (0, t.jsxs)("div", {
      className: "zeta-onboarding-content mx-auto flex min-h-0 w-full max-w-[960px] flex-1 flex-col overflow-y-auto px-5 pb-4 md:grid md:grid-cols-[0.85fr_1.15fr] md:content-start md:gap-8 md:px-7 md:pt-10 lg:content-center lg:pt-0",
      children: [(0, t.jsxs)("div", {
        children: [(0, t.jsxs)(l.motion.div, {
          initial: c ? void 0 : {
            opacity: 0,
            y: 16
          },
          animate: c ? void 0 : {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: .38,
            delay: .04
          },
          children: [(0, t.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.18em] text-brand-primary",
            children: "Your ordering store"
          }), (0, t.jsx)("h1", {
            className: "mt-2 text-[32px] font-extrabold leading-[0.98] tracking-[-0.045em] sm:text-[36px]",
            children: "Choose your Zeta."
          }), (0, t.jsx)("p", {
            className: "mt-3 max-w-[390px] text-[13px] leading-[1.45] text-brand-ink/58 sm:text-[14px]",
            children: "We’ll use this store for menu availability, pickup times and local Zeta Zone deals."
          })]
        }), (0, t.jsxs)("button", {
          type: "button",
          disabled: "loading" === d,
          onClick: m,
          className: "mt-4 flex min-h-[48px] w-full shrink-0 items-center justify-center gap-2 rounded-[13px] border border-brand-primary/28 bg-[#EAF0FF] px-4 text-[13px] font-semibold text-brand-primary disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
          children: ["loading" === d ? (0, t.jsx)(e_.Loader2, {
            className: "h-[18px] w-[18px] animate-spin",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }) : "success" === d ? (0, t.jsx)(eI.Check, {
            className: "h-[18px] w-[18px]",
            strokeWidth: 2.3,
            "aria-hidden": "true"
          }) : (0, t.jsx)(Z.LocateFixed, {
            className: "h-[18px] w-[18px]",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), "loading" === d ? "Finding your nearest Zeta…" : "success" === d ? `${i?.name || "Nearest Zeta"} selected` : "error" === d ? "Try location again" : "Use my location"]
        }), "error" === d ? (0, t.jsx)("p", {
          className: "mt-2 text-center text-[11px] text-brand-ink/48",
          children: "Location is unavailable. Choose a store below instead."
        }) : null]
      }), (0, t.jsxs)("div", {
        className: "mt-4 flex min-h-0 min-w-0 w-full shrink-0 flex-col overflow-x-hidden md:mt-0",
        children: [(0, t.jsxs)("div", {
          className: "mb-2 flex items-center justify-between gap-3 px-0.5",
          children: [(0, t.jsxs)("p", {
            className: "text-[10px] font-semibold text-brand-ink/48",
            children: [e.length, " connected ", 1 === e.length ? "store" : "stores"]
          }), i ? (0, t.jsxs)("p", {
            className: "max-w-[58%] truncate text-right text-[9px] font-semibold text-brand-primary",
            children: ["Selected · ", i.name]
          }) : null]
        }), (0, t.jsx)("div", {
          className: "mb-3 grid grid-cols-2 rounded-[13px] bg-[#F5F5F7] p-1",
          role: "group",
          "aria-label": "Choose how to view Zeta stores",
          children: [{
            id: "map",
            label: "Show on map",
            icon: eW.Map
          }, {
            id: "list",
            label: "Show as list",
            icon: eL.List
          }].map(({
            id: e,
            label: a,
            icon: i
          }) => (0, t.jsxs)("button", {
            type: "button",
            "aria-pressed": b === e,
            onClick: () => {
              (0, L.haptic)("selection"), f(e);
            },
            className: `flex min-h-10 items-center justify-center gap-2 rounded-[10px] px-3 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45 ${b === e ? "bg-white text-brand-primary shadow-[0_4px_14px_rgba(19,19,22,0.08)]" : "text-brand-ink/48"}`,
            children: [(0, t.jsx)(i, {
              className: "h-4 w-4",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), a]
          }, e))
        }), (0, t.jsx)(r.AnimatePresence, {
          mode: "wait",
          initial: !1,
          children: "map" === b ? (0, t.jsxs)(l.motion.div, {
            "data-onboarding-store-map": !0,
            className: "min-h-0",
            initial: !c && {
              opacity: 0,
              y: 6
            },
            animate: {
              opacity: 1,
              y: 0
            },
            exit: c ? void 0 : {
              opacity: 0,
              y: -4
            },
            transition: {
              duration: .2 * !c
            },
            children: [(0, t.jsx)("div", {
              className: "h-[220px] overflow-hidden rounded-[16px] border border-brand-primary/16 bg-[#F5F5F7] md:h-[360px]",
              children: (0, t.jsx)(eZ, {
                branches: e,
                selectedBranch: i,
                onSelectBranch: t => {
                  let a = e.find(e => String(e.id) === String(t));
                  a && x(a);
                },
                userLocation: o,
                compact: !0,
                showPopupActions: !1
              })
            }), i ? (0, t.jsx)(eG, {
              branch: i,
              allConfigs: n,
              branchStatuses: s
            }) : (0, t.jsx)("p", {
              className: "mt-2.5 rounded-[13px] bg-[#F5F5F7] px-3 py-2.5 text-center text-[11px] text-brand-ink/48",
              children: "Tap a map marker to choose your ordering store."
            })]
          }, "map") : (0, t.jsx)(l.motion.div, {
            ref: h,
            "data-onboarding-store-list": !0,
            className: "grid w-full min-w-0 max-w-full max-h-[42dvh] gap-2 overflow-x-hidden overflow-y-auto overscroll-y-contain pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:max-h-[360px]",
            initial: !c && {
              opacity: 0,
              y: 6
            },
            animate: {
              opacity: 1,
              y: 0
            },
            exit: c ? void 0 : {
              opacity: 0,
              y: -4
            },
            transition: {
              duration: .2 * !c
            },
            children: e.map((e, a) => {
              let r = i?.id === e.id,
                o = eq(e, n),
                d = eY(e, s);
              return (0, t.jsxs)(l.motion.button, {
                type: "button",
                onClick: () => x(e),
                "aria-pressed": r,
                className: `flex min-h-[68px] min-w-0 w-full max-w-full overflow-hidden items-center gap-3 rounded-[15px] border px-3.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45 md:min-h-[72px] ${r ? "border-brand-primary bg-[#EAF0FF]" : "border-brand-ink/9 bg-[#F5F5F7]"}`,
                initial: c ? void 0 : {
                  opacity: 0,
                  y: 8
                },
                animate: c ? void 0 : {
                  opacity: 1,
                  y: 0
                },
                transition: {
                  duration: .24,
                  delay: .035 * a
                },
                children: [(0, t.jsx)("span", {
                  className: `relative h-11 w-11 shrink-0 overflow-hidden rounded-[11px] ${r ? "bg-brand-primary" : "bg-white"}`,
                  children: o.image ? (0, t.jsx)(eu.default, {
                    src: o.image,
                    alt: "",
                    fill: !0,
                    sizes: "44px",
                    className: "object-cover",
                    context: "ZetaOnboarding.storeListImage",
                    meta: {
                      branch: e.id
                    },
                    fallbackSrc: "/brands/zeta/pattern-meander.webp"
                  }) : (0, t.jsx)(eu.default, {
                    src: o.logo,
                    alt: "",
                    fill: !0,
                    sizes: "44px",
                    className: "object-contain p-1.5",
                    context: "ZetaOnboarding.storeListLogo",
                    meta: {
                      branch: e.id
                    },
                    fallbackSrc: "/brands/zeta/zeta-logo-horizontal.png"
                  })
                }), (0, t.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, t.jsx)("span", {
                    className: "block truncate text-[14px] font-semibold",
                    children: e.name
                  }), (0, t.jsxs)("span", {
                    className: `mt-0.5 flex items-center gap-1 text-[9px] font-semibold ${"open" === d.tone ? "text-emerald-600" : "text-brand-ink/40"}`,
                    children: [(0, t.jsx)("span", {
                      className: `h-1.5 w-1.5 rounded-full ${"open" === d.tone ? "bg-emerald-500" : "bg-brand-ink/20"}`,
                      "aria-hidden": "true"
                    }), d.label]
                  }), (0, t.jsx)("span", {
                    className: "mt-1 block truncate text-[10px] text-brand-ink/48",
                    children: e.address
                  })]
                }), (0, t.jsx)("span", {
                  className: `flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${r ? "border-brand-primary bg-brand-primary text-white" : "border-brand-ink/18 bg-white"}`,
                  children: r ? (0, t.jsx)(eI.Check, {
                    className: "h-3 w-3",
                    strokeWidth: 3,
                    "aria-hidden": "true"
                  }) : null
                })]
              }, e.id);
            })
          }, "list")
        })]
      })]
    }), (0, t.jsx)("div", {
      className: "shrink-0 border-t border-brand-ink/8 bg-white px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-4 md:px-7",
      children: (0, t.jsxs)("div", {
        className: "mx-auto w-full max-w-[906px] md:flex md:items-center md:justify-between md:gap-8",
        children: [(0, t.jsx)("div", {
          className: "md:flex-1",
          children: (0, t.jsx)(eB, {
            active: 1
          })
        }), (0, t.jsx)("div", {
          className: "mt-4 md:mt-0 md:w-[420px]",
          children: (0, t.jsxs)(eM, {
            disabled: !i,
            onClick: p,
            children: ["Continue with ", i?.name || "a store"]
          })
        })]
      })
    })]
  }, "store");
}
function eK({
  branchName: e,
  isSignedIn: a,
  memberName: i,
  membershipLabel: n,
  points: s,
  reducedMotion: r,
  onStartOrdering: o,
  onOpenZone: d,
  onSkip: c
}) {
  return (0, t.jsxs)(l.motion.section, {
    "data-onboarding-step": "zone",
    className: "absolute inset-0 flex flex-col overflow-hidden bg-[#F5F5F7] text-brand-ink",
    initial: !r && {
      opacity: 0,
      x: "4%"
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: r ? void 0 : {
      opacity: 0
    },
    transition: {
      duration: .28 * !r,
      ease: [.2, .8, .2, 1]
    },
    children: [(0, t.jsx)(eO, {
      onSkip: c
    }), (0, t.jsxs)("div", {
      className: "zeta-onboarding-content mx-auto grid min-h-0 w-full max-w-[960px] flex-1 auto-rows-max overflow-y-auto px-5 pb-4 md:grid-cols-[0.9fr_1.1fr] md:content-center md:gap-8 md:px-7",
      children: [(0, t.jsxs)(l.motion.div, {
        initial: r ? void 0 : {
          opacity: 0,
          y: 15
        },
        animate: r ? void 0 : {
          opacity: 1,
          y: 0
        },
        transition: {
          duration: .38,
          delay: .04
        },
        children: [(0, t.jsx)("p", {
          className: "text-[10px] font-bold uppercase tracking-[0.18em] text-brand-primary",
          children: "Your member space"
        }), (0, t.jsx)("h1", {
          className: "mt-2 text-[36px] font-extrabold leading-[0.98] tracking-[-0.045em]",
          children: "Meet Zeta Zone."
        }), (0, t.jsx)("p", {
          className: "mt-3 max-w-[350px] text-[14px] leading-[1.5] text-brand-ink/58",
          children: "Preview reward progress, member coupons and app-only deals. Your physical member card and points activity stay in Wallet."
        }), (0, t.jsxs)("div", {
          className: "mt-5 grid grid-cols-2 gap-2",
          children: [(0, t.jsxs)("div", {
            className: "rounded-[14px] border border-brand-primary/14 bg-white p-3",
            children: [(0, t.jsx)(f.Gift, {
              className: "h-[18px] w-[18px] text-brand-primary",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), (0, t.jsx)("p", {
              className: "mt-2 text-[12px] font-semibold",
              children: "Benefits at a glance"
            }), (0, t.jsx)("p", {
              className: "mt-1 text-[10px] leading-[1.35] text-brand-ink/48",
              children: "See what is ready before opening each detail page."
            })]
          }), (0, t.jsxs)("div", {
            className: "rounded-[14px] border border-brand-primary/14 bg-white p-3",
            children: [(0, t.jsx)(E.WalletCards, {
              className: "h-[18px] w-[18px] text-brand-primary",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), (0, t.jsx)("p", {
              className: "mt-2 text-[12px] font-semibold",
              children: "Wallet stays focused"
            }), (0, t.jsx)("p", {
              className: "mt-1 text-[10px] leading-[1.35] text-brand-ink/48",
              children: "Card, QR, balance rules and activity remain separate."
            })]
          })]
        })]
      }), (0, t.jsxs)(l.motion.div, {
        className: "mt-5 overflow-hidden rounded-[22px] border border-brand-primary/16 bg-white md:mt-0",
        initial: r ? void 0 : {
          opacity: 0,
          y: 20,
          scale: .97
        },
        animate: r ? void 0 : {
          opacity: 1,
          y: 0,
          scale: 1
        },
        transition: {
          duration: .46,
          delay: .1
        },
        children: [(0, t.jsxs)("div", {
          className: "flex items-center gap-3 bg-[#EAF0FF] p-4",
          children: [(0, t.jsx)("span", {
            className: "grid h-12 w-12 shrink-0 place-items-center rounded-[14px] bg-white text-[19px] font-extrabold text-brand-primary",
            children: i.trim().charAt(0).toUpperCase() || "Z"
          }), (0, t.jsxs)("span", {
            className: "min-w-0 flex-1",
            children: [(0, t.jsx)("span", {
              className: "block text-[9px] font-bold uppercase tracking-[0.12em] text-brand-primary",
              children: a ? n : "Member benefits"
            }), (0, t.jsx)("span", {
              className: "mt-1 block truncate text-[15px] font-bold",
              children: a ? i : "Sign in to personalise"
            }), (0, t.jsx)("span", {
              className: "mt-1 block truncate text-[10px] text-brand-ink/48",
              children: e
            })]
          }), (0, t.jsxs)("span", {
            className: "shrink-0 text-right",
            children: [(0, t.jsx)("span", {
              className: "block text-[22px] font-extrabold tabular-nums",
              children: s.toLocaleString()
            }), (0, t.jsx)("span", {
              className: "mt-1 block text-[9px] text-brand-ink/48",
              children: "points"
            })]
          })]
        }), (0, t.jsx)("div", {
          className: "divide-y divide-brand-ink/8",
          children: [{
            Icon: f.Gift,
            title: "Rewards",
            detail: "Progress and ready rewards"
          }, {
            Icon: C.TicketPercent,
            title: "Coupons",
            detail: "Live member offers"
          }, {
            Icon: et.Sparkles,
            title: "App deals",
            detail: "Offers from your selected store"
          }, {
            Icon: E.WalletCards,
            title: "Wallet",
            detail: "Member card and points activity"
          }].map(({
            Icon: e,
            title: a,
            detail: i
          }) => (0, t.jsxs)("div", {
            className: "flex min-h-[58px] w-full items-center gap-3 px-3.5",
            children: [(0, t.jsx)("span", {
              className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-[#EAF0FF] text-brand-primary",
              children: (0, t.jsx)(e, {
                className: "h-[17px] w-[17px]",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            }), (0, t.jsxs)("span", {
              className: "min-w-0 flex-1",
              children: [(0, t.jsx)("span", {
                className: "block text-[12px] font-semibold",
                children: a
              }), (0, t.jsx)("span", {
                className: "mt-0.5 block truncate text-[9px] text-brand-ink/45",
                children: i
              })]
            })]
          }, a))
        })]
      })]
    }), (0, t.jsx)("div", {
      className: "shrink-0 border-t border-brand-ink/8 bg-white px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-4 md:px-7",
      children: (0, t.jsxs)("div", {
        className: "mx-auto w-full max-w-[906px] md:flex md:items-center md:justify-between md:gap-8",
        children: [(0, t.jsx)("div", {
          className: "md:flex-1",
          children: (0, t.jsx)(eB, {
            active: 2
          })
        }), (0, t.jsxs)("div", {
          className: "mt-4 md:mt-0 md:flex md:w-[440px] md:items-center md:gap-2",
          children: [(0, t.jsx)("div", {
            className: "md:flex-1",
            children: (0, t.jsx)(eM, {
              onClick: o,
              children: "Start ordering"
            })
          }), (0, t.jsx)("button", {
            type: "button",
            onClick: d,
            className: "mt-2 min-h-11 w-full px-3 text-[12px] font-semibold text-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45 md:mt-0 md:w-auto",
            children: a ? "Open Zeta Zone" : "Sign in"
          })]
        })]
      })
    })]
  }, "zone");
}
function eX({
  enabled: e,
  onVisibilityChange: n
}) {
  let s = (0, i.useRouter)(),
    l = (0, o.useReducedMotion)(),
    {
      user: d,
      customerData: c,
      openAccountDrawer: x
    } = (0, I.useAuth)(),
    {
      walletData: m
    } = (0, e$.useLoyalty)(),
    {
      branches: p,
      currentBranch: u,
      setBranch: h,
      allConfigs: b,
      branchStatuses: f
    } = (0, U.useBranch)(),
    [g, y] = (0, a.useState)(null),
    [j, k] = (0, a.useState)(0),
    [w, v] = (0, a.useState)("idle"),
    [N, F] = (0, a.useState)(null);
  (0, P.useBodyScrollLock)(!!(e && g)), (0, a.useEffect)(() => {
    e && y(!(0, eR.hasCompletedZetaOnboarding)());
  }, [e]), (0, a.useEffect)(() => {
    e && null !== g && n?.(g);
  }, [e, n, g]), (0, a.useEffect)(() => {
    function e() {
      k(0), v("idle"), F(null), y(!0);
    }
    return window.addEventListener(eR.ZETA_ONBOARDING_REPLAY_EVENT, e), () => window.removeEventListener(eR.ZETA_ONBOARDING_REPLAY_EVENT, e);
  }, []);
  let C = p.filter(e => "coming-soon" !== e.status),
    S = Number(m?.walletPoints ?? m?.wallet_points ?? 0) || 0,
    z = c?.name || d?.displayName || "Future member",
    E = String(m?.membershipTypeObj?.name || m?.membershipType || "Zeta Rewards"),
    A = (0, a.useCallback)(() => {
      (0, L.haptic)("selection"), k(e => Math.min(e + 1, 2));
    }, []),
    _ = (0, a.useCallback)(() => {
      (0, eR.completeZetaOnboarding)(), (0, L.haptic)("selection"), y(!1);
    }, []),
    W = (0, a.useCallback)(e => {
      (0, L.haptic)("selection"), h(e), v("idle");
    }, [h]),
    T = (0, eT.useDialogFocus)({
      open: !!(e && g),
      onClose: _,
      focusKey: j
    }),
    $ = (0, a.useCallback)(async () => {
      v("loading"), (0, L.haptic)("selection");
      try {
        let {
          latitude: e,
          longitude: t
        } = await (0, q.getCurrentPosition)();
        F({
          latitude: e,
          longitude: t
        });
        let a = (0, q.findNearestBranch)(e, t, C);
        if (!a) throw Error("No branch found");
        h(a), v("success"), (0, L.haptic)("success");
      } catch {
        v("error"), (0, L.haptic)("warning");
      }
    }, [C, h]),
    R = (0, a.useCallback)(() => {
      _(), s.push("/menu");
    }, [_, s]),
    Z = (0, a.useCallback)(() => {
      (_(), d) ? s.push("/offers") : window.setTimeout(() => x(), 120);
    }, [_, x, s, d]);
  return e && g ? (0, t.jsx)("div", {
    ref: T,
    tabIndex: -1,
    "data-zeta-onboarding": !0,
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Welcome to Zeta",
    className: "fixed inset-0 z-[2000] overflow-hidden bg-white outline-none",
    children: (0, t.jsx)(r.AnimatePresence, {
      mode: "wait",
      children: 0 === j ? (0, t.jsx)(eH, {
        reducedMotion: !!l,
        onNext: A,
        onSkip: _
      }) : 1 === j ? (0, t.jsx)(eV, {
        branches: C,
        selectedBranch: u,
        allConfigs: b,
        branchStatuses: f,
        userLocation: N,
        locationStatus: w,
        reducedMotion: !!l,
        onSelect: W,
        onLocate: () => void $(),
        onNext: A,
        onSkip: _
      }) : (0, t.jsx)(eK, {
        branchName: u?.name || "Choose a store",
        isSignedIn: !!d,
        memberName: z,
        membershipLabel: E,
        points: S,
        reducedMotion: !!l,
        onStartOrdering: R,
        onOpenZone: Z,
        onSkip: _
      })
    })
  }) : null;
};e.s(["default",0,eX]);});

  Z.define(901102, e=>{var t = e.i(843476);
var a = e.i(271645);
var i = e.i(618566);
var n = e.i(657688);
var s = e.i(522016);
var r = e.i(88653);
var l = e.i(846932);
var o = e.i(772328);
var c = e.i(867927);
var h = e.i(235056);
var b = e.i(304729);
var f = e.i(282625);
var g = e.i(668109);
var y = e.i(810818);
var j = e.i(96315);
var k = e.i(274588);
var w = e.i(456420);
var v = (0, w.default)("route", [["circle", {
  cx: "6",
  cy: "19",
  r: "3",
  key: "1kj8tv"
}], ["path", {
  d: "M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15",
  key: "1d8sl"
}], ["circle", {
  cx: "18",
  cy: "5",
  r: "3",
  key: "gq8acd"
}]]);
var N = e.i(166794);
var F = e.i(75183);
var C = e.i(200416);
var S = e.i(466787);
var z = e.i(126486);
var E = e.i(763566);
var A = e.i(263676);
var P = e.i(701688);
var I = e.i(990746);
var L = e.i(273341);
var _ = [{
  title: "Plan with Zeta",
  links: [{
    label: "Favourites",
    detail: "Dishes you saved",
    href: "/favourites",
    icon: g.Heart
  }, {
    label: "Franchise",
    detail: "Bring Zeta to your area",
    href: "/franchise",
    icon: F.Store
  }]
}, {
  title: "Discover",
  links: [{
    label: "Our story",
    href: "/about",
    icon: y.Info
  }, {
    label: "Help & FAQ",
    href: "/help",
    icon: h.CircleHelp
  }, {
    label: "Contact Zeta",
    href: "/contact",
    icon: j.Mail
  }, {
    label: "Allergen information",
    href: "/allergen",
    icon: S.TriangleAlert
  }]
}, {
  title: "App",
  links: [{
    label: "Settings",
    href: "/settings",
    icon: N.Settings
  }]
}];
var W = [{
  label: "Favourites",
  href: "/favourites",
  icon: g.Heart
}, {
  label: "Member card",
  href: "/wallet",
  icon: E.WalletCards
}, {
  label: "Rewards",
  href: "/rewards",
  icon: f.Gift
}, {
  label: "Coupons",
  href: "/coupons",
  icon: C.TicketPercent
}];
function T({
  link: e,
  active: a,
  onClose: i
}) {
  let n = e.icon;
  return (0, t.jsxs)(s.default, {
    href: e.href,
    onClick: () => {
      (0, L.haptic)("selection"), i();
    },
    "aria-current": a ? "page" : void 0,
    className: `flex min-h-[46px] items-center gap-2.5 px-2.5 py-1.5 transition-colors active:bg-brand-ink/[0.05] ${a ? "bg-[#EAF0FF] text-brand-primary" : "text-brand-ink"}`,
    children: [(0, t.jsx)("span", {
      className: `grid h-8 w-8 shrink-0 place-items-center rounded-[10px] ${a ? "bg-white text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
      children: (0, t.jsx)(n, {
        className: "h-[17px] w-[17px]",
        strokeWidth: 1.9,
        "aria-hidden": "true"
      })
    }), (0, t.jsxs)("span", {
      className: "min-w-0 flex-1",
      children: [(0, t.jsx)("span", {
        className: `block truncate text-[13px] leading-tight ${a ? "font-bold" : "font-semibold"}`,
        children: e.label
      }), e.detail ? (0, t.jsx)("span", {
        className: "zeta-drawer-row-detail mt-0.5 block truncate text-[9px] leading-tight text-brand-ink-light",
        children: e.detail
      }) : null]
    }), e.badge ? (0, t.jsx)("span", {
      className: "shrink-0 rounded-full bg-[#EAF0FF] px-2 py-1 text-[8px] font-bold uppercase tracking-[0.08em] text-brand-primary",
      children: e.badge
    }) : (0, t.jsx)(c.ChevronRight, {
      className: `h-4 w-4 shrink-0 ${a ? "text-brand-primary" : "text-brand-ink/28"}`,
      strokeWidth: 2,
      "aria-hidden": "true"
    })]
  });
}
function $({
  isOpen: e,
  onClose: d
}) {
  let x = (0, i.usePathname)(),
    m = (0, o.useReducedMotion)(),
    p = (0, a.useRef)(null),
    {
      user: u,
      openAccountDrawer: h
    } = (0, I.useAuth)();
  return (0, P.useBodyScrollLock)(e), (0, a.useEffect)(() => {
    if (!e) return;
    p.current?.focus();
    let t = e => {
      "Escape" === e.key && d();
    };
    return window.addEventListener("keydown", t), () => window.removeEventListener("keydown", t);
  }, [e, d]), (0, t.jsx)(r.AnimatePresence, {
    children: e ? (0, t.jsxs)(l.motion.div, {
      className: "fixed inset-0 z-[130]",
      initial: !m && {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      },
      transition: {
        duration: .18 * !m
      },
      children: [(0, t.jsx)("button", {
        type: "button",
        "aria-label": "Close menu",
        onClick: d,
        className: "absolute inset-0 bg-brand-ink/38 backdrop-blur-[2px]"
      }), (0, t.jsxs)(l.motion.aside, {
        role: "dialog",
        "aria-modal": "true",
        "aria-label": "Zeta menu",
        initial: !m && {
          x: "-100%"
        },
        animate: {
          x: 0
        },
        exit: m ? void 0 : {
          x: "-100%"
        },
        transition: {
          type: "tween",
          ease: [.32, .72, 0, 1],
          duration: .26 * !m
        },
        className: "zeta-app-drawer absolute inset-y-0 left-0 flex max-h-[100dvh] w-[88%] max-w-[360px] flex-col overflow-hidden rounded-r-[24px] bg-white shadow-[16px_0_48px_rgba(19,19,22,0.18)]",
        children: [(0, t.jsxs)("header", {
          className: "flex h-[58px] shrink-0 items-center justify-between border-b border-brand-ink/8 px-4",
          style: {
            marginTop: "env(safe-area-inset-top, 0px)"
          },
          children: [(0, t.jsx)(n.default, {
            src: "/brands/zeta/zeta-logo-horizontal.png",
            alt: "Zeta Greek Kitchen",
            width: 112,
            height: 28,
            priority: !0,
            className: "h-[26px] w-auto object-contain"
          }), (0, t.jsx)("button", {
            ref: p,
            type: "button",
            onClick: d,
            "aria-label": "Close menu",
            className: "grid h-11 w-11 place-items-center rounded-[12px] bg-[#F5F5F7] text-brand-ink transition-colors active:bg-[#E8E8EC] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
            children: (0, t.jsx)(A.X, {
              className: "h-[18px] w-[18px]",
              strokeWidth: 2,
              "aria-hidden": "true"
            })
          })]
        }), (0, t.jsxs)("div", {
          className: "zeta-app-drawer__content flex min-h-0 flex-1 flex-col overflow-hidden",
          children: [(0, t.jsxs)("div", {
            className: "zeta-app-drawer__primary shrink-0 px-4 pb-2 pt-3",
            children: [(0, t.jsxs)(s.default, {
              href: "/menu",
              onClick: () => {
                (0, L.haptic)("selection"), d();
              },
              className: "flex h-12 items-center gap-3 rounded-[15px] bg-brand-primary px-3.5 text-white transition-colors active:bg-brand-primary-dark",
              children: [(0, t.jsx)("span", {
                className: "grid h-8 w-8 place-items-center rounded-[10px] bg-white/16",
                children: (0, t.jsx)(z.UtensilsCrossed, {
                  className: "h-[18px] w-[18px]",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              }), (0, t.jsx)("span", {
                className: "flex-1 text-[14px] font-bold",
                children: "Order now"
              }), (0, t.jsx)(c.ChevronRight, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 2.2,
                "aria-hidden": "true"
              })]
            }), (0, t.jsxs)("section", {
              className: "zeta-drawer-member-links mt-3",
              "aria-labelledby": "zeta-drawer-member-links",
              children: [(0, t.jsx)("h2", {
                id: "zeta-drawer-member-links",
                className: "px-1 pb-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-brand-ink-light",
                children: "My Zeta"
              }), (0, t.jsx)("div", {
                className: "grid grid-cols-3 gap-2",
                children: W.map(e => {
                  let a = e.icon,
                    i = x === e.href,
                    n = `flex min-h-[62px] touch-manipulation flex-col items-center justify-center gap-1 rounded-[13px] border px-1.5 text-center transition-colors active:bg-[#EAF0FF] ${i ? "border-brand-primary/25 bg-[#EAF0FF] text-brand-primary" : "border-brand-ink/7 bg-[#F5F5F7] text-brand-ink"}`,
                    r = (0, t.jsxs)(t.Fragment, {
                      children: [(0, t.jsx)(a, {
                        className: "h-[18px] w-[18px]",
                        strokeWidth: 1.9,
                        "aria-hidden": "true"
                      }), (0, t.jsx)("span", {
                        className: "text-[10px] font-semibold leading-none",
                        children: e.label
                      })]
                    });
                  return u ? (0, t.jsx)(s.default, {
                    href: e.href,
                    onClick: () => {
                      (0, L.haptic)("selection"), d();
                    },
                    "aria-current": i ? "page" : void 0,
                    className: n,
                    children: r
                  }, e.href) : (0, t.jsx)("button", {
                    type: "button",
                    onClick: () => {
                      (0, L.haptic)("selection"), d(), window.setTimeout(() => h(), 120);
                    },
                    "aria-label": `${e.label} — sign in required`,
                    className: n,
                    children: r
                  }, e.href);
                })
              })]
            })]
          }), (0, t.jsxs)("nav", {
            className: "zeta-app-drawer__body flex min-h-0 flex-1 flex-col px-4 pb-3",
            "aria-label": "Secondary navigation",
            children: [(0, t.jsx)("div", {
              className: "zeta-drawer-link-groups space-y-2",
              children: _.map(e => (0, t.jsxs)("section", {
                children: [(0, t.jsx)("h2", {
                  className: "px-1 pb-1.5 pt-1 text-[9px] font-bold uppercase tracking-[0.12em] text-brand-ink-light",
                  children: e.title
                }), (0, t.jsx)("div", {
                  className: "divide-y divide-brand-ink/6 overflow-hidden rounded-[15px] border border-brand-ink/7 bg-white",
                  children: e.links.map(e => (0, t.jsx)(T, {
                    link: e,
                    active: x === e.href,
                    onClose: d
                  }, e.href))
                })]
              }, e.title))
            }), (0, t.jsx)("footer", {
              className: "mt-auto pt-3",
              children: (0, t.jsxs)("div", {
                className: "flex items-center justify-between rounded-[13px] border border-brand-ink/8 bg-[#F5F5F7] px-3 py-2.5",
                children: [(0, t.jsxs)("div", {
                  className: "zeta-drawer-footer-note flex items-center gap-2 text-[10px] text-brand-ink-light",
                  children: [(0, t.jsx)(b.Clock3, {
                    className: "h-3.5 w-3.5 text-brand-primary",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  }), "Fresh Greek, made to order"]
                }), (0, t.jsxs)("div", {
                  className: "flex items-center gap-2.5 text-[10px] font-semibold",
                  children: [(0, t.jsx)(s.default, {
                    href: "/terms",
                    onClick: d,
                    className: "inline-flex min-h-11 items-center text-brand-ink-light active:text-brand-primary",
                    children: "Terms"
                  }), (0, t.jsx)(s.default, {
                    href: "/privacy",
                    onClick: d,
                    className: "inline-flex min-h-11 items-center text-brand-ink-light active:text-brand-primary",
                    children: "Privacy"
                  })]
                })]
              })
            })]
          })]
        })]
      })]
    }) : null
  });
};e.s(["default",0,$]);});

  Z.define(901103, e=>{var t = e.i(843476);
var a = e.i(271645);
var i = e.i(618566);
var r = e.i(88653);
var l = e.i(846932);
var o = e.i(772328);
var c = e.i(867927);
var u = e.i(394556);
var F = e.i(75183);
var A = e.i(263676);
var P = e.i(701688);
var L = e.i(273341);
var R = e.i(174080);
var Z = e.i(678162);
var D = e.i(611568);
var O = e.i(420865);
var B = e.i(578344);
var M = e.i(516306);
var U = e.i(652348);
var q = e.i(515746);
function Y({
  isOpen: e,
  onClose: n
}) {
  let s = (0, i.useRouter)(),
    d = (0, o.useReducedMotion)(),
    {
      branches: x,
      setBranch: m
    } = (0, U.useBranch)(),
    [p, h] = (0, a.useState)(!1),
    [b, f] = (0, a.useState)(!1),
    [g, y] = (0, a.useState)(""),
    [j, k] = (0, a.useState)(""),
    [w, v] = (0, a.useState)(null),
    [N, C] = (0, a.useState)("checking"),
    S = (0, a.useRef)(!1),
    z = (0, a.useRef)(null),
    E = (0, a.useRef)(null);
  (0, P.useBodyScrollLock)(e);
  let I = (0, a.useCallback)(async () => {
      if (!S.current) {
        S.current = !0, f(!0), y("");
        try {
          let e = await (0, q.getCurrentPosition)();
          C("granted");
          let t = (0, q.findNearestBranch)(e.latitude, e.longitude, x);
          v(t), t && m(t);
          let a = await (0, q.reverseGeocode)(e.latitude, e.longitude);
          k(a || "Location detected");
        } catch (e) {
          e?.code === 1 ? C("denied") : (C("error"), y("We could not determine your location. You can choose a Zeta store manually."));
        } finally {
          f(!1), S.current = !1;
        }
      }
    }, [x, m]),
    _ = (0, a.useCallback)(async () => {
      C("checking");
      try {
        if (!navigator.permissions?.query) return void C("prompt");
        let e = await navigator.permissions.query({
          name: "geolocation"
        });
        "granted" === e.state ? (C("fetching"), I()) : C("denied" === e.state ? "denied" : "prompt");
      } catch {
        C("prompt");
      }
    }, [I]);
  function W() {
    (0, L.haptic)("selection"), n(), s.push("/stores");
  }
  return ((0, a.useEffect)(() => h(!0), []), (0, a.useEffect)(() => {
    if (!e) return;
    E.current = document.activeElement;
    let t = requestAnimationFrame(() => z.current?.focus({
        preventScroll: !0
      })),
      a = e => {
        "Escape" === e.key && n();
      };
    return window.addEventListener("keydown", a), () => {
      cancelAnimationFrame(t), window.removeEventListener("keydown", a), E.current?.focus?.({
        preventScroll: !0
      });
    };
  }, [e, n]), (0, a.useEffect)(() => {
    let t;
    return e ? _() : t = setTimeout(() => {
      f(!1), y(""), k(""), v(null), C("checking");
    }, 250), () => {
      t && clearTimeout(t);
    };
  }, [_, e]), p) ? (0, R.createPortal)((0, t.jsx)(r.AnimatePresence, {
    children: e ? (0, t.jsxs)(t.Fragment, {
      children: [(0, t.jsx)(l.motion.button, {
        type: "button",
        "aria-label": "Close store finder",
        initial: !d && {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        },
        transition: {
          duration: .18 * !d
        },
        onClick: n,
        className: "fixed inset-0 z-[10000] bg-brand-ink/55 backdrop-blur-[2px]"
      }), (0, t.jsx)("div", {
        className: "zeta-app-sheet-host pointer-events-none fixed inset-0 z-[10001] flex items-end justify-center sm:items-center",
        children: (0, t.jsxs)(l.motion.section, {
          role: "dialog",
          "aria-modal": "true",
          "aria-labelledby": "store-finder-title",
          "aria-busy": b,
          initial: !d && {
            opacity: 0,
            y: 36,
            scale: .985
          },
          animate: {
            opacity: 1,
            y: 0,
            scale: 1
          },
          exit: d ? void 0 : {
            opacity: 0,
            y: 28,
            scale: .985
          },
          transition: {
            duration: .22 * !d,
            ease: [.32, .72, 0, 1]
          },
          className: "zeta-app-sheet zeta-app-sheet--dialog pointer-events-auto w-full max-w-[560px] overflow-y-auto rounded-t-[24px] bg-white px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-2 shadow-[0_-18px_55px_rgba(19,19,22,0.22)] sm:rounded-[24px] sm:pb-5",
          children: [(0, t.jsx)("div", {
            className: "mx-auto h-1 w-10 rounded-full bg-brand-ink/15",
            "aria-hidden": "true"
          }), (0, t.jsxs)("header", {
            className: "flex items-start gap-3 pb-4 pt-3",
            children: [(0, t.jsx)("span", {
              className: "grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-[#EAF0FF] text-brand-primary",
              children: (0, t.jsx)(O.MapPin, {
                className: "h-[21px] w-[21px]",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            }), (0, t.jsxs)("div", {
              className: "min-w-0 flex-1 pt-0.5",
              children: [(0, t.jsx)("p", {
                className: "text-[10px] font-bold uppercase tracking-[0.12em] text-brand-primary",
                children: "Order location"
              }), (0, t.jsx)("h2", {
                id: "store-finder-title",
                className: "mt-0.5 text-[20px] font-extrabold leading-tight tracking-[-0.02em] text-brand-ink",
                children: "Find your nearest Zeta"
              })]
            }), (0, t.jsx)("button", {
              ref: z,
              type: "button",
              onClick: () => {
                (0, L.haptic)("selection"), n();
              },
              "aria-label": "Close",
              className: "grid h-11 w-11 shrink-0 place-items-center rounded-[12px] bg-[#F5F5F7] text-brand-ink-light active:bg-brand-ink/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
              children: (0, t.jsx)(A.X, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 2,
                "aria-hidden": "true"
              })
            })]
          }), "checking" === N || "fetching" === N && b ? (0, t.jsx)("div", {
            className: "grid min-h-[190px] place-items-center rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] text-center",
            children: (0, t.jsxs)("div", {
              children: [(0, t.jsx)(D.LoaderCircle, {
                className: "zeta-app-motion-spin mx-auto h-7 w-7 animate-spin text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), (0, t.jsx)("p", {
                className: "mt-3 text-[13px] font-semibold text-brand-ink",
                children: "checking" === N ? "Checking location access" : "Finding your nearest store"
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[11px] text-brand-ink-light",
                children: "This should only take a moment."
              })]
            })
          }) : null, "prompt" === N ? (0, t.jsxs)("div", {
            children: [(0, t.jsxs)("div", {
              className: "flex gap-3 rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
              children: [(0, t.jsx)("span", {
                className: "grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-white text-brand-primary",
                children: (0, t.jsx)(Z.LocateFixed, {
                  className: "h-5 w-5",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("div", {
                children: [(0, t.jsx)("h3", {
                  className: "text-[14px] font-bold text-brand-ink",
                  children: "Use your current location"
                }), (0, t.jsxs)("p", {
                  className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                  children: ["Share it once and we'll match you with the closest ", u.default.name, " store."]
                })]
              })]
            }), (0, t.jsx)(H, {
              primaryLabel: "Find nearest store",
              primaryIcon: B.Navigation,
              onPrimary: () => {
                (0, L.haptic)("selection"), C("fetching"), I();
              },
              secondaryLabel: "Choose a store instead",
              onSecondary: W
            })]
          }) : null, "denied" === N ? (0, t.jsxs)("div", {
            children: [(0, t.jsxs)("div", {
              className: "flex gap-3 rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
              children: [(0, t.jsx)("span", {
                className: "grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-white text-brand-ink-light",
                children: (0, t.jsx)(M.ShieldAlert, {
                  className: "h-5 w-5",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              }), (0, t.jsxs)("div", {
                children: [(0, t.jsx)("h3", {
                  className: "text-[14px] font-bold text-brand-ink",
                  children: "Location access is off"
                }), (0, t.jsx)("p", {
                  className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                  children: "Enable it in your device settings, or select a store from the directory."
                })]
              })]
            }), (0, t.jsx)(H, {
              primaryLabel: "View all stores",
              primaryIcon: F.Store,
              onPrimary: W
            })]
          }) : null, "error" === N && g ? (0, t.jsxs)("div", {
            children: [(0, t.jsxs)("div", {
              className: "rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
              children: [(0, t.jsx)("h3", {
                className: "text-[14px] font-bold text-brand-ink",
                children: "Location unavailable"
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                children: g
              })]
            }), (0, t.jsx)(H, {
              primaryLabel: "View all stores",
              primaryIcon: F.Store,
              onPrimary: W
            })]
          }) : null, "granted" === N ? w ? (0, t.jsxs)("div", {
            children: [(0, t.jsxs)("div", {
              className: "overflow-hidden rounded-[18px] border border-brand-ink/8",
              children: [(0, t.jsx)(G, {
                icon: Z.LocateFixed,
                label: "Your location",
                value: j || "Location detected"
              }), (0, t.jsx)("div", {
                className: "mx-4 border-t border-brand-ink/8"
              }), (0, t.jsx)(G, {
                icon: F.Store,
                label: "Nearest store",
                value: w.name,
                highlighted: !0
              })]
            }), (0, t.jsx)(H, {
              primaryLabel: "Order from this store",
              primaryIcon: c.ChevronRight,
              onPrimary: function () {
                (0, L.haptic)("medium"), n(true), s.push("/menu");
              },
              secondaryLabel: "Choose another store",
              onSecondary: W
            })]
          }) : (0, t.jsxs)("div", {
            children: [(0, t.jsxs)("div", {
              className: "rounded-[18px] border border-brand-ink/8 bg-[#F5F5F7] p-4",
              children: [(0, t.jsx)("h3", {
                className: "text-[14px] font-bold text-brand-ink",
                children: "No nearby Zeta found"
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                children: "Your location was found, but there is no connected store close by."
              })]
            }), (0, t.jsx)(H, {
              primaryLabel: "View all stores",
              primaryIcon: F.Store,
              onPrimary: W
            })]
          }) : null]
        })
      })]
    }) : null
  }), document.body) : null;
}
function G({
  icon: e,
  label: a,
  value: i,
  highlighted: n = !1
}) {
  return (0, t.jsxs)("div", {
    className: "flex items-center gap-3 p-4",
    children: [(0, t.jsx)("span", {
      className: `grid h-10 w-10 shrink-0 place-items-center rounded-[13px] ${n ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink-light"}`,
      children: (0, t.jsx)(e, {
        className: "h-5 w-5",
        strokeWidth: 2,
        "aria-hidden": "true"
      })
    }), (0, t.jsxs)("div", {
      className: "min-w-0",
      children: [(0, t.jsx)("p", {
        className: "text-[10px] font-semibold text-brand-ink-light",
        children: a
      }), (0, t.jsx)("p", {
        "data-selectable": !0,
        className: `mt-0.5 break-words text-[14px] font-bold leading-snug ${n ? "text-brand-primary" : "text-brand-ink"}`,
        children: i
      })]
    })]
  });
}
function H({
  primaryLabel: e,
  primaryIcon: a,
  onPrimary: i,
  secondaryLabel: n,
  onSecondary: s
}) {
  return (0, t.jsxs)("div", {
    className: "mt-4 space-y-2",
    children: [(0, t.jsxs)("button", {
      type: "button",
      onClick: i,
      className: "flex h-12 w-full touch-manipulation items-center justify-center gap-2 rounded-[15px] bg-brand-primary px-4 text-[14px] font-bold text-white active:bg-brand-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
      children: [(0, t.jsx)(a, {
        className: "h-[18px] w-[18px]",
        strokeWidth: 2.2,
        "aria-hidden": "true"
      }), e]
    }), n && s ? (0, t.jsx)("button", {
      type: "button",
      onClick: s,
      className: "h-11 w-full touch-manipulation rounded-[14px] text-[13px] font-semibold text-brand-ink active:bg-[#F5F5F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
      children: n
    }) : null]
  });
};e.s(["default",0,Y]);});

  Z.define(902434, e => {
  "use strict";

  let t = (0, e.i(456420).default)("house", [["path", {
    d: "M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8",
    key: "5wwlr5"
  }], ["path", {
    d: "M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
    key: "r6nss1"
  }]]);
  e.s(["default", 0, t]);
});

  Z.define(911358, e => {
  "use strict";

  e.s(["getAuthErrorMessage", 0, function (e, t = "We could not complete sign-in. Please try again.") {
    let a = e && "object" == typeof e ? e : null,
      r = String("string" == typeof e ? e : a?.message ?? "").trim(),
      n = String(a?.code ?? "");
    return /invalid or expired (verification )?code|invalid (verification )?code|incorrect (verification )?code|invalid otp|otp.{0,20}(invalid|expired)|code.{0,20}(invalid|expired)/i.test(r) ? "That code is invalid or has expired. Please request a new code and try again." : /network|failed to fetch|load failed|timeout|timed out|econn|cors/i.test(r + " " + n) ? "We could not connect. Check your connection and try again." : /(session|registration).{0,20}expired|expired (session|registration)/i.test(r) ? "Your sign-in session expired. Please request a new code." : /store data not found|restaurant not found/i.test(r) ? "We could not verify the selected store. Please select a store and try again." : !r || /^(invalid|error|failed)$/i.test(r) || r.length > 160 || /firebase|auth\/|json|undefined|null|typeerror|syntaxerror|xhr|status code|https?:|<!doctype|<html|stack/i.test(r + " " + n) ? t : r;
  }]);
});

  Z.define(913734, e => {
  "use strict";

  var r = e.i(843476);
  let t = {
    root: "Preparing Zeta",
    checkout: "Preparing checkout",
    member: "Loading your Zeta membership",
    menu: "Loading the Zeta menu",
    offers: "Loading Zeta Zone",
    "order-confirmed": "Confirming your order",
    orders: "Loading your orders",
    profile: "Loading your account",
    stores: "Finding Zeta stores",
    track: "Finding your order",
    wallet: "Loading your wallet"
  };
  function a({
    className: e = "",
    delay: t = 0
  }) {
    return (0, r.jsx)("div", {
      className: `zeta-app-skeleton ${e}`,
      style: {
        "--skeleton-delay": `${t}ms`
      },
      "aria-hidden": "true"
    });
  }
  function n() {
    return (0, r.jsxs)("header", {
      className: "px-4 pb-3 pt-5",
      children: [(0, r.jsx)(a, {
        className: "h-3 w-28 rounded-full"
      }), (0, r.jsx)(a, {
        className: "mt-2 h-8 w-44 rounded-[10px]",
        delay: 40
      }), (0, r.jsx)(a, {
        className: "mt-2 h-3 w-64 max-w-[78vw] rounded-full",
        delay: 80
      })]
    });
  }
  function i({
    children: e,
    className: t = ""
  }) {
    return (0, r.jsx)("div", {
      className: `rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] p-3.5 ${t}`,
      children: e
    });
  }
  function s() {
    return (0, r.jsxs)(r.Fragment, {
      children: [(0, r.jsx)(n, {}), (0, r.jsxs)("main", {
        className: "space-y-4 px-4",
        children: [(0, r.jsx)(a, {
          className: "h-12 w-full rounded-[15px]"
        }), (0, r.jsx)("div", {
          className: "flex gap-2 overflow-hidden",
          children: [78, 98, 84, 92].map((e, t) => (0, r.jsx)(a, {
            className: "h-9 shrink-0 rounded-full",
            delay: 45 * t
          }, e)).map((e, t) => (0, r.jsx)("div", {
            style: {
              width: [78, 98, 84, 92][t]
            },
            children: e
          }, t))
        }), (0, r.jsxs)("div", {
          className: "flex items-center justify-between",
          children: [(0, r.jsx)(a, {
            className: "h-5 w-28 rounded-full"
          }), (0, r.jsx)(a, {
            className: "h-3 w-16 rounded-full"
          })]
        }), (0, r.jsx)("div", {
          className: "space-y-2.5",
          children: [0, 1, 2, 3].map(e => (0, r.jsxs)(i, {
            className: "flex min-h-[96px] items-center gap-3 bg-white ring-1 ring-brand-ink/8",
            children: [(0, r.jsx)(a, {
              className: "h-[72px] w-[72px] shrink-0 rounded-[14px]",
              delay: 55 * e
            }), (0, r.jsxs)("div", {
              className: "min-w-0 flex-1",
              children: [(0, r.jsx)(a, {
                className: "h-4 w-3/4 rounded-full",
                delay: 55 * e + 25
              }), (0, r.jsx)(a, {
                className: "mt-2 h-3 w-full rounded-full",
                delay: 55 * e + 45
              }), (0, r.jsx)(a, {
                className: "mt-3 h-4 w-16 rounded-full",
                delay: 55 * e + 65
              })]
            }), (0, r.jsx)(a, {
              className: "h-9 w-9 shrink-0 rounded-[12px]",
              delay: 55 * e + 85
            })]
          }, e))
        })]
      })]
    });
  }
  function l() {
    return (0, r.jsxs)(r.Fragment, {
      children: [(0, r.jsx)(n, {}), (0, r.jsxs)("main", {
        className: "space-y-3 px-4",
        children: [(0, r.jsx)(a, {
          className: "h-11 w-full rounded-[14px]"
        }), (0, r.jsxs)(i, {
          className: "flex items-center gap-3",
          children: [(0, r.jsx)(a, {
            className: "h-10 w-10 shrink-0 rounded-[12px]"
          }), (0, r.jsxs)("div", {
            className: "flex-1",
            children: [(0, r.jsx)(a, {
              className: "h-3 w-20 rounded-full"
            }), (0, r.jsx)(a, {
              className: "mt-2 h-4 w-40 rounded-full"
            })]
          }), (0, r.jsx)(a, {
            className: "h-8 w-16 rounded-full"
          })]
        }), [0, 1, 2].map(e => (0, r.jsxs)("div", {
          className: "rounded-[16px] border border-brand-ink/8 bg-white p-3.5",
          children: [(0, r.jsxs)("div", {
            className: "flex items-start justify-between gap-3",
            children: [(0, r.jsxs)("div", {
              className: "flex-1",
              children: [(0, r.jsx)(a, {
                className: "h-4 w-32 rounded-full",
                delay: 70 * e
              }), (0, r.jsx)(a, {
                className: "mt-2 h-3 w-24 rounded-full",
                delay: 70 * e + 25
              })]
            }), (0, r.jsx)(a, {
              className: "h-7 w-20 rounded-full",
              delay: 70 * e + 45
            })]
          }), (0, r.jsx)(a, {
            className: "mt-4 h-3 w-full rounded-full",
            delay: 70 * e + 65
          }), (0, r.jsxs)("div", {
            className: "mt-4 grid grid-cols-2 gap-2",
            children: [(0, r.jsx)(a, {
              className: "h-10 rounded-[12px]",
              delay: 70 * e + 85
            }), (0, r.jsx)(a, {
              className: "h-10 rounded-[12px]",
              delay: 70 * e + 105
            })]
          })]
        }, e))]
      })]
    });
  }
  function o() {
    return (0, r.jsxs)(r.Fragment, {
      children: [(0, r.jsx)(n, {}), (0, r.jsxs)("main", {
        className: "space-y-3 px-4",
        children: [(0, r.jsxs)("div", {
          className: "rounded-[20px] border border-brand-primary/15 bg-[#EAF0FF] p-4",
          children: [(0, r.jsx)(a, {
            className: "h-3 w-24 rounded-full"
          }), (0, r.jsx)(a, {
            className: "mt-3 h-7 w-44 rounded-[9px]",
            delay: 35
          }), (0, r.jsx)(a, {
            className: "mt-2 h-3 w-32 rounded-full",
            delay: 70
          }), (0, r.jsxs)("div", {
            className: "mt-7 flex items-end justify-between",
            children: [(0, r.jsx)(a, {
              className: "h-10 w-28 rounded-[10px]",
              delay: 105
            }), (0, r.jsx)(a, {
              className: "h-[74px] w-[74px] rounded-[14px]",
              delay: 140
            })]
          })]
        }), (0, r.jsx)("div", {
          className: "grid grid-cols-3 gap-2",
          children: [0, 1, 2].map(e => (0, r.jsx)(a, {
            className: "h-[82px] rounded-[14px]",
            delay: 55 * e
          }, e))
        }), [0, 1, 2, 3].map(e => (0, r.jsxs)(i, {
          className: "flex min-h-[64px] items-center gap-3",
          children: [(0, r.jsx)(a, {
            className: "h-10 w-10 shrink-0 rounded-[12px]",
            delay: 55 * e
          }), (0, r.jsxs)("div", {
            className: "flex-1",
            children: [(0, r.jsx)(a, {
              className: "h-4 w-36 rounded-full",
              delay: 55 * e + 25
            }), (0, r.jsx)(a, {
              className: "mt-2 h-3 w-3/4 rounded-full",
              delay: 55 * e + 45
            })]
          })]
        }, e))]
      })]
    });
  }
  function d() {
    return (0, r.jsxs)(r.Fragment, {
      children: [(0, r.jsx)(n, {}), (0, r.jsxs)("main", {
        className: "space-y-3 px-4",
        children: [(0, r.jsx)(a, {
          className: "h-12 rounded-[15px]"
        }), [0, 1, 2, 3].map(e => (0, r.jsxs)("div", {
          className: "rounded-[16px] border border-brand-ink/8 bg-white p-3.5",
          children: [(0, r.jsxs)("div", {
            className: "flex gap-3",
            children: [(0, r.jsx)(a, {
              className: "h-12 w-12 shrink-0 rounded-[13px]",
              delay: 50 * e
            }), (0, r.jsxs)("div", {
              className: "flex-1",
              children: [(0, r.jsx)(a, {
                className: "h-4 w-36 rounded-full",
                delay: 50 * e + 25
              }), (0, r.jsx)(a, {
                className: "mt-2 h-3 w-full rounded-full",
                delay: 50 * e + 45
              }), (0, r.jsx)(a, {
                className: "mt-2 h-3 w-2/3 rounded-full",
                delay: 50 * e + 65
              })]
            })]
          }), (0, r.jsxs)("div", {
            className: "mt-3 grid grid-cols-2 gap-2",
            children: [(0, r.jsx)(a, {
              className: "h-10 rounded-[12px]",
              delay: 50 * e + 85
            }), (0, r.jsx)(a, {
              className: "h-10 rounded-[12px]",
              delay: 50 * e + 105
            })]
          })]
        }, e))]
      })]
    });
  }
  function c() {
    return (0, r.jsxs)("main", {
      className: "space-y-3 px-4 pt-4",
      children: [(0, r.jsx)(a, {
        className: "h-[154px] w-full rounded-[20px]"
      }), (0, r.jsx)(a, {
        className: "h-12 w-full rounded-[15px]",
        delay: 40
      }), (0, r.jsxs)(i, {
        className: "flex h-[66px] items-center gap-3",
        children: [(0, r.jsx)(a, {
          className: "h-10 w-10 rounded-[12px]",
          delay: 80
        }), (0, r.jsxs)("div", {
          className: "flex-1",
          children: [(0, r.jsx)(a, {
            className: "h-4 w-40 rounded-full",
            delay: 110
          }), (0, r.jsx)(a, {
            className: "mt-2 h-3 w-52 rounded-full",
            delay: 140
          })]
        })]
      }), (0, r.jsxs)("div", {
        className: "flex items-center justify-between pt-1",
        children: [(0, r.jsx)(a, {
          className: "h-5 w-32 rounded-full",
          delay: 170
        }), (0, r.jsx)(a, {
          className: "h-3 w-16 rounded-full",
          delay: 190
        })]
      }), (0, r.jsx)("div", {
        className: "grid grid-cols-3 gap-2",
        children: [0, 1, 2].map(e => (0, r.jsx)(a, {
          className: "h-[120px] rounded-[15px]",
          delay: 210 + 45 * e
        }, e))
      }), (0, r.jsx)(a, {
        className: "h-5 w-40 rounded-full",
        delay: 340
      }), (0, r.jsxs)("div", {
        className: "grid grid-cols-2 gap-2",
        children: [(0, r.jsx)(a, {
          className: "h-[92px] rounded-[16px]",
          delay: 370
        }), (0, r.jsx)(a, {
          className: "h-[92px] rounded-[16px]",
          delay: 410
        })]
      })]
    });
  }
  function u() {
    return (0, r.jsxs)(r.Fragment, {
      children: [(0, r.jsx)(n, {}), (0, r.jsx)("main", {
        className: "space-y-3 px-4",
        children: [0, 1, 2, 3].map(e => (0, r.jsxs)("div", {
          className: "rounded-[16px] border border-brand-ink/8 bg-white p-4",
          children: [(0, r.jsx)(a, {
            className: "h-4 w-36 rounded-full",
            delay: 60 * e
          }), (0, r.jsx)(a, {
            className: "mt-4 h-12 w-full rounded-[14px]",
            delay: 60 * e + 25
          }), e < 2 ? (0, r.jsx)(a, {
            className: "mt-2 h-12 w-full rounded-[14px]",
            delay: 60 * e + 45
          }) : null]
        }, e))
      })]
    });
  }
  e.s(["default", 0, function ({
    kind: e = "root"
  }) {
    let a;
    return a = "menu" === e ? (0, r.jsx)(s, {}) : "orders" === e || "track" === e || "order-confirmed" === e ? (0, r.jsx)(l, {}) : "profile" === e || "wallet" === e || "member" === e || "offers" === e ? (0, r.jsx)(o, {}) : "stores" === e ? (0, r.jsx)(d, {}) : "checkout" === e ? (0, r.jsx)(u, {}) : (0, r.jsx)(c, {}), (0, r.jsxs)("section", {
      className: "min-h-[70vh] overflow-hidden bg-white pb-6 text-brand-ink",
      role: "status",
      "aria-live": "polite",
      "aria-busy": "true",
      "aria-label": t[e] || t.root,
      children: [(0, r.jsx)("span", {
        className: "sr-only",
        children: t[e] || t.root
      }), a]
    });
  }]);
});

  Z.define(922386, e => {
  "use strict";

  var t = e.i(394556);
  let a = `${t.default.storagePrefix}-favorite-orders`;
  function r() {
    try {
      let e = JSON.parse(window.localStorage.getItem(a) || "[]");
      return Array.isArray(e) ? e.filter(e => e?.id) : [];
    } catch {
      return [];
    }
  }
  function i(e) {
    let t = e?.items || e?.lineItems || e?.orderData || [],
      a = t.map(e => e.productName || e.name || e.label).filter(Boolean).slice(0, 2);
    if (!a.length) return t.length ? `${t.length} item${1 === t.length ? "" : "s"}` : "Past Zeta order";
    let r = t.length - a.length;
    return r > 0 ? `${a.join(", ")} + ${r} more` : a.join(", ");
  }
  e.s(["isFavoriteOrder", 0, function (e, t) {
    return e.some(e => String(e.id) === String(t));
  }, "readFavoriteOrders", 0, r, "summarizeOrderItems", 0, i, "toggleFavoriteOrder", 0, function (e) {
    let t = e?.id ? {
      id: String(e.id),
      onlineKey: e.onlineKey || "",
      branchName: e.branchName || "Zeta",
      createdAt: e.createdAt || null,
      total: e.total ?? null,
      itemSummary: i(e),
      savedAt: Date.now()
    } : null;
    if (!t) return {
      favorites: r(),
      isFavorite: !1
    };
    let s = r(),
      n = s.some(e => String(e.id) === t.id),
      l = n ? s.filter(e => String(e.id) !== t.id) : [t, ...s.filter(e => String(e.id) !== t.id)].slice(0, 12);
    try {
      window.localStorage.setItem(a, JSON.stringify(l));
    } catch {}
    return {
      favorites: l,
      isFavorite: !n
    };
  }]);
});

  Z.define(935829, e => {
  "use strict";

  let t = (0, e.i(456420).default)("minus", [["path", {
    d: "M5 12h14",
    key: "1ays0h"
  }]]);
  e.s(["Minus", 0, t], 935829);
});

  Z.define(956094, e => {
  "use strict";

  let t = (0, e.i(456420).default)("utensils", [["path", {
    d: "M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2",
    key: "cjf0a3"
  }], ["path", {
    d: "M7 2v20",
    key: "1473qp"
  }], ["path", {
    d: "M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7",
    key: "j28e5"
  }]]);
  e.s(["Utensils", 0, t], 956094);
});

  Z.define(958449, e => {
  "use strict";

  var a = e.i(843476),
    t = e.i(271645),
    s = e.i(618566),
    r = e.i(306555),
    n = e.i(375775),
    l = e.i(689664),
    i = e.i(508734),
    d = e.i(328623),
    o = e.i(763566),
    c = e.i(657688),
    m = e.i(972764);
  let u = (0, e.i(456420).default)("scan-line", [["path", {
    d: "M3 7V5a2 2 0 0 1 2-2h2",
    key: "aa7l1z"
  }], ["path", {
    d: "M17 3h2a2 2 0 0 1 2 2v2",
    key: "4qcy5o"
  }], ["path", {
    d: "M21 17v2a2 2 0 0 1-2 2h-2",
    key: "6vwrx8"
  }], ["path", {
    d: "M7 21H5a2 2 0 0 1-2-2v-2",
    key: "ioqczr"
  }], ["path", {
    d: "M7 12h10",
    key: "b7w52i"
  }]]);
  var p = e.i(377572),
    x = e.i(273341);
  function b({
    isGuest: e = !1,
    memberCode: t,
    memberName: s,
    membershipLabel: r,
    onOpen: n,
    points: l,
    storeName: i
  }) {
    let d = String(t || "").replace(/\s+/g, "").trim(),
      o = d ? `#${d}` : "•••• ••••",
      h = e ? "Future member" : s || "Zeta member";
    return (0, a.jsxs)("button", {
      type: "button",
      onClick: () => {
        (0, x.haptic)("selection"), n();
      },
      className: "relative mx-auto block w-full max-w-[460px] touch-manipulation overflow-hidden rounded-[9px] border border-brand-ink/16 bg-white text-left shadow-[0_14px_30px_-22px_rgba(19,19,22,0.5)] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/45",
      style: {
        aspectRatio: "1.586 / 1"
      },
      "aria-label": e ? "Inactive Zeta member card. Sign in to activate." : `${r} member card for ${h}, code ${d}`,
      children: [(0, a.jsx)(c.default, {
        src: "/brands/zeta/pattern-meander.webp",
        alt: "",
        fill: !0,
        sizes: "(max-width: 520px) calc(100vw - 32px), 460px",
        className: "pointer-events-none object-cover opacity-[0.055]"
      }), (0, a.jsxs)("span", {
        className: "relative flex h-full flex-col justify-between p-3.5",
        children: [(0, a.jsxs)("span", {
          className: "flex items-start justify-between gap-3",
          children: [(0, a.jsx)(c.default, {
            src: "/brands/zeta/zeta-logo-horizontal.png",
            alt: "Zeta Greek Kitchen",
            width: 132,
            height: 20,
            className: "h-auto w-[132px]"
          }), (0, a.jsx)("span", {
            className: "rounded-[4px] border border-brand-primary/25 bg-white/90 px-2.5 py-1 text-[8px] font-bold uppercase tracking-[0.12em] text-brand-primary",
            children: "Member card"
          })]
        }), (0, a.jsxs)("span", {
          className: "grid grid-cols-[minmax(0,1fr)_82px] items-center gap-3",
          children: [(0, a.jsxs)("span", {
            className: "min-w-0",
            children: [(0, a.jsx)("span", {
              className: "block text-[8px] font-bold uppercase tracking-[0.13em] text-brand-primary/70",
              children: "Member name"
            }), (0, a.jsx)("span", {
              className: "mt-1 block truncate text-[20px] font-extrabold leading-none text-brand-ink",
              children: h
            }), (0, a.jsx)("span", {
              className: "mt-3 block text-[8px] font-bold uppercase tracking-[0.13em] text-brand-ink/38",
              children: "Member number"
            }), (0, a.jsx)("span", {
              className: `mt-1 block truncate font-mono text-[14px] font-bold leading-none tracking-[0.06em] ${e ? "text-brand-ink/32" : "text-brand-primary"}`,
              children: o
            }), (0, a.jsxs)("span", {
              className: "mt-2 flex items-center gap-1.5 text-[8px] font-semibold text-brand-ink/45",
              children: [(0, a.jsx)(u, {
                className: "h-3.5 w-3.5 text-brand-primary",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), e ? "Sign in to reveal your QR" : "Scan at Zeta"]
            })]
          }), e ? (0, a.jsx)("span", {
            className: "grid h-[82px] w-[82px] place-items-center rounded-[6px] border border-brand-primary/20 bg-[#EAF0FF] text-brand-primary",
            children: (0, a.jsx)(m.LockKeyhole, {
              className: "h-7 w-7",
              strokeWidth: 1.8,
              "aria-hidden": "true"
            })
          }) : (0, a.jsx)(p.default, {
            value: d,
            size: 82
          })]
        }), (0, a.jsxs)("span", {
          className: "grid grid-cols-[0.7fr_1.15fr_1.15fr] items-end gap-2 border-t border-brand-primary/15 pt-2.5",
          children: [(0, a.jsxs)("span", {
            className: "min-w-0",
            children: [(0, a.jsx)("span", {
              className: "block text-[7px] font-bold uppercase tracking-[0.11em] text-brand-ink/38",
              children: "Points"
            }), (0, a.jsx)("span", {
              className: "mt-1 block truncate text-[12px] font-extrabold leading-none tabular-nums text-brand-ink",
              children: e ? "—" : l.toLocaleString()
            })]
          }), (0, a.jsxs)("span", {
            className: "min-w-0",
            children: [(0, a.jsx)("span", {
              className: "block text-[7px] font-bold uppercase tracking-[0.11em] text-brand-ink/38",
              children: "Membership"
            }), (0, a.jsx)("span", {
              className: "mt-1 block truncate text-[10px] font-extrabold leading-none text-brand-ink",
              children: e ? "Zeta Rewards" : r
            })]
          }), (0, a.jsxs)("span", {
            className: "min-w-0 text-right",
            children: [(0, a.jsx)("span", {
              className: "block text-[7px] font-bold uppercase tracking-[0.11em] text-brand-ink/38",
              children: "Selected store"
            }), (0, a.jsx)("span", {
              className: "mt-1 block truncate text-[10px] font-extrabold leading-none text-brand-ink",
              children: i
            })]
          })]
        })]
      })]
    });
  }
  var h = e.i(913734),
    g = e.i(528526),
    _ = e.i(990746),
    f = e.i(652348),
    j = e.i(238325),
    y = e.i(675378);
  function N() {
    return (0, a.jsx)(h.default, {
      kind: "wallet"
    });
  }
  e.s(["default", 0, function () {
    let e = (0, s.useRouter)(),
      c = (0, g.useZetaAppSurface)(),
      {
        user: m,
        customerData: u,
        loading: p
      } = (0, _.useAuth)(),
      {
        currentBranch: h,
        branches: w
      } = (0, f.useBranch)(),
      {
        walletData: k,
        walletTransactions: v,
        refreshLoyalty: P
      } = (0, j.useLoyalty)(),
      V = (0, t.useRef)(!1),
      [C, S] = (0, t.useState)(!1);
    (0, t.useEffect)(() => {
      p || (m ? V.current || (V.current = !0, P()) : e.replace("/"));
    }, [p, P, e, m]);
    let I = Number(k?.walletPoints ?? k?.wallet_points ?? 0) || 0,
      A = (0, y.getDisplayWalletRatio)(k, c, "PICKUP"),
      T = (0, y.summarizeRedemptionScope)(k, c),
      L = A?.ratioPoints || 0,
      M = A?.maximumConsumablePoints || 0,
      R = String(k?.membershipTypeObj?.name || k?.membershipType || "Zeta Rewards"),
      q = String(k?.customerCode || m?.uid || ""),
      G = (0, t.useMemo)(() => [...(v || [])].sort((e, a) => {
        let t = new Date(e.created_at || e.createdAt || e.date || 0).getTime();
        return new Date(a.created_at || a.createdAt || a.date || 0).getTime() - t;
      }), [v]);
    if (p || !m) return (0, a.jsx)(N, {});
    function W() {
      q && ((0, x.haptic)("selection"), navigator.clipboard?.writeText(q), S(!0), window.setTimeout(() => S(!1), 1500));
    }
    return (0, a.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, a.jsxs)("header", {
        className: "mx-auto w-full max-w-[760px] px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6",
        children: [(0, a.jsxs)("div", {
          className: "flex items-center gap-2 text-brand-primary",
          children: [(0, a.jsx)(o.WalletCards, {
            className: "h-4 w-4",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), (0, a.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.12em]",
            children: "Zeta member wallet"
          })]
        }), (0, a.jsx)("h1", {
          className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.025em] sm:text-[30px]",
          children: "Wallet"
        }), (0, a.jsx)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light sm:text-[13px]",
          children: "Your member card, points and activity."
        })]
      }), (0, a.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-4 px-4 sm:px-6",
        children: [(0, a.jsxs)("div", {
          className: "grid gap-4 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-start",
          children: [(0, a.jsxs)("div", {
            className: "space-y-3",
            children: [(0, a.jsx)(b, {
              memberCode: q,
              memberName: u?.name || m.displayName || "Zeta member",
              membershipLabel: R,
              onOpen: W,
              points: I,
              storeName: h?.name || "Choose a store"
            }), (0, a.jsxs)("button", {
              type: "button",
              onClick: W,
              disabled: !q,
              className: "flex min-h-12 w-full touch-manipulation items-center gap-3 rounded-[14px] bg-[#F5F5F7] px-3.5 text-left disabled:opacity-50",
              children: [(0, a.jsx)("span", {
                className: "grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white text-brand-primary",
                children: C ? (0, a.jsx)(l.Check, {
                  className: "h-4 w-4",
                  strokeWidth: 2.3,
                  "aria-hidden": "true"
                }) : (0, a.jsx)(i.Copy, {
                  className: "h-4 w-4",
                  strokeWidth: 2,
                  "aria-hidden": "true"
                })
              }), (0, a.jsxs)("span", {
                className: "min-w-0 flex-1",
                children: [(0, a.jsx)("span", {
                  className: "block text-[10px] text-brand-ink-light",
                  children: "Member number"
                }), (0, a.jsx)("span", {
                  className: "mt-0.5 block truncate font-mono text-[13px] font-bold tracking-[0.04em]",
                  children: q ? `#${q}` : "Pending"
                })]
              }), (0, a.jsx)("span", {
                className: "text-[11px] font-semibold text-brand-primary",
                children: C ? "Copied" : "Copy"
              })]
            })]
          }), (0, a.jsxs)("div", {
            className: "space-y-4",
            children: [(0, a.jsxs)("section", {
              className: "grid grid-cols-3 gap-2",
              "aria-label": "Wallet summary",
              children: [(0, a.jsxs)("div", {
                className: "rounded-[14px] bg-[#EAF0FF] p-3",
                children: [(0, a.jsx)("p", {
                  className: "text-[9px] font-semibold text-brand-primary",
                  children: "Balance"
                }), (0, a.jsx)("p", {
                  className: "mt-2 text-[18px] font-extrabold leading-none tabular-nums",
                  children: I.toLocaleString()
                }), (0, a.jsx)("p", {
                  className: "mt-1 text-[9px] text-brand-ink-light",
                  children: "points"
                })]
              }), (0, a.jsxs)("div", {
                className: "rounded-[14px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
                children: [(0, a.jsx)("p", {
                  className: "text-[9px] font-semibold text-brand-ink-light",
                  children: "Value"
                }), (0, a.jsx)("p", {
                  className: "mt-2 text-[18px] font-extrabold leading-none tabular-nums",
                  children: A ? `$${(I / L).toFixed(2)}` : "—"
                }), (0, a.jsx)("p", {
                  className: "mt-1 text-[9px] text-brand-ink-light",
                  children: "if enabled"
                })]
              }), (0, a.jsxs)("div", {
                className: "rounded-[14px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
                children: [(0, a.jsx)("p", {
                  className: "text-[9px] font-semibold text-brand-ink-light",
                  children: "Activity"
                }), (0, a.jsx)("p", {
                  className: "mt-2 text-[18px] font-extrabold leading-none tabular-nums",
                  children: G.length
                }), (0, a.jsx)("p", {
                  className: "mt-1 text-[9px] text-brand-ink-light",
                  children: "entries"
                })]
              })]
            }), (0, a.jsx)("section", {
              className: "rounded-[16px] border border-brand-ink/8 p-3.5",
              children: (0, a.jsxs)("div", {
                className: "flex items-start gap-3",
                children: [(0, a.jsx)("span", {
                  className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-[#EAF0FF] text-brand-primary",
                  children: (0, a.jsx)(d.Sparkles, {
                    className: "h-[18px] w-[18px]",
                    strokeWidth: 1.9,
                    "aria-hidden": "true"
                  })
                }), (0, a.jsxs)("div", {
                  className: "min-w-0 flex-1",
                  children: [(0, a.jsx)("h2", {
                    className: "text-[15px] font-bold",
                    children: "Using your points"
                  }), A ? (0, a.jsxs)(a.Fragment, {
                    children: [(0, a.jsxs)("p", {
                      className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                      children: [L.toLocaleString(), " point", 1 === L ? "" : "s", " equals $1 when redemption is enabled.", M > 0 ? ` Up to $${(M / L).toFixed(2)} can be used per order.` : ""]
                    }), T.scopeLabel ? (0, a.jsx)("span", {
                      className: "mt-2 inline-flex rounded-full bg-[#EAF0FF] px-2.5 py-1 text-[10px] font-semibold text-brand-primary",
                      children: T.scopeLabel
                    }) : null]
                  }) : (0, a.jsx)("p", {
                    className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                    children: "This store does not currently expose points redemption for this app context. You can still earn points here."
                  })]
                })]
              })
            })]
          })]
        }), (0, a.jsxs)("section", {
          children: [(0, a.jsx)("div", {
            className: "mb-2 flex items-end justify-between",
            children: (0, a.jsxs)("div", {
              children: [(0, a.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: "Points activity"
              }), (0, a.jsx)("p", {
                className: "mt-0.5 text-[10px] text-brand-ink-light",
                children: "Earned and redeemed through SIPO."
              })]
            })
          }), G.length ? (0, a.jsx)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8",
            children: G.map((e, t) => {
              let s,
                l = "credit" === String(e.transactionType || e.transaction_type || "").toLowerCase(),
                i = Math.abs(Number(e.walletPoints ?? e.wallet_points ?? e.points ?? 0)) || 0,
                d = e.createdAt || e.created_at || e.date,
                o = 349 === (s = Number(e.storePosOwnerId ?? e.restaurant_id)) ? "Zeta" : w.find(e => e.storeId === s)?.name || null;
              return (0, a.jsxs)("div", {
                className: "flex min-h-[62px] items-center gap-3 px-3.5 py-2.5",
                children: [(0, a.jsx)("span", {
                  className: `grid h-9 w-9 shrink-0 place-items-center rounded-[11px] ${l ? "bg-[#EAF0FF] text-brand-primary" : "bg-[#F5F5F7] text-brand-ink"}`,
                  children: l ? (0, a.jsx)(r.ArrowDownLeft, {
                    className: "h-4 w-4",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  }) : (0, a.jsx)(n.ArrowUpRight, {
                    className: "h-4 w-4",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  })
                }), (0, a.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, a.jsx)("span", {
                    className: "block text-[13px] font-semibold",
                    children: l ? "Points earned" : "Points redeemed"
                  }), (0, a.jsx)("span", {
                    className: "mt-0.5 block truncate text-[10px] text-brand-ink-light",
                    children: [o, d ? new Date(d).toLocaleDateString("en-NZ", {
                      day: "numeric",
                      month: "short",
                      year: "numeric"
                    }) : null].filter(Boolean).join(" · ")
                  })]
                }), (0, a.jsxs)("span", {
                  className: `shrink-0 text-[13px] font-bold tabular-nums ${l ? "text-brand-primary" : "text-brand-ink"}`,
                  children: [l ? "+" : "-", i.toLocaleString()]
                })]
              }, e.walletTransactionId || e.wallet_transaction_id || e.id || t);
            })
          }) : (0, a.jsxs)("div", {
            className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-4 py-6 text-center",
            children: [(0, a.jsx)("p", {
              className: "text-[14px] font-semibold",
              children: "No activity yet"
            }), (0, a.jsx)("p", {
              className: "mt-1 text-[11px] text-brand-ink-light",
              children: "Your points history will appear after you order."
            })]
          })]
        })]
      })]
    });
  }], 958449);
});

  Z.define(972764, e => {
  "use strict";

  let t = (0, e.i(456420).default)("lock-keyhole", [["circle", {
    cx: "12",
    cy: "16",
    r: "1",
    key: "1au0dj"
  }], ["rect", {
    x: "3",
    y: "10",
    width: "18",
    height: "12",
    rx: "2",
    key: "6s8ecr"
  }], ["path", {
    d: "M7 10V7a5 5 0 0 1 10 0v3",
    key: "1pqi11"
  }]]);
  e.s(["LockKeyhole", 0, t], 972764);
});

  Z.define(973438, e => {
  "use strict";

  function t(e) {
    return [...(e?.groupstoproduct || []), ...(e?.groups || []), ...(e?.group || [])];
  }
  e.s(["isCustomizable", 0, function (e) {
    let r = Array.isArray(e?.sizes) && e.sizes.length > 1,
      a = t(e).some(e => Array.isArray(e?.addOns) && e.addOns.length > 0);
    return r || a;
  }, "suggestionToCartItem", 0, function (e) {
    let r = Array.isArray(e.sizes) && e.sizes.length > 0 ? [...e.sizes].sort((e, t) => (e.position || 0) - (t.position || 0)) : [],
      a = r[0],
      i = t(e),
      n = [];
    for (let e of i) if (Array.isArray(e?.addOns)) for (let t of e.addOns) t?.preselect === 1 && n.push({
      addOnId: t.addOnId || t.id,
      addOnName: t.addOnName || t.name,
      price: parseFloat(String(t.price || 0)),
      _basePrice: parseFloat(String(t.price || 0)),
      count: 1,
      groupId: e.groupId || e.id,
      max_no_price_count: Math.max(0, Math.floor(Number(e.max_no_price_count) || 0)),
      preselect: 1,
      is_alert: t.is_alert || 0,
      alert_text: t.alert_text || null,
      position: t.position || null,
      availability: t.availability || null
    });
    return {
      product_id: e.productItemId,
      itemId: e.productItemId,
      label: e.name,
      name: e.name,
      sale_price: e.salePrice,
      imageUrl: e.imageUrl,
      image_url: e.imageUrl,
      sizes: r,
      size: a ? String(a.id) : "",
      group: e.group || [],
      groups: e.groups || [],
      groupstoproduct: e.groupstoproduct || [],
      modifiers: n,
      count: 1,
      is_combo: !1,
      availability: e.availability ?? null,
      is_inactive: e.is_inactive || 0,
      description: e.description || "",
      category_id: e.categoryId ?? null,
      categoryId: e.categoryId ?? null,
      is_loyalty_product: 0
    };
  }, "suggestionToMenuItem", 0, function (e) {
    return {
      itemId: e.productItemId,
      label: e.name,
      name: e.name,
      sale_price: e.salePrice,
      imageUrl: e.imageUrl,
      image_url: e.imageUrl,
      sizes: e.sizes || [],
      group: e.group || [],
      groups: e.groups || [],
      groupstoproduct: e.groupstoproduct || [],
      availability: e.availability ?? null,
      is_inactive: e.is_inactive || 0,
      tags: e.tags || null,
      description: e.description || "",
      category_id: e.categoryId ?? null,
      categoryId: e.categoryId ?? null,
      is_loyalty_product: 0
    };
  }]);
});

  Z.define(982996, e => {
  "use strict";

  var t = e.i(843476),
    a = e.i(271645),
    r = e.i(618566),
    s = e.i(306555),
    n = e.i(375775),
    i = e.i(199383),
    l = e.i(867927),
    d = e.i(282625),
    c = e.i(972764),
    o = e.i(341987),
    m = e.i(328623),
    x = e.i(763566),
    p = e.i(343699),
    u = e.i(977617),
    b = e.i(913734),
    h = e.i(528526),
    f = e.i(990746),
    g = e.i(652348),
    j = e.i(238325),
    N = e.i(309808),
    k = e.i(273341),
    y = e.i(675378);
  function w(e) {
    return Number(e?.pointsRequired ?? e?.points_required ?? 0) || 0;
  }
  function v(e) {
    return e?.productItemId || e?.itemId || e?.id || e?.name;
  }
  e.s(["default", 0, function () {
    let e = (0, r.useRouter)(),
      S = (0, N.useToast)(),
      _ = (0, h.useZetaAppSurface)(),
      {
        user: R,
        loading: L
      } = (0, f.useAuth)(),
      {
        currentBranch: I
      } = (0, g.useBranch)(),
      {
        walletData: T,
        walletTransactions: C,
        loyaltyProducts: P,
        isLoading: M,
        refreshLoyalty: $
      } = (0, j.useLoyalty)(),
      A = (0, a.useRef)(!1),
      [z, F] = (0, a.useState)(null);
    (0, a.useEffect)(() => {
      L || (R ? A.current || (A.current = !0, $()) : e.replace("/"));
    }, [L, $, e, R]);
    let q = Number(T?.walletPoints ?? T?.wallet_points ?? 0) || 0,
      D = (0, y.summarizeRedemptionScope)(T, _),
      E = (0, a.useMemo)(() => [...(P || [])].sort((e, t) => w(e) - w(t)), [P]),
      O = E.filter(e => w(e) > 0 && q >= w(e)).length,
      W = E.find(e => w(e) > q) || null,
      U = W ? w(W) : 0,
      B = Math.max(0, U - q),
      Y = U ? Math.min(100, q / U * 100) : 100,
      K = T?.referralCode || T?.referral_code,
      G = T?.birthdayReward || T?.birthday_reward;
    return L || !R ? (0, t.jsx)(b.default, {
      kind: "member"
    }) : (0, t.jsxs)("div", {
      className: "min-h-[70vh] bg-white pb-6 text-brand-ink",
      children: [(0, t.jsxs)("header", {
        className: "mx-auto w-full max-w-[760px] px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6",
        children: [(0, t.jsxs)("div", {
          className: "flex items-center gap-2 text-brand-primary",
          children: [(0, t.jsx)(d.Gift, {
            className: "h-4 w-4",
            strokeWidth: 2.1,
            "aria-hidden": "true"
          }), (0, t.jsx)("p", {
            className: "text-[10px] font-bold uppercase tracking-[0.12em]",
            children: "Zeta Rewards"
          })]
        }), (0, t.jsx)("h1", {
          className: "mt-1 text-[28px] font-extrabold leading-none tracking-[-0.025em] sm:text-[30px]",
          children: "Rewards"
        }), (0, t.jsx)("p", {
          className: "mt-1.5 text-[12px] text-brand-ink-light sm:text-[13px]",
          children: "Use your points on live rewards from this store."
        })]
      }), (0, t.jsxs)("main", {
        className: "mx-auto w-full max-w-[760px] space-y-4 px-4 sm:px-6",
        children: [(0, t.jsxs)("section", {
          className: "rounded-[16px] border border-brand-primary/16 bg-[#EAF0FF] p-3.5",
          children: [(0, t.jsxs)("div", {
            className: "flex items-start justify-between gap-3",
            children: [(0, t.jsxs)("div", {
              children: [(0, t.jsx)("p", {
                className: "text-[10px] font-semibold text-brand-primary",
                children: "Available balance"
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[30px] font-extrabold leading-none tracking-[-0.03em] tabular-nums",
                children: q.toLocaleString()
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[10px] text-brand-ink-light",
                children: "points"
              })]
            }), (0, t.jsxs)("button", {
              type: "button",
              onClick: () => {
                (0, k.haptic)("selection"), e.push("/wallet");
              },
              className: "flex min-h-11 touch-manipulation items-center gap-1.5 rounded-[12px] bg-white px-3 text-[11px] font-semibold text-brand-primary",
              children: [(0, t.jsx)(x.WalletCards, {
                className: "h-4 w-4",
                strokeWidth: 2,
                "aria-hidden": "true"
              }), "Wallet"]
            })]
          }), W ? (0, t.jsxs)("div", {
            className: "mt-3 border-t border-brand-primary/12 pt-3",
            children: [(0, t.jsxs)("div", {
              className: "flex items-center justify-between gap-3 text-[10px]",
              children: [(0, t.jsx)("span", {
                className: "min-w-0 truncate font-semibold",
                children: W.name || W.label || "Next reward"
              }), (0, t.jsxs)("span", {
                className: "shrink-0 text-brand-ink-light",
                children: [B.toLocaleString(), " to go"]
              })]
            }), (0, t.jsx)("div", {
              className: "mt-2 h-1.5 overflow-hidden rounded-full bg-white",
              children: (0, t.jsx)("div", {
                className: "h-full rounded-full bg-brand-primary",
                style: {
                  width: `${Y}%`
                }
              })
            })]
          }) : O ? (0, t.jsxs)("div", {
            className: "mt-3 flex items-center gap-2 border-t border-brand-primary/12 pt-3 text-[11px] font-semibold text-brand-primary",
            children: [(0, t.jsx)(m.Sparkles, {
              className: "h-4 w-4",
              strokeWidth: 2,
              "aria-hidden": "true"
            }), O, " reward", 1 === O ? "" : "s", " ready to redeem"]
          }) : null]
        }), !D.hasForContext && E.length ? (0, t.jsx)("section", {
          className: "rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3.5",
          children: (0, t.jsxs)("div", {
            className: "flex gap-3",
            children: [(0, t.jsx)("span", {
              className: "grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-white text-brand-primary",
              children: (0, t.jsx)(c.LockKeyhole, {
                className: "h-[18px] w-[18px]",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), (0, t.jsxs)("div", {
              children: [(0, t.jsx)("h2", {
                className: "text-[13px] font-semibold",
                children: "Redemption is unavailable here"
              }), (0, t.jsx)("p", {
                className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
                children: D.hasAny ? "This store has enabled redemption for a different ordering channel." : "You can still earn points, but this store has not enabled redemption yet."
              })]
            })]
          })
        }) : null, K || G ? (0, t.jsxs)("section", {
          className: "grid grid-cols-2 gap-2",
          children: [K ? (0, t.jsxs)("div", {
            className: "rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
            children: [(0, t.jsx)(o.Share2, {
              className: "h-5 w-5 text-brand-primary",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            }), (0, t.jsx)("p", {
              className: "mt-2 text-[12px] font-semibold",
              children: "Referral code"
            }), (0, t.jsx)("p", {
              className: "mt-1 truncate font-mono text-[11px] font-bold text-brand-primary",
              children: K
            })]
          }) : null, G ? (0, t.jsxs)("div", {
            className: "rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-3",
            children: [(0, t.jsx)(i.Cake, {
              className: "h-5 w-5 text-brand-primary",
              strokeWidth: 1.9,
              "aria-hidden": "true"
            }), (0, t.jsx)("p", {
              className: "mt-2 text-[12px] font-semibold",
              children: "Birthday reward"
            }), (0, t.jsx)("p", {
              className: "mt-1 line-clamp-2 text-[10px] leading-snug text-brand-ink-light",
              children: G.description || G.name || "Available"
            })]
          }) : null]
        }) : null, (0, t.jsxs)("section", {
          children: [(0, t.jsxs)("div", {
            className: "mb-2 flex items-end justify-between",
            children: [(0, t.jsxs)("div", {
              children: [(0, t.jsx)("h2", {
                className: "text-[16px] font-bold",
                children: "Reward menu"
              }), (0, t.jsx)("p", {
                className: "mt-0.5 text-[10px] text-brand-ink-light",
                children: I?.name || "Selected Zeta store"
              })]
            }), E.length ? (0, t.jsxs)("span", {
              className: "rounded-full bg-[#EAF0FF] px-2.5 py-1 text-[10px] font-semibold text-brand-primary",
              children: [O, " ready"]
            }) : null]
          }), M ? (0, t.jsx)("div", {
            className: "grid gap-2 md:grid-cols-2",
            "aria-label": "Loading reward menu",
            children: [0, 1, 2].map(e => (0, t.jsx)("div", {
              className: "zeta-app-skeleton h-[82px] rounded-[15px]"
            }, e))
          }) : E.length ? (0, t.jsx)("div", {
            className: "grid gap-2 md:grid-cols-2",
            children: E.map((e, a) => {
              let r = w(e),
                s = r > 0 && q >= r && D.hasForContext,
                n = e.name || e.label || "Reward",
                i = "string" == typeof e.imageUrl && e.imageUrl.trim();
              return (0, t.jsxs)("article", {
                className: "flex min-h-[82px] items-center gap-3 rounded-[15px] border border-brand-ink/8 bg-[#F5F5F7] p-2",
                children: [i ? (0, t.jsx)("span", {
                  className: "relative h-[66px] w-[66px] shrink-0 overflow-hidden rounded-[12px] bg-white",
                  children: (0, t.jsx)(u.default, {
                    src: e.imageUrl,
                    alt: "",
                    fill: !0,
                    sizes: "66px",
                    className: "object-contain p-1.5",
                    context: "MobileRewards.reward",
                    meta: {
                      id: v(e)
                    }
                  })
                }) : (0, t.jsx)("span", {
                  className: "grid h-[66px] w-[66px] shrink-0 place-items-center rounded-[12px] bg-white text-brand-primary",
                  children: s ? (0, t.jsx)(d.Gift, {
                    className: "h-5 w-5",
                    strokeWidth: 1.9,
                    "aria-hidden": "true"
                  }) : (0, t.jsx)(c.LockKeyhole, {
                    className: "h-5 w-5",
                    strokeWidth: 1.9,
                    "aria-hidden": "true"
                  })
                }), (0, t.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, t.jsx)("span", {
                    className: "line-clamp-2 text-[13px] font-semibold leading-tight",
                    children: n
                  }), e.description ? (0, t.jsx)("span", {
                    className: "mt-1 line-clamp-1 text-[10px] text-brand-ink-light",
                    children: e.description
                  }) : null, (0, t.jsxs)("span", {
                    className: "mt-1 block text-[10px] font-semibold text-brand-primary",
                    children: [r.toLocaleString(), " points"]
                  })]
                }), (0, t.jsx)("button", {
                  type: "button",
                  disabled: !s,
                  onClick: () => {
                    (0, k.haptic)("selection"), F({
                      ...e,
                      itemId: e.productItemId || e.itemId,
                      label: e.name || e.label || "",
                      is_loyalty_product: 1,
                      isLoyaltyAddedProduct: !0,
                      isWalletRedeemed: !0,
                      walletType: 2,
                      walletDiscount: 100,
                      points_required: e.pointsRequired ?? e.points_required ?? 0
                    });
                  },
                  className: `min-h-11 shrink-0 touch-manipulation rounded-[11px] px-3 text-[10px] font-semibold ${s ? "bg-brand-primary text-white" : "bg-white text-brand-ink-light"}`,
                  children: s ? "Redeem" : D.hasForContext ? `${Math.max(0, r - q).toLocaleString()} to go` : "Unavailable"
                })]
              }, v(e) || `${n}-${a}`);
            })
          }) : (0, t.jsxs)("div", {
            className: "rounded-[16px] border border-brand-ink/8 bg-[#F5F5F7] px-5 py-7 text-center",
            children: [(0, t.jsx)("span", {
              className: "mx-auto grid h-11 w-11 place-items-center rounded-[13px] bg-white text-brand-primary",
              children: (0, t.jsx)(d.Gift, {
                className: "h-5 w-5",
                strokeWidth: 1.9,
                "aria-hidden": "true"
              })
            }), (0, t.jsx)("h3", {
              className: "mt-3 text-[15px] font-bold",
              children: "No rewards at this store"
            }), (0, t.jsx)("p", {
              className: "mt-1 text-[11px] leading-relaxed text-brand-ink-light",
              children: "Rewards will appear automatically when SIPO returns eligible products."
            }), (0, t.jsx)("button", {
              type: "button",
              onClick: () => e.push("/menu"),
              className: "mt-4 h-11 rounded-[13px] bg-brand-primary px-5 text-[13px] font-semibold text-white",
              children: "Order to earn"
            })]
          })]
        }), C?.length ? (0, t.jsxs)("section", {
          children: [(0, t.jsx)("h2", {
            className: "mb-2 text-[16px] font-bold",
            children: "Recent points"
          }), (0, t.jsx)("div", {
            className: "divide-y divide-brand-ink/8 overflow-hidden rounded-[16px] border border-brand-ink/8",
            children: C.slice(0, 8).map((e, a) => {
              let r = Number(e.walletPoints ?? e.wallet_points ?? 0),
                i = r < 0,
                l = e.createdAt || e.created_at,
                d = l ? new Date(String(l).replace(" ", "T")) : null;
              return (0, t.jsxs)("div", {
                className: "flex min-h-[58px] items-center gap-3 px-3.5 py-2.5",
                children: [(0, t.jsx)("span", {
                  className: `grid h-8 w-8 shrink-0 place-items-center rounded-[10px] ${i ? "bg-[#F5F5F7] text-brand-ink" : "bg-[#EAF0FF] text-brand-primary"}`,
                  children: i ? (0, t.jsx)(n.ArrowUpRight, {
                    className: "h-4 w-4",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  }) : (0, t.jsx)(s.ArrowDownLeft, {
                    className: "h-4 w-4",
                    strokeWidth: 2,
                    "aria-hidden": "true"
                  })
                }), (0, t.jsxs)("span", {
                  className: "min-w-0 flex-1",
                  children: [(0, t.jsx)("span", {
                    className: "block text-[12px] font-semibold",
                    children: i ? "Points redeemed" : "Points earned"
                  }), d && !Number.isNaN(d.getTime()) ? (0, t.jsx)("span", {
                    className: "mt-0.5 block text-[10px] text-brand-ink-light",
                    children: d.toLocaleDateString("en-NZ", {
                      day: "numeric",
                      month: "short",
                      year: "numeric"
                    })
                  }) : null]
                }), (0, t.jsxs)("span", {
                  className: `text-[12px] font-bold tabular-nums ${i ? "text-brand-ink" : "text-brand-primary"}`,
                  children: [i ? "-" : "+", Math.abs(r).toLocaleString()]
                })]
              }, e.id || e.walletTransactionId || a);
            })
          })]
        }) : null, (0, t.jsxs)("button", {
          type: "button",
          onClick: () => e.push("/menu"),
          className: "flex min-h-12 w-full touch-manipulation items-center justify-between rounded-[14px] bg-brand-primary px-4 text-[14px] font-semibold text-white",
          children: [(0, t.jsx)("span", {
            children: "Order and earn more"
          }), (0, t.jsx)(l.ChevronRight, {
            className: "h-4 w-4",
            strokeWidth: 2.2,
            "aria-hidden": "true"
          })]
        })]
      }), (0, t.jsx)(p.default, {
        item: z,
        mode: "loyalty",
        onClose: () => F(null),
        onSuccess: () => S.success("Reward added to cart", {
          title: z?.label || z?.name,
          duration: 800
        })
      })]
    });
  }]);
});

  Z.define(986206, e => {
  "use strict";

  let t = (0, e.i(456420).default)("smartphone", [["rect", {
    width: "14",
    height: "20",
    x: "5",
    y: "2",
    rx: "2",
    ry: "2",
    key: "1yt0o3"
  }], ["path", {
    d: "M12 18h.01",
    key: "mhygvu"
  }]]);
  e.s(["Smartphone", 0, t], 986206);
});

  Z.define(988771, e => {
  "use strict";

  let t = (0, e.i(456420).default)("users-round", [["path", {
    d: "M18 21a8 8 0 0 0-16 0",
    key: "3ypg7q"
  }], ["circle", {
    cx: "10",
    cy: "8",
    r: "5",
    key: "o932ke"
  }], ["path", {
    d: "M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3",
    key: "10s06x"
  }]]);
  e.s(["UsersRound", 0, t], 988771);
});

  Z.define(994004, e => {
  "use strict";

  let r = (0, e.i(456420).default)("history", [["path", {
    d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8",
    key: "1357e3"
  }], ["path", {
    d: "M3 3v5h5",
    key: "1xhq8a"
  }], ["path", {
    d: "M12 7v5l4 2",
    key: "1fdv2h"
  }]]);
  e.s(["History", 0, r], 994004);
});
})();


// ===================== src/pages/zeta-enhancements.jsx =====================
// App-only view controllers for the Zeta navigation and welcome screens.
(function () {
  'use strict';

  var OO = window.OO,
    Z = OO.zetaReference,
    K = OO.appKit,
    A = Z.adapters;
  var assets = new Set(["/brands/zeta/catering-platter.webp", "/brands/zeta/hero-gyros.webp", "/brands/zeta/mezze.webp", "/brands/zeta/pattern-meander.webp", "/brands/zeta/zeta-logo-horizontal-white.svg", "/brands/zeta/zeta-logo-horizontal.png", "/brands/zeta/zeta-mark-white.svg"]);
  K.ZetaAsset = function (src) {
    if (typeof src !== 'string') return src;
    var path = src.replace(/^https:\/\/(?:app\.zeta\.sipo\.nz|app\.sipocloudpos\.com\/online-order)/, '');
    return assets.has(path) ? OO.asset('online-order' + path) : src;
  };
  K.ZetaSlotLabel = function (slot, full) {
    var value = String(slot || '').replace(' ', 'T');
    if (value === 'ASAP') return 'ASAP';
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null;
    var label = Z.require(376151).fmtSlotFull(value);
    if (full) return label;
    var parts = label.split(',');
    return parts[0] + ' ' + parts[parts.length - 1].trim().toLowerCase();
  };
  K.ZetaHero = function () {
    var route = OO.router.current(),
      design = OO.whitelabel.zetaDesign(route.chain || route.hotel);
    return design && design.hero || {};
  };
  var leaflet = null;
  Z.importView = function (id) {
    if (id !== 871400) return Promise.resolve(Z.require(id));
    if (window.L) return Promise.resolve({
      default: window.L
    });
    if (leaflet) return leaflet;
    leaflet = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = (OO.boot.assetRoot || '/') + 'online-order/vendor/leaflet.js';
      script.async = true;
      if (OO.boot.cspNonce) script.nonce = OO.boot.cspNonce;
      script.onload = function () {
        if (window.L) resolve({
          default: window.L
        });else {
          leaflet = null;
          reject(new Error('Store map could not load.'));
        }
      };
      script.onerror = function () {
        leaflet = null;
        reject(new Error('Store map could not load.'));
      };
      document.head.appendChild(script);
    });
    return leaflet;
  };
  A[770703] = {
    default: function () {
      return function MapView(props) {
        var Component = Z.require(414716).default;
        return <Component {...props} />;
      };
    }
  };
  delete A[675378];
  A[999627].hasCompletedZetaOnboarding = function () {
    return OO.appPreferences.read().welcome === false;
  };
  A[999627].ZETA_ONBOARDING_REPLAY_EVENT = 'oo-zeta-replay-welcome';
  A[515746].findNearestBranch = function (lat, lng, branches) {
    return branches.filter(function (b) {
      return OO.model.validCoords(b.lat, b.lng);
    }).map(function (b) {
      return {
        branch: b,
        km: OO.model.haversineKm({
          lat: lat,
          lng: lng
        }, {
          lat: b.lat,
          lng: b.lng
        })
      };
    }).sort(function (a, b) {
      return a.km - b.km;
    })[0]?.branch || null;
  };
  A[515746].reverseGeocode = function (lat, lng) {
    return OO.delivery.load().then(function (maps) {
      return (maps.importLibrary ? maps.importLibrary('geocoding') : Promise.resolve(maps)).then(function(geocoding){return new Promise(function (resolve) {
        new (geocoding.Geocoder || maps.Geocoder)().geocode({ location: { lat: lat, lng: lng } }, function (rows, status) {
          if (status !== 'OK' || !rows || !rows[0]) return resolve(null);
          var components = rows[0].address_components || [];
          var part = components.find(function (part) { return part.types.indexOf('sublocality') >= 0 || part.types.indexOf('locality') >= 0; });
          resolve(part && part.long_name || null);
        });
      });});
    }).catch(function () { return null; });
  };
  K.ZetaOrderIcons = {
    Bag: Z.require(263448).ShoppingBag, Pin: Z.require(578727).Bike,
    Calendar: Z.require(106501).CalendarClock,
    Clock: A[456420].default('zap', [['path', { d: 'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z', key: 'zap' }]])
  };
  K.ZetaOrderIcon = function(p){var Icon=K.ZetaOrderIcons[p.name]||OO.icons[p.name];return Icon ? <Icon size={p.size} strokeWidth={2}/> : null;};
  K.ZetaHeaderIcons = { Menu: Z.require(593583).Menu, Bag: K.ZetaOrderIcons.Bag };
  K.ZetaStoreFinder = function (p) {
    var Component = Z.require(901103).default;
    return <Component isOpen={p.open} onClose={p.onClose} />;
  };
  K.ZetaNavigationDrawer = function (p) {
    var Component = Z.require(901102).default,
      Provider = K.ZetaReferenceScreen.Provider;
    return <Provider route={p.route}><Component isOpen={p.open} onClose={p.onClose} /></Provider>;
  };
  K.AppWelcome = function (p) {
    var Component = Z.require(901101).default,
      Provider = K.ZetaReferenceScreen.Provider;
    return <Provider route={p.route}><Component enabled /></Provider>;
  };
})();


// ===================== src/pages/zeta-native-orders.jsx =====================
// Zeta tracker and rating services: public status only; receipts and writes require the current account's order.
(function () {
  'use strict';

  var OO = window.OO,
    Z = OO.zetaReference,
    A = Z.adapters,
    SDK = Z.require(917848);
  delete A[98828];
  A[519957].resolveBranchForOrder = function (order, onlineKey) {
    var r = OO.router.current(),
      key = onlineKey || order && order.onlineKey || r.hotel,
      id = order && Number(order.storeId || order.hotelId);
    return A[519957].branches.filter(function (b) {
      return b.onlineKey === key || id && b.storeId === id;
    })[0] || null;
  };
  function scope() {
    var r = OO.router.current(),
      s = OO.store.session(),
      h = OO.data.peekHotel(r.hotel, true);
    return {
      hotel: r.hotel,
      customer: s && s.customer,
      store: h && h.store
    };
  }
  async function owned(orderId) {
    var s = scope();
    if (!s.customer) return null;
    var result = await OO.pages.Review.logic.load(s.hotel, String(orderId), s.customer.uid, s.store);
    return result.found ? Object.assign(result, s) : null;
  }
  SDK.getOrderRating = async function (storeId, orderId) {
    try {
      var found = await owned(orderId);
      if (!found || Number(storeId) !== Number(found.storeId)) return {
        success: false
      };
      var r = found.review;
      return {
        success: true,
        data: r ? {
          rating: r.rating,
          feedback: r.feedback,
          items: (r.items || []).map(function (i) {
            return {
              productItemId: i.productId,
              rating: i.rating,
              feedback: i.feedback
            };
          })
        } : null
      };
    } catch (e) {
      return {
        success: false
      };
    }
  };
  SDK.submitOrderRating = async function (p) {
    try {
      var s = scope();
      if (!s.customer || s.customer.uid !== p.firebaseUID) throw new Error('Please sign in to rate your order.');
      var found = await owned(p.orderTrackingId);
      if (!found || Number(p.storePosOwnerId) !== Number(found.storeId)) throw new Error('This order is not available in your account.');
      if (!OO.model.canRateOrder(found.order) || found.off) throw new Error('This order cannot be rated yet.');
      var items = (p.items || []).map(function (i) {
        return {
          productId: i.productItemId,
          rating: i.rating,
          feedback: i.feedback
        };
      });
      var result = await OO.api.rateOrder(s.hotel, {
        uid: s.customer.uid,
        orderId: p.orderTrackingId,
        storeId: found.storeId,
        rating: p.rating,
        feedback: String(p.feedback || '').slice(0, 1000),
        items: items
      });
      return {
        success: !!result.status
      };
    } catch (e) {
      return {
        success: false,
        error: e.message || 'Your rating could not be saved.'
      };
    }
  };
  SDK.fetchOrderStatus = async function (id) {
    try {
      var s = scope(),
        raw = await OO.api.orderStatus(id, s.hotel);
      return Object.assign({}, raw, {
        success: OO.http.isSuccess(raw),
        error: OO.http.isSuccess(raw) ? null : 'Order not found.'
      });
    } catch (e) {
      return {
        success: false,
        error: e.kind === 'notFound' ? 'Order not found.' : 'Could not connect to the kitchen. Please try again.'
      };
    }
  };
  SDK.getOrderData = async function (id) {
    var s = scope();
    if (!s.customer) return {
      success: false
    };
    try {
      var route = OO.router.current(),
        chain = await OO.data.loadChain(route.chain || s.hotel),
        nativeBranches = chain && chain.stores || [],
        seed = Z.require(901002).locations,
        verified = seed.map(function (meta) {
          var b = nativeBranches.filter(function (x) {
            return x.key === meta.onlineKey;
          })[0];
          return b ? Object.assign({}, meta, {
            storeId: Number(b.id),
            address: b.address
          }) : null;
        }).filter(Boolean);
      if (!verified.length) verified = nativeBranches.map(function (b) {
        return {
          onlineKey: b.key,
          storeId: Number(b.id),
          name: b.name,
          address: b.address
        };
      });
      await OO.data.loadHotel(s.hotel);
      var all = await SDK.aggregateAllOrders(s.customer.uid, verified);
      var rows = (all.orders || []).concat(all.acceptedOrders || [], all.historyOrders || []);
      if (!Array.isArray(rows)) rows = rows.data || [];
      var hit = rows.filter(function (o) {
        return String(o.orderTrackingId || o.ticket_id || o.order_id || o.id) === String(id);
      })[0];
      return hit ? {
        success: true,
        data: hit
      } : {
        success: false
      };
    } catch (e) {
      return {
        success: false
      };
    }
  };
  SDK.getDeliveryTrackingLink = async function (id) {
    var last = OO.store.get().lastOrder,
      s = scope();
    if (!last || String(last.orderId) !== String(id) || last.hotel !== s.hotel || last.orderType !== 'DELIVERY') return {
      success: false
    };
    try {
      var result = await OO.api.getTrackingLink(id);
      return {
        success: !!result.url,
        data: result
      };
    } catch (e) {
      return {
        success: false
      };
    }
  };
  Z.orderServices = {
    owned: owned
  };
})();


// ===================== src/pages/zeta-checkout.jsx =====================
// The Zeta checkout view consumes the shared checkout controller; no pricing or payment rules live here.
(function () {
  'use strict';

  var OO = window.OO,
    K = OO.appKit,
    ui = OO.ui;
  function Section(p) {
    return <section className="zeta-checkout-section"><h2 className="zeta-checkout-section-title"><span className="zeta-checkout-app-step">{p.step}</span>{p.title}</h2>{p.children}</section>;
  }
  K.ZetaCheckout = function (p) {
    var confirm = React.useState(false),
      dialogRef = React.useRef(null),
      ZetaLink = K.ZetaReferenceScreen.Link,
      r = p.route,
      site = OO.whitelabel.siteFor(r.chain || r.hotel),
      brand = site && site.doc.brand || {},
      meta = OO.zetaReference.require(901002).locations.filter(function (b) {
        return b.onlineKey === r.hotel;
      })[0];
    var chain = OO.useChain(r.chain || r.hotel, {
        theme: false
      }),
      branch = chain.chain && chain.chain.stores.filter(function (b) {
        return b.key === r.hotel;
      })[0];
    var name = meta && meta.name || p.store.name,
      address = meta && meta.address || branch && branch.address || p.store.address && p.store.address.full || '',
      money = function (n) {
        return OO.format.money(n, p.store.currency);
      };
    function field(key, label, type, placeholder, ref) {
      return <div className={key === 'email' ? 'is-full' : undefined}><label htmlFor={'zeta-checkout-' + key} className="zeta-checkout-field-label">{label}{p.required ? ' *' : ' (Optional)'}</label><input id={'zeta-checkout-' + key} ref={ref} className="zeta-checkout-field" name={key} type={type || 'text'} autoComplete={key === 'phone' ? 'tel' : key} placeholder={placeholder} required={p.required} value={p.draft[key] || ''} aria-invalid={!!p.shown[key]} onChange={function (e) {
          p.setter(key)(e.target.value);
        }} />{p.shown[key] ? <p className="zeta-checkout-error" role="alert">{p.shown[key]}</p> : null}</div>;
    }
    React.useEffect(function () {
      document.title = 'Checkout | ' + (brand.name || 'Zeta Greek Kitchen');
    }, [brand.name]);
    React.useEffect(function () {
      if (!confirm[0]) return;
      var before = document.activeElement,
        overflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      var frame = requestAnimationFrame(function () {
        var first = dialogRef.current && dialogRef.current.querySelector('button');
        if (first) first.focus();
      });
      function key(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          confirm[1](false);
        }
        if (e.key === 'Tab' && dialogRef.current) {
          var list = Array.from(dialogRef.current.querySelectorAll('button,a,input,textarea,[tabindex]')).filter(function (n) {
              return !n.disabled;
            }),
            first = list[0],
            last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
      window.addEventListener('keydown', key);
      return function () {
        cancelAnimationFrame(frame);
        window.removeEventListener('keydown', key);
        document.body.style.overflow = overflow;
        if (before && before.isConnected) before.focus();
      };
    }, [confirm[0]]);
    var time = p.slot && p.slot.value !== 'ASAP' ? K.ZetaSlotLabel(p.slot.value, true) : 'ASAP · about ' + (p.delivery ? p.store.deliveryTimeMins : p.store.cookingTimeMins) + ' mins';
    function changeSetup() {
      OO.store.cartClear();
      OO.store.setCheckout({
        day: null,
        slot: null
      });
      OO.router.go(OO.router.href('menu', null, {
        hotel: r.hotel,
        query: {
          setup: 'true'
        }
      }));
    }
    return <div className="oo-zeta-native oo-zeta-checkout"><div className="zeta-checkout-native-shell">
      <div className="zeta-checkout-topbar"><button type="button" aria-label="Go back" onClick={p.back}><OO.icons.ChevronLeft size={20} /></button><img src={K.ZetaAsset ? K.ZetaAsset(brand.logo && brand.logo.light) : brand.logo && brand.logo.light} alt={brand.name || 'Zeta Greek Kitchen'} /></div>
      <div className="zeta-checkout-hero"><div className="zeta-checkout-hero-copy max-container section-padding"><h1 className="font-display">Checkout</h1><p className="zeta-checkout-hero-subtitle">Review and place your order from {name}</p></div></div>
      <div className="zeta-checkout-main max-container section-padding">
        <section className="zeta-checkout-app-plan"><div className="zeta-checkout-app-plan-heading"><div><p className="zeta-checkout-app-plan-kicker">Order setup</p><h2>Ready for checkout</h2></div><span className="zeta-checkout-app-plan-status"><OO.icons.Check size={14} />Confirmed</span></div>
          <div className="zeta-checkout-app-plan-store"><span className="zeta-checkout-app-plan-icon"><OO.icons.Pin size={20} /></span><div><p className="zeta-checkout-app-plan-store-name">{name}</p><p className="zeta-checkout-app-plan-address">{address}</p></div></div>
          <div className="zeta-checkout-app-plan-facts"><div><OO.icons.Store size={16} /><span>{p.delivery ? 'Delivery' : 'Pickup'}</span></div><div>{p.slot && p.slot.value !== 'ASAP' ? <OO.icons.Calendar size={16} /> : <OO.icons.Clock size={16} />}<span>{time}</span></div></div>
          <button type="button" className="zeta-checkout-app-plan-change" onClick={function () {
              confirm[1](true);
            }}>Change order setup</button><p className="zeta-checkout-app-plan-note">Changes are made from Menu and will clear your current cart.</p>
        </section>
        {p.paused}{p.repriced}{p.blocked}{p.notice}
        <div className="zeta-checkout-content-grid"><div className="zeta-checkout-form">
          <Section title="Contact details" step="1"><div className="zeta-checkout-contact-grid">{field('name', 'Full name', 'text', 'John Doe', p.nameRef)}{field('phone', 'Phone number', 'tel', '021 234 5678', p.phoneRef)}{field('email', 'Email address', 'email', 'john@example.com', p.emailRef)}
            <div className="is-full"><label className="zeta-checkout-field-label" htmlFor="zeta-checkout-notes">Order notes (optional)</label><textarea id="zeta-checkout-notes" className="zeta-checkout-field" maxLength={250} placeholder="Any special requests for the kitchen?" value={p.draft.notes || ''} onChange={function (e) {
                      p.setter('notes')(e.target.value);
                    }} /></div></div>
            {p.delivery ? p.address : null}
          </Section>
          <Section title="Payment method" step="2"><div role="radiogroup" aria-label="Payment method">{p.choices.map(function (c) {
                    var selected = !!p.choice && p.choice.id === c.id;
                    return <button type="button" role="radio" aria-checked={selected} key={c.id} className={'zeta-checkout-payment-option' + (selected ? ' is-selected' : '')} onClick={function () {
                      OO.store.setCheckout({
                        paymentMethod: c.key,
                        paymentChoice: c.id
                      });
                    }}><span className="zeta-checkout-payment-icon">{c.kind === 'gateway' ? <OO.icons.DollarCircle size={20} /> : <OO.icons.Store size={20} />}</span><span><p>{c.kind === 'gateway' ? 'Pay Online — ' + c.label : p.delivery ? 'Pay at door' : 'Pay at counter'}</p><p>{c.kind === 'gateway' ? 'Secure payment via ' + c.label : p.delivery ? 'Pay when your order arrives' : 'Pay when you collect your order'}</p></span><span className="zeta-checkout-payment-check">{selected ? <OO.icons.Check size={18} /> : null}</span></button>;
                  })}</div>{!p.choices.length ? <ui.InlineNotice tone="warn">No payment methods are currently available.</ui.InlineNotice> : null}</Section>
        </div><aside className="zeta-checkout-summary"><h3 className="zeta-checkout-summary-title">Order summary</h3><ul className="zeta-checkout-summary-lines">{p.lines.map(function (row) {
                  var l = row.line;
                  return <li key={row.key}><span>{l.qty}×</span><span><strong>{l.name}</strong>{OO.cart.displayMods(l) ? <small>{OO.cart.displayMods(l)}</small> : null}{l.notes ? <small>{l.notes}</small> : null}{row.free || l.reward ? <small>{row.free ? row.free.label : 'Reward'}</small> : null}</span><span>{money(OO.cart.lineTotal(l))}</span></li>;
                })}</ul><dl className="zeta-checkout-summary-rows">{p.sum.rows.filter(function (row) {
                  return row.key !== 'total' && (row.value !== 0 || ['subtotal', 'tax'].indexOf(row.key) >= 0);
                }).map(function (row) {
                  var label = {
                    subtotal: 'Subtotal',
                    tax: 'GST included',
                    surcharge: (row.label || p.store.surcharge?.label || 'Surcharge') + (p.store.surcharge && p.store.surcharge.percent ? ' (' + p.store.surcharge.percent + '%)' : '')
                  }[row.key] || row.label;
                  return <div key={row.key}><dt>{label}</dt><dd>{row.text}</dd></div>;
                })}<div className="zeta-checkout-order-total"><dt>Order total</dt><dd>{money(p.sum.total)}</dd></div><div className="zeta-checkout-total"><dt>Total payable</dt><dd>{money(p.sum.total)}</dd></div></dl>{p.tip}{p.offers}{p.wallet}</aside></div>
      </div><div className="zeta-checkout-action-bar"><p className="zeta-checkout-terms">By ordering, you agree to <ZetaLink href="/terms">Terms</ZetaLink> and <ZetaLink href="/privacy">Privacy</ZetaLink>.</p><div className="zeta-checkout-action-inner"><div><span className="zeta-checkout-action-total-label">Total</span><strong className="zeta-checkout-action-total">{money(p.sum.total)}</strong></div>{p.pay}</div>{p.preview}</div>
      {confirm[0] ? <div className="zeta-checkout-plan-dialog-wrap" role="presentation"><button type="button" className="zeta-checkout-plan-backdrop" aria-label="Keep current order setup" onClick={function () {
            confirm[1](false);
          }} /><section ref={dialogRef} className="zeta-checkout-plan-dialog" role="dialog" aria-modal="true" aria-labelledby="zeta-change-title"><div className="zeta-checkout-plan-dialog-icon"><OO.icons.Store size={22} /></div><h2 id="zeta-change-title">Change order setup?</h2><p>To change the store, fulfilment type or time, you’ll return to Menu and your current cart will be cleared.</p><div className="zeta-checkout-plan-dialog-actions"><button type="button" onClick={changeSetup}>Clear cart and continue</button><button type="button" onClick={function () {
                confirm[1](false);
              }}>Keep current order</button></div></section></div> : null}
      {p.timeStale && K.OrderPrompt ? <K.OrderPrompt hotel={r.hotel} store={p.store} route={r} /> : null}
      {p.dialogs}
    </div></div>;
  };
})();


// ===================== src/pages/ui-gallery.jsx =====================
// src/pages/ui-gallery.jsx: OO.pages.UiGallery, the dev UI kit gallery at /{hotel}/__ui (page 'ui-gallery', both
// modes; src/app.jsx renders it without PageFrame). Every OO.ui primitive and chrome piece in its states: a
// 390px column forced to the mobile layout beside an .oo-wide column forced to the desktop layout (shown at
// >= 900px), plus screens-in-context in device boxes for side-by-side checks against the design
// (docs/online-order/design/*.dc.txt). Sample data is inline design copy; it reads no data layer.
// Owner: T2b (UI kit). Styles: the "UI gallery" block of styles/chrome.css.
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});
  OO.pages = OO.pages || {};
  var ui = OO.ui;

  // ---- sample data (design copy) --------------------------------------------------------------------------
  function img(file) { return OO.asset('online-order/fixtures/img/' + file); }
  function unsplash(id, w) { return 'https://images.unsplash.com/photo-' + id + '?w=' + (w || 240) + '&h=' + (w || 240) + '&fit=crop&q=70'; }
  function money(n) { return '$' + n.toFixed(2); }
  function plus(n) { return n ? '+$' + (n % 1 ? n.toFixed(2) : String(n)) : ''; }
  function noop() {}

  var STORE = { name: 'Bennys Burger Chapel Street', address: '560 Chapel St, South Yarra, Victoria, Australia, 3141', short: '560 Chapel St, South Yarra' };
  var TABS = ['WEEKLY DEALS', 'BURGERS', 'POUTINES/BOXES', 'TACOS/WRAPS', 'NACHOS/QUESADILLAS', 'SIDES'].map(function (t) { return { id: t, label: t }; });
  var CHIPS = ['ALL', 'BEEF BURGER', "BENNY'S GOURMET", 'VEGGIE & VEGAN', 'CHICKEN BURGER'];
  var MENU = [
    { id: 'sb', name: 'Spicy Beef Burger', desc: 'Prime beef patty, cheese, hot sauce, onion, jalapeño & lettuce', price: '$16.50', img: 'spicy-beef.jpg', cat: 'BEEF BURGER' },
    { id: 'fb', name: 'Fat Boy Burger', desc: 'Prime beef patty, eight-hour slow-cooked brisket, double cheese, BBQ sauce & onion', price: '$25.90', img: 'fat-boy.jpg', cat: "BENNY'S GOURMET" },
    { id: 'vc', name: 'Vego Cheese Burger', desc: 'Veef patty, cheese, tomato sauce, mustard, onion, pickle, tomato & lettuce', price: '$18.90', img: 'vego-cheese.jpg', cat: 'VEGGIE & VEGAN' },
    { id: 'rb', name: 'Royal Beef Burger', desc: 'Prime beef patty, cheese, BBQ sauce, caramelised onion & slaw', price: '$18.50', img: 'royal-beef.jpg', cat: 'BEEF BURGER' },
    { id: 'sv', name: 'Spicy Vegan Burger', desc: 'Veef patty, cheese, hot sauce, onion, jalapeño & lettuce', price: '$18.90', img: 'spicy-vegan.jpg', cat: 'VEGGIE & VEGAN' },
    { id: 'bc', name: 'Buffalo Chicken Burger', desc: "Chicken patty with buffalo sauce, Benny's sauce, cheese & lettuce", price: '$15.50', img: 'buffalo-chicken.jpg', cat: 'CHICKEN BURGER' },
  ];
  var BASES = [{ l: 'Crispy Chips', p: 0 }, { l: 'Tater Tots', p: 2 }, { l: 'Sweet Potato Chips', p: 3 }];
  var BREADS = [{ l: 'Brioche Bun', p: 0 }, { l: 'Gluten Free Bun', p: 2 }, { l: 'Lettuce Wrap', p: 0 }];
  var ADDONS = [{ l: 'Extra Cheese', p: 2 }, { l: 'Extra Gravy', p: 1.5 }, { l: 'Jalapeños', p: 1 }];
  var PIZZAS = [
    { n: 'Margherita', d: 'Tomato, mozzarella, basil', p: 0, img: unsplash('1574071318508-1cdbab80d002') },
    { n: 'Pepperoni', d: 'Tomato, mozzarella, pepperoni', p: 0, img: unsplash('1628840042765-356cda07504e') },
    { n: 'BBQ Chicken', d: 'BBQ base, chicken, red onion, mozzarella', p: 2, img: unsplash('1565299624946-b28f40a0ae38') },
    { n: 'Veggie Supreme', d: 'Capsicum, mushroom, olives, onion', p: 0, img: unsplash('1511689660979-10d2b1aada49') },
  ];
  var CATS = [
    { name: 'WEEKLY DEALS', meta: '1 item' },
    { name: 'ALCOHOL', meta: '5 categories' },
    { name: 'BURGERS', meta: '4 categories', img: 'fat-boy.jpg' },
    { name: 'POUTINES/BOXES', meta: '1 item', img: 'brisket-poutine.jpg' },
  ];
  var ICONS = ['ArrowLeft', 'Bag', 'Search', 'User', 'Store', 'Pin', 'Pencil', 'Trash', 'Plus', 'Minus', 'Check', 'ChevronDown',
    'ChevronUp', 'ChevronRight', 'ChevronLeft', 'Close', 'DollarCircle', 'UserCircle', 'Ticket', 'Bell', 'Home', 'MenuList', 'Receipt',
    'Star', 'Calendar', 'Gift', 'Scan', 'Locate', 'Clock', 'Info', 'ImagePlaceholder', 'CaretDown'];

  // ---- layout helpers -------------------------------------------------------------------------------------
  // <Cols>: the mobile column (ViewportContext 'mobile', no .oo-wide ancestor) beside the desktop column
  // (.oo-wide + 'desktop'; hidden below 900px, where the desktop CSS cannot apply). children: a node or
  // (wide) => node. stack: the desktop column wraps to full width below. bare: no panel. mobileOnly.
  function Cols(props) {
    var V = ui.ViewportContext;
    var render = typeof props.children === 'function' ? props.children : function () { return props.children; };
    var box = props.bare ? 'oo-gal__bare' : 'oo-gal__panel';
    return (
      <div className={ui.cx('oo-gal__cols', props.stack && 'oo-gal__cols--stack')}>
        <div className="oo-gal__col oo-gal__col--m">
          <div className="oo-gal__label">{props.mLabel || 'Mobile · 390px'}</div>
          <V.Provider value="mobile"><div className={box}>{render(false)}</div></V.Provider>
        </div>
        {props.mobileOnly ? null : (
          <div className="oo-gal__col oo-gal__col--d oo-wide">
            <div className="oo-gal__label">{props.dLabel || 'Desktop · ≥ 900px'}</div>
            <V.Provider value="desktop"><div className={box}>{render(true)}</div></V.Provider>
          </div>
        )}
      </div>
    );
  }
  function Section(props) {
    return (
      <section className="oo-gal__sec" id={'g-' + props.id} aria-labelledby={'g-' + props.id + '-h'}>
        <h2 className="oo-gal__h" id={'g-' + props.id + '-h'}>{props.title}</h2>
        {props.note ? <p className="oo-gal__note">{props.note}</p> : null}
        {props.children}
      </section>
    );
  }
  function Label(props) { return <div className="oo-gal__label">{props.children}</div>; }

  // ---- screens in context ---------------------------------------------------------------------------------
  // 01 Menu / D01 Menu: StoreHeader + CategoryTabs + deals, chips, product cards + FloatingCartBar.
  function MenuScreen(props) {
    var tab = React.useState('BURGERS'), chip = React.useState('ALL'), q = React.useState('');
    var items = MENU.filter(function (m) { return chip[0] === 'ALL' || m.cat === chip[0]; });
    return (
      <div className="oo-gal__screen">
        <ui.StoreHeader name={STORE.name} address={STORE.address} addressShort={STORE.short} onBack={noop} onAbout={noop}
          cartCount={2} onCart={noop} onProfile={noop} search={props.search ? { value: q[0], onChange: q[1] } : null} />
        <ui.CategoryTabs tabs={TABS} active={tab[0]} onSelect={tab[1]} onSearch={noop} />
        <ui.Container>
          <div className="oo-gal__pad">
            <ui.SectionTitle>WEEKLY DEALS</ui.SectionTitle>
            <ui.ProductGrid>
              <ui.DealCard name="Wednesday Deal" badge="Today only" desc="Any burger from beef or chicken category, chips and a can of soft drink." price="$12.90" onClick={noop} />
            </ui.ProductGrid>
            <ui.SectionTitle className="oo-gal__mt">BURGERS</ui.SectionTitle>
            <ui.ChipRow label="Burger types">
              {CHIPS.map(function (c) { return <ui.Chip key={c} active={c === chip[0]} onClick={function () { chip[1](c); }}>{c}</ui.Chip>; })}
            </ui.ChipRow>
            <ui.ProductGrid>
              {items.map(function (m) { return <ui.ProductCard key={m.id} name={m.name} desc={m.desc} price={m.price} image={img(m.img)} onClick={noop} />; })}
            </ui.ProductGrid>
            <ui.SectionTitle className="oo-gal__mt">POUTINES/BOXES</ui.SectionTitle>
            <ui.ProductGrid>
              <ui.ProductCard name="Brisket Poutine" desc="Chips topped with cheese, eight-hour slow-cooked pulled beef brisket & gravy" price="$15.50" image={img('brisket-poutine.jpg')} onClick={noop} />
            </ui.ProductGrid>
          </div>
        </ui.Container>
        <ui.FloatingCartBar count={2} total="$54.80" onClick={noop} />
      </div>
    );
  }

  // 02 Item / D02 Item: the split Sheet with required, collapsible and capped-checkbox groups.
  function ItemSheet(props) {
    var wide = ui.useWide();
    var base = React.useState(-1), bread = React.useState(-1), breadOpen = React.useState(false);
    var adds = React.useState([]), qty = React.useState(1), notes = React.useState('');
    var ok = base[0] >= 0;
    var unit = 15.5 + (ok ? BASES[base[0]].p : 0) + (bread[0] >= 0 ? BREADS[bread[0]].p : 0)
      + adds[0].reduce(function (a, i) { return a + ADDONS[i].p; }, 0);
    var headingId = 'g-item-title-' + props.uid;
    return (
      <ui.Sheet open={props.open !== false} onClose={props.onClose || noop} contained={props.contained} labelledBy={headingId}
        hero={img('brisket-poutine.jpg')}
        heading={<ui.SheetHeading id={headingId} title="Brisket Poutine" desc="Chips topped with cheese, eight-hour slow-cooked pulled beef brisket & gravy" price="$15.50" />}
        footer={[
          <ui.QtyStepper key="q" value={qty[0]} onChange={qty[1]} itemName="Brisket Poutine" />,
          <ui.Button key="b" block price={money(unit * qty[0])} disabled={!ok} onClick={props.onClose}>{ok ? 'Add to cart' : 'Choose a base'}</ui.Button>,
        ]}>
        <ui.OptionGroup title="Choice of base" hint="Choose 1" required done={ok}>
          {BASES.map(function (b, i) { return <ui.OptionRow key={b.l} label={b.l} price={plus(b.p)} checked={base[0] === i} onToggle={function () { base[1](i); }} />; })}
        </ui.OptionGroup>
        <ui.OptionGroup title="Choice of bread" collapsible open={breadOpen[0]} onToggle={breadOpen[1]} summary={bread[0] >= 0 ? BREADS[bread[0]].l : 'Choose up to 1'}>
          {BREADS.map(function (b, i) {
            return <ui.OptionRow key={b.l} label={b.l} price={plus(b.p)} checked={bread[0] === i} onToggle={function () { bread[1](bread[0] === i ? -1 : i); }} />;
          })}
        </ui.OptionGroup>
        <ui.OptionGroup title="Add ons" hint="Choose up to 3" type="checkbox">
          {ADDONS.map(function (a, i) {
            var on = adds[0].indexOf(i) >= 0;
            return (
              <ui.OptionRow key={a.l} label={a.l} price={plus(a.p)} checked={on} onToggle={function () {
                adds[1](on ? adds[0].filter(function (x) { return x !== i; }) : adds[0].length < 3 ? adds[0].concat(i) : adds[0]);
              }} />
            );
          })}
        </ui.OptionGroup>
        <ui.TextArea className="oo-gal__notes" size={wide ? 'md' : 'lg'} label="Additional notes" placeholder="Extra comments (optional)" value={notes[0]} onChange={notes[1]} />
      </ui.Sheet>
    );
  }

  // K01 / K02: the combo Sheet — mobile header (PageHeader + StepBar), desktop aside (photo, intro, step list).
  function ComboSheet(props) {
    var wide = ui.useWide();
    var pick = React.useState(-1);
    var picked = pick[0] >= 0;
    var steps = [
      { key: 'p', label: picked ? PIZZAS[pick[0]].n : 'Pizza', name: '1 × Pizza', summary: picked ? PIZZAS[pick[0]].n : 'Choose 1 pizza', state: 'current' },
      { key: 's', label: 'Sides', name: '1 × Sides', summary: 'Choose 1 side', state: 'todo', disabled: true },
      { key: 'd', label: 'Drinks', name: '1 × Drinks', summary: 'Choose 1 drink', state: 'todo', disabled: true },
    ];
    var stepLabel = 'Step 1 of 3 · Pizza';
    var header = wide
      ? [<ui.IconButton key="b" icon="ArrowLeft" label="Back" strokeWidth={2.4} onClick={noop} />, <span key="l" className="oo-gal__steplabel">{stepLabel}</span>]
      : [
        <ui.PageHeader key="h" as="div" sticky={false} title="Pizza Combo" subtitle={stepLabel} onBack={props.onClose || noop}
          right={<ui.HeaderCartPill count={2} onClick={noop} />} />,
        <ui.StepBar key="s" steps={steps} onPick={noop} />,
      ];
    var aside = [
      <div key="i" className="oo-gal__combo-hero"><img src={unsplash('1513104890138-7c749659a591', 800)} alt="" /></div>,
      <div key="t" className="oo-gal__combo-intro"><ui.SheetHeading kind="combo" title="Pizza Combo" desc="Any large pizza, a side and a drink." price="From $29.90" /></div>,
      <div key="s" className="oo-gal__combo-steps"><ui.StepBar variant="list" steps={steps} onPick={noop} /></div>,
    ];
    return (
      <ui.Sheet open={props.open !== false} onClose={props.onClose || noop} contained={props.contained} kind="combo" label="Pizza Combo"
        header={header} aside={aside}
        footer={[
          <ui.TotalBlock key="t" label="Combo total" amount={money(29.9 + (picked ? PIZZAS[pick[0]].p : 0))} />,
          <ui.Button key="c" block disabled={!picked}>{picked ? 'Continue' : 'Choose a pizza'}</ui.Button>,
        ]}>
        <ui.SectionTitle level="step" sub="Pick 1 · included in the combo price">Choose your pizza</ui.SectionTitle>
        <ui.Card pad="list" flat>
          {PIZZAS.map(function (p, i) {
            return (
              <ui.OptionRow key={p.n} variant="item" name={'g-pizza-' + props.uid} image={p.img} label={p.n} desc={p.d}
                price={p.p ? '+' + money(p.p) : 'Included'} priceTone={p.p ? undefined : 'success'} chevron
                checked={pick[0] === i} onToggle={function () { pick[1](i); }} />
            );
          })}
        </ui.Card>
      </ui.Sheet>
    );
  }

  // 03 Cart / D03 Cart: PageHeader, cart lines (ghost edit / delete, small stepper), coupons, PriceRows,
  // Powered by SIPO, and the mobile BottomBarCTA.
  function CartLine(props) {
    var l = props.line;
    return (
      <ui.Card pad="sm" className="oo-gal__line">
        <div className="oo-gal__line-text">
          <div className="oo-gal__line-name">{l.name}</div>
          <div className="oo-gal__line-mods">{l.mods}</div>
        </div>
        <ui.QtyStepper size="sm" value={l.qty} onChange={props.onQty} onRemove={props.onRemove} itemName={l.name} />
        <div className="oo-gal__line-total">{money(l.unit * l.qty)}</div>
        <div className="oo-gal__line-actions">
          <ui.IconButton variant="ghost" icon="Pencil" label={'Edit ' + l.name} onClick={noop} />
          <ui.IconButton variant="ghost" icon="Trash" label={'Remove ' + l.name} onClick={props.onRemove} />
        </div>
      </ui.Card>
    );
  }
  function CartScreen() {
    var wide = ui.useWide();
    var lines = React.useState([
      { key: 1, name: 'Spicy Beef Burger', mods: 'Make it a Meal · 1 × Onion Rings, 1 × Mountain Dew', unit: 27, qty: 1 },
      { key: 2, name: 'Brisket Poutine', mods: 'Tater Tots · Extra Cheese', unit: 19.5, qty: 1 },
    ]);
    var total = lines[0].reduce(function (a, l) { return a + l.unit * l.qty; }, 0);
    function upd(key, qty) {
      lines[1](lines[0].map(function (l) { return l.key === key ? Object.assign({}, l, { qty: qty }) : l; }).filter(function (l) { return l.qty > 0; }));
    }
    var rows = [
      { label: 'Sub-total', value: money(total) }, { label: 'Surcharge', value: '$0.00' },
      { label: 'Tax (GST inclusive)', value: money(total / 11) }, { label: 'Discount', value: '-$0.00' },
    ];
    return (
      <div className="oo-gal__screen">
        <ui.PageHeader title="Cart" onBack={noop} onProfile={noop} />
        <ui.Container>
          <div className="oo-gal__pad oo-gal__cartgrid">
            <div className="oo-gal__stack">
              <ui.SectionTitle level="page" sub={<React.Fragment><OO.icons.Pin size={14} className="oo-only-wide" />{STORE.name + ' · Pickup'}</React.Fragment>}>Order summary</ui.SectionTitle>
              {lines[0].map(function (l) {
                return <CartLine key={l.key} line={l} onQty={function (q) { upd(l.key, q); }} onRemove={function () { upd(l.key, 0); }} />;
              })}
              {!lines[0].length ? <ui.EmptyState title="Your cart is empty" /> : null}
              <ui.Button variant="ghost" size="md" icon="Plus" className="oo-gal__addmore oo-only-wide">Add more items</ui.Button>
            </div>
            <div className="oo-gal__stack">
              <ui.Card pad="sm" className="oo-gal__coupon">
                <OO.icons.Ticket size={22} dashed />
                <div className="oo-gal__coupon-text"><div className="oo-gal__coupon-t">Coupons</div><div className="oo-gal__coupon-s">Select coupon</div></div>
                <ui.Button size="sm">Select</ui.Button>
              </ui.Card>
              <ui.Card pad="summary">
                <ui.PriceRows title="Price summary" rows={rows} total={money(total)}>
                  {wide ? <ui.Button block>Continue to checkout</ui.Button> : null}
                </ui.PriceRows>
              </ui.Card>
              <ui.PoweredBySipo />
            </div>
          </div>
        </ui.Container>
        <ui.BottomBarCTA label="Total" amount={money(total)}><ui.Button block disabled={!lines[0].length}>Checkout</ui.Button></ui.BottomBarCTA>
      </div>
    );
  }

  // 04 Checkout (mobile): pickup selects, login card, contact inputs, payment row, terms, BottomBarCTA.
  function CheckoutScreen() {
    var day = React.useState('today'), time = React.useState('15'), terms = React.useState(true), offers = React.useState(true);
    var name = React.useState(''), mobile = React.useState(''), email = React.useState('');
    return (
      <div className="oo-gal__screen">
        <ui.PageHeader title="Checkout" onBack={noop} onProfile={noop} />
        <div className="oo-gal__pad oo-gal__pad--x">
          <ui.SectionTitle level="page" sub="560 Chapel St, South Yarra, VIC 3141">{STORE.name}</ui.SectionTitle>
          <ui.Card className="oo-gal__ck">
            <div className="oo-gal__ck-head"><OO.icons.Bag size={22} /><div className="oo-gal__ck-title">Pickup</div><div className="oo-gal__ck-aside">Cooking time ~15 mins</div></div>
            <div className="oo-gal__grid2">
              <ui.SelectField label="Day" value={day[0]} onChange={day[1]} options={[{ value: 'today', label: 'Today' }, { value: 'tomorrow', label: 'Tomorrow' }]} />
              <ui.SelectField label="Time" value={time[0]} onChange={time[1]} options={[{ value: '15', label: '15–20 mins' }, { value: '30', label: '30–35 mins' }]} />
            </div>
          </ui.Card>
          <ui.Card className="oo-gal__login">
            <div className="oo-gal__login-text">Have an account? Log in to fill your details.</div>
            <ui.Button size="md">Login</ui.Button>
          </ui.Card>
          <ui.Card className="oo-gal__ck">
            <div className="oo-gal__ck-head oo-gal__ck-head--top"><OO.icons.UserCircle size={22} /><div><div className="oo-gal__ck-title">Contact details</div><div className="oo-gal__ck-sub">We'll send your order confirmation here</div></div></div>
            <ui.TextInput label="Full name" value={name[0]} onChange={name[1]} autoComplete="name" />
            <ui.TextInput label="Mobile number" type="tel" value={mobile[0]} onChange={mobile[1]} autoComplete="tel" inputMode="tel" />
            <ui.TextInput label="Email" type="email" value={email[0]} onChange={email[1]} autoComplete="email" />
          </ui.Card>
          <ui.Card as="button" className="oo-gal__pay" onClick={noop}>
            <OO.icons.DollarCircle size={22} /><span className="oo-gal__ck-title">Payment method</span><span className="oo-gal__pay-val">Pay Online</span><OO.icons.ChevronDown size={16} />
          </ui.Card>
          <div className="oo-gal__checks">
            <ui.CheckRow checked={terms[0]} onChange={terms[1]} required>I have read and agree to the <strong>terms and conditions</strong>.</ui.CheckRow>
            <ui.CheckRow checked={offers[0]} onChange={offers[1]}>Send me email and push notifications about exclusive offers</ui.CheckRow>
          </div>
        </div>
        <ui.BottomBarCTA label="Total · GST incl." amount="$54.80"><ui.Button block disabled={!terms[0]}>{terms[0] ? 'Pay $54.80' : 'Accept terms to pay'}</ui.Button></ui.BottomBarCTA>
      </div>
    );
  }

  // App Home (customer app 01): AppTopBar, loyalty card, quick actions, tab bar.
  function AppHomeScreen(props) {
    return (
      <div className="oo-gal__screen oo-app oo-has-tabs">
        <ui.AppTopBar greeting="Good evening, Sam" storeName={STORE.name} onStoreClick={noop} open status="Open until 10pm" distance="1.2 km" unread={2} onBell={noop} />
        <div className="oo-gal__pad oo-gal__pad--x">
          <ui.Card radius="xl" className="oo-gal__loyal">
            <div className="oo-gal__loyal-row">
              <div className="oo-gal__loyal-text">
                <div className="oo-gal__loyal-name">Bennys Rewards · Gold</div>
                <div className="oo-gal__loyal-pts"><span className="oo-gal__loyal-num">340</span><span className="oo-gal__loyal-word">points</span></div>
              </div>
              <ui.Button size="md" icon="Scan" iconSize={16} className="oo-gal__scan">Scan card</ui.Button>
            </div>
            <ui.ProgressBar value={340} max={500} label="Points to a Free Burger" />
            <div className="oo-gal__loyal-togo">160 points to a <strong>Free Burger</strong></div>
          </ui.Card>
          <div className="oo-gal__actions">
            {[['Order now', 'Bag'], ['Book a table', 'Calendar'], ['Rewards', 'Gift'], ['Stores', 'Pin']].map(function (a) {
              return (
                <button key={a[0]} type="button" className="oo-gal__action">
                  <span className="oo-gal__action-tile">{ui.renderIcon(a[1], { size: 24 })}</span>
                  <span className="oo-gal__action-label">{a[0]}</span>
                </button>
              );
            })}
          </div>
          <ui.SectionTitle level="section">Order again</ui.SectionTitle>
          <ui.Card radius="lg" pad="sm" className="oo-gal__again">
            <img src={img('spicy-beef.jpg')} alt="" />
            <div className="oo-gal__again-text"><div className="oo-gal__again-name">Spicy Beef Burger meal</div><div className="oo-gal__again-sub">Last Friday · Chapel Street · $27.00</div></div>
            <ui.Button variant="outline" size="xs">Reorder</ui.Button>
          </ui.Card>
        </div>
        <ui.AppTabBar route={{ hotel: props.hotel, page: 'home' }} />
      </div>
    );
  }

  // ---- the page -------------------------------------------------------------------------------------------
  var NAV = [['screens', 'Screens'], ['sheets', 'Sheets'], ['chrome', 'Headers & bars'], ['app', 'App chrome'], ['buttons', 'Buttons'],
    ['badges', 'Badges & chips'], ['forms', 'Forms'], ['options', 'Options & stepper'], ['cards', 'Cards & tiles'],
    ['steps', 'Steps & titles'], ['notices', 'Notices & totals'], ['booking', 'Booking & loyalty'], ['icons', 'Icons']];

  OO.pages.UiGallery = function UiGallery(props) {
    var route = props.route || {};
    var hotel = route.hotel || 'bennys';
    var live = React.useState(null); // 'item' | 'combo' | 'dialog'
    var toast = React.useState(false);
    var coupon = React.useState(0);
    var guests = React.useState(2), date = React.useState('d0'), slot = React.useState(null);
    var qtyA = React.useState(1), qtyB = React.useState(3);
    var req = React.useState(-1), many = React.useState([0]);
    var collOpen = React.useState(true), coll = React.useState(-1);
    var terms = React.useState(false), offers = React.useState(true);
    var txt = React.useState('Sam Taylor'), sel = React.useState('today'), search = React.useState(''), dsearch = React.useState('');
    var chip = React.useState('ALL');
    var closeLive = function () { live[1](null); };

    React.useEffect(function () { document.title = 'UI kit · SIPO'; }, []);

    var days = ['Today', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'].map(function (d, i) {
      return { value: 'd' + i, dow: d, day: i + 1, label: d + ' ' + (i + 1) + ' October', disabled: i === 5 };
    });
    var slots = ['5:00', '5:30', '6:00', '6:30', '7:00', '7:30', '8:00', '8:30'].map(function (t) {
      return { value: t, label: t + 'pm', reason: t === '5:00' ? 'past' : (t === '6:30' || t === '7:00' ? 'full' : null) };
    });
    var steps3 = [
      { key: 'a', label: 'Margherita', name: '1 × Pizza', summary: 'Margherita · Thin & crispy', state: 'done' },
      { key: 'b', label: 'Sides', name: '1 × Sides', summary: 'Choose 1 side', state: 'current' },
      { key: 'c', label: 'Drinks', name: '1 × Drinks', summary: 'Choose 1 drink', state: 'todo', disabled: true },
    ];
    var priceRows = [{ label: 'Sub-total', value: '$54.80' }, { label: 'Surcharge', value: '$0.00' }, { label: 'Tax (GST inclusive)', value: '$4.98' }, { label: 'Discount', value: '-$0.00' }];

    return (
      <div className="oo-gal">
        <header className="oo-gal__top">
          <div className="oo-gal__top-in">
            <h1 className="oo-gal__title">SIPO Online Order · UI kit</h1>
            <span className="oo-gal__meta">{'mode ' + (OO.mode || 'online') + ' · left column forced mobile, right column .oo-wide desktop (≥ 900px viewports)'}</span>
            <div className="oo-gal__live">
              <ui.Button size="xs" variant="outline" className="oo-gal__live-btn" onClick={function () { live[1]('item'); }}>Live item sheet</ui.Button>
              <ui.Button size="xs" variant="outline" className="oo-gal__live-btn" onClick={function () { live[1]('combo'); }}>Live combo</ui.Button>
              <ui.Button size="xs" variant="outline" className="oo-gal__live-btn" onClick={function () { live[1]('dialog'); }}>Live dialog</ui.Button>
              <ui.Button size="xs" variant="outline" className="oo-gal__live-btn" onClick={function () { toast[1](true); }}>Toast</ui.Button>
            </div>
            <nav className="oo-gal__nav" aria-label="Gallery sections">
              {NAV.map(function (n) { return <a key={n[0]} href={'#g-' + n[0]}>{n[1]}</a>; })}
            </nav>
          </div>
        </header>

        <Section id="screens" title="Screens in context · 01 Menu / D01 Menu" note="Store header, sticky category tabs, deals, chip row, product cards and the floating cart bar, inside device boxes (the box traps the fixed bars like the design's phone frame).">
          <Cols bare stack dLabel="Desktop · D01 (1200px content)">
            {function (wide) {
              return <div className={wide ? 'oo-gal__desk' : 'oo-gal__phone'}><MenuScreen /></div>;
            }}
          </Cols>
        </Section>

        <Section id="sheets" title="Sheets · 02 Item / D02, K01 / K02 combo, dialog" note="Contained demos (no portal). Mobile: full-height sheet sliding up; desktop: the centred split modal over the dark scrim. Use the Live buttons in the top bar for the real portal / ESC / focus-trap / scroll-lock behaviour.">
          <Cols bare stack>
            {function (wide) {
              return (
                <div className={wide ? 'oo-gal__desk' : 'oo-gal__phone'}>
                  {wide ? <MenuScreen /> : null}
                  <ItemSheet contained uid={wide ? 'd' : 'm'} />
                </div>
              );
            }}
          </Cols>
          <div className="oo-gal__gap" />
          <Cols bare stack>
            {function (wide) {
              return (
                <div className={wide ? 'oo-gal__desk' : 'oo-gal__phone'}>
                  {wide ? <MenuScreen /> : null}
                  <ComboSheet contained uid={wide ? 'd' : 'm'} />
                </div>
              );
            }}
          </Cols>
          <div className="oo-gal__gap" />
          <Cols bare>
            {function (wide) {
              return (
                <div className={ui.cx(wide ? 'oo-gal__desk' : 'oo-gal__phone', 'oo-gal__box--short')}>
                  <ui.Sheet open contained variant="dialog" title="Select coupon" onClose={noop}
                    footer={<ui.Button block>Apply coupon</ui.Button>}>
                    <ui.OptionGroup title="Available coupons" hint="Choose 1" badge={false} name={'g-coupon-' + (wide ? 'd' : 'm')}>
                      {['10% off your order', 'Free Crispy Chips', '$5 off orders over $40'].map(function (c, i) {
                        return <ui.OptionRow key={c} label={c} desc={i === 2 ? 'Spend $40.00 or more' : null} checked={coupon[0] === i} onToggle={function () { coupon[1](i); }} />;
                      })}
                    </ui.OptionGroup>
                  </ui.Sheet>
                </div>
              );
            }}
          </Cols>
        </Section>

        <Section id="chrome" title="Headers & bars · cart / checkout chrome" note="PageHeader (Cart 64 / 72px), cart lines with the small stepper and ghost icon buttons, coupons, PriceRows and the mobile BottomBarCTA (hidden on desktop, where the CTA sits in the summary card).">
          <Cols bare stack>
            {function (wide) {
              return <div className={ui.cx(wide ? 'oo-gal__desk' : 'oo-gal__phone')}><CartScreen /></div>;
            }}
          </Cols>
          <div className="oo-gal__gap" />
          <Cols bare mobileOnly mLabel="Mobile · 04 Checkout">
            <div className="oo-gal__phone"><CheckoutScreen /></div>
          </Cols>
          <div className="oo-gal__gap" />
          <Cols>
            <div className="oo-gal__stack">
              <Label>StoreHeader with search (C02 desktop) · PageHeader with subtitle + cart pill (K01) · static</Label>
              <ui.StoreHeader name={STORE.name} address={STORE.address} addressShort={STORE.short} onBack={noop} cartCount={2} onCart={noop}
                onProfile={noop} search={{ value: dsearch[0], onChange: dsearch[1] }} />
              <ui.PageHeader sticky={false} title="Burgers" subtitle="Menu / Burgers" onBack={noop} onProfile={noop} />
              <ui.PageHeader sticky={false} title="Pizza Combo" subtitle="Step 2 of 3 · Sides" onBack={noop} right={<ui.HeaderCartPill count={3} />} />
              <ui.PageHeader sticky={false} title="Checkout" onBack={noop} onProfile={noop} />
              <ui.CategoryTabs sticky={false} tabs={TABS} active="WEEKLY DEALS" onSelect={noop} onSearch={noop} />
            </div>
          </Cols>
        </Section>

        <Section id="app" title="App chrome · customer app (mobile only)" note="App mode never takes the desktop layout: the app runs in a phone-width column on wide screens. AppTopBar (default and brand variants), the app PageHeader variants and the tab bar.">
          <Cols bare mobileOnly mLabel="Mobile · 01 Home">
            <div className="oo-gal__phone oo-gal__box--short"><AppHomeScreen hotel={hotel} /></div>
          </Cols>
          <div className="oo-gal__gap" />
          <Cols mobileOnly mLabel="Headers (.oo-app)">
            <div className="oo-gal__stack oo-app">
              <ui.AppTopBar brand="Bennys Burger" storeName="Chapel Street" status="Open until 10pm" onStoreClick={noop} unread={0} onBell={noop} />
              <ui.PageHeader sticky={false} title="Select store" onBack={noop} />
              <ui.PageHeader sticky={false} title="Rewards" right={<ui.HeaderLink onClick={noop}>How it works</ui.HeaderLink>} />
              <ui.PageHeader sticky={false} title="Book a table" subtitle={STORE.name} onBack={noop} />
              <ui.PageHeader sticky={false} title="Notifications" onBack={noop} right={<ui.HeaderLink onClick={noop}>Mark all read</ui.HeaderLink>} />
              <ui.AppTopBar greeting="Good morning" storeName="Bennys Burger Richmond" open={false} status="Opens 11am" distance="4.1 km" unread={12} onBell={noop} />
            </div>
          </Cols>
        </Section>

        <Section id="buttons" title="Buttons & icon buttons">
          <Cols>
            <div className="oo-gal__stack">
              <ui.Button block>Continue to checkout</ui.Button>
              <ui.Button block price="$25.90">Add to cart</ui.Button>
              <ui.Button block price="$15.50" disabled>Choose a base</ui.Button>
              <ui.Button block disabled>Accept terms to pay</ui.Button>
              <ui.Button block busy>Pay $54.80</ui.Button>
              <ui.Button block variant="success">Request sent</ui.Button>
              <div className="oo-gal__row">
                <ui.Button size="md">Login</ui.Button>
                <ui.Button size="sm">Select</ui.Button>
                <ui.Button size="xs" variant="outline">Reorder</ui.Button>
                <ui.Button size="xs">Redeem</ui.Button>
                <ui.Button size="xs" variant="soft">Locked</ui.Button>
                <ui.Button size="md" icon="Scan">Scan card</ui.Button>
              </div>
              <div className="oo-gal__row">
                <ui.Button variant="link">Change</ui.Button>
                <ui.Button variant="link">Customise</ui.Button>
                <ui.Button variant="link">Full menu</ui.Button>
                <ui.Button variant="ghost" size="md" icon="Plus">Add more items</ui.Button>
                <ui.Button size="xs" variant="outline" disabled>Disabled</ui.Button>
              </div>
              <div className="oo-gal__row">
                <ui.IconButton icon="Close" label="Close" />
                <ui.IconButton icon="ArrowLeft" label="Back" strokeWidth={2.4} />
                <ui.IconButton icon="Pencil" variant="ghost" label="Edit" />
                <ui.IconButton icon="Trash" variant="ghost" label="Remove" />
                <ui.IconButton icon="Pencil" variant="ghost" label="Edit (disabled)" disabled />
              </div>
              <div className="oo-gal__dark">
                <ui.IconButton icon="ArrowLeft" variant="bare" label="Back" />
                <ui.IconButton icon="Store" variant="ring" label="About this store" />
                <ui.IconButton icon="User" variant="ring" label="Account" />
                <ui.IconButton icon="Bell" variant="ring" size="lg" label="Notifications, 2 unread" badge="2" />
                <ui.HeaderCartPill count={2} />
                <ui.HeaderLink>Mark all read</ui.HeaderLink>
              </div>
            </div>
          </Cols>
        </Section>

        <Section id="badges" title="Badges, status pills & chips">
          <Cols>
            <div className="oo-gal__stack">
              <div className="oo-gal__row">
                <ui.Badge tone="required">Required</ui.Badge>
                <ui.Badge tone="done">Done</ui.Badge>
                <ui.Badge tone="optional">Optional</ui.Badge>
                <ui.Badge tone="success" size="sm">Today only</ui.Badge>
                <ui.Badge tone="warn">Low stock</ui.Badge>
                <ui.Badge tone="danger">Unavailable</ui.Badge>
                <ui.Badge tone="neutral">Sold out</ui.Badge>
              </div>
              <div className="oo-gal__row">
                <ui.StatusPill open>Open until 10pm</ui.StatusPill>
                <ui.StatusPill open={false}>Opens 11am</ui.StatusPill>
                <ui.StatusPill tone="neutral">1.2 km</ui.StatusPill>
                <ui.StatusPill tone="neutral">Pickup · Dine-in</ui.StatusPill>
              </div>
              <div className="oo-gal__dark">
                <ui.StatusPill variant="dot" open className="oo-gal__subtle">Open until 10pm · 1.2 km</ui.StatusPill>
                <ui.StatusPill variant="dot" open={false} className="oo-gal__subtle">Opens 11am · 4.1 km</ui.StatusPill>
                <ui.Badge tone="inverse" upper>Gold</ui.Badge>
              </div>
              <ui.ChipRow label="Burger types">
                {CHIPS.map(function (c) { return <ui.Chip key={c} active={c === chip[0]} onClick={function () { chip[1](c); }}>{c}</ui.Chip>; })}
              </ui.ChipRow>
            </div>
          </Cols>
        </Section>

        <Section id="forms" title="Forms · outlined inputs, selects, search, checkboxes">
          <Cols>
            <div className="oo-gal__stack">
              <ui.TextInput label="Full name" value="" onChange={noop} />
              <ui.TextInput label="Full name (filled)" value={txt[0]} onChange={txt[1]} />
              <ui.TextInput label="Email" type="email" value="sam@" onChange={noop} error="Enter a valid email address" />
              <ui.TextInput label="Mobile number" stacked value="0412 345 678" onChange={noop} hint="Stacked: the Day / Time look" />
              <ui.TextInput label="Table number" labelVisible value="" placeholder="e.g. 12" onChange={noop} />
              <div className="oo-gal__grid2">
                <ui.SelectField label="Day" value={sel[0]} onChange={sel[1]} options={[{ value: 'today', label: 'Today' }, { value: 'tomorrow', label: 'Tomorrow' }]} />
                <ui.SelectField label="Time" value="" placeholder="Select" onChange={noop} options={['15–20 mins', '30–35 mins']} />
              </div>
              <ui.TextArea label="Additional notes" placeholder="Extra comments (optional)" value="" onChange={noop} />
              <ui.TextArea label="Order notes" labelSize="sm" size="xl" placeholder="Extra comments (optional)" value="" onChange={noop} />
              <ui.SearchField value={search[0]} onChange={search[1]} placeholder="Search suburb or postcode" />
              <ui.SearchField asButton onClick={noop} />
              <div className="oo-gal__dark"><ui.SearchField tone="dark" className="oo-gal__grow" value={dsearch[0]} onChange={dsearch[1]} /></div>
              <ui.CheckRow checked={terms[0]} onChange={terms[1]} required invalid={!terms[0]}>
                I have read and agree to the <a href="#g-forms">terms and conditions</a>.
              </ui.CheckRow>
              <ui.CheckRow checked={offers[0]} onChange={offers[1]}>Send me email and push notifications about exclusive offers</ui.CheckRow>
            </div>
          </Cols>
        </Section>

        <Section id="options" title="Option groups, rows & steppers">
          <Cols>
            {function (wide) {
              var sfx = wide ? 'd' : 'm';
              return (
                <div className="oo-gal__stack">
                  <ui.OptionGroup title="Sizes" hint="Choose 1" required done={req[0] >= 0} name={'g-size-' + sfx}>
                    {[{ l: 'Just the Burger', p: 0 }, { l: 'Make it a Meal', p: 6 }].map(function (o, i) {
                      return <ui.OptionRow key={o.l} label={o.l} price={plus(o.p)} checked={req[0] === i} onToggle={function () { req[1](i); }} />;
                    })}
                  </ui.OptionGroup>
                  <ui.OptionGroup title="Choice of bread" collapsible open={collOpen[0]} onToggle={collOpen[1]} name={'g-bread-' + sfx}
                    summary={coll[0] >= 0 ? BREADS[coll[0]].l : 'Choose up to 1'}>
                    {BREADS.map(function (b, i) {
                      return <ui.OptionRow key={b.l} label={b.l} price={plus(b.p)} checked={coll[0] === i} onToggle={function () { coll[1](coll[0] === i ? -1 : i); }} />;
                    })}
                  </ui.OptionGroup>
                  <ui.OptionGroup title="Add ons" hint="Choose up to 3" type="checkbox">
                    {[{ l: 'Add Beef Patty', p: 5 }, { l: 'Add Chicken Patty', p: 5 }, { l: 'Extra Cheese', p: 2 }, { l: 'Add Bacon', p: 3, off: true }].map(function (o, i) {
                      var on = many[0].indexOf(i) >= 0;
                      return (
                        <ui.OptionRow key={o.l} label={o.l} price={plus(o.p)} checked={on} disabled={o.off} desc={o.off ? 'Sold out' : null}
                          onToggle={function () { many[1](on ? many[0].filter(function (x) { return x !== i; }) : many[0].concat(i)); }} />
                      );
                    })}
                  </ui.OptionGroup>
                  <div className="oo-gal__row">
                    <ui.QtyStepper value={qtyA[0]} onChange={qtyA[1]} />
                    <ui.QtyStepper value={qtyB[0]} onChange={qtyB[1]} max={5} />
                    <ui.QtyStepper size="sm" value={1} onChange={noop} onRemove={noop} itemName="Fat Boy Burger" />
                    <ui.QtyStepper size="sm" value={2} onChange={noop} bordered={false} />
                  </div>
                  <div className="oo-gal__footer-demo">
                    <ui.QtyStepper value={qtyA[0]} onChange={qtyA[1]} />
                    <ui.Button block price={money(25.9 * qtyA[0])}>Add to cart</ui.Button>
                  </div>
                </div>
              );
            }}
          </Cols>
        </Section>

        <Section id="cards" title="Product, deal & category cards · empty & loading">
          <Cols>
            {function (wide) {
              return (
                <div className="oo-gal__stack">
                  <ui.ProductGrid>
                    <ui.ProductCard name="Fat Boy Burger" desc={MENU[1].desc} price="$25.90" image={img('fat-boy.jpg')} onClick={noop} />
                    <ui.ProductCard name="Loaded Nachos" desc="Corn chips, beef chilli, cheese sauce, jalapeños, sour cream & guacamole" price="$16.50" onClick={noop} />
                    <ui.ProductCard name="Royal Beef Burger" desc={MENU[3].desc} price="$18.50" image={img('royal-beef.jpg')} soldOut />
                    <ui.ProductCard name="Kids Cheeseburger Meal" desc="Cheeseburger, small chips and a juice" price="$11.00" image={img('vego-cheese.jpg')} unavailable onClick={noop} />
                    <ui.DealCard name="Wednesday Deal" badge="Today only" desc="Any burger from beef or chicken category, chips and a can of soft drink." price="$12.90" onClick={noop} />
                    <ui.DealCard name="Wednesday Deal" desc="Any burger from beef or chicken category, chips and a can of soft drink." price="$12.90" unavailable unavailableLabel="Not available today" onClick={noop} />
                  </ui.ProductGrid>
                  <Label>CategoryGrid · image</Label>
                  <ui.CategoryGrid>
                    {CATS.map(function (c) { return <ui.CategoryTile key={c.name} name={c.name} meta={c.meta} image={c.img ? img(c.img) : null} onClick={noop} />; })}
                  </ui.CategoryGrid>
                  <Label>CategoryGrid · text</Label>
                  <ui.CategoryGrid variant="text">
                    {CATS.map(function (c) { return <ui.CategoryTile key={c.name} variant="text" name={c.name} meta={c.meta} onClick={noop} />; })}
                  </ui.CategoryGrid>
                  <Label>CategoryGrid · row (sub-categories)</Label>
                  <ui.CategoryGrid variant="row">
                    <ui.CategoryTile variant="row" name="ALL BURGERS" meta="6 items" image={img('fat-boy.jpg')} onClick={noop} />
                    <ui.CategoryTile variant="row" name="BEEF BURGER" meta="2 items" withImage onClick={noop} />
                    <ui.CategoryTile variant="row" name="CHICKEN BURGER" meta="1 item" onClick={noop} />
                  </ui.CategoryGrid>
                  <ui.EmptyState title="Your cart is empty" />
                  <ui.EmptyState icon="Receipt" title="No orders yet" body="Your past orders will show up here." action={<ui.Button size="md">Start an order</ui.Button>} />
                  <Label>Skeleton · product / tile / text</Label>
                  <ui.Skeleton variant="product" count={wide ? 2 : 1} />
                  <ui.Skeleton variant="tile" count={wide ? 4 : 2} />
                  <ui.Skeleton lines={3} />
                </div>
              );
            }}
          </Cols>
        </Section>

        <Section id="steps" title="Step bar, breadcrumbs & section titles">
          <Cols>
            <div className="oo-gal__stack">
              <ui.StepBar steps={steps3} onPick={noop} />
              <ui.Card pad="list" flat><ui.StepBar variant="list" steps={steps3} onPick={noop} /></ui.Card>
              <ui.Breadcrumbs items={[{ label: 'Menu', onClick: noop }, { label: 'Burgers', onClick: noop }, { label: 'Beef burger' }]} />
              <ui.SectionTitle>WEEKLY DEALS</ui.SectionTitle>
              <ui.SectionTitle level="page" sub={STORE.name + ' · Pickup'}>Order summary</ui.SectionTitle>
              <ui.SectionTitle level="step" sub="Pick 1 · included in the combo price">Review your combo</ui.SectionTitle>
              <ui.SectionTitle level="section" action={<ui.Button variant="link">Full menu</ui.Button>}>Popular at Chapel Street</ui.SectionTitle>
              <ui.SectionTitle level="group">What's included</ui.SectionTitle>
            </div>
          </Cols>
        </Section>

        <Section id="notices" title="Notices, price summary & totals">
          <Cols>
            <div className="oo-gal__stack">
              <ui.InlineNotice>Combo added to cart</ui.InlineNotice>
              <ui.InlineNotice tone="warn">This store opens at 11am. You can order ahead for later today.</ui.InlineNotice>
              <ui.InlineNotice tone="danger" role="alert">Payment failed. Please try again.</ui.InlineNotice>
              <ui.InlineNotice tone="info">Prices include GST.</ui.InlineNotice>
              <ui.Card pad="summary">
                <ui.PriceRows title="Price summary" rows={priceRows} total="$54.80">
                  <ui.Button block>Continue to checkout</ui.Button>
                </ui.PriceRows>
              </ui.Card>
              <div className="oo-gal__footer-demo">
                <ui.TotalBlock label="Combo total" amount="$29.90" />
                <ui.Button block>Start your combo</ui.Button>
              </div>
              <div className="oo-gal__footer-demo">
                <ui.TotalBlock label="Price summary" amount="$54.80" onLabelClick={noop} />
                <ui.Button block>Checkout</ui.Button>
              </div>
              <ui.PoweredBySipo />
            </div>
          </Cols>
        </Section>

        <Section id="booking" title="Booking & loyalty atoms (customer app)">
          <Cols mobileOnly mLabel="Mobile · 03 Rewards / 04 Book a table">
            <div className="oo-gal__stack">
              <div className="oo-gal__hero">
                <div className="oo-gal__hero-row">
                  <div><div className="oo-gal__hero-sub">Bennys Rewards</div><div className="oo-gal__hero-name">Sam Taylor</div></div>
                  <ui.Badge tone="inverse" upper>Gold</ui.Badge>
                </div>
                <div className="oo-gal__hero-pts">340 <span>points</span></div>
                <ui.ProgressBar tone="inverse" value={340} max={500} label="Points to Free Burger" />
                <div className="oo-gal__hero-sub">160 pts to Free Burger</div>
              </div>
              <ui.Card radius="lg" className="oo-gal__book">
                <div className="oo-gal__book-t">Guests</div>
                <ui.GuestPicker value={guests[0]} onChange={guests[1]} options={[1, 2, 3, 4, 5, 6, 7, { value: 8, label: '8+' }]} />
              </ui.Card>
              <ui.Card radius="lg" className="oo-gal__book">
                <div className="oo-gal__book-t">Date</div>
                <ui.DateStrip days={days} value={date[0]} onChange={date[1]} />
              </ui.Card>
              <ui.Card radius="lg" className="oo-gal__book">
                <div className="oo-gal__book-head"><div className="oo-gal__book-t">Time</div><div className="oo-gal__book-s">Tables held for 15 mins</div></div>
                <ui.TimeGrid slots={slots} value={slot[0]} onChange={slot[1]} />
              </ui.Card>
              <ui.Card radius="lg" className="oo-gal__book">
                <ui.TextArea label="Special requests" size="sm" placeholder="Birthday, high chair, allergies… (optional)" value="" onChange={noop} />
              </ui.Card>
              <div className="oo-gal__row">
                <ui.Avatar name="Sam Taylor" size={42} />
                <ui.Avatar size={42} />
                <ui.Avatar src={img('fat-boy.jpg')} alt="Fat Boy Burger" size={42} />
                <div className="oo-gal__dark"><ui.Avatar ring size={36} /></div>
              </div>
            </div>
          </Cols>
        </Section>

        <Section id="icons" title="Icons · OO.icons (24px, design stroke widths)">
          <div className="oo-gal__icons">
            {ICONS.map(function (n) {
              var I = OO.icons[n];
              return <div key={n} className="oo-gal__icon"><I size={24} filled={n === 'Home'} />{n}</div>;
            })}
            <div className="oo-gal__icon"><OO.icons.Home size={24} />Home (outline)</div>
            <div className="oo-gal__icon"><OO.icons.Ticket size={24} dashed />Ticket dashed</div>
          </div>
        </Section>

        {live[0] === 'item' ? <ItemSheet uid="live" onClose={closeLive} /> : null}
        {live[0] === 'combo' ? <ComboSheet uid="live" onClose={closeLive} /> : null}
        <ui.Sheet open={live[0] === 'dialog'} variant="dialog" title="Select coupon" onClose={closeLive} footer={<ui.Button block onClick={closeLive}>Apply coupon</ui.Button>}>
          <ui.OptionGroup title="Available coupons" hint="Choose 1" badge={false} name="g-coupon-live">
            {['10% off your order', 'Free Crispy Chips', '$5 off orders over $40'].map(function (c, i) {
              return <ui.OptionRow key={c} label={c} checked={coupon[0] === i} onToggle={function () { coupon[1](i); }} />;
            })}
          </ui.OptionGroup>
        </ui.Sheet>
        <ui.Toast open={toast[0]} onClose={function () { toast[1](false); }}>Combo added to cart</ui.Toast>
      </div>
    );
  };
})();


// ===================== src/app.jsx =====================
// src/app.jsx defines OO.App, the route → page switch for every mode. It is rendered by glue/boot.jsx.
//
// Every page component receives { route, mode }:
//   route = OO.router.useRoute() → { hotel, page, params, query }
//   mode  = OO.mode (glue/assets.jsx, from __SIPO_BOOT.mode): 'online' | 'app' | 'qr' (ordering at a table:
//           qr.sipocloudpos.com, qr.sipo.nz, /endpoint/qr-order)
// A route carrying redirectTo (a deep link, an unknown sub-path, REF's order-confirmed / success, an app-only page on
// the online host, a table URL off the qr hosts, a page off at a table in mode qr) is redirected ONCE with
// OO.router.go(redirectTo, { replace: true }) before anything renders.
// Mode qr (P4-14; glue/router.jsx "Mode qr") is ordering at a table. Its own redirect, once, comes from
// OO.router.qrGate with the store's table context (tableContext below, read per render): the scan landing goes to the
// table menu when a context applies; cart, checkout, about and the legal pages without one go to the scan landing. The
// scan landings ('/' and '/{hotel}', page 'scan') are OO.pages.Table (src/pages/table.jsx), and on every other page
// of a store this host serves it is mounted in the page's place with the page (`page`), as the host of the table gate
// and the guest welcome (P4-01 / P4-03): it renders the page, then whichever of them applies; where the store's table
// ordering is off, its "Ordering not available" gate INSTEAD of the page (QR-OFF). No tab bar; the merchant theme
// always (OO.theme) and the TABLE_SIDE domain (OO.model.domain) follow OO.mode. Modes online and app route no table
// (P4-14: the router drops table URLs there, and no table gate runs) and hold none (P4-01: OO.store keeps a table
// context in mode qr only).
// The table context: OO.pages.Table.context(hotel) → { id, name, hotel, guests, excludedCategoryIds, expiresAt } | null
// (P4-01, src/pages/table.jsx: checked against the store, 2 h, per (store, table)), else OO.store.table() while
// table.jsx isn't loaded. The router applies it only when its hotel is the route's (any case).
// Pages in modes online and app (D-18 since P3-01; mode qr sends all but about to the table menu, D-20): account,
// rewards, booking and the Phase 3 pages (wallet, track, review, bookings, booking-track, about, coupons, deals). Each
// mounts its component inside the page chrome; a stub renders nothing until its lane fills it. home, stores and
// notifications stay app-only (the router sends them home online).
// App mode wraps the app pages in the bottom tab bar (OO.ui.AppTabBar inside OO.ui.PageFrame).
// The dev UI kit gallery (page 'ui-gallery', /{hotel}/__ui) renders on its own, without PageFrame.
// On a page change it scrolls to the top (unless the URL has a #hash) and sets a default document.title.
// It sets the title in a layout effect, so a page can set its own title in a plain useEffect, which
// runs later and wins. A route with no store (find-store, the root payment-callback, not-found) takes the
// previous store's theme off :root (OO.data.deactivateHotel), before paint, so it never leaks.
// Per store (P1-02, src/data/api-select.jsx): a store this host doesn't serve (OO.api.availabilityOf(hotel) is
// 'unavailable': not a fixture store, not in the boot `live` list) gets the "not available here yet" card
// (OO.pages.NotFound) on every route under it, with no tab bar, so nothing loads the store; its theme comes off like
// a store-less route's. One exception (G1 fix F, owner decision): the payment pages of the order THIS device placed
// there (OO.api.isOwnOrder, i.e. OO.store's lastOrder) still render, so a store switched off while its customer was at
// the gateway still confirms the payment and shows the order. They are /{hotel}/payment-callback (whatever order id
// the gateway returns), /{hotel}/orderStatus (the page shows the last order) and /{hotel}/profile/orders/{id} when
// {id} is that order. Another order's link stays gated. The facade serves those pages' calls for that store.
// The preview bar shows only where the data is the sample data: fixture stores and the store-less routes
// (OO.api.isFixtureFor); live stores show none. The root store finder, and mode qr's generic scan page, show it while
// the discovery data is the sample stores (OO.api.discoveryIsFixture: a deployment serving no real store).
// Mode app (P4-11; glue/router.jsx "Mode app": D-03, D-30 (a)) is one app per chain: a route under a store carries
// route.chain (the path key) and route.hotel is the active branch, or null (none). OO.App:
//   * reads the route's chain (B31: OO.data.loadChain → OO.router.setChain; POLISH-4, p5-followups #20: the pages'
//     own read, OO.useChain, is the same one, so one request and one cache per page load) when the router
//     doesn't know it, refreshes a remembered one once per page load, and reads a failed one again after CHAIN_RETRY_MS
//     (a key no store has, or one not live here, isn't read again). The route re-resolves when it lands (a child key then
//     redirects to its master, an unlisted ?store= drops); until then it renders as Batch 1 did (the router's guess),
//     but for a fresh launch's Home (below).
//   * a fresh launch (POLISH-4; a fresh Flutter install opening /{masterKey}: nothing remembered): Home on the path
//     key's guess (via 'path': no ?store=, no saved branch) while the chain was never read (OO.router.chainOf null: the
//     read in flight) gets the route with no branch, i.e. the chain home's loading state (P4-17's ChainHome skeleton),
//     until B31 answers, so neither the master as a branch nor its forced order sheet flashes before the chain home. At
//     most ROOT_WAIT_MS (the root callback's cap): then the read counts as failed and the guess renders (never blocked
//     longer; the read landing later still re-resolves the route). A failed read ends it at once; the tab bar keeps the
//     route; only a Home that renders without a branch (withoutBranch) waits, and no other page.
//   * saves the branch in use (OO.router.setBranch) when the URL's ?store= names a store the known chain lists.
//   * no branch (route.hotel null: several stores, none chosen) → Select store first: OO.pages.Stores in place of the
//     page (the URL stays, so Select store can go on to it), without the tab bar. A page that renders without a branch
//     sets `withoutBranch = true` on its component (P4-17: Home's chain home, Stores itself) and gets the route as it is;
//     until then Stores gets it with hotel = the chain's key (Batch 1's Select store loads the chain from a store key).
//     The preview bar follows the chain's key, and the store theme stays (the page shows the chain's).
//   * the root /payment-callback forwards to /{masterKey}/payment-callback?store={branch} + the query as it came
//     (OO.router.paymentReturn): branch = the URL's online_key (B05; acct.paycb.parseParams, Windcave's `amp;` too), else
//     the last store (OO.store.lastHotel); the master from the remembered chain that lists the branch, else from its B31
//     read (nothing renders meanwhile; after ROOT_WAIT_MS, or a failed read, /{branch}/payment-callback?store=…). No
//     branch at all: the page's "We couldn't find your store".
//   OO.App.ensureChain(key) → Promise<OO.router.chainOf(key)>   the read above, on demand (deduped; read only when due).
// Analytics (P5-06b; src/lib/analytics.jsx): OO.analytics.useRoute(route, { redirecting }) runs once per page (one effect):
// the store's sandboxed tag frame follows the route (none on the payment pages, a store not live here, a redirect, in the
// app shell…) and the page's page_view goes. A redirecting render is skipped (the route it lands on follows).
// White label (WL-2; src/lib/whitelabel.jsx): OO.App re-renders on every white-label change (OO.whitelabel.useRevision)
// and starts the read for the route's chain or store (OO.whitelabel.ensure). In a studio preview (?wlPreview=) the fixed
// "Preview — orders are disabled" bar (OO.whitelabel.PreviewBar) sits above every page, in place of the sample-data bar
// and the install prompt; while the draft loads only the bar renders (the previewed chain counts as live only once it
// is in, so nothing reads it as "not live here" meanwhile), and mode app's chain read waits for it.
// Mode landing (WL-3; glue/router.jsx "Mode landing"): OO.mode is 'online' (every ordering page as on the online host);
// /{hotel} is page 'landing' (OO.pages.Landing: the chain's white-label landing page, which sets its own title).
// WL-5 (landing-only websites; mode landing only): when the landing website's ordering is off for the route's store
// (OO.whitelabel.browseOnly: the site's Online ordering tick off, or the store can't take online orders), the ordering
// pages (BROWSE_OFF: cart, checkout, deals, coupons, rewards, account, profile, orders, track, review, wallet and the
// booking pages) redirect once, like the table gate, to the store's browse-only menu (/{hotel}/menu). The landing page,
// the menu, about and the legal pages stay; so do the payment pages (payment-callback, order-status: an order placed
// before the tick changed still confirms). Every other mode, and mode landing with ordering on, is unchanged.
// OO.App.browseRedirect(route) → that href, or null.
// Install prompt (P5-03; src/ui/chrome.jsx): OO.ui.useInstallPrompt(route, mode) (a hook, before the early returns) says
// whether the install banner shows (never in mode qr, in the app shell, for a store not live here, on the cart, checkout
// or payment pages…); <OO.ui.InstallPrompt kind /> takes the preview bar's slot above the page (a store with the
// sample-data bar is never live, so the two never meet).
(function () {
  'use strict';
  var OO = (window.OO = window.OO || {});

  // Router page id → component on OO.pages. ItemSheet and ComboBuilder are overlays opened by the menu,
  // not routed pages; neither is OO.pages.Addresses (the address book the account area mounts, P3-10).
  // Phase 3 (P3-01): track.jsx (Track), review.jsx (Review), wallet-page.jsx (Wallet), bookings.jsx (Bookings: both the
  // history 'bookings' and the lookup 'booking-track'), about.jsx (About), offers.jsx (Coupons, Deals).
  // Phase 4 (P4-14): table.jsx (Table: mode qr's scan landings, 'scan').
  var PAGES = {
    'find-store': 'FindStore',
    'payment-callback': 'PaymentCallback',
    'not-found': 'NotFound',
    'menu': 'Menu',
    'cart': 'Cart',
    'checkout': 'Checkout',
    'order-status': 'OrderStatus',
    'orders': 'Orders',
    'profile': 'Profile',
    'legal': 'Legal',
    'home': 'Home',
    'stores': 'Stores',
    'rewards': 'Rewards',
    'booking': 'Booking',
    'notifications': 'Notifications',
    'account': 'Account',
    'wallet': 'Wallet',
    'track': 'Track',
    'review': 'Review',
    'bookings': 'Bookings',
    'booking-track': 'Bookings',
    'about': 'About',
    'coupons': 'Coupons',
    'deals': 'Deals',
    'scan': 'Table',
    // WL-3: mode landing's /{hotel}, the chain's white-label landing page (src/pages/landing.jsx).
    'landing': 'Landing',
    'app-content': 'AppContent',
    'zeta-addresses': 'Addresses',
    'settings': 'AppSettings',
  };
  // Dev-only pages, kept out of PAGES (tests/build-contract.test.mjs pins its 25 product page entries). They render
  // WITHOUT PageFrame: the UI gallery lays out its own mobile and desktop columns.
  var DEV_PAGES = { 'ui-gallery': 'UiGallery' };
  // App-mode pages that show the bottom tab bar.
  // stores, booking and notifications are sub-screens with their own bottom CTA in the design, so no tab bar; nor do
  // the Phase 3 pages (sub-screens with a back arrow).
  var TAB_PAGES = ['home', 'menu', 'orders', 'rewards', 'account'];
  var TITLES = {
    'find-store': 'Find a store',
    'payment-callback': 'Payment',
    'not-found': 'Page not found',
    'menu': 'Menu',
    'cart': 'Cart',
    'checkout': 'Checkout',
    'order-status': 'Order status',
    'orders': 'Orders',
    'profile': 'Profile',
    'legal': 'Legal',
    'home': 'Home',
    'stores': 'Stores',
    'rewards': 'Rewards',
    'booking': 'Book a table',
    'notifications': 'Notifications',
    'account': 'Account',
    'wallet': 'Wallet',
    'track': 'Track your order',
    'review': 'Rate your order',
    'bookings': 'Your bookings',
    'booking-track': 'Track a booking',
    'about': 'About',
    'coupons': 'Coupons',
    'deals': 'Deals',
    'scan': 'Scan to order',
    'landing': 'Welcome',
    'app-content': 'Information',
    'settings': 'Settings',
    'ui-gallery': 'UI kit',
  };
  // The document title's suffix per mode.
  var SUFFIX = { online: 'Order online', app: 'SIPO', qr: 'Order at your table', kiosk: 'Kiosk' };
  var LEGAL_TITLES = { terms: 'Terms and conditions', privacy: 'Privacy policy', allergy: 'Allergy information' };

  function own(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }

  // A route under a store this host doesn't serve (OO.api.availabilityOf, read per render: the boot `live` list),
  // unless it is a payment page of this device's order there (ownOrderPage).
  function notLiveHere(route) {
    return !!route.hotel && !!OO.api && typeof OO.api.availabilityOf === 'function' && OO.api.availabilityOf(route.hotel) === 'unavailable'
      && !ownOrderPage(route);
  }

  // The payment callback of the store where this device placed its last order, or that order's status page
  // (/orderStatus shows the last order, or the order its ?order_id= names (REF's confirmation links, P3-02);
  // /profile/orders/{id} only with its id). OO.api.isOwnOrder reads OO.store's lastOrder, per render like the live list.
  function ownOrderPage(route) {
    var api = OO.api;
    if (!api || typeof api.isOwnOrder !== 'function') return false;
    if (route.page === 'payment-callback') return !!api.isOwnOrder(route.hotel);
    if (route.page === 'order-status') {
      var linked = route.query && typeof route.query.order_id === 'string' ? route.query.order_id.trim() : '';
      return !!api.isOwnOrder(route.hotel, route.params.orderId || linked || undefined);
    }
    return false;
  }

  // Mode qr: the table context for this store (see the header), read per render: P4-01's when table.jsx provides it,
  // else OO.store's.
  function tableContext(hotel) {
    var T = OO.pages && OO.pages.Table;
    if (T && typeof T.context === 'function') return T.context(hotel) || null;
    return OO.store && typeof OO.store.table === 'function' ? OO.store.table() : null;
  }
  // Mode qr: where OO.router.qrGate sends this route with the store's table context, or null. Other modes: null (no
  // table there since P4-14).
  function tableRedirect(route, mode) {
    if (mode !== 'qr' || !route.hotel || !OO.router || typeof OO.router.qrGate !== 'function') return null;
    return OO.router.qrGate(route, tableContext(route.hotel), { mode: mode }) || null;
  }

  // ---- WL-5: the landing website's browse-only mode (see the header) ---------------------------------------------------
  var BROWSE_OFF = {
    cart: 1, checkout: 1, deals: 1, coupons: 1, rewards: 1, account: 1, profile: 1, orders: 1, track: 1, review: 1,
    wallet: 1, booking: 1, bookings: 1, 'booking-track': 1,
  };
  function browseRedirect(route) {
    if (!OO.landingMode || !route || !route.hotel || !own(BROWSE_OFF, route.page)) return null;
    var W = OO.whitelabel;
    if (!W || typeof W.browseOnly !== 'function' || !W.browseOnly(route.hotel)) return null;
    return OO.router.href('menu', null, { hotel: route.hotel });
  }

  // ---- mode app: chains (P4-11; see the header) ----------------------------------------------------------------------
  var CHAIN_RETRY_MS = 60000;
  var ROOT_WAIT_MS = 4000;
  var chainReads = Object.create(null); // lower-cased key → { at, p (the read in flight | null) }, this page load

  function lowerKey(k) { return String(k).toLowerCase(); }
  // How a chain read failed, as the router records it (the facade's notLive carries status 404 too).
  function failureOf(err) {
    if (err && err.kind === 'notLive') return 'notLive';
    if (err && (err.kind === 'notFound' || err.status === 404)) return 'unknown';
    return 'failed';
  }
  // POLISH-4 (p5-followups #20): through OO.data.loadChain, the pages' read (OO.useChain): one request, one cache.
  function readChain(key) {
    var rec = chainReads[lowerKey(key)] = { at: Date.now(), p: null };
    var p;
    try { p = Promise.resolve(OO.data.loadChain(key)); } catch (e) { p = Promise.reject(e); } // it never throws; be safe
    rec.p = p.then(function (c) {
      if (c && c.source) OO.router.setChain(key, c);
      else OO.router.chainFailed(key, 'failed'); // an unreadable body
    }, function (err) {
      OO.router.chainFailed(key, failureOf(err));
    }).then(function () { rec.p = null; return OO.router.chainOf(key); });
    return rec.p;
  }
  // A chain can be read: B31 (OO.api.getChain) through the data core's cache (OO.data.loadChain).
  function canRead() {
    return !!OO.api && typeof OO.api.getChain === 'function' && !!OO.data && typeof OO.data.loadChain === 'function';
  }
  // Reads `key`'s chain when it is due (see the header): never read, remembered and not read this page load, or failed
  // CHAIN_RETRY_MS ago. A read in flight is shared.
  function ensureChain(key) {
    if (!key || !OO.router || typeof OO.router.chainOf !== 'function' || !canRead()) return Promise.resolve(null);
    var rec = chainReads[lowerKey(key)];
    if (rec && rec.p) return rec.p;
    var c = OO.router.chainOf(key);
    var now = Date.now();
    var due = !c ? !rec || now - rec.at >= CHAIN_RETRY_MS
      : c.state === 'known' ? !c.fresh && !rec
        : c.state === 'failed' && now - c.at >= CHAIN_RETRY_MS;
    return due ? readChain(key) : Promise.resolve(c);
  }

  // Mode app, the root /payment-callback: the branch it returns to (the URL's online_key, else the last store), or null.
  function returnBranch(route, mode) {
    if (mode !== 'app' || route.hotel || route.page !== 'payment-callback') return null;
    var q = route.query || {};
    var P = OO.pages.acct && OO.pages.acct.paycb;
    var k = P && typeof P.parseParams === 'function' ? P.parseParams(q).onlineKey : q.online_key || q['amp;online_key'];
    if (!(k && String(k).trim()) && OO.store && typeof OO.store.lastHotel === 'function') k = OO.store.lastHotel();
    k = k === undefined || k === null ? '' : String(k).trim();
    return k || null;
  }
  // Its forward (OO.router.paymentReturn): the branch's chain once known; its own path once the read failed. null while
  // the chain is read.
  function returnTarget(branch) {
    var c = OO.router.chainOf(branch);
    return OO.router.paymentReturn(branch, window.location.search || '', { fallback: !!c && c.state !== 'known' }) || null;
  }

  // Mode app, no branch (route.hotel null under a chain): what renders instead of the page → { name, route }, or null
  // when the page renders without a branch (its component's withoutBranch) or there is a branch.
  function branchGate(route, mode) {
    if (mode !== 'app' || route.hotel || !route.chain) return null;
    var Own = own(PAGES, route.page) ? OO.pages[PAGES[route.page]] : null;
    if (Own && Own.withoutBranch === true) return null;
    var S = OO.pages.Stores;
    return { name: 'Stores', route: S && S.withoutBranch === true ? route : Object.assign({}, route, { hotel: route.chain }) };
  }
  // Mode app, a fresh launch (POLISH-4; see the header): Home on the path key's guess while its chain was never read.
  function chainWait(route, mode) {
    if (mode !== 'app' || route.page !== 'home' || !route.chain || route.via !== 'path' || route.redirectTo) return false;
    var Home = OO.pages.Home;
    if (!Home || Home.withoutBranch !== true || !canRead() || !OO.router || typeof OO.router.chainOf !== 'function') return false;
    return OO.router.chainOf(route.chain) === null;
  }
  // Mode app: the branch in use is saved for its chain when the URL's ?store= names a store the known chain lists.
  function saveBranch(route, mode) {
    if (mode !== 'app' || !route.chain || !route.hotel || route.via !== 'store' || route.redirectTo) return;
    var c = OO.router.chainOf(route.chain);
    var listed = !!c && c.state === 'known' && c.keys.some(function (k) { return lowerKey(k) === lowerKey(route.hotel); });
    if (listed) OO.router.setBranch(route.chain, route.hotel);
  }

  function titleFor(route, mode, notLive) {
    var t = notLive ? 'Store not available'
      : route.page === 'legal' && own(LEGAL_TITLES, route.params.kind)
        ? LEGAL_TITLES[route.params.kind]
        : (own(TITLES, route.page) ? TITLES[route.page] : '');
    var suffix = own(SUFFIX, mode) ? SUFFIX[mode] : SUFFIX.online;
    return t ? t + ' · ' + suffix : suffix;
  }

  // Fixture stores run on sample data. Their pages (and the store-less routes, whose store finder lists the sample
  // stores) carry this bar so a preview on a public domain can never be mistaken for live ordering. A live store's
  // pages show none.
  function sampleData(route) {
    var A = OO.api;
    if (!A || !A.isFixtureFor) return false;
    if ((route.page === 'find-store' || route.page === 'scan') && !route.hotel && typeof A.discoveryIsFixture === 'function') {
      return A.discoveryIsFixture();
    }
    return A.isFixtureFor(route.hotel || route.chain || null); // mode app with no branch: the chain's data
  }
  function PreviewBar() {
    return (
      <div className="oo-preview-bar" role="note">
        Preview · sample menu and stores — orders are not sent to any store
      </div>
    );
  }

  function MissingPage() {
    return <div className="oo-stub"><h1>Page not found</h1></div>;
  }

  OO.App = function App() {
    var route = OO.router.useRoute();
    var mode = OO.mode;
    var WL = OO.whitelabel || null;
    if (WL) WL.useRevision(); // WL-2: the theme document and the preview state
    var previewing = !!WL && WL.isPreview();
    var wlWait = previewing && WL.previewPending();
    var notLive = notLiveHere(route);
    var pageKey = route.page + '|' + route.hotel + '|' + JSON.stringify(route.params);

    // Mode app (P4-11; see the header): the root payment callback's branch and forward (none while its chain is read),
    // the chain this route needs, and Select store first when there is no branch.
    var payBranch = returnBranch(route, mode);
    var forward = payBranch ? returnTarget(payBranch) : null;
    var waiting = !!payBranch && !forward;
    var chainKey = mode === 'app' ? route.chain || payBranch : null;
    var gate = notLive ? null : branchGate(route, mode);
    // POLISH-4: a fresh launch's Home waits for its chain in the chain home's loading state (see the header).
    var chainWaiting = !notLive && !gate && chainWait(route, mode);

    // Redirect once: the route's own redirect, else (mode qr, a store this host serves) the table gate's, else (mode app)
    // the root payment callback's forward. The ref stops a loop if the target itself asked for the same redirect, and it
    // is cleared on every redirect-free route, so the same bad URL redirects again later in the session.
    var redirectTo = route.redirectTo || (OO.kiosk && OO.kiosk.redirect(route)) || (notLive ? null : tableRedirect(route, mode)) || forward || (notLive ? null : browseRedirect(route));
    var lastRedirect = React.useRef(null);
    var redirecting = (!!redirectTo && lastRedirect.current !== redirectTo) || waiting;
    React.useLayoutEffect(function () {
      if (!redirectTo) { lastRedirect.current = null; return; }
      if (lastRedirect.current === redirectTo) return;
      lastRedirect.current = redirectTo;
      OO.router.go(redirectTo + (window.location.hash || ''), { replace: true });
    }, [route, redirectTo]);

    // Mode app: read the chain when due, save the branch in use, and stop waiting for a root callback's chain after
    // ROOT_WAIT_MS (the read counts as failed: the forward goes to the branch's own path).
    React.useEffect(function () {
      if (WL) WL.ensure(chainKey || route.hotel);
      if (wlWait) return;
      if (chainKey) ensureChain(chainKey);
      saveBranch(route, mode);
    }, [route, chainKey, wlWait]);
    React.useEffect(function () {
      if (!waiting) return undefined;
      var t = setTimeout(function () { if (!OO.router.chainOf(payBranch)) OO.router.chainFailed(payBranch, 'failed'); }, ROOT_WAIT_MS);
      return function () { clearTimeout(t); };
    }, [waiting, payBranch]);
    // POLISH-4: the fresh launch's wait has the same cap: then the read counts as failed and the guess renders.
    var waitKey = chainWaiting ? route.chain : null;
    React.useEffect(function () {
      if (!waitKey) return undefined;
      var t = setTimeout(function () { if (!OO.router.chainOf(waitKey)) OO.router.chainFailed(waitKey, 'failed'); }, ROOT_WAIT_MS);
      return function () { clearTimeout(t); };
    }, [waitKey]);

    var mounted = React.useRef(false);
    React.useLayoutEffect(function () {
      if (mounted.current && !window.location.hash) window.scrollTo(0, 0);
      mounted.current = true;
      document.title = titleFor(gate ? { page: 'stores', params: {} } : route, mode, notLive);
    }, [pageKey, notLive]);

    React.useLayoutEffect(function () {
      if (((!route.hotel && !route.chain) || notLive) && OO.data && OO.data.deactivateHotel) OO.data.deactivateHotel();
    }, [route.hotel, route.chain, notLive]);

    // P4-09: tells the app shell who is signed in (REF FlutterUserSync); nothing outside the shell.
    OO.platform.useUserSync(route.hotel);
    // P5-06b: the store's tag frame follows the settled route, and its page_view goes (src/lib/analytics.jsx).
    if (OO.analytics) OO.analytics.useRoute(route, { redirecting: redirecting });
    // P5-03: whether the install banner shows (src/ui/chrome.jsx); it re-renders when the browser offers an install.
    var install = typeof OO.ui.useInstallPrompt === 'function' ? OO.ui.useInstallPrompt(route, mode) : null;

    if (redirecting) return null;
    var Frame = OO.ui.PageFrame;
    var WLBar = previewing ? WL.PreviewBar : null;
    // WL-2: the preview's draft is on its way: only the bar (see the header).
    if (wlWait) return <Frame mode={mode} withTabBar={false}><WLBar /></Frame>;

    // The dev gallery is never served in production; there it falls through to NotFound.
    if (!notLive && own(DEV_PAGES, route.page) && (OO.boot || {}).env !== 'production') {
      var DevPage = OO.pages[DEV_PAGES[route.page]] || MissingPage;
      return <DevPage route={route} mode={mode} />;
    }
    // Not live here: NotFound shows the "not available here yet" card for this store. Mode app with no branch: Select
    // store first (gate).
    var name = gate ? gate.name : !notLive && own(PAGES, route.page) ? PAGES[route.page] : 'NotFound';
    var Page = OO.pages[name] || OO.pages.NotFound || MissingPage;
    var kitchen = mode === 'app' && OO.whitelabel && OO.whitelabel.kitchenDesign && OO.whitelabel.kitchenDesign(route.chain || route.hotel);
    var pizzaClub = mode === 'app' && OO.whitelabel && OO.whitelabel.pizzaClubDesign && OO.whitelabel.pizzaClubDesign(route.chain || route.hotel);
    var zeta = OO.whitelabel && OO.whitelabel.zetaDesign && OO.whitelabel.zetaDesign(route.chain || route.hotel);
    var withTabs = !gate && !notLive && (mode === 'app' || !!zeta) && (TAB_PAGES.indexOf(route.page) >= 0 || ['app-content', 'settings'].indexOf(route.page) >= 0 || (kitchen && ['bookings', 'booking-track', 'about', 'track'].indexOf(route.page) >= 0) || (zeta && ['track', 'zeta-addresses', 'order-status'].indexOf(route.page) >= 0) || ((pizzaClub || zeta || kitchen) && ['stores', 'profile', 'wallet', 'coupons', 'deals', 'legal', 'booking'].indexOf(route.page) >= 0));
    // Mode qr: the table page hosts the gate and the guest welcome on a store's other pages (it is the page on 'scan').
    // It takes the page and renders it itself (QR-OFF: it leaves it out where the store's table ordering is off; it
    // re-renders when the store loads or changes, which OO.App doesn't follow).
    var TableHost = mode === 'qr' && !notLive && route.hotel && route.page !== 'scan' ? OO.pages.Table || null : null;
    // POLISH-4: while a fresh launch waits, Home gets the route with no branch (its chain home's skeleton); the tab bar
    // keeps the route as it is.
    var pageRoute = gate ? gate.route : chainWaiting ? Object.assign({}, route, { hotel: null, via: null }) : route;
    var page = <Page route={pageRoute} mode={mode} />;
    // Ordering QRs get the same explicit starting step as table-side menus. Other pages and modes keep their flow.
    var QrStart = OO.pages.QrStart;
    if (!gate && !notLive && mode === 'online' && route.page === 'menu' && QrStart && OO.orderingQr && OO.orderingQr.forHotel(route.hotel)) {
      page = <QrStart.Host route={pageRoute} mode={mode} page={page} />;
    }
    if (!gate && !notLive && zeta && OO.appKit.ZetaReferenceScreen) {
      var zr = { home:859871,menu:422031,about:807420,stores:274779,account:104673, profile:104673, 'zeta-addresses':399901, settings:692801, wallet:958449, rewards:982996, coupons:764679, deals:806807, orders:188947 };
      var zrId = route.page === 'app-content' && OO.pages.AppContent.hasPage(pageRoute,route.params.slug) ? OO.appKit.ZetaReferenceScreen.info[route.params.slug] : route.page === 'menu' && route.query.saved === 'true' ? 330084 : route.page === 'track' ? route.params.orderId ? 486935 : 632496 : route.page === 'order-status' ? 486935 : zr[route.page];
      if (zrId) page = <OO.appKit.ZetaReferenceScreen route={pageRoute} id={zrId} />;
    }

    if (!gate && !notLive && zeta && route.page === 'legal' && OO.pages.AppContent.hasPage(pageRoute,route.params.kind==='allergy'?'allergen':route.params.kind)) page=<OO.appKit.ZetaReferenceScreen route={pageRoute} id={OO.appKit.ZetaReferenceScreen.info[route.params.kind==='allergy'?'allergen':route.params.kind]}/>;
    // WL-VEL: mode landing, landing.orderingChrome 'site': the website's header and footer around the ordering pages
    // (src/ui/ordering-site.jsx). Everything else renders the page exactly as before.
    var Site = OO.ui.OrderingSite;
    var siteChrome = Site && typeof Site.applies === 'function' && !notLive && !gate && mode === 'online' ? Site.applies(route) : null;
    if (siteChrome) page = <Site route={route} site={siteChrome}>{page}</Site>;
    var TabBar = OO.ui.AppTabBar;
    return (
      <Frame mode={mode} withTabBar={withTabs}>
        {WLBar ? <WLBar /> : OO.orderingQr && OO.orderingQr.preview() ? <div role="status" style={{ padding: '10px 16px', background: '#fff3cd', color: '#493b13', textAlign: 'center' }}>Ordering QR preview · Checkout is disabled</div> : sampleData(route) ? <PreviewBar /> : install ? <OO.ui.InstallPrompt kind={install} route={route} mode={mode} /> : null}
        {mode === 'kiosk' ? <OO.pages.KioskHost route={route} page={page} /> : TableHost ? <TableHost route={route} mode={mode} page={page} /> : page}
        {withTabs ? <TabBar route={route} /> : null}
      </Frame>
    );
  };
  OO.App.ensureChain = ensureChain;
  OO.App.browseRedirect = browseRedirect; // WL-5
})();


// ===================== glue/version-check.jsx =====================
// window.SipoVersion detects when a NEWER build of the Online Order SPA is deployed while this tab stays
// open. It then signals glue/boot.jsx, which shows the "Update available" prompt. It never force-reloads.
// A fresh page load already gets the latest code, because the Blade shell reads the content-hashed
// manifest server-side; this only closes the long-open-tab gap. There is no backend route: manifest.json
// is a static file in public/online-order/.
//
// Port of the warehouse twin, with the same NO-OP idiom: a network blip must NEVER flip the stale flag.
// Only a positively read, different, non-empty JS or CSS hash does. The manifest URL is path-only (assetRoot
// with the host stripped, no trailing slash), so the fetch stays same-origin under /dev_server/public too.
(function () {
  'use strict';
  var BOOT = window.__SIPO_BOOT || {};
  var loadedJs = BOOT.buildJs || '';                       // the js hash THIS html loaded (baseline)
  var loadedCss = BOOT.buildCss || '';                     // absent in older shells: JS-only detection still works
  var base = String(BOOT.assetRoot || '/').replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, '');
  var MANIFEST = base + '/online-order/manifest.json';
  var POLL_MS = 90000;

  var stale = false;
  var timer = null;

  // A CSS-only release must also offer the existing safe update prompt. Missing fields never mark a tab stale.
  function isStale(loaded, manifest, css) {
    return !!(loaded && manifest && typeof manifest.js === 'string' && manifest.js &&
      (manifest.js !== loaded || (css && typeof manifest.css === 'string' && manifest.css && manifest.css !== css)));
  }

  function stopTimer() { if (timer) { clearInterval(timer); timer = null; } }

  function markStale() {
    if (stale) return;
    stale = true;
    stopTimer();
    window.dispatchEvent(new CustomEvent('sipo:update-available'));
  }

  // Fetch the manifest bypassing every cache (no-store + cache-bust). Any failure is a NO-OP.
  function check() {
    if (stale || !loadedJs) return Promise.resolve();
    return fetch(MANIFEST + '?t=' + Date.now(), { cache: 'no-store' })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (m) { if (isStale(loadedJs, m, loadedCss)) markStale(); })
      .catch(function () { /* network blip: never flip stale */ });
  }

  function startTimer() {
    if (timer || stale || !loadedJs) return;
    timer = setInterval(function () { if (!document.hidden) check(); }, POLL_MS);
  }

  var started = false;
  function start() {
    if (started || !loadedJs) return;      // once; dev / no manifest baseline: do nothing
    started = true;
    var onVisible = function () {
      if (document.hidden) { stopTimer(); }
      else { check(); startTimer(); }
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    startTimer();
    check();                               // immediate first check
  }

  window.SipoVersion = {
    isStale: isStale,
    check: check,
    start: start,
    stale: function () { return stale; },
    buildId: function () { return BOOT.buildId || ''; },
  };
})();


// ===================== glue/boot.jsx =====================
// glue/boot.jsx is concatenated LAST. It renders Root into #root. Root is OO.App inside an error
// boundary, plus the "Update available" prompt, which appears when glue/version-check.jsx detects a newer
// deploy (sipo:update-available). Root starts window.SipoVersion once.
//
// The prompt never force-reloads. "Refresh now" reloads. "Not now" (or Esc) defers to a small "Update available"
// pill, which reopens the prompt, and the customer's cart and checkout state stay untouched. It manages focus as a
// modal dialog (focus in, Tab trapped, focus restored on close).
//
// window.__OO_READY is set after the first commit. From then on the blade's #__err overlay ignores
// script errors, which go to the error boundary or the console instead. A third-party SDK error (payments,
// maps) must never blank a live storefront. There is no push glue in this SPA.
(function () {
  'use strict';
  var React = window.React, ReactDOM = window.ReactDOM;
  var OO = (window.OO = window.OO || {});
  var rootEl = document.getElementById('root');

  function showBootError(err) {
    var msg = err && err.message ? err.message : String(err);
    if (window.console) console.error('[online-order] boot failed:', err);
    var overlay = document.getElementById('__err');
    if (overlay && rootEl && rootEl.querySelector('[data-oo-prerender="website"]')) {
      overlay.style.cssText = 'position:fixed;bottom:0;left:0;right:0;padding:16px;background:#fff7f5;color:#7a1f12;z-index:9999;font:14px sans-serif';
      overlay.setAttribute('role', 'alert');
      overlay.textContent = 'The page could not finish loading. Please refresh to use ordering and forms.';
      return;
    }
    if (overlay && !window.__OO_READY) {
      overlay.style.display = 'block';
      overlay.textContent = 'Sorry, this page could not load. Please refresh.\n\n' + msg;
    } else if (rootEl) {
      rootEl.textContent = 'Sorry, this page could not load. Please refresh.';
    }
  }

  if (!React || !ReactDOM || !ReactDOM.createRoot) {
    showBootError(new Error('React 18 did not load.'));
    return;
  }

  // The "Update available" dialog is modal: focus moves in (to "Not now", the safe choice: the prompt appears on its
  // own, maybe while the customer is typing, and an accidental Enter must not reload the page), Tab stays on its two
  // buttons, Esc = "Not now", and closing it hands focus back to where it was (else to the "Update available" pill).
  function focusEl(el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }
  function UpdatePrompt() {
    var s = React.useState(false), deferred = s[0], setDeferred = s[1];
    var cardRef = React.useRef(null);
    var safeRef = React.useRef(null);
    var pillRef = React.useRef(null);
    var returnTo = React.useRef(null);
    React.useEffect(function () {
      if (deferred) {
        var back = returnTo.current;
        if (back && document.contains(back)) focusEl(back);
        else if (pillRef.current) focusEl(pillRef.current);
        return undefined;
      }
      var active = document.activeElement;
      returnTo.current = active && active !== document.body ? active : null;
      if (safeRef.current) focusEl(safeRef.current);
      function onKey(e) {
        if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); setDeferred(true); return; }
        if (e.key !== 'Tab' || !cardRef.current) return;
        var items = [].slice.call(cardRef.current.querySelectorAll('button'));
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1], at = document.activeElement;
        if (!cardRef.current.contains(at)) { e.preventDefault(); focusEl(e.shiftKey ? last : first); }
        else if (e.shiftKey && at === first) { e.preventDefault(); focusEl(last); }
        else if (!e.shiftKey && at === last) { e.preventDefault(); focusEl(first); }
      }
      document.addEventListener('keydown', onKey, true);
      return function () { document.removeEventListener('keydown', onKey, true); };
    }, [deferred]);
    if (deferred) {
      return (
        <button ref={pillRef} type="button" className="oo-update-pill" aria-haspopup="dialog" onClick={function () { setDeferred(false); }}>
          Update available
        </button>
      );
    }
    return (
      <div className="oo-update" role="dialog" aria-modal="true" aria-labelledby="oo-update-title" aria-describedby="oo-update-body">
        <div className="oo-update-card" ref={cardRef}>
          <h2 id="oo-update-title" className="oo-update-title">Update available</h2>
          <p id="oo-update-body" className="oo-update-body">
            A newer version of this page is ready. Refresh to load it, or carry on and update later.
          </p>
          <div className="oo-update-actions">
            <button ref={safeRef} type="button" className="oo-update-btn" onClick={function () { setDeferred(true); }}>
              Not now
            </button>
            <button type="button" className="oo-update-btn oo-update-btn--primary" onClick={function () { window.location.reload(); }}>
              Refresh now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // A crash in one page shows a calm fallback instead of unmounting the whole tree. Navigating away
  // (popstate / oo:route) clears it.
  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { error: null };
      this.reset = this.reset.bind(this);
    }
    static getDerivedStateFromError(error) { return { error: error }; }
    componentDidCatch(error, info) {
      if (window.console) console.error('[online-order] render error:', error, info && info.componentStack);
    }
    componentDidMount() {
      window.addEventListener('popstate', this.reset);
      window.addEventListener('oo:route', this.reset);
    }
    componentWillUnmount() {
      window.removeEventListener('popstate', this.reset);
      window.removeEventListener('oo:route', this.reset);
    }
    reset() { if (this.state.error) this.setState({ error: null }); }
    render() {
      if (!this.state.error) return this.props.children;
      return (
        <div className="oo-crash" role="alert">
          <h1 className="oo-crash-title">Something went wrong</h1>
          <p className="oo-crash-body">Please refresh the page to try again.</p>
          <button type="button" className="oo-crash-btn" onClick={function () { window.location.reload(); }}>Refresh</button>
        </div>
      );
    }
  }

  var versionStarted = false;

  // Keep the startup screen until the first page's existing loading regions settle. Watching the
  // rendered regions also covers chained reads (store → menu → rewards) and redirect-only renders.
  // This runs once per document: later refreshes, checkout actions and payment progress keep their
  // own UI. Do not replace or delay any of the underlying requests.
  function startupColour() {
    var T = OO.theme, W = OO.whitelabel;
    var applied = document.documentElement.style.getPropertyValue('--oo-primary');
    var route = OO.router.current();
    var site = W && W.siteFor(route.chain || route.hotel);
    var primary = applied || (site && W.forSurface(site.doc).colors.primary);
    return T.normalizeColor(primary) || '#000000';
  }

  // A published website already has useful public HTML. Mount its normal page directly so the
  // startup gate does not cover that content while unrelated menu/directory reads finish.
  // All ordering routes, previews, missing fragments and old shells keep the original gate.
  function hasPublishedWebsite() {
    var pre = OO.boot && OO.boot.websitePrerender, W = OO.whitelabel, route = OO.router.current();
    var site = W && W.siteFor(route.hotel);
    return !!(pre && pre.schema === 1 && OO.landingMode && window.location.pathname === pre.path
      && route.page === 'landing' && !route.redirectTo && !(route.params && route.params.redirect)
      && site && site.doc.landing && !W.isPreview());
  }

  function InitialPage(props) {
    var s = React.useState(function () { return !hasPublishedWebsite(); }), loading = s[0], setLoading = s[1];
    var c = React.useState(startupColour), colour = c[0], setColour = c[1];
    var host = React.useRef(null);
    React.useLayoutEffect(function () {
      if (!loading) {
        if (typeof window.__OO_PRERENDER_CLEANUP === 'function') window.__OO_PRERENDER_CLEANUP();
        rootEl.setAttribute('data-oo-website-mounted', 'true');
        return undefined;
      }
      var content = host.current;
      var frame = null, done = false, unsubscribe = null;
      var observer = new MutationObserver(check);
      function pending() {
        var W = OO.whitelabel;
        if (!content.firstElementChild || (W && W.previewPending())) return true;
        // A failure must reveal the page's retry/refresh controls, even if a secondary block is pending.
        if (content.querySelector('.oo-crash, [role="alert"]')) return false;
        return !!content.querySelector('.oo-skel, .oo-lp-skel, main[aria-busy="true"]');
      }
      function stop() {
        observer.disconnect();
        if (unsubscribe) unsubscribe();
        if (frame !== null) window.cancelAnimationFrame(frame);
        document.body.classList.remove('oo-startup-loading');
      }
      function check() {
        if (done) return;
        setColour(startupColour());
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null;
        if (pending()) return;
        // Let child effects and their first data reads commit before declaring the page ready.
        frame = window.requestAnimationFrame(function () {
          frame = window.requestAnimationFrame(function () {
            frame = null;
            if (pending()) return;
            done = true;
            stop();
            setLoading(false);
          });
        });
      }
      document.body.classList.add('oo-startup-loading');
      observer.observe(content, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-busy', 'role'] });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
      if (OO.whitelabel) unsubscribe = OO.whitelabel.subscribe(check);
      check();
      return function () { done = true; stop(); };
    }, []);
    return <React.Fragment>
      <div ref={host} className="oo-startup-content" inert={loading ? '' : undefined} aria-hidden={loading ? 'true' : undefined}>
        {props.children}
      </div>
      {loading ? <div className="oo-startup" role="status" aria-label="Loading">
        <span className="oo-startup__spinner" style={{ borderTopColor: colour }} aria-hidden="true" />
      </div> : null}
    </React.Fragment>;
  }

  function Root() {
    var u = React.useState(function () { return !!(window.SipoVersion && window.SipoVersion.stale()); });
    var updateReady = u[0], setUpdateReady = u[1];

    React.useEffect(function () {
      window.__OO_READY = true;
      function onUpdate() { setUpdateReady(true); }
      window.addEventListener('sipo:update-available', onUpdate);
      if (!versionStarted && window.SipoVersion && window.SipoVersion.start) {
        versionStarted = true;
        window.SipoVersion.start();
      }
      return function () { window.removeEventListener('sipo:update-available', onUpdate); };
    }, []);

    var App = OO.App;
    return (
      <React.Fragment>
        <InitialPage><ErrorBoundary><App /></ErrorBoundary></InitialPage>
        {updateReady ? <UpdatePrompt /> : null}
      </React.Fragment>
    );
  }

  try {
    if (!rootEl) throw new Error('#root is missing from the page.');
    if (typeof OO.App !== 'function') throw new Error('OO.App is missing from the bundle.');
    if (hasPublishedWebsite()) {
      var site = OO.whitelabel.siteFor(OO.router.current().hotel);
      OO.theme.apply(OO.theme.toCssVars(OO.whitelabel.themeOver({}, site.doc), 'online'));
    }
    ReactDOM.createRoot(rootEl).render(<Root />);
  } catch (err) {
    showBootError(err);
  }
})();
